/**
 * Generated by orval 🍺
 * Do not edit manually.
 * CDF Template API
 * OpenAPI-first contract for the CDF template.
 *
 * Workflow:
 *   1. Edit this spec FIRST for any API change.
 *   2. Run `pnpm gen:api` to regenerate the React Query client
 *      (lib/api-client-react) and Zod schemas (lib/api-zod).
 *   3. Implement/adjust the Fastify routes in artifacts/api-server
 *      to match, validating with the generated Zod schemas.
 *
 * OpenAPI spec version: 0.1.0
 */
import {
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import { customFetch } from '../mutator';

// https://stackoverflow.com/questions/49579094/typescript-conditional-types-filter-out-readonly-properties-pick-only-requir/49579497#49579497
type IfEquals<X, Y, A = X, B = never> = (<T>() => T extends X ? 1 : 2) extends <
T,
>() => T extends Y ? 1 : 2
? A
: B;

type WritableKeys<T> = {
[P in keyof T]-?: IfEquals<
  { [Q in P]: T[P] },
  { -readonly [Q in P]: T[P] },
  P
>;
}[keyof T];

type UnionToIntersection<U> =
  (U extends any ? (k: U)=>void : never) extends ((k: infer I)=>void) ? I : never;
type DistributeReadOnlyOverUnions<T> = T extends any ? NonReadonly<T> : never;

type Writable<T> = Pick<T, WritableKeys<T>>;
type NonReadonly<T> = [T] extends [UnionToIntersection<T>] ? {
  [P in keyof Writable<T>]: T[P] extends object
    ? NonReadonly<NonNullable<T[P]>>
    : T[P];
} : DistributeReadOnlyOverUnions<T>;

/**
 * Canonical CSS variable name (for example --primary) to a validated CSS value.
 */
export interface DesignSystemCssVars {[key: string]: string}

export type DesignSystemContrastCheckMode = typeof DesignSystemContrastCheckMode[keyof typeof DesignSystemContrastCheckMode];


export const DesignSystemContrastCheckMode = {
  light: 'light',
  dark: 'dark',
} as const;

export interface DesignSystemContrastCheck {
  pair: string;
  foreground: string;
  background: string;
  mode: DesignSystemContrastCheckMode;
  ratio: number;
  threshold: number;
}

export interface DesignSystemReport {
  blocking: DesignSystemContrastCheck[];
  warnings: DesignSystemContrastCheck[];
  suppliedTokens: string[];
  inheritedFromPlatform: string[];
  unknownTokens: string[];
  ignoredGroups: string[];
}

export type DesignSystemSummaryStatus = typeof DesignSystemSummaryStatus[keyof typeof DesignSystemSummaryStatus];


export const DesignSystemSummaryStatus = {
  ready: 'ready',
  blocked: 'blocked',
} as const;

export type DesignSystemSummaryVocabulary = typeof DesignSystemSummaryVocabulary[keyof typeof DesignSystemSummaryVocabulary];


export const DesignSystemSummaryVocabulary = {
  mui: 'mui',
  material3: 'material3',
  mixed: 'mixed',
} as const;

export type DesignSystemSummaryOrigin = typeof DesignSystemSummaryOrigin[keyof typeof DesignSystemSummaryOrigin];


export const DesignSystemSummaryOrigin = {
  upload: 'upload',
  sample: 'sample',
} as const;

export type DesignSystemSummaryDarkSource = typeof DesignSystemSummaryDarkSource[keyof typeof DesignSystemSummaryDarkSource];


export const DesignSystemSummaryDarkSource = {
  explicit: 'explicit',
  derived: 'derived',
  none: 'none',
} as const;

export interface DesignSystemSummary {
  id: string;
  name: string;
  description: string;
  version: string;
  status: DesignSystemSummaryStatus;
  vocabulary: DesignSystemSummaryVocabulary;
  origin: DesignSystemSummaryOrigin;
  darkSource: DesignSystemSummaryDarkSource;
  blockingCount: number;
  warningCount: number;
  activeForTenant: boolean;
  activeEntityIds: string[];
  createdAt: string;
  updatedAt: string;
}

export type DesignSystemDetail = DesignSystemSummary & {
  tokensLight: DesignSystemCssVars;
  tokensDark: DesignSystemCssVars;
  report: DesignSystemReport;
};

export interface DesignSystemEntityActivation {
  entityId: string;
  entityName: string;
  designSystemId: string;
}

export type DesignSystemSampleId = typeof DesignSystemSampleId[keyof typeof DesignSystemSampleId];


export const DesignSystemSampleId = {
  'platform-default': 'platform-default',
  template: 'template',
} as const;

export interface DesignSystemSample {
  id: DesignSystemSampleId;
  name: string;
  description: string;
}

export interface DesignSystemList {
  /** @maxItems 100 */
  items: DesignSystemSummary[];
  total: number;
  limit: number;
  offset: number;
  /** @nullable */
  tenantActiveId: string | null;
  entityActivations: DesignSystemEntityActivation[];
  samples: DesignSystemSample[];
}

/**
 * W3C Design Tokens document (JSON object). Material UI or Material Design 3 vocabulary; optional light and dark groups.
 */
export type DesignSystemCreateTokens = { [key: string]: unknown };

export interface DesignSystemCreate {
  /**
     * @minLength 1
     * @maxLength 120
     */
  name: string;
  /** @maxLength 1000 */
  description?: string;
  /**
     * @minLength 1
     * @maxLength 40
     */
  version?: string;
  /** W3C Design Tokens document (JSON object). Material UI or Material Design 3 vocabulary; optional light and dark groups. */
  tokens: DesignSystemCreateTokens;
}

export interface DesignSystemUpdate {
  /**
     * @minLength 1
     * @maxLength 120
     */
  name?: string;
  /** @maxLength 1000 */
  description?: string;
  /**
     * @minLength 1
     * @maxLength 40
     */
  version?: string;
}

export interface DesignSystemScopeSelection {
  /**
     * Omit or null for the tenant-wide choice.
     * @nullable
     */
  entityId?: string | null;
}

export type DesignSystemExportDocument = { [key: string]: unknown };

export interface DesignSystemExport {
  fileName: string;
  document: DesignSystemExportDocument;
}

export type ResolvedBrandingSource = typeof ResolvedBrandingSource[keyof typeof ResolvedBrandingSource];


export const ResolvedBrandingSource = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export interface ResolvedBranding {
  source: ResolvedBrandingSource;
  /** @nullable */
  designSystemId: string | null;
  /** @nullable */
  name: string | null;
  light: DesignSystemCssVars;
  dark: DesignSystemCssVars;
}

export type AccessMigrationEntryKind = typeof AccessMigrationEntryKind[keyof typeof AccessMigrationEntryKind];


export const AccessMigrationEntryKind = {
  person: 'person',
  group: 'group',
} as const;

export type AccessMigrationEntryScope = typeof AccessMigrationEntryScope[keyof typeof AccessMigrationEntryScope];


export const AccessMigrationEntryScope = {
  platform: 'platform',
  tenant: 'tenant',
  entity: 'entity',
} as const;

export type AccessMigrationEntryCurrentRolesItem = {
  id: string;
  name: string;
};

export type AccessMigrationEntryPermanentRolesItem = {
  id: string;
  name: string;
};

export type AccessMigrationEntryEligibleRolesItem = {
  id: string;
  name: string;
};

export type AccessMigrationEntryStatus = typeof AccessMigrationEntryStatus[keyof typeof AccessMigrationEntryStatus];


export const AccessMigrationEntryStatus = {
  ready: 'ready',
  unchanged: 'unchanged',
  blocked: 'blocked',
} as const;

export interface AccessMigrationEntry {
  key: string;
  kind: AccessMigrationEntryKind;
  name: string;
  scope: AccessMigrationEntryScope;
  currentRoles: AccessMigrationEntryCurrentRolesItem[];
  permanentRoles: AccessMigrationEntryPermanentRolesItem[];
  eligibleRoles: AccessMigrationEntryEligibleRolesItem[];
  addedPermissions: string[];
  elevationPermissions: string[];
  status: AccessMigrationEntryStatus;
  message: string;
}

export interface AccessMigrationPreview {
  digest: string;
  items: AccessMigrationEntry[];
  total: number;
  limit: number;
  offset: number;
  ready: number;
  blocked: number;
}

export type AccessMigrationApplyConfirmation = typeof AccessMigrationApplyConfirmation[keyof typeof AccessMigrationApplyConfirmation];


export const AccessMigrationApplyConfirmation = {
  APPLY: 'APPLY',
} as const;

export interface AccessMigrationApply {
  tenantId?: string;
  /** @pattern ^[a-f0-9]{64}$ */
  digest: string;
  confirmation: AccessMigrationApplyConfirmation;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface AccessMigrationResult {
  changed: number;
  unchanged: number;
  blocked: number;
}

export type DocumentConversationCreateVisibility = typeof DocumentConversationCreateVisibility[keyof typeof DocumentConversationCreateVisibility];


export const DocumentConversationCreateVisibility = {
  shared: 'shared',
  personal: 'personal',
} as const;

export interface DocumentConversationCreate {
  clientRequestId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  visibility: DocumentConversationCreateVisibility;
  /** @nullable */
  workspaceId?: string | null;
}

export type DocumentConversationVisibility = typeof DocumentConversationVisibility[keyof typeof DocumentConversationVisibility];


export const DocumentConversationVisibility = {
  shared: 'shared',
  personal: 'personal',
} as const;

export interface DocumentConversation {
  id: string;
  documentId: string;
  /** @nullable */
  workspaceId: string | null;
  visibility: DocumentConversationVisibility;
  createdByAppUserId: string;
  title: string;
  /** @minimum 1 */
  revision: number;
  createdAt: string;
}

export interface DocumentConversationList {
  /** @maxItems 50 */
  items: DocumentConversation[];
  total: number;
  /** @minimum 1 */
  limit: number;
  offset: number;
}

export type DocumentConversationTurnInputMode = typeof DocumentConversationTurnInputMode[keyof typeof DocumentConversationTurnInputMode];


export const DocumentConversationTurnInputMode = {
  answer: 'answer',
  proposal: 'proposal',
} as const;

export type ManagedDocumentAiTargetKind = typeof ManagedDocumentAiTargetKind[keyof typeof ManagedDocumentAiTargetKind];


export const ManagedDocumentAiTargetKind = {
  selection: 'selection',
  cursor: 'cursor',
  section: 'section',
  sections: 'sections',
} as const;

export interface ManagedDocumentAiTarget {
  kind: ManagedDocumentAiTargetKind;
  /**
     * @minItems 1
     * @maxItems 20
     */
  sectionIds?: string[];
  /**
     * @minimum 0
     * @maximum 9999
     */
  blockIndex: number;
  /**
     * @minimum 0
     * @maximum 1000000
     */
  from: number;
  /**
     * @minimum 0
     * @maximum 1000000
     */
  to: number;
  /** @minimum 1 */
  expectedRevision: number;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
}

export type DocumentConversationSnapshotUploadKind = typeof DocumentConversationSnapshotUploadKind[keyof typeof DocumentConversationSnapshotUploadKind];


export const DocumentConversationSnapshotUploadKind = {
  full: 'full',
  delta: 'delta',
} as const;

export type DocumentConversationSnapshotUploadContent = { [key: string]: unknown };

export type DocumentConversationSnapshotUploadChangesItemInsertItem = { [key: string]: unknown };

export type DocumentConversationSnapshotUploadChangesItem = {
  /**
     * @minimum 0
     * @maximum 20000
     */
  index: number;
  /**
     * @minimum 0
     * @maximum 20000
     */
  deleteCount: number;
  /** @maxItems 5000 */
  insert: DocumentConversationSnapshotUploadChangesItemInsertItem[];
};

/**
 * Optional live editor snapshot used as untrusted conversation evidence. kind=full uploads the whole body; kind=delta replaces top-level blocks of this person's last snapshot in this conversation (baseHash). Bounded to 120,000 serialized characters, 5,000 structural values, depth 32 and 200 sections.
 */
export interface DocumentConversationSnapshotUpload {
  kind: DocumentConversationSnapshotUploadKind;
  content?: DocumentConversationSnapshotUploadContent;
  /** @pattern ^[a-f0-9]{64}$ */
  baseHash?: string;
  /** @maxItems 200 */
  changes?: DocumentConversationSnapshotUploadChangesItem[];
}

export interface DocumentConversationTurnInput {
  clientRequestId: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  message: string;
  /** @minimum 1 */
  expectedRevision: number;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
  mode: DocumentConversationTurnInputMode;
  target?: ManagedDocumentAiTarget;
  snapshot?: DocumentConversationSnapshotUpload;
}

export type DocumentConversationTurnMetricsSnapshot = typeof DocumentConversationTurnMetricsSnapshot[keyof typeof DocumentConversationTurnMetricsSnapshot];


export const DocumentConversationTurnMetricsSnapshot = {
  saved: 'saved',
  full: 'full',
  delta: 'delta',
} as const;

export type DocumentConversationTurnMetricsMode = typeof DocumentConversationTurnMetricsMode[keyof typeof DocumentConversationTurnMetricsMode];


export const DocumentConversationTurnMetricsMode = {
  answer: 'answer',
  proposal: 'proposal',
} as const;

/**
 * @nullable
 */
export type DocumentConversationTurnMetrics = {
  /** @minimum 0 */
  durationMs: number;
  /** @minimum 0 */
  modelSteps: number;
  /** @minimum 0 */
  promptChars: number;
  /** @minimum 0 */
  uploadChars: number;
  snapshot: DocumentConversationTurnMetricsSnapshot;
  mode: DocumentConversationTurnMetricsMode;
  /**
     * @minimum 0
     * @nullable
     */
  inputTokens: number | null;
  /**
     * @minimum 0
     * @nullable
     */
  outputTokens: number | null;
  /** @minimum 0 */
  actionsProposed: number;
} | null;

export type DocumentAgentActionProposalAction = typeof DocumentAgentActionProposalAction[keyof typeof DocumentAgentActionProposalAction];


export const DocumentAgentActionProposalAction = {
  move: 'move',
  publish: 'publish',
  restore_version: 'restore_version',
  share_link: 'share_link',
  resolve_suggestions: 'resolve_suggestions',
} as const;

export type DocumentAgentActionProposalShareState = typeof DocumentAgentActionProposalShareState[keyof typeof DocumentAgentActionProposalShareState];


export const DocumentAgentActionProposalShareState = {
  unpublished: 'unpublished',
  revoked: 'revoked',
} as const;

export type DocumentAgentActionProposalDecision = typeof DocumentAgentActionProposalDecision[keyof typeof DocumentAgentActionProposalDecision];


export const DocumentAgentActionProposalDecision = {
  accept: 'accept',
  reject: 'reject',
  resolve_comment: 'resolve_comment',
} as const;

/**
 * A transient native action the document agent suggests. It is never executed until the person confirms it through runDocumentAgentAction, which reruns the native permission checks and audit.
 */
export interface DocumentAgentActionProposal {
  action: DocumentAgentActionProposalAction;
  /**
     * @minLength 1
     * @maxLength 500
     */
  summary: string;
  /** @maxLength 200 */
  folder?: string;
  versionId?: string;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  reason?: string;
  shareState?: DocumentAgentActionProposalShareState;
  decision?: DocumentAgentActionProposalDecision;
  /** @maxItems 50 */
  targetIds?: string[];
}

export interface DocumentAgentActionInput {
  clientRequestId: string;
  turnId: string;
  /** @minimum 1 */
  expectedRevision: number;
  confirmed: true;
  proposal: DocumentAgentActionProposal;
}

export type DocumentAgentActionResultAction = typeof DocumentAgentActionResultAction[keyof typeof DocumentAgentActionResultAction];


export const DocumentAgentActionResultAction = {
  move: 'move',
  publish: 'publish',
  restore_version: 'restore_version',
  share_link: 'share_link',
  resolve_suggestions: 'resolve_suggestions',
} as const;

export interface DocumentAgentActionResult {
  clientRequestId: string;
  action: DocumentAgentActionResultAction;
  replayed: boolean;
  summary: string;
  createdAt: string;
}

export type DocumentConversationMessageStatus = typeof DocumentConversationMessageStatus[keyof typeof DocumentConversationMessageStatus];


export const DocumentConversationMessageStatus = {
  running: 'running',
  completed: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
  interrupted: 'interrupted',
} as const;

export interface DocumentConversationMessage {
  id: string;
  threadId: string;
  authorAppUserId: string;
  /** @minimum 1 */
  sequence: number;
  message: string;
  /**
     * @maxLength 100000
     * @nullable
     */
  answer: string | null;
  status: DocumentConversationMessageStatus;
  restricted: boolean;
  /** @minimum 1 */
  draftRevision: number;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
  createdAt: string;
  /** @nullable */
  completedAt: string | null;
  metrics?: DocumentConversationTurnMetrics | null;
}

export interface DocumentConversationMessages {
  /** @maxItems 50 */
  items: DocumentConversationMessage[];
  total: number;
  /** @minimum 1 */
  limit: number;
  offset: number;
}

export interface DocumentConversationActivity {
  /** @minimum 1 */
  revision: number;
  /** @nullable */
  runningTurnId: string | null;
  /** @nullable */
  leaseExpiresAt: string | null;
  expired: boolean;
}

export interface DocumentConversationOutcome {
  clientRequestId: string;
  message: DocumentConversationMessage;
  /** @nullable */
  proposalId: string | null;
  /**
     * @nullable
     * @pattern ^[a-f0-9]{64}$
     */
  snapshotHash?: string | null;
  /** @maxItems 5 */
  actions?: DocumentAgentActionProposal[];
}

export type CampaignAutonomyBoundsChannelsItem = typeof CampaignAutonomyBoundsChannelsItem[keyof typeof CampaignAutonomyBoundsChannelsItem];


export const CampaignAutonomyBoundsChannelsItem = {
  email: 'email',
  sms: 'sms',
  whatsapp: 'whatsapp',
  social_post: 'social_post',
} as const;

export interface CampaignAutonomyBounds {
  /**
     * @minItems 1
     * @maxItems 4
     */
  channels: CampaignAutonomyBoundsChannelsItem[];
  startsAt: string;
  expiresAt: string;
  /**
     * @minimum 1
     * @maximum 20000
     */
  maxRecipients: number;
  /**
     * @minimum 1
     * @maximum 100000
     */
  maxSends: number;
  /**
     * @minimum 0
     * @maximum 100000000
     */
  maxCostMinor: number;
  /** @pattern ^[A-Z]{3}$ */
  currency: string;
}

export type CampaignAutonomyValidationChannelsItem = typeof CampaignAutonomyValidationChannelsItem[keyof typeof CampaignAutonomyValidationChannelsItem];


export const CampaignAutonomyValidationChannelsItem = {
  email: 'email',
  sms: 'sms',
  whatsapp: 'whatsapp',
  social_post: 'social_post',
} as const;

export interface CampaignAutonomyValidation {
  /**
     * @minLength 10
     * @maxLength 10000
     */
  evidence: string;
  validatedAt: string;
  expiresAt: string;
  /**
     * @minItems 1
     * @maxItems 4
     */
  channels: CampaignAutonomyValidationChannelsItem[];
}

export interface CampaignAutonomyEnable {
  /** @minimum 1 */
  validationVersion: number;
  bounds: CampaignAutonomyBounds;
  confirmed: true;
}

export interface CampaignAutonomyApproval {
  /** @minimum 1 */
  policyVersion: number;
  bounds: CampaignAutonomyBounds;
  autonomousExecution: true;
}

export type CampaignAutonomyChangeOperation = typeof CampaignAutonomyChangeOperation[keyof typeof CampaignAutonomyChangeOperation];


export const CampaignAutonomyChangeOperation = {
  validate: 'validate',
  enable: 'enable',
  disable: 'disable',
  approve: 'approve',
  revoke: 'revoke',
} as const;

export type CampaignAutonomyChangeFields = CampaignAutonomyValidation | CampaignAutonomyEnable | CampaignAutonomyApproval | { [key: string]: unknown };

export interface CampaignAutonomyChange {
  operation: CampaignAutonomyChangeOperation;
  campaignId?: string;
  /** @minimum 0 */
  expectedVersion: number;
  fields: CampaignAutonomyChangeFields;
  /**
     * @minLength 3
     * @maxLength 2000
     */
  reason: string;
}

/**
 * @nullable
 */
export type CampaignAutonomyStatePolicy = { [key: string]: unknown } | null;

/**
 * @nullable
 */
export type CampaignAutonomyStateValidation = { [key: string]: unknown } | null;

/**
 * @nullable
 */
export type CampaignAutonomyStateApproval = { [key: string]: unknown } | null;

export interface CampaignAutonomyState {
  policyVersion: number;
  validationVersion: number;
  approvalVersion: number;
  enabled: boolean;
  authorized: boolean;
  reasons: string[];
  /** @nullable */
  policy: CampaignAutonomyStatePolicy;
  /** @nullable */
  validation: CampaignAutonomyStateValidation;
  /** @nullable */
  approval: CampaignAutonomyStateApproval;
}

export interface AgreementDocumentLink {
  id: string;
  versionId: string;
  /**
     * @minLength 64
     * @maxLength 64
     */
  revision: string;
}

export type AgreementFieldsType = typeof AgreementFieldsType[keyof typeof AgreementFieldsType];


export const AgreementFieldsType = {
  nda: 'nda',
  msa: 'msa',
  partner: 'partner',
  referral: 'referral',
  other: 'other',
} as const;

export type AgreementFieldsStatus = typeof AgreementFieldsStatus[keyof typeof AgreementFieldsStatus];


export const AgreementFieldsStatus = {
  draft: 'draft',
  pending: 'pending',
  active: 'active',
  expired: 'expired',
  terminated: 'terminated',
} as const;

export interface AgreementFields {
  accountId?: string;
  /** @nullable */
  opportunityId?: string | null;
  /** @nullable */
  partyContactId?: string | null;
  /**
     * @maxLength 2000
     * @nullable
     */
  parties?: string | null;
  /** @nullable */
  ownerId?: string | null;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
  type?: AgreementFieldsType;
  status?: AgreementFieldsStatus;
  /** @nullable */
  signedOn?: string | null;
  /** @nullable */
  expiresOn?: string | null;
  /**
     * @maxLength 10000
     * @nullable
     */
  terms?: string | null;
  /**
     * @maxLength 10000
     * @nullable
     */
  notes?: string | null;
  /** @maxItems 30 */
  documents?: AgreementDocumentLink[];
  agreementId?: string;
  /**
     * @minLength 1
     * @maxLength 5000
     */
  description?: string;
  /** @nullable */
  dueOn?: string | null;
  completed?: boolean;
  /** @nullable */
  completedOn?: string | null;
}

export type AgreementChangeResource = typeof AgreementChangeResource[keyof typeof AgreementChangeResource];


export const AgreementChangeResource = {
  agreement: 'agreement',
  obligation: 'obligation',
} as const;

export type AgreementChangeSourceKind = typeof AgreementChangeSourceKind[keyof typeof AgreementChangeSourceKind];


export const AgreementChangeSourceKind = {
  partner: 'partner',
  general: 'general',
} as const;

export type AgreementChangeOperation = typeof AgreementChangeOperation[keyof typeof AgreementChangeOperation];


export const AgreementChangeOperation = {
  create: 'create',
  update: 'update',
  complete: 'complete',
  reopen: 'reopen',
} as const;

export interface AgreementChange {
  resource: AgreementChangeResource;
  sourceKind: AgreementChangeSourceKind;
  operation: AgreementChangeOperation;
  id?: string;
  /**
     * @minLength 64
     * @maxLength 64
     */
  expectedVersion?: string;
  fields?: AgreementFields;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
}

export type ForecastBucketOutcome = typeof ForecastBucketOutcome[keyof typeof ForecastBucketOutcome];


export const ForecastBucketOutcome = {
  open: 'open',
  won: 'won',
  lost: 'lost',
} as const;

export interface ForecastBucket {
  groupKey: string;
  /** @nullable */
  currency: string | null;
  outcome: ForecastBucketOutcome;
  count: number;
  /**
     * Exact decimal sum of valid nonnegative values with known currency
     * @nullable
     */
  gross: string | null;
  /**
     * Exact decimal sum of value times probability divided by 100 for valid inputs and known currency
     * @nullable
     */
  weighted: string | null;
  grossCount: number;
  weightedCount: number;
  missingValue: number;
  missingProbability: number;
  missingCurrency: number;
  missingCloseDate: number;
  missingOwner: number;
}

export type ForecastOpportunityOutcome = typeof ForecastOpportunityOutcome[keyof typeof ForecastOpportunityOutcome];


export const ForecastOpportunityOutcome = {
  open: 'open',
  won: 'won',
  lost: 'lost',
} as const;

export interface ForecastOpportunity {
  id: string;
  title: string;
  pipeline: string;
  stage: string;
  outcome: ForecastOpportunityOutcome;
  /** @nullable */
  ownerId: string | null;
  /** @nullable */
  ownerName: string | null;
  /** @nullable */
  currency: string | null;
  /** @nullable */
  value: string | null;
  /** @nullable */
  probability: number | null;
  /** @nullable */
  weighted: string | null;
  /** @nullable */
  expectedCloseDate: string | null;
  groupKey: string;
  link: string;
}

export interface OpportunityForecast {
  asOf: string;
  calculation: string;
  total: number;
  limit: number;
  offset: number;
  groupTotal: number;
  groupLimit: number;
  groupOffset: number;
  totals: ForecastBucket[];
  groups: ForecastBucket[];
  items: ForecastOpportunity[];
}

export type AgreementRegisterRecordResource = typeof AgreementRegisterRecordResource[keyof typeof AgreementRegisterRecordResource];


export const AgreementRegisterRecordResource = {
  agreement: 'agreement',
  obligation: 'obligation',
} as const;

export type AgreementRegisterRecordSourceKind = typeof AgreementRegisterRecordSourceKind[keyof typeof AgreementRegisterRecordSourceKind];


export const AgreementRegisterRecordSourceKind = {
  partner: 'partner',
  general: 'general',
} as const;

export interface AgreementRegisterRecord {
  id: string;
  resource: AgreementRegisterRecordResource;
  sourceKind: AgreementRegisterRecordSourceKind;
  version: string;
  fields: AgreementFields;
  unavailableDocuments: number;
}

export interface AgreementRegisterPage {
  items: AgreementRegisterRecord[];
  total: number;
  limit: number;
  offset: number;
}

export type AgreementHistoryPageItemsItem = {
  id: string;
  actorId: string;
  reason: string;
  createdAt: string;
  snapshot: AgreementRegisterRecord;
};

export interface AgreementHistoryPage {
  items: AgreementHistoryPageItemsItem[];
  total: number;
  limit: number;
  offset: number;
}

export type AgreementChoicePageItemsItem = {
  id: string;
  label: string;
  revision: string;
  versionId?: string;
};

export interface AgreementChoicePage {
  items: AgreementChoicePageItemsItem[];
  total: number;
  limit: number;
  offset: number;
}

export type AgreementRegisterReviewStatus = typeof AgreementRegisterReviewStatus[keyof typeof AgreementRegisterReviewStatus];


export const AgreementRegisterReviewStatus = {
  reviewed: 'reviewed',
  committed: 'committed',
  cancelled: 'cancelled',
} as const;

export interface AgreementRegisterReview {
  id: string;
  hash: string;
  expiresAt: string;
  status: AgreementRegisterReviewStatus;
  change: AgreementChange;
  before?: AgreementRegisterRecord | null;
  after: AgreementFields;
  receipt?: AgreementRegisterRecord | null;
}

/**
 * @nullable
 */
export type CrmStakeholderFieldsInfluence = typeof CrmStakeholderFieldsInfluence[keyof typeof CrmStakeholderFieldsInfluence] | null;


export const CrmStakeholderFieldsInfluence = {
  low: 'low',
  medium: 'medium',
  high: 'high',
} as const;

export interface CrmStakeholderFields {
  /** @nullable */
  contextAccountId?: string | null;
  /** @nullable */
  contextOpportunityId?: string | null;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  relatedAccountId?: string | null;
  /** @nullable */
  relatedContactId?: string | null;
  /**
     * @minLength 1
     * @maxLength 120
     */
  role?: string;
  /** @nullable */
  influence?: CrmStakeholderFieldsInfluence;
  /**
     * @maxLength 120
     * @nullable
     */
  stance?: string | null;
  /**
     * @maxLength 120
     * @nullable
     */
  relationshipLabel?: string | null;
  /** @maxLength 10000 */
  notes?: string;
  /** @nullable */
  reviewOn?: string | null;
}

export type CrmStakeholder = CrmStakeholderFields & {
  id: string;
  /** @minimum 1 */
  version: number;
  removed: boolean;
  updatedAt: string;
  updatedBy: string;
} & Required<Pick<CrmStakeholderFields & {
  id: string;
  /** @minimum 1 */
  version: number;
  removed: boolean;
  updatedAt: string;
  updatedBy: string;
}, 'role' | 'notes'>>;

export type CrmStakeholderChangeOperation = typeof CrmStakeholderChangeOperation[keyof typeof CrmStakeholderChangeOperation];


export const CrmStakeholderChangeOperation = {
  create: 'create',
  update: 'update',
  remove: 'remove',
} as const;

export interface CrmStakeholderChange {
  operation: CrmStakeholderChangeOperation;
  id?: string;
  /** @minimum 1 */
  expectedVersion?: number;
  fields?: CrmStakeholderFields;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
}

export type CrmStakeholderReviewStatus = typeof CrmStakeholderReviewStatus[keyof typeof CrmStakeholderReviewStatus];


export const CrmStakeholderReviewStatus = {
  reviewed: 'reviewed',
  committed: 'committed',
  cancelled: 'cancelled',
} as const;

export interface CrmStakeholderReview {
  id: string;
  hash: string;
  expiresAt: string;
  change: CrmStakeholderChange;
  before: CrmStakeholder | null;
  after: CrmStakeholderFields;
  status: CrmStakeholderReviewStatus;
  receipt: CrmStakeholder | null;
}

export interface CrmStakeholderPage {
  items: CrmStakeholder[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export type CrmStakeholderHistoryPageItemsItem = {
  id: string;
  operation: string;
  reason: string;
  snapshot: CrmStakeholder;
  actorId: string;
  createdAt: string;
};

export interface CrmStakeholderHistoryPage {
  items: CrmStakeholderHistoryPageItemsItem[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export type CrmStakeholderChoicePageItemsItem = {
  id: string;
  label: string;
};

export interface CrmStakeholderChoicePage {
  items: CrmStakeholderChoicePageItemsItem[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export interface CrmReferralFields {
  /** @nullable */
  sourceAccountId?: string | null;
  /** @nullable */
  sourceContactId?: string | null;
  /**
     * @maxLength 500
     * @nullable
     */
  partyText?: string | null;
  /** @nullable */
  targetAccountId?: string | null;
  /** @nullable */
  targetContactId?: string | null;
  referredOn?: string;
  /** @nullable */
  ownerId?: string | null;
  /** @maxLength 10000 */
  notes?: string;
  /**
     * @minLength 1
     * @maxLength 120
     */
  outcome?: string;
  /** @nullable */
  enquiryId?: string | null;
  /** @nullable */
  opportunityId?: string | null;
}

export type CrmReferral = CrmReferralFields & {
  id: string;
  /** @minimum 1 */
  version: number;
  updatedAt: string;
  updatedBy: string;
} & Required<Pick<CrmReferralFields & {
  id: string;
  /** @minimum 1 */
  version: number;
  updatedAt: string;
  updatedBy: string;
}, 'referredOn' | 'outcome' | 'notes'>>;

export interface CrmReferralPage {
  items: CrmReferral[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export type CrmReferralTargetAccountRelationshipType = typeof CrmReferralTargetAccountRelationshipType[keyof typeof CrmReferralTargetAccountRelationshipType];


export const CrmReferralTargetAccountRelationshipType = {
  b2b_customer: 'b2b_customer',
  b2c_consumer: 'b2c_consumer',
  partner: 'partner',
  supplier: 'supplier',
  investor: 'investor',
  prospect: 'prospect',
  other: 'other',
} as const;

export type CrmReferralTargetAccountAccountKind = typeof CrmReferralTargetAccountAccountKind[keyof typeof CrmReferralTargetAccountAccountKind];


export const CrmReferralTargetAccountAccountKind = {
  organization: 'organization',
  person: 'person',
} as const;

export interface CrmReferralTargetAccount {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  relationshipType: CrmReferralTargetAccountRelationshipType;
  accountKind: CrmReferralTargetAccountAccountKind;
}

export type CrmReferralChangeOperation = typeof CrmReferralChangeOperation[keyof typeof CrmReferralChangeOperation];


export const CrmReferralChangeOperation = {
  create: 'create',
  update: 'update',
  create_target_account: 'create_target_account',
} as const;

export interface CrmReferralChange {
  operation: CrmReferralChangeOperation;
  id?: string;
  /** @minimum 1 */
  expectedVersion?: number;
  fields?: CrmReferralFields;
  account?: CrmReferralTargetAccount;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
}

export type CrmReferralReviewStatus = typeof CrmReferralReviewStatus[keyof typeof CrmReferralReviewStatus];


export const CrmReferralReviewStatus = {
  reviewed: 'reviewed',
  committed: 'committed',
  cancelled: 'cancelled',
} as const;

export interface CrmReferralReview {
  id: string;
  hash: string;
  expiresAt: string;
  change: CrmReferralChange;
  before: CrmReferral | null;
  after: CrmReferralFields;
  status: CrmReferralReviewStatus;
  receipt: CrmReferral | null;
}

export type CrmReferralHistoryPageItemsItem = {
  id: string;
  operation: string;
  reason: string;
  snapshot: CrmReferral;
  actorId: string;
  createdAt: string;
};

export interface CrmReferralHistoryPage {
  items: CrmReferralHistoryPageItemsItem[];
  total: number;
  limit: number;
  offset: number;
}

export type CrmReferralChoicePageItemsItem = {
  id: string;
  label: string;
};

export interface CrmReferralChoicePage {
  items: CrmReferralChoicePageItemsItem[];
  total: number;
  limit: number;
  offset: number;
}

export type NativeWordProposalState = typeof NativeWordProposalState[keyof typeof NativeWordProposalState];


export const NativeWordProposalState = {
  attempted: 'attempted',
  pending: 'pending',
  accepted: 'accepted',
  rejected: 'rejected',
  failed: 'failed',
  uncertain: 'uncertain',
} as const;

export type NativeWordProposalApprovalState = typeof NativeWordProposalApprovalState[keyof typeof NativeWordProposalApprovalState];


export const NativeWordProposalApprovalState = {
  review_only: 'review_only',
} as const;

export type SharePointWordItemKind = typeof SharePointWordItemKind[keyof typeof SharePointWordItemKind];


export const SharePointWordItemKind = {
  folder: 'folder',
  word: 'word',
} as const;

export interface SharePointWordItem {
  id: string;
  name: string;
  webUrl: string;
  /**
     * @minimum 0
     * @maximum 20971520
     */
  size: number;
  /** @nullable */
  eTag: string | null;
  /** @nullable */
  cTag: string | null;
  kind: SharePointWordItemKind;
  siteId: string;
  driveId: string;
}

export interface SharePointWordSnapshot {
  entryId: string;
  fileId: string;
  source: SharePointWordItem;
  /** @nullable */
  versionId: string | null;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
  capturedAt: string;
  bindingFingerprint: string;
  destinationId: string;
  connectionId: string;
  actorAppUserId: string;
}

export type NativeWordPatchOperationsItem = {
  /** @maxLength 100 */
  targetId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  expectedHash: string;
  /** @maxLength 10000 */
  expectedText: string;
  /** @maxLength 10000 */
  replacementText: string;
};

export interface NativeWordPatch {
  /** @pattern ^[a-f0-9]{64}$ */
  sourceHash: string;
  /** @maxItems 50 */
  operations: NativeWordPatchOperationsItem[];
}

export type NativeWordCandidatePreservedPartsItem = {
  /** @maxLength 500 */
  name: string;
  /** @pattern ^[a-f0-9]{64}$ */
  hash: string;
};

export interface NativeWordCandidate {
  /** @pattern ^[a-f0-9]{64}$ */
  sourceHash: string;
  /** @pattern ^[a-f0-9]{64}$ */
  candidateHash: string;
  /**
     * @maxItems 50
     * @items.maxLength 500
     */
  changedParts: string[];
  /** @maxItems 2000 */
  preservedParts: NativeWordCandidatePreservedPartsItem[];
  /**
     * @maxItems 30
     * @items.maxLength 1000
     */
  warnings: string[];
  /**
     * @maxItems 50
     * @items.maxLength 100
     */
  changedTargets: string[];
}

export type NativeWordTargetSensitiveItem = typeof NativeWordTargetSensitiveItem[keyof typeof NativeWordTargetSensitiveItem];


export const NativeWordTargetSensitiveItem = {
  legal: 'legal',
  commercial: 'commercial',
} as const;

export interface NativeWordTarget {
  /** @maxLength 100 */
  id: string;
  /** @maxLength 500 */
  part: string;
  /** @minimum 0 */
  paragraph: number;
  /** @maxLength 10000 */
  text: string;
  /** @pattern ^[a-f0-9]{64}$ */
  textHash: string;
  editable: boolean;
  /**
     * @maxItems 50
     * @items.maxLength 100
     */
  blockers: string[];
  inTable: boolean;
  /** @maxItems 2 */
  sensitive: NativeWordTargetSensitiveItem[];
}

export interface NativeWordProposal {
  id: string;
  clientRequestId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  requestHash: string;
  /** @minimum 1 */
  revision: number;
  state: NativeWordProposalState;
  source: SharePointWordSnapshot;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  instruction: string;
  /**
     * @minItems 1
     * @maxItems 50
     * @items.maxLength 100
     */
  targetIds: string[];
  /** @pattern ^[a-f0-9]{64}$ */
  policyHash: string;
  /** @maxLength 200 */
  model: string;
  /** @maxLength 100 */
  instructionVersion: string;
  patch: NativeWordPatch | null;
  candidate: NativeWordCandidate | null;
  /** @maxItems 50 */
  sensitiveTargets: NativeWordTarget[];
  /**
     * @maxItems 30
     * @items.maxLength 1000
     */
  warnings: string[];
  createdAt: string;
  /** @nullable */
  decidedAt: string | null;
  reviewAcknowledged: boolean;
  approvalState: NativeWordProposalApprovalState;
}

export interface NativeWordProposalInput {
  clientRequestId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  sourceHash: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  instruction: string;
  /**
     * @minItems 1
     * @maxItems 50
     * @items.maxLength 100
     */
  targetIds: string[];
}

export type NativeWordProposalDecisionDecision = typeof NativeWordProposalDecisionDecision[keyof typeof NativeWordProposalDecisionDecision];


export const NativeWordProposalDecisionDecision = {
  accepted: 'accepted',
  rejected: 'rejected',
} as const;

export interface NativeWordProposalDecision {
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @nullable
     * @pattern ^[a-f0-9]{64}$
     */
  candidateHash: string | null;
  decision: NativeWordProposalDecisionDecision;
  acknowledgeReview: boolean;
}

export interface NativeWordProposalPage {
  /** @maxItems 20 */
  items: NativeWordProposal[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export type NativeWordInspectionPartsItem = {
  /** @maxLength 500 */
  name: string;
  /** @pattern ^[a-f0-9]{64}$ */
  hash: string;
};

export interface NativeWordInspection {
  /** @pattern ^[a-f0-9]{64}$ */
  sourceHash: string;
  /** @maxItems 100 */
  targets: NativeWordTarget[];
  /**
     * @maxItems 30
     * @items.maxLength 1000
     */
  warnings: string[];
  editBlocked: boolean;
  /** @maxItems 2000 */
  parts: NativeWordInspectionPartsItem[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /**
     * @minimum 0
     * @maximum 20000
     */
  offset: number;
}

export type NativeWordSaveState = typeof NativeWordSaveState[keyof typeof NativeWordSaveState];


export const NativeWordSaveState = {
  writing: 'writing',
  verified: 'verified',
  blocked: 'blocked',
  conflict: 'conflict',
  uncertain: 'uncertain',
} as const;

export type NativeWordSaveEventsItemState = typeof NativeWordSaveEventsItemState[keyof typeof NativeWordSaveEventsItemState];


export const NativeWordSaveEventsItemState = {
  writing: 'writing',
  verified: 'verified',
  blocked: 'blocked',
  conflict: 'conflict',
  uncertain: 'uncertain',
} as const;

export type NativeWordSaveEventsItem = {
  state: NativeWordSaveEventsItemState;
  at: string;
  /** @maxLength 1000 */
  message: string;
};

export interface NativeWordSave {
  id: string;
  clientRequestId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  requestHash: string;
  proposalId: string;
  /** @minimum 1 */
  proposalRevision: number;
  /** @pattern ^[a-f0-9]{64}$ */
  candidateHash: string;
  source: SharePointWordSnapshot;
  state: NativeWordSaveState;
  /** @minimum 1 */
  revision: number;
  /** @maxLength 1000 */
  message: string;
  createdAt: string;
  /** @nullable */
  leaseUntil: string | null;
  /**
     * @maxLength 500
     * @nullable
     */
  resultETag: string | null;
  /**
     * @maxLength 500
     * @nullable
     */
  resultVersion: string | null;
  /** @nullable */
  verifiedAt: string | null;
  /** @maxItems 100 */
  events: NativeWordSaveEventsItem[];
}

export interface NativeWordSaveInput {
  clientRequestId: string;
  proposalId: string;
  /** @minimum 1 */
  expectedProposalRevision: number;
  /** @pattern ^[a-f0-9]{64}$ */
  candidateHash: string;
  acknowledgeOriginalSave: true;
}

export interface NativeWordSavePage {
  /** @maxItems 20 */
  items: NativeWordSave[];
  total: number;
  limit: number;
  offset: number;
}

export interface SharePointWordScopeInput {
  destinationId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  bindingFingerprint: string;
}

export type SharePointWordBrowseInput = SharePointWordScopeInput & {
  /**
     * @minLength 1
     * @maxLength 500
     */
  folderId?: string;
  /**
     * @minLength 1
     * @maxLength 6000
     */
  cursor?: string;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit?: number;
};

export type SharePointWordResolveInput = SharePointWordScopeInput & {
  /**
     * @minLength 1
     * @maxLength 2048
     */
  url: string;
};

export type SharePointWordSnapshotInput = SharePointWordScopeInput & {
  /**
     * @minLength 1
     * @maxLength 500
     */
  itemId: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  expectedETag: string;
};

export interface SharePointWordPage {
  items: SharePointWordItem[];
  /** @nullable */
  nextCursor: string | null;
}

export type GovernmentOverviewEntityOwnersItem = {
  id: string;
  name: string;
  /** @minimum 0 */
  count: number;
};

export type GovernmentOverviewEntityCoverageOrigin = typeof GovernmentOverviewEntityCoverageOrigin[keyof typeof GovernmentOverviewEntityCoverageOrigin];


export const GovernmentOverviewEntityCoverageOrigin = {
  setup_required: 'setup_required',
  tenant: 'tenant',
  entity: 'entity',
} as const;

export type GovernmentOverviewEntityCoverageState = typeof GovernmentOverviewEntityCoverageState[keyof typeof GovernmentOverviewEntityCoverageState];


export const GovernmentOverviewEntityCoverageState = {
  setup_required: 'setup_required',
  partial: 'partial',
  unavailable: 'unavailable',
} as const;

export interface GovernmentOverviewEntity {
  entityId: string;
  entityName: string;
  /** @minimum 0 */
  matches: number;
  /** @minimum 0 */
  pursuits: number;
  /** @minimum 0 */
  unassigned: number;
  /** @maxItems 20 */
  owners: GovernmentOverviewEntityOwnersItem[];
  /** @minimum 0 */
  ownerTotal: number;
  /** @nullable */
  nextProgramDeadlineAt: string | null;
  /** @nullable */
  nextStaffTargetAt: string | null;
  coverageOrigin: GovernmentOverviewEntityCoverageOrigin;
  /** @minimum 0 */
  coverageGaps: number;
  coverageState: GovernmentOverviewEntityCoverageState;
}

export type GovernmentTenantOverviewTotals = {
  /** @minimum 0 */
  entities: number;
  /** @minimum 0 */
  matches: number;
  /** @minimum 0 */
  pursuits: number;
  /** @minimum 0 */
  coverageGaps: number;
};

/**
 * tender marks a public tender opportunity mirrored from Tender opportunities.
 */
export type GovernmentCategory = typeof GovernmentCategory[keyof typeof GovernmentCategory];


export const GovernmentCategory = {
  supplier_panel: 'supplier_panel',
  supplier_register: 'supplier_register',
  grant: 'grant',
  rebate: 'rebate',
  subsidy: 'subsidy',
  business_support: 'business_support',
  tax_incentive: 'tax_incentive',
  tender: 'tender',
} as const;

export type GovernmentTenantOverviewFacetsCategoriesItem = {
  value: GovernmentCategory;
  /** @minimum 0 */
  count: number;
};

export type GovernmentPursuitProgress = typeof GovernmentPursuitProgress[keyof typeof GovernmentPursuitProgress];


export const GovernmentPursuitProgress = {
  interested: 'interested',
  assessing: 'assessing',
  preparing: 'preparing',
  submitted: 'submitted',
  active_awarded: 'active_awarded',
  unsuccessful: 'unsuccessful',
  withdrawn: 'withdrawn',
  expired: 'expired',
} as const;

export type GovernmentTenantOverviewFacetsProgressItem = {
  value: GovernmentPursuitProgress;
  /** @minimum 0 */
  count: number;
};

export type GovernmentTenantOverviewFacets = {
  /** @maxItems 7 */
  categories: GovernmentTenantOverviewFacetsCategoriesItem[];
  /** @maxItems 8 */
  progress: GovernmentTenantOverviewFacetsProgressItem[];
};

export interface GovernmentTenantOverview {
  tenantId: string;
  /** @maxItems 50 */
  items: GovernmentOverviewEntity[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
  totals: GovernmentTenantOverviewTotals;
  facets: GovernmentTenantOverviewFacets;
  /** @maxItems 10 */
  warnings: string[];
}

export type GovernmentSourceConfigurationJurisdictionLevel = typeof GovernmentSourceConfigurationJurisdictionLevel[keyof typeof GovernmentSourceConfigurationJurisdictionLevel];


export const GovernmentSourceConfigurationJurisdictionLevel = {
  federal: 'federal',
  state: 'state',
  local: 'local',
} as const;

export type GovernmentState = typeof GovernmentState[keyof typeof GovernmentState];


export const GovernmentState = {
  ACT: 'ACT',
  NSW: 'NSW',
  NT: 'NT',
  QLD: 'QLD',
  SA: 'SA',
  TAS: 'TAS',
  VIC: 'VIC',
  WA: 'WA',
} as const;

export interface GovernmentLocalJurisdiction {
  /**
     * @minLength 1
     * @maxLength 100
     * @pattern ^[a-zA-Z0-9][a-zA-Z0-9._:-]*$
     */
  id: string;
  state: GovernmentState;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
}

export interface GovernmentSourceConfiguration {
  /**
     * @minLength 1
     * @maxLength 500
     */
  administeringBody: string;
  jurisdictionLevel: GovernmentSourceConfigurationJurisdictionLevel;
  state: GovernmentState | null;
  localJurisdiction: GovernmentLocalJurisdiction | null;
  /**
     * @minItems 1
     * @maxItems 8
     */
  categories: GovernmentCategory[];
}

export type GovernmentSourceSuggestionVerification = typeof GovernmentSourceSuggestionVerification[keyof typeof GovernmentSourceSuggestionVerification];


export const GovernmentSourceSuggestionVerification = {
  accessible: 'accessible',
  entry_point_only: 'entry_point_only',
} as const;

export interface GovernmentSourceSuggestion {
  /** @maxLength 200 */
  key: string;
  /** @maxLength 500 */
  name: string;
  url: string;
  configuration: GovernmentSourceConfiguration;
  verification: GovernmentSourceSuggestionVerification;
  /** @maxLength 2000 */
  note: string;
  /**
     * Built-in tender portal connector that can monitor this source from Web Signals tender portals.
     * @maxLength 80
     * @nullable
     */
  connectorKey?: string | null;
}

export interface GovernmentSourceSuggestions {
  /** @maxItems 30 */
  items: GovernmentSourceSuggestion[];
  /**
     * @maxItems 100
     * @items.maxLength 1000
     */
  gaps: string[];
}

export type GovernmentFindingFreshness = typeof GovernmentFindingFreshness[keyof typeof GovernmentFindingFreshness];


export const GovernmentFindingFreshness = {
  current: 'current',
  stale: 'stale',
  expired: 'expired',
} as const;

export type GovernmentProgramObservationJurisdictionLevel = typeof GovernmentProgramObservationJurisdictionLevel[keyof typeof GovernmentProgramObservationJurisdictionLevel];


export const GovernmentProgramObservationJurisdictionLevel = {
  federal: 'federal',
  state: 'state',
  local: 'local',
} as const;

export type GovernmentProgramObservationApplicationStatus = typeof GovernmentProgramObservationApplicationStatus[keyof typeof GovernmentProgramObservationApplicationStatus];


export const GovernmentProgramObservationApplicationStatus = {
  unknown: 'unknown',
  upcoming: 'upcoming',
  open: 'open',
  closed: 'closed',
  ongoing: 'ongoing',
  suspended: 'suspended',
} as const;

export interface GovernmentEvidencePin {
  sourceId: string;
  /** @minimum 1 */
  sourceRevision: number;
  /** @nullable */
  recipeId: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  recipeRevision: number | null;
  /** @nullable */
  scanRunId: string | null;
  evidenceId: string;
  /** @minimum 1 */
  evidenceRevision: number;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
  /** @maxLength 2048 */
  url: string;
  retrievedAt: string;
}

export interface GovernmentCriterion {
  /**
     * @minLength 1
     * @maxLength 100
     */
  id: string;
  /**
     * @minLength 1
     * @maxLength 10000
     */
  text: string;
  /**
     * @minLength 1
     * @maxLength 10000
     */
  evidenceQuote: string;
  evidence: GovernmentEvidencePin;
}

export type GovernmentProgramObservationMetadataEvidenceItem = {
  /** @maxLength 100 */
  field: string;
  /**
     * @minLength 1
     * @maxLength 10000
     */
  quote: string;
};

export interface GovernmentProgramObservation {
  id: string;
  tenantId: string;
  ownerEntityId: string;
  candidateId: string;
  /** @minimum 1 */
  revision: number;
  /**
     * @maxLength 200
     * @nullable
     */
  officialProgramId: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  roundId: string | null;
  /** @pattern ^[a-f0-9]{64}$ */
  identityKey: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  administeringBody: string;
  /** @maxLength 2048 */
  officialUrl: string;
  category: GovernmentCategory;
  jurisdictionLevel: GovernmentProgramObservationJurisdictionLevel;
  state: GovernmentState | null;
  localJurisdiction: GovernmentLocalJurisdiction | null;
  applicationStatus: GovernmentProgramObservationApplicationStatus;
  /** @nullable */
  opensAt: string | null;
  /** @nullable */
  deadlineAt: string | null;
  /**
     * @maxLength 100
     * @nullable
     */
  timezone: string | null;
  /**
     * @maxLength 10000
     * @nullable
     */
  benefits: string | null;
  /** @maxItems 100 */
  criteria: GovernmentCriterion[];
  source: GovernmentEvidencePin;
  /** @nullable */
  publishedAt: string | null;
  /** @nullable */
  effectiveAt: string | null;
  /** @maxItems 30 */
  metadataEvidence: GovernmentProgramObservationMetadataEvidenceItem[];
  checkedAt: string;
}

export interface GovernmentProfilePin {
  /** @nullable */
  entityProfileId: string | null;
  /** @nullable */
  entityProfileUpdatedAt: string | null;
  /** @pattern ^[a-f0-9]{64}$ */
  profileHash: string;
  /** @nullable */
  targetingProfileId: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  targetingProfileRevision: number | null;
}

export type GovernmentAdministrationScope = typeof GovernmentAdministrationScope[keyof typeof GovernmentAdministrationScope];


export const GovernmentAdministrationScope = {
  tenant: 'tenant',
  entity: 'entity',
} as const;

export interface GovernmentConfigurationPin {
  scope: GovernmentAdministrationScope;
  /** @nullable */
  id: string | null;
  /** @minimum 0 */
  revision: number;
}

export interface GovernmentAssessmentPins {
  source: GovernmentEvidencePin;
  profile: GovernmentProfilePin;
  /** @pattern ^[a-f0-9]{64}$ */
  configurationFingerprint: string;
  /** @maxItems 2 */
  configurationRevisions: GovernmentConfigurationPin[];
}

export type GovernmentFitAssessmentRelevance = typeof GovernmentFitAssessmentRelevance[keyof typeof GovernmentFitAssessmentRelevance];


export const GovernmentFitAssessmentRelevance = {
  potential: 'potential',
  not_relevant: 'not_relevant',
  unknown: 'unknown',
} as const;

export type GovernmentCriterionFitStatus = typeof GovernmentCriterionFitStatus[keyof typeof GovernmentCriterionFitStatus];


export const GovernmentCriterionFitStatus = {
  supported: 'supported',
  unmet: 'unmet',
  unknown: 'unknown',
  conflicting: 'conflicting',
  expired: 'expired',
} as const;

export interface GovernmentCriterionFit {
  /** @maxLength 100 */
  criterionId: string;
  status: GovernmentCriterionFitStatus;
  /**
     * @minLength 1
     * @maxLength 10000
     */
  reason: string;
  /**
     * @maxItems 100
     * @items.maxLength 200
     */
  factKeys: string[];
  /** @maxItems 100 */
  citations: GovernmentEvidencePin[];
}

export interface GovernmentFitAssessment {
  id: string;
  tenantId: string;
  ownerEntityId: string;
  candidateId: string;
  observationId: string;
  /** @minimum 1 */
  revision: number;
  pins: GovernmentAssessmentPins;
  relevance: GovernmentFitAssessmentRelevance;
  /** @maxItems 100 */
  criteria: GovernmentCriterionFit[];
  /**
     * @maxItems 100
     * @items.maxLength 1000
     */
  missingFacts: string[];
  assessedAt: string;
  eligibilityDetermination: false;
}

export interface GovernmentFinding {
  observation: GovernmentProgramObservation;
  assessment: GovernmentFitAssessment | null;
  freshness: GovernmentFindingFreshness;
  /**
     * @maxItems 100
     * @items.maxLength 1000
     */
  warnings: string[];
}

export interface GovernmentFindingPage {
  /** @maxItems 50 */
  items: GovernmentFinding[];
  total: number;
  limit: number;
  offset: number;
}

export interface GovernmentCoverageSource {
  sourceId: string;
  /** @maxLength 500 */
  name: string;
  configuration: GovernmentSourceConfiguration;
  /** @maxLength 200 */
  status: string;
  /** @maxLength 200 */
  readiness: string;
  /**
     * @maxLength 200
     * @nullable
     */
  code: string | null;
  /** @nullable */
  lastRunAt: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  lastRunStatus: string | null;
  partial: boolean;
}

export type GovernmentDiscoveryCoverageProviderDispatch = typeof GovernmentDiscoveryCoverageProviderDispatch[keyof typeof GovernmentDiscoveryCoverageProviderDispatch];


export const GovernmentDiscoveryCoverageProviderDispatch = {
  not_used: 'not_used',
} as const;

export type GovernmentCoverageCountry = typeof GovernmentCoverageCountry[keyof typeof GovernmentCoverageCountry];


export const GovernmentCoverageCountry = {
  AU: 'AU',
} as const;

export type GovernmentCoverageLocalMode = typeof GovernmentCoverageLocalMode[keyof typeof GovernmentCoverageLocalMode];


export const GovernmentCoverageLocalMode = {
  none: 'none',
  all: 'all',
  selected: 'selected',
} as const;

export interface GovernmentCoverage {
  country: GovernmentCoverageCountry;
  federal: boolean;
  /** @maxItems 8 */
  states: GovernmentState[];
  localMode: GovernmentCoverageLocalMode;
  /** @maxItems 100 */
  localJurisdictions: GovernmentLocalJurisdiction[];
  /** @maxItems 8 */
  categories: GovernmentCategory[];
}

export type GovernmentJurisdictionViewOrigin = typeof GovernmentJurisdictionViewOrigin[keyof typeof GovernmentJurisdictionViewOrigin];


export const GovernmentJurisdictionViewOrigin = {
  setup_required: 'setup_required',
  tenant: 'tenant',
  entity: 'entity',
} as const;

export type GovernmentJurisdictionViewReadiness = typeof GovernmentJurisdictionViewReadiness[keyof typeof GovernmentJurisdictionViewReadiness];


export const GovernmentJurisdictionViewReadiness = {
  setup_required: 'setup_required',
  configured: 'configured',
} as const;

export interface GovernmentJurisdictionView {
  scope: GovernmentAdministrationScope;
  /** @minimum 0 */
  localRevision: number;
  /** @pattern ^[a-f0-9]{64}$ */
  fingerprint: string;
  /** @maxItems 2 */
  revisions: GovernmentConfigurationPin[];
  localCoverage: GovernmentCoverage | null;
  effectiveCoverage: GovernmentCoverage | null;
  origin: GovernmentJurisdictionViewOrigin;
  readiness: GovernmentJurisdictionViewReadiness;
}

export interface GovernmentDiscoveryCoverage {
  configuration: GovernmentJurisdictionView;
  /** @maxItems 50 */
  sources: GovernmentCoverageSource[];
  /**
     * @maxItems 100
     * @items.maxLength 1000
     */
  gaps: string[];
  exhaustive: false;
  providerDispatch: GovernmentDiscoveryCoverageProviderDispatch;
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface WriteGovernmentJurisdictions {
  /** @minimum 0 */
  expectedRevision: number;
  /** @pattern ^[a-f0-9]{64}$ */
  expectedFingerprint: string;
  coverage: GovernmentCoverage | null;
  /**
     * @minLength 10
     * @maxLength 2000
     */
  reason: string;
}

export type GovernmentProfileProposalFieldKey = typeof GovernmentProfileProposalFieldKey[keyof typeof GovernmentProfileProposalFieldKey];


export const GovernmentProfileProposalFieldKey = {
  businessName: 'businessName',
  description: 'description',
  industry: 'industry',
  websiteUrl: 'websiteUrl',
  locationText: 'locationText',
  services: 'services',
  markets: 'markets',
  qualificationFacts: 'qualificationFacts',
} as const;

export type GovernmentProfileProposalFieldAttribution = typeof GovernmentProfileProposalFieldAttribution[keyof typeof GovernmentProfileProposalFieldAttribution];


export const GovernmentProfileProposalFieldAttribution = {
  web_evidence: 'web_evidence',
  user_assertion: 'user_assertion',
  suggestion: 'suggestion',
  unknown: 'unknown',
} as const;

export interface GovernmentProfileProposalField {
  key: GovernmentProfileProposalFieldKey;
  existingValue: unknown;
  proposedValue: unknown;
  attribution: GovernmentProfileProposalFieldAttribution;
  /**
     * @maxLength 10000
     * @nullable
     */
  literalEvidence: string | null;
  /**
     * @maxLength 2048
     * @nullable
     */
  url: string | null;
  /** @nullable */
  retrievedAt: string | null;
  /**
     * @maxLength 2000
     * @nullable
     */
  uncertainty: string | null;
}

export type GovernmentProfileProposalStatus = typeof GovernmentProfileProposalStatus[keyof typeof GovernmentProfileProposalStatus];


export const GovernmentProfileProposalStatus = {
  pending: 'pending',
  ready: 'ready',
  failed: 'failed',
  setup_required: 'setup_required',
  adopted: 'adopted',
  rejected: 'rejected',
} as const;

export interface GovernmentProfileProposal {
  id: string;
  tenantId: string;
  ownerEntityId: string;
  /** @minimum 1 */
  revision: number;
  requestKey: string;
  profile: GovernmentProfilePin;
  status: GovernmentProfileProposalStatus;
  /**
     * @maxLength 2048
     * @nullable
     */
  websiteUrl: string | null;
  /**
     * @maxLength 20000
     * @nullable
     */
  prompt: string | null;
  /** @maxItems 50 */
  fields: GovernmentProfileProposalField[];
  /**
     * @maxItems 100
     * @items.maxLength 1000
     */
  missingFacts: string[];
  /**
     * @maxLength 2000
     * @nullable
     */
  failure: string | null;
  createdAt: string;
}

export interface GovernmentPursuitCreate {
  requestKey: string;
  observationId: string;
  assessmentId: string;
  /** @nullable */
  ownerAppUserId?: string | null;
}

export type GovernmentPursuitUpdateOutcome = typeof GovernmentPursuitUpdateOutcome[keyof typeof GovernmentPursuitUpdateOutcome];


export const GovernmentPursuitUpdateOutcome = {
  not_recorded: 'not_recorded',
  panel_membership: 'panel_membership',
  incentive_award: 'incentive_award',
  unsuccessful: 'unsuccessful',
  withdrawn: 'withdrawn',
} as const;

export interface GovernmentPursuitUpdate {
  requestKey: string;
  /** @minimum 1 */
  expectedRevision: number;
  /** @nullable */
  ownerAppUserId?: string | null;
  progress?: GovernmentPursuitProgress;
  /**
     * @maxLength 2048
     * @nullable
     */
  applicationUrl?: string | null;
  /** @nullable */
  staffTargetAt?: string | null;
  /** @maxLength 20000 */
  notes?: string;
  outcome?: GovernmentPursuitUpdateOutcome;
  /** @nullable */
  validFrom?: string | null;
  /** @nullable */
  validUntil?: string | null;
}

export type GovernmentPursuitOutcome = typeof GovernmentPursuitOutcome[keyof typeof GovernmentPursuitOutcome];


export const GovernmentPursuitOutcome = {
  not_recorded: 'not_recorded',
  panel_membership: 'panel_membership',
  incentive_award: 'incentive_award',
  unsuccessful: 'unsuccessful',
  withdrawn: 'withdrawn',
} as const;

export type GovernmentPursuitActivityTargetEntityType = typeof GovernmentPursuitActivityTargetEntityType[keyof typeof GovernmentPursuitActivityTargetEntityType];


export const GovernmentPursuitActivityTargetEntityType = {
  government_pursuit: 'government_pursuit',
} as const;

export interface GovernmentPursuitActivityTarget {
  entityType: GovernmentPursuitActivityTargetEntityType;
  entityId: string;
  ownerEntityId: string;
  tenantId: string;
}

export interface GovernmentPursuit {
  id: string;
  tenantId: string;
  ownerEntityId: string;
  candidateId: string;
  observationId: string;
  /** @nullable */
  assessmentId: string | null;
  /** @minimum 1 */
  revision: number;
  requestKey: string;
  pins: GovernmentAssessmentPins;
  /** @nullable */
  ownerAppUserId: string | null;
  progress: GovernmentPursuitProgress;
  /**
     * @maxLength 2048
     * @nullable
     */
  applicationUrl: string | null;
  /** @nullable */
  sourceDeadlineAt: string | null;
  /** @nullable */
  staffTargetAt: string | null;
  /** @maxLength 20000 */
  notes: string;
  outcome: GovernmentPursuitOutcome;
  /** @nullable */
  validFrom: string | null;
  /** @nullable */
  validUntil: string | null;
  startingFinding: GovernmentFinding;
  title: string;
  identityKey: string;
  category: GovernmentCategory;
  /** @nullable */
  ownerName: string | null;
  warnings: string[];
  createdAt: string;
  updatedAt: string;
  reviewRequired: boolean;
  activityTarget: GovernmentPursuitActivityTarget;
}

export interface GovernmentPursuitList {
  items: GovernmentPursuit[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export type TenderOpportunityStatus = typeof TenderOpportunityStatus[keyof typeof TenderOpportunityStatus];


export const TenderOpportunityStatus = {
  new: 'new',
  reviewing: 'reviewing',
  shortlisted: 'shortlisted',
  low_match: 'low_match',
  dismissed: 'dismissed',
  converted: 'converted',
  expired: 'expired',
} as const;

export type TenderOpportunityMatchBand = typeof TenderOpportunityMatchBand[keyof typeof TenderOpportunityMatchBand];


export const TenderOpportunityMatchBand = {
  strong: 'strong',
  possible: 'possible',
  low: 'low',
} as const;

export type TenderOpportunityRecommendation = typeof TenderOpportunityRecommendation[keyof typeof TenderOpportunityRecommendation];


export const TenderOpportunityRecommendation = {
  respond: 'respond',
  maybe: 'maybe',
  not_for_us: 'not_for_us',
} as const;

export type TenderOpportunityRankingMethod = typeof TenderOpportunityRankingMethod[keyof typeof TenderOpportunityRankingMethod];


export const TenderOpportunityRankingMethod = {
  prefilter: 'prefilter',
  ai: 'ai',
  keyword: 'keyword',
} as const;

/**
 * Statuses staff may set directly. Converted is set by the conversion flow and expired by the closing-date check.
 */
export type TenderOpportunityUserStatus = typeof TenderOpportunityUserStatus[keyof typeof TenderOpportunityUserStatus];


export const TenderOpportunityUserStatus = {
  new: 'new',
  reviewing: 'reviewing',
  shortlisted: 'shortlisted',
  low_match: 'low_match',
  dismissed: 'dismissed',
} as const;

/**
 * @nullable
 */
export type TenderOpportunitySuggestedProposalType = typeof TenderOpportunitySuggestedProposalType[keyof typeof TenderOpportunitySuggestedProposalType] | null;


export const TenderOpportunitySuggestedProposalType = {
  tender: 'tender',
  sow: 'sow',
} as const;

/**
 * How much to trust the ranking. Low when the AI service was unavailable and keyword scoring was used.
 * @nullable
 */
export type TenderOpportunityMatchConfidence = typeof TenderOpportunityMatchConfidence[keyof typeof TenderOpportunityMatchConfidence] | null;


export const TenderOpportunityMatchConfidence = {
  high: 'high',
  medium: 'medium',
  low: 'low',
} as const;

/**
 * Counts of the opportunity's documents by acquisition state, so lists can show whether documents are ready or need uploading.
 */
export interface TenderOpportunityDocumentSummary {
  /** @minimum 0 */
  total: number;
  /** @minimum 0 */
  acquired: number;
  /** @minimum 0 */
  pending: number;
  /** @minimum 0 */
  needsUpload: number;
  /** @minimum 0 */
  failed: number;
  /** @minimum 0 */
  autoAcquired: number;
}

export interface TenderOpportunity {
  id: string;
  portalKey: string;
  portalReference: string;
  canonicalUrl: string;
  title: string;
  /** @nullable */
  buyerName: string | null;
  /** @nullable */
  buyerAbn: string | null;
  /** @nullable */
  jurisdiction: string | null;
  categoryCodes: string[];
  /** @nullable */
  publishedAt: string | null;
  /** @nullable */
  closesAt: string | null;
  /** @nullable */
  briefingAt: string | null;
  /** @nullable */
  estimatedValue: number | null;
  summaryMd: string;
  status: TenderOpportunityStatus;
  /**
     * @minimum 0
     * @maximum 100
     * @nullable
     */
  matchScore: number | null;
  matchBand: TenderOpportunityMatchBand | null;
  matchReasons: string[];
  disqualifiers: string[];
  /** @nullable */
  suggestedProposalType: TenderOpportunitySuggestedProposalType;
  /**
     * How much to trust the ranking. Low when the AI service was unavailable and keyword scoring was used.
     * @nullable
     */
  matchConfidence: TenderOpportunityMatchConfidence;
  /** Bid/no-bid recommendation from the bid rules and Offerings Profile. People decide; Not for us is kept and only hidden by default. */
  recommendation?: TenderOpportunityRecommendation | null;
  /** prefilter when a bid rule filtered the tender without an AI call, ai for an AI assessment, keyword for the AI-unavailable fallback. */
  rankingMethod?: TenderOpportunityRankingMethod | null;
  /** @nullable */
  rankedAt: string | null;
  /** @nullable */
  assignedAppUserId: string | null;
  /** @nullable */
  assigneeName: string | null;
  /** @nullable */
  candidateId: string | null;
  /** @nullable */
  sourceId: string | null;
  /** @nullable */
  observationId: string | null;
  /** @nullable */
  assessmentId: string | null;
  /** @nullable */
  pursuitId: string | null;
  /** @nullable */
  tenderId: string | null;
  /** @nullable */
  pipelineOpportunityId: string | null;
  /**
     * Buyer account matched or created when the opportunity was converted.
     * @nullable
     */
  accountId: string | null;
  /** @nullable */
  convertedAt: string | null;
  seenByMe: boolean;
  /** A re-scan added an addendum that staff have not yet viewed. */
  hasNewAddenda: boolean;
  documentSummary: TenderOpportunityDocumentSummary;
  /** @minimum 1 */
  revision: number;
  firstSeenAt: string;
  lastSeenAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface TenderOpportunityList {
  items: TenderOpportunity[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export type TenderOpportunityDocumentKind = typeof TenderOpportunityDocumentKind[keyof typeof TenderOpportunityDocumentKind];


export const TenderOpportunityDocumentKind = {
  rfx: 'rfx',
  addendum: 'addendum',
  attachment: 'attachment',
  response_template: 'response_template',
  other: 'other',
} as const;

export type TenderOpportunityDocumentAcquisitionStatus = typeof TenderOpportunityDocumentAcquisitionStatus[keyof typeof TenderOpportunityDocumentAcquisitionStatus];


export const TenderOpportunityDocumentAcquisitionStatus = {
  pending: 'pending',
  acquired: 'acquired',
  needs_manual_upload: 'needs_manual_upload',
  failed: 'failed',
  duplicate: 'duplicate',
} as const;

/**
 * @nullable
 */
export type TenderOpportunityDocumentAcquiredVia = typeof TenderOpportunityDocumentAcquiredVia[keyof typeof TenderOpportunityDocumentAcquiredVia] | null;


export const TenderOpportunityDocumentAcquiredVia = {
  auto: 'auto',
  browser_worker: 'browser_worker',
  manual: 'manual',
} as const;

export type TenderOpportunityDocumentExtractedTextStatus = typeof TenderOpportunityDocumentExtractedTextStatus[keyof typeof TenderOpportunityDocumentExtractedTextStatus];


export const TenderOpportunityDocumentExtractedTextStatus = {
  not_started: 'not_started',
  pending: 'pending',
  ready: 'ready',
  failed: 'failed',
  unsupported: 'unsupported',
} as const;

export interface TenderOpportunityDocument {
  id: string;
  opportunityId: string;
  kind: TenderOpportunityDocumentKind;
  title: string;
  /** @nullable */
  sourceUrl: string | null;
  acquisitionStatus: TenderOpportunityDocumentAcquisitionStatus;
  /** @nullable */
  acquiredVia: TenderOpportunityDocumentAcquiredVia;
  /** @nullable */
  storageProvider: string | null;
  /** @nullable */
  contentHash: string | null;
  /** @nullable */
  sizeBytes: number | null;
  /** @nullable */
  contentType: string | null;
  /** @nullable */
  acquiredByAppUserId: string | null;
  /** @nullable */
  acquiredAt: string | null;
  extractedTextStatus: TenderOpportunityDocumentExtractedTextStatus;
  /** @nullable */
  filename: string | null;
  /**
     * Display name of the staff member who uploaded a manual document.
     * @nullable
     */
  acquiredByName: string | null;
  /**
     * Plain-language reason for the current acquisition state.
     * @nullable
     */
  acquisitionDetail: string | null;
  /** @minimum 0 */
  attemptCount: number;
  /** @nullable */
  duplicateOfDocumentId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TenderOpportunityDocumentList {
  items: TenderOpportunityDocument[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export type TenderOpportunityDocumentUploadKind = typeof TenderOpportunityDocumentUploadKind[keyof typeof TenderOpportunityDocumentUploadKind];


export const TenderOpportunityDocumentUploadKind = {
  rfx: 'rfx',
  addendum: 'addendum',
  attachment: 'attachment',
  response_template: 'response_template',
  other: 'other',
} as const;

export interface TenderOpportunityDocumentUpload {
  /**
     * @minLength 1
     * @maxLength 255
     */
  filename: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  contentType: string;
  /**
     * Base64-encoded file bytes.
     * @minLength 4
     * @maxLength 27962028
     */
  dataBase64: string;
  kind?: TenderOpportunityDocumentUploadKind;
  /**
     * @minLength 1
     * @maxLength 500
     */
  title?: string;
  /** Portal document this upload fulfils (for example one that needs sign-in to download). */
  documentId?: string;
}

export interface TenderOpportunityDocumentUploadResult {
  document: TenderOpportunityDocument;
  /** The same file was already held on this opportunity; the existing document is returned. */
  duplicate: boolean;
}

export type TenderOpportunityEventType = typeof TenderOpportunityEventType[keyof typeof TenderOpportunityEventType];


export const TenderOpportunityEventType = {
  discovered: 'discovered',
  updated: 'updated',
  ranked: 'ranked',
  re_ranked: 're_ranked',
  document_added: 'document_added',
  status_changed: 'status_changed',
  assigned: 'assigned',
  converted: 'converted',
  digest_sent: 'digest_sent',
  dismissed: 'dismissed',
  restored: 'restored',
  expired: 'expired',
} as const;

export type TenderOpportunityEventPayload = { [key: string]: unknown };

export interface TenderOpportunityEvent {
  id: string;
  opportunityId: string;
  type: TenderOpportunityEventType;
  /** @nullable */
  actorAppUserId: string | null;
  payload: TenderOpportunityEventPayload;
  createdAt: string;
}

export interface TenderOpportunityEventList {
  items: TenderOpportunityEvent[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export interface TenderOpportunityDetail {
  opportunity: TenderOpportunity;
  documents: TenderOpportunityDocument[];
  recentEvents: TenderOpportunityEvent[];
}

export interface TenderOpportunityUpdate {
  /** @minimum 1 */
  expectedRevision: number;
  status?: TenderOpportunityUserStatus;
  /** @nullable */
  assignedAppUserId?: string | null;
  /** @maxLength 2000 */
  note?: string;
}

export interface TenderOpportunityUnreadCount {
  /** @minimum 0 */
  count: number;
  /** @minimum 0 */
  strong: number;
  /** @minimum 0 */
  possible: number;
}

export interface BidActivityLink {
  tenderId: string;
  planRevisionId: string;
  requirementIds: string[];
  documentKeys: string[];
  documentIds: string[];
}

export type ActivityEntityType = typeof ActivityEntityType[keyof typeof ActivityEntityType];


export const ActivityEntityType = {
  account: 'account',
  contact: 'contact',
  enquiry: 'enquiry',
  opportunity: 'opportunity',
  government_pursuit: 'government_pursuit',
} as const;

export type ActivityType = typeof ActivityType[keyof typeof ActivityType];


export const ActivityType = {
  note: 'note',
  status_change: 'status_change',
  call: 'call',
  email: 'email',
  meeting: 'meeting',
  assignment: 'assignment',
  conversion: 'conversion',
} as const;

export interface Activity {
  /** @minimum 1 */
  version?: number;
  updatedAt?: string;
  bidLinks?: BidActivityLink[];
  /** When true, this update is shown to the customer in the portal. */
  visibleToCustomer: boolean;
  id: string;
  entityType: ActivityEntityType;
  entityId: string;
  type: ActivityType;
  /** @nullable */
  subject?: string | null;
  /** @nullable */
  status?: string | null;
  /** @nullable */
  priority?: string | null;
  /** @nullable */
  scheduledStartAt?: string | null;
  /** @nullable */
  scheduledEndAt?: string | null;
  /** @nullable */
  actualStartAt?: string | null;
  /** @nullable */
  actualEndAt?: string | null;
  /**
     * @minimum 0
     * @nullable
     */
  durationMinutes?: number | null;
  /** @nullable */
  ownerName?: string | null;
  /** @nullable */
  ownerId?: string | null;
  /** @nullable */
  participants?: unknown[] | null;
  /** @nullable */
  location?: string | null;
  /** @nullable */
  category?: string | null;
  /** @nullable */
  externalUrl?: string | null;
  isTestData?: boolean;
  body: string;
  createdAt: string;
}

export interface GovernmentPursuitEvent {
  id: string;
  pursuitId: string;
  revision: number;
  actorAppUserId: string;
  action: string;
  /** @nullable */
  activityId: string | null;
  activitySnapshot?: Activity | null;
  snapshot: GovernmentPursuit;
  createdAt: string;
}

export interface GovernmentPursuitHistory {
  items: GovernmentPursuitEvent[];
  total: number;
  limit: number;
  offset: number;
}

export interface GovernmentPursuitTaskCreate {
  requestKey: string;
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 5000
     */
  body: string;
  /** @nullable */
  ownerAppUserId: string | null;
  /** @nullable */
  dueAt: string | null;
}

export type GovernmentPursuitOwnerListItemsItem = {
  id: string;
  name: string;
};

export interface GovernmentPursuitOwnerList {
  items: GovernmentPursuitOwnerListItemsItem[];
  limit: number;
  offset: number;
  /** @nullable */
  nextOffset: number | null;
}

export type DocumentWorkspaceEvidenceRequestChannel = typeof DocumentWorkspaceEvidenceRequestChannel[keyof typeof DocumentWorkspaceEvidenceRequestChannel];


export const DocumentWorkspaceEvidenceRequestChannel = {
  agent: 'agent',
  quotes: 'quotes',
  guidance: 'guidance',
  content_generate: 'content_generate',
} as const;

export interface DocumentWorkspaceEvidenceRequest {
  expectedRevision: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  query: string;
  channel?: DocumentWorkspaceEvidenceRequestChannel;
  /**
     * @minimum 2000
     * @maximum 24000
     */
  maxEvidenceChars?: number;
  /**
     * @minimum 2
     * @maximum 24
     */
  maxPassages?: number;
  /**
     * Optional exact preview context fence. Consumers must review the returned immutable snapshot before dispatch.
     * @pattern ^[a-f0-9]{64}$
     */
  expectedContextFingerprint?: string;
}

export type CreateDocumentWorkspaceEvidenceSnapshotChannel = typeof CreateDocumentWorkspaceEvidenceSnapshotChannel[keyof typeof CreateDocumentWorkspaceEvidenceSnapshotChannel];


export const CreateDocumentWorkspaceEvidenceSnapshotChannel = {
  agent: 'agent',
  quotes: 'quotes',
  guidance: 'guidance',
  content_generate: 'content_generate',
} as const;

export interface CreateDocumentWorkspaceEvidenceSnapshot {
  expectedRevision: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  query: string;
  channel?: CreateDocumentWorkspaceEvidenceSnapshotChannel;
  /**
     * @minimum 2000
     * @maximum 24000
     */
  maxEvidenceChars?: number;
  /**
     * @minimum 2
     * @maximum 24
     */
  maxPassages?: number;
  requestId: string;
  /**
     * Optional exact preview context fence. Consumers must review the returned immutable snapshot before dispatch.
     * @pattern ^[a-f0-9]{64}$
     */
  expectedContextFingerprint?: string;
}

export type DocumentWorkspaceEvidencePassageKind = typeof DocumentWorkspaceEvidencePassageKind[keyof typeof DocumentWorkspaceEvidencePassageKind];


export const DocumentWorkspaceEvidencePassageKind = {
  workspace: 'workspace',
  knowledge: 'knowledge',
  crm: 'crm',
} as const;

export type DocumentWorkspaceEvidencePassageTier = typeof DocumentWorkspaceEvidencePassageTier[keyof typeof DocumentWorkspaceEvidencePassageTier];


export const DocumentWorkspaceEvidencePassageTier = {
  primary: 'primary',
  background: 'background',
} as const;

export type DocumentWorkspaceEvidencePassageReason = typeof DocumentWorkspaceEvidencePassageReason[keyof typeof DocumentWorkspaceEvidencePassageReason];


export const DocumentWorkspaceEvidencePassageReason = {
  automatic: 'automatic',
  explicit: 'explicit',
  entity_background: 'entity_background',
} as const;

/**
 * @nullable
 */
export type DocumentWorkspaceEvidencePassageRole = typeof DocumentWorkspaceEvidencePassageRole[keyof typeof DocumentWorkspaceEvidencePassageRole] | null;


export const DocumentWorkspaceEvidencePassageRole = {
  provided: 'provided',
  working: 'working',
  submission: 'submission',
} as const;

export interface DocumentWorkspaceEvidencePassage {
  id: string;
  /**
     * @minimum 1
     * @maximum 24
     */
  citation: number;
  kind: DocumentWorkspaceEvidencePassageKind;
  tier: DocumentWorkspaceEvidencePassageTier;
  reason: DocumentWorkspaceEvidencePassageReason;
  /** @nullable */
  role: DocumentWorkspaceEvidencePassageRole;
  /** @nullable */
  membershipId: string | null;
  /** @nullable */
  membershipRevision: string | null;
  /** @nullable */
  folderRevision: string | null;
  /** @nullable */
  sourceRevisionId: string | null;
  /** @nullable */
  extractionRevision: string | null;
  /** @nullable */
  libraryEntryId: string | null;
  /** @nullable */
  managedDocumentId: string | null;
  /** @nullable */
  fileId: string | null;
  /** @nullable */
  managedVersionId: string | null;
  /** @nullable */
  tenderSourceId: string | null;
  /** @nullable */
  knowledgeFileId: string | null;
  /** @nullable */
  indexDocumentId: string | null;
  /** @nullable */
  chunkId: string | null;
  /**
     * @minLength 1
     * @maxLength 150
     * @nullable
     */
  crmIdentity: string | null;
  /**
     * @minLength 1
     * @maxLength 200
     */
  sourceVersion: string;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
  /** @pattern ^[a-f0-9]{64}$ */
  excerptHash: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  label: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  location: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  excerpt: string;
  /**
     * @minimum 0
     * @maximum 1
     */
  score: number;
  /**
     * @minimum 0
     * @nullable
     */
  start: number | null;
  /**
     * @minimum 0
     * @nullable
     */
  end: number | null;
}

export type DocumentWorkspaceEvidenceStatusRole = typeof DocumentWorkspaceEvidenceStatusRole[keyof typeof DocumentWorkspaceEvidenceStatusRole];


export const DocumentWorkspaceEvidenceStatusRole = {
  provided: 'provided',
  working: 'working',
  submission: 'submission',
} as const;

export type DocumentWorkspaceEvidenceStatusState = typeof DocumentWorkspaceEvidenceStatusState[keyof typeof DocumentWorkspaceEvidenceStatusState];


export const DocumentWorkspaceEvidenceStatusState = {
  excluded: 'excluded',
  stored: 'stored',
  pending: 'pending',
  ready: 'ready',
  unsupported: 'unsupported',
  failed: 'failed',
  denied: 'denied',
  stale: 'stale',
  unavailable: 'unavailable',
  irrelevant: 'irrelevant',
  omitted_budget: 'omitted_budget',
} as const;

export interface DocumentWorkspaceEvidenceStatus {
  membershipId: string;
  role: DocumentWorkspaceEvidenceStatusRole;
  state: DocumentWorkspaceEvidenceStatusState;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  message: string;
}

export type DocumentWorkspaceEvidenceManifestChannel = typeof DocumentWorkspaceEvidenceManifestChannel[keyof typeof DocumentWorkspaceEvidenceManifestChannel];


export const DocumentWorkspaceEvidenceManifestChannel = {
  agent: 'agent',
  quotes: 'quotes',
  guidance: 'guidance',
  content_generate: 'content_generate',
} as const;

export type DocumentWorkspaceEvidenceManifestRetrievalMode = typeof DocumentWorkspaceEvidenceManifestRetrievalMode[keyof typeof DocumentWorkspaceEvidenceManifestRetrievalMode];


export const DocumentWorkspaceEvidenceManifestRetrievalMode = {
  lexical: 'lexical',
} as const;

export type DocumentWorkspaceEvidenceManifestState = typeof DocumentWorkspaceEvidenceManifestState[keyof typeof DocumentWorkspaceEvidenceManifestState];


export const DocumentWorkspaceEvidenceManifestState = {
  ready: 'ready',
  partial: 'partial',
  insufficient: 'insufficient',
  conflicting: 'conflicting',
  unavailable: 'unavailable',
} as const;

export interface DocumentWorkspaceEvidenceManifest {
  tenantId: string;
  entityId: string;
  workspaceId: string;
  workspaceRevision: string;
  /** @pattern ^[a-f0-9]{64}$ */
  contextFingerprint: string;
  /** @pattern ^[a-f0-9]{64}$ */
  queryHash: string;
  channel: DocumentWorkspaceEvidenceManifestChannel;
  retrievalMode: DocumentWorkspaceEvidenceManifestRetrievalMode;
  /**
     * @minimum 2000
     * @maximum 24000
     */
  maxEvidenceChars: number;
  /**
     * @minimum 2
     * @maximum 24
     */
  maxPassages: number;
  state: DocumentWorkspaceEvidenceManifestState;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  message: string;
  /**
     * @minLength 1
     * @maxLength 100
     */
  backgroundState: string;
  /** @maxItems 24 */
  passages: DocumentWorkspaceEvidencePassage[];
  /** @maxItems 100 */
  sources: DocumentWorkspaceEvidenceStatus[];
  /**
     * @maxItems 100
     * @items.minLength 1
     * @items.maxLength 1000
     */
  warnings: string[];
}

export interface DocumentWorkspaceEvidenceSnapshot {
  id: string;
  /** @pattern ^[a-f0-9]{64}$ */
  manifestHash: string;
  createdAt: string;
  manifest: DocumentWorkspaceEvidenceManifest;
}

export interface DocumentWorkspaceEvidenceCitation {
  snapshotId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  manifestHash: string;
  passage: DocumentWorkspaceEvidencePassage;
}

export interface DocumentWorkspaceEvidenceValidation {
  snapshotId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  manifestHash: string;
  valid: boolean;
}

export interface RemoveDocumentWorkspaceEvidenceSelection {
  expectedRevision: string;
  expectedMembershipRevision: string;
}

export interface DocumentWorkspaceMutation {
  expectedRevision: string;
}

/**
 * Explicit Markdown version replacement only. Requires exact native membership and original file/hash; preserves filename/MIME, validates fatal UTF-8 and the 2 MiB editing limit. Unsupported syntax remains source-editable. History, exact downloads and actor-private original recovery use the existing native version/upload operations; no publication transition.
 */
export type UploadDocumentWorkspaceFileMarkdownEdit = {
  expectedFileId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  expectedContentHash: string;
};

export interface UploadDocumentWorkspaceFile {
  /** Explicit Markdown version replacement only. Requires exact native membership and original file/hash; preserves filename/MIME, validates fatal UTF-8 and the 2 MiB editing limit. Unsupported syntax remains source-editable. History, exact downloads and actor-private original recovery use the existing native version/upload operations; no publication transition. */
  markdownEdit?: UploadDocumentWorkspaceFileMarkdownEdit;
  /** Reviewed evidence choice. New tender Working/Submission uploads default to included when omitted; replacement uploads and other workspaces preserve existing selections. Inclusion requires preparation and current access before grounding. */
  includeEvidence?: boolean;
  expectedRevision: string;
  requestId: string;
  folderId: string;
  /**
     * @minLength 1
     * @maxLength 255
     */
  filename: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  contentType: string;
  /**
     * @minLength 4
     * @maxLength 27962028
     */
  contentBase64: string;
  membershipId?: string;
  expectedMembershipRevision?: string;
  destinationId?: string;
  /** @pattern ^[a-f0-9]{64}$ */
  bindingFingerprint?: string;
}

export type LibraryCreationDestination = {
  kind: 'workspace';
  workspaceId: string;
  expectedRevision: string;
  folderId: string;
} | {
  kind: 'account';
  accountId: string;
};

export type ManagedDocumentContentType = typeof ManagedDocumentContentType[keyof typeof ManagedDocumentContentType];


export const ManagedDocumentContentType = {
  doc: 'doc',
} as const;

export type ManagedDocumentPageSettingsSize = typeof ManagedDocumentPageSettingsSize[keyof typeof ManagedDocumentPageSettingsSize];


export const ManagedDocumentPageSettingsSize = {
  a4: 'a4',
  letter: 'letter',
} as const;

export type ManagedDocumentPageSettingsOrientation = typeof ManagedDocumentPageSettingsOrientation[keyof typeof ManagedDocumentPageSettingsOrientation];


export const ManagedDocumentPageSettingsOrientation = {
  portrait: 'portrait',
  landscape: 'landscape',
} as const;

export type ManagedDocumentPageSettingsMargins = {
  /**
     * @minimum 5
     * @maximum 50
     */
  top: number;
  /**
     * @minimum 5
     * @maximum 50
     */
  right: number;
  /**
     * @minimum 5
     * @maximum 50
     */
  bottom: number;
  /**
     * @minimum 5
     * @maximum 50
     */
  left: number;
};

export interface ManagedDocumentPageSettings {
  size: ManagedDocumentPageSettingsSize;
  orientation: ManagedDocumentPageSettingsOrientation;
  margins: ManagedDocumentPageSettingsMargins;
}

export type ManagedDocumentMarkAttrs = { [key: string]: unknown };

export interface ManagedDocumentMark {
  /**
     * @minLength 1
     * @maxLength 40
     */
  type: string;
  attrs?: ManagedDocumentMarkAttrs;
}

export type ManagedDocumentNodeAttrs = { [key: string]: unknown };

export interface ManagedDocumentNode {
  /**
     * @minLength 1
     * @maxLength 40
     */
  type: string;
  /** @maxLength 200000 */
  text?: string;
  attrs?: ManagedDocumentNodeAttrs;
  /** @maxItems 20 */
  marks?: ManagedDocumentMark[];
  /** @maxItems 10000 */
  content?: ManagedDocumentNode[];
}

/**
 * Optional versioned layout in canonical document JSON. Omission preserves historical JSON and hashes. Header and footer contain structured nodes, never HTML.
 */
export interface ManagedDocumentLayout {
  pageSettings: ManagedDocumentPageSettings;
  /** @maxItems 100 */
  header: ManagedDocumentNode[];
  /** @maxItems 100 */
  footer: ManagedDocumentNode[];
}

export type ManagedDocumentSectionsSchemaVersion = typeof ManagedDocumentSectionsSchemaVersion[keyof typeof ManagedDocumentSectionsSchemaVersion];


export const ManagedDocumentSectionsSchemaVersion = {
  NUMBER_1: 1,
} as const;

/**
 * Independent editor-only boundaries; ordinary headings remain document content. Omitted for historical heading boundaries.
 */
export type ManagedDocumentSectionsBoundaryMode = typeof ManagedDocumentSectionsBoundaryMode[keyof typeof ManagedDocumentSectionsBoundaryMode];


export const ManagedDocumentSectionsBoundaryMode = {
  independent: 'independent',
} as const;

export type ManagedDocumentSectionsItemsItemProtection = typeof ManagedDocumentSectionsItemsItemProtection[keyof typeof ManagedDocumentSectionsItemsItemProtection];


export const ManagedDocumentSectionsItemsItemProtection = {
  editable: 'editable',
  bulk_protected: 'bulk_protected',
  locked: 'locked',
} as const;

export type ManagedDocumentSectionsStructure = {
  allowAdd: boolean;
  allowRename: boolean;
  allowReorder: boolean;
  allowHide: boolean;
};

export type ManagedDocumentSectionsItemsItem = {
  id: string;
  /**
     * @minLength 1
     * @maxLength 160
     */
  title: string;
  hidden: boolean;
  protection: ManagedDocumentSectionsItemsItemProtection;
  /** @maxLength 4000 */
  authoringNotes?: string;
  /** @minimum 1 */
  reviewedAtRevision?: number;
  templateDefined?: true;
};

export interface ManagedDocumentSections {
  schemaVersion: ManagedDocumentSectionsSchemaVersion;
  /** Independent editor-only boundaries; ordinary headings remain document content. Omitted for historical heading boundaries. */
  boundaryMode?: ManagedDocumentSectionsBoundaryMode;
  structure: ManagedDocumentSectionsStructure;
  /**
     * @minItems 1
     * @maxItems 200
     */
  items: ManagedDocumentSectionsItemsItem[];
}

export type ManagedDocumentStylesSchemaVersion = typeof ManagedDocumentStylesSchemaVersion[keyof typeof ManagedDocumentStylesSchemaVersion];


export const ManagedDocumentStylesSchemaVersion = {
  NUMBER_1: 1,
} as const;

export type ManagedDocumentStyleFontFamily = typeof ManagedDocumentStyleFontFamily[keyof typeof ManagedDocumentStyleFontFamily];


export const ManagedDocumentStyleFontFamily = {
  Arial: 'Arial',
  Calibri: 'Calibri',
  Times_New_Roman: 'Times New Roman',
  Georgia: 'Georgia',
  Verdana: 'Verdana',
  Courier_New: 'Courier New',
} as const;

export type ManagedDocumentStyleAlignment = typeof ManagedDocumentStyleAlignment[keyof typeof ManagedDocumentStyleAlignment];


export const ManagedDocumentStyleAlignment = {
  left: 'left',
  center: 'center',
  right: 'right',
  justify: 'justify',
} as const;

export interface ManagedDocumentStyle {
  /** @pattern ^(normal|heading-[1-6]|custom-[0-9a-f-]{36})$ */
  id: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  name: string;
  fontFamily: ManagedDocumentStyleFontFamily;
  /**
     * @minimum 6
     * @maximum 96
     */
  fontSize: number;
  /** @pattern ^#[0-9a-fA-F]{6}$ */
  color: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  alignment: ManagedDocumentStyleAlignment;
  /**
     * @minimum 0
     * @maximum 144
     */
  spaceBefore: number;
  /**
     * @minimum 0
     * @maximum 144
     */
  spaceAfter: number;
  /**
     * @minimum 1
     * @maximum 3
     */
  lineHeight: number;
}

export interface ManagedDocumentStyles {
  schemaVersion: ManagedDocumentStylesSchemaVersion;
  /**
     * @minItems 7
     * @maxItems 100
     */
  definitions: ManagedDocumentStyle[];
}

export type ManagedDocumentTemplateControlsSectionGuidanceItem = {
  sectionId: string;
  /** @maxLength 4000 */
  guidance: string;
};

export interface ManagedDocumentTemplateControls {
  available: boolean;
  /** @maxLength 4000 */
  guidance: string;
  /** @maxItems 200 */
  sectionGuidance: ManagedDocumentTemplateControlsSectionGuidanceItem[];
}

export interface ManagedDocumentContent {
  type: ManagedDocumentContentType;
  layout?: ManagedDocumentLayout;
  sections?: ManagedDocumentSections;
  styles?: ManagedDocumentStyles;
  template?: ManagedDocumentTemplateControls;
  /** @maxItems 10000 */
  content: ManagedDocumentNode[];
}

export interface ManagedDocumentTemplateSource {
  documentId: string;
  /** @minimum 1 */
  revision: number;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
}

export type LibraryCreationItem = {
  kind: 'document';
  content?: ManagedDocumentContent;
  templateSource?: ManagedDocumentTemplateSource;
  /**
     * @minLength 1
     * @maxLength 240
     */
  title: string;
} | {
  kind: 'upload';
  /**
     * @minLength 1
     * @maxLength 255
     */
  filename: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  contentType: string;
  /**
     * @minLength 4
     * @maxLength 27962028
     */
  contentBase64: string;
} | {
  kind: 'import';
  /**
     * @minLength 1
     * @maxLength 255
     */
  filename: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  contentType: string;
  /**
     * @minLength 4
     * @maxLength 27962028
     */
  contentBase64: string;
  reviewed: true;
};

export interface CreateLibraryRecordItem {
  requestId: string;
  destination: LibraryCreationDestination;
  item: LibraryCreationItem;
}

export type LibraryRecordCreationResultState = typeof LibraryRecordCreationResultState[keyof typeof LibraryRecordCreationResultState];


export const LibraryRecordCreationResultState = {
  saved: 'saved',
  unconfirmed: 'unconfirmed',
} as const;

export type DocumentWorkspaceSourceKind = typeof DocumentWorkspaceSourceKind[keyof typeof DocumentWorkspaceSourceKind];


export const DocumentWorkspaceSourceKind = {
  library: 'library',
  managed_document: 'managed_document',
} as const;

export interface DocumentWorkspaceSource {
  kind: DocumentWorkspaceSourceKind;
  id: string;
}

export interface LibraryRecordCreationResult {
  requestId: string;
  source: DocumentWorkspaceSource;
  destination: LibraryCreationDestination;
  /** @nullable */
  membershipId: string | null;
  state: LibraryRecordCreationResultState;
}

export type LibraryRecordCreationRecoveryState = typeof LibraryRecordCreationRecoveryState[keyof typeof LibraryRecordCreationRecoveryState];


export const LibraryRecordCreationRecoveryState = {
  absent: 'absent',
  saved: 'saved',
  unconfirmed: 'unconfirmed',
} as const;

export interface LibraryRecordCreationRecovery {
  state: LibraryRecordCreationRecoveryState;
  result: LibraryRecordCreationResult | null;
}

export type CreateDocumentWorkspaceDocumentKind = typeof CreateDocumentWorkspaceDocumentKind[keyof typeof CreateDocumentWorkspaceDocumentKind];


export const CreateDocumentWorkspaceDocumentKind = {
  blank: 'blank',
  tender_response: 'tender_response',
} as const;

export interface CreateDocumentWorkspaceDocument {
  requestId: string;
  expectedRevision: string;
  folderId: string;
  /**
     * @minLength 1
     * @maxLength 240
     */
  title: string;
  kind: CreateDocumentWorkspaceDocumentKind;
  /** Optional authored initial content, replacing the blank or tender scaffold. Uses native document validation and a maximum UTF-8 JSON size of 1000000 bytes; omitted content preserves legacy creation and request recovery. */
  content?: ManagedDocumentContent;
}

export type MarkdownDraftReviewSavedPrompt = {
  id: string;
  /** @minimum 1 */
  revision: number;
};

export interface MarkdownDraftReview {
  requestId: string;
  expectedRevision: string;
  membershipId: string;
  expectedMembershipRevision: string;
  sourceRevisionId: string;
  expectedFileId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  expectedContentHash: string;
  /** @maxLength 131072 */
  source: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  instruction: string;
  savedPrompt?: MarkdownDraftReviewSavedPrompt;
}

export interface MarkdownDraftSuggestion {
  /** @maxLength 80 */
  id: string;
  /** @minimum 0 */
  start: number;
  /** @minimum 1 */
  end: number;
  /** @maxLength 1200 */
  before: string;
  /**
     * @minLength 1
     * @maxLength 1200
     */
  after: string;
  /** @maxLength 300 */
  reason: string;
}

export interface MarkdownDraftReviewResult {
  requestId: string;
  /** @maxItems 24 */
  suggestions: MarkdownDraftSuggestion[];
}

export interface MarkdownConversionReview {
  requestId: string;
  expectedRevision: string;
  folderId: string;
  membershipId: string;
  expectedMembershipRevision: string;
  sourceRevisionId: string;
  expectedFileId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  expectedContentHash: string;
  /** @maxLength 2097152 */
  source: string;
  /**
     * @minLength 1
     * @maxLength 240
     */
  title: string;
  removeFromWorkspace: boolean;
}

export interface MarkdownConversionConfirm {
  review: MarkdownConversionReview;
  /** @pattern ^[a-f0-9]{64}$ */
  previewHash: string;
  confirmed: true;
}

export type MarkdownConversionPreviewContent = { [key: string]: unknown };

export interface MarkdownConversionPreview {
  /** @pattern ^[a-f0-9]{64}$ */
  previewHash: string;
  /** @pattern ^[a-f0-9]{64}$ */
  draftHash: string;
  destinationLabel: string;
  sourceFilename: string;
  removalAllowed: boolean;
  removalReason: string;
  content: MarkdownConversionPreviewContent;
}

export interface DocumentWorkspaceMembership {
  id: string;
  workspaceId: string;
  folderId: string;
  source: DocumentWorkspaceSource;
  revision: string;
  /** @nullable */
  selectedRevisionId: string | null;
}

export interface DocumentWorkspaceDocumentResult {
  documentId: string;
  membership: DocumentWorkspaceMembership;
  workspaceRevision: string;
}

export type DocumentWorkspaceDocumentRecoveryState = typeof DocumentWorkspaceDocumentRecoveryState[keyof typeof DocumentWorkspaceDocumentRecoveryState];


export const DocumentWorkspaceDocumentRecoveryState = {
  absent: 'absent',
  saved: 'saved',
} as const;

export interface DocumentWorkspaceDocumentRecovery {
  state: DocumentWorkspaceDocumentRecoveryState;
  result: DocumentWorkspaceDocumentResult | null;
}

export type DocumentWorkspaceUploadRecoveryState = typeof DocumentWorkspaceUploadRecoveryState[keyof typeof DocumentWorkspaceUploadRecoveryState];


export const DocumentWorkspaceUploadRecoveryState = {
  absent: 'absent',
  saved: 'saved',
  unconfirmed: 'unconfirmed',
} as const;

export interface DocumentWorkspaceSourceRevision {
  id: string;
  source: DocumentWorkspaceSource;
  /** @nullable */
  fileId: string | null;
  /** @nullable */
  managedVersionId: string | null;
  /** @nullable */
  tenderSourceId: string | null;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
}

export interface DocumentWorkspaceUploadResult {
  membership: DocumentWorkspaceMembership;
  sourceRevision: DocumentWorkspaceSourceRevision;
  workspaceRevision: string;
}

export interface DocumentWorkspaceUploadRecovery {
  state: DocumentWorkspaceUploadRecoveryState;
  result: DocumentWorkspaceUploadResult | null;
}

export type DocumentWorkspaceSourceStateState = typeof DocumentWorkspaceSourceStateState[keyof typeof DocumentWorkspaceSourceStateState];


export const DocumentWorkspaceSourceStateState = {
  stored: 'stored',
  pending: 'pending',
  ready: 'ready',
  unsupported: 'unsupported',
  failed: 'failed',
} as const;

export type DocumentWorkspaceSourceStateLocatorsItem = {
  location: string;
  /** @minimum 0 */
  start: number;
  /** @minimum 0 */
  end: number;
};

export interface DocumentWorkspaceSourceState {
  sourceRevision: DocumentWorkspaceSourceRevision;
  state: DocumentWorkspaceSourceStateState;
  /** @nullable */
  message: string | null;
  /**
     * @minimum 0
     * @maximum 3
     */
  attempts: number;
  extractionRevision: string;
  locators: DocumentWorkspaceSourceStateLocatorsItem[];
  warnings: string[];
}

export interface DocumentWorkspaceVersionList {
  items: DocumentWorkspaceSourceState[];
  total: number;
  limit: number;
  offset: number;
}

export interface PrepareDocumentWorkspaceSource {
  expectedRevision: string;
  expectedMembershipRevision: string;
  /** @nullable */
  sourceRevisionId?: string | null;
  expectedExtractionRevision?: string;
}

export interface RemoveDocumentWorkspaceMembership {
  expectedRevision: string;
  expectedMembershipRevision: string;
}

export interface RemoveDocumentWorkspaceFolder {
  expectedRevision: string;
  expectedFolderRevision: string;
}

export type DocumentWorkspaceAnchorKind = typeof DocumentWorkspaceAnchorKind[keyof typeof DocumentWorkspaceAnchorKind];


export const DocumentWorkspaceAnchorKind = {
  partner_agreement: 'partner_agreement',
  managed_document: 'managed_document',
  tender: 'tender',
  sow_preparation: 'sow_preparation',
} as const;

export interface DocumentWorkspaceAnchor {
  kind: DocumentWorkspaceAnchorKind;
  recordId: string;
}

export interface DocumentWorkspace {
  id: string;
  anchor: DocumentWorkspaceAnchor;
  title: string;
  revision: string;
}

export type DocumentWorkspaceFolderRole = typeof DocumentWorkspaceFolderRole[keyof typeof DocumentWorkspaceFolderRole];


export const DocumentWorkspaceFolderRole = {
  root: 'root',
  reference: 'reference',
  provided: 'provided',
  working: 'working',
  submission: 'submission',
} as const;

export interface DocumentWorkspaceFolder {
  id: string;
  workspaceId: string;
  /** @nullable */
  parentId: string | null;
  name: string;
  role: DocumentWorkspaceFolderRole;
  system: boolean;
  /**
     * @minimum 0
     * @maximum 8
     */
  depth: number;
  /** Current full contextual folder breadcrumb when listed; never a provider path. */
  path?: string;
  revision: string;
}

export interface FileLocation {
  workspace: DocumentWorkspace;
  membership: DocumentWorkspaceMembership;
  /** @maxItems 9 */
  breadcrumbs: DocumentWorkspaceFolder[];
}

export interface FileAssociationAccount {
  id: string;
  name: string;
  derived: boolean;
  additional: boolean;
}

export interface FileAssociationPage {
  source: DocumentWorkspaceSource;
  /** @pattern ^[a-f0-9]{64}$ */
  sourceRevision: string;
  /** @nullable */
  revision: string | null;
  /** @maxItems 50 */
  additionalAccountIds: string[];
  items: FileAssociationAccount[];
  total: number;
  limit: number;
  offset: number;
}

export interface SaveFileAssociations {
  requestId: string;
  /** @nullable */
  expectedRevision: string | null;
  /** @pattern ^[a-f0-9]{64}$ */
  expectedSourceRevision: string;
  /** @maxItems 50 */
  accountIds: string[];
  confirmed: true;
}

export interface FileAssociationReceipt {
  requestId: string;
  source: DocumentWorkspaceSource;
  revision: string;
  sourceRevision: string;
  /** @maxItems 50 */
  accountIds: string[];
  savedAt: string;
}

export interface FileLocationList {
  items: FileLocation[];
  total: number;
  limit: number;
  offset: number;
}

export interface RecordFile {
  source: DocumentWorkspaceSource;
  title: string;
  /** @nullable */
  contentType: string | null;
  /** @nullable */
  sizeBytes: number | null;
  folder: string;
  updatedAt: string;
  /** @nullable */
  linkedAt: string | null;
}

export type RecordFileListRecordKind = typeof RecordFileListRecordKind[keyof typeof RecordFileListRecordKind];


export const RecordFileListRecordKind = {
  account: 'account',
  contact: 'contact',
  enquiry: 'enquiry',
  opportunity: 'opportunity',
} as const;

export interface RecordFileList {
  recordKind: RecordFileListRecordKind;
  recordId: string;
  canEdit: boolean;
  items: RecordFile[];
  total: number;
  limit: number;
  offset: number;
}

export interface AttachRecordFile {
  source: DocumentWorkspaceSource;
}

export type RecordFileChangeRecordKind = typeof RecordFileChangeRecordKind[keyof typeof RecordFileChangeRecordKind];


export const RecordFileChangeRecordKind = {
  account: 'account',
  contact: 'contact',
  enquiry: 'enquiry',
  opportunity: 'opportunity',
} as const;

export type RecordFileChangeOutcome = typeof RecordFileChangeOutcome[keyof typeof RecordFileChangeOutcome];


export const RecordFileChangeOutcome = {
  attached: 'attached',
  already_attached: 'already_attached',
  detached: 'detached',
  not_attached: 'not_attached',
} as const;

export interface RecordFileChange {
  recordKind: RecordFileChangeRecordKind;
  recordId: string;
  source: DocumentWorkspaceSource;
  outcome: RecordFileChangeOutcome;
  /** @nullable */
  revision: string | null;
}

export interface CreateDocumentWorkspace {
  anchor: DocumentWorkspaceAnchor;
}

export interface CreateDocumentWorkspaceFolder {
  expectedRevision: string;
  /** Optional stable folder identity for read-first recovery. */
  requestId?: string;
  parentId: string;
  /**
     * @minLength 1
     * @maxLength 120
     */
  name: string;
}

export interface UpdateDocumentWorkspaceFolder {
  expectedRevision: string;
  expectedFolderRevision: string;
  parentId: string;
  /**
     * @minLength 1
     * @maxLength 120
     */
  name: string;
}

export interface CreateDocumentWorkspaceMembership {
  expectedRevision: string;
  folderId: string;
  source: DocumentWorkspaceSource;
}

export interface UpdateDocumentWorkspaceMembership {
  expectedRevision: string;
  expectedMembershipRevision: string;
  folderId: string;
}

export interface SelectDocumentWorkspaceSource {
  expectedRevision: string;
  expectedMembershipRevision: string;
  /** @nullable */
  sourceRevisionId: string | null;
}

export type DocumentWorkspaceContextItemRole = typeof DocumentWorkspaceContextItemRole[keyof typeof DocumentWorkspaceContextItemRole];


export const DocumentWorkspaceContextItemRole = {
  provided: 'provided',
  working: 'working',
  submission: 'submission',
} as const;

export type DocumentWorkspaceContextItemInclusion = typeof DocumentWorkspaceContextItemInclusion[keyof typeof DocumentWorkspaceContextItemInclusion];


export const DocumentWorkspaceContextItemInclusion = {
  automatic: 'automatic',
  explicit: 'explicit',
  excluded: 'excluded',
} as const;

export type DocumentWorkspaceContextItemReadiness = typeof DocumentWorkspaceContextItemReadiness[keyof typeof DocumentWorkspaceContextItemReadiness];


export const DocumentWorkspaceContextItemReadiness = {
  revision_required: 'revision_required',
  version_pinned: 'version_pinned',
  excluded: 'excluded',
} as const;

export interface DocumentWorkspaceContextItem {
  title: string;
  /** True only for canonical local upload sources; destination policy is rechecked on write. */
  canReplace: boolean;
  membership: DocumentWorkspaceMembership;
  role: DocumentWorkspaceContextItemRole;
  inclusion: DocumentWorkspaceContextItemInclusion;
  selectedRevision: DocumentWorkspaceSourceRevision | null;
  readiness: DocumentWorkspaceContextItemReadiness;
  currentRevision: DocumentWorkspaceSourceRevision | null;
  currentState: DocumentWorkspaceSourceState | null;
  selectedState: DocumentWorkspaceSourceState | null;
  versionMismatch: boolean;
}

export interface DocumentWorkspaceContext {
  workspace: DocumentWorkspace;
  items: DocumentWorkspaceContextItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface DocumentWorkspaceList {
  items: DocumentWorkspace[];
  total: number;
  limit: number;
  offset: number;
}

export interface DocumentWorkspaceFolderList {
  items: DocumentWorkspaceFolder[];
  total: number;
  limit: number;
  offset: number;
}

export interface DocumentWorkspaceMembershipList {
  items: DocumentWorkspaceMembership[];
  total: number;
  limit: number;
  offset: number;
}

export interface BidInventoryCitation {
  sourceId: string;
  sourceRevision: string;
  /** @pattern ^[a-f0-9]{64}$ */
  sourceHash: string;
  location: string;
  excerpt: string;
}

export type BidInventoryItemClassification = typeof BidInventoryItemClassification[keyof typeof BidInventoryItemClassification];


export const BidInventoryItemClassification = {
  mandatory: 'mandatory',
  weighted: 'weighted',
  non_weighted: 'non_weighted',
  output: 'output',
  format: 'format',
  delivery: 'delivery',
} as const;

export type SupplierFactKey = typeof SupplierFactKey[keyof typeof SupplierFactKey];


export const SupplierFactKey = {
  legal_name: 'legal_name',
  incorporation: 'incorporation',
  abn_acn: 'abn_acn',
  corporate_structure: 'corporate_structure',
  addresses: 'addresses',
  contact_person: 'contact_person',
  capabilities: 'capabilities',
  capacity: 'capacity',
  intellectual_property: 'intellectual_property',
  data_sovereignty: 'data_sovereignty',
  information_security: 'information_security',
  conflicts: 'conflicts',
  eservices: 'eservices',
  insurance: 'insurance',
  declarations: 'declarations',
  case_study_1: 'case_study_1',
  case_study_2: 'case_study_2',
  case_study_3: 'case_study_3',
  case_study_4: 'case_study_4',
} as const;

export interface BidInventoryItem {
  key: string;
  title: string;
  classification: BidInventoryItemClassification;
  /** @nullable */
  weight: number | null;
  /** @maxItems 19 */
  factKeys: SupplierFactKey[];
  /** @maxItems 30 */
  rules: string[];
  /** @maxItems 30 */
  citations: BidInventoryCitation[];
  /** @nullable */
  requirementId: string | null;
}

export type BidSourceCoverageState = typeof BidSourceCoverageState[keyof typeof BidSourceCoverageState];


export const BidSourceCoverageState = {
  complete: 'complete',
  partial: 'partial',
  unavailable: 'unavailable',
} as const;

export type BidSourceCoverageLedgerItem = {
  location: string;
  start: number;
  end: number;
  /** @pattern ^[a-f0-9]{64}$ */
  hash: string;
};

export interface BidSourceCoverage {
  sourceId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  sourceHash: string;
  format: string;
  state: BidSourceCoverageState;
  blocks: number;
  characters: number;
  /** @maxItems 2000 */
  ledger: BidSourceCoverageLedgerItem[];
  /** @maxItems 100 */
  warnings: string[];
}

export type BidFitEvaluationStatus = typeof BidFitEvaluationStatus[keyof typeof BidFitEvaluationStatus];


export const BidFitEvaluationStatus = {
  pass: 'pass',
  fail: 'fail',
  unknown: 'unknown',
} as const;

export interface BidFitEvaluation {
  key: string;
  status: BidFitEvaluationStatus;
  /**
     * @minimum 0
     * @maximum 100
     * @nullable
     */
  score: number | null;
  /**
     * @minLength 10
     * @maxLength 2000
     */
  rationale: string;
  /** @maxItems 19 */
  factRevisionIds: string[];
}

export type BidFitDecisionDecision = typeof BidFitDecisionDecision[keyof typeof BidFitDecisionDecision];


export const BidFitDecisionDecision = {
  proceed: 'proceed',
  conditional: 'conditional',
  defer: 'defer',
} as const;

export interface BidFitDecision {
  id: string;
  decision: BidFitDecisionDecision;
  rationale: string;
  /** @maxItems 10 */
  evaluations: BidFitEvaluation[];
  actorAppUserId: string;
  createdAt: string;
  current: boolean;
}

export type BidInventoryRecommendation = typeof BidInventoryRecommendation[keyof typeof BidInventoryRecommendation];


export const BidInventoryRecommendation = {
  proceed: 'proceed',
  conditional: 'conditional',
  defer: 'defer',
} as const;

export type BidInventoryMandatoryItemStatus = typeof BidInventoryMandatoryItemStatus[keyof typeof BidInventoryMandatoryItemStatus];


export const BidInventoryMandatoryItemStatus = {
  pass: 'pass',
  fail: 'fail',
  unknown: 'unknown',
} as const;

export type BidInventoryMandatoryItem = {
  key: string;
  status: BidInventoryMandatoryItemStatus;
  reason: string;
};

export type BidInventoryWeightedItem = {
  key: string;
  weight: number;
  /** @nullable */
  score: number | null;
  reason: string;
};

export interface BidInventory {
  /** @pattern ^[a-f0-9]{64}$ */
  fingerprint: string;
  /** @nullable */
  invitationReference: string | null;
  /** @maxItems 30 */
  sources: BidSourceCoverage[];
  /** @maxItems 100 */
  items: BidInventoryItem[];
  /** @maxItems 100 */
  warnings: string[];
  complete: boolean;
  reviewed: boolean;
  recommendation: BidInventoryRecommendation;
  /** @maxItems 5 */
  mandatory: BidInventoryMandatoryItem[];
  /** @maxItems 4 */
  weighted: BidInventoryWeightedItem[];
  /** @maxItems 100 */
  gaps: string[];
  decision: BidFitDecision | null;
}

export interface BidInventoryReviewWrite {
  /** @pattern ^[a-f0-9]{64}$ */
  fingerprint: string;
}

export type BidFitDecisionWriteDecision = typeof BidFitDecisionWriteDecision[keyof typeof BidFitDecisionWriteDecision];


export const BidFitDecisionWriteDecision = {
  proceed: 'proceed',
  conditional: 'conditional',
  defer: 'defer',
} as const;

export interface BidFitDecisionWrite {
  /** @pattern ^[a-f0-9]{64}$ */
  fingerprint: string;
  decision: BidFitDecisionWriteDecision;
  /**
     * @minLength 10
     * @maxLength 2000
     */
  rationale: string;
  /** @maxItems 10 */
  evaluations: BidFitEvaluation[];
}

export type TenderAiReasoningEffort = typeof TenderAiReasoningEffort[keyof typeof TenderAiReasoningEffort];


export const TenderAiReasoningEffort = {
  none: 'none',
  minimal: 'minimal',
  low: 'low',
  medium: 'medium',
  high: 'high',
  xhigh: 'xhigh',
} as const;

export interface BidStageSettings {
  reasoningEffort: TenderAiReasoningEffort | null;
  /**
     * @minimum 1
     * @maximum 131072
     */
  maxOutputTokens: number;
  /**
     * @minimum 1000
     * @maximum 120000
     */
  timeoutMs: number;
}

export type BidStage = typeof BidStage[keyof typeof BidStage];


export const BidStage = {
  research: 'research',
  assessment: 'assessment',
  drafting: 'drafting',
  buyer_review: 'buyer_review',
} as const;

export interface BidStageSelection {
  id: string;
  stage: BidStage;
  /** @maxLength 64 */
  choiceId: string;
  /** @minimum 1 */
  revision: number;
  /** @minimum 1 */
  modelRevision: number;
  /** @maxLength 64 */
  policyFingerprint: string;
  settings: BidStageSettings;
  changedByAppUserId: string;
  createdAt: string;
}

export type BidModelDataStatus = typeof BidModelDataStatus[keyof typeof BidModelDataStatus];


export const BidModelDataStatus = {
  active: 'active',
  disabled: 'disabled',
  removed: 'removed',
} as const;

export type BidModelDataAdapter = typeof BidModelDataAdapter[keyof typeof BidModelDataAdapter];


export const BidModelDataAdapter = {
  'chat-completions': 'chat-completions',
  unsupported: 'unsupported',
} as const;

export type BidModelDataReasoningEffortsItem = typeof BidModelDataReasoningEffortsItem[keyof typeof BidModelDataReasoningEffortsItem];


export const BidModelDataReasoningEffortsItem = {
  none: 'none',
  minimal: 'minimal',
  low: 'low',
  medium: 'medium',
  high: 'high',
  xhigh: 'xhigh',
} as const;

export type BidMetadataEvidenceState = typeof BidMetadataEvidenceState[keyof typeof BidMetadataEvidenceState];


export const BidMetadataEvidenceState = {
  verified: 'verified',
  unknown: 'unknown',
  unavailable: 'unavailable',
} as const;

export interface BidMetadataEvidence {
  state: BidMetadataEvidenceState;
  /**
     * @maxLength 1000
     * @nullable
     */
  sourceRef: string | null;
  /** @nullable */
  verifiedAt: string | null;
}

export type BidModelDataProcessingScope = typeof BidModelDataProcessingScope[keyof typeof BidModelDataProcessingScope];


export const BidModelDataProcessingScope = {
  regional: 'regional',
  global: 'global',
  unknown: 'unknown',
} as const;

export interface BidModelData {
  /**
     * @minLength 1
     * @maxLength 120
     */
  displayName: string;
  /** @maxLength 1000 */
  description: string;
  status: BidModelDataStatus;
  /** @pattern ^[a-z0-9][a-z0-9-]{0,63}$ */
  connectionRef: string;
  adapter: BidModelDataAdapter;
  /** @maxItems 4 */
  stages: BidStage[];
  /** @maxItems 6 */
  reasoningEfforts: BidModelDataReasoningEffortsItem[];
  /**
     * @minimum 1
     * @maximum 4000000
     * @nullable
     */
  contextWindowTokens: number | null;
  /**
     * @minimum 1
     * @maximum 131072
     * @nullable
     */
  maxOutputTokens: number | null;
  capabilityEvidence: BidMetadataEvidence;
  /**
     * @maxItems 40
     * @items.pattern ^[A-Z]{2}$
     */
  processingLocations: string[];
  processingScope: BidModelDataProcessingScope;
  processingEvidence: BidMetadataEvidence;
  /**
     * @minimum 0
     * @maximum 100000
     * @nullable
     */
  inputPerMillionUsd: number | null;
  /**
     * @minimum 0
     * @maximum 100000
     * @nullable
     */
  outputPerMillionUsd: number | null;
  /**
     * @minimum 1
     * @maximum 3600000
     * @nullable
     */
  latencyMs: number | null;
  performanceEvidence: BidMetadataEvidence;
}

export interface BidModelRevision {
  id: string;
  /** @pattern ^[a-z0-9][a-z0-9-]{0,63}$ */
  choiceId: string;
  /**
     * @minimum 1
     * @maximum 4000000
     */
  revision: number;
  data: BidModelData;
  changedByAppUserId: string;
  createdAt: string;
  /**
     * @minLength 10
     * @maxLength 2000
     */
  reason: string;
}

export interface BidEffectiveModel {
  record: BidModelRevision;
  configured: boolean;
  policyEligible: boolean;
  executable: boolean;
  liveVerified: boolean;
  /**
     * @maxItems 20
     * @items.maxLength 1000
     */
  reasons: string[];
}

export interface BidStageChoice {
  model: BidEffectiveModel;
  /** @nullable */
  contextWindowTokens: number | null;
  /** @minimum 1 */
  maxOutputTokens: number;
  /** @minimum 1000 */
  maxTimeoutMs: number;
  /** @maxItems 6 */
  reasoningEfforts: TenderAiReasoningEffort[];
  /**
     * @maxItems 30
     * @items.maxLength 1000
     */
  reasons: string[];
  /**
     * @maxItems 30
     * @items.maxLength 1000
     */
  limitations: string[];
}

export interface BidStageOptions {
  stage: BidStage;
  selection: BidStageSelection | null;
  /** @maxLength 64 */
  policyFingerprint: string;
  /**
     * @maxLength 64
     * @nullable
     */
  recommendedChoiceId: string | null;
  /** @maxItems 100 */
  choices: BidStageChoice[];
  total: number;
  limit: number;
  offset: number;
}

export interface BidStageSelectionWrite {
  /** @minimum 0 */
  expectedRevision: number;
  /**
     * @maxLength 64
     * @pattern ^[a-z0-9][a-z0-9-]{0,63}$
     */
  choiceId: string;
  /** @minimum 1 */
  expectedModelRevision: number;
  /**
     * @minLength 64
     * @maxLength 64
     */
  policyFingerprint: string;
  settings: BidStageSettings;
}

export interface BidStageRunCreate {
  /** @minimum 1 */
  selectionRevision: number;
  /** @maxLength 4000 */
  instruction?: string;
  /** @nullable */
  planRevisionId?: string | null;
}

export type BidModelScope = typeof BidModelScope[keyof typeof BidModelScope];


export const BidModelScope = {
  platform: 'platform',
  tenant: 'tenant',
  entity: 'entity',
} as const;

export interface BidModelWrite {
  /** @minimum 0 */
  expectedRevision: number;
  data: BidModelData;
  /**
     * @minLength 10
     * @maxLength 2000
     */
  reason: string;
}

export interface BidProcessingPolicy {
  /**
     * @maxItems 200
     * @nullable
     * @items.pattern ^[a-z0-9][a-z0-9-]{0,63}$
     */
  allowedChoiceIds: string[] | null;
  /**
     * @maxItems 40
     * @nullable
     * @items.pattern ^[A-Z]{2}$
     */
  allowedLocations: string[] | null;
  /** @nullable */
  requireVerifiedLocation: boolean | null;
}

export interface BidPolicyRevision {
  id: string;
  scope: BidModelScope;
  /**
     * @minimum 1
     * @maximum 4000000
     */
  revision: number;
  policy: BidProcessingPolicy;
  changedByAppUserId: string;
  createdAt: string;
  /**
     * @minLength 10
     * @maxLength 2000
     */
  reason: string;
}

export interface BidPolicyWrite {
  /** @minimum 0 */
  expectedRevision: number;
  /**
     * @minLength 64
     * @maxLength 64
     */
  parentFingerprint: string;
  policy: BidProcessingPolicy;
  /**
     * @minLength 10
     * @maxLength 2000
     */
  reason: string;
}

export interface BidModelAdministration {
  scope: BidModelScope;
  localPolicy: BidPolicyRevision | null;
  /** @maxItems 2 */
  parents: BidPolicyRevision[];
  effectivePolicy: BidProcessingPolicy;
  /** @maxLength 64 */
  parentFingerprint: string;
  /** @maxItems 100 */
  models: BidEffectiveModel[];
  total: number;
  limit: number;
  offset: number;
  /**
     * @maxItems 20
     * @items.pattern ^[a-z0-9][a-z0-9-]{0,63}$
     */
  connectionRefs: string[];
}

export interface BidModelRevisionPage {
  /** @maxItems 100 */
  items: BidModelRevision[];
  total: number;
  limit: number;
  offset: number;
}

export interface BidPolicyRevisionPage {
  /** @maxItems 100 */
  items: BidPolicyRevision[];
  total: number;
  limit: number;
  offset: number;
}

export type SupplierFactState = typeof SupplierFactState[keyof typeof SupplierFactState];


export const SupplierFactState = {
  confirmed: 'confirmed',
  unknown: 'unknown',
  conflicting: 'conflicting',
  expired: 'expired',
} as const;

export interface SupplierCitation {
  sourceId: string;
  /** @minimum 1 */
  sourceRevision: number;
  fileId: string;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  location: string;
  /**
     * @minLength 1
     * @maxLength 1200
     */
  excerpt: string;
  /** @pattern ^[a-f0-9]{64}$ */
  sourceHash: string;
}

export interface SupplierFactInput {
  /** @minimum 0 */
  expectedRevision: number;
  state: SupplierFactState;
  /** @maxLength 5000 */
  value: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  note: string;
  /** @nullable */
  expiresAt?: string | null;
  /** @maxItems 10 */
  citations: SupplierCitation[];
}

export interface SupplierFact {
  id: string;
  key: SupplierFactKey;
  revision: number;
  state: SupplierFactState;
  recordedState: SupplierFactState;
  value: string;
  note: string;
  citations: SupplierCitation[];
  confirmedByAppUserId: string;
  createdAt: string;
  /** @nullable */
  expiresAt: string | null;
  evidenceMessage: string;
}

export type SupplierGroundingChecklistItem = {
  key: SupplierFactKey;
  label: string;
  state: SupplierFactState;
  action: string;
};

export interface SupplierGrounding {
  /** @maxItems 19 */
  facts: SupplierFact[];
  checklist: SupplierGroundingChecklistItem[];
  /** Evidence checklist complete only; never submission or signature approval */
  ready: boolean;
}

export type BidSetupInputDocumentsItem = {
  /**
     * @minLength 1
     * @maxLength 64
     * @pattern ^[a-z0-9][a-z0-9_-]*$
     */
  key: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /** @maxLength 4000 */
  skeletonText: string;
  /** @maxItems 100 */
  requirementIds: string[];
};

export type BidSetupInputAssignmentsItem = {
  /**
     * @minLength 1
     * @maxLength 64
     * @pattern ^[a-z0-9][a-z0-9_-]*$
     */
  key: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /** @nullable */
  ownerAppUserId: string | null;
  /** @nullable */
  dueAt: string | null;
  /** @maxItems 100 */
  requirementIds: string[];
  /**
     * @maxItems 20
     * @items.maxLength 64
     */
  documentKeys: string[];
};

export interface BidSetupInput {
  tenderId: string;
  /**
     * @minItems 1
     * @maxItems 20
     */
  documents: BidSetupInputDocumentsItem[];
  /** @maxItems 40 */
  assignments: BidSetupInputAssignmentsItem[];
}

export type BidSetupSnapshotPriorStepHashes = {[key: string]: string};

export type BidSetupSnapshotExistingAssignmentsItem = {
  key: string;
  activityId: string;
  /** @nullable */
  ownerAppUserId: string | null;
  /** @nullable */
  ownerName: string | null;
  /** @nullable */
  dueAt: string | null;
};

export type BidSetupSnapshotSourceRevisionsItem = {
  id: string;
  revision: string;
  hash: string;
};

export type BidSetupSnapshotOwnersItem = {
  id: string;
  name: string;
  authorizationRevision: string;
};

/**
 * @nullable
 */
export type BidSetupSnapshotStageModelsItemBinding = {[key: string]: unknown} | null;

export type BidSetupSnapshotStageModelsItem = {
  stage: BidStage;
  /** @nullable */
  choiceId: string | null;
  /** @nullable */
  selectionRevision: number | null;
  policyFingerprint: string;
  /** @nullable */
  binding: BidSetupSnapshotStageModelsItemBinding;
};

export interface BidSetupSnapshot {
  input: BidSetupInput;
  tenantId: string;
  entityId: string;
  tenantName: string;
  entityName: string;
  tenderTitle: string;
  inventoryFingerprint: string;
  storageFingerprint: string;
  priorStepHashes?: BidSetupSnapshotPriorStepHashes;
  /** @maxItems 40 */
  existingAssignments?: BidSetupSnapshotExistingAssignmentsItem[];
  /** @maxItems 30 */
  sourceRevisions: BidSetupSnapshotSourceRevisionsItem[];
  /** @maxItems 40 */
  owners: BidSetupSnapshotOwnersItem[];
  /** @maxItems 4 */
  stageModels: BidSetupSnapshotStageModelsItem[];
  /** @maxItems 150 */
  warnings: string[];
  permittedDraftWork: string;
}

export type BidSetupProgressStatus = typeof BidSetupProgressStatus[keyof typeof BidSetupProgressStatus];


export const BidSetupProgressStatus = {
  proposed: 'proposed',
  approved: 'approved',
  cancelled: 'cancelled',
  superseded: 'superseded',
} as const;

export type BidSetupProgressItemsItemKind = typeof BidSetupProgressItemsItemKind[keyof typeof BidSetupProgressItemsItemKind];


export const BidSetupProgressItemsItemKind = {
  document: 'document',
  assignment: 'assignment',
} as const;

export type BidSetupProgressItemsItem = {
  key: string;
  kind: BidSetupProgressItemsItemKind;
  /** @nullable */
  documentId: string | null;
  /** @nullable */
  versionId: string | null;
  /** @nullable */
  activityId: string | null;
  materialized: boolean;
  requirementIds: string[];
  documentKeys: string[];
  status: string;
  /** @nullable */
  dueAt: string | null;
  /** @nullable */
  ownerAppUserId: string | null;
  /** @nullable */
  completionEvidence: string | null;
  blockers: string[];
};

export interface BidSetupProgress {
  planId: string;
  tenderId: string;
  status: BidSetupProgressStatus;
  snapshotHash: string;
  link: string;
  /** @nullable */
  error: string | null;
  total: number;
  limit: number;
  offset: number;
  items: BidSetupProgressItemsItem[];
}

export type BidPlanSnapshotDocumentsItem = {
  /** @maxLength 100 */
  key: string;
  /** @maxLength 300 */
  title: string;
  /** @maxItems 200 */
  requirementIds: string[];
};

export type BidPlanSnapshotAssignmentsItem = {
  /** @nullable */
  ownerAppUserId: string | null;
  /** @maxLength 300 */
  title: string;
  /** @nullable */
  dueAt: string | null;
  /** @maxItems 200 */
  requirementIds: string[];
  /**
     * @maxItems 100
     * @items.maxLength 100
     */
  documentKeys: string[];
};

export type BidPlanSnapshotStageModelsItem = {
  stage: BidStage;
  /** @maxLength 300 */
  modelId: string;
  /** @maxLength 128 */
  policyRevision: string;
};

export interface BidPlanSnapshot {
  setup?: BidSetupSnapshot;
  /**
     * @minLength 1
     * @maxLength 10000
     */
  scope: string;
  /** @maxItems 200 */
  sourceRevisions: string[];
  /** @maxItems 200 */
  supplierFactRevisionIds: string[];
  /** @maxItems 100 */
  documents: BidPlanSnapshotDocumentsItem[];
  /** @maxItems 100 */
  assignments: BidPlanSnapshotAssignmentsItem[];
  /** @maxItems 4 */
  stageModels: BidPlanSnapshotStageModelsItem[];
  /** @maxLength 10000 */
  permittedDraftWork: string;
}

export type BidPlanRevisionStatus = typeof BidPlanRevisionStatus[keyof typeof BidPlanRevisionStatus];


export const BidPlanRevisionStatus = {
  proposed: 'proposed',
  approved: 'approved',
  superseded: 'superseded',
  cancelled: 'cancelled',
} as const;

export interface BidPlanRevision {
  id: string;
  tenderId: string;
  /** @minimum 1 */
  revision: number;
  snapshot: BidPlanSnapshot;
  snapshotHash: string;
  status: BidPlanRevisionStatus;
  /** @nullable */
  approvedByAppUserId: string | null;
  proposedByAppUserId?: string;
}

export type BidResponseDocumentLinkSourceCoverage = typeof BidResponseDocumentLinkSourceCoverage[keyof typeof BidResponseDocumentLinkSourceCoverage];


export const BidResponseDocumentLinkSourceCoverage = {
  unaddressed: 'unaddressed',
  partial: 'partial',
  covered: 'covered',
  not_applicable: 'not_applicable',
} as const;

export interface BidResponseDocumentLink {
  id: string;
  requirementId: string;
  documentId: string;
  versionId: string;
  sourceCoverage: BidResponseDocumentLinkSourceCoverage;
}

export interface BidStageRunReference {
  id: string;
  runId: string;
  stage: BidStage;
  planRevisionId: string;
  selectedModelId: string;
  policyRevision: string;
}

export interface BidFoundation {
  planRevisions: BidPlanRevision[];
  responseDocuments: BidResponseDocumentLink[];
  stageRuns: BidStageRunReference[];
  planTotal: number;
  documentTotal: number;
  runTotal: number;
  limit: number;
  offset: number;
}

export type DocumentMaterializationState = typeof DocumentMaterializationState[keyof typeof DocumentMaterializationState];


export const DocumentMaterializationState = {
  pending: 'pending',
  writing: 'writing',
  complete: 'complete',
  uncertain: 'uncertain',
  blocked: 'blocked',
} as const;

export interface DocumentMaterialization {
  id: string;
  storageKey: string;
  category: string;
  state: DocumentMaterializationState;
  message: string;
  destinationId: string;
  createdAt: string;
  updatedAt: string;
}

export interface M365Library {
  id: string;
  displayName: string;
}

export interface M365LibraryList {
  items: M365Library[];
  total: number;
  limit: number;
  offset: number;
}

export type M365AdminConnectionGrantKind = typeof M365AdminConnectionGrantKind[keyof typeof M365AdminConnectionGrantKind];


export const M365AdminConnectionGrantKind = {
  environment: 'environment',
  delegated: 'delegated',
  application: 'application',
} as const;

export type M365AdminConnectionStatus = typeof M365AdminConnectionStatus[keyof typeof M365AdminConnectionStatus];


export const M365AdminConnectionStatus = {
  setup_required: 'setup_required',
  ready: 'ready',
  unavailable: 'unavailable',
  revoked: 'revoked',
} as const;

export interface M365VerifiedDestination {
  id: string;
  siteId: string;
  driveId: string;
  displayName: string;
  readVerified: boolean;
  writeVerified: boolean;
  requiredMetadata: string[];
}

export interface M365AdminConnection {
  id: string;
  resourceId: string;
  /** @minimum 1 */
  revision: number;
  tenantId: string;
  /** @nullable */
  entityId: string | null;
  displayName: string;
  externalTenantId: string;
  grantKind: M365AdminConnectionGrantKind;
  status: M365AdminConnectionStatus;
  /** @nullable */
  clientId: string | null;
  /** @nullable */
  principalId: string | null;
  credentialConfigured: boolean;
  /** @nullable */
  verifiedAt: string | null;
  message: string;
  verifiedMailboxes: string[];
  destinations: M365VerifiedDestination[];
}

export interface M365AdminConnectionList {
  items: M365AdminConnection[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type CreateM365ConnectionGrantKind = typeof CreateM365ConnectionGrantKind[keyof typeof CreateM365ConnectionGrantKind];


export const CreateM365ConnectionGrantKind = {
  delegated: 'delegated',
  application: 'application',
} as const;

export interface CreateM365Connection {
  /**
     * @minLength 1
     * @maxLength 120
     */
  displayName: string;
  externalTenantId: string;
  grantKind: CreateM365ConnectionGrantKind;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface ConfigureM365Credentials {
  /** @minimum 0 */
  expectedRevision: number;
  clientId: string;
  /**
     * @minLength 1
     * @maxLength 4096
     */
  clientSecret: string;
  sharepoint: boolean;
  /** @maxItems 50 */
  mailboxes: string[];
  /** @nullable */
  excludedMailbox: string | null;
  /** @maxLength 2000 */
  scopeEvidence: string;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface M365RevisionAction {
  /** @minimum 0 */
  expectedRevision: number;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface M365OAuthStart {
  authorizationUrl: string;
}

export interface ProbeM365Connection {
  /** @minimum 0 */
  expectedRevision: number;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
  /**
     * @maxLength 2000
     * @nullable
     */
  siteUrl: string | null;
  /**
     * @maxLength 500
     * @nullable
     */
  driveId: string | null;
}

export type M365ConnectionDescriptorStatus = typeof M365ConnectionDescriptorStatus[keyof typeof M365ConnectionDescriptorStatus];


export const M365ConnectionDescriptorStatus = {
  setup_required: 'setup_required',
  ready: 'ready',
  unavailable: 'unavailable',
  revoked: 'revoked',
} as const;

export type M365ConnectionDescriptorGrantKind = typeof M365ConnectionDescriptorGrantKind[keyof typeof M365ConnectionDescriptorGrantKind];


export const M365ConnectionDescriptorGrantKind = {
  environment: 'environment',
  delegated: 'delegated',
  application: 'application',
} as const;

export interface M365ConnectionDescriptor {
  id: string;
  resourceId: string;
  /** @minimum 1 */
  revision: number;
  tenantId: string;
  /** @nullable */
  entityId: string | null;
  /**
     * @minLength 1
     * @maxLength 120
     */
  displayName: string;
  status: M365ConnectionDescriptorStatus;
  externalTenantId: string;
  grantKind: M365ConnectionDescriptorGrantKind;
  /** @nullable */
  principalAppUserId: string | null;
}

export type DocumentDestinationDescriptorStatus = typeof DocumentDestinationDescriptorStatus[keyof typeof DocumentDestinationDescriptorStatus];


export const DocumentDestinationDescriptorStatus = {
  setup_required: 'setup_required',
  ready: 'ready',
  unavailable: 'unavailable',
  revoked: 'revoked',
} as const;

export type DocumentDestinationDescriptorProvider = typeof DocumentDestinationDescriptorProvider[keyof typeof DocumentDestinationDescriptorProvider];


export const DocumentDestinationDescriptorProvider = {
  application: 'application',
  sharepoint: 'sharepoint',
  sharepoint_embedded: 'sharepoint_embedded',
} as const;

export interface DocumentDestinationDescriptor {
  id: string;
  resourceId: string;
  /** @minimum 1 */
  revision: number;
  tenantId: string;
  /** @nullable */
  entityId: string | null;
  /**
     * @minLength 1
     * @maxLength 120
     */
  displayName: string;
  status: DocumentDestinationDescriptorStatus;
  provider: DocumentDestinationDescriptorProvider;
  /** @nullable */
  connectionId: string | null;
  /** @nullable */
  siteId: string | null;
  /** @nullable */
  driveId: string | null;
  /** @nullable */
  containerId: string | null;
  /** @maxLength 1000 */
  folderPath: string;
}

export interface DocumentStorageOptions {
  /** @maxItems 100 */
  connections: M365ConnectionDescriptor[];
  /** @minimum 0 */
  connectionTotal: number;
  /** @maxItems 100 */
  destinations: DocumentDestinationDescriptor[];
  /** @minimum 0 */
  destinationTotal: number;
}

export type DocumentStorageConfigurationConnectionMode = typeof DocumentStorageConfigurationConnectionMode[keyof typeof DocumentStorageConfigurationConnectionMode];


export const DocumentStorageConfigurationConnectionMode = {
  inherit: 'inherit',
  disabled: 'disabled',
  override: 'override',
} as const;

export type DocumentStorageConfigurationStorageMode = typeof DocumentStorageConfigurationStorageMode[keyof typeof DocumentStorageConfigurationStorageMode];


export const DocumentStorageConfigurationStorageMode = {
  inherit: 'inherit',
  override: 'override',
} as const;

export type DocumentStorageConfigurationCategoryDefaultsItem = {
  /**
     * @minLength 1
     * @maxLength 80
     * @pattern ^[a-z][a-z0-9_]*$
     */
  category: string;
  destinationId: string;
};

/**
 * @nullable
 */
export type DocumentStorageConfigurationSavePolicy = typeof DocumentStorageConfigurationSavePolicy[keyof typeof DocumentStorageConfigurationSavePolicy] | null;


export const DocumentStorageConfigurationSavePolicy = {
  approved_only: 'approved_only',
  drafts_and_approved: 'drafts_and_approved',
} as const;

export interface DocumentStorageConfiguration {
  connectionMode: DocumentStorageConfigurationConnectionMode;
  /** @nullable */
  connectionId: string | null;
  storageMode: DocumentStorageConfigurationStorageMode;
  /** @maxItems 100 */
  allowedDestinationIds: string[];
  /** @nullable */
  defaultDestinationId: string | null;
  /** @maxItems 50 */
  categoryDefaults: DocumentStorageConfigurationCategoryDefaultsItem[];
  /** @nullable */
  savePolicy: DocumentStorageConfigurationSavePolicy;
}

export interface UpdateDocumentStorageSettings {
  /**
     * @minimum 0
     * @maximum 2147483646
     */
  expectedRevision: number;
  configuration: DocumentStorageConfiguration;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface ScopedDocumentStorageSettings {
  tenantId: string;
  /** @nullable */
  entityId: string | null;
  /** @minimum 0 */
  revision: number;
  configuration: DocumentStorageConfiguration;
}

export type EffectiveDocumentStorageConnectionSource = typeof EffectiveDocumentStorageConnectionSource[keyof typeof EffectiveDocumentStorageConnectionSource];


export const EffectiveDocumentStorageConnectionSource = {
  tenant: 'tenant',
  entity: 'entity',
  disabled: 'disabled',
  legacy: 'legacy',
} as const;

export type EffectiveDocumentStorageStorageSource = typeof EffectiveDocumentStorageStorageSource[keyof typeof EffectiveDocumentStorageStorageSource];


export const EffectiveDocumentStorageStorageSource = {
  tenant: 'tenant',
  entity: 'entity',
  legacy: 'legacy',
} as const;

export type EffectiveDocumentStoragePolicySource = typeof EffectiveDocumentStoragePolicySource[keyof typeof EffectiveDocumentStoragePolicySource];


export const EffectiveDocumentStoragePolicySource = {
  tenant: 'tenant',
  entity: 'entity',
  legacy: 'legacy',
} as const;

export type EffectiveDocumentStorageCategoryDefaultsItem = {
  /**
     * @minLength 1
     * @maxLength 80
     * @pattern ^[a-z][a-z0-9_]*$
     */
  category: string;
  destinationId: string;
};

/**
 * @nullable
 */
export type EffectiveDocumentStorageSavePolicy = typeof EffectiveDocumentStorageSavePolicy[keyof typeof EffectiveDocumentStorageSavePolicy] | null;


export const EffectiveDocumentStorageSavePolicy = {
  approved_only: 'approved_only',
  drafts_and_approved: 'drafts_and_approved',
} as const;

export type EffectiveDocumentStorageIssuesItemCode = typeof EffectiveDocumentStorageIssuesItemCode[keyof typeof EffectiveDocumentStorageIssuesItemCode];


export const EffectiveDocumentStorageIssuesItemCode = {
  connection_unavailable: 'connection_unavailable',
  destination_unavailable: 'destination_unavailable',
  stale_binding: 'stale_binding',
  legacy_unbound: 'legacy_unbound',
} as const;

export type EffectiveDocumentStorageIssuesItem = {
  code: EffectiveDocumentStorageIssuesItemCode;
  /** @nullable */
  referenceId: string | null;
  message: string;
};

export interface EffectiveDocumentStorage {
  tenantId: string;
  entityId: string;
  /** @minimum 0 */
  tenantRevision: number;
  /** @minimum 0 */
  entityRevision: number;
  connectionSource: EffectiveDocumentStorageConnectionSource;
  storageSource: EffectiveDocumentStorageStorageSource;
  policySource: EffectiveDocumentStoragePolicySource;
  connection: M365ConnectionDescriptor | null;
  /** @maxItems 100 */
  allowedDestinations: DocumentDestinationDescriptor[];
  /** @nullable */
  defaultDestinationId: string | null;
  /** @maxItems 50 */
  categoryDefaults: EffectiveDocumentStorageCategoryDefaultsItem[];
  /** @nullable */
  savePolicy: EffectiveDocumentStorageSavePolicy;
  /** @pattern ^[a-f0-9]{64}$ */
  bindingFingerprint: string;
  /** @maxItems 302 */
  issues: EffectiveDocumentStorageIssuesItem[];
  legacyBehavior: boolean;
}

export interface IcloudConnectInput {
  /**
     * @minLength 3
     * @maxLength 320
     */
  appleId: string;
  /**
     * @minLength 1
     * @maxLength 128
     */
  appPassword: string;
}

/**
 * @nullable
 */
export type IcloudConnectionStatus = typeof IcloudConnectionStatus[keyof typeof IcloudConnectionStatus] | null;


export const IcloudConnectionStatus = {
  connected: 'connected',
  needs_reconnect: 'needs_reconnect',
} as const;

export interface IcloudConnection {
  connected: boolean;
  /** @nullable */
  status: IcloudConnectionStatus;
  /** @nullable */
  appleId: string | null;
  /** @minimum 0 */
  contactCount: number;
  /** @nullable */
  lastSyncedAt: string | null;
  /** @nullable */
  lastBackupAt: string | null;
  /** @minimum 0 */
  snapshotVersion: number;
  /**
     * @minimum 0
     * @nullable
     */
  backupSnapshotVersion: number | null;
}

export interface IcloudContactField {
  label: string;
  value: string;
}

export interface IcloudContact {
  id: string;
  uid: string;
  displayName: string;
  /** @nullable */
  firstName: string | null;
  /** @nullable */
  lastName: string | null;
  /** @nullable */
  organization: string | null;
  emails: IcloudContactField[];
  phones: IcloudContactField[];
  /** @nullable */
  linkedContactId: string | null;
  syncedAt: string;
}

export interface IcloudContactList {
  items: IcloudContact[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
  /** @minimum 0 */
  snapshotVersion: number;
  /** @nullable */
  lastSyncedAt: string | null;
}

export interface IcloudPromotionPreviewRequest {
  /**
     * @minItems 1
     * @maxItems 100
     * @items.minLength 1
     * @items.maxLength 500
     */
  uids: string[];
}

export type IcloudPromotionMatchMatchType = typeof IcloudPromotionMatchMatchType[keyof typeof IcloudPromotionMatchMatchType];


export const IcloudPromotionMatchMatchType = {
  email: 'email',
  phone: 'phone',
  name: 'name',
} as const;

export interface IcloudPromotionMatch {
  contactId: string;
  name: string;
  email: string;
  matchType: IcloudPromotionMatchMatchType;
}

export interface IcloudPromotionPreviewItem {
  uid: string;
  displayName: string;
  /** @nullable */
  firstName: string | null;
  /** @nullable */
  lastName: string | null;
  /** @nullable */
  organization: string | null;
  emails: IcloudContactField[];
  phones: IcloudContactField[];
  matches: IcloudPromotionMatch[];
  /** @nullable */
  alreadyLinkedContactId: string | null;
  canCreate: boolean;
}

export interface IcloudPromotionPreview {
  items: IcloudPromotionPreviewItem[];
}

export type IcloudPromotionDecisionAction = typeof IcloudPromotionDecisionAction[keyof typeof IcloudPromotionDecisionAction];


export const IcloudPromotionDecisionAction = {
  create: 'create',
  link: 'link',
  skip: 'skip',
} as const;

export interface IcloudPromotionDecision {
  /**
     * @minLength 1
     * @maxLength 500
     */
  uid: string;
  action: IcloudPromotionDecisionAction;
  contactId?: string;
  accountId?: string;
}

export interface IcloudPromotionRequest {
  /**
     * @minItems 1
     * @maxItems 100
     */
  decisions: IcloudPromotionDecision[];
}

export type IcloudPromotionOutcomeOutcome = typeof IcloudPromotionOutcomeOutcome[keyof typeof IcloudPromotionOutcomeOutcome];


export const IcloudPromotionOutcomeOutcome = {
  created: 'created',
  linked: 'linked',
  skipped: 'skipped',
} as const;

export interface IcloudPromotionOutcome {
  uid: string;
  outcome: IcloudPromotionOutcomeOutcome;
  /** @nullable */
  contactId: string | null;
}

export interface IcloudPromotionResult {
  results: IcloudPromotionOutcome[];
}

export interface IcloudBackup {
  filename: string;
  vcard: string;
  /** @minimum 0 */
  contactCount: number;
  /** @minimum 0 */
  snapshotVersion: number;
}

export interface IcloudDisconnectResult {
  disconnected: true;
}

export interface IcloudPrompt {
  id: string;
  name: string;
  instruction: string;
  createdAt: string;
  updatedAt: string;
}

export interface IcloudPromptInput {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @minLength 1
     * @maxLength 12000
     */
  instruction: string;
}

export interface IcloudPromptUpdate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @minLength 1
     * @maxLength 12000
     */
  instruction?: string;
}

export type IcloudMergeRuleField = typeof IcloudMergeRuleField[keyof typeof IcloudMergeRuleField];


export const IcloudMergeRuleField = {
  name: 'name',
  email: 'email',
  phone: 'phone',
  organization: 'organization',
} as const;

export type IcloudMergeRuleMatch = typeof IcloudMergeRuleMatch[keyof typeof IcloudMergeRuleMatch];


export const IcloudMergeRuleMatch = {
  exact: 'exact',
  similar: 'similar',
} as const;

export type IcloudMergeRuleEffect = typeof IcloudMergeRuleEffect[keyof typeof IcloudMergeRuleEffect];


export const IcloudMergeRuleEffect = {
  merge: 'merge',
  never_merge: 'never_merge',
} as const;

export interface IcloudMergeRule {
  id: string;
  field: IcloudMergeRuleField;
  match: IcloudMergeRuleMatch;
  effect: IcloudMergeRuleEffect;
  enabled: boolean;
  /**
     * @minimum 0
     * @maximum 10000
     */
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export type IcloudMergeRuleInputField = typeof IcloudMergeRuleInputField[keyof typeof IcloudMergeRuleInputField];


export const IcloudMergeRuleInputField = {
  name: 'name',
  email: 'email',
  phone: 'phone',
  organization: 'organization',
} as const;

export type IcloudMergeRuleInputMatch = typeof IcloudMergeRuleInputMatch[keyof typeof IcloudMergeRuleInputMatch];


export const IcloudMergeRuleInputMatch = {
  exact: 'exact',
  similar: 'similar',
} as const;

export type IcloudMergeRuleInputEffect = typeof IcloudMergeRuleInputEffect[keyof typeof IcloudMergeRuleInputEffect];


export const IcloudMergeRuleInputEffect = {
  merge: 'merge',
  never_merge: 'never_merge',
} as const;

export interface IcloudMergeRuleInput {
  field: IcloudMergeRuleInputField;
  match: IcloudMergeRuleInputMatch;
  effect: IcloudMergeRuleInputEffect;
  enabled?: boolean;
  /**
     * @minimum 0
     * @maximum 10000
     */
  sortOrder?: number;
}

export type IcloudMergeRuleUpdateField = typeof IcloudMergeRuleUpdateField[keyof typeof IcloudMergeRuleUpdateField];


export const IcloudMergeRuleUpdateField = {
  name: 'name',
  email: 'email',
  phone: 'phone',
  organization: 'organization',
} as const;

export type IcloudMergeRuleUpdateMatch = typeof IcloudMergeRuleUpdateMatch[keyof typeof IcloudMergeRuleUpdateMatch];


export const IcloudMergeRuleUpdateMatch = {
  exact: 'exact',
  similar: 'similar',
} as const;

export type IcloudMergeRuleUpdateEffect = typeof IcloudMergeRuleUpdateEffect[keyof typeof IcloudMergeRuleUpdateEffect];


export const IcloudMergeRuleUpdateEffect = {
  merge: 'merge',
  never_merge: 'never_merge',
} as const;

export interface IcloudMergeRuleUpdate {
  field?: IcloudMergeRuleUpdateField;
  match?: IcloudMergeRuleUpdateMatch;
  effect?: IcloudMergeRuleUpdateEffect;
  enabled?: boolean;
  /**
     * @minimum 0
     * @maximum 10000
     */
  sortOrder?: number;
}

export interface IcloudMergeRuleOrder {
  /** @maxItems 100 */
  ruleIds: string[];
}

export interface IcloudReviewContact {
  uid: string;
  displayName: string;
  /** @nullable */
  firstName: string | null;
  /** @nullable */
  lastName: string | null;
  /** @nullable */
  organization: string | null;
  emails: IcloudContactField[];
  phones: IcloudContactField[];
}

export interface IcloudMergeSource {
  uid: string;
  url: string;
  etag: string;
}

export interface IcloudMergedContact {
  primaryUid: string;
  displayName: string;
  /** @nullable */
  firstName: string | null;
  /** @nullable */
  lastName: string | null;
  /** @nullable */
  organization: string | null;
  emails: IcloudContactField[];
  phones: IcloudContactField[];
}

export type IcloudMergeBatchItemDecision = typeof IcloudMergeBatchItemDecision[keyof typeof IcloudMergeBatchItemDecision];


export const IcloudMergeBatchItemDecision = {
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
} as const;

export type IcloudMergeBatchItemResult = typeof IcloudMergeBatchItemResult[keyof typeof IcloudMergeBatchItemResult];


export const IcloudMergeBatchItemResult = {
  pending: 'pending',
  applied: 'applied',
  failed: 'failed',
  stale: 'stale',
  skipped: 'skipped',
  restored: 'restored',
} as const;

export type IcloudMergeBatchItemConfidence = typeof IcloudMergeBatchItemConfidence[keyof typeof IcloudMergeBatchItemConfidence];


export const IcloudMergeBatchItemConfidence = {
  high: 'high',
  medium: 'medium',
  low: 'low',
} as const;

export interface IcloudMergeBatchItem {
  id: string;
  primaryUid: string;
  removeUids: string[];
  sources: IcloudMergeSource[];
  contacts: IcloudReviewContact[];
  merged: IcloudMergedContact;
  decision: IcloudMergeBatchItemDecision;
  result: IcloudMergeBatchItemResult;
  /** @nullable */
  resultError: string | null;
  confidence: IcloudMergeBatchItemConfidence;
  reason: string;
  /** @nullable */
  appliedAt: string | null;
  /** @nullable */
  originalsPurgeAt: string | null;
  /** @nullable */
  restoredAt: string | null;
  restoreAvailable: boolean;
  createdAt: string;
  updatedAt: string;
}

export type IcloudMergeBatchSummaryStatus = typeof IcloudMergeBatchSummaryStatus[keyof typeof IcloudMergeBatchSummaryStatus];


export const IcloudMergeBatchSummaryStatus = {
  draft: 'draft',
  executing: 'executing',
  executed: 'executed',
} as const;

export interface IcloudMergeBatchSummary {
  id: string;
  name: string;
  status: IcloudMergeBatchSummaryStatus;
  /** @nullable */
  instructionUsed: string | null;
  /** @minimum 0 */
  snapshotVersion: number;
  /** @nullable */
  executedAt: string | null;
  /** @minimum 0 */
  itemCount: number;
  /** @minimum 0 */
  pendingCount: number;
  /** @minimum 0 */
  approvedCount: number;
  /** @minimum 0 */
  rejectedCount: number;
  /** @minimum 0 */
  appliedCount: number;
  createdAt: string;
  updatedAt: string;
}

export type IcloudMergeBatchDetail = IcloudMergeBatchSummary & {
  items: IcloudMergeBatchItem[];
};

export interface IcloudMergeBatchList {
  items: IcloudMergeBatchSummary[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface IcloudMergeBatchCreate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
}

export interface IcloudMergeBatchGenerate {
  promptId?: string;
  /**
     * @minLength 1
     * @maxLength 12000
     */
  instruction?: string;
}

export type IcloudMergeDecisionDecision = typeof IcloudMergeDecisionDecision[keyof typeof IcloudMergeDecisionDecision];


export const IcloudMergeDecisionDecision = {
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
} as const;

export interface IcloudMergeDecision {
  decision: IcloudMergeDecisionDecision;
}

export type IcloudMergeExecutionItemResult = typeof IcloudMergeExecutionItemResult[keyof typeof IcloudMergeExecutionItemResult];


export const IcloudMergeExecutionItemResult = {
  applied: 'applied',
  failed: 'failed',
  stale: 'stale',
  skipped: 'skipped',
} as const;

export interface IcloudMergeExecutionItem {
  itemId: string;
  primaryUid: string;
  result: IcloudMergeExecutionItemResult;
  /** @nullable */
  error: string | null;
}

export interface IcloudMergeExecution {
  /** @minimum 0 */
  applied: number;
  /** @minimum 0 */
  failed: number;
  /** @minimum 0 */
  stale: number;
  /** @minimum 0 */
  skipped: number;
  results: IcloudMergeExecutionItem[];
}

export type IcloudWritebackLogAction = typeof IcloudWritebackLogAction[keyof typeof IcloudWritebackLogAction];


export const IcloudWritebackLogAction = {
  update: 'update',
  delete: 'delete',
  restore_create: 'restore_create',
  restore_update: 'restore_update',
} as const;

export type IcloudWritebackLogResult = typeof IcloudWritebackLogResult[keyof typeof IcloudWritebackLogResult];


export const IcloudWritebackLogResult = {
  ok: 'ok',
  failed: 'failed',
} as const;

export interface IcloudWritebackLog {
  id: string;
  batchId: string;
  itemId: string;
  uid: string;
  url: string;
  action: IcloudWritebackLogAction;
  /** @nullable */
  etagBefore: string | null;
  /** @nullable */
  etagAfter: string | null;
  result: IcloudWritebackLogResult;
  /** @nullable */
  error: string | null;
  createdAt: string;
}

export interface IcloudWritebackLogList {
  items: IcloudWritebackLog[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface MicrositeAdminEntity {
  tenantId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  tenantName: string;
  entityId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  entityName: string;
}

export interface MicrositeAdminEntityList {
  items: MicrositeAdminEntity[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface MicrositePublicLink {
  isPublished: boolean;
  /** @nullable */
  publicUrl: string | null;
  /**
     * @minLength 3
     * @maxLength 80
     */
  slug: string;
  /** @maxLength 253 */
  canonicalHost?: string;
}

export type MicrositeAssetKind = typeof MicrositeAssetKind[keyof typeof MicrositeAssetKind];


export const MicrositeAssetKind = {
  logo: 'logo',
  hero: 'hero',
} as const;

export interface MicrositeAsset {
  id: string;
  kind: MicrositeAssetKind;
  filename: string;
  contentType: string;
  /**
     * @minimum 1
     * @maximum 8388608
     */
  sizeBytes: number;
  createdAt: string;
}

export interface MicrositeAssetList {
  items: MicrositeAsset[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type MicrositeBrandEvidenceState = typeof MicrositeBrandEvidenceState[keyof typeof MicrositeBrandEvidenceState];


export const MicrositeBrandEvidenceState = {
  evidence: 'evidence',
  inferred: 'inferred',
  missing: 'missing',
} as const;

export type MicrositeBrandEvidenceSourceKind = typeof MicrositeBrandEvidenceSourceKind[keyof typeof MicrositeBrandEvidenceSourceKind];


export const MicrositeBrandEvidenceSourceKind = {
  style_guide: 'style_guide',
  website: 'website',
} as const;

export interface MicrositeBrandEvidence {
  /**
     * @minLength 1
     * @maxLength 80
     */
  id: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  property: string;
  value: unknown | null;
  sourceKind: MicrositeBrandEvidenceSourceKind;
  /**
     * @minLength 1
     * @maxLength 500
     */
  location: string;
  state: MicrositeBrandEvidenceState;
}

export type MicrositeBrandAssetCandidateContentType = typeof MicrositeBrandAssetCandidateContentType[keyof typeof MicrositeBrandAssetCandidateContentType];


export const MicrositeBrandAssetCandidateContentType = {
  'image/png': 'image/png',
  'image/jpeg': 'image/jpeg',
  'image/webp': 'image/webp',
  'image/avif': 'image/avif',
} as const;

export interface MicrositeBrandAssetCandidate {
  /**
     * @minLength 1
     * @maxLength 80
     */
  id: string;
  /** @maxLength 2048 */
  url: string;
  contentType: MicrositeBrandAssetCandidateContentType;
  /**
     * @minimum 1
     * @maximum 1048576
     */
  sizeBytes: number;
  /** @pattern ^[0-9a-f]{64}$ */
  fingerprint: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  location: string;
}

export type MicrositeBrandSourceSourceKind = typeof MicrositeBrandSourceSourceKind[keyof typeof MicrositeBrandSourceSourceKind];


export const MicrositeBrandSourceSourceKind = {
  style_guide: 'style_guide',
  website: 'website',
} as const;

export type MicrositeBrandSourceVisualEvidenceState = typeof MicrositeBrandSourceVisualEvidenceState[keyof typeof MicrositeBrandSourceVisualEvidenceState];


export const MicrositeBrandSourceVisualEvidenceState = {
  available: 'available',
  unavailable: 'unavailable',
} as const;

export type MicrositeBrandSourceVisualEvidence = {
  state: MicrositeBrandSourceVisualEvidenceState;
  /** @maxLength 300 */
  reason?: string;
  /**
     * @minimum 0
     * @maximum 20
     */
  embeddedImageCount: number;
};

export interface MicrositeBrandSource {
  id: string;
  sourceKind: MicrositeBrandSourceSourceKind;
  /** @minimum 1 */
  version: number;
  /**
     * @minLength 1
     * @maxLength 200
     */
  label: string;
  /** @nullable */
  url?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  filename?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  contentType?: string | null;
  /**
     * @minimum 1
     * @maximum 8388608
     * @nullable
     */
  sizeBytes?: number | null;
  /** @pattern ^[0-9a-f]{64}$ */
  fingerprint: string;
  /** @maxItems 200 */
  evidence: MicrositeBrandEvidence[];
  visualEvidence: MicrositeBrandSourceVisualEvidence;
  /** @maxItems 8 */
  assetCandidates: MicrositeBrandAssetCandidate[];
  createdAt: string;
}

export interface MicrositeBrandSourceList {
  items: MicrositeBrandSource[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface MicrositeBrandSuggestion {
  /**
     * @minLength 1
     * @maxLength 80
     */
  property: string;
  value: unknown | null;
  state: MicrositeBrandEvidenceState;
  /**
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 80
     */
  evidenceIds: string[];
  /** @maxLength 120 */
  requestedFont?: string;
  /** @maxLength 120 */
  substitution?: string;
}

export type MicrositeBrandConflictResolutionChoice = typeof MicrositeBrandConflictResolutionChoice[keyof typeof MicrositeBrandConflictResolutionChoice];


export const MicrositeBrandConflictResolutionChoice = {
  guide: 'guide',
  website: 'website',
  custom: 'custom',
  keep_current: 'keep_current',
} as const;

export interface MicrositeBrandConflictResolution {
  choice: MicrositeBrandConflictResolutionChoice;
  customValue?: unknown | null;
}

export interface MicrositeBrandConflict {
  /**
     * @minLength 1
     * @maxLength 80
     */
  id: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  property: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  guideEvidenceId: string;
  guideValue: unknown | null;
  /**
     * @minLength 1
     * @maxLength 500
     */
  guideLocation: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  websiteEvidenceId: string;
  websiteValue: unknown | null;
  /**
     * @minLength 1
     * @maxLength 500
     */
  websiteLocation: string;
  resolution: MicrositeBrandConflictResolution | null;
}

export type MicrositeBrandProposalStatus = typeof MicrositeBrandProposalStatus[keyof typeof MicrositeBrandProposalStatus];


export const MicrositeBrandProposalStatus = {
  processing: 'processing',
  ready: 'ready',
  failed: 'failed',
} as const;

export interface MicrositeBrandProposal {
  id: string;
  status: MicrositeBrandProposalStatus;
  /** @minimum 0 */
  revision: number;
  /** @minimum 1 */
  attempt: number;
  /**
     * @minItems 1
     * @maxItems 2
     */
  sourceIds: string[];
  /** @pattern ^[0-9a-f]{64}$ */
  sourceFingerprint: string;
  /** @pattern ^[0-9a-f]{64}$ */
  draftFingerprint: string;
  /** @maxItems 30 */
  suggestions: MicrositeBrandSuggestion[];
  /** @maxItems 30 */
  conflicts: MicrositeBrandConflict[];
  /** @maxItems 8 */
  assetCandidates: MicrositeBrandAssetCandidate[];
  allConflictsResolved: boolean;
  stale: boolean;
  /**
     * @maxLength 100
     * @nullable
     */
  errorCode: string | null;
  /**
     * @maxLength 300
     * @nullable
     */
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MicrositeBrandProposalList {
  items: MicrositeBrandProposal[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type MicrositeBrandProposalCreate = (unknown & {
  styleGuideSourceId?: string;
  /** @maxLength 2048 */
  websiteUrl?: string;
  /** @minimum 1 */
  expectedMicrositeRevision: number;
});

export interface MicrositeBrandProposalRevisionWrite {
  /** @minimum 1 */
  expectedRevision: number;
}

export interface MicrositeBrandAssetImport {
  /**
     * @minLength 1
     * @maxLength 80
     */
  candidateId: string;
  /** @pattern ^[0-9a-f]{64}$ */
  expectedFingerprint: string;
  kind: MicrositeAssetKind;
}

export type MicrositeBrandConflictResolutionInputChoice = typeof MicrositeBrandConflictResolutionInputChoice[keyof typeof MicrositeBrandConflictResolutionInputChoice];


export const MicrositeBrandConflictResolutionInputChoice = {
  guide: 'guide',
  website: 'website',
  custom: 'custom',
  keep_current: 'keep_current',
} as const;

export interface MicrositeBrandConflictResolutionInput {
  /**
     * @minLength 1
     * @maxLength 80
     */
  conflictId: string;
  choice: MicrositeBrandConflictResolutionInputChoice;
  customValue?: unknown | null;
}

export interface MicrositeBrandConflictResolutionWrite {
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minItems 1
     * @maxItems 30
     */
  resolutions: MicrositeBrandConflictResolutionInput[];
}

export type MicrositeBrandKitHeadingFont = typeof MicrositeBrandKitHeadingFont[keyof typeof MicrositeBrandKitHeadingFont];


export const MicrositeBrandKitHeadingFont = {
  'system-sans': 'system-sans',
  'humanist-sans': 'humanist-sans',
  'editorial-serif': 'editorial-serif',
  monospace: 'monospace',
} as const;

export type MicrositeBrandKitBodyFont = typeof MicrositeBrandKitBodyFont[keyof typeof MicrositeBrandKitBodyFont];


export const MicrositeBrandKitBodyFont = {
  'system-sans': 'system-sans',
  'humanist-sans': 'humanist-sans',
  'editorial-serif': 'editorial-serif',
  monospace: 'monospace',
} as const;

export type MicrositeBrandKitTypeScale = typeof MicrositeBrandKitTypeScale[keyof typeof MicrositeBrandKitTypeScale];


export const MicrositeBrandKitTypeScale = {
  compact: 'compact',
  standard: 'standard',
  large: 'large',
} as const;

export type MicrositeBrandKitSpacing = typeof MicrositeBrandKitSpacing[keyof typeof MicrositeBrandKitSpacing];


export const MicrositeBrandKitSpacing = {
  compact: 'compact',
  standard: 'standard',
  generous: 'generous',
} as const;

export type MicrositeBrandKitContentWidth = typeof MicrositeBrandKitContentWidth[keyof typeof MicrositeBrandKitContentWidth];


export const MicrositeBrandKitContentWidth = {
  narrow: 'narrow',
  standard: 'standard',
  wide: 'wide',
} as const;

export type MicrositeBrandKitLogoTreatment = typeof MicrositeBrandKitLogoTreatment[keyof typeof MicrositeBrandKitLogoTreatment];


export const MicrositeBrandKitLogoTreatment = {
  natural: 'natural',
  contained: 'contained',
  monochrome: 'monochrome',
} as const;

export type MicrositeBrandKitLogoScale = typeof MicrositeBrandKitLogoScale[keyof typeof MicrositeBrandKitLogoScale];


export const MicrositeBrandKitLogoScale = {
  small: 'small',
  medium: 'medium',
  large: 'large',
} as const;

export type MicrositeBrandKitImageryTreatment = typeof MicrositeBrandKitImageryTreatment[keyof typeof MicrositeBrandKitImageryTreatment];


export const MicrositeBrandKitImageryTreatment = {
  natural: 'natural',
  soft: 'soft',
  'high-contrast': 'high-contrast',
  monochrome: 'monochrome',
} as const;

export type MicrositeBrandKitImageShape = typeof MicrositeBrandKitImageShape[keyof typeof MicrositeBrandKitImageShape];


export const MicrositeBrandKitImageShape = {
  square: 'square',
  rounded: 'rounded',
  pill: 'pill',
} as const;

export type MicrositeBrandKitButtonStyle = typeof MicrositeBrandKitButtonStyle[keyof typeof MicrositeBrandKitButtonStyle];


export const MicrositeBrandKitButtonStyle = {
  solid: 'solid',
  soft: 'soft',
  outline: 'outline',
} as const;

export type MicrositeBrandKitButtonShape = typeof MicrositeBrandKitButtonShape[keyof typeof MicrositeBrandKitButtonShape];


export const MicrositeBrandKitButtonShape = {
  square: 'square',
  rounded: 'rounded',
  pill: 'pill',
} as const;

export type MicrositeBrandKitButtonWeight = typeof MicrositeBrandKitButtonWeight[keyof typeof MicrositeBrandKitButtonWeight];


export const MicrositeBrandKitButtonWeight = {
  regular: 'regular',
  medium: 'medium',
  bold: 'bold',
} as const;

export type MicrositeBrandKitSurfaceStyle = typeof MicrositeBrandKitSurfaceStyle[keyof typeof MicrositeBrandKitSurfaceStyle];


export const MicrositeBrandKitSurfaceStyle = {
  flat: 'flat',
  bordered: 'bordered',
  elevated: 'elevated',
} as const;

export type MicrositeBrandKitBorderStyle = typeof MicrositeBrandKitBorderStyle[keyof typeof MicrositeBrandKitBorderStyle];


export const MicrositeBrandKitBorderStyle = {
  none: 'none',
  subtle: 'subtle',
  strong: 'strong',
} as const;

export type MicrositeBrandKitRadius = typeof MicrositeBrandKitRadius[keyof typeof MicrositeBrandKitRadius];


export const MicrositeBrandKitRadius = {
  none: 'none',
  small: 'small',
  medium: 'medium',
  large: 'large',
} as const;

export type MicrositeBrandKitResponsiveLayout = typeof MicrositeBrandKitResponsiveLayout[keyof typeof MicrositeBrandKitResponsiveLayout];


export const MicrositeBrandKitResponsiveLayout = {
  adaptive: 'adaptive',
  stacked: 'stacked',
} as const;

export type MicrositeBrandKitHeroAlignment = typeof MicrositeBrandKitHeroAlignment[keyof typeof MicrositeBrandKitHeroAlignment];


export const MicrositeBrandKitHeroAlignment = {
  left: 'left',
  center: 'center',
} as const;

export interface MicrositeBrandKit {
  /** @nullable */
  logoAssetId?: string | null;
  /** @nullable */
  heroAssetId?: string | null;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  primaryColor: string;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  backgroundColor: string;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  foregroundColor: string;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  accentColor?: string;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  surfaceColor?: string;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  mutedColor?: string;
  headingFont?: MicrositeBrandKitHeadingFont;
  bodyFont?: MicrositeBrandKitBodyFont;
  typeScale?: MicrositeBrandKitTypeScale;
  spacing?: MicrositeBrandKitSpacing;
  contentWidth?: MicrositeBrandKitContentWidth;
  logoTreatment?: MicrositeBrandKitLogoTreatment;
  logoScale?: MicrositeBrandKitLogoScale;
  imageryTreatment?: MicrositeBrandKitImageryTreatment;
  imageShape?: MicrositeBrandKitImageShape;
  buttonStyle?: MicrositeBrandKitButtonStyle;
  buttonShape?: MicrositeBrandKitButtonShape;
  buttonWeight?: MicrositeBrandKitButtonWeight;
  surfaceStyle?: MicrositeBrandKitSurfaceStyle;
  borderStyle?: MicrositeBrandKitBorderStyle;
  radius?: MicrositeBrandKitRadius;
  responsiveLayout?: MicrositeBrandKitResponsiveLayout;
  heroAlignment?: MicrositeBrandKitHeroAlignment;
}

export interface MicrositePromptChoice {
  id: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  label: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  instruction: string;
}

export type MicrositeApprovedContentReferenceSourceType = typeof MicrositeApprovedContentReferenceSourceType[keyof typeof MicrositeApprovedContentReferenceSourceType];


export const MicrositeApprovedContentReferenceSourceType = {
  website: 'website',
  document: 'document',
  manual: 'manual',
} as const;

export interface MicrositeApprovedContentReference {
  sourceId: string;
  sourceType: MicrositeApprovedContentReferenceSourceType;
  /** @minimum 1 */
  version: number;
  /**
     * @minLength 1
     * @maxLength 160
     */
  label: string;
}

export interface MicrositeContactDetails {
  /** @maxLength 254 */
  email?: string;
  /** @maxLength 40 */
  phone?: string;
  /** @maxLength 300 */
  address?: string;
}

export interface MicrositeEnquiryCallToAction {
  /**
     * @minLength 1
     * @maxLength 140
     */
  heading: string;
  /**
     * @minLength 1
     * @maxLength 60
     */
  label: string;
  /** @maxLength 400 */
  supportingText?: string;
}

export type MicrositeLayoutColumnWidth = typeof MicrositeLayoutColumnWidth[keyof typeof MicrositeLayoutColumnWidth];


export const MicrositeLayoutColumnWidth = {
  full: 'full',
  half: 'half',
  third: 'third',
  'two-thirds': 'two-thirds',
} as const;

export type MicrositeHeaderComponentType = typeof MicrositeHeaderComponentType[keyof typeof MicrositeHeaderComponentType];


export const MicrositeHeaderComponentType = {
  header: 'header',
} as const;

export type MicrositeHeaderComponentProps = {
  showLogo: boolean;
  showSiteName: boolean;
};

export interface MicrositeHeaderComponent {
  id: string;
  type: MicrositeHeaderComponentType;
  props: MicrositeHeaderComponentProps;
}

export type MicrositeHeroComponentType = typeof MicrositeHeroComponentType[keyof typeof MicrositeHeroComponentType];


export const MicrositeHeroComponentType = {
  hero: 'hero',
} as const;

export type MicrositeHeroComponentProps = {
  showImage: boolean;
  showPromptChoices: boolean;
  /** @maxLength 120 */
  eyebrow?: string;
};

export interface MicrositeHeroComponent {
  id: string;
  type: MicrositeHeroComponentType;
  props: MicrositeHeroComponentProps;
}

export type MicrositeApprovedContentSectionsComponentType = typeof MicrositeApprovedContentSectionsComponentType[keyof typeof MicrositeApprovedContentSectionsComponentType];


export const MicrositeApprovedContentSectionsComponentType = {
  approvedContentSections: 'approvedContentSections',
} as const;

export type MicrositeApprovedContentSectionsComponentPropsColumns = typeof MicrositeApprovedContentSectionsComponentPropsColumns[keyof typeof MicrositeApprovedContentSectionsComponentPropsColumns];


export const MicrositeApprovedContentSectionsComponentPropsColumns = {
  NUMBER_1: 1,
  NUMBER_2: 2,
} as const;

export type MicrositeApprovedContentSectionsComponentProps = {
  columns: MicrositeApprovedContentSectionsComponentPropsColumns;
  showHighlights: boolean;
  showCitations: boolean;
};

export interface MicrositeApprovedContentSectionsComponent {
  id: string;
  type: MicrositeApprovedContentSectionsComponentType;
  props: MicrositeApprovedContentSectionsComponentProps;
}

export type MicrositeSourcesFooterComponentType = typeof MicrositeSourcesFooterComponentType[keyof typeof MicrositeSourcesFooterComponentType];


export const MicrositeSourcesFooterComponentType = {
  sourcesFooter: 'sourcesFooter',
} as const;

export type MicrositeSourcesFooterComponentProps = {
  /**
     * @minLength 1
     * @maxLength 80
     */
  heading: string;
};

export interface MicrositeSourcesFooterComponent {
  id: string;
  type: MicrositeSourcesFooterComponentType;
  props: MicrositeSourcesFooterComponentProps;
}

export type MicrositeTextComponentType = typeof MicrositeTextComponentType[keyof typeof MicrositeTextComponentType];


export const MicrositeTextComponentType = {
  text: 'text',
} as const;

export type MicrositeTextComponentPropsAlignment = typeof MicrositeTextComponentPropsAlignment[keyof typeof MicrositeTextComponentPropsAlignment];


export const MicrositeTextComponentPropsAlignment = {
  left: 'left',
  center: 'center',
  right: 'right',
} as const;

export type MicrositeTextComponentProps = {
  /** @maxLength 160 */
  heading?: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  text: string;
  alignment: MicrositeTextComponentPropsAlignment;
};

export interface MicrositeTextComponent {
  id: string;
  type: MicrositeTextComponentType;
  props: MicrositeTextComponentProps;
}

export type MicrositeImageComponentType = typeof MicrositeImageComponentType[keyof typeof MicrositeImageComponentType];


export const MicrositeImageComponentType = {
  image: 'image',
} as const;

export type MicrositeImageComponentPropsFit = typeof MicrositeImageComponentPropsFit[keyof typeof MicrositeImageComponentPropsFit];


export const MicrositeImageComponentPropsFit = {
  cover: 'cover',
  contain: 'contain',
} as const;

export type MicrositeImageComponentProps = {
  assetId: string;
  /** @maxLength 300 */
  alt: string;
  /** @maxLength 300 */
  caption?: string;
  fit: MicrositeImageComponentPropsFit;
};

export interface MicrositeImageComponent {
  id: string;
  type: MicrositeImageComponentType;
  props: MicrositeImageComponentProps;
}

export type MicrositeButtonComponentType = typeof MicrositeButtonComponentType[keyof typeof MicrositeButtonComponentType];


export const MicrositeButtonComponentType = {
  button: 'button',
} as const;

export type MicrositeButtonComponentPropsAppearance = typeof MicrositeButtonComponentPropsAppearance[keyof typeof MicrositeButtonComponentPropsAppearance];


export const MicrositeButtonComponentPropsAppearance = {
  primary: 'primary',
  outline: 'outline',
  link: 'link',
} as const;

export type MicrositeButtonComponentPropsAlignment = typeof MicrositeButtonComponentPropsAlignment[keyof typeof MicrositeButtonComponentPropsAlignment];


export const MicrositeButtonComponentPropsAlignment = {
  left: 'left',
  center: 'center',
  right: 'right',
} as const;

export type MicrositeButtonComponentProps = {
  /**
     * @minLength 1
     * @maxLength 80
     */
  label: string;
  /**
     * @minLength 1
     * @maxLength 2048
     */
  href: string;
  appearance: MicrositeButtonComponentPropsAppearance;
  alignment: MicrositeButtonComponentPropsAlignment;
  openInNewTab: boolean;
};

export interface MicrositeButtonComponent {
  id: string;
  type: MicrositeButtonComponentType;
  props: MicrositeButtonComponentProps;
}

export type MicrositeSpacerComponentType = typeof MicrositeSpacerComponentType[keyof typeof MicrositeSpacerComponentType];


export const MicrositeSpacerComponentType = {
  spacer: 'spacer',
} as const;

export type MicrositeSpacerComponentPropsSize = typeof MicrositeSpacerComponentPropsSize[keyof typeof MicrositeSpacerComponentPropsSize];


export const MicrositeSpacerComponentPropsSize = {
  small: 'small',
  medium: 'medium',
  large: 'large',
} as const;

export type MicrositeSpacerComponentProps = {
  size: MicrositeSpacerComponentPropsSize;
};

export interface MicrositeSpacerComponent {
  id: string;
  type: MicrositeSpacerComponentType;
  props: MicrositeSpacerComponentProps;
}

export type MicrositeDividerComponentType = typeof MicrositeDividerComponentType[keyof typeof MicrositeDividerComponentType];


export const MicrositeDividerComponentType = {
  divider: 'divider',
} as const;

export type MicrositeDividerComponentPropsStyle = typeof MicrositeDividerComponentPropsStyle[keyof typeof MicrositeDividerComponentPropsStyle];


export const MicrositeDividerComponentPropsStyle = {
  solid: 'solid',
  dashed: 'dashed',
} as const;

export type MicrositeDividerComponentProps = {
  style: MicrositeDividerComponentPropsStyle;
};

export interface MicrositeDividerComponent {
  id: string;
  type: MicrositeDividerComponentType;
  props: MicrositeDividerComponentProps;
}

export type MicrositeRichContentEmbedProvider = typeof MicrositeRichContentEmbedProvider[keyof typeof MicrositeRichContentEmbedProvider];


export const MicrositeRichContentEmbedProvider = {
  youtube: 'youtube',
  vimeo: 'vimeo',
  'google-maps': 'google-maps',
} as const;

export interface MicrositeRichContentEmbed {
  provider: MicrositeRichContentEmbedProvider;
  /** @maxLength 2048 */
  url: string;
  /**
     * @minLength 1
     * @maxLength 120
     */
  title: string;
}

export type MicrositeRichContentComponentType = typeof MicrositeRichContentComponentType[keyof typeof MicrositeRichContentComponentType];


export const MicrositeRichContentComponentType = {
  richContent: 'richContent',
} as const;

export type MicrositeRichContentComponentProps = {
  /** @maxLength 20000 */
  html: string;
  /** @maxItems 6 */
  embeds: MicrositeRichContentEmbed[];
};

export interface MicrositeRichContentComponent {
  id: string;
  type: MicrositeRichContentComponentType;
  props: MicrositeRichContentComponentProps;
}

export type MicrositeContactFormFieldType = typeof MicrositeContactFormFieldType[keyof typeof MicrositeContactFormFieldType];


export const MicrositeContactFormFieldType = {
  name: 'name',
  email: 'email',
  phone: 'phone',
  account: 'account',
  message: 'message',
} as const;

export interface MicrositeContactFormField {
  type: MicrositeContactFormFieldType;
  /**
     * @minLength 1
     * @maxLength 80
     */
  label: string;
  enabled: boolean;
  required: boolean;
}

export type MicrositeContactFormComponentType = typeof MicrositeContactFormComponentType[keyof typeof MicrositeContactFormComponentType];


export const MicrositeContactFormComponentType = {
  contactForm: 'contactForm',
} as const;

export type MicrositeContactFormComponentProps = {
  /**
     * @minLength 1
     * @maxLength 140
     */
  heading: string;
  /** @maxLength 400 */
  supportingText?: string;
  /**
     * @minLength 1
     * @maxLength 60
     */
  submitLabel: string;
  /**
     * @minLength 1
     * @maxLength 400
     */
  successMessage: string;
  /**
     * @minItems 1
     * @maxItems 5
     */
  fields: MicrositeContactFormField[];
};

export interface MicrositeContactFormComponent {
  id: string;
  type: MicrositeContactFormComponentType;
  props: MicrositeContactFormComponentProps;
}

export type MicrositeAiChatComponentType = typeof MicrositeAiChatComponentType[keyof typeof MicrositeAiChatComponentType];


export const MicrositeAiChatComponentType = {
  aiChat: 'aiChat',
} as const;

export type MicrositeAiChatComponentPropsDisplayMode = typeof MicrositeAiChatComponentPropsDisplayMode[keyof typeof MicrositeAiChatComponentPropsDisplayMode];


export const MicrositeAiChatComponentPropsDisplayMode = {
  inline: 'inline',
  floating: 'floating',
} as const;

export type MicrositeAiChatComponentProps = {
  /**
     * @minLength 1
     * @maxLength 140
     */
  heading: string;
  /**
     * @minLength 1
     * @maxLength 600
     */
  welcomeMessage: string;
  /**
     * @minLength 1
     * @maxLength 160
     */
  inputPlaceholder: string;
  /**
     * @minLength 1
     * @maxLength 60
     */
  submitLabel: string;
  handoffEnabled: boolean;
  /**
     * @minLength 1
     * @maxLength 80
     */
  handoffLabel: string;
  /**
     * @maxItems 6
     * @items.minLength 1
     * @items.maxLength 160
     */
  suggestedQuestions: string[];
  displayMode: MicrositeAiChatComponentPropsDisplayMode;
};

export interface MicrositeAiChatComponent {
  id: string;
  type: MicrositeAiChatComponentType;
  props: MicrositeAiChatComponentProps;
}

export type MicrositeClickToCallComponentType = typeof MicrositeClickToCallComponentType[keyof typeof MicrositeClickToCallComponentType];


export const MicrositeClickToCallComponentType = {
  clickToCall: 'clickToCall',
} as const;

export type MicrositeClickToCallComponentProps = {
  /**
     * @minLength 1
     * @maxLength 140
     */
  heading: string;
  /** @maxLength 400 */
  supportingText?: string;
  browserVoice: boolean;
  callbackRequest: boolean;
  tapToDial: boolean;
  /**
     * @minLength 1
     * @maxLength 80
     */
  browserVoiceLabel: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  callbackRequestLabel: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  tapToDialLabel: string;
};

export interface MicrositeClickToCallComponent {
  id: string;
  type: MicrositeClickToCallComponentType;
  props: MicrositeClickToCallComponentProps;
}

export type MicrositeVoiceAgentComponentType = typeof MicrositeVoiceAgentComponentType[keyof typeof MicrositeVoiceAgentComponentType];


export const MicrositeVoiceAgentComponentType = {
  voiceAgent: 'voiceAgent',
} as const;

export type MicrositeVoiceAgentComponentProps = {
  /**
     * @minLength 1
     * @maxLength 140
     */
  heading: string;
  /** @maxLength 400 */
  supportingText?: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  startLabel: string;
};

export interface MicrositeVoiceAgentComponent {
  id: string;
  type: MicrositeVoiceAgentComponentType;
  props: MicrositeVoiceAgentComponentProps;
}

export type MicrositeLayoutComponent = MicrositeHeaderComponent | MicrositeHeroComponent | MicrositeApprovedContentSectionsComponent | MicrositeSourcesFooterComponent | MicrositeTextComponent | MicrositeImageComponent | MicrositeButtonComponent | MicrositeSpacerComponent | MicrositeDividerComponent | MicrositeRichContentComponent | MicrositeContactFormComponent | MicrositeAiChatComponent | MicrositeClickToCallComponent | MicrositeVoiceAgentComponent;

export interface MicrositeLayoutColumn {
  id: string;
  width: MicrositeLayoutColumnWidth;
  /** @maxItems 50 */
  components: MicrositeLayoutComponent[];
}

export interface MicrositeLayoutRow {
  id: string;
  /**
     * @minItems 1
     * @maxItems 4
     */
  columns: MicrositeLayoutColumn[];
}

export interface MicrositeLayout {
  /**
     * @minItems 1
     * @maxItems 50
     */
  rows: MicrositeLayoutRow[];
}

export type MicrositeConfigurationDocumentSchemaVersion = typeof MicrositeConfigurationDocumentSchemaVersion[keyof typeof MicrositeConfigurationDocumentSchemaVersion];


export const MicrositeConfigurationDocumentSchemaVersion = {
  NUMBER_2: 2,
} as const;

export interface MicrositeConfigurationDocument {
  schemaVersion: MicrositeConfigurationDocumentSchemaVersion;
  /**
     * @minLength 1
     * @maxLength 160
     */
  headline: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  description: string;
  /** @maxLength 2048 */
  publicWebsiteUrl: string;
  brand: MicrositeBrandKit;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  startingInstruction: string;
  /**
     * @minItems 1
     * @maxItems 12
     */
  promptChoices: MicrositePromptChoice[];
  /** @maxItems 100 */
  approvedContent: MicrositeApprovedContentReference[];
  contact: MicrositeContactDetails;
  enquiryCallToAction: MicrositeEnquiryCallToAction;
  layout: MicrositeLayout;
}

export interface MicrositePublicIdentityWrite {
  /**
     * @minLength 3
     * @maxLength 80
     */
  slug: string;
  /** @maxLength 253 */
  canonicalHost?: string;
}

export interface MicrositeDraftWrite {
  /** @minimum 0 */
  revision: number;
  publicIdentity: MicrositePublicIdentityWrite;
  configuration: MicrositeConfigurationDocument;
}

export interface MicrositeRevisionWrite {
  /** @minimum 1 */
  revision: number;
}

export type MicrositeAdminResponsePublicIdentityStatus = typeof MicrositeAdminResponsePublicIdentityStatus[keyof typeof MicrositeAdminResponsePublicIdentityStatus];


export const MicrositeAdminResponsePublicIdentityStatus = {
  disabled: 'disabled',
  active: 'active',
} as const;

export type MicrositeAdminResponsePublicIdentity = MicrositePublicIdentityWrite & {
  status: MicrositeAdminResponsePublicIdentityStatus;
};

export interface MicrositeAdminResponse {
  id: string;
  entityId: string;
  publicIdentity: MicrositeAdminResponsePublicIdentity;
  /** @minimum 1 */
  revision: number;
  /** @minimum 1 */
  draftVersion: number;
  /**
     * @minimum 1
     * @nullable
     */
  publishedVersion?: number | null;
  isPublished: boolean;
  draft: MicrositeConfigurationDocument;
  published?: MicrositeConfigurationDocument | null;
  /** @nullable */
  publishedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type MicrositeVersionAction = typeof MicrositeVersionAction[keyof typeof MicrositeVersionAction];


export const MicrositeVersionAction = {
  draft_updated: 'draft_updated',
  published: 'published',
  unpublished: 'unpublished',
} as const;

export interface MicrositeVersion {
  id: string;
  /** @minimum 1 */
  revision: number;
  /** @minimum 1 */
  draftVersion: number;
  /**
     * @minimum 1
     * @nullable
     */
  publishedVersion?: number | null;
  action: MicrositeVersionAction;
  draft: MicrositeConfigurationDocument;
  published?: MicrositeConfigurationDocument | null;
  createdAt: string;
}

export interface MicrositeVersionList {
  items: MicrositeVersion[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type MicrositeSourceType = typeof MicrositeSourceType[keyof typeof MicrositeSourceType];


export const MicrositeSourceType = {
  website: 'website',
  document: 'document',
  manual: 'manual',
} as const;

export interface MicrositeSourceCreate {
  sourceType: MicrositeSourceType;
  /**
     * @minLength 1
     * @maxLength 160
     */
  label: string;
  /** @maxLength 2048 */
  url?: string;
  /**
     * @minLength 1
     * @maxLength 160000
     */
  content?: string;
}

export type MicrositeSourceStatus = typeof MicrositeSourceStatus[keyof typeof MicrositeSourceStatus];


export const MicrositeSourceStatus = {
  pending: 'pending',
  refreshing: 'refreshing',
  ready: 'ready',
  failed: 'failed',
} as const;

export interface MicrositeSource {
  id: string;
  sourceType: MicrositeSourceType;
  /**
     * @minLength 1
     * @maxLength 160
     */
  label: string;
  /** @nullable */
  url?: string | null;
  status: MicrositeSourceStatus;
  /** @minimum 1 */
  revision: number;
  /**
     * @minimum 1
     * @nullable
     */
  latestVersion?: number | null;
  /**
     * @minimum 1
     * @nullable
     */
  approvedVersion?: number | null;
  /** @nullable */
  lastRefreshRequestedAt?: string | null;
  /** @nullable */
  lastCompletedAt?: string | null;
  /** @nullable */
  lastErrorCode?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MicrositeSourceList {
  items: MicrositeSource[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface MicrositeSourcePage {
  url?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 160000
     */
  text: string;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
}

export type MicrositeSourceVersionReviewStatus = typeof MicrositeSourceVersionReviewStatus[keyof typeof MicrositeSourceVersionReviewStatus];


export const MicrositeSourceVersionReviewStatus = {
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
} as const;

export interface MicrositeSourceVersion {
  id: string;
  sourceId: string;
  /** @minimum 1 */
  version: number;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
  /**
     * @minItems 1
     * @maxItems 5
     */
  pages: MicrositeSourcePage[];
  /**
     * @minimum 1
     * @maximum 2097152
     */
  totalBytes: number;
  /**
     * @minimum 1
     * @maximum 5
     */
  pageCount: number;
  reviewStatus: MicrositeSourceVersionReviewStatus;
  fetchedAt: string;
  /** @nullable */
  reviewedAt?: string | null;
}

export interface MicrositeSourceVersionList {
  items: MicrositeSourceVersion[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface MicrositeSourceRevisionWrite {
  /** @minimum 1 */
  revision: number;
}

export type MicrositeSourceReviewWriteDecision = typeof MicrositeSourceReviewWriteDecision[keyof typeof MicrositeSourceReviewWriteDecision];


export const MicrositeSourceReviewWriteDecision = {
  approve: 'approve',
  reject: 'reject',
} as const;

export interface MicrositeSourceReviewWrite {
  /** @minimum 1 */
  revision: number;
  /** @minimum 1 */
  version: number;
  decision: MicrositeSourceReviewWriteDecision;
}

export interface MicrositePageRequest {
  promptChoiceId?: string;
}

export interface MicrositePublicPromptChoice {
  id: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  label: string;
}

export type MicrositePublicBrandKitHeadingFont = typeof MicrositePublicBrandKitHeadingFont[keyof typeof MicrositePublicBrandKitHeadingFont];


export const MicrositePublicBrandKitHeadingFont = {
  'system-sans': 'system-sans',
  'humanist-sans': 'humanist-sans',
  'editorial-serif': 'editorial-serif',
  monospace: 'monospace',
} as const;

export type MicrositePublicBrandKitBodyFont = typeof MicrositePublicBrandKitBodyFont[keyof typeof MicrositePublicBrandKitBodyFont];


export const MicrositePublicBrandKitBodyFont = {
  'system-sans': 'system-sans',
  'humanist-sans': 'humanist-sans',
  'editorial-serif': 'editorial-serif',
  monospace: 'monospace',
} as const;

export type MicrositePublicBrandKitTypeScale = typeof MicrositePublicBrandKitTypeScale[keyof typeof MicrositePublicBrandKitTypeScale];


export const MicrositePublicBrandKitTypeScale = {
  compact: 'compact',
  standard: 'standard',
  large: 'large',
} as const;

export type MicrositePublicBrandKitSpacing = typeof MicrositePublicBrandKitSpacing[keyof typeof MicrositePublicBrandKitSpacing];


export const MicrositePublicBrandKitSpacing = {
  compact: 'compact',
  standard: 'standard',
  generous: 'generous',
} as const;

export type MicrositePublicBrandKitContentWidth = typeof MicrositePublicBrandKitContentWidth[keyof typeof MicrositePublicBrandKitContentWidth];


export const MicrositePublicBrandKitContentWidth = {
  narrow: 'narrow',
  standard: 'standard',
  wide: 'wide',
} as const;

export type MicrositePublicBrandKitLogoTreatment = typeof MicrositePublicBrandKitLogoTreatment[keyof typeof MicrositePublicBrandKitLogoTreatment];


export const MicrositePublicBrandKitLogoTreatment = {
  natural: 'natural',
  contained: 'contained',
  monochrome: 'monochrome',
} as const;

export type MicrositePublicBrandKitLogoScale = typeof MicrositePublicBrandKitLogoScale[keyof typeof MicrositePublicBrandKitLogoScale];


export const MicrositePublicBrandKitLogoScale = {
  small: 'small',
  medium: 'medium',
  large: 'large',
} as const;

export type MicrositePublicBrandKitImageryTreatment = typeof MicrositePublicBrandKitImageryTreatment[keyof typeof MicrositePublicBrandKitImageryTreatment];


export const MicrositePublicBrandKitImageryTreatment = {
  natural: 'natural',
  soft: 'soft',
  'high-contrast': 'high-contrast',
  monochrome: 'monochrome',
} as const;

export type MicrositePublicBrandKitImageShape = typeof MicrositePublicBrandKitImageShape[keyof typeof MicrositePublicBrandKitImageShape];


export const MicrositePublicBrandKitImageShape = {
  square: 'square',
  rounded: 'rounded',
  pill: 'pill',
} as const;

export type MicrositePublicBrandKitButtonStyle = typeof MicrositePublicBrandKitButtonStyle[keyof typeof MicrositePublicBrandKitButtonStyle];


export const MicrositePublicBrandKitButtonStyle = {
  solid: 'solid',
  soft: 'soft',
  outline: 'outline',
} as const;

export type MicrositePublicBrandKitButtonShape = typeof MicrositePublicBrandKitButtonShape[keyof typeof MicrositePublicBrandKitButtonShape];


export const MicrositePublicBrandKitButtonShape = {
  square: 'square',
  rounded: 'rounded',
  pill: 'pill',
} as const;

export type MicrositePublicBrandKitButtonWeight = typeof MicrositePublicBrandKitButtonWeight[keyof typeof MicrositePublicBrandKitButtonWeight];


export const MicrositePublicBrandKitButtonWeight = {
  regular: 'regular',
  medium: 'medium',
  bold: 'bold',
} as const;

export type MicrositePublicBrandKitSurfaceStyle = typeof MicrositePublicBrandKitSurfaceStyle[keyof typeof MicrositePublicBrandKitSurfaceStyle];


export const MicrositePublicBrandKitSurfaceStyle = {
  flat: 'flat',
  bordered: 'bordered',
  elevated: 'elevated',
} as const;

export type MicrositePublicBrandKitBorderStyle = typeof MicrositePublicBrandKitBorderStyle[keyof typeof MicrositePublicBrandKitBorderStyle];


export const MicrositePublicBrandKitBorderStyle = {
  none: 'none',
  subtle: 'subtle',
  strong: 'strong',
} as const;

export type MicrositePublicBrandKitRadius = typeof MicrositePublicBrandKitRadius[keyof typeof MicrositePublicBrandKitRadius];


export const MicrositePublicBrandKitRadius = {
  none: 'none',
  small: 'small',
  medium: 'medium',
  large: 'large',
} as const;

export type MicrositePublicBrandKitResponsiveLayout = typeof MicrositePublicBrandKitResponsiveLayout[keyof typeof MicrositePublicBrandKitResponsiveLayout];


export const MicrositePublicBrandKitResponsiveLayout = {
  adaptive: 'adaptive',
  stacked: 'stacked',
} as const;

export type MicrositePublicBrandKitHeroAlignment = typeof MicrositePublicBrandKitHeroAlignment[keyof typeof MicrositePublicBrandKitHeroAlignment];


export const MicrositePublicBrandKitHeroAlignment = {
  left: 'left',
  center: 'center',
} as const;

export interface MicrositePublicBrandKit {
  /**
     * @maxLength 2048
     * @nullable
     */
  logoUrl: string | null;
  /**
     * @maxLength 2048
     * @nullable
     */
  heroUrl: string | null;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  primaryColor: string;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  backgroundColor: string;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  foregroundColor: string;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  accentColor?: string;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  surfaceColor?: string;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  mutedColor?: string;
  headingFont?: MicrositePublicBrandKitHeadingFont;
  bodyFont?: MicrositePublicBrandKitBodyFont;
  typeScale?: MicrositePublicBrandKitTypeScale;
  spacing?: MicrositePublicBrandKitSpacing;
  contentWidth?: MicrositePublicBrandKitContentWidth;
  logoTreatment?: MicrositePublicBrandKitLogoTreatment;
  logoScale?: MicrositePublicBrandKitLogoScale;
  imageryTreatment?: MicrositePublicBrandKitImageryTreatment;
  imageShape?: MicrositePublicBrandKitImageShape;
  buttonStyle?: MicrositePublicBrandKitButtonStyle;
  buttonShape?: MicrositePublicBrandKitButtonShape;
  buttonWeight?: MicrositePublicBrandKitButtonWeight;
  surfaceStyle?: MicrositePublicBrandKitSurfaceStyle;
  borderStyle?: MicrositePublicBrandKitBorderStyle;
  radius?: MicrositePublicBrandKitRadius;
  responsiveLayout?: MicrositePublicBrandKitResponsiveLayout;
  heroAlignment?: MicrositePublicBrandKitHeroAlignment;
}

export interface MicrositePublicSite {
  /**
     * @minLength 3
     * @maxLength 80
     */
  slug: string;
  /**
     * @minLength 1
     * @maxLength 2048
     */
  canonicalUrl: string;
  /**
     * @minLength 1
     * @maxLength 160
     */
  headline: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  description: string;
  /** @maxLength 2048 */
  publicWebsiteUrl: string;
  brand: MicrositePublicBrandKit;
  /**
     * @minItems 1
     * @maxItems 12
     */
  promptChoices: MicrositePublicPromptChoice[];
  contact: MicrositeContactDetails;
  enquiryCallToAction: MicrositeEnquiryCallToAction;
  layout: MicrositeLayout;
  aiEnabled: boolean;
  /** @minimum 1 */
  publishedVersion: number;
  publishedAt: string;
}

export interface MicrositePreviewRequest {
  /** @minimum 1 */
  configurationVersion: number;
  configuration: MicrositeConfigurationDocument;
  promptChoiceId?: string;
}

export interface MicrositePageSource {
  /**
     * @minLength 1
     * @maxLength 80
     */
  id: string;
  /**
     * @minLength 1
     * @maxLength 160
     */
  label: string;
  url?: string;
  /** @minimum 1 */
  version: number;
}

export interface MicrositePageSection {
  /**
     * @maxLength 60
     * @pattern ^[a-z0-9]+(?:-[a-z0-9]+)*$
     */
  id: string;
  /**
     * @minLength 1
     * @maxLength 120
     */
  heading: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  body: string;
  /**
     * @maxItems 6
     * @items.minLength 1
     * @items.maxLength 240
     */
  highlights: string[];
  /**
     * @maxItems 12
     * @items.minLength 1
     * @items.maxLength 80
     */
  citationIds: string[];
}

export type MicrositePageCompositionGeneratedBy = typeof MicrositePageCompositionGeneratedBy[keyof typeof MicrositePageCompositionGeneratedBy];


export const MicrositePageCompositionGeneratedBy = {
  ai: 'ai',
  fallback: 'fallback',
} as const;

export interface MicrositePageComposition {
  /**
     * @minLength 1
     * @maxLength 160
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 600
     */
  intro: string;
  /**
     * @minItems 1
     * @maxItems 8
     */
  sections: MicrositePageSection[];
  /** @maxItems 100 */
  sources: MicrositePageSource[];
  generatedBy: MicrositePageCompositionGeneratedBy;
  /** @maxLength 300 */
  notice?: string;
  /** @minimum 1 */
  configurationVersion: number;
  /** @nullable */
  promptChoiceId: string | null;
  /**
     * @maxLength 80
     * @nullable
     */
  promptLabel: string | null;
}

export type HomepageTab = typeof HomepageTab[keyof typeof HomepageTab];


export const HomepageTab = {
  all: 'all',
  b2c: 'b2c',
  b2b: 'b2b',
  partner: 'partner',
  investor: 'investor',
} as const;

export type HomepageSectionId = typeof HomepageSectionId[keyof typeof HomepageSectionId];


export const HomepageSectionId = {
  'daily-workflow': 'daily-workflow',
  'daily-briefing': 'daily-briefing',
  'briefing-insights': 'briefing-insights',
  'ask-ai': 'ask-ai',
  'workspace-overview': 'workspace-overview',
  'next-actions': 'next-actions',
  'needs-attention': 'needs-attention',
  'pipeline-snapshot': 'pipeline-snapshot',
} as const;

export type HomepageComponentId = HomepageSectionId | string;

export type HomepageLayoutItemColumnSpan = typeof HomepageLayoutItemColumnSpan[keyof typeof HomepageLayoutItemColumnSpan];


export const HomepageLayoutItemColumnSpan = {
  NUMBER_1: 1,
  NUMBER_4: 4,
  NUMBER_6: 6,
  NUMBER_12: 12,
} as const;

export interface HomepageLayoutItem {
  componentId: HomepageComponentId;
  /**
     * @minimum 0
     * @maximum 11
     */
  column: number;
  /**
     * @minimum 0
     * @maximum 199
     */
  row: number;
  columnSpan: HomepageLayoutItemColumnSpan;
  /**
     * @minimum 1
     * @maximum 12
     */
  rowSpan: number;
  visible: boolean;
  /** Whether the section content is collapsed for this viewport */
  collapsed: boolean;
}

export type HomepageRequiredItemColumnSpan = typeof HomepageRequiredItemColumnSpan[keyof typeof HomepageRequiredItemColumnSpan];


export const HomepageRequiredItemColumnSpan = {
  NUMBER_1: 1,
  NUMBER_4: 4,
  NUMBER_6: 6,
  NUMBER_12: 12,
} as const;

export interface HomepageRequiredItem {
  componentId: HomepageSectionId;
  /**
     * @minimum 0
     * @maximum 11
     */
  column: number;
  /**
     * @minimum 0
     * @maximum 199
     */
  row: number;
  columnSpan: HomepageRequiredItemColumnSpan;
  /**
     * @minimum 1
     * @maximum 12
     */
  rowSpan: number;
}

export type HomepageWidgetInstanceConfig = { [key: string]: unknown };

export type CustomPageWidgetType = typeof CustomPageWidgetType[keyof typeof CustomPageWidgetType];


export const CustomPageWidgetType = {
  text: 'text',
  quick_links: 'quick_links',
  kpi: 'kpi',
  pipeline_chart: 'pipeline_chart',
  recent_records: 'recent_records',
} as const;

export interface HomepageWidgetInstance {
  instanceId: string;
  type: CustomPageWidgetType;
  /** @maxLength 300 */
  title?: string;
  config: HomepageWidgetInstanceConfig;
}

export type HomepageLayoutDocumentSchemaVersion = typeof HomepageLayoutDocumentSchemaVersion[keyof typeof HomepageLayoutDocumentSchemaVersion];


export const HomepageLayoutDocumentSchemaVersion = {
  NUMBER_3: 3,
} as const;

export interface HomepageLayoutDocument {
  schemaVersion: HomepageLayoutDocumentSchemaVersion;
  /** @maxItems 50 */
  widgets: HomepageWidgetInstance[];
  /**
     * @minItems 8
     * @maxItems 58
     */
  desktop: HomepageLayoutItem[];
  /**
     * @minItems 8
     * @maxItems 58
     */
  mobile: HomepageLayoutItem[];
}

export interface HomepageLayoutWrite {
  /** @minimum 0 */
  version: number;
  layout: HomepageLayoutDocument;
}

export interface HomepageLayoutReset {
  /** @minimum 0 */
  version: number;
}

export type HomepageLayoutResponse = HomepageLayoutDocument & ({
  tab: HomepageTab;
  /** @minimum 0 */
  version: number;
  /** @minimum 0 */
  policyVersion: number;
  requiredComponentIds: HomepageSectionId[];
  reconciled: boolean;
  /** @nullable */
  updatedAt: string | null;
});

export type HomepagePolicyDocumentSchemaVersion = typeof HomepagePolicyDocumentSchemaVersion[keyof typeof HomepagePolicyDocumentSchemaVersion];


export const HomepagePolicyDocumentSchemaVersion = {
  NUMBER_3: 3,
} as const;

export interface HomepagePolicyDocument {
  schemaVersion: HomepagePolicyDocumentSchemaVersion;
  /** @maxItems 8 */
  desktop: HomepageRequiredItem[];
  /** @maxItems 8 */
  mobile: HomepageRequiredItem[];
}

export interface HomepagePolicyWrite {
  /** @minimum 0 */
  version: number;
  policy: HomepagePolicyDocument;
}

export type HomepagePolicyResponse = HomepagePolicyDocument & ({
  /** @minimum 0 */
  version: number;
  /** @nullable */
  updatedAt: string | null;
});

export type NavigationView = typeof NavigationView[keyof typeof NavigationView];


export const NavigationView = {
  all: 'all',
  b2c: 'b2c',
  b2b: 'b2b',
  partner: 'partner',
  investor: 'investor',
} as const;

export type NavigationIconName = typeof NavigationIconName[keyof typeof NavigationIconName];


export const NavigationIconName = {
  'layout-dashboard': 'layout-dashboard',
  'file-text': 'file-text',
  'bar-chart': 'bar-chart',
  users: 'users',
  briefcase: 'briefcase',
  links: 'links',
  globe: 'globe',
} as const;

export type NavigationStandardItemKind = typeof NavigationStandardItemKind[keyof typeof NavigationStandardItemKind];


export const NavigationStandardItemKind = {
  standard: 'standard',
} as const;

export interface NavigationStandardItem {
  kind: NavigationStandardItemKind;
  /**
     * @minLength 1
     * @maxLength 100
     */
  key: string;
  /**
     * @minLength 1
     * @maxLength 60
     */
  title?: string;
  icon?: NavigationIconName;
  hidden: boolean;
}

export type NavigationCustomPageItemKind = typeof NavigationCustomPageItemKind[keyof typeof NavigationCustomPageItemKind];


export const NavigationCustomPageItemKind = {
  'custom-page': 'custom-page',
} as const;

export interface NavigationCustomPageItem {
  kind: NavigationCustomPageItemKind;
  pageId: string;
  /**
     * @minLength 1
     * @maxLength 60
     */
  title?: string;
  icon?: NavigationIconName;
  hidden: boolean;
}

export type NavigationExternalItemKind = typeof NavigationExternalItemKind[keyof typeof NavigationExternalItemKind];


export const NavigationExternalItemKind = {
  external: 'external',
} as const;

export interface NavigationExternalItem {
  kind: NavigationExternalItemKind;
  /**
     * @minLength 1
     * @maxLength 100
     */
  id: string;
  /**
     * @minLength 1
     * @maxLength 60
     */
  title: string;
  icon: NavigationIconName;
  /**
     * @maxLength 2048
     * @pattern ^https://
     */
  href: string;
}

export type NavigationRouteItemKind = typeof NavigationRouteItemKind[keyof typeof NavigationRouteItemKind];


export const NavigationRouteItemKind = {
  route: 'route',
} as const;

export interface NavigationRouteItem {
  kind: NavigationRouteItemKind;
  /**
     * @minLength 1
     * @maxLength 100
     */
  id: string;
  /**
     * @minLength 1
     * @maxLength 60
     */
  title: string;
  icon: NavigationIconName;
  /**
     * @minLength 1
     * @maxLength 2048
     */
  to: string;
}

export type NavigationLayoutItem = NavigationStandardItem | NavigationCustomPageItem | NavigationExternalItem | NavigationRouteItem;

export interface NavigationLayoutSection {
  /**
     * @minLength 1
     * @maxLength 100
     */
  id: string;
  /**
     * @minLength 1
     * @maxLength 40
     */
  heading: string;
  /** @maxItems 500 */
  items: NavigationLayoutItem[];
}

export interface NavigationLayoutDocument {
  /** @maxItems 50 */
  sections: NavigationLayoutSection[];
}

export interface NavigationLayoutsByView {
  all: NavigationLayoutDocument | null;
  b2c: NavigationLayoutDocument | null;
  b2b: NavigationLayoutDocument | null;
  partner: NavigationLayoutDocument | null;
  investor: NavigationLayoutDocument | null;
}

export interface NavigationVersionsByView {
  /** @minimum 0 */
  all: number;
  /** @minimum 0 */
  b2c: number;
  /** @minimum 0 */
  b2b: number;
  /** @minimum 0 */
  partner: number;
  /** @minimum 0 */
  investor: number;
}

export interface NavigationLayoutsResponse {
  layouts: NavigationLayoutsByView;
  versions: NavigationVersionsByView;
}

export interface NavigationLayoutWrite {
  document: NavigationLayoutDocument;
  /** @minimum 0 */
  expectedVersion: number;
  /**
     * @minLength 10
     * @maxLength 500
     */
  reason: string;
}

export interface NavigationLayoutCopy {
  /** Optional loaded versions for source and targets; rejects concurrent changes. */
  expectedVersions?: NavigationVersionsByView;
  /**
     * @minItems 1
     * @maxItems 4
     */
  toViews: NavigationView[];
  /**
     * @minLength 10
     * @maxLength 500
     */
  reason: string;
}

export interface NavigationLayoutReset {
  /**
     * Optional loaded version; rejects concurrent changes.
     * @minimum 0
     */
  expectedVersion?: number;
  /**
     * @minLength 10
     * @maxLength 500
     */
  reason: string;
}

export interface NavigationLayoutMutationResponse {
  document: NavigationLayoutDocument | null;
  /** @minimum 0 */
  version: number;
  /** @nullable */
  updatedAt?: string | null;
}

export interface NavigationCatalogueItem {
  key: string;
  defaultTitle: string;
  iconName: NavigationIconName;
  to: string;
  permissions?: string[];
  moduleKey?: string;
  views: NavigationView[];
}

export interface NavigationCustomPageSummary {
  id: string;
  title: string;
  /** @nullable */
  icon: string | null;
}

export interface NavigationCatalogue {
  items: NavigationCatalogueItem[];
  iconNames: NavigationIconName[];
  customPages: NavigationCustomPageSummary[];
}

export type LegacySourceRecordPayload = { [key: string]: unknown };

export interface LegacySourceRecord {
  id: string;
  domain: string;
  sourceTable: string;
  legacyId: string;
  title: string;
  /** @nullable */
  summary?: string | null;
  /** @nullable */
  targetTable?: string | null;
  /** @nullable */
  targetId?: string | null;
  payload: LegacySourceRecordPayload;
  /** @nullable */
  sourceCreatedAt?: string | null;
  /** @nullable */
  sourceUpdatedAt?: string | null;
  importedAt: string;
  updatedAt: string;
}

export interface LegacySourceRecordList {
  items: LegacySourceRecord[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  page: number;
  /** @minimum 1 */
  limit: number;
}

export interface LegacySourceRecordCount {
  domain: string;
  sourceTable: string;
  /** @minimum 0 */
  count: number;
}

export interface LegacySourceRecordStats {
  /** @minimum 0 */
  total: number;
  groups: LegacySourceRecordCount[];
}

export type QuoteRequestStatus = typeof QuoteRequestStatus[keyof typeof QuoteRequestStatus];


export const QuoteRequestStatus = {
  draft: 'draft',
  submitted: 'submitted',
  in_review: 'in_review',
  priced: 'priced',
  pending_approval: 'pending_approval',
  sent: 'sent',
  accepted: 'accepted',
  declined: 'declined',
  expired: 'expired',
} as const;

export type QuoteApprovalStatus = typeof QuoteApprovalStatus[keyof typeof QuoteApprovalStatus];


export const QuoteApprovalStatus = {
  not_required: 'not_required',
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
} as const;

export type QuoteRequestSource = typeof QuoteRequestSource[keyof typeof QuoteRequestSource];


export const QuoteRequestSource = {
  staff: 'staff',
  guest: 'guest',
  ai: 'ai',
} as const;

export type QuoteActorKind = typeof QuoteActorKind[keyof typeof QuoteActorKind];


export const QuoteActorKind = {
  staff: 'staff',
  ai: 'ai',
  customer: 'customer',
  system: 'system',
} as const;

export type QuoteEventType = typeof QuoteEventType[keyof typeof QuoteEventType];


export const QuoteEventType = {
  created: 'created',
  updated: 'updated',
  priced: 'priced',
  approval_requested: 'approval_requested',
  approved: 'approved',
  rejected: 'rejected',
  sent: 'sent',
  viewed: 'viewed',
  accepted: 'accepted',
  declined: 'declined',
  expired: 'expired',
  ai_drafted: 'ai_drafted',
} as const;

export type QuoteItemType = typeof QuoteItemType[keyof typeof QuoteItemType];


export const QuoteItemType = {
  solution: 'solution',
  product: 'product',
  module: 'module',
  service: 'service',
} as const;

export type QuoteKind = typeof QuoteKind[keyof typeof QuoteKind];


export const QuoteKind = {
  quote: 'quote',
  estimate: 'estimate',
  tender: 'tender',
} as const;

export type QuoteItemInclusion = typeof QuoteItemInclusion[keyof typeof QuoteItemInclusion];


export const QuoteItemInclusion = {
  included: 'included',
  optional: 'optional',
  alternative: 'alternative',
} as const;

export interface QuoteRequestBase {
  kind?: QuoteKind;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  opportunityId?: string | null;
  /** @nullable */
  priceListId?: string | null;
  /** @nullable */
  requesterName?: string | null;
  /** @nullable */
  requesterEmail?: string | null;
  /** @nullable */
  requesterPhone?: string | null;
  /** @nullable */
  requesterOrganization?: string | null;
  /** @nullable */
  notes?: string | null;
  /** @pattern ^[A-Z]{3}$ */
  currency?: string;
  /** @pattern ^\d+(\.\d+)?$ */
  discountPct?: string;
  /** @nullable */
  termsMd?: string | null;
  /**
     * @maxLength 20000
     * @nullable
     */
  assumptionsMd?: string | null;
  /**
     * @maxLength 20000
     * @nullable
     */
  exclusionsMd?: string | null;
  /** @pattern ^\d+(\.\d+)?$ */
  contingencyPct?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  contingencyLabel?: string;
  /** @nullable */
  validUntil?: string | null;
  /** @nullable */
  expiresAt?: string | null;
}

export type TenderStatus = typeof TenderStatus[keyof typeof TenderStatus];


export const TenderStatus = {
  draft: 'draft',
  in_preparation: 'in_preparation',
  in_review: 'in_review',
  changes_requested: 'changes_requested',
  approved: 'approved',
  submitted: 'submitted',
  closed: 'closed',
  archived: 'archived',
} as const;

export type TenderStorageProvider = typeof TenderStorageProvider[keyof typeof TenderStorageProvider];


export const TenderStorageProvider = {
  application: 'application',
  sharepoint: 'sharepoint',
  sharepoint_embedded: 'sharepoint_embedded',
} as const;

export type TenderOutputKind = typeof TenderOutputKind[keyof typeof TenderOutputKind];


export const TenderOutputKind = {
  standalone_response: 'standalone_response',
  completed_buyer_template: 'completed_buyer_template',
  converted_template: 'converted_template',
  compliance_matrix: 'compliance_matrix',
} as const;

export type TenderEventType = typeof TenderEventType[keyof typeof TenderEventType];


export const TenderEventType = {
  created: 'created',
  updated: 'updated',
  status_changed: 'status_changed',
  quote_linked: 'quote_linked',
  quote_unlinked: 'quote_unlinked',
  submitted: 'submitted',
  archived: 'archived',
} as const;

export interface TenderBrowseFile {
  key: string;
  title: string;
  categories: string[];
  locations: string[];
  status: string;
  updatedAt: string;
  /** @nullable */
  contentType: string | null;
  /** @nullable */
  sizeBytes: number | null;
  /** @nullable */
  documentId: string | null;
  /** @nullable */
  libraryEntryId: string | null;
  /** @nullable */
  sourceId: string | null;
  /** @nullable */
  outputId: string | null;
  /** @nullable */
  knowledgeId: string | null;
}

export type TenderSourceKind = typeof TenderSourceKind[keyof typeof TenderSourceKind];


export const TenderSourceKind = {
  instructions: 'instructions',
  buyer_template: 'buyer_template',
  addendum: 'addendum',
  supporting_collateral: 'supporting_collateral',
} as const;

export type TenderRequirementReviewStatus = typeof TenderRequirementReviewStatus[keyof typeof TenderRequirementReviewStatus];


export const TenderRequirementReviewStatus = {
  proposed: 'proposed',
  confirmed: 'confirmed',
  rejected: 'rejected',
} as const;

export type TenderRequirementCoverageStatus = typeof TenderRequirementCoverageStatus[keyof typeof TenderRequirementCoverageStatus];


export const TenderRequirementCoverageStatus = {
  unaddressed: 'unaddressed',
  partial: 'partial',
  covered: 'covered',
  not_applicable: 'not_applicable',
} as const;

export type TenderAiModelAvailability = typeof TenderAiModelAvailability[keyof typeof TenderAiModelAvailability];


export const TenderAiModelAvailability = {
  configured_unverified: 'configured_unverified',
  disabled: 'disabled',
  unavailable: 'unavailable',
} as const;

export type TenderAiConnectionMode = typeof TenderAiConnectionMode[keyof typeof TenderAiConnectionMode];


export const TenderAiConnectionMode = {
  'cdf-managed': 'cdf-managed',
  'byo-direct': 'byo-direct',
} as const;

export type TenderAiTokenParameter = typeof TenderAiTokenParameter[keyof typeof TenderAiTokenParameter];


export const TenderAiTokenParameter = {
  max_tokens: 'max_tokens',
  max_completion_tokens: 'max_completion_tokens',
} as const;

export interface TenderAiModelChoice {
  /** @pattern ^[a-z0-9][a-z0-9-]{0,63}$ */
  choiceId: string;
  /**
     * @minLength 1
     * @maxLength 120
     */
  displayName: string;
  /** @maxLength 500 */
  description: string;
  isDefault: boolean;
  availability: TenderAiModelAvailability;
  /**
     * @maxLength 500
     * @nullable
     */
  disabledReason?: string | null;
  reasoningEfforts: TenderAiReasoningEffort[];
  defaultReasoningEffort: TenderAiReasoningEffort | null;
  /**
     * @minimum 1
     * @nullable
     */
  contextWindowTokens: number | null;
  /**
     * @minimum 1
     * @maximum 131072
     */
  maxOutputTokens: number;
  /**
     * @minimum 1000
     * @maximum 120000
     */
  maxTimeoutMs: number;
  pricingEstimateAvailable: boolean;
  connectionMode: TenderAiConnectionMode;
  tokenParameter: TenderAiTokenParameter;
}

export type TenderAiModelCatalogueCatalogueSource = typeof TenderAiModelCatalogueCatalogueSource[keyof typeof TenderAiModelCatalogueCatalogueSource];


export const TenderAiModelCatalogueCatalogueSource = {
  project_default: 'project_default',
  server_allowlist: 'server_allowlist',
  invalid_configuration: 'invalid_configuration',
} as const;

export interface TenderAiModelCatalogue {
  /** @maxItems 20 */
  data: TenderAiModelChoice[];
  /** @nullable */
  defaultChoiceId: string | null;
  /** @minimum 0 */
  availableChoiceCount: number;
  multiModelAvailable: boolean;
  connectionMode: TenderAiConnectionMode;
  catalogueSource: TenderAiModelCatalogueCatalogueSource;
  /** False until an authorized provider request verifies the configured route. */
  liveAvailabilityVerified: boolean;
  message: string;
}

export type TenderRunKind = typeof TenderRunKind[keyof typeof TenderRunKind];


export const TenderRunKind = {
  requirement_extraction: 'requirement_extraction',
  outline: 'outline',
  section_draft: 'section_draft',
  rewrite: 'rewrite',
  whole_response: 'whole_response',
  compliance_check: 'compliance_check',
} as const;

export type TenderRunStatus = typeof TenderRunStatus[keyof typeof TenderRunStatus];


export const TenderRunStatus = {
  queued: 'queued',
  running: 'running',
  succeeded: 'succeeded',
  failed: 'failed',
  cancelled: 'cancelled',
} as const;

export type TenderProposalStatus = typeof TenderProposalStatus[keyof typeof TenderProposalStatus];


export const TenderProposalStatus = {
  pending: 'pending',
  accepted: 'accepted',
  rejected: 'rejected',
  stale: 'stale',
} as const;

export type TenderReviewStatus = typeof TenderReviewStatus[keyof typeof TenderReviewStatus];


export const TenderReviewStatus = {
  pending: 'pending',
  approved: 'approved',
  changes_requested: 'changes_requested',
  invalidated: 'invalidated',
  cancelled: 'cancelled',
} as const;

export type TenderExportStatus = typeof TenderExportStatus[keyof typeof TenderExportStatus];


export const TenderExportStatus = {
  queued: 'queued',
  generated: 'generated',
  saved: 'saved',
  failed: 'failed',
} as const;

export type TenderExportPurpose = typeof TenderExportPurpose[keyof typeof TenderExportPurpose];


export const TenderExportPurpose = {
  draft: 'draft',
  approved: 'approved',
} as const;

export interface TenderProviderLocation {
  provider: TenderStorageProvider;
  connectionId?: string;
  tenantId?: string;
  containerTypeId?: string;
  siteId?: string;
  driveId?: string;
  folderItemId?: string;
  path?: string;
  containerId?: string;
  itemId?: string;
  versionId?: string;
  eTag?: string;
  webUrl?: string;
}

export type TenderSharePointAuthStatus = typeof TenderSharePointAuthStatus[keyof typeof TenderSharePointAuthStatus];


export const TenderSharePointAuthStatus = {
  not_connected: 'not_connected',
  connected: 'connected',
  reauth_required: 'reauth_required',
  denied: 'denied',
} as const;

export interface TenderSharePointReadiness {
  /** @nullable */
  connectionId: string | null;
  authStatus: TenderSharePointAuthStatus;
  destinationConfigured: boolean;
  /** @nullable */
  accountDisplayName: string | null;
  /** @nullable */
  siteId: string | null;
  /** @nullable */
  siteName: string | null;
  /** @nullable */
  siteUrl: string | null;
  /** @nullable */
  driveId: string | null;
  /** @nullable */
  driveName: string | null;
  /** @nullable */
  folderItemId: string | null;
  folderPath: string;
  /** @minimum 0 */
  destinationRevision: number;
  /** @nullable */
  lastVerifiedAt: string | null;
  /** @nullable */
  setupMessage: string | null;
  /** @nullable */
  lastErrorCode: string | null;
  /** @nullable */
  lastError: string | null;
  /** @nullable */
  updatedAt: string | null;
}

export interface TenderSharePointAuthorization {
  authorizationUrl: string;
}

export interface TenderEmbeddedReadiness {
  /** @nullable */
  connectionId: string | null;
  platformConfigured: boolean;
  authStatus: TenderSharePointAuthStatus;
  destinationConfigured: boolean;
  /** @nullable */
  accountDisplayName: string | null;
  /** @nullable */
  tenantId: string | null;
  /** @nullable */
  containerTypeId: string | null;
  /** @nullable */
  containerId: string | null;
  /** @nullable */
  driveId: string | null;
  /** @nullable */
  containerName: string | null;
  /** @nullable */
  containerWebUrl: string | null;
  /** @minimum 0 */
  destinationRevision: number;
  /** @nullable */
  lastVerifiedAt: string | null;
  /** @nullable */
  setupMessage: string | null;
  /** @nullable */
  lastErrorCode: string | null;
  /** @nullable */
  lastError: string | null;
  /** @nullable */
  updatedAt: string | null;
}

export interface TenderEmbeddedDestinationUpdate {
  /**
     * @minLength 1
     * @maxLength 512
     */
  containerId: string;
  /** @minimum 0 */
  expectedDestinationRevision: number;
}

export interface TenderEmbeddedContainer {
  id: string;
  displayName: string;
  containerTypeId: string;
  status: string;
  /** @nullable */
  webUrl: string | null;
  selectable: boolean;
}

export interface TenderEmbeddedContainerPage {
  items: TenderEmbeddedContainer[];
  /** @nullable */
  nextCursor: string | null;
}

export interface TenderSharePointDestinationUpdate {
  /**
     * @minLength 1
     * @maxLength 512
     */
  siteId: string;
  /**
     * @minLength 1
     * @maxLength 512
     */
  driveId: string;
  /**
     * @minLength 1
     * @maxLength 512
     * @nullable
     */
  folderItemId?: string | null;
  /** @maxLength 1000 */
  folderPath?: string;
  /** @minimum 0 */
  expectedDestinationRevision: number;
}

export interface TenderSharePointSite {
  id: string;
  name: string;
  displayName: string;
  webUrl: string;
}

export interface TenderSharePointDrive {
  id: string;
  name: string;
  webUrl: string;
  driveType: string;
}

export interface TenderSharePointFolder {
  id: string;
  name: string;
  webUrl: string;
  /** @minimum 0 */
  childCount: number;
}

export interface TenderSharePointSitePage {
  items: TenderSharePointSite[];
  /** @nullable */
  nextCursor: string | null;
}

export interface TenderSharePointDrivePage {
  items: TenderSharePointDrive[];
  /** @nullable */
  nextCursor: string | null;
}

export interface TenderSharePointFolderPage {
  items: TenderSharePointFolder[];
  /** @nullable */
  nextCursor: string | null;
}

export interface TenderOfficeLaunch {
  url: string;
  itemId: string;
  expectedETag: string;
  note: string;
}

export interface TenderSharePointReconcile {
  /** @minimum 1 */
  expectedSourceRevision: number;
  /**
     * @minLength 1
     * @maxLength 500
     */
  expectedETag: string;
}

export type TenderSourceDescriptor = TenderProviderLocation & {
  /**
     * @minLength 1
     * @maxLength 500
     */
  filename: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  contentType: string;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
  capturedAt: string;
  /**
     * @minLength 1
     * @maxLength 300
     */
  sourceRevision: string;
};

export type TenderSettingsProviderConfiguration = {[key: string]: TenderProviderLocation};

export interface TenderSettings {
  id: string;
  /** @pattern ^[A-Z0-9][A-Z0-9-]{0,11}$ */
  referencePrefix: string;
  defaultStorageProvider: TenderStorageProvider;
  providerConfiguration: TenderSettingsProviderConfiguration;
  /** Send the daily tender opportunity digest email to tender staff. */
  digestEnabled: boolean;
  /**
     * Local hour (24-hour clock) from which the daily digest is sent.
     * @minimum 0
     * @maximum 23
     */
  digestHour: number;
  /**
     * IANA timezone used for the digest send hour and day.
     * @minLength 1
     * @maxLength 64
     */
  digestTimezone: string;
  /**
     * Bid rules combined with the Offerings Profile when tenders are ranked. Edited in the Web Signals Targeting step and on tender settings.
     * @maxLength 8000
     */
  bidRulesPrompt: string;
  /**
     * Revision of the bid rules prompt; 0 until first saved.
     * @minimum 0
     */
  bidRulesRevision: number;
  /** @nullable */
  bidRulesUpdatedAt: string | null;
  /** @minimum 1 */
  revision: number;
  updatedAt: string;
}

export type TenderSettingsUpdateProviderConfiguration = {[key: string]: TenderProviderLocation};

export interface TenderSettingsUpdate {
  /** @minimum 0 */
  expectedRevision: number;
  /** @pattern ^[A-Z0-9][A-Z0-9-]{0,11}$ */
  referencePrefix: string;
  defaultStorageProvider: TenderStorageProvider;
  providerConfiguration: TenderSettingsUpdateProviderConfiguration;
  /** Omit to keep the current value. */
  digestEnabled?: boolean;
  /**
     * Omit to keep the current value.
     * @minimum 0
     * @maximum 23
     */
  digestHour?: number;
  /**
     * IANA timezone such as Australia/Sydney. Omit to keep the current value.
     * @minLength 1
     * @maxLength 64
     */
  digestTimezone?: string;
}

export interface TenderBidRulesUpdate {
  /**
     * The bidRulesRevision that was loaded.
     * @minimum 0
     */
  expectedBidRulesRevision: number;
  /** @maxLength 8000 */
  bidRulesPrompt: string;
}

export interface TenderDigestPreference {
  /** True when the caller has turned off the daily digest for this entity. */
  optedOut: boolean;
  /** True when the caller holds tender write access and so is a digest recipient. */
  eligible: boolean;
}

export interface TenderDigestPreferenceUpdate {
  optedOut: boolean;
}

/**
 * @nullable
 */
export type TenderOpportunityDigestItemMatchBand = typeof TenderOpportunityDigestItemMatchBand[keyof typeof TenderOpportunityDigestItemMatchBand] | null;


export const TenderOpportunityDigestItemMatchBand = {
  strong: 'strong',
  possible: 'possible',
  low: 'low',
} as const;

export interface TenderOpportunityDigestItem {
  id: string;
  title: string;
  /** @nullable */
  buyerName: string | null;
  /** @nullable */
  matchBand: TenderOpportunityDigestItemMatchBand;
  /**
     * @minimum 0
     * @maximum 100
     * @nullable
     */
  matchScore: number | null;
  /** @nullable */
  closesAt: string | null;
  /** Deep link to the opportunity in the app. */
  link: string;
  /** Short plain-language line shown under the title. */
  detail: string;
}

export interface TenderOpportunityDigestPreview {
  localDate: string;
  /**
     * @minimum 0
     * @maximum 23
     */
  sendHour: number;
  timezone: string;
  enabled: boolean;
  optedOut: boolean;
  eligible: boolean;
  alreadySentToday: boolean;
  /** True when today's digest would be emailed to the caller: enabled, eligible, not opted out, not already sent and not empty. */
  wouldSend: boolean;
  /** @minimum 0 */
  itemCount: number;
  /** Changes after this time count as new. */
  since: string;
  subject: string;
  html: string;
  text: string;
  /** @maxItems 25 */
  newMatches: TenderOpportunityDigestItem[];
  /** @maxItems 25 */
  closingSoon: TenderOpportunityDigestItem[];
  /** @maxItems 25 */
  addenda: TenderOpportunityDigestItem[];
}

export type ProposalTypeSuggestionType = typeof ProposalTypeSuggestionType[keyof typeof ProposalTypeSuggestionType];


export const ProposalTypeSuggestionType = {
  tender: 'tender',
  sow: 'sow',
  inconclusive: 'inconclusive',
} as const;

export interface ProposalTypeSuggestion {
  type: ProposalTypeSuggestionType;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  explanation: string;
  /**
     * @maxItems 20
     * @items.maxLength 100
     */
  citations: string[];
}

export type TenderPreparationAnalysisStatus = typeof TenderPreparationAnalysisStatus[keyof typeof TenderPreparationAnalysisStatus];


export const TenderPreparationAnalysisStatus = {
  pending: 'pending',
  complete: 'complete',
  failed: 'failed',
} as const;

export interface TenderPreparationAnalysis {
  status: TenderPreparationAnalysisStatus;
  /** @minimum 1 */
  sourceRevision: number;
}

export type TenderDocumentMetadataDatesItem = {
  /** @maxLength 100 */
  kind: string;
  /** @maxLength 200 */
  value: string;
  /** @maxLength 100 */
  timezone: string;
  /** @maxLength 100 */
  precision: string;
  /**
     * @minItems 1
     * @maxItems 20
     * @items.maxLength 100
     */
  citations: string[];
};

export type TenderDocumentMetadataCriteriaItem = {
  /** @maxLength 100 */
  id: string;
  /** @maxLength 10000 */
  description: string;
  /** @maxLength 100 */
  weight: string;
  /** @maxLength 200 */
  responseLimit: string;
  /**
     * @minItems 1
     * @maxItems 20
     * @items.maxLength 100
     */
  citations: string[];
};

export interface TenderDocumentMetadata {
  typeSuggestion?: ProposalTypeSuggestion;
  /**
     * Exact normalized closing timestamp only when a complete date, time and timezone are explicitly supported by the cited closing entry.
     * @nullable
     */
  deadlineAt?: string | null;
  /** @maxLength 300 */
  title: string;
  /** @maxLength 300 */
  buyer: string;
  /** @maxLength 200 */
  reference: string;
  /** @maxLength 10000 */
  summary: string;
  /** @maxItems 100 */
  dates: TenderDocumentMetadataDatesItem[];
  /** @maxItems 100 */
  criteria: TenderDocumentMetadataCriteriaItem[];
  /**
     * @maxItems 100
     * @items.maxLength 2000
     */
  obligations: string[];
  /**
     * @maxItems 100
     * @items.maxLength 1000
     */
  missingDocuments: string[];
  /**
     * @maxItems 100
     * @items.maxLength 5000
     */
  qa: string[];
  /**
     * @maxItems 100
     * @items.maxLength 1000
     */
  warnings: string[];
}

export type TenderPreparationFileStatus = typeof TenderPreparationFileStatus[keyof typeof TenderPreparationFileStatus];


export const TenderPreparationFileStatus = {
  ready: 'ready',
  partial: 'partial',
  unsupported: 'unsupported',
  failed: 'failed',
} as const;

export interface TenderPreparationFile {
  id: string;
  /** @maxLength 1024 */
  filename: string;
  /** @maxLength 64 */
  contentHash: string;
  status: TenderPreparationFileStatus;
  /**
     * @maxItems 110
     * @items.maxLength 500
     */
  warnings: string[];
  /**
     * @maxItems 250
     * @items.maxLength 300
     */
  locations: string[];
}

export type TenderPreparationReviewSelectedFilesItem = {
  id: string;
  kind: TenderSourceKind;
};

export type TenderPreparationReviewSourceLabelsItem = {
  id: string;
  filename: string;
  contentHash: string;
  kind: TenderSourceKind;
};

/**
 * Supported native proposal types. Omission on legacy creation means Tender. Fixed after creation.
 */
export type ProposalType = typeof ProposalType[keyof typeof ProposalType];


export const ProposalType = {
  tender: 'tender',
  sow: 'sow',
} as const;

export interface TenderCreate {
  proposalType?: ProposalType;
  accountId: string;
  /** @nullable */
  quoteRequestId?: string | null;
  /**
     * @minLength 1
     * @maxLength 300
     */
  title: string;
  /** @nullable */
  ownerAppUserId?: string | null;
  /** @nullable */
  deadlineAt?: string | null;
  /**
     * @maxLength 50000
     * @nullable
     */
  briefingMd?: string | null;
  storageProvider?: TenderStorageProvider;
  /**
     * @minItems 1
     * @maxItems 4
     */
  selectedOutputKinds?: TenderOutputKind[];
}

export type AgentAccessScopeKind = typeof AgentAccessScopeKind[keyof typeof AgentAccessScopeKind];


export const AgentAccessScopeKind = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export interface AgentAccessScope {
  kind: AgentAccessScopeKind;
  /** @nullable */
  tenantId?: string | null;
  /** @nullable */
  entityId?: string | null;
}

export type AgentActionProposalStatus = typeof AgentActionProposalStatus[keyof typeof AgentActionProposalStatus];


export const AgentActionProposalStatus = {
  prepared: 'prepared',
  reviewed: 'reviewed',
  executing: 'executing',
  succeeded: 'succeeded',
  failed: 'failed',
  partial: 'partial',
  cancelled: 'cancelled',
  expired: 'expired',
  stale: 'stale',
} as const;

export interface AgentReviewChange {
  field: string;
  /** @nullable */
  before: string | null;
  /** @nullable */
  after: string | null;
}

export interface AgentActionReview {
  title: string;
  summary: string;
  targets: string[];
  changes: AgentReviewChange[];
  sideEffects: string[];
  requirements: string[];
  costs: string[];
  reasonRequired: boolean;
  destructive?: boolean;
  nonTransactionalEffects?: string[];
}

/**
 * instant changes run on request with a 24-hour undo; confirm changes always need an exact review.
 */
export type AgentAutonomyTier = typeof AgentAutonomyTier[keyof typeof AgentAutonomyTier];


export const AgentAutonomyTier = {
  instant: 'instant',
  confirm: 'confirm',
} as const;

export interface AgentActionProposal {
  id: string;
  conversationId: string;
  scope: AgentAccessScope;
  actionKey: string;
  /** @minimum 1 */
  actionVersion: number;
  status: AgentActionProposalStatus;
  review: AgentActionReview;
  previewHash: string;
  /** @nullable */
  reviewReason?: string | null;
  /** @nullable */
  executionId?: string | null;
  expiresAt: string;
  createdAt: string;
  autonomyTier?: AgentAutonomyTier;
  autonomyReason?: string;
}

export interface TenderPreparationReview {
  hash: string;
  expiresAt: string;
  fields: TenderCreate;
  /** @maxItems 100 */
  selectedFiles: TenderPreparationReviewSelectedFilesItem[];
  consequence: string;
  metadata: TenderDocumentMetadata;
  /** @maxItems 100 */
  sourceLabels: TenderPreparationReviewSourceLabelsItem[];
  proposal?: AgentActionProposal;
}

export type TenderPreparationMatchesItem = {
  id: string;
  name: string;
};

export type TenderPreparationReceiptSourcesItemStatus = typeof TenderPreparationReceiptSourcesItemStatus[keyof typeof TenderPreparationReceiptSourcesItemStatus];


export const TenderPreparationReceiptSourcesItemStatus = {
  pending: 'pending',
  available: 'available',
  failed: 'failed',
} as const;

export type TenderPreparationReceiptSourcesItem = {
  fileId: string;
  sourceId?: string;
  status: TenderPreparationReceiptSourcesItemStatus;
};

export type TenderPreparationReceipt = {
  tenderId: string;
  link: string;
  /** @maxItems 100 */
  sources: TenderPreparationReceiptSourcesItem[];
};

export type TenderPreparationAccountReviewAccountKind = typeof TenderPreparationAccountReviewAccountKind[keyof typeof TenderPreparationAccountReviewAccountKind];


export const TenderPreparationAccountReviewAccountKind = {
  organization: 'organization',
  person: 'person',
} as const;

export type TenderPreparationAccountReviewRelationshipType = typeof TenderPreparationAccountReviewRelationshipType[keyof typeof TenderPreparationAccountReviewRelationshipType];


export const TenderPreparationAccountReviewRelationshipType = {
  b2b_customer: 'b2b_customer',
  b2c_consumer: 'b2c_consumer',
  partner: 'partner',
  supplier: 'supplier',
  investor: 'investor',
  prospect: 'prospect',
  other: 'other',
} as const;

export type TenderPreparationAccountReview = {
  hash: string;
  expiresAt: string;
  name: string;
  accountKind: TenderPreparationAccountReviewAccountKind;
  relationshipType: TenderPreparationAccountReviewRelationshipType;
  /**
     * @maxLength 2000
     * @nullable
     */
  website?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  industry?: string | null;
  /**
     * @maxLength 100
     * @nullable
     */
  phone?: string | null;
  /**
     * @maxLength 320
     * @nullable
     */
  email?: string | null;
  /**
     * @maxLength 253
     * @nullable
     */
  emailDomain?: string | null;
  /**
     * @maxLength 500
     * @nullable
     */
  addressLine1?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  city?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  stateProvince?: string | null;
  /**
     * @maxLength 50
     * @nullable
     */
  postcode?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  country?: string | null;
  /**
     * @maxLength 10000
     * @nullable
     */
  notes?: string | null;
  /**
     * @minimum 0
     * @maximum 99999999999999
     * @nullable
     */
  annualRevenue?: number | null;
  /**
     * @minimum 0
     * @maximum 2147483647
     * @nullable
     */
  numberOfEmployees?: number | null;
};

export type TenderPreparationAccountReceipt = {
  id: string;
  name: string;
};

export interface TenderPreparation {
  id: string;
  /** @minimum 1 */
  revision: number;
  /** @nullable */
  conversationId?: string | null;
  /** @maxItems 25 */
  files: TenderPreparationFile[];
  total: number;
  limit: number;
  offset: number;
  metadata?: TenderDocumentMetadata;
  analysis?: TenderPreparationAnalysis;
  /** @maxItems 2 */
  matches: TenderPreparationMatchesItem[];
  /**
     * @maxItems 100
     * @items.maxLength 1000
     */
  warnings: string[];
  review?: TenderPreparationReview;
  receipt?: TenderPreparationReceipt;
  accountReview?: TenderPreparationAccountReview;
  accountReceipt?: TenderPreparationAccountReceipt;
}

export interface AgentAttachmentReference {
  id: string;
  /** @minimum 1 */
  revision: number;
  /** @pattern ^[0-9a-f]{64}$ */
  contentHash: string;
}

export interface TenderPreparationCreate {
  requestId: string;
  conversationId?: string;
  /**
     * @minItems 1
     * @maxItems 10
     */
  attachments?: AgentAttachmentReference[];
}

export interface TenderPreparationUpload {
  uploadId: string;
  /**
     * @minLength 1
     * @maxLength 255
     */
  filename: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  contentType: string;
  /**
     * @minLength 4
     * @maxLength 27962028
     */
  dataBase64: string;
}

export type TenderPreparationEditSelectedFilesItem = {
  id: string;
  kind: TenderSourceKind;
};

export interface TenderPreparationEdit {
  /** @minimum 1 */
  expectedRevision: number;
  fields: TenderCreate;
  metadata: TenderDocumentMetadata;
  /** @maxItems 100 */
  selectedFiles: TenderPreparationEditSelectedFilesItem[];
}

export type ProposalListItemSource = typeof ProposalListItemSource[keyof typeof ProposalListItemSource];


export const ProposalListItemSource = {
  tender: 'tender',
  managed_sow: 'managed_sow',
} as const;

export interface ProposalListItem {
  /** Canonical Tender ID or historical managed SOW document ID. */
  id: string;
  type: ProposalType;
  source: ProposalListItemSource;
  title: string;
  /** @nullable */
  reference?: string | null;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  accountName?: string | null;
  /** @nullable */
  quoteRequestId?: string | null;
  /** @nullable */
  ownerAppUserId?: string | null;
  /** @nullable */
  deadlineAt?: string | null;
  /** Unmodified native source status. */
  status: string;
  /** @nullable */
  sowDocumentId?: string | null;
  /** @nullable */
  sowDocumentStatus?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProposalList {
  data: ProposalListItem[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface TenderUpdate {
  /** @minimum 1 */
  expectedRevision: number;
  accountId?: string;
  /** @nullable */
  quoteRequestId?: string | null;
  /**
     * @minLength 1
     * @maxLength 300
     */
  title?: string;
  /** @nullable */
  ownerAppUserId?: string | null;
  /** @nullable */
  deadlineAt?: string | null;
  /**
     * @maxLength 50000
     * @nullable
     */
  briefingMd?: string | null;
  storageProvider?: TenderStorageProvider;
  providerLocation?: TenderProviderLocation | null;
  /**
     * @minItems 1
     * @maxItems 4
     */
  selectedOutputKinds?: TenderOutputKind[];
}

export interface TenderTransition {
  /** @minimum 1 */
  expectedRevision: number;
  status: TenderStatus;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  note: string;
  /** @nullable */
  submittedAt?: string | null;
  /**
     * @maxLength 300
     * @nullable
     */
  submissionReference?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  outcome?: string | null;
  /**
     * @maxLength 5000
     * @nullable
     */
  outcomeNote?: string | null;
}

export interface Tender {
  proposalType?: ProposalType;
  /** @nullable */
  sowDocumentId?: string | null;
  id: string;
  reference: string;
  accountId: string;
  /** @nullable */
  quoteRequestId?: string | null;
  title: string;
  /** @nullable */
  ownerAppUserId?: string | null;
  /** @nullable */
  deadlineAt?: string | null;
  /** @nullable */
  briefingMd?: string | null;
  status: TenderStatus;
  storageProvider: TenderStorageProvider;
  providerLocation?: TenderProviderLocation | null;
  selectedOutputKinds: TenderOutputKind[];
  /** @minimum 1 */
  revision: number;
  /** @nullable */
  submittedAt?: string | null;
  /** @nullable */
  submissionReference?: string | null;
  /** @nullable */
  outcome?: string | null;
  /** @nullable */
  outcomeNote?: string | null;
  /** @nullable */
  closedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TenderRelatedRecord {
  id: string;
  label: string;
  /** @nullable */
  reference?: string | null;
}

export interface TenderWorkflowCounts {
  /** @minimum 0 */
  sources: number;
  /** @minimum 0 */
  requirements: number;
  /** @minimum 0 */
  responseSections: number;
  /** @minimum 0 */
  evidenceSelections: number;
  /** @minimum 0 */
  aiRuns: number;
  /** @minimum 0 */
  proposals: number;
  /** @minimum 0 */
  packageRevisions: number;
  /** @minimum 0 */
  reviews: number;
  /** @minimum 0 */
  exports: number;
}

export type TenderDetail = Tender & ({
  documentMetadata?: TenderDocumentMetadata;
  account: TenderRelatedRecord;
  quote?: TenderRelatedRecord | null;
  workflowCounts: TenderWorkflowCounts;
});

export type TenderListDataItem = Tender & ({
  /** @nullable */
  accountName?: string | null;
});

export interface TenderList {
  data: TenderListDataItem[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type TenderEventPayload = { [key: string]: unknown };

export interface TenderEvent {
  id: string;
  tenderId: string;
  type: TenderEventType;
  /** @nullable */
  actorAppUserId?: string | null;
  payload: TenderEventPayload;
  createdAt: string;
}

export interface TenderEventList {
  data: TenderEvent[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type TenderSourceExtractionStatus = typeof TenderSourceExtractionStatus[keyof typeof TenderSourceExtractionStatus];


export const TenderSourceExtractionStatus = {
  pending: 'pending',
  ready: 'ready',
  unsupported: 'unsupported',
  failed: 'failed',
} as const;

export type TenderSourceUploadStatus = typeof TenderSourceUploadStatus[keyof typeof TenderSourceUploadStatus];


export const TenderSourceUploadStatus = {
  staging: 'staging',
  available: 'available',
  failed: 'failed',
} as const;

export interface TenderSource {
  id: string;
  tenderId: string;
  lineageId: string;
  kind: TenderSourceKind;
  /**
     * @minLength 1
     * @maxLength 300
     */
  label: string;
  /** @nullable */
  fileId?: string | null;
  descriptor: TenderSourceDescriptor;
  /** @minimum 1 */
  revision: number;
  isCurrent: boolean;
  uploadStatus: TenderSourceUploadStatus;
  /** @nullable */
  uploadMessage?: string | null;
  extractionStatus: TenderSourceExtractionStatus;
  /** @nullable */
  extractionMessage?: string | null;
  extractedTextAvailable: boolean;
  /**
     * @minimum 0
     * @nullable
     */
  sizeBytes: number | null;
  createdAt: string;
}

export interface TenderSourceList {
  data: TenderSource[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface TenderSourceUpload {
  file: Blob;
  kind: TenderSourceKind;
  /**
     * @minLength 1
     * @maxLength 300
     */
  label: string;
}

export interface TenderSourceRevisionUpload {
  file: Blob;
  /** @minimum 1 */
  expectedRevision: number;
  kind: TenderSourceKind;
  /**
     * @minLength 1
     * @maxLength 300
     */
  label: string;
}

export interface TenderSourceUpdate {
  /** @minimum 1 */
  expectedRevision: number;
  kind?: TenderSourceKind;
  /**
     * @minLength 1
     * @maxLength 300
     */
  label?: string;
}

export type TenderBuyerTemplateTargetKind = typeof TenderBuyerTemplateTargetKind[keyof typeof TenderBuyerTemplateTargetKind];


export const TenderBuyerTemplateTargetKind = {
  content_control: 'content_control',
  bookmark: 'bookmark',
} as const;

export interface TenderBuyerTemplateTarget {
  /**
     * @minLength 1
     * @maxLength 300
     */
  targetId: string;
  kind: TenderBuyerTemplateTargetKind;
  /**
     * @minLength 1
     * @maxLength 300
     */
  label: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  partName: string;
  supported: boolean;
  /** @nullable */
  reason?: string | null;
}

export interface TenderBuyerTemplateMappingEntry {
  /**
     * @minLength 1
     * @maxLength 300
     */
  targetId: string;
  requirementId: string;
  responseSectionId: string;
  /**
     * @minLength 1
     * @maxLength 100
     */
  requirementKey: string;
}

export interface TenderBuyerTemplateMapping {
  /** @minimum 1 */
  mappingRevision: number;
  templateSourceId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  templateHash: string;
  /** @pattern ^[a-f0-9]{64}$ */
  inspectionHash: string;
  /** @pattern ^[a-f0-9]{64}$ */
  mappingHash: string;
  /**
     * @minItems 1
     * @maxItems 500
     */
  entries: TenderBuyerTemplateMappingEntry[];
  confirmedAt: string;
  confirmedByAppUserId: string;
}

export interface TenderBuyerTemplateMappingConfirmEntry {
  /**
     * @minLength 1
     * @maxLength 300
     */
  targetId: string;
  requirementId: string;
}

export interface TenderBuyerTemplateMappingConfirm {
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minItems 1
     * @maxItems 500
     */
  entries: TenderBuyerTemplateMappingConfirmEntry[];
}

export interface TenderBuyerTemplateHandoff {
  downloadUrl: string;
  reuploadUrl: string;
  /** @nullable */
  officeLaunchUrl?: string | null;
  instructions: string;
}

export interface TenderBuyerTemplateInspection {
  tenderId: string;
  sourceId: string;
  /** @minimum 1 */
  sourceRevision: number;
  /** @pattern ^[a-f0-9]{64}$ */
  templateHash: string;
  /** @pattern ^[a-f0-9]{64}$ */
  inspectionHash: string;
  editable: boolean;
  handoffRequired: boolean;
  /** @nullable */
  handoffReason?: string | null;
  /** @maxItems 500 */
  targets: TenderBuyerTemplateTarget[];
  /** @maxItems 100 */
  unsupportedConstructs: string[];
  /** @maxItems 100 */
  warnings: string[];
  mapping?: TenderBuyerTemplateMapping | null;
  handoff: TenderBuyerTemplateHandoff;
}

export interface TenderOrderedIds {
  /** @maxItems 500 */
  orderedIds: string[];
}

export type TenderResponseSectionContent = { [key: string]: unknown };

export interface TenderResponseSection {
  id: string;
  tenderId: string;
  /**
     * @minLength 1
     * @maxLength 100
     */
  key: string;
  /**
     * @minLength 1
     * @maxLength 300
     */
  title: string;
  content: TenderResponseSectionContent;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
  /** @minimum 1 */
  revision: number;
  /** @minimum 0 */
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export type TenderResponseSectionCreateContent = { [key: string]: unknown };

export interface TenderResponseSectionCreate {
  /**
     * @minLength 1
     * @maxLength 100
     */
  key: string;
  /**
     * @minLength 1
     * @maxLength 300
     */
  title: string;
  content?: TenderResponseSectionCreateContent;
}

export type TenderResponseSectionUpdateContent = { [key: string]: unknown };

export interface TenderResponseSectionUpdate {
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 100
     */
  key?: string;
  /**
     * @minLength 1
     * @maxLength 300
     */
  title?: string;
  content?: TenderResponseSectionUpdateContent;
}

export interface TenderResponseSectionList {
  data: TenderResponseSection[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 500
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type TenderRequirementDesignation = typeof TenderRequirementDesignation[keyof typeof TenderRequirementDesignation];


export const TenderRequirementDesignation = {
  mandatory: 'mandatory',
  optional: 'optional',
} as const;

export interface TenderRequirementConstraints {
  designation: TenderRequirementDesignation;
  /**
     * @minimum 1
     * @maximum 100000
     * @nullable
     */
  wordLimit?: number | null;
  /**
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 1000
     */
  notes?: string[];
  authorConfirmed: boolean;
}

export interface TenderRequirementCreate {
  /**
     * @minLength 1
     * @maxLength 100
     */
  requirementKey: string;
  /**
     * @maxLength 500
     * @nullable
     */
  buyerReference?: string | null;
  /**
     * @minLength 1
     * @maxLength 20000
     */
  text: string;
  constraints: TenderRequirementConstraints;
  /** @nullable */
  sourceId?: string | null;
  /**
     * @maxLength 1000
     * @nullable
     */
  sourceLocation?: string | null;
  /** @nullable */
  responseSectionId?: string | null;
  reviewStatus?: TenderRequirementReviewStatus;
}

export interface TenderRequirementUpdate {
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 100
     */
  requirementKey?: string;
  /**
     * @maxLength 500
     * @nullable
     */
  buyerReference?: string | null;
  /**
     * @minLength 1
     * @maxLength 20000
     */
  text?: string;
  constraints?: TenderRequirementConstraints;
  /** @nullable */
  sourceId?: string | null;
  /**
     * @maxLength 1000
     * @nullable
     */
  sourceLocation?: string | null;
  /** @nullable */
  responseSectionId?: string | null;
  reviewStatus?: TenderRequirementReviewStatus;
}

export interface TenderRequirementContract {
  id: string;
  tenderId: string;
  requirementKey: string;
  /** @nullable */
  buyerReference?: string | null;
  text: string;
  constraints: TenderRequirementConstraints;
  /** @nullable */
  sourceId?: string | null;
  /** @nullable */
  sourceRevision: string | null;
  /**
     * @nullable
     * @pattern ^[a-f0-9]{64}$
     */
  sourceHash: string | null;
  /** @nullable */
  sourceLocation?: string | null;
  /** @nullable */
  responseSectionId?: string | null;
  reviewStatus: TenderRequirementReviewStatus;
  coverageStatus: TenderRequirementCoverageStatus;
  authorConfirmed: boolean;
  /** @minimum 1 */
  revision: number;
  /** @minimum 0 */
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface TenderRequirementList {
  data: TenderRequirementContract[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 500
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type TenderEvidenceState = typeof TenderEvidenceState[keyof typeof TenderEvidenceState];


export const TenderEvidenceState = {
  valid: 'valid',
  missing: 'missing',
  stale: 'stale',
  conflicting: 'conflicting',
  denied: 'denied',
  unsupported: 'unsupported',
} as const;

export type TenderEvidenceDiscoveryMode = typeof TenderEvidenceDiscoveryMode[keyof typeof TenderEvidenceDiscoveryMode];


export const TenderEvidenceDiscoveryMode = {
  selected_only: 'selected_only',
  entity_wide: 'entity_wide',
} as const;

export type TenderEvidenceCandidateSourceType = typeof TenderEvidenceCandidateSourceType[keyof typeof TenderEvidenceCandidateSourceType];


export const TenderEvidenceCandidateSourceType = {
  tender_source: 'tender_source',
  knowledge: 'knowledge',
} as const;

export interface TenderEvidenceCandidate {
  candidateId: string;
  sourceType: TenderEvidenceCandidateSourceType;
  sourceId: string;
  sourceRevision: string;
  /** @pattern ^[a-f0-9]{64}$ */
  sourceHash: string;
  title: string;
  location: string;
  excerpt: string;
  state: TenderEvidenceState;
  message: string;
  channelEligible: boolean;
  selected: boolean;
  score: number;
}

export type TenderEvidenceSourceStateSourceType = typeof TenderEvidenceSourceStateSourceType[keyof typeof TenderEvidenceSourceStateSourceType];


export const TenderEvidenceSourceStateSourceType = {
  tender_source: 'tender_source',
  knowledge: 'knowledge',
} as const;

export interface TenderEvidenceSourceState {
  sourceType: TenderEvidenceSourceStateSourceType;
  sourceId: string;
  title: string;
  revision: string;
  state: TenderEvidenceState;
  message: string;
  channelEligible: boolean;
}

export type TenderEvidenceSearchResultState = typeof TenderEvidenceSearchResultState[keyof typeof TenderEvidenceSearchResultState];


export const TenderEvidenceSearchResultState = {
  ready: 'ready',
  stale: 'stale',
  conflicting: 'conflicting',
  denied: 'denied',
  failed_ingestion: 'failed_ingestion',
  empty_index: 'empty_index',
  no_sources: 'no_sources',
  low_confidence: 'low_confidence',
} as const;

export interface TenderEvidenceSearchResult {
  workspaceEvidence?: DocumentWorkspaceEvidenceManifest;
  mode: TenderEvidenceDiscoveryMode;
  /** @maxItems 25 */
  items: TenderEvidenceCandidate[];
  /** @maxItems 250 */
  sourceStates: TenderEvidenceSourceState[];
  state: TenderEvidenceSearchResultState;
  message: string;
}

export type TenderEvidenceSelectionCreateSourceType = typeof TenderEvidenceSelectionCreateSourceType[keyof typeof TenderEvidenceSelectionCreateSourceType];


export const TenderEvidenceSelectionCreateSourceType = {
  tender_source: 'tender_source',
  knowledge: 'knowledge',
} as const;

export interface TenderEvidenceSelectionCreate {
  /** @nullable */
  requirementId?: string | null;
  sourceType: TenderEvidenceSelectionCreateSourceType;
  sourceId: string;
  /**
     * @minLength 1
     * @maxLength 300
     */
  sourceRevision: string;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  location: string;
  discoveryMode: TenderEvidenceDiscoveryMode;
}

export type TenderEvidenceSelectionSourceType = typeof TenderEvidenceSelectionSourceType[keyof typeof TenderEvidenceSelectionSourceType];


export const TenderEvidenceSelectionSourceType = {
  tender_source: 'tender_source',
  knowledge: 'knowledge',
} as const;

export type TenderEvidenceSelectionEligibility = { [key: string]: unknown };

export interface TenderEvidenceSelection {
  id: string;
  tenderId: string;
  /** @nullable */
  requirementId?: string | null;
  sourceType: TenderEvidenceSelectionSourceType;
  sourceId: string;
  sourceRevision: string;
  /** @pattern ^[a-f0-9]{64}$ */
  sourceHash: string;
  location: string;
  excerpt: string;
  eligibility: TenderEvidenceSelectionEligibility;
  validationState: TenderEvidenceState;
  validationMessage: string;
  /** @nullable */
  selectedByAppUserId?: string | null;
  createdAt: string;
}

export interface TenderEvidenceSelectionList {
  data: TenderEvidenceSelection[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface TenderCoverageItem {
  requirementId: string;
  requirementKey: string;
  status: TenderRequirementCoverageStatus;
  /** @minimum 0 */
  wordCount: number;
  /**
     * @minimum 1
     * @nullable
     */
  wordLimit: number | null;
  evidenceStates: TenderEvidenceState[];
  warnings: string[];
}

export type TenderCoverageReportBasis = typeof TenderCoverageReportBasis[keyof typeof TenderCoverageReportBasis];


export const TenderCoverageReportBasis = {
  deterministic_section_content_constraints_and_pinned_evidence: 'deterministic_section_content_constraints_and_pinned_evidence',
} as const;

export interface TenderCoverageReport {
  items: TenderCoverageItem[];
  /** @minimum 0 */
  total: number;
  /** @minimum 0 */
  covered: number;
  /** @minimum 0 */
  partial: number;
  /** @minimum 0 */
  unaddressed: number;
  /** @minimum 0 */
  notApplicable: number;
  generatedAt: string;
  basis: TenderCoverageReportBasis;
}

export type BidLessonGuideline = typeof BidLessonGuideline[keyof typeof BidLessonGuideline];


export const BidLessonGuideline = {
  evidence_first: 'evidence_first',
  explicit_gaps: 'explicit_gaps',
  buyer_headings: 'buyer_headings',
  proposed_commitments: 'proposed_commitments',
  concise_narrative: 'concise_narrative',
} as const;

export type BidLessonStatus = typeof BidLessonStatus[keyof typeof BidLessonStatus];


export const BidLessonStatus = {
  proposed: 'proposed',
  approved: 'approved',
  rejected: 'rejected',
  withdrawn: 'withdrawn',
} as const;

export type BidLessonGuidelineProperty = typeof BidLessonGuidelineProperty[keyof typeof BidLessonGuidelineProperty];


export const BidLessonGuidelineProperty = {
  evidence_first: 'evidence_first',
  explicit_gaps: 'explicit_gaps',
  buyer_headings: 'buyer_headings',
  proposed_commitments: 'proposed_commitments',
  concise_narrative: 'concise_narrative',
} as const;

export type BidLessonApplicability = typeof BidLessonApplicability[keyof typeof BidLessonApplicability];


export const BidLessonApplicability = {
  all_responses: 'all_responses',
  matching_document_category: 'matching_document_category',
} as const;

export type BidLessonSourcePinsItem = {
  kind: string;
  id: string;
  revision: string;
  hash: string;
};

export interface BidLesson {
  id: string;
  lessonId: string;
  /** @minimum 1 */
  revision: number;
  status: BidLessonStatus;
  guideline: BidLessonGuidelineProperty;
  applicability: BidLessonApplicability;
  guidance: string;
  tenderId: string;
  documentId: string;
  beforeVersionId: string;
  afterVersionId: string;
  beforeHash: string;
  afterHash: string;
  beforeExcerpt: string;
  afterExcerpt: string;
  excerptNote: string;
  explanation: string;
  reason: string;
  actorAppUserId: string;
  editActorAppUserId: string;
  createdAt: string;
  eligible: boolean;
  sourcePins?: BidLessonSourcePinsItem[];
  reviewOutcomeIds?: string[];
  /** @nullable */
  exclusionReason: string | null;
}

export type BidLessonCreateApplicability = typeof BidLessonCreateApplicability[keyof typeof BidLessonCreateApplicability];


export const BidLessonCreateApplicability = {
  all_responses: 'all_responses',
  matching_document_category: 'matching_document_category',
} as const;

export interface BidLessonCreate {
  beforeVersionId: string;
  afterVersionId: string;
  guideline: BidLessonGuideline;
  applicability: BidLessonCreateApplicability;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  explanation: string;
}

export type BidLessonDecisionDecision = typeof BidLessonDecisionDecision[keyof typeof BidLessonDecisionDecision];


export const BidLessonDecisionDecision = {
  approve: 'approve',
  reject: 'reject',
  withdraw: 'withdraw',
} as const;

export interface BidLessonDecision {
  /** @minimum 1 */
  expectedRevision: number;
  decision: BidLessonDecisionDecision;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  reason: string;
}

export interface BidLessonPage {
  data: BidLesson[];
  total: number;
  limit: number;
  offset: number;
}

export interface BidLessonInfluence {
  lessonId: string;
  /** @minimum 1 */
  revision: number;
  guidance: string;
  sourceTenderId: string;
  documentId: string;
  afterVersionId: string;
}

export interface BidSynthesisCreate {
  planRevisionId: string;
  /** @minimum 1 */
  selectionRevision: number;
  /**
     * @minItems 1
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 100
     */
  documentKeys?: string[];
  /** @maxLength 4000 */
  instruction?: string;
}

export type BidSynthesisCoverageSourceType = typeof BidSynthesisCoverageSourceType[keyof typeof BidSynthesisCoverageSourceType];


export const BidSynthesisCoverageSourceType = {
  tender_source: 'tender_source',
  knowledge: 'knowledge',
  workspace: 'workspace',
} as const;

export type BidSynthesisCoverageTier = typeof BidSynthesisCoverageTier[keyof typeof BidSynthesisCoverageTier];


export const BidSynthesisCoverageTier = {
  primary: 'primary',
  background: 'background',
} as const;

export interface WorkspaceEvidenceBinding {
  workspaceId: string;
  snapshotId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  manifestHash: string;
}

export interface BidSynthesisCoverage {
  id: string;
  sourceType: BidSynthesisCoverageSourceType;
  sourceId: string;
  revision: string;
  sourceHash: string;
  location: string;
  start: number;
  end: number;
  text: string;
  textHash: string;
  reviewed: boolean;
  /** @nullable */
  summary: string | null;
  tier?: BidSynthesisCoverageTier;
  workspaceContext?: WorkspaceEvidenceBinding;
}

export type BidSynthesisDocumentProgressStatus = typeof BidSynthesisDocumentProgressStatus[keyof typeof BidSynthesisDocumentProgressStatus];


export const BidSynthesisDocumentProgressStatus = {
  not_generated: 'not_generated',
  pending: 'pending',
  accepted: 'accepted',
  rejected: 'rejected',
  stale: 'stale',
} as const;

export type TenderProposalTargetType = typeof TenderProposalTargetType[keyof typeof TenderProposalTargetType];


export const TenderProposalTargetType = {
  tender: 'tender',
  requirement: 'requirement',
  section: 'section',
} as const;

export type TenderProposalStatusProperty = typeof TenderProposalStatusProperty[keyof typeof TenderProposalStatusProperty];


export const TenderProposalStatusProperty = {
  pending: 'pending',
  accepted: 'accepted',
  rejected: 'rejected',
  stale: 'stale',
} as const;

export type TenderProposalContent = { [key: string]: unknown };

export type TenderProposalCitationsItem = { [key: string]: unknown };

export interface TenderProposal {
  id: string;
  tenderId: string;
  runId: string;
  targetType: TenderProposalTargetType;
  /** @nullable */
  targetId?: string | null;
  /** @minimum 1 */
  baseRevision: number;
  kind: TenderRunKind;
  status: TenderProposalStatusProperty;
  content: TenderProposalContent;
  citations: TenderProposalCitationsItem[];
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
  /** @nullable */
  decisionNote?: string | null;
  createdAt: string;
  /** @nullable */
  decidedAt?: string | null;
}

export interface BidSynthesisDocumentProgress {
  key: string;
  documentId: string;
  title: string;
  /** @minimum 1 */
  baseRevision: number;
  /** @minimum 1 */
  currentRevision: number;
  /** @nullable */
  proposalId: string | null;
  status: BidSynthesisDocumentProgressStatus;
  /** @nullable */
  acceptedVersionId: string | null;
  baseChanged: boolean;
  gaps: string[];
  proposal: TenderProposal | null;
  requirementIds: string[];
}

export type BidSynthesisGapTaskStatus = typeof BidSynthesisGapTaskStatus[keyof typeof BidSynthesisGapTaskStatus];


export const BidSynthesisGapTaskStatus = {
  proposed: 'proposed',
} as const;

export interface BidSynthesisGapTask {
  title: string;
  /** @nullable */
  ownerAppUserId: string | null;
  status: BidSynthesisGapTaskStatus;
  approvalRequired: true;
  planRevisionId: string;
  documentKeys: string[];
  requirementIds: string[];
}

export interface BidSynthesisProgress {
  runId: string;
  planRevisionId: string;
  coverageComplete: boolean;
  warnings: string[];
  coverage: BidSynthesisCoverage[];
  total: number;
  limit: number;
  offset: number;
  /** @maxItems 20 */
  documents: BidSynthesisDocumentProgress[];
  consistency: string[];
  gapTasks: BidSynthesisGapTask[];
  /** @maxItems 30 */
  lessons?: BidLessonInfluence[];
}

export type TenderAiRunContractSettings = { [key: string]: unknown };

/**
 * @nullable
 */
export type TenderAiRunContractUsage = { [key: string]: unknown } | null;

export interface TenderAiRunContract {
  workspaceContext?: WorkspaceEvidenceBinding;
  id: string;
  tenderId: string;
  kind: TenderRunKind;
  status: TenderRunStatus;
  modelId: string;
  settings: TenderAiRunContractSettings;
  /** @pattern ^[a-f0-9]{64}$ */
  inputHash: string;
  /** @minimum 0 */
  progressCompleted: number;
  /** @minimum 1 */
  progressTotal: number;
  /** @minimum 0 */
  attemptCount: number;
  /**
     * @minimum 1
     * @maximum 5
     */
  maxAttempts: number;
  cancelRequested: boolean;
  /** @nullable */
  usage?: TenderAiRunContractUsage;
  /** @nullable */
  errorCode?: string | null;
  /** @nullable */
  errorMessage?: string | null;
  createdAt: string;
  /** @nullable */
  startedAt?: string | null;
  /** @nullable */
  completedAt?: string | null;
  updatedAt: string;
}

export type TenderAiRunCreateReasoningEffort = typeof TenderAiRunCreateReasoningEffort[keyof typeof TenderAiRunCreateReasoningEffort];


export const TenderAiRunCreateReasoningEffort = {
  none: 'none',
  minimal: 'minimal',
  low: 'low',
  medium: 'medium',
  high: 'high',
  xhigh: 'xhigh',
} as const;

export interface TenderAiRunCreate {
  workspaceContext?: WorkspaceEvidenceBinding;
  kind: TenderRunKind;
  /**
     * @minLength 1
     * @maxLength 64
     */
  modelChoiceId?: string;
  reasoningEffort?: TenderAiRunCreateReasoningEffort;
  /**
     * @minimum 1
     * @maximum 131072
     */
  maxOutputTokens?: number;
  /**
     * @minimum 1000
     * @maximum 120000
     */
  timeoutMs?: number;
  /** @maxLength 4000 */
  instruction?: string;
  /** @nullable */
  targetSectionId?: string | null;
  /** @maxItems 250 */
  targetRequirementIds?: string[];
  discoveryMode: TenderEvidenceDiscoveryMode;
}

export interface TenderAiRunList {
  data: TenderAiRunContract[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface TenderProposalList {
  data: TenderProposal[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type TenderProposalDecisionDecision = typeof TenderProposalDecisionDecision[keyof typeof TenderProposalDecisionDecision];


export const TenderProposalDecisionDecision = {
  accept: 'accept',
  reject: 'reject',
} as const;

export interface TenderProposalDecision {
  decision: TenderProposalDecisionDecision;
  /** @maxLength 2000 */
  note?: string;
}

export interface TenderPackageCreate {
  /**
     * Explicitly reviewed current PDF scans; signatures remain human responsibilities.
     * @maxItems 10
     */
  signedSourceIds?: string[];
  /**
     * @minItems 1
     * @maxItems 4
     */
  outputKinds: TenderOutputKind[];
  includeQuote: boolean;
  /** Exact current buyer-template source to use for converted or native-template output. */
  buyerTemplateSourceId?: string;
}

export type BidPackageSnapshotVersion = typeof BidPackageSnapshotVersion[keyof typeof BidPackageSnapshotVersion];


export const BidPackageSnapshotVersion = {
  NUMBER_1: 1,
} as const;

export type BidPackageSnapshotDocumentsItemRole = typeof BidPackageSnapshotDocumentsItemRole[keyof typeof BidPackageSnapshotDocumentsItemRole];


export const BidPackageSnapshotDocumentsItemRole = {
  appendix_1: 'appendix_1',
  summary: 'summary',
  schedule_1: 'schedule_1',
  schedule_2: 'schedule_2',
  schedule_3: 'schedule_3',
  case_study: 'case_study',
  other: 'other',
} as const;

export type BidPackageSnapshotSignedScansItem = {
  sourceId: string;
  fileId: string;
  hash: string;
  revision: string;
  filename: string;
};

export type BidPackageSnapshotDocumentsItemContent = { [key: string]: unknown };

export type BidPackageSnapshotDocumentsItem = {
  key: string;
  documentId: string;
  /** @nullable */
  versionId: string | null;
  title: string;
  revision: number;
  contentHash: string;
  content: BidPackageSnapshotDocumentsItemContent;
  requirementIds: string[];
  role: BidPackageSnapshotDocumentsItemRole;
};

export type BidPackageSnapshotLessonRevisionsItem = {
  lessonId: string;
  revision: number;
  current: boolean;
};

export interface BidPackageSnapshot {
  version: BidPackageSnapshotVersion;
  policyVersion: string;
  planRevisionId: string;
  fingerprint: string;
  supplierName: string;
  /** @maxItems 10 */
  signedScans?: BidPackageSnapshotSignedScansItem[];
  /** @maxItems 60 */
  documents: BidPackageSnapshotDocumentsItem[];
  blockers: string[];
  checklist: string[];
  lessonRevisions: BidPackageSnapshotLessonRevisionsItem[];
}

/**
 * @nullable
 */
export type TenderPackageSnapshotQuoteSnapshot = { [key: string]: unknown } | null;

export type TenderPackageSnapshotRequirementRevisionsItem = {
  requirementId: string;
  /** @minimum 1 */
  revision: number;
};

export type TenderPackageSnapshotResponseSectionRevisionsItem = {
  sectionId: string;
  /** @minimum 1 */
  revision: number;
};

export interface TenderPackageSnapshot {
  bid?: BidPackageSnapshot;
  /** @minimum 1 */
  tenderRevision: number;
  /** @nullable */
  quoteRevisionId?: string | null;
  /** @nullable */
  quoteSnapshot?: TenderPackageSnapshotQuoteSnapshot;
  /** @nullable */
  buyerTemplateSourceId?: string | null;
  buyerTemplateMapping?: TenderBuyerTemplateMapping | null;
  sourceRevisionIds: string[];
  evidenceRevisionIds: string[];
  requirementRevisions: TenderPackageSnapshotRequirementRevisionsItem[];
  responseSectionRevisions: TenderPackageSnapshotResponseSectionRevisionsItem[];
  outputKinds: TenderOutputKind[];
}

export interface TenderPackageRevision {
  /** Current content and evidence still match this checkpoint. */
  current?: boolean;
  id: string;
  tenderId: string;
  /** @minimum 1 */
  revisionNo: number;
  snapshot: TenderPackageSnapshot;
  /** @pattern ^[a-f0-9]{64}$ */
  snapshotHash: string;
  createdAt: string;
}

export interface TenderPackageRevisionList {
  data: TenderPackageRevision[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface TenderReviewSubmit {
  /** @maxLength 4000 */
  note?: string;
}

export type TenderReviewDecisionDecision = typeof TenderReviewDecisionDecision[keyof typeof TenderReviewDecisionDecision];


export const TenderReviewDecisionDecision = {
  approve: 'approve',
  request_changes: 'request_changes',
} as const;

export interface TenderReviewDecision {
  decision: TenderReviewDecisionDecision;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  note: string;
}

export interface TenderReview {
  id: string;
  tenderId: string;
  packageRevisionId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  packageSnapshotHash: string;
  status: TenderReviewStatus;
  /** @nullable */
  submissionNote?: string | null;
  /** @nullable */
  decisionNote?: string | null;
  createdAt: string;
  /** @nullable */
  decidedAt?: string | null;
  /** @nullable */
  invalidatedAt?: string | null;
}

export interface TenderReviewList {
  data: TenderReview[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface TenderManualSubmission {
  submittedAt: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  reference: string;
  /** @maxLength 200 */
  outcome?: string;
  /** @maxLength 4000 */
  note?: string;
}

export interface TenderExportCreate {
  purpose: TenderExportPurpose;
}

export type TenderExportArtifactMetadata = { [key: string]: unknown };

export interface TenderExportArtifact {
  id: string;
  tenderId: string;
  packageRevisionId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  packageSnapshotHash: string;
  outputKind: TenderOutputKind;
  purpose: TenderExportPurpose;
  status: TenderExportStatus;
  provider: TenderStorageProvider;
  providerLocation?: TenderProviderLocation | null;
  /** @nullable */
  fileId?: string | null;
  /**
     * @minLength 1
     * @maxLength 500
     */
  filename: string;
  contentType: string;
  /**
     * @minimum 1
     * @nullable
     */
  sizeBytes?: number | null;
  /**
     * @nullable
     * @pattern ^[a-f0-9]{64}$
     */
  contentHash?: string | null;
  /** @maxItems 100 */
  warnings: string[];
  metadata: TenderExportArtifactMetadata;
  /** @nullable */
  errorCode?: string | null;
  /** @nullable */
  errorMessage?: string | null;
  /** @minimum 0 */
  attemptCount: number;
  /** @nullable */
  downloadUrl?: string | null;
  createdAt: string;
  /** @nullable */
  completedAt?: string | null;
}

export interface TenderExportSourceRevision {
  sourceId: string;
  sourceRevision: string;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
}

export type TenderExportEvidenceRevisionSourceType = typeof TenderExportEvidenceRevisionSourceType[keyof typeof TenderExportEvidenceRevisionSourceType];


export const TenderExportEvidenceRevisionSourceType = {
  tender_source: 'tender_source',
  knowledge: 'knowledge',
} as const;

export interface TenderExportEvidenceRevision {
  selectionId: string;
  sourceType: TenderExportEvidenceRevisionSourceType;
  sourceId: string;
  sourceRevision: string;
  /** @pattern ^[a-f0-9]{64}$ */
  sourceHash: string;
  location: string;
}

export type TenderExportManifestRequirementRevisionsItem = {
  requirementId: string;
  /** @minimum 1 */
  revision: number;
};

export type TenderExportManifestResponseSectionRevisionsItem = {
  sectionId: string;
  /** @minimum 1 */
  revision: number;
};

export interface TenderExportManifest {
  packageRevisionId: string;
  /** @minimum 1 */
  packageRevisionNo: number;
  /** @pattern ^[a-f0-9]{64}$ */
  packageSnapshotHash: string;
  /** @minimum 1 */
  tenderRevision: number;
  bid?: BidPackageSnapshot;
  purpose: TenderExportPurpose;
  /**
     * @minItems 1
     * @maxItems 4
     */
  selectedOutputs: TenderOutputKind[];
  /** @nullable */
  buyerTemplateSourceId?: string | null;
  buyerTemplateMapping?: TenderBuyerTemplateMapping | null;
  /** @nullable */
  quoteRevisionId?: string | null;
  sourceRevisions: TenderExportSourceRevision[];
  evidenceRevisions: TenderExportEvidenceRevision[];
  requirementRevisions: TenderExportManifestRequirementRevisionsItem[];
  responseSectionRevisions: TenderExportManifestResponseSectionRevisionsItem[];
}

export interface TenderExportResult {
  manifest: TenderExportManifest;
  artifacts: TenderExportArtifact[];
}

export interface TenderExportList {
  data: TenderExportArtifact[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type RecurringInterval = typeof RecurringInterval[keyof typeof RecurringInterval];


export const RecurringInterval = {
  none: 'none',
  monthly: 'monthly',
  annual: 'annual',
} as const;

export type QuoteRequest = QuoteRequestBase & ({
  id: string;
  reference: string;
  status: QuoteRequestStatus;
  source: QuoteRequestSource;
  kind: QuoteKind;
  /** @minimum 0 */
  currentRevisionNo: number;
  /** @nullable */
  internalNotes?: string | null;
  /** @nullable */
  subtotal?: string | null;
  /** @nullable */
  discountAmount?: string | null;
  /** @nullable */
  totalPrice?: string | null;
  taxAmount: string;
  oneOffTotal: string;
  recurringTotal: string;
  optionalTotal: string;
  contingencyAmount: string;
  /** Internal total returned only to quote administrators. */
  costTotal?: string;
  /**
     * Internal margin percentage returned only to quote administrators.
     * @nullable
     */
  marginPct?: string | null;
  recurringInterval: RecurringInterval;
  requiresApproval: boolean;
  approvalStatus: QuoteApprovalStatus;
  /** @nullable */
  approvedByUserId?: string | null;
  /** @nullable */
  approvedAt?: string | null;
  /** @nullable */
  approvalNote?: string | null;
  /** @nullable */
  sentAt?: string | null;
  /** @nullable */
  sentByUserId?: string | null;
  /** @nullable */
  acceptedAt?: string | null;
  /** @nullable */
  declinedAt?: string | null;
  /** @nullable */
  declineReason?: string | null;
  aiGenerated: boolean;
  /** @nullable */
  aiBrief?: string | null;
  /** @nullable */
  aiRationaleMd?: string | null;
  /** @nullable */
  submittedAt?: string | null;
  /** @nullable */
  reviewedAt?: string | null;
  /** @nullable */
  respondedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}) & Required<Pick<QuoteRequestBase & ({
  id: string;
  reference: string;
  status: QuoteRequestStatus;
  source: QuoteRequestSource;
  kind: QuoteKind;
  /** @minimum 0 */
  currentRevisionNo: number;
  /** @nullable */
  internalNotes?: string | null;
  /** @nullable */
  subtotal?: string | null;
  /** @nullable */
  discountAmount?: string | null;
  /** @nullable */
  totalPrice?: string | null;
  taxAmount: string;
  oneOffTotal: string;
  recurringTotal: string;
  optionalTotal: string;
  contingencyAmount: string;
  /** Internal total returned only to quote administrators. */
  costTotal?: string;
  /**
     * Internal margin percentage returned only to quote administrators.
     * @nullable
     */
  marginPct?: string | null;
  recurringInterval: RecurringInterval;
  requiresApproval: boolean;
  approvalStatus: QuoteApprovalStatus;
  /** @nullable */
  approvedByUserId?: string | null;
  /** @nullable */
  approvedAt?: string | null;
  /** @nullable */
  approvalNote?: string | null;
  /** @nullable */
  sentAt?: string | null;
  /** @nullable */
  sentByUserId?: string | null;
  /** @nullable */
  acceptedAt?: string | null;
  /** @nullable */
  declinedAt?: string | null;
  /** @nullable */
  declineReason?: string | null;
  aiGenerated: boolean;
  /** @nullable */
  aiBrief?: string | null;
  /** @nullable */
  aiRationaleMd?: string | null;
  /** @nullable */
  submittedAt?: string | null;
  /** @nullable */
  reviewedAt?: string | null;
  /** @nullable */
  respondedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}), 'currency' | 'discountPct' | 'contingencyPct' | 'contingencyLabel'>>;

export interface QuoteRequestList {
  data: QuoteRequest[];
  total: number;
}

export interface QuoteSendResult {
  quote: QuoteRequest;
  /** Secure customer review URL created for this delivery. */
  publicUrl: string;
  pdfDownloadUrl: string;
  expiresAt: string;
}

export interface PublicQuoteLine {
  id: string;
  label: string;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  sectionId?: string | null;
  inclusion: QuoteItemInclusion;
  /** @nullable */
  alternativeToItemId?: string | null;
  quantity: string;
  oneOffTotal: string;
  recurringTotal: string;
  recurringInterval: RecurringInterval;
}

export interface PublicQuoteSection {
  id: string;
  title: string;
  /** @nullable */
  descriptionMd?: string | null;
  sortOrder: number;
}

export interface PublicQuoteSummary {
  reference: string;
  kind: QuoteKind;
  organizationName: string;
  customerName: string;
  /** @nullable */
  customerOrganization?: string | null;
  status: QuoteRequestStatus;
  /** @pattern ^[A-Z]{3}$ */
  currency: string;
  /** @nullable */
  validUntil?: string | null;
  /** @nullable */
  expiresAt?: string | null;
  oneOffTotal: string;
  recurringTotal: string;
  recurringInterval: RecurringInterval;
  taxAmount: string;
  totalPrice: string;
  optionalTotal: string;
  contingencyLabel: string;
  contingencyAmount: string;
  /** @nullable */
  assumptionsMd?: string | null;
  /** @nullable */
  exclusionsMd?: string | null;
  /** @nullable */
  termsMd?: string | null;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  accentColour: string;
  /** @nullable */
  footerText?: string | null;
  pdfDownloadUrl: string;
  sections: PublicQuoteSection[];
  items: PublicQuoteLine[];
}

export interface PublicQuoteDecision {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @maxLength 2000
     * @nullable
     */
  note?: string | null;
}

export interface PublicQuoteDecline {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  reason: string;
}

export type PublicQuoteDecisionResultStatus = typeof PublicQuoteDecisionResultStatus[keyof typeof PublicQuoteDecisionResultStatus];


export const PublicQuoteDecisionResultStatus = {
  accepted: 'accepted',
  declined: 'declined',
} as const;

export interface PublicQuoteDecisionResult {
  status: PublicQuoteDecisionResultStatus;
  message: string;
}

export type QuoteRequestCreate = QuoteRequestBase;

export interface QuoteRequestUpdate {
  kind?: QuoteKind;
  status?: QuoteRequestStatus;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  opportunityId?: string | null;
  /** @nullable */
  priceListId?: string | null;
  /** @nullable */
  requesterName?: string | null;
  /** @nullable */
  requesterEmail?: string | null;
  /** @nullable */
  requesterPhone?: string | null;
  /** @nullable */
  requesterOrganization?: string | null;
  /** @nullable */
  notes?: string | null;
  /** @nullable */
  internalNotes?: string | null;
  /** @nullable */
  subtotal?: string | null;
  /** @nullable */
  discountAmount?: string | null;
  /** @nullable */
  totalPrice?: string | null;
  /** @pattern ^[A-Z]{3}$ */
  currency?: string;
  /** @pattern ^\d+(\.\d+)?$ */
  discountPct?: string;
  /** @nullable */
  termsMd?: string | null;
  /**
     * @maxLength 20000
     * @nullable
     */
  assumptionsMd?: string | null;
  /**
     * @maxLength 20000
     * @nullable
     */
  exclusionsMd?: string | null;
  /** @pattern ^\d+(\.\d+)?$ */
  contingencyPct?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  contingencyLabel?: string;
  /** @nullable */
  validUntil?: string | null;
  /** @nullable */
  expiresAt?: string | null;
}

export type QuoteRequestItemBasePricingMethod = typeof QuoteRequestItemBasePricingMethod[keyof typeof QuoteRequestItemBasePricingMethod];


export const QuoteRequestItemBasePricingMethod = {
  fixed: 'fixed',
  effort: 'effort',
} as const;

export interface QuoteRequestItemBase {
  itemType: QuoteItemType;
  /** @nullable */
  solutionId?: string | null;
  /** @nullable */
  productId?: string | null;
  /** @nullable */
  moduleId?: string | null;
  /** @nullable */
  serviceId?: string | null;
  /** @nullable */
  priceListItemId?: string | null;
  /** @nullable */
  sectionId?: string | null;
  inclusion?: QuoteItemInclusion;
  /** @nullable */
  alternativeToItemId?: string | null;
  pricingMethod?: QuoteRequestItemBasePricingMethod;
  /**
     * @nullable
     * @pattern ^\d+(\.\d+)?$
     */
  effortHours?: string | null;
  /** @nullable */
  rateId?: string | null;
  /** @nullable */
  roleName?: string | null;
  /** @nullable */
  hourlyRateSnapshot?: string | null;
  /** @nullable */
  unitCost?: string | null;
  label: string;
  /** @nullable */
  description?: string | null;
  quantity?: string;
  /** @nullable */
  unitPrice?: string | null;
  /** @nullable */
  oneOffUnitPrice?: string | null;
  /** @nullable */
  recurringUnitPrice?: string | null;
  recurringInterval?: RecurringInterval;
  /** @pattern ^\d+(\.\d+)?$ */
  lineDiscountPct?: string;
  /** @pattern ^\d+(\.\d+)?$ */
  taxRatePct?: string;
  /** @nullable */
  aiRationale?: string | null;
  sortOrder?: number;
}

export type QuoteRequestItem = QuoteRequestItemBase & ({
  id: string;
  quoteRequestId: string;
  /** @nullable */
  lineTotal?: string | null;
  lineOneOffTotal: string;
  lineRecurringTotal: string;
  /** Internal line cost returned only to quote administrators. */
  lineCost?: string;
  /**
     * Internal line margin percentage returned only to quote administrators.
     * @nullable
     */
  marginPct?: string | null;
  createdAt: string;
  updatedAt: string;
}) & Required<Pick<QuoteRequestItemBase & ({
  id: string;
  quoteRequestId: string;
  /** @nullable */
  lineTotal?: string | null;
  lineOneOffTotal: string;
  lineRecurringTotal: string;
  /** Internal line cost returned only to quote administrators. */
  lineCost?: string;
  /**
     * Internal line margin percentage returned only to quote administrators.
     * @nullable
     */
  marginPct?: string | null;
  createdAt: string;
  updatedAt: string;
}), 'pricingMethod' | 'effortHours' | 'rateId' | 'roleName' | 'hourlyRateSnapshot' | 'sectionId' | 'inclusion' | 'alternativeToItemId'>>;

export interface QuoteSectionBase {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /** @nullable */
  descriptionMd?: string | null;
  /** @minimum 0 */
  sortOrder?: number;
}

export type QuoteSection = QuoteSectionBase & {
  id: string;
  quoteRequestId: string;
  createdAt: string;
  updatedAt: string;
} & Required<Pick<QuoteSectionBase & {
  id: string;
  quoteRequestId: string;
  createdAt: string;
  updatedAt: string;
}, 'sortOrder'>>;

export interface QuoteRequestItemList {
  data: QuoteRequestItem[];
  total: number;
  sections: QuoteSection[];
}

export type QuoteRequestItemCreate = QuoteRequestItemBase;

export type QuoteRequestItemUpdatePricingMethod = typeof QuoteRequestItemUpdatePricingMethod[keyof typeof QuoteRequestItemUpdatePricingMethod];


export const QuoteRequestItemUpdatePricingMethod = {
  fixed: 'fixed',
  effort: 'effort',
} as const;

export interface QuoteRequestItemUpdate {
  itemType?: QuoteItemType;
  /** @nullable */
  solutionId?: string | null;
  /** @nullable */
  productId?: string | null;
  /** @nullable */
  moduleId?: string | null;
  /** @nullable */
  serviceId?: string | null;
  /** @nullable */
  priceListItemId?: string | null;
  /** @nullable */
  sectionId?: string | null;
  inclusion?: QuoteItemInclusion;
  /** @nullable */
  alternativeToItemId?: string | null;
  pricingMethod?: QuoteRequestItemUpdatePricingMethod;
  /**
     * @nullable
     * @pattern ^\d+(\.\d+)?$
     */
  effortHours?: string | null;
  /** @nullable */
  rateId?: string | null;
  /** @nullable */
  roleName?: string | null;
  /** @nullable */
  hourlyRateSnapshot?: string | null;
  /** @nullable */
  unitCost?: string | null;
  label?: string;
  /** @nullable */
  description?: string | null;
  quantity?: string;
  /** @nullable */
  unitPrice?: string | null;
  /** @nullable */
  oneOffUnitPrice?: string | null;
  /** @nullable */
  recurringUnitPrice?: string | null;
  recurringInterval?: RecurringInterval;
  /** @pattern ^\d+(\.\d+)?$ */
  lineDiscountPct?: string;
  /** @pattern ^\d+(\.\d+)?$ */
  taxRatePct?: string;
  /** @nullable */
  aiRationale?: string | null;
  sortOrder?: number;
}

export type QuoteSectionCreate = QuoteSectionBase;

export interface QuoteSectionUpdate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
  /** @nullable */
  descriptionMd?: string | null;
  /** @minimum 0 */
  sortOrder?: number;
}

export interface QuoteSectionReorder {
  sectionIds: string[];
}

export interface QuoteRequestDocumentBase {
  fileId: string;
  /** @nullable */
  label?: string | null;
}

export type QuoteRequestDocument = QuoteRequestDocumentBase & ({
  id: string;
  quoteRequestId: string;
  /** @nullable */
  uploadedByUserId?: string | null;
  /** @nullable */
  filename?: string | null;
  /** @nullable */
  contentType?: string | null;
  /** @nullable */
  sizeBytes?: number | null;
  createdAt: string;
});

export interface QuoteRequestDocumentList {
  data: QuoteRequestDocument[];
  total: number;
}

export type QuoteRequestDocumentCreate = QuoteRequestDocumentBase;

export type QuoteRequestDetail = QuoteRequest & {
  items: QuoteRequestItem[];
  sections: QuoteSection[];
  documents: QuoteRequestDocument[];
};

export interface AiQuoteDraftRequest {
  /**
     * @minLength 1
     * @maxLength 4000
     */
  brief: string;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  opportunityId?: string | null;
  /** @nullable */
  priceListId?: string | null;
  requestId?: string;
}

export interface AiOpportunityQuoteDraftRequest {
  /**
     * @minLength 1
     * @maxLength 4000
     */
  brief: string;
  /** @nullable */
  priceListId?: string | null;
  requestId?: string;
}

export interface AiQuoteRevisionRequest {
  /**
     * @minLength 1
     * @maxLength 4000
     */
  instruction: string;
  requestId?: string;
}

export interface AiQuoteDraftResult {
  quote: QuoteRequestDetail;
  summary: string;
  assumptions: string[];
  openQuestions: string[];
  governanceActionId: string;
  draftOnly: true;
}

export interface QuoteApprovalAction {
  /**
     * @maxLength 2000
     * @nullable
     */
  note?: string | null;
}

export interface QuoteRejectionAction {
  /**
     * @minLength 1
     * @maxLength 2000
     */
  note: string;
}

export type QuoteRevisionSnapshot = { [key: string]: unknown };

export interface QuoteRevision {
  id: string;
  quoteRequestId: string;
  /** @minimum 1 */
  revisionNo: number;
  snapshot: QuoteRevisionSnapshot;
  /** @nullable */
  createdByUserId?: string | null;
  reason: string;
  createdAt: string;
}

export interface QuoteRevisionList {
  data: QuoteRevision[];
  /** @minimum 0 */
  total: number;
}

export type QuoteEventPayload = { [key: string]: unknown };

export interface QuoteEvent {
  id: string;
  quoteRequestId: string;
  type: QuoteEventType;
  actorKind: QuoteActorKind;
  /** @nullable */
  actorId?: string | null;
  payload: QuoteEventPayload;
  createdAt: string;
}

export interface QuoteEventList {
  data: QuoteEvent[];
  /** @minimum 0 */
  total: number;
}

export type HealthStatus = typeof HealthStatus[keyof typeof HealthStatus];


export const HealthStatus = {
  ok: 'ok',
} as const;

export type HealthDatabase = typeof HealthDatabase[keyof typeof HealthDatabase];


export const HealthDatabase = {
  connected: 'connected',
  not_configured: 'not_configured',
} as const;

export type HealthPlugins = {
  database: boolean;
  storage: boolean;
  ai: boolean;
  auth: boolean;
};

export interface Health {
  status: HealthStatus;
  uptimeSeconds: number;
  timestamp: string;
  database: HealthDatabase;
  plugins: HealthPlugins;
}

export type PluginStatusValue = typeof PluginStatusValue[keyof typeof PluginStatusValue];


export const PluginStatusValue = {
  ready: 'ready',
  disabled: 'disabled',
  misconfigured: 'misconfigured',
} as const;

export type DatabasePluginCapabilities = {
  persistentStorage: boolean;
  appUsers: boolean;
};

export interface DatabasePlugin {
  enabled: boolean;
  status: PluginStatusValue;
  capabilities: DatabasePluginCapabilities;
}

export type StoragePluginCapabilities = {
  fileUpload: boolean;
  fileDownload: boolean;
};

export interface StoragePlugin {
  enabled: boolean;
  status: PluginStatusValue;
  capabilities: StoragePluginCapabilities;
}

export type AiPluginCapabilities = {
  llm: boolean;
  embeddings: boolean;
  semanticSearch: boolean;
};

export interface AiPlugin {
  enabled: boolean;
  status: PluginStatusValue;
  /** Public-safe explanation when AI is disabled, misconfigured, or partially available. */
  reason?: string;
  capabilities: AiPluginCapabilities;
}

/**
 * Browser interaction contract. popup enables embedded-first sign-in through the dedicated redirect bridge.
 */
export type AuthClientConfigInteractionMode = typeof AuthClientConfigInteractionMode[keyof typeof AuthClientConfigInteractionMode];


export const AuthClientConfigInteractionMode = {
  redirect: 'redirect',
  popup: 'popup',
} as const;

/**
 * Public MSAL client configuration (no secrets).
 */
export interface AuthClientConfig {
  clientId: string;
  authority: string;
  redirectUri: string;
  postLoginRedirect: string;
  scopes: string[];
  /** Delegated custom API scopes for this provider, separate from sign-in scopes. */
  apiScopes?: string[];
  /** Browser interaction contract. popup enables embedded-first sign-in through the dedicated redirect bridge. */
  interactionMode?: AuthClientConfigInteractionMode;
  /** Dedicated MSAL redirect bridge on the application origin, used only for popup and silent requests. */
  popupRedirectUri?: string;
}

export interface ExternalAuthPlugin {
  enabled: boolean;
  status: PluginStatusValue;
  config?: AuthClientConfig;
}

export type AuthPluginCapabilities = {
  login: boolean;
  protectedRoutes: boolean;
  persistentUsers: boolean;
};

export interface AuthPlugin {
  enabled: boolean;
  status: PluginStatusValue;
  capabilities: AuthPluginCapabilities;
  config?: AuthClientConfig;
  external?: ExternalAuthPlugin;
}

export type VideoGenerationPluginStatus = typeof VideoGenerationPluginStatus[keyof typeof VideoGenerationPluginStatus];


export const VideoGenerationPluginStatus = {
  ready: 'ready',
  disabled: 'disabled',
} as const;

export type VideoGenerationPluginProvider = typeof VideoGenerationPluginProvider[keyof typeof VideoGenerationPluginProvider];


export const VideoGenerationPluginProvider = {
  openrouter: 'openrouter',
} as const;

export type VideoGenerationPluginCapabilities = {
  render: boolean;
  stitch: boolean;
};

export type VideoGenerationModelId = typeof VideoGenerationModelId[keyof typeof VideoGenerationModelId];


export const VideoGenerationModelId = {
  'google/veo-31-fast': 'google/veo-3.1-fast',
  'google/veo-31': 'google/veo-3.1',
  'google/veo-31-lite': 'google/veo-3.1-lite',
} as const;

export type VideoGenerationModelTier = typeof VideoGenerationModelTier[keyof typeof VideoGenerationModelTier];


export const VideoGenerationModelTier = {
  draft: 'draft',
  polish: 'polish',
  economy: 'economy',
} as const;

export type VideoGenerationAspectRatio = typeof VideoGenerationAspectRatio[keyof typeof VideoGenerationAspectRatio];


export const VideoGenerationAspectRatio = {
  '16:9': '16:9',
  '9:16': '9:16',
} as const;

export type VideoGenerationDurationSeconds = typeof VideoGenerationDurationSeconds[keyof typeof VideoGenerationDurationSeconds];


export const VideoGenerationDurationSeconds = {
  NUMBER_4: 4,
  NUMBER_6: 6,
  NUMBER_8: 8,
} as const;

export type VideoGenerationResolution = typeof VideoGenerationResolution[keyof typeof VideoGenerationResolution];


export const VideoGenerationResolution = {
  '720p': '720p',
  '1080p': '1080p',
} as const;

export type VideoGenerationModelResolutionConstraints1080p = {
  aspectRatios: VideoGenerationAspectRatio[];
  durationsSeconds: VideoGenerationDurationSeconds[];
};

export type VideoGenerationModelResolutionConstraints = {
  '1080p': VideoGenerationModelResolutionConstraints1080p;
};

export interface VideoGenerationModel {
  id: VideoGenerationModelId;
  label: string;
  tier: VideoGenerationModelTier;
  supportedAspectRatios: VideoGenerationAspectRatio[];
  supportedDurationsSeconds: VideoGenerationDurationSeconds[];
  supportedResolutions: VideoGenerationResolution[];
  resolutionConstraints: VideoGenerationModelResolutionConstraints;
  /** @minimum 0 */
  priceUsdPerSecond: number;
  supportsFirstFrame: boolean;
  supportsReferenceImages: boolean;
}

export interface VideoGenerationPlugin {
  enabled: boolean;
  status: VideoGenerationPluginStatus;
  /** Public-safe setup guidance when OpenRouter video generation is disabled. */
  reason?: string;
  provider: VideoGenerationPluginProvider;
  capabilities: VideoGenerationPluginCapabilities;
  models: VideoGenerationModel[];
}

export interface PluginsStatus {
  database: DatabasePlugin;
  storage: StoragePlugin;
  ai: AiPlugin;
  videoGeneration: VideoGenerationPlugin;
  auth: AuthPlugin;
}

export interface PluginTestResult {
  success: boolean;
  message: string;
  /** @minimum 0 */
  latencyMs: number;
}

export type AgentConfigurationErrorCode = typeof AgentConfigurationErrorCode[keyof typeof AgentConfigurationErrorCode];


export const AgentConfigurationErrorCode = {
  AI_UPSTREAM_AUTH: 'AI_UPSTREAM_AUTH',
  AI_MODEL_UNAVAILABLE: 'AI_MODEL_UNAVAILABLE',
  AI_REQUEST_REJECTED: 'AI_REQUEST_REJECTED',
} as const;

export interface AgentConfigurationError {
  error: string;
  code: AgentConfigurationErrorCode;
}

export type AgentRetryableErrorCode = typeof AgentRetryableErrorCode[keyof typeof AgentRetryableErrorCode];


export const AgentRetryableErrorCode = {
  AI_UPSTREAM_BUSY: 'AI_UPSTREAM_BUSY',
  AI_TIMEOUT: 'AI_TIMEOUT',
  AI_UPSTREAM_UNAVAILABLE: 'AI_UPSTREAM_UNAVAILABLE',
  AI_RESPONSE_INVALID: 'AI_RESPONSE_INVALID',
} as const;

export interface AgentRetryableError {
  error: string;
  code: AgentRetryableErrorCode;
}

/**
 * Database-backed application role stored on app_users.
 */
export type AppUserRole = typeof AppUserRole[keyof typeof AppUserRole];


export const AppUserRole = {
  staff: 'staff',
  admin: 'admin',
} as const;

export interface AdminUser {
  id: string;
  displayName: string;
  /** @nullable */
  email?: string | null;
  role: AppUserRole;
  /** @nullable */
  lastLoginAt?: string | null;
  createdAt: string;
}

export interface PlatformUser {
  id: string;
  displayName: string;
  /** @nullable */
  email?: string | null;
  role: AppUserRole;
  /** @nullable */
  lastLoginAt?: string | null;
  tenantNames: string[];
  platformRoles: string[];
}

export interface PlatformUserPage {
  items: PlatformUser[];
  total: number;
  limit: number;
  offset: number;
}

export interface AddPlatformUserBody {
  /** Stable Microsoft Entra object identifier returned by Graph. */
  entraObjectId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  displayName: string;
  /**
     * Directory mail address or user principal name.
     * @maxLength 320
     */
  email: string;
  /**
     * @minItems 1
     * @maxItems 20
     */
  roleDefinitionIds: string[];
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface SetUserRoleBody {
  role: AppUserRole;
}

/**
 * staff = Entra tenant member; customer = invited guest user.
 */
export type UserRole = typeof UserRole[keyof typeof UserRole];


export const UserRole = {
  staff: 'staff',
  admin: 'admin',
  customer: 'customer',
} as const;

export interface CurrentUser {
  role: UserRole;
  /** @nullable */
  appUserId?: string | null;
  entraObjectId: string;
  entraTenantId: string;
  /** @nullable */
  email?: string | null;
  displayName: string;
  /** True when the user is synced to the app_users table. */
  persistent: boolean;
}

export interface AdminReason {
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export type AdminPermissionCatalogueItemArea = typeof AdminPermissionCatalogueItemArea[keyof typeof AdminPermissionCatalogueItemArea];


export const AdminPermissionCatalogueItemArea = {
  Platform: 'Platform',
  Tenant: 'Tenant',
  Entity: 'Entity',
} as const;

export type AdminPermissionCatalogueItemScope = typeof AdminPermissionCatalogueItemScope[keyof typeof AdminPermissionCatalogueItemScope];


export const AdminPermissionCatalogueItemScope = {
  platform: 'platform',
  tenant: 'tenant',
  entity: 'entity',
} as const;

export interface AdminPermissionCatalogueItem {
  area: AdminPermissionCatalogueItemArea;
  scope: AdminPermissionCatalogueItemScope;
  key: string;
  label: string;
  description: string;
}

export type AdminRoleDefinitionScope = typeof AdminRoleDefinitionScope[keyof typeof AdminRoleDefinitionScope];


export const AdminRoleDefinitionScope = {
  platform: 'platform',
  tenant: 'tenant',
  entity: 'entity',
} as const;

export interface AdminRoleDefinition {
  id: string;
  /** @nullable */
  tenantId: string | null;
  key: string;
  name: string;
  description: string;
  scope: AdminRoleDefinitionScope;
  isSystem: boolean;
  permissions: string[];
  /** @minimum 0 */
  assignmentCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminRoleDefinitionPage {
  items: AdminRoleDefinition[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface AdminPermanentTenantRoleOption {
  id: string;
  name: string;
  description: string;
  permissions: string[];
}

export interface AdminPermanentTenantRoleOptionPage {
  items: AdminPermanentTenantRoleOption[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface AdminPermanentTenantRoleGrantWrite {
  tenantId: string;
  roleDefinitionId: string;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface AdminPermanentTenantRoleGrant {
  tenantId: string;
  membershipId: string;
  roleAssignmentId: string;
  roleDefinitionId: string;
  roleName: string;
  membershipCreated: boolean;
}

export type AdminRoleWriteScope = typeof AdminRoleWriteScope[keyof typeof AdminRoleWriteScope];


export const AdminRoleWriteScope = {
  platform: 'platform',
  tenant: 'tenant',
  entity: 'entity',
} as const;

export interface AdminRoleWrite {
  /** @pattern ^[a-z][a-z0-9-]{1,63}$ */
  key: string;
  /**
     * @minLength 2
     * @maxLength 120
     */
  name: string;
  /**
     * @minLength 2
     * @maxLength 500
     */
  description: string;
  scope: AdminRoleWriteScope;
  /**
     * @minItems 1
     * @maxItems 50
     */
  permissionKeys: string[];
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface AdminGroupMember {
  id: string;
  name: string;
  /** @nullable */
  email: string | null;
}

export type AdminGroupRoleScope = typeof AdminGroupRoleScope[keyof typeof AdminGroupRoleScope];


export const AdminGroupRoleScope = {
  platform: 'platform',
  tenant: 'tenant',
  entity: 'entity',
} as const;

export interface AdminGroupRole {
  id: string;
  name: string;
  scope: AdminGroupRoleScope;
}

export interface AdminGroupEntraBinding {
  id: string;
  objectId: string;
  note: string;
}

export interface AdminGroupEntraBindingWrite {
  objectId: string;
  /** @maxLength 250 */
  note: string;
}

export type AdminAccessGroupScope = typeof AdminAccessGroupScope[keyof typeof AdminAccessGroupScope];


export const AdminAccessGroupScope = {
  platform: 'platform',
  tenant: 'tenant',
} as const;

export interface AdminAccessGroup {
  id: string;
  /** @nullable */
  tenantId: string | null;
  scope: AdminAccessGroupScope;
  name: string;
  description: string;
  members: AdminGroupMember[];
  roles: AdminGroupRole[];
  entraBindings: AdminGroupEntraBinding[];
  createdAt: string;
  updatedAt: string;
}

export interface AdminAccessGroupPage {
  items: AdminAccessGroup[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
  entraGroupsClaimActive: boolean;
  /** @nullable */
  entraGroupsSeenAt: string | null;
}

export interface AdminAccessGroupWrite {
  /**
     * @minLength 2
     * @maxLength 120
     */
  name: string;
  /** @maxLength 500 */
  description: string;
  /** @maxItems 100 */
  memberIds: string[];
  /**
     * @minItems 1
     * @maxItems 50
     */
  roleIds: string[];
  /** @maxItems 20 */
  entraBindings: AdminGroupEntraBindingWrite[];
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface AdminGroupMemberPage {
  items: AdminGroupMember[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface AdminTenant {
  id: string;
  /**
     * @minLength 2
     * @maxLength 120
     */
  name: string;
  slug: string;
  /**
     * @minLength 2
     * @maxLength 80
     * @pattern ^[a-z0-9]+(?:-[a-z0-9]+)*$
     */
  vanityAlias: string;
  status: string;
  ownerEmails: string[];
  entityCount: number;
  memberCount: number;
  createdAt: string;
}

export type AdminTenantDetailsTenant = {
  id: string;
  name: string;
  slug: string;
  /**
     * @minLength 2
     * @maxLength 80
     * @pattern ^[a-z0-9]+(?:-[a-z0-9]+)*$
     */
  vanityAlias: string;
  status: string;
  ownerEmails: string[];
  entityCount: number;
  memberCount: number;
  createdAt: string;
  lastActivityAt: string;
};

export interface AdminOwnerInvitation {
  id: string;
  email: string;
  status: string;
  createdAt: string;
  expiresAt: string;
  /** @nullable */
  acceptedAt: string | null;
}

export interface AdminTenantAuditSummary {
  id: string;
  action: string;
  /** @nullable */
  actorName: string | null;
  outcome: string;
  /** @nullable */
  reason: string | null;
  createdAt: string;
}

export interface AdminTenantDetails {
  tenant: AdminTenantDetailsTenant;
  ownerInvitation: AdminOwnerInvitation | null;
  /** @nullable */
  latestCompletedExportAt: string | null;
  recentAudit: AdminTenantAuditSummary[];
}

export interface AdminOwnerEmailChange {
  /** @maxLength 254 */
  email: string;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface AdminProvisionTenant {
  /**
     * @minLength 2
     * @maxLength 120
     */
  name: string;
  /**
     * @minLength 2
     * @maxLength 80
     * @pattern ^[a-z0-9]+(?:-[a-z0-9]+)*$
     */
  vanityAlias: string;
  /** @maxLength 254 */
  firstOwnerEmail?: string;
  /**
     * @minLength 2
     * @maxLength 120
     */
  firstEntityName?: string;
  /** Grant the provisioning administrator tenant-owner authority and first-entity access for eight hours. */
  grantCreatorTemporaryAccess?: boolean;
  /** @maxLength 1000 */
  reason?: string;
}

export type AdminProvisionResultEmailStatus = typeof AdminProvisionResultEmailStatus[keyof typeof AdminProvisionResultEmailStatus];


export const AdminProvisionResultEmailStatus = {
  sent: 'sent',
  failed: 'failed',
  not_requested: 'not_requested',
} as const;

export interface AdminProvisionResult {
  id: string;
  /** @nullable */
  invitationId: string | null;
  emailStatus: AdminProvisionResultEmailStatus;
  /** @nullable */
  creatorAccessExpiresAt: string | null;
}

export type AdminTenantUpdateStatus = typeof AdminTenantUpdateStatus[keyof typeof AdminTenantUpdateStatus];


export const AdminTenantUpdateStatus = {
  active: 'active',
  suspended: 'suspended',
} as const;

export interface AdminTenantUpdate {
  status?: AdminTenantUpdateStatus;
  /**
     * @minLength 2
     * @maxLength 80
     * @pattern ^[a-z0-9]+(?:-[a-z0-9]+)*$
     */
  vanityAlias?: string;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface AdminTenantArchive {
  /**
     * @minLength 2
     * @maxLength 120
     */
  confirmName: string;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface AdminRoleAssignment {
  id: string;
  appUserId: string;
  /** @nullable */
  email: string | null;
  name: string;
  roleDefinitionId: string;
  roleName: string;
  status: string;
  createdAt: string;
}

export interface AdminAssignRole {
  email: string;
  roleDefinitionId: string;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface AdminEligibleGrant {
  appUserId: string;
  roleDefinitionId: string;
  /** @nullable */
  entityId?: string | null;
  /** @nullable */
  expiresAt?: string | null;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface AdminElevationActivate {
  eligibleAssignmentId: string;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
  /**
     * @minimum 60
     * @maximum 1440
     */
  durationMinutes?: number;
}

export interface AdminElevation {
  id: string;
  appUserId: string;
  /** @nullable */
  tenantId: string | null;
  /** @nullable */
  entityId: string | null;
  roleDefinitionId: string;
  roleName: string;
  status: string;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
  /** @nullable */
  activatedAt: string | null;
  /** @nullable */
  expiresAt: string | null;
  /** @nullable */
  userName?: string | null;
  /** @nullable */
  tenantName?: string | null;
  /** @nullable */
  entityName?: string | null;
}

export interface AdminEligibility {
  id: string;
  appUserId: string;
  /** @nullable */
  tenantId: string | null;
  /** @nullable */
  entityId: string | null;
  roleDefinitionId: string;
  roleName: string;
  status: string;
  /** @nullable */
  expiresAt: string | null;
}

export interface AdminSupportStart {
  tenantId: string;
  /** @nullable */
  entityId?: string | null;
  roleDefinitionId?: string;
  /**
     * @minItems 1
     * @maxItems 15
     */
  permissionKeys?: string[];
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
  /**
     * @minimum 1
     * @maximum 480
     */
  durationMinutes?: number;
}

export interface AdminSupportSession {
  id: string;
  appUserId: string;
  tenantId: string;
  /** @nullable */
  entityId: string | null;
  permissionKeys: string[];
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
  status: string;
  expiresAt: string;
  createdAt: string;
  /** @nullable */
  userName?: string | null;
  /** @nullable */
  tenantName?: string | null;
  /** @nullable */
  entityName?: string | null;
}

export interface AdminEntity {
  id: string;
  tenantId: string;
  /**
     * @minLength 2
     * @maxLength 120
     */
  name: string;
  slug: string;
  status: string;
  memberCount: number;
  createdAt: string;
}

export interface AdminCreateEntity {
  /**
     * @minLength 2
     * @maxLength 120
     */
  name: string;
  /**
     * @minLength 2
     * @maxLength 80
     * @pattern ^[a-z0-9]+(?:-[a-z0-9]+)*$
     */
  slug: string;
  /** @maxLength 1000 */
  reason?: string;
}

export type AdminUpdateEntityStatus = typeof AdminUpdateEntityStatus[keyof typeof AdminUpdateEntityStatus];


export const AdminUpdateEntityStatus = {
  active: 'active',
  suspended: 'suspended',
} as const;

export interface AdminUpdateEntity {
  /**
     * @minLength 2
     * @maxLength 120
     */
  name?: string;
  status?: AdminUpdateEntityStatus;
  /** @maxLength 1000 */
  reason?: string;
}

export interface AdminTransferPreviewInput {
  destinationTenantId: string;
}

export interface AdminTransferInput {
  destinationTenantId: string;
  previewDigest: string;
  /** @maxLength 1000 */
  reason?: string;
}

export interface AdminTransferPreview {
  previewDigest: string;
  entityId: string;
  destinationTenantId: string;
  recordCount: number;
  grantCount: number;
}

export interface AdminSetting {
  key: string;
  value: unknown;
  revision: number;
  updatedAt: string;
}

/**
 * @nullable
 */
export type AdminModuleConfiguration = { [key: string]: unknown } | null;

export interface AdminModule {
  /** @nullable */
  configuration?: AdminModuleConfiguration;
  key: string;
  enabled: boolean;
  revision: number;
  updatedAt: string;
}

export interface AdminSettingWrite {
  /**
     * @minLength 1
     * @maxLength 100
     */
  key: string;
  value: unknown;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

/**
 * @nullable
 */
export type AdminModuleWriteConfiguration = { [key: string]: unknown } | null;

export interface AdminModuleWrite {
  /** @nullable */
  configuration?: AdminModuleWriteConfiguration;
  /**
     * @minLength 1
     * @maxLength 100
     */
  key: string;
  enabled: boolean;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface UsageTokenRate {
  actionKey: string;
  name: string;
  description: string;
  /** @minimum 1 */
  tokens: number;
  active: boolean;
  updatedAt: string;
}

export interface UsageTokenRatePage {
  items: UsageTokenRate[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface UsageTokenRateWrite {
  /**
     * @minimum 1
     * @maximum 1000000
     */
  tokens: number;
  active: boolean;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface UsageTokenBalance {
  tenantId: string;
  tenantName: string;
  /** @minimum 0 */
  balance: number;
  /** @minimum 0 */
  allocated: number;
}

export interface UsageTokenBalancePage {
  items: UsageTokenBalance[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface UsageTokenGrant {
  id: string;
  tenantId: string;
  tenantName?: string;
  /** @minimum 1 */
  amount: number;
  reason: string;
  /** @nullable */
  expiresAt: string | null;
  createdAt: string;
}

export interface UsageTokenGrantPage {
  items: UsageTokenGrant[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface UsageTokenGrantWrite {
  tenantId: string;
  /**
     * @minimum 1
     * @maximum 1000000000
     */
  amount: number;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
  /** @nullable */
  expiresAt?: string | null;
}

export type UsageTokenGrantResult = UsageTokenGrant & ({
  /** @nullable */
  ledgerId: string | null;
  /** @minimum 0 */
  balance: number;
});

export interface TenantUsageTokenSummary {
  tenantId: string;
  /** @minimum 0 */
  balance: number;
  /** @minimum 0 */
  allocated: number;
  /** @minimum 0 */
  entityAllocationCount: number;
}

export interface UsageTokenAllocation {
  /** @nullable */
  id: string | null;
  entityId: string;
  entityName: string;
  /** @minimum 0 */
  amount: number;
  /** @minimum 0 */
  spent: number;
  /** @minimum 0 */
  remaining: number;
  /** @nullable */
  updatedAt: string | null;
}

export interface UsageTokenAllocationPage {
  items: UsageTokenAllocation[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface UsageTokenAllocationWrite {
  /**
     * @minimum 0
     * @maximum 1000000000
     */
  amount: number;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface UsageTokenAllocationRecord {
  id: string;
  tenantId: string;
  entityId: string;
  /** @minimum 0 */
  amount: number;
  createdAt: string;
  updatedAt: string;
}

export interface UsageTokenLedgerEntry {
  id: string;
  /** @nullable */
  entityId: string | null;
  /** @nullable */
  entityName: string | null;
  /** @nullable */
  userId: string | null;
  /** @nullable */
  userName: string | null;
  actionKey: string;
  delta: number;
  /** @minimum 0 */
  balanceAfter: number;
  /** @nullable */
  reference: string | null;
  createdAt: string;
}

export interface UsageTokenLedgerPage {
  items: UsageTokenLedgerEntry[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface UsageTokenReportItem {
  date: string;
  actionKey: string;
  /** @minimum 0 */
  tokens: number;
  /** @minimum 0 */
  transactionCount: number;
}

export interface UsageTokenReport {
  items: UsageTokenReportItem[];
}

export interface UsageTokenCsvExport {
  filename: string;
  csv: string;
  truncated: boolean;
}

export type EntityPackageMode = typeof EntityPackageMode[keyof typeof EntityPackageMode];


export const EntityPackageMode = {
  add: 'add',
  merge: 'merge',
  replace: 'replace',
} as const;

export interface EntityPackageBinding {
  /** @pattern ^[a-f0-9]{64}$ */
  packageSha256: string;
  destinationEntityId: string;
  mode: EntityPackageMode;
  /** @minimum 1 */
  previewRevision: number;
  /** @minimum 0 */
  mappingRevision: number;
  /** @minimum 0 */
  decisionRevision: number;
  /** @pattern ^[a-f0-9]{64}$ */
  destinationContentSha256: string;
  /** @pattern ^[a-f0-9]{64}$ */
  planSha256: string;
  expiresAt: string;
}

export type EntityPackageDependencyKind = typeof EntityPackageDependencyKind[keyof typeof EntityPackageDependencyKind];


export const EntityPackageDependencyKind = {
  destination_user: 'destination_user',
  record: 'record',
  copy_lineage: 'copy_lineage',
  unique_key: 'unique_key',
  singleton: 'singleton',
  provider: 'provider',
  adapter: 'adapter',
} as const;

export type EntityPackageDependencyStatus = typeof EntityPackageDependencyStatus[keyof typeof EntityPackageDependencyStatus];


export const EntityPackageDependencyStatus = {
  resolved: 'resolved',
  unresolved: 'unresolved',
  excluded: 'excluded',
  reconnect_required: 'reconnect_required',
} as const;

export interface EntityPackageDependency {
  key: string;
  kind: EntityPackageDependencyKind;
  required: boolean;
  sourceReference: string;
  /** @nullable */
  destinationReference: string | null;
  status: EntityPackageDependencyStatus;
  message: string;
}

export type EntityPackageFileImportStorage = typeof EntityPackageFileImportStorage[keyof typeof EntityPackageFileImportStorage];


export const EntityPackageFileImportStorage = {
  private_app_files: 'private_app_files',
} as const;

export interface EntityPackageFile {
  fileKey: string;
  family: string;
  recordType: string;
  sourceRecordKey: string;
  fieldPath: string;
  /** @pattern ^files/[a-zA-Z0-9._/-]+$ */
  archivePath: string;
  /** @minimum 0 */
  sizeBytes: number;
  /** @pattern ^[a-f0-9]{64}$ */
  sha256: string;
  contentType: string;
  filename: string;
  sourceRevision: string;
  required: boolean;
  importStorage: EntityPackageFileImportStorage;
}

export interface EntityPackageTable {
  recordType: string;
  /** @minimum 0 */
  recordCount: number;
  /** @pattern ^[a-f0-9]{64}$ */
  sha256: string;
  /** @pattern ^records/[a-z0-9_-]+[.]jsonl$ */
  archivePath: string;
  /** @minimum 1 */
  adapterVersion: number;
}

export type EntityPackageTransformationClassification = typeof EntityPackageTransformationClassification[keyof typeof EntityPackageTransformationClassification];


export const EntityPackageTransformationClassification = {
  transformed: 'transformed',
  derived: 'derived',
  excluded: 'excluded',
} as const;

export interface EntityPackageTransformation {
  recordType: string;
  fieldPath: string;
  classification: EntityPackageTransformationClassification;
  reason: string;
  /** @minimum 0 */
  count: number;
}

export type EntityPackageManifestPackageType = typeof EntityPackageManifestPackageType[keyof typeof EntityPackageManifestPackageType];


export const EntityPackageManifestPackageType = {
  'cdfentity-package': 'cdf.entity-package',
} as const;

export type EntityPackageManifestFormatVersion = typeof EntityPackageManifestFormatVersion[keyof typeof EntityPackageManifestFormatVersion];


export const EntityPackageManifestFormatVersion = {
  NUMBER_1: 1,
} as const;

export type EntityPackageManifestSource = {
  applicationId: string;
  environmentId: string;
  tenantId: string;
  entityId: string;
  entityName: string;
  entityLineageId: string;
};

export type EntityPackageManifestCompatibilityApplicationContract = typeof EntityPackageManifestCompatibilityApplicationContract[keyof typeof EntityPackageManifestCompatibilityApplicationContract];


export const EntityPackageManifestCompatibilityApplicationContract = {
  'cdf-business-entity': 'cdf-business-entity',
} as const;

export type EntityPackageManifestCompatibilityAdapterContractVersion = typeof EntityPackageManifestCompatibilityAdapterContractVersion[keyof typeof EntityPackageManifestCompatibilityAdapterContractVersion];


export const EntityPackageManifestCompatibilityAdapterContractVersion = {
  NUMBER_1: 1,
} as const;

export type EntityPackageManifestCompatibility = {
  applicationContract: EntityPackageManifestCompatibilityApplicationContract;
  schemaRevision: string;
  /** @pattern ^[a-f0-9]{64}$ */
  inventorySha256: string;
  /** @minimum 1 */
  minimumReaderVersion: number;
  adapterContractVersion: EntityPackageManifestCompatibilityAdapterContractVersion;
};

export type EntityPackageManifestTotals = {
  /** @minimum 0 */
  records: number;
  /** @minimum 0 */
  files: number;
  /** @minimum 0 */
  fileBytes: number;
};

export interface EntityPackageManifest {
  packageType: EntityPackageManifestPackageType;
  formatVersion: EntityPackageManifestFormatVersion;
  packageId: string;
  snapshotId: string;
  createdAt: string;
  source: EntityPackageManifestSource;
  compatibility: EntityPackageManifestCompatibility;
  tables: EntityPackageTable[];
  files: EntityPackageFile[];
  dependencies: EntityPackageDependency[];
  transformations: EntityPackageTransformation[];
  totals: EntityPackageManifestTotals;
  /** @pattern ^[a-f0-9]{64}$ */
  contentSha256: string;
}

export interface EntityPackageProgress {
  phase: string;
  /** @minimum 0 */
  completedRecords: number;
  /** @minimum 0 */
  totalRecords: number;
  /** @minimum 0 */
  completedFiles: number;
  /** @minimum 0 */
  totalFiles: number;
  /** @minimum 0 */
  completedBytes: number;
  /** @minimum 0 */
  totalBytes: number;
  /** @minimum 0 */
  attempt: number;
  cancellable: boolean;
  retryable: boolean;
}

export type EntityPackageJobKind = typeof EntityPackageJobKind[keyof typeof EntityPackageJobKind];


export const EntityPackageJobKind = {
  export: 'export',
  upload: 'upload',
  preflight: 'preflight',
  apply: 'apply',
  recovery: 'recovery',
} as const;

export type EntityPackageJobStatus = typeof EntityPackageJobStatus[keyof typeof EntityPackageJobStatus];


export const EntityPackageJobStatus = {
  queued: 'queued',
  running: 'running',
  blocked: 'blocked',
  awaiting_review: 'awaiting_review',
  committing: 'committing',
  succeeded: 'succeeded',
  failed: 'failed',
  cancelled: 'cancelled',
  reconciliation_required: 'reconciliation_required',
} as const;

export interface EntityPackageJob {
  id: string;
  entityId: string;
  kind: EntityPackageJobKind;
  mode: EntityPackageMode | null;
  status: EntityPackageJobStatus;
  progress: EntityPackageProgress;
  /**
     * @nullable
     * @pattern ^[a-f0-9]{64}$
     */
  packageSha256: string | null;
  manifest: EntityPackageManifest | null;
  binding: EntityPackageBinding | null;
  /** @nullable */
  recoveryJobId: string | null;
  /** @nullable */
  errorCode: string | null;
  /** @nullable */
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EntityPackageJobPage {
  items: EntityPackageJob[];
  /** @minimum 0 */
  total: number;
  /** @minimum 0 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type EntityPackageRecordEnvelopeData = {[key: string]: unknown};

export interface EntityPackageRecordEnvelope {
  recordType: string;
  sourceRecordKey: string;
  sourceEntityLineageId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  sourceContentSha256: string;
  immutable: boolean;
  data: EntityPackageRecordEnvelopeData;
}

export type EntityPackageChangeAction = typeof EntityPackageChangeAction[keyof typeof EntityPackageChangeAction];


export const EntityPackageChangeAction = {
  add: 'add',
  update: 'update',
  delete: 'delete',
  unchanged: 'unchanged',
  conflict: 'conflict',
  blocked: 'blocked',
} as const;

export interface EntityPackageChange {
  recordType: string;
  sourceRecordKey: string;
  /** @nullable */
  destinationRecordKey: string | null;
  action: EntityPackageChangeAction;
  changedFields: string[];
  immutable: boolean;
  packageRecord?: EntityPackageRecordEnvelope;
}

export type EntityPackageConflictKind = typeof EntityPackageConflictKind[keyof typeof EntityPackageConflictKind];


export const EntityPackageConflictKind = {
  value: 'value',
  relationship: 'relationship',
  local_deletion: 'local_deletion',
  multiple_copies: 'multiple_copies',
  singleton: 'singleton',
  unique_key: 'unique_key',
  immutable_history: 'immutable_history',
} as const;

export type EntityPackageConflictResolution = typeof EntityPackageConflictResolution[keyof typeof EntityPackageConflictResolution];


export const EntityPackageConflictResolution = {
  unresolved: 'unresolved',
  keep_destination: 'keep_destination',
  use_package: 'use_package',
  add_copy: 'add_copy',
  block: 'block',
} as const;

export interface EntityPackageConflict {
  id: string;
  recordType: string;
  sourceRecordKey: string;
  /** @nullable */
  destinationRecordKey: string | null;
  fieldPath: string;
  kind: EntityPackageConflictKind;
  packageValue: unknown | null;
  destinationValue: unknown | null;
  /** @pattern ^[a-f0-9]{64}$ */
  packageValueSha256: string;
  /** @pattern ^[a-f0-9]{64}$ */
  destinationValueSha256: string;
  resolution: EntityPackageConflictResolution;
  /** @minimum 0 */
  revision: number;
}

export interface EntityPackageConflictPage {
  items: EntityPackageConflict[];
  /** @minimum 0 */
  total: number;
  /** @minimum 0 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type EntityPackagePreviewCounts = {
  /** @minimum 0 */
  adds: number;
  /** @minimum 0 */
  updates: number;
  /** @minimum 0 */
  deletes: number;
  /** @minimum 0 */
  unchanged: number;
  /** @minimum 0 */
  conflicts: number;
  /** @minimum 0 */
  blockers: number;
};

export type EntityPackagePreviewFiles = {
  /** @minimum 0 */
  count: number;
  /** @minimum 0 */
  bytes: number;
  /** @minimum 0 */
  unavailable: number;
};

export interface EntityPackagePreview {
  job: EntityPackageJob;
  binding: EntityPackageBinding;
  counts: EntityPackagePreviewCounts;
  changes: EntityPackageChange[];
  /** @minimum 0 */
  totalChanges: number;
  /** @minimum 0 */
  limit: number;
  /** @minimum 0 */
  offset: number;
  dependencies: EntityPackageDependency[];
  transformations: EntityPackageTransformation[];
  files: EntityPackagePreviewFiles;
  recoveryRequired: boolean;
  activationRequired: boolean;
}

export interface EntityPackageExportRequest {
  idempotencyKey: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  reason: string;
}

export type EntityPackageImportOptionsCopiesItem = {
  id: string;
  createdAt: string;
  /** @minimum 0 */
  mappingRevision: number;
};

export type EntityPackageImportOptionsUserReferencesItem = {
  sourceReference: string;
  label: string;
};

export interface EntityPackageImportOptions {
  copies: EntityPackageImportOptionsCopiesItem[];
  /** @minimum 0 */
  copyTotal: number;
  userReferences: EntityPackageImportOptionsUserReferencesItem[];
  /** @minimum 0 */
  userReferenceTotal: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type EntityPackagePreflightRequestUserMappingsItem = {
  sourceReference: string;
  destinationAppUserId: string;
};

export interface EntityPackagePreflightRequest {
  mode: EntityPackageMode;
  copyLineageId?: string;
  userMappings: EntityPackagePreflightRequestUserMappingsItem[];
  /** @minimum 0 */
  expectedMappingRevision: number;
  idempotencyKey: string;
}

export type EntityPackageDecisionRequestResolution = typeof EntityPackageDecisionRequestResolution[keyof typeof EntityPackageDecisionRequestResolution];


export const EntityPackageDecisionRequestResolution = {
  keep_destination: 'keep_destination',
  use_package: 'use_package',
  add_copy: 'add_copy',
  block: 'block',
} as const;

export interface EntityPackageDecisionRequest {
  binding: EntityPackageBinding;
  conflictId: string;
  /** @minimum 0 */
  expectedRevision: number;
  resolution: EntityPackageDecisionRequestResolution;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  reason: string;
  idempotencyKey: string;
}

export type EntityPackageApplyRequest = {
  binding: {
  /** @pattern ^[a-f0-9]{64}$ */
  packageSha256: string;
  destinationEntityId: string;
  mode: 'add' | 'merge';
  /** @minimum 1 */
  previewRevision: number;
  /** @minimum 0 */
  mappingRevision: number;
  /** @minimum 0 */
  decisionRevision: number;
  /** @pattern ^[a-f0-9]{64}$ */
  destinationContentSha256: string;
  /** @pattern ^[a-f0-9]{64}$ */
  planSha256: string;
  expiresAt: string;
};
  idempotencyKey: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  reason: string;
} | {
  binding: {
  /** @pattern ^[a-f0-9]{64}$ */
  packageSha256: string;
  destinationEntityId: string;
  mode: 'replace';
  /** @minimum 1 */
  previewRevision: number;
  /** @minimum 0 */
  mappingRevision: number;
  /** @minimum 0 */
  decisionRevision: number;
  /** @pattern ^[a-f0-9]{64}$ */
  destinationContentSha256: string;
  /** @pattern ^[a-f0-9]{64}$ */
  planSha256: string;
  expiresAt: string;
};
  idempotencyKey: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  reason: string;
  replacementConfirmation: {
  destinationEntityId: string;
  destinationEntityName: string;
  confirmation: 'REPLACE';
  /** @pattern ^[a-f0-9]{64}$ */
  recoverySnapshotSha256: string;
};
};

export interface EntityPackageReplacementRequest {
  binding: EntityPackageBinding;
  idempotencyKey: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  reason: string;
}

export interface EntityPackageReplacementReview {
  recoveryJobId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  recoverySnapshotSha256: string;
  destinationEntityId: string;
  destinationEntityName: string;
  binding: EntityPackageBinding;
  /** @minimum 0 */
  deletionCount: number;
}

export type EntityPackageRecoveryRequestConfirmation = typeof EntityPackageRecoveryRequestConfirmation[keyof typeof EntityPackageRecoveryRequestConfirmation];


export const EntityPackageRecoveryRequestConfirmation = {
  RECOVER: 'RECOVER',
} as const;

export interface EntityPackageRecoveryRequest {
  binding: EntityPackageBinding;
  /** @pattern ^[a-f0-9]{64}$ */
  recoverySnapshotSha256: string;
  confirmation: EntityPackageRecoveryRequestConfirmation;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  reason: string;
  idempotencyKey: string;
}

export type DataManagementArea = typeof DataManagementArea[keyof typeof DataManagementArea];


export const DataManagementArea = {
  entities_access: 'entities_access',
  crm_records: 'crm_records',
  content_hub: 'content_hub',
  research: 'research',
  catalogue: 'catalogue',
  custom_pages: 'custom_pages',
  settings_modules: 'settings_modules',
} as const;

export interface DataManagementCount {
  /** @minimum 0 */
  create: number;
  /** @minimum 0 */
  update: number;
  /** @minimum 0 */
  delete: number;
  /** @minimum 0 */
  skip: number;
}

export type DataManagementAreaDiff = DataManagementCount & {
  area: DataManagementArea;
  label: string;
};

export type DataManagementTableDiff = DataManagementCount & {
  table: string;
  area: DataManagementArea;
};

export interface DataManagementSummary {
  areas: DataManagementAreaDiff[];
  tables: DataManagementTableDiff[];
  warnings: string[];
}

export type DataManagementJobKind = typeof DataManagementJobKind[keyof typeof DataManagementJobKind];


export const DataManagementJobKind = {
  export: 'export',
  import: 'import',
} as const;

export type DataManagementJobStatus = typeof DataManagementJobStatus[keyof typeof DataManagementJobStatus];


export const DataManagementJobStatus = {
  queued: 'queued',
  running: 'running',
  completed: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
  awaiting_apply: 'awaiting_apply',
  applied: 'applied',
} as const;

export interface DataManagementJob {
  id: string;
  tenantId: string;
  kind: DataManagementJobKind;
  status: DataManagementJobStatus;
  areas: DataManagementArea[];
  summary: DataManagementSummary | null;
  /** @nullable */
  error: string | null;
  createdAt: string;
  /** @nullable */
  startedAt: string | null;
  /** @nullable */
  completedAt: string | null;
  downloadReady: boolean;
}

export interface DataManagementJobPage {
  items: DataManagementJob[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface DataExportRequest {
  /**
     * @minItems 1
     * @maxItems 7
     */
  areas: DataManagementArea[];
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export type DataImportApplyConfirmation = typeof DataImportApplyConfirmation[keyof typeof DataImportApplyConfirmation];


export const DataImportApplyConfirmation = {
  OVERWRITE: 'OVERWRITE',
} as const;

export interface DataImportApply {
  confirmation: DataImportApplyConfirmation;
}

export type AdminFeatureStage = typeof AdminFeatureStage[keyof typeof AdminFeatureStage];


export const AdminFeatureStage = {
  available: 'available',
  preview: 'preview',
  deprecated: 'deprecated',
  hidden: 'hidden',
} as const;

export interface AdminFeature {
  key: string;
  name: string;
  description: string;
  stage: AdminFeatureStage;
  defaultEnabled: boolean;
  sortOrder: number;
  updatedAt: string;
}

export interface AdminFeaturePage {
  items: AdminFeature[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type AdminFeatureWriteStage = typeof AdminFeatureWriteStage[keyof typeof AdminFeatureWriteStage];


export const AdminFeatureWriteStage = {
  available: 'available',
  preview: 'preview',
  deprecated: 'deprecated',
  hidden: 'hidden',
} as const;

export interface AdminFeatureWrite {
  /**
     * @minLength 2
     * @maxLength 100
     */
  name: string;
  /**
     * @minLength 2
     * @maxLength 500
     */
  description: string;
  stage: AdminFeatureWriteStage;
  defaultEnabled: boolean;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export type AdminFeatureTenantOverrideWriteForcedState = typeof AdminFeatureTenantOverrideWriteForcedState[keyof typeof AdminFeatureTenantOverrideWriteForcedState];


export const AdminFeatureTenantOverrideWriteForcedState = {
  on: 'on',
  off: 'off',
} as const;

export interface AdminFeatureTenantOverrideWrite {
  forcedState: AdminFeatureTenantOverrideWriteForcedState;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export type AdminFeatureTenantOverrideForcedState = typeof AdminFeatureTenantOverrideForcedState[keyof typeof AdminFeatureTenantOverrideForcedState];


export const AdminFeatureTenantOverrideForcedState = {
  on: 'on',
  off: 'off',
} as const;

export interface AdminFeatureTenantOverride {
  featureKey: string;
  tenantId: string;
  forcedState: AdminFeatureTenantOverrideForcedState;
  updatedAt: string;
}

export type TenantModuleStage = typeof TenantModuleStage[keyof typeof TenantModuleStage];


export const TenantModuleStage = {
  available: 'available',
  preview: 'preview',
  deprecated: 'deprecated',
  hidden: 'hidden',
} as const;

/**
 * @nullable
 */
export type TenantModuleForcedState = typeof TenantModuleForcedState[keyof typeof TenantModuleForcedState] | null;


export const TenantModuleForcedState = {
  on: 'on',
  off: 'off',
} as const;

export interface TenantModule {
  key: string;
  name: string;
  description: string;
  stage: TenantModuleStage;
  enabled: boolean;
  locked: boolean;
  /** @nullable */
  forcedState: TenantModuleForcedState;
  /** @nullable */
  revision: number | null;
  /** @nullable */
  updatedAt: string | null;
}

export interface TenantModulePage {
  items: TenantModule[];
  /** @minimum 0 */
  total: number;
}

export interface TenantModuleToggle {
  enabled: boolean;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export type TenantTypedSettingsAdvanced = { [key: string]: unknown };

export interface TenantTypedSettings {
  /**
     * @minLength 2
     * @maxLength 120
     */
  brandingName: string;
  /** @pattern ^[a-z]{2,3}(?:-[A-Z]{2})?$ */
  defaultLocale: string;
  /**
     * @minLength 1
     * @maxLength 100
     */
  defaultTimezone: string;
  /** @nullable */
  notificationSender: string | null;
  /** @nullable */
  supportContact: string | null;
  advanced: TenantTypedSettingsAdvanced;
}

export type TenantTypedSettingsWrite = TenantTypedSettings & {
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
};

export interface BusinessFunction {
  id: string;
  key: string;
  label: string;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  colour: string;
  /** @minimum 0 */
  sortOrder: number;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export type BusinessFunctionAssignmentKind = typeof BusinessFunctionAssignmentKind[keyof typeof BusinessFunctionAssignmentKind];


export const BusinessFunctionAssignmentKind = {
  stage: 'stage',
  action_type: 'action_type',
  staff: 'staff',
} as const;

export interface BusinessFunctionAssignment {
  id: string;
  functionId: string;
  kind: BusinessFunctionAssignmentKind;
  /** @nullable */
  journeyKey: string | null;
  ref: string;
  createdAt: string;
  updatedAt: string;
}

export interface BusinessFunctionJourneyStage {
  id: string;
  label: string;
  order: number;
}

export interface BusinessFunctionJourney {
  key: string;
  label: string;
  stages: BusinessFunctionJourneyStage[];
}

export interface BusinessFunctionActionType {
  id: string;
  label: string;
}

export interface BusinessFunctionStaffMember {
  id: string;
  name: string;
  /** @nullable */
  email: string | null;
}

export interface BusinessFunctionStaffPage {
  items: BusinessFunctionStaffMember[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface BusinessFunctionDirectory {
  functions: BusinessFunction[];
  assignments: BusinessFunctionAssignment[];
  journeys: BusinessFunctionJourney[];
  actionTypes: BusinessFunctionActionType[];
  staff: BusinessFunctionStaffPage;
}

export interface CreateBusinessFunctionInput {
  /**
     * @minLength 2
     * @maxLength 80
     */
  label: string;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  colour: string;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface UpdateBusinessFunctionInput {
  /**
     * @minLength 2
     * @maxLength 80
     */
  label?: string;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  colour?: string;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface DeleteBusinessFunctionInput {
  /** @nullable */
  reassignToId?: string | null;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface ReorderBusinessFunctionsInput {
  /**
     * @minItems 1
     * @maxItems 100
     */
  ids: string[];
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export type BusinessFunctionAssignmentInputKind = typeof BusinessFunctionAssignmentInputKind[keyof typeof BusinessFunctionAssignmentInputKind];


export const BusinessFunctionAssignmentInputKind = {
  stage: 'stage',
  action_type: 'action_type',
  staff: 'staff',
} as const;

export interface BusinessFunctionAssignmentInput {
  kind: BusinessFunctionAssignmentInputKind;
  /** @nullable */
  journeyKey?: string | null;
  /**
     * @minLength 1
     * @maxLength 200
     */
  ref: string;
}

export interface ReplaceBusinessFunctionAssignmentsInput {
  /** @maxItems 1000 */
  assignments: BusinessFunctionAssignmentInput[];
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export interface AdminAuditEvent {
  id: string;
  /** @nullable */
  tenantId: string | null;
  /** @nullable */
  entityId: string | null;
  /** @nullable */
  actorAppUserId: string | null;
  action: string;
  targetType: string;
  /** @nullable */
  targetId: string | null;
  outcome: string;
  /** @nullable */
  reason: string | null;
  createdAt: string;
  /** @nullable */
  actorName?: string | null;
}

export interface AdminRecoveryActivate {
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
  /**
     * @minimum 15
     * @maximum 60
     */
  durationMinutes?: number;
}

export interface AdminRecoveryCustodianAppoint {
  appUserId: string;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export type AdminRecoveryCustodianStatus = typeof AdminRecoveryCustodianStatus[keyof typeof AdminRecoveryCustodianStatus];


export const AdminRecoveryCustodianStatus = {
  active: 'active',
  revoked: 'revoked',
} as const;

export interface AdminRecoveryCustodian {
  id: string;
  appUserId: string;
  /** @nullable */
  userName: string | null;
  /** @nullable */
  email: string | null;
  status: AdminRecoveryCustodianStatus;
  reason: string;
  /** @nullable */
  appointedByName: string | null;
  createdAt: string;
  /** @nullable */
  revokedAt: string | null;
}

export interface AdminRecoveryCustodianPage {
  items: AdminRecoveryCustodian[];
  total: number;
  limit: number;
  offset: number;
  activeCount: number;
  minimumCustodians: number;
  ready: boolean;
}

export type AdminRecoverySessionStatus = typeof AdminRecoverySessionStatus[keyof typeof AdminRecoverySessionStatus];


export const AdminRecoverySessionStatus = {
  active: 'active',
  expired: 'expired',
  ended: 'ended',
  revoked: 'revoked',
} as const;

export interface AdminRecoverySession {
  id: string;
  appUserId: string;
  /** @nullable */
  userName: string | null;
  custodianId: string;
  status: AdminRecoverySessionStatus;
  reason: string;
  expiresAt: string;
  createdAt: string;
  /** @nullable */
  endedAt: string | null;
}

export interface AdminRecoverySessionPage {
  items: AdminRecoverySession[];
  total: number;
  limit: number;
  offset: number;
  activeTotal: number;
}

export interface AdminMyRecoveryAccess {
  isCustodian: boolean;
  ready: boolean;
  activeCustodians: number;
  minimumCustodians: number;
  minDurationMinutes: number;
  maxDurationMinutes: number;
  session: AdminRecoverySession | null;
}

export interface AdminOk {
  ok: boolean;
}

export interface AdminTenantPage {
  items: AdminTenant[];
  total: number;
  limit: number;
  offset: number;
  activeTotal?: number;
  suspendedTotal?: number;
  archivedTotal?: number;
}

export interface AdminRoleAssignmentPage {
  items: AdminRoleAssignment[];
  total: number;
  limit: number;
  offset: number;
  administratorTotal?: number;
}

export interface AdminEligibilityPage {
  items: AdminEligibility[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminElevationPage {
  items: AdminElevation[];
  total: number;
  limit: number;
  offset: number;
  activeTotal?: number;
}

export interface AdminSupportSessionPage {
  items: AdminSupportSession[];
  total: number;
  limit: number;
  offset: number;
  activeTotal?: number;
}

export interface AdminEntityPage {
  items: AdminEntity[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminSettingPage {
  items: AdminSetting[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminModulePage {
  items: AdminModule[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminAuditEventPage {
  items: AdminAuditEvent[];
  total: number;
  limit: number;
  offset: number;
}

export type AccessIdentityRoleScope = typeof AccessIdentityRoleScope[keyof typeof AccessIdentityRoleScope];


export const AccessIdentityRoleScope = {
  platform: 'platform',
  tenant: 'tenant',
  entity: 'entity',
} as const;

export type AccessIdentityRoleSource = typeof AccessIdentityRoleSource[keyof typeof AccessIdentityRoleSource];


export const AccessIdentityRoleSource = {
  direct: 'direct',
  group: 'group',
  entra: 'entra',
  elevation: 'elevation',
} as const;

export interface AccessIdentityRole {
  id: string;
  key: string;
  name: string;
  scope: AccessIdentityRoleScope;
  source?: AccessIdentityRoleSource;
  /** @nullable */
  groupName?: string | null;
}

export interface AccessIdentityEntity {
  id: string;
  name: string;
  status: string;
  roles: AccessIdentityRole[];
}

export type AccessProfileStatus = typeof AccessProfileStatus[keyof typeof AccessProfileStatus];


export const AccessProfileStatus = {
  active: 'active',
  suspended: 'suspended',
  archived: 'archived',
  transferring: 'transferring',
} as const;

export interface AccessProfile {
  id: string;
  name: string;
  slug: string;
  vanityAlias?: string;
  status: AccessProfileStatus;
}

export type AccessIdentityTenant = AccessProfile & {
  permissions?: string[];
  roles: AccessIdentityRole[];
  entities: AccessIdentityEntity[];
  /** @minimum 0 */
  entityTotal: number;
};

export type AccessEligibleRole = AccessIdentityRole & ({
  eligibleAssignmentId: string;
  /** @nullable */
  tenantId: string | null;
  /** @nullable */
  entityId: string | null;
  /** @nullable */
  expiresAt: string | null;
});

export interface AccessActiveElevation {
  id: string;
  /** @nullable */
  tenantId: string | null;
  /** @nullable */
  entityId: string | null;
  roleId: string;
  roleName: string;
  reason: string;
  expiresAt: string;
  activatedAt: string;
}

export interface AccessActiveSupportSession {
  id: string;
  tenantId: string;
  /** @nullable */
  entityId: string | null;
  permissionKey: string;
  permissionKeys?: string[];
  reason: string;
  expiresAt: string;
  createdAt: string;
}

/**
 * Identity access independent of the selected entity. Collections use limit/offset and report their own totals. Tenants and nested entities are grouped from a flattened tenant/entity page; hierarchyTotal counts those entries.
 */
export interface AccessIdentitySummary {
  platformPermissions: string[];
  platformRoles: AccessIdentityRole[];
  isPlatformAdmin: boolean;
  tenants: AccessIdentityTenant[];
  /** @minimum 0 */
  tenantTotal: number;
  /** @minimum 0 */
  hierarchyTotal: number;
  eligibleRoles: AccessEligibleRole[];
  /** @minimum 0 */
  eligibleRoleTotal: number;
  activeElevations: AccessActiveElevation[];
  /** @minimum 0 */
  activeElevationTotal: number;
  activeSupportSessions: AccessActiveSupportSession[];
  /** @minimum 0 */
  activeSupportSessionTotal: number;
}

export interface AccessContextOption {
  tenantId: string;
  tenantName: string;
  entityId: string;
  entityName: string;
}

export type AccessContextList = AccessIdentitySummary & {
  items: AccessContextOption[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
};

/**
 * Lifecycle metadata for enabled modules; preview is displayed as Frontier.
 */
export type AccessContextSnapshotFeatureStages = {[key: string]: 'available' | 'preview' | 'deprecated' | 'hidden'};

export type AccessContextSnapshot = AccessIdentitySummary & AccessContextOption & ({
  /** @nullable */
  logoAssetId: string | null;
  permissions: string[];
  enabledModules: string[];
  /** Lifecycle metadata for enabled modules; preview is displayed as Frontier. */
  featureStages?: AccessContextSnapshotFeatureStages;
  authorizationRevision: string;
  viaOverride: boolean;
});

export type AccessRoleScope = typeof AccessRoleScope[keyof typeof AccessRoleScope];


export const AccessRoleScope = {
  tenant: 'tenant',
  entity: 'entity',
} as const;

export interface AccessRole {
  id: string;
  key: string;
  name: string;
  scope: AccessRoleScope;
  permissions: string[];
  canonical: boolean;
}

export type AccessMemberMembershipStatus = typeof AccessMemberMembershipStatus[keyof typeof AccessMemberMembershipStatus];


export const AccessMemberMembershipStatus = {
  active: 'active',
  suspended: 'suspended',
  revoked: 'revoked',
} as const;

export interface AccessMember {
  id: string;
  membershipId: string;
  displayName: string;
  /** @nullable */
  email: string | null;
  legacyRole: AppUserRole;
  roles: AccessRole[];
  hasEntityAccess: boolean;
  membershipStatus: AccessMemberMembershipStatus;
  /** @nullable */
  grantId?: string | null;
  /** @nullable */
  grantExpiresAt?: string | null;
  /** @nullable */
  grantedBy?: string | null;
  /** @nullable */
  grantedAt?: string | null;
  /** @nullable */
  lastActiveAt?: string | null;
}

export type EntityAccessInvitationStatus = typeof EntityAccessInvitationStatus[keyof typeof EntityAccessInvitationStatus];


export const EntityAccessInvitationStatus = {
  pending: 'pending',
  expired: 'expired',
} as const;

export interface EntityAccessInvitation {
  id: string;
  email: string;
  status: EntityAccessInvitationStatus;
  roleIds: string[];
  roleNames: string[];
  /** @nullable */
  message?: string | null;
  expiresAt: string;
  createdAt: string;
  /** @nullable */
  lastSentAt: string | null;
}

export interface EntityAdministrationPage {
  tenant: AccessProfile;
  entity: AccessProfile;
  members: AccessMember[];
  memberTotal: number;
  invitations: EntityAccessInvitation[];
  invitationTotal: number;
  roles: AccessRole[];
  /** @nullable */
  connectionCount: number | null;
  /** @nullable */
  publishedPageCount: number | null;
  limit: number;
  offset: number;
}

export interface EntityAccessCandidate {
  id: string;
  displayName: string;
  /** @nullable */
  email: string | null;
  /** @nullable */
  lastActiveAt: string | null;
}

export interface EntityAccessCandidatePage {
  items: EntityAccessCandidate[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface EntityAccessCreate {
  userId?: string;
  /** @maxLength 320 */
  email?: string;
  /**
     * @minItems 1
     * @maxItems 20
     */
  roleIds: string[];
  /** @maxLength 2000 */
  message?: string;
}

export type EntityAccessCreateResultStatus = typeof EntityAccessCreateResultStatus[keyof typeof EntityAccessCreateResultStatus];


export const EntityAccessCreateResultStatus = {
  granted: 'granted',
  invited: 'invited',
  merged: 'merged',
} as const;

export type EntityAccessCreateResultEmailStatus = typeof EntityAccessCreateResultEmailStatus[keyof typeof EntityAccessCreateResultEmailStatus];


export const EntityAccessCreateResultEmailStatus = {
  not_required: 'not_required',
  sent: 'sent',
  suppressed: 'suppressed',
  failed: 'failed',
} as const;

export interface EntityAccessCreateResult {
  status: EntityAccessCreateResultStatus;
  /** @nullable */
  grantId: string | null;
  /** @nullable */
  invitationId: string | null;
  emailStatus: EntityAccessCreateResultEmailStatus;
}

export interface EntityAccessRolesUpdate {
  /**
     * @minItems 1
     * @maxItems 20
     */
  roleIds: string[];
  /**
     * @minLength 8
     * @maxLength 500
     */
  reason: string;
}

export type EntityAccessInvitationResultEmailStatus = typeof EntityAccessInvitationResultEmailStatus[keyof typeof EntityAccessInvitationResultEmailStatus];


export const EntityAccessInvitationResultEmailStatus = {
  sent: 'sent',
  suppressed: 'suppressed',
  failed: 'failed',
} as const;

export interface EntityAccessInvitationResult {
  ok: boolean;
  emailStatus: EntityAccessInvitationResultEmailStatus;
}

export type AccessInvitationStatus = typeof AccessInvitationStatus[keyof typeof AccessInvitationStatus];


export const AccessInvitationStatus = {
  pending: 'pending',
  accepted: 'accepted',
  revoked: 'revoked',
  expired: 'expired',
} as const;

export interface AccessInvitation {
  id: string;
  email: string;
  status: AccessInvitationStatus;
  /** @nullable */
  roleDefinitionId: string | null;
  /** @nullable */
  roleName: string | null;
  expiresAt: string;
  createdAt: string;
}

export interface AccessSetting {
  key: string;
  value: unknown;
  /** @minimum 1 */
  revision: number;
  updatedAt: string;
}

export interface AccessModule {
  key: string;
  enabled: boolean;
  /** @minimum 1 */
  revision: number;
  updatedAt: string;
}

export interface AccessAdministrationPage {
  tenant: AccessProfile;
  entity: AccessProfile;
  permissions: string[];
  members: AccessMember[];
  /** @minimum 0 */
  memberTotal: number;
  invitations: AccessInvitation[];
  /** @minimum 0 */
  invitationTotal: number;
  roles: AccessRole[];
  settings: AccessSetting[];
  modules: AccessModule[];
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface AccessInvitationCreate {
  /** @maxLength 320 */
  email: string;
  roleDefinitionId: string;
  expiresAt: string;
}

export interface AccessInvitationCreated {
  id: string;
  /** @minLength 32 */
  token: string;
}

export interface AccessInvitationAccept {
  /**
     * @minLength 32
     * @maxLength 512
     */
  token: string;
}

export interface AccessInvitationAccepted {
  tenantId: string;
  /** @nullable */
  entityId: string | null;
}

export interface AccessReason {
  /**
     * @minLength 8
     * @maxLength 500
     */
  reason: string;
}

export interface AccessEntityGrant {
  roleDefinitionId?: string;
}

export interface AccessProfileUpdate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
}

export interface AccessSettingUpdate {
  value: unknown;
}

export interface AccessModuleUpdate {
  enabled: boolean;
}

export interface AccessMutationResult {
  ok: boolean;
}

export type AccessAuditEventOutcome = typeof AccessAuditEventOutcome[keyof typeof AccessAuditEventOutcome];


export const AccessAuditEventOutcome = {
  succeeded: 'succeeded',
  denied: 'denied',
  failed: 'failed',
} as const;

export interface AccessAuditEvent {
  id: string;
  action: string;
  targetType: string;
  /** @nullable */
  targetId?: string | null;
  /** @nullable */
  actorName?: string | null;
  outcome: AccessAuditEventOutcome;
  /** @nullable */
  reason?: string | null;
  createdAt: string;
}

export interface AccessAuditPage {
  items: AccessAuditEvent[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface AgentContextRequest {
  accessScope: AgentAccessScope;
}

export type AgentCapabilityScope = typeof AgentCapabilityScope[keyof typeof AgentCapabilityScope];


export const AgentCapabilityScope = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export type AgentCapabilityKind = typeof AgentCapabilityKind[keyof typeof AgentCapabilityKind];


export const AgentCapabilityKind = {
  read: 'read',
  execute: 'execute',
  human_handoff: 'human_handoff',
} as const;

export interface AgentCapability {
  key: string;
  label: string;
  category: string;
  scope: AgentCapabilityScope;
  kind: AgentCapabilityKind;
  available: boolean;
  requiredPermission: string;
  description: string;
  /** @nullable */
  unavailableReason?: string | null;
  /** @nullable */
  handoffPath?: string | null;
}

export type AgentBillingOwnerKind = typeof AgentBillingOwnerKind[keyof typeof AgentBillingOwnerKind];


export const AgentBillingOwnerKind = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export interface AgentBillingOwner {
  kind: AgentBillingOwnerKind;
  label: string;
  /**
     * @minimum 0
     * @nullable
     */
  balance: number | null;
  /** @minimum 0 */
  actionCost: number;
}

export interface AgentContextResponse {
  scope: AgentAccessScope;
  label: string;
  permissions: string[];
  capabilities: AgentCapability[];
  billing: AgentBillingOwner;
}

export type QueueItemStateKind = typeof QueueItemStateKind[keyof typeof QueueItemStateKind];


export const QueueItemStateKind = {
  snoozed: 'snoozed',
  dismissed: 'dismissed',
  done: 'done',
} as const;

export interface QueueItemState {
  itemType: string;
  itemId: string;
  state: QueueItemStateKind;
  /** @nullable */
  snoozedUntil: string | null;
  updatedAt: string;
}

export interface QueueItemStateList {
  items: QueueItemState[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface QueueItemStateWrite {
  state: QueueItemStateKind;
  /**
     * Required for snoozed; must be in the future and within 365 days.
     * @nullable
     */
  snoozedUntil?: string | null;
}

export interface SavedSharedMailbox {
  id: string;
  /**
     * @minLength 3
     * @maxLength 254
     */
  address: string;
  /** Everyone in the entity sees this mailbox in their list. */
  sharedWithEntity: boolean;
  /** The signed-in user saved this entry and can share or remove it. */
  mine: boolean;
  /**
     * @maxLength 200
     * @nullable
     */
  addedByName: string | null;
  /** The signed-in user hid this colleague's shared mailbox from their own list. */
  hidden: boolean;
  updatedAt: string;
}

export interface SavedSharedMailboxList {
  items: SavedSharedMailbox[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface SavedSharedMailboxCreate {
  /**
     * @minLength 3
     * @maxLength 254
     */
  address: string;
  sharedWithEntity?: boolean;
}

export interface SavedSharedMailboxUpdate {
  sharedWithEntity: boolean;
}

export interface SavedSharedMailboxImport {
  /**
     * @minItems 1
     * @maxItems 20
     * @items.minLength 3
     * @items.maxLength 254
     */
  addresses: string[];
}

export interface SavedSharedMailboxImportResult {
  /** @minimum 0 */
  imported: number;
  /** @minimum 0 */
  skipped: number;
}

export type AgentUndoResultStatus = typeof AgentUndoResultStatus[keyof typeof AgentUndoResultStatus];


export const AgentUndoResultStatus = {
  undone: 'undone',
} as const;

export type AgentUndoResultReceipt = { [key: string]: unknown };

export interface AgentUndoResult {
  id: string;
  status: AgentUndoResultStatus;
  label: string;
  receipt: AgentUndoResultReceipt;
}

export type AgentProposalRequestArguments = { [key: string]: unknown };

export interface AgentProposalRequest {
  conversationId: string;
  accessScope: AgentAccessScope;
  /**
     * @minLength 1
     * @maxLength 120
     */
  actionKey: string;
  arguments: AgentProposalRequestArguments;
}

export interface AgentProposalReviewRequest {
  /**
     * @minLength 64
     * @maxLength 64
     */
  previewHash: string;
  /**
     * @minLength 3
     * @maxLength 1000
     */
  reason: string;
}

export interface AgentProposalCancelRequest {
  /**
     * @minLength 3
     * @maxLength 1000
     */
  reason: string;
}

export type AgentActionExecutionStatus = typeof AgentActionExecutionStatus[keyof typeof AgentActionExecutionStatus];


export const AgentActionExecutionStatus = {
  queued: 'queued',
  running: 'running',
  succeeded: 'succeeded',
  failed: 'failed',
  partial: 'partial',
  indeterminate: 'indeterminate',
} as const;

/**
 * @nullable
 */
export type AgentActionExecutionReceipt = { [key: string]: unknown } | null;

export interface AgentActionExecution {
  /** True for read-only receipt recovery or a review that found an existing execution. Only an explicit false on a fresh successful confirmation is eligible for create-and-open navigation. */
  replayed?: boolean;
  id: string;
  proposalId: string;
  status: AgentActionExecutionStatus;
  /** @nullable */
  receipt: AgentActionExecutionReceipt;
  /** @nullable */
  errorCode?: string | null;
  /** @nullable */
  errorMessage?: string | null;
  auditIds: string[];
  jobIds: string[];
  startedAt: string;
  /** @nullable */
  finishedAt?: string | null;
}

export type PersonalWorkSource = typeof PersonalWorkSource[keyof typeof PersonalWorkSource];


export const PersonalWorkSource = {
  agent: 'agent',
  content_asset: 'content_asset',
  social_post: 'social_post',
  campaign: 'campaign',
  research_thread: 'research_thread',
  research_document: 'research_document',
} as const;

export type PersonalWorkState = typeof PersonalWorkState[keyof typeof PersonalWorkState];


export const PersonalWorkState = {
  needs_details: 'needs_details',
  awaiting_review: 'awaiting_review',
  pending: 'pending',
  uncertain: 'uncertain',
  expired: 'expired',
  stale: 'stale',
  draft: 'draft',
  active: 'active',
  failed: 'failed',
} as const;

export type PersonalWorkItemResumeKind = typeof PersonalWorkItemResumeKind[keyof typeof PersonalWorkItemResumeKind];


export const PersonalWorkItemResumeKind = {
  conversation: 'conversation',
  native: 'native',
  receipt: 'receipt',
} as const;

export type PersonalWorkItemResumeMode = typeof PersonalWorkItemResumeMode[keyof typeof PersonalWorkItemResumeMode];


export const PersonalWorkItemResumeMode = {
  internal: 'internal',
  public_research: 'public_research',
} as const;

export type PersonalWorkItemResume = {
  kind: PersonalWorkItemResumeKind;
  destination: string;
  requiresFreshRequest: boolean;
  conversationId?: string;
  mode?: PersonalWorkItemResumeMode;
  proposalId?: string;
  executionId?: string;
  actionId?: string;
  /** @minimum 1 */
  actionRevision?: number;
  expiresAt?: string;
};

export interface PersonalWorkItem {
  id: string;
  source: PersonalWorkSource;
  title: string;
  state: PersonalWorkState;
  nativeStatus: string;
  revision: string;
  updatedAt: string;
  nextAction: string;
  scope: AgentAccessScope;
  resume: PersonalWorkItemResume;
}

export type PersonalWorkSourceAvailabilityStatus = typeof PersonalWorkSourceAvailabilityStatus[keyof typeof PersonalWorkSourceAvailabilityStatus];


export const PersonalWorkSourceAvailabilityStatus = {
  available: 'available',
  unavailable: 'unavailable',
  not_authorized: 'not_authorized',
  unsupported: 'unsupported',
} as const;

export interface PersonalWorkSourceAvailability {
  source: string;
  status: PersonalWorkSourceAvailabilityStatus;
  reason: string;
}

export interface PersonalWorkPage {
  items: PersonalWorkItem[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
  asOf: string;
  sources: PersonalWorkSourceAvailability[];
}

export type AgentRecordContextTenderKind = typeof AgentRecordContextTenderKind[keyof typeof AgentRecordContextTenderKind];


export const AgentRecordContextTenderKind = {
  tender: 'tender',
} as const;

export interface AgentRecordContextTender {
  kind: AgentRecordContextTenderKind;
  tenantId: string;
  entityId: string;
  recordId: string;
}

export type AgentRecordContextCampaignKind = typeof AgentRecordContextCampaignKind[keyof typeof AgentRecordContextCampaignKind];


export const AgentRecordContextCampaignKind = {
  campaign: 'campaign',
} as const;

export interface AgentRecordContextCampaign {
  kind: AgentRecordContextCampaignKind;
  tenantId: string;
  entityId: string;
  recordId: string;
}

export type AgentRecordContextContentAssetKind = typeof AgentRecordContextContentAssetKind[keyof typeof AgentRecordContextContentAssetKind];


export const AgentRecordContextContentAssetKind = {
  content_asset: 'content_asset',
} as const;

export interface AgentRecordContextContentAsset {
  kind: AgentRecordContextContentAssetKind;
  tenantId: string;
  entityId: string;
  recordId: string;
}

export type AgentRecordContextSocialPostKind = typeof AgentRecordContextSocialPostKind[keyof typeof AgentRecordContextSocialPostKind];


export const AgentRecordContextSocialPostKind = {
  social_post: 'social_post',
} as const;

export interface AgentRecordContextSocialPost {
  kind: AgentRecordContextSocialPostKind;
  tenantId: string;
  entityId: string;
  recordId: string;
}

export type AgentRecordContextManagedDocumentKind = typeof AgentRecordContextManagedDocumentKind[keyof typeof AgentRecordContextManagedDocumentKind];


export const AgentRecordContextManagedDocumentKind = {
  managed_document: 'managed_document',
} as const;

export interface AgentRecordContextManagedDocument {
  kind: AgentRecordContextManagedDocumentKind;
  tenantId: string;
  entityId: string;
  recordId: string;
}

export type AgentRecordContextMicrositeKind = typeof AgentRecordContextMicrositeKind[keyof typeof AgentRecordContextMicrositeKind];


export const AgentRecordContextMicrositeKind = {
  microsite: 'microsite',
} as const;

export interface AgentRecordContextMicrosite {
  kind: AgentRecordContextMicrositeKind;
  tenantId: string;
  entityId: string;
  recordId: string;
}

export type AgentRecordContextSowPreparationKind = typeof AgentRecordContextSowPreparationKind[keyof typeof AgentRecordContextSowPreparationKind];


export const AgentRecordContextSowPreparationKind = {
  sow_preparation: 'sow_preparation',
} as const;

/**
 * An exact shared SOW preparation record. Its native SOW preparation reader is the authority; a SOW Files document or a legacy author-private SOW session is never accepted in its place.
 */
export interface AgentRecordContextSowPreparation {
  kind: AgentRecordContextSowPreparationKind;
  tenantId: string;
  entityId: string;
  recordId: string;
}

/**
 * Explicit exact native record. Omitted context on existing conversation operations remains private; private history is never converted.
 */
export type AgentRecordContext = AgentRecordContextTender | AgentRecordContextCampaign | AgentRecordContextContentAsset | AgentRecordContextSocialPost | AgentRecordContextManagedDocument | AgentRecordContextMicrosite | AgentRecordContextSowPreparation;

export type AgentContinuationRequestMode = typeof AgentContinuationRequestMode[keyof typeof AgentContinuationRequestMode];


export const AgentContinuationRequestMode = {
  details: 'details',
  review: 'review',
  cancel: 'cancel',
} as const;

export type AgentContinuationRequestFields = {[key: string]: string};

export interface AgentContinuationRequest {
  /**
     * @minLength 1
     * @maxLength 120
     */
  actionKey: string;
  actionId: string;
  /** @minimum 1 */
  revision: number;
  mode: AgentContinuationRequestMode;
  fields?: AgentContinuationRequestFields;
}

export type AgentSharedTurnRequestMode = typeof AgentSharedTurnRequestMode[keyof typeof AgentSharedTurnRequestMode];


export const AgentSharedTurnRequestMode = {
  shared: 'shared',
  private_action: 'private_action',
} as const;

export interface AgentClarificationAnswer {
  /**
     * @minLength 1
     * @maxLength 64
     * @pattern ^[A-Za-z0-9][A-Za-z0-9_-]*$
     */
  questionId: string;
  /**
     * @maxItems 8
     * @items.minLength 1
     * @items.maxLength 64
     * @items.pattern ^[A-Za-z0-9][A-Za-z0-9_-]*$
     */
  optionIds: string[];
  /**
     * @maxLength 1000
     * @nullable
     */
  text?: string | null;
}

export interface AgentClarificationAnswerBundle {
  groupId: string;
  /**
     * @minItems 1
     * @maxItems 6
     */
  answers: AgentClarificationAnswer[];
}

export interface AgentSharedTurnRequest {
  context: AgentRecordContext;
  clientRequestId: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  message: string;
  mode: AgentSharedTurnRequestMode;
  continuation?: AgentContinuationRequest;
  clarificationAnswers?: AgentClarificationAnswerBundle;
  /** @maxItems 10 */
  attachments?: AgentAttachmentReference[];
}

export interface AgentSharedStopRequest {
  context: AgentRecordContext;
  /**
     * @minLength 1
     * @maxLength 500
     */
  reason: string;
}

export type AgentSharedMessageStatus = typeof AgentSharedMessageStatus[keyof typeof AgentSharedMessageStatus];


export const AgentSharedMessageStatus = {
  running: 'running',
  completed: 'completed',
  failed: 'failed',
  interrupted: 'interrupted',
  cancelled: 'cancelled',
} as const;

export type AgentSourceSourceType = typeof AgentSourceSourceType[keyof typeof AgentSourceSourceType];


export const AgentSourceSourceType = {
  crm_record: 'crm_record',
  uploaded_document: 'uploaded_document',
  crm_inventory: 'crm_inventory',
  workspace_evidence: 'workspace_evidence',
  conversation_attachment: 'conversation_attachment',
  shared_conversation_attachment: 'shared_conversation_attachment',
} as const;

export type AgentSourceTier = typeof AgentSourceTier[keyof typeof AgentSourceTier];


export const AgentSourceTier = {
  primary: 'primary',
  background: 'background',
} as const;

export interface AgentSource {
  id: string;
  kind: string;
  label: string;
  /** @nullable */
  occurredAt?: string | null;
  /** @nullable */
  deepLink?: string | null;
  /** @minimum 1 */
  citation: number;
  sourceType: AgentSourceSourceType;
  /** @nullable */
  sourceVersion?: string | null;
  tier?: AgentSourceTier;
  contentHash?: string;
  workspaceContext?: WorkspaceEvidenceBinding;
  sourceIdentity: string;
  location: string;
  excerpt: string;
  /** @nullable */
  freshness: string | null;
  /** @nullable */
  retrievalScore: number | null;
}

export interface AgentSharedMessage {
  id: string;
  threadId: string;
  authorAppUserId: string;
  /** @nullable */
  content: string | null;
  /** @nullable */
  assistantContent: string | null;
  status: AgentSharedMessageStatus;
  restricted: boolean;
  sources?: AgentSource[];
  /** @nullable */
  evidenceMessage?: string | null;
  createdAt: string;
  /** @nullable */
  completedAt: string | null;
  /** @nullable */
  stoppedByAppUserId: string | null;
}

export interface AgentSharedMessagePage {
  items: AgentSharedMessage[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export type AgentSharedFileResultOperation = typeof AgentSharedFileResultOperation[keyof typeof AgentSharedFileResultOperation];


export const AgentSharedFileResultOperation = {
  create: 'create',
  update: 'update',
} as const;

export type AgentSharedFileResultState = typeof AgentSharedFileResultState[keyof typeof AgentSharedFileResultState];


export const AgentSharedFileResultState = {
  saved: 'saved',
  unconfirmed: 'unconfirmed',
} as const;

/**
 * Current actor-private native file outcome. Present only after current record, evidence and original-save checks; never inferred from model text.
 */
export interface AgentSharedFileResult {
  operation: AgentSharedFileResultOperation;
  workspaceId: string;
  /**
     * @minLength 1
     * @maxLength 120
     */
  filename: string;
  /** @pattern ^/files(?:\?entry=|/)[0-9a-fA-F-]{36}$ */
  link: string;
  state: AgentSharedFileResultState;
}

export type AgentWorkCardVersion = typeof AgentWorkCardVersion[keyof typeof AgentWorkCardVersion];


export const AgentWorkCardVersion = {
  NUMBER_1: 1,
} as const;

export type AgentWorkCardState = typeof AgentWorkCardState[keyof typeof AgentWorkCardState];


export const AgentWorkCardState = {
  needs_details: 'needs_details',
  ready_for_review: 'ready_for_review',
  awaiting_review: 'awaiting_review',
  pending: 'pending',
  uncertain: 'uncertain',
  completed: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
  stale: 'stale',
  expired: 'expired',
  native_only: 'native_only',
  read_only: 'read_only',
} as const;

/**
 * Canonical native input reference. The registered action schema owns its supported subset and all validation; field metadata is only a rendering hint.
 */
export type AgentWorkCardNativeSchema = typeof AgentWorkCardNativeSchema[keyof typeof AgentWorkCardNativeSchema];


export const AgentWorkCardNativeSchema = {
  '#/components/schemas/AccountCreate': '#/components/schemas/AccountCreate',
  '#/components/schemas/EmailCampaignCreate': '#/components/schemas/EmailCampaignCreate',
  '#/components/schemas/CampaignBriefChange': '#/components/schemas/CampaignBriefChange',
  '#/components/schemas/ContentHubAssetCreate': '#/components/schemas/ContentHubAssetCreate',
  '#/components/schemas/CreateResearchThread': '#/components/schemas/CreateResearchThread',
  '#/components/schemas/CreateResearchDocument': '#/components/schemas/CreateResearchDocument',
} as const;

export type AgentWorkCardFieldControl = typeof AgentWorkCardFieldControl[keyof typeof AgentWorkCardFieldControl];


export const AgentWorkCardFieldControl = {
  text: 'text',
  multiline: 'multiline',
  select: 'select',
} as const;

export interface AgentWorkCardField {
  /**
     * @minLength 1
     * @maxLength 120
     */
  name: string;
  /**
     * @minLength 1
     * @maxLength 120
     */
  label: string;
  control: AgentWorkCardFieldControl;
  required: boolean;
  /** @maxLength 30000 */
  value?: string;
  /** @minimum 0 */
  minLength?: number;
  /** @minimum 1 */
  maxLength?: number;
  /**
     * @maxItems 40
     * @items.maxLength 120
     */
  options: string[];
}

export type AgentWorkCardAvailableActionsItem = typeof AgentWorkCardAvailableActionsItem[keyof typeof AgentWorkCardAvailableActionsItem];


export const AgentWorkCardAvailableActionsItem = {
  details: 'details',
  review: 'review',
  cancel: 'cancel',
  open_native: 'open_native',
  confirm: 'confirm',
  check_result: 'check_result',
} as const;

/**
 * Server-derived presentation over private continuation and canonical proposal/receipt owners. Never accept a submitted card as authority or dispatch model-provided UI. Edits use the existing actionId/revision continuation request; confirmation uses the exact canonical proposal.
 */
export interface AgentWorkCard {
  version: AgentWorkCardVersion;
  actionId: string;
  /** @minimum 1 */
  revision: number;
  /**
     * @minLength 1
     * @maxLength 120
     */
  actionKey: string;
  scope: AgentAccessScope;
  /**
     * @minLength 1
     * @maxLength 300
     */
  title: string;
  state: AgentWorkCardState;
  expiresAt: string;
  /** Canonical native input reference. The registered action schema owns its supported subset and all validation; field metadata is only a rendering hint. */
  nativeSchema?: AgentWorkCardNativeSchema;
  /** @maxItems 20 */
  fields: AgentWorkCardField[];
  /**
     * @maxItems 40
     * @items.maxLength 120
     */
  missingInputs: string[];
  /**
     * Authorized source evidence only. Empty for supplied inert draft values; never infer source attribution.
     * @maxItems 20
     */
  evidence: AgentSource[];
  /** @maxItems 8 */
  availableActions: AgentWorkCardAvailableActionsItem[];
  proposal?: AgentActionProposal;
  execution?: AgentActionExecution;
  /** @maxLength 120 */
  dependencyOwner?: string;
  reviewRequired: true;
}

export type AgentContinuationState = typeof AgentContinuationState[keyof typeof AgentContinuationState];


export const AgentContinuationState = {
  needs_details: 'needs_details',
  ready_for_review: 'ready_for_review',
  native_only: 'native_only',
  read_only: 'read_only',
} as const;

export type AgentContinuationDetailsMode = typeof AgentContinuationDetailsMode[keyof typeof AgentContinuationDetailsMode];


export const AgentContinuationDetailsMode = {
  exact_review: 'exact_review',
  native_clarification: 'native_clarification',
  read_query: 'read_query',
} as const;

export type AgentContinuationScreenMode = typeof AgentContinuationScreenMode[keyof typeof AgentContinuationScreenMode];


export const AgentContinuationScreenMode = {
  review_draft: 'review_draft',
  native_workflow: 'native_workflow',
} as const;

export type AgentContinuationPrefillKind = typeof AgentContinuationPrefillKind[keyof typeof AgentContinuationPrefillKind];


export const AgentContinuationPrefillKind = {
  lead_magnet: 'lead_magnet',
  testimonial: 'testimonial',
  case_study: 'case_study',
  faq: 'faq',
  operation: 'operation',
} as const;

export type AccountRelationshipType = typeof AccountRelationshipType[keyof typeof AccountRelationshipType];


export const AccountRelationshipType = {
  b2b_customer: 'b2b_customer',
  b2c_consumer: 'b2c_consumer',
  partner: 'partner',
  supplier: 'supplier',
  investor: 'investor',
  prospect: 'prospect',
  other: 'other',
} as const;

export type AccountKind = typeof AccountKind[keyof typeof AccountKind];


export const AccountKind = {
  organization: 'organization',
  person: 'person',
} as const;

/**
 * Editable non-authoritative values in this actor-private response. Transfer only within the same signed-in actor and scope; never in a URL.
 */
export type AgentContinuationPrefill = {
  /** @maxLength 200 */
  name?: string;
  /** @maxLength 300 */
  title?: string;
  /** @maxLength 500 */
  subject?: string;
  /** @maxLength 30000 */
  body?: string;
  /** @maxLength 30000 */
  content?: string;
  /** @maxLength 30000 */
  markdown?: string;
  kind?: AgentContinuationPrefillKind;
  relationshipType?: AccountRelationshipType;
  accountKind?: AccountKind;
};

/**
 * Non-authoritative choices for one recognized action. Recheck current scope, permissions, modules and native prerequisites before review or navigation. Never dispatch from these fields.
 */
export interface AgentContinuation {
  workCard?: AgentWorkCard;
  /**
     * @minLength 1
     * @maxLength 120
     */
  actionKey: string;
  actionId?: string;
  /** @minimum 1 */
  revision?: number;
  scope: AgentAccessScope;
  state: AgentContinuationState;
  detailsMode: AgentContinuationDetailsMode;
  screenMode: AgentContinuationScreenMode;
  /**
     * Exact server-allowlisted application route; never includes supplied private text.
     * @minLength 1
     * @maxLength 500
     * @pattern ^/(?!/)[a-zA-Z0-9/?=&._-]+$
     */
  destination: string;
  /** True only for an existing inert local creation adapter. Required inputs and explicit proposal confirmation still apply. */
  draftEligible: boolean;
  /**
     * @maxItems 40
     * @items.minLength 1
     * @items.maxLength 120
     */
  minimumInputs: string[];
  /**
     * Invalid or missing field paths, never submitted values.
     * @maxItems 40
     * @items.minLength 1
     * @items.maxLength 120
     */
  missingInputs: string[];
  /** @maxLength 2000 */
  explanation?: string;
  /** Editable non-authoritative values in this actor-private response. Transfer only within the same signed-in actor and scope; never in a URL. */
  prefill?: AgentContinuationPrefill;
  reviewRequired: true;
}

export type AgentSuggestedStepKind = typeof AgentSuggestedStepKind[keyof typeof AgentSuggestedStepKind];


export const AgentSuggestedStepKind = {
  ask: 'ask',
  open: 'open',
} as const;

/**
 * A clickable next step the Agent recommends after its answer. `ask` sends `prompt` back to the Agent as the user's next message; `open` navigates to a server-validated in-app destination. Neither executes work; changes still need an exact review and confirmation, and destinations recheck access.
 */
export interface AgentSuggestedStep {
  /**
     * @minLength 1
     * @maxLength 60
     * @pattern ^[A-Za-z0-9_-]+$
     */
  id: string;
  kind: AgentSuggestedStepKind;
  /**
     * @minLength 1
     * @maxLength 80
     */
  label: string;
  /** @maxLength 200 */
  description?: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  prompt?: string;
  /**
     * In-app route validated against the application navigation catalogue; required when kind is open.
     * @minLength 1
     * @maxLength 500
     * @pattern ^/(?!/)[a-zA-Z0-9/?=&._-]+$
     */
  destination?: string;
}

export interface SowSessionSummary {
  proposalId?: string;
  /** @minimum 1 */
  proposalRevision?: number;
  id: string;
  /** @minimum 1 */
  revision: number;
  conversationId: string;
  templateVersionId: string;
  title: string;
  /** @nullable */
  documentId: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  documentRevision: number | null;
  updatedAt: string;
}

export type SowProjectFactsNarrativeItemSection = typeof SowProjectFactsNarrativeItemSection[keyof typeof SowProjectFactsNarrativeItemSection];


export const SowProjectFactsNarrativeItemSection = {
  summary: 'summary',
  method: 'method',
  foundry: 'foundry',
  appendix: 'appendix',
} as const;

export type SowProjectTableSection = typeof SowProjectTableSection[keyof typeof SowProjectTableSection];


export const SowProjectTableSection = {
  deliverables: 'deliverables',
  components: 'components',
  milestones: 'milestones',
  risks: 'risks',
  assumptions: 'assumptions',
  resources: 'resources',
} as const;

export interface SowProjectTable {
  section: SowProjectTableSection;
  /**
     * @maxItems 100
     * @items.maxItems 7
     * @items.items.maxLength 4000
     */
  rows: string[][];
}

export type SowPriceRowKind = typeof SowPriceRowKind[keyof typeof SowPriceRowKind];


export const SowPriceRowKind = {
  effort: 'effort',
  operations: 'operations',
  material: 'material',
  service: 'service',
  donated: 'donated',
} as const;

/**
 * @nullable
 * @pattern ^(0|[1-9][0-9]{0,8})(\.[0-9]{1,4})?$
 */
export type SowDecimal = string | null;

export interface SowPriceRow {
  /**
     * @minLength 1
     * @maxLength 80
     */
  id: string;
  /** @maxLength 300 */
  title: string;
  /** @maxLength 4000 */
  description: string;
  kind: SowPriceRowKind;
  quantity: SowDecimal | null;
  rate: SowDecimal | null;
  discountPercent: SowDecimal | null;
}

export type SowQuoteCommercialLinesItemInclusion = typeof SowQuoteCommercialLinesItemInclusion[keyof typeof SowQuoteCommercialLinesItemInclusion];


export const SowQuoteCommercialLinesItemInclusion = {
  included: 'included',
  optional: 'optional',
  alternative: 'alternative',
} as const;

export type SowQuoteCommercialLinesItemPricingMethod = typeof SowQuoteCommercialLinesItemPricingMethod[keyof typeof SowQuoteCommercialLinesItemPricingMethod];


export const SowQuoteCommercialLinesItemPricingMethod = {
  fixed: 'fixed',
  effort: 'effort',
} as const;

export type SowQuoteCommercialLinesItemInterval = typeof SowQuoteCommercialLinesItemInterval[keyof typeof SowQuoteCommercialLinesItemInterval];


export const SowQuoteCommercialLinesItemInterval = {
  none: 'none',
  monthly: 'monthly',
  annual: 'annual',
} as const;

export type SowQuoteCommercialLinesItem = {
  id: string;
  /** @maxLength 300 */
  title: string;
  /**
     * @maxLength 4000
     * @nullable
     */
  description: string | null;
  inclusion: SowQuoteCommercialLinesItemInclusion;
  selected: boolean;
  quantity: string;
  pricingMethod?: SowQuoteCommercialLinesItemPricingMethod;
  /** @nullable */
  effortHours?: string | null;
  /** @nullable */
  hourlyRate?: string | null;
  /** @nullable */
  role?: string | null;
  /** @nullable */
  rate: string | null;
  /** @nullable */
  recurringRate: string | null;
  interval: SowQuoteCommercialLinesItemInterval;
  discountPercent: string;
  taxPercent: string;
  oneOffTotal: string;
  recurringTotal: string;
};

export interface SowQuoteCommercial {
  quoteId: string;
  /** @minimum 1 */
  revision: number;
  /** @pattern ^[a-f0-9]{64}$ */
  hash: string;
  /** @maxLength 300 */
  reference: string;
  /** @maxLength 100 */
  sourceStatus: string;
  /** @pattern ^[A-Z]{3}$ */
  currency: string;
  headerDiscountPercent: string;
  contingencyPercent: string;
  /**
     * @maxLength 20000
     * @nullable
     */
  terms: string | null;
  /**
     * @maxLength 20000
     * @nullable
     */
  assumptions: string | null;
  /**
     * @maxLength 20000
     * @nullable
     */
  exclusions: string | null;
  /**
     * @minLength 10
     * @maxLength 2000
     */
  reviewReason: string;
  subtotal: string;
  discountAmount: string;
  oneOffTotal: string;
  recurringTotal: string;
  taxAmount: string;
  total: string;
  contingencyAmount: string;
  /** @maxItems 100 */
  lines: SowQuoteCommercialLinesItem[];
}

export type SowProjectFactsNarrativeItem = {
  section: SowProjectFactsNarrativeItemSection;
  /** @maxLength 20000 */
  text: string;
};

export type SowProjectFactsInvoicesItem = {
  /**
     * @minLength 1
     * @maxLength 80
     */
  id: string;
  /** @maxLength 500 */
  milestones: string;
  /**
     * @maxLength 40
     * @nullable
     */
  date: string | null;
  grossAmount: SowDecimal | null;
  percent: SowDecimal | null;
  /** @maxLength 2000 */
  notes: string;
};

export type SowProjectFactsEngagementPack = {
  purchasedDays: SowDecimal | null;
  donatedDays: SowDecimal | null;
};

export type SowProjectFactsUnresolvedFactsItem = {
  /** @maxLength 100 */
  field: string;
  /** @maxLength 2000 */
  message: string;
};

export type SowProjectFactsCustomFieldsItem = {
  /** @maxLength 48 */
  sectionId: string;
  /** @maxLength 48 */
  fieldId: string;
  /**
     * @maxLength 4000
     * @nullable
     */
  value: string | null;
};

export type SowProjectFactsCustomProseItem = {
  /** @maxLength 48 */
  sectionId: string;
  /** @maxLength 20000 */
  text: string;
};

export type SowProjectFactsCustomTablesItem = {
  /** @maxLength 48 */
  sectionId: string;
  /**
     * @maxItems 100
     * @items.maxItems 8
     * @items.items.maxLength 4000
     */
  rows: string[][];
};

/**
 * Project values for an administrator-defined custom template. Rejected for the built-in formats.
 */
export type SowProjectFactsCustom = {
  /** @maxItems 600 */
  fields: SowProjectFactsCustomFieldsItem[];
  /** @maxItems 40 */
  prose: SowProjectFactsCustomProseItem[];
  /** @maxItems 40 */
  tables: SowProjectFactsCustomTablesItem[];
};

export interface SowProjectFacts {
  /**
     * @maxLength 200
     * @nullable
     */
  projectTitle: string | null;
  /**
     * @maxLength 300
     * @nullable
     */
  customerName: string | null;
  /**
     * @maxLength 100
     * @nullable
     */
  agreementId: string | null;
  /**
     * @maxLength 40
     * @nullable
     */
  preparedDate: string | null;
  /**
     * @maxLength 40
     * @nullable
     */
  startDate: string | null;
  /**
     * @minimum 1
     * @maximum 52
     * @nullable
     */
  sprintWeeks: number | null;
  /** @maxItems 5 */
  narrative: SowProjectFactsNarrativeItem[];
  /** @maxItems 6 */
  tables: SowProjectTable[];
  /** @maxItems 100 */
  investment: SowPriceRow[];
  /** @maxItems 100 */
  materials: SowPriceRow[];
  /** @maxItems 100 */
  invoices: SowProjectFactsInvoicesItem[];
  milestoneDays: SowDecimal | null;
  displayedMaterialsTotal: SowDecimal | null;
  quoteCommercial?: SowQuoteCommercial;
  engagementPack?: SowProjectFactsEngagementPack;
  /** @maxItems 200 */
  unresolvedFacts?: SowProjectFactsUnresolvedFactsItem[];
  /** Project values for an administrator-defined custom template. Rejected for the built-in formats. */
  custom?: SowProjectFactsCustom;
}

export type SowSourceSelectionKind = typeof SowSourceSelectionKind[keyof typeof SowSourceSelectionKind];


export const SowSourceSelectionKind = {
  account: 'account',
  contact: 'contact',
  opportunity: 'opportunity',
  document: 'document',
  request: 'request',
} as const;

export interface SowSourceSelection {
  kind: SowSourceSelectionKind;
  id: string;
}

export type SowEvidence = SowSourceSelection & {
  /** @pattern ^[a-f0-9]{64}$ */
  hash: string;
  /** @maxLength 100 */
  revision: string;
  /** @maxLength 100 */
  sourceKey: string;
  /** @maxLength 500 */
  label: string;
  /** @maxLength 12000 */
  excerpt: string;
};

export type SowFactAttributionField = typeof SowFactAttributionField[keyof typeof SowFactAttributionField];


export const SowFactAttributionField = {
  projectTitle: 'projectTitle',
  customerName: 'customerName',
  agreementId: 'agreementId',
  preparedDate: 'preparedDate',
  startDate: 'startDate',
  sprintWeeks: 'sprintWeeks',
  narrative: 'narrative',
  tables: 'tables',
  investment: 'investment',
  materials: 'materials',
  invoices: 'invoices',
  milestoneDays: 'milestoneDays',
  displayedMaterialsTotal: 'displayedMaterialsTotal',
  engagementPack: 'engagementPack',
  quoteCommercial: 'quoteCommercial',
  custom: 'custom',
} as const;

export type SowFactAttributionKind = typeof SowFactAttributionKind[keyof typeof SowFactAttributionKind];


export const SowFactAttributionKind = {
  user: 'user',
  evidence: 'evidence',
  assumption: 'assumption',
} as const;

export interface SowFactAttribution {
  field: SowFactAttributionField;
  kind: SowFactAttributionKind;
  /** @maxLength 4000 */
  quote: string;
  /**
     * @maxItems 20
     * @items.maxLength 100
     */
  sourceIds: string[];
}

export type SowValidationFindingsItem = {
  code: string;
  field: string;
  message: string;
};

export interface SowValidation {
  readyForReview: boolean;
  findings: SowValidationFindingsItem[];
  /** @nullable */
  subtotal: string | null;
  /** @nullable */
  gst: string | null;
  /** @nullable */
  gross: string | null;
  /** @nullable */
  materials: string | null;
  /** @nullable */
  invoices: string | null;
  /** @nullable */
  effortDays: string | null;
}

export type SowSessionSourceStatus = typeof SowSessionSourceStatus[keyof typeof SowSessionSourceStatus];


export const SowSessionSourceStatus = {
  current: 'current',
  stale: 'stale',
} as const;

export type SowSessionGapsItem = {
  field: string;
  message: string;
};

export type SowSessionCorrectionsItem = {
  field: string;
  before: string;
  after: string;
  reason: string;
};

export type SowSession = SowSessionSummary & ({
  workspaceContext?: WorkspaceEvidenceBinding;
  /** @nullable */
  requestId?: string | null;
  facts: SowProjectFacts;
  /** @maxItems 20 */
  selectedSources: SowEvidence[];
  /** @maxItems 200 */
  confirmedFacts: SowFactAttribution[];
  /** @maxItems 200 */
  assumptions: SowFactAttribution[];
  /** @maxItems 200 */
  gaps: SowSessionGapsItem[];
  /** @maxItems 200 */
  corrections: SowSessionCorrectionsItem[];
  validation: SowValidation;
  content: ManagedDocumentContent;
  sourceStatus: SowSessionSourceStatus;
});

export type ResearchEvidenceSourceKind = typeof ResearchEvidenceSourceKind[keyof typeof ResearchEvidenceSourceKind];


export const ResearchEvidenceSourceKind = {
  entity: 'entity',
  attachment: 'attachment',
  public_web: 'public_web',
} as const;

export type ResearchEvidenceEvidenceState = typeof ResearchEvidenceEvidenceState[keyof typeof ResearchEvidenceEvidenceState];


export const ResearchEvidenceEvidenceState = {
  available: 'available',
  missing: 'missing',
  stale: 'stale',
  conflicting: 'conflicting',
  unavailable: 'unavailable',
  unsupported: 'unsupported',
} as const;

export interface ResearchEvidence {
  id: string;
  sourceKind: ResearchEvidenceSourceKind;
  sourceType: string;
  /** @nullable */
  sourceId: string | null;
  /** @nullable */
  sourceVersion: string | null;
  title: string;
  /** @nullable */
  authorizedLocation: string | null;
  excerpt: string;
  evidenceState: ResearchEvidenceEvidenceState;
  /** @nullable */
  sourceTimestamp: string | null;
  retrievedAt: string;
}

export type DraftHandoffChannel = typeof DraftHandoffChannel[keyof typeof DraftHandoffChannel];


export const DraftHandoffChannel = {
  email: 'email',
  social: 'social',
  quote: 'quote',
  video_project: 'video_project',
} as const;

export type VideoProjectMode = typeof VideoProjectMode[keyof typeof VideoProjectMode];


export const VideoProjectMode = {
  single_clip: 'single_clip',
  storyboard: 'storyboard',
} as const;

export interface VideoNarrativeScene {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 20000
     */
  prompt: string;
  /**
     * @maxLength 5000
     * @nullable
     */
  negativePrompt: string | null;
  durationSeconds: VideoGenerationDurationSeconds;
}

export interface VideoNarrativeDraft {
  mode: VideoProjectMode;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 10000
     */
  brief: string;
  aspectRatio: VideoGenerationAspectRatio;
  /**
     * @minItems 1
     * @maxItems 8
     */
  scenes: VideoNarrativeScene[];
}

export interface DraftHandoff {
  channel: DraftHandoffChannel;
  label: string;
  deepLink: string;
  draft: string;
  videoProject?: VideoNarrativeDraft | null;
  /** @nullable */
  aiAuditId?: string | null;
}

export type AgentChatResponseEvidenceState = typeof AgentChatResponseEvidenceState[keyof typeof AgentChatResponseEvidenceState];


export const AgentChatResponseEvidenceState = {
  ready: 'ready',
  crm_only: 'crm_only',
  empty_index: 'empty_index',
  no_sources: 'no_sources',
  stale: 'stale',
  conflicting: 'conflicting',
  denied: 'denied',
  failed_ingestion: 'failed_ingestion',
  low_confidence: 'low_confidence',
} as const;

export type AgentClarificationQuestionMode = typeof AgentClarificationQuestionMode[keyof typeof AgentClarificationQuestionMode];


export const AgentClarificationQuestionMode = {
  single_choice: 'single_choice',
  multiple_choice: 'multiple_choice',
  text: 'text',
} as const;

export interface AgentClarificationOption {
  /**
     * @minLength 1
     * @maxLength 64
     * @pattern ^[A-Za-z0-9][A-Za-z0-9_-]*$
     */
  id: string;
  /**
     * @minLength 1
     * @maxLength 160
     */
  label: string;
  /**
     * @maxLength 300
     * @nullable
     */
  description?: string | null;
}

export interface AgentClarificationQuestion {
  /**
     * @minLength 1
     * @maxLength 64
     * @pattern ^[A-Za-z0-9][A-Za-z0-9_-]*$
     */
  id: string;
  /**
     * @minLength 1
     * @maxLength 300
     */
  label: string;
  mode: AgentClarificationQuestionMode;
  /** @maxItems 8 */
  options: AgentClarificationOption[];
  allowFreeText: boolean;
}

export interface AgentClarificationGroup {
  id: string;
  /**
     * @minItems 1
     * @maxItems 6
     */
  questions: AgentClarificationQuestion[];
}

export interface AgentChatResponse {
  tenderPreparationId?: string;
  /** @maxItems 20 */
  continuations?: AgentContinuation[];
  /**
     * Clickable next steps for the user after an answer that identifies work (for example what needs attention).
     * @maxItems 6
     */
  suggestedSteps?: AgentSuggestedStep[];
  workspaceEvidence?: DocumentWorkspaceEvidenceSnapshot;
  sow?: SowSession;
  /** @maxItems 15 */
  webEvidence?: ResearchEvidence[];
  reply: string;
  conversationId: string;
  sources: AgentSource[];
  draftHandoffs: DraftHandoff[];
  evidenceState: AgentChatResponseEvidenceState;
  evidenceMessage: string;
  scope: AgentAccessScope;
  proposals: AgentActionProposal[];
  clarification?: AgentClarificationGroup | null;
}

export interface AgentSharedTurnResult {
  message: AgentSharedMessage;
  clientRequestId: string;
  fileResult?: AgentSharedFileResult;
  privateResponse: AgentChatResponse | null;
}

export interface AgentSharedConversation {
  id: string;
  context: AgentRecordContext;
  title: string;
  /** @minimum 1 */
  revision: number;
  createdByAppUserId: string;
  deleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AgentSharedConversationPage {
  /**
     * Current default shared thread for this exact record, independent of paging
     * @nullable
     */
  defaultConversationId?: string | null;
  items: AgentSharedConversation[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export interface AgentSharedConversationCreate {
  /** Resolve or create the record default thread on an explicit first send; competing staff reuse the same active thread */
  useDefault?: boolean;
  context: AgentRecordContext;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  clientConversationId: string;
}

export interface AgentSharedConversationUpdate {
  context: AgentRecordContext;
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
  deleted?: true;
}

export type AgentHandoffTargetMode = typeof AgentHandoffTargetMode[keyof typeof AgentHandoffTargetMode];


export const AgentHandoffTargetMode = {
  internal: 'internal',
  public_research: 'public_research',
} as const;

export interface AgentHandoffTarget {
  scope: AgentAccessScope;
  mode: AgentHandoffTargetMode;
}

export type AgentHandoffDestinationAssistant = typeof AgentHandoffDestinationAssistant[keyof typeof AgentHandoffDestinationAssistant];


export const AgentHandoffDestinationAssistant = {
  assistant: 'assistant',
  research: 'research',
} as const;

export interface AgentHandoffDestination {
  key: string;
  target: AgentHandoffTarget;
  label: string;
  assistant: AgentHandoffDestinationAssistant;
}

export interface AgentHandoffDestinationPage {
  items: AgentHandoffDestination[];
  total: number;
  limit: number;
  offset: number;
}

export interface AgentHandoffReviewRequest {
  accessScope: AgentAccessScope;
  destination: AgentHandoffTarget;
  /** @maxItems 200 */
  excludedMessageIds?: string[];
}

export interface AgentHandoffReviewMessage {
  id: string;
  preview: string;
  createdAt: string;
  included: boolean;
  evidenceCount: number;
  attachmentCount: number;
}

export interface AgentHandoffReview {
  handoffId: string;
  reviewToken: string;
  expiresAt: string;
  sourceTitle: string;
  destination: AgentHandoffTarget;
  destinationLabel: string;
  /** @maxItems 200 */
  messages: AgentHandoffReviewMessage[];
  includedCount: number;
  excludedMessageIds: string[];
  notes: string[];
}

export interface AgentHandoffConfirmRequest {
  accessScope: AgentAccessScope;
  handoffId: string;
  /**
     * @minLength 43
     * @maxLength 128
     */
  reviewToken: string;
  /** @maxItems 200 */
  excludedMessageIds: string[];
  requestId: string;
}

export type AgentHandoffResultStatus = typeof AgentHandoffResultStatus[keyof typeof AgentHandoffResultStatus];


export const AgentHandoffResultStatus = {
  confirmed: 'confirmed',
} as const;

export type AgentConversationMode = typeof AgentConversationMode[keyof typeof AgentConversationMode];


export const AgentConversationMode = {
  internal: 'internal',
  public_research: 'public_research',
} as const;

export interface AgentConversation {
  mode?: AgentConversationMode;
  id: string;
  scope: AgentAccessScope;
  title: string;
  revision: number;
  deleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AgentHandoffResult {
  handoffId: string;
  status: AgentHandoffResultStatus;
  conversation: AgentConversation;
  copiedMessages: number;
}

export interface AgentSharedAttachmentUpload {
  context: AgentRecordContext;
  /** Explicit acknowledgement that this new file is shared with staff who currently have access to this exact record. */
  acknowledgeSharing: true;
  uploadId: string;
  /**
     * @minLength 1
     * @maxLength 255
     */
  filename: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  contentType: string;
  /**
     * @minLength 4
     * @maxLength 27962028
     */
  dataBase64: string;
}

export interface AgentSharedAttachmentProcess {
  context: AgentRecordContext;
}

export type AgentAttachmentStatus = typeof AgentAttachmentStatus[keyof typeof AgentAttachmentStatus];


export const AgentAttachmentStatus = {
  uploading: 'uploading',
  pending: 'pending',
  processing: 'processing',
  ready: 'ready',
  partial: 'partial',
  unsupported: 'unsupported',
  failed: 'failed',
  removed: 'removed',
} as const;

export type AgentAttachmentExtraction = {
  sectionCount: number;
  /**
     * @maxItems 110
     * @items.maxLength 500
     */
  warnings: string[];
  truncated: boolean;
  hash: string;
};

export interface AgentAttachment {
  id: string;
  conversationId: string;
  batchId: string;
  uploadId: string;
  /** @nullable */
  parentId: string | null;
  filename: string;
  /** @nullable */
  relativePath: string | null;
  contentType: string;
  /**
     * @minimum 0
     * @maximum 20971520
     */
  sizeBytes: number;
  /** @pattern ^[0-9a-f]{64}$ */
  contentHash: string;
  /** @minimum 1 */
  revision: number;
  status: AgentAttachmentStatus;
  /**
     * @maxLength 80
     * @nullable
     */
  failureCode: string | null;
  cleanupPending: boolean;
  extraction?: AgentAttachmentExtraction;
  createdAt: string;
  updatedAt: string;
}

/**
 * conversationId identifies this shared thread; audience is record access. No private conversation is relabelled.
 */
export type AgentSharedAttachment = AgentAttachment;

export interface AgentSharedAttachmentPage {
  items: AgentSharedAttachment[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export interface AgentAttachmentSection {
  /** @minimum 0 */
  index: number;
  /** @maxLength 300 */
  location: string;
  /** @maxLength 2000 */
  text: string;
}

export interface AgentAttachmentSectionPage {
  items: AgentAttachmentSection[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export interface AgentAttachmentUpload {
  accessScope: AgentAccessScope;
  uploadId: string;
  /**
     * @minLength 1
     * @maxLength 255
     */
  filename: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  contentType: string;
  /**
     * Canonical padded base64 of at most 20 MiB
     * @minLength 4
     * @maxLength 27962028
     */
  dataBase64: string;
}

export interface AgentAttachmentPage {
  items: AgentAttachment[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export type AgentConversationCreateMode = typeof AgentConversationCreateMode[keyof typeof AgentConversationCreateMode];


export const AgentConversationCreateMode = {
  internal: 'internal',
  public_research: 'public_research',
} as const;

export interface AgentConversationCreate {
  /** Optional immutable reservation ID; exact retries recover the original empty chat even after rename */
  clientConversationId?: string;
  mode?: AgentConversationCreateMode;
  accessScope: AgentAccessScope;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
}

export interface AgentConversationUpdate {
  accessScope: AgentAccessScope;
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
  deleted?: true;
}

export interface AgentPromptSnapshot {
  id: string;
  /** @minimum 1 */
  revision: number;
  title: string;
  /** @maxLength 4000 */
  content: string;
}

export interface AgentReviewFindingReference {
  /**
     * @minimum 0
     * @maximum 19
     */
  checkIndex: number;
  /**
     * @minimum 0
     * @maximum 49
     */
  findingIndex: number;
}

export interface AgentReviewCommentsSelection {
  accessScope: AgentAccessScope;
  /**
     * @minItems 1
     * @maxItems 100
     */
  findings: AgentReviewFindingReference[];
}

export interface AgentReviewCommentPreviewItem {
  checkIndex: number;
  findingIndex: number;
  quote: string;
  body: string;
  mappable: boolean;
  reason: string;
}

export interface AgentReviewCommentsPreview {
  /** @pattern ^[a-f0-9]{64}$ */
  fingerprint: string;
  title: string;
  versionId: string;
  items: AgentReviewCommentPreviewItem[];
  warnings: string[];
}

export interface AgentReviewCommentsApply {
  /** Original reviewed document for destination review; validated against the private report. */
  documentId?: string;
  accessScope: AgentAccessScope;
  /**
     * @minItems 1
     * @maxItems 100
     */
  findings: AgentReviewFindingReference[];
  /** @pattern ^[a-f0-9]{64}$ */
  fingerprint: string;
}

export interface AgentReviewCommentsReceipt {
  documentId: string;
  versionId: string;
  commentCount: number;
  skippedCount: number;
}

export interface AgentPromptReference {
  id: string;
  /** @minimum 1 */
  revision: number;
}

export interface AgentDocumentReviewCreate {
  accessScope: AgentAccessScope;
  clientRequestId: string;
  documentId: string;
  versionId: string;
  prompt?: AgentPromptReference;
  reviewSet?: AgentPromptReference;
  parentReviewId?: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  question?: string;
}

export interface AgentReviewFinding {
  anchor: string;
  /** @maxLength 2000 */
  quote: string;
  /** @maxLength 3000 */
  finding: string;
  /** @maxLength 4000 */
  suggestedText: string;
}

export type AgentReviewCheckStatus = typeof AgentReviewCheckStatus[keyof typeof AgentReviewCheckStatus];


export const AgentReviewCheckStatus = {
  pending: 'pending',
  completed: 'completed',
  failed: 'failed',
} as const;

export interface AgentReviewCheck {
  prompt: AgentPromptSnapshot;
  status: AgentReviewCheckStatus;
  summary: string;
  /** @maxItems 50 */
  findings: AgentReviewFinding[];
  model: string;
}

export type AgentDocumentReviewStatus = typeof AgentDocumentReviewStatus[keyof typeof AgentDocumentReviewStatus];


export const AgentDocumentReviewStatus = {
  running: 'running',
  completed: 'completed',
  partial: 'partial',
  failed: 'failed',
  interrupted: 'interrupted',
  cancelled: 'cancelled',
} as const;

export interface AgentDocumentReview {
  id: string;
  documentId: string;
  versionId: string;
  contentHash: string;
  title: string;
  status: AgentDocumentReviewStatus;
  warnings: string[];
  /** @maxItems 20 */
  checks: AgentReviewCheck[];
  /** @nullable */
  parentReviewId: string | null;
  setRevision: AgentPromptReference | null;
  commentDraft?: AgentReviewCommentsReceipt | null;
  createdAt: string;
}

export interface AgentDocumentReviewPage {
  items: AgentDocumentReview[];
  total: number;
  limit: number;
  offset: number;
}

export type AgentPromptTargetKind = typeof AgentPromptTargetKind[keyof typeof AgentPromptTargetKind];


export const AgentPromptTargetKind = {
  general: 'general',
  function: 'function',
  module: 'module',
} as const;

export interface AgentPromptTarget {
  kind: AgentPromptTargetKind;
  /**
     * @minLength 1
     * @maxLength 100
     */
  key?: string;
}

export interface AgentPromptProvenance {
  id: string;
  /** @minimum 1 */
  revision: number;
  tenantId: string;
}

export interface AgentPromptCopy {
  accessScope: AgentAccessScope;
  sourceId: string;
  /** @minimum 1 */
  sourceRevision: number;
  clientEntryId: string;
}

export type AgentLibraryEntryOrigin = typeof AgentLibraryEntryOrigin[keyof typeof AgentLibraryEntryOrigin];


export const AgentLibraryEntryOrigin = {
  personal: 'personal',
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export type AgentLibraryEntryVisibility = typeof AgentLibraryEntryVisibility[keyof typeof AgentLibraryEntryVisibility];


export const AgentLibraryEntryVisibility = {
  personal: 'personal',
  team: 'team',
} as const;

export type AgentLibraryEntryKind = typeof AgentLibraryEntryKind[keyof typeof AgentLibraryEntryKind];


export const AgentLibraryEntryKind = {
  prompt: 'prompt',
  review_set: 'review_set',
} as const;

export interface AgentLibraryEntry {
  /**
     * @minItems 1
     * @maxItems 20
     */
  targets?: AgentPromptTarget[];
  origin?: AgentLibraryEntryOrigin;
  inherited?: boolean;
  copiedFrom?: AgentPromptProvenance | null;
  /** @maxLength 500 */
  description?: string;
  /** @maxLength 60 */
  category?: string;
  isActive?: boolean;
  /**
     * @minimum 0
     * @maximum 100000
     */
  sortOrder?: number;
  id: string;
  scope: AgentAccessScope;
  visibility: AgentLibraryEntryVisibility;
  kind: AgentLibraryEntryKind;
  title: string;
  content: string;
  /** @maxItems 20 */
  prompts: AgentPromptSnapshot[];
  revision: number;
  deleted: boolean;
  canEdit: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AgentPromptData {
  /**
     * @minItems 1
     * @maxItems 20
     */
  targets?: AgentPromptTarget[];
  /**
     * @minLength 1
     * @maxLength 120
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  content: string;
  /** @maxLength 500 */
  description?: string;
  /**
     * @minLength 1
     * @maxLength 60
     */
  category?: string;
  isActive?: boolean;
  /**
     * @minimum 0
     * @maximum 100000
     */
  sortOrder?: number;
}

export type AgentPromptFileKind = typeof AgentPromptFileKind[keyof typeof AgentPromptFileKind];


export const AgentPromptFileKind = {
  'agent-prompts': 'agent-prompts',
} as const;

export type AgentPromptFileVersion = typeof AgentPromptFileVersion[keyof typeof AgentPromptFileVersion];


export const AgentPromptFileVersion = {
  NUMBER_1: 1,
} as const;

export interface AgentPromptFile {
  kind: AgentPromptFileKind;
  version: AgentPromptFileVersion;
  /** @maxItems 100 */
  prompts: AgentPromptData[];
}

export type AgentPromptExportRequestVisibility = typeof AgentPromptExportRequestVisibility[keyof typeof AgentPromptExportRequestVisibility];


export const AgentPromptExportRequestVisibility = {
  personal: 'personal',
  team: 'team',
} as const;

export interface AgentPromptExportRequest {
  accessScope: AgentAccessScope;
  visibility: AgentPromptExportRequestVisibility;
}

export type AgentPromptImportRequestVisibility = typeof AgentPromptImportRequestVisibility[keyof typeof AgentPromptImportRequestVisibility];


export const AgentPromptImportRequestVisibility = {
  personal: 'personal',
  team: 'team',
} as const;

export type AgentPromptImportRequestMode = typeof AgentPromptImportRequestMode[keyof typeof AgentPromptImportRequestMode];


export const AgentPromptImportRequestMode = {
  add: 'add',
  update: 'update',
  replace: 'replace',
} as const;

export interface AgentPromptImportRequest {
  accessScope: AgentAccessScope;
  visibility: AgentPromptImportRequestVisibility;
  mode: AgentPromptImportRequestMode;
  file: AgentPromptFile;
}

export type AgentPromptImportPreviewMode = typeof AgentPromptImportPreviewMode[keyof typeof AgentPromptImportPreviewMode];


export const AgentPromptImportPreviewMode = {
  add: 'add',
  update: 'update',
  replace: 'replace',
} as const;

export type AgentPromptImportPreviewVisibility = typeof AgentPromptImportPreviewVisibility[keyof typeof AgentPromptImportPreviewVisibility];


export const AgentPromptImportPreviewVisibility = {
  personal: 'personal',
  team: 'team',
} as const;

export interface AgentPromptImportPreview {
  previewId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  inputHash: string;
  expiresAt: string;
  mode: AgentPromptImportPreviewMode;
  visibility: AgentPromptImportPreviewVisibility;
  /** @maxItems 100 */
  additions: string[];
  /** @maxItems 100 */
  updates: string[];
  /** @maxItems 1000 */
  removals: string[];
  /** @maxItems 1100 */
  conflicts: string[];
  canApply: boolean;
}

export interface AgentPromptImportApply {
  accessScope: AgentAccessScope;
  previewId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  inputHash: string;
  confirmed: true;
}

export interface AgentPromptImportReceipt {
  previewId: string;
  inputHash: string;
  /** @minimum 0 */
  added: number;
  /** @minimum 0 */
  updated: number;
  /** @minimum 0 */
  removed: number;
  appliedAt: string;
}

export type AgentPromptCreateVisibility = typeof AgentPromptCreateVisibility[keyof typeof AgentPromptCreateVisibility];


export const AgentPromptCreateVisibility = {
  personal: 'personal',
  team: 'team',
} as const;

export interface AgentPromptCreate {
  /**
     * @minItems 1
     * @maxItems 20
     */
  targets?: AgentPromptTarget[];
  /** @maxLength 500 */
  description?: string;
  /**
     * @minLength 1
     * @maxLength 60
     */
  category?: string;
  isActive?: boolean;
  /**
     * @minimum 0
     * @maximum 100000
     */
  sortOrder?: number;
  accessScope: AgentAccessScope;
  visibility: AgentPromptCreateVisibility;
  /**
     * @minLength 1
     * @maxLength 120
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  content: string;
}

export type AgentReviewSetCreateVisibility = typeof AgentReviewSetCreateVisibility[keyof typeof AgentReviewSetCreateVisibility];


export const AgentReviewSetCreateVisibility = {
  personal: 'personal',
  team: 'team',
} as const;

export interface AgentReviewSetCreate {
  accessScope: AgentAccessScope;
  visibility: AgentReviewSetCreateVisibility;
  /**
     * @minLength 1
     * @maxLength 120
     */
  title: string;
  /**
     * @minItems 1
     * @maxItems 20
     */
  prompts: AgentPromptReference[];
}

export interface AgentPromptUpdate {
  /**
     * @minItems 1
     * @maxItems 20
     */
  targets?: AgentPromptTarget[];
  /** @maxLength 500 */
  description?: string;
  /**
     * @minLength 1
     * @maxLength 60
     */
  category?: string;
  isActive?: boolean;
  /**
     * @minimum 0
     * @maximum 100000
     */
  sortOrder?: number;
  accessScope: AgentAccessScope;
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 120
     */
  title?: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  content?: string;
  deleted?: true;
}

export interface AgentReviewSetUpdate {
  accessScope: AgentAccessScope;
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 120
     */
  title?: string;
  /**
     * @minItems 1
     * @maxItems 20
     */
  prompts?: AgentPromptReference[];
  deleted?: true;
}

export type AgentStoredMessageStatus = typeof AgentStoredMessageStatus[keyof typeof AgentStoredMessageStatus];


export const AgentStoredMessageStatus = {
  running: 'running',
  completed: 'completed',
  interrupted: 'interrupted',
  failed: 'failed',
} as const;

export interface AgentStoredMessage {
  id: string;
  conversationId: string;
  clientRequestId: string;
  content: string;
  status: AgentStoredMessageStatus;
  response?: AgentChatResponse | null;
  /** @nullable */
  errorCode?: string | null;
  promptSnapshot: AgentPromptSnapshot | null;
  createdAt: string;
}

export interface AgentConversationPage {
  items: AgentConversation[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export interface AgentLibraryPage {
  items: AgentLibraryEntry[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export interface AgentMessagePage {
  items: AgentStoredMessage[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export type AgentChatRequestSowSession = {
  id: string;
  /** @minimum 1 */
  expectedRevision: number;
};

export type AgentChatRequestResearchMode = typeof AgentChatRequestResearchMode[keyof typeof AgentChatRequestResearchMode];


export const AgentChatRequestResearchMode = {
  internal: 'internal',
  public_research: 'public_research',
} as const;

export type AgentChatRequestInteractionMode = typeof AgentChatRequestInteractionMode[keyof typeof AgentChatRequestInteractionMode];


export const AgentChatRequestInteractionMode = {
  chat: 'chat',
  voice: 'voice',
} as const;

export type CrmAudience = typeof CrmAudience[keyof typeof CrmAudience];


export const CrmAudience = {
  b2b: 'b2b',
  b2c: 'b2c',
  partner: 'partner',
  investor: 'investor',
} as const;

export interface AgentChatRequest {
  /**
     * Exact private sources for this turn; archive originals include readable members with explicit omission warnings. Empty selection excludes attachment retrieval.
     * @maxItems 10
     */
  attachments?: AgentAttachmentReference[];
  continuation?: AgentContinuationRequest;
  workspaceContext?: WorkspaceEvidenceBinding;
  sowSession?: AgentChatRequestSowSession;
  researchMode?: AgentChatRequestResearchMode;
  /**
     * @minItems 1
     * @maxItems 3
     * @items.minLength 3
     * @items.maxLength 300
     */
  publicSearchQueries?: string[];
  clientRequestId?: string;
  savedPrompt?: AgentPromptReference;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  message: string;
  conversationId?: string;
  interactionMode?: AgentChatRequestInteractionMode;
  accessScope: AgentAccessScope;
  audience?: CrmAudience;
  /** @maxLength 500 */
  pathname?: string;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  opportunityId?: string | null;
  clarificationAnswers?: AgentClarificationAnswerBundle;
}

/**
 * Exact immutable content-hash anchor. Offsets alone never authorize relocation after edits.
 */
export interface ManagedDocumentTextAnchor {
  /**
     * @minItems 1
     * @maxItems 30
     * @items.minimum 0
     * @items.maximum 19999
     */
  path?: number[];
  /**
     * @minItems 1
     * @maxItems 30
     * @items.minimum 0
     * @items.maximum 19999
     */
  endPath?: number[];
  /**
     * @minimum 0
     * @maximum 19999
     */
  blockIndex: number;
  /**
     * @minimum 0
     * @maximum 1000000
     */
  from: number;
  /**
     * @minimum 0
     * @maximum 1000000
     */
  to: number;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  quote: string;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
}

export type ManagedDocumentEditingReviewCommandAction = typeof ManagedDocumentEditingReviewCommandAction[keyof typeof ManagedDocumentEditingReviewCommandAction];


export const ManagedDocumentEditingReviewCommandAction = {
  tracking: 'tracking',
  comment: 'comment',
  reply: 'reply',
  edit_comment: 'edit_comment',
  delete_comment: 'delete_comment',
  resolve: 'resolve',
  reopen: 'reopen',
  accept: 'accept',
  reject: 'reject',
  accept_inline: 'accept_inline',
  reject_inline: 'reject_inline',
} as const;

export interface ManagedDocumentEditingReviewCommand {
  clientRequestId: string;
  /**
     * @minimum 1
     * @maximum 2147483646
     */
  expectedRevision: number;
  action: ManagedDocumentEditingReviewCommandAction;
  enabled?: boolean;
  messageId?: string;
  id?: string;
  all?: boolean;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  body?: string;
  anchor?: ManagedDocumentTextAnchor;
}

export interface ManagedDocumentEditingMessage {
  id: string;
  body: string;
  authorAppUserId: string;
  /** @nullable */
  authorName?: string | null;
  createdAt: string;
  editedAt?: string;
  deletedAt?: string;
  canEdit?: boolean;
}

export interface ManagedDocumentEditingThread {
  id: string;
  anchor: ManagedDocumentTextAnchor;
  attached: boolean;
  resolved: boolean;
  messages: ManagedDocumentEditingMessage[];
  events: ManagedDocumentEditingMessage[];
}

export type ManagedDocumentEditingChangeStatus = typeof ManagedDocumentEditingChangeStatus[keyof typeof ManagedDocumentEditingChangeStatus];


export const ManagedDocumentEditingChangeStatus = {
  pending: 'pending',
  accepted: 'accepted',
  rejected: 'rejected',
} as const;

export interface ManagedDocumentEditingChange {
  id: string;
  authorAppUserId: string;
  /** @nullable */
  authorName?: string | null;
  createdAt: string;
  status: ManagedDocumentEditingChangeStatus;
  beforeText: string;
  afterText: string;
  canReject: boolean;
  decision?: ManagedDocumentEditingMessage;
}

export type ManagedDocumentInlineChangeStatus = typeof ManagedDocumentInlineChangeStatus[keyof typeof ManagedDocumentInlineChangeStatus];


export const ManagedDocumentInlineChangeStatus = {
  pending: 'pending',
  accepted: 'accepted',
  rejected: 'rejected',
} as const;

export interface ManagedDocumentInlineChange {
  id: string;
  authorAppUserId: string;
  /** @nullable */
  authorName?: string | null;
  createdAt: string;
  status: ManagedDocumentInlineChangeStatus;
  insertion: string;
  deletion: string;
  decision?: ManagedDocumentEditingMessage;
}

export type ManagedDocumentEditingReviewPageChangeAuthorsItem = {
  id: string;
  /** @nullable */
  name: string | null;
};

export interface ManagedDocumentEditingReviewPage {
  /** @minimum 1 */
  revision: number;
  tracking: boolean;
  /** @nullable */
  appliedRevision?: number | null;
  /** @minimum 0 */
  inlineTotal?: number;
  inlineChanges?: ManagedDocumentInlineChange[];
  threads: ManagedDocumentEditingThread[];
  changes: ManagedDocumentEditingChange[];
  /** @minimum 0 */
  threadTotal: number;
  /** @minimum 0 */
  changeTotal: number;
  /**
     * Unresolved comment threads in the whole draft.
     * @minimum 0
     */
  openThreadTotal?: number;
  /**
     * Undecided tracked changes (edit groups and inline edits) in the whole draft.
     * @minimum 0
     */
  pendingChangeTotal?: number;
  /** People who recorded tracked changes, for the per-author filter. */
  changeAuthors?: ManagedDocumentEditingReviewPageChangeAuthorsItem[];
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type ManagedDocumentChangeStampKind = typeof ManagedDocumentChangeStampKind[keyof typeof ManagedDocumentChangeStampKind];


export const ManagedDocumentChangeStampKind = {
  insertion: 'insertion',
  deletion: 'deletion',
} as const;

/**
 * Attribution only; the server must derive author and time from the authenticated mutation. This cannot grant approval or publication authority.
 */
export interface ManagedDocumentChangeStamp {
  id: string;
  kind: ManagedDocumentChangeStampKind;
  authorAppUserId: string;
  createdAt: string;
}

export interface ManagedDocumentLiveCursor {
  /** @maxLength 2048 */
  anchor: string;
  /** @maxLength 2048 */
  head: string;
}

export type ManagedDocumentCollaborationSnapshotPeersItem = {
  sessionId: string;
  actorId: string;
  /**
     * Server-resolved display name. Never taken from the client.
     * @maxLength 200
     * @nullable
     */
  name?: string | null;
  /** False for people who can only view. */
  canEdit?: boolean;
  seenAt: string;
  cursor?: ManagedDocumentLiveCursor;
};

export type ManagedDocumentWorkspaceStatus = typeof ManagedDocumentWorkspaceStatus[keyof typeof ManagedDocumentWorkspaceStatus];


export const ManagedDocumentWorkspaceStatus = {
  draft: 'draft',
  in_review: 'in_review',
  approved: 'approved',
  published: 'published',
  archived: 'archived',
} as const;

export type ManagedDocumentWorkspaceOwnershipMode = typeof ManagedDocumentWorkspaceOwnershipMode[keyof typeof ManagedDocumentWorkspaceOwnershipMode];


export const ManagedDocumentWorkspaceOwnershipMode = {
  managed: 'managed',
  source_owned_copy: 'source_owned_copy',
} as const;

export interface SowBindingInput {
  templateVersionId: string;
  facts: SowProjectFacts;
}

export type SowApprovalScope = typeof SowApprovalScope[keyof typeof SowApprovalScope];


export const SowApprovalScope = {
  platform: 'platform',
  tenant: 'tenant',
  entity: 'entity',
} as const;

export interface SowApprovalRevisionPin {
  scope: SowApprovalScope;
  /** @nullable */
  id: string | null;
  /** @minimum 0 */
  revision: number;
}

export type SowApproverEligibilityMode = typeof SowApproverEligibilityMode[keyof typeof SowApproverEligibilityMode];


export const SowApproverEligibilityMode = {
  authorised: 'authorised',
  selected_users: 'selected_users',
} as const;

export interface SowApproverEligibility {
  mode: SowApproverEligibilityMode;
  /** @maxItems 100 */
  userIds: string[];
}

export interface SowApprovalOverrides {
  eligibility: SowApproverEligibility | null;
  eligibilityLocked: boolean;
  /** @nullable */
  authorApproval: boolean | null;
  authorApprovalLocked: boolean;
}

export type SowApprovalFieldOriginOrigin = typeof SowApprovalFieldOriginOrigin[keyof typeof SowApprovalFieldOriginOrigin];


export const SowApprovalFieldOriginOrigin = {
  default: 'default',
  platform: 'platform',
  tenant: 'tenant',
  entity: 'entity',
} as const;

export interface SowApprovalFieldOrigin {
  origin: SowApprovalFieldOriginOrigin;
  lockedBy: SowApprovalScope | null;
  canOverride: boolean;
}

export interface SowApprovalPolicyView {
  scope: SowApprovalScope;
  /** @minimum 0 */
  localRevision: number;
  /** @pattern ^[a-f0-9]{64}$ */
  fingerprint: string;
  /** @maxItems 3 */
  revisions: SowApprovalRevisionPin[];
  overrides: SowApprovalOverrides;
  humanApprovalRequired: true;
  eligibility: SowApproverEligibility;
  authorApproval: boolean;
  eligibilityOrigin: SowApprovalFieldOrigin;
  authorApprovalOrigin: SowApprovalFieldOrigin;
}

/**
 * @nullable
 */
export type ManagedDocumentWorkspaceSowApprovalStatus = typeof ManagedDocumentWorkspaceSowApprovalStatus[keyof typeof ManagedDocumentWorkspaceSowApprovalStatus] | null;


export const ManagedDocumentWorkspaceSowApprovalStatus = {
  review_required: 'review_required',
  approved: 'approved',
} as const;

export interface ManagedDocumentDraft {
  /** @minimum 1 */
  revision: number;
  title: string;
  content: ManagedDocumentContent;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
  conversionWarnings: string[];
  updatedAt: string;
}

export type ManagedDocumentVersionKind = typeof ManagedDocumentVersionKind[keyof typeof ManagedDocumentVersionKind];


export const ManagedDocumentVersionKind = {
  draft: 'draft',
  review: 'review',
  publication: 'publication',
  restore: 'restore',
  import: 'import',
} as const;

export interface ManagedDocumentVersion {
  id: string;
  label: string;
  /** @minimum 0 */
  majorVersion: number;
  /** @minimum 0 */
  minorVersion: number;
  kind: ManagedDocumentVersionKind;
  title: string;
  content?: ManagedDocumentContent;
  contentHash: string;
  reason: string;
  /** @nullable */
  actorId?: string | null;
  /** @nullable */
  restoredFromVersionId?: string | null;
  createdAt: string;
}

export type ManagedDocumentReviewStatus = typeof ManagedDocumentReviewStatus[keyof typeof ManagedDocumentReviewStatus];


export const ManagedDocumentReviewStatus = {
  pending: 'pending',
  approved: 'approved',
  changes_requested: 'changes_requested',
  cancelled: 'cancelled',
  invalidated: 'invalidated',
} as const;

export type ManagedDocumentReviewCommentAnchor = { [key: string]: unknown };

export interface ManagedDocumentReviewComment {
  id: string;
  versionId: string;
  body: string;
  anchor: ManagedDocumentReviewCommentAnchor;
  resolved: boolean;
  createdAt: string;
}

export type ManagedDocumentReviewDecisionsItem = { [key: string]: unknown };

export interface ManagedDocumentReview {
  id: string;
  versionId: string;
  contentHash: string;
  status: ManagedDocumentReviewStatus;
  /** @nullable */
  sowApprovalCurrent?: boolean | null;
  /** @nullable */
  submissionNote?: string | null;
  policyVersion: number;
  createdAt: string;
  comments: ManagedDocumentReviewComment[];
  decisions: ManagedDocumentReviewDecisionsItem[];
}

export type ManagedDocumentAiProposalStatus = typeof ManagedDocumentAiProposalStatus[keyof typeof ManagedDocumentAiProposalStatus];


export const ManagedDocumentAiProposalStatus = {
  pending: 'pending',
  accepted: 'accepted',
  rejected: 'rejected',
  stale: 'stale',
} as const;

export type ManagedDocumentAiProposalFindingsItem = { [key: string]: unknown };

export type ManagedDocumentAiProposalSourcesItem = { [key: string]: unknown };

export interface ManagedDocumentAiProposal {
  id: string;
  draftRevision: number;
  contentHash: string;
  action: string;
  status: ManagedDocumentAiProposalStatus;
  proposedContent?: ManagedDocumentContent | null;
  findings: ManagedDocumentAiProposalFindingsItem[];
  sources: ManagedDocumentAiProposalSourcesItem[];
  limitations: string[];
  modelVersion: string;
  instructionVersion: string;
  createdAt: string;
}

export type ManagedDocumentPublicationStatus = typeof ManagedDocumentPublicationStatus[keyof typeof ManagedDocumentPublicationStatus];


export const ManagedDocumentPublicationStatus = {
  active: 'active',
  unpublished: 'unpublished',
  revoked: 'revoked',
  archived: 'archived',
} as const;

export interface ManagedDocumentPublication {
  publicId: string;
  status: ManagedDocumentPublicationStatus;
  /** @minimum 1 */
  revision: number;
  publicUrl: string;
  /** @nullable */
  publishedAt: string | null;
}

export interface ManagedDocumentWorkspace {
  id: string;
  title: string;
  category: string;
  folder: string;
  labels: string[];
  status: ManagedDocumentWorkspaceStatus;
  sourceKind: string;
  ownershipMode: ManagedDocumentWorkspaceOwnershipMode;
  sow?: SowBindingInput | null;
  sowValidation?: SowValidation | null;
  sowApprovalPolicy?: SowApprovalPolicyView | null;
  /** @nullable */
  sowApprovalStatus?: ManagedDocumentWorkspaceSowApprovalStatus;
  /** @nullable */
  sowApprovalReason?: string | null;
  /** @nullable */
  sowRequestId?: string | null;
  draft: ManagedDocumentDraft;
  versions: ManagedDocumentVersion[];
  reviews: ManagedDocumentReview[];
  aiProposals: ManagedDocumentAiProposal[];
  publication: ManagedDocumentPublication | null;
  updatedAt: string;
}

/**
 * Authorized durable collaboration snapshot. CRDT state is compacted without losing operation identity; absent state is legacy semantic mode.
 */
export interface ManagedDocumentCollaborationSnapshot {
  workspace: ManagedDocumentWorkspace;
  epoch: string;
  /** @nullable */
  receiptRevision: number | null;
  /** @maxLength 5592408 */
  crdtState?: string;
  /** @maxLength 131072 */
  stateVector?: string;
  /** Whether the signed-in caller may change content in this live session. Viewers join read-only. */
  canEdit?: boolean;
  /**
     * Label of the automatic version created when the last editor left live editing (null when none was needed).
     * @maxLength 32
     * @nullable
     */
  autoVersionLabel?: string | null;
  /** @maxItems 32 */
  peers: ManagedDocumentCollaborationSnapshotPeersItem[];
}

export type ManagedDocumentCollaborationCommandAction = typeof ManagedDocumentCollaborationCommandAction[keyof typeof ManagedDocumentCollaborationCommandAction];


export const ManagedDocumentCollaborationCommandAction = {
  join: 'join',
  heartbeat: 'heartbeat',
  sync: 'sync',
  leave: 'leave',
  commit: 'commit',
  live_join: 'live_join',
  update: 'update',
} as const;

export interface ManagedDocumentCollaborationCommand {
  sessionId: string;
  action: ManagedDocumentCollaborationCommandAction;
  /**
     * @minLength 4
     * @maxLength 349528
     */
  update?: string;
  /** @maxLength 131072 */
  stateVector?: string;
  cursor?: ManagedDocumentLiveCursor;
  clearCursor?: boolean;
  clientRequestId?: string;
  epoch?: string;
  /**
     * @minimum 1
     * @maximum 2147483646
     */
  expectedRevision?: number;
  /** @pattern ^[a-f0-9]{64}$ */
  baseContentHash?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
  content?: ManagedDocumentContent;
}

/**
 * Foundation for scoped collaborative commits. Full document CAS; stale base requires read-first recovery, never overwrite or blind retry. No endpoint or merge permission is supplied by this type.
 */
export interface ManagedDocumentCollaborationCommit {
  clientRequestId: string;
  /**
     * @minimum 1
     * @maximum 2147483646
     */
  expectedRevision: number;
  /** @pattern ^[a-f0-9]{64}$ */
  baseContentHash: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  content: ManagedDocumentContent;
}

export type ManagedDocumentSectionCommandAction = typeof ManagedDocumentSectionCommandAction[keyof typeof ManagedDocumentSectionCommandAction];


export const ManagedDocumentSectionCommandAction = {
  setup: 'setup',
  structure: 'structure',
  protection: 'protection',
  content: 'content',
  guidance: 'guidance',
  convert: 'convert',
  insert: 'insert',
  settings: 'settings',
  remove: 'remove',
  template: 'template',
} as const;

export type ManagedDocumentSectionCommandBoundaryMode = typeof ManagedDocumentSectionCommandBoundaryMode[keyof typeof ManagedDocumentSectionCommandBoundaryMode];


export const ManagedDocumentSectionCommandBoundaryMode = {
  independent: 'independent',
} as const;

export type ManagedDocumentSectionCommandMode = typeof ManagedDocumentSectionCommandMode[keyof typeof ManagedDocumentSectionCommandMode];


export const ManagedDocumentSectionCommandMode = {
  headings: 'headings',
  whole: 'whole',
} as const;

export type ManagedDocumentSectionCommandProtection = typeof ManagedDocumentSectionCommandProtection[keyof typeof ManagedDocumentSectionCommandProtection];


export const ManagedDocumentSectionCommandProtection = {
  editable: 'editable',
  bulk_protected: 'bulk_protected',
  locked: 'locked',
} as const;

export interface ManagedDocumentSectionCommand {
  clientRequestId: string;
  /**
     * @minimum 1
     * @maximum 2147483646
     */
  expectedRevision: number;
  action: ManagedDocumentSectionCommandAction;
  /**
     * @minLength 1
     * @maxLength 160
     */
  title?: string;
  hidden?: boolean;
  unlockConfirmed?: boolean;
  boundaryMode?: ManagedDocumentSectionCommandBoundaryMode;
  /**
     * @minimum 0
     * @maximum 10000
     */
  blockIndex?: number;
  /**
     * @minLength 1
     * @maxLength 160
     */
  label?: string;
  /** @maxLength 4000 */
  authoringNotes?: string;
  markReviewed?: boolean;
  template?: ManagedDocumentTemplateControls;
  confirmed?: true;
  mode?: ManagedDocumentSectionCommandMode;
  sectionId?: string;
  sections?: ManagedDocumentSections;
  protection?: ManagedDocumentSectionCommandProtection;
  content?: ManagedDocumentContent;
}

export type FileLibraryEntryKind = typeof FileLibraryEntryKind[keyof typeof FileLibraryEntryKind];


export const FileLibraryEntryKind = {
  file: 'file',
  document: 'document',
} as const;

export type FileLibraryEntryManagementMode = typeof FileLibraryEntryManagementMode[keyof typeof FileLibraryEntryManagementMode];


export const FileLibraryEntryManagementMode = {
  managed: 'managed',
  source_owned: 'source_owned',
} as const;

export interface FileLibraryEntry {
  id: string;
  kind: FileLibraryEntryKind;
  title: string;
  /** @nullable */
  contentType: string | null;
  /**
     * @minimum 0
     * @nullable
     */
  sizeBytes: number | null;
  sourceKind: string;
  sourceLabel: string;
  /** @nullable */
  sourceUrl?: string | null;
  /** @nullable */
  owningRecordType?: string | null;
  /** @nullable */
  owningRecordId?: string | null;
  managementMode: FileLibraryEntryManagementMode;
  folder: string;
  labels: string[];
  status: string;
  canEdit: boolean;
  canCreateManagedCopy: boolean;
  /** @nullable */
  downloadUrl: string | null;
  updatedAt: string;
}

/**
 * Owning record whose permissions govern a library file. The tenant library is browse-only and is not accepted here.
 */
export type FileHostKind = typeof FileHostKind[keyof typeof FileHostKind];


export const FileHostKind = {
  'entity-files': 'entity-files',
  workspace: 'workspace',
  account: 'account',
  tender: 'tender',
  proposal: 'proposal',
  campaign: 'campaign',
} as const;

/**
 * library-entry is an entity library entry ID; file is a stored file ID; tender-source and tender-export are tender record IDs.
 */
export type LibraryFileKind = typeof LibraryFileKind[keyof typeof LibraryFileKind];


export const LibraryFileKind = {
  'library-entry': 'library-entry',
  file: 'file',
  'tender-source': 'tender-source',
  'tender-export': 'tender-export',
} as const;

export type LibraryFileVersionKind = typeof LibraryFileVersionKind[keyof typeof LibraryFileVersionKind];


export const LibraryFileVersionKind = {
  major: 'major',
  minor: 'minor',
} as const;

export interface FileHostRef {
  kind: FileHostKind;
  id: string;
}

export interface LibraryFileRef {
  kind: LibraryFileKind;
  id: string;
}

export interface FileHostCapabilities {
  read: boolean;
  edit: boolean;
  delete: boolean;
  share: boolean;
  createDocument: boolean;
  createImage: boolean;
  manageVersions: boolean;
}

export interface FileHostDetails {
  host: FileHostRef;
  title: string;
  capabilities: FileHostCapabilities;
}

export interface LibraryFileMoveRequest {
  host: FileHostRef;
  /**
     * @minItems 1
     * @maxItems 100
     */
  files: LibraryFileRef[];
  /** @maxLength 500 */
  folder: string;
  /**
     * Folder the caller last saw the files in. A file found elsewhere fails as stale (FOLDER_MOVE_STALE) and is left unchanged.
     * @maxLength 500
     * @nullable
     */
  expectedFolder?: string | null;
  requestId: string;
}

export interface LibraryFileOperationResult {
  file: LibraryFileRef;
  ok: boolean;
  /** @nullable */
  error: string | null;
}

export interface LibraryFileMoveOutcome {
  requestId: string;
  folder: string;
  /** @minimum 0 */
  moved: number;
  results: LibraryFileOperationResult[];
}

export interface LibraryFileDeletionReviewRequest {
  host: FileHostRef;
  file: LibraryFileRef;
}

export interface LibraryFileDeletionReview {
  file: LibraryFileRef;
  /** @pattern ^[a-f0-9]{64}$ */
  version: string;
  filename: string;
  deletable: boolean;
  blockers: string[];
  /** @minimum 0 */
  versions: number;
  /** @minimum 0 */
  activeShareLinks: number;
}

export interface LibraryFileDeletionRequest {
  host: FileHostRef;
  file: LibraryFileRef;
  /** @pattern ^[a-f0-9]{64}$ */
  version: string;
  requestId: string;
}

export interface LibraryFileDeletionOutcome {
  requestId: string;
  file: LibraryFileRef;
  filename: string;
  deleted: boolean;
  /**
     * The recycle bin item holding the file until it is restored or purged.
     * @nullable
     */
  recycleBinItemId: string | null;
}

export interface LibraryFileVersion {
  /**
     * Null for an original that has never been versioned.
     * @nullable
     */
  id: string | null;
  label: string;
  /** @minimum 1 */
  major: number;
  /** @minimum 0 */
  minor: number;
  filename: string;
  contentType: string;
  /** @minimum 0 */
  sizeBytes: number;
  /** @nullable */
  sha256: string | null;
  note: string;
  /** @nullable */
  restoredFromVersionId: string | null;
  isCurrent: boolean;
  isOriginal: boolean;
  createdAt: string;
}

export interface LibraryFileVersionPage {
  items: LibraryFileVersion[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
  /** @nullable */
  currentVersionId: string | null;
  canUpload: boolean;
  /** @nullable */
  uploadBlockedReason: string | null;
}

export interface LibraryFileRestoreReview {
  version: LibraryFileVersion;
  /** @nullable */
  currentVersionId: string | null;
  nextMajorLabel: string;
  nextMinorLabel: string;
  integrityVerified: boolean;
  canRestore: boolean;
  /** @nullable */
  blockedReason: string | null;
}

export interface LibraryFileRestoreRequest {
  /** @nullable */
  expectedCurrentVersionId: string | null;
  versionKind: LibraryFileVersionKind;
  requestId: string;
}

export type FileShareLinkStatus = typeof FileShareLinkStatus[keyof typeof FileShareLinkStatus];


export const FileShareLinkStatus = {
  active: 'active',
  expired: 'expired',
  revoked: 'revoked',
} as const;

/**
 * signed-in links re-check the opener's permission; external links open without signing in within tenant policy.
 */
export type FileShareAudience = typeof FileShareAudience[keyof typeof FileShareAudience];


export const FileShareAudience = {
  'signed-in': 'signed-in',
  external: 'external',
} as const;

export interface FileShareLink {
  id: string;
  host: FileHostRef;
  file: LibraryFileRef;
  /**
     * Published major version the link opens; null means the original upload (v1.0) of a file without version history, or a link made before version pinning.
     * @nullable
     */
  versionId: string | null;
  /** Download filename captured when the link was made. */
  title: string;
  /** Semantic document title captured when the link was made; older links fall back to the filename. */
  documentTitle: string;
  status: FileShareLinkStatus;
  expiresAt: string;
  /** @nullable */
  revokedAt: string | null;
  createdAt: string;
  /** @nullable */
  lastOpenedAt: string | null;
  /** @minimum 0 */
  openCount: number;
  audience: FileShareAudience;
  hasPassword: boolean;
  createdByMe: boolean;
}

export interface FileShareLinkPage {
  items: FileShareLink[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export interface FileShareLinkCreate {
  host: FileHostRef;
  file: LibraryFileRef;
  /** @nullable */
  versionId?: string | null;
  /**
     * @minimum 1
     * @maximum 90
     */
  expiresInDays: number;
  audience?: FileShareAudience;
  requestId: string;
}

export interface FileShareLinkProtectedCreate {
  host: FileHostRef;
  file: LibraryFileRef;
  /** @nullable */
  versionId?: string | null;
  /**
     * @minimum 1
     * @maximum 90
     */
  expiresInDays: number;
  /**
     * @minLength 8
     * @maxLength 72
     */
  password: string;
  requestId: string;
}

export interface FileShareLinkCreated {
  link: FileShareLink;
  /**
     * Shown once on creation; null when a requestId is replayed.
     * @nullable
     */
  token: string | null;
  replayed: boolean;
}

export interface FileShareLinkRevoke {
  confirmed: true;
}

export interface FileShareLinkOpen {
  /** @pattern ^[A-Za-z0-9_-]{43}$ */
  token: string;
}

export type RecycleBinItemKind = typeof RecycleBinItemKind[keyof typeof RecycleBinItemKind];


export const RecycleBinItemKind = {
  file: 'file',
  folder: 'folder',
  document: 'document',
} as const;

export interface RecycleBinItemContents {
  /** @minimum 0 */
  files: number;
  /** @minimum 0 */
  documents: number;
  /** @minimum 0 */
  folders: number;
}

export type RecycleBinItemStatus = typeof RecycleBinItemStatus[keyof typeof RecycleBinItemStatus];


export const RecycleBinItemStatus = {
  restorable: 'restorable',
  expired: 'expired',
} as const;

export interface RecycleBinItem {
  id: string;
  kind: RecycleBinItemKind;
  targetId: string;
  title: string;
  originalFolder: string;
  contents: RecycleBinItemContents;
  deletedAt: string;
  deletedByAppUserId: string;
  deletedByMe: boolean;
  /** @minimum 1 */
  retentionDays: number;
  purgeAfter: string;
  status: RecycleBinItemStatus;
  canRestore: boolean;
  canPurge: boolean;
}

export interface RecycleBinPage {
  items: RecycleBinItem[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
  /** @minimum 1 */
  retentionDays: number;
}

/**
 * One item to delete. Files need `file`, documents need `id`, folders need `path`.
 */
export interface RecycleBinTarget {
  kind: RecycleBinItemKind;
  file?: LibraryFileRef;
  id?: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  path?: string;
}

export interface RecycleBinDeleteRequest {
  host: FileHostRef;
  /**
     * @minItems 1
     * @maxItems 100
     */
  items: RecycleBinTarget[];
  requestId: string;
}

export interface RecycleBinDeleteResult {
  item: RecycleBinTarget;
  ok: boolean;
  /** @nullable */
  recycleBinItemId: string | null;
  /** @nullable */
  error: string | null;
}

export interface RecycleBinDeleteOutcome {
  requestId: string;
  /** @minimum 0 */
  deleted: number;
  results: RecycleBinDeleteResult[];
}

export interface RecycleBinRestoreRequest {
  /**
     * @minItems 1
     * @maxItems 100
     */
  ids: string[];
  requestId: string;
}

export interface RecycleBinRestoreResult {
  id: string;
  ok: boolean;
  /** @nullable */
  error: string | null;
}

export interface RecycleBinRestoreOutcome {
  requestId: string;
  /** @minimum 0 */
  restored: number;
  results: RecycleBinRestoreResult[];
}

export interface RecycleBinPurgeRequest {
  /**
     * The item's exact name.
     * @minLength 1
     * @maxLength 500
     */
  confirmation: string;
  requestId: string;
}

export interface RecycleBinPurgeOutcome {
  requestId: string;
  id: string;
  purged: boolean;
}

export interface FileManagementSettings {
  /**
     * @minimum 1
     * @maximum 3650
     */
  recycleRetentionDays: number;
  defaultRecycleRetentionDays: number;
  /** @minimum 0 */
  revision: number;
  /** @nullable */
  updatedAt: string | null;
}

export interface RecycleRetentionUpdate {
  /**
     * @minimum 1
     * @maximum 3650
     */
  retentionDays: number;
  /** @minimum 0 */
  expectedRevision: number;
  requestId: string;
}

export interface FileSharingPolicy {
  allowExternalSharing: boolean;
  /**
     * @minimum 1
     * @maximum 90
     */
  maxExpiryDays: number;
  requirePassword: boolean;
  /** @minimum 0 */
  revision: number;
  /** @nullable */
  updatedAt: string | null;
}

export interface FileSharingPolicyUpdate {
  allowExternalSharing: boolean;
  /**
     * @minimum 1
     * @maximum 90
     */
  maxExpiryDays: number;
  requirePassword: boolean;
  /** @minimum 0 */
  expectedRevision: number;
  requestId: string;
}

export interface LibraryFolder {
  path: string;
  name: string;
  parent: string;
  /** True for folders created explicitly; false for folders implied by their contents. */
  explicit: boolean;
}

export type LibraryFolderPageBreadcrumbsItem = {
  path: string;
  name: string;
};

export interface LibraryFolderPage {
  parent: string;
  /** Folders from the library root down to `parent`; empty at the root. */
  breadcrumbs: LibraryFolderPageBreadcrumbsItem[];
  items: LibraryFolder[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export interface LibraryFolderCreate {
  host: FileHostRef;
  /**
     * @minLength 1
     * @maxLength 500
     */
  path: string;
  requestId: string;
}

export interface LibraryFolderCreateOutcome {
  requestId: string;
  path: string;
  created: boolean;
}

export interface LibraryFolderRename {
  host: FileHostRef;
  /**
     * @minLength 1
     * @maxLength 500
     */
  path: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  newPath: string;
  requestId: string;
}

export interface LibraryFolderRenameOutcome {
  requestId: string;
  path: string;
  newPath: string;
  /** @minimum 0 */
  files: number;
  /** @minimum 0 */
  documents: number;
  /** @minimum 0 */
  folders: number;
}

export interface LibraryFileCopyRequest {
  host: FileHostRef;
  /**
     * @minItems 1
     * @maxItems 100
     */
  files: LibraryFileRef[];
  /** @maxLength 500 */
  folder: string;
  requestId: string;
}

/**
 * @nullable
 */
export type LibraryFileCopyResultCopy = {
  kind: LibraryFileKind;
  id: string;
} | null;

export interface LibraryFileCopyResult {
  file: LibraryFileRef;
  ok: boolean;
  /** @nullable */
  copy: LibraryFileCopyResultCopy;
  /** @nullable */
  error: string | null;
}

export interface LibraryFileCopyOutcome {
  requestId: string;
  folder: string;
  /** @minimum 0 */
  copied: number;
  results: LibraryFileCopyResult[];
}

export type FileInventoryItemKind = typeof FileInventoryItemKind[keyof typeof FileInventoryItemKind];


export const FileInventoryItemKind = {
  file: 'file',
  document: 'document',
} as const;

export interface FileInventoryItem {
  kind: FileInventoryItemKind;
  id: string;
  title: string;
  contentType: string;
  /**
     * @minimum 0
     * @nullable
     */
  sizeBytes: number | null;
  folder: string;
  sourceKind: string;
  sourceLabel: string;
  status: string;
  updatedAt: string;
}

export interface FileInventoryPage {
  items: FileInventoryItem[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export type StorageCleanupItemReason = typeof StorageCleanupItemReason[keyof typeof StorageCleanupItemReason];


export const StorageCleanupItemReason = {
  upload_failed: 'upload_failed',
  delete_failed: 'delete_failed',
  purge: 'purge',
} as const;

export type StorageCleanupItemState = typeof StorageCleanupItemState[keyof typeof StorageCleanupItemState];


export const StorageCleanupItemState = {
  pending: 'pending',
  deleted: 'deleted',
  retained: 'retained',
} as const;

export interface StorageCleanupItem {
  id: string;
  reason: StorageCleanupItemReason;
  state: StorageCleanupItemState;
  /** @minimum 0 */
  attempts: number;
  /** @nullable */
  lastErrorCode: string | null;
  createdAt: string;
  retryAt: string;
  /** @nullable */
  completedAt: string | null;
}

export interface StorageCleanupPage {
  items: StorageCleanupItem[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
}

export interface StorageCleanupRunRequest {
  confirmed: true;
}

export interface StorageCleanupRunResult {
  /** @minimum 0 */
  deleted: number;
  /** @minimum 0 */
  retained: number;
  /** @minimum 0 */
  failed: number;
}

export interface PublicFileShare {
  requiresPassword: boolean;
  expiresAt: string;
  /** @nullable */
  title: string | null;
  /** @nullable */
  filename: string | null;
  /** @nullable */
  contentType: string | null;
  /**
     * @minimum 0
     * @nullable
     */
  sizeBytes: number | null;
}

export interface PublicFileShareDownload {
  /** @maxLength 200 */
  password?: string;
}

export interface SharedFile {
  linkId: string;
  title: string;
  filename: string;
  contentType: string;
  /** @minimum 0 */
  sizeBytes: number;
  /** @nullable */
  versionLabel: string | null;
  expiresAt: string;
  host: FileHostRef;
}

export interface LibraryImageDraftCreate {
  host: FileHostRef;
  /**
     * @minLength 3
     * @maxLength 2000
     */
  prompt: string;
  /** @maxLength 500 */
  folder?: string;
  requestId: string;
}

export interface LibraryImageDraftSave {
  /**
     * @minLength 1
     * @maxLength 200
     */
  filename: string;
  requestId: string;
}

export type LibraryImageDraftStatus = typeof LibraryImageDraftStatus[keyof typeof LibraryImageDraftStatus];


export const LibraryImageDraftStatus = {
  ready: 'ready',
  saved: 'saved',
  discarded: 'discarded',
} as const;

export interface LibraryImageDraft {
  id: string;
  host: FileHostRef;
  status: LibraryImageDraftStatus;
  prompt: string;
  folder: string;
  contentType: string;
  /** @minimum 0 */
  sizeBytes: number;
  /** @nullable */
  savedLibraryEntryId: string | null;
  createdAt: string;
}

export interface FileSourceRegistrationReport {
  /** @minimum 0 */
  created: number;
  /** @minimum 0 */
  updated: number;
  /** @minimum 0 */
  unchanged: number;
  discrepancies: string[];
  sources: string[];
}

export interface FileLibraryPage {
  items: FileLibraryEntry[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
  registration: FileSourceRegistrationReport;
}

export interface FileLibraryEntryUpdate {
  /** @maxLength 500 */
  folder?: string;
  /**
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 80
     */
  labels?: string[];
  archived?: boolean;
}

export interface SowEmailRequestInput {
  idempotencyKey: string;
  customerId: string;
  /**
     * @maxLength 60000
     * @nullable
     */
  text: string | null;
  /** @nullable */
  emailId: string | null;
  /** @maxItems 10 */
  attachmentFileIds: string[];
}

export interface SowQuoteRequestInput {
  proposalId?: string;
  /** @minimum 1 */
  expectedProposalRevision?: number;
  idempotencyKey: string;
  /** @minimum 1 */
  expectedRevision: number;
  templateVersionId: string;
  /** @maxItems 100 */
  optionalItemIds: string[];
  /**
     * @minLength 10
     * @maxLength 2000
     */
  reviewReason: string;
}

export interface SowRequestAttemptInput {
  /** @minimum 1 */
  expectedRevision: number;
  attemptId: string;
}

export interface SowRequestReviewInput {
  /** @minimum 1 */
  expectedRevision: number;
  /** @nullable */
  templateVersionId: string | null;
}

export type SowRequestAnalysisOutputTemplateStatus = typeof SowRequestAnalysisOutputTemplateStatus[keyof typeof SowRequestAnalysisOutputTemplateStatus];


export const SowRequestAnalysisOutputTemplateStatus = {
  matched: 'matched',
  ambiguous: 'ambiguous',
  none: 'none',
} as const;

export type SowRequestAnalysisOutputTemplate = {
  status: SowRequestAnalysisOutputTemplateStatus;
  /** @nullable */
  templateVersionId: string | null;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  rationale: string;
};

export type SowRequestAnalysisOutputFactsItemField = typeof SowRequestAnalysisOutputFactsItemField[keyof typeof SowRequestAnalysisOutputFactsItemField];


export const SowRequestAnalysisOutputFactsItemField = {
  projectTitle: 'projectTitle',
  customerName: 'customerName',
  recipient: 'recipient',
  startDate: 'startDate',
  deadline: 'deadline',
  scope: 'scope',
  background: 'background',
  exclusion: 'exclusion',
  commercial: 'commercial',
} as const;

export type SowRequestAnalysisOutputFactsItem = {
  field: SowRequestAnalysisOutputFactsItemField;
  /** @maxLength 4000 */
  value: string;
  /**
     * @minimum 0
     * @maximum 19
     */
  sourceIndex: number;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  quote: string;
};

export type SowRequestAnalysisOutputEffortItemKind = typeof SowRequestAnalysisOutputEffortItemKind[keyof typeof SowRequestAnalysisOutputEffortItemKind];


export const SowRequestAnalysisOutputEffortItemKind = {
  paid: 'paid',
  donated: 'donated',
} as const;

export type SowRequestAnalysisOutputEffortItem = {
  /**
     * @minLength 1
     * @maxLength 300
     */
  label: string;
  /** @pattern ^[1-9][0-9]{0,3}$ */
  days: string;
  kind: SowRequestAnalysisOutputEffortItemKind;
  /**
     * @minimum 0
     * @maximum 19
     */
  sourceIndex: number;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  quote: string;
};

export type SowRequestAnalysisOutputUnresolvedItem = {
  /**
     * @minLength 1
     * @maxLength 100
     */
  field: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  message: string;
};

export interface SowRequestAnalysisOutput {
  /** @maxLength 3000 */
  summary: string;
  template: SowRequestAnalysisOutputTemplate;
  /** @maxItems 80 */
  facts: SowRequestAnalysisOutputFactsItem[];
  /** @maxItems 20 */
  effort: SowRequestAnalysisOutputEffortItem[];
  /** @maxItems 100 */
  unresolved: SowRequestAnalysisOutputUnresolvedItem[];
}

export type SowRequestAnalysis = SowRequestAnalysisOutput & {
  attemptId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  model: string;
  /** @pattern ^[a-f0-9]{64}$ */
  sourceHash: string;
  /** @pattern ^[a-f0-9]{64}$ */
  catalogueHash: string;
  /** @pattern ^[a-f0-9]{64}$ */
  policyFingerprint: string;
  createdAt: string;
};

export interface PortalSowStart {
  idempotencyKey: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  message: string;
}

export interface PortalSowMessageInput {
  operationId: string;
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  message: string;
}

export interface PortalSowAttachmentInput {
  operationId: string;
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 120
     * @pattern ^[^/\\]+$
     */
  filename: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  contentType: string;
  /**
     * @minLength 4
     * @maxLength 5592408
     * @pattern ^[A-Za-z0-9+/]+={0,2}$
     */
  data: string;
}

export interface PortalSowSubmission {
  /** @minimum 1 */
  expectedRevision: number;
}

export type PortalSowRequestMessagesItem = {
  id: string;
  /** @maxLength 4000 */
  text: string;
};

export type PortalSowRequestAttachmentsItemExtractionState = typeof PortalSowRequestAttachmentsItemExtractionState[keyof typeof PortalSowRequestAttachmentsItemExtractionState];


export const PortalSowRequestAttachmentsItemExtractionState = {
  complete: 'complete',
  partial: 'partial',
  unsupported: 'unsupported',
  unreadable: 'unreadable',
} as const;

export type PortalSowRequestAttachmentsItem = {
  id: string;
  /** @maxLength 120 */
  filename: string;
  extractionState: PortalSowRequestAttachmentsItemExtractionState;
};

export type PortalSowRequestStatus = typeof PortalSowRequestStatus[keyof typeof PortalSowRequestStatus];


export const PortalSowRequestStatus = {
  draft: 'draft',
  received: 'received',
  processing: 'processing',
  needs_information: 'needs_information',
  failed: 'failed',
  completed_for_staff: 'completed_for_staff',
} as const;

export interface PortalSowRequest {
  id: string;
  /** @minimum 1 */
  revision: number;
  /** @maxLength 12000 */
  summary: string;
  /** @maxLength 1000 */
  guidance: string;
  /** @maxItems 15 */
  messages: PortalSowRequestMessagesItem[];
  /** @maxItems 5 */
  attachments: PortalSowRequestAttachmentsItem[];
  status: PortalSowRequestStatus;
  /** @maxLength 1000 */
  statusMessage: string;
  submitted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PortalSowRequestPage {
  /** @maxItems 20 */
  items: PortalSowRequest[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 20
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type SowRequestSourceKind = typeof SowRequestSourceKind[keyof typeof SowRequestSourceKind];


export const SowRequestSourceKind = {
  email_paste: 'email_paste',
  email_record: 'email_record',
  attachment: 'attachment',
  portal_chat: 'portal_chat',
  quote: 'quote',
} as const;

export type SowRequestSourceExtractionState = typeof SowRequestSourceExtractionState[keyof typeof SowRequestSourceExtractionState];


export const SowRequestSourceExtractionState = {
  complete: 'complete',
  partial: 'partial',
  unsupported: 'unsupported',
  unreadable: 'unreadable',
} as const;

export type SowRequestSourceAttachmentsItemExtractionState = typeof SowRequestSourceAttachmentsItemExtractionState[keyof typeof SowRequestSourceAttachmentsItemExtractionState];


export const SowRequestSourceAttachmentsItemExtractionState = {
  complete: 'complete',
  partial: 'partial',
  unsupported: 'unsupported',
  unreadable: 'unreadable',
} as const;

export type SowRequestSourceAttachmentsItem = {
  fileId: string;
  /**
     * @minLength 1
     * @maxLength 100
     */
  revision: string;
  /** @pattern ^[a-f0-9]{64}$ */
  hash: string;
  /** @maxLength 200 */
  contentType: string;
  /**
     * @minimum 0
     * @maximum 16777216
     */
  sizeBytes: number;
  extractionState: SowRequestSourceAttachmentsItemExtractionState;
};

export interface SowRequestSource {
  kind: SowRequestSourceKind;
  /**
     * @minLength 1
     * @maxLength 200
     */
  sourceId: string;
  /**
     * @minLength 1
     * @maxLength 100
     */
  revision: string;
  /** @pattern ^[a-f0-9]{64}$ */
  hash: string;
  /** @pattern ^[a-f0-9]{64}$ */
  extractionHash: string;
  /** @maxLength 60000 */
  excerpt: string;
  extractionState: SowRequestSourceExtractionState;
  /**
     * @maxItems 50
     * @items.maxLength 2000
     */
  warnings: string[];
  /** @maxItems 20 */
  attachments: SowRequestSourceAttachmentsItem[];
}

export type SowApprovalCandidatePageItemsItem = {
  id: string;
  displayName: string;
};

export interface SowApprovalCandidatePage {
  /** @maxItems 100 */
  items: SowApprovalCandidatePageItemsItem[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface PlatformAgentVoicePolicy {
  /** @minimum 0 */
  revision: number;
  /** @nullable */
  requestId: string | null;
  /**
     * @minimum 1
     * @maximum 1000000
     * @nullable
     */
  dailySessionLimit: number | null;
  /**
     * @minimum 60
     * @maximum 31536000
     * @nullable
     */
  maxSessionSeconds: number | null;
}

export interface PlatformAgentVoicePolicyWrite {
  requestId: string;
  /** @minimum 0 */
  expectedRevision: number;
  /**
     * @minimum 1
     * @maximum 1000000
     * @nullable
     */
  dailySessionLimit: number | null;
  /**
     * @minimum 60
     * @maximum 31536000
     * @nullable
     */
  maxSessionSeconds: number | null;
  /**
     * @minLength 10
     * @maxLength 2000
     */
  reason: string;
}

export type AiActivityFeature = typeof AiActivityFeature[keyof typeof AiActivityFeature];


export const AiActivityFeature = {
  assistant_chat: 'assistant_chat',
  research: 'research',
  search: 'search',
  image_draft: 'image_draft',
  document_agent: 'document_agent',
  model_preference: 'model_preference',
} as const;

export type AiActivityOutcome = typeof AiActivityOutcome[keyof typeof AiActivityOutcome];


export const AiActivityOutcome = {
  succeeded: 'succeeded',
  failed: 'failed',
  denied: 'denied',
  limited: 'limited',
} as const;

/**
 * Identifiers, counts and codes only. Never prompts, replies, search text, file names or provider error prose.
 */
export interface AiActivityEvent {
  id: string;
  occurredAt: string;
  actorAppUserId: string;
  /** @nullable */
  actorDisplayName: string | null;
  /** @nullable */
  tenantId: string | null;
  /** @nullable */
  entityId: string | null;
  feature: AiActivityFeature;
  action: string;
  outcome: AiActivityOutcome;
  /** @nullable */
  model: string | null;
  /** @nullable */
  durationMs: number | null;
  /** @nullable */
  inputTokens: number | null;
  /** @nullable */
  outputTokens: number | null;
  /** @nullable */
  resultCount: number | null;
  /** @nullable */
  errorCode: string | null;
  /** @nullable */
  resourceId: string | null;
}

export interface AiActivityList {
  items: AiActivityEvent[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface AssistantModelOption {
  id: string;
  isDefault: boolean;
}

export interface AssistantModelChoices {
  models: AssistantModelOption[];
  /** @nullable */
  defaultModel: string | null;
  allowPersonalChoice: boolean;
  /** @nullable */
  preferredModel: string | null;
  /**
     * The model the server will use for this person's next reply
     * @nullable
     */
  effectiveModel: string | null;
}

export interface AssistantModelPreferenceWrite {
  /**
     * Null clears the preference
     * @minLength 1
     * @maxLength 200
     * @nullable
     */
  model: string | null;
}

export interface PlatformAssistantModelPolicy {
  /** @minimum 0 */
  revision: number;
  /** @nullable */
  requestId: string | null;
  allowedModels: string[];
  /** @nullable */
  defaultModel: string | null;
  allowPersonalChoice: boolean;
  /** Models served by the saved Project AI connection */
  configuredModels: string[];
}

export interface PlatformAssistantModelPolicyWrite {
  requestId: string;
  /** @minimum 0 */
  expectedRevision: number;
  /**
     * @minItems 1
     * @maxItems 16
     * @items.minLength 1
     * @items.maxLength 200
     */
  allowedModels: string[];
  /**
     * @minLength 1
     * @maxLength 200
     */
  defaultModel: string;
  allowPersonalChoice: boolean;
  /**
     * @minLength 10
     * @maxLength 2000
     */
  reason: string;
}

export interface AssistantUsageLimitValues {
  /**
     * Signed-in assistant replies and image drafts per person per UTC day; null for no limit
     * @minimum 1
     * @maximum 100000
     * @nullable
     */
  dailyRequestLimit: number | null;
  /**
     * AI tokens per person per UTC day; null for no limit
     * @minimum 1000
     * @maximum 1000000000
     * @nullable
     */
  dailyTokenLimit: number | null;
}

export interface PlatformAssistantUsagePolicy {
  /** @minimum 0 */
  revision: number;
  /** @nullable */
  requestId: string | null;
  limits: AssistantUsageLimitValues;
}

export interface PlatformAssistantUsagePolicyWrite {
  requestId: string;
  /** @minimum 0 */
  expectedRevision: number;
  limits: AssistantUsageLimitValues;
  /**
     * @minLength 10
     * @maxLength 2000
     */
  reason: string;
}

export interface AssistantMyUsage {
  date: string;
  /** @minimum 0 */
  requests: number;
  /** @minimum 0 */
  inputTokens: number;
  /** @minimum 0 */
  outputTokens: number;
  limits: AssistantUsageLimitValues;
  /**
     * @minimum 0
     * @nullable
     */
  remainingRequests: number | null;
  /**
     * @minimum 0
     * @nullable
     */
  remainingTokens: number | null;
}

export interface PlatformAssistantUsageRow {
  appUserId: string;
  /** @nullable */
  displayName: string | null;
  /** @minimum 0 */
  requests: number;
  /** @minimum 0 */
  inputTokens: number;
  /** @minimum 0 */
  outputTokens: number;
}

export interface PlatformAssistantUsageList {
  date: string;
  items: PlatformAssistantUsageRow[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
  limits: AssistantUsageLimitValues;
}

export type AssistantImageSize = typeof AssistantImageSize[keyof typeof AssistantImageSize];


export const AssistantImageSize = {
  '1024x1024': '1024x1024',
  '1536x1024': '1536x1024',
  '1024x1536': '1024x1536',
} as const;

export interface AssistantImageDraftCreate {
  /**
     * @minLength 3
     * @maxLength 2000
     */
  prompt: string;
  size: AssistantImageSize;
}

export interface AssistantImageDraft {
  id: string;
  prompt: string;
  size: AssistantImageSize;
  contentType: string;
  /** @minimum 1 */
  sizeBytes: number;
  model: string;
  createdAt: string;
}

export interface AssistantImageDraftList {
  items: AssistantImageDraft[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type AssistantSearchSourceKind = typeof AssistantSearchSourceKind[keyof typeof AssistantSearchSourceKind];


export const AssistantSearchSourceKind = {
  crm_record: 'crm_record',
  knowledge_document: 'knowledge_document',
} as const;

export interface AssistantSearchSource {
  kind: AssistantSearchSourceKind;
  id: string;
  title: string;
  /** @nullable */
  deepLink: string | null;
  /** @nullable */
  sourceVersion: number | null;
  /** @nullable */
  location: string | null;
}

export interface AssistantSearchRecord {
  key: string;
  type: string;
  recordId: string;
  title: string;
  /** @nullable */
  subtitle: string | null;
  /** @nullable */
  status: string | null;
  /** @nullable */
  accountName: string | null;
  /** @nullable */
  updatedAt: string | null;
  source: AssistantSearchSource;
}

export interface AssistantSearchPassage {
  chunkId: string;
  excerpt: string;
  freshness: string;
  score: number;
  source: AssistantSearchSource;
}

export interface AssistantSearchResult {
  search: string;
  records: AssistantSearchRecord[];
  /** @minimum 0 */
  recordTotal: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
  /** Approved knowledge passages; only on the first page */
  passages: AssistantSearchPassage[];
  knowledgeState: string;
  knowledgeMessage: string;
}

export type AiLimitSettingCategory = typeof AiLimitSettingCategory[keyof typeof AiLimitSettingCategory];


export const AiLimitSettingCategory = {
  agent: 'agent',
  reply: 'reply',
  usage: 'usage',
  public: 'public',
} as const;

/**
 * Who the setting would apply to
 */
export type AiLimitSettingAudience = typeof AiLimitSettingAudience[keyof typeof AiLimitSettingAudience];


export const AiLimitSettingAudience = {
  signed_in: 'signed_in',
  public: 'public',
} as const;

/**
 * removed means permanently disabled; enforced means a public protection
 */
export type AiLimitSettingStatus = typeof AiLimitSettingStatus[keyof typeof AiLimitSettingStatus];


export const AiLimitSettingStatus = {
  removed: 'removed',
  enforced: 'enforced',
} as const;

export type AiLimitSettingEffectiveSource = typeof AiLimitSettingEffectiveSource[keyof typeof AiLimitSettingEffectiveSource];


export const AiLimitSettingEffectiveSource = {
  disabled: 'disabled',
  server: 'server',
  default: 'default',
} as const;

export interface AiLimitSetting {
  /** @pattern ^[A-Z][A-Z0-9_]{1,63}$ */
  key: string;
  category: AiLimitSettingCategory;
  label: string;
  description: string;
  unit: string;
  /** Who the setting would apply to */
  audience: AiLimitSettingAudience;
  /** removed means permanently disabled; enforced means a public protection */
  status: AiLimitSettingStatus;
  effectiveSource: AiLimitSettingEffectiveSource;
  /**
     * Effective limit; null when no limit applies
     * @nullable
     */
  effectiveValue: number | null;
  /**
     * Built-in value kept for reference
     * @nullable
     */
  retainedDefault: number | null;
  disabledBehaviour: string;
  /**
     * Retired administrator value kept as inert history
     * @nullable
     */
  legacyValue: number | null;
}

export type PlatformAiLimitsLegacy = {
  /**
     * Latest retained Agent working limits revision (inert)
     * @minimum 0
     */
  agentLimitRevision: number;
  /**
     * Latest retained usage limits revision (inert)
     * @minimum 0
     */
  assistantUsagePolicyRevision: number;
};

export interface PlatformAiLimits {
  items: AiLimitSetting[];
  legacy: PlatformAiLimitsLegacy;
}

export interface AgentWorkingLimitValues {
  /**
     * Model steps for application lookups in one reply
     * @minimum 1
     * @maximum 48
     */
  lookupRounds: number;
  /**
     * Lookup tool calls in one reply
     * @minimum 1
     * @maximum 512
     */
  lookupCalls: number;
  /**
     * Overall time for lookups in one reply
     * @minimum 30
     * @maximum 1800
     */
  lookupTimeLimitSeconds: number;
  /**
     * Size of one tool result returned to the model
     * @minimum 8192
     * @maximum 1048576
     */
  toolResultBytes: number;
  /**
     * Size of one tool request from the model
     * @minimum 131072
     * @maximum 4194304
     */
  toolArgumentBytes: number;
  /**
     * Size of one model response
     * @minimum 65536
     * @maximum 4194304
     */
  responseBytes: number;
  /**
     * Model steps for record file actions in one reply
     * @minimum 1
     * @maximum 48
     */
  fileToolRounds: number;
}

export interface PlatformAgentLimits {
  /** @minimum 0 */
  revision: number;
  /** @nullable */
  requestId: string | null;
  limits: AgentWorkingLimitValues;
  defaults: AgentWorkingLimitValues;
}

export interface PlatformAgentLimitsWrite {
  requestId: string;
  /** @minimum 0 */
  expectedRevision: number;
  limits: AgentWorkingLimitValues;
  /**
     * @minLength 10
     * @maxLength 2000
     */
  reason: string;
}

export interface SowApprovalPolicyWrite {
  /** @minimum 0 */
  expectedRevision: number;
  /** @pattern ^[a-f0-9]{64}$ */
  expectedFingerprint: string;
  overrides: SowApprovalOverrides;
  /**
     * @minLength 10
     * @maxLength 2000
     */
  reason: string;
}

export type SowRequestOrigin = typeof SowRequestOrigin[keyof typeof SowRequestOrigin];


export const SowRequestOrigin = {
  email: 'email',
  portal: 'portal',
  quote: 'quote',
} as const;

export type SowRequestExtractedFieldsItem = {
  /**
     * @minLength 1
     * @maxLength 100
     */
  field: string;
  /** @maxLength 8000 */
  value: string;
  /**
     * @minimum 0
     * @maximum 19
     */
  sourceIndex: number;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  quote: string;
};

export type SowRequestUnresolvedItemsItem = {
  /**
     * @minLength 1
     * @maxLength 100
     */
  field: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  message: string;
};

export type SowRequestProcessingState = typeof SowRequestProcessingState[keyof typeof SowRequestProcessingState];


export const SowRequestProcessingState = {
  received: 'received',
  analyzing: 'analyzing',
  needs_information: 'needs_information',
  needs_template: 'needs_template',
  ready_for_review: 'ready_for_review',
  materializing: 'materializing',
  drafted: 'drafted',
  failed: 'failed',
  cancelled: 'cancelled',
} as const;

export interface SowRequest {
  id: string;
  tenantId: string;
  entityId: string;
  customerId: string;
  /** @minimum 1 */
  revision: number;
  sourceKind: SowRequestOrigin;
  /**
     * @minLength 1
     * @maxLength 100
     */
  sourceRevision: string;
  /** @pattern ^[a-f0-9]{64}$ */
  sourceHash: string;
  analysis?: SowRequestAnalysis | null;
  /** @nullable */
  sessionId?: string | null;
  /**
     * @minItems 1
     * @maxItems 20
     */
  sources: SowRequestSource[];
  /** @nullable */
  templateVersionId: string | null;
  /**
     * @nullable
     * @pattern ^[a-f0-9]{64}$
     */
  templateSourceHash: string | null;
  /** @maxItems 200 */
  extractedFields: SowRequestExtractedFieldsItem[];
  /** @maxItems 200 */
  unresolvedItems: SowRequestUnresolvedItemsItem[];
  processingState: SowRequestProcessingState;
  /**
     * @maxLength 2000
     * @nullable
     */
  processingError: string | null;
  /** @nullable */
  documentId: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  documentRevision: number | null;
  /** @nullable */
  quoteId: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  quoteRevision: number | null;
  /**
     * @nullable
     * @pattern ^[a-f0-9]{64}$
     */
  quoteHash: string | null;
  createdAt: string;
  updatedAt: string;
}

export type SowProtectedTermKey = typeof SowProtectedTermKey[keyof typeof SowProtectedTermKey];


export const SowProtectedTermKey = {
  materials: 'materials',
  agreement: 'agreement',
  payment: 'payment',
  schedule1: 'schedule1',
  operations: 'operations',
} as const;

export interface SowProtectedTerm {
  key: SowProtectedTermKey;
  content: ManagedDocumentContent;
}

export type SowTemplateDefinition = { [key: string]: unknown };

export type SowTemplateFormat = typeof SowTemplateFormat[keyof typeof SowTemplateFormat];


export const SowTemplateFormat = {
  standard: 'standard',
  'engagement-pack': 'engagement-pack',
  custom: 'custom',
} as const;

export interface SowTemplate {
  id: string;
  key: string;
  /** @minimum 1 */
  version: number;
  sourceHash: string;
  definition: SowTemplateDefinition;
  terms: SowProtectedTerm[];
  createdAt: string;
  format?: SowTemplateFormat;
  name?: string;
  description?: string;
}

export interface SowTemplatePage {
  items: SowTemplate[];
  total: number;
  limit: number;
  offset: number;
}

export type SowCustomTemplateFieldType = typeof SowCustomTemplateFieldType[keyof typeof SowCustomTemplateFieldType];


export const SowCustomTemplateFieldType = {
  text: 'text',
  date: 'date',
  number: 'number',
  choice: 'choice',
} as const;

export interface SowCustomTemplateField {
  /** @pattern ^[a-z][a-z0-9-]{0,47}$ */
  id: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  label: string;
  type: SowCustomTemplateFieldType;
  required: boolean;
  /**
     * @maxItems 30
     * @items.minLength 1
     * @items.maxLength 200
     */
  options?: string[];
  /**
     * Example guidance for authors. Never rendered or treated as project data.
     * @maxLength 2000
     */
  guidance: string;
}

export interface SowCustomTemplateColumn {
  /** @pattern ^[a-z][a-z0-9-]{0,47}$ */
  id: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  label: string;
}

export type SowCustomTemplateSectionKind = typeof SowCustomTemplateSectionKind[keyof typeof SowCustomTemplateSectionKind];


export const SowCustomTemplateSectionKind = {
  fields: 'fields',
  prose: 'prose',
  table: 'table',
  protected: 'protected',
  commercial: 'commercial',
  signature: 'signature',
} as const;

export type SowCustomTemplateSectionComponentsItem = typeof SowCustomTemplateSectionComponentsItem[keyof typeof SowCustomTemplateSectionComponentsItem];


export const SowCustomTemplateSectionComponentsItem = {
  investment: 'investment',
  materials: 'materials',
  invoices: 'invoices',
} as const;

/**
 * fields, prose and table sections are author-editable project inputs. protected, commercial and signature sections are fixed by the template and native calculations.
 */
export interface SowCustomTemplateSection {
  /** @pattern ^[a-z][a-z0-9-]{0,47}$ */
  id: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  heading: string;
  kind: SowCustomTemplateSectionKind;
  required: boolean;
  /**
     * Example guidance for authors. Never rendered or treated as project data.
     * @maxLength 4000
     */
  guidance: string;
  /** @maxItems 30 */
  fields?: SowCustomTemplateField[];
  /** @maxItems 8 */
  columns?: SowCustomTemplateColumn[];
  /**
     * @minimum 1
     * @maximum 100
     */
  maxRows?: number;
  /** @maxItems 3 */
  components?: SowCustomTemplateSectionComponentsItem[];
  content?: ManagedDocumentContent;
}

export type SowCustomTemplateDefinitionFormat = typeof SowCustomTemplateDefinitionFormat[keyof typeof SowCustomTemplateDefinitionFormat];


export const SowCustomTemplateDefinitionFormat = {
  'circle-t-custom-v1': 'circle-t-custom-v1',
} as const;

export type SowCustomTemplateDefinitionLayoutPageSize = typeof SowCustomTemplateDefinitionLayoutPageSize[keyof typeof SowCustomTemplateDefinitionLayoutPageSize];


export const SowCustomTemplateDefinitionLayoutPageSize = {
  a4: 'a4',
  letter: 'letter',
} as const;

export type SowCustomTemplateDefinitionLayoutOrientation = typeof SowCustomTemplateDefinitionLayoutOrientation[keyof typeof SowCustomTemplateDefinitionLayoutOrientation];


export const SowCustomTemplateDefinitionLayoutOrientation = {
  portrait: 'portrait',
  landscape: 'landscape',
} as const;

export type SowCustomTemplateDefinitionLayout = {
  pageSize: SowCustomTemplateDefinitionLayoutPageSize;
  orientation: SowCustomTemplateDefinitionLayoutOrientation;
  numberedHeadings: boolean;
};

export interface SowCustomTemplateDefinition {
  format: SowCustomTemplateDefinitionFormat;
  /**
     * @minLength 1
     * @maxLength 160
     */
  name: string;
  /** @maxLength 2000 */
  description: string;
  layout: SowCustomTemplateDefinitionLayout;
  /**
     * @minItems 1
     * @maxItems 40
     */
  sections: SowCustomTemplateSection[];
}

export interface SowCustomTemplateCreate {
  /** @pattern ^[a-z][a-z0-9-]{2,47}$ */
  key: string;
  definition: SowCustomTemplateDefinition;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  reason: string;
}

export interface SowCustomTemplateRevision {
  /** @minimum 1 */
  expectedVersion: number;
  definition: SowCustomTemplateDefinition;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  reason: string;
}

export interface SowCustomTemplatePreviewInput {
  definition: SowCustomTemplateDefinition;
  facts?: SowProjectFacts;
}

export interface SowCustomTemplatePreview {
  definition: SowCustomTemplateDefinition;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
  content: ManagedDocumentContent;
  validation: SowValidation;
  sampleNotice: string;
}

export type SowCustomTemplateDraftSourceFormat = typeof SowCustomTemplateDraftSourceFormat[keyof typeof SowCustomTemplateDraftSourceFormat];


export const SowCustomTemplateDraftSourceFormat = {
  standard: 'standard',
  'engagement-pack': 'engagement-pack',
  custom: 'custom',
} as const;

export interface SowCustomTemplateDraft {
  definition: SowCustomTemplateDefinition;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
  sourceTemplateId: string;
  /** @minimum 1 */
  sourceVersion: number;
  sourceFormat: SowCustomTemplateDraftSourceFormat;
}

/**
 * Preparation progress only. Native document review/publication status is reported separately.
 */
export type SowPreparationStage = typeof SowPreparationStage[keyof typeof SowPreparationStage];


export const SowPreparationStage = {
  brief: 'brief',
  scoping: 'scoping',
  drafting: 'drafting',
  review: 'review',
} as const;

export interface SowPreparationDocumentState {
  id: string;
  title: string;
  status: string;
  /** @minimum 1 */
  draftRevision: number;
  templateVersionId: string;
}

export interface SowPreparationSummary {
  id: string;
  title: string;
  stage: SowPreparationStage;
  templateVersionId: string;
  /** @nullable */
  accountId: string | null;
  /** @nullable */
  opportunityId: string | null;
  /** @nullable */
  quoteId: string | null;
  /** @nullable */
  documentId: string | null;
  /** @nullable */
  documentStatus: string | null;
  /** @minimum 1 */
  revision: number;
  /** @nullable */
  archivedAt: string | null;
  updatedAt: string;
}

export type SowPreparationWorkKind = typeof SowPreparationWorkKind[keyof typeof SowPreparationWorkKind];


export const SowPreparationWorkKind = {
  sow_preparation: 'sow_preparation',
} as const;

export interface SowPreparation {
  id: string;
  workKind: SowPreparationWorkKind;
  title: string;
  brief: string;
  stage: SowPreparationStage;
  templateVersionId: string;
  templateSourceHash: string;
  /** @nullable */
  accountId: string | null;
  /** @nullable */
  opportunityId: string | null;
  /** @nullable */
  quoteId: string | null;
  /** @nullable */
  documentId: string | null;
  document: SowPreparationDocumentState | null;
  /** @minimum 1 */
  revision: number;
  /** @nullable */
  archivedAt: string | null;
  /** @nullable */
  archiveReason: string | null;
  createdByAppUserId: string;
  createdAt: string;
  updatedAt: string;
  /** Current native record-writer authority; not a grant. */
  canWrite: boolean;
}

export interface SowPreparationPage {
  items: SowPreparationSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface SowPreparationCreate {
  clientRequestId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /** @maxLength 8000 */
  brief?: string;
  templateVersionId: string;
  accountId?: string;
  opportunityId?: string;
  quoteId?: string;
  documentId?: string;
}

export interface SowPreparationUpdate {
  clientRequestId: string;
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
  /** @maxLength 8000 */
  brief?: string;
  stage?: SowPreparationStage;
  templateVersionId?: string;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  opportunityId?: string | null;
  /** @nullable */
  quoteId?: string | null;
}

export interface SowPreparationDocumentLink {
  clientRequestId: string;
  /** @minimum 1 */
  expectedRevision: number;
  documentId: string;
}

export interface SowPreparationArchive {
  clientRequestId: string;
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
}

export type SowPreparationOutcomeKind = typeof SowPreparationOutcomeKind[keyof typeof SowPreparationOutcomeKind];


export const SowPreparationOutcomeKind = {
  created: 'created',
  updated: 'updated',
  document_linked: 'document_linked',
  archived: 'archived',
  scope_suggested: 'scope_suggested',
  scope_reviewed: 'scope_reviewed',
  plan_approved: 'plan_approved',
  draft_proposed: 'draft_proposed',
  draft_decided: 'draft_decided',
} as const;

export interface SowPreparationOutcome {
  clientRequestId: string;
  preparationId: string;
  kind: SowPreparationOutcomeKind;
  /** @minimum 1 */
  resultRevision: number;
  replayed: boolean;
  createdAt: string;
}

export interface SowDraftProposeInput {
  clientRequestId: string;
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  instruction?: string;
}

export type SowDraftDecisionInputDecision = typeof SowDraftDecisionInputDecision[keyof typeof SowDraftDecisionInputDecision];


export const SowDraftDecisionInputDecision = {
  accept: 'accept',
  reject: 'reject',
} as const;

export interface SowDraftDecisionInput {
  clientRequestId: string;
  /** @minimum 1 */
  expectedRevision: number;
  runId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  proposalHash: string;
  decision: SowDraftDecisionInputDecision;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  note?: string;
}

export interface SowDraftCancelInput {
  clientRequestId: string;
}

export type SowDraftParagraphKind = typeof SowDraftParagraphKind[keyof typeof SowDraftParagraphKind];


export const SowDraftParagraphKind = {
  scope_statement: 'scope_statement',
  evidenced_claim: 'evidenced_claim',
  proposed_approach: 'proposed_approach',
  commitment: 'commitment',
  unresolved: 'unresolved',
} as const;

export interface SowDraftParagraph {
  kind: SowDraftParagraphKind;
  text: string;
  factIds: string[];
  referenceIds: string[];
}

export type SowDraftRowKind = typeof SowDraftRowKind[keyof typeof SowDraftRowKind];


export const SowDraftRowKind = {
  scope_statement: 'scope_statement',
  evidenced_claim: 'evidenced_claim',
  proposed_approach: 'proposed_approach',
  commitment: 'commitment',
  unresolved: 'unresolved',
} as const;

export interface SowDraftRow {
  cells: string[];
  kind: SowDraftRowKind;
  factIds: string[];
  referenceIds: string[];
}

export type SowDraftTargetKind = typeof SowDraftTargetKind[keyof typeof SowDraftTargetKind];


export const SowDraftTargetKind = {
  narrative: 'narrative',
  table: 'table',
  custom_prose: 'custom_prose',
  custom_table: 'custom_table',
} as const;

export interface SowDraftTarget {
  key: string;
  label: string;
  kind: SowDraftTargetKind;
  columns: string[];
}

export type SowDraftProposalViewTargetKind = typeof SowDraftProposalViewTargetKind[keyof typeof SowDraftProposalViewTargetKind];


export const SowDraftProposalViewTargetKind = {
  narrative: 'narrative',
  table: 'table',
  custom_prose: 'custom_prose',
  custom_table: 'custom_table',
} as const;

export type SowDraftProposalViewStatus = typeof SowDraftProposalViewStatus[keyof typeof SowDraftProposalViewStatus];


export const SowDraftProposalViewStatus = {
  pending: 'pending',
  accepted: 'accepted',
  rejected: 'rejected',
  stale: 'stale',
} as const;

export interface SowDraftProposalView {
  id: string;
  targetKey: string;
  targetLabel: string;
  targetKind: SowDraftProposalViewTargetKind;
  paragraphs: SowDraftParagraph[];
  rows: SowDraftRow[];
  proposalHash: string;
  status: SowDraftProposalViewStatus;
  staleReasons: string[];
  /** @nullable */
  decidedByAppUserId: string | null;
  /** @nullable */
  decidedAt: string | null;
  /** @nullable */
  decisionNote: string | null;
  /** @nullable */
  acceptedVersionId: string | null;
  /** @nullable */
  acceptedDocumentRevision: number | null;
}

export interface SowDraftRunPins {
  templateVersionId: string;
  templateSourceHash: string;
  scopeFingerprint: string;
  /** @nullable */
  documentId: string | null;
  /** @nullable */
  documentRevision: number | null;
  /** @nullable */
  documentHash: string | null;
  lessonsFingerprint: string;
  modelPolicyFingerprint: string;
}

export type SowDraftRunViewFactsItem = {
  id: string;
  category: string;
  text: string;
  referenceIds: string[];
};

export type SowDraftRunViewReferencesItemKind = typeof SowDraftRunViewReferencesItemKind[keyof typeof SowDraftRunViewReferencesItemKind];


export const SowDraftRunViewReferencesItemKind = {
  brief: 'brief',
  workspace: 'workspace',
} as const;

export type SowDraftRunViewReferencesItem = {
  id: string;
  kind: SowDraftRunViewReferencesItemKind;
  /** @nullable */
  label: string | null;
  /** @nullable */
  location: string | null;
  /** @nullable */
  excerpt: string | null;
  available: boolean;
};

export type SowDraftRunViewLessonsItem = {
  lessonId: string;
  revision: number;
  guidance: string;
  category: string;
  applicability: string;
};

export interface SowDraftRunView {
  id: string;
  requestedByAppUserId: string;
  requestedAt: string;
  /** @nullable */
  instruction: string | null;
  pins: SowDraftRunPins;
  facts: SowDraftRunViewFactsItem[];
  references: SowDraftRunViewReferencesItem[];
  lessons: SowDraftRunViewLessonsItem[];
  proposals: SowDraftProposalView[];
  gaps: string[];
  warnings: string[];
}

export type SowPreparationDraftingAttemptsItemStatus = typeof SowPreparationDraftingAttemptsItemStatus[keyof typeof SowPreparationDraftingAttemptsItemStatus];


export const SowPreparationDraftingAttemptsItemStatus = {
  in_flight: 'in_flight',
  cancelled: 'cancelled',
  failed: 'failed',
  completed: 'completed',
} as const;

export type SowPreparationDraftingAttemptsItem = {
  clientRequestId: string;
  status: SowPreparationDraftingAttemptsItemStatus;
  startedAt: string;
  /** @nullable */
  endedAt: string | null;
  /** @nullable */
  errorCode: string | null;
};

export type SowPreparationDraftingReviewReadiness = typeof SowPreparationDraftingReviewReadiness[keyof typeof SowPreparationDraftingReviewReadiness];


export const SowPreparationDraftingReviewReadiness = {
  no_document: 'no_document',
  needs_drafting: 'needs_drafting',
  proposals_pending: 'proposals_pending',
  stale: 'stale',
  ready_for_native_review: 'ready_for_native_review',
} as const;

export type SowPreparationDraftingReview = {
  /** Always true. This review never approves or publishes. */
  advisory: boolean;
  approvalOwner: string;
  readiness: SowPreparationDraftingReviewReadiness;
  readyForReview: boolean;
  /** @nullable */
  documentRevision: number | null;
  /** @nullable */
  documentHash: string | null;
  /** @nullable */
  documentStatus: string | null;
  /** @nullable */
  approvalStatus: string | null;
  gaps: string[];
};

export interface SowPreparationDrafting {
  preparationId: string;
  /** @minimum 1 */
  revision: number;
  canWrite: boolean;
  /** @nullable */
  documentId: string | null;
  templateVersionId: string;
  targets: SowDraftTarget[];
  runs: SowDraftRunView[];
  /** The caller's own drafting requests only. */
  attempts: SowPreparationDraftingAttemptsItem[];
  review: SowPreparationDraftingReview;
}

export interface SowDraftMutation {
  drafting: SowPreparationDrafting;
  outcome: SowPreparationOutcome;
}

export interface SowPreparationMutation {
  preparation: SowPreparation;
  outcome: SowPreparationOutcome;
}

export interface SowPreparationDocumentCandidate {
  documentId: string;
  title: string;
  status: string;
  category: string;
  /** @nullable */
  templateVersionId: string | null;
  /** True when the document is template-bound and has no active preparation. */
  linkable: boolean;
  /** @nullable */
  preparationId: string | null;
  updatedAt: string;
}

export interface SowPreparationDocumentPage {
  items: SowPreparationDocumentCandidate[];
  total: number;
  limit: number;
  offset: number;
}

export type SowScopeCategory = typeof SowScopeCategory[keyof typeof SowScopeCategory];


export const SowScopeCategory = {
  scope: 'scope',
  deliverable: 'deliverable',
  acceptance: 'acceptance',
  assumption: 'assumption',
  exclusion: 'exclusion',
  date: 'date',
  question: 'question',
} as const;

export type SowScopeEvidenceKind = typeof SowScopeEvidenceKind[keyof typeof SowScopeEvidenceKind];


export const SowScopeEvidenceKind = {
  brief: 'brief',
  workspace: 'workspace',
} as const;

/**
 * Exact provenance. Brief evidence carries a verbatim brief excerpt and brief hash; workspace evidence carries the reviewed snapshot passage pin.
 */
export interface SowScopeEvidence {
  kind: SowScopeEvidenceKind;
  /**
     * Withheld (null) when the caller cannot currently access the source.
     * @nullable
     */
  excerpt: string | null;
  available: boolean;
  /** @nullable */
  briefHash: string | null;
  /** @nullable */
  workspaceId: string | null;
  /** @nullable */
  snapshotId: string | null;
  /** @nullable */
  manifestHash: string | null;
  /** @nullable */
  passageId: string | null;
  /** @nullable */
  citation: number | null;
  /** @nullable */
  label: string | null;
  /** @nullable */
  location: string | null;
  /** @nullable */
  sourceVersion: string | null;
  /** @nullable */
  sourceRevisionId: string | null;
  /** @nullable */
  contentHash: string | null;
  /** @nullable */
  excerptHash: string | null;
}

export type SowScopeItemStatus = typeof SowScopeItemStatus[keyof typeof SowScopeItemStatus];


export const SowScopeItemStatus = {
  suggested: 'suggested',
  confirmed: 'confirmed',
  rejected: 'rejected',
} as const;

export type SowScopeItemOrigin = typeof SowScopeItemOrigin[keyof typeof SowScopeItemOrigin];


export const SowScopeItemOrigin = {
  analysis: 'analysis',
  manual: 'manual',
} as const;

export type SowScopeItemFreshness = typeof SowScopeItemFreshness[keyof typeof SowScopeItemFreshness];


export const SowScopeItemFreshness = {
  current: 'current',
  stale: 'stale',
  unavailable: 'unavailable',
} as const;

export interface SowScopeItem {
  id: string;
  category: SowScopeCategory;
  text: string;
  status: SowScopeItemStatus;
  origin: SowScopeItemOrigin;
  evidence: SowScopeEvidence[];
  /** Exact reviewed content hash required to confirm or reject. */
  itemHash: string;
  freshness: SowScopeItemFreshness;
  /** @nullable */
  freshnessMessage: string | null;
  suggestedByAppUserId: string;
  suggestedAt: string;
  /** @nullable */
  decidedByAppUserId: string | null;
  /** @nullable */
  decidedAt: string | null;
}

export interface SowScopeGap {
  category: SowScopeCategory;
  message: string;
}

export interface SowScopeAnalysisState {
  /** @nullable */
  snapshotId: string | null;
  briefHash: string;
  /** none, complete or partial coverage of the reviewed evidence snapshot. */
  evidenceState: string;
  analysedAt: string;
  analysedByAppUserId: string;
  briefChanged: boolean;
}

/**
 * stale when any confirmed item's evidence changed, conflicts or became unavailable; prepared work depending on it must be reviewed again.
 */
export type SowPreparationScopeState = typeof SowPreparationScopeState[keyof typeof SowPreparationScopeState];


export const SowPreparationScopeState = {
  incomplete: 'incomplete',
  ready: 'ready',
  stale: 'stale',
} as const;

export interface SowPreparationScope {
  preparationId: string;
  /** @minimum 1 */
  revision: number;
  briefHash: string;
  /** stale when any confirmed item's evidence changed, conflicts or became unavailable; prepared work depending on it must be reviewed again. */
  state: SowPreparationScopeState;
  items: SowScopeItem[];
  gaps: SowScopeGap[];
  analysis: SowScopeAnalysisState | null;
  /** @minimum 0 */
  staleConfirmedCount: number;
  /**
     * Existing Files workspace anchored to this preparation, if any.
     * @nullable
     */
  workspaceId: string | null;
  canWrite: boolean;
}

export interface SowPlanActivityInput {
  /** @pattern ^[a-z0-9][a-z0-9-]{0,39}$ */
  key: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /**
     * Only an explicitly chosen owner; null stays unassigned.
     * @nullable
     */
  ownerAppUserId: string | null;
  /** @nullable */
  dueAt: string | null;
}

/**
 * linked_document updates only the preparation's own linked Files SOW through drafting; new_document reserves a new canonical draft created later by explicit drafting adoption.
 */
export type SowPreparationPlanInputTarget = typeof SowPreparationPlanInputTarget[keyof typeof SowPreparationPlanInputTarget];


export const SowPreparationPlanInputTarget = {
  linked_document: 'linked_document',
  new_document: 'new_document',
} as const;

export interface SowPreparationPlanInput {
  preparationId: string;
  /** linked_document updates only the preparation's own linked Files SOW through drafting; new_document reserves a new canonical draft created later by explicit drafting adoption. */
  target: SowPreparationPlanInputTarget;
  /** @maxItems 20 */
  activities: SowPlanActivityInput[];
  /**
     * @minLength 1
     * @maxLength 200
     * @nullable
     */
  modelChoiceId: string | null;
}

export type SowPlanDocumentTargetTarget = typeof SowPlanDocumentTargetTarget[keyof typeof SowPlanDocumentTargetTarget];


export const SowPlanDocumentTargetTarget = {
  linked_document: 'linked_document',
  new_document: 'new_document',
} as const;

export interface SowPlanDocumentTarget {
  target: SowPlanDocumentTargetTarget;
  /** @nullable */
  documentId: string | null;
  title: string;
  /** @nullable */
  draftRevision: number | null;
  /** @nullable */
  status: string | null;
}

export interface SowPlanScopeItem {
  id: string;
  category: SowScopeCategory;
  text: string;
  itemHash: string;
}

export type SowPlanScopeState = typeof SowPlanScopeState[keyof typeof SowPlanScopeState];


export const SowPlanScopeState = {
  incomplete: 'incomplete',
  ready: 'ready',
  stale: 'stale',
} as const;

export interface SowPlanScope {
  state: SowPlanScopeState;
  confirmedItems: SowPlanScopeItem[];
  gaps: SowScopeGap[];
  /** Exact brief, confirmed items and their current evidence freshness. */
  fingerprint: string;
}

export type SowPlanActivityAction = typeof SowPlanActivityAction[keyof typeof SowPlanActivityAction];


export const SowPlanActivityAction = {
  create: 'create',
  update: 'update',
  unchanged: 'unchanged',
} as const;

export interface SowPlanActivity {
  key: string;
  title: string;
  /** @nullable */
  ownerAppUserId: string | null;
  /** @nullable */
  ownerName: string | null;
  /** @nullable */
  ownerAuthorizationRevision: string | null;
  /** @nullable */
  dueAt: string | null;
  /** @nullable */
  existingActivityId: string | null;
  action: SowPlanActivityAction;
}

export interface SowPlanModel {
  stage: string;
  /** @nullable */
  choiceId: string | null;
  /** @nullable */
  modelRevision: number | null;
  policyFingerprint: string;
  executable: boolean;
  eligibleChoiceIds: string[];
  reasons: string[];
}

export type SowPlanReadinessState = typeof SowPlanReadinessState[keyof typeof SowPlanReadinessState];


export const SowPlanReadinessState = {
  ready: 'ready',
  blocked: 'blocked',
} as const;

export interface SowPlanReadiness {
  state: SowPlanReadinessState;
  blockers: string[];
}

export interface SowPreparationPlanPreview {
  input: SowPreparationPlanInput;
  preparationId: string;
  preparationTitle: string;
  templateVersionId: string;
  templateSourceHash: string;
  document: SowPlanDocumentTarget;
  scope: SowPlanScope;
  activities: SowPlanActivity[];
  model: SowPlanModel;
  readiness: SowPlanReadiness;
  warnings: string[];
  permittedWork: string;
  /** Exact hash pinned by the private review; any change requires a fresh review. */
  snapshotHash: string;
}

export interface SowPlanLinkedActivity {
  key: string;
  activityId: string;
  /** @nullable */
  subject: string | null;
  /** @nullable */
  status: string | null;
}

export interface SowApprovedPlan {
  proposalId: string;
  snapshotHash: string;
  approvedByAppUserId: string;
  approvedAt: string;
  snapshot: SowPreparationPlanPreview;
  activities: SowPlanLinkedActivity[];
  current: boolean;
  staleReasons: string[];
}

export interface SowPlanConfirm {
  clientRequestId: string;
  /** @minimum 1 */
  expectedRevision: number;
  /** @pattern ^[a-f0-9]{64}$ */
  snapshotHash: string;
  input: SowPreparationPlanInput;
  /**
     * @minLength 1
     * @maxLength 500
     */
  reason: string;
}

export interface SowPreparationPlanStatus {
  preparationId: string;
  plan: SowApprovedPlan | null;
}

export interface SowPlanConfirmResult {
  preparation: SowPreparation;
  plan: SowPreparationPlanStatus;
  outcome: SowPreparationOutcome;
}

export interface SowScopeSuggest {
  clientRequestId: string;
  /** @minimum 1 */
  expectedRevision: number;
  /** Caller's reviewed evidence snapshot from this preparation's workspace. */
  snapshotId?: string;
}

export interface SowScopeCitationInput {
  snapshotId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  passageId: string;
}

export interface SowScopeItemCreate {
  clientRequestId: string;
  /** @minimum 1 */
  expectedRevision: number;
  category: SowScopeCategory;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  text: string;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  briefExcerpt?: string;
  citation?: SowScopeCitationInput;
}

export type SowScopeDecisionDecision = typeof SowScopeDecisionDecision[keyof typeof SowScopeDecisionDecision];


export const SowScopeDecisionDecision = {
  confirm: 'confirm',
  reject: 'reject',
} as const;

export interface SowScopeDecision {
  itemId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  itemHash: string;
  decision: SowScopeDecisionDecision;
}

export interface SowScopeReview {
  clientRequestId: string;
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minItems 1
     * @maxItems 50
     */
  decisions: SowScopeDecision[];
}

export interface SowScopeMutation {
  preparation: SowPreparation;
  scope: SowPreparationScope;
  outcome: SowPreparationOutcome;
}

export interface SowActionInput {
  sessionId: string;
  /** @minimum 1 */
  expectedRevision: number;
  conversationId: string;
}

export type SowWorkingStateSelectedSourcesItemKind = typeof SowWorkingStateSelectedSourcesItemKind[keyof typeof SowWorkingStateSelectedSourcesItemKind];


export const SowWorkingStateSelectedSourcesItemKind = {
  account: 'account',
  contact: 'contact',
  opportunity: 'opportunity',
  document: 'document',
  request: 'request',
} as const;

export type SowWorkingStateSelectedSourcesItem = {
  kind: SowWorkingStateSelectedSourcesItemKind;
  id: string;
  /** @maxLength 100 */
  revision: string;
  /** @pattern ^[a-f0-9]{64}$ */
  hash: string;
};

export type SowWorkingStateConfirmedFactsItem = {
  /** @maxLength 100 */
  field: string;
  /** @maxLength 20000 */
  value: string;
  /** @maxLength 2000 */
  attribution: string;
};

export interface SowWorkingState {
  templateVersionId: string;
  facts: SowProjectFacts;
  /** @maxItems 20 */
  selectedSources: SowWorkingStateSelectedSourcesItem[];
  /** @maxItems 200 */
  confirmedFacts: SowWorkingStateConfirmedFactsItem[];
  /**
     * @maxItems 200
     * @items.maxLength 2000
     */
  unresolvedFacts: string[];
  /** @nullable */
  documentId: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  documentRevision: number | null;
}

export type ManagedDocumentCreateTemplateSource = {
  documentId: string;
  /** @minimum 1 */
  revision: number;
  /** @pattern ^[a-f0-9]{64}$ */
  contentHash: string;
};

export interface ManagedDocumentCreate {
  templateDraftSource?: ManagedDocumentTemplateSource;
  templateSource?: ManagedDocumentCreateTemplateSource;
  clientRequestId?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  category?: string;
  /** @maxLength 500 */
  folder?: string;
  /**
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 80
     */
  labels?: string[];
  content?: ManagedDocumentContent;
  sow?: SowBindingInput;
}

export interface ManagedDocumentDraftSave {
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  content: ManagedDocumentContent;
  sowFacts?: SowProjectFacts;
}

export interface ManagedDocumentReason {
  /**
     * @minLength 1
     * @maxLength 2000
     */
  reason: string;
}

export interface ManagedDocumentVersionPage {
  items: ManagedDocumentVersion[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface ManagedDocumentVersionPreview {
  nextLabel: string;
  /** @minimum 0 */
  majorVersion: number;
  /** @minimum 0 */
  minorVersion: number;
  /** @nullable */
  latestVersionId: string | null;
  /** @nullable */
  latestLabel: string | null;
  /** True when the draft matches the latest saved version exactly. */
  unchanged: boolean;
  /** @minimum 0 */
  changedBlocks: number;
  /** @minimum 0 */
  addedWords: number;
  /** @minimum 0 */
  removedWords: number;
  /**
     * Draft revision the preview describes.
     * @minimum 1
     */
  revision: number;
}

export interface DocumentStyleTemplate {
  id: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  name: string;
  /** @maxLength 500 */
  description: string;
  /**
     * @minItems 7
     * @maxItems 100
     */
  definitions: ManagedDocumentStyle[];
  /** @minimum 1 */
  revision: number;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentStyleTemplatePage {
  items: DocumentStyleTemplate[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
  /** Whether the caller may add, change or archive style templates. */
  canEdit: boolean;
}

export interface DocumentStyleTemplateCreate {
  /**
     * @minLength 1
     * @maxLength 80
     */
  name: string;
  /** @maxLength 500 */
  description?: string;
  /**
     * @minItems 7
     * @maxItems 100
     */
  definitions: ManagedDocumentStyle[];
}

export interface DocumentStyleTemplateUpdate {
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 80
     */
  name?: string;
  /** @maxLength 500 */
  description?: string;
  /**
     * @minItems 7
     * @maxItems 100
     */
  definitions?: ManagedDocumentStyle[];
}

export type ContentDocumentTarget = {
  kind: 'content_hub_asset';
  assetId: string;
} | {
  kind: 'microsite_rich_content';
  /** @pattern ^[A-Za-z0-9_-]{1,120}$ */
  componentId: string;
};

export interface OpenContentDocument {
  target: ContentDocumentTarget;
  /** Microsite blocks only. The editor's reading of the block's current HTML; ignored when a document already exists. */
  initialContent?: ManagedDocumentContent;
}

export type ContentDocumentBindingTargetKind = typeof ContentDocumentBindingTargetKind[keyof typeof ContentDocumentBindingTargetKind];


export const ContentDocumentBindingTargetKind = {
  content_hub_asset: 'content_hub_asset',
  microsite_rich_content: 'microsite_rich_content',
} as const;

export interface ContentDocumentBinding {
  id: string;
  documentId: string;
  documentTitle: string;
  targetKind: ContentDocumentBindingTargetKind;
  /** @nullable */
  assetId: string | null;
  /** @nullable */
  componentId: string | null;
  /** @nullable */
  lastAppliedAt: string | null;
  createdAt: string;
  /** True when this request created the authoring document. */
  created: boolean;
}

export interface ContentDocumentBindingPage {
  items: ContentDocumentBinding[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface ApplyContentDocument {
  /**
     * Content Hub asset version or microsite draft revision the person reviewed.
     * @minimum 0
     */
  expectedTargetRevision: number;
}

export type ApplyContentDocumentResultOutcome = typeof ApplyContentDocumentResultOutcome[keyof typeof ApplyContentDocumentResultOutcome];


export const ApplyContentDocumentResultOutcome = {
  applied: 'applied',
  unchanged: 'unchanged',
} as const;

export interface ApplyContentDocumentResult {
  binding: ContentDocumentBinding;
  outcome: ApplyContentDocumentResultOutcome;
  /** @minimum 0 */
  targetRevision: number;
}

export type DocumentNamedStyleDefinitionFontFamily = typeof DocumentNamedStyleDefinitionFontFamily[keyof typeof DocumentNamedStyleDefinitionFontFamily];


export const DocumentNamedStyleDefinitionFontFamily = {
  Arial: 'Arial',
  Calibri: 'Calibri',
  Times_New_Roman: 'Times New Roman',
  Georgia: 'Georgia',
  Verdana: 'Verdana',
  Courier_New: 'Courier New',
} as const;

export type DocumentNamedStyleDefinitionAlignment = typeof DocumentNamedStyleDefinitionAlignment[keyof typeof DocumentNamedStyleDefinitionAlignment];


export const DocumentNamedStyleDefinitionAlignment = {
  left: 'left',
  center: 'center',
  right: 'right',
  justify: 'justify',
} as const;

export interface DocumentNamedStyleDefinition {
  fontFamily: DocumentNamedStyleDefinitionFontFamily;
  /**
     * @minimum 6
     * @maximum 96
     */
  fontSize: number;
  /** @pattern ^#[0-9a-fA-F]{6}$ */
  color: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  alignment: DocumentNamedStyleDefinitionAlignment;
  /**
     * @minimum 0
     * @maximum 144
     */
  spaceBefore: number;
  /**
     * @minimum 0
     * @maximum 144
     */
  spaceAfter: number;
  /**
     * @minimum 1
     * @maximum 3
     */
  lineHeight: number;
}

export interface DocumentNamedStyle {
  id: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  name: string;
  definition: DocumentNamedStyleDefinition;
  /** @minimum 1 */
  revision: number;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentNamedStylePage {
  items: DocumentNamedStyle[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
  /** Whether the caller may add, change or archive library styles. */
  canEdit: boolean;
}

export interface DocumentNamedStyleCreate {
  /**
     * @minLength 1
     * @maxLength 80
     */
  name: string;
  definition: DocumentNamedStyleDefinition;
}

export interface DocumentNamedStyleUpdate {
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 80
     */
  name?: string;
  definition?: DocumentNamedStyleDefinition;
}

export interface DocumentNamedStyleArchive {
  /** @minimum 1 */
  expectedRevision: number;
}

export interface TranscriptCleanupCreate {
  requestId: string;
  /**
     * @minLength 1
     * @maxLength 20000
     */
  transcript: string;
  /** Optional managed document the text is intended for; read permission is checked. */
  documentId?: string;
}

export type TranscriptCleanupStatus = typeof TranscriptCleanupStatus[keyof typeof TranscriptCleanupStatus];


export const TranscriptCleanupStatus = {
  proposed: 'proposed',
  applied: 'applied',
  discarded: 'discarded',
} as const;

export interface TranscriptCleanup {
  id: string;
  original: string;
  cleaned: string;
  status: TranscriptCleanupStatus;
  /** @nullable */
  documentId: string | null;
  createdAt: string;
  /** @nullable */
  decidedAt: string | null;
}

export type TranscriptCleanupDecisionDecision = typeof TranscriptCleanupDecisionDecision[keyof typeof TranscriptCleanupDecisionDecision];


export const TranscriptCleanupDecisionDecision = {
  applied: 'applied',
  discarded: 'discarded',
} as const;

export interface TranscriptCleanupDecision {
  decision: TranscriptCleanupDecisionDecision;
}

export type ManagedDocumentConflictCode = typeof ManagedDocumentConflictCode[keyof typeof ManagedDocumentConflictCode];


export const ManagedDocumentConflictCode = {
  STALE_DRAFT: 'STALE_DRAFT',
} as const;

export interface ManagedDocumentConflict {
  error: string;
  code: ManagedDocumentConflictCode;
  latestRevision: number;
  latestDraft: ManagedDocumentDraft;
}

export interface ManagedDocumentImportPreview {
  filename: string;
  content: ManagedDocumentContent;
  warnings: string[];
  supported: boolean;
  originalPreservedOnAdoption: true;
}

export type ManagedDocumentReviewDecisionInputOutcome = typeof ManagedDocumentReviewDecisionInputOutcome[keyof typeof ManagedDocumentReviewDecisionInputOutcome];


export const ManagedDocumentReviewDecisionInputOutcome = {
  approved: 'approved',
  changes_requested: 'changes_requested',
  cancelled: 'cancelled',
  reversed: 'reversed',
} as const;

export interface ManagedDocumentReviewDecisionInput {
  outcome: ManagedDocumentReviewDecisionInputOutcome;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  note: string;
  editedAfterApproval?: boolean;
}

export type ManagedDocumentReviewCommentInputAnchor = { [key: string]: unknown };

export interface ManagedDocumentReviewCommentInput {
  /**
     * @minLength 1
     * @maxLength 8000
     */
  body: string;
  anchor?: ManagedDocumentReviewCommentInputAnchor;
}

export type DocumentApprovalPolicyUpdateMode = typeof DocumentApprovalPolicyUpdateMode[keyof typeof DocumentApprovalPolicyUpdateMode];


export const DocumentApprovalPolicyUpdateMode = {
  manual_only: 'manual_only',
  evidence_gated_ai: 'evidence_gated_ai',
} as const;

export type DocumentApprovalPolicyUpdateReviewRules = { [key: string]: unknown };

export interface DocumentApprovalPolicyUpdate {
  /** @minimum 1 */
  expectedVersion: number;
  mode: DocumentApprovalPolicyUpdateMode;
  /**
     * @maxItems 100
     * @items.minLength 1
     * @items.maxLength 80
     */
  eligibleCategories: string[];
  reviewRules: DocumentApprovalPolicyUpdateReviewRules;
  /**
     * @minimum 0
     * @maximum 10000
     */
  confidenceThresholdBasisPoints: number;
  /**
     * @minimum 1
     * @maximum 1000
     */
  minimumComparableHumanDecisions: number;
  /**
     * @minimum 0
     * @maximum 10000
     */
  similarityThresholdBasisPoints: number;
  /**
     * @minimum 1
     * @maximum 3650
     */
  evidenceWindowDays: number;
}

export type DocumentApprovalPolicy = DocumentApprovalPolicyUpdate & {
  id: string;
  /** @minimum 1 */
  version: number;
  updatedAt: string;
};

export type ManagedDocumentAiProposalInputAction = typeof ManagedDocumentAiProposalInputAction[keyof typeof ManagedDocumentAiProposalInputAction];


export const ManagedDocumentAiProposalInputAction = {
  draft: 'draft',
  rewrite: 'rewrite',
  expand: 'expand',
  shorten: 'shorten',
  tone: 'tone',
  summarize: 'summarize',
  compare: 'compare',
  review: 'review',
} as const;

export interface ManagedDocumentAiProposalInput {
  clientRequestId?: string;
  workspaceContext?: WorkspaceEvidenceBinding;
  target?: ManagedDocumentAiTarget;
  action: ManagedDocumentAiProposalInputAction;
  /**
     * @minLength 1
     * @maxLength 8000
     */
  instruction: string;
  /** @nullable */
  comparisonVersionId?: string | null;
}

export type ManagedDocumentAiProposalDecisionDecision = typeof ManagedDocumentAiProposalDecisionDecision[keyof typeof ManagedDocumentAiProposalDecisionDecision];


export const ManagedDocumentAiProposalDecisionDecision = {
  accepted: 'accepted',
  rejected: 'rejected',
} as const;

/**
 * document (default) saves the accepted content on the server. live_session records the reviewed acceptance after the same protection checks; the requester's editor then applies the change as a collaborator in the live session, where the native draft path enforces protection again and records tracked changes. Only Document agent proposals support live_session.
 */
export type ManagedDocumentAiProposalDecisionDelivery = typeof ManagedDocumentAiProposalDecisionDelivery[keyof typeof ManagedDocumentAiProposalDecisionDelivery];


export const ManagedDocumentAiProposalDecisionDelivery = {
  document: 'document',
  live_session: 'live_session',
} as const;

/**
 * Document agent mode chosen for a live_session delivery: coauthor applies tracked suggestions, auto applies directly with a brief highlight.
 */
export type ManagedDocumentAiProposalDecisionAgentMode = typeof ManagedDocumentAiProposalDecisionAgentMode[keyof typeof ManagedDocumentAiProposalDecisionAgentMode];


export const ManagedDocumentAiProposalDecisionAgentMode = {
  coauthor: 'coauthor',
  auto: 'auto',
} as const;

export interface ManagedDocumentAiProposalDecision {
  decision: ManagedDocumentAiProposalDecisionDecision;
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minItems 1
     * @maxItems 20
     */
  sectionIds?: string[];
  /**
     * @minItems 1
     * @maxItems 100
     * @items.minimum 0
     * @items.maximum 9999
     */
  blockIndexes?: number[];
  /** document (default) saves the accepted content on the server. live_session records the reviewed acceptance after the same protection checks; the requester's editor then applies the change as a collaborator in the live session, where the native draft path enforces protection again and records tracked changes. Only Document agent proposals support live_session. */
  delivery?: ManagedDocumentAiProposalDecisionDelivery;
  /** Document agent mode chosen for a live_session delivery: coauthor applies tracked suggestions, auto applies directly with a brief highlight. */
  agentMode?: ManagedDocumentAiProposalDecisionAgentMode;
}

export type ManagedDocumentAiEvaluationOutcome = typeof ManagedDocumentAiEvaluationOutcome[keyof typeof ManagedDocumentAiEvaluationOutcome];


export const ManagedDocumentAiEvaluationOutcome = {
  eligible: 'eligible',
  escalated: 'escalated',
  failed: 'failed',
  stale: 'stale',
} as const;

export interface ManagedDocumentAiEvaluation {
  id: string;
  outcome: ManagedDocumentAiEvaluationOutcome;
  /**
     * @minimum 0
     * @maximum 10000
     */
  confidenceBasisPoints: number;
  /** @minimum 0 */
  comparableHumanDecisions: number;
  /** @minimum 0 */
  approvedWithoutChanges: number;
  /** @minimum 0 */
  editedApprovals: number;
  /** @minimum 0 */
  rejections: number;
  /** @minimum 0 */
  reversals: number;
  reasons: string[];
  policyVersion: number;
  modelVersion: string;
  instructionVersion: string;
  createdAt: string;
}

export type ManagedDocumentPublicationStateInputState = typeof ManagedDocumentPublicationStateInputState[keyof typeof ManagedDocumentPublicationStateInputState];


export const ManagedDocumentPublicationStateInputState = {
  unpublished: 'unpublished',
  revoked: 'revoked',
  archived: 'archived',
} as const;

export interface ManagedDocumentPublicationStateInput {
  state: ManagedDocumentPublicationStateInputState;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  reason: string;
  /** @minimum 1 */
  expectedRevision: number;
}

export type PublicManagedDocumentRepresentationsItemFormat = typeof PublicManagedDocumentRepresentationsItemFormat[keyof typeof PublicManagedDocumentRepresentationsItemFormat];


export const PublicManagedDocumentRepresentationsItemFormat = {
  docx: 'docx',
  pdf: 'pdf',
} as const;

export type PublicManagedDocumentRepresentationsItem = {
  format: PublicManagedDocumentRepresentationsItemFormat;
  url: string;
};

export interface PublicManagedDocument {
  title: string;
  version: string;
  content: ManagedDocumentContent;
  publishedAt: string;
  representations: PublicManagedDocumentRepresentationsItem[];
}

export interface StoredFile {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
  /** @nullable */
  ownerDisplayName?: string | null;
}

export interface FileList {
  files: StoredFile[];
  /** @minimum 0 */
  total: number;
}

export interface DashboardStats {
  categories: number;
  solutions: number;
  products: number;
  modules: number;
  capabilities: number;
}

export type OpportunityStage = typeof OpportunityStage[keyof typeof OpportunityStage];


export const OpportunityStage = {
  lead: 'lead',
  qualified: 'qualified',
  proposal: 'proposal',
  negotiation: 'negotiation',
  won: 'won',
  lost: 'lost',
  awareness: 'awareness',
  consideration: 'consideration',
  checkout: 'checkout',
  purchased: 'purchased',
  abandoned: 'abandoned',
  identified: 'identified',
  contacted: 'contacted',
  diligence: 'diligence',
  term_sheet: 'term_sheet',
  committed: 'committed',
  passed: 'passed',
} as const;

export interface DashboardStageCount {
  stage: OpportunityStage;
  /** @minimum 0 */
  count: number;
}

export interface DashboardSummary {
  /** @minimum 0 */
  accountTotal: number;
  /** @minimum 0 */
  b2bCustomerCount: number;
  /** @minimum 0 */
  emailOptInCount: number;
  /** @minimum 0 */
  activePartnerCount: number;
  /** @minimum 0 */
  opportunityTotal: number;
  /** @minimum 0 */
  activeOpportunityCount: number;
  /** @minimum 0 */
  activeOpportunityValue: number;
  /** @minimum 0 */
  committedOpportunityCount: number;
  /** @minimum 0 */
  enquiryTotal: number;
  /** @minimum 0 */
  newEnquiryCount: number;
  /** @maxItems 17 */
  stageCounts: DashboardStageCount[];
}

export interface TimeseriesPoint {
  date: string;
  created: number;
  completed: number;
}

export interface Timeseries {
  days: number;
  points: TimeseriesPoint[];
}

export type ContentStatus = typeof ContentStatus[keyof typeof ContentStatus];


export const ContentStatus = {
  draft: 'draft',
  in_review: 'in_review',
  approved: 'approved',
  published: 'published',
  needs_update: 'needs_update',
  archived: 'archived',
} as const;

export type BuildStatus = typeof BuildStatus[keyof typeof BuildStatus];


export const BuildStatus = {
  idea: 'idea',
  planning: 'planning',
  in_development: 'in_development',
  testing: 'testing',
  released: 'released',
  deprecated: 'deprecated',
  retired: 'retired',
} as const;

export interface CatalogueCategory {
  id: string;
  name: string;
  slug: string;
  /** @nullable */
  description?: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CatalogueCategoryCreate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug?: string;
  /** @nullable */
  description?: string | null;
  isActive?: boolean;
  sortOrder?: number;
}

export interface CatalogueCategoryUpdate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug?: string;
  /** @nullable */
  description?: string | null;
  isActive?: boolean;
  sortOrder?: number;
}

export interface CatalogueSolution {
  id: string;
  categoryId: string;
  name: string;
  slug: string;
  /** @nullable */
  description?: string | null;
  contentStatus: ContentStatus;
  buildStatus: BuildStatus;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CatalogueSolutionCreate {
  categoryId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug?: string;
  /** @nullable */
  description?: string | null;
  contentStatus?: ContentStatus;
  buildStatus?: BuildStatus;
  isActive?: boolean;
  sortOrder?: number;
}

export interface CatalogueSolutionUpdate {
  categoryId?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug?: string;
  /** @nullable */
  description?: string | null;
  contentStatus?: ContentStatus;
  buildStatus?: BuildStatus;
  isActive?: boolean;
  sortOrder?: number;
}

export interface CatalogueProduct {
  id: string;
  solutionId: string;
  name: string;
  slug: string;
  /** @nullable */
  description?: string | null;
  contentStatus: ContentStatus;
  buildStatus: BuildStatus;
  /** @nullable */
  basePrice?: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CatalogueProductCreate {
  solutionId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug?: string;
  /** @nullable */
  description?: string | null;
  contentStatus?: ContentStatus;
  buildStatus?: BuildStatus;
  /** @nullable */
  basePrice?: string | null;
  isActive?: boolean;
  sortOrder?: number;
}

export interface CatalogueProductUpdate {
  solutionId?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug?: string;
  /** @nullable */
  description?: string | null;
  contentStatus?: ContentStatus;
  buildStatus?: BuildStatus;
  /** @nullable */
  basePrice?: string | null;
  isActive?: boolean;
  sortOrder?: number;
}

export interface ProductModule {
  id: string;
  productId: string;
  name: string;
  slug: string;
  /** @nullable */
  description?: string | null;
  contentStatus: ContentStatus;
  buildStatus: BuildStatus;
  /** @nullable */
  modulePrice?: string | null;
  isCore: boolean;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProductModuleCreate {
  productId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug?: string;
  /** @nullable */
  description?: string | null;
  contentStatus?: ContentStatus;
  buildStatus?: BuildStatus;
  /** @nullable */
  modulePrice?: string | null;
  isCore?: boolean;
  isActive?: boolean;
  sortOrder?: number;
}

export interface ProductModuleUpdate {
  productId?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug?: string;
  /** @nullable */
  description?: string | null;
  contentStatus?: ContentStatus;
  buildStatus?: BuildStatus;
  /** @nullable */
  modulePrice?: string | null;
  isCore?: boolean;
  isActive?: boolean;
  sortOrder?: number;
}

export interface ModuleCapability {
  id: string;
  moduleId: string;
  name: string;
  /** @nullable */
  description?: string | null;
  isUnverified: boolean;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ModuleCapabilityCreate {
  moduleId: string;
  /**
     * @minLength 1
     * @maxLength 300
     */
  name: string;
  /** @nullable */
  description?: string | null;
  isUnverified?: boolean;
  isActive?: boolean;
  sortOrder?: number;
}

export interface ModuleCapabilityUpdate {
  moduleId?: string;
  /**
     * @minLength 1
     * @maxLength 300
     */
  name?: string;
  /** @nullable */
  description?: string | null;
  isUnverified?: boolean;
  isActive?: boolean;
  sortOrder?: number;
}

export interface ProductRequirement {
  id: string;
  productId: string;
  /** @nullable */
  capabilityId?: string | null;
  code: string;
  role: string;
  requirement: string;
  /** @nullable */
  category?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProductRequirementCreate {
  /**
     * @minLength 1
     * @maxLength 50
     */
  code: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  role: string;
  /** @minLength 1 */
  requirement: string;
  /** @nullable */
  category?: string | null;
  /** @nullable */
  capabilityId?: string | null;
  isActive?: boolean;
}

export interface ProductRequirementUpdate {
  /**
     * @minLength 1
     * @maxLength 50
     */
  code?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  role?: string;
  /** @minLength 1 */
  requirement?: string;
  /** @nullable */
  category?: string | null;
  /** @nullable */
  capabilityId?: string | null;
  isActive?: boolean;
}

export type PriceListStatus = typeof PriceListStatus[keyof typeof PriceListStatus];


export const PriceListStatus = {
  active: 'active',
  archived: 'archived',
} as const;

export interface PriceList {
  id: string;
  name: string;
  code: string;
  currency: string;
  appliesToAccountTypes: AccountRelationshipType[];
  isDefault: boolean;
  /** @nullable */
  validFrom: string | null;
  /** @nullable */
  validTo: string | null;
  status: PriceListStatus;
  createdAt: string;
  updatedAt: string;
}

export interface PriceListInput {
  /**
     * @minLength 1
     * @maxLength 120
     */
  name: string;
  /** @pattern ^[A-Za-z0-9][A-Za-z0-9_-]{0,29}$ */
  code: string;
  /** @pattern ^[A-Za-z]{3}$ */
  currency: string;
  /** @maxItems 6 */
  appliesToAccountTypes: AccountRelationshipType[];
  isDefault?: boolean;
  /** @nullable */
  validFrom?: string | null;
  /** @nullable */
  validTo?: string | null;
  status?: PriceListStatus;
}

export type PriceListCreate = PriceListInput;

export interface PriceListUpdate {
  /**
     * @minLength 1
     * @maxLength 120
     */
  name?: string;
  /** @pattern ^[A-Za-z0-9][A-Za-z0-9_-]{0,29}$ */
  code?: string;
  /** @pattern ^[A-Za-z]{3}$ */
  currency?: string;
  /** @maxItems 6 */
  appliesToAccountTypes?: AccountRelationshipType[];
  isDefault?: boolean;
  /** @nullable */
  validFrom?: string | null;
  /** @nullable */
  validTo?: string | null;
  status?: PriceListStatus;
}

export interface PriceListList {
  items: PriceList[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface PriceListCopy {
  /**
     * @minLength 1
     * @maxLength 120
     */
  name: string;
  /** @pattern ^[A-Za-z0-9][A-Za-z0-9_-]{0,29}$ */
  code: string;
}

export interface PriceListItemInput {
  itemType: QuoteItemType;
  itemId: string;
  /**
     * @minimum 0
     * @nullable
     */
  oneOffPrice?: number | null;
  /**
     * @minimum 0
     * @nullable
     */
  recurringPrice?: number | null;
  recurringInterval: RecurringInterval;
  /**
     * @maxLength 80
     * @nullable
     */
  unitLabel?: string | null;
  /**
     * @minLength 1
     * @maxLength 80
     */
  taxTreatment: string;
  /** @exclusiveMinimum 0 */
  minQty: number;
  isQuotable: boolean;
  /**
     * @maxLength 2000
     * @nullable
     */
  notes?: string | null;
}

export interface PriceListItem {
  id: string;
  priceListId: string;
  itemType: QuoteItemType;
  itemId: string;
  /** @nullable */
  oneOffPrice: string | null;
  /** @nullable */
  recurringPrice: string | null;
  recurringInterval: RecurringInterval;
  /** @nullable */
  unitLabel: string | null;
  taxTreatment: string;
  minQty: string;
  isQuotable: boolean;
  /** @nullable */
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PriceListItemBulkUpsert {
  /**
     * @minItems 1
     * @maxItems 100
     */
  items: PriceListItemInput[];
}

export interface PriceListItemBulkResult {
  items: PriceListItem[];
}

export interface PriceListItemList {
  items: PriceListItem[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface PriceListRateInput {
  /** @pattern ^[A-Za-z0-9][A-Za-z0-9_-]{0,29}$ */
  roleCode: string;
  /**
     * @minLength 1
     * @maxLength 120
     */
  roleName: string;
  /** @minimum 0 */
  hourlyRate: number;
  /**
     * @minimum 0
     * @nullable
     */
  dailyRate?: number | null;
  /**
     * @minimum 0
     * @nullable
     */
  costHourlyRate?: number | null;
  /** @minimum 0 */
  sortOrder: number;
  isActive: boolean;
}

export interface PriceListRate {
  id: string;
  priceListId: string;
  roleCode: string;
  roleName: string;
  hourlyRate: string;
  /** @nullable */
  dailyRate: string | null;
  /** @nullable */
  costHourlyRate: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PriceListRateBulkUpsert {
  /**
     * @minItems 1
     * @maxItems 100
     */
  rates: PriceListRateInput[];
}

export interface PriceListRateBulkResult {
  items: PriceListRate[];
}

export interface PriceListRateList {
  items: PriceListRate[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface PriceBookItem {
  /** @nullable */
  priceListItemId: string | null;
  itemType: QuoteItemType;
  itemId: string;
  name: string;
  /** @nullable */
  description: string | null;
  requirements: string[];
  /** @nullable */
  effortHours: string | null;
  /** @nullable */
  oneOffPrice: string | null;
  /** @nullable */
  recurringPrice: string | null;
  recurringInterval: RecurringInterval;
  /** @nullable */
  unitLabel: string | null;
  taxTreatment: string;
  minQty: string;
  isQuotable: boolean;
  /** @nullable */
  notes: string | null;
}

export interface PriceBook {
  priceList: PriceList;
  items: PriceBookItem[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface QuoteSettings {
  /** @nullable */
  defaultPriceListId: string | null;
  /**
     * @minimum 0
     * @maximum 100
     */
  discountApprovalThresholdPct: number;
  /**
     * @minimum 1
     * @maximum 365
     */
  defaultValidityDays: number;
  /** @pattern ^[A-Z0-9_-]{1,12}$ */
  quoteReferencePrefix: string;
  /**
     * @maxLength 20000
     * @nullable
     */
  termsMd: string | null;
  /** @nullable */
  pdfBrandLogoFileId: string | null;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  pdfBrandAccentColour: string;
  /**
     * @maxLength 500
     * @nullable
     */
  pdfBrandFooterText: string | null;
  /** @nullable */
  updatedAt: string | null;
}

export interface QuoteSettingsUpdate {
  /** @nullable */
  defaultPriceListId?: string | null;
  /**
     * @minimum 0
     * @maximum 100
     */
  discountApprovalThresholdPct: number;
  /**
     * @minimum 1
     * @maximum 365
     */
  defaultValidityDays: number;
  /** @pattern ^[A-Za-z0-9_-]{1,12}$ */
  quoteReferencePrefix: string;
  /**
     * @maxLength 20000
     * @nullable
     */
  termsMd?: string | null;
  /** @nullable */
  pdfBrandLogoFileId?: string | null;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  pdfBrandAccentColour: string;
  /**
     * @maxLength 500
     * @nullable
     */
  pdfBrandFooterText?: string | null;
}

export type CatalogueModuleNode = ProductModule & {
  capabilities: ModuleCapability[];
};

export type CatalogueProductNode = CatalogueProduct & {
  modules: CatalogueModuleNode[];
  requirements: ProductRequirement[];
};

export type CatalogueSolutionNode = CatalogueSolution & {
  products: CatalogueProductNode[];
};

export type CatalogueCategoryNode = CatalogueCategory & {
  solutions: CatalogueSolutionNode[];
};

export interface CatalogueHierarchy {
  categories: CatalogueCategoryNode[];
}

export type PublicCatalogueModuleNode = ProductModule & {
  capabilities: ModuleCapability[];
};

export type PublicCatalogueProductNode = CatalogueProduct & {
  modules: PublicCatalogueModuleNode[];
};

export type PublicCatalogueSolutionNode = CatalogueSolution & {
  products: PublicCatalogueProductNode[];
};

export type PublicCatalogueCategoryNode = CatalogueCategory & {
  solutions: PublicCatalogueSolutionNode[];
};

export interface PublicCatalogueHierarchy {
  categories: PublicCatalogueCategoryNode[];
}

export type AccountBaseTemperature = typeof AccountBaseTemperature[keyof typeof AccountBaseTemperature];


export const AccountBaseTemperature = {
  hot: 'hot',
  warm: 'warm',
  cool: 'cool',
  cold: 'cold',
  new: 'new',
} as const;

export type AccountBaseFocusLevel = typeof AccountBaseFocusLevel[keyof typeof AccountBaseFocusLevel];


export const AccountBaseFocusLevel = {
  strategic: 'strategic',
  key: 'key',
  growth: 'growth',
  standard: 'standard',
} as const;

export type AccountBaseTargetSegmentsItem = typeof AccountBaseTargetSegmentsItem[keyof typeof AccountBaseTargetSegmentsItem];


export const AccountBaseTargetSegmentsItem = {
  'au-federal-defence': 'au-federal-defence',
  'au-federal-national-security': 'au-federal-national-security',
  'au-federal-services-citizen': 'au-federal-services-citizen',
  'au-federal-policy-economic': 'au-federal-policy-economic',
  'au-federal-research-science': 'au-federal-research-science',
  'au-federal-regulator': 'au-federal-regulator',
  'au-federal-other': 'au-federal-other',
  'au-state-central': 'au-state-central',
  'au-state-police-justice': 'au-state-police-justice',
  'au-state-health': 'au-state-health',
  'au-state-transport': 'au-state-transport',
  'au-state-education': 'au-state-education',
  'au-state-other': 'au-state-other',
  'au-local-government-large': 'au-local-government-large',
  'au-defence-prime': 'au-defence-prime',
  'au-defence-supplier': 'au-defence-supplier',
  'au-critical-infra-energy': 'au-critical-infra-energy',
  'au-critical-infra-water': 'au-critical-infra-water',
  'au-critical-infra-telco': 'au-critical-infra-telco',
  'au-critical-infra-postal-logistics': 'au-critical-infra-postal-logistics',
  'au-bank-tier1': 'au-bank-tier1',
  'au-insurer-tier1': 'au-insurer-tier1',
  'au-superannuation': 'au-superannuation',
  'au-university-go8': 'au-university-go8',
  'au-private-health-tier1': 'au-private-health-tier1',
  'uk-public-sector': 'uk-public-sector',
  'uk-defence': 'uk-defence',
  'ca-public-sector': 'ca-public-sector',
  'nz-public-sector': 'nz-public-sector',
  'eu-sovereign-cloud-mover': 'eu-sovereign-cloud-mover',
} as const;

export type PartnerType = typeof PartnerType[keyof typeof PartnerType];


export const PartnerType = {
  reseller: 'reseller',
  referral: 'referral',
  technology: 'technology',
  consulting: 'consulting',
  strategic: 'strategic',
} as const;

export type PartnerStatus = typeof PartnerStatus[keyof typeof PartnerStatus];


export const PartnerStatus = {
  identified: 'identified',
  qualified: 'qualified',
  onboarding: 'onboarding',
  active: 'active',
  review: 'review',
  inactive: 'inactive',
} as const;

export type ConsentStatus = typeof ConsentStatus[keyof typeof ConsentStatus];


export const ConsentStatus = {
  unknown: 'unknown',
  opted_in: 'opted_in',
  opted_out: 'opted_out',
} as const;

export interface AccountBase {
  accountKind?: AccountKind;
  /** @nullable */
  website?: string | null;
  /** @nullable */
  industry?: string | null;
  /** @nullable */
  phone?: string | null;
  /** @nullable */
  email?: string | null;
  /** @nullable */
  emailDomain?: string | null;
  /** @nullable */
  addressLine1?: string | null;
  /** @nullable */
  addressLine2?: string | null;
  /** @nullable */
  city?: string | null;
  /** @nullable */
  stateProvince?: string | null;
  /** @nullable */
  postcode?: string | null;
  /** @nullable */
  country?: string | null;
  /** @nullable */
  status?: string | null;
  relationshipType?: AccountRelationshipType;
  partnerType?: PartnerType | null;
  partnerStatus?: PartnerStatus | null;
  /** @nullable */
  organisationType?: string | null;
  /** @nullable */
  size?: string | null;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  accountManagerName?: string | null;
  /** @nullable */
  primaryContactName?: string | null;
  /** @nullable */
  primaryContactEmail?: string | null;
  /** @nullable */
  primaryContactPhone?: string | null;
  showPricesInQuotes?: boolean;
  /** @nullable */
  ownerName?: string | null;
  /** @nullable */
  ownerId?: string | null;
  /** @nullable */
  accountNumber?: string | null;
  /** @nullable */
  category?: string | null;
  /** @nullable */
  sector?: string | null;
  /** @nullable */
  source?: string | null;
  /** Marks test or demo data so production-style lists can exclude it. */
  isTestData?: boolean;
  /**
     * @minimum 0
     * @nullable
     */
  annualRevenue?: number | null;
  /**
     * @minimum 0
     * @nullable
     */
  numberOfEmployees?: number | null;
  /** @nullable */
  lastContactedAt?: string | null;
  /** @nullable */
  renewalDate?: string | null;
  /** @nullable */
  notes?: string | null;
  emailConsent?: ConsentStatus;
  smsConsent?: ConsentStatus;
  /** @nullable */
  consentSource?: string | null;
  /** @nullable */
  consentUpdatedAt?: string | null;
  /** @nullable */
  investorType?: string | null;
  /** @nullable */
  investmentFocus?: string | null;
  /**
     * @minimum 0
     * @nullable
     */
  typicalChequeMin?: number | null;
  /**
     * @minimum 0
     * @nullable
     */
  typicalChequeMax?: number | null;
  /** @nullable */
  accreditationStatus?: string | null;
  /** @nullable */
  lastInvestorUpdateAt?: string | null;
  /**
     * @minimum 0
     * @maximum 100
     * @nullable
     */
  readonly leadScore?: number | null;
  /**
     * 0-100 composite score computed by account intelligence; staff-editable.
     * @minimum 0
     * @maximum 100
     * @nullable
     */
  healthScore?: number | null;
  temperature?: AccountBaseTemperature;
  focusLevel?: AccountBaseFocusLevel;
  targetSegments?: AccountBaseTargetSegmentsItem[];
}

export type Account = AccountBase & {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
} & Required<Pick<AccountBase & {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}, 'accountKind' | 'relationshipType' | 'emailConsent' | 'smsConsent'>>;

export type CrmSegmentFiltersSegmentMatch = typeof CrmSegmentFiltersSegmentMatch[keyof typeof CrmSegmentFiltersSegmentMatch];


export const CrmSegmentFiltersSegmentMatch = {
  all: 'all',
  any: 'any',
} as const;

/**
 * AND conditions over authorized accounts. Omitted fields impose no condition. Dates are inclusive UTC calendar days. Tags use the selected all/any rule. Never grants consent.
 */
export interface CrmSegmentFilters {
  /** @maxLength 200 */
  search?: string;
  audience?: CrmAudience;
  relationshipType?: AccountRelationshipType;
  accountKind?: AccountKind;
  /**
     * @maxItems 30
     * @items.minLength 1
     * @items.maxLength 100
     */
  targetSegments?: string[];
  segmentMatch?: CrmSegmentFiltersSegmentMatch;
  /**
     * @minLength 1
     * @maxLength 200
     */
  ownerId?: string;
  unassigned?: boolean;
  /**
     * @minLength 1
     * @maxLength 200
     */
  industry?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  status?: string;
  includeTestData?: boolean;
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
  lastContactedFrom?: string;
  lastContactedTo?: string;
  renewalFrom?: string;
  renewalTo?: string;
}

export interface CrmSegment {
  id: string;
  name: string;
  filters: CrmSegmentFilters;
  version: number;
  deleted: boolean;
  updatedAt: string;
  updatedBy: string;
}

export interface CrmSegmentList {
  items: CrmSegment[];
  total: number;
  limit: number;
  offset: number;
}

export type CrmSegmentChangeOperation = typeof CrmSegmentChangeOperation[keyof typeof CrmSegmentChangeOperation];


export const CrmSegmentChangeOperation = {
  create: 'create',
  update: 'update',
  delete: 'delete',
} as const;

export interface CrmSegmentChange {
  operation: CrmSegmentChangeOperation;
  id?: string;
  /** @minimum 1 */
  expectedVersion?: number;
  /**
     * @minLength 1
     * @maxLength 120
     */
  name?: string;
  filters?: CrmSegmentFilters;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
}

export interface CrmSegmentReviewRequest {
  requestId: string;
  change: CrmSegmentChange;
}

export type CrmSegmentReviewStatus = typeof CrmSegmentReviewStatus[keyof typeof CrmSegmentReviewStatus];


export const CrmSegmentReviewStatus = {
  reviewed: 'reviewed',
  committed: 'committed',
  cancelled: 'cancelled',
} as const;

export interface CrmSegmentReview {
  id: string;
  hash: string;
  expiresAt: string;
  change: CrmSegmentChange;
  before: CrmSegment | null;
  after: CrmSegment | null;
  status: CrmSegmentReviewStatus;
  receipt: CrmSegment | null;
}

export interface CrmSegmentConfirm {
  /**
     * @minLength 64
     * @maxLength 64
     */
  hash: string;
  confirmed: true;
}

export type CrmSegmentHistoryItemsItem = {
  id: string;
  operation: string;
  reason: string;
  snapshot: CrmSegment;
  createdAt: string;
  actorId: string;
};

export interface CrmSegmentHistory {
  items: CrmSegmentHistoryItemsItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface AccountList {
  items: Account[];
  /** @minimum 0 */
  total: number;
  limit?: number;
  offset?: number;
}

export interface HomeWorkspaceKpi {
  audience: CrmAudience;
  /** @minimum 0 */
  directoryTotal: number;
  /** @minimum 0 */
  openOpportunityCount: number;
  /** @minimum 0 */
  openOpportunityValue: number;
  /** @minimum 0 */
  newEnquiryCount: number;
}

export type HomeAttentionItemKind = typeof HomeAttentionItemKind[keyof typeof HomeAttentionItemKind];


export const HomeAttentionItemKind = {
  stalled_opportunity: 'stalled_opportunity',
  aging_enquiry: 'aging_enquiry',
  new_tender_opportunity: 'new_tender_opportunity',
  tender_closing_soon: 'tender_closing_soon',
} as const;

/**
 * More important of the related account and primary contact.
 */
export type HomeAttentionItemFocusLevel = typeof HomeAttentionItemFocusLevel[keyof typeof HomeAttentionItemFocusLevel];


export const HomeAttentionItemFocusLevel = {
  strategic: 'strategic',
  key: 'key',
  growth: 'growth',
  standard: 'standard',
} as const;

export interface HomeAttentionItem {
  id: string;
  kind: HomeAttentionItemKind;
  name: string;
  audience: CrmAudience;
  /** @minimum 0 */
  ageDays: number;
  deepLink: string;
  /**
     * Name of the related account, when the item has one.
     * @nullable
     */
  accountName?: string | null;
  /**
     * Full name of the related contact, when the item has one.
     * @nullable
     */
  contactName?: string | null;
  /**
     * Closing date for tender items; absent for other kinds.
     * @nullable
     */
  closesAt?: string | null;
  /** More important of the related account and primary contact. */
  focusLevel?: HomeAttentionItemFocusLevel;
  /**
     * Bounded strategic boost that orders otherwise-equally urgent items.
     * @minimum 0
     * @maximum 10
     */
  rankingBoost?: number;
  /** Why the item ranks higher, e.g. 'Strategic account' or 'Strategic brief on file'. */
  priorityReasons?: string[];
}

export type HomePipelineStageCountStage = typeof HomePipelineStageCountStage[keyof typeof HomePipelineStageCountStage];


export const HomePipelineStageCountStage = {
  lead: 'lead',
  qualified: 'qualified',
  proposal: 'proposal',
  negotiation: 'negotiation',
  won: 'won',
  lost: 'lost',
  awareness: 'awareness',
  consideration: 'consideration',
  checkout: 'checkout',
  purchased: 'purchased',
  abandoned: 'abandoned',
  identified: 'identified',
  contacted: 'contacted',
  diligence: 'diligence',
  term_sheet: 'term_sheet',
  committed: 'committed',
  passed: 'passed',
} as const;

export interface HomePipelineStageCount {
  stage: HomePipelineStageCountStage;
  /** @minimum 0 */
  count: number;
}

export type HomePipelineSnapshotKey = typeof HomePipelineSnapshotKey[keyof typeof HomePipelineSnapshotKey];


export const HomePipelineSnapshotKey = {
  b2b_sales: 'b2b_sales',
  b2c_sales: 'b2c_sales',
  partner_joint: 'partner_joint',
  investor_funding: 'investor_funding',
} as const;

export type HomePipelineSnapshotPipeline = typeof HomePipelineSnapshotPipeline[keyof typeof HomePipelineSnapshotPipeline];


export const HomePipelineSnapshotPipeline = {
  b2b_sales: 'b2b_sales',
  b2c_sales: 'b2c_sales',
  investor_funding: 'investor_funding',
} as const;

export interface HomePipelineSnapshot {
  key: HomePipelineSnapshotKey;
  audience: CrmAudience;
  pipeline: HomePipelineSnapshotPipeline;
  stages: HomePipelineStageCount[];
}

/**
 * Present only for callers who can read tender opportunities. New counts are open items the caller has not viewed.
 */
export interface HomeTenderOpportunities {
  /** @minimum 0 */
  newStrong: number;
  /** @minimum 0 */
  newPossible: number;
  /**
     * Open strong or possible matches closing within 7 days.
     * @minimum 0
     */
  closingSoon: number;
  /**
     * Open opportunities with addenda added since they were last viewed.
     * @minimum 0
     */
  newAddenda: number;
}

export interface HomeOverview {
  /** False when DATABASE_URL is absent and deterministic empty aggregates are returned */
  databaseConfigured: boolean;
  generatedAt: string;
  /**
     * @minItems 4
     * @maxItems 4
     */
  workspaces: HomeWorkspaceKpi[];
  needsAttention: HomeAttentionItem[];
  /**
     * @minItems 4
     * @maxItems 4
     */
  pipelineSnapshot: HomePipelineSnapshot[];
  tenderOpportunities?: HomeTenderOpportunities;
}

export type HomeBriefingInsightTone = typeof HomeBriefingInsightTone[keyof typeof HomeBriefingInsightTone];


export const HomeBriefingInsightTone = {
  positive: 'positive',
  warning: 'warning',
  neutral: 'neutral',
} as const;

export interface HomeBriefingInsight {
  /**
     * @minLength 1
     * @maxLength 100
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 300
     */
  detail: string;
  tone: HomeBriefingInsightTone;
  /** @pattern ^(?:/\?view=(?:b2b|b2c|partner|investor)|/(?:dashboard|next-actions|partner-engagement/(?:opportunities|enquiries|accounts))(?:/[A-Za-z0-9-]+)?(?:\?[A-Za-z0-9_=&%.-]*)?)$ */
  link?: string;
}

export type HomeBriefingFactKind = typeof HomeBriefingFactKind[keyof typeof HomeBriefingFactKind];


export const HomeBriefingFactKind = {
  count: 'count',
  currency: 'currency',
  record: 'record',
  workspace: 'workspace',
} as const;

export interface HomeBriefingFact {
  /**
     * @minLength 1
     * @maxLength 100
     */
  id: string;
  /**
     * @minLength 1
     * @maxLength 120
     */
  label: string;
  /**
     * @minLength 1
     * @maxLength 100
     */
  value: string;
  kind: HomeBriefingFactKind;
  /** @pattern ^(?:/\?view=(?:b2b|b2c|partner|investor)|/(?:dashboard|next-actions|partner-engagement/(?:opportunities|enquiries|accounts))(?:/[A-Za-z0-9-]+)?(?:\?[A-Za-z0-9_=&%.-]*)?)$ */
  href: string;
}

export type HomeBriefingSummarySegmentType = typeof HomeBriefingSummarySegmentType[keyof typeof HomeBriefingSummarySegmentType];


export const HomeBriefingSummarySegmentType = {
  text: 'text',
  link: 'link',
} as const;

export interface HomeBriefingSummarySegment {
  type: HomeBriefingSummarySegmentType;
  /** @minLength 1 */
  text: string;
  /** @pattern ^(?:/\?view=(?:b2b|b2c|partner|investor)|/(?:dashboard|next-actions|partner-engagement/(?:opportunities|enquiries|accounts))(?:/[A-Za-z0-9-]+)?(?:\?[A-Za-z0-9_=&%.-]*)?)$ */
  href?: string;
  /**
     * @minLength 1
     * @maxLength 100
     */
  factId?: string;
}

/**
 * @nullable
 */
export type HomeBriefingAudience = typeof HomeBriefingAudience[keyof typeof HomeBriefingAudience] | null;


export const HomeBriefingAudience = {
  b2b: 'b2b',
  b2c: 'b2c',
  partner: 'partner',
  investor: 'investor',
} as const;

export type HomeBriefingSource = typeof HomeBriefingSource[keyof typeof HomeBriefingSource];


export const HomeBriefingSource = {
  ai: 'ai',
  fallback: 'fallback',
} as const;

export interface HomeBriefing {
  /** @nullable */
  audience: HomeBriefingAudience;
  /**
     * @minLength 1
     * @maxLength 600
     */
  summary: string;
  facts: HomeBriefingFact[];
  /** @minItems 1 */
  segments: HomeBriefingSummarySegment[];
  /**
     * @minItems 3
     * @maxItems 5
     */
  insights: HomeBriefingInsight[];
  source: HomeBriefingSource;
  generatedAt: string;
  expiresAt: string;
  /** True when the response was served from the in-memory one-hour cache */
  cached: boolean;
}

export interface AccountOrganisationReviewInput {
  requestId: string;
  /**
     * @minLength 3
     * @maxLength 200
     */
  name: string;
  /**
     * Public HTTPS origin without credentials or query parameters
     * @minLength 1
     * @maxLength 300
     */
  website: string;
}

export type AccountOrganisationReviewField = typeof AccountOrganisationReviewField[keyof typeof AccountOrganisationReviewField];


export const AccountOrganisationReviewField = {
  industry: 'industry',
  organisationType: 'organisationType',
  size: 'size',
  description: 'description',
  phone: 'phone',
  email: 'email',
  addressLine1: 'addressLine1',
  addressLine2: 'addressLine2',
  city: 'city',
  stateProvince: 'stateProvince',
  postcode: 'postcode',
  country: 'country',
} as const;

/**
 * Website text is a literal retrieved excerpt; general_ai is unverified model knowledge
 */
export type AccountOrganisationReviewClaimOrigin = typeof AccountOrganisationReviewClaimOrigin[keyof typeof AccountOrganisationReviewClaimOrigin];


export const AccountOrganisationReviewClaimOrigin = {
  website: 'website',
  general_ai: 'general_ai',
} as const;

export interface AccountOrganisationReviewClaim {
  /**
     * @minLength 1
     * @maxLength 2000
     */
  text: string;
  /** Website text is a literal retrieved excerpt; general_ai is unverified model knowledge */
  origin: AccountOrganisationReviewClaimOrigin;
  /**
     * @maxItems 5
     * @items.pattern ^page-[1-5]$
     */
  sourceIds: string[];
}

export type AccountOrganisationReviewSuggestionOrigin = typeof AccountOrganisationReviewSuggestionOrigin[keyof typeof AccountOrganisationReviewSuggestionOrigin];


export const AccountOrganisationReviewSuggestionOrigin = {
  website: 'website',
  general_ai: 'general_ai',
} as const;

export interface AccountOrganisationReviewSuggestion {
  field: AccountOrganisationReviewField;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  value: string;
  origin: AccountOrganisationReviewSuggestionOrigin;
  /**
     * @maxItems 5
     * @items.pattern ^page-[1-5]$
     */
  sourceIds: string[];
}

export interface AccountOrganisationReviewSource {
  /** @pattern ^page-[1-5]$ */
  id: string;
  /** @maxLength 200 */
  title: string;
  /** @maxLength 2000 */
  url: string;
  /**
     * @minLength 1
     * @maxLength 6000
     */
  excerpt: string;
  retrievedAt: string;
}

export interface AccountOrganisationReviewDraft {
  /** @maxItems 24 */
  suggestions: AccountOrganisationReviewSuggestion[];
  /** @maxItems 8 */
  summary: AccountOrganisationReviewClaim[];
  /** @maxItems 12 */
  missingFields: AccountOrganisationReviewField[];
  /**
     * @maxItems 10
     * @items.minLength 1
     * @items.maxLength 500
     */
  limitations: string[];
}

export interface AccountOrganisationReviewResult {
  requestId: string;
  tenantId: string;
  entityId: string;
  actorAppUserId: string;
  /**
     * @minLength 3
     * @maxLength 200
     */
  name: string;
  /** @maxLength 300 */
  website: string;
  reviewedAt: string;
  /**
     * @minItems 1
     * @maxItems 5
     */
  sources: AccountOrganisationReviewSource[];
  /** @maxLength 1000 */
  message: string;
  /** @maxItems 24 */
  suggestions: AccountOrganisationReviewSuggestion[];
  /** @maxItems 8 */
  summary: AccountOrganisationReviewClaim[];
  /** @maxItems 12 */
  missingFields: AccountOrganisationReviewField[];
  /**
     * @maxItems 10
     * @items.minLength 1
     * @items.maxLength 500
     */
  limitations: string[];
}

export interface AccountBusinessLookupInput {
  requestId: string;
  /**
     * @minLength 3
     * @maxLength 200
     */
  name: string;
  /**
     * Public HTTPS origin only; no path, query or credentials
     * @minLength 1
     * @maxLength 300
     */
  website?: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  location?: string;
}

export type AccountBusinessLookupCandidateSuggestions = {
  /**
     * Literal untrusted public excerpt; review before applying
     * @maxLength 2000
     */
  notes: string;
  /**
     * Unverified homepage candidate, never a verified business identity
     * @maxLength 2000
     */
  website?: string;
};

export interface AccountBusinessLookupCandidate {
  id: string;
  /** @maxLength 500 */
  title: string;
  /** @maxLength 4000 */
  sourceUrl: string;
  /** @maxLength 2000 */
  excerpt: string;
  retrievedAt: string;
  suggestions: AccountBusinessLookupCandidateSuggestions;
}

export type AccountBusinessLookupResultState = typeof AccountBusinessLookupResultState[keyof typeof AccountBusinessLookupResultState];


export const AccountBusinessLookupResultState = {
  suggestions: 'suggestions',
  ambiguous: 'ambiguous',
  no_match: 'no_match',
} as const;

export interface AccountBusinessLookupResult {
  requestId: string;
  tenantId: string;
  entityId: string;
  actorAppUserId: string;
  /** @maxLength 300 */
  query: string;
  state: AccountBusinessLookupResultState;
  /** @maxLength 1000 */
  message: string;
  /**
     * @minimum 0
     * @maximum 1
     */
  creditsUsed: number;
  /** @maxItems 5 */
  candidates: AccountBusinessLookupCandidate[];
}

export type AccountCreate = AccountBase & {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
};

export type AccountUpdate = AccountBase & {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
};

/**
 * Importance rating per TargetSegment key, 1-5 (validated server-side); unset segments default to 3.
 */
export type TargetMarketConfigSegmentRatings = {[key: string]: number};

export interface TargetMarketConfig {
  /** Free-text industry names treated as target market for scoring. */
  targetIndustries: string[];
  /** Importance rating per TargetSegment key, 1-5 (validated server-side); unset segments default to 3. */
  segmentRatings: TargetMarketConfigSegmentRatings;
}

export type AccountRecommendationRecommendedTemperature = typeof AccountRecommendationRecommendedTemperature[keyof typeof AccountRecommendationRecommendedTemperature];


export const AccountRecommendationRecommendedTemperature = {
  hot: 'hot',
  warm: 'warm',
  cool: 'cool',
  cold: 'cold',
  new: 'new',
} as const;

export type AccountRecommendationRecommendedFocus = typeof AccountRecommendationRecommendedFocus[keyof typeof AccountRecommendationRecommendedFocus];


export const AccountRecommendationRecommendedFocus = {
  strategic: 'strategic',
  key: 'key',
  growth: 'growth',
  standard: 'standard',
} as const;

export interface AccountRecommendation {
  accountId: string;
  companyName: string;
  currentTemperature?: string;
  currentFocus?: string;
  recommendedTemperature: AccountRecommendationRecommendedTemperature;
  recommendedFocus: AccountRecommendationRecommendedFocus;
  /**
     * @minimum 0
     * @maximum 100
     */
  score: number;
  reasons: string[];
  contactCount?: number;
  recentActivityCount?: number;
  openOpportunityCount?: number;
  wonOpportunityCount?: number;
}

export interface AccountRecommendationList {
  recommendations: AccountRecommendation[];
}

export type ApplyRecommendationsBodyField = typeof ApplyRecommendationsBodyField[keyof typeof ApplyRecommendationsBodyField];


export const ApplyRecommendationsBodyField = {
  temperature: 'temperature',
  focus: 'focus',
  both: 'both',
} as const;

export interface ApplyRecommendationsBody {
  /** @minItems 1 */
  accountIds: string[];
  field: ApplyRecommendationsBodyField;
}

export interface ApplyRecommendationsResult {
  /** @minimum 0 */
  updated: number;
}

export type StrategicBriefSourceRecordType = typeof StrategicBriefSourceRecordType[keyof typeof StrategicBriefSourceRecordType];


export const StrategicBriefSourceRecordType = {
  account: 'account',
  contact: 'contact',
} as const;

export type StrategicBriefSourceImportance = typeof StrategicBriefSourceImportance[keyof typeof StrategicBriefSourceImportance];


export const StrategicBriefSourceImportance = {
  strategic: 'strategic',
  key: 'key',
  growth: 'growth',
  standard: 'standard',
} as const;

/**
 * A strategic brief (and importance level) the AI was given as grounding. Only briefs the caller can read are returned.
 */
export interface StrategicBriefSource {
  briefId: string;
  documentId: string;
  recordType: StrategicBriefSourceRecordType;
  recordId: string;
  recordName: string;
  importance: StrategicBriefSourceImportance;
  /** Short plain-text start of the brief as given to the AI */
  excerpt: string;
  /** True when only part of the brief fitted the AI size limit */
  truncated: boolean;
  /** @nullable */
  updatedAt: string | null;
  /** Record page with the Strategic brief tab open */
  deepLink: string;
}

export interface AccountBrief {
  brief: string;
  /** Strategic briefs the AI was given for this account brief */
  strategicBriefs: StrategicBriefSource[];
}

export type ScoreEvidenceChannels = {[key: string]: number};

export interface ScoreEvidence {
  /** @minimum 0 */
  interactionCount: number;
  channels: ScoreEvidenceChannels;
  /** @minimum 0 */
  confirmedSignalCount: number;
  signalTypes: string[];
  sourceUrls: string[];
  targetIndustry: boolean;
  targetSegments: string[];
  /**
     * @minimum 0
     * @maximum 100
     * @nullable
     */
  productFit: number | null;
  /**
     * @minimum 0
     * @maximum 100
     * @nullable
     */
  progressiveness: number | null;
}

export type RankedProspectWeights = {
  fit: number;
  engagement: number;
  signals: number;
};

export interface RankedProspect {
  accountId: string;
  companyName: string;
  /** @nullable */
  industry?: string | null;
  /**
     * @minimum 0
     * @maximum 100
     */
  score: number;
  /**
     * @minimum 0
     * @maximum 100
     */
  fitScore: number;
  /**
     * @minimum 0
     * @maximum 100
     */
  engagementScore: number;
  /**
     * @minimum 0
     * @maximum 100
     */
  signalScore: number;
  weights: RankedProspectWeights;
  evidence: ScoreEvidence;
  calculatedAt: string;
}

export interface RankedProspectList {
  items: RankedProspect[];
  /** @minimum 0 */
  total: number;
}

export interface OutcomeReason {
  id: string;
  label: string;
}

export interface OpportunityOutcomeTaxonomy {
  won: OutcomeReason[];
  lost: OutcomeReason[];
}

export type OpportunityOutcomeCreateOutcome = typeof OpportunityOutcomeCreateOutcome[keyof typeof OpportunityOutcomeCreateOutcome];


export const OpportunityOutcomeCreateOutcome = {
  won: 'won',
  lost: 'lost',
} as const;

export interface OpportunityOutcomeCreate {
  outcome: OpportunityOutcomeCreateOutcome;
  /**
     * @minLength 1
     * @maxLength 100
     */
  reasonCode: string;
  /**
     * @maxLength 200
     * @nullable
     */
  competitor?: string | null;
  /**
     * @maxLength 5000
     * @nullable
     */
  notes?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  capturedBy?: string | null;
}

export type OpportunityOutcomeCommandOutcome = typeof OpportunityOutcomeCommandOutcome[keyof typeof OpportunityOutcomeCommandOutcome];


export const OpportunityOutcomeCommandOutcome = {
  won: 'won',
  lost: 'lost',
} as const;

export interface OpportunityOutcomeCommand {
  requestId: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  expectedStageId: string;
  expectedUpdatedAt: string;
  outcome: OpportunityOutcomeCommandOutcome;
  /**
     * @minLength 1
     * @maxLength 100
     */
  reasonCode: string;
  /**
     * @maxLength 200
     * @nullable
     */
  competitor?: string | null;
  /**
     * @maxLength 5000
     * @nullable
     */
  notes?: string | null;
}

export type OpportunityOutcome = OpportunityOutcomeCreate & ({
  id: string;
  opportunityId: string;
  opportunityTitle: string;
  /** @nullable */
  accountId: string | null;
  /** @nullable */
  companyName: string | null;
  /** @nullable */
  segment?: string | null;
  closedAt: string;
});

export interface MarketTrend {
  label: string;
  insight: string;
  evidence: string[];
  sourceUrls: string[];
}

export type WinLossPatternOutcome = typeof WinLossPatternOutcome[keyof typeof WinLossPatternOutcome];


export const WinLossPatternOutcome = {
  won: 'won',
  lost: 'lost',
  mixed: 'mixed',
} as const;

export interface WinLossPattern {
  pattern: string;
  outcome: WinLossPatternOutcome;
  /** @minimum 0 */
  count: number;
  rationale: string;
}

export type MarketSourceKind = typeof MarketSourceKind[keyof typeof MarketSourceKind];


export const MarketSourceKind = {
  signal: 'signal',
  opportunity: 'opportunity',
} as const;

export interface MarketSource {
  label: string;
  url: string;
  kind: MarketSourceKind;
  occurredAt: string;
}

export interface MarketAnalysis {
  id: string;
  lookbackDays: number;
  summary: string;
  trends: MarketTrend[];
  winLossPatterns: WinLossPattern[];
  sources: MarketSource[];
  generatedAt: string;
}

export type NextBestActionActionType = typeof NextBestActionActionType[keyof typeof NextBestActionActionType];


export const NextBestActionActionType = {
  email: 'email',
  social: 'social',
  call: 'call',
  review_signal: 'review_signal',
  create_opportunity: 'create_opportunity',
} as const;

export type NextBestActionStatus = typeof NextBestActionStatus[keyof typeof NextBestActionStatus];


export const NextBestActionStatus = {
  open: 'open',
  processing: 'processing',
  completed: 'completed',
  dismissed: 'dismissed',
} as const;

export interface NextBestAction {
  id: string;
  accountId: string;
  companyName: string;
  actionType: NextBestActionActionType;
  title: string;
  rationale: string;
  deepLink: string;
  status: NextBestActionStatus;
  /**
     * @minimum 0
     * @maximum 100
     */
  scoreSnapshot: number;
  createdAt: string;
}

export type RelationshipHealthEvidenceState = typeof RelationshipHealthEvidenceState[keyof typeof RelationshipHealthEvidenceState];


export const RelationshipHealthEvidenceState = {
  insufficient: 'insufficient',
  fresh: 'fresh',
  stale: 'stale',
  unavailable: 'unavailable',
} as const;

export type RelationshipHealthEvidenceFreshness = typeof RelationshipHealthEvidenceFreshness[keyof typeof RelationshipHealthEvidenceFreshness];


export const RelationshipHealthEvidenceFreshness = {
  fresh: 'fresh',
  aging: 'aging',
  stale: 'stale',
  unknown: 'unknown',
} as const;

export type RelationshipHealthTrend = typeof RelationshipHealthTrend[keyof typeof RelationshipHealthTrend];


export const RelationshipHealthTrend = {
  improving: 'improving',
  stable: 'stable',
  declining: 'declining',
  unknown: 'unknown',
} as const;

export type RelationshipHealthEvidenceDetail = { [key: string]: unknown };

export interface RelationshipHealthEvidence {
  id: string;
  /** @nullable */
  interactionId?: string | null;
  /** @nullable */
  signalId?: string | null;
  /** @nullable */
  nextActionId?: string | null;
  /** @nullable */
  auditId?: string | null;
  /**
     * @minLength 1
     * @maxLength 80
     */
  sourceKind: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  sourceRef: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  factorKey: string;
  freshness: RelationshipHealthEvidenceFreshness;
  /**
     * @minLength 1
     * @maxLength 1000
     * @nullable
     */
  sourcePath?: string | null;
  /**
     * @minLength 1
     * @maxLength 300
     */
  label: string;
  /**
     * @minimum -100
     * @maximum 100
     */
  impact: number;
  observedAt: string;
  detail: RelationshipHealthEvidenceDetail;
}

export interface RelationshipHealthSnapshot {
  id: string;
  accountId: string;
  accountName: string;
  accountKind: AccountKind;
  relationshipType: AccountRelationshipType;
  /** @minimum 1 */
  version: number;
  evidenceState: RelationshipHealthEvidenceState;
  /**
     * @minimum 0
     * @maximum 100
     * @nullable
     */
  score?: number | null;
  trend: RelationshipHealthTrend;
  /**
     * @minLength 1
     * @maxLength 100
     */
  rulesVersion: string;
  /**
     * @minimum 1
     * @maximum 3650
     */
  expectedContactDays: number;
  /**
     * @minimum 1
     * @maximum 3650
     */
  staleAfterDays: number;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  summary: string;
  evaluatedAt: string;
  /** @nullable */
  evidenceThrough?: string | null;
  expiresAt: string;
  evidence: RelationshipHealthEvidence[];
}

export interface RelationshipHealthSnapshotList {
  items: RelationshipHealthSnapshot[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface RelationshipHealthRuleExpectation {
  relationshipType: AccountRelationshipType;
  /**
     * @minimum 1
     * @maximum 3650
     */
  expectedContactDays: number;
  /**
     * @minimum 1
     * @maximum 3650
     */
  staleAfterDays: number;
}

export interface RelationshipHealthRuleFactor {
  /**
     * @minLength 1
     * @maxLength 80
     */
  key: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  label: string;
  /**
     * @minimum 0
     * @maximum 100
     */
  maximumImpact: number;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  description: string;
}

export interface RelationshipHealthRuleSet {
  /**
     * @minLength 1
     * @maxLength 100
     */
  version: string;
  /**
     * @minimum 1
     * @maximum 3650
     */
  lookbackDays: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  minimumMeaningfulEvents: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  trendThreshold: number;
  /** @minItems 1 */
  expectations: RelationshipHealthRuleExpectation[];
  /** @minItems 1 */
  factors: RelationshipHealthRuleFactor[];
  /**
     * @minItems 1
     * @items.minLength 1
     * @items.maxLength 500
     */
  cautions: string[];
}

export type OutreachPlanStatus = typeof OutreachPlanStatus[keyof typeof OutreachPlanStatus];


export const OutreachPlanStatus = {
  draft: 'draft',
  pending_review: 'pending_review',
  active: 'active',
  paused: 'paused',
  suspended: 'suspended',
  revoked: 'revoked',
  expired: 'expired',
} as const;

export type OutreachRecipientRulesMode = typeof OutreachRecipientRulesMode[keyof typeof OutreachRecipientRulesMode];


export const OutreachRecipientRulesMode = {
  explicit: 'explicit',
  eligibility_rules: 'eligibility_rules',
} as const;

export interface OutreachRecipientRules {
  mode: OutreachRecipientRulesMode;
  /** @maxItems 10000 */
  accountIds?: string[];
  /**
     * @maxItems 50
     * @items.minLength 1
     * @items.maxLength 80
     */
  relationshipTypes?: string[];
  audience?: CrmAudience;
}

export type OutreachChannelBoundChannel = typeof OutreachChannelBoundChannel[keyof typeof OutreachChannelBoundChannel];


export const OutreachChannelBoundChannel = {
  email: 'email',
  sms: 'sms',
  public_social: 'public_social',
  private_social: 'private_social',
  call: 'call',
} as const;

/**
 * @nullable
 */
export type OutreachChannelBoundCallMode = typeof OutreachChannelBoundCallMode[keyof typeof OutreachChannelBoundCallMode] | null;


export const OutreachChannelBoundCallMode = {
  ai_led: 'ai_led',
  staff_led: 'staff_led',
} as const;

export interface OutreachChannelBound {
  channel: OutreachChannelBoundChannel;
  /**
     * @minLength 1
     * @maxLength 200
     */
  senderKey: string;
  /** @nullable */
  callMode?: OutreachChannelBoundCallMode;
}

export interface OutreachPermittedInformation {
  /**
     * @maxItems 100
     * @items.minLength 1
     * @items.maxLength 120
     */
  crmFields: string[];
  /**
     * @maxItems 100
     * @items.minLength 1
     * @items.maxLength 200
     */
  knowledgeSourceIds: string[];
  publicSafeOnly: boolean;
}

export interface OutreachContactWindows {
  /**
     * @minLength 1
     * @maxLength 100
     */
  timezoneFallback: string;
  /**
     * @minItems 1
     * @maxItems 7
     * @items.minimum 1
     * @items.maximum 7
     */
  weekdays: number[];
  /** @pattern ^([01]\d|2[0-3]):[0-5]\d$ */
  startLocal: string;
  /** @pattern ^([01]\d|2[0-3]):[0-5]\d$ */
  endLocal: string;
}

export interface OutreachFrequencyLimits {
  /**
     * @minimum 1
     * @maximum 100
     */
  maxContactsPerRecipient: number;
  /**
     * @minimum 1
     * @maximum 365
     */
  periodDays: number;
  /**
     * @minimum 1
     * @maximum 8760
     */
  minimumHoursBetween: number;
}

export interface OutreachSpendingLimits {
  /** @pattern ^[A-Z]{3}$ */
  currency: string;
  /** @minimum 0 */
  maximumTotalMinor: number;
  /** @minimum 0 */
  maximumPerRecipientMinor: number;
}

export interface OutreachReplyLimits {
  /**
     * @minimum 0
     * @maximum 20
     */
  maximumAutomatedTurns: number;
  stopOnHumanRequest: boolean;
  stopOnObjection: boolean;
}

export interface OutreachPlanRevisionInput {
  /**
     * @minLength 1
     * @maxLength 1000
     */
  goal: string;
  recipientRules: OutreachRecipientRules;
  /**
     * @minItems 1
     * @maxItems 20
     */
  channels: OutreachChannelBound[];
  permittedInformation: OutreachPermittedInformation;
  contactWindows: OutreachContactWindows;
  frequencyLimits: OutreachFrequencyLimits;
  spendingLimits: OutreachSpendingLimits;
  replyLimits: OutreachReplyLimits;
  startsAt: string;
  expiresAt: string;
  escalationOwnerAppUserId: string;
}

export interface CreateOutreachPlanRequest {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  revision: OutreachPlanRevisionInput;
}

export interface ReviseOutreachPlanRequest {
  /** @minimum 1 */
  expectedRevision: number;
  revision: OutreachPlanRevisionInput;
}

export interface SubmitOutreachPlanReviewRequest {
  /** @minimum 1 */
  expectedRevision: number;
  requestId: string;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  note?: string;
  /** @maxItems 100 */
  healthSnapshotIds: string[];
  /** @maxItems 100 */
  nextActionIds: string[];
}

export interface ActivateOutreachPlanRequest {
  /** @minimum 1 */
  expectedRevision: number;
  reviewAuditId: string;
}

export interface OutreachPlanLifecycleRequest {
  /** @minimum 1 */
  expectedRevision: number;
  expectedStatus: OutreachPlanStatus;
  /**
     * @minLength 3
     * @maxLength 1000
     */
  reason: string;
}

export interface OutreachPlanRevision {
  id: string;
  planId: string;
  /** @minimum 1 */
  revision: number;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  goal: string;
  recipientRules: OutreachRecipientRules;
  /**
     * @minItems 1
     * @maxItems 20
     */
  channels: OutreachChannelBound[];
  permittedInformation: OutreachPermittedInformation;
  contactWindows: OutreachContactWindows;
  frequencyLimits: OutreachFrequencyLimits;
  spendingLimits: OutreachSpendingLimits;
  replyLimits: OutreachReplyLimits;
  startsAt: string;
  expiresAt: string;
  escalationOwnerAppUserId: string;
  createdByAppUserId: string;
  createdAt: string;
}

export type OutreachPlanApprovalStatus = typeof OutreachPlanApprovalStatus[keyof typeof OutreachPlanApprovalStatus];


export const OutreachPlanApprovalStatus = {
  approved: 'approved',
  revoked: 'revoked',
  stale: 'stale',
} as const;

export interface OutreachPlanApproval {
  id: string;
  planRevisionId: string;
  /** @minimum 1 */
  revision: number;
  auditId: string;
  approvedByAppUserId: string;
  status: OutreachPlanApprovalStatus;
  /** @nullable */
  reason?: string | null;
  approvedAt: string;
  /** @nullable */
  terminalAt?: string | null;
}

export interface OutreachPlanSummary {
  id: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  status: OutreachPlanStatus;
  /** @minimum 1 */
  revision: number;
  createdByAppUserId: string;
  /** @nullable */
  pausedAt?: string | null;
  /** @nullable */
  suspendedAt?: string | null;
  /** @nullable */
  revokedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type OutreachPlanDetail = OutreachPlanSummary & {
  revisions: OutreachPlanRevision[];
  approvals: OutreachPlanApproval[];
  /** @minimum 0 */
  memberCount: number;
};

export interface OutreachPlanList {
  items: OutreachPlanSummary[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface OutreachPlanPreviewIssue {
  /**
     * @minLength 1
     * @maxLength 100
     */
  code: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  path: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  message: string;
}

export type OutreachPlanAuthorityReferenceLevel = typeof OutreachPlanAuthorityReferenceLevel[keyof typeof OutreachPlanAuthorityReferenceLevel];


export const OutreachPlanAuthorityReferenceLevel = {
  automated: 'automated',
  recommended: 'recommended',
  prepared_with_approval: 'prepared_with_approval',
  human_only: 'human_only',
} as const;

export interface OutreachPlanAuthorityReference {
  /**
     * @minLength 1
     * @maxLength 100
     */
  actionType: string;
  level: OutreachPlanAuthorityReferenceLevel;
}

export type OutreachPlanPreviewApprovalKind = typeof OutreachPlanPreviewApprovalKind[keyof typeof OutreachPlanPreviewApprovalKind];


export const OutreachPlanPreviewApprovalKind = {
  bounded_plan: 'bounded_plan',
} as const;

export interface OutreachPlanPreview {
  planId: string;
  planRevisionId: string;
  /** @minimum 1 */
  revision: number;
  status: OutreachPlanStatus;
  approvalKind: OutreachPlanPreviewApprovalKind;
  doesNotApproveIndividualMessages: boolean;
  canSubmitReview: boolean;
  canActivate: boolean;
  issues: OutreachPlanPreviewIssue[];
  /** @minimum 1 */
  policyVersion: number;
  /** @minItems 1 */
  authority: OutreachPlanAuthorityReference[];
  /** @nullable */
  reviewAuditId?: string | null;
}

export type OutreachPlanHistoryEventEvent = typeof OutreachPlanHistoryEventEvent[keyof typeof OutreachPlanHistoryEventEvent];


export const OutreachPlanHistoryEventEvent = {
  review_submitted: 'review_submitted',
  activated: 'activated',
  paused: 'paused',
  resumed: 'resumed',
  revoked: 'revoked',
  expired: 'expired',
} as const;

export type OutreachPlanHistoryEventStatus = typeof OutreachPlanHistoryEventStatus[keyof typeof OutreachPlanHistoryEventStatus];


export const OutreachPlanHistoryEventStatus = {
  executed: 'executed',
  pending_approval: 'pending_approval',
  approved: 'approved',
  rejected: 'rejected',
  deferred: 'deferred',
  dismissed: 'dismissed',
  blocked: 'blocked',
  failed: 'failed',
} as const;

export type OutreachPlanHistoryEventAuthorityLevel = typeof OutreachPlanHistoryEventAuthorityLevel[keyof typeof OutreachPlanHistoryEventAuthorityLevel];


export const OutreachPlanHistoryEventAuthorityLevel = {
  automated: 'automated',
  recommended: 'recommended',
  prepared_with_approval: 'prepared_with_approval',
  human_only: 'human_only',
} as const;

export interface OutreachPlanHistoryEvent {
  auditId: string;
  event: OutreachPlanHistoryEventEvent;
  /** @nullable */
  planRevisionId?: string | null;
  /** @minimum 1 */
  revision: number;
  status: OutreachPlanHistoryEventStatus;
  authorityLevel: OutreachPlanHistoryEventAuthorityLevel;
  /** @nullable */
  requestedBy: string | null;
  /** @nullable */
  approvedBy?: string | null;
  /** @nullable */
  reason?: string | null;
  /** @nullable */
  approvalId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OutreachPlanHistoryList {
  items: OutreachPlanHistoryEvent[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type OutreachExecutionStatus = typeof OutreachExecutionStatus[keyof typeof OutreachExecutionStatus];


export const OutreachExecutionStatus = {
  scheduled: 'scheduled',
  claimed: 'claimed',
  blocked: 'blocked',
  provider_accepted: 'provider_accepted',
  delivered: 'delivered',
  completed: 'completed',
  failed: 'failed',
  unknown: 'unknown',
  manual_handoff: 'manual_handoff',
  cancelled: 'cancelled',
} as const;

export type ScheduleOutreachExecutionRequestChannel = typeof ScheduleOutreachExecutionRequestChannel[keyof typeof ScheduleOutreachExecutionRequestChannel];


export const ScheduleOutreachExecutionRequestChannel = {
  email: 'email',
  sms: 'sms',
  public_social: 'public_social',
  private_social: 'private_social',
  call: 'call',
} as const;

export interface ScheduleOutreachExecutionRequest {
  /** @minimum 1 */
  expectedRevision: number;
  /** Stable idempotency key reused for provider dispatch when supported */
  requestId: string;
  accountId: string;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  nextActionId?: string | null;
  /**
     * Canonical approved Content Hub social post used by public social outreach
     * @nullable
     */
  socialPostId?: string | null;
  /**
     * Exact separately approved canonical social post version
     * @minimum 1
     * @nullable
     */
  socialPostVersion?: number | null;
  /**
     * Exact canonical inbound social conversation used for an eligible private reply
     * @nullable
     */
  socialInboxItemId?: string | null;
  channel: ScheduleOutreachExecutionRequestChannel;
  /**
     * @minLength 1
     * @maxLength 200
     */
  senderKey: string;
  dueAt: string;
  /**
     * @minimum 0
     * @maximum 1000000000
     */
  estimatedCostMinor: number;
}

export type OutreachExecutionChannel = typeof OutreachExecutionChannel[keyof typeof OutreachExecutionChannel];


export const OutreachExecutionChannel = {
  email: 'email',
  sms: 'sms',
  public_social: 'public_social',
  private_social: 'private_social',
  call: 'call',
} as const;

export interface OutreachExecution {
  id: string;
  planId: string;
  planRevisionId: string;
  memberId: string;
  /** @nullable */
  accountId: string | null;
  /** @nullable */
  contactId: string | null;
  /** @nullable */
  nextActionId: string | null;
  /** @nullable */
  socialPostId: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  socialPostVersion: number | null;
  /** @nullable */
  socialInboxItemId: string | null;
  /** @nullable */
  replyStateId: string | null;
  /** @nullable */
  auditId: string | null;
  channel: OutreachExecutionChannel;
  /**
     * @minLength 1
     * @maxLength 200
     */
  senderKey: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  recipientKey: string;
  /**
     * @minLength 1
     * @maxLength 100
     */
  recipientTimezone: string;
  deduplicationKey: string;
  status: OutreachExecutionStatus;
  /** @minimum 0 */
  attemptCount: number;
  dueAt: string;
  /** @nullable */
  nextAttemptAt: string | null;
  /** @pattern ^[A-Z]{3}$ */
  currency: string;
  /** @minimum 0 */
  estimatedCostMinor: number;
  /**
     * @minimum 0
     * @nullable
     */
  actualCostMinor: number | null;
  /** @nullable */
  leaseExpiresAt: string | null;
  /** @nullable */
  providerActionId: string | null;
  /** @nullable */
  providerAcceptedAt: string | null;
  /** @nullable */
  deliveredAt: string | null;
  /** @nullable */
  blockedReason: string | null;
  /** @nullable */
  blockedDetail: string | null;
  /** @nullable */
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OutreachExecutionList {
  items: OutreachExecution[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type OutreachReplyStateStatus = typeof OutreachReplyStateStatus[keyof typeof OutreachReplyStateStatus];


export const OutreachReplyStateStatus = {
  active: 'active',
  handoff: 'handoff',
  staff_owned: 'staff_owned',
  stopped: 'stopped',
  completed: 'completed',
  quarantined: 'quarantined',
} as const;

export type OutreachReplyClassification = typeof OutreachReplyClassification[keyof typeof OutreachReplyClassification];


export const OutreachReplyClassification = {
  routine: 'routine',
  opt_out: 'opt_out',
  human_request: 'human_request',
  sensitive: 'sensitive',
  out_of_scope: 'out_of_scope',
  uncertain: 'uncertain',
  voice_handoff: 'voice_handoff',
} as const;

export type OutreachReplyStateChannel = typeof OutreachReplyStateChannel[keyof typeof OutreachReplyStateChannel];


export const OutreachReplyStateChannel = {
  email: 'email',
  sms: 'sms',
  private_social: 'private_social',
  call: 'call',
} as const;

export interface OutreachReplyState {
  id: string;
  planId: string;
  planRevisionId: string;
  memberId: string;
  accountId: string;
  /** @nullable */
  contactId: string | null;
  channel: OutreachReplyStateChannel;
  status: OutreachReplyStateStatus;
  /** @minimum 0 */
  automatedTurnCount: number;
  lastClassification: OutreachReplyClassification | null;
  /** @nullable */
  handoffReason: string | null;
  /** @nullable */
  handoffBrief: string | null;
  /** @nullable */
  suggestedResponse: string | null;
  /** @nullable */
  staffActionId: string | null;
  /** @nullable */
  ownedByAppUserId: string | null;
  /** @nullable */
  claimedAt: string | null;
  /** @nullable */
  lastInboundAt: string | null;
  /** @nullable */
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OutreachReplyStateList {
  items: OutreachReplyState[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type RelationshipHealthFeedbackType = typeof RelationshipHealthFeedbackType[keyof typeof RelationshipHealthFeedbackType];


export const RelationshipHealthFeedbackType = {
  approval: 'approval',
  edit: 'edit',
  rejection: 'rejection',
  deferral: 'deferral',
  handoff: 'handoff',
  delivery: 'delivery',
  reply: 'reply',
  commitment: 'commitment',
  relationship_outcome: 'relationship_outcome',
} as const;

export type RelationshipHealthFeedbackOutcome = typeof RelationshipHealthFeedbackOutcome[keyof typeof RelationshipHealthFeedbackOutcome];


export const RelationshipHealthFeedbackOutcome = {
  accepted: 'accepted',
  edited: 'edited',
  rejected: 'rejected',
  deferred: 'deferred',
  staff_handoff: 'staff_handoff',
  delivered: 'delivered',
  failed: 'failed',
  unknown: 'unknown',
  meaningful_reply: 'meaningful_reply',
  no_reply: 'no_reply',
  opt_out: 'opt_out',
  commitment_completed: 'commitment_completed',
  relationship_improved: 'relationship_improved',
  relationship_stable: 'relationship_stable',
  relationship_declined: 'relationship_declined',
  missing_evidence: 'missing_evidence',
} as const;

export type RelationshipHealthEditSignal = typeof RelationshipHealthEditSignal[keyof typeof RelationshipHealthEditSignal];


export const RelationshipHealthEditSignal = {
  shorter: 'shorter',
  longer: 'longer',
  warmer: 'warmer',
  more_direct: 'more_direct',
  question_led: 'question_led',
  less_frequent: 'less_frequent',
  later: 'later',
  earlier: 'earlier',
} as const;

export type RelationshipHealthFeedbackDetail = { [key: string]: unknown };

export interface RelationshipHealthFeedback {
  id: string;
  /** @nullable */
  snapshotId: string | null;
  /** @nullable */
  planId: string | null;
  /** @nullable */
  planRevisionId: string | null;
  /** @nullable */
  executionId: string | null;
  /** @nullable */
  nextActionId: string | null;
  /** @nullable */
  auditId: string | null;
  feedbackType: RelationshipHealthFeedbackType;
  outcome: RelationshipHealthFeedbackOutcome;
  detail: RelationshipHealthFeedbackDetail;
  /** @nullable */
  recordedByAppUserId: string | null;
  createdAt: string;
}

export interface RelationshipHealthFeedbackList {
  items: RelationshipHealthFeedback[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type RecordRelationshipHealthFeedbackRequestDetail = {
  /**
     * @minLength 1
     * @maxLength 1000
     */
  note?: string;
  /** @maxItems 8 */
  editSignals?: RelationshipHealthEditSignal[];
};

export type RecordRelationshipHealthFeedbackRequest = (unknown & {
  requestId: string;
  feedbackType: RelationshipHealthFeedbackType;
  outcome: RelationshipHealthFeedbackOutcome;
  snapshotId?: string;
  planId?: string;
  planRevisionId?: string;
  executionId?: string;
  nextActionId?: string;
  auditId?: string;
  detail?: RecordRelationshipHealthFeedbackRequestDetail;
});

export type RelationshipHealthLearningConfigurationContentStyle = typeof RelationshipHealthLearningConfigurationContentStyle[keyof typeof RelationshipHealthLearningConfigurationContentStyle];


export const RelationshipHealthLearningConfigurationContentStyle = {
  balanced: 'balanced',
  concise: 'concise',
  question_led: 'question_led',
} as const;

export interface RelationshipHealthLearningConfiguration {
  /**
     * @minimum -8
     * @maximum 8
     */
  priorityOffset: number;
  /**
     * @minimum -24
     * @maximum 48
     */
  timingOffsetHours: number;
  contentStyle: RelationshipHealthLearningConfigurationContentStyle;
}

export type RelationshipHealthLearningEvidenceCounts = {[key: string]: number};

export interface RelationshipHealthLearningEvidence {
  windowStart: string;
  windowEnd: string;
  feedbackIds: string[];
  nextActionIds: string[];
  planRevisionIds: string[];
  executionIds: string[];
  snapshotIds: string[];
  counts: RelationshipHealthLearningEvidenceCounts;
}

export type RelationshipHealthLearningVersionStatus = typeof RelationshipHealthLearningVersionStatus[keyof typeof RelationshipHealthLearningVersionStatus];


export const RelationshipHealthLearningVersionStatus = {
  active: 'active',
  superseded: 'superseded',
} as const;

export interface RelationshipHealthLearningVersion {
  id: string;
  /** @minimum 1 */
  version: number;
  status: RelationshipHealthLearningVersionStatus;
  /** @nullable */
  basedOnVersionId: string | null;
  configuration: RelationshipHealthLearningConfiguration;
  evidence: RelationshipHealthLearningEvidence;
  /** @minItems 1 */
  explanation: string[];
  correlationNotice: string;
  policyBoundary: string;
  createdByAppUserId: string;
  activatedAt: string;
  createdAt: string;
}

export interface RelationshipHealthLearningVersionList {
  items: RelationshipHealthLearningVersion[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface RelationshipHealthCountMap {[key: string]: number}

export type RelationshipHealthAutomationReportDelivery = {
  statuses: RelationshipHealthCountMap;
  channels: RelationshipHealthCountMap;
  /** @minimum 0 */
  estimatedCostMinor: number;
  /** @minimum 0 */
  actualCostMinor: number;
};

export type RelationshipHealthAutomationReportEngagementEvidenceState = typeof RelationshipHealthAutomationReportEngagementEvidenceState[keyof typeof RelationshipHealthAutomationReportEngagementEvidenceState];


export const RelationshipHealthAutomationReportEngagementEvidenceState = {
  observed: 'observed',
  missing: 'missing',
} as const;

export type RelationshipHealthAutomationReportEngagement = {
  classifications: RelationshipHealthCountMap;
  /** @minimum 0 */
  meaningfulReplies: number;
  /** @minimum 0 */
  optOuts: number;
  /** @minimum 0 */
  humanRequests: number;
  /** @minimum 0 */
  automatedTurns: number;
  evidenceState: RelationshipHealthAutomationReportEngagementEvidenceState;
};

export type RelationshipHealthAutomationReportRelationshipOutcomes = {
  trends: RelationshipHealthCountMap;
  evidenceStates: RelationshipHealthCountMap;
  /** @minimum 0 */
  missingEvidence: number;
  caution: string;
};

export type RelationshipHealthAutomationReportOperations = {
  planStatuses: RelationshipHealthCountMap;
  replyStatuses: RelationshipHealthCountMap;
  /** @minimum 0 */
  blocked: number;
  /** @minimum 0 */
  failed: number;
  /** @minimum 0 */
  unknown: number;
  /** @minimum 0 */
  handoffs: number;
};

export type RelationshipHealthAutomationReportFeedback = {
  types: RelationshipHealthCountMap;
  outcomes: RelationshipHealthCountMap;
};

export interface RelationshipHealthAutomationReport {
  from: string;
  to: string;
  generatedAt: string;
  delivery: RelationshipHealthAutomationReportDelivery;
  engagement: RelationshipHealthAutomationReportEngagement;
  relationshipOutcomes: RelationshipHealthAutomationReportRelationshipOutcomes;
  operations: RelationshipHealthAutomationReportOperations;
  feedback: RelationshipHealthAutomationReportFeedback;
  learning: RelationshipHealthLearningVersion | null;
  /** @minItems 1 */
  limitations: string[];
}

export type ResolveOutreachReplyStateRequestResolution = typeof ResolveOutreachReplyStateRequestResolution[keyof typeof ResolveOutreachReplyStateRequestResolution];


export const ResolveOutreachReplyStateRequestResolution = {
  resume_automation: 'resume_automation',
  close_conversation: 'close_conversation',
} as const;

export interface ResolveOutreachReplyStateRequest {
  expectedUpdatedAt: string;
  resolution: ResolveOutreachReplyStateRequestResolution;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  reason: string;
}

/**
 * Prepared email draft, used only after explicit human review
 * @nullable
 */
export type NextActionDraft = {
  to: string;
  subject: string;
  body: string;
  /** Canonical email message to reply to in the same provider thread */
  replyToMessageId?: string;
} | null;

export type NextActionRecommendationEvidenceFreshness = typeof NextActionRecommendationEvidenceFreshness[keyof typeof NextActionRecommendationEvidenceFreshness];


export const NextActionRecommendationEvidenceFreshness = {
  fresh: 'fresh',
  aging: 'aging',
  stale: 'stale',
  unknown: 'unknown',
} as const;

export interface NextActionRecommendationEvidence {
  sourceKind: string;
  sourceRef: string;
  label: string;
  observedAt: string;
  freshness: NextActionRecommendationEvidenceFreshness;
  /** @nullable */
  sourcePath: string | null;
}

export interface NextActionKnowledgeReference {
  knowledgeFileId: string;
  documentId: string;
  title: string;
  location: string;
  /** @minimum 1 */
  sourceVersion: number;
  freshness: string;
  /**
     * @minimum 0
     * @maximum 1
     */
  score: number;
}

export type NextActionMessageContentKind = typeof NextActionMessageContentKind[keyof typeof NextActionMessageContentKind];


export const NextActionMessageContentKind = {
  message: 'message',
} as const;

export interface NextActionMessageContent {
  kind: NextActionMessageContentKind;
  subject: string;
  body: string;
}

export type NextActionCallBriefKind = typeof NextActionCallBriefKind[keyof typeof NextActionCallBriefKind];


export const NextActionCallBriefKind = {
  call_brief: 'call_brief',
} as const;

export interface NextActionCallBrief {
  kind: NextActionCallBriefKind;
  opening: string;
  talkingPoints: string[];
  questions: string[];
  desiredOutcome: string;
}

export type NextActionRecommendationLearningContentStyle = typeof NextActionRecommendationLearningContentStyle[keyof typeof NextActionRecommendationLearningContentStyle];


export const NextActionRecommendationLearningContentStyle = {
  balanced: 'balanced',
  concise: 'concise',
  question_led: 'question_led',
} as const;

export interface NextActionRecommendationLearning {
  versionId: string;
  /** @minimum 1 */
  version: number;
  /**
     * @minimum -8
     * @maximum 8
     */
  priorityOffset: number;
  /**
     * @minimum -24
     * @maximum 48
     */
  timingOffsetHours: number;
  contentStyle: NextActionRecommendationLearningContentStyle;
  /** @minItems 1 */
  explanation: string[];
  correlationNotice: string;
  policyBoundary: string;
  evidenceCounts: RelationshipHealthCountMap;
}

export type NextActionRecommendationVersion = typeof NextActionRecommendationVersion[keyof typeof NextActionRecommendationVersion];


export const NextActionRecommendationVersion = {
  'relationship-recommendation-v1': 'relationship-recommendation-v1',
} as const;

export type NextActionRecommendationRecipient = {
  accountId: string;
  /** @nullable */
  contactId: string | null;
  name: string;
};

export type NextActionRecommendationTiming = {
  recommendedAt: string;
  reason: string;
};

export type NextActionRecommendationChannel = typeof NextActionRecommendationChannel[keyof typeof NextActionRecommendationChannel];


export const NextActionRecommendationChannel = {
  email: 'email',
  sms: 'sms',
  call: 'call',
} as const;

export type NextActionRecommendationHealthTrend = typeof NextActionRecommendationHealthTrend[keyof typeof NextActionRecommendationHealthTrend];


export const NextActionRecommendationHealthTrend = {
  improving: 'improving',
  stable: 'stable',
  declining: 'declining',
  unknown: 'unknown',
} as const;

export type NextActionRecommendationHealth = {
  snapshotId: string;
  rulesVersion: string;
  /**
     * @minimum 0
     * @maximum 100
     */
  score: number;
  trend: NextActionRecommendationHealthTrend;
};

export type NextActionRecommendationGroundingState = typeof NextActionRecommendationGroundingState[keyof typeof NextActionRecommendationGroundingState];


export const NextActionRecommendationGroundingState = {
  ready: 'ready',
  empty_index: 'empty_index',
  no_sources: 'no_sources',
  stale: 'stale',
  conflicting: 'conflicting',
  denied: 'denied',
  failed_ingestion: 'failed_ingestion',
  low_confidence: 'low_confidence',
  unavailable: 'unavailable',
} as const;

export type NextActionRecommendationGrounding = {
  state: NextActionRecommendationGroundingState;
  message: string;
  sources: NextActionKnowledgeReference[];
};

/**
 * Evidence-backed, review-only relationship outreach proposal; never execution authority
 * @nullable
 */
export type NextActionRecommendation = {
  version: NextActionRecommendationVersion;
  recipient: NextActionRecommendationRecipient;
  timing: NextActionRecommendationTiming;
  channel: NextActionRecommendationChannel;
  objective: string;
  content: NextActionMessageContent | NextActionCallBrief;
  health: NextActionRecommendationHealth;
  evidence: NextActionRecommendationEvidence[];
  grounding: NextActionRecommendationGrounding;
  learning?: NextActionRecommendationLearning | null;
  limitations: string[];
  /** Strategic briefs the AI was given when drafting this suggestion */
  strategicBriefs?: StrategicBriefSource[];
} | null;

export type NextActionFeedItemActionType = typeof NextActionFeedItemActionType[keyof typeof NextActionFeedItemActionType];


export const NextActionFeedItemActionType = {
  email: 'email',
  sms: 'sms',
  social: 'social',
  call: 'call',
  review_signal: 'review_signal',
  create_deal: 'create_deal',
} as const;

export type NextActionFeedItemStatus = typeof NextActionFeedItemStatus[keyof typeof NextActionFeedItemStatus];


export const NextActionFeedItemStatus = {
  open: 'open',
  processing: 'processing',
  completed: 'completed',
  dismissed: 'dismissed',
} as const;

export interface NextActionFeedItem {
  id: string;
  audience: CrmAudience;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  contactName?: string | null;
  /** @nullable */
  companyName?: string | null;
  actionType: NextActionFeedItemActionType;
  title: string;
  rationale: string;
  deepLink: string;
  status: NextActionFeedItemStatus;
  /**
     * @minimum 0
     * @maximum 100
     */
  priority: number;
  /**
     * @minimum 0
     * @maximum 100
     */
  scoreSnapshot: number;
  /**
     * Signal provider key (e.g. 'email')
     * @nullable
     */
  sourceKind?: string | null;
  /**
     * Stable reference to the underlying signal record
     * @nullable
     */
  sourceRef?: string | null;
  draft?: NextActionDraft | null;
  recommendation: NextActionRecommendation | null;
  /** Why the action ranks higher, e.g. 'Strategic account', 'Key contact' or 'Strategic brief on file'. */
  priorityReasons?: string[];
  /** @nullable */
  snoozedUntil?: string | null;
  /** @nullable */
  aiAuditId?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * sent = email dispatched via the connected mailbox; completed = non-email action resolved; human_only = automated send refused, draft returned for manual use
 */
export type NextActionApprovalResultOutcome = typeof NextActionApprovalResultOutcome[keyof typeof NextActionApprovalResultOutcome];


export const NextActionApprovalResultOutcome = {
  sent: 'sent',
  completed: 'completed',
  human_only: 'human_only',
} as const;

export interface NextActionApprovalResult {
  /** sent = email dispatched via the connected mailbox; completed = non-email action resolved; human_only = automated send refused, draft returned for manual use */
  outcome: NextActionApprovalResultOutcome;
  action: NextActionFeedItem;
  /** Id of the recorded outbound email_messages row (outcome sent only) */
  messageId?: string;
  /** Recipient the email was actually sent to (outcome sent only) */
  sentTo?: string;
  /** Subject the email was actually sent with (outcome sent only) */
  sentSubject?: string;
  /** Explanation returned when automated sending is blocked */
  message?: string;
  draft?: NextActionDraft | null;
}

export type NextActionsRefreshResultLlm = typeof NextActionsRefreshResultLlm[keyof typeof NextActionsRefreshResultLlm];


export const NextActionsRefreshResultLlm = {
  generated: 'generated',
  fallback: 'fallback',
  skipped: 'skipped',
} as const;

export type NextActionsRefreshResultProvidersItem = {
  key: string;
  /** @minimum 0 */
  signals: number;
  error?: string;
};

export interface NextActionsRefreshResult {
  /** @minimum 0 */
  signalsConsidered: number;
  /** @minimum 0 */
  created: number;
  /** @minimum 0 */
  updated: number;
  /** @minimum 0 */
  skipped: number;
  /**
     * Open actions closed because their source signal is no longer active
     * @minimum 0
     */
  closed: number;
  llm: NextActionsRefreshResultLlm;
  llmError?: string;
  providers: NextActionsRefreshResultProvidersItem[];
  auditId: string;
}

export type FunnelStageStage = typeof FunnelStageStage[keyof typeof FunnelStageStage];


export const FunnelStageStage = {
  first_touch: 'first_touch',
  engaged: 'engaged',
  enquiry: 'enquiry',
  opportunity: 'opportunity',
  won: 'won',
} as const;

export interface FunnelStage {
  stage: FunnelStageStage;
  /** @minimum 0 */
  count: number;
  /** @minimum 0 */
  conversionRate: number;
}

export interface FunnelBreakdown {
  key: string;
  label: string;
  channel: string;
  /** @nullable */
  campaign?: string | null;
  stages: FunnelStage[];
}

export interface AcquisitionFunnel {
  dateFrom: string;
  dateTo: string;
  stages: FunnelStage[];
  breakdowns: FunnelBreakdown[];
}

export type AttributionTouchAttributionType = typeof AttributionTouchAttributionType[keyof typeof AttributionTouchAttributionType];


export const AttributionTouchAttributionType = {
  first_touch: 'first_touch',
  contributing: 'contributing',
} as const;

export interface AttributionTouch {
  id: string;
  interactionId: string;
  attributionType: AttributionTouchAttributionType;
  channel: string;
  type: string;
  /** @nullable */
  subject?: string | null;
  body: string;
  /** @nullable */
  campaign?: string | null;
  weight: number;
  occurredAt: string;
  deepLink: string;
}

export type OpportunityAttributionOpportunity = {
  id: string;
  title: string;
  stage: string;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  contactId?: string | null;
};

export interface OpportunityAttribution {
  opportunity: OpportunityAttributionOpportunity;
  firstTouch?: AttributionTouch | null;
  contributingTouches: AttributionTouch[];
  /** @minimum 0 */
  totalTouches: number;
}

export type ChannelPerformanceChannel = typeof ChannelPerformanceChannel[keyof typeof ChannelPerformanceChannel];


export const ChannelPerformanceChannel = {
  social: 'social',
  email: 'email',
  chat: 'chat',
  voice: 'voice',
  web: 'web',
} as const;

export interface ChannelPerformance {
  channel: ChannelPerformanceChannel;
  /** @minimum 0 */
  touches: number;
  /** @minimum 0 */
  inbound: number;
  /** @minimum 0 */
  outbound: number;
  /** @minimum 0 */
  engagedAccounts: number;
  /** @minimum 0 */
  enquiries: number;
  /** @minimum 0 */
  opportunities: number;
  /** @minimum 0 */
  wonOpportunities: number;
  /** @minimum 0 */
  engagements: number;
  /** @minimum 0 */
  conversionRate: number;
}

export interface ChannelPerformanceList {
  dateFrom: string;
  dateTo: string;
  items: ChannelPerformance[];
}

export interface AcquisitionReportRequest {
  /**
     * @minLength 1
     * @maxLength 160
     */
  name: string;
  /**
     * @minLength 3
     * @maxLength 2000
     */
  prompt: string;
  /**
     * @minimum 1
     * @maximum 730
     */
  days?: number;
}

export interface AcquisitionReportDatum {
  label: string;
  value: number;
  /** @nullable */
  secondaryValue?: number | null;
}

export type AcquisitionReportSourceKind = typeof AcquisitionReportSourceKind[keyof typeof AcquisitionReportSourceKind];


export const AcquisitionReportSourceKind = {
  interaction: 'interaction',
  opportunity: 'opportunity',
  signal: 'signal',
  aggregate: 'aggregate',
} as const;

export interface AcquisitionReportSource {
  id: string;
  kind: AcquisitionReportSourceKind;
  label: string;
  /** @nullable */
  occurredAt?: string | null;
  /** @nullable */
  deepLink?: string | null;
}

export type AcquisitionReportChartType = typeof AcquisitionReportChartType[keyof typeof AcquisitionReportChartType];


export const AcquisitionReportChartType = {
  bar: 'bar',
  line: 'line',
  area: 'area',
} as const;

export interface AcquisitionReport {
  id: string;
  name: string;
  prompt: string;
  title: string;
  description: string;
  chartType: AcquisitionReportChartType;
  metric: string;
  dimension: string;
  dateFrom: string;
  dateTo: string;
  data: AcquisitionReportDatum[];
  sources: AcquisitionReportSource[];
  /** @nullable */
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * @minimum 1
 * @maximum 5
 * @nullable
 */
export type ProgressivenessDimensionScore = number | null;

export interface AccountProgressivenessBase {
  leadershipBuyIn?: ProgressivenessDimensionScore | null;
  budgetAllocation?: ProgressivenessDimensionScore | null;
  changeReadiness?: ProgressivenessDimensionScore | null;
  techMaturity?: ProgressivenessDimensionScore | null;
  adoptionSpeed?: ProgressivenessDimensionScore | null;
  internalCapability?: ProgressivenessDimensionScore | null;
  /** @nullable */
  notes?: string | null;
  /** @nullable */
  assessedBy?: string | null;
}

export type AccountProgressiveness = AccountProgressivenessBase & ({
  id: string;
  accountId: string;
  /** @nullable */
  overallScore?: string | null;
  /** @nullable */
  category?: string | null;
  assessedAt: string;
  updatedAt: string;
});

/**
 * @nullable
 */
export type AccountProductFitBaseTargetPriority = typeof AccountProductFitBaseTargetPriority[keyof typeof AccountProductFitBaseTargetPriority] | null;


export const AccountProductFitBaseTargetPriority = {
  A: 'A',
  B: 'B',
  C: 'C',
  watchlist: 'watchlist',
} as const;

export type AccountProductFitBaseSignals = {[key: string]: 'none' | 'weak' | 'moderate' | 'strong'};

export type AccountProductFitBaseStatus = typeof AccountProductFitBaseStatus[keyof typeof AccountProductFitBaseStatus];


export const AccountProductFitBaseStatus = {
  draft: 'draft',
  reviewed: 'reviewed',
  confirmed: 'confirmed',
  rejected: 'rejected',
} as const;

export interface AccountProductFitBase {
  /**
     * @minimum 0
     * @maximum 100
     * @nullable
     */
  fitScore?: number | null;
  /** @nullable */
  targetPriority?: AccountProductFitBaseTargetPriority;
  signals?: AccountProductFitBaseSignals;
  /** @nullable */
  rationale?: string | null;
  status?: AccountProductFitBaseStatus;
  /** @nullable */
  reviewedBy?: string | null;
  /** @nullable */
  lastReviewedAt?: string | null;
}

export type AccountProductFit = AccountProductFitBase & {
  id: string;
  accountId: string;
  productId: string;
  createdAt: string;
  updatedAt: string;
};

export interface AccountProductFitList {
  items: AccountProductFit[];
  /** @minimum 0 */
  total: number;
}

export type AccountProductFitCreate = AccountProductFitBase & {
  accountId: string;
  productId: string;
};

export type AccountProductFitUpdate = AccountProductFitBase;

export type ContactBaseSmsConsent = typeof ContactBaseSmsConsent[keyof typeof ContactBaseSmsConsent];


export const ContactBaseSmsConsent = {
  unknown: 'unknown',
  opted_in: 'opted_in',
  opted_out: 'opted_out',
} as const;

/**
 * Relationship importance, using the same levels as accounts.
 */
export type ContactBaseFocusLevel = typeof ContactBaseFocusLevel[keyof typeof ContactBaseFocusLevel];


export const ContactBaseFocusLevel = {
  strategic: 'strategic',
  key: 'key',
  growth: 'growth',
  standard: 'standard',
} as const;

export interface ContactBase {
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  phone?: string | null;
  /** @nullable */
  mobile?: string | null;
  /** @nullable */
  secondaryEmail?: string | null;
  /** @nullable */
  fax?: string | null;
  /** @nullable */
  department?: string | null;
  /** @nullable */
  addressLine1?: string | null;
  /** @nullable */
  addressLine2?: string | null;
  /** @nullable */
  city?: string | null;
  /** @nullable */
  stateProvince?: string | null;
  /** @nullable */
  postcode?: string | null;
  /** @nullable */
  country?: string | null;
  /** @nullable */
  status?: string | null;
  /** @nullable */
  ownerName?: string | null;
  /** @nullable */
  ownerId?: string | null;
  /** @nullable */
  preferredContactMethod?: string | null;
  doNotEmail?: boolean;
  doNotPhone?: boolean;
  smsConsent?: ContactBaseSmsConsent;
  /** @nullable */
  source?: string | null;
  /** Marks test or demo data so production-style lists can exclude it. */
  isTestData?: boolean;
  /** @nullable */
  unsubscribedAt?: string | null;
  /** @nullable */
  jobTitle?: string | null;
  /** @nullable */
  notes?: string | null;
  /** Relationship importance, using the same levels as accounts. */
  focusLevel?: ContactBaseFocusLevel;
  /**
     * @minimum 0
     * @maximum 100
     * @nullable
     */
  readonly leadScore?: number | null;
}

export type Contact = ContactBase & {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
  updatedAt: string;
} & Required<Pick<ContactBase & {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
  updatedAt: string;
}, 'smsConsent' | 'focusLevel'>>;

export interface ContactList {
  items: Contact[];
  /** @minimum 0 */
  total: number;
  limit?: number;
  offset?: number;
}

export type ContactCreate = ContactBase & {
  /**
     * @minLength 1
     * @maxLength 100
     */
  firstName: string;
  /**
     * @minLength 1
     * @maxLength 100
     */
  lastName: string;
  email: string;
};

export type ContactUpdate = ContactBase & {
  /**
     * @minLength 1
     * @maxLength 100
     */
  firstName?: string;
  /**
     * @minLength 1
     * @maxLength 100
     */
  lastName?: string;
  email?: string;
};

export type ContactFocusLevelFocusLevel = typeof ContactFocusLevelFocusLevel[keyof typeof ContactFocusLevelFocusLevel];


export const ContactFocusLevelFocusLevel = {
  strategic: 'strategic',
  key: 'key',
  growth: 'growth',
  standard: 'standard',
} as const;

export interface ContactFocusLevel {
  id: string;
  focusLevel: ContactFocusLevelFocusLevel;
}

export type ContactFocusLevelUpdateFocusLevel = typeof ContactFocusLevelUpdateFocusLevel[keyof typeof ContactFocusLevelUpdateFocusLevel];


export const ContactFocusLevelUpdateFocusLevel = {
  strategic: 'strategic',
  key: 'key',
  growth: 'growth',
  standard: 'standard',
} as const;

export interface ContactFocusLevelUpdate {
  focusLevel: ContactFocusLevelUpdateFocusLevel;
}

export type StrategicBriefRecordType = typeof StrategicBriefRecordType[keyof typeof StrategicBriefRecordType];


export const StrategicBriefRecordType = {
  account: 'account',
  contact: 'contact',
} as const;

export interface StrategicBriefCreate {
  recordType: StrategicBriefRecordType;
  recordId: string;
}

export interface StrategicBrief {
  /** Brief link identifier. */
  id: string;
  recordType: StrategicBriefRecordType;
  recordId: string;
  /** Managed document holding the brief; edit it through the managed-document API. */
  documentId: string;
  title: string;
  /**
     * Plain-text start of the brief, or null when nothing has been written yet.
     * @maxLength 500
     * @nullable
     */
  excerpt: string | null;
  /** Whether the signed-in user may change this brief. */
  canEdit: boolean;
  /** Present on create; true when this request created the brief. */
  created?: boolean;
  createdAt: string;
  /** When the brief's document was last saved. */
  updatedAt: string;
}

export interface StrategicBriefLookup {
  brief: StrategicBrief | null;
}

export type EnquiryStatus = typeof EnquiryStatus[keyof typeof EnquiryStatus];


export const EnquiryStatus = {
  new: 'new',
  in_progress: 'in_progress',
  converted: 'converted',
  closed: 'closed',
  confirmed: 'confirmed',
  cancelled: 'cancelled',
} as const;

export type EnquirySource = typeof EnquirySource[keyof typeof EnquirySource];


export const EnquirySource = {
  web_form: 'web_form',
  chat: 'chat',
  manual: 'manual',
  portal: 'portal',
  social: 'social',
  voice: 'voice',
  email: 'email',
} as const;

/**
 * Distinguishes a general enquiry from a meeting/booking request (package Module E, folded into the existing enquiries pipeline).
 */
export type EnquiryType = typeof EnquiryType[keyof typeof EnquiryType];


export const EnquiryType = {
  general: 'general',
  booking: 'booking',
} as const;

export interface Enquiry {
  id: string;
  name: string;
  email: string;
  /** @nullable */
  phone?: string | null;
  /** @nullable */
  companyName?: string | null;
  subject: string;
  message: string;
  status: EnquiryStatus;
  source: EnquirySource;
  type: EnquiryType;
  audience: CrmAudience;
  /** @nullable */
  bookingProduct?: string | null;
  /** @nullable */
  preferredAt?: string | null;
  /** @nullable */
  assignedTo?: string | null;
  /** @nullable */
  firstResponseDueAt?: string | null;
  /** @nullable */
  contactedAt?: string | null;
  /** @minimum 0 */
  contactAttempts: number;
  /** @nullable */
  outcomeReason?: string | null;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  opportunityId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type OpportunityHubItemType = typeof OpportunityHubItemType[keyof typeof OpportunityHubItemType];


export const OpportunityHubItemType = {
  enquiry: 'enquiry',
  social: 'social',
  web_signal: 'web_signal',
  opportunity: 'opportunity',
  referral: 'referral',
  quote: 'quote',
  tender: 'tender',
} as const;

export type SiteSearchType = typeof SiteSearchType[keyof typeof SiteSearchType];


export const SiteSearchType = {
  account: 'account',
  contact: 'contact',
  opportunity: 'opportunity',
  enquiry: 'enquiry',
  quote: 'quote',
  tender: 'tender',
  agreement: 'agreement',
} as const;

export interface SiteSearchResult {
  key: string;
  type: SiteSearchType;
  recordId: string;
  title: string;
  /**
     * Short detail such as job title, email, stage or reference.
     * @nullable
     */
  subtitle: string | null;
  /** @nullable */
  status: string | null;
  /** @nullable */
  accountName: string | null;
  /** Relevance; higher is better. */
  score: number;
  /** @nullable */
  updatedAt: string | null;
  deepLink: string;
}

export interface SiteSearchCounts {
  /** @minimum 0 */
  account: number;
  /** @minimum 0 */
  contact: number;
  /** @minimum 0 */
  opportunity: number;
  /** @minimum 0 */
  enquiry: number;
  /** @minimum 0 */
  quote: number;
  /** @minimum 0 */
  tender: number;
  /** @minimum 0 */
  agreement: number;
}

export interface SiteSearchResultList {
  /** @maxItems 700 */
  items: SiteSearchResult[];
  /**
     * Matches across the requested types.
     * @minimum 0
     */
  total: number;
  limit: number;
  offset: number;
  counts: SiteSearchCounts;
}

export type OpportunityHubItemPriority = typeof OpportunityHubItemPriority[keyof typeof OpportunityHubItemPriority];


export const OpportunityHubItemPriority = {
  urgent: 'urgent',
  normal: 'normal',
  low: 'low',
} as const;

/**
 * For opportunities, the kind of lead it came from.
 * @nullable
 */
export type OpportunityHubItemOrigin = typeof OpportunityHubItemOrigin[keyof typeof OpportunityHubItemOrigin] | null;


export const OpportunityHubItemOrigin = {
  enquiry: 'enquiry',
  social: 'social',
  web_signal: 'web_signal',
  referral: 'referral',
  quote: 'quote',
} as const;

/**
 * For tenders, the bid recommendation. People decide; the AI only recommends.
 * @nullable
 */
export type OpportunityHubItemRecommendation = typeof OpportunityHubItemRecommendation[keyof typeof OpportunityHubItemRecommendation] | null;


export const OpportunityHubItemRecommendation = {
  respond: 'respond',
  maybe: 'maybe',
  not_for_us: 'not_for_us',
} as const;

export interface OpportunityHubItem {
  /** Stable list key: type and record id. */
  key: string;
  type: OpportunityHubItemType;
  recordId: string;
  title: string;
  /** @nullable */
  summary: string | null;
  /**
     * Body text, notes or rationale, shortened to 2000 characters.
     * @nullable
     */
  message: string | null;
  /** Native status or stage of the source record. */
  status: string;
  priority: OpportunityHubItemPriority;
  isOpen: boolean;
  isNew: boolean;
  overdue: boolean;
  /** @nullable */
  accountId: string | null;
  /** @nullable */
  accountName: string | null;
  /** @nullable */
  contactId: string | null;
  /** @nullable */
  contactName: string | null;
  /** @nullable */
  contactEmail: string | null;
  /** @nullable */
  contactPhone: string | null;
  /** @nullable */
  ownerName: string | null;
  /** @nullable */
  value: number | null;
  /** @nullable */
  currency: string | null;
  /**
     * Web signal fit score or tender match score from 0 to 100.
     * @nullable
     */
  score: number | null;
  /**
     * Channel, network or kind of the source record.
     * @nullable
     */
  source: string | null;
  /** @nullable */
  stage: string | null;
  /** @nullable */
  pipeline: string | null;
  /**
     * For opportunities, the kind of lead it came from.
     * @nullable
     */
  origin: OpportunityHubItemOrigin;
  /** @nullable */
  dueAt: string | null;
  /**
     * For tenders, the portal closing date.
     * @nullable
     */
  closesAt: string | null;
  /**
     * For tenders, the bid recommendation. People decide; the AI only recommends.
     * @nullable
     */
  recommendation: OpportunityHubItemRecommendation;
  receivedAt: string;
  lastActivityAt: string;
  /** @minimum 0 */
  ageDays: number;
  deepLink: string;
}

export interface OpportunityHubCounts {
  /** @minimum 0 */
  enquiry: number;
  /** @minimum 0 */
  social: number;
  /** @minimum 0 */
  web_signal: number;
  /** @minimum 0 */
  opportunity: number;
  /** @minimum 0 */
  referral: number;
  /** @minimum 0 */
  quote: number;
  /** @minimum 0 */
  tender: number;
}

export interface OpportunityHubItemList {
  /** @maxItems 100 */
  items: OpportunityHubItem[];
  /** @minimum 0 */
  total: number;
  limit: number;
  offset: number;
  counts: OpportunityHubCounts;
}

export interface EnquiryList {
  items: Enquiry[];
  /** @minimum 0 */
  total: number;
  limit?: number;
  offset?: number;
}

export interface EnquiryCreate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  email: string;
  /** @nullable */
  phone?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  companyName?: string | null;
  /**
     * @minLength 1
     * @maxLength 300
     */
  subject: string;
  /**
     * @minLength 1
     * @maxLength 5000
     */
  message: string;
  source?: EnquirySource;
  type?: EnquiryType;
  audience?: CrmAudience;
  /**
     * @maxLength 200
     * @nullable
     */
  bookingProduct?: string | null;
  /** @nullable */
  preferredAt?: string | null;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  firstResponseDueAt?: string | null;
}

export interface EnquiryContactAttemptCreate {
  /** @maxLength 500 */
  note?: string;
  /** @maxLength 200 */
  by?: string;
}

export interface EnquiryStatusHistoryItem {
  id: string;
  enquiryId: string;
  fromStatus?: EnquiryStatus | null;
  toStatus: EnquiryStatus;
  /** @nullable */
  reason?: string | null;
  /** @nullable */
  changedBy?: string | null;
  createdAt: string;
}

export interface EnquiryStatusHistoryList {
  items: EnquiryStatusHistoryItem[];
}

export type StaleEnquirySegmentKey = typeof StaleEnquirySegmentKey[keyof typeof StaleEnquirySegmentKey];


export const StaleEnquirySegmentKey = {
  overdue_first_response: 'overdue_first_response',
  stalled_no_decision: 'stalled_no_decision',
  aged_new: 'aged_new',
} as const;

export interface StaleEnquirySegment {
  key: StaleEnquirySegmentKey;
  label: string;
  items: Enquiry[];
}

export interface StaleEnquirySegments {
  generatedAt: string;
  segments: StaleEnquirySegment[];
}

export interface EnquiryUpdate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  email?: string;
  /** @nullable */
  phone?: string | null;
  /**
     * @minLength 1
     * @maxLength 300
     */
  subject?: string;
  /**
     * @minLength 1
     * @maxLength 5000
     */
  message?: string;
  status?: EnquiryStatus;
  source?: EnquirySource;
  type?: EnquiryType;
  audience?: CrmAudience;
  /**
     * @maxLength 200
     * @nullable
     */
  bookingProduct?: string | null;
  /** @nullable */
  preferredAt?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  assignedTo?: string | null;
  /** @nullable */
  firstResponseDueAt?: string | null;
  /**
     * @maxLength 500
     * @nullable
     */
  outcomeReason?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  changedBy?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  companyName?: string | null;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  opportunityId?: string | null;
}

export interface PublicEnquirySubmission {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  email: string;
  /**
     * @maxLength 50
     * @nullable
     */
  phone?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  account?: string | null;
  /** @maxLength 300 */
  subject?: string;
  /**
     * @minLength 1
     * @maxLength 5000
     */
  message: string;
  /** Honeypot field — must be left empty. Filled values are rejected. */
  website?: string;
}

export interface PublicEnquiryConfirmation {
  id: string;
  message: string;
}

export interface PublicQuoteItem {
  itemType: QuoteItemType;
  /** @nullable */
  solutionId?: string | null;
  /** @nullable */
  productId?: string | null;
  /** @nullable */
  moduleId?: string | null;
  /** @nullable */
  serviceId?: string | null;
  /**
     * @minLength 1
     * @maxLength 300
     */
  label: string;
  /** @nullable */
  quantity?: string | null;
}

export interface PublicQuoteSubmission {
  /**
     * @minLength 1
     * @maxLength 200
     */
  requesterName: string;
  requesterEmail: string;
  /**
     * @maxLength 50
     * @nullable
     */
  requesterPhone?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  requesterOrganization?: string | null;
  /**
     * @maxLength 2000
     * @nullable
     */
  notes?: string | null;
  /**
     * @minItems 1
     * @maxItems 50
     */
  items: PublicQuoteItem[];
  /** Honeypot field — must be left empty. Filled values are rejected. */
  website?: string;
}

export interface PublicQuoteConfirmation {
  id: string;
  reference: string;
}

export type PublicChatMessageRole = typeof PublicChatMessageRole[keyof typeof PublicChatMessageRole];


export const PublicChatMessageRole = {
  user: 'user',
  assistant: 'assistant',
} as const;

export interface PublicChatMessage {
  role: PublicChatMessageRole;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  content: string;
}

export type PublicChatRequestMode = typeof PublicChatRequestMode[keyof typeof PublicChatRequestMode];


export const PublicChatRequestMode = {
  chat: 'chat',
  voice: 'voice',
} as const;

export interface PublicChatRequest {
  /**
     * @minLength 1
     * @maxLength 2000
     */
  message: string;
  /**
     * Prior messages of this browser session, oldest first.
     * @maxItems 20
     */
  history?: PublicChatMessage[];
  /** Honeypot field — must be left empty. Filled values are rejected. */
  website?: string;
  /** Optional durable browser-session id. Existing embeds may omit it. */
  sessionId?: string;
  mode?: PublicChatRequestMode;
}

export interface PublicChatLeadCapture {
  sessionId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /** @maxLength 320 */
  email: string;
  /**
     * @maxLength 50
     * @nullable
     */
  phone?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  account?: string | null;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  need: string;
}

export type PublicChatLeadCaptureResultAction = typeof PublicChatLeadCaptureResultAction[keyof typeof PublicChatLeadCaptureResultAction];


export const PublicChatLeadCaptureResultAction = {
  enquiry: 'enquiry',
  booking_request: 'booking_request',
  contact_only: 'contact_only',
} as const;

export interface PublicChatLeadCaptureResult {
  sessionId: string;
  contactId: string;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  enquiryId?: string | null;
  action: PublicChatLeadCaptureResultAction;
}

export interface PublicChatHandoffRequest {
  sessionId: string;
  /** @maxLength 1000 */
  reason?: string;
}

export type PublicChatHandoffResultStatus = typeof PublicChatHandoffResultStatus[keyof typeof PublicChatHandoffResultStatus];


export const PublicChatHandoffResultStatus = {
  handoff_requested: 'handoff_requested',
} as const;

export interface PublicChatHandoffResult {
  sessionId: string;
  status: PublicChatHandoffResultStatus;
}

export type ChatAssistantMessageRole = typeof ChatAssistantMessageRole[keyof typeof ChatAssistantMessageRole];


export const ChatAssistantMessageRole = {
  visitor: 'visitor',
  assistant: 'assistant',
  system: 'system',
} as const;

export type ChatAssistantMessageMode = typeof ChatAssistantMessageMode[keyof typeof ChatAssistantMessageMode];


export const ChatAssistantMessageMode = {
  chat: 'chat',
  voice: 'voice',
} as const;

export type ChatAssistantMessageValidationStatus = typeof ChatAssistantMessageValidationStatus[keyof typeof ChatAssistantMessageValidationStatus];


export const ChatAssistantMessageValidationStatus = {
  not_applicable: 'not_applicable',
  grounded: 'grounded',
  low_confidence: 'low_confidence',
  failed: 'failed',
} as const;

export interface ChatAssistantMessage {
  id: string;
  role: ChatAssistantMessageRole;
  mode: ChatAssistantMessageMode;
  content: string;
  validationStatus: ChatAssistantMessageValidationStatus;
  /** @nullable */
  validationConfidence?: number | null;
  createdAt: string;
}

export type ChatAssistantSessionStatus = typeof ChatAssistantSessionStatus[keyof typeof ChatAssistantSessionStatus];


export const ChatAssistantSessionStatus = {
  active: 'active',
  handoff_requested: 'handoff_requested',
  resolved: 'resolved',
} as const;

export type ChatAssistantSessionPrimaryMode = typeof ChatAssistantSessionPrimaryMode[keyof typeof ChatAssistantSessionPrimaryMode];


export const ChatAssistantSessionPrimaryMode = {
  chat: 'chat',
  voice: 'voice',
} as const;

export interface ChatAssistantSession {
  id: string;
  publicSessionId: string;
  /** @nullable */
  micrositeBindingId?: string | null;
  status: ChatAssistantSessionStatus;
  primaryMode: ChatAssistantSessionPrimaryMode;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  enquiryId?: string | null;
  /** @nullable */
  visitorName?: string | null;
  /** @nullable */
  visitorEmail?: string | null;
  /** @nullable */
  visitorPhone?: string | null;
  /** @nullable */
  visitorAccount?: string | null;
  /** @nullable */
  handoffReason?: string | null;
  messageCount: number;
  lastMessageAt: string;
  /** @nullable */
  resolvedAt?: string | null;
  createdAt: string;
}

export type ChatAssistantSessionDetail = ChatAssistantSession & {
  messages: ChatAssistantMessage[];
};

export interface ChatAssistantSessionList {
  items: ChatAssistantSession[];
}

export interface PublicEventAgendaItem {
  id: string;
  timeLabel: string;
  title: string;
  /** @nullable */
  speaker?: string | null;
  /** @nullable */
  speakerRole?: string | null;
}

export interface PublicEventSpeaker {
  id: string;
  name: string;
  /** @nullable */
  role?: string | null;
  /** @nullable */
  blurb?: string | null;
  /** @nullable */
  photoUrl?: string | null;
}

export interface PublicEventPrefill {
  firstName: string;
  lastName: string;
  email: string;
  /** @nullable */
  mobile: string | null;
}

export interface PublicEvent {
  id: string;
  slug: string;
  title: string;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  heroBadge?: string | null;
  /** @nullable */
  heroHeadline?: string | null;
  /** @nullable */
  heroSubheading?: string | null;
  /** @nullable */
  dateLabel?: string | null;
  /** @nullable */
  timeLabel?: string | null;
  /** @nullable */
  timezone?: string | null;
  /** @nullable */
  rsvpDeadlineLabel?: string | null;
  /** @nullable */
  startsAt?: string | null;
  /** @nullable */
  endsAt?: string | null;
  /** @nullable */
  venueName?: string | null;
  /** @nullable */
  venueRoom?: string | null;
  /** @nullable */
  venueAddress?: string | null;
  /** @nullable */
  venueWebsite?: string | null;
  /** @nullable */
  venueMapsUrl?: string | null;
  /** @nullable */
  livestreamUrl?: string | null;
  agenda: PublicEventAgendaItem[];
  speakers: PublicEventSpeaker[];
  invitationBypass: boolean;
  prefill: PublicEventPrefill | null;
}

export interface PublicInvitationCodeCheck {
  /**
     * @minLength 1
     * @maxLength 200
     * @pattern ^[A-Za-z0-9-]+$
     */
  code: string;
  /** Honeypot field — must be left empty. Filled values are rejected. */
  website?: string;
}

export interface PublicInvitationCodeResult {
  valid: boolean;
  /** @nullable */
  label?: string | null;
}

export interface PublicRsvpSubmission {
  /**
     * @minLength 1
     * @maxLength 200
     * @pattern ^[A-Za-z0-9-]+$
     */
  code?: string;
  /** @maxLength 100 */
  ref?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /** @maxLength 320 */
  email: string;
  /**
     * @maxLength 200
     * @nullable
     */
  account?: string | null;
  /**
     * @maxLength 50
     * @nullable
     */
  phone?: string | null;
  /**
     * @maxLength 500
     * @nullable
     */
  dietaryRequirements?: string | null;
  /**
     * @maxLength 2000
     * @nullable
     */
  notes?: string | null;
  /** Set false to record a "can't attend" response instead of a confirmed RSVP. */
  attending?: boolean;
  /** Honeypot field — must be left empty. Filled values are rejected. */
  website?: string;
}

export type PublicRsvpConfirmationStatus = typeof PublicRsvpConfirmationStatus[keyof typeof PublicRsvpConfirmationStatus];


export const PublicRsvpConfirmationStatus = {
  confirmed: 'confirmed',
  declined: 'declined',
  waitlisted: 'waitlisted',
  cancelled: 'cancelled',
} as const;

export interface PublicRsvpConfirmation {
  id: string;
  status: PublicRsvpConfirmationStatus;
  message: string;
}

export type CustomPageScope = typeof CustomPageScope[keyof typeof CustomPageScope];


export const CustomPageScope = {
  global: 'global',
  user: 'user',
} as const;

export type CustomPageWidgetConfig = { [key: string]: unknown };

export interface CustomPageWidget {
  id: string;
  pageId: string;
  type: CustomPageWidgetType;
  /** @nullable */
  title?: string | null;
  config: CustomPageWidgetConfig;
  position: number;
  /**
     * @minimum 1
     * @maximum 12
     */
  width: number;
  scope: CustomPageScope;
  /** @nullable */
  ownerUserId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CustomPageWidgetCreateConfig = { [key: string]: unknown };

export type CustomPageWidgetCreateWidth = typeof CustomPageWidgetCreateWidth[keyof typeof CustomPageWidgetCreateWidth];


export const CustomPageWidgetCreateWidth = {
  NUMBER_6: 6,
  NUMBER_12: 12,
} as const;

export interface CustomPageWidgetCreate {
  type: CustomPageWidgetType;
  /**
     * @maxLength 300
     * @nullable
     */
  title?: string | null;
  config?: CustomPageWidgetCreateConfig;
  /** @minimum 0 */
  position?: number;
  width?: CustomPageWidgetCreateWidth;
}

export type CustomPageWidgetUpdateConfig = { [key: string]: unknown };

export type CustomPageWidgetUpdateWidth = typeof CustomPageWidgetUpdateWidth[keyof typeof CustomPageWidgetUpdateWidth];


export const CustomPageWidgetUpdateWidth = {
  NUMBER_6: 6,
  NUMBER_12: 12,
} as const;

export interface CustomPageWidgetUpdate {
  /**
     * @maxLength 300
     * @nullable
     */
  title?: string | null;
  config?: CustomPageWidgetUpdateConfig;
  /** @minimum 0 */
  position?: number;
  width?: CustomPageWidgetUpdateWidth;
}

export interface CustomPageSummary {
  id: string;
  slug: string;
  title: string;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  icon?: string | null;
  sortOrder: number;
  isPublished: boolean;
  scope: CustomPageScope;
  /** @nullable */
  ownerUserId?: string | null;
  /** @minimum 0 */
  widgetCount: number;
  createdAt: string;
  updatedAt: string;
}

export type CustomPageDetail = CustomPageSummary & {
  widgets: CustomPageWidget[];
};

export interface CustomPageCreate {
  /**
     * @minLength 1
     * @maxLength 120
     * @pattern ^[a-z0-9]+(?:-[a-z0-9]+)*$
     */
  slug: string;
  /**
     * @minLength 1
     * @maxLength 300
     */
  title: string;
  /**
     * @maxLength 5000
     * @nullable
     */
  description?: string | null;
  /**
     * @maxLength 100
     * @nullable
     */
  icon?: string | null;
  /** @minimum 0 */
  sortOrder?: number;
  isPublished?: boolean;
}

export interface CustomPageUpdate {
  /**
     * @minLength 1
     * @maxLength 120
     * @pattern ^[a-z0-9]+(?:-[a-z0-9]+)*$
     */
  slug?: string;
  /**
     * @minLength 1
     * @maxLength 300
     */
  title?: string;
  /**
     * @maxLength 5000
     * @nullable
     */
  description?: string | null;
  /**
     * @maxLength 100
     * @nullable
     */
  icon?: string | null;
  /** @minimum 0 */
  sortOrder?: number;
  isPublished?: boolean;
}

export type EventStatus = typeof EventStatus[keyof typeof EventStatus];


export const EventStatus = {
  draft: 'draft',
  published: 'published',
  archived: 'archived',
} as const;

export interface AdminEventSummary {
  id: string;
  slug: string;
  title: string;
  status: EventStatus;
  /** @nullable */
  dateLabel?: string | null;
  /** @nullable */
  startsAt?: string | null;
  rsvpCount: number;
  codeCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminAgendaItem {
  id: string;
  sortOrder: number;
  timeLabel: string;
  title: string;
  /** @nullable */
  speaker?: string | null;
  /** @nullable */
  speakerRole?: string | null;
}

export interface AdminSpeaker {
  id: string;
  sortOrder: number;
  name: string;
  /** @nullable */
  role?: string | null;
  /** @nullable */
  blurb?: string | null;
  /** @nullable */
  photoUrl?: string | null;
}

export interface AdminInvitationCode {
  id: string;
  code: string;
  /** @nullable */
  label?: string | null;
  isActive: boolean;
  /** @nullable */
  maxUses?: number | null;
  useCount: number;
  /** @nullable */
  expiresAt?: string | null;
  /** @nullable */
  notes?: string | null;
  createdAt: string;
}

export interface AdminEventDetail {
  id: string;
  slug: string;
  title: string;
  /** @nullable */
  description?: string | null;
  status: EventStatus;
  /** @nullable */
  heroBadge?: string | null;
  /** @nullable */
  heroHeadline?: string | null;
  /** @nullable */
  heroSubheading?: string | null;
  /** @nullable */
  dateLabel?: string | null;
  /** @nullable */
  timeLabel?: string | null;
  /** @nullable */
  timezone?: string | null;
  /** @nullable */
  rsvpDeadlineLabel?: string | null;
  /** @nullable */
  startsAt?: string | null;
  /** @nullable */
  endsAt?: string | null;
  /** @nullable */
  capacity?: number | null;
  /** @nullable */
  venueName?: string | null;
  /** @nullable */
  venueRoom?: string | null;
  /** @nullable */
  venueAddress?: string | null;
  /** @nullable */
  venueWebsite?: string | null;
  /** @nullable */
  venueMapsUrl?: string | null;
  /** @nullable */
  livestreamUrl?: string | null;
  agenda: AdminAgendaItem[];
  speakers: AdminSpeaker[];
  invitationCodes: AdminInvitationCode[];
  createdAt: string;
  updatedAt: string;
}

export interface AdminEventCreate {
  /**
     * @minLength 1
     * @maxLength 120
     * @pattern ^[a-z0-9]+(?:-[a-z0-9]+)*$
     */
  slug: string;
  /**
     * @minLength 1
     * @maxLength 300
     */
  title: string;
  /**
     * @maxLength 5000
     * @nullable
     */
  description?: string | null;
  status?: EventStatus;
  /**
     * @maxLength 200
     * @nullable
     */
  heroBadge?: string | null;
  /**
     * @maxLength 300
     * @nullable
     */
  heroHeadline?: string | null;
  /**
     * @maxLength 1000
     * @nullable
     */
  heroSubheading?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  dateLabel?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  timeLabel?: string | null;
  /**
     * @maxLength 100
     * @nullable
     */
  timezone?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  rsvpDeadlineLabel?: string | null;
  /** @nullable */
  startsAt?: string | null;
  /** @nullable */
  endsAt?: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  capacity?: number | null;
  /**
     * @maxLength 300
     * @nullable
     */
  venueName?: string | null;
  /**
     * @maxLength 300
     * @nullable
     */
  venueRoom?: string | null;
  /**
     * @maxLength 500
     * @nullable
     */
  venueAddress?: string | null;
  /**
     * @maxLength 500
     * @nullable
     */
  venueWebsite?: string | null;
  /**
     * @maxLength 1000
     * @nullable
     */
  venueMapsUrl?: string | null;
  /**
     * @maxLength 1000
     * @nullable
     */
  livestreamUrl?: string | null;
}

export interface AdminEventUpdate {
  /**
     * @minLength 1
     * @maxLength 120
     * @pattern ^[a-z0-9]+(?:-[a-z0-9]+)*$
     */
  slug?: string;
  /**
     * @minLength 1
     * @maxLength 300
     */
  title?: string;
  /**
     * @maxLength 5000
     * @nullable
     */
  description?: string | null;
  status?: EventStatus;
  /**
     * @maxLength 200
     * @nullable
     */
  heroBadge?: string | null;
  /**
     * @maxLength 300
     * @nullable
     */
  heroHeadline?: string | null;
  /**
     * @maxLength 1000
     * @nullable
     */
  heroSubheading?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  dateLabel?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  timeLabel?: string | null;
  /**
     * @maxLength 100
     * @nullable
     */
  timezone?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  rsvpDeadlineLabel?: string | null;
  /** @nullable */
  startsAt?: string | null;
  /** @nullable */
  endsAt?: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  capacity?: number | null;
  /**
     * @maxLength 300
     * @nullable
     */
  venueName?: string | null;
  /**
     * @maxLength 300
     * @nullable
     */
  venueRoom?: string | null;
  /**
     * @maxLength 500
     * @nullable
     */
  venueAddress?: string | null;
  /**
     * @maxLength 500
     * @nullable
     */
  venueWebsite?: string | null;
  /**
     * @maxLength 1000
     * @nullable
     */
  venueMapsUrl?: string | null;
  /**
     * @maxLength 1000
     * @nullable
     */
  livestreamUrl?: string | null;
}

export type EventRsvpStatus = typeof EventRsvpStatus[keyof typeof EventRsvpStatus];


export const EventRsvpStatus = {
  confirmed: 'confirmed',
  declined: 'declined',
  waitlisted: 'waitlisted',
  cancelled: 'cancelled',
} as const;

export type AdminRsvpSource = typeof AdminRsvpSource[keyof typeof AdminRsvpSource];


export const AdminRsvpSource = {
  web: 'web',
  sms: 'sms',
} as const;

export interface AdminRsvp {
  id: string;
  name: string;
  email: string;
  /** @nullable */
  account?: string | null;
  /** @nullable */
  phone?: string | null;
  /** @nullable */
  dietaryRequirements?: string | null;
  /** @nullable */
  notes?: string | null;
  status: EventRsvpStatus;
  /** @nullable */
  invitationCode?: string | null;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  contactName?: string | null;
  source: AdminRsvpSource;
  /** @nullable */
  campaignName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminRsvpList {
  items: AdminRsvp[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminRsvpSummary {
  total: number;
  confirmed: number;
  declined: number;
  waitlisted: number;
  cancelled: number;
}

export interface AdminRsvpStatusUpdate {
  status: EventRsvpStatus;
}

export interface AdminAgendaItemCreate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  timeLabel: string;
  /**
     * @minLength 1
     * @maxLength 300
     */
  title: string;
  /**
     * @maxLength 200
     * @nullable
     */
  speaker?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  speakerRole?: string | null;
  /** @minimum 0 */
  sortOrder?: number;
}

export interface AdminAgendaItemUpdate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  timeLabel?: string;
  /**
     * @minLength 1
     * @maxLength 300
     */
  title?: string;
  /**
     * @maxLength 200
     * @nullable
     */
  speaker?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  speakerRole?: string | null;
  /** @minimum 0 */
  sortOrder?: number;
}

export interface AdminSpeakerCreate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @maxLength 200
     * @nullable
     */
  role?: string | null;
  /**
     * @maxLength 2000
     * @nullable
     */
  blurb?: string | null;
  /**
     * @maxLength 1000
     * @nullable
     */
  photoUrl?: string | null;
  /** @minimum 0 */
  sortOrder?: number;
}

export interface AdminSpeakerUpdate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @maxLength 200
     * @nullable
     */
  role?: string | null;
  /**
     * @maxLength 2000
     * @nullable
     */
  blurb?: string | null;
  /**
     * @maxLength 1000
     * @nullable
     */
  photoUrl?: string | null;
  /** @minimum 0 */
  sortOrder?: number;
}

export interface AdminInvitationCodeCreate {
  /**
     * @minLength 1
     * @maxLength 64
     * @pattern ^[A-Za-z0-9-]+$
     */
  code: string;
  /**
     * @maxLength 200
     * @nullable
     */
  label?: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  maxUses?: number | null;
  /** @nullable */
  expiresAt?: string | null;
  /**
     * @maxLength 1000
     * @nullable
     */
  notes?: string | null;
}

export interface AdminInvitationCodeUpdate {
  isActive?: boolean;
  /**
     * @maxLength 200
     * @nullable
     */
  label?: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  maxUses?: number | null;
  /** @nullable */
  expiresAt?: string | null;
  /**
     * @maxLength 1000
     * @nullable
     */
  notes?: string | null;
}

export interface AdminBulkCodesRequest {
  /**
     * @minimum 1
     * @maximum 500
     */
  count: number;
  /**
     * @maxLength 20
     * @pattern ^[A-Za-z0-9-]*$
     */
  prefix?: string;
  /**
     * @maxLength 200
     * @nullable
     */
  label?: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  maxUses?: number | null;
}

export interface EnquiryConvert {
  createOpportunity?: boolean;
  /**
     * @minLength 1
     * @maxLength 300
     */
  opportunityTitle?: string;
  /** @nullable */
  opportunityValue?: number | null;
}

export type OpportunityPipeline = typeof OpportunityPipeline[keyof typeof OpportunityPipeline];


export const OpportunityPipeline = {
  b2b_sales: 'b2b_sales',
  b2c_sales: 'b2c_sales',
  investor_funding: 'investor_funding',
} as const;

export interface Opportunity {
  id: string;
  title: string;
  pipeline: OpportunityPipeline;
  stage: OpportunityStage;
  /** @nullable */
  value?: number | null;
  currency: string;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  expectedCloseDate?: string | null;
  /** @nullable */
  actualCloseDate?: string | null;
  /** @nullable */
  status?: string | null;
  /** @nullable */
  topic?: string | null;
  /** @nullable */
  source?: string | null;
  /** @nullable */
  ownerName?: string | null;
  /** @nullable */
  ownerId?: string | null;
  /**
     * @minimum 0
     * @maximum 100
     * @nullable
     */
  probability?: number | null;
  /** @nullable */
  originatingLeadId?: string | null;
  isTestData?: boolean;
  /** @nullable */
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EnquiryConvertResult {
  enquiry: Enquiry;
  contact: Contact;
  account?: Account;
  opportunity?: Opportunity;
  contactCreated: boolean;
  accountCreated: boolean;
  opportunityCreated: boolean;
}

export interface OpportunityList {
  items: Opportunity[];
  /** @minimum 0 */
  total: number;
  limit?: number;
  offset?: number;
}

export interface OpportunityCreate {
  /**
     * @minLength 1
     * @maxLength 300
     */
  title: string;
  pipeline?: OpportunityPipeline;
  stage?: OpportunityStage;
  /** @nullable */
  value?: number | null;
  /**
     * @minLength 3
     * @maxLength 3
     */
  currency?: string;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  expectedCloseDate?: string | null;
  /** @nullable */
  actualCloseDate?: string | null;
  /** @nullable */
  status?: string | null;
  /** @nullable */
  topic?: string | null;
  /** @nullable */
  source?: string | null;
  /** @nullable */
  ownerName?: string | null;
  /** @nullable */
  ownerId?: string | null;
  /**
     * @minimum 0
     * @maximum 100
     * @nullable
     */
  probability?: number | null;
  /** @nullable */
  originatingLeadId?: string | null;
  isTestData?: boolean;
  /** @nullable */
  notes?: string | null;
}

export interface OpportunityUpdate {
  /**
     * @minLength 1
     * @maxLength 300
     */
  title?: string;
  pipeline?: OpportunityPipeline;
  stage?: OpportunityStage;
  /** @nullable */
  value?: number | null;
  /**
     * @minLength 3
     * @maxLength 3
     */
  currency?: string;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  expectedCloseDate?: string | null;
  /** @nullable */
  actualCloseDate?: string | null;
  /** @nullable */
  status?: string | null;
  /** @nullable */
  topic?: string | null;
  /** @nullable */
  source?: string | null;
  /** @nullable */
  ownerName?: string | null;
  /** @nullable */
  ownerId?: string | null;
  /**
     * @minimum 0
     * @maximum 100
     * @nullable
     */
  probability?: number | null;
  /** @nullable */
  originatingLeadId?: string | null;
  isTestData?: boolean;
  /** @nullable */
  notes?: string | null;
}

export interface ProposalTaskSource {
  /**
     * @minLength 1
     * @maxLength 200
     */
  revision: string;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  quote: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  location: string;
}

export type ProposalTaskInputPriority = typeof ProposalTaskInputPriority[keyof typeof ProposalTaskInputPriority];


export const ProposalTaskInputPriority = {
  low: 'low',
  normal: 'normal',
  high: 'high',
  urgent: 'urgent',
} as const;

export interface ProposalTaskInput {
  activityId?: string;
  /** @minimum 1 */
  expectedVersion?: number;
  /**
     * @minLength 1
     * @maxLength 500
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 5000
     */
  description: string;
  /**
     * @minLength 1
     * @maxLength 100
     */
  status: string;
  priority: ProposalTaskInputPriority;
  /** @nullable */
  ownerId: string | null;
  /** @nullable */
  dueAt: string | null;
  /** @maxItems 20 */
  sources: ProposalTaskSource[];
  allowSimilar?: boolean;
}

export type ProposalTaskListItemsItem = {
  activity: Activity;
  sources: ProposalTaskSource[];
};

export interface ProposalTaskList {
  items: ProposalTaskListItemsItem[];
  total: number;
  limit: number;
  offset: number;
}

export type ProposalTaskReviewStatus = typeof ProposalTaskReviewStatus[keyof typeof ProposalTaskReviewStatus];


export const ProposalTaskReviewStatus = {
  generating: 'generating',
  draft: 'draft',
  reviewed: 'reviewed',
  committed: 'committed',
  failed: 'failed',
} as const;

export interface ProposalTaskReview {
  id: string;
  tenderId: string;
  status: ProposalTaskReviewStatus;
  hash: string;
  tasks: ProposalTaskInput[];
  warnings: string[];
  activityIds: string[];
  expiresAt: string;
}

export type OpportunityNoteLinkKind = typeof OpportunityNoteLinkKind[keyof typeof OpportunityNoteLinkKind];


export const OpportunityNoteLinkKind = {
  catalogue_product: 'catalogue_product',
  catalogue_solution: 'catalogue_solution',
  catalogue_service: 'catalogue_service',
  business_case: 'business_case',
  quote: 'quote',
  tender: 'tender',
  library: 'library',
  managed_document: 'managed_document',
} as const;

export interface OpportunityNoteLink {
  kind: OpportunityNoteLinkKind;
  id: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  revision: string;
}

export type OpportunityNoteFieldsKind = typeof OpportunityNoteFieldsKind[keyof typeof OpportunityNoteFieldsKind];


export const OpportunityNoteFieldsKind = {
  discovery: 'discovery',
  negotiation: 'negotiation',
} as const;

export type OpportunityNoteFieldsStatus = typeof OpportunityNoteFieldsStatus[keyof typeof OpportunityNoteFieldsStatus];


export const OpportunityNoteFieldsStatus = {
  open: 'open',
  settled: 'settled',
} as const;

export interface OpportunityNoteFields {
  opportunityId?: string;
  kind?: OpportunityNoteFieldsKind;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
  recordedOn?: string;
  status?: OpportunityNoteFieldsStatus;
  /**
     * @maxLength 5000
     * @nullable
     */
  needs?: string | null;
  /**
     * @maxLength 5000
     * @nullable
     */
  priorities?: string | null;
  /**
     * @maxLength 5000
     * @nullable
     */
  constraints?: string | null;
  /**
     * @maxLength 5000
     * @nullable
     */
  successCriteria?: string | null;
  /**
     * @maxLength 5000
     * @nullable
     */
  solutionOptions?: string | null;
  /**
     * @maxLength 5000
     * @nullable
     */
  notes?: string | null;
  /** @maxItems 30 */
  links?: OpportunityNoteLink[];
}

export type OpportunityNote = OpportunityNoteFields & {
  id: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  unavailableLinks: number;
} & Required<Pick<OpportunityNoteFields & {
  id: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  unavailableLinks: number;
}, 'opportunityId' | 'kind' | 'title' | 'recordedOn' | 'status' | 'links'>>;

export type OpportunityNoteChangeOperation = typeof OpportunityNoteChangeOperation[keyof typeof OpportunityNoteChangeOperation];


export const OpportunityNoteChangeOperation = {
  create: 'create',
  update: 'update',
} as const;

export interface OpportunityNoteChange {
  operation: OpportunityNoteChangeOperation;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
  id?: string;
  /** @minimum 1 */
  expectedVersion?: number;
  fields?: OpportunityNoteFields;
}

export interface OpportunityNoteList {
  items: OpportunityNote[];
  total: number;
  limit: number;
  offset: number;
}

export type OpportunityNoteReviewStatus = typeof OpportunityNoteReviewStatus[keyof typeof OpportunityNoteReviewStatus];


export const OpportunityNoteReviewStatus = {
  reviewed: 'reviewed',
  committed: 'committed',
  cancelled: 'cancelled',
} as const;

export interface OpportunityNoteReview {
  id: string;
  hash: string;
  expiresAt: string;
  status: OpportunityNoteReviewStatus;
  change: OpportunityNoteChange;
  before: OpportunityNote | null;
  after: OpportunityNoteFields;
  receipt: OpportunityNote | null;
}

export type LifecycleInsightKind = typeof LifecycleInsightKind[keyof typeof LifecycleInsightKind];


export const LifecycleInsightKind = {
  segment: 'segment',
  referral: 'referral',
  stakeholder: 'stakeholder',
  activity: 'activity',
  improvement: 'improvement',
  opportunity_note: 'opportunity_note',
  general_agreement: 'general_agreement',
  obligation: 'obligation',
  delivery: 'delivery',
  case: 'case',
  account_review: 'account_review',
  renewal_plan: 'renewal_plan',
  quality_decision: 'quality_decision',
  retention_decision: 'retention_decision',
  opportunity: 'opportunity',
} as const;

export interface LifecycleInsightRow {
  id: string;
  sourceKind: LifecycleInsightKind;
  title: string;
  /** @nullable */
  status: string | null;
  /** @nullable */
  ownerId: string | null;
  /** @nullable */
  date: string | null;
  dateBasis: string;
  nativePath: string;
  archived: boolean;
  /** @nullable */
  currency: string | null;
  /** @nullable */
  value: string | null;
  /** @nullable */
  weighted: string | null;
  /** @nullable */
  rating: number | null;
  /** @nullable */
  ratingScale: string | null;
  /** @nullable */
  responseState: string | null;
  /** @nullable */
  resolutionState: string | null;
}

export interface LifecycleInsightGroup {
  key: string;
  count: number;
  drilldown: string;
}

export type LifecycleInsightsSourcesItem = {
  kind: LifecycleInsightKind;
  available: boolean;
  explanation: string;
  dateBasis: string;
};

export type LifecycleInsightsMoneyItem = {
  /** @nullable */
  currency: string | null;
  outcome: string;
  count: number;
  /** @nullable */
  gross: string | null;
  grossCount: number;
  /** @nullable */
  weighted: string | null;
  weightedCount: number;
};

export type LifecycleInsightsRatingsItem = {
  scale: string;
  count: number;
  average: string;
};

export type LifecycleInsightsServiceStatesItemKind = typeof LifecycleInsightsServiceStatesItemKind[keyof typeof LifecycleInsightsServiceStatesItemKind];


export const LifecycleInsightsServiceStatesItemKind = {
  response: 'response',
  resolution: 'resolution',
} as const;

export type LifecycleInsightsServiceStatesItem = {
  kind: LifecycleInsightsServiceStatesItemKind;
  state: string;
  count: number;
  drilldown: string;
};

export interface LifecycleInsights {
  asOf: string;
  calculation: string;
  sources: LifecycleInsightsSourcesItem[];
  total: number;
  missingDate: number;
  missingOwner: number;
  missingValue: number;
  missingProbability: number;
  missingCurrency: number;
  missingRating: number;
  limit: number;
  offset: number;
  groupTotal: number;
  groupLimit: number;
  groupOffset: number;
  items: LifecycleInsightRow[];
  groups: LifecycleInsightGroup[];
  money: LifecycleInsightsMoneyItem[];
  ratings: LifecycleInsightsRatingsItem[];
  serviceStates: LifecycleInsightsServiceStatesItem[];
}

export type RetentionKind = typeof RetentionKind[keyof typeof RetentionKind];


export const RetentionKind = {
  account: 'account',
  contact: 'contact',
  segment: 'segment',
  referral: 'referral',
  stakeholder: 'stakeholder',
  activity: 'activity',
  opportunity_note: 'opportunity_note',
  general_agreement: 'general_agreement',
  obligation: 'obligation',
  delivery: 'delivery',
  case: 'case',
  account_review: 'account_review',
  renewal_plan: 'renewal_plan',
} as const;

export type RetentionChangeOperation = typeof RetentionChangeOperation[keyof typeof RetentionChangeOperation];


export const RetentionChangeOperation = {
  review: 'review',
  retain: 'retain',
  archive: 'archive',
  unarchive: 'unarchive',
} as const;

export interface RetentionChange {
  operation: RetentionChangeOperation;
  sourceKind: RetentionKind;
  sourceId: string;
  /** @minLength 1 */
  expectedVersion: string;
  /** @nullable */
  reviewOn: string | null;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
}

export type RetentionRecordState = typeof RetentionRecordState[keyof typeof RetentionRecordState];


export const RetentionRecordState = {
  active: 'active',
  archived: 'archived',
} as const;

export interface RetentionRecord {
  id: string;
  sourceKind: RetentionKind;
  label: string;
  nativePath: string;
  version: string;
  state: RetentionRecordState;
  /** @nullable */
  reviewOn: string | null;
  /** @nullable */
  reason: string | null;
  /** @nullable */
  decision: string | null;
  /** @nullable */
  actorId: string | null;
  /** @nullable */
  decidedAt: string | null;
  /** @nullable */
  archivedAt: string | null;
}

export interface RetentionPage {
  items: RetentionRecord[];
  total: number;
  limit: number;
  offset: number;
}

export type RetentionHistoryPageItemsItem = {
  id: string;
  actorId: string;
  reason: string;
  operation: string;
  createdAt: string;
  snapshot: RetentionRecord;
};

export interface RetentionHistoryPage {
  items: RetentionHistoryPageItemsItem[];
  total: number;
  limit: number;
  offset: number;
}

export type RetentionReviewStatus = typeof RetentionReviewStatus[keyof typeof RetentionReviewStatus];


export const RetentionReviewStatus = {
  reviewed: 'reviewed',
  committed: 'committed',
  cancelled: 'cancelled',
} as const;

export interface RetentionReview {
  id: string;
  hash: string;
  expiresAt: string;
  status: RetentionReviewStatus;
  change: RetentionChange;
  before: RetentionRecord;
  effect: string;
  receipt: RetentionRecord | null;
}

export type DataQualityChangeOperation = typeof DataQualityChangeOperation[keyof typeof DataQualityChangeOperation];


export const DataQualityChangeOperation = {
  prefer: 'prefer',
  distinct: 'distinct',
  reverse: 'reverse',
  reassign: 'reassign',
} as const;

export type DataQualityChangeSourceKind = typeof DataQualityChangeSourceKind[keyof typeof DataQualityChangeSourceKind];


export const DataQualityChangeSourceKind = {
  account: 'account',
  contact: 'contact',
  opportunity: 'opportunity',
  enquiry: 'enquiry',
  activity: 'activity',
  tender: 'tender',
} as const;

export interface DataQualityChange {
  operation: DataQualityChangeOperation;
  sourceKind: DataQualityChangeSourceKind;
  sourceId: string;
  /** @minLength 1 */
  expectedVersion: string;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
  targetId?: string;
  /** @minLength 1 */
  expectedTargetVersion?: string;
  /** @nullable */
  ownerId?: string | null;
}

export type DataQualityDecisionDecision = typeof DataQualityDecisionDecision[keyof typeof DataQualityDecisionDecision];


export const DataQualityDecisionDecision = {
  preferred: 'preferred',
  distinct: 'distinct',
  reversed: 'reversed',
} as const;

export interface DataQualityDecision {
  id: string;
  otherId: string;
  decision: DataQualityDecisionDecision;
  /** @nullable */
  preferredId: string | null;
  reason: string;
  actorId: string;
  version: number;
  updatedAt: string;
}

export type DataQualityRecordSourceKind = typeof DataQualityRecordSourceKind[keyof typeof DataQualityRecordSourceKind];


export const DataQualityRecordSourceKind = {
  account: 'account',
  contact: 'contact',
  opportunity: 'opportunity',
  enquiry: 'enquiry',
  activity: 'activity',
  tender: 'tender',
} as const;

export type DataQualityRecordFacts = {[key: string]: string | null};

export interface DataQualityRecord {
  id: string;
  sourceKind: DataQualityRecordSourceKind;
  label: string;
  /** @nullable */
  ownerId: string | null;
  /** @nullable */
  ownerName: string | null;
  version: string;
  nativePath: string;
  findings: string[];
  decisions: DataQualityDecision[];
  facts: DataQualityRecordFacts;
}

export interface DataQualityPage {
  items: DataQualityRecord[];
  total: number;
  limit: number;
  offset: number;
}

export type DataQualityCandidatePageItemsItem = {
  record: DataQualityRecord;
  reasons: string[];
};

export interface DataQualityCandidatePage {
  items: DataQualityCandidatePageItemsItem[];
  total: number;
  limit: number;
  offset: number;
}

export type DataQualityHistoryPageItemsItem = {
  id: string;
  actorId: string;
  reason: string;
  operation: string;
  createdAt: string;
  snapshot: DataQualityRecord;
};

export interface DataQualityHistoryPage {
  items: DataQualityHistoryPageItemsItem[];
  total: number;
  limit: number;
  offset: number;
}

export type DataQualityReviewStatus = typeof DataQualityReviewStatus[keyof typeof DataQualityReviewStatus];


export const DataQualityReviewStatus = {
  reviewed: 'reviewed',
  committed: 'committed',
  cancelled: 'cancelled',
} as const;

export interface DataQualityReview {
  id: string;
  hash: string;
  expiresAt: string;
  status: DataQualityReviewStatus;
  change: DataQualityChange;
  before: DataQualityRecord;
  target: DataQualityRecord | null;
  effect: string;
  receipt: DataQualityRecord | null;
}

export interface RenewalPlanningFields {
  /**
     * @minLength 1
     * @maxLength 120
     */
  decision?: string;
  /** @nullable */
  ownerId?: string | null;
  /**
     * @maxLength 10000
     * @nullable
     */
  nextSteps?: string | null;
  /** @nullable */
  followUpOn?: string | null;
  /** @nullable */
  opportunityId?: string | null;
  /** @nullable */
  referralId?: string | null;
}

export type RenewalPlanningChangeOperation = typeof RenewalPlanningChangeOperation[keyof typeof RenewalPlanningChangeOperation];


export const RenewalPlanningChangeOperation = {
  save: 'save',
  renew_product: 'renew_product',
  create_opportunity: 'create_opportunity',
  create_referral: 'create_referral',
} as const;

export type RenewalPlanningChangeSourceKind = typeof RenewalPlanningChangeSourceKind[keyof typeof RenewalPlanningChangeSourceKind];


export const RenewalPlanningChangeSourceKind = {
  account: 'account',
  product: 'product',
  general_agreement: 'general_agreement',
  partner_agreement: 'partner_agreement',
} as const;

export type RenewalPlanningChangeRenewal = {
  /** @nullable */
  renewalDate?: string | null;
  /** @nullable */
  expirationDate?: string | null;
};

export interface RenewalPlanningChange {
  operation: RenewalPlanningChangeOperation;
  sourceKind: RenewalPlanningChangeSourceKind;
  sourceId: string;
  /** @minLength 1 */
  expectedVersion: string;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
  fields?: RenewalPlanningFields;
  renewal?: RenewalPlanningChangeRenewal;
  opportunity?: OpportunityCreate;
  referral?: CrmReferralFields;
}

export type RenewalPlanningRecordSourceKind = typeof RenewalPlanningRecordSourceKind[keyof typeof RenewalPlanningRecordSourceKind];


export const RenewalPlanningRecordSourceKind = {
  account: 'account',
  product: 'product',
  general_agreement: 'general_agreement',
  partner_agreement: 'partner_agreement',
} as const;

export type RenewalPlanningRecordDateState = typeof RenewalPlanningRecordDateState[keyof typeof RenewalPlanningRecordDateState];


export const RenewalPlanningRecordDateState = {
  missing: 'missing',
  dated: 'dated',
} as const;

export interface RenewalPlanningRecord {
  id: string;
  sourceKind: RenewalPlanningRecordSourceKind;
  accountId: string;
  label: string;
  /** @nullable */
  sourceDate: string | null;
  dateState: RenewalPlanningRecordDateState;
  version: string;
  fields: RenewalPlanningFields;
  nativePath: string;
}

export interface RenewalPlanningPage {
  items: RenewalPlanningRecord[];
  total: number;
  limit: number;
  offset: number;
}

export type RenewalPlanningHistoryPageItemsItem = {
  id: string;
  actorId: string;
  reason: string;
  createdAt: string;
  snapshot: RenewalPlanningRecord;
};

export interface RenewalPlanningHistoryPage {
  items: RenewalPlanningHistoryPageItemsItem[];
  total: number;
  limit: number;
  offset: number;
}

export type RenewalPlanningReviewStatus = typeof RenewalPlanningReviewStatus[keyof typeof RenewalPlanningReviewStatus];


export const RenewalPlanningReviewStatus = {
  reviewed: 'reviewed',
  committed: 'committed',
  cancelled: 'cancelled',
} as const;

export interface RenewalPlanningReview {
  id: string;
  hash: string;
  expiresAt: string;
  status: RenewalPlanningReviewStatus;
  change: RenewalPlanningChange;
  before: RenewalPlanningRecord;
  after: RenewalPlanningFields;
  effect: string;
  receipt: RenewalPlanningRecord | null;
}

export type AccountReviewFieldsKind = typeof AccountReviewFieldsKind[keyof typeof AccountReviewFieldsKind];


export const AccountReviewFieldsKind = {
  review: 'review',
  feedback: 'feedback',
  advocacy: 'advocacy',
} as const;

export type AccountReviewFieldsSourceKind = typeof AccountReviewFieldsSourceKind[keyof typeof AccountReviewFieldsSourceKind];


export const AccountReviewFieldsSourceKind = {
  staff: 'staff',
  customer: 'customer',
  partner: 'partner',
  other: 'other',
} as const;

export type AccountReviewFieldsAdvocacyStatus = typeof AccountReviewFieldsAdvocacyStatus[keyof typeof AccountReviewFieldsAdvocacyStatus];


export const AccountReviewFieldsAdvocacyStatus = {
  unknown: 'unknown',
  granted: 'granted',
  declined: 'declined',
  withdrawn: 'withdrawn',
} as const;

export type AccountReviewFieldsEvidenceItemKind = typeof AccountReviewFieldsEvidenceItemKind[keyof typeof AccountReviewFieldsEvidenceItemKind];


export const AccountReviewFieldsEvidenceItemKind = {
  library: 'library',
  managed_document: 'managed_document',
} as const;

export type AccountReviewFieldsEvidenceItem = {
  kind: AccountReviewFieldsEvidenceItemKind;
  id: string;
  /**
     * @minLength 1
     * @maxLength 128
     */
  revision: string;
};

export interface AccountReviewFields {
  accountId?: string;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  ownerId?: string | null;
  kind?: AccountReviewFieldsKind;
  /**
     * @minLength 1
     * @maxLength 500
     */
  title?: string;
  recordedOn?: string;
  /** @nullable */
  nextReviewOn?: string | null;
  sourceKind?: AccountReviewFieldsSourceKind;
  /**
     * @maxLength 5000
     * @nullable
     */
  sourceContext?: string | null;
  /**
     * @maxLength 10000
     * @nullable
     */
  notes?: string | null;
  /**
     * @maxLength 5000
     * @nullable
     */
  outcome?: string | null;
  /**
     * Optional staff-recorded satisfaction, 1 very dissatisfied through 5 very satisfied. Not a calculated health score.
     * @minimum 1
     * @maximum 5
     * @nullable
     */
  satisfactionRating?: number | null;
  advocacyStatus?: AccountReviewFieldsAdvocacyStatus;
  /**
     * @maxLength 1000
     * @nullable
     */
  advocacyPurpose?: string | null;
  /**
     * @maxLength 5000
     * @nullable
     */
  advocacyEvidence?: string | null;
  /** @maxItems 50 */
  evidence?: AccountReviewFieldsEvidenceItem[];
}

export type AccountReviewChangeOperation = typeof AccountReviewChangeOperation[keyof typeof AccountReviewChangeOperation];


export const AccountReviewChangeOperation = {
  create: 'create',
  update: 'update',
  task: 'task',
  link_task: 'link_task',
} as const;

export type DeliveryTaskOperation = typeof DeliveryTaskOperation[keyof typeof DeliveryTaskOperation];


export const DeliveryTaskOperation = {
  create: 'create',
  update: 'update',
  complete: 'complete',
  reopen: 'reopen',
} as const;

export interface DeliveryTask {
  operation: DeliveryTaskOperation;
  /** @minimum 1 */
  expectedVersion?: number;
  /**
     * @maxLength 500
     * @nullable
     */
  subject?: string | null;
  /**
     * @minLength 1
     * @maxLength 5000
     */
  body?: string;
  /** @nullable */
  ownerId?: string | null;
  /**
     * @maxLength 120
     * @nullable
     */
  priority?: string | null;
  /** @nullable */
  dueAt?: string | null;
  /** @nullable */
  completedAt?: string | null;
}

export interface AccountReviewChange {
  operation: AccountReviewChangeOperation;
  id?: string;
  /** @minLength 1 */
  expectedVersion?: string;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
  fields?: AccountReviewFields;
  task?: DeliveryTask;
  /** @nullable */
  activityId?: string | null;
}

export interface AccountReviewRecord {
  id: string;
  version: string;
  fields: AccountReviewFields;
  task: Activity | null;
  unavailableEvidence: number;
}

export interface AccountReviewPage {
  items: AccountReviewRecord[];
  total: number;
  limit: number;
  offset: number;
}

export type AccountReviewHistoryPageItemsItem = {
  id: string;
  actorId: string;
  reason: string;
  createdAt: string;
  snapshot: AccountReviewRecord;
};

export interface AccountReviewHistoryPage {
  items: AccountReviewHistoryPageItemsItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface AccountReviewChangeRequest {
  requestId: string;
  change: AccountReviewChange;
}

export type AccountReviewChangeReviewStatus = typeof AccountReviewChangeReviewStatus[keyof typeof AccountReviewChangeReviewStatus];


export const AccountReviewChangeReviewStatus = {
  reviewed: 'reviewed',
  committed: 'committed',
  cancelled: 'cancelled',
} as const;

export interface AccountReviewChangeReview {
  id: string;
  hash: string;
  expiresAt: string;
  status: AccountReviewChangeReviewStatus;
  change: AccountReviewChange;
  before: AccountReviewRecord | null;
  after: AccountReviewFields;
  receipt: AccountReviewRecord | null;
}

export type ServiceCaseFieldsEscalationState = typeof ServiceCaseFieldsEscalationState[keyof typeof ServiceCaseFieldsEscalationState];


export const ServiceCaseFieldsEscalationState = {
  none: 'none',
  active: 'active',
  cleared: 'cleared',
} as const;

/**
 * @nullable
 */
export type ServiceCaseFieldsAgreementKind = typeof ServiceCaseFieldsAgreementKind[keyof typeof ServiceCaseFieldsAgreementKind] | null;


export const ServiceCaseFieldsAgreementKind = {
  partner: 'partner',
  general: 'general',
} as const;

export type ServiceCaseFieldsClassification = typeof ServiceCaseFieldsClassification[keyof typeof ServiceCaseFieldsClassification];


export const ServiceCaseFieldsClassification = {
  case: 'case',
  request: 'request',
  complaint: 'complaint',
} as const;

export type ServiceCaseFieldsPriority = typeof ServiceCaseFieldsPriority[keyof typeof ServiceCaseFieldsPriority];


export const ServiceCaseFieldsPriority = {
  low: 'low',
  normal: 'normal',
  high: 'high',
  urgent: 'urgent',
} as const;

export type ServiceCaseFieldsEvidenceItemKind = typeof ServiceCaseFieldsEvidenceItemKind[keyof typeof ServiceCaseFieldsEvidenceItemKind];


export const ServiceCaseFieldsEvidenceItemKind = {
  library: 'library',
  managed_document: 'managed_document',
} as const;

export type ServiceCaseFieldsEvidenceItem = {
  kind: ServiceCaseFieldsEvidenceItemKind;
  id: string;
  /**
     * @minLength 1
     * @maxLength 128
     */
  revision: string;
};

export interface ServiceCaseFields {
  /** @nullable */
  receivedAt?: string | null;
  /** @nullable */
  responseTargetAt?: string | null;
  /** @nullable */
  respondedAt?: string | null;
  /** @nullable */
  resolutionTargetAt?: string | null;
  /** @nullable */
  resolutionActualAt?: string | null;
  escalationState?: ServiceCaseFieldsEscalationState;
  /** @nullable */
  escalationOwnerId?: string | null;
  /**
     * @minLength 3
     * @maxLength 5000
     * @nullable
     */
  escalationReason?: string | null;
  /** @nullable */
  escalatedAt?: string | null;
  /**
     * @maxLength 5000
     * @nullable
     */
  internalUpdate?: string | null;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  enquiryId?: string | null;
  /** @nullable */
  agreementId?: string | null;
  /** @nullable */
  agreementKind?: ServiceCaseFieldsAgreementKind;
  /** @nullable */
  ownerId?: string | null;
  classification?: ServiceCaseFieldsClassification;
  priority?: ServiceCaseFieldsPriority;
  /**
     * @minLength 1
     * @maxLength 500
     */
  summary?: string;
  /**
     * @maxLength 10000
     * @nullable
     */
  notes?: string | null;
  /**
     * @minLength 1
     * @maxLength 120
     */
  status?: string;
  recordedOn?: string;
  /** @nullable */
  resolvedOn?: string | null;
  /** @nullable */
  closedOn?: string | null;
  /**
     * @maxLength 5000
     * @nullable
     */
  decision?: string | null;
  /** @maxItems 50 */
  evidence?: ServiceCaseFieldsEvidenceItem[];
}

export type ServiceCaseChangeOperation = typeof ServiceCaseChangeOperation[keyof typeof ServiceCaseChangeOperation];


export const ServiceCaseChangeOperation = {
  create: 'create',
  from_enquiry: 'from_enquiry',
  update: 'update',
  link_enquiry: 'link_enquiry',
  resolve: 'resolve',
  close: 'close',
  reopen: 'reopen',
  task: 'task',
} as const;

export interface ServiceCaseChange {
  operation: ServiceCaseChangeOperation;
  id?: string;
  /** @minLength 1 */
  expectedVersion?: string;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
  fields?: ServiceCaseFields;
  task?: DeliveryTask;
}

export type ServiceCaseTimingResponseState = typeof ServiceCaseTimingResponseState[keyof typeof ServiceCaseTimingResponseState];


export const ServiceCaseTimingResponseState = {
  missing: 'missing',
  pending: 'pending',
  due: 'due',
  overdue: 'overdue',
  met: 'met',
  late: 'late',
  recorded: 'recorded',
} as const;

export type ServiceCaseTimingResolutionState = typeof ServiceCaseTimingResolutionState[keyof typeof ServiceCaseTimingResolutionState];


export const ServiceCaseTimingResolutionState = {
  missing: 'missing',
  pending: 'pending',
  due: 'due',
  overdue: 'overdue',
  met: 'met',
  late: 'late',
  recorded: 'recorded',
} as const;

export interface ServiceCaseTiming {
  asOf: string;
  responseState: ServiceCaseTimingResponseState;
  resolutionState: ServiceCaseTimingResolutionState;
  /** @nullable */
  responseElapsedSeconds: number | null;
  /** @nullable */
  resolutionElapsedSeconds: number | null;
}

export interface ServiceCaseRecord {
  id: string;
  version: string;
  fields: ServiceCaseFields;
  timing?: ServiceCaseTiming;
  task: Activity | null;
  unavailableEvidence: number;
}

export interface ServiceCasePage {
  asOf?: string;
  items: ServiceCaseRecord[];
  total: number;
  limit: number;
  offset: number;
}

export type ServiceCaseHistoryPageItemsItem = {
  id: string;
  actorId: string;
  reason: string;
  createdAt: string;
  snapshot: ServiceCaseRecord;
};

export interface ServiceCaseHistoryPage {
  items: ServiceCaseHistoryPageItemsItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface ServiceCaseReviewRequest {
  requestId: string;
  change: ServiceCaseChange;
}

export type ServiceCaseReviewStatus = typeof ServiceCaseReviewStatus[keyof typeof ServiceCaseReviewStatus];


export const ServiceCaseReviewStatus = {
  reviewed: 'reviewed',
  committed: 'committed',
  cancelled: 'cancelled',
} as const;

/**
 * @nullable
 */
export type ServiceCaseReviewCopiedContext = { [key: string]: unknown } | null;

export interface ServiceCaseReview {
  id: string;
  hash: string;
  expiresAt: string;
  status: ServiceCaseReviewStatus;
  change: ServiceCaseChange;
  before: ServiceCaseRecord | null;
  after: ServiceCaseFields;
  receipt: ServiceCaseRecord | null;
  /** @nullable */
  copiedContext: ServiceCaseReviewCopiedContext;
}

/**
 * @nullable
 */
export type DeliveryFieldsAgreementKind = typeof DeliveryFieldsAgreementKind[keyof typeof DeliveryFieldsAgreementKind] | null;


export const DeliveryFieldsAgreementKind = {
  partner: 'partner',
  general: 'general',
} as const;

export type DeliveryFieldsKind = typeof DeliveryFieldsKind[keyof typeof DeliveryFieldsKind];


export const DeliveryFieldsKind = {
  setup: 'setup',
  responsibility: 'responsibility',
  obligation: 'obligation',
  milestone: 'milestone',
} as const;

export interface DeliveryFields {
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  planId?: string | null;
  /** @nullable */
  opportunityId?: string | null;
  /** @nullable */
  agreementId?: string | null;
  /** @nullable */
  obligationId?: string | null;
  /** @nullable */
  initiatingId?: string | null;
  /** @nullable */
  receivingId?: string | null;
  /** @nullable */
  ownerId?: string | null;
  /** @nullable */
  agreementKind?: DeliveryFieldsAgreementKind;
  kind?: DeliveryFieldsKind;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
  /**
     * @maxLength 5000
     * @nullable
     */
  summary?: string | null;
  /**
     * @maxLength 5000
     * @nullable
     */
  notes?: string | null;
  /**
     * @maxLength 5000
     * @nullable
     */
  evidence?: string | null;
  /**
     * @minLength 1
     * @maxLength 120
     */
  status?: string;
  /** @nullable */
  dueOn?: string | null;
  /** @nullable */
  completedOn?: string | null;
  /** @nullable */
  acceptedOn?: string | null;
  /** @nullable */
  initiatedOn?: string | null;
  completed?: boolean;
}

export type DeliveryChangeResource = typeof DeliveryChangeResource[keyof typeof DeliveryChangeResource];


export const DeliveryChangeResource = {
  plan: 'plan',
  item: 'item',
} as const;

export type DeliveryChangeOperation = typeof DeliveryChangeOperation[keyof typeof DeliveryChangeOperation];


export const DeliveryChangeOperation = {
  create: 'create',
  update: 'update',
  accept: 'accept',
  complete: 'complete',
  reopen: 'reopen',
  task: 'task',
} as const;

export interface DeliveryChange {
  resource: DeliveryChangeResource;
  operation: DeliveryChangeOperation;
  id?: string;
  /** @minLength 1 */
  expectedVersion?: string;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
  fields?: DeliveryFields;
  task?: DeliveryTask;
}

export type DeliveryRecordResource = typeof DeliveryRecordResource[keyof typeof DeliveryRecordResource];


export const DeliveryRecordResource = {
  plan: 'plan',
  item: 'item',
} as const;

export interface DeliveryRecord {
  id: string;
  resource: DeliveryRecordResource;
  version: string;
  fields: DeliveryFields;
  task: Activity | null;
}

export interface DeliveryPage {
  items: DeliveryRecord[];
  total: number;
  limit: number;
  offset: number;
}

export type DeliveryHistoryPageItemsItem = {
  id: string;
  actorId: string;
  reason: string;
  createdAt: string;
  snapshot: DeliveryRecord;
};

export interface DeliveryHistoryPage {
  items: DeliveryHistoryPageItemsItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface DeliveryReviewRequest {
  requestId: string;
  change: DeliveryChange;
}

export type DeliveryConfirmationConfirmation = typeof DeliveryConfirmationConfirmation[keyof typeof DeliveryConfirmationConfirmation];


export const DeliveryConfirmationConfirmation = {
  CONFIRM: 'CONFIRM',
} as const;

export interface DeliveryConfirmation {
  /** @minLength 1 */
  hash: string;
  confirmation: DeliveryConfirmationConfirmation;
}

export type DeliveryReviewStatus = typeof DeliveryReviewStatus[keyof typeof DeliveryReviewStatus];


export const DeliveryReviewStatus = {
  reviewed: 'reviewed',
  committed: 'committed',
  cancelled: 'cancelled',
} as const;

export interface DeliveryReview {
  id: string;
  hash: string;
  expiresAt: string;
  status: DeliveryReviewStatus;
  change: DeliveryChange;
  before: DeliveryRecord | null;
  after: DeliveryFields;
  receipt: DeliveryRecord | null;
}

export type StaffActivityChangeOperation = typeof StaffActivityChangeOperation[keyof typeof StaffActivityChangeOperation];


export const StaffActivityChangeOperation = {
  create: 'create',
  update: 'update',
  complete: 'complete',
  reopen: 'reopen',
} as const;

export interface StaffActivityFields {
  entityType?: ActivityEntityType;
  entityId?: string;
  type?: ActivityType;
  /**
     * @maxLength 500
     * @nullable
     */
  subject?: string | null;
  /**
     * @minLength 1
     * @maxLength 5000
     */
  body?: string;
  /**
     * @maxLength 120
     * @nullable
     */
  status?: string | null;
  /**
     * @maxLength 120
     * @nullable
     */
  priority?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  ownerId?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  ownerName?: string | null;
  /** @nullable */
  scheduledStartAt?: string | null;
  /** @nullable */
  scheduledEndAt?: string | null;
  /** @nullable */
  actualStartAt?: string | null;
  /** @nullable */
  actualEndAt?: string | null;
  /**
     * @minimum 0
     * @nullable
     */
  durationMinutes?: number | null;
  /**
     * @maxItems 100
     * @nullable
     */
  participants?: unknown[] | null;
  /**
     * @maxLength 500
     * @nullable
     */
  location?: string | null;
  /**
     * @maxLength 120
     * @nullable
     */
  category?: string | null;
  /**
     * @maxLength 2000
     * @nullable
     */
  externalUrl?: string | null;
  visibleToCustomer?: boolean;
  isTestData?: boolean;
}

export interface StaffActivityChange {
  operation: StaffActivityChangeOperation;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
  id?: string;
  /** @minimum 1 */
  expectedVersion?: number;
  fields?: StaffActivityFields;
}

export type StaffActivityReviewStatus = typeof StaffActivityReviewStatus[keyof typeof StaffActivityReviewStatus];


export const StaffActivityReviewStatus = {
  reviewed: 'reviewed',
  committed: 'committed',
  cancelled: 'cancelled',
} as const;

export interface StaffActivityReview {
  id: string;
  hash: string;
  expiresAt: string;
  change: StaffActivityChange;
  before: Activity | null;
  after: StaffActivityFields;
  status: StaffActivityReviewStatus;
  receipt: Activity | null;
}

export type StaffActivityHistoryListItemsItem = {
  id: string;
  activityId: string;
  operation: string;
  reason: string;
  actorId: string;
  createdAt: string;
  snapshot: Activity;
};

export interface StaffActivityHistoryList {
  total: number;
  limit: number;
  offset: number;
  items: StaffActivityHistoryListItemsItem[];
}

export interface ActivityList {
  items: Activity[];
  /** @minimum 0 */
  total: number;
  limit?: number;
  offset?: number;
}

export interface ActivityCreate {
  requestKey?: string;
  /** @minimum 1 */
  expectedPursuitRevision?: number;
  entityType: ActivityEntityType;
  entityId: string;
  type?: ActivityType;
  /** @nullable */
  subject?: string | null;
  /** @nullable */
  status?: string | null;
  /** @nullable */
  priority?: string | null;
  /** @nullable */
  scheduledStartAt?: string | null;
  /** @nullable */
  scheduledEndAt?: string | null;
  /** @nullable */
  actualStartAt?: string | null;
  /** @nullable */
  actualEndAt?: string | null;
  /**
     * @minimum 0
     * @nullable
     */
  durationMinutes?: number | null;
  /** @nullable */
  ownerName?: string | null;
  /** @nullable */
  ownerId?: string | null;
  /** @nullable */
  participants?: unknown[] | null;
  /** @nullable */
  location?: string | null;
  /** @nullable */
  category?: string | null;
  /** @nullable */
  externalUrl?: string | null;
  isTestData?: boolean;
  /**
     * @minLength 1
     * @maxLength 5000
     */
  body: string;
  /** Show this update to the customer in the portal. */
  visibleToCustomer?: boolean;
}

export interface ActivityUpdate {
  /** @minimum 1 */
  expectedPursuitRevision?: number;
  type?: ActivityType;
  /** @nullable */
  subject?: string | null;
  /** @nullable */
  status?: string | null;
  /** @nullable */
  priority?: string | null;
  /** @nullable */
  scheduledStartAt?: string | null;
  /** @nullable */
  scheduledEndAt?: string | null;
  /** @nullable */
  actualStartAt?: string | null;
  /** @nullable */
  actualEndAt?: string | null;
  /**
     * @minimum 0
     * @nullable
     */
  durationMinutes?: number | null;
  /** @nullable */
  ownerName?: string | null;
  /** @nullable */
  ownerId?: string | null;
  /** @nullable */
  participants?: unknown[] | null;
  /** @nullable */
  location?: string | null;
  /** @nullable */
  category?: string | null;
  /** @nullable */
  externalUrl?: string | null;
  isTestData?: boolean;
  /**
     * @minLength 1
     * @maxLength 5000
     */
  body?: string;
  /** Show this update to the customer in the portal. */
  visibleToCustomer?: boolean;
}

export type TestDataReportItemEntityType = typeof TestDataReportItemEntityType[keyof typeof TestDataReportItemEntityType];


export const TestDataReportItemEntityType = {
  account: 'account',
  contact: 'contact',
  opportunity: 'opportunity',
  partner: 'partner',
  activity: 'activity',
} as const;

export interface TestDataReportItem {
  entityType: TestDataReportItemEntityType;
  id: string;
  name: string;
  createdAt: string;
  reason: string;
}

export interface TestDataReport {
  generatedAt: string;
  /** @minimum 0 */
  total: number;
  items: TestDataReportItem[];
}

export type JourneyKey = typeof JourneyKey[keyof typeof JourneyKey];


export const JourneyKey = {
  enquiry_to_outcome: 'enquiry_to_outcome',
  opportunity_b2b_sales: 'opportunity_b2b_sales',
  opportunity_b2c_sales: 'opportunity_b2c_sales',
  investor_funding: 'investor_funding',
  partner_engagement: 'partner_engagement',
} as const;

export type JourneyEntityType = typeof JourneyEntityType[keyof typeof JourneyEntityType];


export const JourneyEntityType = {
  enquiry: 'enquiry',
  opportunity: 'opportunity',
  partner: 'partner',
} as const;

export interface JourneyOwnership {
  field: string;
  label: string;
}

export interface JourneyServiceExpectation {
  /**
     * @minimum 1
     * @maximum 8760
     */
  durationHours: number;
  label: string;
}

export type JourneyRequirementDefinitionRule = typeof JourneyRequirementDefinitionRule[keyof typeof JourneyRequirementDefinitionRule];


export const JourneyRequirementDefinitionRule = {
  present: 'present',
  min_number: 'min_number',
} as const;

export interface JourneyRequirementDefinition {
  id: string;
  label: string;
  field: string;
  rule: JourneyRequirementDefinitionRule;
  /** @nullable */
  minimum?: number | null;
  blocking: boolean;
}

export interface JourneyNextActionDefinition {
  id: string;
  label: string;
  description: string;
  actionType: string;
}

export interface JourneyStageDefinition {
  id: string;
  label: string;
  objective: string;
  /** @minimum 0 */
  order: number;
  terminal: boolean;
  /** @nullable */
  outcome?: string | null;
  requiresOutcomeDetails: boolean;
  requirements: JourneyRequirementDefinition[];
  transitions: string[];
  ownership: JourneyOwnership;
  serviceExpectation: JourneyServiceExpectation;
  nextActions: JourneyNextActionDefinition[];
}

export type JourneyDefinitionAppliesTo = {[key: string]: string};

export interface JourneyDefinition {
  key: JourneyKey;
  label: string;
  description: string;
  entityType: JourneyEntityType;
  initialStageId: string;
  appliesTo: JourneyDefinitionAppliesTo;
  /** @minItems 1 */
  stages: JourneyStageDefinition[];
}

export interface JourneyDefinitionList {
  items: JourneyDefinition[];
  /** @minimum 0 */
  total: number;
}

export type JourneyRequirementStatus = JourneyRequirementDefinition & {
  satisfied: boolean;
  detail: string;
};

export type JourneyStageStatusStatus = typeof JourneyStageStatusStatus[keyof typeof JourneyStageStatusStatus];


export const JourneyStageStatusStatus = {
  completed: 'completed',
  current: 'current',
  upcoming: 'upcoming',
} as const;

export interface JourneyStageStatus {
  id: string;
  label: string;
  objective: string;
  /** @minimum 0 */
  order: number;
  terminal: boolean;
  /** @nullable */
  outcome?: string | null;
  status: JourneyStageStatusStatus;
}

export interface JourneyTransitionOption {
  stageId: string;
  label: string;
  /** @nullable */
  outcome?: string | null;
  blocked: boolean;
  requiresOutcomeDetails: boolean;
  missingRequirements: string[];
}

export type JourneyHandoffDirection = typeof JourneyHandoffDirection[keyof typeof JourneyHandoffDirection];


export const JourneyHandoffDirection = {
  inbound: 'inbound',
  outbound: 'outbound',
} as const;

export interface JourneyHandoff {
  direction: JourneyHandoffDirection;
  journeyKey: JourneyKey;
  recordId: string;
  recordLabel: string;
  stageId: string;
  /** @nullable */
  owner: string | null;
  href: string;
  context: string;
}

export interface JourneyRecordState {
  definition: JourneyDefinition;
  recordId: string;
  recordLabel: string;
  entityType: JourneyEntityType;
  currentStageId: string;
  stages: JourneyStageStatus[];
  requirements: JourneyRequirementStatus[];
  validTransitions: JourneyTransitionOption[];
  handoffs: JourneyHandoff[];
  /** @nullable */
  owner: string | null;
  startedAt: string;
  updatedAt: string;
  /** @nullable */
  dueAt?: string | null;
  overdue: boolean;
  terminal: boolean;
}

export type JourneySignalWindow = typeof JourneySignalWindow[keyof typeof JourneySignalWindow];


export const JourneySignalWindow = {
  '7d': '7d',
  '30d': '30d',
  '90d': '90d',
  all: 'all',
} as const;

export interface JourneySignalEntityGraph {
  /** @nullable */
  enquiryId: string | null;
  /** @nullable */
  contactId: string | null;
  /** @nullable */
  accountId: string | null;
  opportunityIds: string[];
}

export type JourneySignalChannelSummaryConnection = typeof JourneySignalChannelSummaryConnection[keyof typeof JourneySignalChannelSummaryConnection];


export const JourneySignalChannelSummaryConnection = {
  connected: 'connected',
  not_configured: 'not_configured',
  inbound_unavailable: 'inbound_unavailable',
  not_applicable: 'not_applicable',
} as const;

export interface JourneySignalChannelSummary {
  channel: string;
  label: string;
  /** @minimum 0 */
  inboundCount: number;
  /** @minimum 0 */
  outboundCount: number;
  /** @nullable */
  lastAt: string | null;
  connection: JourneySignalChannelSummaryConnection;
}

export type JourneySignalSourceKind = typeof JourneySignalSourceKind[keyof typeof JourneySignalSourceKind];


export const JourneySignalSourceKind = {
  interaction: 'interaction',
  telephony_call: 'telephony_call',
  chat_session: 'chat_session',
  social_inbox_item: 'social_inbox_item',
  email_message: 'email_message',
  activity: 'activity',
  enquiry: 'enquiry',
} as const;

export type JourneySignalDirection = typeof JourneySignalDirection[keyof typeof JourneySignalDirection];


export const JourneySignalDirection = {
  inbound: 'inbound',
  outbound: 'outbound',
  internal: 'internal',
} as const;

export interface JourneySignal {
  /** @minLength 1 */
  id: string;
  sourceKind: JourneySignalSourceKind;
  channel: string;
  type: string;
  direction: JourneySignalDirection;
  /** @nullable */
  subject: string | null;
  /** @maxLength 240 */
  preview: string;
  occurredAt: string;
  stageId: string;
  href: string;
}

export interface JourneySignalTwin {
  journeyKey: JourneyKey;
  recordId: string;
  recordLabel: string;
  window: JourneySignalWindow;
  entity: JourneySignalEntityGraph;
  channels: JourneySignalChannelSummary[];
  signals: JourneySignal[];
  /** @minimum 0 */
  total: number;
}

export type JourneySignalBucket = typeof JourneySignalBucket[keyof typeof JourneySignalBucket];


export const JourneySignalBucket = {
  hour: 'hour',
  day: 'day',
  week: 'week',
} as const;

export interface JourneySignalTimePoint {
  t: string;
  /** @minimum 0 */
  inbound: number;
  /** @minimum 0 */
  outbound: number;
  /** @minimum 0 */
  internal: number;
}

export type JourneySignalTimeSeriesConnection = typeof JourneySignalTimeSeriesConnection[keyof typeof JourneySignalTimeSeriesConnection];


export const JourneySignalTimeSeriesConnection = {
  connected: 'connected',
  not_configured: 'not_configured',
  inbound_unavailable: 'inbound_unavailable',
  not_applicable: 'not_applicable',
} as const;

export interface JourneySignalTimeSeries {
  channel: string;
  label: string;
  stageId: string;
  connection: JourneySignalTimeSeriesConnection;
  /** @maxItems 400 */
  points: JourneySignalTimePoint[];
}

export interface JourneySignalSummaryTotals {
  /** @minimum 0 */
  signals: number;
  /** @minimum 0 */
  records: number;
}

export interface JourneySignalTimeseries {
  journeyKey: JourneyKey;
  recordId?: string;
  window: JourneySignalWindow;
  bucket: JourneySignalBucket;
  /**
     * Whole bucket units per point; weeks start on Monday UTC. Increases only to preserve the 400-point bound.
     * @minimum 1
     */
  bucketSize: number;
  from: string;
  to: string;
  series: JourneySignalTimeSeries[];
  totals: JourneySignalSummaryTotals;
}

export interface JourneySignalSummaryCell {
  stageId: string;
  channel: string;
  /** @minimum 0 */
  inboundCount: number;
  /** @minimum 0 */
  outboundCount: number;
  /** @minimum 0 */
  recordCount: number;
  lastAt: string;
}

export interface JourneySignalSummaryRecord {
  recordId: string;
  recordLabel: string;
  stageId: string;
  /** @nullable */
  owner: string | null;
  channel: string;
  lastAt: string;
  /** @minimum 1 */
  signalCount: number;
}

export interface JourneySignalSummary {
  journeyKey: JourneyKey;
  window: JourneySignalWindow;
  cells: JourneySignalSummaryCell[];
  /** @maxItems 200 */
  records: JourneySignalSummaryRecord[];
  totals: JourneySignalSummaryTotals;
}

export interface StageCoachingRequest {
  /**
     * @minLength 1
     * @maxLength 100
     */
  actionType: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  text: string;
}

export type AgentInstructionVersionStatus = typeof AgentInstructionVersionStatus[keyof typeof AgentInstructionVersionStatus];


export const AgentInstructionVersionStatus = {
  draft: 'draft',
  active: 'active',
  retired: 'retired',
} as const;

export type InstructionReviewStatus = typeof InstructionReviewStatus[keyof typeof InstructionReviewStatus];


export const InstructionReviewStatus = {
  awaiting_review: 'awaiting_review',
  activated: 'activated',
  declined: 'declined',
} as const;

export interface AgentInstructionVersion {
  id: string;
  actionType: string;
  /** @nullable */
  journeyKey?: string | null;
  /** @nullable */
  stageId?: string | null;
  /** @minimum 0 */
  version: number;
  content: string;
  note: string;
  status: AgentInstructionVersionStatus;
  /** @nullable */
  createdBy?: string | null;
  createdAt: string;
  /** @nullable */
  activatedBy?: string | null;
  /** @nullable */
  activatedAt?: string | null;
  /** @nullable */
  proposedBy: string | null;
  /** @nullable */
  proposedAt: string | null;
  reviewStatus: InstructionReviewStatus | null;
  /** @nullable */
  reviewNote: string | null;
  /** @nullable */
  reviewedBy: string | null;
  /** @nullable */
  reviewedAt: string | null;
}

export interface StageCoachingResult {
  draft: AgentInstructionVersion;
  effectiveInstruction: AgentInstructionVersion;
}

export interface CoachingStageCount {
  stageId: string;
  /** @minimum 0 */
  awaitingReview: number;
  /** @minimum 0 */
  activated: number;
  /** @minimum 0 */
  declined: number;
  /** @minimum 0 */
  total: number;
}

export interface JourneyCoachingList {
  items: AgentInstructionVersion[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
  stageCounts: CoachingStageCount[];
}

export interface RecordSteerRequest {
  /**
     * @minLength 1
     * @maxLength 2000
     */
  text: string;
  itemId?: string;
}

export interface RecordSteerNote {
  id: string;
  /** @nullable */
  itemId: string | null;
  text: string;
  stageId: string;
  createdBy: string;
  createdAt: string;
}

export type StageGuidanceStateStatus = typeof StageGuidanceStateStatus[keyof typeof StageGuidanceStateStatus];


export const StageGuidanceStateStatus = {
  empty: 'empty',
  ready: 'ready',
} as const;

export type StageGuidanceAvailability = typeof StageGuidanceAvailability[keyof typeof StageGuidanceAvailability];


export const StageGuidanceAvailability = {
  ready: 'ready',
  blocked: 'blocked',
} as const;

export interface StageGuidanceRecommendation {
  label: string;
  description: string;
  actionType: string;
}

export type StageGuidanceEvidenceState = typeof StageGuidanceEvidenceState[keyof typeof StageGuidanceEvidenceState];


export const StageGuidanceEvidenceState = {
  grounded: 'grounded',
  insufficient: 'insufficient',
  crm_only: 'crm_only',
  ready: 'ready',
  empty_index: 'empty_index',
  no_sources: 'no_sources',
  stale: 'stale',
  conflicting: 'conflicting',
  denied: 'denied',
  failed_ingestion: 'failed_ingestion',
  low_confidence: 'low_confidence',
} as const;

export type StageGuidanceEvidenceSourceType = typeof StageGuidanceEvidenceSourceType[keyof typeof StageGuidanceEvidenceSourceType];


export const StageGuidanceEvidenceSourceType = {
  crm_record: 'crm_record',
  uploaded_document: 'uploaded_document',
} as const;

export interface StageGuidanceEvidence {
  id: string;
  sourceType: StageGuidanceEvidenceSourceType;
  sourceIdentity: string;
  location: string;
  excerpt: string;
  freshness: string;
  /** @nullable */
  retrievalScore: number | null;
  href: string;
}

export interface StageGuidancePreparedOutput {
  title: string;
  body: string;
  editable: boolean;
  editedByUser: boolean;
}

export interface StageGuidance {
  journeyKey: JourneyKey;
  recordId: string;
  stageId: string;
  availability: StageGuidanceAvailability;
  summary: string;
  objective: string;
  missingInformation: string[];
  recommendation: StageGuidanceRecommendation | null;
  rationale: string;
  evidenceState: StageGuidanceEvidenceState;
  evidence: StageGuidanceEvidence[];
  citationIds: string[];
  limitations: string[];
  contextNotes: string[];
  preparedOutput: StageGuidancePreparedOutput | null;
  manualAlternative: string;
  generatedAt: string;
}

export type AuthorityLevel = typeof AuthorityLevel[keyof typeof AuthorityLevel];


export const AuthorityLevel = {
  automated: 'automated',
  recommended: 'recommended',
  prepared_with_approval: 'prepared_with_approval',
  human_only: 'human_only',
} as const;

export type AiActionAuditStatus = typeof AiActionAuditStatus[keyof typeof AiActionAuditStatus];


export const AiActionAuditStatus = {
  executed: 'executed',
  recommended: 'recommended',
  pending_approval: 'pending_approval',
  approved: 'approved',
  rejected: 'rejected',
  deferred: 'deferred',
  dismissed: 'dismissed',
  blocked: 'blocked',
  failed: 'failed',
} as const;

export type AiActionFeedbackRating = typeof AiActionFeedbackRating[keyof typeof AiActionFeedbackRating];


export const AiActionFeedbackRating = {
  helpful: 'helpful',
  not_helpful: 'not_helpful',
} as const;

export interface AiActionFeedback {
  rating: AiActionFeedbackRating;
  /** @maxLength 1000 */
  comment?: string;
}

export type AiActionAuditInput = { [key: string]: unknown };

/**
 * @nullable
 */
export type AiActionAuditOutput = { [key: string]: unknown } | null;

export interface AiActionAudit {
  id: string;
  actionType: string;
  authorityLevel: AuthorityLevel;
  status: AiActionAuditStatus;
  /** @nullable */
  requestId?: string | null;
  journeyKey?: JourneyKey | null;
  entityType?: JourneyEntityType | null;
  /** @nullable */
  entityId?: string | null;
  /** @nullable */
  stageId?: string | null;
  /** @minimum 1 */
  revision: number;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  contactId?: string | null;
  input: AiActionAuditInput;
  /** @nullable */
  output?: AiActionAuditOutput;
  /** @nullable */
  requestedBy?: string | null;
  /** @nullable */
  approvedBy?: string | null;
  /** @nullable */
  approvedAt?: string | null;
  /** @nullable */
  deferredUntil?: string | null;
  feedback?: AiActionFeedback | null;
  createdAt: string;
  updatedAt: string;
}

export interface StageGuidanceState {
  status: StageGuidanceStateStatus;
  guidance: StageGuidance | null;
  action: AiActionAudit | null;
  replayed?: boolean;
}

export interface RecordSteerResult {
  note: RecordSteerNote;
  guidance: StageGuidanceState;
  audit: AiActionAudit;
}

export interface StageGuidanceGenerateRequest {
  requestId: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  expectedStageId: string;
  expectedRecordUpdatedAt: string;
}

export interface JourneyTransitionRequest {
  requestId: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  fromStageId: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  toStageId: string;
  expectedUpdatedAt: string;
  /**
     * @minLength 1
     * @maxLength 500
     * @nullable
     */
  reason?: string | null;
}

export type JourneyTransitionHistoryItemKind = typeof JourneyTransitionHistoryItemKind[keyof typeof JourneyTransitionHistoryItemKind];


export const JourneyTransitionHistoryItemKind = {
  transition: 'transition',
  steer_note: 'steer_note',
} as const;

export interface JourneyTransitionHistoryItem {
  id: string;
  kind: JourneyTransitionHistoryItemKind;
  requestId: string;
  journeyKey: JourneyKey;
  entityType: JourneyEntityType;
  recordId: string;
  fromStageId: string;
  toStageId: string;
  /** @nullable */
  outcome?: string | null;
  /** @nullable */
  reason?: string | null;
  changedBy: string;
  occurredAt: string;
  /** @nullable */
  note: string | null;
  /** @nullable */
  itemId: string | null;
}

export interface JourneyTransitionHistoryList {
  items: JourneyTransitionHistoryItem[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface JourneyTransitionResult {
  state: JourneyRecordState;
  transition: JourneyTransitionHistoryItem;
  replayed: boolean;
}

export type PartnerAgreementType = typeof PartnerAgreementType[keyof typeof PartnerAgreementType];


export const PartnerAgreementType = {
  nda: 'nda',
  msa: 'msa',
  partner: 'partner',
  referral: 'referral',
  other: 'other',
} as const;

export type PartnerAgreementStatus = typeof PartnerAgreementStatus[keyof typeof PartnerAgreementStatus];


export const PartnerAgreementStatus = {
  draft: 'draft',
  pending: 'pending',
  active: 'active',
  expired: 'expired',
  terminated: 'terminated',
} as const;

export type PartnerInteractionType = typeof PartnerInteractionType[keyof typeof PartnerInteractionType];


export const PartnerInteractionType = {
  meeting: 'meeting',
  call: 'call',
  email: 'email',
  presentation: 'presentation',
  event: 'event',
  other: 'other',
} as const;

export type PartnerInteractionDirection = typeof PartnerInteractionDirection[keyof typeof PartnerInteractionDirection];


export const PartnerInteractionDirection = {
  inbound: 'inbound',
  outbound: 'outbound',
} as const;

export type PartnerInteractionStatus = typeof PartnerInteractionStatus[keyof typeof PartnerInteractionStatus];


export const PartnerInteractionStatus = {
  planned: 'planned',
  completed: 'completed',
  cancelled: 'cancelled',
} as const;

export interface PartnerBase {
  /** @nullable */
  website?: string | null;
  /** @nullable */
  organisationType?: string | null;
  /** @nullable */
  industry?: string | null;
  /** @nullable */
  size?: string | null;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  email?: string | null;
  /** @nullable */
  phone?: string | null;
  /** @nullable */
  addressLine1?: string | null;
  /** @nullable */
  addressLine2?: string | null;
  /** @nullable */
  city?: string | null;
  /** @nullable */
  stateProvince?: string | null;
  /** @nullable */
  postcode?: string | null;
  /** @nullable */
  country?: string | null;
  /** @nullable */
  accountManagerName?: string | null;
  /** @nullable */
  lastContactedAt?: string | null;
  isTestData?: boolean;
  /** @nullable */
  primaryContactName?: string | null;
  /** @nullable */
  primaryContactEmail?: string | null;
  /** @nullable */
  primaryContactPhone?: string | null;
  showPricesInQuotes?: boolean;
  /** @nullable */
  notes?: string | null;
}

export type Partner = PartnerBase & {
  id: string;
  name: string;
  type: PartnerType;
  status: PartnerStatus;
  createdAt: string;
  updatedAt: string;
};

export interface PartnerList {
  items: Partner[];
  /** @minimum 0 */
  total: number;
  limit?: number;
  offset?: number;
}

export type PartnerCreate = PartnerBase & {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  type?: PartnerType;
  status?: PartnerStatus;
};

export type PartnerUpdate = PartnerBase & {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  type?: PartnerType;
  status?: PartnerStatus;
};

export interface PartnerEmailBase {
  /** @nullable */
  name?: string | null;
  /** @nullable */
  role?: string | null;
  isActive?: boolean;
  isAdmin?: boolean;
}

export type PartnerEmail = PartnerEmailBase & ({
  id: string;
  accountId: string;
  email: string;
  /** @nullable */
  lastSignedInAt: string | null;
  createdAt: string;
}) & Required<Pick<PartnerEmailBase & ({
  id: string;
  accountId: string;
  email: string;
  /** @nullable */
  lastSignedInAt: string | null;
  createdAt: string;
}), 'isActive' | 'isAdmin'>>;

export interface PartnerEmailList {
  items: PartnerEmail[];
  /** @minimum 0 */
  total: number;
}

export type PartnerEmailCreate = PartnerEmailBase & {
  accountId: string;
  email: string;
};

export type PartnerEmailUpdate = PartnerEmailBase & {
  email?: string;
};

export interface PartnerAgreementBase {
  /** @nullable */
  signedDate?: string | null;
  /** @nullable */
  expiryDate?: string | null;
  /** @nullable */
  documentUrl?: string | null;
  /** @nullable */
  notes?: string | null;
}

export type PartnerAgreement = PartnerAgreementBase & {
  id: string;
  accountId: string;
  title: string;
  type: PartnerAgreementType;
  status: PartnerAgreementStatus;
  createdAt: string;
  updatedAt: string;
};

export interface PartnerAgreementList {
  items: PartnerAgreement[];
  /** @minimum 0 */
  total: number;
}

export type PartnerAgreementCreate = PartnerAgreementBase & {
  accountId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  type?: PartnerAgreementType;
  status?: PartnerAgreementStatus;
};

export type PartnerAgreementUpdate = PartnerAgreementBase & {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
  type?: PartnerAgreementType;
  status?: PartnerAgreementStatus;
};

export interface PartnerInteractionBase {
  /** @nullable */
  contactName?: string | null;
  /** @nullable */
  notes?: string | null;
  /** @nullable */
  followUpDate?: string | null;
}

export type PartnerInteraction = PartnerInteractionBase & {
  id: string;
  accountId: string;
  type: PartnerInteractionType;
  direction: PartnerInteractionDirection;
  subject: string;
  interactionDate: string;
  status: PartnerInteractionStatus;
  createdAt: string;
};

export interface PartnerInteractionList {
  items: PartnerInteraction[];
  /** @minimum 0 */
  total: number;
}

export type PartnerInteractionCreate = PartnerInteractionBase & {
  accountId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  subject: string;
  type?: PartnerInteractionType;
  direction?: PartnerInteractionDirection;
  interactionDate?: string;
  status?: PartnerInteractionStatus;
};

export type PartnerInteractionUpdate = PartnerInteractionBase & {
  /**
     * @minLength 1
     * @maxLength 200
     */
  subject?: string;
  type?: PartnerInteractionType;
  direction?: PartnerInteractionDirection;
  interactionDate?: string;
  status?: PartnerInteractionStatus;
};

export interface PartnerCollateralBase {
  /** @nullable */
  description?: string | null;
  /** @nullable */
  category?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

export type PartnerCollateral = PartnerCollateralBase & {
  id: string;
  accountId: string;
  title: string;
  href: string;
  createdAt: string;
};

export interface PartnerCollateralList {
  items: PartnerCollateral[];
  /** @minimum 0 */
  total: number;
}

export type PartnerCollateralCreate = PartnerCollateralBase & {
  accountId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  href: string;
};

export type PartnerCollateralUpdate = PartnerCollateralBase & {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  href?: string;
};

export interface PartnerDashboardStats {
  totalPartners: number;
  activePartners: number;
  linkedToAccount: number;
  /** Agreements with status active and expiryDate within 30 days. */
  agreementsExpiringSoon: number;
  interactionsLast30Days: number;
}

/**
 * Authorization role within the active portal context.
 */
export type PortalRole = typeof PortalRole[keyof typeof PortalRole];


export const PortalRole = {
  member: 'member',
  entity_admin: 'entity_admin',
  viewer: 'viewer',
  actor: 'actor',
} as const;

export interface PortalOnBehalfOf {
  name: string;
  email: string;
}

export type PortalContextKind = typeof PortalContextKind[keyof typeof PortalContextKind];


export const PortalContextKind = {
  organisation: 'organisation',
  self: 'self',
  delegate: 'delegate',
} as const;

export type PortalContextAccessType = typeof PortalContextAccessType[keyof typeof PortalContextAccessType];


export const PortalContextAccessType = {
  contact: 'contact',
  membership: 'membership',
  self: 'self',
  share: 'share',
} as const;

export interface PortalContext {
  kind: PortalContextKind;
  accessType: PortalContextAccessType;
  role: PortalRole;
  tenantId: string;
  ownerEntityId: string;
  /** @nullable */
  contactId: string | null;
  contactName: string;
  contactEmail: string;
  accountId: string;
  personAccountId?: string;
  membershipId?: string;
  shareId?: string;
  onBehalfOf?: PortalOnBehalfOf;
  companyName: string;
  relationshipType: AccountRelationshipType;
}

export interface PortalSignedInUser {
  email: string;
  name: string;
}

export interface PortalMe {
  role: PortalRole;
  /** True when the signed-in email matched a CRM contact or active portal-user email for an entity account. */
  matched: boolean;
  /** @nullable */
  contactName?: string | null;
  /** @nullable */
  companyName?: string | null;
  user: PortalSignedInUser;
  /** @minItems 1 */
  contexts: PortalContext[];
  active: PortalContext;
}

export interface PortalFinanceLastPayment {
  amount: string;
  /** @pattern ^[A-Z]{3}$ */
  currency: string;
  paymentDate: string;
  invoiceNumber: string;
}

export interface PortalFinanceSummary {
  outstandingTotal: string;
  /** @pattern ^[A-Z]{3}$ */
  currency: string;
  /** @minimum 0 */
  overdueCount: number;
  lastPayment: PortalFinanceLastPayment | null;
  connected: boolean;
  /** @nullable */
  lastSyncAt: string | null;
}

export type XeroInvoiceStatus = typeof XeroInvoiceStatus[keyof typeof XeroInvoiceStatus];


export const XeroInvoiceStatus = {
  DRAFT: 'DRAFT',
  SUBMITTED: 'SUBMITTED',
  AUTHORISED: 'AUTHORISED',
  PAID: 'PAID',
  VOIDED: 'VOIDED',
} as const;

export interface PortalFinanceInvoice {
  id: string;
  invoiceNumber: string;
  /** @nullable */
  reference: string | null;
  status: XeroInvoiceStatus;
  subTotal: string;
  totalTax: string;
  total: string;
  /** @pattern ^[A-Z]{3}$ */
  currency: string;
  issueDate: string;
  /** @nullable */
  dueDate: string | null;
  lastSyncedAt: string;
}

export interface PortalFinanceInvoiceLine {
  id: string;
  description: string;
  quantity: string;
  /** @nullable */
  unitAmount: string | null;
  /** @nullable */
  lineAmount: string | null;
  taxAmount: string;
  sortOrder: number;
}

export type PortalFinanceInvoiceDetail = PortalFinanceInvoice & {
  lines: PortalFinanceInvoiceLine[];
};

export interface PortalFinanceInvoiceList {
  data: PortalFinanceInvoice[];
  /** @minimum 0 */
  total: number;
}

export type PortalFinancePaymentMethod = typeof PortalFinancePaymentMethod[keyof typeof PortalFinancePaymentMethod];


export const PortalFinancePaymentMethod = {
  bank_transfer: 'bank_transfer',
  manual: 'manual',
  card: 'card',
  other: 'other',
} as const;

export interface PortalFinancePayment {
  id: string;
  invoiceId: string;
  invoiceNumber: string;
  amount: string;
  /** @pattern ^[A-Z]{3}$ */
  currency: string;
  paymentDate: string;
  method: PortalFinancePaymentMethod;
  /** @nullable */
  reference: string | null;
}

export interface PortalFinancePaymentList {
  data: PortalFinancePayment[];
  /** @minimum 0 */
  total: number;
}

export interface PortalEnquiry {
  id: string;
  subject: string;
  status: EnquiryStatus;
  /** @nullable */
  submittedByEmail: string | null;
  /** @nullable */
  submittedOnBehalfOf: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PortalOpportunity {
  id: string;
  title: string;
  /** Customer-friendly wording of the internal pipeline stage. */
  stageLabel: string;
  updatedAt: string;
}

export interface PortalUpdate {
  id: string;
  body: string;
  /** Which record this update relates to (enquiry/engagement). */
  context: string;
  createdAt: string;
}

export interface PortalOverview {
  companyName: string;
  enquiries: PortalEnquiry[];
  opportunities: PortalOpportunity[];
  updates: PortalUpdate[];
}

export type PortalProfileAccountKind = typeof PortalProfileAccountKind[keyof typeof PortalProfileAccountKind];


export const PortalProfileAccountKind = {
  organization: 'organization',
  person: 'person',
} as const;

/**
 * External-safe entity profile. Internal owners, notes, scoring and classifications are omitted.
 */
export interface PortalProfile {
  name: string;
  accountKind: PortalProfileAccountKind;
  /** @nullable */
  organisationType: string | null;
  /** @nullable */
  industry: string | null;
  /** @nullable */
  website: string | null;
  /** @nullable */
  email: string | null;
  /** @nullable */
  phone: string | null;
  /** @nullable */
  addressLine1: string | null;
  /** @nullable */
  addressLine2: string | null;
  /** @nullable */
  city: string | null;
  /** @nullable */
  stateProvince: string | null;
  /** @nullable */
  postcode: string | null;
  /** @nullable */
  country: string | null;
}

export interface PortalProfileResponse {
  profile: PortalProfile | null;
}

/**
 * External-safe agreement summary. Internal notes and document locations are omitted.
 */
export interface PortalAgreement {
  id: string;
  title: string;
  type: PartnerAgreementType;
  status: PartnerAgreementStatus;
  /** @nullable */
  signedDate: string | null;
  /** @nullable */
  expiryDate: string | null;
}

export interface PortalAgreementList {
  items: PortalAgreement[];
  /** @minimum 0 */
  total: number;
}

/**
 * @nullable
 */
export type PortalProductBillingFrequency = typeof PortalProductBillingFrequency[keyof typeof PortalProductBillingFrequency] | null;


export const PortalProductBillingFrequency = {
  monthly: 'monthly',
  quarterly: 'quarterly',
  annual: 'annual',
  one_time: 'one_time',
} as const;

export type PartnerProductItemType = typeof PartnerProductItemType[keyof typeof PartnerProductItemType];


export const PartnerProductItemType = {
  product: 'product',
  module: 'module',
  service: 'service',
} as const;

export type PartnerProductStatus = typeof PartnerProductStatus[keyof typeof PartnerProductStatus];


export const PartnerProductStatus = {
  active: 'active',
  pending: 'pending',
  expired: 'expired',
  cancelled: 'cancelled',
} as const;

/**
 * External-safe product or service summary. Internal notes, values and catalogue identifiers are omitted.
 */
export interface PortalProduct {
  id: string;
  name: string;
  itemType: PartnerProductItemType;
  status: PartnerProductStatus;
  /** @nullable */
  procuredDate: string | null;
  /** @nullable */
  startDate: string | null;
  /** @nullable */
  expirationDate: string | null;
  /** @nullable */
  renewalDate: string | null;
  autoRenew: boolean;
  /** @nullable */
  billingFrequency: PortalProductBillingFrequency;
}

export interface PortalProductList {
  items: PortalProduct[];
  /** @minimum 0 */
  total: number;
}

/**
 * External-safe interaction summary. Internal notes and follow-up metadata are omitted.
 */
export interface PortalInteraction {
  id: string;
  /** @nullable */
  contactName: string | null;
  type: PartnerInteractionType;
  direction: PartnerInteractionDirection;
  subject: string;
  interactionDate: string;
  status: PartnerInteractionStatus;
}

export interface PortalInteractionList {
  items: PortalInteraction[];
  /** @minimum 0 */
  total: number;
}

export interface PortalUser {
  id: string;
  email: string;
  /** @nullable */
  name: string | null;
  /** @nullable */
  role: string | null;
  isActive: boolean;
  isAdmin: boolean;
  /** @nullable */
  lastSignedInAt: string | null;
}

export interface PortalUserList {
  items: PortalUser[];
  /** @minimum 0 */
  total: number;
}

export interface PortalUserCreate {
  email: string;
  /**
     * @maxLength 200
     * @nullable
     */
  name?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  role?: string | null;
}

export interface PortalUserUpdate {
  isActive?: boolean;
  isAdmin?: boolean;
}

export interface PortalEnquiryCreate {
  /** @maxLength 200 */
  subject?: string;
  /**
     * @minLength 1
     * @maxLength 5000
     */
  message: string;
}

export type PortalSharePermission = typeof PortalSharePermission[keyof typeof PortalSharePermission];


export const PortalSharePermission = {
  view: 'view',
  act: 'act',
} as const;

export interface PortalShare {
  id: string;
  ownerAccountId: string;
  granteeEmail: string;
  /** @nullable */
  granteeName: string | null;
  permission: PortalSharePermission;
  /** @nullable */
  expiresAt: string | null;
  /** @nullable */
  revokedAt: string | null;
  createdByEmail: string;
  createdAt: string;
  updatedAt: string;
}

export interface PortalShareList {
  items: PortalShare[];
  /** @minimum 0 */
  total: number;
}

export interface PortalShareCreate {
  granteeEmail: string;
  /**
     * @maxLength 200
     * @nullable
     */
  granteeName?: string | null;
  permission: PortalSharePermission;
  /** @nullable */
  expiresAt?: string | null;
}

export interface PortalShareUpdate {
  permission?: PortalSharePermission;
  /** @nullable */
  expiresAt?: string | null;
}

export interface ServiceAreaBase {
  /** @nullable */
  description?: string | null;
  isActive?: boolean;
  sortOrder?: number;
}

export type ServiceArea = ServiceAreaBase & {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
} & Required<Pick<ServiceAreaBase & {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
}, 'isActive' | 'sortOrder'>>;

export interface ServiceAreaList {
  items: ServiceArea[];
  /** @minimum 0 */
  total: number;
}

export type ServiceAreaCreate = ServiceAreaBase & {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug: string;
};

export type ServiceAreaUpdate = ServiceAreaBase & {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug?: string;
};

export interface ServiceLineBase {
  /** @nullable */
  description?: string | null;
  isActive?: boolean;
  sortOrder?: number;
}

export type ServiceLine = ServiceLineBase & {
  id: string;
  areaId: string;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
} & Required<Pick<ServiceLineBase & {
  id: string;
  areaId: string;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
}, 'isActive' | 'sortOrder'>>;

export interface ServiceLineList {
  items: ServiceLine[];
  /** @minimum 0 */
  total: number;
}

export type ServiceLineCreate = ServiceLineBase & {
  areaId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug: string;
};

export type ServiceLineUpdate = ServiceLineBase & {
  areaId?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug?: string;
};

export interface ServiceComponentBase {
  /** @nullable */
  description?: string | null;
  /** @nullable */
  effortHours?: string | null;
  isActive?: boolean;
  sortOrder?: number;
}

export type ServiceComponent = ServiceComponentBase & {
  id: string;
  lineId: string;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
} & Required<Pick<ServiceComponentBase & {
  id: string;
  lineId: string;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
}, 'isActive' | 'sortOrder'>>;

export interface ServiceComponentList {
  items: ServiceComponent[];
  /** @minimum 0 */
  total: number;
}

export type ServiceComponentCreate = ServiceComponentBase & {
  lineId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug: string;
};

export type ServiceComponentUpdate = ServiceComponentBase & {
  lineId?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug?: string;
};

export type ServiceLineNode = ServiceLine & {
  components: ServiceComponent[];
};

export type ServiceAreaNode = ServiceArea & {
  lines: ServiceLineNode[];
};

export interface ServiceHierarchy {
  areas: ServiceAreaNode[];
}

export interface CatalogueDeliverableBase {
  /** @nullable */
  description?: string | null;
  /** @nullable */
  category?: string | null;
  /** @nullable */
  estimatedHours?: string | null;
  /** @nullable */
  productId?: string | null;
  /** @nullable */
  moduleId?: string | null;
  isActive?: boolean;
  sortOrder?: number;
}

export type CatalogueDeliverable = CatalogueDeliverableBase & {
  id: string;
  code: string;
  title: string;
  createdAt: string;
  updatedAt: string;
} & Required<Pick<CatalogueDeliverableBase & {
  id: string;
  code: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}, 'isActive' | 'sortOrder'>>;

export interface CatalogueDeliverableList {
  items: CatalogueDeliverable[];
  /** @minimum 0 */
  total: number;
}

export type CatalogueDeliverableCreate = CatalogueDeliverableBase & {
  /** Optional explicit code; auto-generated from category when omitted. */
  code?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
};

export type CatalogueDeliverableUpdate = CatalogueDeliverableBase & {
  code?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
};

export interface NextDeliverableCode {
  code: string;
}

export interface CatalogueServiceBase {
  /** @nullable */
  componentId?: string | null;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  shortDescription?: string | null;
  serviceType?: string;
  /** @nullable */
  servicePrice?: string | null;
  /** @nullable */
  effortHours?: string | null;
  isFeatured?: boolean;
  isActive?: boolean;
  sortOrder?: number;
}

export type CatalogueService = CatalogueServiceBase & {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
} & Required<Pick<CatalogueServiceBase & {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
}, 'serviceType' | 'isFeatured' | 'isActive' | 'sortOrder'>>;

export interface CatalogueServiceList {
  items: CatalogueService[];
  /** @minimum 0 */
  total: number;
}

export type CatalogueServiceCreate = CatalogueServiceBase & {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug: string;
};

export type CatalogueServiceUpdate = CatalogueServiceBase & {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug?: string;
};

export interface CatalogueServiceLinkBase {
  relationshipType?: string;
  /** @nullable */
  notes?: string | null;
}

export type CatalogueServiceLinkItemType = typeof CatalogueServiceLinkItemType[keyof typeof CatalogueServiceLinkItemType];


export const CatalogueServiceLinkItemType = {
  category: 'category',
  solution: 'solution',
  product: 'product',
  module: 'module',
  capability: 'capability',
} as const;

export type CatalogueServiceLink = CatalogueServiceLinkBase & {
  id: string;
  itemType: CatalogueServiceLinkItemType;
  itemId: string;
  offeringId: string;
  createdAt: string;
} & Required<Pick<CatalogueServiceLinkBase & {
  id: string;
  itemType: CatalogueServiceLinkItemType;
  itemId: string;
  offeringId: string;
  createdAt: string;
}, 'relationshipType'>>;

export interface CatalogueServiceLinkList {
  items: CatalogueServiceLink[];
  /** @minimum 0 */
  total: number;
}

export type CatalogueServiceLinkCreateItemType = typeof CatalogueServiceLinkCreateItemType[keyof typeof CatalogueServiceLinkCreateItemType];


export const CatalogueServiceLinkCreateItemType = {
  category: 'category',
  solution: 'solution',
  product: 'product',
  module: 'module',
  capability: 'capability',
} as const;

export type CatalogueServiceLinkCreate = CatalogueServiceLinkBase & {
  itemType: CatalogueServiceLinkCreateItemType;
  itemId: string;
  offeringId: string;
};

export interface CatalogueCollateralBase {
  /** @nullable */
  description?: string | null;
  /** @nullable */
  category?: string | null;
  /** @nullable */
  fileId?: string | null;
  /** @nullable */
  url?: string | null;
  /** @nullable */
  productId?: string | null;
  /** @nullable */
  solutionId?: string | null;
  isActive?: boolean;
  sortOrder?: number;
}

export type CatalogueCollateral = CatalogueCollateralBase & {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
} & Required<Pick<CatalogueCollateralBase & {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}, 'isActive' | 'sortOrder'>>;

export interface CatalogueCollateralList {
  items: CatalogueCollateral[];
  /** @minimum 0 */
  total: number;
}

export type CatalogueCollateralCreate = CatalogueCollateralBase & {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
};

export type CatalogueCollateralUpdate = CatalogueCollateralBase & {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
};

/**
 * @nullable
 */
export type PartnerProductBaseBillingFrequency = typeof PartnerProductBaseBillingFrequency[keyof typeof PartnerProductBaseBillingFrequency] | null;


export const PartnerProductBaseBillingFrequency = {
  monthly: 'monthly',
  quarterly: 'quarterly',
  annual: 'annual',
  one_time: 'one_time',
} as const;

export interface PartnerProductBase {
  itemType?: PartnerProductItemType;
  /** @nullable */
  catalogueProductId?: string | null;
  /** @nullable */
  catalogueModuleId?: string | null;
  /** @nullable */
  catalogueServiceId?: string | null;
  status?: PartnerProductStatus;
  /** @nullable */
  procuredDate?: string | null;
  /** @nullable */
  startDate?: string | null;
  /** @nullable */
  expirationDate?: string | null;
  /** @nullable */
  renewalDate?: string | null;
  autoRenew?: boolean;
  /** @nullable */
  value?: number | null;
  currency?: string;
  /** @nullable */
  billingFrequency?: PartnerProductBaseBillingFrequency;
  /** @nullable */
  notes?: string | null;
}

export type PartnerProduct = PartnerProductBase & {
  id: string;
  accountId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
} & Required<Pick<PartnerProductBase & {
  id: string;
  accountId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}, 'itemType' | 'status' | 'autoRenew' | 'currency'>>;

export interface PartnerProductList {
  items: PartnerProduct[];
  /** @minimum 0 */
  total: number;
}

export type PartnerProductCreate = PartnerProductBase & {
  accountId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
};

export type PartnerProductUpdate = PartnerProductBase & {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
};

export interface PartnerProductRenew {
  /** @nullable */
  renewalDate?: string | null;
  /** @nullable */
  expirationDate?: string | null;
}

/**
 * Arbitrary jsonb template structure (sections, prompts, default figures).
 */
export type BusinessCaseTemplateTemplateData = { [key: string]: unknown };

export interface BusinessCaseTemplate {
  id: string;
  name: string;
  slug: string;
  /** @nullable */
  description?: string | null;
  isPublished: boolean;
  /** Arbitrary jsonb template structure (sections, prompts, default figures). */
  templateData: BusinessCaseTemplateTemplateData;
  /** @nullable */
  catalogueProductId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BusinessCaseTemplateList {
  items: BusinessCaseTemplate[];
  /** @minimum 0 */
  total: number;
}

export type BusinessCaseTemplateCreateTemplateData = { [key: string]: unknown };

export interface BusinessCaseTemplateCreate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug: string;
  /** @nullable */
  description?: string | null;
  isPublished?: boolean;
  templateData?: BusinessCaseTemplateCreateTemplateData;
  /** @nullable */
  catalogueProductId?: string | null;
}

export type BusinessCaseTemplateUpdateTemplateData = { [key: string]: unknown };

export interface BusinessCaseTemplateUpdate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug?: string;
  /** @nullable */
  description?: string | null;
  isPublished?: boolean;
  templateData?: BusinessCaseTemplateUpdateTemplateData;
  /** @nullable */
  catalogueProductId?: string | null;
}

export type SavedBusinessCaseCaseData = { [key: string]: unknown };

export interface SavedBusinessCase {
  id: string;
  templateId: string;
  name: string;
  /** @nullable */
  description?: string | null;
  isDefault: boolean;
  caseData: SavedBusinessCaseCaseData;
  /** @nullable */
  accountId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SavedBusinessCaseList {
  items: SavedBusinessCase[];
  /** @minimum 0 */
  total: number;
}

export type SavedBusinessCaseCreateCaseData = { [key: string]: unknown };

export interface SavedBusinessCaseCreate {
  templateId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /** @nullable */
  description?: string | null;
  isDefault?: boolean;
  caseData?: SavedBusinessCaseCreateCaseData;
  /** @nullable */
  accountId?: string | null;
}

export type SavedBusinessCaseUpdateCaseData = { [key: string]: unknown };

export interface SavedBusinessCaseUpdate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /** @nullable */
  description?: string | null;
  caseData?: SavedBusinessCaseUpdateCaseData;
  /** @nullable */
  accountId?: string | null;
}

export interface DocumentSuite {
  id: string;
  name: string;
  slug: string;
  /** @nullable */
  description?: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentSuiteCreate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug: string;
  /** @nullable */
  description?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

export interface DocumentSuiteUpdate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug?: string;
  /** @nullable */
  description?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

export type CatalogueDocumentItemType = typeof CatalogueDocumentItemType[keyof typeof CatalogueDocumentItemType];


export const CatalogueDocumentItemType = {
  category: 'category',
  solution: 'solution',
  product: 'product',
  module: 'module',
  capability: 'capability',
} as const;

export interface CatalogueDocument {
  id: string;
  suiteId: string;
  title: string;
  slug: string;
  /** @nullable */
  description?: string | null;
  itemType?: CatalogueDocumentItemType | null;
  /** @nullable */
  itemId?: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CatalogueDocumentCreate {
  suiteId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug: string;
  /** @nullable */
  description?: string | null;
  itemType?: CatalogueDocumentItemType | null;
  /** @nullable */
  itemId?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

export interface CatalogueDocumentUpdate {
  suiteId?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  slug?: string;
  /** @nullable */
  description?: string | null;
  itemType?: CatalogueDocumentItemType | null;
  /** @nullable */
  itemId?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

export type ArtifactType = typeof ArtifactType[keyof typeof ArtifactType];


export const ArtifactType = {
  datasheet: 'datasheet',
  case_study: 'case_study',
  whitepaper: 'whitepaper',
  presentation: 'presentation',
  video: 'video',
  other: 'other',
} as const;

export interface CatalogueArtifact {
  id: string;
  documentId: string;
  title: string;
  /** @nullable */
  description?: string | null;
  artifactType: ArtifactType;
  /** @nullable */
  fileId?: string | null;
  /** @nullable */
  url?: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CatalogueArtifactCreate {
  documentId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /** @nullable */
  description?: string | null;
  artifactType?: ArtifactType;
  /** @nullable */
  fileId?: string | null;
  /** @nullable */
  url?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

export interface CatalogueArtifactUpdate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
  /** @nullable */
  description?: string | null;
  artifactType?: ArtifactType;
  /** @nullable */
  fileId?: string | null;
  /** @nullable */
  url?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

export type KnowledgeChannel = typeof KnowledgeChannel[keyof typeof KnowledgeChannel];


export const KnowledgeChannel = {
  agent: 'agent',
  content_generate: 'content_generate',
  quotes: 'quotes',
  guidance: 'guidance',
  public_chat: 'public_chat',
  voice: 'voice',
  microsites: 'microsites',
} as const;

export interface WebHarvestStart {
  /** @maxLength 2048 */
  url: string;
  channels?: KnowledgeChannel[];
}

export interface WebHarvestArticleGenerate {
  /**
     * @minLength 1
     * @maxLength 4000
     */
  prompt?: string;
}

export interface WebHarvestArticleSave {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 100000
     */
  markdown: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  promptUsed: string;
  channels?: KnowledgeChannel[];
}

export interface WebHarvestArticleDraft {
  title: string;
  markdown: string;
  citedUrls: string[];
  promptUsed: string;
}

export interface WebHarvestFailure {
  url: string;
  title: string;
  error: string;
}

export type KnowledgeFileStatus = typeof KnowledgeFileStatus[keyof typeof KnowledgeFileStatus];


export const KnowledgeFileStatus = {
  uploaded: 'uploaded',
  analyzing: 'analyzing',
  analyzed: 'analyzed',
  stored: 'stored',
  extracting: 'extracting',
  chunking: 'chunking',
  embedding: 'embedding',
  ready: 'ready',
  failed: 'failed',
} as const;

export type KnowledgeSourceKind = typeof KnowledgeSourceKind[keyof typeof KnowledgeSourceKind];


export const KnowledgeSourceKind = {
  upload: 'upload',
  sharepoint: 'sharepoint',
  web: 'web',
} as const;

export interface KnowledgeFile {
  id: string;
  fileId: string;
  /** @nullable */
  suiteId?: string | null;
  title: string;
  status: KnowledgeFileStatus;
  /** @nullable */
  ragDocumentId?: string | null;
  /** @nullable */
  errorMessage?: string | null;
  approvedForVoice: boolean;
  sourceKind: KnowledgeSourceKind;
  /** @nullable */
  sourceUrl: string | null;
  /** @nullable */
  harvestRunId: string | null;
  /** @nullable */
  harvestPrompt: string | null;
  channels: KnowledgeChannel[];
  /** @minimum 1 */
  revision: number;
  /** @nullable */
  lastIndexedAt?: string | null;
  /** @nullable */
  uploadedByUserId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WebHarvestRun {
  runId: string;
  rootUrl: string;
  /** @nullable */
  createdByUserId: string | null;
  createdAt: string;
  articleExists: boolean;
  /** @maxItems 6 */
  sources: KnowledgeFile[];
  /** @maxItems 5 */
  failures: WebHarvestFailure[];
}

export type KnowledgeIngestionStage = typeof KnowledgeIngestionStage[keyof typeof KnowledgeIngestionStage];


export const KnowledgeIngestionStage = {
  stored: 'stored',
  extracting: 'extracting',
  chunking: 'chunking',
  embedding: 'embedding',
  ready: 'ready',
  failed: 'failed',
} as const;

export interface KnowledgeStageHistoryEntry {
  stage: KnowledgeIngestionStage;
  at: string;
  detail?: string;
}

export interface KnowledgeFileCreate {
  fileId: string;
  /** @nullable */
  suiteId?: string | null;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  sourceKind?: KnowledgeSourceKind;
  /** @nullable */
  sourceUrl?: string | null;
  /** @nullable */
  harvestRunId?: string | null;
  /** @nullable */
  harvestPrompt?: string | null;
  channels?: KnowledgeChannel[];
}

export interface KnowledgeChannelsUpdate {
  channels: KnowledgeChannel[];
}

export interface KnowledgeVoiceApproval {
  approvedForVoice: boolean;
  /** @minimum 1 */
  expectedRevision: number;
}

export type AnalysisSessionStatus = typeof AnalysisSessionStatus[keyof typeof AnalysisSessionStatus];


export const AnalysisSessionStatus = {
  pending: 'pending',
  running: 'running',
  completed: 'completed',
  failed: 'failed',
} as const;

export interface AnalysisSession {
  id: string;
  /** @nullable */
  requestId?: string | null;
  knowledgeFileId: string;
  status: AnalysisSessionStatus;
  /** @nullable */
  summary?: string | null;
  /** @nullable */
  chunkCount?: number | null;
  /** @nullable */
  errorMessage?: string | null;
  stage: KnowledgeIngestionStage;
  stageHistory: KnowledgeStageHistoryEntry[];
  /** @nullable */
  startedAt?: string | null;
  /** @nullable */
  completedAt?: string | null;
  createdAt: string;
}

export interface KnowledgeFileAnalysisResult {
  knowledgeFile: KnowledgeFile;
  session: AnalysisSession;
  replayed: boolean;
}

export interface KnowledgeFileAnalysisRequest {
  requestId: string;
  /** @minimum 1 */
  expectedRevision: number;
}

export interface KnowledgeFileReplacement {
  fileId: string;
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
}

export interface KnowledgeSourceChunk {
  id: string;
  /** @minimum 0 */
  chunkIndex: number;
  location: string;
  content: string;
  createdAt: string;
}

export type KnowledgeSharePointItemSourceStatus = typeof KnowledgeSharePointItemSourceStatus[keyof typeof KnowledgeSharePointItemSourceStatus];


export const KnowledgeSharePointItemSourceStatus = {
  current: 'current',
  removed_upstream: 'removed_upstream',
  disconnected: 'disconnected',
} as const;

export type KnowledgeSharePointItemOutcome = typeof KnowledgeSharePointItemOutcome[keyof typeof KnowledgeSharePointItemOutcome];


export const KnowledgeSharePointItemOutcome = {
  created: 'created',
  updated: 'updated',
  unchanged: 'unchanged',
  retained: 'retained',
  skipped: 'skipped',
  failed: 'failed',
  folder: 'folder',
} as const;

export interface KnowledgeSharePointItem {
  id: string;
  driveId: string;
  driveItemId: string;
  /** @nullable */
  pendingKnowledgeFileId?: string | null;
  stored: boolean;
  format: string;
  /** @nullable */
  knowledgeFileId?: string | null;
  name: string;
  webUrl: string;
  sourcePath?: string;
  /** @nullable */
  etag?: string | null;
  /** @nullable */
  importedAt?: string | null;
  sourceStatus: KnowledgeSharePointItemSourceStatus;
  outcome: KnowledgeSharePointItemOutcome;
  indexed: boolean;
  /** @nullable */
  reason?: string | null;
}

export interface KnowledgeSource {
  knowledgeFile: KnowledgeFile;
  filename: string;
  sharePoint?: KnowledgeSharePointItem | null;
  chunks: KnowledgeSourceChunk[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  page: number;
  /** @minimum 1 */
  limit: number;
}

export type RetrievalRuntimeHealthLastState = typeof RetrievalRuntimeHealthLastState[keyof typeof RetrievalRuntimeHealthLastState];


export const RetrievalRuntimeHealthLastState = {
  ready: 'ready',
  empty_index: 'empty_index',
  no_sources: 'no_sources',
  stale: 'stale',
  conflicting: 'conflicting',
  denied: 'denied',
  failed_ingestion: 'failed_ingestion',
  low_confidence: 'low_confidence',
} as const;

export interface RetrievalRuntimeHealth {
  /** @minimum 0 */
  requests: number;
  /** @minimum 0 */
  returnedSources: number;
  /** @minimum 0 */
  deniedMatches: number;
  /** @minimum 0 */
  lowConfidenceQueries: number;
  lastState: RetrievalRuntimeHealthLastState;
  /** @nullable */
  lastRetrievedAt: string | null;
}

export interface KnowledgeHealthFailure {
  id: string;
  title: string;
  errorMessage: string;
  updatedAt: string;
}

export type KnowledgeSharePointHealthFailureStatus = typeof KnowledgeSharePointHealthFailureStatus[keyof typeof KnowledgeSharePointHealthFailureStatus];


export const KnowledgeSharePointHealthFailureStatus = {
  reauth_required: 'reauth_required',
  partial: 'partial',
  failed: 'failed',
} as const;

export interface KnowledgeSharePointHealthFailure {
  connectionId: string;
  sourceName: string;
  status: KnowledgeSharePointHealthFailureStatus;
  error: string;
  /** @nullable */
  finishedAt: string | null;
}

export interface KnowledgeSharePointHealth {
  /** @minimum 0 */
  totalConnections: number;
  /** @minimum 0 */
  enabledConnections: number;
  /** @minimum 0 */
  reauthRequiredConnections: number;
  /** @minimum 0 */
  failingConnections: number;
  lastSyncFailures: KnowledgeSharePointHealthFailure[];
}

export type KnowledgeHealthState = typeof KnowledgeHealthState[keyof typeof KnowledgeHealthState];


export const KnowledgeHealthState = {
  empty: 'empty',
  not_ready: 'not_ready',
  ready: 'ready',
} as const;

export interface KnowledgeHealth {
  state: KnowledgeHealthState;
  /** @minimum 0 */
  totalFiles: number;
  /** @minimum 0 */
  readyFiles: number;
  /** @minimum 0 */
  failedFiles: number;
  /** @minimum 0 */
  processingFiles: number;
  /** @minimum 0 */
  staleFiles: number;
  /** @minimum 0 */
  documentCount: number;
  /** @minimum 0 */
  chunkCount: number;
  /** @minimum 0 */
  embeddedChunkCount: number;
  /**
     * @minimum 0
     * @maximum 1
     */
  embeddingCoverage: number;
  supportedTypes: string[];
  /** @minimum 1 */
  staleAfterDays: number;
  failures: KnowledgeHealthFailure[];
  sharePoint: KnowledgeSharePointHealth;
  retrieval: RetrievalRuntimeHealth;
  checkedAt: string;
}

export interface KnowledgeSharePointAuthorization {
  authorizationUrl: string;
}

export type KnowledgeSharePointMode = typeof KnowledgeSharePointMode[keyof typeof KnowledgeSharePointMode];


export const KnowledgeSharePointMode = {
  mirror: 'mirror',
  manual_retained: 'manual_retained',
} as const;

export interface KnowledgeSharePointItemPage {
  items: KnowledgeSharePointItem[];
  total: number;
  page: number;
  limit: number;
}

export type KnowledgeSharePointConnectionStatus = typeof KnowledgeSharePointConnectionStatus[keyof typeof KnowledgeSharePointConnectionStatus];


export const KnowledgeSharePointConnectionStatus = {
  connected: 'connected',
  reauth_required: 'reauth_required',
} as const;

export type KnowledgeSharePointConnectionLastSyncStatus = typeof KnowledgeSharePointConnectionLastSyncStatus[keyof typeof KnowledgeSharePointConnectionLastSyncStatus];


export const KnowledgeSharePointConnectionLastSyncStatus = {
  idle: 'idle',
  running: 'running',
  succeeded: 'succeeded',
  partial: 'partial',
  failed: 'failed',
} as const;

export interface KnowledgeSharePointConnection {
  id: string;
  accountDisplayName: string;
  mode?: KnowledgeSharePointMode;
  /** @nullable */
  lastSuccessfulRefreshAt?: string | null;
  /** @nullable */
  disconnectedAt?: string | null;
  authStatus: KnowledgeSharePointConnectionStatus;
  sourceConfigured: boolean;
  siteId: string;
  siteName: string;
  siteUrl: string;
  driveId: string;
  driveName: string;
  /** @nullable */
  folderItemId?: string | null;
  folderPath: string;
  /**
     * @minimum 5
     * @maximum 10080
     */
  scheduleMinutes: number;
  enabled: boolean;
  /** @nullable */
  lastSyncStartedAt?: string | null;
  /** @nullable */
  lastSyncFinishedAt?: string | null;
  lastSyncStatus: KnowledgeSharePointConnectionLastSyncStatus;
  /** @nullable */
  lastError?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface KnowledgeSharePointConnectionUpdate {
  mode?: KnowledgeSharePointMode;
  /**
     * @minLength 1
     * @maxLength 512
     */
  siteId?: string;
  /**
     * @minLength 1
     * @maxLength 300
     */
  siteName?: string;
  /** @maxLength 2048 */
  siteUrl?: string;
  /**
     * @minLength 1
     * @maxLength 512
     */
  driveId?: string;
  /**
     * @minLength 1
     * @maxLength 300
     */
  driveName?: string;
  /**
     * @maxLength 512
     * @nullable
     */
  folderItemId?: string | null;
  /** @maxLength 2048 */
  folderPath?: string;
  /**
     * @minimum 5
     * @maximum 10080
     */
  scheduleMinutes?: number;
  enabled?: boolean;
}

export interface KnowledgeSharePointSite {
  id: string;
  name: string;
  displayName: string;
  webUrl: string;
}

export interface KnowledgeSharePointDrive {
  id: string;
  name: string;
  webUrl: string;
  driveType: string;
}

export interface KnowledgeSharePointFolder {
  id: string;
  name: string;
  webUrl: string;
  /** @minimum 0 */
  childCount: number;
}

export interface KnowledgeSharePointSitePage {
  items: KnowledgeSharePointSite[];
  /** @nullable */
  nextCursor: string | null;
}

export interface KnowledgeSharePointDrivePage {
  items: KnowledgeSharePointDrive[];
  /** @nullable */
  nextCursor: string | null;
}

export interface KnowledgeSharePointFolderPage {
  items: KnowledgeSharePointFolder[];
  /** @nullable */
  nextCursor: string | null;
}

export type KnowledgeSharePointSyncRunStatus = typeof KnowledgeSharePointSyncRunStatus[keyof typeof KnowledgeSharePointSyncRunStatus];


export const KnowledgeSharePointSyncRunStatus = {
  running: 'running',
  succeeded: 'succeeded',
  partial: 'partial',
  failed: 'failed',
} as const;

export type KnowledgeSharePointSyncRunTrigger = typeof KnowledgeSharePointSyncRunTrigger[keyof typeof KnowledgeSharePointSyncRunTrigger];


export const KnowledgeSharePointSyncRunTrigger = {
  schedule: 'schedule',
  manual: 'manual',
} as const;

export interface KnowledgeSharePointSyncRun {
  id: string;
  connectionId: string;
  startedAt: string;
  /** @nullable */
  finishedAt: string | null;
  status: KnowledgeSharePointSyncRunStatus;
  /** @minimum 0 */
  createdCount: number;
  /** @minimum 0 */
  updatedCount: number;
  /** @minimum 0 */
  retiredCount: number;
  /** @minimum 0 */
  retainedCount?: number;
  /** @minimum 0 */
  unchangedCount?: number;
  /** @minimum 0 */
  skippedCount: number;
  /** @minimum 0 */
  failedCount: number;
  /** @nullable */
  error: string | null;
  trigger: KnowledgeSharePointSyncRunTrigger;
}

export interface KnowledgeSharePointSyncRunPage {
  items: KnowledgeSharePointSyncRun[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  page: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
}

export type WorkflowMapMetricEventName = typeof WorkflowMapMetricEventName[keyof typeof WorkflowMapMetricEventName];


export const WorkflowMapMetricEventName = {
  map_open: 'map_open',
  view_change: 'view_change',
  node_select: 'node_select',
  camera_control: 'camera_control',
  layer_change: 'layer_change',
  fullscreen_change: 'fullscreen_change',
  fallback: 'fallback',
  signal_drilldown: 'signal_drilldown',
  signal_window_change: 'signal_window_change',
  signal_channel_filter: 'signal_channel_filter',
  building_enter: 'building_enter',
  building_exit: 'building_exit',
  timeline_scrub: 'timeline_scrub',
  timeline_play: 'timeline_play',
  timeline_range: 'timeline_range',
  timeline_mode: 'timeline_mode',
  workforce_view: 'workforce_view',
  control_room_open: 'control_room_open',
  agent_setting_change: 'agent_setting_change',
  coaching_submit: 'coaching_submit',
  steer_submit: 'steer_submit',
  review_action: 'review_action',
  floorplan_view: 'floorplan_view',
  floorplan_select: 'floorplan_select',
} as const;

export type WorkflowMapMetricEventView = typeof WorkflowMapMetricEventView[keyof typeof WorkflowMapMetricEventView];


export const WorkflowMapMetricEventView = {
  '3d': '3d',
  arena: 'arena',
  floor: 'floor',
  list: 'list',
} as const;

export type WorkflowMapMetricEventCountBucket = typeof WorkflowMapMetricEventCountBucket[keyof typeof WorkflowMapMetricEventCountBucket];


export const WorkflowMapMetricEventCountBucket = {
  NUMBER_1: '1',
  '2-5': '2-5',
  '6-20': '6-20',
  '20+': '20+',
} as const;

export type WorkflowMapMetricEventSetting = typeof WorkflowMapMetricEventSetting[keyof typeof WorkflowMapMetricEventSetting];


export const WorkflowMapMetricEventSetting = {
  level: 'level',
  instruction: 'instruction',
  suggestion: 'suggestion',
} as const;

export type WorkflowMapMetricEventMode = typeof WorkflowMapMetricEventMode[keyof typeof WorkflowMapMetricEventMode];


export const WorkflowMapMetricEventMode = {
  signals: 'signals',
  work: 'work',
} as const;

export interface WorkflowMapMetricEvent {
  eventId: string;
  name: WorkflowMapMetricEventName;
  occurredAt: string;
  journeyKey: JourneyKey;
  view?: WorkflowMapMetricEventView;
  /** @maxLength 40 */
  nodeKind?: string;
  /** @maxLength 80 */
  control?: string;
  /** @maxLength 80 */
  reason?: string;
  /**
     * @minimum 0
     * @maximum 600000
     */
  durationMs?: number;
  /** @maxLength 40 */
  channel?: string;
  countBucket?: WorkflowMapMetricEventCountBucket;
  setting?: WorkflowMapMetricEventSetting;
  level?: AuthorityLevel;
  mode?: WorkflowMapMetricEventMode;
  window?: JourneySignalWindow;
}

export interface WorkflowMapMetricBatch {
  /**
     * @minItems 1
     * @maxItems 50
     */
  events: WorkflowMapMetricEvent[];
}

export interface WorkflowMapMetricBatchResult {
  /** @minimum 0 */
  accepted: number;
  /** @minimum 0 */
  duplicates: number;
}

export interface ProgrammeCount {
  key: string;
  /** @minimum 0 */
  count: number;
}

export interface ProgrammeJourneySummary {
  journeyKey: JourneyKey;
  label: string;
  /** @minimum 0 */
  activeRecords: number;
  /** @minimum 0 */
  completedRecords: number;
  /** @minimum 0 */
  overdueRecords: number;
  /** @minimum 0 */
  transitions: number;
  /** @minimum 0 */
  uniqueRecords: number;
  /** @minimum 0 */
  terminalOutcomes: number;
  /** @minimum 0 */
  positiveOutcomes: number;
  /**
     * @minimum 0
     * @nullable
     */
  averageObservedStageHours: number | null;
}

export interface ProgrammeJourneyReport {
  /** @minimum 0 */
  activeRecords: number;
  /** @minimum 0 */
  completedRecords: number;
  /** @minimum 0 */
  overdueRecords: number;
  /** @minimum 0 */
  transitions: number;
  /** @minimum 0 */
  terminalOutcomes: number;
  /**
     * @minimum 0
     * @maximum 1
     * @nullable
     */
  positiveOutcomeRate: number | null;
  byJourney: ProgrammeJourneySummary[];
  outcomes: ProgrammeCount[];
}

export interface ProgrammeAiReport {
  /** @minimum 0 */
  totalActions: number;
  /**
     * @minimum 0
     * @maximum 1
     * @nullable
     */
  recommendationAcceptanceRate: number | null;
  /**
     * @minimum 0
     * @maximum 1
     * @nullable
     */
  executionSuccessRate: number | null;
  /** @minimum 0 */
  authorityBlocks: number;
  /** @minimum 0 */
  approvalOverrides: number;
  byStatus: ProgrammeCount[];
  byAuthority: ProgrammeCount[];
}

export interface ProgrammeVisualizationReport {
  /** @minimum 0 */
  totalEvents: number;
  /** @minimum 0 */
  mapOpens: number;
  /** @minimum 0 */
  fallbacks: number;
  /**
     * @minimum 0
     * @maximum 1
     * @nullable
     */
  fallbackRate: number | null;
  /**
     * @minimum 0
     * @nullable
     */
  averageControlDurationMs: number | null;
  byView: ProgrammeCount[];
  byInteractionType: ProgrammeCount[];
  byFallbackReason: ProgrammeCount[];
}

export type CrmProgrammeOperationsReportPrivacy = {
  contentCaptured: false;
  identityCaptured: false;
  detail: string;
};

export interface ProgrammeAuthorizationReport {
  startedAt: string;
  /** @minimum 0 */
  totalEvents: number;
  /** @minimum 0 */
  expectedDenials: number;
  /** @minimum 0 */
  faults: number;
  byCode: ProgrammeCount[];
  contentCaptured: false;
  identityCaptured: false;
}

export interface CrmProgrammeOperationsReport {
  generatedAt: string;
  windowStartedAt: string;
  /**
     * @minimum 1
     * @maximum 365
     */
  rangeDays: number;
  journeys: ProgrammeJourneyReport;
  authorization: ProgrammeAuthorizationReport;
  ai: ProgrammeAiReport;
  knowledge: KnowledgeHealth;
  visualization: ProgrammeVisualizationReport;
  privacy: CrmProgrammeOperationsReportPrivacy;
}

export type IngestionSourceType = typeof IngestionSourceType[keyof typeof IngestionSourceType];


export const IngestionSourceType = {
  news: 'news',
  website: 'website',
  jobs: 'jobs',
} as const;

export interface IngestionSource {
  type: IngestionSourceType;
  /** @maxLength 2000 */
  url: string;
  /** @maxLength 200 */
  label?: string;
}

export type IngestionRuleCreateStatus = typeof IngestionRuleCreateStatus[keyof typeof IngestionRuleCreateStatus];


export const IngestionRuleCreateStatus = {
  active: 'active',
  paused: 'paused',
} as const;

export interface IngestionRuleCreate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @maxLength 2000
     * @nullable
     */
  description?: string | null;
  status: IngestionRuleCreateStatus;
  /**
     * @minItems 1
     * @maxItems 50
     */
  sources: IngestionSource[];
  /**
     * @maxItems 100
     * @items.minLength 1
     * @items.maxLength 100
     */
  keywords: string[];
  /**
     * @minimum 60
     * @maximum 43200
     */
  frequencyMinutes: number;
  /** @maxItems 500 */
  targetAccountIds: string[];
  /**
     * @minimum 0.5
     * @maximum 1
     */
  autoAttachThreshold: number;
}

export type IngestionRule = IngestionRuleCreate & ({
  id: string;
  /** @nullable */
  lastRunAt: string | null;
  /** @nullable */
  nextRunAt: string | null;
  /** @nullable */
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
});

export type IngestedDocumentCreateSourceType = typeof IngestedDocumentCreateSourceType[keyof typeof IngestedDocumentCreateSourceType];


export const IngestedDocumentCreateSourceType = {
  news: 'news',
  website: 'website',
  jobs: 'jobs',
} as const;

export type IngestedDocumentCreateMetadata = { [key: string]: unknown };

export interface IngestedDocumentCreate {
  sourceType: IngestedDocumentCreateSourceType;
  /** @maxLength 2000 */
  sourceUrl: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  title: string;
  /**
     * @minLength 20
     * @maxLength 200000
     */
  content: string;
  /** @nullable */
  publishedAt?: string | null;
  metadata?: IngestedDocumentCreateMetadata;
}

export type IngestedDocumentSourceType = typeof IngestedDocumentSourceType[keyof typeof IngestedDocumentSourceType];


export const IngestedDocumentSourceType = {
  news: 'news',
  website: 'website',
  jobs: 'jobs',
} as const;

export interface IngestedDocument {
  id: string;
  /** @nullable */
  ruleId: string | null;
  sourceType: IngestedDocumentSourceType;
  sourceUrl: string;
  canonicalUrl: string;
  title: string;
  content?: string;
  contentHash: string;
  /** @nullable */
  httpStatus?: number | null;
  fetchedAt: string;
  /** @nullable */
  publishedAt?: string | null;
  embeddingAvailable: boolean;
  createdAt: string;
}

export type WebSignalType = typeof WebSignalType[keyof typeof WebSignalType];


export const WebSignalType = {
  hiring_surge: 'hiring_surge',
  funding_budget: 'funding_budget',
  expansion: 'expansion',
  leadership_change: 'leadership_change',
  technology_adoption: 'technology_adoption',
  explicit_need: 'explicit_need',
  other: 'other',
} as const;

export type WebDiscoveryCrmMatchStatus = typeof WebDiscoveryCrmMatchStatus[keyof typeof WebDiscoveryCrmMatchStatus];


export const WebDiscoveryCrmMatchStatus = {
  none: 'none',
  matched: 'matched',
  ambiguous: 'ambiguous',
} as const;

export interface WebDiscoveryCrmMatch {
  status: WebDiscoveryCrmMatchStatus;
  /** @nullable */
  matchedId: string | null;
  candidateIds: string[];
  reasons: string[];
}

/**
 * @nullable
 */
export type WebSignalCandidateDiscoveryKind = typeof WebSignalCandidateDiscoveryKind[keyof typeof WebSignalCandidateDiscoveryKind] | null;


export const WebSignalCandidateDiscoveryKind = {
  tender: 'tender',
  project: 'project',
  potential_customer: 'potential_customer',
  buying_signal: 'buying_signal',
  other: 'other',
} as const;

export type WebSignalCandidateExtractedFields = { [key: string]: unknown };

export type WebSignalCandidateEditedFields = { [key: string]: unknown };

export type WebSignalCandidateReviewStatus = typeof WebSignalCandidateReviewStatus[keyof typeof WebSignalCandidateReviewStatus];


export const WebSignalCandidateReviewStatus = {
  unreviewed: 'unreviewed',
  accepted: 'accepted',
  dismissed: 'dismissed',
  deferred: 'deferred',
  duplicate: 'duplicate',
  promoted: 'promoted',
  changed: 'changed',
} as const;

export type WebSignalCandidateStatus = typeof WebSignalCandidateStatus[keyof typeof WebSignalCandidateStatus];


export const WebSignalCandidateStatus = {
  pending: 'pending',
  confirmed: 'confirmed',
  dismissed: 'dismissed',
} as const;

/**
 * @nullable
 */
export type WebSignalCandidateIndustrySource = typeof WebSignalCandidateIndustrySource[keyof typeof WebSignalCandidateIndustrySource] | null;


export const WebSignalCandidateIndustrySource = {
  ai: 'ai',
  human: 'human',
} as const;

/**
 * @nullable
 */
export type WebSignalCandidateDecision = typeof WebSignalCandidateDecision[keyof typeof WebSignalCandidateDecision] | null;


export const WebSignalCandidateDecision = {
  pursue: 'pursue',
  not_pursuing: 'not_pursuing',
} as const;

/**
 * @nullable
 */
export type WebSignalTenderRecommendationConfidence = typeof WebSignalTenderRecommendationConfidence[keyof typeof WebSignalTenderRecommendationConfidence] | null;


export const WebSignalTenderRecommendationConfidence = {
  high: 'high',
  medium: 'medium',
  low: 'low',
} as const;

export interface WebSignalTenderRecommendation {
  opportunityId: string;
  recommendation: TenderOpportunityRecommendation | null;
  rankingMethod: TenderOpportunityRankingMethod | null;
  matchBand: TenderOpportunityMatchBand | null;
  /** @maxItems 10 */
  reasons: string[];
  /** @maxItems 20 */
  disqualifiers: string[];
  /** @nullable */
  confidence: WebSignalTenderRecommendationConfidence;
}

export interface WebSignalCandidate {
  id: string;
  documentId: string;
  /** @nullable */
  accountId: string | null;
  /** @nullable */
  companyName: string | null;
  /** @nullable */
  discoverySourceId?: string | null;
  /** @nullable */
  targetingProfileId?: string | null;
  /** @nullable */
  recipeId?: string | null;
  /** @nullable */
  discoveryRunId?: string | null;
  /** @nullable */
  candidateKey?: string | null;
  /** @nullable */
  discoveryKind?: WebSignalCandidateDiscoveryKind;
  /** @nullable */
  opportunityTitle?: string | null;
  /** @nullable */
  organizationName?: string | null;
  /** @nullable */
  sourceReference?: string | null;
  /**
     * @minimum 0
     * @maximum 100
     * @nullable
     */
  fitScore?: number | null;
  fitReasons?: string[];
  unknowns?: string[];
  extractedFields?: WebSignalCandidateExtractedFields;
  editedFields?: WebSignalCandidateEditedFields;
  reviewStatus?: WebSignalCandidateReviewStatus;
  /** @minimum 1 */
  reviewRevision?: number;
  /** @nullable */
  deferredUntil?: string | null;
  /** @nullable */
  duplicateCandidateId?: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  latestEvidenceRevision?: number | null;
  /** @nullable */
  observedDeadlineAt?: string | null;
  /** @nullable */
  lastMaterialChangeAt?: string | null;
  type: WebSignalType;
  /**
     * @minimum 0
     * @maximum 1
     */
  confidence: number;
  rationale: string;
  /**
     * @minimum -1
     * @maximum 1
     * @nullable
     */
  targetMarketSimilarity?: number | null;
  status: WebSignalCandidateStatus;
  sourceUrl: string;
  sourceTitle: string;
  fetchedAt: string;
  /** @nullable */
  signalId: string | null;
  accountMatch: WebDiscoveryCrmMatch;
  opportunityMatch: WebDiscoveryCrmMatch;
  /** @nullable */
  reviewedBy?: string | null;
  /** @nullable */
  reviewedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  /**
     * Industry suggested by AI or set by a reviewer
     * @nullable
     */
  industry?: string | null;
  /** @nullable */
  industrySource?: WebSignalCandidateIndustrySource;
  /**
     * Job that found this candidate
     * @nullable
     */
  jobId?: string | null;
  /** @nullable */
  decision?: WebSignalCandidateDecision;
  /** Bid recommendation of the tender opportunity created from this candidate (tender portal candidates only). */
  tenderRecommendation?: WebSignalTenderRecommendation | null;
}

export interface WebSignalCandidateIndustryGroup {
  /** @nullable */
  industry: string | null;
  /** @minimum 0 */
  count: number;
}

export interface WebSignalCandidateIndustryGroupPage {
  items: WebSignalCandidateIndustryGroup[];
  /**
     * Number of industry groups
     * @minimum 0
     */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type WebSignalReviewAction = typeof WebSignalReviewAction[keyof typeof WebSignalReviewAction];


export const WebSignalReviewAction = {
  confirm: 'confirm',
  reassign: 'reassign',
  dismiss: 'dismiss',
} as const;

export interface WebSignalReview {
  action: WebSignalReviewAction;
  /** @nullable */
  accountId?: string | null;
  /** @maxLength 200 */
  reviewedBy?: string;
}

export type WebSignalDigestByType = {[key: string]: number};

export interface WebSignalDigest {
  days: number;
  total: number;
  pending: number;
  confirmed: number;
  byType: WebSignalDigestByType;
  items: WebSignalCandidate[];
}

export interface WebDiscoveryCustomerProfile {
  /**
     * @minLength 1
     * @maxLength 160
     */
  label: string;
  /**
     * @minLength 1
     * @maxLength 1200
     */
  description: string;
  /**
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 240
     */
  characteristics: string[];
}

export interface WebDiscoveryQualificationCriterion {
  /** @pattern ^[a-z][a-z0-9_]{1,63}$ */
  key: string;
  /**
     * @minLength 1
     * @maxLength 160
     */
  label: string;
  /**
     * @minLength 1
     * @maxLength 1200
     */
  description: string;
  required: boolean;
}

export type WebDiscoveryEvidenceCitationKind = typeof WebDiscoveryEvidenceCitationKind[keyof typeof WebDiscoveryEvidenceCitationKind];


export const WebDiscoveryEvidenceCitationKind = {
  product: 'product',
  service: 'service',
  capability: 'capability',
  account_history: 'account_history',
  opportunity_history: 'opportunity_history',
  outcome_history: 'outcome_history',
  public_web: 'public_web',
} as const;

export type WebDiscoveryEvidenceCitationFacts = { [key: string]: unknown };

export interface WebDiscoveryEvidenceCitation {
  /**
     * @minLength 1
     * @maxLength 200
     */
  id: string;
  kind: WebDiscoveryEvidenceCitationKind;
  /**
     * @minLength 1
     * @maxLength 300
     */
  label: string;
  facts: WebDiscoveryEvidenceCitationFacts;
  /**
     * @minLength 1
     * @maxLength 200
     */
  revision: string;
  observedAt: string;
  /** @nullable */
  url?: string | null;
}

export type WebDiscoverySourceSuggestionVerification = typeof WebDiscoverySourceSuggestionVerification[keyof typeof WebDiscoverySourceSuggestionVerification];


export const WebDiscoverySourceSuggestionVerification = {
  unverified: 'unverified',
} as const;

export interface WebDiscoverySourceSuggestion {
  /**
     * @minLength 1
     * @maxLength 240
     */
  name: string;
  url: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  rationale: string;
  /**
     * @minItems 1
     * @maxItems 10
     */
  evidenceIds: string[];
  verification: WebDiscoverySourceSuggestionVerification;
}

export type WebDiscoveryCustomerSuggestionVerification = typeof WebDiscoveryCustomerSuggestionVerification[keyof typeof WebDiscoveryCustomerSuggestionVerification];


export const WebDiscoveryCustomerSuggestionVerification = {
  unverified: 'unverified',
} as const;

export interface WebDiscoveryCustomerSuggestion {
  /**
     * @minLength 1
     * @maxLength 240
     */
  name: string;
  /** @nullable */
  website: string | null;
  /**
     * @maxLength 240
     * @nullable
     */
  market: string | null;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  rationale: string;
  /**
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 500
     */
  observedFacts: string[];
  /**
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 500
     */
  uncertainty: string[];
  /**
     * @minItems 1
     * @maxItems 10
     */
  evidenceIds: string[];
  verification: WebDiscoveryCustomerSuggestionVerification;
}

export type WebDiscoverySearchStatusState = typeof WebDiscoverySearchStatusState[keyof typeof WebDiscoverySearchStatusState];


export const WebDiscoverySearchStatusState = {
  disabled: 'disabled',
  approval_required: 'approval_required',
  misconfigured: 'misconfigured',
  quota_exhausted: 'quota_exhausted',
  timeout: 'timeout',
  failed: 'failed',
  ready: 'ready',
} as const;

export interface WebDiscoverySearchStatus {
  state: WebDiscoverySearchStatusState;
  ready: boolean;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  message: string;
  /**
     * @maxItems 3
     * @items.maxLength 240
     */
  queries: string[];
}

export interface WebDiscoveryPublicCriteria {
  /**
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 160
     */
  markets?: string[];
  /**
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 160
     */
  geographies?: string[];
  /**
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 160
     */
  opportunityTypes?: string[];
}

export interface CreateWebDiscoveryTargetingProposal {
  requestId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /** @nullable */
  baseProfileId?: string | null;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  feedback?: string;
  publicCriteria?: WebDiscoveryPublicCriteria;
  includePublicSuggestions?: boolean;
}

export interface UpdateWebDiscoveryTargetingProfile {
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @maxItems 50
     * @items.minLength 1
     * @items.maxLength 160
     */
  markets?: string[];
  /** @maxItems 30 */
  customerProfiles?: WebDiscoveryCustomerProfile[];
  /**
     * @maxItems 50
     * @items.minLength 1
     * @items.maxLength 160
     */
  geographies?: string[];
  /**
     * @maxItems 50
     * @items.minLength 1
     * @items.maxLength 500
     */
  exclusions?: string[];
  /** @maxItems 50 */
  qualificationCriteria?: WebDiscoveryQualificationCriterion[];
  /**
     * @minLength 1
     * @maxLength 8000
     */
  rationale?: string;
}

export interface ConfirmWebDiscoveryTargetingProfile {
  /** @minimum 1 */
  expectedRevision: number;
}

export type WebDiscoveryTargetingProfileStatus = typeof WebDiscoveryTargetingProfileStatus[keyof typeof WebDiscoveryTargetingProfileStatus];


export const WebDiscoveryTargetingProfileStatus = {
  draft: 'draft',
  confirmed: 'confirmed',
  superseded: 'superseded',
  withdrawn: 'withdrawn',
} as const;

export interface WebDiscoveryTargetingProfile {
  id: string;
  /** @minimum 1 */
  version: number;
  /** @minimum 1 */
  revision: number;
  status: WebDiscoveryTargetingProfileStatus;
  name: string;
  markets: string[];
  customerProfiles: WebDiscoveryCustomerProfile[];
  geographies: string[];
  exclusions: string[];
  qualificationCriteria: WebDiscoveryQualificationCriterion[];
  evidenceSummary: WebDiscoveryEvidenceCitation[];
  rationale: string;
  evidenceFingerprint: string;
  /** @nullable */
  auditId: string | null;
  createdBy: string;
  /** @nullable */
  confirmedBy: string | null;
  /** @nullable */
  confirmedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WebDiscoveryTargetingProposalResult {
  profile: WebDiscoveryTargetingProfile;
  /**
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 300
     */
  missingInputs: string[];
  /** @maxItems 200 */
  evidence: WebDiscoveryEvidenceCitation[];
  /** @maxItems 25 */
  sourceSuggestions: WebDiscoverySourceSuggestion[];
  /** @maxItems 50 */
  customerSuggestions: WebDiscoveryCustomerSuggestion[];
  search: WebDiscoverySearchStatus;
}

export interface WebDiscoveryTargetingProfilePage {
  items: WebDiscoveryTargetingProfile[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type WebDiscoverySourceSourceType = typeof WebDiscoverySourceSourceType[keyof typeof WebDiscoverySourceSourceType];


export const WebDiscoverySourceSourceType = {
  public: 'public',
  signed_in: 'signed_in',
  feed: 'feed',
} as const;

/**
 * Tender sources run a built-in portal connector and need no recipe or targeting profile.
 */
export type WebDiscoverySourceCategory = typeof WebDiscoverySourceCategory[keyof typeof WebDiscoverySourceCategory];


export const WebDiscoverySourceCategory = {
  general: 'general',
  government: 'government',
  tender: 'tender',
} as const;

export type WebDiscoverySourceStatus = typeof WebDiscoverySourceStatus[keyof typeof WebDiscoverySourceStatus];


export const WebDiscoverySourceStatus = {
  inactive: 'inactive',
  active: 'active',
  paused: 'paused',
  revoked: 'revoked',
} as const;

export type WebDiscoverySourceReadiness = typeof WebDiscoverySourceReadiness[keyof typeof WebDiscoverySourceReadiness];


export const WebDiscoverySourceReadiness = {
  unconfigured: 'unconfigured',
  ready: 'ready',
  sign_in_required: 'sign_in_required',
  renewal_required: 'renewal_required',
  blocked: 'blocked',
  unsupported: 'unsupported',
  unavailable: 'unavailable',
} as const;

export type WebDiscoverySourceSessionStatus = typeof WebDiscoverySourceSessionStatus[keyof typeof WebDiscoverySourceSessionStatus];


export const WebDiscoverySourceSessionStatus = {
  none: 'none',
  available: 'available',
  renewal_required: 'renewal_required',
  revoked: 'revoked',
  expired: 'expired',
} as const;

export type TenderSourceScanSummaryStatus = typeof TenderSourceScanSummaryStatus[keyof typeof TenderSourceScanSummaryStatus];


export const TenderSourceScanSummaryStatus = {
  completed: 'completed',
  partial: 'partial',
  failed: 'failed',
  blocked: 'blocked',
} as const;

export type TenderSourceScanSummaryTrigger = typeof TenderSourceScanSummaryTrigger[keyof typeof TenderSourceScanSummaryTrigger];


export const TenderSourceScanSummaryTrigger = {
  scheduled: 'scheduled',
  manual: 'manual',
} as const;

/**
 * ok: tenders read; empty: the portal listed none; page_changed: the page or feed no longer matches how it is read; blocked: the portal refused, needs sign-in or a browser; error: temporary failure.
 */
export type TenderSourceHealthOutcome = typeof TenderSourceHealthOutcome[keyof typeof TenderSourceHealthOutcome];


export const TenderSourceHealthOutcome = {
  ok: 'ok',
  empty: 'empty',
  page_changed: 'page_changed',
  blocked: 'blocked',
  error: 'error',
} as const;

export type TenderSourceHealthHistoryItemOutcome = typeof TenderSourceHealthHistoryItemOutcome[keyof typeof TenderSourceHealthHistoryItemOutcome];


export const TenderSourceHealthHistoryItemOutcome = {
  ok: 'ok',
  empty: 'empty',
  page_changed: 'page_changed',
  blocked: 'blocked',
  error: 'error',
} as const;

export type TenderSourceHealthHistoryItem = {
  completedAt: string;
  outcome: TenderSourceHealthHistoryItemOutcome;
  /** @minimum 0 */
  listingsFound: number;
  /** @maxLength 120 */
  code: string;
};

/**
 * Portal health after the most recent scan. Absent on summaries recorded before health tracking.
 */
export interface TenderSourceHealth {
  /** ok: tenders read; empty: the portal listed none; page_changed: the page or feed no longer matches how it is read; blocked: the portal refused, needs sign-in or a browser; error: temporary failure. */
  outcome: TenderSourceHealthOutcome;
  /**
     * Open tender opportunities currently held from this portal.
     * @minimum 0
     */
  openTenders: number;
  /** @minimum 0 */
  consecutiveFailures: number;
  /** True after repeated failed scans. */
  needsAttention: boolean;
  /** @nullable */
  lastSuccessAt: string | null;
  /**
     * Most recent scans first.
     * @maxItems 10
     */
  history: TenderSourceHealthHistoryItem[];
}

/**
 * Result of the most recent tender portal scan.
 */
export interface TenderSourceScanSummary {
  status: TenderSourceScanSummaryStatus;
  /** @maxLength 120 */
  code: string;
  /** @maxLength 2000 */
  detail: string;
  trigger: TenderSourceScanSummaryTrigger;
  startedAt: string;
  completedAt: string;
  /** @minimum 0 */
  listingsFound: number;
  /** @minimum 0 */
  filteredOut: number;
  /** @minimum 0 */
  created: number;
  /** @minimum 0 */
  updated: number;
  /** @minimum 0 */
  unchanged: number;
  /** @minimum 0 */
  skipped: number;
  /** @minimum 0 */
  documentsAdded: number;
  /** @minimum 0 */
  detailFailures: number;
  /** @minimum 0 */
  requests: number;
  /** @minimum 0 */
  bytes: number;
  health?: TenderSourceHealth;
}

/**
 * Public source configuration and lifecycle metadata. Protected browser state references are deliberately excluded.
 */
export interface WebDiscoverySource {
  id: string;
  sourceKey: string;
  government?: GovernmentSourceConfiguration | null;
  name: string;
  sourceType: WebDiscoverySourceSourceType;
  /** Tender sources run a built-in portal connector and need no recipe or targeting profile. */
  category?: WebDiscoverySourceCategory;
  /** @nullable */
  connectorKey?: string | null;
  jurisdictionFilter?: string[];
  keywordFilter?: string[];
  lastScanSummary?: TenderSourceScanSummary | null;
  baseUrl: string;
  status: WebDiscoverySourceStatus;
  readiness: WebDiscoverySourceReadiness;
  sessionStatus: WebDiscoverySourceSessionStatus;
  /** @nullable */
  targetingProfileId: string | null;
  allowedHosts: string[];
  allowedPaths: string[];
  excludedPaths: string[];
  extractionFields: string[];
  /** @minimum 5 */
  cadenceMinutes: number;
  /**
     * @minimum 1
     * @maximum 1000
     */
  maxPagesPerRun: number;
  /** @minimum 1 */
  maxBytesPerRun: number;
  /** @minimum 1 */
  revision: number;
  configuredBy: string;
  /** @nullable */
  readinessCode: string | null;
  /** @nullable */
  readinessDetail: string | null;
  /** @nullable */
  readinessCheckedAt: string | null;
  /** @nullable */
  lastRunAt: string | null;
  /** @nullable */
  nextRunAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WebDiscoverySourcePage {
  items: WebDiscoverySource[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type CreateWebDiscoverySourceSourceType = typeof CreateWebDiscoverySourceSourceType[keyof typeof CreateWebDiscoverySourceSourceType];


export const CreateWebDiscoverySourceSourceType = {
  public: 'public',
  signed_in: 'signed_in',
  feed: 'feed',
} as const;

export type WebDiscoveryExtractionField = typeof WebDiscoveryExtractionField[keyof typeof WebDiscoveryExtractionField];


export const WebDiscoveryExtractionField = {
  title: 'title',
  summary: 'summary',
  organization: 'organization',
  reference: 'reference',
  location: 'location',
  published_at: 'published_at',
  deadline_at: 'deadline_at',
  value: 'value',
  currency: 'currency',
  contact_name: 'contact_name',
  contact_email: 'contact_email',
  contact_phone: 'contact_phone',
} as const;

export interface CreateWebDiscoverySource {
  /**
     * @minLength 1
     * @maxLength 120
     * @pattern ^[a-z0-9]+(?:[a-z0-9-]*[a-z0-9])?$
     */
  sourceKey: string;
  government?: GovernmentSourceConfiguration | null;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  sourceType: CreateWebDiscoverySourceSourceType;
  /** @maxLength 2000 */
  baseUrl: string;
  /** @nullable */
  targetingProfileId?: string | null;
  /**
     * @minItems 1
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 253
     */
  allowedHosts: string[];
  /**
     * @minItems 1
     * @maxItems 50
     * @items.minLength 1
     * @items.maxLength 500
     * @items.pattern ^/
     */
  allowedPaths: string[];
  /**
     * @maxItems 50
     * @items.minLength 1
     * @items.maxLength 500
     * @items.pattern ^/
     */
  excludedPaths: string[];
  /**
     * @minItems 1
     * @maxItems 20
     */
  extractionFields: WebDiscoveryExtractionField[];
  /**
     * @minimum 5
     * @maximum 525600
     */
  cadenceMinutes: number;
  /**
     * @minimum 1
     * @maximum 1000
     */
  maxPagesPerRun: number;
  /**
     * @minimum 1024
     * @maximum 100000000
     */
  maxBytesPerRun: number;
}

export type TenderSourceSuggestionKind = typeof TenderSourceSuggestionKind[keyof typeof TenderSourceSuggestionKind];


export const TenderSourceSuggestionKind = {
  api: 'api',
  rss: 'rss',
  html: 'html',
  recipe: 'recipe',
} as const;

export interface TenderSourceSuggestion {
  /** @maxLength 120 */
  key: string;
  /** @maxLength 80 */
  connectorKey: string;
  /** @maxLength 200 */
  name: string;
  url: string;
  /** @nullable */
  jurisdiction: string | null;
  kind: TenderSourceSuggestionKind;
  /** @minimum 5 */
  defaultCadenceMinutes: number;
  requiresBrowser: boolean;
  /**
     * Existing monitored source for this portal, when already added.
     * @nullable
     */
  sourceId: string | null;
}

export interface TenderSourceSuggestions {
  /** @maxItems 50 */
  items: TenderSourceSuggestion[];
}

export interface CreateTenderSourceFromSuggestion {
  /**
     * @minLength 1
     * @maxLength 120
     * @pattern ^[a-z0-9]+(?:-[a-z0-9]+)*$
     */
  suggestionKey: string;
}

export interface TenderSourceFromSuggestionResult {
  created: boolean;
  source: WebDiscoverySource;
}

export interface RunTenderSourceScan {
  /** @minimum 1 */
  expectedRevision: number;
}

export interface TenderSourceScanRequest {
  sourceId: string;
  queuedAt: string;
}

export interface RunTenderPortalDiscovery {
  /**
     * Website address, organisation name or category such as 'NSW councils'.
     * @minLength 1
     * @maxLength 500
     */
  seed: string;
}

export interface TenderPortalSample {
  /** @maxLength 300 */
  title: string;
  /** @nullable */
  url: string | null;
  /** @nullable */
  reference: string | null;
  /** @nullable */
  closesAt: string | null;
  /** @nullable */
  buyerName: string | null;
}

export type TenderPortalReadConfigFeedFormat = typeof TenderPortalReadConfigFeedFormat[keyof typeof TenderPortalReadConfigFeedFormat];


export const TenderPortalReadConfigFeedFormat = {
  rss: 'rss',
  atom: 'atom',
  json: 'json',
  ocds: 'ocds',
} as const;

export interface TenderPortalReadConfig {
  feedUrl?: string;
  feedFormat?: TenderPortalReadConfigFeedFormat;
  listingUrl?: string;
  detailPattern?: string;
}

export type TenderPortalCandidateReadMethod = typeof TenderPortalCandidateReadMethod[keyof typeof TenderPortalCandidateReadMethod];


export const TenderPortalCandidateReadMethod = {
  feed: 'feed',
  listing_page: 'listing_page',
  browser_page: 'browser_page',
  requires_sign_in: 'requires_sign_in',
  unsupported: 'unsupported',
} as const;

export type TenderPortalCandidateStatus = typeof TenderPortalCandidateStatus[keyof typeof TenderPortalCandidateStatus];


export const TenderPortalCandidateStatus = {
  suggested: 'suggested',
  approved: 'approved',
  rejected: 'rejected',
} as const;

export interface TenderPortalCandidate {
  id: string;
  seed: string;
  url: string;
  host: string;
  title: string;
  readMethod: TenderPortalCandidateReadMethod;
  detail: string;
  /** @maxItems 5 */
  samples: TenderPortalSample[];
  readConfig: TenderPortalReadConfig;
  status: TenderPortalCandidateStatus;
  /** @nullable */
  approvedSourceId: string | null;
  /** @nullable */
  reviewedBy: string | null;
  /** @nullable */
  reviewedAt: string | null;
  /** @nullable */
  reviewNote: string | null;
  /** @minimum 1 */
  revision: number;
  lastDiscoveredAt: string;
  createdAt: string;
}

export interface TenderPortalLinkedSite {
  url: string;
  title: string;
}

export interface TenderPortalDiscoveryRun {
  seed: string;
  searchedUrl: string;
  /** @minimum 0 */
  pagesChecked: number;
  /** @minimum 0 */
  requests: number;
  /** @maxItems 10 */
  notes: string[];
  /** @maxItems 10 */
  linkedSites: TenderPortalLinkedSite[];
  /** @maxItems 10 */
  candidates: TenderPortalCandidate[];
}

export interface TenderPortalCandidatePage {
  /** @maxItems 50 */
  items: TenderPortalCandidate[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface ReviewTenderPortalCandidate {
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @maxLength 1000
     * @nullable
     */
  note?: string | null;
}

export interface TenderPortalCandidateApproval {
  candidate: TenderPortalCandidate;
  sourceId: string;
  sourceCreated: boolean;
  /** False while the reader for discovered portals is not yet switched on; the source then waits paused. */
  collectionReady: boolean;
}

export type UpdateWebDiscoverySourceStatus = typeof UpdateWebDiscoverySourceStatus[keyof typeof UpdateWebDiscoverySourceStatus];


export const UpdateWebDiscoverySourceStatus = {
  inactive: 'inactive',
  active: 'active',
  paused: 'paused',
} as const;

export interface UpdateWebDiscoverySource {
  /**
     * Tender portal sources only.
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 80
     */
  jurisdictionFilter?: string[];
  /**
     * Tender portal sources only.
     * @maxItems 50
     * @items.minLength 1
     * @items.maxLength 100
     */
  keywordFilter?: string[];
  government?: GovernmentSourceConfiguration | null;
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /** @maxLength 2000 */
  baseUrl?: string;
  status?: UpdateWebDiscoverySourceStatus;
  /** @nullable */
  targetingProfileId?: string | null;
  /**
     * @minItems 1
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 253
     */
  allowedHosts?: string[];
  /**
     * @minItems 1
     * @maxItems 50
     * @items.minLength 1
     * @items.maxLength 500
     * @items.pattern ^/
     */
  allowedPaths?: string[];
  /**
     * @maxItems 50
     * @items.minLength 1
     * @items.maxLength 500
     * @items.pattern ^/
     */
  excludedPaths?: string[];
  /**
     * @minItems 1
     * @maxItems 20
     */
  extractionFields?: WebDiscoveryExtractionField[];
  /**
     * @minimum 5
     * @maximum 525600
     */
  cadenceMinutes?: number;
  /**
     * @minimum 1
     * @maximum 1000
     */
  maxPagesPerRun?: number;
  /**
     * @minimum 1024
     * @maximum 100000000
     */
  maxBytesPerRun?: number;
}

export type StartWebDiscoverySignInSessionMode = typeof StartWebDiscoverySignInSessionMode[keyof typeof StartWebDiscoverySignInSessionMode];


export const StartWebDiscoverySignInSessionMode = {
  initial: 'initial',
  renewal: 'renewal',
} as const;

export interface StartWebDiscoverySignInSession {
  /** @minimum 1 */
  expectedSourceRevision: number;
  mode: StartWebDiscoverySignInSessionMode;
}

export interface TransitionWebDiscoverySignInSession {
  /** @minimum 1 */
  expectedRevision: number;
}

export interface RevokeWebDiscoverySourceSession {
  /** @minimum 1 */
  expectedSourceRevision: number;
}

export type WebDiscoverySignInSessionMode = typeof WebDiscoverySignInSessionMode[keyof typeof WebDiscoverySignInSessionMode];


export const WebDiscoverySignInSessionMode = {
  initial: 'initial',
  renewal: 'renewal',
} as const;

export type WebDiscoverySignInSessionStatus = typeof WebDiscoverySignInSessionStatus[keyof typeof WebDiscoverySignInSessionStatus];


export const WebDiscoverySignInSessionStatus = {
  starting: 'starting',
  active: 'active',
  mfa_required: 'mfa_required',
  challenge_required: 'challenge_required',
  completed: 'completed',
  cancelled: 'cancelled',
  expired: 'expired',
  failed: 'failed',
} as const;

export type WebDiscoverySignInSessionReadiness = typeof WebDiscoverySignInSessionReadiness[keyof typeof WebDiscoverySignInSessionReadiness];


export const WebDiscoverySignInSessionReadiness = {
  sign_in_required: 'sign_in_required',
  ready: 'ready',
  renewal_required: 'renewal_required',
  blocked: 'blocked',
  unsupported: 'unsupported',
  unavailable: 'unavailable',
} as const;

/**
 * Safe viewer lifecycle metadata. Credentials, request headers, page bodies, screenshots, worker identifiers, storage references, ciphertext and browser state are excluded.
 */
export interface WebDiscoverySignInSession {
  id: string;
  sourceId: string;
  /** @minimum 1 */
  sourceRevision: number;
  mode: WebDiscoverySignInSessionMode;
  status: WebDiscoverySignInSessionStatus;
  readiness: WebDiscoverySignInSessionReadiness;
  code: string;
  detail: string;
  expiresAt: string;
  /** @nullable */
  lastObservedAt: string | null;
  /** @minimum 1 */
  revision: number;
  /**
     * @minLength 1
     * @nullable
     */
  viewerPath: string | null;
  createdAt: string;
  updatedAt: string;
}

export type WebDiscoverySignInViewerFrameContentType = typeof WebDiscoverySignInViewerFrameContentType[keyof typeof WebDiscoverySignInViewerFrameContentType];


export const WebDiscoverySignInViewerFrameContentType = {
  'image/jpeg': 'image/jpeg',
} as const;

/**
 * Transient no-store viewer frame returned only to the initiating authorized actor.
 */
export interface WebDiscoverySignInViewerFrame {
  contentType: WebDiscoverySignInViewerFrameContentType;
  /**
     * @minLength 1
     * @maxLength 2000000
     */
  imageBase64: string;
  capturedAt: string;
}

export type WebDiscoverySignInViewerInputAction = typeof WebDiscoverySignInViewerInputAction[keyof typeof WebDiscoverySignInViewerInputAction];


export const WebDiscoverySignInViewerInputAction = {
  click: 'click',
  type: 'type',
  press: 'press',
  scroll: 'scroll',
} as const;

/**
 * Bounded direct staff input. Values are transient and must never be logged or persisted.
 */
export interface WebDiscoverySignInViewerInput {
  action: WebDiscoverySignInViewerInputAction;
  /**
     * @minimum 0
     * @maximum 4096
     */
  x?: number;
  /**
     * @minimum 0
     * @maximum 4096
     */
  y?: number;
  /** @maxLength 4096 */
  value?: string;
  /**
     * @minimum -4000
     * @maximum 4000
     */
  deltaY?: number;
}

export interface WebDiscoveryReadOnlyFilter {
  /**
     * @minLength 1
     * @maxLength 80
     * @pattern ^[A-Za-z0-9_.~-]+$
     */
  parameter: string;
  /** @maxLength 200 */
  value: string;
}

export interface WebDiscoveryLinkDiscovery {
  enabled: boolean;
  /**
     * @minimum 0
     * @maximum 3
     */
  maxDepth: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  maxLinksPerPage: number;
  /**
     * @maxItems 50
     * @items.minLength 1
     * @items.maxLength 500
     * @items.pattern ^/
     */
  includePaths: string[];
  /**
     * @maxItems 50
     * @items.minLength 1
     * @items.maxLength 500
     * @items.pattern ^/
     */
  excludedPaths: string[];
}

export type WebDiscoveryPaginationMode = typeof WebDiscoveryPaginationMode[keyof typeof WebDiscoveryPaginationMode];


export const WebDiscoveryPaginationMode = {
  none: 'none',
  next_link: 'next_link',
} as const;

export interface WebDiscoveryPagination {
  mode: WebDiscoveryPaginationMode;
  /**
     * @minimum 1
     * @maximum 100
     */
  maxPages: number;
}

export interface WebDiscoveryRecipeDefinition {
  /**
     * @minItems 1
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 500
     * @items.pattern ^/
     */
  entryPaths: string[];
  /**
     * @minItems 1
     * @maxItems 20
     */
  fields: WebDiscoveryExtractionField[];
  /**
     * @maxItems 25
     * @items.minLength 1
     * @items.maxLength 120
     */
  keywords: string[];
  /**
     * @maxItems 25
     * @items.minLength 1
     * @items.maxLength 120
     */
  exclusions: string[];
  /**
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 120
     */
  requiredMarkers: string[];
  /** @maxItems 20 */
  readOnlyFilters: WebDiscoveryReadOnlyFilter[];
  linkDiscovery: WebDiscoveryLinkDiscovery;
  pagination: WebDiscoveryPagination;
}

export interface CreateWebDiscoveryRecipe {
  definition: WebDiscoveryRecipeDefinition;
}

export interface UpdateWebDiscoveryRecipe {
  /** @minimum 1 */
  expectedRevision: number;
  definition: WebDiscoveryRecipeDefinition;
}

export interface PreviewWebDiscoveryRecipe {
  /** @minimum 1 */
  expectedSourceRevision: number;
  /** @minimum 1 */
  expectedRecipeRevision: number;
}

export interface ConfirmWebDiscoveryRecipe {
  /** @minimum 1 */
  expectedSourceRevision: number;
  /** @minimum 1 */
  expectedRecipeRevision: number;
}

export type WebDiscoveryRecipeStatus = typeof WebDiscoveryRecipeStatus[keyof typeof WebDiscoveryRecipeStatus];


export const WebDiscoveryRecipeStatus = {
  draft: 'draft',
  confirmed: 'confirmed',
  superseded: 'superseded',
  withdrawn: 'withdrawn',
} as const;

/**
 * @nullable
 */
export type WebDiscoveryRecipePreviewReadiness = typeof WebDiscoveryRecipePreviewReadiness[keyof typeof WebDiscoveryRecipePreviewReadiness] | null;


export const WebDiscoveryRecipePreviewReadiness = {
  ready: 'ready',
  sign_in_required: 'sign_in_required',
  blocked: 'blocked',
  unsupported: 'unsupported',
  unavailable: 'unavailable',
} as const;

export interface WebDiscoveryRecipe {
  id: string;
  sourceId: string;
  /** @minimum 1 */
  version: number;
  /** @minimum 1 */
  revision: number;
  /** @minimum 1 */
  schemaVersion: number;
  status: WebDiscoveryRecipeStatus;
  definition: WebDiscoveryRecipeDefinition;
  contentHash: string;
  /**
     * @minimum 1
     * @nullable
     */
  previewedSourceRevision: number | null;
  /**
     * @minimum 1
     * @nullable
     */
  previewedRecipeRevision: number | null;
  /** @nullable */
  previewReadiness: WebDiscoveryRecipePreviewReadiness;
  /** @nullable */
  previewCode: string | null;
  /** @nullable */
  previewedAt: string | null;
  createdBy: string;
  /** @nullable */
  confirmedBy: string | null;
  /** @nullable */
  confirmedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WebDiscoveryRecipePage {
  items: WebDiscoveryRecipe[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type WebDiscoveryPreviewItemFields = {[key: string]: string | null};

export interface WebDiscoveryPreviewItem {
  url: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  excerpt: string;
  contentHash: string;
  contentType: string;
  fields: WebDiscoveryPreviewItemFields;
  /** @maxItems 25 */
  matchedKeywords: string[];
}

export interface WebDiscoveryRecipePreview {
  sourceId: string;
  /** @minimum 1 */
  sourceRevision: number;
  recipeId: string;
  /** @minimum 1 */
  recipeVersion: number;
  /** @minimum 1 */
  recipeRevision: number;
  readiness: WebDiscoveryRecipePreviewReadiness;
  code: string;
  detail: string;
  checkedAt: string;
  /** @minimum 0 */
  pagesFetched: number;
  /** @minimum 0 */
  bytesFetched: number;
  truncated: boolean;
  /** @maxItems 100 */
  items: WebDiscoveryPreviewItem[];
}

export type WebDiscoveryScanRunTrigger = typeof WebDiscoveryScanRunTrigger[keyof typeof WebDiscoveryScanRunTrigger];


export const WebDiscoveryScanRunTrigger = {
  manual: 'manual',
  scheduled: 'scheduled',
  readiness_preview: 'readiness_preview',
} as const;

export type WebDiscoveryScanRunStatus = typeof WebDiscoveryScanRunStatus[keyof typeof WebDiscoveryScanRunStatus];


export const WebDiscoveryScanRunStatus = {
  queued: 'queued',
  claimed: 'claimed',
  running: 'running',
  paused: 'paused',
  completed: 'completed',
  partial: 'partial',
  failed: 'failed',
  cancelled: 'cancelled',
  blocked: 'blocked',
} as const;

export type WebDiscoveryScanRunCheckpoint = { [key: string]: unknown };

/**
 * Durable run projection. Worker lease tokens are deliberately excluded.
 */
export interface WebDiscoveryScanRun {
  id: string;
  sourceId: string;
  targetingProfileId: string;
  recipeId: string;
  trigger: WebDiscoveryScanRunTrigger;
  status: WebDiscoveryScanRunStatus;
  idempotencyKey: string;
  /** @minimum 1 */
  revision: number;
  /** @minimum 0 */
  attemptCount: number;
  checkpoint: WebDiscoveryScanRunCheckpoint;
  /** @minimum 0 */
  pagesReserved: number;
  /** @minimum 0 */
  pagesConsumed: number;
  /** @minimum 0 */
  bytesReserved: number;
  /** @minimum 0 */
  bytesConsumed: number;
  /** @minimum 0 */
  costReservedMinor: number;
  /** @minimum 0 */
  costConsumedMinor: number;
  /** @pattern ^[A-Z]{3}$ */
  currency: string;
  requestedBy: string;
  /** @nullable */
  startedAt: string | null;
  /** @nullable */
  completedAt: string | null;
  /** @nullable */
  lastErrorCode: string | null;
  /** @nullable */
  lastErrorDetail: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWebDiscoveryScanRunRequest {
  /** @minimum 1 */
  expectedSourceRevision: number;
  /**
     * @minLength 8
     * @maxLength 160
     * @pattern ^[A-Za-z0-9._:-]+$
     */
  idempotencyKey: string;
}

export interface CancelWebDiscoveryScanRunRequest {
  /** @minimum 1 */
  expectedRevision: number;
}

/**
 * What a job checks and how AI picks out opportunities. Its hash identifies the exact definition a test run covered.
 */
export interface WebDiscoveryJobDefinition {
  /**
     * @minLength 1
     * @maxLength 160
     */
  name: string;
  /**
     * @maxLength 2000
     * @nullable
     */
  description?: string | null;
  /** @nullable */
  targetingProfileId?: string | null;
  /** @maxItems 25 */
  sourceIds: string[];
  webSearchEnabled: boolean;
  /**
     * @minLength 1
     * @maxLength 8000
     */
  aiInstructions: string;
  /**
     * @maxItems 10
     * @items.minLength 1
     * @items.maxLength 200
     */
  searchQueries: string[];
  /**
     * Schedule interval; at least hourly.
     * @minimum 60
     * @maximum 525600
     */
  cadenceMinutes: number;
}

export interface CreateWebDiscoveryJobRequest {
  definition: WebDiscoveryJobDefinition;
  /** A successful test run of exactly this definition. */
  testRunId: string;
  /** Start scheduled runs immediately. */
  activate?: boolean;
}

export type UpdateWebDiscoveryJobRequestStatus = typeof UpdateWebDiscoveryJobRequestStatus[keyof typeof UpdateWebDiscoveryJobRequestStatus];


export const UpdateWebDiscoveryJobRequestStatus = {
  draft: 'draft',
  active: 'active',
  paused: 'paused',
} as const;

export interface UpdateWebDiscoveryJobRequest {
  /** @minimum 1 */
  expectedRevision: number;
  definition?: WebDiscoveryJobDefinition;
  /** Required when the definition changes. */
  testRunId?: string;
  status?: UpdateWebDiscoveryJobRequestStatus;
}

export type WebDiscoveryJobStatus = typeof WebDiscoveryJobStatus[keyof typeof WebDiscoveryJobStatus];


export const WebDiscoveryJobStatus = {
  draft: 'draft',
  active: 'active',
  paused: 'paused',
} as const;

export interface WebDiscoveryJob {
  id: string;
  name: string;
  /** @nullable */
  description: string | null;
  /** @nullable */
  targetingProfileId: string | null;
  sourceIds: string[];
  webSearchEnabled: boolean;
  aiInstructions: string;
  searchQueries: string[];
  cadenceMinutes: number;
  status: WebDiscoveryJobStatus;
  /** @minimum 1 */
  revision: number;
  definitionHash: string;
  /** The current definition has a successful test run. */
  tested: boolean;
  /** @nullable */
  lastSuccessfulTestRunId: string | null;
  /** @nullable */
  lastRunAt: string | null;
  /** @nullable */
  nextRunAt: string | null;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface WebDiscoveryJobPage {
  items: WebDiscoveryJob[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface TestWebDiscoveryJobDefinitionRequest {
  definition: WebDiscoveryJobDefinition;
  /** The saved job being edited, if any. */
  jobId?: string;
}

export interface RunWebDiscoveryJobRequest {
  /** @minimum 1 */
  expectedRevision: number;
}

export type WebDiscoveryJobSourceCheckStatus = typeof WebDiscoveryJobSourceCheckStatus[keyof typeof WebDiscoveryJobSourceCheckStatus];


export const WebDiscoveryJobSourceCheckStatus = {
  checked: 'checked',
  skipped: 'skipped',
  failed: 'failed',
} as const;

export interface WebDiscoveryJobSourceCheck {
  sourceId: string;
  name: string;
  status: WebDiscoveryJobSourceCheckStatus;
  /** @minimum 0 */
  pagesFetched: number;
  /** @nullable */
  message: string | null;
}

export interface WebDiscoveryTokenUsage {
  /** @minimum 0 */
  promptTokens: number;
  /** @minimum 0 */
  completionTokens: number;
  /** @minimum 0 */
  totalTokens: number;
}

export type WebDiscoveryJobRunTrigger = typeof WebDiscoveryJobRunTrigger[keyof typeof WebDiscoveryJobRunTrigger];


export const WebDiscoveryJobRunTrigger = {
  test: 'test',
  manual: 'manual',
  schedule: 'schedule',
} as const;

export type WebDiscoveryJobRunStatus = typeof WebDiscoveryJobRunStatus[keyof typeof WebDiscoveryJobRunStatus];


export const WebDiscoveryJobRunStatus = {
  queued: 'queued',
  running: 'running',
  succeeded: 'succeeded',
  partial: 'partial',
  failed: 'failed',
} as const;

export type WebDiscoveryJobRunWebSearchState = typeof WebDiscoveryJobRunWebSearchState[keyof typeof WebDiscoveryJobRunWebSearchState];


export const WebDiscoveryJobRunWebSearchState = {
  disabled: 'disabled',
  used: 'used',
  skipped: 'skipped',
  failed: 'failed',
} as const;

export interface WebDiscoveryJobRun {
  id: string;
  /**
     * Null for a test of an unsaved definition.
     * @nullable
     */
  jobId: string | null;
  trigger: WebDiscoveryJobRunTrigger;
  status: WebDiscoveryJobRunStatus;
  definitionHash: string;
  definition: WebDiscoveryJobDefinition;
  requestedBy: string;
  /** @nullable */
  startedAt: string | null;
  /** @nullable */
  completedAt: string | null;
  /**
     * @minimum 0
     * @nullable
     */
  durationMs: number | null;
  sourcesChecked: WebDiscoveryJobSourceCheck[];
  /** @minimum 0 */
  pagesFetched: number;
  /** @minimum 0 */
  searchQueriesRun: number;
  /** @minimum 0 */
  resultsFound: number;
  /** @minimum 0 */
  newCandidates: number;
  /** @minimum 0 */
  duplicates: number;
  webSearchState: WebDiscoveryJobRunWebSearchState;
  /**
     * Why web search was skipped or failed.
     * @nullable
     */
  webSearchMessage: string | null;
  /** @nullable */
  errorCode: string | null;
  /** @nullable */
  errorMessage: string | null;
  tokenUsage: WebDiscoveryTokenUsage;
  createdAt: string;
  updatedAt: string;
}

export interface WebDiscoveryJobRunPage {
  items: WebDiscoveryJobRun[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type WebDiscoveryJobRunResultExtractedFields = { [key: string]: unknown };

export type WebDiscoveryJobRunResultOrigin = typeof WebDiscoveryJobRunResultOrigin[keyof typeof WebDiscoveryJobRunResultOrigin];


export const WebDiscoveryJobRunResultOrigin = {
  site: 'site',
  web_search: 'web_search',
} as const;

export interface WebDiscoveryJobRunResult {
  id: string;
  runId: string;
  /** @minimum 0 */
  position: number;
  title: string;
  url: string;
  canonicalUrl: string;
  summary: string;
  extractedFields: WebDiscoveryJobRunResultExtractedFields;
  /** @nullable */
  suggestedIndustry: string | null;
  origin: WebDiscoveryJobRunResultOrigin;
  /** @nullable */
  sourceId: string | null;
  /**
     * The Review queue candidate created or matched; always null for test runs.
     * @nullable
     */
  candidateId: string | null;
  duplicate: boolean;
  createdAt: string;
}

export interface WebDiscoveryJobRunResultPage {
  items: WebDiscoveryJobRunResult[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface WebDiscoveryJobTestResult {
  run: WebDiscoveryJobRun;
  /** @maxItems 50 */
  results: WebDiscoveryJobRunResult[];
  warnings: string[];
}

export interface WebDiscoveryCitation {
  /**
     * @minLength 1
     * @maxLength 300
     */
  title: string;
  /** @maxLength 2048 */
  url: string;
}

export type WebDiscoveryExtraInformationEntrySource = typeof WebDiscoveryExtraInformationEntrySource[keyof typeof WebDiscoveryExtraInformationEntrySource];


export const WebDiscoveryExtraInformationEntrySource = {
  human: 'human',
  ai: 'ai',
} as const;

export interface WebDiscoveryExtraInformationEntry {
  /**
     * @minLength 1
     * @maxLength 120
     */
  label: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  value: string;
  source: WebDiscoveryExtraInformationEntrySource;
  /** @maxItems 10 */
  citations: WebDiscoveryCitation[];
}

export type WebDiscoveryCandidateAssessmentRecommendation = typeof WebDiscoveryCandidateAssessmentRecommendation[keyof typeof WebDiscoveryCandidateAssessmentRecommendation];


export const WebDiscoveryCandidateAssessmentRecommendation = {
  pursue: 'pursue',
  do_not_pursue: 'do_not_pursue',
  needs_more_information: 'needs_more_information',
} as const;

/**
 * AI suggestion only; a person records the decision.
 */
export interface WebDiscoveryCandidateAssessment {
  recommendation: WebDiscoveryCandidateAssessmentRecommendation;
  reasons: string[];
  citations: WebDiscoveryCitation[];
  assessedAt: string;
}

export type WebDiscoveryCandidateDecisionDecision = typeof WebDiscoveryCandidateDecisionDecision[keyof typeof WebDiscoveryCandidateDecisionDecision];


export const WebDiscoveryCandidateDecisionDecision = {
  pursue: 'pursue',
  not_pursuing: 'not_pursuing',
} as const;

export interface WebDiscoveryCandidateDecision {
  decision: WebDiscoveryCandidateDecisionDecision;
  decidedBy: string;
  decidedAt: string;
  /** @nullable */
  reason: string | null;
}

/**
 * @nullable
 */
export type WebDiscoveryCandidateEnrichmentIndustrySource = typeof WebDiscoveryCandidateEnrichmentIndustrySource[keyof typeof WebDiscoveryCandidateEnrichmentIndustrySource] | null;


export const WebDiscoveryCandidateEnrichmentIndustrySource = {
  ai: 'ai',
  human: 'human',
} as const;

export interface WebDiscoveryCandidateEnrichment {
  candidateId: string;
  /** @nullable */
  jobId: string | null;
  /** @nullable */
  runId: string | null;
  /** @nullable */
  industry: string | null;
  /** @nullable */
  industrySource: WebDiscoveryCandidateEnrichmentIndustrySource;
  /** @nullable */
  notes: string | null;
  extraInformation: WebDiscoveryExtraInformationEntry[];
  assessment: WebDiscoveryCandidateAssessment | null;
  decision: WebDiscoveryCandidateDecision | null;
  /**
     * 0 until something is saved.
     * @minimum 0
     */
  revision: number;
  /** @nullable */
  updatedBy: string | null;
  /** @nullable */
  updatedAt: string | null;
}

export interface UpdateWebDiscoveryCandidateEnrichmentRequest {
  /** @minimum 0 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 120
     * @nullable
     */
  industry?: string | null;
  /**
     * @maxLength 10000
     * @nullable
     */
  notes?: string | null;
  /** @maxItems 50 */
  extraInformation?: WebDiscoveryExtraInformationEntry[];
}

export interface RequestWebDiscoveryCandidateEnrichmentRequest {
  /** @minimum 0 */
  expectedRevision: number;
  /**
     * What the reviewer wants to know more about.
     * @maxLength 1000
     */
  focus?: string;
}

export interface WebDiscoveryCandidateEnrichmentProposal {
  candidateId: string;
  entries: WebDiscoveryExtraInformationEntry[];
  tokenUsage: WebDiscoveryTokenUsage;
}

export interface RequestWebDiscoveryCandidateAssessmentRequest {
  /** @minimum 0 */
  expectedRevision: number;
}

export type RecordWebDiscoveryCandidateDecisionRequestDecision = typeof RecordWebDiscoveryCandidateDecisionRequestDecision[keyof typeof RecordWebDiscoveryCandidateDecisionRequestDecision];


export const RecordWebDiscoveryCandidateDecisionRequestDecision = {
  pursue: 'pursue',
  not_pursuing: 'not_pursuing',
} as const;

export interface RecordWebDiscoveryCandidateDecisionRequest {
  /** @minimum 0 */
  expectedRevision: number;
  decision: RecordWebDiscoveryCandidateDecisionRequestDecision;
  /** @maxLength 2000 */
  reason?: string;
}

export interface WebDiscoveryCandidateDocument {
  id: string;
  candidateId: string;
  documentId: string;
  /** @nullable */
  label: string | null;
  title: string;
  /** @nullable */
  sourceUrl: string | null;
  addedBy: string;
  createdAt: string;
}

export interface WebDiscoveryCandidateDocumentPage {
  items: WebDiscoveryCandidateDocument[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface AddWebDiscoveryCandidateDocumentRequest {
  documentId: string;
  /**
     * @minLength 1
     * @maxLength 160
     */
  label?: string;
}

export interface UploadWebDiscoveryCandidateDocumentRequest {
  /**
     * @minLength 1
     * @maxLength 255
     */
  filename: string;
  /**
     * @minLength 3
     * @maxLength 200
     */
  contentType: string;
  /**
     * Canonical padded base64 of at most 10 MiB
     * @minLength 4
     * @maxLength 13981016
     */
  dataBase64: string;
  /**
     * @minLength 1
     * @maxLength 160
     */
  label?: string;
}

export type WebDiscoveryCandidateHistoryEntryAction = typeof WebDiscoveryCandidateHistoryEntryAction[keyof typeof WebDiscoveryCandidateHistoryEntryAction];


export const WebDiscoveryCandidateHistoryEntryAction = {
  industry_changed: 'industry_changed',
  notes_updated: 'notes_updated',
  extra_information_updated: 'extra_information_updated',
  document_added: 'document_added',
  document_removed: 'document_removed',
  enrichment_requested: 'enrichment_requested',
  assessment_recorded: 'assessment_recorded',
  decision_recorded: 'decision_recorded',
} as const;

export type WebDiscoveryCandidateHistoryEntryDetails = { [key: string]: unknown };

export interface WebDiscoveryCandidateHistoryEntry {
  id: string;
  candidateId: string;
  action: WebDiscoveryCandidateHistoryEntryAction;
  actor: string;
  details: WebDiscoveryCandidateHistoryEntryDetails;
  createdAt: string;
}

export interface WebDiscoveryCandidateHistoryPage {
  items: WebDiscoveryCandidateHistoryEntry[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface WebDiscoveryScanRunPage {
  items: WebDiscoveryScanRun[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type WebDiscoveryEvidenceFacts = { [key: string]: unknown };

/**
 * Bounded immutable evidence only; login responses and browser session material are never evidence.
 */
export interface WebDiscoveryEvidence {
  id: string;
  candidateId: string;
  documentId: string;
  sourceId: string;
  scanRunId: string;
  profileId: string;
  recipeId: string;
  /** @minimum 1 */
  revision: number;
  /** @nullable */
  sourceIdentifier: string | null;
  canonicalUrl: string;
  title: string;
  /**
     * @minLength 1
     * @maxLength 12000
     */
  excerpt: string;
  contentHash: string;
  facts: WebDiscoveryEvidenceFacts;
  uncertainty: string[];
  restrictedSource: boolean;
  retrievedAt: string;
  /** @nullable */
  publishedAt: string | null;
  /** @nullable */
  deadlineAt: string | null;
  createdAt: string;
}

export interface WebDiscoveryEvidencePage {
  items: WebDiscoveryEvidence[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type WebDiscoveryAutomationPolicyMode = typeof WebDiscoveryAutomationPolicyMode[keyof typeof WebDiscoveryAutomationPolicyMode];


export const WebDiscoveryAutomationPolicyMode = {
  review_only: 'review_only',
  automatic: 'automatic',
} as const;

export type WebDiscoveryAutomationPolicySourceRevisionBindingsItem = {
  sourceId: string;
  /** @minimum 1 */
  sourceRevision: number;
  recipeId: string;
  /** @minimum 1 */
  recipeVersion: number;
  /** @minimum 1 */
  recipeRevision: number;
};

/**
 * @nullable
 */
export type WebDiscoveryAutomationPolicyPipeline = typeof WebDiscoveryAutomationPolicyPipeline[keyof typeof WebDiscoveryAutomationPolicyPipeline] | null;


export const WebDiscoveryAutomationPolicyPipeline = {
  b2b_sales: 'b2b_sales',
  b2c_sales: 'b2c_sales',
  investor_funding: 'investor_funding',
} as const;

/**
 * Tender opportunity match bands and deterministic pre-filters. Scores at or above strongMinScore are Strong, at or above possibleMinScore are Possible, otherwise Low (kept visible as Low match).
 */
export interface TenderRankingSettings {
  /**
     * @minimum 2
     * @maximum 100
     */
  strongMinScore: number;
  /**
     * @minimum 1
     * @maximum 99
     */
  possibleMinScore: number;
  /**
     * Empty allows every jurisdiction.
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 80
     */
  allowedJurisdictions: string[];
  /**
     * @maxItems 50
     * @items.minLength 2
     * @items.maxLength 120
     */
  excludedKeywords: string[];
  /**
     * @maxItems 100
     * @items.minLength 1
     * @items.maxLength 100
     */
  excludedCategoryCodes: string[];
  /**
     * @minimum 0
     * @nullable
     */
  minEstimatedValue: number | null;
  /**
     * @minimum 0
     * @nullable
     */
  maxEstimatedValue: number | null;
  /** When false, ranking uses keyword scoring only. */
  aiEnabled: boolean;
}

export interface WebDiscoveryAutomationPolicy {
  /** @nullable */
  id: string | null;
  /** @minimum 0 */
  revision: number;
  mode: WebDiscoveryAutomationPolicyMode;
  enabled: boolean;
  /** @nullable */
  targetingProfileId: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  targetingProfileRevision: number | null;
  allowedSourceIds: string[];
  sourceRevisionBindings: WebDiscoveryAutomationPolicySourceRevisionBindingsItem[];
  /**
     * @minimum 0
     * @maximum 100
     */
  minimumQualificationScore: number;
  requireUnambiguousAccount: boolean;
  allowAccountCreation: boolean;
  allowOpportunityCreation: boolean;
  /** @nullable */
  pipeline: WebDiscoveryAutomationPolicyPipeline;
  /** @nullable */
  openStage: string | null;
  /** @nullable */
  ownerId: string | null;
  /** @minimum 0 */
  maxPromotionsPerDay: number;
  /** @minimum 0 */
  maxSpendMinorPerDay: number;
  /** @pattern ^[A-Z]{3}$ */
  currency: string;
  /** @nullable */
  auditId: string | null;
  /** @nullable */
  configuredBy: string | null;
  /** @nullable */
  configuredAt: string | null;
  /** @nullable */
  updatedAt: string | null;
  tenderRanking?: TenderRankingSettings;
}

export type TenderOpportunityConvertRequestProposalType = typeof TenderOpportunityConvertRequestProposalType[keyof typeof TenderOpportunityConvertRequestProposalType];


export const TenderOpportunityConvertRequestProposalType = {
  tender: 'tender',
  sow: 'sow',
} as const;

export interface TenderOpportunityConvertRequest {
  /** Client-generated key; retrying with the same key never duplicates records. */
  requestKey: string;
  /** @minimum 1 */
  expectedRevision: number;
  proposalType: TenderOpportunityConvertRequestProposalType;
  /**
     * @minLength 1
     * @maxLength 300
     */
  title: string;
  /** @nullable */
  deadlineAt?: string | null;
  /** @nullable */
  ownerAppUserId?: string | null;
  /**
     * Existing buyer account. Leave empty and supply newAccountName to create the buyer.
     * @nullable
     */
  accountId?: string | null;
  /**
     * Name for a new buyer account. An existing account with exactly this name is reused instead.
     * @minLength 1
     * @maxLength 200
     * @nullable
     */
  newAccountName?: string | null;
  pipelineStage?: OpportunityStage | null;
  /**
     * @minimum 0
     * @nullable
     */
  estimatedValue?: number | null;
  /**
     * Acquired opportunity documents to attach to the response. May be empty.
     * @maxItems 100
     */
  documentIds: string[];
}

/**
 * preparation: documents were read by AI tender preparation; direct: the response was created without AI reading.
 */
export type TenderOpportunityConversionPath = typeof TenderOpportunityConversionPath[keyof typeof TenderOpportunityConversionPath];


export const TenderOpportunityConversionPath = {
  preparation: 'preparation',
  direct: 'direct',
} as const;

export type TenderOpportunityConversionTenderProposalType = typeof TenderOpportunityConversionTenderProposalType[keyof typeof TenderOpportunityConversionTenderProposalType];


export const TenderOpportunityConversionTenderProposalType = {
  tender: 'tender',
  sow: 'sow',
} as const;

export type TenderOpportunityConversionTender = {
  id: string;
  title: string;
  proposalType: TenderOpportunityConversionTenderProposalType;
  link: string;
};

export type TenderOpportunityConversionAccount = {
  id: string;
  name: string;
  created: boolean;
  link: string;
};

export type TenderOpportunityConversionPipelineOpportunity = {
  id: string;
  title: string;
  link: string;
};

/**
 * @nullable
 */
export type TenderOpportunityConversionPursuit = {
  id: string;
  progress: string;
} | null;

export type TenderOpportunityConversionDocuments = {
  /** @minimum 0 */
  selected: number;
  /** @minimum 0 */
  attached: number;
  /** @minimum 0 */
  failed: number;
};

export interface TenderOpportunityConversion {
  opportunity: TenderOpportunity;
  replayed: boolean;
  /** preparation: documents were read by AI tender preparation; direct: the response was created without AI reading. */
  path: TenderOpportunityConversionPath;
  tender: TenderOpportunityConversionTender;
  account: TenderOpportunityConversionAccount;
  pipelineOpportunity: TenderOpportunityConversionPipelineOpportunity;
  /** @nullable */
  pursuit: TenderOpportunityConversionPursuit;
  documents: TenderOpportunityConversionDocuments;
  warnings: string[];
}

export interface TenderOpportunityRerankQueued {
  opportunityId: string;
  queuedAt: string;
}

export type UpdateWebDiscoveryAutomationPolicyRequestMode = typeof UpdateWebDiscoveryAutomationPolicyRequestMode[keyof typeof UpdateWebDiscoveryAutomationPolicyRequestMode];


export const UpdateWebDiscoveryAutomationPolicyRequestMode = {
  review_only: 'review_only',
  automatic: 'automatic',
} as const;

export type UpdateWebDiscoveryAutomationPolicyRequestSourceRevisionBindingsItem = {
  sourceId: string;
  /** @minimum 1 */
  sourceRevision: number;
  recipeId: string;
  /** @minimum 1 */
  recipeVersion: number;
  /** @minimum 1 */
  recipeRevision: number;
};

/**
 * @nullable
 */
export type UpdateWebDiscoveryAutomationPolicyRequestPipeline = typeof UpdateWebDiscoveryAutomationPolicyRequestPipeline[keyof typeof UpdateWebDiscoveryAutomationPolicyRequestPipeline] | null;


export const UpdateWebDiscoveryAutomationPolicyRequestPipeline = {
  b2b_sales: 'b2b_sales',
  b2c_sales: 'b2c_sales',
  investor_funding: 'investor_funding',
} as const;

export interface UpdateWebDiscoveryAutomationPolicyRequest {
  /** @minimum 0 */
  expectedRevision: number;
  mode: UpdateWebDiscoveryAutomationPolicyRequestMode;
  enabled: boolean;
  /** @nullable */
  targetingProfileId: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  targetingProfileRevision: number | null;
  /** @maxItems 100 */
  sourceRevisionBindings: UpdateWebDiscoveryAutomationPolicyRequestSourceRevisionBindingsItem[];
  /**
     * @minimum 0
     * @maximum 100
     */
  minimumQualificationScore: number;
  requireUnambiguousAccount: boolean;
  allowAccountCreation: boolean;
  allowOpportunityCreation: boolean;
  /** @nullable */
  pipeline: UpdateWebDiscoveryAutomationPolicyRequestPipeline;
  /**
     * @minLength 1
     * @maxLength 80
     * @nullable
     */
  openStage: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  ownerId: string | null;
  /**
     * @minimum 0
     * @maximum 10000
     */
  maxPromotionsPerDay: number;
  /** @minimum 0 */
  maxSpendMinorPerDay: number;
  /** @pattern ^[A-Z]{3}$ */
  currency: string;
  tenderRanking?: TenderRankingSettings;
}

export type WebDiscoveryProposedRecordAction = typeof WebDiscoveryProposedRecordAction[keyof typeof WebDiscoveryProposedRecordAction];


export const WebDiscoveryProposedRecordAction = {
  reuse: 'reuse',
  create: 'create',
  review: 'review',
  unavailable: 'unavailable',
} as const;

export type WebDiscoveryProposedRecordFields = { [key: string]: unknown };

export interface WebDiscoveryProposedRecord {
  action: WebDiscoveryProposedRecordAction;
  /** @nullable */
  recordId: string | null;
  fields: WebDiscoveryProposedRecordFields;
  reasons: string[];
}

export interface WebDiscoveryPromotionEligibility {
  eligible: boolean;
  reasons: string[];
  warnings: string[];
}

export type WebDiscoveryCandidateReviewPreviewReviewStatus = typeof WebDiscoveryCandidateReviewPreviewReviewStatus[keyof typeof WebDiscoveryCandidateReviewPreviewReviewStatus];


export const WebDiscoveryCandidateReviewPreviewReviewStatus = {
  unreviewed: 'unreviewed',
  accepted: 'accepted',
  dismissed: 'dismissed',
  deferred: 'deferred',
  duplicate: 'duplicate',
  promoted: 'promoted',
  changed: 'changed',
} as const;

export interface WebDiscoveryCandidateReviewPreview {
  candidateId: string;
  /** @minimum 1 */
  reviewRevision: number;
  reviewStatus: WebDiscoveryCandidateReviewPreviewReviewStatus;
  /** @minimum 1 */
  evidenceRevision: number;
  evidence: WebDiscoveryEvidence;
  /**
     * @minimum 0
     * @maximum 100
     * @nullable
     */
  fitScore: number | null;
  fitReasons: string[];
  unknowns: string[];
  accountMatch: WebDiscoveryCrmMatch;
  opportunityMatch: WebDiscoveryCrmMatch;
  proposedAccount: WebDiscoveryProposedRecord;
  proposedOpportunity: WebDiscoveryProposedRecord;
  policy: WebDiscoveryAutomationPolicy;
  eligibility: WebDiscoveryPromotionEligibility;
}

export type ReviewWebDiscoveryCandidateRequestAction = typeof ReviewWebDiscoveryCandidateRequestAction[keyof typeof ReviewWebDiscoveryCandidateRequestAction];


export const ReviewWebDiscoveryCandidateRequestAction = {
  dismiss: 'dismiss',
  defer: 'defer',
  resolve_duplicate: 'resolve_duplicate',
} as const;

export interface ReviewWebDiscoveryCandidateRequest {
  action: ReviewWebDiscoveryCandidateRequestAction;
  /** @minimum 1 */
  expectedReviewRevision: number;
  /** @minimum 1 */
  expectedEvidenceRevision: number;
  /** @nullable */
  deferredUntil?: string | null;
  /** @nullable */
  duplicateCandidateId?: string | null;
  /**
     * @minLength 1
     * @maxLength 1000
     * @nullable
     */
  reason?: string | null;
}

export type WebDiscoveryCandidateReviewResultReviewStatus = typeof WebDiscoveryCandidateReviewResultReviewStatus[keyof typeof WebDiscoveryCandidateReviewResultReviewStatus];


export const WebDiscoveryCandidateReviewResultReviewStatus = {
  dismissed: 'dismissed',
  deferred: 'deferred',
  duplicate: 'duplicate',
} as const;

export interface WebDiscoveryCandidateReviewResult {
  candidateId: string;
  reviewStatus: WebDiscoveryCandidateReviewResultReviewStatus;
  /** @minimum 2 */
  reviewRevision: number;
  auditId: string;
  reviewedBy: string;
  reviewedAt: string;
}

export type PromoteWebDiscoveryCandidateRequestEdits = {
  /**
     * @minLength 1
     * @maxLength 300
     */
  accountName?: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  opportunityTitle?: string;
  accountId?: string;
  opportunityId?: string;
  /** @maxLength 1000 */
  reviewNote?: string;
};

export interface PromoteWebDiscoveryCandidateRequest {
  /** @minimum 1 */
  expectedReviewRevision: number;
  /** @minimum 1 */
  expectedEvidenceRevision: number;
  /** @minimum 1 */
  expectedPolicyRevision: number;
  /**
     * @minLength 8
     * @maxLength 160
     * @pattern ^[A-Za-z0-9._:-]+$
     */
  idempotencyKey: string;
  edits?: PromoteWebDiscoveryCandidateRequestEdits;
}

export type WebDiscoveryPromotionStatus = typeof WebDiscoveryPromotionStatus[keyof typeof WebDiscoveryPromotionStatus];


export const WebDiscoveryPromotionStatus = {
  reserved: 'reserved',
  promoted: 'promoted',
  blocked: 'blocked',
  failed: 'failed',
  cancelled: 'cancelled',
} as const;

export interface WebDiscoveryPromotion {
  id: string;
  candidateId: string;
  evidenceId: string;
  /** @nullable */
  policyId: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  policyRevision: number | null;
  targetingProfileId: string;
  sourceId: string;
  /** @nullable */
  accountId: string | null;
  /** @nullable */
  opportunityId: string | null;
  /** @nullable */
  accountHref: string | null;
  /** @nullable */
  opportunityHref: string | null;
  /** @nullable */
  auditId: string | null;
  promotionKey: string;
  /** @minimum 1 */
  candidateReviewRevision: number;
  status: WebDiscoveryPromotionStatus;
  promotedBy: string;
  /** @nullable */
  promotedAt: string | null;
  /** @nullable */
  errorCode: string | null;
  /** @nullable */
  errorDetail: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WebDiscoveryPromotionResult {
  promotion: WebDiscoveryPromotion;
  replayed: boolean;
}

export type VoiceSettingsEnquiryCaptureMode = typeof VoiceSettingsEnquiryCaptureMode[keyof typeof VoiceSettingsEnquiryCaptureMode];


export const VoiceSettingsEnquiryCaptureMode = {
  after_consent: 'after_consent',
  useful_request: 'useful_request',
  every_call: 'every_call',
} as const;

export type VoiceSettingsCallAuthentication = {
  staffApprovalEnabled: boolean;
  customerPortalApprovalEnabled: boolean;
  customerOtpFallbackEnabled: boolean;
};

export interface VoiceSettings {
  /**
     * @minLength 1
     * @maxLength 1000
     */
  greeting: string;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  consentMessage: string;
  recordingEnabled: boolean;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  behavior: string;
  enquiryCaptureMode: VoiceSettingsEnquiryCaptureMode;
  /**
     * @minLength 1
     * @maxLength 120
     */
  voiceName: string;
  /**
     * @minimum 0.1
     * @maximum 0.95
     */
  vadThreshold: number;
  /**
     * @minimum 200
     * @maximum 3000
     */
  silenceDurationMs: number;
  /**
     * @nullable
     * @pattern ^[+][1-9][0-9]{7,14}$
     */
  businessNumber?: string | null;
  /**
     * @nullable
     * @pattern ^[+][1-9][0-9]{7,14}$
     */
  transferNumber?: string | null;
  callAuthentication: VoiceSettingsCallAuthentication;
}

export type VoiceStyleConversationTone = typeof VoiceStyleConversationTone[keyof typeof VoiceStyleConversationTone];


export const VoiceStyleConversationTone = {
  professional: 'professional',
  conversational: 'conversational',
  energetic: 'energetic',
  calm: 'calm',
} as const;

export type VoiceStyleConversationFormality = typeof VoiceStyleConversationFormality[keyof typeof VoiceStyleConversationFormality];


export const VoiceStyleConversationFormality = {
  formal: 'formal',
  balanced: 'balanced',
  casual: 'casual',
} as const;

export type VoiceStyleConversationQuestionPacing = typeof VoiceStyleConversationQuestionPacing[keyof typeof VoiceStyleConversationQuestionPacing];


export const VoiceStyleConversationQuestionPacing = {
  one_at_a_time: 'one_at_a_time',
  group_related: 'group_related',
} as const;

export interface VoiceStyleConversation {
  tone: VoiceStyleConversationTone;
  formality: VoiceStyleConversationFormality;
  /**
     * @minimum 0
     * @maximum 100
     */
  warmth: number;
  /**
     * @minimum 0
     * @maximum 100
     */
  brevity: number;
  /**
     * @minLength 1
     * @maxLength 500
     */
  greeting: string;
  questionPacing: VoiceStyleConversationQuestionPacing;
  /**
     * @maxItems 8
     * @items.minLength 1
     * @items.maxLength 300
     */
  examplePhrases: string[];
  /** @maxLength 4000 */
  advancedInstructions: string;
}

export interface VoiceStyleSound {
  /**
     * @minLength 1
     * @maxLength 160
     */
  voiceName: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  accent: string;
  /**
     * @minimum 0.8
     * @maximum 1.2
     */
  speakingRate: number;
  /**
     * @minimum 300
     * @maximum 2000
     */
  responseTimingMs: number;
}

export type VoiceStyleProfileSchemaVersion = typeof VoiceStyleProfileSchemaVersion[keyof typeof VoiceStyleProfileSchemaVersion];


export const VoiceStyleProfileSchemaVersion = {
  NUMBER_1: 1,
} as const;

export interface VoiceStyleProfile {
  schemaVersion: VoiceStyleProfileSchemaVersion;
  conversation: VoiceStyleConversation;
  sound: VoiceStyleSound;
}

export type VoiceStyleVersionStatus = typeof VoiceStyleVersionStatus[keyof typeof VoiceStyleVersionStatus];


export const VoiceStyleVersionStatus = {
  draft: 'draft',
  active: 'active',
  retired: 'retired',
} as const;

export interface VoiceStyleVersion {
  /** @nullable */
  id: string | null;
  /** @minimum 0 */
  version: number;
  /** @minimum 0 */
  revision: number;
  status: VoiceStyleVersionStatus;
  /** @nullable */
  baseActiveVersionId?: string | null;
  profile: VoiceStyleProfile;
  compiledInstructions: string;
  compiledHash: string;
  protectedFingerprint: string;
  /** @nullable */
  createdAt: string | null;
  /** @nullable */
  updatedAt: string | null;
  /** @nullable */
  activatedAt: string | null;
}

export interface SaveVoiceStyleDraftRequest {
  /** @minimum 0 */
  expectedRevision: number;
  profile: VoiceStyleProfile;
}

export interface VoiceStyleProposalRequest {
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  message: string;
}

export type VoiceStyleProposalSampleExchangeItemSpeaker = typeof VoiceStyleProposalSampleExchangeItemSpeaker[keyof typeof VoiceStyleProposalSampleExchangeItemSpeaker];


export const VoiceStyleProposalSampleExchangeItemSpeaker = {
  Caller: 'Caller',
  Assistant: 'Assistant',
} as const;

export type VoiceStyleProposalSampleExchangeItem = {
  speaker: VoiceStyleProposalSampleExchangeItemSpeaker;
  text: string;
};

export interface VoiceStyleProposal {
  draftId: string;
  /** @minimum 1 */
  draftRevision: number;
  profile: VoiceStyleProfile;
  explanation: string;
  sampleExchange: VoiceStyleProposalSampleExchangeItem[];
  changedFields: string[];
}

export interface VoiceStyleTestSnapshot {
  id: string;
  draftId: string;
  /** @minimum 1 */
  draftRevision: number;
  compiledHash: string;
  profile: VoiceStyleProfile;
}

export interface ActivateVoiceStyleRequest {
  draftId: string;
  /** @minimum 1 */
  expectedRevision: number;
  testId: string;
}

/**
 * @nullable
 */
export type VoiceStyleWorkspaceActivationEligibility = {
  id: string;
  /** @nullable */
  completedAt: string | null;
} | null;

export type VoiceStyleWorkspaceCapabilitiesVoicesItem = {
  name: string;
  label: string;
  accent: string;
};

export type VoiceStyleWorkspaceCapabilitiesControls = { [key: string]: unknown };

export type VoiceStyleWorkspaceCapabilities = {
  apiVersion: string;
  voices: VoiceStyleWorkspaceCapabilitiesVoicesItem[];
  controls: VoiceStyleWorkspaceCapabilitiesControls;
  [key: string]: unknown;
 };

export interface VoiceStyleWorkspace {
  active: VoiceStyleVersion;
  draft: VoiceStyleVersion | null;
  /** @nullable */
  activationEligibility: VoiceStyleWorkspaceActivationEligibility;
  capabilities: VoiceStyleWorkspaceCapabilities;
  aiAvailable: boolean;
  voiceLiveAvailable: boolean;
  entityId: string;
}

export type VoiceStatusConnectionStatus = typeof VoiceStatusConnectionStatus[keyof typeof VoiceStatusConnectionStatus];


export const VoiceStatusConnectionStatus = {
  disconnected: 'disconnected',
  connected: 'connected',
  error: 'error',
  expired: 'expired',
} as const;

export type VoiceStatusMode = typeof VoiceStatusMode[keyof typeof VoiceStatusMode];


export const VoiceStatusMode = {
  simulation: 'simulation',
  live: 'live',
} as const;

export type VoiceStatusProviderKey = typeof VoiceStatusProviderKey[keyof typeof VoiceStatusProviderKey];


export const VoiceStatusProviderKey = {
  simulation: 'simulation',
  twilio: 'twilio',
} as const;

export interface VoiceStatus {
  connectionStatus: VoiceStatusConnectionStatus;
  connected: boolean;
  mode: VoiceStatusMode;
  providerKey: VoiceStatusProviderKey;
  voiceLiveConfigured: boolean;
  /** @nullable */
  displayName?: string | null;
  /** @nullable */
  phoneNumber?: string | null;
  settings: VoiceSettings;
}

export interface VoiceLiveConfigStatus {
  available: boolean;
  conversationRecording: false;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  recordingDisclosure: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  agentName: string;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  greeting: string;
  voiceLiveConfigured: boolean;
}

export interface VoiceLiveTicket {
  ticket: string;
  expiresAt: string;
}

export type PublicVoiceStatusMode = typeof PublicVoiceStatusMode[keyof typeof PublicVoiceStatusMode];


export const PublicVoiceStatusMode = {
  simulation: 'simulation',
  live: 'live',
} as const;

export interface PublicVoiceStatus {
  available: boolean;
  browserVoiceAvailable: boolean;
  callbackAvailable: boolean;
  telephonyConnected: boolean;
  mode: PublicVoiceStatusMode;
  recordingDisclosure: string;
}

export interface MicrositeCallbackRequest {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /** @pattern ^[+][1-9][0-9]{7,14}$ */
  phone: string;
  /** @maxLength 2000 */
  message?: string;
  /** @maxLength 200 */
  website?: string;
}

export type MicrositeCallbackResultMode = typeof MicrositeCallbackResultMode[keyof typeof MicrositeCallbackResultMode];


export const MicrositeCallbackResultMode = {
  simulation: 'simulation',
  live: 'live',
} as const;

export type VoiceCallStatus = typeof VoiceCallStatus[keyof typeof VoiceCallStatus];


export const VoiceCallStatus = {
  queued: 'queued',
  ringing: 'ringing',
  in_progress: 'in_progress',
  completed: 'completed',
  transfer_requested: 'transfer_requested',
  failed: 'failed',
  no_answer: 'no_answer',
  cancelled: 'cancelled',
} as const;

export interface MicrositeCallbackResult {
  callId: string;
  contactId: string;
  enquiryId: string;
  status: VoiceCallStatus;
  mode: MicrositeCallbackResultMode;
  message: string;
}

export interface TelephonyConnectionRequest {
  /** @pattern ^AC[a-fA-F0-9]{32}$ */
  accountSid: string;
  /** @minLength 16 */
  authToken: string;
  /** @pattern ^[+][1-9][0-9]{7,14}$ */
  phoneNumber: string;
  /**
     * @nullable
     * @pattern ^[+][1-9][0-9]{7,14}$
     */
  transferNumber?: string | null;
}

export type VoiceCallMode = typeof VoiceCallMode[keyof typeof VoiceCallMode];


export const VoiceCallMode = {
  simulation: 'simulation',
  live: 'live',
} as const;

export type VoiceCallDirection = typeof VoiceCallDirection[keyof typeof VoiceCallDirection];


export const VoiceCallDirection = {
  inbound: 'inbound',
  outbound: 'outbound',
} as const;

export type VoiceCallKind = typeof VoiceCallKind[keyof typeof VoiceCallKind];


export const VoiceCallKind = {
  ai: 'ai',
  click_to_call: 'click_to_call',
} as const;

/**
 * @nullable
 */
export type VoiceCallOutcome = typeof VoiceCallOutcome[keyof typeof VoiceCallOutcome] | null;


export const VoiceCallOutcome = {
  enquiry: 'enquiry',
  qualified: 'qualified',
  follow_up: 'follow_up',
  transfer_requested: 'transfer_requested',
  message: 'message',
  no_answer: 'no_answer',
  other: 'other',
} as const;

export type VoiceCallVoiceStyleVersionSource = typeof VoiceCallVoiceStyleVersionSource[keyof typeof VoiceCallVoiceStyleVersionSource];


export const VoiceCallVoiceStyleVersionSource = {
  legacy: 'legacy',
  active: 'active',
  draft_test: 'draft_test',
} as const;

/**
 * @nullable
 */
export type VoiceCallVoiceStyleVersion = {
  /** @nullable */
  versionId: string | null;
  /** @minimum 0 */
  version: number;
  source: VoiceCallVoiceStyleVersionSource;
} | null;

export interface VoiceCall {
  id: string;
  providerKey: string;
  mode: VoiceCallMode;
  /** @nullable */
  externalCallId?: string | null;
  direction: VoiceCallDirection;
  kind: VoiceCallKind;
  status: VoiceCallStatus;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  accountId?: string | null;
  fromNumber: string;
  toNumber: string;
  /** @nullable */
  objective?: string | null;
  consentPlayed: boolean;
  transferRequested: boolean;
  /** @nullable */
  recordingReference?: string | null;
  transcript: string;
  /** @nullable */
  summary?: string | null;
  /** @nullable */
  outcome?: VoiceCallOutcome;
  /** @nullable */
  durationSeconds?: number | null;
  /** @nullable */
  enquiryId?: string | null;
  /** @nullable */
  outreachExecutionId?: string | null;
  /** @nullable */
  lastError?: string | null;
  /** @nullable */
  voiceStyleVersion?: VoiceCallVoiceStyleVersion;
  /** @nullable */
  startedAt?: string | null;
  /** @nullable */
  endedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InboundVoiceSimulationRequest {
  /**
     * @minLength 3
     * @maxLength 40
     */
  fromNumber: string;
  /** @nullable */
  contactId?: string | null;
  /**
     * @minItems 1
     * @maxItems 12
     * @items.minLength 1
     * @items.maxLength 4000
     */
  turns: string[];
}

export type OutboundVoiceCallRequestKind = typeof OutboundVoiceCallRequestKind[keyof typeof OutboundVoiceCallRequestKind];


export const OutboundVoiceCallRequestKind = {
  ai: 'ai',
  click_to_call: 'click_to_call',
} as const;

export interface OutboundVoiceCallRequest {
  contactId: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  objective: string;
  kind: OutboundVoiceCallRequestKind;
}

export type CallAuthIdentityType = typeof CallAuthIdentityType[keyof typeof CallAuthIdentityType];


export const CallAuthIdentityType = {
  customer: 'customer',
  staff: 'staff',
} as const;

export type CallAuthRequestStatus = typeof CallAuthRequestStatus[keyof typeof CallAuthRequestStatus];


export const CallAuthRequestStatus = {
  pending: 'pending',
  approved: 'approved',
  denied: 'denied',
  expired: 'expired',
  cancelled: 'cancelled',
} as const;

export type CallAuthGrantStatus = typeof CallAuthGrantStatus[keyof typeof CallAuthGrantStatus];


export const CallAuthGrantStatus = {
  active: 'active',
  revoked: 'revoked',
  expired: 'expired',
  ended: 'ended',
} as const;

export type CallAuthState = typeof CallAuthState[keyof typeof CallAuthState];


export const CallAuthState = {
  anonymous: 'anonymous',
  verification_pending: 'verification_pending',
  verified_customer: 'verified_customer',
  verified_staff: 'verified_staff',
} as const;

/**
 * @pattern ^[a-z][a-z0-9_.:-]{2,119}$
 */
export type CallAuthCapability = string;

export interface CallAuthPairingRequest {
  id: string;
  callId: string;
  /** @pattern ^[A-Z0-9]{6,12}$ */
  pairingReference: string;
  identityType: CallAuthIdentityType;
  /**
     * Masked caller information safe for the approval interface.
     * @minLength 1
     * @maxLength 80
     */
  callerHint: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  tenantName: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  entityName: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  purpose: string;
  /**
     * @minItems 1
     * @maxItems 20
     */
  capabilities: CallAuthCapability[];
  status: CallAuthRequestStatus;
  /** @nullable */
  resolvedAt?: string | null;
  /** @nullable */
  consumedAt?: string | null;
  /** @nullable */
  terminalAt?: string | null;
  createdAt: string;
  expiresAt: string;
  updatedAt: string;
}

export type CallAuthOtpChallengeStatus = typeof CallAuthOtpChallengeStatus[keyof typeof CallAuthOtpChallengeStatus];


export const CallAuthOtpChallengeStatus = {
  pending: 'pending',
  verified: 'verified',
  locked: 'locked',
  expired: 'expired',
  cancelled: 'cancelled',
} as const;

export interface CallAuthOtpChallenge {
  id: string;
  requestId: string;
  callId: string;
  status: CallAuthOtpChallengeStatus;
  /**
     * @minimum 0
     * @maximum 3
     */
  attemptsRemaining: number;
  /**
     * @minLength 1
     * @maxLength 120
     */
  destinationHint: string;
  /** @nullable */
  verifiedAt?: string | null;
  /** @nullable */
  consumedAt?: string | null;
  createdAt: string;
  expiresAt: string;
}

export interface CallAuthGrant {
  id: string;
  callId: string;
  requestId: string;
  identityType: CallAuthIdentityType;
  /** @nullable */
  customerAccountId?: string | null;
  /**
     * @minLength 1
     * @maxLength 200
     */
  purpose: string;
  /**
     * @minItems 1
     * @maxItems 20
     */
  capabilities: CallAuthCapability[];
  /** @minimum 1 */
  authorizationRevision: number;
  status: CallAuthGrantStatus;
  /** @nullable */
  firstUsedAt?: string | null;
  /** @nullable */
  lastUsedAt?: string | null;
  /** @nullable */
  revokedAt?: string | null;
  /** @nullable */
  endedAt?: string | null;
  /** @nullable */
  terminalAt?: string | null;
  createdAt: string;
  expiresAt: string;
}

export interface CallAuthHistoryItem {
  request: CallAuthPairingRequest;
  otpChallenge?: CallAuthOtpChallenge | null;
  grant?: CallAuthGrant | null;
}

export interface CallAuthHistoryPage {
  items: CallAuthHistoryItem[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface CallAuthStatus {
  callId: string;
  state: CallAuthState;
  /** @nullable */
  requestId?: string | null;
  /** @nullable */
  grantId?: string | null;
  identityType?: CallAuthIdentityType | null;
  /** @maxItems 20 */
  capabilities: CallAuthCapability[];
  /** @nullable */
  expiresAt?: string | null;
}

export interface ApproveCallAuthRequestInput {
  expectedUpdatedAt: string;
}

export interface DenyCallAuthRequestInput {
  expectedUpdatedAt: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  reason?: string;
}

export interface RevokeCallAuthGrantInput {
  /**
     * @minLength 1
     * @maxLength 500
     */
  reason: string;
}

export interface TwilioCallAuthDtmfInput {
  /** @pattern ^CA[a-fA-F0-9]{32}$ */
  CallSid: string;
  /** @pattern ^[0-9]{6}$ */
  Digits: string;
}

export type MailboxIntakeStatus = typeof MailboxIntakeStatus[keyof typeof MailboxIntakeStatus];


export const MailboxIntakeStatus = {
  inactive: 'inactive',
  ready: 'ready',
  active: 'active',
  paused: 'paused',
  setup_required: 'setup_required',
  disconnected: 'disconnected',
  error: 'error',
} as const;

export type MailboxIntakeConnectionStatus = typeof MailboxIntakeConnectionStatus[keyof typeof MailboxIntakeConnectionStatus];


export const MailboxIntakeConnectionStatus = {
  setup_required: 'setup_required',
  ready: 'ready',
  expired: 'expired',
  revoked: 'revoked',
  disconnected: 'disconnected',
  error: 'error',
} as const;

export type MailboxIntakeConnectionCreateAccessMode = typeof MailboxIntakeConnectionCreateAccessMode[keyof typeof MailboxIntakeConnectionCreateAccessMode];


export const MailboxIntakeConnectionCreateAccessMode = {
  delegated: 'delegated',
  application: 'application',
} as const;

export interface MailboxIntakeConnectionCreate {
  accessMode: MailboxIntakeConnectionCreateAccessMode;
  useScopedConnection?: boolean;
  expectedConnectionId?: string;
  reconnectConnectionId?: string;
}

export type MailboxIntakeConnectionProvider = typeof MailboxIntakeConnectionProvider[keyof typeof MailboxIntakeConnectionProvider];


export const MailboxIntakeConnectionProvider = {
  microsoft365: 'microsoft365',
} as const;

export type MailboxIntakeConnectionAccessMode = typeof MailboxIntakeConnectionAccessMode[keyof typeof MailboxIntakeConnectionAccessMode];


export const MailboxIntakeConnectionAccessMode = {
  delegated: 'delegated',
  application: 'application',
} as const;

export type MailboxIntakeConnectionConnectionSource = typeof MailboxIntakeConnectionConnectionSource[keyof typeof MailboxIntakeConnectionConnectionSource];


export const MailboxIntakeConnectionConnectionSource = {
  legacy: 'legacy',
  tenant: 'tenant',
  entity: 'entity',
} as const;

export type MailboxIntakeReadinessState = typeof MailboxIntakeReadinessState[keyof typeof MailboxIntakeReadinessState];


export const MailboxIntakeReadinessState = {
  setup_required: 'setup_required',
  ready: 'ready',
  blocked: 'blocked',
  error: 'error',
} as const;

export interface MailboxIntakeReadiness {
  state: MailboxIntakeReadinessState;
  code: string;
  message: string;
}

export interface MailboxIntakeConnection {
  id: string;
  provider: MailboxIntakeConnectionProvider;
  accessMode: MailboxIntakeConnectionAccessMode;
  status: MailboxIntakeConnectionStatus;
  readiness: MailboxIntakeReadiness;
  /** @nullable */
  externalTenantId?: string | null;
  /** @nullable */
  scopedConnectionId?: string | null;
  /** @maxItems 50 */
  allowedMailboxes?: string[];
  connectionSource?: MailboxIntakeConnectionConnectionSource;
  /** @nullable */
  verifiedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MailboxIntakeConnectionList {
  items: MailboxIntakeConnection[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface MailboxIntakeDisconnect {
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
}

export interface MailboxIntakeFolder {
  id: string;
  providerFolderId: string;
  displayName: string;
  enabled: boolean;
  /** @nullable */
  lastSuccessfulSyncAt?: string | null;
  /** @nullable */
  lastErrorCode?: string | null;
  /** @nullable */
  lastError?: string | null;
}

export type MailboxGraphSubscriptionStatus = typeof MailboxGraphSubscriptionStatus[keyof typeof MailboxGraphSubscriptionStatus];


export const MailboxGraphSubscriptionStatus = {
  active: 'active',
  renewal_due: 'renewal_due',
  reauthorization_required: 'reauthorization_required',
  removed: 'removed',
  expired: 'expired',
  disconnected: 'disconnected',
  error: 'error',
} as const;

export interface MailboxGraphSubscription {
  id: string;
  folderId: string;
  status: MailboxGraphSubscriptionStatus;
  expiresAt: string;
  /** @nullable */
  lastNotificationAt: string | null;
}

export type MailboxIntakeMailboxCreateMailboxType = typeof MailboxIntakeMailboxCreateMailboxType[keyof typeof MailboxIntakeMailboxCreateMailboxType];


export const MailboxIntakeMailboxCreateMailboxType = {
  individual: 'individual',
  shared: 'shared',
} as const;

export interface MailboxIntakeMailboxCreate {
  connectionId: string;
  mailboxType: MailboxIntakeMailboxCreateMailboxType;
  mailboxAddress: string;
  /**
     * @maxLength 200
     * @nullable
     */
  displayName?: string | null;
  /**
     * @minItems 1
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 500
     */
  folderIds: string[];
}

export type MailboxIntakeMailboxUpdateAction = typeof MailboxIntakeMailboxUpdateAction[keyof typeof MailboxIntakeMailboxUpdateAction];


export const MailboxIntakeMailboxUpdateAction = {
  verify: 'verify',
  activate: 'activate',
  pause: 'pause',
  resume: 'resume',
  disconnect: 'disconnect',
} as const;

export interface MailboxIntakeMailboxUpdate {
  expectedUpdatedAt: string;
  /**
     * @maxLength 200
     * @nullable
     */
  displayName?: string | null;
  /**
     * @minItems 1
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 500
     */
  folderIds?: string[];
  action?: MailboxIntakeMailboxUpdateAction;
  automaticRoutingEnabled?: boolean;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason?: string;
}

export type MailboxIntakeMailboxProvider = typeof MailboxIntakeMailboxProvider[keyof typeof MailboxIntakeMailboxProvider];


export const MailboxIntakeMailboxProvider = {
  microsoft365: 'microsoft365',
} as const;

export type MailboxIntakeMailboxMailboxType = typeof MailboxIntakeMailboxMailboxType[keyof typeof MailboxIntakeMailboxMailboxType];


export const MailboxIntakeMailboxMailboxType = {
  individual: 'individual',
  shared: 'shared',
} as const;

export interface MailboxIntakeMailbox {
  id: string;
  connectionId: string;
  provider: MailboxIntakeMailboxProvider;
  mailboxType: MailboxIntakeMailboxMailboxType;
  mailboxAddress: string;
  /** @nullable */
  displayName?: string | null;
  status: MailboxIntakeStatus;
  readiness: MailboxIntakeReadiness;
  monitoringEnabled: boolean;
  automaticRoutingEnabled: boolean;
  /** @nullable */
  activationWatermarkAt?: string | null;
  /** @nullable */
  lastSuccessfulSyncAt?: string | null;
  /** @nullable */
  lastErrorCode?: string | null;
  /** @nullable */
  lastError?: string | null;
  folders: MailboxIntakeFolder[];
  subscriptions: MailboxGraphSubscription[];
  createdAt: string;
  updatedAt: string;
}

export interface MailboxIntakeMailboxList {
  items: MailboxIntakeMailbox[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

/**
 * @nullable
 */
export type MailboxGraphNotificationBatchValueItemChangeType = typeof MailboxGraphNotificationBatchValueItemChangeType[keyof typeof MailboxGraphNotificationBatchValueItemChangeType] | null;


export const MailboxGraphNotificationBatchValueItemChangeType = {
  created: 'created',
  updated: 'updated',
  deleted: 'deleted',
} as const;

/**
 * @nullable
 */
export type MailboxGraphNotificationBatchValueItemLifecycleEvent = typeof MailboxGraphNotificationBatchValueItemLifecycleEvent[keyof typeof MailboxGraphNotificationBatchValueItemLifecycleEvent] | null;


export const MailboxGraphNotificationBatchValueItemLifecycleEvent = {
  missed: 'missed',
  subscriptionRemoved: 'subscriptionRemoved',
  reauthorizationRequired: 'reauthorizationRequired',
} as const;

/**
 * @nullable
 */
export type MailboxGraphNotificationBatchValueItemResourceData = {
  /** @maxLength 1000 */
  id?: string;
  [key: string]: unknown;
 } | null;

export type MailboxGraphNotificationBatchValueItem = {
  /**
     * @minLength 1
     * @maxLength 500
     */
  subscriptionId: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  clientState: string;
  /** @nullable */
  changeType?: MailboxGraphNotificationBatchValueItemChangeType;
  /** @nullable */
  lifecycleEvent?: MailboxGraphNotificationBatchValueItemLifecycleEvent;
  /**
     * @maxLength 2000
     * @nullable
     */
  resource?: string | null;
  /** @nullable */
  subscriptionExpirationDateTime?: string | null;
  /** @nullable */
  resourceData?: MailboxGraphNotificationBatchValueItemResourceData;
  [key: string]: unknown;
 };

export interface MailboxGraphNotificationBatch {
  /**
     * @minItems 1
     * @maxItems 100
     */
  value: MailboxGraphNotificationBatchValueItem[];
}

export interface MailboxGraphNotificationReceipt {
  /** @minimum 0 */
  accepted: number;
  /** @minimum 0 */
  queued: number;
  /** @minimum 0 */
  rejected: number;
}

export type MailboxRoutingTargetKind = typeof MailboxRoutingTargetKind[keyof typeof MailboxRoutingTargetKind];


export const MailboxRoutingTargetKind = {
  lead: 'lead',
  pipeline: 'pipeline',
} as const;

export interface MailboxRoutingTarget {
  kind: MailboxRoutingTargetKind;
  pipeline?: OpportunityPipeline | null;
  /** @nullable */
  stage?: string | null;
  /**
     * @minLength 1
     * @maxLength 200
     */
  ownerId: string;
}

export interface MailboxIntakeFilters {
  /**
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 500
     */
  folderIds: string[];
  /**
     * @maxItems 100
     * @items.minLength 1
     * @items.maxLength 255
     */
  allowedSenderDomains: string[];
  /**
     * @maxItems 100
     * @items.minLength 1
     * @items.maxLength 255
     */
  blockedSenderDomains: string[];
}

export interface MailboxRoutingPromptRevisionCreate {
  /**
     * @minLength 1
     * @maxLength 20000
     */
  prompt: string;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  note: string;
  target: MailboxRoutingTarget;
  filters: MailboxIntakeFilters;
}

export type MailboxRoutingPromptRevision = MailboxRoutingPromptRevisionCreate & ({
  id: string;
  configurationId: string;
  /** @minimum 1 */
  version: number;
  status: 'draft' | 'active' | 'retired';
  configurationUpdatedAt: string;
  /** @nullable */
  aiAuditId?: string | null;
  createdBy: string;
  createdAt: string;
  /** @nullable */
  activatedBy?: string | null;
  /** @nullable */
  activatedAt?: string | null;
});

export interface MailboxRoutingPromptRevisionList {
  items: MailboxRoutingPromptRevision[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface MailboxRoutingPromptTestRequest {
  messageId: string;
  includeAttachments?: boolean;
}

export type MailboxRoutingPromptTestResultExtractedFields = {
  organisationName?: string;
  contactName?: string;
  contactEmail?: string;
  requestSummary?: string;
};

export type MailboxRoutingClassification = typeof MailboxRoutingClassification[keyof typeof MailboxRoutingClassification];


export const MailboxRoutingClassification = {
  irrelevant: 'irrelevant',
  linked_reply: 'linked_reply',
  new_lead_request: 'new_lead_request',
  pipeline_request: 'pipeline_request',
  needs_review: 'needs_review',
} as const;

export type MailboxEvidenceReferenceKind = typeof MailboxEvidenceReferenceKind[keyof typeof MailboxEvidenceReferenceKind];


export const MailboxEvidenceReferenceKind = {
  message: 'message',
  attachment: 'attachment',
} as const;

export interface MailboxEvidenceReference {
  kind: MailboxEvidenceReferenceKind;
  id: string;
  /**
     * @maxLength 1000
     * @nullable
     */
  excerpt?: string | null;
}

export interface MailboxRoutingPromptTestResult {
  id: string;
  configurationId: string;
  promptRevisionId: string;
  sourceId: string;
  classification: MailboxRoutingClassification;
  reason: string;
  evidence: MailboxEvidenceReference[];
  missingEvidence: string[];
  extractedFields: MailboxRoutingPromptTestResultExtractedFields;
  proposedTarget: MailboxRoutingTarget | null;
  /** @pattern ^[a-f0-9]{64}$ */
  sourceContentHash: string;
  /**
     * @nullable
     * @pattern ^[a-f0-9]{64}$
     */
  attachmentEvidenceHash: string | null;
  /** @nullable */
  model: string | null;
  writesPerformed: false;
  /** @nullable */
  aiAuditId?: string | null;
  createdAt: string;
}

export interface MailboxRoutingPromptActivation {
  expectedConfigurationUpdatedAt: string;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
}

export interface MailboxRoutingPromptPause {
  expectedConfigurationUpdatedAt: string;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
}

export type MailboxRoutingPromptConfigurationStateStatus = typeof MailboxRoutingPromptConfigurationStateStatus[keyof typeof MailboxRoutingPromptConfigurationStateStatus];


export const MailboxRoutingPromptConfigurationStateStatus = {
  draft: 'draft',
  active: 'active',
  paused: 'paused',
} as const;

export interface MailboxRoutingPromptConfigurationState {
  configurationId: string;
  mailboxId: string;
  status: MailboxRoutingPromptConfigurationStateStatus;
  /**
     * @minimum 1
     * @nullable
     */
  activeRevision: number | null;
  updatedAt: string;
}

export type MailboxAttachmentEvidenceAttachmentType = typeof MailboxAttachmentEvidenceAttachmentType[keyof typeof MailboxAttachmentEvidenceAttachmentType];


export const MailboxAttachmentEvidenceAttachmentType = {
  file: 'file',
  item: 'item',
  reference: 'reference',
} as const;

export type MailboxAttachmentEvidenceStorageStatus = typeof MailboxAttachmentEvidenceStorageStatus[keyof typeof MailboxAttachmentEvidenceStorageStatus];


export const MailboxAttachmentEvidenceStorageStatus = {
  pending: 'pending',
  ready: 'ready',
  failed: 'failed',
  unavailable: 'unavailable',
} as const;

export type MailboxAttachmentEvidenceExtractionStatus = typeof MailboxAttachmentEvidenceExtractionStatus[keyof typeof MailboxAttachmentEvidenceExtractionStatus];


export const MailboxAttachmentEvidenceExtractionStatus = {
  pending: 'pending',
  ready: 'ready',
  unsupported: 'unsupported',
  encrypted: 'encrypted',
  oversized: 'oversized',
  failed: 'failed',
  unavailable: 'unavailable',
} as const;

export interface MailboxAttachmentEvidence {
  id: string;
  /** @nullable */
  fileId?: string | null;
  attachmentType: MailboxAttachmentEvidenceAttachmentType;
  isInline: boolean;
  filename: string;
  contentType: string;
  /** @minimum 0 */
  sizeBytes: number;
  /** @nullable */
  contentHash?: string | null;
  storageStatus: MailboxAttachmentEvidenceStorageStatus;
  extractionStatus: MailboxAttachmentEvidenceExtractionStatus;
  /** @nullable */
  extractionErrorCode?: string | null;
  requiredEvidence: boolean;
  /** @nullable */
  contentUrl: string | null;
}

export type MailboxRoutingCandidateStatus = typeof MailboxRoutingCandidateStatus[keyof typeof MailboxRoutingCandidateStatus];


export const MailboxRoutingCandidateStatus = {
  pending_review: 'pending_review',
  eligible_automatic: 'eligible_automatic',
  approved: 'approved',
  dismissed: 'dismissed',
  executed: 'executed',
  stale: 'stale',
  failed: 'failed',
} as const;

export interface MailboxRoutingEmailSummary {
  subject: string;
  fromAddress: string;
  toAddress: string;
  /** @maxLength 4000 */
  bodyExcerpt: string;
  receivedAt: string;
  /** @nullable */
  outlookWebUrl: string | null;
}

export interface MailboxRoutingCandidate {
  id: string;
  mailboxId: string;
  sourceId: string;
  messageId: string;
  email: MailboxRoutingEmailSummary;
  /** @nullable */
  promptRevisionId?: string | null;
  classification: MailboxRoutingClassification;
  status: MailboxRoutingCandidateStatus;
  knownContactNewNeed: boolean;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  linkedMessageId?: string | null;
  reason: string;
  evidence: MailboxEvidenceReference[];
  missingEvidence: string[];
  attachments: MailboxAttachmentEvidence[];
  /** @nullable */
  aiAuditId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MailboxRoutingCandidateList {
  items: MailboxRoutingCandidate[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type MailboxRoutingCandidateDecisionAction = typeof MailboxRoutingCandidateDecisionAction[keyof typeof MailboxRoutingCandidateDecisionAction];


export const MailboxRoutingCandidateDecisionAction = {
  approve: 'approve',
  correct: 'correct',
  link: 'link',
  dismiss: 'dismiss',
  retry: 'retry',
} as const;

export interface MailboxRoutingCandidateDecision {
  action: MailboxRoutingCandidateDecisionAction;
  expectedUpdatedAt: string;
  /**
     * @minLength 3
     * @maxLength 1000
     */
  reason: string;
  classification?: MailboxRoutingClassification | null;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  linkedMessageId?: string | null;
}

export type MailboxIntakeJobKind = typeof MailboxIntakeJobKind[keyof typeof MailboxIntakeJobKind];


export const MailboxIntakeJobKind = {
  sync: 'sync',
  analysis: 'analysis',
  lookback_preview: 'lookback_preview',
  lookback_import: 'lookback_import',
  subscription_renewal: 'subscription_renewal',
} as const;

export type MailboxIntakeJobStatus = typeof MailboxIntakeJobStatus[keyof typeof MailboxIntakeJobStatus];


export const MailboxIntakeJobStatus = {
  queued: 'queued',
  running: 'running',
  paused: 'paused',
  retry: 'retry',
  completed: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
  stale: 'stale',
} as const;

export interface MailboxIntakeJob {
  id: string;
  mailboxId: string;
  kind: MailboxIntakeJobKind;
  status: MailboxIntakeJobStatus;
  /** @nullable */
  promptConfigurationId?: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  promptRevision?: number | null;
  /** @minimum 0 */
  totalItems: number;
  /** @minimum 0 */
  processedItems: number;
  /** @minimum 0 */
  createdItems: number;
  /** @minimum 0 */
  reviewItems: number;
  /** @minimum 0 */
  duplicateItems: number;
  /** @minimum 0 */
  failedItems: number;
  /** @minimum 0 */
  attemptCount: number;
  /** @nullable */
  nextAttemptAt?: string | null;
  /** @nullable */
  startedAt?: string | null;
  /** @nullable */
  completedAt?: string | null;
  /** @nullable */
  lastErrorCode?: string | null;
  /** @nullable */
  lastError?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MailboxIntakeJobList {
  items: MailboxIntakeJob[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface MailboxIntakeJobCancel {
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
  expectedUpdatedAt: string;
}

export interface MailboxHistoryPreviewCreate {
  mailboxId: string;
  /**
     * @minItems 1
     * @maxItems 20
     */
  folderIds: string[];
  receivedFrom: string;
  receivedTo: string;
  promptRevisionId: string;
}

export interface MailboxHistoryPreview {
  job: MailboxIntakeJob;
  candidates: MailboxRoutingCandidate[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
  writesPerformed: false;
}

export interface MailboxHistoryImportConfirm {
  expectedPreviewUpdatedAt: string;
  promptRevisionId: string;
  /**
     * @minItems 1
     * @maxItems 1000
     */
  candidateIds: string[];
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
}

export type MailboxRoutingExecutionStatus = typeof MailboxRoutingExecutionStatus[keyof typeof MailboxRoutingExecutionStatus];


export const MailboxRoutingExecutionStatus = {
  queued: 'queued',
  claimed: 'claimed',
  executed: 'executed',
  dismissed: 'dismissed',
  failed: 'failed',
  stale: 'stale',
  cancelled: 'cancelled',
} as const;

export type MailboxRoutingExecutionTargetKind = typeof MailboxRoutingExecutionTargetKind[keyof typeof MailboxRoutingExecutionTargetKind];


export const MailboxRoutingExecutionTargetKind = {
  lead: 'lead',
  pipeline: 'pipeline',
  link_only: 'link_only',
  none: 'none',
} as const;

export type MailboxRoutingExecutionActorType = typeof MailboxRoutingExecutionActorType[keyof typeof MailboxRoutingExecutionActorType];


export const MailboxRoutingExecutionActorType = {
  automation: 'automation',
  staff: 'staff',
} as const;

export interface MailboxRoutingExecution {
  id: string;
  candidateId: string;
  status: MailboxRoutingExecutionStatus;
  targetKind: MailboxRoutingExecutionTargetKind;
  /** @nullable */
  promptRevisionId?: string | null;
  /** @nullable */
  enquiryId?: string | null;
  /** @nullable */
  opportunityId?: string | null;
  /** @nullable */
  interactionId?: string | null;
  /** @nullable */
  aiAuditId?: string | null;
  actorType: MailboxRoutingExecutionActorType;
  actorId: string;
  reason: string;
  /** @minimum 0 */
  attemptCount: number;
  /** @nullable */
  lastErrorCode?: string | null;
  /** @nullable */
  lastError?: string | null;
  /** @nullable */
  executedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MailboxRoutingExecutionList {
  items: MailboxRoutingExecution[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type EmailConnectionStatus = typeof EmailConnectionStatus[keyof typeof EmailConnectionStatus];


export const EmailConnectionStatus = {
  disconnected: 'disconnected',
  connected: 'connected',
  error: 'error',
  expired: 'expired',
} as const;

export interface EmailConnection {
  status: EmailConnectionStatus;
  providerKey: string;
  connected: boolean;
  /** @nullable */
  mailboxAddress?: string | null;
  /** @nullable */
  displayName?: string | null;
  dailyLimit: number;
  sentToday: number;
  /** @nullable */
  expiresAt?: string | null;
  healthAutomationReady: boolean;
  /** @nullable */
  healthAutomationBlockedReason: string | null;
}

export interface EmailOAuthStart {
  authorizationUrl: string;
}

export interface EmailTestRequest {
  to: string;
  /**
     * @minLength 1
     * @maxLength 998
     */
  subject: string;
  /** @minLength 1 */
  body: string;
}

export interface EmailVariant {
  key: string;
  subject: string;
  body: string;
  /**
     * @minimum 1
     * @maximum 100
     */
  weight: number;
}

export type EmailSequenceStepCreateType = typeof EmailSequenceStepCreateType[keyof typeof EmailSequenceStepCreateType];


export const EmailSequenceStepCreateType = {
  email: 'email',
  wait: 'wait',
  condition: 'condition',
} as const;

/**
 * @nullable
 */
export type EmailSequenceStepCreateCondition = typeof EmailSequenceStepCreateCondition[keyof typeof EmailSequenceStepCreateCondition] | null;


export const EmailSequenceStepCreateCondition = {
  opened: 'opened',
  replied: 'replied',
  not_opened: 'not_opened',
  not_replied: 'not_replied',
} as const;

export interface EmailSequenceStepCreate {
  /** @minimum 1 */
  position: number;
  type: EmailSequenceStepCreateType;
  /** @minLength 1 */
  name: string;
  subject?: string;
  body?: string;
  /** @minimum 0 */
  delayMinutes?: number;
  /** @nullable */
  condition?: EmailSequenceStepCreateCondition;
  /** @nullable */
  trueStepPosition?: number | null;
  /** @nullable */
  falseStepPosition?: number | null;
  variants?: EmailVariant[];
  /** @nullable */
  aiAuditId?: string | null;
}

export type EmailSequenceStep = EmailSequenceStepCreate & ({
  id: string;
  sequenceId: string;
  /** @nullable */
  aiAuditId?: string | null;
  createdAt: string;
  updatedAt: string;
}) & Required<Pick<EmailSequenceStepCreate & ({
  id: string;
  sequenceId: string;
  /** @nullable */
  aiAuditId?: string | null;
  createdAt: string;
  updatedAt: string;
}), 'delayMinutes' | 'variants'>>;

export type EmailSequenceCreateStatus = typeof EmailSequenceCreateStatus[keyof typeof EmailSequenceCreateStatus];


export const EmailSequenceCreateStatus = {
  draft: 'draft',
  active: 'active',
  paused: 'paused',
  archived: 'archived',
} as const;

export interface EmailSequenceCreate {
  /** @minLength 1 */
  name: string;
  description?: string;
  status?: EmailSequenceCreateStatus;
  stopOnReply?: boolean;
  /**
     * @minimum 1
     * @maximum 500
     */
  dailyLimit?: number;
}

export type EmailSequenceUpdateStatus = typeof EmailSequenceUpdateStatus[keyof typeof EmailSequenceUpdateStatus];


export const EmailSequenceUpdateStatus = {
  draft: 'draft',
  active: 'active',
  paused: 'paused',
  archived: 'archived',
} as const;

export interface EmailSequenceUpdate {
  expectedUpdatedAt: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @maxLength 2000
     * @nullable
     */
  description?: string | null;
  status?: EmailSequenceUpdateStatus;
  stopOnReply?: boolean;
  /**
     * @minimum 1
     * @maximum 500
     */
  dailyLimit?: number;
}

export type EmailSequenceStatus = typeof EmailSequenceStatus[keyof typeof EmailSequenceStatus];


export const EmailSequenceStatus = {
  draft: 'draft',
  active: 'active',
  paused: 'paused',
  archived: 'archived',
} as const;

export type EmailSequence = EmailSequenceCreate & {
  id: string;
  status: EmailSequenceStatus;
  stopOnReply: boolean;
  providerKey: string;
  dailyLimit: number;
  steps: EmailSequenceStep[];
  createdAt: string;
  updatedAt: string;
};

export interface EmailEnrollmentCreate {
  email: string;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  accountId?: string | null;
}

export type EmailEnrollmentStatus = typeof EmailEnrollmentStatus[keyof typeof EmailEnrollmentStatus];


export const EmailEnrollmentStatus = {
  active: 'active',
  paused: 'paused',
  completed: 'completed',
  replied: 'replied',
  stopped: 'stopped',
  suppressed: 'suppressed',
  failed: 'failed',
} as const;

export type EmailEnrollment = EmailEnrollmentCreate & ({
  id: string;
  sequenceId: string;
  status: EmailEnrollmentStatus;
  currentStepPosition: number;
  nextActionAt: string;
  /** @nullable */
  stoppedReason?: string | null;
  /** @nullable */
  lastError?: string | null;
  createdAt: string;
  updatedAt: string;
});

export type EmailEnrollmentUpdateAction = typeof EmailEnrollmentUpdateAction[keyof typeof EmailEnrollmentUpdateAction];


export const EmailEnrollmentUpdateAction = {
  pause: 'pause',
  resume: 'resume',
  stop: 'stop',
} as const;

export interface EmailEnrollmentUpdate {
  action: EmailEnrollmentUpdateAction;
  expectedUpdatedAt: string;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
}

export interface EmailEnrollmentList {
  items: EmailEnrollment[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface EmailRecipient {
  email: string;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  accountId?: string | null;
}

export type EmailRecipientPreviewItemStatus = typeof EmailRecipientPreviewItemStatus[keyof typeof EmailRecipientPreviewItemStatus];


export const EmailRecipientPreviewItemStatus = {
  eligible: 'eligible',
  suppressed: 'suppressed',
  contact_opt_out: 'contact_opt_out',
  consent_opted_out: 'consent_opted_out',
  consent_missing: 'consent_missing',
  identity_mismatch: 'identity_mismatch',
} as const;

export interface EmailRecipientPreviewItem {
  email: string;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  accountId?: string | null;
  eligible: boolean;
  status: EmailRecipientPreviewItemStatus;
  reason: string;
}

export interface EmailRecipientPreview {
  items: EmailRecipientPreviewItem[];
  /** @minimum 0 */
  total: number;
  /** @minimum 0 */
  eligible: number;
  /** @minimum 0 */
  blocked: number;
}

export type SmsProviderStatusProvider = typeof SmsProviderStatusProvider[keyof typeof SmsProviderStatusProvider];


export const SmsProviderStatusProvider = {
  twilio: 'twilio',
  simulation: 'simulation',
} as const;

export interface SmsProviderStatus {
  provider: SmsProviderStatusProvider;
  configured: boolean;
}

export interface TwilioInboundSms {
  /**
     * @minLength 10
     * @maxLength 80
     */
  MessageSid: string;
  /**
     * @minLength 5
     * @maxLength 50
     */
  From: string;
  /**
     * @minLength 5
     * @maxLength 50
     */
  To: string;
  /** @maxLength 1600 */
  Body: string;
  [key: string]: unknown;
 }

export interface TwilioSmsStatus {
  /**
     * @minLength 10
     * @maxLength 80
     */
  MessageSid: string;
  /**
     * @minLength 2
     * @maxLength 40
     */
  MessageStatus: string;
  /**
     * @maxLength 40
     * @nullable
     */
  ErrorCode?: string | null;
  /**
     * @maxLength 500
     * @nullable
     */
  ErrorMessage?: string | null;
  [key: string]: unknown;
 }

export interface SmsTestSend {
  /**
     * @minLength 5
     * @maxLength 50
     */
  to: string;
  /**
     * @minLength 1
     * @maxLength 1600
     */
  body: string;
}

export type SmsCampaignStepUpdateSmsConsentMode = typeof SmsCampaignStepUpdateSmsConsentMode[keyof typeof SmsCampaignStepUpdateSmsConsentMode];


export const SmsCampaignStepUpdateSmsConsentMode = {
  unless_opted_out: 'unless_opted_out',
  opted_in_only: 'opted_in_only',
} as const;

export interface SmsCampaignStepUpdate {
  /**
     * @minLength 1
     * @maxLength 1600
     */
  smsBody: string;
  smsConsentMode: SmsCampaignStepUpdateSmsConsentMode;
  /** Explicitly confirms a change from opted-in-only to unless-opted-out. */
  confirmOverride?: boolean;
}

export type SmsSuppressionReason = typeof SmsSuppressionReason[keyof typeof SmsSuppressionReason];


export const SmsSuppressionReason = {
  no_mobile: 'no_mobile',
  do_not_phone: 'do_not_phone',
  opted_out: 'opted_out',
  stop_reply: 'stop_reply',
  suppression_list: 'suppression_list',
  invalid_number: 'invalid_number',
  consent_missing: 'consent_missing',
} as const;

export interface SmsRecipientPreviewItem {
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  accountId?: string | null;
  email: string;
  firstName: string;
  lastName: string;
  /** @nullable */
  company?: string | null;
  /** @nullable */
  mobile?: string | null;
  /** @nullable */
  normalizedNumber?: string | null;
  eligible: boolean;
  suppressionReason: SmsSuppressionReason | null;
  /** @nullable */
  reason?: string | null;
  /** @nullable */
  previouslyTextedAt?: string | null;
  /** @nullable */
  previousCampaignId?: string | null;
  /** @nullable */
  previousCampaignName?: string | null;
}

export interface SmsRecipientPreview {
  eligible: SmsRecipientPreviewItem[];
  suppressed: SmsRecipientPreviewItem[];
  /** @minimum 0 */
  total: number;
  /** @minimum 0 */
  eligibleTotal: number;
  /** @minimum 0 */
  suppressedTotal: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type SmsDeliveryStatus = typeof SmsDeliveryStatus[keyof typeof SmsDeliveryStatus];


export const SmsDeliveryStatus = {
  queued: 'queued',
  dispatching: 'dispatching',
  retry: 'retry',
  sent: 'sent',
  suppressed: 'suppressed',
  failed: 'failed',
  uncertain: 'uncertain',
  cancelled: 'cancelled',
} as const;

export type SmsDeliveryJobKind = typeof SmsDeliveryJobKind[keyof typeof SmsDeliveryJobKind];


export const SmsDeliveryJobKind = {
  campaign: 'campaign',
  test: 'test',
  reply: 'reply',
  health: 'health',
} as const;

export type SmsDeliveryJobProviderKey = typeof SmsDeliveryJobProviderKey[keyof typeof SmsDeliveryJobProviderKey];


export const SmsDeliveryJobProviderKey = {
  twilio: 'twilio',
  simulation: 'simulation',
} as const;

export interface SmsDeliveryJob {
  id: string;
  kind: SmsDeliveryJobKind;
  /** @nullable */
  campaignId?: string | null;
  /** @nullable */
  campaignStepId?: string | null;
  /** @nullable */
  messageId?: string | null;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  outreachExecutionId?: string | null;
  /** @nullable */
  nextActionId?: string | null;
  /** @nullable */
  contactName?: string | null;
  toNumber: string;
  fromNumber: string;
  body?: string;
  providerKey: SmsDeliveryJobProviderKey;
  /** @nullable */
  providerMessageId?: string | null;
  status: SmsDeliveryStatus;
  /** @nullable */
  reason?: string | null;
  runAt: string;
  /** @nullable */
  nextAttemptAt?: string | null;
  /** @minimum 0 */
  attemptCount: number;
  /** @nullable */
  sentAt?: string | null;
  /** @nullable */
  deliveredAt?: string | null;
  /** @nullable */
  lastErrorCode?: string | null;
  /** @nullable */
  lastError?: string | null;
  /** @minimum 0 */
  clicks: number;
  registered: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SmsDeliveryJobList {
  items: SmsDeliveryJob[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface SmsSendResult {
  /** @minimum 0 */
  queued: number;
  /** @minimum 0 */
  suppressed: number;
  /** @minimum 0 */
  total: number;
}

export interface SmsCampaignSummary {
  /** @minimum 0 */
  sent: number;
  /** @minimum 0 */
  delivered: number;
  /** @minimum 0 */
  failed: number;
  /** @minimum 0 */
  suppressed: number;
  /** @minimum 0 */
  clicked: number;
  /** @minimum 0 */
  registered: number;
}

export type SmsHistoryItemDirection = typeof SmsHistoryItemDirection[keyof typeof SmsHistoryItemDirection];


export const SmsHistoryItemDirection = {
  inbound: 'inbound',
  outbound: 'outbound',
} as const;

export type SmsHistoryItemStatus = typeof SmsHistoryItemStatus[keyof typeof SmsHistoryItemStatus];


export const SmsHistoryItemStatus = {
  queued: 'queued',
  sent: 'sent',
  delivered: 'delivered',
  undelivered: 'undelivered',
  failed: 'failed',
  received: 'received',
} as const;

export interface SmsHistoryItem {
  id: string;
  /** @nullable */
  campaignId?: string | null;
  /** @nullable */
  campaignName?: string | null;
  direction: SmsHistoryItemDirection;
  body: string;
  status: SmsHistoryItemStatus;
  /** @minimum 0 */
  clicks: number;
  /** @minimum 1 */
  segments: number;
  /** @nullable */
  sentAt?: string | null;
  /** @nullable */
  deliveredAt?: string | null;
  createdAt: string;
  /** @nullable */
  errorCode?: string | null;
  /** @nullable */
  errorMessage?: string | null;
}

export interface SmsHistoryList {
  items: SmsHistoryItem[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type CampaignStepCreateType = typeof CampaignStepCreateType[keyof typeof CampaignStepCreateType];


export const CampaignStepCreateType = {
  email: 'email',
  sms: 'sms',
  social_post: 'social_post',
  whatsapp: 'whatsapp',
} as const;

export type CampaignStepCreateSmsConsentMode = typeof CampaignStepCreateSmsConsentMode[keyof typeof CampaignStepCreateSmsConsentMode];


export const CampaignStepCreateSmsConsentMode = {
  unless_opted_out: 'unless_opted_out',
  opted_in_only: 'opted_in_only',
} as const;

export type CampaignStepCreateNetworksItem = typeof CampaignStepCreateNetworksItem[keyof typeof CampaignStepCreateNetworksItem];


export const CampaignStepCreateNetworksItem = {
  linkedin: 'linkedin',
  facebook: 'facebook',
  instagram: 'instagram',
  x: 'x',
  youtube: 'youtube',
  google_business: 'google_business',
  tiktok: 'tiktok',
} as const;

export interface CampaignStepCreate {
  type: CampaignStepCreateType;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /** @nullable */
  subject?: string | null;
  /** @nullable */
  body?: string | null;
  /**
     * @maxLength 1600
     * @nullable
     */
  smsBody?: string | null;
  smsConsentMode?: CampaignStepCreateSmsConsentMode;
  networks?: CampaignStepCreateNetworksItem[];
  /** @nullable */
  videoProjectId?: string | null;
  /** @nullable */
  mediaAssetId?: string | null;
  autoPostWhenReady?: boolean;
}

export type CampaignStepStatus = typeof CampaignStepStatus[keyof typeof CampaignStepStatus];


export const CampaignStepStatus = {
  pending: 'pending',
  queued: 'queued',
  partial: 'partial',
  sent: 'sent',
  awaiting_approval: 'awaiting_approval',
  failed: 'failed',
} as const;

export type CampaignStepNetworkOutcomesItemStatus = typeof CampaignStepNetworkOutcomesItemStatus[keyof typeof CampaignStepNetworkOutcomesItemStatus];


export const CampaignStepNetworkOutcomesItemStatus = {
  queued: 'queued',
  skipped: 'skipped',
  published: 'published',
  failed: 'failed',
} as const;

export type CampaignStepNetworkOutcomesItem = {
  network: string;
  status: CampaignStepNetworkOutcomesItemStatus;
  reason?: string;
};

export type CampaignStep = CampaignStepCreate & ({
  id: string;
  campaignId: string;
  /** @minimum 1 */
  position: number;
  /** @nullable */
  readonly smsOverrideConfirmedBy?: string | null;
  /** @nullable */
  readonly smsOverrideConfirmedAt?: string | null;
  status: CampaignStepStatus;
  networkOutcomes: CampaignStepNetworkOutcomesItem[];
  /** @nullable */
  lastError?: string | null;
  createdAt: string;
  updatedAt: string;
}) & Required<Pick<CampaignStepCreate & ({
  id: string;
  campaignId: string;
  /** @minimum 1 */
  position: number;
  /** @nullable */
  readonly smsOverrideConfirmedBy?: string | null;
  /** @nullable */
  readonly smsOverrideConfirmedAt?: string | null;
  status: CampaignStepStatus;
  networkOutcomes: CampaignStepNetworkOutcomesItem[];
  /** @nullable */
  lastError?: string | null;
  createdAt: string;
  updatedAt: string;
}), 'smsConsentMode' | 'autoPostWhenReady'>>;

export type CampaignPlanningLinkKind = typeof CampaignPlanningLinkKind[keyof typeof CampaignPlanningLinkKind];


export const CampaignPlanningLinkKind = {
  content_asset: 'content_asset',
  social_post: 'social_post',
  tender: 'tender',
  managed_document: 'managed_document',
  microsite: 'microsite',
  media_asset: 'media_asset',
} as const;

export interface CampaignPlanningLink {
  kind: CampaignPlanningLinkKind;
  recordId: string;
  /**
     * @minLength 1
     * @maxLength 100
     */
  revision: string;
}

export type CampaignAudienceDefinitionChannelsItemChannel = typeof CampaignAudienceDefinitionChannelsItemChannel[keyof typeof CampaignAudienceDefinitionChannelsItemChannel];


export const CampaignAudienceDefinitionChannelsItemChannel = {
  email: 'email',
  sms: 'sms',
  whatsapp: 'whatsapp',
} as const;

export type CampaignAudienceDefinitionChannelsItem = {
  channel: CampaignAudienceDefinitionChannelsItemChannel;
  /** @nullable */
  stepId: string | null;
};

export interface CampaignAudienceDefinition {
  /** @nullable */
  segmentId: string | null;
  /** @maxItems 20000 */
  contactIds: string[];
  /** @maxItems 20000 */
  excludedContactIds: string[];
  /**
     * @minItems 1
     * @maxItems 3
     */
  channels: CampaignAudienceDefinitionChannelsItem[];
}

export type CampaignBriefFieldsOutputsItem = typeof CampaignBriefFieldsOutputsItem[keyof typeof CampaignBriefFieldsOutputsItem];


export const CampaignBriefFieldsOutputsItem = {
  email: 'email',
  sms: 'sms',
  whatsapp: 'whatsapp',
  social: 'social',
  media: 'media',
  web_page: 'web_page',
  download: 'download',
  meta_ads: 'meta_ads',
  linkedin_ads: 'linkedin_ads',
  google_search: 'google_search',
  google_display: 'google_display',
  youtube_ads: 'youtube_ads',
  tiktok_ads: 'tiktok_ads',
  x_ads: 'x_ads',
} as const;

export interface CampaignBriefFields {
  /**
     * @maxLength 5000
     * @nullable
     */
  objective: string | null;
  /**
     * @maxLength 5000
     * @nullable
     */
  successMeasure: string | null;
  /**
     * @maxLength 5000
     * @nullable
     */
  offer: string | null;
  /**
     * @maxLength 5000
     * @nullable
     */
  audienceRationale: string | null;
  /**
     * @maxItems 50
     * @items.maxLength 1000
     */
  themes: string[];
  /** @maxItems 14 */
  outputs: CampaignBriefFieldsOutputsItem[];
  /** @nullable */
  ownerId: string | null;
  /** @nullable */
  plannedStart: string | null;
  /** @nullable */
  plannedEnd: string | null;
  /**
     * @nullable
     * @pattern ^[0-9]{1,12}(\.[0-9]{1,2})?$
     */
  budget: string | null;
  /**
     * @nullable
     * @pattern ^[A-Z]{3}$
     */
  currency: string | null;
  /**
     * @maxItems 50
     * @items.minLength 1
     * @items.maxLength 1000
     */
  unknowns: string[];
  /** @maxItems 50 */
  evidence: CampaignPlanningLink[];
  /** @maxItems 50 */
  content: CampaignPlanningLink[];
  audience: CampaignAudienceDefinition | null;
}

export interface CampaignBriefChange {
  campaignId: string;
  /** @minimum 0 */
  expectedVersion: number;
  expectedCampaignUpdatedAt: string;
  fields: CampaignBriefFields;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
}

export type CampaignAudienceItemChannelsItem = {
  channel: string;
  eligible: boolean;
  /** @maxItems 50 */
  reasons: string[];
  /** @nullable */
  destination: string | null;
};

export interface CampaignAudienceItem {
  contactId: string;
  /** @nullable */
  accountId: string | null;
  email: string;
  excluded: boolean;
  /** @maxItems 50 */
  channels: CampaignAudienceItemChannelsItem[];
}

export type CampaignAudiencePreviewTotalsItem = {
  channel: string;
  eligible: number;
  ineligible: number;
};

export interface CampaignAudiencePreview {
  /** @maxItems 100 */
  items: CampaignAudienceItem[];
  total: number;
  excluded: number;
  /** @maxItems 50 */
  totals: CampaignAudiencePreviewTotalsItem[];
  fingerprint: string;
  limit: number;
  offset: number;
}

export interface CampaignBrief {
  campaignId: string;
  version: number;
  fields: CampaignBriefFields;
  updatedBy: string;
  updatedAt: string;
  /** @nullable */
  audienceFingerprint: string | null;
  current: boolean;
}

export type CampaignBriefReviewStatus = typeof CampaignBriefReviewStatus[keyof typeof CampaignBriefReviewStatus];


export const CampaignBriefReviewStatus = {
  reviewed: 'reviewed',
  committed: 'committed',
  cancelled: 'cancelled',
} as const;

export interface CampaignBriefReview {
  id: string;
  hash: string;
  change: CampaignBriefChange;
  before: CampaignBrief | null;
  after: CampaignBrief;
  status: CampaignBriefReviewStatus;
  expiresAt: string;
  receipt: CampaignBrief | null;
}

export type EmailCampaignCreateRecipientsItem = {
  email: string;
  contactId?: string;
  accountId?: string;
};

/**
 * A name-only campaign is saved as an unscheduled unfinished draft. Subject, body and recipients may be completed later. Scheduling and delivery require complete content and recipients for every email step; channel-specific consent and review remain mandatory.
 */
export interface EmailCampaignCreate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /** @maxLength 998 */
  subject?: string;
  /** @maxLength 100000 */
  body?: string;
  /** @maxItems 20000 */
  recipients?: EmailCampaignCreateRecipientsItem[];
  /** @nullable */
  scheduledAt?: string | null;
  /** Optional cross-channel steps. Email and SMS steps use their respective consent-checked delivery paths; social-post steps are created in pending approval unless a media manager explicitly enables autoPostWhenReady for a generated-video project. */
  steps?: CampaignStepCreate[];
}

export type CampaignMessagingChangeOperation = typeof CampaignMessagingChangeOperation[keyof typeof CampaignMessagingChangeOperation];


export const CampaignMessagingChangeOperation = {
  update: 'update',
  save_step: 'save_step',
  remove_step: 'remove_step',
  send: 'send',
  control: 'control',
  retry: 'retry',
} as const;

export type CampaignMessagingChangeChannel = typeof CampaignMessagingChangeChannel[keyof typeof CampaignMessagingChangeChannel];


export const CampaignMessagingChangeChannel = {
  email: 'email',
  sms: 'sms',
  whatsapp: 'whatsapp',
} as const;

/**
 * Exact native fields for the selected operation; validated against its native generated schema.
 */
export type CampaignMessagingChangeFields = { [key: string]: unknown };

export interface CampaignMessagingChange {
  operation: CampaignMessagingChangeOperation;
  campaignId: string;
  channel: CampaignMessagingChangeChannel;
  expectedUpdatedAt: string;
  /** @nullable */
  stepId?: string | null;
  /** @nullable */
  deliveryId?: string | null;
  /** Exact native fields for the selected operation; validated against its native generated schema. */
  fields: CampaignMessagingChangeFields;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
}

export interface EmailCampaignUpdate {
  expectedUpdatedAt: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /** @maxLength 998 */
  subject?: string;
  /** @maxLength 100000 */
  body?: string;
  /**
     * @minItems 0
     * @maxItems 500
     */
  recipients?: EmailRecipient[];
  /** @nullable */
  scheduledAt?: string | null;
}

export type EmailCampaignControlAction = typeof EmailCampaignControlAction[keyof typeof EmailCampaignControlAction];


export const EmailCampaignControlAction = {
  pause: 'pause',
  resume: 'resume',
  stop: 'stop',
} as const;

export interface EmailCampaignControl {
  action: EmailCampaignControlAction;
  expectedUpdatedAt: string;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
}

export interface CampaignProgressCounts {
  /** @minimum 0 */
  queued: number;
  /** @minimum 0 */
  dispatching: number;
  /** @minimum 0 */
  retry: number;
  /** @minimum 0 */
  awaitingApproval: number;
  /** @minimum 0 */
  pending: number;
  /** @minimum 0 */
  unconfirmed: number;
  /** @minimum 0 */
  awaitingProviderConfirmation: number;
  /** @minimum 0 */
  succeeded: number;
  /** @minimum 0 */
  partial: number;
  /** @minimum 0 */
  definitiveFailed: number;
  /** @minimum 0 */
  suppressed: number;
  /** @minimum 0 */
  cancelled: number;
}

export interface CampaignSocialTargetCounts {
  /** @minimum 0 */
  queued: number;
  /** @minimum 0 */
  dispatching: number;
  /** @minimum 0 */
  retry: number;
  /** @minimum 0 */
  pending: number;
  /** @minimum 0 */
  unconfirmed: number;
  /** @minimum 0 */
  published: number;
  /** @minimum 0 */
  failed: number;
  /** @minimum 0 */
  skipped: number;
  /** @minimum 0 */
  cancelled: number;
}

export interface CampaignEvidenceMilestones {
  /**
     * @minimum 0
     * @nullable
     */
  accepted: number | null;
  /**
     * @minimum 0
     * @nullable
     */
  sent: number | null;
  /**
     * @minimum 0
     * @nullable
     */
  delivered: number | null;
  /**
     * @minimum 0
     * @nullable
     */
  read: number | null;
  /**
     * @minimum 0
     * @nullable
     */
  published: number | null;
}

export type CampaignChannelProgressChannel = typeof CampaignChannelProgressChannel[keyof typeof CampaignChannelProgressChannel];


export const CampaignChannelProgressChannel = {
  email: 'email',
  sms: 'sms',
  whatsapp: 'whatsapp',
  social_post: 'social_post',
} as const;

export type CampaignChannelProgressUnit = typeof CampaignChannelProgressUnit[keyof typeof CampaignChannelProgressUnit];


export const CampaignChannelProgressUnit = {
  recipient_delivery: 'recipient_delivery',
  social_step: 'social_step',
} as const;

/**
 * @nullable
 */
export type CampaignChannelProgressSocialTargets = {
  /** @minimum 0 */
  total: number;
  counts: CampaignSocialTargetCounts;
} | null;

/**
 * Exclusive counts sum to total. Email/SMS/WhatsApp use recipient_delivery; social_post uses social_step. Target counts have their own network-target unit.
 */
export interface CampaignChannelProgress {
  channel: CampaignChannelProgressChannel;
  unit: CampaignChannelProgressUnit;
  /** @minimum 0 */
  total: number;
  counts: CampaignProgressCounts;
  /** @minimum 0 */
  plannedStepCount: number;
  milestones: CampaignEvidenceMilestones;
  /** @nullable */
  socialTargets: CampaignChannelProgressSocialTargets;
}

export type CampaignProgressVersion = typeof CampaignProgressVersion[keyof typeof CampaignProgressVersion];


export const CampaignProgressVersion = {
  NUMBER_1: 1,
} as const;

export type CampaignProgressLifecycle = typeof CampaignProgressLifecycle[keyof typeof CampaignProgressLifecycle];


export const CampaignProgressLifecycle = {
  draft: 'draft',
  scheduled: 'scheduled',
  queued: 'queued',
  sending: 'sending',
  paused: 'paused',
  sent: 'sent',
  partial: 'partial',
  failed: 'failed',
  cancelled: 'cancelled',
} as const;

export type CampaignProgressControl = typeof CampaignProgressControl[keyof typeof CampaignProgressControl];


export const CampaignProgressControl = {
  unrestricted: 'unrestricted',
  paused: 'paused',
  cancelled: 'cancelled',
} as const;

export type CampaignProgressState = typeof CampaignProgressState[keyof typeof CampaignProgressState];


export const CampaignProgressState = {
  empty: 'empty',
  pending: 'pending',
  awaiting_approval: 'awaiting_approval',
  queued: 'queued',
  dispatching: 'dispatching',
  unconfirmed: 'unconfirmed',
  settled: 'settled',
} as const;

/**
 * @nullable
 */
export type CampaignProgressOutcome = typeof CampaignProgressOutcome[keyof typeof CampaignProgressOutcome] | null;


export const CampaignProgressOutcome = {
  none: 'none',
  succeeded: 'succeeded',
  partial: 'partial',
  failed: 'failed',
  stopped: 'stopped',
} as const;

export type CampaignProgressEvidence = {
  /** @nullable */
  firstAcceptedAt: string | null;
  /** @nullable */
  latestSentAt: string | null;
  /** @nullable */
  latestDeliveredAt: string | null;
  /** @nullable */
  latestReadAt: string | null;
  /** @nullable */
  latestPublishedAt: string | null;
};

export type CampaignProgressDiscrepanciesItem = typeof CampaignProgressDiscrepanciesItem[keyof typeof CampaignProgressDiscrepanciesItem];


export const CampaignProgressDiscrepanciesItem = {
  legacy_status_differs: 'legacy_status_differs',
  missing_execution: 'missing_execution',
  ambiguous_social_link: 'ambiguous_social_link',
  unsupported_fact: 'unsupported_fact',
} as const;

/**
 * Read-only complete scoped dispatch/publication facts; never readiness, approval or permission to send. No mixed-channel percentage. Missing progress means unavailable, never inferred from lifecycle.
 */
export interface CampaignProgress {
  version: CampaignProgressVersion;
  lifecycle: CampaignProgressLifecycle;
  control: CampaignProgressControl;
  state: CampaignProgressState;
  /** @nullable */
  outcome: CampaignProgressOutcome;
  /** @maxItems 4 */
  channels: CampaignChannelProgress[];
  evidence: CampaignProgressEvidence;
  hasUnconfirmed: boolean;
  hasPendingApproval: boolean;
  /** @maxItems 4 */
  discrepancies: CampaignProgressDiscrepanciesItem[];
  /** @pattern ^[a-f0-9]{64}$ */
  snapshotToken: string;
}

export type EmailCampaignRecipientsItem = {
  email: string;
  contactId?: string;
  accountId?: string;
};

export type EmailCampaignStatus = typeof EmailCampaignStatus[keyof typeof EmailCampaignStatus];


export const EmailCampaignStatus = {
  draft: 'draft',
  scheduled: 'scheduled',
  queued: 'queued',
  sending: 'sending',
  paused: 'paused',
  sent: 'sent',
  partial: 'partial',
  failed: 'failed',
  cancelled: 'cancelled',
} as const;

/**
 * Draft campaigns may have empty subject, body and recipients; delivery readiness is checked separately. Optional progress is read-only; absence means detailed progress unavailable.
 */
export interface EmailCampaign {
  /** @minLength 1 */
  name: string;
  subject: string;
  body: string;
  recipients: EmailCampaignRecipientsItem[];
  /** @nullable */
  scheduledAt?: string | null;
  id: string;
  status: EmailCampaignStatus;
  providerKey: string;
  /** @nullable */
  sentAt?: string | null;
  /** @nullable */
  lastError?: string | null;
  steps: CampaignStep[];
  progress?: CampaignProgress;
  createdAt: string;
  updatedAt: string;
}

export type EmailDeliveryJobKind = typeof EmailDeliveryJobKind[keyof typeof EmailDeliveryJobKind];


export const EmailDeliveryJobKind = {
  campaign: 'campaign',
  sequence: 'sequence',
  review_request: 'review_request',
} as const;

export type EmailDeliveryJobStatus = typeof EmailDeliveryJobStatus[keyof typeof EmailDeliveryJobStatus];


export const EmailDeliveryJobStatus = {
  queued: 'queued',
  dispatching: 'dispatching',
  retry: 'retry',
  sent: 'sent',
  suppressed: 'suppressed',
  failed: 'failed',
  uncertain: 'uncertain',
  cancelled: 'cancelled',
} as const;

export interface EmailDeliveryJob {
  id: string;
  kind: EmailDeliveryJobKind;
  /** @nullable */
  campaignId?: string | null;
  /** @nullable */
  sequenceId?: string | null;
  /** @nullable */
  stepId?: string | null;
  /** @nullable */
  campaignStepId?: string | null;
  /** @nullable */
  enrollmentId?: string | null;
  /** @nullable */
  reviewRequestId?: string | null;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  accountId?: string | null;
  toAddress: string;
  subject?: string;
  status: EmailDeliveryJobStatus;
  /** @nullable */
  reason?: string | null;
  providerKey: string;
  /** @nullable */
  providerMessageId?: string | null;
  /** @minimum 0 */
  attemptCount: number;
  runAt: string;
  /** @nullable */
  nextAttemptAt?: string | null;
  /** @nullable */
  sentAt?: string | null;
  /** @nullable */
  lastErrorCode?: string | null;
  /** @nullable */
  lastError?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmailDeliveryJobList {
  items: EmailDeliveryJob[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type EmailMessageDirection = typeof EmailMessageDirection[keyof typeof EmailMessageDirection];


export const EmailMessageDirection = {
  inbound: 'inbound',
  outbound: 'outbound',
} as const;

export type EmailMessageKind = typeof EmailMessageKind[keyof typeof EmailMessageKind];


export const EmailMessageKind = {
  test: 'test',
  sequence: 'sequence',
  campaign: 'campaign',
  review_request: 'review_request',
  reply: 'reply',
  health_outreach: 'health_outreach',
  mailbox_intake: 'mailbox_intake',
} as const;

export type EmailMessageStatus = typeof EmailMessageStatus[keyof typeof EmailMessageStatus];


export const EmailMessageStatus = {
  queued: 'queued',
  sent: 'sent',
  delivered: 'delivered',
  failed: 'failed',
  replied: 'replied',
  unknown: 'unknown',
} as const;

/**
 * Campaign steps use campaignStepId; sequence steps retain their existing stepId linkage.
 */
export interface EmailMessage {
  id: string;
  providerKey: string;
  direction: EmailMessageDirection;
  kind: EmailMessageKind;
  /** @nullable */
  campaignStepId?: string | null;
  /** @nullable */
  intakeMailboxId?: string | null;
  /** @nullable */
  outreachExecutionId?: string | null;
  /** @nullable */
  nextActionId?: string | null;
  /** @nullable */
  parentMessageId?: string | null;
  /** @nullable */
  interactionId?: string | null;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  enquiryId?: string | null;
  /** @nullable */
  opportunityId?: string | null;
  fromAddress: string;
  toAddress: string;
  subject: string;
  body: string;
  status: EmailMessageStatus;
  trackingToken: string;
  openCount: number;
  clickCount: number;
  /** @nullable */
  sentAt?: string | null;
  /** @nullable */
  receivedAt?: string | null;
  /** @nullable */
  lastError?: string | null;
  createdAt: string;
}

export interface EmailAiDraftRequest {
  /** @minLength 1 */
  objective: string;
  contactId?: string;
  accountId?: string;
  tone?: string;
  audience?: CrmAudience;
  investorUpdate?: boolean;
}

export interface EmailAiDraftResponse {
  subject: string;
  body: string;
  auditId: string;
  /** Strategic briefs the AI was given for this draft */
  strategicBriefs: StrategicBriefSource[];
}

export type CommunicationExtractionType = typeof CommunicationExtractionType[keyof typeof CommunicationExtractionType];


export const CommunicationExtractionType = {
  commitment: 'commitment',
  requirement: 'requirement',
  follow_up: 'follow_up',
} as const;

export type CommunicationExtractionStatus = typeof CommunicationExtractionStatus[keyof typeof CommunicationExtractionStatus];


export const CommunicationExtractionStatus = {
  pending_review: 'pending_review',
  approved: 'approved',
  rejected: 'rejected',
  completed: 'completed',
} as const;

export interface CommunicationExtraction {
  id: string;
  messageId: string;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  accountId?: string | null;
  type: CommunicationExtractionType;
  summary: string;
  /** @nullable */
  dueAt?: string | null;
  status: CommunicationExtractionStatus;
  /** @nullable */
  aiAuditId?: string | null;
  /** @nullable */
  reviewedBy?: string | null;
  createdAt: string;
}

/**
 * Writing-style text and analysis metadata. GET returns the saved state; the analyze operation returns an unsaved preview.
 */
export interface OutlookStyleProfileState {
  /** @nullable */
  styleProfile: string | null;
  emailsAnalyzed: number;
  /** @nullable */
  analyzedAt: string | null;
}

/**
 * Display metadata from the reviewed analysis preview; confers no execution authority. Omit when manually editing an existing saved profile.
 */
export type OutlookStyleProfileUpdateAnalysis = {
  /**
     * @minimum 3
     * @maximum 100
     */
  emailsAnalyzed: number;
  analyzedAt: string;
};

export interface OutlookStyleProfileUpdate {
  /**
     * @minLength 1
     * @maxLength 4000
     */
  styleProfile: string;
  /** Display metadata from the reviewed analysis preview; confers no execution authority. Omit when manually editing an existing saved profile. */
  analysis?: OutlookStyleProfileUpdateAnalysis;
}

/**
 * Plain-text sent-email samples; raw HTML and Graph tokens must never be supplied
 */
export interface OutlookStyleAnalyzeRequest {
  /**
     * @minItems 3
     * @maxItems 100
     * @items.minLength 1
     * @items.maxLength 8000
     */
  samples: string[];
}

/**
 * Plain-text message content for the reply draft; raw HTML and Graph tokens must never be supplied
 */
export interface OutlookSmartReplyRequest {
  /**
     * @minLength 1
     * @maxLength 320
     */
  from: string;
  /** @maxLength 998 */
  subject: string;
  /**
     * @minLength 1
     * @maxLength 8000
     */
  body: string;
  /**
     * Display name of the email sender, used for the first-name sign-off
     * @maxLength 120
     */
  senderName?: string;
}

export interface OutlookSmartReplyResponse {
  draft: string;
  auditId: string;
  /** True when the user's stored writing-style profile was injected into the prompt */
  styleProfileApplied: boolean;
}

export type ManagedDocumentProofingRequestParagraphsItem = {
  /** @pattern ^[A-Za-z0-9_-]{1,64}$ */
  id: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  text: string;
};

/**
 * Plain-text paragraphs from the open document. Text is treated as untrusted data and is never stored.
 */
export interface ManagedDocumentProofingRequest {
  /**
     * @minItems 1
     * @maxItems 8
     */
  paragraphs: ManagedDocumentProofingRequestParagraphsItem[];
}

export type ManagedDocumentProofingIssueKind = typeof ManagedDocumentProofingIssueKind[keyof typeof ManagedDocumentProofingIssueKind];


export const ManagedDocumentProofingIssueKind = {
  spelling: 'spelling',
  grammar: 'grammar',
} as const;

export type ManagedDocumentProofingIssueConfidence = typeof ManagedDocumentProofingIssueConfidence[keyof typeof ManagedDocumentProofingIssueConfidence];


export const ManagedDocumentProofingIssueConfidence = {
  obvious: 'obvious',
  likely: 'likely',
  unsure: 'unsure',
} as const;

export interface ManagedDocumentProofingIssue {
  paragraphId: string;
  /**
     * Offset into the paragraph text
     * @minimum 0
     */
  start: number;
  /**
     * Exclusive end offset into the paragraph text
     * @minimum 1
     */
  end: number;
  /** Exact text at the offsets */
  original: string;
  /** Suggested text; empty means remove the original */
  replacement: string;
  kind: ManagedDocumentProofingIssueKind;
  confidence: ManagedDocumentProofingIssueConfidence;
  reason: string;
}

export interface ManagedDocumentProofing {
  /** @maxItems 60 */
  issues: ManagedDocumentProofingIssue[];
  auditId: string;
}

/**
 * Sender address plus plain-text message content; raw HTML and Graph tokens must never be supplied
 */
export interface OutlookContactSuggestionRequest {
  /** @maxLength 320 */
  email: string;
  /**
     * Display name shown on the email sender
     * @maxLength 200
     */
  name?: string;
  /** @maxLength 998 */
  subject: string;
  /**
     * @minLength 1
     * @maxLength 8000
     */
  body: string;
}

export type OutlookContactSuggestionAccountMatchedBy = typeof OutlookContactSuggestionAccountMatchedBy[keyof typeof OutlookContactSuggestionAccountMatchedBy];


export const OutlookContactSuggestionAccountMatchedBy = {
  email_domain: 'email_domain',
  company_name: 'company_name',
} as const;

/**
 * The single existing account matched by the sender's email domain or the company name; null when none or more than one match
 * @nullable
 */
export type OutlookContactSuggestionAccount = {
  id: string;
  name: string;
  matchedBy: OutlookContactSuggestionAccountMatchedBy;
} | null;

export interface OutlookContactSuggestion {
  /** @nullable */
  firstName: string | null;
  /** @nullable */
  lastName: string | null;
  /** @nullable */
  jobTitle: string | null;
  /** @nullable */
  phone: string | null;
  /** @nullable */
  mobile: string | null;
  /**
     * Organisation named in the email, whether or not it matches an account
     * @nullable
     */
  companyName: string | null;
  /**
     * The single existing account matched by the sender's email domain or the company name; null when none or more than one match
     * @nullable
     */
  account: OutlookContactSuggestionAccount;
  auditId: string;
}

export type OutlookDevOpsLinkOrganization = typeof OutlookDevOpsLinkOrganization[keyof typeof OutlookDevOpsLinkOrganization];


export const OutlookDevOpsLinkOrganization = {
  circlet: 'circlet',
} as const;

/**
 * User-owned Outlook message mapping. Email content, credentials, and access tokens are never stored.
 */
export interface OutlookDevOpsLink {
  id: string;
  /**
     * @minLength 1
     * @maxLength 1024
     */
  stableMessageId: string;
  organization: OutlookDevOpsLinkOrganization;
  /**
     * @minLength 1
     * @maxLength 255
     */
  project: string;
  /** @minimum 1 */
  workItemId: number;
  /** @maxLength 2048 */
  workItemUrl: string;
  createdAt: string;
  updatedAt: string;
}

export interface OutlookDevOpsLinkLookup {
  link: OutlookDevOpsLink | null;
}

export type OutlookDevOpsLinkUpsertOrganization = typeof OutlookDevOpsLinkUpsertOrganization[keyof typeof OutlookDevOpsLinkUpsertOrganization];


export const OutlookDevOpsLinkUpsertOrganization = {
  circlet: 'circlet',
} as const;

export interface OutlookDevOpsLinkUpsert {
  /**
     * @minLength 1
     * @maxLength 1024
     */
  stableMessageId: string;
  organization: OutlookDevOpsLinkUpsertOrganization;
  /**
     * @minLength 1
     * @maxLength 255
     */
  project: string;
  /** @minimum 1 */
  workItemId: number;
  /** @maxLength 2048 */
  workItemUrl: string;
}

export type SocialNetwork = typeof SocialNetwork[keyof typeof SocialNetwork];


export const SocialNetwork = {
  linkedin: 'linkedin',
  facebook: 'facebook',
  instagram: 'instagram',
  x: 'x',
  youtube: 'youtube',
  google_business: 'google_business',
  tiktok: 'tiktok',
} as const;

export type SocialNetworkStatusStatus = typeof SocialNetworkStatusStatus[keyof typeof SocialNetworkStatusStatus];


export const SocialNetworkStatusStatus = {
  disconnected: 'disconnected',
  connected: 'connected',
  error: 'error',
  expired: 'expired',
} as const;

export interface SocialNetworkStatus {
  network: SocialNetwork;
  label: string;
  status: SocialNetworkStatusStatus;
  canPublishText: boolean;
  canReply: boolean;
  oauthConfigured: boolean;
}

export interface SocialNetworkList {
  items: SocialNetworkStatus[];
}

export type SocialProvider = typeof SocialProvider[keyof typeof SocialProvider];


export const SocialProvider = {
  linkedin: 'linkedin',
  facebook: 'facebook',
  instagram: 'instagram',
  x: 'x',
  youtube: 'youtube',
  google_business: 'google_business',
  tiktok: 'tiktok',
} as const;

export type SocialProviderCapabilityKey = typeof SocialProviderCapabilityKey[keyof typeof SocialProviderCapabilityKey];


export const SocialProviderCapabilityKey = {
  publish_text: 'publish_text',
  publish_image: 'publish_image',
  publish_video: 'publish_video',
  publish_document: 'publish_document',
  publish_carousel: 'publish_carousel',
  read_comments: 'read_comments',
  reply_comments: 'reply_comments',
  read_mentions: 'read_mentions',
  read_messages: 'read_messages',
  send_messages: 'send_messages',
  read_reviews: 'read_reviews',
  reply_reviews: 'reply_reviews',
  read_analytics: 'read_analytics',
  manage_advertising: 'manage_advertising',
} as const;

export type SocialProviderCapabilityProviderSupport = typeof SocialProviderCapabilityProviderSupport[keyof typeof SocialProviderCapabilityProviderSupport];


export const SocialProviderCapabilityProviderSupport = {
  supported: 'supported',
  restricted: 'restricted',
  unsupported: 'unsupported',
} as const;

export type SocialProviderCapabilityImplementationStatus = typeof SocialProviderCapabilityImplementationStatus[keyof typeof SocialProviderCapabilityImplementationStatus];


export const SocialProviderCapabilityImplementationStatus = {
  available: 'available',
  partial: 'partial',
  planned: 'planned',
} as const;

export interface SocialProviderCapability {
  key: SocialProviderCapabilityKey;
  providerSupport: SocialProviderCapabilityProviderSupport;
  implementationStatus: SocialProviderCapabilityImplementationStatus;
  requiredPermissions: string[];
  /** @minLength 1 */
  detail: string;
}

export type SocialProviderSetupRequirementKey = typeof SocialProviderSetupRequirementKey[keyof typeof SocialProviderSetupRequirementKey];


export const SocialProviderSetupRequirementKey = {
  app_registration: 'app_registration',
  api_product_approval: 'api_product_approval',
  oauth_consent: 'oauth_consent',
  eligible_target: 'eligible_target',
  verified_media_domain: 'verified_media_domain',
  paid_api_access: 'paid_api_access',
  public_upload_audit: 'public_upload_audit',
} as const;

export interface SocialProviderSetupRequirement {
  key: SocialProviderSetupRequirementKey;
  /** @minLength 1 */
  label: string;
  required: boolean;
}

export type SocialProviderCapabilityStatusTargetKindsItem = typeof SocialProviderCapabilityStatusTargetKindsItem[keyof typeof SocialProviderCapabilityStatusTargetKindsItem];


export const SocialProviderCapabilityStatusTargetKindsItem = {
  member: 'member',
  organization_page: 'organization_page',
  page: 'page',
  professional_account: 'professional_account',
  account: 'account',
  channel: 'channel',
  location: 'location',
  creator: 'creator',
} as const;

export type SocialProviderCapabilityStatusConnectionStatus = typeof SocialProviderCapabilityStatusConnectionStatus[keyof typeof SocialProviderCapabilityStatusConnectionStatus];


export const SocialProviderCapabilityStatusConnectionStatus = {
  disconnected: 'disconnected',
  connected: 'connected',
  error: 'error',
  expired: 'expired',
} as const;

export type SocialProviderCapabilityStatusSetupState = typeof SocialProviderCapabilityStatusSetupState[keyof typeof SocialProviderCapabilityStatusSetupState];


export const SocialProviderCapabilityStatusSetupState = {
  ready: 'ready',
  connection_required: 'connection_required',
  configuration_required: 'configuration_required',
  provider_approval_required: 'provider_approval_required',
  reauthorization_required: 'reauthorization_required',
  not_implemented: 'not_implemented',
} as const;

/**
 * @nullable
 */
export type SocialProviderCapabilityStatusAccountType = typeof SocialProviderCapabilityStatusAccountType[keyof typeof SocialProviderCapabilityStatusAccountType] | null;


export const SocialProviderCapabilityStatusAccountType = {
  organization_page: 'organization_page',
  page: 'page',
  professional_account: 'professional_account',
  account: 'account',
  channel: 'channel',
  location: 'location',
  creator: 'creator',
} as const;

export interface SocialProviderCapabilityStatus {
  provider: SocialProvider;
  /** @minLength 1 */
  label: string;
  /** @minItems 1 */
  targetKinds: SocialProviderCapabilityStatusTargetKindsItem[];
  connectionStatus: SocialProviderCapabilityStatusConnectionStatus;
  setupState: SocialProviderCapabilityStatusSetupState;
  /** @nullable */
  displayName: string | null;
  /** @nullable */
  externalAccountId: string | null;
  /** @nullable */
  accountType: SocialProviderCapabilityStatusAccountType;
  grantedCapabilities: string[];
  /** @nullable */
  connectedAt: string | null;
  /** @nullable */
  expiresAt: string | null;
  /** @nullable */
  lastVerifiedAt: string | null;
  /** @nullable */
  lastSynchronizedAt: string | null;
  /** @nullable */
  safeError: string | null;
  documentationUrl: string;
  setupRequirements: SocialProviderSetupRequirement[];
  capabilities: SocialProviderCapability[];
}

export interface SocialProviderCapabilityMatrix {
  verifiedAt: string;
  generatedAt: string;
  /**
     * @minItems 7
     * @maxItems 7
     */
  items: SocialProviderCapabilityStatus[];
}

export interface SocialOAuthStart {
  network: SocialNetwork;
  available: boolean;
  /** @nullable */
  authorizationUrl?: string | null;
  /** @nullable */
  reason?: string | null;
}

export type SocialEligibleAccountAccountType = typeof SocialEligibleAccountAccountType[keyof typeof SocialEligibleAccountAccountType];


export const SocialEligibleAccountAccountType = {
  organization_page: 'organization_page',
  page: 'page',
  professional_account: 'professional_account',
  account: 'account',
  channel: 'channel',
  location: 'location',
  creator: 'creator',
} as const;

export interface SocialEligibleAccount {
  /** @minLength 1 */
  externalAccountId: string;
  /** @minLength 1 */
  displayName: string;
  accountType: SocialEligibleAccountAccountType;
  capabilities: string[];
}

export interface SocialOAuthAccountSelection {
  selectionId: string;
  provider: SocialNetwork;
  expiresAt: string;
  eligibleAccounts: SocialEligibleAccount[];
  /** @minimum 1 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface SocialOAuthAccountSelectRequest {
  /**
     * @minLength 1
     * @maxLength 500
     */
  externalAccountId: string;
}

export type SocialConnectedAccountStatus = typeof SocialConnectedAccountStatus[keyof typeof SocialConnectedAccountStatus];


export const SocialConnectedAccountStatus = {
  connected: 'connected',
} as const;

export type SocialConnectedAccountAccountType = typeof SocialConnectedAccountAccountType[keyof typeof SocialConnectedAccountAccountType];


export const SocialConnectedAccountAccountType = {
  organization_page: 'organization_page',
  page: 'page',
  professional_account: 'professional_account',
  account: 'account',
  channel: 'channel',
  location: 'location',
  creator: 'creator',
} as const;

export interface SocialConnectedAccount {
  network: SocialNetwork;
  status: SocialConnectedAccountStatus;
  displayName: string;
  externalAccountId: string;
  accountType: SocialConnectedAccountAccountType;
  grantedCapabilities: string[];
  /** @nullable */
  expiresAt: string | null;
}

export type SocialConnectionRefreshResultStatus = typeof SocialConnectionRefreshResultStatus[keyof typeof SocialConnectionRefreshResultStatus];


export const SocialConnectionRefreshResultStatus = {
  refreshed: 'refreshed',
  already_running: 'already_running',
  superseded: 'superseded',
} as const;

export interface SocialConnectionRefreshResult {
  network: SocialNetwork;
  status: SocialConnectionRefreshResultStatus;
  /** @nullable */
  expiresAt: string | null;
  /** @nullable */
  refreshedAt: string | null;
}

export type SocialDisconnectResultStatus = typeof SocialDisconnectResultStatus[keyof typeof SocialDisconnectResultStatus];


export const SocialDisconnectResultStatus = {
  disconnected: 'disconnected',
} as const;

export interface SocialDisconnectResult {
  network: SocialNetwork;
  status: SocialDisconnectResultStatus;
  /** @minimum 0 */
  blockedQueuedActions: number;
}

export interface SocialDisconnectRequest {
  /**
     * @minLength 10
     * @maxLength 500
     */
  reason: string;
}

export type SocialConnectionAuditAction = typeof SocialConnectionAuditAction[keyof typeof SocialConnectionAuditAction];


export const SocialConnectionAuditAction = {
  channel_connected: 'channel_connected',
  channel_disconnected: 'channel_disconnected',
  channel_refresh_succeeded: 'channel_refresh_succeeded',
  channel_refresh_failed: 'channel_refresh_failed',
  channel_test_succeeded: 'channel_test_succeeded',
  channel_test_failed: 'channel_test_failed',
} as const;

export type SocialConnectionAuditItemOutcome = typeof SocialConnectionAuditItemOutcome[keyof typeof SocialConnectionAuditItemOutcome];


export const SocialConnectionAuditItemOutcome = {
  succeeded: 'succeeded',
  failed: 'failed',
} as const;

export interface SocialConnectionAuditItem {
  id: string;
  provider: SocialProvider;
  action: SocialConnectionAuditAction;
  outcome: SocialConnectionAuditItemOutcome;
  /** @nullable */
  actorEmail: string | null;
  /** @nullable */
  reason: string | null;
  /** @nullable */
  externalAccountId: string | null;
  /**
     * @minimum 0
     * @nullable
     */
  blockedQueuedActions: number | null;
  createdAt: string;
}

export interface SocialConnectionAuditPage {
  items: SocialConnectionAuditItem[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface SocialVariant {
  network: SocialNetwork;
  content: string;
}

export type TikTokPrivacyLevel = typeof TikTokPrivacyLevel[keyof typeof TikTokPrivacyLevel];


export const TikTokPrivacyLevel = {
  PUBLIC_TO_EVERYONE: 'PUBLIC_TO_EVERYONE',
  MUTUAL_FOLLOW_FRIENDS: 'MUTUAL_FOLLOW_FRIENDS',
  FOLLOWER_OF_CREATOR: 'FOLLOWER_OF_CREATOR',
  SELF_ONLY: 'SELF_ONLY',
} as const;

export type TikTokMediaType = typeof TikTokMediaType[keyof typeof TikTokMediaType];


export const TikTokMediaType = {
  photo: 'photo',
  video: 'video',
} as const;

export interface TikTokCreatorInfo {
  /** @minLength 1 */
  creatorUsername: string;
  /** @minLength 1 */
  creatorNickname: string;
  /** @minItems 1 */
  privacyLevelOptions: TikTokPrivacyLevel[];
  commentDisabled: boolean;
  duetDisabled: boolean;
  stitchDisabled: boolean;
  /**
     * @minimum 1
     * @maximum 600
     */
  maxVideoPostDurationSec: number;
  /** Whether visibility beyond Only me is enabled for this application */
  directPostAudited: boolean;
  /** Whether server-side verified media URL prefixes are configured */
  verifiedMediaPrefixesConfigured: boolean;
  checkedAt: string;
}

export interface TikTokPostConfigurationInput {
  mediaType: TikTokMediaType;
  /**
     * @minItems 1
     * @maxItems 35
     * @items.maxLength 2048
     */
  mediaUrls: string[];
  /**
     * @maximum 600
     * @exclusiveMinimum 0
     * @nullable
     */
  videoDurationSeconds?: number | null;
  /** @maxLength 2200 */
  title: string;
  /** @maxLength 4000 */
  description: string;
  privacyLevel: TikTokPrivacyLevel;
  allowComment: boolean;
  allowDuet: boolean;
  allowStitch: boolean;
  commercialContent: boolean;
  brandOrganic: boolean;
  brandContent: boolean;
  isAigc: boolean;
  /** Explicit creator consent to transfer this media and publish it to TikTok */
  consentConfirmed: true;
}

export type TikTokPostConfiguration = TikTokPostConfigurationInput & {
  /** @minLength 1 */
  creatorUsername: string;
  /** @minLength 1 */
  creatorNickname: string;
  creatorInfoCheckedAt: string;
};

export interface SocialProviderOptions {
  tiktok?: TikTokPostConfiguration | null;
}

export type SocialPublishResultStatus = typeof SocialPublishResultStatus[keyof typeof SocialPublishResultStatus];


export const SocialPublishResultStatus = {
  processing: 'processing',
  published: 'published',
  failed: 'failed',
  uncertain: 'uncertain',
  unsupported: 'unsupported',
} as const;

export interface SocialPublishResult {
  network: SocialNetwork;
  status: SocialPublishResultStatus;
  /** @nullable */
  externalId?: string | null;
  /** @nullable */
  providerStatus?: string | null;
  /** @nullable */
  providerPostIds?: string[] | null;
  /** @nullable */
  url?: string | null;
  /** @nullable */
  error?: string | null;
  /** @nullable */
  errorCode?: string | null;
  /** @nullable */
  retryable?: boolean | null;
  /**
     * @minimum 0
     * @nullable
     */
  attemptCount?: number | null;
  /** @nullable */
  nextRetryAt?: string | null;
  /** @nullable */
  updatedAt?: string | null;
}

export type ManualAdPackFormat = typeof ManualAdPackFormat[keyof typeof ManualAdPackFormat];


export const ManualAdPackFormat = {
  meta: 'meta',
  linkedin: 'linkedin',
  google_search: 'google_search',
  google_display: 'google_display',
  youtube: 'youtube',
  tiktok: 'tiktok',
  x: 'x',
} as const;

export type ManualAdPackVariantsItemFields = {[key: string]: string[]};

export type ManualAdPackVariantsItem = {
  /**
     * @minLength 1
     * @maxLength 100
     */
  name: string;
  fields: ManualAdPackVariantsItemFields;
};

export interface ManualAdPack {
  /**
     * @minLength 1
     * @maxLength 300
     */
  title: string;
  format: ManualAdPackFormat;
  /** @maxLength 300 */
  placement: string;
  /** @maxLength 2000 */
  objective: string;
  /** @maxLength 2000 */
  destinationUrl: string;
  /** @maxLength 5000 */
  audience: string;
  /** @nullable */
  startsAt: string | null;
  /** @nullable */
  endsAt: string | null;
  /**
     * @nullable
     * @pattern ^[0-9]{1,12}(\.[0-9]{1,2})?$
     */
  budget: string | null;
  /**
     * @nullable
     * @pattern ^[A-Z]{3}$
     */
  currency: string | null;
  /**
     * @minItems 1
     * @maxItems 20
     */
  variants: ManualAdPackVariantsItem[];
  /** @maxItems 20 */
  assets: CampaignPlanningLink[];
  /** @maxItems 50 */
  sources: CampaignPlanningLink[];
  /**
     * @maxItems 50
     * @items.maxLength 2000
     */
  unknowns: string[];
}

export type CampaignOutputChangeOperation = typeof CampaignOutputChangeOperation[keyof typeof CampaignOutputChangeOperation];


export const CampaignOutputChangeOperation = {
  create_document: 'create_document',
  save_document: 'save_document',
  save_page: 'save_page',
  create_ad_pack: 'create_ad_pack',
} as const;

export type CampaignOutputChangeFields = ManualAdPack | { [key: string]: unknown };

export interface CampaignOutputChange {
  campaignId: string;
  /** @minimum 0 */
  expectedBriefVersion: number;
  expectedCampaignUpdatedAt: string;
  operation: CampaignOutputChangeOperation;
  recordId?: string;
  /** @minimum 0 */
  expectedRevision?: number;
  fields: CampaignOutputChangeFields;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  reason: string;
}

export type CampaignOutputStateKind = typeof CampaignOutputStateKind[keyof typeof CampaignOutputStateKind];


export const CampaignOutputStateKind = {
  content_asset: 'content_asset',
  managed_document: 'managed_document',
  microsite: 'microsite',
} as const;

export type CampaignOutputStateState = typeof CampaignOutputStateState[keyof typeof CampaignOutputStateState];


export const CampaignOutputStateState = {
  draft: 'draft',
  in_review: 'in_review',
  approved: 'approved',
  published: 'published',
  stale: 'stale',
  unavailable: 'unavailable',
  archived: 'archived',
} as const;

export interface CampaignOutputState {
  /** Manual ad preparation document; native approval is never provider approval. */
  manualHandoff?: boolean;
  kind: CampaignOutputStateKind;
  recordId: string;
  revision: string;
  /** @nullable */
  currentRevision: string | null;
  /** @nullable */
  title: string | null;
  state: CampaignOutputStateState;
  /** @nullable */
  editorUrl: string | null;
  /** @nullable */
  publicUrl: string | null;
  limitations: string[];
}

export type SocialProductionChangeOperation = typeof SocialProductionChangeOperation[keyof typeof SocialProductionChangeOperation];


export const SocialProductionChangeOperation = {
  create: 'create',
  update: 'update',
  submit: 'submit',
  approve: 'approve',
  reject: 'reject',
  schedule: 'schedule',
  archive: 'archive',
  duplicate: 'duplicate',
  retry: 'retry',
  reconcile: 'reconcile',
} as const;

export type SocialProductionChangeFields = { [key: string]: unknown };

export interface SocialProductionChange {
  operation: SocialProductionChangeOperation;
  campaignId?: string;
  postId?: string;
  /** @minimum 1 */
  expectedVersion?: number;
  fields: SocialProductionChangeFields;
  /**
     * @minLength 3
     * @maxLength 1000
     */
  reason: string;
}

export interface SocialPostCreate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 10000
     */
  content: string;
  /** @minItems 1 */
  networks: SocialNetwork[];
  variants?: SocialVariant[];
  /** @maxItems 10 */
  mediaAssetIds?: string[];
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  campaignStepId?: string | null;
  /** Create an inert social step in this draft campaign when no existing step is selected. */
  campaignId?: string;
}

export interface SocialPostUpdate {
  /** @minimum 1 */
  expectedVersion: number;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
  /**
     * @minLength 1
     * @maxLength 10000
     */
  content?: string;
  /** @minItems 1 */
  networks?: SocialNetwork[];
  variants?: SocialVariant[];
  /** @maxItems 10 */
  mediaAssetIds?: string[];
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  campaignStepId?: string | null;
}

export type SocialPostStatus = typeof SocialPostStatus[keyof typeof SocialPostStatus];


export const SocialPostStatus = {
  draft: 'draft',
  pending_approval: 'pending_approval',
  approved: 'approved',
  rejected: 'rejected',
  scheduled: 'scheduled',
  publishing: 'publishing',
  published: 'published',
  partial_failed: 'partial_failed',
  failed: 'failed',
  archived: 'archived',
} as const;

export interface SocialPostVersionRequest {
  /** @minimum 1 */
  expectedVersion: number;
}

export interface SocialPostRejectRequest {
  /** @minimum 1 */
  expectedVersion: number;
  /**
     * @minLength 3
     * @maxLength 1000
     */
  reason: string;
}

export interface SocialPostArchiveRequest {
  /** @minimum 1 */
  expectedVersion: number;
  /**
     * @minLength 3
     * @maxLength 1000
     */
  reason: string;
}

export interface SocialPostRetryRequest {
  /** @minimum 1 */
  expectedVersion: number;
  /** @minItems 1 */
  networks: SocialNetwork[];
}

/**
 * Required only when the local time occurs twice during a daylight-saving transition
 * @nullable
 */
export type SocialPostScheduleRequestDisambiguation = typeof SocialPostScheduleRequestDisambiguation[keyof typeof SocialPostScheduleRequestDisambiguation] | null;


export const SocialPostScheduleRequestDisambiguation = {
  earlier: 'earlier',
  later: 'later',
} as const;

export interface SocialPostScheduleRequest {
  /** @minimum 1 */
  expectedVersion: number;
  /**
     * Wall-clock time in the supplied IANA timezone
     * @pattern ^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$
     */
  localDateTime: string;
  /**
     * IANA timezone identifier
     * @minLength 1
     * @maxLength 100
     */
  timezone: string;
  /**
     * Required only when the local time occurs twice during a daylight-saving transition
     * @nullable
     */
  disambiguation?: SocialPostScheduleRequestDisambiguation;
}

export type ContentHubMediaKind = typeof ContentHubMediaKind[keyof typeof ContentHubMediaKind];


export const ContentHubMediaKind = {
  image: 'image',
  video: 'video',
} as const;

export interface SocialPostMedia {
  id: string;
  filename: string;
  kind: ContentHubMediaKind;
  contentType: string;
  selectable: boolean;
}

/**
 * @nullable
 */
export type SocialPostScheduleDisambiguation = typeof SocialPostScheduleDisambiguation[keyof typeof SocialPostScheduleDisambiguation] | null;


export const SocialPostScheduleDisambiguation = {
  earlier: 'earlier',
  later: 'later',
} as const;

export type SocialPost = SocialPostCreate & ({
  id: string;
  status: SocialPostStatus;
  /** @minimum 1 */
  version: number;
  media: SocialPostMedia[];
  /** @minimum 0 */
  revisionCount: number;
  /** @nullable */
  scheduledAt?: string | null;
  /** @nullable */
  scheduleLocalDateTime?: string | null;
  /** @nullable */
  scheduleTimezone?: string | null;
  /** @nullable */
  scheduleDisambiguation?: SocialPostScheduleDisambiguation;
  /** @nullable */
  approvedBy?: string | null;
  /** @nullable */
  approvedAt?: string | null;
  /** @nullable */
  submittedBy?: string | null;
  /** @nullable */
  submittedAt?: string | null;
  /** @nullable */
  rejectedBy?: string | null;
  /** @nullable */
  rejectedAt?: string | null;
  /** @nullable */
  rejectionReason?: string | null;
  /** @nullable */
  archivedBy?: string | null;
  /** @nullable */
  archivedAt?: string | null;
  attemptCount: number;
  /** @nullable */
  nextRetryAt?: string | null;
  /** @nullable */
  lastError?: string | null;
  providerOptions: SocialProviderOptions;
  publishResults: SocialPublishResult[];
  createdAt: string;
  updatedAt: string;
});

export interface KnowledgeGroundingSource {
  knowledgeFileId: string;
  title: string;
  location: string;
  score: number;
}

export type KnowledgeGroundingState = typeof KnowledgeGroundingState[keyof typeof KnowledgeGroundingState];


export const KnowledgeGroundingState = {
  ready: 'ready',
  empty_index: 'empty_index',
  no_sources: 'no_sources',
  stale: 'stale',
  conflicting: 'conflicting',
  denied: 'denied',
  failed_ingestion: 'failed_ingestion',
  low_confidence: 'low_confidence',
} as const;

export interface KnowledgeGroundingResult {
  sources: KnowledgeGroundingSource[];
  knowledgeState: KnowledgeGroundingState;
}

export type SocialDraftGenerationResult = SocialPost & KnowledgeGroundingResult;

export interface SocialPostList {
  items: SocialPost[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
  displayTimezone: string;
}

export type SocialPostRevisionAction = typeof SocialPostRevisionAction[keyof typeof SocialPostRevisionAction];


export const SocialPostRevisionAction = {
  created: 'created',
  edited: 'edited',
  submitted: 'submitted',
  approved: 'approved',
  rejected: 'rejected',
  scheduled: 'scheduled',
  archived: 'archived',
  duplicated: 'duplicated',
} as const;

export interface SocialPostRevision {
  id: string;
  postId: string;
  /** @minimum 1 */
  version: number;
  action: SocialPostRevisionAction;
  title: string;
  content: string;
  networks: SocialNetwork[];
  variants: SocialVariant[];
  mediaAssetIds: string[];
  actor: string;
  /** @nullable */
  note?: string | null;
  createdAt: string;
}

export interface SocialPostRevisionList {
  items: SocialPostRevision[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface SocialStrategyRequest {
  /** @maxLength 1000 */
  objective?: string;
  networks?: SocialNetwork[];
}

export interface SocialStrategyResponse {
  summary: string;
  topics: string[];
  auditId: string;
}

export interface SocialConnectionTestResult {
  network: SocialNetwork;
  connected: boolean;
  ok: boolean;
  /** @nullable */
  reason?: string | null;
  /** @nullable */
  detail?: string | null;
  testedAt: string;
}

/**
 * @nullable
 */
export type SocialAutoGenerateResultReason = typeof SocialAutoGenerateResultReason[keyof typeof SocialAutoGenerateResultReason] | null;


export const SocialAutoGenerateResultReason = {
  open_draft_cap_reached: 'open_draft_cap_reached',
  no_topic: 'no_topic',
  already_running: 'already_running',
} as const;

export interface SocialAutoGenerateResult {
  created: number;
  /** @nullable */
  reason?: SocialAutoGenerateResultReason;
  /** @nullable */
  topic?: string | null;
  /** @nullable */
  postId?: string | null;
  networks?: SocialNetwork[];
}

export interface SocialDraftRequest {
  /**
     * @minLength 1
     * @maxLength 1000
     */
  topic: string;
  /** @maxLength 100 */
  tone?: string;
  /** @minItems 1 */
  networks: SocialNetwork[];
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  contactId?: string | null;
}

export type SocialInboxItemType = typeof SocialInboxItemType[keyof typeof SocialInboxItemType];


export const SocialInboxItemType = {
  comment: 'comment',
  mention: 'mention',
  dm: 'dm',
} as const;

export type SocialInboxItemStatus = typeof SocialInboxItemStatus[keyof typeof SocialInboxItemStatus];


export const SocialInboxItemStatus = {
  unread: 'unread',
  open: 'open',
  replied: 'replied',
  archived: 'archived',
} as const;

export interface SocialInboxItem {
  id: string;
  network: SocialNetwork;
  externalId: string;
  /** @nullable */
  externalPostId?: string | null;
  type: SocialInboxItemType;
  authorExternalId: string;
  authorName: string;
  /** @nullable */
  authorEmail?: string | null;
  body: string;
  receivedAt: string;
  status: SocialInboxItemStatus;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  enquiryId: string | null;
  /** @nullable */
  suggestedReply?: string | null;
  /** @nullable */
  replyText?: string | null;
  /** @nullable */
  repliedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SocialInboxList {
  items: SocialInboxItem[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type SocialInboxSyncNetworkResultStatus = typeof SocialInboxSyncNetworkResultStatus[keyof typeof SocialInboxSyncNetworkResultStatus];


export const SocialInboxSyncNetworkResultStatus = {
  completed: 'completed',
  skipped: 'skipped',
  failed: 'failed',
} as const;

export interface SocialInboxSyncNetworkResult {
  network: SocialNetwork;
  status: SocialInboxSyncNetworkResultStatus;
  /** @minimum 0 */
  imported: number;
  /** @minimum 0 */
  examined: number;
  /** @nullable */
  reason?: string | null;
}

export interface SocialInboxSyncResult {
  /** @minimum 0 */
  synced: number;
  networks: SocialInboxSyncNetworkResult[];
}

export interface SocialLeadConvertRequest {
  requestId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  email: string;
  /**
     * @minLength 1
     * @maxLength 300
     */
  subject?: string;
  /**
     * @minLength 1
     * @maxLength 5000
     */
  message?: string;
}

export interface SocialLeadConvertResponse {
  inboxItem: SocialInboxItem;
  enquiry: Enquiry;
}

export type SocialMessageRuleStatus = typeof SocialMessageRuleStatus[keyof typeof SocialMessageRuleStatus];


export const SocialMessageRuleStatus = {
  draft: 'draft',
  pending_approval: 'pending_approval',
  approved: 'approved',
  active: 'active',
  paused: 'paused',
} as const;

export type SocialMessageRuleVersionMatchMode = typeof SocialMessageRuleVersionMatchMode[keyof typeof SocialMessageRuleVersionMatchMode];


export const SocialMessageRuleVersionMatchMode = {
  any: 'any',
  all: 'all',
} as const;

export type SocialMessageRuleVersionAllowedItemTypesItem = typeof SocialMessageRuleVersionAllowedItemTypesItem[keyof typeof SocialMessageRuleVersionAllowedItemTypesItem];


export const SocialMessageRuleVersionAllowedItemTypesItem = {
  comment: 'comment',
  mention: 'mention',
  dm: 'dm',
} as const;

export interface SocialMessageRuleVersion {
  id: string;
  /** @minimum 1 */
  version: number;
  /**
     * @minItems 1
     * @maxItems 50
     * @items.minLength 1
     * @items.maxLength 100
     */
  keywords: string[];
  matchMode: SocialMessageRuleVersionMatchMode;
  caseSensitive: boolean;
  /**
     * @minLength 1
     * @maxLength 5000
     */
  replyTemplate: string;
  /** @minItems 1 */
  allowedItemTypes: SocialMessageRuleVersionAllowedItemTypesItem[];
  /**
     * @maxItems 100
     * @items.minLength 1
     * @items.maxLength 100
     */
  blockedTerms: string[];
  /**
     * @maxItems 100
     * @items.minLength 1
     * @items.maxLength 255
     */
  blockedAuthors: string[];
  /**
     * @minimum 0
     * @maximum 43200
     */
  cooldownMinutes: number;
  /**
     * @minimum 1
     * @maximum 10000
     */
  dailyCap: number;
  /** @minLength 1 */
  createdBy: string;
  createdAt: string;
}

export interface SocialMessageRule {
  id: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  network: SocialNetwork;
  status: SocialMessageRuleStatus;
  /** @minimum 1 */
  currentVersion: number;
  /**
     * @minimum 1
     * @nullable
     */
  approvedVersion: number | null;
  /**
     * @minimum 1
     * @nullable
     */
  activeVersion: number | null;
  /** @nullable */
  approvedBy: string | null;
  /** @nullable */
  approvedAt: string | null;
  current: SocialMessageRuleVersion;
  createdAt: string;
  updatedAt: string;
}

export type SocialMessageRuleInputMatchMode = typeof SocialMessageRuleInputMatchMode[keyof typeof SocialMessageRuleInputMatchMode];


export const SocialMessageRuleInputMatchMode = {
  any: 'any',
  all: 'all',
} as const;

export type SocialMessageRuleInputAllowedItemTypesItem = typeof SocialMessageRuleInputAllowedItemTypesItem[keyof typeof SocialMessageRuleInputAllowedItemTypesItem];


export const SocialMessageRuleInputAllowedItemTypesItem = {
  comment: 'comment',
  mention: 'mention',
  dm: 'dm',
} as const;

export interface SocialMessageRuleInput {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  network: SocialNetwork;
  /**
     * @minItems 1
     * @maxItems 50
     * @items.minLength 1
     * @items.maxLength 100
     */
  keywords: string[];
  matchMode: SocialMessageRuleInputMatchMode;
  caseSensitive: boolean;
  /**
     * @minLength 1
     * @maxLength 5000
     */
  replyTemplate: string;
  /** @minItems 1 */
  allowedItemTypes: SocialMessageRuleInputAllowedItemTypesItem[];
  /**
     * @maxItems 100
     * @items.minLength 1
     * @items.maxLength 100
     */
  blockedTerms: string[];
  /**
     * @maxItems 100
     * @items.minLength 1
     * @items.maxLength 255
     */
  blockedAuthors: string[];
  /**
     * @minimum 0
     * @maximum 43200
     */
  cooldownMinutes: number;
  /**
     * @minimum 1
     * @maximum 10000
     */
  dailyCap: number;
}

export type SocialMessageRuleUpdate = SocialMessageRuleInput & {
  /** @minimum 1 */
  expectedVersion: number;
};

export interface SocialMessageRuleTransition {
  /** @minimum 1 */
  expectedVersion: number;
  /** @maxLength 1000 */
  reason?: string;
}

export type SocialMessageRulePreviewRequestItemType = typeof SocialMessageRulePreviewRequestItemType[keyof typeof SocialMessageRulePreviewRequestItemType];


export const SocialMessageRulePreviewRequestItemType = {
  comment: 'comment',
  mention: 'mention',
  dm: 'dm',
} as const;

export interface SocialMessageRulePreviewRequest {
  /**
     * @minLength 1
     * @maxLength 10000
     */
  text: string;
  /**
     * @minLength 1
     * @maxLength 255
     */
  authorExternalId: string;
  itemType: SocialMessageRulePreviewRequestItemType;
}

export type SocialMessageRulePreviewOutcome = typeof SocialMessageRulePreviewOutcome[keyof typeof SocialMessageRulePreviewOutcome];


export const SocialMessageRulePreviewOutcome = {
  matched: 'matched',
  skipped: 'skipped',
  blocked: 'blocked',
} as const;

export interface SocialMessageRulePreview {
  matched: boolean;
  outcome: SocialMessageRulePreviewOutcome;
  reason: string;
  /** @nullable */
  renderedReply: string | null;
}

export interface SocialMessageRuleList {
  items: SocialMessageRule[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface SocialMessageRuleHistory {
  items: SocialMessageRuleVersion[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type SocialMessageRuleExecutionOutcome = typeof SocialMessageRuleExecutionOutcome[keyof typeof SocialMessageRuleExecutionOutcome];


export const SocialMessageRuleExecutionOutcome = {
  matched: 'matched',
  skipped: 'skipped',
  blocked: 'blocked',
  dispatching: 'dispatching',
  failed: 'failed',
  completed: 'completed',
  uncertain: 'uncertain',
} as const;

export interface SocialMessageRuleExecution {
  id: string;
  /** @nullable */
  ruleId: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  ruleVersion: number | null;
  inboxItemId: string;
  network: SocialNetwork;
  outcome: SocialMessageRuleExecutionOutcome;
  reasonCode: string;
  reason: string;
  /** @nullable */
  externalReplyId: string | null;
  createdAt: string;
  /** @nullable */
  completedAt: string | null;
}

export interface SocialMessageRuleExecutionList {
  items: SocialMessageRuleExecution[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface SocialMessageAutomationRunResult {
  /** @minimum 0 */
  processed: number;
  /** @minimum 0 */
  completed: number;
  /** @minimum 0 */
  blocked: number;
  /** @minimum 0 */
  skipped: number;
  /** @minimum 0 */
  failed: number;
}

export type SocialMetricMetricAvailability = {[key: string]: 'measured' | 'unmeasured' | 'unavailable' | 'unsupported'};

export type SocialMetricSourceStatus = typeof SocialMetricSourceStatus[keyof typeof SocialMetricSourceStatus];


export const SocialMetricSourceStatus = {
  measured: 'measured',
  unmeasured: 'unmeasured',
  unavailable: 'unavailable',
  unsupported: 'unsupported',
} as const;

export interface SocialMetric {
  socialPostId: string;
  network: SocialNetwork;
  impressions: number;
  reach: number;
  reactions: number;
  comments: number;
  shares: number;
  clicks: number;
  views: number;
  metricAvailability: SocialMetricMetricAvailability;
  sourceStatus: SocialMetricSourceStatus;
  /** @nullable */
  sourceTimestamp: string | null;
  syncedAt: string;
}

export interface SocialNetworkMetric {
  network: SocialNetwork;
  posts: number;
  impressions: number;
  engagement: number;
}

export interface SocialAnalytics {
  networks: SocialNetworkMetric[];
  posts: SocialMetric[];
}

export interface PlatformConfigEntry {
  namespace: string;
  key: string;
  value: unknown;
  version: number;
  updatedAt: string;
}

export interface PlatformConfigUpdate {
  value: unknown;
  updatedBy?: string;
}

export interface PlatformConfigList {
  items: PlatformConfigEntry[];
}

export type ChannelProvider = typeof ChannelProvider[keyof typeof ChannelProvider];


export const ChannelProvider = {
  linkedin: 'linkedin',
  facebook: 'facebook',
  instagram: 'instagram',
  x: 'x',
  youtube: 'youtube',
  google_business: 'google_business',
  tiktok: 'tiktok',
  microsoft365: 'microsoft365',
  telephony: 'telephony',
} as const;

export type ChannelConnectionStatus = typeof ChannelConnectionStatus[keyof typeof ChannelConnectionStatus];


export const ChannelConnectionStatus = {
  disconnected: 'disconnected',
  connected: 'connected',
  error: 'error',
  expired: 'expired',
} as const;

export interface ChannelConnection {
  provider: ChannelProvider;
  label: string;
  status: ChannelConnectionStatus;
  /** @nullable */
  displayName?: string | null;
  /** @nullable */
  externalAccountId?: string | null;
  supportsLiveOAuth: boolean;
  /** @nullable */
  connectedAt?: string | null;
  /** @nullable */
  expiresAt?: string | null;
  /** @nullable */
  errorMessage?: string | null;
}

export interface ChannelConnectionList {
  items: ChannelConnection[];
}

export type ChannelConnectRequestCredentials = { [key: string]: unknown };

export interface ChannelConnectRequest {
  displayName?: string;
  externalAccountId?: string;
  credentials?: ChannelConnectRequestCredentials;
  expiresAt?: string;
}

export type InteractionCreateDirection = typeof InteractionCreateDirection[keyof typeof InteractionCreateDirection];


export const InteractionCreateDirection = {
  inbound: 'inbound',
  outbound: 'outbound',
  internal: 'internal',
} as const;

export type InteractionCreateMetadata = { [key: string]: unknown };

export interface InteractionCreate {
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  contactId?: string | null;
  channel: string;
  type: string;
  direction?: InteractionCreateDirection;
  /** @nullable */
  subject?: string | null;
  /** @minLength 1 */
  body: string;
  /** @nullable */
  sourceId?: string | null;
  metadata?: InteractionCreateMetadata;
  occurredAt?: string;
}

export type InteractionDirection = typeof InteractionDirection[keyof typeof InteractionDirection];


export const InteractionDirection = {
  inbound: 'inbound',
  outbound: 'outbound',
  internal: 'internal',
} as const;

export type Interaction = InteractionCreate & {
  id: string;
  direction: InteractionDirection;
  occurredAt: string;
  createdAt: string;
};

export type TimelineItemDirection = typeof TimelineItemDirection[keyof typeof TimelineItemDirection];


export const TimelineItemDirection = {
  inbound: 'inbound',
  outbound: 'outbound',
  internal: 'internal',
} as const;

export type TimelineItemSource = typeof TimelineItemSource[keyof typeof TimelineItemSource];


export const TimelineItemSource = {
  interaction: 'interaction',
  activity: 'activity',
  enquiry: 'enquiry',
} as const;

export interface TimelineItem {
  id: string;
  channel: string;
  type: string;
  direction: TimelineItemDirection;
  /** @nullable */
  subject?: string | null;
  body: string;
  occurredAt: string;
  source: TimelineItemSource;
}

export interface TimelinePage {
  items: TimelineItem[];
  total: number;
  limit: number;
  offset: number;
}

export type SignalCreatePayload = { [key: string]: unknown };

export interface SignalCreate {
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  contactId?: string | null;
  source: string;
  type: string;
  payload?: SignalCreatePayload;
  /**
     * @minimum 0
     * @maximum 1
     */
  confidence?: number;
  occurredAt?: string;
}

export type Signal = SignalCreate & {
  id: string;
  confidence: number;
  occurredAt: string;
  createdAt: string;
};

export type LeadScoreEntityType = typeof LeadScoreEntityType[keyof typeof LeadScoreEntityType];


export const LeadScoreEntityType = {
  account: 'account',
  contact: 'contact',
} as const;

export type LeadScoreBreakdown = {[key: string]: number};

export interface LeadScore {
  entityType: LeadScoreEntityType;
  entityId: string;
  score: number;
  fitScore: number;
  engagementScore: number;
  signalScore: number;
  breakdown: LeadScoreBreakdown;
  calculatedAt: string;
}

export interface AiAuthorityActionType {
  id: string;
  label: string;
  description: string;
  defaultLevel: AuthorityLevel;
  currentLevel: AuthorityLevel;
  preparedOutputEditable: boolean;
  manualAlternative: string;
}

export type StageAuthorityOverrideLevels = {[key: string]: AuthorityLevel};

export type AiAuthorityPolicyHistoryItemPreviousLevels = {[key: string]: AuthorityLevel};

export type AiAuthorityPolicyHistoryItemLevels = {[key: string]: AuthorityLevel};

export interface AiAuthorityPolicyHistoryItem {
  id: string;
  actor: string;
  /** @nullable */
  actorAppUserId?: string | null;
  /** @minimum 0 */
  previousVersion: number;
  /** @minimum 1 */
  version: number;
  changedActionTypes: string[];
  /** @nullable */
  journeyKey?: string | null;
  /** @nullable */
  stageId?: string | null;
  previousLevels?: AiAuthorityPolicyHistoryItemPreviousLevels;
  levels?: AiAuthorityPolicyHistoryItemLevels;
  createdAt: string;
}

export interface AiAuthorityPolicyHistory {
  items: AiAuthorityPolicyHistoryItem[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type StageControlRoomPolicyEffects = {[key: string]: string};

export interface StageControlRoomPolicy {
  actionTypes: AiAuthorityActionType[];
  effects: StageControlRoomPolicyEffects;
  history: AiAuthorityPolicyHistory;
}

export interface StageAuthorityOverride {
  policy?: StageControlRoomPolicy;
  journeyKey: JourneyKey;
  stageId: string;
  version: number;
  levels: StageAuthorityOverrideLevels;
}

export interface InstructionActivation {
  id: string;
  actor: string;
  createdAt: string;
  version: number;
  previousVersion: number;
  note: string;
}

export interface WorkforceCounts {
  /** @minimum 0 */
  executed: number;
  /** @minimum 0 */
  recommended: number;
  /** @minimum 0 */
  pending_approval: number;
  /** @minimum 0 */
  approved: number;
  /** @minimum 0 */
  rejected: number;
  /** @minimum 0 */
  deferred: number;
  /** @minimum 0 */
  dismissed: number;
  /** @minimum 0 */
  blocked: number;
  /** @minimum 0 */
  failed: number;
}

export interface WorkforceHumanCounts {
  /** @minimum 0 */
  transitions: number;
  /** @minimum 0 */
  approvals: number;
  /** @minimum 0 */
  rejections: number;
  /** @minimum 0 */
  dismissals: number;
  /** @minimum 0 */
  total: number;
}

export interface WorkforceBucket {
  t: string;
  agent: number;
  human: number;
}

export interface WorkforceSuggestion {
  actionType: string;
  from: AuthorityLevel;
  to: AuthorityLevel;
  evidence: string;
}

export type WorkforceActionOperator = typeof WorkforceActionOperator[keyof typeof WorkforceActionOperator];


export const WorkforceActionOperator = {
  agent: 'agent',
  human: 'human',
  mixed: 'mixed',
  human_only: 'human_only',
} as const;

export type WorkforceActionAuthoritySource = typeof WorkforceActionAuthoritySource[keyof typeof WorkforceActionAuthoritySource];


export const WorkforceActionAuthoritySource = {
  stage_override: 'stage_override',
  crm_policy: 'crm_policy',
  default: 'default',
} as const;

export interface WorkforceAction {
  actionType: string;
  agent: WorkforceCounts;
  human: WorkforceHumanCounts;
  operator: WorkforceActionOperator;
  authorityLevel: AuthorityLevel;
  authoritySource: WorkforceActionAuthoritySource;
  /** @nullable */
  lastAgentAt?: string | null;
  /** @nullable */
  lastHumanAt?: string | null;
  suggestions: WorkforceSuggestion[];
}

export type JourneyWorkforceWindow = typeof JourneyWorkforceWindow[keyof typeof JourneyWorkforceWindow];


export const JourneyWorkforceWindow = {
  '7d': '7d',
  '30d': '30d',
  '90d': '90d',
  all: 'all',
} as const;

export type JourneyWorkforceBucket = typeof JourneyWorkforceBucket[keyof typeof JourneyWorkforceBucket];


export const JourneyWorkforceBucket = {
  day: 'day',
  month: 'month',
} as const;

export type JourneyWorkforceTotals = {
  agent: number;
  human: number;
};

export type JourneyWorkforceStagesItem = {
  stageId: string;
  label: string;
  actions: WorkforceAction[];
  human: WorkforceHumanCounts;
  series: WorkforceBucket[];
};

export interface JourneyWorkforce {
  journeyKey: JourneyKey;
  window: JourneyWorkforceWindow;
  bucket: JourneyWorkforceBucket;
  series: WorkforceBucket[];
  totals: JourneyWorkforceTotals;
  stages: JourneyWorkforceStagesItem[];
}

export type JourneyFloorplanQueueItemRecordKind = typeof JourneyFloorplanQueueItemRecordKind[keyof typeof JourneyFloorplanQueueItemRecordKind];


export const JourneyFloorplanQueueItemRecordKind = {
  enquiry: 'enquiry',
  opportunity: 'opportunity',
  partner: 'partner',
} as const;

export type JourneyFloorplanQueueItemStatus = typeof JourneyFloorplanQueueItemStatus[keyof typeof JourneyFloorplanQueueItemStatus];


export const JourneyFloorplanQueueItemStatus = {
  pending_approval: 'pending_approval',
  recommended: 'recommended',
  blocked: 'blocked',
  assigned_open: 'assigned_open',
  awaiting_decision: 'awaiting_decision',
} as const;

export interface JourneyFloorplanQueueItem {
  /**
     * @minLength 1
     * @maxLength 160
     */
  id: string;
  recordId: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  recordLabel: string;
  recordKind: JourneyFloorplanQueueItemRecordKind;
  /**
     * @minLength 1
     * @maxLength 100
     */
  stageId: string;
  status: JourneyFloorplanQueueItemStatus;
  waitingSince: string;
  /**
     * @minLength 1
     * @maxLength 2048
     */
  deepLink: string;
}

export interface JourneyFloorplanQueue {
  /** @minimum 0 */
  pendingApproval: number;
  /** @minimum 0 */
  recommended: number;
  /** @minimum 0 */
  blocked: number;
  /** @minimum 0 */
  assignedOpen: number;
  /** @minimum 0 */
  awaitingDecision: number;
  /** @maxItems 50 */
  items: JourneyFloorplanQueueItem[];
}

export interface JourneyFloorplanTotals {
  /** @minimum 0 */
  agents: number;
  /** @minimum 0 */
  humans: number;
  /** @minimum 0 */
  queue: number;
  /** @minimum 0 */
  throughput: number;
}

export type JourneyFloorplanDeskKind = typeof JourneyFloorplanDeskKind[keyof typeof JourneyFloorplanDeskKind];


export const JourneyFloorplanDeskKind = {
  agent: 'agent',
  human: 'human',
} as const;

export type JourneyFloorplanDeskBand = typeof JourneyFloorplanDeskBand[keyof typeof JourneyFloorplanDeskBand];


export const JourneyFloorplanDeskBand = {
  idle: 'idle',
  steady: 'steady',
  busy: 'busy',
  overloaded: 'overloaded',
} as const;

export interface JourneyFloorplanDesk {
  /**
     * @minLength 1
     * @maxLength 160
     */
  id: string;
  kind: JourneyFloorplanDeskKind;
  /**
     * @minLength 1
     * @maxLength 200
     */
  label: string;
  /**
     * @minLength 1
     * @maxLength 160
     */
  ref: string;
  queue: JourneyFloorplanQueue;
  /** @minimum 0 */
  throughput: number;
  /** @nullable */
  lastActivityAt: string | null;
  authorityLevel?: AuthorityLevel;
  /**
     * @minimum 0
     * @maximum 1
     */
  busyness: number;
  band: JourneyFloorplanDeskBand;
}

export interface JourneyFloorplanFunction {
  /**
     * @minLength 1
     * @maxLength 160
     */
  id: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  key: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  label: string;
  /** @pattern ^#[0-9A-Fa-f]{6}$ */
  colour: string;
  totals: JourneyFloorplanTotals;
  desks: JourneyFloorplanDesk[];
}

export type JourneyFloorplanWindow = typeof JourneyFloorplanWindow[keyof typeof JourneyFloorplanWindow];


export const JourneyFloorplanWindow = {
  '7d': '7d',
  '30d': '30d',
  '90d': '90d',
  all: 'all',
} as const;

export interface JourneyFloorplan {
  journeyKey: JourneyKey;
  window: JourneyFloorplanWindow;
  functions: JourneyFloorplanFunction[];
  unassigned: JourneyFloorplanFunction;
  anonymised: boolean;
}

export type JourneyBacklogItemKind = typeof JourneyBacklogItemKind[keyof typeof JourneyBacklogItemKind];


export const JourneyBacklogItemKind = {
  next_action: 'next_action',
  record_guidance: 'record_guidance',
} as const;

export interface JourneyBacklogItem {
  itemId: string;
  kind: JourneyBacklogItemKind;
  recordId: string;
  recordLabel: string;
  channel: string;
  waitingSince: string;
  /** @minimum 0 */
  ageMinutes: number;
}

export type JourneyBacklogQueueActor = typeof JourneyBacklogQueueActor[keyof typeof JourneyBacklogQueueActor];


export const JourneyBacklogQueueActor = {
  agent: 'agent',
  human: 'human',
} as const;

export type JourneyBacklogQueueOutcome = typeof JourneyBacklogQueueOutcome[keyof typeof JourneyBacklogQueueOutcome];


export const JourneyBacklogQueueOutcome = {
  pending_approval: 'pending_approval',
  recommended: 'recommended',
  deferred: 'deferred',
  blocked: 'blocked',
} as const;

export interface JourneyBacklogQueue {
  actionType: string;
  actor: JourneyBacklogQueueActor;
  outcome: JourneyBacklogQueueOutcome;
  /** @minimum 0 */
  total: number;
  /** @maxItems 50 */
  items: JourneyBacklogItem[];
}

export interface JourneyBacklogStage {
  stageId: string;
  label: string;
  /** @minimum 0 */
  waiting: number;
  /** @minimum 0 */
  oldestAgeMinutes: number;
  /** @minimum 0 */
  executedInWindow: number;
  queues: JourneyBacklogQueue[];
}

export interface JourneyBacklogChannel {
  channel: string;
  /** @minimum 0 */
  inflow: number;
  lastAt: string;
}

export type JourneyBacklogWindow = typeof JourneyBacklogWindow[keyof typeof JourneyBacklogWindow];


export const JourneyBacklogWindow = {
  '7d': '7d',
  '30d': '30d',
  '90d': '90d',
  all: 'all',
} as const;

export interface JourneyBacklog {
  journeyKey: JourneyKey;
  window: JourneyBacklogWindow;
  stages: JourneyBacklogStage[];
  channels: JourneyBacklogChannel[];
}

export type AiAuthorityPolicyLevels = {[key: string]: AuthorityLevel};

export type AiAuthorityPolicyEffects = {[key: string]: string};

export interface AiAuthorityPolicy {
  /** @minimum 1 */
  version: number;
  updatedAt: string;
  /** @nullable */
  updatedBy?: string | null;
  levels: AiAuthorityPolicyLevels;
  effects: AiAuthorityPolicyEffects;
  stageOverrides?: StageAuthorityOverride[];
  actionTypes: AiAuthorityActionType[];
}

export type AiAuthorityPolicyWithHistory = AiAuthorityPolicy & {
  history: AiAuthorityPolicyHistory;
};

export type AiAuthorityPolicyUpdateLevels = {[key: string]: AuthorityLevel};

export interface AiAuthorityPolicyUpdate {
  /** @minimum 1 */
  expectedVersion: number;
  levels: AiAuthorityPolicyUpdateLevels;
}

export type AiActionDecisionRequestDecision = typeof AiActionDecisionRequestDecision[keyof typeof AiActionDecisionRequestDecision];


export const AiActionDecisionRequestDecision = {
  approve: 'approve',
  reject: 'reject',
  defer: 'defer',
  dismiss: 'dismiss',
  feedback: 'feedback',
} as const;

export type AiActionDecisionRequestEditedPreparedOutput = {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 8000
     */
  body: string;
};

export interface AiActionDecisionRequest {
  decision: AiActionDecisionRequestDecision;
  /** @minimum 1 */
  expectedRevision: number;
  editedPreparedOutput?: AiActionDecisionRequestEditedPreparedOutput;
  deferUntil?: string;
  feedback?: AiActionFeedback;
}

export type AiActionRequestInput = { [key: string]: unknown };

export interface AiActionRequest {
  actionType: string;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  contactId?: string | null;
  input?: AiActionRequestInput;
  requestedBy?: string;
}

export type XeroConnectionStatus = typeof XeroConnectionStatus[keyof typeof XeroConnectionStatus];


export const XeroConnectionStatus = {
  connected: 'connected',
  disconnected: 'disconnected',
} as const;

export interface XeroConnection {
  id: string;
  organisationName: string;
  tenantId: string;
  status: XeroConnectionStatus;
  connectedAt: string;
  /** @nullable */
  disconnectedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface XeroConnectionConnect {
  /** @maxLength 200 */
  organisationName?: string;
}

export interface XeroConnectionState {
  connected: boolean;
  connection?: XeroConnection | null;
}

export interface XeroInvoiceLine {
  id: string;
  invoiceId: string;
  description: string;
  quantity: string;
  /** @nullable */
  unitAmount?: string | null;
  /** @nullable */
  lineAmount?: string | null;
  taxAmount: string;
  sortOrder: number;
  createdAt: string;
}

export interface XeroInvoice {
  id: string;
  invoiceNumber: string;
  /** @nullable */
  reference?: string | null;
  /** @nullable */
  quoteRequestId?: string | null;
  status: XeroInvoiceStatus;
  currency: string;
  subTotal: string;
  totalTax: string;
  total: string;
  issueDate: string;
  /** @nullable */
  dueDate?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface XeroInvoiceList {
  data: XeroInvoice[];
  total: number;
}

export type XeroInvoiceDetail = XeroInvoice & {
  lines: XeroInvoiceLine[];
  allowedTransitions: XeroInvoiceStatus[];
};

export interface XeroInvoiceFromQuoteCreate {
  quoteRequestId: string;
}

export interface XeroInvoiceStatusUpdate {
  status: XeroInvoiceStatus;
}

export type XeroBankTxnType = typeof XeroBankTxnType[keyof typeof XeroBankTxnType];


export const XeroBankTxnType = {
  credit: 'credit',
  debit: 'debit',
} as const;

export type XeroBankTxnStatus = typeof XeroBankTxnStatus[keyof typeof XeroBankTxnStatus];


export const XeroBankTxnStatus = {
  unmatched: 'unmatched',
  suggested: 'suggested',
  matched: 'matched',
  reconciled: 'reconciled',
  excluded: 'excluded',
} as const;

export interface XeroBankTransaction {
  id: string;
  transactionDate: string;
  payee: string;
  /** @nullable */
  reference?: string | null;
  /** @nullable */
  description?: string | null;
  type: XeroBankTxnType;
  amount: string;
  status: XeroBankTxnStatus;
  /** @nullable */
  matchedInvoiceId?: string | null;
  /** @nullable */
  reconciledAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface XeroBankTransactionList {
  data: XeroBankTransaction[];
  total: number;
}

export interface XeroBankTransactionClearResult {
  cleared: number;
}

export interface XeroBankFeedGenerationResult {
  generated: number;
  exactMatches: number;
  partialPayments: number;
  typoedReferences: number;
  duplicates: number;
  noise: number;
}

export type XeroMatchConfidence = typeof XeroMatchConfidence[keyof typeof XeroMatchConfidence];


export const XeroMatchConfidence = {
  high: 'high',
  medium: 'medium',
  low: 'low',
} as const;

export type XeroSuggestionStatus = typeof XeroSuggestionStatus[keyof typeof XeroSuggestionStatus];


export const XeroSuggestionStatus = {
  suggested: 'suggested',
  accepted: 'accepted',
  rejected: 'rejected',
} as const;

export type XeroInvoicePaymentState = XeroInvoice & {
  amountPaid: string;
  amountOutstanding: string;
};

export interface XeroMatchSuggestion {
  id: string;
  bankTransactionId: string;
  invoiceId: string;
  /**
     * @minimum 0
     * @maximum 100
     */
  score: number;
  confidence: XeroMatchConfidence;
  reasons: string[];
  status: XeroSuggestionStatus;
  transaction: XeroBankTransaction;
  invoice: XeroInvoicePaymentState;
  createdAt: string;
  updatedAt: string;
}

export interface XeroMatchSuggestionList {
  data: XeroMatchSuggestion[];
  total: number;
}

export interface XeroMatchingRunResult {
  transactionsConsidered: number;
  invoicesConsidered: number;
  suggestionsCreated: number;
  unmatchedCredits: number;
}

export interface XeroManualMatchCreate {
  bankTransactionId: string;
  invoiceId: string;
}

export interface XeroUnmatchCreate {
  bankTransactionId: string;
}

export interface XeroUnmatchResult {
  transaction: XeroBankTransaction;
  affectedInvoices: XeroInvoicePaymentState[];
}

export type XeroReconciliationIssueType = typeof XeroReconciliationIssueType[keyof typeof XeroReconciliationIssueType];


export const XeroReconciliationIssueType = {
  amount_mismatch: 'amount_mismatch',
  over_payment: 'over_payment',
  under_payment: 'under_payment',
  duplicate_transaction: 'duplicate_transaction',
  aging_unmatched: 'aging_unmatched',
  orphan_credit: 'orphan_credit',
} as const;

export type XeroReconciliationIssueSeverity = typeof XeroReconciliationIssueSeverity[keyof typeof XeroReconciliationIssueSeverity];


export const XeroReconciliationIssueSeverity = {
  high: 'high',
  medium: 'medium',
  low: 'low',
} as const;

export interface XeroReconciliationIssue {
  type: XeroReconciliationIssueType;
  severity: XeroReconciliationIssueSeverity;
  message: string;
  details: string[];
  /** @nullable */
  bankTransactionId?: string | null;
  /** @nullable */
  invoiceId?: string | null;
  /** @nullable */
  invoiceNumber?: string | null;
}

export interface XeroReconciliationSummary {
  invoiceCount: number;
  totalInvoiced: string;
  totalReconciled: string;
  totalOutstanding: string;
  unmatchedTransactionCount: number;
  suggestedCount: number;
  reconciledTransactionCount: number;
  issueCount: number;
}

export interface XeroReconciliationReport {
  summary: XeroReconciliationSummary;
  /** @maxItems 200 */
  issues: XeroReconciliationIssue[];
  issueTotal: number;
}

export type AccountingPlatform = typeof AccountingPlatform[keyof typeof AccountingPlatform];


export const AccountingPlatform = {
  xero: 'xero',
  myob: 'myob',
  quickbooks: 'quickbooks',
  reckon: 'reckon',
  saasu: 'saasu',
} as const;

export type AccountingConnectionStatus = typeof AccountingConnectionStatus[keyof typeof AccountingConnectionStatus];


export const AccountingConnectionStatus = {
  connected: 'connected',
  disconnected: 'disconnected',
} as const;

/**
 * Stored connection details. The client secret is never returned — only whether one is stored.
 */
export interface AccountingConnectionState {
  status: AccountingConnectionStatus;
  organisationName: string;
  clientId: string;
  entityId: string;
  hasClientSecret: boolean;
  /** @nullable */
  region?: string | null;
  connectedAt: string;
  /** @nullable */
  disconnectedAt?: string | null;
  updatedAt: string;
}

export interface AccountingIntegration {
  platform: AccountingPlatform;
  label: string;
  description: string;
  entityIdLabel: string;
  supportsRegion: boolean;
  /** True when the platform drives CRM invoicing today (Xero); others are configuration-only for now. */
  crmSync: boolean;
  connection: AccountingConnectionState | null;
}

export interface AccountingIntegrationList {
  /** @maxItems 20 */
  items: AccountingIntegration[];
}

export interface AccountingIntegrationConnect {
  /**
     * @minLength 1
     * @maxLength 200
     */
  organisationName: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  clientId: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  clientSecret: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  entityId: string;
  /** @maxLength 50 */
  region?: string;
}

export interface XeroCatalogSyncResult {
  items: number;
  contacts: number;
}

export interface XeroCatalogClearResult {
  items: number;
  contacts: number;
}

export interface XeroItem {
  id: string;
  code: string;
  name: string;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  unitPrice?: string | null;
  /** @nullable */
  taxRateName?: string | null;
  /** @nullable */
  taxRatePercent?: string | null;
  /** @nullable */
  revenueAccountCode?: string | null;
}

export interface XeroItemList {
  data: XeroItem[];
  total: number;
}

export interface XeroContact {
  id: string;
  xeroContactId?: string;
  name: string;
  /** @nullable */
  firstName?: string | null;
  /** @nullable */
  lastName?: string | null;
  /** @nullable */
  email?: string | null;
  /** @nullable */
  phone?: string | null;
  /** @nullable */
  city?: string | null;
  /** @nullable */
  country?: string | null;
}

export interface XeroContactList {
  data: XeroContact[];
  total: number;
}

export type EntityProfileQualificationFactKind = typeof EntityProfileQualificationFactKind[keyof typeof EntityProfileQualificationFactKind];


export const EntityProfileQualificationFactKind = {
  accreditation: 'accreditation',
  panel_membership: 'panel_membership',
  tax_status: 'tax_status',
  annual_turnover: 'annual_turnover',
  employee_count: 'employee_count',
  research_activities: 'research_activities',
} as const;

export type EntityProfileQualificationFactAttribution = typeof EntityProfileQualificationFactAttribution[keyof typeof EntityProfileQualificationFactAttribution];


export const EntityProfileQualificationFactAttribution = {
  user_assertion: 'user_assertion',
} as const;

export interface EntityProfileQualificationFact {
  id: string;
  kind: EntityProfileQualificationFactKind;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  value: string;
  /**
     * @maxLength 10000
     * @nullable
     */
  literalEvidence: string | null;
  /**
     * @maxLength 2048
     * @nullable
     */
  url: string | null;
  /** @nullable */
  retrievedAt: string | null;
  attribution: EntityProfileQualificationFactAttribution;
}

export interface EntityProfileEnrichmentInput {
  requestKey: string;
  /**
     * @maxLength 1000
     * @nullable
     */
  websiteUrl: string | null;
  /**
     * @maxLength 20000
     * @nullable
     */
  prompt: string | null;
  /**
     * @maxItems 3
     * @items.minLength 3
     * @items.maxLength 300
     */
  publicSearchTerms: string[];
}

export interface EntityProfileIdentityCandidate {
  /** @maxLength 2048 */
  url: string;
  /** @maxLength 500 */
  title: string;
  /** @maxLength 2000 */
  excerpt: string;
  retrievedAt: string;
}

export type EntityProfileEnrichment = GovernmentProfileProposal & ({
  /** @minimum 0 */
  profileVersion: number;
  /** @maxItems 15 */
  identityCandidates: EntityProfileIdentityCandidate[];
  /**
     * @maxItems 3
     * @items.maxLength 300
     */
  publicSearchTerms: string[];
  /**
     * @minimum 1
     * @nullable
     */
  savedProfileVersion: number | null;
  /** @maxItems 50 */
  qualificationFacts: EntityProfileQualificationFact[];
});

export interface EntityProfileEnrichmentList {
  items: EntityProfileEnrichment[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 50
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface EntityProfileEnrichmentSelection {
  /** @minimum 1 */
  expectedRevision: number;
  /** @maxLength 2048 */
  websiteUrl: string;
}

export type EntityProfileEnrichmentDecisionFieldKey = typeof EntityProfileEnrichmentDecisionFieldKey[keyof typeof EntityProfileEnrichmentDecisionFieldKey];


export const EntityProfileEnrichmentDecisionFieldKey = {
  businessName: 'businessName',
  description: 'description',
  industry: 'industry',
  websiteUrl: 'websiteUrl',
  locationText: 'locationText',
  services: 'services',
  markets: 'markets',
} as const;

export interface EntityProfileEnrichmentDecisionField {
  key: EntityProfileEnrichmentDecisionFieldKey;
  value: string | string[];
}

export interface EntityProfileEnrichmentDecision {
  requestKey: string;
  /** @minimum 1 */
  expectedRevision: number;
  /** @minimum 0 */
  expectedProfileVersion: number;
  /** @maxItems 7 */
  fields: EntityProfileEnrichmentDecisionField[];
  /** @maxItems 50 */
  qualificationFacts?: EntityProfileQualificationFact[];
}

export interface EntityProfileUpdate {
  /** @minimum 0 */
  expectedVersion?: number;
  /** @maxLength 200 */
  businessName?: string;
  /** @maxLength 300 */
  tagline?: string;
  /** @maxLength 5000 */
  description?: string;
  /** @maxLength 200 */
  industry?: string;
  /** @maxLength 1000 */
  websiteUrl?: string;
  /** @maxLength 320 */
  contactEmail?: string;
  /** @maxLength 100 */
  contactPhone?: string;
  /** @maxLength 500 */
  locationText?: string;
  /** @maxItems 50 */
  markets?: string[];
  /** @maxLength 3000 */
  targetAudience?: string;
  /** @maxItems 100 */
  services?: string[];
  /** @maxLength 3000 */
  valueProposition?: string;
  /** @maxItems 50 */
  differentiators?: string[];
  /** @maxLength 3000 */
  toneOfVoice?: string;
  /** @maxLength 3000 */
  aiPersona?: string;
  /** @maxLength 10000 */
  voiceExamples?: string;
  /** @maxItems 50 */
  signatureCtas?: string[];
  /** @maxItems 100 */
  hashtagBank?: string[];
  /** @maxItems 20 */
  preferredPlatforms?: string[];
  /** @maxItems 50 */
  contentPillars?: string[];
  /** @maxLength 500 */
  imageStyle?: string;
  /** @maxLength 5000 */
  restrictedClaims?: string;
  /** @maxLength 5000 */
  disclaimers?: string;
  /** @maxLength 50 */
  locale?: string;
  /** @maxLength 100 */
  timezone?: string;
  /** @maxLength 10 */
  currency?: string;
  requireApproval?: boolean;
  notifyOnPublish?: boolean;
}

export interface EntityProfileSave {
  /** @minimum 0 */
  expectedVersion: number;
  /** @maxLength 200 */
  businessName?: string;
  /** @maxLength 300 */
  tagline?: string;
  /** @maxLength 5000 */
  description?: string;
  /** @maxLength 200 */
  industry?: string;
  /** @maxLength 1000 */
  websiteUrl?: string;
  /** @maxLength 320 */
  contactEmail?: string;
  /** @maxLength 100 */
  contactPhone?: string;
  /** @maxLength 500 */
  locationText?: string;
  /** @maxItems 50 */
  markets?: string[];
  /** @maxLength 3000 */
  targetAudience?: string;
  /** @maxItems 100 */
  services?: string[];
  /** @maxLength 3000 */
  valueProposition?: string;
  /** @maxItems 50 */
  differentiators?: string[];
  /** @maxLength 3000 */
  toneOfVoice?: string;
  /** @maxLength 3000 */
  aiPersona?: string;
  /** @maxLength 10000 */
  voiceExamples?: string;
  /** @maxItems 50 */
  signatureCtas?: string[];
  /** @maxItems 100 */
  hashtagBank?: string[];
  /** @maxItems 20 */
  preferredPlatforms?: string[];
  /** @maxItems 50 */
  contentPillars?: string[];
  /** @maxLength 500 */
  imageStyle?: string;
  /** @maxLength 5000 */
  restrictedClaims?: string;
  /** @maxLength 5000 */
  disclaimers?: string;
  /** @maxLength 50 */
  locale?: string;
  /** @maxLength 100 */
  timezone?: string;
  /** @maxLength 10 */
  currency?: string;
  requireApproval?: boolean;
  notifyOnPublish?: boolean;
}

export type EntityProfileLogoContentType = typeof EntityProfileLogoContentType[keyof typeof EntityProfileLogoContentType];


export const EntityProfileLogoContentType = {
  'image/png': 'image/png',
  'image/jpeg': 'image/jpeg',
  'image/webp': 'image/webp',
  'image/avif': 'image/avif',
} as const;

export interface EntityProfileLogo {
  id: string;
  filename: string;
  contentType: EntityProfileLogoContentType;
  /**
     * @minimum 1
     * @maximum 8388608
     */
  sizeBytes: number;
  updatedAt: string;
}

export type EntityProfile = EntityProfileUpdate & ({
  /** @nullable */
  id?: string | null;
  logo: EntityProfileLogo | null;
  version: number;
  /** @nullable */
  createdAt?: string | null;
  /** @nullable */
  updatedAt?: string | null;
}) & Required<Pick<EntityProfileUpdate & ({
  /** @nullable */
  id?: string | null;
  logo: EntityProfileLogo | null;
  version: number;
  /** @nullable */
  createdAt?: string | null;
  /** @nullable */
  updatedAt?: string | null;
}), 'businessName' | 'tagline' | 'description' | 'industry' | 'websiteUrl' | 'contactEmail' | 'contactPhone' | 'locationText' | 'markets' | 'targetAudience' | 'services' | 'valueProposition' | 'differentiators' | 'toneOfVoice' | 'aiPersona' | 'voiceExamples' | 'signatureCtas' | 'hashtagBank' | 'preferredPlatforms' | 'contentPillars' | 'imageStyle' | 'restrictedClaims' | 'disclaimers' | 'locale' | 'timezone' | 'currency' | 'requireApproval' | 'notifyOnPublish'>>;

export interface EntityProfileWebsiteSummaryRequest {
  /**
     * @minLength 1
     * @maxLength 1000
     */
  websiteUrl: string;
}

export interface EntityProfileWebsiteSummary {
  /**
     * @minLength 1
     * @maxLength 5000
     */
  summary: string;
  sourceUrl: string;
}

export type ContentHubBookingStatus = typeof ContentHubBookingStatus[keyof typeof ContentHubBookingStatus];


export const ContentHubBookingStatus = {
  pending: 'pending',
  confirmed: 'confirmed',
  completed: 'completed',
  cancelled: 'cancelled',
} as const;

export type ContentHubMediaStatus = typeof ContentHubMediaStatus[keyof typeof ContentHubMediaStatus];


export const ContentHubMediaStatus = {
  processing: 'processing',
  ready: 'ready',
  failed: 'failed',
} as const;

export type ContentHubMediaDerivativeStatus = typeof ContentHubMediaDerivativeStatus[keyof typeof ContentHubMediaDerivativeStatus];


export const ContentHubMediaDerivativeStatus = {
  pending: 'pending',
  ready: 'ready',
  failed: 'failed',
} as const;

export type ContentHubMediaAssetContentType = typeof ContentHubMediaAssetContentType[keyof typeof ContentHubMediaAssetContentType];


export const ContentHubMediaAssetContentType = {
  'image/png': 'image/png',
  'image/jpeg': 'image/jpeg',
  'image/webp': 'image/webp',
  'video/mp4': 'video/mp4',
  'video/quicktime': 'video/quicktime',
  'video/webm': 'video/webm',
} as const;

export type ContentHubMediaAssetScanStatus = typeof ContentHubMediaAssetScanStatus[keyof typeof ContentHubMediaAssetScanStatus];


export const ContentHubMediaAssetScanStatus = {
  pending: 'pending',
  structural_passed: 'structural_passed',
  failed: 'failed',
} as const;

export type ContentHubGeneratedMediaOriginSource = typeof ContentHubGeneratedMediaOriginSource[keyof typeof ContentHubGeneratedMediaOriginSource];


export const ContentHubGeneratedMediaOriginSource = {
  openrouter: 'openrouter',
  project_ai: 'project_ai',
} as const;

export type ContentHubGeneratedMediaOriginKind = typeof ContentHubGeneratedMediaOriginKind[keyof typeof ContentHubGeneratedMediaOriginKind];


export const ContentHubGeneratedMediaOriginKind = {
  scene: 'scene',
  final: 'final',
  image: 'image',
} as const;

export interface ContentHubGeneratedMediaOrigin {
  source: ContentHubGeneratedMediaOriginSource;
  /** @nullable */
  model: string | null;
  /** @nullable */
  projectId: string | null;
  /** @nullable */
  sceneId: string | null;
  kind: ContentHubGeneratedMediaOriginKind;
}

export interface ContentHubMediaAsset {
  id: string;
  filename: string;
  kind: ContentHubMediaKind;
  contentType: ContentHubMediaAssetContentType;
  /**
     * @minimum 1
     * @maximum 20971520
     */
  sizeBytes: number;
  /** @pattern ^[a-f0-9]{64}$ */
  checksumSha256: string;
  origin: ContentHubGeneratedMediaOrigin | null;
  status: ContentHubMediaStatus;
  scanStatus: ContentHubMediaAssetScanStatus;
  derivativeStatus: ContentHubMediaDerivativeStatus;
  selectable: boolean;
  /** @minimum 0 */
  processingAttempts: number;
  /** @nullable */
  failureCode: string | null;
  /** @nullable */
  failureMessage: string | null;
  /** @nullable */
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export type ContentHubImageGenerationRequestSize = typeof ContentHubImageGenerationRequestSize[keyof typeof ContentHubImageGenerationRequestSize];


export const ContentHubImageGenerationRequestSize = {
  '1024x1024': '1024x1024',
  '1536x1024': '1536x1024',
  '1024x1536': '1024x1536',
} as const;

export interface ContentHubImageGenerationRequest {
  /**
     * @minLength 1
     * @maxLength 4000
     */
  prompt: string;
  size?: ContentHubImageGenerationRequestSize;
}

export type ContentHubMediaDerivativeProfile = typeof ContentHubMediaDerivativeProfile[keyof typeof ContentHubMediaDerivativeProfile];


export const ContentHubMediaDerivativeProfile = {
  preview: 'preview',
  provider: 'provider',
} as const;

export interface ContentHubMediaDerivative {
  id: string;
  profile: ContentHubMediaDerivativeProfile;
  contentType: string;
  /** @minimum 1 */
  sizeBytes: number;
  status: ContentHubMediaDerivativeStatus;
  createdAt: string;
}

export type ContentHubMediaDetail = ContentHubMediaAsset & {
  derivatives: ContentHubMediaDerivative[];
};

export interface ContentHubMediaList {
  items: ContentHubMediaAsset[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface ContentHubMediaSelectionRequest {
  /**
     * @minItems 1
     * @maxItems 10
     */
  assetIds: string[];
}

export interface ContentHubMediaSelection {
  items: ContentHubMediaAsset[];
}

export interface ContentHubMediaDeliveryRequest {
  provider: SocialNetwork;
  /**
     * @minimum 300
     * @maximum 86400
     */
  lifetimeSeconds?: number;
}

export interface ContentHubMediaDelivery {
  url: string;
  provider: SocialNetwork;
  expiresAt: string;
}

export type ContentHubMediaCapabilityState = typeof ContentHubMediaCapabilityState[keyof typeof ContentHubMediaCapabilityState];


export const ContentHubMediaCapabilityState = {
  ready: 'ready',
  unavailable: 'unavailable',
  unsupported: 'unsupported',
} as const;

export interface ContentHubMediaCapability {
  key: string;
  label: string;
  state: ContentHubMediaCapabilityState;
  detail: string;
}

export type ContentHubMediaReadinessAcceptsItem = typeof ContentHubMediaReadinessAcceptsItem[keyof typeof ContentHubMediaReadinessAcceptsItem];


export const ContentHubMediaReadinessAcceptsItem = {
  'image/png': 'image/png',
  'image/jpeg': 'image/jpeg',
  'image/webp': 'image/webp',
  'video/mp4': 'video/mp4',
  'video/quicktime': 'video/quicktime',
  'video/webm': 'video/webm',
} as const;

export interface ContentHubMediaReadiness {
  accepts: ContentHubMediaReadinessAcceptsItem[];
  /** @minimum 1 */
  maxUploadBytes: number;
  capabilities: ContentHubMediaCapability[];
}

export type VideoGenerationConnectionStatus = typeof VideoGenerationConnectionStatus[keyof typeof VideoGenerationConnectionStatus];


export const VideoGenerationConnectionStatus = {
  ready: 'ready',
  disabled: 'disabled',
} as const;

export type VideoGenerationConnectionProvider = typeof VideoGenerationConnectionProvider[keyof typeof VideoGenerationConnectionProvider];


export const VideoGenerationConnectionProvider = {
  openrouter: 'openrouter',
} as const;

export interface VideoGenerationConnection {
  enabled: boolean;
  status: VideoGenerationConnectionStatus;
  reason?: string;
  provider: VideoGenerationConnectionProvider;
  stitchAvailable: boolean;
}

export interface ContentHubVideoModelsResponse {
  connection: VideoGenerationConnection;
  /**
     * @minItems 3
     * @maxItems 3
     */
  models: VideoGenerationModel[];
}

export type VideoProjectStatus = typeof VideoProjectStatus[keyof typeof VideoProjectStatus];


export const VideoProjectStatus = {
  draft: 'draft',
  rendering: 'rendering',
  ready: 'ready',
  failed: 'failed',
} as const;

export type VideoContinuityMode = typeof VideoContinuityMode[keyof typeof VideoContinuityMode];


export const VideoContinuityMode = {
  last_frame: 'last_frame',
  none: 'none',
} as const;

export type VideoSceneStatus = typeof VideoSceneStatus[keyof typeof VideoSceneStatus];


export const VideoSceneStatus = {
  draft: 'draft',
  rendering: 'rendering',
  ready: 'ready',
  stale: 'stale',
  failed: 'failed',
} as const;

export type VideoFirstFrameSource = typeof VideoFirstFrameSource[keyof typeof VideoFirstFrameSource];


export const VideoFirstFrameSource = {
  previous_scene: 'previous_scene',
  none: 'none',
} as const;

export type VideoGenerationJobKind = typeof VideoGenerationJobKind[keyof typeof VideoGenerationJobKind];


export const VideoGenerationJobKind = {
  scene_render: 'scene_render',
  final_assembly: 'final_assembly',
} as const;

export type VideoGenerationJobStatus = typeof VideoGenerationJobStatus[keyof typeof VideoGenerationJobStatus];


export const VideoGenerationJobStatus = {
  queued: 'queued',
  submitted: 'submitted',
  polling: 'polling',
  downloading: 'downloading',
  assembling: 'assembling',
  succeeded: 'succeeded',
  failed: 'failed',
  cancelled: 'cancelled',
} as const;

export interface VideoSceneEditProposal {
  sceneId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 20000
     */
  prompt: string;
  /**
     * @maxLength 5000
     * @nullable
     */
  negativePrompt: string | null;
  durationSeconds: VideoGenerationDurationSeconds;
}

export interface VideoProjectChatRequest {
  /**
     * @minLength 1
     * @maxLength 4000
     */
  message: string;
  /** @nullable */
  sceneId?: string | null;
}

export interface VideoProjectChatResponse {
  /** @minLength 1 */
  reply: string;
  aiAuditId: string;
  proposedSceneEdit: VideoSceneEditProposal | null;
}

export interface VideoProjectDistributionInput {
  downloadEnabled: true;
  autoPostToSocial: boolean;
  /**
     * @maxItems 20
     * @items.minLength 1
     * @items.maxLength 100
     */
  socialNetworks: string[];
}

export type VideoProjectDistributionStatus = typeof VideoProjectDistributionStatus[keyof typeof VideoProjectDistributionStatus];


export const VideoProjectDistributionStatus = {
  disabled: 'disabled',
  waiting_for_networks: 'waiting_for_networks',
  waiting_for_video: 'waiting_for_video',
  queued: 'queued',
  partial: 'partial',
  stale: 'stale',
  failed: 'failed',
} as const;

export type VideoProjectDistributionNetworkOutcomesItemStatus = typeof VideoProjectDistributionNetworkOutcomesItemStatus[keyof typeof VideoProjectDistributionNetworkOutcomesItemStatus];


export const VideoProjectDistributionNetworkOutcomesItemStatus = {
  queued: 'queued',
  skipped: 'skipped',
  published: 'published',
  failed: 'failed',
} as const;

export type VideoProjectDistributionNetworkOutcomesItem = {
  network: string;
  status: VideoProjectDistributionNetworkOutcomesItemStatus;
  reason?: string;
};

export type VideoProjectDistribution = VideoProjectDistributionInput & ({
  /** @nullable */
  campaignId: string | null;
  /** @nullable */
  campaignStepId: string | null;
  /** @nullable */
  socialPostId: string | null;
  status: VideoProjectDistributionStatus;
  networkOutcomes: VideoProjectDistributionNetworkOutcomesItem[];
});

export interface VideoScene {
  id: string;
  projectId: string;
  /**
     * @minimum 1
     * @maximum 8
     */
  position: number;
  title: string;
  prompt: string;
  /** @nullable */
  negativePrompt?: string | null;
  durationSeconds: VideoGenerationDurationSeconds;
  status: VideoSceneStatus;
  /** @nullable */
  currentJobId?: string | null;
  /** @nullable */
  mediaAssetId?: string | null;
  /** @nullable */
  lastFrameStorageKey?: string | null;
  firstFrameSource: VideoFirstFrameSource;
  /** @nullable */
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VideoProject {
  id: string;
  title: string;
  brief: string;
  mode: VideoProjectMode;
  aspectRatio: VideoGenerationAspectRatio;
  resolution: VideoGenerationResolution;
  defaultModel: VideoGenerationModelId;
  continuityMode: VideoContinuityMode;
  status: VideoProjectStatus;
  /** @nullable */
  finalMediaAssetId?: string | null;
  distribution: VideoProjectDistribution;
  createdBy: string;
  /**
     * @minItems 1
     * @maxItems 8
     */
  scenes: VideoScene[];
  createdAt: string;
  updatedAt: string;
}

export interface VideoProjectList {
  items: VideoProject[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface VideoProjectCreate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /** @maxLength 10000 */
  brief?: string;
  mode: VideoProjectMode;
  aspectRatio: VideoGenerationAspectRatio;
  resolution: VideoGenerationResolution;
  defaultModel: VideoGenerationModelId;
  continuityMode?: VideoContinuityMode;
  distribution?: VideoProjectDistributionInput;
  initialDurationSeconds: VideoGenerationDurationSeconds;
  /**
     * @minLength 1
     * @maxLength 200
     */
  initialSceneTitle?: string;
  /** @maxLength 20000 */
  initialPrompt?: string;
}

export interface VideoProjectUpdate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
  /** @maxLength 10000 */
  brief?: string;
  aspectRatio?: VideoGenerationAspectRatio;
  resolution?: VideoGenerationResolution;
  defaultModel?: VideoGenerationModelId;
  continuityMode?: VideoContinuityMode;
  distribution?: VideoProjectDistributionInput;
}

export interface VideoSceneCreate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /** @maxLength 20000 */
  prompt: string;
  /**
     * @maxLength 5000
     * @nullable
     */
  negativePrompt?: string | null;
  durationSeconds: VideoGenerationDurationSeconds;
  /**
     * @maxLength 5000
     * @nullable
     */
  notes?: string | null;
}

export interface VideoSceneUpdate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
  /** @maxLength 20000 */
  prompt?: string;
  /**
     * @maxLength 5000
     * @nullable
     */
  negativePrompt?: string | null;
  durationSeconds?: VideoGenerationDurationSeconds;
  /**
     * @maxLength 5000
     * @nullable
     */
  notes?: string | null;
}

export interface VideoSceneReorder {
  /**
     * @minItems 1
     * @maxItems 8
     */
  sceneIds: string[];
}

export interface VideoSceneRenderRequest {
  model: VideoGenerationModelId;
}

export type VideoGenerationJobProvider = typeof VideoGenerationJobProvider[keyof typeof VideoGenerationJobProvider];


export const VideoGenerationJobProvider = {
  openrouter: 'openrouter',
} as const;

export type VideoGenerationJobParams = { [key: string]: unknown };

export interface VideoGenerationJob {
  id: string;
  projectId: string;
  /** @nullable */
  sceneId?: string | null;
  kind: VideoGenerationJobKind;
  provider: VideoGenerationJobProvider;
  model?: VideoGenerationModelId | null;
  params: VideoGenerationJobParams;
  /** @minimum 0 */
  estimatedCostUsd: number;
  /** @nullable */
  providerJobId?: string | null;
  status: VideoGenerationJobStatus;
  /** @nullable */
  errorCode?: string | null;
  /** @nullable */
  errorMessage?: string | null;
  /** @minimum 1 */
  attempt: number;
  startedBy: string;
  /** @nullable */
  startedAt?: string | null;
  /** @nullable */
  finishedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VideoGenerationJobList {
  items: VideoGenerationJob[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type VideoGenerationUsageByModelProvider = typeof VideoGenerationUsageByModelProvider[keyof typeof VideoGenerationUsageByModelProvider];


export const VideoGenerationUsageByModelProvider = {
  openrouter: 'openrouter',
} as const;

export interface VideoGenerationUsageByModel {
  provider: VideoGenerationUsageByModelProvider;
  model: VideoGenerationModelId;
  /** @minimum 0 */
  runs: number;
  /** @minimum 0 */
  seconds: number;
  /** @minimum 0 */
  estimatedCostUsd: number;
  /** @minimum 0 */
  actualCostUsd: number;
}

export interface VideoGenerationUsageSummary {
  /** @nullable */
  from: string | null;
  /** @nullable */
  to: string | null;
  /** @minimum 0 */
  totalRuns: number;
  /** @minimum 0 */
  totalSeconds: number;
  /** @minimum 0 */
  estimatedCostUsd: number;
  /** @minimum 0 */
  actualCostUsd: number;
  /** @maxItems 3 */
  byModel: VideoGenerationUsageByModel[];
}

export interface ContentHubBookingCreate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  email: string;
  /**
     * @maxLength 100
     * @nullable
     */
  phone?: string | null;
  /**
     * @minLength 1
     * @maxLength 300
     */
  service: string;
  startsAt: string;
  /** @nullable */
  endsAt?: string | null;
  /**
     * @maxLength 5000
     * @nullable
     */
  notes?: string | null;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  accountId?: string | null;
  /** @nullable */
  enquiryId?: string | null;
  /** @nullable */
  slotId?: string | null;
  consentToContact?: boolean;
}

export interface ContentHubBookingUpdate {
  status?: ContentHubBookingStatus;
  startsAt?: string;
  /** @nullable */
  endsAt?: string | null;
  /**
     * @maxLength 5000
     * @nullable
     */
  notes?: string | null;
  /** @nullable */
  slotId?: string | null;
  expectedUpdatedAt: string;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  reason?: string;
}

export type ContentHubBookingSource = typeof ContentHubBookingSource[keyof typeof ContentHubBookingSource];


export const ContentHubBookingSource = {
  manual: 'manual',
  public_microsite: 'public_microsite',
} as const;

export type ContentHubBooking = ContentHubBookingCreate & ({
  id: string;
  status: ContentHubBookingStatus;
  source: ContentHubBookingSource;
  /** @nullable */
  micrositeBindingId?: string | null;
  createdAt: string;
  updatedAt: string;
}) & Required<Pick<ContentHubBookingCreate & ({
  id: string;
  status: ContentHubBookingStatus;
  source: ContentHubBookingSource;
  /** @nullable */
  micrositeBindingId?: string | null;
  createdAt: string;
  updatedAt: string;
}), 'consentToContact'>>;

export interface ContentHubBookingList {
  items: ContentHubBooking[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface ContentHubBookingSlotCreate {
  /**
     * @minLength 1
     * @maxLength 300
     */
  service: string;
  /**
     * @maxLength 1000
     * @nullable
     */
  description?: string | null;
  startsAt: string;
  endsAt: string;
  /**
     * @minLength 1
     * @maxLength 100
     */
  timezone: string;
  /**
     * @minimum 1
     * @maximum 100
     */
  capacity: number;
  active?: boolean;
}

export interface ContentHubBookingSlotUpdate {
  /**
     * @maxLength 1000
     * @nullable
     */
  description?: string | null;
  /**
     * @minimum 1
     * @maximum 100
     */
  capacity?: number;
  active?: boolean;
  expectedUpdatedAt: string;
}

export type ContentHubBookingSlot = ContentHubBookingSlotCreate & {
  id: string;
  /** @minimum 0 */
  reserved: number;
  /** @minimum 0 */
  remaining: number;
  createdAt: string;
  updatedAt: string;
};

export interface ContentHubBookingSlotList {
  items: ContentHubBookingSlot[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type ContentHubBookingEventType = typeof ContentHubBookingEventType[keyof typeof ContentHubBookingEventType];


export const ContentHubBookingEventType = {
  created: 'created',
  confirmed: 'confirmed',
  rescheduled: 'rescheduled',
  cancelled: 'cancelled',
  completed: 'completed',
  notes_updated: 'notes_updated',
} as const;

export interface ContentHubBookingEvent {
  id: string;
  bookingId: string;
  type: ContentHubBookingEventType;
  actor: string;
  /** @nullable */
  reason: string | null;
  fromStatus: ContentHubBookingStatus | null;
  toStatus: ContentHubBookingStatus | null;
  /** @nullable */
  fromStartsAt: string | null;
  /** @nullable */
  toStartsAt: string | null;
  createdAt: string;
}

export interface ContentHubBookingEventList {
  items: ContentHubBookingEvent[];
}

export interface PublicBookingSlot {
  id: string;
  service: string;
  /** @nullable */
  description?: string | null;
  startsAt: string;
  endsAt: string;
  timezone: string;
  /** @minimum 1 */
  remaining: number;
}

export interface PublicBookingSlotList {
  items: PublicBookingSlot[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface PublicBookingCreate {
  requestId: string;
  slotId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  email: string;
  /**
     * @maxLength 100
     * @nullable
     */
  phone?: string | null;
  /**
     * @maxLength 2000
     * @nullable
     */
  notes?: string | null;
  consentToContact: true;
  /** @maxLength 200 */
  website?: string;
}

export interface PublicBookingConfirmation {
  id: string;
  status: ContentHubBookingStatus;
  service: string;
  startsAt: string;
  endsAt: string;
  message: string;
}

export type ContentHubAssetKind = typeof ContentHubAssetKind[keyof typeof ContentHubAssetKind];


export const ContentHubAssetKind = {
  lead_magnet: 'lead_magnet',
  testimonial: 'testimonial',
  case_study: 'case_study',
  faq: 'faq',
  operation: 'operation',
} as const;

export type ContentHubAssetCreateStatus = typeof ContentHubAssetCreateStatus[keyof typeof ContentHubAssetCreateStatus];


export const ContentHubAssetCreateStatus = {
  draft: 'draft',
  approved: 'approved',
  archived: 'archived',
} as const;

export type ContentHubAssetCreateMetadata = { [key: string]: unknown };

export interface ContentHubAssetCreate {
  kind: ContentHubAssetKind;
  /**
     * @minLength 1
     * @maxLength 300
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 30000
     */
  content: string;
  status?: ContentHubAssetCreateStatus;
  metadata?: ContentHubAssetCreateMetadata;
}

export type ContentHubAssetUpdateStatus = typeof ContentHubAssetUpdateStatus[keyof typeof ContentHubAssetUpdateStatus];


export const ContentHubAssetUpdateStatus = {
  draft: 'draft',
  approved: 'approved',
  archived: 'archived',
} as const;

export type ContentHubAssetUpdateMetadata = { [key: string]: unknown };

export interface ContentHubAssetUpdate {
  /** @minimum 1 */
  expectedVersion: number;
  /**
     * @minLength 1
     * @maxLength 300
     */
  title?: string;
  /**
     * @minLength 1
     * @maxLength 30000
     */
  content?: string;
  status?: ContentHubAssetUpdateStatus;
  metadata?: ContentHubAssetUpdateMetadata;
  publicEnabled?: boolean;
}

export interface ContentHubAssetGenerate {
  kind: ContentHubAssetKind;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  topic: string;
  /** @maxLength 200 */
  format?: string;
  /** @maxLength 10000 */
  sourceFacts?: string;
}

export type ContentHubAsset = ContentHubAssetCreate & ({
  id: string;
  /** @minimum 1 */
  version: number;
  publicEnabled: boolean;
  /** @nullable */
  aiAuditId: string | null;
  /** @nullable */
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
});

export type ContentHubAssetGenerationResult = ContentHubAsset & KnowledgeGroundingResult;

export interface ContentHubAssetList {
  items: ContentHubAsset[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type ContentHubAssetVersionStatus = typeof ContentHubAssetVersionStatus[keyof typeof ContentHubAssetVersionStatus];


export const ContentHubAssetVersionStatus = {
  draft: 'draft',
  approved: 'approved',
  archived: 'archived',
} as const;

export type ContentHubAssetVersionMetadata = { [key: string]: unknown };

export type ContentHubAssetVersionProvenance = { [key: string]: unknown };

export type ContentHubAssetVersionAction = typeof ContentHubAssetVersionAction[keyof typeof ContentHubAssetVersionAction];


export const ContentHubAssetVersionAction = {
  created: 'created',
  generated: 'generated',
  updated: 'updated',
  published: 'published',
  unpublished: 'unpublished',
} as const;

export interface ContentHubAssetVersion {
  id: string;
  assetId: string;
  /** @minimum 1 */
  version: number;
  title: string;
  content: string;
  status: ContentHubAssetVersionStatus;
  metadata: ContentHubAssetVersionMetadata;
  provenance: ContentHubAssetVersionProvenance;
  action: ContentHubAssetVersionAction;
  actor: string;
  createdAt: string;
}

export interface ContentHubAssetVersionList {
  items: ContentHubAssetVersion[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface ContentHubAssetReuse {
  /** @minItems 1 */
  networks: SocialNetwork[];
}

export type ContentHubAssetExportMediaType = typeof ContentHubAssetExportMediaType[keyof typeof ContentHubAssetExportMediaType];


export const ContentHubAssetExportMediaType = {
  'text/markdown': 'text/markdown',
} as const;

export type ContentHubAssetExportProvenance = { [key: string]: unknown };

export interface ContentHubAssetExport {
  filename: string;
  mediaType: ContentHubAssetExportMediaType;
  content: string;
  provenance: ContentHubAssetExportProvenance;
}

export type ContentHubIntelligenceSourceKind = typeof ContentHubIntelligenceSourceKind[keyof typeof ContentHubIntelligenceSourceKind];


export const ContentHubIntelligenceSourceKind = {
  manual_observation: 'manual_observation',
  internal_history: 'internal_history',
  platform_analytics: 'platform_analytics',
  weather: 'weather',
  local_authority: 'local_authority',
  industry_news: 'industry_news',
} as const;

export interface ContentHubIntelligenceSourceCreate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  kind: ContentHubIntelligenceSourceKind;
}

export type ContentHubIntelligenceSourceUpdateStatus = typeof ContentHubIntelligenceSourceUpdateStatus[keyof typeof ContentHubIntelligenceSourceUpdateStatus];


export const ContentHubIntelligenceSourceUpdateStatus = {
  active: 'active',
  paused: 'paused',
} as const;

export interface ContentHubIntelligenceSourceUpdate {
  status: ContentHubIntelligenceSourceUpdateStatus;
}

export type ContentHubIntelligenceSourceStatus = typeof ContentHubIntelligenceSourceStatus[keyof typeof ContentHubIntelligenceSourceStatus];


export const ContentHubIntelligenceSourceStatus = {
  active: 'active',
  paused: 'paused',
  unavailable: 'unavailable',
} as const;

export interface ContentHubIntelligenceSource {
  id: string;
  name: string;
  kind: ContentHubIntelligenceSourceKind;
  status: ContentHubIntelligenceSourceStatus;
  /** @nullable */
  setupMessage: string | null;
  createdBy: string;
  /** @nullable */
  lastIngestedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContentHubIntelligenceSourceList {
  items: ContentHubIntelligenceSource[];
}

export interface ContentHubIntelligenceIngestItem {
  /** @maxLength 500 */
  externalId?: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 5000
     */
  summary: string;
  /** @maxLength 2000 */
  sourceUrl?: string;
  occurredAt?: string;
  /**
     * @minimum 0
     * @maximum 100
     */
  relevance?: number;
}

export interface ContentHubIntelligenceIngest {
  /**
     * @minItems 1
     * @maxItems 50
     */
  items: ContentHubIntelligenceIngestItem[];
}

export interface ContentHubIntelligenceIngestResult {
  /** @minimum 0 */
  created: number;
  /** @minimum 0 */
  duplicates: number;
}

export type ContentHubIntelligenceItemStatus = typeof ContentHubIntelligenceItemStatus[keyof typeof ContentHubIntelligenceItemStatus];


export const ContentHubIntelligenceItemStatus = {
  pending_review: 'pending_review',
  reviewed: 'reviewed',
  converting: 'converting',
  rejected: 'rejected',
  converted: 'converted',
} as const;

export interface ContentHubIntelligenceItem {
  id: string;
  sourceId: string;
  sourceName: string;
  sourceKind: ContentHubIntelligenceSourceKind;
  /** @nullable */
  externalId: string | null;
  fingerprint: string;
  title: string;
  summary: string;
  /** @nullable */
  sourceUrl: string | null;
  /** @nullable */
  occurredAt: string | null;
  /**
     * @minimum 0
     * @maximum 100
     */
  relevance: number;
  status: ContentHubIntelligenceItemStatus;
  /** @nullable */
  reviewedBy: string | null;
  /** @nullable */
  reviewedAt: string | null;
  /** @nullable */
  convertedPostId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContentHubIntelligenceItemList {
  items: ContentHubIntelligenceItem[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type ContentHubIntelligenceReviewDecision = typeof ContentHubIntelligenceReviewDecision[keyof typeof ContentHubIntelligenceReviewDecision];


export const ContentHubIntelligenceReviewDecision = {
  reviewed: 'reviewed',
  rejected: 'rejected',
} as const;

export interface ContentHubIntelligenceReview {
  decision: ContentHubIntelligenceReviewDecision;
}

export interface ContentHubIntelligenceConvert {
  /** @minItems 1 */
  networks: SocialNetwork[];
}

export type ContentHubAdvertisingStatus = typeof ContentHubAdvertisingStatus[keyof typeof ContentHubAdvertisingStatus];


export const ContentHubAdvertisingStatus = {
  draft: 'draft',
  pending_approval: 'pending_approval',
  approved: 'approved',
  launching: 'launching',
  active: 'active',
  paused: 'paused',
  completed: 'completed',
  cancelled: 'cancelled',
  failed: 'failed',
  unavailable: 'unavailable',
  uncertain: 'uncertain',
} as const;

export type ContentHubAdvertisingObjective = typeof ContentHubAdvertisingObjective[keyof typeof ContentHubAdvertisingObjective];


export const ContentHubAdvertisingObjective = {
  awareness: 'awareness',
  traffic: 'traffic',
  engagement: 'engagement',
  leads: 'leads',
  sales: 'sales',
} as const;

export interface ContentHubAdvertisingAudience {
  /**
     * @minItems 1
     * @maxItems 25
     * @items.minLength 2
     * @items.maxLength 120
     */
  locations: string[];
  /**
     * @minimum 18
     * @maximum 65
     */
  ageMin: number;
  /**
     * @minimum 18
     * @maximum 65
     */
  ageMax: number;
  /**
     * @maxItems 25
     * @items.minLength 1
     * @items.maxLength 120
     */
  interests: string[];
}

export type ContentHubAdvertisingCampaignCreateBudgetType = typeof ContentHubAdvertisingCampaignCreateBudgetType[keyof typeof ContentHubAdvertisingCampaignCreateBudgetType];


export const ContentHubAdvertisingCampaignCreateBudgetType = {
  daily: 'daily',
  lifetime: 'lifetime',
} as const;

export type ContentHubAdvertisingCampaignCreateCtaLabel = typeof ContentHubAdvertisingCampaignCreateCtaLabel[keyof typeof ContentHubAdvertisingCampaignCreateCtaLabel];


export const ContentHubAdvertisingCampaignCreateCtaLabel = {
  learn_more: 'learn_more',
  sign_up: 'sign_up',
  book_now: 'book_now',
  contact_us: 'contact_us',
} as const;

export interface ContentHubAdvertisingCampaignCreate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  provider: SocialNetwork;
  connectionId: string;
  /**
     * @minLength 2
     * @maxLength 200
     * @pattern ^[A-Za-z0-9:_-]+$
     */
  adAccountId: string;
  sourcePostId: string;
  objective: ContentHubAdvertisingObjective;
  budgetType: ContentHubAdvertisingCampaignCreateBudgetType;
  /**
     * Budget in minor currency units.
     * @minimum 500
     * @maximum 10000000
     */
  budgetMinor: number;
  /** @pattern ^[A-Z]{3}$ */
  currency: string;
  startsAt: string;
  endsAt: string;
  ctaLabel: ContentHubAdvertisingCampaignCreateCtaLabel;
  /** @pattern ^https:// */
  ctaUrl: string;
  audience: ContentHubAdvertisingAudience;
  /**
     * @minLength 1
     * @maxLength 5000
     */
  creativeText: string;
  /** @nullable */
  mediaAssetId?: string | null;
}

export type ContentHubAdvertisingCampaignUpdateBudgetType = typeof ContentHubAdvertisingCampaignUpdateBudgetType[keyof typeof ContentHubAdvertisingCampaignUpdateBudgetType];


export const ContentHubAdvertisingCampaignUpdateBudgetType = {
  daily: 'daily',
  lifetime: 'lifetime',
} as const;

export type ContentHubAdvertisingCampaignUpdateCtaLabel = typeof ContentHubAdvertisingCampaignUpdateCtaLabel[keyof typeof ContentHubAdvertisingCampaignUpdateCtaLabel];


export const ContentHubAdvertisingCampaignUpdateCtaLabel = {
  learn_more: 'learn_more',
  sign_up: 'sign_up',
  book_now: 'book_now',
  contact_us: 'contact_us',
} as const;

export interface ContentHubAdvertisingCampaignUpdate {
  /** @minimum 1 */
  expectedVersion: number;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @minLength 2
     * @maxLength 200
     * @pattern ^[A-Za-z0-9:_-]+$
     */
  adAccountId?: string;
  objective?: ContentHubAdvertisingObjective;
  budgetType?: ContentHubAdvertisingCampaignUpdateBudgetType;
  /**
     * @minimum 500
     * @maximum 10000000
     */
  budgetMinor?: number;
  /** @pattern ^[A-Z]{3}$ */
  currency?: string;
  startsAt?: string;
  endsAt?: string;
  ctaLabel?: ContentHubAdvertisingCampaignUpdateCtaLabel;
  /** @pattern ^https:// */
  ctaUrl?: string;
  audience?: ContentHubAdvertisingAudience;
  /**
     * @minLength 1
     * @maxLength 5000
     */
  creativeText?: string;
  /** @nullable */
  mediaAssetId?: string | null;
}

export interface ContentHubAdvertisingMetrics {
  /** @minimum 0 */
  impressions?: number;
  /** @minimum 0 */
  reach?: number;
  /** @minimum 0 */
  clicks?: number;
  /** @minimum 0 */
  engagements?: number;
  /** @minimum 0 */
  leads?: number;
  /** @minimum 0 */
  conversions?: number;
  /** @minimum 0 */
  spendMinor?: number;
}

export type ContentHubAdvertisingCampaign = ContentHubAdvertisingCampaignCreate & ({
  id: string;
  /** @minimum 1 */
  sourcePostVersion: number;
  status: ContentHubAdvertisingStatus;
  /** @minimum 1 */
  currentVersion: number;
  /**
     * @minimum 1
     * @nullable
     */
  approvedVersion: number | null;
  /** @nullable */
  approvedBy: string | null;
  /** @nullable */
  approvedAt: string | null;
  /** @nullable */
  launchedBy: string | null;
  /** @nullable */
  launchedAt: string | null;
  /** @nullable */
  providerCampaignId: string | null;
  /** @nullable */
  providerAdSetId: string | null;
  /** @nullable */
  providerAdId: string | null;
  /** @nullable */
  providerStatus: string | null;
  /** @nullable */
  lastSynchronizedAt: string | null;
  /** @nullable */
  lastErrorCode: string | null;
  /** @nullable */
  lastError: string | null;
  latestMetrics: ContentHubAdvertisingMetrics;
  /** @nullable */
  metricsSourceAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
});

export interface ContentHubAdvertisingCampaignList {
  items: ContentHubAdvertisingCampaign[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type ContentHubAdvertisingVersionBudgetType = typeof ContentHubAdvertisingVersionBudgetType[keyof typeof ContentHubAdvertisingVersionBudgetType];


export const ContentHubAdvertisingVersionBudgetType = {
  daily: 'daily',
  lifetime: 'lifetime',
} as const;

export type ContentHubAdvertisingVersionCtaLabel = typeof ContentHubAdvertisingVersionCtaLabel[keyof typeof ContentHubAdvertisingVersionCtaLabel];


export const ContentHubAdvertisingVersionCtaLabel = {
  learn_more: 'learn_more',
  sign_up: 'sign_up',
  book_now: 'book_now',
  contact_us: 'contact_us',
} as const;

export type ContentHubAdvertisingVersionActionProperty = typeof ContentHubAdvertisingVersionActionProperty[keyof typeof ContentHubAdvertisingVersionActionProperty];


export const ContentHubAdvertisingVersionActionProperty = {
  created: 'created',
  edited: 'edited',
  submitted: 'submitted',
  approved: 'approved',
  rejected: 'rejected',
} as const;

export interface ContentHubAdvertisingVersion {
  id: string;
  campaignId: string;
  /** @minimum 1 */
  version: number;
  objective: ContentHubAdvertisingObjective;
  budgetType: ContentHubAdvertisingVersionBudgetType;
  /**
     * @minimum 500
     * @maximum 10000000
     */
  budgetMinor: number;
  currency: string;
  startsAt: string;
  endsAt: string;
  ctaLabel: ContentHubAdvertisingVersionCtaLabel;
  ctaUrl: string;
  audience: ContentHubAdvertisingAudience;
  creativeText: string;
  /** @nullable */
  mediaAssetId: string | null;
  action: ContentHubAdvertisingVersionActionProperty;
  actor: string;
  /** @nullable */
  note: string | null;
  createdAt: string;
}

export interface ContentHubAdvertisingVersionList {
  items: ContentHubAdvertisingVersion[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type ContentHubAdvertisingAttemptOperation = typeof ContentHubAdvertisingAttemptOperation[keyof typeof ContentHubAdvertisingAttemptOperation];


export const ContentHubAdvertisingAttemptOperation = {
  launch: 'launch',
  pause: 'pause',
  resume: 'resume',
  cancel: 'cancel',
  sync: 'sync',
} as const;

export type ContentHubAdvertisingAttemptStatus = typeof ContentHubAdvertisingAttemptStatus[keyof typeof ContentHubAdvertisingAttemptStatus];


export const ContentHubAdvertisingAttemptStatus = {
  started: 'started',
  succeeded: 'succeeded',
  failed: 'failed',
  unavailable: 'unavailable',
  uncertain: 'uncertain',
} as const;

export interface ContentHubAdvertisingAttempt {
  id: string;
  campaignId: string;
  /** @minimum 1 */
  campaignVersion: number;
  operation: ContentHubAdvertisingAttemptOperation;
  requestId: string;
  actor: string;
  status: ContentHubAdvertisingAttemptStatus;
  /** @nullable */
  providerCampaignId: string | null;
  /** @nullable */
  providerAdSetId: string | null;
  /** @nullable */
  providerAdId: string | null;
  /** @nullable */
  providerStatus: string | null;
  /** @nullable */
  errorCode: string | null;
  /** @nullable */
  errorMessage: string | null;
  startedAt: string;
  /** @nullable */
  completedAt: string | null;
  createdAt: string;
}

export interface ContentHubAdvertisingAttemptList {
  items: ContentHubAdvertisingAttempt[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface ContentHubAdvertisingVersionAction {
  /** @minimum 1 */
  expectedVersion: number;
  /**
     * @minLength 3
     * @maxLength 500
     */
  note?: string;
}

export interface ContentHubAdvertisingReject {
  /** @minimum 1 */
  expectedVersion: number;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
}

export interface ContentHubAdvertisingProviderActionWithoutConfirmation {
  requestId: string;
}

export type ContentHubAdvertisingProviderActionConfirmation = typeof ContentHubAdvertisingProviderActionConfirmation[keyof typeof ContentHubAdvertisingProviderActionConfirmation];


export const ContentHubAdvertisingProviderActionConfirmation = {
  LAUNCH: 'LAUNCH',
} as const;

export type ContentHubAdvertisingProviderAction = ContentHubAdvertisingProviderActionWithoutConfirmation & {
  confirmation: ContentHubAdvertisingProviderActionConfirmation;
};

export type ContentHubAdvertisingProviderReadinessProviderSupport = typeof ContentHubAdvertisingProviderReadinessProviderSupport[keyof typeof ContentHubAdvertisingProviderReadinessProviderSupport];


export const ContentHubAdvertisingProviderReadinessProviderSupport = {
  supported: 'supported',
  restricted: 'restricted',
  unsupported: 'unsupported',
} as const;

export type ContentHubAdvertisingProviderReadinessImplementationStatus = typeof ContentHubAdvertisingProviderReadinessImplementationStatus[keyof typeof ContentHubAdvertisingProviderReadinessImplementationStatus];


export const ContentHubAdvertisingProviderReadinessImplementationStatus = {
  available: 'available',
  partial: 'partial',
  planned: 'planned',
} as const;

export type ContentHubAdvertisingProviderReadinessReadiness = typeof ContentHubAdvertisingProviderReadinessReadiness[keyof typeof ContentHubAdvertisingProviderReadinessReadiness];


export const ContentHubAdvertisingProviderReadinessReadiness = {
  ready: 'ready',
  connection_required: 'connection_required',
  provider_approval_required: 'provider_approval_required',
  adapter_unavailable: 'adapter_unavailable',
  unsupported: 'unsupported',
} as const;

export type ContentHubAdvertisingProviderReadinessSetupRequirementsItem = { [key: string]: unknown };

export interface ContentHubAdvertisingProviderReadiness {
  provider: SocialNetwork;
  label: string;
  providerSupport: ContentHubAdvertisingProviderReadinessProviderSupport;
  implementationStatus: ContentHubAdvertisingProviderReadinessImplementationStatus;
  readiness: ContentHubAdvertisingProviderReadinessReadiness;
  /** @nullable */
  connectionId: string | null;
  /** @nullable */
  displayName: string | null;
  grantedCapabilities: string[];
  detail: string;
  setupRequirements: ContentHubAdvertisingProviderReadinessSetupRequirementsItem[];
}

export interface ContentHubAdvertisingProviderList {
  verifiedAt: string;
  generatedAt: string;
  items: ContentHubAdvertisingProviderReadiness[];
}

export interface PublicContentHubAsset {
  id: string;
  title: string;
  kind: ContentHubAssetKind;
  requiresCapture: true;
}

export interface PublicContentHubAssetCapture {
  requestId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  email: string;
  consentToContact: true;
  /** @maxLength 200 */
  website?: string;
}

export interface PublicContentHubAssetDelivery {
  id: string;
  title: string;
  content: string;
  /** @minimum 1 */
  version: number;
  replayed: boolean;
}

export interface ContentHubReviewRequestCreate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  recipientName: string;
  recipientEmail: string;
  /**
     * @minLength 1
     * @maxLength 300
     */
  subject: string;
  /**
     * @minLength 1
     * @maxLength 10000
     */
  message: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  reviewUrl: string;
  /** @nullable */
  contactId?: string | null;
  /** @nullable */
  accountId?: string | null;
}

export type ContentHubReviewRequestStatus = typeof ContentHubReviewRequestStatus[keyof typeof ContentHubReviewRequestStatus];


export const ContentHubReviewRequestStatus = {
  draft: 'draft',
  queued: 'queued',
  sent: 'sent',
  opened: 'opened',
  clicked: 'clicked',
  completed: 'completed',
  failed: 'failed',
} as const;

export type ContentHubReviewRequest = ContentHubReviewRequestCreate & ({
  id: string;
  status: ContentHubReviewRequestStatus;
  /** @nullable */
  sentAt: string | null;
  /** @nullable */
  openedAt: string | null;
  /** @nullable */
  clickedAt: string | null;
  /** @nullable */
  completedAt: string | null;
  /** @minimum 0 */
  sendCount: number;
  /** @minimum 1 */
  maxSends: number;
  /** @nullable */
  nextResendAt: string | null;
  /** @nullable */
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
});

export interface ContentHubReviewRequestList {
  items: ContentHubReviewRequest[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type ContentHubAutomationSettingsUpdateCommentKeywordRulesItem = {
  /**
     * @minLength 1
     * @maxLength 100
     */
  keyword: string;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  reply: string;
  createLead: boolean;
};

export interface ContentHubAutomationSettingsUpdate {
  enabled?: boolean;
  /** @maxLength 100 */
  generateSchedule?: string;
  /** @maxLength 100 */
  publishSchedule?: string;
  /**
     * @minimum 1
     * @maximum 20
     */
  postsPerRun?: number;
  autoApprove?: boolean;
  /** @maxItems 100 */
  commentKeywordRules?: ContentHubAutomationSettingsUpdateCommentKeywordRulesItem[];
}

export type ContentHubAutomationSettings = ContentHubAutomationSettingsUpdate & ({
  id: string;
  /** @nullable */
  lastGeneratedAt: string | null;
  /** @nullable */
  lastPublishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}) & Required<Pick<ContentHubAutomationSettingsUpdate & ({
  id: string;
  /** @nullable */
  lastGeneratedAt: string | null;
  /** @nullable */
  lastPublishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}), 'enabled' | 'generateSchedule' | 'publishSchedule' | 'postsPerRun' | 'autoApprove' | 'commentKeywordRules'>>;

export type ContentHubAutomationRunCategory = typeof ContentHubAutomationRunCategory[keyof typeof ContentHubAutomationRunCategory];


export const ContentHubAutomationRunCategory = {
  generation: 'generation',
  social_publication: 'social_publication',
  inbox_sync: 'inbox_sync',
  messaging: 'messaging',
  email_delivery: 'email_delivery',
  advertising: 'advertising',
} as const;

export type ContentHubAutomationRunStatus = typeof ContentHubAutomationRunStatus[keyof typeof ContentHubAutomationRunStatus];


export const ContentHubAutomationRunStatus = {
  queued: 'queued',
  running: 'running',
  completed: 'completed',
  partial: 'partial',
  skipped: 'skipped',
  blocked: 'blocked',
  failed: 'failed',
  unavailable: 'unavailable',
  uncertain: 'uncertain',
  cancelled: 'cancelled',
} as const;

export interface ContentHubAutomationRun {
  id: string;
  category: ContentHubAutomationRunCategory;
  status: ContentHubAutomationRunStatus;
  summary: string;
  network: SocialNetwork | null;
  occurredAt: string;
  /** @nullable */
  sourceTimestamp: string | null;
  /** @nullable */
  errorCode: string | null;
  /** @nullable */
  errorMessage: string | null;
}

export interface ContentHubAutomationRunList {
  items: ContentHubAutomationRun[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type ContentHubMetricAvailability = typeof ContentHubMetricAvailability[keyof typeof ContentHubMetricAvailability];


export const ContentHubMetricAvailability = {
  measured: 'measured',
  unmeasured: 'unmeasured',
  unavailable: 'unavailable',
  unsupported: 'unsupported',
} as const;

export interface ContentHubReportMetricSet {
  /** @nullable */
  impressions: number | null;
  /** @nullable */
  reach: number | null;
  /** @nullable */
  reactions: number | null;
  /** @nullable */
  comments: number | null;
  /** @nullable */
  shares: number | null;
  /** @nullable */
  clicks: number | null;
  /** @nullable */
  views: number | null;
}

export interface ContentHubReportMetricStates {
  impressions: ContentHubMetricAvailability;
  reach: ContentHubMetricAvailability;
  reactions: ContentHubMetricAvailability;
  comments: ContentHubMetricAvailability;
  shares: ContentHubMetricAvailability;
  clicks: ContentHubMetricAvailability;
  views: ContentHubMetricAvailability;
}

export interface ContentHubReportProvider {
  network: SocialNetwork;
  status: ContentHubMetricAvailability;
  detail: string;
  /** @nullable */
  sourceTimestamp: string | null;
  /** @minimum 0 */
  publishedTargets: number;
  metrics: ContentHubReportMetricSet;
  metricStates: ContentHubReportMetricStates;
}

export interface ContentHubReportInternal {
  /** @minimum 0 */
  postsCreated: number;
  /** @minimum 0 */
  publishedTargets: number;
  /** @minimum 0 */
  inboxItems: number;
  /** @minimum 0 */
  socialLeads: number;
  /** @minimum 0 */
  bookings: number;
  /** @minimum 0 */
  emailsSent: number;
  /** @minimum 0 */
  emailsSuppressed: number;
  /** @minimum 0 */
  advertisingCampaigns: number;
  /** @minimum 0 */
  advertisingConversions: number;
}

export type ContentHubReportOutcomeArea = typeof ContentHubReportOutcomeArea[keyof typeof ContentHubReportOutcomeArea];


export const ContentHubReportOutcomeArea = {
  social_publication: 'social_publication',
  messaging: 'messaging',
  email_delivery: 'email_delivery',
  advertising: 'advertising',
} as const;

export interface ContentHubReportOutcome {
  area: ContentHubReportOutcomeArea;
  status: string;
  /** @minimum 0 */
  count: number;
}

export interface ContentHubReport {
  from: string;
  to: string;
  networks: SocialNetwork[];
  generatedAt: string;
  internal: ContentHubReportInternal;
  providers: ContentHubReportProvider[];
  outcomes: ContentHubReportOutcome[];
}

export interface ContentHubMetric {
  key: string;
  label: string;
  value: number;
  detail: string;
}

export interface ContentHubOverview {
  metrics: ContentHubMetric[];
  recentPosts: SocialPost[];
  upcomingBookings: ContentHubBooking[];
  generatedAt: string;
}

export type ContentHubGoLiveCheckStatus = typeof ContentHubGoLiveCheckStatus[keyof typeof ContentHubGoLiveCheckStatus];


export const ContentHubGoLiveCheckStatus = {
  ready: 'ready',
  blocked: 'blocked',
  unavailable: 'unavailable',
  manual: 'manual',
} as const;

export interface ContentHubGoLiveCheck {
  key: string;
  section: string;
  label: string;
  passed: boolean;
  status: ContentHubGoLiveCheckStatus;
  detail: string;
  href: string;
  manual: boolean;
  /** @nullable */
  observedAt: string | null;
}

export interface ContentHubGoLiveChecklist {
  checks: ContentHubGoLiveCheck[];
  passed: number;
  total: number;
  percent: number;
}

export type ResearchThreadStatus = typeof ResearchThreadStatus[keyof typeof ResearchThreadStatus];


export const ResearchThreadStatus = {
  active: 'active',
  archived: 'archived',
} as const;

export interface ResearchThread {
  id: string;
  title: string;
  status: ResearchThreadStatus;
  /** @minimum 1 */
  revision: number;
  /** @nullable */
  createdByAppUserId: string | null;
  /** @nullable */
  updatedByAppUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ResearchThreadPage {
  items: ResearchThread[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface CreateResearchThread {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
}

export interface UpdateResearchThread {
  /** @minimum 1 */
  baseRevision: number;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
  status?: ResearchThreadStatus;
}

export type ResearchDeleteConfirmationConfirmation = typeof ResearchDeleteConfirmationConfirmation[keyof typeof ResearchDeleteConfirmationConfirmation];


export const ResearchDeleteConfirmationConfirmation = {
  DELETE: 'DELETE',
} as const;

export interface ResearchDeleteConfirmation {
  confirmation: ResearchDeleteConfirmationConfirmation;
}

export type ResearchMessageRole = typeof ResearchMessageRole[keyof typeof ResearchMessageRole];


export const ResearchMessageRole = {
  user: 'user',
  assistant: 'assistant',
  system: 'system',
} as const;

export type ResearchMessageState = typeof ResearchMessageState[keyof typeof ResearchMessageState];


export const ResearchMessageState = {
  complete: 'complete',
  incomplete: 'incomplete',
  failed: 'failed',
  cancelled: 'cancelled',
} as const;

export interface ResearchMessage {
  id: string;
  threadId: string;
  role: ResearchMessageRole;
  content: string;
  /** @nullable */
  authorAppUserId: string | null;
  authorDisplayName: string;
  state: ResearchMessageState;
  evidence: ResearchEvidence[];
  createdAt: string;
}

export interface ResearchMessagePage {
  items: ResearchMessage[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type ResearchAttachmentProcessingStatus = typeof ResearchAttachmentProcessingStatus[keyof typeof ResearchAttachmentProcessingStatus];


export const ResearchAttachmentProcessingStatus = {
  pending: 'pending',
  processing: 'processing',
  ready: 'ready',
  failed: 'failed',
  unsupported: 'unsupported',
} as const;

export interface ResearchAttachment {
  id: string;
  threadId: string;
  fileId: string;
  filename: string;
  contentType: string;
  /**
     * @minimum 0
     * @maximum 20971520
     */
  sizeBytes: number;
  processingStatus: ResearchAttachmentProcessingStatus;
  /** @nullable */
  failureCode: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ResearchAttachmentPage {
  items: ResearchAttachment[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type ResearchWebSearchState = typeof ResearchWebSearchState[keyof typeof ResearchWebSearchState];


export const ResearchWebSearchState = {
  disabled: 'disabled',
  approval_required: 'approval_required',
  misconfigured: 'misconfigured',
  quota_exhausted: 'quota_exhausted',
  timeout: 'timeout',
  failed: 'failed',
  ready: 'ready',
} as const;

export type ResearchWebSearchLimitsSearchDepth = typeof ResearchWebSearchLimitsSearchDepth[keyof typeof ResearchWebSearchLimitsSearchDepth];


export const ResearchWebSearchLimitsSearchDepth = {
  basic: 'basic',
} as const;

export interface ResearchWebSearchLimits {
  searchDepth: ResearchWebSearchLimitsSearchDepth;
  /**
     * @minimum 1
     * @maximum 5
     */
  maxResultsPerQuery: number;
  /**
     * @minimum 1
     * @maximum 3
     */
  maxSearchesPerTurn: number;
  /**
     * @minimum 1
     * @maximum 1000
     */
  monthlyCreditCap: number;
  /** @minimum 0 */
  creditsUsedThisMonth: number;
}

export type ResearchWebSearchStatusProvider = typeof ResearchWebSearchStatusProvider[keyof typeof ResearchWebSearchStatusProvider];


export const ResearchWebSearchStatusProvider = {
  azure_web_search: 'azure_web_search',
} as const;

export interface ResearchWebSearchStatus {
  provider: ResearchWebSearchStatusProvider;
  state: ResearchWebSearchState;
  approved: boolean;
  configured: boolean;
  enabled: boolean;
  liveSmokeTested: boolean;
  message: string;
  limits: ResearchWebSearchLimits;
  pricingCheckedAt: string;
  pricingUrl: string;
  searchApiUrl: string;
}

export type ResearchRunStatus = typeof ResearchRunStatus[keyof typeof ResearchRunStatus];


export const ResearchRunStatus = {
  queued: 'queued',
  running: 'running',
  completed: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
} as const;

export interface ResearchRun {
  id: string;
  threadId: string;
  clientRequestId: string;
  /** @nullable */
  retryOfRunId: string | null;
  /** @nullable */
  questionMessageId: string | null;
  /** @nullable */
  responseMessageId: string | null;
  status: ResearchRunStatus;
  /** @nullable */
  errorCode: string | null;
  /** @nullable */
  startedAt: string | null;
  /** @nullable */
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ResearchRunPage {
  items: ResearchRun[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type CreateResearchRunSourceMode = typeof CreateResearchRunSourceMode[keyof typeof CreateResearchRunSourceMode];


export const CreateResearchRunSourceMode = {
  entity_only: 'entity_only',
  entity_and_web: 'entity_and_web',
} as const;

export interface CreateResearchRun {
  clientRequestId: string;
  /**
     * @minLength 1
     * @maxLength 20000
     */
  content: string;
  /** @maxItems 10 */
  attachmentIds: string[];
  sourceMode: CreateResearchRunSourceMode;
  /**
     * Explicit public-only terms sent to the approved web provider. Private context is never derived into these queries.
     * @maxItems 3
     * @items.minLength 3
     * @items.maxLength 300
     */
  publicSearchQueries: string[];
}

export interface RetryResearchRun {
  clientRequestId: string;
}

export type ResearchRunStreamEventEvent = typeof ResearchRunStreamEventEvent[keyof typeof ResearchRunStreamEventEvent];


export const ResearchRunStreamEventEvent = {
  started: 'started',
  delta: 'delta',
  evidence: 'evidence',
  completed: 'completed',
  error: 'error',
  cancelled: 'cancelled',
  keepalive: 'keepalive',
} as const;

export interface ResearchRunStreamEvent {
  event: ResearchRunStreamEventEvent;
  runId: string;
  /** @minimum 0 */
  sequence: number;
  delta?: string;
  message?: ResearchMessage | null;
  evidence?: ResearchEvidence | null;
  error?: string;
  code?: string;
}

export interface ResearchPrompt {
  id: string;
  title: string;
  prompt: string;
  category: string;
  sortOrder: number;
  active: boolean;
  /** @minimum 1 */
  revision: number;
  createdAt: string;
  updatedAt: string;
}

export type ResearchGroundingSourceType = typeof ResearchGroundingSourceType[keyof typeof ResearchGroundingSourceType];


export const ResearchGroundingSourceType = {
  crm: 'crm',
  knowledge: 'knowledge',
} as const;

export type ResearchGroundingSourceState = typeof ResearchGroundingSourceState[keyof typeof ResearchGroundingSourceState];


export const ResearchGroundingSourceState = {
  eligible: 'eligible',
  excluded: 'excluded',
  unavailable: 'unavailable',
} as const;

export interface ResearchGroundingSource {
  /**
     * @minLength 1
     * @maxLength 200
     */
  key: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  title: string;
  type: ResearchGroundingSourceType;
  state: ResearchGroundingSourceState;
  /** @nullable */
  inclusion: boolean | null;
  /**
     * @minimum 1
     * @maximum 5
     * @nullable
     */
  priority: number | null;
  inheritedPriority: number;
  primary: boolean;
  /**
     * @minLength 1
     * @maxLength 200
     */
  revision: string;
}

export interface ResearchGroundingPage {
  items: ResearchGroundingSource[];
  total: number;
  limit: number;
  offset: number;
  /** @pattern ^[a-f0-9]{64}$ */
  sourceVersion: string;
}

export interface ResearchGroundingSave {
  requestId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  expectedSource: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  key: string;
  /** @nullable */
  inclusion: boolean | null;
  /**
     * @minimum 1
     * @maximum 5
     * @nullable
     */
  priority: number | null;
}

export type ResearchControlReceiptKind = typeof ResearchControlReceiptKind[keyof typeof ResearchControlReceiptKind];


export const ResearchControlReceiptKind = {
  grounding: 'grounding',
  prompts: 'prompts',
} as const;

export interface ResearchControlReceipt {
  requestId: string;
  kind: ResearchControlReceiptKind;
  replayed: boolean;
  currentMatch: boolean;
  /** @pattern ^[a-f0-9]{64}$ */
  savedVersion: string;
}

export interface ResearchPromptTransferItem {
  /**
     * @minLength 1
     * @maxLength 120
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 8000
     */
  prompt: string;
  /**
     * @minLength 1
     * @maxLength 60
     */
  category: string;
}

export type ResearchPromptTransferFormat = typeof ResearchPromptTransferFormat[keyof typeof ResearchPromptTransferFormat];


export const ResearchPromptTransferFormat = {
  'research-prompts-v1': 'research-prompts-v1',
} as const;

export interface ResearchPromptTransfer {
  format: ResearchPromptTransferFormat;
  /**
     * @minItems 1
     * @maxItems 100
     */
  items: ResearchPromptTransferItem[];
}

export interface ResearchPromptTransferPreview {
  /** @pattern ^[a-f0-9]{64}$ */
  version: string;
  /**
     * @items.minLength 1
     * @items.maxLength 120
     */
  create: string[];
  /**
     * @items.minLength 1
     * @items.maxLength 120
     */
  replace: string[];
  /**
     * @items.minLength 1
     * @items.maxLength 120
     */
  unchanged: string[];
}

export interface ResearchPromptTransferApply {
  requestId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  expectedVersion: string;
  transfer: ResearchPromptTransfer;
}

export interface ResearchPromptPage {
  items: ResearchPrompt[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface ResearchPromptWrite {
  /**
     * @minLength 1
     * @maxLength 120
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 8000
     */
  prompt: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  category: string;
  /**
     * @minimum 0
     * @maximum 10000
     */
  sortOrder: number;
  active: boolean;
}

export type ResearchPromptUpdate = ResearchPromptWrite & {
  /** @minimum 1 */
  baseRevision: number;
};

export type ResearchPresenceView = typeof ResearchPresenceView[keyof typeof ResearchPresenceView];


export const ResearchPresenceView = {
  research: 'research',
  documents: 'documents',
} as const;

export interface ResearchPresence {
  appUserId: string;
  displayName: string;
  /** @nullable */
  activeThreadId: string | null;
  view: ResearchPresenceView;
  lastHeartbeatAt: string;
  expiresAt: string;
}

export interface ResearchPresenceList {
  /** @maxItems 200 */
  items: ResearchPresence[];
  /** @minimum 1 */
  expiresInSeconds: number;
}

export type ResearchPresenceHeartbeatView = typeof ResearchPresenceHeartbeatView[keyof typeof ResearchPresenceHeartbeatView];


export const ResearchPresenceHeartbeatView = {
  research: 'research',
  documents: 'documents',
} as const;

export interface ResearchPresenceHeartbeat {
  /** @nullable */
  activeThreadId?: string | null;
  view: ResearchPresenceHeartbeatView;
}

export type ResearchDocumentStatus = typeof ResearchDocumentStatus[keyof typeof ResearchDocumentStatus];


export const ResearchDocumentStatus = {
  active: 'active',
  archived: 'archived',
} as const;

export interface ResearchDocument {
  id: string;
  title: string;
  markdown: string;
  status: ResearchDocumentStatus;
  /** @minimum 1 */
  revision: number;
  /** @nullable */
  createdByAppUserId: string | null;
  /** @nullable */
  updatedByAppUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export type ResearchDocumentSummaryStatus = typeof ResearchDocumentSummaryStatus[keyof typeof ResearchDocumentSummaryStatus];


export const ResearchDocumentSummaryStatus = {
  active: 'active',
  archived: 'archived',
} as const;

export interface ResearchDocumentSummary {
  id: string;
  title: string;
  status: ResearchDocumentSummaryStatus;
  /** @minimum 1 */
  revision: number;
  /** @nullable */
  createdByAppUserId: string | null;
  /** @nullable */
  updatedByAppUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ResearchDocumentPage {
  items: ResearchDocumentSummary[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type ResearchDocumentDetail = ResearchDocument & {
  threads: ResearchThread[];
};

export interface CreateResearchDocument {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /** @maxLength 1000000 */
  markdown: string;
  /** @nullable */
  threadId?: string | null;
  /** @maxItems 100 */
  sourceMessageIds?: string[];
}

export type SaveResearchDocumentStatus = typeof SaveResearchDocumentStatus[keyof typeof SaveResearchDocumentStatus];


export const SaveResearchDocumentStatus = {
  active: 'active',
  archived: 'archived',
} as const;

export type SaveResearchDocumentAction = typeof SaveResearchDocumentAction[keyof typeof SaveResearchDocumentAction];


export const SaveResearchDocumentAction = {
  saved: 'saved',
} as const;

export interface SaveResearchDocument {
  /** @minimum 1 */
  baseRevision: number;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /** @maxLength 1000000 */
  markdown: string;
  status?: SaveResearchDocumentStatus;
  action: SaveResearchDocumentAction;
}

export interface AppendResearchDocument {
  /** @minimum 1 */
  baseRevision: number;
  /**
     * @minLength 1
     * @maxLength 200
     */
  heading?: string;
  /**
     * @minLength 1
     * @maxLength 1000000
     */
  markdown: string;
}

export type ResearchDocumentVersionAction = typeof ResearchDocumentVersionAction[keyof typeof ResearchDocumentVersionAction];


export const ResearchDocumentVersionAction = {
  created: 'created',
  saved: 'saved',
  appended: 'appended',
  proposal_applied: 'proposal_applied',
  restored: 'restored',
} as const;

export interface ResearchDocumentVersion {
  id: string;
  documentId: string;
  /** @minimum 1 */
  revision: number;
  title: string;
  markdown: string;
  action: ResearchDocumentVersionAction;
  /** @nullable */
  actorAppUserId: string | null;
  actorDisplayName: string;
  /** @nullable */
  restoredFromVersionId: string | null;
  /** @maxItems 32 */
  evidence: ResearchEvidence[];
  createdAt: string;
}

export interface ResearchDocumentVersionPage {
  items: ResearchDocumentVersion[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface ResearchVersionComparison {
  from: ResearchDocumentVersion;
  to: ResearchDocumentVersion;
}

export interface RestoreResearchDocument {
  /** @minimum 1 */
  baseRevision: number;
}

export interface ResearchDocumentThreadLink {
  threadId: string;
}

export interface ResearchDocumentThreadLinkResult {
  id: string;
  documentId: string;
  threadId: string;
  createdAt: string;
}

export type ResearchProposalAction = typeof ResearchProposalAction[keyof typeof ResearchProposalAction];


export const ResearchProposalAction = {
  generate: 'generate',
  rewrite: 'rewrite',
  shorten: 'shorten',
  expand: 'expand',
  change_tone: 'change_tone',
  tidy: 'tidy',
  append: 'append',
} as const;

export type ResearchProposalStatus = typeof ResearchProposalStatus[keyof typeof ResearchProposalStatus];


export const ResearchProposalStatus = {
  pending: 'pending',
  accepted: 'accepted',
  rejected: 'rejected',
  expired: 'expired',
} as const;

export interface ResearchProposal {
  id: string;
  documentId: string;
  /** @nullable */
  runId: string | null;
  /** @minimum 1 */
  baseRevision: number;
  action: ResearchProposalAction;
  /**
     * @minimum 0
     * @nullable
     */
  selectionStart: number | null;
  /**
     * @minimum 1
     * @nullable
     */
  selectionEnd: number | null;
  /**
     * @nullable
     * @pattern ^[a-f0-9]{64}$
     */
  selectedTextSha256: string | null;
  proposedMarkdown: string;
  status: ResearchProposalStatus;
  evidence: ResearchEvidence[];
  createdAt: string;
  /** @nullable */
  resolvedAt: string | null;
}

export interface ResearchProposalPage {
  items: ResearchProposal[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface CreateResearchProposal {
  /** @minimum 1 */
  baseRevision: number;
  action: ResearchProposalAction;
  /** @maxLength 8000 */
  instruction?: string;
  /** @minimum 0 */
  selectionStart?: number;
  /** @minimum 1 */
  selectionEnd?: number;
  /** @pattern ^[a-f0-9]{64}$ */
  selectedTextSha256?: string;
  /** @maxItems 100 */
  sourceMessageIds?: string[];
}

export interface AcceptResearchProposal {
  /** @minimum 1 */
  baseRevision: number;
}

export interface Error {
  error: string;
  code?: string;
}

export type ResearchRevisionConflict = Error & ({
  /** @minimum 1 */
  latestRevision: number;
  /** @nullable */
  latestVersionId?: string | null;
});

export type GeographicRecordType = typeof GeographicRecordType[keyof typeof GeographicRecordType];


export const GeographicRecordType = {
  account: 'account',
  contact: 'contact',
  enquiry: 'enquiry',
  opportunity: 'opportunity',
} as const;

export type GeographicProviderCapabilityId = typeof GeographicProviderCapabilityId[keyof typeof GeographicProviderCapabilityId];


export const GeographicProviderCapabilityId = {
  openstreetmap: 'openstreetmap',
  azure_maps: 'azure_maps',
  manual: 'manual',
  azure_geocoding: 'azure_geocoding',
} as const;

export type GeographicProviderCapabilityKind = typeof GeographicProviderCapabilityKind[keyof typeof GeographicProviderCapabilityKind];


export const GeographicProviderCapabilityKind = {
  basemap: 'basemap',
  geocoder: 'geocoder',
} as const;

export type GeographicProviderCapabilityStatus = typeof GeographicProviderCapabilityStatus[keyof typeof GeographicProviderCapabilityStatus];


export const GeographicProviderCapabilityStatus = {
  ready: 'ready',
  setup_required: 'setup_required',
  unavailable: 'unavailable',
} as const;

export interface GeographicProviderCapability {
  id: GeographicProviderCapabilityId;
  label: string;
  kind: GeographicProviderCapabilityKind;
  ready: boolean;
  status: GeographicProviderCapabilityStatus;
  message?: string;
  attribution: string;
}

export type GeographicProviderListDefaultBasemap = typeof GeographicProviderListDefaultBasemap[keyof typeof GeographicProviderListDefaultBasemap];


export const GeographicProviderListDefaultBasemap = {
  openstreetmap: 'openstreetmap',
} as const;

export interface GeographicProviderList {
  basemaps: GeographicProviderCapability[];
  geocoders: GeographicProviderCapability[];
  defaultBasemap: GeographicProviderListDefaultBasemap;
}

export type GeographicLayerSourceType = typeof GeographicLayerSourceType[keyof typeof GeographicLayerSourceType];


export const GeographicLayerSourceType = {
  custom: 'custom',
  crm: 'crm',
  csv: 'csv',
  geojson: 'geojson',
  kml: 'kml',
  shapefile: 'shapefile',
} as const;

export interface GeographicLayer {
  id: string;
  name: string;
  /** @nullable */
  description?: string | null;
  sourceType: GeographicLayerSourceType;
  visibleByDefault: boolean;
  /** @minimum 1 */
  version: number;
  /** @minimum 0 */
  featureCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface GeographicLayerPage {
  items: GeographicLayer[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface GeographicLayerWrite {
  /**
     * @minLength 1
     * @maxLength 120
     */
  name: string;
  /**
     * @maxLength 2000
     * @nullable
     */
  description?: string | null;
  visibleByDefault?: boolean;
}

export interface GeographicLayerUpdate {
  /** @minimum 1 */
  expectedVersion: number;
  /**
     * @minLength 1
     * @maxLength 120
     */
  name?: string;
  /**
     * @maxLength 2000
     * @nullable
     */
  description?: string | null;
  visibleByDefault?: boolean;
}

export type GeographicPointGeometryType = typeof GeographicPointGeometryType[keyof typeof GeographicPointGeometryType];


export const GeographicPointGeometryType = {
  Point: 'Point',
} as const;

export interface GeographicPointGeometry {
  type: GeographicPointGeometryType;
  /**
     * @minItems 2
     * @maxItems 2
     */
  coordinates: number[];
}

export type GeographicLineGeometryType = typeof GeographicLineGeometryType[keyof typeof GeographicLineGeometryType];


export const GeographicLineGeometryType = {
  LineString: 'LineString',
} as const;

export interface GeographicLineGeometry {
  type: GeographicLineGeometryType;
  /**
     * @minItems 2
     * @maxItems 10000
     * @items.minItems 2
     * @items.maxItems 2
     */
  coordinates: number[][];
}

export type GeographicPolygonGeometryType = typeof GeographicPolygonGeometryType[keyof typeof GeographicPolygonGeometryType];


export const GeographicPolygonGeometryType = {
  Polygon: 'Polygon',
} as const;

export interface GeographicPolygonGeometry {
  type: GeographicPolygonGeometryType;
  /**
     * @minItems 1
     * @maxItems 100
     * @items.minItems 4
     * @items.maxItems 10000
     * @items.items.minItems 2
     * @items.items.maxItems 2
     */
  coordinates: number[][][];
}

export type GeographicGeometry = GeographicPointGeometry | GeographicLineGeometry | GeographicPolygonGeometry;

export interface GeographicFeatureLink {
  recordType: GeographicRecordType;
  recordId: string;
}

export type GeographicFeatureProperties = { [key: string]: unknown };

export interface GeographicFeature {
  id: string;
  layerId: string;
  label: string;
  /** @nullable */
  description?: string | null;
  geometry: GeographicGeometry;
  properties: GeographicFeatureProperties;
  /** @maxItems 20 */
  links: GeographicFeatureLink[];
  /** @nullable */
  sourceFeatureId?: string | null;
  /** @minimum 1 */
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface GeographicFeaturePage {
  items: GeographicFeature[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 500
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
  bounded: boolean;
}

export type GeographicFeatureWriteProperties = { [key: string]: unknown };

export interface GeographicFeatureWrite {
  layerId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  label: string;
  /**
     * @maxLength 5000
     * @nullable
     */
  description?: string | null;
  geometry: GeographicGeometry;
  properties?: GeographicFeatureWriteProperties;
  /** @maxItems 20 */
  links?: GeographicFeatureLink[];
}

export type GeographicFeatureUpdate = GeographicFeatureWrite & {
  /** @minimum 1 */
  expectedVersion: number;
};

export type GeographicAddressResolutionRecordType = typeof GeographicAddressResolutionRecordType[keyof typeof GeographicAddressResolutionRecordType];


export const GeographicAddressResolutionRecordType = {
  account: 'account',
  contact: 'contact',
} as const;

export type GeographicAddressResolutionStatus = typeof GeographicAddressResolutionStatus[keyof typeof GeographicAddressResolutionStatus];


export const GeographicAddressResolutionStatus = {
  unresolved: 'unresolved',
  ambiguous: 'ambiguous',
  resolved: 'resolved',
  corrected: 'corrected',
  failed: 'failed',
  stale: 'stale',
} as const;

export type GeographicAddressResolutionProvider = typeof GeographicAddressResolutionProvider[keyof typeof GeographicAddressResolutionProvider];


export const GeographicAddressResolutionProvider = {
  manual: 'manual',
  azure: 'azure',
} as const;

export type GeographicAddressResolutionCandidatesItem = {
  /**
     * @minItems 2
     * @maxItems 2
     */
  coordinate: number[];
  label: string;
  /**
     * @minimum 0
     * @maximum 1
     */
  confidence?: number;
};

export interface GeographicAddressResolution {
  recordType: GeographicAddressResolutionRecordType;
  recordId: string;
  address: string;
  /** @pattern ^[a-f0-9]{64}$ */
  addressFingerprint: string;
  status: GeographicAddressResolutionStatus;
  provider: GeographicAddressResolutionProvider;
  /**
     * @minItems 2
     * @maxItems 2
     * @nullable
     */
  coordinate?: number[] | null;
  /** @nullable */
  precision?: string | null;
  /**
     * @minimum 0
     * @maximum 1
     * @nullable
     */
  confidence?: number | null;
  /** @maxItems 10 */
  candidates: GeographicAddressResolutionCandidatesItem[];
  /** @minimum 1 */
  version: number;
  updatedAt: string;
}

export interface GeographicAddressCorrection {
  /**
     * @minItems 2
     * @maxItems 2
     */
  coordinate: number[];
  /** @minimum 0 */
  expectedVersion: number;
}

export type GeographicAddressLookupRequestRecordType = typeof GeographicAddressLookupRequestRecordType[keyof typeof GeographicAddressLookupRequestRecordType];


export const GeographicAddressLookupRequestRecordType = {
  account: 'account',
  contact: 'contact',
} as const;

export interface GeographicAddressLookupRequest {
  recordType: GeographicAddressLookupRequestRecordType;
  /**
     * @minItems 1
     * @maxItems 25
     */
  recordIds: string[];
  idempotencyKey: string;
}

export type GeographicAddressLookupJobRecordType = typeof GeographicAddressLookupJobRecordType[keyof typeof GeographicAddressLookupJobRecordType];


export const GeographicAddressLookupJobRecordType = {
  account: 'account',
  contact: 'contact',
} as const;

export type GeographicAddressLookupJobStatus = typeof GeographicAddressLookupJobStatus[keyof typeof GeographicAddressLookupJobStatus];


export const GeographicAddressLookupJobStatus = {
  queued: 'queued',
  running: 'running',
  completed: 'completed',
  cancelled: 'cancelled',
  failed: 'failed',
} as const;

export interface GeographicAddressLookupJob {
  id: string;
  recordType: GeographicAddressLookupJobRecordType;
  status: GeographicAddressLookupJobStatus;
  /** @minimum 0 */
  cursor: number;
  /**
     * @minimum 1
     * @maximum 25
     */
  total: number;
  /** @minimum 0 */
  attempted: number;
  /** @minimum 0 */
  resolved: number;
  /** @minimum 0 */
  ambiguous: number;
  /** @minimum 0 */
  failed: number;
  /** @nullable */
  error?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type GeographicImportFormat = typeof GeographicImportFormat[keyof typeof GeographicImportFormat];


export const GeographicImportFormat = {
  csv: 'csv',
  geojson: 'geojson',
  kml: 'kml',
  shapefile: 'shapefile',
} as const;

export type GeographicImportStatus = typeof GeographicImportStatus[keyof typeof GeographicImportStatus];


export const GeographicImportStatus = {
  previewed: 'previewed',
  committed: 'committed',
  failed: 'failed',
  cancelled: 'cancelled',
} as const;

export type GeographicImportSummary = {
  /** @minimum 0 */
  valid: number;
  /** @minimum 0 */
  invalid: number;
  /** @minimum 0 */
  unresolved: number;
  /** @maxItems 100 */
  errors?: string[];
};

export interface GeographicImport {
  id: string;
  /** @nullable */
  layerId?: string | null;
  filename: string;
  format: GeographicImportFormat;
  status: GeographicImportStatus;
  summary: GeographicImportSummary;
  /** @nullable */
  error?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type GeographicImportPreview = GeographicImport & {
  /** @maxItems 200 */
  previewFeatures: GeographicFeature[];
};

export type SalesFloorTwinStageId = typeof SalesFloorTwinStageId[keyof typeof SalesFloorTwinStageId];


export const SalesFloorTwinStageId = {
  intake: 'intake',
  triage: 'triage',
  review: 'review',
  response: 'response',
  quote: 'quote',
  quoteSent: 'quoteSent',
  followUp: 'followUp',
  won: 'won',
  lost: 'lost',
} as const;

export type SalesFloorTwinChannel = typeof SalesFloorTwinChannel[keyof typeof SalesFloorTwinChannel];


export const SalesFloorTwinChannel = {
  social: 'social',
  email: 'email',
  web: 'web',
  phone: 'phone',
} as const;

export type SalesFloorTwinStageWorkedBy = typeof SalesFloorTwinStageWorkedBy[keyof typeof SalesFloorTwinStageWorkedBy];


export const SalesFloorTwinStageWorkedBy = {
  people: 'people',
  agents: 'agents',
  both: 'both',
} as const;

export type SalesFloorTwinStageApprovalRuleKind = typeof SalesFloorTwinStageApprovalRuleKind[keyof typeof SalesFloorTwinStageApprovalRuleKind];


export const SalesFloorTwinStageApprovalRuleKind = {
  'value-threshold': 'value-threshold',
} as const;

export type SalesFloorTwinStageApprovalRuleApproverRole = typeof SalesFloorTwinStageApprovalRuleApproverRole[keyof typeof SalesFloorTwinStageApprovalRuleApproverRole];


export const SalesFloorTwinStageApprovalRuleApproverRole = {
  'sales-manager': 'sales-manager',
} as const;

export type SalesFloorTwinStageApprovalRule = {
  kind: SalesFloorTwinStageApprovalRuleKind;
  approverRole: SalesFloorTwinStageApprovalRuleApproverRole;
  description: string;
};

export interface SalesFloorTwinStage {
  id: SalesFloorTwinStageId;
  name: string;
  shortLabel: string;
  description: string;
  /**
     * @minItems 3
     * @maxItems 3
     */
  position: number[];
  /**
     * @minItems 2
     * @maxItems 2
     * @items.exclusiveMinimum 0
     */
  footprint: number[];
  /** @exclusiveMinimum 0 */
  defaultCycleSeconds: number;
  workedBy: SalesFloorTwinStageWorkedBy;
  approvalRule?: SalesFloorTwinStageApprovalRule;
}

export type SalesFloorTwinWorkerKind = typeof SalesFloorTwinWorkerKind[keyof typeof SalesFloorTwinWorkerKind];


export const SalesFloorTwinWorkerKind = {
  person: 'person',
  agent: 'agent',
} as const;

export type SalesFloorTwinWorkerRole = typeof SalesFloorTwinWorkerRole[keyof typeof SalesFloorTwinWorkerRole];


export const SalesFloorTwinWorkerRole = {
  'sales-coordinator': 'sales-coordinator',
  'sales-manager': 'sales-manager',
  'events-specialist': 'events-specialist',
  'intake-triage-agent': 'intake-triage-agent',
  'response-drafting-agent': 'response-drafting-agent',
  'pricing-agent': 'pricing-agent',
  'follow-up-agent': 'follow-up-agent',
} as const;

export interface SalesFloorTwinWorker {
  /**
     * @minLength 4
     * @maxLength 128
     * @pattern ^[A-Za-z0-9._:-]+$
     */
  id: string;
  name: string;
  kind: SalesFloorTwinWorkerKind;
  role: SalesFloorTwinWorkerRole;
  homeStage: SalesFloorTwinStageId;
  /**
     * @minItems 3
     * @maxItems 3
     */
  position: number[];
  /** @minItems 1 */
  canWork: SalesFloorTwinStageId[];
  /** @minimum 1 */
  coordinatorNumber?: number;
}

export type SalesFloorTwinDefinitionId = typeof SalesFloorTwinDefinitionId[keyof typeof SalesFloorTwinDefinitionId];


export const SalesFloorTwinDefinitionId = {
  'pmf-sales-floor': 'pmf-sales-floor',
} as const;

export type SalesFloorTwinDefinitionChannelsItem = {
  id: SalesFloorTwinChannel;
  name: string;
  /**
     * @minItems 3
     * @maxItems 3
     */
  position: number[];
};

export type SalesFloorTwinDefinitionFlowsItemKind = typeof SalesFloorTwinDefinitionFlowsItemKind[keyof typeof SalesFloorTwinDefinitionFlowsItemKind];


export const SalesFloorTwinDefinitionFlowsItemKind = {
  forward: 'forward',
  clarification: 'clarification',
  revision: 'revision',
  exit: 'exit',
} as const;

export type SalesFloorTwinDefinitionFlowsItemVia = typeof SalesFloorTwinDefinitionFlowsItemVia[keyof typeof SalesFloorTwinDefinitionFlowsItemVia];


export const SalesFloorTwinDefinitionFlowsItemVia = {
  'customer-clarification': 'customer-clarification',
  handover: 'handover',
  archive: 'archive',
} as const;

export type SalesFloorTwinDefinitionFlowsItem = {
  id: string;
  from: SalesFloorTwinStageId;
  to: SalesFloorTwinStageId;
  label: string;
  /** @exclusiveMinimum 0 */
  travelSeconds: number;
  kind: SalesFloorTwinDefinitionFlowsItemKind;
  isLoop?: boolean;
  via?: SalesFloorTwinDefinitionFlowsItemVia;
};

export interface SalesFloorTwinDefinition {
  id: SalesFloorTwinDefinitionId;
  name: string;
  /**
     * @minItems 9
     * @maxItems 9
     */
  stages: SalesFloorTwinStage[];
  /**
     * @minItems 4
     * @maxItems 4
     */
  channels: SalesFloorTwinDefinitionChannelsItem[];
  flows: SalesFloorTwinDefinitionFlowsItem[];
  workers: SalesFloorTwinWorker[];
}

export interface SalesFloorTwinStageSnapshot {
  id: SalesFloorTwinStageId;
  /** @minimum 0 */
  queueDepth: number;
  /** @minimum 0 */
  inProgress: number;
  /** @minimum 0 */
  completed: number;
}

export type SalesFloorTwinWorkerSnapshotKind = typeof SalesFloorTwinWorkerSnapshotKind[keyof typeof SalesFloorTwinWorkerSnapshotKind];


export const SalesFloorTwinWorkerSnapshotKind = {
  person: 'person',
  agent: 'agent',
} as const;

export type SalesFloorTwinWorkerSnapshotState = typeof SalesFloorTwinWorkerSnapshotState[keyof typeof SalesFloorTwinWorkerSnapshotState];


export const SalesFloorTwinWorkerSnapshotState = {
  idle: 'idle',
  working: 'working',
  processing: 'processing',
} as const;

export interface SalesFloorTwinWorkerSnapshot {
  id: string;
  name: string;
  kind: SalesFloorTwinWorkerSnapshotKind;
  role: string;
  homeStage: SalesFloorTwinStageId;
  active: boolean;
  state: SalesFloorTwinWorkerSnapshotState;
  /** @nullable */
  currentEnquiryId: string | null;
  /** @minimum 0 */
  completedWorkItems: number;
  /** @minimum 0 */
  busySeconds: number;
}

export type SalesFloorTwinEnquiryType = typeof SalesFloorTwinEnquiryType[keyof typeof SalesFloorTwinEnquiryType];


export const SalesFloorTwinEnquiryType = {
  'group-ski-booking': 'group-ski-booking',
  'corporate-retreat': 'corporate-retreat',
  'wedding-event': 'wedding-event',
  'school-trip': 'school-trip',
  'season-pass-query': 'season-pass-query',
  'accommodation-only': 'accommodation-only',
} as const;

export type SalesFloorTwinEnquiryValueBand = typeof SalesFloorTwinEnquiryValueBand[keyof typeof SalesFloorTwinEnquiryValueBand];


export const SalesFloorTwinEnquiryValueBand = {
  'under-5k': 'under-5k',
  '5k-15k': '5k-15k',
  '15k-40k': '15k-40k',
  '40k-plus': '40k-plus',
} as const;

export type SalesFloorTwinEnquiryPriority = typeof SalesFloorTwinEnquiryPriority[keyof typeof SalesFloorTwinEnquiryPriority];


export const SalesFloorTwinEnquiryPriority = {
  standard: 'standard',
  high: 'high',
  urgent: 'urgent',
} as const;

export type SalesFloorTwinEnquiryQuality = typeof SalesFloorTwinEnquiryQuality[keyof typeof SalesFloorTwinEnquiryQuality];


export const SalesFloorTwinEnquiryQuality = {
  genuine: 'genuine',
  spam: 'spam',
  duplicate: 'duplicate',
} as const;

export type SalesFloorTwinEnquiryPhase = typeof SalesFloorTwinEnquiryPhase[keyof typeof SalesFloorTwinEnquiryPhase];


export const SalesFloorTwinEnquiryPhase = {
  queued: 'queued',
  processing: 'processing',
  flow: 'flow',
  waiting: 'waiting',
  completed: 'completed',
  dropped: 'dropped',
} as const;

export type SalesFloorTwinEnquiryHistoryItemType = typeof SalesFloorTwinEnquiryHistoryItemType[keyof typeof SalesFloorTwinEnquiryHistoryItemType];


export const SalesFloorTwinEnquiryHistoryItemType = {
  arrived: 'arrived',
  entered: 'entered',
  started: 'started',
  completed: 'completed',
  clarification: 'clarification',
  approved: 'approved',
  'quote-sent': 'quote-sent',
  won: 'won',
  lost: 'lost',
  dropped: 'dropped',
} as const;

export type SalesFloorTwinEnquiryHistoryItem = {
  /** @minimum 0 */
  atSeconds: number;
  type: SalesFloorTwinEnquiryHistoryItemType;
  stageId: SalesFloorTwinStageId;
  /** @nullable */
  workerId: string | null;
  note: string;
};

export interface SalesFloorTwinEnquiry {
  /**
     * @minLength 4
     * @maxLength 128
     * @pattern ^[A-Za-z0-9._:-]+$
     */
  id: string;
  type: SalesFloorTwinEnquiryType;
  channel: SalesFloorTwinChannel;
  customerName: string;
  /** @nullable */
  organisationName: string | null;
  /** @minimum 0 */
  partySize: number;
  /** @minimum 0 */
  estimatedValueAud: number;
  valueBand: SalesFloorTwinEnquiryValueBand;
  priority: SalesFloorTwinEnquiryPriority;
  /** @minimum 0 */
  arrivalTimestamp: number;
  quality: SalesFloorTwinEnquiryQuality;
  phase: SalesFloorTwinEnquiryPhase;
  stageId: SalesFloorTwinStageId;
  /** @nullable */
  assignedWorkerId: string | null;
  fromStageId: SalesFloorTwinStageId | null;
  toStageId: SalesFloorTwinStageId | null;
  /** @nullable */
  flowId: string | null;
  /**
     * @minimum 0
     * @maximum 1
     */
  progress: number;
  /** @minimum 0 */
  queueEnteredAt: number;
  /**
     * @minimum 0
     * @nullable
     */
  processingStartedAt: number | null;
  /** @minimum 0 */
  processingRemainingSeconds: number;
  /** @minimum 0 */
  processingDurationSeconds: number;
  /** @minimum 0 */
  flowRemainingSeconds: number;
  /**
     * @minimum 0
     * @nullable
     */
  waitUntilSeconds: number | null;
  approvalPending: boolean;
  approved: boolean;
  /** @minimum 0 */
  clarificationCount: number;
  /** @minimum 0 */
  revisionCount: number;
  /**
     * @minimum 0
     * @nullable
     */
  quoteSentAt: number | null;
  /** @minimum 0 */
  quoteSentCount: number;
  slaBreached: boolean;
  /** @maxItems 20 */
  history: SalesFloorTwinEnquiryHistoryItem[];
}

export type SalesFloorTwinSnapshotDefinitionId = typeof SalesFloorTwinSnapshotDefinitionId[keyof typeof SalesFloorTwinSnapshotDefinitionId];


export const SalesFloorTwinSnapshotDefinitionId = {
  'pmf-sales-floor': 'pmf-sales-floor',
} as const;

export type SalesFloorTwinSnapshotScenarioId = typeof SalesFloorTwinSnapshotScenarioId[keyof typeof SalesFloorTwinSnapshotScenarioId];


export const SalesFloorTwinSnapshotScenarioId = {
  'agent-assisted': 'agent-assisted',
} as const;

export type SalesFloorTwinSnapshotLeversSpeed = typeof SalesFloorTwinSnapshotLeversSpeed[keyof typeof SalesFloorTwinSnapshotLeversSpeed];


export const SalesFloorTwinSnapshotLeversSpeed = {
  NUMBER_1: 1,
} as const;

export type SalesFloorTwinSnapshotLevers = {
  /** @minimum 0 */
  enquiriesPerHour: number;
  /**
     * @minimum 0
     * @maximum 1
     */
  agentShare: number;
  /** @minimum 0 */
  coordinatorsOnShift: number;
  /** @minimum 0 */
  approvalThreshold: number;
  speed: SalesFloorTwinSnapshotLeversSpeed;
  paused: boolean;
};

export type SalesFloorTwinSnapshotStages = {[key: string]: SalesFloorTwinStageSnapshot};

export type SalesFloorTwinSnapshotEnquiryPositionsItemPhase = typeof SalesFloorTwinSnapshotEnquiryPositionsItemPhase[keyof typeof SalesFloorTwinSnapshotEnquiryPositionsItemPhase];


export const SalesFloorTwinSnapshotEnquiryPositionsItemPhase = {
  processing: 'processing',
  completed: 'completed',
} as const;

export type SalesFloorTwinSnapshotEnquiryPositionsItem = {
  enquiryId: string;
  phase: SalesFloorTwinSnapshotEnquiryPositionsItemPhase;
  stageId: SalesFloorTwinStageId;
  fromStageId: SalesFloorTwinStageId | null;
  toStageId: SalesFloorTwinStageId | null;
  /**
     * @minimum 0
     * @maximum 1
     */
  progress: number;
};

export type SalesFloorTwinSnapshotLedger = {
  /** @minimum 0 */
  arrived: number;
  /** @minimum 0 */
  dropped: number;
  /** @minimum 0 */
  outstanding: number;
  /** @minimum 0 */
  inProgress: number;
  /** @minimum 0 */
  completedToQuote: number;
  /** @minimum 0 */
  won: number;
  /** @minimum 0 */
  lost: number;
  /** @minimum 0 */
  slaBreaches: number;
};

export type SalesFloorTwinSnapshotReadingsStageCounts = {[key: string]: number};

export type SalesFloorTwinSnapshotReadingsHistory = {[key: string]: number[]};

export type SalesFloorTwinSnapshotReadings = {
  /** @minimum 0 */
  enquiriesOutstanding: number;
  /** @minimum 0 */
  quotesSentPerHour: number;
  /** @minimum 0 */
  averageTimeToQuoteSeconds: number;
  /**
     * @minimum 0
     * @maximum 1
     */
  winRate: number;
  /**
     * @minimum 0
     * @maximum 100
     */
  agentWorkloadPercent: number;
  /**
     * @minimum 0
     * @maximum 100
     */
  humanWorkloadPercent: number;
  /** @minimum 0 */
  slaBreaches: number;
  /** @minimum 0 */
  wonValueAud: number;
  /** @minimum 0 */
  averageTimeInStageSeconds: number;
  stageCounts: SalesFloorTwinSnapshotReadingsStageCounts;
  history: SalesFloorTwinSnapshotReadingsHistory;
};

export type SalesFloorTwinSnapshotRecentEventsItemType = typeof SalesFloorTwinSnapshotRecentEventsItemType[keyof typeof SalesFloorTwinSnapshotRecentEventsItemType];


export const SalesFloorTwinSnapshotRecentEventsItemType = {
  arrived: 'arrived',
  triaged: 'triaged',
  'clarification-requested': 'clarification-requested',
  'quote-approved': 'quote-approved',
  'quote-sent': 'quote-sent',
  won: 'won',
  lost: 'lost',
} as const;

export type SalesFloorTwinSnapshotRecentEventsItem = {
  id: string;
  /** @minimum 0 */
  atSeconds: number;
  type: SalesFloorTwinSnapshotRecentEventsItemType;
  enquiryId: string;
  stageId: SalesFloorTwinStageId;
  message: string;
};

export type SalesFloorTwinSnapshotEngine = { [key: string]: unknown };

export type SalesFloorTwinSnapshotTotals = {
  /** @minimum 0 */
  matchingEnquiries: number;
  /**
     * @minimum 0
     * @maximum 400
     */
  returnedEnquiries: number;
  /** @minimum 0 */
  matchingEvents: number;
  /**
     * @minimum 0
     * @maximum 200
     */
  returnedEvents: number;
  /** @minimum 0 */
  wonValueAud: number;
};

export interface SalesFloorTwinSnapshot {
  definitionId: SalesFloorTwinSnapshotDefinitionId;
  scenarioId: SalesFloorTwinSnapshotScenarioId;
  /** @minimum 0 */
  seed: number;
  /** @minimum 0 */
  elapsedSeconds: number;
  levers: SalesFloorTwinSnapshotLevers;
  stages: SalesFloorTwinSnapshotStages;
  workers: SalesFloorTwinWorkerSnapshot[];
  /** @maxItems 400 */
  enquiries: SalesFloorTwinEnquiry[];
  /** @maxItems 400 */
  enquiryPositions: SalesFloorTwinSnapshotEnquiryPositionsItem[];
  ledger: SalesFloorTwinSnapshotLedger;
  readings: SalesFloorTwinSnapshotReadings;
  /** @maxItems 200 */
  recentEvents: SalesFloorTwinSnapshotRecentEventsItem[];
  engine: SalesFloorTwinSnapshotEngine;
  truncated: boolean;
  totals: SalesFloorTwinSnapshotTotals;
  generatedAt: string;
}

export type PlatformOptionsRolesItem = {
  id: string;
  name: string;
  scope: string;
};

export interface PlatformOptions {
  roles: PlatformOptionsRolesItem[];
}

export type PlatformEntityChoicePageItemsItem = {
  id: string;
  name: string;
  status: string;
};

export interface PlatformEntityChoicePage {
  items: PlatformEntityChoicePageItemsItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface WhatsAppCampaignPause {
  expectedUpdatedAt: string;
  requestKey: string;
}

export type WhatsAppReadinessChecksItem = {
  /**
     * @minLength 1
     * @maxLength 200
     */
  code: string;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  message: string;
  passed: boolean;
};

export interface WhatsAppReadiness {
  available: boolean;
  /**
     * @minLength 1
     * @maxLength 200
     */
  code: string;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  message: string;
  /** @maxItems 100 */
  checks: WhatsAppReadinessChecksItem[];
  /** @nullable */
  verifiedAt: string | null;
}

export type WhatsAppSenderOnboardingPath = typeof WhatsAppSenderOnboardingPath[keyof typeof WhatsAppSenderOnboardingPath];


export const WhatsAppSenderOnboardingPath = {
  personal_conversion: 'personal_conversion',
  business_app_coexistence: 'business_app_coexistence',
  existing_cloud_api: 'existing_cloud_api',
} as const;

export type WhatsAppSenderStatus = typeof WhatsAppSenderStatus[keyof typeof WhatsAppSenderStatus];


export const WhatsAppSenderStatus = {
  setup_required: 'setup_required',
  pending_verification: 'pending_verification',
  ready: 'ready',
  unavailable: 'unavailable',
  revoked: 'revoked',
  disconnected: 'disconnected',
} as const;

export interface WhatsAppSender {
  id: string;
  /** @minimum 0 */
  revision: number;
  /** @nullable */
  revisionId: string | null;
  /**
     * @maxLength 16
     * @pattern ^\+[1-9][0-9]{7,14}$
     */
  phoneNumber: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  providerAccountId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  providerPhoneNumberId: string;
  onboardingPath: WhatsAppSenderOnboardingPath;
  status: WhatsAppSenderStatus;
  active: boolean;
  coexistenceVerified: boolean;
  readiness: WhatsAppReadiness;
}

export type WhatsAppSettingsCampaignCountriesItem = typeof WhatsAppSettingsCampaignCountriesItem[keyof typeof WhatsAppSettingsCampaignCountriesItem];


export const WhatsAppSettingsCampaignCountriesItem = {
  AU: 'AU',
  NZ: 'NZ',
} as const;

export interface WhatsAppSettings {
  /** @minimum 0 */
  revision: number;
  sender: WhatsAppSender | null;
  readiness: WhatsAppReadiness;
  /** @maxItems 2 */
  campaignCountries: WhatsAppSettingsCampaignCountriesItem[];
}

export type WhatsAppSenderConfigureOnboardingPath = typeof WhatsAppSenderConfigureOnboardingPath[keyof typeof WhatsAppSenderConfigureOnboardingPath];


export const WhatsAppSenderConfigureOnboardingPath = {
  personal_conversion: 'personal_conversion',
  business_app_coexistence: 'business_app_coexistence',
  existing_cloud_api: 'existing_cloud_api',
} as const;

export interface WhatsAppSenderConfigure {
  /** @minimum 0 */
  expectedRevision: number;
  requestKey: string;
  onboardingPath: WhatsAppSenderConfigureOnboardingPath;
  /**
     * @maxLength 16
     * @pattern ^\+[1-9][0-9]{7,14}$
     */
  phoneNumber: string;
  /**
     * @minLength 1
     * @maxLength 100
     */
  providerAccountId: string;
  /**
     * @minLength 1
     * @maxLength 100
     */
  providerPhoneNumberId: string;
}

export type WhatsAppOnboardingRequestPath = typeof WhatsAppOnboardingRequestPath[keyof typeof WhatsAppOnboardingRequestPath];


export const WhatsAppOnboardingRequestPath = {
  personal_conversion: 'personal_conversion',
  business_app_coexistence: 'business_app_coexistence',
  existing_cloud_api: 'existing_cloud_api',
} as const;

export interface WhatsAppOnboardingRequest {
  /** @minimum 0 */
  expectedRevision: number;
  path: WhatsAppOnboardingRequestPath;
  requestKey: string;
}

export type WhatsAppOnboardingSessionStatus = typeof WhatsAppOnboardingSessionStatus[keyof typeof WhatsAppOnboardingSessionStatus];


export const WhatsAppOnboardingSessionStatus = {
  available: 'available',
  unavailable: 'unavailable',
  owner_setup_required: 'owner_setup_required',
} as const;

export interface WhatsAppOnboardingSession {
  id: string;
  status: WhatsAppOnboardingSessionStatus;
  /** @nullable */
  expiresAt: string | null;
  /**
     * @minLength 1
     * @maxLength 200
     * @nullable
     */
  publicAppId: string | null;
  /**
     * @minLength 1
     * @maxLength 200
     * @nullable
     */
  configurationId: string | null;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  message: string;
}

export interface WhatsAppOnboardingComplete {
  /** @pattern ^[0-9]{1,100}$ */
  providerAccountId: string;
  /** @pattern ^[0-9]{1,100}$ */
  providerPhoneNumberId: string;
  /**
     * @maxLength 16
     * @pattern ^\+[1-9][0-9]{7,14}$
     */
  phoneNumber: string;
  sessionId: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  code: string;
  requestKey: string;
}

export interface WhatsAppRevisionAction {
  /** @minimum 0 */
  expectedRevision: number;
  requestKey: string;
}

export interface WhatsAppPermissionRecord {
  /**
     * @maxLength 16
     * @pattern ^\+[1-9][0-9]{7,14}$
     */
  recipientNumber: string;
  /** @minimum 0 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  purpose: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  wording: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  source: string;
  consentAt: string;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  evidenceReference: string;
  requestKey: string;
}

export interface WhatsAppPermissionWithdrawal {
  /**
     * @maxLength 16
     * @pattern ^\+[1-9][0-9]{7,14}$
     */
  recipientNumber: string;
  /** @minimum 0 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  reason: string;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  evidenceReference: string;
  requestKey: string;
}

export type WhatsAppPermissionEvidenceAction = typeof WhatsAppPermissionEvidenceAction[keyof typeof WhatsAppPermissionEvidenceAction];


export const WhatsAppPermissionEvidenceAction = {
  recorded: 'recorded',
  withdrawn: 'withdrawn',
} as const;

export interface WhatsAppPermissionEvidence {
  id: string;
  contactId: string;
  /**
     * @maxLength 16
     * @pattern ^\+[1-9][0-9]{7,14}$
     */
  recipientNumber: string;
  /** @minimum 1 */
  revision: number;
  action: WhatsAppPermissionEvidenceAction;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  purpose: string;
  /**
     * @minLength 1
     * @maxLength 4000
     */
  wording: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  source: string;
  consentAt: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  recordedBy: string;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  evidenceReference: string;
  createdAt: string;
}

export type WhatsAppPermissionStatus = typeof WhatsAppPermissionStatus[keyof typeof WhatsAppPermissionStatus];


export const WhatsAppPermissionStatus = {
  unknown: 'unknown',
  opted_in: 'opted_in',
  withdrawn: 'withdrawn',
} as const;

export interface WhatsAppPermission {
  contactId: string;
  /**
     * @maxLength 16
     * @pattern ^\+[1-9][0-9]{7,14}$
     */
  recipientNumber: string;
  status: WhatsAppPermissionStatus;
  /** @minimum 0 */
  revision: number;
  /** @nullable */
  evidenceId: string | null;
}

export type WhatsAppButtonType = typeof WhatsAppButtonType[keyof typeof WhatsAppButtonType];


export const WhatsAppButtonType = {
  url: 'url',
  phone_number: 'phone_number',
  quick_reply: 'quick_reply',
} as const;

export interface WhatsAppButton {
  type: WhatsAppButtonType;
  /**
     * @minLength 1
     * @maxLength 100
     */
  text: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  value?: string;
}

export type WhatsAppComponentType = typeof WhatsAppComponentType[keyof typeof WhatsAppComponentType];


export const WhatsAppComponentType = {
  header: 'header',
  body: 'body',
  footer: 'footer',
  buttons: 'buttons',
  unsupported: 'unsupported',
} as const;

export type WhatsAppComponentFormat = typeof WhatsAppComponentFormat[keyof typeof WhatsAppComponentFormat];


export const WhatsAppComponentFormat = {
  text: 'text',
  image: 'image',
  document: 'document',
  video: 'video',
} as const;

export interface WhatsAppComponent {
  /** @maxLength 100 */
  providerType?: string;
  /**
     * @maxItems 40
     * @items.maxLength 2000
     */
  exampleValues?: string[];
  type: WhatsAppComponentType;
  format?: WhatsAppComponentFormat;
  /**
     * @minLength 0
     * @maxLength 4000
     */
  text?: string;
  /** @maxItems 10 */
  buttons?: WhatsAppButton[];
}

export type WhatsAppTemplateCategory = typeof WhatsAppTemplateCategory[keyof typeof WhatsAppTemplateCategory];


export const WhatsAppTemplateCategory = {
  marketing: 'marketing',
  utility: 'utility',
  authentication: 'authentication',
} as const;

export type WhatsAppTemplateApprovalStatus = typeof WhatsAppTemplateApprovalStatus[keyof typeof WhatsAppTemplateApprovalStatus];


export const WhatsAppTemplateApprovalStatus = {
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
  paused: 'paused',
  disabled: 'disabled',
  deleted: 'deleted',
  unavailable: 'unavailable',
} as const;

export interface WhatsAppTemplate {
  eligible: boolean;
  /**
     * @maxItems 50
     * @items.maxLength 200
     */
  validationErrors: string[];
  id: string;
  senderId: string;
  snapshotId: string;
  /** @minimum 1 */
  version: number;
  /**
     * @minLength 1
     * @maxLength 200
     */
  providerTemplateId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @minLength 1
     * @maxLength 35
     */
  language: string;
  category: WhatsAppTemplateCategory;
  approvalStatus: WhatsAppTemplateApprovalStatus;
  /** @maxItems 10 */
  components: WhatsAppComponent[];
  /** @pattern ^[a-f0-9]{64}$ */
  contentSha256: string;
  updatedAt: string;
}

export type WhatsAppTemplateCreateCategory = typeof WhatsAppTemplateCreateCategory[keyof typeof WhatsAppTemplateCreateCategory];


export const WhatsAppTemplateCreateCategory = {
  marketing: 'marketing',
  utility: 'utility',
  authentication: 'authentication',
} as const;

export interface WhatsAppTemplateCreate {
  /**
     * @minLength 1
     * @maxLength 512
     */
  name: string;
  /**
     * @minLength 1
     * @maxLength 35
     */
  language: string;
  category: WhatsAppTemplateCreateCategory;
  /** @maxItems 10 */
  components: WhatsAppComponent[];
  requestKey: string;
}

export type WhatsAppCampaignStepUpdateParameterBindings = {[key: string]: string};

export interface WhatsAppCampaignStepUpdate {
  templateId: string;
  templateSnapshotId: string;
  parameterBindings: WhatsAppCampaignStepUpdateParameterBindings;
  /** @minimum 0 */
  expectedRevision: number;
  requestKey: string;
}

export type WhatsAppCampaignStepParameterBindings = {[key: string]: string};

export interface WhatsAppCampaignStep {
  campaignId: string;
  stepId: string;
  templateId: string;
  templateSnapshotId: string;
  parameterBindings: WhatsAppCampaignStepParameterBindings;
  /** @minimum 1 */
  revision: number;
}

export type WhatsAppExclusionReason = typeof WhatsAppExclusionReason[keyof typeof WhatsAppExclusionReason];


export const WhatsAppExclusionReason = {
  permission_unknown: 'permission_unknown',
  permission_withdrawn: 'permission_withdrawn',
  number_changed: 'number_changed',
  invalid_number: 'invalid_number',
  unsupported_country: 'unsupported_country',
  suppressed: 'suppressed',
  sender_unavailable: 'sender_unavailable',
  sender_changed: 'sender_changed',
  template_unapproved: 'template_unapproved',
  template_changed: 'template_changed',
  missing_parameter: 'missing_parameter',
  duplicate_number: 'duplicate_number',
  contact_unavailable: 'contact_unavailable',
  campaign_paused: 'campaign_paused',
  service_window_closed: 'service_window_closed',
  template_stale: 'template_stale',
  template_unavailable: 'template_unavailable',
  unsupported_component: 'unsupported_component',
  invalid_parameter: 'invalid_parameter',
  conflicting_contact_ownership: 'conflicting_contact_ownership',
  incomplete_evidence: 'incomplete_evidence',
  do_not_phone: 'do_not_phone',
  ambiguous_contact: 'ambiguous_contact',
} as const;

/**
 * @nullable
 */
export type WhatsAppRecipientCountry = typeof WhatsAppRecipientCountry[keyof typeof WhatsAppRecipientCountry] | null;


export const WhatsAppRecipientCountry = {
  AU: 'AU',
  NZ: 'NZ',
} as const;

export type WhatsAppRecipientResolvedParameters = {[key: string]: string};

export interface WhatsAppRecipient {
  /** @minimum 0 */
  recipientIndex: number;
  /**
     * @maxLength 6400
     * @nullable
     */
  resolvedContent: string | null;
  /** @nullable */
  contactId: string | null;
  /**
     * @maxLength 16
     * @nullable
     * @pattern ^\+[1-9][0-9]{7,14}$
     */
  recipientNumber: string | null;
  /** @nullable */
  country: WhatsAppRecipientCountry;
  eligible: boolean;
  /** @maxItems 20 */
  exclusionReasons: WhatsAppExclusionReason[];
  /** @minimum 0 */
  permissionRevision: number;
  resolvedParameters: WhatsAppRecipientResolvedParameters;
}

export interface WhatsAppRecipientPreview {
  /** False until a later durable launch review is persisted and readiness is proved. */
  launchable: boolean;
  /** @maxItems 100 */
  items: WhatsAppRecipient[];
  /** @minimum 0 */
  total: number;
  /** @minimum 0 */
  eligibleCount: number;
  /** @minimum 0 */
  excludedCount: number;
  /** @nullable */
  previewId: string | null;
  /** @pattern ^[a-f0-9]{64}$ */
  fingerprint: string;
  /** @nullable */
  senderRevisionId: string | null;
  /** @nullable */
  templateSnapshotId: string | null;
  expiresAt: string;
}

export interface WhatsAppSendRequest {
  previewId: string;
  /** @pattern ^[a-f0-9]{64}$ */
  fingerprint: string;
  requestKey: string;
  scheduledAt?: string;
}

export interface WhatsAppSendResult {
  /** @minimum 0 */
  queued: number;
  /** @minimum 0 */
  excluded: number;
  requestKey: string;
}

export type WhatsAppTestSendResolvedParameters = {[key: string]: string};

export interface WhatsAppTestSend {
  contactId: string;
  /**
     * @maxLength 16
     * @pattern ^\+[1-9][0-9]{7,14}$
     */
  recipientNumber: string;
  templateSnapshotId: string;
  resolvedParameters: WhatsAppTestSendResolvedParameters;
  requestKey: string;
}

export type WhatsAppMessageStatus = typeof WhatsAppMessageStatus[keyof typeof WhatsAppMessageStatus];


export const WhatsAppMessageStatus = {
  queued: 'queued',
  accepted: 'accepted',
  sent: 'sent',
  delivered: 'delivered',
  read: 'read',
  failed: 'failed',
  unknown: 'unknown',
  received: 'received',
  suppressed: 'suppressed',
  cancelled: 'cancelled',
} as const;

export type WhatsAppJobStatus = typeof WhatsAppJobStatus[keyof typeof WhatsAppJobStatus];


export const WhatsAppJobStatus = {
  queued: 'queued',
  dispatching: 'dispatching',
  retry: 'retry',
  accepted: 'accepted',
  suppressed: 'suppressed',
  failed: 'failed',
  unknown: 'unknown',
  cancelled: 'cancelled',
} as const;

export type WhatsAppMessageDirection = typeof WhatsAppMessageDirection[keyof typeof WhatsAppMessageDirection];


export const WhatsAppMessageDirection = {
  inbound: 'inbound',
  outbound: 'outbound',
} as const;

export type WhatsAppMessageKind = typeof WhatsAppMessageKind[keyof typeof WhatsAppMessageKind];


export const WhatsAppMessageKind = {
  campaign: 'campaign',
  test: 'test',
  staff_reply: 'staff_reply',
  inbound: 'inbound',
} as const;

export type WhatsAppMessageContentType = typeof WhatsAppMessageContentType[keyof typeof WhatsAppMessageContentType];


export const WhatsAppMessageContentType = {
  text: 'text',
  template: 'template',
  image: 'image',
  audio: 'audio',
  video: 'video',
  document: 'document',
  unsupported: 'unsupported',
} as const;

export type WhatsAppMessageMediaStatus = typeof WhatsAppMessageMediaStatus[keyof typeof WhatsAppMessageMediaStatus];


export const WhatsAppMessageMediaStatus = {
  none: 'none',
  pending: 'pending',
  ready: 'ready',
  unsupported: 'unsupported',
  unavailable: 'unavailable',
} as const;

export type WhatsAppMessageResolvedParameters = {[key: string]: string};

export interface WhatsAppMessage {
  id: string;
  conversationId: string;
  senderId: string;
  senderRevisionId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  providerAccountId: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  providerPhoneNumberId: string;
  /**
     * @minLength 1
     * @maxLength 200
     * @nullable
     */
  providerMessageId: string | null;
  /** @nullable */
  contactId: string | null;
  /** @nullable */
  campaignId: string | null;
  /** @nullable */
  stepId: string | null;
  /** @nullable */
  interactionId: string | null;
  /** @nullable */
  parentMessageId: string | null;
  /** @nullable */
  templateSnapshotId: string | null;
  /**
     * @maxLength 16
     * @pattern ^\+[1-9][0-9]{7,14}$
     */
  fromNumber: string;
  /**
     * @maxLength 16
     * @pattern ^\+[1-9][0-9]{7,14}$
     */
  toNumber: string;
  direction: WhatsAppMessageDirection;
  kind: WhatsAppMessageKind;
  contentType: WhatsAppMessageContentType;
  /**
     * @minLength 0
     * @maxLength 6400
     * @nullable
     */
  body: string | null;
  /** @nullable */
  fileId: string | null;
  mediaStatus?: WhatsAppMessageMediaStatus;
  /**
     * @minLength 1
     * @maxLength 200
     * @nullable
     */
  unsupportedType: string | null;
  resolvedParameters: WhatsAppMessageResolvedParameters;
  status: WhatsAppMessageStatus;
  /**
     * @minLength 1
     * @maxLength 200
     * @nullable
     */
  authorId: string | null;
  /** @nullable */
  acceptedAt: string | null;
  /** @nullable */
  sentAt: string | null;
  /** @nullable */
  deliveredAt: string | null;
  /** @nullable */
  readAt: string | null;
  /** @nullable */
  receivedAt: string | null;
  /**
     * @minLength 1
     * @maxLength 200
     * @nullable
     */
  errorCode: string | null;
  createdAt: string;
}

export interface WhatsAppDelivery {
  id: string;
  message: WhatsAppMessage;
  status: WhatsAppJobStatus;
  /** @minimum 0 */
  attemptCount: number;
  runAt: string;
  /** @nullable */
  nextAttemptAt: string | null;
  /**
     * @minLength 0
     * @maxLength 1000
     * @nullable
     */
  reason: string | null;
  /**
     * @minLength 1
     * @maxLength 200
     * @nullable
     */
  lastErrorCode: string | null;
}

export type WhatsAppCampaignSummaryCounts = {
  /** @minimum 0 */
  queued: number;
  /** @minimum 0 */
  accepted: number;
  /** @minimum 0 */
  sent: number;
  /** @minimum 0 */
  delivered: number;
  /** @minimum 0 */
  read: number;
  /** @minimum 0 */
  failed: number;
  /** @minimum 0 */
  unknown: number;
  /** @minimum 0 */
  suppressed: number;
  /** @minimum 0 */
  cancelled: number;
};

export type WhatsAppCampaignSummaryRateSource = typeof WhatsAppCampaignSummaryRateSource[keyof typeof WhatsAppCampaignSummaryRateSource];


export const WhatsAppCampaignSummaryRateSource = {
  provider: 'provider',
  unavailable: 'unavailable',
} as const;

export type WhatsAppCampaignSummaryCostEstimateSource = typeof WhatsAppCampaignSummaryCostEstimateSource[keyof typeof WhatsAppCampaignSummaryCostEstimateSource];


export const WhatsAppCampaignSummaryCostEstimateSource = {
  unavailable: 'unavailable',
} as const;

export type WhatsAppCampaignSummaryCostEstimate = {
  /** @nullable */
  amount: number | null;
  /** @nullable */
  currency: string | null;
  source: WhatsAppCampaignSummaryCostEstimateSource;
  /** @nullable */
  asOf: string | null;
  billed: false;
};

export interface WhatsAppCampaignSummary {
  campaignId: string;
  /** @minimum 0 */
  total: number;
  counts: WhatsAppCampaignSummaryCounts;
  /**
     * @minimum 0
     * @nullable
     */
  providerRate: number | null;
  rateSource: WhatsAppCampaignSummaryRateSource;
  readReceiptsMayBeAbsent: boolean;
  costEstimate: WhatsAppCampaignSummaryCostEstimate;
}

export type WhatsAppConversationStatus = typeof WhatsAppConversationStatus[keyof typeof WhatsAppConversationStatus];


export const WhatsAppConversationStatus = {
  open: 'open',
  closed: 'closed',
} as const;

export interface WhatsAppConversation {
  /**
     * Current revision of this conversation’s original sender; unavailable senders cannot authorize replies.
     * @nullable
     */
  senderRevisionId: string | null;
  id: string;
  senderId: string;
  /**
     * @maxLength 16
     * @pattern ^\+[1-9][0-9]{7,14}$
     */
  recipientNumber: string;
  /** @nullable */
  contactId: string | null;
  /**
     * @minLength 1
     * @maxLength 200
     * @nullable
     */
  assignedTo: string | null;
  status: WhatsAppConversationStatus;
  /** @nullable */
  lastVerifiedInboundAt: string | null;
  /** @nullable */
  windowExpiresAt: string | null;
  /** @nullable */
  lastMessageAt: string | null;
  /** @minimum 0 */
  unreadCount: number;
  /** @minimum 1 */
  revision: number;
  canReplyFreeText: boolean;
  readiness: WhatsAppReadiness;
}

export type WhatsAppConversationUpdateStatus = typeof WhatsAppConversationUpdateStatus[keyof typeof WhatsAppConversationUpdateStatus];


export const WhatsAppConversationUpdateStatus = {
  open: 'open',
  closed: 'closed',
} as const;

export interface WhatsAppConversationUpdate {
  /** @nullable */
  contactId?: string | null;
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 200
     * @nullable
     */
  assignedTo?: string | null;
  status?: WhatsAppConversationUpdateStatus;
  requestKey: string;
}

export interface WhatsAppMarkRead {
  readThroughMessageId: string;
  requestKey: string;
}

export type WhatsAppStaffReply = {
  kind: 'text';
  /**
     * @minLength 1
     * @maxLength 4096
     */
  body: string;
  expectedSenderRevisionId: string;
  requestKey: string;
} | {
  kind: 'template';
  templateSnapshotId: string;
  resolvedParameters: {[key: string]: string};
  expectedSenderRevisionId: string;
  requestKey: string;
};

/**
 * Untrusted provider boundary. Task 2 pins the supported Meta version; task 5 validates exact nested events after raw-body signature verification. No owner is accepted from this body.
 */
export interface WhatsAppWebhookEnvelope { [key: string]: unknown }

export interface WhatsAppTemplateList {
  /** @maxItems 100 */
  items: WhatsAppTemplate[];
  /** @minimum 0 */
  total: number;
}

export interface WhatsAppPermissionEvidenceList {
  /** @maxItems 100 */
  items: WhatsAppPermissionEvidence[];
  /** @minimum 0 */
  total: number;
}

export interface WhatsAppConversationList {
  /** @maxItems 100 */
  items: WhatsAppConversation[];
  /** @minimum 0 */
  total: number;
}

export interface WhatsAppMessageList {
  /** @maxItems 100 */
  items: WhatsAppMessage[];
  /** @minimum 0 */
  total: number;
}

export interface WhatsAppDeliveryList {
  /** @maxItems 100 */
  items: WhatsAppDelivery[];
  /** @minimum 0 */
  total: number;
}

/**
 * Request rejected
 */
export type ErrorResponse = Error;

/**
 * Explicit administrator-approved destination. Requires its reviewed binding fingerprint. Omit to use the effective category default.
 */
export type DocumentDestinationHeaderParameter = string;

/**
 * Effective storage fingerprint reviewed before saving. Stale values fail closed.
 */
export type DocumentBindingHeaderParameter = string;

/**
 * Account id from GET /api/portal/me. Unknown contexts are rejected.
 */
export type PortalContextHeaderParameter = string;

export type CallAuthCallIdParameter = string;

export type CallAuthChallengeIdParameter = string;

/**
 * Twilio request signature validated against the exact public callback URL and form body.
 */
export type TwilioSignatureHeaderParameter = string;

export type LimitParameter = number;

export type OffsetParameter = number;

/**
 * Include records flagged as test or demo data. They are excluded by default.
 */
export type IncludeTestDataParameter = boolean;

export type PreviewAccessMigrationParams = {
tenantId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListProposalTasksParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListProposalTaskOwnersParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListProposalTaskOwners200ItemsItem = {
  id: string;
  name: string;
};

export type ListProposalTaskOwners200 = {
  total: number;
  items: ListProposalTaskOwners200ItemsItem[];
};

export type ReviewProposalTasksBody = {
  requestId: string;
  draftId?: string;
  /**
     * @minItems 1
     * @maxItems 30
     */
  tasks: ProposalTaskInput[];
};

export type ConfirmProposalTasksBodyConfirmation = typeof ConfirmProposalTasksBodyConfirmation[keyof typeof ConfirmProposalTasksBodyConfirmation];


export const ConfirmProposalTasksBodyConfirmation = {
  CONFIRM: 'CONFIRM',
} as const;

export type ConfirmProposalTasksBody = {
  /**
     * @minLength 64
     * @maxLength 64
     */
  hash: string;
  confirmation: ConfirmProposalTasksBodyConfirmation;
};

export type GetCampaignAutonomyParams = {
campaignId?: string;
};

export type ReviewCampaignAutonomyBody = {
  conversationId: string;
  change: CampaignAutonomyChange;
};

export type GetLifecycleInsightsParams = {
asOf?: string;
responseState?: GetLifecycleInsightsResponseState;
resolutionState?: GetLifecycleInsightsResolutionState;
sourceKind?: LifecycleInsightKind;
audience?: GetLifecycleInsightsAudience;
groupBy?: GetLifecycleInsightsGroupBy;
/**
 * @maxLength 200
 */
groupKey?: string;
/**
 * @maxLength 200
 */
ownerId?: string;
/**
 * @maxLength 120
 */
status?: string;
from?: string;
to?: string;
dateState?: GetLifecycleInsightsDateState;
archiveState?: GetLifecycleInsightsArchiveState;
recordId?: string;
/**
 * @pattern ^[A-Z]{3}$
 */
currency?: string;
ratingScale?: GetLifecycleInsightsRatingScale;
includeTestData?: boolean;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
/**
 * @minimum 1
 * @maximum 100
 */
groupLimit?: number;
/**
 * @minimum 0
 */
groupOffset?: number;
};

export type GetLifecycleInsightsResponseState = typeof GetLifecycleInsightsResponseState[keyof typeof GetLifecycleInsightsResponseState];


export const GetLifecycleInsightsResponseState = {
  missing: 'missing',
  recorded: 'recorded',
  met: 'met',
  late: 'late',
  due: 'due',
  overdue: 'overdue',
  pending: 'pending',
} as const;

export type GetLifecycleInsightsResolutionState = typeof GetLifecycleInsightsResolutionState[keyof typeof GetLifecycleInsightsResolutionState];


export const GetLifecycleInsightsResolutionState = {
  missing: 'missing',
  recorded: 'recorded',
  met: 'met',
  late: 'late',
  due: 'due',
  overdue: 'overdue',
  pending: 'pending',
} as const;

export type GetLifecycleInsightsAudience = typeof GetLifecycleInsightsAudience[keyof typeof GetLifecycleInsightsAudience];


export const GetLifecycleInsightsAudience = {
  all: 'all',
  b2b: 'b2b',
  b2c: 'b2c',
  partner: 'partner',
  investor: 'investor',
} as const;

export type GetLifecycleInsightsGroupBy = typeof GetLifecycleInsightsGroupBy[keyof typeof GetLifecycleInsightsGroupBy];


export const GetLifecycleInsightsGroupBy = {
  source: 'source',
  status: 'status',
  owner: 'owner',
  month: 'month',
} as const;

export type GetLifecycleInsightsDateState = typeof GetLifecycleInsightsDateState[keyof typeof GetLifecycleInsightsDateState];


export const GetLifecycleInsightsDateState = {
  all: 'all',
  known: 'known',
  missing: 'missing',
} as const;

export type GetLifecycleInsightsArchiveState = typeof GetLifecycleInsightsArchiveState[keyof typeof GetLifecycleInsightsArchiveState];


export const GetLifecycleInsightsArchiveState = {
  active: 'active',
  archived: 'archived',
  all: 'all',
} as const;

export type GetLifecycleInsightsRatingScale = typeof GetLifecycleInsightsRatingScale[keyof typeof GetLifecycleInsightsRatingScale];


export const GetLifecycleInsightsRatingScale = {
  one_to_five: 'one_to_five',
} as const;

export type ListRetentionParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
sourceKind?: RetentionKind;
/**
 * @maxLength 200
 */
search?: string;
state?: ListRetentionState;
reviewState?: ListRetentionReviewState;
reviewOnOrBefore?: string;
includeTestData?: boolean;
};

export type ListRetentionState = typeof ListRetentionState[keyof typeof ListRetentionState];


export const ListRetentionState = {
  all: 'all',
  active: 'active',
  archived: 'archived',
} as const;

export type ListRetentionReviewState = typeof ListRetentionReviewState[keyof typeof ListRetentionReviewState];


export const ListRetentionReviewState = {
  all: 'all',
  unreviewed: 'unreviewed',
  due: 'due',
  scheduled: 'scheduled',
} as const;

export type ListRetentionHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ReviewRetentionChangeBody = {
  requestId: string;
  change: RetentionChange;
};

export type ConfirmRetentionChangeBodyConfirmation = typeof ConfirmRetentionChangeBodyConfirmation[keyof typeof ConfirmRetentionChangeBodyConfirmation];


export const ConfirmRetentionChangeBodyConfirmation = {
  CONFIRM: 'CONFIRM',
} as const;

export type ConfirmRetentionChangeBody = {
  /** @minLength 1 */
  hash: string;
  confirmation: ConfirmRetentionChangeBodyConfirmation;
};

export type ListDataQualityParams = {
search?: string;
ownerId?: string;
finding?: string;
decision?: string;
sourceKind?: ListDataQualitySourceKind;
includeTestData?: boolean;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListDataQualitySourceKind = typeof ListDataQualitySourceKind[keyof typeof ListDataQualitySourceKind];


export const ListDataQualitySourceKind = {
  account: 'account',
  contact: 'contact',
  opportunity: 'opportunity',
  enquiry: 'enquiry',
  activity: 'activity',
  tender: 'tender',
} as const;

export type ListDataQualityCandidatesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListDataQualityHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListDataQualityOwnersParams = {
search?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ReviewDataQualityChangeBody = {
  requestId: string;
  change: DataQualityChange;
};

export type ConfirmDataQualityChangeBodyConfirmation = typeof ConfirmDataQualityChangeBodyConfirmation[keyof typeof ConfirmDataQualityChangeBodyConfirmation];


export const ConfirmDataQualityChangeBodyConfirmation = {
  CONFIRM: 'CONFIRM',
} as const;

export type ConfirmDataQualityChangeBody = {
  /** @minLength 1 */
  hash: string;
  confirmation: ConfirmDataQualityChangeBodyConfirmation;
};

export type ListRenewalPlanningParams = {
search?: string;
accountId?: string;
ownerId?: string;
sourceKind?: string;
decision?: string;
dateState?: string;
from?: string;
to?: string;
followUpFrom?: string;
followUpTo?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListRenewalPlanningHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ReviewRenewalPlanningChangeBody = {
  requestId: string;
  change: RenewalPlanningChange;
};

export type ConfirmRenewalPlanningChangeBodyConfirmation = typeof ConfirmRenewalPlanningChangeBodyConfirmation[keyof typeof ConfirmRenewalPlanningChangeBodyConfirmation];


export const ConfirmRenewalPlanningChangeBodyConfirmation = {
  CONFIRM: 'CONFIRM',
} as const;

export type ConfirmRenewalPlanningChangeBody = {
  /** @minLength 1 */
  hash: string;
  confirmation: ConfirmRenewalPlanningChangeBodyConfirmation;
};

export type ListAccountReviewsParams = {
accountId?: string;
contactId?: string;
ownerId?: string;
kind?: ListAccountReviewsKind;
sourceKind?: ListAccountReviewsSourceKind;
advocacyStatus?: ListAccountReviewsAdvocacyStatus;
ratingState?: ListAccountReviewsRatingState;
/**
 * @maxLength 200
 */
search?: string;
from?: string;
to?: string;
nextFrom?: string;
nextTo?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 1000000
 */
offset?: number;
};

export type ListAccountReviewsKind = typeof ListAccountReviewsKind[keyof typeof ListAccountReviewsKind];


export const ListAccountReviewsKind = {
  review: 'review',
  feedback: 'feedback',
  advocacy: 'advocacy',
} as const;

export type ListAccountReviewsSourceKind = typeof ListAccountReviewsSourceKind[keyof typeof ListAccountReviewsSourceKind];


export const ListAccountReviewsSourceKind = {
  staff: 'staff',
  customer: 'customer',
  partner: 'partner',
  other: 'other',
} as const;

export type ListAccountReviewsAdvocacyStatus = typeof ListAccountReviewsAdvocacyStatus[keyof typeof ListAccountReviewsAdvocacyStatus];


export const ListAccountReviewsAdvocacyStatus = {
  unknown: 'unknown',
  granted: 'granted',
  declined: 'declined',
  withdrawn: 'withdrawn',
} as const;

export type ListAccountReviewsRatingState = typeof ListAccountReviewsRatingState[keyof typeof ListAccountReviewsRatingState];


export const ListAccountReviewsRatingState = {
  rated: 'rated',
  unrated: 'unrated',
} as const;

export type ListAccountReviewHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 1000000
 */
offset?: number;
};

export type ListServiceCasesParams = {
responseState?: ListServiceCasesResponseState;
resolutionState?: ListServiceCasesResolutionState;
escalationState?: ListServiceCasesEscalationState;
escalationOwnerId?: string;
asOf?: string;
accountId?: string;
contactId?: string;
enquiryId?: string;
agreementId?: string;
ownerId?: string;
classification?: ListServiceCasesClassification;
priority?: ListServiceCasesPriority;
/**
 * @maxLength 120
 */
status?: string;
/**
 * @maxLength 200
 */
search?: string;
from?: string;
to?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 1000000
 */
offset?: number;
};

export type ListServiceCasesResponseState = typeof ListServiceCasesResponseState[keyof typeof ListServiceCasesResponseState];


export const ListServiceCasesResponseState = {
  missing: 'missing',
  pending: 'pending',
  due: 'due',
  overdue: 'overdue',
  met: 'met',
  late: 'late',
  recorded: 'recorded',
} as const;

export type ListServiceCasesResolutionState = typeof ListServiceCasesResolutionState[keyof typeof ListServiceCasesResolutionState];


export const ListServiceCasesResolutionState = {
  missing: 'missing',
  pending: 'pending',
  due: 'due',
  overdue: 'overdue',
  met: 'met',
  late: 'late',
  recorded: 'recorded',
} as const;

export type ListServiceCasesEscalationState = typeof ListServiceCasesEscalationState[keyof typeof ListServiceCasesEscalationState];


export const ListServiceCasesEscalationState = {
  none: 'none',
  active: 'active',
  cleared: 'cleared',
} as const;

export type ListServiceCasesClassification = typeof ListServiceCasesClassification[keyof typeof ListServiceCasesClassification];


export const ListServiceCasesClassification = {
  case: 'case',
  request: 'request',
  complaint: 'complaint',
} as const;

export type ListServiceCasesPriority = typeof ListServiceCasesPriority[keyof typeof ListServiceCasesPriority];


export const ListServiceCasesPriority = {
  low: 'low',
  normal: 'normal',
  high: 'high',
  urgent: 'urgent',
} as const;

export type ListServiceCaseHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 1000000
 */
offset?: number;
};

export type ListDeliveryRecordsParams = {
resource?: ListDeliveryRecordsResource;
kind?: ListDeliveryRecordsKind;
accountId?: string;
planId?: string;
opportunityId?: string;
agreementId?: string;
ownerId?: string;
/**
 * @maxLength 120
 */
status?: string;
/**
 * @maxLength 200
 */
search?: string;
from?: string;
to?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 1000000
 */
offset?: number;
};

export type ListDeliveryRecordsResource = typeof ListDeliveryRecordsResource[keyof typeof ListDeliveryRecordsResource];


export const ListDeliveryRecordsResource = {
  plan: 'plan',
  item: 'item',
} as const;

export type ListDeliveryRecordsKind = typeof ListDeliveryRecordsKind[keyof typeof ListDeliveryRecordsKind];


export const ListDeliveryRecordsKind = {
  setup: 'setup',
  responsibility: 'responsibility',
  obligation: 'obligation',
  milestone: 'milestone',
} as const;

export type ListDeliveryHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 1000000
 */
offset?: number;
};

export type GetOpportunityForecastParams = {
audience?: GetOpportunityForecastAudience;
pipeline?: GetOpportunityForecastPipeline;
/**
 * @minLength 1
 * @maxLength 200
 */
ownerId?: string;
ownerState?: GetOpportunityForecastOwnerState;
/**
 * @pattern ^[A-Z]{3}$
 */
currency?: string;
outcome?: GetOpportunityForecastOutcome;
from?: string;
to?: string;
closeDate?: GetOpportunityForecastCloseDate;
missing?: GetOpportunityForecastMissing;
groupBy?: GetOpportunityForecastGroupBy;
/**
 * @minLength 1
 * @maxLength 300
 */
groupKey?: string;
opportunityId?: string;
includeTestData?: boolean;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 1000000
 */
offset?: number;
/**
 * @minimum 1
 * @maximum 100
 */
groupLimit?: number;
/**
 * @minimum 0
 * @maximum 1000000
 */
groupOffset?: number;
};

export type GetOpportunityForecastAudience = typeof GetOpportunityForecastAudience[keyof typeof GetOpportunityForecastAudience];


export const GetOpportunityForecastAudience = {
  b2b: 'b2b',
  b2c: 'b2c',
  partner: 'partner',
  investor: 'investor',
} as const;

export type GetOpportunityForecastPipeline = typeof GetOpportunityForecastPipeline[keyof typeof GetOpportunityForecastPipeline];


export const GetOpportunityForecastPipeline = {
  b2b_sales: 'b2b_sales',
  b2c_sales: 'b2c_sales',
  investor_funding: 'investor_funding',
} as const;

export type GetOpportunityForecastOwnerState = typeof GetOpportunityForecastOwnerState[keyof typeof GetOpportunityForecastOwnerState];


export const GetOpportunityForecastOwnerState = {
  any: 'any',
  assigned: 'assigned',
  unassigned: 'unassigned',
} as const;

export type GetOpportunityForecastOutcome = typeof GetOpportunityForecastOutcome[keyof typeof GetOpportunityForecastOutcome];


export const GetOpportunityForecastOutcome = {
  all: 'all',
  open: 'open',
  won: 'won',
  lost: 'lost',
} as const;

export type GetOpportunityForecastCloseDate = typeof GetOpportunityForecastCloseDate[keyof typeof GetOpportunityForecastCloseDate];


export const GetOpportunityForecastCloseDate = {
  any: 'any',
  known: 'known',
  missing: 'missing',
} as const;

export type GetOpportunityForecastMissing = typeof GetOpportunityForecastMissing[keyof typeof GetOpportunityForecastMissing];


export const GetOpportunityForecastMissing = {
  all: 'all',
  complete: 'complete',
  any: 'any',
  value: 'value',
  probability: 'probability',
  currency: 'currency',
  closeDate: 'closeDate',
  owner: 'owner',
} as const;

export type GetOpportunityForecastGroupBy = typeof GetOpportunityForecastGroupBy[keyof typeof GetOpportunityForecastGroupBy];


export const GetOpportunityForecastGroupBy = {
  currency: 'currency',
  owner: 'owner',
  pipeline: 'pipeline',
  month: 'month',
  quarter: 'quarter',
  year: 'year',
} as const;

export type ListAgreementRegisterParams = {
resource?: ListAgreementRegisterResource;
sourceKind?: ListAgreementRegisterSourceKind;
agreementId?: string;
accountId?: string;
ownerId?: string;
/**
 * @maxLength 120
 */
status?: string;
/**
 * @maxLength 200
 */
search?: string;
from?: string;
to?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
};

export type ListAgreementRegisterResource = typeof ListAgreementRegisterResource[keyof typeof ListAgreementRegisterResource];


export const ListAgreementRegisterResource = {
  agreement: 'agreement',
  obligation: 'obligation',
} as const;

export type ListAgreementRegisterSourceKind = typeof ListAgreementRegisterSourceKind[keyof typeof ListAgreementRegisterSourceKind];


export const ListAgreementRegisterSourceKind = {
  partner: 'partner',
  general: 'general',
} as const;

export type ListAgreementRegisterChoicesParams = {
kind: ListAgreementRegisterChoicesKind;
/**
 * @maxLength 200
 */
search?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
};

export type ListAgreementRegisterChoicesKind = typeof ListAgreementRegisterChoicesKind[keyof typeof ListAgreementRegisterChoicesKind];


export const ListAgreementRegisterChoicesKind = {
  account: 'account',
  contact: 'contact',
  opportunity: 'opportunity',
  owner: 'owner',
  document: 'document',
} as const;

export type ListAgreementRegisterHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
};

export type ReviewAgreementRegisterChangeBody = {
  requestId: string;
  change: AgreementChange;
};

export type ConfirmAgreementRegisterChangeBodyConfirmation = typeof ConfirmAgreementRegisterChangeBodyConfirmation[keyof typeof ConfirmAgreementRegisterChangeBodyConfirmation];


export const ConfirmAgreementRegisterChangeBodyConfirmation = {
  CONFIRM: 'CONFIRM',
} as const;

export type ConfirmAgreementRegisterChangeBody = {
  /**
     * @minLength 64
     * @maxLength 64
     */
  hash: string;
  confirmation: ConfirmAgreementRegisterChangeBodyConfirmation;
};

export type ListCrmStakeholdersParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
/**
 * @maxLength 200
 */
search?: string;
contextAccountId?: string;
contextOpportunityId?: string;
accountId?: string;
contactId?: string;
relatedAccountId?: string;
relatedContactId?: string;
/**
 * @maxLength 120
 */
role?: string;
influence?: ListCrmStakeholdersInfluence;
/**
 * @maxLength 120
 */
stance?: string;
from?: string;
to?: string;
includeRemoved?: boolean;
};

export type ListCrmStakeholdersInfluence = typeof ListCrmStakeholdersInfluence[keyof typeof ListCrmStakeholdersInfluence];


export const ListCrmStakeholdersInfluence = {
  low: 'low',
  medium: 'medium',
  high: 'high',
  unknown: 'unknown',
} as const;

export type ListCrmStakeholderHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListCrmStakeholderChoicesParams = {
kind: ListCrmStakeholderChoicesKind;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
/**
 * @maxLength 200
 */
search?: string;
};

export type ListCrmStakeholderChoicesKind = typeof ListCrmStakeholderChoicesKind[keyof typeof ListCrmStakeholderChoicesKind];


export const ListCrmStakeholderChoicesKind = {
  account: 'account',
  contact: 'contact',
  opportunity: 'opportunity',
} as const;

export type ReviewCrmStakeholderBody = {
  requestId: string;
  change: CrmStakeholderChange;
};

export type ConfirmCrmStakeholderBody = {
  /**
     * @minLength 64
     * @maxLength 64
     */
  hash: string;
  confirmed: true;
};

export type ListCrmReferralsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
/**
 * @maxLength 200
 */
search?: string;
/**
 * @maxLength 120
 */
outcome?: string;
ownerId?: string;
accountId?: string;
contactId?: string;
enquiryId?: string;
opportunityId?: string;
from?: string;
to?: string;
};

export type ListCrmReferralHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListCrmReferralChoicesParams = {
kind: ListCrmReferralChoicesKind;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
/**
 * @maxLength 200
 */
search?: string;
};

export type ListCrmReferralChoicesKind = typeof ListCrmReferralChoicesKind[keyof typeof ListCrmReferralChoicesKind];


export const ListCrmReferralChoicesKind = {
  account: 'account',
  contact: 'contact',
  enquiry: 'enquiry',
  opportunity: 'opportunity',
} as const;

export type ReviewCrmReferralBody = {
  requestId: string;
  change: CrmReferralChange;
};

export type ConfirmCrmReferralBody = {
  /**
     * @minLength 64
     * @maxLength 64
     */
  hash: string;
  confirmed: true;
};

export type ListNativeWordProposalsParams = {
/**
 * @minimum 1
 * @maximum 20
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 20
 */
offset?: number;
};

export type InspectSharePointWordSnapshotParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 20000
 */
offset?: number;
};

export type ListNativeWordSavesParams = {
/**
 * @minimum 1
 * @maximum 20
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 20
 */
offset?: number;
};

export type ListSharePointWordSourcesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
};

export type ListSharePointWordSources200State = typeof ListSharePointWordSources200State[keyof typeof ListSharePointWordSources200State];


export const ListSharePointWordSources200State = {
  ready: 'ready',
  setup_required: 'setup_required',
} as const;

export type ListSharePointWordSources200ItemsItem = {
  destinationId: string;
  displayName: string;
  siteId: string;
  driveId: string;
  folderPath: string;
};

export type ListSharePointWordSources200 = {
  state: ListSharePointWordSources200State;
  message: string;
  bindingFingerprint: string;
  total: number;
  limit: number;
  offset: number;
  items: ListSharePointWordSources200ItemsItem[];
};

export type GetGovernmentTenantOverviewParams = {
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
entityId?: string;
category?: GovernmentCategory;
progress?: GovernmentPursuitProgress;
ownerAppUserId?: string;
targetBefore?: string;
};

export type ListGovernmentPursuitsParams = {
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
progress?: GovernmentPursuitProgress;
ownerAppUserId?: string;
unassigned?: boolean;
targetBefore?: string;
};

export type ListGovernmentPursuitOwnersParams = {
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListGovernmentPursuitHistoryParams = {
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListTenderOpportunitiesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
/**
 * @maxItems 7
 */
status?: TenderOpportunityStatus[];
/**
 * @maxItems 3
 */
band?: TenderOpportunityMatchBand[];
/**
 * Not for us recommendations: include (default), exclude (the tender list default view) or only.
 */
notForUs?: ListTenderOpportunitiesNotForUs;
/**
 * @minLength 1
 * @maxLength 80
 */
jurisdiction?: string;
/**
 * @minimum 1
 * @maximum 365
 */
closingWithinDays?: number;
/**
 * @minLength 1
 * @maxLength 200
 */
search?: string;
assigned?: ListTenderOpportunitiesAssigned;
includeDismissed?: boolean;
/**
 * Only opportunities mirrored into these Government observations.
 * @maxItems 50
 */
observationId?: string[];
/**
 * Only the opportunity linked to this Government pursuit.
 */
pursuitId?: string;
/**
 * Only the opportunity converted into this tender/SOW response.
 */
tenderId?: string;
/**
 * Only the opportunity linked to this pipeline opportunity.
 */
pipelineOpportunityId?: string;
};

export type ListTenderOpportunitiesNotForUs = typeof ListTenderOpportunitiesNotForUs[keyof typeof ListTenderOpportunitiesNotForUs];


export const ListTenderOpportunitiesNotForUs = {
  include: 'include',
  exclude: 'exclude',
  only: 'only',
} as const;

export type ListTenderOpportunitiesAssigned = typeof ListTenderOpportunitiesAssigned[keyof typeof ListTenderOpportunitiesAssigned];


export const ListTenderOpportunitiesAssigned = {
  me: 'me',
  unassigned: 'unassigned',
} as const;

export type ListTenderOpportunityEventsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListTenderOpportunityDocumentsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetGovernmentDiscoveryCoverageParams = {
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListGovernmentFindingsParams = {
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
category?: GovernmentCategory;
state?: GovernmentState;
jurisdictionLevel?: ListGovernmentFindingsJurisdictionLevel;
applicationStatus?: ListGovernmentFindingsApplicationStatus;
relevance?: ListGovernmentFindingsRelevance;
};

export type ListGovernmentFindingsJurisdictionLevel = typeof ListGovernmentFindingsJurisdictionLevel[keyof typeof ListGovernmentFindingsJurisdictionLevel];


export const ListGovernmentFindingsJurisdictionLevel = {
  federal: 'federal',
  state: 'state',
  local: 'local',
} as const;

export type ListGovernmentFindingsApplicationStatus = typeof ListGovernmentFindingsApplicationStatus[keyof typeof ListGovernmentFindingsApplicationStatus];


export const ListGovernmentFindingsApplicationStatus = {
  unknown: 'unknown',
  upcoming: 'upcoming',
  open: 'open',
  closed: 'closed',
  ongoing: 'ongoing',
  suspended: 'suspended',
} as const;

export type ListGovernmentFindingsRelevance = typeof ListGovernmentFindingsRelevance[keyof typeof ListGovernmentFindingsRelevance];


export const ListGovernmentFindingsRelevance = {
  potential: 'potential',
  not_relevant: 'not_relevant',
  unknown: 'unknown',
} as const;

export type ListDocumentWorkspaceSourceVersionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
};

export type ListDocumentWorkspacesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
anchorKind?: ListDocumentWorkspacesAnchorKind;
recordId?: string;
};

export type ListDocumentWorkspacesAnchorKind = typeof ListDocumentWorkspacesAnchorKind[keyof typeof ListDocumentWorkspacesAnchorKind];


export const ListDocumentWorkspacesAnchorKind = {
  partner_agreement: 'partner_agreement',
  managed_document: 'managed_document',
  tender: 'tender',
  sow_preparation: 'sow_preparation',
} as const;

export type ListDocumentWorkspaceFoldersParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
};

export type ListDocumentWorkspaceMembershipsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
folderId?: string;
};

export type GetFileAssociationsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
};

export type ListRecordFilesParams = {
/**
 * @maxLength 200
 */
search?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
};

export type ListFileLocationsParams = {
sourceKind: ListFileLocationsSourceKind;
sourceId: string;
workspaceId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
};

export type ListFileLocationsSourceKind = typeof ListFileLocationsSourceKind[keyof typeof ListFileLocationsSourceKind];


export const ListFileLocationsSourceKind = {
  library: 'library',
  managed_document: 'managed_document',
} as const;

export type GetDocumentWorkspaceContextParams = {
/**
 * @maxLength 200
 */
search?: string;
sourceKind?: GetDocumentWorkspaceContextSourceKind;
/**
 * @maxLength 200
 */
type?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
folderId?: string;
};

export type GetDocumentWorkspaceContextSourceKind = typeof GetDocumentWorkspaceContextSourceKind[keyof typeof GetDocumentWorkspaceContextSourceKind];


export const GetDocumentWorkspaceContextSourceKind = {
  library: 'library',
  managed_document: 'managed_document',
} as const;

export type CreateBidBuyerReviewBody = {
  packageRevisionId: string;
  /** @minimum 1 */
  selectionRevision: number;
};

export type ListBidLessonsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListBidLessonHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type GetBidSynthesisParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type GetBidStageModelsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type GetBidModelAdministrationParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListBidModelRevisionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListBidPolicyRevisionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetEffectiveBidModelsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
stage?: BidStage;
};

export type ListSupplierFactRevisionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListSupplierFactRevisions200 = {
  items: SupplierFact[];
  total: number;
  limit: number;
  offset: number;
};

export type GetBidInventoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetBidFoundationParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListDocumentStorageOptionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
connectionOffset?: number;
/**
 * @minimum 0
 */
destinationOffset?: number;
};

export type CreateApplicationDocumentDestinationBody = {
  /**
     * @minLength 1
     * @maxLength 120
     */
  displayName: string;
  /**
     * @minLength 10
     * @maxLength 1000
     */
  reason: string;
};

export type ListDocumentMaterializationsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListDocumentMaterializations200 = {
  items: DocumentMaterialization[];
  /** @minimum 0 */
  total: number;
  /** @minimum 0 */
  limit: number;
  /** @minimum 0 */
  offset: number;
};

export type SaveDocumentRepresentationBodySourceKind = typeof SaveDocumentRepresentationBodySourceKind[keyof typeof SaveDocumentRepresentationBodySourceKind];


export const SaveDocumentRepresentationBodySourceKind = {
  managed_version: 'managed_version',
  research_version: 'research_version',
  content_asset: 'content_asset',
  quote_spreadsheet: 'quote_spreadsheet',
} as const;

export type SaveDocumentRepresentationBodyFormat = typeof SaveDocumentRepresentationBodyFormat[keyof typeof SaveDocumentRepresentationBodyFormat];


export const SaveDocumentRepresentationBodyFormat = {
  docx: 'docx',
  pdf: 'pdf',
  markdown: 'markdown',
  xlsx: 'xlsx',
} as const;

export type SaveDocumentRepresentationBody = {
  sourceKind: SaveDocumentRepresentationBodySourceKind;
  sourceId: string;
  /**
     * @minLength 1
     * @maxLength 100
     */
  sourceRevision: string;
  format?: SaveDocumentRepresentationBodyFormat;
  destinationId?: string;
  /** @pattern ^[a-f0-9]{64}$ */
  bindingFingerprint: string;
};

export type SaveDocumentRepresentation201 = {
  fileId: string;
  filename: string;
  storageKey: string;
};

export type ListM365LibrariesParams = {
/**
 * @minimum 1
 */
expectedRevision: number;
/**
 * @maxLength 2000
 */
siteUrl: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListM365ConnectionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type CompleteM365ConnectionParams = {
/**
 * @minLength 1
 * @maxLength 4096
 */
code: string;
state: string;
};

export type GetDashboardSummaryParams = {
audience?: CrmAudience;
pipeline?: OpportunityPipeline;
accountKind?: AccountKind;
/**
 * Include records flagged as test or demo data. They are excluded by default.
 */
includeTestData?: IncludeTestDataParameter;
};

export type GetDashboardTimeseriesParams = {
/**
 * @minimum 7
 * @maximum 90
 */
days?: number;
};

export type GetHomeBriefingParams = {
/**
 * Limit the briefing to this audience; omit for the overall briefing.
 */
audience?: CrmAudience;
};

export type RefreshHomeBriefingParams = {
/**
 * Limit the briefing to this audience; omit for the overall briefing.
 */
audience?: CrmAudience;
};

export type GetCatalogueHierarchyParams = {
includeInactive?: boolean;
};

export type ListPriceListsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ResolveAccountPriceListParams = {
accountId: string;
};

export type ListPriceListItemsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListPriceListRatesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type GetQuotablePriceBookParams = {
/**
 * @maxLength 100
 */
search?: string;
/**
 * Include active catalogue entries that do not yet have a price-list item (admin grid only).
 */
includeUnpriced?: boolean;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListCrmSegmentsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
/**
 * @maxLength 200
 */
search?: string;
};

export type ListCrmSegmentHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListAccountsParams = {
/**
 * JSON encoded CrmSegmentFilters. Validated allowlist; intersects existing query conditions before paging.
 * @maxLength 6000
 */
targeting?: string;
/**
 * Re-evaluate this current scoped saved definition before paging.
 */
segmentId?: string;
/**
 * Case-insensitive match on account name.
 * @maxLength 200
 */
search?: string;
relationshipType?: AccountRelationshipType;
/**
 * Workspace audience lens. B2B includes both customers and prospects.
 */
audience?: CrmAudience;
accountKind?: AccountKind;
/**
 * Include records flagged as test or demo data. They are excluded by default.
 */
includeTestData?: IncludeTestDataParameter;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListContactsParams = {
accountId?: string;
/**
 * Case-insensitive match on name or email.
 * @maxLength 200
 */
search?: string;
/**
 * Include records flagged as test or demo data. They are excluded by default.
 */
includeTestData?: IncludeTestDataParameter;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListOpportunityHubItemsParams = {
/**
 * Literal case-insensitive text in the title, summary, account, contact or owner.
 * @maxLength 200
 */
search?: string;
/**
 * Comma-separated item types to include. Omit for every type.
 * @maxLength 200
 * @pattern ^(enquiry|social|web_signal|opportunity|referral|quote|tender)(,(enquiry|social|web_signal|opportunity|referral|quote|tender))*$
 */
types?: string;
/**
 * Tenders recommended Not for us are kept but hidden by default; include shows them.
 */
notForUs?: ListOpportunityHubItemsNotForUs;
/**
 * open shows items still needing attention; all includes closed, won, lost and dismissed items.
 */
view?: ListOpportunityHubItemsView;
/**
 * mine keeps items owned by or assigned to the signed-in person.
 */
owner?: ListOpportunityHubItemsOwner;
sort?: ListOpportunityHubItemsSort;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListOpportunityHubItemsNotForUs = typeof ListOpportunityHubItemsNotForUs[keyof typeof ListOpportunityHubItemsNotForUs];


export const ListOpportunityHubItemsNotForUs = {
  exclude: 'exclude',
  include: 'include',
} as const;

export type ListOpportunityHubItemsView = typeof ListOpportunityHubItemsView[keyof typeof ListOpportunityHubItemsView];


export const ListOpportunityHubItemsView = {
  open: 'open',
  all: 'all',
} as const;

export type ListOpportunityHubItemsOwner = typeof ListOpportunityHubItemsOwner[keyof typeof ListOpportunityHubItemsOwner];


export const ListOpportunityHubItemsOwner = {
  everyone: 'everyone',
  mine: 'mine',
} as const;

export type ListOpportunityHubItemsSort = typeof ListOpportunityHubItemsSort[keyof typeof ListOpportunityHubItemsSort];


export const ListOpportunityHubItemsSort = {
  urgent: 'urgent',
  newest: 'newest',
  value: 'value',
  activity: 'activity',
} as const;

export type SearchSiteParams = {
/**
 * Literal case-insensitive words to find.
 * @minLength 2
 * @maxLength 200
 */
search: string;
/**
 * Comma-separated record types to return. Omit for every type. Counts always cover every type.
 * @maxLength 200
 * @pattern ^(account|contact|opportunity|enquiry|quote|tender|agreement)(,(account|contact|opportunity|enquiry|quote|tender|agreement))*$
 */
types?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListEnquiriesParams = {
/**
 * Literal case-insensitive text in name, email, company, or subject; applied before paging.
 * @maxLength 200
 */
search?: string;
status?: EnquiryStatus;
source?: EnquirySource;
type?: EnquiryType;
audience?: CrmAudience;
accountId?: string;
contactId?: string;
/**
 * Include records flagged as test or demo data. They are excluded by default.
 */
includeTestData?: IncludeTestDataParameter;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type GetJourneyRecordSignalsParams = {
window?: JourneySignalWindow;
/**
 * @minLength 1
 * @maxLength 100
 */
channel?: string;
/**
 * @minimum 1
 * @maximum 500
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetJourneySignalTimeseriesParams = {
window?: JourneySignalWindow;
/**
 * @minLength 1
 * @maxLength 100
 */
channel?: string;
recordId?: string;
bucket?: JourneySignalBucket;
includeTestData?: boolean;
};

export type GetJourneySignalSummaryParams = {
window?: JourneySignalWindow;
/**
 * @minLength 1
 * @maxLength 100
 */
channel?: string;
includeTestData?: boolean;
};

export type ListJourneyCoachingParams = {
status?: InstructionReviewStatus;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListJourneyTransitionHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListMicrositeAdminEntitiesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListEntityMicrositeVersionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListEntityMicrositeAssetsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type UploadEntityMicrositeAssetParams = {
kind: MicrositeAssetKind;
};

export type UploadEntityMicrositeAssetBody = {
  file: Blob | File;
};

export type ListEntityMicrositeSourcesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListEntityMicrositeSourceVersionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListEntityMicrositeBrandSourcesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type UploadEntityMicrositeBrandGuideBody = {
  file: Blob | File;
};

export type ListEntityMicrositeBrandProposalsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListPublicMicrositeBookingSlotsParams = {
from?: string;
to?: string;
/**
 * @minLength 1
 * @maxLength 300
 */
service?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListChatAssistantSessionsParams = {
status?: ListChatAssistantSessionsStatus;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
};

export type ListChatAssistantSessionsStatus = typeof ListChatAssistantSessionsStatus[keyof typeof ListChatAssistantSessionsStatus];


export const ListChatAssistantSessionsStatus = {
  active: 'active',
  handoff_requested: 'handoff_requested',
  resolved: 'resolved',
} as const;

export type GetPublicEventParams = {
/**
 * Personal SMS tracking reference. Invalid references are ignored.
 * @maxLength 100
 */
ref?: string;
};

export type ListOpportunitiesParams = {
stage?: OpportunityStage;
pipeline?: OpportunityPipeline;
audience?: CrmAudience;
accountId?: string;
contactId?: string;
/**
 * Include records flagged as test or demo data. They are excluded by default.
 */
includeTestData?: IncludeTestDataParameter;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListOpportunityNotesParams = {
opportunityId?: string;
kind?: ListOpportunityNotesKind;
status?: ListOpportunityNotesStatus;
/**
 * @maxLength 200
 */
search?: string;
from?: string;
to?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListOpportunityNotesKind = typeof ListOpportunityNotesKind[keyof typeof ListOpportunityNotesKind];


export const ListOpportunityNotesKind = {
  discovery: 'discovery',
  negotiation: 'negotiation',
} as const;

export type ListOpportunityNotesStatus = typeof ListOpportunityNotesStatus[keyof typeof ListOpportunityNotesStatus];


export const ListOpportunityNotesStatus = {
  open: 'open',
  settled: 'settled',
} as const;

export type ListOpportunityNoteChoicesParams = {
kind: ListOpportunityNoteChoicesKind;
/**
 * @maxLength 200
 */
search?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListOpportunityNoteChoicesKind = typeof ListOpportunityNoteChoicesKind[keyof typeof ListOpportunityNoteChoicesKind];


export const ListOpportunityNoteChoicesKind = {
  opportunity: 'opportunity',
  catalogue_product: 'catalogue_product',
  catalogue_solution: 'catalogue_solution',
  catalogue_service: 'catalogue_service',
  business_case: 'business_case',
  quote: 'quote',
  tender: 'tender',
  library: 'library',
  managed_document: 'managed_document',
} as const;

export type ListOpportunityNoteChoices200ItemsItem = {
  id: string;
  title: string;
  revision: string;
};

export type ListOpportunityNoteChoices200 = {
  items: ListOpportunityNoteChoices200ItemsItem[];
  total: number;
  limit: number;
  offset: number;
};

export type ListOpportunityNoteHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListOpportunityNoteHistory200ItemsItem = {
  id: string;
  actorId: string;
  reason: string;
  createdAt: string;
  snapshot: OpportunityNote;
};

export type ListOpportunityNoteHistory200 = {
  items: ListOpportunityNoteHistory200ItemsItem[];
  total: number;
  limit: number;
  offset: number;
};

export type ReviewOpportunityNoteBody = {
  requestId: string;
  change: OpportunityNoteChange;
};

export type ConfirmOpportunityNoteBodyConfirmation = typeof ConfirmOpportunityNoteBodyConfirmation[keyof typeof ConfirmOpportunityNoteBodyConfirmation];


export const ConfirmOpportunityNoteBodyConfirmation = {
  CONFIRM: 'CONFIRM',
} as const;

export type ConfirmOpportunityNoteBody = {
  /** @pattern ^[a-f0-9]{64}$ */
  hash: string;
  confirmation: ConfirmOpportunityNoteBodyConfirmation;
};

export type ListStaffActivitiesParams = {
/**
 * @maxLength 200
 */
category?: string;
/**
 * @maxLength 200
 */
search?: string;
entityType?: ActivityEntityType;
entityId?: string;
type?: ActivityType;
/**
 * @maxLength 200
 */
ownerId?: string;
/**
 * @maxLength 120
 */
status?: string;
/**
 * @maxLength 120
 */
priority?: string;
/**
 * Inclusive scheduled end UTC date
 */
from?: string;
/**
 * Inclusive scheduled end UTC date
 */
to?: string;
/**
 * Include records flagged as test or demo data. They are excluded by default.
 */
includeTestData?: IncludeTestDataParameter;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListStaffActivityHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ReviewStaffActivityBody = {
  requestId: string;
  change: StaffActivityChange;
};

export type ConfirmStaffActivityBodyConfirmation = typeof ConfirmStaffActivityBodyConfirmation[keyof typeof ConfirmStaffActivityBodyConfirmation];


export const ConfirmStaffActivityBodyConfirmation = {
  CONFIRM: 'CONFIRM',
} as const;

export type ConfirmStaffActivityBody = {
  /**
     * @minLength 64
     * @maxLength 64
     */
  hash: string;
  confirmation: ConfirmStaffActivityBodyConfirmation;
};

export type ListActivitiesParams = {
entityType?: ActivityEntityType;
entityId?: string;
/**
 * Include records flagged as test or demo data. They are excluded by default.
 */
includeTestData?: IncludeTestDataParameter;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListPartnersParams = {
status?: PartnerStatus;
type?: PartnerType;
/**
 * Case-insensitive match on partner name.
 * @maxLength 200
 */
search?: string;
/**
 * Return partners with an active agreement expiring within this many days.
 * @minimum 1
 * @maximum 365
 */
agreementsExpiringWithinDays?: number;
/**
 * Include records flagged as test or demo data. They are excluded by default.
 */
includeTestData?: IncludeTestDataParameter;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListPartnerEmailsParams = {
accountId: string;
};

export type ListPortalSharesForStaffParams = {
accountId: string;
};

export type RevokePortalShareForStaffParams = {
accountId: string;
};

export type ListPartnerAgreementsParams = {
accountId: string;
};

export type ListPartnerInteractionsParams = {
accountId: string;
};

export type ListPartnerCollateralParams = {
accountId: string;
};

export type ListPlatformUsageTokenRatesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListPlatformUsageTokenBalancesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
/**
 * @maxLength 200
 */
search?: string;
};

export type ListPlatformUsageTokenGrantsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
tenantId?: string;
};

export type GetPlatformAgentVoicePolicySave200 = {
  policy: PlatformAgentVoicePolicy | null;
};

export type ListMyAiActivityParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListAssistantImageDraftsParams = {
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type SearchAssistantSourcesParams = {
/**
 * @minLength 2
 * @maxLength 200
 */
search: string;
/**
 * @minimum 1
 * @maximum 20
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListPlatformAssistantUsageParams = {
date?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListPlatformAiActivityParams = {
feature?: AiActivityFeature;
outcome?: AiActivityOutcome;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetPlatformAgentLimitsSave200 = {
  limits: PlatformAgentLimits | null;
};

export type ListPlatformFeaturesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListPlatformUsersParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
/**
 * @maxLength 200
 */
search?: string;
role?: AppUserRole;
};

export type ListPlatformTenantEntitiesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListPlatformTenantsParams = {
status?: ListPlatformTenantsStatus;
/**
 * @maxLength 120
 */
search?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListPlatformTenantsStatus = typeof ListPlatformTenantsStatus[keyof typeof ListPlatformTenantsStatus];


export const ListPlatformTenantsStatus = {
  active: 'active',
  suspended: 'suspended',
  archived: 'archived',
} as const;

export type ListPlatformRoleAssignmentsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListPlatformRolesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListPlatformGroupsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListPlatformGroupMembersParams = {
/**
 * @maxLength 120
 */
search?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListPlatformAuditParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
tenantId?: string;
/**
 * @maxLength 100
 */
action?: string;
/**
 * @maxLength 200
 */
actor?: string;
outcome?: ListPlatformAuditOutcome;
};

export type ListPlatformAuditOutcome = typeof ListPlatformAuditOutcome[keyof typeof ListPlatformAuditOutcome];


export const ListPlatformAuditOutcome = {
  succeeded: 'succeeded',
  denied: 'denied',
  failed: 'failed',
} as const;

export type ListMyEligibleRolesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListMyPermanentTenantRoleOptionsParams = {
tenantId: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListMyElevationsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListPlatformEligibleRolesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListPlatformElevationsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
status?: ListPlatformElevationsStatus;
tenantId?: string;
};

export type ListPlatformElevationsStatus = typeof ListPlatformElevationsStatus[keyof typeof ListPlatformElevationsStatus];


export const ListPlatformElevationsStatus = {
  active: 'active',
  expired: 'expired',
  revoked: 'revoked',
} as const;

export type ListPlatformRecoveryCustodiansParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
status?: ListPlatformRecoveryCustodiansStatus;
};

export type ListPlatformRecoveryCustodiansStatus = typeof ListPlatformRecoveryCustodiansStatus[keyof typeof ListPlatformRecoveryCustodiansStatus];


export const ListPlatformRecoveryCustodiansStatus = {
  active: 'active',
  revoked: 'revoked',
} as const;

export type ListPlatformRecoverySessionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
status?: ListPlatformRecoverySessionsStatus;
};

export type ListPlatformRecoverySessionsStatus = typeof ListPlatformRecoverySessionsStatus[keyof typeof ListPlatformRecoverySessionsStatus];


export const ListPlatformRecoverySessionsStatus = {
  active: 'active',
  expired: 'expired',
  ended: 'ended',
  revoked: 'revoked',
} as const;

export type ListTenantEligibleRolesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListTenantElevationsParams = {
status?: ListTenantElevationsStatus;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListTenantElevationsStatus = typeof ListTenantElevationsStatus[keyof typeof ListTenantElevationsStatus];


export const ListTenantElevationsStatus = {
  pending: 'pending',
  active: 'active',
  expired: 'expired',
  revoked: 'revoked',
} as const;

export type ListPlatformSupportSessionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
status?: ListPlatformSupportSessionsStatus;
tenantId?: string;
};

export type ListPlatformSupportSessionsStatus = typeof ListPlatformSupportSessionsStatus[keyof typeof ListPlatformSupportSessionsStatus];


export const ListPlatformSupportSessionsStatus = {
  active: 'active',
  expired: 'expired',
  revoked: 'revoked',
} as const;

export type GetTenantAdministration200 = {
  tenant: AccessProfile;
  memberCount: number;
  entityCount: number;
};

export type ListAdminBusinessFunctionsParams = {
/**
 * @maxLength 120
 */
staffSearch?: string;
/**
 * @minimum 1
 * @maximum 100
 */
staffLimit?: number;
/**
 * @minimum 0
 */
staffOffset?: number;
};

export type ListTenantRolesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListTenantGroupsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListTenantGroupMembersParams = {
/**
 * @maxLength 120
 */
search?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetEntityAdministrationParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListEntityAccessCandidatesParams = {
/**
 * @maxLength 200
 */
q?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListTenantEntitiesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListTenantSettingsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListTenantModulesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListTenantUsageTokenAllocationsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListTenantUsageTokenLedgerParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
from?: string;
to?: string;
entityId?: string;
/**
 * @maxLength 120
 */
actionKey?: string;
};

export type GetTenantUsageTokenReportParams = {
from?: string;
to?: string;
entityId?: string;
/**
 * @maxLength 120
 */
actionKey?: string;
};

export type ExportTenantUsageTokenLedgerParams = {
from?: string;
to?: string;
entityId?: string;
/**
 * @maxLength 120
 */
actionKey?: string;
};

export type UploadEntityPackageBody = {
  file: Blob | File;
  idempotencyKey: string;
};

export type ListEntityPackageJobsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetEntityPackageImportOptionsParams = {
/**
 * @minimum 1
 * @maximum 25
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetEntityPackagePreviewParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListEntityPackageConflictsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListTenantDataManagementJobsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
kind?: ListTenantDataManagementJobsKind;
};

export type ListTenantDataManagementJobsKind = typeof ListTenantDataManagementJobsKind[keyof typeof ListTenantDataManagementJobsKind];


export const ListTenantDataManagementJobsKind = {
  export: 'export',
  import: 'import',
} as const;

export type CreateTenantDataImportDryRunBody = {
  file: Blob | File;
};

export type ListDesignSystemsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListAccessContextsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetAccessAdministrationParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListAccessAuditParams = {
/**
 * @maxLength 150
 */
action?: string;
/**
 * @maxLength 150
 */
targetType?: string;
/**
 * @maxLength 200
 */
targetId?: string;
outcome?: ListAccessAuditOutcome;
/**
 * @maxLength 200
 */
actor?: string;
entityId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListAccessAuditOutcome = typeof ListAccessAuditOutcome[keyof typeof ListAccessAuditOutcome];


export const ListAccessAuditOutcome = {
  succeeded: 'succeeded',
  denied: 'denied',
  failed: 'failed',
} as const;

export type ListEventRsvpsParams = {
status?: EventRsvpStatus;
/**
 * Case-insensitive match on attendee name, email, or account
 * @maxLength 200
 */
search?: string;
/**
 * @minimum 1
 */
page?: number;
/**
 * @minimum 1
 * @maximum 100
 */
pageSize?: number;
};

export type ExportEventRsvpsParams = {
status?: EventRsvpStatus;
/**
 * Case-insensitive match on attendee name, email, or account
 * @maxLength 200
 */
search?: string;
};

export type GetAgentResearchStatusBody = {
  accessScope: AgentAccessScope;
};

export type ListPersonalWorkParams = {
scopeKind: ListPersonalWorkScopeKind;
tenantId?: string;
entityId?: string;
source?: PersonalWorkSource;
state?: PersonalWorkState;
/**
 * @maxLength 200
 */
search?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListPersonalWorkScopeKind = typeof ListPersonalWorkScopeKind[keyof typeof ListPersonalWorkScopeKind];


export const ListPersonalWorkScopeKind = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export type GetPersonalWorkResumeParams = {
scopeKind: GetPersonalWorkResumeScopeKind;
tenantId?: string;
entityId?: string;
/**
 * @maxLength 160
 */
expectedRevision?: string;
};

export type GetPersonalWorkResumeScopeKind = typeof GetPersonalWorkResumeScopeKind[keyof typeof GetPersonalWorkResumeScopeKind];


export const GetPersonalWorkResumeScopeKind = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export type ListAgentAttachmentsParams = {
scopeKind: ListAgentAttachmentsScopeKind;
tenantId?: string;
entityId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListAgentAttachmentsScopeKind = typeof ListAgentAttachmentsScopeKind[keyof typeof ListAgentAttachmentsScopeKind];


export const ListAgentAttachmentsScopeKind = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export type GetAgentAttachmentParams = {
scopeKind: GetAgentAttachmentScopeKind;
tenantId?: string;
entityId?: string;
};

export type GetAgentAttachmentScopeKind = typeof GetAgentAttachmentScopeKind[keyof typeof GetAgentAttachmentScopeKind];


export const GetAgentAttachmentScopeKind = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export type RemoveAgentAttachmentParams = {
scopeKind: RemoveAgentAttachmentScopeKind;
tenantId?: string;
entityId?: string;
};

export type RemoveAgentAttachmentScopeKind = typeof RemoveAgentAttachmentScopeKind[keyof typeof RemoveAgentAttachmentScopeKind];


export const RemoveAgentAttachmentScopeKind = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export type ListAgentAttachmentMembersParams = {
scopeKind: ListAgentAttachmentMembersScopeKind;
tenantId?: string;
entityId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListAgentAttachmentMembersScopeKind = typeof ListAgentAttachmentMembersScopeKind[keyof typeof ListAgentAttachmentMembersScopeKind];


export const ListAgentAttachmentMembersScopeKind = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export type GetAgentAttachmentUploadParams = {
scopeKind: GetAgentAttachmentUploadScopeKind;
tenantId?: string;
entityId?: string;
};

export type GetAgentAttachmentUploadScopeKind = typeof GetAgentAttachmentUploadScopeKind[keyof typeof GetAgentAttachmentUploadScopeKind];


export const GetAgentAttachmentUploadScopeKind = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export type ProcessAgentAttachmentBody = {
  accessScope: AgentAccessScope;
};

export type ListAgentAttachmentSectionsParams = {
scopeKind: ListAgentAttachmentSectionsScopeKind;
tenantId?: string;
entityId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListAgentAttachmentSectionsScopeKind = typeof ListAgentAttachmentSectionsScopeKind[keyof typeof ListAgentAttachmentSectionsScopeKind];


export const ListAgentAttachmentSectionsScopeKind = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export type ListAgentAttachmentSections200 = {
  items: AgentAttachmentSection[];
  total: number;
  limit: number;
  offset: number;
};

export type DownloadAgentAttachmentParams = {
scopeKind: DownloadAgentAttachmentScopeKind;
tenantId?: string;
entityId?: string;
};

export type DownloadAgentAttachmentScopeKind = typeof DownloadAgentAttachmentScopeKind[keyof typeof DownloadAgentAttachmentScopeKind];


export const DownloadAgentAttachmentScopeKind = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export type ListAgentSharedAttachmentsParams = {
tenantId: string;
entityId: string;
contextKind: ListAgentSharedAttachmentsContextKind;
recordId: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListAgentSharedAttachmentsContextKind = typeof ListAgentSharedAttachmentsContextKind[keyof typeof ListAgentSharedAttachmentsContextKind];


export const ListAgentSharedAttachmentsContextKind = {
  tender: 'tender',
  campaign: 'campaign',
  content_asset: 'content_asset',
  social_post: 'social_post',
  managed_document: 'managed_document',
  microsite: 'microsite',
  sow_preparation: 'sow_preparation',
} as const;

export type GetAgentSharedAttachmentUploadParams = {
tenantId: string;
entityId: string;
contextKind: GetAgentSharedAttachmentUploadContextKind;
recordId: string;
};

export type GetAgentSharedAttachmentUploadContextKind = typeof GetAgentSharedAttachmentUploadContextKind[keyof typeof GetAgentSharedAttachmentUploadContextKind];


export const GetAgentSharedAttachmentUploadContextKind = {
  tender: 'tender',
  campaign: 'campaign',
  content_asset: 'content_asset',
  social_post: 'social_post',
  managed_document: 'managed_document',
  microsite: 'microsite',
  sow_preparation: 'sow_preparation',
} as const;

export type GetAgentSharedAttachmentParams = {
tenantId: string;
entityId: string;
contextKind: GetAgentSharedAttachmentContextKind;
recordId: string;
};

export type GetAgentSharedAttachmentContextKind = typeof GetAgentSharedAttachmentContextKind[keyof typeof GetAgentSharedAttachmentContextKind];


export const GetAgentSharedAttachmentContextKind = {
  tender: 'tender',
  campaign: 'campaign',
  content_asset: 'content_asset',
  social_post: 'social_post',
  managed_document: 'managed_document',
  microsite: 'microsite',
  sow_preparation: 'sow_preparation',
} as const;

export type RemoveAgentSharedAttachmentParams = {
tenantId: string;
entityId: string;
contextKind: RemoveAgentSharedAttachmentContextKind;
recordId: string;
};

export type RemoveAgentSharedAttachmentContextKind = typeof RemoveAgentSharedAttachmentContextKind[keyof typeof RemoveAgentSharedAttachmentContextKind];


export const RemoveAgentSharedAttachmentContextKind = {
  tender: 'tender',
  campaign: 'campaign',
  content_asset: 'content_asset',
  social_post: 'social_post',
  managed_document: 'managed_document',
  microsite: 'microsite',
  sow_preparation: 'sow_preparation',
} as const;

export type ListAgentSharedAttachmentMembersParams = {
tenantId: string;
entityId: string;
contextKind: ListAgentSharedAttachmentMembersContextKind;
recordId: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListAgentSharedAttachmentMembersContextKind = typeof ListAgentSharedAttachmentMembersContextKind[keyof typeof ListAgentSharedAttachmentMembersContextKind];


export const ListAgentSharedAttachmentMembersContextKind = {
  tender: 'tender',
  campaign: 'campaign',
  content_asset: 'content_asset',
  social_post: 'social_post',
  managed_document: 'managed_document',
  microsite: 'microsite',
  sow_preparation: 'sow_preparation',
} as const;

export type ListAgentSharedAttachmentSectionsParams = {
tenantId: string;
entityId: string;
contextKind: ListAgentSharedAttachmentSectionsContextKind;
recordId: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListAgentSharedAttachmentSectionsContextKind = typeof ListAgentSharedAttachmentSectionsContextKind[keyof typeof ListAgentSharedAttachmentSectionsContextKind];


export const ListAgentSharedAttachmentSectionsContextKind = {
  tender: 'tender',
  campaign: 'campaign',
  content_asset: 'content_asset',
  social_post: 'social_post',
  managed_document: 'managed_document',
  microsite: 'microsite',
  sow_preparation: 'sow_preparation',
} as const;

export type DownloadAgentSharedAttachmentParams = {
tenantId: string;
entityId: string;
contextKind: DownloadAgentSharedAttachmentContextKind;
recordId: string;
};

export type DownloadAgentSharedAttachmentContextKind = typeof DownloadAgentSharedAttachmentContextKind[keyof typeof DownloadAgentSharedAttachmentContextKind];


export const DownloadAgentSharedAttachmentContextKind = {
  tender: 'tender',
  campaign: 'campaign',
  content_asset: 'content_asset',
  social_post: 'social_post',
  managed_document: 'managed_document',
  microsite: 'microsite',
  sow_preparation: 'sow_preparation',
} as const;

export type ListAgentSharedMessagesParams = {
tenantId: string;
entityId: string;
contextKind: ListAgentSharedMessagesContextKind;
recordId: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListAgentSharedMessagesContextKind = typeof ListAgentSharedMessagesContextKind[keyof typeof ListAgentSharedMessagesContextKind];


export const ListAgentSharedMessagesContextKind = {
  tender: 'tender',
  campaign: 'campaign',
  content_asset: 'content_asset',
  social_post: 'social_post',
  managed_document: 'managed_document',
  microsite: 'microsite',
  sow_preparation: 'sow_preparation',
} as const;

export type GetAgentSharedOperationParams = {
tenantId: string;
entityId: string;
contextKind: GetAgentSharedOperationContextKind;
recordId: string;
};

export type GetAgentSharedOperationContextKind = typeof GetAgentSharedOperationContextKind[keyof typeof GetAgentSharedOperationContextKind];


export const GetAgentSharedOperationContextKind = {
  tender: 'tender',
  campaign: 'campaign',
  content_asset: 'content_asset',
  social_post: 'social_post',
  managed_document: 'managed_document',
  microsite: 'microsite',
  sow_preparation: 'sow_preparation',
} as const;

export type ListAgentSharedConversationsParams = {
tenantId: string;
entityId: string;
contextKind: ListAgentSharedConversationsContextKind;
recordId: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListAgentSharedConversationsContextKind = typeof ListAgentSharedConversationsContextKind[keyof typeof ListAgentSharedConversationsContextKind];


export const ListAgentSharedConversationsContextKind = {
  tender: 'tender',
  campaign: 'campaign',
  content_asset: 'content_asset',
  social_post: 'social_post',
  managed_document: 'managed_document',
  microsite: 'microsite',
  sow_preparation: 'sow_preparation',
} as const;

export type GetAgentSharedConversationParams = {
tenantId: string;
entityId: string;
contextKind: GetAgentSharedConversationContextKind;
recordId: string;
};

export type GetAgentSharedConversationContextKind = typeof GetAgentSharedConversationContextKind[keyof typeof GetAgentSharedConversationContextKind];


export const GetAgentSharedConversationContextKind = {
  tender: 'tender',
  campaign: 'campaign',
  content_asset: 'content_asset',
  social_post: 'social_post',
  managed_document: 'managed_document',
  microsite: 'microsite',
  sow_preparation: 'sow_preparation',
} as const;

export type ListAgentConversationsParams = {
mode?: ListAgentConversationsMode;
scopeKind: ListAgentConversationsScopeKind;
tenantId?: string;
entityId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListAgentConversationsMode = typeof ListAgentConversationsMode[keyof typeof ListAgentConversationsMode];


export const ListAgentConversationsMode = {
  internal: 'internal',
  public_research: 'public_research',
} as const;

export type ListAgentConversationsScopeKind = typeof ListAgentConversationsScopeKind[keyof typeof ListAgentConversationsScopeKind];


export const ListAgentConversationsScopeKind = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export type GetAgentConversationParams = {
scopeKind: GetAgentConversationScopeKind;
tenantId?: string;
entityId?: string;
};

export type GetAgentConversationScopeKind = typeof GetAgentConversationScopeKind[keyof typeof GetAgentConversationScopeKind];


export const GetAgentConversationScopeKind = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export type ListAgentHandoffDestinationsParams = {
scopeKind: ListAgentHandoffDestinationsScopeKind;
tenantId?: string;
entityId?: string;
mode?: ListAgentHandoffDestinationsMode;
/**
 * @maxLength 120
 */
search?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListAgentHandoffDestinationsScopeKind = typeof ListAgentHandoffDestinationsScopeKind[keyof typeof ListAgentHandoffDestinationsScopeKind];


export const ListAgentHandoffDestinationsScopeKind = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export type ListAgentHandoffDestinationsMode = typeof ListAgentHandoffDestinationsMode[keyof typeof ListAgentHandoffDestinationsMode];


export const ListAgentHandoffDestinationsMode = {
  internal: 'internal',
  public_research: 'public_research',
} as const;

export type ListAgentPromptsParams = {
origin?: ListAgentPromptsOrigin;
/**
 * @minLength 1
 * @maxLength 100
 */
functionKey?: string;
/**
 * @minLength 1
 * @maxLength 100
 */
moduleKey?: string;
/**
 * @maxLength 200
 */
search?: string;
/**
 * @maxLength 60
 */
category?: string;
status?: ListAgentPromptsStatus;
visibility?: ListAgentPromptsVisibility;
scopeKind: ListAgentPromptsScopeKind;
tenantId?: string;
entityId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListAgentPromptsOrigin = typeof ListAgentPromptsOrigin[keyof typeof ListAgentPromptsOrigin];


export const ListAgentPromptsOrigin = {
  personal: 'personal',
  entity: 'entity',
  tenant: 'tenant',
} as const;

export type ListAgentPromptsStatus = typeof ListAgentPromptsStatus[keyof typeof ListAgentPromptsStatus];


export const ListAgentPromptsStatus = {
  all: 'all',
  active: 'active',
  inactive: 'inactive',
} as const;

export type ListAgentPromptsVisibility = typeof ListAgentPromptsVisibility[keyof typeof ListAgentPromptsVisibility];


export const ListAgentPromptsVisibility = {
  personal: 'personal',
  team: 'team',
} as const;

export type ListAgentPromptsScopeKind = typeof ListAgentPromptsScopeKind[keyof typeof ListAgentPromptsScopeKind];


export const ListAgentPromptsScopeKind = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export type GetAgentPromptParams = {
scopeKind: GetAgentPromptScopeKind;
tenantId?: string;
entityId?: string;
};

export type GetAgentPromptScopeKind = typeof GetAgentPromptScopeKind[keyof typeof GetAgentPromptScopeKind];


export const GetAgentPromptScopeKind = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export type ListAgentReviewSetsParams = {
scopeKind: ListAgentReviewSetsScopeKind;
tenantId?: string;
entityId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListAgentReviewSetsScopeKind = typeof ListAgentReviewSetsScopeKind[keyof typeof ListAgentReviewSetsScopeKind];


export const ListAgentReviewSetsScopeKind = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export type GetAgentReviewSetParams = {
scopeKind: GetAgentReviewSetScopeKind;
tenantId?: string;
entityId?: string;
};

export type GetAgentReviewSetScopeKind = typeof GetAgentReviewSetScopeKind[keyof typeof GetAgentReviewSetScopeKind];


export const GetAgentReviewSetScopeKind = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export type ListAgentMessagesParams = {
scopeKind: ListAgentMessagesScopeKind;
tenantId?: string;
entityId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListAgentMessagesScopeKind = typeof ListAgentMessagesScopeKind[keyof typeof ListAgentMessagesScopeKind];


export const ListAgentMessagesScopeKind = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
} as const;

export type StartAgentVoiceBody = {
  accessScope: AgentAccessScope;
  conversationId: string;
  context?: AgentRecordContext;
  requestId: string;
};

export type StartAgentVoice200 = {
  sessionId: string;
  /**
     * Null means no configured session duration; idle sessions still expire after 30 minutes without speech activity.
     * @nullable
     */
  expiresAt: string | null;
  /**
     * Original configured duration at admission; null means no quota.
     * @nullable
     */
  maxSeconds: number | null;
};

export type OperateAgentVoiceBodyOperation = typeof OperateAgentVoiceBodyOperation[keyof typeof OperateAgentVoiceBodyOperation];


export const OperateAgentVoiceBodyOperation = {
  transcribe: 'transcribe',
  speak: 'speak',
  end: 'end',
} as const;

export type OperateAgentVoiceBody = {
  accessScope: AgentAccessScope;
  /** Exact chat pin for cancelling an admission whose response was lost */
  conversationId?: string;
  context?: AgentRecordContext;
  sessionId: string;
  requestId: string;
  operation: OperateAgentVoiceBodyOperation;
  /**
     * Mono signed 16-bit little-endian PCM at 16000Hz, 0.1–30 seconds
     * @minLength 4268
     * @maxLength 1280000
     */
  audioBase64?: string;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  text?: string;
};

export type OperateAgentVoice200 = {
  /** @maxLength 4000 */
  text?: string;
  /** @maxLength 6990508 */
  audioBase64?: string;
  ended?: boolean;
};

export type GetBidSetupProgressParams = {
/**
 * @maxLength 200
 */
search?: string;
filesOnly?: boolean;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type CancelBidSetupBody = {
  /**
     * @minLength 3
     * @maxLength 1000
     */
  reason: string;
};

export type ListFilesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type UploadFileBody = {
  file: Blob | File;
};

export type ListFileLibraryParams = {
/**
 * Exact canonical library entry for authenticated app links; all source permissions still apply.
 */
entryId?: string;
grouping?: ListFileLibraryGrouping;
tenderId?: string;
campaignId?: string;
accountId?: string;
/**
 * @maxLength 200
 */
search?: string;
/**
 * Restrict eligible canonical identities before counts and paging.
 */
kind?: ListFileLibraryKind;
/**
 * @maxLength 80
 */
source?: string;
/**
 * @maxLength 40
 */
status?: string;
/**
 * @maxLength 500
 */
folder?: string;
/**
 * @maxLength 120
 */
type?: string;
/**
 * @maxLength 80
 */
label?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListFileLibraryGrouping = typeof ListFileLibraryGrouping[keyof typeof ListFileLibraryGrouping];


export const ListFileLibraryGrouping = {
  tenders: 'tenders',
  campaigns: 'campaigns',
  accounts: 'accounts',
} as const;

export type ListFileLibraryKind = typeof ListFileLibraryKind[keyof typeof ListFileLibraryKind];


export const ListFileLibraryKind = {
  file: 'file',
  document: 'document',
} as const;

export type ListTenantLibraryEntitiesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListTenantLibraryEntities200 = {
  items: AccessContextOption[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
};

export type ListTenantFileLibraryParams = {
/**
 * @maxLength 200
 */
search?: string;
/**
 * @maxLength 80
 */
source?: string;
/**
 * @maxLength 40
 */
status?: string;
/**
 * @maxLength 500
 */
folder?: string;
/**
 * @maxLength 120
 */
type?: string;
/**
 * @maxLength 80
 */
label?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListLibraryFileVersionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type UploadLibraryFileVersionParams = {
requestId: string;
versionKind: LibraryFileVersionKind;
expectedCurrentVersionId?: string;
/**
 * @maxLength 500
 */
note?: string;
};

export type UploadLibraryFileVersionBody = {
  file: Blob | File;
};

export type ListFileShareLinksParams = {
hostKind: FileHostKind;
hostId: string;
fileKind?: LibraryFileKind;
fileId?: string;
status?: ListFileShareLinksStatus;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListFileShareLinksStatus = typeof ListFileShareLinksStatus[keyof typeof ListFileShareLinksStatus];


export const ListFileShareLinksStatus = {
  active: 'active',
  expired: 'expired',
  revoked: 'revoked',
} as const;

export type ListRecycleBinParams = {
kind?: RecycleBinItemKind;
/**
 * @maxLength 200
 */
search?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListLibraryFoldersParams = {
/**
 * @maxLength 500
 */
parent?: string;
/**
 * @maxLength 120
 */
search?: string;
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListFileInventoryParams = {
kind?: ListFileInventoryKind;
/**
 * @maxLength 200
 */
search?: string;
/**
 * @maxLength 500
 */
folder?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListFileInventoryKind = typeof ListFileInventoryKind[keyof typeof ListFileInventoryKind];


export const ListFileInventoryKind = {
  file: 'file',
  document: 'document',
} as const;

export type ListStorageCleanupParams = {
state?: ListStorageCleanupState;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListStorageCleanupState = typeof ListStorageCleanupState[keyof typeof ListStorageCleanupState];


export const ListStorageCleanupState = {
  pending: 'pending',
  deleted: 'deleted',
  retained: 'retained',
} as const;

export type PreviewManagedDocumentImportBody = {
  file: Blob | File;
};

export type AdoptManagedDocumentImportBody = {
  file: Blob | File;
  /**
     * @minLength 1
     * @maxLength 200
     */
  title?: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  category?: string;
  /** @maxLength 500 */
  folder?: string;
};

export type GetManagedDocumentSectionOutcomeParams = {
clientRequestId: string;
};

export type GetManagedDocumentSectionOutcome200 = {
  /** @nullable */
  appliedRevision: number | null;
  workspace: ManagedDocumentWorkspace;
};

export type GetManagedDocumentCollaborationParams = {
sessionId: string;
clientRequestId?: string;
};

export type GetManagedDocumentEditingReviewParams = {
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 1000
 */
offset?: number;
showResolved?: boolean;
showDecided?: boolean;
/**
 * Show only tracked changes recorded by this person.
 */
author?: string;
clientRequestId?: string;
};

export type UploadManagedDocumentImageBodyContentType = typeof UploadManagedDocumentImageBodyContentType[keyof typeof UploadManagedDocumentImageBodyContentType];


export const UploadManagedDocumentImageBodyContentType = {
  'image/png': 'image/png',
  'image/jpeg': 'image/jpeg',
  'image/gif': 'image/gif',
  'image/webp': 'image/webp',
} as const;

export type UploadManagedDocumentImageBody = {
  contentType: UploadManagedDocumentImageBodyContentType;
  /**
     * @minLength 4
     * @maxLength 2796204
     */
  dataBase64: string;
};

export type UploadManagedDocumentImage201 = {
  /** @maxLength 200 */
  src: string;
};

export type ListManagedDocumentVersionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListDocumentStylesParams = {
/**
 * @maxLength 80
 */
search?: string;
includeArchived?: boolean;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListDocumentStyleTemplatesParams = {
/**
 * @maxLength 80
 */
search?: string;
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
};

export type ListContentDocumentBindingsParams = {
targetKind?: ListContentDocumentBindingsTargetKind;
assetId?: string;
/**
 * @pattern ^[A-Za-z0-9_-]{1,120}$
 */
componentId?: string;
documentId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
};

export type ListContentDocumentBindingsTargetKind = typeof ListContentDocumentBindingsTargetKind[keyof typeof ListContentDocumentBindingsTargetKind];


export const ListContentDocumentBindingsTargetKind = {
  content_hub_asset: 'content_hub_asset',
  microsite_rich_content: 'microsite_rich_content',
} as const;

export type RestoreManagedDocumentVersionBody = {
  /**
     * @minLength 1
     * @maxLength 2000
     */
  reason: string;
  /** @minimum 1 */
  expectedRevision: number;
};

export type ListManagedDocumentTemplatesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
/**
 * @maxLength 200
 */
search?: string;
};

export type ListManagedDocumentTemplates200ItemsItem = {
  id: string;
  title: string;
  revision: number;
  contentHash: string;
  sections: string[];
};

export type ListManagedDocumentTemplates200 = {
  total: number;
  limit: number;
  offset: number;
  items: ListManagedDocumentTemplates200ItemsItem[];
};

export type PreviewManagedDocumentTemplateParams = {
/**
 * @minimum 1
 */
expectedRevision: number;
};

export type DownloadManagedDocumentDraftParams = {
/**
 * @minimum 1
 */
expectedRevision: number;
format: DownloadManagedDocumentDraftFormat;
projection?: DownloadManagedDocumentDraftProjection;
};

export type DownloadManagedDocumentDraftFormat = typeof DownloadManagedDocumentDraftFormat[keyof typeof DownloadManagedDocumentDraftFormat];


export const DownloadManagedDocumentDraftFormat = {
  docx: 'docx',
  pdf: 'pdf',
  markdown: 'markdown',
} as const;

export type DownloadManagedDocumentDraftProjection = typeof DownloadManagedDocumentDraftProjection[keyof typeof DownloadManagedDocumentDraftProjection];


export const DownloadManagedDocumentDraftProjection = {
  clean: 'clean',
  review: 'review',
} as const;

export type DownloadManagedDocumentVersionParams = {
format: DownloadManagedDocumentVersionFormat;
projection?: DownloadManagedDocumentVersionProjection;
};

export type DownloadManagedDocumentVersionFormat = typeof DownloadManagedDocumentVersionFormat[keyof typeof DownloadManagedDocumentVersionFormat];


export const DownloadManagedDocumentVersionFormat = {
  docx: 'docx',
  pdf: 'pdf',
  markdown: 'markdown',
} as const;

export type DownloadManagedDocumentVersionProjection = typeof DownloadManagedDocumentVersionProjection[keyof typeof DownloadManagedDocumentVersionProjection];


export const DownloadManagedDocumentVersionProjection = {
  clean: 'clean',
  review: 'review',
} as const;

export type ListDocumentConversationsParams = {
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListDocumentConversationMessagesParams = {
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListPortalFinanceInvoicesParams = {
status?: XeroInvoiceStatus;
dateFrom?: string;
dateTo?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListPortalFinancePaymentsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListPortalSowRequestsParams = {
/**
 * @minimum 1
 * @maximum 20
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListServiceLinesParams = {
areaId?: string;
};

export type ListServiceComponentsParams = {
lineId?: string;
};

export type ListCatalogueDeliverablesParams = {
productId?: string;
moduleId?: string;
search?: string;
};

export type GetNextDeliverableCodeParams = {
category?: string;
};

export type ListCatalogueServicesParams = {
componentId?: string;
isFeatured?: boolean;
search?: string;
};

export type ListCatalogueServiceLinksParams = {
itemType?: string;
itemId?: string;
offeringId?: string;
};

export type ListCatalogueCollateralsParams = {
productId?: string;
solutionId?: string;
category?: string;
};

export type ListPartnerProductsParams = {
accountId: string;
};

export type ListRankedProspectsParams = {
/**
 * @minimum 0
 * @maximum 100
 */
minScore?: number;
industry?: string;
sort?: ListRankedProspectsSort;
/**
 * @minimum 1
 * @maximum 250
 */
limit?: number;
};

export type ListRankedProspectsSort = typeof ListRankedProspectsSort[keyof typeof ListRankedProspectsSort];


export const ListRankedProspectsSort = {
  score_desc: 'score_desc',
  score_asc: 'score_asc',
  account: 'account',
} as const;

export type RecomputePortfolioScores200 = {
  /** @minimum 0 */
  updated: number;
};

export type ListOpportunityOutcomesParams = {
outcome?: ListOpportunityOutcomesOutcome;
reasonCode?: string;
/**
 * @minimum 1
 * @maximum 250
 */
limit?: number;
};

export type ListOpportunityOutcomesOutcome = typeof ListOpportunityOutcomesOutcome[keyof typeof ListOpportunityOutcomesOutcome];


export const ListOpportunityOutcomesOutcome = {
  won: 'won',
  lost: 'lost',
} as const;

export type ListOpportunityOutcomes200 = {
  items: OpportunityOutcome[];
};

export type GenerateMarketAnalysisBody = {
  /**
     * @minimum 1
     * @maximum 365
     */
  lookbackDays?: number;
};

export type ListNextBestActionsParams = {
accountId?: string;
status?: ListNextBestActionsStatus;
/**
 * @minimum 1
 * @maximum 250
 */
limit?: number;
};

export type ListNextBestActionsStatus = typeof ListNextBestActionsStatus[keyof typeof ListNextBestActionsStatus];


export const ListNextBestActionsStatus = {
  open: 'open',
  completed: 'completed',
  dismissed: 'dismissed',
} as const;

export type ListNextBestActions200 = {
  items: NextBestAction[];
};

export type UpdateNextBestActionStatusBodyStatus = typeof UpdateNextBestActionStatusBodyStatus[keyof typeof UpdateNextBestActionStatusBodyStatus];


export const UpdateNextBestActionStatusBodyStatus = {
  completed: 'completed',
  dismissed: 'dismissed',
} as const;

export type UpdateNextBestActionStatusBody = {
  status: UpdateNextBestActionStatusBodyStatus;
};

export type ListRelationshipHealthParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
relationshipType?: AccountRelationshipType;
audience?: CrmAudience;
evidenceState?: RelationshipHealthEvidenceState;
includeTestData?: boolean;
};

export type RefreshRelationshipHealthBody = {
  /**
     * @minItems 1
     * @maxItems 100
     */
  accountIds?: string[];
  relationshipType?: AccountRelationshipType;
  audience?: CrmAudience;
  includeTestData?: boolean;
};

export type ListRelationshipHealthSnapshotsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
accountId?: string;
};

export type ListOutreachPlansParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
status?: OutreachPlanStatus;
};

export type ListOutreachPlanHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListOutreachExecutionsParams = {
status?: OutreachExecutionStatus;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListOutreachScheduleParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListOutreachReplyStatesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
status?: OutreachReplyStateStatus;
planId?: string;
};

export type ListRelationshipHealthFeedbackParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
planId?: string;
nextActionId?: string;
feedbackType?: RelationshipHealthFeedbackType;
};

export type ListRelationshipHealthLearningVersionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetRelationshipHealthAutomationReportParams = {
from: string;
to: string;
};

export type ListNextActionFeedParams = {
/**
 * Read one exact open action and recheck its source lifecycle without generating or executing work
 */
actionId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
/**
 * Restrict the unified feed to actions linked to one account
 */
accountId?: string;
/**
 * Restrict actions to accounts in one CRM audience; omit for the unified all-audience feed
 */
audience?: CrmAudience;
};

export type ListNextActionFeed200 = {
  items: NextActionFeedItem[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
  /**
     * Completion time of the most recent signal-driven engine run
     * @nullable
     */
  generatedAt: string | null;
};

export type RefreshNextActionsBody = {
  /** @maxLength 200 */
  requestedBy?: string;
  audience?: CrmAudience;
};

export type SnoozeNextActionBody = {
  /**
     * @minimum 1
     * @maximum 720
     */
  hours?: number;
};

export type ApproveNextActionBody = {
  /** Stable idempotency key reused when the same approval is retried */
  requestId?: string;
  /**
     * Optional edited recipient (defaults to the prepared draft recipient)
     * @maxLength 320
     */
  to?: string;
  /**
     * Optional edited subject (defaults to the prepared draft subject)
     * @maxLength 300
     */
  subject?: string;
  /**
     * Optional edited body (defaults to the prepared draft body)
     * @maxLength 20000
     */
  body?: string;
  /** @maxLength 200 */
  approvedBy?: string;
};

export type GetAcquisitionFunnelParams = {
from?: string;
to?: string;
/**
 * @minimum 1
 * @maximum 730
 */
days?: number;
channel?: string;
campaign?: string;
};

export type RefreshOpportunityAttributions200 = {
  /** @minimum 0 */
  opportunities: number;
  /** @minimum 0 */
  updated: number;
};

export type GetAcquisitionChannelPerformanceParams = {
from?: string;
to?: string;
/**
 * @minimum 1
 * @maximum 730
 */
days?: number;
};

export type ListAcquisitionReports200 = {
  items: AcquisitionReport[];
};

export type ListAccountProductFitParams = {
accountId?: string;
productId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type CompleteTenderSharePointConnectionParams = {
/**
 * @minLength 1
 * @maxLength 8192
 */
code: string;
state: string;
};

export type ListTenderSharePointSitesParams = {
/**
 * @minLength 2
 * @maxLength 100
 */
search: string;
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @maxLength 4096
 */
cursor?: string;
};

export type ListTenderSharePointDrivesParams = {
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @maxLength 4096
 */
cursor?: string;
};

export type ListTenderSharePointFoldersParams = {
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @maxLength 4096
 */
cursor?: string;
};

export type CompleteTenderEmbeddedConnectionParams = {
/**
 * @minLength 1
 * @maxLength 8192
 */
code: string;
state: string;
};

export type ListTenderEmbeddedContainersParams = {
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @maxLength 4096
 */
cursor?: string;
};

export type GetTenderPreparationParams = {
limit?: number;
offset?: number;
};

export type PrepareTenderDocumentsBody = {
  /** @minimum 1 */
  expectedRevision: number;
};

export type ConfirmTenderPreparationBody = {
  /** @pattern ^[a-f0-9]{64}$ */
  hash: string;
};

export type ConfirmTenderPreparationAccountBody = {
  /** @pattern ^[a-f0-9]{64}$ */
  hash: string;
};

export type CancelTenderPreparationReviewBody = {
  /** @pattern ^[a-f0-9]{64}$ */
  hash: string;
};

export type ReviewTenderPreparationAccountBodyAccountKind = typeof ReviewTenderPreparationAccountBodyAccountKind[keyof typeof ReviewTenderPreparationAccountBodyAccountKind];


export const ReviewTenderPreparationAccountBodyAccountKind = {
  organization: 'organization',
  person: 'person',
} as const;

export type ReviewTenderPreparationAccountBodyRelationshipType = typeof ReviewTenderPreparationAccountBodyRelationshipType[keyof typeof ReviewTenderPreparationAccountBodyRelationshipType];


export const ReviewTenderPreparationAccountBodyRelationshipType = {
  b2b_customer: 'b2b_customer',
  b2c_consumer: 'b2c_consumer',
  partner: 'partner',
  supplier: 'supplier',
  investor: 'investor',
  prospect: 'prospect',
  other: 'other',
} as const;

export type ReviewTenderPreparationAccountBody = {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  accountKind: ReviewTenderPreparationAccountBodyAccountKind;
  relationshipType: ReviewTenderPreparationAccountBodyRelationshipType;
  /**
     * @maxLength 2000
     * @nullable
     */
  website?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  industry?: string | null;
  /**
     * @maxLength 100
     * @nullable
     */
  phone?: string | null;
  /**
     * @maxLength 320
     * @nullable
     */
  email?: string | null;
  /**
     * @maxLength 253
     * @nullable
     */
  emailDomain?: string | null;
  /**
     * @maxLength 500
     * @nullable
     */
  addressLine1?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  city?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  stateProvince?: string | null;
  /**
     * @maxLength 50
     * @nullable
     */
  postcode?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  country?: string | null;
  /**
     * @maxLength 10000
     * @nullable
     */
  notes?: string | null;
  /**
     * @minimum 0
     * @maximum 99999999999999
     * @nullable
     */
  annualRevenue?: number | null;
  /**
     * @minimum 0
     * @maximum 2147483647
     * @nullable
     */
  numberOfEmployees?: number | null;
};

export type ListProposalsParams = {
type?: ProposalType;
accountId?: string;
quoteRequestId?: string;
ownerAppUserId?: string;
/**
 * @minLength 1
 * @maxLength 50
 */
status?: string;
/**
 * @maxLength 200
 */
search?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListTendersParams = {
accountId?: string;
quoteRequestId?: string;
status?: TenderStatus;
ownerAppUserId?: string;
/**
 * @maxLength 200
 */
search?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListTenderEventsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListTenderFilesParams = {
/**
 * @maxLength 200
 */
search?: string;
/**
 * @maxLength 200
 */
type?: string;
category?: ListTenderFilesCategory;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListTenderFilesCategory = typeof ListTenderFilesCategory[keyof typeof ListTenderFilesCategory];


export const ListTenderFilesCategory = {
  instructions: 'instructions',
  buyer_template: 'buyer_template',
  addendum: 'addendum',
  supporting_collateral: 'supporting_collateral',
  uploads: 'uploads',
  working: 'working',
  submission: 'submission',
  response: 'response',
  entity_grounding: 'entity_grounding',
  tender_grounding: 'tender_grounding',
} as const;

export type ListTenderFiles200 = {
  items: TenderBrowseFile[];
  total: number;
  limit: number;
  offset: number;
};

export type ListTenderSourcesParams = {
/**
 * @maxLength 200
 */
search?: string;
uploadStatus?: ListTenderSourcesUploadStatus;
kind?: TenderSourceKind;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListTenderSourcesUploadStatus = typeof ListTenderSourcesUploadStatus[keyof typeof ListTenderSourcesUploadStatus];


export const ListTenderSourcesUploadStatus = {
  staging: 'staging',
  available: 'available',
  failed: 'failed',
} as const;

export type ListTenderSourceRevisionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListTenderResponseSectionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListTenderRequirementsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type SearchTenderEvidenceParams = {
workspaceId?: string;
/**
 * @minLength 2
 * @maxLength 500
 */
q: string;
mode: TenderEvidenceDiscoveryMode;
/**
 * @minimum 1
 * @maximum 25
 */
limit?: number;
};

export type ListTenderEvidenceParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListTenderAiRunsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListTenderProposalsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListTenderPackagesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListTenderExportsParams = {
/**
 * @maxLength 200
 */
search?: string;
status?: ListTenderExportsStatus;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListTenderExportsStatus = typeof ListTenderExportsStatus[keyof typeof ListTenderExportsStatus];


export const ListTenderExportsStatus = {
  queued: 'queued',
  generated: 'generated',
  saved: 'saved',
  failed: 'failed',
} as const;

export type ListTenderReviewsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListQuoteTendersParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListQuoteRequestsParams = {
kind?: QuoteKind;
status?: QuoteRequestStatus;
accountId?: string;
opportunityId?: string;
approvalStatus?: QuoteApprovalStatus;
source?: QuoteRequestSource;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type DownloadQuotePricingScheduleParams = {
audience?: DownloadQuotePricingScheduleAudience;
};

export type DownloadQuotePricingScheduleAudience = typeof DownloadQuotePricingScheduleAudience[keyof typeof DownloadQuotePricingScheduleAudience];


export const DownloadQuotePricingScheduleAudience = {
  customer: 'customer',
  internal: 'internal',
} as const;

export type ExpireDueQuotes200 = {
  /** @minimum 0 */
  expired: number;
};

export type ApplyQuotePriceListBody = {
  priceListId: string;
};

export type ListQuoteRevisionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListQuoteEventsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListOpportunityQuotesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListAccountQuotesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListXeroItemsParams = {
/**
 * Matches item code, name, or description
 * @maxLength 100
 */
search?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListXeroContactsParams = {
/**
 * Matches contact name or email
 * @maxLength 100
 */
search?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListXeroInvoicesParams = {
status?: XeroInvoiceStatus;
quoteRequestId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListXeroBankTransactionsParams = {
status?: XeroBankTxnStatus;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListXeroMatchSuggestionsParams = {
status?: XeroSuggestionStatus;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type GetXeroReconciliationReportParams = {
/**
 * Aging threshold in days for unmatched AUTHORISED invoices
 * @minimum 0
 * @maximum 365
 */
days?: number;
};

export type ListBusinessCaseTemplatesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
catalogueProductId?: string;
};

export type ListSavedBusinessCasesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
templateId?: string;
accountId?: string;
};

export type ListDocumentSuites200 = {
  items: DocumentSuite[];
};

export type ListCatalogueDocumentsParams = {
suiteId?: string;
itemType?: CatalogueDocumentItemType;
itemId?: string;
};

export type ListCatalogueDocuments200 = {
  items: CatalogueDocument[];
};

export type ListCatalogueArtifactsParams = {
documentId?: string;
};

export type ListCatalogueArtifacts200 = {
  items: CatalogueArtifact[];
};

export type DeleteWebHarvestRun200 = {
  /** @minimum 0 */
  deletedCount: number;
};

export type ListKnowledgeFilesParams = {
suiteId?: string;
status?: KnowledgeFileStatus;
sourceKind?: KnowledgeSourceKind;
harvestRunId?: string;
/**
 * @minimum 1
 */
page?: number;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
};

export type ListKnowledgeFiles200 = {
  items: KnowledgeFile[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  page: number;
  /** @minimum 1 */
  limit: number;
};

export type ListAnalysisSessionsParams = {
/**
 * @minimum 1
 */
page?: number;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
};

export type ListAnalysisSessions200 = {
  items: AnalysisSession[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  page: number;
  /** @minimum 1 */
  limit: number;
};

export type GetKnowledgeSourceParams = {
/**
 * @minimum 1
 */
page?: number;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
chunkId?: string;
};

export type ListKnowledgeSharePointItemsParams = {
/**
 * @minimum 1
 */
page?: number;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
};

export type CompleteKnowledgeSharePointConnectionParams = {
/**
 * @minLength 1
 * @maxLength 8192
 */
code: string;
state: string;
};

export type ListKnowledgeSharePointConnectionsParams = {
/**
 * @minimum 1
 */
page?: number;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
};

export type ListKnowledgeSharePointConnections200 = {
  items: KnowledgeSharePointConnection[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  page: number;
  /** @minimum 1 */
  limit: number;
};

export type ListKnowledgeSharePointSitesParams = {
/**
 * @minLength 2
 * @maxLength 100
 */
search: string;
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @maxLength 4096
 */
cursor?: string;
};

export type ListKnowledgeSharePointSyncRunsParams = {
/**
 * @minimum 1
 */
page?: number;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
};

export type ListKnowledgeSharePointDrivesParams = {
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @maxLength 4096
 */
cursor?: string;
};

export type ListKnowledgeSharePointFoldersParams = {
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @maxLength 4096
 */
cursor?: string;
};

export type GetCrmProgrammeOperationsParams = {
/**
 * @minimum 1
 * @maximum 365
 */
rangeDays?: number;
};

export type ListLegacySourceRecordsParams = {
domain?: string;
sourceTable?: string;
search?: string;
/**
 * @minimum 1
 */
page?: number;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
};

export type ListVideoProjectsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
status?: VideoProjectStatus;
};

export type ListVideoProjectJobsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetVideoGenerationUsageParams = {
from?: string;
to?: string;
};

export type ListContentHubMediaParams = {
kind?: ContentHubMediaKind;
status?: ContentHubMediaStatus;
origin?: ListContentHubMediaOrigin;
/**
 * Include individual storyboard scene outputs; final videos and single clips are returned by default
 */
includeSceneClips?: boolean;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListContentHubMediaOrigin = typeof ListContentHubMediaOrigin[keyof typeof ListContentHubMediaOrigin];


export const ListContentHubMediaOrigin = {
  uploaded: 'uploaded',
  ai_generated: 'ai_generated',
} as const;

export type UploadContentHubMediaBody = {
  file: Blob | File;
};

export type DeleteContentHubMediaParams = {
/**
 * @minLength 3
 * @maxLength 500
 */
reason: string;
};

export type ListEntityProfileEnrichmentsParams = {
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type UploadEntityProfileLogoBody = {
  file: Blob | File;
};

export type ListContentHubBookingsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
status?: ContentHubBookingStatus;
};

export type ListContentHubBookingSlotsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
includePast?: boolean;
};

export type ListContentHubAssetsParams = {
kind?: ContentHubAssetKind;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListContentHubAssetVersionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListContentHubIntelligenceItemsParams = {
status?: ListContentHubIntelligenceItemsStatus;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListContentHubIntelligenceItemsStatus = typeof ListContentHubIntelligenceItemsStatus[keyof typeof ListContentHubIntelligenceItemsStatus];


export const ListContentHubIntelligenceItemsStatus = {
  pending_review: 'pending_review',
  reviewed: 'reviewed',
  converting: 'converting',
  rejected: 'rejected',
  converted: 'converted',
} as const;

export type ListContentHubAdvertisingCampaignsParams = {
provider?: SocialNetwork;
status?: ContentHubAdvertisingStatus;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListContentHubAdvertisingCampaignVersionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListContentHubAdvertisingCampaignAttemptsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListContentHubReviewRequestsParams = {
status?: ListContentHubReviewRequestsStatus;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListContentHubReviewRequestsStatus = typeof ListContentHubReviewRequestsStatus[keyof typeof ListContentHubReviewRequestsStatus];


export const ListContentHubReviewRequestsStatus = {
  draft: 'draft',
  queued: 'queued',
  sent: 'sent',
  opened: 'opened',
  clicked: 'clicked',
  completed: 'completed',
  failed: 'failed',
} as const;

export type SendContentHubReviewRequestBody = {
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason?: string;
};

export type ListContentHubAutomationRunsParams = {
category?: ListContentHubAutomationRunsCategory;
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 1000
 */
offset?: number;
};

export type ListContentHubAutomationRunsCategory = typeof ListContentHubAutomationRunsCategory[keyof typeof ListContentHubAutomationRunsCategory];


export const ListContentHubAutomationRunsCategory = {
  generation: 'generation',
  social_publication: 'social_publication',
  inbox_sync: 'inbox_sync',
  messaging: 'messaging',
  email_delivery: 'email_delivery',
  advertising: 'advertising',
} as const;

export type GetContentHubReportParams = {
from: string;
to: string;
/**
 * @maxItems 7
 */
networks?: SocialNetwork[];
};

export type ExportContentHubReportParams = {
from: string;
to: string;
/**
 * @maxItems 7
 */
networks?: SocialNetwork[];
};

export type ListSocialConnectionAuditParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type CompleteSocialOAuthParams = {
/**
 * @minLength 1
 */
code: string;
/**
 * @minLength 1
 */
state: string;
};

export type GetSocialOAuthAccountSelectionParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListCampaignOutputsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListCampaignOutputs200 = {
  items: CampaignOutputState[];
  total: number;
  limit: number;
  offset: number;
};

export type PrepareManualAdHandoffParams = {
documentId: string;
/**
 * @minimum 1
 */
expectedRevision: number;
};

export type PrepareManualAdHandoff200Status = typeof PrepareManualAdHandoff200Status[keyof typeof PrepareManualAdHandoff200Status];


export const PrepareManualAdHandoff200Status = {
  handoff_prepared: 'handoff_prepared',
} as const;

export type PrepareManualAdHandoff200 = {
  text: string;
  filename: string;
  revision: number;
  preparedAt: string;
  status: PrepareManualAdHandoff200Status;
};

export type ReviewCampaignOutputBody = {
  conversationId: string;
  change: CampaignOutputChange;
};

export type ReviewSocialProductionBody = {
  conversationId: string;
  change: SocialProductionChange;
};

export type ListSocialPostsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
status?: SocialPostStatus;
network?: SocialNetwork;
/**
 * @maxLength 200
 */
search?: string;
};

export type ListSocialPostRevisionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ApproveSocialPostBody = {
  /** @minimum 1 */
  expectedVersion: number;
  /** After approval, automatically schedule the post at the next free business slot in the entity timezone */
  autoSchedule?: boolean;
};

export type RunSocialPublishingQueue200 = {
  processed: number;
};

export type ListSocialInboxParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
network?: SocialNetwork;
status?: ListSocialInboxStatus;
};

export type ListSocialInboxStatus = typeof ListSocialInboxStatus[keyof typeof ListSocialInboxStatus];


export const ListSocialInboxStatus = {
  unread: 'unread',
  open: 'open',
  replied: 'replied',
  archived: 'archived',
} as const;

export type ReplySocialInboxItemBody = {
  /**
     * @minLength 1
     * @maxLength 5000
     */
  reply: string;
  requestId: string;
};

export type ListSocialMessageRulesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListSocialMessageRuleHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListSocialMessageRuleExecutionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type SyncSocialAnalytics200 = {
  synced: number;
};

export type CompleteMicrosoft365EmailOAuthParams = {
code: string;
state: string;
};

export type ListEmailSequences200 = {
  items: EmailSequence[];
};

export type ListEmailSequenceEnrollmentsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type RunEmailSequenceEngine200 = {
  processed: number;
  sent: number;
};

export type ListCampaignBriefHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListCampaignBriefHistory200 = {
  /** @maxItems 50 */
  items: CampaignBrief[];
  total: number;
  limit: number;
  offset: number;
};

export type PreviewCampaignAudienceParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ReviewCampaignMessagingBody = {
  conversationId: string;
  change: CampaignMessagingChange;
};

export type ReviewCampaignBriefBody = {
  requestId: string;
  change: CampaignBriefChange;
};

export type ConfirmCampaignBriefBody = {
  hash: string;
};

export type ListEmailCampaignsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
/**
 * Exact canonical campaign backlink; scoped to the active entity.
 */
campaignId?: string;
};

export type ListEmailCampaigns200 = {
  items: EmailCampaign[];
  /** @minimum 0 */
  total?: number;
};

export type ListEmailCampaignDeliveriesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type PreviewEmailRecipientsBody = {
  /**
     * @minItems 1
     * @maxItems 500
     */
  recipients: EmailRecipient[];
};

export type ReceiveTwilioSmsStatusParams = {
ref: string;
};

export type PreviewSmsRecipientsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListSmsDeliveriesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetContactSmsHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListEmailMessages200 = {
  items: EmailMessage[];
};

export type SyncEmailReplies200 = {
  synced: number;
};

export type SuggestEmailReply200 = {
  suggestion: string;
  auditId: string;
  strategicBriefs: StrategicBriefSource[];
};

export type ListCommunicationExtractions200 = {
  items: CommunicationExtraction[];
};

export type ReviewCommunicationExtractionBodyStatus = typeof ReviewCommunicationExtractionBodyStatus[keyof typeof ReviewCommunicationExtractionBodyStatus];


export const ReviewCommunicationExtractionBodyStatus = {
  approved: 'approved',
  rejected: 'rejected',
  completed: 'completed',
} as const;

export type ReviewCommunicationExtractionBody = {
  status: ReviewCommunicationExtractionBodyStatus;
  reviewedBy?: string;
};

export type TrackEmailClickParams = {
/**
 * Original destination carried by the tracked link; must match a URL recorded in the tracked message body
 */
url: string;
};

export type ListMailboxIntakeConnectionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ReceiveMailboxGraphNotificationsParams = {
/**
 * @minLength 1
 * @maxLength 4096
 */
validationToken?: string;
};

export type ListMailboxIntakeMailboxesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
status?: MailboxIntakeStatus;
};

export type ListMailboxRoutingPromptRevisionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListMailboxRoutingCandidatesParams = {
mailboxId?: string;
status?: MailboxRoutingCandidateStatus;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetMailboxHistoryPreviewParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListMailboxIntakeJobsParams = {
mailboxId?: string;
kind?: MailboxIntakeJobKind;
status?: MailboxIntakeJobStatus;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListMailboxRoutingExecutionsParams = {
mailboxId?: string;
enquiryId?: string;
opportunityId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ClearOutlookStyleProfile200 = {
  cleared: boolean;
};

export type GetOutlookDevOpsLinkParams = {
/**
 * @minLength 1
 * @maxLength 1024
 */
stableMessageId: string;
};

export type DeleteOutlookDevOpsLinkParams = {
/**
 * @minLength 1
 * @maxLength 1024
 */
stableMessageId: string;
};

export type DeleteOutlookDevOpsLink200 = {
  removed: boolean;
};

export type ListIngestionRules200 = {
  items: IngestionRule[];
};

export type RunIngestionRule200 = {
  fetched: number;
  ingested: number;
  duplicates: number;
  classified: number;
  autoAttached: number;
  errors: string[];
};

export type IngestWebSignalDocument201 = {
  document: IngestedDocument;
  candidate?: WebSignalCandidate | null;
  duplicate: boolean;
};

export type ListIngestedDocumentsParams = {
ruleId?: string;
/**
 * @minimum 1
 * @maximum 250
 */
limit?: number;
};

export type ListIngestedDocuments200 = {
  items: IngestedDocument[];
};

export type ListWebSignalCandidatesParams = {
status?: ListWebSignalCandidatesStatus;
accountId?: string;
/**
 * Only candidates in this industry (case-insensitive)
 * @minLength 1
 * @maxLength 120
 */
industry?: string;
/**
 * Only candidates that have no industry yet
 */
withoutIndustry?: boolean;
/**
 * Only candidates found by this job
 */
jobId?: string;
/**
 * Filter by the recorded Pursue decision
 */
decision?: ListWebSignalCandidatesDecision;
/**
 * @minimum 1
 * @maximum 250
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListWebSignalCandidatesStatus = typeof ListWebSignalCandidatesStatus[keyof typeof ListWebSignalCandidatesStatus];


export const ListWebSignalCandidatesStatus = {
  pending: 'pending',
  confirmed: 'confirmed',
  dismissed: 'dismissed',
} as const;

export type ListWebSignalCandidatesDecision = typeof ListWebSignalCandidatesDecision[keyof typeof ListWebSignalCandidatesDecision];


export const ListWebSignalCandidatesDecision = {
  pursue: 'pursue',
  not_pursuing: 'not_pursuing',
  undecided: 'undecided',
} as const;

export type ListWebSignalCandidates200 = {
  items: WebSignalCandidate[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
};

export type ListWebSignalCandidateIndustriesParams = {
status?: ListWebSignalCandidateIndustriesStatus;
jobId?: string;
decision?: ListWebSignalCandidateIndustriesDecision;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListWebSignalCandidateIndustriesStatus = typeof ListWebSignalCandidateIndustriesStatus[keyof typeof ListWebSignalCandidateIndustriesStatus];


export const ListWebSignalCandidateIndustriesStatus = {
  pending: 'pending',
  confirmed: 'confirmed',
  dismissed: 'dismissed',
} as const;

export type ListWebSignalCandidateIndustriesDecision = typeof ListWebSignalCandidateIndustriesDecision[keyof typeof ListWebSignalCandidateIndustriesDecision];


export const ListWebSignalCandidateIndustriesDecision = {
  pursue: 'pursue',
  not_pursuing: 'not_pursuing',
  undecided: 'undecided',
} as const;

export type GetWebSignalDigestParams = {
/**
 * @minimum 1
 * @maximum 90
 */
days?: number;
};

export type ListAccountWebSignals200 = {
  items: WebSignalCandidate[];
};

export type ListWebDiscoveryTargetingProfilesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListWebDiscoverySourcesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListTenderPortalCandidatesParams = {
status?: ListTenderPortalCandidatesStatus;
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
};

export type ListTenderPortalCandidatesStatus = typeof ListTenderPortalCandidatesStatus[keyof typeof ListTenderPortalCandidatesStatus];


export const ListTenderPortalCandidatesStatus = {
  suggested: 'suggested',
  approved: 'approved',
  rejected: 'rejected',
} as const;

export type DeleteTenderDiscoverySourceParams = {
/**
 * @minimum 1
 */
expectedRevision: number;
};

export type ListWebDiscoveryRecipesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListWebDiscoveryScanRunsParams = {
sourceId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListWebDiscoveryJobsParams = {
status?: ListWebDiscoveryJobsStatus;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListWebDiscoveryJobsStatus = typeof ListWebDiscoveryJobsStatus[keyof typeof ListWebDiscoveryJobsStatus];


export const ListWebDiscoveryJobsStatus = {
  draft: 'draft',
  active: 'active',
  paused: 'paused',
} as const;

export type DeleteWebDiscoveryJobParams = {
/**
 * @minimum 1
 */
expectedRevision: number;
};

export type ListWebDiscoveryJobRunsParams = {
trigger?: ListWebDiscoveryJobRunsTrigger;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListWebDiscoveryJobRunsTrigger = typeof ListWebDiscoveryJobRunsTrigger[keyof typeof ListWebDiscoveryJobRunsTrigger];


export const ListWebDiscoveryJobRunsTrigger = {
  test: 'test',
  manual: 'manual',
  schedule: 'schedule',
} as const;

export type ListWebDiscoveryJobRunResultsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListWebDiscoveryCandidateDocumentsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListWebDiscoveryCandidateHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListWebDiscoveryEvidenceParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetWebDiscoveryPromotion200 = {
  promotion: WebDiscoveryPromotion | null;
};

export type ConnectStaffVoiceLiveParams = {
ticket: string;
};

export type ConnectPublicVoiceLiveParams = {
/**
 * @minLength 1
 * @maxLength 120
 */
slug?: string;
};

export type ListVoiceStyleHistoryParams = {
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListVoiceStyleHistory200 = {
  items: VoiceStyleVersion[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
};

export type RestoreVoiceStyleVersionBody = {
  /** @minimum 0 */
  expectedRevision: number;
};

export type ListVoiceCallsParams = {
contactId?: string;
direction?: ListVoiceCallsDirection;
status?: VoiceCallStatus;
/**
 * @minimum 1
 */
page?: number;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
};

export type ListVoiceCallsDirection = typeof ListVoiceCallsDirection[keyof typeof ListVoiceCallsDirection];


export const ListVoiceCallsDirection = {
  inbound: 'inbound',
  outbound: 'outbound',
} as const;

export type ListVoiceCalls200 = {
  items: VoiceCall[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  page: number;
  /** @minimum 1 */
  limit: number;
};

export type ListCallAuthHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
identityType?: CallAuthIdentityType;
/**
 * Optional non-secret pairing locator entered by the signed-in staff member.
 * @pattern ^[A-Z0-9]{6,12}$
 */
pairingReference?: string;
requestStatus?: CallAuthRequestStatus;
callId?: string;
};

export type ListPortalCallAuthRequestsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
/**
 * @pattern ^[A-Z0-9]{6,12}$
 */
pairingReference?: string;
requestStatus?: CallAuthRequestStatus;
};

export type ReceiveTwilioInboundVoiceCallBody = {
  CallSid: string;
  From: string;
  To: string;
};

export type AnswerTwilioOutboundVoiceCallParams = {
callId: string;
};

export type AnswerTwilioOutboundVoiceCallBody = {
  CallSid?: string;
};

export type ContinueTwilioVoiceTurnParams = {
callId: string;
};

export type ContinueTwilioVoiceTurnBody = {
  CallSid?: string;
  SpeechResult?: string;
};

export type UpdateTwilioVoiceCallStatusParams = {
/**
 * Legacy CRM call identifier retained for existing outbound callbacks
 */
callId?: string;
};

export type UpdateTwilioVoiceCallStatusBody = {
  CallSid: string;
  CallStatus: string;
  /** @pattern ^[0-9]+$ */
  CallDuration?: string;
  /** @pattern ^[0-9]+$ */
  SequenceNumber?: string;
};

export type UpdateTwilioVoiceRecordingParams = {
callId: string;
};

export type UpdateTwilioVoiceRecordingBody = {
  CallSid?: string;
  RecordingSid?: string;
  RecordingUrl?: string;
};

export type VerifyTwilioCallAuthDigitsParams = {
callId: CallAuthCallIdParameter;
challengeId: CallAuthChallengeIdParameter;
};

export type ReceiveTwilioCallAuthSmsStatusParams = {
callId: CallAuthCallIdParameter;
challengeId: CallAuthChallengeIdParameter;
};

export type ReceiveTwilioCallAuthSmsStatusBody = {
  /**
     * @minLength 1
     * @maxLength 100
     */
  MessageSid: string;
  /**
     * @minLength 1
     * @maxLength 40
     */
  MessageStatus: string;
};

export type GetJourneyWorkforceParams = {
recordId?: string;
window?: GetJourneyWorkforceWindow;
includeTestData?: boolean;
};

export type GetJourneyWorkforceWindow = typeof GetJourneyWorkforceWindow[keyof typeof GetJourneyWorkforceWindow];


export const GetJourneyWorkforceWindow = {
  '7d': '7d',
  '30d': '30d',
  '90d': '90d',
  all: 'all',
} as const;

export type GetJourneyFloorplanParams = {
recordId?: string;
window?: GetJourneyFloorplanWindow;
includeTestData?: boolean;
};

export type GetJourneyFloorplanWindow = typeof GetJourneyFloorplanWindow[keyof typeof GetJourneyFloorplanWindow];


export const GetJourneyFloorplanWindow = {
  '7d': '7d',
  '30d': '30d',
  '90d': '90d',
  all: 'all',
} as const;

export type GetJourneyBacklogParams = {
recordId?: string;
window?: GetJourneyBacklogWindow;
includeTestData?: boolean;
};

export type GetJourneyBacklogWindow = typeof GetJourneyBacklogWindow[keyof typeof GetJourneyBacklogWindow];


export const GetJourneyBacklogWindow = {
  '7d': '7d',
  '30d': '30d',
  '90d': '90d',
  all: 'all',
} as const;

export type GetStageAuthorityParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type UpdateStageAuthorityBodyLevels = {[key: string]: AuthorityLevel};

export type UpdateStageAuthorityBody = {
  levels: UpdateStageAuthorityBodyLevels;
};

export type ListAgentInstructionVersionsParams = {
journeyKey?: JourneyKey;
/**
 * @minLength 1
 * @maxLength 100
 */
stageId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListAgentInstructionVersions200 = {
  effectiveActive?: AgentInstructionVersion;
  activations?: InstructionActivation[];
  activationTotal?: number;
  items: AgentInstructionVersion[];
  total: number;
  limit: number;
  offset: number;
};

export type CreateAgentInstructionVersionParams = {
journeyKey?: JourneyKey;
/**
 * @minLength 1
 * @maxLength 100
 */
stageId?: string;
};

export type CreateAgentInstructionVersionBody = {
  /**
     * @minLength 1
     * @maxLength 20000
     */
  content: string;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  note: string;
};

export type ActivateAgentInstructionVersionParams = {
journeyKey?: JourneyKey;
/**
 * @minLength 1
 * @maxLength 100
 */
stageId?: string;
};

export type DeclineAgentInstructionVersionParams = {
journeyKey: JourneyKey;
/**
 * @minLength 1
 * @maxLength 100
 */
stageId: string;
};

export type DeclineAgentInstructionVersionBody = {
  /**
     * @minLength 1
     * @maxLength 1000
     */
  note: string;
};

export type GetAiAuthorityPolicyParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListUnifiedTimelineParams = {
accountId?: string;
contactId?: string;
channel?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type GetLeadScoreParams = {
entityType: GetLeadScoreEntityType;
entityId: string;
};

export type GetLeadScoreEntityType = typeof GetLeadScoreEntityType[keyof typeof GetLeadScoreEntityType];


export const GetLeadScoreEntityType = {
  account: 'account',
  contact: 'contact',
} as const;

export type ListAiActionAudit200 = {
  items: AiActionAudit[];
};

export type ApproveAiActionBodyDecision = typeof ApproveAiActionBodyDecision[keyof typeof ApproveAiActionBodyDecision];


export const ApproveAiActionBodyDecision = {
  approved: 'approved',
  rejected: 'rejected',
} as const;

export type ApproveAiActionBody = {
  decision: ApproveAiActionBodyDecision;
  approvedBy?: string;
};

export type ListQueueItemStatesParams = {
/**
 * @minLength 1
 * @maxLength 60
 * @pattern ^[a-z][a-z0-9_.-]*$
 */
itemType?: string;
/**
 * @minimum 1
 * @maximum 500
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListSavedSharedMailboxesParams = {
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type GetHomepageLayoutParams = {
/**
 * Personal audience page; omitted selects All for existing clients.
 */
tab?: GetHomepageLayoutTab;
};

export type GetHomepageLayoutTab = typeof GetHomepageLayoutTab[keyof typeof GetHomepageLayoutTab];


export const GetHomepageLayoutTab = {
  all: 'all',
  b2c: 'b2c',
  b2b: 'b2b',
  partner: 'partner',
  investor: 'investor',
} as const;

export type SaveHomepageLayoutParams = {
/**
 * Personal audience page; omitted selects All for existing clients.
 */
tab?: SaveHomepageLayoutTab;
};

export type SaveHomepageLayoutTab = typeof SaveHomepageLayoutTab[keyof typeof SaveHomepageLayoutTab];


export const SaveHomepageLayoutTab = {
  all: 'all',
  b2c: 'b2c',
  b2b: 'b2b',
  partner: 'partner',
  investor: 'investor',
} as const;

export type ResetHomepageLayoutParams = {
/**
 * Personal audience page; omitted selects All for existing clients.
 */
tab?: ResetHomepageLayoutTab;
};

export type ResetHomepageLayoutTab = typeof ResetHomepageLayoutTab[keyof typeof ResetHomepageLayoutTab];


export const ResetHomepageLayoutTab = {
  all: 'all',
  b2c: 'b2c',
  b2b: 'b2b',
  partner: 'partner',
  investor: 'investor',
} as const;

export type ListResearchThreadsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
status?: ResearchThreadStatus;
/**
 * @maxLength 200
 */
search?: string;
};

export type ListResearchMessagesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListResearchThreadAttachmentsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type UploadResearchThreadAttachmentBody = {
  file: Blob | File;
};

export type ListResearchRunsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListResearchPromptsParams = {
/**
 * @maxLength 200
 */
search?: string;
/**
 * @maxLength 60
 */
category?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
active?: boolean;
};

export type ListResearchDocumentsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
status?: ListResearchDocumentsStatus;
/**
 * @maxLength 200
 */
search?: string;
};

export type ListResearchDocumentsStatus = typeof ListResearchDocumentsStatus[keyof typeof ListResearchDocumentsStatus];


export const ListResearchDocumentsStatus = {
  active: 'active',
  archived: 'archived',
} as const;

export type ListResearchDocumentVersionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type CompareResearchDocumentVersionsParams = {
from: string;
to: string;
};

export type ListResearchDocumentProposalsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetSalesFloorTwinSnapshotParams = {
window?: GetSalesFloorTwinSnapshotWindow;
audience?: CrmAudience;
pipeline?: GetSalesFloorTwinSnapshotPipeline;
/**
 * Include records flagged as test or demo data. They are excluded by default.
 */
includeTestData?: IncludeTestDataParameter;
};

export type GetSalesFloorTwinSnapshotWindow = typeof GetSalesFloorTwinSnapshotWindow[keyof typeof GetSalesFloorTwinSnapshotWindow];


export const GetSalesFloorTwinSnapshotWindow = {
  '24h': '24h',
  '7d': '7d',
  '30d': '30d',
} as const;

export type GetSalesFloorTwinSnapshotPipeline = typeof GetSalesFloorTwinSnapshotPipeline[keyof typeof GetSalesFloorTwinSnapshotPipeline];


export const GetSalesFloorTwinSnapshotPipeline = {
  b2b_sales: 'b2b_sales',
  b2c_sales: 'b2c_sales',
  investor_funding: 'investor_funding',
} as const;

export type ListGeographicLayersParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type DeleteGeographicLayerParams = {
/**
 * @minimum 1
 */
expectedVersion: number;
};

export type ListGeographicFeaturesParams = {
layerId?: string;
recordType?: GeographicRecordType;
recordId?: string;
/**
 * @maxLength 200
 */
search?: string;
/**
 * @minimum -180
 * @maximum 180
 */
minLongitude?: number;
/**
 * @minimum -90
 * @maximum 90
 */
minLatitude?: number;
/**
 * @minimum -180
 * @maximum 180
 */
maxLongitude?: number;
/**
 * @minimum -90
 * @maximum 90
 */
maxLatitude?: number;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type DeleteGeographicFeatureParams = {
/**
 * @minimum 1
 */
expectedVersion: number;
};

export type PreviewGeographicImportParams = {
idempotencyKey: string;
/**
 * @minLength 1
 * @maxLength 120
 */
layerName: string;
};

export type PreviewGeographicImportBody = {
  file: Blob | File;
};

export type CommitGeographicImportBodyExpectedStatus = typeof CommitGeographicImportBodyExpectedStatus[keyof typeof CommitGeographicImportBodyExpectedStatus];


export const CommitGeographicImportBodyExpectedStatus = {
  previewed: 'previewed',
} as const;

export type CommitGeographicImportBody = {
  expectedStatus: CommitGeographicImportBodyExpectedStatus;
};

export type ListIcloudContactsParams = {
/**
 * @maxLength 200
 */
search?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListIcloudBatchesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListIcloudWritebackLogParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListWhatsAppTemplatesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetWhatsAppPermissionParams = {
/**
 * @maxLength 16
 * @pattern ^\+[1-9][0-9]{7,14}$
 */
recipientNumber: string;
};

export type ListWhatsAppPermissionEvidenceParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type PreviewWhatsAppRecipientsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListWhatsAppDeliveriesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListWhatsAppConversationsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
contactId?: string;
status?: ListWhatsAppConversationsStatus;
};

export type ListWhatsAppConversationsStatus = typeof ListWhatsAppConversationsStatus[keyof typeof ListWhatsAppConversationsStatus];


export const ListWhatsAppConversationsStatus = {
  open: 'open',
  closed: 'closed',
} as const;

export type ListWhatsAppMessagesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type VerifyWhatsAppWebhookParams = {
/**
 * @minLength 1
 * @maxLength 1000
 */
'hub.mode': string;
/**
 * @minLength 1
 * @maxLength 1000
 */
'hub.verify_token': string;
/**
 * @minLength 1
 * @maxLength 1000
 */
'hub.challenge': string;
};

export type ListAgentDocumentReviewsParams = {
tenantId: string;
entityId: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type GetAgentDocumentReviewParams = {
tenantId: string;
entityId: string;
};

export type CancelAgentDocumentReviewBody = {
  accessScope: AgentAccessScope;
};

export type ListSowSessionsParams = {
proposalId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListSowSessions200 = {
  items: SowSessionSummary[];
  total: number;
  limit: number;
  offset: number;
};

export type StartSowSessionBody = {
  proposalId?: string;
  /** @minimum 1 */
  expectedProposalRevision?: number;
  workspaceContext?: WorkspaceEvidenceBinding;
  clientRequestId: string;
  templateVersionId: string;
  /** @maxItems 20 */
  selectedSources: SowSourceSelection[];
};

export type UpdateSowSessionBody = {
  /** @minimum 1 */
  expectedRevision: number;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  reason: string;
  workspaceContext?: WorkspaceEvidenceBinding;
  facts?: SowProjectFacts;
  /** @maxItems 20 */
  selectedSources?: SowSourceSelection[];
};

export type PrepareSowSessionReviewBody = {
  /** @minimum 1 */
  expectedRevision: number;
};

export type ListSowApprovalCandidatesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
/**
 * @maxLength 100
 */
search?: string;
};

export type ListSowEmailEvidenceParams = {
customerId: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListSowEmailEvidence200ItemsItem = {
  id: string;
  /** @nullable */
  subject: string | null;
  from: string;
};

export type ListSowEmailEvidence200 = {
  items: ListSowEmailEvidence200ItemsItem[];
  total: number;
  limit: number;
  offset: number;
};

export type ListSowRequestsParams = {
customerId?: string;
quoteId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListSowRequests200 = {
  items: SowRequest[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
};

export type GetSowTemplateSourceParams = {
templateKey?: GetSowTemplateSourceTemplateKey;
};

export type GetSowTemplateSourceTemplateKey = typeof GetSowTemplateSourceTemplateKey[keyof typeof GetSowTemplateSourceTemplateKey];


export const GetSowTemplateSourceTemplateKey = {
  'circle-t-sow': 'circle-t-sow',
  'circle-t-engagement-pack': 'circle-t-engagement-pack',
} as const;

export type GetSowTemplateSource200AdoptionState = typeof GetSowTemplateSource200AdoptionState[keyof typeof GetSowTemplateSource200AdoptionState];


export const GetSowTemplateSource200AdoptionState = {
  review_required: 'review_required',
} as const;

export type GetSowTemplateSource200Definition = { [key: string]: unknown };

export type GetSowTemplateSource200 = {
  sourceHash: string;
  sourceFile: string;
  templateKey?: string;
  adoptionState?: GetSowTemplateSource200AdoptionState;
  definition: GetSowTemplateSource200Definition;
  terms: SowProtectedTerm[];
};

export type ListSowPreparationsParams = {
/**
 * @minLength 1
 * @maxLength 160
 */
search?: string;
status?: ListSowPreparationsStatus;
accountId?: string;
opportunityId?: string;
quoteId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListSowPreparationsStatus = typeof ListSowPreparationsStatus[keyof typeof ListSowPreparationsStatus];


export const ListSowPreparationsStatus = {
  active: 'active',
  archived: 'archived',
  all: 'all',
} as const;

export type ListSowPreparationDocumentsParams = {
/**
 * @minLength 1
 * @maxLength 160
 */
search?: string;
unlinkedOnly?: boolean;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListSowTemplatesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
/**
 * @minLength 1
 * @maxLength 160
 */
search?: string;
};

export type InstallSowTemplateBodyTemplateKey = typeof InstallSowTemplateBodyTemplateKey[keyof typeof InstallSowTemplateBodyTemplateKey];


export const InstallSowTemplateBodyTemplateKey = {
  'circle-t-sow': 'circle-t-sow',
  'circle-t-engagement-pack': 'circle-t-engagement-pack',
} as const;

export type InstallSowTemplateBody = {
  /** @pattern ^[a-f0-9]{64}$ */
  sourceHash: string;
  templateKey?: InstallSowTemplateBodyTemplateKey;
};

export type ReviseSowTemplateBodyReviewedPricingItem = {
  /** @pattern ^[1-9][0-9]{0,3}$ */
  quantity: string;
  /** @pattern ^(0|[1-9][0-9]{0,8})(\.[0-9]{1,4})?$ */
  rate: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  reason: string;
};

export type ReviseSowTemplateBody = {
  /** @minimum 1 */
  expectedVersion: number;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  reason: string;
  /**
     * @minItems 1
     * @maxItems 20
     */
  terms: SowProtectedTerm[];
  /** @maxItems 50 */
  reviewedPricing?: ReviseSowTemplateBodyReviewedPricingItem[];
};

export type ValidateSowFacts200 = {
  content: ManagedDocumentContent;
  validation: SowValidation;
};

export type ListResearchGroundingParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
/**
 * @maxLength 200
 */
search?: string;
state?: ListResearchGroundingState;
/**
 * @pattern ^[a-f0-9]{64}$
 */
expectedSource?: string;
};

export type ListResearchGroundingState = typeof ListResearchGroundingState[keyof typeof ListResearchGroundingState];


export const ListResearchGroundingState = {
  eligible: 'eligible',
  excluded: 'excluded',
  unavailable: 'unavailable',
} as const;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getPreviewAccessMigrationUrl = (params?: PreviewAccessMigrationParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/role-migration?${stringifiedParams}` : `/api/access/role-migration`
}

/**
 * Platform Admin may preview platform roles; a tenant Owner may preview only their tenant. Read-only. Lists permanent and eligible replacements separately.
 * @summary Preview access migration
 */
export const previewAccessMigration = async (params?: PreviewAccessMigrationParams, options?: Parameters<typeof customFetch>[1]): Promise<AccessMigrationPreview> => {

  return customFetch<AccessMigrationPreview>(getPreviewAccessMigrationUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPreviewAccessMigrationQueryKey = (params?: PreviewAccessMigrationParams,) => {
    return [
    `/api/access/role-migration`, ...(params ? [params] : [])
    ] as const;
    }


export const getPreviewAccessMigrationQueryOptions = <TData = Awaited<ReturnType<typeof previewAccessMigration>>, TError = unknown>(params?: PreviewAccessMigrationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof previewAccessMigration>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPreviewAccessMigrationQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof previewAccessMigration>>> = ({ signal }) => previewAccessMigration(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof previewAccessMigration>>, TError, TData> & { queryKey: QueryKey }
}

export type PreviewAccessMigrationQueryResult = NonNullable<Awaited<ReturnType<typeof previewAccessMigration>>>
export type PreviewAccessMigrationQueryError = unknown


/**
 * @summary Preview access migration
 */

export function usePreviewAccessMigration<TData = Awaited<ReturnType<typeof previewAccessMigration>>, TError = unknown>(
 params?: PreviewAccessMigrationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof previewAccessMigration>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getPreviewAccessMigrationQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getApplyAccessMigrationUrl = () => {




  return `/api/access/role-migration`
}

/**
 * Requires explicit confirmation of the exact current preview digest and Platform Admin or tenant Owner authority. Conflicting duties move to eligible access requiring separate approval. Atomic, audited, and rejects stale previews.
 * @summary Apply reviewed access migration
 */
export const applyAccessMigration = async (accessMigrationApply: AccessMigrationApply, options?: Parameters<typeof customFetch>[1]): Promise<AccessMigrationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccessMigrationResult>(getApplyAccessMigrationUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accessMigrationApply)
  }
);}





export const getApplyAccessMigrationMutationKey = () => ['applyAccessMigration'] as const;

export const getApplyAccessMigrationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyAccessMigration>>, TError,ApplyAccessMigrationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof applyAccessMigration>>, TError,ApplyAccessMigrationMutationVariables, TContext> => {

const mutationKey = getApplyAccessMigrationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof applyAccessMigration>>, ApplyAccessMigrationMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  applyAccessMigration(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApplyAccessMigrationMutationResult = NonNullable<Awaited<ReturnType<typeof applyAccessMigration>>>
    export type ApplyAccessMigrationMutationBody = AccessMigrationApply
    export type ApplyAccessMigrationMutationError = unknown
    export type ApplyAccessMigrationMutationVariables = {data: AccessMigrationApply}

    /**
 * @summary Apply reviewed access migration
 */
export const useApplyAccessMigration = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyAccessMigration>>, TError,ApplyAccessMigrationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof applyAccessMigration>>,
        TError,
        ApplyAccessMigrationMutationVariables,
        TContext
      > => {
      return useMutation(getApplyAccessMigrationMutationOptions(options));
    }

export const getListProposalTasksUrl = (id: string,
    params?: ListProposalTasksParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/tasks?${stringifiedParams}` : `/api/tenders/${id}/tasks`
}

export const listProposalTasks = async (id: string,
    params?: ListProposalTasksParams, options?: Parameters<typeof customFetch>[1]): Promise<ProposalTaskList> => {

  return customFetch<ProposalTaskList>(getListProposalTasksUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListProposalTasksQueryKey = (id: string,
    params?: ListProposalTasksParams,) => {
    return [
    `/api/tenders/${id}/tasks`, ...(params ? [params] : [])
    ] as const;
    }


export const getListProposalTasksQueryOptions = <TData = Awaited<ReturnType<typeof listProposalTasks>>, TError = unknown>(id: string,
    params?: ListProposalTasksParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listProposalTasks>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListProposalTasksQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listProposalTasks>>> = ({ signal }) => listProposalTasks(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listProposalTasks>>, TError, TData> & { queryKey: QueryKey }
}

export type ListProposalTasksQueryResult = NonNullable<Awaited<ReturnType<typeof listProposalTasks>>>
export type ListProposalTasksQueryError = unknown



export function useListProposalTasks<TData = Awaited<ReturnType<typeof listProposalTasks>>, TError = unknown>(
 id: string,
    params?: ListProposalTasksParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listProposalTasks>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListProposalTasksQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListProposalTaskOwnersUrl = (id: string,
    params?: ListProposalTaskOwnersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/task-owners?${stringifiedParams}` : `/api/tenders/${id}/task-owners`
}

export const listProposalTaskOwners = async (id: string,
    params?: ListProposalTaskOwnersParams, options?: Parameters<typeof customFetch>[1]): Promise<ListProposalTaskOwners200> => {

  return customFetch<ListProposalTaskOwners200>(getListProposalTaskOwnersUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListProposalTaskOwnersQueryKey = (id: string,
    params?: ListProposalTaskOwnersParams,) => {
    return [
    `/api/tenders/${id}/task-owners`, ...(params ? [params] : [])
    ] as const;
    }


export const getListProposalTaskOwnersQueryOptions = <TData = Awaited<ReturnType<typeof listProposalTaskOwners>>, TError = unknown>(id: string,
    params?: ListProposalTaskOwnersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listProposalTaskOwners>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListProposalTaskOwnersQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listProposalTaskOwners>>> = ({ signal }) => listProposalTaskOwners(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listProposalTaskOwners>>, TError, TData> & { queryKey: QueryKey }
}

export type ListProposalTaskOwnersQueryResult = NonNullable<Awaited<ReturnType<typeof listProposalTaskOwners>>>
export type ListProposalTaskOwnersQueryError = unknown



export function useListProposalTaskOwners<TData = Awaited<ReturnType<typeof listProposalTaskOwners>>, TError = unknown>(
 id: string,
    params?: ListProposalTaskOwnersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listProposalTaskOwners>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListProposalTaskOwnersQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewProposalTasksUrl = (id: string,) => {




  return `/api/tenders/${id}/task-reviews`
}

export const reviewProposalTasks = async (id: string,
    reviewProposalTasksBody: ReviewProposalTasksBody, options?: Parameters<typeof customFetch>[1]): Promise<ProposalTaskReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ProposalTaskReview>(getReviewProposalTasksUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewProposalTasksBody)
  }
);}





export const getReviewProposalTasksMutationKey = () => ['reviewProposalTasks'] as const;

export const getReviewProposalTasksMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewProposalTasks>>, TError,ReviewProposalTasksMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewProposalTasks>>, TError,ReviewProposalTasksMutationVariables, TContext> => {

const mutationKey = getReviewProposalTasksMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewProposalTasks>>, ReviewProposalTasksMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reviewProposalTasks(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewProposalTasksMutationResult = NonNullable<Awaited<ReturnType<typeof reviewProposalTasks>>>
    export type ReviewProposalTasksMutationBody = ReviewProposalTasksBody
    export type ReviewProposalTasksMutationError = unknown
    export type ReviewProposalTasksMutationVariables = {id: string;data: ReviewProposalTasksBody}

    export const useReviewProposalTasks = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewProposalTasks>>, TError,ReviewProposalTasksMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewProposalTasks>>,
        TError,
        ReviewProposalTasksMutationVariables,
        TContext
      > => {
      return useMutation(getReviewProposalTasksMutationOptions(options));
    }

export const getGetProposalTaskReviewUrl = (id: string,
    requestId: string,) => {




  return `/api/tenders/${id}/task-reviews/${requestId}`
}

export const getProposalTaskReview = async (id: string,
    requestId: string, options?: Parameters<typeof customFetch>[1]): Promise<ProposalTaskReview> => {

  return customFetch<ProposalTaskReview>(getGetProposalTaskReviewUrl(id,requestId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetProposalTaskReviewQueryKey = (id: string,
    requestId: string,) => {
    return [
    `/api/tenders/${id}/task-reviews/${requestId}`
    ] as const;
    }


export const getGetProposalTaskReviewQueryOptions = <TData = Awaited<ReturnType<typeof getProposalTaskReview>>, TError = unknown>(id: string,
    requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getProposalTaskReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetProposalTaskReviewQueryKey(id,requestId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getProposalTaskReview>>> = ({ signal }) => getProposalTaskReview(id,requestId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && requestId !== null && requestId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getProposalTaskReview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetProposalTaskReviewQueryResult = NonNullable<Awaited<ReturnType<typeof getProposalTaskReview>>>
export type GetProposalTaskReviewQueryError = unknown



export function useGetProposalTaskReview<TData = Awaited<ReturnType<typeof getProposalTaskReview>>, TError = unknown>(
 id: string,
    requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getProposalTaskReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetProposalTaskReviewQueryOptions(id,requestId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConfirmProposalTasksUrl = (id: string,
    requestId: string,) => {




  return `/api/tenders/${id}/task-reviews/${requestId}/confirm`
}

export const confirmProposalTasks = async (id: string,
    requestId: string,
    confirmProposalTasksBody: ConfirmProposalTasksBody, options?: Parameters<typeof customFetch>[1]): Promise<ProposalTaskReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ProposalTaskReview>(getConfirmProposalTasksUrl(id,requestId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(confirmProposalTasksBody)
  }
);}





export const getConfirmProposalTasksMutationKey = () => ['confirmProposalTasks'] as const;

export const getConfirmProposalTasksMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmProposalTasks>>, TError,ConfirmProposalTasksMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmProposalTasks>>, TError,ConfirmProposalTasksMutationVariables, TContext> => {

const mutationKey = getConfirmProposalTasksMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmProposalTasks>>, ConfirmProposalTasksMutationVariables> = (props) => {
          const {id,requestId,data} = props ?? {};

          return  confirmProposalTasks(id,requestId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmProposalTasksMutationResult = NonNullable<Awaited<ReturnType<typeof confirmProposalTasks>>>
    export type ConfirmProposalTasksMutationBody = ConfirmProposalTasksBody
    export type ConfirmProposalTasksMutationError = unknown
    export type ConfirmProposalTasksMutationVariables = {id: string;requestId: string;data: ConfirmProposalTasksBody}

    export const useConfirmProposalTasks = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmProposalTasks>>, TError,ConfirmProposalTasksMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmProposalTasks>>,
        TError,
        ConfirmProposalTasksMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmProposalTasksMutationOptions(options));
    }

export const getGetCampaignAutonomyUrl = (params?: GetCampaignAutonomyParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/campaign-autonomy?${stringifiedParams}` : `/api/campaign-autonomy`
}

export const getCampaignAutonomy = async (params?: GetCampaignAutonomyParams, options?: Parameters<typeof customFetch>[1]): Promise<CampaignAutonomyState> => {

  return customFetch<CampaignAutonomyState>(getGetCampaignAutonomyUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetCampaignAutonomyQueryKey = (params?: GetCampaignAutonomyParams,) => {
    return [
    `/api/campaign-autonomy`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetCampaignAutonomyQueryOptions = <TData = Awaited<ReturnType<typeof getCampaignAutonomy>>, TError = unknown>(params?: GetCampaignAutonomyParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCampaignAutonomy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCampaignAutonomyQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCampaignAutonomy>>> = ({ signal }) => getCampaignAutonomy(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCampaignAutonomy>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCampaignAutonomyQueryResult = NonNullable<Awaited<ReturnType<typeof getCampaignAutonomy>>>
export type GetCampaignAutonomyQueryError = unknown



export function useGetCampaignAutonomy<TData = Awaited<ReturnType<typeof getCampaignAutonomy>>, TError = unknown>(
 params?: GetCampaignAutonomyParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCampaignAutonomy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCampaignAutonomyQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewCampaignAutonomyUrl = () => {




  return `/api/campaign-autonomy/reviews`
}

export const reviewCampaignAutonomy = async (reviewCampaignAutonomyBody: ReviewCampaignAutonomyBody, options?: Parameters<typeof customFetch>[1]): Promise<AgentActionProposal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentActionProposal>(getReviewCampaignAutonomyUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewCampaignAutonomyBody)
  }
);}





export const getReviewCampaignAutonomyMutationKey = () => ['reviewCampaignAutonomy'] as const;

export const getReviewCampaignAutonomyMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewCampaignAutonomy>>, TError,ReviewCampaignAutonomyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewCampaignAutonomy>>, TError,ReviewCampaignAutonomyMutationVariables, TContext> => {

const mutationKey = getReviewCampaignAutonomyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewCampaignAutonomy>>, ReviewCampaignAutonomyMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reviewCampaignAutonomy(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewCampaignAutonomyMutationResult = NonNullable<Awaited<ReturnType<typeof reviewCampaignAutonomy>>>
    export type ReviewCampaignAutonomyMutationBody = ReviewCampaignAutonomyBody
    export type ReviewCampaignAutonomyMutationError = unknown
    export type ReviewCampaignAutonomyMutationVariables = {data: ReviewCampaignAutonomyBody}

    export const useReviewCampaignAutonomy = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewCampaignAutonomy>>, TError,ReviewCampaignAutonomyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewCampaignAutonomy>>,
        TError,
        ReviewCampaignAutonomyMutationVariables,
        TContext
      > => {
      return useMutation(getReviewCampaignAutonomyMutationOptions(options));
    }

export const getGetLifecycleInsightsUrl = (params?: GetLifecycleInsightsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/lifecycle-insights?${stringifiedParams}` : `/api/lifecycle-insights`
}

/**
 * @summary Complete authorized lifecycle metrics and exact filtered record drilldown
 */
export const getLifecycleInsights = async (params?: GetLifecycleInsightsParams, options?: Parameters<typeof customFetch>[1]): Promise<LifecycleInsights> => {

  return customFetch<LifecycleInsights>(getGetLifecycleInsightsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetLifecycleInsightsQueryKey = (params?: GetLifecycleInsightsParams,) => {
    return [
    `/api/lifecycle-insights`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetLifecycleInsightsQueryOptions = <TData = Awaited<ReturnType<typeof getLifecycleInsights>>, TError = unknown>(params?: GetLifecycleInsightsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLifecycleInsights>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetLifecycleInsightsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getLifecycleInsights>>> = ({ signal }) => getLifecycleInsights(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getLifecycleInsights>>, TError, TData> & { queryKey: QueryKey }
}

export type GetLifecycleInsightsQueryResult = NonNullable<Awaited<ReturnType<typeof getLifecycleInsights>>>
export type GetLifecycleInsightsQueryError = unknown


/**
 * @summary Complete authorized lifecycle metrics and exact filtered record drilldown
 */

export function useGetLifecycleInsights<TData = Awaited<ReturnType<typeof getLifecycleInsights>>, TError = unknown>(
 params?: GetLifecycleInsightsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLifecycleInsights>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetLifecycleInsightsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListRetentionUrl = (params?: ListRetentionParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/retention?${stringifiedParams}` : `/api/retention`
}

export const listRetention = async (params?: ListRetentionParams, options?: Parameters<typeof customFetch>[1]): Promise<RetentionPage> => {

  return customFetch<RetentionPage>(getListRetentionUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListRetentionQueryKey = (params?: ListRetentionParams,) => {
    return [
    `/api/retention`, ...(params ? [params] : [])
    ] as const;
    }


export const getListRetentionQueryOptions = <TData = Awaited<ReturnType<typeof listRetention>>, TError = unknown>(params?: ListRetentionParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRetention>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListRetentionQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listRetention>>> = ({ signal }) => listRetention(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listRetention>>, TError, TData> & { queryKey: QueryKey }
}

export type ListRetentionQueryResult = NonNullable<Awaited<ReturnType<typeof listRetention>>>
export type ListRetentionQueryError = unknown



export function useListRetention<TData = Awaited<ReturnType<typeof listRetention>>, TError = unknown>(
 params?: ListRetentionParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRetention>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListRetentionQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetRetentionUrl = (sourceKind: RetentionKind,
    id: string,) => {




  return `/api/retention/${sourceKind}/${id}`
}

export const getRetention = async (sourceKind: RetentionKind,
    id: string, options?: Parameters<typeof customFetch>[1]): Promise<RetentionRecord> => {

  return customFetch<RetentionRecord>(getGetRetentionUrl(sourceKind,id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetRetentionQueryKey = (sourceKind: RetentionKind,
    id: string,) => {
    return [
    `/api/retention/${sourceKind}/${id}`
    ] as const;
    }


export const getGetRetentionQueryOptions = <TData = Awaited<ReturnType<typeof getRetention>>, TError = unknown>(sourceKind: RetentionKind,
    id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRetention>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetRetentionQueryKey(sourceKind,id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getRetention>>> = ({ signal }) => getRetention(sourceKind,id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: sourceKind !== null && sourceKind !== undefined && id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getRetention>>, TError, TData> & { queryKey: QueryKey }
}

export type GetRetentionQueryResult = NonNullable<Awaited<ReturnType<typeof getRetention>>>
export type GetRetentionQueryError = unknown



export function useGetRetention<TData = Awaited<ReturnType<typeof getRetention>>, TError = unknown>(
 sourceKind: RetentionKind,
    id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRetention>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetRetentionQueryOptions(sourceKind,id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListRetentionHistoryUrl = (sourceKind: RetentionKind,
    id: string,
    params?: ListRetentionHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/retention/${sourceKind}/${id}/history?${stringifiedParams}` : `/api/retention/${sourceKind}/${id}/history`
}

export const listRetentionHistory = async (sourceKind: RetentionKind,
    id: string,
    params?: ListRetentionHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<RetentionHistoryPage> => {

  return customFetch<RetentionHistoryPage>(getListRetentionHistoryUrl(sourceKind,id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListRetentionHistoryQueryKey = (sourceKind: RetentionKind,
    id: string,
    params?: ListRetentionHistoryParams,) => {
    return [
    `/api/retention/${sourceKind}/${id}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListRetentionHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listRetentionHistory>>, TError = unknown>(sourceKind: RetentionKind,
    id: string,
    params?: ListRetentionHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRetentionHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListRetentionHistoryQueryKey(sourceKind,id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listRetentionHistory>>> = ({ signal }) => listRetentionHistory(sourceKind,id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: sourceKind !== null && sourceKind !== undefined && id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listRetentionHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListRetentionHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listRetentionHistory>>>
export type ListRetentionHistoryQueryError = unknown



export function useListRetentionHistory<TData = Awaited<ReturnType<typeof listRetentionHistory>>, TError = unknown>(
 sourceKind: RetentionKind,
    id: string,
    params?: ListRetentionHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRetentionHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListRetentionHistoryQueryOptions(sourceKind,id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewRetentionChangeUrl = () => {




  return `/api/retention-changes`
}

export const reviewRetentionChange = async (reviewRetentionChangeBody: ReviewRetentionChangeBody, options?: Parameters<typeof customFetch>[1]): Promise<RetentionReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<RetentionReview>(getReviewRetentionChangeUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewRetentionChangeBody)
  }
);}





export const getReviewRetentionChangeMutationKey = () => ['reviewRetentionChange'] as const;

export const getReviewRetentionChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewRetentionChange>>, TError,ReviewRetentionChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewRetentionChange>>, TError,ReviewRetentionChangeMutationVariables, TContext> => {

const mutationKey = getReviewRetentionChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewRetentionChange>>, ReviewRetentionChangeMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reviewRetentionChange(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewRetentionChangeMutationResult = NonNullable<Awaited<ReturnType<typeof reviewRetentionChange>>>
    export type ReviewRetentionChangeMutationBody = ReviewRetentionChangeBody
    export type ReviewRetentionChangeMutationError = unknown
    export type ReviewRetentionChangeMutationVariables = {data: ReviewRetentionChangeBody}

    export const useReviewRetentionChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewRetentionChange>>, TError,ReviewRetentionChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewRetentionChange>>,
        TError,
        ReviewRetentionChangeMutationVariables,
        TContext
      > => {
      return useMutation(getReviewRetentionChangeMutationOptions(options));
    }

export const getGetRetentionChangeUrl = (id: string,) => {




  return `/api/retention-changes/${id}`
}

export const getRetentionChange = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<RetentionReview> => {

  return customFetch<RetentionReview>(getGetRetentionChangeUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetRetentionChangeQueryKey = (id: string,) => {
    return [
    `/api/retention-changes/${id}`
    ] as const;
    }


export const getGetRetentionChangeQueryOptions = <TData = Awaited<ReturnType<typeof getRetentionChange>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRetentionChange>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetRetentionChangeQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getRetentionChange>>> = ({ signal }) => getRetentionChange(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getRetentionChange>>, TError, TData> & { queryKey: QueryKey }
}

export type GetRetentionChangeQueryResult = NonNullable<Awaited<ReturnType<typeof getRetentionChange>>>
export type GetRetentionChangeQueryError = unknown



export function useGetRetentionChange<TData = Awaited<ReturnType<typeof getRetentionChange>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRetentionChange>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetRetentionChangeQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConfirmRetentionChangeUrl = (id: string,) => {




  return `/api/retention-changes/${id}/confirm`
}

export const confirmRetentionChange = async (id: string,
    confirmRetentionChangeBody: ConfirmRetentionChangeBody, options?: Parameters<typeof customFetch>[1]): Promise<RetentionReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<RetentionReview>(getConfirmRetentionChangeUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(confirmRetentionChangeBody)
  }
);}





export const getConfirmRetentionChangeMutationKey = () => ['confirmRetentionChange'] as const;

export const getConfirmRetentionChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmRetentionChange>>, TError,ConfirmRetentionChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmRetentionChange>>, TError,ConfirmRetentionChangeMutationVariables, TContext> => {

const mutationKey = getConfirmRetentionChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmRetentionChange>>, ConfirmRetentionChangeMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  confirmRetentionChange(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmRetentionChangeMutationResult = NonNullable<Awaited<ReturnType<typeof confirmRetentionChange>>>
    export type ConfirmRetentionChangeMutationBody = ConfirmRetentionChangeBody
    export type ConfirmRetentionChangeMutationError = unknown
    export type ConfirmRetentionChangeMutationVariables = {id: string;data: ConfirmRetentionChangeBody}

    export const useConfirmRetentionChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmRetentionChange>>, TError,ConfirmRetentionChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmRetentionChange>>,
        TError,
        ConfirmRetentionChangeMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmRetentionChangeMutationOptions(options));
    }

export const getCancelRetentionChangeUrl = (id: string,) => {




  return `/api/retention-changes/${id}/cancel`
}

export const cancelRetentionChange = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<RetentionReview> => {

  return customFetch<RetentionReview>(getCancelRetentionChangeUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelRetentionChangeMutationKey = () => ['cancelRetentionChange'] as const;

export const getCancelRetentionChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelRetentionChange>>, TError,CancelRetentionChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelRetentionChange>>, TError,CancelRetentionChangeMutationVariables, TContext> => {

const mutationKey = getCancelRetentionChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelRetentionChange>>, CancelRetentionChangeMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  cancelRetentionChange(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelRetentionChangeMutationResult = NonNullable<Awaited<ReturnType<typeof cancelRetentionChange>>>

    export type CancelRetentionChangeMutationError = unknown
    export type CancelRetentionChangeMutationVariables = {id: string}

    export const useCancelRetentionChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelRetentionChange>>, TError,CancelRetentionChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelRetentionChange>>,
        TError,
        CancelRetentionChangeMutationVariables,
        TContext
      > => {
      return useMutation(getCancelRetentionChangeMutationOptions(options));
    }

export const getListDataQualityUrl = (params?: ListDataQualityParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/data-quality?${stringifiedParams}` : `/api/data-quality`
}

export const listDataQuality = async (params?: ListDataQualityParams, options?: Parameters<typeof customFetch>[1]): Promise<DataQualityPage> => {

  return customFetch<DataQualityPage>(getListDataQualityUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDataQualityQueryKey = (params?: ListDataQualityParams,) => {
    return [
    `/api/data-quality`, ...(params ? [params] : [])
    ] as const;
    }


export const getListDataQualityQueryOptions = <TData = Awaited<ReturnType<typeof listDataQuality>>, TError = unknown>(params?: ListDataQualityParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDataQuality>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDataQualityQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDataQuality>>> = ({ signal }) => listDataQuality(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDataQuality>>, TError, TData> & { queryKey: QueryKey }
}

export type ListDataQualityQueryResult = NonNullable<Awaited<ReturnType<typeof listDataQuality>>>
export type ListDataQualityQueryError = unknown



export function useListDataQuality<TData = Awaited<ReturnType<typeof listDataQuality>>, TError = unknown>(
 params?: ListDataQualityParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDataQuality>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListDataQualityQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetDataQualityUrl = (sourceKind: 'account' | 'contact' | 'opportunity' | 'enquiry' | 'activity' | 'tender',
    id: string,) => {




  return `/api/data-quality/${sourceKind}/${id}`
}

export const getDataQuality = async (sourceKind: 'account' | 'contact' | 'opportunity' | 'enquiry' | 'activity' | 'tender',
    id: string, options?: Parameters<typeof customFetch>[1]): Promise<DataQualityRecord> => {

  return customFetch<DataQualityRecord>(getGetDataQualityUrl(sourceKind,id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDataQualityQueryKey = (sourceKind: 'account' | 'contact' | 'opportunity' | 'enquiry' | 'activity' | 'tender',
    id: string,) => {
    return [
    `/api/data-quality/${sourceKind}/${id}`
    ] as const;
    }


export const getGetDataQualityQueryOptions = <TData = Awaited<ReturnType<typeof getDataQuality>>, TError = unknown>(sourceKind: 'account' | 'contact' | 'opportunity' | 'enquiry' | 'activity' | 'tender',
    id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDataQuality>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDataQualityQueryKey(sourceKind,id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDataQuality>>> = ({ signal }) => getDataQuality(sourceKind,id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: sourceKind !== null && sourceKind !== undefined && id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDataQuality>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDataQualityQueryResult = NonNullable<Awaited<ReturnType<typeof getDataQuality>>>
export type GetDataQualityQueryError = unknown



export function useGetDataQuality<TData = Awaited<ReturnType<typeof getDataQuality>>, TError = unknown>(
 sourceKind: 'account' | 'contact' | 'opportunity' | 'enquiry' | 'activity' | 'tender',
    id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDataQuality>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDataQualityQueryOptions(sourceKind,id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListDataQualityCandidatesUrl = (sourceKind: 'account' | 'contact' | 'opportunity' | 'enquiry' | 'activity' | 'tender',
    id: string,
    params?: ListDataQualityCandidatesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/data-quality/${sourceKind}/${id}/candidates?${stringifiedParams}` : `/api/data-quality/${sourceKind}/${id}/candidates`
}

export const listDataQualityCandidates = async (sourceKind: 'account' | 'contact' | 'opportunity' | 'enquiry' | 'activity' | 'tender',
    id: string,
    params?: ListDataQualityCandidatesParams, options?: Parameters<typeof customFetch>[1]): Promise<DataQualityCandidatePage> => {

  return customFetch<DataQualityCandidatePage>(getListDataQualityCandidatesUrl(sourceKind,id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDataQualityCandidatesQueryKey = (sourceKind: 'account' | 'contact' | 'opportunity' | 'enquiry' | 'activity' | 'tender',
    id: string,
    params?: ListDataQualityCandidatesParams,) => {
    return [
    `/api/data-quality/${sourceKind}/${id}/candidates`, ...(params ? [params] : [])
    ] as const;
    }


export const getListDataQualityCandidatesQueryOptions = <TData = Awaited<ReturnType<typeof listDataQualityCandidates>>, TError = unknown>(sourceKind: 'account' | 'contact' | 'opportunity' | 'enquiry' | 'activity' | 'tender',
    id: string,
    params?: ListDataQualityCandidatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDataQualityCandidates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDataQualityCandidatesQueryKey(sourceKind,id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDataQualityCandidates>>> = ({ signal }) => listDataQualityCandidates(sourceKind,id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: sourceKind !== null && sourceKind !== undefined && id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDataQualityCandidates>>, TError, TData> & { queryKey: QueryKey }
}

export type ListDataQualityCandidatesQueryResult = NonNullable<Awaited<ReturnType<typeof listDataQualityCandidates>>>
export type ListDataQualityCandidatesQueryError = unknown



export function useListDataQualityCandidates<TData = Awaited<ReturnType<typeof listDataQualityCandidates>>, TError = unknown>(
 sourceKind: 'account' | 'contact' | 'opportunity' | 'enquiry' | 'activity' | 'tender',
    id: string,
    params?: ListDataQualityCandidatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDataQualityCandidates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListDataQualityCandidatesQueryOptions(sourceKind,id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListDataQualityHistoryUrl = (sourceKind: 'account' | 'contact' | 'opportunity' | 'enquiry' | 'activity' | 'tender',
    id: string,
    params?: ListDataQualityHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/data-quality/${sourceKind}/${id}/history?${stringifiedParams}` : `/api/data-quality/${sourceKind}/${id}/history`
}

export const listDataQualityHistory = async (sourceKind: 'account' | 'contact' | 'opportunity' | 'enquiry' | 'activity' | 'tender',
    id: string,
    params?: ListDataQualityHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<DataQualityHistoryPage> => {

  return customFetch<DataQualityHistoryPage>(getListDataQualityHistoryUrl(sourceKind,id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDataQualityHistoryQueryKey = (sourceKind: 'account' | 'contact' | 'opportunity' | 'enquiry' | 'activity' | 'tender',
    id: string,
    params?: ListDataQualityHistoryParams,) => {
    return [
    `/api/data-quality/${sourceKind}/${id}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListDataQualityHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listDataQualityHistory>>, TError = unknown>(sourceKind: 'account' | 'contact' | 'opportunity' | 'enquiry' | 'activity' | 'tender',
    id: string,
    params?: ListDataQualityHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDataQualityHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDataQualityHistoryQueryKey(sourceKind,id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDataQualityHistory>>> = ({ signal }) => listDataQualityHistory(sourceKind,id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: sourceKind !== null && sourceKind !== undefined && id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDataQualityHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListDataQualityHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listDataQualityHistory>>>
export type ListDataQualityHistoryQueryError = unknown



export function useListDataQualityHistory<TData = Awaited<ReturnType<typeof listDataQualityHistory>>, TError = unknown>(
 sourceKind: 'account' | 'contact' | 'opportunity' | 'enquiry' | 'activity' | 'tender',
    id: string,
    params?: ListDataQualityHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDataQualityHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListDataQualityHistoryQueryOptions(sourceKind,id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListDataQualityOwnersUrl = (params?: ListDataQualityOwnersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/data-quality-choices?${stringifiedParams}` : `/api/data-quality-choices`
}

export const listDataQualityOwners = async (params?: ListDataQualityOwnersParams, options?: Parameters<typeof customFetch>[1]): Promise<AgreementChoicePage> => {

  return customFetch<AgreementChoicePage>(getListDataQualityOwnersUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDataQualityOwnersQueryKey = (params?: ListDataQualityOwnersParams,) => {
    return [
    `/api/data-quality-choices`, ...(params ? [params] : [])
    ] as const;
    }


export const getListDataQualityOwnersQueryOptions = <TData = Awaited<ReturnType<typeof listDataQualityOwners>>, TError = unknown>(params?: ListDataQualityOwnersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDataQualityOwners>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDataQualityOwnersQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDataQualityOwners>>> = ({ signal }) => listDataQualityOwners(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDataQualityOwners>>, TError, TData> & { queryKey: QueryKey }
}

export type ListDataQualityOwnersQueryResult = NonNullable<Awaited<ReturnType<typeof listDataQualityOwners>>>
export type ListDataQualityOwnersQueryError = unknown



export function useListDataQualityOwners<TData = Awaited<ReturnType<typeof listDataQualityOwners>>, TError = unknown>(
 params?: ListDataQualityOwnersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDataQualityOwners>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListDataQualityOwnersQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewDataQualityChangeUrl = () => {




  return `/api/data-quality-changes`
}

export const reviewDataQualityChange = async (reviewDataQualityChangeBody: ReviewDataQualityChangeBody, options?: Parameters<typeof customFetch>[1]): Promise<DataQualityReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DataQualityReview>(getReviewDataQualityChangeUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewDataQualityChangeBody)
  }
);}





export const getReviewDataQualityChangeMutationKey = () => ['reviewDataQualityChange'] as const;

export const getReviewDataQualityChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewDataQualityChange>>, TError,ReviewDataQualityChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewDataQualityChange>>, TError,ReviewDataQualityChangeMutationVariables, TContext> => {

const mutationKey = getReviewDataQualityChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewDataQualityChange>>, ReviewDataQualityChangeMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reviewDataQualityChange(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewDataQualityChangeMutationResult = NonNullable<Awaited<ReturnType<typeof reviewDataQualityChange>>>
    export type ReviewDataQualityChangeMutationBody = ReviewDataQualityChangeBody
    export type ReviewDataQualityChangeMutationError = unknown
    export type ReviewDataQualityChangeMutationVariables = {data: ReviewDataQualityChangeBody}

    export const useReviewDataQualityChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewDataQualityChange>>, TError,ReviewDataQualityChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewDataQualityChange>>,
        TError,
        ReviewDataQualityChangeMutationVariables,
        TContext
      > => {
      return useMutation(getReviewDataQualityChangeMutationOptions(options));
    }

export const getGetDataQualityChangeUrl = (id: string,) => {




  return `/api/data-quality-changes/${id}`
}

export const getDataQualityChange = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<DataQualityReview> => {

  return customFetch<DataQualityReview>(getGetDataQualityChangeUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDataQualityChangeQueryKey = (id: string,) => {
    return [
    `/api/data-quality-changes/${id}`
    ] as const;
    }


export const getGetDataQualityChangeQueryOptions = <TData = Awaited<ReturnType<typeof getDataQualityChange>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDataQualityChange>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDataQualityChangeQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDataQualityChange>>> = ({ signal }) => getDataQualityChange(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDataQualityChange>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDataQualityChangeQueryResult = NonNullable<Awaited<ReturnType<typeof getDataQualityChange>>>
export type GetDataQualityChangeQueryError = unknown



export function useGetDataQualityChange<TData = Awaited<ReturnType<typeof getDataQualityChange>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDataQualityChange>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDataQualityChangeQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConfirmDataQualityChangeUrl = (id: string,) => {




  return `/api/data-quality-changes/${id}/confirm`
}

export const confirmDataQualityChange = async (id: string,
    confirmDataQualityChangeBody: ConfirmDataQualityChangeBody, options?: Parameters<typeof customFetch>[1]): Promise<DataQualityReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DataQualityReview>(getConfirmDataQualityChangeUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(confirmDataQualityChangeBody)
  }
);}





export const getConfirmDataQualityChangeMutationKey = () => ['confirmDataQualityChange'] as const;

export const getConfirmDataQualityChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmDataQualityChange>>, TError,ConfirmDataQualityChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmDataQualityChange>>, TError,ConfirmDataQualityChangeMutationVariables, TContext> => {

const mutationKey = getConfirmDataQualityChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmDataQualityChange>>, ConfirmDataQualityChangeMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  confirmDataQualityChange(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmDataQualityChangeMutationResult = NonNullable<Awaited<ReturnType<typeof confirmDataQualityChange>>>
    export type ConfirmDataQualityChangeMutationBody = ConfirmDataQualityChangeBody
    export type ConfirmDataQualityChangeMutationError = unknown
    export type ConfirmDataQualityChangeMutationVariables = {id: string;data: ConfirmDataQualityChangeBody}

    export const useConfirmDataQualityChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmDataQualityChange>>, TError,ConfirmDataQualityChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmDataQualityChange>>,
        TError,
        ConfirmDataQualityChangeMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmDataQualityChangeMutationOptions(options));
    }

export const getCancelDataQualityChangeUrl = (id: string,) => {




  return `/api/data-quality-changes/${id}/cancel`
}

export const cancelDataQualityChange = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<DataQualityReview> => {

  return customFetch<DataQualityReview>(getCancelDataQualityChangeUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelDataQualityChangeMutationKey = () => ['cancelDataQualityChange'] as const;

export const getCancelDataQualityChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelDataQualityChange>>, TError,CancelDataQualityChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelDataQualityChange>>, TError,CancelDataQualityChangeMutationVariables, TContext> => {

const mutationKey = getCancelDataQualityChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelDataQualityChange>>, CancelDataQualityChangeMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  cancelDataQualityChange(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelDataQualityChangeMutationResult = NonNullable<Awaited<ReturnType<typeof cancelDataQualityChange>>>

    export type CancelDataQualityChangeMutationError = unknown
    export type CancelDataQualityChangeMutationVariables = {id: string}

    export const useCancelDataQualityChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelDataQualityChange>>, TError,CancelDataQualityChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelDataQualityChange>>,
        TError,
        CancelDataQualityChangeMutationVariables,
        TContext
      > => {
      return useMutation(getCancelDataQualityChangeMutationOptions(options));
    }

export const getListRenewalPlanningUrl = (params?: ListRenewalPlanningParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/renewal-planning?${stringifiedParams}` : `/api/renewal-planning`
}

export const listRenewalPlanning = async (params?: ListRenewalPlanningParams, options?: Parameters<typeof customFetch>[1]): Promise<RenewalPlanningPage> => {

  return customFetch<RenewalPlanningPage>(getListRenewalPlanningUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListRenewalPlanningQueryKey = (params?: ListRenewalPlanningParams,) => {
    return [
    `/api/renewal-planning`, ...(params ? [params] : [])
    ] as const;
    }


export const getListRenewalPlanningQueryOptions = <TData = Awaited<ReturnType<typeof listRenewalPlanning>>, TError = unknown>(params?: ListRenewalPlanningParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRenewalPlanning>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListRenewalPlanningQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listRenewalPlanning>>> = ({ signal }) => listRenewalPlanning(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listRenewalPlanning>>, TError, TData> & { queryKey: QueryKey }
}

export type ListRenewalPlanningQueryResult = NonNullable<Awaited<ReturnType<typeof listRenewalPlanning>>>
export type ListRenewalPlanningQueryError = unknown



export function useListRenewalPlanning<TData = Awaited<ReturnType<typeof listRenewalPlanning>>, TError = unknown>(
 params?: ListRenewalPlanningParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRenewalPlanning>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListRenewalPlanningQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetRenewalPlanningUrl = (sourceKind: 'account' | 'product' | 'general_agreement' | 'partner_agreement',
    id: string,) => {




  return `/api/renewal-planning/${sourceKind}/${id}`
}

export const getRenewalPlanning = async (sourceKind: 'account' | 'product' | 'general_agreement' | 'partner_agreement',
    id: string, options?: Parameters<typeof customFetch>[1]): Promise<RenewalPlanningRecord> => {

  return customFetch<RenewalPlanningRecord>(getGetRenewalPlanningUrl(sourceKind,id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetRenewalPlanningQueryKey = (sourceKind: 'account' | 'product' | 'general_agreement' | 'partner_agreement',
    id: string,) => {
    return [
    `/api/renewal-planning/${sourceKind}/${id}`
    ] as const;
    }


export const getGetRenewalPlanningQueryOptions = <TData = Awaited<ReturnType<typeof getRenewalPlanning>>, TError = unknown>(sourceKind: 'account' | 'product' | 'general_agreement' | 'partner_agreement',
    id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRenewalPlanning>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetRenewalPlanningQueryKey(sourceKind,id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getRenewalPlanning>>> = ({ signal }) => getRenewalPlanning(sourceKind,id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: sourceKind !== null && sourceKind !== undefined && id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getRenewalPlanning>>, TError, TData> & { queryKey: QueryKey }
}

export type GetRenewalPlanningQueryResult = NonNullable<Awaited<ReturnType<typeof getRenewalPlanning>>>
export type GetRenewalPlanningQueryError = unknown



export function useGetRenewalPlanning<TData = Awaited<ReturnType<typeof getRenewalPlanning>>, TError = unknown>(
 sourceKind: 'account' | 'product' | 'general_agreement' | 'partner_agreement',
    id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRenewalPlanning>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetRenewalPlanningQueryOptions(sourceKind,id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListRenewalPlanningHistoryUrl = (sourceKind: 'account' | 'product' | 'general_agreement' | 'partner_agreement',
    id: string,
    params?: ListRenewalPlanningHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/renewal-planning/${sourceKind}/${id}/history?${stringifiedParams}` : `/api/renewal-planning/${sourceKind}/${id}/history`
}

export const listRenewalPlanningHistory = async (sourceKind: 'account' | 'product' | 'general_agreement' | 'partner_agreement',
    id: string,
    params?: ListRenewalPlanningHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<RenewalPlanningHistoryPage> => {

  return customFetch<RenewalPlanningHistoryPage>(getListRenewalPlanningHistoryUrl(sourceKind,id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListRenewalPlanningHistoryQueryKey = (sourceKind: 'account' | 'product' | 'general_agreement' | 'partner_agreement',
    id: string,
    params?: ListRenewalPlanningHistoryParams,) => {
    return [
    `/api/renewal-planning/${sourceKind}/${id}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListRenewalPlanningHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listRenewalPlanningHistory>>, TError = unknown>(sourceKind: 'account' | 'product' | 'general_agreement' | 'partner_agreement',
    id: string,
    params?: ListRenewalPlanningHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRenewalPlanningHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListRenewalPlanningHistoryQueryKey(sourceKind,id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listRenewalPlanningHistory>>> = ({ signal }) => listRenewalPlanningHistory(sourceKind,id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: sourceKind !== null && sourceKind !== undefined && id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listRenewalPlanningHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListRenewalPlanningHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listRenewalPlanningHistory>>>
export type ListRenewalPlanningHistoryQueryError = unknown



export function useListRenewalPlanningHistory<TData = Awaited<ReturnType<typeof listRenewalPlanningHistory>>, TError = unknown>(
 sourceKind: 'account' | 'product' | 'general_agreement' | 'partner_agreement',
    id: string,
    params?: ListRenewalPlanningHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRenewalPlanningHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListRenewalPlanningHistoryQueryOptions(sourceKind,id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewRenewalPlanningChangeUrl = () => {




  return `/api/renewal-planning-changes`
}

export const reviewRenewalPlanningChange = async (reviewRenewalPlanningChangeBody: ReviewRenewalPlanningChangeBody, options?: Parameters<typeof customFetch>[1]): Promise<RenewalPlanningReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<RenewalPlanningReview>(getReviewRenewalPlanningChangeUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewRenewalPlanningChangeBody)
  }
);}





export const getReviewRenewalPlanningChangeMutationKey = () => ['reviewRenewalPlanningChange'] as const;

export const getReviewRenewalPlanningChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewRenewalPlanningChange>>, TError,ReviewRenewalPlanningChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewRenewalPlanningChange>>, TError,ReviewRenewalPlanningChangeMutationVariables, TContext> => {

const mutationKey = getReviewRenewalPlanningChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewRenewalPlanningChange>>, ReviewRenewalPlanningChangeMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reviewRenewalPlanningChange(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewRenewalPlanningChangeMutationResult = NonNullable<Awaited<ReturnType<typeof reviewRenewalPlanningChange>>>
    export type ReviewRenewalPlanningChangeMutationBody = ReviewRenewalPlanningChangeBody
    export type ReviewRenewalPlanningChangeMutationError = unknown
    export type ReviewRenewalPlanningChangeMutationVariables = {data: ReviewRenewalPlanningChangeBody}

    export const useReviewRenewalPlanningChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewRenewalPlanningChange>>, TError,ReviewRenewalPlanningChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewRenewalPlanningChange>>,
        TError,
        ReviewRenewalPlanningChangeMutationVariables,
        TContext
      > => {
      return useMutation(getReviewRenewalPlanningChangeMutationOptions(options));
    }

export const getGetRenewalPlanningChangeUrl = (id: string,) => {




  return `/api/renewal-planning-changes/${id}`
}

export const getRenewalPlanningChange = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<RenewalPlanningReview> => {

  return customFetch<RenewalPlanningReview>(getGetRenewalPlanningChangeUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetRenewalPlanningChangeQueryKey = (id: string,) => {
    return [
    `/api/renewal-planning-changes/${id}`
    ] as const;
    }


export const getGetRenewalPlanningChangeQueryOptions = <TData = Awaited<ReturnType<typeof getRenewalPlanningChange>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRenewalPlanningChange>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetRenewalPlanningChangeQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getRenewalPlanningChange>>> = ({ signal }) => getRenewalPlanningChange(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getRenewalPlanningChange>>, TError, TData> & { queryKey: QueryKey }
}

export type GetRenewalPlanningChangeQueryResult = NonNullable<Awaited<ReturnType<typeof getRenewalPlanningChange>>>
export type GetRenewalPlanningChangeQueryError = unknown



export function useGetRenewalPlanningChange<TData = Awaited<ReturnType<typeof getRenewalPlanningChange>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRenewalPlanningChange>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetRenewalPlanningChangeQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConfirmRenewalPlanningChangeUrl = (id: string,) => {




  return `/api/renewal-planning-changes/${id}/confirm`
}

export const confirmRenewalPlanningChange = async (id: string,
    confirmRenewalPlanningChangeBody: ConfirmRenewalPlanningChangeBody, options?: Parameters<typeof customFetch>[1]): Promise<RenewalPlanningReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<RenewalPlanningReview>(getConfirmRenewalPlanningChangeUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(confirmRenewalPlanningChangeBody)
  }
);}





export const getConfirmRenewalPlanningChangeMutationKey = () => ['confirmRenewalPlanningChange'] as const;

export const getConfirmRenewalPlanningChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmRenewalPlanningChange>>, TError,ConfirmRenewalPlanningChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmRenewalPlanningChange>>, TError,ConfirmRenewalPlanningChangeMutationVariables, TContext> => {

const mutationKey = getConfirmRenewalPlanningChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmRenewalPlanningChange>>, ConfirmRenewalPlanningChangeMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  confirmRenewalPlanningChange(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmRenewalPlanningChangeMutationResult = NonNullable<Awaited<ReturnType<typeof confirmRenewalPlanningChange>>>
    export type ConfirmRenewalPlanningChangeMutationBody = ConfirmRenewalPlanningChangeBody
    export type ConfirmRenewalPlanningChangeMutationError = unknown
    export type ConfirmRenewalPlanningChangeMutationVariables = {id: string;data: ConfirmRenewalPlanningChangeBody}

    export const useConfirmRenewalPlanningChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmRenewalPlanningChange>>, TError,ConfirmRenewalPlanningChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmRenewalPlanningChange>>,
        TError,
        ConfirmRenewalPlanningChangeMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmRenewalPlanningChangeMutationOptions(options));
    }

export const getCancelRenewalPlanningChangeUrl = (id: string,) => {




  return `/api/renewal-planning-changes/${id}/cancel`
}

export const cancelRenewalPlanningChange = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<RenewalPlanningReview> => {

  return customFetch<RenewalPlanningReview>(getCancelRenewalPlanningChangeUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelRenewalPlanningChangeMutationKey = () => ['cancelRenewalPlanningChange'] as const;

export const getCancelRenewalPlanningChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelRenewalPlanningChange>>, TError,CancelRenewalPlanningChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelRenewalPlanningChange>>, TError,CancelRenewalPlanningChangeMutationVariables, TContext> => {

const mutationKey = getCancelRenewalPlanningChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelRenewalPlanningChange>>, CancelRenewalPlanningChangeMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  cancelRenewalPlanningChange(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelRenewalPlanningChangeMutationResult = NonNullable<Awaited<ReturnType<typeof cancelRenewalPlanningChange>>>

    export type CancelRenewalPlanningChangeMutationError = unknown
    export type CancelRenewalPlanningChangeMutationVariables = {id: string}

    export const useCancelRenewalPlanningChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelRenewalPlanningChange>>, TError,CancelRenewalPlanningChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelRenewalPlanningChange>>,
        TError,
        CancelRenewalPlanningChangeMutationVariables,
        TContext
      > => {
      return useMutation(getCancelRenewalPlanningChangeMutationOptions(options));
    }

export const getListAccountReviewsUrl = (params?: ListAccountReviewsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/account-reviews?${stringifiedParams}` : `/api/account-reviews`
}

export const listAccountReviews = async (params?: ListAccountReviewsParams, options?: Parameters<typeof customFetch>[1]): Promise<AccountReviewPage> => {

  return customFetch<AccountReviewPage>(getListAccountReviewsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAccountReviewsQueryKey = (params?: ListAccountReviewsParams,) => {
    return [
    `/api/account-reviews`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAccountReviewsQueryOptions = <TData = Awaited<ReturnType<typeof listAccountReviews>>, TError = unknown>(params?: ListAccountReviewsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAccountReviews>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAccountReviewsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAccountReviews>>> = ({ signal }) => listAccountReviews(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAccountReviews>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAccountReviewsQueryResult = NonNullable<Awaited<ReturnType<typeof listAccountReviews>>>
export type ListAccountReviewsQueryError = unknown



export function useListAccountReviews<TData = Awaited<ReturnType<typeof listAccountReviews>>, TError = unknown>(
 params?: ListAccountReviewsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAccountReviews>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAccountReviewsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetAccountReviewUrl = (id: string,) => {




  return `/api/account-reviews/${id}`
}

export const getAccountReview = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<AccountReviewRecord> => {

  return customFetch<AccountReviewRecord>(getGetAccountReviewUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAccountReviewQueryKey = (id: string,) => {
    return [
    `/api/account-reviews/${id}`
    ] as const;
    }


export const getGetAccountReviewQueryOptions = <TData = Awaited<ReturnType<typeof getAccountReview>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccountReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAccountReviewQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAccountReview>>> = ({ signal }) => getAccountReview(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAccountReview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAccountReviewQueryResult = NonNullable<Awaited<ReturnType<typeof getAccountReview>>>
export type GetAccountReviewQueryError = unknown



export function useGetAccountReview<TData = Awaited<ReturnType<typeof getAccountReview>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccountReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAccountReviewQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListAccountReviewHistoryUrl = (id: string,
    params?: ListAccountReviewHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/account-reviews/${id}/history?${stringifiedParams}` : `/api/account-reviews/${id}/history`
}

export const listAccountReviewHistory = async (id: string,
    params?: ListAccountReviewHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<AccountReviewHistoryPage> => {

  return customFetch<AccountReviewHistoryPage>(getListAccountReviewHistoryUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAccountReviewHistoryQueryKey = (id: string,
    params?: ListAccountReviewHistoryParams,) => {
    return [
    `/api/account-reviews/${id}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAccountReviewHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listAccountReviewHistory>>, TError = unknown>(id: string,
    params?: ListAccountReviewHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAccountReviewHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAccountReviewHistoryQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAccountReviewHistory>>> = ({ signal }) => listAccountReviewHistory(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAccountReviewHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAccountReviewHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listAccountReviewHistory>>>
export type ListAccountReviewHistoryQueryError = unknown



export function useListAccountReviewHistory<TData = Awaited<ReturnType<typeof listAccountReviewHistory>>, TError = unknown>(
 id: string,
    params?: ListAccountReviewHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAccountReviewHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAccountReviewHistoryQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewAccountReviewChangeUrl = () => {




  return `/api/account-review-changes`
}

export const reviewAccountReviewChange = async (accountReviewChangeRequest: AccountReviewChangeRequest, options?: Parameters<typeof customFetch>[1]): Promise<AccountReviewChangeReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccountReviewChangeReview>(getReviewAccountReviewChangeUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accountReviewChangeRequest)
  }
);}





export const getReviewAccountReviewChangeMutationKey = () => ['reviewAccountReviewChange'] as const;

export const getReviewAccountReviewChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewAccountReviewChange>>, TError,ReviewAccountReviewChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewAccountReviewChange>>, TError,ReviewAccountReviewChangeMutationVariables, TContext> => {

const mutationKey = getReviewAccountReviewChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewAccountReviewChange>>, ReviewAccountReviewChangeMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reviewAccountReviewChange(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewAccountReviewChangeMutationResult = NonNullable<Awaited<ReturnType<typeof reviewAccountReviewChange>>>
    export type ReviewAccountReviewChangeMutationBody = AccountReviewChangeRequest
    export type ReviewAccountReviewChangeMutationError = unknown
    export type ReviewAccountReviewChangeMutationVariables = {data: AccountReviewChangeRequest}

    export const useReviewAccountReviewChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewAccountReviewChange>>, TError,ReviewAccountReviewChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewAccountReviewChange>>,
        TError,
        ReviewAccountReviewChangeMutationVariables,
        TContext
      > => {
      return useMutation(getReviewAccountReviewChangeMutationOptions(options));
    }

export const getGetAccountReviewChangeUrl = (id: string,) => {




  return `/api/account-review-changes/${id}`
}

export const getAccountReviewChange = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<AccountReviewChangeReview> => {

  return customFetch<AccountReviewChangeReview>(getGetAccountReviewChangeUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAccountReviewChangeQueryKey = (id: string,) => {
    return [
    `/api/account-review-changes/${id}`
    ] as const;
    }


export const getGetAccountReviewChangeQueryOptions = <TData = Awaited<ReturnType<typeof getAccountReviewChange>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccountReviewChange>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAccountReviewChangeQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAccountReviewChange>>> = ({ signal }) => getAccountReviewChange(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAccountReviewChange>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAccountReviewChangeQueryResult = NonNullable<Awaited<ReturnType<typeof getAccountReviewChange>>>
export type GetAccountReviewChangeQueryError = unknown



export function useGetAccountReviewChange<TData = Awaited<ReturnType<typeof getAccountReviewChange>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccountReviewChange>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAccountReviewChangeQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConfirmAccountReviewChangeUrl = (id: string,) => {




  return `/api/account-review-changes/${id}/confirm`
}

export const confirmAccountReviewChange = async (id: string,
    deliveryConfirmation: DeliveryConfirmation, options?: Parameters<typeof customFetch>[1]): Promise<AccountReviewChangeReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccountReviewChangeReview>(getConfirmAccountReviewChangeUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(deliveryConfirmation)
  }
);}





export const getConfirmAccountReviewChangeMutationKey = () => ['confirmAccountReviewChange'] as const;

export const getConfirmAccountReviewChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmAccountReviewChange>>, TError,ConfirmAccountReviewChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmAccountReviewChange>>, TError,ConfirmAccountReviewChangeMutationVariables, TContext> => {

const mutationKey = getConfirmAccountReviewChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmAccountReviewChange>>, ConfirmAccountReviewChangeMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  confirmAccountReviewChange(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmAccountReviewChangeMutationResult = NonNullable<Awaited<ReturnType<typeof confirmAccountReviewChange>>>
    export type ConfirmAccountReviewChangeMutationBody = DeliveryConfirmation
    export type ConfirmAccountReviewChangeMutationError = unknown
    export type ConfirmAccountReviewChangeMutationVariables = {id: string;data: DeliveryConfirmation}

    export const useConfirmAccountReviewChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmAccountReviewChange>>, TError,ConfirmAccountReviewChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmAccountReviewChange>>,
        TError,
        ConfirmAccountReviewChangeMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmAccountReviewChangeMutationOptions(options));
    }

export const getCancelAccountReviewChangeUrl = (id: string,) => {




  return `/api/account-review-changes/${id}/cancel`
}

export const cancelAccountReviewChange = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<AccountReviewChangeReview> => {

  return customFetch<AccountReviewChangeReview>(getCancelAccountReviewChangeUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelAccountReviewChangeMutationKey = () => ['cancelAccountReviewChange'] as const;

export const getCancelAccountReviewChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelAccountReviewChange>>, TError,CancelAccountReviewChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelAccountReviewChange>>, TError,CancelAccountReviewChangeMutationVariables, TContext> => {

const mutationKey = getCancelAccountReviewChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelAccountReviewChange>>, CancelAccountReviewChangeMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  cancelAccountReviewChange(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelAccountReviewChangeMutationResult = NonNullable<Awaited<ReturnType<typeof cancelAccountReviewChange>>>

    export type CancelAccountReviewChangeMutationError = unknown
    export type CancelAccountReviewChangeMutationVariables = {id: string}

    export const useCancelAccountReviewChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelAccountReviewChange>>, TError,CancelAccountReviewChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelAccountReviewChange>>,
        TError,
        CancelAccountReviewChangeMutationVariables,
        TContext
      > => {
      return useMutation(getCancelAccountReviewChangeMutationOptions(options));
    }

export const getListServiceCasesUrl = (params?: ListServiceCasesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/service-cases?${stringifiedParams}` : `/api/service-cases`
}

export const listServiceCases = async (params?: ListServiceCasesParams, options?: Parameters<typeof customFetch>[1]): Promise<ServiceCasePage> => {

  return customFetch<ServiceCasePage>(getListServiceCasesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListServiceCasesQueryKey = (params?: ListServiceCasesParams,) => {
    return [
    `/api/service-cases`, ...(params ? [params] : [])
    ] as const;
    }


export const getListServiceCasesQueryOptions = <TData = Awaited<ReturnType<typeof listServiceCases>>, TError = unknown>(params?: ListServiceCasesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listServiceCases>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListServiceCasesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listServiceCases>>> = ({ signal }) => listServiceCases(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listServiceCases>>, TError, TData> & { queryKey: QueryKey }
}

export type ListServiceCasesQueryResult = NonNullable<Awaited<ReturnType<typeof listServiceCases>>>
export type ListServiceCasesQueryError = unknown



export function useListServiceCases<TData = Awaited<ReturnType<typeof listServiceCases>>, TError = unknown>(
 params?: ListServiceCasesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listServiceCases>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListServiceCasesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetServiceCaseUrl = (id: string,) => {




  return `/api/service-cases/${id}`
}

export const getServiceCase = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<ServiceCaseRecord> => {

  return customFetch<ServiceCaseRecord>(getGetServiceCaseUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetServiceCaseQueryKey = (id: string,) => {
    return [
    `/api/service-cases/${id}`
    ] as const;
    }


export const getGetServiceCaseQueryOptions = <TData = Awaited<ReturnType<typeof getServiceCase>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getServiceCase>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetServiceCaseQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getServiceCase>>> = ({ signal }) => getServiceCase(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getServiceCase>>, TError, TData> & { queryKey: QueryKey }
}

export type GetServiceCaseQueryResult = NonNullable<Awaited<ReturnType<typeof getServiceCase>>>
export type GetServiceCaseQueryError = unknown



export function useGetServiceCase<TData = Awaited<ReturnType<typeof getServiceCase>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getServiceCase>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetServiceCaseQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListServiceCaseHistoryUrl = (id: string,
    params?: ListServiceCaseHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/service-cases/${id}/history?${stringifiedParams}` : `/api/service-cases/${id}/history`
}

export const listServiceCaseHistory = async (id: string,
    params?: ListServiceCaseHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<ServiceCaseHistoryPage> => {

  return customFetch<ServiceCaseHistoryPage>(getListServiceCaseHistoryUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListServiceCaseHistoryQueryKey = (id: string,
    params?: ListServiceCaseHistoryParams,) => {
    return [
    `/api/service-cases/${id}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListServiceCaseHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listServiceCaseHistory>>, TError = unknown>(id: string,
    params?: ListServiceCaseHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listServiceCaseHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListServiceCaseHistoryQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listServiceCaseHistory>>> = ({ signal }) => listServiceCaseHistory(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listServiceCaseHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListServiceCaseHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listServiceCaseHistory>>>
export type ListServiceCaseHistoryQueryError = unknown



export function useListServiceCaseHistory<TData = Awaited<ReturnType<typeof listServiceCaseHistory>>, TError = unknown>(
 id: string,
    params?: ListServiceCaseHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listServiceCaseHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListServiceCaseHistoryQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewServiceCaseChangeUrl = () => {




  return `/api/service-case-reviews`
}

export const reviewServiceCaseChange = async (serviceCaseReviewRequest: ServiceCaseReviewRequest, options?: Parameters<typeof customFetch>[1]): Promise<ServiceCaseReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ServiceCaseReview>(getReviewServiceCaseChangeUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(serviceCaseReviewRequest)
  }
);}





export const getReviewServiceCaseChangeMutationKey = () => ['reviewServiceCaseChange'] as const;

export const getReviewServiceCaseChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewServiceCaseChange>>, TError,ReviewServiceCaseChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewServiceCaseChange>>, TError,ReviewServiceCaseChangeMutationVariables, TContext> => {

const mutationKey = getReviewServiceCaseChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewServiceCaseChange>>, ReviewServiceCaseChangeMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reviewServiceCaseChange(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewServiceCaseChangeMutationResult = NonNullable<Awaited<ReturnType<typeof reviewServiceCaseChange>>>
    export type ReviewServiceCaseChangeMutationBody = ServiceCaseReviewRequest
    export type ReviewServiceCaseChangeMutationError = unknown
    export type ReviewServiceCaseChangeMutationVariables = {data: ServiceCaseReviewRequest}

    export const useReviewServiceCaseChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewServiceCaseChange>>, TError,ReviewServiceCaseChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewServiceCaseChange>>,
        TError,
        ReviewServiceCaseChangeMutationVariables,
        TContext
      > => {
      return useMutation(getReviewServiceCaseChangeMutationOptions(options));
    }

export const getGetServiceCaseReviewUrl = (id: string,) => {




  return `/api/service-case-reviews/${id}`
}

export const getServiceCaseReview = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<ServiceCaseReview> => {

  return customFetch<ServiceCaseReview>(getGetServiceCaseReviewUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetServiceCaseReviewQueryKey = (id: string,) => {
    return [
    `/api/service-case-reviews/${id}`
    ] as const;
    }


export const getGetServiceCaseReviewQueryOptions = <TData = Awaited<ReturnType<typeof getServiceCaseReview>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getServiceCaseReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetServiceCaseReviewQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getServiceCaseReview>>> = ({ signal }) => getServiceCaseReview(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getServiceCaseReview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetServiceCaseReviewQueryResult = NonNullable<Awaited<ReturnType<typeof getServiceCaseReview>>>
export type GetServiceCaseReviewQueryError = unknown



export function useGetServiceCaseReview<TData = Awaited<ReturnType<typeof getServiceCaseReview>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getServiceCaseReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetServiceCaseReviewQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConfirmServiceCaseChangeUrl = (id: string,) => {




  return `/api/service-case-reviews/${id}/confirm`
}

export const confirmServiceCaseChange = async (id: string,
    deliveryConfirmation: DeliveryConfirmation, options?: Parameters<typeof customFetch>[1]): Promise<ServiceCaseReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ServiceCaseReview>(getConfirmServiceCaseChangeUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(deliveryConfirmation)
  }
);}





export const getConfirmServiceCaseChangeMutationKey = () => ['confirmServiceCaseChange'] as const;

export const getConfirmServiceCaseChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmServiceCaseChange>>, TError,ConfirmServiceCaseChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmServiceCaseChange>>, TError,ConfirmServiceCaseChangeMutationVariables, TContext> => {

const mutationKey = getConfirmServiceCaseChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmServiceCaseChange>>, ConfirmServiceCaseChangeMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  confirmServiceCaseChange(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmServiceCaseChangeMutationResult = NonNullable<Awaited<ReturnType<typeof confirmServiceCaseChange>>>
    export type ConfirmServiceCaseChangeMutationBody = DeliveryConfirmation
    export type ConfirmServiceCaseChangeMutationError = unknown
    export type ConfirmServiceCaseChangeMutationVariables = {id: string;data: DeliveryConfirmation}

    export const useConfirmServiceCaseChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmServiceCaseChange>>, TError,ConfirmServiceCaseChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmServiceCaseChange>>,
        TError,
        ConfirmServiceCaseChangeMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmServiceCaseChangeMutationOptions(options));
    }

export const getCancelServiceCaseChangeUrl = (id: string,) => {




  return `/api/service-case-reviews/${id}/cancel`
}

export const cancelServiceCaseChange = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<ServiceCaseReview> => {

  return customFetch<ServiceCaseReview>(getCancelServiceCaseChangeUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelServiceCaseChangeMutationKey = () => ['cancelServiceCaseChange'] as const;

export const getCancelServiceCaseChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelServiceCaseChange>>, TError,CancelServiceCaseChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelServiceCaseChange>>, TError,CancelServiceCaseChangeMutationVariables, TContext> => {

const mutationKey = getCancelServiceCaseChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelServiceCaseChange>>, CancelServiceCaseChangeMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  cancelServiceCaseChange(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelServiceCaseChangeMutationResult = NonNullable<Awaited<ReturnType<typeof cancelServiceCaseChange>>>

    export type CancelServiceCaseChangeMutationError = unknown
    export type CancelServiceCaseChangeMutationVariables = {id: string}

    export const useCancelServiceCaseChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelServiceCaseChange>>, TError,CancelServiceCaseChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelServiceCaseChange>>,
        TError,
        CancelServiceCaseChangeMutationVariables,
        TContext
      > => {
      return useMutation(getCancelServiceCaseChangeMutationOptions(options));
    }

export const getListDeliveryRecordsUrl = (params?: ListDeliveryRecordsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/delivery-plans?${stringifiedParams}` : `/api/delivery-plans`
}

export const listDeliveryRecords = async (params?: ListDeliveryRecordsParams, options?: Parameters<typeof customFetch>[1]): Promise<DeliveryPage> => {

  return customFetch<DeliveryPage>(getListDeliveryRecordsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDeliveryRecordsQueryKey = (params?: ListDeliveryRecordsParams,) => {
    return [
    `/api/delivery-plans`, ...(params ? [params] : [])
    ] as const;
    }


export const getListDeliveryRecordsQueryOptions = <TData = Awaited<ReturnType<typeof listDeliveryRecords>>, TError = unknown>(params?: ListDeliveryRecordsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDeliveryRecords>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDeliveryRecordsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDeliveryRecords>>> = ({ signal }) => listDeliveryRecords(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDeliveryRecords>>, TError, TData> & { queryKey: QueryKey }
}

export type ListDeliveryRecordsQueryResult = NonNullable<Awaited<ReturnType<typeof listDeliveryRecords>>>
export type ListDeliveryRecordsQueryError = unknown



export function useListDeliveryRecords<TData = Awaited<ReturnType<typeof listDeliveryRecords>>, TError = unknown>(
 params?: ListDeliveryRecordsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDeliveryRecords>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListDeliveryRecordsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetDeliveryRecordUrl = (id: string,) => {




  return `/api/delivery-plans/${id}`
}

export const getDeliveryRecord = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<DeliveryRecord> => {

  return customFetch<DeliveryRecord>(getGetDeliveryRecordUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDeliveryRecordQueryKey = (id: string,) => {
    return [
    `/api/delivery-plans/${id}`
    ] as const;
    }


export const getGetDeliveryRecordQueryOptions = <TData = Awaited<ReturnType<typeof getDeliveryRecord>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDeliveryRecord>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDeliveryRecordQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDeliveryRecord>>> = ({ signal }) => getDeliveryRecord(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDeliveryRecord>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDeliveryRecordQueryResult = NonNullable<Awaited<ReturnType<typeof getDeliveryRecord>>>
export type GetDeliveryRecordQueryError = unknown



export function useGetDeliveryRecord<TData = Awaited<ReturnType<typeof getDeliveryRecord>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDeliveryRecord>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDeliveryRecordQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListDeliveryHistoryUrl = (id: string,
    params?: ListDeliveryHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/delivery-plans/${id}/history?${stringifiedParams}` : `/api/delivery-plans/${id}/history`
}

export const listDeliveryHistory = async (id: string,
    params?: ListDeliveryHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<DeliveryHistoryPage> => {

  return customFetch<DeliveryHistoryPage>(getListDeliveryHistoryUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDeliveryHistoryQueryKey = (id: string,
    params?: ListDeliveryHistoryParams,) => {
    return [
    `/api/delivery-plans/${id}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListDeliveryHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listDeliveryHistory>>, TError = unknown>(id: string,
    params?: ListDeliveryHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDeliveryHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDeliveryHistoryQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDeliveryHistory>>> = ({ signal }) => listDeliveryHistory(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDeliveryHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListDeliveryHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listDeliveryHistory>>>
export type ListDeliveryHistoryQueryError = unknown



export function useListDeliveryHistory<TData = Awaited<ReturnType<typeof listDeliveryHistory>>, TError = unknown>(
 id: string,
    params?: ListDeliveryHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDeliveryHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListDeliveryHistoryQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewDeliveryChangeUrl = () => {




  return `/api/delivery-reviews`
}

export const reviewDeliveryChange = async (deliveryReviewRequest: DeliveryReviewRequest, options?: Parameters<typeof customFetch>[1]): Promise<DeliveryReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DeliveryReview>(getReviewDeliveryChangeUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(deliveryReviewRequest)
  }
);}





export const getReviewDeliveryChangeMutationKey = () => ['reviewDeliveryChange'] as const;

export const getReviewDeliveryChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewDeliveryChange>>, TError,ReviewDeliveryChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewDeliveryChange>>, TError,ReviewDeliveryChangeMutationVariables, TContext> => {

const mutationKey = getReviewDeliveryChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewDeliveryChange>>, ReviewDeliveryChangeMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reviewDeliveryChange(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewDeliveryChangeMutationResult = NonNullable<Awaited<ReturnType<typeof reviewDeliveryChange>>>
    export type ReviewDeliveryChangeMutationBody = DeliveryReviewRequest
    export type ReviewDeliveryChangeMutationError = unknown
    export type ReviewDeliveryChangeMutationVariables = {data: DeliveryReviewRequest}

    export const useReviewDeliveryChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewDeliveryChange>>, TError,ReviewDeliveryChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewDeliveryChange>>,
        TError,
        ReviewDeliveryChangeMutationVariables,
        TContext
      > => {
      return useMutation(getReviewDeliveryChangeMutationOptions(options));
    }

export const getGetDeliveryReviewUrl = (id: string,) => {




  return `/api/delivery-reviews/${id}`
}

export const getDeliveryReview = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<DeliveryReview> => {

  return customFetch<DeliveryReview>(getGetDeliveryReviewUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDeliveryReviewQueryKey = (id: string,) => {
    return [
    `/api/delivery-reviews/${id}`
    ] as const;
    }


export const getGetDeliveryReviewQueryOptions = <TData = Awaited<ReturnType<typeof getDeliveryReview>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDeliveryReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDeliveryReviewQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDeliveryReview>>> = ({ signal }) => getDeliveryReview(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDeliveryReview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDeliveryReviewQueryResult = NonNullable<Awaited<ReturnType<typeof getDeliveryReview>>>
export type GetDeliveryReviewQueryError = unknown



export function useGetDeliveryReview<TData = Awaited<ReturnType<typeof getDeliveryReview>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDeliveryReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDeliveryReviewQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConfirmDeliveryChangeUrl = (id: string,) => {




  return `/api/delivery-reviews/${id}/confirm`
}

export const confirmDeliveryChange = async (id: string,
    deliveryConfirmation: DeliveryConfirmation, options?: Parameters<typeof customFetch>[1]): Promise<DeliveryReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DeliveryReview>(getConfirmDeliveryChangeUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(deliveryConfirmation)
  }
);}





export const getConfirmDeliveryChangeMutationKey = () => ['confirmDeliveryChange'] as const;

export const getConfirmDeliveryChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmDeliveryChange>>, TError,ConfirmDeliveryChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmDeliveryChange>>, TError,ConfirmDeliveryChangeMutationVariables, TContext> => {

const mutationKey = getConfirmDeliveryChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmDeliveryChange>>, ConfirmDeliveryChangeMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  confirmDeliveryChange(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmDeliveryChangeMutationResult = NonNullable<Awaited<ReturnType<typeof confirmDeliveryChange>>>
    export type ConfirmDeliveryChangeMutationBody = DeliveryConfirmation
    export type ConfirmDeliveryChangeMutationError = unknown
    export type ConfirmDeliveryChangeMutationVariables = {id: string;data: DeliveryConfirmation}

    export const useConfirmDeliveryChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmDeliveryChange>>, TError,ConfirmDeliveryChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmDeliveryChange>>,
        TError,
        ConfirmDeliveryChangeMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmDeliveryChangeMutationOptions(options));
    }

export const getCancelDeliveryChangeUrl = (id: string,) => {




  return `/api/delivery-reviews/${id}/cancel`
}

export const cancelDeliveryChange = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<DeliveryReview> => {

  return customFetch<DeliveryReview>(getCancelDeliveryChangeUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelDeliveryChangeMutationKey = () => ['cancelDeliveryChange'] as const;

export const getCancelDeliveryChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelDeliveryChange>>, TError,CancelDeliveryChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelDeliveryChange>>, TError,CancelDeliveryChangeMutationVariables, TContext> => {

const mutationKey = getCancelDeliveryChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelDeliveryChange>>, CancelDeliveryChangeMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  cancelDeliveryChange(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelDeliveryChangeMutationResult = NonNullable<Awaited<ReturnType<typeof cancelDeliveryChange>>>

    export type CancelDeliveryChangeMutationError = unknown
    export type CancelDeliveryChangeMutationVariables = {id: string}

    export const useCancelDeliveryChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelDeliveryChange>>, TError,CancelDeliveryChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelDeliveryChange>>,
        TError,
        CancelDeliveryChangeMutationVariables,
        TContext
      > => {
      return useMutation(getCancelDeliveryChangeMutationOptions(options));
    }

export const getGetOpportunityForecastUrl = (params?: GetOpportunityForecastParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/opportunity-forecast?${stringifiedParams}` : `/api/opportunity-forecast`
}

/**
 * @summary Complete scoped read-only opportunity forecast with stable paged groups and drilldowns
 */
export const getOpportunityForecast = async (params?: GetOpportunityForecastParams, options?: Parameters<typeof customFetch>[1]): Promise<OpportunityForecast> => {

  return customFetch<OpportunityForecast>(getGetOpportunityForecastUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetOpportunityForecastQueryKey = (params?: GetOpportunityForecastParams,) => {
    return [
    `/api/opportunity-forecast`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetOpportunityForecastQueryOptions = <TData = Awaited<ReturnType<typeof getOpportunityForecast>>, TError = void>(params?: GetOpportunityForecastParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getOpportunityForecast>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetOpportunityForecastQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getOpportunityForecast>>> = ({ signal }) => getOpportunityForecast(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getOpportunityForecast>>, TError, TData> & { queryKey: QueryKey }
}

export type GetOpportunityForecastQueryResult = NonNullable<Awaited<ReturnType<typeof getOpportunityForecast>>>
export type GetOpportunityForecastQueryError = void


/**
 * @summary Complete scoped read-only opportunity forecast with stable paged groups and drilldowns
 */

export function useGetOpportunityForecast<TData = Awaited<ReturnType<typeof getOpportunityForecast>>, TError = void>(
 params?: GetOpportunityForecastParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getOpportunityForecast>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetOpportunityForecastQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListAgreementRegisterUrl = (params?: ListAgreementRegisterParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agreement-register?${stringifiedParams}` : `/api/agreement-register`
}

export const listAgreementRegister = async (params?: ListAgreementRegisterParams, options?: Parameters<typeof customFetch>[1]): Promise<AgreementRegisterPage> => {

  return customFetch<AgreementRegisterPage>(getListAgreementRegisterUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgreementRegisterQueryKey = (params?: ListAgreementRegisterParams,) => {
    return [
    `/api/agreement-register`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAgreementRegisterQueryOptions = <TData = Awaited<ReturnType<typeof listAgreementRegister>>, TError = unknown>(params?: ListAgreementRegisterParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgreementRegister>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgreementRegisterQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgreementRegister>>> = ({ signal }) => listAgreementRegister(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgreementRegister>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAgreementRegisterQueryResult = NonNullable<Awaited<ReturnType<typeof listAgreementRegister>>>
export type ListAgreementRegisterQueryError = unknown



export function useListAgreementRegister<TData = Awaited<ReturnType<typeof listAgreementRegister>>, TError = unknown>(
 params?: ListAgreementRegisterParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgreementRegister>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAgreementRegisterQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListAgreementRegisterChoicesUrl = (params: ListAgreementRegisterChoicesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agreement-register-choices?${stringifiedParams}` : `/api/agreement-register-choices`
}

export const listAgreementRegisterChoices = async (params: ListAgreementRegisterChoicesParams, options?: Parameters<typeof customFetch>[1]): Promise<AgreementChoicePage> => {

  return customFetch<AgreementChoicePage>(getListAgreementRegisterChoicesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgreementRegisterChoicesQueryKey = (params?: ListAgreementRegisterChoicesParams,) => {
    return [
    `/api/agreement-register-choices`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAgreementRegisterChoicesQueryOptions = <TData = Awaited<ReturnType<typeof listAgreementRegisterChoices>>, TError = unknown>(params: ListAgreementRegisterChoicesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgreementRegisterChoices>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgreementRegisterChoicesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgreementRegisterChoices>>> = ({ signal }) => listAgreementRegisterChoices(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgreementRegisterChoices>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAgreementRegisterChoicesQueryResult = NonNullable<Awaited<ReturnType<typeof listAgreementRegisterChoices>>>
export type ListAgreementRegisterChoicesQueryError = unknown



export function useListAgreementRegisterChoices<TData = Awaited<ReturnType<typeof listAgreementRegisterChoices>>, TError = unknown>(
 params: ListAgreementRegisterChoicesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgreementRegisterChoices>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAgreementRegisterChoicesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetAgreementRegisterRecordUrl = (resource: 'agreement' | 'obligation',
    sourceKind: 'partner' | 'general',
    id: string,) => {




  return `/api/agreement-register/${resource}/${sourceKind}/${id}`
}

export const getAgreementRegisterRecord = async (resource: 'agreement' | 'obligation',
    sourceKind: 'partner' | 'general',
    id: string, options?: Parameters<typeof customFetch>[1]): Promise<AgreementRegisterRecord> => {

  return customFetch<AgreementRegisterRecord>(getGetAgreementRegisterRecordUrl(resource,sourceKind,id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAgreementRegisterRecordQueryKey = (resource: 'agreement' | 'obligation',
    sourceKind: 'partner' | 'general',
    id: string,) => {
    return [
    `/api/agreement-register/${resource}/${sourceKind}/${id}`
    ] as const;
    }


export const getGetAgreementRegisterRecordQueryOptions = <TData = Awaited<ReturnType<typeof getAgreementRegisterRecord>>, TError = unknown>(resource: 'agreement' | 'obligation',
    sourceKind: 'partner' | 'general',
    id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgreementRegisterRecord>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAgreementRegisterRecordQueryKey(resource,sourceKind,id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAgreementRegisterRecord>>> = ({ signal }) => getAgreementRegisterRecord(resource,sourceKind,id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: resource !== null && resource !== undefined && sourceKind !== null && sourceKind !== undefined && id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAgreementRegisterRecord>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAgreementRegisterRecordQueryResult = NonNullable<Awaited<ReturnType<typeof getAgreementRegisterRecord>>>
export type GetAgreementRegisterRecordQueryError = unknown



export function useGetAgreementRegisterRecord<TData = Awaited<ReturnType<typeof getAgreementRegisterRecord>>, TError = unknown>(
 resource: 'agreement' | 'obligation',
    sourceKind: 'partner' | 'general',
    id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgreementRegisterRecord>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAgreementRegisterRecordQueryOptions(resource,sourceKind,id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListAgreementRegisterHistoryUrl = (resource: 'agreement' | 'obligation',
    sourceKind: 'partner' | 'general',
    id: string,
    params?: ListAgreementRegisterHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agreement-register/${resource}/${sourceKind}/${id}/history?${stringifiedParams}` : `/api/agreement-register/${resource}/${sourceKind}/${id}/history`
}

export const listAgreementRegisterHistory = async (resource: 'agreement' | 'obligation',
    sourceKind: 'partner' | 'general',
    id: string,
    params?: ListAgreementRegisterHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<AgreementHistoryPage> => {

  return customFetch<AgreementHistoryPage>(getListAgreementRegisterHistoryUrl(resource,sourceKind,id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgreementRegisterHistoryQueryKey = (resource: 'agreement' | 'obligation',
    sourceKind: 'partner' | 'general',
    id: string,
    params?: ListAgreementRegisterHistoryParams,) => {
    return [
    `/api/agreement-register/${resource}/${sourceKind}/${id}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAgreementRegisterHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listAgreementRegisterHistory>>, TError = unknown>(resource: 'agreement' | 'obligation',
    sourceKind: 'partner' | 'general',
    id: string,
    params?: ListAgreementRegisterHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgreementRegisterHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgreementRegisterHistoryQueryKey(resource,sourceKind,id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgreementRegisterHistory>>> = ({ signal }) => listAgreementRegisterHistory(resource,sourceKind,id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: resource !== null && resource !== undefined && sourceKind !== null && sourceKind !== undefined && id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgreementRegisterHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAgreementRegisterHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listAgreementRegisterHistory>>>
export type ListAgreementRegisterHistoryQueryError = unknown



export function useListAgreementRegisterHistory<TData = Awaited<ReturnType<typeof listAgreementRegisterHistory>>, TError = unknown>(
 resource: 'agreement' | 'obligation',
    sourceKind: 'partner' | 'general',
    id: string,
    params?: ListAgreementRegisterHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgreementRegisterHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAgreementRegisterHistoryQueryOptions(resource,sourceKind,id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewAgreementRegisterChangeUrl = () => {




  return `/api/agreement-register-reviews`
}

export const reviewAgreementRegisterChange = async (reviewAgreementRegisterChangeBody: ReviewAgreementRegisterChangeBody, options?: Parameters<typeof customFetch>[1]): Promise<AgreementRegisterReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgreementRegisterReview>(getReviewAgreementRegisterChangeUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewAgreementRegisterChangeBody)
  }
);}





export const getReviewAgreementRegisterChangeMutationKey = () => ['reviewAgreementRegisterChange'] as const;

export const getReviewAgreementRegisterChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewAgreementRegisterChange>>, TError,ReviewAgreementRegisterChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewAgreementRegisterChange>>, TError,ReviewAgreementRegisterChangeMutationVariables, TContext> => {

const mutationKey = getReviewAgreementRegisterChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewAgreementRegisterChange>>, ReviewAgreementRegisterChangeMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reviewAgreementRegisterChange(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewAgreementRegisterChangeMutationResult = NonNullable<Awaited<ReturnType<typeof reviewAgreementRegisterChange>>>
    export type ReviewAgreementRegisterChangeMutationBody = ReviewAgreementRegisterChangeBody
    export type ReviewAgreementRegisterChangeMutationError = unknown
    export type ReviewAgreementRegisterChangeMutationVariables = {data: ReviewAgreementRegisterChangeBody}

    export const useReviewAgreementRegisterChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewAgreementRegisterChange>>, TError,ReviewAgreementRegisterChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewAgreementRegisterChange>>,
        TError,
        ReviewAgreementRegisterChangeMutationVariables,
        TContext
      > => {
      return useMutation(getReviewAgreementRegisterChangeMutationOptions(options));
    }

export const getGetAgreementRegisterReviewUrl = (id: string,) => {




  return `/api/agreement-register-reviews/${id}`
}

export const getAgreementRegisterReview = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<AgreementRegisterReview> => {

  return customFetch<AgreementRegisterReview>(getGetAgreementRegisterReviewUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAgreementRegisterReviewQueryKey = (id: string,) => {
    return [
    `/api/agreement-register-reviews/${id}`
    ] as const;
    }


export const getGetAgreementRegisterReviewQueryOptions = <TData = Awaited<ReturnType<typeof getAgreementRegisterReview>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgreementRegisterReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAgreementRegisterReviewQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAgreementRegisterReview>>> = ({ signal }) => getAgreementRegisterReview(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAgreementRegisterReview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAgreementRegisterReviewQueryResult = NonNullable<Awaited<ReturnType<typeof getAgreementRegisterReview>>>
export type GetAgreementRegisterReviewQueryError = unknown



export function useGetAgreementRegisterReview<TData = Awaited<ReturnType<typeof getAgreementRegisterReview>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgreementRegisterReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAgreementRegisterReviewQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConfirmAgreementRegisterChangeUrl = (id: string,) => {




  return `/api/agreement-register-reviews/${id}/confirm`
}

export const confirmAgreementRegisterChange = async (id: string,
    confirmAgreementRegisterChangeBody: ConfirmAgreementRegisterChangeBody, options?: Parameters<typeof customFetch>[1]): Promise<AgreementRegisterReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgreementRegisterReview>(getConfirmAgreementRegisterChangeUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(confirmAgreementRegisterChangeBody)
  }
);}





export const getConfirmAgreementRegisterChangeMutationKey = () => ['confirmAgreementRegisterChange'] as const;

export const getConfirmAgreementRegisterChangeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmAgreementRegisterChange>>, TError,ConfirmAgreementRegisterChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmAgreementRegisterChange>>, TError,ConfirmAgreementRegisterChangeMutationVariables, TContext> => {

const mutationKey = getConfirmAgreementRegisterChangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmAgreementRegisterChange>>, ConfirmAgreementRegisterChangeMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  confirmAgreementRegisterChange(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmAgreementRegisterChangeMutationResult = NonNullable<Awaited<ReturnType<typeof confirmAgreementRegisterChange>>>
    export type ConfirmAgreementRegisterChangeMutationBody = ConfirmAgreementRegisterChangeBody
    export type ConfirmAgreementRegisterChangeMutationError = unknown
    export type ConfirmAgreementRegisterChangeMutationVariables = {id: string;data: ConfirmAgreementRegisterChangeBody}

    export const useConfirmAgreementRegisterChange = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmAgreementRegisterChange>>, TError,ConfirmAgreementRegisterChangeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmAgreementRegisterChange>>,
        TError,
        ConfirmAgreementRegisterChangeMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmAgreementRegisterChangeMutationOptions(options));
    }

export const getCancelAgreementRegisterReviewUrl = (id: string,) => {




  return `/api/agreement-register-reviews/${id}/cancel`
}

export const cancelAgreementRegisterReview = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<AgreementRegisterReview> => {

  return customFetch<AgreementRegisterReview>(getCancelAgreementRegisterReviewUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelAgreementRegisterReviewMutationKey = () => ['cancelAgreementRegisterReview'] as const;

export const getCancelAgreementRegisterReviewMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelAgreementRegisterReview>>, TError,CancelAgreementRegisterReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelAgreementRegisterReview>>, TError,CancelAgreementRegisterReviewMutationVariables, TContext> => {

const mutationKey = getCancelAgreementRegisterReviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelAgreementRegisterReview>>, CancelAgreementRegisterReviewMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  cancelAgreementRegisterReview(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelAgreementRegisterReviewMutationResult = NonNullable<Awaited<ReturnType<typeof cancelAgreementRegisterReview>>>

    export type CancelAgreementRegisterReviewMutationError = unknown
    export type CancelAgreementRegisterReviewMutationVariables = {id: string}

    export const useCancelAgreementRegisterReview = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelAgreementRegisterReview>>, TError,CancelAgreementRegisterReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelAgreementRegisterReview>>,
        TError,
        CancelAgreementRegisterReviewMutationVariables,
        TContext
      > => {
      return useMutation(getCancelAgreementRegisterReviewMutationOptions(options));
    }

export const getListCrmStakeholdersUrl = (params?: ListCrmStakeholdersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/crm-stakeholders?${stringifiedParams}` : `/api/crm-stakeholders`
}

/**
 * @summary listCrmStakeholders
 */
export const listCrmStakeholders = async (params?: ListCrmStakeholdersParams, options?: Parameters<typeof customFetch>[1]): Promise<CrmStakeholderPage> => {

  return customFetch<CrmStakeholderPage>(getListCrmStakeholdersUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCrmStakeholdersQueryKey = (params?: ListCrmStakeholdersParams,) => {
    return [
    `/api/crm-stakeholders`, ...(params ? [params] : [])
    ] as const;
    }


export const getListCrmStakeholdersQueryOptions = <TData = Awaited<ReturnType<typeof listCrmStakeholders>>, TError = unknown>(params?: ListCrmStakeholdersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCrmStakeholders>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCrmStakeholdersQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCrmStakeholders>>> = ({ signal }) => listCrmStakeholders(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCrmStakeholders>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCrmStakeholdersQueryResult = NonNullable<Awaited<ReturnType<typeof listCrmStakeholders>>>
export type ListCrmStakeholdersQueryError = unknown


/**
 * @summary listCrmStakeholders
 */

export function useListCrmStakeholders<TData = Awaited<ReturnType<typeof listCrmStakeholders>>, TError = unknown>(
 params?: ListCrmStakeholdersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCrmStakeholders>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCrmStakeholdersQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetCrmStakeholderUrl = (id: string,) => {




  return `/api/crm-stakeholders/${id}`
}

/**
 * @summary getCrmStakeholder
 */
export const getCrmStakeholder = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<CrmStakeholder> => {

  return customFetch<CrmStakeholder>(getGetCrmStakeholderUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetCrmStakeholderQueryKey = (id: string,) => {
    return [
    `/api/crm-stakeholders/${id}`
    ] as const;
    }


export const getGetCrmStakeholderQueryOptions = <TData = Awaited<ReturnType<typeof getCrmStakeholder>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCrmStakeholder>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCrmStakeholderQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCrmStakeholder>>> = ({ signal }) => getCrmStakeholder(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCrmStakeholder>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCrmStakeholderQueryResult = NonNullable<Awaited<ReturnType<typeof getCrmStakeholder>>>
export type GetCrmStakeholderQueryError = unknown


/**
 * @summary getCrmStakeholder
 */

export function useGetCrmStakeholder<TData = Awaited<ReturnType<typeof getCrmStakeholder>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCrmStakeholder>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCrmStakeholderQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListCrmStakeholderHistoryUrl = (id: string,
    params?: ListCrmStakeholderHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/crm-stakeholders/${id}/history?${stringifiedParams}` : `/api/crm-stakeholders/${id}/history`
}

/**
 * @summary listCrmStakeholderHistory
 */
export const listCrmStakeholderHistory = async (id: string,
    params?: ListCrmStakeholderHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<CrmStakeholderHistoryPage> => {

  return customFetch<CrmStakeholderHistoryPage>(getListCrmStakeholderHistoryUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCrmStakeholderHistoryQueryKey = (id: string,
    params?: ListCrmStakeholderHistoryParams,) => {
    return [
    `/api/crm-stakeholders/${id}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListCrmStakeholderHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listCrmStakeholderHistory>>, TError = unknown>(id: string,
    params?: ListCrmStakeholderHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCrmStakeholderHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCrmStakeholderHistoryQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCrmStakeholderHistory>>> = ({ signal }) => listCrmStakeholderHistory(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCrmStakeholderHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCrmStakeholderHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listCrmStakeholderHistory>>>
export type ListCrmStakeholderHistoryQueryError = unknown


/**
 * @summary listCrmStakeholderHistory
 */

export function useListCrmStakeholderHistory<TData = Awaited<ReturnType<typeof listCrmStakeholderHistory>>, TError = unknown>(
 id: string,
    params?: ListCrmStakeholderHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCrmStakeholderHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCrmStakeholderHistoryQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListCrmStakeholderChoicesUrl = (params: ListCrmStakeholderChoicesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/crm-stakeholder-choices?${stringifiedParams}` : `/api/crm-stakeholder-choices`
}

/**
 * @summary listCrmStakeholderChoices
 */
export const listCrmStakeholderChoices = async (params: ListCrmStakeholderChoicesParams, options?: Parameters<typeof customFetch>[1]): Promise<CrmStakeholderChoicePage> => {

  return customFetch<CrmStakeholderChoicePage>(getListCrmStakeholderChoicesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCrmStakeholderChoicesQueryKey = (params?: ListCrmStakeholderChoicesParams,) => {
    return [
    `/api/crm-stakeholder-choices`, ...(params ? [params] : [])
    ] as const;
    }


export const getListCrmStakeholderChoicesQueryOptions = <TData = Awaited<ReturnType<typeof listCrmStakeholderChoices>>, TError = unknown>(params: ListCrmStakeholderChoicesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCrmStakeholderChoices>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCrmStakeholderChoicesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCrmStakeholderChoices>>> = ({ signal }) => listCrmStakeholderChoices(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCrmStakeholderChoices>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCrmStakeholderChoicesQueryResult = NonNullable<Awaited<ReturnType<typeof listCrmStakeholderChoices>>>
export type ListCrmStakeholderChoicesQueryError = unknown


/**
 * @summary listCrmStakeholderChoices
 */

export function useListCrmStakeholderChoices<TData = Awaited<ReturnType<typeof listCrmStakeholderChoices>>, TError = unknown>(
 params: ListCrmStakeholderChoicesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCrmStakeholderChoices>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCrmStakeholderChoicesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewCrmStakeholderUrl = () => {




  return `/api/crm-stakeholder-reviews`
}

/**
 * @summary reviewCrmStakeholder
 */
export const reviewCrmStakeholder = async (reviewCrmStakeholderBody: ReviewCrmStakeholderBody, options?: Parameters<typeof customFetch>[1]): Promise<CrmStakeholderReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CrmStakeholderReview>(getReviewCrmStakeholderUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewCrmStakeholderBody)
  }
);}





export const getReviewCrmStakeholderMutationKey = () => ['reviewCrmStakeholder'] as const;

export const getReviewCrmStakeholderMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewCrmStakeholder>>, TError,ReviewCrmStakeholderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewCrmStakeholder>>, TError,ReviewCrmStakeholderMutationVariables, TContext> => {

const mutationKey = getReviewCrmStakeholderMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewCrmStakeholder>>, ReviewCrmStakeholderMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reviewCrmStakeholder(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewCrmStakeholderMutationResult = NonNullable<Awaited<ReturnType<typeof reviewCrmStakeholder>>>
    export type ReviewCrmStakeholderMutationBody = ReviewCrmStakeholderBody
    export type ReviewCrmStakeholderMutationError = unknown
    export type ReviewCrmStakeholderMutationVariables = {data: ReviewCrmStakeholderBody}

    /**
 * @summary reviewCrmStakeholder
 */
export const useReviewCrmStakeholder = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewCrmStakeholder>>, TError,ReviewCrmStakeholderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewCrmStakeholder>>,
        TError,
        ReviewCrmStakeholderMutationVariables,
        TContext
      > => {
      return useMutation(getReviewCrmStakeholderMutationOptions(options));
    }

export const getGetCrmStakeholderReviewUrl = (id: string,) => {




  return `/api/crm-stakeholder-reviews/${id}`
}

/**
 * @summary getCrmStakeholderReview
 */
export const getCrmStakeholderReview = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<CrmStakeholderReview> => {

  return customFetch<CrmStakeholderReview>(getGetCrmStakeholderReviewUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetCrmStakeholderReviewQueryKey = (id: string,) => {
    return [
    `/api/crm-stakeholder-reviews/${id}`
    ] as const;
    }


export const getGetCrmStakeholderReviewQueryOptions = <TData = Awaited<ReturnType<typeof getCrmStakeholderReview>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCrmStakeholderReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCrmStakeholderReviewQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCrmStakeholderReview>>> = ({ signal }) => getCrmStakeholderReview(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCrmStakeholderReview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCrmStakeholderReviewQueryResult = NonNullable<Awaited<ReturnType<typeof getCrmStakeholderReview>>>
export type GetCrmStakeholderReviewQueryError = unknown


/**
 * @summary getCrmStakeholderReview
 */

export function useGetCrmStakeholderReview<TData = Awaited<ReturnType<typeof getCrmStakeholderReview>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCrmStakeholderReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCrmStakeholderReviewQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConfirmCrmStakeholderUrl = (id: string,) => {




  return `/api/crm-stakeholder-reviews/${id}/confirm`
}

/**
 * @summary confirmCrmStakeholder
 */
export const confirmCrmStakeholder = async (id: string,
    confirmCrmStakeholderBody: ConfirmCrmStakeholderBody, options?: Parameters<typeof customFetch>[1]): Promise<CrmStakeholderReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CrmStakeholderReview>(getConfirmCrmStakeholderUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(confirmCrmStakeholderBody)
  }
);}





export const getConfirmCrmStakeholderMutationKey = () => ['confirmCrmStakeholder'] as const;

export const getConfirmCrmStakeholderMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmCrmStakeholder>>, TError,ConfirmCrmStakeholderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmCrmStakeholder>>, TError,ConfirmCrmStakeholderMutationVariables, TContext> => {

const mutationKey = getConfirmCrmStakeholderMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmCrmStakeholder>>, ConfirmCrmStakeholderMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  confirmCrmStakeholder(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmCrmStakeholderMutationResult = NonNullable<Awaited<ReturnType<typeof confirmCrmStakeholder>>>
    export type ConfirmCrmStakeholderMutationBody = ConfirmCrmStakeholderBody
    export type ConfirmCrmStakeholderMutationError = unknown
    export type ConfirmCrmStakeholderMutationVariables = {id: string;data: ConfirmCrmStakeholderBody}

    /**
 * @summary confirmCrmStakeholder
 */
export const useConfirmCrmStakeholder = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmCrmStakeholder>>, TError,ConfirmCrmStakeholderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmCrmStakeholder>>,
        TError,
        ConfirmCrmStakeholderMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmCrmStakeholderMutationOptions(options));
    }

export const getCancelCrmStakeholderReviewUrl = (id: string,) => {




  return `/api/crm-stakeholder-reviews/${id}/cancel`
}

/**
 * @summary cancelCrmStakeholderReview
 */
export const cancelCrmStakeholderReview = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<CrmStakeholderReview> => {

  return customFetch<CrmStakeholderReview>(getCancelCrmStakeholderReviewUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelCrmStakeholderReviewMutationKey = () => ['cancelCrmStakeholderReview'] as const;

export const getCancelCrmStakeholderReviewMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelCrmStakeholderReview>>, TError,CancelCrmStakeholderReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelCrmStakeholderReview>>, TError,CancelCrmStakeholderReviewMutationVariables, TContext> => {

const mutationKey = getCancelCrmStakeholderReviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelCrmStakeholderReview>>, CancelCrmStakeholderReviewMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  cancelCrmStakeholderReview(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelCrmStakeholderReviewMutationResult = NonNullable<Awaited<ReturnType<typeof cancelCrmStakeholderReview>>>

    export type CancelCrmStakeholderReviewMutationError = unknown
    export type CancelCrmStakeholderReviewMutationVariables = {id: string}

    /**
 * @summary cancelCrmStakeholderReview
 */
export const useCancelCrmStakeholderReview = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelCrmStakeholderReview>>, TError,CancelCrmStakeholderReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelCrmStakeholderReview>>,
        TError,
        CancelCrmStakeholderReviewMutationVariables,
        TContext
      > => {
      return useMutation(getCancelCrmStakeholderReviewMutationOptions(options));
    }

export const getListCrmReferralsUrl = (params?: ListCrmReferralsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/crm-referrals?${stringifiedParams}` : `/api/crm-referrals`
}

/**
 * @summary listCrmReferrals
 */
export const listCrmReferrals = async (params?: ListCrmReferralsParams, options?: Parameters<typeof customFetch>[1]): Promise<CrmReferralPage> => {

  return customFetch<CrmReferralPage>(getListCrmReferralsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCrmReferralsQueryKey = (params?: ListCrmReferralsParams,) => {
    return [
    `/api/crm-referrals`, ...(params ? [params] : [])
    ] as const;
    }


export const getListCrmReferralsQueryOptions = <TData = Awaited<ReturnType<typeof listCrmReferrals>>, TError = unknown>(params?: ListCrmReferralsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCrmReferrals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCrmReferralsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCrmReferrals>>> = ({ signal }) => listCrmReferrals(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCrmReferrals>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCrmReferralsQueryResult = NonNullable<Awaited<ReturnType<typeof listCrmReferrals>>>
export type ListCrmReferralsQueryError = unknown


/**
 * @summary listCrmReferrals
 */

export function useListCrmReferrals<TData = Awaited<ReturnType<typeof listCrmReferrals>>, TError = unknown>(
 params?: ListCrmReferralsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCrmReferrals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCrmReferralsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetCrmReferralUrl = (id: string,) => {




  return `/api/crm-referrals/${id}`
}

/**
 * @summary getCrmReferral
 */
export const getCrmReferral = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<CrmReferral> => {

  return customFetch<CrmReferral>(getGetCrmReferralUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetCrmReferralQueryKey = (id: string,) => {
    return [
    `/api/crm-referrals/${id}`
    ] as const;
    }


export const getGetCrmReferralQueryOptions = <TData = Awaited<ReturnType<typeof getCrmReferral>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCrmReferral>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCrmReferralQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCrmReferral>>> = ({ signal }) => getCrmReferral(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCrmReferral>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCrmReferralQueryResult = NonNullable<Awaited<ReturnType<typeof getCrmReferral>>>
export type GetCrmReferralQueryError = unknown


/**
 * @summary getCrmReferral
 */

export function useGetCrmReferral<TData = Awaited<ReturnType<typeof getCrmReferral>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCrmReferral>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCrmReferralQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListCrmReferralHistoryUrl = (id: string,
    params?: ListCrmReferralHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/crm-referrals/${id}/history?${stringifiedParams}` : `/api/crm-referrals/${id}/history`
}

/**
 * @summary listCrmReferralHistory
 */
export const listCrmReferralHistory = async (id: string,
    params?: ListCrmReferralHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<CrmReferralHistoryPage> => {

  return customFetch<CrmReferralHistoryPage>(getListCrmReferralHistoryUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCrmReferralHistoryQueryKey = (id: string,
    params?: ListCrmReferralHistoryParams,) => {
    return [
    `/api/crm-referrals/${id}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListCrmReferralHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listCrmReferralHistory>>, TError = unknown>(id: string,
    params?: ListCrmReferralHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCrmReferralHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCrmReferralHistoryQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCrmReferralHistory>>> = ({ signal }) => listCrmReferralHistory(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCrmReferralHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCrmReferralHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listCrmReferralHistory>>>
export type ListCrmReferralHistoryQueryError = unknown


/**
 * @summary listCrmReferralHistory
 */

export function useListCrmReferralHistory<TData = Awaited<ReturnType<typeof listCrmReferralHistory>>, TError = unknown>(
 id: string,
    params?: ListCrmReferralHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCrmReferralHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCrmReferralHistoryQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListCrmReferralChoicesUrl = (params: ListCrmReferralChoicesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/crm-referral-choices?${stringifiedParams}` : `/api/crm-referral-choices`
}

/**
 * @summary listCrmReferralChoices
 */
export const listCrmReferralChoices = async (params: ListCrmReferralChoicesParams, options?: Parameters<typeof customFetch>[1]): Promise<CrmReferralChoicePage> => {

  return customFetch<CrmReferralChoicePage>(getListCrmReferralChoicesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCrmReferralChoicesQueryKey = (params?: ListCrmReferralChoicesParams,) => {
    return [
    `/api/crm-referral-choices`, ...(params ? [params] : [])
    ] as const;
    }


export const getListCrmReferralChoicesQueryOptions = <TData = Awaited<ReturnType<typeof listCrmReferralChoices>>, TError = unknown>(params: ListCrmReferralChoicesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCrmReferralChoices>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCrmReferralChoicesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCrmReferralChoices>>> = ({ signal }) => listCrmReferralChoices(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCrmReferralChoices>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCrmReferralChoicesQueryResult = NonNullable<Awaited<ReturnType<typeof listCrmReferralChoices>>>
export type ListCrmReferralChoicesQueryError = unknown


/**
 * @summary listCrmReferralChoices
 */

export function useListCrmReferralChoices<TData = Awaited<ReturnType<typeof listCrmReferralChoices>>, TError = unknown>(
 params: ListCrmReferralChoicesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCrmReferralChoices>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCrmReferralChoicesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewCrmReferralUrl = () => {




  return `/api/crm-referral-reviews`
}

/**
 * @summary reviewCrmReferral
 */
export const reviewCrmReferral = async (reviewCrmReferralBody: ReviewCrmReferralBody, options?: Parameters<typeof customFetch>[1]): Promise<CrmReferralReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CrmReferralReview>(getReviewCrmReferralUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewCrmReferralBody)
  }
);}





export const getReviewCrmReferralMutationKey = () => ['reviewCrmReferral'] as const;

export const getReviewCrmReferralMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewCrmReferral>>, TError,ReviewCrmReferralMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewCrmReferral>>, TError,ReviewCrmReferralMutationVariables, TContext> => {

const mutationKey = getReviewCrmReferralMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewCrmReferral>>, ReviewCrmReferralMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reviewCrmReferral(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewCrmReferralMutationResult = NonNullable<Awaited<ReturnType<typeof reviewCrmReferral>>>
    export type ReviewCrmReferralMutationBody = ReviewCrmReferralBody
    export type ReviewCrmReferralMutationError = unknown
    export type ReviewCrmReferralMutationVariables = {data: ReviewCrmReferralBody}

    /**
 * @summary reviewCrmReferral
 */
export const useReviewCrmReferral = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewCrmReferral>>, TError,ReviewCrmReferralMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewCrmReferral>>,
        TError,
        ReviewCrmReferralMutationVariables,
        TContext
      > => {
      return useMutation(getReviewCrmReferralMutationOptions(options));
    }

export const getGetCrmReferralReviewUrl = (id: string,) => {




  return `/api/crm-referral-reviews/${id}`
}

/**
 * @summary getCrmReferralReview
 */
export const getCrmReferralReview = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<CrmReferralReview> => {

  return customFetch<CrmReferralReview>(getGetCrmReferralReviewUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetCrmReferralReviewQueryKey = (id: string,) => {
    return [
    `/api/crm-referral-reviews/${id}`
    ] as const;
    }


export const getGetCrmReferralReviewQueryOptions = <TData = Awaited<ReturnType<typeof getCrmReferralReview>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCrmReferralReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCrmReferralReviewQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCrmReferralReview>>> = ({ signal }) => getCrmReferralReview(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCrmReferralReview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCrmReferralReviewQueryResult = NonNullable<Awaited<ReturnType<typeof getCrmReferralReview>>>
export type GetCrmReferralReviewQueryError = unknown


/**
 * @summary getCrmReferralReview
 */

export function useGetCrmReferralReview<TData = Awaited<ReturnType<typeof getCrmReferralReview>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCrmReferralReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCrmReferralReviewQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConfirmCrmReferralUrl = (id: string,) => {




  return `/api/crm-referral-reviews/${id}/confirm`
}

/**
 * @summary confirmCrmReferral
 */
export const confirmCrmReferral = async (id: string,
    confirmCrmReferralBody: ConfirmCrmReferralBody, options?: Parameters<typeof customFetch>[1]): Promise<CrmReferralReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CrmReferralReview>(getConfirmCrmReferralUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(confirmCrmReferralBody)
  }
);}





export const getConfirmCrmReferralMutationKey = () => ['confirmCrmReferral'] as const;

export const getConfirmCrmReferralMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmCrmReferral>>, TError,ConfirmCrmReferralMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmCrmReferral>>, TError,ConfirmCrmReferralMutationVariables, TContext> => {

const mutationKey = getConfirmCrmReferralMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmCrmReferral>>, ConfirmCrmReferralMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  confirmCrmReferral(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmCrmReferralMutationResult = NonNullable<Awaited<ReturnType<typeof confirmCrmReferral>>>
    export type ConfirmCrmReferralMutationBody = ConfirmCrmReferralBody
    export type ConfirmCrmReferralMutationError = unknown
    export type ConfirmCrmReferralMutationVariables = {id: string;data: ConfirmCrmReferralBody}

    /**
 * @summary confirmCrmReferral
 */
export const useConfirmCrmReferral = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmCrmReferral>>, TError,ConfirmCrmReferralMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmCrmReferral>>,
        TError,
        ConfirmCrmReferralMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmCrmReferralMutationOptions(options));
    }

export const getCancelCrmReferralReviewUrl = (id: string,) => {




  return `/api/crm-referral-reviews/${id}/cancel`
}

/**
 * @summary cancelCrmReferralReview
 */
export const cancelCrmReferralReview = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<CrmReferralReview> => {

  return customFetch<CrmReferralReview>(getCancelCrmReferralReviewUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelCrmReferralReviewMutationKey = () => ['cancelCrmReferralReview'] as const;

export const getCancelCrmReferralReviewMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelCrmReferralReview>>, TError,CancelCrmReferralReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelCrmReferralReview>>, TError,CancelCrmReferralReviewMutationVariables, TContext> => {

const mutationKey = getCancelCrmReferralReviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelCrmReferralReview>>, CancelCrmReferralReviewMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  cancelCrmReferralReview(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelCrmReferralReviewMutationResult = NonNullable<Awaited<ReturnType<typeof cancelCrmReferralReview>>>

    export type CancelCrmReferralReviewMutationError = unknown
    export type CancelCrmReferralReviewMutationVariables = {id: string}

    /**
 * @summary cancelCrmReferralReview
 */
export const useCancelCrmReferralReview = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelCrmReferralReview>>, TError,CancelCrmReferralReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelCrmReferralReview>>,
        TError,
        CancelCrmReferralReviewMutationVariables,
        TContext
      > => {
      return useMutation(getCancelCrmReferralReviewMutationOptions(options));
    }

export const getListNativeWordProposalsUrl = (entryId: string,
    params?: ListNativeWordProposalsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/sharepoint-word/snapshots/${entryId}/proposals?${stringifiedParams}` : `/api/sharepoint-word/snapshots/${entryId}/proposals`
}

export const listNativeWordProposals = async (entryId: string,
    params?: ListNativeWordProposalsParams, options?: Parameters<typeof customFetch>[1]): Promise<NativeWordProposalPage> => {

  return customFetch<NativeWordProposalPage>(getListNativeWordProposalsUrl(entryId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListNativeWordProposalsQueryKey = (entryId: string,
    params?: ListNativeWordProposalsParams,) => {
    return [
    `/api/sharepoint-word/snapshots/${entryId}/proposals`, ...(params ? [params] : [])
    ] as const;
    }


export const getListNativeWordProposalsQueryOptions = <TData = Awaited<ReturnType<typeof listNativeWordProposals>>, TError = unknown>(entryId: string,
    params?: ListNativeWordProposalsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listNativeWordProposals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListNativeWordProposalsQueryKey(entryId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listNativeWordProposals>>> = ({ signal }) => listNativeWordProposals(entryId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entryId !== null && entryId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listNativeWordProposals>>, TError, TData> & { queryKey: QueryKey }
}

export type ListNativeWordProposalsQueryResult = NonNullable<Awaited<ReturnType<typeof listNativeWordProposals>>>
export type ListNativeWordProposalsQueryError = unknown



export function useListNativeWordProposals<TData = Awaited<ReturnType<typeof listNativeWordProposals>>, TError = unknown>(
 entryId: string,
    params?: ListNativeWordProposalsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listNativeWordProposals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListNativeWordProposalsQueryOptions(entryId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateNativeWordProposalUrl = (entryId: string,) => {




  return `/api/sharepoint-word/snapshots/${entryId}/proposals`
}

export const createNativeWordProposal = async (entryId: string,
    nativeWordProposalInput: NativeWordProposalInput, options?: Parameters<typeof customFetch>[1]): Promise<NativeWordProposal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<NativeWordProposal>(getCreateNativeWordProposalUrl(entryId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(nativeWordProposalInput)
  }
);}





export const getCreateNativeWordProposalMutationKey = () => ['createNativeWordProposal'] as const;

export const getCreateNativeWordProposalMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createNativeWordProposal>>, TError,CreateNativeWordProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createNativeWordProposal>>, TError,CreateNativeWordProposalMutationVariables, TContext> => {

const mutationKey = getCreateNativeWordProposalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createNativeWordProposal>>, CreateNativeWordProposalMutationVariables> = (props) => {
          const {entryId,data} = props ?? {};

          return  createNativeWordProposal(entryId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateNativeWordProposalMutationResult = NonNullable<Awaited<ReturnType<typeof createNativeWordProposal>>>
    export type CreateNativeWordProposalMutationBody = NativeWordProposalInput
    export type CreateNativeWordProposalMutationError = unknown
    export type CreateNativeWordProposalMutationVariables = {entryId: string;data: NativeWordProposalInput}

    export const useCreateNativeWordProposal = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createNativeWordProposal>>, TError,CreateNativeWordProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createNativeWordProposal>>,
        TError,
        CreateNativeWordProposalMutationVariables,
        TContext
      > => {
      return useMutation(getCreateNativeWordProposalMutationOptions(options));
    }

export const getGetNativeWordProposalUrl = (entryId: string,
    proposalId: string,) => {




  return `/api/sharepoint-word/snapshots/${entryId}/proposals/${proposalId}`
}

export const getNativeWordProposal = async (entryId: string,
    proposalId: string, options?: Parameters<typeof customFetch>[1]): Promise<NativeWordProposal> => {

  return customFetch<NativeWordProposal>(getGetNativeWordProposalUrl(entryId,proposalId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetNativeWordProposalQueryKey = (entryId: string,
    proposalId: string,) => {
    return [
    `/api/sharepoint-word/snapshots/${entryId}/proposals/${proposalId}`
    ] as const;
    }


export const getGetNativeWordProposalQueryOptions = <TData = Awaited<ReturnType<typeof getNativeWordProposal>>, TError = unknown>(entryId: string,
    proposalId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getNativeWordProposal>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetNativeWordProposalQueryKey(entryId,proposalId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getNativeWordProposal>>> = ({ signal }) => getNativeWordProposal(entryId,proposalId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entryId !== null && entryId !== undefined && proposalId !== null && proposalId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getNativeWordProposal>>, TError, TData> & { queryKey: QueryKey }
}

export type GetNativeWordProposalQueryResult = NonNullable<Awaited<ReturnType<typeof getNativeWordProposal>>>
export type GetNativeWordProposalQueryError = unknown



export function useGetNativeWordProposal<TData = Awaited<ReturnType<typeof getNativeWordProposal>>, TError = unknown>(
 entryId: string,
    proposalId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getNativeWordProposal>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetNativeWordProposalQueryOptions(entryId,proposalId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDecideNativeWordProposalUrl = (entryId: string,
    proposalId: string,) => {




  return `/api/sharepoint-word/snapshots/${entryId}/proposals/${proposalId}/decision`
}

export const decideNativeWordProposal = async (entryId: string,
    proposalId: string,
    nativeWordProposalDecision: NativeWordProposalDecision, options?: Parameters<typeof customFetch>[1]): Promise<NativeWordProposal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<NativeWordProposal>(getDecideNativeWordProposalUrl(entryId,proposalId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(nativeWordProposalDecision)
  }
);}





export const getDecideNativeWordProposalMutationKey = () => ['decideNativeWordProposal'] as const;

export const getDecideNativeWordProposalMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideNativeWordProposal>>, TError,DecideNativeWordProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof decideNativeWordProposal>>, TError,DecideNativeWordProposalMutationVariables, TContext> => {

const mutationKey = getDecideNativeWordProposalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof decideNativeWordProposal>>, DecideNativeWordProposalMutationVariables> = (props) => {
          const {entryId,proposalId,data} = props ?? {};

          return  decideNativeWordProposal(entryId,proposalId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DecideNativeWordProposalMutationResult = NonNullable<Awaited<ReturnType<typeof decideNativeWordProposal>>>
    export type DecideNativeWordProposalMutationBody = NativeWordProposalDecision
    export type DecideNativeWordProposalMutationError = unknown
    export type DecideNativeWordProposalMutationVariables = {entryId: string;proposalId: string;data: NativeWordProposalDecision}

    export const useDecideNativeWordProposal = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideNativeWordProposal>>, TError,DecideNativeWordProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof decideNativeWordProposal>>,
        TError,
        DecideNativeWordProposalMutationVariables,
        TContext
      > => {
      return useMutation(getDecideNativeWordProposalMutationOptions(options));
    }

export const getInspectSharePointWordSnapshotUrl = (entryId: string,
    params?: InspectSharePointWordSnapshotParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/sharepoint-word/snapshots/${entryId}/inspection?${stringifiedParams}` : `/api/sharepoint-word/snapshots/${entryId}/inspection`
}

export const inspectSharePointWordSnapshot = async (entryId: string,
    params?: InspectSharePointWordSnapshotParams, options?: Parameters<typeof customFetch>[1]): Promise<NativeWordInspection> => {

  return customFetch<NativeWordInspection>(getInspectSharePointWordSnapshotUrl(entryId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInspectSharePointWordSnapshotQueryKey = (entryId: string,
    params?: InspectSharePointWordSnapshotParams,) => {
    return [
    `/api/sharepoint-word/snapshots/${entryId}/inspection`, ...(params ? [params] : [])
    ] as const;
    }


export const getInspectSharePointWordSnapshotQueryOptions = <TData = Awaited<ReturnType<typeof inspectSharePointWordSnapshot>>, TError = Error>(entryId: string,
    params?: InspectSharePointWordSnapshotParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof inspectSharePointWordSnapshot>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInspectSharePointWordSnapshotQueryKey(entryId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof inspectSharePointWordSnapshot>>> = ({ signal }) => inspectSharePointWordSnapshot(entryId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entryId !== null && entryId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof inspectSharePointWordSnapshot>>, TError, TData> & { queryKey: QueryKey }
}

export type InspectSharePointWordSnapshotQueryResult = NonNullable<Awaited<ReturnType<typeof inspectSharePointWordSnapshot>>>
export type InspectSharePointWordSnapshotQueryError = Error



export function useInspectSharePointWordSnapshot<TData = Awaited<ReturnType<typeof inspectSharePointWordSnapshot>>, TError = Error>(
 entryId: string,
    params?: InspectSharePointWordSnapshotParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof inspectSharePointWordSnapshot>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getInspectSharePointWordSnapshotQueryOptions(entryId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPreviewSharePointWordCandidateUrl = (entryId: string,) => {




  return `/api/sharepoint-word/snapshots/${entryId}/candidate-preview`
}

export const previewSharePointWordCandidate = async (entryId: string,
    nativeWordPatch: NativeWordPatch, options?: Parameters<typeof customFetch>[1]): Promise<NativeWordCandidate> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<NativeWordCandidate>(getPreviewSharePointWordCandidateUrl(entryId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(nativeWordPatch)
  }
);}





export const getPreviewSharePointWordCandidateMutationKey = () => ['previewSharePointWordCandidate'] as const;

export const getPreviewSharePointWordCandidateMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewSharePointWordCandidate>>, TError,PreviewSharePointWordCandidateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewSharePointWordCandidate>>, TError,PreviewSharePointWordCandidateMutationVariables, TContext> => {

const mutationKey = getPreviewSharePointWordCandidateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewSharePointWordCandidate>>, PreviewSharePointWordCandidateMutationVariables> = (props) => {
          const {entryId,data} = props ?? {};

          return  previewSharePointWordCandidate(entryId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewSharePointWordCandidateMutationResult = NonNullable<Awaited<ReturnType<typeof previewSharePointWordCandidate>>>
    export type PreviewSharePointWordCandidateMutationBody = NativeWordPatch
    export type PreviewSharePointWordCandidateMutationError = Error
    export type PreviewSharePointWordCandidateMutationVariables = {entryId: string;data: NativeWordPatch}

    export const usePreviewSharePointWordCandidate = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewSharePointWordCandidate>>, TError,PreviewSharePointWordCandidateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewSharePointWordCandidate>>,
        TError,
        PreviewSharePointWordCandidateMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewSharePointWordCandidateMutationOptions(options));
    }

export const getListNativeWordSavesUrl = (entryId: string,
    params?: ListNativeWordSavesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/sharepoint-word/snapshots/${entryId}/saves?${stringifiedParams}` : `/api/sharepoint-word/snapshots/${entryId}/saves`
}

export const listNativeWordSaves = async (entryId: string,
    params?: ListNativeWordSavesParams, options?: Parameters<typeof customFetch>[1]): Promise<NativeWordSavePage> => {

  return customFetch<NativeWordSavePage>(getListNativeWordSavesUrl(entryId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListNativeWordSavesQueryKey = (entryId: string,
    params?: ListNativeWordSavesParams,) => {
    return [
    `/api/sharepoint-word/snapshots/${entryId}/saves`, ...(params ? [params] : [])
    ] as const;
    }


export const getListNativeWordSavesQueryOptions = <TData = Awaited<ReturnType<typeof listNativeWordSaves>>, TError = ErrorResponse>(entryId: string,
    params?: ListNativeWordSavesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listNativeWordSaves>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListNativeWordSavesQueryKey(entryId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listNativeWordSaves>>> = ({ signal }) => listNativeWordSaves(entryId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entryId !== null && entryId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listNativeWordSaves>>, TError, TData> & { queryKey: QueryKey }
}

export type ListNativeWordSavesQueryResult = NonNullable<Awaited<ReturnType<typeof listNativeWordSaves>>>
export type ListNativeWordSavesQueryError = ErrorResponse



export function useListNativeWordSaves<TData = Awaited<ReturnType<typeof listNativeWordSaves>>, TError = ErrorResponse>(
 entryId: string,
    params?: ListNativeWordSavesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listNativeWordSaves>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListNativeWordSavesQueryOptions(entryId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSaveNativeWordOriginalUrl = (entryId: string,) => {




  return `/api/sharepoint-word/snapshots/${entryId}/saves`
}

export const saveNativeWordOriginal = async (entryId: string,
    nativeWordSaveInput: NativeWordSaveInput, options?: Parameters<typeof customFetch>[1]): Promise<NativeWordSave> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<NativeWordSave>(getSaveNativeWordOriginalUrl(entryId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(nativeWordSaveInput)
  }
);}





export const getSaveNativeWordOriginalMutationKey = () => ['saveNativeWordOriginal'] as const;

export const getSaveNativeWordOriginalMutationOptions = <TError = ErrorResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveNativeWordOriginal>>, TError,SaveNativeWordOriginalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof saveNativeWordOriginal>>, TError,SaveNativeWordOriginalMutationVariables, TContext> => {

const mutationKey = getSaveNativeWordOriginalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof saveNativeWordOriginal>>, SaveNativeWordOriginalMutationVariables> = (props) => {
          const {entryId,data} = props ?? {};

          return  saveNativeWordOriginal(entryId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveNativeWordOriginalMutationResult = NonNullable<Awaited<ReturnType<typeof saveNativeWordOriginal>>>
    export type SaveNativeWordOriginalMutationBody = NativeWordSaveInput
    export type SaveNativeWordOriginalMutationError = ErrorResponse
    export type SaveNativeWordOriginalMutationVariables = {entryId: string;data: NativeWordSaveInput}

    export const useSaveNativeWordOriginal = <TError = ErrorResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveNativeWordOriginal>>, TError,SaveNativeWordOriginalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof saveNativeWordOriginal>>,
        TError,
        SaveNativeWordOriginalMutationVariables,
        TContext
      > => {
      return useMutation(getSaveNativeWordOriginalMutationOptions(options));
    }

export const getGetNativeWordSaveUrl = (entryId: string,
    saveId: string,) => {




  return `/api/sharepoint-word/snapshots/${entryId}/saves/${saveId}`
}

export const getNativeWordSave = async (entryId: string,
    saveId: string, options?: Parameters<typeof customFetch>[1]): Promise<NativeWordSave> => {

  return customFetch<NativeWordSave>(getGetNativeWordSaveUrl(entryId,saveId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetNativeWordSaveQueryKey = (entryId: string,
    saveId: string,) => {
    return [
    `/api/sharepoint-word/snapshots/${entryId}/saves/${saveId}`
    ] as const;
    }


export const getGetNativeWordSaveQueryOptions = <TData = Awaited<ReturnType<typeof getNativeWordSave>>, TError = ErrorResponse>(entryId: string,
    saveId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getNativeWordSave>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetNativeWordSaveQueryKey(entryId,saveId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getNativeWordSave>>> = ({ signal }) => getNativeWordSave(entryId,saveId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entryId !== null && entryId !== undefined && saveId !== null && saveId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getNativeWordSave>>, TError, TData> & { queryKey: QueryKey }
}

export type GetNativeWordSaveQueryResult = NonNullable<Awaited<ReturnType<typeof getNativeWordSave>>>
export type GetNativeWordSaveQueryError = ErrorResponse



export function useGetNativeWordSave<TData = Awaited<ReturnType<typeof getNativeWordSave>>, TError = ErrorResponse>(
 entryId: string,
    saveId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getNativeWordSave>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetNativeWordSaveQueryOptions(entryId,saveId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReconcileNativeWordSaveUrl = (entryId: string,
    saveId: string,) => {




  return `/api/sharepoint-word/snapshots/${entryId}/saves/${saveId}/reconcile`
}

/**
 * Read-only provider reconciliation. Never resends content.
 */
export const reconcileNativeWordSave = async (entryId: string,
    saveId: string, options?: Parameters<typeof customFetch>[1]): Promise<NativeWordSave> => {

  return customFetch<NativeWordSave>(getReconcileNativeWordSaveUrl(entryId,saveId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getReconcileNativeWordSaveMutationKey = () => ['reconcileNativeWordSave'] as const;

export const getReconcileNativeWordSaveMutationOptions = <TError = ErrorResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconcileNativeWordSave>>, TError,ReconcileNativeWordSaveMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reconcileNativeWordSave>>, TError,ReconcileNativeWordSaveMutationVariables, TContext> => {

const mutationKey = getReconcileNativeWordSaveMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconcileNativeWordSave>>, ReconcileNativeWordSaveMutationVariables> = (props) => {
          const {entryId,saveId} = props ?? {};

          return  reconcileNativeWordSave(entryId,saveId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconcileNativeWordSaveMutationResult = NonNullable<Awaited<ReturnType<typeof reconcileNativeWordSave>>>

    export type ReconcileNativeWordSaveMutationError = ErrorResponse
    export type ReconcileNativeWordSaveMutationVariables = {entryId: string;saveId: string}

    export const useReconcileNativeWordSave = <TError = ErrorResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconcileNativeWordSave>>, TError,ReconcileNativeWordSaveMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reconcileNativeWordSave>>,
        TError,
        ReconcileNativeWordSaveMutationVariables,
        TContext
      > => {
      return useMutation(getReconcileNativeWordSaveMutationOptions(options));
    }

export const getListSharePointWordSourcesUrl = (params?: ListSharePointWordSourcesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/sharepoint-word/sources?${stringifiedParams}` : `/api/sharepoint-word/sources`
}

export const listSharePointWordSources = async (params?: ListSharePointWordSourcesParams, options?: Parameters<typeof customFetch>[1]): Promise<ListSharePointWordSources200> => {

  return customFetch<ListSharePointWordSources200>(getListSharePointWordSourcesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSharePointWordSourcesQueryKey = (params?: ListSharePointWordSourcesParams,) => {
    return [
    `/api/sharepoint-word/sources`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSharePointWordSourcesQueryOptions = <TData = Awaited<ReturnType<typeof listSharePointWordSources>>, TError = unknown>(params?: ListSharePointWordSourcesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSharePointWordSources>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSharePointWordSourcesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSharePointWordSources>>> = ({ signal }) => listSharePointWordSources(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSharePointWordSources>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSharePointWordSourcesQueryResult = NonNullable<Awaited<ReturnType<typeof listSharePointWordSources>>>
export type ListSharePointWordSourcesQueryError = unknown



export function useListSharePointWordSources<TData = Awaited<ReturnType<typeof listSharePointWordSources>>, TError = unknown>(
 params?: ListSharePointWordSourcesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSharePointWordSources>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSharePointWordSourcesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getBrowseSharePointWordSourcesUrl = () => {




  return `/api/sharepoint-word/browse`
}

/**
 * @summary Actor-authorized native Word browse
 */
export const browseSharePointWordSources = async (sharePointWordBrowseInput: SharePointWordBrowseInput, options?: Parameters<typeof customFetch>[1]): Promise<SharePointWordPage> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SharePointWordPage>(getBrowseSharePointWordSourcesUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sharePointWordBrowseInput)
  }
);}





export const getBrowseSharePointWordSourcesMutationKey = () => ['browseSharePointWordSources'] as const;

export const getBrowseSharePointWordSourcesMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof browseSharePointWordSources>>, TError,BrowseSharePointWordSourcesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof browseSharePointWordSources>>, TError,BrowseSharePointWordSourcesMutationVariables, TContext> => {

const mutationKey = getBrowseSharePointWordSourcesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof browseSharePointWordSources>>, BrowseSharePointWordSourcesMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  browseSharePointWordSources(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type BrowseSharePointWordSourcesMutationResult = NonNullable<Awaited<ReturnType<typeof browseSharePointWordSources>>>
    export type BrowseSharePointWordSourcesMutationBody = SharePointWordBrowseInput
    export type BrowseSharePointWordSourcesMutationError = Error
    export type BrowseSharePointWordSourcesMutationVariables = {data: SharePointWordBrowseInput}

    /**
 * @summary Actor-authorized native Word browse
 */
export const useBrowseSharePointWordSources = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof browseSharePointWordSources>>, TError,BrowseSharePointWordSourcesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof browseSharePointWordSources>>,
        TError,
        BrowseSharePointWordSourcesMutationVariables,
        TContext
      > => {
      return useMutation(getBrowseSharePointWordSourcesMutationOptions(options));
    }

export const getResolveSharePointWordSourceUrl = () => {




  return `/api/sharepoint-word/resolve`
}

/**
 * @summary Actor-authorized native Word resolve
 */
export const resolveSharePointWordSource = async (sharePointWordResolveInput: SharePointWordResolveInput, options?: Parameters<typeof customFetch>[1]): Promise<SharePointWordItem> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SharePointWordItem>(getResolveSharePointWordSourceUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sharePointWordResolveInput)
  }
);}





export const getResolveSharePointWordSourceMutationKey = () => ['resolveSharePointWordSource'] as const;

export const getResolveSharePointWordSourceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resolveSharePointWordSource>>, TError,ResolveSharePointWordSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof resolveSharePointWordSource>>, TError,ResolveSharePointWordSourceMutationVariables, TContext> => {

const mutationKey = getResolveSharePointWordSourceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof resolveSharePointWordSource>>, ResolveSharePointWordSourceMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  resolveSharePointWordSource(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResolveSharePointWordSourceMutationResult = NonNullable<Awaited<ReturnType<typeof resolveSharePointWordSource>>>
    export type ResolveSharePointWordSourceMutationBody = SharePointWordResolveInput
    export type ResolveSharePointWordSourceMutationError = Error
    export type ResolveSharePointWordSourceMutationVariables = {data: SharePointWordResolveInput}

    /**
 * @summary Actor-authorized native Word resolve
 */
export const useResolveSharePointWordSource = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resolveSharePointWordSource>>, TError,ResolveSharePointWordSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof resolveSharePointWordSource>>,
        TError,
        ResolveSharePointWordSourceMutationVariables,
        TContext
      > => {
      return useMutation(getResolveSharePointWordSourceMutationOptions(options));
    }

export const getCaptureSharePointWordSnapshotUrl = () => {




  return `/api/sharepoint-word/snapshots`
}

/**
 * @summary Actor-authorized native Word snapshots
 */
export const captureSharePointWordSnapshot = async (sharePointWordSnapshotInput: SharePointWordSnapshotInput, options?: Parameters<typeof customFetch>[1]): Promise<SharePointWordSnapshot> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SharePointWordSnapshot>(getCaptureSharePointWordSnapshotUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sharePointWordSnapshotInput)
  }
);}





export const getCaptureSharePointWordSnapshotMutationKey = () => ['captureSharePointWordSnapshot'] as const;

export const getCaptureSharePointWordSnapshotMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof captureSharePointWordSnapshot>>, TError,CaptureSharePointWordSnapshotMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof captureSharePointWordSnapshot>>, TError,CaptureSharePointWordSnapshotMutationVariables, TContext> => {

const mutationKey = getCaptureSharePointWordSnapshotMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof captureSharePointWordSnapshot>>, CaptureSharePointWordSnapshotMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  captureSharePointWordSnapshot(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CaptureSharePointWordSnapshotMutationResult = NonNullable<Awaited<ReturnType<typeof captureSharePointWordSnapshot>>>
    export type CaptureSharePointWordSnapshotMutationBody = SharePointWordSnapshotInput
    export type CaptureSharePointWordSnapshotMutationError = Error
    export type CaptureSharePointWordSnapshotMutationVariables = {data: SharePointWordSnapshotInput}

    /**
 * @summary Actor-authorized native Word snapshots
 */
export const useCaptureSharePointWordSnapshot = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof captureSharePointWordSnapshot>>, TError,CaptureSharePointWordSnapshotMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof captureSharePointWordSnapshot>>,
        TError,
        CaptureSharePointWordSnapshotMutationVariables,
        TContext
      > => {
      return useMutation(getCaptureSharePointWordSnapshotMutationOptions(options));
    }

export const getGetSharePointWordSnapshotUrl = (entryId: string,) => {




  return `/api/sharepoint-word/snapshots/${entryId}`
}

export const getSharePointWordSnapshot = async (entryId: string, options?: Parameters<typeof customFetch>[1]): Promise<SharePointWordSnapshot> => {

  return customFetch<SharePointWordSnapshot>(getGetSharePointWordSnapshotUrl(entryId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSharePointWordSnapshotQueryKey = (entryId: string,) => {
    return [
    `/api/sharepoint-word/snapshots/${entryId}`
    ] as const;
    }


export const getGetSharePointWordSnapshotQueryOptions = <TData = Awaited<ReturnType<typeof getSharePointWordSnapshot>>, TError = unknown>(entryId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSharePointWordSnapshot>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSharePointWordSnapshotQueryKey(entryId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSharePointWordSnapshot>>> = ({ signal }) => getSharePointWordSnapshot(entryId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entryId !== null && entryId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSharePointWordSnapshot>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSharePointWordSnapshotQueryResult = NonNullable<Awaited<ReturnType<typeof getSharePointWordSnapshot>>>
export type GetSharePointWordSnapshotQueryError = unknown



export function useGetSharePointWordSnapshot<TData = Awaited<ReturnType<typeof getSharePointWordSnapshot>>, TError = unknown>(
 entryId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSharePointWordSnapshot>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSharePointWordSnapshotQueryOptions(entryId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetGovernmentTenantOverviewUrl = (params?: GetGovernmentTenantOverviewParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/government/tenant-overview?${stringifiedParams}` : `/api/web-signals/government/tenant-overview`
}

export const getGovernmentTenantOverview = async (params?: GetGovernmentTenantOverviewParams, options?: Parameters<typeof customFetch>[1]): Promise<GovernmentTenantOverview> => {

  return customFetch<GovernmentTenantOverview>(getGetGovernmentTenantOverviewUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetGovernmentTenantOverviewQueryKey = (params?: GetGovernmentTenantOverviewParams,) => {
    return [
    `/api/web-signals/government/tenant-overview`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetGovernmentTenantOverviewQueryOptions = <TData = Awaited<ReturnType<typeof getGovernmentTenantOverview>>, TError = void>(params?: GetGovernmentTenantOverviewParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGovernmentTenantOverview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetGovernmentTenantOverviewQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getGovernmentTenantOverview>>> = ({ signal }) => getGovernmentTenantOverview(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getGovernmentTenantOverview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetGovernmentTenantOverviewQueryResult = NonNullable<Awaited<ReturnType<typeof getGovernmentTenantOverview>>>
export type GetGovernmentTenantOverviewQueryError = void



export function useGetGovernmentTenantOverview<TData = Awaited<ReturnType<typeof getGovernmentTenantOverview>>, TError = void>(
 params?: GetGovernmentTenantOverviewParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGovernmentTenantOverview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetGovernmentTenantOverviewQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListGovernmentPursuitsUrl = (params?: ListGovernmentPursuitsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/government/pursuits?${stringifiedParams}` : `/api/web-signals/government/pursuits`
}

export const listGovernmentPursuits = async (params?: ListGovernmentPursuitsParams, options?: Parameters<typeof customFetch>[1]): Promise<GovernmentPursuitList> => {

  return customFetch<GovernmentPursuitList>(getListGovernmentPursuitsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListGovernmentPursuitsQueryKey = (params?: ListGovernmentPursuitsParams,) => {
    return [
    `/api/web-signals/government/pursuits`, ...(params ? [params] : [])
    ] as const;
    }


export const getListGovernmentPursuitsQueryOptions = <TData = Awaited<ReturnType<typeof listGovernmentPursuits>>, TError = void>(params?: ListGovernmentPursuitsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listGovernmentPursuits>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListGovernmentPursuitsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listGovernmentPursuits>>> = ({ signal }) => listGovernmentPursuits(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listGovernmentPursuits>>, TError, TData> & { queryKey: QueryKey }
}

export type ListGovernmentPursuitsQueryResult = NonNullable<Awaited<ReturnType<typeof listGovernmentPursuits>>>
export type ListGovernmentPursuitsQueryError = void



export function useListGovernmentPursuits<TData = Awaited<ReturnType<typeof listGovernmentPursuits>>, TError = void>(
 params?: ListGovernmentPursuitsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listGovernmentPursuits>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListGovernmentPursuitsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateGovernmentPursuitUrl = () => {




  return `/api/web-signals/government/pursuits`
}

export const createGovernmentPursuit = async (governmentPursuitCreate: GovernmentPursuitCreate, options?: Parameters<typeof customFetch>[1]): Promise<GovernmentPursuit> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<GovernmentPursuit>(getCreateGovernmentPursuitUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(governmentPursuitCreate)
  }
);}





export const getCreateGovernmentPursuitMutationKey = () => ['createGovernmentPursuit'] as const;

export const getCreateGovernmentPursuitMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createGovernmentPursuit>>, TError,CreateGovernmentPursuitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createGovernmentPursuit>>, TError,CreateGovernmentPursuitMutationVariables, TContext> => {

const mutationKey = getCreateGovernmentPursuitMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createGovernmentPursuit>>, CreateGovernmentPursuitMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createGovernmentPursuit(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateGovernmentPursuitMutationResult = NonNullable<Awaited<ReturnType<typeof createGovernmentPursuit>>>
    export type CreateGovernmentPursuitMutationBody = GovernmentPursuitCreate
    export type CreateGovernmentPursuitMutationError = void
    export type CreateGovernmentPursuitMutationVariables = {data: GovernmentPursuitCreate}

    export const useCreateGovernmentPursuit = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createGovernmentPursuit>>, TError,CreateGovernmentPursuitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createGovernmentPursuit>>,
        TError,
        CreateGovernmentPursuitMutationVariables,
        TContext
      > => {
      return useMutation(getCreateGovernmentPursuitMutationOptions(options));
    }

export const getListGovernmentPursuitOwnersUrl = (params?: ListGovernmentPursuitOwnersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/government/pursuit-owners?${stringifiedParams}` : `/api/web-signals/government/pursuit-owners`
}

export const listGovernmentPursuitOwners = async (params?: ListGovernmentPursuitOwnersParams, options?: Parameters<typeof customFetch>[1]): Promise<GovernmentPursuitOwnerList> => {

  return customFetch<GovernmentPursuitOwnerList>(getListGovernmentPursuitOwnersUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListGovernmentPursuitOwnersQueryKey = (params?: ListGovernmentPursuitOwnersParams,) => {
    return [
    `/api/web-signals/government/pursuit-owners`, ...(params ? [params] : [])
    ] as const;
    }


export const getListGovernmentPursuitOwnersQueryOptions = <TData = Awaited<ReturnType<typeof listGovernmentPursuitOwners>>, TError = void>(params?: ListGovernmentPursuitOwnersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listGovernmentPursuitOwners>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListGovernmentPursuitOwnersQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listGovernmentPursuitOwners>>> = ({ signal }) => listGovernmentPursuitOwners(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listGovernmentPursuitOwners>>, TError, TData> & { queryKey: QueryKey }
}

export type ListGovernmentPursuitOwnersQueryResult = NonNullable<Awaited<ReturnType<typeof listGovernmentPursuitOwners>>>
export type ListGovernmentPursuitOwnersQueryError = void



export function useListGovernmentPursuitOwners<TData = Awaited<ReturnType<typeof listGovernmentPursuitOwners>>, TError = void>(
 params?: ListGovernmentPursuitOwnersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listGovernmentPursuitOwners>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListGovernmentPursuitOwnersQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetGovernmentPursuitUrl = (id: string,) => {




  return `/api/web-signals/government/pursuits/${id}`
}

export const getGovernmentPursuit = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<GovernmentPursuit> => {

  return customFetch<GovernmentPursuit>(getGetGovernmentPursuitUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetGovernmentPursuitQueryKey = (id: string,) => {
    return [
    `/api/web-signals/government/pursuits/${id}`
    ] as const;
    }


export const getGetGovernmentPursuitQueryOptions = <TData = Awaited<ReturnType<typeof getGovernmentPursuit>>, TError = void>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGovernmentPursuit>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetGovernmentPursuitQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getGovernmentPursuit>>> = ({ signal }) => getGovernmentPursuit(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getGovernmentPursuit>>, TError, TData> & { queryKey: QueryKey }
}

export type GetGovernmentPursuitQueryResult = NonNullable<Awaited<ReturnType<typeof getGovernmentPursuit>>>
export type GetGovernmentPursuitQueryError = void



export function useGetGovernmentPursuit<TData = Awaited<ReturnType<typeof getGovernmentPursuit>>, TError = void>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGovernmentPursuit>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetGovernmentPursuitQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateGovernmentPursuitUrl = (id: string,) => {




  return `/api/web-signals/government/pursuits/${id}`
}

export const updateGovernmentPursuit = async (id: string,
    governmentPursuitUpdate: GovernmentPursuitUpdate, options?: Parameters<typeof customFetch>[1]): Promise<GovernmentPursuit> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<GovernmentPursuit>(getUpdateGovernmentPursuitUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(governmentPursuitUpdate)
  }
);}





export const getUpdateGovernmentPursuitMutationKey = () => ['updateGovernmentPursuit'] as const;

export const getUpdateGovernmentPursuitMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateGovernmentPursuit>>, TError,UpdateGovernmentPursuitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateGovernmentPursuit>>, TError,UpdateGovernmentPursuitMutationVariables, TContext> => {

const mutationKey = getUpdateGovernmentPursuitMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateGovernmentPursuit>>, UpdateGovernmentPursuitMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateGovernmentPursuit(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateGovernmentPursuitMutationResult = NonNullable<Awaited<ReturnType<typeof updateGovernmentPursuit>>>
    export type UpdateGovernmentPursuitMutationBody = GovernmentPursuitUpdate
    export type UpdateGovernmentPursuitMutationError = void
    export type UpdateGovernmentPursuitMutationVariables = {id: string;data: GovernmentPursuitUpdate}

    export const useUpdateGovernmentPursuit = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateGovernmentPursuit>>, TError,UpdateGovernmentPursuitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateGovernmentPursuit>>,
        TError,
        UpdateGovernmentPursuitMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateGovernmentPursuitMutationOptions(options));
    }

export const getListGovernmentPursuitHistoryUrl = (id: string,
    params?: ListGovernmentPursuitHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/government/pursuits/${id}/history?${stringifiedParams}` : `/api/web-signals/government/pursuits/${id}/history`
}

export const listGovernmentPursuitHistory = async (id: string,
    params?: ListGovernmentPursuitHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<GovernmentPursuitHistory> => {

  return customFetch<GovernmentPursuitHistory>(getListGovernmentPursuitHistoryUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListGovernmentPursuitHistoryQueryKey = (id: string,
    params?: ListGovernmentPursuitHistoryParams,) => {
    return [
    `/api/web-signals/government/pursuits/${id}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListGovernmentPursuitHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listGovernmentPursuitHistory>>, TError = void>(id: string,
    params?: ListGovernmentPursuitHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listGovernmentPursuitHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListGovernmentPursuitHistoryQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listGovernmentPursuitHistory>>> = ({ signal }) => listGovernmentPursuitHistory(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listGovernmentPursuitHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListGovernmentPursuitHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listGovernmentPursuitHistory>>>
export type ListGovernmentPursuitHistoryQueryError = void



export function useListGovernmentPursuitHistory<TData = Awaited<ReturnType<typeof listGovernmentPursuitHistory>>, TError = void>(
 id: string,
    params?: ListGovernmentPursuitHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listGovernmentPursuitHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListGovernmentPursuitHistoryQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListTenderOpportunitiesUrl = (params?: ListTenderOpportunitiesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    const explodeParameters = ["status","band","observationId"];

    if (Array.isArray(value) && explodeParameters.includes(key)) {
      value.forEach((v) => {
        normalizedParams.append(key, v === null ? 'null' : String(v));
      });
      return;
    }

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tender-opportunities?${stringifiedParams}` : `/api/tender-opportunities`
}

/**
 * @summary List discovered tender opportunities in this entity, ranked by match band then closing date. Low-match items are included unless filtered out.
 */
export const listTenderOpportunities = async (params?: ListTenderOpportunitiesParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderOpportunityList> => {

  return customFetch<TenderOpportunityList>(getListTenderOpportunitiesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderOpportunitiesQueryKey = (params?: ListTenderOpportunitiesParams,) => {
    return [
    `/api/tender-opportunities`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderOpportunitiesQueryOptions = <TData = Awaited<ReturnType<typeof listTenderOpportunities>>, TError = void>(params?: ListTenderOpportunitiesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderOpportunities>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderOpportunitiesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderOpportunities>>> = ({ signal }) => listTenderOpportunities(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderOpportunities>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderOpportunitiesQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderOpportunities>>>
export type ListTenderOpportunitiesQueryError = void


/**
 * @summary List discovered tender opportunities in this entity, ranked by match band then closing date. Low-match items are included unless filtered out.
 */

export function useListTenderOpportunities<TData = Awaited<ReturnType<typeof listTenderOpportunities>>, TError = void>(
 params?: ListTenderOpportunitiesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderOpportunities>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderOpportunitiesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetTenderOpportunityUnreadCountUrl = () => {




  return `/api/tender-opportunities/unread-count`
}

/**
 * @summary Count new strong or possible tender opportunities the caller has not viewed.
 */
export const getTenderOpportunityUnreadCount = async ( options?: Parameters<typeof customFetch>[1]): Promise<TenderOpportunityUnreadCount> => {

  return customFetch<TenderOpportunityUnreadCount>(getGetTenderOpportunityUnreadCountUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenderOpportunityUnreadCountQueryKey = () => {
    return [
    `/api/tender-opportunities/unread-count`
    ] as const;
    }


export const getGetTenderOpportunityUnreadCountQueryOptions = <TData = Awaited<ReturnType<typeof getTenderOpportunityUnreadCount>>, TError = void>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderOpportunityUnreadCount>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenderOpportunityUnreadCountQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenderOpportunityUnreadCount>>> = ({ signal }) => getTenderOpportunityUnreadCount({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenderOpportunityUnreadCount>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTenderOpportunityUnreadCountQueryResult = NonNullable<Awaited<ReturnType<typeof getTenderOpportunityUnreadCount>>>
export type GetTenderOpportunityUnreadCountQueryError = void


/**
 * @summary Count new strong or possible tender opportunities the caller has not viewed.
 */

export function useGetTenderOpportunityUnreadCount<TData = Awaited<ReturnType<typeof getTenderOpportunityUnreadCount>>, TError = void>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderOpportunityUnreadCount>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTenderOpportunityUnreadCountQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPreviewTenderOpportunityDigestUrl = () => {




  return `/api/tender-opportunities/digest/preview`
}

/**
 * @summary Render the daily tender digest the caller would receive today: new matches, closing-soon items and new addenda. Nothing is sent.
 */
export const previewTenderOpportunityDigest = async ( options?: Parameters<typeof customFetch>[1]): Promise<TenderOpportunityDigestPreview> => {

  return customFetch<TenderOpportunityDigestPreview>(getPreviewTenderOpportunityDigestUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPreviewTenderOpportunityDigestMutationKey = () => ['previewTenderOpportunityDigest'] as const;

export const getPreviewTenderOpportunityDigestMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewTenderOpportunityDigest>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewTenderOpportunityDigest>>, TError,void, TContext> => {

const mutationKey = getPreviewTenderOpportunityDigestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewTenderOpportunityDigest>>, void> = () => {


          return  previewTenderOpportunityDigest(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewTenderOpportunityDigestMutationResult = NonNullable<Awaited<ReturnType<typeof previewTenderOpportunityDigest>>>

    export type PreviewTenderOpportunityDigestMutationError = void


    /**
 * @summary Render the daily tender digest the caller would receive today: new matches, closing-soon items and new addenda. Nothing is sent.
 */
export const usePreviewTenderOpportunityDigest = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewTenderOpportunityDigest>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewTenderOpportunityDigest>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getPreviewTenderOpportunityDigestMutationOptions(options));
    }

export const getGetTenderDigestPreferenceUrl = () => {




  return `/api/tender-opportunities/digest/preference`
}

/**
 * @summary Read the caller's personal daily tender digest preference in this entity.
 */
export const getTenderDigestPreference = async ( options?: Parameters<typeof customFetch>[1]): Promise<TenderDigestPreference> => {

  return customFetch<TenderDigestPreference>(getGetTenderDigestPreferenceUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenderDigestPreferenceQueryKey = () => {
    return [
    `/api/tender-opportunities/digest/preference`
    ] as const;
    }


export const getGetTenderDigestPreferenceQueryOptions = <TData = Awaited<ReturnType<typeof getTenderDigestPreference>>, TError = void>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderDigestPreference>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenderDigestPreferenceQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenderDigestPreference>>> = ({ signal }) => getTenderDigestPreference({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenderDigestPreference>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTenderDigestPreferenceQueryResult = NonNullable<Awaited<ReturnType<typeof getTenderDigestPreference>>>
export type GetTenderDigestPreferenceQueryError = void


/**
 * @summary Read the caller's personal daily tender digest preference in this entity.
 */

export function useGetTenderDigestPreference<TData = Awaited<ReturnType<typeof getTenderDigestPreference>>, TError = void>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderDigestPreference>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTenderDigestPreferenceQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateTenderDigestPreferenceUrl = () => {




  return `/api/tender-opportunities/digest/preference`
}

/**
 * @summary Opt the caller in to or out of the daily tender digest email for this entity.
 */
export const updateTenderDigestPreference = async (tenderDigestPreferenceUpdate: TenderDigestPreferenceUpdate, options?: Parameters<typeof customFetch>[1]): Promise<TenderDigestPreference> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderDigestPreference>(getUpdateTenderDigestPreferenceUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderDigestPreferenceUpdate)
  }
);}





export const getUpdateTenderDigestPreferenceMutationKey = () => ['updateTenderDigestPreference'] as const;

export const getUpdateTenderDigestPreferenceMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenderDigestPreference>>, TError,UpdateTenderDigestPreferenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateTenderDigestPreference>>, TError,UpdateTenderDigestPreferenceMutationVariables, TContext> => {

const mutationKey = getUpdateTenderDigestPreferenceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateTenderDigestPreference>>, UpdateTenderDigestPreferenceMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateTenderDigestPreference(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateTenderDigestPreferenceMutationResult = NonNullable<Awaited<ReturnType<typeof updateTenderDigestPreference>>>
    export type UpdateTenderDigestPreferenceMutationBody = TenderDigestPreferenceUpdate
    export type UpdateTenderDigestPreferenceMutationError = void
    export type UpdateTenderDigestPreferenceMutationVariables = {data: TenderDigestPreferenceUpdate}

    /**
 * @summary Opt the caller in to or out of the daily tender digest email for this entity.
 */
export const useUpdateTenderDigestPreference = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenderDigestPreference>>, TError,UpdateTenderDigestPreferenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateTenderDigestPreference>>,
        TError,
        UpdateTenderDigestPreferenceMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateTenderDigestPreferenceMutationOptions(options));
    }

export const getGetTenderOpportunityUrl = (id: string,) => {




  return `/api/tender-opportunities/${id}`
}

/**
 * @summary Read one tender opportunity with its documents and recent history.
 */
export const getTenderOpportunity = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<TenderOpportunityDetail> => {

  return customFetch<TenderOpportunityDetail>(getGetTenderOpportunityUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenderOpportunityQueryKey = (id: string,) => {
    return [
    `/api/tender-opportunities/${id}`
    ] as const;
    }


export const getGetTenderOpportunityQueryOptions = <TData = Awaited<ReturnType<typeof getTenderOpportunity>>, TError = void>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderOpportunity>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenderOpportunityQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenderOpportunity>>> = ({ signal }) => getTenderOpportunity(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenderOpportunity>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTenderOpportunityQueryResult = NonNullable<Awaited<ReturnType<typeof getTenderOpportunity>>>
export type GetTenderOpportunityQueryError = void


/**
 * @summary Read one tender opportunity with its documents and recent history.
 */

export function useGetTenderOpportunity<TData = Awaited<ReturnType<typeof getTenderOpportunity>>, TError = void>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderOpportunity>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTenderOpportunityQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateTenderOpportunityUrl = (id: string,) => {




  return `/api/tender-opportunities/${id}`
}

/**
 * @summary Change a tender opportunity's review status or assignee. Converted and expired opportunities cannot be changed; dismissed ones can be restored.
 */
export const updateTenderOpportunity = async (id: string,
    tenderOpportunityUpdate: TenderOpportunityUpdate, options?: Parameters<typeof customFetch>[1]): Promise<TenderOpportunity> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderOpportunity>(getUpdateTenderOpportunityUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderOpportunityUpdate)
  }
);}





export const getUpdateTenderOpportunityMutationKey = () => ['updateTenderOpportunity'] as const;

export const getUpdateTenderOpportunityMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenderOpportunity>>, TError,UpdateTenderOpportunityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateTenderOpportunity>>, TError,UpdateTenderOpportunityMutationVariables, TContext> => {

const mutationKey = getUpdateTenderOpportunityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateTenderOpportunity>>, UpdateTenderOpportunityMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateTenderOpportunity(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateTenderOpportunityMutationResult = NonNullable<Awaited<ReturnType<typeof updateTenderOpportunity>>>
    export type UpdateTenderOpportunityMutationBody = TenderOpportunityUpdate
    export type UpdateTenderOpportunityMutationError = void
    export type UpdateTenderOpportunityMutationVariables = {id: string;data: TenderOpportunityUpdate}

    /**
 * @summary Change a tender opportunity's review status or assignee. Converted and expired opportunities cannot be changed; dismissed ones can be restored.
 */
export const useUpdateTenderOpportunity = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenderOpportunity>>, TError,UpdateTenderOpportunityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateTenderOpportunity>>,
        TError,
        UpdateTenderOpportunityMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateTenderOpportunityMutationOptions(options));
    }

export const getConvertTenderOpportunityUrl = (id: string,) => {




  return `/api/tender-opportunities/${id}/convert`
}

/**
 * @summary Respond to a tender opportunity: create the tender/SOW response with its documents, match or create the buyer account, add a linked pipeline opportunity and mark the opportunity converted. Idempotent by request key.
 */
export const convertTenderOpportunity = async (id: string,
    tenderOpportunityConvertRequest: TenderOpportunityConvertRequest, options?: Parameters<typeof customFetch>[1]): Promise<TenderOpportunityConversion> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderOpportunityConversion>(getConvertTenderOpportunityUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderOpportunityConvertRequest)
  }
);}





export const getConvertTenderOpportunityMutationKey = () => ['convertTenderOpportunity'] as const;

export const getConvertTenderOpportunityMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof convertTenderOpportunity>>, TError,ConvertTenderOpportunityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof convertTenderOpportunity>>, TError,ConvertTenderOpportunityMutationVariables, TContext> => {

const mutationKey = getConvertTenderOpportunityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof convertTenderOpportunity>>, ConvertTenderOpportunityMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  convertTenderOpportunity(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConvertTenderOpportunityMutationResult = NonNullable<Awaited<ReturnType<typeof convertTenderOpportunity>>>
    export type ConvertTenderOpportunityMutationBody = TenderOpportunityConvertRequest
    export type ConvertTenderOpportunityMutationError = void
    export type ConvertTenderOpportunityMutationVariables = {id: string;data: TenderOpportunityConvertRequest}

    /**
 * @summary Respond to a tender opportunity: create the tender/SOW response with its documents, match or create the buyer account, add a linked pipeline opportunity and mark the opportunity converted. Idempotent by request key.
 */
export const useConvertTenderOpportunity = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof convertTenderOpportunity>>, TError,ConvertTenderOpportunityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof convertTenderOpportunity>>,
        TError,
        ConvertTenderOpportunityMutationVariables,
        TContext
      > => {
      return useMutation(getConvertTenderOpportunityMutationOptions(options));
    }

export const getRerankTenderOpportunityUrl = (id: string,) => {




  return `/api/tender-opportunities/${id}/rerank`
}

/**
 * @summary Queue a fresh match ranking for one tender opportunity against the current Offerings Profile and thresholds.
 */
export const rerankTenderOpportunity = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<TenderOpportunityRerankQueued> => {

  return customFetch<TenderOpportunityRerankQueued>(getRerankTenderOpportunityUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRerankTenderOpportunityMutationKey = () => ['rerankTenderOpportunity'] as const;

export const getRerankTenderOpportunityMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rerankTenderOpportunity>>, TError,RerankTenderOpportunityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof rerankTenderOpportunity>>, TError,RerankTenderOpportunityMutationVariables, TContext> => {

const mutationKey = getRerankTenderOpportunityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof rerankTenderOpportunity>>, RerankTenderOpportunityMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  rerankTenderOpportunity(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RerankTenderOpportunityMutationResult = NonNullable<Awaited<ReturnType<typeof rerankTenderOpportunity>>>

    export type RerankTenderOpportunityMutationError = void
    export type RerankTenderOpportunityMutationVariables = {id: string}

    /**
 * @summary Queue a fresh match ranking for one tender opportunity against the current Offerings Profile and thresholds.
 */
export const useRerankTenderOpportunity = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rerankTenderOpportunity>>, TError,RerankTenderOpportunityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof rerankTenderOpportunity>>,
        TError,
        RerankTenderOpportunityMutationVariables,
        TContext
      > => {
      return useMutation(getRerankTenderOpportunityMutationOptions(options));
    }

export const getMarkTenderOpportunitySeenUrl = (id: string,) => {




  return `/api/tender-opportunities/${id}/seen`
}

/**
 * @summary Record that the caller has viewed a tender opportunity so it no longer counts as unread for them.
 */
export const markTenderOpportunitySeen = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<TenderOpportunity> => {

  return customFetch<TenderOpportunity>(getMarkTenderOpportunitySeenUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getMarkTenderOpportunitySeenMutationKey = () => ['markTenderOpportunitySeen'] as const;

export const getMarkTenderOpportunitySeenMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markTenderOpportunitySeen>>, TError,MarkTenderOpportunitySeenMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof markTenderOpportunitySeen>>, TError,MarkTenderOpportunitySeenMutationVariables, TContext> => {

const mutationKey = getMarkTenderOpportunitySeenMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof markTenderOpportunitySeen>>, MarkTenderOpportunitySeenMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  markTenderOpportunitySeen(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MarkTenderOpportunitySeenMutationResult = NonNullable<Awaited<ReturnType<typeof markTenderOpportunitySeen>>>

    export type MarkTenderOpportunitySeenMutationError = void
    export type MarkTenderOpportunitySeenMutationVariables = {id: string}

    /**
 * @summary Record that the caller has viewed a tender opportunity so it no longer counts as unread for them.
 */
export const useMarkTenderOpportunitySeen = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markTenderOpportunitySeen>>, TError,MarkTenderOpportunitySeenMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof markTenderOpportunitySeen>>,
        TError,
        MarkTenderOpportunitySeenMutationVariables,
        TContext
      > => {
      return useMutation(getMarkTenderOpportunitySeenMutationOptions(options));
    }

export const getListTenderOpportunityEventsUrl = (id: string,
    params?: ListTenderOpportunityEventsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tender-opportunities/${id}/events?${stringifiedParams}` : `/api/tender-opportunities/${id}/events`
}

/**
 * @summary List the append-only history of a tender opportunity, newest first.
 */
export const listTenderOpportunityEvents = async (id: string,
    params?: ListTenderOpportunityEventsParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderOpportunityEventList> => {

  return customFetch<TenderOpportunityEventList>(getListTenderOpportunityEventsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderOpportunityEventsQueryKey = (id: string,
    params?: ListTenderOpportunityEventsParams,) => {
    return [
    `/api/tender-opportunities/${id}/events`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderOpportunityEventsQueryOptions = <TData = Awaited<ReturnType<typeof listTenderOpportunityEvents>>, TError = void>(id: string,
    params?: ListTenderOpportunityEventsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderOpportunityEvents>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderOpportunityEventsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderOpportunityEvents>>> = ({ signal }) => listTenderOpportunityEvents(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderOpportunityEvents>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderOpportunityEventsQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderOpportunityEvents>>>
export type ListTenderOpportunityEventsQueryError = void


/**
 * @summary List the append-only history of a tender opportunity, newest first.
 */

export function useListTenderOpportunityEvents<TData = Awaited<ReturnType<typeof listTenderOpportunityEvents>>, TError = void>(
 id: string,
    params?: ListTenderOpportunityEventsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderOpportunityEvents>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderOpportunityEventsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListTenderOpportunityDocumentsUrl = (id: string,
    params?: ListTenderOpportunityDocumentsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tender-opportunities/${id}/documents?${stringifiedParams}` : `/api/tender-opportunities/${id}/documents`
}

/**
 * @summary List the tender documents on an opportunity with how each was acquired.
 */
export const listTenderOpportunityDocuments = async (id: string,
    params?: ListTenderOpportunityDocumentsParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderOpportunityDocumentList> => {

  return customFetch<TenderOpportunityDocumentList>(getListTenderOpportunityDocumentsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderOpportunityDocumentsQueryKey = (id: string,
    params?: ListTenderOpportunityDocumentsParams,) => {
    return [
    `/api/tender-opportunities/${id}/documents`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderOpportunityDocumentsQueryOptions = <TData = Awaited<ReturnType<typeof listTenderOpportunityDocuments>>, TError = void>(id: string,
    params?: ListTenderOpportunityDocumentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderOpportunityDocuments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderOpportunityDocumentsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderOpportunityDocuments>>> = ({ signal }) => listTenderOpportunityDocuments(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderOpportunityDocuments>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderOpportunityDocumentsQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderOpportunityDocuments>>>
export type ListTenderOpportunityDocumentsQueryError = void


/**
 * @summary List the tender documents on an opportunity with how each was acquired.
 */

export function useListTenderOpportunityDocuments<TData = Awaited<ReturnType<typeof listTenderOpportunityDocuments>>, TError = void>(
 id: string,
    params?: ListTenderOpportunityDocumentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderOpportunityDocuments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderOpportunityDocumentsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUploadTenderOpportunityDocumentUrl = (id: string,) => {




  return `/api/tender-opportunities/${id}/documents`
}

/**
 * @summary Upload a tender document from the portal (up to 20 MB). Uploading the same file again returns the existing document.
 */
export const uploadTenderOpportunityDocument = async (id: string,
    tenderOpportunityDocumentUpload: TenderOpportunityDocumentUpload, options?: Parameters<typeof customFetch>[1]): Promise<TenderOpportunityDocumentUploadResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderOpportunityDocumentUploadResult>(getUploadTenderOpportunityDocumentUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderOpportunityDocumentUpload)
  }
);}





export const getUploadTenderOpportunityDocumentMutationKey = () => ['uploadTenderOpportunityDocument'] as const;

export const getUploadTenderOpportunityDocumentMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadTenderOpportunityDocument>>, TError,UploadTenderOpportunityDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadTenderOpportunityDocument>>, TError,UploadTenderOpportunityDocumentMutationVariables, TContext> => {

const mutationKey = getUploadTenderOpportunityDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadTenderOpportunityDocument>>, UploadTenderOpportunityDocumentMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  uploadTenderOpportunityDocument(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadTenderOpportunityDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof uploadTenderOpportunityDocument>>>
    export type UploadTenderOpportunityDocumentMutationBody = TenderOpportunityDocumentUpload
    export type UploadTenderOpportunityDocumentMutationError = void
    export type UploadTenderOpportunityDocumentMutationVariables = {id: string;data: TenderOpportunityDocumentUpload}

    /**
 * @summary Upload a tender document from the portal (up to 20 MB). Uploading the same file again returns the existing document.
 */
export const useUploadTenderOpportunityDocument = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadTenderOpportunityDocument>>, TError,UploadTenderOpportunityDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadTenderOpportunityDocument>>,
        TError,
        UploadTenderOpportunityDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getUploadTenderOpportunityDocumentMutationOptions(options));
    }

export const getDeleteTenderOpportunityDocumentUrl = (id: string,
    documentId: string,) => {




  return `/api/tender-opportunities/${id}/documents/${documentId}`
}

/**
 * @summary Remove a document that staff uploaded by hand. Portal documents cannot be removed.
 */
export const deleteTenderOpportunityDocument = async (id: string,
    documentId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteTenderOpportunityDocumentUrl(id,documentId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteTenderOpportunityDocumentMutationKey = () => ['deleteTenderOpportunityDocument'] as const;

export const getDeleteTenderOpportunityDocumentMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTenderOpportunityDocument>>, TError,DeleteTenderOpportunityDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteTenderOpportunityDocument>>, TError,DeleteTenderOpportunityDocumentMutationVariables, TContext> => {

const mutationKey = getDeleteTenderOpportunityDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteTenderOpportunityDocument>>, DeleteTenderOpportunityDocumentMutationVariables> = (props) => {
          const {id,documentId} = props ?? {};

          return  deleteTenderOpportunityDocument(id,documentId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteTenderOpportunityDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof deleteTenderOpportunityDocument>>>

    export type DeleteTenderOpportunityDocumentMutationError = void
    export type DeleteTenderOpportunityDocumentMutationVariables = {id: string;documentId: string}

    /**
 * @summary Remove a document that staff uploaded by hand. Portal documents cannot be removed.
 */
export const useDeleteTenderOpportunityDocument = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTenderOpportunityDocument>>, TError,DeleteTenderOpportunityDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteTenderOpportunityDocument>>,
        TError,
        DeleteTenderOpportunityDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteTenderOpportunityDocumentMutationOptions(options));
    }

export const getDownloadTenderOpportunityDocumentUrl = (id: string,
    documentId: string,) => {




  return `/api/tender-opportunities/${id}/documents/${documentId}/content`
}

/**
 * @summary Download the stored bytes of one acquired tender opportunity document
 */
export const downloadTenderOpportunityDocument = async (id: string,
    documentId: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadTenderOpportunityDocumentUrl(id,documentId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadTenderOpportunityDocumentQueryKey = (id: string,
    documentId: string,) => {
    return [
    `/api/tender-opportunities/${id}/documents/${documentId}/content`
    ] as const;
    }


export const getDownloadTenderOpportunityDocumentQueryOptions = <TData = Awaited<ReturnType<typeof downloadTenderOpportunityDocument>>, TError = void>(id: string,
    documentId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadTenderOpportunityDocument>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadTenderOpportunityDocumentQueryKey(id,documentId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadTenderOpportunityDocument>>> = ({ signal }) => downloadTenderOpportunityDocument(id,documentId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && documentId !== null && documentId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadTenderOpportunityDocument>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadTenderOpportunityDocumentQueryResult = NonNullable<Awaited<ReturnType<typeof downloadTenderOpportunityDocument>>>
export type DownloadTenderOpportunityDocumentQueryError = void


/**
 * @summary Download the stored bytes of one acquired tender opportunity document
 */

export function useDownloadTenderOpportunityDocument<TData = Awaited<ReturnType<typeof downloadTenderOpportunityDocument>>, TError = void>(
 id: string,
    documentId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadTenderOpportunityDocument>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadTenderOpportunityDocumentQueryOptions(id,documentId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRetryTenderOpportunityDocumentUrl = (id: string,
    documentId: string,) => {




  return `/api/tender-opportunities/${id}/documents/${documentId}/retry`
}

/**
 * @summary Queue another download attempt for a portal document that failed or needs sign-in.
 */
export const retryTenderOpportunityDocument = async (id: string,
    documentId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenderOpportunityDocument> => {

  return customFetch<TenderOpportunityDocument>(getRetryTenderOpportunityDocumentUrl(id,documentId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRetryTenderOpportunityDocumentMutationKey = () => ['retryTenderOpportunityDocument'] as const;

export const getRetryTenderOpportunityDocumentMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryTenderOpportunityDocument>>, TError,RetryTenderOpportunityDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof retryTenderOpportunityDocument>>, TError,RetryTenderOpportunityDocumentMutationVariables, TContext> => {

const mutationKey = getRetryTenderOpportunityDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof retryTenderOpportunityDocument>>, RetryTenderOpportunityDocumentMutationVariables> = (props) => {
          const {id,documentId} = props ?? {};

          return  retryTenderOpportunityDocument(id,documentId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RetryTenderOpportunityDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof retryTenderOpportunityDocument>>>

    export type RetryTenderOpportunityDocumentMutationError = void
    export type RetryTenderOpportunityDocumentMutationVariables = {id: string;documentId: string}

    /**
 * @summary Queue another download attempt for a portal document that failed or needs sign-in.
 */
export const useRetryTenderOpportunityDocument = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryTenderOpportunityDocument>>, TError,RetryTenderOpportunityDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof retryTenderOpportunityDocument>>,
        TError,
        RetryTenderOpportunityDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getRetryTenderOpportunityDocumentMutationOptions(options));
    }

export const getCreateGovernmentPursuitTaskUrl = (id: string,) => {




  return `/api/web-signals/government/pursuits/${id}/tasks`
}

export const createGovernmentPursuitTask = async (id: string,
    governmentPursuitTaskCreate: GovernmentPursuitTaskCreate, options?: Parameters<typeof customFetch>[1]): Promise<Activity> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Activity>(getCreateGovernmentPursuitTaskUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(governmentPursuitTaskCreate)
  }
);}





export const getCreateGovernmentPursuitTaskMutationKey = () => ['createGovernmentPursuitTask'] as const;

export const getCreateGovernmentPursuitTaskMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createGovernmentPursuitTask>>, TError,CreateGovernmentPursuitTaskMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createGovernmentPursuitTask>>, TError,CreateGovernmentPursuitTaskMutationVariables, TContext> => {

const mutationKey = getCreateGovernmentPursuitTaskMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createGovernmentPursuitTask>>, CreateGovernmentPursuitTaskMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createGovernmentPursuitTask(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateGovernmentPursuitTaskMutationResult = NonNullable<Awaited<ReturnType<typeof createGovernmentPursuitTask>>>
    export type CreateGovernmentPursuitTaskMutationBody = GovernmentPursuitTaskCreate
    export type CreateGovernmentPursuitTaskMutationError = void
    export type CreateGovernmentPursuitTaskMutationVariables = {id: string;data: GovernmentPursuitTaskCreate}

    export const useCreateGovernmentPursuitTask = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createGovernmentPursuitTask>>, TError,CreateGovernmentPursuitTaskMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createGovernmentPursuitTask>>,
        TError,
        CreateGovernmentPursuitTaskMutationVariables,
        TContext
      > => {
      return useMutation(getCreateGovernmentPursuitTaskMutationOptions(options));
    }

export const getGetGovernmentSourceSuggestionsUrl = () => {




  return `/api/web-signals/government/source-suggestions`
}

export const getGovernmentSourceSuggestions = async ( options?: Parameters<typeof customFetch>[1]): Promise<GovernmentSourceSuggestions> => {

  return customFetch<GovernmentSourceSuggestions>(getGetGovernmentSourceSuggestionsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetGovernmentSourceSuggestionsQueryKey = () => {
    return [
    `/api/web-signals/government/source-suggestions`
    ] as const;
    }


export const getGetGovernmentSourceSuggestionsQueryOptions = <TData = Awaited<ReturnType<typeof getGovernmentSourceSuggestions>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGovernmentSourceSuggestions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetGovernmentSourceSuggestionsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getGovernmentSourceSuggestions>>> = ({ signal }) => getGovernmentSourceSuggestions({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getGovernmentSourceSuggestions>>, TError, TData> & { queryKey: QueryKey }
}

export type GetGovernmentSourceSuggestionsQueryResult = NonNullable<Awaited<ReturnType<typeof getGovernmentSourceSuggestions>>>
export type GetGovernmentSourceSuggestionsQueryError = unknown



export function useGetGovernmentSourceSuggestions<TData = Awaited<ReturnType<typeof getGovernmentSourceSuggestions>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGovernmentSourceSuggestions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetGovernmentSourceSuggestionsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetGovernmentDiscoveryCoverageUrl = (params?: GetGovernmentDiscoveryCoverageParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/government/coverage?${stringifiedParams}` : `/api/web-signals/government/coverage`
}

export const getGovernmentDiscoveryCoverage = async (params?: GetGovernmentDiscoveryCoverageParams, options?: Parameters<typeof customFetch>[1]): Promise<GovernmentDiscoveryCoverage> => {

  return customFetch<GovernmentDiscoveryCoverage>(getGetGovernmentDiscoveryCoverageUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetGovernmentDiscoveryCoverageQueryKey = (params?: GetGovernmentDiscoveryCoverageParams,) => {
    return [
    `/api/web-signals/government/coverage`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetGovernmentDiscoveryCoverageQueryOptions = <TData = Awaited<ReturnType<typeof getGovernmentDiscoveryCoverage>>, TError = unknown>(params?: GetGovernmentDiscoveryCoverageParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGovernmentDiscoveryCoverage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetGovernmentDiscoveryCoverageQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getGovernmentDiscoveryCoverage>>> = ({ signal }) => getGovernmentDiscoveryCoverage(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getGovernmentDiscoveryCoverage>>, TError, TData> & { queryKey: QueryKey }
}

export type GetGovernmentDiscoveryCoverageQueryResult = NonNullable<Awaited<ReturnType<typeof getGovernmentDiscoveryCoverage>>>
export type GetGovernmentDiscoveryCoverageQueryError = unknown



export function useGetGovernmentDiscoveryCoverage<TData = Awaited<ReturnType<typeof getGovernmentDiscoveryCoverage>>, TError = unknown>(
 params?: GetGovernmentDiscoveryCoverageParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGovernmentDiscoveryCoverage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetGovernmentDiscoveryCoverageQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListGovernmentFindingsUrl = (params?: ListGovernmentFindingsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/government/findings?${stringifiedParams}` : `/api/web-signals/government/findings`
}

export const listGovernmentFindings = async (params?: ListGovernmentFindingsParams, options?: Parameters<typeof customFetch>[1]): Promise<GovernmentFindingPage> => {

  return customFetch<GovernmentFindingPage>(getListGovernmentFindingsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListGovernmentFindingsQueryKey = (params?: ListGovernmentFindingsParams,) => {
    return [
    `/api/web-signals/government/findings`, ...(params ? [params] : [])
    ] as const;
    }


export const getListGovernmentFindingsQueryOptions = <TData = Awaited<ReturnType<typeof listGovernmentFindings>>, TError = unknown>(params?: ListGovernmentFindingsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listGovernmentFindings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListGovernmentFindingsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listGovernmentFindings>>> = ({ signal }) => listGovernmentFindings(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listGovernmentFindings>>, TError, TData> & { queryKey: QueryKey }
}

export type ListGovernmentFindingsQueryResult = NonNullable<Awaited<ReturnType<typeof listGovernmentFindings>>>
export type ListGovernmentFindingsQueryError = unknown



export function useListGovernmentFindings<TData = Awaited<ReturnType<typeof listGovernmentFindings>>, TError = unknown>(
 params?: ListGovernmentFindingsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listGovernmentFindings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListGovernmentFindingsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetGovernmentJurisdictionsUrl = (scope: GovernmentAdministrationScope,) => {




  return `/api/web-signals/government/jurisdictions/${scope}`
}

/**
 * @summary Read administrator jurisdiction settings
 */
export const getGovernmentJurisdictions = async (scope: GovernmentAdministrationScope, options?: Parameters<typeof customFetch>[1]): Promise<GovernmentJurisdictionView> => {

  return customFetch<GovernmentJurisdictionView>(getGetGovernmentJurisdictionsUrl(scope),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetGovernmentJurisdictionsQueryKey = (scope: GovernmentAdministrationScope,) => {
    return [
    `/api/web-signals/government/jurisdictions/${scope}`
    ] as const;
    }


export const getGetGovernmentJurisdictionsQueryOptions = <TData = Awaited<ReturnType<typeof getGovernmentJurisdictions>>, TError = void>(scope: GovernmentAdministrationScope, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGovernmentJurisdictions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetGovernmentJurisdictionsQueryKey(scope);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getGovernmentJurisdictions>>> = ({ signal }) => getGovernmentJurisdictions(scope, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: scope !== null && scope !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getGovernmentJurisdictions>>, TError, TData> & { queryKey: QueryKey }
}

export type GetGovernmentJurisdictionsQueryResult = NonNullable<Awaited<ReturnType<typeof getGovernmentJurisdictions>>>
export type GetGovernmentJurisdictionsQueryError = void


/**
 * @summary Read administrator jurisdiction settings
 */

export function useGetGovernmentJurisdictions<TData = Awaited<ReturnType<typeof getGovernmentJurisdictions>>, TError = void>(
 scope: GovernmentAdministrationScope, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGovernmentJurisdictions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetGovernmentJurisdictionsQueryOptions(scope,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getWriteGovernmentJurisdictionsUrl = (scope: GovernmentAdministrationScope,) => {




  return `/api/web-signals/government/jurisdictions/${scope}`
}

/**
 * @summary Append tenant defaults or entity override; null resets inheritance
 */
export const writeGovernmentJurisdictions = async (scope: GovernmentAdministrationScope,
    writeGovernmentJurisdictionsBody: WriteGovernmentJurisdictions, options?: Parameters<typeof customFetch>[1]): Promise<GovernmentJurisdictionView> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<GovernmentJurisdictionView>(getWriteGovernmentJurisdictionsUrl(scope),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(writeGovernmentJurisdictionsBody)
  }
);}





export const getWriteGovernmentJurisdictionsMutationKey = () => ['writeGovernmentJurisdictions'] as const;

export const getWriteGovernmentJurisdictionsMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writeGovernmentJurisdictions>>, TError,WriteGovernmentJurisdictionsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof writeGovernmentJurisdictions>>, TError,WriteGovernmentJurisdictionsMutationVariables, TContext> => {

const mutationKey = getWriteGovernmentJurisdictionsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof writeGovernmentJurisdictions>>, WriteGovernmentJurisdictionsMutationVariables> = (props) => {
          const {scope,data} = props ?? {};

          return  writeGovernmentJurisdictions(scope,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type WriteGovernmentJurisdictionsMutationResult = NonNullable<Awaited<ReturnType<typeof writeGovernmentJurisdictions>>>
    export type WriteGovernmentJurisdictionsMutationBody = WriteGovernmentJurisdictions
    export type WriteGovernmentJurisdictionsMutationError = void
    export type WriteGovernmentJurisdictionsMutationVariables = {scope: GovernmentAdministrationScope;data: WriteGovernmentJurisdictions}

    /**
 * @summary Append tenant defaults or entity override; null resets inheritance
 */
export const useWriteGovernmentJurisdictions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writeGovernmentJurisdictions>>, TError,WriteGovernmentJurisdictionsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof writeGovernmentJurisdictions>>,
        TError,
        WriteGovernmentJurisdictionsMutationVariables,
        TContext
      > => {
      return useMutation(getWriteGovernmentJurisdictionsMutationOptions(options));
    }

export const getGetEffectiveGovernmentJurisdictionsUrl = () => {




  return `/api/web-signals/government/jurisdictions`
}

/**
 * @summary Read selected entity effective government coverage
 */
export const getEffectiveGovernmentJurisdictions = async ( options?: Parameters<typeof customFetch>[1]): Promise<GovernmentJurisdictionView> => {

  return customFetch<GovernmentJurisdictionView>(getGetEffectiveGovernmentJurisdictionsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEffectiveGovernmentJurisdictionsQueryKey = () => {
    return [
    `/api/web-signals/government/jurisdictions`
    ] as const;
    }


export const getGetEffectiveGovernmentJurisdictionsQueryOptions = <TData = Awaited<ReturnType<typeof getEffectiveGovernmentJurisdictions>>, TError = void>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEffectiveGovernmentJurisdictions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEffectiveGovernmentJurisdictionsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEffectiveGovernmentJurisdictions>>> = ({ signal }) => getEffectiveGovernmentJurisdictions({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEffectiveGovernmentJurisdictions>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEffectiveGovernmentJurisdictionsQueryResult = NonNullable<Awaited<ReturnType<typeof getEffectiveGovernmentJurisdictions>>>
export type GetEffectiveGovernmentJurisdictionsQueryError = void


/**
 * @summary Read selected entity effective government coverage
 */

export function useGetEffectiveGovernmentJurisdictions<TData = Awaited<ReturnType<typeof getEffectiveGovernmentJurisdictions>>, TError = void>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEffectiveGovernmentJurisdictions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEffectiveGovernmentJurisdictionsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPreviewDocumentWorkspaceEvidenceUrl = (workspaceId: string,) => {




  return `/api/document-workspaces/${workspaceId}/source-preview`
}

export const previewDocumentWorkspaceEvidence = async (workspaceId: string,
    documentWorkspaceEvidenceRequest: DocumentWorkspaceEvidenceRequest, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceEvidenceManifest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentWorkspaceEvidenceManifest>(getPreviewDocumentWorkspaceEvidenceUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(documentWorkspaceEvidenceRequest)
  }
);}





export const getPreviewDocumentWorkspaceEvidenceMutationKey = () => ['previewDocumentWorkspaceEvidence'] as const;

export const getPreviewDocumentWorkspaceEvidenceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewDocumentWorkspaceEvidence>>, TError,PreviewDocumentWorkspaceEvidenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewDocumentWorkspaceEvidence>>, TError,PreviewDocumentWorkspaceEvidenceMutationVariables, TContext> => {

const mutationKey = getPreviewDocumentWorkspaceEvidenceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewDocumentWorkspaceEvidence>>, PreviewDocumentWorkspaceEvidenceMutationVariables> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  previewDocumentWorkspaceEvidence(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewDocumentWorkspaceEvidenceMutationResult = NonNullable<Awaited<ReturnType<typeof previewDocumentWorkspaceEvidence>>>
    export type PreviewDocumentWorkspaceEvidenceMutationBody = DocumentWorkspaceEvidenceRequest
    export type PreviewDocumentWorkspaceEvidenceMutationError = Error
    export type PreviewDocumentWorkspaceEvidenceMutationVariables = {workspaceId: string;data: DocumentWorkspaceEvidenceRequest}

    export const usePreviewDocumentWorkspaceEvidence = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewDocumentWorkspaceEvidence>>, TError,PreviewDocumentWorkspaceEvidenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewDocumentWorkspaceEvidence>>,
        TError,
        PreviewDocumentWorkspaceEvidenceMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewDocumentWorkspaceEvidenceMutationOptions(options));
    }

export const getCreateDocumentWorkspaceEvidenceSnapshotUrl = (workspaceId: string,) => {




  return `/api/document-workspaces/${workspaceId}/evidence-snapshots`
}

export const createDocumentWorkspaceEvidenceSnapshot = async (workspaceId: string,
    createDocumentWorkspaceEvidenceSnapshotBody: CreateDocumentWorkspaceEvidenceSnapshot, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceEvidenceSnapshot> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentWorkspaceEvidenceSnapshot>(getCreateDocumentWorkspaceEvidenceSnapshotUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createDocumentWorkspaceEvidenceSnapshotBody)
  }
);}





export const getCreateDocumentWorkspaceEvidenceSnapshotMutationKey = () => ['createDocumentWorkspaceEvidenceSnapshot'] as const;

export const getCreateDocumentWorkspaceEvidenceSnapshotMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDocumentWorkspaceEvidenceSnapshot>>, TError,CreateDocumentWorkspaceEvidenceSnapshotMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createDocumentWorkspaceEvidenceSnapshot>>, TError,CreateDocumentWorkspaceEvidenceSnapshotMutationVariables, TContext> => {

const mutationKey = getCreateDocumentWorkspaceEvidenceSnapshotMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createDocumentWorkspaceEvidenceSnapshot>>, CreateDocumentWorkspaceEvidenceSnapshotMutationVariables> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  createDocumentWorkspaceEvidenceSnapshot(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateDocumentWorkspaceEvidenceSnapshotMutationResult = NonNullable<Awaited<ReturnType<typeof createDocumentWorkspaceEvidenceSnapshot>>>
    export type CreateDocumentWorkspaceEvidenceSnapshotMutationBody = CreateDocumentWorkspaceEvidenceSnapshot
    export type CreateDocumentWorkspaceEvidenceSnapshotMutationError = Error
    export type CreateDocumentWorkspaceEvidenceSnapshotMutationVariables = {workspaceId: string;data: CreateDocumentWorkspaceEvidenceSnapshot}

    export const useCreateDocumentWorkspaceEvidenceSnapshot = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDocumentWorkspaceEvidenceSnapshot>>, TError,CreateDocumentWorkspaceEvidenceSnapshotMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createDocumentWorkspaceEvidenceSnapshot>>,
        TError,
        CreateDocumentWorkspaceEvidenceSnapshotMutationVariables,
        TContext
      > => {
      return useMutation(getCreateDocumentWorkspaceEvidenceSnapshotMutationOptions(options));
    }

export const getGetDocumentWorkspaceEvidenceSnapshotUrl = (workspaceId: string,
    snapshotId: string,) => {




  return `/api/document-workspaces/${workspaceId}/evidence-snapshots/${snapshotId}`
}

export const getDocumentWorkspaceEvidenceSnapshot = async (workspaceId: string,
    snapshotId: string, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceEvidenceSnapshot> => {

  return customFetch<DocumentWorkspaceEvidenceSnapshot>(getGetDocumentWorkspaceEvidenceSnapshotUrl(workspaceId,snapshotId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDocumentWorkspaceEvidenceSnapshotQueryKey = (workspaceId: string,
    snapshotId: string,) => {
    return [
    `/api/document-workspaces/${workspaceId}/evidence-snapshots/${snapshotId}`
    ] as const;
    }


export const getGetDocumentWorkspaceEvidenceSnapshotQueryOptions = <TData = Awaited<ReturnType<typeof getDocumentWorkspaceEvidenceSnapshot>>, TError = Error>(workspaceId: string,
    snapshotId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentWorkspaceEvidenceSnapshot>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDocumentWorkspaceEvidenceSnapshotQueryKey(workspaceId,snapshotId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDocumentWorkspaceEvidenceSnapshot>>> = ({ signal }) => getDocumentWorkspaceEvidenceSnapshot(workspaceId,snapshotId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && snapshotId !== null && snapshotId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDocumentWorkspaceEvidenceSnapshot>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDocumentWorkspaceEvidenceSnapshotQueryResult = NonNullable<Awaited<ReturnType<typeof getDocumentWorkspaceEvidenceSnapshot>>>
export type GetDocumentWorkspaceEvidenceSnapshotQueryError = Error



export function useGetDocumentWorkspaceEvidenceSnapshot<TData = Awaited<ReturnType<typeof getDocumentWorkspaceEvidenceSnapshot>>, TError = Error>(
 workspaceId: string,
    snapshotId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentWorkspaceEvidenceSnapshot>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDocumentWorkspaceEvidenceSnapshotQueryOptions(workspaceId,snapshotId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getValidateDocumentWorkspaceEvidenceSnapshotUrl = (workspaceId: string,
    snapshotId: string,) => {




  return `/api/document-workspaces/${workspaceId}/evidence-snapshots/${snapshotId}/validate`
}

export const validateDocumentWorkspaceEvidenceSnapshot = async (workspaceId: string,
    snapshotId: string, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceEvidenceValidation> => {

  return customFetch<DocumentWorkspaceEvidenceValidation>(getValidateDocumentWorkspaceEvidenceSnapshotUrl(workspaceId,snapshotId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getValidateDocumentWorkspaceEvidenceSnapshotMutationKey = () => ['validateDocumentWorkspaceEvidenceSnapshot'] as const;

export const getValidateDocumentWorkspaceEvidenceSnapshotMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof validateDocumentWorkspaceEvidenceSnapshot>>, TError,ValidateDocumentWorkspaceEvidenceSnapshotMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof validateDocumentWorkspaceEvidenceSnapshot>>, TError,ValidateDocumentWorkspaceEvidenceSnapshotMutationVariables, TContext> => {

const mutationKey = getValidateDocumentWorkspaceEvidenceSnapshotMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof validateDocumentWorkspaceEvidenceSnapshot>>, ValidateDocumentWorkspaceEvidenceSnapshotMutationVariables> = (props) => {
          const {workspaceId,snapshotId} = props ?? {};

          return  validateDocumentWorkspaceEvidenceSnapshot(workspaceId,snapshotId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ValidateDocumentWorkspaceEvidenceSnapshotMutationResult = NonNullable<Awaited<ReturnType<typeof validateDocumentWorkspaceEvidenceSnapshot>>>

    export type ValidateDocumentWorkspaceEvidenceSnapshotMutationError = Error
    export type ValidateDocumentWorkspaceEvidenceSnapshotMutationVariables = {workspaceId: string;snapshotId: string}

    export const useValidateDocumentWorkspaceEvidenceSnapshot = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof validateDocumentWorkspaceEvidenceSnapshot>>, TError,ValidateDocumentWorkspaceEvidenceSnapshotMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof validateDocumentWorkspaceEvidenceSnapshot>>,
        TError,
        ValidateDocumentWorkspaceEvidenceSnapshotMutationVariables,
        TContext
      > => {
      return useMutation(getValidateDocumentWorkspaceEvidenceSnapshotMutationOptions(options));
    }

export const getOpenDocumentWorkspaceEvidenceCitationUrl = (workspaceId: string,
    snapshotId: string,
    citationId: string,) => {




  return `/api/document-workspaces/${workspaceId}/evidence-snapshots/${snapshotId}/citations/${citationId}`
}

export const openDocumentWorkspaceEvidenceCitation = async (workspaceId: string,
    snapshotId: string,
    citationId: string, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceEvidenceCitation> => {

  return customFetch<DocumentWorkspaceEvidenceCitation>(getOpenDocumentWorkspaceEvidenceCitationUrl(workspaceId,snapshotId,citationId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getOpenDocumentWorkspaceEvidenceCitationQueryKey = (workspaceId: string,
    snapshotId: string,
    citationId: string,) => {
    return [
    `/api/document-workspaces/${workspaceId}/evidence-snapshots/${snapshotId}/citations/${citationId}`
    ] as const;
    }


export const getOpenDocumentWorkspaceEvidenceCitationQueryOptions = <TData = Awaited<ReturnType<typeof openDocumentWorkspaceEvidenceCitation>>, TError = Error>(workspaceId: string,
    snapshotId: string,
    citationId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof openDocumentWorkspaceEvidenceCitation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getOpenDocumentWorkspaceEvidenceCitationQueryKey(workspaceId,snapshotId,citationId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof openDocumentWorkspaceEvidenceCitation>>> = ({ signal }) => openDocumentWorkspaceEvidenceCitation(workspaceId,snapshotId,citationId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && snapshotId !== null && snapshotId !== undefined && citationId !== null && citationId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof openDocumentWorkspaceEvidenceCitation>>, TError, TData> & { queryKey: QueryKey }
}

export type OpenDocumentWorkspaceEvidenceCitationQueryResult = NonNullable<Awaited<ReturnType<typeof openDocumentWorkspaceEvidenceCitation>>>
export type OpenDocumentWorkspaceEvidenceCitationQueryError = Error



export function useOpenDocumentWorkspaceEvidenceCitation<TData = Awaited<ReturnType<typeof openDocumentWorkspaceEvidenceCitation>>, TError = Error>(
 workspaceId: string,
    snapshotId: string,
    citationId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof openDocumentWorkspaceEvidenceCitation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getOpenDocumentWorkspaceEvidenceCitationQueryOptions(workspaceId,snapshotId,citationId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateLibraryRecordItemUrl = () => {




  return `/api/library/record-creations`
}

export const createLibraryRecordItem = async (createLibraryRecordItemBody: CreateLibraryRecordItem, options?: Parameters<typeof customFetch>[1]): Promise<LibraryRecordCreationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<LibraryRecordCreationResult>(getCreateLibraryRecordItemUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createLibraryRecordItemBody)
  }
);}





export const getCreateLibraryRecordItemMutationKey = () => ['createLibraryRecordItem'] as const;

export const getCreateLibraryRecordItemMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createLibraryRecordItem>>, TError,CreateLibraryRecordItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createLibraryRecordItem>>, TError,CreateLibraryRecordItemMutationVariables, TContext> => {

const mutationKey = getCreateLibraryRecordItemMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createLibraryRecordItem>>, CreateLibraryRecordItemMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createLibraryRecordItem(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateLibraryRecordItemMutationResult = NonNullable<Awaited<ReturnType<typeof createLibraryRecordItem>>>
    export type CreateLibraryRecordItemMutationBody = CreateLibraryRecordItem
    export type CreateLibraryRecordItemMutationError = void
    export type CreateLibraryRecordItemMutationVariables = {data: CreateLibraryRecordItem}

    export const useCreateLibraryRecordItem = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createLibraryRecordItem>>, TError,CreateLibraryRecordItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createLibraryRecordItem>>,
        TError,
        CreateLibraryRecordItemMutationVariables,
        TContext
      > => {
      return useMutation(getCreateLibraryRecordItemMutationOptions(options));
    }

export const getGetLibraryRecordCreationUrl = (requestId: string,) => {




  return `/api/library/record-creations/${requestId}`
}

export const getLibraryRecordCreation = async (requestId: string, options?: Parameters<typeof customFetch>[1]): Promise<LibraryRecordCreationRecovery> => {

  return customFetch<LibraryRecordCreationRecovery>(getGetLibraryRecordCreationUrl(requestId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetLibraryRecordCreationQueryKey = (requestId: string,) => {
    return [
    `/api/library/record-creations/${requestId}`
    ] as const;
    }


export const getGetLibraryRecordCreationQueryOptions = <TData = Awaited<ReturnType<typeof getLibraryRecordCreation>>, TError = void>(requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLibraryRecordCreation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetLibraryRecordCreationQueryKey(requestId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getLibraryRecordCreation>>> = ({ signal }) => getLibraryRecordCreation(requestId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: requestId !== null && requestId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getLibraryRecordCreation>>, TError, TData> & { queryKey: QueryKey }
}

export type GetLibraryRecordCreationQueryResult = NonNullable<Awaited<ReturnType<typeof getLibraryRecordCreation>>>
export type GetLibraryRecordCreationQueryError = void



export function useGetLibraryRecordCreation<TData = Awaited<ReturnType<typeof getLibraryRecordCreation>>, TError = void>(
 requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLibraryRecordCreation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetLibraryRecordCreationQueryOptions(requestId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewWorkspaceMarkdownUrl = (workspaceId: string,) => {




  return `/api/document-workspaces/${workspaceId}/markdown-reviews`
}

export const reviewWorkspaceMarkdown = async (workspaceId: string,
    markdownDraftReview: MarkdownDraftReview, options?: Parameters<typeof customFetch>[1]): Promise<MarkdownDraftReviewResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MarkdownDraftReviewResult>(getReviewWorkspaceMarkdownUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(markdownDraftReview)
  }
);}





export const getReviewWorkspaceMarkdownMutationKey = () => ['reviewWorkspaceMarkdown'] as const;

export const getReviewWorkspaceMarkdownMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewWorkspaceMarkdown>>, TError,ReviewWorkspaceMarkdownMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewWorkspaceMarkdown>>, TError,ReviewWorkspaceMarkdownMutationVariables, TContext> => {

const mutationKey = getReviewWorkspaceMarkdownMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewWorkspaceMarkdown>>, ReviewWorkspaceMarkdownMutationVariables> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  reviewWorkspaceMarkdown(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewWorkspaceMarkdownMutationResult = NonNullable<Awaited<ReturnType<typeof reviewWorkspaceMarkdown>>>
    export type ReviewWorkspaceMarkdownMutationBody = MarkdownDraftReview
    export type ReviewWorkspaceMarkdownMutationError = void
    export type ReviewWorkspaceMarkdownMutationVariables = {workspaceId: string;data: MarkdownDraftReview}

    export const useReviewWorkspaceMarkdown = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewWorkspaceMarkdown>>, TError,ReviewWorkspaceMarkdownMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewWorkspaceMarkdown>>,
        TError,
        ReviewWorkspaceMarkdownMutationVariables,
        TContext
      > => {
      return useMutation(getReviewWorkspaceMarkdownMutationOptions(options));
    }

export const getPreviewMarkdownConversionUrl = (workspaceId: string,) => {




  return `/api/document-workspaces/${workspaceId}/markdown-conversions/preview`
}

export const previewMarkdownConversion = async (workspaceId: string,
    markdownConversionReview: MarkdownConversionReview, options?: Parameters<typeof customFetch>[1]): Promise<MarkdownConversionPreview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MarkdownConversionPreview>(getPreviewMarkdownConversionUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(markdownConversionReview)
  }
);}





export const getPreviewMarkdownConversionMutationKey = () => ['previewMarkdownConversion'] as const;

export const getPreviewMarkdownConversionMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewMarkdownConversion>>, TError,PreviewMarkdownConversionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewMarkdownConversion>>, TError,PreviewMarkdownConversionMutationVariables, TContext> => {

const mutationKey = getPreviewMarkdownConversionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewMarkdownConversion>>, PreviewMarkdownConversionMutationVariables> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  previewMarkdownConversion(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewMarkdownConversionMutationResult = NonNullable<Awaited<ReturnType<typeof previewMarkdownConversion>>>
    export type PreviewMarkdownConversionMutationBody = MarkdownConversionReview
    export type PreviewMarkdownConversionMutationError = void
    export type PreviewMarkdownConversionMutationVariables = {workspaceId: string;data: MarkdownConversionReview}

    export const usePreviewMarkdownConversion = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewMarkdownConversion>>, TError,PreviewMarkdownConversionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewMarkdownConversion>>,
        TError,
        PreviewMarkdownConversionMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewMarkdownConversionMutationOptions(options));
    }

export const getConfirmMarkdownConversionUrl = (workspaceId: string,) => {




  return `/api/document-workspaces/${workspaceId}/markdown-conversions`
}

export const confirmMarkdownConversion = async (workspaceId: string,
    markdownConversionConfirm: MarkdownConversionConfirm, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceDocumentResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentWorkspaceDocumentResult>(getConfirmMarkdownConversionUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(markdownConversionConfirm)
  }
);}





export const getConfirmMarkdownConversionMutationKey = () => ['confirmMarkdownConversion'] as const;

export const getConfirmMarkdownConversionMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmMarkdownConversion>>, TError,ConfirmMarkdownConversionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmMarkdownConversion>>, TError,ConfirmMarkdownConversionMutationVariables, TContext> => {

const mutationKey = getConfirmMarkdownConversionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmMarkdownConversion>>, ConfirmMarkdownConversionMutationVariables> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  confirmMarkdownConversion(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmMarkdownConversionMutationResult = NonNullable<Awaited<ReturnType<typeof confirmMarkdownConversion>>>
    export type ConfirmMarkdownConversionMutationBody = MarkdownConversionConfirm
    export type ConfirmMarkdownConversionMutationError = void
    export type ConfirmMarkdownConversionMutationVariables = {workspaceId: string;data: MarkdownConversionConfirm}

    export const useConfirmMarkdownConversion = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmMarkdownConversion>>, TError,ConfirmMarkdownConversionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmMarkdownConversion>>,
        TError,
        ConfirmMarkdownConversionMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmMarkdownConversionMutationOptions(options));
    }

export const getGetMarkdownConversionOperationUrl = (workspaceId: string,
    requestId: string,) => {




  return `/api/document-workspaces/${workspaceId}/markdown-conversions/${requestId}`
}

export const getMarkdownConversionOperation = async (workspaceId: string,
    requestId: string, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceDocumentRecovery> => {

  return customFetch<DocumentWorkspaceDocumentRecovery>(getGetMarkdownConversionOperationUrl(workspaceId,requestId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMarkdownConversionOperationQueryKey = (workspaceId: string,
    requestId: string,) => {
    return [
    `/api/document-workspaces/${workspaceId}/markdown-conversions/${requestId}`
    ] as const;
    }


export const getGetMarkdownConversionOperationQueryOptions = <TData = Awaited<ReturnType<typeof getMarkdownConversionOperation>>, TError = void>(workspaceId: string,
    requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMarkdownConversionOperation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMarkdownConversionOperationQueryKey(workspaceId,requestId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMarkdownConversionOperation>>> = ({ signal }) => getMarkdownConversionOperation(workspaceId,requestId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && requestId !== null && requestId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMarkdownConversionOperation>>, TError, TData> & { queryKey: QueryKey }
}

export type GetMarkdownConversionOperationQueryResult = NonNullable<Awaited<ReturnType<typeof getMarkdownConversionOperation>>>
export type GetMarkdownConversionOperationQueryError = void



export function useGetMarkdownConversionOperation<TData = Awaited<ReturnType<typeof getMarkdownConversionOperation>>, TError = void>(
 workspaceId: string,
    requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMarkdownConversionOperation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetMarkdownConversionOperationQueryOptions(workspaceId,requestId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateDocumentWorkspaceDocumentUrl = (workspaceId: string,) => {




  return `/api/document-workspaces/${workspaceId}/documents`
}

export const createDocumentWorkspaceDocument = async (workspaceId: string,
    createDocumentWorkspaceDocumentBody: CreateDocumentWorkspaceDocument, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceDocumentResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentWorkspaceDocumentResult>(getCreateDocumentWorkspaceDocumentUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createDocumentWorkspaceDocumentBody)
  }
);}





export const getCreateDocumentWorkspaceDocumentMutationKey = () => ['createDocumentWorkspaceDocument'] as const;

export const getCreateDocumentWorkspaceDocumentMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDocumentWorkspaceDocument>>, TError,CreateDocumentWorkspaceDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createDocumentWorkspaceDocument>>, TError,CreateDocumentWorkspaceDocumentMutationVariables, TContext> => {

const mutationKey = getCreateDocumentWorkspaceDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createDocumentWorkspaceDocument>>, CreateDocumentWorkspaceDocumentMutationVariables> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  createDocumentWorkspaceDocument(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateDocumentWorkspaceDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof createDocumentWorkspaceDocument>>>
    export type CreateDocumentWorkspaceDocumentMutationBody = CreateDocumentWorkspaceDocument
    export type CreateDocumentWorkspaceDocumentMutationError = void
    export type CreateDocumentWorkspaceDocumentMutationVariables = {workspaceId: string;data: CreateDocumentWorkspaceDocument}

    export const useCreateDocumentWorkspaceDocument = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDocumentWorkspaceDocument>>, TError,CreateDocumentWorkspaceDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createDocumentWorkspaceDocument>>,
        TError,
        CreateDocumentWorkspaceDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getCreateDocumentWorkspaceDocumentMutationOptions(options));
    }

export const getGetDocumentWorkspaceDocumentOperationUrl = (workspaceId: string,
    requestId: string,) => {




  return `/api/document-workspaces/${workspaceId}/documents/${requestId}`
}

export const getDocumentWorkspaceDocumentOperation = async (workspaceId: string,
    requestId: string, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceDocumentRecovery> => {

  return customFetch<DocumentWorkspaceDocumentRecovery>(getGetDocumentWorkspaceDocumentOperationUrl(workspaceId,requestId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDocumentWorkspaceDocumentOperationQueryKey = (workspaceId: string,
    requestId: string,) => {
    return [
    `/api/document-workspaces/${workspaceId}/documents/${requestId}`
    ] as const;
    }


export const getGetDocumentWorkspaceDocumentOperationQueryOptions = <TData = Awaited<ReturnType<typeof getDocumentWorkspaceDocumentOperation>>, TError = void>(workspaceId: string,
    requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentWorkspaceDocumentOperation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDocumentWorkspaceDocumentOperationQueryKey(workspaceId,requestId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDocumentWorkspaceDocumentOperation>>> = ({ signal }) => getDocumentWorkspaceDocumentOperation(workspaceId,requestId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && requestId !== null && requestId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDocumentWorkspaceDocumentOperation>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDocumentWorkspaceDocumentOperationQueryResult = NonNullable<Awaited<ReturnType<typeof getDocumentWorkspaceDocumentOperation>>>
export type GetDocumentWorkspaceDocumentOperationQueryError = void



export function useGetDocumentWorkspaceDocumentOperation<TData = Awaited<ReturnType<typeof getDocumentWorkspaceDocumentOperation>>, TError = void>(
 workspaceId: string,
    requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentWorkspaceDocumentOperation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDocumentWorkspaceDocumentOperationQueryOptions(workspaceId,requestId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetDocumentWorkspaceUploadOperationUrl = (workspaceId: string,
    requestId: string,) => {




  return `/api/document-workspaces/${workspaceId}/uploads/${requestId}`
}

export const getDocumentWorkspaceUploadOperation = async (workspaceId: string,
    requestId: string, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceUploadRecovery> => {

  return customFetch<DocumentWorkspaceUploadRecovery>(getGetDocumentWorkspaceUploadOperationUrl(workspaceId,requestId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDocumentWorkspaceUploadOperationQueryKey = (workspaceId: string,
    requestId: string,) => {
    return [
    `/api/document-workspaces/${workspaceId}/uploads/${requestId}`
    ] as const;
    }


export const getGetDocumentWorkspaceUploadOperationQueryOptions = <TData = Awaited<ReturnType<typeof getDocumentWorkspaceUploadOperation>>, TError = void>(workspaceId: string,
    requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentWorkspaceUploadOperation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDocumentWorkspaceUploadOperationQueryKey(workspaceId,requestId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDocumentWorkspaceUploadOperation>>> = ({ signal }) => getDocumentWorkspaceUploadOperation(workspaceId,requestId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && requestId !== null && requestId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDocumentWorkspaceUploadOperation>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDocumentWorkspaceUploadOperationQueryResult = NonNullable<Awaited<ReturnType<typeof getDocumentWorkspaceUploadOperation>>>
export type GetDocumentWorkspaceUploadOperationQueryError = void



export function useGetDocumentWorkspaceUploadOperation<TData = Awaited<ReturnType<typeof getDocumentWorkspaceUploadOperation>>, TError = void>(
 workspaceId: string,
    requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentWorkspaceUploadOperation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDocumentWorkspaceUploadOperationQueryOptions(workspaceId,requestId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUploadDocumentWorkspaceFileUrl = (workspaceId: string,) => {




  return `/api/document-workspaces/${workspaceId}/uploads`
}

export const uploadDocumentWorkspaceFile = async (workspaceId: string,
    uploadDocumentWorkspaceFileBody: UploadDocumentWorkspaceFile, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceUploadResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentWorkspaceUploadResult>(getUploadDocumentWorkspaceFileUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(uploadDocumentWorkspaceFileBody)
  }
);}





export const getUploadDocumentWorkspaceFileMutationKey = () => ['uploadDocumentWorkspaceFile'] as const;

export const getUploadDocumentWorkspaceFileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadDocumentWorkspaceFile>>, TError,UploadDocumentWorkspaceFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadDocumentWorkspaceFile>>, TError,UploadDocumentWorkspaceFileMutationVariables, TContext> => {

const mutationKey = getUploadDocumentWorkspaceFileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadDocumentWorkspaceFile>>, UploadDocumentWorkspaceFileMutationVariables> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  uploadDocumentWorkspaceFile(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadDocumentWorkspaceFileMutationResult = NonNullable<Awaited<ReturnType<typeof uploadDocumentWorkspaceFile>>>
    export type UploadDocumentWorkspaceFileMutationBody = UploadDocumentWorkspaceFile
    export type UploadDocumentWorkspaceFileMutationError = Error
    export type UploadDocumentWorkspaceFileMutationVariables = {workspaceId: string;data: UploadDocumentWorkspaceFile}

    export const useUploadDocumentWorkspaceFile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadDocumentWorkspaceFile>>, TError,UploadDocumentWorkspaceFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadDocumentWorkspaceFile>>,
        TError,
        UploadDocumentWorkspaceFileMutationVariables,
        TContext
      > => {
      return useMutation(getUploadDocumentWorkspaceFileMutationOptions(options));
    }

export const getListDocumentWorkspaceSourceVersionsUrl = (workspaceId: string,
    membershipId: string,
    params?: ListDocumentWorkspaceSourceVersionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/document-workspaces/${workspaceId}/memberships/${membershipId}/versions?${stringifiedParams}` : `/api/document-workspaces/${workspaceId}/memberships/${membershipId}/versions`
}

export const listDocumentWorkspaceSourceVersions = async (workspaceId: string,
    membershipId: string,
    params?: ListDocumentWorkspaceSourceVersionsParams, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceVersionList> => {

  return customFetch<DocumentWorkspaceVersionList>(getListDocumentWorkspaceSourceVersionsUrl(workspaceId,membershipId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDocumentWorkspaceSourceVersionsQueryKey = (workspaceId: string,
    membershipId: string,
    params?: ListDocumentWorkspaceSourceVersionsParams,) => {
    return [
    `/api/document-workspaces/${workspaceId}/memberships/${membershipId}/versions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListDocumentWorkspaceSourceVersionsQueryOptions = <TData = Awaited<ReturnType<typeof listDocumentWorkspaceSourceVersions>>, TError = Error>(workspaceId: string,
    membershipId: string,
    params?: ListDocumentWorkspaceSourceVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentWorkspaceSourceVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDocumentWorkspaceSourceVersionsQueryKey(workspaceId,membershipId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDocumentWorkspaceSourceVersions>>> = ({ signal }) => listDocumentWorkspaceSourceVersions(workspaceId,membershipId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && membershipId !== null && membershipId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDocumentWorkspaceSourceVersions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListDocumentWorkspaceSourceVersionsQueryResult = NonNullable<Awaited<ReturnType<typeof listDocumentWorkspaceSourceVersions>>>
export type ListDocumentWorkspaceSourceVersionsQueryError = Error



export function useListDocumentWorkspaceSourceVersions<TData = Awaited<ReturnType<typeof listDocumentWorkspaceSourceVersions>>, TError = Error>(
 workspaceId: string,
    membershipId: string,
    params?: ListDocumentWorkspaceSourceVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentWorkspaceSourceVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListDocumentWorkspaceSourceVersionsQueryOptions(workspaceId,membershipId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPrepareDocumentWorkspaceSourceUrl = (workspaceId: string,
    membershipId: string,) => {




  return `/api/document-workspaces/${workspaceId}/memberships/${membershipId}/versions`
}

export const prepareDocumentWorkspaceSource = async (workspaceId: string,
    membershipId: string,
    prepareDocumentWorkspaceSourceBody: PrepareDocumentWorkspaceSource, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceSourceState> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentWorkspaceSourceState>(getPrepareDocumentWorkspaceSourceUrl(workspaceId,membershipId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(prepareDocumentWorkspaceSourceBody)
  }
);}





export const getPrepareDocumentWorkspaceSourceMutationKey = () => ['prepareDocumentWorkspaceSource'] as const;

export const getPrepareDocumentWorkspaceSourceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof prepareDocumentWorkspaceSource>>, TError,PrepareDocumentWorkspaceSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof prepareDocumentWorkspaceSource>>, TError,PrepareDocumentWorkspaceSourceMutationVariables, TContext> => {

const mutationKey = getPrepareDocumentWorkspaceSourceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof prepareDocumentWorkspaceSource>>, PrepareDocumentWorkspaceSourceMutationVariables> = (props) => {
          const {workspaceId,membershipId,data} = props ?? {};

          return  prepareDocumentWorkspaceSource(workspaceId,membershipId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PrepareDocumentWorkspaceSourceMutationResult = NonNullable<Awaited<ReturnType<typeof prepareDocumentWorkspaceSource>>>
    export type PrepareDocumentWorkspaceSourceMutationBody = PrepareDocumentWorkspaceSource
    export type PrepareDocumentWorkspaceSourceMutationError = Error
    export type PrepareDocumentWorkspaceSourceMutationVariables = {workspaceId: string;membershipId: string;data: PrepareDocumentWorkspaceSource}

    export const usePrepareDocumentWorkspaceSource = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof prepareDocumentWorkspaceSource>>, TError,PrepareDocumentWorkspaceSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof prepareDocumentWorkspaceSource>>,
        TError,
        PrepareDocumentWorkspaceSourceMutationVariables,
        TContext
      > => {
      return useMutation(getPrepareDocumentWorkspaceSourceMutationOptions(options));
    }

export const getDownloadDocumentWorkspaceSourceVersionUrl = (workspaceId: string,
    membershipId: string,
    sourceRevisionId: string,) => {




  return `/api/document-workspaces/${workspaceId}/memberships/${membershipId}/versions/${sourceRevisionId}/content`
}

export const downloadDocumentWorkspaceSourceVersion = async (workspaceId: string,
    membershipId: string,
    sourceRevisionId: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadDocumentWorkspaceSourceVersionUrl(workspaceId,membershipId,sourceRevisionId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadDocumentWorkspaceSourceVersionQueryKey = (workspaceId: string,
    membershipId: string,
    sourceRevisionId: string,) => {
    return [
    `/api/document-workspaces/${workspaceId}/memberships/${membershipId}/versions/${sourceRevisionId}/content`
    ] as const;
    }


export const getDownloadDocumentWorkspaceSourceVersionQueryOptions = <TData = Awaited<ReturnType<typeof downloadDocumentWorkspaceSourceVersion>>, TError = Error>(workspaceId: string,
    membershipId: string,
    sourceRevisionId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadDocumentWorkspaceSourceVersion>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadDocumentWorkspaceSourceVersionQueryKey(workspaceId,membershipId,sourceRevisionId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadDocumentWorkspaceSourceVersion>>> = ({ signal }) => downloadDocumentWorkspaceSourceVersion(workspaceId,membershipId,sourceRevisionId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined && membershipId !== null && membershipId !== undefined && sourceRevisionId !== null && sourceRevisionId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadDocumentWorkspaceSourceVersion>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadDocumentWorkspaceSourceVersionQueryResult = NonNullable<Awaited<ReturnType<typeof downloadDocumentWorkspaceSourceVersion>>>
export type DownloadDocumentWorkspaceSourceVersionQueryError = Error



export function useDownloadDocumentWorkspaceSourceVersion<TData = Awaited<ReturnType<typeof downloadDocumentWorkspaceSourceVersion>>, TError = Error>(
 workspaceId: string,
    membershipId: string,
    sourceRevisionId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadDocumentWorkspaceSourceVersion>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadDocumentWorkspaceSourceVersionQueryOptions(workspaceId,membershipId,sourceRevisionId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getInitializeDocumentWorkspaceSourcesUrl = (workspaceId: string,) => {




  return `/api/document-workspaces/${workspaceId}/initialize-sources`
}

export const initializeDocumentWorkspaceSources = async (workspaceId: string,
    documentWorkspaceMutation: DocumentWorkspaceMutation, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceMembershipList> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentWorkspaceMembershipList>(getInitializeDocumentWorkspaceSourcesUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(documentWorkspaceMutation)
  }
);}





export const getInitializeDocumentWorkspaceSourcesMutationKey = () => ['initializeDocumentWorkspaceSources'] as const;

export const getInitializeDocumentWorkspaceSourcesMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof initializeDocumentWorkspaceSources>>, TError,InitializeDocumentWorkspaceSourcesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof initializeDocumentWorkspaceSources>>, TError,InitializeDocumentWorkspaceSourcesMutationVariables, TContext> => {

const mutationKey = getInitializeDocumentWorkspaceSourcesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof initializeDocumentWorkspaceSources>>, InitializeDocumentWorkspaceSourcesMutationVariables> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  initializeDocumentWorkspaceSources(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InitializeDocumentWorkspaceSourcesMutationResult = NonNullable<Awaited<ReturnType<typeof initializeDocumentWorkspaceSources>>>
    export type InitializeDocumentWorkspaceSourcesMutationBody = DocumentWorkspaceMutation
    export type InitializeDocumentWorkspaceSourcesMutationError = Error
    export type InitializeDocumentWorkspaceSourcesMutationVariables = {workspaceId: string;data: DocumentWorkspaceMutation}

    export const useInitializeDocumentWorkspaceSources = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof initializeDocumentWorkspaceSources>>, TError,InitializeDocumentWorkspaceSourcesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof initializeDocumentWorkspaceSources>>,
        TError,
        InitializeDocumentWorkspaceSourcesMutationVariables,
        TContext
      > => {
      return useMutation(getInitializeDocumentWorkspaceSourcesMutationOptions(options));
    }

export const getListDocumentWorkspacesUrl = (params?: ListDocumentWorkspacesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/document-workspaces?${stringifiedParams}` : `/api/document-workspaces`
}

export const listDocumentWorkspaces = async (params?: ListDocumentWorkspacesParams, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceList> => {

  return customFetch<DocumentWorkspaceList>(getListDocumentWorkspacesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDocumentWorkspacesQueryKey = (params?: ListDocumentWorkspacesParams,) => {
    return [
    `/api/document-workspaces`, ...(params ? [params] : [])
    ] as const;
    }


export const getListDocumentWorkspacesQueryOptions = <TData = Awaited<ReturnType<typeof listDocumentWorkspaces>>, TError = Error>(params?: ListDocumentWorkspacesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentWorkspaces>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDocumentWorkspacesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDocumentWorkspaces>>> = ({ signal }) => listDocumentWorkspaces(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDocumentWorkspaces>>, TError, TData> & { queryKey: QueryKey }
}

export type ListDocumentWorkspacesQueryResult = NonNullable<Awaited<ReturnType<typeof listDocumentWorkspaces>>>
export type ListDocumentWorkspacesQueryError = Error



export function useListDocumentWorkspaces<TData = Awaited<ReturnType<typeof listDocumentWorkspaces>>, TError = Error>(
 params?: ListDocumentWorkspacesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentWorkspaces>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListDocumentWorkspacesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateDocumentWorkspaceUrl = () => {




  return `/api/document-workspaces`
}

export const createDocumentWorkspace = async (createDocumentWorkspaceBody: CreateDocumentWorkspace, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspace> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentWorkspace>(getCreateDocumentWorkspaceUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createDocumentWorkspaceBody)
  }
);}





export const getCreateDocumentWorkspaceMutationKey = () => ['createDocumentWorkspace'] as const;

export const getCreateDocumentWorkspaceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDocumentWorkspace>>, TError,CreateDocumentWorkspaceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createDocumentWorkspace>>, TError,CreateDocumentWorkspaceMutationVariables, TContext> => {

const mutationKey = getCreateDocumentWorkspaceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createDocumentWorkspace>>, CreateDocumentWorkspaceMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createDocumentWorkspace(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateDocumentWorkspaceMutationResult = NonNullable<Awaited<ReturnType<typeof createDocumentWorkspace>>>
    export type CreateDocumentWorkspaceMutationBody = CreateDocumentWorkspace
    export type CreateDocumentWorkspaceMutationError = Error
    export type CreateDocumentWorkspaceMutationVariables = {data: CreateDocumentWorkspace}

    export const useCreateDocumentWorkspace = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDocumentWorkspace>>, TError,CreateDocumentWorkspaceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createDocumentWorkspace>>,
        TError,
        CreateDocumentWorkspaceMutationVariables,
        TContext
      > => {
      return useMutation(getCreateDocumentWorkspaceMutationOptions(options));
    }

export const getGetDocumentWorkspaceUrl = (workspaceId: string,) => {




  return `/api/document-workspaces/${workspaceId}`
}

export const getDocumentWorkspace = async (workspaceId: string, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspace> => {

  return customFetch<DocumentWorkspace>(getGetDocumentWorkspaceUrl(workspaceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDocumentWorkspaceQueryKey = (workspaceId: string,) => {
    return [
    `/api/document-workspaces/${workspaceId}`
    ] as const;
    }


export const getGetDocumentWorkspaceQueryOptions = <TData = Awaited<ReturnType<typeof getDocumentWorkspace>>, TError = Error>(workspaceId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentWorkspace>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDocumentWorkspaceQueryKey(workspaceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDocumentWorkspace>>> = ({ signal }) => getDocumentWorkspace(workspaceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDocumentWorkspace>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDocumentWorkspaceQueryResult = NonNullable<Awaited<ReturnType<typeof getDocumentWorkspace>>>
export type GetDocumentWorkspaceQueryError = Error



export function useGetDocumentWorkspace<TData = Awaited<ReturnType<typeof getDocumentWorkspace>>, TError = Error>(
 workspaceId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentWorkspace>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDocumentWorkspaceQueryOptions(workspaceId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListDocumentWorkspaceFoldersUrl = (workspaceId: string,
    params?: ListDocumentWorkspaceFoldersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/document-workspaces/${workspaceId}/folders?${stringifiedParams}` : `/api/document-workspaces/${workspaceId}/folders`
}

export const listDocumentWorkspaceFolders = async (workspaceId: string,
    params?: ListDocumentWorkspaceFoldersParams, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceFolderList> => {

  return customFetch<DocumentWorkspaceFolderList>(getListDocumentWorkspaceFoldersUrl(workspaceId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDocumentWorkspaceFoldersQueryKey = (workspaceId: string,
    params?: ListDocumentWorkspaceFoldersParams,) => {
    return [
    `/api/document-workspaces/${workspaceId}/folders`, ...(params ? [params] : [])
    ] as const;
    }


export const getListDocumentWorkspaceFoldersQueryOptions = <TData = Awaited<ReturnType<typeof listDocumentWorkspaceFolders>>, TError = Error>(workspaceId: string,
    params?: ListDocumentWorkspaceFoldersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentWorkspaceFolders>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDocumentWorkspaceFoldersQueryKey(workspaceId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDocumentWorkspaceFolders>>> = ({ signal }) => listDocumentWorkspaceFolders(workspaceId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDocumentWorkspaceFolders>>, TError, TData> & { queryKey: QueryKey }
}

export type ListDocumentWorkspaceFoldersQueryResult = NonNullable<Awaited<ReturnType<typeof listDocumentWorkspaceFolders>>>
export type ListDocumentWorkspaceFoldersQueryError = Error



export function useListDocumentWorkspaceFolders<TData = Awaited<ReturnType<typeof listDocumentWorkspaceFolders>>, TError = Error>(
 workspaceId: string,
    params?: ListDocumentWorkspaceFoldersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentWorkspaceFolders>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListDocumentWorkspaceFoldersQueryOptions(workspaceId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateDocumentWorkspaceFolderUrl = (workspaceId: string,) => {




  return `/api/document-workspaces/${workspaceId}/folders`
}

export const createDocumentWorkspaceFolder = async (workspaceId: string,
    createDocumentWorkspaceFolderBody: CreateDocumentWorkspaceFolder, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceFolder> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentWorkspaceFolder>(getCreateDocumentWorkspaceFolderUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createDocumentWorkspaceFolderBody)
  }
);}





export const getCreateDocumentWorkspaceFolderMutationKey = () => ['createDocumentWorkspaceFolder'] as const;

export const getCreateDocumentWorkspaceFolderMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDocumentWorkspaceFolder>>, TError,CreateDocumentWorkspaceFolderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createDocumentWorkspaceFolder>>, TError,CreateDocumentWorkspaceFolderMutationVariables, TContext> => {

const mutationKey = getCreateDocumentWorkspaceFolderMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createDocumentWorkspaceFolder>>, CreateDocumentWorkspaceFolderMutationVariables> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  createDocumentWorkspaceFolder(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateDocumentWorkspaceFolderMutationResult = NonNullable<Awaited<ReturnType<typeof createDocumentWorkspaceFolder>>>
    export type CreateDocumentWorkspaceFolderMutationBody = CreateDocumentWorkspaceFolder
    export type CreateDocumentWorkspaceFolderMutationError = Error
    export type CreateDocumentWorkspaceFolderMutationVariables = {workspaceId: string;data: CreateDocumentWorkspaceFolder}

    export const useCreateDocumentWorkspaceFolder = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDocumentWorkspaceFolder>>, TError,CreateDocumentWorkspaceFolderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createDocumentWorkspaceFolder>>,
        TError,
        CreateDocumentWorkspaceFolderMutationVariables,
        TContext
      > => {
      return useMutation(getCreateDocumentWorkspaceFolderMutationOptions(options));
    }

export const getUpdateDocumentWorkspaceFolderUrl = (workspaceId: string,
    folderId: string,) => {




  return `/api/document-workspaces/${workspaceId}/folders/${folderId}`
}

export const updateDocumentWorkspaceFolder = async (workspaceId: string,
    folderId: string,
    updateDocumentWorkspaceFolderBody: UpdateDocumentWorkspaceFolder, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceFolder> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentWorkspaceFolder>(getUpdateDocumentWorkspaceFolderUrl(workspaceId,folderId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateDocumentWorkspaceFolderBody)
  }
);}





export const getUpdateDocumentWorkspaceFolderMutationKey = () => ['updateDocumentWorkspaceFolder'] as const;

export const getUpdateDocumentWorkspaceFolderMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDocumentWorkspaceFolder>>, TError,UpdateDocumentWorkspaceFolderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateDocumentWorkspaceFolder>>, TError,UpdateDocumentWorkspaceFolderMutationVariables, TContext> => {

const mutationKey = getUpdateDocumentWorkspaceFolderMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateDocumentWorkspaceFolder>>, UpdateDocumentWorkspaceFolderMutationVariables> = (props) => {
          const {workspaceId,folderId,data} = props ?? {};

          return  updateDocumentWorkspaceFolder(workspaceId,folderId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateDocumentWorkspaceFolderMutationResult = NonNullable<Awaited<ReturnType<typeof updateDocumentWorkspaceFolder>>>
    export type UpdateDocumentWorkspaceFolderMutationBody = UpdateDocumentWorkspaceFolder
    export type UpdateDocumentWorkspaceFolderMutationError = Error
    export type UpdateDocumentWorkspaceFolderMutationVariables = {workspaceId: string;folderId: string;data: UpdateDocumentWorkspaceFolder}

    export const useUpdateDocumentWorkspaceFolder = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDocumentWorkspaceFolder>>, TError,UpdateDocumentWorkspaceFolderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateDocumentWorkspaceFolder>>,
        TError,
        UpdateDocumentWorkspaceFolderMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateDocumentWorkspaceFolderMutationOptions(options));
    }

export const getRemoveDocumentWorkspaceFolderUrl = (workspaceId: string,
    folderId: string,) => {




  return `/api/document-workspaces/${workspaceId}/folders/${folderId}`
}

export const removeDocumentWorkspaceFolder = async (workspaceId: string,
    folderId: string,
    removeDocumentWorkspaceFolderBody: RemoveDocumentWorkspaceFolder, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspace> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentWorkspace>(getRemoveDocumentWorkspaceFolderUrl(workspaceId,folderId),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(removeDocumentWorkspaceFolderBody)
  }
);}





export const getRemoveDocumentWorkspaceFolderMutationKey = () => ['removeDocumentWorkspaceFolder'] as const;

export const getRemoveDocumentWorkspaceFolderMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeDocumentWorkspaceFolder>>, TError,RemoveDocumentWorkspaceFolderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof removeDocumentWorkspaceFolder>>, TError,RemoveDocumentWorkspaceFolderMutationVariables, TContext> => {

const mutationKey = getRemoveDocumentWorkspaceFolderMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof removeDocumentWorkspaceFolder>>, RemoveDocumentWorkspaceFolderMutationVariables> = (props) => {
          const {workspaceId,folderId,data} = props ?? {};

          return  removeDocumentWorkspaceFolder(workspaceId,folderId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RemoveDocumentWorkspaceFolderMutationResult = NonNullable<Awaited<ReturnType<typeof removeDocumentWorkspaceFolder>>>
    export type RemoveDocumentWorkspaceFolderMutationBody = RemoveDocumentWorkspaceFolder
    export type RemoveDocumentWorkspaceFolderMutationError = Error
    export type RemoveDocumentWorkspaceFolderMutationVariables = {workspaceId: string;folderId: string;data: RemoveDocumentWorkspaceFolder}

    export const useRemoveDocumentWorkspaceFolder = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeDocumentWorkspaceFolder>>, TError,RemoveDocumentWorkspaceFolderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof removeDocumentWorkspaceFolder>>,
        TError,
        RemoveDocumentWorkspaceFolderMutationVariables,
        TContext
      > => {
      return useMutation(getRemoveDocumentWorkspaceFolderMutationOptions(options));
    }

export const getListDocumentWorkspaceMembershipsUrl = (workspaceId: string,
    params?: ListDocumentWorkspaceMembershipsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/document-workspaces/${workspaceId}/memberships?${stringifiedParams}` : `/api/document-workspaces/${workspaceId}/memberships`
}

export const listDocumentWorkspaceMemberships = async (workspaceId: string,
    params?: ListDocumentWorkspaceMembershipsParams, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceMembershipList> => {

  return customFetch<DocumentWorkspaceMembershipList>(getListDocumentWorkspaceMembershipsUrl(workspaceId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDocumentWorkspaceMembershipsQueryKey = (workspaceId: string,
    params?: ListDocumentWorkspaceMembershipsParams,) => {
    return [
    `/api/document-workspaces/${workspaceId}/memberships`, ...(params ? [params] : [])
    ] as const;
    }


export const getListDocumentWorkspaceMembershipsQueryOptions = <TData = Awaited<ReturnType<typeof listDocumentWorkspaceMemberships>>, TError = Error>(workspaceId: string,
    params?: ListDocumentWorkspaceMembershipsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentWorkspaceMemberships>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDocumentWorkspaceMembershipsQueryKey(workspaceId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDocumentWorkspaceMemberships>>> = ({ signal }) => listDocumentWorkspaceMemberships(workspaceId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDocumentWorkspaceMemberships>>, TError, TData> & { queryKey: QueryKey }
}

export type ListDocumentWorkspaceMembershipsQueryResult = NonNullable<Awaited<ReturnType<typeof listDocumentWorkspaceMemberships>>>
export type ListDocumentWorkspaceMembershipsQueryError = Error



export function useListDocumentWorkspaceMemberships<TData = Awaited<ReturnType<typeof listDocumentWorkspaceMemberships>>, TError = Error>(
 workspaceId: string,
    params?: ListDocumentWorkspaceMembershipsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentWorkspaceMemberships>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListDocumentWorkspaceMembershipsQueryOptions(workspaceId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateDocumentWorkspaceMembershipUrl = (workspaceId: string,) => {




  return `/api/document-workspaces/${workspaceId}/memberships`
}

export const createDocumentWorkspaceMembership = async (workspaceId: string,
    createDocumentWorkspaceMembershipBody: CreateDocumentWorkspaceMembership, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceMembership> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentWorkspaceMembership>(getCreateDocumentWorkspaceMembershipUrl(workspaceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createDocumentWorkspaceMembershipBody)
  }
);}





export const getCreateDocumentWorkspaceMembershipMutationKey = () => ['createDocumentWorkspaceMembership'] as const;

export const getCreateDocumentWorkspaceMembershipMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDocumentWorkspaceMembership>>, TError,CreateDocumentWorkspaceMembershipMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createDocumentWorkspaceMembership>>, TError,CreateDocumentWorkspaceMembershipMutationVariables, TContext> => {

const mutationKey = getCreateDocumentWorkspaceMembershipMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createDocumentWorkspaceMembership>>, CreateDocumentWorkspaceMembershipMutationVariables> = (props) => {
          const {workspaceId,data} = props ?? {};

          return  createDocumentWorkspaceMembership(workspaceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateDocumentWorkspaceMembershipMutationResult = NonNullable<Awaited<ReturnType<typeof createDocumentWorkspaceMembership>>>
    export type CreateDocumentWorkspaceMembershipMutationBody = CreateDocumentWorkspaceMembership
    export type CreateDocumentWorkspaceMembershipMutationError = Error
    export type CreateDocumentWorkspaceMembershipMutationVariables = {workspaceId: string;data: CreateDocumentWorkspaceMembership}

    export const useCreateDocumentWorkspaceMembership = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDocumentWorkspaceMembership>>, TError,CreateDocumentWorkspaceMembershipMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createDocumentWorkspaceMembership>>,
        TError,
        CreateDocumentWorkspaceMembershipMutationVariables,
        TContext
      > => {
      return useMutation(getCreateDocumentWorkspaceMembershipMutationOptions(options));
    }

export const getUpdateDocumentWorkspaceMembershipUrl = (workspaceId: string,
    membershipId: string,) => {




  return `/api/document-workspaces/${workspaceId}/memberships/${membershipId}`
}

export const updateDocumentWorkspaceMembership = async (workspaceId: string,
    membershipId: string,
    updateDocumentWorkspaceMembershipBody: UpdateDocumentWorkspaceMembership, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceMembership> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentWorkspaceMembership>(getUpdateDocumentWorkspaceMembershipUrl(workspaceId,membershipId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateDocumentWorkspaceMembershipBody)
  }
);}





export const getUpdateDocumentWorkspaceMembershipMutationKey = () => ['updateDocumentWorkspaceMembership'] as const;

export const getUpdateDocumentWorkspaceMembershipMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDocumentWorkspaceMembership>>, TError,UpdateDocumentWorkspaceMembershipMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateDocumentWorkspaceMembership>>, TError,UpdateDocumentWorkspaceMembershipMutationVariables, TContext> => {

const mutationKey = getUpdateDocumentWorkspaceMembershipMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateDocumentWorkspaceMembership>>, UpdateDocumentWorkspaceMembershipMutationVariables> = (props) => {
          const {workspaceId,membershipId,data} = props ?? {};

          return  updateDocumentWorkspaceMembership(workspaceId,membershipId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateDocumentWorkspaceMembershipMutationResult = NonNullable<Awaited<ReturnType<typeof updateDocumentWorkspaceMembership>>>
    export type UpdateDocumentWorkspaceMembershipMutationBody = UpdateDocumentWorkspaceMembership
    export type UpdateDocumentWorkspaceMembershipMutationError = Error
    export type UpdateDocumentWorkspaceMembershipMutationVariables = {workspaceId: string;membershipId: string;data: UpdateDocumentWorkspaceMembership}

    export const useUpdateDocumentWorkspaceMembership = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDocumentWorkspaceMembership>>, TError,UpdateDocumentWorkspaceMembershipMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateDocumentWorkspaceMembership>>,
        TError,
        UpdateDocumentWorkspaceMembershipMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateDocumentWorkspaceMembershipMutationOptions(options));
    }

export const getUnlinkDocumentWorkspaceSourceUrl = (workspaceId: string,
    membershipId: string,) => {




  return `/api/document-workspaces/${workspaceId}/memberships/${membershipId}`
}

export const unlinkDocumentWorkspaceSource = async (workspaceId: string,
    membershipId: string,
    removeDocumentWorkspaceMembership: RemoveDocumentWorkspaceMembership, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspace> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentWorkspace>(getUnlinkDocumentWorkspaceSourceUrl(workspaceId,membershipId),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(removeDocumentWorkspaceMembership)
  }
);}





export const getUnlinkDocumentWorkspaceSourceMutationKey = () => ['unlinkDocumentWorkspaceSource'] as const;

export const getUnlinkDocumentWorkspaceSourceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unlinkDocumentWorkspaceSource>>, TError,UnlinkDocumentWorkspaceSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof unlinkDocumentWorkspaceSource>>, TError,UnlinkDocumentWorkspaceSourceMutationVariables, TContext> => {

const mutationKey = getUnlinkDocumentWorkspaceSourceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof unlinkDocumentWorkspaceSource>>, UnlinkDocumentWorkspaceSourceMutationVariables> = (props) => {
          const {workspaceId,membershipId,data} = props ?? {};

          return  unlinkDocumentWorkspaceSource(workspaceId,membershipId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UnlinkDocumentWorkspaceSourceMutationResult = NonNullable<Awaited<ReturnType<typeof unlinkDocumentWorkspaceSource>>>
    export type UnlinkDocumentWorkspaceSourceMutationBody = RemoveDocumentWorkspaceMembership
    export type UnlinkDocumentWorkspaceSourceMutationError = Error
    export type UnlinkDocumentWorkspaceSourceMutationVariables = {workspaceId: string;membershipId: string;data: RemoveDocumentWorkspaceMembership}

    export const useUnlinkDocumentWorkspaceSource = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unlinkDocumentWorkspaceSource>>, TError,UnlinkDocumentWorkspaceSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof unlinkDocumentWorkspaceSource>>,
        TError,
        UnlinkDocumentWorkspaceSourceMutationVariables,
        TContext
      > => {
      return useMutation(getUnlinkDocumentWorkspaceSourceMutationOptions(options));
    }

export const getSelectDocumentWorkspaceSourceUrl = (workspaceId: string,
    membershipId: string,) => {




  return `/api/document-workspaces/${workspaceId}/memberships/${membershipId}/selection`
}

export const selectDocumentWorkspaceSource = async (workspaceId: string,
    membershipId: string,
    selectDocumentWorkspaceSourceBody: SelectDocumentWorkspaceSource, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceMembership> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentWorkspaceMembership>(getSelectDocumentWorkspaceSourceUrl(workspaceId,membershipId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(selectDocumentWorkspaceSourceBody)
  }
);}





export const getSelectDocumentWorkspaceSourceMutationKey = () => ['selectDocumentWorkspaceSource'] as const;

export const getSelectDocumentWorkspaceSourceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof selectDocumentWorkspaceSource>>, TError,SelectDocumentWorkspaceSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof selectDocumentWorkspaceSource>>, TError,SelectDocumentWorkspaceSourceMutationVariables, TContext> => {

const mutationKey = getSelectDocumentWorkspaceSourceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof selectDocumentWorkspaceSource>>, SelectDocumentWorkspaceSourceMutationVariables> = (props) => {
          const {workspaceId,membershipId,data} = props ?? {};

          return  selectDocumentWorkspaceSource(workspaceId,membershipId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SelectDocumentWorkspaceSourceMutationResult = NonNullable<Awaited<ReturnType<typeof selectDocumentWorkspaceSource>>>
    export type SelectDocumentWorkspaceSourceMutationBody = SelectDocumentWorkspaceSource
    export type SelectDocumentWorkspaceSourceMutationError = Error
    export type SelectDocumentWorkspaceSourceMutationVariables = {workspaceId: string;membershipId: string;data: SelectDocumentWorkspaceSource}

    export const useSelectDocumentWorkspaceSource = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof selectDocumentWorkspaceSource>>, TError,SelectDocumentWorkspaceSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof selectDocumentWorkspaceSource>>,
        TError,
        SelectDocumentWorkspaceSourceMutationVariables,
        TContext
      > => {
      return useMutation(getSelectDocumentWorkspaceSourceMutationOptions(options));
    }

export const getRemoveDocumentWorkspaceEvidenceSelectionUrl = (workspaceId: string,
    membershipId: string,) => {




  return `/api/document-workspaces/${workspaceId}/memberships/${membershipId}/selection`
}

export const removeDocumentWorkspaceEvidenceSelection = async (workspaceId: string,
    membershipId: string,
    removeDocumentWorkspaceEvidenceSelectionBody: RemoveDocumentWorkspaceEvidenceSelection, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceMembership> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentWorkspaceMembership>(getRemoveDocumentWorkspaceEvidenceSelectionUrl(workspaceId,membershipId),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(removeDocumentWorkspaceEvidenceSelectionBody)
  }
);}





export const getRemoveDocumentWorkspaceEvidenceSelectionMutationKey = () => ['removeDocumentWorkspaceEvidenceSelection'] as const;

export const getRemoveDocumentWorkspaceEvidenceSelectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeDocumentWorkspaceEvidenceSelection>>, TError,RemoveDocumentWorkspaceEvidenceSelectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof removeDocumentWorkspaceEvidenceSelection>>, TError,RemoveDocumentWorkspaceEvidenceSelectionMutationVariables, TContext> => {

const mutationKey = getRemoveDocumentWorkspaceEvidenceSelectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof removeDocumentWorkspaceEvidenceSelection>>, RemoveDocumentWorkspaceEvidenceSelectionMutationVariables> = (props) => {
          const {workspaceId,membershipId,data} = props ?? {};

          return  removeDocumentWorkspaceEvidenceSelection(workspaceId,membershipId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RemoveDocumentWorkspaceEvidenceSelectionMutationResult = NonNullable<Awaited<ReturnType<typeof removeDocumentWorkspaceEvidenceSelection>>>
    export type RemoveDocumentWorkspaceEvidenceSelectionMutationBody = RemoveDocumentWorkspaceEvidenceSelection
    export type RemoveDocumentWorkspaceEvidenceSelectionMutationError = Error
    export type RemoveDocumentWorkspaceEvidenceSelectionMutationVariables = {workspaceId: string;membershipId: string;data: RemoveDocumentWorkspaceEvidenceSelection}

    export const useRemoveDocumentWorkspaceEvidenceSelection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeDocumentWorkspaceEvidenceSelection>>, TError,RemoveDocumentWorkspaceEvidenceSelectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof removeDocumentWorkspaceEvidenceSelection>>,
        TError,
        RemoveDocumentWorkspaceEvidenceSelectionMutationVariables,
        TContext
      > => {
      return useMutation(getRemoveDocumentWorkspaceEvidenceSelectionMutationOptions(options));
    }

export const getGetFileAssociationsUrl = (sourceKind: 'library' | 'managed_document',
    sourceId: string,
    params?: GetFileAssociationsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/file-associations/${sourceKind}/${sourceId}?${stringifiedParams}` : `/api/file-associations/${sourceKind}/${sourceId}`
}

export const getFileAssociations = async (sourceKind: 'library' | 'managed_document',
    sourceId: string,
    params?: GetFileAssociationsParams, options?: Parameters<typeof customFetch>[1]): Promise<FileAssociationPage> => {

  return customFetch<FileAssociationPage>(getGetFileAssociationsUrl(sourceKind,sourceId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetFileAssociationsQueryKey = (sourceKind: 'library' | 'managed_document',
    sourceId: string,
    params?: GetFileAssociationsParams,) => {
    return [
    `/api/file-associations/${sourceKind}/${sourceId}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetFileAssociationsQueryOptions = <TData = Awaited<ReturnType<typeof getFileAssociations>>, TError = Error>(sourceKind: 'library' | 'managed_document',
    sourceId: string,
    params?: GetFileAssociationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getFileAssociations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetFileAssociationsQueryKey(sourceKind,sourceId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getFileAssociations>>> = ({ signal }) => getFileAssociations(sourceKind,sourceId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: sourceKind !== null && sourceKind !== undefined && sourceId !== null && sourceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getFileAssociations>>, TError, TData> & { queryKey: QueryKey }
}

export type GetFileAssociationsQueryResult = NonNullable<Awaited<ReturnType<typeof getFileAssociations>>>
export type GetFileAssociationsQueryError = Error



export function useGetFileAssociations<TData = Awaited<ReturnType<typeof getFileAssociations>>, TError = Error>(
 sourceKind: 'library' | 'managed_document',
    sourceId: string,
    params?: GetFileAssociationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getFileAssociations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetFileAssociationsQueryOptions(sourceKind,sourceId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSaveFileAssociationsUrl = (sourceKind: 'library' | 'managed_document',
    sourceId: string,) => {




  return `/api/file-associations/${sourceKind}/${sourceId}`
}

export const saveFileAssociations = async (sourceKind: 'library' | 'managed_document',
    sourceId: string,
    saveFileAssociationsBody: SaveFileAssociations, options?: Parameters<typeof customFetch>[1]): Promise<FileAssociationReceipt> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<FileAssociationReceipt>(getSaveFileAssociationsUrl(sourceKind,sourceId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(saveFileAssociationsBody)
  }
);}





export const getSaveFileAssociationsMutationKey = () => ['saveFileAssociations'] as const;

export const getSaveFileAssociationsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveFileAssociations>>, TError,SaveFileAssociationsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof saveFileAssociations>>, TError,SaveFileAssociationsMutationVariables, TContext> => {

const mutationKey = getSaveFileAssociationsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof saveFileAssociations>>, SaveFileAssociationsMutationVariables> = (props) => {
          const {sourceKind,sourceId,data} = props ?? {};

          return  saveFileAssociations(sourceKind,sourceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveFileAssociationsMutationResult = NonNullable<Awaited<ReturnType<typeof saveFileAssociations>>>
    export type SaveFileAssociationsMutationBody = SaveFileAssociations
    export type SaveFileAssociationsMutationError = Error
    export type SaveFileAssociationsMutationVariables = {sourceKind: 'library' | 'managed_document';sourceId: string;data: SaveFileAssociations}

    export const useSaveFileAssociations = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveFileAssociations>>, TError,SaveFileAssociationsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof saveFileAssociations>>,
        TError,
        SaveFileAssociationsMutationVariables,
        TContext
      > => {
      return useMutation(getSaveFileAssociationsMutationOptions(options));
    }

export const getGetFileAssociationOperationUrl = (sourceKind: 'library' | 'managed_document',
    sourceId: string,
    requestId: string,) => {




  return `/api/file-associations/${sourceKind}/${sourceId}/operations/${requestId}`
}

export const getFileAssociationOperation = async (sourceKind: 'library' | 'managed_document',
    sourceId: string,
    requestId: string, options?: Parameters<typeof customFetch>[1]): Promise<FileAssociationReceipt> => {

  return customFetch<FileAssociationReceipt>(getGetFileAssociationOperationUrl(sourceKind,sourceId,requestId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetFileAssociationOperationQueryKey = (sourceKind: 'library' | 'managed_document',
    sourceId: string,
    requestId: string,) => {
    return [
    `/api/file-associations/${sourceKind}/${sourceId}/operations/${requestId}`
    ] as const;
    }


export const getGetFileAssociationOperationQueryOptions = <TData = Awaited<ReturnType<typeof getFileAssociationOperation>>, TError = Error>(sourceKind: 'library' | 'managed_document',
    sourceId: string,
    requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getFileAssociationOperation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetFileAssociationOperationQueryKey(sourceKind,sourceId,requestId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getFileAssociationOperation>>> = ({ signal }) => getFileAssociationOperation(sourceKind,sourceId,requestId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: sourceKind !== null && sourceKind !== undefined && sourceId !== null && sourceId !== undefined && requestId !== null && requestId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getFileAssociationOperation>>, TError, TData> & { queryKey: QueryKey }
}

export type GetFileAssociationOperationQueryResult = NonNullable<Awaited<ReturnType<typeof getFileAssociationOperation>>>
export type GetFileAssociationOperationQueryError = Error



export function useGetFileAssociationOperation<TData = Awaited<ReturnType<typeof getFileAssociationOperation>>, TError = Error>(
 sourceKind: 'library' | 'managed_document',
    sourceId: string,
    requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getFileAssociationOperation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetFileAssociationOperationQueryOptions(sourceKind,sourceId,requestId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListRecordFilesUrl = (recordKind: 'account' | 'contact' | 'enquiry' | 'opportunity',
    recordId: string,
    params?: ListRecordFilesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/record-files/${recordKind}/${recordId}?${stringifiedParams}` : `/api/record-files/${recordKind}/${recordId}`
}

/**
 * Library files and managed documents attached to one CRM record, newest first. Only files the caller may read are listed.
 */
export const listRecordFiles = async (recordKind: 'account' | 'contact' | 'enquiry' | 'opportunity',
    recordId: string,
    params?: ListRecordFilesParams, options?: Parameters<typeof customFetch>[1]): Promise<RecordFileList> => {

  return customFetch<RecordFileList>(getListRecordFilesUrl(recordKind,recordId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListRecordFilesQueryKey = (recordKind: 'account' | 'contact' | 'enquiry' | 'opportunity',
    recordId: string,
    params?: ListRecordFilesParams,) => {
    return [
    `/api/record-files/${recordKind}/${recordId}`, ...(params ? [params] : [])
    ] as const;
    }


export const getListRecordFilesQueryOptions = <TData = Awaited<ReturnType<typeof listRecordFiles>>, TError = Error>(recordKind: 'account' | 'contact' | 'enquiry' | 'opportunity',
    recordId: string,
    params?: ListRecordFilesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRecordFiles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListRecordFilesQueryKey(recordKind,recordId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listRecordFiles>>> = ({ signal }) => listRecordFiles(recordKind,recordId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: recordKind !== null && recordKind !== undefined && recordId !== null && recordId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listRecordFiles>>, TError, TData> & { queryKey: QueryKey }
}

export type ListRecordFilesQueryResult = NonNullable<Awaited<ReturnType<typeof listRecordFiles>>>
export type ListRecordFilesQueryError = Error



export function useListRecordFiles<TData = Awaited<ReturnType<typeof listRecordFiles>>, TError = Error>(
 recordKind: 'account' | 'contact' | 'enquiry' | 'opportunity',
    recordId: string,
    params?: ListRecordFilesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRecordFiles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListRecordFilesQueryOptions(recordKind,recordId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getAttachRecordFileUrl = (recordKind: 'account' | 'contact' | 'enquiry' | 'opportunity',
    recordId: string,) => {




  return `/api/record-files/${recordKind}/${recordId}`
}

/**
 * Attach an existing library file or managed document to a CRM record. Idempotent; attaching never grants file access.
 */
export const attachRecordFile = async (recordKind: 'account' | 'contact' | 'enquiry' | 'opportunity',
    recordId: string,
    attachRecordFileBody: AttachRecordFile, options?: Parameters<typeof customFetch>[1]): Promise<RecordFileChange> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<RecordFileChange>(getAttachRecordFileUrl(recordKind,recordId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(attachRecordFileBody)
  }
);}





export const getAttachRecordFileMutationKey = () => ['attachRecordFile'] as const;

export const getAttachRecordFileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof attachRecordFile>>, TError,AttachRecordFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof attachRecordFile>>, TError,AttachRecordFileMutationVariables, TContext> => {

const mutationKey = getAttachRecordFileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof attachRecordFile>>, AttachRecordFileMutationVariables> = (props) => {
          const {recordKind,recordId,data} = props ?? {};

          return  attachRecordFile(recordKind,recordId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AttachRecordFileMutationResult = NonNullable<Awaited<ReturnType<typeof attachRecordFile>>>
    export type AttachRecordFileMutationBody = AttachRecordFile
    export type AttachRecordFileMutationError = Error
    export type AttachRecordFileMutationVariables = {recordKind: 'account' | 'contact' | 'enquiry' | 'opportunity';recordId: string;data: AttachRecordFile}

    export const useAttachRecordFile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof attachRecordFile>>, TError,AttachRecordFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof attachRecordFile>>,
        TError,
        AttachRecordFileMutationVariables,
        TContext
      > => {
      return useMutation(getAttachRecordFileMutationOptions(options));
    }

export const getDetachRecordFileUrl = (recordKind: 'account' | 'contact' | 'enquiry' | 'opportunity',
    recordId: string,
    sourceKind: 'library' | 'managed_document',
    sourceId: string,) => {




  return `/api/record-files/${recordKind}/${recordId}/${sourceKind}/${sourceId}`
}

/**
 * Remove an explicit attachment. The file itself, its versions and other links are unchanged.
 */
export const detachRecordFile = async (recordKind: 'account' | 'contact' | 'enquiry' | 'opportunity',
    recordId: string,
    sourceKind: 'library' | 'managed_document',
    sourceId: string, options?: Parameters<typeof customFetch>[1]): Promise<RecordFileChange> => {

  return customFetch<RecordFileChange>(getDetachRecordFileUrl(recordKind,recordId,sourceKind,sourceId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDetachRecordFileMutationKey = () => ['detachRecordFile'] as const;

export const getDetachRecordFileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof detachRecordFile>>, TError,DetachRecordFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof detachRecordFile>>, TError,DetachRecordFileMutationVariables, TContext> => {

const mutationKey = getDetachRecordFileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof detachRecordFile>>, DetachRecordFileMutationVariables> = (props) => {
          const {recordKind,recordId,sourceKind,sourceId} = props ?? {};

          return  detachRecordFile(recordKind,recordId,sourceKind,sourceId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DetachRecordFileMutationResult = NonNullable<Awaited<ReturnType<typeof detachRecordFile>>>

    export type DetachRecordFileMutationError = Error
    export type DetachRecordFileMutationVariables = {recordKind: 'account' | 'contact' | 'enquiry' | 'opportunity';recordId: string;sourceKind: 'library' | 'managed_document';sourceId: string}

    export const useDetachRecordFile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof detachRecordFile>>, TError,DetachRecordFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof detachRecordFile>>,
        TError,
        DetachRecordFileMutationVariables,
        TContext
      > => {
      return useMutation(getDetachRecordFileMutationOptions(options));
    }

export const getListFileLocationsUrl = (params: ListFileLocationsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/file-locations?${stringifiedParams}` : `/api/file-locations`
}

export const listFileLocations = async (params: ListFileLocationsParams, options?: Parameters<typeof customFetch>[1]): Promise<FileLocationList> => {

  return customFetch<FileLocationList>(getListFileLocationsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListFileLocationsQueryKey = (params?: ListFileLocationsParams,) => {
    return [
    `/api/file-locations`, ...(params ? [params] : [])
    ] as const;
    }


export const getListFileLocationsQueryOptions = <TData = Awaited<ReturnType<typeof listFileLocations>>, TError = Error>(params: ListFileLocationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listFileLocations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListFileLocationsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listFileLocations>>> = ({ signal }) => listFileLocations(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listFileLocations>>, TError, TData> & { queryKey: QueryKey }
}

export type ListFileLocationsQueryResult = NonNullable<Awaited<ReturnType<typeof listFileLocations>>>
export type ListFileLocationsQueryError = Error



export function useListFileLocations<TData = Awaited<ReturnType<typeof listFileLocations>>, TError = Error>(
 params: ListFileLocationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listFileLocations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListFileLocationsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetDocumentWorkspaceContextUrl = (workspaceId: string,
    params?: GetDocumentWorkspaceContextParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/document-workspaces/${workspaceId}/source-context?${stringifiedParams}` : `/api/document-workspaces/${workspaceId}/source-context`
}

export const getDocumentWorkspaceContext = async (workspaceId: string,
    params?: GetDocumentWorkspaceContextParams, options?: Parameters<typeof customFetch>[1]): Promise<DocumentWorkspaceContext> => {

  return customFetch<DocumentWorkspaceContext>(getGetDocumentWorkspaceContextUrl(workspaceId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDocumentWorkspaceContextQueryKey = (workspaceId: string,
    params?: GetDocumentWorkspaceContextParams,) => {
    return [
    `/api/document-workspaces/${workspaceId}/source-context`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetDocumentWorkspaceContextQueryOptions = <TData = Awaited<ReturnType<typeof getDocumentWorkspaceContext>>, TError = Error>(workspaceId: string,
    params?: GetDocumentWorkspaceContextParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentWorkspaceContext>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDocumentWorkspaceContextQueryKey(workspaceId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDocumentWorkspaceContext>>> = ({ signal }) => getDocumentWorkspaceContext(workspaceId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: workspaceId !== null && workspaceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDocumentWorkspaceContext>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDocumentWorkspaceContextQueryResult = NonNullable<Awaited<ReturnType<typeof getDocumentWorkspaceContext>>>
export type GetDocumentWorkspaceContextQueryError = Error



export function useGetDocumentWorkspaceContext<TData = Awaited<ReturnType<typeof getDocumentWorkspaceContext>>, TError = Error>(
 workspaceId: string,
    params?: GetDocumentWorkspaceContextParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentWorkspaceContext>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDocumentWorkspaceContextQueryOptions(workspaceId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateBidBuyerReviewUrl = (id: string,) => {




  return `/api/tenders/${id}/bid-buyer-reviews`
}

export const createBidBuyerReview = async (id: string,
    createBidBuyerReviewBody: CreateBidBuyerReviewBody, options?: Parameters<typeof customFetch>[1]): Promise<TenderAiRunContract> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderAiRunContract>(getCreateBidBuyerReviewUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createBidBuyerReviewBody)
  }
);}





export const getCreateBidBuyerReviewMutationKey = () => ['createBidBuyerReview'] as const;

export const getCreateBidBuyerReviewMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBidBuyerReview>>, TError,CreateBidBuyerReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createBidBuyerReview>>, TError,CreateBidBuyerReviewMutationVariables, TContext> => {

const mutationKey = getCreateBidBuyerReviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createBidBuyerReview>>, CreateBidBuyerReviewMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createBidBuyerReview(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateBidBuyerReviewMutationResult = NonNullable<Awaited<ReturnType<typeof createBidBuyerReview>>>
    export type CreateBidBuyerReviewMutationBody = CreateBidBuyerReviewBody
    export type CreateBidBuyerReviewMutationError = unknown
    export type CreateBidBuyerReviewMutationVariables = {id: string;data: CreateBidBuyerReviewBody}

    export const useCreateBidBuyerReview = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBidBuyerReview>>, TError,CreateBidBuyerReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createBidBuyerReview>>,
        TError,
        CreateBidBuyerReviewMutationVariables,
        TContext
      > => {
      return useMutation(getCreateBidBuyerReviewMutationOptions(options));
    }

export const getListBidLessonsUrl = (params?: ListBidLessonsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/bid-lessons?${stringifiedParams}` : `/api/bid-lessons`
}

export const listBidLessons = async (params?: ListBidLessonsParams, options?: Parameters<typeof customFetch>[1]): Promise<BidLessonPage> => {

  return customFetch<BidLessonPage>(getListBidLessonsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListBidLessonsQueryKey = (params?: ListBidLessonsParams,) => {
    return [
    `/api/bid-lessons`, ...(params ? [params] : [])
    ] as const;
    }


export const getListBidLessonsQueryOptions = <TData = Awaited<ReturnType<typeof listBidLessons>>, TError = unknown>(params?: ListBidLessonsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listBidLessons>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListBidLessonsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listBidLessons>>> = ({ signal }) => listBidLessons(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listBidLessons>>, TError, TData> & { queryKey: QueryKey }
}

export type ListBidLessonsQueryResult = NonNullable<Awaited<ReturnType<typeof listBidLessons>>>
export type ListBidLessonsQueryError = unknown



export function useListBidLessons<TData = Awaited<ReturnType<typeof listBidLessons>>, TError = unknown>(
 params?: ListBidLessonsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listBidLessons>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListBidLessonsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getProposeBidLessonUrl = (id: string,) => {




  return `/api/tenders/${id}/bid-lessons`
}

export const proposeBidLesson = async (id: string,
    bidLessonCreate: BidLessonCreate, options?: Parameters<typeof customFetch>[1]): Promise<BidLesson> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<BidLesson>(getProposeBidLessonUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(bidLessonCreate)
  }
);}





export const getProposeBidLessonMutationKey = () => ['proposeBidLesson'] as const;

export const getProposeBidLessonMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof proposeBidLesson>>, TError,ProposeBidLessonMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof proposeBidLesson>>, TError,ProposeBidLessonMutationVariables, TContext> => {

const mutationKey = getProposeBidLessonMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof proposeBidLesson>>, ProposeBidLessonMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  proposeBidLesson(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ProposeBidLessonMutationResult = NonNullable<Awaited<ReturnType<typeof proposeBidLesson>>>
    export type ProposeBidLessonMutationBody = BidLessonCreate
    export type ProposeBidLessonMutationError = unknown
    export type ProposeBidLessonMutationVariables = {id: string;data: BidLessonCreate}

    export const useProposeBidLesson = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof proposeBidLesson>>, TError,ProposeBidLessonMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof proposeBidLesson>>,
        TError,
        ProposeBidLessonMutationVariables,
        TContext
      > => {
      return useMutation(getProposeBidLessonMutationOptions(options));
    }

export const getDecideBidLessonUrl = (lessonId: string,) => {




  return `/api/bid-lessons/${lessonId}/decisions`
}

export const decideBidLesson = async (lessonId: string,
    bidLessonDecision: BidLessonDecision, options?: Parameters<typeof customFetch>[1]): Promise<BidLesson> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<BidLesson>(getDecideBidLessonUrl(lessonId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(bidLessonDecision)
  }
);}





export const getDecideBidLessonMutationKey = () => ['decideBidLesson'] as const;

export const getDecideBidLessonMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideBidLesson>>, TError,DecideBidLessonMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof decideBidLesson>>, TError,DecideBidLessonMutationVariables, TContext> => {

const mutationKey = getDecideBidLessonMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof decideBidLesson>>, DecideBidLessonMutationVariables> = (props) => {
          const {lessonId,data} = props ?? {};

          return  decideBidLesson(lessonId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DecideBidLessonMutationResult = NonNullable<Awaited<ReturnType<typeof decideBidLesson>>>
    export type DecideBidLessonMutationBody = BidLessonDecision
    export type DecideBidLessonMutationError = unknown
    export type DecideBidLessonMutationVariables = {lessonId: string;data: BidLessonDecision}

    export const useDecideBidLesson = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideBidLesson>>, TError,DecideBidLessonMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof decideBidLesson>>,
        TError,
        DecideBidLessonMutationVariables,
        TContext
      > => {
      return useMutation(getDecideBidLessonMutationOptions(options));
    }

export const getListBidLessonHistoryUrl = (lessonId: string,
    params?: ListBidLessonHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/bid-lessons/${lessonId}/history?${stringifiedParams}` : `/api/bid-lessons/${lessonId}/history`
}

export const listBidLessonHistory = async (lessonId: string,
    params?: ListBidLessonHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<BidLessonPage> => {

  return customFetch<BidLessonPage>(getListBidLessonHistoryUrl(lessonId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListBidLessonHistoryQueryKey = (lessonId: string,
    params?: ListBidLessonHistoryParams,) => {
    return [
    `/api/bid-lessons/${lessonId}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListBidLessonHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listBidLessonHistory>>, TError = unknown>(lessonId: string,
    params?: ListBidLessonHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listBidLessonHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListBidLessonHistoryQueryKey(lessonId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listBidLessonHistory>>> = ({ signal }) => listBidLessonHistory(lessonId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: lessonId !== null && lessonId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listBidLessonHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListBidLessonHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listBidLessonHistory>>>
export type ListBidLessonHistoryQueryError = unknown



export function useListBidLessonHistory<TData = Awaited<ReturnType<typeof listBidLessonHistory>>, TError = unknown>(
 lessonId: string,
    params?: ListBidLessonHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listBidLessonHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListBidLessonHistoryQueryOptions(lessonId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateBidSynthesisUrl = (id: string,) => {




  return `/api/tenders/${id}/bid-synthesis`
}

export const createBidSynthesis = async (id: string,
    bidSynthesisCreate: BidSynthesisCreate, options?: Parameters<typeof customFetch>[1]): Promise<TenderAiRunContract> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderAiRunContract>(getCreateBidSynthesisUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(bidSynthesisCreate)
  }
);}





export const getCreateBidSynthesisMutationKey = () => ['createBidSynthesis'] as const;

export const getCreateBidSynthesisMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBidSynthesis>>, TError,CreateBidSynthesisMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createBidSynthesis>>, TError,CreateBidSynthesisMutationVariables, TContext> => {

const mutationKey = getCreateBidSynthesisMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createBidSynthesis>>, CreateBidSynthesisMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createBidSynthesis(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateBidSynthesisMutationResult = NonNullable<Awaited<ReturnType<typeof createBidSynthesis>>>
    export type CreateBidSynthesisMutationBody = BidSynthesisCreate
    export type CreateBidSynthesisMutationError = unknown
    export type CreateBidSynthesisMutationVariables = {id: string;data: BidSynthesisCreate}

    export const useCreateBidSynthesis = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBidSynthesis>>, TError,CreateBidSynthesisMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createBidSynthesis>>,
        TError,
        CreateBidSynthesisMutationVariables,
        TContext
      > => {
      return useMutation(getCreateBidSynthesisMutationOptions(options));
    }

export const getGetBidSynthesisUrl = (id: string,
    runId: string,
    params?: GetBidSynthesisParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/bid-synthesis/${runId}?${stringifiedParams}` : `/api/tenders/${id}/bid-synthesis/${runId}`
}

export const getBidSynthesis = async (id: string,
    runId: string,
    params?: GetBidSynthesisParams, options?: Parameters<typeof customFetch>[1]): Promise<BidSynthesisProgress> => {

  return customFetch<BidSynthesisProgress>(getGetBidSynthesisUrl(id,runId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetBidSynthesisQueryKey = (id: string,
    runId: string,
    params?: GetBidSynthesisParams,) => {
    return [
    `/api/tenders/${id}/bid-synthesis/${runId}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetBidSynthesisQueryOptions = <TData = Awaited<ReturnType<typeof getBidSynthesis>>, TError = unknown>(id: string,
    runId: string,
    params?: GetBidSynthesisParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getBidSynthesis>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetBidSynthesisQueryKey(id,runId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getBidSynthesis>>> = ({ signal }) => getBidSynthesis(id,runId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && runId !== null && runId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getBidSynthesis>>, TError, TData> & { queryKey: QueryKey }
}

export type GetBidSynthesisQueryResult = NonNullable<Awaited<ReturnType<typeof getBidSynthesis>>>
export type GetBidSynthesisQueryError = unknown



export function useGetBidSynthesis<TData = Awaited<ReturnType<typeof getBidSynthesis>>, TError = unknown>(
 id: string,
    runId: string,
    params?: GetBidSynthesisParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getBidSynthesis>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetBidSynthesisQueryOptions(id,runId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getResumeBidSynthesisUrl = (id: string,
    runId: string,) => {




  return `/api/tenders/${id}/bid-synthesis/${runId}/resume`
}

export const resumeBidSynthesis = async (id: string,
    runId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenderAiRunContract> => {

  return customFetch<TenderAiRunContract>(getResumeBidSynthesisUrl(id,runId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getResumeBidSynthesisMutationKey = () => ['resumeBidSynthesis'] as const;

export const getResumeBidSynthesisMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resumeBidSynthesis>>, TError,ResumeBidSynthesisMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof resumeBidSynthesis>>, TError,ResumeBidSynthesisMutationVariables, TContext> => {

const mutationKey = getResumeBidSynthesisMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof resumeBidSynthesis>>, ResumeBidSynthesisMutationVariables> = (props) => {
          const {id,runId} = props ?? {};

          return  resumeBidSynthesis(id,runId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResumeBidSynthesisMutationResult = NonNullable<Awaited<ReturnType<typeof resumeBidSynthesis>>>

    export type ResumeBidSynthesisMutationError = unknown
    export type ResumeBidSynthesisMutationVariables = {id: string;runId: string}

    export const useResumeBidSynthesis = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resumeBidSynthesis>>, TError,ResumeBidSynthesisMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof resumeBidSynthesis>>,
        TError,
        ResumeBidSynthesisMutationVariables,
        TContext
      > => {
      return useMutation(getResumeBidSynthesisMutationOptions(options));
    }

export const getDecideBidSynthesisProposalUrl = (id: string,
    runId: string,
    proposalId: string,) => {




  return `/api/tenders/${id}/bid-synthesis/${runId}/proposals/${proposalId}/decision`
}

export const decideBidSynthesisProposal = async (id: string,
    runId: string,
    proposalId: string,
    tenderProposalDecision: TenderProposalDecision, options?: Parameters<typeof customFetch>[1]): Promise<TenderProposal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderProposal>(getDecideBidSynthesisProposalUrl(id,runId,proposalId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderProposalDecision)
  }
);}





export const getDecideBidSynthesisProposalMutationKey = () => ['decideBidSynthesisProposal'] as const;

export const getDecideBidSynthesisProposalMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideBidSynthesisProposal>>, TError,DecideBidSynthesisProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof decideBidSynthesisProposal>>, TError,DecideBidSynthesisProposalMutationVariables, TContext> => {

const mutationKey = getDecideBidSynthesisProposalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof decideBidSynthesisProposal>>, DecideBidSynthesisProposalMutationVariables> = (props) => {
          const {id,runId,proposalId,data} = props ?? {};

          return  decideBidSynthesisProposal(id,runId,proposalId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DecideBidSynthesisProposalMutationResult = NonNullable<Awaited<ReturnType<typeof decideBidSynthesisProposal>>>
    export type DecideBidSynthesisProposalMutationBody = TenderProposalDecision
    export type DecideBidSynthesisProposalMutationError = unknown
    export type DecideBidSynthesisProposalMutationVariables = {id: string;runId: string;proposalId: string;data: TenderProposalDecision}

    export const useDecideBidSynthesisProposal = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideBidSynthesisProposal>>, TError,DecideBidSynthesisProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof decideBidSynthesisProposal>>,
        TError,
        DecideBidSynthesisProposalMutationVariables,
        TContext
      > => {
      return useMutation(getDecideBidSynthesisProposalMutationOptions(options));
    }

export const getGetBidStageModelsUrl = (id: string,
    stage: BidStage,
    params?: GetBidStageModelsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/bid-stages/${stage}/models?${stringifiedParams}` : `/api/tenders/${id}/bid-stages/${stage}/models`
}

export const getBidStageModels = async (id: string,
    stage: BidStage,
    params?: GetBidStageModelsParams, options?: Parameters<typeof customFetch>[1]): Promise<BidStageOptions> => {

  return customFetch<BidStageOptions>(getGetBidStageModelsUrl(id,stage,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetBidStageModelsQueryKey = (id: string,
    stage: BidStage,
    params?: GetBidStageModelsParams,) => {
    return [
    `/api/tenders/${id}/bid-stages/${stage}/models`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetBidStageModelsQueryOptions = <TData = Awaited<ReturnType<typeof getBidStageModels>>, TError = unknown>(id: string,
    stage: BidStage,
    params?: GetBidStageModelsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getBidStageModels>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetBidStageModelsQueryKey(id,stage,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getBidStageModels>>> = ({ signal }) => getBidStageModels(id,stage,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && stage !== null && stage !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getBidStageModels>>, TError, TData> & { queryKey: QueryKey }
}

export type GetBidStageModelsQueryResult = NonNullable<Awaited<ReturnType<typeof getBidStageModels>>>
export type GetBidStageModelsQueryError = unknown



export function useGetBidStageModels<TData = Awaited<ReturnType<typeof getBidStageModels>>, TError = unknown>(
 id: string,
    stage: BidStage,
    params?: GetBidStageModelsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getBidStageModels>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetBidStageModelsQueryOptions(id,stage,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSaveBidStageSelectionUrl = (id: string,
    stage: BidStage,) => {




  return `/api/tenders/${id}/bid-stages/${stage}/selection`
}

export const saveBidStageSelection = async (id: string,
    stage: BidStage,
    bidStageSelectionWrite: BidStageSelectionWrite, options?: Parameters<typeof customFetch>[1]): Promise<BidStageSelection> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<BidStageSelection>(getSaveBidStageSelectionUrl(id,stage),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(bidStageSelectionWrite)
  }
);}





export const getSaveBidStageSelectionMutationKey = () => ['saveBidStageSelection'] as const;

export const getSaveBidStageSelectionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveBidStageSelection>>, TError,SaveBidStageSelectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof saveBidStageSelection>>, TError,SaveBidStageSelectionMutationVariables, TContext> => {

const mutationKey = getSaveBidStageSelectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof saveBidStageSelection>>, SaveBidStageSelectionMutationVariables> = (props) => {
          const {id,stage,data} = props ?? {};

          return  saveBidStageSelection(id,stage,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveBidStageSelectionMutationResult = NonNullable<Awaited<ReturnType<typeof saveBidStageSelection>>>
    export type SaveBidStageSelectionMutationBody = BidStageSelectionWrite
    export type SaveBidStageSelectionMutationError = unknown
    export type SaveBidStageSelectionMutationVariables = {id: string;stage: BidStage;data: BidStageSelectionWrite}

    export const useSaveBidStageSelection = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveBidStageSelection>>, TError,SaveBidStageSelectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof saveBidStageSelection>>,
        TError,
        SaveBidStageSelectionMutationVariables,
        TContext
      > => {
      return useMutation(getSaveBidStageSelectionMutationOptions(options));
    }

export const getCreateBidStageRunUrl = (id: string,
    stage: BidStage,) => {




  return `/api/tenders/${id}/bid-stages/${stage}/runs`
}

export const createBidStageRun = async (id: string,
    stage: BidStage,
    bidStageRunCreate: BidStageRunCreate, options?: Parameters<typeof customFetch>[1]): Promise<TenderAiRunContract> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderAiRunContract>(getCreateBidStageRunUrl(id,stage),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(bidStageRunCreate)
  }
);}





export const getCreateBidStageRunMutationKey = () => ['createBidStageRun'] as const;

export const getCreateBidStageRunMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBidStageRun>>, TError,CreateBidStageRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createBidStageRun>>, TError,CreateBidStageRunMutationVariables, TContext> => {

const mutationKey = getCreateBidStageRunMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createBidStageRun>>, CreateBidStageRunMutationVariables> = (props) => {
          const {id,stage,data} = props ?? {};

          return  createBidStageRun(id,stage,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateBidStageRunMutationResult = NonNullable<Awaited<ReturnType<typeof createBidStageRun>>>
    export type CreateBidStageRunMutationBody = BidStageRunCreate
    export type CreateBidStageRunMutationError = unknown
    export type CreateBidStageRunMutationVariables = {id: string;stage: BidStage;data: BidStageRunCreate}

    export const useCreateBidStageRun = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBidStageRun>>, TError,CreateBidStageRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createBidStageRun>>,
        TError,
        CreateBidStageRunMutationVariables,
        TContext
      > => {
      return useMutation(getCreateBidStageRunMutationOptions(options));
    }

export const getGetBidModelAdministrationUrl = (scope: BidModelScope,
    params?: GetBidModelAdministrationParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/bid-models/admin/${scope}?${stringifiedParams}` : `/api/bid-models/admin/${scope}`
}

export const getBidModelAdministration = async (scope: BidModelScope,
    params?: GetBidModelAdministrationParams, options?: Parameters<typeof customFetch>[1]): Promise<BidModelAdministration> => {

  return customFetch<BidModelAdministration>(getGetBidModelAdministrationUrl(scope,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetBidModelAdministrationQueryKey = (scope: BidModelScope,
    params?: GetBidModelAdministrationParams,) => {
    return [
    `/api/bid-models/admin/${scope}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetBidModelAdministrationQueryOptions = <TData = Awaited<ReturnType<typeof getBidModelAdministration>>, TError = void>(scope: BidModelScope,
    params?: GetBidModelAdministrationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getBidModelAdministration>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetBidModelAdministrationQueryKey(scope,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getBidModelAdministration>>> = ({ signal }) => getBidModelAdministration(scope,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: scope !== null && scope !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getBidModelAdministration>>, TError, TData> & { queryKey: QueryKey }
}

export type GetBidModelAdministrationQueryResult = NonNullable<Awaited<ReturnType<typeof getBidModelAdministration>>>
export type GetBidModelAdministrationQueryError = void



export function useGetBidModelAdministration<TData = Awaited<ReturnType<typeof getBidModelAdministration>>, TError = void>(
 scope: BidModelScope,
    params?: GetBidModelAdministrationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getBidModelAdministration>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetBidModelAdministrationQueryOptions(scope,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getWriteBidProcessingPolicyUrl = (scope: BidModelScope,) => {




  return `/api/bid-models/policies/${scope}`
}

export const writeBidProcessingPolicy = async (scope: BidModelScope,
    bidPolicyWrite: BidPolicyWrite, options?: Parameters<typeof customFetch>[1]): Promise<BidPolicyRevision> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<BidPolicyRevision>(getWriteBidProcessingPolicyUrl(scope),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(bidPolicyWrite)
  }
);}





export const getWriteBidProcessingPolicyMutationKey = () => ['writeBidProcessingPolicy'] as const;

export const getWriteBidProcessingPolicyMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writeBidProcessingPolicy>>, TError,WriteBidProcessingPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof writeBidProcessingPolicy>>, TError,WriteBidProcessingPolicyMutationVariables, TContext> => {

const mutationKey = getWriteBidProcessingPolicyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof writeBidProcessingPolicy>>, WriteBidProcessingPolicyMutationVariables> = (props) => {
          const {scope,data} = props ?? {};

          return  writeBidProcessingPolicy(scope,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type WriteBidProcessingPolicyMutationResult = NonNullable<Awaited<ReturnType<typeof writeBidProcessingPolicy>>>
    export type WriteBidProcessingPolicyMutationBody = BidPolicyWrite
    export type WriteBidProcessingPolicyMutationError = void
    export type WriteBidProcessingPolicyMutationVariables = {scope: BidModelScope;data: BidPolicyWrite}

    export const useWriteBidProcessingPolicy = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writeBidProcessingPolicy>>, TError,WriteBidProcessingPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof writeBidProcessingPolicy>>,
        TError,
        WriteBidProcessingPolicyMutationVariables,
        TContext
      > => {
      return useMutation(getWriteBidProcessingPolicyMutationOptions(options));
    }

export const getWriteBidModelRecordUrl = (choiceId: string,) => {




  return `/api/platform/bid-models/${choiceId}`
}

export const writeBidModelRecord = async (choiceId: string,
    bidModelWrite: BidModelWrite, options?: Parameters<typeof customFetch>[1]): Promise<BidModelRevision> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<BidModelRevision>(getWriteBidModelRecordUrl(choiceId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(bidModelWrite)
  }
);}





export const getWriteBidModelRecordMutationKey = () => ['writeBidModelRecord'] as const;

export const getWriteBidModelRecordMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writeBidModelRecord>>, TError,WriteBidModelRecordMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof writeBidModelRecord>>, TError,WriteBidModelRecordMutationVariables, TContext> => {

const mutationKey = getWriteBidModelRecordMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof writeBidModelRecord>>, WriteBidModelRecordMutationVariables> = (props) => {
          const {choiceId,data} = props ?? {};

          return  writeBidModelRecord(choiceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type WriteBidModelRecordMutationResult = NonNullable<Awaited<ReturnType<typeof writeBidModelRecord>>>
    export type WriteBidModelRecordMutationBody = BidModelWrite
    export type WriteBidModelRecordMutationError = void
    export type WriteBidModelRecordMutationVariables = {choiceId: string;data: BidModelWrite}

    export const useWriteBidModelRecord = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writeBidModelRecord>>, TError,WriteBidModelRecordMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof writeBidModelRecord>>,
        TError,
        WriteBidModelRecordMutationVariables,
        TContext
      > => {
      return useMutation(getWriteBidModelRecordMutationOptions(options));
    }

export const getListBidModelRevisionsUrl = (choiceId: string,
    params?: ListBidModelRevisionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/bid-models/${choiceId}/revisions?${stringifiedParams}` : `/api/platform/bid-models/${choiceId}/revisions`
}

export const listBidModelRevisions = async (choiceId: string,
    params?: ListBidModelRevisionsParams, options?: Parameters<typeof customFetch>[1]): Promise<BidModelRevisionPage> => {

  return customFetch<BidModelRevisionPage>(getListBidModelRevisionsUrl(choiceId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListBidModelRevisionsQueryKey = (choiceId: string,
    params?: ListBidModelRevisionsParams,) => {
    return [
    `/api/platform/bid-models/${choiceId}/revisions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListBidModelRevisionsQueryOptions = <TData = Awaited<ReturnType<typeof listBidModelRevisions>>, TError = void>(choiceId: string,
    params?: ListBidModelRevisionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listBidModelRevisions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListBidModelRevisionsQueryKey(choiceId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listBidModelRevisions>>> = ({ signal }) => listBidModelRevisions(choiceId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: choiceId !== null && choiceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listBidModelRevisions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListBidModelRevisionsQueryResult = NonNullable<Awaited<ReturnType<typeof listBidModelRevisions>>>
export type ListBidModelRevisionsQueryError = void



export function useListBidModelRevisions<TData = Awaited<ReturnType<typeof listBidModelRevisions>>, TError = void>(
 choiceId: string,
    params?: ListBidModelRevisionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listBidModelRevisions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListBidModelRevisionsQueryOptions(choiceId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListBidPolicyRevisionsUrl = (scope: BidModelScope,
    params?: ListBidPolicyRevisionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/bid-models/policies/${scope}/revisions?${stringifiedParams}` : `/api/bid-models/policies/${scope}/revisions`
}

export const listBidPolicyRevisions = async (scope: BidModelScope,
    params?: ListBidPolicyRevisionsParams, options?: Parameters<typeof customFetch>[1]): Promise<BidPolicyRevisionPage> => {

  return customFetch<BidPolicyRevisionPage>(getListBidPolicyRevisionsUrl(scope,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListBidPolicyRevisionsQueryKey = (scope: BidModelScope,
    params?: ListBidPolicyRevisionsParams,) => {
    return [
    `/api/bid-models/policies/${scope}/revisions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListBidPolicyRevisionsQueryOptions = <TData = Awaited<ReturnType<typeof listBidPolicyRevisions>>, TError = void>(scope: BidModelScope,
    params?: ListBidPolicyRevisionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listBidPolicyRevisions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListBidPolicyRevisionsQueryKey(scope,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listBidPolicyRevisions>>> = ({ signal }) => listBidPolicyRevisions(scope,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: scope !== null && scope !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listBidPolicyRevisions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListBidPolicyRevisionsQueryResult = NonNullable<Awaited<ReturnType<typeof listBidPolicyRevisions>>>
export type ListBidPolicyRevisionsQueryError = void



export function useListBidPolicyRevisions<TData = Awaited<ReturnType<typeof listBidPolicyRevisions>>, TError = void>(
 scope: BidModelScope,
    params?: ListBidPolicyRevisionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listBidPolicyRevisions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListBidPolicyRevisionsQueryOptions(scope,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetEffectiveBidModelsUrl = (params?: GetEffectiveBidModelsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/bid-models/effective?${stringifiedParams}` : `/api/bid-models/effective`
}

export const getEffectiveBidModels = async (params?: GetEffectiveBidModelsParams, options?: Parameters<typeof customFetch>[1]): Promise<BidModelAdministration> => {

  return customFetch<BidModelAdministration>(getGetEffectiveBidModelsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEffectiveBidModelsQueryKey = (params?: GetEffectiveBidModelsParams,) => {
    return [
    `/api/bid-models/effective`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetEffectiveBidModelsQueryOptions = <TData = Awaited<ReturnType<typeof getEffectiveBidModels>>, TError = void>(params?: GetEffectiveBidModelsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEffectiveBidModels>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEffectiveBidModelsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEffectiveBidModels>>> = ({ signal }) => getEffectiveBidModels(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEffectiveBidModels>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEffectiveBidModelsQueryResult = NonNullable<Awaited<ReturnType<typeof getEffectiveBidModels>>>
export type GetEffectiveBidModelsQueryError = void



export function useGetEffectiveBidModels<TData = Awaited<ReturnType<typeof getEffectiveBidModels>>, TError = void>(
 params?: GetEffectiveBidModelsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEffectiveBidModels>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEffectiveBidModelsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetSupplierGroundingUrl = () => {




  return `/api/supplier-grounding`
}

export const getSupplierGrounding = async ( options?: Parameters<typeof customFetch>[1]): Promise<SupplierGrounding> => {

  return customFetch<SupplierGrounding>(getGetSupplierGroundingUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSupplierGroundingQueryKey = () => {
    return [
    `/api/supplier-grounding`
    ] as const;
    }


export const getGetSupplierGroundingQueryOptions = <TData = Awaited<ReturnType<typeof getSupplierGrounding>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSupplierGrounding>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSupplierGroundingQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSupplierGrounding>>> = ({ signal }) => getSupplierGrounding({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSupplierGrounding>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSupplierGroundingQueryResult = NonNullable<Awaited<ReturnType<typeof getSupplierGrounding>>>
export type GetSupplierGroundingQueryError = unknown



export function useGetSupplierGrounding<TData = Awaited<ReturnType<typeof getSupplierGrounding>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSupplierGrounding>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSupplierGroundingQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSaveSupplierFactUrl = (key: SupplierFactKey,) => {




  return `/api/supplier-grounding/${key}`
}

export const saveSupplierFact = async (key: SupplierFactKey,
    supplierFactInput: SupplierFactInput, options?: Parameters<typeof customFetch>[1]): Promise<SupplierFact> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SupplierFact>(getSaveSupplierFactUrl(key),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(supplierFactInput)
  }
);}





export const getSaveSupplierFactMutationKey = () => ['saveSupplierFact'] as const;

export const getSaveSupplierFactMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveSupplierFact>>, TError,SaveSupplierFactMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof saveSupplierFact>>, TError,SaveSupplierFactMutationVariables, TContext> => {

const mutationKey = getSaveSupplierFactMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof saveSupplierFact>>, SaveSupplierFactMutationVariables> = (props) => {
          const {key,data} = props ?? {};

          return  saveSupplierFact(key,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveSupplierFactMutationResult = NonNullable<Awaited<ReturnType<typeof saveSupplierFact>>>
    export type SaveSupplierFactMutationBody = SupplierFactInput
    export type SaveSupplierFactMutationError = unknown
    export type SaveSupplierFactMutationVariables = {key: SupplierFactKey;data: SupplierFactInput}

    export const useSaveSupplierFact = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveSupplierFact>>, TError,SaveSupplierFactMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof saveSupplierFact>>,
        TError,
        SaveSupplierFactMutationVariables,
        TContext
      > => {
      return useMutation(getSaveSupplierFactMutationOptions(options));
    }

export const getListSupplierFactRevisionsUrl = (key: SupplierFactKey,
    params?: ListSupplierFactRevisionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/supplier-grounding/${key}/revisions?${stringifiedParams}` : `/api/supplier-grounding/${key}/revisions`
}

export const listSupplierFactRevisions = async (key: SupplierFactKey,
    params?: ListSupplierFactRevisionsParams, options?: Parameters<typeof customFetch>[1]): Promise<ListSupplierFactRevisions200> => {

  return customFetch<ListSupplierFactRevisions200>(getListSupplierFactRevisionsUrl(key,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSupplierFactRevisionsQueryKey = (key: SupplierFactKey,
    params?: ListSupplierFactRevisionsParams,) => {
    return [
    `/api/supplier-grounding/${key}/revisions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSupplierFactRevisionsQueryOptions = <TData = Awaited<ReturnType<typeof listSupplierFactRevisions>>, TError = unknown>(key: SupplierFactKey,
    params?: ListSupplierFactRevisionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSupplierFactRevisions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSupplierFactRevisionsQueryKey(key,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSupplierFactRevisions>>> = ({ signal }) => listSupplierFactRevisions(key,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: key !== null && key !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSupplierFactRevisions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSupplierFactRevisionsQueryResult = NonNullable<Awaited<ReturnType<typeof listSupplierFactRevisions>>>
export type ListSupplierFactRevisionsQueryError = unknown



export function useListSupplierFactRevisions<TData = Awaited<ReturnType<typeof listSupplierFactRevisions>>, TError = unknown>(
 key: SupplierFactKey,
    params?: ListSupplierFactRevisionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSupplierFactRevisions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSupplierFactRevisionsQueryOptions(key,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetBidInventoryUrl = (id: string,
    params?: GetBidInventoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/bid-inventory?${stringifiedParams}` : `/api/tenders/${id}/bid-inventory`
}

export const getBidInventory = async (id: string,
    params?: GetBidInventoryParams, options?: Parameters<typeof customFetch>[1]): Promise<BidInventory> => {

  return customFetch<BidInventory>(getGetBidInventoryUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetBidInventoryQueryKey = (id: string,
    params?: GetBidInventoryParams,) => {
    return [
    `/api/tenders/${id}/bid-inventory`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetBidInventoryQueryOptions = <TData = Awaited<ReturnType<typeof getBidInventory>>, TError = void>(id: string,
    params?: GetBidInventoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getBidInventory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetBidInventoryQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getBidInventory>>> = ({ signal }) => getBidInventory(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getBidInventory>>, TError, TData> & { queryKey: QueryKey }
}

export type GetBidInventoryQueryResult = NonNullable<Awaited<ReturnType<typeof getBidInventory>>>
export type GetBidInventoryQueryError = void



export function useGetBidInventory<TData = Awaited<ReturnType<typeof getBidInventory>>, TError = void>(
 id: string,
    params?: GetBidInventoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getBidInventory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetBidInventoryQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewBidInventoryUrl = (id: string,) => {




  return `/api/tenders/${id}/bid-inventory/review`
}

export const reviewBidInventory = async (id: string,
    bidInventoryReviewWrite: BidInventoryReviewWrite, options?: Parameters<typeof customFetch>[1]): Promise<BidInventory> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<BidInventory>(getReviewBidInventoryUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(bidInventoryReviewWrite)
  }
);}





export const getReviewBidInventoryMutationKey = () => ['reviewBidInventory'] as const;

export const getReviewBidInventoryMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewBidInventory>>, TError,ReviewBidInventoryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewBidInventory>>, TError,ReviewBidInventoryMutationVariables, TContext> => {

const mutationKey = getReviewBidInventoryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewBidInventory>>, ReviewBidInventoryMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reviewBidInventory(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewBidInventoryMutationResult = NonNullable<Awaited<ReturnType<typeof reviewBidInventory>>>
    export type ReviewBidInventoryMutationBody = BidInventoryReviewWrite
    export type ReviewBidInventoryMutationError = void
    export type ReviewBidInventoryMutationVariables = {id: string;data: BidInventoryReviewWrite}

    export const useReviewBidInventory = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewBidInventory>>, TError,ReviewBidInventoryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewBidInventory>>,
        TError,
        ReviewBidInventoryMutationVariables,
        TContext
      > => {
      return useMutation(getReviewBidInventoryMutationOptions(options));
    }

export const getRecordBidFitDecisionUrl = (id: string,) => {




  return `/api/tenders/${id}/bid-fit-decisions`
}

export const recordBidFitDecision = async (id: string,
    bidFitDecisionWrite: BidFitDecisionWrite, options?: Parameters<typeof customFetch>[1]): Promise<BidInventory> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<BidInventory>(getRecordBidFitDecisionUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(bidFitDecisionWrite)
  }
);}





export const getRecordBidFitDecisionMutationKey = () => ['recordBidFitDecision'] as const;

export const getRecordBidFitDecisionMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recordBidFitDecision>>, TError,RecordBidFitDecisionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof recordBidFitDecision>>, TError,RecordBidFitDecisionMutationVariables, TContext> => {

const mutationKey = getRecordBidFitDecisionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof recordBidFitDecision>>, RecordBidFitDecisionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  recordBidFitDecision(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RecordBidFitDecisionMutationResult = NonNullable<Awaited<ReturnType<typeof recordBidFitDecision>>>
    export type RecordBidFitDecisionMutationBody = BidFitDecisionWrite
    export type RecordBidFitDecisionMutationError = void
    export type RecordBidFitDecisionMutationVariables = {id: string;data: BidFitDecisionWrite}

    export const useRecordBidFitDecision = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recordBidFitDecision>>, TError,RecordBidFitDecisionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof recordBidFitDecision>>,
        TError,
        RecordBidFitDecisionMutationVariables,
        TContext
      > => {
      return useMutation(getRecordBidFitDecisionMutationOptions(options));
    }

export const getGetBidFoundationUrl = (id: string,
    params?: GetBidFoundationParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/bid-foundation?${stringifiedParams}` : `/api/tenders/${id}/bid-foundation`
}

export const getBidFoundation = async (id: string,
    params?: GetBidFoundationParams, options?: Parameters<typeof customFetch>[1]): Promise<BidFoundation> => {

  return customFetch<BidFoundation>(getGetBidFoundationUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetBidFoundationQueryKey = (id: string,
    params?: GetBidFoundationParams,) => {
    return [
    `/api/tenders/${id}/bid-foundation`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetBidFoundationQueryOptions = <TData = Awaited<ReturnType<typeof getBidFoundation>>, TError = unknown>(id: string,
    params?: GetBidFoundationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getBidFoundation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetBidFoundationQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getBidFoundation>>> = ({ signal }) => getBidFoundation(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getBidFoundation>>, TError, TData> & { queryKey: QueryKey }
}

export type GetBidFoundationQueryResult = NonNullable<Awaited<ReturnType<typeof getBidFoundation>>>
export type GetBidFoundationQueryError = unknown



export function useGetBidFoundation<TData = Awaited<ReturnType<typeof getBidFoundation>>, TError = unknown>(
 id: string,
    params?: GetBidFoundationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getBidFoundation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetBidFoundationQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListDocumentStorageOptionsUrl = (scope: 'tenant' | 'entity',
    params?: ListDocumentStorageOptionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/document-storage/${scope}/options?${stringifiedParams}` : `/api/document-storage/${scope}/options`
}

export const listDocumentStorageOptions = async (scope: 'tenant' | 'entity',
    params?: ListDocumentStorageOptionsParams, options?: Parameters<typeof customFetch>[1]): Promise<DocumentStorageOptions> => {

  return customFetch<DocumentStorageOptions>(getListDocumentStorageOptionsUrl(scope,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDocumentStorageOptionsQueryKey = (scope: 'tenant' | 'entity',
    params?: ListDocumentStorageOptionsParams,) => {
    return [
    `/api/document-storage/${scope}/options`, ...(params ? [params] : [])
    ] as const;
    }


export const getListDocumentStorageOptionsQueryOptions = <TData = Awaited<ReturnType<typeof listDocumentStorageOptions>>, TError = unknown>(scope: 'tenant' | 'entity',
    params?: ListDocumentStorageOptionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentStorageOptions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDocumentStorageOptionsQueryKey(scope,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDocumentStorageOptions>>> = ({ signal }) => listDocumentStorageOptions(scope,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: scope !== null && scope !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDocumentStorageOptions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListDocumentStorageOptionsQueryResult = NonNullable<Awaited<ReturnType<typeof listDocumentStorageOptions>>>
export type ListDocumentStorageOptionsQueryError = unknown



export function useListDocumentStorageOptions<TData = Awaited<ReturnType<typeof listDocumentStorageOptions>>, TError = unknown>(
 scope: 'tenant' | 'entity',
    params?: ListDocumentStorageOptionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentStorageOptions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListDocumentStorageOptionsQueryOptions(scope,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateApplicationDocumentDestinationUrl = (scope: 'tenant' | 'entity',) => {




  return `/api/document-storage/${scope}/options`
}

export const createApplicationDocumentDestination = async (scope: 'tenant' | 'entity',
    createApplicationDocumentDestinationBody: CreateApplicationDocumentDestinationBody, options?: Parameters<typeof customFetch>[1]): Promise<DocumentDestinationDescriptor> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentDestinationDescriptor>(getCreateApplicationDocumentDestinationUrl(scope),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createApplicationDocumentDestinationBody)
  }
);}





export const getCreateApplicationDocumentDestinationMutationKey = () => ['createApplicationDocumentDestination'] as const;

export const getCreateApplicationDocumentDestinationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createApplicationDocumentDestination>>, TError,CreateApplicationDocumentDestinationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createApplicationDocumentDestination>>, TError,CreateApplicationDocumentDestinationMutationVariables, TContext> => {

const mutationKey = getCreateApplicationDocumentDestinationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createApplicationDocumentDestination>>, CreateApplicationDocumentDestinationMutationVariables> = (props) => {
          const {scope,data} = props ?? {};

          return  createApplicationDocumentDestination(scope,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateApplicationDocumentDestinationMutationResult = NonNullable<Awaited<ReturnType<typeof createApplicationDocumentDestination>>>
    export type CreateApplicationDocumentDestinationMutationBody = CreateApplicationDocumentDestinationBody
    export type CreateApplicationDocumentDestinationMutationError = unknown
    export type CreateApplicationDocumentDestinationMutationVariables = {scope: 'tenant' | 'entity';data: CreateApplicationDocumentDestinationBody}

    export const useCreateApplicationDocumentDestination = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createApplicationDocumentDestination>>, TError,CreateApplicationDocumentDestinationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createApplicationDocumentDestination>>,
        TError,
        CreateApplicationDocumentDestinationMutationVariables,
        TContext
      > => {
      return useMutation(getCreateApplicationDocumentDestinationMutationOptions(options));
    }

export const getListDocumentMaterializationsUrl = (params?: ListDocumentMaterializationsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/document-storage/materializations?${stringifiedParams}` : `/api/document-storage/materializations`
}

export const listDocumentMaterializations = async (params?: ListDocumentMaterializationsParams, options?: Parameters<typeof customFetch>[1]): Promise<ListDocumentMaterializations200> => {

  return customFetch<ListDocumentMaterializations200>(getListDocumentMaterializationsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDocumentMaterializationsQueryKey = (params?: ListDocumentMaterializationsParams,) => {
    return [
    `/api/document-storage/materializations`, ...(params ? [params] : [])
    ] as const;
    }


export const getListDocumentMaterializationsQueryOptions = <TData = Awaited<ReturnType<typeof listDocumentMaterializations>>, TError = unknown>(params?: ListDocumentMaterializationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentMaterializations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDocumentMaterializationsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDocumentMaterializations>>> = ({ signal }) => listDocumentMaterializations(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDocumentMaterializations>>, TError, TData> & { queryKey: QueryKey }
}

export type ListDocumentMaterializationsQueryResult = NonNullable<Awaited<ReturnType<typeof listDocumentMaterializations>>>
export type ListDocumentMaterializationsQueryError = unknown



export function useListDocumentMaterializations<TData = Awaited<ReturnType<typeof listDocumentMaterializations>>, TError = unknown>(
 params?: ListDocumentMaterializationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentMaterializations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListDocumentMaterializationsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSaveDocumentRepresentationUrl = () => {




  return `/api/document-storage/materializations`
}

export const saveDocumentRepresentation = async (saveDocumentRepresentationBody: SaveDocumentRepresentationBody, options?: Parameters<typeof customFetch>[1]): Promise<SaveDocumentRepresentation201> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SaveDocumentRepresentation201>(getSaveDocumentRepresentationUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(saveDocumentRepresentationBody)
  }
);}





export const getSaveDocumentRepresentationMutationKey = () => ['saveDocumentRepresentation'] as const;

export const getSaveDocumentRepresentationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveDocumentRepresentation>>, TError,SaveDocumentRepresentationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof saveDocumentRepresentation>>, TError,SaveDocumentRepresentationMutationVariables, TContext> => {

const mutationKey = getSaveDocumentRepresentationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof saveDocumentRepresentation>>, SaveDocumentRepresentationMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  saveDocumentRepresentation(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveDocumentRepresentationMutationResult = NonNullable<Awaited<ReturnType<typeof saveDocumentRepresentation>>>
    export type SaveDocumentRepresentationMutationBody = SaveDocumentRepresentationBody
    export type SaveDocumentRepresentationMutationError = unknown
    export type SaveDocumentRepresentationMutationVariables = {data: SaveDocumentRepresentationBody}

    export const useSaveDocumentRepresentation = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveDocumentRepresentation>>, TError,SaveDocumentRepresentationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof saveDocumentRepresentation>>,
        TError,
        SaveDocumentRepresentationMutationVariables,
        TContext
      > => {
      return useMutation(getSaveDocumentRepresentationMutationOptions(options));
    }

export const getReconcileDocumentMaterializationUrl = (id: string,) => {




  return `/api/document-storage/materializations/${id}/reconcile`
}

export const reconcileDocumentMaterialization = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<DocumentMaterialization> => {

  return customFetch<DocumentMaterialization>(getReconcileDocumentMaterializationUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getReconcileDocumentMaterializationMutationKey = () => ['reconcileDocumentMaterialization'] as const;

export const getReconcileDocumentMaterializationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconcileDocumentMaterialization>>, TError,ReconcileDocumentMaterializationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reconcileDocumentMaterialization>>, TError,ReconcileDocumentMaterializationMutationVariables, TContext> => {

const mutationKey = getReconcileDocumentMaterializationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconcileDocumentMaterialization>>, ReconcileDocumentMaterializationMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  reconcileDocumentMaterialization(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconcileDocumentMaterializationMutationResult = NonNullable<Awaited<ReturnType<typeof reconcileDocumentMaterialization>>>

    export type ReconcileDocumentMaterializationMutationError = unknown
    export type ReconcileDocumentMaterializationMutationVariables = {id: string}

    export const useReconcileDocumentMaterialization = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconcileDocumentMaterialization>>, TError,ReconcileDocumentMaterializationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reconcileDocumentMaterialization>>,
        TError,
        ReconcileDocumentMaterializationMutationVariables,
        TContext
      > => {
      return useMutation(getReconcileDocumentMaterializationMutationOptions(options));
    }

export const getListM365LibrariesUrl = (scope: 'tenant' | 'entity',
    resourceId: string,
    params: ListM365LibrariesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/m365/${scope}/connections/${resourceId}/libraries?${stringifiedParams}` : `/api/m365/${scope}/connections/${resourceId}/libraries`
}

export const listM365Libraries = async (scope: 'tenant' | 'entity',
    resourceId: string,
    params: ListM365LibrariesParams, options?: Parameters<typeof customFetch>[1]): Promise<M365LibraryList> => {

  return customFetch<M365LibraryList>(getListM365LibrariesUrl(scope,resourceId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListM365LibrariesQueryKey = (scope: 'tenant' | 'entity',
    resourceId: string,
    params?: ListM365LibrariesParams,) => {
    return [
    `/api/m365/${scope}/connections/${resourceId}/libraries`, ...(params ? [params] : [])
    ] as const;
    }


export const getListM365LibrariesQueryOptions = <TData = Awaited<ReturnType<typeof listM365Libraries>>, TError = void>(scope: 'tenant' | 'entity',
    resourceId: string,
    params: ListM365LibrariesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listM365Libraries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListM365LibrariesQueryKey(scope,resourceId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listM365Libraries>>> = ({ signal }) => listM365Libraries(scope,resourceId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: scope !== null && scope !== undefined && resourceId !== null && resourceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listM365Libraries>>, TError, TData> & { queryKey: QueryKey }
}

export type ListM365LibrariesQueryResult = NonNullable<Awaited<ReturnType<typeof listM365Libraries>>>
export type ListM365LibrariesQueryError = void



export function useListM365Libraries<TData = Awaited<ReturnType<typeof listM365Libraries>>, TError = void>(
 scope: 'tenant' | 'entity',
    resourceId: string,
    params: ListM365LibrariesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listM365Libraries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListM365LibrariesQueryOptions(scope,resourceId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListM365ConnectionsUrl = (scope: 'tenant' | 'entity',
    params?: ListM365ConnectionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/m365/${scope}/connections?${stringifiedParams}` : `/api/m365/${scope}/connections`
}

export const listM365Connections = async (scope: 'tenant' | 'entity',
    params?: ListM365ConnectionsParams, options?: Parameters<typeof customFetch>[1]): Promise<M365AdminConnectionList> => {

  return customFetch<M365AdminConnectionList>(getListM365ConnectionsUrl(scope,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListM365ConnectionsQueryKey = (scope: 'tenant' | 'entity',
    params?: ListM365ConnectionsParams,) => {
    return [
    `/api/m365/${scope}/connections`, ...(params ? [params] : [])
    ] as const;
    }


export const getListM365ConnectionsQueryOptions = <TData = Awaited<ReturnType<typeof listM365Connections>>, TError = void>(scope: 'tenant' | 'entity',
    params?: ListM365ConnectionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listM365Connections>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListM365ConnectionsQueryKey(scope,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listM365Connections>>> = ({ signal }) => listM365Connections(scope,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: scope !== null && scope !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listM365Connections>>, TError, TData> & { queryKey: QueryKey }
}

export type ListM365ConnectionsQueryResult = NonNullable<Awaited<ReturnType<typeof listM365Connections>>>
export type ListM365ConnectionsQueryError = void



export function useListM365Connections<TData = Awaited<ReturnType<typeof listM365Connections>>, TError = void>(
 scope: 'tenant' | 'entity',
    params?: ListM365ConnectionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listM365Connections>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListM365ConnectionsQueryOptions(scope,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateM365ConnectionUrl = (scope: 'tenant' | 'entity',) => {




  return `/api/m365/${scope}/connections`
}

export const createM365Connection = async (scope: 'tenant' | 'entity',
    createM365ConnectionBody: CreateM365Connection, options?: Parameters<typeof customFetch>[1]): Promise<M365AdminConnection> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<M365AdminConnection>(getCreateM365ConnectionUrl(scope),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createM365ConnectionBody)
  }
);}





export const getCreateM365ConnectionMutationKey = () => ['createM365Connection'] as const;

export const getCreateM365ConnectionMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createM365Connection>>, TError,CreateM365ConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createM365Connection>>, TError,CreateM365ConnectionMutationVariables, TContext> => {

const mutationKey = getCreateM365ConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createM365Connection>>, CreateM365ConnectionMutationVariables> = (props) => {
          const {scope,data} = props ?? {};

          return  createM365Connection(scope,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateM365ConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof createM365Connection>>>
    export type CreateM365ConnectionMutationBody = CreateM365Connection
    export type CreateM365ConnectionMutationError = void
    export type CreateM365ConnectionMutationVariables = {scope: 'tenant' | 'entity';data: CreateM365Connection}

    export const useCreateM365Connection = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createM365Connection>>, TError,CreateM365ConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createM365Connection>>,
        TError,
        CreateM365ConnectionMutationVariables,
        TContext
      > => {
      return useMutation(getCreateM365ConnectionMutationOptions(options));
    }

export const getConfigureM365CredentialsUrl = (scope: 'tenant' | 'entity',
    resourceId: string,) => {




  return `/api/m365/${scope}/connections/${resourceId}/credentials`
}

export const configureM365Credentials = async (scope: 'tenant' | 'entity',
    resourceId: string,
    configureM365CredentialsBody: ConfigureM365Credentials, options?: Parameters<typeof customFetch>[1]): Promise<M365AdminConnection> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<M365AdminConnection>(getConfigureM365CredentialsUrl(scope,resourceId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(configureM365CredentialsBody)
  }
);}





export const getConfigureM365CredentialsMutationKey = () => ['configureM365Credentials'] as const;

export const getConfigureM365CredentialsMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof configureM365Credentials>>, TError,ConfigureM365CredentialsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof configureM365Credentials>>, TError,ConfigureM365CredentialsMutationVariables, TContext> => {

const mutationKey = getConfigureM365CredentialsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof configureM365Credentials>>, ConfigureM365CredentialsMutationVariables> = (props) => {
          const {scope,resourceId,data} = props ?? {};

          return  configureM365Credentials(scope,resourceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfigureM365CredentialsMutationResult = NonNullable<Awaited<ReturnType<typeof configureM365Credentials>>>
    export type ConfigureM365CredentialsMutationBody = ConfigureM365Credentials
    export type ConfigureM365CredentialsMutationError = void
    export type ConfigureM365CredentialsMutationVariables = {scope: 'tenant' | 'entity';resourceId: string;data: ConfigureM365Credentials}

    export const useConfigureM365Credentials = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof configureM365Credentials>>, TError,ConfigureM365CredentialsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof configureM365Credentials>>,
        TError,
        ConfigureM365CredentialsMutationVariables,
        TContext
      > => {
      return useMutation(getConfigureM365CredentialsMutationOptions(options));
    }

export const getDisconnectM365ConnectionUrl = (scope: 'tenant' | 'entity',
    resourceId: string,) => {




  return `/api/m365/${scope}/connections/${resourceId}/disconnect`
}

export const disconnectM365Connection = async (scope: 'tenant' | 'entity',
    resourceId: string,
    m365RevisionAction: M365RevisionAction, options?: Parameters<typeof customFetch>[1]): Promise<M365AdminConnection> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<M365AdminConnection>(getDisconnectM365ConnectionUrl(scope,resourceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(m365RevisionAction)
  }
);}





export const getDisconnectM365ConnectionMutationKey = () => ['disconnectM365Connection'] as const;

export const getDisconnectM365ConnectionMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectM365Connection>>, TError,DisconnectM365ConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof disconnectM365Connection>>, TError,DisconnectM365ConnectionMutationVariables, TContext> => {

const mutationKey = getDisconnectM365ConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof disconnectM365Connection>>, DisconnectM365ConnectionMutationVariables> = (props) => {
          const {scope,resourceId,data} = props ?? {};

          return  disconnectM365Connection(scope,resourceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DisconnectM365ConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof disconnectM365Connection>>>
    export type DisconnectM365ConnectionMutationBody = M365RevisionAction
    export type DisconnectM365ConnectionMutationError = void
    export type DisconnectM365ConnectionMutationVariables = {scope: 'tenant' | 'entity';resourceId: string;data: M365RevisionAction}

    export const useDisconnectM365Connection = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectM365Connection>>, TError,DisconnectM365ConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof disconnectM365Connection>>,
        TError,
        DisconnectM365ConnectionMutationVariables,
        TContext
      > => {
      return useMutation(getDisconnectM365ConnectionMutationOptions(options));
    }

export const getProbeM365ConnectionUrl = (scope: 'tenant' | 'entity',
    resourceId: string,) => {




  return `/api/m365/${scope}/connections/${resourceId}/probe`
}

export const probeM365Connection = async (scope: 'tenant' | 'entity',
    resourceId: string,
    probeM365ConnectionBody: ProbeM365Connection, options?: Parameters<typeof customFetch>[1]): Promise<M365AdminConnection> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<M365AdminConnection>(getProbeM365ConnectionUrl(scope,resourceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(probeM365ConnectionBody)
  }
);}





export const getProbeM365ConnectionMutationKey = () => ['probeM365Connection'] as const;

export const getProbeM365ConnectionMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof probeM365Connection>>, TError,ProbeM365ConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof probeM365Connection>>, TError,ProbeM365ConnectionMutationVariables, TContext> => {

const mutationKey = getProbeM365ConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof probeM365Connection>>, ProbeM365ConnectionMutationVariables> = (props) => {
          const {scope,resourceId,data} = props ?? {};

          return  probeM365Connection(scope,resourceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ProbeM365ConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof probeM365Connection>>>
    export type ProbeM365ConnectionMutationBody = ProbeM365Connection
    export type ProbeM365ConnectionMutationError = void
    export type ProbeM365ConnectionMutationVariables = {scope: 'tenant' | 'entity';resourceId: string;data: ProbeM365Connection}

    export const useProbeM365Connection = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof probeM365Connection>>, TError,ProbeM365ConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof probeM365Connection>>,
        TError,
        ProbeM365ConnectionMutationVariables,
        TContext
      > => {
      return useMutation(getProbeM365ConnectionMutationOptions(options));
    }

export const getStartM365ConnectionUrl = (scope: 'tenant' | 'entity',
    resourceId: string,) => {




  return `/api/m365/${scope}/connections/${resourceId}/connect`
}

export const startM365Connection = async (scope: 'tenant' | 'entity',
    resourceId: string,
    m365RevisionAction: M365RevisionAction, options?: Parameters<typeof customFetch>[1]): Promise<M365OAuthStart> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<M365OAuthStart>(getStartM365ConnectionUrl(scope,resourceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(m365RevisionAction)
  }
);}





export const getStartM365ConnectionMutationKey = () => ['startM365Connection'] as const;

export const getStartM365ConnectionMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startM365Connection>>, TError,StartM365ConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof startM365Connection>>, TError,StartM365ConnectionMutationVariables, TContext> => {

const mutationKey = getStartM365ConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof startM365Connection>>, StartM365ConnectionMutationVariables> = (props) => {
          const {scope,resourceId,data} = props ?? {};

          return  startM365Connection(scope,resourceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type StartM365ConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof startM365Connection>>>
    export type StartM365ConnectionMutationBody = M365RevisionAction
    export type StartM365ConnectionMutationError = void
    export type StartM365ConnectionMutationVariables = {scope: 'tenant' | 'entity';resourceId: string;data: M365RevisionAction}

    export const useStartM365Connection = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startM365Connection>>, TError,StartM365ConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof startM365Connection>>,
        TError,
        StartM365ConnectionMutationVariables,
        TContext
      > => {
      return useMutation(getStartM365ConnectionMutationOptions(options));
    }

export const getCompleteM365ConnectionUrl = (params: CompleteM365ConnectionParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/m365/oauth/callback?${stringifiedParams}` : `/api/m365/oauth/callback`
}

export const completeM365Connection = async (params: CompleteM365ConnectionParams, options?: Parameters<typeof customFetch>[1]): Promise<unknown> => {

  return customFetch<unknown>(getCompleteM365ConnectionUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getCompleteM365ConnectionQueryKey = (params?: CompleteM365ConnectionParams,) => {
    return [
    `/api/m365/oauth/callback`, ...(params ? [params] : [])
    ] as const;
    }


export const getCompleteM365ConnectionQueryOptions = <TData = Awaited<ReturnType<typeof completeM365Connection>>, TError = void>(params: CompleteM365ConnectionParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof completeM365Connection>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getCompleteM365ConnectionQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof completeM365Connection>>> = ({ signal }) => completeM365Connection(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof completeM365Connection>>, TError, TData> & { queryKey: QueryKey }
}

export type CompleteM365ConnectionQueryResult = NonNullable<Awaited<ReturnType<typeof completeM365Connection>>>
export type CompleteM365ConnectionQueryError = void



export function useCompleteM365Connection<TData = Awaited<ReturnType<typeof completeM365Connection>>, TError = void>(
 params: CompleteM365ConnectionParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof completeM365Connection>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getCompleteM365ConnectionQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetTenantDocumentStorageSettingsUrl = () => {




  return `/api/access/tenant/document-storage`
}

export const getTenantDocumentStorageSettings = async ( options?: Parameters<typeof customFetch>[1]): Promise<ScopedDocumentStorageSettings> => {

  return customFetch<ScopedDocumentStorageSettings>(getGetTenantDocumentStorageSettingsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenantDocumentStorageSettingsQueryKey = () => {
    return [
    `/api/access/tenant/document-storage`
    ] as const;
    }


export const getGetTenantDocumentStorageSettingsQueryOptions = <TData = Awaited<ReturnType<typeof getTenantDocumentStorageSettings>>, TError = void>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenantDocumentStorageSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenantDocumentStorageSettingsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenantDocumentStorageSettings>>> = ({ signal }) => getTenantDocumentStorageSettings({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenantDocumentStorageSettings>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTenantDocumentStorageSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof getTenantDocumentStorageSettings>>>
export type GetTenantDocumentStorageSettingsQueryError = void



export function useGetTenantDocumentStorageSettings<TData = Awaited<ReturnType<typeof getTenantDocumentStorageSettings>>, TError = void>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenantDocumentStorageSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTenantDocumentStorageSettingsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateTenantDocumentStorageSettingsUrl = () => {




  return `/api/access/tenant/document-storage`
}

export const updateTenantDocumentStorageSettings = async (updateDocumentStorageSettings: UpdateDocumentStorageSettings, options?: Parameters<typeof customFetch>[1]): Promise<ScopedDocumentStorageSettings> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ScopedDocumentStorageSettings>(getUpdateTenantDocumentStorageSettingsUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateDocumentStorageSettings)
  }
);}





export const getUpdateTenantDocumentStorageSettingsMutationKey = () => ['updateTenantDocumentStorageSettings'] as const;

export const getUpdateTenantDocumentStorageSettingsMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenantDocumentStorageSettings>>, TError,UpdateTenantDocumentStorageSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateTenantDocumentStorageSettings>>, TError,UpdateTenantDocumentStorageSettingsMutationVariables, TContext> => {

const mutationKey = getUpdateTenantDocumentStorageSettingsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateTenantDocumentStorageSettings>>, UpdateTenantDocumentStorageSettingsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateTenantDocumentStorageSettings(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateTenantDocumentStorageSettingsMutationResult = NonNullable<Awaited<ReturnType<typeof updateTenantDocumentStorageSettings>>>
    export type UpdateTenantDocumentStorageSettingsMutationBody = UpdateDocumentStorageSettings
    export type UpdateTenantDocumentStorageSettingsMutationError = void
    export type UpdateTenantDocumentStorageSettingsMutationVariables = {data: UpdateDocumentStorageSettings}

    export const useUpdateTenantDocumentStorageSettings = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenantDocumentStorageSettings>>, TError,UpdateTenantDocumentStorageSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateTenantDocumentStorageSettings>>,
        TError,
        UpdateTenantDocumentStorageSettingsMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateTenantDocumentStorageSettingsMutationOptions(options));
    }

export const getGetEntityDocumentStorageSettingsUrl = () => {




  return `/api/document-storage/settings`
}

export const getEntityDocumentStorageSettings = async ( options?: Parameters<typeof customFetch>[1]): Promise<ScopedDocumentStorageSettings> => {

  return customFetch<ScopedDocumentStorageSettings>(getGetEntityDocumentStorageSettingsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEntityDocumentStorageSettingsQueryKey = () => {
    return [
    `/api/document-storage/settings`
    ] as const;
    }


export const getGetEntityDocumentStorageSettingsQueryOptions = <TData = Awaited<ReturnType<typeof getEntityDocumentStorageSettings>>, TError = void>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityDocumentStorageSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEntityDocumentStorageSettingsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEntityDocumentStorageSettings>>> = ({ signal }) => getEntityDocumentStorageSettings({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEntityDocumentStorageSettings>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEntityDocumentStorageSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof getEntityDocumentStorageSettings>>>
export type GetEntityDocumentStorageSettingsQueryError = void



export function useGetEntityDocumentStorageSettings<TData = Awaited<ReturnType<typeof getEntityDocumentStorageSettings>>, TError = void>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityDocumentStorageSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEntityDocumentStorageSettingsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateEntityDocumentStorageSettingsUrl = () => {




  return `/api/document-storage/settings`
}

export const updateEntityDocumentStorageSettings = async (updateDocumentStorageSettings: UpdateDocumentStorageSettings, options?: Parameters<typeof customFetch>[1]): Promise<ScopedDocumentStorageSettings> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ScopedDocumentStorageSettings>(getUpdateEntityDocumentStorageSettingsUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateDocumentStorageSettings)
  }
);}





export const getUpdateEntityDocumentStorageSettingsMutationKey = () => ['updateEntityDocumentStorageSettings'] as const;

export const getUpdateEntityDocumentStorageSettingsMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEntityDocumentStorageSettings>>, TError,UpdateEntityDocumentStorageSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateEntityDocumentStorageSettings>>, TError,UpdateEntityDocumentStorageSettingsMutationVariables, TContext> => {

const mutationKey = getUpdateEntityDocumentStorageSettingsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateEntityDocumentStorageSettings>>, UpdateEntityDocumentStorageSettingsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateEntityDocumentStorageSettings(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateEntityDocumentStorageSettingsMutationResult = NonNullable<Awaited<ReturnType<typeof updateEntityDocumentStorageSettings>>>
    export type UpdateEntityDocumentStorageSettingsMutationBody = UpdateDocumentStorageSettings
    export type UpdateEntityDocumentStorageSettingsMutationError = void
    export type UpdateEntityDocumentStorageSettingsMutationVariables = {data: UpdateDocumentStorageSettings}

    export const useUpdateEntityDocumentStorageSettings = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEntityDocumentStorageSettings>>, TError,UpdateEntityDocumentStorageSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateEntityDocumentStorageSettings>>,
        TError,
        UpdateEntityDocumentStorageSettingsMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateEntityDocumentStorageSettingsMutationOptions(options));
    }

export const getGetEffectiveDocumentStorageUrl = () => {




  return `/api/document-storage/effective`
}

export const getEffectiveDocumentStorage = async ( options?: Parameters<typeof customFetch>[1]): Promise<EffectiveDocumentStorage> => {

  return customFetch<EffectiveDocumentStorage>(getGetEffectiveDocumentStorageUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEffectiveDocumentStorageQueryKey = () => {
    return [
    `/api/document-storage/effective`
    ] as const;
    }


export const getGetEffectiveDocumentStorageQueryOptions = <TData = Awaited<ReturnType<typeof getEffectiveDocumentStorage>>, TError = void>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEffectiveDocumentStorage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEffectiveDocumentStorageQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEffectiveDocumentStorage>>> = ({ signal }) => getEffectiveDocumentStorage({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEffectiveDocumentStorage>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEffectiveDocumentStorageQueryResult = NonNullable<Awaited<ReturnType<typeof getEffectiveDocumentStorage>>>
export type GetEffectiveDocumentStorageQueryError = void



export function useGetEffectiveDocumentStorage<TData = Awaited<ReturnType<typeof getEffectiveDocumentStorage>>, TError = void>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEffectiveDocumentStorage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEffectiveDocumentStorageQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetHealthUrl = () => {




  return `/api/health`
}

/**
 * @summary Liveness probe
 */
export const getHealth = async ( options?: Parameters<typeof customFetch>[1]): Promise<Health> => {

  return customFetch<Health>(getGetHealthUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetHealthQueryKey = () => {
    return [
    `/api/health`
    ] as const;
    }


export const getGetHealthQueryOptions = <TData = Awaited<ReturnType<typeof getHealth>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetHealthQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getHealth>>> = ({ signal }) => getHealth({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData> & { queryKey: QueryKey }
}

export type GetHealthQueryResult = NonNullable<Awaited<ReturnType<typeof getHealth>>>
export type GetHealthQueryError = unknown


/**
 * @summary Liveness probe
 */

export function useGetHealth<TData = Awaited<ReturnType<typeof getHealth>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetHealthQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetDashboardStatsUrl = () => {




  return `/api/dashboard/stats`
}

/**
 * @summary Aggregate counters for the dashboard stat cards
 */
export const getDashboardStats = async ( options?: Parameters<typeof customFetch>[1]): Promise<DashboardStats> => {

  return customFetch<DashboardStats>(getGetDashboardStatsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDashboardStatsQueryKey = () => {
    return [
    `/api/dashboard/stats`
    ] as const;
    }


export const getGetDashboardStatsQueryOptions = <TData = Awaited<ReturnType<typeof getDashboardStats>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDashboardStats>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDashboardStatsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboardStats>>> = ({ signal }) => getDashboardStats({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDashboardStats>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDashboardStatsQueryResult = NonNullable<Awaited<ReturnType<typeof getDashboardStats>>>
export type GetDashboardStatsQueryError = unknown


/**
 * @summary Aggregate counters for the dashboard stat cards
 */

export function useGetDashboardStats<TData = Awaited<ReturnType<typeof getDashboardStats>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDashboardStats>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDashboardStatsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetDashboardSummaryUrl = (params?: GetDashboardSummaryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/dashboard/summary?${stringifiedParams}` : `/api/dashboard/summary`
}

/**
 * Returns compact aggregates across every matching CRM row. The current authenticated tenant and entity scope is enforced by the database.
 * @summary Get exact scoped CRM dashboard totals and pipeline distribution
 */
export const getDashboardSummary = async (params?: GetDashboardSummaryParams, options?: Parameters<typeof customFetch>[1]): Promise<DashboardSummary> => {

  return customFetch<DashboardSummary>(getGetDashboardSummaryUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDashboardSummaryQueryKey = (params?: GetDashboardSummaryParams,) => {
    return [
    `/api/dashboard/summary`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetDashboardSummaryQueryOptions = <TData = Awaited<ReturnType<typeof getDashboardSummary>>, TError = Error>(params?: GetDashboardSummaryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDashboardSummary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDashboardSummaryQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboardSummary>>> = ({ signal }) => getDashboardSummary(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDashboardSummary>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDashboardSummaryQueryResult = NonNullable<Awaited<ReturnType<typeof getDashboardSummary>>>
export type GetDashboardSummaryQueryError = Error


/**
 * @summary Get exact scoped CRM dashboard totals and pipeline distribution
 */

export function useGetDashboardSummary<TData = Awaited<ReturnType<typeof getDashboardSummary>>, TError = Error>(
 params?: GetDashboardSummaryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDashboardSummary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDashboardSummaryQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetDashboardTimeseriesUrl = (params?: GetDashboardTimeseriesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/dashboard/timeseries?${stringifiedParams}` : `/api/dashboard/timeseries`
}

/**
 * @summary Daily activity series for the dashboard chart
 */
export const getDashboardTimeseries = async (params?: GetDashboardTimeseriesParams, options?: Parameters<typeof customFetch>[1]): Promise<Timeseries> => {

  return customFetch<Timeseries>(getGetDashboardTimeseriesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDashboardTimeseriesQueryKey = (params?: GetDashboardTimeseriesParams,) => {
    return [
    `/api/dashboard/timeseries`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetDashboardTimeseriesQueryOptions = <TData = Awaited<ReturnType<typeof getDashboardTimeseries>>, TError = unknown>(params?: GetDashboardTimeseriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDashboardTimeseries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDashboardTimeseriesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboardTimeseries>>> = ({ signal }) => getDashboardTimeseries(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDashboardTimeseries>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDashboardTimeseriesQueryResult = NonNullable<Awaited<ReturnType<typeof getDashboardTimeseries>>>
export type GetDashboardTimeseriesQueryError = unknown


/**
 * @summary Daily activity series for the dashboard chart
 */

export function useGetDashboardTimeseries<TData = Awaited<ReturnType<typeof getDashboardTimeseries>>, TError = unknown>(
 params?: GetDashboardTimeseriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDashboardTimeseries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDashboardTimeseriesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetHomeOverviewUrl = () => {




  return `/api/home/overview`
}

/**
 * Aggregates all four CRM audiences in PostgreSQL. When DATABASE_URL is not configured, returns the same complete shape with zero values and databaseConfigured false.
 * @summary Get cross-workspace KPIs, attention exceptions, and pipeline stage snapshots
 */
export const getHomeOverview = async ( options?: Parameters<typeof customFetch>[1]): Promise<HomeOverview> => {

  return customFetch<HomeOverview>(getGetHomeOverviewUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetHomeOverviewQueryKey = () => {
    return [
    `/api/home/overview`
    ] as const;
    }


export const getGetHomeOverviewQueryOptions = <TData = Awaited<ReturnType<typeof getHomeOverview>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHomeOverview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetHomeOverviewQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getHomeOverview>>> = ({ signal }) => getHomeOverview({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getHomeOverview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetHomeOverviewQueryResult = NonNullable<Awaited<ReturnType<typeof getHomeOverview>>>
export type GetHomeOverviewQueryError = unknown


/**
 * @summary Get cross-workspace KPIs, attention exceptions, and pipeline stage snapshots
 */

export function useGetHomeOverview<TData = Awaited<ReturnType<typeof getHomeOverview>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHomeOverview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetHomeOverviewQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetHomeBriefingUrl = (params?: GetHomeBriefingParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/home/briefing?${stringifiedParams}` : `/api/home/briefing`
}

/**
 * Generates a bounded briefing from aggregate CRM data and top action signals, then caches it for one hour. If project AI is disabled or the model call fails, a deterministic non-AI briefing is returned instead.
 * @summary Get the cached AI daily briefing for an audience or all CRM workspaces
 */
export const getHomeBriefing = async (params?: GetHomeBriefingParams, options?: Parameters<typeof customFetch>[1]): Promise<HomeBriefing> => {

  return customFetch<HomeBriefing>(getGetHomeBriefingUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetHomeBriefingQueryKey = (params?: GetHomeBriefingParams,) => {
    return [
    `/api/home/briefing`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetHomeBriefingQueryOptions = <TData = Awaited<ReturnType<typeof getHomeBriefing>>, TError = Error>(params?: GetHomeBriefingParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHomeBriefing>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetHomeBriefingQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getHomeBriefing>>> = ({ signal }) => getHomeBriefing(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getHomeBriefing>>, TError, TData> & { queryKey: QueryKey }
}

export type GetHomeBriefingQueryResult = NonNullable<Awaited<ReturnType<typeof getHomeBriefing>>>
export type GetHomeBriefingQueryError = Error


/**
 * @summary Get the cached AI daily briefing for an audience or all CRM workspaces
 */

export function useGetHomeBriefing<TData = Awaited<ReturnType<typeof getHomeBriefing>>, TError = Error>(
 params?: GetHomeBriefingParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHomeBriefing>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetHomeBriefingQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRefreshHomeBriefingUrl = (params?: RefreshHomeBriefingParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/home/briefing/refresh?${stringifiedParams}` : `/api/home/briefing/refresh`
}

/**
 * Bypasses only this audience's one-hour briefing cache and generates a fresh AI or deterministic briefing.
 * @summary Regenerate only the selected audience or overall daily briefing
 */
export const refreshHomeBriefing = async (params?: RefreshHomeBriefingParams, options?: Parameters<typeof customFetch>[1]): Promise<HomeBriefing> => {

  return customFetch<HomeBriefing>(getRefreshHomeBriefingUrl(params),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRefreshHomeBriefingMutationKey = () => ['refreshHomeBriefing'] as const;

export const getRefreshHomeBriefingMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof refreshHomeBriefing>>, TError,RefreshHomeBriefingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof refreshHomeBriefing>>, TError,RefreshHomeBriefingMutationVariables, TContext> => {

const mutationKey = getRefreshHomeBriefingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof refreshHomeBriefing>>, RefreshHomeBriefingMutationVariables> = (props) => {
          const {params} = props ?? {};

          return  refreshHomeBriefing(params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RefreshHomeBriefingMutationResult = NonNullable<Awaited<ReturnType<typeof refreshHomeBriefing>>>

    export type RefreshHomeBriefingMutationError = Error
    export type RefreshHomeBriefingMutationVariables = {params?: RefreshHomeBriefingParams}

    /**
 * @summary Regenerate only the selected audience or overall daily briefing
 */
export const useRefreshHomeBriefing = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof refreshHomeBriefing>>, TError,RefreshHomeBriefingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof refreshHomeBriefing>>,
        TError,
        RefreshHomeBriefingMutationVariables,
        TContext
      > => {
      return useMutation(getRefreshHomeBriefingMutationOptions(options));
    }

export const getGetCatalogueHierarchyUrl = (params?: GetCatalogueHierarchyParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/catalogue/hierarchy?${stringifiedParams}` : `/api/catalogue/hierarchy`
}

/**
 * @summary Full nested catalogue tree
 */
export const getCatalogueHierarchy = async (params?: GetCatalogueHierarchyParams, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueHierarchy> => {

  return customFetch<CatalogueHierarchy>(getGetCatalogueHierarchyUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetCatalogueHierarchyQueryKey = (params?: GetCatalogueHierarchyParams,) => {
    return [
    `/api/catalogue/hierarchy`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetCatalogueHierarchyQueryOptions = <TData = Awaited<ReturnType<typeof getCatalogueHierarchy>>, TError = unknown>(params?: GetCatalogueHierarchyParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCatalogueHierarchy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCatalogueHierarchyQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCatalogueHierarchy>>> = ({ signal }) => getCatalogueHierarchy(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCatalogueHierarchy>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCatalogueHierarchyQueryResult = NonNullable<Awaited<ReturnType<typeof getCatalogueHierarchy>>>
export type GetCatalogueHierarchyQueryError = unknown


/**
 * @summary Full nested catalogue tree
 */

export function useGetCatalogueHierarchy<TData = Awaited<ReturnType<typeof getCatalogueHierarchy>>, TError = unknown>(
 params?: GetCatalogueHierarchyParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCatalogueHierarchy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCatalogueHierarchyQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPublicCatalogueUrl = () => {




  return `/api/public/catalogue`
}

/**
 * @summary Published catalogue hierarchy (public, unauthenticated)
 */
export const getPublicCatalogue = async ( options?: Parameters<typeof customFetch>[1]): Promise<PublicCatalogueHierarchy> => {

  return customFetch<PublicCatalogueHierarchy>(getGetPublicCatalogueUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPublicCatalogueQueryKey = () => {
    return [
    `/api/public/catalogue`
    ] as const;
    }


export const getGetPublicCatalogueQueryOptions = <TData = Awaited<ReturnType<typeof getPublicCatalogue>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicCatalogue>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPublicCatalogueQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPublicCatalogue>>> = ({ signal }) => getPublicCatalogue({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPublicCatalogue>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPublicCatalogueQueryResult = NonNullable<Awaited<ReturnType<typeof getPublicCatalogue>>>
export type GetPublicCatalogueQueryError = unknown


/**
 * @summary Published catalogue hierarchy (public, unauthenticated)
 */

export function useGetPublicCatalogue<TData = Awaited<ReturnType<typeof getPublicCatalogue>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicCatalogue>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPublicCatalogueQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListPublicCatalogueServicesUrl = () => {




  return `/api/public/catalogue/services`
}

/**
 * @summary Active catalogue services (public, unauthenticated)
 */
export const listPublicCatalogueServices = async ( options?: Parameters<typeof customFetch>[1]): Promise<CatalogueServiceList> => {

  return customFetch<CatalogueServiceList>(getListPublicCatalogueServicesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPublicCatalogueServicesQueryKey = () => {
    return [
    `/api/public/catalogue/services`
    ] as const;
    }


export const getListPublicCatalogueServicesQueryOptions = <TData = Awaited<ReturnType<typeof listPublicCatalogueServices>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPublicCatalogueServices>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPublicCatalogueServicesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPublicCatalogueServices>>> = ({ signal }) => listPublicCatalogueServices({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPublicCatalogueServices>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPublicCatalogueServicesQueryResult = NonNullable<Awaited<ReturnType<typeof listPublicCatalogueServices>>>
export type ListPublicCatalogueServicesQueryError = unknown


/**
 * @summary Active catalogue services (public, unauthenticated)
 */

export function useListPublicCatalogueServices<TData = Awaited<ReturnType<typeof listPublicCatalogueServices>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPublicCatalogueServices>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPublicCatalogueServicesQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSubmitPublicQuoteRequestUrl = () => {




  return `/api/public/quotes`
}

/**
 * @summary Guest quote request submission (public, unauthenticated)
 */
export const submitPublicQuoteRequest = async (publicQuoteSubmission: PublicQuoteSubmission, options?: Parameters<typeof customFetch>[1]): Promise<PublicQuoteConfirmation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PublicQuoteConfirmation>(getSubmitPublicQuoteRequestUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(publicQuoteSubmission)
  }
);}





export const getSubmitPublicQuoteRequestMutationKey = () => ['submitPublicQuoteRequest'] as const;

export const getSubmitPublicQuoteRequestMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitPublicQuoteRequest>>, TError,SubmitPublicQuoteRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof submitPublicQuoteRequest>>, TError,SubmitPublicQuoteRequestMutationVariables, TContext> => {

const mutationKey = getSubmitPublicQuoteRequestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof submitPublicQuoteRequest>>, SubmitPublicQuoteRequestMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  submitPublicQuoteRequest(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SubmitPublicQuoteRequestMutationResult = NonNullable<Awaited<ReturnType<typeof submitPublicQuoteRequest>>>
    export type SubmitPublicQuoteRequestMutationBody = PublicQuoteSubmission
    export type SubmitPublicQuoteRequestMutationError = Error
    export type SubmitPublicQuoteRequestMutationVariables = {data: PublicQuoteSubmission}

    /**
 * @summary Guest quote request submission (public, unauthenticated)
 */
export const useSubmitPublicQuoteRequest = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitPublicQuoteRequest>>, TError,SubmitPublicQuoteRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof submitPublicQuoteRequest>>,
        TError,
        SubmitPublicQuoteRequestMutationVariables,
        TContext
      > => {
      return useMutation(getSubmitPublicQuoteRequestMutationOptions(options));
    }

export const getListPriceListsUrl = (params?: ListPriceListsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/price-lists?${stringifiedParams}` : `/api/price-lists`
}

/**
 * @summary List entity price lists
 */
export const listPriceLists = async (params?: ListPriceListsParams, options?: Parameters<typeof customFetch>[1]): Promise<PriceListList> => {

  return customFetch<PriceListList>(getListPriceListsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPriceListsQueryKey = (params?: ListPriceListsParams,) => {
    return [
    `/api/price-lists`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPriceListsQueryOptions = <TData = Awaited<ReturnType<typeof listPriceLists>>, TError = unknown>(params?: ListPriceListsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPriceLists>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPriceListsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPriceLists>>> = ({ signal }) => listPriceLists(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPriceLists>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPriceListsQueryResult = NonNullable<Awaited<ReturnType<typeof listPriceLists>>>
export type ListPriceListsQueryError = unknown


/**
 * @summary List entity price lists
 */

export function useListPriceLists<TData = Awaited<ReturnType<typeof listPriceLists>>, TError = unknown>(
 params?: ListPriceListsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPriceLists>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPriceListsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreatePriceListUrl = () => {




  return `/api/price-lists`
}

/**
 * @summary Create an entity price list
 */
export const createPriceList = async (priceListCreate: PriceListCreate, options?: Parameters<typeof customFetch>[1]): Promise<PriceList> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PriceList>(getCreatePriceListUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(priceListCreate)
  }
);}





export const getCreatePriceListMutationKey = () => ['createPriceList'] as const;

export const getCreatePriceListMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPriceList>>, TError,CreatePriceListMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createPriceList>>, TError,CreatePriceListMutationVariables, TContext> => {

const mutationKey = getCreatePriceListMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createPriceList>>, CreatePriceListMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createPriceList(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreatePriceListMutationResult = NonNullable<Awaited<ReturnType<typeof createPriceList>>>
    export type CreatePriceListMutationBody = PriceListCreate
    export type CreatePriceListMutationError = Error
    export type CreatePriceListMutationVariables = {data: PriceListCreate}

    /**
 * @summary Create an entity price list
 */
export const useCreatePriceList = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPriceList>>, TError,CreatePriceListMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createPriceList>>,
        TError,
        CreatePriceListMutationVariables,
        TContext
      > => {
      return useMutation(getCreatePriceListMutationOptions(options));
    }

export const getResolveAccountPriceListUrl = (params: ResolveAccountPriceListParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/price-lists/resolve?${stringifiedParams}` : `/api/price-lists/resolve`
}

/**
 * @summary Resolve the active price list for an account classification
 */
export const resolveAccountPriceList = async (params: ResolveAccountPriceListParams, options?: Parameters<typeof customFetch>[1]): Promise<PriceList> => {

  return customFetch<PriceList>(getResolveAccountPriceListUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getResolveAccountPriceListQueryKey = (params?: ResolveAccountPriceListParams,) => {
    return [
    `/api/price-lists/resolve`, ...(params ? [params] : [])
    ] as const;
    }


export const getResolveAccountPriceListQueryOptions = <TData = Awaited<ReturnType<typeof resolveAccountPriceList>>, TError = Error>(params: ResolveAccountPriceListParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof resolveAccountPriceList>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getResolveAccountPriceListQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof resolveAccountPriceList>>> = ({ signal }) => resolveAccountPriceList(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof resolveAccountPriceList>>, TError, TData> & { queryKey: QueryKey }
}

export type ResolveAccountPriceListQueryResult = NonNullable<Awaited<ReturnType<typeof resolveAccountPriceList>>>
export type ResolveAccountPriceListQueryError = Error


/**
 * @summary Resolve the active price list for an account classification
 */

export function useResolveAccountPriceList<TData = Awaited<ReturnType<typeof resolveAccountPriceList>>, TError = Error>(
 params: ResolveAccountPriceListParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof resolveAccountPriceList>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getResolveAccountPriceListQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPriceListUrl = (id: string,) => {




  return `/api/price-lists/${id}`
}

/**
 * @summary Get one entity price list
 */
export const getPriceList = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<PriceList> => {

  return customFetch<PriceList>(getGetPriceListUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPriceListQueryKey = (id: string,) => {
    return [
    `/api/price-lists/${id}`
    ] as const;
    }


export const getGetPriceListQueryOptions = <TData = Awaited<ReturnType<typeof getPriceList>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPriceList>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPriceListQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPriceList>>> = ({ signal }) => getPriceList(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPriceList>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPriceListQueryResult = NonNullable<Awaited<ReturnType<typeof getPriceList>>>
export type GetPriceListQueryError = Error


/**
 * @summary Get one entity price list
 */

export function useGetPriceList<TData = Awaited<ReturnType<typeof getPriceList>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPriceList>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPriceListQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdatePriceListUrl = (id: string,) => {




  return `/api/price-lists/${id}`
}

/**
 * @summary Update, archive, or make a price list the default
 */
export const updatePriceList = async (id: string,
    priceListUpdate: PriceListUpdate, options?: Parameters<typeof customFetch>[1]): Promise<PriceList> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PriceList>(getUpdatePriceListUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(priceListUpdate)
  }
);}





export const getUpdatePriceListMutationKey = () => ['updatePriceList'] as const;

export const getUpdatePriceListMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePriceList>>, TError,UpdatePriceListMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updatePriceList>>, TError,UpdatePriceListMutationVariables, TContext> => {

const mutationKey = getUpdatePriceListMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updatePriceList>>, UpdatePriceListMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updatePriceList(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdatePriceListMutationResult = NonNullable<Awaited<ReturnType<typeof updatePriceList>>>
    export type UpdatePriceListMutationBody = PriceListUpdate
    export type UpdatePriceListMutationError = Error
    export type UpdatePriceListMutationVariables = {id: string;data: PriceListUpdate}

    /**
 * @summary Update, archive, or make a price list the default
 */
export const useUpdatePriceList = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePriceList>>, TError,UpdatePriceListMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updatePriceList>>,
        TError,
        UpdatePriceListMutationVariables,
        TContext
      > => {
      return useMutation(getUpdatePriceListMutationOptions(options));
    }

export const getListPriceListItemsUrl = (id: string,
    params?: ListPriceListItemsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/price-lists/${id}/items?${stringifiedParams}` : `/api/price-lists/${id}/items`
}

/**
 * @summary List stored items for a price list
 */
export const listPriceListItems = async (id: string,
    params?: ListPriceListItemsParams, options?: Parameters<typeof customFetch>[1]): Promise<PriceListItemList> => {

  return customFetch<PriceListItemList>(getListPriceListItemsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPriceListItemsQueryKey = (id: string,
    params?: ListPriceListItemsParams,) => {
    return [
    `/api/price-lists/${id}/items`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPriceListItemsQueryOptions = <TData = Awaited<ReturnType<typeof listPriceListItems>>, TError = unknown>(id: string,
    params?: ListPriceListItemsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPriceListItems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPriceListItemsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPriceListItems>>> = ({ signal }) => listPriceListItems(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPriceListItems>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPriceListItemsQueryResult = NonNullable<Awaited<ReturnType<typeof listPriceListItems>>>
export type ListPriceListItemsQueryError = unknown


/**
 * @summary List stored items for a price list
 */

export function useListPriceListItems<TData = Awaited<ReturnType<typeof listPriceListItems>>, TError = unknown>(
 id: string,
    params?: ListPriceListItemsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPriceListItems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPriceListItemsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getBulkUpsertPriceListItemsUrl = (id: string,) => {




  return `/api/price-lists/${id}/items`
}

/**
 * @summary Upsert up to 100 unique price-list items
 */
export const bulkUpsertPriceListItems = async (id: string,
    priceListItemBulkUpsert: PriceListItemBulkUpsert, options?: Parameters<typeof customFetch>[1]): Promise<PriceListItemBulkResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PriceListItemBulkResult>(getBulkUpsertPriceListItemsUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(priceListItemBulkUpsert)
  }
);}





export const getBulkUpsertPriceListItemsMutationKey = () => ['bulkUpsertPriceListItems'] as const;

export const getBulkUpsertPriceListItemsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof bulkUpsertPriceListItems>>, TError,BulkUpsertPriceListItemsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof bulkUpsertPriceListItems>>, TError,BulkUpsertPriceListItemsMutationVariables, TContext> => {

const mutationKey = getBulkUpsertPriceListItemsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof bulkUpsertPriceListItems>>, BulkUpsertPriceListItemsMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  bulkUpsertPriceListItems(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type BulkUpsertPriceListItemsMutationResult = NonNullable<Awaited<ReturnType<typeof bulkUpsertPriceListItems>>>
    export type BulkUpsertPriceListItemsMutationBody = PriceListItemBulkUpsert
    export type BulkUpsertPriceListItemsMutationError = Error
    export type BulkUpsertPriceListItemsMutationVariables = {id: string;data: PriceListItemBulkUpsert}

    /**
 * @summary Upsert up to 100 unique price-list items
 */
export const useBulkUpsertPriceListItems = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof bulkUpsertPriceListItems>>, TError,BulkUpsertPriceListItemsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof bulkUpsertPriceListItems>>,
        TError,
        BulkUpsertPriceListItemsMutationVariables,
        TContext
      > => {
      return useMutation(getBulkUpsertPriceListItemsMutationOptions(options));
    }

export const getListPriceListRatesUrl = (id: string,
    params?: ListPriceListRatesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/pricing/price-lists/${id}/rates?${stringifiedParams}` : `/api/pricing/price-lists/${id}/rates`
}

/**
 * @summary List the roles and sell rates on a price list
 */
export const listPriceListRates = async (id: string,
    params?: ListPriceListRatesParams, options?: Parameters<typeof customFetch>[1]): Promise<PriceListRateList> => {

  return customFetch<PriceListRateList>(getListPriceListRatesUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPriceListRatesQueryKey = (id: string,
    params?: ListPriceListRatesParams,) => {
    return [
    `/api/pricing/price-lists/${id}/rates`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPriceListRatesQueryOptions = <TData = Awaited<ReturnType<typeof listPriceListRates>>, TError = unknown>(id: string,
    params?: ListPriceListRatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPriceListRates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPriceListRatesQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPriceListRates>>> = ({ signal }) => listPriceListRates(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPriceListRates>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPriceListRatesQueryResult = NonNullable<Awaited<ReturnType<typeof listPriceListRates>>>
export type ListPriceListRatesQueryError = unknown


/**
 * @summary List the roles and sell rates on a price list
 */

export function useListPriceListRates<TData = Awaited<ReturnType<typeof listPriceListRates>>, TError = unknown>(
 id: string,
    params?: ListPriceListRatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPriceListRates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPriceListRatesQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getBulkUpsertPriceListRatesUrl = (id: string,) => {




  return `/api/pricing/price-lists/${id}/rates`
}

/**
 * @summary Upsert up to 100 unique rate-card roles
 */
export const bulkUpsertPriceListRates = async (id: string,
    priceListRateBulkUpsert: PriceListRateBulkUpsert, options?: Parameters<typeof customFetch>[1]): Promise<PriceListRateBulkResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PriceListRateBulkResult>(getBulkUpsertPriceListRatesUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(priceListRateBulkUpsert)
  }
);}





export const getBulkUpsertPriceListRatesMutationKey = () => ['bulkUpsertPriceListRates'] as const;

export const getBulkUpsertPriceListRatesMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof bulkUpsertPriceListRates>>, TError,BulkUpsertPriceListRatesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof bulkUpsertPriceListRates>>, TError,BulkUpsertPriceListRatesMutationVariables, TContext> => {

const mutationKey = getBulkUpsertPriceListRatesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof bulkUpsertPriceListRates>>, BulkUpsertPriceListRatesMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  bulkUpsertPriceListRates(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type BulkUpsertPriceListRatesMutationResult = NonNullable<Awaited<ReturnType<typeof bulkUpsertPriceListRates>>>
    export type BulkUpsertPriceListRatesMutationBody = PriceListRateBulkUpsert
    export type BulkUpsertPriceListRatesMutationError = Error
    export type BulkUpsertPriceListRatesMutationVariables = {id: string;data: PriceListRateBulkUpsert}

    /**
 * @summary Upsert up to 100 unique rate-card roles
 */
export const useBulkUpsertPriceListRates = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof bulkUpsertPriceListRates>>, TError,BulkUpsertPriceListRatesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof bulkUpsertPriceListRates>>,
        TError,
        BulkUpsertPriceListRatesMutationVariables,
        TContext
      > => {
      return useMutation(getBulkUpsertPriceListRatesMutationOptions(options));
    }

export const getCopyPriceListUrl = (id: string,) => {




  return `/api/price-lists/${id}/copy`
}

/**
 * @summary Copy a price list and all of its item prices
 */
export const copyPriceList = async (id: string,
    priceListCopy: PriceListCopy, options?: Parameters<typeof customFetch>[1]): Promise<PriceList> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PriceList>(getCopyPriceListUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(priceListCopy)
  }
);}





export const getCopyPriceListMutationKey = () => ['copyPriceList'] as const;

export const getCopyPriceListMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof copyPriceList>>, TError,CopyPriceListMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof copyPriceList>>, TError,CopyPriceListMutationVariables, TContext> => {

const mutationKey = getCopyPriceListMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof copyPriceList>>, CopyPriceListMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  copyPriceList(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CopyPriceListMutationResult = NonNullable<Awaited<ReturnType<typeof copyPriceList>>>
    export type CopyPriceListMutationBody = PriceListCopy
    export type CopyPriceListMutationError = unknown
    export type CopyPriceListMutationVariables = {id: string;data: PriceListCopy}

    /**
 * @summary Copy a price list and all of its item prices
 */
export const useCopyPriceList = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof copyPriceList>>, TError,CopyPriceListMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof copyPriceList>>,
        TError,
        CopyPriceListMutationVariables,
        TContext
      > => {
      return useMutation(getCopyPriceListMutationOptions(options));
    }

export const getGetQuotablePriceBookUrl = (id: string,
    params?: GetQuotablePriceBookParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/price-lists/${id}/price-book?${stringifiedParams}` : `/api/price-lists/${id}/price-book`
}

/**
 * @summary List active quotable catalogue entries with prices and requirements
 */
export const getQuotablePriceBook = async (id: string,
    params?: GetQuotablePriceBookParams, options?: Parameters<typeof customFetch>[1]): Promise<PriceBook> => {

  return customFetch<PriceBook>(getGetQuotablePriceBookUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetQuotablePriceBookQueryKey = (id: string,
    params?: GetQuotablePriceBookParams,) => {
    return [
    `/api/price-lists/${id}/price-book`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetQuotablePriceBookQueryOptions = <TData = Awaited<ReturnType<typeof getQuotablePriceBook>>, TError = unknown>(id: string,
    params?: GetQuotablePriceBookParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getQuotablePriceBook>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetQuotablePriceBookQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getQuotablePriceBook>>> = ({ signal }) => getQuotablePriceBook(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getQuotablePriceBook>>, TError, TData> & { queryKey: QueryKey }
}

export type GetQuotablePriceBookQueryResult = NonNullable<Awaited<ReturnType<typeof getQuotablePriceBook>>>
export type GetQuotablePriceBookQueryError = unknown


/**
 * @summary List active quotable catalogue entries with prices and requirements
 */

export function useGetQuotablePriceBook<TData = Awaited<ReturnType<typeof getQuotablePriceBook>>, TError = unknown>(
 id: string,
    params?: GetQuotablePriceBookParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getQuotablePriceBook>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetQuotablePriceBookQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetQuoteSettingsUrl = () => {




  return `/api/quote-settings`
}

/**
 * @summary Get entity quote defaults and PDF branding
 */
export const getQuoteSettings = async ( options?: Parameters<typeof customFetch>[1]): Promise<QuoteSettings> => {

  return customFetch<QuoteSettings>(getGetQuoteSettingsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetQuoteSettingsQueryKey = () => {
    return [
    `/api/quote-settings`
    ] as const;
    }


export const getGetQuoteSettingsQueryOptions = <TData = Awaited<ReturnType<typeof getQuoteSettings>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getQuoteSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetQuoteSettingsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getQuoteSettings>>> = ({ signal }) => getQuoteSettings({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getQuoteSettings>>, TError, TData> & { queryKey: QueryKey }
}

export type GetQuoteSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof getQuoteSettings>>>
export type GetQuoteSettingsQueryError = unknown


/**
 * @summary Get entity quote defaults and PDF branding
 */

export function useGetQuoteSettings<TData = Awaited<ReturnType<typeof getQuoteSettings>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getQuoteSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetQuoteSettingsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateQuoteSettingsUrl = () => {




  return `/api/quote-settings`
}

/**
 * @summary Save entity quote defaults and PDF branding
 */
export const updateQuoteSettings = async (quoteSettingsUpdate: QuoteSettingsUpdate, options?: Parameters<typeof customFetch>[1]): Promise<QuoteSettings> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<QuoteSettings>(getUpdateQuoteSettingsUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(quoteSettingsUpdate)
  }
);}





export const getUpdateQuoteSettingsMutationKey = () => ['updateQuoteSettings'] as const;

export const getUpdateQuoteSettingsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateQuoteSettings>>, TError,UpdateQuoteSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateQuoteSettings>>, TError,UpdateQuoteSettingsMutationVariables, TContext> => {

const mutationKey = getUpdateQuoteSettingsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateQuoteSettings>>, UpdateQuoteSettingsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateQuoteSettings(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateQuoteSettingsMutationResult = NonNullable<Awaited<ReturnType<typeof updateQuoteSettings>>>
    export type UpdateQuoteSettingsMutationBody = QuoteSettingsUpdate
    export type UpdateQuoteSettingsMutationError = Error
    export type UpdateQuoteSettingsMutationVariables = {data: QuoteSettingsUpdate}

    /**
 * @summary Save entity quote defaults and PDF branding
 */
export const useUpdateQuoteSettings = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateQuoteSettings>>, TError,UpdateQuoteSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateQuoteSettings>>,
        TError,
        UpdateQuoteSettingsMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateQuoteSettingsMutationOptions(options));
    }

export const getCreateCatalogueCategoryUrl = () => {




  return `/api/catalogue/categories`
}

/**
 * @summary Create a catalogue category
 */
export const createCatalogueCategory = async (catalogueCategoryCreate: CatalogueCategoryCreate, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueCategory> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CatalogueCategory>(getCreateCatalogueCategoryUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(catalogueCategoryCreate)
  }
);}





export const getCreateCatalogueCategoryMutationKey = () => ['createCatalogueCategory'] as const;

export const getCreateCatalogueCategoryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCatalogueCategory>>, TError,CreateCatalogueCategoryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createCatalogueCategory>>, TError,CreateCatalogueCategoryMutationVariables, TContext> => {

const mutationKey = getCreateCatalogueCategoryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createCatalogueCategory>>, CreateCatalogueCategoryMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createCatalogueCategory(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateCatalogueCategoryMutationResult = NonNullable<Awaited<ReturnType<typeof createCatalogueCategory>>>
    export type CreateCatalogueCategoryMutationBody = CatalogueCategoryCreate
    export type CreateCatalogueCategoryMutationError = Error
    export type CreateCatalogueCategoryMutationVariables = {data: CatalogueCategoryCreate}

    /**
 * @summary Create a catalogue category
 */
export const useCreateCatalogueCategory = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCatalogueCategory>>, TError,CreateCatalogueCategoryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createCatalogueCategory>>,
        TError,
        CreateCatalogueCategoryMutationVariables,
        TContext
      > => {
      return useMutation(getCreateCatalogueCategoryMutationOptions(options));
    }

export const getUpdateCatalogueCategoryUrl = (id: string,) => {




  return `/api/catalogue/categories/${id}`
}

/**
 * @summary Update a catalogue category
 */
export const updateCatalogueCategory = async (id: string,
    catalogueCategoryUpdate: CatalogueCategoryUpdate, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueCategory> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CatalogueCategory>(getUpdateCatalogueCategoryUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(catalogueCategoryUpdate)
  }
);}





export const getUpdateCatalogueCategoryMutationKey = () => ['updateCatalogueCategory'] as const;

export const getUpdateCatalogueCategoryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueCategory>>, TError,UpdateCatalogueCategoryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueCategory>>, TError,UpdateCatalogueCategoryMutationVariables, TContext> => {

const mutationKey = getUpdateCatalogueCategoryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateCatalogueCategory>>, UpdateCatalogueCategoryMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateCatalogueCategory(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateCatalogueCategoryMutationResult = NonNullable<Awaited<ReturnType<typeof updateCatalogueCategory>>>
    export type UpdateCatalogueCategoryMutationBody = CatalogueCategoryUpdate
    export type UpdateCatalogueCategoryMutationError = Error
    export type UpdateCatalogueCategoryMutationVariables = {id: string;data: CatalogueCategoryUpdate}

    /**
 * @summary Update a catalogue category
 */
export const useUpdateCatalogueCategory = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueCategory>>, TError,UpdateCatalogueCategoryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateCatalogueCategory>>,
        TError,
        UpdateCatalogueCategoryMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateCatalogueCategoryMutationOptions(options));
    }

export const getDeleteCatalogueCategoryUrl = (id: string,) => {




  return `/api/catalogue/categories/${id}`
}

/**
 * @summary Delete a catalogue category
 */
export const deleteCatalogueCategory = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteCatalogueCategoryUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteCatalogueCategoryMutationKey = () => ['deleteCatalogueCategory'] as const;

export const getDeleteCatalogueCategoryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueCategory>>, TError,DeleteCatalogueCategoryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueCategory>>, TError,DeleteCatalogueCategoryMutationVariables, TContext> => {

const mutationKey = getDeleteCatalogueCategoryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteCatalogueCategory>>, DeleteCatalogueCategoryMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteCatalogueCategory(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteCatalogueCategoryMutationResult = NonNullable<Awaited<ReturnType<typeof deleteCatalogueCategory>>>

    export type DeleteCatalogueCategoryMutationError = Error
    export type DeleteCatalogueCategoryMutationVariables = {id: string}

    /**
 * @summary Delete a catalogue category
 */
export const useDeleteCatalogueCategory = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueCategory>>, TError,DeleteCatalogueCategoryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteCatalogueCategory>>,
        TError,
        DeleteCatalogueCategoryMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteCatalogueCategoryMutationOptions(options));
    }

export const getCreateCatalogueSolutionUrl = () => {




  return `/api/catalogue/solutions`
}

/**
 * @summary Create a solution
 */
export const createCatalogueSolution = async (catalogueSolutionCreate: CatalogueSolutionCreate, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueSolution> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CatalogueSolution>(getCreateCatalogueSolutionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(catalogueSolutionCreate)
  }
);}





export const getCreateCatalogueSolutionMutationKey = () => ['createCatalogueSolution'] as const;

export const getCreateCatalogueSolutionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCatalogueSolution>>, TError,CreateCatalogueSolutionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createCatalogueSolution>>, TError,CreateCatalogueSolutionMutationVariables, TContext> => {

const mutationKey = getCreateCatalogueSolutionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createCatalogueSolution>>, CreateCatalogueSolutionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createCatalogueSolution(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateCatalogueSolutionMutationResult = NonNullable<Awaited<ReturnType<typeof createCatalogueSolution>>>
    export type CreateCatalogueSolutionMutationBody = CatalogueSolutionCreate
    export type CreateCatalogueSolutionMutationError = Error
    export type CreateCatalogueSolutionMutationVariables = {data: CatalogueSolutionCreate}

    /**
 * @summary Create a solution
 */
export const useCreateCatalogueSolution = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCatalogueSolution>>, TError,CreateCatalogueSolutionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createCatalogueSolution>>,
        TError,
        CreateCatalogueSolutionMutationVariables,
        TContext
      > => {
      return useMutation(getCreateCatalogueSolutionMutationOptions(options));
    }

export const getUpdateCatalogueSolutionUrl = (id: string,) => {




  return `/api/catalogue/solutions/${id}`
}

/**
 * @summary Update a solution
 */
export const updateCatalogueSolution = async (id: string,
    catalogueSolutionUpdate: CatalogueSolutionUpdate, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueSolution> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CatalogueSolution>(getUpdateCatalogueSolutionUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(catalogueSolutionUpdate)
  }
);}





export const getUpdateCatalogueSolutionMutationKey = () => ['updateCatalogueSolution'] as const;

export const getUpdateCatalogueSolutionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueSolution>>, TError,UpdateCatalogueSolutionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueSolution>>, TError,UpdateCatalogueSolutionMutationVariables, TContext> => {

const mutationKey = getUpdateCatalogueSolutionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateCatalogueSolution>>, UpdateCatalogueSolutionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateCatalogueSolution(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateCatalogueSolutionMutationResult = NonNullable<Awaited<ReturnType<typeof updateCatalogueSolution>>>
    export type UpdateCatalogueSolutionMutationBody = CatalogueSolutionUpdate
    export type UpdateCatalogueSolutionMutationError = Error
    export type UpdateCatalogueSolutionMutationVariables = {id: string;data: CatalogueSolutionUpdate}

    /**
 * @summary Update a solution
 */
export const useUpdateCatalogueSolution = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueSolution>>, TError,UpdateCatalogueSolutionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateCatalogueSolution>>,
        TError,
        UpdateCatalogueSolutionMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateCatalogueSolutionMutationOptions(options));
    }

export const getDeleteCatalogueSolutionUrl = (id: string,) => {




  return `/api/catalogue/solutions/${id}`
}

/**
 * @summary Delete a solution
 */
export const deleteCatalogueSolution = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteCatalogueSolutionUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteCatalogueSolutionMutationKey = () => ['deleteCatalogueSolution'] as const;

export const getDeleteCatalogueSolutionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueSolution>>, TError,DeleteCatalogueSolutionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueSolution>>, TError,DeleteCatalogueSolutionMutationVariables, TContext> => {

const mutationKey = getDeleteCatalogueSolutionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteCatalogueSolution>>, DeleteCatalogueSolutionMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteCatalogueSolution(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteCatalogueSolutionMutationResult = NonNullable<Awaited<ReturnType<typeof deleteCatalogueSolution>>>

    export type DeleteCatalogueSolutionMutationError = Error
    export type DeleteCatalogueSolutionMutationVariables = {id: string}

    /**
 * @summary Delete a solution
 */
export const useDeleteCatalogueSolution = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueSolution>>, TError,DeleteCatalogueSolutionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteCatalogueSolution>>,
        TError,
        DeleteCatalogueSolutionMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteCatalogueSolutionMutationOptions(options));
    }

export const getCreateCatalogueProductUrl = () => {




  return `/api/catalogue/products`
}

/**
 * @summary Create a product
 */
export const createCatalogueProduct = async (catalogueProductCreate: CatalogueProductCreate, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueProduct> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CatalogueProduct>(getCreateCatalogueProductUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(catalogueProductCreate)
  }
);}





export const getCreateCatalogueProductMutationKey = () => ['createCatalogueProduct'] as const;

export const getCreateCatalogueProductMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCatalogueProduct>>, TError,CreateCatalogueProductMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createCatalogueProduct>>, TError,CreateCatalogueProductMutationVariables, TContext> => {

const mutationKey = getCreateCatalogueProductMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createCatalogueProduct>>, CreateCatalogueProductMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createCatalogueProduct(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateCatalogueProductMutationResult = NonNullable<Awaited<ReturnType<typeof createCatalogueProduct>>>
    export type CreateCatalogueProductMutationBody = CatalogueProductCreate
    export type CreateCatalogueProductMutationError = Error
    export type CreateCatalogueProductMutationVariables = {data: CatalogueProductCreate}

    /**
 * @summary Create a product
 */
export const useCreateCatalogueProduct = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCatalogueProduct>>, TError,CreateCatalogueProductMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createCatalogueProduct>>,
        TError,
        CreateCatalogueProductMutationVariables,
        TContext
      > => {
      return useMutation(getCreateCatalogueProductMutationOptions(options));
    }

export const getUpdateCatalogueProductUrl = (id: string,) => {




  return `/api/catalogue/products/${id}`
}

/**
 * @summary Update a product
 */
export const updateCatalogueProduct = async (id: string,
    catalogueProductUpdate: CatalogueProductUpdate, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueProduct> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CatalogueProduct>(getUpdateCatalogueProductUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(catalogueProductUpdate)
  }
);}





export const getUpdateCatalogueProductMutationKey = () => ['updateCatalogueProduct'] as const;

export const getUpdateCatalogueProductMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueProduct>>, TError,UpdateCatalogueProductMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueProduct>>, TError,UpdateCatalogueProductMutationVariables, TContext> => {

const mutationKey = getUpdateCatalogueProductMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateCatalogueProduct>>, UpdateCatalogueProductMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateCatalogueProduct(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateCatalogueProductMutationResult = NonNullable<Awaited<ReturnType<typeof updateCatalogueProduct>>>
    export type UpdateCatalogueProductMutationBody = CatalogueProductUpdate
    export type UpdateCatalogueProductMutationError = Error
    export type UpdateCatalogueProductMutationVariables = {id: string;data: CatalogueProductUpdate}

    /**
 * @summary Update a product
 */
export const useUpdateCatalogueProduct = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueProduct>>, TError,UpdateCatalogueProductMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateCatalogueProduct>>,
        TError,
        UpdateCatalogueProductMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateCatalogueProductMutationOptions(options));
    }

export const getDeleteCatalogueProductUrl = (id: string,) => {




  return `/api/catalogue/products/${id}`
}

/**
 * @summary Delete a product
 */
export const deleteCatalogueProduct = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteCatalogueProductUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteCatalogueProductMutationKey = () => ['deleteCatalogueProduct'] as const;

export const getDeleteCatalogueProductMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueProduct>>, TError,DeleteCatalogueProductMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueProduct>>, TError,DeleteCatalogueProductMutationVariables, TContext> => {

const mutationKey = getDeleteCatalogueProductMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteCatalogueProduct>>, DeleteCatalogueProductMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteCatalogueProduct(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteCatalogueProductMutationResult = NonNullable<Awaited<ReturnType<typeof deleteCatalogueProduct>>>

    export type DeleteCatalogueProductMutationError = Error
    export type DeleteCatalogueProductMutationVariables = {id: string}

    /**
 * @summary Delete a product
 */
export const useDeleteCatalogueProduct = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueProduct>>, TError,DeleteCatalogueProductMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteCatalogueProduct>>,
        TError,
        DeleteCatalogueProductMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteCatalogueProductMutationOptions(options));
    }

export const getCreateProductModuleUrl = () => {




  return `/api/catalogue/modules`
}

/**
 * @summary Create a product module
 */
export const createProductModule = async (productModuleCreate: ProductModuleCreate, options?: Parameters<typeof customFetch>[1]): Promise<ProductModule> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ProductModule>(getCreateProductModuleUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(productModuleCreate)
  }
);}





export const getCreateProductModuleMutationKey = () => ['createProductModule'] as const;

export const getCreateProductModuleMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createProductModule>>, TError,CreateProductModuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createProductModule>>, TError,CreateProductModuleMutationVariables, TContext> => {

const mutationKey = getCreateProductModuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createProductModule>>, CreateProductModuleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createProductModule(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateProductModuleMutationResult = NonNullable<Awaited<ReturnType<typeof createProductModule>>>
    export type CreateProductModuleMutationBody = ProductModuleCreate
    export type CreateProductModuleMutationError = Error
    export type CreateProductModuleMutationVariables = {data: ProductModuleCreate}

    /**
 * @summary Create a product module
 */
export const useCreateProductModule = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createProductModule>>, TError,CreateProductModuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createProductModule>>,
        TError,
        CreateProductModuleMutationVariables,
        TContext
      > => {
      return useMutation(getCreateProductModuleMutationOptions(options));
    }

export const getUpdateProductModuleUrl = (id: string,) => {




  return `/api/catalogue/modules/${id}`
}

/**
 * @summary Update a product module
 */
export const updateProductModule = async (id: string,
    productModuleUpdate: ProductModuleUpdate, options?: Parameters<typeof customFetch>[1]): Promise<ProductModule> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ProductModule>(getUpdateProductModuleUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(productModuleUpdate)
  }
);}





export const getUpdateProductModuleMutationKey = () => ['updateProductModule'] as const;

export const getUpdateProductModuleMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateProductModule>>, TError,UpdateProductModuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateProductModule>>, TError,UpdateProductModuleMutationVariables, TContext> => {

const mutationKey = getUpdateProductModuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateProductModule>>, UpdateProductModuleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateProductModule(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateProductModuleMutationResult = NonNullable<Awaited<ReturnType<typeof updateProductModule>>>
    export type UpdateProductModuleMutationBody = ProductModuleUpdate
    export type UpdateProductModuleMutationError = Error
    export type UpdateProductModuleMutationVariables = {id: string;data: ProductModuleUpdate}

    /**
 * @summary Update a product module
 */
export const useUpdateProductModule = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateProductModule>>, TError,UpdateProductModuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateProductModule>>,
        TError,
        UpdateProductModuleMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateProductModuleMutationOptions(options));
    }

export const getDeleteProductModuleUrl = (id: string,) => {




  return `/api/catalogue/modules/${id}`
}

/**
 * @summary Delete a product module
 */
export const deleteProductModule = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteProductModuleUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteProductModuleMutationKey = () => ['deleteProductModule'] as const;

export const getDeleteProductModuleMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteProductModule>>, TError,DeleteProductModuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteProductModule>>, TError,DeleteProductModuleMutationVariables, TContext> => {

const mutationKey = getDeleteProductModuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteProductModule>>, DeleteProductModuleMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteProductModule(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteProductModuleMutationResult = NonNullable<Awaited<ReturnType<typeof deleteProductModule>>>

    export type DeleteProductModuleMutationError = Error
    export type DeleteProductModuleMutationVariables = {id: string}

    /**
 * @summary Delete a product module
 */
export const useDeleteProductModule = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteProductModule>>, TError,DeleteProductModuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteProductModule>>,
        TError,
        DeleteProductModuleMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteProductModuleMutationOptions(options));
    }

export const getCreateModuleCapabilityUrl = () => {




  return `/api/catalogue/capabilities`
}

/**
 * @summary Create a module capability
 */
export const createModuleCapability = async (moduleCapabilityCreate: ModuleCapabilityCreate, options?: Parameters<typeof customFetch>[1]): Promise<ModuleCapability> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ModuleCapability>(getCreateModuleCapabilityUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(moduleCapabilityCreate)
  }
);}





export const getCreateModuleCapabilityMutationKey = () => ['createModuleCapability'] as const;

export const getCreateModuleCapabilityMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createModuleCapability>>, TError,CreateModuleCapabilityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createModuleCapability>>, TError,CreateModuleCapabilityMutationVariables, TContext> => {

const mutationKey = getCreateModuleCapabilityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createModuleCapability>>, CreateModuleCapabilityMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createModuleCapability(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateModuleCapabilityMutationResult = NonNullable<Awaited<ReturnType<typeof createModuleCapability>>>
    export type CreateModuleCapabilityMutationBody = ModuleCapabilityCreate
    export type CreateModuleCapabilityMutationError = Error
    export type CreateModuleCapabilityMutationVariables = {data: ModuleCapabilityCreate}

    /**
 * @summary Create a module capability
 */
export const useCreateModuleCapability = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createModuleCapability>>, TError,CreateModuleCapabilityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createModuleCapability>>,
        TError,
        CreateModuleCapabilityMutationVariables,
        TContext
      > => {
      return useMutation(getCreateModuleCapabilityMutationOptions(options));
    }

export const getUpdateModuleCapabilityUrl = (id: string,) => {




  return `/api/catalogue/capabilities/${id}`
}

/**
 * @summary Update a module capability
 */
export const updateModuleCapability = async (id: string,
    moduleCapabilityUpdate: ModuleCapabilityUpdate, options?: Parameters<typeof customFetch>[1]): Promise<ModuleCapability> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ModuleCapability>(getUpdateModuleCapabilityUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(moduleCapabilityUpdate)
  }
);}





export const getUpdateModuleCapabilityMutationKey = () => ['updateModuleCapability'] as const;

export const getUpdateModuleCapabilityMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateModuleCapability>>, TError,UpdateModuleCapabilityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateModuleCapability>>, TError,UpdateModuleCapabilityMutationVariables, TContext> => {

const mutationKey = getUpdateModuleCapabilityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateModuleCapability>>, UpdateModuleCapabilityMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateModuleCapability(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateModuleCapabilityMutationResult = NonNullable<Awaited<ReturnType<typeof updateModuleCapability>>>
    export type UpdateModuleCapabilityMutationBody = ModuleCapabilityUpdate
    export type UpdateModuleCapabilityMutationError = Error
    export type UpdateModuleCapabilityMutationVariables = {id: string;data: ModuleCapabilityUpdate}

    /**
 * @summary Update a module capability
 */
export const useUpdateModuleCapability = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateModuleCapability>>, TError,UpdateModuleCapabilityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateModuleCapability>>,
        TError,
        UpdateModuleCapabilityMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateModuleCapabilityMutationOptions(options));
    }

export const getDeleteModuleCapabilityUrl = (id: string,) => {




  return `/api/catalogue/capabilities/${id}`
}

/**
 * @summary Delete a module capability
 */
export const deleteModuleCapability = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteModuleCapabilityUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteModuleCapabilityMutationKey = () => ['deleteModuleCapability'] as const;

export const getDeleteModuleCapabilityMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteModuleCapability>>, TError,DeleteModuleCapabilityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteModuleCapability>>, TError,DeleteModuleCapabilityMutationVariables, TContext> => {

const mutationKey = getDeleteModuleCapabilityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteModuleCapability>>, DeleteModuleCapabilityMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteModuleCapability(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteModuleCapabilityMutationResult = NonNullable<Awaited<ReturnType<typeof deleteModuleCapability>>>

    export type DeleteModuleCapabilityMutationError = Error
    export type DeleteModuleCapabilityMutationVariables = {id: string}

    /**
 * @summary Delete a module capability
 */
export const useDeleteModuleCapability = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteModuleCapability>>, TError,DeleteModuleCapabilityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteModuleCapability>>,
        TError,
        DeleteModuleCapabilityMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteModuleCapabilityMutationOptions(options));
    }

export const getCreateProductRequirementUrl = (id: string,) => {




  return `/api/catalogue/products/${id}/requirements`
}

/**
 * @summary Add a requirement to a product
 */
export const createProductRequirement = async (id: string,
    productRequirementCreate: ProductRequirementCreate, options?: Parameters<typeof customFetch>[1]): Promise<ProductRequirement> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ProductRequirement>(getCreateProductRequirementUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(productRequirementCreate)
  }
);}





export const getCreateProductRequirementMutationKey = () => ['createProductRequirement'] as const;

export const getCreateProductRequirementMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createProductRequirement>>, TError,CreateProductRequirementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createProductRequirement>>, TError,CreateProductRequirementMutationVariables, TContext> => {

const mutationKey = getCreateProductRequirementMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createProductRequirement>>, CreateProductRequirementMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createProductRequirement(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateProductRequirementMutationResult = NonNullable<Awaited<ReturnType<typeof createProductRequirement>>>
    export type CreateProductRequirementMutationBody = ProductRequirementCreate
    export type CreateProductRequirementMutationError = Error
    export type CreateProductRequirementMutationVariables = {id: string;data: ProductRequirementCreate}

    /**
 * @summary Add a requirement to a product
 */
export const useCreateProductRequirement = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createProductRequirement>>, TError,CreateProductRequirementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createProductRequirement>>,
        TError,
        CreateProductRequirementMutationVariables,
        TContext
      > => {
      return useMutation(getCreateProductRequirementMutationOptions(options));
    }

export const getUpdateProductRequirementUrl = (id: string,) => {




  return `/api/catalogue/requirements/${id}`
}

/**
 * @summary Update a requirement
 */
export const updateProductRequirement = async (id: string,
    productRequirementUpdate: ProductRequirementUpdate, options?: Parameters<typeof customFetch>[1]): Promise<ProductRequirement> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ProductRequirement>(getUpdateProductRequirementUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(productRequirementUpdate)
  }
);}





export const getUpdateProductRequirementMutationKey = () => ['updateProductRequirement'] as const;

export const getUpdateProductRequirementMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateProductRequirement>>, TError,UpdateProductRequirementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateProductRequirement>>, TError,UpdateProductRequirementMutationVariables, TContext> => {

const mutationKey = getUpdateProductRequirementMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateProductRequirement>>, UpdateProductRequirementMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateProductRequirement(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateProductRequirementMutationResult = NonNullable<Awaited<ReturnType<typeof updateProductRequirement>>>
    export type UpdateProductRequirementMutationBody = ProductRequirementUpdate
    export type UpdateProductRequirementMutationError = Error
    export type UpdateProductRequirementMutationVariables = {id: string;data: ProductRequirementUpdate}

    /**
 * @summary Update a requirement
 */
export const useUpdateProductRequirement = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateProductRequirement>>, TError,UpdateProductRequirementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateProductRequirement>>,
        TError,
        UpdateProductRequirementMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateProductRequirementMutationOptions(options));
    }

export const getDeleteProductRequirementUrl = (id: string,) => {




  return `/api/catalogue/requirements/${id}`
}

/**
 * @summary Delete a requirement
 */
export const deleteProductRequirement = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteProductRequirementUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteProductRequirementMutationKey = () => ['deleteProductRequirement'] as const;

export const getDeleteProductRequirementMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteProductRequirement>>, TError,DeleteProductRequirementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteProductRequirement>>, TError,DeleteProductRequirementMutationVariables, TContext> => {

const mutationKey = getDeleteProductRequirementMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteProductRequirement>>, DeleteProductRequirementMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteProductRequirement(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteProductRequirementMutationResult = NonNullable<Awaited<ReturnType<typeof deleteProductRequirement>>>

    export type DeleteProductRequirementMutationError = Error
    export type DeleteProductRequirementMutationVariables = {id: string}

    /**
 * @summary Delete a requirement
 */
export const useDeleteProductRequirement = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteProductRequirement>>, TError,DeleteProductRequirementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteProductRequirement>>,
        TError,
        DeleteProductRequirementMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteProductRequirementMutationOptions(options));
    }

export const getReviewAccountOrganisationUrl = () => {




  return `/api/accounts/organisation-review`
}

/**
 * Explicit staff action. Uses only public name and website input, never CRM, documents or chat history. Website evidence is extractive and cited; general AI suggestions are unverified and unsourced.
 * @summary Review an entered public website and general AI knowledge without saving an account
 */
export const reviewAccountOrganisation = async (accountOrganisationReviewInput: AccountOrganisationReviewInput, options?: Parameters<typeof customFetch>[1]): Promise<AccountOrganisationReviewResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccountOrganisationReviewResult>(getReviewAccountOrganisationUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accountOrganisationReviewInput)
  }
);}





export const getReviewAccountOrganisationMutationKey = () => ['reviewAccountOrganisation'] as const;

export const getReviewAccountOrganisationMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewAccountOrganisation>>, TError,ReviewAccountOrganisationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewAccountOrganisation>>, TError,ReviewAccountOrganisationMutationVariables, TContext> => {

const mutationKey = getReviewAccountOrganisationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewAccountOrganisation>>, ReviewAccountOrganisationMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reviewAccountOrganisation(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewAccountOrganisationMutationResult = NonNullable<Awaited<ReturnType<typeof reviewAccountOrganisation>>>
    export type ReviewAccountOrganisationMutationBody = AccountOrganisationReviewInput
    export type ReviewAccountOrganisationMutationError = void
    export type ReviewAccountOrganisationMutationVariables = {data: AccountOrganisationReviewInput}

    /**
 * @summary Review an entered public website and general AI knowledge without saving an account
 */
export const useReviewAccountOrganisation = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewAccountOrganisation>>, TError,ReviewAccountOrganisationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewAccountOrganisation>>,
        TError,
        ReviewAccountOrganisationMutationVariables,
        TContext
      > => {
      return useMutation(getReviewAccountOrganisationMutationOptions(options));
    }

export const getGetAccountBusinessLookupStatusUrl = () => {




  return `/api/accounts/business-lookup/status`
}

/**
 * @summary Read approved public business lookup availability and usage
 */
export const getAccountBusinessLookupStatus = async ( options?: Parameters<typeof customFetch>[1]): Promise<ResearchWebSearchStatus> => {

  return customFetch<ResearchWebSearchStatus>(getGetAccountBusinessLookupStatusUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAccountBusinessLookupStatusQueryKey = () => {
    return [
    `/api/accounts/business-lookup/status`
    ] as const;
    }


export const getGetAccountBusinessLookupStatusQueryOptions = <TData = Awaited<ReturnType<typeof getAccountBusinessLookupStatus>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccountBusinessLookupStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAccountBusinessLookupStatusQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAccountBusinessLookupStatus>>> = ({ signal }) => getAccountBusinessLookupStatus({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAccountBusinessLookupStatus>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAccountBusinessLookupStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getAccountBusinessLookupStatus>>>
export type GetAccountBusinessLookupStatusQueryError = unknown


/**
 * @summary Read approved public business lookup availability and usage
 */

export function useGetAccountBusinessLookupStatus<TData = Awaited<ReturnType<typeof getAccountBusinessLookupStatus>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccountBusinessLookupStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAccountBusinessLookupStatusQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getLookupAccountBusinessUrl = () => {




  return `/api/accounts/business-lookup`
}

/**
 * @summary Explicit public search for an unsaved business; never saves records
 */
export const lookupAccountBusiness = async (accountBusinessLookupInput: AccountBusinessLookupInput, options?: Parameters<typeof customFetch>[1]): Promise<AccountBusinessLookupResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccountBusinessLookupResult>(getLookupAccountBusinessUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accountBusinessLookupInput)
  }
);}





export const getLookupAccountBusinessMutationKey = () => ['lookupAccountBusiness'] as const;

export const getLookupAccountBusinessMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof lookupAccountBusiness>>, TError,LookupAccountBusinessMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof lookupAccountBusiness>>, TError,LookupAccountBusinessMutationVariables, TContext> => {

const mutationKey = getLookupAccountBusinessMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof lookupAccountBusiness>>, LookupAccountBusinessMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  lookupAccountBusiness(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type LookupAccountBusinessMutationResult = NonNullable<Awaited<ReturnType<typeof lookupAccountBusiness>>>
    export type LookupAccountBusinessMutationBody = AccountBusinessLookupInput
    export type LookupAccountBusinessMutationError = void
    export type LookupAccountBusinessMutationVariables = {data: AccountBusinessLookupInput}

    /**
 * @summary Explicit public search for an unsaved business; never saves records
 */
export const useLookupAccountBusiness = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof lookupAccountBusiness>>, TError,LookupAccountBusinessMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof lookupAccountBusiness>>,
        TError,
        LookupAccountBusinessMutationVariables,
        TContext
      > => {
      return useMutation(getLookupAccountBusinessMutationOptions(options));
    }

export const getListCrmSegmentsUrl = (params?: ListCrmSegmentsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/crm-segments?${stringifiedParams}` : `/api/crm-segments`
}

/**
 * @summary List current entity saved targeting filters
 */
export const listCrmSegments = async (params?: ListCrmSegmentsParams, options?: Parameters<typeof customFetch>[1]): Promise<CrmSegmentList> => {

  return customFetch<CrmSegmentList>(getListCrmSegmentsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCrmSegmentsQueryKey = (params?: ListCrmSegmentsParams,) => {
    return [
    `/api/crm-segments`, ...(params ? [params] : [])
    ] as const;
    }


export const getListCrmSegmentsQueryOptions = <TData = Awaited<ReturnType<typeof listCrmSegments>>, TError = unknown>(params?: ListCrmSegmentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCrmSegments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCrmSegmentsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCrmSegments>>> = ({ signal }) => listCrmSegments(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCrmSegments>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCrmSegmentsQueryResult = NonNullable<Awaited<ReturnType<typeof listCrmSegments>>>
export type ListCrmSegmentsQueryError = unknown


/**
 * @summary List current entity saved targeting filters
 */

export function useListCrmSegments<TData = Awaited<ReturnType<typeof listCrmSegments>>, TError = unknown>(
 params?: ListCrmSegmentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCrmSegments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCrmSegmentsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetCrmSegmentUrl = (id: string,) => {




  return `/api/crm-segments/${id}`
}

/**
 * @summary Read one current segment definition
 */
export const getCrmSegment = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<CrmSegment> => {

  return customFetch<CrmSegment>(getGetCrmSegmentUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetCrmSegmentQueryKey = (id: string,) => {
    return [
    `/api/crm-segments/${id}`
    ] as const;
    }


export const getGetCrmSegmentQueryOptions = <TData = Awaited<ReturnType<typeof getCrmSegment>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCrmSegment>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCrmSegmentQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCrmSegment>>> = ({ signal }) => getCrmSegment(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCrmSegment>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCrmSegmentQueryResult = NonNullable<Awaited<ReturnType<typeof getCrmSegment>>>
export type GetCrmSegmentQueryError = unknown


/**
 * @summary Read one current segment definition
 */

export function useGetCrmSegment<TData = Awaited<ReturnType<typeof getCrmSegment>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCrmSegment>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCrmSegmentQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListCrmSegmentHistoryUrl = (id: string,
    params?: ListCrmSegmentHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/crm-segments/${id}/history?${stringifiedParams}` : `/api/crm-segments/${id}/history`
}

/**
 * @summary Read attributed definition history
 */
export const listCrmSegmentHistory = async (id: string,
    params?: ListCrmSegmentHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<CrmSegmentHistory> => {

  return customFetch<CrmSegmentHistory>(getListCrmSegmentHistoryUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCrmSegmentHistoryQueryKey = (id: string,
    params?: ListCrmSegmentHistoryParams,) => {
    return [
    `/api/crm-segments/${id}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListCrmSegmentHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listCrmSegmentHistory>>, TError = unknown>(id: string,
    params?: ListCrmSegmentHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCrmSegmentHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCrmSegmentHistoryQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCrmSegmentHistory>>> = ({ signal }) => listCrmSegmentHistory(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCrmSegmentHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCrmSegmentHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listCrmSegmentHistory>>>
export type ListCrmSegmentHistoryQueryError = unknown


/**
 * @summary Read attributed definition history
 */

export function useListCrmSegmentHistory<TData = Awaited<ReturnType<typeof listCrmSegmentHistory>>, TError = unknown>(
 id: string,
    params?: ListCrmSegmentHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCrmSegmentHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCrmSegmentHistoryQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewCrmSegmentUrl = () => {




  return `/api/crm-segment-reviews`
}

/**
 * @summary Review exact saved-filter changes without changing accounts
 */
export const reviewCrmSegment = async (crmSegmentReviewRequest: CrmSegmentReviewRequest, options?: Parameters<typeof customFetch>[1]): Promise<CrmSegmentReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CrmSegmentReview>(getReviewCrmSegmentUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(crmSegmentReviewRequest)
  }
);}





export const getReviewCrmSegmentMutationKey = () => ['reviewCrmSegment'] as const;

export const getReviewCrmSegmentMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewCrmSegment>>, TError,ReviewCrmSegmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewCrmSegment>>, TError,ReviewCrmSegmentMutationVariables, TContext> => {

const mutationKey = getReviewCrmSegmentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewCrmSegment>>, ReviewCrmSegmentMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reviewCrmSegment(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewCrmSegmentMutationResult = NonNullable<Awaited<ReturnType<typeof reviewCrmSegment>>>
    export type ReviewCrmSegmentMutationBody = CrmSegmentReviewRequest
    export type ReviewCrmSegmentMutationError = unknown
    export type ReviewCrmSegmentMutationVariables = {data: CrmSegmentReviewRequest}

    /**
 * @summary Review exact saved-filter changes without changing accounts
 */
export const useReviewCrmSegment = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewCrmSegment>>, TError,ReviewCrmSegmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewCrmSegment>>,
        TError,
        ReviewCrmSegmentMutationVariables,
        TContext
      > => {
      return useMutation(getReviewCrmSegmentMutationOptions(options));
    }

export const getGetCrmSegmentReviewUrl = (id: string,) => {




  return `/api/crm-segment-reviews/${id}`
}

/**
 * @summary Recover the original private review or committed receipt
 */
export const getCrmSegmentReview = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<CrmSegmentReview> => {

  return customFetch<CrmSegmentReview>(getGetCrmSegmentReviewUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetCrmSegmentReviewQueryKey = (id: string,) => {
    return [
    `/api/crm-segment-reviews/${id}`
    ] as const;
    }


export const getGetCrmSegmentReviewQueryOptions = <TData = Awaited<ReturnType<typeof getCrmSegmentReview>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCrmSegmentReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCrmSegmentReviewQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCrmSegmentReview>>> = ({ signal }) => getCrmSegmentReview(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCrmSegmentReview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCrmSegmentReviewQueryResult = NonNullable<Awaited<ReturnType<typeof getCrmSegmentReview>>>
export type GetCrmSegmentReviewQueryError = unknown


/**
 * @summary Recover the original private review or committed receipt
 */

export function useGetCrmSegmentReview<TData = Awaited<ReturnType<typeof getCrmSegmentReview>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCrmSegmentReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCrmSegmentReviewQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConfirmCrmSegmentUrl = (id: string,) => {




  return `/api/crm-segment-reviews/${id}/confirm`
}

/**
 * @summary Confirm the reviewed definition once
 */
export const confirmCrmSegment = async (id: string,
    crmSegmentConfirm: CrmSegmentConfirm, options?: Parameters<typeof customFetch>[1]): Promise<CrmSegmentReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CrmSegmentReview>(getConfirmCrmSegmentUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(crmSegmentConfirm)
  }
);}





export const getConfirmCrmSegmentMutationKey = () => ['confirmCrmSegment'] as const;

export const getConfirmCrmSegmentMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmCrmSegment>>, TError,ConfirmCrmSegmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmCrmSegment>>, TError,ConfirmCrmSegmentMutationVariables, TContext> => {

const mutationKey = getConfirmCrmSegmentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmCrmSegment>>, ConfirmCrmSegmentMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  confirmCrmSegment(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmCrmSegmentMutationResult = NonNullable<Awaited<ReturnType<typeof confirmCrmSegment>>>
    export type ConfirmCrmSegmentMutationBody = CrmSegmentConfirm
    export type ConfirmCrmSegmentMutationError = unknown
    export type ConfirmCrmSegmentMutationVariables = {id: string;data: CrmSegmentConfirm}

    /**
 * @summary Confirm the reviewed definition once
 */
export const useConfirmCrmSegment = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmCrmSegment>>, TError,ConfirmCrmSegmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmCrmSegment>>,
        TError,
        ConfirmCrmSegmentMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmCrmSegmentMutationOptions(options));
    }

export const getCancelCrmSegmentReviewUrl = (id: string,) => {




  return `/api/crm-segment-reviews/${id}/cancel`
}

/**
 * @summary Cancel an uncommitted review
 */
export const cancelCrmSegmentReview = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<CrmSegmentReview> => {

  return customFetch<CrmSegmentReview>(getCancelCrmSegmentReviewUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelCrmSegmentReviewMutationKey = () => ['cancelCrmSegmentReview'] as const;

export const getCancelCrmSegmentReviewMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelCrmSegmentReview>>, TError,CancelCrmSegmentReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelCrmSegmentReview>>, TError,CancelCrmSegmentReviewMutationVariables, TContext> => {

const mutationKey = getCancelCrmSegmentReviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelCrmSegmentReview>>, CancelCrmSegmentReviewMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  cancelCrmSegmentReview(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelCrmSegmentReviewMutationResult = NonNullable<Awaited<ReturnType<typeof cancelCrmSegmentReview>>>

    export type CancelCrmSegmentReviewMutationError = unknown
    export type CancelCrmSegmentReviewMutationVariables = {id: string}

    /**
 * @summary Cancel an uncommitted review
 */
export const useCancelCrmSegmentReview = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelCrmSegmentReview>>, TError,CancelCrmSegmentReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelCrmSegmentReview>>,
        TError,
        CancelCrmSegmentReviewMutationVariables,
        TContext
      > => {
      return useMutation(getCancelCrmSegmentReviewMutationOptions(options));
    }

export const getListAccountsUrl = (params?: ListAccountsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/accounts?${stringifiedParams}` : `/api/accounts`
}

/**
 * @summary List accounts
 */
export const listAccounts = async (params?: ListAccountsParams, options?: Parameters<typeof customFetch>[1]): Promise<AccountList> => {

  return customFetch<AccountList>(getListAccountsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAccountsQueryKey = (params?: ListAccountsParams,) => {
    return [
    `/api/accounts`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAccountsQueryOptions = <TData = Awaited<ReturnType<typeof listAccounts>>, TError = unknown>(params?: ListAccountsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAccounts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAccountsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAccounts>>> = ({ signal }) => listAccounts(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAccounts>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAccountsQueryResult = NonNullable<Awaited<ReturnType<typeof listAccounts>>>
export type ListAccountsQueryError = unknown


/**
 * @summary List accounts
 */

export function useListAccounts<TData = Awaited<ReturnType<typeof listAccounts>>, TError = unknown>(
 params?: ListAccountsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAccounts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAccountsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateAccountUrl = () => {




  return `/api/accounts`
}

/**
 * @summary Create an account
 */
export const createAccount = async (accountCreate: NonReadonly<AccountCreate>, options?: Parameters<typeof customFetch>[1]): Promise<Account> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Account>(getCreateAccountUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accountCreate)
  }
);}





export const getCreateAccountMutationKey = () => ['createAccount'] as const;

export const getCreateAccountMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAccount>>, TError,CreateAccountMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createAccount>>, TError,CreateAccountMutationVariables, TContext> => {

const mutationKey = getCreateAccountMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAccount>>, CreateAccountMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createAccount(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateAccountMutationResult = NonNullable<Awaited<ReturnType<typeof createAccount>>>
    export type CreateAccountMutationBody = NonReadonly<AccountCreate>
    export type CreateAccountMutationError = Error
    export type CreateAccountMutationVariables = {data: NonReadonly<AccountCreate>}

    /**
 * @summary Create an account
 */
export const useCreateAccount = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAccount>>, TError,CreateAccountMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createAccount>>,
        TError,
        CreateAccountMutationVariables,
        TContext
      > => {
      return useMutation(getCreateAccountMutationOptions(options));
    }

export const getGetAccountUrl = (id: string,) => {




  return `/api/accounts/${id}`
}

/**
 * @summary Get one account
 */
export const getAccount = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Account> => {

  return customFetch<Account>(getGetAccountUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAccountQueryKey = (id: string,) => {
    return [
    `/api/accounts/${id}`
    ] as const;
    }


export const getGetAccountQueryOptions = <TData = Awaited<ReturnType<typeof getAccount>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccount>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAccountQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAccount>>> = ({ signal }) => getAccount(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAccount>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAccountQueryResult = NonNullable<Awaited<ReturnType<typeof getAccount>>>
export type GetAccountQueryError = Error


/**
 * @summary Get one account
 */

export function useGetAccount<TData = Awaited<ReturnType<typeof getAccount>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccount>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAccountQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateAccountUrl = (id: string,) => {




  return `/api/accounts/${id}`
}

/**
 * @summary Update an account
 */
export const updateAccount = async (id: string,
    accountUpdate: NonReadonly<AccountUpdate>, options?: Parameters<typeof customFetch>[1]): Promise<Account> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Account>(getUpdateAccountUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accountUpdate)
  }
);}





export const getUpdateAccountMutationKey = () => ['updateAccount'] as const;

export const getUpdateAccountMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAccount>>, TError,UpdateAccountMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateAccount>>, TError,UpdateAccountMutationVariables, TContext> => {

const mutationKey = getUpdateAccountMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAccount>>, UpdateAccountMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateAccount(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateAccountMutationResult = NonNullable<Awaited<ReturnType<typeof updateAccount>>>
    export type UpdateAccountMutationBody = NonReadonly<AccountUpdate>
    export type UpdateAccountMutationError = Error
    export type UpdateAccountMutationVariables = {id: string;data: NonReadonly<AccountUpdate>}

    /**
 * @summary Update an account
 */
export const useUpdateAccount = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAccount>>, TError,UpdateAccountMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateAccount>>,
        TError,
        UpdateAccountMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateAccountMutationOptions(options));
    }

export const getDeleteAccountUrl = (id: string,) => {




  return `/api/accounts/${id}`
}

/**
 * @summary Delete an account
 */
export const deleteAccount = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteAccountUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteAccountMutationKey = () => ['deleteAccount'] as const;

export const getDeleteAccountMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAccount>>, TError,DeleteAccountMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteAccount>>, TError,DeleteAccountMutationVariables, TContext> => {

const mutationKey = getDeleteAccountMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteAccount>>, DeleteAccountMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteAccount(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteAccountMutationResult = NonNullable<Awaited<ReturnType<typeof deleteAccount>>>

    export type DeleteAccountMutationError = Error
    export type DeleteAccountMutationVariables = {id: string}

    /**
 * @summary Delete an account
 */
export const useDeleteAccount = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAccount>>, TError,DeleteAccountMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteAccount>>,
        TError,
        DeleteAccountMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteAccountMutationOptions(options));
    }

export const getListContactsUrl = (params?: ListContactsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/contacts?${stringifiedParams}` : `/api/contacts`
}

/**
 * @summary List contacts
 */
export const listContacts = async (params?: ListContactsParams, options?: Parameters<typeof customFetch>[1]): Promise<ContactList> => {

  return customFetch<ContactList>(getListContactsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListContactsQueryKey = (params?: ListContactsParams,) => {
    return [
    `/api/contacts`, ...(params ? [params] : [])
    ] as const;
    }


export const getListContactsQueryOptions = <TData = Awaited<ReturnType<typeof listContacts>>, TError = unknown>(params?: ListContactsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContacts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListContactsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listContacts>>> = ({ signal }) => listContacts(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listContacts>>, TError, TData> & { queryKey: QueryKey }
}

export type ListContactsQueryResult = NonNullable<Awaited<ReturnType<typeof listContacts>>>
export type ListContactsQueryError = unknown


/**
 * @summary List contacts
 */

export function useListContacts<TData = Awaited<ReturnType<typeof listContacts>>, TError = unknown>(
 params?: ListContactsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContacts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListContactsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateContactUrl = () => {




  return `/api/contacts`
}

/**
 * @summary Create a contact
 */
export const createContact = async (contactCreate: NonReadonly<ContactCreate>, options?: Parameters<typeof customFetch>[1]): Promise<Contact> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Contact>(getCreateContactUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contactCreate)
  }
);}





export const getCreateContactMutationKey = () => ['createContact'] as const;

export const getCreateContactMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createContact>>, TError,CreateContactMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createContact>>, TError,CreateContactMutationVariables, TContext> => {

const mutationKey = getCreateContactMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createContact>>, CreateContactMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createContact(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateContactMutationResult = NonNullable<Awaited<ReturnType<typeof createContact>>>
    export type CreateContactMutationBody = NonReadonly<ContactCreate>
    export type CreateContactMutationError = Error
    export type CreateContactMutationVariables = {data: NonReadonly<ContactCreate>}

    /**
 * @summary Create a contact
 */
export const useCreateContact = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createContact>>, TError,CreateContactMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createContact>>,
        TError,
        CreateContactMutationVariables,
        TContext
      > => {
      return useMutation(getCreateContactMutationOptions(options));
    }

export const getGetContactUrl = (id: string,) => {




  return `/api/contacts/${id}`
}

/**
 * @summary Get one contact
 */
export const getContact = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Contact> => {

  return customFetch<Contact>(getGetContactUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetContactQueryKey = (id: string,) => {
    return [
    `/api/contacts/${id}`
    ] as const;
    }


export const getGetContactQueryOptions = <TData = Awaited<ReturnType<typeof getContact>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContact>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetContactQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getContact>>> = ({ signal }) => getContact(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getContact>>, TError, TData> & { queryKey: QueryKey }
}

export type GetContactQueryResult = NonNullable<Awaited<ReturnType<typeof getContact>>>
export type GetContactQueryError = Error


/**
 * @summary Get one contact
 */

export function useGetContact<TData = Awaited<ReturnType<typeof getContact>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContact>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetContactQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateContactUrl = (id: string,) => {




  return `/api/contacts/${id}`
}

/**
 * @summary Update a contact
 */
export const updateContact = async (id: string,
    contactUpdate: NonReadonly<ContactUpdate>, options?: Parameters<typeof customFetch>[1]): Promise<Contact> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Contact>(getUpdateContactUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contactUpdate)
  }
);}





export const getUpdateContactMutationKey = () => ['updateContact'] as const;

export const getUpdateContactMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateContact>>, TError,UpdateContactMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateContact>>, TError,UpdateContactMutationVariables, TContext> => {

const mutationKey = getUpdateContactMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateContact>>, UpdateContactMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateContact(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateContactMutationResult = NonNullable<Awaited<ReturnType<typeof updateContact>>>
    export type UpdateContactMutationBody = NonReadonly<ContactUpdate>
    export type UpdateContactMutationError = Error
    export type UpdateContactMutationVariables = {id: string;data: NonReadonly<ContactUpdate>}

    /**
 * @summary Update a contact
 */
export const useUpdateContact = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateContact>>, TError,UpdateContactMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateContact>>,
        TError,
        UpdateContactMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateContactMutationOptions(options));
    }

export const getDeleteContactUrl = (id: string,) => {




  return `/api/contacts/${id}`
}

/**
 * @summary Delete a contact
 */
export const deleteContact = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteContactUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteContactMutationKey = () => ['deleteContact'] as const;

export const getDeleteContactMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteContact>>, TError,DeleteContactMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteContact>>, TError,DeleteContactMutationVariables, TContext> => {

const mutationKey = getDeleteContactMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteContact>>, DeleteContactMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteContact(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteContactMutationResult = NonNullable<Awaited<ReturnType<typeof deleteContact>>>

    export type DeleteContactMutationError = Error
    export type DeleteContactMutationVariables = {id: string}

    /**
 * @summary Delete a contact
 */
export const useDeleteContact = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteContact>>, TError,DeleteContactMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteContact>>,
        TError,
        DeleteContactMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteContactMutationOptions(options));
    }

export const getGetContactFocusLevelUrl = (id: string,) => {




  return `/api/contacts/${id}/focus-level`
}

/**
 * @summary Get a contact's importance level
 */
export const getContactFocusLevel = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<ContactFocusLevel> => {

  return customFetch<ContactFocusLevel>(getGetContactFocusLevelUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetContactFocusLevelQueryKey = (id: string,) => {
    return [
    `/api/contacts/${id}/focus-level`
    ] as const;
    }


export const getGetContactFocusLevelQueryOptions = <TData = Awaited<ReturnType<typeof getContactFocusLevel>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContactFocusLevel>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetContactFocusLevelQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getContactFocusLevel>>> = ({ signal }) => getContactFocusLevel(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getContactFocusLevel>>, TError, TData> & { queryKey: QueryKey }
}

export type GetContactFocusLevelQueryResult = NonNullable<Awaited<ReturnType<typeof getContactFocusLevel>>>
export type GetContactFocusLevelQueryError = Error


/**
 * @summary Get a contact's importance level
 */

export function useGetContactFocusLevel<TData = Awaited<ReturnType<typeof getContactFocusLevel>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContactFocusLevel>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetContactFocusLevelQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateContactFocusLevelUrl = (id: string,) => {




  return `/api/contacts/${id}/focus-level`
}

/**
 * Requires the same edit permission as the contact itself.
 * @summary Set a contact's importance level (Strategic, Key, Growth or Standard)
 */
export const updateContactFocusLevel = async (id: string,
    contactFocusLevelUpdate: ContactFocusLevelUpdate, options?: Parameters<typeof customFetch>[1]): Promise<ContactFocusLevel> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContactFocusLevel>(getUpdateContactFocusLevelUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contactFocusLevelUpdate)
  }
);}





export const getUpdateContactFocusLevelMutationKey = () => ['updateContactFocusLevel'] as const;

export const getUpdateContactFocusLevelMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateContactFocusLevel>>, TError,UpdateContactFocusLevelMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateContactFocusLevel>>, TError,UpdateContactFocusLevelMutationVariables, TContext> => {

const mutationKey = getUpdateContactFocusLevelMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateContactFocusLevel>>, UpdateContactFocusLevelMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateContactFocusLevel(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateContactFocusLevelMutationResult = NonNullable<Awaited<ReturnType<typeof updateContactFocusLevel>>>
    export type UpdateContactFocusLevelMutationBody = ContactFocusLevelUpdate
    export type UpdateContactFocusLevelMutationError = Error
    export type UpdateContactFocusLevelMutationVariables = {id: string;data: ContactFocusLevelUpdate}

    /**
 * @summary Set a contact's importance level (Strategic, Key, Growth or Standard)
 */
export const useUpdateContactFocusLevel = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateContactFocusLevel>>, TError,UpdateContactFocusLevelMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateContactFocusLevel>>,
        TError,
        UpdateContactFocusLevelMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateContactFocusLevelMutationOptions(options));
    }

export const getCreateStrategicBriefUrl = () => {




  return `/api/strategic-briefs`
}

/**
 * Idempotent. Returns the record's existing brief when one exists; otherwise creates a managed document titled "<Name> — Strategic brief" and links it. Requires edit permission on the record.
 * @summary Create or open the strategic brief for an account or contact
 */
export const createStrategicBrief = async (strategicBriefCreate: StrategicBriefCreate, options?: Parameters<typeof customFetch>[1]): Promise<StrategicBrief> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<StrategicBrief>(getCreateStrategicBriefUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(strategicBriefCreate)
  }
);}





export const getCreateStrategicBriefMutationKey = () => ['createStrategicBrief'] as const;

export const getCreateStrategicBriefMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createStrategicBrief>>, TError,CreateStrategicBriefMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createStrategicBrief>>, TError,CreateStrategicBriefMutationVariables, TContext> => {

const mutationKey = getCreateStrategicBriefMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createStrategicBrief>>, CreateStrategicBriefMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createStrategicBrief(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateStrategicBriefMutationResult = NonNullable<Awaited<ReturnType<typeof createStrategicBrief>>>
    export type CreateStrategicBriefMutationBody = StrategicBriefCreate
    export type CreateStrategicBriefMutationError = Error
    export type CreateStrategicBriefMutationVariables = {data: StrategicBriefCreate}

    /**
 * @summary Create or open the strategic brief for an account or contact
 */
export const useCreateStrategicBrief = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createStrategicBrief>>, TError,CreateStrategicBriefMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createStrategicBrief>>,
        TError,
        CreateStrategicBriefMutationVariables,
        TContext
      > => {
      return useMutation(getCreateStrategicBriefMutationOptions(options));
    }

export const getGetStrategicBriefUrl = (recordType: StrategicBriefRecordType,
    recordId: string,) => {




  return `/api/strategic-briefs/by-record/${recordType}/${recordId}`
}

/**
 * @summary Get the strategic brief for an account or contact, or null when none exists
 */
export const getStrategicBrief = async (recordType: StrategicBriefRecordType,
    recordId: string, options?: Parameters<typeof customFetch>[1]): Promise<StrategicBriefLookup> => {

  return customFetch<StrategicBriefLookup>(getGetStrategicBriefUrl(recordType,recordId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetStrategicBriefQueryKey = (recordType: StrategicBriefRecordType,
    recordId: string,) => {
    return [
    `/api/strategic-briefs/by-record/${recordType}/${recordId}`
    ] as const;
    }


export const getGetStrategicBriefQueryOptions = <TData = Awaited<ReturnType<typeof getStrategicBrief>>, TError = Error>(recordType: StrategicBriefRecordType,
    recordId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getStrategicBrief>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetStrategicBriefQueryKey(recordType,recordId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getStrategicBrief>>> = ({ signal }) => getStrategicBrief(recordType,recordId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: recordType !== null && recordType !== undefined && recordId !== null && recordId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getStrategicBrief>>, TError, TData> & { queryKey: QueryKey }
}

export type GetStrategicBriefQueryResult = NonNullable<Awaited<ReturnType<typeof getStrategicBrief>>>
export type GetStrategicBriefQueryError = Error


/**
 * @summary Get the strategic brief for an account or contact, or null when none exists
 */

export function useGetStrategicBrief<TData = Awaited<ReturnType<typeof getStrategicBrief>>, TError = Error>(
 recordType: StrategicBriefRecordType,
    recordId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getStrategicBrief>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetStrategicBriefQueryOptions(recordType,recordId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDeleteStrategicBriefUrl = (briefId: string,) => {




  return `/api/strategic-briefs/${briefId}`
}

/**
 * Used to undo starting a brief. The link is removed and the empty document is archived, never deleted. Briefs with written content are refused with 409 so no strategy text is lost.
 * @summary Remove a strategic brief that has no written content yet
 */
export const deleteStrategicBrief = async (briefId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteStrategicBriefUrl(briefId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteStrategicBriefMutationKey = () => ['deleteStrategicBrief'] as const;

export const getDeleteStrategicBriefMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteStrategicBrief>>, TError,DeleteStrategicBriefMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteStrategicBrief>>, TError,DeleteStrategicBriefMutationVariables, TContext> => {

const mutationKey = getDeleteStrategicBriefMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteStrategicBrief>>, DeleteStrategicBriefMutationVariables> = (props) => {
          const {briefId} = props ?? {};

          return  deleteStrategicBrief(briefId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteStrategicBriefMutationResult = NonNullable<Awaited<ReturnType<typeof deleteStrategicBrief>>>

    export type DeleteStrategicBriefMutationError = Error
    export type DeleteStrategicBriefMutationVariables = {briefId: string}

    /**
 * @summary Remove a strategic brief that has no written content yet
 */
export const useDeleteStrategicBrief = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteStrategicBrief>>, TError,DeleteStrategicBriefMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteStrategicBrief>>,
        TError,
        DeleteStrategicBriefMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteStrategicBriefMutationOptions(options));
    }

export const getListOpportunityHubItemsUrl = (params?: ListOpportunityHubItemsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/opportunity-hub/items?${stringifiedParams}` : `/api/opportunity-hub/items`
}

/**
 * Read-only roll-up of enquiries, social leads, web signals, pipeline opportunities, referrals, quote requests and tender opportunities. A lead that has become an opportunity appears once, as the opportunity. Filtering, ordering and paging happen on the server.
 * @summary List every potential opportunity in one inbox
 */
export const listOpportunityHubItems = async (params?: ListOpportunityHubItemsParams, options?: Parameters<typeof customFetch>[1]): Promise<OpportunityHubItemList> => {

  return customFetch<OpportunityHubItemList>(getListOpportunityHubItemsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListOpportunityHubItemsQueryKey = (params?: ListOpportunityHubItemsParams,) => {
    return [
    `/api/opportunity-hub/items`, ...(params ? [params] : [])
    ] as const;
    }


export const getListOpportunityHubItemsQueryOptions = <TData = Awaited<ReturnType<typeof listOpportunityHubItems>>, TError = ErrorResponse>(params?: ListOpportunityHubItemsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOpportunityHubItems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListOpportunityHubItemsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listOpportunityHubItems>>> = ({ signal }) => listOpportunityHubItems(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listOpportunityHubItems>>, TError, TData> & { queryKey: QueryKey }
}

export type ListOpportunityHubItemsQueryResult = NonNullable<Awaited<ReturnType<typeof listOpportunityHubItems>>>
export type ListOpportunityHubItemsQueryError = ErrorResponse


/**
 * @summary List every potential opportunity in one inbox
 */

export function useListOpportunityHubItems<TData = Awaited<ReturnType<typeof listOpportunityHubItems>>, TError = ErrorResponse>(
 params?: ListOpportunityHubItemsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOpportunityHubItems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListOpportunityHubItemsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSearchSiteUrl = (params: SearchSiteParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/search?${stringifiedParams}` : `/api/search`
}

/**
 * Read-only search across accounts, contacts, opportunities, enquiries, quotes, tenders and agreements in the caller's entity. Every word must match. Results are ranked by match quality, with accounts and contacts ahead of other types. limit and offset apply within each type, so an unfiltered search returns the best matches of every type.
 * @summary Search records across the site
 */
export const searchSite = async (params: SearchSiteParams, options?: Parameters<typeof customFetch>[1]): Promise<SiteSearchResultList> => {

  return customFetch<SiteSearchResultList>(getSearchSiteUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getSearchSiteQueryKey = (params?: SearchSiteParams,) => {
    return [
    `/api/search`, ...(params ? [params] : [])
    ] as const;
    }


export const getSearchSiteQueryOptions = <TData = Awaited<ReturnType<typeof searchSite>>, TError = ErrorResponse>(params: SearchSiteParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof searchSite>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getSearchSiteQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof searchSite>>> = ({ signal }) => searchSite(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof searchSite>>, TError, TData> & { queryKey: QueryKey }
}

export type SearchSiteQueryResult = NonNullable<Awaited<ReturnType<typeof searchSite>>>
export type SearchSiteQueryError = ErrorResponse


/**
 * @summary Search records across the site
 */

export function useSearchSite<TData = Awaited<ReturnType<typeof searchSite>>, TError = ErrorResponse>(
 params: SearchSiteParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof searchSite>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getSearchSiteQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListEnquiriesUrl = (params?: ListEnquiriesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/enquiries?${stringifiedParams}` : `/api/enquiries`
}

/**
 * @summary List enquiries
 */
export const listEnquiries = async (params?: ListEnquiriesParams, options?: Parameters<typeof customFetch>[1]): Promise<EnquiryList> => {

  return customFetch<EnquiryList>(getListEnquiriesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListEnquiriesQueryKey = (params?: ListEnquiriesParams,) => {
    return [
    `/api/enquiries`, ...(params ? [params] : [])
    ] as const;
    }


export const getListEnquiriesQueryOptions = <TData = Awaited<ReturnType<typeof listEnquiries>>, TError = unknown>(params?: ListEnquiriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEnquiries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEnquiriesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEnquiries>>> = ({ signal }) => listEnquiries(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEnquiries>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEnquiriesQueryResult = NonNullable<Awaited<ReturnType<typeof listEnquiries>>>
export type ListEnquiriesQueryError = unknown


/**
 * @summary List enquiries
 */

export function useListEnquiries<TData = Awaited<ReturnType<typeof listEnquiries>>, TError = unknown>(
 params?: ListEnquiriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEnquiries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEnquiriesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateEnquiryUrl = () => {




  return `/api/enquiries`
}

/**
 * @summary Create an enquiry
 */
export const createEnquiry = async (enquiryCreate: EnquiryCreate, options?: Parameters<typeof customFetch>[1]): Promise<Enquiry> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Enquiry>(getCreateEnquiryUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(enquiryCreate)
  }
);}





export const getCreateEnquiryMutationKey = () => ['createEnquiry'] as const;

export const getCreateEnquiryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEnquiry>>, TError,CreateEnquiryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createEnquiry>>, TError,CreateEnquiryMutationVariables, TContext> => {

const mutationKey = getCreateEnquiryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createEnquiry>>, CreateEnquiryMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createEnquiry(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateEnquiryMutationResult = NonNullable<Awaited<ReturnType<typeof createEnquiry>>>
    export type CreateEnquiryMutationBody = EnquiryCreate
    export type CreateEnquiryMutationError = Error
    export type CreateEnquiryMutationVariables = {data: EnquiryCreate}

    /**
 * @summary Create an enquiry
 */
export const useCreateEnquiry = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEnquiry>>, TError,CreateEnquiryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createEnquiry>>,
        TError,
        CreateEnquiryMutationVariables,
        TContext
      > => {
      return useMutation(getCreateEnquiryMutationOptions(options));
    }

export const getGetEnquiryUrl = (id: string,) => {




  return `/api/enquiries/${id}`
}

/**
 * @summary Get one enquiry
 */
export const getEnquiry = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Enquiry> => {

  return customFetch<Enquiry>(getGetEnquiryUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEnquiryQueryKey = (id: string,) => {
    return [
    `/api/enquiries/${id}`
    ] as const;
    }


export const getGetEnquiryQueryOptions = <TData = Awaited<ReturnType<typeof getEnquiry>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEnquiry>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEnquiryQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEnquiry>>> = ({ signal }) => getEnquiry(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEnquiry>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEnquiryQueryResult = NonNullable<Awaited<ReturnType<typeof getEnquiry>>>
export type GetEnquiryQueryError = Error


/**
 * @summary Get one enquiry
 */

export function useGetEnquiry<TData = Awaited<ReturnType<typeof getEnquiry>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEnquiry>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEnquiryQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateEnquiryUrl = (id: string,) => {




  return `/api/enquiries/${id}`
}

/**
 * @summary Update an enquiry
 */
export const updateEnquiry = async (id: string,
    enquiryUpdate: EnquiryUpdate, options?: Parameters<typeof customFetch>[1]): Promise<Enquiry> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Enquiry>(getUpdateEnquiryUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(enquiryUpdate)
  }
);}





export const getUpdateEnquiryMutationKey = () => ['updateEnquiry'] as const;

export const getUpdateEnquiryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEnquiry>>, TError,UpdateEnquiryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateEnquiry>>, TError,UpdateEnquiryMutationVariables, TContext> => {

const mutationKey = getUpdateEnquiryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateEnquiry>>, UpdateEnquiryMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateEnquiry(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateEnquiryMutationResult = NonNullable<Awaited<ReturnType<typeof updateEnquiry>>>
    export type UpdateEnquiryMutationBody = EnquiryUpdate
    export type UpdateEnquiryMutationError = Error
    export type UpdateEnquiryMutationVariables = {id: string;data: EnquiryUpdate}

    /**
 * @summary Update an enquiry
 */
export const useUpdateEnquiry = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEnquiry>>, TError,UpdateEnquiryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateEnquiry>>,
        TError,
        UpdateEnquiryMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateEnquiryMutationOptions(options));
    }

export const getDeleteEnquiryUrl = (id: string,) => {




  return `/api/enquiries/${id}`
}

/**
 * @summary Delete an enquiry
 */
export const deleteEnquiry = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteEnquiryUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteEnquiryMutationKey = () => ['deleteEnquiry'] as const;

export const getDeleteEnquiryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteEnquiry>>, TError,DeleteEnquiryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteEnquiry>>, TError,DeleteEnquiryMutationVariables, TContext> => {

const mutationKey = getDeleteEnquiryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteEnquiry>>, DeleteEnquiryMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteEnquiry(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteEnquiryMutationResult = NonNullable<Awaited<ReturnType<typeof deleteEnquiry>>>

    export type DeleteEnquiryMutationError = Error
    export type DeleteEnquiryMutationVariables = {id: string}

    /**
 * @summary Delete an enquiry
 */
export const useDeleteEnquiry = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteEnquiry>>, TError,DeleteEnquiryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteEnquiry>>,
        TError,
        DeleteEnquiryMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteEnquiryMutationOptions(options));
    }

export const getConvertEnquiryUrl = (id: string,) => {




  return `/api/enquiries/${id}/convert`
}

/**
 * Matches or creates a contact by email (and an account by name when provided), optionally opens an opportunity in the first pipeline stage, links the records back onto the enquiry, and marks it converted.
 * @summary Convert an enquiry into a contact (and optionally an opportunity)
 */
export const convertEnquiry = async (id: string,
    enquiryConvert: EnquiryConvert, options?: Parameters<typeof customFetch>[1]): Promise<EnquiryConvertResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EnquiryConvertResult>(getConvertEnquiryUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(enquiryConvert)
  }
);}





export const getConvertEnquiryMutationKey = () => ['convertEnquiry'] as const;

export const getConvertEnquiryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof convertEnquiry>>, TError,ConvertEnquiryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof convertEnquiry>>, TError,ConvertEnquiryMutationVariables, TContext> => {

const mutationKey = getConvertEnquiryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof convertEnquiry>>, ConvertEnquiryMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  convertEnquiry(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConvertEnquiryMutationResult = NonNullable<Awaited<ReturnType<typeof convertEnquiry>>>
    export type ConvertEnquiryMutationBody = EnquiryConvert
    export type ConvertEnquiryMutationError = Error
    export type ConvertEnquiryMutationVariables = {id: string;data: EnquiryConvert}

    /**
 * @summary Convert an enquiry into a contact (and optionally an opportunity)
 */
export const useConvertEnquiry = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof convertEnquiry>>, TError,ConvertEnquiryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof convertEnquiry>>,
        TError,
        ConvertEnquiryMutationVariables,
        TContext
      > => {
      return useMutation(getConvertEnquiryMutationOptions(options));
    }

export const getListStaleEnquiriesUrl = () => {




  return `/api/enquiries/stale`
}

/**
 * The lead recovery call list: overdue first responses (new, uncontacted, past deadline), stalled conversations (in progress, untouched for a week), and aged new leads (14+ days, never contacted).
 * @summary List inactive-lead recovery segments
 */
export const listStaleEnquiries = async ( options?: Parameters<typeof customFetch>[1]): Promise<StaleEnquirySegments> => {

  return customFetch<StaleEnquirySegments>(getListStaleEnquiriesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListStaleEnquiriesQueryKey = () => {
    return [
    `/api/enquiries/stale`
    ] as const;
    }


export const getListStaleEnquiriesQueryOptions = <TData = Awaited<ReturnType<typeof listStaleEnquiries>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listStaleEnquiries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListStaleEnquiriesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listStaleEnquiries>>> = ({ signal }) => listStaleEnquiries({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listStaleEnquiries>>, TError, TData> & { queryKey: QueryKey }
}

export type ListStaleEnquiriesQueryResult = NonNullable<Awaited<ReturnType<typeof listStaleEnquiries>>>
export type ListStaleEnquiriesQueryError = unknown


/**
 * @summary List inactive-lead recovery segments
 */

export function useListStaleEnquiries<TData = Awaited<ReturnType<typeof listStaleEnquiries>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listStaleEnquiries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListStaleEnquiriesQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRecordEnquiryContactAttemptUrl = (id: string,) => {




  return `/api/enquiries/${id}/contact-attempt`
}

/**
 * Increments the contact-attempt count and stamps the contacted-at clock, so first-response targets and recovery segments stay accurate.
 * @summary Log a contact attempt on an enquiry
 */
export const recordEnquiryContactAttempt = async (id: string,
    enquiryContactAttemptCreate?: EnquiryContactAttemptCreate, options?: Parameters<typeof customFetch>[1]): Promise<Enquiry> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Enquiry>(getRecordEnquiryContactAttemptUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(enquiryContactAttemptCreate)
  }
);}





export const getRecordEnquiryContactAttemptMutationKey = () => ['recordEnquiryContactAttempt'] as const;

export const getRecordEnquiryContactAttemptMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recordEnquiryContactAttempt>>, TError,RecordEnquiryContactAttemptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof recordEnquiryContactAttempt>>, TError,RecordEnquiryContactAttemptMutationVariables, TContext> => {

const mutationKey = getRecordEnquiryContactAttemptMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof recordEnquiryContactAttempt>>, RecordEnquiryContactAttemptMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  recordEnquiryContactAttempt(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RecordEnquiryContactAttemptMutationResult = NonNullable<Awaited<ReturnType<typeof recordEnquiryContactAttempt>>>
    export type RecordEnquiryContactAttemptMutationBody = EnquiryContactAttemptCreate | undefined
    export type RecordEnquiryContactAttemptMutationError = Error
    export type RecordEnquiryContactAttemptMutationVariables = {id: string;data?: EnquiryContactAttemptCreate}

    /**
 * @summary Log a contact attempt on an enquiry
 */
export const useRecordEnquiryContactAttempt = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recordEnquiryContactAttempt>>, TError,RecordEnquiryContactAttemptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof recordEnquiryContactAttempt>>,
        TError,
        RecordEnquiryContactAttemptMutationVariables,
        TContext
      > => {
      return useMutation(getRecordEnquiryContactAttemptMutationOptions(options));
    }

export const getListEnquiryStatusHistoryUrl = (id: string,) => {




  return `/api/enquiries/${id}/history`
}

/**
 * @summary Immutable stage history for an enquiry
 */
export const listEnquiryStatusHistory = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<EnquiryStatusHistoryList> => {

  return customFetch<EnquiryStatusHistoryList>(getListEnquiryStatusHistoryUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListEnquiryStatusHistoryQueryKey = (id: string,) => {
    return [
    `/api/enquiries/${id}/history`
    ] as const;
    }


export const getListEnquiryStatusHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listEnquiryStatusHistory>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEnquiryStatusHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEnquiryStatusHistoryQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEnquiryStatusHistory>>> = ({ signal }) => listEnquiryStatusHistory(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEnquiryStatusHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEnquiryStatusHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listEnquiryStatusHistory>>>
export type ListEnquiryStatusHistoryQueryError = unknown


/**
 * @summary Immutable stage history for an enquiry
 */

export function useListEnquiryStatusHistory<TData = Awaited<ReturnType<typeof listEnquiryStatusHistory>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEnquiryStatusHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEnquiryStatusHistoryQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListJourneyDefinitionsUrl = () => {




  return `/api/journeys/definitions`
}

/**
 * @summary List the canonical configured CRM journeys
 */
export const listJourneyDefinitions = async ( options?: Parameters<typeof customFetch>[1]): Promise<JourneyDefinitionList> => {

  return customFetch<JourneyDefinitionList>(getListJourneyDefinitionsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListJourneyDefinitionsQueryKey = () => {
    return [
    `/api/journeys/definitions`
    ] as const;
    }


export const getListJourneyDefinitionsQueryOptions = <TData = Awaited<ReturnType<typeof listJourneyDefinitions>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listJourneyDefinitions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListJourneyDefinitionsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listJourneyDefinitions>>> = ({ signal }) => listJourneyDefinitions({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listJourneyDefinitions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListJourneyDefinitionsQueryResult = NonNullable<Awaited<ReturnType<typeof listJourneyDefinitions>>>
export type ListJourneyDefinitionsQueryError = unknown


/**
 * @summary List the canonical configured CRM journeys
 */

export function useListJourneyDefinitions<TData = Awaited<ReturnType<typeof listJourneyDefinitions>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listJourneyDefinitions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListJourneyDefinitionsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetJourneyRecordUrl = (journeyKey: JourneyKey,
    recordId: string,) => {




  return `/api/journeys/${journeyKey}/records/${recordId}`
}

/**
 * @summary Get evaluated journey state for one CRM record
 */
export const getJourneyRecord = async (journeyKey: JourneyKey,
    recordId: string, options?: Parameters<typeof customFetch>[1]): Promise<JourneyRecordState> => {

  return customFetch<JourneyRecordState>(getGetJourneyRecordUrl(journeyKey,recordId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetJourneyRecordQueryKey = (journeyKey: JourneyKey,
    recordId: string,) => {
    return [
    `/api/journeys/${journeyKey}/records/${recordId}`
    ] as const;
    }


export const getGetJourneyRecordQueryOptions = <TData = Awaited<ReturnType<typeof getJourneyRecord>>, TError = Error>(journeyKey: JourneyKey,
    recordId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getJourneyRecord>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetJourneyRecordQueryKey(journeyKey,recordId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getJourneyRecord>>> = ({ signal }) => getJourneyRecord(journeyKey,recordId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: journeyKey !== null && journeyKey !== undefined && recordId !== null && recordId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getJourneyRecord>>, TError, TData> & { queryKey: QueryKey }
}

export type GetJourneyRecordQueryResult = NonNullable<Awaited<ReturnType<typeof getJourneyRecord>>>
export type GetJourneyRecordQueryError = Error


/**
 * @summary Get evaluated journey state for one CRM record
 */

export function useGetJourneyRecord<TData = Awaited<ReturnType<typeof getJourneyRecord>>, TError = Error>(
 journeyKey: JourneyKey,
    recordId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getJourneyRecord>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetJourneyRecordQueryOptions(journeyKey,recordId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetJourneyRecordSignalsUrl = (journeyKey: JourneyKey,
    recordId: string,
    params?: GetJourneyRecordSignalsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/journeys/${journeyKey}/records/${recordId}/signals?${stringifiedParams}` : `/api/journeys/${journeyKey}/records/${recordId}/signals`
}

/**
 * @summary Get the read-only customer signal twin for one journey record
 */
export const getJourneyRecordSignals = async (journeyKey: JourneyKey,
    recordId: string,
    params?: GetJourneyRecordSignalsParams, options?: Parameters<typeof customFetch>[1]): Promise<JourneySignalTwin> => {

  return customFetch<JourneySignalTwin>(getGetJourneyRecordSignalsUrl(journeyKey,recordId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetJourneyRecordSignalsQueryKey = (journeyKey: JourneyKey,
    recordId: string,
    params?: GetJourneyRecordSignalsParams,) => {
    return [
    `/api/journeys/${journeyKey}/records/${recordId}/signals`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetJourneyRecordSignalsQueryOptions = <TData = Awaited<ReturnType<typeof getJourneyRecordSignals>>, TError = Error>(journeyKey: JourneyKey,
    recordId: string,
    params?: GetJourneyRecordSignalsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getJourneyRecordSignals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetJourneyRecordSignalsQueryKey(journeyKey,recordId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getJourneyRecordSignals>>> = ({ signal }) => getJourneyRecordSignals(journeyKey,recordId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: journeyKey !== null && journeyKey !== undefined && recordId !== null && recordId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getJourneyRecordSignals>>, TError, TData> & { queryKey: QueryKey }
}

export type GetJourneyRecordSignalsQueryResult = NonNullable<Awaited<ReturnType<typeof getJourneyRecordSignals>>>
export type GetJourneyRecordSignalsQueryError = Error


/**
 * @summary Get the read-only customer signal twin for one journey record
 */

export function useGetJourneyRecordSignals<TData = Awaited<ReturnType<typeof getJourneyRecordSignals>>, TError = Error>(
 journeyKey: JourneyKey,
    recordId: string,
    params?: GetJourneyRecordSignalsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getJourneyRecordSignals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetJourneyRecordSignalsQueryOptions(journeyKey,recordId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetJourneySignalTimeseriesUrl = (journeyKey: JourneyKey,
    params?: GetJourneySignalTimeseriesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/journeys/${journeyKey}/signals/timeseries?${stringifiedParams}` : `/api/journeys/${journeyKey}/signals/timeseries`
}

/**
 * Uses the summary record scope. Sparse UTC buckets include internal activity so counts reconcile. Missing points are zero. Bucket size widens in whole units to keep at most 400 points per series without dropping history.
 * @summary Get historically attributed, bounded signal time series
 */
export const getJourneySignalTimeseries = async (journeyKey: JourneyKey,
    params?: GetJourneySignalTimeseriesParams, options?: Parameters<typeof customFetch>[1]): Promise<JourneySignalTimeseries> => {

  return customFetch<JourneySignalTimeseries>(getGetJourneySignalTimeseriesUrl(journeyKey,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetJourneySignalTimeseriesQueryKey = (journeyKey: JourneyKey,
    params?: GetJourneySignalTimeseriesParams,) => {
    return [
    `/api/journeys/${journeyKey}/signals/timeseries`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetJourneySignalTimeseriesQueryOptions = <TData = Awaited<ReturnType<typeof getJourneySignalTimeseries>>, TError = Error>(journeyKey: JourneyKey,
    params?: GetJourneySignalTimeseriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getJourneySignalTimeseries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetJourneySignalTimeseriesQueryKey(journeyKey,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getJourneySignalTimeseries>>> = ({ signal }) => getJourneySignalTimeseries(journeyKey,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: journeyKey !== null && journeyKey !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getJourneySignalTimeseries>>, TError, TData> & { queryKey: QueryKey }
}

export type GetJourneySignalTimeseriesQueryResult = NonNullable<Awaited<ReturnType<typeof getJourneySignalTimeseries>>>
export type GetJourneySignalTimeseriesQueryError = Error


/**
 * @summary Get historically attributed, bounded signal time series
 */

export function useGetJourneySignalTimeseries<TData = Awaited<ReturnType<typeof getJourneySignalTimeseries>>, TError = Error>(
 journeyKey: JourneyKey,
    params?: GetJourneySignalTimeseriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getJourneySignalTimeseries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetJourneySignalTimeseriesQueryOptions(journeyKey,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetJourneySignalSummaryUrl = (journeyKey: JourneyKey,
    params?: GetJourneySignalSummaryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/journeys/${journeyKey}/signals/summary?${stringifiedParams}` : `/api/journeys/${journeyKey}/signals/summary`
}

/**
 * @summary Get bounded stage-by-channel signal totals for a journey
 */
export const getJourneySignalSummary = async (journeyKey: JourneyKey,
    params?: GetJourneySignalSummaryParams, options?: Parameters<typeof customFetch>[1]): Promise<JourneySignalSummary> => {

  return customFetch<JourneySignalSummary>(getGetJourneySignalSummaryUrl(journeyKey,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetJourneySignalSummaryQueryKey = (journeyKey: JourneyKey,
    params?: GetJourneySignalSummaryParams,) => {
    return [
    `/api/journeys/${journeyKey}/signals/summary`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetJourneySignalSummaryQueryOptions = <TData = Awaited<ReturnType<typeof getJourneySignalSummary>>, TError = Error>(journeyKey: JourneyKey,
    params?: GetJourneySignalSummaryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getJourneySignalSummary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetJourneySignalSummaryQueryKey(journeyKey,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getJourneySignalSummary>>> = ({ signal }) => getJourneySignalSummary(journeyKey,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: journeyKey !== null && journeyKey !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getJourneySignalSummary>>, TError, TData> & { queryKey: QueryKey }
}

export type GetJourneySignalSummaryQueryResult = NonNullable<Awaited<ReturnType<typeof getJourneySignalSummary>>>
export type GetJourneySignalSummaryQueryError = Error


/**
 * @summary Get bounded stage-by-channel signal totals for a journey
 */

export function useGetJourneySignalSummary<TData = Awaited<ReturnType<typeof getJourneySignalSummary>>, TError = Error>(
 journeyKey: JourneyKey,
    params?: GetJourneySignalSummaryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getJourneySignalSummary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetJourneySignalSummaryQueryOptions(journeyKey,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getProposeStageCoachingUrl = (journeyKey: JourneyKey,
    stageId: string,) => {




  return `/api/journeys/${journeyKey}/stages/${stageId}/coaching`
}

/**
 * @summary Propose a staff-authored stage instruction for administrator review
 */
export const proposeStageCoaching = async (journeyKey: JourneyKey,
    stageId: string,
    stageCoachingRequest: StageCoachingRequest, options?: Parameters<typeof customFetch>[1]): Promise<StageCoachingResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<StageCoachingResult>(getProposeStageCoachingUrl(journeyKey,stageId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(stageCoachingRequest)
  }
);}





export const getProposeStageCoachingMutationKey = () => ['proposeStageCoaching'] as const;

export const getProposeStageCoachingMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof proposeStageCoaching>>, TError,ProposeStageCoachingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof proposeStageCoaching>>, TError,ProposeStageCoachingMutationVariables, TContext> => {

const mutationKey = getProposeStageCoachingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof proposeStageCoaching>>, ProposeStageCoachingMutationVariables> = (props) => {
          const {journeyKey,stageId,data} = props ?? {};

          return  proposeStageCoaching(journeyKey,stageId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ProposeStageCoachingMutationResult = NonNullable<Awaited<ReturnType<typeof proposeStageCoaching>>>
    export type ProposeStageCoachingMutationBody = StageCoachingRequest
    export type ProposeStageCoachingMutationError = Error
    export type ProposeStageCoachingMutationVariables = {journeyKey: JourneyKey;stageId: string;data: StageCoachingRequest}

    /**
 * @summary Propose a staff-authored stage instruction for administrator review
 */
export const useProposeStageCoaching = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof proposeStageCoaching>>, TError,ProposeStageCoachingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof proposeStageCoaching>>,
        TError,
        ProposeStageCoachingMutationVariables,
        TContext
      > => {
      return useMutation(getProposeStageCoachingMutationOptions(options));
    }

export const getListJourneyCoachingUrl = (journeyKey: JourneyKey,
    params?: ListJourneyCoachingParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/journeys/${journeyKey}/coaching?${stringifiedParams}` : `/api/journeys/${journeyKey}/coaching`
}

/**
 * @summary List visible coaching proposals and stage-level review counts
 */
export const listJourneyCoaching = async (journeyKey: JourneyKey,
    params?: ListJourneyCoachingParams, options?: Parameters<typeof customFetch>[1]): Promise<JourneyCoachingList> => {

  return customFetch<JourneyCoachingList>(getListJourneyCoachingUrl(journeyKey,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListJourneyCoachingQueryKey = (journeyKey: JourneyKey,
    params?: ListJourneyCoachingParams,) => {
    return [
    `/api/journeys/${journeyKey}/coaching`, ...(params ? [params] : [])
    ] as const;
    }


export const getListJourneyCoachingQueryOptions = <TData = Awaited<ReturnType<typeof listJourneyCoaching>>, TError = unknown>(journeyKey: JourneyKey,
    params?: ListJourneyCoachingParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listJourneyCoaching>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListJourneyCoachingQueryKey(journeyKey,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listJourneyCoaching>>> = ({ signal }) => listJourneyCoaching(journeyKey,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: journeyKey !== null && journeyKey !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listJourneyCoaching>>, TError, TData> & { queryKey: QueryKey }
}

export type ListJourneyCoachingQueryResult = NonNullable<Awaited<ReturnType<typeof listJourneyCoaching>>>
export type ListJourneyCoachingQueryError = unknown


/**
 * @summary List visible coaching proposals and stage-level review counts
 */

export function useListJourneyCoaching<TData = Awaited<ReturnType<typeof listJourneyCoaching>>, TError = unknown>(
 journeyKey: JourneyKey,
    params?: ListJourneyCoachingParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listJourneyCoaching>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListJourneyCoachingQueryOptions(journeyKey,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSteerJourneyRecordUrl = (journeyKey: JourneyKey,
    recordId: string,) => {




  return `/api/journeys/${journeyKey}/records/${recordId}/steer`
}

/**
 * @summary Add record-only staff context and regenerate governed stage guidance
 */
export const steerJourneyRecord = async (journeyKey: JourneyKey,
    recordId: string,
    recordSteerRequest: RecordSteerRequest, options?: Parameters<typeof customFetch>[1]): Promise<RecordSteerResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<RecordSteerResult>(getSteerJourneyRecordUrl(journeyKey,recordId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(recordSteerRequest)
  }
);}





export const getSteerJourneyRecordMutationKey = () => ['steerJourneyRecord'] as const;

export const getSteerJourneyRecordMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof steerJourneyRecord>>, TError,SteerJourneyRecordMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof steerJourneyRecord>>, TError,SteerJourneyRecordMutationVariables, TContext> => {

const mutationKey = getSteerJourneyRecordMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof steerJourneyRecord>>, SteerJourneyRecordMutationVariables> = (props) => {
          const {journeyKey,recordId,data} = props ?? {};

          return  steerJourneyRecord(journeyKey,recordId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SteerJourneyRecordMutationResult = NonNullable<Awaited<ReturnType<typeof steerJourneyRecord>>>
    export type SteerJourneyRecordMutationBody = RecordSteerRequest
    export type SteerJourneyRecordMutationError = Error
    export type SteerJourneyRecordMutationVariables = {journeyKey: JourneyKey;recordId: string;data: RecordSteerRequest}

    /**
 * @summary Add record-only staff context and regenerate governed stage guidance
 */
export const useSteerJourneyRecord = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof steerJourneyRecord>>, TError,SteerJourneyRecordMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof steerJourneyRecord>>,
        TError,
        SteerJourneyRecordMutationVariables,
        TContext
      > => {
      return useMutation(getSteerJourneyRecordMutationOptions(options));
    }

export const getGetLatestStageGuidanceUrl = (journeyKey: JourneyKey,
    recordId: string,) => {




  return `/api/journeys/${journeyKey}/records/${recordId}/guidance`
}

/**
 * @summary Read the latest governed AI guidance for the record's current stage
 */
export const getLatestStageGuidance = async (journeyKey: JourneyKey,
    recordId: string, options?: Parameters<typeof customFetch>[1]): Promise<StageGuidanceState> => {

  return customFetch<StageGuidanceState>(getGetLatestStageGuidanceUrl(journeyKey,recordId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetLatestStageGuidanceQueryKey = (journeyKey: JourneyKey,
    recordId: string,) => {
    return [
    `/api/journeys/${journeyKey}/records/${recordId}/guidance`
    ] as const;
    }


export const getGetLatestStageGuidanceQueryOptions = <TData = Awaited<ReturnType<typeof getLatestStageGuidance>>, TError = unknown>(journeyKey: JourneyKey,
    recordId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLatestStageGuidance>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetLatestStageGuidanceQueryKey(journeyKey,recordId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getLatestStageGuidance>>> = ({ signal }) => getLatestStageGuidance(journeyKey,recordId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: journeyKey !== null && journeyKey !== undefined && recordId !== null && recordId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getLatestStageGuidance>>, TError, TData> & { queryKey: QueryKey }
}

export type GetLatestStageGuidanceQueryResult = NonNullable<Awaited<ReturnType<typeof getLatestStageGuidance>>>
export type GetLatestStageGuidanceQueryError = unknown


/**
 * @summary Read the latest governed AI guidance for the record's current stage
 */

export function useGetLatestStageGuidance<TData = Awaited<ReturnType<typeof getLatestStageGuidance>>, TError = unknown>(
 journeyKey: JourneyKey,
    recordId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLatestStageGuidance>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetLatestStageGuidanceQueryOptions(journeyKey,recordId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGenerateStageGuidanceUrl = (journeyKey: JourneyKey,
    recordId: string,) => {




  return `/api/journeys/${journeyKey}/records/${recordId}/guidance`
}

/**
 * @summary Generate grounded guidance for the current stage under administrator authority policy
 */
export const generateStageGuidance = async (journeyKey: JourneyKey,
    recordId: string,
    stageGuidanceGenerateRequest: StageGuidanceGenerateRequest, options?: Parameters<typeof customFetch>[1]): Promise<StageGuidanceState> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<StageGuidanceState>(getGenerateStageGuidanceUrl(journeyKey,recordId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(stageGuidanceGenerateRequest)
  }
);}





export const getGenerateStageGuidanceMutationKey = () => ['generateStageGuidance'] as const;

export const getGenerateStageGuidanceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateStageGuidance>>, TError,GenerateStageGuidanceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof generateStageGuidance>>, TError,GenerateStageGuidanceMutationVariables, TContext> => {

const mutationKey = getGenerateStageGuidanceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof generateStageGuidance>>, GenerateStageGuidanceMutationVariables> = (props) => {
          const {journeyKey,recordId,data} = props ?? {};

          return  generateStageGuidance(journeyKey,recordId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenerateStageGuidanceMutationResult = NonNullable<Awaited<ReturnType<typeof generateStageGuidance>>>
    export type GenerateStageGuidanceMutationBody = StageGuidanceGenerateRequest
    export type GenerateStageGuidanceMutationError = Error
    export type GenerateStageGuidanceMutationVariables = {journeyKey: JourneyKey;recordId: string;data: StageGuidanceGenerateRequest}

    /**
 * @summary Generate grounded guidance for the current stage under administrator authority policy
 */
export const useGenerateStageGuidance = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateStageGuidance>>, TError,GenerateStageGuidanceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof generateStageGuidance>>,
        TError,
        GenerateStageGuidanceMutationVariables,
        TContext
      > => {
      return useMutation(getGenerateStageGuidanceMutationOptions(options));
    }

export const getListJourneyTransitionHistoryUrl = (journeyKey: JourneyKey,
    recordId: string,
    params?: ListJourneyTransitionHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/journeys/${journeyKey}/records/${recordId}/history?${stringifiedParams}` : `/api/journeys/${journeyKey}/records/${recordId}/history`
}

/**
 * @summary List append-only journey transitions and staff steer notes for one CRM record
 */
export const listJourneyTransitionHistory = async (journeyKey: JourneyKey,
    recordId: string,
    params?: ListJourneyTransitionHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<JourneyTransitionHistoryList> => {

  return customFetch<JourneyTransitionHistoryList>(getListJourneyTransitionHistoryUrl(journeyKey,recordId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListJourneyTransitionHistoryQueryKey = (journeyKey: JourneyKey,
    recordId: string,
    params?: ListJourneyTransitionHistoryParams,) => {
    return [
    `/api/journeys/${journeyKey}/records/${recordId}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListJourneyTransitionHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listJourneyTransitionHistory>>, TError = Error>(journeyKey: JourneyKey,
    recordId: string,
    params?: ListJourneyTransitionHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listJourneyTransitionHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListJourneyTransitionHistoryQueryKey(journeyKey,recordId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listJourneyTransitionHistory>>> = ({ signal }) => listJourneyTransitionHistory(journeyKey,recordId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: journeyKey !== null && journeyKey !== undefined && recordId !== null && recordId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listJourneyTransitionHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListJourneyTransitionHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listJourneyTransitionHistory>>>
export type ListJourneyTransitionHistoryQueryError = Error


/**
 * @summary List append-only journey transitions and staff steer notes for one CRM record
 */

export function useListJourneyTransitionHistory<TData = Awaited<ReturnType<typeof listJourneyTransitionHistory>>, TError = Error>(
 journeyKey: JourneyKey,
    recordId: string,
    params?: ListJourneyTransitionHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listJourneyTransitionHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListJourneyTransitionHistoryQueryOptions(journeyKey,recordId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getTransitionJourneyRecordUrl = (journeyKey: JourneyKey,
    recordId: string,) => {




  return `/api/journeys/${journeyKey}/records/${recordId}/transitions`
}

/**
 * @summary Validate and apply one journey transition
 */
export const transitionJourneyRecord = async (journeyKey: JourneyKey,
    recordId: string,
    journeyTransitionRequest: JourneyTransitionRequest, options?: Parameters<typeof customFetch>[1]): Promise<JourneyTransitionResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<JourneyTransitionResult>(getTransitionJourneyRecordUrl(journeyKey,recordId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(journeyTransitionRequest)
  }
);}





export const getTransitionJourneyRecordMutationKey = () => ['transitionJourneyRecord'] as const;

export const getTransitionJourneyRecordMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof transitionJourneyRecord>>, TError,TransitionJourneyRecordMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof transitionJourneyRecord>>, TError,TransitionJourneyRecordMutationVariables, TContext> => {

const mutationKey = getTransitionJourneyRecordMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof transitionJourneyRecord>>, TransitionJourneyRecordMutationVariables> = (props) => {
          const {journeyKey,recordId,data} = props ?? {};

          return  transitionJourneyRecord(journeyKey,recordId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TransitionJourneyRecordMutationResult = NonNullable<Awaited<ReturnType<typeof transitionJourneyRecord>>>
    export type TransitionJourneyRecordMutationBody = JourneyTransitionRequest
    export type TransitionJourneyRecordMutationError = Error
    export type TransitionJourneyRecordMutationVariables = {journeyKey: JourneyKey;recordId: string;data: JourneyTransitionRequest}

    /**
 * @summary Validate and apply one journey transition
 */
export const useTransitionJourneyRecord = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof transitionJourneyRecord>>, TError,TransitionJourneyRecordMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof transitionJourneyRecord>>,
        TError,
        TransitionJourneyRecordMutationVariables,
        TContext
      > => {
      return useMutation(getTransitionJourneyRecordMutationOptions(options));
    }

export const getListMicrositeAdminEntitiesUrl = (params?: ListMicrositeAdminEntitiesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/microsite-entities?${stringifiedParams}` : `/api/admin/microsite-entities`
}

/**
 * @summary List active entities the signed-in administrator may manage for microsites
 */
export const listMicrositeAdminEntities = async (params?: ListMicrositeAdminEntitiesParams, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeAdminEntityList> => {

  return customFetch<MicrositeAdminEntityList>(getListMicrositeAdminEntitiesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMicrositeAdminEntitiesQueryKey = (params?: ListMicrositeAdminEntitiesParams,) => {
    return [
    `/api/admin/microsite-entities`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMicrositeAdminEntitiesQueryOptions = <TData = Awaited<ReturnType<typeof listMicrositeAdminEntities>>, TError = Error>(params?: ListMicrositeAdminEntitiesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMicrositeAdminEntities>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMicrositeAdminEntitiesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMicrositeAdminEntities>>> = ({ signal }) => listMicrositeAdminEntities(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMicrositeAdminEntities>>, TError, TData> & { queryKey: QueryKey }
}

export type ListMicrositeAdminEntitiesQueryResult = NonNullable<Awaited<ReturnType<typeof listMicrositeAdminEntities>>>
export type ListMicrositeAdminEntitiesQueryError = Error


/**
 * @summary List active entities the signed-in administrator may manage for microsites
 */

export function useListMicrositeAdminEntities<TData = Awaited<ReturnType<typeof listMicrositeAdminEntities>>, TError = Error>(
 params?: ListMicrositeAdminEntitiesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMicrositeAdminEntities>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListMicrositeAdminEntitiesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetEntityMicrositePublicLinkUrl = (entityId: string,) => {




  return `/api/entities/${entityId}/microsite/public-link`
}

/**
 * @summary Get the selected entity's published public microsite address for signed-in staff
 */
export const getEntityMicrositePublicLink = async (entityId: string, options?: Parameters<typeof customFetch>[1]): Promise<MicrositePublicLink> => {

  return customFetch<MicrositePublicLink>(getGetEntityMicrositePublicLinkUrl(entityId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEntityMicrositePublicLinkQueryKey = (entityId: string,) => {
    return [
    `/api/entities/${entityId}/microsite/public-link`
    ] as const;
    }


export const getGetEntityMicrositePublicLinkQueryOptions = <TData = Awaited<ReturnType<typeof getEntityMicrositePublicLink>>, TError = Error>(entityId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityMicrositePublicLink>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEntityMicrositePublicLinkQueryKey(entityId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEntityMicrositePublicLink>>> = ({ signal }) => getEntityMicrositePublicLink(entityId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entityId !== null && entityId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEntityMicrositePublicLink>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEntityMicrositePublicLinkQueryResult = NonNullable<Awaited<ReturnType<typeof getEntityMicrositePublicLink>>>
export type GetEntityMicrositePublicLinkQueryError = Error


/**
 * @summary Get the selected entity's published public microsite address for signed-in staff
 */

export function useGetEntityMicrositePublicLink<TData = Awaited<ReturnType<typeof getEntityMicrositePublicLink>>, TError = Error>(
 entityId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityMicrositePublicLink>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEntityMicrositePublicLinkQueryOptions(entityId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetEntityMicrositeUrl = (entityId: string,) => {




  return `/api/admin/entities/${entityId}/microsite`
}

/**
 * @summary Get the authorised entity's draft and publication metadata
 */
export const getEntityMicrosite = async (entityId: string, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeAdminResponse> => {

  return customFetch<MicrositeAdminResponse>(getGetEntityMicrositeUrl(entityId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEntityMicrositeQueryKey = (entityId: string,) => {
    return [
    `/api/admin/entities/${entityId}/microsite`
    ] as const;
    }


export const getGetEntityMicrositeQueryOptions = <TData = Awaited<ReturnType<typeof getEntityMicrosite>>, TError = Error>(entityId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityMicrosite>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEntityMicrositeQueryKey(entityId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEntityMicrosite>>> = ({ signal }) => getEntityMicrosite(entityId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entityId !== null && entityId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEntityMicrosite>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEntityMicrositeQueryResult = NonNullable<Awaited<ReturnType<typeof getEntityMicrosite>>>
export type GetEntityMicrositeQueryError = Error


/**
 * @summary Get the authorised entity's draft and publication metadata
 */

export function useGetEntityMicrosite<TData = Awaited<ReturnType<typeof getEntityMicrosite>>, TError = Error>(
 entityId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityMicrosite>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEntityMicrositeQueryOptions(entityId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateEntityMicrositeUrl = (entityId: string,) => {




  return `/api/admin/entities/${entityId}/microsite`
}

/**
 * @summary Create or update an entity microsite draft with optimistic concurrency
 */
export const updateEntityMicrosite = async (entityId: string,
    micrositeDraftWrite: MicrositeDraftWrite, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeAdminResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MicrositeAdminResponse>(getUpdateEntityMicrositeUrl(entityId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(micrositeDraftWrite)
  }
);}





export const getUpdateEntityMicrositeMutationKey = () => ['updateEntityMicrosite'] as const;

export const getUpdateEntityMicrositeMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEntityMicrosite>>, TError,UpdateEntityMicrositeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateEntityMicrosite>>, TError,UpdateEntityMicrositeMutationVariables, TContext> => {

const mutationKey = getUpdateEntityMicrositeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateEntityMicrosite>>, UpdateEntityMicrositeMutationVariables> = (props) => {
          const {entityId,data} = props ?? {};

          return  updateEntityMicrosite(entityId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateEntityMicrositeMutationResult = NonNullable<Awaited<ReturnType<typeof updateEntityMicrosite>>>
    export type UpdateEntityMicrositeMutationBody = MicrositeDraftWrite
    export type UpdateEntityMicrositeMutationError = Error
    export type UpdateEntityMicrositeMutationVariables = {entityId: string;data: MicrositeDraftWrite}

    /**
 * @summary Create or update an entity microsite draft with optimistic concurrency
 */
export const useUpdateEntityMicrosite = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEntityMicrosite>>, TError,UpdateEntityMicrositeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateEntityMicrosite>>,
        TError,
        UpdateEntityMicrositeMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateEntityMicrositeMutationOptions(options));
    }

export const getListEntityMicrositeVersionsUrl = (entityId: string,
    params?: ListEntityMicrositeVersionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/entities/${entityId}/microsite/versions?${stringifiedParams}` : `/api/admin/entities/${entityId}/microsite/versions`
}

/**
 * @summary List immutable microsite change snapshots
 */
export const listEntityMicrositeVersions = async (entityId: string,
    params?: ListEntityMicrositeVersionsParams, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeVersionList> => {

  return customFetch<MicrositeVersionList>(getListEntityMicrositeVersionsUrl(entityId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListEntityMicrositeVersionsQueryKey = (entityId: string,
    params?: ListEntityMicrositeVersionsParams,) => {
    return [
    `/api/admin/entities/${entityId}/microsite/versions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListEntityMicrositeVersionsQueryOptions = <TData = Awaited<ReturnType<typeof listEntityMicrositeVersions>>, TError = unknown>(entityId: string,
    params?: ListEntityMicrositeVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityMicrositeVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEntityMicrositeVersionsQueryKey(entityId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEntityMicrositeVersions>>> = ({ signal }) => listEntityMicrositeVersions(entityId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entityId !== null && entityId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEntityMicrositeVersions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEntityMicrositeVersionsQueryResult = NonNullable<Awaited<ReturnType<typeof listEntityMicrositeVersions>>>
export type ListEntityMicrositeVersionsQueryError = unknown


/**
 * @summary List immutable microsite change snapshots
 */

export function useListEntityMicrositeVersions<TData = Awaited<ReturnType<typeof listEntityMicrositeVersions>>, TError = unknown>(
 entityId: string,
    params?: ListEntityMicrositeVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityMicrositeVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEntityMicrositeVersionsQueryOptions(entityId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPreviewEntityMicrositeUrl = (entityId: string,) => {




  return `/api/admin/entities/${entityId}/microsite/preview`
}

/**
 * @summary Generate a protected, uncached preview from a validated draft configuration
 */
export const previewEntityMicrosite = async (entityId: string,
    micrositePreviewRequest: MicrositePreviewRequest, options?: Parameters<typeof customFetch>[1]): Promise<MicrositePageComposition> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MicrositePageComposition>(getPreviewEntityMicrositeUrl(entityId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(micrositePreviewRequest)
  }
);}





export const getPreviewEntityMicrositeMutationKey = () => ['previewEntityMicrosite'] as const;

export const getPreviewEntityMicrositeMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewEntityMicrosite>>, TError,PreviewEntityMicrositeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewEntityMicrosite>>, TError,PreviewEntityMicrositeMutationVariables, TContext> => {

const mutationKey = getPreviewEntityMicrositeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewEntityMicrosite>>, PreviewEntityMicrositeMutationVariables> = (props) => {
          const {entityId,data} = props ?? {};

          return  previewEntityMicrosite(entityId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewEntityMicrositeMutationResult = NonNullable<Awaited<ReturnType<typeof previewEntityMicrosite>>>
    export type PreviewEntityMicrositeMutationBody = MicrositePreviewRequest
    export type PreviewEntityMicrositeMutationError = Error
    export type PreviewEntityMicrositeMutationVariables = {entityId: string;data: MicrositePreviewRequest}

    /**
 * @summary Generate a protected, uncached preview from a validated draft configuration
 */
export const usePreviewEntityMicrosite = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewEntityMicrosite>>, TError,PreviewEntityMicrositeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewEntityMicrosite>>,
        TError,
        PreviewEntityMicrositeMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewEntityMicrositeMutationOptions(options));
    }

export const getPublishEntityMicrositeUrl = (entityId: string,) => {




  return `/api/admin/entities/${entityId}/microsite/publish`
}

/**
 * @summary Atomically publish the current draft snapshot and activate its public binding
 */
export const publishEntityMicrosite = async (entityId: string,
    micrositeRevisionWrite: MicrositeRevisionWrite, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeAdminResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MicrositeAdminResponse>(getPublishEntityMicrositeUrl(entityId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(micrositeRevisionWrite)
  }
);}





export const getPublishEntityMicrositeMutationKey = () => ['publishEntityMicrosite'] as const;

export const getPublishEntityMicrositeMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof publishEntityMicrosite>>, TError,PublishEntityMicrositeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof publishEntityMicrosite>>, TError,PublishEntityMicrositeMutationVariables, TContext> => {

const mutationKey = getPublishEntityMicrositeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof publishEntityMicrosite>>, PublishEntityMicrositeMutationVariables> = (props) => {
          const {entityId,data} = props ?? {};

          return  publishEntityMicrosite(entityId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PublishEntityMicrositeMutationResult = NonNullable<Awaited<ReturnType<typeof publishEntityMicrosite>>>
    export type PublishEntityMicrositeMutationBody = MicrositeRevisionWrite
    export type PublishEntityMicrositeMutationError = Error
    export type PublishEntityMicrositeMutationVariables = {entityId: string;data: MicrositeRevisionWrite}

    /**
 * @summary Atomically publish the current draft snapshot and activate its public binding
 */
export const usePublishEntityMicrosite = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof publishEntityMicrosite>>, TError,PublishEntityMicrositeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof publishEntityMicrosite>>,
        TError,
        PublishEntityMicrositeMutationVariables,
        TContext
      > => {
      return useMutation(getPublishEntityMicrositeMutationOptions(options));
    }

export const getUnpublishEntityMicrositeUrl = (entityId: string,) => {




  return `/api/admin/entities/${entityId}/microsite/unpublish`
}

/**
 * @summary Disable public access without deleting draft or published history
 */
export const unpublishEntityMicrosite = async (entityId: string,
    micrositeRevisionWrite: MicrositeRevisionWrite, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeAdminResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MicrositeAdminResponse>(getUnpublishEntityMicrositeUrl(entityId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(micrositeRevisionWrite)
  }
);}





export const getUnpublishEntityMicrositeMutationKey = () => ['unpublishEntityMicrosite'] as const;

export const getUnpublishEntityMicrositeMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unpublishEntityMicrosite>>, TError,UnpublishEntityMicrositeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof unpublishEntityMicrosite>>, TError,UnpublishEntityMicrositeMutationVariables, TContext> => {

const mutationKey = getUnpublishEntityMicrositeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof unpublishEntityMicrosite>>, UnpublishEntityMicrositeMutationVariables> = (props) => {
          const {entityId,data} = props ?? {};

          return  unpublishEntityMicrosite(entityId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UnpublishEntityMicrositeMutationResult = NonNullable<Awaited<ReturnType<typeof unpublishEntityMicrosite>>>
    export type UnpublishEntityMicrositeMutationBody = MicrositeRevisionWrite
    export type UnpublishEntityMicrositeMutationError = Error
    export type UnpublishEntityMicrositeMutationVariables = {entityId: string;data: MicrositeRevisionWrite}

    /**
 * @summary Disable public access without deleting draft or published history
 */
export const useUnpublishEntityMicrosite = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unpublishEntityMicrosite>>, TError,UnpublishEntityMicrositeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof unpublishEntityMicrosite>>,
        TError,
        UnpublishEntityMicrositeMutationVariables,
        TContext
      > => {
      return useMutation(getUnpublishEntityMicrositeMutationOptions(options));
    }

export const getListEntityMicrositeAssetsUrl = (entityId: string,
    params?: ListEntityMicrositeAssetsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/entities/${entityId}/microsite/assets?${stringifiedParams}` : `/api/admin/entities/${entityId}/microsite/assets`
}

/**
 * @summary List entity-scoped logo and hero assets
 */
export const listEntityMicrositeAssets = async (entityId: string,
    params?: ListEntityMicrositeAssetsParams, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeAssetList> => {

  return customFetch<MicrositeAssetList>(getListEntityMicrositeAssetsUrl(entityId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListEntityMicrositeAssetsQueryKey = (entityId: string,
    params?: ListEntityMicrositeAssetsParams,) => {
    return [
    `/api/admin/entities/${entityId}/microsite/assets`, ...(params ? [params] : [])
    ] as const;
    }


export const getListEntityMicrositeAssetsQueryOptions = <TData = Awaited<ReturnType<typeof listEntityMicrositeAssets>>, TError = unknown>(entityId: string,
    params?: ListEntityMicrositeAssetsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityMicrositeAssets>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEntityMicrositeAssetsQueryKey(entityId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEntityMicrositeAssets>>> = ({ signal }) => listEntityMicrositeAssets(entityId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entityId !== null && entityId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEntityMicrositeAssets>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEntityMicrositeAssetsQueryResult = NonNullable<Awaited<ReturnType<typeof listEntityMicrositeAssets>>>
export type ListEntityMicrositeAssetsQueryError = unknown


/**
 * @summary List entity-scoped logo and hero assets
 */

export function useListEntityMicrositeAssets<TData = Awaited<ReturnType<typeof listEntityMicrositeAssets>>, TError = unknown>(
 entityId: string,
    params?: ListEntityMicrositeAssetsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityMicrositeAssets>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEntityMicrositeAssetsQueryOptions(entityId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUploadEntityMicrositeAssetUrl = (entityId: string,
    params: UploadEntityMicrositeAssetParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/entities/${entityId}/microsite/assets?${stringifiedParams}` : `/api/admin/entities/${entityId}/microsite/assets`
}

/**
 * @summary Upload an entity-scoped logo or hero image
 */
export const uploadEntityMicrositeAsset = async (entityId: string,
    uploadEntityMicrositeAssetBody: UploadEntityMicrositeAssetBody,
    params: UploadEntityMicrositeAssetParams, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeAsset> => {
    const formData = new FormData();
formData.append(`file`, uploadEntityMicrositeAssetBody.file);

  return customFetch<MicrositeAsset>(getUploadEntityMicrositeAssetUrl(entityId,params),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}





export const getUploadEntityMicrositeAssetMutationKey = () => ['uploadEntityMicrositeAsset'] as const;

export const getUploadEntityMicrositeAssetMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadEntityMicrositeAsset>>, TError,UploadEntityMicrositeAssetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadEntityMicrositeAsset>>, TError,UploadEntityMicrositeAssetMutationVariables, TContext> => {

const mutationKey = getUploadEntityMicrositeAssetMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadEntityMicrositeAsset>>, UploadEntityMicrositeAssetMutationVariables> = (props) => {
          const {entityId,data,params} = props ?? {};

          return  uploadEntityMicrositeAsset(entityId,data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadEntityMicrositeAssetMutationResult = NonNullable<Awaited<ReturnType<typeof uploadEntityMicrositeAsset>>>
    export type UploadEntityMicrositeAssetMutationBody = UploadEntityMicrositeAssetBody
    export type UploadEntityMicrositeAssetMutationError = Error
    export type UploadEntityMicrositeAssetMutationVariables = {entityId: string;data: UploadEntityMicrositeAssetBody;params: UploadEntityMicrositeAssetParams}

    /**
 * @summary Upload an entity-scoped logo or hero image
 */
export const useUploadEntityMicrositeAsset = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadEntityMicrositeAsset>>, TError,UploadEntityMicrositeAssetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadEntityMicrositeAsset>>,
        TError,
        UploadEntityMicrositeAssetMutationVariables,
        TContext
      > => {
      return useMutation(getUploadEntityMicrositeAssetMutationOptions(options));
    }

export const getGetEntityMicrositeAssetUrl = (entityId: string,
    assetId: string,) => {




  return `/api/admin/entities/${entityId}/microsite/assets/${assetId}`
}

/**
 * @summary Download an asset only within its resolved entity scope
 */
export const getEntityMicrositeAsset = async (entityId: string,
    assetId: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getGetEntityMicrositeAssetUrl(entityId,assetId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEntityMicrositeAssetQueryKey = (entityId: string,
    assetId: string,) => {
    return [
    `/api/admin/entities/${entityId}/microsite/assets/${assetId}`
    ] as const;
    }


export const getGetEntityMicrositeAssetQueryOptions = <TData = Awaited<ReturnType<typeof getEntityMicrositeAsset>>, TError = Error>(entityId: string,
    assetId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityMicrositeAsset>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEntityMicrositeAssetQueryKey(entityId,assetId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEntityMicrositeAsset>>> = ({ signal }) => getEntityMicrositeAsset(entityId,assetId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entityId !== null && entityId !== undefined && assetId !== null && assetId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEntityMicrositeAsset>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEntityMicrositeAssetQueryResult = NonNullable<Awaited<ReturnType<typeof getEntityMicrositeAsset>>>
export type GetEntityMicrositeAssetQueryError = Error


/**
 * @summary Download an asset only within its resolved entity scope
 */

export function useGetEntityMicrositeAsset<TData = Awaited<ReturnType<typeof getEntityMicrositeAsset>>, TError = Error>(
 entityId: string,
    assetId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityMicrositeAsset>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEntityMicrositeAssetQueryOptions(entityId,assetId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDeleteEntityMicrositeAssetUrl = (entityId: string,
    assetId: string,) => {




  return `/api/admin/entities/${entityId}/microsite/assets/${assetId}`
}

/**
 * @summary Delete an unused draft asset within its resolved entity scope
 */
export const deleteEntityMicrositeAsset = async (entityId: string,
    assetId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteEntityMicrositeAssetUrl(entityId,assetId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteEntityMicrositeAssetMutationKey = () => ['deleteEntityMicrositeAsset'] as const;

export const getDeleteEntityMicrositeAssetMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteEntityMicrositeAsset>>, TError,DeleteEntityMicrositeAssetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteEntityMicrositeAsset>>, TError,DeleteEntityMicrositeAssetMutationVariables, TContext> => {

const mutationKey = getDeleteEntityMicrositeAssetMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteEntityMicrositeAsset>>, DeleteEntityMicrositeAssetMutationVariables> = (props) => {
          const {entityId,assetId} = props ?? {};

          return  deleteEntityMicrositeAsset(entityId,assetId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteEntityMicrositeAssetMutationResult = NonNullable<Awaited<ReturnType<typeof deleteEntityMicrositeAsset>>>

    export type DeleteEntityMicrositeAssetMutationError = Error
    export type DeleteEntityMicrositeAssetMutationVariables = {entityId: string;assetId: string}

    /**
 * @summary Delete an unused draft asset within its resolved entity scope
 */
export const useDeleteEntityMicrositeAsset = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteEntityMicrositeAsset>>, TError,DeleteEntityMicrositeAssetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteEntityMicrositeAsset>>,
        TError,
        DeleteEntityMicrositeAssetMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteEntityMicrositeAssetMutationOptions(options));
    }

export const getListEntityMicrositeSourcesUrl = (entityId: string,
    params?: ListEntityMicrositeSourcesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/entities/${entityId}/microsite/sources?${stringifiedParams}` : `/api/admin/entities/${entityId}/microsite/sources`
}

/**
 * @summary List source registrations and review status in the authorised entity scope
 */
export const listEntityMicrositeSources = async (entityId: string,
    params?: ListEntityMicrositeSourcesParams, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeSourceList> => {

  return customFetch<MicrositeSourceList>(getListEntityMicrositeSourcesUrl(entityId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListEntityMicrositeSourcesQueryKey = (entityId: string,
    params?: ListEntityMicrositeSourcesParams,) => {
    return [
    `/api/admin/entities/${entityId}/microsite/sources`, ...(params ? [params] : [])
    ] as const;
    }


export const getListEntityMicrositeSourcesQueryOptions = <TData = Awaited<ReturnType<typeof listEntityMicrositeSources>>, TError = unknown>(entityId: string,
    params?: ListEntityMicrositeSourcesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityMicrositeSources>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEntityMicrositeSourcesQueryKey(entityId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEntityMicrositeSources>>> = ({ signal }) => listEntityMicrositeSources(entityId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entityId !== null && entityId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEntityMicrositeSources>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEntityMicrositeSourcesQueryResult = NonNullable<Awaited<ReturnType<typeof listEntityMicrositeSources>>>
export type ListEntityMicrositeSourcesQueryError = unknown


/**
 * @summary List source registrations and review status in the authorised entity scope
 */

export function useListEntityMicrositeSources<TData = Awaited<ReturnType<typeof listEntityMicrositeSources>>, TError = unknown>(
 entityId: string,
    params?: ListEntityMicrositeSourcesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityMicrositeSources>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEntityMicrositeSourcesQueryOptions(entityId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateEntityMicrositeSourceUrl = (entityId: string,) => {




  return `/api/admin/entities/${entityId}/microsite/sources`
}

/**
 * @summary Register a bounded website source or stage administrator-supplied content for review
 */
export const createEntityMicrositeSource = async (entityId: string,
    micrositeSourceCreate: MicrositeSourceCreate, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeSource> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MicrositeSource>(getCreateEntityMicrositeSourceUrl(entityId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(micrositeSourceCreate)
  }
);}





export const getCreateEntityMicrositeSourceMutationKey = () => ['createEntityMicrositeSource'] as const;

export const getCreateEntityMicrositeSourceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEntityMicrositeSource>>, TError,CreateEntityMicrositeSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createEntityMicrositeSource>>, TError,CreateEntityMicrositeSourceMutationVariables, TContext> => {

const mutationKey = getCreateEntityMicrositeSourceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createEntityMicrositeSource>>, CreateEntityMicrositeSourceMutationVariables> = (props) => {
          const {entityId,data} = props ?? {};

          return  createEntityMicrositeSource(entityId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateEntityMicrositeSourceMutationResult = NonNullable<Awaited<ReturnType<typeof createEntityMicrositeSource>>>
    export type CreateEntityMicrositeSourceMutationBody = MicrositeSourceCreate
    export type CreateEntityMicrositeSourceMutationError = Error
    export type CreateEntityMicrositeSourceMutationVariables = {entityId: string;data: MicrositeSourceCreate}

    /**
 * @summary Register a bounded website source or stage administrator-supplied content for review
 */
export const useCreateEntityMicrositeSource = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEntityMicrositeSource>>, TError,CreateEntityMicrositeSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createEntityMicrositeSource>>,
        TError,
        CreateEntityMicrositeSourceMutationVariables,
        TContext
      > => {
      return useMutation(getCreateEntityMicrositeSourceMutationOptions(options));
    }

export const getListEntityMicrositeSourceVersionsUrl = (entityId: string,
    sourceId: string,
    params?: ListEntityMicrositeSourceVersionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/entities/${entityId}/microsite/sources/${sourceId}/versions?${stringifiedParams}` : `/api/admin/entities/${entityId}/microsite/sources/${sourceId}/versions`
}

/**
 * @summary Review immutable fetched or supplied source versions
 */
export const listEntityMicrositeSourceVersions = async (entityId: string,
    sourceId: string,
    params?: ListEntityMicrositeSourceVersionsParams, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeSourceVersionList> => {

  return customFetch<MicrositeSourceVersionList>(getListEntityMicrositeSourceVersionsUrl(entityId,sourceId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListEntityMicrositeSourceVersionsQueryKey = (entityId: string,
    sourceId: string,
    params?: ListEntityMicrositeSourceVersionsParams,) => {
    return [
    `/api/admin/entities/${entityId}/microsite/sources/${sourceId}/versions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListEntityMicrositeSourceVersionsQueryOptions = <TData = Awaited<ReturnType<typeof listEntityMicrositeSourceVersions>>, TError = Error>(entityId: string,
    sourceId: string,
    params?: ListEntityMicrositeSourceVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityMicrositeSourceVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEntityMicrositeSourceVersionsQueryKey(entityId,sourceId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEntityMicrositeSourceVersions>>> = ({ signal }) => listEntityMicrositeSourceVersions(entityId,sourceId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entityId !== null && entityId !== undefined && sourceId !== null && sourceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEntityMicrositeSourceVersions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEntityMicrositeSourceVersionsQueryResult = NonNullable<Awaited<ReturnType<typeof listEntityMicrositeSourceVersions>>>
export type ListEntityMicrositeSourceVersionsQueryError = Error


/**
 * @summary Review immutable fetched or supplied source versions
 */

export function useListEntityMicrositeSourceVersions<TData = Awaited<ReturnType<typeof listEntityMicrositeSourceVersions>>, TError = Error>(
 entityId: string,
    sourceId: string,
    params?: ListEntityMicrositeSourceVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityMicrositeSourceVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEntityMicrositeSourceVersionsQueryOptions(entityId,sourceId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRefreshEntityMicrositeSourceUrl = (entityId: string,
    sourceId: string,) => {




  return `/api/admin/entities/${entityId}/microsite/sources/${sourceId}/refresh`
}

/**
 * @summary Fetch a website source within private-network, redirect, crawl, type, size, timeout, and rate boundaries
 */
export const refreshEntityMicrositeSource = async (entityId: string,
    sourceId: string,
    micrositeSourceRevisionWrite: MicrositeSourceRevisionWrite, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeSource> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MicrositeSource>(getRefreshEntityMicrositeSourceUrl(entityId,sourceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(micrositeSourceRevisionWrite)
  }
);}





export const getRefreshEntityMicrositeSourceMutationKey = () => ['refreshEntityMicrositeSource'] as const;

export const getRefreshEntityMicrositeSourceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof refreshEntityMicrositeSource>>, TError,RefreshEntityMicrositeSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof refreshEntityMicrositeSource>>, TError,RefreshEntityMicrositeSourceMutationVariables, TContext> => {

const mutationKey = getRefreshEntityMicrositeSourceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof refreshEntityMicrositeSource>>, RefreshEntityMicrositeSourceMutationVariables> = (props) => {
          const {entityId,sourceId,data} = props ?? {};

          return  refreshEntityMicrositeSource(entityId,sourceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RefreshEntityMicrositeSourceMutationResult = NonNullable<Awaited<ReturnType<typeof refreshEntityMicrositeSource>>>
    export type RefreshEntityMicrositeSourceMutationBody = MicrositeSourceRevisionWrite
    export type RefreshEntityMicrositeSourceMutationError = Error
    export type RefreshEntityMicrositeSourceMutationVariables = {entityId: string;sourceId: string;data: MicrositeSourceRevisionWrite}

    /**
 * @summary Fetch a website source within private-network, redirect, crawl, type, size, timeout, and rate boundaries
 */
export const useRefreshEntityMicrositeSource = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof refreshEntityMicrositeSource>>, TError,RefreshEntityMicrositeSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof refreshEntityMicrositeSource>>,
        TError,
        RefreshEntityMicrositeSourceMutationVariables,
        TContext
      > => {
      return useMutation(getRefreshEntityMicrositeSourceMutationOptions(options));
    }

export const getReviewEntityMicrositeSourceUrl = (entityId: string,
    sourceId: string,) => {




  return `/api/admin/entities/${entityId}/microsite/sources/${sourceId}/review`
}

/**
 * @summary Approve or reject one immutable source version with optimistic concurrency
 */
export const reviewEntityMicrositeSource = async (entityId: string,
    sourceId: string,
    micrositeSourceReviewWrite: MicrositeSourceReviewWrite, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeSource> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MicrositeSource>(getReviewEntityMicrositeSourceUrl(entityId,sourceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(micrositeSourceReviewWrite)
  }
);}





export const getReviewEntityMicrositeSourceMutationKey = () => ['reviewEntityMicrositeSource'] as const;

export const getReviewEntityMicrositeSourceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewEntityMicrositeSource>>, TError,ReviewEntityMicrositeSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewEntityMicrositeSource>>, TError,ReviewEntityMicrositeSourceMutationVariables, TContext> => {

const mutationKey = getReviewEntityMicrositeSourceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewEntityMicrositeSource>>, ReviewEntityMicrositeSourceMutationVariables> = (props) => {
          const {entityId,sourceId,data} = props ?? {};

          return  reviewEntityMicrositeSource(entityId,sourceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewEntityMicrositeSourceMutationResult = NonNullable<Awaited<ReturnType<typeof reviewEntityMicrositeSource>>>
    export type ReviewEntityMicrositeSourceMutationBody = MicrositeSourceReviewWrite
    export type ReviewEntityMicrositeSourceMutationError = Error
    export type ReviewEntityMicrositeSourceMutationVariables = {entityId: string;sourceId: string;data: MicrositeSourceReviewWrite}

    /**
 * @summary Approve or reject one immutable source version with optimistic concurrency
 */
export const useReviewEntityMicrositeSource = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewEntityMicrositeSource>>, TError,ReviewEntityMicrositeSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewEntityMicrositeSource>>,
        TError,
        ReviewEntityMicrositeSourceMutationVariables,
        TContext
      > => {
      return useMutation(getReviewEntityMicrositeSourceMutationOptions(options));
    }

export const getListEntityMicrositeBrandSourcesUrl = (entityId: string,
    params?: ListEntityMicrositeBrandSourcesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/entities/${entityId}/microsite/brand-sources?${stringifiedParams}` : `/api/admin/entities/${entityId}/microsite/brand-sources`
}

/**
 * @summary List private immutable brand-analysis sources in the authorised entity scope
 */
export const listEntityMicrositeBrandSources = async (entityId: string,
    params?: ListEntityMicrositeBrandSourcesParams, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeBrandSourceList> => {

  return customFetch<MicrositeBrandSourceList>(getListEntityMicrositeBrandSourcesUrl(entityId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListEntityMicrositeBrandSourcesQueryKey = (entityId: string,
    params?: ListEntityMicrositeBrandSourcesParams,) => {
    return [
    `/api/admin/entities/${entityId}/microsite/brand-sources`, ...(params ? [params] : [])
    ] as const;
    }


export const getListEntityMicrositeBrandSourcesQueryOptions = <TData = Awaited<ReturnType<typeof listEntityMicrositeBrandSources>>, TError = unknown>(entityId: string,
    params?: ListEntityMicrositeBrandSourcesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityMicrositeBrandSources>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEntityMicrositeBrandSourcesQueryKey(entityId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEntityMicrositeBrandSources>>> = ({ signal }) => listEntityMicrositeBrandSources(entityId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entityId !== null && entityId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEntityMicrositeBrandSources>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEntityMicrositeBrandSourcesQueryResult = NonNullable<Awaited<ReturnType<typeof listEntityMicrositeBrandSources>>>
export type ListEntityMicrositeBrandSourcesQueryError = unknown


/**
 * @summary List private immutable brand-analysis sources in the authorised entity scope
 */

export function useListEntityMicrositeBrandSources<TData = Awaited<ReturnType<typeof listEntityMicrositeBrandSources>>, TError = unknown>(
 entityId: string,
    params?: ListEntityMicrositeBrandSourcesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityMicrositeBrandSources>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEntityMicrositeBrandSourcesQueryOptions(entityId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUploadEntityMicrositeBrandGuideUrl = (entityId: string,) => {




  return `/api/admin/entities/${entityId}/microsite/brand-sources`
}

/**
 * @summary Privately upload and extract one bounded organisation style guide
 */
export const uploadEntityMicrositeBrandGuide = async (entityId: string,
    uploadEntityMicrositeBrandGuideBody: UploadEntityMicrositeBrandGuideBody, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeBrandSource> => {
    const formData = new FormData();
formData.append(`file`, uploadEntityMicrositeBrandGuideBody.file);

  return customFetch<MicrositeBrandSource>(getUploadEntityMicrositeBrandGuideUrl(entityId),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}





export const getUploadEntityMicrositeBrandGuideMutationKey = () => ['uploadEntityMicrositeBrandGuide'] as const;

export const getUploadEntityMicrositeBrandGuideMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadEntityMicrositeBrandGuide>>, TError,UploadEntityMicrositeBrandGuideMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadEntityMicrositeBrandGuide>>, TError,UploadEntityMicrositeBrandGuideMutationVariables, TContext> => {

const mutationKey = getUploadEntityMicrositeBrandGuideMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadEntityMicrositeBrandGuide>>, UploadEntityMicrositeBrandGuideMutationVariables> = (props) => {
          const {entityId,data} = props ?? {};

          return  uploadEntityMicrositeBrandGuide(entityId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadEntityMicrositeBrandGuideMutationResult = NonNullable<Awaited<ReturnType<typeof uploadEntityMicrositeBrandGuide>>>
    export type UploadEntityMicrositeBrandGuideMutationBody = UploadEntityMicrositeBrandGuideBody
    export type UploadEntityMicrositeBrandGuideMutationError = Error
    export type UploadEntityMicrositeBrandGuideMutationVariables = {entityId: string;data: UploadEntityMicrositeBrandGuideBody}

    /**
 * @summary Privately upload and extract one bounded organisation style guide
 */
export const useUploadEntityMicrositeBrandGuide = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadEntityMicrositeBrandGuide>>, TError,UploadEntityMicrositeBrandGuideMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadEntityMicrositeBrandGuide>>,
        TError,
        UploadEntityMicrositeBrandGuideMutationVariables,
        TContext
      > => {
      return useMutation(getUploadEntityMicrositeBrandGuideMutationOptions(options));
    }

export const getListEntityMicrositeBrandProposalsUrl = (entityId: string,
    params?: ListEntityMicrositeBrandProposalsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/entities/${entityId}/microsite/brand-proposals?${stringifiedParams}` : `/api/admin/entities/${entityId}/microsite/brand-proposals`
}

/**
 * @summary List bounded review-only brand analysis jobs
 */
export const listEntityMicrositeBrandProposals = async (entityId: string,
    params?: ListEntityMicrositeBrandProposalsParams, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeBrandProposalList> => {

  return customFetch<MicrositeBrandProposalList>(getListEntityMicrositeBrandProposalsUrl(entityId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListEntityMicrositeBrandProposalsQueryKey = (entityId: string,
    params?: ListEntityMicrositeBrandProposalsParams,) => {
    return [
    `/api/admin/entities/${entityId}/microsite/brand-proposals`, ...(params ? [params] : [])
    ] as const;
    }


export const getListEntityMicrositeBrandProposalsQueryOptions = <TData = Awaited<ReturnType<typeof listEntityMicrositeBrandProposals>>, TError = unknown>(entityId: string,
    params?: ListEntityMicrositeBrandProposalsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityMicrositeBrandProposals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEntityMicrositeBrandProposalsQueryKey(entityId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEntityMicrositeBrandProposals>>> = ({ signal }) => listEntityMicrositeBrandProposals(entityId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entityId !== null && entityId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEntityMicrositeBrandProposals>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEntityMicrositeBrandProposalsQueryResult = NonNullable<Awaited<ReturnType<typeof listEntityMicrositeBrandProposals>>>
export type ListEntityMicrositeBrandProposalsQueryError = unknown


/**
 * @summary List bounded review-only brand analysis jobs
 */

export function useListEntityMicrositeBrandProposals<TData = Awaited<ReturnType<typeof listEntityMicrositeBrandProposals>>, TError = unknown>(
 entityId: string,
    params?: ListEntityMicrositeBrandProposalsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityMicrositeBrandProposals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEntityMicrositeBrandProposalsQueryOptions(entityId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateEntityMicrositeBrandProposalUrl = (entityId: string,) => {




  return `/api/admin/entities/${entityId}/microsite/brand-proposals`
}

/**
 * @summary Analyse a private style guide, a public website, or both without changing the draft
 */
export const createEntityMicrositeBrandProposal = async (entityId: string,
    micrositeBrandProposalCreate: MicrositeBrandProposalCreate, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeBrandProposal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MicrositeBrandProposal>(getCreateEntityMicrositeBrandProposalUrl(entityId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(micrositeBrandProposalCreate)
  }
);}





export const getCreateEntityMicrositeBrandProposalMutationKey = () => ['createEntityMicrositeBrandProposal'] as const;

export const getCreateEntityMicrositeBrandProposalMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEntityMicrositeBrandProposal>>, TError,CreateEntityMicrositeBrandProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createEntityMicrositeBrandProposal>>, TError,CreateEntityMicrositeBrandProposalMutationVariables, TContext> => {

const mutationKey = getCreateEntityMicrositeBrandProposalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createEntityMicrositeBrandProposal>>, CreateEntityMicrositeBrandProposalMutationVariables> = (props) => {
          const {entityId,data} = props ?? {};

          return  createEntityMicrositeBrandProposal(entityId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateEntityMicrositeBrandProposalMutationResult = NonNullable<Awaited<ReturnType<typeof createEntityMicrositeBrandProposal>>>
    export type CreateEntityMicrositeBrandProposalMutationBody = MicrositeBrandProposalCreate
    export type CreateEntityMicrositeBrandProposalMutationError = Error
    export type CreateEntityMicrositeBrandProposalMutationVariables = {entityId: string;data: MicrositeBrandProposalCreate}

    /**
 * @summary Analyse a private style guide, a public website, or both without changing the draft
 */
export const useCreateEntityMicrositeBrandProposal = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEntityMicrositeBrandProposal>>, TError,CreateEntityMicrositeBrandProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createEntityMicrositeBrandProposal>>,
        TError,
        CreateEntityMicrositeBrandProposalMutationVariables,
        TContext
      > => {
      return useMutation(getCreateEntityMicrositeBrandProposalMutationOptions(options));
    }

export const getGetEntityMicrositeBrandProposalUrl = (entityId: string,
    proposalId: string,) => {




  return `/api/admin/entities/${entityId}/microsite/brand-proposals/${proposalId}`
}

/**
 * @summary Read one entity-scoped proposal and its source evidence
 */
export const getEntityMicrositeBrandProposal = async (entityId: string,
    proposalId: string, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeBrandProposal> => {

  return customFetch<MicrositeBrandProposal>(getGetEntityMicrositeBrandProposalUrl(entityId,proposalId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEntityMicrositeBrandProposalQueryKey = (entityId: string,
    proposalId: string,) => {
    return [
    `/api/admin/entities/${entityId}/microsite/brand-proposals/${proposalId}`
    ] as const;
    }


export const getGetEntityMicrositeBrandProposalQueryOptions = <TData = Awaited<ReturnType<typeof getEntityMicrositeBrandProposal>>, TError = Error>(entityId: string,
    proposalId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityMicrositeBrandProposal>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEntityMicrositeBrandProposalQueryKey(entityId,proposalId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEntityMicrositeBrandProposal>>> = ({ signal }) => getEntityMicrositeBrandProposal(entityId,proposalId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entityId !== null && entityId !== undefined && proposalId !== null && proposalId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEntityMicrositeBrandProposal>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEntityMicrositeBrandProposalQueryResult = NonNullable<Awaited<ReturnType<typeof getEntityMicrositeBrandProposal>>>
export type GetEntityMicrositeBrandProposalQueryError = Error


/**
 * @summary Read one entity-scoped proposal and its source evidence
 */

export function useGetEntityMicrositeBrandProposal<TData = Awaited<ReturnType<typeof getEntityMicrositeBrandProposal>>, TError = Error>(
 entityId: string,
    proposalId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityMicrositeBrandProposal>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEntityMicrositeBrandProposalQueryOptions(entityId,proposalId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRetryEntityMicrositeBrandProposalUrl = (entityId: string,
    proposalId: string,) => {




  return `/api/admin/entities/${entityId}/microsite/brand-proposals/${proposalId}/retry`
}

/**
 * @summary Retry a failed analysis against the same source versions and unchanged draft
 */
export const retryEntityMicrositeBrandProposal = async (entityId: string,
    proposalId: string,
    micrositeBrandProposalRevisionWrite: MicrositeBrandProposalRevisionWrite, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeBrandProposal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MicrositeBrandProposal>(getRetryEntityMicrositeBrandProposalUrl(entityId,proposalId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(micrositeBrandProposalRevisionWrite)
  }
);}





export const getRetryEntityMicrositeBrandProposalMutationKey = () => ['retryEntityMicrositeBrandProposal'] as const;

export const getRetryEntityMicrositeBrandProposalMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryEntityMicrositeBrandProposal>>, TError,RetryEntityMicrositeBrandProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof retryEntityMicrositeBrandProposal>>, TError,RetryEntityMicrositeBrandProposalMutationVariables, TContext> => {

const mutationKey = getRetryEntityMicrositeBrandProposalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof retryEntityMicrositeBrandProposal>>, RetryEntityMicrositeBrandProposalMutationVariables> = (props) => {
          const {entityId,proposalId,data} = props ?? {};

          return  retryEntityMicrositeBrandProposal(entityId,proposalId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RetryEntityMicrositeBrandProposalMutationResult = NonNullable<Awaited<ReturnType<typeof retryEntityMicrositeBrandProposal>>>
    export type RetryEntityMicrositeBrandProposalMutationBody = MicrositeBrandProposalRevisionWrite
    export type RetryEntityMicrositeBrandProposalMutationError = unknown
    export type RetryEntityMicrositeBrandProposalMutationVariables = {entityId: string;proposalId: string;data: MicrositeBrandProposalRevisionWrite}

    /**
 * @summary Retry a failed analysis against the same source versions and unchanged draft
 */
export const useRetryEntityMicrositeBrandProposal = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryEntityMicrositeBrandProposal>>, TError,RetryEntityMicrositeBrandProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof retryEntityMicrositeBrandProposal>>,
        TError,
        RetryEntityMicrositeBrandProposalMutationVariables,
        TContext
      > => {
      return useMutation(getRetryEntityMicrositeBrandProposalMutationOptions(options));
    }

export const getResolveEntityMicrositeBrandProposalConflictsUrl = (entityId: string,
    proposalId: string,) => {




  return `/api/admin/entities/${entityId}/microsite/brand-proposals/${proposalId}/resolutions`
}

/**
 * @summary Record an explicit guide, website, custom, or keep-current decision for conflicts
 */
export const resolveEntityMicrositeBrandProposalConflicts = async (entityId: string,
    proposalId: string,
    micrositeBrandConflictResolutionWrite: MicrositeBrandConflictResolutionWrite, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeBrandProposal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MicrositeBrandProposal>(getResolveEntityMicrositeBrandProposalConflictsUrl(entityId,proposalId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(micrositeBrandConflictResolutionWrite)
  }
);}





export const getResolveEntityMicrositeBrandProposalConflictsMutationKey = () => ['resolveEntityMicrositeBrandProposalConflicts'] as const;

export const getResolveEntityMicrositeBrandProposalConflictsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resolveEntityMicrositeBrandProposalConflicts>>, TError,ResolveEntityMicrositeBrandProposalConflictsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof resolveEntityMicrositeBrandProposalConflicts>>, TError,ResolveEntityMicrositeBrandProposalConflictsMutationVariables, TContext> => {

const mutationKey = getResolveEntityMicrositeBrandProposalConflictsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof resolveEntityMicrositeBrandProposalConflicts>>, ResolveEntityMicrositeBrandProposalConflictsMutationVariables> = (props) => {
          const {entityId,proposalId,data} = props ?? {};

          return  resolveEntityMicrositeBrandProposalConflicts(entityId,proposalId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResolveEntityMicrositeBrandProposalConflictsMutationResult = NonNullable<Awaited<ReturnType<typeof resolveEntityMicrositeBrandProposalConflicts>>>
    export type ResolveEntityMicrositeBrandProposalConflictsMutationBody = MicrositeBrandConflictResolutionWrite
    export type ResolveEntityMicrositeBrandProposalConflictsMutationError = Error
    export type ResolveEntityMicrositeBrandProposalConflictsMutationVariables = {entityId: string;proposalId: string;data: MicrositeBrandConflictResolutionWrite}

    /**
 * @summary Record an explicit guide, website, custom, or keep-current decision for conflicts
 */
export const useResolveEntityMicrositeBrandProposalConflicts = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resolveEntityMicrositeBrandProposalConflicts>>, TError,ResolveEntityMicrositeBrandProposalConflictsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof resolveEntityMicrositeBrandProposalConflicts>>,
        TError,
        ResolveEntityMicrositeBrandProposalConflictsMutationVariables,
        TContext
      > => {
      return useMutation(getResolveEntityMicrositeBrandProposalConflictsMutationOptions(options));
    }

export const getImportEntityMicrositeBrandAssetUrl = (entityId: string,
    proposalId: string,) => {




  return `/api/admin/entities/${entityId}/microsite/brand-proposals/${proposalId}/assets`
}

/**
 * @summary Revalidate and import one accepted website image into the entity asset library
 */
export const importEntityMicrositeBrandAsset = async (entityId: string,
    proposalId: string,
    micrositeBrandAssetImport: MicrositeBrandAssetImport, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeAsset> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MicrositeAsset>(getImportEntityMicrositeBrandAssetUrl(entityId,proposalId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(micrositeBrandAssetImport)
  }
);}





export const getImportEntityMicrositeBrandAssetMutationKey = () => ['importEntityMicrositeBrandAsset'] as const;

export const getImportEntityMicrositeBrandAssetMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importEntityMicrositeBrandAsset>>, TError,ImportEntityMicrositeBrandAssetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof importEntityMicrositeBrandAsset>>, TError,ImportEntityMicrositeBrandAssetMutationVariables, TContext> => {

const mutationKey = getImportEntityMicrositeBrandAssetMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof importEntityMicrositeBrandAsset>>, ImportEntityMicrositeBrandAssetMutationVariables> = (props) => {
          const {entityId,proposalId,data} = props ?? {};

          return  importEntityMicrositeBrandAsset(entityId,proposalId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ImportEntityMicrositeBrandAssetMutationResult = NonNullable<Awaited<ReturnType<typeof importEntityMicrositeBrandAsset>>>
    export type ImportEntityMicrositeBrandAssetMutationBody = MicrositeBrandAssetImport
    export type ImportEntityMicrositeBrandAssetMutationError = Error
    export type ImportEntityMicrositeBrandAssetMutationVariables = {entityId: string;proposalId: string;data: MicrositeBrandAssetImport}

    /**
 * @summary Revalidate and import one accepted website image into the entity asset library
 */
export const useImportEntityMicrositeBrandAsset = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importEntityMicrositeBrandAsset>>, TError,ImportEntityMicrositeBrandAssetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof importEntityMicrositeBrandAsset>>,
        TError,
        ImportEntityMicrositeBrandAssetMutationVariables,
        TContext
      > => {
      return useMutation(getImportEntityMicrositeBrandAssetMutationOptions(options));
    }

export const getGetPublicMicrositeUrl = (slug: string,) => {




  return `/api/public/microsites/${slug}`
}

/**
 * Returns only visitor-safe published fields after resolving the active shared public binding.
 * @summary Get the latest published public microsite shell without authentication
 */
export const getPublicMicrosite = async (slug: string, options?: Parameters<typeof customFetch>[1]): Promise<MicrositePublicSite> => {

  return customFetch<MicrositePublicSite>(getGetPublicMicrositeUrl(slug),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPublicMicrositeQueryKey = (slug: string,) => {
    return [
    `/api/public/microsites/${slug}`
    ] as const;
    }


export const getGetPublicMicrositeQueryOptions = <TData = Awaited<ReturnType<typeof getPublicMicrosite>>, TError = Error>(slug: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicMicrosite>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPublicMicrositeQueryKey(slug);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPublicMicrosite>>> = ({ signal }) => getPublicMicrosite(slug, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: slug !== null && slug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPublicMicrosite>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPublicMicrositeQueryResult = NonNullable<Awaited<ReturnType<typeof getPublicMicrosite>>>
export type GetPublicMicrositeQueryError = Error


/**
 * @summary Get the latest published public microsite shell without authentication
 */

export function useGetPublicMicrosite<TData = Awaited<ReturnType<typeof getPublicMicrosite>>, TError = Error>(
 slug: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicMicrosite>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPublicMicrositeQueryOptions(slug,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPublicMicrositeAssetUrl = (slug: string,
    assetId: string,) => {




  return `/api/public/microsites/${slug}/assets/${assetId}`
}

/**
 * @summary Get an image referenced by the latest published microsite
 */
export const getPublicMicrositeAsset = async (slug: string,
    assetId: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getGetPublicMicrositeAssetUrl(slug,assetId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPublicMicrositeAssetQueryKey = (slug: string,
    assetId: string,) => {
    return [
    `/api/public/microsites/${slug}/assets/${assetId}`
    ] as const;
    }


export const getGetPublicMicrositeAssetQueryOptions = <TData = Awaited<ReturnType<typeof getPublicMicrositeAsset>>, TError = Error>(slug: string,
    assetId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicMicrositeAsset>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPublicMicrositeAssetQueryKey(slug,assetId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPublicMicrositeAsset>>> = ({ signal }) => getPublicMicrositeAsset(slug,assetId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: slug !== null && slug !== undefined && assetId !== null && assetId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPublicMicrositeAsset>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPublicMicrositeAssetQueryResult = NonNullable<Awaited<ReturnType<typeof getPublicMicrositeAsset>>>
export type GetPublicMicrositeAssetQueryError = Error


/**
 * @summary Get an image referenced by the latest published microsite
 */

export function useGetPublicMicrositeAsset<TData = Awaited<ReturnType<typeof getPublicMicrositeAsset>>, TError = Error>(
 slug: string,
    assetId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicMicrositeAsset>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPublicMicrositeAssetQueryOptions(slug,assetId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGeneratePublicMicrositePageUrl = (slug: string,) => {




  return `/api/public/microsites/${slug}/page`
}

/**
 * Resolves ownership from the active shared public binding; never accepts tenant, entity, or raw instruction input.
 * @summary Generate or load a structured published microsite page without authentication
 */
export const generatePublicMicrositePage = async (slug: string,
    micrositePageRequest: MicrositePageRequest, options?: Parameters<typeof customFetch>[1]): Promise<MicrositePageComposition> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MicrositePageComposition>(getGeneratePublicMicrositePageUrl(slug),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(micrositePageRequest)
  }
);}





export const getGeneratePublicMicrositePageMutationKey = () => ['generatePublicMicrositePage'] as const;

export const getGeneratePublicMicrositePageMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generatePublicMicrositePage>>, TError,GeneratePublicMicrositePageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof generatePublicMicrositePage>>, TError,GeneratePublicMicrositePageMutationVariables, TContext> => {

const mutationKey = getGeneratePublicMicrositePageMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof generatePublicMicrositePage>>, GeneratePublicMicrositePageMutationVariables> = (props) => {
          const {slug,data} = props ?? {};

          return  generatePublicMicrositePage(slug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GeneratePublicMicrositePageMutationResult = NonNullable<Awaited<ReturnType<typeof generatePublicMicrositePage>>>
    export type GeneratePublicMicrositePageMutationBody = MicrositePageRequest
    export type GeneratePublicMicrositePageMutationError = Error
    export type GeneratePublicMicrositePageMutationVariables = {slug: string;data: MicrositePageRequest}

    /**
 * @summary Generate or load a structured published microsite page without authentication
 */
export const useGeneratePublicMicrositePage = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generatePublicMicrositePage>>, TError,GeneratePublicMicrositePageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof generatePublicMicrositePage>>,
        TError,
        GeneratePublicMicrositePageMutationVariables,
        TContext
      > => {
      return useMutation(getGeneratePublicMicrositePageMutationOptions(options));
    }

export const getSubmitPublicMicrositeEnquiryUrl = (slug: string,) => {




  return `/api/public/microsites/${slug}/enquiries`
}

/**
 * Reuses public enquiry validation, honeypot, and rate limits; tenant and entity identifiers are never accepted from the visitor.
 * @summary Submit an enquiry to the entity resolved from a published microsite
 */
export const submitPublicMicrositeEnquiry = async (slug: string,
    publicEnquirySubmission: PublicEnquirySubmission, options?: Parameters<typeof customFetch>[1]): Promise<PublicEnquiryConfirmation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PublicEnquiryConfirmation>(getSubmitPublicMicrositeEnquiryUrl(slug),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(publicEnquirySubmission)
  }
);}





export const getSubmitPublicMicrositeEnquiryMutationKey = () => ['submitPublicMicrositeEnquiry'] as const;

export const getSubmitPublicMicrositeEnquiryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitPublicMicrositeEnquiry>>, TError,SubmitPublicMicrositeEnquiryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof submitPublicMicrositeEnquiry>>, TError,SubmitPublicMicrositeEnquiryMutationVariables, TContext> => {

const mutationKey = getSubmitPublicMicrositeEnquiryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof submitPublicMicrositeEnquiry>>, SubmitPublicMicrositeEnquiryMutationVariables> = (props) => {
          const {slug,data} = props ?? {};

          return  submitPublicMicrositeEnquiry(slug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SubmitPublicMicrositeEnquiryMutationResult = NonNullable<Awaited<ReturnType<typeof submitPublicMicrositeEnquiry>>>
    export type SubmitPublicMicrositeEnquiryMutationBody = PublicEnquirySubmission
    export type SubmitPublicMicrositeEnquiryMutationError = Error
    export type SubmitPublicMicrositeEnquiryMutationVariables = {slug: string;data: PublicEnquirySubmission}

    /**
 * @summary Submit an enquiry to the entity resolved from a published microsite
 */
export const useSubmitPublicMicrositeEnquiry = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitPublicMicrositeEnquiry>>, TError,SubmitPublicMicrositeEnquiryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof submitPublicMicrositeEnquiry>>,
        TError,
        SubmitPublicMicrositeEnquiryMutationVariables,
        TContext
      > => {
      return useMutation(getSubmitPublicMicrositeEnquiryMutationOptions(options));
    }

export const getListPublicMicrositeBookingSlotsUrl = (slug: string,
    params?: ListPublicMicrositeBookingSlotsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/public/microsites/${slug}/booking-slots?${stringifiedParams}` : `/api/public/microsites/${slug}/booking-slots`
}

/**
 * Tenant and entity identifiers are resolved exclusively from the active public microsite binding.
 * @summary List future bookable slots for the server-resolved entity
 */
export const listPublicMicrositeBookingSlots = async (slug: string,
    params?: ListPublicMicrositeBookingSlotsParams, options?: Parameters<typeof customFetch>[1]): Promise<PublicBookingSlotList> => {

  return customFetch<PublicBookingSlotList>(getListPublicMicrositeBookingSlotsUrl(slug,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPublicMicrositeBookingSlotsQueryKey = (slug: string,
    params?: ListPublicMicrositeBookingSlotsParams,) => {
    return [
    `/api/public/microsites/${slug}/booking-slots`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPublicMicrositeBookingSlotsQueryOptions = <TData = Awaited<ReturnType<typeof listPublicMicrositeBookingSlots>>, TError = Error>(slug: string,
    params?: ListPublicMicrositeBookingSlotsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPublicMicrositeBookingSlots>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPublicMicrositeBookingSlotsQueryKey(slug,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPublicMicrositeBookingSlots>>> = ({ signal }) => listPublicMicrositeBookingSlots(slug,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: slug !== null && slug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPublicMicrositeBookingSlots>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPublicMicrositeBookingSlotsQueryResult = NonNullable<Awaited<ReturnType<typeof listPublicMicrositeBookingSlots>>>
export type ListPublicMicrositeBookingSlotsQueryError = Error


/**
 * @summary List future bookable slots for the server-resolved entity
 */

export function useListPublicMicrositeBookingSlots<TData = Awaited<ReturnType<typeof listPublicMicrositeBookingSlots>>, TError = Error>(
 slug: string,
    params?: ListPublicMicrositeBookingSlotsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPublicMicrositeBookingSlots>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPublicMicrositeBookingSlotsQueryOptions(slug,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreatePublicMicrositeBookingUrl = (slug: string,) => {




  return `/api/public/microsites/${slug}/bookings`
}

/**
 * Reuses canonical CRM contacts and enquiries; tenant, entity, contact, account, and enquiry identifiers are never accepted from the visitor.
 * @summary Reserve a capacity-controlled slot for the server-resolved entity
 */
export const createPublicMicrositeBooking = async (slug: string,
    publicBookingCreate: PublicBookingCreate, options?: Parameters<typeof customFetch>[1]): Promise<PublicBookingConfirmation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PublicBookingConfirmation>(getCreatePublicMicrositeBookingUrl(slug),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(publicBookingCreate)
  }
);}





export const getCreatePublicMicrositeBookingMutationKey = () => ['createPublicMicrositeBooking'] as const;

export const getCreatePublicMicrositeBookingMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPublicMicrositeBooking>>, TError,CreatePublicMicrositeBookingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createPublicMicrositeBooking>>, TError,CreatePublicMicrositeBookingMutationVariables, TContext> => {

const mutationKey = getCreatePublicMicrositeBookingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createPublicMicrositeBooking>>, CreatePublicMicrositeBookingMutationVariables> = (props) => {
          const {slug,data} = props ?? {};

          return  createPublicMicrositeBooking(slug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreatePublicMicrositeBookingMutationResult = NonNullable<Awaited<ReturnType<typeof createPublicMicrositeBooking>>>
    export type CreatePublicMicrositeBookingMutationBody = PublicBookingCreate
    export type CreatePublicMicrositeBookingMutationError = Error
    export type CreatePublicMicrositeBookingMutationVariables = {slug: string;data: PublicBookingCreate}

    /**
 * @summary Reserve a capacity-controlled slot for the server-resolved entity
 */
export const useCreatePublicMicrositeBooking = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPublicMicrositeBooking>>, TError,CreatePublicMicrositeBookingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createPublicMicrositeBooking>>,
        TError,
        CreatePublicMicrositeBookingMutationVariables,
        TContext
      > => {
      return useMutation(getCreatePublicMicrositeBookingMutationOptions(options));
    }

export const getMicrositeChatUrl = (slug: string,) => {




  return `/api/public/microsites/${slug}/chat`
}

/**
 * Streams Server-Sent Events grounded only in approved sources from the resolved published microsite.
 * @summary Stream a microsite-scoped AI chat reply
 */
export const micrositeChat = async (slug: string,
    publicChatRequest: PublicChatRequest, options?: Parameters<typeof customFetch>[1]): Promise<string> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<string>(getMicrositeChatUrl(slug),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(publicChatRequest)
  }
);}





export const getMicrositeChatMutationKey = () => ['micrositeChat'] as const;

export const getMicrositeChatMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof micrositeChat>>, TError,MicrositeChatMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof micrositeChat>>, TError,MicrositeChatMutationVariables, TContext> => {

const mutationKey = getMicrositeChatMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof micrositeChat>>, MicrositeChatMutationVariables> = (props) => {
          const {slug,data} = props ?? {};

          return  micrositeChat(slug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MicrositeChatMutationResult = NonNullable<Awaited<ReturnType<typeof micrositeChat>>>
    export type MicrositeChatMutationBody = PublicChatRequest
    export type MicrositeChatMutationError = Error
    export type MicrositeChatMutationVariables = {slug: string;data: PublicChatRequest}

    /**
 * @summary Stream a microsite-scoped AI chat reply
 */
export const useMicrositeChat = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof micrositeChat>>, TError,MicrositeChatMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof micrositeChat>>,
        TError,
        MicrositeChatMutationVariables,
        TContext
      > => {
      return useMutation(getMicrositeChatMutationOptions(options));
    }

export const getCaptureMicrositeChatLeadUrl = (slug: string,) => {




  return `/api/public/microsites/${slug}/chat/capture`
}

/**
 * @summary Capture visitor details for a microsite chat
 */
export const captureMicrositeChatLead = async (slug: string,
    publicChatLeadCapture: PublicChatLeadCapture, options?: Parameters<typeof customFetch>[1]): Promise<PublicChatLeadCaptureResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PublicChatLeadCaptureResult>(getCaptureMicrositeChatLeadUrl(slug),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(publicChatLeadCapture)
  }
);}





export const getCaptureMicrositeChatLeadMutationKey = () => ['captureMicrositeChatLead'] as const;

export const getCaptureMicrositeChatLeadMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof captureMicrositeChatLead>>, TError,CaptureMicrositeChatLeadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof captureMicrositeChatLead>>, TError,CaptureMicrositeChatLeadMutationVariables, TContext> => {

const mutationKey = getCaptureMicrositeChatLeadMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof captureMicrositeChatLead>>, CaptureMicrositeChatLeadMutationVariables> = (props) => {
          const {slug,data} = props ?? {};

          return  captureMicrositeChatLead(slug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CaptureMicrositeChatLeadMutationResult = NonNullable<Awaited<ReturnType<typeof captureMicrositeChatLead>>>
    export type CaptureMicrositeChatLeadMutationBody = PublicChatLeadCapture
    export type CaptureMicrositeChatLeadMutationError = Error
    export type CaptureMicrositeChatLeadMutationVariables = {slug: string;data: PublicChatLeadCapture}

    /**
 * @summary Capture visitor details for a microsite chat
 */
export const useCaptureMicrositeChatLead = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof captureMicrositeChatLead>>, TError,CaptureMicrositeChatLeadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof captureMicrositeChatLead>>,
        TError,
        CaptureMicrositeChatLeadMutationVariables,
        TContext
      > => {
      return useMutation(getCaptureMicrositeChatLeadMutationOptions(options));
    }

export const getRequestMicrositeChatHandoffUrl = (slug: string,) => {




  return `/api/public/microsites/${slug}/chat/handoff`
}

/**
 * @summary Request human follow-up for a microsite chat
 */
export const requestMicrositeChatHandoff = async (slug: string,
    publicChatHandoffRequest: PublicChatHandoffRequest, options?: Parameters<typeof customFetch>[1]): Promise<PublicChatHandoffResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PublicChatHandoffResult>(getRequestMicrositeChatHandoffUrl(slug),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(publicChatHandoffRequest)
  }
);}





export const getRequestMicrositeChatHandoffMutationKey = () => ['requestMicrositeChatHandoff'] as const;

export const getRequestMicrositeChatHandoffMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestMicrositeChatHandoff>>, TError,RequestMicrositeChatHandoffMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof requestMicrositeChatHandoff>>, TError,RequestMicrositeChatHandoffMutationVariables, TContext> => {

const mutationKey = getRequestMicrositeChatHandoffMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof requestMicrositeChatHandoff>>, RequestMicrositeChatHandoffMutationVariables> = (props) => {
          const {slug,data} = props ?? {};

          return  requestMicrositeChatHandoff(slug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RequestMicrositeChatHandoffMutationResult = NonNullable<Awaited<ReturnType<typeof requestMicrositeChatHandoff>>>
    export type RequestMicrositeChatHandoffMutationBody = PublicChatHandoffRequest
    export type RequestMicrositeChatHandoffMutationError = Error
    export type RequestMicrositeChatHandoffMutationVariables = {slug: string;data: PublicChatHandoffRequest}

    /**
 * @summary Request human follow-up for a microsite chat
 */
export const useRequestMicrositeChatHandoff = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestMicrositeChatHandoff>>, TError,RequestMicrositeChatHandoffMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof requestMicrositeChatHandoff>>,
        TError,
        RequestMicrositeChatHandoffMutationVariables,
        TContext
      > => {
      return useMutation(getRequestMicrositeChatHandoffMutationOptions(options));
    }

export const getSubmitPublicEnquiryUrl = () => {




  return `/api/public/enquiries`
}

/**
 * Public endpoint used by the website enquiry form. Protected by a honeypot field and per-client rate limiting rather than authentication.
 * @summary Submit a website enquiry (public, no authentication)
 */
export const submitPublicEnquiry = async (publicEnquirySubmission: PublicEnquirySubmission, options?: Parameters<typeof customFetch>[1]): Promise<PublicEnquiryConfirmation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PublicEnquiryConfirmation>(getSubmitPublicEnquiryUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(publicEnquirySubmission)
  }
);}





export const getSubmitPublicEnquiryMutationKey = () => ['submitPublicEnquiry'] as const;

export const getSubmitPublicEnquiryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitPublicEnquiry>>, TError,SubmitPublicEnquiryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof submitPublicEnquiry>>, TError,SubmitPublicEnquiryMutationVariables, TContext> => {

const mutationKey = getSubmitPublicEnquiryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof submitPublicEnquiry>>, SubmitPublicEnquiryMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  submitPublicEnquiry(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SubmitPublicEnquiryMutationResult = NonNullable<Awaited<ReturnType<typeof submitPublicEnquiry>>>
    export type SubmitPublicEnquiryMutationBody = PublicEnquirySubmission
    export type SubmitPublicEnquiryMutationError = Error
    export type SubmitPublicEnquiryMutationVariables = {data: PublicEnquirySubmission}

    /**
 * @summary Submit a website enquiry (public, no authentication)
 */
export const useSubmitPublicEnquiry = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitPublicEnquiry>>, TError,SubmitPublicEnquiryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof submitPublicEnquiry>>,
        TError,
        SubmitPublicEnquiryMutationVariables,
        TContext
      > => {
      return useMutation(getSubmitPublicEnquiryMutationOptions(options));
    }

export const getPublicChatUrl = () => {




  return `/api/public/chat`
}

/**
 * Public endpoint backing the website AI chat widget. Streams the assistant reply as Server-Sent Events (text/event-stream), so the SPA consumes it with a dedicated streaming reader instead of a generated hook. Protected by per-client rate limiting, message length caps, and a honeypot field. The assistant can create Partner Engagement enquiries (source "chat") on the visitor's behalf.
 * @summary Website AI chat (public, no authentication)
 */
export const publicChat = async (publicChatRequest: PublicChatRequest, options?: Parameters<typeof customFetch>[1]): Promise<string> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<string>(getPublicChatUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(publicChatRequest)
  }
);}





export const getPublicChatMutationKey = () => ['publicChat'] as const;

export const getPublicChatMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof publicChat>>, TError,PublicChatMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof publicChat>>, TError,PublicChatMutationVariables, TContext> => {

const mutationKey = getPublicChatMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof publicChat>>, PublicChatMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  publicChat(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PublicChatMutationResult = NonNullable<Awaited<ReturnType<typeof publicChat>>>
    export type PublicChatMutationBody = PublicChatRequest
    export type PublicChatMutationError = Error
    export type PublicChatMutationVariables = {data: PublicChatRequest}

    /**
 * @summary Website AI chat (public, no authentication)
 */
export const usePublicChat = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof publicChat>>, TError,PublicChatMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof publicChat>>,
        TError,
        PublicChatMutationVariables,
        TContext
      > => {
      return useMutation(getPublicChatMutationOptions(options));
    }

export const getCapturePublicChatLeadUrl = () => {




  return `/api/public/chat/capture`
}

/**
 * @summary Attach visitor details to a public chat and create or update the CRM lead
 */
export const capturePublicChatLead = async (publicChatLeadCapture: PublicChatLeadCapture, options?: Parameters<typeof customFetch>[1]): Promise<PublicChatLeadCaptureResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PublicChatLeadCaptureResult>(getCapturePublicChatLeadUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(publicChatLeadCapture)
  }
);}





export const getCapturePublicChatLeadMutationKey = () => ['capturePublicChatLead'] as const;

export const getCapturePublicChatLeadMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof capturePublicChatLead>>, TError,CapturePublicChatLeadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof capturePublicChatLead>>, TError,CapturePublicChatLeadMutationVariables, TContext> => {

const mutationKey = getCapturePublicChatLeadMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof capturePublicChatLead>>, CapturePublicChatLeadMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  capturePublicChatLead(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CapturePublicChatLeadMutationResult = NonNullable<Awaited<ReturnType<typeof capturePublicChatLead>>>
    export type CapturePublicChatLeadMutationBody = PublicChatLeadCapture
    export type CapturePublicChatLeadMutationError = Error
    export type CapturePublicChatLeadMutationVariables = {data: PublicChatLeadCapture}

    /**
 * @summary Attach visitor details to a public chat and create or update the CRM lead
 */
export const useCapturePublicChatLead = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof capturePublicChatLead>>, TError,CapturePublicChatLeadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof capturePublicChatLead>>,
        TError,
        CapturePublicChatLeadMutationVariables,
        TContext
      > => {
      return useMutation(getCapturePublicChatLeadMutationOptions(options));
    }

export const getRequestPublicChatHandoffUrl = () => {




  return `/api/public/chat/handoff`
}

/**
 * @summary Request a human follow-up for a public chat
 */
export const requestPublicChatHandoff = async (publicChatHandoffRequest: PublicChatHandoffRequest, options?: Parameters<typeof customFetch>[1]): Promise<PublicChatHandoffResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PublicChatHandoffResult>(getRequestPublicChatHandoffUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(publicChatHandoffRequest)
  }
);}





export const getRequestPublicChatHandoffMutationKey = () => ['requestPublicChatHandoff'] as const;

export const getRequestPublicChatHandoffMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestPublicChatHandoff>>, TError,RequestPublicChatHandoffMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof requestPublicChatHandoff>>, TError,RequestPublicChatHandoffMutationVariables, TContext> => {

const mutationKey = getRequestPublicChatHandoffMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof requestPublicChatHandoff>>, RequestPublicChatHandoffMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  requestPublicChatHandoff(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RequestPublicChatHandoffMutationResult = NonNullable<Awaited<ReturnType<typeof requestPublicChatHandoff>>>
    export type RequestPublicChatHandoffMutationBody = PublicChatHandoffRequest
    export type RequestPublicChatHandoffMutationError = Error
    export type RequestPublicChatHandoffMutationVariables = {data: PublicChatHandoffRequest}

    /**
 * @summary Request a human follow-up for a public chat
 */
export const useRequestPublicChatHandoff = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestPublicChatHandoff>>, TError,RequestPublicChatHandoffMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof requestPublicChatHandoff>>,
        TError,
        RequestPublicChatHandoffMutationVariables,
        TContext
      > => {
      return useMutation(getRequestPublicChatHandoffMutationOptions(options));
    }

export const getListChatAssistantSessionsUrl = (params?: ListChatAssistantSessionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/chat-assistant/sessions?${stringifiedParams}` : `/api/chat-assistant/sessions`
}

/**
 * @summary List persisted chat sessions and human handoff queue
 */
export const listChatAssistantSessions = async (params?: ListChatAssistantSessionsParams, options?: Parameters<typeof customFetch>[1]): Promise<ChatAssistantSessionList> => {

  return customFetch<ChatAssistantSessionList>(getListChatAssistantSessionsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListChatAssistantSessionsQueryKey = (params?: ListChatAssistantSessionsParams,) => {
    return [
    `/api/chat-assistant/sessions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListChatAssistantSessionsQueryOptions = <TData = Awaited<ReturnType<typeof listChatAssistantSessions>>, TError = unknown>(params?: ListChatAssistantSessionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listChatAssistantSessions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListChatAssistantSessionsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listChatAssistantSessions>>> = ({ signal }) => listChatAssistantSessions(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listChatAssistantSessions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListChatAssistantSessionsQueryResult = NonNullable<Awaited<ReturnType<typeof listChatAssistantSessions>>>
export type ListChatAssistantSessionsQueryError = unknown


/**
 * @summary List persisted chat sessions and human handoff queue
 */

export function useListChatAssistantSessions<TData = Awaited<ReturnType<typeof listChatAssistantSessions>>, TError = unknown>(
 params?: ListChatAssistantSessionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listChatAssistantSessions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListChatAssistantSessionsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetChatAssistantSessionUrl = (id: string,) => {




  return `/api/chat-assistant/sessions/${id}`
}

/**
 * @summary Get a chat session with its complete transcript
 */
export const getChatAssistantSession = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<ChatAssistantSessionDetail> => {

  return customFetch<ChatAssistantSessionDetail>(getGetChatAssistantSessionUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetChatAssistantSessionQueryKey = (id: string,) => {
    return [
    `/api/chat-assistant/sessions/${id}`
    ] as const;
    }


export const getGetChatAssistantSessionQueryOptions = <TData = Awaited<ReturnType<typeof getChatAssistantSession>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getChatAssistantSession>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetChatAssistantSessionQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getChatAssistantSession>>> = ({ signal }) => getChatAssistantSession(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getChatAssistantSession>>, TError, TData> & { queryKey: QueryKey }
}

export type GetChatAssistantSessionQueryResult = NonNullable<Awaited<ReturnType<typeof getChatAssistantSession>>>
export type GetChatAssistantSessionQueryError = Error


/**
 * @summary Get a chat session with its complete transcript
 */

export function useGetChatAssistantSession<TData = Awaited<ReturnType<typeof getChatAssistantSession>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getChatAssistantSession>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetChatAssistantSessionQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getResolveChatAssistantSessionUrl = (id: string,) => {




  return `/api/chat-assistant/sessions/${id}/resolve`
}

/**
 * @summary Mark a human handoff as resolved
 */
export const resolveChatAssistantSession = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<ChatAssistantSession> => {

  return customFetch<ChatAssistantSession>(getResolveChatAssistantSessionUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getResolveChatAssistantSessionMutationKey = () => ['resolveChatAssistantSession'] as const;

export const getResolveChatAssistantSessionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resolveChatAssistantSession>>, TError,ResolveChatAssistantSessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof resolveChatAssistantSession>>, TError,ResolveChatAssistantSessionMutationVariables, TContext> => {

const mutationKey = getResolveChatAssistantSessionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof resolveChatAssistantSession>>, ResolveChatAssistantSessionMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  resolveChatAssistantSession(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResolveChatAssistantSessionMutationResult = NonNullable<Awaited<ReturnType<typeof resolveChatAssistantSession>>>

    export type ResolveChatAssistantSessionMutationError = Error
    export type ResolveChatAssistantSessionMutationVariables = {id: string}

    /**
 * @summary Mark a human handoff as resolved
 */
export const useResolveChatAssistantSession = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resolveChatAssistantSession>>, TError,ResolveChatAssistantSessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof resolveChatAssistantSession>>,
        TError,
        ResolveChatAssistantSessionMutationVariables,
        TContext
      > => {
      return useMutation(getResolveChatAssistantSessionMutationOptions(options));
    }

export const getGetPublicEventUrl = (slug: string,
    params?: GetPublicEventParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/public/events/${slug}?${stringifiedParams}` : `/api/public/events/${slug}`
}

/**
 * Public endpoint backing the standalone event microsites. Returns the published event with its agenda, speakers, and venue details. Unknown, draft, or archived slugs return 404.
 * @summary Get a published event for its public microsite (no authentication)
 */
export const getPublicEvent = async (slug: string,
    params?: GetPublicEventParams, options?: Parameters<typeof customFetch>[1]): Promise<PublicEvent> => {

  return customFetch<PublicEvent>(getGetPublicEventUrl(slug,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPublicEventQueryKey = (slug: string,
    params?: GetPublicEventParams,) => {
    return [
    `/api/public/events/${slug}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetPublicEventQueryOptions = <TData = Awaited<ReturnType<typeof getPublicEvent>>, TError = Error>(slug: string,
    params?: GetPublicEventParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicEvent>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPublicEventQueryKey(slug,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPublicEvent>>> = ({ signal }) => getPublicEvent(slug,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: slug !== null && slug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPublicEvent>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPublicEventQueryResult = NonNullable<Awaited<ReturnType<typeof getPublicEvent>>>
export type GetPublicEventQueryError = Error


/**
 * @summary Get a published event for its public microsite (no authentication)
 */

export function useGetPublicEvent<TData = Awaited<ReturnType<typeof getPublicEvent>>, TError = Error>(
 slug: string,
    params?: GetPublicEventParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicEvent>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPublicEventQueryOptions(slug,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getValidateEventInvitationCodeUrl = (slug: string,) => {




  return `/api/public/events/${slug}/validate-code`
}

/**
 * Checks an invitation code for a published event without consuming a use. Invalid, inactive, expired, and exhausted codes all collapse to the same safe 404 so codes cannot be probed. Protected by a honeypot field and per-client rate limiting.
 * @summary Validate an event invitation code (public, no authentication)
 */
export const validateEventInvitationCode = async (slug: string,
    publicInvitationCodeCheck: PublicInvitationCodeCheck, options?: Parameters<typeof customFetch>[1]): Promise<PublicInvitationCodeResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PublicInvitationCodeResult>(getValidateEventInvitationCodeUrl(slug),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(publicInvitationCodeCheck)
  }
);}





export const getValidateEventInvitationCodeMutationKey = () => ['validateEventInvitationCode'] as const;

export const getValidateEventInvitationCodeMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof validateEventInvitationCode>>, TError,ValidateEventInvitationCodeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof validateEventInvitationCode>>, TError,ValidateEventInvitationCodeMutationVariables, TContext> => {

const mutationKey = getValidateEventInvitationCodeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof validateEventInvitationCode>>, ValidateEventInvitationCodeMutationVariables> = (props) => {
          const {slug,data} = props ?? {};

          return  validateEventInvitationCode(slug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ValidateEventInvitationCodeMutationResult = NonNullable<Awaited<ReturnType<typeof validateEventInvitationCode>>>
    export type ValidateEventInvitationCodeMutationBody = PublicInvitationCodeCheck
    export type ValidateEventInvitationCodeMutationError = Error
    export type ValidateEventInvitationCodeMutationVariables = {slug: string;data: PublicInvitationCodeCheck}

    /**
 * @summary Validate an event invitation code (public, no authentication)
 */
export const useValidateEventInvitationCode = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof validateEventInvitationCode>>, TError,ValidateEventInvitationCodeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof validateEventInvitationCode>>,
        TError,
        ValidateEventInvitationCodeMutationVariables,
        TContext
      > => {
      return useMutation(getValidateEventInvitationCodeMutationOptions(options));
    }

export const getSubmitEventRsvpUrl = (slug: string,) => {




  return `/api/public/events/${slug}/rsvps`
}

/**
 * Validates and consumes one invitation-code use, stores the attendee details, and links the RSVP to a Partner Engagement contact matched by email (a new contact is created when no match exists). One RSVP per email per event. Protected by a honeypot field and per-client rate limiting.
 * @summary Submit an RSVP for a published event (public, no authentication)
 */
export const submitEventRsvp = async (slug: string,
    publicRsvpSubmission: PublicRsvpSubmission, options?: Parameters<typeof customFetch>[1]): Promise<PublicRsvpConfirmation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PublicRsvpConfirmation>(getSubmitEventRsvpUrl(slug),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(publicRsvpSubmission)
  }
);}





export const getSubmitEventRsvpMutationKey = () => ['submitEventRsvp'] as const;

export const getSubmitEventRsvpMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitEventRsvp>>, TError,SubmitEventRsvpMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof submitEventRsvp>>, TError,SubmitEventRsvpMutationVariables, TContext> => {

const mutationKey = getSubmitEventRsvpMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof submitEventRsvp>>, SubmitEventRsvpMutationVariables> = (props) => {
          const {slug,data} = props ?? {};

          return  submitEventRsvp(slug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SubmitEventRsvpMutationResult = NonNullable<Awaited<ReturnType<typeof submitEventRsvp>>>
    export type SubmitEventRsvpMutationBody = PublicRsvpSubmission
    export type SubmitEventRsvpMutationError = Error
    export type SubmitEventRsvpMutationVariables = {slug: string;data: PublicRsvpSubmission}

    /**
 * @summary Submit an RSVP for a published event (public, no authentication)
 */
export const useSubmitEventRsvp = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitEventRsvp>>, TError,SubmitEventRsvpMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof submitEventRsvp>>,
        TError,
        SubmitEventRsvpMutationVariables,
        TContext
      > => {
      return useMutation(getSubmitEventRsvpMutationOptions(options));
    }

export const getListOpportunitiesUrl = (params?: ListOpportunitiesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/opportunities?${stringifiedParams}` : `/api/opportunities`
}

/**
 * @summary List opportunities
 */
export const listOpportunities = async (params?: ListOpportunitiesParams, options?: Parameters<typeof customFetch>[1]): Promise<OpportunityList> => {

  return customFetch<OpportunityList>(getListOpportunitiesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListOpportunitiesQueryKey = (params?: ListOpportunitiesParams,) => {
    return [
    `/api/opportunities`, ...(params ? [params] : [])
    ] as const;
    }


export const getListOpportunitiesQueryOptions = <TData = Awaited<ReturnType<typeof listOpportunities>>, TError = unknown>(params?: ListOpportunitiesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOpportunities>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListOpportunitiesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listOpportunities>>> = ({ signal }) => listOpportunities(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listOpportunities>>, TError, TData> & { queryKey: QueryKey }
}

export type ListOpportunitiesQueryResult = NonNullable<Awaited<ReturnType<typeof listOpportunities>>>
export type ListOpportunitiesQueryError = unknown


/**
 * @summary List opportunities
 */

export function useListOpportunities<TData = Awaited<ReturnType<typeof listOpportunities>>, TError = unknown>(
 params?: ListOpportunitiesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOpportunities>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListOpportunitiesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateOpportunityUrl = () => {




  return `/api/opportunities`
}

/**
 * @summary Create an opportunity
 */
export const createOpportunity = async (opportunityCreate: OpportunityCreate, options?: Parameters<typeof customFetch>[1]): Promise<Opportunity> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Opportunity>(getCreateOpportunityUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(opportunityCreate)
  }
);}





export const getCreateOpportunityMutationKey = () => ['createOpportunity'] as const;

export const getCreateOpportunityMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createOpportunity>>, TError,CreateOpportunityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createOpportunity>>, TError,CreateOpportunityMutationVariables, TContext> => {

const mutationKey = getCreateOpportunityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createOpportunity>>, CreateOpportunityMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createOpportunity(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateOpportunityMutationResult = NonNullable<Awaited<ReturnType<typeof createOpportunity>>>
    export type CreateOpportunityMutationBody = OpportunityCreate
    export type CreateOpportunityMutationError = Error
    export type CreateOpportunityMutationVariables = {data: OpportunityCreate}

    /**
 * @summary Create an opportunity
 */
export const useCreateOpportunity = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createOpportunity>>, TError,CreateOpportunityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createOpportunity>>,
        TError,
        CreateOpportunityMutationVariables,
        TContext
      > => {
      return useMutation(getCreateOpportunityMutationOptions(options));
    }

export const getGetOpportunityUrl = (id: string,) => {




  return `/api/opportunities/${id}`
}

/**
 * @summary Get one opportunity
 */
export const getOpportunity = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Opportunity> => {

  return customFetch<Opportunity>(getGetOpportunityUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetOpportunityQueryKey = (id: string,) => {
    return [
    `/api/opportunities/${id}`
    ] as const;
    }


export const getGetOpportunityQueryOptions = <TData = Awaited<ReturnType<typeof getOpportunity>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getOpportunity>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetOpportunityQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getOpportunity>>> = ({ signal }) => getOpportunity(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getOpportunity>>, TError, TData> & { queryKey: QueryKey }
}

export type GetOpportunityQueryResult = NonNullable<Awaited<ReturnType<typeof getOpportunity>>>
export type GetOpportunityQueryError = Error


/**
 * @summary Get one opportunity
 */

export function useGetOpportunity<TData = Awaited<ReturnType<typeof getOpportunity>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getOpportunity>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetOpportunityQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateOpportunityUrl = (id: string,) => {




  return `/api/opportunities/${id}`
}

/**
 * @summary Update an opportunity
 */
export const updateOpportunity = async (id: string,
    opportunityUpdate: OpportunityUpdate, options?: Parameters<typeof customFetch>[1]): Promise<Opportunity> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Opportunity>(getUpdateOpportunityUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(opportunityUpdate)
  }
);}





export const getUpdateOpportunityMutationKey = () => ['updateOpportunity'] as const;

export const getUpdateOpportunityMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateOpportunity>>, TError,UpdateOpportunityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateOpportunity>>, TError,UpdateOpportunityMutationVariables, TContext> => {

const mutationKey = getUpdateOpportunityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateOpportunity>>, UpdateOpportunityMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateOpportunity(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateOpportunityMutationResult = NonNullable<Awaited<ReturnType<typeof updateOpportunity>>>
    export type UpdateOpportunityMutationBody = OpportunityUpdate
    export type UpdateOpportunityMutationError = Error
    export type UpdateOpportunityMutationVariables = {id: string;data: OpportunityUpdate}

    /**
 * @summary Update an opportunity
 */
export const useUpdateOpportunity = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateOpportunity>>, TError,UpdateOpportunityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateOpportunity>>,
        TError,
        UpdateOpportunityMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateOpportunityMutationOptions(options));
    }

export const getDeleteOpportunityUrl = (id: string,) => {




  return `/api/opportunities/${id}`
}

/**
 * @summary Delete an opportunity
 */
export const deleteOpportunity = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteOpportunityUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteOpportunityMutationKey = () => ['deleteOpportunity'] as const;

export const getDeleteOpportunityMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteOpportunity>>, TError,DeleteOpportunityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteOpportunity>>, TError,DeleteOpportunityMutationVariables, TContext> => {

const mutationKey = getDeleteOpportunityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteOpportunity>>, DeleteOpportunityMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteOpportunity(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteOpportunityMutationResult = NonNullable<Awaited<ReturnType<typeof deleteOpportunity>>>

    export type DeleteOpportunityMutationError = Error
    export type DeleteOpportunityMutationVariables = {id: string}

    /**
 * @summary Delete an opportunity
 */
export const useDeleteOpportunity = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteOpportunity>>, TError,DeleteOpportunityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteOpportunity>>,
        TError,
        DeleteOpportunityMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteOpportunityMutationOptions(options));
    }

export const getListOpportunityNotesUrl = (params?: ListOpportunityNotesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/opportunity-notes?${stringifiedParams}` : `/api/opportunity-notes`
}

export const listOpportunityNotes = async (params?: ListOpportunityNotesParams, options?: Parameters<typeof customFetch>[1]): Promise<OpportunityNoteList> => {

  return customFetch<OpportunityNoteList>(getListOpportunityNotesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListOpportunityNotesQueryKey = (params?: ListOpportunityNotesParams,) => {
    return [
    `/api/opportunity-notes`, ...(params ? [params] : [])
    ] as const;
    }


export const getListOpportunityNotesQueryOptions = <TData = Awaited<ReturnType<typeof listOpportunityNotes>>, TError = unknown>(params?: ListOpportunityNotesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOpportunityNotes>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListOpportunityNotesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listOpportunityNotes>>> = ({ signal }) => listOpportunityNotes(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listOpportunityNotes>>, TError, TData> & { queryKey: QueryKey }
}

export type ListOpportunityNotesQueryResult = NonNullable<Awaited<ReturnType<typeof listOpportunityNotes>>>
export type ListOpportunityNotesQueryError = unknown



export function useListOpportunityNotes<TData = Awaited<ReturnType<typeof listOpportunityNotes>>, TError = unknown>(
 params?: ListOpportunityNotesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOpportunityNotes>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListOpportunityNotesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListOpportunityNoteChoicesUrl = (params: ListOpportunityNoteChoicesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/opportunity-note-choices?${stringifiedParams}` : `/api/opportunity-note-choices`
}

export const listOpportunityNoteChoices = async (params: ListOpportunityNoteChoicesParams, options?: Parameters<typeof customFetch>[1]): Promise<ListOpportunityNoteChoices200> => {

  return customFetch<ListOpportunityNoteChoices200>(getListOpportunityNoteChoicesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListOpportunityNoteChoicesQueryKey = (params?: ListOpportunityNoteChoicesParams,) => {
    return [
    `/api/opportunity-note-choices`, ...(params ? [params] : [])
    ] as const;
    }


export const getListOpportunityNoteChoicesQueryOptions = <TData = Awaited<ReturnType<typeof listOpportunityNoteChoices>>, TError = unknown>(params: ListOpportunityNoteChoicesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOpportunityNoteChoices>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListOpportunityNoteChoicesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listOpportunityNoteChoices>>> = ({ signal }) => listOpportunityNoteChoices(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listOpportunityNoteChoices>>, TError, TData> & { queryKey: QueryKey }
}

export type ListOpportunityNoteChoicesQueryResult = NonNullable<Awaited<ReturnType<typeof listOpportunityNoteChoices>>>
export type ListOpportunityNoteChoicesQueryError = unknown



export function useListOpportunityNoteChoices<TData = Awaited<ReturnType<typeof listOpportunityNoteChoices>>, TError = unknown>(
 params: ListOpportunityNoteChoicesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOpportunityNoteChoices>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListOpportunityNoteChoicesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetOpportunityNoteUrl = (id: string,) => {




  return `/api/opportunity-notes/${id}`
}

export const getOpportunityNote = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<OpportunityNote> => {

  return customFetch<OpportunityNote>(getGetOpportunityNoteUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetOpportunityNoteQueryKey = (id: string,) => {
    return [
    `/api/opportunity-notes/${id}`
    ] as const;
    }


export const getGetOpportunityNoteQueryOptions = <TData = Awaited<ReturnType<typeof getOpportunityNote>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getOpportunityNote>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetOpportunityNoteQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getOpportunityNote>>> = ({ signal }) => getOpportunityNote(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getOpportunityNote>>, TError, TData> & { queryKey: QueryKey }
}

export type GetOpportunityNoteQueryResult = NonNullable<Awaited<ReturnType<typeof getOpportunityNote>>>
export type GetOpportunityNoteQueryError = unknown



export function useGetOpportunityNote<TData = Awaited<ReturnType<typeof getOpportunityNote>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getOpportunityNote>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetOpportunityNoteQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListOpportunityNoteHistoryUrl = (id: string,
    params?: ListOpportunityNoteHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/opportunity-notes/${id}/history?${stringifiedParams}` : `/api/opportunity-notes/${id}/history`
}

export const listOpportunityNoteHistory = async (id: string,
    params?: ListOpportunityNoteHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<ListOpportunityNoteHistory200> => {

  return customFetch<ListOpportunityNoteHistory200>(getListOpportunityNoteHistoryUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListOpportunityNoteHistoryQueryKey = (id: string,
    params?: ListOpportunityNoteHistoryParams,) => {
    return [
    `/api/opportunity-notes/${id}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListOpportunityNoteHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listOpportunityNoteHistory>>, TError = unknown>(id: string,
    params?: ListOpportunityNoteHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOpportunityNoteHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListOpportunityNoteHistoryQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listOpportunityNoteHistory>>> = ({ signal }) => listOpportunityNoteHistory(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listOpportunityNoteHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListOpportunityNoteHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listOpportunityNoteHistory>>>
export type ListOpportunityNoteHistoryQueryError = unknown



export function useListOpportunityNoteHistory<TData = Awaited<ReturnType<typeof listOpportunityNoteHistory>>, TError = unknown>(
 id: string,
    params?: ListOpportunityNoteHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOpportunityNoteHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListOpportunityNoteHistoryQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewOpportunityNoteUrl = () => {




  return `/api/opportunity-note-reviews`
}

export const reviewOpportunityNote = async (reviewOpportunityNoteBody: ReviewOpportunityNoteBody, options?: Parameters<typeof customFetch>[1]): Promise<OpportunityNoteReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OpportunityNoteReview>(getReviewOpportunityNoteUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewOpportunityNoteBody)
  }
);}





export const getReviewOpportunityNoteMutationKey = () => ['reviewOpportunityNote'] as const;

export const getReviewOpportunityNoteMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewOpportunityNote>>, TError,ReviewOpportunityNoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewOpportunityNote>>, TError,ReviewOpportunityNoteMutationVariables, TContext> => {

const mutationKey = getReviewOpportunityNoteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewOpportunityNote>>, ReviewOpportunityNoteMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reviewOpportunityNote(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewOpportunityNoteMutationResult = NonNullable<Awaited<ReturnType<typeof reviewOpportunityNote>>>
    export type ReviewOpportunityNoteMutationBody = ReviewOpportunityNoteBody
    export type ReviewOpportunityNoteMutationError = unknown
    export type ReviewOpportunityNoteMutationVariables = {data: ReviewOpportunityNoteBody}

    export const useReviewOpportunityNote = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewOpportunityNote>>, TError,ReviewOpportunityNoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewOpportunityNote>>,
        TError,
        ReviewOpportunityNoteMutationVariables,
        TContext
      > => {
      return useMutation(getReviewOpportunityNoteMutationOptions(options));
    }

export const getGetOpportunityNoteReviewUrl = (id: string,) => {




  return `/api/opportunity-note-reviews/${id}`
}

export const getOpportunityNoteReview = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<OpportunityNoteReview> => {

  return customFetch<OpportunityNoteReview>(getGetOpportunityNoteReviewUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetOpportunityNoteReviewQueryKey = (id: string,) => {
    return [
    `/api/opportunity-note-reviews/${id}`
    ] as const;
    }


export const getGetOpportunityNoteReviewQueryOptions = <TData = Awaited<ReturnType<typeof getOpportunityNoteReview>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getOpportunityNoteReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetOpportunityNoteReviewQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getOpportunityNoteReview>>> = ({ signal }) => getOpportunityNoteReview(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getOpportunityNoteReview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetOpportunityNoteReviewQueryResult = NonNullable<Awaited<ReturnType<typeof getOpportunityNoteReview>>>
export type GetOpportunityNoteReviewQueryError = unknown



export function useGetOpportunityNoteReview<TData = Awaited<ReturnType<typeof getOpportunityNoteReview>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getOpportunityNoteReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetOpportunityNoteReviewQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConfirmOpportunityNoteUrl = (id: string,) => {




  return `/api/opportunity-note-reviews/${id}/confirm`
}

export const confirmOpportunityNote = async (id: string,
    confirmOpportunityNoteBody: ConfirmOpportunityNoteBody, options?: Parameters<typeof customFetch>[1]): Promise<OpportunityNoteReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OpportunityNoteReview>(getConfirmOpportunityNoteUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(confirmOpportunityNoteBody)
  }
);}





export const getConfirmOpportunityNoteMutationKey = () => ['confirmOpportunityNote'] as const;

export const getConfirmOpportunityNoteMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmOpportunityNote>>, TError,ConfirmOpportunityNoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmOpportunityNote>>, TError,ConfirmOpportunityNoteMutationVariables, TContext> => {

const mutationKey = getConfirmOpportunityNoteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmOpportunityNote>>, ConfirmOpportunityNoteMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  confirmOpportunityNote(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmOpportunityNoteMutationResult = NonNullable<Awaited<ReturnType<typeof confirmOpportunityNote>>>
    export type ConfirmOpportunityNoteMutationBody = ConfirmOpportunityNoteBody
    export type ConfirmOpportunityNoteMutationError = unknown
    export type ConfirmOpportunityNoteMutationVariables = {id: string;data: ConfirmOpportunityNoteBody}

    export const useConfirmOpportunityNote = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmOpportunityNote>>, TError,ConfirmOpportunityNoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmOpportunityNote>>,
        TError,
        ConfirmOpportunityNoteMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmOpportunityNoteMutationOptions(options));
    }

export const getCancelOpportunityNoteReviewUrl = (id: string,) => {




  return `/api/opportunity-note-reviews/${id}/cancel`
}

export const cancelOpportunityNoteReview = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<OpportunityNoteReview> => {

  return customFetch<OpportunityNoteReview>(getCancelOpportunityNoteReviewUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelOpportunityNoteReviewMutationKey = () => ['cancelOpportunityNoteReview'] as const;

export const getCancelOpportunityNoteReviewMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelOpportunityNoteReview>>, TError,CancelOpportunityNoteReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelOpportunityNoteReview>>, TError,CancelOpportunityNoteReviewMutationVariables, TContext> => {

const mutationKey = getCancelOpportunityNoteReviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelOpportunityNoteReview>>, CancelOpportunityNoteReviewMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  cancelOpportunityNoteReview(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelOpportunityNoteReviewMutationResult = NonNullable<Awaited<ReturnType<typeof cancelOpportunityNoteReview>>>

    export type CancelOpportunityNoteReviewMutationError = unknown
    export type CancelOpportunityNoteReviewMutationVariables = {id: string}

    export const useCancelOpportunityNoteReview = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelOpportunityNoteReview>>, TError,CancelOpportunityNoteReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelOpportunityNoteReview>>,
        TError,
        CancelOpportunityNoteReviewMutationVariables,
        TContext
      > => {
      return useMutation(getCancelOpportunityNoteReviewMutationOptions(options));
    }

export const getListStaffActivitiesUrl = (params?: ListStaffActivitiesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/staff-activities?${stringifiedParams}` : `/api/staff-activities`
}

/**
 * @summary Complete scoped staff interaction and task worklist
 */
export const listStaffActivities = async (params?: ListStaffActivitiesParams, options?: Parameters<typeof customFetch>[1]): Promise<ActivityList> => {

  return customFetch<ActivityList>(getListStaffActivitiesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListStaffActivitiesQueryKey = (params?: ListStaffActivitiesParams,) => {
    return [
    `/api/staff-activities`, ...(params ? [params] : [])
    ] as const;
    }


export const getListStaffActivitiesQueryOptions = <TData = Awaited<ReturnType<typeof listStaffActivities>>, TError = unknown>(params?: ListStaffActivitiesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listStaffActivities>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListStaffActivitiesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listStaffActivities>>> = ({ signal }) => listStaffActivities(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listStaffActivities>>, TError, TData> & { queryKey: QueryKey }
}

export type ListStaffActivitiesQueryResult = NonNullable<Awaited<ReturnType<typeof listStaffActivities>>>
export type ListStaffActivitiesQueryError = unknown


/**
 * @summary Complete scoped staff interaction and task worklist
 */

export function useListStaffActivities<TData = Awaited<ReturnType<typeof listStaffActivities>>, TError = unknown>(
 params?: ListStaffActivitiesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listStaffActivities>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListStaffActivitiesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetStaffActivityUrl = (id: string,) => {




  return `/api/staff-activities/${id}`
}

export const getStaffActivity = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Activity> => {

  return customFetch<Activity>(getGetStaffActivityUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetStaffActivityQueryKey = (id: string,) => {
    return [
    `/api/staff-activities/${id}`
    ] as const;
    }


export const getGetStaffActivityQueryOptions = <TData = Awaited<ReturnType<typeof getStaffActivity>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getStaffActivity>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetStaffActivityQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getStaffActivity>>> = ({ signal }) => getStaffActivity(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getStaffActivity>>, TError, TData> & { queryKey: QueryKey }
}

export type GetStaffActivityQueryResult = NonNullable<Awaited<ReturnType<typeof getStaffActivity>>>
export type GetStaffActivityQueryError = unknown



export function useGetStaffActivity<TData = Awaited<ReturnType<typeof getStaffActivity>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getStaffActivity>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetStaffActivityQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListStaffActivityHistoryUrl = (id: string,
    params?: ListStaffActivityHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/staff-activities/${id}/history?${stringifiedParams}` : `/api/staff-activities/${id}/history`
}

export const listStaffActivityHistory = async (id: string,
    params?: ListStaffActivityHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<StaffActivityHistoryList> => {

  return customFetch<StaffActivityHistoryList>(getListStaffActivityHistoryUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListStaffActivityHistoryQueryKey = (id: string,
    params?: ListStaffActivityHistoryParams,) => {
    return [
    `/api/staff-activities/${id}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListStaffActivityHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listStaffActivityHistory>>, TError = unknown>(id: string,
    params?: ListStaffActivityHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listStaffActivityHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListStaffActivityHistoryQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listStaffActivityHistory>>> = ({ signal }) => listStaffActivityHistory(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listStaffActivityHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListStaffActivityHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listStaffActivityHistory>>>
export type ListStaffActivityHistoryQueryError = unknown



export function useListStaffActivityHistory<TData = Awaited<ReturnType<typeof listStaffActivityHistory>>, TError = unknown>(
 id: string,
    params?: ListStaffActivityHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listStaffActivityHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListStaffActivityHistoryQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewStaffActivityUrl = () => {




  return `/api/staff-activity-reviews`
}

export const reviewStaffActivity = async (reviewStaffActivityBody: ReviewStaffActivityBody, options?: Parameters<typeof customFetch>[1]): Promise<StaffActivityReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<StaffActivityReview>(getReviewStaffActivityUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewStaffActivityBody)
  }
);}





export const getReviewStaffActivityMutationKey = () => ['reviewStaffActivity'] as const;

export const getReviewStaffActivityMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewStaffActivity>>, TError,ReviewStaffActivityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewStaffActivity>>, TError,ReviewStaffActivityMutationVariables, TContext> => {

const mutationKey = getReviewStaffActivityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewStaffActivity>>, ReviewStaffActivityMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reviewStaffActivity(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewStaffActivityMutationResult = NonNullable<Awaited<ReturnType<typeof reviewStaffActivity>>>
    export type ReviewStaffActivityMutationBody = ReviewStaffActivityBody
    export type ReviewStaffActivityMutationError = unknown
    export type ReviewStaffActivityMutationVariables = {data: ReviewStaffActivityBody}

    export const useReviewStaffActivity = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewStaffActivity>>, TError,ReviewStaffActivityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewStaffActivity>>,
        TError,
        ReviewStaffActivityMutationVariables,
        TContext
      > => {
      return useMutation(getReviewStaffActivityMutationOptions(options));
    }

export const getGetStaffActivityReviewUrl = (id: string,) => {




  return `/api/staff-activity-reviews/${id}`
}

export const getStaffActivityReview = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<StaffActivityReview> => {

  return customFetch<StaffActivityReview>(getGetStaffActivityReviewUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetStaffActivityReviewQueryKey = (id: string,) => {
    return [
    `/api/staff-activity-reviews/${id}`
    ] as const;
    }


export const getGetStaffActivityReviewQueryOptions = <TData = Awaited<ReturnType<typeof getStaffActivityReview>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getStaffActivityReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetStaffActivityReviewQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getStaffActivityReview>>> = ({ signal }) => getStaffActivityReview(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getStaffActivityReview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetStaffActivityReviewQueryResult = NonNullable<Awaited<ReturnType<typeof getStaffActivityReview>>>
export type GetStaffActivityReviewQueryError = unknown



export function useGetStaffActivityReview<TData = Awaited<ReturnType<typeof getStaffActivityReview>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getStaffActivityReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetStaffActivityReviewQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConfirmStaffActivityUrl = (id: string,) => {




  return `/api/staff-activity-reviews/${id}/confirm`
}

export const confirmStaffActivity = async (id: string,
    confirmStaffActivityBody: ConfirmStaffActivityBody, options?: Parameters<typeof customFetch>[1]): Promise<StaffActivityReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<StaffActivityReview>(getConfirmStaffActivityUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(confirmStaffActivityBody)
  }
);}





export const getConfirmStaffActivityMutationKey = () => ['confirmStaffActivity'] as const;

export const getConfirmStaffActivityMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmStaffActivity>>, TError,ConfirmStaffActivityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmStaffActivity>>, TError,ConfirmStaffActivityMutationVariables, TContext> => {

const mutationKey = getConfirmStaffActivityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmStaffActivity>>, ConfirmStaffActivityMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  confirmStaffActivity(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmStaffActivityMutationResult = NonNullable<Awaited<ReturnType<typeof confirmStaffActivity>>>
    export type ConfirmStaffActivityMutationBody = ConfirmStaffActivityBody
    export type ConfirmStaffActivityMutationError = unknown
    export type ConfirmStaffActivityMutationVariables = {id: string;data: ConfirmStaffActivityBody}

    export const useConfirmStaffActivity = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmStaffActivity>>, TError,ConfirmStaffActivityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmStaffActivity>>,
        TError,
        ConfirmStaffActivityMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmStaffActivityMutationOptions(options));
    }

export const getCancelStaffActivityReviewUrl = (id: string,) => {




  return `/api/staff-activity-reviews/${id}/cancel`
}

export const cancelStaffActivityReview = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<StaffActivityReview> => {

  return customFetch<StaffActivityReview>(getCancelStaffActivityReviewUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelStaffActivityReviewMutationKey = () => ['cancelStaffActivityReview'] as const;

export const getCancelStaffActivityReviewMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelStaffActivityReview>>, TError,CancelStaffActivityReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelStaffActivityReview>>, TError,CancelStaffActivityReviewMutationVariables, TContext> => {

const mutationKey = getCancelStaffActivityReviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelStaffActivityReview>>, CancelStaffActivityReviewMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  cancelStaffActivityReview(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelStaffActivityReviewMutationResult = NonNullable<Awaited<ReturnType<typeof cancelStaffActivityReview>>>

    export type CancelStaffActivityReviewMutationError = unknown
    export type CancelStaffActivityReviewMutationVariables = {id: string}

    export const useCancelStaffActivityReview = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelStaffActivityReview>>, TError,CancelStaffActivityReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelStaffActivityReview>>,
        TError,
        CancelStaffActivityReviewMutationVariables,
        TContext
      > => {
      return useMutation(getCancelStaffActivityReviewMutationOptions(options));
    }

export const getListActivitiesUrl = (params?: ListActivitiesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/activities?${stringifiedParams}` : `/api/activities`
}

/**
 * @summary List activities (optionally scoped to one record)
 */
export const listActivities = async (params?: ListActivitiesParams, options?: Parameters<typeof customFetch>[1]): Promise<ActivityList> => {

  return customFetch<ActivityList>(getListActivitiesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListActivitiesQueryKey = (params?: ListActivitiesParams,) => {
    return [
    `/api/activities`, ...(params ? [params] : [])
    ] as const;
    }


export const getListActivitiesQueryOptions = <TData = Awaited<ReturnType<typeof listActivities>>, TError = unknown>(params?: ListActivitiesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listActivities>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListActivitiesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listActivities>>> = ({ signal }) => listActivities(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listActivities>>, TError, TData> & { queryKey: QueryKey }
}

export type ListActivitiesQueryResult = NonNullable<Awaited<ReturnType<typeof listActivities>>>
export type ListActivitiesQueryError = unknown


/**
 * @summary List activities (optionally scoped to one record)
 */

export function useListActivities<TData = Awaited<ReturnType<typeof listActivities>>, TError = unknown>(
 params?: ListActivitiesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listActivities>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListActivitiesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateActivityUrl = () => {




  return `/api/activities`
}

/**
 * @summary Log an activity against a record
 */
export const createActivity = async (activityCreate: ActivityCreate, options?: Parameters<typeof customFetch>[1]): Promise<Activity> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Activity>(getCreateActivityUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(activityCreate)
  }
);}





export const getCreateActivityMutationKey = () => ['createActivity'] as const;

export const getCreateActivityMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createActivity>>, TError,CreateActivityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createActivity>>, TError,CreateActivityMutationVariables, TContext> => {

const mutationKey = getCreateActivityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createActivity>>, CreateActivityMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createActivity(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateActivityMutationResult = NonNullable<Awaited<ReturnType<typeof createActivity>>>
    export type CreateActivityMutationBody = ActivityCreate
    export type CreateActivityMutationError = Error
    export type CreateActivityMutationVariables = {data: ActivityCreate}

    /**
 * @summary Log an activity against a record
 */
export const useCreateActivity = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createActivity>>, TError,CreateActivityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createActivity>>,
        TError,
        CreateActivityMutationVariables,
        TContext
      > => {
      return useMutation(getCreateActivityMutationOptions(options));
    }

export const getGetActivityUrl = (id: string,) => {




  return `/api/activities/${id}`
}

/**
 * @summary Get one activity
 */
export const getActivity = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Activity> => {

  return customFetch<Activity>(getGetActivityUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetActivityQueryKey = (id: string,) => {
    return [
    `/api/activities/${id}`
    ] as const;
    }


export const getGetActivityQueryOptions = <TData = Awaited<ReturnType<typeof getActivity>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getActivity>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetActivityQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getActivity>>> = ({ signal }) => getActivity(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getActivity>>, TError, TData> & { queryKey: QueryKey }
}

export type GetActivityQueryResult = NonNullable<Awaited<ReturnType<typeof getActivity>>>
export type GetActivityQueryError = Error


/**
 * @summary Get one activity
 */

export function useGetActivity<TData = Awaited<ReturnType<typeof getActivity>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getActivity>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetActivityQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateActivityUrl = (id: string,) => {




  return `/api/activities/${id}`
}

/**
 * @summary Update an activity
 */
export const updateActivity = async (id: string,
    activityUpdate: ActivityUpdate, options?: Parameters<typeof customFetch>[1]): Promise<Activity> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Activity>(getUpdateActivityUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(activityUpdate)
  }
);}





export const getUpdateActivityMutationKey = () => ['updateActivity'] as const;

export const getUpdateActivityMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateActivity>>, TError,UpdateActivityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateActivity>>, TError,UpdateActivityMutationVariables, TContext> => {

const mutationKey = getUpdateActivityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateActivity>>, UpdateActivityMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateActivity(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateActivityMutationResult = NonNullable<Awaited<ReturnType<typeof updateActivity>>>
    export type UpdateActivityMutationBody = ActivityUpdate
    export type UpdateActivityMutationError = Error
    export type UpdateActivityMutationVariables = {id: string;data: ActivityUpdate}

    /**
 * @summary Update an activity
 */
export const useUpdateActivity = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateActivity>>, TError,UpdateActivityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateActivity>>,
        TError,
        UpdateActivityMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateActivityMutationOptions(options));
    }

export const getDeleteActivityUrl = (id: string,) => {




  return `/api/activities/${id}`
}

/**
 * @summary Delete an activity
 */
export const deleteActivity = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteActivityUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteActivityMutationKey = () => ['deleteActivity'] as const;

export const getDeleteActivityMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteActivity>>, TError,DeleteActivityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteActivity>>, TError,DeleteActivityMutationVariables, TContext> => {

const mutationKey = getDeleteActivityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteActivity>>, DeleteActivityMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteActivity(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteActivityMutationResult = NonNullable<Awaited<ReturnType<typeof deleteActivity>>>

    export type DeleteActivityMutationError = Error
    export type DeleteActivityMutationVariables = {id: string}

    /**
 * @summary Delete an activity
 */
export const useDeleteActivity = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteActivity>>, TError,DeleteActivityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteActivity>>,
        TError,
        DeleteActivityMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteActivityMutationOptions(options));
    }

export const getGetTestDataReportUrl = () => {




  return `/api/test-data-report`
}

/**
 * @summary List records currently flagged as test data for review
 */
export const getTestDataReport = async ( options?: Parameters<typeof customFetch>[1]): Promise<TestDataReport> => {

  return customFetch<TestDataReport>(getGetTestDataReportUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTestDataReportQueryKey = () => {
    return [
    `/api/test-data-report`
    ] as const;
    }


export const getGetTestDataReportQueryOptions = <TData = Awaited<ReturnType<typeof getTestDataReport>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTestDataReport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTestDataReportQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTestDataReport>>> = ({ signal }) => getTestDataReport({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTestDataReport>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTestDataReportQueryResult = NonNullable<Awaited<ReturnType<typeof getTestDataReport>>>
export type GetTestDataReportQueryError = unknown


/**
 * @summary List records currently flagged as test data for review
 */

export function useGetTestDataReport<TData = Awaited<ReturnType<typeof getTestDataReport>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTestDataReport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTestDataReportQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListPartnersUrl = (params?: ListPartnersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/partners?${stringifiedParams}` : `/api/partners`
}

/**
 * @summary List partners
 */
export const listPartners = async (params?: ListPartnersParams, options?: Parameters<typeof customFetch>[1]): Promise<PartnerList> => {

  return customFetch<PartnerList>(getListPartnersUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPartnersQueryKey = (params?: ListPartnersParams,) => {
    return [
    `/api/partners`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPartnersQueryOptions = <TData = Awaited<ReturnType<typeof listPartners>>, TError = unknown>(params?: ListPartnersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPartners>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPartnersQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPartners>>> = ({ signal }) => listPartners(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPartners>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPartnersQueryResult = NonNullable<Awaited<ReturnType<typeof listPartners>>>
export type ListPartnersQueryError = unknown


/**
 * @summary List partners
 */

export function useListPartners<TData = Awaited<ReturnType<typeof listPartners>>, TError = unknown>(
 params?: ListPartnersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPartners>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPartnersQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreatePartnerUrl = () => {




  return `/api/partners`
}

/**
 * @summary Create a partner
 */
export const createPartner = async (partnerCreate: PartnerCreate, options?: Parameters<typeof customFetch>[1]): Promise<Partner> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Partner>(getCreatePartnerUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(partnerCreate)
  }
);}





export const getCreatePartnerMutationKey = () => ['createPartner'] as const;

export const getCreatePartnerMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPartner>>, TError,CreatePartnerMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createPartner>>, TError,CreatePartnerMutationVariables, TContext> => {

const mutationKey = getCreatePartnerMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createPartner>>, CreatePartnerMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createPartner(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreatePartnerMutationResult = NonNullable<Awaited<ReturnType<typeof createPartner>>>
    export type CreatePartnerMutationBody = PartnerCreate
    export type CreatePartnerMutationError = Error
    export type CreatePartnerMutationVariables = {data: PartnerCreate}

    /**
 * @summary Create a partner
 */
export const useCreatePartner = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPartner>>, TError,CreatePartnerMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createPartner>>,
        TError,
        CreatePartnerMutationVariables,
        TContext
      > => {
      return useMutation(getCreatePartnerMutationOptions(options));
    }

export const getGetPartnerUrl = (id: string,) => {




  return `/api/partners/${id}`
}

/**
 * @summary Get one partner
 */
export const getPartner = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Partner> => {

  return customFetch<Partner>(getGetPartnerUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPartnerQueryKey = (id: string,) => {
    return [
    `/api/partners/${id}`
    ] as const;
    }


export const getGetPartnerQueryOptions = <TData = Awaited<ReturnType<typeof getPartner>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPartner>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPartnerQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPartner>>> = ({ signal }) => getPartner(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPartner>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPartnerQueryResult = NonNullable<Awaited<ReturnType<typeof getPartner>>>
export type GetPartnerQueryError = Error


/**
 * @summary Get one partner
 */

export function useGetPartner<TData = Awaited<ReturnType<typeof getPartner>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPartner>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPartnerQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdatePartnerUrl = (id: string,) => {




  return `/api/partners/${id}`
}

/**
 * @summary Update a partner
 */
export const updatePartner = async (id: string,
    partnerUpdate: PartnerUpdate, options?: Parameters<typeof customFetch>[1]): Promise<Partner> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Partner>(getUpdatePartnerUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(partnerUpdate)
  }
);}





export const getUpdatePartnerMutationKey = () => ['updatePartner'] as const;

export const getUpdatePartnerMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePartner>>, TError,UpdatePartnerMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updatePartner>>, TError,UpdatePartnerMutationVariables, TContext> => {

const mutationKey = getUpdatePartnerMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updatePartner>>, UpdatePartnerMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updatePartner(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdatePartnerMutationResult = NonNullable<Awaited<ReturnType<typeof updatePartner>>>
    export type UpdatePartnerMutationBody = PartnerUpdate
    export type UpdatePartnerMutationError = Error
    export type UpdatePartnerMutationVariables = {id: string;data: PartnerUpdate}

    /**
 * @summary Update a partner
 */
export const useUpdatePartner = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePartner>>, TError,UpdatePartnerMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updatePartner>>,
        TError,
        UpdatePartnerMutationVariables,
        TContext
      > => {
      return useMutation(getUpdatePartnerMutationOptions(options));
    }

export const getDeletePartnerUrl = (id: string,) => {




  return `/api/partners/${id}`
}

/**
 * @summary Remove the partner classification from an account
 */
export const deletePartner = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeletePartnerUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeletePartnerMutationKey = () => ['deletePartner'] as const;

export const getDeletePartnerMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deletePartner>>, TError,DeletePartnerMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deletePartner>>, TError,DeletePartnerMutationVariables, TContext> => {

const mutationKey = getDeletePartnerMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deletePartner>>, DeletePartnerMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deletePartner(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeletePartnerMutationResult = NonNullable<Awaited<ReturnType<typeof deletePartner>>>

    export type DeletePartnerMutationError = Error
    export type DeletePartnerMutationVariables = {id: string}

    /**
 * @summary Remove the partner classification from an account
 */
export const useDeletePartner = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deletePartner>>, TError,DeletePartnerMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deletePartner>>,
        TError,
        DeletePartnerMutationVariables,
        TContext
      > => {
      return useMutation(getDeletePartnerMutationOptions(options));
    }

export const getListPartnerEmailsUrl = (params: ListPartnerEmailsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/partner-emails?${stringifiedParams}` : `/api/partner-emails`
}

/**
 * @summary List portal memberships for an account (internal staff)
 */
export const listPartnerEmails = async (params: ListPartnerEmailsParams, options?: Parameters<typeof customFetch>[1]): Promise<PartnerEmailList> => {

  return customFetch<PartnerEmailList>(getListPartnerEmailsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPartnerEmailsQueryKey = (params?: ListPartnerEmailsParams,) => {
    return [
    `/api/partner-emails`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPartnerEmailsQueryOptions = <TData = Awaited<ReturnType<typeof listPartnerEmails>>, TError = Error>(params: ListPartnerEmailsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPartnerEmails>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPartnerEmailsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPartnerEmails>>> = ({ signal }) => listPartnerEmails(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPartnerEmails>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPartnerEmailsQueryResult = NonNullable<Awaited<ReturnType<typeof listPartnerEmails>>>
export type ListPartnerEmailsQueryError = Error


/**
 * @summary List portal memberships for an account (internal staff)
 */

export function useListPartnerEmails<TData = Awaited<ReturnType<typeof listPartnerEmails>>, TError = Error>(
 params: ListPartnerEmailsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPartnerEmails>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPartnerEmailsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreatePartnerEmailUrl = () => {




  return `/api/partner-emails`
}

/**
 * @summary Add a portal membership to an account (internal staff)
 */
export const createPartnerEmail = async (partnerEmailCreate: PartnerEmailCreate, options?: Parameters<typeof customFetch>[1]): Promise<PartnerEmail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PartnerEmail>(getCreatePartnerEmailUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(partnerEmailCreate)
  }
);}





export const getCreatePartnerEmailMutationKey = () => ['createPartnerEmail'] as const;

export const getCreatePartnerEmailMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPartnerEmail>>, TError,CreatePartnerEmailMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createPartnerEmail>>, TError,CreatePartnerEmailMutationVariables, TContext> => {

const mutationKey = getCreatePartnerEmailMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createPartnerEmail>>, CreatePartnerEmailMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createPartnerEmail(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreatePartnerEmailMutationResult = NonNullable<Awaited<ReturnType<typeof createPartnerEmail>>>
    export type CreatePartnerEmailMutationBody = PartnerEmailCreate
    export type CreatePartnerEmailMutationError = Error
    export type CreatePartnerEmailMutationVariables = {data: PartnerEmailCreate}

    /**
 * @summary Add a portal membership to an account (internal staff)
 */
export const useCreatePartnerEmail = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPartnerEmail>>, TError,CreatePartnerEmailMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createPartnerEmail>>,
        TError,
        CreatePartnerEmailMutationVariables,
        TContext
      > => {
      return useMutation(getCreatePartnerEmailMutationOptions(options));
    }

export const getUpdatePartnerEmailUrl = (id: string,) => {




  return `/api/partner-emails/${id}`
}

/**
 * @summary Update a portal membership (internal staff)
 */
export const updatePartnerEmail = async (id: string,
    partnerEmailUpdate: PartnerEmailUpdate, options?: Parameters<typeof customFetch>[1]): Promise<PartnerEmail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PartnerEmail>(getUpdatePartnerEmailUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(partnerEmailUpdate)
  }
);}





export const getUpdatePartnerEmailMutationKey = () => ['updatePartnerEmail'] as const;

export const getUpdatePartnerEmailMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePartnerEmail>>, TError,UpdatePartnerEmailMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updatePartnerEmail>>, TError,UpdatePartnerEmailMutationVariables, TContext> => {

const mutationKey = getUpdatePartnerEmailMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updatePartnerEmail>>, UpdatePartnerEmailMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updatePartnerEmail(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdatePartnerEmailMutationResult = NonNullable<Awaited<ReturnType<typeof updatePartnerEmail>>>
    export type UpdatePartnerEmailMutationBody = PartnerEmailUpdate
    export type UpdatePartnerEmailMutationError = Error
    export type UpdatePartnerEmailMutationVariables = {id: string;data: PartnerEmailUpdate}

    /**
 * @summary Update a portal membership (internal staff)
 */
export const useUpdatePartnerEmail = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePartnerEmail>>, TError,UpdatePartnerEmailMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updatePartnerEmail>>,
        TError,
        UpdatePartnerEmailMutationVariables,
        TContext
      > => {
      return useMutation(getUpdatePartnerEmailMutationOptions(options));
    }

export const getDeletePartnerEmailUrl = (id: string,) => {




  return `/api/partner-emails/${id}`
}

/**
 * @summary Delete a never-used portal membership (internal staff)
 */
export const deletePartnerEmail = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeletePartnerEmailUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeletePartnerEmailMutationKey = () => ['deletePartnerEmail'] as const;

export const getDeletePartnerEmailMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deletePartnerEmail>>, TError,DeletePartnerEmailMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deletePartnerEmail>>, TError,DeletePartnerEmailMutationVariables, TContext> => {

const mutationKey = getDeletePartnerEmailMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deletePartnerEmail>>, DeletePartnerEmailMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deletePartnerEmail(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeletePartnerEmailMutationResult = NonNullable<Awaited<ReturnType<typeof deletePartnerEmail>>>

    export type DeletePartnerEmailMutationError = Error
    export type DeletePartnerEmailMutationVariables = {id: string}

    /**
 * @summary Delete a never-used portal membership (internal staff)
 */
export const useDeletePartnerEmail = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deletePartnerEmail>>, TError,DeletePartnerEmailMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deletePartnerEmail>>,
        TError,
        DeletePartnerEmailMutationVariables,
        TContext
      > => {
      return useMutation(getDeletePartnerEmailMutationOptions(options));
    }

export const getListPortalSharesForStaffUrl = (params: ListPortalSharesForStaffParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/portal-shares?${stringifiedParams}` : `/api/portal-shares`
}

/**
 * @summary List consumer portal shares for a person account (internal staff)
 */
export const listPortalSharesForStaff = async (params: ListPortalSharesForStaffParams, options?: Parameters<typeof customFetch>[1]): Promise<PortalShareList> => {

  return customFetch<PortalShareList>(getListPortalSharesForStaffUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPortalSharesForStaffQueryKey = (params?: ListPortalSharesForStaffParams,) => {
    return [
    `/api/portal-shares`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPortalSharesForStaffQueryOptions = <TData = Awaited<ReturnType<typeof listPortalSharesForStaff>>, TError = Error>(params: ListPortalSharesForStaffParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPortalSharesForStaff>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPortalSharesForStaffQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPortalSharesForStaff>>> = ({ signal }) => listPortalSharesForStaff(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPortalSharesForStaff>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPortalSharesForStaffQueryResult = NonNullable<Awaited<ReturnType<typeof listPortalSharesForStaff>>>
export type ListPortalSharesForStaffQueryError = Error


/**
 * @summary List consumer portal shares for a person account (internal staff)
 */

export function useListPortalSharesForStaff<TData = Awaited<ReturnType<typeof listPortalSharesForStaff>>, TError = Error>(
 params: ListPortalSharesForStaffParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPortalSharesForStaff>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPortalSharesForStaffQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRevokePortalShareForStaffUrl = (id: string,
    params: RevokePortalShareForStaffParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/portal-shares/${id}?${stringifiedParams}` : `/api/portal-shares/${id}`
}

/**
 * @summary Revoke a consumer portal share (internal staff)
 */
export const revokePortalShareForStaff = async (id: string,
    params: RevokePortalShareForStaffParams, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getRevokePortalShareForStaffUrl(id,params),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getRevokePortalShareForStaffMutationKey = () => ['revokePortalShareForStaff'] as const;

export const getRevokePortalShareForStaffMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokePortalShareForStaff>>, TError,RevokePortalShareForStaffMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokePortalShareForStaff>>, TError,RevokePortalShareForStaffMutationVariables, TContext> => {

const mutationKey = getRevokePortalShareForStaffMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokePortalShareForStaff>>, RevokePortalShareForStaffMutationVariables> = (props) => {
          const {id,params} = props ?? {};

          return  revokePortalShareForStaff(id,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokePortalShareForStaffMutationResult = NonNullable<Awaited<ReturnType<typeof revokePortalShareForStaff>>>

    export type RevokePortalShareForStaffMutationError = Error
    export type RevokePortalShareForStaffMutationVariables = {id: string;params: RevokePortalShareForStaffParams}

    /**
 * @summary Revoke a consumer portal share (internal staff)
 */
export const useRevokePortalShareForStaff = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokePortalShareForStaff>>, TError,RevokePortalShareForStaffMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof revokePortalShareForStaff>>,
        TError,
        RevokePortalShareForStaffMutationVariables,
        TContext
      > => {
      return useMutation(getRevokePortalShareForStaffMutationOptions(options));
    }

export const getListPartnerAgreementsUrl = (params: ListPartnerAgreementsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/partner-agreements?${stringifiedParams}` : `/api/partner-agreements`
}

/**
 * @summary List agreements for a partner
 */
export const listPartnerAgreements = async (params: ListPartnerAgreementsParams, options?: Parameters<typeof customFetch>[1]): Promise<PartnerAgreementList> => {

  return customFetch<PartnerAgreementList>(getListPartnerAgreementsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPartnerAgreementsQueryKey = (params?: ListPartnerAgreementsParams,) => {
    return [
    `/api/partner-agreements`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPartnerAgreementsQueryOptions = <TData = Awaited<ReturnType<typeof listPartnerAgreements>>, TError = unknown>(params: ListPartnerAgreementsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPartnerAgreements>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPartnerAgreementsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPartnerAgreements>>> = ({ signal }) => listPartnerAgreements(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPartnerAgreements>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPartnerAgreementsQueryResult = NonNullable<Awaited<ReturnType<typeof listPartnerAgreements>>>
export type ListPartnerAgreementsQueryError = unknown


/**
 * @summary List agreements for a partner
 */

export function useListPartnerAgreements<TData = Awaited<ReturnType<typeof listPartnerAgreements>>, TError = unknown>(
 params: ListPartnerAgreementsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPartnerAgreements>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPartnerAgreementsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreatePartnerAgreementUrl = () => {




  return `/api/partner-agreements`
}

/**
 * @summary Add an agreement to a partner
 */
export const createPartnerAgreement = async (partnerAgreementCreate: PartnerAgreementCreate, options?: Parameters<typeof customFetch>[1]): Promise<PartnerAgreement> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PartnerAgreement>(getCreatePartnerAgreementUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(partnerAgreementCreate)
  }
);}





export const getCreatePartnerAgreementMutationKey = () => ['createPartnerAgreement'] as const;

export const getCreatePartnerAgreementMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPartnerAgreement>>, TError,CreatePartnerAgreementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createPartnerAgreement>>, TError,CreatePartnerAgreementMutationVariables, TContext> => {

const mutationKey = getCreatePartnerAgreementMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createPartnerAgreement>>, CreatePartnerAgreementMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createPartnerAgreement(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreatePartnerAgreementMutationResult = NonNullable<Awaited<ReturnType<typeof createPartnerAgreement>>>
    export type CreatePartnerAgreementMutationBody = PartnerAgreementCreate
    export type CreatePartnerAgreementMutationError = Error
    export type CreatePartnerAgreementMutationVariables = {data: PartnerAgreementCreate}

    /**
 * @summary Add an agreement to a partner
 */
export const useCreatePartnerAgreement = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPartnerAgreement>>, TError,CreatePartnerAgreementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createPartnerAgreement>>,
        TError,
        CreatePartnerAgreementMutationVariables,
        TContext
      > => {
      return useMutation(getCreatePartnerAgreementMutationOptions(options));
    }

export const getUpdatePartnerAgreementUrl = (id: string,) => {




  return `/api/partner-agreements/${id}`
}

/**
 * @summary Update a partner agreement
 */
export const updatePartnerAgreement = async (id: string,
    partnerAgreementUpdate: PartnerAgreementUpdate, options?: Parameters<typeof customFetch>[1]): Promise<PartnerAgreement> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PartnerAgreement>(getUpdatePartnerAgreementUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(partnerAgreementUpdate)
  }
);}





export const getUpdatePartnerAgreementMutationKey = () => ['updatePartnerAgreement'] as const;

export const getUpdatePartnerAgreementMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePartnerAgreement>>, TError,UpdatePartnerAgreementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updatePartnerAgreement>>, TError,UpdatePartnerAgreementMutationVariables, TContext> => {

const mutationKey = getUpdatePartnerAgreementMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updatePartnerAgreement>>, UpdatePartnerAgreementMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updatePartnerAgreement(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdatePartnerAgreementMutationResult = NonNullable<Awaited<ReturnType<typeof updatePartnerAgreement>>>
    export type UpdatePartnerAgreementMutationBody = PartnerAgreementUpdate
    export type UpdatePartnerAgreementMutationError = Error
    export type UpdatePartnerAgreementMutationVariables = {id: string;data: PartnerAgreementUpdate}

    /**
 * @summary Update a partner agreement
 */
export const useUpdatePartnerAgreement = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePartnerAgreement>>, TError,UpdatePartnerAgreementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updatePartnerAgreement>>,
        TError,
        UpdatePartnerAgreementMutationVariables,
        TContext
      > => {
      return useMutation(getUpdatePartnerAgreementMutationOptions(options));
    }

export const getDeletePartnerAgreementUrl = (id: string,) => {




  return `/api/partner-agreements/${id}`
}

/**
 * @summary Remove a partner agreement
 */
export const deletePartnerAgreement = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeletePartnerAgreementUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeletePartnerAgreementMutationKey = () => ['deletePartnerAgreement'] as const;

export const getDeletePartnerAgreementMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deletePartnerAgreement>>, TError,DeletePartnerAgreementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deletePartnerAgreement>>, TError,DeletePartnerAgreementMutationVariables, TContext> => {

const mutationKey = getDeletePartnerAgreementMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deletePartnerAgreement>>, DeletePartnerAgreementMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deletePartnerAgreement(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeletePartnerAgreementMutationResult = NonNullable<Awaited<ReturnType<typeof deletePartnerAgreement>>>

    export type DeletePartnerAgreementMutationError = Error
    export type DeletePartnerAgreementMutationVariables = {id: string}

    /**
 * @summary Remove a partner agreement
 */
export const useDeletePartnerAgreement = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deletePartnerAgreement>>, TError,DeletePartnerAgreementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deletePartnerAgreement>>,
        TError,
        DeletePartnerAgreementMutationVariables,
        TContext
      > => {
      return useMutation(getDeletePartnerAgreementMutationOptions(options));
    }

export const getListPartnerInteractionsUrl = (params: ListPartnerInteractionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/partner-interactions?${stringifiedParams}` : `/api/partner-interactions`
}

/**
 * @summary List interactions for a partner
 */
export const listPartnerInteractions = async (params: ListPartnerInteractionsParams, options?: Parameters<typeof customFetch>[1]): Promise<PartnerInteractionList> => {

  return customFetch<PartnerInteractionList>(getListPartnerInteractionsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPartnerInteractionsQueryKey = (params?: ListPartnerInteractionsParams,) => {
    return [
    `/api/partner-interactions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPartnerInteractionsQueryOptions = <TData = Awaited<ReturnType<typeof listPartnerInteractions>>, TError = unknown>(params: ListPartnerInteractionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPartnerInteractions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPartnerInteractionsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPartnerInteractions>>> = ({ signal }) => listPartnerInteractions(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPartnerInteractions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPartnerInteractionsQueryResult = NonNullable<Awaited<ReturnType<typeof listPartnerInteractions>>>
export type ListPartnerInteractionsQueryError = unknown


/**
 * @summary List interactions for a partner
 */

export function useListPartnerInteractions<TData = Awaited<ReturnType<typeof listPartnerInteractions>>, TError = unknown>(
 params: ListPartnerInteractionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPartnerInteractions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPartnerInteractionsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreatePartnerInteractionUrl = () => {




  return `/api/partner-interactions`
}

/**
 * @summary Log an interaction with a partner
 */
export const createPartnerInteraction = async (partnerInteractionCreate: PartnerInteractionCreate, options?: Parameters<typeof customFetch>[1]): Promise<PartnerInteraction> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PartnerInteraction>(getCreatePartnerInteractionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(partnerInteractionCreate)
  }
);}





export const getCreatePartnerInteractionMutationKey = () => ['createPartnerInteraction'] as const;

export const getCreatePartnerInteractionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPartnerInteraction>>, TError,CreatePartnerInteractionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createPartnerInteraction>>, TError,CreatePartnerInteractionMutationVariables, TContext> => {

const mutationKey = getCreatePartnerInteractionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createPartnerInteraction>>, CreatePartnerInteractionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createPartnerInteraction(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreatePartnerInteractionMutationResult = NonNullable<Awaited<ReturnType<typeof createPartnerInteraction>>>
    export type CreatePartnerInteractionMutationBody = PartnerInteractionCreate
    export type CreatePartnerInteractionMutationError = Error
    export type CreatePartnerInteractionMutationVariables = {data: PartnerInteractionCreate}

    /**
 * @summary Log an interaction with a partner
 */
export const useCreatePartnerInteraction = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPartnerInteraction>>, TError,CreatePartnerInteractionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createPartnerInteraction>>,
        TError,
        CreatePartnerInteractionMutationVariables,
        TContext
      > => {
      return useMutation(getCreatePartnerInteractionMutationOptions(options));
    }

export const getUpdatePartnerInteractionUrl = (id: string,) => {




  return `/api/partner-interactions/${id}`
}

/**
 * @summary Update a partner interaction
 */
export const updatePartnerInteraction = async (id: string,
    partnerInteractionUpdate: PartnerInteractionUpdate, options?: Parameters<typeof customFetch>[1]): Promise<PartnerInteraction> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PartnerInteraction>(getUpdatePartnerInteractionUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(partnerInteractionUpdate)
  }
);}





export const getUpdatePartnerInteractionMutationKey = () => ['updatePartnerInteraction'] as const;

export const getUpdatePartnerInteractionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePartnerInteraction>>, TError,UpdatePartnerInteractionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updatePartnerInteraction>>, TError,UpdatePartnerInteractionMutationVariables, TContext> => {

const mutationKey = getUpdatePartnerInteractionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updatePartnerInteraction>>, UpdatePartnerInteractionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updatePartnerInteraction(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdatePartnerInteractionMutationResult = NonNullable<Awaited<ReturnType<typeof updatePartnerInteraction>>>
    export type UpdatePartnerInteractionMutationBody = PartnerInteractionUpdate
    export type UpdatePartnerInteractionMutationError = Error
    export type UpdatePartnerInteractionMutationVariables = {id: string;data: PartnerInteractionUpdate}

    /**
 * @summary Update a partner interaction
 */
export const useUpdatePartnerInteraction = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePartnerInteraction>>, TError,UpdatePartnerInteractionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updatePartnerInteraction>>,
        TError,
        UpdatePartnerInteractionMutationVariables,
        TContext
      > => {
      return useMutation(getUpdatePartnerInteractionMutationOptions(options));
    }

export const getDeletePartnerInteractionUrl = (id: string,) => {




  return `/api/partner-interactions/${id}`
}

/**
 * @summary Remove a partner interaction
 */
export const deletePartnerInteraction = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeletePartnerInteractionUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeletePartnerInteractionMutationKey = () => ['deletePartnerInteraction'] as const;

export const getDeletePartnerInteractionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deletePartnerInteraction>>, TError,DeletePartnerInteractionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deletePartnerInteraction>>, TError,DeletePartnerInteractionMutationVariables, TContext> => {

const mutationKey = getDeletePartnerInteractionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deletePartnerInteraction>>, DeletePartnerInteractionMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deletePartnerInteraction(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeletePartnerInteractionMutationResult = NonNullable<Awaited<ReturnType<typeof deletePartnerInteraction>>>

    export type DeletePartnerInteractionMutationError = Error
    export type DeletePartnerInteractionMutationVariables = {id: string}

    /**
 * @summary Remove a partner interaction
 */
export const useDeletePartnerInteraction = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deletePartnerInteraction>>, TError,DeletePartnerInteractionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deletePartnerInteraction>>,
        TError,
        DeletePartnerInteractionMutationVariables,
        TContext
      > => {
      return useMutation(getDeletePartnerInteractionMutationOptions(options));
    }

export const getListPartnerCollateralUrl = (params: ListPartnerCollateralParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/partner-collateral?${stringifiedParams}` : `/api/partner-collateral`
}

/**
 * @summary List collateral links for a partner
 */
export const listPartnerCollateral = async (params: ListPartnerCollateralParams, options?: Parameters<typeof customFetch>[1]): Promise<PartnerCollateralList> => {

  return customFetch<PartnerCollateralList>(getListPartnerCollateralUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPartnerCollateralQueryKey = (params?: ListPartnerCollateralParams,) => {
    return [
    `/api/partner-collateral`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPartnerCollateralQueryOptions = <TData = Awaited<ReturnType<typeof listPartnerCollateral>>, TError = unknown>(params: ListPartnerCollateralParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPartnerCollateral>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPartnerCollateralQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPartnerCollateral>>> = ({ signal }) => listPartnerCollateral(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPartnerCollateral>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPartnerCollateralQueryResult = NonNullable<Awaited<ReturnType<typeof listPartnerCollateral>>>
export type ListPartnerCollateralQueryError = unknown


/**
 * @summary List collateral links for a partner
 */

export function useListPartnerCollateral<TData = Awaited<ReturnType<typeof listPartnerCollateral>>, TError = unknown>(
 params: ListPartnerCollateralParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPartnerCollateral>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPartnerCollateralQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreatePartnerCollateralUrl = () => {




  return `/api/partner-collateral`
}

/**
 * @summary Add a collateral link to a partner
 */
export const createPartnerCollateral = async (partnerCollateralCreate: PartnerCollateralCreate, options?: Parameters<typeof customFetch>[1]): Promise<PartnerCollateral> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PartnerCollateral>(getCreatePartnerCollateralUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(partnerCollateralCreate)
  }
);}





export const getCreatePartnerCollateralMutationKey = () => ['createPartnerCollateral'] as const;

export const getCreatePartnerCollateralMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPartnerCollateral>>, TError,CreatePartnerCollateralMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createPartnerCollateral>>, TError,CreatePartnerCollateralMutationVariables, TContext> => {

const mutationKey = getCreatePartnerCollateralMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createPartnerCollateral>>, CreatePartnerCollateralMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createPartnerCollateral(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreatePartnerCollateralMutationResult = NonNullable<Awaited<ReturnType<typeof createPartnerCollateral>>>
    export type CreatePartnerCollateralMutationBody = PartnerCollateralCreate
    export type CreatePartnerCollateralMutationError = Error
    export type CreatePartnerCollateralMutationVariables = {data: PartnerCollateralCreate}

    /**
 * @summary Add a collateral link to a partner
 */
export const useCreatePartnerCollateral = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPartnerCollateral>>, TError,CreatePartnerCollateralMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createPartnerCollateral>>,
        TError,
        CreatePartnerCollateralMutationVariables,
        TContext
      > => {
      return useMutation(getCreatePartnerCollateralMutationOptions(options));
    }

export const getUpdatePartnerCollateralUrl = (id: string,) => {




  return `/api/partner-collateral/${id}`
}

/**
 * @summary Update a partner collateral link
 */
export const updatePartnerCollateral = async (id: string,
    partnerCollateralUpdate: PartnerCollateralUpdate, options?: Parameters<typeof customFetch>[1]): Promise<PartnerCollateral> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PartnerCollateral>(getUpdatePartnerCollateralUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(partnerCollateralUpdate)
  }
);}





export const getUpdatePartnerCollateralMutationKey = () => ['updatePartnerCollateral'] as const;

export const getUpdatePartnerCollateralMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePartnerCollateral>>, TError,UpdatePartnerCollateralMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updatePartnerCollateral>>, TError,UpdatePartnerCollateralMutationVariables, TContext> => {

const mutationKey = getUpdatePartnerCollateralMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updatePartnerCollateral>>, UpdatePartnerCollateralMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updatePartnerCollateral(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdatePartnerCollateralMutationResult = NonNullable<Awaited<ReturnType<typeof updatePartnerCollateral>>>
    export type UpdatePartnerCollateralMutationBody = PartnerCollateralUpdate
    export type UpdatePartnerCollateralMutationError = Error
    export type UpdatePartnerCollateralMutationVariables = {id: string;data: PartnerCollateralUpdate}

    /**
 * @summary Update a partner collateral link
 */
export const useUpdatePartnerCollateral = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePartnerCollateral>>, TError,UpdatePartnerCollateralMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updatePartnerCollateral>>,
        TError,
        UpdatePartnerCollateralMutationVariables,
        TContext
      > => {
      return useMutation(getUpdatePartnerCollateralMutationOptions(options));
    }

export const getDeletePartnerCollateralUrl = (id: string,) => {




  return `/api/partner-collateral/${id}`
}

/**
 * @summary Remove a partner collateral link
 */
export const deletePartnerCollateral = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeletePartnerCollateralUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeletePartnerCollateralMutationKey = () => ['deletePartnerCollateral'] as const;

export const getDeletePartnerCollateralMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deletePartnerCollateral>>, TError,DeletePartnerCollateralMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deletePartnerCollateral>>, TError,DeletePartnerCollateralMutationVariables, TContext> => {

const mutationKey = getDeletePartnerCollateralMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deletePartnerCollateral>>, DeletePartnerCollateralMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deletePartnerCollateral(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeletePartnerCollateralMutationResult = NonNullable<Awaited<ReturnType<typeof deletePartnerCollateral>>>

    export type DeletePartnerCollateralMutationError = Error
    export type DeletePartnerCollateralMutationVariables = {id: string}

    /**
 * @summary Remove a partner collateral link
 */
export const useDeletePartnerCollateral = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deletePartnerCollateral>>, TError,DeletePartnerCollateralMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deletePartnerCollateral>>,
        TError,
        DeletePartnerCollateralMutationVariables,
        TContext
      > => {
      return useMutation(getDeletePartnerCollateralMutationOptions(options));
    }

export const getGetPartnerDashboardStatsUrl = () => {




  return `/api/partner-dashboard/stats`
}

/**
 * @summary Partner Engagement dashboard KPI aggregates
 */
export const getPartnerDashboardStats = async ( options?: Parameters<typeof customFetch>[1]): Promise<PartnerDashboardStats> => {

  return customFetch<PartnerDashboardStats>(getGetPartnerDashboardStatsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPartnerDashboardStatsQueryKey = () => {
    return [
    `/api/partner-dashboard/stats`
    ] as const;
    }


export const getGetPartnerDashboardStatsQueryOptions = <TData = Awaited<ReturnType<typeof getPartnerDashboardStats>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPartnerDashboardStats>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPartnerDashboardStatsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPartnerDashboardStats>>> = ({ signal }) => getPartnerDashboardStats({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPartnerDashboardStats>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPartnerDashboardStatsQueryResult = NonNullable<Awaited<ReturnType<typeof getPartnerDashboardStats>>>
export type GetPartnerDashboardStatsQueryError = unknown


/**
 * @summary Partner Engagement dashboard KPI aggregates
 */

export function useGetPartnerDashboardStats<TData = Awaited<ReturnType<typeof getPartnerDashboardStats>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPartnerDashboardStats>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPartnerDashboardStatsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPluginsStatusUrl = () => {




  return `/api/plugins/status`
}

/**
 * Public (unauthenticated) endpoint. Returns the normalized state of the
 * database, storage, AI, video generation, and auth plugins. Only non-secret values are
 * included (e.g. the MSAL public client config). Never returns keys,
 * connection strings, or SAS URLs.
 * @summary Availability and public config of platform plugins
 */
export const getPluginsStatus = async ( options?: Parameters<typeof customFetch>[1]): Promise<PluginsStatus> => {

  return customFetch<PluginsStatus>(getGetPluginsStatusUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPluginsStatusQueryKey = () => {
    return [
    `/api/plugins/status`
    ] as const;
    }


export const getGetPluginsStatusQueryOptions = <TData = Awaited<ReturnType<typeof getPluginsStatus>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPluginsStatusQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPluginsStatus>>> = ({ signal }) => getPluginsStatus({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatus>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPluginsStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getPluginsStatus>>>
export type GetPluginsStatusQueryError = unknown


/**
 * @summary Availability and public config of platform plugins
 */

export function useGetPluginsStatus<TData = Awaited<ReturnType<typeof getPluginsStatus>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPluginsStatusQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getTestAiPluginConnectionUrl = () => {




  return `/api/plugins/test/ai`
}

/**
 * Sends a minimal server-side completion using the configured model and
 * credentials. The response contains only a safe result message and
 * latency; credentials, upstream headers, prompts, and response bodies
 * are never returned.
 * @summary Test the configured Project AI chat connection
 */
export const testAiPluginConnection = async ( options?: Parameters<typeof customFetch>[1]): Promise<PluginTestResult> => {

  return customFetch<PluginTestResult>(getTestAiPluginConnectionUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getTestAiPluginConnectionMutationKey = () => ['testAiPluginConnection'] as const;

export const getTestAiPluginConnectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof testAiPluginConnection>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof testAiPluginConnection>>, TError,void, TContext> => {

const mutationKey = getTestAiPluginConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof testAiPluginConnection>>, void> = () => {


          return  testAiPluginConnection(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TestAiPluginConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof testAiPluginConnection>>>

    export type TestAiPluginConnectionMutationError = Error


    /**
 * @summary Test the configured Project AI chat connection
 */
export const useTestAiPluginConnection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof testAiPluginConnection>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof testAiPluginConnection>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getTestAiPluginConnectionMutationOptions(options));
    }

export const getListPlatformUsageTokenRatesUrl = (params?: ListPlatformUsageTokenRatesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/usage-token-rates?${stringifiedParams}` : `/api/platform/usage-token-rates`
}

/**
 * Requires platform.tokens.manage.
 * @summary List configured metered action rates
 */
export const listPlatformUsageTokenRates = async (params?: ListPlatformUsageTokenRatesParams, options?: Parameters<typeof customFetch>[1]): Promise<UsageTokenRatePage> => {

  return customFetch<UsageTokenRatePage>(getListPlatformUsageTokenRatesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformUsageTokenRatesQueryKey = (params?: ListPlatformUsageTokenRatesParams,) => {
    return [
    `/api/platform/usage-token-rates`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformUsageTokenRatesQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformUsageTokenRates>>, TError = void>(params?: ListPlatformUsageTokenRatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformUsageTokenRates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformUsageTokenRatesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformUsageTokenRates>>> = ({ signal }) => listPlatformUsageTokenRates(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformUsageTokenRates>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformUsageTokenRatesQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformUsageTokenRates>>>
export type ListPlatformUsageTokenRatesQueryError = void


/**
 * @summary List configured metered action rates
 */

export function useListPlatformUsageTokenRates<TData = Awaited<ReturnType<typeof listPlatformUsageTokenRates>>, TError = void>(
 params?: ListPlatformUsageTokenRatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformUsageTokenRates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformUsageTokenRatesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdatePlatformUsageTokenRateUrl = (actionKey: string,) => {




  return `/api/platform/usage-token-rates/${actionKey}`
}

/**
 * Requires platform.tokens.manage.
 * @summary Update a metered action rate
 */
export const updatePlatformUsageTokenRate = async (actionKey: string,
    usageTokenRateWrite: UsageTokenRateWrite, options?: Parameters<typeof customFetch>[1]): Promise<UsageTokenRate> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<UsageTokenRate>(getUpdatePlatformUsageTokenRateUrl(actionKey),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(usageTokenRateWrite)
  }
);}





export const getUpdatePlatformUsageTokenRateMutationKey = () => ['updatePlatformUsageTokenRate'] as const;

export const getUpdatePlatformUsageTokenRateMutationOptions = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePlatformUsageTokenRate>>, TError,UpdatePlatformUsageTokenRateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updatePlatformUsageTokenRate>>, TError,UpdatePlatformUsageTokenRateMutationVariables, TContext> => {

const mutationKey = getUpdatePlatformUsageTokenRateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updatePlatformUsageTokenRate>>, UpdatePlatformUsageTokenRateMutationVariables> = (props) => {
          const {actionKey,data} = props ?? {};

          return  updatePlatformUsageTokenRate(actionKey,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdatePlatformUsageTokenRateMutationResult = NonNullable<Awaited<ReturnType<typeof updatePlatformUsageTokenRate>>>
    export type UpdatePlatformUsageTokenRateMutationBody = UsageTokenRateWrite
    export type UpdatePlatformUsageTokenRateMutationError = Error | void
    export type UpdatePlatformUsageTokenRateMutationVariables = {actionKey: string;data: UsageTokenRateWrite}

    /**
 * @summary Update a metered action rate
 */
export const useUpdatePlatformUsageTokenRate = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePlatformUsageTokenRate>>, TError,UpdatePlatformUsageTokenRateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updatePlatformUsageTokenRate>>,
        TError,
        UpdatePlatformUsageTokenRateMutationVariables,
        TContext
      > => {
      return useMutation(getUpdatePlatformUsageTokenRateMutationOptions(options));
    }

export const getListPlatformUsageTokenBalancesUrl = (params?: ListPlatformUsageTokenBalancesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/usage-token-balances?${stringifiedParams}` : `/api/platform/usage-token-balances`
}

/**
 * Requires platform.tokens.manage.
 * @summary List tenant usage token balances
 */
export const listPlatformUsageTokenBalances = async (params?: ListPlatformUsageTokenBalancesParams, options?: Parameters<typeof customFetch>[1]): Promise<UsageTokenBalancePage> => {

  return customFetch<UsageTokenBalancePage>(getListPlatformUsageTokenBalancesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformUsageTokenBalancesQueryKey = (params?: ListPlatformUsageTokenBalancesParams,) => {
    return [
    `/api/platform/usage-token-balances`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformUsageTokenBalancesQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformUsageTokenBalances>>, TError = void>(params?: ListPlatformUsageTokenBalancesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformUsageTokenBalances>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformUsageTokenBalancesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformUsageTokenBalances>>> = ({ signal }) => listPlatformUsageTokenBalances(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformUsageTokenBalances>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformUsageTokenBalancesQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformUsageTokenBalances>>>
export type ListPlatformUsageTokenBalancesQueryError = void


/**
 * @summary List tenant usage token balances
 */

export function useListPlatformUsageTokenBalances<TData = Awaited<ReturnType<typeof listPlatformUsageTokenBalances>>, TError = void>(
 params?: ListPlatformUsageTokenBalancesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformUsageTokenBalances>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformUsageTokenBalancesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListPlatformUsageTokenGrantsUrl = (params?: ListPlatformUsageTokenGrantsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/usage-token-grants?${stringifiedParams}` : `/api/platform/usage-token-grants`
}

/**
 * Requires platform.tokens.manage.
 * @summary List usage token grants
 */
export const listPlatformUsageTokenGrants = async (params?: ListPlatformUsageTokenGrantsParams, options?: Parameters<typeof customFetch>[1]): Promise<UsageTokenGrantPage> => {

  return customFetch<UsageTokenGrantPage>(getListPlatformUsageTokenGrantsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformUsageTokenGrantsQueryKey = (params?: ListPlatformUsageTokenGrantsParams,) => {
    return [
    `/api/platform/usage-token-grants`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformUsageTokenGrantsQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformUsageTokenGrants>>, TError = void>(params?: ListPlatformUsageTokenGrantsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformUsageTokenGrants>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformUsageTokenGrantsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformUsageTokenGrants>>> = ({ signal }) => listPlatformUsageTokenGrants(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformUsageTokenGrants>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformUsageTokenGrantsQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformUsageTokenGrants>>>
export type ListPlatformUsageTokenGrantsQueryError = void


/**
 * @summary List usage token grants
 */

export function useListPlatformUsageTokenGrants<TData = Awaited<ReturnType<typeof listPlatformUsageTokenGrants>>, TError = void>(
 params?: ListPlatformUsageTokenGrantsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformUsageTokenGrants>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformUsageTokenGrantsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreatePlatformUsageTokenGrantUrl = () => {




  return `/api/platform/usage-token-grants`
}

/**
 * Requires platform.tokens.manage.
 * @summary Grant usage tokens to a tenant
 */
export const createPlatformUsageTokenGrant = async (usageTokenGrantWrite: UsageTokenGrantWrite, options?: Parameters<typeof customFetch>[1]): Promise<UsageTokenGrantResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<UsageTokenGrantResult>(getCreatePlatformUsageTokenGrantUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(usageTokenGrantWrite)
  }
);}





export const getCreatePlatformUsageTokenGrantMutationKey = () => ['createPlatformUsageTokenGrant'] as const;

export const getCreatePlatformUsageTokenGrantMutationOptions = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPlatformUsageTokenGrant>>, TError,CreatePlatformUsageTokenGrantMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createPlatformUsageTokenGrant>>, TError,CreatePlatformUsageTokenGrantMutationVariables, TContext> => {

const mutationKey = getCreatePlatformUsageTokenGrantMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createPlatformUsageTokenGrant>>, CreatePlatformUsageTokenGrantMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createPlatformUsageTokenGrant(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreatePlatformUsageTokenGrantMutationResult = NonNullable<Awaited<ReturnType<typeof createPlatformUsageTokenGrant>>>
    export type CreatePlatformUsageTokenGrantMutationBody = UsageTokenGrantWrite
    export type CreatePlatformUsageTokenGrantMutationError = Error | void
    export type CreatePlatformUsageTokenGrantMutationVariables = {data: UsageTokenGrantWrite}

    /**
 * @summary Grant usage tokens to a tenant
 */
export const useCreatePlatformUsageTokenGrant = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPlatformUsageTokenGrant>>, TError,CreatePlatformUsageTokenGrantMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createPlatformUsageTokenGrant>>,
        TError,
        CreatePlatformUsageTokenGrantMutationVariables,
        TContext
      > => {
      return useMutation(getCreatePlatformUsageTokenGrantMutationOptions(options));
    }

export const getCreatePlatformTenantDataExportUrl = (id: string,) => {




  return `/api/platform/tenants/${id}/data/exports`
}

/**
 * Requires platform.tenants.manage.
 * @summary Queue a data export for a tenant
 */
export const createPlatformTenantDataExport = async (id: string,
    dataExportRequest: DataExportRequest, options?: Parameters<typeof customFetch>[1]): Promise<DataManagementJob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DataManagementJob>(getCreatePlatformTenantDataExportUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(dataExportRequest)
  }
);}





export const getCreatePlatformTenantDataExportMutationKey = () => ['createPlatformTenantDataExport'] as const;

export const getCreatePlatformTenantDataExportMutationOptions = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPlatformTenantDataExport>>, TError,CreatePlatformTenantDataExportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createPlatformTenantDataExport>>, TError,CreatePlatformTenantDataExportMutationVariables, TContext> => {

const mutationKey = getCreatePlatformTenantDataExportMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createPlatformTenantDataExport>>, CreatePlatformTenantDataExportMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createPlatformTenantDataExport(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreatePlatformTenantDataExportMutationResult = NonNullable<Awaited<ReturnType<typeof createPlatformTenantDataExport>>>
    export type CreatePlatformTenantDataExportMutationBody = DataExportRequest
    export type CreatePlatformTenantDataExportMutationError = Error | void
    export type CreatePlatformTenantDataExportMutationVariables = {id: string;data: DataExportRequest}

    /**
 * @summary Queue a data export for a tenant
 */
export const useCreatePlatformTenantDataExport = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPlatformTenantDataExport>>, TError,CreatePlatformTenantDataExportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createPlatformTenantDataExport>>,
        TError,
        CreatePlatformTenantDataExportMutationVariables,
        TContext
      > => {
      return useMutation(getCreatePlatformTenantDataExportMutationOptions(options));
    }

export const getGetPlatformAgentVoicePolicyUrl = () => {




  return `/api/platform/agent-voice-policy`
}

/**
 * Requires platform.features.manage. Null means no configured limit.
 * @summary Read optional platform-wide Agent voice allowances
 */
export const getPlatformAgentVoicePolicy = async ( options?: Parameters<typeof customFetch>[1]): Promise<PlatformAgentVoicePolicy> => {

  return customFetch<PlatformAgentVoicePolicy>(getGetPlatformAgentVoicePolicyUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPlatformAgentVoicePolicyQueryKey = () => {
    return [
    `/api/platform/agent-voice-policy`
    ] as const;
    }


export const getGetPlatformAgentVoicePolicyQueryOptions = <TData = Awaited<ReturnType<typeof getPlatformAgentVoicePolicy>>, TError = void>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPlatformAgentVoicePolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPlatformAgentVoicePolicyQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPlatformAgentVoicePolicy>>> = ({ signal }) => getPlatformAgentVoicePolicy({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPlatformAgentVoicePolicy>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPlatformAgentVoicePolicyQueryResult = NonNullable<Awaited<ReturnType<typeof getPlatformAgentVoicePolicy>>>
export type GetPlatformAgentVoicePolicyQueryError = void


/**
 * @summary Read optional platform-wide Agent voice allowances
 */

export function useGetPlatformAgentVoicePolicy<TData = Awaited<ReturnType<typeof getPlatformAgentVoicePolicy>>, TError = void>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPlatformAgentVoicePolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPlatformAgentVoicePolicyQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getWritePlatformAgentVoicePolicyUrl = () => {




  return `/api/platform/agent-voice-policy`
}

/**
 * Requires platform.features.manage and the exact current revision. Null clears a limit.
 * @summary Explicitly save an audited Agent voice policy revision
 */
export const writePlatformAgentVoicePolicy = async (platformAgentVoicePolicyWrite: PlatformAgentVoicePolicyWrite, options?: Parameters<typeof customFetch>[1]): Promise<PlatformAgentVoicePolicy> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PlatformAgentVoicePolicy>(getWritePlatformAgentVoicePolicyUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(platformAgentVoicePolicyWrite)
  }
);}





export const getWritePlatformAgentVoicePolicyMutationKey = () => ['writePlatformAgentVoicePolicy'] as const;

export const getWritePlatformAgentVoicePolicyMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writePlatformAgentVoicePolicy>>, TError,WritePlatformAgentVoicePolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof writePlatformAgentVoicePolicy>>, TError,WritePlatformAgentVoicePolicyMutationVariables, TContext> => {

const mutationKey = getWritePlatformAgentVoicePolicyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof writePlatformAgentVoicePolicy>>, WritePlatformAgentVoicePolicyMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  writePlatformAgentVoicePolicy(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type WritePlatformAgentVoicePolicyMutationResult = NonNullable<Awaited<ReturnType<typeof writePlatformAgentVoicePolicy>>>
    export type WritePlatformAgentVoicePolicyMutationBody = PlatformAgentVoicePolicyWrite
    export type WritePlatformAgentVoicePolicyMutationError = void
    export type WritePlatformAgentVoicePolicyMutationVariables = {data: PlatformAgentVoicePolicyWrite}

    /**
 * @summary Explicitly save an audited Agent voice policy revision
 */
export const useWritePlatformAgentVoicePolicy = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writePlatformAgentVoicePolicy>>, TError,WritePlatformAgentVoicePolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof writePlatformAgentVoicePolicy>>,
        TError,
        WritePlatformAgentVoicePolicyMutationVariables,
        TContext
      > => {
      return useMutation(getWritePlatformAgentVoicePolicyMutationOptions(options));
    }

export const getGetPlatformAgentVoicePolicySaveUrl = (requestId: string,) => {




  return `/api/platform/agent-voice-policy/requests/${requestId}`
}

/**
 * @summary Read the original actor-owned voice policy save outcome
 */
export const getPlatformAgentVoicePolicySave = async (requestId: string, options?: Parameters<typeof customFetch>[1]): Promise<GetPlatformAgentVoicePolicySave200> => {

  return customFetch<GetPlatformAgentVoicePolicySave200>(getGetPlatformAgentVoicePolicySaveUrl(requestId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPlatformAgentVoicePolicySaveQueryKey = (requestId: string,) => {
    return [
    `/api/platform/agent-voice-policy/requests/${requestId}`
    ] as const;
    }


export const getGetPlatformAgentVoicePolicySaveQueryOptions = <TData = Awaited<ReturnType<typeof getPlatformAgentVoicePolicySave>>, TError = void>(requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPlatformAgentVoicePolicySave>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPlatformAgentVoicePolicySaveQueryKey(requestId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPlatformAgentVoicePolicySave>>> = ({ signal }) => getPlatformAgentVoicePolicySave(requestId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: requestId !== null && requestId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPlatformAgentVoicePolicySave>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPlatformAgentVoicePolicySaveQueryResult = NonNullable<Awaited<ReturnType<typeof getPlatformAgentVoicePolicySave>>>
export type GetPlatformAgentVoicePolicySaveQueryError = void


/**
 * @summary Read the original actor-owned voice policy save outcome
 */

export function useGetPlatformAgentVoicePolicySave<TData = Awaited<ReturnType<typeof getPlatformAgentVoicePolicySave>>, TError = void>(
 requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPlatformAgentVoicePolicySave>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPlatformAgentVoicePolicySaveQueryOptions(requestId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetAssistantModelChoicesUrl = () => {




  return `/api/agent/assistant/models`
}

/**
 * The server chooses the model. Only models served by the saved Project AI connection can be allowed, and a personal choice is honoured only while the platform policy permits it.
 * @summary Read the assistant models this person may use
 */
export const getAssistantModelChoices = async ( options?: Parameters<typeof customFetch>[1]): Promise<AssistantModelChoices> => {

  return customFetch<AssistantModelChoices>(getGetAssistantModelChoicesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAssistantModelChoicesQueryKey = () => {
    return [
    `/api/agent/assistant/models`
    ] as const;
    }


export const getGetAssistantModelChoicesQueryOptions = <TData = Awaited<ReturnType<typeof getAssistantModelChoices>>, TError = void>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAssistantModelChoices>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAssistantModelChoicesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAssistantModelChoices>>> = ({ signal }) => getAssistantModelChoices({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAssistantModelChoices>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAssistantModelChoicesQueryResult = NonNullable<Awaited<ReturnType<typeof getAssistantModelChoices>>>
export type GetAssistantModelChoicesQueryError = void


/**
 * @summary Read the assistant models this person may use
 */

export function useGetAssistantModelChoices<TData = Awaited<ReturnType<typeof getAssistantModelChoices>>, TError = void>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAssistantModelChoices>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAssistantModelChoicesQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSetAssistantModelPreferenceUrl = () => {




  return `/api/agent/assistant/model-preference`
}

/**
 * Rejected unless the platform policy allows personal choice and the model is allowed.
 * @summary Save or clear this person's preferred assistant model
 */
export const setAssistantModelPreference = async (assistantModelPreferenceWrite: AssistantModelPreferenceWrite, options?: Parameters<typeof customFetch>[1]): Promise<AssistantModelChoices> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AssistantModelChoices>(getSetAssistantModelPreferenceUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(assistantModelPreferenceWrite)
  }
);}





export const getSetAssistantModelPreferenceMutationKey = () => ['setAssistantModelPreference'] as const;

export const getSetAssistantModelPreferenceMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setAssistantModelPreference>>, TError,SetAssistantModelPreferenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setAssistantModelPreference>>, TError,SetAssistantModelPreferenceMutationVariables, TContext> => {

const mutationKey = getSetAssistantModelPreferenceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setAssistantModelPreference>>, SetAssistantModelPreferenceMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  setAssistantModelPreference(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetAssistantModelPreferenceMutationResult = NonNullable<Awaited<ReturnType<typeof setAssistantModelPreference>>>
    export type SetAssistantModelPreferenceMutationBody = AssistantModelPreferenceWrite
    export type SetAssistantModelPreferenceMutationError = void
    export type SetAssistantModelPreferenceMutationVariables = {data: AssistantModelPreferenceWrite}

    /**
 * @summary Save or clear this person's preferred assistant model
 */
export const useSetAssistantModelPreference = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setAssistantModelPreference>>, TError,SetAssistantModelPreferenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof setAssistantModelPreference>>,
        TError,
        SetAssistantModelPreferenceMutationVariables,
        TContext
      > => {
      return useMutation(getSetAssistantModelPreferenceMutationOptions(options));
    }

export const getGetMyAssistantUsageUrl = () => {




  return `/api/agent/assistant/usage`
}

/**
 * @summary Read this person's assistant usage today (no per-person limits apply)
 */
export const getMyAssistantUsage = async ( options?: Parameters<typeof customFetch>[1]): Promise<AssistantMyUsage> => {

  return customFetch<AssistantMyUsage>(getGetMyAssistantUsageUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMyAssistantUsageQueryKey = () => {
    return [
    `/api/agent/assistant/usage`
    ] as const;
    }


export const getGetMyAssistantUsageQueryOptions = <TData = Awaited<ReturnType<typeof getMyAssistantUsage>>, TError = void>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMyAssistantUsage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMyAssistantUsageQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMyAssistantUsage>>> = ({ signal }) => getMyAssistantUsage({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMyAssistantUsage>>, TError, TData> & { queryKey: QueryKey }
}

export type GetMyAssistantUsageQueryResult = NonNullable<Awaited<ReturnType<typeof getMyAssistantUsage>>>
export type GetMyAssistantUsageQueryError = void


/**
 * @summary Read this person's assistant usage today (no per-person limits apply)
 */

export function useGetMyAssistantUsage<TData = Awaited<ReturnType<typeof getMyAssistantUsage>>, TError = void>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMyAssistantUsage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetMyAssistantUsageQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListMyAiActivityUrl = (params?: ListMyAiActivityParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/assistant/activity?${stringifiedParams}` : `/api/agent/assistant/activity`
}

/**
 * @summary List this person's own AI activity (no message content)
 */
export const listMyAiActivity = async (params?: ListMyAiActivityParams, options?: Parameters<typeof customFetch>[1]): Promise<AiActivityList> => {

  return customFetch<AiActivityList>(getListMyAiActivityUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMyAiActivityQueryKey = (params?: ListMyAiActivityParams,) => {
    return [
    `/api/agent/assistant/activity`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMyAiActivityQueryOptions = <TData = Awaited<ReturnType<typeof listMyAiActivity>>, TError = void>(params?: ListMyAiActivityParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMyAiActivity>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMyAiActivityQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyAiActivity>>> = ({ signal }) => listMyAiActivity(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMyAiActivity>>, TError, TData> & { queryKey: QueryKey }
}

export type ListMyAiActivityQueryResult = NonNullable<Awaited<ReturnType<typeof listMyAiActivity>>>
export type ListMyAiActivityQueryError = void


/**
 * @summary List this person's own AI activity (no message content)
 */

export function useListMyAiActivity<TData = Awaited<ReturnType<typeof listMyAiActivity>>, TError = void>(
 params?: ListMyAiActivityParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMyAiActivity>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListMyAiActivityQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListAssistantImageDraftsUrl = (params?: ListAssistantImageDraftsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/assistant/image-drafts?${stringifiedParams}` : `/api/agent/assistant/image-drafts`
}

/**
 * @summary List this person's private image drafts
 */
export const listAssistantImageDrafts = async (params?: ListAssistantImageDraftsParams, options?: Parameters<typeof customFetch>[1]): Promise<AssistantImageDraftList> => {

  return customFetch<AssistantImageDraftList>(getListAssistantImageDraftsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAssistantImageDraftsQueryKey = (params?: ListAssistantImageDraftsParams,) => {
    return [
    `/api/agent/assistant/image-drafts`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAssistantImageDraftsQueryOptions = <TData = Awaited<ReturnType<typeof listAssistantImageDrafts>>, TError = void>(params?: ListAssistantImageDraftsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAssistantImageDrafts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAssistantImageDraftsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAssistantImageDrafts>>> = ({ signal }) => listAssistantImageDrafts(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAssistantImageDrafts>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAssistantImageDraftsQueryResult = NonNullable<Awaited<ReturnType<typeof listAssistantImageDrafts>>>
export type ListAssistantImageDraftsQueryError = void


/**
 * @summary List this person's private image drafts
 */

export function useListAssistantImageDrafts<TData = Awaited<ReturnType<typeof listAssistantImageDrafts>>, TError = void>(
 params?: ListAssistantImageDraftsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAssistantImageDrafts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAssistantImageDraftsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateAssistantImageDraftUrl = () => {




  return `/api/agent/assistant/image-drafts`
}

/**
 * @summary Generate a private image draft with the saved Project AI image connection
 */
export const createAssistantImageDraft = async (assistantImageDraftCreate: AssistantImageDraftCreate, options?: Parameters<typeof customFetch>[1]): Promise<AssistantImageDraft> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AssistantImageDraft>(getCreateAssistantImageDraftUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(assistantImageDraftCreate)
  }
);}





export const getCreateAssistantImageDraftMutationKey = () => ['createAssistantImageDraft'] as const;

export const getCreateAssistantImageDraftMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAssistantImageDraft>>, TError,CreateAssistantImageDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createAssistantImageDraft>>, TError,CreateAssistantImageDraftMutationVariables, TContext> => {

const mutationKey = getCreateAssistantImageDraftMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAssistantImageDraft>>, CreateAssistantImageDraftMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createAssistantImageDraft(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateAssistantImageDraftMutationResult = NonNullable<Awaited<ReturnType<typeof createAssistantImageDraft>>>
    export type CreateAssistantImageDraftMutationBody = AssistantImageDraftCreate
    export type CreateAssistantImageDraftMutationError = void
    export type CreateAssistantImageDraftMutationVariables = {data: AssistantImageDraftCreate}

    /**
 * @summary Generate a private image draft with the saved Project AI image connection
 */
export const useCreateAssistantImageDraft = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAssistantImageDraft>>, TError,CreateAssistantImageDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createAssistantImageDraft>>,
        TError,
        CreateAssistantImageDraftMutationVariables,
        TContext
      > => {
      return useMutation(getCreateAssistantImageDraftMutationOptions(options));
    }

export const getGetAssistantImageDraftContentUrl = (draftId: string,) => {




  return `/api/agent/assistant/image-drafts/${draftId}/content`
}

/**
 * @summary Read the image bytes of one of this person's drafts
 */
export const getAssistantImageDraftContent = async (draftId: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getGetAssistantImageDraftContentUrl(draftId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAssistantImageDraftContentQueryKey = (draftId: string,) => {
    return [
    `/api/agent/assistant/image-drafts/${draftId}/content`
    ] as const;
    }


export const getGetAssistantImageDraftContentQueryOptions = <TData = Awaited<ReturnType<typeof getAssistantImageDraftContent>>, TError = void>(draftId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAssistantImageDraftContent>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAssistantImageDraftContentQueryKey(draftId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAssistantImageDraftContent>>> = ({ signal }) => getAssistantImageDraftContent(draftId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: draftId !== null && draftId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAssistantImageDraftContent>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAssistantImageDraftContentQueryResult = NonNullable<Awaited<ReturnType<typeof getAssistantImageDraftContent>>>
export type GetAssistantImageDraftContentQueryError = void


/**
 * @summary Read the image bytes of one of this person's drafts
 */

export function useGetAssistantImageDraftContent<TData = Awaited<ReturnType<typeof getAssistantImageDraftContent>>, TError = void>(
 draftId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAssistantImageDraftContent>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAssistantImageDraftContentQueryOptions(draftId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDeleteAssistantImageDraftUrl = (draftId: string,) => {




  return `/api/agent/assistant/image-drafts/${draftId}`
}

/**
 * @summary Discard one of this person's image drafts
 */
export const deleteAssistantImageDraft = async (draftId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteAssistantImageDraftUrl(draftId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteAssistantImageDraftMutationKey = () => ['deleteAssistantImageDraft'] as const;

export const getDeleteAssistantImageDraftMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAssistantImageDraft>>, TError,DeleteAssistantImageDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteAssistantImageDraft>>, TError,DeleteAssistantImageDraftMutationVariables, TContext> => {

const mutationKey = getDeleteAssistantImageDraftMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteAssistantImageDraft>>, DeleteAssistantImageDraftMutationVariables> = (props) => {
          const {draftId} = props ?? {};

          return  deleteAssistantImageDraft(draftId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteAssistantImageDraftMutationResult = NonNullable<Awaited<ReturnType<typeof deleteAssistantImageDraft>>>

    export type DeleteAssistantImageDraftMutationError = void
    export type DeleteAssistantImageDraftMutationVariables = {draftId: string}

    /**
 * @summary Discard one of this person's image drafts
 */
export const useDeleteAssistantImageDraft = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAssistantImageDraft>>, TError,DeleteAssistantImageDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteAssistantImageDraft>>,
        TError,
        DeleteAssistantImageDraftMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteAssistantImageDraftMutationOptions(options));
    }

export const getSearchAssistantSourcesUrl = (params: SearchAssistantSourcesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/assistant/search?${stringifiedParams}` : `/api/assistant/search`
}

/**
 * Records come from the active entity's permission-filtered search; knowledge passages only from ready sources the caller may read. Every result carries its source.
 * @summary Search CRM records and approved knowledge the person may read, with sources
 */
export const searchAssistantSources = async (params: SearchAssistantSourcesParams, options?: Parameters<typeof customFetch>[1]): Promise<AssistantSearchResult> => {

  return customFetch<AssistantSearchResult>(getSearchAssistantSourcesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getSearchAssistantSourcesQueryKey = (params?: SearchAssistantSourcesParams,) => {
    return [
    `/api/assistant/search`, ...(params ? [params] : [])
    ] as const;
    }


export const getSearchAssistantSourcesQueryOptions = <TData = Awaited<ReturnType<typeof searchAssistantSources>>, TError = void>(params: SearchAssistantSourcesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof searchAssistantSources>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getSearchAssistantSourcesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof searchAssistantSources>>> = ({ signal }) => searchAssistantSources(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof searchAssistantSources>>, TError, TData> & { queryKey: QueryKey }
}

export type SearchAssistantSourcesQueryResult = NonNullable<Awaited<ReturnType<typeof searchAssistantSources>>>
export type SearchAssistantSourcesQueryError = void


/**
 * @summary Search CRM records and approved knowledge the person may read, with sources
 */

export function useSearchAssistantSources<TData = Awaited<ReturnType<typeof searchAssistantSources>>, TError = void>(
 params: SearchAssistantSourcesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof searchAssistantSources>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getSearchAssistantSourcesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPlatformAssistantModelPolicyUrl = () => {




  return `/api/platform/assistant/model-policy`
}

/**
 * Requires platform.features.manage. Revision 0 means the saved Project AI model is the only choice.
 * @summary Read the platform assistant model policy
 */
export const getPlatformAssistantModelPolicy = async ( options?: Parameters<typeof customFetch>[1]): Promise<PlatformAssistantModelPolicy> => {

  return customFetch<PlatformAssistantModelPolicy>(getGetPlatformAssistantModelPolicyUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPlatformAssistantModelPolicyQueryKey = () => {
    return [
    `/api/platform/assistant/model-policy`
    ] as const;
    }


export const getGetPlatformAssistantModelPolicyQueryOptions = <TData = Awaited<ReturnType<typeof getPlatformAssistantModelPolicy>>, TError = void>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPlatformAssistantModelPolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPlatformAssistantModelPolicyQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPlatformAssistantModelPolicy>>> = ({ signal }) => getPlatformAssistantModelPolicy({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPlatformAssistantModelPolicy>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPlatformAssistantModelPolicyQueryResult = NonNullable<Awaited<ReturnType<typeof getPlatformAssistantModelPolicy>>>
export type GetPlatformAssistantModelPolicyQueryError = void


/**
 * @summary Read the platform assistant model policy
 */

export function useGetPlatformAssistantModelPolicy<TData = Awaited<ReturnType<typeof getPlatformAssistantModelPolicy>>, TError = void>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPlatformAssistantModelPolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPlatformAssistantModelPolicyQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getWritePlatformAssistantModelPolicyUrl = () => {




  return `/api/platform/assistant/model-policy`
}

/**
 * Requires platform.features.manage and the exact current revision. Allowed models must be served by the saved Project AI connection; the connection itself is never changed.
 * @summary Save an audited assistant model policy revision
 */
export const writePlatformAssistantModelPolicy = async (platformAssistantModelPolicyWrite: PlatformAssistantModelPolicyWrite, options?: Parameters<typeof customFetch>[1]): Promise<PlatformAssistantModelPolicy> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PlatformAssistantModelPolicy>(getWritePlatformAssistantModelPolicyUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(platformAssistantModelPolicyWrite)
  }
);}





export const getWritePlatformAssistantModelPolicyMutationKey = () => ['writePlatformAssistantModelPolicy'] as const;

export const getWritePlatformAssistantModelPolicyMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writePlatformAssistantModelPolicy>>, TError,WritePlatformAssistantModelPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof writePlatformAssistantModelPolicy>>, TError,WritePlatformAssistantModelPolicyMutationVariables, TContext> => {

const mutationKey = getWritePlatformAssistantModelPolicyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof writePlatformAssistantModelPolicy>>, WritePlatformAssistantModelPolicyMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  writePlatformAssistantModelPolicy(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type WritePlatformAssistantModelPolicyMutationResult = NonNullable<Awaited<ReturnType<typeof writePlatformAssistantModelPolicy>>>
    export type WritePlatformAssistantModelPolicyMutationBody = PlatformAssistantModelPolicyWrite
    export type WritePlatformAssistantModelPolicyMutationError = void
    export type WritePlatformAssistantModelPolicyMutationVariables = {data: PlatformAssistantModelPolicyWrite}

    /**
 * @summary Save an audited assistant model policy revision
 */
export const useWritePlatformAssistantModelPolicy = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writePlatformAssistantModelPolicy>>, TError,WritePlatformAssistantModelPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof writePlatformAssistantModelPolicy>>,
        TError,
        WritePlatformAssistantModelPolicyMutationVariables,
        TContext
      > => {
      return useMutation(getWritePlatformAssistantModelPolicyMutationOptions(options));
    }

export const getGetPlatformAssistantUsagePolicyUrl = () => {




  return `/api/platform/assistant/usage-policy`
}

/**
 * Requires platform.features.manage. Saved revisions are inert history; signed-in people have no per-person usage limits (see getPlatformAiLimits).
 * @summary Read the retired per-person daily assistant usage limits record
 */
export const getPlatformAssistantUsagePolicy = async ( options?: Parameters<typeof customFetch>[1]): Promise<PlatformAssistantUsagePolicy> => {

  return customFetch<PlatformAssistantUsagePolicy>(getGetPlatformAssistantUsagePolicyUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPlatformAssistantUsagePolicyQueryKey = () => {
    return [
    `/api/platform/assistant/usage-policy`
    ] as const;
    }


export const getGetPlatformAssistantUsagePolicyQueryOptions = <TData = Awaited<ReturnType<typeof getPlatformAssistantUsagePolicy>>, TError = void>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPlatformAssistantUsagePolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPlatformAssistantUsagePolicyQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPlatformAssistantUsagePolicy>>> = ({ signal }) => getPlatformAssistantUsagePolicy({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPlatformAssistantUsagePolicy>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPlatformAssistantUsagePolicyQueryResult = NonNullable<Awaited<ReturnType<typeof getPlatformAssistantUsagePolicy>>>
export type GetPlatformAssistantUsagePolicyQueryError = void


/**
 * @summary Read the retired per-person daily assistant usage limits record
 */

export function useGetPlatformAssistantUsagePolicy<TData = Awaited<ReturnType<typeof getPlatformAssistantUsagePolicy>>, TError = void>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPlatformAssistantUsagePolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPlatformAssistantUsagePolicyQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getWritePlatformAssistantUsagePolicyUrl = () => {




  return `/api/platform/assistant/usage-policy`
}

/**
 * Requires platform.features.manage. Per-person usage limits were removed, so nothing is saved and 410 AI_LIMIT_REMOVED is returned. Existing revisions are kept unchanged.
 * @deprecated
 * @summary Retired per-person daily assistant usage limits writer
 */
export const writePlatformAssistantUsagePolicy = async (platformAssistantUsagePolicyWrite: PlatformAssistantUsagePolicyWrite, options?: Parameters<typeof customFetch>[1]): Promise<PlatformAssistantUsagePolicy> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PlatformAssistantUsagePolicy>(getWritePlatformAssistantUsagePolicyUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(platformAssistantUsagePolicyWrite)
  }
);}





export const getWritePlatformAssistantUsagePolicyMutationKey = () => ['writePlatformAssistantUsagePolicy'] as const;

export const getWritePlatformAssistantUsagePolicyMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writePlatformAssistantUsagePolicy>>, TError,WritePlatformAssistantUsagePolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof writePlatformAssistantUsagePolicy>>, TError,WritePlatformAssistantUsagePolicyMutationVariables, TContext> => {

const mutationKey = getWritePlatformAssistantUsagePolicyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof writePlatformAssistantUsagePolicy>>, WritePlatformAssistantUsagePolicyMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  writePlatformAssistantUsagePolicy(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type WritePlatformAssistantUsagePolicyMutationResult = NonNullable<Awaited<ReturnType<typeof writePlatformAssistantUsagePolicy>>>
    export type WritePlatformAssistantUsagePolicyMutationBody = PlatformAssistantUsagePolicyWrite
    export type WritePlatformAssistantUsagePolicyMutationError = void
    export type WritePlatformAssistantUsagePolicyMutationVariables = {data: PlatformAssistantUsagePolicyWrite}

    /**
 * @deprecated
 * @summary Retired per-person daily assistant usage limits writer
 */
export const useWritePlatformAssistantUsagePolicy = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writePlatformAssistantUsagePolicy>>, TError,WritePlatformAssistantUsagePolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof writePlatformAssistantUsagePolicy>>,
        TError,
        WritePlatformAssistantUsagePolicyMutationVariables,
        TContext
      > => {
      return useMutation(getWritePlatformAssistantUsagePolicyMutationOptions(options));
    }

export const getListPlatformAssistantUsageUrl = (params?: ListPlatformAssistantUsageParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/assistant/usage?${stringifiedParams}` : `/api/platform/assistant/usage`
}

/**
 * Requires platform.features.manage.
 * @summary List per-person assistant usage for one UTC day
 */
export const listPlatformAssistantUsage = async (params?: ListPlatformAssistantUsageParams, options?: Parameters<typeof customFetch>[1]): Promise<PlatformAssistantUsageList> => {

  return customFetch<PlatformAssistantUsageList>(getListPlatformAssistantUsageUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformAssistantUsageQueryKey = (params?: ListPlatformAssistantUsageParams,) => {
    return [
    `/api/platform/assistant/usage`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformAssistantUsageQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformAssistantUsage>>, TError = void>(params?: ListPlatformAssistantUsageParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformAssistantUsage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformAssistantUsageQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformAssistantUsage>>> = ({ signal }) => listPlatformAssistantUsage(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformAssistantUsage>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformAssistantUsageQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformAssistantUsage>>>
export type ListPlatformAssistantUsageQueryError = void


/**
 * @summary List per-person assistant usage for one UTC day
 */

export function useListPlatformAssistantUsage<TData = Awaited<ReturnType<typeof listPlatformAssistantUsage>>, TError = void>(
 params?: ListPlatformAssistantUsageParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformAssistantUsage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformAssistantUsageQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListPlatformAiActivityUrl = (params?: ListPlatformAiActivityParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/ai-activity?${stringifiedParams}` : `/api/platform/ai-activity`
}

/**
 * Requires platform.audit.read. Events hold identifiers, counts and codes only, never prompts, replies or file names.
 * @summary List the content-free AI activity log
 */
export const listPlatformAiActivity = async (params?: ListPlatformAiActivityParams, options?: Parameters<typeof customFetch>[1]): Promise<AiActivityList> => {

  return customFetch<AiActivityList>(getListPlatformAiActivityUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformAiActivityQueryKey = (params?: ListPlatformAiActivityParams,) => {
    return [
    `/api/platform/ai-activity`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformAiActivityQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformAiActivity>>, TError = void>(params?: ListPlatformAiActivityParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformAiActivity>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformAiActivityQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformAiActivity>>> = ({ signal }) => listPlatformAiActivity(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformAiActivity>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformAiActivityQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformAiActivity>>>
export type ListPlatformAiActivityQueryError = void


/**
 * @summary List the content-free AI activity log
 */

export function useListPlatformAiActivity<TData = Awaited<ReturnType<typeof listPlatformAiActivity>>, TError = void>(
 params?: ListPlatformAiActivityParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformAiActivity>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformAiActivityQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPlatformAiLimitsUrl = () => {




  return `/api/platform/ai-limits`
}

/**
 * Requires platform.features.manage. Lists every app-made AI limit from the single registry. Signed-in limits are permanently removed (the AI token budget is the only usage stop); public visitor protections stay enforced from the server environment or the built-in default. Retired administrator values are shown as inert history.
 * @summary Read the consolidated AI limits policy
 */
export const getPlatformAiLimits = async ( options?: Parameters<typeof customFetch>[1]): Promise<PlatformAiLimits> => {

  return customFetch<PlatformAiLimits>(getGetPlatformAiLimitsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPlatformAiLimitsQueryKey = () => {
    return [
    `/api/platform/ai-limits`
    ] as const;
    }


export const getGetPlatformAiLimitsQueryOptions = <TData = Awaited<ReturnType<typeof getPlatformAiLimits>>, TError = void>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPlatformAiLimits>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPlatformAiLimitsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPlatformAiLimits>>> = ({ signal }) => getPlatformAiLimits({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPlatformAiLimits>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPlatformAiLimitsQueryResult = NonNullable<Awaited<ReturnType<typeof getPlatformAiLimits>>>
export type GetPlatformAiLimitsQueryError = void


/**
 * @summary Read the consolidated AI limits policy
 */

export function useGetPlatformAiLimits<TData = Awaited<ReturnType<typeof getPlatformAiLimits>>, TError = void>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPlatformAiLimits>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPlatformAiLimitsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPlatformAgentLimitsUrl = () => {




  return `/api/platform/agent-limits`
}

/**
 * Requires platform.features.manage. Saved revisions are inert history; signed-in Agent turns have no app-made limits (see getPlatformAiLimits).
 * @summary Read the retired Agent working limits record
 */
export const getPlatformAgentLimits = async ( options?: Parameters<typeof customFetch>[1]): Promise<PlatformAgentLimits> => {

  return customFetch<PlatformAgentLimits>(getGetPlatformAgentLimitsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPlatformAgentLimitsQueryKey = () => {
    return [
    `/api/platform/agent-limits`
    ] as const;
    }


export const getGetPlatformAgentLimitsQueryOptions = <TData = Awaited<ReturnType<typeof getPlatformAgentLimits>>, TError = void>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPlatformAgentLimits>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPlatformAgentLimitsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPlatformAgentLimits>>> = ({ signal }) => getPlatformAgentLimits({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPlatformAgentLimits>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPlatformAgentLimitsQueryResult = NonNullable<Awaited<ReturnType<typeof getPlatformAgentLimits>>>
export type GetPlatformAgentLimitsQueryError = void


/**
 * @summary Read the retired Agent working limits record
 */

export function useGetPlatformAgentLimits<TData = Awaited<ReturnType<typeof getPlatformAgentLimits>>, TError = void>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPlatformAgentLimits>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPlatformAgentLimitsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getWritePlatformAgentLimitsUrl = () => {




  return `/api/platform/agent-limits`
}

/**
 * Requires platform.features.manage. Agent working limits were removed for signed-in people, so nothing is saved and 410 AI_LIMIT_REMOVED is returned. Existing revisions are kept unchanged.
 * @deprecated
 * @summary Retired Agent working limits writer
 */
export const writePlatformAgentLimits = async (platformAgentLimitsWrite: PlatformAgentLimitsWrite, options?: Parameters<typeof customFetch>[1]): Promise<PlatformAgentLimits> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PlatformAgentLimits>(getWritePlatformAgentLimitsUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(platformAgentLimitsWrite)
  }
);}





export const getWritePlatformAgentLimitsMutationKey = () => ['writePlatformAgentLimits'] as const;

export const getWritePlatformAgentLimitsMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writePlatformAgentLimits>>, TError,WritePlatformAgentLimitsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof writePlatformAgentLimits>>, TError,WritePlatformAgentLimitsMutationVariables, TContext> => {

const mutationKey = getWritePlatformAgentLimitsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof writePlatformAgentLimits>>, WritePlatformAgentLimitsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  writePlatformAgentLimits(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type WritePlatformAgentLimitsMutationResult = NonNullable<Awaited<ReturnType<typeof writePlatformAgentLimits>>>
    export type WritePlatformAgentLimitsMutationBody = PlatformAgentLimitsWrite
    export type WritePlatformAgentLimitsMutationError = void
    export type WritePlatformAgentLimitsMutationVariables = {data: PlatformAgentLimitsWrite}

    /**
 * @deprecated
 * @summary Retired Agent working limits writer
 */
export const useWritePlatformAgentLimits = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writePlatformAgentLimits>>, TError,WritePlatformAgentLimitsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof writePlatformAgentLimits>>,
        TError,
        WritePlatformAgentLimitsMutationVariables,
        TContext
      > => {
      return useMutation(getWritePlatformAgentLimitsMutationOptions(options));
    }

export const getGetPlatformAgentLimitsSaveUrl = (requestId: string,) => {




  return `/api/platform/agent-limits/requests/${requestId}`
}

/**
 * @summary Read the original actor-owned Agent limits save outcome
 */
export const getPlatformAgentLimitsSave = async (requestId: string, options?: Parameters<typeof customFetch>[1]): Promise<GetPlatformAgentLimitsSave200> => {

  return customFetch<GetPlatformAgentLimitsSave200>(getGetPlatformAgentLimitsSaveUrl(requestId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPlatformAgentLimitsSaveQueryKey = (requestId: string,) => {
    return [
    `/api/platform/agent-limits/requests/${requestId}`
    ] as const;
    }


export const getGetPlatformAgentLimitsSaveQueryOptions = <TData = Awaited<ReturnType<typeof getPlatformAgentLimitsSave>>, TError = void>(requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPlatformAgentLimitsSave>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPlatformAgentLimitsSaveQueryKey(requestId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPlatformAgentLimitsSave>>> = ({ signal }) => getPlatformAgentLimitsSave(requestId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: requestId !== null && requestId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPlatformAgentLimitsSave>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPlatformAgentLimitsSaveQueryResult = NonNullable<Awaited<ReturnType<typeof getPlatformAgentLimitsSave>>>
export type GetPlatformAgentLimitsSaveQueryError = void


/**
 * @summary Read the original actor-owned Agent limits save outcome
 */

export function useGetPlatformAgentLimitsSave<TData = Awaited<ReturnType<typeof getPlatformAgentLimitsSave>>, TError = void>(
 requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPlatformAgentLimitsSave>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPlatformAgentLimitsSaveQueryOptions(requestId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListPlatformFeaturesUrl = (params?: ListPlatformFeaturesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/features?${stringifiedParams}` : `/api/platform/features`
}

/**
 * Requires platform.features.manage.
 * @summary List platform-owned module definitions
 */
export const listPlatformFeatures = async (params?: ListPlatformFeaturesParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminFeaturePage> => {

  return customFetch<AdminFeaturePage>(getListPlatformFeaturesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformFeaturesQueryKey = (params?: ListPlatformFeaturesParams,) => {
    return [
    `/api/platform/features`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformFeaturesQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformFeatures>>, TError = void>(params?: ListPlatformFeaturesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformFeatures>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformFeaturesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformFeatures>>> = ({ signal }) => listPlatformFeatures(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformFeatures>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformFeaturesQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformFeatures>>>
export type ListPlatformFeaturesQueryError = void


/**
 * @summary List platform-owned module definitions
 */

export function useListPlatformFeatures<TData = Awaited<ReturnType<typeof listPlatformFeatures>>, TError = void>(
 params?: ListPlatformFeaturesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformFeatures>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformFeaturesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdatePlatformFeatureUrl = (key: string,) => {




  return `/api/platform/features/${key}`
}

/**
 * Requires platform.features.manage.
 */
export const updatePlatformFeature = async (key: string,
    adminFeatureWrite: AdminFeatureWrite, options?: Parameters<typeof customFetch>[1]): Promise<AdminFeature> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminFeature>(getUpdatePlatformFeatureUrl(key),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminFeatureWrite)
  }
);}





export const getUpdatePlatformFeatureMutationKey = () => ['updatePlatformFeature'] as const;

export const getUpdatePlatformFeatureMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePlatformFeature>>, TError,UpdatePlatformFeatureMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updatePlatformFeature>>, TError,UpdatePlatformFeatureMutationVariables, TContext> => {

const mutationKey = getUpdatePlatformFeatureMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updatePlatformFeature>>, UpdatePlatformFeatureMutationVariables> = (props) => {
          const {key,data} = props ?? {};

          return  updatePlatformFeature(key,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdatePlatformFeatureMutationResult = NonNullable<Awaited<ReturnType<typeof updatePlatformFeature>>>
    export type UpdatePlatformFeatureMutationBody = AdminFeatureWrite
    export type UpdatePlatformFeatureMutationError = void
    export type UpdatePlatformFeatureMutationVariables = {key: string;data: AdminFeatureWrite}

    export const useUpdatePlatformFeature = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePlatformFeature>>, TError,UpdatePlatformFeatureMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updatePlatformFeature>>,
        TError,
        UpdatePlatformFeatureMutationVariables,
        TContext
      > => {
      return useMutation(getUpdatePlatformFeatureMutationOptions(options));
    }

export const getSetPlatformFeatureTenantOverrideUrl = (key: string,
    tenantId: string,) => {




  return `/api/platform/features/${key}/tenants/${tenantId}/override`
}

/**
 * Requires platform.features.manage.
 */
export const setPlatformFeatureTenantOverride = async (key: string,
    tenantId: string,
    adminFeatureTenantOverrideWrite: AdminFeatureTenantOverrideWrite, options?: Parameters<typeof customFetch>[1]): Promise<AdminFeatureTenantOverride> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminFeatureTenantOverride>(getSetPlatformFeatureTenantOverrideUrl(key,tenantId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminFeatureTenantOverrideWrite)
  }
);}





export const getSetPlatformFeatureTenantOverrideMutationKey = () => ['setPlatformFeatureTenantOverride'] as const;

export const getSetPlatformFeatureTenantOverrideMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setPlatformFeatureTenantOverride>>, TError,SetPlatformFeatureTenantOverrideMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setPlatformFeatureTenantOverride>>, TError,SetPlatformFeatureTenantOverrideMutationVariables, TContext> => {

const mutationKey = getSetPlatformFeatureTenantOverrideMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setPlatformFeatureTenantOverride>>, SetPlatformFeatureTenantOverrideMutationVariables> = (props) => {
          const {key,tenantId,data} = props ?? {};

          return  setPlatformFeatureTenantOverride(key,tenantId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetPlatformFeatureTenantOverrideMutationResult = NonNullable<Awaited<ReturnType<typeof setPlatformFeatureTenantOverride>>>
    export type SetPlatformFeatureTenantOverrideMutationBody = AdminFeatureTenantOverrideWrite
    export type SetPlatformFeatureTenantOverrideMutationError = void
    export type SetPlatformFeatureTenantOverrideMutationVariables = {key: string;tenantId: string;data: AdminFeatureTenantOverrideWrite}

    export const useSetPlatformFeatureTenantOverride = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setPlatformFeatureTenantOverride>>, TError,SetPlatformFeatureTenantOverrideMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof setPlatformFeatureTenantOverride>>,
        TError,
        SetPlatformFeatureTenantOverrideMutationVariables,
        TContext
      > => {
      return useMutation(getSetPlatformFeatureTenantOverrideMutationOptions(options));
    }

export const getListPlatformUsersUrl = (params?: ListPlatformUsersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/users?${stringifiedParams}` : `/api/platform/users`
}

/**
 * Requires platform.users.manage.
 * @summary List synced application users for platform administration
 */
export const listPlatformUsers = async (params?: ListPlatformUsersParams, options?: Parameters<typeof customFetch>[1]): Promise<PlatformUserPage> => {

  return customFetch<PlatformUserPage>(getListPlatformUsersUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformUsersQueryKey = (params?: ListPlatformUsersParams,) => {
    return [
    `/api/platform/users`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformUsersQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformUsers>>, TError = Error>(params?: ListPlatformUsersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformUsers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformUsersQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformUsers>>> = ({ signal }) => listPlatformUsers(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformUsers>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformUsersQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformUsers>>>
export type ListPlatformUsersQueryError = Error


/**
 * @summary List synced application users for platform administration
 */

export function useListPlatformUsers<TData = Awaited<ReturnType<typeof listPlatformUsers>>, TError = Error>(
 params?: ListPlatformUsersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformUsers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformUsersQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getAddPlatformUserUrl = () => {




  return `/api/platform/users`
}

/**
 * Requires platform.users.manage and platform.roles.manage. The server binds the user to the configured Workforce tenant.
 * @summary Add an Entra directory user and assign platform roles
 */
export const addPlatformUser = async (addPlatformUserBody: AddPlatformUserBody, options?: Parameters<typeof customFetch>[1]): Promise<PlatformUser> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PlatformUser>(getAddPlatformUserUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(addPlatformUserBody)
  }
);}





export const getAddPlatformUserMutationKey = () => ['addPlatformUser'] as const;

export const getAddPlatformUserMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addPlatformUser>>, TError,AddPlatformUserMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof addPlatformUser>>, TError,AddPlatformUserMutationVariables, TContext> => {

const mutationKey = getAddPlatformUserMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof addPlatformUser>>, AddPlatformUserMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  addPlatformUser(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AddPlatformUserMutationResult = NonNullable<Awaited<ReturnType<typeof addPlatformUser>>>
    export type AddPlatformUserMutationBody = AddPlatformUserBody
    export type AddPlatformUserMutationError = Error
    export type AddPlatformUserMutationVariables = {data: AddPlatformUserBody}

    /**
 * @summary Add an Entra directory user and assign platform roles
 */
export const useAddPlatformUser = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addPlatformUser>>, TError,AddPlatformUserMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof addPlatformUser>>,
        TError,
        AddPlatformUserMutationVariables,
        TContext
      > => {
      return useMutation(getAddPlatformUserMutationOptions(options));
    }

export const getSetAppUserRoleUrl = (id: string,) => {




  return `/api/admin/users/${id}/role`
}

/**
 * Requires platform.users.manage.
 * @summary Change the legacy application-wide role
 */
export const setAppUserRole = async (id: string,
    setUserRoleBody: SetUserRoleBody, options?: Parameters<typeof customFetch>[1]): Promise<AdminUser> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminUser>(getSetAppUserRoleUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(setUserRoleBody)
  }
);}





export const getSetAppUserRoleMutationKey = () => ['setAppUserRole'] as const;

export const getSetAppUserRoleMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setAppUserRole>>, TError,SetAppUserRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setAppUserRole>>, TError,SetAppUserRoleMutationVariables, TContext> => {

const mutationKey = getSetAppUserRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setAppUserRole>>, SetAppUserRoleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  setAppUserRole(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetAppUserRoleMutationResult = NonNullable<Awaited<ReturnType<typeof setAppUserRole>>>
    export type SetAppUserRoleMutationBody = SetUserRoleBody
    export type SetAppUserRoleMutationError = Error
    export type SetAppUserRoleMutationVariables = {id: string;data: SetUserRoleBody}

    /**
 * @summary Change the legacy application-wide role
 */
export const useSetAppUserRole = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setAppUserRole>>, TError,SetAppUserRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof setAppUserRole>>,
        TError,
        SetAppUserRoleMutationVariables,
        TContext
      > => {
      return useMutation(getSetAppUserRoleMutationOptions(options));
    }

export const getGetPlatformOptionsUrl = () => {




  return `/api/platform/options`
}

export const getPlatformOptions = async ( options?: Parameters<typeof customFetch>[1]): Promise<PlatformOptions> => {

  return customFetch<PlatformOptions>(getGetPlatformOptionsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPlatformOptionsQueryKey = () => {
    return [
    `/api/platform/options`
    ] as const;
    }


export const getGetPlatformOptionsQueryOptions = <TData = Awaited<ReturnType<typeof getPlatformOptions>>, TError = void>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPlatformOptions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPlatformOptionsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPlatformOptions>>> = ({ signal }) => getPlatformOptions({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPlatformOptions>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPlatformOptionsQueryResult = NonNullable<Awaited<ReturnType<typeof getPlatformOptions>>>
export type GetPlatformOptionsQueryError = void



export function useGetPlatformOptions<TData = Awaited<ReturnType<typeof getPlatformOptions>>, TError = void>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPlatformOptions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPlatformOptionsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListPlatformTenantEntitiesUrl = (id: string,
    params?: ListPlatformTenantEntitiesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/tenants/${id}/entities?${stringifiedParams}` : `/api/platform/tenants/${id}/entities`
}

export const listPlatformTenantEntities = async (id: string,
    params?: ListPlatformTenantEntitiesParams, options?: Parameters<typeof customFetch>[1]): Promise<PlatformEntityChoicePage> => {

  return customFetch<PlatformEntityChoicePage>(getListPlatformTenantEntitiesUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformTenantEntitiesQueryKey = (id: string,
    params?: ListPlatformTenantEntitiesParams,) => {
    return [
    `/api/platform/tenants/${id}/entities`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformTenantEntitiesQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformTenantEntities>>, TError = void>(id: string,
    params?: ListPlatformTenantEntitiesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformTenantEntities>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformTenantEntitiesQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformTenantEntities>>> = ({ signal }) => listPlatformTenantEntities(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformTenantEntities>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformTenantEntitiesQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformTenantEntities>>>
export type ListPlatformTenantEntitiesQueryError = void



export function useListPlatformTenantEntities<TData = Awaited<ReturnType<typeof listPlatformTenantEntities>>, TError = void>(
 id: string,
    params?: ListPlatformTenantEntitiesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformTenantEntities>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformTenantEntitiesQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListPlatformTenantsUrl = (params?: ListPlatformTenantsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/tenants?${stringifiedParams}` : `/api/platform/tenants`
}

/**
 * Requires platform.tenants.read. Status and search are applied before paging; totals describe the filtered directory.
 * @summary listPlatformTenants
 */
export const listPlatformTenants = async (params?: ListPlatformTenantsParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminTenantPage> => {

  return customFetch<AdminTenantPage>(getListPlatformTenantsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformTenantsQueryKey = (params?: ListPlatformTenantsParams,) => {
    return [
    `/api/platform/tenants`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformTenantsQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformTenants>>, TError = void>(params?: ListPlatformTenantsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformTenants>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformTenantsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformTenants>>> = ({ signal }) => listPlatformTenants(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformTenants>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformTenantsQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformTenants>>>
export type ListPlatformTenantsQueryError = void


/**
 * @summary listPlatformTenants
 */

export function useListPlatformTenants<TData = Awaited<ReturnType<typeof listPlatformTenants>>, TError = void>(
 params?: ListPlatformTenantsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformTenants>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformTenantsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getProvisionPlatformTenantUrl = () => {




  return `/api/platform/tenants`
}

/**
 * Requires platform.tenants.provision.
 * @summary provisionPlatformTenant
 */
export const provisionPlatformTenant = async (adminProvisionTenant: AdminProvisionTenant, options?: Parameters<typeof customFetch>[1]): Promise<AdminProvisionResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminProvisionResult>(getProvisionPlatformTenantUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminProvisionTenant)
  }
);}





export const getProvisionPlatformTenantMutationKey = () => ['provisionPlatformTenant'] as const;

export const getProvisionPlatformTenantMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof provisionPlatformTenant>>, TError,ProvisionPlatformTenantMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof provisionPlatformTenant>>, TError,ProvisionPlatformTenantMutationVariables, TContext> => {

const mutationKey = getProvisionPlatformTenantMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof provisionPlatformTenant>>, ProvisionPlatformTenantMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  provisionPlatformTenant(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ProvisionPlatformTenantMutationResult = NonNullable<Awaited<ReturnType<typeof provisionPlatformTenant>>>
    export type ProvisionPlatformTenantMutationBody = AdminProvisionTenant
    export type ProvisionPlatformTenantMutationError = void
    export type ProvisionPlatformTenantMutationVariables = {data: AdminProvisionTenant}

    /**
 * @summary provisionPlatformTenant
 */
export const useProvisionPlatformTenant = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof provisionPlatformTenant>>, TError,ProvisionPlatformTenantMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof provisionPlatformTenant>>,
        TError,
        ProvisionPlatformTenantMutationVariables,
        TContext
      > => {
      return useMutation(getProvisionPlatformTenantMutationOptions(options));
    }

export const getUpdatePlatformTenantUrl = (id: string,) => {




  return `/api/platform/tenants/${id}`
}

/**
 * Requires platform.tenants.manage.
 * @summary updatePlatformTenant
 */
export const updatePlatformTenant = async (id: string,
    adminTenantUpdate: AdminTenantUpdate, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getUpdatePlatformTenantUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminTenantUpdate)
  }
);}





export const getUpdatePlatformTenantMutationKey = () => ['updatePlatformTenant'] as const;

export const getUpdatePlatformTenantMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePlatformTenant>>, TError,UpdatePlatformTenantMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updatePlatformTenant>>, TError,UpdatePlatformTenantMutationVariables, TContext> => {

const mutationKey = getUpdatePlatformTenantMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updatePlatformTenant>>, UpdatePlatformTenantMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updatePlatformTenant(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdatePlatformTenantMutationResult = NonNullable<Awaited<ReturnType<typeof updatePlatformTenant>>>
    export type UpdatePlatformTenantMutationBody = AdminTenantUpdate
    export type UpdatePlatformTenantMutationError = void
    export type UpdatePlatformTenantMutationVariables = {id: string;data: AdminTenantUpdate}

    /**
 * @summary updatePlatformTenant
 */
export const useUpdatePlatformTenant = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePlatformTenant>>, TError,UpdatePlatformTenantMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updatePlatformTenant>>,
        TError,
        UpdatePlatformTenantMutationVariables,
        TContext
      > => {
      return useMutation(getUpdatePlatformTenantMutationOptions(options));
    }

export const getArchivePlatformTenantUrl = (id: string,) => {




  return `/api/platform/tenants/${id}/archive`
}

/**
 * Requires platform.tenants.manage, an exact tenant-name confirmation, and a completed full export from the last 24 hours. Archival disables access but retains tenant data.
 * @summary Archive a suspended tenant indefinitely
 */
export const archivePlatformTenant = async (id: string,
    adminTenantArchive: AdminTenantArchive, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getArchivePlatformTenantUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminTenantArchive)
  }
);}





export const getArchivePlatformTenantMutationKey = () => ['archivePlatformTenant'] as const;

export const getArchivePlatformTenantMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof archivePlatformTenant>>, TError,ArchivePlatformTenantMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof archivePlatformTenant>>, TError,ArchivePlatformTenantMutationVariables, TContext> => {

const mutationKey = getArchivePlatformTenantMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof archivePlatformTenant>>, ArchivePlatformTenantMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  archivePlatformTenant(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ArchivePlatformTenantMutationResult = NonNullable<Awaited<ReturnType<typeof archivePlatformTenant>>>
    export type ArchivePlatformTenantMutationBody = AdminTenantArchive
    export type ArchivePlatformTenantMutationError = void
    export type ArchivePlatformTenantMutationVariables = {id: string;data: AdminTenantArchive}

    /**
 * @summary Archive a suspended tenant indefinitely
 */
export const useArchivePlatformTenant = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof archivePlatformTenant>>, TError,ArchivePlatformTenantMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof archivePlatformTenant>>,
        TError,
        ArchivePlatformTenantMutationVariables,
        TContext
      > => {
      return useMutation(getArchivePlatformTenantMutationOptions(options));
    }

export const getResendPlatformOwnerInvitationUrl = (id: string,) => {




  return `/api/platform/tenants/${id}/invitation`
}

/**
 * Requires platform.tenants.provision.
 * @summary resendPlatformOwnerInvitation
 */
export const resendPlatformOwnerInvitation = async (id: string,
    adminReason: AdminReason, options?: Parameters<typeof customFetch>[1]): Promise<AdminProvisionResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminProvisionResult>(getResendPlatformOwnerInvitationUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminReason)
  }
);}





export const getResendPlatformOwnerInvitationMutationKey = () => ['resendPlatformOwnerInvitation'] as const;

export const getResendPlatformOwnerInvitationMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resendPlatformOwnerInvitation>>, TError,ResendPlatformOwnerInvitationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof resendPlatformOwnerInvitation>>, TError,ResendPlatformOwnerInvitationMutationVariables, TContext> => {

const mutationKey = getResendPlatformOwnerInvitationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof resendPlatformOwnerInvitation>>, ResendPlatformOwnerInvitationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  resendPlatformOwnerInvitation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResendPlatformOwnerInvitationMutationResult = NonNullable<Awaited<ReturnType<typeof resendPlatformOwnerInvitation>>>
    export type ResendPlatformOwnerInvitationMutationBody = AdminReason
    export type ResendPlatformOwnerInvitationMutationError = void
    export type ResendPlatformOwnerInvitationMutationVariables = {id: string;data: AdminReason}

    /**
 * @summary resendPlatformOwnerInvitation
 */
export const useResendPlatformOwnerInvitation = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resendPlatformOwnerInvitation>>, TError,ResendPlatformOwnerInvitationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof resendPlatformOwnerInvitation>>,
        TError,
        ResendPlatformOwnerInvitationMutationVariables,
        TContext
      > => {
      return useMutation(getResendPlatformOwnerInvitationMutationOptions(options));
    }

export const getGetPlatformTenantDetailsUrl = (id: string,) => {




  return `/api/platform/tenants/${id}/details`
}

/**
 * Requires platform.tenants.read.
 * @summary getPlatformTenantDetails
 */
export const getPlatformTenantDetails = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<AdminTenantDetails> => {

  return customFetch<AdminTenantDetails>(getGetPlatformTenantDetailsUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPlatformTenantDetailsQueryKey = (id: string,) => {
    return [
    `/api/platform/tenants/${id}/details`
    ] as const;
    }


export const getGetPlatformTenantDetailsQueryOptions = <TData = Awaited<ReturnType<typeof getPlatformTenantDetails>>, TError = void>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPlatformTenantDetails>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPlatformTenantDetailsQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPlatformTenantDetails>>> = ({ signal }) => getPlatformTenantDetails(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPlatformTenantDetails>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPlatformTenantDetailsQueryResult = NonNullable<Awaited<ReturnType<typeof getPlatformTenantDetails>>>
export type GetPlatformTenantDetailsQueryError = void


/**
 * @summary getPlatformTenantDetails
 */

export function useGetPlatformTenantDetails<TData = Awaited<ReturnType<typeof getPlatformTenantDetails>>, TError = void>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPlatformTenantDetails>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPlatformTenantDetailsQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getChangePlatformTenantOwnerEmailUrl = (id: string,) => {




  return `/api/platform/tenants/${id}/owner-email`
}

/**
 * Creates or updates and resends an unopened tenant's owner invitation. Requires platform.tenants.provision.
 * @summary changePlatformTenantOwnerEmail
 */
export const changePlatformTenantOwnerEmail = async (id: string,
    adminOwnerEmailChange: AdminOwnerEmailChange, options?: Parameters<typeof customFetch>[1]): Promise<AdminProvisionResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminProvisionResult>(getChangePlatformTenantOwnerEmailUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminOwnerEmailChange)
  }
);}





export const getChangePlatformTenantOwnerEmailMutationKey = () => ['changePlatformTenantOwnerEmail'] as const;

export const getChangePlatformTenantOwnerEmailMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof changePlatformTenantOwnerEmail>>, TError,ChangePlatformTenantOwnerEmailMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof changePlatformTenantOwnerEmail>>, TError,ChangePlatformTenantOwnerEmailMutationVariables, TContext> => {

const mutationKey = getChangePlatformTenantOwnerEmailMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof changePlatformTenantOwnerEmail>>, ChangePlatformTenantOwnerEmailMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  changePlatformTenantOwnerEmail(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ChangePlatformTenantOwnerEmailMutationResult = NonNullable<Awaited<ReturnType<typeof changePlatformTenantOwnerEmail>>>
    export type ChangePlatformTenantOwnerEmailMutationBody = AdminOwnerEmailChange
    export type ChangePlatformTenantOwnerEmailMutationError = void
    export type ChangePlatformTenantOwnerEmailMutationVariables = {id: string;data: AdminOwnerEmailChange}

    /**
 * @summary changePlatformTenantOwnerEmail
 */
export const useChangePlatformTenantOwnerEmail = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof changePlatformTenantOwnerEmail>>, TError,ChangePlatformTenantOwnerEmailMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof changePlatformTenantOwnerEmail>>,
        TError,
        ChangePlatformTenantOwnerEmailMutationVariables,
        TContext
      > => {
      return useMutation(getChangePlatformTenantOwnerEmailMutationOptions(options));
    }

export const getListPlatformRoleAssignmentsUrl = (params?: ListPlatformRoleAssignmentsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/roles/assignments?${stringifiedParams}` : `/api/platform/roles/assignments`
}

/**
 * Requires platform.roles.assign.
 * @summary listPlatformRoleAssignments
 */
export const listPlatformRoleAssignments = async (params?: ListPlatformRoleAssignmentsParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminRoleAssignmentPage> => {

  return customFetch<AdminRoleAssignmentPage>(getListPlatformRoleAssignmentsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformRoleAssignmentsQueryKey = (params?: ListPlatformRoleAssignmentsParams,) => {
    return [
    `/api/platform/roles/assignments`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformRoleAssignmentsQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformRoleAssignments>>, TError = void>(params?: ListPlatformRoleAssignmentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformRoleAssignments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformRoleAssignmentsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformRoleAssignments>>> = ({ signal }) => listPlatformRoleAssignments(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformRoleAssignments>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformRoleAssignmentsQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformRoleAssignments>>>
export type ListPlatformRoleAssignmentsQueryError = void


/**
 * @summary listPlatformRoleAssignments
 */

export function useListPlatformRoleAssignments<TData = Awaited<ReturnType<typeof listPlatformRoleAssignments>>, TError = void>(
 params?: ListPlatformRoleAssignmentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformRoleAssignments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformRoleAssignmentsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getAssignPlatformRoleUrl = () => {




  return `/api/platform/roles/assignments`
}

/**
 * Requires platform.roles.assign.
 * @summary assignPlatformRole
 */
export const assignPlatformRole = async (adminAssignRole: AdminAssignRole, options?: Parameters<typeof customFetch>[1]): Promise<AdminRoleAssignment> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminRoleAssignment>(getAssignPlatformRoleUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminAssignRole)
  }
);}





export const getAssignPlatformRoleMutationKey = () => ['assignPlatformRole'] as const;

export const getAssignPlatformRoleMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof assignPlatformRole>>, TError,AssignPlatformRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof assignPlatformRole>>, TError,AssignPlatformRoleMutationVariables, TContext> => {

const mutationKey = getAssignPlatformRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof assignPlatformRole>>, AssignPlatformRoleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  assignPlatformRole(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AssignPlatformRoleMutationResult = NonNullable<Awaited<ReturnType<typeof assignPlatformRole>>>
    export type AssignPlatformRoleMutationBody = AdminAssignRole
    export type AssignPlatformRoleMutationError = void
    export type AssignPlatformRoleMutationVariables = {data: AdminAssignRole}

    /**
 * @summary assignPlatformRole
 */
export const useAssignPlatformRole = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof assignPlatformRole>>, TError,AssignPlatformRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof assignPlatformRole>>,
        TError,
        AssignPlatformRoleMutationVariables,
        TContext
      > => {
      return useMutation(getAssignPlatformRoleMutationOptions(options));
    }

export const getRevokePlatformRoleUrl = (id: string,) => {




  return `/api/platform/roles/assignments/${id}`
}

/**
 * Requires platform.roles.assign.
 * @summary revokePlatformRole
 */
export const revokePlatformRole = async (id: string,
    adminReason: AdminReason, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getRevokePlatformRoleUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminReason)
  }
);}





export const getRevokePlatformRoleMutationKey = () => ['revokePlatformRole'] as const;

export const getRevokePlatformRoleMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokePlatformRole>>, TError,RevokePlatformRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokePlatformRole>>, TError,RevokePlatformRoleMutationVariables, TContext> => {

const mutationKey = getRevokePlatformRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokePlatformRole>>, RevokePlatformRoleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  revokePlatformRole(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokePlatformRoleMutationResult = NonNullable<Awaited<ReturnType<typeof revokePlatformRole>>>
    export type RevokePlatformRoleMutationBody = AdminReason
    export type RevokePlatformRoleMutationError = void
    export type RevokePlatformRoleMutationVariables = {id: string;data: AdminReason}

    /**
 * @summary revokePlatformRole
 */
export const useRevokePlatformRole = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokePlatformRole>>, TError,RevokePlatformRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof revokePlatformRole>>,
        TError,
        RevokePlatformRoleMutationVariables,
        TContext
      > => {
      return useMutation(getRevokePlatformRoleMutationOptions(options));
    }

export const getListPlatformRolesUrl = (params?: ListPlatformRolesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/roles?${stringifiedParams}` : `/api/platform/roles`
}

/**
 * Requires platform.roles.manage, platform.roles.assign or platform.groups.manage.
 */
export const listPlatformRoles = async (params?: ListPlatformRolesParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminRoleDefinitionPage> => {

  return customFetch<AdminRoleDefinitionPage>(getListPlatformRolesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformRolesQueryKey = (params?: ListPlatformRolesParams,) => {
    return [
    `/api/platform/roles`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformRolesQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformRoles>>, TError = void>(params?: ListPlatformRolesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformRoles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformRolesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformRoles>>> = ({ signal }) => listPlatformRoles(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformRoles>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformRolesQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformRoles>>>
export type ListPlatformRolesQueryError = void



export function useListPlatformRoles<TData = Awaited<ReturnType<typeof listPlatformRoles>>, TError = void>(
 params?: ListPlatformRolesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformRoles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformRolesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreatePlatformRoleUrl = () => {




  return `/api/platform/roles`
}

/**
 * Requires platform.roles.manage.
 */
export const createPlatformRole = async (adminRoleWrite: AdminRoleWrite, options?: Parameters<typeof customFetch>[1]): Promise<AdminRoleDefinition> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminRoleDefinition>(getCreatePlatformRoleUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminRoleWrite)
  }
);}





export const getCreatePlatformRoleMutationKey = () => ['createPlatformRole'] as const;

export const getCreatePlatformRoleMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPlatformRole>>, TError,CreatePlatformRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createPlatformRole>>, TError,CreatePlatformRoleMutationVariables, TContext> => {

const mutationKey = getCreatePlatformRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createPlatformRole>>, CreatePlatformRoleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createPlatformRole(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreatePlatformRoleMutationResult = NonNullable<Awaited<ReturnType<typeof createPlatformRole>>>
    export type CreatePlatformRoleMutationBody = AdminRoleWrite
    export type CreatePlatformRoleMutationError = void
    export type CreatePlatformRoleMutationVariables = {data: AdminRoleWrite}

    export const useCreatePlatformRole = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPlatformRole>>, TError,CreatePlatformRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createPlatformRole>>,
        TError,
        CreatePlatformRoleMutationVariables,
        TContext
      > => {
      return useMutation(getCreatePlatformRoleMutationOptions(options));
    }

export const getUpdatePlatformRoleUrl = (id: string,) => {




  return `/api/platform/roles/${id}`
}

/**
 * Requires platform.roles.manage.
 */
export const updatePlatformRole = async (id: string,
    adminRoleWrite: AdminRoleWrite, options?: Parameters<typeof customFetch>[1]): Promise<AdminRoleDefinition> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminRoleDefinition>(getUpdatePlatformRoleUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminRoleWrite)
  }
);}





export const getUpdatePlatformRoleMutationKey = () => ['updatePlatformRole'] as const;

export const getUpdatePlatformRoleMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePlatformRole>>, TError,UpdatePlatformRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updatePlatformRole>>, TError,UpdatePlatformRoleMutationVariables, TContext> => {

const mutationKey = getUpdatePlatformRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updatePlatformRole>>, UpdatePlatformRoleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updatePlatformRole(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdatePlatformRoleMutationResult = NonNullable<Awaited<ReturnType<typeof updatePlatformRole>>>
    export type UpdatePlatformRoleMutationBody = AdminRoleWrite
    export type UpdatePlatformRoleMutationError = void
    export type UpdatePlatformRoleMutationVariables = {id: string;data: AdminRoleWrite}

    export const useUpdatePlatformRole = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePlatformRole>>, TError,UpdatePlatformRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updatePlatformRole>>,
        TError,
        UpdatePlatformRoleMutationVariables,
        TContext
      > => {
      return useMutation(getUpdatePlatformRoleMutationOptions(options));
    }

export const getDeletePlatformRoleUrl = (id: string,) => {




  return `/api/platform/roles/${id}`
}

/**
 * Requires platform.roles.manage.
 */
export const deletePlatformRole = async (id: string,
    adminReason: AdminReason, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getDeletePlatformRoleUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminReason)
  }
);}





export const getDeletePlatformRoleMutationKey = () => ['deletePlatformRole'] as const;

export const getDeletePlatformRoleMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deletePlatformRole>>, TError,DeletePlatformRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deletePlatformRole>>, TError,DeletePlatformRoleMutationVariables, TContext> => {

const mutationKey = getDeletePlatformRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deletePlatformRole>>, DeletePlatformRoleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  deletePlatformRole(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeletePlatformRoleMutationResult = NonNullable<Awaited<ReturnType<typeof deletePlatformRole>>>
    export type DeletePlatformRoleMutationBody = AdminReason
    export type DeletePlatformRoleMutationError = void
    export type DeletePlatformRoleMutationVariables = {id: string;data: AdminReason}

    export const useDeletePlatformRole = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deletePlatformRole>>, TError,DeletePlatformRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deletePlatformRole>>,
        TError,
        DeletePlatformRoleMutationVariables,
        TContext
      > => {
      return useMutation(getDeletePlatformRoleMutationOptions(options));
    }

export const getListPlatformGroupsUrl = (params?: ListPlatformGroupsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/groups?${stringifiedParams}` : `/api/platform/groups`
}

/**
 * Requires platform.groups.manage.
 */
export const listPlatformGroups = async (params?: ListPlatformGroupsParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminAccessGroupPage> => {

  return customFetch<AdminAccessGroupPage>(getListPlatformGroupsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformGroupsQueryKey = (params?: ListPlatformGroupsParams,) => {
    return [
    `/api/platform/groups`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformGroupsQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformGroups>>, TError = void>(params?: ListPlatformGroupsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformGroups>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformGroupsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformGroups>>> = ({ signal }) => listPlatformGroups(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformGroups>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformGroupsQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformGroups>>>
export type ListPlatformGroupsQueryError = void



export function useListPlatformGroups<TData = Awaited<ReturnType<typeof listPlatformGroups>>, TError = void>(
 params?: ListPlatformGroupsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformGroups>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformGroupsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreatePlatformGroupUrl = () => {




  return `/api/platform/groups`
}

/**
 * Requires platform.groups.manage.
 */
export const createPlatformGroup = async (adminAccessGroupWrite: AdminAccessGroupWrite, options?: Parameters<typeof customFetch>[1]): Promise<AdminAccessGroup> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminAccessGroup>(getCreatePlatformGroupUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminAccessGroupWrite)
  }
);}





export const getCreatePlatformGroupMutationKey = () => ['createPlatformGroup'] as const;

export const getCreatePlatformGroupMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPlatformGroup>>, TError,CreatePlatformGroupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createPlatformGroup>>, TError,CreatePlatformGroupMutationVariables, TContext> => {

const mutationKey = getCreatePlatformGroupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createPlatformGroup>>, CreatePlatformGroupMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createPlatformGroup(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreatePlatformGroupMutationResult = NonNullable<Awaited<ReturnType<typeof createPlatformGroup>>>
    export type CreatePlatformGroupMutationBody = AdminAccessGroupWrite
    export type CreatePlatformGroupMutationError = void
    export type CreatePlatformGroupMutationVariables = {data: AdminAccessGroupWrite}

    export const useCreatePlatformGroup = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPlatformGroup>>, TError,CreatePlatformGroupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createPlatformGroup>>,
        TError,
        CreatePlatformGroupMutationVariables,
        TContext
      > => {
      return useMutation(getCreatePlatformGroupMutationOptions(options));
    }

export const getUpdatePlatformGroupUrl = (id: string,) => {




  return `/api/platform/groups/${id}`
}

/**
 * Requires platform.groups.manage.
 */
export const updatePlatformGroup = async (id: string,
    adminAccessGroupWrite: AdminAccessGroupWrite, options?: Parameters<typeof customFetch>[1]): Promise<AdminAccessGroup> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminAccessGroup>(getUpdatePlatformGroupUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminAccessGroupWrite)
  }
);}





export const getUpdatePlatformGroupMutationKey = () => ['updatePlatformGroup'] as const;

export const getUpdatePlatformGroupMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePlatformGroup>>, TError,UpdatePlatformGroupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updatePlatformGroup>>, TError,UpdatePlatformGroupMutationVariables, TContext> => {

const mutationKey = getUpdatePlatformGroupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updatePlatformGroup>>, UpdatePlatformGroupMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updatePlatformGroup(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdatePlatformGroupMutationResult = NonNullable<Awaited<ReturnType<typeof updatePlatformGroup>>>
    export type UpdatePlatformGroupMutationBody = AdminAccessGroupWrite
    export type UpdatePlatformGroupMutationError = void
    export type UpdatePlatformGroupMutationVariables = {id: string;data: AdminAccessGroupWrite}

    export const useUpdatePlatformGroup = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePlatformGroup>>, TError,UpdatePlatformGroupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updatePlatformGroup>>,
        TError,
        UpdatePlatformGroupMutationVariables,
        TContext
      > => {
      return useMutation(getUpdatePlatformGroupMutationOptions(options));
    }

export const getDeletePlatformGroupUrl = (id: string,) => {




  return `/api/platform/groups/${id}`
}

/**
 * Requires platform.groups.manage.
 */
export const deletePlatformGroup = async (id: string,
    adminReason: AdminReason, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getDeletePlatformGroupUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminReason)
  }
);}





export const getDeletePlatformGroupMutationKey = () => ['deletePlatformGroup'] as const;

export const getDeletePlatformGroupMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deletePlatformGroup>>, TError,DeletePlatformGroupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deletePlatformGroup>>, TError,DeletePlatformGroupMutationVariables, TContext> => {

const mutationKey = getDeletePlatformGroupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deletePlatformGroup>>, DeletePlatformGroupMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  deletePlatformGroup(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeletePlatformGroupMutationResult = NonNullable<Awaited<ReturnType<typeof deletePlatformGroup>>>
    export type DeletePlatformGroupMutationBody = AdminReason
    export type DeletePlatformGroupMutationError = void
    export type DeletePlatformGroupMutationVariables = {id: string;data: AdminReason}

    export const useDeletePlatformGroup = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deletePlatformGroup>>, TError,DeletePlatformGroupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deletePlatformGroup>>,
        TError,
        DeletePlatformGroupMutationVariables,
        TContext
      > => {
      return useMutation(getDeletePlatformGroupMutationOptions(options));
    }

export const getListPlatformGroupMembersUrl = (params?: ListPlatformGroupMembersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/groups/members?${stringifiedParams}` : `/api/platform/groups/members`
}

/**
 * Search platform users available for group membership.
 */
export const listPlatformGroupMembers = async (params?: ListPlatformGroupMembersParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminGroupMemberPage> => {

  return customFetch<AdminGroupMemberPage>(getListPlatformGroupMembersUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformGroupMembersQueryKey = (params?: ListPlatformGroupMembersParams,) => {
    return [
    `/api/platform/groups/members`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformGroupMembersQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformGroupMembers>>, TError = void>(params?: ListPlatformGroupMembersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformGroupMembers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformGroupMembersQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformGroupMembers>>> = ({ signal }) => listPlatformGroupMembers(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformGroupMembers>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformGroupMembersQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformGroupMembers>>>
export type ListPlatformGroupMembersQueryError = void



export function useListPlatformGroupMembers<TData = Awaited<ReturnType<typeof listPlatformGroupMembers>>, TError = void>(
 params?: ListPlatformGroupMembersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformGroupMembers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformGroupMembersQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListPlatformAuditUrl = (params?: ListPlatformAuditParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/audit?${stringifiedParams}` : `/api/platform/audit`
}

/**
 * Requires platform.audit.read.
 * @summary listPlatformAudit
 */
export const listPlatformAudit = async (params?: ListPlatformAuditParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminAuditEventPage> => {

  return customFetch<AdminAuditEventPage>(getListPlatformAuditUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformAuditQueryKey = (params?: ListPlatformAuditParams,) => {
    return [
    `/api/platform/audit`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformAuditQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformAudit>>, TError = void>(params?: ListPlatformAuditParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformAudit>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformAuditQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformAudit>>> = ({ signal }) => listPlatformAudit(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformAudit>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformAuditQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformAudit>>>
export type ListPlatformAuditQueryError = void


/**
 * @summary listPlatformAudit
 */

export function useListPlatformAudit<TData = Awaited<ReturnType<typeof listPlatformAudit>>, TError = void>(
 params?: ListPlatformAuditParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformAudit>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformAuditQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListMyEligibleRolesUrl = (params?: ListMyEligibleRolesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/me/eligible-roles?${stringifiedParams}` : `/api/me/eligible-roles`
}

/**
 * Requires signed-in staff.
 * @summary listMyEligibleRoles
 */
export const listMyEligibleRoles = async (params?: ListMyEligibleRolesParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminEligibilityPage> => {

  return customFetch<AdminEligibilityPage>(getListMyEligibleRolesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMyEligibleRolesQueryKey = (params?: ListMyEligibleRolesParams,) => {
    return [
    `/api/me/eligible-roles`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMyEligibleRolesQueryOptions = <TData = Awaited<ReturnType<typeof listMyEligibleRoles>>, TError = void>(params?: ListMyEligibleRolesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMyEligibleRoles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMyEligibleRolesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyEligibleRoles>>> = ({ signal }) => listMyEligibleRoles(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMyEligibleRoles>>, TError, TData> & { queryKey: QueryKey }
}

export type ListMyEligibleRolesQueryResult = NonNullable<Awaited<ReturnType<typeof listMyEligibleRoles>>>
export type ListMyEligibleRolesQueryError = void


/**
 * @summary listMyEligibleRoles
 */

export function useListMyEligibleRoles<TData = Awaited<ReturnType<typeof listMyEligibleRoles>>, TError = void>(
 params?: ListMyEligibleRolesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMyEligibleRoles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListMyEligibleRolesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListMyPermanentTenantRoleOptionsUrl = (params: ListMyPermanentTenantRoleOptionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/me/permanent-tenant-roles?${stringifiedParams}` : `/api/me/permanent-tenant-roles`
}

/**
 * Lists tenant roles whose permissions fit within the caller's active tenant elevation. Requires permanent platform role-management permission.
 * @summary listMyPermanentTenantRoleOptions
 */
export const listMyPermanentTenantRoleOptions = async (params: ListMyPermanentTenantRoleOptionsParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminPermanentTenantRoleOptionPage> => {

  return customFetch<AdminPermanentTenantRoleOptionPage>(getListMyPermanentTenantRoleOptionsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMyPermanentTenantRoleOptionsQueryKey = (params?: ListMyPermanentTenantRoleOptionsParams,) => {
    return [
    `/api/me/permanent-tenant-roles`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMyPermanentTenantRoleOptionsQueryOptions = <TData = Awaited<ReturnType<typeof listMyPermanentTenantRoleOptions>>, TError = void>(params: ListMyPermanentTenantRoleOptionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMyPermanentTenantRoleOptions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMyPermanentTenantRoleOptionsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyPermanentTenantRoleOptions>>> = ({ signal }) => listMyPermanentTenantRoleOptions(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMyPermanentTenantRoleOptions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListMyPermanentTenantRoleOptionsQueryResult = NonNullable<Awaited<ReturnType<typeof listMyPermanentTenantRoleOptions>>>
export type ListMyPermanentTenantRoleOptionsQueryError = void


/**
 * @summary listMyPermanentTenantRoleOptions
 */

export function useListMyPermanentTenantRoleOptions<TData = Awaited<ReturnType<typeof listMyPermanentTenantRoleOptions>>, TError = void>(
 params: ListMyPermanentTenantRoleOptionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMyPermanentTenantRoleOptions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListMyPermanentTenantRoleOptionsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGrantMyPermanentTenantRoleUrl = () => {




  return `/api/me/permanent-tenant-roles`
}

/**
 * Creates or reactivates the caller's tenant membership and grants one tenant role within their active elevation. Requires permanent platform role-management permission.
 * @summary grantMyPermanentTenantRole
 */
export const grantMyPermanentTenantRole = async (adminPermanentTenantRoleGrantWrite: AdminPermanentTenantRoleGrantWrite, options?: Parameters<typeof customFetch>[1]): Promise<AdminPermanentTenantRoleGrant> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminPermanentTenantRoleGrant>(getGrantMyPermanentTenantRoleUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminPermanentTenantRoleGrantWrite)
  }
);}





export const getGrantMyPermanentTenantRoleMutationKey = () => ['grantMyPermanentTenantRole'] as const;

export const getGrantMyPermanentTenantRoleMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof grantMyPermanentTenantRole>>, TError,GrantMyPermanentTenantRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof grantMyPermanentTenantRole>>, TError,GrantMyPermanentTenantRoleMutationVariables, TContext> => {

const mutationKey = getGrantMyPermanentTenantRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof grantMyPermanentTenantRole>>, GrantMyPermanentTenantRoleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  grantMyPermanentTenantRole(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GrantMyPermanentTenantRoleMutationResult = NonNullable<Awaited<ReturnType<typeof grantMyPermanentTenantRole>>>
    export type GrantMyPermanentTenantRoleMutationBody = AdminPermanentTenantRoleGrantWrite
    export type GrantMyPermanentTenantRoleMutationError = void
    export type GrantMyPermanentTenantRoleMutationVariables = {data: AdminPermanentTenantRoleGrantWrite}

    /**
 * @summary grantMyPermanentTenantRole
 */
export const useGrantMyPermanentTenantRole = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof grantMyPermanentTenantRole>>, TError,GrantMyPermanentTenantRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof grantMyPermanentTenantRole>>,
        TError,
        GrantMyPermanentTenantRoleMutationVariables,
        TContext
      > => {
      return useMutation(getGrantMyPermanentTenantRoleMutationOptions(options));
    }

export const getListMyElevationsUrl = (params?: ListMyElevationsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/me/elevations?${stringifiedParams}` : `/api/me/elevations`
}

/**
 * Requires signed-in staff.
 * @summary listMyElevations
 */
export const listMyElevations = async (params?: ListMyElevationsParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminElevationPage> => {

  return customFetch<AdminElevationPage>(getListMyElevationsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMyElevationsQueryKey = (params?: ListMyElevationsParams,) => {
    return [
    `/api/me/elevations`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMyElevationsQueryOptions = <TData = Awaited<ReturnType<typeof listMyElevations>>, TError = void>(params?: ListMyElevationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMyElevations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMyElevationsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyElevations>>> = ({ signal }) => listMyElevations(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMyElevations>>, TError, TData> & { queryKey: QueryKey }
}

export type ListMyElevationsQueryResult = NonNullable<Awaited<ReturnType<typeof listMyElevations>>>
export type ListMyElevationsQueryError = void


/**
 * @summary listMyElevations
 */

export function useListMyElevations<TData = Awaited<ReturnType<typeof listMyElevations>>, TError = void>(
 params?: ListMyElevationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMyElevations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListMyElevationsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getActivateMyElevationUrl = () => {




  return `/api/me/elevations`
}

/**
 * Requests 1–24 hours for an eligible role. Access stays pending until another authorized administrator approves it.
 * @summary activateMyElevation
 */
export const activateMyElevation = async (adminElevationActivate: AdminElevationActivate, options?: Parameters<typeof customFetch>[1]): Promise<AdminElevation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminElevation>(getActivateMyElevationUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminElevationActivate)
  }
);}





export const getActivateMyElevationMutationKey = () => ['activateMyElevation'] as const;

export const getActivateMyElevationMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof activateMyElevation>>, TError,ActivateMyElevationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof activateMyElevation>>, TError,ActivateMyElevationMutationVariables, TContext> => {

const mutationKey = getActivateMyElevationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof activateMyElevation>>, ActivateMyElevationMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  activateMyElevation(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ActivateMyElevationMutationResult = NonNullable<Awaited<ReturnType<typeof activateMyElevation>>>
    export type ActivateMyElevationMutationBody = AdminElevationActivate
    export type ActivateMyElevationMutationError = void
    export type ActivateMyElevationMutationVariables = {data: AdminElevationActivate}

    /**
 * @summary activateMyElevation
 */
export const useActivateMyElevation = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof activateMyElevation>>, TError,ActivateMyElevationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof activateMyElevation>>,
        TError,
        ActivateMyElevationMutationVariables,
        TContext
      > => {
      return useMutation(getActivateMyElevationMutationOptions(options));
    }

export const getEndMyElevationUrl = (id: string,) => {




  return `/api/me/elevations/${id}`
}

/**
 * Requires session owner.
 * @summary endMyElevation
 */
export const endMyElevation = async (id: string,
    adminReason: AdminReason, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getEndMyElevationUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminReason)
  }
);}





export const getEndMyElevationMutationKey = () => ['endMyElevation'] as const;

export const getEndMyElevationMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof endMyElevation>>, TError,EndMyElevationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof endMyElevation>>, TError,EndMyElevationMutationVariables, TContext> => {

const mutationKey = getEndMyElevationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof endMyElevation>>, EndMyElevationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  endMyElevation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type EndMyElevationMutationResult = NonNullable<Awaited<ReturnType<typeof endMyElevation>>>
    export type EndMyElevationMutationBody = AdminReason
    export type EndMyElevationMutationError = void
    export type EndMyElevationMutationVariables = {id: string;data: AdminReason}

    /**
 * @summary endMyElevation
 */
export const useEndMyElevation = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof endMyElevation>>, TError,EndMyElevationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof endMyElevation>>,
        TError,
        EndMyElevationMutationVariables,
        TContext
      > => {
      return useMutation(getEndMyElevationMutationOptions(options));
    }

export const getListPlatformEligibleRolesUrl = (params?: ListPlatformEligibleRolesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/eligible-roles?${stringifiedParams}` : `/api/platform/eligible-roles`
}

/**
 * Requires platform.elevation.manage.
 * @summary listPlatformEligibleRoles
 */
export const listPlatformEligibleRoles = async (params?: ListPlatformEligibleRolesParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminEligibilityPage> => {

  return customFetch<AdminEligibilityPage>(getListPlatformEligibleRolesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformEligibleRolesQueryKey = (params?: ListPlatformEligibleRolesParams,) => {
    return [
    `/api/platform/eligible-roles`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformEligibleRolesQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformEligibleRoles>>, TError = void>(params?: ListPlatformEligibleRolesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformEligibleRoles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformEligibleRolesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformEligibleRoles>>> = ({ signal }) => listPlatformEligibleRoles(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformEligibleRoles>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformEligibleRolesQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformEligibleRoles>>>
export type ListPlatformEligibleRolesQueryError = void


/**
 * @summary listPlatformEligibleRoles
 */

export function useListPlatformEligibleRoles<TData = Awaited<ReturnType<typeof listPlatformEligibleRoles>>, TError = void>(
 params?: ListPlatformEligibleRolesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformEligibleRoles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformEligibleRolesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGrantPlatformEligibleRoleUrl = () => {




  return `/api/platform/eligible-roles`
}

/**
 * Requires platform.elevation.manage.
 * @summary grantPlatformEligibleRole
 */
export const grantPlatformEligibleRole = async (adminEligibleGrant: AdminEligibleGrant, options?: Parameters<typeof customFetch>[1]): Promise<AdminEligibility> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminEligibility>(getGrantPlatformEligibleRoleUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminEligibleGrant)
  }
);}





export const getGrantPlatformEligibleRoleMutationKey = () => ['grantPlatformEligibleRole'] as const;

export const getGrantPlatformEligibleRoleMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof grantPlatformEligibleRole>>, TError,GrantPlatformEligibleRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof grantPlatformEligibleRole>>, TError,GrantPlatformEligibleRoleMutationVariables, TContext> => {

const mutationKey = getGrantPlatformEligibleRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof grantPlatformEligibleRole>>, GrantPlatformEligibleRoleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  grantPlatformEligibleRole(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GrantPlatformEligibleRoleMutationResult = NonNullable<Awaited<ReturnType<typeof grantPlatformEligibleRole>>>
    export type GrantPlatformEligibleRoleMutationBody = AdminEligibleGrant
    export type GrantPlatformEligibleRoleMutationError = void
    export type GrantPlatformEligibleRoleMutationVariables = {data: AdminEligibleGrant}

    /**
 * @summary grantPlatformEligibleRole
 */
export const useGrantPlatformEligibleRole = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof grantPlatformEligibleRole>>, TError,GrantPlatformEligibleRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof grantPlatformEligibleRole>>,
        TError,
        GrantPlatformEligibleRoleMutationVariables,
        TContext
      > => {
      return useMutation(getGrantPlatformEligibleRoleMutationOptions(options));
    }

export const getRevokePlatformEligibleRoleUrl = (id: string,) => {




  return `/api/platform/eligible-roles/${id}`
}

/**
 * Requires platform.elevation.manage.
 * @summary revokePlatformEligibleRole
 */
export const revokePlatformEligibleRole = async (id: string,
    adminReason: AdminReason, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getRevokePlatformEligibleRoleUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminReason)
  }
);}





export const getRevokePlatformEligibleRoleMutationKey = () => ['revokePlatformEligibleRole'] as const;

export const getRevokePlatformEligibleRoleMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokePlatformEligibleRole>>, TError,RevokePlatformEligibleRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokePlatformEligibleRole>>, TError,RevokePlatformEligibleRoleMutationVariables, TContext> => {

const mutationKey = getRevokePlatformEligibleRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokePlatformEligibleRole>>, RevokePlatformEligibleRoleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  revokePlatformEligibleRole(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokePlatformEligibleRoleMutationResult = NonNullable<Awaited<ReturnType<typeof revokePlatformEligibleRole>>>
    export type RevokePlatformEligibleRoleMutationBody = AdminReason
    export type RevokePlatformEligibleRoleMutationError = void
    export type RevokePlatformEligibleRoleMutationVariables = {id: string;data: AdminReason}

    /**
 * @summary revokePlatformEligibleRole
 */
export const useRevokePlatformEligibleRole = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokePlatformEligibleRole>>, TError,RevokePlatformEligibleRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof revokePlatformEligibleRole>>,
        TError,
        RevokePlatformEligibleRoleMutationVariables,
        TContext
      > => {
      return useMutation(getRevokePlatformEligibleRoleMutationOptions(options));
    }

export const getListPlatformElevationsUrl = (params?: ListPlatformElevationsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/elevations?${stringifiedParams}` : `/api/platform/elevations`
}

/**
 * Requires platform.elevation.manage.
 * @summary listPlatformElevations
 */
export const listPlatformElevations = async (params?: ListPlatformElevationsParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminElevationPage> => {

  return customFetch<AdminElevationPage>(getListPlatformElevationsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformElevationsQueryKey = (params?: ListPlatformElevationsParams,) => {
    return [
    `/api/platform/elevations`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformElevationsQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformElevations>>, TError = void>(params?: ListPlatformElevationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformElevations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformElevationsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformElevations>>> = ({ signal }) => listPlatformElevations(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformElevations>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformElevationsQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformElevations>>>
export type ListPlatformElevationsQueryError = void


/**
 * @summary listPlatformElevations
 */

export function useListPlatformElevations<TData = Awaited<ReturnType<typeof listPlatformElevations>>, TError = void>(
 params?: ListPlatformElevationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformElevations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformElevationsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetMyRecoveryAccessUrl = () => {




  return `/api/me/recovery-access`
}

/**
 * Shows whether the signed-in person is an appointed recovery custodian, whether recovery is ready (at least two active custodians) and any active recovery access.
 * @summary getMyRecoveryAccess
 */
export const getMyRecoveryAccess = async ( options?: Parameters<typeof customFetch>[1]): Promise<AdminMyRecoveryAccess> => {

  return customFetch<AdminMyRecoveryAccess>(getGetMyRecoveryAccessUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMyRecoveryAccessQueryKey = () => {
    return [
    `/api/me/recovery-access`
    ] as const;
    }


export const getGetMyRecoveryAccessQueryOptions = <TData = Awaited<ReturnType<typeof getMyRecoveryAccess>>, TError = void>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMyRecoveryAccess>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMyRecoveryAccessQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMyRecoveryAccess>>> = ({ signal }) => getMyRecoveryAccess({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMyRecoveryAccess>>, TError, TData> & { queryKey: QueryKey }
}

export type GetMyRecoveryAccessQueryResult = NonNullable<Awaited<ReturnType<typeof getMyRecoveryAccess>>>
export type GetMyRecoveryAccessQueryError = void


/**
 * @summary getMyRecoveryAccess
 */

export function useGetMyRecoveryAccess<TData = Awaited<ReturnType<typeof getMyRecoveryAccess>>, TError = void>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMyRecoveryAccess>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetMyRecoveryAccessQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getActivateMyRecoveryAccessUrl = () => {




  return `/api/me/recovery-access`
}

/**
 * Emergency recovery for an appointed custodian when normal administrator approval is unavailable. Grants Platform Administrator permissions for 15–60 minutes, requires at least two active custodians, and is audited.
 * @summary activateMyRecoveryAccess
 */
export const activateMyRecoveryAccess = async (adminRecoveryActivate: AdminRecoveryActivate, options?: Parameters<typeof customFetch>[1]): Promise<AdminRecoverySession> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminRecoverySession>(getActivateMyRecoveryAccessUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminRecoveryActivate)
  }
);}





export const getActivateMyRecoveryAccessMutationKey = () => ['activateMyRecoveryAccess'] as const;

export const getActivateMyRecoveryAccessMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof activateMyRecoveryAccess>>, TError,ActivateMyRecoveryAccessMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof activateMyRecoveryAccess>>, TError,ActivateMyRecoveryAccessMutationVariables, TContext> => {

const mutationKey = getActivateMyRecoveryAccessMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof activateMyRecoveryAccess>>, ActivateMyRecoveryAccessMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  activateMyRecoveryAccess(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ActivateMyRecoveryAccessMutationResult = NonNullable<Awaited<ReturnType<typeof activateMyRecoveryAccess>>>
    export type ActivateMyRecoveryAccessMutationBody = AdminRecoveryActivate
    export type ActivateMyRecoveryAccessMutationError = void
    export type ActivateMyRecoveryAccessMutationVariables = {data: AdminRecoveryActivate}

    /**
 * @summary activateMyRecoveryAccess
 */
export const useActivateMyRecoveryAccess = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof activateMyRecoveryAccess>>, TError,ActivateMyRecoveryAccessMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof activateMyRecoveryAccess>>,
        TError,
        ActivateMyRecoveryAccessMutationVariables,
        TContext
      > => {
      return useMutation(getActivateMyRecoveryAccessMutationOptions(options));
    }

export const getEndMyRecoveryAccessUrl = (id: string,) => {




  return `/api/me/recovery-access/${id}`
}

/**
 * Ends your own active recovery access early.
 * @summary endMyRecoveryAccess
 */
export const endMyRecoveryAccess = async (id: string,
    adminReason: AdminReason, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getEndMyRecoveryAccessUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminReason)
  }
);}





export const getEndMyRecoveryAccessMutationKey = () => ['endMyRecoveryAccess'] as const;

export const getEndMyRecoveryAccessMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof endMyRecoveryAccess>>, TError,EndMyRecoveryAccessMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof endMyRecoveryAccess>>, TError,EndMyRecoveryAccessMutationVariables, TContext> => {

const mutationKey = getEndMyRecoveryAccessMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof endMyRecoveryAccess>>, EndMyRecoveryAccessMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  endMyRecoveryAccess(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type EndMyRecoveryAccessMutationResult = NonNullable<Awaited<ReturnType<typeof endMyRecoveryAccess>>>
    export type EndMyRecoveryAccessMutationBody = AdminReason
    export type EndMyRecoveryAccessMutationError = void
    export type EndMyRecoveryAccessMutationVariables = {id: string;data: AdminReason}

    /**
 * @summary endMyRecoveryAccess
 */
export const useEndMyRecoveryAccess = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof endMyRecoveryAccess>>, TError,EndMyRecoveryAccessMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof endMyRecoveryAccess>>,
        TError,
        EndMyRecoveryAccessMutationVariables,
        TContext
      > => {
      return useMutation(getEndMyRecoveryAccessMutationOptions(options));
    }

export const getListPlatformRecoveryCustodiansUrl = (params?: ListPlatformRecoveryCustodiansParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/recovery-custodians?${stringifiedParams}` : `/api/platform/recovery-custodians`
}

/**
 * Requires platform.elevation.manage.
 * @summary listPlatformRecoveryCustodians
 */
export const listPlatformRecoveryCustodians = async (params?: ListPlatformRecoveryCustodiansParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminRecoveryCustodianPage> => {

  return customFetch<AdminRecoveryCustodianPage>(getListPlatformRecoveryCustodiansUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformRecoveryCustodiansQueryKey = (params?: ListPlatformRecoveryCustodiansParams,) => {
    return [
    `/api/platform/recovery-custodians`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformRecoveryCustodiansQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformRecoveryCustodians>>, TError = void>(params?: ListPlatformRecoveryCustodiansParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformRecoveryCustodians>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformRecoveryCustodiansQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformRecoveryCustodians>>> = ({ signal }) => listPlatformRecoveryCustodians(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformRecoveryCustodians>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformRecoveryCustodiansQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformRecoveryCustodians>>>
export type ListPlatformRecoveryCustodiansQueryError = void


/**
 * @summary listPlatformRecoveryCustodians
 */

export function useListPlatformRecoveryCustodians<TData = Awaited<ReturnType<typeof listPlatformRecoveryCustodians>>, TError = void>(
 params?: ListPlatformRecoveryCustodiansParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformRecoveryCustodians>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformRecoveryCustodiansQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getAppointPlatformRecoveryCustodianUrl = () => {




  return `/api/platform/recovery-custodians`
}

/**
 * Requires platform.elevation.manage. Appoints another signed-in person, identified by their Microsoft account, as a recovery custodian. You cannot appoint yourself.
 * @summary appointPlatformRecoveryCustodian
 */
export const appointPlatformRecoveryCustodian = async (adminRecoveryCustodianAppoint: AdminRecoveryCustodianAppoint, options?: Parameters<typeof customFetch>[1]): Promise<AdminRecoveryCustodian> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminRecoveryCustodian>(getAppointPlatformRecoveryCustodianUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminRecoveryCustodianAppoint)
  }
);}





export const getAppointPlatformRecoveryCustodianMutationKey = () => ['appointPlatformRecoveryCustodian'] as const;

export const getAppointPlatformRecoveryCustodianMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof appointPlatformRecoveryCustodian>>, TError,AppointPlatformRecoveryCustodianMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof appointPlatformRecoveryCustodian>>, TError,AppointPlatformRecoveryCustodianMutationVariables, TContext> => {

const mutationKey = getAppointPlatformRecoveryCustodianMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof appointPlatformRecoveryCustodian>>, AppointPlatformRecoveryCustodianMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  appointPlatformRecoveryCustodian(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AppointPlatformRecoveryCustodianMutationResult = NonNullable<Awaited<ReturnType<typeof appointPlatformRecoveryCustodian>>>
    export type AppointPlatformRecoveryCustodianMutationBody = AdminRecoveryCustodianAppoint
    export type AppointPlatformRecoveryCustodianMutationError = void
    export type AppointPlatformRecoveryCustodianMutationVariables = {data: AdminRecoveryCustodianAppoint}

    /**
 * @summary appointPlatformRecoveryCustodian
 */
export const useAppointPlatformRecoveryCustodian = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof appointPlatformRecoveryCustodian>>, TError,AppointPlatformRecoveryCustodianMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof appointPlatformRecoveryCustodian>>,
        TError,
        AppointPlatformRecoveryCustodianMutationVariables,
        TContext
      > => {
      return useMutation(getAppointPlatformRecoveryCustodianMutationOptions(options));
    }

export const getRevokePlatformRecoveryCustodianUrl = (id: string,) => {




  return `/api/platform/recovery-custodians/${id}`
}

/**
 * Requires platform.elevation.manage. Removes the appointment and immediately ends that custodian's active recovery access.
 * @summary revokePlatformRecoveryCustodian
 */
export const revokePlatformRecoveryCustodian = async (id: string,
    adminReason: AdminReason, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getRevokePlatformRecoveryCustodianUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminReason)
  }
);}





export const getRevokePlatformRecoveryCustodianMutationKey = () => ['revokePlatformRecoveryCustodian'] as const;

export const getRevokePlatformRecoveryCustodianMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokePlatformRecoveryCustodian>>, TError,RevokePlatformRecoveryCustodianMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokePlatformRecoveryCustodian>>, TError,RevokePlatformRecoveryCustodianMutationVariables, TContext> => {

const mutationKey = getRevokePlatformRecoveryCustodianMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokePlatformRecoveryCustodian>>, RevokePlatformRecoveryCustodianMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  revokePlatformRecoveryCustodian(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokePlatformRecoveryCustodianMutationResult = NonNullable<Awaited<ReturnType<typeof revokePlatformRecoveryCustodian>>>
    export type RevokePlatformRecoveryCustodianMutationBody = AdminReason
    export type RevokePlatformRecoveryCustodianMutationError = void
    export type RevokePlatformRecoveryCustodianMutationVariables = {id: string;data: AdminReason}

    /**
 * @summary revokePlatformRecoveryCustodian
 */
export const useRevokePlatformRecoveryCustodian = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokePlatformRecoveryCustodian>>, TError,RevokePlatformRecoveryCustodianMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof revokePlatformRecoveryCustodian>>,
        TError,
        RevokePlatformRecoveryCustodianMutationVariables,
        TContext
      > => {
      return useMutation(getRevokePlatformRecoveryCustodianMutationOptions(options));
    }

export const getListPlatformRecoverySessionsUrl = (params?: ListPlatformRecoverySessionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/recovery-sessions?${stringifiedParams}` : `/api/platform/recovery-sessions`
}

/**
 * Requires platform.elevation.manage.
 * @summary listPlatformRecoverySessions
 */
export const listPlatformRecoverySessions = async (params?: ListPlatformRecoverySessionsParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminRecoverySessionPage> => {

  return customFetch<AdminRecoverySessionPage>(getListPlatformRecoverySessionsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformRecoverySessionsQueryKey = (params?: ListPlatformRecoverySessionsParams,) => {
    return [
    `/api/platform/recovery-sessions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformRecoverySessionsQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformRecoverySessions>>, TError = void>(params?: ListPlatformRecoverySessionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformRecoverySessions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformRecoverySessionsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformRecoverySessions>>> = ({ signal }) => listPlatformRecoverySessions(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformRecoverySessions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformRecoverySessionsQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformRecoverySessions>>>
export type ListPlatformRecoverySessionsQueryError = void


/**
 * @summary listPlatformRecoverySessions
 */

export function useListPlatformRecoverySessions<TData = Awaited<ReturnType<typeof listPlatformRecoverySessions>>, TError = void>(
 params?: ListPlatformRecoverySessionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformRecoverySessions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformRecoverySessionsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRevokePlatformRecoverySessionUrl = (id: string,) => {




  return `/api/platform/recovery-sessions/${id}`
}

/**
 * Requires platform.elevation.manage.
 * @summary revokePlatformRecoverySession
 */
export const revokePlatformRecoverySession = async (id: string,
    adminReason: AdminReason, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getRevokePlatformRecoverySessionUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminReason)
  }
);}





export const getRevokePlatformRecoverySessionMutationKey = () => ['revokePlatformRecoverySession'] as const;

export const getRevokePlatformRecoverySessionMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokePlatformRecoverySession>>, TError,RevokePlatformRecoverySessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokePlatformRecoverySession>>, TError,RevokePlatformRecoverySessionMutationVariables, TContext> => {

const mutationKey = getRevokePlatformRecoverySessionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokePlatformRecoverySession>>, RevokePlatformRecoverySessionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  revokePlatformRecoverySession(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokePlatformRecoverySessionMutationResult = NonNullable<Awaited<ReturnType<typeof revokePlatformRecoverySession>>>
    export type RevokePlatformRecoverySessionMutationBody = AdminReason
    export type RevokePlatformRecoverySessionMutationError = void
    export type RevokePlatformRecoverySessionMutationVariables = {id: string;data: AdminReason}

    /**
 * @summary revokePlatformRecoverySession
 */
export const useRevokePlatformRecoverySession = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokePlatformRecoverySession>>, TError,RevokePlatformRecoverySessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof revokePlatformRecoverySession>>,
        TError,
        RevokePlatformRecoverySessionMutationVariables,
        TContext
      > => {
      return useMutation(getRevokePlatformRecoverySessionMutationOptions(options));
    }

export const getRevokePlatformElevationUrl = (id: string,) => {




  return `/api/platform/elevations/${id}`
}

/**
 * Requires platform.elevation.manage.
 * @summary revokePlatformElevation
 */
export const revokePlatformElevation = async (id: string,
    adminReason: AdminReason, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getRevokePlatformElevationUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminReason)
  }
);}





export const getRevokePlatformElevationMutationKey = () => ['revokePlatformElevation'] as const;

export const getRevokePlatformElevationMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokePlatformElevation>>, TError,RevokePlatformElevationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokePlatformElevation>>, TError,RevokePlatformElevationMutationVariables, TContext> => {

const mutationKey = getRevokePlatformElevationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokePlatformElevation>>, RevokePlatformElevationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  revokePlatformElevation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokePlatformElevationMutationResult = NonNullable<Awaited<ReturnType<typeof revokePlatformElevation>>>
    export type RevokePlatformElevationMutationBody = AdminReason
    export type RevokePlatformElevationMutationError = void
    export type RevokePlatformElevationMutationVariables = {id: string;data: AdminReason}

    /**
 * @summary revokePlatformElevation
 */
export const useRevokePlatformElevation = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokePlatformElevation>>, TError,RevokePlatformElevationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof revokePlatformElevation>>,
        TError,
        RevokePlatformElevationMutationVariables,
        TContext
      > => {
      return useMutation(getRevokePlatformElevationMutationOptions(options));
    }

export const getListTenantEligibleRolesUrl = (params?: ListTenantEligibleRolesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/eligible-roles?${stringifiedParams}` : `/api/access/eligible-roles`
}

/**
 * Requires tenant.roles.assign.
 * @summary listTenantEligibleRoles
 */
export const listTenantEligibleRoles = async (params?: ListTenantEligibleRolesParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminEligibilityPage> => {

  return customFetch<AdminEligibilityPage>(getListTenantEligibleRolesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenantEligibleRolesQueryKey = (params?: ListTenantEligibleRolesParams,) => {
    return [
    `/api/access/eligible-roles`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenantEligibleRolesQueryOptions = <TData = Awaited<ReturnType<typeof listTenantEligibleRoles>>, TError = void>(params?: ListTenantEligibleRolesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantEligibleRoles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenantEligibleRolesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenantEligibleRoles>>> = ({ signal }) => listTenantEligibleRoles(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenantEligibleRoles>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenantEligibleRolesQueryResult = NonNullable<Awaited<ReturnType<typeof listTenantEligibleRoles>>>
export type ListTenantEligibleRolesQueryError = void


/**
 * @summary listTenantEligibleRoles
 */

export function useListTenantEligibleRoles<TData = Awaited<ReturnType<typeof listTenantEligibleRoles>>, TError = void>(
 params?: ListTenantEligibleRolesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantEligibleRoles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenantEligibleRolesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGrantTenantEligibleRoleUrl = () => {




  return `/api/access/eligible-roles`
}

/**
 * Requires tenant.roles.assign.
 * @summary grantTenantEligibleRole
 */
export const grantTenantEligibleRole = async (adminEligibleGrant: AdminEligibleGrant, options?: Parameters<typeof customFetch>[1]): Promise<AdminEligibility> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminEligibility>(getGrantTenantEligibleRoleUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminEligibleGrant)
  }
);}





export const getGrantTenantEligibleRoleMutationKey = () => ['grantTenantEligibleRole'] as const;

export const getGrantTenantEligibleRoleMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof grantTenantEligibleRole>>, TError,GrantTenantEligibleRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof grantTenantEligibleRole>>, TError,GrantTenantEligibleRoleMutationVariables, TContext> => {

const mutationKey = getGrantTenantEligibleRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof grantTenantEligibleRole>>, GrantTenantEligibleRoleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  grantTenantEligibleRole(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GrantTenantEligibleRoleMutationResult = NonNullable<Awaited<ReturnType<typeof grantTenantEligibleRole>>>
    export type GrantTenantEligibleRoleMutationBody = AdminEligibleGrant
    export type GrantTenantEligibleRoleMutationError = void
    export type GrantTenantEligibleRoleMutationVariables = {data: AdminEligibleGrant}

    /**
 * @summary grantTenantEligibleRole
 */
export const useGrantTenantEligibleRole = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof grantTenantEligibleRole>>, TError,GrantTenantEligibleRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof grantTenantEligibleRole>>,
        TError,
        GrantTenantEligibleRoleMutationVariables,
        TContext
      > => {
      return useMutation(getGrantTenantEligibleRoleMutationOptions(options));
    }

export const getRevokeTenantEligibleRoleUrl = (id: string,) => {




  return `/api/access/eligible-roles/${id}`
}

/**
 * Requires tenant.roles.assign.
 * @summary revokeTenantEligibleRole
 */
export const revokeTenantEligibleRole = async (id: string,
    adminReason: AdminReason, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getRevokeTenantEligibleRoleUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminReason)
  }
);}





export const getRevokeTenantEligibleRoleMutationKey = () => ['revokeTenantEligibleRole'] as const;

export const getRevokeTenantEligibleRoleMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeTenantEligibleRole>>, TError,RevokeTenantEligibleRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokeTenantEligibleRole>>, TError,RevokeTenantEligibleRoleMutationVariables, TContext> => {

const mutationKey = getRevokeTenantEligibleRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokeTenantEligibleRole>>, RevokeTenantEligibleRoleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  revokeTenantEligibleRole(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokeTenantEligibleRoleMutationResult = NonNullable<Awaited<ReturnType<typeof revokeTenantEligibleRole>>>
    export type RevokeTenantEligibleRoleMutationBody = AdminReason
    export type RevokeTenantEligibleRoleMutationError = void
    export type RevokeTenantEligibleRoleMutationVariables = {id: string;data: AdminReason}

    /**
 * @summary revokeTenantEligibleRole
 */
export const useRevokeTenantEligibleRole = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeTenantEligibleRole>>, TError,RevokeTenantEligibleRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof revokeTenantEligibleRole>>,
        TError,
        RevokeTenantEligibleRoleMutationVariables,
        TContext
      > => {
      return useMutation(getRevokeTenantEligibleRoleMutationOptions(options));
    }

export const getApproveTenantElevationUrl = (id: string,) => {




  return `/api/access/elevations/${id}/approval`
}

/**
 * Requires tenant.roles.assign. Another administrator must approve; self-approval is forbidden.
 * @summary Approve a pending elevation request
 */
export const approveTenantElevation = async (id: string,
    adminReason: AdminReason, options?: Parameters<typeof customFetch>[1]): Promise<AdminElevation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminElevation>(getApproveTenantElevationUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminReason)
  }
);}





export const getApproveTenantElevationMutationKey = () => ['approveTenantElevation'] as const;

export const getApproveTenantElevationMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveTenantElevation>>, TError,ApproveTenantElevationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof approveTenantElevation>>, TError,ApproveTenantElevationMutationVariables, TContext> => {

const mutationKey = getApproveTenantElevationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof approveTenantElevation>>, ApproveTenantElevationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  approveTenantElevation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApproveTenantElevationMutationResult = NonNullable<Awaited<ReturnType<typeof approveTenantElevation>>>
    export type ApproveTenantElevationMutationBody = AdminReason
    export type ApproveTenantElevationMutationError = void
    export type ApproveTenantElevationMutationVariables = {id: string;data: AdminReason}

    /**
 * @summary Approve a pending elevation request
 */
export const useApproveTenantElevation = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveTenantElevation>>, TError,ApproveTenantElevationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof approveTenantElevation>>,
        TError,
        ApproveTenantElevationMutationVariables,
        TContext
      > => {
      return useMutation(getApproveTenantElevationMutationOptions(options));
    }

export const getApprovePlatformElevationUrl = (id: string,) => {




  return `/api/platform/elevations/${id}/approval`
}

/**
 * Requires platform.elevation.manage. Another administrator must approve; self-approval is forbidden.
 * @summary Approve a pending elevation request
 */
export const approvePlatformElevation = async (id: string,
    adminReason: AdminReason, options?: Parameters<typeof customFetch>[1]): Promise<AdminElevation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminElevation>(getApprovePlatformElevationUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminReason)
  }
);}





export const getApprovePlatformElevationMutationKey = () => ['approvePlatformElevation'] as const;

export const getApprovePlatformElevationMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approvePlatformElevation>>, TError,ApprovePlatformElevationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof approvePlatformElevation>>, TError,ApprovePlatformElevationMutationVariables, TContext> => {

const mutationKey = getApprovePlatformElevationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof approvePlatformElevation>>, ApprovePlatformElevationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  approvePlatformElevation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApprovePlatformElevationMutationResult = NonNullable<Awaited<ReturnType<typeof approvePlatformElevation>>>
    export type ApprovePlatformElevationMutationBody = AdminReason
    export type ApprovePlatformElevationMutationError = void
    export type ApprovePlatformElevationMutationVariables = {id: string;data: AdminReason}

    /**
 * @summary Approve a pending elevation request
 */
export const useApprovePlatformElevation = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approvePlatformElevation>>, TError,ApprovePlatformElevationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof approvePlatformElevation>>,
        TError,
        ApprovePlatformElevationMutationVariables,
        TContext
      > => {
      return useMutation(getApprovePlatformElevationMutationOptions(options));
    }

export const getListTenantElevationsUrl = (params?: ListTenantElevationsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/elevations?${stringifiedParams}` : `/api/access/elevations`
}

/**
 * Requires tenant.roles.assign.
 * @summary listTenantElevations
 */
export const listTenantElevations = async (params?: ListTenantElevationsParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminElevationPage> => {

  return customFetch<AdminElevationPage>(getListTenantElevationsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenantElevationsQueryKey = (params?: ListTenantElevationsParams,) => {
    return [
    `/api/access/elevations`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenantElevationsQueryOptions = <TData = Awaited<ReturnType<typeof listTenantElevations>>, TError = void>(params?: ListTenantElevationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantElevations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenantElevationsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenantElevations>>> = ({ signal }) => listTenantElevations(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenantElevations>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenantElevationsQueryResult = NonNullable<Awaited<ReturnType<typeof listTenantElevations>>>
export type ListTenantElevationsQueryError = void


/**
 * @summary listTenantElevations
 */

export function useListTenantElevations<TData = Awaited<ReturnType<typeof listTenantElevations>>, TError = void>(
 params?: ListTenantElevationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantElevations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenantElevationsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRevokeTenantElevationUrl = (id: string,) => {




  return `/api/access/elevations/${id}`
}

/**
 * Requires tenant.roles.assign.
 * @summary revokeTenantElevation
 */
export const revokeTenantElevation = async (id: string,
    adminReason: AdminReason, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getRevokeTenantElevationUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminReason)
  }
);}





export const getRevokeTenantElevationMutationKey = () => ['revokeTenantElevation'] as const;

export const getRevokeTenantElevationMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeTenantElevation>>, TError,RevokeTenantElevationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokeTenantElevation>>, TError,RevokeTenantElevationMutationVariables, TContext> => {

const mutationKey = getRevokeTenantElevationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokeTenantElevation>>, RevokeTenantElevationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  revokeTenantElevation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokeTenantElevationMutationResult = NonNullable<Awaited<ReturnType<typeof revokeTenantElevation>>>
    export type RevokeTenantElevationMutationBody = AdminReason
    export type RevokeTenantElevationMutationError = void
    export type RevokeTenantElevationMutationVariables = {id: string;data: AdminReason}

    /**
 * @summary revokeTenantElevation
 */
export const useRevokeTenantElevation = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeTenantElevation>>, TError,RevokeTenantElevationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof revokeTenantElevation>>,
        TError,
        RevokeTenantElevationMutationVariables,
        TContext
      > => {
      return useMutation(getRevokeTenantElevationMutationOptions(options));
    }

export const getListPlatformSupportSessionsUrl = (params?: ListPlatformSupportSessionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform/support-sessions?${stringifiedParams}` : `/api/platform/support-sessions`
}

/**
 * Requires platform.support.access.
 * @summary listPlatformSupportSessions
 */
export const listPlatformSupportSessions = async (params?: ListPlatformSupportSessionsParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminSupportSessionPage> => {

  return customFetch<AdminSupportSessionPage>(getListPlatformSupportSessionsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformSupportSessionsQueryKey = (params?: ListPlatformSupportSessionsParams,) => {
    return [
    `/api/platform/support-sessions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformSupportSessionsQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformSupportSessions>>, TError = void>(params?: ListPlatformSupportSessionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformSupportSessions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformSupportSessionsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformSupportSessions>>> = ({ signal }) => listPlatformSupportSessions(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformSupportSessions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformSupportSessionsQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformSupportSessions>>>
export type ListPlatformSupportSessionsQueryError = void


/**
 * @summary listPlatformSupportSessions
 */

export function useListPlatformSupportSessions<TData = Awaited<ReturnType<typeof listPlatformSupportSessions>>, TError = void>(
 params?: ListPlatformSupportSessionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformSupportSessions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformSupportSessionsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getStartPlatformSupportSessionUrl = () => {




  return `/api/platform/support-sessions`
}

/**
 * Requires platform.support.access.
 * @summary startPlatformSupportSession
 */
export const startPlatformSupportSession = async (adminSupportStart: AdminSupportStart, options?: Parameters<typeof customFetch>[1]): Promise<AdminSupportSession> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminSupportSession>(getStartPlatformSupportSessionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminSupportStart)
  }
);}





export const getStartPlatformSupportSessionMutationKey = () => ['startPlatformSupportSession'] as const;

export const getStartPlatformSupportSessionMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startPlatformSupportSession>>, TError,StartPlatformSupportSessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof startPlatformSupportSession>>, TError,StartPlatformSupportSessionMutationVariables, TContext> => {

const mutationKey = getStartPlatformSupportSessionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof startPlatformSupportSession>>, StartPlatformSupportSessionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  startPlatformSupportSession(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type StartPlatformSupportSessionMutationResult = NonNullable<Awaited<ReturnType<typeof startPlatformSupportSession>>>
    export type StartPlatformSupportSessionMutationBody = AdminSupportStart
    export type StartPlatformSupportSessionMutationError = void
    export type StartPlatformSupportSessionMutationVariables = {data: AdminSupportStart}

    /**
 * @summary startPlatformSupportSession
 */
export const useStartPlatformSupportSession = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startPlatformSupportSession>>, TError,StartPlatformSupportSessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof startPlatformSupportSession>>,
        TError,
        StartPlatformSupportSessionMutationVariables,
        TContext
      > => {
      return useMutation(getStartPlatformSupportSessionMutationOptions(options));
    }

export const getEndPlatformSupportSessionUrl = (id: string,) => {




  return `/api/platform/support-sessions/${id}`
}

/**
 * Requires platform.support.access.
 * @summary endPlatformSupportSession
 */
export const endPlatformSupportSession = async (id: string,
    adminReason: AdminReason, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getEndPlatformSupportSessionUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminReason)
  }
);}





export const getEndPlatformSupportSessionMutationKey = () => ['endPlatformSupportSession'] as const;

export const getEndPlatformSupportSessionMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof endPlatformSupportSession>>, TError,EndPlatformSupportSessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof endPlatformSupportSession>>, TError,EndPlatformSupportSessionMutationVariables, TContext> => {

const mutationKey = getEndPlatformSupportSessionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof endPlatformSupportSession>>, EndPlatformSupportSessionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  endPlatformSupportSession(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type EndPlatformSupportSessionMutationResult = NonNullable<Awaited<ReturnType<typeof endPlatformSupportSession>>>
    export type EndPlatformSupportSessionMutationBody = AdminReason
    export type EndPlatformSupportSessionMutationError = void
    export type EndPlatformSupportSessionMutationVariables = {id: string;data: AdminReason}

    /**
 * @summary endPlatformSupportSession
 */
export const useEndPlatformSupportSession = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof endPlatformSupportSession>>, TError,EndPlatformSupportSessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof endPlatformSupportSession>>,
        TError,
        EndPlatformSupportSessionMutationVariables,
        TContext
      > => {
      return useMutation(getEndPlatformSupportSessionMutationOptions(options));
    }

export const getGetTenantAdministrationUrl = () => {




  return `/api/access/tenant-administration`
}

/**
 * @summary Read tenant administration profile independently of entity access
 */
export const getTenantAdministration = async ( options?: Parameters<typeof customFetch>[1]): Promise<GetTenantAdministration200> => {

  return customFetch<GetTenantAdministration200>(getGetTenantAdministrationUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenantAdministrationQueryKey = () => {
    return [
    `/api/access/tenant-administration`
    ] as const;
    }


export const getGetTenantAdministrationQueryOptions = <TData = Awaited<ReturnType<typeof getTenantAdministration>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenantAdministration>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenantAdministrationQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenantAdministration>>> = ({ signal }) => getTenantAdministration({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenantAdministration>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTenantAdministrationQueryResult = NonNullable<Awaited<ReturnType<typeof getTenantAdministration>>>
export type GetTenantAdministrationQueryError = unknown


/**
 * @summary Read tenant administration profile independently of entity access
 */

export function useGetTenantAdministration<TData = Awaited<ReturnType<typeof getTenantAdministration>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenantAdministration>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTenantAdministrationQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListAdminBusinessFunctionsUrl = (params?: ListAdminBusinessFunctionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/business-functions?${stringifiedParams}` : `/api/admin/business-functions`
}

/**
 * List tenant business functions, assignments, journey stages, AI action types, and a page of active staff. Requires tenant.members.manage.
 */
export const listAdminBusinessFunctions = async (params?: ListAdminBusinessFunctionsParams, options?: Parameters<typeof customFetch>[1]): Promise<BusinessFunctionDirectory> => {

  return customFetch<BusinessFunctionDirectory>(getListAdminBusinessFunctionsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAdminBusinessFunctionsQueryKey = (params?: ListAdminBusinessFunctionsParams,) => {
    return [
    `/api/admin/business-functions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAdminBusinessFunctionsQueryOptions = <TData = Awaited<ReturnType<typeof listAdminBusinessFunctions>>, TError = void>(params?: ListAdminBusinessFunctionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAdminBusinessFunctions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAdminBusinessFunctionsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAdminBusinessFunctions>>> = ({ signal }) => listAdminBusinessFunctions(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAdminBusinessFunctions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAdminBusinessFunctionsQueryResult = NonNullable<Awaited<ReturnType<typeof listAdminBusinessFunctions>>>
export type ListAdminBusinessFunctionsQueryError = void



export function useListAdminBusinessFunctions<TData = Awaited<ReturnType<typeof listAdminBusinessFunctions>>, TError = void>(
 params?: ListAdminBusinessFunctionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAdminBusinessFunctions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAdminBusinessFunctionsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateAdminBusinessFunctionUrl = () => {




  return `/api/admin/business-functions`
}

/**
 * Create a tenant business function. Requires tenant.members.manage.
 */
export const createAdminBusinessFunction = async (createBusinessFunctionInput: CreateBusinessFunctionInput, options?: Parameters<typeof customFetch>[1]): Promise<BusinessFunction> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<BusinessFunction>(getCreateAdminBusinessFunctionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createBusinessFunctionInput)
  }
);}





export const getCreateAdminBusinessFunctionMutationKey = () => ['createAdminBusinessFunction'] as const;

export const getCreateAdminBusinessFunctionMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAdminBusinessFunction>>, TError,CreateAdminBusinessFunctionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createAdminBusinessFunction>>, TError,CreateAdminBusinessFunctionMutationVariables, TContext> => {

const mutationKey = getCreateAdminBusinessFunctionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAdminBusinessFunction>>, CreateAdminBusinessFunctionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createAdminBusinessFunction(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateAdminBusinessFunctionMutationResult = NonNullable<Awaited<ReturnType<typeof createAdminBusinessFunction>>>
    export type CreateAdminBusinessFunctionMutationBody = CreateBusinessFunctionInput
    export type CreateAdminBusinessFunctionMutationError = void
    export type CreateAdminBusinessFunctionMutationVariables = {data: CreateBusinessFunctionInput}

    export const useCreateAdminBusinessFunction = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAdminBusinessFunction>>, TError,CreateAdminBusinessFunctionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createAdminBusinessFunction>>,
        TError,
        CreateAdminBusinessFunctionMutationVariables,
        TContext
      > => {
      return useMutation(getCreateAdminBusinessFunctionMutationOptions(options));
    }

export const getReorderAdminBusinessFunctionsUrl = () => {




  return `/api/admin/business-functions/order`
}

/**
 * Replace the tenant business-function order. Requires tenant.members.manage.
 */
export const reorderAdminBusinessFunctions = async (reorderBusinessFunctionsInput: ReorderBusinessFunctionsInput, options?: Parameters<typeof customFetch>[1]): Promise<BusinessFunction[]> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<BusinessFunction[]>(getReorderAdminBusinessFunctionsUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reorderBusinessFunctionsInput)
  }
);}





export const getReorderAdminBusinessFunctionsMutationKey = () => ['reorderAdminBusinessFunctions'] as const;

export const getReorderAdminBusinessFunctionsMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reorderAdminBusinessFunctions>>, TError,ReorderAdminBusinessFunctionsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reorderAdminBusinessFunctions>>, TError,ReorderAdminBusinessFunctionsMutationVariables, TContext> => {

const mutationKey = getReorderAdminBusinessFunctionsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reorderAdminBusinessFunctions>>, ReorderAdminBusinessFunctionsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reorderAdminBusinessFunctions(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReorderAdminBusinessFunctionsMutationResult = NonNullable<Awaited<ReturnType<typeof reorderAdminBusinessFunctions>>>
    export type ReorderAdminBusinessFunctionsMutationBody = ReorderBusinessFunctionsInput
    export type ReorderAdminBusinessFunctionsMutationError = void
    export type ReorderAdminBusinessFunctionsMutationVariables = {data: ReorderBusinessFunctionsInput}

    export const useReorderAdminBusinessFunctions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reorderAdminBusinessFunctions>>, TError,ReorderAdminBusinessFunctionsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reorderAdminBusinessFunctions>>,
        TError,
        ReorderAdminBusinessFunctionsMutationVariables,
        TContext
      > => {
      return useMutation(getReorderAdminBusinessFunctionsMutationOptions(options));
    }

export const getUpdateAdminBusinessFunctionUrl = (id: string,) => {




  return `/api/admin/business-functions/${id}`
}

/**
 * Rename or recolour a tenant business function. Requires tenant.members.manage.
 */
export const updateAdminBusinessFunction = async (id: string,
    updateBusinessFunctionInput: UpdateBusinessFunctionInput, options?: Parameters<typeof customFetch>[1]): Promise<BusinessFunction> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<BusinessFunction>(getUpdateAdminBusinessFunctionUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateBusinessFunctionInput)
  }
);}





export const getUpdateAdminBusinessFunctionMutationKey = () => ['updateAdminBusinessFunction'] as const;

export const getUpdateAdminBusinessFunctionMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAdminBusinessFunction>>, TError,UpdateAdminBusinessFunctionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateAdminBusinessFunction>>, TError,UpdateAdminBusinessFunctionMutationVariables, TContext> => {

const mutationKey = getUpdateAdminBusinessFunctionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAdminBusinessFunction>>, UpdateAdminBusinessFunctionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateAdminBusinessFunction(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateAdminBusinessFunctionMutationResult = NonNullable<Awaited<ReturnType<typeof updateAdminBusinessFunction>>>
    export type UpdateAdminBusinessFunctionMutationBody = UpdateBusinessFunctionInput
    export type UpdateAdminBusinessFunctionMutationError = void
    export type UpdateAdminBusinessFunctionMutationVariables = {id: string;data: UpdateBusinessFunctionInput}

    export const useUpdateAdminBusinessFunction = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAdminBusinessFunction>>, TError,UpdateAdminBusinessFunctionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateAdminBusinessFunction>>,
        TError,
        UpdateAdminBusinessFunctionMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateAdminBusinessFunctionMutationOptions(options));
    }

export const getDeleteAdminBusinessFunctionUrl = (id: string,) => {




  return `/api/admin/business-functions/${id}`
}

/**
 * Delete a tenant business function after optionally reassigning its mappings. Requires tenant.members.manage.
 */
export const deleteAdminBusinessFunction = async (id: string,
    deleteBusinessFunctionInput: DeleteBusinessFunctionInput, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getDeleteAdminBusinessFunctionUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(deleteBusinessFunctionInput)
  }
);}





export const getDeleteAdminBusinessFunctionMutationKey = () => ['deleteAdminBusinessFunction'] as const;

export const getDeleteAdminBusinessFunctionMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAdminBusinessFunction>>, TError,DeleteAdminBusinessFunctionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteAdminBusinessFunction>>, TError,DeleteAdminBusinessFunctionMutationVariables, TContext> => {

const mutationKey = getDeleteAdminBusinessFunctionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteAdminBusinessFunction>>, DeleteAdminBusinessFunctionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  deleteAdminBusinessFunction(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteAdminBusinessFunctionMutationResult = NonNullable<Awaited<ReturnType<typeof deleteAdminBusinessFunction>>>
    export type DeleteAdminBusinessFunctionMutationBody = DeleteBusinessFunctionInput
    export type DeleteAdminBusinessFunctionMutationError = void
    export type DeleteAdminBusinessFunctionMutationVariables = {id: string;data: DeleteBusinessFunctionInput}

    export const useDeleteAdminBusinessFunction = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAdminBusinessFunction>>, TError,DeleteAdminBusinessFunctionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteAdminBusinessFunction>>,
        TError,
        DeleteAdminBusinessFunctionMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteAdminBusinessFunctionMutationOptions(options));
    }

export const getReplaceAdminBusinessFunctionAssignmentsUrl = (id: string,) => {




  return `/api/admin/business-functions/${id}/assignments`
}

/**
 * Replace assignments owned by one business function, moving matching references from other functions atomically. Requires tenant.members.manage.
 */
export const replaceAdminBusinessFunctionAssignments = async (id: string,
    replaceBusinessFunctionAssignmentsInput: ReplaceBusinessFunctionAssignmentsInput, options?: Parameters<typeof customFetch>[1]): Promise<BusinessFunctionAssignment[]> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<BusinessFunctionAssignment[]>(getReplaceAdminBusinessFunctionAssignmentsUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(replaceBusinessFunctionAssignmentsInput)
  }
);}





export const getReplaceAdminBusinessFunctionAssignmentsMutationKey = () => ['replaceAdminBusinessFunctionAssignments'] as const;

export const getReplaceAdminBusinessFunctionAssignmentsMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof replaceAdminBusinessFunctionAssignments>>, TError,ReplaceAdminBusinessFunctionAssignmentsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof replaceAdminBusinessFunctionAssignments>>, TError,ReplaceAdminBusinessFunctionAssignmentsMutationVariables, TContext> => {

const mutationKey = getReplaceAdminBusinessFunctionAssignmentsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof replaceAdminBusinessFunctionAssignments>>, ReplaceAdminBusinessFunctionAssignmentsMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  replaceAdminBusinessFunctionAssignments(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReplaceAdminBusinessFunctionAssignmentsMutationResult = NonNullable<Awaited<ReturnType<typeof replaceAdminBusinessFunctionAssignments>>>
    export type ReplaceAdminBusinessFunctionAssignmentsMutationBody = ReplaceBusinessFunctionAssignmentsInput
    export type ReplaceAdminBusinessFunctionAssignmentsMutationError = void
    export type ReplaceAdminBusinessFunctionAssignmentsMutationVariables = {id: string;data: ReplaceBusinessFunctionAssignmentsInput}

    export const useReplaceAdminBusinessFunctionAssignments = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof replaceAdminBusinessFunctionAssignments>>, TError,ReplaceAdminBusinessFunctionAssignmentsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof replaceAdminBusinessFunctionAssignments>>,
        TError,
        ReplaceAdminBusinessFunctionAssignmentsMutationVariables,
        TContext
      > => {
      return useMutation(getReplaceAdminBusinessFunctionAssignmentsMutationOptions(options));
    }

export const getGetPermissionCatalogueUrl = () => {




  return `/api/access/permissions/catalogue`
}

/**
 * Returns the permission catalogue available to the signed-in administrator.
 */
export const getPermissionCatalogue = async ( options?: Parameters<typeof customFetch>[1]): Promise<AdminPermissionCatalogueItem[]> => {

  return customFetch<AdminPermissionCatalogueItem[]>(getGetPermissionCatalogueUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPermissionCatalogueQueryKey = () => {
    return [
    `/api/access/permissions/catalogue`
    ] as const;
    }


export const getGetPermissionCatalogueQueryOptions = <TData = Awaited<ReturnType<typeof getPermissionCatalogue>>, TError = void>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPermissionCatalogue>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPermissionCatalogueQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPermissionCatalogue>>> = ({ signal }) => getPermissionCatalogue({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPermissionCatalogue>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPermissionCatalogueQueryResult = NonNullable<Awaited<ReturnType<typeof getPermissionCatalogue>>>
export type GetPermissionCatalogueQueryError = void



export function useGetPermissionCatalogue<TData = Awaited<ReturnType<typeof getPermissionCatalogue>>, TError = void>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPermissionCatalogue>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPermissionCatalogueQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListTenantRolesUrl = (params?: ListTenantRolesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/tenant/roles?${stringifiedParams}` : `/api/access/tenant/roles`
}

/**
 * Requires tenant.roles.manage, tenant.roles.assign or tenant.groups.manage.
 */
export const listTenantRoles = async (params?: ListTenantRolesParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminRoleDefinitionPage> => {

  return customFetch<AdminRoleDefinitionPage>(getListTenantRolesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenantRolesQueryKey = (params?: ListTenantRolesParams,) => {
    return [
    `/api/access/tenant/roles`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenantRolesQueryOptions = <TData = Awaited<ReturnType<typeof listTenantRoles>>, TError = void>(params?: ListTenantRolesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantRoles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenantRolesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenantRoles>>> = ({ signal }) => listTenantRoles(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenantRoles>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenantRolesQueryResult = NonNullable<Awaited<ReturnType<typeof listTenantRoles>>>
export type ListTenantRolesQueryError = void



export function useListTenantRoles<TData = Awaited<ReturnType<typeof listTenantRoles>>, TError = void>(
 params?: ListTenantRolesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantRoles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenantRolesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateTenantRoleUrl = () => {




  return `/api/access/tenant/roles`
}

/**
 * Requires tenant.roles.manage.
 */
export const createTenantRole = async (adminRoleWrite: AdminRoleWrite, options?: Parameters<typeof customFetch>[1]): Promise<AdminRoleDefinition> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminRoleDefinition>(getCreateTenantRoleUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminRoleWrite)
  }
);}





export const getCreateTenantRoleMutationKey = () => ['createTenantRole'] as const;

export const getCreateTenantRoleMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenantRole>>, TError,CreateTenantRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTenantRole>>, TError,CreateTenantRoleMutationVariables, TContext> => {

const mutationKey = getCreateTenantRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTenantRole>>, CreateTenantRoleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createTenantRole(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTenantRoleMutationResult = NonNullable<Awaited<ReturnType<typeof createTenantRole>>>
    export type CreateTenantRoleMutationBody = AdminRoleWrite
    export type CreateTenantRoleMutationError = void
    export type CreateTenantRoleMutationVariables = {data: AdminRoleWrite}

    export const useCreateTenantRole = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenantRole>>, TError,CreateTenantRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createTenantRole>>,
        TError,
        CreateTenantRoleMutationVariables,
        TContext
      > => {
      return useMutation(getCreateTenantRoleMutationOptions(options));
    }

export const getUpdateTenantRoleUrl = (id: string,) => {




  return `/api/access/tenant/roles/${id}`
}

/**
 * Requires tenant.roles.manage.
 */
export const updateTenantRole = async (id: string,
    adminRoleWrite: AdminRoleWrite, options?: Parameters<typeof customFetch>[1]): Promise<AdminRoleDefinition> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminRoleDefinition>(getUpdateTenantRoleUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminRoleWrite)
  }
);}





export const getUpdateTenantRoleMutationKey = () => ['updateTenantRole'] as const;

export const getUpdateTenantRoleMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenantRole>>, TError,UpdateTenantRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateTenantRole>>, TError,UpdateTenantRoleMutationVariables, TContext> => {

const mutationKey = getUpdateTenantRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateTenantRole>>, UpdateTenantRoleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateTenantRole(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateTenantRoleMutationResult = NonNullable<Awaited<ReturnType<typeof updateTenantRole>>>
    export type UpdateTenantRoleMutationBody = AdminRoleWrite
    export type UpdateTenantRoleMutationError = void
    export type UpdateTenantRoleMutationVariables = {id: string;data: AdminRoleWrite}

    export const useUpdateTenantRole = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenantRole>>, TError,UpdateTenantRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateTenantRole>>,
        TError,
        UpdateTenantRoleMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateTenantRoleMutationOptions(options));
    }

export const getDeleteTenantRoleUrl = (id: string,) => {




  return `/api/access/tenant/roles/${id}`
}

/**
 * Requires tenant.roles.manage.
 */
export const deleteTenantRole = async (id: string,
    adminReason: AdminReason, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getDeleteTenantRoleUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminReason)
  }
);}





export const getDeleteTenantRoleMutationKey = () => ['deleteTenantRole'] as const;

export const getDeleteTenantRoleMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTenantRole>>, TError,DeleteTenantRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteTenantRole>>, TError,DeleteTenantRoleMutationVariables, TContext> => {

const mutationKey = getDeleteTenantRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteTenantRole>>, DeleteTenantRoleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  deleteTenantRole(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteTenantRoleMutationResult = NonNullable<Awaited<ReturnType<typeof deleteTenantRole>>>
    export type DeleteTenantRoleMutationBody = AdminReason
    export type DeleteTenantRoleMutationError = void
    export type DeleteTenantRoleMutationVariables = {id: string;data: AdminReason}

    export const useDeleteTenantRole = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTenantRole>>, TError,DeleteTenantRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteTenantRole>>,
        TError,
        DeleteTenantRoleMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteTenantRoleMutationOptions(options));
    }

export const getListTenantGroupsUrl = (params?: ListTenantGroupsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/tenant/groups?${stringifiedParams}` : `/api/access/tenant/groups`
}

/**
 * Requires tenant.groups.manage.
 */
export const listTenantGroups = async (params?: ListTenantGroupsParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminAccessGroupPage> => {

  return customFetch<AdminAccessGroupPage>(getListTenantGroupsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenantGroupsQueryKey = (params?: ListTenantGroupsParams,) => {
    return [
    `/api/access/tenant/groups`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenantGroupsQueryOptions = <TData = Awaited<ReturnType<typeof listTenantGroups>>, TError = void>(params?: ListTenantGroupsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantGroups>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenantGroupsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenantGroups>>> = ({ signal }) => listTenantGroups(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenantGroups>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenantGroupsQueryResult = NonNullable<Awaited<ReturnType<typeof listTenantGroups>>>
export type ListTenantGroupsQueryError = void



export function useListTenantGroups<TData = Awaited<ReturnType<typeof listTenantGroups>>, TError = void>(
 params?: ListTenantGroupsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantGroups>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenantGroupsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateTenantGroupUrl = () => {




  return `/api/access/tenant/groups`
}

/**
 * Requires tenant.groups.manage.
 */
export const createTenantGroup = async (adminAccessGroupWrite: AdminAccessGroupWrite, options?: Parameters<typeof customFetch>[1]): Promise<AdminAccessGroup> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminAccessGroup>(getCreateTenantGroupUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminAccessGroupWrite)
  }
);}





export const getCreateTenantGroupMutationKey = () => ['createTenantGroup'] as const;

export const getCreateTenantGroupMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenantGroup>>, TError,CreateTenantGroupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTenantGroup>>, TError,CreateTenantGroupMutationVariables, TContext> => {

const mutationKey = getCreateTenantGroupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTenantGroup>>, CreateTenantGroupMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createTenantGroup(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTenantGroupMutationResult = NonNullable<Awaited<ReturnType<typeof createTenantGroup>>>
    export type CreateTenantGroupMutationBody = AdminAccessGroupWrite
    export type CreateTenantGroupMutationError = void
    export type CreateTenantGroupMutationVariables = {data: AdminAccessGroupWrite}

    export const useCreateTenantGroup = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenantGroup>>, TError,CreateTenantGroupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createTenantGroup>>,
        TError,
        CreateTenantGroupMutationVariables,
        TContext
      > => {
      return useMutation(getCreateTenantGroupMutationOptions(options));
    }

export const getUpdateTenantGroupUrl = (id: string,) => {




  return `/api/access/tenant/groups/${id}`
}

/**
 * Requires tenant.groups.manage.
 */
export const updateTenantGroup = async (id: string,
    adminAccessGroupWrite: AdminAccessGroupWrite, options?: Parameters<typeof customFetch>[1]): Promise<AdminAccessGroup> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminAccessGroup>(getUpdateTenantGroupUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminAccessGroupWrite)
  }
);}





export const getUpdateTenantGroupMutationKey = () => ['updateTenantGroup'] as const;

export const getUpdateTenantGroupMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenantGroup>>, TError,UpdateTenantGroupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateTenantGroup>>, TError,UpdateTenantGroupMutationVariables, TContext> => {

const mutationKey = getUpdateTenantGroupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateTenantGroup>>, UpdateTenantGroupMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateTenantGroup(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateTenantGroupMutationResult = NonNullable<Awaited<ReturnType<typeof updateTenantGroup>>>
    export type UpdateTenantGroupMutationBody = AdminAccessGroupWrite
    export type UpdateTenantGroupMutationError = void
    export type UpdateTenantGroupMutationVariables = {id: string;data: AdminAccessGroupWrite}

    export const useUpdateTenantGroup = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenantGroup>>, TError,UpdateTenantGroupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateTenantGroup>>,
        TError,
        UpdateTenantGroupMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateTenantGroupMutationOptions(options));
    }

export const getDeleteTenantGroupUrl = (id: string,) => {




  return `/api/access/tenant/groups/${id}`
}

/**
 * Requires tenant.groups.manage.
 */
export const deleteTenantGroup = async (id: string,
    adminReason: AdminReason, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getDeleteTenantGroupUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminReason)
  }
);}





export const getDeleteTenantGroupMutationKey = () => ['deleteTenantGroup'] as const;

export const getDeleteTenantGroupMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTenantGroup>>, TError,DeleteTenantGroupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteTenantGroup>>, TError,DeleteTenantGroupMutationVariables, TContext> => {

const mutationKey = getDeleteTenantGroupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteTenantGroup>>, DeleteTenantGroupMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  deleteTenantGroup(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteTenantGroupMutationResult = NonNullable<Awaited<ReturnType<typeof deleteTenantGroup>>>
    export type DeleteTenantGroupMutationBody = AdminReason
    export type DeleteTenantGroupMutationError = void
    export type DeleteTenantGroupMutationVariables = {id: string;data: AdminReason}

    export const useDeleteTenantGroup = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTenantGroup>>, TError,DeleteTenantGroupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteTenantGroup>>,
        TError,
        DeleteTenantGroupMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteTenantGroupMutationOptions(options));
    }

export const getListTenantGroupMembersUrl = (params?: ListTenantGroupMembersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/tenant/groups/members?${stringifiedParams}` : `/api/access/tenant/groups/members`
}

/**
 * Search active tenant members available for group membership.
 */
export const listTenantGroupMembers = async (params?: ListTenantGroupMembersParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminGroupMemberPage> => {

  return customFetch<AdminGroupMemberPage>(getListTenantGroupMembersUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenantGroupMembersQueryKey = (params?: ListTenantGroupMembersParams,) => {
    return [
    `/api/access/tenant/groups/members`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenantGroupMembersQueryOptions = <TData = Awaited<ReturnType<typeof listTenantGroupMembers>>, TError = void>(params?: ListTenantGroupMembersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantGroupMembers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenantGroupMembersQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenantGroupMembers>>> = ({ signal }) => listTenantGroupMembers(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenantGroupMembers>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenantGroupMembersQueryResult = NonNullable<Awaited<ReturnType<typeof listTenantGroupMembers>>>
export type ListTenantGroupMembersQueryError = void



export function useListTenantGroupMembers<TData = Awaited<ReturnType<typeof listTenantGroupMembers>>, TError = void>(
 params?: ListTenantGroupMembersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantGroupMembers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenantGroupMembersQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetEntityAdministrationUrl = (params?: GetEntityAdministrationParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/entity-administration?${stringifiedParams}` : `/api/access/entity-administration`
}

/**
 * @summary Read selected entity profile and permission-filtered administration data
 */
export const getEntityAdministration = async (params?: GetEntityAdministrationParams, options?: Parameters<typeof customFetch>[1]): Promise<EntityAdministrationPage> => {

  return customFetch<EntityAdministrationPage>(getGetEntityAdministrationUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEntityAdministrationQueryKey = (params?: GetEntityAdministrationParams,) => {
    return [
    `/api/access/entity-administration`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetEntityAdministrationQueryOptions = <TData = Awaited<ReturnType<typeof getEntityAdministration>>, TError = unknown>(params?: GetEntityAdministrationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityAdministration>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEntityAdministrationQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEntityAdministration>>> = ({ signal }) => getEntityAdministration(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEntityAdministration>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEntityAdministrationQueryResult = NonNullable<Awaited<ReturnType<typeof getEntityAdministration>>>
export type GetEntityAdministrationQueryError = unknown


/**
 * @summary Read selected entity profile and permission-filtered administration data
 */

export function useGetEntityAdministration<TData = Awaited<ReturnType<typeof getEntityAdministration>>, TError = unknown>(
 params?: GetEntityAdministrationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityAdministration>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEntityAdministrationQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListEntityAccessCandidatesUrl = (params?: ListEntityAccessCandidatesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/entity/members/candidates?${stringifiedParams}` : `/api/access/entity/members/candidates`
}

/**
 * @summary Search active tenant members without access to the selected entity
 */
export const listEntityAccessCandidates = async (params?: ListEntityAccessCandidatesParams, options?: Parameters<typeof customFetch>[1]): Promise<EntityAccessCandidatePage> => {

  return customFetch<EntityAccessCandidatePage>(getListEntityAccessCandidatesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListEntityAccessCandidatesQueryKey = (params?: ListEntityAccessCandidatesParams,) => {
    return [
    `/api/access/entity/members/candidates`, ...(params ? [params] : [])
    ] as const;
    }


export const getListEntityAccessCandidatesQueryOptions = <TData = Awaited<ReturnType<typeof listEntityAccessCandidates>>, TError = Error>(params?: ListEntityAccessCandidatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityAccessCandidates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEntityAccessCandidatesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEntityAccessCandidates>>> = ({ signal }) => listEntityAccessCandidates(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEntityAccessCandidates>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEntityAccessCandidatesQueryResult = NonNullable<Awaited<ReturnType<typeof listEntityAccessCandidates>>>
export type ListEntityAccessCandidatesQueryError = Error


/**
 * @summary Search active tenant members without access to the selected entity
 */

export function useListEntityAccessCandidates<TData = Awaited<ReturnType<typeof listEntityAccessCandidates>>, TError = Error>(
 params?: ListEntityAccessCandidatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityAccessCandidates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEntityAccessCandidatesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateEntityAccessUrl = () => {




  return `/api/access/entity/grants`
}

/**
 * @summary Grant an existing tenant member or invite an email to the selected entity
 */
export const createEntityAccess = async (entityAccessCreate: EntityAccessCreate, options?: Parameters<typeof customFetch>[1]): Promise<EntityAccessCreateResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EntityAccessCreateResult>(getCreateEntityAccessUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(entityAccessCreate)
  }
);}





export const getCreateEntityAccessMutationKey = () => ['createEntityAccess'] as const;

export const getCreateEntityAccessMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEntityAccess>>, TError,CreateEntityAccessMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createEntityAccess>>, TError,CreateEntityAccessMutationVariables, TContext> => {

const mutationKey = getCreateEntityAccessMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createEntityAccess>>, CreateEntityAccessMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createEntityAccess(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateEntityAccessMutationResult = NonNullable<Awaited<ReturnType<typeof createEntityAccess>>>
    export type CreateEntityAccessMutationBody = EntityAccessCreate
    export type CreateEntityAccessMutationError = Error
    export type CreateEntityAccessMutationVariables = {data: EntityAccessCreate}

    /**
 * @summary Grant an existing tenant member or invite an email to the selected entity
 */
export const useCreateEntityAccess = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEntityAccess>>, TError,CreateEntityAccessMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createEntityAccess>>,
        TError,
        CreateEntityAccessMutationVariables,
        TContext
      > => {
      return useMutation(getCreateEntityAccessMutationOptions(options));
    }

export const getUpdateEntityAccessGrantUrl = (id: string,) => {




  return `/api/access/entity/grants/${id}`
}

/**
 * @summary Replace the entity roles assigned through an access grant
 */
export const updateEntityAccessGrant = async (id: string,
    entityAccessRolesUpdate: EntityAccessRolesUpdate, options?: Parameters<typeof customFetch>[1]): Promise<AccessMutationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccessMutationResult>(getUpdateEntityAccessGrantUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(entityAccessRolesUpdate)
  }
);}





export const getUpdateEntityAccessGrantMutationKey = () => ['updateEntityAccessGrant'] as const;

export const getUpdateEntityAccessGrantMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEntityAccessGrant>>, TError,UpdateEntityAccessGrantMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateEntityAccessGrant>>, TError,UpdateEntityAccessGrantMutationVariables, TContext> => {

const mutationKey = getUpdateEntityAccessGrantMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateEntityAccessGrant>>, UpdateEntityAccessGrantMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateEntityAccessGrant(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateEntityAccessGrantMutationResult = NonNullable<Awaited<ReturnType<typeof updateEntityAccessGrant>>>
    export type UpdateEntityAccessGrantMutationBody = EntityAccessRolesUpdate
    export type UpdateEntityAccessGrantMutationError = Error
    export type UpdateEntityAccessGrantMutationVariables = {id: string;data: EntityAccessRolesUpdate}

    /**
 * @summary Replace the entity roles assigned through an access grant
 */
export const useUpdateEntityAccessGrant = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEntityAccessGrant>>, TError,UpdateEntityAccessGrantMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateEntityAccessGrant>>,
        TError,
        UpdateEntityAccessGrantMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateEntityAccessGrantMutationOptions(options));
    }

export const getDeleteEntityAccessGrantUrl = (id: string,) => {




  return `/api/access/entity/grants/${id}`
}

/**
 * @summary Revoke an entity access grant and its entity roles
 */
export const deleteEntityAccessGrant = async (id: string,
    accessReason: AccessReason, options?: Parameters<typeof customFetch>[1]): Promise<AccessMutationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccessMutationResult>(getDeleteEntityAccessGrantUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accessReason)
  }
);}





export const getDeleteEntityAccessGrantMutationKey = () => ['deleteEntityAccessGrant'] as const;

export const getDeleteEntityAccessGrantMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteEntityAccessGrant>>, TError,DeleteEntityAccessGrantMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteEntityAccessGrant>>, TError,DeleteEntityAccessGrantMutationVariables, TContext> => {

const mutationKey = getDeleteEntityAccessGrantMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteEntityAccessGrant>>, DeleteEntityAccessGrantMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  deleteEntityAccessGrant(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteEntityAccessGrantMutationResult = NonNullable<Awaited<ReturnType<typeof deleteEntityAccessGrant>>>
    export type DeleteEntityAccessGrantMutationBody = AccessReason
    export type DeleteEntityAccessGrantMutationError = Error
    export type DeleteEntityAccessGrantMutationVariables = {id: string;data: AccessReason}

    /**
 * @summary Revoke an entity access grant and its entity roles
 */
export const useDeleteEntityAccessGrant = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteEntityAccessGrant>>, TError,DeleteEntityAccessGrantMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteEntityAccessGrant>>,
        TError,
        DeleteEntityAccessGrantMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteEntityAccessGrantMutationOptions(options));
    }

export const getResendEntityAccessInvitationUrl = (id: string,) => {




  return `/api/access/entity/invitations/${id}/resend`
}

/**
 * @summary Resend a pending invitation containing access to the selected entity
 */
export const resendEntityAccessInvitation = async (id: string,
    accessReason: AccessReason, options?: Parameters<typeof customFetch>[1]): Promise<EntityAccessInvitationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EntityAccessInvitationResult>(getResendEntityAccessInvitationUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accessReason)
  }
);}





export const getResendEntityAccessInvitationMutationKey = () => ['resendEntityAccessInvitation'] as const;

export const getResendEntityAccessInvitationMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resendEntityAccessInvitation>>, TError,ResendEntityAccessInvitationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof resendEntityAccessInvitation>>, TError,ResendEntityAccessInvitationMutationVariables, TContext> => {

const mutationKey = getResendEntityAccessInvitationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof resendEntityAccessInvitation>>, ResendEntityAccessInvitationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  resendEntityAccessInvitation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResendEntityAccessInvitationMutationResult = NonNullable<Awaited<ReturnType<typeof resendEntityAccessInvitation>>>
    export type ResendEntityAccessInvitationMutationBody = AccessReason
    export type ResendEntityAccessInvitationMutationError = Error
    export type ResendEntityAccessInvitationMutationVariables = {id: string;data: AccessReason}

    /**
 * @summary Resend a pending invitation containing access to the selected entity
 */
export const useResendEntityAccessInvitation = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resendEntityAccessInvitation>>, TError,ResendEntityAccessInvitationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof resendEntityAccessInvitation>>,
        TError,
        ResendEntityAccessInvitationMutationVariables,
        TContext
      > => {
      return useMutation(getResendEntityAccessInvitationMutationOptions(options));
    }

export const getCancelEntityAccessInvitationUrl = (id: string,) => {




  return `/api/access/entity/invitations/${id}`
}

/**
 * @summary Cancel access to the selected entity from a pending invitation
 */
export const cancelEntityAccessInvitation = async (id: string,
    accessReason: AccessReason, options?: Parameters<typeof customFetch>[1]): Promise<AccessMutationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccessMutationResult>(getCancelEntityAccessInvitationUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accessReason)
  }
);}





export const getCancelEntityAccessInvitationMutationKey = () => ['cancelEntityAccessInvitation'] as const;

export const getCancelEntityAccessInvitationMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelEntityAccessInvitation>>, TError,CancelEntityAccessInvitationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelEntityAccessInvitation>>, TError,CancelEntityAccessInvitationMutationVariables, TContext> => {

const mutationKey = getCancelEntityAccessInvitationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelEntityAccessInvitation>>, CancelEntityAccessInvitationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  cancelEntityAccessInvitation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelEntityAccessInvitationMutationResult = NonNullable<Awaited<ReturnType<typeof cancelEntityAccessInvitation>>>
    export type CancelEntityAccessInvitationMutationBody = AccessReason
    export type CancelEntityAccessInvitationMutationError = Error
    export type CancelEntityAccessInvitationMutationVariables = {id: string;data: AccessReason}

    /**
 * @summary Cancel access to the selected entity from a pending invitation
 */
export const useCancelEntityAccessInvitation = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelEntityAccessInvitation>>, TError,CancelEntityAccessInvitationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelEntityAccessInvitation>>,
        TError,
        CancelEntityAccessInvitationMutationVariables,
        TContext
      > => {
      return useMutation(getCancelEntityAccessInvitationMutationOptions(options));
    }

export const getListTenantEntitiesUrl = (params?: ListTenantEntitiesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/entities?${stringifiedParams}` : `/api/access/entities`
}

/**
 * Requires tenant.roles.manage.
 * @summary listTenantEntities
 */
export const listTenantEntities = async (params?: ListTenantEntitiesParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminEntityPage> => {

  return customFetch<AdminEntityPage>(getListTenantEntitiesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenantEntitiesQueryKey = (params?: ListTenantEntitiesParams,) => {
    return [
    `/api/access/entities`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenantEntitiesQueryOptions = <TData = Awaited<ReturnType<typeof listTenantEntities>>, TError = void>(params?: ListTenantEntitiesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantEntities>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenantEntitiesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenantEntities>>> = ({ signal }) => listTenantEntities(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenantEntities>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenantEntitiesQueryResult = NonNullable<Awaited<ReturnType<typeof listTenantEntities>>>
export type ListTenantEntitiesQueryError = void


/**
 * @summary listTenantEntities
 */

export function useListTenantEntities<TData = Awaited<ReturnType<typeof listTenantEntities>>, TError = void>(
 params?: ListTenantEntitiesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantEntities>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenantEntitiesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateTenantEntityUrl = () => {




  return `/api/access/entities`
}

/**
 * Requires entity.manage and tenant owner.
 * @summary createTenantEntity
 */
export const createTenantEntity = async (adminCreateEntity: AdminCreateEntity, options?: Parameters<typeof customFetch>[1]): Promise<AdminEntity> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminEntity>(getCreateTenantEntityUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminCreateEntity)
  }
);}





export const getCreateTenantEntityMutationKey = () => ['createTenantEntity'] as const;

export const getCreateTenantEntityMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenantEntity>>, TError,CreateTenantEntityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTenantEntity>>, TError,CreateTenantEntityMutationVariables, TContext> => {

const mutationKey = getCreateTenantEntityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTenantEntity>>, CreateTenantEntityMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createTenantEntity(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTenantEntityMutationResult = NonNullable<Awaited<ReturnType<typeof createTenantEntity>>>
    export type CreateTenantEntityMutationBody = AdminCreateEntity
    export type CreateTenantEntityMutationError = void
    export type CreateTenantEntityMutationVariables = {data: AdminCreateEntity}

    /**
 * @summary createTenantEntity
 */
export const useCreateTenantEntity = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenantEntity>>, TError,CreateTenantEntityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createTenantEntity>>,
        TError,
        CreateTenantEntityMutationVariables,
        TContext
      > => {
      return useMutation(getCreateTenantEntityMutationOptions(options));
    }

export const getUpdateTenantEntityUrl = (id: string,) => {




  return `/api/access/entities/${id}`
}

/**
 * Requires entity.manage and tenant owner.
 * @summary updateTenantEntity
 */
export const updateTenantEntity = async (id: string,
    adminUpdateEntity: AdminUpdateEntity, options?: Parameters<typeof customFetch>[1]): Promise<AccessMutationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccessMutationResult>(getUpdateTenantEntityUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminUpdateEntity)
  }
);}





export const getUpdateTenantEntityMutationKey = () => ['updateTenantEntity'] as const;

export const getUpdateTenantEntityMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenantEntity>>, TError,UpdateTenantEntityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateTenantEntity>>, TError,UpdateTenantEntityMutationVariables, TContext> => {

const mutationKey = getUpdateTenantEntityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateTenantEntity>>, UpdateTenantEntityMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateTenantEntity(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateTenantEntityMutationResult = NonNullable<Awaited<ReturnType<typeof updateTenantEntity>>>
    export type UpdateTenantEntityMutationBody = AdminUpdateEntity
    export type UpdateTenantEntityMutationError = void
    export type UpdateTenantEntityMutationVariables = {id: string;data: AdminUpdateEntity}

    /**
 * @summary updateTenantEntity
 */
export const useUpdateTenantEntity = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenantEntity>>, TError,UpdateTenantEntityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateTenantEntity>>,
        TError,
        UpdateTenantEntityMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateTenantEntityMutationOptions(options));
    }

export const getPreviewTenantEntityTransferUrl = (id: string,) => {




  return `/api/access/entities/${id}/transfer-preview`
}

/**
 * Requires entity.transfer in both tenants.
 * @summary previewTenantEntityTransfer
 */
export const previewTenantEntityTransfer = async (id: string,
    adminTransferPreviewInput: AdminTransferPreviewInput, options?: Parameters<typeof customFetch>[1]): Promise<AdminTransferPreview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminTransferPreview>(getPreviewTenantEntityTransferUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminTransferPreviewInput)
  }
);}





export const getPreviewTenantEntityTransferMutationKey = () => ['previewTenantEntityTransfer'] as const;

export const getPreviewTenantEntityTransferMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewTenantEntityTransfer>>, TError,PreviewTenantEntityTransferMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewTenantEntityTransfer>>, TError,PreviewTenantEntityTransferMutationVariables, TContext> => {

const mutationKey = getPreviewTenantEntityTransferMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewTenantEntityTransfer>>, PreviewTenantEntityTransferMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  previewTenantEntityTransfer(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewTenantEntityTransferMutationResult = NonNullable<Awaited<ReturnType<typeof previewTenantEntityTransfer>>>
    export type PreviewTenantEntityTransferMutationBody = AdminTransferPreviewInput
    export type PreviewTenantEntityTransferMutationError = void
    export type PreviewTenantEntityTransferMutationVariables = {id: string;data: AdminTransferPreviewInput}

    /**
 * @summary previewTenantEntityTransfer
 */
export const usePreviewTenantEntityTransfer = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewTenantEntityTransfer>>, TError,PreviewTenantEntityTransferMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewTenantEntityTransfer>>,
        TError,
        PreviewTenantEntityTransferMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewTenantEntityTransferMutationOptions(options));
    }

export const getTransferTenantEntityUrl = (id: string,) => {




  return `/api/access/entities/${id}/transfer`
}

/**
 * Requires entity.transfer in both tenants.
 * @summary transferTenantEntity
 */
export const transferTenantEntity = async (id: string,
    adminTransferInput: AdminTransferInput, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getTransferTenantEntityUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminTransferInput)
  }
);}





export const getTransferTenantEntityMutationKey = () => ['transferTenantEntity'] as const;

export const getTransferTenantEntityMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof transferTenantEntity>>, TError,TransferTenantEntityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof transferTenantEntity>>, TError,TransferTenantEntityMutationVariables, TContext> => {

const mutationKey = getTransferTenantEntityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof transferTenantEntity>>, TransferTenantEntityMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  transferTenantEntity(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TransferTenantEntityMutationResult = NonNullable<Awaited<ReturnType<typeof transferTenantEntity>>>
    export type TransferTenantEntityMutationBody = AdminTransferInput
    export type TransferTenantEntityMutationError = void
    export type TransferTenantEntityMutationVariables = {id: string;data: AdminTransferInput}

    /**
 * @summary transferTenantEntity
 */
export const useTransferTenantEntity = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof transferTenantEntity>>, TError,TransferTenantEntityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof transferTenantEntity>>,
        TError,
        TransferTenantEntityMutationVariables,
        TContext
      > => {
      return useMutation(getTransferTenantEntityMutationOptions(options));
    }

export const getListTenantSettingsUrl = (params?: ListTenantSettingsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/settings?${stringifiedParams}` : `/api/access/settings`
}

/**
 * Requires tenant.settings.read.
 * @summary listTenantSettings
 */
export const listTenantSettings = async (params?: ListTenantSettingsParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminSettingPage> => {

  return customFetch<AdminSettingPage>(getListTenantSettingsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenantSettingsQueryKey = (params?: ListTenantSettingsParams,) => {
    return [
    `/api/access/settings`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenantSettingsQueryOptions = <TData = Awaited<ReturnType<typeof listTenantSettings>>, TError = void>(params?: ListTenantSettingsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenantSettingsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenantSettings>>> = ({ signal }) => listTenantSettings(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenantSettings>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenantSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof listTenantSettings>>>
export type ListTenantSettingsQueryError = void


/**
 * @summary listTenantSettings
 */

export function useListTenantSettings<TData = Awaited<ReturnType<typeof listTenantSettings>>, TError = void>(
 params?: ListTenantSettingsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenantSettingsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getWriteTenantSettingUrl = () => {




  return `/api/access/settings`
}

/**
 * Requires tenant.settings.manage.
 * @summary writeTenantSetting
 */
export const writeTenantSetting = async (adminSettingWrite: AdminSettingWrite, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getWriteTenantSettingUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminSettingWrite)
  }
);}





export const getWriteTenantSettingMutationKey = () => ['writeTenantSetting'] as const;

export const getWriteTenantSettingMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writeTenantSetting>>, TError,WriteTenantSettingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof writeTenantSetting>>, TError,WriteTenantSettingMutationVariables, TContext> => {

const mutationKey = getWriteTenantSettingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof writeTenantSetting>>, WriteTenantSettingMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  writeTenantSetting(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type WriteTenantSettingMutationResult = NonNullable<Awaited<ReturnType<typeof writeTenantSetting>>>
    export type WriteTenantSettingMutationBody = AdminSettingWrite
    export type WriteTenantSettingMutationError = void
    export type WriteTenantSettingMutationVariables = {data: AdminSettingWrite}

    /**
 * @summary writeTenantSetting
 */
export const useWriteTenantSetting = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writeTenantSetting>>, TError,WriteTenantSettingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof writeTenantSetting>>,
        TError,
        WriteTenantSettingMutationVariables,
        TContext
      > => {
      return useMutation(getWriteTenantSettingMutationOptions(options));
    }

export const getListTenantModulesUrl = (params?: ListTenantModulesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/modules?${stringifiedParams}` : `/api/access/modules`
}

/**
 * Requires tenant.settings.read.
 * @summary listTenantModules
 */
export const listTenantModules = async (params?: ListTenantModulesParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminModulePage> => {

  return customFetch<AdminModulePage>(getListTenantModulesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenantModulesQueryKey = (params?: ListTenantModulesParams,) => {
    return [
    `/api/access/modules`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenantModulesQueryOptions = <TData = Awaited<ReturnType<typeof listTenantModules>>, TError = void>(params?: ListTenantModulesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantModules>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenantModulesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenantModules>>> = ({ signal }) => listTenantModules(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenantModules>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenantModulesQueryResult = NonNullable<Awaited<ReturnType<typeof listTenantModules>>>
export type ListTenantModulesQueryError = void


/**
 * @summary listTenantModules
 */

export function useListTenantModules<TData = Awaited<ReturnType<typeof listTenantModules>>, TError = void>(
 params?: ListTenantModulesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantModules>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenantModulesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getWriteTenantModuleUrl = () => {




  return `/api/access/modules`
}

/**
 * Requires tenant.settings.manage.
 * @summary writeTenantModule
 */
export const writeTenantModule = async (adminModuleWrite: AdminModuleWrite, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getWriteTenantModuleUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminModuleWrite)
  }
);}





export const getWriteTenantModuleMutationKey = () => ['writeTenantModule'] as const;

export const getWriteTenantModuleMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writeTenantModule>>, TError,WriteTenantModuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof writeTenantModule>>, TError,WriteTenantModuleMutationVariables, TContext> => {

const mutationKey = getWriteTenantModuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof writeTenantModule>>, WriteTenantModuleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  writeTenantModule(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type WriteTenantModuleMutationResult = NonNullable<Awaited<ReturnType<typeof writeTenantModule>>>
    export type WriteTenantModuleMutationBody = AdminModuleWrite
    export type WriteTenantModuleMutationError = void
    export type WriteTenantModuleMutationVariables = {data: AdminModuleWrite}

    /**
 * @summary writeTenantModule
 */
export const useWriteTenantModule = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writeTenantModule>>, TError,WriteTenantModuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof writeTenantModule>>,
        TError,
        WriteTenantModuleMutationVariables,
        TContext
      > => {
      return useMutation(getWriteTenantModuleMutationOptions(options));
    }

export const getGetTenantUsageTokenSummaryUrl = () => {




  return `/api/access/tenant/usage-tokens/summary`
}

/**
 * Requires tenant.tokens.read or tenant.tokens.manage.
 * @summary Get the selected tenant usage token balance
 */
export const getTenantUsageTokenSummary = async ( options?: Parameters<typeof customFetch>[1]): Promise<TenantUsageTokenSummary> => {

  return customFetch<TenantUsageTokenSummary>(getGetTenantUsageTokenSummaryUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenantUsageTokenSummaryQueryKey = () => {
    return [
    `/api/access/tenant/usage-tokens/summary`
    ] as const;
    }


export const getGetTenantUsageTokenSummaryQueryOptions = <TData = Awaited<ReturnType<typeof getTenantUsageTokenSummary>>, TError = void>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenantUsageTokenSummary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenantUsageTokenSummaryQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenantUsageTokenSummary>>> = ({ signal }) => getTenantUsageTokenSummary({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenantUsageTokenSummary>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTenantUsageTokenSummaryQueryResult = NonNullable<Awaited<ReturnType<typeof getTenantUsageTokenSummary>>>
export type GetTenantUsageTokenSummaryQueryError = void


/**
 * @summary Get the selected tenant usage token balance
 */

export function useGetTenantUsageTokenSummary<TData = Awaited<ReturnType<typeof getTenantUsageTokenSummary>>, TError = void>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenantUsageTokenSummary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTenantUsageTokenSummaryQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListTenantUsageTokenAllocationsUrl = (params?: ListTenantUsageTokenAllocationsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/tenant/usage-tokens/allocations?${stringifiedParams}` : `/api/access/tenant/usage-tokens/allocations`
}

/**
 * Requires tenant.tokens.read or tenant.tokens.manage.
 * @summary List per-entity usage token allocations
 */
export const listTenantUsageTokenAllocations = async (params?: ListTenantUsageTokenAllocationsParams, options?: Parameters<typeof customFetch>[1]): Promise<UsageTokenAllocationPage> => {

  return customFetch<UsageTokenAllocationPage>(getListTenantUsageTokenAllocationsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenantUsageTokenAllocationsQueryKey = (params?: ListTenantUsageTokenAllocationsParams,) => {
    return [
    `/api/access/tenant/usage-tokens/allocations`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenantUsageTokenAllocationsQueryOptions = <TData = Awaited<ReturnType<typeof listTenantUsageTokenAllocations>>, TError = void>(params?: ListTenantUsageTokenAllocationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantUsageTokenAllocations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenantUsageTokenAllocationsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenantUsageTokenAllocations>>> = ({ signal }) => listTenantUsageTokenAllocations(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenantUsageTokenAllocations>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenantUsageTokenAllocationsQueryResult = NonNullable<Awaited<ReturnType<typeof listTenantUsageTokenAllocations>>>
export type ListTenantUsageTokenAllocationsQueryError = void


/**
 * @summary List per-entity usage token allocations
 */

export function useListTenantUsageTokenAllocations<TData = Awaited<ReturnType<typeof listTenantUsageTokenAllocations>>, TError = void>(
 params?: ListTenantUsageTokenAllocationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantUsageTokenAllocations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenantUsageTokenAllocationsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSetTenantUsageTokenAllocationUrl = (entityId: string,) => {




  return `/api/access/tenant/usage-tokens/allocations/${entityId}`
}

/**
 * Requires tenant.tokens.manage.
 * @summary Set an entity usage token allocation
 */
export const setTenantUsageTokenAllocation = async (entityId: string,
    usageTokenAllocationWrite: UsageTokenAllocationWrite, options?: Parameters<typeof customFetch>[1]): Promise<UsageTokenAllocationRecord> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<UsageTokenAllocationRecord>(getSetTenantUsageTokenAllocationUrl(entityId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(usageTokenAllocationWrite)
  }
);}





export const getSetTenantUsageTokenAllocationMutationKey = () => ['setTenantUsageTokenAllocation'] as const;

export const getSetTenantUsageTokenAllocationMutationOptions = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setTenantUsageTokenAllocation>>, TError,SetTenantUsageTokenAllocationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setTenantUsageTokenAllocation>>, TError,SetTenantUsageTokenAllocationMutationVariables, TContext> => {

const mutationKey = getSetTenantUsageTokenAllocationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setTenantUsageTokenAllocation>>, SetTenantUsageTokenAllocationMutationVariables> = (props) => {
          const {entityId,data} = props ?? {};

          return  setTenantUsageTokenAllocation(entityId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetTenantUsageTokenAllocationMutationResult = NonNullable<Awaited<ReturnType<typeof setTenantUsageTokenAllocation>>>
    export type SetTenantUsageTokenAllocationMutationBody = UsageTokenAllocationWrite
    export type SetTenantUsageTokenAllocationMutationError = Error | void
    export type SetTenantUsageTokenAllocationMutationVariables = {entityId: string;data: UsageTokenAllocationWrite}

    /**
 * @summary Set an entity usage token allocation
 */
export const useSetTenantUsageTokenAllocation = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setTenantUsageTokenAllocation>>, TError,SetTenantUsageTokenAllocationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof setTenantUsageTokenAllocation>>,
        TError,
        SetTenantUsageTokenAllocationMutationVariables,
        TContext
      > => {
      return useMutation(getSetTenantUsageTokenAllocationMutationOptions(options));
    }

export const getListTenantUsageTokenLedgerUrl = (params?: ListTenantUsageTokenLedgerParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/tenant/usage-tokens/ledger?${stringifiedParams}` : `/api/access/tenant/usage-tokens/ledger`
}

/**
 * Requires tenant.tokens.read or tenant.tokens.manage.
 * @summary List immutable usage token ledger entries
 */
export const listTenantUsageTokenLedger = async (params?: ListTenantUsageTokenLedgerParams, options?: Parameters<typeof customFetch>[1]): Promise<UsageTokenLedgerPage> => {

  return customFetch<UsageTokenLedgerPage>(getListTenantUsageTokenLedgerUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenantUsageTokenLedgerQueryKey = (params?: ListTenantUsageTokenLedgerParams,) => {
    return [
    `/api/access/tenant/usage-tokens/ledger`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenantUsageTokenLedgerQueryOptions = <TData = Awaited<ReturnType<typeof listTenantUsageTokenLedger>>, TError = void>(params?: ListTenantUsageTokenLedgerParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantUsageTokenLedger>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenantUsageTokenLedgerQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenantUsageTokenLedger>>> = ({ signal }) => listTenantUsageTokenLedger(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenantUsageTokenLedger>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenantUsageTokenLedgerQueryResult = NonNullable<Awaited<ReturnType<typeof listTenantUsageTokenLedger>>>
export type ListTenantUsageTokenLedgerQueryError = void


/**
 * @summary List immutable usage token ledger entries
 */

export function useListTenantUsageTokenLedger<TData = Awaited<ReturnType<typeof listTenantUsageTokenLedger>>, TError = void>(
 params?: ListTenantUsageTokenLedgerParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantUsageTokenLedger>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenantUsageTokenLedgerQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetTenantUsageTokenReportUrl = (params?: GetTenantUsageTokenReportParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/tenant/usage-tokens/report?${stringifiedParams}` : `/api/access/tenant/usage-tokens/report`
}

/**
 * Requires tenant.tokens.read or tenant.tokens.manage.
 * @summary Aggregate token consumption by day and action
 */
export const getTenantUsageTokenReport = async (params?: GetTenantUsageTokenReportParams, options?: Parameters<typeof customFetch>[1]): Promise<UsageTokenReport> => {

  return customFetch<UsageTokenReport>(getGetTenantUsageTokenReportUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenantUsageTokenReportQueryKey = (params?: GetTenantUsageTokenReportParams,) => {
    return [
    `/api/access/tenant/usage-tokens/report`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetTenantUsageTokenReportQueryOptions = <TData = Awaited<ReturnType<typeof getTenantUsageTokenReport>>, TError = void>(params?: GetTenantUsageTokenReportParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenantUsageTokenReport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenantUsageTokenReportQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenantUsageTokenReport>>> = ({ signal }) => getTenantUsageTokenReport(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenantUsageTokenReport>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTenantUsageTokenReportQueryResult = NonNullable<Awaited<ReturnType<typeof getTenantUsageTokenReport>>>
export type GetTenantUsageTokenReportQueryError = void


/**
 * @summary Aggregate token consumption by day and action
 */

export function useGetTenantUsageTokenReport<TData = Awaited<ReturnType<typeof getTenantUsageTokenReport>>, TError = void>(
 params?: GetTenantUsageTokenReportParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenantUsageTokenReport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTenantUsageTokenReportQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getExportTenantUsageTokenLedgerUrl = (params?: ExportTenantUsageTokenLedgerParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/tenant/usage-tokens/export?${stringifiedParams}` : `/api/access/tenant/usage-tokens/export`
}

/**
 * Requires tenant.tokens.read or tenant.tokens.manage. Exports up to 10,000 filtered rows.
 * @summary Export the filtered usage token ledger as CSV
 */
export const exportTenantUsageTokenLedger = async (params?: ExportTenantUsageTokenLedgerParams, options?: Parameters<typeof customFetch>[1]): Promise<UsageTokenCsvExport> => {

  return customFetch<UsageTokenCsvExport>(getExportTenantUsageTokenLedgerUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getExportTenantUsageTokenLedgerQueryKey = (params?: ExportTenantUsageTokenLedgerParams,) => {
    return [
    `/api/access/tenant/usage-tokens/export`, ...(params ? [params] : [])
    ] as const;
    }


export const getExportTenantUsageTokenLedgerQueryOptions = <TData = Awaited<ReturnType<typeof exportTenantUsageTokenLedger>>, TError = void>(params?: ExportTenantUsageTokenLedgerParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof exportTenantUsageTokenLedger>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getExportTenantUsageTokenLedgerQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof exportTenantUsageTokenLedger>>> = ({ signal }) => exportTenantUsageTokenLedger(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof exportTenantUsageTokenLedger>>, TError, TData> & { queryKey: QueryKey }
}

export type ExportTenantUsageTokenLedgerQueryResult = NonNullable<Awaited<ReturnType<typeof exportTenantUsageTokenLedger>>>
export type ExportTenantUsageTokenLedgerQueryError = void


/**
 * @summary Export the filtered usage token ledger as CSV
 */

export function useExportTenantUsageTokenLedger<TData = Awaited<ReturnType<typeof exportTenantUsageTokenLedger>>, TError = void>(
 params?: ExportTenantUsageTokenLedgerParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof exportTenantUsageTokenLedger>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getExportTenantUsageTokenLedgerQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateEntityPackageExportUrl = (entityId: string,) => {




  return `/api/access/entities/${entityId}/data-packages/exports`
}

/**
 * Requires tenant.data.manage and current server-verified ownership/access to the exact path entity. Exports use durable leased jobs, a consistent source snapshot and verified private ZIP downloads. Retry starts a new snapshot; incomplete content blocks completion. Package metadata grants no authority. Destination identity, access and tenant settings remain authoritative.
 */
export const createEntityPackageExport = async (entityId: string,
    entityPackageExportRequest: EntityPackageExportRequest, options?: Parameters<typeof customFetch>[1]): Promise<EntityPackageJob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EntityPackageJob>(getCreateEntityPackageExportUrl(entityId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(entityPackageExportRequest)
  }
);}





export const getCreateEntityPackageExportMutationKey = () => ['createEntityPackageExport'] as const;

export const getCreateEntityPackageExportMutationOptions = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEntityPackageExport>>, TError,CreateEntityPackageExportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createEntityPackageExport>>, TError,CreateEntityPackageExportMutationVariables, TContext> => {

const mutationKey = getCreateEntityPackageExportMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createEntityPackageExport>>, CreateEntityPackageExportMutationVariables> = (props) => {
          const {entityId,data} = props ?? {};

          return  createEntityPackageExport(entityId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateEntityPackageExportMutationResult = NonNullable<Awaited<ReturnType<typeof createEntityPackageExport>>>
    export type CreateEntityPackageExportMutationBody = EntityPackageExportRequest
    export type CreateEntityPackageExportMutationError = Error | void
    export type CreateEntityPackageExportMutationVariables = {entityId: string;data: EntityPackageExportRequest}

    export const useCreateEntityPackageExport = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEntityPackageExport>>, TError,CreateEntityPackageExportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createEntityPackageExport>>,
        TError,
        CreateEntityPackageExportMutationVariables,
        TContext
      > => {
      return useMutation(getCreateEntityPackageExportMutationOptions(options));
    }

export const getUploadEntityPackageUrl = (entityId: string,) => {




  return `/api/access/entities/${entityId}/data-packages/uploads`
}

/**
 * Requires tenant.data.manage and current server-verified ownership/access to the exact path entity. Private upload validation and read-only, entity-scoped Add, Merge and Replace planning. Merge uses persisted source lineage only. Exact value-bound review expires after 30 minutes. Multipart uploads require idempotencyKey before file; previews support up to 20,000 records. Package metadata grants no authority. Destination identity, access and tenant settings remain authoritative.
 */
export const uploadEntityPackage = async (entityId: string,
    uploadEntityPackageBody: UploadEntityPackageBody, options?: Parameters<typeof customFetch>[1]): Promise<EntityPackageJob> => {
    const formData = new FormData();
formData.append(`file`, uploadEntityPackageBody.file);
formData.append(`idempotencyKey`, uploadEntityPackageBody.idempotencyKey);

  return customFetch<EntityPackageJob>(getUploadEntityPackageUrl(entityId),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}





export const getUploadEntityPackageMutationKey = () => ['uploadEntityPackage'] as const;

export const getUploadEntityPackageMutationOptions = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadEntityPackage>>, TError,UploadEntityPackageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadEntityPackage>>, TError,UploadEntityPackageMutationVariables, TContext> => {

const mutationKey = getUploadEntityPackageMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadEntityPackage>>, UploadEntityPackageMutationVariables> = (props) => {
          const {entityId,data} = props ?? {};

          return  uploadEntityPackage(entityId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadEntityPackageMutationResult = NonNullable<Awaited<ReturnType<typeof uploadEntityPackage>>>
    export type UploadEntityPackageMutationBody = UploadEntityPackageBody
    export type UploadEntityPackageMutationError = Error | void
    export type UploadEntityPackageMutationVariables = {entityId: string;data: UploadEntityPackageBody}

    export const useUploadEntityPackage = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadEntityPackage>>, TError,UploadEntityPackageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadEntityPackage>>,
        TError,
        UploadEntityPackageMutationVariables,
        TContext
      > => {
      return useMutation(getUploadEntityPackageMutationOptions(options));
    }

export const getListEntityPackageJobsUrl = (entityId: string,
    params?: ListEntityPackageJobsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/entities/${entityId}/data-packages/jobs?${stringifiedParams}` : `/api/access/entities/${entityId}/data-packages/jobs`
}

/**
 * Requires tenant.data.manage and current server-verified ownership/access to the exact path entity. Exports use durable leased jobs, a consistent source snapshot and verified private ZIP downloads. Retry starts a new snapshot; incomplete content blocks completion. Package metadata grants no authority. Destination identity, access and tenant settings remain authoritative.
 */
export const listEntityPackageJobs = async (entityId: string,
    params?: ListEntityPackageJobsParams, options?: Parameters<typeof customFetch>[1]): Promise<EntityPackageJobPage> => {

  return customFetch<EntityPackageJobPage>(getListEntityPackageJobsUrl(entityId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListEntityPackageJobsQueryKey = (entityId: string,
    params?: ListEntityPackageJobsParams,) => {
    return [
    `/api/access/entities/${entityId}/data-packages/jobs`, ...(params ? [params] : [])
    ] as const;
    }


export const getListEntityPackageJobsQueryOptions = <TData = Awaited<ReturnType<typeof listEntityPackageJobs>>, TError = Error | void>(entityId: string,
    params?: ListEntityPackageJobsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityPackageJobs>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEntityPackageJobsQueryKey(entityId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEntityPackageJobs>>> = ({ signal }) => listEntityPackageJobs(entityId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entityId !== null && entityId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEntityPackageJobs>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEntityPackageJobsQueryResult = NonNullable<Awaited<ReturnType<typeof listEntityPackageJobs>>>
export type ListEntityPackageJobsQueryError = Error | void



export function useListEntityPackageJobs<TData = Awaited<ReturnType<typeof listEntityPackageJobs>>, TError = Error | void>(
 entityId: string,
    params?: ListEntityPackageJobsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityPackageJobs>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEntityPackageJobsQueryOptions(entityId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetEntityPackageJobUrl = (entityId: string,
    jobId: string,) => {




  return `/api/access/entities/${entityId}/data-packages/jobs/${jobId}`
}

/**
 * Requires tenant.data.manage and current server-verified ownership/access to the exact path entity. Exports use durable leased jobs, a consistent source snapshot and verified private ZIP downloads. Retry starts a new snapshot; incomplete content blocks completion. Package metadata grants no authority. Destination identity, access and tenant settings remain authoritative.
 */
export const getEntityPackageJob = async (entityId: string,
    jobId: string, options?: Parameters<typeof customFetch>[1]): Promise<EntityPackageJob> => {

  return customFetch<EntityPackageJob>(getGetEntityPackageJobUrl(entityId,jobId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEntityPackageJobQueryKey = (entityId: string,
    jobId: string,) => {
    return [
    `/api/access/entities/${entityId}/data-packages/jobs/${jobId}`
    ] as const;
    }


export const getGetEntityPackageJobQueryOptions = <TData = Awaited<ReturnType<typeof getEntityPackageJob>>, TError = Error | void>(entityId: string,
    jobId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityPackageJob>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEntityPackageJobQueryKey(entityId,jobId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEntityPackageJob>>> = ({ signal }) => getEntityPackageJob(entityId,jobId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entityId !== null && entityId !== undefined && jobId !== null && jobId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEntityPackageJob>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEntityPackageJobQueryResult = NonNullable<Awaited<ReturnType<typeof getEntityPackageJob>>>
export type GetEntityPackageJobQueryError = Error | void



export function useGetEntityPackageJob<TData = Awaited<ReturnType<typeof getEntityPackageJob>>, TError = Error | void>(
 entityId: string,
    jobId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityPackageJob>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEntityPackageJobQueryOptions(entityId,jobId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetEntityPackageImportOptionsUrl = (entityId: string,
    jobId: string,
    params?: GetEntityPackageImportOptionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/entities/${entityId}/data-packages/jobs/${jobId}/import-options?${stringifiedParams}` : `/api/access/entities/${entityId}/data-packages/jobs/${jobId}/import-options`
}

/**
 * Read-only, authorized destination-scoped prior copy revisions and compiled source-person references for a verified upload. No natural-key identity matching or access changes.
 */
export const getEntityPackageImportOptions = async (entityId: string,
    jobId: string,
    params?: GetEntityPackageImportOptionsParams, options?: Parameters<typeof customFetch>[1]): Promise<EntityPackageImportOptions> => {

  return customFetch<EntityPackageImportOptions>(getGetEntityPackageImportOptionsUrl(entityId,jobId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEntityPackageImportOptionsQueryKey = (entityId: string,
    jobId: string,
    params?: GetEntityPackageImportOptionsParams,) => {
    return [
    `/api/access/entities/${entityId}/data-packages/jobs/${jobId}/import-options`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetEntityPackageImportOptionsQueryOptions = <TData = Awaited<ReturnType<typeof getEntityPackageImportOptions>>, TError = unknown>(entityId: string,
    jobId: string,
    params?: GetEntityPackageImportOptionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityPackageImportOptions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEntityPackageImportOptionsQueryKey(entityId,jobId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEntityPackageImportOptions>>> = ({ signal }) => getEntityPackageImportOptions(entityId,jobId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entityId !== null && entityId !== undefined && jobId !== null && jobId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEntityPackageImportOptions>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEntityPackageImportOptionsQueryResult = NonNullable<Awaited<ReturnType<typeof getEntityPackageImportOptions>>>
export type GetEntityPackageImportOptionsQueryError = unknown



export function useGetEntityPackageImportOptions<TData = Awaited<ReturnType<typeof getEntityPackageImportOptions>>, TError = unknown>(
 entityId: string,
    jobId: string,
    params?: GetEntityPackageImportOptionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityPackageImportOptions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEntityPackageImportOptionsQueryOptions(entityId,jobId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPreflightEntityPackageUrl = (entityId: string,
    jobId: string,) => {




  return `/api/access/entities/${entityId}/data-packages/jobs/${jobId}/preflight`
}

/**
 * Requires tenant.data.manage and current server-verified ownership/access to the exact path entity. Private upload validation and read-only, entity-scoped Add, Merge and Replace planning. Merge uses persisted source lineage only. Exact value-bound review expires after 30 minutes. Multipart uploads require idempotencyKey before file; previews support up to 20,000 records. Package metadata grants no authority. Destination identity, access and tenant settings remain authoritative.
 */
export const preflightEntityPackage = async (entityId: string,
    jobId: string,
    entityPackagePreflightRequest: EntityPackagePreflightRequest, options?: Parameters<typeof customFetch>[1]): Promise<EntityPackageJob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EntityPackageJob>(getPreflightEntityPackageUrl(entityId,jobId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(entityPackagePreflightRequest)
  }
);}





export const getPreflightEntityPackageMutationKey = () => ['preflightEntityPackage'] as const;

export const getPreflightEntityPackageMutationOptions = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof preflightEntityPackage>>, TError,PreflightEntityPackageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof preflightEntityPackage>>, TError,PreflightEntityPackageMutationVariables, TContext> => {

const mutationKey = getPreflightEntityPackageMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof preflightEntityPackage>>, PreflightEntityPackageMutationVariables> = (props) => {
          const {entityId,jobId,data} = props ?? {};

          return  preflightEntityPackage(entityId,jobId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreflightEntityPackageMutationResult = NonNullable<Awaited<ReturnType<typeof preflightEntityPackage>>>
    export type PreflightEntityPackageMutationBody = EntityPackagePreflightRequest
    export type PreflightEntityPackageMutationError = Error | void
    export type PreflightEntityPackageMutationVariables = {entityId: string;jobId: string;data: EntityPackagePreflightRequest}

    export const usePreflightEntityPackage = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof preflightEntityPackage>>, TError,PreflightEntityPackageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof preflightEntityPackage>>,
        TError,
        PreflightEntityPackageMutationVariables,
        TContext
      > => {
      return useMutation(getPreflightEntityPackageMutationOptions(options));
    }

export const getGetEntityPackagePreviewUrl = (entityId: string,
    jobId: string,
    params?: GetEntityPackagePreviewParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/entities/${entityId}/data-packages/jobs/${jobId}/preview?${stringifiedParams}` : `/api/access/entities/${entityId}/data-packages/jobs/${jobId}/preview`
}

/**
 * Requires tenant.data.manage and current server-verified ownership/access to the exact path entity. Private upload validation and read-only, entity-scoped Add, Merge and Replace planning. Merge uses persisted source lineage only. Exact value-bound review expires after 30 minutes. Multipart uploads require idempotencyKey before file; previews support up to 20,000 records. Package metadata grants no authority. Destination identity, access and tenant settings remain authoritative.
 */
export const getEntityPackagePreview = async (entityId: string,
    jobId: string,
    params?: GetEntityPackagePreviewParams, options?: Parameters<typeof customFetch>[1]): Promise<EntityPackagePreview> => {

  return customFetch<EntityPackagePreview>(getGetEntityPackagePreviewUrl(entityId,jobId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEntityPackagePreviewQueryKey = (entityId: string,
    jobId: string,
    params?: GetEntityPackagePreviewParams,) => {
    return [
    `/api/access/entities/${entityId}/data-packages/jobs/${jobId}/preview`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetEntityPackagePreviewQueryOptions = <TData = Awaited<ReturnType<typeof getEntityPackagePreview>>, TError = Error | void>(entityId: string,
    jobId: string,
    params?: GetEntityPackagePreviewParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityPackagePreview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEntityPackagePreviewQueryKey(entityId,jobId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEntityPackagePreview>>> = ({ signal }) => getEntityPackagePreview(entityId,jobId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entityId !== null && entityId !== undefined && jobId !== null && jobId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEntityPackagePreview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEntityPackagePreviewQueryResult = NonNullable<Awaited<ReturnType<typeof getEntityPackagePreview>>>
export type GetEntityPackagePreviewQueryError = Error | void



export function useGetEntityPackagePreview<TData = Awaited<ReturnType<typeof getEntityPackagePreview>>, TError = Error | void>(
 entityId: string,
    jobId: string,
    params?: GetEntityPackagePreviewParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityPackagePreview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEntityPackagePreviewQueryOptions(entityId,jobId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListEntityPackageConflictsUrl = (entityId: string,
    jobId: string,
    params?: ListEntityPackageConflictsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/entities/${entityId}/data-packages/jobs/${jobId}/conflicts?${stringifiedParams}` : `/api/access/entities/${entityId}/data-packages/jobs/${jobId}/conflicts`
}

/**
 * Requires tenant.data.manage and current server-verified ownership/access to the exact path entity. Private upload validation and read-only, entity-scoped Add, Merge and Replace planning. Merge uses persisted source lineage only. Exact value-bound review expires after 30 minutes. Multipart uploads require idempotencyKey before file; previews support up to 20,000 records. Package metadata grants no authority. Destination identity, access and tenant settings remain authoritative.
 */
export const listEntityPackageConflicts = async (entityId: string,
    jobId: string,
    params?: ListEntityPackageConflictsParams, options?: Parameters<typeof customFetch>[1]): Promise<EntityPackageConflictPage> => {

  return customFetch<EntityPackageConflictPage>(getListEntityPackageConflictsUrl(entityId,jobId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListEntityPackageConflictsQueryKey = (entityId: string,
    jobId: string,
    params?: ListEntityPackageConflictsParams,) => {
    return [
    `/api/access/entities/${entityId}/data-packages/jobs/${jobId}/conflicts`, ...(params ? [params] : [])
    ] as const;
    }


export const getListEntityPackageConflictsQueryOptions = <TData = Awaited<ReturnType<typeof listEntityPackageConflicts>>, TError = Error | void>(entityId: string,
    jobId: string,
    params?: ListEntityPackageConflictsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityPackageConflicts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEntityPackageConflictsQueryKey(entityId,jobId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEntityPackageConflicts>>> = ({ signal }) => listEntityPackageConflicts(entityId,jobId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entityId !== null && entityId !== undefined && jobId !== null && jobId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEntityPackageConflicts>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEntityPackageConflictsQueryResult = NonNullable<Awaited<ReturnType<typeof listEntityPackageConflicts>>>
export type ListEntityPackageConflictsQueryError = Error | void



export function useListEntityPackageConflicts<TData = Awaited<ReturnType<typeof listEntityPackageConflicts>>, TError = Error | void>(
 entityId: string,
    jobId: string,
    params?: ListEntityPackageConflictsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityPackageConflicts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEntityPackageConflictsQueryOptions(entityId,jobId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDecideEntityPackageConflictUrl = (entityId: string,
    jobId: string,) => {




  return `/api/access/entities/${entityId}/data-packages/jobs/${jobId}/decisions`
}

/**
 * Requires tenant.data.manage and current server-verified ownership/access to the exact path entity. Private upload validation and read-only, entity-scoped Add, Merge and Replace planning. Merge uses persisted source lineage only. Exact value-bound review expires after 30 minutes. Multipart uploads require idempotencyKey before file; previews support up to 20,000 records. Package metadata grants no authority. Destination identity, access and tenant settings remain authoritative.
 */
export const decideEntityPackageConflict = async (entityId: string,
    jobId: string,
    entityPackageDecisionRequest: EntityPackageDecisionRequest, options?: Parameters<typeof customFetch>[1]): Promise<EntityPackageJob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EntityPackageJob>(getDecideEntityPackageConflictUrl(entityId,jobId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(entityPackageDecisionRequest)
  }
);}





export const getDecideEntityPackageConflictMutationKey = () => ['decideEntityPackageConflict'] as const;

export const getDecideEntityPackageConflictMutationOptions = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideEntityPackageConflict>>, TError,DecideEntityPackageConflictMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof decideEntityPackageConflict>>, TError,DecideEntityPackageConflictMutationVariables, TContext> => {

const mutationKey = getDecideEntityPackageConflictMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof decideEntityPackageConflict>>, DecideEntityPackageConflictMutationVariables> = (props) => {
          const {entityId,jobId,data} = props ?? {};

          return  decideEntityPackageConflict(entityId,jobId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DecideEntityPackageConflictMutationResult = NonNullable<Awaited<ReturnType<typeof decideEntityPackageConflict>>>
    export type DecideEntityPackageConflictMutationBody = EntityPackageDecisionRequest
    export type DecideEntityPackageConflictMutationError = Error | void
    export type DecideEntityPackageConflictMutationVariables = {entityId: string;jobId: string;data: EntityPackageDecisionRequest}

    export const useDecideEntityPackageConflict = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideEntityPackageConflict>>, TError,DecideEntityPackageConflictMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof decideEntityPackageConflict>>,
        TError,
        DecideEntityPackageConflictMutationVariables,
        TContext
      > => {
      return useMutation(getDecideEntityPackageConflictMutationOptions(options));
    }

export const getApplyEntityPackageUrl = (entityId: string,
    jobId: string,) => {




  return `/api/access/entities/${entityId}/data-packages/jobs/${jobId}/apply`
}

/**
 * Requires tenant.data.manage and current server-verified ownership/access to the exact path entity. Atomically applies current reviewed Add, Merge or guarded Replace plans with private files, lineage and audit. A committed operation replays only the same request; a deliberate new Add needs a new upload and review. Unresolved conflicts, stale contents or lost authority block publication. Replace requires a verified private recovery snapshot and exact destination confirmation; active workers and protected dependencies fail closed. Package metadata grants no authority. Destination identity, access and tenant settings remain authoritative.
 */
export const applyEntityPackage = async (entityId: string,
    jobId: string,
    entityPackageApplyRequest: EntityPackageApplyRequest, options?: Parameters<typeof customFetch>[1]): Promise<EntityPackageJob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EntityPackageJob>(getApplyEntityPackageUrl(entityId,jobId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(entityPackageApplyRequest)
  }
);}





export const getApplyEntityPackageMutationKey = () => ['applyEntityPackage'] as const;

export const getApplyEntityPackageMutationOptions = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyEntityPackage>>, TError,ApplyEntityPackageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof applyEntityPackage>>, TError,ApplyEntityPackageMutationVariables, TContext> => {

const mutationKey = getApplyEntityPackageMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof applyEntityPackage>>, ApplyEntityPackageMutationVariables> = (props) => {
          const {entityId,jobId,data} = props ?? {};

          return  applyEntityPackage(entityId,jobId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApplyEntityPackageMutationResult = NonNullable<Awaited<ReturnType<typeof applyEntityPackage>>>
    export type ApplyEntityPackageMutationBody = EntityPackageApplyRequest
    export type ApplyEntityPackageMutationError = Error | void
    export type ApplyEntityPackageMutationVariables = {entityId: string;jobId: string;data: EntityPackageApplyRequest}

    export const useApplyEntityPackage = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyEntityPackage>>, TError,ApplyEntityPackageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof applyEntityPackage>>,
        TError,
        ApplyEntityPackageMutationVariables,
        TContext
      > => {
      return useMutation(getApplyEntityPackageMutationOptions(options));
    }

export const getCancelEntityPackageJobUrl = (entityId: string,
    jobId: string,) => {




  return `/api/access/entities/${entityId}/data-packages/jobs/${jobId}/cancel`
}

/**
 * Requires tenant.data.manage and current server-verified ownership/access to the exact path entity. Exports use durable leased jobs, a consistent source snapshot and verified private ZIP downloads. Retry starts a new snapshot; incomplete content blocks completion. Package metadata grants no authority. Destination identity, access and tenant settings remain authoritative.
 */
export const cancelEntityPackageJob = async (entityId: string,
    jobId: string,
    accessReason: AccessReason, options?: Parameters<typeof customFetch>[1]): Promise<EntityPackageJob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EntityPackageJob>(getCancelEntityPackageJobUrl(entityId,jobId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accessReason)
  }
);}





export const getCancelEntityPackageJobMutationKey = () => ['cancelEntityPackageJob'] as const;

export const getCancelEntityPackageJobMutationOptions = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelEntityPackageJob>>, TError,CancelEntityPackageJobMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelEntityPackageJob>>, TError,CancelEntityPackageJobMutationVariables, TContext> => {

const mutationKey = getCancelEntityPackageJobMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelEntityPackageJob>>, CancelEntityPackageJobMutationVariables> = (props) => {
          const {entityId,jobId,data} = props ?? {};

          return  cancelEntityPackageJob(entityId,jobId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelEntityPackageJobMutationResult = NonNullable<Awaited<ReturnType<typeof cancelEntityPackageJob>>>
    export type CancelEntityPackageJobMutationBody = AccessReason
    export type CancelEntityPackageJobMutationError = Error | void
    export type CancelEntityPackageJobMutationVariables = {entityId: string;jobId: string;data: AccessReason}

    export const useCancelEntityPackageJob = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelEntityPackageJob>>, TError,CancelEntityPackageJobMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelEntityPackageJob>>,
        TError,
        CancelEntityPackageJobMutationVariables,
        TContext
      > => {
      return useMutation(getCancelEntityPackageJobMutationOptions(options));
    }

export const getRetryEntityPackageJobUrl = (entityId: string,
    jobId: string,) => {




  return `/api/access/entities/${entityId}/data-packages/jobs/${jobId}/retry`
}

/**
 * Requires tenant.data.manage and current server-verified ownership/access to the exact path entity. Exports use durable leased jobs, a consistent source snapshot and verified private ZIP downloads. Retry starts a new snapshot; incomplete content blocks completion. Package metadata grants no authority. Destination identity, access and tenant settings remain authoritative.
 */
export const retryEntityPackageJob = async (entityId: string,
    jobId: string,
    entityPackageExportRequest: EntityPackageExportRequest, options?: Parameters<typeof customFetch>[1]): Promise<EntityPackageJob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EntityPackageJob>(getRetryEntityPackageJobUrl(entityId,jobId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(entityPackageExportRequest)
  }
);}





export const getRetryEntityPackageJobMutationKey = () => ['retryEntityPackageJob'] as const;

export const getRetryEntityPackageJobMutationOptions = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryEntityPackageJob>>, TError,RetryEntityPackageJobMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof retryEntityPackageJob>>, TError,RetryEntityPackageJobMutationVariables, TContext> => {

const mutationKey = getRetryEntityPackageJobMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof retryEntityPackageJob>>, RetryEntityPackageJobMutationVariables> = (props) => {
          const {entityId,jobId,data} = props ?? {};

          return  retryEntityPackageJob(entityId,jobId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RetryEntityPackageJobMutationResult = NonNullable<Awaited<ReturnType<typeof retryEntityPackageJob>>>
    export type RetryEntityPackageJobMutationBody = EntityPackageExportRequest
    export type RetryEntityPackageJobMutationError = Error | void
    export type RetryEntityPackageJobMutationVariables = {entityId: string;jobId: string;data: EntityPackageExportRequest}

    export const useRetryEntityPackageJob = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryEntityPackageJob>>, TError,RetryEntityPackageJobMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof retryEntityPackageJob>>,
        TError,
        RetryEntityPackageJobMutationVariables,
        TContext
      > => {
      return useMutation(getRetryEntityPackageJobMutationOptions(options));
    }

export const getReviewEntityPackageRecoveryUrl = (entityId: string,
    jobId: string,) => {




  return `/api/access/entities/${entityId}/data-packages/jobs/${jobId}/recovery-preview`
}

/**
 * Reviews restoration of a verified private recovery snapshot. Rejects subsequent destination edits and requires current destination authority.
 */
export const reviewEntityPackageRecovery = async (entityId: string,
    jobId: string, options?: Parameters<typeof customFetch>[1]): Promise<EntityPackageReplacementReview> => {

  return customFetch<EntityPackageReplacementReview>(getReviewEntityPackageRecoveryUrl(entityId,jobId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getReviewEntityPackageRecoveryMutationKey = () => ['reviewEntityPackageRecovery'] as const;

export const getReviewEntityPackageRecoveryMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewEntityPackageRecovery>>, TError,ReviewEntityPackageRecoveryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewEntityPackageRecovery>>, TError,ReviewEntityPackageRecoveryMutationVariables, TContext> => {

const mutationKey = getReviewEntityPackageRecoveryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewEntityPackageRecovery>>, ReviewEntityPackageRecoveryMutationVariables> = (props) => {
          const {entityId,jobId} = props ?? {};

          return  reviewEntityPackageRecovery(entityId,jobId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewEntityPackageRecoveryMutationResult = NonNullable<Awaited<ReturnType<typeof reviewEntityPackageRecovery>>>

    export type ReviewEntityPackageRecoveryMutationError = void
    export type ReviewEntityPackageRecoveryMutationVariables = {entityId: string;jobId: string}

    export const useReviewEntityPackageRecovery = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewEntityPackageRecovery>>, TError,ReviewEntityPackageRecoveryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewEntityPackageRecovery>>,
        TError,
        ReviewEntityPackageRecoveryMutationVariables,
        TContext
      > => {
      return useMutation(getReviewEntityPackageRecoveryMutationOptions(options));
    }

export const getPrepareEntityPackageReplacementUrl = (entityId: string,
    jobId: string,) => {




  return `/api/access/entities/${entityId}/data-packages/jobs/${jobId}/replacement-snapshot`
}

/**
 * Creates and verifies a private recovery snapshot for the current exact Replace preview. No destination business mutation. Native operational dependencies or protected references block Replace.
 */
export const prepareEntityPackageReplacement = async (entityId: string,
    jobId: string,
    entityPackageReplacementRequest: EntityPackageReplacementRequest, options?: Parameters<typeof customFetch>[1]): Promise<EntityPackageReplacementReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EntityPackageReplacementReview>(getPrepareEntityPackageReplacementUrl(entityId,jobId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(entityPackageReplacementRequest)
  }
);}





export const getPrepareEntityPackageReplacementMutationKey = () => ['prepareEntityPackageReplacement'] as const;

export const getPrepareEntityPackageReplacementMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof prepareEntityPackageReplacement>>, TError,PrepareEntityPackageReplacementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof prepareEntityPackageReplacement>>, TError,PrepareEntityPackageReplacementMutationVariables, TContext> => {

const mutationKey = getPrepareEntityPackageReplacementMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof prepareEntityPackageReplacement>>, PrepareEntityPackageReplacementMutationVariables> = (props) => {
          const {entityId,jobId,data} = props ?? {};

          return  prepareEntityPackageReplacement(entityId,jobId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PrepareEntityPackageReplacementMutationResult = NonNullable<Awaited<ReturnType<typeof prepareEntityPackageReplacement>>>
    export type PrepareEntityPackageReplacementMutationBody = EntityPackageReplacementRequest
    export type PrepareEntityPackageReplacementMutationError = void
    export type PrepareEntityPackageReplacementMutationVariables = {entityId: string;jobId: string;data: EntityPackageReplacementRequest}

    export const usePrepareEntityPackageReplacement = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof prepareEntityPackageReplacement>>, TError,PrepareEntityPackageReplacementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof prepareEntityPackageReplacement>>,
        TError,
        PrepareEntityPackageReplacementMutationVariables,
        TContext
      > => {
      return useMutation(getPrepareEntityPackageReplacementMutationOptions(options));
    }

export const getRecoverEntityPackageUrl = (entityId: string,
    jobId: string,) => {




  return `/api/access/entities/${entityId}/data-packages/jobs/${jobId}/recovery`
}

/**
 * Requires tenant.data.manage and current server-verified ownership/access to the exact path entity. Restores a verified private recovery snapshot only after a fresh authority-bound recovery review; subsequent destination changes block restoration. Package metadata grants no authority. Destination identity, access and tenant settings remain authoritative.
 */
export const recoverEntityPackage = async (entityId: string,
    jobId: string,
    entityPackageRecoveryRequest: EntityPackageRecoveryRequest, options?: Parameters<typeof customFetch>[1]): Promise<EntityPackageJob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EntityPackageJob>(getRecoverEntityPackageUrl(entityId,jobId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(entityPackageRecoveryRequest)
  }
);}





export const getRecoverEntityPackageMutationKey = () => ['recoverEntityPackage'] as const;

export const getRecoverEntityPackageMutationOptions = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recoverEntityPackage>>, TError,RecoverEntityPackageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof recoverEntityPackage>>, TError,RecoverEntityPackageMutationVariables, TContext> => {

const mutationKey = getRecoverEntityPackageMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof recoverEntityPackage>>, RecoverEntityPackageMutationVariables> = (props) => {
          const {entityId,jobId,data} = props ?? {};

          return  recoverEntityPackage(entityId,jobId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RecoverEntityPackageMutationResult = NonNullable<Awaited<ReturnType<typeof recoverEntityPackage>>>
    export type RecoverEntityPackageMutationBody = EntityPackageRecoveryRequest
    export type RecoverEntityPackageMutationError = Error | void
    export type RecoverEntityPackageMutationVariables = {entityId: string;jobId: string;data: EntityPackageRecoveryRequest}

    export const useRecoverEntityPackage = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recoverEntityPackage>>, TError,RecoverEntityPackageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof recoverEntityPackage>>,
        TError,
        RecoverEntityPackageMutationVariables,
        TContext
      > => {
      return useMutation(getRecoverEntityPackageMutationOptions(options));
    }

export const getDownloadEntityPackageUrl = (entityId: string,
    jobId: string,) => {




  return `/api/access/entities/${entityId}/data-packages/jobs/${jobId}/download`
}

/**
 * Requires tenant.data.manage and current server-verified ownership/access to the exact path entity. Exports use durable leased jobs, a consistent source snapshot and verified private ZIP downloads. Retry starts a new snapshot; incomplete content blocks completion. Package metadata grants no authority. Destination identity, access and tenant settings remain authoritative.
 */
export const downloadEntityPackage = async (entityId: string,
    jobId: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadEntityPackageUrl(entityId,jobId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadEntityPackageQueryKey = (entityId: string,
    jobId: string,) => {
    return [
    `/api/access/entities/${entityId}/data-packages/jobs/${jobId}/download`
    ] as const;
    }


export const getDownloadEntityPackageQueryOptions = <TData = Awaited<ReturnType<typeof downloadEntityPackage>>, TError = Error | void>(entityId: string,
    jobId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadEntityPackage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadEntityPackageQueryKey(entityId,jobId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadEntityPackage>>> = ({ signal }) => downloadEntityPackage(entityId,jobId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entityId !== null && entityId !== undefined && jobId !== null && jobId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadEntityPackage>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadEntityPackageQueryResult = NonNullable<Awaited<ReturnType<typeof downloadEntityPackage>>>
export type DownloadEntityPackageQueryError = Error | void



export function useDownloadEntityPackage<TData = Awaited<ReturnType<typeof downloadEntityPackage>>, TError = Error | void>(
 entityId: string,
    jobId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadEntityPackage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadEntityPackageQueryOptions(entityId,jobId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListTenantDataManagementJobsUrl = (params?: ListTenantDataManagementJobsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/tenant/data/jobs?${stringifiedParams}` : `/api/access/tenant/data/jobs`
}

/**
 * Requires tenant.data.manage.
 * @summary List tenant data export and import jobs
 */
export const listTenantDataManagementJobs = async (params?: ListTenantDataManagementJobsParams, options?: Parameters<typeof customFetch>[1]): Promise<DataManagementJobPage> => {

  return customFetch<DataManagementJobPage>(getListTenantDataManagementJobsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenantDataManagementJobsQueryKey = (params?: ListTenantDataManagementJobsParams,) => {
    return [
    `/api/access/tenant/data/jobs`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenantDataManagementJobsQueryOptions = <TData = Awaited<ReturnType<typeof listTenantDataManagementJobs>>, TError = void>(params?: ListTenantDataManagementJobsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantDataManagementJobs>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenantDataManagementJobsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenantDataManagementJobs>>> = ({ signal }) => listTenantDataManagementJobs(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenantDataManagementJobs>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenantDataManagementJobsQueryResult = NonNullable<Awaited<ReturnType<typeof listTenantDataManagementJobs>>>
export type ListTenantDataManagementJobsQueryError = void


/**
 * @summary List tenant data export and import jobs
 */

export function useListTenantDataManagementJobs<TData = Awaited<ReturnType<typeof listTenantDataManagementJobs>>, TError = void>(
 params?: ListTenantDataManagementJobsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantDataManagementJobs>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenantDataManagementJobsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetTenantDataManagementJobUrl = (id: string,) => {




  return `/api/access/tenant/data/jobs/${id}`
}

/**
 * Requires tenant.data.manage.
 */
export const getTenantDataManagementJob = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<DataManagementJob> => {

  return customFetch<DataManagementJob>(getGetTenantDataManagementJobUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenantDataManagementJobQueryKey = (id: string,) => {
    return [
    `/api/access/tenant/data/jobs/${id}`
    ] as const;
    }


export const getGetTenantDataManagementJobQueryOptions = <TData = Awaited<ReturnType<typeof getTenantDataManagementJob>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenantDataManagementJob>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenantDataManagementJobQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenantDataManagementJob>>> = ({ signal }) => getTenantDataManagementJob(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenantDataManagementJob>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTenantDataManagementJobQueryResult = NonNullable<Awaited<ReturnType<typeof getTenantDataManagementJob>>>
export type GetTenantDataManagementJobQueryError = Error



export function useGetTenantDataManagementJob<TData = Awaited<ReturnType<typeof getTenantDataManagementJob>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenantDataManagementJob>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTenantDataManagementJobQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateTenantDataExportUrl = () => {




  return `/api/access/tenant/data/exports`
}

/**
 * Requires tenant.data.manage.
 * @summary Queue a tenant data export
 */
export const createTenantDataExport = async (dataExportRequest: DataExportRequest, options?: Parameters<typeof customFetch>[1]): Promise<DataManagementJob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DataManagementJob>(getCreateTenantDataExportUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(dataExportRequest)
  }
);}





export const getCreateTenantDataExportMutationKey = () => ['createTenantDataExport'] as const;

export const getCreateTenantDataExportMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenantDataExport>>, TError,CreateTenantDataExportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTenantDataExport>>, TError,CreateTenantDataExportMutationVariables, TContext> => {

const mutationKey = getCreateTenantDataExportMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTenantDataExport>>, CreateTenantDataExportMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createTenantDataExport(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTenantDataExportMutationResult = NonNullable<Awaited<ReturnType<typeof createTenantDataExport>>>
    export type CreateTenantDataExportMutationBody = DataExportRequest
    export type CreateTenantDataExportMutationError = Error
    export type CreateTenantDataExportMutationVariables = {data: DataExportRequest}

    /**
 * @summary Queue a tenant data export
 */
export const useCreateTenantDataExport = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenantDataExport>>, TError,CreateTenantDataExportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createTenantDataExport>>,
        TError,
        CreateTenantDataExportMutationVariables,
        TContext
      > => {
      return useMutation(getCreateTenantDataExportMutationOptions(options));
    }

export const getCancelTenantDataExportUrl = (id: string,) => {




  return `/api/access/tenant/data/exports/${id}/cancel`
}

/**
 * Requires tenant.data.manage.
 */
export const cancelTenantDataExport = async (id: string,
    accessReason: AccessReason, options?: Parameters<typeof customFetch>[1]): Promise<AccessMutationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccessMutationResult>(getCancelTenantDataExportUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accessReason)
  }
);}





export const getCancelTenantDataExportMutationKey = () => ['cancelTenantDataExport'] as const;

export const getCancelTenantDataExportMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelTenantDataExport>>, TError,CancelTenantDataExportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelTenantDataExport>>, TError,CancelTenantDataExportMutationVariables, TContext> => {

const mutationKey = getCancelTenantDataExportMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelTenantDataExport>>, CancelTenantDataExportMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  cancelTenantDataExport(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelTenantDataExportMutationResult = NonNullable<Awaited<ReturnType<typeof cancelTenantDataExport>>>
    export type CancelTenantDataExportMutationBody = AccessReason
    export type CancelTenantDataExportMutationError = Error
    export type CancelTenantDataExportMutationVariables = {id: string;data: AccessReason}

    export const useCancelTenantDataExport = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelTenantDataExport>>, TError,CancelTenantDataExportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelTenantDataExport>>,
        TError,
        CancelTenantDataExportMutationVariables,
        TContext
      > => {
      return useMutation(getCancelTenantDataExportMutationOptions(options));
    }

export const getDownloadTenantDataExportUrl = (id: string,) => {




  return `/api/access/tenant/data/exports/${id}/download`
}

/**
 * Requires tenant.data.manage. The archive is streamed through this authenticated endpoint.
 */
export const downloadTenantDataExport = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadTenantDataExportUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadTenantDataExportQueryKey = (id: string,) => {
    return [
    `/api/access/tenant/data/exports/${id}/download`
    ] as const;
    }


export const getDownloadTenantDataExportQueryOptions = <TData = Awaited<ReturnType<typeof downloadTenantDataExport>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadTenantDataExport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadTenantDataExportQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadTenantDataExport>>> = ({ signal }) => downloadTenantDataExport(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadTenantDataExport>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadTenantDataExportQueryResult = NonNullable<Awaited<ReturnType<typeof downloadTenantDataExport>>>
export type DownloadTenantDataExportQueryError = Error



export function useDownloadTenantDataExport<TData = Awaited<ReturnType<typeof downloadTenantDataExport>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadTenantDataExport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadTenantDataExportQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateTenantDataImportDryRunUrl = () => {




  return `/api/access/tenant/data/imports/dry-run`
}

/**
 * Requires tenant.data.manage. No tenant data is changed.
 * @summary Upload an export ZIP and calculate a dry-run diff
 */
export const createTenantDataImportDryRun = async (createTenantDataImportDryRunBody: CreateTenantDataImportDryRunBody, options?: Parameters<typeof customFetch>[1]): Promise<DataManagementJob> => {
    const formData = new FormData();
formData.append(`file`, createTenantDataImportDryRunBody.file);

  return customFetch<DataManagementJob>(getCreateTenantDataImportDryRunUrl(),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}





export const getCreateTenantDataImportDryRunMutationKey = () => ['createTenantDataImportDryRun'] as const;

export const getCreateTenantDataImportDryRunMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenantDataImportDryRun>>, TError,CreateTenantDataImportDryRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTenantDataImportDryRun>>, TError,CreateTenantDataImportDryRunMutationVariables, TContext> => {

const mutationKey = getCreateTenantDataImportDryRunMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTenantDataImportDryRun>>, CreateTenantDataImportDryRunMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createTenantDataImportDryRun(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTenantDataImportDryRunMutationResult = NonNullable<Awaited<ReturnType<typeof createTenantDataImportDryRun>>>
    export type CreateTenantDataImportDryRunMutationBody = CreateTenantDataImportDryRunBody
    export type CreateTenantDataImportDryRunMutationError = Error
    export type CreateTenantDataImportDryRunMutationVariables = {data: CreateTenantDataImportDryRunBody}

    /**
 * @summary Upload an export ZIP and calculate a dry-run diff
 */
export const useCreateTenantDataImportDryRun = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenantDataImportDryRun>>, TError,CreateTenantDataImportDryRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createTenantDataImportDryRun>>,
        TError,
        CreateTenantDataImportDryRunMutationVariables,
        TContext
      > => {
      return useMutation(getCreateTenantDataImportDryRunMutationOptions(options));
    }

export const getApplyTenantDataImportUrl = (id: string,) => {




  return `/api/access/tenant/data/imports/${id}/apply`
}

/**
 * Requires tenant.data.manage and exact OVERWRITE confirmation.
 * @summary Apply a reviewed tenant import
 */
export const applyTenantDataImport = async (id: string,
    dataImportApply: DataImportApply, options?: Parameters<typeof customFetch>[1]): Promise<DataManagementJob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DataManagementJob>(getApplyTenantDataImportUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(dataImportApply)
  }
);}





export const getApplyTenantDataImportMutationKey = () => ['applyTenantDataImport'] as const;

export const getApplyTenantDataImportMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyTenantDataImport>>, TError,ApplyTenantDataImportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof applyTenantDataImport>>, TError,ApplyTenantDataImportMutationVariables, TContext> => {

const mutationKey = getApplyTenantDataImportMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof applyTenantDataImport>>, ApplyTenantDataImportMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  applyTenantDataImport(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApplyTenantDataImportMutationResult = NonNullable<Awaited<ReturnType<typeof applyTenantDataImport>>>
    export type ApplyTenantDataImportMutationBody = DataImportApply
    export type ApplyTenantDataImportMutationError = Error
    export type ApplyTenantDataImportMutationVariables = {id: string;data: DataImportApply}

    /**
 * @summary Apply a reviewed tenant import
 */
export const useApplyTenantDataImport = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyTenantDataImport>>, TError,ApplyTenantDataImportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof applyTenantDataImport>>,
        TError,
        ApplyTenantDataImportMutationVariables,
        TContext
      > => {
      return useMutation(getApplyTenantDataImportMutationOptions(options));
    }

export const getListResolvedTenantModulesUrl = () => {




  return `/api/access/tenant/modules`
}

/**
 * Returns visible resolved modules. Requires tenant.settings.read.
 */
export const listResolvedTenantModules = async ( options?: Parameters<typeof customFetch>[1]): Promise<TenantModulePage> => {

  return customFetch<TenantModulePage>(getListResolvedTenantModulesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListResolvedTenantModulesQueryKey = () => {
    return [
    `/api/access/tenant/modules`
    ] as const;
    }


export const getListResolvedTenantModulesQueryOptions = <TData = Awaited<ReturnType<typeof listResolvedTenantModules>>, TError = void>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResolvedTenantModules>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListResolvedTenantModulesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listResolvedTenantModules>>> = ({ signal }) => listResolvedTenantModules({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listResolvedTenantModules>>, TError, TData> & { queryKey: QueryKey }
}

export type ListResolvedTenantModulesQueryResult = NonNullable<Awaited<ReturnType<typeof listResolvedTenantModules>>>
export type ListResolvedTenantModulesQueryError = void



export function useListResolvedTenantModules<TData = Awaited<ReturnType<typeof listResolvedTenantModules>>, TError = void>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResolvedTenantModules>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListResolvedTenantModulesQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateResolvedTenantModuleUrl = (key: string,) => {




  return `/api/access/tenant/modules/${key}`
}

/**
 * Requires tenant.settings.manage.
 */
export const updateResolvedTenantModule = async (key: string,
    tenantModuleToggle: TenantModuleToggle, options?: Parameters<typeof customFetch>[1]): Promise<AdminOk> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminOk>(getUpdateResolvedTenantModuleUrl(key),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenantModuleToggle)
  }
);}





export const getUpdateResolvedTenantModuleMutationKey = () => ['updateResolvedTenantModule'] as const;

export const getUpdateResolvedTenantModuleMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateResolvedTenantModule>>, TError,UpdateResolvedTenantModuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateResolvedTenantModule>>, TError,UpdateResolvedTenantModuleMutationVariables, TContext> => {

const mutationKey = getUpdateResolvedTenantModuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateResolvedTenantModule>>, UpdateResolvedTenantModuleMutationVariables> = (props) => {
          const {key,data} = props ?? {};

          return  updateResolvedTenantModule(key,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateResolvedTenantModuleMutationResult = NonNullable<Awaited<ReturnType<typeof updateResolvedTenantModule>>>
    export type UpdateResolvedTenantModuleMutationBody = TenantModuleToggle
    export type UpdateResolvedTenantModuleMutationError = void
    export type UpdateResolvedTenantModuleMutationVariables = {key: string;data: TenantModuleToggle}

    export const useUpdateResolvedTenantModule = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateResolvedTenantModule>>, TError,UpdateResolvedTenantModuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateResolvedTenantModule>>,
        TError,
        UpdateResolvedTenantModuleMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateResolvedTenantModuleMutationOptions(options));
    }

export const getGetTenantTypedSettingsUrl = () => {




  return `/api/access/tenant/settings`
}

/**
 * Requires tenant.settings.read.
 */
export const getTenantTypedSettings = async ( options?: Parameters<typeof customFetch>[1]): Promise<TenantTypedSettings> => {

  return customFetch<TenantTypedSettings>(getGetTenantTypedSettingsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenantTypedSettingsQueryKey = () => {
    return [
    `/api/access/tenant/settings`
    ] as const;
    }


export const getGetTenantTypedSettingsQueryOptions = <TData = Awaited<ReturnType<typeof getTenantTypedSettings>>, TError = void>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenantTypedSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenantTypedSettingsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenantTypedSettings>>> = ({ signal }) => getTenantTypedSettings({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenantTypedSettings>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTenantTypedSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof getTenantTypedSettings>>>
export type GetTenantTypedSettingsQueryError = void



export function useGetTenantTypedSettings<TData = Awaited<ReturnType<typeof getTenantTypedSettings>>, TError = void>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenantTypedSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTenantTypedSettingsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateTenantTypedSettingsUrl = () => {




  return `/api/access/tenant/settings`
}

/**
 * Requires tenant.settings.manage.
 */
export const updateTenantTypedSettings = async (tenantTypedSettingsWrite: TenantTypedSettingsWrite, options?: Parameters<typeof customFetch>[1]): Promise<TenantTypedSettings> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenantTypedSettings>(getUpdateTenantTypedSettingsUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenantTypedSettingsWrite)
  }
);}





export const getUpdateTenantTypedSettingsMutationKey = () => ['updateTenantTypedSettings'] as const;

export const getUpdateTenantTypedSettingsMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenantTypedSettings>>, TError,UpdateTenantTypedSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateTenantTypedSettings>>, TError,UpdateTenantTypedSettingsMutationVariables, TContext> => {

const mutationKey = getUpdateTenantTypedSettingsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateTenantTypedSettings>>, UpdateTenantTypedSettingsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateTenantTypedSettings(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateTenantTypedSettingsMutationResult = NonNullable<Awaited<ReturnType<typeof updateTenantTypedSettings>>>
    export type UpdateTenantTypedSettingsMutationBody = TenantTypedSettingsWrite
    export type UpdateTenantTypedSettingsMutationError = void
    export type UpdateTenantTypedSettingsMutationVariables = {data: TenantTypedSettingsWrite}

    export const useUpdateTenantTypedSettings = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenantTypedSettings>>, TError,UpdateTenantTypedSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateTenantTypedSettings>>,
        TError,
        UpdateTenantTypedSettingsMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateTenantTypedSettingsMutationOptions(options));
    }

export const getListDesignSystemsUrl = (params?: ListDesignSystemsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/design-systems?${stringifiedParams}` : `/api/access/design-systems`
}

/**
 * Requires tenant.settings.read.
 * @summary List the tenant's stored design systems and which tenant or entities use them
 */
export const listDesignSystems = async (params?: ListDesignSystemsParams, options?: Parameters<typeof customFetch>[1]): Promise<DesignSystemList> => {

  return customFetch<DesignSystemList>(getListDesignSystemsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDesignSystemsQueryKey = (params?: ListDesignSystemsParams,) => {
    return [
    `/api/access/design-systems`, ...(params ? [params] : [])
    ] as const;
    }


export const getListDesignSystemsQueryOptions = <TData = Awaited<ReturnType<typeof listDesignSystems>>, TError = Error>(params?: ListDesignSystemsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDesignSystems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDesignSystemsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDesignSystems>>> = ({ signal }) => listDesignSystems(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDesignSystems>>, TError, TData> & { queryKey: QueryKey }
}

export type ListDesignSystemsQueryResult = NonNullable<Awaited<ReturnType<typeof listDesignSystems>>>
export type ListDesignSystemsQueryError = Error


/**
 * @summary List the tenant's stored design systems and which tenant or entities use them
 */

export function useListDesignSystems<TData = Awaited<ReturnType<typeof listDesignSystems>>, TError = Error>(
 params?: ListDesignSystemsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDesignSystems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListDesignSystemsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateDesignSystemUrl = () => {




  return `/api/access/design-systems`
}

/**
 * Requires tenant.settings.manage. Tokens are validated and contrast-checked; remote references are rejected.
 * @summary Add a W3C design-token document (Material UI or Material Design 3 vocabulary)
 */
export const createDesignSystem = async (designSystemCreate: DesignSystemCreate, options?: Parameters<typeof customFetch>[1]): Promise<DesignSystemDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DesignSystemDetail>(getCreateDesignSystemUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(designSystemCreate)
  }
);}





export const getCreateDesignSystemMutationKey = () => ['createDesignSystem'] as const;

export const getCreateDesignSystemMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDesignSystem>>, TError,CreateDesignSystemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createDesignSystem>>, TError,CreateDesignSystemMutationVariables, TContext> => {

const mutationKey = getCreateDesignSystemMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createDesignSystem>>, CreateDesignSystemMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createDesignSystem(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateDesignSystemMutationResult = NonNullable<Awaited<ReturnType<typeof createDesignSystem>>>
    export type CreateDesignSystemMutationBody = DesignSystemCreate
    export type CreateDesignSystemMutationError = Error
    export type CreateDesignSystemMutationVariables = {data: DesignSystemCreate}

    /**
 * @summary Add a W3C design-token document (Material UI or Material Design 3 vocabulary)
 */
export const useCreateDesignSystem = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDesignSystem>>, TError,CreateDesignSystemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createDesignSystem>>,
        TError,
        CreateDesignSystemMutationVariables,
        TContext
      > => {
      return useMutation(getCreateDesignSystemMutationOptions(options));
    }

export const getImportDesignSystemSampleUrl = (sampleId: 'platform-default' | 'template',) => {




  return `/api/access/design-systems/samples/${sampleId}`
}

/**
 * Requires tenant.settings.manage.
 * @summary Add a built-in sample design system to the tenant library
 */
export const importDesignSystemSample = async (sampleId: 'platform-default' | 'template', options?: Parameters<typeof customFetch>[1]): Promise<DesignSystemDetail> => {

  return customFetch<DesignSystemDetail>(getImportDesignSystemSampleUrl(sampleId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getImportDesignSystemSampleMutationKey = () => ['importDesignSystemSample'] as const;

export const getImportDesignSystemSampleMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importDesignSystemSample>>, TError,ImportDesignSystemSampleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof importDesignSystemSample>>, TError,ImportDesignSystemSampleMutationVariables, TContext> => {

const mutationKey = getImportDesignSystemSampleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof importDesignSystemSample>>, ImportDesignSystemSampleMutationVariables> = (props) => {
          const {sampleId} = props ?? {};

          return  importDesignSystemSample(sampleId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ImportDesignSystemSampleMutationResult = NonNullable<Awaited<ReturnType<typeof importDesignSystemSample>>>

    export type ImportDesignSystemSampleMutationError = Error
    export type ImportDesignSystemSampleMutationVariables = {sampleId: 'platform-default' | 'template'}

    /**
 * @summary Add a built-in sample design system to the tenant library
 */
export const useImportDesignSystemSample = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importDesignSystemSample>>, TError,ImportDesignSystemSampleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof importDesignSystemSample>>,
        TError,
        ImportDesignSystemSampleMutationVariables,
        TContext
      > => {
      return useMutation(getImportDesignSystemSampleMutationOptions(options));
    }

export const getResetDesignSystemActivationUrl = () => {




  return `/api/access/design-systems/reset`
}

/**
 * Requires tenant.settings.manage.
 * @summary Return the tenant to the platform default, or an entity to the tenant choice
 */
export const resetDesignSystemActivation = async (designSystemScopeSelection: DesignSystemScopeSelection, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<void>(getResetDesignSystemActivationUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(designSystemScopeSelection)
  }
);}





export const getResetDesignSystemActivationMutationKey = () => ['resetDesignSystemActivation'] as const;

export const getResetDesignSystemActivationMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resetDesignSystemActivation>>, TError,ResetDesignSystemActivationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof resetDesignSystemActivation>>, TError,ResetDesignSystemActivationMutationVariables, TContext> => {

const mutationKey = getResetDesignSystemActivationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof resetDesignSystemActivation>>, ResetDesignSystemActivationMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  resetDesignSystemActivation(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResetDesignSystemActivationMutationResult = NonNullable<Awaited<ReturnType<typeof resetDesignSystemActivation>>>
    export type ResetDesignSystemActivationMutationBody = DesignSystemScopeSelection
    export type ResetDesignSystemActivationMutationError = Error
    export type ResetDesignSystemActivationMutationVariables = {data: DesignSystemScopeSelection}

    /**
 * @summary Return the tenant to the platform default, or an entity to the tenant choice
 */
export const useResetDesignSystemActivation = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resetDesignSystemActivation>>, TError,ResetDesignSystemActivationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof resetDesignSystemActivation>>,
        TError,
        ResetDesignSystemActivationMutationVariables,
        TContext
      > => {
      return useMutation(getResetDesignSystemActivationMutationOptions(options));
    }

export const getGetDesignSystemUrl = (designSystemId: string,) => {




  return `/api/access/design-systems/${designSystemId}`
}

/**
 * Requires tenant.settings.read.
 * @summary Read one design system with its resolved tokens and contrast report
 */
export const getDesignSystem = async (designSystemId: string, options?: Parameters<typeof customFetch>[1]): Promise<DesignSystemDetail> => {

  return customFetch<DesignSystemDetail>(getGetDesignSystemUrl(designSystemId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDesignSystemQueryKey = (designSystemId: string,) => {
    return [
    `/api/access/design-systems/${designSystemId}`
    ] as const;
    }


export const getGetDesignSystemQueryOptions = <TData = Awaited<ReturnType<typeof getDesignSystem>>, TError = Error>(designSystemId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDesignSystem>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDesignSystemQueryKey(designSystemId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDesignSystem>>> = ({ signal }) => getDesignSystem(designSystemId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: designSystemId !== null && designSystemId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDesignSystem>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDesignSystemQueryResult = NonNullable<Awaited<ReturnType<typeof getDesignSystem>>>
export type GetDesignSystemQueryError = Error


/**
 * @summary Read one design system with its resolved tokens and contrast report
 */

export function useGetDesignSystem<TData = Awaited<ReturnType<typeof getDesignSystem>>, TError = Error>(
 designSystemId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDesignSystem>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDesignSystemQueryOptions(designSystemId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateDesignSystemUrl = (designSystemId: string,) => {




  return `/api/access/design-systems/${designSystemId}`
}

/**
 * Requires tenant.settings.manage.
 * @summary Rename or re-describe a design system (tokens are immutable; add a new version instead)
 */
export const updateDesignSystem = async (designSystemId: string,
    designSystemUpdate: DesignSystemUpdate, options?: Parameters<typeof customFetch>[1]): Promise<DesignSystemDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DesignSystemDetail>(getUpdateDesignSystemUrl(designSystemId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(designSystemUpdate)
  }
);}





export const getUpdateDesignSystemMutationKey = () => ['updateDesignSystem'] as const;

export const getUpdateDesignSystemMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDesignSystem>>, TError,UpdateDesignSystemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateDesignSystem>>, TError,UpdateDesignSystemMutationVariables, TContext> => {

const mutationKey = getUpdateDesignSystemMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateDesignSystem>>, UpdateDesignSystemMutationVariables> = (props) => {
          const {designSystemId,data} = props ?? {};

          return  updateDesignSystem(designSystemId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateDesignSystemMutationResult = NonNullable<Awaited<ReturnType<typeof updateDesignSystem>>>
    export type UpdateDesignSystemMutationBody = DesignSystemUpdate
    export type UpdateDesignSystemMutationError = Error
    export type UpdateDesignSystemMutationVariables = {designSystemId: string;data: DesignSystemUpdate}

    /**
 * @summary Rename or re-describe a design system (tokens are immutable; add a new version instead)
 */
export const useUpdateDesignSystem = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDesignSystem>>, TError,UpdateDesignSystemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateDesignSystem>>,
        TError,
        UpdateDesignSystemMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateDesignSystemMutationOptions(options));
    }

export const getDeleteDesignSystemUrl = (designSystemId: string,) => {




  return `/api/access/design-systems/${designSystemId}`
}

/**
 * Requires tenant.settings.manage.
 * @summary Delete a design system; anything using it falls back to the next level
 */
export const deleteDesignSystem = async (designSystemId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteDesignSystemUrl(designSystemId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteDesignSystemMutationKey = () => ['deleteDesignSystem'] as const;

export const getDeleteDesignSystemMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteDesignSystem>>, TError,DeleteDesignSystemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteDesignSystem>>, TError,DeleteDesignSystemMutationVariables, TContext> => {

const mutationKey = getDeleteDesignSystemMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteDesignSystem>>, DeleteDesignSystemMutationVariables> = (props) => {
          const {designSystemId} = props ?? {};

          return  deleteDesignSystem(designSystemId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteDesignSystemMutationResult = NonNullable<Awaited<ReturnType<typeof deleteDesignSystem>>>

    export type DeleteDesignSystemMutationError = Error
    export type DeleteDesignSystemMutationVariables = {designSystemId: string}

    /**
 * @summary Delete a design system; anything using it falls back to the next level
 */
export const useDeleteDesignSystem = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteDesignSystem>>, TError,DeleteDesignSystemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteDesignSystem>>,
        TError,
        DeleteDesignSystemMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteDesignSystemMutationOptions(options));
    }

export const getExportDesignSystemUrl = (designSystemId: string,) => {




  return `/api/access/design-systems/${designSystemId}/export`
}

/**
 * Requires tenant.settings.read.
 * @summary Export a design system as a normalised Material UI token document
 */
export const exportDesignSystem = async (designSystemId: string, options?: Parameters<typeof customFetch>[1]): Promise<DesignSystemExport> => {

  return customFetch<DesignSystemExport>(getExportDesignSystemUrl(designSystemId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getExportDesignSystemQueryKey = (designSystemId: string,) => {
    return [
    `/api/access/design-systems/${designSystemId}/export`
    ] as const;
    }


export const getExportDesignSystemQueryOptions = <TData = Awaited<ReturnType<typeof exportDesignSystem>>, TError = Error>(designSystemId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof exportDesignSystem>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getExportDesignSystemQueryKey(designSystemId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof exportDesignSystem>>> = ({ signal }) => exportDesignSystem(designSystemId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: designSystemId !== null && designSystemId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof exportDesignSystem>>, TError, TData> & { queryKey: QueryKey }
}

export type ExportDesignSystemQueryResult = NonNullable<Awaited<ReturnType<typeof exportDesignSystem>>>
export type ExportDesignSystemQueryError = Error


/**
 * @summary Export a design system as a normalised Material UI token document
 */

export function useExportDesignSystem<TData = Awaited<ReturnType<typeof exportDesignSystem>>, TError = Error>(
 designSystemId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof exportDesignSystem>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getExportDesignSystemQueryOptions(designSystemId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getActivateDesignSystemUrl = (designSystemId: string,) => {




  return `/api/access/design-systems/${designSystemId}/activate`
}

/**
 * Requires tenant.settings.manage. Design systems with blocking contrast issues cannot be activated.
 * @summary Use a ready design system for the tenant, or for one entity
 */
export const activateDesignSystem = async (designSystemId: string,
    designSystemScopeSelection: DesignSystemScopeSelection, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<void>(getActivateDesignSystemUrl(designSystemId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(designSystemScopeSelection)
  }
);}





export const getActivateDesignSystemMutationKey = () => ['activateDesignSystem'] as const;

export const getActivateDesignSystemMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof activateDesignSystem>>, TError,ActivateDesignSystemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof activateDesignSystem>>, TError,ActivateDesignSystemMutationVariables, TContext> => {

const mutationKey = getActivateDesignSystemMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof activateDesignSystem>>, ActivateDesignSystemMutationVariables> = (props) => {
          const {designSystemId,data} = props ?? {};

          return  activateDesignSystem(designSystemId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ActivateDesignSystemMutationResult = NonNullable<Awaited<ReturnType<typeof activateDesignSystem>>>
    export type ActivateDesignSystemMutationBody = DesignSystemScopeSelection
    export type ActivateDesignSystemMutationError = Error
    export type ActivateDesignSystemMutationVariables = {designSystemId: string;data: DesignSystemScopeSelection}

    /**
 * @summary Use a ready design system for the tenant, or for one entity
 */
export const useActivateDesignSystem = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof activateDesignSystem>>, TError,ActivateDesignSystemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof activateDesignSystem>>,
        TError,
        ActivateDesignSystemMutationVariables,
        TContext
      > => {
      return useMutation(getActivateDesignSystemMutationOptions(options));
    }

export const getGetResolvedBrandingUrl = () => {




  return `/api/branding/resolved`
}

/**
 * Resolution order is entity, then tenant, then the platform default.
 * @summary Design-system variables for the signed-in staff member's selected entity
 */
export const getResolvedBranding = async ( options?: Parameters<typeof customFetch>[1]): Promise<ResolvedBranding> => {

  return customFetch<ResolvedBranding>(getGetResolvedBrandingUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetResolvedBrandingQueryKey = () => {
    return [
    `/api/branding/resolved`
    ] as const;
    }


export const getGetResolvedBrandingQueryOptions = <TData = Awaited<ReturnType<typeof getResolvedBranding>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getResolvedBranding>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetResolvedBrandingQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getResolvedBranding>>> = ({ signal }) => getResolvedBranding({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getResolvedBranding>>, TError, TData> & { queryKey: QueryKey }
}

export type GetResolvedBrandingQueryResult = NonNullable<Awaited<ReturnType<typeof getResolvedBranding>>>
export type GetResolvedBrandingQueryError = Error


/**
 * @summary Design-system variables for the signed-in staff member's selected entity
 */

export function useGetResolvedBranding<TData = Awaited<ReturnType<typeof getResolvedBranding>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getResolvedBranding>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetResolvedBrandingQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListAccessContextsUrl = (params?: ListAccessContextsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/contexts?${stringifiedParams}` : `/api/access/contexts`
}

/**
 * @summary List active tenant/entity contexts available to the signed-in user
 */
export const listAccessContexts = async (params?: ListAccessContextsParams, options?: Parameters<typeof customFetch>[1]): Promise<AccessContextList> => {

  return customFetch<AccessContextList>(getListAccessContextsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAccessContextsQueryKey = (params?: ListAccessContextsParams,) => {
    return [
    `/api/access/contexts`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAccessContextsQueryOptions = <TData = Awaited<ReturnType<typeof listAccessContexts>>, TError = Error>(params?: ListAccessContextsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAccessContexts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAccessContextsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAccessContexts>>> = ({ signal }) => listAccessContexts(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAccessContexts>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAccessContextsQueryResult = NonNullable<Awaited<ReturnType<typeof listAccessContexts>>>
export type ListAccessContextsQueryError = Error


/**
 * @summary List active tenant/entity contexts available to the signed-in user
 */

export function useListAccessContexts<TData = Awaited<ReturnType<typeof listAccessContexts>>, TError = Error>(
 params?: ListAccessContextsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAccessContexts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAccessContextsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetAccessContextUrl = () => {




  return `/api/access/context`
}

/**
 * @summary Resolve the selected active context and effective permissions
 */
export const getAccessContext = async ( options?: Parameters<typeof customFetch>[1]): Promise<AccessContextSnapshot> => {

  return customFetch<AccessContextSnapshot>(getGetAccessContextUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAccessContextQueryKey = () => {
    return [
    `/api/access/context`
    ] as const;
    }


export const getGetAccessContextQueryOptions = <TData = Awaited<ReturnType<typeof getAccessContext>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccessContext>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAccessContextQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAccessContext>>> = ({ signal }) => getAccessContext({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAccessContext>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAccessContextQueryResult = NonNullable<Awaited<ReturnType<typeof getAccessContext>>>
export type GetAccessContextQueryError = Error


/**
 * @summary Resolve the selected active context and effective permissions
 */

export function useGetAccessContext<TData = Awaited<ReturnType<typeof getAccessContext>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccessContext>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAccessContextQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetAccessContextLogoUrl = () => {




  return `/api/access/context/logo`
}

/**
 * @summary Download the active entity logo for an authorized entity reader
 */
export const getAccessContextLogo = async ( options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getGetAccessContextLogoUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAccessContextLogoQueryKey = () => {
    return [
    `/api/access/context/logo`
    ] as const;
    }


export const getGetAccessContextLogoQueryOptions = <TData = Awaited<ReturnType<typeof getAccessContextLogo>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccessContextLogo>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAccessContextLogoQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAccessContextLogo>>> = ({ signal }) => getAccessContextLogo({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAccessContextLogo>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAccessContextLogoQueryResult = NonNullable<Awaited<ReturnType<typeof getAccessContextLogo>>>
export type GetAccessContextLogoQueryError = Error


/**
 * @summary Download the active entity logo for an authorized entity reader
 */

export function useGetAccessContextLogo<TData = Awaited<ReturnType<typeof getAccessContextLogo>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccessContextLogo>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAccessContextLogoQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetAccessAdministrationUrl = (params?: GetAccessAdministrationParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/administration?${stringifiedParams}` : `/api/access/administration`
}

/**
 * @summary Get the selected tenant/entity access administration view
 */
export const getAccessAdministration = async (params?: GetAccessAdministrationParams, options?: Parameters<typeof customFetch>[1]): Promise<AccessAdministrationPage> => {

  return customFetch<AccessAdministrationPage>(getGetAccessAdministrationUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAccessAdministrationQueryKey = (params?: GetAccessAdministrationParams,) => {
    return [
    `/api/access/administration`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetAccessAdministrationQueryOptions = <TData = Awaited<ReturnType<typeof getAccessAdministration>>, TError = Error>(params?: GetAccessAdministrationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccessAdministration>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAccessAdministrationQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAccessAdministration>>> = ({ signal }) => getAccessAdministration(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAccessAdministration>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAccessAdministrationQueryResult = NonNullable<Awaited<ReturnType<typeof getAccessAdministration>>>
export type GetAccessAdministrationQueryError = Error


/**
 * @summary Get the selected tenant/entity access administration view
 */

export function useGetAccessAdministration<TData = Awaited<ReturnType<typeof getAccessAdministration>>, TError = Error>(
 params?: GetAccessAdministrationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccessAdministration>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAccessAdministrationQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateAccessInvitationUrl = () => {




  return `/api/access/invitations`
}

/**
 * @summary Invite a person to the selected tenant and entity
 */
export const createAccessInvitation = async (accessInvitationCreate: AccessInvitationCreate, options?: Parameters<typeof customFetch>[1]): Promise<AccessInvitationCreated> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccessInvitationCreated>(getCreateAccessInvitationUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accessInvitationCreate)
  }
);}





export const getCreateAccessInvitationMutationKey = () => ['createAccessInvitation'] as const;

export const getCreateAccessInvitationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAccessInvitation>>, TError,CreateAccessInvitationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createAccessInvitation>>, TError,CreateAccessInvitationMutationVariables, TContext> => {

const mutationKey = getCreateAccessInvitationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAccessInvitation>>, CreateAccessInvitationMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createAccessInvitation(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateAccessInvitationMutationResult = NonNullable<Awaited<ReturnType<typeof createAccessInvitation>>>
    export type CreateAccessInvitationMutationBody = AccessInvitationCreate
    export type CreateAccessInvitationMutationError = unknown
    export type CreateAccessInvitationMutationVariables = {data: AccessInvitationCreate}

    /**
 * @summary Invite a person to the selected tenant and entity
 */
export const useCreateAccessInvitation = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAccessInvitation>>, TError,CreateAccessInvitationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createAccessInvitation>>,
        TError,
        CreateAccessInvitationMutationVariables,
        TContext
      > => {
      return useMutation(getCreateAccessInvitationMutationOptions(options));
    }

export const getRevokeAccessInvitationUrl = (id: string,) => {




  return `/api/access/invitations/${id}`
}

/**
 * @summary Revoke a pending invitation
 */
export const revokeAccessInvitation = async (id: string,
    accessReason: AccessReason, options?: Parameters<typeof customFetch>[1]): Promise<AccessMutationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccessMutationResult>(getRevokeAccessInvitationUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accessReason)
  }
);}





export const getRevokeAccessInvitationMutationKey = () => ['revokeAccessInvitation'] as const;

export const getRevokeAccessInvitationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeAccessInvitation>>, TError,RevokeAccessInvitationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokeAccessInvitation>>, TError,RevokeAccessInvitationMutationVariables, TContext> => {

const mutationKey = getRevokeAccessInvitationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokeAccessInvitation>>, RevokeAccessInvitationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  revokeAccessInvitation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokeAccessInvitationMutationResult = NonNullable<Awaited<ReturnType<typeof revokeAccessInvitation>>>
    export type RevokeAccessInvitationMutationBody = AccessReason
    export type RevokeAccessInvitationMutationError = unknown
    export type RevokeAccessInvitationMutationVariables = {id: string;data: AccessReason}

    /**
 * @summary Revoke a pending invitation
 */
export const useRevokeAccessInvitation = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeAccessInvitation>>, TError,RevokeAccessInvitationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof revokeAccessInvitation>>,
        TError,
        RevokeAccessInvitationMutationVariables,
        TContext
      > => {
      return useMutation(getRevokeAccessInvitationMutationOptions(options));
    }

export const getAcceptAccessInvitationUrl = () => {




  return `/api/access/invitations/accept`
}

/**
 * @summary Accept an email-bound invitation as the signed-in identity
 */
export const acceptAccessInvitation = async (accessInvitationAccept: AccessInvitationAccept, options?: Parameters<typeof customFetch>[1]): Promise<AccessInvitationAccepted> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccessInvitationAccepted>(getAcceptAccessInvitationUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accessInvitationAccept)
  }
);}





export const getAcceptAccessInvitationMutationKey = () => ['acceptAccessInvitation'] as const;

export const getAcceptAccessInvitationMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof acceptAccessInvitation>>, TError,AcceptAccessInvitationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof acceptAccessInvitation>>, TError,AcceptAccessInvitationMutationVariables, TContext> => {

const mutationKey = getAcceptAccessInvitationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof acceptAccessInvitation>>, AcceptAccessInvitationMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  acceptAccessInvitation(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AcceptAccessInvitationMutationResult = NonNullable<Awaited<ReturnType<typeof acceptAccessInvitation>>>
    export type AcceptAccessInvitationMutationBody = AccessInvitationAccept
    export type AcceptAccessInvitationMutationError = Error
    export type AcceptAccessInvitationMutationVariables = {data: AccessInvitationAccept}

    /**
 * @summary Accept an email-bound invitation as the signed-in identity
 */
export const useAcceptAccessInvitation = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof acceptAccessInvitation>>, TError,AcceptAccessInvitationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof acceptAccessInvitation>>,
        TError,
        AcceptAccessInvitationMutationVariables,
        TContext
      > => {
      return useMutation(getAcceptAccessInvitationMutationOptions(options));
    }

export const getGrantAccessMemberEntityUrl = (id: string,) => {




  return `/api/access/members/${id}/grant`
}

/**
 * @summary Grant a tenant member access to the selected entity
 */
export const grantAccessMemberEntity = async (id: string,
    accessEntityGrant?: AccessEntityGrant, options?: Parameters<typeof customFetch>[1]): Promise<AccessMutationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccessMutationResult>(getGrantAccessMemberEntityUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accessEntityGrant)
  }
);}





export const getGrantAccessMemberEntityMutationKey = () => ['grantAccessMemberEntity'] as const;

export const getGrantAccessMemberEntityMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof grantAccessMemberEntity>>, TError,GrantAccessMemberEntityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof grantAccessMemberEntity>>, TError,GrantAccessMemberEntityMutationVariables, TContext> => {

const mutationKey = getGrantAccessMemberEntityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof grantAccessMemberEntity>>, GrantAccessMemberEntityMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  grantAccessMemberEntity(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GrantAccessMemberEntityMutationResult = NonNullable<Awaited<ReturnType<typeof grantAccessMemberEntity>>>
    export type GrantAccessMemberEntityMutationBody = AccessEntityGrant | undefined
    export type GrantAccessMemberEntityMutationError = unknown
    export type GrantAccessMemberEntityMutationVariables = {id: string;data?: AccessEntityGrant}

    /**
 * @summary Grant a tenant member access to the selected entity
 */
export const useGrantAccessMemberEntity = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof grantAccessMemberEntity>>, TError,GrantAccessMemberEntityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof grantAccessMemberEntity>>,
        TError,
        GrantAccessMemberEntityMutationVariables,
        TContext
      > => {
      return useMutation(getGrantAccessMemberEntityMutationOptions(options));
    }

export const getRevokeAccessMemberEntityUrl = (id: string,) => {




  return `/api/access/members/${id}/grant`
}

/**
 * @summary Revoke a tenant member's access to the selected entity
 */
export const revokeAccessMemberEntity = async (id: string,
    accessReason: AccessReason, options?: Parameters<typeof customFetch>[1]): Promise<AccessMutationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccessMutationResult>(getRevokeAccessMemberEntityUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accessReason)
  }
);}





export const getRevokeAccessMemberEntityMutationKey = () => ['revokeAccessMemberEntity'] as const;

export const getRevokeAccessMemberEntityMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeAccessMemberEntity>>, TError,RevokeAccessMemberEntityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokeAccessMemberEntity>>, TError,RevokeAccessMemberEntityMutationVariables, TContext> => {

const mutationKey = getRevokeAccessMemberEntityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokeAccessMemberEntity>>, RevokeAccessMemberEntityMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  revokeAccessMemberEntity(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokeAccessMemberEntityMutationResult = NonNullable<Awaited<ReturnType<typeof revokeAccessMemberEntity>>>
    export type RevokeAccessMemberEntityMutationBody = AccessReason
    export type RevokeAccessMemberEntityMutationError = unknown
    export type RevokeAccessMemberEntityMutationVariables = {id: string;data: AccessReason}

    /**
 * @summary Revoke a tenant member's access to the selected entity
 */
export const useRevokeAccessMemberEntity = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeAccessMemberEntity>>, TError,RevokeAccessMemberEntityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof revokeAccessMemberEntity>>,
        TError,
        RevokeAccessMemberEntityMutationVariables,
        TContext
      > => {
      return useMutation(getRevokeAccessMemberEntityMutationOptions(options));
    }

export const getRevokeAccessMemberUrl = (id: string,) => {




  return `/api/access/members/${id}`
}

/**
 * @summary Revoke tenant membership, roles, and grants atomically
 */
export const revokeAccessMember = async (id: string,
    accessReason: AccessReason, options?: Parameters<typeof customFetch>[1]): Promise<AccessMutationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccessMutationResult>(getRevokeAccessMemberUrl(id),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accessReason)
  }
);}





export const getRevokeAccessMemberMutationKey = () => ['revokeAccessMember'] as const;

export const getRevokeAccessMemberMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeAccessMember>>, TError,RevokeAccessMemberMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokeAccessMember>>, TError,RevokeAccessMemberMutationVariables, TContext> => {

const mutationKey = getRevokeAccessMemberMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokeAccessMember>>, RevokeAccessMemberMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  revokeAccessMember(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokeAccessMemberMutationResult = NonNullable<Awaited<ReturnType<typeof revokeAccessMember>>>
    export type RevokeAccessMemberMutationBody = AccessReason
    export type RevokeAccessMemberMutationError = unknown
    export type RevokeAccessMemberMutationVariables = {id: string;data: AccessReason}

    /**
 * @summary Revoke tenant membership, roles, and grants atomically
 */
export const useRevokeAccessMember = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeAccessMember>>, TError,RevokeAccessMemberMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof revokeAccessMember>>,
        TError,
        RevokeAccessMemberMutationVariables,
        TContext
      > => {
      return useMutation(getRevokeAccessMemberMutationOptions(options));
    }

export const getSetAccessMemberRoleUrl = (id: string,) => {




  return `/api/access/members/${id}/role`
}

/**
 * @summary Set the canonical owner or entity-manager role
 */
export const setAccessMemberRole = async (id: string,
    setUserRoleBody: SetUserRoleBody, options?: Parameters<typeof customFetch>[1]): Promise<AccessMutationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccessMutationResult>(getSetAccessMemberRoleUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(setUserRoleBody)
  }
);}





export const getSetAccessMemberRoleMutationKey = () => ['setAccessMemberRole'] as const;

export const getSetAccessMemberRoleMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setAccessMemberRole>>, TError,SetAccessMemberRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setAccessMemberRole>>, TError,SetAccessMemberRoleMutationVariables, TContext> => {

const mutationKey = getSetAccessMemberRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setAccessMemberRole>>, SetAccessMemberRoleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  setAccessMemberRole(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetAccessMemberRoleMutationResult = NonNullable<Awaited<ReturnType<typeof setAccessMemberRole>>>
    export type SetAccessMemberRoleMutationBody = SetUserRoleBody
    export type SetAccessMemberRoleMutationError = Error
    export type SetAccessMemberRoleMutationVariables = {id: string;data: SetUserRoleBody}

    /**
 * @summary Set the canonical owner or entity-manager role
 */
export const useSetAccessMemberRole = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setAccessMemberRole>>, TError,SetAccessMemberRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof setAccessMemberRole>>,
        TError,
        SetAccessMemberRoleMutationVariables,
        TContext
      > => {
      return useMutation(getSetAccessMemberRoleMutationOptions(options));
    }

export const getUpdateAccessTenantProfileUrl = () => {




  return `/api/access/tenant-profile`
}

/**
 * @summary Update the selected tenant profile
 */
export const updateAccessTenantProfile = async (accessProfileUpdate: AccessProfileUpdate, options?: Parameters<typeof customFetch>[1]): Promise<AccessMutationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccessMutationResult>(getUpdateAccessTenantProfileUrl(),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accessProfileUpdate)
  }
);}





export const getUpdateAccessTenantProfileMutationKey = () => ['updateAccessTenantProfile'] as const;

export const getUpdateAccessTenantProfileMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAccessTenantProfile>>, TError,UpdateAccessTenantProfileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateAccessTenantProfile>>, TError,UpdateAccessTenantProfileMutationVariables, TContext> => {

const mutationKey = getUpdateAccessTenantProfileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAccessTenantProfile>>, UpdateAccessTenantProfileMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateAccessTenantProfile(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateAccessTenantProfileMutationResult = NonNullable<Awaited<ReturnType<typeof updateAccessTenantProfile>>>
    export type UpdateAccessTenantProfileMutationBody = AccessProfileUpdate
    export type UpdateAccessTenantProfileMutationError = unknown
    export type UpdateAccessTenantProfileMutationVariables = {data: AccessProfileUpdate}

    /**
 * @summary Update the selected tenant profile
 */
export const useUpdateAccessTenantProfile = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAccessTenantProfile>>, TError,UpdateAccessTenantProfileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateAccessTenantProfile>>,
        TError,
        UpdateAccessTenantProfileMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateAccessTenantProfileMutationOptions(options));
    }

export const getUpdateAccessEntityProfileUrl = () => {




  return `/api/access/entity-profile`
}

/**
 * @summary Update the selected entity profile
 */
export const updateAccessEntityProfile = async (accessProfileUpdate: AccessProfileUpdate, options?: Parameters<typeof customFetch>[1]): Promise<AccessMutationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccessMutationResult>(getUpdateAccessEntityProfileUrl(),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accessProfileUpdate)
  }
);}





export const getUpdateAccessEntityProfileMutationKey = () => ['updateAccessEntityProfile'] as const;

export const getUpdateAccessEntityProfileMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAccessEntityProfile>>, TError,UpdateAccessEntityProfileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateAccessEntityProfile>>, TError,UpdateAccessEntityProfileMutationVariables, TContext> => {

const mutationKey = getUpdateAccessEntityProfileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAccessEntityProfile>>, UpdateAccessEntityProfileMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateAccessEntityProfile(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateAccessEntityProfileMutationResult = NonNullable<Awaited<ReturnType<typeof updateAccessEntityProfile>>>
    export type UpdateAccessEntityProfileMutationBody = AccessProfileUpdate
    export type UpdateAccessEntityProfileMutationError = unknown
    export type UpdateAccessEntityProfileMutationVariables = {data: AccessProfileUpdate}

    /**
 * @summary Update the selected entity profile
 */
export const useUpdateAccessEntityProfile = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAccessEntityProfile>>, TError,UpdateAccessEntityProfileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateAccessEntityProfile>>,
        TError,
        UpdateAccessEntityProfileMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateAccessEntityProfileMutationOptions(options));
    }

export const getSetAccessTenantSettingUrl = (key: string,) => {




  return `/api/access/settings/${key}`
}

/**
 * @summary Save a selected-tenant setting
 */
export const setAccessTenantSetting = async (key: string,
    accessSettingUpdate: AccessSettingUpdate, options?: Parameters<typeof customFetch>[1]): Promise<AccessMutationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccessMutationResult>(getSetAccessTenantSettingUrl(key),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accessSettingUpdate)
  }
);}





export const getSetAccessTenantSettingMutationKey = () => ['setAccessTenantSetting'] as const;

export const getSetAccessTenantSettingMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setAccessTenantSetting>>, TError,SetAccessTenantSettingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setAccessTenantSetting>>, TError,SetAccessTenantSettingMutationVariables, TContext> => {

const mutationKey = getSetAccessTenantSettingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setAccessTenantSetting>>, SetAccessTenantSettingMutationVariables> = (props) => {
          const {key,data} = props ?? {};

          return  setAccessTenantSetting(key,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetAccessTenantSettingMutationResult = NonNullable<Awaited<ReturnType<typeof setAccessTenantSetting>>>
    export type SetAccessTenantSettingMutationBody = AccessSettingUpdate
    export type SetAccessTenantSettingMutationError = unknown
    export type SetAccessTenantSettingMutationVariables = {key: string;data: AccessSettingUpdate}

    /**
 * @summary Save a selected-tenant setting
 */
export const useSetAccessTenantSetting = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setAccessTenantSetting>>, TError,SetAccessTenantSettingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof setAccessTenantSetting>>,
        TError,
        SetAccessTenantSettingMutationVariables,
        TContext
      > => {
      return useMutation(getSetAccessTenantSettingMutationOptions(options));
    }

export const getSetAccessTenantModuleUrl = (key: string,) => {




  return `/api/access/modules/${key}`
}

/**
 * @summary Enable or disable a selected-tenant module
 */
export const setAccessTenantModule = async (key: string,
    accessModuleUpdate: AccessModuleUpdate, options?: Parameters<typeof customFetch>[1]): Promise<AccessMutationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccessMutationResult>(getSetAccessTenantModuleUrl(key),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accessModuleUpdate)
  }
);}





export const getSetAccessTenantModuleMutationKey = () => ['setAccessTenantModule'] as const;

export const getSetAccessTenantModuleMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setAccessTenantModule>>, TError,SetAccessTenantModuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setAccessTenantModule>>, TError,SetAccessTenantModuleMutationVariables, TContext> => {

const mutationKey = getSetAccessTenantModuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setAccessTenantModule>>, SetAccessTenantModuleMutationVariables> = (props) => {
          const {key,data} = props ?? {};

          return  setAccessTenantModule(key,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetAccessTenantModuleMutationResult = NonNullable<Awaited<ReturnType<typeof setAccessTenantModule>>>
    export type SetAccessTenantModuleMutationBody = AccessModuleUpdate
    export type SetAccessTenantModuleMutationError = unknown
    export type SetAccessTenantModuleMutationVariables = {key: string;data: AccessModuleUpdate}

    /**
 * @summary Enable or disable a selected-tenant module
 */
export const useSetAccessTenantModule = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setAccessTenantModule>>, TError,SetAccessTenantModuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof setAccessTenantModule>>,
        TError,
        SetAccessTenantModuleMutationVariables,
        TContext
      > => {
      return useMutation(getSetAccessTenantModuleMutationOptions(options));
    }

export const getListAccessAuditUrl = (params?: ListAccessAuditParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/audit?${stringifiedParams}` : `/api/access/audit`
}

/**
 * Tenant auditors may query their tenant; entity managers must provide the currently selected entityId.
 * @summary List immutable access and security audit events for the selected tenant or managed entity
 */
export const listAccessAudit = async (params?: ListAccessAuditParams, options?: Parameters<typeof customFetch>[1]): Promise<AccessAuditPage> => {

  return customFetch<AccessAuditPage>(getListAccessAuditUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAccessAuditQueryKey = (params?: ListAccessAuditParams,) => {
    return [
    `/api/access/audit`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAccessAuditQueryOptions = <TData = Awaited<ReturnType<typeof listAccessAudit>>, TError = unknown>(params?: ListAccessAuditParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAccessAudit>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAccessAuditQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAccessAudit>>> = ({ signal }) => listAccessAudit(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAccessAudit>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAccessAuditQueryResult = NonNullable<Awaited<ReturnType<typeof listAccessAudit>>>
export type ListAccessAuditQueryError = unknown


/**
 * @summary List immutable access and security audit events for the selected tenant or managed entity
 */

export function useListAccessAudit<TData = Awaited<ReturnType<typeof listAccessAudit>>, TError = unknown>(
 params?: ListAccessAuditParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAccessAudit>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAccessAuditQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListCustomPagesUrl = () => {




  return `/api/custom-pages`
}

/**
 * @summary List published global custom pages for staff navigation
 */
export const listCustomPages = async ( options?: Parameters<typeof customFetch>[1]): Promise<CustomPageSummary[]> => {

  return customFetch<CustomPageSummary[]>(getListCustomPagesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCustomPagesQueryKey = () => {
    return [
    `/api/custom-pages`
    ] as const;
    }


export const getListCustomPagesQueryOptions = <TData = Awaited<ReturnType<typeof listCustomPages>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCustomPages>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCustomPagesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCustomPages>>> = ({ signal }) => listCustomPages({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCustomPages>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCustomPagesQueryResult = NonNullable<Awaited<ReturnType<typeof listCustomPages>>>
export type ListCustomPagesQueryError = Error


/**
 * @summary List published global custom pages for staff navigation
 */

export function useListCustomPages<TData = Awaited<ReturnType<typeof listCustomPages>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCustomPages>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCustomPagesQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetCustomPageUrl = (slug: string,) => {




  return `/api/custom-pages/${slug}`
}

/**
 * @summary Get a published global custom page and its widgets by slug
 */
export const getCustomPage = async (slug: string, options?: Parameters<typeof customFetch>[1]): Promise<CustomPageDetail> => {

  return customFetch<CustomPageDetail>(getGetCustomPageUrl(slug),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetCustomPageQueryKey = (slug: string,) => {
    return [
    `/api/custom-pages/${slug}`
    ] as const;
    }


export const getGetCustomPageQueryOptions = <TData = Awaited<ReturnType<typeof getCustomPage>>, TError = Error>(slug: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCustomPage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCustomPageQueryKey(slug);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCustomPage>>> = ({ signal }) => getCustomPage(slug, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: slug !== null && slug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCustomPage>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCustomPageQueryResult = NonNullable<Awaited<ReturnType<typeof getCustomPage>>>
export type GetCustomPageQueryError = Error


/**
 * @summary Get a published global custom page and its widgets by slug
 */

export function useGetCustomPage<TData = Awaited<ReturnType<typeof getCustomPage>>, TError = Error>(
 slug: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCustomPage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCustomPageQueryOptions(slug,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListAdminCustomPagesUrl = () => {




  return `/api/admin/custom-pages`
}

/**
 * @summary List all global custom pages including drafts (admin only)
 */
export const listAdminCustomPages = async ( options?: Parameters<typeof customFetch>[1]): Promise<CustomPageSummary[]> => {

  return customFetch<CustomPageSummary[]>(getListAdminCustomPagesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAdminCustomPagesQueryKey = () => {
    return [
    `/api/admin/custom-pages`
    ] as const;
    }


export const getListAdminCustomPagesQueryOptions = <TData = Awaited<ReturnType<typeof listAdminCustomPages>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAdminCustomPages>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAdminCustomPagesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAdminCustomPages>>> = ({ signal }) => listAdminCustomPages({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAdminCustomPages>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAdminCustomPagesQueryResult = NonNullable<Awaited<ReturnType<typeof listAdminCustomPages>>>
export type ListAdminCustomPagesQueryError = Error


/**
 * @summary List all global custom pages including drafts (admin only)
 */

export function useListAdminCustomPages<TData = Awaited<ReturnType<typeof listAdminCustomPages>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAdminCustomPages>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAdminCustomPagesQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateAdminCustomPageUrl = () => {




  return `/api/admin/custom-pages`
}

/**
 * @summary Create a global custom page (admin only)
 */
export const createAdminCustomPage = async (customPageCreate: CustomPageCreate, options?: Parameters<typeof customFetch>[1]): Promise<CustomPageDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CustomPageDetail>(getCreateAdminCustomPageUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(customPageCreate)
  }
);}





export const getCreateAdminCustomPageMutationKey = () => ['createAdminCustomPage'] as const;

export const getCreateAdminCustomPageMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAdminCustomPage>>, TError,CreateAdminCustomPageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createAdminCustomPage>>, TError,CreateAdminCustomPageMutationVariables, TContext> => {

const mutationKey = getCreateAdminCustomPageMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAdminCustomPage>>, CreateAdminCustomPageMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createAdminCustomPage(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateAdminCustomPageMutationResult = NonNullable<Awaited<ReturnType<typeof createAdminCustomPage>>>
    export type CreateAdminCustomPageMutationBody = CustomPageCreate
    export type CreateAdminCustomPageMutationError = Error
    export type CreateAdminCustomPageMutationVariables = {data: CustomPageCreate}

    /**
 * @summary Create a global custom page (admin only)
 */
export const useCreateAdminCustomPage = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAdminCustomPage>>, TError,CreateAdminCustomPageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createAdminCustomPage>>,
        TError,
        CreateAdminCustomPageMutationVariables,
        TContext
      > => {
      return useMutation(getCreateAdminCustomPageMutationOptions(options));
    }

export const getGetAdminCustomPageUrl = (id: string,) => {




  return `/api/admin/custom-pages/${id}`
}

/**
 * @summary Get a global custom page and its widgets including drafts (admin only)
 */
export const getAdminCustomPage = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<CustomPageDetail> => {

  return customFetch<CustomPageDetail>(getGetAdminCustomPageUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAdminCustomPageQueryKey = (id: string,) => {
    return [
    `/api/admin/custom-pages/${id}`
    ] as const;
    }


export const getGetAdminCustomPageQueryOptions = <TData = Awaited<ReturnType<typeof getAdminCustomPage>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAdminCustomPage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAdminCustomPageQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAdminCustomPage>>> = ({ signal }) => getAdminCustomPage(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAdminCustomPage>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAdminCustomPageQueryResult = NonNullable<Awaited<ReturnType<typeof getAdminCustomPage>>>
export type GetAdminCustomPageQueryError = Error


/**
 * @summary Get a global custom page and its widgets including drafts (admin only)
 */

export function useGetAdminCustomPage<TData = Awaited<ReturnType<typeof getAdminCustomPage>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAdminCustomPage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAdminCustomPageQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateAdminCustomPageUrl = (id: string,) => {




  return `/api/admin/custom-pages/${id}`
}

/**
 * @summary Update page metadata, publication state, or display order (admin only)
 */
export const updateAdminCustomPage = async (id: string,
    customPageUpdate: CustomPageUpdate, options?: Parameters<typeof customFetch>[1]): Promise<CustomPageDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CustomPageDetail>(getUpdateAdminCustomPageUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(customPageUpdate)
  }
);}





export const getUpdateAdminCustomPageMutationKey = () => ['updateAdminCustomPage'] as const;

export const getUpdateAdminCustomPageMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAdminCustomPage>>, TError,UpdateAdminCustomPageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateAdminCustomPage>>, TError,UpdateAdminCustomPageMutationVariables, TContext> => {

const mutationKey = getUpdateAdminCustomPageMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAdminCustomPage>>, UpdateAdminCustomPageMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateAdminCustomPage(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateAdminCustomPageMutationResult = NonNullable<Awaited<ReturnType<typeof updateAdminCustomPage>>>
    export type UpdateAdminCustomPageMutationBody = CustomPageUpdate
    export type UpdateAdminCustomPageMutationError = Error
    export type UpdateAdminCustomPageMutationVariables = {id: string;data: CustomPageUpdate}

    /**
 * @summary Update page metadata, publication state, or display order (admin only)
 */
export const useUpdateAdminCustomPage = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAdminCustomPage>>, TError,UpdateAdminCustomPageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateAdminCustomPage>>,
        TError,
        UpdateAdminCustomPageMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateAdminCustomPageMutationOptions(options));
    }

export const getDeleteAdminCustomPageUrl = (id: string,) => {




  return `/api/admin/custom-pages/${id}`
}

/**
 * @summary Delete a page and only its widget configuration (admin only)
 */
export const deleteAdminCustomPage = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteAdminCustomPageUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteAdminCustomPageMutationKey = () => ['deleteAdminCustomPage'] as const;

export const getDeleteAdminCustomPageMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAdminCustomPage>>, TError,DeleteAdminCustomPageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteAdminCustomPage>>, TError,DeleteAdminCustomPageMutationVariables, TContext> => {

const mutationKey = getDeleteAdminCustomPageMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteAdminCustomPage>>, DeleteAdminCustomPageMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteAdminCustomPage(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteAdminCustomPageMutationResult = NonNullable<Awaited<ReturnType<typeof deleteAdminCustomPage>>>

    export type DeleteAdminCustomPageMutationError = Error
    export type DeleteAdminCustomPageMutationVariables = {id: string}

    /**
 * @summary Delete a page and only its widget configuration (admin only)
 */
export const useDeleteAdminCustomPage = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAdminCustomPage>>, TError,DeleteAdminCustomPageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteAdminCustomPage>>,
        TError,
        DeleteAdminCustomPageMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteAdminCustomPageMutationOptions(options));
    }

export const getCreateAdminCustomPageWidgetUrl = (id: string,) => {




  return `/api/admin/custom-pages/${id}/widgets`
}

/**
 * @summary Add a widget to a global custom page (admin only)
 */
export const createAdminCustomPageWidget = async (id: string,
    customPageWidgetCreate: CustomPageWidgetCreate, options?: Parameters<typeof customFetch>[1]): Promise<CustomPageWidget> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CustomPageWidget>(getCreateAdminCustomPageWidgetUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(customPageWidgetCreate)
  }
);}





export const getCreateAdminCustomPageWidgetMutationKey = () => ['createAdminCustomPageWidget'] as const;

export const getCreateAdminCustomPageWidgetMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAdminCustomPageWidget>>, TError,CreateAdminCustomPageWidgetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createAdminCustomPageWidget>>, TError,CreateAdminCustomPageWidgetMutationVariables, TContext> => {

const mutationKey = getCreateAdminCustomPageWidgetMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAdminCustomPageWidget>>, CreateAdminCustomPageWidgetMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createAdminCustomPageWidget(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateAdminCustomPageWidgetMutationResult = NonNullable<Awaited<ReturnType<typeof createAdminCustomPageWidget>>>
    export type CreateAdminCustomPageWidgetMutationBody = CustomPageWidgetCreate
    export type CreateAdminCustomPageWidgetMutationError = Error
    export type CreateAdminCustomPageWidgetMutationVariables = {id: string;data: CustomPageWidgetCreate}

    /**
 * @summary Add a widget to a global custom page (admin only)
 */
export const useCreateAdminCustomPageWidget = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAdminCustomPageWidget>>, TError,CreateAdminCustomPageWidgetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createAdminCustomPageWidget>>,
        TError,
        CreateAdminCustomPageWidgetMutationVariables,
        TContext
      > => {
      return useMutation(getCreateAdminCustomPageWidgetMutationOptions(options));
    }

export const getUpdateAdminCustomPageWidgetUrl = (id: string,
    widgetId: string,) => {




  return `/api/admin/custom-pages/${id}/widgets/${widgetId}`
}

/**
 * @summary Edit, resize, or reposition a page widget (admin only)
 */
export const updateAdminCustomPageWidget = async (id: string,
    widgetId: string,
    customPageWidgetUpdate: CustomPageWidgetUpdate, options?: Parameters<typeof customFetch>[1]): Promise<CustomPageWidget> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CustomPageWidget>(getUpdateAdminCustomPageWidgetUrl(id,widgetId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(customPageWidgetUpdate)
  }
);}





export const getUpdateAdminCustomPageWidgetMutationKey = () => ['updateAdminCustomPageWidget'] as const;

export const getUpdateAdminCustomPageWidgetMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAdminCustomPageWidget>>, TError,UpdateAdminCustomPageWidgetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateAdminCustomPageWidget>>, TError,UpdateAdminCustomPageWidgetMutationVariables, TContext> => {

const mutationKey = getUpdateAdminCustomPageWidgetMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAdminCustomPageWidget>>, UpdateAdminCustomPageWidgetMutationVariables> = (props) => {
          const {id,widgetId,data} = props ?? {};

          return  updateAdminCustomPageWidget(id,widgetId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateAdminCustomPageWidgetMutationResult = NonNullable<Awaited<ReturnType<typeof updateAdminCustomPageWidget>>>
    export type UpdateAdminCustomPageWidgetMutationBody = CustomPageWidgetUpdate
    export type UpdateAdminCustomPageWidgetMutationError = Error
    export type UpdateAdminCustomPageWidgetMutationVariables = {id: string;widgetId: string;data: CustomPageWidgetUpdate}

    /**
 * @summary Edit, resize, or reposition a page widget (admin only)
 */
export const useUpdateAdminCustomPageWidget = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAdminCustomPageWidget>>, TError,UpdateAdminCustomPageWidgetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateAdminCustomPageWidget>>,
        TError,
        UpdateAdminCustomPageWidgetMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateAdminCustomPageWidgetMutationOptions(options));
    }

export const getDeleteAdminCustomPageWidgetUrl = (id: string,
    widgetId: string,) => {




  return `/api/admin/custom-pages/${id}/widgets/${widgetId}`
}

/**
 * @summary Remove a page widget (admin only)
 */
export const deleteAdminCustomPageWidget = async (id: string,
    widgetId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteAdminCustomPageWidgetUrl(id,widgetId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteAdminCustomPageWidgetMutationKey = () => ['deleteAdminCustomPageWidget'] as const;

export const getDeleteAdminCustomPageWidgetMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAdminCustomPageWidget>>, TError,DeleteAdminCustomPageWidgetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteAdminCustomPageWidget>>, TError,DeleteAdminCustomPageWidgetMutationVariables, TContext> => {

const mutationKey = getDeleteAdminCustomPageWidgetMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteAdminCustomPageWidget>>, DeleteAdminCustomPageWidgetMutationVariables> = (props) => {
          const {id,widgetId} = props ?? {};

          return  deleteAdminCustomPageWidget(id,widgetId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteAdminCustomPageWidgetMutationResult = NonNullable<Awaited<ReturnType<typeof deleteAdminCustomPageWidget>>>

    export type DeleteAdminCustomPageWidgetMutationError = Error
    export type DeleteAdminCustomPageWidgetMutationVariables = {id: string;widgetId: string}

    /**
 * @summary Remove a page widget (admin only)
 */
export const useDeleteAdminCustomPageWidget = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAdminCustomPageWidget>>, TError,DeleteAdminCustomPageWidgetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteAdminCustomPageWidget>>,
        TError,
        DeleteAdminCustomPageWidgetMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteAdminCustomPageWidgetMutationOptions(options));
    }

export const getListAdminEventsUrl = () => {




  return `/api/admin/events`
}

/**
 * @summary List all events with RSVP and code counts (admin only)
 */
export const listAdminEvents = async ( options?: Parameters<typeof customFetch>[1]): Promise<AdminEventSummary[]> => {

  return customFetch<AdminEventSummary[]>(getListAdminEventsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAdminEventsQueryKey = () => {
    return [
    `/api/admin/events`
    ] as const;
    }


export const getListAdminEventsQueryOptions = <TData = Awaited<ReturnType<typeof listAdminEvents>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAdminEvents>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAdminEventsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAdminEvents>>> = ({ signal }) => listAdminEvents({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAdminEvents>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAdminEventsQueryResult = NonNullable<Awaited<ReturnType<typeof listAdminEvents>>>
export type ListAdminEventsQueryError = Error


/**
 * @summary List all events with RSVP and code counts (admin only)
 */

export function useListAdminEvents<TData = Awaited<ReturnType<typeof listAdminEvents>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAdminEvents>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAdminEventsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateAdminEventUrl = () => {




  return `/api/admin/events`
}

/**
 * @summary Create a new event (admin only)
 */
export const createAdminEvent = async (adminEventCreate: AdminEventCreate, options?: Parameters<typeof customFetch>[1]): Promise<AdminEventDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminEventDetail>(getCreateAdminEventUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminEventCreate)
  }
);}





export const getCreateAdminEventMutationKey = () => ['createAdminEvent'] as const;

export const getCreateAdminEventMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAdminEvent>>, TError,CreateAdminEventMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createAdminEvent>>, TError,CreateAdminEventMutationVariables, TContext> => {

const mutationKey = getCreateAdminEventMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAdminEvent>>, CreateAdminEventMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createAdminEvent(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateAdminEventMutationResult = NonNullable<Awaited<ReturnType<typeof createAdminEvent>>>
    export type CreateAdminEventMutationBody = AdminEventCreate
    export type CreateAdminEventMutationError = Error
    export type CreateAdminEventMutationVariables = {data: AdminEventCreate}

    /**
 * @summary Create a new event (admin only)
 */
export const useCreateAdminEvent = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAdminEvent>>, TError,CreateAdminEventMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createAdminEvent>>,
        TError,
        CreateAdminEventMutationVariables,
        TContext
      > => {
      return useMutation(getCreateAdminEventMutationOptions(options));
    }

export const getGetAdminEventUrl = (id: string,) => {




  return `/api/admin/events/${id}`
}

/**
 * @summary Get an event with agenda, speakers, and invitation codes (admin only)
 */
export const getAdminEvent = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<AdminEventDetail> => {

  return customFetch<AdminEventDetail>(getGetAdminEventUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAdminEventQueryKey = (id: string,) => {
    return [
    `/api/admin/events/${id}`
    ] as const;
    }


export const getGetAdminEventQueryOptions = <TData = Awaited<ReturnType<typeof getAdminEvent>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAdminEvent>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAdminEventQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAdminEvent>>> = ({ signal }) => getAdminEvent(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAdminEvent>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAdminEventQueryResult = NonNullable<Awaited<ReturnType<typeof getAdminEvent>>>
export type GetAdminEventQueryError = Error


/**
 * @summary Get an event with agenda, speakers, and invitation codes (admin only)
 */

export function useGetAdminEvent<TData = Awaited<ReturnType<typeof getAdminEvent>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAdminEvent>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAdminEventQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateAdminEventUrl = (id: string,) => {




  return `/api/admin/events/${id}`
}

/**
 * @summary Update event details, venue, hero content, or status (admin only)
 */
export const updateAdminEvent = async (id: string,
    adminEventUpdate: AdminEventUpdate, options?: Parameters<typeof customFetch>[1]): Promise<AdminEventDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminEventDetail>(getUpdateAdminEventUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminEventUpdate)
  }
);}





export const getUpdateAdminEventMutationKey = () => ['updateAdminEvent'] as const;

export const getUpdateAdminEventMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAdminEvent>>, TError,UpdateAdminEventMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateAdminEvent>>, TError,UpdateAdminEventMutationVariables, TContext> => {

const mutationKey = getUpdateAdminEventMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAdminEvent>>, UpdateAdminEventMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateAdminEvent(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateAdminEventMutationResult = NonNullable<Awaited<ReturnType<typeof updateAdminEvent>>>
    export type UpdateAdminEventMutationBody = AdminEventUpdate
    export type UpdateAdminEventMutationError = Error
    export type UpdateAdminEventMutationVariables = {id: string;data: AdminEventUpdate}

    /**
 * @summary Update event details, venue, hero content, or status (admin only)
 */
export const useUpdateAdminEvent = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAdminEvent>>, TError,UpdateAdminEventMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateAdminEvent>>,
        TError,
        UpdateAdminEventMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateAdminEventMutationOptions(options));
    }

export const getDeleteAdminEventUrl = (id: string,) => {




  return `/api/admin/events/${id}`
}

/**
 * @summary Delete an event and all its agenda, speakers, codes, and RSVPs (admin only)
 */
export const deleteAdminEvent = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteAdminEventUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteAdminEventMutationKey = () => ['deleteAdminEvent'] as const;

export const getDeleteAdminEventMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAdminEvent>>, TError,DeleteAdminEventMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteAdminEvent>>, TError,DeleteAdminEventMutationVariables, TContext> => {

const mutationKey = getDeleteAdminEventMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteAdminEvent>>, DeleteAdminEventMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteAdminEvent(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteAdminEventMutationResult = NonNullable<Awaited<ReturnType<typeof deleteAdminEvent>>>

    export type DeleteAdminEventMutationError = Error
    export type DeleteAdminEventMutationVariables = {id: string}

    /**
 * @summary Delete an event and all its agenda, speakers, codes, and RSVPs (admin only)
 */
export const useDeleteAdminEvent = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAdminEvent>>, TError,DeleteAdminEventMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteAdminEvent>>,
        TError,
        DeleteAdminEventMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteAdminEventMutationOptions(options));
    }

export const getCreateEventAgendaItemUrl = (id: string,) => {




  return `/api/admin/events/${id}/agenda`
}

/**
 * @summary Add an agenda item to an event (admin only)
 */
export const createEventAgendaItem = async (id: string,
    adminAgendaItemCreate: AdminAgendaItemCreate, options?: Parameters<typeof customFetch>[1]): Promise<AdminAgendaItem> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminAgendaItem>(getCreateEventAgendaItemUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminAgendaItemCreate)
  }
);}





export const getCreateEventAgendaItemMutationKey = () => ['createEventAgendaItem'] as const;

export const getCreateEventAgendaItemMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEventAgendaItem>>, TError,CreateEventAgendaItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createEventAgendaItem>>, TError,CreateEventAgendaItemMutationVariables, TContext> => {

const mutationKey = getCreateEventAgendaItemMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createEventAgendaItem>>, CreateEventAgendaItemMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createEventAgendaItem(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateEventAgendaItemMutationResult = NonNullable<Awaited<ReturnType<typeof createEventAgendaItem>>>
    export type CreateEventAgendaItemMutationBody = AdminAgendaItemCreate
    export type CreateEventAgendaItemMutationError = Error
    export type CreateEventAgendaItemMutationVariables = {id: string;data: AdminAgendaItemCreate}

    /**
 * @summary Add an agenda item to an event (admin only)
 */
export const useCreateEventAgendaItem = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEventAgendaItem>>, TError,CreateEventAgendaItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createEventAgendaItem>>,
        TError,
        CreateEventAgendaItemMutationVariables,
        TContext
      > => {
      return useMutation(getCreateEventAgendaItemMutationOptions(options));
    }

export const getUpdateEventAgendaItemUrl = (id: string,
    itemId: string,) => {




  return `/api/admin/events/${id}/agenda/${itemId}`
}

/**
 * @summary Update an agenda item (admin only)
 */
export const updateEventAgendaItem = async (id: string,
    itemId: string,
    adminAgendaItemUpdate: AdminAgendaItemUpdate, options?: Parameters<typeof customFetch>[1]): Promise<AdminAgendaItem> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminAgendaItem>(getUpdateEventAgendaItemUrl(id,itemId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminAgendaItemUpdate)
  }
);}





export const getUpdateEventAgendaItemMutationKey = () => ['updateEventAgendaItem'] as const;

export const getUpdateEventAgendaItemMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEventAgendaItem>>, TError,UpdateEventAgendaItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateEventAgendaItem>>, TError,UpdateEventAgendaItemMutationVariables, TContext> => {

const mutationKey = getUpdateEventAgendaItemMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateEventAgendaItem>>, UpdateEventAgendaItemMutationVariables> = (props) => {
          const {id,itemId,data} = props ?? {};

          return  updateEventAgendaItem(id,itemId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateEventAgendaItemMutationResult = NonNullable<Awaited<ReturnType<typeof updateEventAgendaItem>>>
    export type UpdateEventAgendaItemMutationBody = AdminAgendaItemUpdate
    export type UpdateEventAgendaItemMutationError = Error
    export type UpdateEventAgendaItemMutationVariables = {id: string;itemId: string;data: AdminAgendaItemUpdate}

    /**
 * @summary Update an agenda item (admin only)
 */
export const useUpdateEventAgendaItem = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEventAgendaItem>>, TError,UpdateEventAgendaItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateEventAgendaItem>>,
        TError,
        UpdateEventAgendaItemMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateEventAgendaItemMutationOptions(options));
    }

export const getDeleteEventAgendaItemUrl = (id: string,
    itemId: string,) => {




  return `/api/admin/events/${id}/agenda/${itemId}`
}

/**
 * @summary Remove an agenda item (admin only)
 */
export const deleteEventAgendaItem = async (id: string,
    itemId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteEventAgendaItemUrl(id,itemId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteEventAgendaItemMutationKey = () => ['deleteEventAgendaItem'] as const;

export const getDeleteEventAgendaItemMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteEventAgendaItem>>, TError,DeleteEventAgendaItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteEventAgendaItem>>, TError,DeleteEventAgendaItemMutationVariables, TContext> => {

const mutationKey = getDeleteEventAgendaItemMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteEventAgendaItem>>, DeleteEventAgendaItemMutationVariables> = (props) => {
          const {id,itemId} = props ?? {};

          return  deleteEventAgendaItem(id,itemId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteEventAgendaItemMutationResult = NonNullable<Awaited<ReturnType<typeof deleteEventAgendaItem>>>

    export type DeleteEventAgendaItemMutationError = Error
    export type DeleteEventAgendaItemMutationVariables = {id: string;itemId: string}

    /**
 * @summary Remove an agenda item (admin only)
 */
export const useDeleteEventAgendaItem = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteEventAgendaItem>>, TError,DeleteEventAgendaItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteEventAgendaItem>>,
        TError,
        DeleteEventAgendaItemMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteEventAgendaItemMutationOptions(options));
    }

export const getCreateEventSpeakerUrl = (id: string,) => {




  return `/api/admin/events/${id}/speakers`
}

/**
 * @summary Add a speaker to an event (admin only)
 */
export const createEventSpeaker = async (id: string,
    adminSpeakerCreate: AdminSpeakerCreate, options?: Parameters<typeof customFetch>[1]): Promise<AdminSpeaker> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminSpeaker>(getCreateEventSpeakerUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminSpeakerCreate)
  }
);}





export const getCreateEventSpeakerMutationKey = () => ['createEventSpeaker'] as const;

export const getCreateEventSpeakerMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEventSpeaker>>, TError,CreateEventSpeakerMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createEventSpeaker>>, TError,CreateEventSpeakerMutationVariables, TContext> => {

const mutationKey = getCreateEventSpeakerMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createEventSpeaker>>, CreateEventSpeakerMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createEventSpeaker(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateEventSpeakerMutationResult = NonNullable<Awaited<ReturnType<typeof createEventSpeaker>>>
    export type CreateEventSpeakerMutationBody = AdminSpeakerCreate
    export type CreateEventSpeakerMutationError = Error
    export type CreateEventSpeakerMutationVariables = {id: string;data: AdminSpeakerCreate}

    /**
 * @summary Add a speaker to an event (admin only)
 */
export const useCreateEventSpeaker = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEventSpeaker>>, TError,CreateEventSpeakerMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createEventSpeaker>>,
        TError,
        CreateEventSpeakerMutationVariables,
        TContext
      > => {
      return useMutation(getCreateEventSpeakerMutationOptions(options));
    }

export const getUpdateEventSpeakerUrl = (id: string,
    speakerId: string,) => {




  return `/api/admin/events/${id}/speakers/${speakerId}`
}

/**
 * @summary Update a speaker (admin only)
 */
export const updateEventSpeaker = async (id: string,
    speakerId: string,
    adminSpeakerUpdate: AdminSpeakerUpdate, options?: Parameters<typeof customFetch>[1]): Promise<AdminSpeaker> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminSpeaker>(getUpdateEventSpeakerUrl(id,speakerId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminSpeakerUpdate)
  }
);}





export const getUpdateEventSpeakerMutationKey = () => ['updateEventSpeaker'] as const;

export const getUpdateEventSpeakerMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEventSpeaker>>, TError,UpdateEventSpeakerMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateEventSpeaker>>, TError,UpdateEventSpeakerMutationVariables, TContext> => {

const mutationKey = getUpdateEventSpeakerMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateEventSpeaker>>, UpdateEventSpeakerMutationVariables> = (props) => {
          const {id,speakerId,data} = props ?? {};

          return  updateEventSpeaker(id,speakerId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateEventSpeakerMutationResult = NonNullable<Awaited<ReturnType<typeof updateEventSpeaker>>>
    export type UpdateEventSpeakerMutationBody = AdminSpeakerUpdate
    export type UpdateEventSpeakerMutationError = Error
    export type UpdateEventSpeakerMutationVariables = {id: string;speakerId: string;data: AdminSpeakerUpdate}

    /**
 * @summary Update a speaker (admin only)
 */
export const useUpdateEventSpeaker = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEventSpeaker>>, TError,UpdateEventSpeakerMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateEventSpeaker>>,
        TError,
        UpdateEventSpeakerMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateEventSpeakerMutationOptions(options));
    }

export const getDeleteEventSpeakerUrl = (id: string,
    speakerId: string,) => {




  return `/api/admin/events/${id}/speakers/${speakerId}`
}

/**
 * @summary Remove a speaker (admin only)
 */
export const deleteEventSpeaker = async (id: string,
    speakerId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteEventSpeakerUrl(id,speakerId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteEventSpeakerMutationKey = () => ['deleteEventSpeaker'] as const;

export const getDeleteEventSpeakerMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteEventSpeaker>>, TError,DeleteEventSpeakerMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteEventSpeaker>>, TError,DeleteEventSpeakerMutationVariables, TContext> => {

const mutationKey = getDeleteEventSpeakerMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteEventSpeaker>>, DeleteEventSpeakerMutationVariables> = (props) => {
          const {id,speakerId} = props ?? {};

          return  deleteEventSpeaker(id,speakerId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteEventSpeakerMutationResult = NonNullable<Awaited<ReturnType<typeof deleteEventSpeaker>>>

    export type DeleteEventSpeakerMutationError = Error
    export type DeleteEventSpeakerMutationVariables = {id: string;speakerId: string}

    /**
 * @summary Remove a speaker (admin only)
 */
export const useDeleteEventSpeaker = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteEventSpeaker>>, TError,DeleteEventSpeakerMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteEventSpeaker>>,
        TError,
        DeleteEventSpeakerMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteEventSpeakerMutationOptions(options));
    }

export const getCreateEventInvitationCodeUrl = (id: string,) => {




  return `/api/admin/events/${id}/codes`
}

/**
 * @summary Create a single invitation code for an event (admin only)
 */
export const createEventInvitationCode = async (id: string,
    adminInvitationCodeCreate: AdminInvitationCodeCreate, options?: Parameters<typeof customFetch>[1]): Promise<AdminInvitationCode> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminInvitationCode>(getCreateEventInvitationCodeUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminInvitationCodeCreate)
  }
);}





export const getCreateEventInvitationCodeMutationKey = () => ['createEventInvitationCode'] as const;

export const getCreateEventInvitationCodeMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEventInvitationCode>>, TError,CreateEventInvitationCodeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createEventInvitationCode>>, TError,CreateEventInvitationCodeMutationVariables, TContext> => {

const mutationKey = getCreateEventInvitationCodeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createEventInvitationCode>>, CreateEventInvitationCodeMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createEventInvitationCode(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateEventInvitationCodeMutationResult = NonNullable<Awaited<ReturnType<typeof createEventInvitationCode>>>
    export type CreateEventInvitationCodeMutationBody = AdminInvitationCodeCreate
    export type CreateEventInvitationCodeMutationError = Error
    export type CreateEventInvitationCodeMutationVariables = {id: string;data: AdminInvitationCodeCreate}

    /**
 * @summary Create a single invitation code for an event (admin only)
 */
export const useCreateEventInvitationCode = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEventInvitationCode>>, TError,CreateEventInvitationCodeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createEventInvitationCode>>,
        TError,
        CreateEventInvitationCodeMutationVariables,
        TContext
      > => {
      return useMutation(getCreateEventInvitationCodeMutationOptions(options));
    }

export const getBulkGenerateEventInvitationCodesUrl = (id: string,) => {




  return `/api/admin/events/${id}/codes/bulk`
}

/**
 * @summary Bulk-generate unique invitation codes for an event (admin only)
 */
export const bulkGenerateEventInvitationCodes = async (id: string,
    adminBulkCodesRequest: AdminBulkCodesRequest, options?: Parameters<typeof customFetch>[1]): Promise<AdminInvitationCode[]> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminInvitationCode[]>(getBulkGenerateEventInvitationCodesUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminBulkCodesRequest)
  }
);}





export const getBulkGenerateEventInvitationCodesMutationKey = () => ['bulkGenerateEventInvitationCodes'] as const;

export const getBulkGenerateEventInvitationCodesMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof bulkGenerateEventInvitationCodes>>, TError,BulkGenerateEventInvitationCodesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof bulkGenerateEventInvitationCodes>>, TError,BulkGenerateEventInvitationCodesMutationVariables, TContext> => {

const mutationKey = getBulkGenerateEventInvitationCodesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof bulkGenerateEventInvitationCodes>>, BulkGenerateEventInvitationCodesMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  bulkGenerateEventInvitationCodes(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type BulkGenerateEventInvitationCodesMutationResult = NonNullable<Awaited<ReturnType<typeof bulkGenerateEventInvitationCodes>>>
    export type BulkGenerateEventInvitationCodesMutationBody = AdminBulkCodesRequest
    export type BulkGenerateEventInvitationCodesMutationError = Error
    export type BulkGenerateEventInvitationCodesMutationVariables = {id: string;data: AdminBulkCodesRequest}

    /**
 * @summary Bulk-generate unique invitation codes for an event (admin only)
 */
export const useBulkGenerateEventInvitationCodes = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof bulkGenerateEventInvitationCodes>>, TError,BulkGenerateEventInvitationCodesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof bulkGenerateEventInvitationCodes>>,
        TError,
        BulkGenerateEventInvitationCodesMutationVariables,
        TContext
      > => {
      return useMutation(getBulkGenerateEventInvitationCodesMutationOptions(options));
    }

export const getUpdateEventInvitationCodeUrl = (id: string,
    codeId: string,) => {




  return `/api/admin/events/${id}/codes/${codeId}`
}

/**
 * @summary Update an invitation code (activate/deactivate, label, limits) (admin only)
 */
export const updateEventInvitationCode = async (id: string,
    codeId: string,
    adminInvitationCodeUpdate: AdminInvitationCodeUpdate, options?: Parameters<typeof customFetch>[1]): Promise<AdminInvitationCode> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminInvitationCode>(getUpdateEventInvitationCodeUrl(id,codeId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminInvitationCodeUpdate)
  }
);}





export const getUpdateEventInvitationCodeMutationKey = () => ['updateEventInvitationCode'] as const;

export const getUpdateEventInvitationCodeMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEventInvitationCode>>, TError,UpdateEventInvitationCodeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateEventInvitationCode>>, TError,UpdateEventInvitationCodeMutationVariables, TContext> => {

const mutationKey = getUpdateEventInvitationCodeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateEventInvitationCode>>, UpdateEventInvitationCodeMutationVariables> = (props) => {
          const {id,codeId,data} = props ?? {};

          return  updateEventInvitationCode(id,codeId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateEventInvitationCodeMutationResult = NonNullable<Awaited<ReturnType<typeof updateEventInvitationCode>>>
    export type UpdateEventInvitationCodeMutationBody = AdminInvitationCodeUpdate
    export type UpdateEventInvitationCodeMutationError = Error
    export type UpdateEventInvitationCodeMutationVariables = {id: string;codeId: string;data: AdminInvitationCodeUpdate}

    /**
 * @summary Update an invitation code (activate/deactivate, label, limits) (admin only)
 */
export const useUpdateEventInvitationCode = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEventInvitationCode>>, TError,UpdateEventInvitationCodeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateEventInvitationCode>>,
        TError,
        UpdateEventInvitationCodeMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateEventInvitationCodeMutationOptions(options));
    }

export const getDeleteEventInvitationCodeUrl = (id: string,
    codeId: string,) => {




  return `/api/admin/events/${id}/codes/${codeId}`
}

/**
 * @summary Delete an invitation code (admin only)
 */
export const deleteEventInvitationCode = async (id: string,
    codeId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteEventInvitationCodeUrl(id,codeId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteEventInvitationCodeMutationKey = () => ['deleteEventInvitationCode'] as const;

export const getDeleteEventInvitationCodeMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteEventInvitationCode>>, TError,DeleteEventInvitationCodeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteEventInvitationCode>>, TError,DeleteEventInvitationCodeMutationVariables, TContext> => {

const mutationKey = getDeleteEventInvitationCodeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteEventInvitationCode>>, DeleteEventInvitationCodeMutationVariables> = (props) => {
          const {id,codeId} = props ?? {};

          return  deleteEventInvitationCode(id,codeId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteEventInvitationCodeMutationResult = NonNullable<Awaited<ReturnType<typeof deleteEventInvitationCode>>>

    export type DeleteEventInvitationCodeMutationError = Error
    export type DeleteEventInvitationCodeMutationVariables = {id: string;codeId: string}

    /**
 * @summary Delete an invitation code (admin only)
 */
export const useDeleteEventInvitationCode = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteEventInvitationCode>>, TError,DeleteEventInvitationCodeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteEventInvitationCode>>,
        TError,
        DeleteEventInvitationCodeMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteEventInvitationCodeMutationOptions(options));
    }

export const getListEventRsvpsUrl = (id: string,
    params?: ListEventRsvpsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/events/${id}/rsvps?${stringifiedParams}` : `/api/admin/events/${id}/rsvps`
}

/**
 * @summary List RSVPs for an event with filtering and pagination (admin only)
 */
export const listEventRsvps = async (id: string,
    params?: ListEventRsvpsParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminRsvpList> => {

  return customFetch<AdminRsvpList>(getListEventRsvpsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListEventRsvpsQueryKey = (id: string,
    params?: ListEventRsvpsParams,) => {
    return [
    `/api/admin/events/${id}/rsvps`, ...(params ? [params] : [])
    ] as const;
    }


export const getListEventRsvpsQueryOptions = <TData = Awaited<ReturnType<typeof listEventRsvps>>, TError = Error>(id: string,
    params?: ListEventRsvpsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEventRsvps>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEventRsvpsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEventRsvps>>> = ({ signal }) => listEventRsvps(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEventRsvps>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEventRsvpsQueryResult = NonNullable<Awaited<ReturnType<typeof listEventRsvps>>>
export type ListEventRsvpsQueryError = Error


/**
 * @summary List RSVPs for an event with filtering and pagination (admin only)
 */

export function useListEventRsvps<TData = Awaited<ReturnType<typeof listEventRsvps>>, TError = Error>(
 id: string,
    params?: ListEventRsvpsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEventRsvps>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEventRsvpsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetEventRsvpSummaryUrl = (id: string,) => {




  return `/api/admin/events/${id}/rsvps/summary`
}

/**
 * @summary Per-event RSVP counts by status (admin only)
 */
export const getEventRsvpSummary = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<AdminRsvpSummary> => {

  return customFetch<AdminRsvpSummary>(getGetEventRsvpSummaryUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEventRsvpSummaryQueryKey = (id: string,) => {
    return [
    `/api/admin/events/${id}/rsvps/summary`
    ] as const;
    }


export const getGetEventRsvpSummaryQueryOptions = <TData = Awaited<ReturnType<typeof getEventRsvpSummary>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEventRsvpSummary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEventRsvpSummaryQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEventRsvpSummary>>> = ({ signal }) => getEventRsvpSummary(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEventRsvpSummary>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEventRsvpSummaryQueryResult = NonNullable<Awaited<ReturnType<typeof getEventRsvpSummary>>>
export type GetEventRsvpSummaryQueryError = Error


/**
 * @summary Per-event RSVP counts by status (admin only)
 */

export function useGetEventRsvpSummary<TData = Awaited<ReturnType<typeof getEventRsvpSummary>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEventRsvpSummary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEventRsvpSummaryQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getExportEventRsvpsUrl = (id: string,
    params?: ExportEventRsvpsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/events/${id}/rsvps/export?${stringifiedParams}` : `/api/admin/events/${id}/rsvps/export`
}

/**
 * @summary Download RSVPs matching the filters as CSV (admin only)
 */
export const exportEventRsvps = async (id: string,
    params?: ExportEventRsvpsParams, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getExportEventRsvpsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getExportEventRsvpsQueryKey = (id: string,
    params?: ExportEventRsvpsParams,) => {
    return [
    `/api/admin/events/${id}/rsvps/export`, ...(params ? [params] : [])
    ] as const;
    }


export const getExportEventRsvpsQueryOptions = <TData = Awaited<ReturnType<typeof exportEventRsvps>>, TError = Error>(id: string,
    params?: ExportEventRsvpsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof exportEventRsvps>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getExportEventRsvpsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof exportEventRsvps>>> = ({ signal }) => exportEventRsvps(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof exportEventRsvps>>, TError, TData> & { queryKey: QueryKey }
}

export type ExportEventRsvpsQueryResult = NonNullable<Awaited<ReturnType<typeof exportEventRsvps>>>
export type ExportEventRsvpsQueryError = Error


/**
 * @summary Download RSVPs matching the filters as CSV (admin only)
 */

export function useExportEventRsvps<TData = Awaited<ReturnType<typeof exportEventRsvps>>, TError = Error>(
 id: string,
    params?: ExportEventRsvpsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof exportEventRsvps>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getExportEventRsvpsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateEventRsvpStatusUrl = (id: string,
    rsvpId: string,) => {




  return `/api/admin/events/${id}/rsvps/${rsvpId}`
}

/**
 * @summary Update an RSVP's status (admin only)
 */
export const updateEventRsvpStatus = async (id: string,
    rsvpId: string,
    adminRsvpStatusUpdate: AdminRsvpStatusUpdate, options?: Parameters<typeof customFetch>[1]): Promise<AdminRsvp> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminRsvp>(getUpdateEventRsvpStatusUrl(id,rsvpId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminRsvpStatusUpdate)
  }
);}





export const getUpdateEventRsvpStatusMutationKey = () => ['updateEventRsvpStatus'] as const;

export const getUpdateEventRsvpStatusMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEventRsvpStatus>>, TError,UpdateEventRsvpStatusMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateEventRsvpStatus>>, TError,UpdateEventRsvpStatusMutationVariables, TContext> => {

const mutationKey = getUpdateEventRsvpStatusMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateEventRsvpStatus>>, UpdateEventRsvpStatusMutationVariables> = (props) => {
          const {id,rsvpId,data} = props ?? {};

          return  updateEventRsvpStatus(id,rsvpId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateEventRsvpStatusMutationResult = NonNullable<Awaited<ReturnType<typeof updateEventRsvpStatus>>>
    export type UpdateEventRsvpStatusMutationBody = AdminRsvpStatusUpdate
    export type UpdateEventRsvpStatusMutationError = Error
    export type UpdateEventRsvpStatusMutationVariables = {id: string;rsvpId: string;data: AdminRsvpStatusUpdate}

    /**
 * @summary Update an RSVP's status (admin only)
 */
export const useUpdateEventRsvpStatus = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEventRsvpStatus>>, TError,UpdateEventRsvpStatusMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateEventRsvpStatus>>,
        TError,
        UpdateEventRsvpStatusMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateEventRsvpStatusMutationOptions(options));
    }

export const getGetCurrentUserUrl = () => {




  return `/api/auth/me`
}

/**
 * @summary Current authenticated user
 */
export const getCurrentUser = async ( options?: Parameters<typeof customFetch>[1]): Promise<CurrentUser> => {

  return customFetch<CurrentUser>(getGetCurrentUserUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetCurrentUserQueryKey = () => {
    return [
    `/api/auth/me`
    ] as const;
    }


export const getGetCurrentUserQueryOptions = <TData = Awaited<ReturnType<typeof getCurrentUser>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCurrentUserQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCurrentUser>>> = ({ signal }) => getCurrentUser({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCurrentUserQueryResult = NonNullable<Awaited<ReturnType<typeof getCurrentUser>>>
export type GetCurrentUserQueryError = Error


/**
 * @summary Current authenticated user
 */

export function useGetCurrentUser<TData = Awaited<ReturnType<typeof getCurrentUser>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCurrentUserQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetAgentResearchStatusUrl = () => {




  return `/api/agent/research/status`
}

export const getAgentResearchStatus = async (getAgentResearchStatusBody: GetAgentResearchStatusBody, options?: Parameters<typeof customFetch>[1]): Promise<ResearchWebSearchStatus> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ResearchWebSearchStatus>(getGetAgentResearchStatusUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(getAgentResearchStatusBody)
  }
);}





export const getGetAgentResearchStatusMutationKey = () => ['getAgentResearchStatus'] as const;

export const getGetAgentResearchStatusMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof getAgentResearchStatus>>, TError,GetAgentResearchStatusMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof getAgentResearchStatus>>, TError,GetAgentResearchStatusMutationVariables, TContext> => {

const mutationKey = getGetAgentResearchStatusMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof getAgentResearchStatus>>, GetAgentResearchStatusMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  getAgentResearchStatus(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GetAgentResearchStatusMutationResult = NonNullable<Awaited<ReturnType<typeof getAgentResearchStatus>>>
    export type GetAgentResearchStatusMutationBody = GetAgentResearchStatusBody
    export type GetAgentResearchStatusMutationError = unknown
    export type GetAgentResearchStatusMutationVariables = {data: GetAgentResearchStatusBody}

    export const useGetAgentResearchStatus = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof getAgentResearchStatus>>, TError,GetAgentResearchStatusMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof getAgentResearchStatus>>,
        TError,
        GetAgentResearchStatusMutationVariables,
        TContext
      > => {
      return useMutation(getGetAgentResearchStatusMutationOptions(options));
    }

export const getListPersonalWorkUrl = (params: ListPersonalWorkParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/work-index?${stringifiedParams}` : `/api/work-index`
}

/**
 * Read the current actor's authorized unfinished work. Derived metadata only; no task store or execution.
 */
export const listPersonalWork = async (params: ListPersonalWorkParams, options?: Parameters<typeof customFetch>[1]): Promise<PersonalWorkPage> => {

  return customFetch<PersonalWorkPage>(getListPersonalWorkUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPersonalWorkQueryKey = (params?: ListPersonalWorkParams,) => {
    return [
    `/api/work-index`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPersonalWorkQueryOptions = <TData = Awaited<ReturnType<typeof listPersonalWork>>, TError = void>(params: ListPersonalWorkParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPersonalWork>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPersonalWorkQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPersonalWork>>> = ({ signal }) => listPersonalWork(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPersonalWork>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPersonalWorkQueryResult = NonNullable<Awaited<ReturnType<typeof listPersonalWork>>>
export type ListPersonalWorkQueryError = void



export function useListPersonalWork<TData = Awaited<ReturnType<typeof listPersonalWork>>, TError = void>(
 params: ListPersonalWorkParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPersonalWork>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPersonalWorkQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPersonalWorkResumeUrl = (source: PersonalWorkSource,
    id: string,
    params: GetPersonalWorkResumeParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/work-index/${source}/${id}/resume?${stringifiedParams}` : `/api/work-index/${source}/${id}/resume`
}

/**
 * Reauthorize the exact source and read its current resume context. Never confirms, retries, renews expiry or writes.
 */
export const getPersonalWorkResume = async (source: PersonalWorkSource,
    id: string,
    params: GetPersonalWorkResumeParams, options?: Parameters<typeof customFetch>[1]): Promise<PersonalWorkItem> => {

  return customFetch<PersonalWorkItem>(getGetPersonalWorkResumeUrl(source,id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPersonalWorkResumeQueryKey = (source: PersonalWorkSource,
    id: string,
    params?: GetPersonalWorkResumeParams,) => {
    return [
    `/api/work-index/${source}/${id}/resume`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetPersonalWorkResumeQueryOptions = <TData = Awaited<ReturnType<typeof getPersonalWorkResume>>, TError = void>(source: PersonalWorkSource,
    id: string,
    params: GetPersonalWorkResumeParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPersonalWorkResume>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPersonalWorkResumeQueryKey(source,id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPersonalWorkResume>>> = ({ signal }) => getPersonalWorkResume(source,id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: source !== null && source !== undefined && id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPersonalWorkResume>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPersonalWorkResumeQueryResult = NonNullable<Awaited<ReturnType<typeof getPersonalWorkResume>>>
export type GetPersonalWorkResumeQueryError = void



export function useGetPersonalWorkResume<TData = Awaited<ReturnType<typeof getPersonalWorkResume>>, TError = void>(
 source: PersonalWorkSource,
    id: string,
    params: GetPersonalWorkResumeParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPersonalWorkResume>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPersonalWorkResumeQueryOptions(source,id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListAgentAttachmentsUrl = (id: string,
    params: ListAgentAttachmentsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/conversations/${id}/attachments?${stringifiedParams}` : `/api/agent/conversations/${id}/attachments`
}

export const listAgentAttachments = async (id: string,
    params: ListAgentAttachmentsParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentAttachmentPage> => {

  return customFetch<AgentAttachmentPage>(getListAgentAttachmentsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgentAttachmentsQueryKey = (id: string,
    params?: ListAgentAttachmentsParams,) => {
    return [
    `/api/agent/conversations/${id}/attachments`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAgentAttachmentsQueryOptions = <TData = Awaited<ReturnType<typeof listAgentAttachments>>, TError = unknown>(id: string,
    params: ListAgentAttachmentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentAttachments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgentAttachmentsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgentAttachments>>> = ({ signal }) => listAgentAttachments(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgentAttachments>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAgentAttachmentsQueryResult = NonNullable<Awaited<ReturnType<typeof listAgentAttachments>>>
export type ListAgentAttachmentsQueryError = unknown



export function useListAgentAttachments<TData = Awaited<ReturnType<typeof listAgentAttachments>>, TError = unknown>(
 id: string,
    params: ListAgentAttachmentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentAttachments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAgentAttachmentsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUploadAgentAttachmentUrl = (id: string,) => {




  return `/api/agent/conversations/${id}/attachments`
}

/**
 * One original per immutable batch. Retry the same uploadId and bytes; never sends chat or creates a tender.
 */
export const uploadAgentAttachment = async (id: string,
    agentAttachmentUpload: AgentAttachmentUpload, options?: Parameters<typeof customFetch>[1]): Promise<AgentAttachment> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentAttachment>(getUploadAgentAttachmentUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentAttachmentUpload)
  }
);}





export const getUploadAgentAttachmentMutationKey = () => ['uploadAgentAttachment'] as const;

export const getUploadAgentAttachmentMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadAgentAttachment>>, TError,UploadAgentAttachmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadAgentAttachment>>, TError,UploadAgentAttachmentMutationVariables, TContext> => {

const mutationKey = getUploadAgentAttachmentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadAgentAttachment>>, UploadAgentAttachmentMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  uploadAgentAttachment(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadAgentAttachmentMutationResult = NonNullable<Awaited<ReturnType<typeof uploadAgentAttachment>>>
    export type UploadAgentAttachmentMutationBody = AgentAttachmentUpload
    export type UploadAgentAttachmentMutationError = unknown
    export type UploadAgentAttachmentMutationVariables = {id: string;data: AgentAttachmentUpload}

    export const useUploadAgentAttachment = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadAgentAttachment>>, TError,UploadAgentAttachmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadAgentAttachment>>,
        TError,
        UploadAgentAttachmentMutationVariables,
        TContext
      > => {
      return useMutation(getUploadAgentAttachmentMutationOptions(options));
    }

export const getGetAgentAttachmentUrl = (id: string,
    attachmentId: string,
    params: GetAgentAttachmentParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/conversations/${id}/attachments/${attachmentId}?${stringifiedParams}` : `/api/agent/conversations/${id}/attachments/${attachmentId}`
}

export const getAgentAttachment = async (id: string,
    attachmentId: string,
    params: GetAgentAttachmentParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentAttachment> => {

  return customFetch<AgentAttachment>(getGetAgentAttachmentUrl(id,attachmentId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAgentAttachmentQueryKey = (id: string,
    attachmentId: string,
    params?: GetAgentAttachmentParams,) => {
    return [
    `/api/agent/conversations/${id}/attachments/${attachmentId}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetAgentAttachmentQueryOptions = <TData = Awaited<ReturnType<typeof getAgentAttachment>>, TError = unknown>(id: string,
    attachmentId: string,
    params: GetAgentAttachmentParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentAttachment>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAgentAttachmentQueryKey(id,attachmentId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAgentAttachment>>> = ({ signal }) => getAgentAttachment(id,attachmentId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && attachmentId !== null && attachmentId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAgentAttachment>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAgentAttachmentQueryResult = NonNullable<Awaited<ReturnType<typeof getAgentAttachment>>>
export type GetAgentAttachmentQueryError = unknown



export function useGetAgentAttachment<TData = Awaited<ReturnType<typeof getAgentAttachment>>, TError = unknown>(
 id: string,
    attachmentId: string,
    params: GetAgentAttachmentParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentAttachment>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAgentAttachmentQueryOptions(id,attachmentId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRemoveAgentAttachmentUrl = (id: string,
    attachmentId: string,
    params: RemoveAgentAttachmentParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/conversations/${id}/attachments/${attachmentId}?${stringifiedParams}` : `/api/agent/conversations/${id}/attachments/${attachmentId}`
}

/**
 * Removes uncommitted evidence only. Saved-turn references prevent byte deletion. Repeating removal retries pending cleanup.
 */
export const removeAgentAttachment = async (id: string,
    attachmentId: string,
    params: RemoveAgentAttachmentParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentAttachment> => {

  return customFetch<AgentAttachment>(getRemoveAgentAttachmentUrl(id,attachmentId,params),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getRemoveAgentAttachmentMutationKey = () => ['removeAgentAttachment'] as const;

export const getRemoveAgentAttachmentMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeAgentAttachment>>, TError,RemoveAgentAttachmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof removeAgentAttachment>>, TError,RemoveAgentAttachmentMutationVariables, TContext> => {

const mutationKey = getRemoveAgentAttachmentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof removeAgentAttachment>>, RemoveAgentAttachmentMutationVariables> = (props) => {
          const {id,attachmentId,params} = props ?? {};

          return  removeAgentAttachment(id,attachmentId,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RemoveAgentAttachmentMutationResult = NonNullable<Awaited<ReturnType<typeof removeAgentAttachment>>>

    export type RemoveAgentAttachmentMutationError = unknown
    export type RemoveAgentAttachmentMutationVariables = {id: string;attachmentId: string;params: RemoveAgentAttachmentParams}

    export const useRemoveAgentAttachment = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeAgentAttachment>>, TError,RemoveAgentAttachmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof removeAgentAttachment>>,
        TError,
        RemoveAgentAttachmentMutationVariables,
        TContext
      > => {
      return useMutation(getRemoveAgentAttachmentMutationOptions(options));
    }

export const getListAgentAttachmentMembersUrl = (id: string,
    attachmentId: string,
    params: ListAgentAttachmentMembersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/conversations/${id}/attachments/${attachmentId}/members?${stringifiedParams}` : `/api/agent/conversations/${id}/attachments/${attachmentId}/members`
}

export const listAgentAttachmentMembers = async (id: string,
    attachmentId: string,
    params: ListAgentAttachmentMembersParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentAttachmentPage> => {

  return customFetch<AgentAttachmentPage>(getListAgentAttachmentMembersUrl(id,attachmentId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgentAttachmentMembersQueryKey = (id: string,
    attachmentId: string,
    params?: ListAgentAttachmentMembersParams,) => {
    return [
    `/api/agent/conversations/${id}/attachments/${attachmentId}/members`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAgentAttachmentMembersQueryOptions = <TData = Awaited<ReturnType<typeof listAgentAttachmentMembers>>, TError = unknown>(id: string,
    attachmentId: string,
    params: ListAgentAttachmentMembersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentAttachmentMembers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgentAttachmentMembersQueryKey(id,attachmentId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgentAttachmentMembers>>> = ({ signal }) => listAgentAttachmentMembers(id,attachmentId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && attachmentId !== null && attachmentId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgentAttachmentMembers>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAgentAttachmentMembersQueryResult = NonNullable<Awaited<ReturnType<typeof listAgentAttachmentMembers>>>
export type ListAgentAttachmentMembersQueryError = unknown



export function useListAgentAttachmentMembers<TData = Awaited<ReturnType<typeof listAgentAttachmentMembers>>, TError = unknown>(
 id: string,
    attachmentId: string,
    params: ListAgentAttachmentMembersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentAttachmentMembers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAgentAttachmentMembersQueryOptions(id,attachmentId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetAgentAttachmentUploadUrl = (id: string,
    uploadId: string,
    params: GetAgentAttachmentUploadParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/conversations/${id}/attachments/uploads/${uploadId}?${stringifiedParams}` : `/api/agent/conversations/${id}/attachments/uploads/${uploadId}`
}

export const getAgentAttachmentUpload = async (id: string,
    uploadId: string,
    params: GetAgentAttachmentUploadParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentAttachment> => {

  return customFetch<AgentAttachment>(getGetAgentAttachmentUploadUrl(id,uploadId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAgentAttachmentUploadQueryKey = (id: string,
    uploadId: string,
    params?: GetAgentAttachmentUploadParams,) => {
    return [
    `/api/agent/conversations/${id}/attachments/uploads/${uploadId}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetAgentAttachmentUploadQueryOptions = <TData = Awaited<ReturnType<typeof getAgentAttachmentUpload>>, TError = unknown>(id: string,
    uploadId: string,
    params: GetAgentAttachmentUploadParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentAttachmentUpload>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAgentAttachmentUploadQueryKey(id,uploadId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAgentAttachmentUpload>>> = ({ signal }) => getAgentAttachmentUpload(id,uploadId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && uploadId !== null && uploadId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAgentAttachmentUpload>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAgentAttachmentUploadQueryResult = NonNullable<Awaited<ReturnType<typeof getAgentAttachmentUpload>>>
export type GetAgentAttachmentUploadQueryError = unknown



export function useGetAgentAttachmentUpload<TData = Awaited<ReturnType<typeof getAgentAttachmentUpload>>, TError = unknown>(
 id: string,
    uploadId: string,
    params: GetAgentAttachmentUploadParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentAttachmentUpload>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAgentAttachmentUploadQueryOptions(id,uploadId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getProcessAgentAttachmentUrl = (id: string,
    attachmentId: string,) => {




  return `/api/agent/conversations/${id}/attachments/${attachmentId}/process`
}

export const processAgentAttachment = async (id: string,
    attachmentId: string,
    processAgentAttachmentBody: ProcessAgentAttachmentBody, options?: Parameters<typeof customFetch>[1]): Promise<AgentAttachment> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentAttachment>(getProcessAgentAttachmentUrl(id,attachmentId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(processAgentAttachmentBody)
  }
);}





export const getProcessAgentAttachmentMutationKey = () => ['processAgentAttachment'] as const;

export const getProcessAgentAttachmentMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof processAgentAttachment>>, TError,ProcessAgentAttachmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof processAgentAttachment>>, TError,ProcessAgentAttachmentMutationVariables, TContext> => {

const mutationKey = getProcessAgentAttachmentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof processAgentAttachment>>, ProcessAgentAttachmentMutationVariables> = (props) => {
          const {id,attachmentId,data} = props ?? {};

          return  processAgentAttachment(id,attachmentId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ProcessAgentAttachmentMutationResult = NonNullable<Awaited<ReturnType<typeof processAgentAttachment>>>
    export type ProcessAgentAttachmentMutationBody = ProcessAgentAttachmentBody
    export type ProcessAgentAttachmentMutationError = unknown
    export type ProcessAgentAttachmentMutationVariables = {id: string;attachmentId: string;data: ProcessAgentAttachmentBody}

    export const useProcessAgentAttachment = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof processAgentAttachment>>, TError,ProcessAgentAttachmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof processAgentAttachment>>,
        TError,
        ProcessAgentAttachmentMutationVariables,
        TContext
      > => {
      return useMutation(getProcessAgentAttachmentMutationOptions(options));
    }

export const getListAgentAttachmentSectionsUrl = (id: string,
    attachmentId: string,
    params: ListAgentAttachmentSectionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/conversations/${id}/attachments/${attachmentId}/sections?${stringifiedParams}` : `/api/agent/conversations/${id}/attachments/${attachmentId}/sections`
}

export const listAgentAttachmentSections = async (id: string,
    attachmentId: string,
    params: ListAgentAttachmentSectionsParams, options?: Parameters<typeof customFetch>[1]): Promise<ListAgentAttachmentSections200> => {

  return customFetch<ListAgentAttachmentSections200>(getListAgentAttachmentSectionsUrl(id,attachmentId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgentAttachmentSectionsQueryKey = (id: string,
    attachmentId: string,
    params?: ListAgentAttachmentSectionsParams,) => {
    return [
    `/api/agent/conversations/${id}/attachments/${attachmentId}/sections`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAgentAttachmentSectionsQueryOptions = <TData = Awaited<ReturnType<typeof listAgentAttachmentSections>>, TError = unknown>(id: string,
    attachmentId: string,
    params: ListAgentAttachmentSectionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentAttachmentSections>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgentAttachmentSectionsQueryKey(id,attachmentId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgentAttachmentSections>>> = ({ signal }) => listAgentAttachmentSections(id,attachmentId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && attachmentId !== null && attachmentId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgentAttachmentSections>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAgentAttachmentSectionsQueryResult = NonNullable<Awaited<ReturnType<typeof listAgentAttachmentSections>>>
export type ListAgentAttachmentSectionsQueryError = unknown



export function useListAgentAttachmentSections<TData = Awaited<ReturnType<typeof listAgentAttachmentSections>>, TError = unknown>(
 id: string,
    attachmentId: string,
    params: ListAgentAttachmentSectionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentAttachmentSections>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAgentAttachmentSectionsQueryOptions(id,attachmentId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDownloadAgentAttachmentUrl = (id: string,
    attachmentId: string,
    params: DownloadAgentAttachmentParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/conversations/${id}/attachments/${attachmentId}/content?${stringifiedParams}` : `/api/agent/conversations/${id}/attachments/${attachmentId}/content`
}

export const downloadAgentAttachment = async (id: string,
    attachmentId: string,
    params: DownloadAgentAttachmentParams, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadAgentAttachmentUrl(id,attachmentId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadAgentAttachmentQueryKey = (id: string,
    attachmentId: string,
    params?: DownloadAgentAttachmentParams,) => {
    return [
    `/api/agent/conversations/${id}/attachments/${attachmentId}/content`, ...(params ? [params] : [])
    ] as const;
    }


export const getDownloadAgentAttachmentQueryOptions = <TData = Awaited<ReturnType<typeof downloadAgentAttachment>>, TError = unknown>(id: string,
    attachmentId: string,
    params: DownloadAgentAttachmentParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadAgentAttachment>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadAgentAttachmentQueryKey(id,attachmentId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadAgentAttachment>>> = ({ signal }) => downloadAgentAttachment(id,attachmentId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && attachmentId !== null && attachmentId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadAgentAttachment>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadAgentAttachmentQueryResult = NonNullable<Awaited<ReturnType<typeof downloadAgentAttachment>>>
export type DownloadAgentAttachmentQueryError = unknown



export function useDownloadAgentAttachment<TData = Awaited<ReturnType<typeof downloadAgentAttachment>>, TError = unknown>(
 id: string,
    attachmentId: string,
    params: DownloadAgentAttachmentParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadAgentAttachment>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadAgentAttachmentQueryOptions(id,attachmentId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListAgentSharedAttachmentsUrl = (id: string,
    params: ListAgentSharedAttachmentsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/shared-conversations/${id}/attachments?${stringifiedParams}` : `/api/agent/shared-conversations/${id}/attachments`
}

/**
 * Exact record-shared uploads; no Collateral adoption, public link or document approval.
 */
export const listAgentSharedAttachments = async (id: string,
    params: ListAgentSharedAttachmentsParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentSharedAttachmentPage> => {

  return customFetch<AgentSharedAttachmentPage>(getListAgentSharedAttachmentsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgentSharedAttachmentsQueryKey = (id: string,
    params?: ListAgentSharedAttachmentsParams,) => {
    return [
    `/api/agent/shared-conversations/${id}/attachments`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAgentSharedAttachmentsQueryOptions = <TData = Awaited<ReturnType<typeof listAgentSharedAttachments>>, TError = unknown>(id: string,
    params: ListAgentSharedAttachmentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentSharedAttachments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgentSharedAttachmentsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgentSharedAttachments>>> = ({ signal }) => listAgentSharedAttachments(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgentSharedAttachments>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAgentSharedAttachmentsQueryResult = NonNullable<Awaited<ReturnType<typeof listAgentSharedAttachments>>>
export type ListAgentSharedAttachmentsQueryError = unknown



export function useListAgentSharedAttachments<TData = Awaited<ReturnType<typeof listAgentSharedAttachments>>, TError = unknown>(
 id: string,
    params: ListAgentSharedAttachmentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentSharedAttachments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAgentSharedAttachmentsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUploadAgentSharedAttachmentUrl = (id: string,) => {




  return `/api/agent/shared-conversations/${id}/attachments`
}

/**
 * Exact record-shared uploads; no Collateral adoption, public link or document approval.
 */
export const uploadAgentSharedAttachment = async (id: string,
    agentSharedAttachmentUpload: AgentSharedAttachmentUpload, options?: Parameters<typeof customFetch>[1]): Promise<AgentSharedAttachment> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentSharedAttachment>(getUploadAgentSharedAttachmentUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentSharedAttachmentUpload)
  }
);}





export const getUploadAgentSharedAttachmentMutationKey = () => ['uploadAgentSharedAttachment'] as const;

export const getUploadAgentSharedAttachmentMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadAgentSharedAttachment>>, TError,UploadAgentSharedAttachmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadAgentSharedAttachment>>, TError,UploadAgentSharedAttachmentMutationVariables, TContext> => {

const mutationKey = getUploadAgentSharedAttachmentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadAgentSharedAttachment>>, UploadAgentSharedAttachmentMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  uploadAgentSharedAttachment(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadAgentSharedAttachmentMutationResult = NonNullable<Awaited<ReturnType<typeof uploadAgentSharedAttachment>>>
    export type UploadAgentSharedAttachmentMutationBody = AgentSharedAttachmentUpload
    export type UploadAgentSharedAttachmentMutationError = unknown
    export type UploadAgentSharedAttachmentMutationVariables = {id: string;data: AgentSharedAttachmentUpload}

    export const useUploadAgentSharedAttachment = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadAgentSharedAttachment>>, TError,UploadAgentSharedAttachmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadAgentSharedAttachment>>,
        TError,
        UploadAgentSharedAttachmentMutationVariables,
        TContext
      > => {
      return useMutation(getUploadAgentSharedAttachmentMutationOptions(options));
    }

export const getGetAgentSharedAttachmentUploadUrl = (id: string,
    uploadId: string,
    params: GetAgentSharedAttachmentUploadParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/shared-conversations/${id}/attachments/uploads/${uploadId}?${stringifiedParams}` : `/api/agent/shared-conversations/${id}/attachments/uploads/${uploadId}`
}

/**
 * Exact record-shared uploads; no Collateral adoption, public link or document approval.
 */
export const getAgentSharedAttachmentUpload = async (id: string,
    uploadId: string,
    params: GetAgentSharedAttachmentUploadParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentSharedAttachment> => {

  return customFetch<AgentSharedAttachment>(getGetAgentSharedAttachmentUploadUrl(id,uploadId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAgentSharedAttachmentUploadQueryKey = (id: string,
    uploadId: string,
    params?: GetAgentSharedAttachmentUploadParams,) => {
    return [
    `/api/agent/shared-conversations/${id}/attachments/uploads/${uploadId}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetAgentSharedAttachmentUploadQueryOptions = <TData = Awaited<ReturnType<typeof getAgentSharedAttachmentUpload>>, TError = unknown>(id: string,
    uploadId: string,
    params: GetAgentSharedAttachmentUploadParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentSharedAttachmentUpload>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAgentSharedAttachmentUploadQueryKey(id,uploadId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAgentSharedAttachmentUpload>>> = ({ signal }) => getAgentSharedAttachmentUpload(id,uploadId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && uploadId !== null && uploadId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAgentSharedAttachmentUpload>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAgentSharedAttachmentUploadQueryResult = NonNullable<Awaited<ReturnType<typeof getAgentSharedAttachmentUpload>>>
export type GetAgentSharedAttachmentUploadQueryError = unknown



export function useGetAgentSharedAttachmentUpload<TData = Awaited<ReturnType<typeof getAgentSharedAttachmentUpload>>, TError = unknown>(
 id: string,
    uploadId: string,
    params: GetAgentSharedAttachmentUploadParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentSharedAttachmentUpload>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAgentSharedAttachmentUploadQueryOptions(id,uploadId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetAgentSharedAttachmentUrl = (id: string,
    attachmentId: string,
    params: GetAgentSharedAttachmentParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/shared-conversations/${id}/attachments/${attachmentId}?${stringifiedParams}` : `/api/agent/shared-conversations/${id}/attachments/${attachmentId}`
}

/**
 * Exact record-shared uploads; no Collateral adoption, public link or document approval.
 */
export const getAgentSharedAttachment = async (id: string,
    attachmentId: string,
    params: GetAgentSharedAttachmentParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentSharedAttachment> => {

  return customFetch<AgentSharedAttachment>(getGetAgentSharedAttachmentUrl(id,attachmentId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAgentSharedAttachmentQueryKey = (id: string,
    attachmentId: string,
    params?: GetAgentSharedAttachmentParams,) => {
    return [
    `/api/agent/shared-conversations/${id}/attachments/${attachmentId}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetAgentSharedAttachmentQueryOptions = <TData = Awaited<ReturnType<typeof getAgentSharedAttachment>>, TError = unknown>(id: string,
    attachmentId: string,
    params: GetAgentSharedAttachmentParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentSharedAttachment>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAgentSharedAttachmentQueryKey(id,attachmentId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAgentSharedAttachment>>> = ({ signal }) => getAgentSharedAttachment(id,attachmentId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && attachmentId !== null && attachmentId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAgentSharedAttachment>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAgentSharedAttachmentQueryResult = NonNullable<Awaited<ReturnType<typeof getAgentSharedAttachment>>>
export type GetAgentSharedAttachmentQueryError = unknown



export function useGetAgentSharedAttachment<TData = Awaited<ReturnType<typeof getAgentSharedAttachment>>, TError = unknown>(
 id: string,
    attachmentId: string,
    params: GetAgentSharedAttachmentParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentSharedAttachment>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAgentSharedAttachmentQueryOptions(id,attachmentId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRemoveAgentSharedAttachmentUrl = (id: string,
    attachmentId: string,
    params: RemoveAgentSharedAttachmentParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/shared-conversations/${id}/attachments/${attachmentId}?${stringifiedParams}` : `/api/agent/shared-conversations/${id}/attachments/${attachmentId}`
}

/**
 * Exact record-shared uploads; no Collateral adoption, public link or document approval.
 */
export const removeAgentSharedAttachment = async (id: string,
    attachmentId: string,
    params: RemoveAgentSharedAttachmentParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentSharedAttachment> => {

  return customFetch<AgentSharedAttachment>(getRemoveAgentSharedAttachmentUrl(id,attachmentId,params),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getRemoveAgentSharedAttachmentMutationKey = () => ['removeAgentSharedAttachment'] as const;

export const getRemoveAgentSharedAttachmentMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeAgentSharedAttachment>>, TError,RemoveAgentSharedAttachmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof removeAgentSharedAttachment>>, TError,RemoveAgentSharedAttachmentMutationVariables, TContext> => {

const mutationKey = getRemoveAgentSharedAttachmentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof removeAgentSharedAttachment>>, RemoveAgentSharedAttachmentMutationVariables> = (props) => {
          const {id,attachmentId,params} = props ?? {};

          return  removeAgentSharedAttachment(id,attachmentId,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RemoveAgentSharedAttachmentMutationResult = NonNullable<Awaited<ReturnType<typeof removeAgentSharedAttachment>>>

    export type RemoveAgentSharedAttachmentMutationError = unknown
    export type RemoveAgentSharedAttachmentMutationVariables = {id: string;attachmentId: string;params: RemoveAgentSharedAttachmentParams}

    export const useRemoveAgentSharedAttachment = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeAgentSharedAttachment>>, TError,RemoveAgentSharedAttachmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof removeAgentSharedAttachment>>,
        TError,
        RemoveAgentSharedAttachmentMutationVariables,
        TContext
      > => {
      return useMutation(getRemoveAgentSharedAttachmentMutationOptions(options));
    }

export const getListAgentSharedAttachmentMembersUrl = (id: string,
    attachmentId: string,
    params: ListAgentSharedAttachmentMembersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/shared-conversations/${id}/attachments/${attachmentId}/members?${stringifiedParams}` : `/api/agent/shared-conversations/${id}/attachments/${attachmentId}/members`
}

/**
 * Exact record-shared uploads; no Collateral adoption, public link or document approval.
 */
export const listAgentSharedAttachmentMembers = async (id: string,
    attachmentId: string,
    params: ListAgentSharedAttachmentMembersParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentSharedAttachmentPage> => {

  return customFetch<AgentSharedAttachmentPage>(getListAgentSharedAttachmentMembersUrl(id,attachmentId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgentSharedAttachmentMembersQueryKey = (id: string,
    attachmentId: string,
    params?: ListAgentSharedAttachmentMembersParams,) => {
    return [
    `/api/agent/shared-conversations/${id}/attachments/${attachmentId}/members`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAgentSharedAttachmentMembersQueryOptions = <TData = Awaited<ReturnType<typeof listAgentSharedAttachmentMembers>>, TError = unknown>(id: string,
    attachmentId: string,
    params: ListAgentSharedAttachmentMembersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentSharedAttachmentMembers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgentSharedAttachmentMembersQueryKey(id,attachmentId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgentSharedAttachmentMembers>>> = ({ signal }) => listAgentSharedAttachmentMembers(id,attachmentId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && attachmentId !== null && attachmentId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgentSharedAttachmentMembers>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAgentSharedAttachmentMembersQueryResult = NonNullable<Awaited<ReturnType<typeof listAgentSharedAttachmentMembers>>>
export type ListAgentSharedAttachmentMembersQueryError = unknown



export function useListAgentSharedAttachmentMembers<TData = Awaited<ReturnType<typeof listAgentSharedAttachmentMembers>>, TError = unknown>(
 id: string,
    attachmentId: string,
    params: ListAgentSharedAttachmentMembersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentSharedAttachmentMembers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAgentSharedAttachmentMembersQueryOptions(id,attachmentId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListAgentSharedAttachmentSectionsUrl = (id: string,
    attachmentId: string,
    params: ListAgentSharedAttachmentSectionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/shared-conversations/${id}/attachments/${attachmentId}/sections?${stringifiedParams}` : `/api/agent/shared-conversations/${id}/attachments/${attachmentId}/sections`
}

/**
 * Exact record-shared uploads; no Collateral adoption, public link or document approval.
 */
export const listAgentSharedAttachmentSections = async (id: string,
    attachmentId: string,
    params: ListAgentSharedAttachmentSectionsParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentAttachmentSectionPage> => {

  return customFetch<AgentAttachmentSectionPage>(getListAgentSharedAttachmentSectionsUrl(id,attachmentId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgentSharedAttachmentSectionsQueryKey = (id: string,
    attachmentId: string,
    params?: ListAgentSharedAttachmentSectionsParams,) => {
    return [
    `/api/agent/shared-conversations/${id}/attachments/${attachmentId}/sections`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAgentSharedAttachmentSectionsQueryOptions = <TData = Awaited<ReturnType<typeof listAgentSharedAttachmentSections>>, TError = unknown>(id: string,
    attachmentId: string,
    params: ListAgentSharedAttachmentSectionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentSharedAttachmentSections>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgentSharedAttachmentSectionsQueryKey(id,attachmentId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgentSharedAttachmentSections>>> = ({ signal }) => listAgentSharedAttachmentSections(id,attachmentId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && attachmentId !== null && attachmentId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgentSharedAttachmentSections>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAgentSharedAttachmentSectionsQueryResult = NonNullable<Awaited<ReturnType<typeof listAgentSharedAttachmentSections>>>
export type ListAgentSharedAttachmentSectionsQueryError = unknown



export function useListAgentSharedAttachmentSections<TData = Awaited<ReturnType<typeof listAgentSharedAttachmentSections>>, TError = unknown>(
 id: string,
    attachmentId: string,
    params: ListAgentSharedAttachmentSectionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentSharedAttachmentSections>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAgentSharedAttachmentSectionsQueryOptions(id,attachmentId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDownloadAgentSharedAttachmentUrl = (id: string,
    attachmentId: string,
    params: DownloadAgentSharedAttachmentParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/shared-conversations/${id}/attachments/${attachmentId}/content?${stringifiedParams}` : `/api/agent/shared-conversations/${id}/attachments/${attachmentId}/content`
}

/**
 * Exact record-shared uploads; no Collateral adoption, public link or document approval.
 */
export const downloadAgentSharedAttachment = async (id: string,
    attachmentId: string,
    params: DownloadAgentSharedAttachmentParams, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadAgentSharedAttachmentUrl(id,attachmentId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadAgentSharedAttachmentQueryKey = (id: string,
    attachmentId: string,
    params?: DownloadAgentSharedAttachmentParams,) => {
    return [
    `/api/agent/shared-conversations/${id}/attachments/${attachmentId}/content`, ...(params ? [params] : [])
    ] as const;
    }


export const getDownloadAgentSharedAttachmentQueryOptions = <TData = Awaited<ReturnType<typeof downloadAgentSharedAttachment>>, TError = unknown>(id: string,
    attachmentId: string,
    params: DownloadAgentSharedAttachmentParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadAgentSharedAttachment>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadAgentSharedAttachmentQueryKey(id,attachmentId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadAgentSharedAttachment>>> = ({ signal }) => downloadAgentSharedAttachment(id,attachmentId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && attachmentId !== null && attachmentId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadAgentSharedAttachment>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadAgentSharedAttachmentQueryResult = NonNullable<Awaited<ReturnType<typeof downloadAgentSharedAttachment>>>
export type DownloadAgentSharedAttachmentQueryError = unknown



export function useDownloadAgentSharedAttachment<TData = Awaited<ReturnType<typeof downloadAgentSharedAttachment>>, TError = unknown>(
 id: string,
    attachmentId: string,
    params: DownloadAgentSharedAttachmentParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadAgentSharedAttachment>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadAgentSharedAttachmentQueryOptions(id,attachmentId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getProcessAgentSharedAttachmentUrl = (id: string,
    attachmentId: string,) => {




  return `/api/agent/shared-conversations/${id}/attachments/${attachmentId}/process`
}

/**
 * Exact record-shared uploads; no Collateral adoption, public link or document approval.
 */
export const processAgentSharedAttachment = async (id: string,
    attachmentId: string,
    agentSharedAttachmentProcess: AgentSharedAttachmentProcess, options?: Parameters<typeof customFetch>[1]): Promise<AgentSharedAttachment> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentSharedAttachment>(getProcessAgentSharedAttachmentUrl(id,attachmentId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentSharedAttachmentProcess)
  }
);}





export const getProcessAgentSharedAttachmentMutationKey = () => ['processAgentSharedAttachment'] as const;

export const getProcessAgentSharedAttachmentMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof processAgentSharedAttachment>>, TError,ProcessAgentSharedAttachmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof processAgentSharedAttachment>>, TError,ProcessAgentSharedAttachmentMutationVariables, TContext> => {

const mutationKey = getProcessAgentSharedAttachmentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof processAgentSharedAttachment>>, ProcessAgentSharedAttachmentMutationVariables> = (props) => {
          const {id,attachmentId,data} = props ?? {};

          return  processAgentSharedAttachment(id,attachmentId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ProcessAgentSharedAttachmentMutationResult = NonNullable<Awaited<ReturnType<typeof processAgentSharedAttachment>>>
    export type ProcessAgentSharedAttachmentMutationBody = AgentSharedAttachmentProcess
    export type ProcessAgentSharedAttachmentMutationError = unknown
    export type ProcessAgentSharedAttachmentMutationVariables = {id: string;attachmentId: string;data: AgentSharedAttachmentProcess}

    export const useProcessAgentSharedAttachment = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof processAgentSharedAttachment>>, TError,ProcessAgentSharedAttachmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof processAgentSharedAttachment>>,
        TError,
        ProcessAgentSharedAttachmentMutationVariables,
        TContext
      > => {
      return useMutation(getProcessAgentSharedAttachmentMutationOptions(options));
    }

export const getListAgentSharedMessagesUrl = (id: string,
    params: ListAgentSharedMessagesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/shared-conversations/${id}/messages?${stringifiedParams}` : `/api/agent/shared-conversations/${id}/messages`
}

export const listAgentSharedMessages = async (id: string,
    params: ListAgentSharedMessagesParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentSharedMessagePage> => {

  return customFetch<AgentSharedMessagePage>(getListAgentSharedMessagesUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgentSharedMessagesQueryKey = (id: string,
    params?: ListAgentSharedMessagesParams,) => {
    return [
    `/api/agent/shared-conversations/${id}/messages`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAgentSharedMessagesQueryOptions = <TData = Awaited<ReturnType<typeof listAgentSharedMessages>>, TError = unknown>(id: string,
    params: ListAgentSharedMessagesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentSharedMessages>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgentSharedMessagesQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgentSharedMessages>>> = ({ signal }) => listAgentSharedMessages(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgentSharedMessages>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAgentSharedMessagesQueryResult = NonNullable<Awaited<ReturnType<typeof listAgentSharedMessages>>>
export type ListAgentSharedMessagesQueryError = unknown



export function useListAgentSharedMessages<TData = Awaited<ReturnType<typeof listAgentSharedMessages>>, TError = unknown>(
 id: string,
    params: ListAgentSharedMessagesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentSharedMessages>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAgentSharedMessagesQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSendAgentSharedTurnUrl = (id: string,) => {




  return `/api/agent/shared-conversations/${id}/turns`
}

export const sendAgentSharedTurn = async (id: string,
    agentSharedTurnRequest: AgentSharedTurnRequest, options?: Parameters<typeof customFetch>[1]): Promise<AgentSharedTurnResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentSharedTurnResult>(getSendAgentSharedTurnUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentSharedTurnRequest)
  }
);}





export const getSendAgentSharedTurnMutationKey = () => ['sendAgentSharedTurn'] as const;

export const getSendAgentSharedTurnMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendAgentSharedTurn>>, TError,SendAgentSharedTurnMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof sendAgentSharedTurn>>, TError,SendAgentSharedTurnMutationVariables, TContext> => {

const mutationKey = getSendAgentSharedTurnMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof sendAgentSharedTurn>>, SendAgentSharedTurnMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  sendAgentSharedTurn(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SendAgentSharedTurnMutationResult = NonNullable<Awaited<ReturnType<typeof sendAgentSharedTurn>>>
    export type SendAgentSharedTurnMutationBody = AgentSharedTurnRequest
    export type SendAgentSharedTurnMutationError = unknown
    export type SendAgentSharedTurnMutationVariables = {id: string;data: AgentSharedTurnRequest}

    export const useSendAgentSharedTurn = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendAgentSharedTurn>>, TError,SendAgentSharedTurnMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof sendAgentSharedTurn>>,
        TError,
        SendAgentSharedTurnMutationVariables,
        TContext
      > => {
      return useMutation(getSendAgentSharedTurnMutationOptions(options));
    }

export const getGetAgentSharedOperationUrl = (id: string,
    requestId: string,
    params: GetAgentSharedOperationParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/shared-conversations/${id}/operations/${requestId}?${stringifiedParams}` : `/api/agent/shared-conversations/${id}/operations/${requestId}`
}

export const getAgentSharedOperation = async (id: string,
    requestId: string,
    params: GetAgentSharedOperationParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentSharedTurnResult> => {

  return customFetch<AgentSharedTurnResult>(getGetAgentSharedOperationUrl(id,requestId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAgentSharedOperationQueryKey = (id: string,
    requestId: string,
    params?: GetAgentSharedOperationParams,) => {
    return [
    `/api/agent/shared-conversations/${id}/operations/${requestId}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetAgentSharedOperationQueryOptions = <TData = Awaited<ReturnType<typeof getAgentSharedOperation>>, TError = unknown>(id: string,
    requestId: string,
    params: GetAgentSharedOperationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentSharedOperation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAgentSharedOperationQueryKey(id,requestId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAgentSharedOperation>>> = ({ signal }) => getAgentSharedOperation(id,requestId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && requestId !== null && requestId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAgentSharedOperation>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAgentSharedOperationQueryResult = NonNullable<Awaited<ReturnType<typeof getAgentSharedOperation>>>
export type GetAgentSharedOperationQueryError = unknown



export function useGetAgentSharedOperation<TData = Awaited<ReturnType<typeof getAgentSharedOperation>>, TError = unknown>(
 id: string,
    requestId: string,
    params: GetAgentSharedOperationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentSharedOperation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAgentSharedOperationQueryOptions(id,requestId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getStopAgentSharedTurnUrl = (id: string,
    messageId: string,) => {




  return `/api/agent/shared-conversations/${id}/messages/${messageId}/stop`
}

export const stopAgentSharedTurn = async (id: string,
    messageId: string,
    agentSharedStopRequest: AgentSharedStopRequest, options?: Parameters<typeof customFetch>[1]): Promise<AgentSharedMessage> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentSharedMessage>(getStopAgentSharedTurnUrl(id,messageId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentSharedStopRequest)
  }
);}





export const getStopAgentSharedTurnMutationKey = () => ['stopAgentSharedTurn'] as const;

export const getStopAgentSharedTurnMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof stopAgentSharedTurn>>, TError,StopAgentSharedTurnMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof stopAgentSharedTurn>>, TError,StopAgentSharedTurnMutationVariables, TContext> => {

const mutationKey = getStopAgentSharedTurnMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof stopAgentSharedTurn>>, StopAgentSharedTurnMutationVariables> = (props) => {
          const {id,messageId,data} = props ?? {};

          return  stopAgentSharedTurn(id,messageId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type StopAgentSharedTurnMutationResult = NonNullable<Awaited<ReturnType<typeof stopAgentSharedTurn>>>
    export type StopAgentSharedTurnMutationBody = AgentSharedStopRequest
    export type StopAgentSharedTurnMutationError = unknown
    export type StopAgentSharedTurnMutationVariables = {id: string;messageId: string;data: AgentSharedStopRequest}

    export const useStopAgentSharedTurn = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof stopAgentSharedTurn>>, TError,StopAgentSharedTurnMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof stopAgentSharedTurn>>,
        TError,
        StopAgentSharedTurnMutationVariables,
        TContext
      > => {
      return useMutation(getStopAgentSharedTurnMutationOptions(options));
    }

export const getListAgentSharedConversationsUrl = (params: ListAgentSharedConversationsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/shared-conversations?${stringifiedParams}` : `/api/agent/shared-conversations`
}

export const listAgentSharedConversations = async (params: ListAgentSharedConversationsParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentSharedConversationPage> => {

  return customFetch<AgentSharedConversationPage>(getListAgentSharedConversationsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgentSharedConversationsQueryKey = (params?: ListAgentSharedConversationsParams,) => {
    return [
    `/api/agent/shared-conversations`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAgentSharedConversationsQueryOptions = <TData = Awaited<ReturnType<typeof listAgentSharedConversations>>, TError = unknown>(params: ListAgentSharedConversationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentSharedConversations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgentSharedConversationsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgentSharedConversations>>> = ({ signal }) => listAgentSharedConversations(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgentSharedConversations>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAgentSharedConversationsQueryResult = NonNullable<Awaited<ReturnType<typeof listAgentSharedConversations>>>
export type ListAgentSharedConversationsQueryError = unknown



export function useListAgentSharedConversations<TData = Awaited<ReturnType<typeof listAgentSharedConversations>>, TError = unknown>(
 params: ListAgentSharedConversationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentSharedConversations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAgentSharedConversationsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateAgentSharedConversationUrl = () => {




  return `/api/agent/shared-conversations`
}

export const createAgentSharedConversation = async (agentSharedConversationCreate: AgentSharedConversationCreate, options?: Parameters<typeof customFetch>[1]): Promise<AgentSharedConversation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentSharedConversation>(getCreateAgentSharedConversationUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentSharedConversationCreate)
  }
);}





export const getCreateAgentSharedConversationMutationKey = () => ['createAgentSharedConversation'] as const;

export const getCreateAgentSharedConversationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAgentSharedConversation>>, TError,CreateAgentSharedConversationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createAgentSharedConversation>>, TError,CreateAgentSharedConversationMutationVariables, TContext> => {

const mutationKey = getCreateAgentSharedConversationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAgentSharedConversation>>, CreateAgentSharedConversationMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createAgentSharedConversation(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateAgentSharedConversationMutationResult = NonNullable<Awaited<ReturnType<typeof createAgentSharedConversation>>>
    export type CreateAgentSharedConversationMutationBody = AgentSharedConversationCreate
    export type CreateAgentSharedConversationMutationError = unknown
    export type CreateAgentSharedConversationMutationVariables = {data: AgentSharedConversationCreate}

    export const useCreateAgentSharedConversation = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAgentSharedConversation>>, TError,CreateAgentSharedConversationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createAgentSharedConversation>>,
        TError,
        CreateAgentSharedConversationMutationVariables,
        TContext
      > => {
      return useMutation(getCreateAgentSharedConversationMutationOptions(options));
    }

export const getGetAgentSharedConversationUrl = (id: string,
    params: GetAgentSharedConversationParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/shared-conversations/${id}?${stringifiedParams}` : `/api/agent/shared-conversations/${id}`
}

export const getAgentSharedConversation = async (id: string,
    params: GetAgentSharedConversationParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentSharedConversation> => {

  return customFetch<AgentSharedConversation>(getGetAgentSharedConversationUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAgentSharedConversationQueryKey = (id: string,
    params?: GetAgentSharedConversationParams,) => {
    return [
    `/api/agent/shared-conversations/${id}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetAgentSharedConversationQueryOptions = <TData = Awaited<ReturnType<typeof getAgentSharedConversation>>, TError = unknown>(id: string,
    params: GetAgentSharedConversationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentSharedConversation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAgentSharedConversationQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAgentSharedConversation>>> = ({ signal }) => getAgentSharedConversation(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAgentSharedConversation>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAgentSharedConversationQueryResult = NonNullable<Awaited<ReturnType<typeof getAgentSharedConversation>>>
export type GetAgentSharedConversationQueryError = unknown



export function useGetAgentSharedConversation<TData = Awaited<ReturnType<typeof getAgentSharedConversation>>, TError = unknown>(
 id: string,
    params: GetAgentSharedConversationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentSharedConversation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAgentSharedConversationQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateAgentSharedConversationUrl = (id: string,) => {




  return `/api/agent/shared-conversations/${id}`
}

export const updateAgentSharedConversation = async (id: string,
    agentSharedConversationUpdate: AgentSharedConversationUpdate, options?: Parameters<typeof customFetch>[1]): Promise<AgentSharedConversation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentSharedConversation>(getUpdateAgentSharedConversationUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentSharedConversationUpdate)
  }
);}





export const getUpdateAgentSharedConversationMutationKey = () => ['updateAgentSharedConversation'] as const;

export const getUpdateAgentSharedConversationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAgentSharedConversation>>, TError,UpdateAgentSharedConversationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateAgentSharedConversation>>, TError,UpdateAgentSharedConversationMutationVariables, TContext> => {

const mutationKey = getUpdateAgentSharedConversationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAgentSharedConversation>>, UpdateAgentSharedConversationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateAgentSharedConversation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateAgentSharedConversationMutationResult = NonNullable<Awaited<ReturnType<typeof updateAgentSharedConversation>>>
    export type UpdateAgentSharedConversationMutationBody = AgentSharedConversationUpdate
    export type UpdateAgentSharedConversationMutationError = unknown
    export type UpdateAgentSharedConversationMutationVariables = {id: string;data: AgentSharedConversationUpdate}

    export const useUpdateAgentSharedConversation = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAgentSharedConversation>>, TError,UpdateAgentSharedConversationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateAgentSharedConversation>>,
        TError,
        UpdateAgentSharedConversationMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateAgentSharedConversationMutationOptions(options));
    }

export const getListAgentConversationsUrl = (params: ListAgentConversationsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/conversations?${stringifiedParams}` : `/api/agent/conversations`
}

export const listAgentConversations = async (params: ListAgentConversationsParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentConversationPage> => {

  return customFetch<AgentConversationPage>(getListAgentConversationsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgentConversationsQueryKey = (params?: ListAgentConversationsParams,) => {
    return [
    `/api/agent/conversations`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAgentConversationsQueryOptions = <TData = Awaited<ReturnType<typeof listAgentConversations>>, TError = unknown>(params: ListAgentConversationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentConversations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgentConversationsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgentConversations>>> = ({ signal }) => listAgentConversations(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgentConversations>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAgentConversationsQueryResult = NonNullable<Awaited<ReturnType<typeof listAgentConversations>>>
export type ListAgentConversationsQueryError = unknown



export function useListAgentConversations<TData = Awaited<ReturnType<typeof listAgentConversations>>, TError = unknown>(
 params: ListAgentConversationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentConversations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAgentConversationsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateAgentConversationUrl = () => {




  return `/api/agent/conversations`
}

export const createAgentConversation = async (agentConversationCreate: AgentConversationCreate, options?: Parameters<typeof customFetch>[1]): Promise<AgentConversation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentConversation>(getCreateAgentConversationUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentConversationCreate)
  }
);}





export const getCreateAgentConversationMutationKey = () => ['createAgentConversation'] as const;

export const getCreateAgentConversationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAgentConversation>>, TError,CreateAgentConversationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createAgentConversation>>, TError,CreateAgentConversationMutationVariables, TContext> => {

const mutationKey = getCreateAgentConversationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAgentConversation>>, CreateAgentConversationMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createAgentConversation(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateAgentConversationMutationResult = NonNullable<Awaited<ReturnType<typeof createAgentConversation>>>
    export type CreateAgentConversationMutationBody = AgentConversationCreate
    export type CreateAgentConversationMutationError = unknown
    export type CreateAgentConversationMutationVariables = {data: AgentConversationCreate}

    export const useCreateAgentConversation = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAgentConversation>>, TError,CreateAgentConversationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createAgentConversation>>,
        TError,
        CreateAgentConversationMutationVariables,
        TContext
      > => {
      return useMutation(getCreateAgentConversationMutationOptions(options));
    }

export const getGetAgentConversationUrl = (id: string,
    params: GetAgentConversationParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/conversations/${id}?${stringifiedParams}` : `/api/agent/conversations/${id}`
}

export const getAgentConversation = async (id: string,
    params: GetAgentConversationParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentConversation> => {

  return customFetch<AgentConversation>(getGetAgentConversationUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAgentConversationQueryKey = (id: string,
    params?: GetAgentConversationParams,) => {
    return [
    `/api/agent/conversations/${id}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetAgentConversationQueryOptions = <TData = Awaited<ReturnType<typeof getAgentConversation>>, TError = unknown>(id: string,
    params: GetAgentConversationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentConversation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAgentConversationQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAgentConversation>>> = ({ signal }) => getAgentConversation(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAgentConversation>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAgentConversationQueryResult = NonNullable<Awaited<ReturnType<typeof getAgentConversation>>>
export type GetAgentConversationQueryError = unknown



export function useGetAgentConversation<TData = Awaited<ReturnType<typeof getAgentConversation>>, TError = unknown>(
 id: string,
    params: GetAgentConversationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentConversation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAgentConversationQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateAgentConversationUrl = (id: string,) => {




  return `/api/agent/conversations/${id}`
}

export const updateAgentConversation = async (id: string,
    agentConversationUpdate: AgentConversationUpdate, options?: Parameters<typeof customFetch>[1]): Promise<AgentConversation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentConversation>(getUpdateAgentConversationUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentConversationUpdate)
  }
);}





export const getUpdateAgentConversationMutationKey = () => ['updateAgentConversation'] as const;

export const getUpdateAgentConversationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAgentConversation>>, TError,UpdateAgentConversationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateAgentConversation>>, TError,UpdateAgentConversationMutationVariables, TContext> => {

const mutationKey = getUpdateAgentConversationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAgentConversation>>, UpdateAgentConversationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateAgentConversation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateAgentConversationMutationResult = NonNullable<Awaited<ReturnType<typeof updateAgentConversation>>>
    export type UpdateAgentConversationMutationBody = AgentConversationUpdate
    export type UpdateAgentConversationMutationError = unknown
    export type UpdateAgentConversationMutationVariables = {id: string;data: AgentConversationUpdate}

    export const useUpdateAgentConversation = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAgentConversation>>, TError,UpdateAgentConversationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateAgentConversation>>,
        TError,
        UpdateAgentConversationMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateAgentConversationMutationOptions(options));
    }

export const getListAgentHandoffDestinationsUrl = (params: ListAgentHandoffDestinationsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/handoff-destinations?${stringifiedParams}` : `/api/agent/handoff-destinations`
}

/**
 * Paged list of the other assistants (authorized scopes and research or internal modes) the signed-in person may hand a private saved chat to.
 * @summary List assistants a saved chat can be handed to
 */
export const listAgentHandoffDestinations = async (params: ListAgentHandoffDestinationsParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentHandoffDestinationPage> => {

  return customFetch<AgentHandoffDestinationPage>(getListAgentHandoffDestinationsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgentHandoffDestinationsQueryKey = (params?: ListAgentHandoffDestinationsParams,) => {
    return [
    `/api/agent/handoff-destinations`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAgentHandoffDestinationsQueryOptions = <TData = Awaited<ReturnType<typeof listAgentHandoffDestinations>>, TError = unknown>(params: ListAgentHandoffDestinationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentHandoffDestinations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgentHandoffDestinationsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgentHandoffDestinations>>> = ({ signal }) => listAgentHandoffDestinations(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgentHandoffDestinations>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAgentHandoffDestinationsQueryResult = NonNullable<Awaited<ReturnType<typeof listAgentHandoffDestinations>>>
export type ListAgentHandoffDestinationsQueryError = unknown


/**
 * @summary List assistants a saved chat can be handed to
 */

export function useListAgentHandoffDestinations<TData = Awaited<ReturnType<typeof listAgentHandoffDestinations>>, TError = unknown>(
 params: ListAgentHandoffDestinationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentHandoffDestinations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAgentHandoffDestinationsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewAgentHandoffUrl = (id: string,) => {




  return `/api/agent/conversations/${id}/handoff-review`
}

/**
 * Prepares an actor-private, expiring hand-off review. Nothing is copied until the person confirms it.
 * @summary Review which messages a chat hand-off copies
 */
export const reviewAgentHandoff = async (id: string,
    agentHandoffReviewRequest: AgentHandoffReviewRequest, options?: Parameters<typeof customFetch>[1]): Promise<AgentHandoffReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentHandoffReview>(getReviewAgentHandoffUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentHandoffReviewRequest)
  }
);}





export const getReviewAgentHandoffMutationKey = () => ['reviewAgentHandoff'] as const;

export const getReviewAgentHandoffMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewAgentHandoff>>, TError,ReviewAgentHandoffMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewAgentHandoff>>, TError,ReviewAgentHandoffMutationVariables, TContext> => {

const mutationKey = getReviewAgentHandoffMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewAgentHandoff>>, ReviewAgentHandoffMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reviewAgentHandoff(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewAgentHandoffMutationResult = NonNullable<Awaited<ReturnType<typeof reviewAgentHandoff>>>
    export type ReviewAgentHandoffMutationBody = AgentHandoffReviewRequest
    export type ReviewAgentHandoffMutationError = unknown
    export type ReviewAgentHandoffMutationVariables = {id: string;data: AgentHandoffReviewRequest}

    /**
 * @summary Review which messages a chat hand-off copies
 */
export const useReviewAgentHandoff = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewAgentHandoff>>, TError,ReviewAgentHandoffMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewAgentHandoff>>,
        TError,
        ReviewAgentHandoffMutationVariables,
        TContext
      > => {
      return useMutation(getReviewAgentHandoffMutationOptions(options));
    }

export const getConfirmAgentHandoffUrl = (id: string,) => {




  return `/api/agent/conversations/${id}/handoff`
}

/**
 * Copies the reviewed messages into a new private chat in the destination assistant. The source chat is unchanged. Retrying the same request returns the same receipt.
 * @summary Confirm a reviewed chat hand-off
 */
export const confirmAgentHandoff = async (id: string,
    agentHandoffConfirmRequest: AgentHandoffConfirmRequest, options?: Parameters<typeof customFetch>[1]): Promise<AgentHandoffResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentHandoffResult>(getConfirmAgentHandoffUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentHandoffConfirmRequest)
  }
);}





export const getConfirmAgentHandoffMutationKey = () => ['confirmAgentHandoff'] as const;

export const getConfirmAgentHandoffMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmAgentHandoff>>, TError,ConfirmAgentHandoffMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmAgentHandoff>>, TError,ConfirmAgentHandoffMutationVariables, TContext> => {

const mutationKey = getConfirmAgentHandoffMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmAgentHandoff>>, ConfirmAgentHandoffMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  confirmAgentHandoff(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmAgentHandoffMutationResult = NonNullable<Awaited<ReturnType<typeof confirmAgentHandoff>>>
    export type ConfirmAgentHandoffMutationBody = AgentHandoffConfirmRequest
    export type ConfirmAgentHandoffMutationError = unknown
    export type ConfirmAgentHandoffMutationVariables = {id: string;data: AgentHandoffConfirmRequest}

    /**
 * @summary Confirm a reviewed chat hand-off
 */
export const useConfirmAgentHandoff = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmAgentHandoff>>, TError,ConfirmAgentHandoffMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmAgentHandoff>>,
        TError,
        ConfirmAgentHandoffMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmAgentHandoffMutationOptions(options));
    }

export const getExportAgentPromptsUrl = () => {




  return `/api/agent/prompts/export`
}

export const exportAgentPrompts = async (agentPromptExportRequest: AgentPromptExportRequest, options?: Parameters<typeof customFetch>[1]): Promise<AgentPromptFile> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentPromptFile>(getExportAgentPromptsUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentPromptExportRequest)
  }
);}





export const getExportAgentPromptsMutationKey = () => ['exportAgentPrompts'] as const;

export const getExportAgentPromptsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof exportAgentPrompts>>, TError,ExportAgentPromptsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof exportAgentPrompts>>, TError,ExportAgentPromptsMutationVariables, TContext> => {

const mutationKey = getExportAgentPromptsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof exportAgentPrompts>>, ExportAgentPromptsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  exportAgentPrompts(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ExportAgentPromptsMutationResult = NonNullable<Awaited<ReturnType<typeof exportAgentPrompts>>>
    export type ExportAgentPromptsMutationBody = AgentPromptExportRequest
    export type ExportAgentPromptsMutationError = unknown
    export type ExportAgentPromptsMutationVariables = {data: AgentPromptExportRequest}

    export const useExportAgentPrompts = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof exportAgentPrompts>>, TError,ExportAgentPromptsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof exportAgentPrompts>>,
        TError,
        ExportAgentPromptsMutationVariables,
        TContext
      > => {
      return useMutation(getExportAgentPromptsMutationOptions(options));
    }

export const getPreviewAgentPromptImportUrl = () => {




  return `/api/agent/prompts/import-preview`
}

export const previewAgentPromptImport = async (agentPromptImportRequest: AgentPromptImportRequest, options?: Parameters<typeof customFetch>[1]): Promise<AgentPromptImportPreview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentPromptImportPreview>(getPreviewAgentPromptImportUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentPromptImportRequest)
  }
);}





export const getPreviewAgentPromptImportMutationKey = () => ['previewAgentPromptImport'] as const;

export const getPreviewAgentPromptImportMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewAgentPromptImport>>, TError,PreviewAgentPromptImportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewAgentPromptImport>>, TError,PreviewAgentPromptImportMutationVariables, TContext> => {

const mutationKey = getPreviewAgentPromptImportMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewAgentPromptImport>>, PreviewAgentPromptImportMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  previewAgentPromptImport(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewAgentPromptImportMutationResult = NonNullable<Awaited<ReturnType<typeof previewAgentPromptImport>>>
    export type PreviewAgentPromptImportMutationBody = AgentPromptImportRequest
    export type PreviewAgentPromptImportMutationError = unknown
    export type PreviewAgentPromptImportMutationVariables = {data: AgentPromptImportRequest}

    export const usePreviewAgentPromptImport = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewAgentPromptImport>>, TError,PreviewAgentPromptImportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewAgentPromptImport>>,
        TError,
        PreviewAgentPromptImportMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewAgentPromptImportMutationOptions(options));
    }

export const getApplyAgentPromptImportUrl = () => {




  return `/api/agent/prompts/import-apply`
}

export const applyAgentPromptImport = async (agentPromptImportApply: AgentPromptImportApply, options?: Parameters<typeof customFetch>[1]): Promise<AgentPromptImportReceipt> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentPromptImportReceipt>(getApplyAgentPromptImportUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentPromptImportApply)
  }
);}





export const getApplyAgentPromptImportMutationKey = () => ['applyAgentPromptImport'] as const;

export const getApplyAgentPromptImportMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyAgentPromptImport>>, TError,ApplyAgentPromptImportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof applyAgentPromptImport>>, TError,ApplyAgentPromptImportMutationVariables, TContext> => {

const mutationKey = getApplyAgentPromptImportMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof applyAgentPromptImport>>, ApplyAgentPromptImportMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  applyAgentPromptImport(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApplyAgentPromptImportMutationResult = NonNullable<Awaited<ReturnType<typeof applyAgentPromptImport>>>
    export type ApplyAgentPromptImportMutationBody = AgentPromptImportApply
    export type ApplyAgentPromptImportMutationError = unknown
    export type ApplyAgentPromptImportMutationVariables = {data: AgentPromptImportApply}

    export const useApplyAgentPromptImport = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyAgentPromptImport>>, TError,ApplyAgentPromptImportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof applyAgentPromptImport>>,
        TError,
        ApplyAgentPromptImportMutationVariables,
        TContext
      > => {
      return useMutation(getApplyAgentPromptImportMutationOptions(options));
    }

export const getCopyAgentPromptToEntityUrl = () => {




  return `/api/agent/prompts/copy-to-entity`
}

export const copyAgentPromptToEntity = async (agentPromptCopy: AgentPromptCopy, options?: Parameters<typeof customFetch>[1]): Promise<AgentLibraryEntry> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentLibraryEntry>(getCopyAgentPromptToEntityUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentPromptCopy)
  }
);}





export const getCopyAgentPromptToEntityMutationKey = () => ['copyAgentPromptToEntity'] as const;

export const getCopyAgentPromptToEntityMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof copyAgentPromptToEntity>>, TError,CopyAgentPromptToEntityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof copyAgentPromptToEntity>>, TError,CopyAgentPromptToEntityMutationVariables, TContext> => {

const mutationKey = getCopyAgentPromptToEntityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof copyAgentPromptToEntity>>, CopyAgentPromptToEntityMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  copyAgentPromptToEntity(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CopyAgentPromptToEntityMutationResult = NonNullable<Awaited<ReturnType<typeof copyAgentPromptToEntity>>>
    export type CopyAgentPromptToEntityMutationBody = AgentPromptCopy
    export type CopyAgentPromptToEntityMutationError = unknown
    export type CopyAgentPromptToEntityMutationVariables = {data: AgentPromptCopy}

    export const useCopyAgentPromptToEntity = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof copyAgentPromptToEntity>>, TError,CopyAgentPromptToEntityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof copyAgentPromptToEntity>>,
        TError,
        CopyAgentPromptToEntityMutationVariables,
        TContext
      > => {
      return useMutation(getCopyAgentPromptToEntityMutationOptions(options));
    }

export const getListAgentPromptsUrl = (params: ListAgentPromptsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/prompts?${stringifiedParams}` : `/api/agent/prompts`
}

export const listAgentPrompts = async (params: ListAgentPromptsParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentLibraryPage> => {

  return customFetch<AgentLibraryPage>(getListAgentPromptsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgentPromptsQueryKey = (params?: ListAgentPromptsParams,) => {
    return [
    `/api/agent/prompts`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAgentPromptsQueryOptions = <TData = Awaited<ReturnType<typeof listAgentPrompts>>, TError = unknown>(params: ListAgentPromptsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentPrompts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgentPromptsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgentPrompts>>> = ({ signal }) => listAgentPrompts(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgentPrompts>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAgentPromptsQueryResult = NonNullable<Awaited<ReturnType<typeof listAgentPrompts>>>
export type ListAgentPromptsQueryError = unknown



export function useListAgentPrompts<TData = Awaited<ReturnType<typeof listAgentPrompts>>, TError = unknown>(
 params: ListAgentPromptsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentPrompts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAgentPromptsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateAgentPromptUrl = () => {




  return `/api/agent/prompts`
}

export const createAgentPrompt = async (agentPromptCreate: AgentPromptCreate, options?: Parameters<typeof customFetch>[1]): Promise<AgentLibraryEntry> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentLibraryEntry>(getCreateAgentPromptUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentPromptCreate)
  }
);}





export const getCreateAgentPromptMutationKey = () => ['createAgentPrompt'] as const;

export const getCreateAgentPromptMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAgentPrompt>>, TError,CreateAgentPromptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createAgentPrompt>>, TError,CreateAgentPromptMutationVariables, TContext> => {

const mutationKey = getCreateAgentPromptMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAgentPrompt>>, CreateAgentPromptMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createAgentPrompt(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateAgentPromptMutationResult = NonNullable<Awaited<ReturnType<typeof createAgentPrompt>>>
    export type CreateAgentPromptMutationBody = AgentPromptCreate
    export type CreateAgentPromptMutationError = unknown
    export type CreateAgentPromptMutationVariables = {data: AgentPromptCreate}

    export const useCreateAgentPrompt = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAgentPrompt>>, TError,CreateAgentPromptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createAgentPrompt>>,
        TError,
        CreateAgentPromptMutationVariables,
        TContext
      > => {
      return useMutation(getCreateAgentPromptMutationOptions(options));
    }

export const getGetAgentPromptUrl = (id: string,
    params: GetAgentPromptParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/prompts/${id}?${stringifiedParams}` : `/api/agent/prompts/${id}`
}

export const getAgentPrompt = async (id: string,
    params: GetAgentPromptParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentLibraryEntry> => {

  return customFetch<AgentLibraryEntry>(getGetAgentPromptUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAgentPromptQueryKey = (id: string,
    params?: GetAgentPromptParams,) => {
    return [
    `/api/agent/prompts/${id}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetAgentPromptQueryOptions = <TData = Awaited<ReturnType<typeof getAgentPrompt>>, TError = unknown>(id: string,
    params: GetAgentPromptParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentPrompt>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAgentPromptQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAgentPrompt>>> = ({ signal }) => getAgentPrompt(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAgentPrompt>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAgentPromptQueryResult = NonNullable<Awaited<ReturnType<typeof getAgentPrompt>>>
export type GetAgentPromptQueryError = unknown



export function useGetAgentPrompt<TData = Awaited<ReturnType<typeof getAgentPrompt>>, TError = unknown>(
 id: string,
    params: GetAgentPromptParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentPrompt>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAgentPromptQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateAgentPromptUrl = (id: string,) => {




  return `/api/agent/prompts/${id}`
}

export const updateAgentPrompt = async (id: string,
    agentPromptUpdate: AgentPromptUpdate, options?: Parameters<typeof customFetch>[1]): Promise<AgentLibraryEntry> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentLibraryEntry>(getUpdateAgentPromptUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentPromptUpdate)
  }
);}





export const getUpdateAgentPromptMutationKey = () => ['updateAgentPrompt'] as const;

export const getUpdateAgentPromptMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAgentPrompt>>, TError,UpdateAgentPromptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateAgentPrompt>>, TError,UpdateAgentPromptMutationVariables, TContext> => {

const mutationKey = getUpdateAgentPromptMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAgentPrompt>>, UpdateAgentPromptMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateAgentPrompt(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateAgentPromptMutationResult = NonNullable<Awaited<ReturnType<typeof updateAgentPrompt>>>
    export type UpdateAgentPromptMutationBody = AgentPromptUpdate
    export type UpdateAgentPromptMutationError = unknown
    export type UpdateAgentPromptMutationVariables = {id: string;data: AgentPromptUpdate}

    export const useUpdateAgentPrompt = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAgentPrompt>>, TError,UpdateAgentPromptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateAgentPrompt>>,
        TError,
        UpdateAgentPromptMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateAgentPromptMutationOptions(options));
    }

export const getListAgentReviewSetsUrl = (params: ListAgentReviewSetsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/review-sets?${stringifiedParams}` : `/api/agent/review-sets`
}

export const listAgentReviewSets = async (params: ListAgentReviewSetsParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentLibraryPage> => {

  return customFetch<AgentLibraryPage>(getListAgentReviewSetsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgentReviewSetsQueryKey = (params?: ListAgentReviewSetsParams,) => {
    return [
    `/api/agent/review-sets`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAgentReviewSetsQueryOptions = <TData = Awaited<ReturnType<typeof listAgentReviewSets>>, TError = unknown>(params: ListAgentReviewSetsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentReviewSets>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgentReviewSetsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgentReviewSets>>> = ({ signal }) => listAgentReviewSets(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgentReviewSets>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAgentReviewSetsQueryResult = NonNullable<Awaited<ReturnType<typeof listAgentReviewSets>>>
export type ListAgentReviewSetsQueryError = unknown



export function useListAgentReviewSets<TData = Awaited<ReturnType<typeof listAgentReviewSets>>, TError = unknown>(
 params: ListAgentReviewSetsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentReviewSets>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAgentReviewSetsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateAgentReviewSetUrl = () => {




  return `/api/agent/review-sets`
}

export const createAgentReviewSet = async (agentReviewSetCreate: AgentReviewSetCreate, options?: Parameters<typeof customFetch>[1]): Promise<AgentLibraryEntry> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentLibraryEntry>(getCreateAgentReviewSetUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentReviewSetCreate)
  }
);}





export const getCreateAgentReviewSetMutationKey = () => ['createAgentReviewSet'] as const;

export const getCreateAgentReviewSetMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAgentReviewSet>>, TError,CreateAgentReviewSetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createAgentReviewSet>>, TError,CreateAgentReviewSetMutationVariables, TContext> => {

const mutationKey = getCreateAgentReviewSetMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAgentReviewSet>>, CreateAgentReviewSetMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createAgentReviewSet(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateAgentReviewSetMutationResult = NonNullable<Awaited<ReturnType<typeof createAgentReviewSet>>>
    export type CreateAgentReviewSetMutationBody = AgentReviewSetCreate
    export type CreateAgentReviewSetMutationError = unknown
    export type CreateAgentReviewSetMutationVariables = {data: AgentReviewSetCreate}

    export const useCreateAgentReviewSet = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAgentReviewSet>>, TError,CreateAgentReviewSetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createAgentReviewSet>>,
        TError,
        CreateAgentReviewSetMutationVariables,
        TContext
      > => {
      return useMutation(getCreateAgentReviewSetMutationOptions(options));
    }

export const getGetAgentReviewSetUrl = (id: string,
    params: GetAgentReviewSetParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/review-sets/${id}?${stringifiedParams}` : `/api/agent/review-sets/${id}`
}

export const getAgentReviewSet = async (id: string,
    params: GetAgentReviewSetParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentLibraryEntry> => {

  return customFetch<AgentLibraryEntry>(getGetAgentReviewSetUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAgentReviewSetQueryKey = (id: string,
    params?: GetAgentReviewSetParams,) => {
    return [
    `/api/agent/review-sets/${id}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetAgentReviewSetQueryOptions = <TData = Awaited<ReturnType<typeof getAgentReviewSet>>, TError = unknown>(id: string,
    params: GetAgentReviewSetParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentReviewSet>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAgentReviewSetQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAgentReviewSet>>> = ({ signal }) => getAgentReviewSet(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAgentReviewSet>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAgentReviewSetQueryResult = NonNullable<Awaited<ReturnType<typeof getAgentReviewSet>>>
export type GetAgentReviewSetQueryError = unknown



export function useGetAgentReviewSet<TData = Awaited<ReturnType<typeof getAgentReviewSet>>, TError = unknown>(
 id: string,
    params: GetAgentReviewSetParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentReviewSet>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAgentReviewSetQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateAgentReviewSetUrl = (id: string,) => {




  return `/api/agent/review-sets/${id}`
}

export const updateAgentReviewSet = async (id: string,
    agentReviewSetUpdate: AgentReviewSetUpdate, options?: Parameters<typeof customFetch>[1]): Promise<AgentLibraryEntry> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentLibraryEntry>(getUpdateAgentReviewSetUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentReviewSetUpdate)
  }
);}





export const getUpdateAgentReviewSetMutationKey = () => ['updateAgentReviewSet'] as const;

export const getUpdateAgentReviewSetMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAgentReviewSet>>, TError,UpdateAgentReviewSetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateAgentReviewSet>>, TError,UpdateAgentReviewSetMutationVariables, TContext> => {

const mutationKey = getUpdateAgentReviewSetMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAgentReviewSet>>, UpdateAgentReviewSetMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateAgentReviewSet(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateAgentReviewSetMutationResult = NonNullable<Awaited<ReturnType<typeof updateAgentReviewSet>>>
    export type UpdateAgentReviewSetMutationBody = AgentReviewSetUpdate
    export type UpdateAgentReviewSetMutationError = unknown
    export type UpdateAgentReviewSetMutationVariables = {id: string;data: AgentReviewSetUpdate}

    export const useUpdateAgentReviewSet = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAgentReviewSet>>, TError,UpdateAgentReviewSetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateAgentReviewSet>>,
        TError,
        UpdateAgentReviewSetMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateAgentReviewSetMutationOptions(options));
    }

export const getListAgentMessagesUrl = (id: string,
    params: ListAgentMessagesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/conversations/${id}/messages?${stringifiedParams}` : `/api/agent/conversations/${id}/messages`
}

export const listAgentMessages = async (id: string,
    params: ListAgentMessagesParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentMessagePage> => {

  return customFetch<AgentMessagePage>(getListAgentMessagesUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgentMessagesQueryKey = (id: string,
    params?: ListAgentMessagesParams,) => {
    return [
    `/api/agent/conversations/${id}/messages`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAgentMessagesQueryOptions = <TData = Awaited<ReturnType<typeof listAgentMessages>>, TError = unknown>(id: string,
    params: ListAgentMessagesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentMessages>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgentMessagesQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgentMessages>>> = ({ signal }) => listAgentMessages(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgentMessages>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAgentMessagesQueryResult = NonNullable<Awaited<ReturnType<typeof listAgentMessages>>>
export type ListAgentMessagesQueryError = unknown



export function useListAgentMessages<TData = Awaited<ReturnType<typeof listAgentMessages>>, TError = unknown>(
 id: string,
    params: ListAgentMessagesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentMessages>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAgentMessagesQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getStartAgentVoiceUrl = () => {




  return `/api/agent/voice/start`
}

/**
 * @summary Explicitly admit an actor-private bounded ElevenLabs session for a private or exact record-shared chat
 */
export const startAgentVoice = async (startAgentVoiceBody: StartAgentVoiceBody, options?: Parameters<typeof customFetch>[1]): Promise<StartAgentVoice200> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<StartAgentVoice200>(getStartAgentVoiceUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(startAgentVoiceBody)
  }
);}





export const getStartAgentVoiceMutationKey = () => ['startAgentVoice'] as const;

export const getStartAgentVoiceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startAgentVoice>>, TError,StartAgentVoiceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof startAgentVoice>>, TError,StartAgentVoiceMutationVariables, TContext> => {

const mutationKey = getStartAgentVoiceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof startAgentVoice>>, StartAgentVoiceMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  startAgentVoice(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type StartAgentVoiceMutationResult = NonNullable<Awaited<ReturnType<typeof startAgentVoice>>>
    export type StartAgentVoiceMutationBody = StartAgentVoiceBody
    export type StartAgentVoiceMutationError = unknown
    export type StartAgentVoiceMutationVariables = {data: StartAgentVoiceBody}

    /**
 * @summary Explicitly admit an actor-private bounded ElevenLabs session for a private or exact record-shared chat
 */
export const useStartAgentVoice = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startAgentVoice>>, TError,StartAgentVoiceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof startAgentVoice>>,
        TError,
        StartAgentVoiceMutationVariables,
        TContext
      > => {
      return useMutation(getStartAgentVoiceMutationOptions(options));
    }

export const getOperateAgentVoiceUrl = () => {




  return `/api/agent/voice/operate`
}

/**
 * @summary Transcribe bounded PCM audio, speak a reply, or end a private session
 */
export const operateAgentVoice = async (operateAgentVoiceBody: OperateAgentVoiceBody, options?: Parameters<typeof customFetch>[1]): Promise<OperateAgentVoice200> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OperateAgentVoice200>(getOperateAgentVoiceUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(operateAgentVoiceBody)
  }
);}





export const getOperateAgentVoiceMutationKey = () => ['operateAgentVoice'] as const;

export const getOperateAgentVoiceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof operateAgentVoice>>, TError,OperateAgentVoiceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof operateAgentVoice>>, TError,OperateAgentVoiceMutationVariables, TContext> => {

const mutationKey = getOperateAgentVoiceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof operateAgentVoice>>, OperateAgentVoiceMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  operateAgentVoice(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OperateAgentVoiceMutationResult = NonNullable<Awaited<ReturnType<typeof operateAgentVoice>>>
    export type OperateAgentVoiceMutationBody = OperateAgentVoiceBody
    export type OperateAgentVoiceMutationError = unknown
    export type OperateAgentVoiceMutationVariables = {data: OperateAgentVoiceBody}

    /**
 * @summary Transcribe bounded PCM audio, speak a reply, or end a private session
 */
export const useOperateAgentVoice = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof operateAgentVoice>>, TError,OperateAgentVoiceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof operateAgentVoice>>,
        TError,
        OperateAgentVoiceMutationVariables,
        TContext
      > => {
      return useMutation(getOperateAgentVoiceMutationOptions(options));
    }

export const getChatWithAgentUrl = () => {




  return `/api/agent/chat`
}

/**
 * Both the floating Agent and the full Agent workspace use this endpoint.
 * The server retrieves safe, relevant business records from the database;
 * model, endpoint, credentials and grounding remain server-side.
 * @summary Send a message to the authenticated database-grounded Agent
 */
export const chatWithAgent = async (agentChatRequest: AgentChatRequest, options?: Parameters<typeof customFetch>[1]): Promise<AgentChatResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentChatResponse>(getChatWithAgentUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentChatRequest)
  }
);}





export const getChatWithAgentMutationKey = () => ['chatWithAgent'] as const;

export const getChatWithAgentMutationOptions = <TError = Error | AgentConfigurationError | AgentRetryableError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof chatWithAgent>>, TError,ChatWithAgentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof chatWithAgent>>, TError,ChatWithAgentMutationVariables, TContext> => {

const mutationKey = getChatWithAgentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof chatWithAgent>>, ChatWithAgentMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  chatWithAgent(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ChatWithAgentMutationResult = NonNullable<Awaited<ReturnType<typeof chatWithAgent>>>
    export type ChatWithAgentMutationBody = AgentChatRequest
    export type ChatWithAgentMutationError = Error | AgentConfigurationError | AgentRetryableError
    export type ChatWithAgentMutationVariables = {data: AgentChatRequest}

    /**
 * @summary Send a message to the authenticated database-grounded Agent
 */
export const useChatWithAgent = <TError = Error | AgentConfigurationError | AgentRetryableError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof chatWithAgent>>, TError,ChatWithAgentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof chatWithAgent>>,
        TError,
        ChatWithAgentMutationVariables,
        TContext
      > => {
      return useMutation(getChatWithAgentMutationOptions(options));
    }

export const getGetAgentContextUrl = () => {




  return `/api/agent/context`
}

/**
 * @summary Resolve an explicitly selected Agent access scope
 */
export const getAgentContext = async (agentContextRequest: AgentContextRequest, options?: Parameters<typeof customFetch>[1]): Promise<AgentContextResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentContextResponse>(getGetAgentContextUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentContextRequest)
  }
);}





export const getGetAgentContextMutationKey = () => ['getAgentContext'] as const;

export const getGetAgentContextMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof getAgentContext>>, TError,GetAgentContextMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof getAgentContext>>, TError,GetAgentContextMutationVariables, TContext> => {

const mutationKey = getGetAgentContextMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof getAgentContext>>, GetAgentContextMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  getAgentContext(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GetAgentContextMutationResult = NonNullable<Awaited<ReturnType<typeof getAgentContext>>>
    export type GetAgentContextMutationBody = AgentContextRequest
    export type GetAgentContextMutationError = Error
    export type GetAgentContextMutationVariables = {data: AgentContextRequest}

    /**
 * @summary Resolve an explicitly selected Agent access scope
 */
export const useGetAgentContext = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof getAgentContext>>, TError,GetAgentContextMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof getAgentContext>>,
        TError,
        GetAgentContextMutationVariables,
        TContext
      > => {
      return useMutation(getGetAgentContextMutationOptions(options));
    }

export const getPreviewBidSetupUrl = () => {




  return `/api/agent/bid-setup-preview`
}

/**
 * @summary Read the exact bid setup without creating documents or tasks
 */
export const previewBidSetup = async (bidSetupInput: BidSetupInput, options?: Parameters<typeof customFetch>[1]): Promise<BidSetupSnapshot> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<BidSetupSnapshot>(getPreviewBidSetupUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(bidSetupInput)
  }
);}





export const getPreviewBidSetupMutationKey = () => ['previewBidSetup'] as const;

export const getPreviewBidSetupMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewBidSetup>>, TError,PreviewBidSetupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewBidSetup>>, TError,PreviewBidSetupMutationVariables, TContext> => {

const mutationKey = getPreviewBidSetupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewBidSetup>>, PreviewBidSetupMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  previewBidSetup(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewBidSetupMutationResult = NonNullable<Awaited<ReturnType<typeof previewBidSetup>>>
    export type PreviewBidSetupMutationBody = BidSetupInput
    export type PreviewBidSetupMutationError = unknown
    export type PreviewBidSetupMutationVariables = {data: BidSetupInput}

    /**
 * @summary Read the exact bid setup without creating documents or tasks
 */
export const usePreviewBidSetup = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewBidSetup>>, TError,PreviewBidSetupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewBidSetup>>,
        TError,
        PreviewBidSetupMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewBidSetupMutationOptions(options));
    }

export const getGetBidSetupProgressUrl = (id: string,
    planId: string,
    params?: GetBidSetupProgressParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/bid-plans/${planId}/setup?${stringifiedParams}` : `/api/tenders/${id}/bid-plans/${planId}/setup`
}

export const getBidSetupProgress = async (id: string,
    planId: string,
    params?: GetBidSetupProgressParams, options?: Parameters<typeof customFetch>[1]): Promise<BidSetupProgress> => {

  return customFetch<BidSetupProgress>(getGetBidSetupProgressUrl(id,planId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetBidSetupProgressQueryKey = (id: string,
    planId: string,
    params?: GetBidSetupProgressParams,) => {
    return [
    `/api/tenders/${id}/bid-plans/${planId}/setup`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetBidSetupProgressQueryOptions = <TData = Awaited<ReturnType<typeof getBidSetupProgress>>, TError = unknown>(id: string,
    planId: string,
    params?: GetBidSetupProgressParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getBidSetupProgress>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetBidSetupProgressQueryKey(id,planId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getBidSetupProgress>>> = ({ signal }) => getBidSetupProgress(id,planId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && planId !== null && planId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getBidSetupProgress>>, TError, TData> & { queryKey: QueryKey }
}

export type GetBidSetupProgressQueryResult = NonNullable<Awaited<ReturnType<typeof getBidSetupProgress>>>
export type GetBidSetupProgressQueryError = unknown



export function useGetBidSetupProgress<TData = Awaited<ReturnType<typeof getBidSetupProgress>>, TError = unknown>(
 id: string,
    planId: string,
    params?: GetBidSetupProgressParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getBidSetupProgress>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetBidSetupProgressQueryOptions(id,planId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getResumeBidSetupUrl = (id: string,
    planId: string,) => {




  return `/api/tenders/${id}/bid-plans/${planId}/setup`
}

/**
 * @summary Resume the unchanged approved actor-bound setup without repeating completed writes
 */
export const resumeBidSetup = async (id: string,
    planId: string, options?: Parameters<typeof customFetch>[1]): Promise<BidSetupProgress> => {

  return customFetch<BidSetupProgress>(getResumeBidSetupUrl(id,planId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getResumeBidSetupMutationKey = () => ['resumeBidSetup'] as const;

export const getResumeBidSetupMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resumeBidSetup>>, TError,ResumeBidSetupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof resumeBidSetup>>, TError,ResumeBidSetupMutationVariables, TContext> => {

const mutationKey = getResumeBidSetupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof resumeBidSetup>>, ResumeBidSetupMutationVariables> = (props) => {
          const {id,planId} = props ?? {};

          return  resumeBidSetup(id,planId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResumeBidSetupMutationResult = NonNullable<Awaited<ReturnType<typeof resumeBidSetup>>>

    export type ResumeBidSetupMutationError = unknown
    export type ResumeBidSetupMutationVariables = {id: string;planId: string}

    /**
 * @summary Resume the unchanged approved actor-bound setup without repeating completed writes
 */
export const useResumeBidSetup = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resumeBidSetup>>, TError,ResumeBidSetupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof resumeBidSetup>>,
        TError,
        ResumeBidSetupMutationVariables,
        TContext
      > => {
      return useMutation(getResumeBidSetupMutationOptions(options));
    }

export const getCancelBidSetupUrl = (id: string,
    planId: string,) => {




  return `/api/tenders/${id}/bid-plans/${planId}/cancel`
}

export const cancelBidSetup = async (id: string,
    planId: string,
    cancelBidSetupBody: CancelBidSetupBody, options?: Parameters<typeof customFetch>[1]): Promise<BidSetupProgress> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<BidSetupProgress>(getCancelBidSetupUrl(id,planId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(cancelBidSetupBody)
  }
);}





export const getCancelBidSetupMutationKey = () => ['cancelBidSetup'] as const;

export const getCancelBidSetupMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelBidSetup>>, TError,CancelBidSetupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelBidSetup>>, TError,CancelBidSetupMutationVariables, TContext> => {

const mutationKey = getCancelBidSetupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelBidSetup>>, CancelBidSetupMutationVariables> = (props) => {
          const {id,planId,data} = props ?? {};

          return  cancelBidSetup(id,planId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelBidSetupMutationResult = NonNullable<Awaited<ReturnType<typeof cancelBidSetup>>>
    export type CancelBidSetupMutationBody = CancelBidSetupBody
    export type CancelBidSetupMutationError = unknown
    export type CancelBidSetupMutationVariables = {id: string;planId: string;data: CancelBidSetupBody}

    export const useCancelBidSetup = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelBidSetup>>, TError,CancelBidSetupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelBidSetup>>,
        TError,
        CancelBidSetupMutationVariables,
        TContext
      > => {
      return useMutation(getCancelBidSetupMutationOptions(options));
    }

export const getPrepareAgentActionUrl = () => {




  return `/api/agent/proposals`
}

/**
 * @summary Prepare a side-effect-free, durable Agent action proposal
 */
export const prepareAgentAction = async (agentProposalRequest: AgentProposalRequest, options?: Parameters<typeof customFetch>[1]): Promise<AgentActionProposal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentActionProposal>(getPrepareAgentActionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentProposalRequest)
  }
);}





export const getPrepareAgentActionMutationKey = () => ['prepareAgentAction'] as const;

export const getPrepareAgentActionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof prepareAgentAction>>, TError,PrepareAgentActionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof prepareAgentAction>>, TError,PrepareAgentActionMutationVariables, TContext> => {

const mutationKey = getPrepareAgentActionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof prepareAgentAction>>, PrepareAgentActionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  prepareAgentAction(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PrepareAgentActionMutationResult = NonNullable<Awaited<ReturnType<typeof prepareAgentAction>>>
    export type PrepareAgentActionMutationBody = AgentProposalRequest
    export type PrepareAgentActionMutationError = Error
    export type PrepareAgentActionMutationVariables = {data: AgentProposalRequest}

    /**
 * @summary Prepare a side-effect-free, durable Agent action proposal
 */
export const usePrepareAgentAction = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof prepareAgentAction>>, TError,PrepareAgentActionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof prepareAgentAction>>,
        TError,
        PrepareAgentActionMutationVariables,
        TContext
      > => {
      return useMutation(getPrepareAgentActionMutationOptions(options));
    }

export const getGetAgentProposalUrl = (id: string,) => {




  return `/api/agent/proposals/${id}`
}

/**
 * @summary Read one actor-owned Agent proposal
 */
export const getAgentProposal = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<AgentActionProposal> => {

  return customFetch<AgentActionProposal>(getGetAgentProposalUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAgentProposalQueryKey = (id: string,) => {
    return [
    `/api/agent/proposals/${id}`
    ] as const;
    }


export const getGetAgentProposalQueryOptions = <TData = Awaited<ReturnType<typeof getAgentProposal>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentProposal>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAgentProposalQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAgentProposal>>> = ({ signal }) => getAgentProposal(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAgentProposal>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAgentProposalQueryResult = NonNullable<Awaited<ReturnType<typeof getAgentProposal>>>
export type GetAgentProposalQueryError = Error


/**
 * @summary Read one actor-owned Agent proposal
 */

export function useGetAgentProposal<TData = Awaited<ReturnType<typeof getAgentProposal>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentProposal>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAgentProposalQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewAgentProposalUrl = (id: string,) => {




  return `/api/agent/proposals/${id}/review`
}

/**
 * @summary Confirm and execute the exact stored proposal
 */
export const reviewAgentProposal = async (id: string,
    agentProposalReviewRequest: AgentProposalReviewRequest, options?: Parameters<typeof customFetch>[1]): Promise<AgentActionExecution> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentActionExecution>(getReviewAgentProposalUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentProposalReviewRequest)
  }
);}





export const getReviewAgentProposalMutationKey = () => ['reviewAgentProposal'] as const;

export const getReviewAgentProposalMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewAgentProposal>>, TError,ReviewAgentProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewAgentProposal>>, TError,ReviewAgentProposalMutationVariables, TContext> => {

const mutationKey = getReviewAgentProposalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewAgentProposal>>, ReviewAgentProposalMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reviewAgentProposal(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewAgentProposalMutationResult = NonNullable<Awaited<ReturnType<typeof reviewAgentProposal>>>
    export type ReviewAgentProposalMutationBody = AgentProposalReviewRequest
    export type ReviewAgentProposalMutationError = Error
    export type ReviewAgentProposalMutationVariables = {id: string;data: AgentProposalReviewRequest}

    /**
 * @summary Confirm and execute the exact stored proposal
 */
export const useReviewAgentProposal = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewAgentProposal>>, TError,ReviewAgentProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewAgentProposal>>,
        TError,
        ReviewAgentProposalMutationVariables,
        TContext
      > => {
      return useMutation(getReviewAgentProposalMutationOptions(options));
    }

export const getCancelAgentProposalUrl = (id: string,) => {




  return `/api/agent/proposals/${id}/cancel`
}

/**
 * @summary Cancel an unexecuted Agent proposal
 */
export const cancelAgentProposal = async (id: string,
    agentProposalCancelRequest: AgentProposalCancelRequest, options?: Parameters<typeof customFetch>[1]): Promise<AgentActionProposal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentActionProposal>(getCancelAgentProposalUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentProposalCancelRequest)
  }
);}





export const getCancelAgentProposalMutationKey = () => ['cancelAgentProposal'] as const;

export const getCancelAgentProposalMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelAgentProposal>>, TError,CancelAgentProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelAgentProposal>>, TError,CancelAgentProposalMutationVariables, TContext> => {

const mutationKey = getCancelAgentProposalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelAgentProposal>>, CancelAgentProposalMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  cancelAgentProposal(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelAgentProposalMutationResult = NonNullable<Awaited<ReturnType<typeof cancelAgentProposal>>>
    export type CancelAgentProposalMutationBody = AgentProposalCancelRequest
    export type CancelAgentProposalMutationError = unknown
    export type CancelAgentProposalMutationVariables = {id: string;data: AgentProposalCancelRequest}

    /**
 * @summary Cancel an unexecuted Agent proposal
 */
export const useCancelAgentProposal = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelAgentProposal>>, TError,CancelAgentProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelAgentProposal>>,
        TError,
        CancelAgentProposalMutationVariables,
        TContext
      > => {
      return useMutation(getCancelAgentProposalMutationOptions(options));
    }

export const getGetAgentExecutionUrl = (id: string,) => {




  return `/api/agent/executions/${id}`
}

/**
 * @summary Read one actor-owned Agent execution status and receipt
 */
export const getAgentExecution = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<AgentActionExecution> => {

  return customFetch<AgentActionExecution>(getGetAgentExecutionUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAgentExecutionQueryKey = (id: string,) => {
    return [
    `/api/agent/executions/${id}`
    ] as const;
    }


export const getGetAgentExecutionQueryOptions = <TData = Awaited<ReturnType<typeof getAgentExecution>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentExecution>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAgentExecutionQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAgentExecution>>> = ({ signal }) => getAgentExecution(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAgentExecution>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAgentExecutionQueryResult = NonNullable<Awaited<ReturnType<typeof getAgentExecution>>>
export type GetAgentExecutionQueryError = Error


/**
 * @summary Read one actor-owned Agent execution status and receipt
 */

export function useGetAgentExecution<TData = Awaited<ReturnType<typeof getAgentExecution>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentExecution>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAgentExecutionQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListFilesUrl = (params?: ListFilesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/files?${stringifiedParams}` : `/api/files`
}

/**
 * @summary List authorized stored file metadata
 */
export const listFiles = async (params?: ListFilesParams, options?: Parameters<typeof customFetch>[1]): Promise<FileList> => {

  return customFetch<FileList>(getListFilesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListFilesQueryKey = (params?: ListFilesParams,) => {
    return [
    `/api/files`, ...(params ? [params] : [])
    ] as const;
    }


export const getListFilesQueryOptions = <TData = Awaited<ReturnType<typeof listFiles>>, TError = Error>(params?: ListFilesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listFiles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListFilesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listFiles>>> = ({ signal }) => listFiles(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listFiles>>, TError, TData> & { queryKey: QueryKey }
}

export type ListFilesQueryResult = NonNullable<Awaited<ReturnType<typeof listFiles>>>
export type ListFilesQueryError = Error


/**
 * @summary List authorized stored file metadata
 */

export function useListFiles<TData = Awaited<ReturnType<typeof listFiles>>, TError = Error>(
 params?: ListFilesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listFiles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListFilesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUploadFileUrl = () => {




  return `/api/files`
}

/**
 * @summary Upload a file
 */
export const uploadFile = async (uploadFileBody: UploadFileBody, options?: Parameters<typeof customFetch>[1]): Promise<StoredFile> => {
    const formData = new FormData();
formData.append(`file`, uploadFileBody.file);

  return customFetch<StoredFile>(getUploadFileUrl(),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}





export const getUploadFileMutationKey = () => ['uploadFile'] as const;

export const getUploadFileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadFile>>, TError,UploadFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadFile>>, TError,UploadFileMutationVariables, TContext> => {

const mutationKey = getUploadFileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadFile>>, UploadFileMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  uploadFile(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadFileMutationResult = NonNullable<Awaited<ReturnType<typeof uploadFile>>>
    export type UploadFileMutationBody = UploadFileBody
    export type UploadFileMutationError = Error
    export type UploadFileMutationVariables = {data: UploadFileBody}

    /**
 * @summary Upload a file
 */
export const useUploadFile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadFile>>, TError,UploadFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadFile>>,
        TError,
        UploadFileMutationVariables,
        TContext
      > => {
      return useMutation(getUploadFileMutationOptions(options));
    }

export const getDeleteFileUrl = (id: string,) => {




  return `/api/files/${id}`
}

/**
 * @summary Delete a stored file
 */
export const deleteFile = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteFileUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteFileMutationKey = () => ['deleteFile'] as const;

export const getDeleteFileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteFile>>, TError,DeleteFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteFile>>, TError,DeleteFileMutationVariables, TContext> => {

const mutationKey = getDeleteFileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteFile>>, DeleteFileMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteFile(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteFileMutationResult = NonNullable<Awaited<ReturnType<typeof deleteFile>>>

    export type DeleteFileMutationError = Error
    export type DeleteFileMutationVariables = {id: string}

    /**
 * @summary Delete a stored file
 */
export const useDeleteFile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteFile>>, TError,DeleteFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteFile>>,
        TError,
        DeleteFileMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteFileMutationOptions(options));
    }

export const getDownloadFileUrl = (id: string,) => {




  return `/api/files/${id}/content`
}

/**
 * @summary Download file content
 */
export const downloadFile = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadFileUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadFileQueryKey = (id: string,) => {
    return [
    `/api/files/${id}/content`
    ] as const;
    }


export const getDownloadFileQueryOptions = <TData = Awaited<ReturnType<typeof downloadFile>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadFileQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadFile>>> = ({ signal }) => downloadFile(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadFileQueryResult = NonNullable<Awaited<ReturnType<typeof downloadFile>>>
export type DownloadFileQueryError = Error


/**
 * @summary Download file content
 */

export function useDownloadFile<TData = Awaited<ReturnType<typeof downloadFile>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadFileQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListFileLibraryUrl = (params?: ListFileLibraryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/library?${stringifiedParams}` : `/api/library`
}

/**
 * Filters apply before counts, source facets and stable modified-time/kind/ID pagination. Hierarchy views reference canonical sources without copying or provisioning. Account links never grant source access. Campaign files follow current native step media references; campaigns have no inferred primary account. Native Word snapshots remain actor-private.
 * @summary Search all authorized registered file and managed document metadata
 */
export const listFileLibrary = async (params?: ListFileLibraryParams, options?: Parameters<typeof customFetch>[1]): Promise<FileLibraryPage> => {

  return customFetch<FileLibraryPage>(getListFileLibraryUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListFileLibraryQueryKey = (params?: ListFileLibraryParams,) => {
    return [
    `/api/library`, ...(params ? [params] : [])
    ] as const;
    }


export const getListFileLibraryQueryOptions = <TData = Awaited<ReturnType<typeof listFileLibrary>>, TError = Error>(params?: ListFileLibraryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listFileLibrary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListFileLibraryQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listFileLibrary>>> = ({ signal }) => listFileLibrary(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listFileLibrary>>, TError, TData> & { queryKey: QueryKey }
}

export type ListFileLibraryQueryResult = NonNullable<Awaited<ReturnType<typeof listFileLibrary>>>
export type ListFileLibraryQueryError = Error


/**
 * @summary Search all authorized registered file and managed document metadata
 */

export function useListFileLibrary<TData = Awaited<ReturnType<typeof listFileLibrary>>, TError = Error>(
 params?: ListFileLibraryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listFileLibrary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListFileLibraryQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListTenantLibraryEntitiesUrl = (params?: ListTenantLibraryEntitiesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/tenant/library/entities?${stringifiedParams}` : `/api/access/tenant/library/entities`
}

/**
 * Requires tenant.settings.manage and current native entity.records.read. Never changes the active workspace or grants business-record access.
 * @summary Page entities with native file access in the administration tenant
 */
export const listTenantLibraryEntities = async (params?: ListTenantLibraryEntitiesParams, options?: Parameters<typeof customFetch>[1]): Promise<ListTenantLibraryEntities200> => {

  return customFetch<ListTenantLibraryEntities200>(getListTenantLibraryEntitiesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenantLibraryEntitiesQueryKey = (params?: ListTenantLibraryEntitiesParams,) => {
    return [
    `/api/access/tenant/library/entities`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenantLibraryEntitiesQueryOptions = <TData = Awaited<ReturnType<typeof listTenantLibraryEntities>>, TError = void>(params?: ListTenantLibraryEntitiesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantLibraryEntities>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenantLibraryEntitiesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenantLibraryEntities>>> = ({ signal }) => listTenantLibraryEntities(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenantLibraryEntities>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenantLibraryEntitiesQueryResult = NonNullable<Awaited<ReturnType<typeof listTenantLibraryEntities>>>
export type ListTenantLibraryEntitiesQueryError = void


/**
 * @summary Page entities with native file access in the administration tenant
 */

export function useListTenantLibraryEntities<TData = Awaited<ReturnType<typeof listTenantLibraryEntities>>, TError = void>(
 params?: ListTenantLibraryEntitiesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantLibraryEntities>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenantLibraryEntitiesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListTenantFileLibraryUrl = (entityId: string,
    params?: ListTenantFileLibraryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/access/tenant/library/${entityId}?${stringifiedParams}` : `/api/access/tenant/library/${entityId}`
}

/**
 * Requires tenant.settings.manage plus native entity and source permissions. Filters, counts and facets use the native complete library snapshot. No writes or active-context changes.
 * @summary Read-only library in an authorized entity of the administration tenant
 */
export const listTenantFileLibrary = async (entityId: string,
    params?: ListTenantFileLibraryParams, options?: Parameters<typeof customFetch>[1]): Promise<FileLibraryPage> => {

  return customFetch<FileLibraryPage>(getListTenantFileLibraryUrl(entityId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenantFileLibraryQueryKey = (entityId: string,
    params?: ListTenantFileLibraryParams,) => {
    return [
    `/api/access/tenant/library/${entityId}`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenantFileLibraryQueryOptions = <TData = Awaited<ReturnType<typeof listTenantFileLibrary>>, TError = void>(entityId: string,
    params?: ListTenantFileLibraryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantFileLibrary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenantFileLibraryQueryKey(entityId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenantFileLibrary>>> = ({ signal }) => listTenantFileLibrary(entityId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entityId !== null && entityId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenantFileLibrary>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenantFileLibraryQueryResult = NonNullable<Awaited<ReturnType<typeof listTenantFileLibrary>>>
export type ListTenantFileLibraryQueryError = void


/**
 * @summary Read-only library in an authorized entity of the administration tenant
 */

export function useListTenantFileLibrary<TData = Awaited<ReturnType<typeof listTenantFileLibrary>>, TError = void>(
 entityId: string,
    params?: ListTenantFileLibraryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenantFileLibrary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenantFileLibraryQueryOptions(entityId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDownloadTenantLibraryContentUrl = (entityId: string,
    kind: 'file' | 'document',
    id: string,) => {




  return `/api/access/tenant/library/${entityId}/${kind}/${id}/content`
}

/**
 * Requires current tenant administration, native entity/source/file permission and exact tenant/entity ownership. Managed documents export clean Markdown through their native saved-revision owner. Source-owned external resources remain in their native workflow.
 * @summary Read permitted original file bytes or a clean saved document projection
 */
export const downloadTenantLibraryContent = async (entityId: string,
    kind: 'file' | 'document',
    id: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadTenantLibraryContentUrl(entityId,kind,id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadTenantLibraryContentQueryKey = (entityId: string,
    kind: 'file' | 'document',
    id: string,) => {
    return [
    `/api/access/tenant/library/${entityId}/${kind}/${id}/content`
    ] as const;
    }


export const getDownloadTenantLibraryContentQueryOptions = <TData = Awaited<ReturnType<typeof downloadTenantLibraryContent>>, TError = void>(entityId: string,
    kind: 'file' | 'document',
    id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadTenantLibraryContent>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadTenantLibraryContentQueryKey(entityId,kind,id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadTenantLibraryContent>>> = ({ signal }) => downloadTenantLibraryContent(entityId,kind,id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: entityId !== null && entityId !== undefined && kind !== null && kind !== undefined && id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadTenantLibraryContent>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadTenantLibraryContentQueryResult = NonNullable<Awaited<ReturnType<typeof downloadTenantLibraryContent>>>
export type DownloadTenantLibraryContentQueryError = void


/**
 * @summary Read permitted original file bytes or a clean saved document projection
 */

export function useDownloadTenantLibraryContent<TData = Awaited<ReturnType<typeof downloadTenantLibraryContent>>, TError = void>(
 entityId: string,
    kind: 'file' | 'document',
    id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadTenantLibraryContent>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadTenantLibraryContentQueryOptions(entityId,kind,id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRegisterFileLibrarySourcesUrl = () => {




  return `/api/library/register-sources`
}

/**
 * @summary Idempotently register existing entity file sources
 */
export const registerFileLibrarySources = async ( options?: Parameters<typeof customFetch>[1]): Promise<FileSourceRegistrationReport> => {

  return customFetch<FileSourceRegistrationReport>(getRegisterFileLibrarySourcesUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRegisterFileLibrarySourcesMutationKey = () => ['registerFileLibrarySources'] as const;

export const getRegisterFileLibrarySourcesMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof registerFileLibrarySources>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof registerFileLibrarySources>>, TError,void, TContext> => {

const mutationKey = getRegisterFileLibrarySourcesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof registerFileLibrarySources>>, void> = () => {


          return  registerFileLibrarySources(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RegisterFileLibrarySourcesMutationResult = NonNullable<Awaited<ReturnType<typeof registerFileLibrarySources>>>

    export type RegisterFileLibrarySourcesMutationError = unknown


    /**
 * @summary Idempotently register existing entity file sources
 */
export const useRegisterFileLibrarySources = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof registerFileLibrarySources>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof registerFileLibrarySources>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getRegisterFileLibrarySourcesMutationOptions(options));
    }

export const getUpdateFileLibraryEntryUrl = (id: string,) => {




  return `/api/library/${id}`
}

/**
 * Requires entity.records.write and the current source permission. Native Word metadata stays source-owned.
 * @summary Update folder, labels, or archive state
 */
export const updateFileLibraryEntry = async (id: string,
    fileLibraryEntryUpdate: FileLibraryEntryUpdate, options?: Parameters<typeof customFetch>[1]): Promise<FileLibraryEntry> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<FileLibraryEntry>(getUpdateFileLibraryEntryUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(fileLibraryEntryUpdate)
  }
);}





export const getUpdateFileLibraryEntryMutationKey = () => ['updateFileLibraryEntry'] as const;

export const getUpdateFileLibraryEntryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateFileLibraryEntry>>, TError,UpdateFileLibraryEntryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateFileLibraryEntry>>, TError,UpdateFileLibraryEntryMutationVariables, TContext> => {

const mutationKey = getUpdateFileLibraryEntryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateFileLibraryEntry>>, UpdateFileLibraryEntryMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateFileLibraryEntry(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateFileLibraryEntryMutationResult = NonNullable<Awaited<ReturnType<typeof updateFileLibraryEntry>>>
    export type UpdateFileLibraryEntryMutationBody = FileLibraryEntryUpdate
    export type UpdateFileLibraryEntryMutationError = Error
    export type UpdateFileLibraryEntryMutationVariables = {id: string;data: FileLibraryEntryUpdate}

    /**
 * @summary Update folder, labels, or archive state
 */
export const useUpdateFileLibraryEntry = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateFileLibraryEntry>>, TError,UpdateFileLibraryEntryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateFileLibraryEntry>>,
        TError,
        UpdateFileLibraryEntryMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateFileLibraryEntryMutationOptions(options));
    }

export const getGetFileHostUrl = (hostKind: FileHostKind,
    hostId: string,) => {




  return `/api/library/hosts/${hostKind}/${hostId}`
}

/**
 * Requires entity.records.read. Capabilities come from the caller's current permissions and the host record; a missing or invisible host returns 404.
 * @summary Read the server-computed file library capabilities of a host record
 */
export const getFileHost = async (hostKind: FileHostKind,
    hostId: string, options?: Parameters<typeof customFetch>[1]): Promise<FileHostDetails> => {

  return customFetch<FileHostDetails>(getGetFileHostUrl(hostKind,hostId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetFileHostQueryKey = (hostKind: FileHostKind,
    hostId: string,) => {
    return [
    `/api/library/hosts/${hostKind}/${hostId}`
    ] as const;
    }


export const getGetFileHostQueryOptions = <TData = Awaited<ReturnType<typeof getFileHost>>, TError = Error>(hostKind: FileHostKind,
    hostId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getFileHost>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetFileHostQueryKey(hostKind,hostId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getFileHost>>> = ({ signal }) => getFileHost(hostKind,hostId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: hostKind !== null && hostKind !== undefined && hostId !== null && hostId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getFileHost>>, TError, TData> & { queryKey: QueryKey }
}

export type GetFileHostQueryResult = NonNullable<Awaited<ReturnType<typeof getFileHost>>>
export type GetFileHostQueryError = Error


/**
 * @summary Read the server-computed file library capabilities of a host record
 */

export function useGetFileHost<TData = Awaited<ReturnType<typeof getFileHost>>, TError = Error>(
 hostKind: FileHostKind,
    hostId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getFileHost>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetFileHostQueryOptions(hostKind,hostId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getMoveLibraryFilesUrl = () => {




  return `/api/library/files/move`
}

/**
 * Requires entity.records.write. Every file must belong to the host. Idempotent by requestId; a replay returns the original outcome.
 * @summary Move selected library files of one host into a library folder
 */
export const moveLibraryFiles = async (libraryFileMoveRequest: LibraryFileMoveRequest, options?: Parameters<typeof customFetch>[1]): Promise<LibraryFileMoveOutcome> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<LibraryFileMoveOutcome>(getMoveLibraryFilesUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(libraryFileMoveRequest)
  }
);}





export const getMoveLibraryFilesMutationKey = () => ['moveLibraryFiles'] as const;

export const getMoveLibraryFilesMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof moveLibraryFiles>>, TError,MoveLibraryFilesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof moveLibraryFiles>>, TError,MoveLibraryFilesMutationVariables, TContext> => {

const mutationKey = getMoveLibraryFilesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof moveLibraryFiles>>, MoveLibraryFilesMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  moveLibraryFiles(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MoveLibraryFilesMutationResult = NonNullable<Awaited<ReturnType<typeof moveLibraryFiles>>>
    export type MoveLibraryFilesMutationBody = LibraryFileMoveRequest
    export type MoveLibraryFilesMutationError = Error
    export type MoveLibraryFilesMutationVariables = {data: LibraryFileMoveRequest}

    /**
 * @summary Move selected library files of one host into a library folder
 */
export const useMoveLibraryFiles = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof moveLibraryFiles>>, TError,MoveLibraryFilesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof moveLibraryFiles>>,
        TError,
        MoveLibraryFilesMutationVariables,
        TContext
      > => {
      return useMutation(getMoveLibraryFilesMutationOptions(options));
    }

export const getReviewLibraryFileDeletionUrl = () => {




  return `/api/library/files/delete-review`
}

/**
 * Writes nothing. Returns the exact current version token that a confirmed delete must repeat, and any records that retain the file.
 * @summary Prepare a delete review for one library file
 */
export const reviewLibraryFileDeletion = async (libraryFileDeletionReviewRequest: LibraryFileDeletionReviewRequest, options?: Parameters<typeof customFetch>[1]): Promise<LibraryFileDeletionReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<LibraryFileDeletionReview>(getReviewLibraryFileDeletionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(libraryFileDeletionReviewRequest)
  }
);}





export const getReviewLibraryFileDeletionMutationKey = () => ['reviewLibraryFileDeletion'] as const;

export const getReviewLibraryFileDeletionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewLibraryFileDeletion>>, TError,ReviewLibraryFileDeletionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewLibraryFileDeletion>>, TError,ReviewLibraryFileDeletionMutationVariables, TContext> => {

const mutationKey = getReviewLibraryFileDeletionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewLibraryFileDeletion>>, ReviewLibraryFileDeletionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reviewLibraryFileDeletion(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewLibraryFileDeletionMutationResult = NonNullable<Awaited<ReturnType<typeof reviewLibraryFileDeletion>>>
    export type ReviewLibraryFileDeletionMutationBody = LibraryFileDeletionReviewRequest
    export type ReviewLibraryFileDeletionMutationError = Error
    export type ReviewLibraryFileDeletionMutationVariables = {data: LibraryFileDeletionReviewRequest}

    /**
 * @summary Prepare a delete review for one library file
 */
export const useReviewLibraryFileDeletion = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewLibraryFileDeletion>>, TError,ReviewLibraryFileDeletionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewLibraryFileDeletion>>,
        TError,
        ReviewLibraryFileDeletionMutationVariables,
        TContext
      > => {
      return useMutation(getReviewLibraryFileDeletionMutationOptions(options));
    }

export const getConfirmLibraryFileDeletionUrl = () => {




  return `/api/library/files/delete`
}

/**
 * Requires entity.records.write and the reviewed version token. A changed file returns 409. Idempotent by requestId.
 * @summary Delete one reviewed library file
 */
export const confirmLibraryFileDeletion = async (libraryFileDeletionRequest: LibraryFileDeletionRequest, options?: Parameters<typeof customFetch>[1]): Promise<LibraryFileDeletionOutcome> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<LibraryFileDeletionOutcome>(getConfirmLibraryFileDeletionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(libraryFileDeletionRequest)
  }
);}





export const getConfirmLibraryFileDeletionMutationKey = () => ['confirmLibraryFileDeletion'] as const;

export const getConfirmLibraryFileDeletionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmLibraryFileDeletion>>, TError,ConfirmLibraryFileDeletionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmLibraryFileDeletion>>, TError,ConfirmLibraryFileDeletionMutationVariables, TContext> => {

const mutationKey = getConfirmLibraryFileDeletionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmLibraryFileDeletion>>, ConfirmLibraryFileDeletionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  confirmLibraryFileDeletion(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmLibraryFileDeletionMutationResult = NonNullable<Awaited<ReturnType<typeof confirmLibraryFileDeletion>>>
    export type ConfirmLibraryFileDeletionMutationBody = LibraryFileDeletionRequest
    export type ConfirmLibraryFileDeletionMutationError = Error
    export type ConfirmLibraryFileDeletionMutationVariables = {data: LibraryFileDeletionRequest}

    /**
 * @summary Delete one reviewed library file
 */
export const useConfirmLibraryFileDeletion = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmLibraryFileDeletion>>, TError,ConfirmLibraryFileDeletionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmLibraryFileDeletion>>,
        TError,
        ConfirmLibraryFileDeletionMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmLibraryFileDeletionMutationOptions(options));
    }

export const getListLibraryFileVersionsUrl = (hostKind: FileHostKind,
    hostId: string,
    fileKind: LibraryFileKind,
    fileId: string,
    params?: ListLibraryFileVersionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/library/hosts/${hostKind}/${hostId}/files/${fileKind}/${fileId}/versions?${stringifiedParams}` : `/api/library/hosts/${hostKind}/${hostId}/files/${fileKind}/${fileId}/versions`
}

/**
 * Requires entity.records.read and host ownership of the file. A file without saved versions reports its original as v1.0.
 * @summary Page the version history of a library file
 */
export const listLibraryFileVersions = async (hostKind: FileHostKind,
    hostId: string,
    fileKind: LibraryFileKind,
    fileId: string,
    params?: ListLibraryFileVersionsParams, options?: Parameters<typeof customFetch>[1]): Promise<LibraryFileVersionPage> => {

  return customFetch<LibraryFileVersionPage>(getListLibraryFileVersionsUrl(hostKind,hostId,fileKind,fileId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListLibraryFileVersionsQueryKey = (hostKind: FileHostKind,
    hostId: string,
    fileKind: LibraryFileKind,
    fileId: string,
    params?: ListLibraryFileVersionsParams,) => {
    return [
    `/api/library/hosts/${hostKind}/${hostId}/files/${fileKind}/${fileId}/versions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListLibraryFileVersionsQueryOptions = <TData = Awaited<ReturnType<typeof listLibraryFileVersions>>, TError = Error>(hostKind: FileHostKind,
    hostId: string,
    fileKind: LibraryFileKind,
    fileId: string,
    params?: ListLibraryFileVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listLibraryFileVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListLibraryFileVersionsQueryKey(hostKind,hostId,fileKind,fileId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listLibraryFileVersions>>> = ({ signal }) => listLibraryFileVersions(hostKind,hostId,fileKind,fileId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: hostKind !== null && hostKind !== undefined && hostId !== null && hostId !== undefined && fileKind !== null && fileKind !== undefined && fileId !== null && fileId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listLibraryFileVersions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListLibraryFileVersionsQueryResult = NonNullable<Awaited<ReturnType<typeof listLibraryFileVersions>>>
export type ListLibraryFileVersionsQueryError = Error


/**
 * @summary Page the version history of a library file
 */

export function useListLibraryFileVersions<TData = Awaited<ReturnType<typeof listLibraryFileVersions>>, TError = Error>(
 hostKind: FileHostKind,
    hostId: string,
    fileKind: LibraryFileKind,
    fileId: string,
    params?: ListLibraryFileVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listLibraryFileVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListLibraryFileVersionsQueryOptions(hostKind,hostId,fileKind,fileId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUploadLibraryFileVersionUrl = (hostKind: FileHostKind,
    hostId: string,
    fileKind: LibraryFileKind,
    fileId: string,
    params: UploadLibraryFileVersionParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/library/hosts/${hostKind}/${hostId}/files/${fileKind}/${fileId}/versions?${stringifiedParams}` : `/api/library/hosts/${hostKind}/${hostId}/files/${fileKind}/${fileId}/versions`
}

/**
 * Requires entity.records.write. The current version must equal expectedCurrentVersionId (omit while the file is still the v1.0 original). Files retained by other records keep their native history. Idempotent by requestId.
 * @summary Upload a new major or minor version of an uploaded library file
 */
export const uploadLibraryFileVersion = async (hostKind: FileHostKind,
    hostId: string,
    fileKind: LibraryFileKind,
    fileId: string,
    uploadLibraryFileVersionBody: UploadLibraryFileVersionBody,
    params: UploadLibraryFileVersionParams, options?: Parameters<typeof customFetch>[1]): Promise<LibraryFileVersion> => {
    const formData = new FormData();
formData.append(`file`, uploadLibraryFileVersionBody.file);

  return customFetch<LibraryFileVersion>(getUploadLibraryFileVersionUrl(hostKind,hostId,fileKind,fileId,params),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}





export const getUploadLibraryFileVersionMutationKey = () => ['uploadLibraryFileVersion'] as const;

export const getUploadLibraryFileVersionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadLibraryFileVersion>>, TError,UploadLibraryFileVersionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadLibraryFileVersion>>, TError,UploadLibraryFileVersionMutationVariables, TContext> => {

const mutationKey = getUploadLibraryFileVersionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadLibraryFileVersion>>, UploadLibraryFileVersionMutationVariables> = (props) => {
          const {hostKind,hostId,fileKind,fileId,data,params} = props ?? {};

          return  uploadLibraryFileVersion(hostKind,hostId,fileKind,fileId,data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadLibraryFileVersionMutationResult = NonNullable<Awaited<ReturnType<typeof uploadLibraryFileVersion>>>
    export type UploadLibraryFileVersionMutationBody = UploadLibraryFileVersionBody
    export type UploadLibraryFileVersionMutationError = Error
    export type UploadLibraryFileVersionMutationVariables = {hostKind: FileHostKind;hostId: string;fileKind: LibraryFileKind;fileId: string;data: UploadLibraryFileVersionBody;params: UploadLibraryFileVersionParams}

    /**
 * @summary Upload a new major or minor version of an uploaded library file
 */
export const useUploadLibraryFileVersion = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadLibraryFileVersion>>, TError,UploadLibraryFileVersionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadLibraryFileVersion>>,
        TError,
        UploadLibraryFileVersionMutationVariables,
        TContext
      > => {
      return useMutation(getUploadLibraryFileVersionMutationOptions(options));
    }

export const getDownloadLibraryFileVersionUrl = (hostKind: FileHostKind,
    hostId: string,
    fileKind: LibraryFileKind,
    fileId: string,
    versionId: string,) => {




  return `/api/library/hosts/${hostKind}/${hostId}/files/${fileKind}/${fileId}/versions/${versionId}/content`
}

/**
 * @summary Download the exact bytes of one saved version
 */
export const downloadLibraryFileVersion = async (hostKind: FileHostKind,
    hostId: string,
    fileKind: LibraryFileKind,
    fileId: string,
    versionId: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadLibraryFileVersionUrl(hostKind,hostId,fileKind,fileId,versionId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadLibraryFileVersionQueryKey = (hostKind: FileHostKind,
    hostId: string,
    fileKind: LibraryFileKind,
    fileId: string,
    versionId: string,) => {
    return [
    `/api/library/hosts/${hostKind}/${hostId}/files/${fileKind}/${fileId}/versions/${versionId}/content`
    ] as const;
    }


export const getDownloadLibraryFileVersionQueryOptions = <TData = Awaited<ReturnType<typeof downloadLibraryFileVersion>>, TError = Error>(hostKind: FileHostKind,
    hostId: string,
    fileKind: LibraryFileKind,
    fileId: string,
    versionId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadLibraryFileVersion>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadLibraryFileVersionQueryKey(hostKind,hostId,fileKind,fileId,versionId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadLibraryFileVersion>>> = ({ signal }) => downloadLibraryFileVersion(hostKind,hostId,fileKind,fileId,versionId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: hostKind !== null && hostKind !== undefined && hostId !== null && hostId !== undefined && fileKind !== null && fileKind !== undefined && fileId !== null && fileId !== undefined && versionId !== null && versionId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadLibraryFileVersion>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadLibraryFileVersionQueryResult = NonNullable<Awaited<ReturnType<typeof downloadLibraryFileVersion>>>
export type DownloadLibraryFileVersionQueryError = Error


/**
 * @summary Download the exact bytes of one saved version
 */

export function useDownloadLibraryFileVersion<TData = Awaited<ReturnType<typeof downloadLibraryFileVersion>>, TError = Error>(
 hostKind: FileHostKind,
    hostId: string,
    fileKind: LibraryFileKind,
    fileId: string,
    versionId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadLibraryFileVersion>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadLibraryFileVersionQueryOptions(hostKind,hostId,fileKind,fileId,versionId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewLibraryFileRestoreUrl = (hostKind: FileHostKind,
    hostId: string,
    fileKind: LibraryFileKind,
    fileId: string,
    versionId: string,) => {




  return `/api/library/hosts/${hostKind}/${hostId}/files/${fileKind}/${fileId}/versions/${versionId}/restore-review`
}

/**
 * Writes nothing. Restoring creates a new version; history is never rewritten.
 * @summary Review restoring a saved version, including an integrity check of its stored bytes
 */
export const reviewLibraryFileRestore = async (hostKind: FileHostKind,
    hostId: string,
    fileKind: LibraryFileKind,
    fileId: string,
    versionId: string, options?: Parameters<typeof customFetch>[1]): Promise<LibraryFileRestoreReview> => {

  return customFetch<LibraryFileRestoreReview>(getReviewLibraryFileRestoreUrl(hostKind,hostId,fileKind,fileId,versionId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getReviewLibraryFileRestoreMutationKey = () => ['reviewLibraryFileRestore'] as const;

export const getReviewLibraryFileRestoreMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewLibraryFileRestore>>, TError,ReviewLibraryFileRestoreMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewLibraryFileRestore>>, TError,ReviewLibraryFileRestoreMutationVariables, TContext> => {

const mutationKey = getReviewLibraryFileRestoreMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewLibraryFileRestore>>, ReviewLibraryFileRestoreMutationVariables> = (props) => {
          const {hostKind,hostId,fileKind,fileId,versionId} = props ?? {};

          return  reviewLibraryFileRestore(hostKind,hostId,fileKind,fileId,versionId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewLibraryFileRestoreMutationResult = NonNullable<Awaited<ReturnType<typeof reviewLibraryFileRestore>>>

    export type ReviewLibraryFileRestoreMutationError = Error
    export type ReviewLibraryFileRestoreMutationVariables = {hostKind: FileHostKind;hostId: string;fileKind: LibraryFileKind;fileId: string;versionId: string}

    /**
 * @summary Review restoring a saved version, including an integrity check of its stored bytes
 */
export const useReviewLibraryFileRestore = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewLibraryFileRestore>>, TError,ReviewLibraryFileRestoreMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewLibraryFileRestore>>,
        TError,
        ReviewLibraryFileRestoreMutationVariables,
        TContext
      > => {
      return useMutation(getReviewLibraryFileRestoreMutationOptions(options));
    }

export const getRestoreLibraryFileVersionUrl = (hostKind: FileHostKind,
    hostId: string,
    fileKind: LibraryFileKind,
    fileId: string,
    versionId: string,) => {




  return `/api/library/hosts/${hostKind}/${hostId}/files/${fileKind}/${fileId}/versions/${versionId}/restore`
}

/**
 * Requires entity.records.write, a matching expectedCurrentVersionId and verified stored bytes. Idempotent by requestId.
 * @summary Restore a saved version as a new current version
 */
export const restoreLibraryFileVersion = async (hostKind: FileHostKind,
    hostId: string,
    fileKind: LibraryFileKind,
    fileId: string,
    versionId: string,
    libraryFileRestoreRequest: LibraryFileRestoreRequest, options?: Parameters<typeof customFetch>[1]): Promise<LibraryFileVersion> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<LibraryFileVersion>(getRestoreLibraryFileVersionUrl(hostKind,hostId,fileKind,fileId,versionId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(libraryFileRestoreRequest)
  }
);}





export const getRestoreLibraryFileVersionMutationKey = () => ['restoreLibraryFileVersion'] as const;

export const getRestoreLibraryFileVersionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof restoreLibraryFileVersion>>, TError,RestoreLibraryFileVersionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof restoreLibraryFileVersion>>, TError,RestoreLibraryFileVersionMutationVariables, TContext> => {

const mutationKey = getRestoreLibraryFileVersionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof restoreLibraryFileVersion>>, RestoreLibraryFileVersionMutationVariables> = (props) => {
          const {hostKind,hostId,fileKind,fileId,versionId,data} = props ?? {};

          return  restoreLibraryFileVersion(hostKind,hostId,fileKind,fileId,versionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RestoreLibraryFileVersionMutationResult = NonNullable<Awaited<ReturnType<typeof restoreLibraryFileVersion>>>
    export type RestoreLibraryFileVersionMutationBody = LibraryFileRestoreRequest
    export type RestoreLibraryFileVersionMutationError = Error
    export type RestoreLibraryFileVersionMutationVariables = {hostKind: FileHostKind;hostId: string;fileKind: LibraryFileKind;fileId: string;versionId: string;data: LibraryFileRestoreRequest}

    /**
 * @summary Restore a saved version as a new current version
 */
export const useRestoreLibraryFileVersion = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof restoreLibraryFileVersion>>, TError,RestoreLibraryFileVersionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof restoreLibraryFileVersion>>,
        TError,
        RestoreLibraryFileVersionMutationVariables,
        TContext
      > => {
      return useMutation(getRestoreLibraryFileVersionMutationOptions(options));
    }

export const getListFileShareLinksUrl = (params: ListFileShareLinksParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/file-share-links?${stringifiedParams}` : `/api/file-share-links`
}

/**
 * Requires share permission on the host. Tokens are never returned.
 * @summary Page share links of a host, optionally for one file
 */
export const listFileShareLinks = async (params: ListFileShareLinksParams, options?: Parameters<typeof customFetch>[1]): Promise<FileShareLinkPage> => {

  return customFetch<FileShareLinkPage>(getListFileShareLinksUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListFileShareLinksQueryKey = (params?: ListFileShareLinksParams,) => {
    return [
    `/api/file-share-links`, ...(params ? [params] : [])
    ] as const;
    }


export const getListFileShareLinksQueryOptions = <TData = Awaited<ReturnType<typeof listFileShareLinks>>, TError = Error>(params: ListFileShareLinksParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listFileShareLinks>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListFileShareLinksQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listFileShareLinks>>> = ({ signal }) => listFileShareLinks(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listFileShareLinks>>, TError, TData> & { queryKey: QueryKey }
}

export type ListFileShareLinksQueryResult = NonNullable<Awaited<ReturnType<typeof listFileShareLinks>>>
export type ListFileShareLinksQueryError = Error


/**
 * @summary Page share links of a host, optionally for one file
 */

export function useListFileShareLinks<TData = Awaited<ReturnType<typeof listFileShareLinks>>, TError = Error>(
 params: ListFileShareLinksParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listFileShareLinks>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListFileShareLinksQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateFileShareLinkUrl = () => {




  return `/api/file-share-links`
}

/**
 * Requires share permission on the host. A signed-in link (default) only opens for colleagues who pass the host's permission check. An external link opens without signing in and must satisfy the tenant's outside-sharing policy (enabled, maximum expiry, password requirement); use createProtectedFileShareLink to add a password. Expiry is required (1-90 days). The token is returned once; a requestId replay returns the link without its token.
 * @summary Create a share link to a library file
 */
export const createFileShareLink = async (fileShareLinkCreate: FileShareLinkCreate, options?: Parameters<typeof customFetch>[1]): Promise<FileShareLinkCreated> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<FileShareLinkCreated>(getCreateFileShareLinkUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(fileShareLinkCreate)
  }
);}





export const getCreateFileShareLinkMutationKey = () => ['createFileShareLink'] as const;

export const getCreateFileShareLinkMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createFileShareLink>>, TError,CreateFileShareLinkMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createFileShareLink>>, TError,CreateFileShareLinkMutationVariables, TContext> => {

const mutationKey = getCreateFileShareLinkMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createFileShareLink>>, CreateFileShareLinkMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createFileShareLink(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateFileShareLinkMutationResult = NonNullable<Awaited<ReturnType<typeof createFileShareLink>>>
    export type CreateFileShareLinkMutationBody = FileShareLinkCreate
    export type CreateFileShareLinkMutationError = Error
    export type CreateFileShareLinkMutationVariables = {data: FileShareLinkCreate}

    /**
 * @summary Create a share link to a library file
 */
export const useCreateFileShareLink = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createFileShareLink>>, TError,CreateFileShareLinkMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createFileShareLink>>,
        TError,
        CreateFileShareLinkMutationVariables,
        TContext
      > => {
      return useMutation(getCreateFileShareLinkMutationOptions(options));
    }

export const getCreateProtectedFileShareLinkUrl = () => {




  return `/api/file-share-links/protected`
}

/**
 * New share links never carry passwords (Solution Studio sharing). Always returns 410 SHARE_PASSWORD_RETIRED. Existing password links stay readable until they expire or are revoked.
 * @deprecated
 * @summary Retired - password-protected share links
 */
export const createProtectedFileShareLink = async (fileShareLinkProtectedCreate: FileShareLinkProtectedCreate, options?: Parameters<typeof customFetch>[1]): Promise<FileShareLinkCreated> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<FileShareLinkCreated>(getCreateProtectedFileShareLinkUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(fileShareLinkProtectedCreate)
  }
);}





export const getCreateProtectedFileShareLinkMutationKey = () => ['createProtectedFileShareLink'] as const;

export const getCreateProtectedFileShareLinkMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createProtectedFileShareLink>>, TError,CreateProtectedFileShareLinkMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createProtectedFileShareLink>>, TError,CreateProtectedFileShareLinkMutationVariables, TContext> => {

const mutationKey = getCreateProtectedFileShareLinkMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createProtectedFileShareLink>>, CreateProtectedFileShareLinkMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createProtectedFileShareLink(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateProtectedFileShareLinkMutationResult = NonNullable<Awaited<ReturnType<typeof createProtectedFileShareLink>>>
    export type CreateProtectedFileShareLinkMutationBody = FileShareLinkProtectedCreate
    export type CreateProtectedFileShareLinkMutationError = Error
    export type CreateProtectedFileShareLinkMutationVariables = {data: FileShareLinkProtectedCreate}

    /**
 * @deprecated
 * @summary Retired - password-protected share links
 */
export const useCreateProtectedFileShareLink = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createProtectedFileShareLink>>, TError,CreateProtectedFileShareLinkMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createProtectedFileShareLink>>,
        TError,
        CreateProtectedFileShareLinkMutationVariables,
        TContext
      > => {
      return useMutation(getCreateProtectedFileShareLinkMutationOptions(options));
    }

export const getRevokeFileShareLinkUrl = (linkId: string,) => {




  return `/api/file-share-links/${linkId}/revoke`
}

/**
 * Allowed for the link's creator or anyone with share permission on its host. Revoked links return 410 when opened.
 * @summary Withdraw a share link
 */
export const revokeFileShareLink = async (linkId: string,
    fileShareLinkRevoke: FileShareLinkRevoke, options?: Parameters<typeof customFetch>[1]): Promise<FileShareLink> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<FileShareLink>(getRevokeFileShareLinkUrl(linkId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(fileShareLinkRevoke)
  }
);}





export const getRevokeFileShareLinkMutationKey = () => ['revokeFileShareLink'] as const;

export const getRevokeFileShareLinkMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeFileShareLink>>, TError,RevokeFileShareLinkMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokeFileShareLink>>, TError,RevokeFileShareLinkMutationVariables, TContext> => {

const mutationKey = getRevokeFileShareLinkMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokeFileShareLink>>, RevokeFileShareLinkMutationVariables> = (props) => {
          const {linkId,data} = props ?? {};

          return  revokeFileShareLink(linkId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokeFileShareLinkMutationResult = NonNullable<Awaited<ReturnType<typeof revokeFileShareLink>>>
    export type RevokeFileShareLinkMutationBody = FileShareLinkRevoke
    export type RevokeFileShareLinkMutationError = Error
    export type RevokeFileShareLinkMutationVariables = {linkId: string;data: FileShareLinkRevoke}

    /**
 * @summary Withdraw a share link
 */
export const useRevokeFileShareLink = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeFileShareLink>>, TError,RevokeFileShareLinkMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof revokeFileShareLink>>,
        TError,
        RevokeFileShareLinkMutationVariables,
        TContext
      > => {
      return useMutation(getRevokeFileShareLinkMutationOptions(options));
    }

export const getResolveFileShareLinkUrl = () => {




  return `/api/file-share-links/open`
}

/**
 * Requires sign-in. The caller must pass the host's current read permission; unknown tokens and denied callers both return 404. Expired or revoked links return 410. Every attempt is audited without the token.
 * @summary Open a share link as the signed-in caller
 */
export const resolveFileShareLink = async (fileShareLinkOpen: FileShareLinkOpen, options?: Parameters<typeof customFetch>[1]): Promise<SharedFile> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SharedFile>(getResolveFileShareLinkUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(fileShareLinkOpen)
  }
);}





export const getResolveFileShareLinkMutationKey = () => ['resolveFileShareLink'] as const;

export const getResolveFileShareLinkMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resolveFileShareLink>>, TError,ResolveFileShareLinkMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof resolveFileShareLink>>, TError,ResolveFileShareLinkMutationVariables, TContext> => {

const mutationKey = getResolveFileShareLinkMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof resolveFileShareLink>>, ResolveFileShareLinkMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  resolveFileShareLink(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResolveFileShareLinkMutationResult = NonNullable<Awaited<ReturnType<typeof resolveFileShareLink>>>
    export type ResolveFileShareLinkMutationBody = FileShareLinkOpen
    export type ResolveFileShareLinkMutationError = Error
    export type ResolveFileShareLinkMutationVariables = {data: FileShareLinkOpen}

    /**
 * @summary Open a share link as the signed-in caller
 */
export const useResolveFileShareLink = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resolveFileShareLink>>, TError,ResolveFileShareLinkMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof resolveFileShareLink>>,
        TError,
        ResolveFileShareLinkMutationVariables,
        TContext
      > => {
      return useMutation(getResolveFileShareLinkMutationOptions(options));
    }

export const getDownloadSharedFileUrl = () => {




  return `/api/file-share-links/open/content`
}

/**
 * Same checks as resolveFileShareLink. Serves the pinned version when the link has one.
 * @summary Download the file behind a share link as the signed-in caller
 */
export const downloadSharedFile = async (fileShareLinkOpen: FileShareLinkOpen, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Blob>(getDownloadSharedFileUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(fileShareLinkOpen)
  }
);}





export const getDownloadSharedFileMutationKey = () => ['downloadSharedFile'] as const;

export const getDownloadSharedFileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof downloadSharedFile>>, TError,DownloadSharedFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof downloadSharedFile>>, TError,DownloadSharedFileMutationVariables, TContext> => {

const mutationKey = getDownloadSharedFileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof downloadSharedFile>>, DownloadSharedFileMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  downloadSharedFile(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DownloadSharedFileMutationResult = NonNullable<Awaited<ReturnType<typeof downloadSharedFile>>>
    export type DownloadSharedFileMutationBody = FileShareLinkOpen
    export type DownloadSharedFileMutationError = Error
    export type DownloadSharedFileMutationVariables = {data: FileShareLinkOpen}

    /**
 * @summary Download the file behind a share link as the signed-in caller
 */
export const useDownloadSharedFile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof downloadSharedFile>>, TError,DownloadSharedFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof downloadSharedFile>>,
        TError,
        DownloadSharedFileMutationVariables,
        TContext
      > => {
      return useMutation(getDownloadSharedFileMutationOptions(options));
    }

export const getListRecycleBinUrl = (params?: ListRecycleBinParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/library/recycle-bin?${stringifiedParams}` : `/api/library/recycle-bin`
}

/**
 * Requires entity.records.read. Items stay restorable until their retention ends, then are purged automatically.
 * @summary Page deleted files, folders and documents that can still be restored
 */
export const listRecycleBin = async (params?: ListRecycleBinParams, options?: Parameters<typeof customFetch>[1]): Promise<RecycleBinPage> => {

  return customFetch<RecycleBinPage>(getListRecycleBinUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListRecycleBinQueryKey = (params?: ListRecycleBinParams,) => {
    return [
    `/api/library/recycle-bin`, ...(params ? [params] : [])
    ] as const;
    }


export const getListRecycleBinQueryOptions = <TData = Awaited<ReturnType<typeof listRecycleBin>>, TError = unknown>(params?: ListRecycleBinParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRecycleBin>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListRecycleBinQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listRecycleBin>>> = ({ signal }) => listRecycleBin(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listRecycleBin>>, TError, TData> & { queryKey: QueryKey }
}

export type ListRecycleBinQueryResult = NonNullable<Awaited<ReturnType<typeof listRecycleBin>>>
export type ListRecycleBinQueryError = unknown


/**
 * @summary Page deleted files, folders and documents that can still be restored
 */

export function useListRecycleBin<TData = Awaited<ReturnType<typeof listRecycleBin>>, TError = unknown>(
 params?: ListRecycleBinParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRecycleBin>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListRecycleBinQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getMoveToRecycleBinUrl = () => {




  return `/api/library/recycle-bin/delete`
}

/**
 * Requires entity.records.write. Nothing is destroyed; each item is restorable until its retention ends. Returns a result per item. Idempotent by requestId.
 * @summary Delete files, folders or documents to the recycle bin
 */
export const moveToRecycleBin = async (recycleBinDeleteRequest: RecycleBinDeleteRequest, options?: Parameters<typeof customFetch>[1]): Promise<RecycleBinDeleteOutcome> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<RecycleBinDeleteOutcome>(getMoveToRecycleBinUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(recycleBinDeleteRequest)
  }
);}





export const getMoveToRecycleBinMutationKey = () => ['moveToRecycleBin'] as const;

export const getMoveToRecycleBinMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof moveToRecycleBin>>, TError,MoveToRecycleBinMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof moveToRecycleBin>>, TError,MoveToRecycleBinMutationVariables, TContext> => {

const mutationKey = getMoveToRecycleBinMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof moveToRecycleBin>>, MoveToRecycleBinMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  moveToRecycleBin(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MoveToRecycleBinMutationResult = NonNullable<Awaited<ReturnType<typeof moveToRecycleBin>>>
    export type MoveToRecycleBinMutationBody = RecycleBinDeleteRequest
    export type MoveToRecycleBinMutationError = Error
    export type MoveToRecycleBinMutationVariables = {data: RecycleBinDeleteRequest}

    /**
 * @summary Delete files, folders or documents to the recycle bin
 */
export const useMoveToRecycleBin = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof moveToRecycleBin>>, TError,MoveToRecycleBinMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof moveToRecycleBin>>,
        TError,
        MoveToRecycleBinMutationVariables,
        TContext
      > => {
      return useMutation(getMoveToRecycleBinMutationOptions(options));
    }

export const getRestoreRecycleBinItemsUrl = () => {




  return `/api/library/recycle-bin/restore`
}

/**
 * Requires entity.records.write. Expired items cannot be restored. Returns a result per item. Idempotent by requestId.
 * @summary Restore deleted items to their original folders
 */
export const restoreRecycleBinItems = async (recycleBinRestoreRequest: RecycleBinRestoreRequest, options?: Parameters<typeof customFetch>[1]): Promise<RecycleBinRestoreOutcome> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<RecycleBinRestoreOutcome>(getRestoreRecycleBinItemsUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(recycleBinRestoreRequest)
  }
);}





export const getRestoreRecycleBinItemsMutationKey = () => ['restoreRecycleBinItems'] as const;

export const getRestoreRecycleBinItemsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof restoreRecycleBinItems>>, TError,RestoreRecycleBinItemsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof restoreRecycleBinItems>>, TError,RestoreRecycleBinItemsMutationVariables, TContext> => {

const mutationKey = getRestoreRecycleBinItemsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof restoreRecycleBinItems>>, RestoreRecycleBinItemsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  restoreRecycleBinItems(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RestoreRecycleBinItemsMutationResult = NonNullable<Awaited<ReturnType<typeof restoreRecycleBinItems>>>
    export type RestoreRecycleBinItemsMutationBody = RecycleBinRestoreRequest
    export type RestoreRecycleBinItemsMutationError = unknown
    export type RestoreRecycleBinItemsMutationVariables = {data: RecycleBinRestoreRequest}

    /**
 * @summary Restore deleted items to their original folders
 */
export const useRestoreRecycleBinItems = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof restoreRecycleBinItems>>, TError,RestoreRecycleBinItemsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof restoreRecycleBinItems>>,
        TError,
        RestoreRecycleBinItemsMutationVariables,
        TContext
      > => {
      return useMutation(getRestoreRecycleBinItemsMutationOptions(options));
    }

export const getPurgeRecycleBinItemUrl = (itemId: string,) => {




  return `/api/library/recycle-bin/${itemId}/purge`
}

/**
 * Requires entity.manage and the item's exact name typed as confirmation. Stored bytes are removed by the clean-up worker after the records are gone. Idempotent by requestId.
 * @summary Permanently delete one recycle bin item
 */
export const purgeRecycleBinItem = async (itemId: string,
    recycleBinPurgeRequest: RecycleBinPurgeRequest, options?: Parameters<typeof customFetch>[1]): Promise<RecycleBinPurgeOutcome> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<RecycleBinPurgeOutcome>(getPurgeRecycleBinItemUrl(itemId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(recycleBinPurgeRequest)
  }
);}





export const getPurgeRecycleBinItemMutationKey = () => ['purgeRecycleBinItem'] as const;

export const getPurgeRecycleBinItemMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof purgeRecycleBinItem>>, TError,PurgeRecycleBinItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof purgeRecycleBinItem>>, TError,PurgeRecycleBinItemMutationVariables, TContext> => {

const mutationKey = getPurgeRecycleBinItemMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof purgeRecycleBinItem>>, PurgeRecycleBinItemMutationVariables> = (props) => {
          const {itemId,data} = props ?? {};

          return  purgeRecycleBinItem(itemId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PurgeRecycleBinItemMutationResult = NonNullable<Awaited<ReturnType<typeof purgeRecycleBinItem>>>
    export type PurgeRecycleBinItemMutationBody = RecycleBinPurgeRequest
    export type PurgeRecycleBinItemMutationError = Error
    export type PurgeRecycleBinItemMutationVariables = {itemId: string;data: RecycleBinPurgeRequest}

    /**
 * @summary Permanently delete one recycle bin item
 */
export const usePurgeRecycleBinItem = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof purgeRecycleBinItem>>, TError,PurgeRecycleBinItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof purgeRecycleBinItem>>,
        TError,
        PurgeRecycleBinItemMutationVariables,
        TContext
      > => {
      return useMutation(getPurgeRecycleBinItemMutationOptions(options));
    }

export const getGetFileManagementSettingsUrl = () => {




  return `/api/library/settings`
}

/**
 * Requires entity.records.read. Entities without saved settings keep deleted items for 90 days.
 * @summary Read the library's recycle bin settings
 */
export const getFileManagementSettings = async ( options?: Parameters<typeof customFetch>[1]): Promise<FileManagementSettings> => {

  return customFetch<FileManagementSettings>(getGetFileManagementSettingsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetFileManagementSettingsQueryKey = () => {
    return [
    `/api/library/settings`
    ] as const;
    }


export const getGetFileManagementSettingsQueryOptions = <TData = Awaited<ReturnType<typeof getFileManagementSettings>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getFileManagementSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetFileManagementSettingsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getFileManagementSettings>>> = ({ signal }) => getFileManagementSettings({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getFileManagementSettings>>, TError, TData> & { queryKey: QueryKey }
}

export type GetFileManagementSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof getFileManagementSettings>>>
export type GetFileManagementSettingsQueryError = unknown


/**
 * @summary Read the library's recycle bin settings
 */

export function useGetFileManagementSettings<TData = Awaited<ReturnType<typeof getFileManagementSettings>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getFileManagementSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetFileManagementSettingsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateRecycleRetentionUrl = () => {




  return `/api/library/settings/retention`
}

/**
 * Requires entity.manage. Applies to items deleted from now on. A stale expectedRevision returns 409. Idempotent by requestId.
 * @summary Change how long deleted items stay restorable
 */
export const updateRecycleRetention = async (recycleRetentionUpdate: RecycleRetentionUpdate, options?: Parameters<typeof customFetch>[1]): Promise<FileManagementSettings> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<FileManagementSettings>(getUpdateRecycleRetentionUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(recycleRetentionUpdate)
  }
);}





export const getUpdateRecycleRetentionMutationKey = () => ['updateRecycleRetention'] as const;

export const getUpdateRecycleRetentionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateRecycleRetention>>, TError,UpdateRecycleRetentionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateRecycleRetention>>, TError,UpdateRecycleRetentionMutationVariables, TContext> => {

const mutationKey = getUpdateRecycleRetentionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateRecycleRetention>>, UpdateRecycleRetentionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateRecycleRetention(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateRecycleRetentionMutationResult = NonNullable<Awaited<ReturnType<typeof updateRecycleRetention>>>
    export type UpdateRecycleRetentionMutationBody = RecycleRetentionUpdate
    export type UpdateRecycleRetentionMutationError = Error
    export type UpdateRecycleRetentionMutationVariables = {data: RecycleRetentionUpdate}

    /**
 * @summary Change how long deleted items stay restorable
 */
export const useUpdateRecycleRetention = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateRecycleRetention>>, TError,UpdateRecycleRetentionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateRecycleRetention>>,
        TError,
        UpdateRecycleRetentionMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateRecycleRetentionMutationOptions(options));
    }

export const getGetFileSharingPolicyUrl = () => {




  return `/api/library/sharing-policy`
}

/**
 * Requires entity.records.read. Tenants without saved rules allow outside links for up to 90 days without a required password.
 * @summary Read the tenant's outside-sharing rules
 */
export const getFileSharingPolicy = async ( options?: Parameters<typeof customFetch>[1]): Promise<FileSharingPolicy> => {

  return customFetch<FileSharingPolicy>(getGetFileSharingPolicyUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetFileSharingPolicyQueryKey = () => {
    return [
    `/api/library/sharing-policy`
    ] as const;
    }


export const getGetFileSharingPolicyQueryOptions = <TData = Awaited<ReturnType<typeof getFileSharingPolicy>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getFileSharingPolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetFileSharingPolicyQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getFileSharingPolicy>>> = ({ signal }) => getFileSharingPolicy({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getFileSharingPolicy>>, TError, TData> & { queryKey: QueryKey }
}

export type GetFileSharingPolicyQueryResult = NonNullable<Awaited<ReturnType<typeof getFileSharingPolicy>>>
export type GetFileSharingPolicyQueryError = unknown


/**
 * @summary Read the tenant's outside-sharing rules
 */

export function useGetFileSharingPolicy<TData = Awaited<ReturnType<typeof getFileSharingPolicy>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getFileSharingPolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetFileSharingPolicyQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateFileSharingPolicyUrl = () => {




  return `/api/library/sharing-policy`
}

/**
 * Requires tenant.settings.manage. Tightened rules also stop existing outside links that no longer comply. A stale expectedRevision returns 409. Idempotent by requestId.
 * @summary Change the tenant's outside-sharing rules
 */
export const updateFileSharingPolicy = async (fileSharingPolicyUpdate: FileSharingPolicyUpdate, options?: Parameters<typeof customFetch>[1]): Promise<FileSharingPolicy> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<FileSharingPolicy>(getUpdateFileSharingPolicyUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(fileSharingPolicyUpdate)
  }
);}





export const getUpdateFileSharingPolicyMutationKey = () => ['updateFileSharingPolicy'] as const;

export const getUpdateFileSharingPolicyMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateFileSharingPolicy>>, TError,UpdateFileSharingPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateFileSharingPolicy>>, TError,UpdateFileSharingPolicyMutationVariables, TContext> => {

const mutationKey = getUpdateFileSharingPolicyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateFileSharingPolicy>>, UpdateFileSharingPolicyMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateFileSharingPolicy(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateFileSharingPolicyMutationResult = NonNullable<Awaited<ReturnType<typeof updateFileSharingPolicy>>>
    export type UpdateFileSharingPolicyMutationBody = FileSharingPolicyUpdate
    export type UpdateFileSharingPolicyMutationError = Error
    export type UpdateFileSharingPolicyMutationVariables = {data: FileSharingPolicyUpdate}

    /**
 * @summary Change the tenant's outside-sharing rules
 */
export const useUpdateFileSharingPolicy = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateFileSharingPolicy>>, TError,UpdateFileSharingPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateFileSharingPolicy>>,
        TError,
        UpdateFileSharingPolicyMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateFileSharingPolicyMutationOptions(options));
    }

export const getListLibraryFoldersUrl = (params?: ListLibraryFoldersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/library/folders?${stringifiedParams}` : `/api/library/folders`
}

/**
 * Requires entity.records.read. Includes empty folders and folders implied by visible files and documents. `search` is a literal, case-insensitive match on folder names. Returns the breadcrumb trail of `parent`.
 * @summary Page the folders directly inside a library folder
 */
export const listLibraryFolders = async (params?: ListLibraryFoldersParams, options?: Parameters<typeof customFetch>[1]): Promise<LibraryFolderPage> => {

  return customFetch<LibraryFolderPage>(getListLibraryFoldersUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListLibraryFoldersQueryKey = (params?: ListLibraryFoldersParams,) => {
    return [
    `/api/library/folders`, ...(params ? [params] : [])
    ] as const;
    }


export const getListLibraryFoldersQueryOptions = <TData = Awaited<ReturnType<typeof listLibraryFolders>>, TError = unknown>(params?: ListLibraryFoldersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listLibraryFolders>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListLibraryFoldersQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listLibraryFolders>>> = ({ signal }) => listLibraryFolders(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listLibraryFolders>>, TError, TData> & { queryKey: QueryKey }
}

export type ListLibraryFoldersQueryResult = NonNullable<Awaited<ReturnType<typeof listLibraryFolders>>>
export type ListLibraryFoldersQueryError = unknown


/**
 * @summary Page the folders directly inside a library folder
 */

export function useListLibraryFolders<TData = Awaited<ReturnType<typeof listLibraryFolders>>, TError = unknown>(
 params?: ListLibraryFoldersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listLibraryFolders>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListLibraryFoldersQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateLibraryFolderUrl = () => {




  return `/api/library/folders`
}

/**
 * Requires entity.records.write on the entity library. Missing parent folders are created too. Idempotent by requestId.
 * @summary Create a library folder
 */
export const createLibraryFolder = async (libraryFolderCreate: LibraryFolderCreate, options?: Parameters<typeof customFetch>[1]): Promise<LibraryFolderCreateOutcome> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<LibraryFolderCreateOutcome>(getCreateLibraryFolderUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(libraryFolderCreate)
  }
);}





export const getCreateLibraryFolderMutationKey = () => ['createLibraryFolder'] as const;

export const getCreateLibraryFolderMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createLibraryFolder>>, TError,CreateLibraryFolderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createLibraryFolder>>, TError,CreateLibraryFolderMutationVariables, TContext> => {

const mutationKey = getCreateLibraryFolderMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createLibraryFolder>>, CreateLibraryFolderMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createLibraryFolder(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateLibraryFolderMutationResult = NonNullable<Awaited<ReturnType<typeof createLibraryFolder>>>
    export type CreateLibraryFolderMutationBody = LibraryFolderCreate
    export type CreateLibraryFolderMutationError = Error
    export type CreateLibraryFolderMutationVariables = {data: LibraryFolderCreate}

    /**
 * @summary Create a library folder
 */
export const useCreateLibraryFolder = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createLibraryFolder>>, TError,CreateLibraryFolderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createLibraryFolder>>,
        TError,
        CreateLibraryFolderMutationVariables,
        TContext
      > => {
      return useMutation(getCreateLibraryFolderMutationOptions(options));
    }

export const getRenameLibraryFolderUrl = () => {




  return `/api/library/folders/rename`
}

/**
 * Requires entity.records.write on the entity library. Folder ancestry is immutable, so newPath must keep the same parent (400 FOLDER_ANCESTRY_IMMUTABLE). Refused when a sibling of any case already has the name or the folder holds native Word files. Idempotent by requestId.
 * @summary Rename a folder in place with everything inside it
 */
export const renameLibraryFolder = async (libraryFolderRename: LibraryFolderRename, options?: Parameters<typeof customFetch>[1]): Promise<LibraryFolderRenameOutcome> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<LibraryFolderRenameOutcome>(getRenameLibraryFolderUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(libraryFolderRename)
  }
);}





export const getRenameLibraryFolderMutationKey = () => ['renameLibraryFolder'] as const;

export const getRenameLibraryFolderMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof renameLibraryFolder>>, TError,RenameLibraryFolderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof renameLibraryFolder>>, TError,RenameLibraryFolderMutationVariables, TContext> => {

const mutationKey = getRenameLibraryFolderMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof renameLibraryFolder>>, RenameLibraryFolderMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  renameLibraryFolder(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RenameLibraryFolderMutationResult = NonNullable<Awaited<ReturnType<typeof renameLibraryFolder>>>
    export type RenameLibraryFolderMutationBody = LibraryFolderRename
    export type RenameLibraryFolderMutationError = Error
    export type RenameLibraryFolderMutationVariables = {data: LibraryFolderRename}

    /**
 * @summary Rename a folder in place with everything inside it
 */
export const useRenameLibraryFolder = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof renameLibraryFolder>>, TError,RenameLibraryFolderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof renameLibraryFolder>>,
        TError,
        RenameLibraryFolderMutationVariables,
        TContext
      > => {
      return useMutation(getRenameLibraryFolderMutationOptions(options));
    }

export const getCopyLibraryFilesUrl = () => {




  return `/api/library/files/copy`
}

/**
 * Requires read access to each file and entity.records.write on the entity library. Returns a result per file. Idempotent by requestId.
 * @summary Copy library files into an entity library folder as new uploads
 */
export const copyLibraryFiles = async (libraryFileCopyRequest: LibraryFileCopyRequest, options?: Parameters<typeof customFetch>[1]): Promise<LibraryFileCopyOutcome> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<LibraryFileCopyOutcome>(getCopyLibraryFilesUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(libraryFileCopyRequest)
  }
);}





export const getCopyLibraryFilesMutationKey = () => ['copyLibraryFiles'] as const;

export const getCopyLibraryFilesMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof copyLibraryFiles>>, TError,CopyLibraryFilesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof copyLibraryFiles>>, TError,CopyLibraryFilesMutationVariables, TContext> => {

const mutationKey = getCopyLibraryFilesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof copyLibraryFiles>>, CopyLibraryFilesMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  copyLibraryFiles(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CopyLibraryFilesMutationResult = NonNullable<Awaited<ReturnType<typeof copyLibraryFiles>>>
    export type CopyLibraryFilesMutationBody = LibraryFileCopyRequest
    export type CopyLibraryFilesMutationError = unknown
    export type CopyLibraryFilesMutationVariables = {data: LibraryFileCopyRequest}

    /**
 * @summary Copy library files into an entity library folder as new uploads
 */
export const useCopyLibraryFiles = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof copyLibraryFiles>>, TError,CopyLibraryFilesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof copyLibraryFiles>>,
        TError,
        CopyLibraryFilesMutationVariables,
        TContext
      > => {
      return useMutation(getCopyLibraryFilesMutationOptions(options));
    }

export const getListFileInventoryUrl = (params?: ListFileInventoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/library/inventory?${stringifiedParams}` : `/api/library/inventory`
}

/**
 * Requires entity.records.read. Files whose source permission the caller lacks and items in the recycle bin are excluded.
 * @summary Page every stored file and managed document the entity owns
 */
export const listFileInventory = async (params?: ListFileInventoryParams, options?: Parameters<typeof customFetch>[1]): Promise<FileInventoryPage> => {

  return customFetch<FileInventoryPage>(getListFileInventoryUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListFileInventoryQueryKey = (params?: ListFileInventoryParams,) => {
    return [
    `/api/library/inventory`, ...(params ? [params] : [])
    ] as const;
    }


export const getListFileInventoryQueryOptions = <TData = Awaited<ReturnType<typeof listFileInventory>>, TError = unknown>(params?: ListFileInventoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listFileInventory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListFileInventoryQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listFileInventory>>> = ({ signal }) => listFileInventory(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listFileInventory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListFileInventoryQueryResult = NonNullable<Awaited<ReturnType<typeof listFileInventory>>>
export type ListFileInventoryQueryError = unknown


/**
 * @summary Page every stored file and managed document the entity owns
 */

export function useListFileInventory<TData = Awaited<ReturnType<typeof listFileInventory>>, TError = unknown>(
 params?: ListFileInventoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listFileInventory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListFileInventoryQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListStorageCleanupUrl = (params?: ListStorageCleanupParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/library/storage-cleanup?${stringifiedParams}` : `/api/library/storage-cleanup`
}

/**
 * Requires entity.manage. Storage keys are never returned.
 * @summary Page stored-file clean-ups queued after failed uploads, deletes and purges
 */
export const listStorageCleanup = async (params?: ListStorageCleanupParams, options?: Parameters<typeof customFetch>[1]): Promise<StorageCleanupPage> => {

  return customFetch<StorageCleanupPage>(getListStorageCleanupUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListStorageCleanupQueryKey = (params?: ListStorageCleanupParams,) => {
    return [
    `/api/library/storage-cleanup`, ...(params ? [params] : [])
    ] as const;
    }


export const getListStorageCleanupQueryOptions = <TData = Awaited<ReturnType<typeof listStorageCleanup>>, TError = unknown>(params?: ListStorageCleanupParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listStorageCleanup>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListStorageCleanupQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listStorageCleanup>>> = ({ signal }) => listStorageCleanup(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listStorageCleanup>>, TError, TData> & { queryKey: QueryKey }
}

export type ListStorageCleanupQueryResult = NonNullable<Awaited<ReturnType<typeof listStorageCleanup>>>
export type ListStorageCleanupQueryError = unknown


/**
 * @summary Page stored-file clean-ups queued after failed uploads, deletes and purges
 */

export function useListStorageCleanup<TData = Awaited<ReturnType<typeof listStorageCleanup>>, TError = unknown>(
 params?: ListStorageCleanupParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listStorageCleanup>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListStorageCleanupQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRunStorageCleanupUrl = () => {




  return `/api/library/storage-cleanup/run`
}

/**
 * Requires entity.manage. Deletes queued stored bytes that no record references; referenced bytes are kept.
 * @summary Run one bounded storage clean-up pass now
 */
export const runStorageCleanup = async (storageCleanupRunRequest: StorageCleanupRunRequest, options?: Parameters<typeof customFetch>[1]): Promise<StorageCleanupRunResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<StorageCleanupRunResult>(getRunStorageCleanupUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(storageCleanupRunRequest)
  }
);}





export const getRunStorageCleanupMutationKey = () => ['runStorageCleanup'] as const;

export const getRunStorageCleanupMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runStorageCleanup>>, TError,RunStorageCleanupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof runStorageCleanup>>, TError,RunStorageCleanupMutationVariables, TContext> => {

const mutationKey = getRunStorageCleanupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof runStorageCleanup>>, RunStorageCleanupMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  runStorageCleanup(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RunStorageCleanupMutationResult = NonNullable<Awaited<ReturnType<typeof runStorageCleanup>>>
    export type RunStorageCleanupMutationBody = StorageCleanupRunRequest
    export type RunStorageCleanupMutationError = unknown
    export type RunStorageCleanupMutationVariables = {data: StorageCleanupRunRequest}

    /**
 * @summary Run one bounded storage clean-up pass now
 */
export const useRunStorageCleanup = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runStorageCleanup>>, TError,RunStorageCleanupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof runStorageCleanup>>,
        TError,
        RunStorageCleanupMutationVariables,
        TContext
      > => {
      return useMutation(getRunStorageCleanupMutationOptions(options));
    }

export const getGetPublicFileShareUrl = (token: string,) => {




  return `/api/public/file-shares/${token}`
}

/**
 * No sign-in. Rate limited per address. Unknown, expired, revoked and policy-blocked links all return the same 404. A password-protected link reveals only that a password is needed.
 * @summary Read an outside share link before downloading
 */
export const getPublicFileShare = async (token: string, options?: Parameters<typeof customFetch>[1]): Promise<PublicFileShare> => {

  return customFetch<PublicFileShare>(getGetPublicFileShareUrl(token),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPublicFileShareQueryKey = (token: string,) => {
    return [
    `/api/public/file-shares/${token}`
    ] as const;
    }


export const getGetPublicFileShareQueryOptions = <TData = Awaited<ReturnType<typeof getPublicFileShare>>, TError = Error>(token: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicFileShare>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPublicFileShareQueryKey(token);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPublicFileShare>>> = ({ signal }) => getPublicFileShare(token, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: token !== null && token !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPublicFileShare>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPublicFileShareQueryResult = NonNullable<Awaited<ReturnType<typeof getPublicFileShare>>>
export type GetPublicFileShareQueryError = Error


/**
 * @summary Read an outside share link before downloading
 */

export function useGetPublicFileShare<TData = Awaited<ReturnType<typeof getPublicFileShare>>, TError = Error>(
 token: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicFileShare>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPublicFileShareQueryOptions(token,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDownloadPublicFileShareUrl = (token: string,) => {




  return `/api/public/file-shares/${token}/content`
}

/**
 * No sign-in. Checks the password when one is set (failed attempts are rate limited), counts the open and records it without the token or password.
 * @summary Download the file behind an outside share link
 */
export const downloadPublicFileShare = async (token: string,
    publicFileShareDownload: PublicFileShareDownload, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Blob>(getDownloadPublicFileShareUrl(token),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(publicFileShareDownload)
  }
);}





export const getDownloadPublicFileShareMutationKey = () => ['downloadPublicFileShare'] as const;

export const getDownloadPublicFileShareMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof downloadPublicFileShare>>, TError,DownloadPublicFileShareMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof downloadPublicFileShare>>, TError,DownloadPublicFileShareMutationVariables, TContext> => {

const mutationKey = getDownloadPublicFileShareMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof downloadPublicFileShare>>, DownloadPublicFileShareMutationVariables> = (props) => {
          const {token,data} = props ?? {};

          return  downloadPublicFileShare(token,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DownloadPublicFileShareMutationResult = NonNullable<Awaited<ReturnType<typeof downloadPublicFileShare>>>
    export type DownloadPublicFileShareMutationBody = PublicFileShareDownload
    export type DownloadPublicFileShareMutationError = Error
    export type DownloadPublicFileShareMutationVariables = {token: string;data: PublicFileShareDownload}

    /**
 * @summary Download the file behind an outside share link
 */
export const useDownloadPublicFileShare = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof downloadPublicFileShare>>, TError,DownloadPublicFileShareMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof downloadPublicFileShare>>,
        TError,
        DownloadPublicFileShareMutationVariables,
        TContext
      > => {
      return useMutation(getDownloadPublicFileShareMutationOptions(options));
    }

export const getCreateLibraryImageDraftUrl = () => {




  return `/api/library/image-drafts`
}

/**
 * Requires entity.records.write and project image generation. Nothing enters the library until the draft is saved. Idempotent by requestId.
 * @summary Generate a private image draft for review before saving it to a host library
 */
export const createLibraryImageDraft = async (libraryImageDraftCreate: LibraryImageDraftCreate, options?: Parameters<typeof customFetch>[1]): Promise<LibraryImageDraft> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<LibraryImageDraft>(getCreateLibraryImageDraftUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(libraryImageDraftCreate)
  }
);}





export const getCreateLibraryImageDraftMutationKey = () => ['createLibraryImageDraft'] as const;

export const getCreateLibraryImageDraftMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createLibraryImageDraft>>, TError,CreateLibraryImageDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createLibraryImageDraft>>, TError,CreateLibraryImageDraftMutationVariables, TContext> => {

const mutationKey = getCreateLibraryImageDraftMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createLibraryImageDraft>>, CreateLibraryImageDraftMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createLibraryImageDraft(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateLibraryImageDraftMutationResult = NonNullable<Awaited<ReturnType<typeof createLibraryImageDraft>>>
    export type CreateLibraryImageDraftMutationBody = LibraryImageDraftCreate
    export type CreateLibraryImageDraftMutationError = Error
    export type CreateLibraryImageDraftMutationVariables = {data: LibraryImageDraftCreate}

    /**
 * @summary Generate a private image draft for review before saving it to a host library
 */
export const useCreateLibraryImageDraft = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createLibraryImageDraft>>, TError,CreateLibraryImageDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createLibraryImageDraft>>,
        TError,
        CreateLibraryImageDraftMutationVariables,
        TContext
      > => {
      return useMutation(getCreateLibraryImageDraftMutationOptions(options));
    }

export const getGetLibraryImageDraftUrl = (draftId: string,) => {




  return `/api/library/image-drafts/${draftId}`
}

/**
 * @summary Read one of the caller's image drafts
 */
export const getLibraryImageDraft = async (draftId: string, options?: Parameters<typeof customFetch>[1]): Promise<LibraryImageDraft> => {

  return customFetch<LibraryImageDraft>(getGetLibraryImageDraftUrl(draftId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetLibraryImageDraftQueryKey = (draftId: string,) => {
    return [
    `/api/library/image-drafts/${draftId}`
    ] as const;
    }


export const getGetLibraryImageDraftQueryOptions = <TData = Awaited<ReturnType<typeof getLibraryImageDraft>>, TError = Error>(draftId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLibraryImageDraft>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetLibraryImageDraftQueryKey(draftId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getLibraryImageDraft>>> = ({ signal }) => getLibraryImageDraft(draftId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: draftId !== null && draftId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getLibraryImageDraft>>, TError, TData> & { queryKey: QueryKey }
}

export type GetLibraryImageDraftQueryResult = NonNullable<Awaited<ReturnType<typeof getLibraryImageDraft>>>
export type GetLibraryImageDraftQueryError = Error


/**
 * @summary Read one of the caller's image drafts
 */

export function useGetLibraryImageDraft<TData = Awaited<ReturnType<typeof getLibraryImageDraft>>, TError = Error>(
 draftId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLibraryImageDraft>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetLibraryImageDraftQueryOptions(draftId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDiscardLibraryImageDraftUrl = (draftId: string,) => {




  return `/api/library/image-drafts/${draftId}`
}

/**
 * @summary Discard one of the caller's unsaved image drafts
 */
export const discardLibraryImageDraft = async (draftId: string, options?: Parameters<typeof customFetch>[1]): Promise<LibraryImageDraft> => {

  return customFetch<LibraryImageDraft>(getDiscardLibraryImageDraftUrl(draftId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDiscardLibraryImageDraftMutationKey = () => ['discardLibraryImageDraft'] as const;

export const getDiscardLibraryImageDraftMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof discardLibraryImageDraft>>, TError,DiscardLibraryImageDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof discardLibraryImageDraft>>, TError,DiscardLibraryImageDraftMutationVariables, TContext> => {

const mutationKey = getDiscardLibraryImageDraftMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof discardLibraryImageDraft>>, DiscardLibraryImageDraftMutationVariables> = (props) => {
          const {draftId} = props ?? {};

          return  discardLibraryImageDraft(draftId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DiscardLibraryImageDraftMutationResult = NonNullable<Awaited<ReturnType<typeof discardLibraryImageDraft>>>

    export type DiscardLibraryImageDraftMutationError = Error
    export type DiscardLibraryImageDraftMutationVariables = {draftId: string}

    /**
 * @summary Discard one of the caller's unsaved image drafts
 */
export const useDiscardLibraryImageDraft = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof discardLibraryImageDraft>>, TError,DiscardLibraryImageDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof discardLibraryImageDraft>>,
        TError,
        DiscardLibraryImageDraftMutationVariables,
        TContext
      > => {
      return useMutation(getDiscardLibraryImageDraftMutationOptions(options));
    }

export const getDownloadLibraryImageDraftUrl = (draftId: string,) => {




  return `/api/library/image-drafts/${draftId}/content`
}

/**
 * @summary Read the image bytes of one of the caller's drafts
 */
export const downloadLibraryImageDraft = async (draftId: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadLibraryImageDraftUrl(draftId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadLibraryImageDraftQueryKey = (draftId: string,) => {
    return [
    `/api/library/image-drafts/${draftId}/content`
    ] as const;
    }


export const getDownloadLibraryImageDraftQueryOptions = <TData = Awaited<ReturnType<typeof downloadLibraryImageDraft>>, TError = Error>(draftId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadLibraryImageDraft>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadLibraryImageDraftQueryKey(draftId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadLibraryImageDraft>>> = ({ signal }) => downloadLibraryImageDraft(draftId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: draftId !== null && draftId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadLibraryImageDraft>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadLibraryImageDraftQueryResult = NonNullable<Awaited<ReturnType<typeof downloadLibraryImageDraft>>>
export type DownloadLibraryImageDraftQueryError = Error


/**
 * @summary Read the image bytes of one of the caller's drafts
 */

export function useDownloadLibraryImageDraft<TData = Awaited<ReturnType<typeof downloadLibraryImageDraft>>, TError = Error>(
 draftId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadLibraryImageDraft>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadLibraryImageDraftQueryOptions(draftId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSaveLibraryImageDraftUrl = (draftId: string,) => {




  return `/api/library/image-drafts/${draftId}/save`
}

/**
 * Requires entity.records.write. Creates an uploaded library file in the draft's folder. Idempotent by requestId.
 * @summary Save a reviewed image draft into the library
 */
export const saveLibraryImageDraft = async (draftId: string,
    libraryImageDraftSave: LibraryImageDraftSave, options?: Parameters<typeof customFetch>[1]): Promise<LibraryImageDraft> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<LibraryImageDraft>(getSaveLibraryImageDraftUrl(draftId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(libraryImageDraftSave)
  }
);}





export const getSaveLibraryImageDraftMutationKey = () => ['saveLibraryImageDraft'] as const;

export const getSaveLibraryImageDraftMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveLibraryImageDraft>>, TError,SaveLibraryImageDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof saveLibraryImageDraft>>, TError,SaveLibraryImageDraftMutationVariables, TContext> => {

const mutationKey = getSaveLibraryImageDraftMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof saveLibraryImageDraft>>, SaveLibraryImageDraftMutationVariables> = (props) => {
          const {draftId,data} = props ?? {};

          return  saveLibraryImageDraft(draftId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveLibraryImageDraftMutationResult = NonNullable<Awaited<ReturnType<typeof saveLibraryImageDraft>>>
    export type SaveLibraryImageDraftMutationBody = LibraryImageDraftSave
    export type SaveLibraryImageDraftMutationError = Error
    export type SaveLibraryImageDraftMutationVariables = {draftId: string;data: LibraryImageDraftSave}

    /**
 * @summary Save a reviewed image draft into the library
 */
export const useSaveLibraryImageDraft = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveLibraryImageDraft>>, TError,SaveLibraryImageDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof saveLibraryImageDraft>>,
        TError,
        SaveLibraryImageDraftMutationVariables,
        TContext
      > => {
      return useMutation(getSaveLibraryImageDraftMutationOptions(options));
    }

export const getCreateManagedDocumentCopyUrl = (id: string,) => {




  return `/api/library/${id}/managed-copy`
}

/**
 * @summary Create an editable managed copy of a permitted source file
 */
export const createManagedDocumentCopy = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentWorkspace> => {

  return customFetch<ManagedDocumentWorkspace>(getCreateManagedDocumentCopyUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCreateManagedDocumentCopyMutationKey = () => ['createManagedDocumentCopy'] as const;

export const getCreateManagedDocumentCopyMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createManagedDocumentCopy>>, TError,CreateManagedDocumentCopyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createManagedDocumentCopy>>, TError,CreateManagedDocumentCopyMutationVariables, TContext> => {

const mutationKey = getCreateManagedDocumentCopyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createManagedDocumentCopy>>, CreateManagedDocumentCopyMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  createManagedDocumentCopy(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateManagedDocumentCopyMutationResult = NonNullable<Awaited<ReturnType<typeof createManagedDocumentCopy>>>

    export type CreateManagedDocumentCopyMutationError = Error
    export type CreateManagedDocumentCopyMutationVariables = {id: string}

    /**
 * @summary Create an editable managed copy of a permitted source file
 */
export const useCreateManagedDocumentCopy = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createManagedDocumentCopy>>, TError,CreateManagedDocumentCopyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createManagedDocumentCopy>>,
        TError,
        CreateManagedDocumentCopyMutationVariables,
        TContext
      > => {
      return useMutation(getCreateManagedDocumentCopyMutationOptions(options));
    }

export const getCreateManagedDocumentUrl = () => {




  return `/api/managed-documents`
}

/**
 * @summary Create a managed document at version 0.1
 */
export const createManagedDocument = async (managedDocumentCreate: ManagedDocumentCreate, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentWorkspace> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ManagedDocumentWorkspace>(getCreateManagedDocumentUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(managedDocumentCreate)
  }
);}





export const getCreateManagedDocumentMutationKey = () => ['createManagedDocument'] as const;

export const getCreateManagedDocumentMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createManagedDocument>>, TError,CreateManagedDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createManagedDocument>>, TError,CreateManagedDocumentMutationVariables, TContext> => {

const mutationKey = getCreateManagedDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createManagedDocument>>, CreateManagedDocumentMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createManagedDocument(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateManagedDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof createManagedDocument>>>
    export type CreateManagedDocumentMutationBody = ManagedDocumentCreate
    export type CreateManagedDocumentMutationError = unknown
    export type CreateManagedDocumentMutationVariables = {data: ManagedDocumentCreate}

    /**
 * @summary Create a managed document at version 0.1
 */
export const useCreateManagedDocument = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createManagedDocument>>, TError,CreateManagedDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createManagedDocument>>,
        TError,
        CreateManagedDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getCreateManagedDocumentMutationOptions(options));
    }

export const getGetManagedDocumentCreationOutcomeUrl = (requestId: string,) => {




  return `/api/managed-documents/creation-outcomes/${requestId}`
}

/**
 * @summary Read the original actor-owned creation outcome
 */
export const getManagedDocumentCreationOutcome = async (requestId: string, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentWorkspace> => {

  return customFetch<ManagedDocumentWorkspace>(getGetManagedDocumentCreationOutcomeUrl(requestId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetManagedDocumentCreationOutcomeQueryKey = (requestId: string,) => {
    return [
    `/api/managed-documents/creation-outcomes/${requestId}`
    ] as const;
    }


export const getGetManagedDocumentCreationOutcomeQueryOptions = <TData = Awaited<ReturnType<typeof getManagedDocumentCreationOutcome>>, TError = Error>(requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getManagedDocumentCreationOutcome>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetManagedDocumentCreationOutcomeQueryKey(requestId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getManagedDocumentCreationOutcome>>> = ({ signal }) => getManagedDocumentCreationOutcome(requestId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: requestId !== null && requestId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getManagedDocumentCreationOutcome>>, TError, TData> & { queryKey: QueryKey }
}

export type GetManagedDocumentCreationOutcomeQueryResult = NonNullable<Awaited<ReturnType<typeof getManagedDocumentCreationOutcome>>>
export type GetManagedDocumentCreationOutcomeQueryError = Error


/**
 * @summary Read the original actor-owned creation outcome
 */

export function useGetManagedDocumentCreationOutcome<TData = Awaited<ReturnType<typeof getManagedDocumentCreationOutcome>>, TError = Error>(
 requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getManagedDocumentCreationOutcome>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetManagedDocumentCreationOutcomeQueryOptions(requestId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPreviewManagedDocumentImportUrl = () => {




  return `/api/managed-documents/import-preview`
}

/**
 * @summary Convert DOCX or UTF-8 Markdown for review without adopting it
 */
export const previewManagedDocumentImport = async (previewManagedDocumentImportBody: PreviewManagedDocumentImportBody, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentImportPreview> => {
    const formData = new FormData();
formData.append(`file`, previewManagedDocumentImportBody.file);

  return customFetch<ManagedDocumentImportPreview>(getPreviewManagedDocumentImportUrl(),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}





export const getPreviewManagedDocumentImportMutationKey = () => ['previewManagedDocumentImport'] as const;

export const getPreviewManagedDocumentImportMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewManagedDocumentImport>>, TError,PreviewManagedDocumentImportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewManagedDocumentImport>>, TError,PreviewManagedDocumentImportMutationVariables, TContext> => {

const mutationKey = getPreviewManagedDocumentImportMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewManagedDocumentImport>>, PreviewManagedDocumentImportMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  previewManagedDocumentImport(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewManagedDocumentImportMutationResult = NonNullable<Awaited<ReturnType<typeof previewManagedDocumentImport>>>
    export type PreviewManagedDocumentImportMutationBody = PreviewManagedDocumentImportBody
    export type PreviewManagedDocumentImportMutationError = Error
    export type PreviewManagedDocumentImportMutationVariables = {data: PreviewManagedDocumentImportBody}

    /**
 * @summary Convert DOCX or UTF-8 Markdown for review without adopting it
 */
export const usePreviewManagedDocumentImport = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewManagedDocumentImport>>, TError,PreviewManagedDocumentImportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewManagedDocumentImport>>,
        TError,
        PreviewManagedDocumentImportMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewManagedDocumentImportMutationOptions(options));
    }

export const getAdoptManagedDocumentImportUrl = () => {




  return `/api/managed-documents/import`
}

/**
 * @summary Preserve a DOCX or Markdown original and adopt a reviewed conversion
 */
export const adoptManagedDocumentImport = async (adoptManagedDocumentImportBody: AdoptManagedDocumentImportBody, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentWorkspace> => {
    const formData = new FormData();
formData.append(`file`, adoptManagedDocumentImportBody.file);
if(adoptManagedDocumentImportBody.title !== undefined) {
 formData.append(`title`, adoptManagedDocumentImportBody.title);
 }
if(adoptManagedDocumentImportBody.category !== undefined) {
 formData.append(`category`, adoptManagedDocumentImportBody.category);
 }
if(adoptManagedDocumentImportBody.folder !== undefined) {
 formData.append(`folder`, adoptManagedDocumentImportBody.folder);
 }

  return customFetch<ManagedDocumentWorkspace>(getAdoptManagedDocumentImportUrl(),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}





export const getAdoptManagedDocumentImportMutationKey = () => ['adoptManagedDocumentImport'] as const;

export const getAdoptManagedDocumentImportMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adoptManagedDocumentImport>>, TError,AdoptManagedDocumentImportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof adoptManagedDocumentImport>>, TError,AdoptManagedDocumentImportMutationVariables, TContext> => {

const mutationKey = getAdoptManagedDocumentImportMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof adoptManagedDocumentImport>>, AdoptManagedDocumentImportMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  adoptManagedDocumentImport(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AdoptManagedDocumentImportMutationResult = NonNullable<Awaited<ReturnType<typeof adoptManagedDocumentImport>>>
    export type AdoptManagedDocumentImportMutationBody = AdoptManagedDocumentImportBody
    export type AdoptManagedDocumentImportMutationError = unknown
    export type AdoptManagedDocumentImportMutationVariables = {data: AdoptManagedDocumentImportBody}

    /**
 * @summary Preserve a DOCX or Markdown original and adopt a reviewed conversion
 */
export const useAdoptManagedDocumentImport = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adoptManagedDocumentImport>>, TError,AdoptManagedDocumentImportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof adoptManagedDocumentImport>>,
        TError,
        AdoptManagedDocumentImportMutationVariables,
        TContext
      > => {
      return useMutation(getAdoptManagedDocumentImportMutationOptions(options));
    }

export const getGetManagedDocumentUrl = (id: string,) => {




  return `/api/managed-documents/${id}`
}

/**
 * @summary Get the private authoring workspace and exact draft revision
 */
export const getManagedDocument = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentWorkspace> => {

  return customFetch<ManagedDocumentWorkspace>(getGetManagedDocumentUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetManagedDocumentQueryKey = (id: string,) => {
    return [
    `/api/managed-documents/${id}`
    ] as const;
    }


export const getGetManagedDocumentQueryOptions = <TData = Awaited<ReturnType<typeof getManagedDocument>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getManagedDocument>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetManagedDocumentQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getManagedDocument>>> = ({ signal }) => getManagedDocument(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getManagedDocument>>, TError, TData> & { queryKey: QueryKey }
}

export type GetManagedDocumentQueryResult = NonNullable<Awaited<ReturnType<typeof getManagedDocument>>>
export type GetManagedDocumentQueryError = Error


/**
 * @summary Get the private authoring workspace and exact draft revision
 */

export function useGetManagedDocument<TData = Awaited<ReturnType<typeof getManagedDocument>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getManagedDocument>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetManagedDocumentQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSaveManagedDocumentDraftUrl = (id: string,) => {




  return `/api/managed-documents/${id}`
}

/**
 * @summary Autosave a working draft using optimistic concurrency
 */
export const saveManagedDocumentDraft = async (id: string,
    managedDocumentDraftSave: ManagedDocumentDraftSave, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentWorkspace> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ManagedDocumentWorkspace>(getSaveManagedDocumentDraftUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(managedDocumentDraftSave)
  }
);}





export const getSaveManagedDocumentDraftMutationKey = () => ['saveManagedDocumentDraft'] as const;

export const getSaveManagedDocumentDraftMutationOptions = <TError = ManagedDocumentConflict,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveManagedDocumentDraft>>, TError,SaveManagedDocumentDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof saveManagedDocumentDraft>>, TError,SaveManagedDocumentDraftMutationVariables, TContext> => {

const mutationKey = getSaveManagedDocumentDraftMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof saveManagedDocumentDraft>>, SaveManagedDocumentDraftMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  saveManagedDocumentDraft(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveManagedDocumentDraftMutationResult = NonNullable<Awaited<ReturnType<typeof saveManagedDocumentDraft>>>
    export type SaveManagedDocumentDraftMutationBody = ManagedDocumentDraftSave
    export type SaveManagedDocumentDraftMutationError = ManagedDocumentConflict
    export type SaveManagedDocumentDraftMutationVariables = {id: string;data: ManagedDocumentDraftSave}

    /**
 * @summary Autosave a working draft using optimistic concurrency
 */
export const useSaveManagedDocumentDraft = <TError = ManagedDocumentConflict,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveManagedDocumentDraft>>, TError,SaveManagedDocumentDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof saveManagedDocumentDraft>>,
        TError,
        SaveManagedDocumentDraftMutationVariables,
        TContext
      > => {
      return useMutation(getSaveManagedDocumentDraftMutationOptions(options));
    }

export const getChangeManagedDocumentSectionsUrl = (id: string,) => {




  return `/api/managed-documents/${id}/sections`
}

export const changeManagedDocumentSections = async (id: string,
    managedDocumentSectionCommand: ManagedDocumentSectionCommand, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentWorkspace> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ManagedDocumentWorkspace>(getChangeManagedDocumentSectionsUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(managedDocumentSectionCommand)
  }
);}





export const getChangeManagedDocumentSectionsMutationKey = () => ['changeManagedDocumentSections'] as const;

export const getChangeManagedDocumentSectionsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof changeManagedDocumentSections>>, TError,ChangeManagedDocumentSectionsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof changeManagedDocumentSections>>, TError,ChangeManagedDocumentSectionsMutationVariables, TContext> => {

const mutationKey = getChangeManagedDocumentSectionsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof changeManagedDocumentSections>>, ChangeManagedDocumentSectionsMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  changeManagedDocumentSections(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ChangeManagedDocumentSectionsMutationResult = NonNullable<Awaited<ReturnType<typeof changeManagedDocumentSections>>>
    export type ChangeManagedDocumentSectionsMutationBody = ManagedDocumentSectionCommand
    export type ChangeManagedDocumentSectionsMutationError = unknown
    export type ChangeManagedDocumentSectionsMutationVariables = {id: string;data: ManagedDocumentSectionCommand}

    export const useChangeManagedDocumentSections = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof changeManagedDocumentSections>>, TError,ChangeManagedDocumentSectionsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof changeManagedDocumentSections>>,
        TError,
        ChangeManagedDocumentSectionsMutationVariables,
        TContext
      > => {
      return useMutation(getChangeManagedDocumentSectionsMutationOptions(options));
    }

export const getGetManagedDocumentSectionOutcomeUrl = (id: string,
    params: GetManagedDocumentSectionOutcomeParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/managed-documents/${id}/sections?${stringifiedParams}` : `/api/managed-documents/${id}/sections`
}

export const getManagedDocumentSectionOutcome = async (id: string,
    params: GetManagedDocumentSectionOutcomeParams, options?: Parameters<typeof customFetch>[1]): Promise<GetManagedDocumentSectionOutcome200> => {

  return customFetch<GetManagedDocumentSectionOutcome200>(getGetManagedDocumentSectionOutcomeUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetManagedDocumentSectionOutcomeQueryKey = (id: string,
    params?: GetManagedDocumentSectionOutcomeParams,) => {
    return [
    `/api/managed-documents/${id}/sections`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetManagedDocumentSectionOutcomeQueryOptions = <TData = Awaited<ReturnType<typeof getManagedDocumentSectionOutcome>>, TError = unknown>(id: string,
    params: GetManagedDocumentSectionOutcomeParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getManagedDocumentSectionOutcome>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetManagedDocumentSectionOutcomeQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getManagedDocumentSectionOutcome>>> = ({ signal }) => getManagedDocumentSectionOutcome(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getManagedDocumentSectionOutcome>>, TError, TData> & { queryKey: QueryKey }
}

export type GetManagedDocumentSectionOutcomeQueryResult = NonNullable<Awaited<ReturnType<typeof getManagedDocumentSectionOutcome>>>
export type GetManagedDocumentSectionOutcomeQueryError = unknown



export function useGetManagedDocumentSectionOutcome<TData = Awaited<ReturnType<typeof getManagedDocumentSectionOutcome>>, TError = unknown>(
 id: string,
    params: GetManagedDocumentSectionOutcomeParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getManagedDocumentSectionOutcome>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetManagedDocumentSectionOutcomeQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetManagedDocumentCollaborationUrl = (id: string,
    params: GetManagedDocumentCollaborationParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/managed-documents/${id}/collaboration?${stringifiedParams}` : `/api/managed-documents/${id}/collaboration`
}

export const getManagedDocumentCollaboration = async (id: string,
    params: GetManagedDocumentCollaborationParams, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentCollaborationSnapshot> => {

  return customFetch<ManagedDocumentCollaborationSnapshot>(getGetManagedDocumentCollaborationUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetManagedDocumentCollaborationQueryKey = (id: string,
    params?: GetManagedDocumentCollaborationParams,) => {
    return [
    `/api/managed-documents/${id}/collaboration`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetManagedDocumentCollaborationQueryOptions = <TData = Awaited<ReturnType<typeof getManagedDocumentCollaboration>>, TError = unknown>(id: string,
    params: GetManagedDocumentCollaborationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getManagedDocumentCollaboration>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetManagedDocumentCollaborationQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getManagedDocumentCollaboration>>> = ({ signal }) => getManagedDocumentCollaboration(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getManagedDocumentCollaboration>>, TError, TData> & { queryKey: QueryKey }
}

export type GetManagedDocumentCollaborationQueryResult = NonNullable<Awaited<ReturnType<typeof getManagedDocumentCollaboration>>>
export type GetManagedDocumentCollaborationQueryError = unknown



export function useGetManagedDocumentCollaboration<TData = Awaited<ReturnType<typeof getManagedDocumentCollaboration>>, TError = unknown>(
 id: string,
    params: GetManagedDocumentCollaborationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getManagedDocumentCollaboration>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetManagedDocumentCollaborationQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateManagedDocumentCollaborationUrl = (id: string,) => {




  return `/api/managed-documents/${id}/collaboration`
}

export const updateManagedDocumentCollaboration = async (id: string,
    managedDocumentCollaborationCommand: ManagedDocumentCollaborationCommand, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentCollaborationSnapshot> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ManagedDocumentCollaborationSnapshot>(getUpdateManagedDocumentCollaborationUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(managedDocumentCollaborationCommand)
  }
);}





export const getUpdateManagedDocumentCollaborationMutationKey = () => ['updateManagedDocumentCollaboration'] as const;

export const getUpdateManagedDocumentCollaborationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateManagedDocumentCollaboration>>, TError,UpdateManagedDocumentCollaborationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateManagedDocumentCollaboration>>, TError,UpdateManagedDocumentCollaborationMutationVariables, TContext> => {

const mutationKey = getUpdateManagedDocumentCollaborationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateManagedDocumentCollaboration>>, UpdateManagedDocumentCollaborationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateManagedDocumentCollaboration(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateManagedDocumentCollaborationMutationResult = NonNullable<Awaited<ReturnType<typeof updateManagedDocumentCollaboration>>>
    export type UpdateManagedDocumentCollaborationMutationBody = ManagedDocumentCollaborationCommand
    export type UpdateManagedDocumentCollaborationMutationError = unknown
    export type UpdateManagedDocumentCollaborationMutationVariables = {id: string;data: ManagedDocumentCollaborationCommand}

    export const useUpdateManagedDocumentCollaboration = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateManagedDocumentCollaboration>>, TError,UpdateManagedDocumentCollaborationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateManagedDocumentCollaboration>>,
        TError,
        UpdateManagedDocumentCollaborationMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateManagedDocumentCollaborationMutationOptions(options));
    }

export const getGetManagedDocumentEditingReviewUrl = (id: string,
    params?: GetManagedDocumentEditingReviewParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/managed-documents/${id}/editing-review?${stringifiedParams}` : `/api/managed-documents/${id}/editing-review`
}

export const getManagedDocumentEditingReview = async (id: string,
    params?: GetManagedDocumentEditingReviewParams, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentEditingReviewPage> => {

  return customFetch<ManagedDocumentEditingReviewPage>(getGetManagedDocumentEditingReviewUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetManagedDocumentEditingReviewQueryKey = (id: string,
    params?: GetManagedDocumentEditingReviewParams,) => {
    return [
    `/api/managed-documents/${id}/editing-review`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetManagedDocumentEditingReviewQueryOptions = <TData = Awaited<ReturnType<typeof getManagedDocumentEditingReview>>, TError = unknown>(id: string,
    params?: GetManagedDocumentEditingReviewParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getManagedDocumentEditingReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetManagedDocumentEditingReviewQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getManagedDocumentEditingReview>>> = ({ signal }) => getManagedDocumentEditingReview(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getManagedDocumentEditingReview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetManagedDocumentEditingReviewQueryResult = NonNullable<Awaited<ReturnType<typeof getManagedDocumentEditingReview>>>
export type GetManagedDocumentEditingReviewQueryError = unknown



export function useGetManagedDocumentEditingReview<TData = Awaited<ReturnType<typeof getManagedDocumentEditingReview>>, TError = unknown>(
 id: string,
    params?: GetManagedDocumentEditingReviewParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getManagedDocumentEditingReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetManagedDocumentEditingReviewQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getChangeManagedDocumentEditingReviewUrl = (id: string,) => {




  return `/api/managed-documents/${id}/editing-review`
}

export const changeManagedDocumentEditingReview = async (id: string,
    managedDocumentEditingReviewCommand: ManagedDocumentEditingReviewCommand, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentWorkspace> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ManagedDocumentWorkspace>(getChangeManagedDocumentEditingReviewUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(managedDocumentEditingReviewCommand)
  }
);}





export const getChangeManagedDocumentEditingReviewMutationKey = () => ['changeManagedDocumentEditingReview'] as const;

export const getChangeManagedDocumentEditingReviewMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof changeManagedDocumentEditingReview>>, TError,ChangeManagedDocumentEditingReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof changeManagedDocumentEditingReview>>, TError,ChangeManagedDocumentEditingReviewMutationVariables, TContext> => {

const mutationKey = getChangeManagedDocumentEditingReviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof changeManagedDocumentEditingReview>>, ChangeManagedDocumentEditingReviewMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  changeManagedDocumentEditingReview(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ChangeManagedDocumentEditingReviewMutationResult = NonNullable<Awaited<ReturnType<typeof changeManagedDocumentEditingReview>>>
    export type ChangeManagedDocumentEditingReviewMutationBody = ManagedDocumentEditingReviewCommand
    export type ChangeManagedDocumentEditingReviewMutationError = unknown
    export type ChangeManagedDocumentEditingReviewMutationVariables = {id: string;data: ManagedDocumentEditingReviewCommand}

    export const useChangeManagedDocumentEditingReview = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof changeManagedDocumentEditingReview>>, TError,ChangeManagedDocumentEditingReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof changeManagedDocumentEditingReview>>,
        TError,
        ChangeManagedDocumentEditingReviewMutationVariables,
        TContext
      > => {
      return useMutation(getChangeManagedDocumentEditingReviewMutationOptions(options));
    }

export const getUploadManagedDocumentImageUrl = (id: string,) => {




  return `/api/managed-documents/${id}/images`
}

/**
 * @summary Store a validated private image bound to this exact document
 */
export const uploadManagedDocumentImage = async (id: string,
    uploadManagedDocumentImageBody: UploadManagedDocumentImageBody, options?: Parameters<typeof customFetch>[1]): Promise<UploadManagedDocumentImage201> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<UploadManagedDocumentImage201>(getUploadManagedDocumentImageUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(uploadManagedDocumentImageBody)
  }
);}





export const getUploadManagedDocumentImageMutationKey = () => ['uploadManagedDocumentImage'] as const;

export const getUploadManagedDocumentImageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadManagedDocumentImage>>, TError,UploadManagedDocumentImageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadManagedDocumentImage>>, TError,UploadManagedDocumentImageMutationVariables, TContext> => {

const mutationKey = getUploadManagedDocumentImageMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadManagedDocumentImage>>, UploadManagedDocumentImageMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  uploadManagedDocumentImage(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadManagedDocumentImageMutationResult = NonNullable<Awaited<ReturnType<typeof uploadManagedDocumentImage>>>
    export type UploadManagedDocumentImageMutationBody = UploadManagedDocumentImageBody
    export type UploadManagedDocumentImageMutationError = unknown
    export type UploadManagedDocumentImageMutationVariables = {id: string;data: UploadManagedDocumentImageBody}

    /**
 * @summary Store a validated private image bound to this exact document
 */
export const useUploadManagedDocumentImage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadManagedDocumentImage>>, TError,UploadManagedDocumentImageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadManagedDocumentImage>>,
        TError,
        UploadManagedDocumentImageMutationVariables,
        TContext
      > => {
      return useMutation(getUploadManagedDocumentImageMutationOptions(options));
    }

export const getDownloadManagedDocumentImageUrl = (id: string,
    imageId: string,) => {




  return `/api/managed-documents/${id}/images/${imageId}`
}

/**
 * @summary Read a private image under current native document authority
 */
export const downloadManagedDocumentImage = async (id: string,
    imageId: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadManagedDocumentImageUrl(id,imageId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadManagedDocumentImageQueryKey = (id: string,
    imageId: string,) => {
    return [
    `/api/managed-documents/${id}/images/${imageId}`
    ] as const;
    }


export const getDownloadManagedDocumentImageQueryOptions = <TData = Awaited<ReturnType<typeof downloadManagedDocumentImage>>, TError = unknown>(id: string,
    imageId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadManagedDocumentImage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadManagedDocumentImageQueryKey(id,imageId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadManagedDocumentImage>>> = ({ signal }) => downloadManagedDocumentImage(id,imageId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && imageId !== null && imageId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadManagedDocumentImage>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadManagedDocumentImageQueryResult = NonNullable<Awaited<ReturnType<typeof downloadManagedDocumentImage>>>
export type DownloadManagedDocumentImageQueryError = unknown


/**
 * @summary Read a private image under current native document authority
 */

export function useDownloadManagedDocumentImage<TData = Awaited<ReturnType<typeof downloadManagedDocumentImage>>, TError = unknown>(
 id: string,
    imageId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadManagedDocumentImage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadManagedDocumentImageQueryOptions(id,imageId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListManagedDocumentVersionsUrl = (id: string,
    params?: ListManagedDocumentVersionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/managed-documents/${id}/versions?${stringifiedParams}` : `/api/managed-documents/${id}/versions`
}

/**
 * @summary List immutable document versions
 */
export const listManagedDocumentVersions = async (id: string,
    params?: ListManagedDocumentVersionsParams, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentVersionPage> => {

  return customFetch<ManagedDocumentVersionPage>(getListManagedDocumentVersionsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListManagedDocumentVersionsQueryKey = (id: string,
    params?: ListManagedDocumentVersionsParams,) => {
    return [
    `/api/managed-documents/${id}/versions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListManagedDocumentVersionsQueryOptions = <TData = Awaited<ReturnType<typeof listManagedDocumentVersions>>, TError = unknown>(id: string,
    params?: ListManagedDocumentVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listManagedDocumentVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListManagedDocumentVersionsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listManagedDocumentVersions>>> = ({ signal }) => listManagedDocumentVersions(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listManagedDocumentVersions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListManagedDocumentVersionsQueryResult = NonNullable<Awaited<ReturnType<typeof listManagedDocumentVersions>>>
export type ListManagedDocumentVersionsQueryError = unknown


/**
 * @summary List immutable document versions
 */

export function useListManagedDocumentVersions<TData = Awaited<ReturnType<typeof listManagedDocumentVersions>>, TError = unknown>(
 id: string,
    params?: ListManagedDocumentVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listManagedDocumentVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListManagedDocumentVersionsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCheckpointManagedDocumentUrl = (id: string,) => {




  return `/api/managed-documents/${id}/versions`
}

/**
 * @summary Create an immutable minor draft checkpoint
 */
export const checkpointManagedDocument = async (id: string,
    managedDocumentReason: ManagedDocumentReason, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentVersion> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ManagedDocumentVersion>(getCheckpointManagedDocumentUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(managedDocumentReason)
  }
);}





export const getCheckpointManagedDocumentMutationKey = () => ['checkpointManagedDocument'] as const;

export const getCheckpointManagedDocumentMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof checkpointManagedDocument>>, TError,CheckpointManagedDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof checkpointManagedDocument>>, TError,CheckpointManagedDocumentMutationVariables, TContext> => {

const mutationKey = getCheckpointManagedDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof checkpointManagedDocument>>, CheckpointManagedDocumentMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  checkpointManagedDocument(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CheckpointManagedDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof checkpointManagedDocument>>>
    export type CheckpointManagedDocumentMutationBody = ManagedDocumentReason
    export type CheckpointManagedDocumentMutationError = unknown
    export type CheckpointManagedDocumentMutationVariables = {id: string;data: ManagedDocumentReason}

    /**
 * @summary Create an immutable minor draft checkpoint
 */
export const useCheckpointManagedDocument = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof checkpointManagedDocument>>, TError,CheckpointManagedDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof checkpointManagedDocument>>,
        TError,
        CheckpointManagedDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getCheckpointManagedDocumentMutationOptions(options));
    }

export const getPreviewManagedDocumentVersionUrl = (id: string,) => {




  return `/api/managed-documents/${id}/version-preview`
}

/**
 * Read-only. Shown before checkpointManagedDocument so the person confirms the version they are about to save; the saved version is then listed and restorable from the document's file library history.
 * @summary Preview the next saved version number and what changed since the latest version
 */
export const previewManagedDocumentVersion = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentVersionPreview> => {

  return customFetch<ManagedDocumentVersionPreview>(getPreviewManagedDocumentVersionUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPreviewManagedDocumentVersionQueryKey = (id: string,) => {
    return [
    `/api/managed-documents/${id}/version-preview`
    ] as const;
    }


export const getPreviewManagedDocumentVersionQueryOptions = <TData = Awaited<ReturnType<typeof previewManagedDocumentVersion>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof previewManagedDocumentVersion>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPreviewManagedDocumentVersionQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof previewManagedDocumentVersion>>> = ({ signal }) => previewManagedDocumentVersion(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof previewManagedDocumentVersion>>, TError, TData> & { queryKey: QueryKey }
}

export type PreviewManagedDocumentVersionQueryResult = NonNullable<Awaited<ReturnType<typeof previewManagedDocumentVersion>>>
export type PreviewManagedDocumentVersionQueryError = Error


/**
 * @summary Preview the next saved version number and what changed since the latest version
 */

export function usePreviewManagedDocumentVersion<TData = Awaited<ReturnType<typeof previewManagedDocumentVersion>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof previewManagedDocumentVersion>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getPreviewManagedDocumentVersionQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListDocumentStylesUrl = (params?: ListDocumentStylesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/document-styles?${stringifiedParams}` : `/api/document-styles`
}

/**
 * The style library is shared by everyone who can read entity records. Applying a style copies it into the document, so documents never depend on library rows.
 * @summary Page the entity's reusable named paragraph styles
 */
export const listDocumentStyles = async (params?: ListDocumentStylesParams, options?: Parameters<typeof customFetch>[1]): Promise<DocumentNamedStylePage> => {

  return customFetch<DocumentNamedStylePage>(getListDocumentStylesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDocumentStylesQueryKey = (params?: ListDocumentStylesParams,) => {
    return [
    `/api/document-styles`, ...(params ? [params] : [])
    ] as const;
    }


export const getListDocumentStylesQueryOptions = <TData = Awaited<ReturnType<typeof listDocumentStyles>>, TError = Error>(params?: ListDocumentStylesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentStyles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDocumentStylesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDocumentStyles>>> = ({ signal }) => listDocumentStyles(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDocumentStyles>>, TError, TData> & { queryKey: QueryKey }
}

export type ListDocumentStylesQueryResult = NonNullable<Awaited<ReturnType<typeof listDocumentStyles>>>
export type ListDocumentStylesQueryError = Error


/**
 * @summary Page the entity's reusable named paragraph styles
 */

export function useListDocumentStyles<TData = Awaited<ReturnType<typeof listDocumentStyles>>, TError = Error>(
 params?: ListDocumentStylesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentStyles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListDocumentStylesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateDocumentStyleUrl = () => {




  return `/api/document-styles`
}

/**
 * Requires permission to change entity records. Active style names are unique regardless of letter case.
 * @summary Add a named paragraph style to the entity style library
 */
export const createDocumentStyle = async (documentNamedStyleCreate: DocumentNamedStyleCreate, options?: Parameters<typeof customFetch>[1]): Promise<DocumentNamedStyle> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentNamedStyle>(getCreateDocumentStyleUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(documentNamedStyleCreate)
  }
);}





export const getCreateDocumentStyleMutationKey = () => ['createDocumentStyle'] as const;

export const getCreateDocumentStyleMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDocumentStyle>>, TError,CreateDocumentStyleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createDocumentStyle>>, TError,CreateDocumentStyleMutationVariables, TContext> => {

const mutationKey = getCreateDocumentStyleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createDocumentStyle>>, CreateDocumentStyleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createDocumentStyle(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateDocumentStyleMutationResult = NonNullable<Awaited<ReturnType<typeof createDocumentStyle>>>
    export type CreateDocumentStyleMutationBody = DocumentNamedStyleCreate
    export type CreateDocumentStyleMutationError = Error
    export type CreateDocumentStyleMutationVariables = {data: DocumentNamedStyleCreate}

    /**
 * @summary Add a named paragraph style to the entity style library
 */
export const useCreateDocumentStyle = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDocumentStyle>>, TError,CreateDocumentStyleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createDocumentStyle>>,
        TError,
        CreateDocumentStyleMutationVariables,
        TContext
      > => {
      return useMutation(getCreateDocumentStyleMutationOptions(options));
    }

export const getUpdateDocumentStyleUrl = (styleId: string,) => {




  return `/api/document-styles/${styleId}`
}

/**
 * @summary Rename or reformat a library style using its reviewed revision
 */
export const updateDocumentStyle = async (styleId: string,
    documentNamedStyleUpdate: DocumentNamedStyleUpdate, options?: Parameters<typeof customFetch>[1]): Promise<DocumentNamedStyle> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentNamedStyle>(getUpdateDocumentStyleUrl(styleId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(documentNamedStyleUpdate)
  }
);}





export const getUpdateDocumentStyleMutationKey = () => ['updateDocumentStyle'] as const;

export const getUpdateDocumentStyleMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDocumentStyle>>, TError,UpdateDocumentStyleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateDocumentStyle>>, TError,UpdateDocumentStyleMutationVariables, TContext> => {

const mutationKey = getUpdateDocumentStyleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateDocumentStyle>>, UpdateDocumentStyleMutationVariables> = (props) => {
          const {styleId,data} = props ?? {};

          return  updateDocumentStyle(styleId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateDocumentStyleMutationResult = NonNullable<Awaited<ReturnType<typeof updateDocumentStyle>>>
    export type UpdateDocumentStyleMutationBody = DocumentNamedStyleUpdate
    export type UpdateDocumentStyleMutationError = Error
    export type UpdateDocumentStyleMutationVariables = {styleId: string;data: DocumentNamedStyleUpdate}

    /**
 * @summary Rename or reformat a library style using its reviewed revision
 */
export const useUpdateDocumentStyle = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDocumentStyle>>, TError,UpdateDocumentStyleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateDocumentStyle>>,
        TError,
        UpdateDocumentStyleMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateDocumentStyleMutationOptions(options));
    }

export const getArchiveDocumentStyleUrl = (styleId: string,) => {




  return `/api/document-styles/${styleId}/archive`
}

/**
 * @summary Withdraw a library style; documents that already use it keep their copy
 */
export const archiveDocumentStyle = async (styleId: string,
    documentNamedStyleArchive: DocumentNamedStyleArchive, options?: Parameters<typeof customFetch>[1]): Promise<DocumentNamedStyle> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentNamedStyle>(getArchiveDocumentStyleUrl(styleId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(documentNamedStyleArchive)
  }
);}





export const getArchiveDocumentStyleMutationKey = () => ['archiveDocumentStyle'] as const;

export const getArchiveDocumentStyleMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof archiveDocumentStyle>>, TError,ArchiveDocumentStyleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof archiveDocumentStyle>>, TError,ArchiveDocumentStyleMutationVariables, TContext> => {

const mutationKey = getArchiveDocumentStyleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof archiveDocumentStyle>>, ArchiveDocumentStyleMutationVariables> = (props) => {
          const {styleId,data} = props ?? {};

          return  archiveDocumentStyle(styleId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ArchiveDocumentStyleMutationResult = NonNullable<Awaited<ReturnType<typeof archiveDocumentStyle>>>
    export type ArchiveDocumentStyleMutationBody = DocumentNamedStyleArchive
    export type ArchiveDocumentStyleMutationError = Error
    export type ArchiveDocumentStyleMutationVariables = {styleId: string;data: DocumentNamedStyleArchive}

    /**
 * @summary Withdraw a library style; documents that already use it keep their copy
 */
export const useArchiveDocumentStyle = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof archiveDocumentStyle>>, TError,ArchiveDocumentStyleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof archiveDocumentStyle>>,
        TError,
        ArchiveDocumentStyleMutationVariables,
        TContext
      > => {
      return useMutation(getArchiveDocumentStyleMutationOptions(options));
    }

export const getListDocumentStyleTemplatesUrl = (params?: ListDocumentStyleTemplatesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/document-style-templates?${stringifiedParams}` : `/api/document-style-templates`
}

/**
 * @summary Named style templates shared across the entity, newest first
 */
export const listDocumentStyleTemplates = async (params?: ListDocumentStyleTemplatesParams, options?: Parameters<typeof customFetch>[1]): Promise<DocumentStyleTemplatePage> => {

  return customFetch<DocumentStyleTemplatePage>(getListDocumentStyleTemplatesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDocumentStyleTemplatesQueryKey = (params?: ListDocumentStyleTemplatesParams,) => {
    return [
    `/api/document-style-templates`, ...(params ? [params] : [])
    ] as const;
    }


export const getListDocumentStyleTemplatesQueryOptions = <TData = Awaited<ReturnType<typeof listDocumentStyleTemplates>>, TError = Error>(params?: ListDocumentStyleTemplatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentStyleTemplates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDocumentStyleTemplatesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDocumentStyleTemplates>>> = ({ signal }) => listDocumentStyleTemplates(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDocumentStyleTemplates>>, TError, TData> & { queryKey: QueryKey }
}

export type ListDocumentStyleTemplatesQueryResult = NonNullable<Awaited<ReturnType<typeof listDocumentStyleTemplates>>>
export type ListDocumentStyleTemplatesQueryError = Error


/**
 * @summary Named style templates shared across the entity, newest first
 */

export function useListDocumentStyleTemplates<TData = Awaited<ReturnType<typeof listDocumentStyleTemplates>>, TError = Error>(
 params?: ListDocumentStyleTemplatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentStyleTemplates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListDocumentStyleTemplatesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateDocumentStyleTemplateUrl = () => {




  return `/api/document-style-templates`
}

/**
 * @summary Save a document's named styles as a reusable template
 */
export const createDocumentStyleTemplate = async (documentStyleTemplateCreate: DocumentStyleTemplateCreate, options?: Parameters<typeof customFetch>[1]): Promise<DocumentStyleTemplate> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentStyleTemplate>(getCreateDocumentStyleTemplateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(documentStyleTemplateCreate)
  }
);}





export const getCreateDocumentStyleTemplateMutationKey = () => ['createDocumentStyleTemplate'] as const;

export const getCreateDocumentStyleTemplateMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDocumentStyleTemplate>>, TError,CreateDocumentStyleTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createDocumentStyleTemplate>>, TError,CreateDocumentStyleTemplateMutationVariables, TContext> => {

const mutationKey = getCreateDocumentStyleTemplateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createDocumentStyleTemplate>>, CreateDocumentStyleTemplateMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createDocumentStyleTemplate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateDocumentStyleTemplateMutationResult = NonNullable<Awaited<ReturnType<typeof createDocumentStyleTemplate>>>
    export type CreateDocumentStyleTemplateMutationBody = DocumentStyleTemplateCreate
    export type CreateDocumentStyleTemplateMutationError = Error
    export type CreateDocumentStyleTemplateMutationVariables = {data: DocumentStyleTemplateCreate}

    /**
 * @summary Save a document's named styles as a reusable template
 */
export const useCreateDocumentStyleTemplate = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDocumentStyleTemplate>>, TError,CreateDocumentStyleTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createDocumentStyleTemplate>>,
        TError,
        CreateDocumentStyleTemplateMutationVariables,
        TContext
      > => {
      return useMutation(getCreateDocumentStyleTemplateMutationOptions(options));
    }

export const getUpdateDocumentStyleTemplateUrl = (templateId: string,) => {




  return `/api/document-style-templates/${templateId}`
}

/**
 * @summary Rename or replace a style template using its reviewed revision
 */
export const updateDocumentStyleTemplate = async (templateId: string,
    documentStyleTemplateUpdate: DocumentStyleTemplateUpdate, options?: Parameters<typeof customFetch>[1]): Promise<DocumentStyleTemplate> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentStyleTemplate>(getUpdateDocumentStyleTemplateUrl(templateId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(documentStyleTemplateUpdate)
  }
);}





export const getUpdateDocumentStyleTemplateMutationKey = () => ['updateDocumentStyleTemplate'] as const;

export const getUpdateDocumentStyleTemplateMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDocumentStyleTemplate>>, TError,UpdateDocumentStyleTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateDocumentStyleTemplate>>, TError,UpdateDocumentStyleTemplateMutationVariables, TContext> => {

const mutationKey = getUpdateDocumentStyleTemplateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateDocumentStyleTemplate>>, UpdateDocumentStyleTemplateMutationVariables> = (props) => {
          const {templateId,data} = props ?? {};

          return  updateDocumentStyleTemplate(templateId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateDocumentStyleTemplateMutationResult = NonNullable<Awaited<ReturnType<typeof updateDocumentStyleTemplate>>>
    export type UpdateDocumentStyleTemplateMutationBody = DocumentStyleTemplateUpdate
    export type UpdateDocumentStyleTemplateMutationError = Error
    export type UpdateDocumentStyleTemplateMutationVariables = {templateId: string;data: DocumentStyleTemplateUpdate}

    /**
 * @summary Rename or replace a style template using its reviewed revision
 */
export const useUpdateDocumentStyleTemplate = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDocumentStyleTemplate>>, TError,UpdateDocumentStyleTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateDocumentStyleTemplate>>,
        TError,
        UpdateDocumentStyleTemplateMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateDocumentStyleTemplateMutationOptions(options));
    }

export const getArchiveDocumentStyleTemplateUrl = (templateId: string,) => {




  return `/api/document-style-templates/${templateId}/archive`
}

/**
 * @summary Withdraw a style template; documents that applied it keep their copy
 */
export const archiveDocumentStyleTemplate = async (templateId: string,
    documentNamedStyleArchive: DocumentNamedStyleArchive, options?: Parameters<typeof customFetch>[1]): Promise<DocumentStyleTemplate> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentStyleTemplate>(getArchiveDocumentStyleTemplateUrl(templateId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(documentNamedStyleArchive)
  }
);}





export const getArchiveDocumentStyleTemplateMutationKey = () => ['archiveDocumentStyleTemplate'] as const;

export const getArchiveDocumentStyleTemplateMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof archiveDocumentStyleTemplate>>, TError,ArchiveDocumentStyleTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof archiveDocumentStyleTemplate>>, TError,ArchiveDocumentStyleTemplateMutationVariables, TContext> => {

const mutationKey = getArchiveDocumentStyleTemplateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof archiveDocumentStyleTemplate>>, ArchiveDocumentStyleTemplateMutationVariables> = (props) => {
          const {templateId,data} = props ?? {};

          return  archiveDocumentStyleTemplate(templateId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ArchiveDocumentStyleTemplateMutationResult = NonNullable<Awaited<ReturnType<typeof archiveDocumentStyleTemplate>>>
    export type ArchiveDocumentStyleTemplateMutationBody = DocumentNamedStyleArchive
    export type ArchiveDocumentStyleTemplateMutationError = Error
    export type ArchiveDocumentStyleTemplateMutationVariables = {templateId: string;data: DocumentNamedStyleArchive}

    /**
 * @summary Withdraw a style template; documents that applied it keep their copy
 */
export const useArchiveDocumentStyleTemplate = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof archiveDocumentStyleTemplate>>, TError,ArchiveDocumentStyleTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof archiveDocumentStyleTemplate>>,
        TError,
        ArchiveDocumentStyleTemplateMutationVariables,
        TContext
      > => {
      return useMutation(getArchiveDocumentStyleTemplateMutationOptions(options));
    }

export const getListContentDocumentBindingsUrl = (params?: ListContentDocumentBindingsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/document-content-bindings?${stringifiedParams}` : `/api/document-content-bindings`
}

/**
 * @summary Managed documents that author Content Hub assets or microsite rich-content blocks
 */
export const listContentDocumentBindings = async (params?: ListContentDocumentBindingsParams, options?: Parameters<typeof customFetch>[1]): Promise<ContentDocumentBindingPage> => {

  return customFetch<ContentDocumentBindingPage>(getListContentDocumentBindingsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListContentDocumentBindingsQueryKey = (params?: ListContentDocumentBindingsParams,) => {
    return [
    `/api/document-content-bindings`, ...(params ? [params] : [])
    ] as const;
    }


export const getListContentDocumentBindingsQueryOptions = <TData = Awaited<ReturnType<typeof listContentDocumentBindings>>, TError = Error>(params?: ListContentDocumentBindingsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentDocumentBindings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListContentDocumentBindingsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listContentDocumentBindings>>> = ({ signal }) => listContentDocumentBindings(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listContentDocumentBindings>>, TError, TData> & { queryKey: QueryKey }
}

export type ListContentDocumentBindingsQueryResult = NonNullable<Awaited<ReturnType<typeof listContentDocumentBindings>>>
export type ListContentDocumentBindingsQueryError = Error


/**
 * @summary Managed documents that author Content Hub assets or microsite rich-content blocks
 */

export function useListContentDocumentBindings<TData = Awaited<ReturnType<typeof listContentDocumentBindings>>, TError = Error>(
 params?: ListContentDocumentBindingsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentDocumentBindings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListContentDocumentBindingsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getOpenContentDocumentUrl = () => {




  return `/api/document-content-bindings`
}

/**
 * Returns the existing authoring document, or creates one from the target's current content. The target itself is unchanged.
 * @summary Open a Content Hub asset or microsite rich-content block in the rich document editor
 */
export const openContentDocument = async (openContentDocumentBody: OpenContentDocument, options?: Parameters<typeof customFetch>[1]): Promise<ContentDocumentBinding> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentDocumentBinding>(getOpenContentDocumentUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(openContentDocumentBody)
  }
);}





export const getOpenContentDocumentMutationKey = () => ['openContentDocument'] as const;

export const getOpenContentDocumentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof openContentDocument>>, TError,OpenContentDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof openContentDocument>>, TError,OpenContentDocumentMutationVariables, TContext> => {

const mutationKey = getOpenContentDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof openContentDocument>>, OpenContentDocumentMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  openContentDocument(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OpenContentDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof openContentDocument>>>
    export type OpenContentDocumentMutationBody = OpenContentDocument
    export type OpenContentDocumentMutationError = Error
    export type OpenContentDocumentMutationVariables = {data: OpenContentDocument}

    /**
 * @summary Open a Content Hub asset or microsite rich-content block in the rich document editor
 */
export const useOpenContentDocument = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof openContentDocument>>, TError,OpenContentDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof openContentDocument>>,
        TError,
        OpenContentDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getOpenContentDocumentMutationOptions(options));
    }

export const getApplyContentDocumentUrl = (bindingId: string,) => {




  return `/api/document-content-bindings/${bindingId}/apply`
}

/**
 * Content Hub assets receive Markdown and return to draft for review; microsite blocks receive sanitized HTML in the draft. Publishing stays a separate step.
 * @summary Write the authoring document back to its target as a new draft
 */
export const applyContentDocument = async (bindingId: string,
    applyContentDocumentBody: ApplyContentDocument, options?: Parameters<typeof customFetch>[1]): Promise<ApplyContentDocumentResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ApplyContentDocumentResult>(getApplyContentDocumentUrl(bindingId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(applyContentDocumentBody)
  }
);}





export const getApplyContentDocumentMutationKey = () => ['applyContentDocument'] as const;

export const getApplyContentDocumentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyContentDocument>>, TError,ApplyContentDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof applyContentDocument>>, TError,ApplyContentDocumentMutationVariables, TContext> => {

const mutationKey = getApplyContentDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof applyContentDocument>>, ApplyContentDocumentMutationVariables> = (props) => {
          const {bindingId,data} = props ?? {};

          return  applyContentDocument(bindingId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApplyContentDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof applyContentDocument>>>
    export type ApplyContentDocumentMutationBody = ApplyContentDocument
    export type ApplyContentDocumentMutationError = Error
    export type ApplyContentDocumentMutationVariables = {bindingId: string;data: ApplyContentDocument}

    /**
 * @summary Write the authoring document back to its target as a new draft
 */
export const useApplyContentDocument = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyContentDocument>>, TError,ApplyContentDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof applyContentDocument>>,
        TError,
        ApplyContentDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getApplyContentDocumentMutationOptions(options));
    }

export const getCreateTranscriptCleanupUrl = () => {




  return `/api/transcript-cleanups`
}

/**
 * Writes nothing to any document. The proposal is private to the requester; the person reviews the tidied text and inserts it through the editor's normal save. A requestId replay returns the original proposal.
 * @summary Ask AI to tidy dictated text into a proposal for review
 */
export const createTranscriptCleanup = async (transcriptCleanupCreate: TranscriptCleanupCreate, options?: Parameters<typeof customFetch>[1]): Promise<TranscriptCleanup> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TranscriptCleanup>(getCreateTranscriptCleanupUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(transcriptCleanupCreate)
  }
);}





export const getCreateTranscriptCleanupMutationKey = () => ['createTranscriptCleanup'] as const;

export const getCreateTranscriptCleanupMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTranscriptCleanup>>, TError,CreateTranscriptCleanupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTranscriptCleanup>>, TError,CreateTranscriptCleanupMutationVariables, TContext> => {

const mutationKey = getCreateTranscriptCleanupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTranscriptCleanup>>, CreateTranscriptCleanupMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createTranscriptCleanup(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTranscriptCleanupMutationResult = NonNullable<Awaited<ReturnType<typeof createTranscriptCleanup>>>
    export type CreateTranscriptCleanupMutationBody = TranscriptCleanupCreate
    export type CreateTranscriptCleanupMutationError = Error
    export type CreateTranscriptCleanupMutationVariables = {data: TranscriptCleanupCreate}

    /**
 * @summary Ask AI to tidy dictated text into a proposal for review
 */
export const useCreateTranscriptCleanup = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTranscriptCleanup>>, TError,CreateTranscriptCleanupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createTranscriptCleanup>>,
        TError,
        CreateTranscriptCleanupMutationVariables,
        TContext
      > => {
      return useMutation(getCreateTranscriptCleanupMutationOptions(options));
    }

export const getDecideTranscriptCleanupUrl = (cleanupId: string,) => {




  return `/api/transcript-cleanups/${cleanupId}/decision`
}

/**
 * @summary Record whether the requester inserted or discarded a clean-up proposal
 */
export const decideTranscriptCleanup = async (cleanupId: string,
    transcriptCleanupDecision: TranscriptCleanupDecision, options?: Parameters<typeof customFetch>[1]): Promise<TranscriptCleanup> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TranscriptCleanup>(getDecideTranscriptCleanupUrl(cleanupId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(transcriptCleanupDecision)
  }
);}





export const getDecideTranscriptCleanupMutationKey = () => ['decideTranscriptCleanup'] as const;

export const getDecideTranscriptCleanupMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideTranscriptCleanup>>, TError,DecideTranscriptCleanupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof decideTranscriptCleanup>>, TError,DecideTranscriptCleanupMutationVariables, TContext> => {

const mutationKey = getDecideTranscriptCleanupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof decideTranscriptCleanup>>, DecideTranscriptCleanupMutationVariables> = (props) => {
          const {cleanupId,data} = props ?? {};

          return  decideTranscriptCleanup(cleanupId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DecideTranscriptCleanupMutationResult = NonNullable<Awaited<ReturnType<typeof decideTranscriptCleanup>>>
    export type DecideTranscriptCleanupMutationBody = TranscriptCleanupDecision
    export type DecideTranscriptCleanupMutationError = Error
    export type DecideTranscriptCleanupMutationVariables = {cleanupId: string;data: TranscriptCleanupDecision}

    /**
 * @summary Record whether the requester inserted or discarded a clean-up proposal
 */
export const useDecideTranscriptCleanup = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideTranscriptCleanup>>, TError,DecideTranscriptCleanupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof decideTranscriptCleanup>>,
        TError,
        DecideTranscriptCleanupMutationVariables,
        TContext
      > => {
      return useMutation(getDecideTranscriptCleanupMutationOptions(options));
    }

export const getGetManagedDocumentVersionUrl = (id: string,
    versionId: string,) => {




  return `/api/managed-documents/${id}/versions/${versionId}`
}

/**
 * @summary Read one exact authorized immutable snapshot
 */
export const getManagedDocumentVersion = async (id: string,
    versionId: string, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentVersion> => {

  return customFetch<ManagedDocumentVersion>(getGetManagedDocumentVersionUrl(id,versionId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetManagedDocumentVersionQueryKey = (id: string,
    versionId: string,) => {
    return [
    `/api/managed-documents/${id}/versions/${versionId}`
    ] as const;
    }


export const getGetManagedDocumentVersionQueryOptions = <TData = Awaited<ReturnType<typeof getManagedDocumentVersion>>, TError = unknown>(id: string,
    versionId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getManagedDocumentVersion>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetManagedDocumentVersionQueryKey(id,versionId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getManagedDocumentVersion>>> = ({ signal }) => getManagedDocumentVersion(id,versionId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && versionId !== null && versionId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getManagedDocumentVersion>>, TError, TData> & { queryKey: QueryKey }
}

export type GetManagedDocumentVersionQueryResult = NonNullable<Awaited<ReturnType<typeof getManagedDocumentVersion>>>
export type GetManagedDocumentVersionQueryError = unknown


/**
 * @summary Read one exact authorized immutable snapshot
 */

export function useGetManagedDocumentVersion<TData = Awaited<ReturnType<typeof getManagedDocumentVersion>>, TError = unknown>(
 id: string,
    versionId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getManagedDocumentVersion>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetManagedDocumentVersionQueryOptions(id,versionId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRestoreManagedDocumentVersionUrl = (id: string,
    versionId: string,) => {




  return `/api/managed-documents/${id}/versions/${versionId}/restore`
}

/**
 * @summary Restore a historical snapshot into a new draft using the reviewed current revision
 */
export const restoreManagedDocumentVersion = async (id: string,
    versionId: string,
    restoreManagedDocumentVersionBody: RestoreManagedDocumentVersionBody, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentWorkspace> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ManagedDocumentWorkspace>(getRestoreManagedDocumentVersionUrl(id,versionId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(restoreManagedDocumentVersionBody)
  }
);}





export const getRestoreManagedDocumentVersionMutationKey = () => ['restoreManagedDocumentVersion'] as const;

export const getRestoreManagedDocumentVersionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof restoreManagedDocumentVersion>>, TError,RestoreManagedDocumentVersionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof restoreManagedDocumentVersion>>, TError,RestoreManagedDocumentVersionMutationVariables, TContext> => {

const mutationKey = getRestoreManagedDocumentVersionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof restoreManagedDocumentVersion>>, RestoreManagedDocumentVersionMutationVariables> = (props) => {
          const {id,versionId,data} = props ?? {};

          return  restoreManagedDocumentVersion(id,versionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RestoreManagedDocumentVersionMutationResult = NonNullable<Awaited<ReturnType<typeof restoreManagedDocumentVersion>>>
    export type RestoreManagedDocumentVersionMutationBody = RestoreManagedDocumentVersionBody
    export type RestoreManagedDocumentVersionMutationError = unknown
    export type RestoreManagedDocumentVersionMutationVariables = {id: string;versionId: string;data: RestoreManagedDocumentVersionBody}

    /**
 * @summary Restore a historical snapshot into a new draft using the reviewed current revision
 */
export const useRestoreManagedDocumentVersion = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof restoreManagedDocumentVersion>>, TError,RestoreManagedDocumentVersionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof restoreManagedDocumentVersion>>,
        TError,
        RestoreManagedDocumentVersionMutationVariables,
        TContext
      > => {
      return useMutation(getRestoreManagedDocumentVersionMutationOptions(options));
    }

export const getListManagedDocumentTemplatesUrl = (params?: ListManagedDocumentTemplatesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/managed-document-templates?${stringifiedParams}` : `/api/managed-document-templates`
}

/**
 * @summary Page currently eligible ordinary templates in this entity
 */
export const listManagedDocumentTemplates = async (params?: ListManagedDocumentTemplatesParams, options?: Parameters<typeof customFetch>[1]): Promise<ListManagedDocumentTemplates200> => {

  return customFetch<ListManagedDocumentTemplates200>(getListManagedDocumentTemplatesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListManagedDocumentTemplatesQueryKey = (params?: ListManagedDocumentTemplatesParams,) => {
    return [
    `/api/managed-document-templates`, ...(params ? [params] : [])
    ] as const;
    }


export const getListManagedDocumentTemplatesQueryOptions = <TData = Awaited<ReturnType<typeof listManagedDocumentTemplates>>, TError = unknown>(params?: ListManagedDocumentTemplatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listManagedDocumentTemplates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListManagedDocumentTemplatesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listManagedDocumentTemplates>>> = ({ signal }) => listManagedDocumentTemplates(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listManagedDocumentTemplates>>, TError, TData> & { queryKey: QueryKey }
}

export type ListManagedDocumentTemplatesQueryResult = NonNullable<Awaited<ReturnType<typeof listManagedDocumentTemplates>>>
export type ListManagedDocumentTemplatesQueryError = unknown


/**
 * @summary Page currently eligible ordinary templates in this entity
 */

export function useListManagedDocumentTemplates<TData = Awaited<ReturnType<typeof listManagedDocumentTemplates>>, TError = unknown>(
 params?: ListManagedDocumentTemplatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listManagedDocumentTemplates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListManagedDocumentTemplatesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPreviewManagedDocumentTemplateUrl = (id: string,
    params: PreviewManagedDocumentTemplateParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/managed-documents/${id}/template-preview?${stringifiedParams}` : `/api/managed-documents/${id}/template-preview`
}

/**
 * @summary Prepare a clean native reusable-template review without creating a document
 */
export const previewManagedDocumentTemplate = async (id: string,
    params: PreviewManagedDocumentTemplateParams, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentImportPreview> => {

  return customFetch<ManagedDocumentImportPreview>(getPreviewManagedDocumentTemplateUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPreviewManagedDocumentTemplateQueryKey = (id: string,
    params?: PreviewManagedDocumentTemplateParams,) => {
    return [
    `/api/managed-documents/${id}/template-preview`, ...(params ? [params] : [])
    ] as const;
    }


export const getPreviewManagedDocumentTemplateQueryOptions = <TData = Awaited<ReturnType<typeof previewManagedDocumentTemplate>>, TError = void>(id: string,
    params: PreviewManagedDocumentTemplateParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof previewManagedDocumentTemplate>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPreviewManagedDocumentTemplateQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof previewManagedDocumentTemplate>>> = ({ signal }) => previewManagedDocumentTemplate(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof previewManagedDocumentTemplate>>, TError, TData> & { queryKey: QueryKey }
}

export type PreviewManagedDocumentTemplateQueryResult = NonNullable<Awaited<ReturnType<typeof previewManagedDocumentTemplate>>>
export type PreviewManagedDocumentTemplateQueryError = void


/**
 * @summary Prepare a clean native reusable-template review without creating a document
 */

export function usePreviewManagedDocumentTemplate<TData = Awaited<ReturnType<typeof previewManagedDocumentTemplate>>, TError = void>(
 id: string,
    params: PreviewManagedDocumentTemplateParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof previewManagedDocumentTemplate>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getPreviewManagedDocumentTemplateQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDownloadManagedDocumentDraftUrl = (id: string,
    params: DownloadManagedDocumentDraftParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/managed-documents/${id}/download?${stringifiedParams}` : `/api/managed-documents/${id}/download`
}

/**
 * @summary Export the exact saved working revision without creating a checkpoint
 */
export const downloadManagedDocumentDraft = async (id: string,
    params: DownloadManagedDocumentDraftParams, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadManagedDocumentDraftUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadManagedDocumentDraftQueryKey = (id: string,
    params?: DownloadManagedDocumentDraftParams,) => {
    return [
    `/api/managed-documents/${id}/download`, ...(params ? [params] : [])
    ] as const;
    }


export const getDownloadManagedDocumentDraftQueryOptions = <TData = Awaited<ReturnType<typeof downloadManagedDocumentDraft>>, TError = Error>(id: string,
    params: DownloadManagedDocumentDraftParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadManagedDocumentDraft>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadManagedDocumentDraftQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadManagedDocumentDraft>>> = ({ signal }) => downloadManagedDocumentDraft(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadManagedDocumentDraft>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadManagedDocumentDraftQueryResult = NonNullable<Awaited<ReturnType<typeof downloadManagedDocumentDraft>>>
export type DownloadManagedDocumentDraftQueryError = Error


/**
 * @summary Export the exact saved working revision without creating a checkpoint
 */

export function useDownloadManagedDocumentDraft<TData = Awaited<ReturnType<typeof downloadManagedDocumentDraft>>, TError = Error>(
 id: string,
    params: DownloadManagedDocumentDraftParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadManagedDocumentDraft>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadManagedDocumentDraftQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDownloadManagedDocumentVersionUrl = (id: string,
    versionId: string,
    params: DownloadManagedDocumentVersionParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/managed-documents/${id}/versions/${versionId}/download?${stringifiedParams}` : `/api/managed-documents/${id}/versions/${versionId}/download`
}

/**
 * @summary Export an immutable version as DOCX, PDF or Markdown
 */
export const downloadManagedDocumentVersion = async (id: string,
    versionId: string,
    params: DownloadManagedDocumentVersionParams, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadManagedDocumentVersionUrl(id,versionId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadManagedDocumentVersionQueryKey = (id: string,
    versionId: string,
    params?: DownloadManagedDocumentVersionParams,) => {
    return [
    `/api/managed-documents/${id}/versions/${versionId}/download`, ...(params ? [params] : [])
    ] as const;
    }


export const getDownloadManagedDocumentVersionQueryOptions = <TData = Awaited<ReturnType<typeof downloadManagedDocumentVersion>>, TError = unknown>(id: string,
    versionId: string,
    params: DownloadManagedDocumentVersionParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadManagedDocumentVersion>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadManagedDocumentVersionQueryKey(id,versionId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadManagedDocumentVersion>>> = ({ signal }) => downloadManagedDocumentVersion(id,versionId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && versionId !== null && versionId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadManagedDocumentVersion>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadManagedDocumentVersionQueryResult = NonNullable<Awaited<ReturnType<typeof downloadManagedDocumentVersion>>>
export type DownloadManagedDocumentVersionQueryError = unknown


/**
 * @summary Export an immutable version as DOCX, PDF or Markdown
 */

export function useDownloadManagedDocumentVersion<TData = Awaited<ReturnType<typeof downloadManagedDocumentVersion>>, TError = unknown>(
 id: string,
    versionId: string,
    params: DownloadManagedDocumentVersionParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadManagedDocumentVersion>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadManagedDocumentVersionQueryOptions(id,versionId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSubmitManagedDocumentReviewUrl = (id: string,) => {




  return `/api/managed-documents/${id}/reviews`
}

/**
 * @summary Submit the exact current content for publication review
 */
export const submitManagedDocumentReview = async (id: string,
    managedDocumentReason: ManagedDocumentReason, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ManagedDocumentReview>(getSubmitManagedDocumentReviewUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(managedDocumentReason)
  }
);}





export const getSubmitManagedDocumentReviewMutationKey = () => ['submitManagedDocumentReview'] as const;

export const getSubmitManagedDocumentReviewMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitManagedDocumentReview>>, TError,SubmitManagedDocumentReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof submitManagedDocumentReview>>, TError,SubmitManagedDocumentReviewMutationVariables, TContext> => {

const mutationKey = getSubmitManagedDocumentReviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof submitManagedDocumentReview>>, SubmitManagedDocumentReviewMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  submitManagedDocumentReview(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SubmitManagedDocumentReviewMutationResult = NonNullable<Awaited<ReturnType<typeof submitManagedDocumentReview>>>
    export type SubmitManagedDocumentReviewMutationBody = ManagedDocumentReason
    export type SubmitManagedDocumentReviewMutationError = unknown
    export type SubmitManagedDocumentReviewMutationVariables = {id: string;data: ManagedDocumentReason}

    /**
 * @summary Submit the exact current content for publication review
 */
export const useSubmitManagedDocumentReview = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitManagedDocumentReview>>, TError,SubmitManagedDocumentReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof submitManagedDocumentReview>>,
        TError,
        SubmitManagedDocumentReviewMutationVariables,
        TContext
      > => {
      return useMutation(getSubmitManagedDocumentReviewMutationOptions(options));
    }

export const getDecideManagedDocumentReviewUrl = (id: string,) => {




  return `/api/document-reviews/${id}/decision`
}

/**
 * @summary Approve, return, cancel, or reverse an exact-version review
 */
export const decideManagedDocumentReview = async (id: string,
    managedDocumentReviewDecisionInput: ManagedDocumentReviewDecisionInput, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ManagedDocumentReview>(getDecideManagedDocumentReviewUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(managedDocumentReviewDecisionInput)
  }
);}





export const getDecideManagedDocumentReviewMutationKey = () => ['decideManagedDocumentReview'] as const;

export const getDecideManagedDocumentReviewMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideManagedDocumentReview>>, TError,DecideManagedDocumentReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof decideManagedDocumentReview>>, TError,DecideManagedDocumentReviewMutationVariables, TContext> => {

const mutationKey = getDecideManagedDocumentReviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof decideManagedDocumentReview>>, DecideManagedDocumentReviewMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  decideManagedDocumentReview(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DecideManagedDocumentReviewMutationResult = NonNullable<Awaited<ReturnType<typeof decideManagedDocumentReview>>>
    export type DecideManagedDocumentReviewMutationBody = ManagedDocumentReviewDecisionInput
    export type DecideManagedDocumentReviewMutationError = unknown
    export type DecideManagedDocumentReviewMutationVariables = {id: string;data: ManagedDocumentReviewDecisionInput}

    /**
 * @summary Approve, return, cancel, or reverse an exact-version review
 */
export const useDecideManagedDocumentReview = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideManagedDocumentReview>>, TError,DecideManagedDocumentReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof decideManagedDocumentReview>>,
        TError,
        DecideManagedDocumentReviewMutationVariables,
        TContext
      > => {
      return useMutation(getDecideManagedDocumentReviewMutationOptions(options));
    }

export const getCommentManagedDocumentReviewUrl = (id: string,) => {




  return `/api/document-reviews/${id}/comments`
}

/**
 * @summary Add a version-bound review comment
 */
export const commentManagedDocumentReview = async (id: string,
    managedDocumentReviewCommentInput: ManagedDocumentReviewCommentInput, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentReviewComment> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ManagedDocumentReviewComment>(getCommentManagedDocumentReviewUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(managedDocumentReviewCommentInput)
  }
);}





export const getCommentManagedDocumentReviewMutationKey = () => ['commentManagedDocumentReview'] as const;

export const getCommentManagedDocumentReviewMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof commentManagedDocumentReview>>, TError,CommentManagedDocumentReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof commentManagedDocumentReview>>, TError,CommentManagedDocumentReviewMutationVariables, TContext> => {

const mutationKey = getCommentManagedDocumentReviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof commentManagedDocumentReview>>, CommentManagedDocumentReviewMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  commentManagedDocumentReview(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CommentManagedDocumentReviewMutationResult = NonNullable<Awaited<ReturnType<typeof commentManagedDocumentReview>>>
    export type CommentManagedDocumentReviewMutationBody = ManagedDocumentReviewCommentInput
    export type CommentManagedDocumentReviewMutationError = unknown
    export type CommentManagedDocumentReviewMutationVariables = {id: string;data: ManagedDocumentReviewCommentInput}

    /**
 * @summary Add a version-bound review comment
 */
export const useCommentManagedDocumentReview = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof commentManagedDocumentReview>>, TError,CommentManagedDocumentReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof commentManagedDocumentReview>>,
        TError,
        CommentManagedDocumentReviewMutationVariables,
        TContext
      > => {
      return useMutation(getCommentManagedDocumentReviewMutationOptions(options));
    }

export const getGetDocumentApprovalPolicyUrl = () => {




  return `/api/document-approval-policy`
}

/**
 * @summary Get the active entity publication approval policy
 */
export const getDocumentApprovalPolicy = async ( options?: Parameters<typeof customFetch>[1]): Promise<DocumentApprovalPolicy> => {

  return customFetch<DocumentApprovalPolicy>(getGetDocumentApprovalPolicyUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDocumentApprovalPolicyQueryKey = () => {
    return [
    `/api/document-approval-policy`
    ] as const;
    }


export const getGetDocumentApprovalPolicyQueryOptions = <TData = Awaited<ReturnType<typeof getDocumentApprovalPolicy>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentApprovalPolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDocumentApprovalPolicyQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDocumentApprovalPolicy>>> = ({ signal }) => getDocumentApprovalPolicy({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDocumentApprovalPolicy>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDocumentApprovalPolicyQueryResult = NonNullable<Awaited<ReturnType<typeof getDocumentApprovalPolicy>>>
export type GetDocumentApprovalPolicyQueryError = unknown


/**
 * @summary Get the active entity publication approval policy
 */

export function useGetDocumentApprovalPolicy<TData = Awaited<ReturnType<typeof getDocumentApprovalPolicy>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentApprovalPolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDocumentApprovalPolicyQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateDocumentApprovalPolicyUrl = () => {




  return `/api/document-approval-policy`
}

/**
 * @summary Replace the entity policy using optimistic concurrency
 */
export const updateDocumentApprovalPolicy = async (documentApprovalPolicyUpdate: DocumentApprovalPolicyUpdate, options?: Parameters<typeof customFetch>[1]): Promise<DocumentApprovalPolicy> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentApprovalPolicy>(getUpdateDocumentApprovalPolicyUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(documentApprovalPolicyUpdate)
  }
);}





export const getUpdateDocumentApprovalPolicyMutationKey = () => ['updateDocumentApprovalPolicy'] as const;

export const getUpdateDocumentApprovalPolicyMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDocumentApprovalPolicy>>, TError,UpdateDocumentApprovalPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateDocumentApprovalPolicy>>, TError,UpdateDocumentApprovalPolicyMutationVariables, TContext> => {

const mutationKey = getUpdateDocumentApprovalPolicyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateDocumentApprovalPolicy>>, UpdateDocumentApprovalPolicyMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateDocumentApprovalPolicy(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateDocumentApprovalPolicyMutationResult = NonNullable<Awaited<ReturnType<typeof updateDocumentApprovalPolicy>>>
    export type UpdateDocumentApprovalPolicyMutationBody = DocumentApprovalPolicyUpdate
    export type UpdateDocumentApprovalPolicyMutationError = Error
    export type UpdateDocumentApprovalPolicyMutationVariables = {data: DocumentApprovalPolicyUpdate}

    /**
 * @summary Replace the entity policy using optimistic concurrency
 */
export const useUpdateDocumentApprovalPolicy = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDocumentApprovalPolicy>>, TError,UpdateDocumentApprovalPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateDocumentApprovalPolicy>>,
        TError,
        UpdateDocumentApprovalPolicyMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateDocumentApprovalPolicyMutationOptions(options));
    }

export const getListDocumentConversationsUrl = (id: string,
    params?: ListDocumentConversationsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/managed-documents/${id}/conversations?${stringifiedParams}` : `/api/managed-documents/${id}/conversations`
}

/**
 * @summary Read authorized shared and own personal document conversations without creating
 */
export const listDocumentConversations = async (id: string,
    params?: ListDocumentConversationsParams, options?: Parameters<typeof customFetch>[1]): Promise<DocumentConversationList> => {

  return customFetch<DocumentConversationList>(getListDocumentConversationsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDocumentConversationsQueryKey = (id: string,
    params?: ListDocumentConversationsParams,) => {
    return [
    `/api/managed-documents/${id}/conversations`, ...(params ? [params] : [])
    ] as const;
    }


export const getListDocumentConversationsQueryOptions = <TData = Awaited<ReturnType<typeof listDocumentConversations>>, TError = unknown>(id: string,
    params?: ListDocumentConversationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentConversations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDocumentConversationsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDocumentConversations>>> = ({ signal }) => listDocumentConversations(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDocumentConversations>>, TError, TData> & { queryKey: QueryKey }
}

export type ListDocumentConversationsQueryResult = NonNullable<Awaited<ReturnType<typeof listDocumentConversations>>>
export type ListDocumentConversationsQueryError = unknown


/**
 * @summary Read authorized shared and own personal document conversations without creating
 */

export function useListDocumentConversations<TData = Awaited<ReturnType<typeof listDocumentConversations>>, TError = unknown>(
 id: string,
    params?: ListDocumentConversationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentConversations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListDocumentConversationsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateDocumentConversationUrl = (id: string,) => {




  return `/api/managed-documents/${id}/conversations`
}

/**
 * @summary Explicitly create an immutable audience and document bound conversation
 */
export const createDocumentConversation = async (id: string,
    documentConversationCreate: DocumentConversationCreate, options?: Parameters<typeof customFetch>[1]): Promise<DocumentConversation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentConversation>(getCreateDocumentConversationUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(documentConversationCreate)
  }
);}





export const getCreateDocumentConversationMutationKey = () => ['createDocumentConversation'] as const;

export const getCreateDocumentConversationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDocumentConversation>>, TError,CreateDocumentConversationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createDocumentConversation>>, TError,CreateDocumentConversationMutationVariables, TContext> => {

const mutationKey = getCreateDocumentConversationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createDocumentConversation>>, CreateDocumentConversationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createDocumentConversation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateDocumentConversationMutationResult = NonNullable<Awaited<ReturnType<typeof createDocumentConversation>>>
    export type CreateDocumentConversationMutationBody = DocumentConversationCreate
    export type CreateDocumentConversationMutationError = unknown
    export type CreateDocumentConversationMutationVariables = {id: string;data: DocumentConversationCreate}

    /**
 * @summary Explicitly create an immutable audience and document bound conversation
 */
export const useCreateDocumentConversation = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDocumentConversation>>, TError,CreateDocumentConversationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createDocumentConversation>>,
        TError,
        CreateDocumentConversationMutationVariables,
        TContext
      > => {
      return useMutation(getCreateDocumentConversationMutationOptions(options));
    }

export const getGetDocumentConversationCreationOutcomeUrl = (id: string,
    requestId: string,) => {




  return `/api/managed-documents/${id}/conversation-outcomes/${requestId}`
}

/**
 * @summary Read the original actor owned conversation creation outcome
 */
export const getDocumentConversationCreationOutcome = async (id: string,
    requestId: string, options?: Parameters<typeof customFetch>[1]): Promise<DocumentConversation> => {

  return customFetch<DocumentConversation>(getGetDocumentConversationCreationOutcomeUrl(id,requestId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDocumentConversationCreationOutcomeQueryKey = (id: string,
    requestId: string,) => {
    return [
    `/api/managed-documents/${id}/conversation-outcomes/${requestId}`
    ] as const;
    }


export const getGetDocumentConversationCreationOutcomeQueryOptions = <TData = Awaited<ReturnType<typeof getDocumentConversationCreationOutcome>>, TError = unknown>(id: string,
    requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentConversationCreationOutcome>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDocumentConversationCreationOutcomeQueryKey(id,requestId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDocumentConversationCreationOutcome>>> = ({ signal }) => getDocumentConversationCreationOutcome(id,requestId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && requestId !== null && requestId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDocumentConversationCreationOutcome>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDocumentConversationCreationOutcomeQueryResult = NonNullable<Awaited<ReturnType<typeof getDocumentConversationCreationOutcome>>>
export type GetDocumentConversationCreationOutcomeQueryError = unknown


/**
 * @summary Read the original actor owned conversation creation outcome
 */

export function useGetDocumentConversationCreationOutcome<TData = Awaited<ReturnType<typeof getDocumentConversationCreationOutcome>>, TError = unknown>(
 id: string,
    requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentConversationCreationOutcome>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDocumentConversationCreationOutcomeQueryOptions(id,requestId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetDocumentConversationUrl = (id: string,
    threadId: string,) => {




  return `/api/managed-documents/${id}/conversations/${threadId}`
}

/**
 * @summary Read exact current document and audience binding
 */
export const getDocumentConversation = async (id: string,
    threadId: string, options?: Parameters<typeof customFetch>[1]): Promise<DocumentConversation> => {

  return customFetch<DocumentConversation>(getGetDocumentConversationUrl(id,threadId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDocumentConversationQueryKey = (id: string,
    threadId: string,) => {
    return [
    `/api/managed-documents/${id}/conversations/${threadId}`
    ] as const;
    }


export const getGetDocumentConversationQueryOptions = <TData = Awaited<ReturnType<typeof getDocumentConversation>>, TError = unknown>(id: string,
    threadId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentConversation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDocumentConversationQueryKey(id,threadId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDocumentConversation>>> = ({ signal }) => getDocumentConversation(id,threadId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && threadId !== null && threadId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDocumentConversation>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDocumentConversationQueryResult = NonNullable<Awaited<ReturnType<typeof getDocumentConversation>>>
export type GetDocumentConversationQueryError = unknown


/**
 * @summary Read exact current document and audience binding
 */

export function useGetDocumentConversation<TData = Awaited<ReturnType<typeof getDocumentConversation>>, TError = unknown>(
 id: string,
    threadId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentConversation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDocumentConversationQueryOptions(id,threadId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListDocumentConversationMessagesUrl = (id: string,
    threadId: string,
    params?: ListDocumentConversationMessagesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/managed-documents/${id}/conversations/${threadId}/messages?${stringifiedParams}` : `/api/managed-documents/${id}/conversations/${threadId}/messages`
}

/**
 * @summary Read bounded stable document turn history with current source checks
 */
export const listDocumentConversationMessages = async (id: string,
    threadId: string,
    params?: ListDocumentConversationMessagesParams, options?: Parameters<typeof customFetch>[1]): Promise<DocumentConversationMessages> => {

  return customFetch<DocumentConversationMessages>(getListDocumentConversationMessagesUrl(id,threadId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDocumentConversationMessagesQueryKey = (id: string,
    threadId: string,
    params?: ListDocumentConversationMessagesParams,) => {
    return [
    `/api/managed-documents/${id}/conversations/${threadId}/messages`, ...(params ? [params] : [])
    ] as const;
    }


export const getListDocumentConversationMessagesQueryOptions = <TData = Awaited<ReturnType<typeof listDocumentConversationMessages>>, TError = unknown>(id: string,
    threadId: string,
    params?: ListDocumentConversationMessagesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentConversationMessages>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDocumentConversationMessagesQueryKey(id,threadId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDocumentConversationMessages>>> = ({ signal }) => listDocumentConversationMessages(id,threadId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && threadId !== null && threadId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDocumentConversationMessages>>, TError, TData> & { queryKey: QueryKey }
}

export type ListDocumentConversationMessagesQueryResult = NonNullable<Awaited<ReturnType<typeof listDocumentConversationMessages>>>
export type ListDocumentConversationMessagesQueryError = unknown


/**
 * @summary Read bounded stable document turn history with current source checks
 */

export function useListDocumentConversationMessages<TData = Awaited<ReturnType<typeof listDocumentConversationMessages>>, TError = unknown>(
 id: string,
    threadId: string,
    params?: ListDocumentConversationMessagesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentConversationMessages>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListDocumentConversationMessagesQueryOptions(id,threadId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetDocumentConversationActivityUrl = (id: string,
    threadId: string,) => {




  return `/api/managed-documents/${id}/conversations/${threadId}/activity`
}

/**
 * @summary Read durable revision and pending activity without generating
 */
export const getDocumentConversationActivity = async (id: string,
    threadId: string, options?: Parameters<typeof customFetch>[1]): Promise<DocumentConversationActivity> => {

  return customFetch<DocumentConversationActivity>(getGetDocumentConversationActivityUrl(id,threadId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDocumentConversationActivityQueryKey = (id: string,
    threadId: string,) => {
    return [
    `/api/managed-documents/${id}/conversations/${threadId}/activity`
    ] as const;
    }


export const getGetDocumentConversationActivityQueryOptions = <TData = Awaited<ReturnType<typeof getDocumentConversationActivity>>, TError = unknown>(id: string,
    threadId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentConversationActivity>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDocumentConversationActivityQueryKey(id,threadId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDocumentConversationActivity>>> = ({ signal }) => getDocumentConversationActivity(id,threadId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && threadId !== null && threadId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDocumentConversationActivity>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDocumentConversationActivityQueryResult = NonNullable<Awaited<ReturnType<typeof getDocumentConversationActivity>>>
export type GetDocumentConversationActivityQueryError = unknown


/**
 * @summary Read durable revision and pending activity without generating
 */

export function useGetDocumentConversationActivity<TData = Awaited<ReturnType<typeof getDocumentConversationActivity>>, TError = unknown>(
 id: string,
    threadId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentConversationActivity>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDocumentConversationActivityQueryOptions(id,threadId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSendDocumentConversationTurnUrl = (id: string,
    threadId: string,) => {




  return `/api/managed-documents/${id}/conversations/${threadId}/turns`
}

/**
 * @summary Reserve and run one explicit turn with one provider attempt and private native proposal
 */
export const sendDocumentConversationTurn = async (id: string,
    threadId: string,
    documentConversationTurnInput: DocumentConversationTurnInput, options?: Parameters<typeof customFetch>[1]): Promise<DocumentConversationOutcome> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentConversationOutcome>(getSendDocumentConversationTurnUrl(id,threadId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(documentConversationTurnInput)
  }
);}





export const getSendDocumentConversationTurnMutationKey = () => ['sendDocumentConversationTurn'] as const;

export const getSendDocumentConversationTurnMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendDocumentConversationTurn>>, TError,SendDocumentConversationTurnMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof sendDocumentConversationTurn>>, TError,SendDocumentConversationTurnMutationVariables, TContext> => {

const mutationKey = getSendDocumentConversationTurnMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof sendDocumentConversationTurn>>, SendDocumentConversationTurnMutationVariables> = (props) => {
          const {id,threadId,data} = props ?? {};

          return  sendDocumentConversationTurn(id,threadId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SendDocumentConversationTurnMutationResult = NonNullable<Awaited<ReturnType<typeof sendDocumentConversationTurn>>>
    export type SendDocumentConversationTurnMutationBody = DocumentConversationTurnInput
    export type SendDocumentConversationTurnMutationError = unknown
    export type SendDocumentConversationTurnMutationVariables = {id: string;threadId: string;data: DocumentConversationTurnInput}

    /**
 * @summary Reserve and run one explicit turn with one provider attempt and private native proposal
 */
export const useSendDocumentConversationTurn = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendDocumentConversationTurn>>, TError,SendDocumentConversationTurnMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof sendDocumentConversationTurn>>,
        TError,
        SendDocumentConversationTurnMutationVariables,
        TContext
      > => {
      return useMutation(getSendDocumentConversationTurnMutationOptions(options));
    }

export const getGetDocumentConversationTurnOutcomeUrl = (id: string,
    threadId: string,
    requestId: string,) => {




  return `/api/managed-documents/${id}/conversations/${threadId}/outcomes/${requestId}`
}

/**
 * @summary Recover the exact actor original without resending or provider work
 */
export const getDocumentConversationTurnOutcome = async (id: string,
    threadId: string,
    requestId: string, options?: Parameters<typeof customFetch>[1]): Promise<DocumentConversationOutcome> => {

  return customFetch<DocumentConversationOutcome>(getGetDocumentConversationTurnOutcomeUrl(id,threadId,requestId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDocumentConversationTurnOutcomeQueryKey = (id: string,
    threadId: string,
    requestId: string,) => {
    return [
    `/api/managed-documents/${id}/conversations/${threadId}/outcomes/${requestId}`
    ] as const;
    }


export const getGetDocumentConversationTurnOutcomeQueryOptions = <TData = Awaited<ReturnType<typeof getDocumentConversationTurnOutcome>>, TError = unknown>(id: string,
    threadId: string,
    requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentConversationTurnOutcome>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDocumentConversationTurnOutcomeQueryKey(id,threadId,requestId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDocumentConversationTurnOutcome>>> = ({ signal }) => getDocumentConversationTurnOutcome(id,threadId,requestId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && threadId !== null && threadId !== undefined && requestId !== null && requestId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDocumentConversationTurnOutcome>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDocumentConversationTurnOutcomeQueryResult = NonNullable<Awaited<ReturnType<typeof getDocumentConversationTurnOutcome>>>
export type GetDocumentConversationTurnOutcomeQueryError = unknown


/**
 * @summary Recover the exact actor original without resending or provider work
 */

export function useGetDocumentConversationTurnOutcome<TData = Awaited<ReturnType<typeof getDocumentConversationTurnOutcome>>, TError = unknown>(
 id: string,
    threadId: string,
    requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentConversationTurnOutcome>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDocumentConversationTurnOutcomeQueryOptions(id,threadId,requestId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRunDocumentAgentActionUrl = (id: string,) => {




  return `/api/managed-documents/${id}/agent-actions`
}

/**
 * @summary Run one confirmed native action suggested by the document agent through native permissions and audit
 */
export const runDocumentAgentAction = async (id: string,
    documentAgentActionInput: DocumentAgentActionInput, options?: Parameters<typeof customFetch>[1]): Promise<DocumentAgentActionResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentAgentActionResult>(getRunDocumentAgentActionUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(documentAgentActionInput)
  }
);}





export const getRunDocumentAgentActionMutationKey = () => ['runDocumentAgentAction'] as const;

export const getRunDocumentAgentActionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runDocumentAgentAction>>, TError,RunDocumentAgentActionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof runDocumentAgentAction>>, TError,RunDocumentAgentActionMutationVariables, TContext> => {

const mutationKey = getRunDocumentAgentActionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof runDocumentAgentAction>>, RunDocumentAgentActionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  runDocumentAgentAction(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RunDocumentAgentActionMutationResult = NonNullable<Awaited<ReturnType<typeof runDocumentAgentAction>>>
    export type RunDocumentAgentActionMutationBody = DocumentAgentActionInput
    export type RunDocumentAgentActionMutationError = Error
    export type RunDocumentAgentActionMutationVariables = {id: string;data: DocumentAgentActionInput}

    /**
 * @summary Run one confirmed native action suggested by the document agent through native permissions and audit
 */
export const useRunDocumentAgentAction = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runDocumentAgentAction>>, TError,RunDocumentAgentActionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof runDocumentAgentAction>>,
        TError,
        RunDocumentAgentActionMutationVariables,
        TContext
      > => {
      return useMutation(getRunDocumentAgentActionMutationOptions(options));
    }

export const getStopDocumentConversationTurnUrl = (id: string,
    threadId: string,
    turnId: string,) => {




  return `/api/managed-documents/${id}/conversations/${threadId}/turns/${turnId}/stop`
}

/**
 * @summary Persist Stop and fence late results for an authorized editor
 */
export const stopDocumentConversationTurn = async (id: string,
    threadId: string,
    turnId: string, options?: Parameters<typeof customFetch>[1]): Promise<DocumentConversationMessage> => {

  return customFetch<DocumentConversationMessage>(getStopDocumentConversationTurnUrl(id,threadId,turnId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getStopDocumentConversationTurnMutationKey = () => ['stopDocumentConversationTurn'] as const;

export const getStopDocumentConversationTurnMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof stopDocumentConversationTurn>>, TError,StopDocumentConversationTurnMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof stopDocumentConversationTurn>>, TError,StopDocumentConversationTurnMutationVariables, TContext> => {

const mutationKey = getStopDocumentConversationTurnMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof stopDocumentConversationTurn>>, StopDocumentConversationTurnMutationVariables> = (props) => {
          const {id,threadId,turnId} = props ?? {};

          return  stopDocumentConversationTurn(id,threadId,turnId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type StopDocumentConversationTurnMutationResult = NonNullable<Awaited<ReturnType<typeof stopDocumentConversationTurn>>>

    export type StopDocumentConversationTurnMutationError = unknown
    export type StopDocumentConversationTurnMutationVariables = {id: string;threadId: string;turnId: string}

    /**
 * @summary Persist Stop and fence late results for an authorized editor
 */
export const useStopDocumentConversationTurn = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof stopDocumentConversationTurn>>, TError,StopDocumentConversationTurnMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof stopDocumentConversationTurn>>,
        TError,
        StopDocumentConversationTurnMutationVariables,
        TContext
      > => {
      return useMutation(getStopDocumentConversationTurnMutationOptions(options));
    }

export const getCreateManagedDocumentAiProposalUrl = (id: string,) => {




  return `/api/managed-documents/${id}/ai-proposals`
}

/**
 * @summary Create a grounded inspectable authoring or review proposal
 */
export const createManagedDocumentAiProposal = async (id: string,
    managedDocumentAiProposalInput: ManagedDocumentAiProposalInput, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentAiProposal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ManagedDocumentAiProposal>(getCreateManagedDocumentAiProposalUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(managedDocumentAiProposalInput)
  }
);}





export const getCreateManagedDocumentAiProposalMutationKey = () => ['createManagedDocumentAiProposal'] as const;

export const getCreateManagedDocumentAiProposalMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createManagedDocumentAiProposal>>, TError,CreateManagedDocumentAiProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createManagedDocumentAiProposal>>, TError,CreateManagedDocumentAiProposalMutationVariables, TContext> => {

const mutationKey = getCreateManagedDocumentAiProposalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createManagedDocumentAiProposal>>, CreateManagedDocumentAiProposalMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createManagedDocumentAiProposal(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateManagedDocumentAiProposalMutationResult = NonNullable<Awaited<ReturnType<typeof createManagedDocumentAiProposal>>>
    export type CreateManagedDocumentAiProposalMutationBody = ManagedDocumentAiProposalInput
    export type CreateManagedDocumentAiProposalMutationError = Error
    export type CreateManagedDocumentAiProposalMutationVariables = {id: string;data: ManagedDocumentAiProposalInput}

    /**
 * @summary Create a grounded inspectable authoring or review proposal
 */
export const useCreateManagedDocumentAiProposal = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createManagedDocumentAiProposal>>, TError,CreateManagedDocumentAiProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createManagedDocumentAiProposal>>,
        TError,
        CreateManagedDocumentAiProposalMutationVariables,
        TContext
      > => {
      return useMutation(getCreateManagedDocumentAiProposalMutationOptions(options));
    }

export const getDecideManagedDocumentAiProposalUrl = (id: string,
    proposalId: string,) => {




  return `/api/managed-documents/${id}/ai-proposals/${proposalId}/decision`
}

/**
 * @summary Accept or reject an exact-revision AI proposal
 */
export const decideManagedDocumentAiProposal = async (id: string,
    proposalId: string,
    managedDocumentAiProposalDecision: ManagedDocumentAiProposalDecision, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentWorkspace> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ManagedDocumentWorkspace>(getDecideManagedDocumentAiProposalUrl(id,proposalId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(managedDocumentAiProposalDecision)
  }
);}





export const getDecideManagedDocumentAiProposalMutationKey = () => ['decideManagedDocumentAiProposal'] as const;

export const getDecideManagedDocumentAiProposalMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideManagedDocumentAiProposal>>, TError,DecideManagedDocumentAiProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof decideManagedDocumentAiProposal>>, TError,DecideManagedDocumentAiProposalMutationVariables, TContext> => {

const mutationKey = getDecideManagedDocumentAiProposalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof decideManagedDocumentAiProposal>>, DecideManagedDocumentAiProposalMutationVariables> = (props) => {
          const {id,proposalId,data} = props ?? {};

          return  decideManagedDocumentAiProposal(id,proposalId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DecideManagedDocumentAiProposalMutationResult = NonNullable<Awaited<ReturnType<typeof decideManagedDocumentAiProposal>>>
    export type DecideManagedDocumentAiProposalMutationBody = ManagedDocumentAiProposalDecision
    export type DecideManagedDocumentAiProposalMutationError = Error
    export type DecideManagedDocumentAiProposalMutationVariables = {id: string;proposalId: string;data: ManagedDocumentAiProposalDecision}

    /**
 * @summary Accept or reject an exact-revision AI proposal
 */
export const useDecideManagedDocumentAiProposal = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideManagedDocumentAiProposal>>, TError,DecideManagedDocumentAiProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof decideManagedDocumentAiProposal>>,
        TError,
        DecideManagedDocumentAiProposalMutationVariables,
        TContext
      > => {
      return useMutation(getDecideManagedDocumentAiProposalMutationOptions(options));
    }

export const getProofManagedDocumentUrl = (id: string,) => {




  return `/api/managed-documents/${id}/proofing`
}

/**
 * Governed by the `document_proofing` AI action. Accepts at most 8 plain-text paragraphs of up to 2,000 characters from the open document. Suggestions are returned for the person to accept or dismiss one by one; nothing is stored or applied to the document.
 * @summary Suggest AI spelling and grammar fixes for paragraphs of a managed document
 */
export const proofManagedDocument = async (id: string,
    managedDocumentProofingRequest: ManagedDocumentProofingRequest, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentProofing> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ManagedDocumentProofing>(getProofManagedDocumentUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(managedDocumentProofingRequest)
  }
);}





export const getProofManagedDocumentMutationKey = () => ['proofManagedDocument'] as const;

export const getProofManagedDocumentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof proofManagedDocument>>, TError,ProofManagedDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof proofManagedDocument>>, TError,ProofManagedDocumentMutationVariables, TContext> => {

const mutationKey = getProofManagedDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof proofManagedDocument>>, ProofManagedDocumentMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  proofManagedDocument(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ProofManagedDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof proofManagedDocument>>>
    export type ProofManagedDocumentMutationBody = ManagedDocumentProofingRequest
    export type ProofManagedDocumentMutationError = Error
    export type ProofManagedDocumentMutationVariables = {id: string;data: ManagedDocumentProofingRequest}

    /**
 * @summary Suggest AI spelling and grammar fixes for paragraphs of a managed document
 */
export const useProofManagedDocument = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof proofManagedDocument>>, TError,ProofManagedDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof proofManagedDocument>>,
        TError,
        ProofManagedDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getProofManagedDocumentMutationOptions(options));
    }

export const getEvaluateManagedDocumentAiApprovalUrl = (id: string,) => {




  return `/api/document-reviews/${id}/ai-evaluation`
}

/**
 * @summary Evaluate a pending review against entity evidence policy
 */
export const evaluateManagedDocumentAiApproval = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentAiEvaluation> => {

  return customFetch<ManagedDocumentAiEvaluation>(getEvaluateManagedDocumentAiApprovalUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getEvaluateManagedDocumentAiApprovalMutationKey = () => ['evaluateManagedDocumentAiApproval'] as const;

export const getEvaluateManagedDocumentAiApprovalMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof evaluateManagedDocumentAiApproval>>, TError,EvaluateManagedDocumentAiApprovalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof evaluateManagedDocumentAiApproval>>, TError,EvaluateManagedDocumentAiApprovalMutationVariables, TContext> => {

const mutationKey = getEvaluateManagedDocumentAiApprovalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof evaluateManagedDocumentAiApproval>>, EvaluateManagedDocumentAiApprovalMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  evaluateManagedDocumentAiApproval(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type EvaluateManagedDocumentAiApprovalMutationResult = NonNullable<Awaited<ReturnType<typeof evaluateManagedDocumentAiApproval>>>

    export type EvaluateManagedDocumentAiApprovalMutationError = unknown
    export type EvaluateManagedDocumentAiApprovalMutationVariables = {id: string}

    /**
 * @summary Evaluate a pending review against entity evidence policy
 */
export const useEvaluateManagedDocumentAiApproval = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof evaluateManagedDocumentAiApproval>>, TError,EvaluateManagedDocumentAiApprovalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof evaluateManagedDocumentAiApproval>>,
        TError,
        EvaluateManagedDocumentAiApprovalMutationVariables,
        TContext
      > => {
      return useMutation(getEvaluateManagedDocumentAiApprovalMutationOptions(options));
    }

export const getPublishManagedDocumentUrl = (id: string,) => {




  return `/api/managed-documents/${id}/publish`
}

/**
 * @summary Explicitly publish the latest valid approved snapshot
 */
export const publishManagedDocument = async (id: string,
    managedDocumentReason: ManagedDocumentReason, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentPublication> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ManagedDocumentPublication>(getPublishManagedDocumentUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(managedDocumentReason)
  }
);}





export const getPublishManagedDocumentMutationKey = () => ['publishManagedDocument'] as const;

export const getPublishManagedDocumentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof publishManagedDocument>>, TError,PublishManagedDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof publishManagedDocument>>, TError,PublishManagedDocumentMutationVariables, TContext> => {

const mutationKey = getPublishManagedDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof publishManagedDocument>>, PublishManagedDocumentMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  publishManagedDocument(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PublishManagedDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof publishManagedDocument>>>
    export type PublishManagedDocumentMutationBody = ManagedDocumentReason
    export type PublishManagedDocumentMutationError = Error
    export type PublishManagedDocumentMutationVariables = {id: string;data: ManagedDocumentReason}

    /**
 * @summary Explicitly publish the latest valid approved snapshot
 */
export const usePublishManagedDocument = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof publishManagedDocument>>, TError,PublishManagedDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof publishManagedDocument>>,
        TError,
        PublishManagedDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getPublishManagedDocumentMutationOptions(options));
    }

export const getChangeManagedDocumentPublicationStateUrl = (id: string,) => {




  return `/api/managed-documents/${id}/publication-state`
}

/**
 * @summary Unpublish, revoke, or archive a public document link
 */
export const changeManagedDocumentPublicationState = async (id: string,
    managedDocumentPublicationStateInput: ManagedDocumentPublicationStateInput, options?: Parameters<typeof customFetch>[1]): Promise<ManagedDocumentPublication> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ManagedDocumentPublication>(getChangeManagedDocumentPublicationStateUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(managedDocumentPublicationStateInput)
  }
);}





export const getChangeManagedDocumentPublicationStateMutationKey = () => ['changeManagedDocumentPublicationState'] as const;

export const getChangeManagedDocumentPublicationStateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof changeManagedDocumentPublicationState>>, TError,ChangeManagedDocumentPublicationStateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof changeManagedDocumentPublicationState>>, TError,ChangeManagedDocumentPublicationStateMutationVariables, TContext> => {

const mutationKey = getChangeManagedDocumentPublicationStateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof changeManagedDocumentPublicationState>>, ChangeManagedDocumentPublicationStateMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  changeManagedDocumentPublicationState(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ChangeManagedDocumentPublicationStateMutationResult = NonNullable<Awaited<ReturnType<typeof changeManagedDocumentPublicationState>>>
    export type ChangeManagedDocumentPublicationStateMutationBody = ManagedDocumentPublicationStateInput
    export type ChangeManagedDocumentPublicationStateMutationError = unknown
    export type ChangeManagedDocumentPublicationStateMutationVariables = {id: string;data: ManagedDocumentPublicationStateInput}

    /**
 * @summary Unpublish, revoke, or archive a public document link
 */
export const useChangeManagedDocumentPublicationState = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof changeManagedDocumentPublicationState>>, TError,ChangeManagedDocumentPublicationStateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof changeManagedDocumentPublicationState>>,
        TError,
        ChangeManagedDocumentPublicationStateMutationVariables,
        TContext
      > => {
      return useMutation(getChangeManagedDocumentPublicationStateMutationOptions(options));
    }

export const getGetPublicManagedDocumentUrl = (publicId: string,) => {




  return `/api/public/documents/${publicId}`
}

/**
 * @summary Read only the latest explicitly published major snapshot
 */
export const getPublicManagedDocument = async (publicId: string, options?: Parameters<typeof customFetch>[1]): Promise<PublicManagedDocument> => {

  return customFetch<PublicManagedDocument>(getGetPublicManagedDocumentUrl(publicId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPublicManagedDocumentQueryKey = (publicId: string,) => {
    return [
    `/api/public/documents/${publicId}`
    ] as const;
    }


export const getGetPublicManagedDocumentQueryOptions = <TData = Awaited<ReturnType<typeof getPublicManagedDocument>>, TError = Error>(publicId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicManagedDocument>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPublicManagedDocumentQueryKey(publicId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPublicManagedDocument>>> = ({ signal }) => getPublicManagedDocument(publicId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: publicId !== null && publicId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPublicManagedDocument>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPublicManagedDocumentQueryResult = NonNullable<Awaited<ReturnType<typeof getPublicManagedDocument>>>
export type GetPublicManagedDocumentQueryError = Error


/**
 * @summary Read only the latest explicitly published major snapshot
 */

export function useGetPublicManagedDocument<TData = Awaited<ReturnType<typeof getPublicManagedDocument>>, TError = Error>(
 publicId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicManagedDocument>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPublicManagedDocumentQueryOptions(publicId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDownloadPublicManagedDocumentUrl = (publicId: string,
    format: 'docx' | 'pdf',) => {




  return `/api/public/documents/${publicId}/download/${format}`
}

/**
 * @summary Download a public approved representation
 */
export const downloadPublicManagedDocument = async (publicId: string,
    format: 'docx' | 'pdf', options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadPublicManagedDocumentUrl(publicId,format),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadPublicManagedDocumentQueryKey = (publicId: string,
    format: 'docx' | 'pdf',) => {
    return [
    `/api/public/documents/${publicId}/download/${format}`
    ] as const;
    }


export const getDownloadPublicManagedDocumentQueryOptions = <TData = Awaited<ReturnType<typeof downloadPublicManagedDocument>>, TError = Error>(publicId: string,
    format: 'docx' | 'pdf', options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadPublicManagedDocument>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadPublicManagedDocumentQueryKey(publicId,format);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadPublicManagedDocument>>> = ({ signal }) => downloadPublicManagedDocument(publicId,format, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: publicId !== null && publicId !== undefined && format !== null && format !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadPublicManagedDocument>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadPublicManagedDocumentQueryResult = NonNullable<Awaited<ReturnType<typeof downloadPublicManagedDocument>>>
export type DownloadPublicManagedDocumentQueryError = Error


/**
 * @summary Download a public approved representation
 */

export function useDownloadPublicManagedDocument<TData = Awaited<ReturnType<typeof downloadPublicManagedDocument>>, TError = Error>(
 publicId: string,
    format: 'docx' | 'pdf', options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadPublicManagedDocument>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadPublicManagedDocumentQueryOptions(publicId,format,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPortalMeUrl = () => {




  return `/api/portal/me`
}

/**
 * @summary Signed-in portal identity and account match
 */
export const getPortalMe = async ( options?: Parameters<typeof customFetch>[1]): Promise<PortalMe> => {

  return customFetch<PortalMe>(getGetPortalMeUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPortalMeQueryKey = () => {
    return [
    `/api/portal/me`
    ] as const;
    }


export const getGetPortalMeQueryOptions = <TData = Awaited<ReturnType<typeof getPortalMe>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalMe>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPortalMeQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPortalMe>>> = ({ signal }) => getPortalMe({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPortalMe>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPortalMeQueryResult = NonNullable<Awaited<ReturnType<typeof getPortalMe>>>
export type GetPortalMeQueryError = Error


/**
 * @summary Signed-in portal identity and account match
 */

export function useGetPortalMe<TData = Awaited<ReturnType<typeof getPortalMe>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalMe>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPortalMeQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPortalOverviewUrl = () => {




  return `/api/portal/overview`
}

/**
 * @summary Account-scoped engagement overview for the signed-in portal user
 */
export const getPortalOverview = async ( options?: Parameters<typeof customFetch>[1]): Promise<PortalOverview> => {

  return customFetch<PortalOverview>(getGetPortalOverviewUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPortalOverviewQueryKey = () => {
    return [
    `/api/portal/overview`
    ] as const;
    }


export const getGetPortalOverviewQueryOptions = <TData = Awaited<ReturnType<typeof getPortalOverview>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalOverview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPortalOverviewQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPortalOverview>>> = ({ signal }) => getPortalOverview({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPortalOverview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPortalOverviewQueryResult = NonNullable<Awaited<ReturnType<typeof getPortalOverview>>>
export type GetPortalOverviewQueryError = Error


/**
 * @summary Account-scoped engagement overview for the signed-in portal user
 */

export function useGetPortalOverview<TData = Awaited<ReturnType<typeof getPortalOverview>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalOverview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPortalOverviewQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPortalProfileUrl = () => {




  return `/api/portal/profile`
}

/**
 * @summary External-safe profile for the signed-in portal user's entity
 */
export const getPortalProfile = async ( options?: Parameters<typeof customFetch>[1]): Promise<PortalProfileResponse> => {

  return customFetch<PortalProfileResponse>(getGetPortalProfileUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPortalProfileQueryKey = () => {
    return [
    `/api/portal/profile`
    ] as const;
    }


export const getGetPortalProfileQueryOptions = <TData = Awaited<ReturnType<typeof getPortalProfile>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalProfile>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPortalProfileQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPortalProfile>>> = ({ signal }) => getPortalProfile({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPortalProfile>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPortalProfileQueryResult = NonNullable<Awaited<ReturnType<typeof getPortalProfile>>>
export type GetPortalProfileQueryError = Error


/**
 * @summary External-safe profile for the signed-in portal user's entity
 */

export function useGetPortalProfile<TData = Awaited<ReturnType<typeof getPortalProfile>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalProfile>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPortalProfileQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPortalAgreementsUrl = () => {




  return `/api/portal/agreements`
}

/**
 * @summary Account-scoped agreements for the signed-in portal user
 */
export const getPortalAgreements = async ( options?: Parameters<typeof customFetch>[1]): Promise<PortalAgreementList> => {

  return customFetch<PortalAgreementList>(getGetPortalAgreementsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPortalAgreementsQueryKey = () => {
    return [
    `/api/portal/agreements`
    ] as const;
    }


export const getGetPortalAgreementsQueryOptions = <TData = Awaited<ReturnType<typeof getPortalAgreements>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalAgreements>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPortalAgreementsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPortalAgreements>>> = ({ signal }) => getPortalAgreements({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPortalAgreements>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPortalAgreementsQueryResult = NonNullable<Awaited<ReturnType<typeof getPortalAgreements>>>
export type GetPortalAgreementsQueryError = Error


/**
 * @summary Account-scoped agreements for the signed-in portal user
 */

export function useGetPortalAgreements<TData = Awaited<ReturnType<typeof getPortalAgreements>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalAgreements>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPortalAgreementsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPortalProductsUrl = () => {




  return `/api/portal/products`
}

/**
 * @summary Account-scoped products and services for the signed-in portal user
 */
export const getPortalProducts = async ( options?: Parameters<typeof customFetch>[1]): Promise<PortalProductList> => {

  return customFetch<PortalProductList>(getGetPortalProductsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPortalProductsQueryKey = () => {
    return [
    `/api/portal/products`
    ] as const;
    }


export const getGetPortalProductsQueryOptions = <TData = Awaited<ReturnType<typeof getPortalProducts>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalProducts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPortalProductsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPortalProducts>>> = ({ signal }) => getPortalProducts({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPortalProducts>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPortalProductsQueryResult = NonNullable<Awaited<ReturnType<typeof getPortalProducts>>>
export type GetPortalProductsQueryError = Error


/**
 * @summary Account-scoped products and services for the signed-in portal user
 */

export function useGetPortalProducts<TData = Awaited<ReturnType<typeof getPortalProducts>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalProducts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPortalProductsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPortalInteractionsUrl = () => {




  return `/api/portal/interactions`
}

/**
 * @summary Account-scoped interaction history for the signed-in portal user
 */
export const getPortalInteractions = async ( options?: Parameters<typeof customFetch>[1]): Promise<PortalInteractionList> => {

  return customFetch<PortalInteractionList>(getGetPortalInteractionsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPortalInteractionsQueryKey = () => {
    return [
    `/api/portal/interactions`
    ] as const;
    }


export const getGetPortalInteractionsQueryOptions = <TData = Awaited<ReturnType<typeof getPortalInteractions>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalInteractions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPortalInteractionsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPortalInteractions>>> = ({ signal }) => getPortalInteractions({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPortalInteractions>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPortalInteractionsQueryResult = NonNullable<Awaited<ReturnType<typeof getPortalInteractions>>>
export type GetPortalInteractionsQueryError = Error


/**
 * @summary Account-scoped interaction history for the signed-in portal user
 */

export function useGetPortalInteractions<TData = Awaited<ReturnType<typeof getPortalInteractions>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalInteractions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPortalInteractionsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPortalFinanceSummaryUrl = () => {




  return `/api/portal/finance/summary`
}

/**
 * @summary Supplier finance summary for the active portal organisation
 */
export const getPortalFinanceSummary = async ( options?: Parameters<typeof customFetch>[1]): Promise<PortalFinanceSummary> => {

  return customFetch<PortalFinanceSummary>(getGetPortalFinanceSummaryUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPortalFinanceSummaryQueryKey = () => {
    return [
    `/api/portal/finance/summary`
    ] as const;
    }


export const getGetPortalFinanceSummaryQueryOptions = <TData = Awaited<ReturnType<typeof getPortalFinanceSummary>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalFinanceSummary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPortalFinanceSummaryQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPortalFinanceSummary>>> = ({ signal }) => getPortalFinanceSummary({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPortalFinanceSummary>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPortalFinanceSummaryQueryResult = NonNullable<Awaited<ReturnType<typeof getPortalFinanceSummary>>>
export type GetPortalFinanceSummaryQueryError = Error


/**
 * @summary Supplier finance summary for the active portal organisation
 */

export function useGetPortalFinanceSummary<TData = Awaited<ReturnType<typeof getPortalFinanceSummary>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalFinanceSummary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPortalFinanceSummaryQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListPortalFinanceInvoicesUrl = (params?: ListPortalFinanceInvoicesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/portal/finance/invoices?${stringifiedParams}` : `/api/portal/finance/invoices`
}

/**
 * @summary List locally synced invoices or bills for the active supplier organisation
 */
export const listPortalFinanceInvoices = async (params?: ListPortalFinanceInvoicesParams, options?: Parameters<typeof customFetch>[1]): Promise<PortalFinanceInvoiceList> => {

  return customFetch<PortalFinanceInvoiceList>(getListPortalFinanceInvoicesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPortalFinanceInvoicesQueryKey = (params?: ListPortalFinanceInvoicesParams,) => {
    return [
    `/api/portal/finance/invoices`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPortalFinanceInvoicesQueryOptions = <TData = Awaited<ReturnType<typeof listPortalFinanceInvoices>>, TError = Error>(params?: ListPortalFinanceInvoicesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPortalFinanceInvoices>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPortalFinanceInvoicesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPortalFinanceInvoices>>> = ({ signal }) => listPortalFinanceInvoices(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPortalFinanceInvoices>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPortalFinanceInvoicesQueryResult = NonNullable<Awaited<ReturnType<typeof listPortalFinanceInvoices>>>
export type ListPortalFinanceInvoicesQueryError = Error


/**
 * @summary List locally synced invoices or bills for the active supplier organisation
 */

export function useListPortalFinanceInvoices<TData = Awaited<ReturnType<typeof listPortalFinanceInvoices>>, TError = Error>(
 params?: ListPortalFinanceInvoicesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPortalFinanceInvoices>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPortalFinanceInvoicesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPortalFinanceInvoiceUrl = (id: string,) => {




  return `/api/portal/finance/invoices/${id}`
}

/**
 * @summary Get one locally synced supplier invoice or bill with line items
 */
export const getPortalFinanceInvoice = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<PortalFinanceInvoiceDetail> => {

  return customFetch<PortalFinanceInvoiceDetail>(getGetPortalFinanceInvoiceUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPortalFinanceInvoiceQueryKey = (id: string,) => {
    return [
    `/api/portal/finance/invoices/${id}`
    ] as const;
    }


export const getGetPortalFinanceInvoiceQueryOptions = <TData = Awaited<ReturnType<typeof getPortalFinanceInvoice>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalFinanceInvoice>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPortalFinanceInvoiceQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPortalFinanceInvoice>>> = ({ signal }) => getPortalFinanceInvoice(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPortalFinanceInvoice>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPortalFinanceInvoiceQueryResult = NonNullable<Awaited<ReturnType<typeof getPortalFinanceInvoice>>>
export type GetPortalFinanceInvoiceQueryError = Error


/**
 * @summary Get one locally synced supplier invoice or bill with line items
 */

export function useGetPortalFinanceInvoice<TData = Awaited<ReturnType<typeof getPortalFinanceInvoice>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalFinanceInvoice>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPortalFinanceInvoiceQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListPortalFinancePaymentsUrl = (params?: ListPortalFinancePaymentsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/portal/finance/payments?${stringifiedParams}` : `/api/portal/finance/payments`
}

/**
 * @summary List payments for the active supplier organisation's synced documents
 */
export const listPortalFinancePayments = async (params?: ListPortalFinancePaymentsParams, options?: Parameters<typeof customFetch>[1]): Promise<PortalFinancePaymentList> => {

  return customFetch<PortalFinancePaymentList>(getListPortalFinancePaymentsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPortalFinancePaymentsQueryKey = (params?: ListPortalFinancePaymentsParams,) => {
    return [
    `/api/portal/finance/payments`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPortalFinancePaymentsQueryOptions = <TData = Awaited<ReturnType<typeof listPortalFinancePayments>>, TError = Error>(params?: ListPortalFinancePaymentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPortalFinancePayments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPortalFinancePaymentsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPortalFinancePayments>>> = ({ signal }) => listPortalFinancePayments(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPortalFinancePayments>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPortalFinancePaymentsQueryResult = NonNullable<Awaited<ReturnType<typeof listPortalFinancePayments>>>
export type ListPortalFinancePaymentsQueryError = Error


/**
 * @summary List payments for the active supplier organisation's synced documents
 */

export function useListPortalFinancePayments<TData = Awaited<ReturnType<typeof listPortalFinancePayments>>, TError = Error>(
 params?: ListPortalFinancePaymentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPortalFinancePayments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPortalFinancePaymentsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListPortalUsersUrl = () => {




  return `/api/portal/users`
}

/**
 * @summary List portal users for the signed-in entity administrator's own entity
 */
export const listPortalUsers = async ( options?: Parameters<typeof customFetch>[1]): Promise<PortalUserList> => {

  return customFetch<PortalUserList>(getListPortalUsersUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPortalUsersQueryKey = () => {
    return [
    `/api/portal/users`
    ] as const;
    }


export const getListPortalUsersQueryOptions = <TData = Awaited<ReturnType<typeof listPortalUsers>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPortalUsers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPortalUsersQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPortalUsers>>> = ({ signal }) => listPortalUsers({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPortalUsers>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPortalUsersQueryResult = NonNullable<Awaited<ReturnType<typeof listPortalUsers>>>
export type ListPortalUsersQueryError = Error


/**
 * @summary List portal users for the signed-in entity administrator's own entity
 */

export function useListPortalUsers<TData = Awaited<ReturnType<typeof listPortalUsers>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPortalUsers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPortalUsersQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreatePortalUserUrl = () => {




  return `/api/portal/users`
}

/**
 * @summary Add an active member to the signed-in entity administrator's own entity
 */
export const createPortalUser = async (portalUserCreate: PortalUserCreate, options?: Parameters<typeof customFetch>[1]): Promise<PortalUser> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PortalUser>(getCreatePortalUserUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(portalUserCreate)
  }
);}





export const getCreatePortalUserMutationKey = () => ['createPortalUser'] as const;

export const getCreatePortalUserMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPortalUser>>, TError,CreatePortalUserMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createPortalUser>>, TError,CreatePortalUserMutationVariables, TContext> => {

const mutationKey = getCreatePortalUserMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createPortalUser>>, CreatePortalUserMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createPortalUser(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreatePortalUserMutationResult = NonNullable<Awaited<ReturnType<typeof createPortalUser>>>
    export type CreatePortalUserMutationBody = PortalUserCreate
    export type CreatePortalUserMutationError = Error
    export type CreatePortalUserMutationVariables = {data: PortalUserCreate}

    /**
 * @summary Add an active member to the signed-in entity administrator's own entity
 */
export const useCreatePortalUser = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPortalUser>>, TError,CreatePortalUserMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createPortalUser>>,
        TError,
        CreatePortalUserMutationVariables,
        TContext
      > => {
      return useMutation(getCreatePortalUserMutationOptions(options));
    }

export const getUpdatePortalUserUrl = (id: string,) => {




  return `/api/portal/users/${id}`
}

/**
 * @summary Update access or entity-admin status within the caller's own entity
 */
export const updatePortalUser = async (id: string,
    portalUserUpdate: PortalUserUpdate, options?: Parameters<typeof customFetch>[1]): Promise<PortalUser> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PortalUser>(getUpdatePortalUserUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(portalUserUpdate)
  }
);}





export const getUpdatePortalUserMutationKey = () => ['updatePortalUser'] as const;

export const getUpdatePortalUserMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePortalUser>>, TError,UpdatePortalUserMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updatePortalUser>>, TError,UpdatePortalUserMutationVariables, TContext> => {

const mutationKey = getUpdatePortalUserMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updatePortalUser>>, UpdatePortalUserMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updatePortalUser(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdatePortalUserMutationResult = NonNullable<Awaited<ReturnType<typeof updatePortalUser>>>
    export type UpdatePortalUserMutationBody = PortalUserUpdate
    export type UpdatePortalUserMutationError = Error
    export type UpdatePortalUserMutationVariables = {id: string;data: PortalUserUpdate}

    /**
 * @summary Update access or entity-admin status within the caller's own entity
 */
export const useUpdatePortalUser = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePortalUser>>, TError,UpdatePortalUserMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updatePortalUser>>,
        TError,
        UpdatePortalUserMutationVariables,
        TContext
      > => {
      return useMutation(getUpdatePortalUserMutationOptions(options));
    }

export const getListPortalSharesUrl = () => {




  return `/api/portal/shares`
}

/**
 * @summary List shares created by the consumer in the active self context
 */
export const listPortalShares = async ( options?: Parameters<typeof customFetch>[1]): Promise<PortalShareList> => {

  return customFetch<PortalShareList>(getListPortalSharesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPortalSharesQueryKey = () => {
    return [
    `/api/portal/shares`
    ] as const;
    }


export const getListPortalSharesQueryOptions = <TData = Awaited<ReturnType<typeof listPortalShares>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPortalShares>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPortalSharesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPortalShares>>> = ({ signal }) => listPortalShares({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPortalShares>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPortalSharesQueryResult = NonNullable<Awaited<ReturnType<typeof listPortalShares>>>
export type ListPortalSharesQueryError = Error


/**
 * @summary List shares created by the consumer in the active self context
 */

export function useListPortalShares<TData = Awaited<ReturnType<typeof listPortalShares>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPortalShares>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPortalSharesQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreatePortalShareUrl = () => {




  return `/api/portal/shares`
}

/**
 * @summary Share the active consumer account with another person
 */
export const createPortalShare = async (portalShareCreate: PortalShareCreate, options?: Parameters<typeof customFetch>[1]): Promise<PortalShare> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PortalShare>(getCreatePortalShareUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(portalShareCreate)
  }
);}





export const getCreatePortalShareMutationKey = () => ['createPortalShare'] as const;

export const getCreatePortalShareMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPortalShare>>, TError,CreatePortalShareMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createPortalShare>>, TError,CreatePortalShareMutationVariables, TContext> => {

const mutationKey = getCreatePortalShareMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createPortalShare>>, CreatePortalShareMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createPortalShare(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreatePortalShareMutationResult = NonNullable<Awaited<ReturnType<typeof createPortalShare>>>
    export type CreatePortalShareMutationBody = PortalShareCreate
    export type CreatePortalShareMutationError = Error
    export type CreatePortalShareMutationVariables = {data: PortalShareCreate}

    /**
 * @summary Share the active consumer account with another person
 */
export const useCreatePortalShare = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPortalShare>>, TError,CreatePortalShareMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createPortalShare>>,
        TError,
        CreatePortalShareMutationVariables,
        TContext
      > => {
      return useMutation(getCreatePortalShareMutationOptions(options));
    }

export const getUpdatePortalShareUrl = (id: string,) => {




  return `/api/portal/shares/${id}`
}

/**
 * @summary Change a consumer share permission or expiry
 */
export const updatePortalShare = async (id: string,
    portalShareUpdate: PortalShareUpdate, options?: Parameters<typeof customFetch>[1]): Promise<PortalShare> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PortalShare>(getUpdatePortalShareUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(portalShareUpdate)
  }
);}





export const getUpdatePortalShareMutationKey = () => ['updatePortalShare'] as const;

export const getUpdatePortalShareMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePortalShare>>, TError,UpdatePortalShareMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updatePortalShare>>, TError,UpdatePortalShareMutationVariables, TContext> => {

const mutationKey = getUpdatePortalShareMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updatePortalShare>>, UpdatePortalShareMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updatePortalShare(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdatePortalShareMutationResult = NonNullable<Awaited<ReturnType<typeof updatePortalShare>>>
    export type UpdatePortalShareMutationBody = PortalShareUpdate
    export type UpdatePortalShareMutationError = Error
    export type UpdatePortalShareMutationVariables = {id: string;data: PortalShareUpdate}

    /**
 * @summary Change a consumer share permission or expiry
 */
export const useUpdatePortalShare = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePortalShare>>, TError,UpdatePortalShareMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updatePortalShare>>,
        TError,
        UpdatePortalShareMutationVariables,
        TContext
      > => {
      return useMutation(getUpdatePortalShareMutationOptions(options));
    }

export const getRevokePortalShareUrl = (id: string,) => {




  return `/api/portal/shares/${id}`
}

/**
 * @summary Revoke a consumer portal share immediately
 */
export const revokePortalShare = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getRevokePortalShareUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getRevokePortalShareMutationKey = () => ['revokePortalShare'] as const;

export const getRevokePortalShareMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokePortalShare>>, TError,RevokePortalShareMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokePortalShare>>, TError,RevokePortalShareMutationVariables, TContext> => {

const mutationKey = getRevokePortalShareMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokePortalShare>>, RevokePortalShareMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  revokePortalShare(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokePortalShareMutationResult = NonNullable<Awaited<ReturnType<typeof revokePortalShare>>>

    export type RevokePortalShareMutationError = Error
    export type RevokePortalShareMutationVariables = {id: string}

    /**
 * @summary Revoke a consumer portal share immediately
 */
export const useRevokePortalShare = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokePortalShare>>, TError,RevokePortalShareMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof revokePortalShare>>,
        TError,
        RevokePortalShareMutationVariables,
        TContext
      > => {
      return useMutation(getRevokePortalShareMutationOptions(options));
    }

export const getCreatePortalEnquiryUrl = () => {




  return `/api/portal/enquiries`
}

/**
 * @summary Submit a new enquiry as the signed-in portal user
 */
export const createPortalEnquiry = async (portalEnquiryCreate: PortalEnquiryCreate, options?: Parameters<typeof customFetch>[1]): Promise<PortalEnquiry> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PortalEnquiry>(getCreatePortalEnquiryUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(portalEnquiryCreate)
  }
);}





export const getCreatePortalEnquiryMutationKey = () => ['createPortalEnquiry'] as const;

export const getCreatePortalEnquiryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPortalEnquiry>>, TError,CreatePortalEnquiryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createPortalEnquiry>>, TError,CreatePortalEnquiryMutationVariables, TContext> => {

const mutationKey = getCreatePortalEnquiryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createPortalEnquiry>>, CreatePortalEnquiryMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createPortalEnquiry(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreatePortalEnquiryMutationResult = NonNullable<Awaited<ReturnType<typeof createPortalEnquiry>>>
    export type CreatePortalEnquiryMutationBody = PortalEnquiryCreate
    export type CreatePortalEnquiryMutationError = Error
    export type CreatePortalEnquiryMutationVariables = {data: PortalEnquiryCreate}

    /**
 * @summary Submit a new enquiry as the signed-in portal user
 */
export const useCreatePortalEnquiry = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPortalEnquiry>>, TError,CreatePortalEnquiryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createPortalEnquiry>>,
        TError,
        CreatePortalEnquiryMutationVariables,
        TContext
      > => {
      return useMutation(getCreatePortalEnquiryMutationOptions(options));
    }

export const getListPortalSowRequestsUrl = (params?: ListPortalSowRequestsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/portal/sow-requests?${stringifiedParams}` : `/api/portal/sow-requests`
}

/**
 * @summary list Portal Sow Requests
 */
export const listPortalSowRequests = async (params?: ListPortalSowRequestsParams, options?: Parameters<typeof customFetch>[1]): Promise<PortalSowRequestPage> => {

  return customFetch<PortalSowRequestPage>(getListPortalSowRequestsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPortalSowRequestsQueryKey = (params?: ListPortalSowRequestsParams,) => {
    return [
    `/api/portal/sow-requests`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPortalSowRequestsQueryOptions = <TData = Awaited<ReturnType<typeof listPortalSowRequests>>, TError = Error>(params?: ListPortalSowRequestsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPortalSowRequests>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPortalSowRequestsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPortalSowRequests>>> = ({ signal }) => listPortalSowRequests(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPortalSowRequests>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPortalSowRequestsQueryResult = NonNullable<Awaited<ReturnType<typeof listPortalSowRequests>>>
export type ListPortalSowRequestsQueryError = Error


/**
 * @summary list Portal Sow Requests
 */

export function useListPortalSowRequests<TData = Awaited<ReturnType<typeof listPortalSowRequests>>, TError = Error>(
 params?: ListPortalSowRequestsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPortalSowRequests>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPortalSowRequestsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreatePortalSowRequestUrl = () => {




  return `/api/portal/sow-requests`
}

/**
 * @summary create Portal Sow Request
 */
export const createPortalSowRequest = async (portalSowStart: PortalSowStart, options?: Parameters<typeof customFetch>[1]): Promise<PortalSowRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PortalSowRequest>(getCreatePortalSowRequestUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(portalSowStart)
  }
);}





export const getCreatePortalSowRequestMutationKey = () => ['createPortalSowRequest'] as const;

export const getCreatePortalSowRequestMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPortalSowRequest>>, TError,CreatePortalSowRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createPortalSowRequest>>, TError,CreatePortalSowRequestMutationVariables, TContext> => {

const mutationKey = getCreatePortalSowRequestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createPortalSowRequest>>, CreatePortalSowRequestMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createPortalSowRequest(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreatePortalSowRequestMutationResult = NonNullable<Awaited<ReturnType<typeof createPortalSowRequest>>>
    export type CreatePortalSowRequestMutationBody = PortalSowStart
    export type CreatePortalSowRequestMutationError = Error
    export type CreatePortalSowRequestMutationVariables = {data: PortalSowStart}

    /**
 * @summary create Portal Sow Request
 */
export const useCreatePortalSowRequest = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPortalSowRequest>>, TError,CreatePortalSowRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createPortalSowRequest>>,
        TError,
        CreatePortalSowRequestMutationVariables,
        TContext
      > => {
      return useMutation(getCreatePortalSowRequestMutationOptions(options));
    }

export const getGetPortalSowRequestUrl = (id: string,) => {




  return `/api/portal/sow-requests/${id}`
}

/**
 * @summary get Portal Sow Request
 */
export const getPortalSowRequest = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<PortalSowRequest> => {

  return customFetch<PortalSowRequest>(getGetPortalSowRequestUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPortalSowRequestQueryKey = (id: string,) => {
    return [
    `/api/portal/sow-requests/${id}`
    ] as const;
    }


export const getGetPortalSowRequestQueryOptions = <TData = Awaited<ReturnType<typeof getPortalSowRequest>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalSowRequest>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPortalSowRequestQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPortalSowRequest>>> = ({ signal }) => getPortalSowRequest(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPortalSowRequest>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPortalSowRequestQueryResult = NonNullable<Awaited<ReturnType<typeof getPortalSowRequest>>>
export type GetPortalSowRequestQueryError = Error


/**
 * @summary get Portal Sow Request
 */

export function useGetPortalSowRequest<TData = Awaited<ReturnType<typeof getPortalSowRequest>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalSowRequest>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPortalSowRequestQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getAddPortalSowMessageUrl = (id: string,) => {




  return `/api/portal/sow-requests/${id}/messages`
}

/**
 * @summary add Portal Sow Message
 */
export const addPortalSowMessage = async (id: string,
    portalSowMessageInput: PortalSowMessageInput, options?: Parameters<typeof customFetch>[1]): Promise<PortalSowRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PortalSowRequest>(getAddPortalSowMessageUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(portalSowMessageInput)
  }
);}





export const getAddPortalSowMessageMutationKey = () => ['addPortalSowMessage'] as const;

export const getAddPortalSowMessageMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addPortalSowMessage>>, TError,AddPortalSowMessageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof addPortalSowMessage>>, TError,AddPortalSowMessageMutationVariables, TContext> => {

const mutationKey = getAddPortalSowMessageMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof addPortalSowMessage>>, AddPortalSowMessageMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  addPortalSowMessage(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AddPortalSowMessageMutationResult = NonNullable<Awaited<ReturnType<typeof addPortalSowMessage>>>
    export type AddPortalSowMessageMutationBody = PortalSowMessageInput
    export type AddPortalSowMessageMutationError = Error
    export type AddPortalSowMessageMutationVariables = {id: string;data: PortalSowMessageInput}

    /**
 * @summary add Portal Sow Message
 */
export const useAddPortalSowMessage = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addPortalSowMessage>>, TError,AddPortalSowMessageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof addPortalSowMessage>>,
        TError,
        AddPortalSowMessageMutationVariables,
        TContext
      > => {
      return useMutation(getAddPortalSowMessageMutationOptions(options));
    }

export const getAddPortalSowAttachmentUrl = (id: string,) => {




  return `/api/portal/sow-requests/${id}/attachments`
}

/**
 * @summary add Portal Sow Attachment
 */
export const addPortalSowAttachment = async (id: string,
    portalSowAttachmentInput: PortalSowAttachmentInput, options?: Parameters<typeof customFetch>[1]): Promise<PortalSowRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PortalSowRequest>(getAddPortalSowAttachmentUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(portalSowAttachmentInput)
  }
);}





export const getAddPortalSowAttachmentMutationKey = () => ['addPortalSowAttachment'] as const;

export const getAddPortalSowAttachmentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addPortalSowAttachment>>, TError,AddPortalSowAttachmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof addPortalSowAttachment>>, TError,AddPortalSowAttachmentMutationVariables, TContext> => {

const mutationKey = getAddPortalSowAttachmentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof addPortalSowAttachment>>, AddPortalSowAttachmentMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  addPortalSowAttachment(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AddPortalSowAttachmentMutationResult = NonNullable<Awaited<ReturnType<typeof addPortalSowAttachment>>>
    export type AddPortalSowAttachmentMutationBody = PortalSowAttachmentInput
    export type AddPortalSowAttachmentMutationError = Error
    export type AddPortalSowAttachmentMutationVariables = {id: string;data: PortalSowAttachmentInput}

    /**
 * @summary add Portal Sow Attachment
 */
export const useAddPortalSowAttachment = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addPortalSowAttachment>>, TError,AddPortalSowAttachmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof addPortalSowAttachment>>,
        TError,
        AddPortalSowAttachmentMutationVariables,
        TContext
      > => {
      return useMutation(getAddPortalSowAttachmentMutationOptions(options));
    }

export const getGetPortalSowAttachmentUrl = (id: string,
    attachmentId: string,) => {




  return `/api/portal/sow-requests/${id}/attachments/${attachmentId}`
}

/**
 * @summary get Portal Sow Attachment
 */
export const getPortalSowAttachment = async (id: string,
    attachmentId: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getGetPortalSowAttachmentUrl(id,attachmentId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPortalSowAttachmentQueryKey = (id: string,
    attachmentId: string,) => {
    return [
    `/api/portal/sow-requests/${id}/attachments/${attachmentId}`
    ] as const;
    }


export const getGetPortalSowAttachmentQueryOptions = <TData = Awaited<ReturnType<typeof getPortalSowAttachment>>, TError = Error>(id: string,
    attachmentId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalSowAttachment>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPortalSowAttachmentQueryKey(id,attachmentId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPortalSowAttachment>>> = ({ signal }) => getPortalSowAttachment(id,attachmentId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && attachmentId !== null && attachmentId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPortalSowAttachment>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPortalSowAttachmentQueryResult = NonNullable<Awaited<ReturnType<typeof getPortalSowAttachment>>>
export type GetPortalSowAttachmentQueryError = Error


/**
 * @summary get Portal Sow Attachment
 */

export function useGetPortalSowAttachment<TData = Awaited<ReturnType<typeof getPortalSowAttachment>>, TError = Error>(
 id: string,
    attachmentId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPortalSowAttachment>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPortalSowAttachmentQueryOptions(id,attachmentId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSubmitPortalSowRequestUrl = (id: string,) => {




  return `/api/portal/sow-requests/${id}/submission`
}

/**
 * @summary submit Portal Sow Request
 */
export const submitPortalSowRequest = async (id: string,
    portalSowSubmission: PortalSowSubmission, options?: Parameters<typeof customFetch>[1]): Promise<PortalSowRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PortalSowRequest>(getSubmitPortalSowRequestUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(portalSowSubmission)
  }
);}





export const getSubmitPortalSowRequestMutationKey = () => ['submitPortalSowRequest'] as const;

export const getSubmitPortalSowRequestMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitPortalSowRequest>>, TError,SubmitPortalSowRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof submitPortalSowRequest>>, TError,SubmitPortalSowRequestMutationVariables, TContext> => {

const mutationKey = getSubmitPortalSowRequestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof submitPortalSowRequest>>, SubmitPortalSowRequestMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  submitPortalSowRequest(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SubmitPortalSowRequestMutationResult = NonNullable<Awaited<ReturnType<typeof submitPortalSowRequest>>>
    export type SubmitPortalSowRequestMutationBody = PortalSowSubmission
    export type SubmitPortalSowRequestMutationError = Error
    export type SubmitPortalSowRequestMutationVariables = {id: string;data: PortalSowSubmission}

    /**
 * @summary submit Portal Sow Request
 */
export const useSubmitPortalSowRequest = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitPortalSowRequest>>, TError,SubmitPortalSowRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof submitPortalSowRequest>>,
        TError,
        SubmitPortalSowRequestMutationVariables,
        TContext
      > => {
      return useMutation(getSubmitPortalSowRequestMutationOptions(options));
    }

export const getGetServiceCatalogueHierarchyUrl = () => {




  return `/api/service-catalogue/hierarchy`
}

/**
 * @summary Full service delivery hierarchy (area -> line -> component)
 */
export const getServiceCatalogueHierarchy = async ( options?: Parameters<typeof customFetch>[1]): Promise<ServiceHierarchy> => {

  return customFetch<ServiceHierarchy>(getGetServiceCatalogueHierarchyUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetServiceCatalogueHierarchyQueryKey = () => {
    return [
    `/api/service-catalogue/hierarchy`
    ] as const;
    }


export const getGetServiceCatalogueHierarchyQueryOptions = <TData = Awaited<ReturnType<typeof getServiceCatalogueHierarchy>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getServiceCatalogueHierarchy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetServiceCatalogueHierarchyQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getServiceCatalogueHierarchy>>> = ({ signal }) => getServiceCatalogueHierarchy({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getServiceCatalogueHierarchy>>, TError, TData> & { queryKey: QueryKey }
}

export type GetServiceCatalogueHierarchyQueryResult = NonNullable<Awaited<ReturnType<typeof getServiceCatalogueHierarchy>>>
export type GetServiceCatalogueHierarchyQueryError = unknown


/**
 * @summary Full service delivery hierarchy (area -> line -> component)
 */

export function useGetServiceCatalogueHierarchy<TData = Awaited<ReturnType<typeof getServiceCatalogueHierarchy>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getServiceCatalogueHierarchy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetServiceCatalogueHierarchyQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateServiceAreaUrl = () => {




  return `/api/service-areas`
}

/**
 * @summary Create a service area
 */
export const createServiceArea = async (serviceAreaCreate: ServiceAreaCreate, options?: Parameters<typeof customFetch>[1]): Promise<ServiceArea> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ServiceArea>(getCreateServiceAreaUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(serviceAreaCreate)
  }
);}





export const getCreateServiceAreaMutationKey = () => ['createServiceArea'] as const;

export const getCreateServiceAreaMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createServiceArea>>, TError,CreateServiceAreaMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createServiceArea>>, TError,CreateServiceAreaMutationVariables, TContext> => {

const mutationKey = getCreateServiceAreaMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createServiceArea>>, CreateServiceAreaMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createServiceArea(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateServiceAreaMutationResult = NonNullable<Awaited<ReturnType<typeof createServiceArea>>>
    export type CreateServiceAreaMutationBody = ServiceAreaCreate
    export type CreateServiceAreaMutationError = Error
    export type CreateServiceAreaMutationVariables = {data: ServiceAreaCreate}

    /**
 * @summary Create a service area
 */
export const useCreateServiceArea = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createServiceArea>>, TError,CreateServiceAreaMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createServiceArea>>,
        TError,
        CreateServiceAreaMutationVariables,
        TContext
      > => {
      return useMutation(getCreateServiceAreaMutationOptions(options));
    }

export const getUpdateServiceAreaUrl = (id: string,) => {




  return `/api/service-areas/${id}`
}

/**
 * @summary Update a service area
 */
export const updateServiceArea = async (id: string,
    serviceAreaUpdate: ServiceAreaUpdate, options?: Parameters<typeof customFetch>[1]): Promise<ServiceArea> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ServiceArea>(getUpdateServiceAreaUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(serviceAreaUpdate)
  }
);}





export const getUpdateServiceAreaMutationKey = () => ['updateServiceArea'] as const;

export const getUpdateServiceAreaMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateServiceArea>>, TError,UpdateServiceAreaMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateServiceArea>>, TError,UpdateServiceAreaMutationVariables, TContext> => {

const mutationKey = getUpdateServiceAreaMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateServiceArea>>, UpdateServiceAreaMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateServiceArea(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateServiceAreaMutationResult = NonNullable<Awaited<ReturnType<typeof updateServiceArea>>>
    export type UpdateServiceAreaMutationBody = ServiceAreaUpdate
    export type UpdateServiceAreaMutationError = Error
    export type UpdateServiceAreaMutationVariables = {id: string;data: ServiceAreaUpdate}

    /**
 * @summary Update a service area
 */
export const useUpdateServiceArea = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateServiceArea>>, TError,UpdateServiceAreaMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateServiceArea>>,
        TError,
        UpdateServiceAreaMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateServiceAreaMutationOptions(options));
    }

export const getDeleteServiceAreaUrl = (id: string,) => {




  return `/api/service-areas/${id}`
}

/**
 * @summary Delete a service area
 */
export const deleteServiceArea = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteServiceAreaUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteServiceAreaMutationKey = () => ['deleteServiceArea'] as const;

export const getDeleteServiceAreaMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteServiceArea>>, TError,DeleteServiceAreaMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteServiceArea>>, TError,DeleteServiceAreaMutationVariables, TContext> => {

const mutationKey = getDeleteServiceAreaMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteServiceArea>>, DeleteServiceAreaMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteServiceArea(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteServiceAreaMutationResult = NonNullable<Awaited<ReturnType<typeof deleteServiceArea>>>

    export type DeleteServiceAreaMutationError = Error
    export type DeleteServiceAreaMutationVariables = {id: string}

    /**
 * @summary Delete a service area
 */
export const useDeleteServiceArea = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteServiceArea>>, TError,DeleteServiceAreaMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteServiceArea>>,
        TError,
        DeleteServiceAreaMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteServiceAreaMutationOptions(options));
    }

export const getListServiceLinesUrl = (params?: ListServiceLinesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/service-lines?${stringifiedParams}` : `/api/service-lines`
}

/**
 * @summary List service lines
 */
export const listServiceLines = async (params?: ListServiceLinesParams, options?: Parameters<typeof customFetch>[1]): Promise<ServiceLineList> => {

  return customFetch<ServiceLineList>(getListServiceLinesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListServiceLinesQueryKey = (params?: ListServiceLinesParams,) => {
    return [
    `/api/service-lines`, ...(params ? [params] : [])
    ] as const;
    }


export const getListServiceLinesQueryOptions = <TData = Awaited<ReturnType<typeof listServiceLines>>, TError = unknown>(params?: ListServiceLinesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listServiceLines>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListServiceLinesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listServiceLines>>> = ({ signal }) => listServiceLines(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listServiceLines>>, TError, TData> & { queryKey: QueryKey }
}

export type ListServiceLinesQueryResult = NonNullable<Awaited<ReturnType<typeof listServiceLines>>>
export type ListServiceLinesQueryError = unknown


/**
 * @summary List service lines
 */

export function useListServiceLines<TData = Awaited<ReturnType<typeof listServiceLines>>, TError = unknown>(
 params?: ListServiceLinesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listServiceLines>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListServiceLinesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateServiceLineUrl = () => {




  return `/api/service-lines`
}

/**
 * @summary Create a service line
 */
export const createServiceLine = async (serviceLineCreate: ServiceLineCreate, options?: Parameters<typeof customFetch>[1]): Promise<ServiceLine> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ServiceLine>(getCreateServiceLineUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(serviceLineCreate)
  }
);}





export const getCreateServiceLineMutationKey = () => ['createServiceLine'] as const;

export const getCreateServiceLineMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createServiceLine>>, TError,CreateServiceLineMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createServiceLine>>, TError,CreateServiceLineMutationVariables, TContext> => {

const mutationKey = getCreateServiceLineMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createServiceLine>>, CreateServiceLineMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createServiceLine(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateServiceLineMutationResult = NonNullable<Awaited<ReturnType<typeof createServiceLine>>>
    export type CreateServiceLineMutationBody = ServiceLineCreate
    export type CreateServiceLineMutationError = Error
    export type CreateServiceLineMutationVariables = {data: ServiceLineCreate}

    /**
 * @summary Create a service line
 */
export const useCreateServiceLine = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createServiceLine>>, TError,CreateServiceLineMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createServiceLine>>,
        TError,
        CreateServiceLineMutationVariables,
        TContext
      > => {
      return useMutation(getCreateServiceLineMutationOptions(options));
    }

export const getUpdateServiceLineUrl = (id: string,) => {




  return `/api/service-lines/${id}`
}

/**
 * @summary Update a service line
 */
export const updateServiceLine = async (id: string,
    serviceLineUpdate: ServiceLineUpdate, options?: Parameters<typeof customFetch>[1]): Promise<ServiceLine> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ServiceLine>(getUpdateServiceLineUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(serviceLineUpdate)
  }
);}





export const getUpdateServiceLineMutationKey = () => ['updateServiceLine'] as const;

export const getUpdateServiceLineMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateServiceLine>>, TError,UpdateServiceLineMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateServiceLine>>, TError,UpdateServiceLineMutationVariables, TContext> => {

const mutationKey = getUpdateServiceLineMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateServiceLine>>, UpdateServiceLineMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateServiceLine(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateServiceLineMutationResult = NonNullable<Awaited<ReturnType<typeof updateServiceLine>>>
    export type UpdateServiceLineMutationBody = ServiceLineUpdate
    export type UpdateServiceLineMutationError = Error
    export type UpdateServiceLineMutationVariables = {id: string;data: ServiceLineUpdate}

    /**
 * @summary Update a service line
 */
export const useUpdateServiceLine = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateServiceLine>>, TError,UpdateServiceLineMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateServiceLine>>,
        TError,
        UpdateServiceLineMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateServiceLineMutationOptions(options));
    }

export const getDeleteServiceLineUrl = (id: string,) => {




  return `/api/service-lines/${id}`
}

/**
 * @summary Delete a service line
 */
export const deleteServiceLine = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteServiceLineUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteServiceLineMutationKey = () => ['deleteServiceLine'] as const;

export const getDeleteServiceLineMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteServiceLine>>, TError,DeleteServiceLineMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteServiceLine>>, TError,DeleteServiceLineMutationVariables, TContext> => {

const mutationKey = getDeleteServiceLineMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteServiceLine>>, DeleteServiceLineMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteServiceLine(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteServiceLineMutationResult = NonNullable<Awaited<ReturnType<typeof deleteServiceLine>>>

    export type DeleteServiceLineMutationError = Error
    export type DeleteServiceLineMutationVariables = {id: string}

    /**
 * @summary Delete a service line
 */
export const useDeleteServiceLine = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteServiceLine>>, TError,DeleteServiceLineMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteServiceLine>>,
        TError,
        DeleteServiceLineMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteServiceLineMutationOptions(options));
    }

export const getListServiceComponentsUrl = (params?: ListServiceComponentsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/service-components?${stringifiedParams}` : `/api/service-components`
}

/**
 * @summary List service components
 */
export const listServiceComponents = async (params?: ListServiceComponentsParams, options?: Parameters<typeof customFetch>[1]): Promise<ServiceComponentList> => {

  return customFetch<ServiceComponentList>(getListServiceComponentsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListServiceComponentsQueryKey = (params?: ListServiceComponentsParams,) => {
    return [
    `/api/service-components`, ...(params ? [params] : [])
    ] as const;
    }


export const getListServiceComponentsQueryOptions = <TData = Awaited<ReturnType<typeof listServiceComponents>>, TError = unknown>(params?: ListServiceComponentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listServiceComponents>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListServiceComponentsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listServiceComponents>>> = ({ signal }) => listServiceComponents(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listServiceComponents>>, TError, TData> & { queryKey: QueryKey }
}

export type ListServiceComponentsQueryResult = NonNullable<Awaited<ReturnType<typeof listServiceComponents>>>
export type ListServiceComponentsQueryError = unknown


/**
 * @summary List service components
 */

export function useListServiceComponents<TData = Awaited<ReturnType<typeof listServiceComponents>>, TError = unknown>(
 params?: ListServiceComponentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listServiceComponents>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListServiceComponentsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateServiceComponentUrl = () => {




  return `/api/service-components`
}

/**
 * @summary Create a service component
 */
export const createServiceComponent = async (serviceComponentCreate: ServiceComponentCreate, options?: Parameters<typeof customFetch>[1]): Promise<ServiceComponent> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ServiceComponent>(getCreateServiceComponentUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(serviceComponentCreate)
  }
);}





export const getCreateServiceComponentMutationKey = () => ['createServiceComponent'] as const;

export const getCreateServiceComponentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createServiceComponent>>, TError,CreateServiceComponentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createServiceComponent>>, TError,CreateServiceComponentMutationVariables, TContext> => {

const mutationKey = getCreateServiceComponentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createServiceComponent>>, CreateServiceComponentMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createServiceComponent(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateServiceComponentMutationResult = NonNullable<Awaited<ReturnType<typeof createServiceComponent>>>
    export type CreateServiceComponentMutationBody = ServiceComponentCreate
    export type CreateServiceComponentMutationError = Error
    export type CreateServiceComponentMutationVariables = {data: ServiceComponentCreate}

    /**
 * @summary Create a service component
 */
export const useCreateServiceComponent = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createServiceComponent>>, TError,CreateServiceComponentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createServiceComponent>>,
        TError,
        CreateServiceComponentMutationVariables,
        TContext
      > => {
      return useMutation(getCreateServiceComponentMutationOptions(options));
    }

export const getUpdateServiceComponentUrl = (id: string,) => {




  return `/api/service-components/${id}`
}

/**
 * @summary Update a service component
 */
export const updateServiceComponent = async (id: string,
    serviceComponentUpdate: ServiceComponentUpdate, options?: Parameters<typeof customFetch>[1]): Promise<ServiceComponent> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ServiceComponent>(getUpdateServiceComponentUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(serviceComponentUpdate)
  }
);}





export const getUpdateServiceComponentMutationKey = () => ['updateServiceComponent'] as const;

export const getUpdateServiceComponentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateServiceComponent>>, TError,UpdateServiceComponentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateServiceComponent>>, TError,UpdateServiceComponentMutationVariables, TContext> => {

const mutationKey = getUpdateServiceComponentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateServiceComponent>>, UpdateServiceComponentMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateServiceComponent(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateServiceComponentMutationResult = NonNullable<Awaited<ReturnType<typeof updateServiceComponent>>>
    export type UpdateServiceComponentMutationBody = ServiceComponentUpdate
    export type UpdateServiceComponentMutationError = Error
    export type UpdateServiceComponentMutationVariables = {id: string;data: ServiceComponentUpdate}

    /**
 * @summary Update a service component
 */
export const useUpdateServiceComponent = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateServiceComponent>>, TError,UpdateServiceComponentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateServiceComponent>>,
        TError,
        UpdateServiceComponentMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateServiceComponentMutationOptions(options));
    }

export const getDeleteServiceComponentUrl = (id: string,) => {




  return `/api/service-components/${id}`
}

/**
 * @summary Delete a service component
 */
export const deleteServiceComponent = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteServiceComponentUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteServiceComponentMutationKey = () => ['deleteServiceComponent'] as const;

export const getDeleteServiceComponentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteServiceComponent>>, TError,DeleteServiceComponentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteServiceComponent>>, TError,DeleteServiceComponentMutationVariables, TContext> => {

const mutationKey = getDeleteServiceComponentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteServiceComponent>>, DeleteServiceComponentMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteServiceComponent(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteServiceComponentMutationResult = NonNullable<Awaited<ReturnType<typeof deleteServiceComponent>>>

    export type DeleteServiceComponentMutationError = Error
    export type DeleteServiceComponentMutationVariables = {id: string}

    /**
 * @summary Delete a service component
 */
export const useDeleteServiceComponent = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteServiceComponent>>, TError,DeleteServiceComponentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteServiceComponent>>,
        TError,
        DeleteServiceComponentMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteServiceComponentMutationOptions(options));
    }

export const getListCatalogueDeliverablesUrl = (params?: ListCatalogueDeliverablesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/catalogue/deliverables?${stringifiedParams}` : `/api/catalogue/deliverables`
}

/**
 * @summary List catalogue deliverables
 */
export const listCatalogueDeliverables = async (params?: ListCatalogueDeliverablesParams, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueDeliverableList> => {

  return customFetch<CatalogueDeliverableList>(getListCatalogueDeliverablesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCatalogueDeliverablesQueryKey = (params?: ListCatalogueDeliverablesParams,) => {
    return [
    `/api/catalogue/deliverables`, ...(params ? [params] : [])
    ] as const;
    }


export const getListCatalogueDeliverablesQueryOptions = <TData = Awaited<ReturnType<typeof listCatalogueDeliverables>>, TError = unknown>(params?: ListCatalogueDeliverablesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCatalogueDeliverables>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCatalogueDeliverablesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCatalogueDeliverables>>> = ({ signal }) => listCatalogueDeliverables(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCatalogueDeliverables>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCatalogueDeliverablesQueryResult = NonNullable<Awaited<ReturnType<typeof listCatalogueDeliverables>>>
export type ListCatalogueDeliverablesQueryError = unknown


/**
 * @summary List catalogue deliverables
 */

export function useListCatalogueDeliverables<TData = Awaited<ReturnType<typeof listCatalogueDeliverables>>, TError = unknown>(
 params?: ListCatalogueDeliverablesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCatalogueDeliverables>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCatalogueDeliverablesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateCatalogueDeliverableUrl = () => {




  return `/api/catalogue/deliverables`
}

/**
 * @summary Create a catalogue deliverable (auto-generates a code when omitted)
 */
export const createCatalogueDeliverable = async (catalogueDeliverableCreate: CatalogueDeliverableCreate, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueDeliverable> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CatalogueDeliverable>(getCreateCatalogueDeliverableUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(catalogueDeliverableCreate)
  }
);}





export const getCreateCatalogueDeliverableMutationKey = () => ['createCatalogueDeliverable'] as const;

export const getCreateCatalogueDeliverableMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCatalogueDeliverable>>, TError,CreateCatalogueDeliverableMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createCatalogueDeliverable>>, TError,CreateCatalogueDeliverableMutationVariables, TContext> => {

const mutationKey = getCreateCatalogueDeliverableMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createCatalogueDeliverable>>, CreateCatalogueDeliverableMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createCatalogueDeliverable(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateCatalogueDeliverableMutationResult = NonNullable<Awaited<ReturnType<typeof createCatalogueDeliverable>>>
    export type CreateCatalogueDeliverableMutationBody = CatalogueDeliverableCreate
    export type CreateCatalogueDeliverableMutationError = Error
    export type CreateCatalogueDeliverableMutationVariables = {data: CatalogueDeliverableCreate}

    /**
 * @summary Create a catalogue deliverable (auto-generates a code when omitted)
 */
export const useCreateCatalogueDeliverable = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCatalogueDeliverable>>, TError,CreateCatalogueDeliverableMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createCatalogueDeliverable>>,
        TError,
        CreateCatalogueDeliverableMutationVariables,
        TContext
      > => {
      return useMutation(getCreateCatalogueDeliverableMutationOptions(options));
    }

export const getGetNextDeliverableCodeUrl = (params?: GetNextDeliverableCodeParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/catalogue/deliverables/next-code?${stringifiedParams}` : `/api/catalogue/deliverables/next-code`
}

/**
 * @summary Preview the next auto-generated deliverable code
 */
export const getNextDeliverableCode = async (params?: GetNextDeliverableCodeParams, options?: Parameters<typeof customFetch>[1]): Promise<NextDeliverableCode> => {

  return customFetch<NextDeliverableCode>(getGetNextDeliverableCodeUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetNextDeliverableCodeQueryKey = (params?: GetNextDeliverableCodeParams,) => {
    return [
    `/api/catalogue/deliverables/next-code`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetNextDeliverableCodeQueryOptions = <TData = Awaited<ReturnType<typeof getNextDeliverableCode>>, TError = unknown>(params?: GetNextDeliverableCodeParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getNextDeliverableCode>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetNextDeliverableCodeQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getNextDeliverableCode>>> = ({ signal }) => getNextDeliverableCode(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getNextDeliverableCode>>, TError, TData> & { queryKey: QueryKey }
}

export type GetNextDeliverableCodeQueryResult = NonNullable<Awaited<ReturnType<typeof getNextDeliverableCode>>>
export type GetNextDeliverableCodeQueryError = unknown


/**
 * @summary Preview the next auto-generated deliverable code
 */

export function useGetNextDeliverableCode<TData = Awaited<ReturnType<typeof getNextDeliverableCode>>, TError = unknown>(
 params?: GetNextDeliverableCodeParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getNextDeliverableCode>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetNextDeliverableCodeQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateCatalogueDeliverableUrl = (id: string,) => {




  return `/api/catalogue/deliverables/${id}`
}

/**
 * @summary Update a catalogue deliverable
 */
export const updateCatalogueDeliverable = async (id: string,
    catalogueDeliverableUpdate: CatalogueDeliverableUpdate, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueDeliverable> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CatalogueDeliverable>(getUpdateCatalogueDeliverableUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(catalogueDeliverableUpdate)
  }
);}





export const getUpdateCatalogueDeliverableMutationKey = () => ['updateCatalogueDeliverable'] as const;

export const getUpdateCatalogueDeliverableMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueDeliverable>>, TError,UpdateCatalogueDeliverableMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueDeliverable>>, TError,UpdateCatalogueDeliverableMutationVariables, TContext> => {

const mutationKey = getUpdateCatalogueDeliverableMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateCatalogueDeliverable>>, UpdateCatalogueDeliverableMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateCatalogueDeliverable(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateCatalogueDeliverableMutationResult = NonNullable<Awaited<ReturnType<typeof updateCatalogueDeliverable>>>
    export type UpdateCatalogueDeliverableMutationBody = CatalogueDeliverableUpdate
    export type UpdateCatalogueDeliverableMutationError = Error
    export type UpdateCatalogueDeliverableMutationVariables = {id: string;data: CatalogueDeliverableUpdate}

    /**
 * @summary Update a catalogue deliverable
 */
export const useUpdateCatalogueDeliverable = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueDeliverable>>, TError,UpdateCatalogueDeliverableMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateCatalogueDeliverable>>,
        TError,
        UpdateCatalogueDeliverableMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateCatalogueDeliverableMutationOptions(options));
    }

export const getDeleteCatalogueDeliverableUrl = (id: string,) => {




  return `/api/catalogue/deliverables/${id}`
}

/**
 * @summary Delete a catalogue deliverable
 */
export const deleteCatalogueDeliverable = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteCatalogueDeliverableUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteCatalogueDeliverableMutationKey = () => ['deleteCatalogueDeliverable'] as const;

export const getDeleteCatalogueDeliverableMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueDeliverable>>, TError,DeleteCatalogueDeliverableMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueDeliverable>>, TError,DeleteCatalogueDeliverableMutationVariables, TContext> => {

const mutationKey = getDeleteCatalogueDeliverableMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteCatalogueDeliverable>>, DeleteCatalogueDeliverableMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteCatalogueDeliverable(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteCatalogueDeliverableMutationResult = NonNullable<Awaited<ReturnType<typeof deleteCatalogueDeliverable>>>

    export type DeleteCatalogueDeliverableMutationError = Error
    export type DeleteCatalogueDeliverableMutationVariables = {id: string}

    /**
 * @summary Delete a catalogue deliverable
 */
export const useDeleteCatalogueDeliverable = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueDeliverable>>, TError,DeleteCatalogueDeliverableMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteCatalogueDeliverable>>,
        TError,
        DeleteCatalogueDeliverableMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteCatalogueDeliverableMutationOptions(options));
    }

export const getListCatalogueServicesUrl = (params?: ListCatalogueServicesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/catalogue/services?${stringifiedParams}` : `/api/catalogue/services`
}

/**
 * @summary List catalogue services
 */
export const listCatalogueServices = async (params?: ListCatalogueServicesParams, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueServiceList> => {

  return customFetch<CatalogueServiceList>(getListCatalogueServicesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCatalogueServicesQueryKey = (params?: ListCatalogueServicesParams,) => {
    return [
    `/api/catalogue/services`, ...(params ? [params] : [])
    ] as const;
    }


export const getListCatalogueServicesQueryOptions = <TData = Awaited<ReturnType<typeof listCatalogueServices>>, TError = unknown>(params?: ListCatalogueServicesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCatalogueServices>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCatalogueServicesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCatalogueServices>>> = ({ signal }) => listCatalogueServices(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCatalogueServices>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCatalogueServicesQueryResult = NonNullable<Awaited<ReturnType<typeof listCatalogueServices>>>
export type ListCatalogueServicesQueryError = unknown


/**
 * @summary List catalogue services
 */

export function useListCatalogueServices<TData = Awaited<ReturnType<typeof listCatalogueServices>>, TError = unknown>(
 params?: ListCatalogueServicesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCatalogueServices>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCatalogueServicesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateCatalogueServiceUrl = () => {




  return `/api/catalogue/services`
}

/**
 * @summary Create a catalogue service
 */
export const createCatalogueService = async (catalogueServiceCreate: CatalogueServiceCreate, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueService> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CatalogueService>(getCreateCatalogueServiceUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(catalogueServiceCreate)
  }
);}





export const getCreateCatalogueServiceMutationKey = () => ['createCatalogueService'] as const;

export const getCreateCatalogueServiceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCatalogueService>>, TError,CreateCatalogueServiceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createCatalogueService>>, TError,CreateCatalogueServiceMutationVariables, TContext> => {

const mutationKey = getCreateCatalogueServiceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createCatalogueService>>, CreateCatalogueServiceMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createCatalogueService(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateCatalogueServiceMutationResult = NonNullable<Awaited<ReturnType<typeof createCatalogueService>>>
    export type CreateCatalogueServiceMutationBody = CatalogueServiceCreate
    export type CreateCatalogueServiceMutationError = Error
    export type CreateCatalogueServiceMutationVariables = {data: CatalogueServiceCreate}

    /**
 * @summary Create a catalogue service
 */
export const useCreateCatalogueService = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCatalogueService>>, TError,CreateCatalogueServiceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createCatalogueService>>,
        TError,
        CreateCatalogueServiceMutationVariables,
        TContext
      > => {
      return useMutation(getCreateCatalogueServiceMutationOptions(options));
    }

export const getUpdateCatalogueServiceUrl = (id: string,) => {




  return `/api/catalogue/services/${id}`
}

/**
 * @summary Update a catalogue service
 */
export const updateCatalogueService = async (id: string,
    catalogueServiceUpdate: CatalogueServiceUpdate, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueService> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CatalogueService>(getUpdateCatalogueServiceUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(catalogueServiceUpdate)
  }
);}





export const getUpdateCatalogueServiceMutationKey = () => ['updateCatalogueService'] as const;

export const getUpdateCatalogueServiceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueService>>, TError,UpdateCatalogueServiceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueService>>, TError,UpdateCatalogueServiceMutationVariables, TContext> => {

const mutationKey = getUpdateCatalogueServiceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateCatalogueService>>, UpdateCatalogueServiceMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateCatalogueService(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateCatalogueServiceMutationResult = NonNullable<Awaited<ReturnType<typeof updateCatalogueService>>>
    export type UpdateCatalogueServiceMutationBody = CatalogueServiceUpdate
    export type UpdateCatalogueServiceMutationError = Error
    export type UpdateCatalogueServiceMutationVariables = {id: string;data: CatalogueServiceUpdate}

    /**
 * @summary Update a catalogue service
 */
export const useUpdateCatalogueService = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueService>>, TError,UpdateCatalogueServiceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateCatalogueService>>,
        TError,
        UpdateCatalogueServiceMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateCatalogueServiceMutationOptions(options));
    }

export const getDeleteCatalogueServiceUrl = (id: string,) => {




  return `/api/catalogue/services/${id}`
}

/**
 * @summary Delete a catalogue service
 */
export const deleteCatalogueService = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteCatalogueServiceUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteCatalogueServiceMutationKey = () => ['deleteCatalogueService'] as const;

export const getDeleteCatalogueServiceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueService>>, TError,DeleteCatalogueServiceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueService>>, TError,DeleteCatalogueServiceMutationVariables, TContext> => {

const mutationKey = getDeleteCatalogueServiceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteCatalogueService>>, DeleteCatalogueServiceMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteCatalogueService(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteCatalogueServiceMutationResult = NonNullable<Awaited<ReturnType<typeof deleteCatalogueService>>>

    export type DeleteCatalogueServiceMutationError = Error
    export type DeleteCatalogueServiceMutationVariables = {id: string}

    /**
 * @summary Delete a catalogue service
 */
export const useDeleteCatalogueService = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueService>>, TError,DeleteCatalogueServiceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteCatalogueService>>,
        TError,
        DeleteCatalogueServiceMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteCatalogueServiceMutationOptions(options));
    }

export const getListCatalogueServiceLinksUrl = (params?: ListCatalogueServiceLinksParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/catalogue/service-links?${stringifiedParams}` : `/api/catalogue/service-links`
}

/**
 * @summary List links between catalogue items and service offerings
 */
export const listCatalogueServiceLinks = async (params?: ListCatalogueServiceLinksParams, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueServiceLinkList> => {

  return customFetch<CatalogueServiceLinkList>(getListCatalogueServiceLinksUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCatalogueServiceLinksQueryKey = (params?: ListCatalogueServiceLinksParams,) => {
    return [
    `/api/catalogue/service-links`, ...(params ? [params] : [])
    ] as const;
    }


export const getListCatalogueServiceLinksQueryOptions = <TData = Awaited<ReturnType<typeof listCatalogueServiceLinks>>, TError = unknown>(params?: ListCatalogueServiceLinksParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCatalogueServiceLinks>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCatalogueServiceLinksQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCatalogueServiceLinks>>> = ({ signal }) => listCatalogueServiceLinks(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCatalogueServiceLinks>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCatalogueServiceLinksQueryResult = NonNullable<Awaited<ReturnType<typeof listCatalogueServiceLinks>>>
export type ListCatalogueServiceLinksQueryError = unknown


/**
 * @summary List links between catalogue items and service offerings
 */

export function useListCatalogueServiceLinks<TData = Awaited<ReturnType<typeof listCatalogueServiceLinks>>, TError = unknown>(
 params?: ListCatalogueServiceLinksParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCatalogueServiceLinks>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCatalogueServiceLinksQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateCatalogueServiceLinkUrl = () => {




  return `/api/catalogue/service-links`
}

/**
 * @summary Link a catalogue item to a service offering
 */
export const createCatalogueServiceLink = async (catalogueServiceLinkCreate: CatalogueServiceLinkCreate, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueServiceLink> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CatalogueServiceLink>(getCreateCatalogueServiceLinkUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(catalogueServiceLinkCreate)
  }
);}





export const getCreateCatalogueServiceLinkMutationKey = () => ['createCatalogueServiceLink'] as const;

export const getCreateCatalogueServiceLinkMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCatalogueServiceLink>>, TError,CreateCatalogueServiceLinkMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createCatalogueServiceLink>>, TError,CreateCatalogueServiceLinkMutationVariables, TContext> => {

const mutationKey = getCreateCatalogueServiceLinkMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createCatalogueServiceLink>>, CreateCatalogueServiceLinkMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createCatalogueServiceLink(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateCatalogueServiceLinkMutationResult = NonNullable<Awaited<ReturnType<typeof createCatalogueServiceLink>>>
    export type CreateCatalogueServiceLinkMutationBody = CatalogueServiceLinkCreate
    export type CreateCatalogueServiceLinkMutationError = Error
    export type CreateCatalogueServiceLinkMutationVariables = {data: CatalogueServiceLinkCreate}

    /**
 * @summary Link a catalogue item to a service offering
 */
export const useCreateCatalogueServiceLink = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCatalogueServiceLink>>, TError,CreateCatalogueServiceLinkMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createCatalogueServiceLink>>,
        TError,
        CreateCatalogueServiceLinkMutationVariables,
        TContext
      > => {
      return useMutation(getCreateCatalogueServiceLinkMutationOptions(options));
    }

export const getDeleteCatalogueServiceLinkUrl = (id: string,) => {




  return `/api/catalogue/service-links/${id}`
}

/**
 * @summary Remove a catalogue-item-to-service link
 */
export const deleteCatalogueServiceLink = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteCatalogueServiceLinkUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteCatalogueServiceLinkMutationKey = () => ['deleteCatalogueServiceLink'] as const;

export const getDeleteCatalogueServiceLinkMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueServiceLink>>, TError,DeleteCatalogueServiceLinkMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueServiceLink>>, TError,DeleteCatalogueServiceLinkMutationVariables, TContext> => {

const mutationKey = getDeleteCatalogueServiceLinkMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteCatalogueServiceLink>>, DeleteCatalogueServiceLinkMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteCatalogueServiceLink(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteCatalogueServiceLinkMutationResult = NonNullable<Awaited<ReturnType<typeof deleteCatalogueServiceLink>>>

    export type DeleteCatalogueServiceLinkMutationError = Error
    export type DeleteCatalogueServiceLinkMutationVariables = {id: string}

    /**
 * @summary Remove a catalogue-item-to-service link
 */
export const useDeleteCatalogueServiceLink = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueServiceLink>>, TError,DeleteCatalogueServiceLinkMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteCatalogueServiceLink>>,
        TError,
        DeleteCatalogueServiceLinkMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteCatalogueServiceLinkMutationOptions(options));
    }

export const getListCatalogueCollateralsUrl = (params?: ListCatalogueCollateralsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/catalogue/collaterals?${stringifiedParams}` : `/api/catalogue/collaterals`
}

/**
 * @summary List catalogue collateral (marketing/sales files and links)
 */
export const listCatalogueCollaterals = async (params?: ListCatalogueCollateralsParams, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueCollateralList> => {

  return customFetch<CatalogueCollateralList>(getListCatalogueCollateralsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCatalogueCollateralsQueryKey = (params?: ListCatalogueCollateralsParams,) => {
    return [
    `/api/catalogue/collaterals`, ...(params ? [params] : [])
    ] as const;
    }


export const getListCatalogueCollateralsQueryOptions = <TData = Awaited<ReturnType<typeof listCatalogueCollaterals>>, TError = unknown>(params?: ListCatalogueCollateralsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCatalogueCollaterals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCatalogueCollateralsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCatalogueCollaterals>>> = ({ signal }) => listCatalogueCollaterals(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCatalogueCollaterals>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCatalogueCollateralsQueryResult = NonNullable<Awaited<ReturnType<typeof listCatalogueCollaterals>>>
export type ListCatalogueCollateralsQueryError = unknown


/**
 * @summary List catalogue collateral (marketing/sales files and links)
 */

export function useListCatalogueCollaterals<TData = Awaited<ReturnType<typeof listCatalogueCollaterals>>, TError = unknown>(
 params?: ListCatalogueCollateralsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCatalogueCollaterals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCatalogueCollateralsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateCatalogueCollateralUrl = () => {




  return `/api/catalogue/collaterals`
}

/**
 * @summary Create a catalogue collateral entry
 */
export const createCatalogueCollateral = async (catalogueCollateralCreate: CatalogueCollateralCreate, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueCollateral> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CatalogueCollateral>(getCreateCatalogueCollateralUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(catalogueCollateralCreate)
  }
);}





export const getCreateCatalogueCollateralMutationKey = () => ['createCatalogueCollateral'] as const;

export const getCreateCatalogueCollateralMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCatalogueCollateral>>, TError,CreateCatalogueCollateralMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createCatalogueCollateral>>, TError,CreateCatalogueCollateralMutationVariables, TContext> => {

const mutationKey = getCreateCatalogueCollateralMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createCatalogueCollateral>>, CreateCatalogueCollateralMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createCatalogueCollateral(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateCatalogueCollateralMutationResult = NonNullable<Awaited<ReturnType<typeof createCatalogueCollateral>>>
    export type CreateCatalogueCollateralMutationBody = CatalogueCollateralCreate
    export type CreateCatalogueCollateralMutationError = Error
    export type CreateCatalogueCollateralMutationVariables = {data: CatalogueCollateralCreate}

    /**
 * @summary Create a catalogue collateral entry
 */
export const useCreateCatalogueCollateral = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCatalogueCollateral>>, TError,CreateCatalogueCollateralMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createCatalogueCollateral>>,
        TError,
        CreateCatalogueCollateralMutationVariables,
        TContext
      > => {
      return useMutation(getCreateCatalogueCollateralMutationOptions(options));
    }

export const getUpdateCatalogueCollateralUrl = (id: string,) => {




  return `/api/catalogue/collaterals/${id}`
}

/**
 * @summary Update a catalogue collateral entry
 */
export const updateCatalogueCollateral = async (id: string,
    catalogueCollateralUpdate: CatalogueCollateralUpdate, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueCollateral> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CatalogueCollateral>(getUpdateCatalogueCollateralUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(catalogueCollateralUpdate)
  }
);}





export const getUpdateCatalogueCollateralMutationKey = () => ['updateCatalogueCollateral'] as const;

export const getUpdateCatalogueCollateralMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueCollateral>>, TError,UpdateCatalogueCollateralMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueCollateral>>, TError,UpdateCatalogueCollateralMutationVariables, TContext> => {

const mutationKey = getUpdateCatalogueCollateralMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateCatalogueCollateral>>, UpdateCatalogueCollateralMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateCatalogueCollateral(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateCatalogueCollateralMutationResult = NonNullable<Awaited<ReturnType<typeof updateCatalogueCollateral>>>
    export type UpdateCatalogueCollateralMutationBody = CatalogueCollateralUpdate
    export type UpdateCatalogueCollateralMutationError = Error
    export type UpdateCatalogueCollateralMutationVariables = {id: string;data: CatalogueCollateralUpdate}

    /**
 * @summary Update a catalogue collateral entry
 */
export const useUpdateCatalogueCollateral = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueCollateral>>, TError,UpdateCatalogueCollateralMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateCatalogueCollateral>>,
        TError,
        UpdateCatalogueCollateralMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateCatalogueCollateralMutationOptions(options));
    }

export const getDeleteCatalogueCollateralUrl = (id: string,) => {




  return `/api/catalogue/collaterals/${id}`
}

/**
 * @summary Delete a catalogue collateral entry
 */
export const deleteCatalogueCollateral = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteCatalogueCollateralUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteCatalogueCollateralMutationKey = () => ['deleteCatalogueCollateral'] as const;

export const getDeleteCatalogueCollateralMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueCollateral>>, TError,DeleteCatalogueCollateralMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueCollateral>>, TError,DeleteCatalogueCollateralMutationVariables, TContext> => {

const mutationKey = getDeleteCatalogueCollateralMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteCatalogueCollateral>>, DeleteCatalogueCollateralMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteCatalogueCollateral(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteCatalogueCollateralMutationResult = NonNullable<Awaited<ReturnType<typeof deleteCatalogueCollateral>>>

    export type DeleteCatalogueCollateralMutationError = Error
    export type DeleteCatalogueCollateralMutationVariables = {id: string}

    /**
 * @summary Delete a catalogue collateral entry
 */
export const useDeleteCatalogueCollateral = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueCollateral>>, TError,DeleteCatalogueCollateralMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteCatalogueCollateral>>,
        TError,
        DeleteCatalogueCollateralMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteCatalogueCollateralMutationOptions(options));
    }

export const getListPartnerProductsUrl = (params: ListPartnerProductsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/partner-products?${stringifiedParams}` : `/api/partner-products`
}

/**
 * @summary List procured products/modules/services for a partner
 */
export const listPartnerProducts = async (params: ListPartnerProductsParams, options?: Parameters<typeof customFetch>[1]): Promise<PartnerProductList> => {

  return customFetch<PartnerProductList>(getListPartnerProductsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPartnerProductsQueryKey = (params?: ListPartnerProductsParams,) => {
    return [
    `/api/partner-products`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPartnerProductsQueryOptions = <TData = Awaited<ReturnType<typeof listPartnerProducts>>, TError = unknown>(params: ListPartnerProductsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPartnerProducts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPartnerProductsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPartnerProducts>>> = ({ signal }) => listPartnerProducts(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPartnerProducts>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPartnerProductsQueryResult = NonNullable<Awaited<ReturnType<typeof listPartnerProducts>>>
export type ListPartnerProductsQueryError = unknown


/**
 * @summary List procured products/modules/services for a partner
 */

export function useListPartnerProducts<TData = Awaited<ReturnType<typeof listPartnerProducts>>, TError = unknown>(
 params: ListPartnerProductsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPartnerProducts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPartnerProductsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreatePartnerProductUrl = () => {




  return `/api/partner-products`
}

/**
 * @summary Record a procured product/module/service for a partner
 */
export const createPartnerProduct = async (partnerProductCreate: PartnerProductCreate, options?: Parameters<typeof customFetch>[1]): Promise<PartnerProduct> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PartnerProduct>(getCreatePartnerProductUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(partnerProductCreate)
  }
);}





export const getCreatePartnerProductMutationKey = () => ['createPartnerProduct'] as const;

export const getCreatePartnerProductMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPartnerProduct>>, TError,CreatePartnerProductMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createPartnerProduct>>, TError,CreatePartnerProductMutationVariables, TContext> => {

const mutationKey = getCreatePartnerProductMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createPartnerProduct>>, CreatePartnerProductMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createPartnerProduct(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreatePartnerProductMutationResult = NonNullable<Awaited<ReturnType<typeof createPartnerProduct>>>
    export type CreatePartnerProductMutationBody = PartnerProductCreate
    export type CreatePartnerProductMutationError = Error
    export type CreatePartnerProductMutationVariables = {data: PartnerProductCreate}

    /**
 * @summary Record a procured product/module/service for a partner
 */
export const useCreatePartnerProduct = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPartnerProduct>>, TError,CreatePartnerProductMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createPartnerProduct>>,
        TError,
        CreatePartnerProductMutationVariables,
        TContext
      > => {
      return useMutation(getCreatePartnerProductMutationOptions(options));
    }

export const getUpdatePartnerProductUrl = (id: string,) => {




  return `/api/partner-products/${id}`
}

/**
 * @summary Update a partner product record
 */
export const updatePartnerProduct = async (id: string,
    partnerProductUpdate: PartnerProductUpdate, options?: Parameters<typeof customFetch>[1]): Promise<PartnerProduct> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PartnerProduct>(getUpdatePartnerProductUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(partnerProductUpdate)
  }
);}





export const getUpdatePartnerProductMutationKey = () => ['updatePartnerProduct'] as const;

export const getUpdatePartnerProductMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePartnerProduct>>, TError,UpdatePartnerProductMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updatePartnerProduct>>, TError,UpdatePartnerProductMutationVariables, TContext> => {

const mutationKey = getUpdatePartnerProductMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updatePartnerProduct>>, UpdatePartnerProductMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updatePartnerProduct(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdatePartnerProductMutationResult = NonNullable<Awaited<ReturnType<typeof updatePartnerProduct>>>
    export type UpdatePartnerProductMutationBody = PartnerProductUpdate
    export type UpdatePartnerProductMutationError = Error
    export type UpdatePartnerProductMutationVariables = {id: string;data: PartnerProductUpdate}

    /**
 * @summary Update a partner product record
 */
export const useUpdatePartnerProduct = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePartnerProduct>>, TError,UpdatePartnerProductMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updatePartnerProduct>>,
        TError,
        UpdatePartnerProductMutationVariables,
        TContext
      > => {
      return useMutation(getUpdatePartnerProductMutationOptions(options));
    }

export const getDeletePartnerProductUrl = (id: string,) => {




  return `/api/partner-products/${id}`
}

/**
 * @summary Delete a partner product record
 */
export const deletePartnerProduct = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeletePartnerProductUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeletePartnerProductMutationKey = () => ['deletePartnerProduct'] as const;

export const getDeletePartnerProductMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deletePartnerProduct>>, TError,DeletePartnerProductMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deletePartnerProduct>>, TError,DeletePartnerProductMutationVariables, TContext> => {

const mutationKey = getDeletePartnerProductMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deletePartnerProduct>>, DeletePartnerProductMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deletePartnerProduct(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeletePartnerProductMutationResult = NonNullable<Awaited<ReturnType<typeof deletePartnerProduct>>>

    export type DeletePartnerProductMutationError = Error
    export type DeletePartnerProductMutationVariables = {id: string}

    /**
 * @summary Delete a partner product record
 */
export const useDeletePartnerProduct = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deletePartnerProduct>>, TError,DeletePartnerProductMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deletePartnerProduct>>,
        TError,
        DeletePartnerProductMutationVariables,
        TContext
      > => {
      return useMutation(getDeletePartnerProductMutationOptions(options));
    }

export const getRenewPartnerProductUrl = (id: string,) => {




  return `/api/partner-products/${id}/renew`
}

/**
 * @summary Renew a partner product (advances renewal/expiration dates)
 */
export const renewPartnerProduct = async (id: string,
    partnerProductRenew: PartnerProductRenew, options?: Parameters<typeof customFetch>[1]): Promise<PartnerProduct> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PartnerProduct>(getRenewPartnerProductUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(partnerProductRenew)
  }
);}





export const getRenewPartnerProductMutationKey = () => ['renewPartnerProduct'] as const;

export const getRenewPartnerProductMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof renewPartnerProduct>>, TError,RenewPartnerProductMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof renewPartnerProduct>>, TError,RenewPartnerProductMutationVariables, TContext> => {

const mutationKey = getRenewPartnerProductMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof renewPartnerProduct>>, RenewPartnerProductMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  renewPartnerProduct(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RenewPartnerProductMutationResult = NonNullable<Awaited<ReturnType<typeof renewPartnerProduct>>>
    export type RenewPartnerProductMutationBody = PartnerProductRenew
    export type RenewPartnerProductMutationError = Error
    export type RenewPartnerProductMutationVariables = {id: string;data: PartnerProductRenew}

    /**
 * @summary Renew a partner product (advances renewal/expiration dates)
 */
export const useRenewPartnerProduct = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof renewPartnerProduct>>, TError,RenewPartnerProductMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof renewPartnerProduct>>,
        TError,
        RenewPartnerProductMutationVariables,
        TContext
      > => {
      return useMutation(getRenewPartnerProductMutationOptions(options));
    }

export const getGetTargetMarketConfigUrl = () => {




  return `/api/account-intelligence/target-market-config`
}

/**
 * @summary Get the target-market segment ratings and target industry list
 */
export const getTargetMarketConfig = async ( options?: Parameters<typeof customFetch>[1]): Promise<TargetMarketConfig> => {

  return customFetch<TargetMarketConfig>(getGetTargetMarketConfigUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTargetMarketConfigQueryKey = () => {
    return [
    `/api/account-intelligence/target-market-config`
    ] as const;
    }


export const getGetTargetMarketConfigQueryOptions = <TData = Awaited<ReturnType<typeof getTargetMarketConfig>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTargetMarketConfig>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTargetMarketConfigQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTargetMarketConfig>>> = ({ signal }) => getTargetMarketConfig({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTargetMarketConfig>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTargetMarketConfigQueryResult = NonNullable<Awaited<ReturnType<typeof getTargetMarketConfig>>>
export type GetTargetMarketConfigQueryError = unknown


/**
 * @summary Get the target-market segment ratings and target industry list
 */

export function useGetTargetMarketConfig<TData = Awaited<ReturnType<typeof getTargetMarketConfig>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTargetMarketConfig>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTargetMarketConfigQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateTargetMarketConfigUrl = () => {




  return `/api/account-intelligence/target-market-config`
}

/**
 * @summary Replace the target-market configuration
 */
export const updateTargetMarketConfig = async (targetMarketConfig: TargetMarketConfig, options?: Parameters<typeof customFetch>[1]): Promise<TargetMarketConfig> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TargetMarketConfig>(getUpdateTargetMarketConfigUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(targetMarketConfig)
  }
);}





export const getUpdateTargetMarketConfigMutationKey = () => ['updateTargetMarketConfig'] as const;

export const getUpdateTargetMarketConfigMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTargetMarketConfig>>, TError,UpdateTargetMarketConfigMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateTargetMarketConfig>>, TError,UpdateTargetMarketConfigMutationVariables, TContext> => {

const mutationKey = getUpdateTargetMarketConfigMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateTargetMarketConfig>>, UpdateTargetMarketConfigMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateTargetMarketConfig(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateTargetMarketConfigMutationResult = NonNullable<Awaited<ReturnType<typeof updateTargetMarketConfig>>>
    export type UpdateTargetMarketConfigMutationBody = TargetMarketConfig
    export type UpdateTargetMarketConfigMutationError = unknown
    export type UpdateTargetMarketConfigMutationVariables = {data: TargetMarketConfig}

    /**
 * @summary Replace the target-market configuration
 */
export const useUpdateTargetMarketConfig = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTargetMarketConfig>>, TError,UpdateTargetMarketConfigMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateTargetMarketConfig>>,
        TError,
        UpdateTargetMarketConfigMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateTargetMarketConfigMutationOptions(options));
    }

export const getGetAccountRecommendationsUrl = () => {




  return `/api/account-intelligence/recommendations`
}

/**
 * @summary Compute health-score-based temperature/focus recommendations for every account
 */
export const getAccountRecommendations = async ( options?: Parameters<typeof customFetch>[1]): Promise<AccountRecommendationList> => {

  return customFetch<AccountRecommendationList>(getGetAccountRecommendationsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAccountRecommendationsQueryKey = () => {
    return [
    `/api/account-intelligence/recommendations`
    ] as const;
    }


export const getGetAccountRecommendationsQueryOptions = <TData = Awaited<ReturnType<typeof getAccountRecommendations>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccountRecommendations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAccountRecommendationsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAccountRecommendations>>> = ({ signal }) => getAccountRecommendations({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAccountRecommendations>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAccountRecommendationsQueryResult = NonNullable<Awaited<ReturnType<typeof getAccountRecommendations>>>
export type GetAccountRecommendationsQueryError = unknown


/**
 * @summary Compute health-score-based temperature/focus recommendations for every account
 */

export function useGetAccountRecommendations<TData = Awaited<ReturnType<typeof getAccountRecommendations>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccountRecommendations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAccountRecommendationsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getApplyAccountRecommendationsUrl = () => {




  return `/api/account-intelligence/apply-recommendations`
}

/**
 * @summary Apply recommended temperature/focus values to the selected accounts
 */
export const applyAccountRecommendations = async (applyRecommendationsBody: ApplyRecommendationsBody, options?: Parameters<typeof customFetch>[1]): Promise<ApplyRecommendationsResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ApplyRecommendationsResult>(getApplyAccountRecommendationsUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(applyRecommendationsBody)
  }
);}





export const getApplyAccountRecommendationsMutationKey = () => ['applyAccountRecommendations'] as const;

export const getApplyAccountRecommendationsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyAccountRecommendations>>, TError,ApplyAccountRecommendationsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof applyAccountRecommendations>>, TError,ApplyAccountRecommendationsMutationVariables, TContext> => {

const mutationKey = getApplyAccountRecommendationsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof applyAccountRecommendations>>, ApplyAccountRecommendationsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  applyAccountRecommendations(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApplyAccountRecommendationsMutationResult = NonNullable<Awaited<ReturnType<typeof applyAccountRecommendations>>>
    export type ApplyAccountRecommendationsMutationBody = ApplyRecommendationsBody
    export type ApplyAccountRecommendationsMutationError = unknown
    export type ApplyAccountRecommendationsMutationVariables = {data: ApplyRecommendationsBody}

    /**
 * @summary Apply recommended temperature/focus values to the selected accounts
 */
export const useApplyAccountRecommendations = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyAccountRecommendations>>, TError,ApplyAccountRecommendationsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof applyAccountRecommendations>>,
        TError,
        ApplyAccountRecommendationsMutationVariables,
        TContext
      > => {
      return useMutation(getApplyAccountRecommendationsMutationOptions(options));
    }

export const getGenerateAccountBriefUrl = (id: string,) => {




  return `/api/account-intelligence/accounts/${id}/brief`
}

/**
 * @summary Generate an AI narrative brief for a account via the project LLM
 */
export const generateAccountBrief = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<AccountBrief> => {

  return customFetch<AccountBrief>(getGenerateAccountBriefUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getGenerateAccountBriefMutationKey = () => ['generateAccountBrief'] as const;

export const getGenerateAccountBriefMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateAccountBrief>>, TError,GenerateAccountBriefMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof generateAccountBrief>>, TError,GenerateAccountBriefMutationVariables, TContext> => {

const mutationKey = getGenerateAccountBriefMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof generateAccountBrief>>, GenerateAccountBriefMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  generateAccountBrief(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenerateAccountBriefMutationResult = NonNullable<Awaited<ReturnType<typeof generateAccountBrief>>>

    export type GenerateAccountBriefMutationError = Error
    export type GenerateAccountBriefMutationVariables = {id: string}

    /**
 * @summary Generate an AI narrative brief for a account via the project LLM
 */
export const useGenerateAccountBrief = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateAccountBrief>>, TError,GenerateAccountBriefMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof generateAccountBrief>>,
        TError,
        GenerateAccountBriefMutationVariables,
        TContext
      > => {
      return useMutation(getGenerateAccountBriefMutationOptions(options));
    }

export const getListRankedProspectsUrl = (params?: ListRankedProspectsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/intelligence/prospects?${stringifiedParams}` : `/api/intelligence/prospects`
}

/**
 * @summary Rank accounts by transparent fit, engagement, and buying-signal factors
 */
export const listRankedProspects = async (params?: ListRankedProspectsParams, options?: Parameters<typeof customFetch>[1]): Promise<RankedProspectList> => {

  return customFetch<RankedProspectList>(getListRankedProspectsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListRankedProspectsQueryKey = (params?: ListRankedProspectsParams,) => {
    return [
    `/api/intelligence/prospects`, ...(params ? [params] : [])
    ] as const;
    }


export const getListRankedProspectsQueryOptions = <TData = Awaited<ReturnType<typeof listRankedProspects>>, TError = unknown>(params?: ListRankedProspectsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRankedProspects>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListRankedProspectsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listRankedProspects>>> = ({ signal }) => listRankedProspects(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listRankedProspects>>, TError, TData> & { queryKey: QueryKey }
}

export type ListRankedProspectsQueryResult = NonNullable<Awaited<ReturnType<typeof listRankedProspects>>>
export type ListRankedProspectsQueryError = unknown


/**
 * @summary Rank accounts by transparent fit, engagement, and buying-signal factors
 */

export function useListRankedProspects<TData = Awaited<ReturnType<typeof listRankedProspects>>, TError = unknown>(
 params?: ListRankedProspectsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRankedProspects>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListRankedProspectsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRecomputePortfolioScoresUrl = () => {




  return `/api/intelligence/scores/recompute`
}

/**
 * @summary Recompute signal-aware ICP scores for every account
 */
export const recomputePortfolioScores = async ( options?: Parameters<typeof customFetch>[1]): Promise<RecomputePortfolioScores200> => {

  return customFetch<RecomputePortfolioScores200>(getRecomputePortfolioScoresUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRecomputePortfolioScoresMutationKey = () => ['recomputePortfolioScores'] as const;

export const getRecomputePortfolioScoresMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recomputePortfolioScores>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof recomputePortfolioScores>>, TError,void, TContext> => {

const mutationKey = getRecomputePortfolioScoresMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof recomputePortfolioScores>>, void> = () => {


          return  recomputePortfolioScores(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RecomputePortfolioScoresMutationResult = NonNullable<Awaited<ReturnType<typeof recomputePortfolioScores>>>

    export type RecomputePortfolioScoresMutationError = unknown


    /**
 * @summary Recompute signal-aware ICP scores for every account
 */
export const useRecomputePortfolioScores = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recomputePortfolioScores>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof recomputePortfolioScores>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getRecomputePortfolioScoresMutationOptions(options));
    }

export const getGetAccountScoreBreakdownUrl = (accountId: string,) => {




  return `/api/intelligence/accounts/${accountId}/score`
}

/**
 * @summary Get the transparent ICP score and evidence for one account
 */
export const getAccountScoreBreakdown = async (accountId: string, options?: Parameters<typeof customFetch>[1]): Promise<RankedProspect> => {

  return customFetch<RankedProspect>(getGetAccountScoreBreakdownUrl(accountId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAccountScoreBreakdownQueryKey = (accountId: string,) => {
    return [
    `/api/intelligence/accounts/${accountId}/score`
    ] as const;
    }


export const getGetAccountScoreBreakdownQueryOptions = <TData = Awaited<ReturnType<typeof getAccountScoreBreakdown>>, TError = Error>(accountId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccountScoreBreakdown>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAccountScoreBreakdownQueryKey(accountId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAccountScoreBreakdown>>> = ({ signal }) => getAccountScoreBreakdown(accountId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: accountId !== null && accountId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAccountScoreBreakdown>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAccountScoreBreakdownQueryResult = NonNullable<Awaited<ReturnType<typeof getAccountScoreBreakdown>>>
export type GetAccountScoreBreakdownQueryError = Error


/**
 * @summary Get the transparent ICP score and evidence for one account
 */

export function useGetAccountScoreBreakdown<TData = Awaited<ReturnType<typeof getAccountScoreBreakdown>>, TError = Error>(
 accountId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccountScoreBreakdown>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAccountScoreBreakdownQueryOptions(accountId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetOpportunityOutcomeTaxonomyUrl = () => {




  return `/api/intelligence/outcome-taxonomy`
}

/**
 * @summary Get configured structured win and loss reasons
 */
export const getOpportunityOutcomeTaxonomy = async ( options?: Parameters<typeof customFetch>[1]): Promise<OpportunityOutcomeTaxonomy> => {

  return customFetch<OpportunityOutcomeTaxonomy>(getGetOpportunityOutcomeTaxonomyUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetOpportunityOutcomeTaxonomyQueryKey = () => {
    return [
    `/api/intelligence/outcome-taxonomy`
    ] as const;
    }


export const getGetOpportunityOutcomeTaxonomyQueryOptions = <TData = Awaited<ReturnType<typeof getOpportunityOutcomeTaxonomy>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getOpportunityOutcomeTaxonomy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetOpportunityOutcomeTaxonomyQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getOpportunityOutcomeTaxonomy>>> = ({ signal }) => getOpportunityOutcomeTaxonomy({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getOpportunityOutcomeTaxonomy>>, TError, TData> & { queryKey: QueryKey }
}

export type GetOpportunityOutcomeTaxonomyQueryResult = NonNullable<Awaited<ReturnType<typeof getOpportunityOutcomeTaxonomy>>>
export type GetOpportunityOutcomeTaxonomyQueryError = unknown


/**
 * @summary Get configured structured win and loss reasons
 */

export function useGetOpportunityOutcomeTaxonomy<TData = Awaited<ReturnType<typeof getOpportunityOutcomeTaxonomy>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getOpportunityOutcomeTaxonomy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetOpportunityOutcomeTaxonomyQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCaptureOpportunityOutcomeUrl = (opportunityId: string,) => {




  return `/api/intelligence/opportunities/${opportunityId}/outcome`
}

/**
 * @summary Close a opportunity with a structured won or lost reason
 */
export const captureOpportunityOutcome = async (opportunityId: string,
    opportunityOutcomeCommand: OpportunityOutcomeCommand, options?: Parameters<typeof customFetch>[1]): Promise<OpportunityOutcome> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OpportunityOutcome>(getCaptureOpportunityOutcomeUrl(opportunityId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(opportunityOutcomeCommand)
  }
);}





export const getCaptureOpportunityOutcomeMutationKey = () => ['captureOpportunityOutcome'] as const;

export const getCaptureOpportunityOutcomeMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof captureOpportunityOutcome>>, TError,CaptureOpportunityOutcomeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof captureOpportunityOutcome>>, TError,CaptureOpportunityOutcomeMutationVariables, TContext> => {

const mutationKey = getCaptureOpportunityOutcomeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof captureOpportunityOutcome>>, CaptureOpportunityOutcomeMutationVariables> = (props) => {
          const {opportunityId,data} = props ?? {};

          return  captureOpportunityOutcome(opportunityId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CaptureOpportunityOutcomeMutationResult = NonNullable<Awaited<ReturnType<typeof captureOpportunityOutcome>>>
    export type CaptureOpportunityOutcomeMutationBody = OpportunityOutcomeCommand
    export type CaptureOpportunityOutcomeMutationError = Error
    export type CaptureOpportunityOutcomeMutationVariables = {opportunityId: string;data: OpportunityOutcomeCommand}

    /**
 * @summary Close a opportunity with a structured won or lost reason
 */
export const useCaptureOpportunityOutcome = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof captureOpportunityOutcome>>, TError,CaptureOpportunityOutcomeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof captureOpportunityOutcome>>,
        TError,
        CaptureOpportunityOutcomeMutationVariables,
        TContext
      > => {
      return useMutation(getCaptureOpportunityOutcomeMutationOptions(options));
    }

export const getListOpportunityOutcomesUrl = (params?: ListOpportunityOutcomesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/intelligence/outcomes?${stringifiedParams}` : `/api/intelligence/outcomes`
}

/**
 * @summary List structured opportunity outcomes
 */
export const listOpportunityOutcomes = async (params?: ListOpportunityOutcomesParams, options?: Parameters<typeof customFetch>[1]): Promise<ListOpportunityOutcomes200> => {

  return customFetch<ListOpportunityOutcomes200>(getListOpportunityOutcomesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListOpportunityOutcomesQueryKey = (params?: ListOpportunityOutcomesParams,) => {
    return [
    `/api/intelligence/outcomes`, ...(params ? [params] : [])
    ] as const;
    }


export const getListOpportunityOutcomesQueryOptions = <TData = Awaited<ReturnType<typeof listOpportunityOutcomes>>, TError = unknown>(params?: ListOpportunityOutcomesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOpportunityOutcomes>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListOpportunityOutcomesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listOpportunityOutcomes>>> = ({ signal }) => listOpportunityOutcomes(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listOpportunityOutcomes>>, TError, TData> & { queryKey: QueryKey }
}

export type ListOpportunityOutcomesQueryResult = NonNullable<Awaited<ReturnType<typeof listOpportunityOutcomes>>>
export type ListOpportunityOutcomesQueryError = unknown


/**
 * @summary List structured opportunity outcomes
 */

export function useListOpportunityOutcomes<TData = Awaited<ReturnType<typeof listOpportunityOutcomes>>, TError = unknown>(
 params?: ListOpportunityOutcomesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOpportunityOutcomes>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListOpportunityOutcomesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGenerateMarketAnalysisUrl = () => {




  return `/api/intelligence/market-analysis`
}

/**
 * @summary Generate an attributed market and win/loss analysis
 */
export const generateMarketAnalysis = async (generateMarketAnalysisBody?: GenerateMarketAnalysisBody, options?: Parameters<typeof customFetch>[1]): Promise<MarketAnalysis> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MarketAnalysis>(getGenerateMarketAnalysisUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(generateMarketAnalysisBody)
  }
);}





export const getGenerateMarketAnalysisMutationKey = () => ['generateMarketAnalysis'] as const;

export const getGenerateMarketAnalysisMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateMarketAnalysis>>, TError,GenerateMarketAnalysisMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof generateMarketAnalysis>>, TError,GenerateMarketAnalysisMutationVariables, TContext> => {

const mutationKey = getGenerateMarketAnalysisMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof generateMarketAnalysis>>, GenerateMarketAnalysisMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  generateMarketAnalysis(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenerateMarketAnalysisMutationResult = NonNullable<Awaited<ReturnType<typeof generateMarketAnalysis>>>
    export type GenerateMarketAnalysisMutationBody = GenerateMarketAnalysisBody | undefined
    export type GenerateMarketAnalysisMutationError = unknown
    export type GenerateMarketAnalysisMutationVariables = {data?: GenerateMarketAnalysisBody}

    /**
 * @summary Generate an attributed market and win/loss analysis
 */
export const useGenerateMarketAnalysis = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateMarketAnalysis>>, TError,GenerateMarketAnalysisMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof generateMarketAnalysis>>,
        TError,
        GenerateMarketAnalysisMutationVariables,
        TContext
      > => {
      return useMutation(getGenerateMarketAnalysisMutationOptions(options));
    }

export const getGetLatestMarketAnalysisUrl = () => {




  return `/api/intelligence/market-analysis/latest`
}

/**
 * @summary Get the most recently generated market analysis
 */
export const getLatestMarketAnalysis = async ( options?: Parameters<typeof customFetch>[1]): Promise<MarketAnalysis> => {

  return customFetch<MarketAnalysis>(getGetLatestMarketAnalysisUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetLatestMarketAnalysisQueryKey = () => {
    return [
    `/api/intelligence/market-analysis/latest`
    ] as const;
    }


export const getGetLatestMarketAnalysisQueryOptions = <TData = Awaited<ReturnType<typeof getLatestMarketAnalysis>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLatestMarketAnalysis>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetLatestMarketAnalysisQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getLatestMarketAnalysis>>> = ({ signal }) => getLatestMarketAnalysis({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getLatestMarketAnalysis>>, TError, TData> & { queryKey: QueryKey }
}

export type GetLatestMarketAnalysisQueryResult = NonNullable<Awaited<ReturnType<typeof getLatestMarketAnalysis>>>
export type GetLatestMarketAnalysisQueryError = Error


/**
 * @summary Get the most recently generated market analysis
 */

export function useGetLatestMarketAnalysis<TData = Awaited<ReturnType<typeof getLatestMarketAnalysis>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLatestMarketAnalysis>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetLatestMarketAnalysisQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGenerateNextBestActionUrl = (accountId: string,) => {




  return `/api/intelligence/accounts/${accountId}/next-action`
}

/**
 * @summary Generate an authority-gated concrete next-best-action
 */
export const generateNextBestAction = async (accountId: string, options?: Parameters<typeof customFetch>[1]): Promise<NextBestAction> => {

  return customFetch<NextBestAction>(getGenerateNextBestActionUrl(accountId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getGenerateNextBestActionMutationKey = () => ['generateNextBestAction'] as const;

export const getGenerateNextBestActionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateNextBestAction>>, TError,GenerateNextBestActionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof generateNextBestAction>>, TError,GenerateNextBestActionMutationVariables, TContext> => {

const mutationKey = getGenerateNextBestActionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof generateNextBestAction>>, GenerateNextBestActionMutationVariables> = (props) => {
          const {accountId} = props ?? {};

          return  generateNextBestAction(accountId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenerateNextBestActionMutationResult = NonNullable<Awaited<ReturnType<typeof generateNextBestAction>>>

    export type GenerateNextBestActionMutationError = unknown
    export type GenerateNextBestActionMutationVariables = {accountId: string}

    /**
 * @summary Generate an authority-gated concrete next-best-action
 */
export const useGenerateNextBestAction = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateNextBestAction>>, TError,GenerateNextBestActionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof generateNextBestAction>>,
        TError,
        GenerateNextBestActionMutationVariables,
        TContext
      > => {
      return useMutation(getGenerateNextBestActionMutationOptions(options));
    }

export const getListNextBestActionsUrl = (params?: ListNextBestActionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/intelligence/next-actions?${stringifiedParams}` : `/api/intelligence/next-actions`
}

/**
 * @summary List the cross-portfolio next-action queue
 */
export const listNextBestActions = async (params?: ListNextBestActionsParams, options?: Parameters<typeof customFetch>[1]): Promise<ListNextBestActions200> => {

  return customFetch<ListNextBestActions200>(getListNextBestActionsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListNextBestActionsQueryKey = (params?: ListNextBestActionsParams,) => {
    return [
    `/api/intelligence/next-actions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListNextBestActionsQueryOptions = <TData = Awaited<ReturnType<typeof listNextBestActions>>, TError = unknown>(params?: ListNextBestActionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listNextBestActions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListNextBestActionsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listNextBestActions>>> = ({ signal }) => listNextBestActions(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listNextBestActions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListNextBestActionsQueryResult = NonNullable<Awaited<ReturnType<typeof listNextBestActions>>>
export type ListNextBestActionsQueryError = unknown


/**
 * @summary List the cross-portfolio next-action queue
 */

export function useListNextBestActions<TData = Awaited<ReturnType<typeof listNextBestActions>>, TError = unknown>(
 params?: ListNextBestActionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listNextBestActions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListNextBestActionsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateNextBestActionStatusUrl = (id: string,) => {




  return `/api/intelligence/next-actions/${id}/status`
}

/**
 * @summary Complete or dismiss a recommended action
 */
export const updateNextBestActionStatus = async (id: string,
    updateNextBestActionStatusBody: UpdateNextBestActionStatusBody, options?: Parameters<typeof customFetch>[1]): Promise<NextBestAction> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<NextBestAction>(getUpdateNextBestActionStatusUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateNextBestActionStatusBody)
  }
);}





export const getUpdateNextBestActionStatusMutationKey = () => ['updateNextBestActionStatus'] as const;

export const getUpdateNextBestActionStatusMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateNextBestActionStatus>>, TError,UpdateNextBestActionStatusMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateNextBestActionStatus>>, TError,UpdateNextBestActionStatusMutationVariables, TContext> => {

const mutationKey = getUpdateNextBestActionStatusMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateNextBestActionStatus>>, UpdateNextBestActionStatusMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateNextBestActionStatus(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateNextBestActionStatusMutationResult = NonNullable<Awaited<ReturnType<typeof updateNextBestActionStatus>>>
    export type UpdateNextBestActionStatusMutationBody = UpdateNextBestActionStatusBody
    export type UpdateNextBestActionStatusMutationError = Error
    export type UpdateNextBestActionStatusMutationVariables = {id: string;data: UpdateNextBestActionStatusBody}

    /**
 * @summary Complete or dismiss a recommended action
 */
export const useUpdateNextBestActionStatus = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateNextBestActionStatus>>, TError,UpdateNextBestActionStatusMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateNextBestActionStatus>>,
        TError,
        UpdateNextBestActionStatusMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateNextBestActionStatusMutationOptions(options));
    }

export const getListRelationshipHealthUrl = (params?: ListRelationshipHealthParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/relationship-health?${stringifiedParams}` : `/api/relationship-health`
}

/**
 * @summary List the latest explainable health assessment for each authorised relationship
 */
export const listRelationshipHealth = async (params?: ListRelationshipHealthParams, options?: Parameters<typeof customFetch>[1]): Promise<RelationshipHealthSnapshotList> => {

  return customFetch<RelationshipHealthSnapshotList>(getListRelationshipHealthUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListRelationshipHealthQueryKey = (params?: ListRelationshipHealthParams,) => {
    return [
    `/api/relationship-health`, ...(params ? [params] : [])
    ] as const;
    }


export const getListRelationshipHealthQueryOptions = <TData = Awaited<ReturnType<typeof listRelationshipHealth>>, TError = Error>(params?: ListRelationshipHealthParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRelationshipHealth>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListRelationshipHealthQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listRelationshipHealth>>> = ({ signal }) => listRelationshipHealth(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listRelationshipHealth>>, TError, TData> & { queryKey: QueryKey }
}

export type ListRelationshipHealthQueryResult = NonNullable<Awaited<ReturnType<typeof listRelationshipHealth>>>
export type ListRelationshipHealthQueryError = Error


/**
 * @summary List the latest explainable health assessment for each authorised relationship
 */

export function useListRelationshipHealth<TData = Awaited<ReturnType<typeof listRelationshipHealth>>, TError = Error>(
 params?: ListRelationshipHealthParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRelationshipHealth>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListRelationshipHealthQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetRelationshipHealthUrl = (id: string,) => {




  return `/api/relationship-health/accounts/${id}`
}

/**
 * @summary Read the latest explainable health assessment for one authorised relationship
 */
export const getRelationshipHealth = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<RelationshipHealthSnapshot> => {

  return customFetch<RelationshipHealthSnapshot>(getGetRelationshipHealthUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetRelationshipHealthQueryKey = (id: string,) => {
    return [
    `/api/relationship-health/accounts/${id}`
    ] as const;
    }


export const getGetRelationshipHealthQueryOptions = <TData = Awaited<ReturnType<typeof getRelationshipHealth>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRelationshipHealth>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetRelationshipHealthQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getRelationshipHealth>>> = ({ signal }) => getRelationshipHealth(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getRelationshipHealth>>, TError, TData> & { queryKey: QueryKey }
}

export type GetRelationshipHealthQueryResult = NonNullable<Awaited<ReturnType<typeof getRelationshipHealth>>>
export type GetRelationshipHealthQueryError = Error


/**
 * @summary Read the latest explainable health assessment for one authorised relationship
 */

export function useGetRelationshipHealth<TData = Awaited<ReturnType<typeof getRelationshipHealth>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRelationshipHealth>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetRelationshipHealthQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRefreshRelationshipHealthUrl = () => {




  return `/api/relationship-health/refresh`
}

/**
 * @summary Recalculate bounded health assessments without dispatching outreach
 */
export const refreshRelationshipHealth = async (refreshRelationshipHealthBody: RefreshRelationshipHealthBody, options?: Parameters<typeof customFetch>[1]): Promise<RelationshipHealthSnapshotList> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<RelationshipHealthSnapshotList>(getRefreshRelationshipHealthUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(refreshRelationshipHealthBody)
  }
);}





export const getRefreshRelationshipHealthMutationKey = () => ['refreshRelationshipHealth'] as const;

export const getRefreshRelationshipHealthMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof refreshRelationshipHealth>>, TError,RefreshRelationshipHealthMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof refreshRelationshipHealth>>, TError,RefreshRelationshipHealthMutationVariables, TContext> => {

const mutationKey = getRefreshRelationshipHealthMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof refreshRelationshipHealth>>, RefreshRelationshipHealthMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  refreshRelationshipHealth(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RefreshRelationshipHealthMutationResult = NonNullable<Awaited<ReturnType<typeof refreshRelationshipHealth>>>
    export type RefreshRelationshipHealthMutationBody = RefreshRelationshipHealthBody
    export type RefreshRelationshipHealthMutationError = Error
    export type RefreshRelationshipHealthMutationVariables = {data: RefreshRelationshipHealthBody}

    /**
 * @summary Recalculate bounded health assessments without dispatching outreach
 */
export const useRefreshRelationshipHealth = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof refreshRelationshipHealth>>, TError,RefreshRelationshipHealthMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof refreshRelationshipHealth>>,
        TError,
        RefreshRelationshipHealthMutationVariables,
        TContext
      > => {
      return useMutation(getRefreshRelationshipHealthMutationOptions(options));
    }

export const getGetRelationshipHealthRulesUrl = () => {




  return `/api/relationship-health/rules`
}

/**
 * @summary Read the deterministic versioned scoring rules and relationship expectations
 */
export const getRelationshipHealthRules = async ( options?: Parameters<typeof customFetch>[1]): Promise<RelationshipHealthRuleSet> => {

  return customFetch<RelationshipHealthRuleSet>(getGetRelationshipHealthRulesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetRelationshipHealthRulesQueryKey = () => {
    return [
    `/api/relationship-health/rules`
    ] as const;
    }


export const getGetRelationshipHealthRulesQueryOptions = <TData = Awaited<ReturnType<typeof getRelationshipHealthRules>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRelationshipHealthRules>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetRelationshipHealthRulesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getRelationshipHealthRules>>> = ({ signal }) => getRelationshipHealthRules({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getRelationshipHealthRules>>, TError, TData> & { queryKey: QueryKey }
}

export type GetRelationshipHealthRulesQueryResult = NonNullable<Awaited<ReturnType<typeof getRelationshipHealthRules>>>
export type GetRelationshipHealthRulesQueryError = unknown


/**
 * @summary Read the deterministic versioned scoring rules and relationship expectations
 */

export function useGetRelationshipHealthRules<TData = Awaited<ReturnType<typeof getRelationshipHealthRules>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRelationshipHealthRules>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetRelationshipHealthRulesQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListRelationshipHealthSnapshotsUrl = (params?: ListRelationshipHealthSnapshotsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/relationship-health/snapshots?${stringifiedParams}` : `/api/relationship-health/snapshots`
}

/**
 * @summary List versioned relationship-health assessments for the current entity
 */
export const listRelationshipHealthSnapshots = async (params?: ListRelationshipHealthSnapshotsParams, options?: Parameters<typeof customFetch>[1]): Promise<RelationshipHealthSnapshotList> => {

  return customFetch<RelationshipHealthSnapshotList>(getListRelationshipHealthSnapshotsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListRelationshipHealthSnapshotsQueryKey = (params?: ListRelationshipHealthSnapshotsParams,) => {
    return [
    `/api/relationship-health/snapshots`, ...(params ? [params] : [])
    ] as const;
    }


export const getListRelationshipHealthSnapshotsQueryOptions = <TData = Awaited<ReturnType<typeof listRelationshipHealthSnapshots>>, TError = Error>(params?: ListRelationshipHealthSnapshotsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRelationshipHealthSnapshots>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListRelationshipHealthSnapshotsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listRelationshipHealthSnapshots>>> = ({ signal }) => listRelationshipHealthSnapshots(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listRelationshipHealthSnapshots>>, TError, TData> & { queryKey: QueryKey }
}

export type ListRelationshipHealthSnapshotsQueryResult = NonNullable<Awaited<ReturnType<typeof listRelationshipHealthSnapshots>>>
export type ListRelationshipHealthSnapshotsQueryError = Error


/**
 * @summary List versioned relationship-health assessments for the current entity
 */

export function useListRelationshipHealthSnapshots<TData = Awaited<ReturnType<typeof listRelationshipHealthSnapshots>>, TError = Error>(
 params?: ListRelationshipHealthSnapshotsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRelationshipHealthSnapshots>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListRelationshipHealthSnapshotsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListOutreachPlansUrl = (params?: ListOutreachPlansParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/relationship-health/plans?${stringifiedParams}` : `/api/relationship-health/plans`
}

/**
 * @summary List governed outreach plans for the current entity
 */
export const listOutreachPlans = async (params?: ListOutreachPlansParams, options?: Parameters<typeof customFetch>[1]): Promise<OutreachPlanList> => {

  return customFetch<OutreachPlanList>(getListOutreachPlansUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListOutreachPlansQueryKey = (params?: ListOutreachPlansParams,) => {
    return [
    `/api/relationship-health/plans`, ...(params ? [params] : [])
    ] as const;
    }


export const getListOutreachPlansQueryOptions = <TData = Awaited<ReturnType<typeof listOutreachPlans>>, TError = Error>(params?: ListOutreachPlansParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOutreachPlans>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListOutreachPlansQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listOutreachPlans>>> = ({ signal }) => listOutreachPlans(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listOutreachPlans>>, TError, TData> & { queryKey: QueryKey }
}

export type ListOutreachPlansQueryResult = NonNullable<Awaited<ReturnType<typeof listOutreachPlans>>>
export type ListOutreachPlansQueryError = Error


/**
 * @summary List governed outreach plans for the current entity
 */

export function useListOutreachPlans<TData = Awaited<ReturnType<typeof listOutreachPlans>>, TError = Error>(
 params?: ListOutreachPlansParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOutreachPlans>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListOutreachPlansQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateOutreachPlanUrl = () => {




  return `/api/relationship-health/plans`
}

/**
 * @summary Create an inactive outreach plan with its immutable first revision
 */
export const createOutreachPlan = async (createOutreachPlanRequest: CreateOutreachPlanRequest, options?: Parameters<typeof customFetch>[1]): Promise<OutreachPlanDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OutreachPlanDetail>(getCreateOutreachPlanUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createOutreachPlanRequest)
  }
);}





export const getCreateOutreachPlanMutationKey = () => ['createOutreachPlan'] as const;

export const getCreateOutreachPlanMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createOutreachPlan>>, TError,CreateOutreachPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createOutreachPlan>>, TError,CreateOutreachPlanMutationVariables, TContext> => {

const mutationKey = getCreateOutreachPlanMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createOutreachPlan>>, CreateOutreachPlanMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createOutreachPlan(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateOutreachPlanMutationResult = NonNullable<Awaited<ReturnType<typeof createOutreachPlan>>>
    export type CreateOutreachPlanMutationBody = CreateOutreachPlanRequest
    export type CreateOutreachPlanMutationError = Error
    export type CreateOutreachPlanMutationVariables = {data: CreateOutreachPlanRequest}

    /**
 * @summary Create an inactive outreach plan with its immutable first revision
 */
export const useCreateOutreachPlan = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createOutreachPlan>>, TError,CreateOutreachPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createOutreachPlan>>,
        TError,
        CreateOutreachPlanMutationVariables,
        TContext
      > => {
      return useMutation(getCreateOutreachPlanMutationOptions(options));
    }

export const getGetOutreachPlanUrl = (id: string,) => {




  return `/api/relationship-health/plans/${id}`
}

/**
 * @summary Read one outreach plan and its exact immutable revisions
 */
export const getOutreachPlan = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<OutreachPlanDetail> => {

  return customFetch<OutreachPlanDetail>(getGetOutreachPlanUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetOutreachPlanQueryKey = (id: string,) => {
    return [
    `/api/relationship-health/plans/${id}`
    ] as const;
    }


export const getGetOutreachPlanQueryOptions = <TData = Awaited<ReturnType<typeof getOutreachPlan>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getOutreachPlan>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetOutreachPlanQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getOutreachPlan>>> = ({ signal }) => getOutreachPlan(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getOutreachPlan>>, TError, TData> & { queryKey: QueryKey }
}

export type GetOutreachPlanQueryResult = NonNullable<Awaited<ReturnType<typeof getOutreachPlan>>>
export type GetOutreachPlanQueryError = Error


/**
 * @summary Read one outreach plan and its exact immutable revisions
 */

export function useGetOutreachPlan<TData = Awaited<ReturnType<typeof getOutreachPlan>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getOutreachPlan>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetOutreachPlanQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviseOutreachPlanUrl = (id: string,) => {




  return `/api/relationship-health/plans/${id}/revisions`
}

/**
 * @summary Append an immutable plan revision and invalidate prior bounded authority
 */
export const reviseOutreachPlan = async (id: string,
    reviseOutreachPlanRequest: ReviseOutreachPlanRequest, options?: Parameters<typeof customFetch>[1]): Promise<OutreachPlanRevision> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OutreachPlanRevision>(getReviseOutreachPlanUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviseOutreachPlanRequest)
  }
);}





export const getReviseOutreachPlanMutationKey = () => ['reviseOutreachPlan'] as const;

export const getReviseOutreachPlanMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviseOutreachPlan>>, TError,ReviseOutreachPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviseOutreachPlan>>, TError,ReviseOutreachPlanMutationVariables, TContext> => {

const mutationKey = getReviseOutreachPlanMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviseOutreachPlan>>, ReviseOutreachPlanMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reviseOutreachPlan(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviseOutreachPlanMutationResult = NonNullable<Awaited<ReturnType<typeof reviseOutreachPlan>>>
    export type ReviseOutreachPlanMutationBody = ReviseOutreachPlanRequest
    export type ReviseOutreachPlanMutationError = Error
    export type ReviseOutreachPlanMutationVariables = {id: string;data: ReviseOutreachPlanRequest}

    /**
 * @summary Append an immutable plan revision and invalidate prior bounded authority
 */
export const useReviseOutreachPlan = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviseOutreachPlan>>, TError,ReviseOutreachPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviseOutreachPlan>>,
        TError,
        ReviseOutreachPlanMutationVariables,
        TContext
      > => {
      return useMutation(getReviseOutreachPlanMutationOptions(options));
    }

export const getPreviewOutreachPlanUrl = (id: string,) => {




  return `/api/relationship-health/plans/${id}/preview`
}

/**
 * @summary Validate the exact current revision and show authority limitations
 */
export const previewOutreachPlan = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<OutreachPlanPreview> => {

  return customFetch<OutreachPlanPreview>(getPreviewOutreachPlanUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPreviewOutreachPlanQueryKey = (id: string,) => {
    return [
    `/api/relationship-health/plans/${id}/preview`
    ] as const;
    }


export const getPreviewOutreachPlanQueryOptions = <TData = Awaited<ReturnType<typeof previewOutreachPlan>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof previewOutreachPlan>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPreviewOutreachPlanQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof previewOutreachPlan>>> = ({ signal }) => previewOutreachPlan(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof previewOutreachPlan>>, TError, TData> & { queryKey: QueryKey }
}

export type PreviewOutreachPlanQueryResult = NonNullable<Awaited<ReturnType<typeof previewOutreachPlan>>>
export type PreviewOutreachPlanQueryError = Error


/**
 * @summary Validate the exact current revision and show authority limitations
 */

export function usePreviewOutreachPlan<TData = Awaited<ReturnType<typeof previewOutreachPlan>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof previewOutreachPlan>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getPreviewOutreachPlanQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSubmitOutreachPlanReviewUrl = (id: string,) => {




  return `/api/relationship-health/plans/${id}/review`
}

/**
 * @summary Submit an exact immutable plan revision for administrator activation
 */
export const submitOutreachPlanReview = async (id: string,
    submitOutreachPlanReviewRequest: SubmitOutreachPlanReviewRequest, options?: Parameters<typeof customFetch>[1]): Promise<OutreachPlanPreview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OutreachPlanPreview>(getSubmitOutreachPlanReviewUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(submitOutreachPlanReviewRequest)
  }
);}





export const getSubmitOutreachPlanReviewMutationKey = () => ['submitOutreachPlanReview'] as const;

export const getSubmitOutreachPlanReviewMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitOutreachPlanReview>>, TError,SubmitOutreachPlanReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof submitOutreachPlanReview>>, TError,SubmitOutreachPlanReviewMutationVariables, TContext> => {

const mutationKey = getSubmitOutreachPlanReviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof submitOutreachPlanReview>>, SubmitOutreachPlanReviewMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  submitOutreachPlanReview(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SubmitOutreachPlanReviewMutationResult = NonNullable<Awaited<ReturnType<typeof submitOutreachPlanReview>>>
    export type SubmitOutreachPlanReviewMutationBody = SubmitOutreachPlanReviewRequest
    export type SubmitOutreachPlanReviewMutationError = Error
    export type SubmitOutreachPlanReviewMutationVariables = {id: string;data: SubmitOutreachPlanReviewRequest}

    /**
 * @summary Submit an exact immutable plan revision for administrator activation
 */
export const useSubmitOutreachPlanReview = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitOutreachPlanReview>>, TError,SubmitOutreachPlanReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof submitOutreachPlanReview>>,
        TError,
        SubmitOutreachPlanReviewMutationVariables,
        TContext
      > => {
      return useMutation(getSubmitOutreachPlanReviewMutationOptions(options));
    }

export const getActivateOutreachPlanUrl = (id: string,) => {




  return `/api/relationship-health/plans/${id}/activate`
}

/**
 * @summary Administrator activation of one exact staff-reviewed plan revision
 */
export const activateOutreachPlan = async (id: string,
    activateOutreachPlanRequest: ActivateOutreachPlanRequest, options?: Parameters<typeof customFetch>[1]): Promise<OutreachPlanDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OutreachPlanDetail>(getActivateOutreachPlanUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(activateOutreachPlanRequest)
  }
);}





export const getActivateOutreachPlanMutationKey = () => ['activateOutreachPlan'] as const;

export const getActivateOutreachPlanMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof activateOutreachPlan>>, TError,ActivateOutreachPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof activateOutreachPlan>>, TError,ActivateOutreachPlanMutationVariables, TContext> => {

const mutationKey = getActivateOutreachPlanMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof activateOutreachPlan>>, ActivateOutreachPlanMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  activateOutreachPlan(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ActivateOutreachPlanMutationResult = NonNullable<Awaited<ReturnType<typeof activateOutreachPlan>>>
    export type ActivateOutreachPlanMutationBody = ActivateOutreachPlanRequest
    export type ActivateOutreachPlanMutationError = Error
    export type ActivateOutreachPlanMutationVariables = {id: string;data: ActivateOutreachPlanRequest}

    /**
 * @summary Administrator activation of one exact staff-reviewed plan revision
 */
export const useActivateOutreachPlan = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof activateOutreachPlan>>, TError,ActivateOutreachPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof activateOutreachPlan>>,
        TError,
        ActivateOutreachPlanMutationVariables,
        TContext
      > => {
      return useMutation(getActivateOutreachPlanMutationOptions(options));
    }

export const getPauseOutreachPlanUrl = (id: string,) => {




  return `/api/relationship-health/plans/${id}/pause`
}

/**
 * @summary Pause new activity under an active plan
 */
export const pauseOutreachPlan = async (id: string,
    outreachPlanLifecycleRequest: OutreachPlanLifecycleRequest, options?: Parameters<typeof customFetch>[1]): Promise<OutreachPlanDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OutreachPlanDetail>(getPauseOutreachPlanUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(outreachPlanLifecycleRequest)
  }
);}





export const getPauseOutreachPlanMutationKey = () => ['pauseOutreachPlan'] as const;

export const getPauseOutreachPlanMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof pauseOutreachPlan>>, TError,PauseOutreachPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof pauseOutreachPlan>>, TError,PauseOutreachPlanMutationVariables, TContext> => {

const mutationKey = getPauseOutreachPlanMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof pauseOutreachPlan>>, PauseOutreachPlanMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  pauseOutreachPlan(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PauseOutreachPlanMutationResult = NonNullable<Awaited<ReturnType<typeof pauseOutreachPlan>>>
    export type PauseOutreachPlanMutationBody = OutreachPlanLifecycleRequest
    export type PauseOutreachPlanMutationError = Error
    export type PauseOutreachPlanMutationVariables = {id: string;data: OutreachPlanLifecycleRequest}

    /**
 * @summary Pause new activity under an active plan
 */
export const usePauseOutreachPlan = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof pauseOutreachPlan>>, TError,PauseOutreachPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof pauseOutreachPlan>>,
        TError,
        PauseOutreachPlanMutationVariables,
        TContext
      > => {
      return useMutation(getPauseOutreachPlanMutationOptions(options));
    }

export const getResumeOutreachPlanUrl = (id: string,) => {




  return `/api/relationship-health/plans/${id}/resume`
}

/**
 * @summary Administrator resume of a paused plan with current approval and policy checks
 */
export const resumeOutreachPlan = async (id: string,
    outreachPlanLifecycleRequest: OutreachPlanLifecycleRequest, options?: Parameters<typeof customFetch>[1]): Promise<OutreachPlanDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OutreachPlanDetail>(getResumeOutreachPlanUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(outreachPlanLifecycleRequest)
  }
);}





export const getResumeOutreachPlanMutationKey = () => ['resumeOutreachPlan'] as const;

export const getResumeOutreachPlanMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resumeOutreachPlan>>, TError,ResumeOutreachPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof resumeOutreachPlan>>, TError,ResumeOutreachPlanMutationVariables, TContext> => {

const mutationKey = getResumeOutreachPlanMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof resumeOutreachPlan>>, ResumeOutreachPlanMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  resumeOutreachPlan(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResumeOutreachPlanMutationResult = NonNullable<Awaited<ReturnType<typeof resumeOutreachPlan>>>
    export type ResumeOutreachPlanMutationBody = OutreachPlanLifecycleRequest
    export type ResumeOutreachPlanMutationError = Error
    export type ResumeOutreachPlanMutationVariables = {id: string;data: OutreachPlanLifecycleRequest}

    /**
 * @summary Administrator resume of a paused plan with current approval and policy checks
 */
export const useResumeOutreachPlan = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resumeOutreachPlan>>, TError,ResumeOutreachPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof resumeOutreachPlan>>,
        TError,
        ResumeOutreachPlanMutationVariables,
        TContext
      > => {
      return useMutation(getResumeOutreachPlanMutationOptions(options));
    }

export const getRevokeOutreachPlanUrl = (id: string,) => {




  return `/api/relationship-health/plans/${id}/revoke`
}

/**
 * @summary Administrator revocation of all future authority under a plan
 */
export const revokeOutreachPlan = async (id: string,
    outreachPlanLifecycleRequest: OutreachPlanLifecycleRequest, options?: Parameters<typeof customFetch>[1]): Promise<OutreachPlanDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OutreachPlanDetail>(getRevokeOutreachPlanUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(outreachPlanLifecycleRequest)
  }
);}





export const getRevokeOutreachPlanMutationKey = () => ['revokeOutreachPlan'] as const;

export const getRevokeOutreachPlanMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeOutreachPlan>>, TError,RevokeOutreachPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokeOutreachPlan>>, TError,RevokeOutreachPlanMutationVariables, TContext> => {

const mutationKey = getRevokeOutreachPlanMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokeOutreachPlan>>, RevokeOutreachPlanMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  revokeOutreachPlan(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokeOutreachPlanMutationResult = NonNullable<Awaited<ReturnType<typeof revokeOutreachPlan>>>
    export type RevokeOutreachPlanMutationBody = OutreachPlanLifecycleRequest
    export type RevokeOutreachPlanMutationError = Error
    export type RevokeOutreachPlanMutationVariables = {id: string;data: OutreachPlanLifecycleRequest}

    /**
 * @summary Administrator revocation of all future authority under a plan
 */
export const useRevokeOutreachPlan = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeOutreachPlan>>, TError,RevokeOutreachPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof revokeOutreachPlan>>,
        TError,
        RevokeOutreachPlanMutationVariables,
        TContext
      > => {
      return useMutation(getRevokeOutreachPlanMutationOptions(options));
    }

export const getListOutreachPlanHistoryUrl = (id: string,
    params?: ListOutreachPlanHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/relationship-health/plans/${id}/history?${stringifiedParams}` : `/api/relationship-health/plans/${id}/history`
}

/**
 * @summary List canonical review, activation, pause, resume, expiry, and revocation audit events
 */
export const listOutreachPlanHistory = async (id: string,
    params?: ListOutreachPlanHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<OutreachPlanHistoryList> => {

  return customFetch<OutreachPlanHistoryList>(getListOutreachPlanHistoryUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListOutreachPlanHistoryQueryKey = (id: string,
    params?: ListOutreachPlanHistoryParams,) => {
    return [
    `/api/relationship-health/plans/${id}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListOutreachPlanHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listOutreachPlanHistory>>, TError = Error>(id: string,
    params?: ListOutreachPlanHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOutreachPlanHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListOutreachPlanHistoryQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listOutreachPlanHistory>>> = ({ signal }) => listOutreachPlanHistory(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listOutreachPlanHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListOutreachPlanHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listOutreachPlanHistory>>>
export type ListOutreachPlanHistoryQueryError = Error


/**
 * @summary List canonical review, activation, pause, resume, expiry, and revocation audit events
 */

export function useListOutreachPlanHistory<TData = Awaited<ReturnType<typeof listOutreachPlanHistory>>, TError = Error>(
 id: string,
    params?: ListOutreachPlanHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOutreachPlanHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListOutreachPlanHistoryQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListOutreachExecutionsUrl = (id: string,
    params?: ListOutreachExecutionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/relationship-health/plans/${id}/executions?${stringifiedParams}` : `/api/relationship-health/plans/${id}/executions`
}

/**
 * @summary List durable execution and delivery history for an outreach plan
 */
export const listOutreachExecutions = async (id: string,
    params?: ListOutreachExecutionsParams, options?: Parameters<typeof customFetch>[1]): Promise<OutreachExecutionList> => {

  return customFetch<OutreachExecutionList>(getListOutreachExecutionsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListOutreachExecutionsQueryKey = (id: string,
    params?: ListOutreachExecutionsParams,) => {
    return [
    `/api/relationship-health/plans/${id}/executions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListOutreachExecutionsQueryOptions = <TData = Awaited<ReturnType<typeof listOutreachExecutions>>, TError = Error>(id: string,
    params?: ListOutreachExecutionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOutreachExecutions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListOutreachExecutionsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listOutreachExecutions>>> = ({ signal }) => listOutreachExecutions(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listOutreachExecutions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListOutreachExecutionsQueryResult = NonNullable<Awaited<ReturnType<typeof listOutreachExecutions>>>
export type ListOutreachExecutionsQueryError = Error


/**
 * @summary List durable execution and delivery history for an outreach plan
 */

export function useListOutreachExecutions<TData = Awaited<ReturnType<typeof listOutreachExecutions>>, TError = Error>(
 id: string,
    params?: ListOutreachExecutionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOutreachExecutions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListOutreachExecutionsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getScheduleOutreachExecutionUrl = (id: string,) => {




  return `/api/relationship-health/plans/${id}/executions`
}

/**
 * @summary Reserve and schedule one execution under an exact approved plan revision
 */
export const scheduleOutreachExecution = async (id: string,
    scheduleOutreachExecutionRequest: ScheduleOutreachExecutionRequest, options?: Parameters<typeof customFetch>[1]): Promise<OutreachExecution> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OutreachExecution>(getScheduleOutreachExecutionUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(scheduleOutreachExecutionRequest)
  }
);}





export const getScheduleOutreachExecutionMutationKey = () => ['scheduleOutreachExecution'] as const;

export const getScheduleOutreachExecutionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof scheduleOutreachExecution>>, TError,ScheduleOutreachExecutionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof scheduleOutreachExecution>>, TError,ScheduleOutreachExecutionMutationVariables, TContext> => {

const mutationKey = getScheduleOutreachExecutionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof scheduleOutreachExecution>>, ScheduleOutreachExecutionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  scheduleOutreachExecution(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ScheduleOutreachExecutionMutationResult = NonNullable<Awaited<ReturnType<typeof scheduleOutreachExecution>>>
    export type ScheduleOutreachExecutionMutationBody = ScheduleOutreachExecutionRequest
    export type ScheduleOutreachExecutionMutationError = Error
    export type ScheduleOutreachExecutionMutationVariables = {id: string;data: ScheduleOutreachExecutionRequest}

    /**
 * @summary Reserve and schedule one execution under an exact approved plan revision
 */
export const useScheduleOutreachExecution = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof scheduleOutreachExecution>>, TError,ScheduleOutreachExecutionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof scheduleOutreachExecution>>,
        TError,
        ScheduleOutreachExecutionMutationVariables,
        TContext
      > => {
      return useMutation(getScheduleOutreachExecutionMutationOptions(options));
    }

export const getListOutreachScheduleUrl = (id: string,
    params?: ListOutreachScheduleParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/relationship-health/plans/${id}/schedule?${stringifiedParams}` : `/api/relationship-health/plans/${id}/schedule`
}

/**
 * @summary List upcoming, blocked, accepted, unknown, and handoff work for a plan
 */
export const listOutreachSchedule = async (id: string,
    params?: ListOutreachScheduleParams, options?: Parameters<typeof customFetch>[1]): Promise<OutreachExecutionList> => {

  return customFetch<OutreachExecutionList>(getListOutreachScheduleUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListOutreachScheduleQueryKey = (id: string,
    params?: ListOutreachScheduleParams,) => {
    return [
    `/api/relationship-health/plans/${id}/schedule`, ...(params ? [params] : [])
    ] as const;
    }


export const getListOutreachScheduleQueryOptions = <TData = Awaited<ReturnType<typeof listOutreachSchedule>>, TError = Error>(id: string,
    params?: ListOutreachScheduleParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOutreachSchedule>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListOutreachScheduleQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listOutreachSchedule>>> = ({ signal }) => listOutreachSchedule(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listOutreachSchedule>>, TError, TData> & { queryKey: QueryKey }
}

export type ListOutreachScheduleQueryResult = NonNullable<Awaited<ReturnType<typeof listOutreachSchedule>>>
export type ListOutreachScheduleQueryError = Error


/**
 * @summary List upcoming, blocked, accepted, unknown, and handoff work for a plan
 */

export function useListOutreachSchedule<TData = Awaited<ReturnType<typeof listOutreachSchedule>>, TError = Error>(
 id: string,
    params?: ListOutreachScheduleParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOutreachSchedule>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListOutreachScheduleQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListOutreachReplyStatesUrl = (params?: ListOutreachReplyStatesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/relationship-health/replies?${stringifiedParams}` : `/api/relationship-health/replies`
}

/**
 * @summary List plan-scoped reply conversations and staff handoffs
 */
export const listOutreachReplyStates = async (params?: ListOutreachReplyStatesParams, options?: Parameters<typeof customFetch>[1]): Promise<OutreachReplyStateList> => {

  return customFetch<OutreachReplyStateList>(getListOutreachReplyStatesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListOutreachReplyStatesQueryKey = (params?: ListOutreachReplyStatesParams,) => {
    return [
    `/api/relationship-health/replies`, ...(params ? [params] : [])
    ] as const;
    }


export const getListOutreachReplyStatesQueryOptions = <TData = Awaited<ReturnType<typeof listOutreachReplyStates>>, TError = Error>(params?: ListOutreachReplyStatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOutreachReplyStates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListOutreachReplyStatesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listOutreachReplyStates>>> = ({ signal }) => listOutreachReplyStates(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listOutreachReplyStates>>, TError, TData> & { queryKey: QueryKey }
}

export type ListOutreachReplyStatesQueryResult = NonNullable<Awaited<ReturnType<typeof listOutreachReplyStates>>>
export type ListOutreachReplyStatesQueryError = Error


/**
 * @summary List plan-scoped reply conversations and staff handoffs
 */

export function useListOutreachReplyStates<TData = Awaited<ReturnType<typeof listOutreachReplyStates>>, TError = Error>(
 params?: ListOutreachReplyStatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOutreachReplyStates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListOutreachReplyStatesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getClaimOutreachReplyStateUrl = (id: string,) => {




  return `/api/relationship-health/replies/${id}/claim`
}

/**
 * @summary Atomically take staff ownership and stop automated continuation
 */
export const claimOutreachReplyState = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<OutreachReplyState> => {

  return customFetch<OutreachReplyState>(getClaimOutreachReplyStateUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getClaimOutreachReplyStateMutationKey = () => ['claimOutreachReplyState'] as const;

export const getClaimOutreachReplyStateMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof claimOutreachReplyState>>, TError,ClaimOutreachReplyStateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof claimOutreachReplyState>>, TError,ClaimOutreachReplyStateMutationVariables, TContext> => {

const mutationKey = getClaimOutreachReplyStateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof claimOutreachReplyState>>, ClaimOutreachReplyStateMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  claimOutreachReplyState(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClaimOutreachReplyStateMutationResult = NonNullable<Awaited<ReturnType<typeof claimOutreachReplyState>>>

    export type ClaimOutreachReplyStateMutationError = Error
    export type ClaimOutreachReplyStateMutationVariables = {id: string}

    /**
 * @summary Atomically take staff ownership and stop automated continuation
 */
export const useClaimOutreachReplyState = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof claimOutreachReplyState>>, TError,ClaimOutreachReplyStateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof claimOutreachReplyState>>,
        TError,
        ClaimOutreachReplyStateMutationVariables,
        TContext
      > => {
      return useMutation(getClaimOutreachReplyStateMutationOptions(options));
    }

export const getResolveOutreachReplyStateUrl = (id: string,) => {




  return `/api/relationship-health/replies/${id}/resolve`
}

/**
 * @summary Close a staff-owned conversation or deliberately resume bounded automation
 */
export const resolveOutreachReplyState = async (id: string,
    resolveOutreachReplyStateRequest: ResolveOutreachReplyStateRequest, options?: Parameters<typeof customFetch>[1]): Promise<OutreachReplyState> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OutreachReplyState>(getResolveOutreachReplyStateUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(resolveOutreachReplyStateRequest)
  }
);}





export const getResolveOutreachReplyStateMutationKey = () => ['resolveOutreachReplyState'] as const;

export const getResolveOutreachReplyStateMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resolveOutreachReplyState>>, TError,ResolveOutreachReplyStateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof resolveOutreachReplyState>>, TError,ResolveOutreachReplyStateMutationVariables, TContext> => {

const mutationKey = getResolveOutreachReplyStateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof resolveOutreachReplyState>>, ResolveOutreachReplyStateMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  resolveOutreachReplyState(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResolveOutreachReplyStateMutationResult = NonNullable<Awaited<ReturnType<typeof resolveOutreachReplyState>>>
    export type ResolveOutreachReplyStateMutationBody = ResolveOutreachReplyStateRequest
    export type ResolveOutreachReplyStateMutationError = Error
    export type ResolveOutreachReplyStateMutationVariables = {id: string;data: ResolveOutreachReplyStateRequest}

    /**
 * @summary Close a staff-owned conversation or deliberately resume bounded automation
 */
export const useResolveOutreachReplyState = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resolveOutreachReplyState>>, TError,ResolveOutreachReplyStateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof resolveOutreachReplyState>>,
        TError,
        ResolveOutreachReplyStateMutationVariables,
        TContext
      > => {
      return useMutation(getResolveOutreachReplyStateMutationOptions(options));
    }

export const getListRelationshipHealthFeedbackUrl = (params?: ListRelationshipHealthFeedbackParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/relationship-health/feedback?${stringifiedParams}` : `/api/relationship-health/feedback`
}

/**
 * @summary List entity-scoped staff feedback linked to canonical actions and plan versions
 */
export const listRelationshipHealthFeedback = async (params?: ListRelationshipHealthFeedbackParams, options?: Parameters<typeof customFetch>[1]): Promise<RelationshipHealthFeedbackList> => {

  return customFetch<RelationshipHealthFeedbackList>(getListRelationshipHealthFeedbackUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListRelationshipHealthFeedbackQueryKey = (params?: ListRelationshipHealthFeedbackParams,) => {
    return [
    `/api/relationship-health/feedback`, ...(params ? [params] : [])
    ] as const;
    }


export const getListRelationshipHealthFeedbackQueryOptions = <TData = Awaited<ReturnType<typeof listRelationshipHealthFeedback>>, TError = Error>(params?: ListRelationshipHealthFeedbackParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRelationshipHealthFeedback>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListRelationshipHealthFeedbackQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listRelationshipHealthFeedback>>> = ({ signal }) => listRelationshipHealthFeedback(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listRelationshipHealthFeedback>>, TError, TData> & { queryKey: QueryKey }
}

export type ListRelationshipHealthFeedbackQueryResult = NonNullable<Awaited<ReturnType<typeof listRelationshipHealthFeedback>>>
export type ListRelationshipHealthFeedbackQueryError = Error


/**
 * @summary List entity-scoped staff feedback linked to canonical actions and plan versions
 */

export function useListRelationshipHealthFeedback<TData = Awaited<ReturnType<typeof listRelationshipHealthFeedback>>, TError = Error>(
 params?: ListRelationshipHealthFeedbackParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRelationshipHealthFeedback>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListRelationshipHealthFeedbackQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRecordRelationshipHealthFeedbackUrl = () => {




  return `/api/relationship-health/feedback`
}

/**
 * @summary Record idempotent staff feedback without granting outreach authority
 */
export const recordRelationshipHealthFeedback = async (recordRelationshipHealthFeedbackRequest: RecordRelationshipHealthFeedbackRequest, options?: Parameters<typeof customFetch>[1]): Promise<RelationshipHealthFeedback> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<RelationshipHealthFeedback>(getRecordRelationshipHealthFeedbackUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(recordRelationshipHealthFeedbackRequest)
  }
);}





export const getRecordRelationshipHealthFeedbackMutationKey = () => ['recordRelationshipHealthFeedback'] as const;

export const getRecordRelationshipHealthFeedbackMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recordRelationshipHealthFeedback>>, TError,RecordRelationshipHealthFeedbackMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof recordRelationshipHealthFeedback>>, TError,RecordRelationshipHealthFeedbackMutationVariables, TContext> => {

const mutationKey = getRecordRelationshipHealthFeedbackMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof recordRelationshipHealthFeedback>>, RecordRelationshipHealthFeedbackMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  recordRelationshipHealthFeedback(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RecordRelationshipHealthFeedbackMutationResult = NonNullable<Awaited<ReturnType<typeof recordRelationshipHealthFeedback>>>
    export type RecordRelationshipHealthFeedbackMutationBody = RecordRelationshipHealthFeedbackRequest
    export type RecordRelationshipHealthFeedbackMutationError = Error
    export type RecordRelationshipHealthFeedbackMutationVariables = {data: RecordRelationshipHealthFeedbackRequest}

    /**
 * @summary Record idempotent staff feedback without granting outreach authority
 */
export const useRecordRelationshipHealthFeedback = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recordRelationshipHealthFeedback>>, TError,RecordRelationshipHealthFeedbackMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof recordRelationshipHealthFeedback>>,
        TError,
        RecordRelationshipHealthFeedbackMutationVariables,
        TContext
      > => {
      return useMutation(getRecordRelationshipHealthFeedbackMutationOptions(options));
    }

export const getListRelationshipHealthLearningVersionsUrl = (params?: ListRelationshipHealthLearningVersionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/relationship-health/learning?${stringifiedParams}` : `/api/relationship-health/learning`
}

/**
 * @summary List inspectable immutable recommendation-learning versions
 */
export const listRelationshipHealthLearningVersions = async (params?: ListRelationshipHealthLearningVersionsParams, options?: Parameters<typeof customFetch>[1]): Promise<RelationshipHealthLearningVersionList> => {

  return customFetch<RelationshipHealthLearningVersionList>(getListRelationshipHealthLearningVersionsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListRelationshipHealthLearningVersionsQueryKey = (params?: ListRelationshipHealthLearningVersionsParams,) => {
    return [
    `/api/relationship-health/learning`, ...(params ? [params] : [])
    ] as const;
    }


export const getListRelationshipHealthLearningVersionsQueryOptions = <TData = Awaited<ReturnType<typeof listRelationshipHealthLearningVersions>>, TError = Error>(params?: ListRelationshipHealthLearningVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRelationshipHealthLearningVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListRelationshipHealthLearningVersionsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listRelationshipHealthLearningVersions>>> = ({ signal }) => listRelationshipHealthLearningVersions(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listRelationshipHealthLearningVersions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListRelationshipHealthLearningVersionsQueryResult = NonNullable<Awaited<ReturnType<typeof listRelationshipHealthLearningVersions>>>
export type ListRelationshipHealthLearningVersionsQueryError = Error


/**
 * @summary List inspectable immutable recommendation-learning versions
 */

export function useListRelationshipHealthLearningVersions<TData = Awaited<ReturnType<typeof listRelationshipHealthLearningVersions>>, TError = Error>(
 params?: ListRelationshipHealthLearningVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listRelationshipHealthLearningVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListRelationshipHealthLearningVersionsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRefreshRelationshipHealthLearningUrl = () => {




  return `/api/relationship-health/learning/refresh`
}

/**
 * @summary Activate a bounded recommendation-learning version without enabling outreach
 */
export const refreshRelationshipHealthLearning = async ( options?: Parameters<typeof customFetch>[1]): Promise<RelationshipHealthLearningVersion> => {

  return customFetch<RelationshipHealthLearningVersion>(getRefreshRelationshipHealthLearningUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRefreshRelationshipHealthLearningMutationKey = () => ['refreshRelationshipHealthLearning'] as const;

export const getRefreshRelationshipHealthLearningMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof refreshRelationshipHealthLearning>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof refreshRelationshipHealthLearning>>, TError,void, TContext> => {

const mutationKey = getRefreshRelationshipHealthLearningMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof refreshRelationshipHealthLearning>>, void> = () => {


          return  refreshRelationshipHealthLearning(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RefreshRelationshipHealthLearningMutationResult = NonNullable<Awaited<ReturnType<typeof refreshRelationshipHealthLearning>>>

    export type RefreshRelationshipHealthLearningMutationError = Error


    /**
 * @summary Activate a bounded recommendation-learning version without enabling outreach
 */
export const useRefreshRelationshipHealthLearning = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof refreshRelationshipHealthLearning>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof refreshRelationshipHealthLearning>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getRefreshRelationshipHealthLearningMutationOptions(options));
    }

export const getRollbackRelationshipHealthLearningUrl = (id: string,) => {




  return `/api/relationship-health/learning/${id}/rollback`
}

/**
 * @summary Create a new active version from a prior learned configuration
 */
export const rollbackRelationshipHealthLearning = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<RelationshipHealthLearningVersion> => {

  return customFetch<RelationshipHealthLearningVersion>(getRollbackRelationshipHealthLearningUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRollbackRelationshipHealthLearningMutationKey = () => ['rollbackRelationshipHealthLearning'] as const;

export const getRollbackRelationshipHealthLearningMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rollbackRelationshipHealthLearning>>, TError,RollbackRelationshipHealthLearningMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof rollbackRelationshipHealthLearning>>, TError,RollbackRelationshipHealthLearningMutationVariables, TContext> => {

const mutationKey = getRollbackRelationshipHealthLearningMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof rollbackRelationshipHealthLearning>>, RollbackRelationshipHealthLearningMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  rollbackRelationshipHealthLearning(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RollbackRelationshipHealthLearningMutationResult = NonNullable<Awaited<ReturnType<typeof rollbackRelationshipHealthLearning>>>

    export type RollbackRelationshipHealthLearningMutationError = Error
    export type RollbackRelationshipHealthLearningMutationVariables = {id: string}

    /**
 * @summary Create a new active version from a prior learned configuration
 */
export const useRollbackRelationshipHealthLearning = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rollbackRelationshipHealthLearning>>, TError,RollbackRelationshipHealthLearningMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof rollbackRelationshipHealthLearning>>,
        TError,
        RollbackRelationshipHealthLearningMutationVariables,
        TContext
      > => {
      return useMutation(getRollbackRelationshipHealthLearningMutationOptions(options));
    }

export const getGetRelationshipHealthAutomationReportUrl = (params: GetRelationshipHealthAutomationReportParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/relationship-health/report?${stringifiedParams}` : `/api/relationship-health/report`
}

/**
 * @summary Report delivery, engagement, relationship evidence and operational failures separately
 */
export const getRelationshipHealthAutomationReport = async (params: GetRelationshipHealthAutomationReportParams, options?: Parameters<typeof customFetch>[1]): Promise<RelationshipHealthAutomationReport> => {

  return customFetch<RelationshipHealthAutomationReport>(getGetRelationshipHealthAutomationReportUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetRelationshipHealthAutomationReportQueryKey = (params?: GetRelationshipHealthAutomationReportParams,) => {
    return [
    `/api/relationship-health/report`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetRelationshipHealthAutomationReportQueryOptions = <TData = Awaited<ReturnType<typeof getRelationshipHealthAutomationReport>>, TError = Error>(params: GetRelationshipHealthAutomationReportParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRelationshipHealthAutomationReport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetRelationshipHealthAutomationReportQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getRelationshipHealthAutomationReport>>> = ({ signal }) => getRelationshipHealthAutomationReport(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getRelationshipHealthAutomationReport>>, TError, TData> & { queryKey: QueryKey }
}

export type GetRelationshipHealthAutomationReportQueryResult = NonNullable<Awaited<ReturnType<typeof getRelationshipHealthAutomationReport>>>
export type GetRelationshipHealthAutomationReportQueryError = Error


/**
 * @summary Report delivery, engagement, relationship evidence and operational failures separately
 */

export function useGetRelationshipHealthAutomationReport<TData = Awaited<ReturnType<typeof getRelationshipHealthAutomationReport>>, TError = Error>(
 params: GetRelationshipHealthAutomationReportParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getRelationshipHealthAutomationReport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetRelationshipHealthAutomationReportQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListNextActionFeedUrl = (params?: ListNextActionFeedParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/next-actions?${stringifiedParams}` : `/api/next-actions`
}

/**
 * @summary List the open, prioritized next-action approvals feed (excludes snoozed and dismissed)
 */
export const listNextActionFeed = async (params?: ListNextActionFeedParams, options?: Parameters<typeof customFetch>[1]): Promise<ListNextActionFeed200> => {

  return customFetch<ListNextActionFeed200>(getListNextActionFeedUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListNextActionFeedQueryKey = (params?: ListNextActionFeedParams,) => {
    return [
    `/api/next-actions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListNextActionFeedQueryOptions = <TData = Awaited<ReturnType<typeof listNextActionFeed>>, TError = unknown>(params?: ListNextActionFeedParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listNextActionFeed>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListNextActionFeedQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listNextActionFeed>>> = ({ signal }) => listNextActionFeed(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listNextActionFeed>>, TError, TData> & { queryKey: QueryKey }
}

export type ListNextActionFeedQueryResult = NonNullable<Awaited<ReturnType<typeof listNextActionFeed>>>
export type ListNextActionFeedQueryError = unknown


/**
 * @summary List the open, prioritized next-action approvals feed (excludes snoozed and dismissed)
 */

export function useListNextActionFeed<TData = Awaited<ReturnType<typeof listNextActionFeed>>, TError = unknown>(
 params?: ListNextActionFeedParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listNextActionFeed>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListNextActionFeedQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRefreshNextActionsUrl = () => {




  return `/api/next-actions/refresh`
}

/**
 * @summary Run the signal-driven next-action engine now (email signals first; authority-gated)
 */
export const refreshNextActions = async (refreshNextActionsBody?: RefreshNextActionsBody, options?: Parameters<typeof customFetch>[1]): Promise<NextActionsRefreshResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<NextActionsRefreshResult>(getRefreshNextActionsUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(refreshNextActionsBody)
  }
);}





export const getRefreshNextActionsMutationKey = () => ['refreshNextActions'] as const;

export const getRefreshNextActionsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof refreshNextActions>>, TError,RefreshNextActionsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof refreshNextActions>>, TError,RefreshNextActionsMutationVariables, TContext> => {

const mutationKey = getRefreshNextActionsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof refreshNextActions>>, RefreshNextActionsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  refreshNextActions(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RefreshNextActionsMutationResult = NonNullable<Awaited<ReturnType<typeof refreshNextActions>>>
    export type RefreshNextActionsMutationBody = RefreshNextActionsBody | undefined
    export type RefreshNextActionsMutationError = Error
    export type RefreshNextActionsMutationVariables = {data?: RefreshNextActionsBody}

    /**
 * @summary Run the signal-driven next-action engine now (email signals first; authority-gated)
 */
export const useRefreshNextActions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof refreshNextActions>>, TError,RefreshNextActionsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof refreshNextActions>>,
        TError,
        RefreshNextActionsMutationVariables,
        TContext
      > => {
      return useMutation(getRefreshNextActionsMutationOptions(options));
    }

export const getDismissNextActionUrl = (id: string,) => {




  return `/api/next-actions/${id}/dismiss`
}

/**
 * @summary Dismiss a proposed next action so it no longer appears in the feed
 */
export const dismissNextAction = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<NextActionFeedItem> => {

  return customFetch<NextActionFeedItem>(getDismissNextActionUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getDismissNextActionMutationKey = () => ['dismissNextAction'] as const;

export const getDismissNextActionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof dismissNextAction>>, TError,DismissNextActionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof dismissNextAction>>, TError,DismissNextActionMutationVariables, TContext> => {

const mutationKey = getDismissNextActionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof dismissNextAction>>, DismissNextActionMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  dismissNextAction(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DismissNextActionMutationResult = NonNullable<Awaited<ReturnType<typeof dismissNextAction>>>

    export type DismissNextActionMutationError = Error
    export type DismissNextActionMutationVariables = {id: string}

    /**
 * @summary Dismiss a proposed next action so it no longer appears in the feed
 */
export const useDismissNextAction = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof dismissNextAction>>, TError,DismissNextActionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof dismissNextAction>>,
        TError,
        DismissNextActionMutationVariables,
        TContext
      > => {
      return useMutation(getDismissNextActionMutationOptions(options));
    }

export const getSnoozeNextActionUrl = (id: string,) => {




  return `/api/next-actions/${id}/snooze`
}

/**
 * @summary Snooze a proposed next action; it reappears in the feed after the snooze window
 */
export const snoozeNextAction = async (id: string,
    snoozeNextActionBody?: SnoozeNextActionBody, options?: Parameters<typeof customFetch>[1]): Promise<NextActionFeedItem> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<NextActionFeedItem>(getSnoozeNextActionUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(snoozeNextActionBody)
  }
);}





export const getSnoozeNextActionMutationKey = () => ['snoozeNextAction'] as const;

export const getSnoozeNextActionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof snoozeNextAction>>, TError,SnoozeNextActionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof snoozeNextAction>>, TError,SnoozeNextActionMutationVariables, TContext> => {

const mutationKey = getSnoozeNextActionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof snoozeNextAction>>, SnoozeNextActionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  snoozeNextAction(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SnoozeNextActionMutationResult = NonNullable<Awaited<ReturnType<typeof snoozeNextAction>>>
    export type SnoozeNextActionMutationBody = SnoozeNextActionBody | undefined
    export type SnoozeNextActionMutationError = Error
    export type SnoozeNextActionMutationVariables = {id: string;data?: SnoozeNextActionBody}

    /**
 * @summary Snooze a proposed next action; it reappears in the feed after the snooze window
 */
export const useSnoozeNextAction = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof snoozeNextAction>>, TError,SnoozeNextActionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof snoozeNextAction>>,
        TError,
        SnoozeNextActionMutationVariables,
        TContext
      > => {
      return useMutation(getSnoozeNextActionMutationOptions(options));
    }

export const getApproveNextActionUrl = (id: string,) => {




  return `/api/next-actions/${id}/approve`
}

/**
 * Re-checks authority before executing. Email actions with a draft send the (possibly edited) draft through the connected Microsoft 365 mailbox and record the outbound message; non-email actions are marked completed. human_only authority returns the draft for manual use and never sends.
 * @summary Approve a proposed next action; email actions send the reviewed draft, other actions complete and deep-link
 */
export const approveNextAction = async (id: string,
    approveNextActionBody?: ApproveNextActionBody, options?: Parameters<typeof customFetch>[1]): Promise<NextActionApprovalResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<NextActionApprovalResult>(getApproveNextActionUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(approveNextActionBody)
  }
);}





export const getApproveNextActionMutationKey = () => ['approveNextAction'] as const;

export const getApproveNextActionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveNextAction>>, TError,ApproveNextActionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof approveNextAction>>, TError,ApproveNextActionMutationVariables, TContext> => {

const mutationKey = getApproveNextActionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof approveNextAction>>, ApproveNextActionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  approveNextAction(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApproveNextActionMutationResult = NonNullable<Awaited<ReturnType<typeof approveNextAction>>>
    export type ApproveNextActionMutationBody = ApproveNextActionBody | undefined
    export type ApproveNextActionMutationError = Error
    export type ApproveNextActionMutationVariables = {id: string;data?: ApproveNextActionBody}

    /**
 * @summary Approve a proposed next action; email actions send the reviewed draft, other actions complete and deep-link
 */
export const useApproveNextAction = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveNextAction>>, TError,ApproveNextActionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof approveNextAction>>,
        TError,
        ApproveNextActionMutationVariables,
        TContext
      > => {
      return useMutation(getApproveNextActionMutationOptions(options));
    }

export const getGetAcquisitionFunnelUrl = (params?: GetAcquisitionFunnelParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/acquisition-analytics/funnel?${stringifiedParams}` : `/api/acquisition-analytics/funnel`
}

/**
 * @summary Get cross-channel acquisition funnel stages and channel/campaign breakdowns
 */
export const getAcquisitionFunnel = async (params?: GetAcquisitionFunnelParams, options?: Parameters<typeof customFetch>[1]): Promise<AcquisitionFunnel> => {

  return customFetch<AcquisitionFunnel>(getGetAcquisitionFunnelUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAcquisitionFunnelQueryKey = (params?: GetAcquisitionFunnelParams,) => {
    return [
    `/api/acquisition-analytics/funnel`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetAcquisitionFunnelQueryOptions = <TData = Awaited<ReturnType<typeof getAcquisitionFunnel>>, TError = unknown>(params?: GetAcquisitionFunnelParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAcquisitionFunnel>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAcquisitionFunnelQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAcquisitionFunnel>>> = ({ signal }) => getAcquisitionFunnel(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAcquisitionFunnel>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAcquisitionFunnelQueryResult = NonNullable<Awaited<ReturnType<typeof getAcquisitionFunnel>>>
export type GetAcquisitionFunnelQueryError = unknown


/**
 * @summary Get cross-channel acquisition funnel stages and channel/campaign breakdowns
 */

export function useGetAcquisitionFunnel<TData = Awaited<ReturnType<typeof getAcquisitionFunnel>>, TError = unknown>(
 params?: GetAcquisitionFunnelParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAcquisitionFunnel>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAcquisitionFunnelQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRefreshOpportunityAttributionsUrl = () => {




  return `/api/acquisition-analytics/attributions/refresh`
}

/**
 * @summary Rebuild won-opportunity attribution from unified interactions
 */
export const refreshOpportunityAttributions = async ( options?: Parameters<typeof customFetch>[1]): Promise<RefreshOpportunityAttributions200> => {

  return customFetch<RefreshOpportunityAttributions200>(getRefreshOpportunityAttributionsUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRefreshOpportunityAttributionsMutationKey = () => ['refreshOpportunityAttributions'] as const;

export const getRefreshOpportunityAttributionsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof refreshOpportunityAttributions>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof refreshOpportunityAttributions>>, TError,void, TContext> => {

const mutationKey = getRefreshOpportunityAttributionsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof refreshOpportunityAttributions>>, void> = () => {


          return  refreshOpportunityAttributions(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RefreshOpportunityAttributionsMutationResult = NonNullable<Awaited<ReturnType<typeof refreshOpportunityAttributions>>>

    export type RefreshOpportunityAttributionsMutationError = unknown


    /**
 * @summary Rebuild won-opportunity attribution from unified interactions
 */
export const useRefreshOpportunityAttributions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof refreshOpportunityAttributions>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof refreshOpportunityAttributions>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getRefreshOpportunityAttributionsMutationOptions(options));
    }

export const getGetOpportunityAttributionUrl = (opportunityId: string,) => {




  return `/api/acquisition-analytics/opportunities/${opportunityId}/attribution`
}

/**
 * @summary Get first-touch and contributing-touch attribution for a won opportunity
 */
export const getOpportunityAttribution = async (opportunityId: string, options?: Parameters<typeof customFetch>[1]): Promise<OpportunityAttribution> => {

  return customFetch<OpportunityAttribution>(getGetOpportunityAttributionUrl(opportunityId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetOpportunityAttributionQueryKey = (opportunityId: string,) => {
    return [
    `/api/acquisition-analytics/opportunities/${opportunityId}/attribution`
    ] as const;
    }


export const getGetOpportunityAttributionQueryOptions = <TData = Awaited<ReturnType<typeof getOpportunityAttribution>>, TError = Error>(opportunityId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getOpportunityAttribution>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetOpportunityAttributionQueryKey(opportunityId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getOpportunityAttribution>>> = ({ signal }) => getOpportunityAttribution(opportunityId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: opportunityId !== null && opportunityId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getOpportunityAttribution>>, TError, TData> & { queryKey: QueryKey }
}

export type GetOpportunityAttributionQueryResult = NonNullable<Awaited<ReturnType<typeof getOpportunityAttribution>>>
export type GetOpportunityAttributionQueryError = Error


/**
 * @summary Get first-touch and contributing-touch attribution for a won opportunity
 */

export function useGetOpportunityAttribution<TData = Awaited<ReturnType<typeof getOpportunityAttribution>>, TError = Error>(
 opportunityId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getOpportunityAttribution>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetOpportunityAttributionQueryOptions(opportunityId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetAcquisitionChannelPerformanceUrl = (params?: GetAcquisitionChannelPerformanceParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/acquisition-analytics/channels?${stringifiedParams}` : `/api/acquisition-analytics/channels`
}

/**
 * @summary Get combined social, email, chat, voice and signal performance
 */
export const getAcquisitionChannelPerformance = async (params?: GetAcquisitionChannelPerformanceParams, options?: Parameters<typeof customFetch>[1]): Promise<ChannelPerformanceList> => {

  return customFetch<ChannelPerformanceList>(getGetAcquisitionChannelPerformanceUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAcquisitionChannelPerformanceQueryKey = (params?: GetAcquisitionChannelPerformanceParams,) => {
    return [
    `/api/acquisition-analytics/channels`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetAcquisitionChannelPerformanceQueryOptions = <TData = Awaited<ReturnType<typeof getAcquisitionChannelPerformance>>, TError = unknown>(params?: GetAcquisitionChannelPerformanceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAcquisitionChannelPerformance>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAcquisitionChannelPerformanceQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAcquisitionChannelPerformance>>> = ({ signal }) => getAcquisitionChannelPerformance(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAcquisitionChannelPerformance>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAcquisitionChannelPerformanceQueryResult = NonNullable<Awaited<ReturnType<typeof getAcquisitionChannelPerformance>>>
export type GetAcquisitionChannelPerformanceQueryError = unknown


/**
 * @summary Get combined social, email, chat, voice and signal performance
 */

export function useGetAcquisitionChannelPerformance<TData = Awaited<ReturnType<typeof getAcquisitionChannelPerformance>>, TError = unknown>(
 params?: GetAcquisitionChannelPerformanceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAcquisitionChannelPerformance>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAcquisitionChannelPerformanceQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListAcquisitionReportsUrl = () => {




  return `/api/acquisition-analytics/reports`
}

/**
 * @summary List saved natural-language acquisition reports
 */
export const listAcquisitionReports = async ( options?: Parameters<typeof customFetch>[1]): Promise<ListAcquisitionReports200> => {

  return customFetch<ListAcquisitionReports200>(getListAcquisitionReportsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAcquisitionReportsQueryKey = () => {
    return [
    `/api/acquisition-analytics/reports`
    ] as const;
    }


export const getListAcquisitionReportsQueryOptions = <TData = Awaited<ReturnType<typeof listAcquisitionReports>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAcquisitionReports>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAcquisitionReportsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAcquisitionReports>>> = ({ signal }) => listAcquisitionReports({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAcquisitionReports>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAcquisitionReportsQueryResult = NonNullable<Awaited<ReturnType<typeof listAcquisitionReports>>>
export type ListAcquisitionReportsQueryError = unknown


/**
 * @summary List saved natural-language acquisition reports
 */

export function useListAcquisitionReports<TData = Awaited<ReturnType<typeof listAcquisitionReports>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAcquisitionReports>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAcquisitionReportsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGenerateAcquisitionReportUrl = () => {




  return `/api/acquisition-analytics/reports`
}

/**
 * @summary Generate and save a safe parameterized chart from a natural-language request
 */
export const generateAcquisitionReport = async (acquisitionReportRequest: AcquisitionReportRequest, options?: Parameters<typeof customFetch>[1]): Promise<AcquisitionReport> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AcquisitionReport>(getGenerateAcquisitionReportUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(acquisitionReportRequest)
  }
);}





export const getGenerateAcquisitionReportMutationKey = () => ['generateAcquisitionReport'] as const;

export const getGenerateAcquisitionReportMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateAcquisitionReport>>, TError,GenerateAcquisitionReportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof generateAcquisitionReport>>, TError,GenerateAcquisitionReportMutationVariables, TContext> => {

const mutationKey = getGenerateAcquisitionReportMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof generateAcquisitionReport>>, GenerateAcquisitionReportMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  generateAcquisitionReport(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenerateAcquisitionReportMutationResult = NonNullable<Awaited<ReturnType<typeof generateAcquisitionReport>>>
    export type GenerateAcquisitionReportMutationBody = AcquisitionReportRequest
    export type GenerateAcquisitionReportMutationError = unknown
    export type GenerateAcquisitionReportMutationVariables = {data: AcquisitionReportRequest}

    /**
 * @summary Generate and save a safe parameterized chart from a natural-language request
 */
export const useGenerateAcquisitionReport = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateAcquisitionReport>>, TError,GenerateAcquisitionReportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof generateAcquisitionReport>>,
        TError,
        GenerateAcquisitionReportMutationVariables,
        TContext
      > => {
      return useMutation(getGenerateAcquisitionReportMutationOptions(options));
    }

export const getGetAcquisitionReportUrl = (id: string,) => {




  return `/api/acquisition-analytics/reports/${id}`
}

/**
 * @summary Reopen a saved acquisition report
 */
export const getAcquisitionReport = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<AcquisitionReport> => {

  return customFetch<AcquisitionReport>(getGetAcquisitionReportUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAcquisitionReportQueryKey = (id: string,) => {
    return [
    `/api/acquisition-analytics/reports/${id}`
    ] as const;
    }


export const getGetAcquisitionReportQueryOptions = <TData = Awaited<ReturnType<typeof getAcquisitionReport>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAcquisitionReport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAcquisitionReportQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAcquisitionReport>>> = ({ signal }) => getAcquisitionReport(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAcquisitionReport>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAcquisitionReportQueryResult = NonNullable<Awaited<ReturnType<typeof getAcquisitionReport>>>
export type GetAcquisitionReportQueryError = Error


/**
 * @summary Reopen a saved acquisition report
 */

export function useGetAcquisitionReport<TData = Awaited<ReturnType<typeof getAcquisitionReport>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAcquisitionReport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAcquisitionReportQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetAccountProgressivenessUrl = (accountId: string,) => {




  return `/api/account-progressiveness/${accountId}`
}

/**
 * @summary Get the progressiveness assessment for a account
 */
export const getAccountProgressiveness = async (accountId: string, options?: Parameters<typeof customFetch>[1]): Promise<AccountProgressiveness> => {

  return customFetch<AccountProgressiveness>(getGetAccountProgressivenessUrl(accountId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAccountProgressivenessQueryKey = (accountId: string,) => {
    return [
    `/api/account-progressiveness/${accountId}`
    ] as const;
    }


export const getGetAccountProgressivenessQueryOptions = <TData = Awaited<ReturnType<typeof getAccountProgressiveness>>, TError = Error>(accountId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccountProgressiveness>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAccountProgressivenessQueryKey(accountId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAccountProgressiveness>>> = ({ signal }) => getAccountProgressiveness(accountId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: accountId !== null && accountId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAccountProgressiveness>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAccountProgressivenessQueryResult = NonNullable<Awaited<ReturnType<typeof getAccountProgressiveness>>>
export type GetAccountProgressivenessQueryError = Error


/**
 * @summary Get the progressiveness assessment for a account
 */

export function useGetAccountProgressiveness<TData = Awaited<ReturnType<typeof getAccountProgressiveness>>, TError = Error>(
 accountId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAccountProgressiveness>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAccountProgressivenessQueryOptions(accountId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpsertAccountProgressivenessUrl = (accountId: string,) => {




  return `/api/account-progressiveness/${accountId}`
}

/**
 * @summary Create or update the progressiveness assessment for a account
 */
export const upsertAccountProgressiveness = async (accountId: string,
    accountProgressivenessBase: AccountProgressivenessBase, options?: Parameters<typeof customFetch>[1]): Promise<AccountProgressiveness> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccountProgressiveness>(getUpsertAccountProgressivenessUrl(accountId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accountProgressivenessBase)
  }
);}





export const getUpsertAccountProgressivenessMutationKey = () => ['upsertAccountProgressiveness'] as const;

export const getUpsertAccountProgressivenessMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertAccountProgressiveness>>, TError,UpsertAccountProgressivenessMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertAccountProgressiveness>>, TError,UpsertAccountProgressivenessMutationVariables, TContext> => {

const mutationKey = getUpsertAccountProgressivenessMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertAccountProgressiveness>>, UpsertAccountProgressivenessMutationVariables> = (props) => {
          const {accountId,data} = props ?? {};

          return  upsertAccountProgressiveness(accountId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertAccountProgressivenessMutationResult = NonNullable<Awaited<ReturnType<typeof upsertAccountProgressiveness>>>
    export type UpsertAccountProgressivenessMutationBody = AccountProgressivenessBase
    export type UpsertAccountProgressivenessMutationError = Error
    export type UpsertAccountProgressivenessMutationVariables = {accountId: string;data: AccountProgressivenessBase}

    /**
 * @summary Create or update the progressiveness assessment for a account
 */
export const useUpsertAccountProgressiveness = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertAccountProgressiveness>>, TError,UpsertAccountProgressivenessMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof upsertAccountProgressiveness>>,
        TError,
        UpsertAccountProgressivenessMutationVariables,
        TContext
      > => {
      return useMutation(getUpsertAccountProgressivenessMutationOptions(options));
    }

export const getListAccountProductFitUrl = (params?: ListAccountProductFitParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/account-product-fit?${stringifiedParams}` : `/api/account-product-fit`
}

/**
 * @summary List product-fit scores, optionally filtered by account or product
 */
export const listAccountProductFit = async (params?: ListAccountProductFitParams, options?: Parameters<typeof customFetch>[1]): Promise<AccountProductFitList> => {

  return customFetch<AccountProductFitList>(getListAccountProductFitUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAccountProductFitQueryKey = (params?: ListAccountProductFitParams,) => {
    return [
    `/api/account-product-fit`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAccountProductFitQueryOptions = <TData = Awaited<ReturnType<typeof listAccountProductFit>>, TError = unknown>(params?: ListAccountProductFitParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAccountProductFit>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAccountProductFitQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAccountProductFit>>> = ({ signal }) => listAccountProductFit(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAccountProductFit>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAccountProductFitQueryResult = NonNullable<Awaited<ReturnType<typeof listAccountProductFit>>>
export type ListAccountProductFitQueryError = unknown


/**
 * @summary List product-fit scores, optionally filtered by account or product
 */

export function useListAccountProductFit<TData = Awaited<ReturnType<typeof listAccountProductFit>>, TError = unknown>(
 params?: ListAccountProductFitParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAccountProductFit>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAccountProductFitQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateAccountProductFitUrl = () => {




  return `/api/account-product-fit`
}

/**
 * @summary Create a product-fit score for a account/product pair
 */
export const createAccountProductFit = async (accountProductFitCreate: AccountProductFitCreate, options?: Parameters<typeof customFetch>[1]): Promise<AccountProductFit> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccountProductFit>(getCreateAccountProductFitUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accountProductFitCreate)
  }
);}





export const getCreateAccountProductFitMutationKey = () => ['createAccountProductFit'] as const;

export const getCreateAccountProductFitMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAccountProductFit>>, TError,CreateAccountProductFitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createAccountProductFit>>, TError,CreateAccountProductFitMutationVariables, TContext> => {

const mutationKey = getCreateAccountProductFitMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAccountProductFit>>, CreateAccountProductFitMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createAccountProductFit(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateAccountProductFitMutationResult = NonNullable<Awaited<ReturnType<typeof createAccountProductFit>>>
    export type CreateAccountProductFitMutationBody = AccountProductFitCreate
    export type CreateAccountProductFitMutationError = Error
    export type CreateAccountProductFitMutationVariables = {data: AccountProductFitCreate}

    /**
 * @summary Create a product-fit score for a account/product pair
 */
export const useCreateAccountProductFit = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAccountProductFit>>, TError,CreateAccountProductFitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createAccountProductFit>>,
        TError,
        CreateAccountProductFitMutationVariables,
        TContext
      > => {
      return useMutation(getCreateAccountProductFitMutationOptions(options));
    }

export const getUpdateAccountProductFitUrl = (id: string,) => {




  return `/api/account-product-fit/${id}`
}

/**
 * @summary Update a product-fit score
 */
export const updateAccountProductFit = async (id: string,
    accountProductFitBase: AccountProductFitBase, options?: Parameters<typeof customFetch>[1]): Promise<AccountProductFit> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccountProductFit>(getUpdateAccountProductFitUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accountProductFitBase)
  }
);}





export const getUpdateAccountProductFitMutationKey = () => ['updateAccountProductFit'] as const;

export const getUpdateAccountProductFitMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAccountProductFit>>, TError,UpdateAccountProductFitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateAccountProductFit>>, TError,UpdateAccountProductFitMutationVariables, TContext> => {

const mutationKey = getUpdateAccountProductFitMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAccountProductFit>>, UpdateAccountProductFitMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateAccountProductFit(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateAccountProductFitMutationResult = NonNullable<Awaited<ReturnType<typeof updateAccountProductFit>>>
    export type UpdateAccountProductFitMutationBody = AccountProductFitBase
    export type UpdateAccountProductFitMutationError = Error
    export type UpdateAccountProductFitMutationVariables = {id: string;data: AccountProductFitBase}

    /**
 * @summary Update a product-fit score
 */
export const useUpdateAccountProductFit = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAccountProductFit>>, TError,UpdateAccountProductFitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateAccountProductFit>>,
        TError,
        UpdateAccountProductFitMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateAccountProductFitMutationOptions(options));
    }

export const getDeleteAccountProductFitUrl = (id: string,) => {




  return `/api/account-product-fit/${id}`
}

/**
 * @summary Delete a product-fit score
 */
export const deleteAccountProductFit = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteAccountProductFitUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteAccountProductFitMutationKey = () => ['deleteAccountProductFit'] as const;

export const getDeleteAccountProductFitMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAccountProductFit>>, TError,DeleteAccountProductFitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteAccountProductFit>>, TError,DeleteAccountProductFitMutationVariables, TContext> => {

const mutationKey = getDeleteAccountProductFitMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteAccountProductFit>>, DeleteAccountProductFitMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteAccountProductFit(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteAccountProductFitMutationResult = NonNullable<Awaited<ReturnType<typeof deleteAccountProductFit>>>

    export type DeleteAccountProductFitMutationError = Error
    export type DeleteAccountProductFitMutationVariables = {id: string}

    /**
 * @summary Delete a product-fit score
 */
export const useDeleteAccountProductFit = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAccountProductFit>>, TError,DeleteAccountProductFitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteAccountProductFit>>,
        TError,
        DeleteAccountProductFitMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteAccountProductFitMutationOptions(options));
    }

export const getListTenderAiModelsUrl = () => {




  return `/api/tender-ai/models`
}

/**
 * @summary List backend-approved tender AI model choices and verified controls
 */
export const listTenderAiModels = async ( options?: Parameters<typeof customFetch>[1]): Promise<TenderAiModelCatalogue> => {

  return customFetch<TenderAiModelCatalogue>(getListTenderAiModelsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderAiModelsQueryKey = () => {
    return [
    `/api/tender-ai/models`
    ] as const;
    }


export const getListTenderAiModelsQueryOptions = <TData = Awaited<ReturnType<typeof listTenderAiModels>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderAiModels>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderAiModelsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderAiModels>>> = ({ signal }) => listTenderAiModels({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderAiModels>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderAiModelsQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderAiModels>>>
export type ListTenderAiModelsQueryError = unknown


/**
 * @summary List backend-approved tender AI model choices and verified controls
 */

export function useListTenderAiModels<TData = Awaited<ReturnType<typeof listTenderAiModels>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderAiModels>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderAiModelsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetTenderSettingsUrl = () => {




  return `/api/tender-settings`
}

/**
 * @summary Get entity tender storage defaults
 */
export const getTenderSettings = async ( options?: Parameters<typeof customFetch>[1]): Promise<TenderSettings> => {

  return customFetch<TenderSettings>(getGetTenderSettingsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenderSettingsQueryKey = () => {
    return [
    `/api/tender-settings`
    ] as const;
    }


export const getGetTenderSettingsQueryOptions = <TData = Awaited<ReturnType<typeof getTenderSettings>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenderSettingsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenderSettings>>> = ({ signal }) => getTenderSettings({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenderSettings>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTenderSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof getTenderSettings>>>
export type GetTenderSettingsQueryError = unknown


/**
 * @summary Get entity tender storage defaults
 */

export function useGetTenderSettings<TData = Awaited<ReturnType<typeof getTenderSettings>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTenderSettingsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateTenderSettingsUrl = () => {




  return `/api/tender-settings`
}

/**
 * @summary Save entity tender defaults using optimistic concurrency
 */
export const updateTenderSettings = async (tenderSettingsUpdate: TenderSettingsUpdate, options?: Parameters<typeof customFetch>[1]): Promise<TenderSettings> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderSettings>(getUpdateTenderSettingsUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderSettingsUpdate)
  }
);}





export const getUpdateTenderSettingsMutationKey = () => ['updateTenderSettings'] as const;

export const getUpdateTenderSettingsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenderSettings>>, TError,UpdateTenderSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateTenderSettings>>, TError,UpdateTenderSettingsMutationVariables, TContext> => {

const mutationKey = getUpdateTenderSettingsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateTenderSettings>>, UpdateTenderSettingsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateTenderSettings(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateTenderSettingsMutationResult = NonNullable<Awaited<ReturnType<typeof updateTenderSettings>>>
    export type UpdateTenderSettingsMutationBody = TenderSettingsUpdate
    export type UpdateTenderSettingsMutationError = Error
    export type UpdateTenderSettingsMutationVariables = {data: TenderSettingsUpdate}

    /**
 * @summary Save entity tender defaults using optimistic concurrency
 */
export const useUpdateTenderSettings = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenderSettings>>, TError,UpdateTenderSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateTenderSettings>>,
        TError,
        UpdateTenderSettingsMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateTenderSettingsMutationOptions(options));
    }

export const getUpdateTenderBidRulesUrl = () => {




  return `/api/tender-settings/bid-rules`
}

/**
 * @summary Save the entity bid rules prompt used with the Offerings Profile to recommend Respond, Maybe or Not for us; open tenders are re-scored
 */
export const updateTenderBidRules = async (tenderBidRulesUpdate: TenderBidRulesUpdate, options?: Parameters<typeof customFetch>[1]): Promise<TenderSettings> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderSettings>(getUpdateTenderBidRulesUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderBidRulesUpdate)
  }
);}





export const getUpdateTenderBidRulesMutationKey = () => ['updateTenderBidRules'] as const;

export const getUpdateTenderBidRulesMutationOptions = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenderBidRules>>, TError,UpdateTenderBidRulesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateTenderBidRules>>, TError,UpdateTenderBidRulesMutationVariables, TContext> => {

const mutationKey = getUpdateTenderBidRulesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateTenderBidRules>>, UpdateTenderBidRulesMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateTenderBidRules(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateTenderBidRulesMutationResult = NonNullable<Awaited<ReturnType<typeof updateTenderBidRules>>>
    export type UpdateTenderBidRulesMutationBody = TenderBidRulesUpdate
    export type UpdateTenderBidRulesMutationError = Error | void
    export type UpdateTenderBidRulesMutationVariables = {data: TenderBidRulesUpdate}

    /**
 * @summary Save the entity bid rules prompt used with the Offerings Profile to recommend Respond, Maybe or Not for us; open tenders are re-scored
 */
export const useUpdateTenderBidRules = <TError = Error | void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenderBidRules>>, TError,UpdateTenderBidRulesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateTenderBidRules>>,
        TError,
        UpdateTenderBidRulesMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateTenderBidRulesMutationOptions(options));
    }

export const getGetTenderSharePointReadinessUrl = () => {




  return `/api/tender-storage/sharepoint/connection`
}

/**
 * @summary Get safe entity SharePoint tender-storage readiness without credentials
 */
export const getTenderSharePointReadiness = async ( options?: Parameters<typeof customFetch>[1]): Promise<TenderSharePointReadiness> => {

  return customFetch<TenderSharePointReadiness>(getGetTenderSharePointReadinessUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenderSharePointReadinessQueryKey = () => {
    return [
    `/api/tender-storage/sharepoint/connection`
    ] as const;
    }


export const getGetTenderSharePointReadinessQueryOptions = <TData = Awaited<ReturnType<typeof getTenderSharePointReadiness>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderSharePointReadiness>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenderSharePointReadinessQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenderSharePointReadiness>>> = ({ signal }) => getTenderSharePointReadiness({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenderSharePointReadiness>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTenderSharePointReadinessQueryResult = NonNullable<Awaited<ReturnType<typeof getTenderSharePointReadiness>>>
export type GetTenderSharePointReadinessQueryError = unknown


/**
 * @summary Get safe entity SharePoint tender-storage readiness without credentials
 */

export function useGetTenderSharePointReadiness<TData = Awaited<ReturnType<typeof getTenderSharePointReadiness>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderSharePointReadiness>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTenderSharePointReadinessQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getStartTenderSharePointConnectionUrl = () => {




  return `/api/tender-storage/sharepoint/connections/start`
}

/**
 * @summary Start explicit administrator consent for SharePoint tender storage
 */
export const startTenderSharePointConnection = async ( options?: Parameters<typeof customFetch>[1]): Promise<TenderSharePointAuthorization> => {

  return customFetch<TenderSharePointAuthorization>(getStartTenderSharePointConnectionUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getStartTenderSharePointConnectionMutationKey = () => ['startTenderSharePointConnection'] as const;

export const getStartTenderSharePointConnectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startTenderSharePointConnection>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof startTenderSharePointConnection>>, TError,void, TContext> => {

const mutationKey = getStartTenderSharePointConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof startTenderSharePointConnection>>, void> = () => {


          return  startTenderSharePointConnection(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type StartTenderSharePointConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof startTenderSharePointConnection>>>

    export type StartTenderSharePointConnectionMutationError = Error


    /**
 * @summary Start explicit administrator consent for SharePoint tender storage
 */
export const useStartTenderSharePointConnection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startTenderSharePointConnection>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof startTenderSharePointConnection>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getStartTenderSharePointConnectionMutationOptions(options));
    }

export const getReconnectTenderSharePointConnectionUrl = (connectionId: string,) => {




  return `/api/tender-storage/sharepoint/connections/${connectionId}/reconnect`
}

/**
 * @summary Reauthorize tender storage without changing its verified destination
 */
export const reconnectTenderSharePointConnection = async (connectionId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenderSharePointAuthorization> => {

  return customFetch<TenderSharePointAuthorization>(getReconnectTenderSharePointConnectionUrl(connectionId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getReconnectTenderSharePointConnectionMutationKey = () => ['reconnectTenderSharePointConnection'] as const;

export const getReconnectTenderSharePointConnectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconnectTenderSharePointConnection>>, TError,ReconnectTenderSharePointConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reconnectTenderSharePointConnection>>, TError,ReconnectTenderSharePointConnectionMutationVariables, TContext> => {

const mutationKey = getReconnectTenderSharePointConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconnectTenderSharePointConnection>>, ReconnectTenderSharePointConnectionMutationVariables> = (props) => {
          const {connectionId} = props ?? {};

          return  reconnectTenderSharePointConnection(connectionId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconnectTenderSharePointConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof reconnectTenderSharePointConnection>>>

    export type ReconnectTenderSharePointConnectionMutationError = Error
    export type ReconnectTenderSharePointConnectionMutationVariables = {connectionId: string}

    /**
 * @summary Reauthorize tender storage without changing its verified destination
 */
export const useReconnectTenderSharePointConnection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconnectTenderSharePointConnection>>, TError,ReconnectTenderSharePointConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reconnectTenderSharePointConnection>>,
        TError,
        ReconnectTenderSharePointConnectionMutationVariables,
        TContext
      > => {
      return useMutation(getReconnectTenderSharePointConnectionMutationOptions(options));
    }

export const getCompleteTenderSharePointConnectionUrl = (params: CompleteTenderSharePointConnectionParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tender-storage/sharepoint/connections/callback?${stringifiedParams}` : `/api/tender-storage/sharepoint/connections/callback`
}

/**
 * @summary Complete browser-bound SharePoint tender-storage consent
 */
export const completeTenderSharePointConnection = async (params: CompleteTenderSharePointConnectionParams, options?: Parameters<typeof customFetch>[1]): Promise<unknown> => {

  return customFetch<unknown>(getCompleteTenderSharePointConnectionUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getCompleteTenderSharePointConnectionQueryKey = (params?: CompleteTenderSharePointConnectionParams,) => {
    return [
    `/api/tender-storage/sharepoint/connections/callback`, ...(params ? [params] : [])
    ] as const;
    }


export const getCompleteTenderSharePointConnectionQueryOptions = <TData = Awaited<ReturnType<typeof completeTenderSharePointConnection>>, TError = void | Error>(params: CompleteTenderSharePointConnectionParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof completeTenderSharePointConnection>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getCompleteTenderSharePointConnectionQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof completeTenderSharePointConnection>>> = ({ signal }) => completeTenderSharePointConnection(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof completeTenderSharePointConnection>>, TError, TData> & { queryKey: QueryKey }
}

export type CompleteTenderSharePointConnectionQueryResult = NonNullable<Awaited<ReturnType<typeof completeTenderSharePointConnection>>>
export type CompleteTenderSharePointConnectionQueryError = void | Error


/**
 * @summary Complete browser-bound SharePoint tender-storage consent
 */

export function useCompleteTenderSharePointConnection<TData = Awaited<ReturnType<typeof completeTenderSharePointConnection>>, TError = void | Error>(
 params: CompleteTenderSharePointConnectionParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof completeTenderSharePointConnection>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getCompleteTenderSharePointConnectionQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListTenderSharePointSitesUrl = (connectionId: string,
    params: ListTenderSharePointSitesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tender-storage/sharepoint/connections/${connectionId}/sites?${stringifiedParams}` : `/api/tender-storage/sharepoint/connections/${connectionId}/sites`
}

/**
 * @summary Search bounded SharePoint sites visible to the connected administrator
 */
export const listTenderSharePointSites = async (connectionId: string,
    params: ListTenderSharePointSitesParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderSharePointSitePage> => {

  return customFetch<TenderSharePointSitePage>(getListTenderSharePointSitesUrl(connectionId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderSharePointSitesQueryKey = (connectionId: string,
    params?: ListTenderSharePointSitesParams,) => {
    return [
    `/api/tender-storage/sharepoint/connections/${connectionId}/sites`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderSharePointSitesQueryOptions = <TData = Awaited<ReturnType<typeof listTenderSharePointSites>>, TError = unknown>(connectionId: string,
    params: ListTenderSharePointSitesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderSharePointSites>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderSharePointSitesQueryKey(connectionId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderSharePointSites>>> = ({ signal }) => listTenderSharePointSites(connectionId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: connectionId !== null && connectionId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderSharePointSites>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderSharePointSitesQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderSharePointSites>>>
export type ListTenderSharePointSitesQueryError = unknown


/**
 * @summary Search bounded SharePoint sites visible to the connected administrator
 */

export function useListTenderSharePointSites<TData = Awaited<ReturnType<typeof listTenderSharePointSites>>, TError = unknown>(
 connectionId: string,
    params: ListTenderSharePointSitesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderSharePointSites>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderSharePointSitesQueryOptions(connectionId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListTenderSharePointDrivesUrl = (connectionId: string,
    siteId: string,
    params?: ListTenderSharePointDrivesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tender-storage/sharepoint/connections/${connectionId}/sites/${siteId}/drives?${stringifiedParams}` : `/api/tender-storage/sharepoint/connections/${connectionId}/sites/${siteId}/drives`
}

/**
 * @summary List bounded document libraries for a SharePoint site
 */
export const listTenderSharePointDrives = async (connectionId: string,
    siteId: string,
    params?: ListTenderSharePointDrivesParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderSharePointDrivePage> => {

  return customFetch<TenderSharePointDrivePage>(getListTenderSharePointDrivesUrl(connectionId,siteId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderSharePointDrivesQueryKey = (connectionId: string,
    siteId: string,
    params?: ListTenderSharePointDrivesParams,) => {
    return [
    `/api/tender-storage/sharepoint/connections/${connectionId}/sites/${siteId}/drives`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderSharePointDrivesQueryOptions = <TData = Awaited<ReturnType<typeof listTenderSharePointDrives>>, TError = unknown>(connectionId: string,
    siteId: string,
    params?: ListTenderSharePointDrivesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderSharePointDrives>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderSharePointDrivesQueryKey(connectionId,siteId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderSharePointDrives>>> = ({ signal }) => listTenderSharePointDrives(connectionId,siteId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: connectionId !== null && connectionId !== undefined && siteId !== null && siteId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderSharePointDrives>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderSharePointDrivesQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderSharePointDrives>>>
export type ListTenderSharePointDrivesQueryError = unknown


/**
 * @summary List bounded document libraries for a SharePoint site
 */

export function useListTenderSharePointDrives<TData = Awaited<ReturnType<typeof listTenderSharePointDrives>>, TError = unknown>(
 connectionId: string,
    siteId: string,
    params?: ListTenderSharePointDrivesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderSharePointDrives>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderSharePointDrivesQueryOptions(connectionId,siteId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListTenderSharePointFoldersUrl = (connectionId: string,
    driveId: string,
    itemId: string,
    params?: ListTenderSharePointFoldersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tender-storage/sharepoint/connections/${connectionId}/drives/${driveId}/items/${itemId}/children?${stringifiedParams}` : `/api/tender-storage/sharepoint/connections/${connectionId}/drives/${driveId}/items/${itemId}/children`
}

/**
 * @summary List bounded folders below a SharePoint drive item
 */
export const listTenderSharePointFolders = async (connectionId: string,
    driveId: string,
    itemId: string,
    params?: ListTenderSharePointFoldersParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderSharePointFolderPage> => {

  return customFetch<TenderSharePointFolderPage>(getListTenderSharePointFoldersUrl(connectionId,driveId,itemId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderSharePointFoldersQueryKey = (connectionId: string,
    driveId: string,
    itemId: string,
    params?: ListTenderSharePointFoldersParams,) => {
    return [
    `/api/tender-storage/sharepoint/connections/${connectionId}/drives/${driveId}/items/${itemId}/children`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderSharePointFoldersQueryOptions = <TData = Awaited<ReturnType<typeof listTenderSharePointFolders>>, TError = unknown>(connectionId: string,
    driveId: string,
    itemId: string,
    params?: ListTenderSharePointFoldersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderSharePointFolders>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderSharePointFoldersQueryKey(connectionId,driveId,itemId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderSharePointFolders>>> = ({ signal }) => listTenderSharePointFolders(connectionId,driveId,itemId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: connectionId !== null && connectionId !== undefined && driveId !== null && driveId !== undefined && itemId !== null && itemId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderSharePointFolders>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderSharePointFoldersQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderSharePointFolders>>>
export type ListTenderSharePointFoldersQueryError = unknown


/**
 * @summary List bounded folders below a SharePoint drive item
 */

export function useListTenderSharePointFolders<TData = Awaited<ReturnType<typeof listTenderSharePointFolders>>, TError = unknown>(
 connectionId: string,
    driveId: string,
    itemId: string,
    params?: ListTenderSharePointFoldersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderSharePointFolders>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderSharePointFoldersQueryOptions(connectionId,driveId,itemId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSelectTenderSharePointDestinationUrl = (connectionId: string,) => {




  return `/api/tender-storage/sharepoint/connections/${connectionId}/destination`
}

/**
 * @summary Verify and save the SharePoint tender destination
 */
export const selectTenderSharePointDestination = async (connectionId: string,
    tenderSharePointDestinationUpdate: TenderSharePointDestinationUpdate, options?: Parameters<typeof customFetch>[1]): Promise<TenderSharePointReadiness> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderSharePointReadiness>(getSelectTenderSharePointDestinationUrl(connectionId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderSharePointDestinationUpdate)
  }
);}





export const getSelectTenderSharePointDestinationMutationKey = () => ['selectTenderSharePointDestination'] as const;

export const getSelectTenderSharePointDestinationMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof selectTenderSharePointDestination>>, TError,SelectTenderSharePointDestinationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof selectTenderSharePointDestination>>, TError,SelectTenderSharePointDestinationMutationVariables, TContext> => {

const mutationKey = getSelectTenderSharePointDestinationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof selectTenderSharePointDestination>>, SelectTenderSharePointDestinationMutationVariables> = (props) => {
          const {connectionId,data} = props ?? {};

          return  selectTenderSharePointDestination(connectionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SelectTenderSharePointDestinationMutationResult = NonNullable<Awaited<ReturnType<typeof selectTenderSharePointDestination>>>
    export type SelectTenderSharePointDestinationMutationBody = TenderSharePointDestinationUpdate
    export type SelectTenderSharePointDestinationMutationError = Error
    export type SelectTenderSharePointDestinationMutationVariables = {connectionId: string;data: TenderSharePointDestinationUpdate}

    /**
 * @summary Verify and save the SharePoint tender destination
 */
export const useSelectTenderSharePointDestination = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof selectTenderSharePointDestination>>, TError,SelectTenderSharePointDestinationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof selectTenderSharePointDestination>>,
        TError,
        SelectTenderSharePointDestinationMutationVariables,
        TContext
      > => {
      return useMutation(getSelectTenderSharePointDestinationMutationOptions(options));
    }

export const getGetTenderEmbeddedReadinessUrl = () => {




  return `/api/tender-storage/sharepoint-embedded/connection`
}

/**
 * @summary Get safe entity SharePoint Embedded readiness without credentials
 */
export const getTenderEmbeddedReadiness = async ( options?: Parameters<typeof customFetch>[1]): Promise<TenderEmbeddedReadiness> => {

  return customFetch<TenderEmbeddedReadiness>(getGetTenderEmbeddedReadinessUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenderEmbeddedReadinessQueryKey = () => {
    return [
    `/api/tender-storage/sharepoint-embedded/connection`
    ] as const;
    }


export const getGetTenderEmbeddedReadinessQueryOptions = <TData = Awaited<ReturnType<typeof getTenderEmbeddedReadiness>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderEmbeddedReadiness>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenderEmbeddedReadinessQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenderEmbeddedReadiness>>> = ({ signal }) => getTenderEmbeddedReadiness({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenderEmbeddedReadiness>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTenderEmbeddedReadinessQueryResult = NonNullable<Awaited<ReturnType<typeof getTenderEmbeddedReadiness>>>
export type GetTenderEmbeddedReadinessQueryError = unknown


/**
 * @summary Get safe entity SharePoint Embedded readiness without credentials
 */

export function useGetTenderEmbeddedReadiness<TData = Awaited<ReturnType<typeof getTenderEmbeddedReadiness>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderEmbeddedReadiness>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTenderEmbeddedReadinessQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getStartTenderEmbeddedConnectionUrl = () => {




  return `/api/tender-storage/sharepoint-embedded/connections/start`
}

/**
 * @summary Start explicit administrator consent for Embedded tender storage
 */
export const startTenderEmbeddedConnection = async ( options?: Parameters<typeof customFetch>[1]): Promise<TenderSharePointAuthorization> => {

  return customFetch<TenderSharePointAuthorization>(getStartTenderEmbeddedConnectionUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getStartTenderEmbeddedConnectionMutationKey = () => ['startTenderEmbeddedConnection'] as const;

export const getStartTenderEmbeddedConnectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startTenderEmbeddedConnection>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof startTenderEmbeddedConnection>>, TError,void, TContext> => {

const mutationKey = getStartTenderEmbeddedConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof startTenderEmbeddedConnection>>, void> = () => {


          return  startTenderEmbeddedConnection(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type StartTenderEmbeddedConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof startTenderEmbeddedConnection>>>

    export type StartTenderEmbeddedConnectionMutationError = Error


    /**
 * @summary Start explicit administrator consent for Embedded tender storage
 */
export const useStartTenderEmbeddedConnection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startTenderEmbeddedConnection>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof startTenderEmbeddedConnection>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getStartTenderEmbeddedConnectionMutationOptions(options));
    }

export const getReconnectTenderEmbeddedConnectionUrl = (connectionId: string,) => {




  return `/api/tender-storage/sharepoint-embedded/connections/${connectionId}/reconnect`
}

/**
 * @summary Reauthorize Embedded storage without changing its verified entity container
 */
export const reconnectTenderEmbeddedConnection = async (connectionId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenderSharePointAuthorization> => {

  return customFetch<TenderSharePointAuthorization>(getReconnectTenderEmbeddedConnectionUrl(connectionId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getReconnectTenderEmbeddedConnectionMutationKey = () => ['reconnectTenderEmbeddedConnection'] as const;

export const getReconnectTenderEmbeddedConnectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconnectTenderEmbeddedConnection>>, TError,ReconnectTenderEmbeddedConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reconnectTenderEmbeddedConnection>>, TError,ReconnectTenderEmbeddedConnectionMutationVariables, TContext> => {

const mutationKey = getReconnectTenderEmbeddedConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconnectTenderEmbeddedConnection>>, ReconnectTenderEmbeddedConnectionMutationVariables> = (props) => {
          const {connectionId} = props ?? {};

          return  reconnectTenderEmbeddedConnection(connectionId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconnectTenderEmbeddedConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof reconnectTenderEmbeddedConnection>>>

    export type ReconnectTenderEmbeddedConnectionMutationError = Error
    export type ReconnectTenderEmbeddedConnectionMutationVariables = {connectionId: string}

    /**
 * @summary Reauthorize Embedded storage without changing its verified entity container
 */
export const useReconnectTenderEmbeddedConnection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconnectTenderEmbeddedConnection>>, TError,ReconnectTenderEmbeddedConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reconnectTenderEmbeddedConnection>>,
        TError,
        ReconnectTenderEmbeddedConnectionMutationVariables,
        TContext
      > => {
      return useMutation(getReconnectTenderEmbeddedConnectionMutationOptions(options));
    }

export const getCompleteTenderEmbeddedConnectionUrl = (params: CompleteTenderEmbeddedConnectionParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tender-storage/sharepoint-embedded/connections/callback?${stringifiedParams}` : `/api/tender-storage/sharepoint-embedded/connections/callback`
}

/**
 * @summary Complete browser-bound SharePoint Embedded consent
 */
export const completeTenderEmbeddedConnection = async (params: CompleteTenderEmbeddedConnectionParams, options?: Parameters<typeof customFetch>[1]): Promise<unknown> => {

  return customFetch<unknown>(getCompleteTenderEmbeddedConnectionUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getCompleteTenderEmbeddedConnectionQueryKey = (params?: CompleteTenderEmbeddedConnectionParams,) => {
    return [
    `/api/tender-storage/sharepoint-embedded/connections/callback`, ...(params ? [params] : [])
    ] as const;
    }


export const getCompleteTenderEmbeddedConnectionQueryOptions = <TData = Awaited<ReturnType<typeof completeTenderEmbeddedConnection>>, TError = void | Error>(params: CompleteTenderEmbeddedConnectionParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof completeTenderEmbeddedConnection>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getCompleteTenderEmbeddedConnectionQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof completeTenderEmbeddedConnection>>> = ({ signal }) => completeTenderEmbeddedConnection(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof completeTenderEmbeddedConnection>>, TError, TData> & { queryKey: QueryKey }
}

export type CompleteTenderEmbeddedConnectionQueryResult = NonNullable<Awaited<ReturnType<typeof completeTenderEmbeddedConnection>>>
export type CompleteTenderEmbeddedConnectionQueryError = void | Error


/**
 * @summary Complete browser-bound SharePoint Embedded consent
 */

export function useCompleteTenderEmbeddedConnection<TData = Awaited<ReturnType<typeof completeTenderEmbeddedConnection>>, TError = void | Error>(
 params: CompleteTenderEmbeddedConnectionParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof completeTenderEmbeddedConnection>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getCompleteTenderEmbeddedConnectionQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListTenderEmbeddedContainersUrl = (connectionId: string,
    params?: ListTenderEmbeddedContainersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tender-storage/sharepoint-embedded/connections/${connectionId}/containers?${stringifiedParams}` : `/api/tender-storage/sharepoint-embedded/connections/${connectionId}/containers`
}

/**
 * @summary List bounded Embedded containers of the approved project type
 */
export const listTenderEmbeddedContainers = async (connectionId: string,
    params?: ListTenderEmbeddedContainersParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderEmbeddedContainerPage> => {

  return customFetch<TenderEmbeddedContainerPage>(getListTenderEmbeddedContainersUrl(connectionId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderEmbeddedContainersQueryKey = (connectionId: string,
    params?: ListTenderEmbeddedContainersParams,) => {
    return [
    `/api/tender-storage/sharepoint-embedded/connections/${connectionId}/containers`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderEmbeddedContainersQueryOptions = <TData = Awaited<ReturnType<typeof listTenderEmbeddedContainers>>, TError = unknown>(connectionId: string,
    params?: ListTenderEmbeddedContainersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderEmbeddedContainers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderEmbeddedContainersQueryKey(connectionId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderEmbeddedContainers>>> = ({ signal }) => listTenderEmbeddedContainers(connectionId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: connectionId !== null && connectionId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderEmbeddedContainers>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderEmbeddedContainersQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderEmbeddedContainers>>>
export type ListTenderEmbeddedContainersQueryError = unknown


/**
 * @summary List bounded Embedded containers of the approved project type
 */

export function useListTenderEmbeddedContainers<TData = Awaited<ReturnType<typeof listTenderEmbeddedContainers>>, TError = unknown>(
 connectionId: string,
    params?: ListTenderEmbeddedContainersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderEmbeddedContainers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderEmbeddedContainersQueryOptions(connectionId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSelectTenderEmbeddedDestinationUrl = (connectionId: string,) => {




  return `/api/tender-storage/sharepoint-embedded/connections/${connectionId}/destination`
}

/**
 * @summary Verify and save the entity Embedded container mapping
 */
export const selectTenderEmbeddedDestination = async (connectionId: string,
    tenderEmbeddedDestinationUpdate: TenderEmbeddedDestinationUpdate, options?: Parameters<typeof customFetch>[1]): Promise<TenderEmbeddedReadiness> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderEmbeddedReadiness>(getSelectTenderEmbeddedDestinationUrl(connectionId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderEmbeddedDestinationUpdate)
  }
);}





export const getSelectTenderEmbeddedDestinationMutationKey = () => ['selectTenderEmbeddedDestination'] as const;

export const getSelectTenderEmbeddedDestinationMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof selectTenderEmbeddedDestination>>, TError,SelectTenderEmbeddedDestinationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof selectTenderEmbeddedDestination>>, TError,SelectTenderEmbeddedDestinationMutationVariables, TContext> => {

const mutationKey = getSelectTenderEmbeddedDestinationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof selectTenderEmbeddedDestination>>, SelectTenderEmbeddedDestinationMutationVariables> = (props) => {
          const {connectionId,data} = props ?? {};

          return  selectTenderEmbeddedDestination(connectionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SelectTenderEmbeddedDestinationMutationResult = NonNullable<Awaited<ReturnType<typeof selectTenderEmbeddedDestination>>>
    export type SelectTenderEmbeddedDestinationMutationBody = TenderEmbeddedDestinationUpdate
    export type SelectTenderEmbeddedDestinationMutationError = Error
    export type SelectTenderEmbeddedDestinationMutationVariables = {connectionId: string;data: TenderEmbeddedDestinationUpdate}

    /**
 * @summary Verify and save the entity Embedded container mapping
 */
export const useSelectTenderEmbeddedDestination = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof selectTenderEmbeddedDestination>>, TError,SelectTenderEmbeddedDestinationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof selectTenderEmbeddedDestination>>,
        TError,
        SelectTenderEmbeddedDestinationMutationVariables,
        TContext
      > => {
      return useMutation(getSelectTenderEmbeddedDestinationMutationOptions(options));
    }

export const getCreateTenderPreparationUrl = () => {




  return `/api/tender-preparations`
}

export const createTenderPreparation = async (tenderPreparationCreate: TenderPreparationCreate, options?: Parameters<typeof customFetch>[1]): Promise<TenderPreparation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderPreparation>(getCreateTenderPreparationUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderPreparationCreate)
  }
);}





export const getCreateTenderPreparationMutationKey = () => ['createTenderPreparation'] as const;

export const getCreateTenderPreparationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenderPreparation>>, TError,CreateTenderPreparationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTenderPreparation>>, TError,CreateTenderPreparationMutationVariables, TContext> => {

const mutationKey = getCreateTenderPreparationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTenderPreparation>>, CreateTenderPreparationMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createTenderPreparation(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTenderPreparationMutationResult = NonNullable<Awaited<ReturnType<typeof createTenderPreparation>>>
    export type CreateTenderPreparationMutationBody = TenderPreparationCreate
    export type CreateTenderPreparationMutationError = unknown
    export type CreateTenderPreparationMutationVariables = {data: TenderPreparationCreate}

    export const useCreateTenderPreparation = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenderPreparation>>, TError,CreateTenderPreparationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createTenderPreparation>>,
        TError,
        CreateTenderPreparationMutationVariables,
        TContext
      > => {
      return useMutation(getCreateTenderPreparationMutationOptions(options));
    }

export const getGetTenderPreparationUrl = (id: string,
    params?: GetTenderPreparationParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tender-preparations/${id}?${stringifiedParams}` : `/api/tender-preparations/${id}`
}

export const getTenderPreparation = async (id: string,
    params?: GetTenderPreparationParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderPreparation> => {

  return customFetch<TenderPreparation>(getGetTenderPreparationUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenderPreparationQueryKey = (id: string,
    params?: GetTenderPreparationParams,) => {
    return [
    `/api/tender-preparations/${id}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetTenderPreparationQueryOptions = <TData = Awaited<ReturnType<typeof getTenderPreparation>>, TError = unknown>(id: string,
    params?: GetTenderPreparationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderPreparation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenderPreparationQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenderPreparation>>> = ({ signal }) => getTenderPreparation(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenderPreparation>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTenderPreparationQueryResult = NonNullable<Awaited<ReturnType<typeof getTenderPreparation>>>
export type GetTenderPreparationQueryError = unknown



export function useGetTenderPreparation<TData = Awaited<ReturnType<typeof getTenderPreparation>>, TError = unknown>(
 id: string,
    params?: GetTenderPreparationParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderPreparation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTenderPreparationQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUploadTenderPreparationUrl = (id: string,) => {




  return `/api/tender-preparations/${id}/uploads`
}

export const uploadTenderPreparation = async (id: string,
    tenderPreparationUpload: TenderPreparationUpload, options?: Parameters<typeof customFetch>[1]): Promise<TenderPreparation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderPreparation>(getUploadTenderPreparationUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderPreparationUpload)
  }
);}





export const getUploadTenderPreparationMutationKey = () => ['uploadTenderPreparation'] as const;

export const getUploadTenderPreparationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadTenderPreparation>>, TError,UploadTenderPreparationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadTenderPreparation>>, TError,UploadTenderPreparationMutationVariables, TContext> => {

const mutationKey = getUploadTenderPreparationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadTenderPreparation>>, UploadTenderPreparationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  uploadTenderPreparation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadTenderPreparationMutationResult = NonNullable<Awaited<ReturnType<typeof uploadTenderPreparation>>>
    export type UploadTenderPreparationMutationBody = TenderPreparationUpload
    export type UploadTenderPreparationMutationError = unknown
    export type UploadTenderPreparationMutationVariables = {id: string;data: TenderPreparationUpload}

    export const useUploadTenderPreparation = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadTenderPreparation>>, TError,UploadTenderPreparationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadTenderPreparation>>,
        TError,
        UploadTenderPreparationMutationVariables,
        TContext
      > => {
      return useMutation(getUploadTenderPreparationMutationOptions(options));
    }

export const getPrepareTenderDocumentsUrl = (id: string,) => {




  return `/api/tender-preparations/${id}/prepare`
}

export const prepareTenderDocuments = async (id: string,
    prepareTenderDocumentsBody: PrepareTenderDocumentsBody, options?: Parameters<typeof customFetch>[1]): Promise<TenderPreparation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderPreparation>(getPrepareTenderDocumentsUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(prepareTenderDocumentsBody)
  }
);}





export const getPrepareTenderDocumentsMutationKey = () => ['prepareTenderDocuments'] as const;

export const getPrepareTenderDocumentsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof prepareTenderDocuments>>, TError,PrepareTenderDocumentsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof prepareTenderDocuments>>, TError,PrepareTenderDocumentsMutationVariables, TContext> => {

const mutationKey = getPrepareTenderDocumentsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof prepareTenderDocuments>>, PrepareTenderDocumentsMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  prepareTenderDocuments(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PrepareTenderDocumentsMutationResult = NonNullable<Awaited<ReturnType<typeof prepareTenderDocuments>>>
    export type PrepareTenderDocumentsMutationBody = PrepareTenderDocumentsBody
    export type PrepareTenderDocumentsMutationError = unknown
    export type PrepareTenderDocumentsMutationVariables = {id: string;data: PrepareTenderDocumentsBody}

    export const usePrepareTenderDocuments = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof prepareTenderDocuments>>, TError,PrepareTenderDocumentsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof prepareTenderDocuments>>,
        TError,
        PrepareTenderDocumentsMutationVariables,
        TContext
      > => {
      return useMutation(getPrepareTenderDocumentsMutationOptions(options));
    }

export const getReviewTenderPreparationUrl = (id: string,) => {




  return `/api/tender-preparations/${id}/review`
}

export const reviewTenderPreparation = async (id: string,
    tenderPreparationEdit: TenderPreparationEdit, options?: Parameters<typeof customFetch>[1]): Promise<TenderPreparation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderPreparation>(getReviewTenderPreparationUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderPreparationEdit)
  }
);}





export const getReviewTenderPreparationMutationKey = () => ['reviewTenderPreparation'] as const;

export const getReviewTenderPreparationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewTenderPreparation>>, TError,ReviewTenderPreparationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewTenderPreparation>>, TError,ReviewTenderPreparationMutationVariables, TContext> => {

const mutationKey = getReviewTenderPreparationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewTenderPreparation>>, ReviewTenderPreparationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reviewTenderPreparation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewTenderPreparationMutationResult = NonNullable<Awaited<ReturnType<typeof reviewTenderPreparation>>>
    export type ReviewTenderPreparationMutationBody = TenderPreparationEdit
    export type ReviewTenderPreparationMutationError = unknown
    export type ReviewTenderPreparationMutationVariables = {id: string;data: TenderPreparationEdit}

    export const useReviewTenderPreparation = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewTenderPreparation>>, TError,ReviewTenderPreparationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewTenderPreparation>>,
        TError,
        ReviewTenderPreparationMutationVariables,
        TContext
      > => {
      return useMutation(getReviewTenderPreparationMutationOptions(options));
    }

export const getConfirmTenderPreparationUrl = (id: string,) => {




  return `/api/tender-preparations/${id}/confirm`
}

export const confirmTenderPreparation = async (id: string,
    confirmTenderPreparationBody: ConfirmTenderPreparationBody, options?: Parameters<typeof customFetch>[1]): Promise<TenderPreparation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderPreparation>(getConfirmTenderPreparationUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(confirmTenderPreparationBody)
  }
);}





export const getConfirmTenderPreparationMutationKey = () => ['confirmTenderPreparation'] as const;

export const getConfirmTenderPreparationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmTenderPreparation>>, TError,ConfirmTenderPreparationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmTenderPreparation>>, TError,ConfirmTenderPreparationMutationVariables, TContext> => {

const mutationKey = getConfirmTenderPreparationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmTenderPreparation>>, ConfirmTenderPreparationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  confirmTenderPreparation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmTenderPreparationMutationResult = NonNullable<Awaited<ReturnType<typeof confirmTenderPreparation>>>
    export type ConfirmTenderPreparationMutationBody = ConfirmTenderPreparationBody
    export type ConfirmTenderPreparationMutationError = unknown
    export type ConfirmTenderPreparationMutationVariables = {id: string;data: ConfirmTenderPreparationBody}

    export const useConfirmTenderPreparation = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmTenderPreparation>>, TError,ConfirmTenderPreparationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmTenderPreparation>>,
        TError,
        ConfirmTenderPreparationMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmTenderPreparationMutationOptions(options));
    }

export const getConfirmTenderPreparationAccountUrl = (id: string,) => {




  return `/api/tender-preparations/${id}/account-confirm`
}

export const confirmTenderPreparationAccount = async (id: string,
    confirmTenderPreparationAccountBody: ConfirmTenderPreparationAccountBody, options?: Parameters<typeof customFetch>[1]): Promise<TenderPreparation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderPreparation>(getConfirmTenderPreparationAccountUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(confirmTenderPreparationAccountBody)
  }
);}





export const getConfirmTenderPreparationAccountMutationKey = () => ['confirmTenderPreparationAccount'] as const;

export const getConfirmTenderPreparationAccountMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmTenderPreparationAccount>>, TError,ConfirmTenderPreparationAccountMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmTenderPreparationAccount>>, TError,ConfirmTenderPreparationAccountMutationVariables, TContext> => {

const mutationKey = getConfirmTenderPreparationAccountMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmTenderPreparationAccount>>, ConfirmTenderPreparationAccountMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  confirmTenderPreparationAccount(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmTenderPreparationAccountMutationResult = NonNullable<Awaited<ReturnType<typeof confirmTenderPreparationAccount>>>
    export type ConfirmTenderPreparationAccountMutationBody = ConfirmTenderPreparationAccountBody
    export type ConfirmTenderPreparationAccountMutationError = unknown
    export type ConfirmTenderPreparationAccountMutationVariables = {id: string;data: ConfirmTenderPreparationAccountBody}

    export const useConfirmTenderPreparationAccount = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmTenderPreparationAccount>>, TError,ConfirmTenderPreparationAccountMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmTenderPreparationAccount>>,
        TError,
        ConfirmTenderPreparationAccountMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmTenderPreparationAccountMutationOptions(options));
    }

export const getCancelTenderPreparationReviewUrl = (id: string,) => {




  return `/api/tender-preparations/${id}/cancel-review`
}

export const cancelTenderPreparationReview = async (id: string,
    cancelTenderPreparationReviewBody: CancelTenderPreparationReviewBody, options?: Parameters<typeof customFetch>[1]): Promise<TenderPreparation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderPreparation>(getCancelTenderPreparationReviewUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(cancelTenderPreparationReviewBody)
  }
);}





export const getCancelTenderPreparationReviewMutationKey = () => ['cancelTenderPreparationReview'] as const;

export const getCancelTenderPreparationReviewMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelTenderPreparationReview>>, TError,CancelTenderPreparationReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelTenderPreparationReview>>, TError,CancelTenderPreparationReviewMutationVariables, TContext> => {

const mutationKey = getCancelTenderPreparationReviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelTenderPreparationReview>>, CancelTenderPreparationReviewMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  cancelTenderPreparationReview(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelTenderPreparationReviewMutationResult = NonNullable<Awaited<ReturnType<typeof cancelTenderPreparationReview>>>
    export type CancelTenderPreparationReviewMutationBody = CancelTenderPreparationReviewBody
    export type CancelTenderPreparationReviewMutationError = unknown
    export type CancelTenderPreparationReviewMutationVariables = {id: string;data: CancelTenderPreparationReviewBody}

    export const useCancelTenderPreparationReview = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelTenderPreparationReview>>, TError,CancelTenderPreparationReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelTenderPreparationReview>>,
        TError,
        CancelTenderPreparationReviewMutationVariables,
        TContext
      > => {
      return useMutation(getCancelTenderPreparationReviewMutationOptions(options));
    }

export const getAdoptTenderPreparationUrl = (id: string,) => {




  return `/api/tender-preparations/${id}/adopt`
}

export const adoptTenderPreparation = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<TenderPreparation> => {

  return customFetch<TenderPreparation>(getAdoptTenderPreparationUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAdoptTenderPreparationMutationKey = () => ['adoptTenderPreparation'] as const;

export const getAdoptTenderPreparationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adoptTenderPreparation>>, TError,AdoptTenderPreparationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof adoptTenderPreparation>>, TError,AdoptTenderPreparationMutationVariables, TContext> => {

const mutationKey = getAdoptTenderPreparationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof adoptTenderPreparation>>, AdoptTenderPreparationMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  adoptTenderPreparation(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AdoptTenderPreparationMutationResult = NonNullable<Awaited<ReturnType<typeof adoptTenderPreparation>>>

    export type AdoptTenderPreparationMutationError = unknown
    export type AdoptTenderPreparationMutationVariables = {id: string}

    export const useAdoptTenderPreparation = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adoptTenderPreparation>>, TError,AdoptTenderPreparationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof adoptTenderPreparation>>,
        TError,
        AdoptTenderPreparationMutationVariables,
        TContext
      > => {
      return useMutation(getAdoptTenderPreparationMutationOptions(options));
    }

export const getReviewTenderPreparationAccountUrl = (id: string,) => {




  return `/api/tender-preparations/${id}/account-review`
}

export const reviewTenderPreparationAccount = async (id: string,
    reviewTenderPreparationAccountBody: ReviewTenderPreparationAccountBody, options?: Parameters<typeof customFetch>[1]): Promise<TenderPreparation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderPreparation>(getReviewTenderPreparationAccountUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewTenderPreparationAccountBody)
  }
);}





export const getReviewTenderPreparationAccountMutationKey = () => ['reviewTenderPreparationAccount'] as const;

export const getReviewTenderPreparationAccountMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewTenderPreparationAccount>>, TError,ReviewTenderPreparationAccountMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewTenderPreparationAccount>>, TError,ReviewTenderPreparationAccountMutationVariables, TContext> => {

const mutationKey = getReviewTenderPreparationAccountMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewTenderPreparationAccount>>, ReviewTenderPreparationAccountMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reviewTenderPreparationAccount(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewTenderPreparationAccountMutationResult = NonNullable<Awaited<ReturnType<typeof reviewTenderPreparationAccount>>>
    export type ReviewTenderPreparationAccountMutationBody = ReviewTenderPreparationAccountBody
    export type ReviewTenderPreparationAccountMutationError = unknown
    export type ReviewTenderPreparationAccountMutationVariables = {id: string;data: ReviewTenderPreparationAccountBody}

    export const useReviewTenderPreparationAccount = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewTenderPreparationAccount>>, TError,ReviewTenderPreparationAccountMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewTenderPreparationAccount>>,
        TError,
        ReviewTenderPreparationAccountMutationVariables,
        TContext
      > => {
      return useMutation(getReviewTenderPreparationAccountMutationOptions(options));
    }

export const getListProposalsUrl = (params?: ListProposalsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/proposals?${stringifiedParams}` : `/api/proposals`
}

/**
 * Read-only native projection. Linked SOW proposal/document pairs appear once. Status remains the source workflow status; this listing grants no approval authority.
 * @summary List authorized Tender proposals and managed SOW documents
 */
export const listProposals = async (params?: ListProposalsParams, options?: Parameters<typeof customFetch>[1]): Promise<ProposalList> => {

  return customFetch<ProposalList>(getListProposalsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListProposalsQueryKey = (params?: ListProposalsParams,) => {
    return [
    `/api/proposals`, ...(params ? [params] : [])
    ] as const;
    }


export const getListProposalsQueryOptions = <TData = Awaited<ReturnType<typeof listProposals>>, TError = Error>(params?: ListProposalsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listProposals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListProposalsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listProposals>>> = ({ signal }) => listProposals(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listProposals>>, TError, TData> & { queryKey: QueryKey }
}

export type ListProposalsQueryResult = NonNullable<Awaited<ReturnType<typeof listProposals>>>
export type ListProposalsQueryError = Error


/**
 * @summary List authorized Tender proposals and managed SOW documents
 */

export function useListProposals<TData = Awaited<ReturnType<typeof listProposals>>, TError = Error>(
 params?: ListProposalsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listProposals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListProposalsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListTendersUrl = (params?: ListTendersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders?${stringifiedParams}` : `/api/tenders`
}

/**
 * @summary List entity tenders
 */
export const listTenders = async (params?: ListTendersParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderList> => {

  return customFetch<TenderList>(getListTendersUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTendersQueryKey = (params?: ListTendersParams,) => {
    return [
    `/api/tenders`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTendersQueryOptions = <TData = Awaited<ReturnType<typeof listTenders>>, TError = unknown>(params?: ListTendersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenders>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTendersQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenders>>> = ({ signal }) => listTenders(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenders>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTendersQueryResult = NonNullable<Awaited<ReturnType<typeof listTenders>>>
export type ListTendersQueryError = unknown


/**
 * @summary List entity tenders
 */

export function useListTenders<TData = Awaited<ReturnType<typeof listTenders>>, TError = unknown>(
 params?: ListTendersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenders>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTendersQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateTenderUrl = () => {




  return `/api/tenders`
}

/**
 * @summary Create a tender for an entity account
 */
export const createTender = async (tenderCreate: TenderCreate, options?: Parameters<typeof customFetch>[1]): Promise<Tender> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Tender>(getCreateTenderUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderCreate)
  }
);}





export const getCreateTenderMutationKey = () => ['createTender'] as const;

export const getCreateTenderMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTender>>, TError,CreateTenderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTender>>, TError,CreateTenderMutationVariables, TContext> => {

const mutationKey = getCreateTenderMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTender>>, CreateTenderMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createTender(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTenderMutationResult = NonNullable<Awaited<ReturnType<typeof createTender>>>
    export type CreateTenderMutationBody = TenderCreate
    export type CreateTenderMutationError = Error
    export type CreateTenderMutationVariables = {data: TenderCreate}

    /**
 * @summary Create a tender for an entity account
 */
export const useCreateTender = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTender>>, TError,CreateTenderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createTender>>,
        TError,
        CreateTenderMutationVariables,
        TContext
      > => {
      return useMutation(getCreateTenderMutationOptions(options));
    }

export const getGetTenderUrl = (id: string,) => {




  return `/api/tenders/${id}`
}

/**
 * @summary Get a tender and foundation workflow counts
 */
export const getTender = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<TenderDetail> => {

  return customFetch<TenderDetail>(getGetTenderUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenderQueryKey = (id: string,) => {
    return [
    `/api/tenders/${id}`
    ] as const;
    }


export const getGetTenderQueryOptions = <TData = Awaited<ReturnType<typeof getTender>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTender>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenderQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTender>>> = ({ signal }) => getTender(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTender>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTenderQueryResult = NonNullable<Awaited<ReturnType<typeof getTender>>>
export type GetTenderQueryError = Error


/**
 * @summary Get a tender and foundation workflow counts
 */

export function useGetTender<TData = Awaited<ReturnType<typeof getTender>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTender>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTenderQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateTenderUrl = (id: string,) => {




  return `/api/tenders/${id}`
}

/**
 * @summary Update tender metadata or link a matching-account quote
 */
export const updateTender = async (id: string,
    tenderUpdate: TenderUpdate, options?: Parameters<typeof customFetch>[1]): Promise<Tender> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Tender>(getUpdateTenderUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderUpdate)
  }
);}





export const getUpdateTenderMutationKey = () => ['updateTender'] as const;

export const getUpdateTenderMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTender>>, TError,UpdateTenderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateTender>>, TError,UpdateTenderMutationVariables, TContext> => {

const mutationKey = getUpdateTenderMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateTender>>, UpdateTenderMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateTender(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateTenderMutationResult = NonNullable<Awaited<ReturnType<typeof updateTender>>>
    export type UpdateTenderMutationBody = TenderUpdate
    export type UpdateTenderMutationError = Error
    export type UpdateTenderMutationVariables = {id: string;data: TenderUpdate}

    /**
 * @summary Update tender metadata or link a matching-account quote
 */
export const useUpdateTender = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTender>>, TError,UpdateTenderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateTender>>,
        TError,
        UpdateTenderMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateTenderMutationOptions(options));
    }

export const getDeleteTenderUrl = (id: string,) => {




  return `/api/tenders/${id}`
}

/**
 * @summary Delete a draft tender before response work exists
 */
export const deleteTender = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteTenderUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteTenderMutationKey = () => ['deleteTender'] as const;

export const getDeleteTenderMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTender>>, TError,DeleteTenderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteTender>>, TError,DeleteTenderMutationVariables, TContext> => {

const mutationKey = getDeleteTenderMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteTender>>, DeleteTenderMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteTender(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteTenderMutationResult = NonNullable<Awaited<ReturnType<typeof deleteTender>>>

    export type DeleteTenderMutationError = Error
    export type DeleteTenderMutationVariables = {id: string}

    /**
 * @summary Delete a draft tender before response work exists
 */
export const useDeleteTender = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTender>>, TError,DeleteTenderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteTender>>,
        TError,
        DeleteTenderMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteTenderMutationOptions(options));
    }

export const getTransitionTenderUrl = (id: string,) => {




  return `/api/tenders/${id}/transition`
}

/**
 * @summary Record a permitted tender lifecycle transition
 */
export const transitionTender = async (id: string,
    tenderTransition: TenderTransition, options?: Parameters<typeof customFetch>[1]): Promise<Tender> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Tender>(getTransitionTenderUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderTransition)
  }
);}





export const getTransitionTenderMutationKey = () => ['transitionTender'] as const;

export const getTransitionTenderMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof transitionTender>>, TError,TransitionTenderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof transitionTender>>, TError,TransitionTenderMutationVariables, TContext> => {

const mutationKey = getTransitionTenderMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof transitionTender>>, TransitionTenderMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  transitionTender(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TransitionTenderMutationResult = NonNullable<Awaited<ReturnType<typeof transitionTender>>>
    export type TransitionTenderMutationBody = TenderTransition
    export type TransitionTenderMutationError = Error
    export type TransitionTenderMutationVariables = {id: string;data: TenderTransition}

    /**
 * @summary Record a permitted tender lifecycle transition
 */
export const useTransitionTender = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof transitionTender>>, TError,TransitionTenderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof transitionTender>>,
        TError,
        TransitionTenderMutationVariables,
        TContext
      > => {
      return useMutation(getTransitionTenderMutationOptions(options));
    }

export const getListTenderEventsUrl = (id: string,
    params?: ListTenderEventsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/events?${stringifiedParams}` : `/api/tenders/${id}/events`
}

/**
 * @summary List append-only tender history
 */
export const listTenderEvents = async (id: string,
    params?: ListTenderEventsParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderEventList> => {

  return customFetch<TenderEventList>(getListTenderEventsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderEventsQueryKey = (id: string,
    params?: ListTenderEventsParams,) => {
    return [
    `/api/tenders/${id}/events`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderEventsQueryOptions = <TData = Awaited<ReturnType<typeof listTenderEvents>>, TError = unknown>(id: string,
    params?: ListTenderEventsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderEvents>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderEventsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderEvents>>> = ({ signal }) => listTenderEvents(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderEvents>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderEventsQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderEvents>>>
export type ListTenderEventsQueryError = unknown


/**
 * @summary List append-only tender history
 */

export function useListTenderEvents<TData = Awaited<ReturnType<typeof listTenderEvents>>, TError = unknown>(
 id: string,
    params?: ListTenderEventsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderEvents>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderEventsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListTenderFilesUrl = (id: string,
    params?: ListTenderFilesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/files?${stringifiedParams}` : `/api/tenders/${id}/files`
}

/**
 * @summary Browse authorized proposal files and grounding without changing evidence
 */
export const listTenderFiles = async (id: string,
    params?: ListTenderFilesParams, options?: Parameters<typeof customFetch>[1]): Promise<ListTenderFiles200> => {

  return customFetch<ListTenderFiles200>(getListTenderFilesUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderFilesQueryKey = (id: string,
    params?: ListTenderFilesParams,) => {
    return [
    `/api/tenders/${id}/files`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderFilesQueryOptions = <TData = Awaited<ReturnType<typeof listTenderFiles>>, TError = unknown>(id: string,
    params?: ListTenderFilesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderFiles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderFilesQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderFiles>>> = ({ signal }) => listTenderFiles(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderFiles>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderFilesQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderFiles>>>
export type ListTenderFilesQueryError = unknown


/**
 * @summary Browse authorized proposal files and grounding without changing evidence
 */

export function useListTenderFiles<TData = Awaited<ReturnType<typeof listTenderFiles>>, TError = unknown>(
 id: string,
    params?: ListTenderFilesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderFiles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderFilesQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListTenderSourcesUrl = (id: string,
    params?: ListTenderSourcesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/sources?${stringifiedParams}` : `/api/tenders/${id}/sources`
}

/**
 * @summary List current tender collateral
 */
export const listTenderSources = async (id: string,
    params?: ListTenderSourcesParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderSourceList> => {

  return customFetch<TenderSourceList>(getListTenderSourcesUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderSourcesQueryKey = (id: string,
    params?: ListTenderSourcesParams,) => {
    return [
    `/api/tenders/${id}/sources`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderSourcesQueryOptions = <TData = Awaited<ReturnType<typeof listTenderSources>>, TError = unknown>(id: string,
    params?: ListTenderSourcesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderSources>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderSourcesQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderSources>>> = ({ signal }) => listTenderSources(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderSources>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderSourcesQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderSources>>>
export type ListTenderSourcesQueryError = unknown


/**
 * @summary List current tender collateral
 */

export function useListTenderSources<TData = Awaited<ReturnType<typeof listTenderSources>>, TError = unknown>(
 id: string,
    params?: ListTenderSourcesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderSources>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderSourcesQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUploadTenderSourceUrl = (id: string,) => {




  return `/api/tenders/${id}/sources`
}

/**
 * @summary Upload immutable tender collateral to application storage
 */
export const uploadTenderSource = async (id: string,
    tenderSourceUpload: TenderSourceUpload, options?: Parameters<typeof customFetch>[1]): Promise<TenderSource> => {
    const formData = new FormData();
formData.append(`file`, tenderSourceUpload.file);
formData.append(`kind`, tenderSourceUpload.kind);
formData.append(`label`, tenderSourceUpload.label);

  return customFetch<TenderSource>(getUploadTenderSourceUrl(id),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}





export const getUploadTenderSourceMutationKey = () => ['uploadTenderSource'] as const;

export const getUploadTenderSourceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadTenderSource>>, TError,UploadTenderSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadTenderSource>>, TError,UploadTenderSourceMutationVariables, TContext> => {

const mutationKey = getUploadTenderSourceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadTenderSource>>, UploadTenderSourceMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  uploadTenderSource(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadTenderSourceMutationResult = NonNullable<Awaited<ReturnType<typeof uploadTenderSource>>>
    export type UploadTenderSourceMutationBody = TenderSourceUpload
    export type UploadTenderSourceMutationError = Error
    export type UploadTenderSourceMutationVariables = {id: string;data: TenderSourceUpload}

    /**
 * @summary Upload immutable tender collateral to application storage
 */
export const useUploadTenderSource = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadTenderSource>>, TError,UploadTenderSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadTenderSource>>,
        TError,
        UploadTenderSourceMutationVariables,
        TContext
      > => {
      return useMutation(getUploadTenderSourceMutationOptions(options));
    }

export const getGetTenderSourceOfficeLaunchUrl = (id: string,
    sourceId: string,) => {




  return `/api/tenders/${id}/sources/${sourceId}/office-launch`
}

/**
 * @summary Get the verified SharePoint Word launch URL for current collateral
 */
export const getTenderSourceOfficeLaunch = async (id: string,
    sourceId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenderOfficeLaunch> => {

  return customFetch<TenderOfficeLaunch>(getGetTenderSourceOfficeLaunchUrl(id,sourceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenderSourceOfficeLaunchQueryKey = (id: string,
    sourceId: string,) => {
    return [
    `/api/tenders/${id}/sources/${sourceId}/office-launch`
    ] as const;
    }


export const getGetTenderSourceOfficeLaunchQueryOptions = <TData = Awaited<ReturnType<typeof getTenderSourceOfficeLaunch>>, TError = Error>(id: string,
    sourceId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderSourceOfficeLaunch>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenderSourceOfficeLaunchQueryKey(id,sourceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenderSourceOfficeLaunch>>> = ({ signal }) => getTenderSourceOfficeLaunch(id,sourceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && sourceId !== null && sourceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenderSourceOfficeLaunch>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTenderSourceOfficeLaunchQueryResult = NonNullable<Awaited<ReturnType<typeof getTenderSourceOfficeLaunch>>>
export type GetTenderSourceOfficeLaunchQueryError = Error


/**
 * @summary Get the verified SharePoint Word launch URL for current collateral
 */

export function useGetTenderSourceOfficeLaunch<TData = Awaited<ReturnType<typeof getTenderSourceOfficeLaunch>>, TError = Error>(
 id: string,
    sourceId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderSourceOfficeLaunch>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTenderSourceOfficeLaunchQueryOptions(id,sourceId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReconcileTenderSourceFromSharePointUrl = (id: string,
    sourceId: string,) => {




  return `/api/tenders/${id}/sources/${sourceId}/sharepoint-reconcile`
}

/**
 * @summary Import a changed native Word file as a new immutable tender source revision
 */
export const reconcileTenderSourceFromSharePoint = async (id: string,
    sourceId: string,
    tenderSharePointReconcile: TenderSharePointReconcile, options?: Parameters<typeof customFetch>[1]): Promise<TenderSource> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderSource>(getReconcileTenderSourceFromSharePointUrl(id,sourceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderSharePointReconcile)
  }
);}





export const getReconcileTenderSourceFromSharePointMutationKey = () => ['reconcileTenderSourceFromSharePoint'] as const;

export const getReconcileTenderSourceFromSharePointMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconcileTenderSourceFromSharePoint>>, TError,ReconcileTenderSourceFromSharePointMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reconcileTenderSourceFromSharePoint>>, TError,ReconcileTenderSourceFromSharePointMutationVariables, TContext> => {

const mutationKey = getReconcileTenderSourceFromSharePointMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconcileTenderSourceFromSharePoint>>, ReconcileTenderSourceFromSharePointMutationVariables> = (props) => {
          const {id,sourceId,data} = props ?? {};

          return  reconcileTenderSourceFromSharePoint(id,sourceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconcileTenderSourceFromSharePointMutationResult = NonNullable<Awaited<ReturnType<typeof reconcileTenderSourceFromSharePoint>>>
    export type ReconcileTenderSourceFromSharePointMutationBody = TenderSharePointReconcile
    export type ReconcileTenderSourceFromSharePointMutationError = Error
    export type ReconcileTenderSourceFromSharePointMutationVariables = {id: string;sourceId: string;data: TenderSharePointReconcile}

    /**
 * @summary Import a changed native Word file as a new immutable tender source revision
 */
export const useReconcileTenderSourceFromSharePoint = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconcileTenderSourceFromSharePoint>>, TError,ReconcileTenderSourceFromSharePointMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reconcileTenderSourceFromSharePoint>>,
        TError,
        ReconcileTenderSourceFromSharePointMutationVariables,
        TContext
      > => {
      return useMutation(getReconcileTenderSourceFromSharePointMutationOptions(options));
    }

export const getReconcileTenderSourceFromEmbeddedUrl = (id: string,
    sourceId: string,) => {




  return `/api/tenders/${id}/sources/${sourceId}/sharepoint-embedded-reconcile`
}

/**
 * @summary Import a changed Embedded Word file as a new immutable tender source revision
 */
export const reconcileTenderSourceFromEmbedded = async (id: string,
    sourceId: string,
    tenderSharePointReconcile: TenderSharePointReconcile, options?: Parameters<typeof customFetch>[1]): Promise<TenderSource> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderSource>(getReconcileTenderSourceFromEmbeddedUrl(id,sourceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderSharePointReconcile)
  }
);}





export const getReconcileTenderSourceFromEmbeddedMutationKey = () => ['reconcileTenderSourceFromEmbedded'] as const;

export const getReconcileTenderSourceFromEmbeddedMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconcileTenderSourceFromEmbedded>>, TError,ReconcileTenderSourceFromEmbeddedMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reconcileTenderSourceFromEmbedded>>, TError,ReconcileTenderSourceFromEmbeddedMutationVariables, TContext> => {

const mutationKey = getReconcileTenderSourceFromEmbeddedMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconcileTenderSourceFromEmbedded>>, ReconcileTenderSourceFromEmbeddedMutationVariables> = (props) => {
          const {id,sourceId,data} = props ?? {};

          return  reconcileTenderSourceFromEmbedded(id,sourceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconcileTenderSourceFromEmbeddedMutationResult = NonNullable<Awaited<ReturnType<typeof reconcileTenderSourceFromEmbedded>>>
    export type ReconcileTenderSourceFromEmbeddedMutationBody = TenderSharePointReconcile
    export type ReconcileTenderSourceFromEmbeddedMutationError = Error
    export type ReconcileTenderSourceFromEmbeddedMutationVariables = {id: string;sourceId: string;data: TenderSharePointReconcile}

    /**
 * @summary Import a changed Embedded Word file as a new immutable tender source revision
 */
export const useReconcileTenderSourceFromEmbedded = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconcileTenderSourceFromEmbedded>>, TError,ReconcileTenderSourceFromEmbeddedMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reconcileTenderSourceFromEmbedded>>,
        TError,
        ReconcileTenderSourceFromEmbeddedMutationVariables,
        TContext
      > => {
      return useMutation(getReconcileTenderSourceFromEmbeddedMutationOptions(options));
    }

export const getGetTenderSourceUrl = (id: string,
    sourceId: string,) => {




  return `/api/tenders/${id}/sources/${sourceId}`
}

/**
 * @summary Get one tender collateral revision
 */
export const getTenderSource = async (id: string,
    sourceId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenderSource> => {

  return customFetch<TenderSource>(getGetTenderSourceUrl(id,sourceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenderSourceQueryKey = (id: string,
    sourceId: string,) => {
    return [
    `/api/tenders/${id}/sources/${sourceId}`
    ] as const;
    }


export const getGetTenderSourceQueryOptions = <TData = Awaited<ReturnType<typeof getTenderSource>>, TError = unknown>(id: string,
    sourceId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderSource>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenderSourceQueryKey(id,sourceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenderSource>>> = ({ signal }) => getTenderSource(id,sourceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && sourceId !== null && sourceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenderSource>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTenderSourceQueryResult = NonNullable<Awaited<ReturnType<typeof getTenderSource>>>
export type GetTenderSourceQueryError = unknown


/**
 * @summary Get one tender collateral revision
 */

export function useGetTenderSource<TData = Awaited<ReturnType<typeof getTenderSource>>, TError = unknown>(
 id: string,
    sourceId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderSource>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTenderSourceQueryOptions(id,sourceId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateTenderSourceUrl = (id: string,
    sourceId: string,) => {




  return `/api/tenders/${id}/sources/${sourceId}`
}

/**
 * @summary Create a metadata-only revision for tender collateral
 */
export const updateTenderSource = async (id: string,
    sourceId: string,
    tenderSourceUpdate: TenderSourceUpdate, options?: Parameters<typeof customFetch>[1]): Promise<TenderSource> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderSource>(getUpdateTenderSourceUrl(id,sourceId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderSourceUpdate)
  }
);}





export const getUpdateTenderSourceMutationKey = () => ['updateTenderSource'] as const;

export const getUpdateTenderSourceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenderSource>>, TError,UpdateTenderSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateTenderSource>>, TError,UpdateTenderSourceMutationVariables, TContext> => {

const mutationKey = getUpdateTenderSourceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateTenderSource>>, UpdateTenderSourceMutationVariables> = (props) => {
          const {id,sourceId,data} = props ?? {};

          return  updateTenderSource(id,sourceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateTenderSourceMutationResult = NonNullable<Awaited<ReturnType<typeof updateTenderSource>>>
    export type UpdateTenderSourceMutationBody = TenderSourceUpdate
    export type UpdateTenderSourceMutationError = Error
    export type UpdateTenderSourceMutationVariables = {id: string;sourceId: string;data: TenderSourceUpdate}

    /**
 * @summary Create a metadata-only revision for tender collateral
 */
export const useUpdateTenderSource = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenderSource>>, TError,UpdateTenderSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateTenderSource>>,
        TError,
        UpdateTenderSourceMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateTenderSourceMutationOptions(options));
    }

export const getDownloadTenderSourceUrl = (id: string,
    sourceId: string,) => {




  return `/api/tenders/${id}/sources/${sourceId}/content`
}

/**
 * @summary Download exact bytes for one tender collateral revision
 */
export const downloadTenderSource = async (id: string,
    sourceId: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadTenderSourceUrl(id,sourceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadTenderSourceQueryKey = (id: string,
    sourceId: string,) => {
    return [
    `/api/tenders/${id}/sources/${sourceId}/content`
    ] as const;
    }


export const getDownloadTenderSourceQueryOptions = <TData = Awaited<ReturnType<typeof downloadTenderSource>>, TError = unknown>(id: string,
    sourceId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadTenderSource>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadTenderSourceQueryKey(id,sourceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadTenderSource>>> = ({ signal }) => downloadTenderSource(id,sourceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && sourceId !== null && sourceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadTenderSource>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadTenderSourceQueryResult = NonNullable<Awaited<ReturnType<typeof downloadTenderSource>>>
export type DownloadTenderSourceQueryError = unknown


/**
 * @summary Download exact bytes for one tender collateral revision
 */

export function useDownloadTenderSource<TData = Awaited<ReturnType<typeof downloadTenderSource>>, TError = unknown>(
 id: string,
    sourceId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadTenderSource>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadTenderSourceQueryOptions(id,sourceId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListTenderSourceRevisionsUrl = (id: string,
    sourceId: string,
    params?: ListTenderSourceRevisionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/sources/${sourceId}/revisions?${stringifiedParams}` : `/api/tenders/${id}/sources/${sourceId}/revisions`
}

/**
 * @summary List immutable revisions for one tender collateral item
 */
export const listTenderSourceRevisions = async (id: string,
    sourceId: string,
    params?: ListTenderSourceRevisionsParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderSourceList> => {

  return customFetch<TenderSourceList>(getListTenderSourceRevisionsUrl(id,sourceId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderSourceRevisionsQueryKey = (id: string,
    sourceId: string,
    params?: ListTenderSourceRevisionsParams,) => {
    return [
    `/api/tenders/${id}/sources/${sourceId}/revisions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderSourceRevisionsQueryOptions = <TData = Awaited<ReturnType<typeof listTenderSourceRevisions>>, TError = unknown>(id: string,
    sourceId: string,
    params?: ListTenderSourceRevisionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderSourceRevisions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderSourceRevisionsQueryKey(id,sourceId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderSourceRevisions>>> = ({ signal }) => listTenderSourceRevisions(id,sourceId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && sourceId !== null && sourceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderSourceRevisions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderSourceRevisionsQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderSourceRevisions>>>
export type ListTenderSourceRevisionsQueryError = unknown


/**
 * @summary List immutable revisions for one tender collateral item
 */

export function useListTenderSourceRevisions<TData = Awaited<ReturnType<typeof listTenderSourceRevisions>>, TError = unknown>(
 id: string,
    sourceId: string,
    params?: ListTenderSourceRevisionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderSourceRevisions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderSourceRevisionsQueryOptions(id,sourceId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviseTenderSourceUrl = (id: string,
    sourceId: string,) => {




  return `/api/tenders/${id}/sources/${sourceId}/revisions`
}

/**
 * @summary Upload a new immutable revision for tender collateral
 */
export const reviseTenderSource = async (id: string,
    sourceId: string,
    tenderSourceRevisionUpload: TenderSourceRevisionUpload, options?: Parameters<typeof customFetch>[1]): Promise<TenderSource> => {
    const formData = new FormData();
formData.append(`file`, tenderSourceRevisionUpload.file);
formData.append(`expectedRevision`, tenderSourceRevisionUpload.expectedRevision.toString())
formData.append(`kind`, tenderSourceRevisionUpload.kind);
formData.append(`label`, tenderSourceRevisionUpload.label);

  return customFetch<TenderSource>(getReviseTenderSourceUrl(id,sourceId),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}





export const getReviseTenderSourceMutationKey = () => ['reviseTenderSource'] as const;

export const getReviseTenderSourceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviseTenderSource>>, TError,ReviseTenderSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviseTenderSource>>, TError,ReviseTenderSourceMutationVariables, TContext> => {

const mutationKey = getReviseTenderSourceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviseTenderSource>>, ReviseTenderSourceMutationVariables> = (props) => {
          const {id,sourceId,data} = props ?? {};

          return  reviseTenderSource(id,sourceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviseTenderSourceMutationResult = NonNullable<Awaited<ReturnType<typeof reviseTenderSource>>>
    export type ReviseTenderSourceMutationBody = TenderSourceRevisionUpload
    export type ReviseTenderSourceMutationError = Error
    export type ReviseTenderSourceMutationVariables = {id: string;sourceId: string;data: TenderSourceRevisionUpload}

    /**
 * @summary Upload a new immutable revision for tender collateral
 */
export const useReviseTenderSource = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviseTenderSource>>, TError,ReviseTenderSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviseTenderSource>>,
        TError,
        ReviseTenderSourceMutationVariables,
        TContext
      > => {
      return useMutation(getReviseTenderSourceMutationOptions(options));
    }

export const getRetryTenderSourceExtractionUrl = (id: string,
    sourceId: string,) => {




  return `/api/tenders/${id}/sources/${sourceId}/retry-extraction`
}

/**
 * @summary Retry bounded extraction from an available collateral revision
 */
export const retryTenderSourceExtraction = async (id: string,
    sourceId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenderSource> => {

  return customFetch<TenderSource>(getRetryTenderSourceExtractionUrl(id,sourceId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRetryTenderSourceExtractionMutationKey = () => ['retryTenderSourceExtraction'] as const;

export const getRetryTenderSourceExtractionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryTenderSourceExtraction>>, TError,RetryTenderSourceExtractionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof retryTenderSourceExtraction>>, TError,RetryTenderSourceExtractionMutationVariables, TContext> => {

const mutationKey = getRetryTenderSourceExtractionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof retryTenderSourceExtraction>>, RetryTenderSourceExtractionMutationVariables> = (props) => {
          const {id,sourceId} = props ?? {};

          return  retryTenderSourceExtraction(id,sourceId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RetryTenderSourceExtractionMutationResult = NonNullable<Awaited<ReturnType<typeof retryTenderSourceExtraction>>>

    export type RetryTenderSourceExtractionMutationError = unknown
    export type RetryTenderSourceExtractionMutationVariables = {id: string;sourceId: string}

    /**
 * @summary Retry bounded extraction from an available collateral revision
 */
export const useRetryTenderSourceExtraction = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryTenderSourceExtraction>>, TError,RetryTenderSourceExtractionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof retryTenderSourceExtraction>>,
        TError,
        RetryTenderSourceExtractionMutationVariables,
        TContext
      > => {
      return useMutation(getRetryTenderSourceExtractionMutationOptions(options));
    }

export const getInspectTenderBuyerTemplateUrl = (id: string,
    sourceId: string,) => {




  return `/api/tenders/${id}/buyer-templates/${sourceId}/inspection`
}

/**
 * @summary Inspect safe answer targets in one current buyer DOCX template
 */
export const inspectTenderBuyerTemplate = async (id: string,
    sourceId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenderBuyerTemplateInspection> => {

  return customFetch<TenderBuyerTemplateInspection>(getInspectTenderBuyerTemplateUrl(id,sourceId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getInspectTenderBuyerTemplateQueryKey = (id: string,
    sourceId: string,) => {
    return [
    `/api/tenders/${id}/buyer-templates/${sourceId}/inspection`
    ] as const;
    }


export const getInspectTenderBuyerTemplateQueryOptions = <TData = Awaited<ReturnType<typeof inspectTenderBuyerTemplate>>, TError = Error>(id: string,
    sourceId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof inspectTenderBuyerTemplate>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getInspectTenderBuyerTemplateQueryKey(id,sourceId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof inspectTenderBuyerTemplate>>> = ({ signal }) => inspectTenderBuyerTemplate(id,sourceId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && sourceId !== null && sourceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof inspectTenderBuyerTemplate>>, TError, TData> & { queryKey: QueryKey }
}

export type InspectTenderBuyerTemplateQueryResult = NonNullable<Awaited<ReturnType<typeof inspectTenderBuyerTemplate>>>
export type InspectTenderBuyerTemplateQueryError = Error


/**
 * @summary Inspect safe answer targets in one current buyer DOCX template
 */

export function useInspectTenderBuyerTemplate<TData = Awaited<ReturnType<typeof inspectTenderBuyerTemplate>>, TError = Error>(
 id: string,
    sourceId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof inspectTenderBuyerTemplate>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getInspectTenderBuyerTemplateQueryOptions(id,sourceId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConfirmTenderBuyerTemplateMappingUrl = (id: string,
    sourceId: string,) => {




  return `/api/tenders/${id}/buyer-templates/${sourceId}/mapping`
}

/**
 * @summary Confirm requirement answers for unique buyer-template targets
 */
export const confirmTenderBuyerTemplateMapping = async (id: string,
    sourceId: string,
    tenderBuyerTemplateMappingConfirm: TenderBuyerTemplateMappingConfirm, options?: Parameters<typeof customFetch>[1]): Promise<TenderBuyerTemplateInspection> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderBuyerTemplateInspection>(getConfirmTenderBuyerTemplateMappingUrl(id,sourceId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderBuyerTemplateMappingConfirm)
  }
);}





export const getConfirmTenderBuyerTemplateMappingMutationKey = () => ['confirmTenderBuyerTemplateMapping'] as const;

export const getConfirmTenderBuyerTemplateMappingMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmTenderBuyerTemplateMapping>>, TError,ConfirmTenderBuyerTemplateMappingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmTenderBuyerTemplateMapping>>, TError,ConfirmTenderBuyerTemplateMappingMutationVariables, TContext> => {

const mutationKey = getConfirmTenderBuyerTemplateMappingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmTenderBuyerTemplateMapping>>, ConfirmTenderBuyerTemplateMappingMutationVariables> = (props) => {
          const {id,sourceId,data} = props ?? {};

          return  confirmTenderBuyerTemplateMapping(id,sourceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmTenderBuyerTemplateMappingMutationResult = NonNullable<Awaited<ReturnType<typeof confirmTenderBuyerTemplateMapping>>>
    export type ConfirmTenderBuyerTemplateMappingMutationBody = TenderBuyerTemplateMappingConfirm
    export type ConfirmTenderBuyerTemplateMappingMutationError = Error
    export type ConfirmTenderBuyerTemplateMappingMutationVariables = {id: string;sourceId: string;data: TenderBuyerTemplateMappingConfirm}

    /**
 * @summary Confirm requirement answers for unique buyer-template targets
 */
export const useConfirmTenderBuyerTemplateMapping = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmTenderBuyerTemplateMapping>>, TError,ConfirmTenderBuyerTemplateMappingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmTenderBuyerTemplateMapping>>,
        TError,
        ConfirmTenderBuyerTemplateMappingMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmTenderBuyerTemplateMappingMutationOptions(options));
    }

export const getListTenderResponseSectionsUrl = (id: string,
    params?: ListTenderResponseSectionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/sections?${stringifiedParams}` : `/api/tenders/${id}/sections`
}

/**
 * @summary List ordered tender response sections
 */
export const listTenderResponseSections = async (id: string,
    params?: ListTenderResponseSectionsParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderResponseSectionList> => {

  return customFetch<TenderResponseSectionList>(getListTenderResponseSectionsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderResponseSectionsQueryKey = (id: string,
    params?: ListTenderResponseSectionsParams,) => {
    return [
    `/api/tenders/${id}/sections`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderResponseSectionsQueryOptions = <TData = Awaited<ReturnType<typeof listTenderResponseSections>>, TError = unknown>(id: string,
    params?: ListTenderResponseSectionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderResponseSections>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderResponseSectionsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderResponseSections>>> = ({ signal }) => listTenderResponseSections(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderResponseSections>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderResponseSectionsQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderResponseSections>>>
export type ListTenderResponseSectionsQueryError = unknown


/**
 * @summary List ordered tender response sections
 */

export function useListTenderResponseSections<TData = Awaited<ReturnType<typeof listTenderResponseSections>>, TError = unknown>(
 id: string,
    params?: ListTenderResponseSectionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderResponseSections>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderResponseSectionsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateTenderResponseSectionUrl = (id: string,) => {




  return `/api/tenders/${id}/sections`
}

/**
 * @summary Create a tender response section
 */
export const createTenderResponseSection = async (id: string,
    tenderResponseSectionCreate: TenderResponseSectionCreate, options?: Parameters<typeof customFetch>[1]): Promise<TenderResponseSection> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderResponseSection>(getCreateTenderResponseSectionUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderResponseSectionCreate)
  }
);}





export const getCreateTenderResponseSectionMutationKey = () => ['createTenderResponseSection'] as const;

export const getCreateTenderResponseSectionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenderResponseSection>>, TError,CreateTenderResponseSectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTenderResponseSection>>, TError,CreateTenderResponseSectionMutationVariables, TContext> => {

const mutationKey = getCreateTenderResponseSectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTenderResponseSection>>, CreateTenderResponseSectionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createTenderResponseSection(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTenderResponseSectionMutationResult = NonNullable<Awaited<ReturnType<typeof createTenderResponseSection>>>
    export type CreateTenderResponseSectionMutationBody = TenderResponseSectionCreate
    export type CreateTenderResponseSectionMutationError = unknown
    export type CreateTenderResponseSectionMutationVariables = {id: string;data: TenderResponseSectionCreate}

    /**
 * @summary Create a tender response section
 */
export const useCreateTenderResponseSection = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenderResponseSection>>, TError,CreateTenderResponseSectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createTenderResponseSection>>,
        TError,
        CreateTenderResponseSectionMutationVariables,
        TContext
      > => {
      return useMutation(getCreateTenderResponseSectionMutationOptions(options));
    }

export const getReorderTenderResponseSectionsUrl = (id: string,) => {




  return `/api/tenders/${id}/sections/reorder`
}

/**
 * @summary Reorder every response section using stable identifiers
 */
export const reorderTenderResponseSections = async (id: string,
    tenderOrderedIds: TenderOrderedIds, options?: Parameters<typeof customFetch>[1]): Promise<TenderResponseSectionList> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderResponseSectionList>(getReorderTenderResponseSectionsUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderOrderedIds)
  }
);}





export const getReorderTenderResponseSectionsMutationKey = () => ['reorderTenderResponseSections'] as const;

export const getReorderTenderResponseSectionsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reorderTenderResponseSections>>, TError,ReorderTenderResponseSectionsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reorderTenderResponseSections>>, TError,ReorderTenderResponseSectionsMutationVariables, TContext> => {

const mutationKey = getReorderTenderResponseSectionsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reorderTenderResponseSections>>, ReorderTenderResponseSectionsMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reorderTenderResponseSections(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReorderTenderResponseSectionsMutationResult = NonNullable<Awaited<ReturnType<typeof reorderTenderResponseSections>>>
    export type ReorderTenderResponseSectionsMutationBody = TenderOrderedIds
    export type ReorderTenderResponseSectionsMutationError = unknown
    export type ReorderTenderResponseSectionsMutationVariables = {id: string;data: TenderOrderedIds}

    /**
 * @summary Reorder every response section using stable identifiers
 */
export const useReorderTenderResponseSections = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reorderTenderResponseSections>>, TError,ReorderTenderResponseSectionsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reorderTenderResponseSections>>,
        TError,
        ReorderTenderResponseSectionsMutationVariables,
        TContext
      > => {
      return useMutation(getReorderTenderResponseSectionsMutationOptions(options));
    }

export const getUpdateTenderResponseSectionUrl = (id: string,
    sectionId: string,) => {




  return `/api/tenders/${id}/sections/${sectionId}`
}

/**
 * @summary Update a response section with optimistic concurrency
 */
export const updateTenderResponseSection = async (id: string,
    sectionId: string,
    tenderResponseSectionUpdate: TenderResponseSectionUpdate, options?: Parameters<typeof customFetch>[1]): Promise<TenderResponseSection> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderResponseSection>(getUpdateTenderResponseSectionUrl(id,sectionId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderResponseSectionUpdate)
  }
);}





export const getUpdateTenderResponseSectionMutationKey = () => ['updateTenderResponseSection'] as const;

export const getUpdateTenderResponseSectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenderResponseSection>>, TError,UpdateTenderResponseSectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateTenderResponseSection>>, TError,UpdateTenderResponseSectionMutationVariables, TContext> => {

const mutationKey = getUpdateTenderResponseSectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateTenderResponseSection>>, UpdateTenderResponseSectionMutationVariables> = (props) => {
          const {id,sectionId,data} = props ?? {};

          return  updateTenderResponseSection(id,sectionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateTenderResponseSectionMutationResult = NonNullable<Awaited<ReturnType<typeof updateTenderResponseSection>>>
    export type UpdateTenderResponseSectionMutationBody = TenderResponseSectionUpdate
    export type UpdateTenderResponseSectionMutationError = Error
    export type UpdateTenderResponseSectionMutationVariables = {id: string;sectionId: string;data: TenderResponseSectionUpdate}

    /**
 * @summary Update a response section with optimistic concurrency
 */
export const useUpdateTenderResponseSection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenderResponseSection>>, TError,UpdateTenderResponseSectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateTenderResponseSection>>,
        TError,
        UpdateTenderResponseSectionMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateTenderResponseSectionMutationOptions(options));
    }

export const getDeleteTenderResponseSectionUrl = (id: string,
    sectionId: string,) => {




  return `/api/tenders/${id}/sections/${sectionId}`
}

/**
 * @summary Delete an unmapped response section
 */
export const deleteTenderResponseSection = async (id: string,
    sectionId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteTenderResponseSectionUrl(id,sectionId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteTenderResponseSectionMutationKey = () => ['deleteTenderResponseSection'] as const;

export const getDeleteTenderResponseSectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTenderResponseSection>>, TError,DeleteTenderResponseSectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteTenderResponseSection>>, TError,DeleteTenderResponseSectionMutationVariables, TContext> => {

const mutationKey = getDeleteTenderResponseSectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteTenderResponseSection>>, DeleteTenderResponseSectionMutationVariables> = (props) => {
          const {id,sectionId} = props ?? {};

          return  deleteTenderResponseSection(id,sectionId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteTenderResponseSectionMutationResult = NonNullable<Awaited<ReturnType<typeof deleteTenderResponseSection>>>

    export type DeleteTenderResponseSectionMutationError = Error
    export type DeleteTenderResponseSectionMutationVariables = {id: string;sectionId: string}

    /**
 * @summary Delete an unmapped response section
 */
export const useDeleteTenderResponseSection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTenderResponseSection>>, TError,DeleteTenderResponseSectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteTenderResponseSection>>,
        TError,
        DeleteTenderResponseSectionMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteTenderResponseSectionMutationOptions(options));
    }

export const getListTenderRequirementsUrl = (id: string,
    params?: ListTenderRequirementsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/requirements?${stringifiedParams}` : `/api/tenders/${id}/requirements`
}

/**
 * @summary List the ordered requirement-to-answer matrix
 */
export const listTenderRequirements = async (id: string,
    params?: ListTenderRequirementsParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderRequirementList> => {

  return customFetch<TenderRequirementList>(getListTenderRequirementsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderRequirementsQueryKey = (id: string,
    params?: ListTenderRequirementsParams,) => {
    return [
    `/api/tenders/${id}/requirements`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderRequirementsQueryOptions = <TData = Awaited<ReturnType<typeof listTenderRequirements>>, TError = unknown>(id: string,
    params?: ListTenderRequirementsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderRequirements>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderRequirementsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderRequirements>>> = ({ signal }) => listTenderRequirements(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderRequirements>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderRequirementsQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderRequirements>>>
export type ListTenderRequirementsQueryError = unknown


/**
 * @summary List the ordered requirement-to-answer matrix
 */

export function useListTenderRequirements<TData = Awaited<ReturnType<typeof listTenderRequirements>>, TError = unknown>(
 id: string,
    params?: ListTenderRequirementsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderRequirements>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderRequirementsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateTenderRequirementUrl = (id: string,) => {




  return `/api/tenders/${id}/requirements`
}

/**
 * @summary Add a manual or source-backed tender requirement
 */
export const createTenderRequirement = async (id: string,
    tenderRequirementCreate: TenderRequirementCreate, options?: Parameters<typeof customFetch>[1]): Promise<TenderRequirementContract> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderRequirementContract>(getCreateTenderRequirementUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderRequirementCreate)
  }
);}





export const getCreateTenderRequirementMutationKey = () => ['createTenderRequirement'] as const;

export const getCreateTenderRequirementMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenderRequirement>>, TError,CreateTenderRequirementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTenderRequirement>>, TError,CreateTenderRequirementMutationVariables, TContext> => {

const mutationKey = getCreateTenderRequirementMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTenderRequirement>>, CreateTenderRequirementMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createTenderRequirement(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTenderRequirementMutationResult = NonNullable<Awaited<ReturnType<typeof createTenderRequirement>>>
    export type CreateTenderRequirementMutationBody = TenderRequirementCreate
    export type CreateTenderRequirementMutationError = unknown
    export type CreateTenderRequirementMutationVariables = {id: string;data: TenderRequirementCreate}

    /**
 * @summary Add a manual or source-backed tender requirement
 */
export const useCreateTenderRequirement = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenderRequirement>>, TError,CreateTenderRequirementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createTenderRequirement>>,
        TError,
        CreateTenderRequirementMutationVariables,
        TContext
      > => {
      return useMutation(getCreateTenderRequirementMutationOptions(options));
    }

export const getReorderTenderRequirementsUrl = (id: string,) => {




  return `/api/tenders/${id}/requirements/reorder`
}

/**
 * @summary Reorder every requirement using stable identifiers
 */
export const reorderTenderRequirements = async (id: string,
    tenderOrderedIds: TenderOrderedIds, options?: Parameters<typeof customFetch>[1]): Promise<TenderRequirementList> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderRequirementList>(getReorderTenderRequirementsUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderOrderedIds)
  }
);}





export const getReorderTenderRequirementsMutationKey = () => ['reorderTenderRequirements'] as const;

export const getReorderTenderRequirementsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reorderTenderRequirements>>, TError,ReorderTenderRequirementsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reorderTenderRequirements>>, TError,ReorderTenderRequirementsMutationVariables, TContext> => {

const mutationKey = getReorderTenderRequirementsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reorderTenderRequirements>>, ReorderTenderRequirementsMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reorderTenderRequirements(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReorderTenderRequirementsMutationResult = NonNullable<Awaited<ReturnType<typeof reorderTenderRequirements>>>
    export type ReorderTenderRequirementsMutationBody = TenderOrderedIds
    export type ReorderTenderRequirementsMutationError = unknown
    export type ReorderTenderRequirementsMutationVariables = {id: string;data: TenderOrderedIds}

    /**
 * @summary Reorder every requirement using stable identifiers
 */
export const useReorderTenderRequirements = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reorderTenderRequirements>>, TError,ReorderTenderRequirementsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reorderTenderRequirements>>,
        TError,
        ReorderTenderRequirementsMutationVariables,
        TContext
      > => {
      return useMutation(getReorderTenderRequirementsMutationOptions(options));
    }

export const getUpdateTenderRequirementUrl = (id: string,
    requirementId: string,) => {




  return `/api/tenders/${id}/requirements/${requirementId}`
}

/**
 * @summary Correct or map a requirement with optimistic concurrency
 */
export const updateTenderRequirement = async (id: string,
    requirementId: string,
    tenderRequirementUpdate: TenderRequirementUpdate, options?: Parameters<typeof customFetch>[1]): Promise<TenderRequirementContract> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderRequirementContract>(getUpdateTenderRequirementUrl(id,requirementId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderRequirementUpdate)
  }
);}





export const getUpdateTenderRequirementMutationKey = () => ['updateTenderRequirement'] as const;

export const getUpdateTenderRequirementMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenderRequirement>>, TError,UpdateTenderRequirementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateTenderRequirement>>, TError,UpdateTenderRequirementMutationVariables, TContext> => {

const mutationKey = getUpdateTenderRequirementMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateTenderRequirement>>, UpdateTenderRequirementMutationVariables> = (props) => {
          const {id,requirementId,data} = props ?? {};

          return  updateTenderRequirement(id,requirementId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateTenderRequirementMutationResult = NonNullable<Awaited<ReturnType<typeof updateTenderRequirement>>>
    export type UpdateTenderRequirementMutationBody = TenderRequirementUpdate
    export type UpdateTenderRequirementMutationError = Error
    export type UpdateTenderRequirementMutationVariables = {id: string;requirementId: string;data: TenderRequirementUpdate}

    /**
 * @summary Correct or map a requirement with optimistic concurrency
 */
export const useUpdateTenderRequirement = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTenderRequirement>>, TError,UpdateTenderRequirementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateTenderRequirement>>,
        TError,
        UpdateTenderRequirementMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateTenderRequirementMutationOptions(options));
    }

export const getDeleteTenderRequirementUrl = (id: string,
    requirementId: string,) => {




  return `/api/tenders/${id}/requirements/${requirementId}`
}

/**
 * @summary Delete a requirement and its requirement-scoped evidence mappings
 */
export const deleteTenderRequirement = async (id: string,
    requirementId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteTenderRequirementUrl(id,requirementId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteTenderRequirementMutationKey = () => ['deleteTenderRequirement'] as const;

export const getDeleteTenderRequirementMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTenderRequirement>>, TError,DeleteTenderRequirementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteTenderRequirement>>, TError,DeleteTenderRequirementMutationVariables, TContext> => {

const mutationKey = getDeleteTenderRequirementMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteTenderRequirement>>, DeleteTenderRequirementMutationVariables> = (props) => {
          const {id,requirementId} = props ?? {};

          return  deleteTenderRequirement(id,requirementId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteTenderRequirementMutationResult = NonNullable<Awaited<ReturnType<typeof deleteTenderRequirement>>>

    export type DeleteTenderRequirementMutationError = unknown
    export type DeleteTenderRequirementMutationVariables = {id: string;requirementId: string}

    /**
 * @summary Delete a requirement and its requirement-scoped evidence mappings
 */
export const useDeleteTenderRequirement = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTenderRequirement>>, TError,DeleteTenderRequirementMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteTenderRequirement>>,
        TError,
        DeleteTenderRequirementMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteTenderRequirementMutationOptions(options));
    }

export const getSearchTenderEvidenceUrl = (id: string,
    params: SearchTenderEvidenceParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/evidence/search?${stringifiedParams}` : `/api/tenders/${id}/evidence/search`
}

/**
 * @summary Discover traceable tender-private and authorized entity evidence
 */
export const searchTenderEvidence = async (id: string,
    params: SearchTenderEvidenceParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderEvidenceSearchResult> => {

  return customFetch<TenderEvidenceSearchResult>(getSearchTenderEvidenceUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getSearchTenderEvidenceQueryKey = (id: string,
    params?: SearchTenderEvidenceParams,) => {
    return [
    `/api/tenders/${id}/evidence/search`, ...(params ? [params] : [])
    ] as const;
    }


export const getSearchTenderEvidenceQueryOptions = <TData = Awaited<ReturnType<typeof searchTenderEvidence>>, TError = unknown>(id: string,
    params: SearchTenderEvidenceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof searchTenderEvidence>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getSearchTenderEvidenceQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof searchTenderEvidence>>> = ({ signal }) => searchTenderEvidence(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof searchTenderEvidence>>, TError, TData> & { queryKey: QueryKey }
}

export type SearchTenderEvidenceQueryResult = NonNullable<Awaited<ReturnType<typeof searchTenderEvidence>>>
export type SearchTenderEvidenceQueryError = unknown


/**
 * @summary Discover traceable tender-private and authorized entity evidence
 */

export function useSearchTenderEvidence<TData = Awaited<ReturnType<typeof searchTenderEvidence>>, TError = unknown>(
 id: string,
    params: SearchTenderEvidenceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof searchTenderEvidence>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getSearchTenderEvidenceQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListTenderEvidenceUrl = (id: string,
    params?: ListTenderEvidenceParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/evidence?${stringifiedParams}` : `/api/tenders/${id}/evidence`
}

/**
 * @summary List selected evidence with current validation state
 */
export const listTenderEvidence = async (id: string,
    params?: ListTenderEvidenceParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderEvidenceSelectionList> => {

  return customFetch<TenderEvidenceSelectionList>(getListTenderEvidenceUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderEvidenceQueryKey = (id: string,
    params?: ListTenderEvidenceParams,) => {
    return [
    `/api/tenders/${id}/evidence`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderEvidenceQueryOptions = <TData = Awaited<ReturnType<typeof listTenderEvidence>>, TError = unknown>(id: string,
    params?: ListTenderEvidenceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderEvidence>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderEvidenceQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderEvidence>>> = ({ signal }) => listTenderEvidence(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderEvidence>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderEvidenceQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderEvidence>>>
export type ListTenderEvidenceQueryError = unknown


/**
 * @summary List selected evidence with current validation state
 */

export function useListTenderEvidence<TData = Awaited<ReturnType<typeof listTenderEvidence>>, TError = unknown>(
 id: string,
    params?: ListTenderEvidenceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderEvidence>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderEvidenceQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSelectTenderEvidenceUrl = (id: string,) => {




  return `/api/tenders/${id}/evidence`
}

/**
 * @summary Pin one exact evidence revision and excerpt
 */
export const selectTenderEvidence = async (id: string,
    tenderEvidenceSelectionCreate: TenderEvidenceSelectionCreate, options?: Parameters<typeof customFetch>[1]): Promise<TenderEvidenceSelection> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderEvidenceSelection>(getSelectTenderEvidenceUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderEvidenceSelectionCreate)
  }
);}





export const getSelectTenderEvidenceMutationKey = () => ['selectTenderEvidence'] as const;

export const getSelectTenderEvidenceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof selectTenderEvidence>>, TError,SelectTenderEvidenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof selectTenderEvidence>>, TError,SelectTenderEvidenceMutationVariables, TContext> => {

const mutationKey = getSelectTenderEvidenceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof selectTenderEvidence>>, SelectTenderEvidenceMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  selectTenderEvidence(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SelectTenderEvidenceMutationResult = NonNullable<Awaited<ReturnType<typeof selectTenderEvidence>>>
    export type SelectTenderEvidenceMutationBody = TenderEvidenceSelectionCreate
    export type SelectTenderEvidenceMutationError = Error
    export type SelectTenderEvidenceMutationVariables = {id: string;data: TenderEvidenceSelectionCreate}

    /**
 * @summary Pin one exact evidence revision and excerpt
 */
export const useSelectTenderEvidence = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof selectTenderEvidence>>, TError,SelectTenderEvidenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof selectTenderEvidence>>,
        TError,
        SelectTenderEvidenceMutationVariables,
        TContext
      > => {
      return useMutation(getSelectTenderEvidenceMutationOptions(options));
    }

export const getDeleteTenderEvidenceUrl = (id: string,
    evidenceId: string,) => {




  return `/api/tenders/${id}/evidence/${evidenceId}`
}

/**
 * @summary Remove one selected evidence mapping
 */
export const deleteTenderEvidence = async (id: string,
    evidenceId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteTenderEvidenceUrl(id,evidenceId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteTenderEvidenceMutationKey = () => ['deleteTenderEvidence'] as const;

export const getDeleteTenderEvidenceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTenderEvidence>>, TError,DeleteTenderEvidenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteTenderEvidence>>, TError,DeleteTenderEvidenceMutationVariables, TContext> => {

const mutationKey = getDeleteTenderEvidenceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteTenderEvidence>>, DeleteTenderEvidenceMutationVariables> = (props) => {
          const {id,evidenceId} = props ?? {};

          return  deleteTenderEvidence(id,evidenceId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteTenderEvidenceMutationResult = NonNullable<Awaited<ReturnType<typeof deleteTenderEvidence>>>

    export type DeleteTenderEvidenceMutationError = unknown
    export type DeleteTenderEvidenceMutationVariables = {id: string;evidenceId: string}

    /**
 * @summary Remove one selected evidence mapping
 */
export const useDeleteTenderEvidence = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTenderEvidence>>, TError,DeleteTenderEvidenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteTenderEvidence>>,
        TError,
        DeleteTenderEvidenceMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteTenderEvidenceMutationOptions(options));
    }

export const getGetTenderCoverageUrl = (id: string,) => {




  return `/api/tenders/${id}/coverage`
}

/**
 * @summary Evaluate deterministic requirement coverage and constraints
 */
export const getTenderCoverage = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<TenderCoverageReport> => {

  return customFetch<TenderCoverageReport>(getGetTenderCoverageUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenderCoverageQueryKey = (id: string,) => {
    return [
    `/api/tenders/${id}/coverage`
    ] as const;
    }


export const getGetTenderCoverageQueryOptions = <TData = Awaited<ReturnType<typeof getTenderCoverage>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderCoverage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenderCoverageQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenderCoverage>>> = ({ signal }) => getTenderCoverage(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenderCoverage>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTenderCoverageQueryResult = NonNullable<Awaited<ReturnType<typeof getTenderCoverage>>>
export type GetTenderCoverageQueryError = unknown


/**
 * @summary Evaluate deterministic requirement coverage and constraints
 */

export function useGetTenderCoverage<TData = Awaited<ReturnType<typeof getTenderCoverage>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderCoverage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTenderCoverageQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListTenderAiRunsUrl = (id: string,
    params?: ListTenderAiRunsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/ai-runs?${stringifiedParams}` : `/api/tenders/${id}/ai-runs`
}

/**
 * @summary List durable AI runs for a tender
 */
export const listTenderAiRuns = async (id: string,
    params?: ListTenderAiRunsParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderAiRunList> => {

  return customFetch<TenderAiRunList>(getListTenderAiRunsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderAiRunsQueryKey = (id: string,
    params?: ListTenderAiRunsParams,) => {
    return [
    `/api/tenders/${id}/ai-runs`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderAiRunsQueryOptions = <TData = Awaited<ReturnType<typeof listTenderAiRuns>>, TError = unknown>(id: string,
    params?: ListTenderAiRunsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderAiRuns>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderAiRunsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderAiRuns>>> = ({ signal }) => listTenderAiRuns(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderAiRuns>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderAiRunsQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderAiRuns>>>
export type ListTenderAiRunsQueryError = unknown


/**
 * @summary List durable AI runs for a tender
 */

export function useListTenderAiRuns<TData = Awaited<ReturnType<typeof listTenderAiRuns>>, TError = unknown>(
 id: string,
    params?: ListTenderAiRunsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderAiRuns>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderAiRunsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateTenderAiRunUrl = (id: string,) => {




  return `/api/tenders/${id}/ai-runs`
}

/**
 * @summary Queue a source-grounded tender AI proposal run
 */
export const createTenderAiRun = async (id: string,
    tenderAiRunCreate: TenderAiRunCreate, options?: Parameters<typeof customFetch>[1]): Promise<TenderAiRunContract> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderAiRunContract>(getCreateTenderAiRunUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderAiRunCreate)
  }
);}





export const getCreateTenderAiRunMutationKey = () => ['createTenderAiRun'] as const;

export const getCreateTenderAiRunMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenderAiRun>>, TError,CreateTenderAiRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTenderAiRun>>, TError,CreateTenderAiRunMutationVariables, TContext> => {

const mutationKey = getCreateTenderAiRunMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTenderAiRun>>, CreateTenderAiRunMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createTenderAiRun(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTenderAiRunMutationResult = NonNullable<Awaited<ReturnType<typeof createTenderAiRun>>>
    export type CreateTenderAiRunMutationBody = TenderAiRunCreate
    export type CreateTenderAiRunMutationError = unknown
    export type CreateTenderAiRunMutationVariables = {id: string;data: TenderAiRunCreate}

    /**
 * @summary Queue a source-grounded tender AI proposal run
 */
export const useCreateTenderAiRun = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenderAiRun>>, TError,CreateTenderAiRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createTenderAiRun>>,
        TError,
        CreateTenderAiRunMutationVariables,
        TContext
      > => {
      return useMutation(getCreateTenderAiRunMutationOptions(options));
    }

export const getGetTenderAiRunUrl = (id: string,
    runId: string,) => {




  return `/api/tenders/${id}/ai-runs/${runId}`
}

/**
 * @summary Get durable AI run progress and provenance
 */
export const getTenderAiRun = async (id: string,
    runId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenderAiRunContract> => {

  return customFetch<TenderAiRunContract>(getGetTenderAiRunUrl(id,runId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenderAiRunQueryKey = (id: string,
    runId: string,) => {
    return [
    `/api/tenders/${id}/ai-runs/${runId}`
    ] as const;
    }


export const getGetTenderAiRunQueryOptions = <TData = Awaited<ReturnType<typeof getTenderAiRun>>, TError = unknown>(id: string,
    runId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderAiRun>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenderAiRunQueryKey(id,runId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenderAiRun>>> = ({ signal }) => getTenderAiRun(id,runId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && runId !== null && runId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenderAiRun>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTenderAiRunQueryResult = NonNullable<Awaited<ReturnType<typeof getTenderAiRun>>>
export type GetTenderAiRunQueryError = unknown


/**
 * @summary Get durable AI run progress and provenance
 */

export function useGetTenderAiRun<TData = Awaited<ReturnType<typeof getTenderAiRun>>, TError = unknown>(
 id: string,
    runId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderAiRun>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTenderAiRunQueryOptions(id,runId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCancelTenderAiRunUrl = (id: string,
    runId: string,) => {




  return `/api/tenders/${id}/ai-runs/${runId}/cancel`
}

/**
 * @summary Request cancellation without discarding saved human work
 */
export const cancelTenderAiRun = async (id: string,
    runId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenderAiRunContract> => {

  return customFetch<TenderAiRunContract>(getCancelTenderAiRunUrl(id,runId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelTenderAiRunMutationKey = () => ['cancelTenderAiRun'] as const;

export const getCancelTenderAiRunMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelTenderAiRun>>, TError,CancelTenderAiRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelTenderAiRun>>, TError,CancelTenderAiRunMutationVariables, TContext> => {

const mutationKey = getCancelTenderAiRunMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelTenderAiRun>>, CancelTenderAiRunMutationVariables> = (props) => {
          const {id,runId} = props ?? {};

          return  cancelTenderAiRun(id,runId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelTenderAiRunMutationResult = NonNullable<Awaited<ReturnType<typeof cancelTenderAiRun>>>

    export type CancelTenderAiRunMutationError = unknown
    export type CancelTenderAiRunMutationVariables = {id: string;runId: string}

    /**
 * @summary Request cancellation without discarding saved human work
 */
export const useCancelTenderAiRun = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelTenderAiRun>>, TError,CancelTenderAiRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelTenderAiRun>>,
        TError,
        CancelTenderAiRunMutationVariables,
        TContext
      > => {
      return useMutation(getCancelTenderAiRunMutationOptions(options));
    }

export const getRetryTenderAiRunUrl = (id: string,
    runId: string,) => {




  return `/api/tenders/${id}/ai-runs/${runId}/retry`
}

/**
 * @summary Requeue a recoverable failed AI run within its retry limit
 */
export const retryTenderAiRun = async (id: string,
    runId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenderAiRunContract> => {

  return customFetch<TenderAiRunContract>(getRetryTenderAiRunUrl(id,runId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRetryTenderAiRunMutationKey = () => ['retryTenderAiRun'] as const;

export const getRetryTenderAiRunMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryTenderAiRun>>, TError,RetryTenderAiRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof retryTenderAiRun>>, TError,RetryTenderAiRunMutationVariables, TContext> => {

const mutationKey = getRetryTenderAiRunMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof retryTenderAiRun>>, RetryTenderAiRunMutationVariables> = (props) => {
          const {id,runId} = props ?? {};

          return  retryTenderAiRun(id,runId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RetryTenderAiRunMutationResult = NonNullable<Awaited<ReturnType<typeof retryTenderAiRun>>>

    export type RetryTenderAiRunMutationError = unknown
    export type RetryTenderAiRunMutationVariables = {id: string;runId: string}

    /**
 * @summary Requeue a recoverable failed AI run within its retry limit
 */
export const useRetryTenderAiRun = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryTenderAiRun>>, TError,RetryTenderAiRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof retryTenderAiRun>>,
        TError,
        RetryTenderAiRunMutationVariables,
        TContext
      > => {
      return useMutation(getRetryTenderAiRunMutationOptions(options));
    }

export const getListTenderProposalsUrl = (id: string,
    params?: ListTenderProposalsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/proposals?${stringifiedParams}` : `/api/tenders/${id}/proposals`
}

/**
 * @summary List immutable AI proposals awaiting an author decision
 */
export const listTenderProposals = async (id: string,
    params?: ListTenderProposalsParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderProposalList> => {

  return customFetch<TenderProposalList>(getListTenderProposalsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderProposalsQueryKey = (id: string,
    params?: ListTenderProposalsParams,) => {
    return [
    `/api/tenders/${id}/proposals`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderProposalsQueryOptions = <TData = Awaited<ReturnType<typeof listTenderProposals>>, TError = unknown>(id: string,
    params?: ListTenderProposalsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderProposals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderProposalsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderProposals>>> = ({ signal }) => listTenderProposals(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderProposals>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderProposalsQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderProposals>>>
export type ListTenderProposalsQueryError = unknown


/**
 * @summary List immutable AI proposals awaiting an author decision
 */

export function useListTenderProposals<TData = Awaited<ReturnType<typeof listTenderProposals>>, TError = unknown>(
 id: string,
    params?: ListTenderProposalsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderProposals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderProposalsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDecideTenderProposalUrl = (id: string,
    proposalId: string,) => {




  return `/api/tenders/${id}/proposals/${proposalId}/decision`
}

/**
 * @summary Accept or reject a proposal against its exact base revision
 */
export const decideTenderProposal = async (id: string,
    proposalId: string,
    tenderProposalDecision: TenderProposalDecision, options?: Parameters<typeof customFetch>[1]): Promise<TenderProposal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderProposal>(getDecideTenderProposalUrl(id,proposalId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderProposalDecision)
  }
);}





export const getDecideTenderProposalMutationKey = () => ['decideTenderProposal'] as const;

export const getDecideTenderProposalMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideTenderProposal>>, TError,DecideTenderProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof decideTenderProposal>>, TError,DecideTenderProposalMutationVariables, TContext> => {

const mutationKey = getDecideTenderProposalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof decideTenderProposal>>, DecideTenderProposalMutationVariables> = (props) => {
          const {id,proposalId,data} = props ?? {};

          return  decideTenderProposal(id,proposalId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DecideTenderProposalMutationResult = NonNullable<Awaited<ReturnType<typeof decideTenderProposal>>>
    export type DecideTenderProposalMutationBody = TenderProposalDecision
    export type DecideTenderProposalMutationError = Error
    export type DecideTenderProposalMutationVariables = {id: string;proposalId: string;data: TenderProposalDecision}

    /**
 * @summary Accept or reject a proposal against its exact base revision
 */
export const useDecideTenderProposal = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideTenderProposal>>, TError,DecideTenderProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof decideTenderProposal>>,
        TError,
        DecideTenderProposalMutationVariables,
        TContext
      > => {
      return useMutation(getDecideTenderProposalMutationOptions(options));
    }

export const getListTenderPackagesUrl = (id: string,
    params?: ListTenderPackagesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/packages?${stringifiedParams}` : `/api/tenders/${id}/packages`
}

/**
 * @summary List immutable response-package checkpoints
 */
export const listTenderPackages = async (id: string,
    params?: ListTenderPackagesParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderPackageRevisionList> => {

  return customFetch<TenderPackageRevisionList>(getListTenderPackagesUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderPackagesQueryKey = (id: string,
    params?: ListTenderPackagesParams,) => {
    return [
    `/api/tenders/${id}/packages`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderPackagesQueryOptions = <TData = Awaited<ReturnType<typeof listTenderPackages>>, TError = unknown>(id: string,
    params?: ListTenderPackagesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderPackages>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderPackagesQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderPackages>>> = ({ signal }) => listTenderPackages(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderPackages>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderPackagesQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderPackages>>>
export type ListTenderPackagesQueryError = unknown


/**
 * @summary List immutable response-package checkpoints
 */

export function useListTenderPackages<TData = Awaited<ReturnType<typeof listTenderPackages>>, TError = unknown>(
 id: string,
    params?: ListTenderPackagesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderPackages>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderPackagesQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateTenderPackageUrl = (id: string,) => {




  return `/api/tenders/${id}/packages`
}

/**
 * @summary Freeze all output-affecting inputs into one immutable package revision
 */
export const createTenderPackage = async (id: string,
    tenderPackageCreate: TenderPackageCreate, options?: Parameters<typeof customFetch>[1]): Promise<TenderPackageRevision> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderPackageRevision>(getCreateTenderPackageUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderPackageCreate)
  }
);}





export const getCreateTenderPackageMutationKey = () => ['createTenderPackage'] as const;

export const getCreateTenderPackageMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenderPackage>>, TError,CreateTenderPackageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTenderPackage>>, TError,CreateTenderPackageMutationVariables, TContext> => {

const mutationKey = getCreateTenderPackageMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTenderPackage>>, CreateTenderPackageMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createTenderPackage(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTenderPackageMutationResult = NonNullable<Awaited<ReturnType<typeof createTenderPackage>>>
    export type CreateTenderPackageMutationBody = TenderPackageCreate
    export type CreateTenderPackageMutationError = unknown
    export type CreateTenderPackageMutationVariables = {id: string;data: TenderPackageCreate}

    /**
 * @summary Freeze all output-affecting inputs into one immutable package revision
 */
export const useCreateTenderPackage = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenderPackage>>, TError,CreateTenderPackageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createTenderPackage>>,
        TError,
        CreateTenderPackageMutationVariables,
        TContext
      > => {
      return useMutation(getCreateTenderPackageMutationOptions(options));
    }

export const getSubmitTenderPackageReviewUrl = (id: string,
    packageRevisionId: string,) => {




  return `/api/tenders/${id}/packages/${packageRevisionId}/reviews`
}

/**
 * @summary Submit an exact package checkpoint for administrator review
 */
export const submitTenderPackageReview = async (id: string,
    packageRevisionId: string,
    tenderReviewSubmit: TenderReviewSubmit, options?: Parameters<typeof customFetch>[1]): Promise<TenderReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderReview>(getSubmitTenderPackageReviewUrl(id,packageRevisionId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderReviewSubmit)
  }
);}





export const getSubmitTenderPackageReviewMutationKey = () => ['submitTenderPackageReview'] as const;

export const getSubmitTenderPackageReviewMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitTenderPackageReview>>, TError,SubmitTenderPackageReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof submitTenderPackageReview>>, TError,SubmitTenderPackageReviewMutationVariables, TContext> => {

const mutationKey = getSubmitTenderPackageReviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof submitTenderPackageReview>>, SubmitTenderPackageReviewMutationVariables> = (props) => {
          const {id,packageRevisionId,data} = props ?? {};

          return  submitTenderPackageReview(id,packageRevisionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SubmitTenderPackageReviewMutationResult = NonNullable<Awaited<ReturnType<typeof submitTenderPackageReview>>>
    export type SubmitTenderPackageReviewMutationBody = TenderReviewSubmit
    export type SubmitTenderPackageReviewMutationError = unknown
    export type SubmitTenderPackageReviewMutationVariables = {id: string;packageRevisionId: string;data: TenderReviewSubmit}

    /**
 * @summary Submit an exact package checkpoint for administrator review
 */
export const useSubmitTenderPackageReview = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitTenderPackageReview>>, TError,SubmitTenderPackageReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof submitTenderPackageReview>>,
        TError,
        SubmitTenderPackageReviewMutationVariables,
        TContext
      > => {
      return useMutation(getSubmitTenderPackageReviewMutationOptions(options));
    }

export const getCreateTenderPackageExportsUrl = (id: string,
    packageRevisionId: string,) => {




  return `/api/tenders/${id}/packages/${packageRevisionId}/exports`
}

/**
 * @summary Generate the selected Word outputs from one current immutable package checkpoint
 */
export const createTenderPackageExports = async (id: string,
    packageRevisionId: string,
    tenderExportCreate: TenderExportCreate, options?: Parameters<typeof customFetch>[1]): Promise<TenderExportResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderExportResult>(getCreateTenderPackageExportsUrl(id,packageRevisionId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderExportCreate)
  }
);}





export const getCreateTenderPackageExportsMutationKey = () => ['createTenderPackageExports'] as const;

export const getCreateTenderPackageExportsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenderPackageExports>>, TError,CreateTenderPackageExportsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTenderPackageExports>>, TError,CreateTenderPackageExportsMutationVariables, TContext> => {

const mutationKey = getCreateTenderPackageExportsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTenderPackageExports>>, CreateTenderPackageExportsMutationVariables> = (props) => {
          const {id,packageRevisionId,data} = props ?? {};

          return  createTenderPackageExports(id,packageRevisionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTenderPackageExportsMutationResult = NonNullable<Awaited<ReturnType<typeof createTenderPackageExports>>>
    export type CreateTenderPackageExportsMutationBody = TenderExportCreate
    export type CreateTenderPackageExportsMutationError = Error
    export type CreateTenderPackageExportsMutationVariables = {id: string;packageRevisionId: string;data: TenderExportCreate}

    /**
 * @summary Generate the selected Word outputs from one current immutable package checkpoint
 */
export const useCreateTenderPackageExports = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenderPackageExports>>, TError,CreateTenderPackageExportsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createTenderPackageExports>>,
        TError,
        CreateTenderPackageExportsMutationVariables,
        TContext
      > => {
      return useMutation(getCreateTenderPackageExportsMutationOptions(options));
    }

export const getListTenderExportsUrl = (id: string,
    params?: ListTenderExportsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/exports?${stringifiedParams}` : `/api/tenders/${id}/exports`
}

/**
 * @summary List generated tender artifact history
 */
export const listTenderExports = async (id: string,
    params?: ListTenderExportsParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderExportList> => {

  return customFetch<TenderExportList>(getListTenderExportsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderExportsQueryKey = (id: string,
    params?: ListTenderExportsParams,) => {
    return [
    `/api/tenders/${id}/exports`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderExportsQueryOptions = <TData = Awaited<ReturnType<typeof listTenderExports>>, TError = unknown>(id: string,
    params?: ListTenderExportsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderExports>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderExportsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderExports>>> = ({ signal }) => listTenderExports(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderExports>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderExportsQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderExports>>>
export type ListTenderExportsQueryError = unknown


/**
 * @summary List generated tender artifact history
 */

export function useListTenderExports<TData = Awaited<ReturnType<typeof listTenderExports>>, TError = unknown>(
 id: string,
    params?: ListTenderExportsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderExports>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderExportsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDownloadTenderExportUrl = (id: string,
    exportId: string,) => {




  return `/api/tenders/${id}/exports/${exportId}/content`
}

/**
 * @summary Download one saved private tender Word artifact
 */
export const downloadTenderExport = async (id: string,
    exportId: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadTenderExportUrl(id,exportId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadTenderExportQueryKey = (id: string,
    exportId: string,) => {
    return [
    `/api/tenders/${id}/exports/${exportId}/content`
    ] as const;
    }


export const getDownloadTenderExportQueryOptions = <TData = Awaited<ReturnType<typeof downloadTenderExport>>, TError = Error>(id: string,
    exportId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadTenderExport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadTenderExportQueryKey(id,exportId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadTenderExport>>> = ({ signal }) => downloadTenderExport(id,exportId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && exportId !== null && exportId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadTenderExport>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadTenderExportQueryResult = NonNullable<Awaited<ReturnType<typeof downloadTenderExport>>>
export type DownloadTenderExportQueryError = Error


/**
 * @summary Download one saved private tender Word artifact
 */

export function useDownloadTenderExport<TData = Awaited<ReturnType<typeof downloadTenderExport>>, TError = Error>(
 id: string,
    exportId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadTenderExport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadTenderExportQueryOptions(id,exportId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetTenderExportOfficeLaunchUrl = (id: string,
    exportId: string,) => {




  return `/api/tenders/${id}/exports/${exportId}/office-launch`
}

/**
 * @summary Get the verified SharePoint Word launch URL for a saved tender output
 */
export const getTenderExportOfficeLaunch = async (id: string,
    exportId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenderOfficeLaunch> => {

  return customFetch<TenderOfficeLaunch>(getGetTenderExportOfficeLaunchUrl(id,exportId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenderExportOfficeLaunchQueryKey = (id: string,
    exportId: string,) => {
    return [
    `/api/tenders/${id}/exports/${exportId}/office-launch`
    ] as const;
    }


export const getGetTenderExportOfficeLaunchQueryOptions = <TData = Awaited<ReturnType<typeof getTenderExportOfficeLaunch>>, TError = Error>(id: string,
    exportId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderExportOfficeLaunch>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenderExportOfficeLaunchQueryKey(id,exportId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenderExportOfficeLaunch>>> = ({ signal }) => getTenderExportOfficeLaunch(id,exportId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && exportId !== null && exportId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenderExportOfficeLaunch>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTenderExportOfficeLaunchQueryResult = NonNullable<Awaited<ReturnType<typeof getTenderExportOfficeLaunch>>>
export type GetTenderExportOfficeLaunchQueryError = Error


/**
 * @summary Get the verified SharePoint Word launch URL for a saved tender output
 */

export function useGetTenderExportOfficeLaunch<TData = Awaited<ReturnType<typeof getTenderExportOfficeLaunch>>, TError = Error>(
 id: string,
    exportId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTenderExportOfficeLaunch>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTenderExportOfficeLaunchQueryOptions(id,exportId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListTenderReviewsUrl = (id: string,
    params?: ListTenderReviewsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenders/${id}/reviews?${stringifiedParams}` : `/api/tenders/${id}/reviews`
}

/**
 * @summary List package review history
 */
export const listTenderReviews = async (id: string,
    params?: ListTenderReviewsParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderReviewList> => {

  return customFetch<TenderReviewList>(getListTenderReviewsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderReviewsQueryKey = (id: string,
    params?: ListTenderReviewsParams,) => {
    return [
    `/api/tenders/${id}/reviews`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderReviewsQueryOptions = <TData = Awaited<ReturnType<typeof listTenderReviews>>, TError = unknown>(id: string,
    params?: ListTenderReviewsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderReviews>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderReviewsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderReviews>>> = ({ signal }) => listTenderReviews(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderReviews>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderReviewsQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderReviews>>>
export type ListTenderReviewsQueryError = unknown


/**
 * @summary List package review history
 */

export function useListTenderReviews<TData = Awaited<ReturnType<typeof listTenderReviews>>, TError = unknown>(
 id: string,
    params?: ListTenderReviewsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderReviews>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderReviewsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDecideTenderReviewUrl = (id: string,
    reviewId: string,) => {




  return `/api/tenders/${id}/reviews/${reviewId}/decision`
}

/**
 * @summary Approve or return the exact reviewed package
 */
export const decideTenderReview = async (id: string,
    reviewId: string,
    tenderReviewDecision: TenderReviewDecision, options?: Parameters<typeof customFetch>[1]): Promise<TenderReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderReview>(getDecideTenderReviewUrl(id,reviewId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderReviewDecision)
  }
);}





export const getDecideTenderReviewMutationKey = () => ['decideTenderReview'] as const;

export const getDecideTenderReviewMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideTenderReview>>, TError,DecideTenderReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof decideTenderReview>>, TError,DecideTenderReviewMutationVariables, TContext> => {

const mutationKey = getDecideTenderReviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof decideTenderReview>>, DecideTenderReviewMutationVariables> = (props) => {
          const {id,reviewId,data} = props ?? {};

          return  decideTenderReview(id,reviewId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DecideTenderReviewMutationResult = NonNullable<Awaited<ReturnType<typeof decideTenderReview>>>
    export type DecideTenderReviewMutationBody = TenderReviewDecision
    export type DecideTenderReviewMutationError = unknown
    export type DecideTenderReviewMutationVariables = {id: string;reviewId: string;data: TenderReviewDecision}

    /**
 * @summary Approve or return the exact reviewed package
 */
export const useDecideTenderReview = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideTenderReview>>, TError,DecideTenderReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof decideTenderReview>>,
        TError,
        DecideTenderReviewMutationVariables,
        TContext
      > => {
      return useMutation(getDecideTenderReviewMutationOptions(options));
    }

export const getRecordTenderManualSubmissionUrl = (id: string,
    packageRevisionId: string,) => {




  return `/api/tenders/${id}/packages/${packageRevisionId}/manual-submission`
}

/**
 * @summary Record an external submission fact without sending or publishing anything
 */
export const recordTenderManualSubmission = async (id: string,
    packageRevisionId: string,
    tenderManualSubmission: TenderManualSubmission, options?: Parameters<typeof customFetch>[1]): Promise<Tender> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Tender>(getRecordTenderManualSubmissionUrl(id,packageRevisionId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenderManualSubmission)
  }
);}





export const getRecordTenderManualSubmissionMutationKey = () => ['recordTenderManualSubmission'] as const;

export const getRecordTenderManualSubmissionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recordTenderManualSubmission>>, TError,RecordTenderManualSubmissionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof recordTenderManualSubmission>>, TError,RecordTenderManualSubmissionMutationVariables, TContext> => {

const mutationKey = getRecordTenderManualSubmissionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof recordTenderManualSubmission>>, RecordTenderManualSubmissionMutationVariables> = (props) => {
          const {id,packageRevisionId,data} = props ?? {};

          return  recordTenderManualSubmission(id,packageRevisionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RecordTenderManualSubmissionMutationResult = NonNullable<Awaited<ReturnType<typeof recordTenderManualSubmission>>>
    export type RecordTenderManualSubmissionMutationBody = TenderManualSubmission
    export type RecordTenderManualSubmissionMutationError = unknown
    export type RecordTenderManualSubmissionMutationVariables = {id: string;packageRevisionId: string;data: TenderManualSubmission}

    /**
 * @summary Record an external submission fact without sending or publishing anything
 */
export const useRecordTenderManualSubmission = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recordTenderManualSubmission>>, TError,RecordTenderManualSubmissionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof recordTenderManualSubmission>>,
        TError,
        RecordTenderManualSubmissionMutationVariables,
        TContext
      > => {
      return useMutation(getRecordTenderManualSubmissionMutationOptions(options));
    }

export const getListQuoteTendersUrl = (id: string,
    params?: ListQuoteTendersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/quotes/${id}/tenders?${stringifiedParams}` : `/api/quotes/${id}/tenders`
}

/**
 * @summary List tenders linked to a quote
 */
export const listQuoteTenders = async (id: string,
    params?: ListQuoteTendersParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderList> => {

  return customFetch<TenderList>(getListQuoteTendersUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListQuoteTendersQueryKey = (id: string,
    params?: ListQuoteTendersParams,) => {
    return [
    `/api/quotes/${id}/tenders`, ...(params ? [params] : [])
    ] as const;
    }


export const getListQuoteTendersQueryOptions = <TData = Awaited<ReturnType<typeof listQuoteTenders>>, TError = unknown>(id: string,
    params?: ListQuoteTendersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listQuoteTenders>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListQuoteTendersQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listQuoteTenders>>> = ({ signal }) => listQuoteTenders(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listQuoteTenders>>, TError, TData> & { queryKey: QueryKey }
}

export type ListQuoteTendersQueryResult = NonNullable<Awaited<ReturnType<typeof listQuoteTenders>>>
export type ListQuoteTendersQueryError = unknown


/**
 * @summary List tenders linked to a quote
 */

export function useListQuoteTenders<TData = Awaited<ReturnType<typeof listQuoteTenders>>, TError = unknown>(
 id: string,
    params?: ListQuoteTendersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listQuoteTenders>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListQuoteTendersQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListQuoteRequestsUrl = (params?: ListQuoteRequestsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/quote-requests?${stringifiedParams}` : `/api/quote-requests`
}

/**
 * @summary List quote requests
 */
export const listQuoteRequests = async (params?: ListQuoteRequestsParams, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRequestList> => {

  return customFetch<QuoteRequestList>(getListQuoteRequestsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListQuoteRequestsQueryKey = (params?: ListQuoteRequestsParams,) => {
    return [
    `/api/quote-requests`, ...(params ? [params] : [])
    ] as const;
    }


export const getListQuoteRequestsQueryOptions = <TData = Awaited<ReturnType<typeof listQuoteRequests>>, TError = unknown>(params?: ListQuoteRequestsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listQuoteRequests>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListQuoteRequestsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listQuoteRequests>>> = ({ signal }) => listQuoteRequests(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listQuoteRequests>>, TError, TData> & { queryKey: QueryKey }
}

export type ListQuoteRequestsQueryResult = NonNullable<Awaited<ReturnType<typeof listQuoteRequests>>>
export type ListQuoteRequestsQueryError = unknown


/**
 * @summary List quote requests
 */

export function useListQuoteRequests<TData = Awaited<ReturnType<typeof listQuoteRequests>>, TError = unknown>(
 params?: ListQuoteRequestsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listQuoteRequests>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListQuoteRequestsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateQuoteRequestUrl = () => {




  return `/api/quote-requests`
}

/**
 * @summary Create a quote request (reference is server-generated)
 */
export const createQuoteRequest = async (quoteRequestCreate: QuoteRequestCreate, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<QuoteRequest>(getCreateQuoteRequestUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(quoteRequestCreate)
  }
);}





export const getCreateQuoteRequestMutationKey = () => ['createQuoteRequest'] as const;

export const getCreateQuoteRequestMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createQuoteRequest>>, TError,CreateQuoteRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createQuoteRequest>>, TError,CreateQuoteRequestMutationVariables, TContext> => {

const mutationKey = getCreateQuoteRequestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createQuoteRequest>>, CreateQuoteRequestMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createQuoteRequest(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateQuoteRequestMutationResult = NonNullable<Awaited<ReturnType<typeof createQuoteRequest>>>
    export type CreateQuoteRequestMutationBody = QuoteRequestCreate
    export type CreateQuoteRequestMutationError = unknown
    export type CreateQuoteRequestMutationVariables = {data: QuoteRequestCreate}

    /**
 * @summary Create a quote request (reference is server-generated)
 */
export const useCreateQuoteRequest = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createQuoteRequest>>, TError,CreateQuoteRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createQuoteRequest>>,
        TError,
        CreateQuoteRequestMutationVariables,
        TContext
      > => {
      return useMutation(getCreateQuoteRequestMutationOptions(options));
    }

export const getGetQuoteRequestUrl = (id: string,) => {




  return `/api/quote-requests/${id}`
}

/**
 * @summary Get a quote request with its items and documents
 */
export const getQuoteRequest = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRequestDetail> => {

  return customFetch<QuoteRequestDetail>(getGetQuoteRequestUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetQuoteRequestQueryKey = (id: string,) => {
    return [
    `/api/quote-requests/${id}`
    ] as const;
    }


export const getGetQuoteRequestQueryOptions = <TData = Awaited<ReturnType<typeof getQuoteRequest>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getQuoteRequest>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetQuoteRequestQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getQuoteRequest>>> = ({ signal }) => getQuoteRequest(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getQuoteRequest>>, TError, TData> & { queryKey: QueryKey }
}

export type GetQuoteRequestQueryResult = NonNullable<Awaited<ReturnType<typeof getQuoteRequest>>>
export type GetQuoteRequestQueryError = Error


/**
 * @summary Get a quote request with its items and documents
 */

export function useGetQuoteRequest<TData = Awaited<ReturnType<typeof getQuoteRequest>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getQuoteRequest>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetQuoteRequestQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateQuoteRequestUrl = (id: string,) => {




  return `/api/quote-requests/${id}`
}

/**
 * @summary Update status, pricing, notes, or linked account/contact/partner
 */
export const updateQuoteRequest = async (id: string,
    quoteRequestUpdate: QuoteRequestUpdate, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<QuoteRequest>(getUpdateQuoteRequestUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(quoteRequestUpdate)
  }
);}





export const getUpdateQuoteRequestMutationKey = () => ['updateQuoteRequest'] as const;

export const getUpdateQuoteRequestMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateQuoteRequest>>, TError,UpdateQuoteRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateQuoteRequest>>, TError,UpdateQuoteRequestMutationVariables, TContext> => {

const mutationKey = getUpdateQuoteRequestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateQuoteRequest>>, UpdateQuoteRequestMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateQuoteRequest(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateQuoteRequestMutationResult = NonNullable<Awaited<ReturnType<typeof updateQuoteRequest>>>
    export type UpdateQuoteRequestMutationBody = QuoteRequestUpdate
    export type UpdateQuoteRequestMutationError = Error
    export type UpdateQuoteRequestMutationVariables = {id: string;data: QuoteRequestUpdate}

    /**
 * @summary Update status, pricing, notes, or linked account/contact/partner
 */
export const useUpdateQuoteRequest = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateQuoteRequest>>, TError,UpdateQuoteRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateQuoteRequest>>,
        TError,
        UpdateQuoteRequestMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateQuoteRequestMutationOptions(options));
    }

export const getDeleteQuoteRequestUrl = (id: string,) => {




  return `/api/quote-requests/${id}`
}

/**
 * @summary Delete a quote request
 */
export const deleteQuoteRequest = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteQuoteRequestUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteQuoteRequestMutationKey = () => ['deleteQuoteRequest'] as const;

export const getDeleteQuoteRequestMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteQuoteRequest>>, TError,DeleteQuoteRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteQuoteRequest>>, TError,DeleteQuoteRequestMutationVariables, TContext> => {

const mutationKey = getDeleteQuoteRequestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteQuoteRequest>>, DeleteQuoteRequestMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteQuoteRequest(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteQuoteRequestMutationResult = NonNullable<Awaited<ReturnType<typeof deleteQuoteRequest>>>

    export type DeleteQuoteRequestMutationError = Error
    export type DeleteQuoteRequestMutationVariables = {id: string}

    /**
 * @summary Delete a quote request
 */
export const useDeleteQuoteRequest = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteQuoteRequest>>, TError,DeleteQuoteRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteQuoteRequest>>,
        TError,
        DeleteQuoteRequestMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteQuoteRequestMutationOptions(options));
    }

export const getListQuoteRequestItemsUrl = (quoteRequestId: string,) => {




  return `/api/quote-requests/${quoteRequestId}/items`
}

/**
 * @summary List line items for a quote request
 */
export const listQuoteRequestItems = async (quoteRequestId: string, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRequestItemList> => {

  return customFetch<QuoteRequestItemList>(getListQuoteRequestItemsUrl(quoteRequestId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListQuoteRequestItemsQueryKey = (quoteRequestId: string,) => {
    return [
    `/api/quote-requests/${quoteRequestId}/items`
    ] as const;
    }


export const getListQuoteRequestItemsQueryOptions = <TData = Awaited<ReturnType<typeof listQuoteRequestItems>>, TError = unknown>(quoteRequestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listQuoteRequestItems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListQuoteRequestItemsQueryKey(quoteRequestId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listQuoteRequestItems>>> = ({ signal }) => listQuoteRequestItems(quoteRequestId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: quoteRequestId !== null && quoteRequestId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listQuoteRequestItems>>, TError, TData> & { queryKey: QueryKey }
}

export type ListQuoteRequestItemsQueryResult = NonNullable<Awaited<ReturnType<typeof listQuoteRequestItems>>>
export type ListQuoteRequestItemsQueryError = unknown


/**
 * @summary List line items for a quote request
 */

export function useListQuoteRequestItems<TData = Awaited<ReturnType<typeof listQuoteRequestItems>>, TError = unknown>(
 quoteRequestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listQuoteRequestItems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListQuoteRequestItemsQueryOptions(quoteRequestId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateQuoteRequestItemUrl = (quoteRequestId: string,) => {




  return `/api/quote-requests/${quoteRequestId}/items`
}

/**
 * @summary Add a line item to a quote request
 */
export const createQuoteRequestItem = async (quoteRequestId: string,
    quoteRequestItemCreate: QuoteRequestItemCreate, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRequestItem> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<QuoteRequestItem>(getCreateQuoteRequestItemUrl(quoteRequestId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(quoteRequestItemCreate)
  }
);}





export const getCreateQuoteRequestItemMutationKey = () => ['createQuoteRequestItem'] as const;

export const getCreateQuoteRequestItemMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createQuoteRequestItem>>, TError,CreateQuoteRequestItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createQuoteRequestItem>>, TError,CreateQuoteRequestItemMutationVariables, TContext> => {

const mutationKey = getCreateQuoteRequestItemMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createQuoteRequestItem>>, CreateQuoteRequestItemMutationVariables> = (props) => {
          const {quoteRequestId,data} = props ?? {};

          return  createQuoteRequestItem(quoteRequestId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateQuoteRequestItemMutationResult = NonNullable<Awaited<ReturnType<typeof createQuoteRequestItem>>>
    export type CreateQuoteRequestItemMutationBody = QuoteRequestItemCreate
    export type CreateQuoteRequestItemMutationError = Error
    export type CreateQuoteRequestItemMutationVariables = {quoteRequestId: string;data: QuoteRequestItemCreate}

    /**
 * @summary Add a line item to a quote request
 */
export const useCreateQuoteRequestItem = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createQuoteRequestItem>>, TError,CreateQuoteRequestItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createQuoteRequestItem>>,
        TError,
        CreateQuoteRequestItemMutationVariables,
        TContext
      > => {
      return useMutation(getCreateQuoteRequestItemMutationOptions(options));
    }

export const getUpdateQuoteRequestItemUrl = (quoteRequestId: string,
    itemId: string,) => {




  return `/api/quote-requests/${quoteRequestId}/items/${itemId}`
}

/**
 * @summary Update a line item
 */
export const updateQuoteRequestItem = async (quoteRequestId: string,
    itemId: string,
    quoteRequestItemUpdate: QuoteRequestItemUpdate, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRequestItem> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<QuoteRequestItem>(getUpdateQuoteRequestItemUrl(quoteRequestId,itemId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(quoteRequestItemUpdate)
  }
);}





export const getUpdateQuoteRequestItemMutationKey = () => ['updateQuoteRequestItem'] as const;

export const getUpdateQuoteRequestItemMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateQuoteRequestItem>>, TError,UpdateQuoteRequestItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateQuoteRequestItem>>, TError,UpdateQuoteRequestItemMutationVariables, TContext> => {

const mutationKey = getUpdateQuoteRequestItemMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateQuoteRequestItem>>, UpdateQuoteRequestItemMutationVariables> = (props) => {
          const {quoteRequestId,itemId,data} = props ?? {};

          return  updateQuoteRequestItem(quoteRequestId,itemId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateQuoteRequestItemMutationResult = NonNullable<Awaited<ReturnType<typeof updateQuoteRequestItem>>>
    export type UpdateQuoteRequestItemMutationBody = QuoteRequestItemUpdate
    export type UpdateQuoteRequestItemMutationError = Error
    export type UpdateQuoteRequestItemMutationVariables = {quoteRequestId: string;itemId: string;data: QuoteRequestItemUpdate}

    /**
 * @summary Update a line item
 */
export const useUpdateQuoteRequestItem = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateQuoteRequestItem>>, TError,UpdateQuoteRequestItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateQuoteRequestItem>>,
        TError,
        UpdateQuoteRequestItemMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateQuoteRequestItemMutationOptions(options));
    }

export const getDeleteQuoteRequestItemUrl = (quoteRequestId: string,
    itemId: string,) => {




  return `/api/quote-requests/${quoteRequestId}/items/${itemId}`
}

/**
 * @summary Remove a line item
 */
export const deleteQuoteRequestItem = async (quoteRequestId: string,
    itemId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteQuoteRequestItemUrl(quoteRequestId,itemId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteQuoteRequestItemMutationKey = () => ['deleteQuoteRequestItem'] as const;

export const getDeleteQuoteRequestItemMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteQuoteRequestItem>>, TError,DeleteQuoteRequestItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteQuoteRequestItem>>, TError,DeleteQuoteRequestItemMutationVariables, TContext> => {

const mutationKey = getDeleteQuoteRequestItemMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteQuoteRequestItem>>, DeleteQuoteRequestItemMutationVariables> = (props) => {
          const {quoteRequestId,itemId} = props ?? {};

          return  deleteQuoteRequestItem(quoteRequestId,itemId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteQuoteRequestItemMutationResult = NonNullable<Awaited<ReturnType<typeof deleteQuoteRequestItem>>>

    export type DeleteQuoteRequestItemMutationError = Error
    export type DeleteQuoteRequestItemMutationVariables = {quoteRequestId: string;itemId: string}

    /**
 * @summary Remove a line item
 */
export const useDeleteQuoteRequestItem = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteQuoteRequestItem>>, TError,DeleteQuoteRequestItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteQuoteRequestItem>>,
        TError,
        DeleteQuoteRequestItemMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteQuoteRequestItemMutationOptions(options));
    }

export const getCreateQuoteSectionUrl = (id: string,) => {




  return `/api/quotes/${id}/sections`
}

/**
 * @summary Add a work-package section to a quote
 */
export const createQuoteSection = async (id: string,
    quoteSectionCreate: QuoteSectionCreate, options?: Parameters<typeof customFetch>[1]): Promise<QuoteSection> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<QuoteSection>(getCreateQuoteSectionUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(quoteSectionCreate)
  }
);}





export const getCreateQuoteSectionMutationKey = () => ['createQuoteSection'] as const;

export const getCreateQuoteSectionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createQuoteSection>>, TError,CreateQuoteSectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createQuoteSection>>, TError,CreateQuoteSectionMutationVariables, TContext> => {

const mutationKey = getCreateQuoteSectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createQuoteSection>>, CreateQuoteSectionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createQuoteSection(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateQuoteSectionMutationResult = NonNullable<Awaited<ReturnType<typeof createQuoteSection>>>
    export type CreateQuoteSectionMutationBody = QuoteSectionCreate
    export type CreateQuoteSectionMutationError = unknown
    export type CreateQuoteSectionMutationVariables = {id: string;data: QuoteSectionCreate}

    /**
 * @summary Add a work-package section to a quote
 */
export const useCreateQuoteSection = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createQuoteSection>>, TError,CreateQuoteSectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createQuoteSection>>,
        TError,
        CreateQuoteSectionMutationVariables,
        TContext
      > => {
      return useMutation(getCreateQuoteSectionMutationOptions(options));
    }

export const getReorderQuoteSectionsUrl = (id: string,) => {




  return `/api/quotes/${id}/sections/reorder`
}

/**
 * @summary Replace the complete order of a quote's sections
 */
export const reorderQuoteSections = async (id: string,
    quoteSectionReorder: QuoteSectionReorder, options?: Parameters<typeof customFetch>[1]): Promise<QuoteSection[]> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<QuoteSection[]>(getReorderQuoteSectionsUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(quoteSectionReorder)
  }
);}





export const getReorderQuoteSectionsMutationKey = () => ['reorderQuoteSections'] as const;

export const getReorderQuoteSectionsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reorderQuoteSections>>, TError,ReorderQuoteSectionsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reorderQuoteSections>>, TError,ReorderQuoteSectionsMutationVariables, TContext> => {

const mutationKey = getReorderQuoteSectionsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reorderQuoteSections>>, ReorderQuoteSectionsMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reorderQuoteSections(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReorderQuoteSectionsMutationResult = NonNullable<Awaited<ReturnType<typeof reorderQuoteSections>>>
    export type ReorderQuoteSectionsMutationBody = QuoteSectionReorder
    export type ReorderQuoteSectionsMutationError = unknown
    export type ReorderQuoteSectionsMutationVariables = {id: string;data: QuoteSectionReorder}

    /**
 * @summary Replace the complete order of a quote's sections
 */
export const useReorderQuoteSections = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reorderQuoteSections>>, TError,ReorderQuoteSectionsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reorderQuoteSections>>,
        TError,
        ReorderQuoteSectionsMutationVariables,
        TContext
      > => {
      return useMutation(getReorderQuoteSectionsMutationOptions(options));
    }

export const getUpdateQuoteSectionUrl = (id: string,
    sectionId: string,) => {




  return `/api/quotes/${id}/sections/${sectionId}`
}

/**
 * @summary Rename or update a quote section
 */
export const updateQuoteSection = async (id: string,
    sectionId: string,
    quoteSectionUpdate: QuoteSectionUpdate, options?: Parameters<typeof customFetch>[1]): Promise<QuoteSection> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<QuoteSection>(getUpdateQuoteSectionUrl(id,sectionId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(quoteSectionUpdate)
  }
);}





export const getUpdateQuoteSectionMutationKey = () => ['updateQuoteSection'] as const;

export const getUpdateQuoteSectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateQuoteSection>>, TError,UpdateQuoteSectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateQuoteSection>>, TError,UpdateQuoteSectionMutationVariables, TContext> => {

const mutationKey = getUpdateQuoteSectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateQuoteSection>>, UpdateQuoteSectionMutationVariables> = (props) => {
          const {id,sectionId,data} = props ?? {};

          return  updateQuoteSection(id,sectionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateQuoteSectionMutationResult = NonNullable<Awaited<ReturnType<typeof updateQuoteSection>>>
    export type UpdateQuoteSectionMutationBody = QuoteSectionUpdate
    export type UpdateQuoteSectionMutationError = Error
    export type UpdateQuoteSectionMutationVariables = {id: string;sectionId: string;data: QuoteSectionUpdate}

    /**
 * @summary Rename or update a quote section
 */
export const useUpdateQuoteSection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateQuoteSection>>, TError,UpdateQuoteSectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateQuoteSection>>,
        TError,
        UpdateQuoteSectionMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateQuoteSectionMutationOptions(options));
    }

export const getDeleteQuoteSectionUrl = (id: string,
    sectionId: string,) => {




  return `/api/quotes/${id}/sections/${sectionId}`
}

/**
 * @summary Delete a quote section and leave its lines unsectioned
 */
export const deleteQuoteSection = async (id: string,
    sectionId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteQuoteSectionUrl(id,sectionId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteQuoteSectionMutationKey = () => ['deleteQuoteSection'] as const;

export const getDeleteQuoteSectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteQuoteSection>>, TError,DeleteQuoteSectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteQuoteSection>>, TError,DeleteQuoteSectionMutationVariables, TContext> => {

const mutationKey = getDeleteQuoteSectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteQuoteSection>>, DeleteQuoteSectionMutationVariables> = (props) => {
          const {id,sectionId} = props ?? {};

          return  deleteQuoteSection(id,sectionId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteQuoteSectionMutationResult = NonNullable<Awaited<ReturnType<typeof deleteQuoteSection>>>

    export type DeleteQuoteSectionMutationError = Error
    export type DeleteQuoteSectionMutationVariables = {id: string;sectionId: string}

    /**
 * @summary Delete a quote section and leave its lines unsectioned
 */
export const useDeleteQuoteSection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteQuoteSection>>, TError,DeleteQuoteSectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteQuoteSection>>,
        TError,
        DeleteQuoteSectionMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteQuoteSectionMutationOptions(options));
    }

export const getListQuoteRequestDocumentsUrl = (quoteRequestId: string,) => {




  return `/api/quote-requests/${quoteRequestId}/documents`
}

/**
 * @summary List documents attached to a quote request
 */
export const listQuoteRequestDocuments = async (quoteRequestId: string, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRequestDocumentList> => {

  return customFetch<QuoteRequestDocumentList>(getListQuoteRequestDocumentsUrl(quoteRequestId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListQuoteRequestDocumentsQueryKey = (quoteRequestId: string,) => {
    return [
    `/api/quote-requests/${quoteRequestId}/documents`
    ] as const;
    }


export const getListQuoteRequestDocumentsQueryOptions = <TData = Awaited<ReturnType<typeof listQuoteRequestDocuments>>, TError = unknown>(quoteRequestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listQuoteRequestDocuments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListQuoteRequestDocumentsQueryKey(quoteRequestId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listQuoteRequestDocuments>>> = ({ signal }) => listQuoteRequestDocuments(quoteRequestId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: quoteRequestId !== null && quoteRequestId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listQuoteRequestDocuments>>, TError, TData> & { queryKey: QueryKey }
}

export type ListQuoteRequestDocumentsQueryResult = NonNullable<Awaited<ReturnType<typeof listQuoteRequestDocuments>>>
export type ListQuoteRequestDocumentsQueryError = unknown


/**
 * @summary List documents attached to a quote request
 */

export function useListQuoteRequestDocuments<TData = Awaited<ReturnType<typeof listQuoteRequestDocuments>>, TError = unknown>(
 quoteRequestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listQuoteRequestDocuments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListQuoteRequestDocumentsQueryOptions(quoteRequestId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateQuoteRequestDocumentUrl = (quoteRequestId: string,) => {




  return `/api/quote-requests/${quoteRequestId}/documents`
}

/**
 * @summary Attach an already-uploaded file (via /api/files) to a quote request
 */
export const createQuoteRequestDocument = async (quoteRequestId: string,
    quoteRequestDocumentCreate: QuoteRequestDocumentCreate, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRequestDocument> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<QuoteRequestDocument>(getCreateQuoteRequestDocumentUrl(quoteRequestId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(quoteRequestDocumentCreate)
  }
);}





export const getCreateQuoteRequestDocumentMutationKey = () => ['createQuoteRequestDocument'] as const;

export const getCreateQuoteRequestDocumentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createQuoteRequestDocument>>, TError,CreateQuoteRequestDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createQuoteRequestDocument>>, TError,CreateQuoteRequestDocumentMutationVariables, TContext> => {

const mutationKey = getCreateQuoteRequestDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createQuoteRequestDocument>>, CreateQuoteRequestDocumentMutationVariables> = (props) => {
          const {quoteRequestId,data} = props ?? {};

          return  createQuoteRequestDocument(quoteRequestId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateQuoteRequestDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof createQuoteRequestDocument>>>
    export type CreateQuoteRequestDocumentMutationBody = QuoteRequestDocumentCreate
    export type CreateQuoteRequestDocumentMutationError = Error
    export type CreateQuoteRequestDocumentMutationVariables = {quoteRequestId: string;data: QuoteRequestDocumentCreate}

    /**
 * @summary Attach an already-uploaded file (via /api/files) to a quote request
 */
export const useCreateQuoteRequestDocument = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createQuoteRequestDocument>>, TError,CreateQuoteRequestDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createQuoteRequestDocument>>,
        TError,
        CreateQuoteRequestDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getCreateQuoteRequestDocumentMutationOptions(options));
    }

export const getDeleteQuoteRequestDocumentUrl = (quoteRequestId: string,
    documentId: string,) => {




  return `/api/quote-requests/${quoteRequestId}/documents/${documentId}`
}

/**
 * @summary Detach a document from a quote request
 */
export const deleteQuoteRequestDocument = async (quoteRequestId: string,
    documentId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteQuoteRequestDocumentUrl(quoteRequestId,documentId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteQuoteRequestDocumentMutationKey = () => ['deleteQuoteRequestDocument'] as const;

export const getDeleteQuoteRequestDocumentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteQuoteRequestDocument>>, TError,DeleteQuoteRequestDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteQuoteRequestDocument>>, TError,DeleteQuoteRequestDocumentMutationVariables, TContext> => {

const mutationKey = getDeleteQuoteRequestDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteQuoteRequestDocument>>, DeleteQuoteRequestDocumentMutationVariables> = (props) => {
          const {quoteRequestId,documentId} = props ?? {};

          return  deleteQuoteRequestDocument(quoteRequestId,documentId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteQuoteRequestDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof deleteQuoteRequestDocument>>>

    export type DeleteQuoteRequestDocumentMutationError = Error
    export type DeleteQuoteRequestDocumentMutationVariables = {quoteRequestId: string;documentId: string}

    /**
 * @summary Detach a document from a quote request
 */
export const useDeleteQuoteRequestDocument = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteQuoteRequestDocument>>, TError,DeleteQuoteRequestDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteQuoteRequestDocument>>,
        TError,
        DeleteQuoteRequestDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteQuoteRequestDocumentMutationOptions(options));
    }

export const getRecalculateQuoteUrl = (id: string,) => {




  return `/api/quotes/${id}/recalculate`
}

/**
 * @summary Recalculate line and quote totals from immutable unit-price snapshots
 */
export const recalculateQuote = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRequest> => {

  return customFetch<QuoteRequest>(getRecalculateQuoteUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRecalculateQuoteMutationKey = () => ['recalculateQuote'] as const;

export const getRecalculateQuoteMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recalculateQuote>>, TError,RecalculateQuoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof recalculateQuote>>, TError,RecalculateQuoteMutationVariables, TContext> => {

const mutationKey = getRecalculateQuoteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof recalculateQuote>>, RecalculateQuoteMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  recalculateQuote(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RecalculateQuoteMutationResult = NonNullable<Awaited<ReturnType<typeof recalculateQuote>>>

    export type RecalculateQuoteMutationError = unknown
    export type RecalculateQuoteMutationVariables = {id: string}

    /**
 * @summary Recalculate line and quote totals from immutable unit-price snapshots
 */
export const useRecalculateQuote = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recalculateQuote>>, TError,RecalculateQuoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof recalculateQuote>>,
        TError,
        RecalculateQuoteMutationVariables,
        TContext
      > => {
      return useMutation(getRecalculateQuoteMutationOptions(options));
    }

export const getDraftQuoteWithAiUrl = () => {




  return `/api/quotes/ai-draft`
}

/**
 * @summary Prepare an editable priced quote draft from a staff brief
 */
export const draftQuoteWithAi = async (aiQuoteDraftRequest: AiQuoteDraftRequest, options?: Parameters<typeof customFetch>[1]): Promise<AiQuoteDraftResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AiQuoteDraftResult>(getDraftQuoteWithAiUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(aiQuoteDraftRequest)
  }
);}





export const getDraftQuoteWithAiMutationKey = () => ['draftQuoteWithAi'] as const;

export const getDraftQuoteWithAiMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof draftQuoteWithAi>>, TError,DraftQuoteWithAiMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof draftQuoteWithAi>>, TError,DraftQuoteWithAiMutationVariables, TContext> => {

const mutationKey = getDraftQuoteWithAiMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof draftQuoteWithAi>>, DraftQuoteWithAiMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  draftQuoteWithAi(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DraftQuoteWithAiMutationResult = NonNullable<Awaited<ReturnType<typeof draftQuoteWithAi>>>
    export type DraftQuoteWithAiMutationBody = AiQuoteDraftRequest
    export type DraftQuoteWithAiMutationError = Error
    export type DraftQuoteWithAiMutationVariables = {data: AiQuoteDraftRequest}

    /**
 * @summary Prepare an editable priced quote draft from a staff brief
 */
export const useDraftQuoteWithAi = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof draftQuoteWithAi>>, TError,DraftQuoteWithAiMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof draftQuoteWithAi>>,
        TError,
        DraftQuoteWithAiMutationVariables,
        TContext
      > => {
      return useMutation(getDraftQuoteWithAiMutationOptions(options));
    }

export const getDraftOpportunityQuoteWithAiUrl = (id: string,) => {




  return `/api/opportunities/${id}/quotes/ai-draft`
}

/**
 * @summary Prepare an editable priced quote draft from an opportunity and staff brief
 */
export const draftOpportunityQuoteWithAi = async (id: string,
    aiOpportunityQuoteDraftRequest: AiOpportunityQuoteDraftRequest, options?: Parameters<typeof customFetch>[1]): Promise<AiQuoteDraftResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AiQuoteDraftResult>(getDraftOpportunityQuoteWithAiUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(aiOpportunityQuoteDraftRequest)
  }
);}





export const getDraftOpportunityQuoteWithAiMutationKey = () => ['draftOpportunityQuoteWithAi'] as const;

export const getDraftOpportunityQuoteWithAiMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof draftOpportunityQuoteWithAi>>, TError,DraftOpportunityQuoteWithAiMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof draftOpportunityQuoteWithAi>>, TError,DraftOpportunityQuoteWithAiMutationVariables, TContext> => {

const mutationKey = getDraftOpportunityQuoteWithAiMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof draftOpportunityQuoteWithAi>>, DraftOpportunityQuoteWithAiMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  draftOpportunityQuoteWithAi(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DraftOpportunityQuoteWithAiMutationResult = NonNullable<Awaited<ReturnType<typeof draftOpportunityQuoteWithAi>>>
    export type DraftOpportunityQuoteWithAiMutationBody = AiOpportunityQuoteDraftRequest
    export type DraftOpportunityQuoteWithAiMutationError = unknown
    export type DraftOpportunityQuoteWithAiMutationVariables = {id: string;data: AiOpportunityQuoteDraftRequest}

    /**
 * @summary Prepare an editable priced quote draft from an opportunity and staff brief
 */
export const useDraftOpportunityQuoteWithAi = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof draftOpportunityQuoteWithAi>>, TError,DraftOpportunityQuoteWithAiMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof draftOpportunityQuoteWithAi>>,
        TError,
        DraftOpportunityQuoteWithAiMutationVariables,
        TContext
      > => {
      return useMutation(getDraftOpportunityQuoteWithAiMutationOptions(options));
    }

export const getReviseQuoteWithAiUrl = (id: string,) => {




  return `/api/quotes/${id}/ai-revise`
}

/**
 * @summary Apply a staff instruction to an AI quote as a new editable revision
 */
export const reviseQuoteWithAi = async (id: string,
    aiQuoteRevisionRequest: AiQuoteRevisionRequest, options?: Parameters<typeof customFetch>[1]): Promise<AiQuoteDraftResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AiQuoteDraftResult>(getReviseQuoteWithAiUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(aiQuoteRevisionRequest)
  }
);}





export const getReviseQuoteWithAiMutationKey = () => ['reviseQuoteWithAi'] as const;

export const getReviseQuoteWithAiMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviseQuoteWithAi>>, TError,ReviseQuoteWithAiMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviseQuoteWithAi>>, TError,ReviseQuoteWithAiMutationVariables, TContext> => {

const mutationKey = getReviseQuoteWithAiMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviseQuoteWithAi>>, ReviseQuoteWithAiMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reviseQuoteWithAi(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviseQuoteWithAiMutationResult = NonNullable<Awaited<ReturnType<typeof reviseQuoteWithAi>>>
    export type ReviseQuoteWithAiMutationBody = AiQuoteRevisionRequest
    export type ReviseQuoteWithAiMutationError = Error
    export type ReviseQuoteWithAiMutationVariables = {id: string;data: AiQuoteRevisionRequest}

    /**
 * @summary Apply a staff instruction to an AI quote as a new editable revision
 */
export const useReviseQuoteWithAi = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviseQuoteWithAi>>, TError,ReviseQuoteWithAiMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviseQuoteWithAi>>,
        TError,
        ReviseQuoteWithAiMutationVariables,
        TContext
      > => {
      return useMutation(getReviseQuoteWithAiMutationOptions(options));
    }

export const getSendQuoteUrl = (id: string,) => {




  return `/api/quotes/${id}/send`
}

/**
 * @summary Generate, store and email the current quote PDF with a secure decision link
 */
export const sendQuote = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<QuoteSendResult> => {

  return customFetch<QuoteSendResult>(getSendQuoteUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getSendQuoteMutationKey = () => ['sendQuote'] as const;

export const getSendQuoteMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendQuote>>, TError,SendQuoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof sendQuote>>, TError,SendQuoteMutationVariables, TContext> => {

const mutationKey = getSendQuoteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof sendQuote>>, SendQuoteMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  sendQuote(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SendQuoteMutationResult = NonNullable<Awaited<ReturnType<typeof sendQuote>>>

    export type SendQuoteMutationError = Error
    export type SendQuoteMutationVariables = {id: string}

    /**
 * @summary Generate, store and email the current quote PDF with a secure decision link
 */
export const useSendQuote = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendQuote>>, TError,SendQuoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof sendQuote>>,
        TError,
        SendQuoteMutationVariables,
        TContext
      > => {
      return useMutation(getSendQuoteMutationOptions(options));
    }

export const getDownloadQuotePdfUrl = (id: string,) => {




  return `/api/quotes/${id}/pdf`
}

/**
 * @summary Download the latest generated PDF for a quote
 */
export const downloadQuotePdf = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadQuotePdfUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadQuotePdfQueryKey = (id: string,) => {
    return [
    `/api/quotes/${id}/pdf`
    ] as const;
    }


export const getDownloadQuotePdfQueryOptions = <TData = Awaited<ReturnType<typeof downloadQuotePdf>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadQuotePdf>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadQuotePdfQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadQuotePdf>>> = ({ signal }) => downloadQuotePdf(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadQuotePdf>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadQuotePdfQueryResult = NonNullable<Awaited<ReturnType<typeof downloadQuotePdf>>>
export type DownloadQuotePdfQueryError = Error


/**
 * @summary Download the latest generated PDF for a quote
 */

export function useDownloadQuotePdf<TData = Awaited<ReturnType<typeof downloadQuotePdf>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadQuotePdf>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadQuotePdfQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDownloadQuotePricingScheduleUrl = (id: string,
    params?: DownloadQuotePricingScheduleParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/quotes/${id}/pricing-schedule.xlsx?${stringifiedParams}` : `/api/quotes/${id}/pricing-schedule.xlsx`
}

/**
 * @summary Download the current quote as a customer or internal Excel pricing schedule
 */
export const downloadQuotePricingSchedule = async (id: string,
    params?: DownloadQuotePricingScheduleParams, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadQuotePricingScheduleUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadQuotePricingScheduleQueryKey = (id: string,
    params?: DownloadQuotePricingScheduleParams,) => {
    return [
    `/api/quotes/${id}/pricing-schedule.xlsx`, ...(params ? [params] : [])
    ] as const;
    }


export const getDownloadQuotePricingScheduleQueryOptions = <TData = Awaited<ReturnType<typeof downloadQuotePricingSchedule>>, TError = Error>(id: string,
    params?: DownloadQuotePricingScheduleParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadQuotePricingSchedule>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadQuotePricingScheduleQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadQuotePricingSchedule>>> = ({ signal }) => downloadQuotePricingSchedule(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadQuotePricingSchedule>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadQuotePricingScheduleQueryResult = NonNullable<Awaited<ReturnType<typeof downloadQuotePricingSchedule>>>
export type DownloadQuotePricingScheduleQueryError = Error


/**
 * @summary Download the current quote as a customer or internal Excel pricing schedule
 */

export function useDownloadQuotePricingSchedule<TData = Awaited<ReturnType<typeof downloadQuotePricingSchedule>>, TError = Error>(
 id: string,
    params?: DownloadQuotePricingScheduleParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadQuotePricingSchedule>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadQuotePricingScheduleQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getMarkQuotePricedUrl = (id: string,) => {




  return `/api/quotes/${id}/mark-priced`
}

/**
 * @summary Mark a recalculated quote as priced when approval is satisfied
 */
export const markQuotePriced = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRequest> => {

  return customFetch<QuoteRequest>(getMarkQuotePricedUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getMarkQuotePricedMutationKey = () => ['markQuotePriced'] as const;

export const getMarkQuotePricedMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markQuotePriced>>, TError,MarkQuotePricedMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof markQuotePriced>>, TError,MarkQuotePricedMutationVariables, TContext> => {

const mutationKey = getMarkQuotePricedMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof markQuotePriced>>, MarkQuotePricedMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  markQuotePriced(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MarkQuotePricedMutationResult = NonNullable<Awaited<ReturnType<typeof markQuotePriced>>>

    export type MarkQuotePricedMutationError = Error
    export type MarkQuotePricedMutationVariables = {id: string}

    /**
 * @summary Mark a recalculated quote as priced when approval is satisfied
 */
export const useMarkQuotePriced = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markQuotePriced>>, TError,MarkQuotePricedMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof markQuotePriced>>,
        TError,
        MarkQuotePricedMutationVariables,
        TContext
      > => {
      return useMutation(getMarkQuotePricedMutationOptions(options));
    }

export const getExpireDueQuotesUrl = () => {




  return `/api/quotes/expire-due`
}

/**
 * @summary Expire up to 100 sent quotes whose validity has elapsed
 */
export const expireDueQuotes = async ( options?: Parameters<typeof customFetch>[1]): Promise<ExpireDueQuotes200> => {

  return customFetch<ExpireDueQuotes200>(getExpireDueQuotesUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getExpireDueQuotesMutationKey = () => ['expireDueQuotes'] as const;

export const getExpireDueQuotesMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof expireDueQuotes>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof expireDueQuotes>>, TError,void, TContext> => {

const mutationKey = getExpireDueQuotesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof expireDueQuotes>>, void> = () => {


          return  expireDueQuotes(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ExpireDueQuotesMutationResult = NonNullable<Awaited<ReturnType<typeof expireDueQuotes>>>

    export type ExpireDueQuotesMutationError = unknown


    /**
 * @summary Expire up to 100 sent quotes whose validity has elapsed
 */
export const useExpireDueQuotes = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof expireDueQuotes>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof expireDueQuotes>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getExpireDueQuotesMutationOptions(options));
    }

export const getApplyQuotePriceListUrl = (id: string,) => {




  return `/api/quotes/${id}/apply-price-list`
}

/**
 * @summary Snapshot matching prices from an active price list onto quote lines
 */
export const applyQuotePriceList = async (id: string,
    applyQuotePriceListBody: ApplyQuotePriceListBody, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<QuoteRequest>(getApplyQuotePriceListUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(applyQuotePriceListBody)
  }
);}





export const getApplyQuotePriceListMutationKey = () => ['applyQuotePriceList'] as const;

export const getApplyQuotePriceListMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyQuotePriceList>>, TError,ApplyQuotePriceListMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof applyQuotePriceList>>, TError,ApplyQuotePriceListMutationVariables, TContext> => {

const mutationKey = getApplyQuotePriceListMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof applyQuotePriceList>>, ApplyQuotePriceListMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  applyQuotePriceList(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApplyQuotePriceListMutationResult = NonNullable<Awaited<ReturnType<typeof applyQuotePriceList>>>
    export type ApplyQuotePriceListMutationBody = ApplyQuotePriceListBody
    export type ApplyQuotePriceListMutationError = unknown
    export type ApplyQuotePriceListMutationVariables = {id: string;data: ApplyQuotePriceListBody}

    /**
 * @summary Snapshot matching prices from an active price list onto quote lines
 */
export const useApplyQuotePriceList = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyQuotePriceList>>, TError,ApplyQuotePriceListMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof applyQuotePriceList>>,
        TError,
        ApplyQuotePriceListMutationVariables,
        TContext
      > => {
      return useMutation(getApplyQuotePriceListMutationOptions(options));
    }

export const getRequestQuoteApprovalUrl = (id: string,) => {




  return `/api/quotes/${id}/request-approval`
}

/**
 * @summary Evaluate the configured discount threshold and request approval when required
 */
export const requestQuoteApproval = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRequest> => {

  return customFetch<QuoteRequest>(getRequestQuoteApprovalUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRequestQuoteApprovalMutationKey = () => ['requestQuoteApproval'] as const;

export const getRequestQuoteApprovalMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestQuoteApproval>>, TError,RequestQuoteApprovalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof requestQuoteApproval>>, TError,RequestQuoteApprovalMutationVariables, TContext> => {

const mutationKey = getRequestQuoteApprovalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof requestQuoteApproval>>, RequestQuoteApprovalMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  requestQuoteApproval(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RequestQuoteApprovalMutationResult = NonNullable<Awaited<ReturnType<typeof requestQuoteApproval>>>

    export type RequestQuoteApprovalMutationError = unknown
    export type RequestQuoteApprovalMutationVariables = {id: string}

    /**
 * @summary Evaluate the configured discount threshold and request approval when required
 */
export const useRequestQuoteApproval = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestQuoteApproval>>, TError,RequestQuoteApprovalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof requestQuoteApproval>>,
        TError,
        RequestQuoteApprovalMutationVariables,
        TContext
      > => {
      return useMutation(getRequestQuoteApprovalMutationOptions(options));
    }

export const getApproveQuoteUrl = (id: string,) => {




  return `/api/quotes/${id}/approve`
}

/**
 * @summary Approve a pending quote as an entity administrator
 */
export const approveQuote = async (id: string,
    quoteApprovalAction: QuoteApprovalAction, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<QuoteRequest>(getApproveQuoteUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(quoteApprovalAction)
  }
);}





export const getApproveQuoteMutationKey = () => ['approveQuote'] as const;

export const getApproveQuoteMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveQuote>>, TError,ApproveQuoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof approveQuote>>, TError,ApproveQuoteMutationVariables, TContext> => {

const mutationKey = getApproveQuoteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof approveQuote>>, ApproveQuoteMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  approveQuote(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApproveQuoteMutationResult = NonNullable<Awaited<ReturnType<typeof approveQuote>>>
    export type ApproveQuoteMutationBody = QuoteApprovalAction
    export type ApproveQuoteMutationError = Error
    export type ApproveQuoteMutationVariables = {id: string;data: QuoteApprovalAction}

    /**
 * @summary Approve a pending quote as an entity administrator
 */
export const useApproveQuote = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveQuote>>, TError,ApproveQuoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof approveQuote>>,
        TError,
        ApproveQuoteMutationVariables,
        TContext
      > => {
      return useMutation(getApproveQuoteMutationOptions(options));
    }

export const getRejectQuoteUrl = (id: string,) => {




  return `/api/quotes/${id}/reject`
}

/**
 * @summary Reject a pending quote as an entity administrator
 */
export const rejectQuote = async (id: string,
    quoteRejectionAction: QuoteRejectionAction, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<QuoteRequest>(getRejectQuoteUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(quoteRejectionAction)
  }
);}





export const getRejectQuoteMutationKey = () => ['rejectQuote'] as const;

export const getRejectQuoteMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rejectQuote>>, TError,RejectQuoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof rejectQuote>>, TError,RejectQuoteMutationVariables, TContext> => {

const mutationKey = getRejectQuoteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof rejectQuote>>, RejectQuoteMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  rejectQuote(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RejectQuoteMutationResult = NonNullable<Awaited<ReturnType<typeof rejectQuote>>>
    export type RejectQuoteMutationBody = QuoteRejectionAction
    export type RejectQuoteMutationError = Error
    export type RejectQuoteMutationVariables = {id: string;data: QuoteRejectionAction}

    /**
 * @summary Reject a pending quote as an entity administrator
 */
export const useRejectQuote = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rejectQuote>>, TError,RejectQuoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof rejectQuote>>,
        TError,
        RejectQuoteMutationVariables,
        TContext
      > => {
      return useMutation(getRejectQuoteMutationOptions(options));
    }

export const getDuplicateQuoteUrl = (id: string,) => {




  return `/api/quotes/${id}/duplicate`
}

/**
 * @summary Duplicate a quote and its immutable line-price snapshots as a new draft
 */
export const duplicateQuote = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRequest> => {

  return customFetch<QuoteRequest>(getDuplicateQuoteUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getDuplicateQuoteMutationKey = () => ['duplicateQuote'] as const;

export const getDuplicateQuoteMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof duplicateQuote>>, TError,DuplicateQuoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof duplicateQuote>>, TError,DuplicateQuoteMutationVariables, TContext> => {

const mutationKey = getDuplicateQuoteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof duplicateQuote>>, DuplicateQuoteMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  duplicateQuote(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DuplicateQuoteMutationResult = NonNullable<Awaited<ReturnType<typeof duplicateQuote>>>

    export type DuplicateQuoteMutationError = unknown
    export type DuplicateQuoteMutationVariables = {id: string}

    /**
 * @summary Duplicate a quote and its immutable line-price snapshots as a new draft
 */
export const useDuplicateQuote = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof duplicateQuote>>, TError,DuplicateQuoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof duplicateQuote>>,
        TError,
        DuplicateQuoteMutationVariables,
        TContext
      > => {
      return useMutation(getDuplicateQuoteMutationOptions(options));
    }

export const getListQuoteRevisionsUrl = (id: string,
    params?: ListQuoteRevisionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/quotes/${id}/revisions?${stringifiedParams}` : `/api/quotes/${id}/revisions`
}

/**
 * @summary List immutable numbered quote revisions
 */
export const listQuoteRevisions = async (id: string,
    params?: ListQuoteRevisionsParams, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRevisionList> => {

  return customFetch<QuoteRevisionList>(getListQuoteRevisionsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListQuoteRevisionsQueryKey = (id: string,
    params?: ListQuoteRevisionsParams,) => {
    return [
    `/api/quotes/${id}/revisions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListQuoteRevisionsQueryOptions = <TData = Awaited<ReturnType<typeof listQuoteRevisions>>, TError = unknown>(id: string,
    params?: ListQuoteRevisionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listQuoteRevisions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListQuoteRevisionsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listQuoteRevisions>>> = ({ signal }) => listQuoteRevisions(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listQuoteRevisions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListQuoteRevisionsQueryResult = NonNullable<Awaited<ReturnType<typeof listQuoteRevisions>>>
export type ListQuoteRevisionsQueryError = unknown


/**
 * @summary List immutable numbered quote revisions
 */

export function useListQuoteRevisions<TData = Awaited<ReturnType<typeof listQuoteRevisions>>, TError = unknown>(
 id: string,
    params?: ListQuoteRevisionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listQuoteRevisions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListQuoteRevisionsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRestoreQuoteRevisionUrl = (id: string,
    revisionId: string,) => {




  return `/api/quotes/${id}/revisions/${revisionId}/restore`
}

/**
 * @summary Restore an editable quote from an immutable revision snapshot
 */
export const restoreQuoteRevision = async (id: string,
    revisionId: string, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRequest> => {

  return customFetch<QuoteRequest>(getRestoreQuoteRevisionUrl(id,revisionId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRestoreQuoteRevisionMutationKey = () => ['restoreQuoteRevision'] as const;

export const getRestoreQuoteRevisionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof restoreQuoteRevision>>, TError,RestoreQuoteRevisionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof restoreQuoteRevision>>, TError,RestoreQuoteRevisionMutationVariables, TContext> => {

const mutationKey = getRestoreQuoteRevisionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof restoreQuoteRevision>>, RestoreQuoteRevisionMutationVariables> = (props) => {
          const {id,revisionId} = props ?? {};

          return  restoreQuoteRevision(id,revisionId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RestoreQuoteRevisionMutationResult = NonNullable<Awaited<ReturnType<typeof restoreQuoteRevision>>>

    export type RestoreQuoteRevisionMutationError = Error
    export type RestoreQuoteRevisionMutationVariables = {id: string;revisionId: string}

    /**
 * @summary Restore an editable quote from an immutable revision snapshot
 */
export const useRestoreQuoteRevision = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof restoreQuoteRevision>>, TError,RestoreQuoteRevisionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof restoreQuoteRevision>>,
        TError,
        RestoreQuoteRevisionMutationVariables,
        TContext
      > => {
      return useMutation(getRestoreQuoteRevisionMutationOptions(options));
    }

export const getListQuoteEventsUrl = (id: string,
    params?: ListQuoteEventsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/quotes/${id}/events?${stringifiedParams}` : `/api/quotes/${id}/events`
}

/**
 * @summary List the append-only quote event timeline
 */
export const listQuoteEvents = async (id: string,
    params?: ListQuoteEventsParams, options?: Parameters<typeof customFetch>[1]): Promise<QuoteEventList> => {

  return customFetch<QuoteEventList>(getListQuoteEventsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListQuoteEventsQueryKey = (id: string,
    params?: ListQuoteEventsParams,) => {
    return [
    `/api/quotes/${id}/events`, ...(params ? [params] : [])
    ] as const;
    }


export const getListQuoteEventsQueryOptions = <TData = Awaited<ReturnType<typeof listQuoteEvents>>, TError = unknown>(id: string,
    params?: ListQuoteEventsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listQuoteEvents>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListQuoteEventsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listQuoteEvents>>> = ({ signal }) => listQuoteEvents(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listQuoteEvents>>, TError, TData> & { queryKey: QueryKey }
}

export type ListQuoteEventsQueryResult = NonNullable<Awaited<ReturnType<typeof listQuoteEvents>>>
export type ListQuoteEventsQueryError = unknown


/**
 * @summary List the append-only quote event timeline
 */

export function useListQuoteEvents<TData = Awaited<ReturnType<typeof listQuoteEvents>>, TError = unknown>(
 id: string,
    params?: ListQuoteEventsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listQuoteEvents>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListQuoteEventsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListOpportunityQuotesUrl = (id: string,
    params?: ListOpportunityQuotesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/opportunities/${id}/quotes?${stringifiedParams}` : `/api/opportunities/${id}/quotes`
}

/**
 * @summary List quotes linked to an opportunity
 */
export const listOpportunityQuotes = async (id: string,
    params?: ListOpportunityQuotesParams, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRequestList> => {

  return customFetch<QuoteRequestList>(getListOpportunityQuotesUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListOpportunityQuotesQueryKey = (id: string,
    params?: ListOpportunityQuotesParams,) => {
    return [
    `/api/opportunities/${id}/quotes`, ...(params ? [params] : [])
    ] as const;
    }


export const getListOpportunityQuotesQueryOptions = <TData = Awaited<ReturnType<typeof listOpportunityQuotes>>, TError = unknown>(id: string,
    params?: ListOpportunityQuotesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOpportunityQuotes>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListOpportunityQuotesQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listOpportunityQuotes>>> = ({ signal }) => listOpportunityQuotes(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listOpportunityQuotes>>, TError, TData> & { queryKey: QueryKey }
}

export type ListOpportunityQuotesQueryResult = NonNullable<Awaited<ReturnType<typeof listOpportunityQuotes>>>
export type ListOpportunityQuotesQueryError = unknown


/**
 * @summary List quotes linked to an opportunity
 */

export function useListOpportunityQuotes<TData = Awaited<ReturnType<typeof listOpportunityQuotes>>, TError = unknown>(
 id: string,
    params?: ListOpportunityQuotesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listOpportunityQuotes>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListOpportunityQuotesQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListAccountQuotesUrl = (id: string,
    params?: ListAccountQuotesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/accounts/${id}/quotes?${stringifiedParams}` : `/api/accounts/${id}/quotes`
}

/**
 * @summary List quotes linked to an account
 */
export const listAccountQuotes = async (id: string,
    params?: ListAccountQuotesParams, options?: Parameters<typeof customFetch>[1]): Promise<QuoteRequestList> => {

  return customFetch<QuoteRequestList>(getListAccountQuotesUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAccountQuotesQueryKey = (id: string,
    params?: ListAccountQuotesParams,) => {
    return [
    `/api/accounts/${id}/quotes`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAccountQuotesQueryOptions = <TData = Awaited<ReturnType<typeof listAccountQuotes>>, TError = unknown>(id: string,
    params?: ListAccountQuotesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAccountQuotes>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAccountQuotesQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAccountQuotes>>> = ({ signal }) => listAccountQuotes(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAccountQuotes>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAccountQuotesQueryResult = NonNullable<Awaited<ReturnType<typeof listAccountQuotes>>>
export type ListAccountQuotesQueryError = unknown


/**
 * @summary List quotes linked to an account
 */

export function useListAccountQuotes<TData = Awaited<ReturnType<typeof listAccountQuotes>>, TError = unknown>(
 id: string,
    params?: ListAccountQuotesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAccountQuotes>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAccountQuotesQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPublicQuoteUrl = (token: string,) => {




  return `/api/public/quotes/${token}`
}

/**
 * @summary View a quote through its opaque customer decision token
 */
export const getPublicQuote = async (token: string, options?: Parameters<typeof customFetch>[1]): Promise<PublicQuoteSummary> => {

  return customFetch<PublicQuoteSummary>(getGetPublicQuoteUrl(token),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPublicQuoteQueryKey = (token: string,) => {
    return [
    `/api/public/quotes/${token}`
    ] as const;
    }


export const getGetPublicQuoteQueryOptions = <TData = Awaited<ReturnType<typeof getPublicQuote>>, TError = Error>(token: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicQuote>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPublicQuoteQueryKey(token);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPublicQuote>>> = ({ signal }) => getPublicQuote(token, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: token !== null && token !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPublicQuote>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPublicQuoteQueryResult = NonNullable<Awaited<ReturnType<typeof getPublicQuote>>>
export type GetPublicQuoteQueryError = Error


/**
 * @summary View a quote through its opaque customer decision token
 */

export function useGetPublicQuote<TData = Awaited<ReturnType<typeof getPublicQuote>>, TError = Error>(
 token: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicQuote>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPublicQuoteQueryOptions(token,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDownloadPublicQuotePdfUrl = (token: string,) => {




  return `/api/public/quotes/${token}/pdf`
}

/**
 * @summary Download the stored quote PDF through an active customer token
 */
export const downloadPublicQuotePdf = async (token: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadPublicQuotePdfUrl(token),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadPublicQuotePdfQueryKey = (token: string,) => {
    return [
    `/api/public/quotes/${token}/pdf`
    ] as const;
    }


export const getDownloadPublicQuotePdfQueryOptions = <TData = Awaited<ReturnType<typeof downloadPublicQuotePdf>>, TError = Error>(token: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadPublicQuotePdf>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadPublicQuotePdfQueryKey(token);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadPublicQuotePdf>>> = ({ signal }) => downloadPublicQuotePdf(token, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: token !== null && token !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadPublicQuotePdf>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadPublicQuotePdfQueryResult = NonNullable<Awaited<ReturnType<typeof downloadPublicQuotePdf>>>
export type DownloadPublicQuotePdfQueryError = Error


/**
 * @summary Download the stored quote PDF through an active customer token
 */

export function useDownloadPublicQuotePdf<TData = Awaited<ReturnType<typeof downloadPublicQuotePdf>>, TError = Error>(
 token: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadPublicQuotePdf>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadPublicQuotePdfQueryOptions(token,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getAcceptPublicQuoteUrl = (token: string,) => {




  return `/api/public/quotes/${token}/accept`
}

/**
 * @summary Accept a quote once through its active customer token
 */
export const acceptPublicQuote = async (token: string,
    publicQuoteDecision: PublicQuoteDecision, options?: Parameters<typeof customFetch>[1]): Promise<PublicQuoteDecisionResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PublicQuoteDecisionResult>(getAcceptPublicQuoteUrl(token),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(publicQuoteDecision)
  }
);}





export const getAcceptPublicQuoteMutationKey = () => ['acceptPublicQuote'] as const;

export const getAcceptPublicQuoteMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof acceptPublicQuote>>, TError,AcceptPublicQuoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof acceptPublicQuote>>, TError,AcceptPublicQuoteMutationVariables, TContext> => {

const mutationKey = getAcceptPublicQuoteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof acceptPublicQuote>>, AcceptPublicQuoteMutationVariables> = (props) => {
          const {token,data} = props ?? {};

          return  acceptPublicQuote(token,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AcceptPublicQuoteMutationResult = NonNullable<Awaited<ReturnType<typeof acceptPublicQuote>>>
    export type AcceptPublicQuoteMutationBody = PublicQuoteDecision
    export type AcceptPublicQuoteMutationError = Error
    export type AcceptPublicQuoteMutationVariables = {token: string;data: PublicQuoteDecision}

    /**
 * @summary Accept a quote once through its active customer token
 */
export const useAcceptPublicQuote = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof acceptPublicQuote>>, TError,AcceptPublicQuoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof acceptPublicQuote>>,
        TError,
        AcceptPublicQuoteMutationVariables,
        TContext
      > => {
      return useMutation(getAcceptPublicQuoteMutationOptions(options));
    }

export const getDeclinePublicQuoteUrl = (token: string,) => {




  return `/api/public/quotes/${token}/decline`
}

/**
 * @summary Decline a quote once through its active customer token
 */
export const declinePublicQuote = async (token: string,
    publicQuoteDecline: PublicQuoteDecline, options?: Parameters<typeof customFetch>[1]): Promise<PublicQuoteDecisionResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PublicQuoteDecisionResult>(getDeclinePublicQuoteUrl(token),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(publicQuoteDecline)
  }
);}





export const getDeclinePublicQuoteMutationKey = () => ['declinePublicQuote'] as const;

export const getDeclinePublicQuoteMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof declinePublicQuote>>, TError,DeclinePublicQuoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof declinePublicQuote>>, TError,DeclinePublicQuoteMutationVariables, TContext> => {

const mutationKey = getDeclinePublicQuoteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof declinePublicQuote>>, DeclinePublicQuoteMutationVariables> = (props) => {
          const {token,data} = props ?? {};

          return  declinePublicQuote(token,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeclinePublicQuoteMutationResult = NonNullable<Awaited<ReturnType<typeof declinePublicQuote>>>
    export type DeclinePublicQuoteMutationBody = PublicQuoteDecline
    export type DeclinePublicQuoteMutationError = Error
    export type DeclinePublicQuoteMutationVariables = {token: string;data: PublicQuoteDecline}

    /**
 * @summary Decline a quote once through its active customer token
 */
export const useDeclinePublicQuote = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof declinePublicQuote>>, TError,DeclinePublicQuoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof declinePublicQuote>>,
        TError,
        DeclinePublicQuoteMutationVariables,
        TContext
      > => {
      return useMutation(getDeclinePublicQuoteMutationOptions(options));
    }

export const getGetXeroConnectionUrl = () => {




  return `/api/xero/connection`
}

/**
 * @summary Get the mock Xero organisation connection state
 */
export const getXeroConnection = async ( options?: Parameters<typeof customFetch>[1]): Promise<XeroConnectionState> => {

  return customFetch<XeroConnectionState>(getGetXeroConnectionUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetXeroConnectionQueryKey = () => {
    return [
    `/api/xero/connection`
    ] as const;
    }


export const getGetXeroConnectionQueryOptions = <TData = Awaited<ReturnType<typeof getXeroConnection>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getXeroConnection>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetXeroConnectionQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getXeroConnection>>> = ({ signal }) => getXeroConnection({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getXeroConnection>>, TError, TData> & { queryKey: QueryKey }
}

export type GetXeroConnectionQueryResult = NonNullable<Awaited<ReturnType<typeof getXeroConnection>>>
export type GetXeroConnectionQueryError = unknown


/**
 * @summary Get the mock Xero organisation connection state
 */

export function useGetXeroConnection<TData = Awaited<ReturnType<typeof getXeroConnection>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getXeroConnection>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetXeroConnectionQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConnectXeroOrganisationUrl = () => {




  return `/api/xero/connection/connect`
}

/**
 * @summary Connect (or reconnect) the mock Xero organisation
 */
export const connectXeroOrganisation = async (xeroConnectionConnect?: XeroConnectionConnect, options?: Parameters<typeof customFetch>[1]): Promise<XeroConnection> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<XeroConnection>(getConnectXeroOrganisationUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(xeroConnectionConnect)
  }
);}





export const getConnectXeroOrganisationMutationKey = () => ['connectXeroOrganisation'] as const;

export const getConnectXeroOrganisationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof connectXeroOrganisation>>, TError,ConnectXeroOrganisationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof connectXeroOrganisation>>, TError,ConnectXeroOrganisationMutationVariables, TContext> => {

const mutationKey = getConnectXeroOrganisationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof connectXeroOrganisation>>, ConnectXeroOrganisationMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  connectXeroOrganisation(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConnectXeroOrganisationMutationResult = NonNullable<Awaited<ReturnType<typeof connectXeroOrganisation>>>
    export type ConnectXeroOrganisationMutationBody = XeroConnectionConnect | undefined
    export type ConnectXeroOrganisationMutationError = unknown
    export type ConnectXeroOrganisationMutationVariables = {data?: XeroConnectionConnect}

    /**
 * @summary Connect (or reconnect) the mock Xero organisation
 */
export const useConnectXeroOrganisation = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof connectXeroOrganisation>>, TError,ConnectXeroOrganisationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof connectXeroOrganisation>>,
        TError,
        ConnectXeroOrganisationMutationVariables,
        TContext
      > => {
      return useMutation(getConnectXeroOrganisationMutationOptions(options));
    }

export const getDisconnectXeroOrganisationUrl = () => {




  return `/api/xero/connection/disconnect`
}

/**
 * @summary Disconnect the mock Xero organisation
 */
export const disconnectXeroOrganisation = async ( options?: Parameters<typeof customFetch>[1]): Promise<XeroConnection> => {

  return customFetch<XeroConnection>(getDisconnectXeroOrganisationUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getDisconnectXeroOrganisationMutationKey = () => ['disconnectXeroOrganisation'] as const;

export const getDisconnectXeroOrganisationMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectXeroOrganisation>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof disconnectXeroOrganisation>>, TError,void, TContext> => {

const mutationKey = getDisconnectXeroOrganisationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof disconnectXeroOrganisation>>, void> = () => {


          return  disconnectXeroOrganisation(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DisconnectXeroOrganisationMutationResult = NonNullable<Awaited<ReturnType<typeof disconnectXeroOrganisation>>>

    export type DisconnectXeroOrganisationMutationError = Error


    /**
 * @summary Disconnect the mock Xero organisation
 */
export const useDisconnectXeroOrganisation = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectXeroOrganisation>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof disconnectXeroOrganisation>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getDisconnectXeroOrganisationMutationOptions(options));
    }

export const getListXeroItemsUrl = (params?: ListXeroItemsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/xero/items?${stringifiedParams}` : `/api/xero/items`
}

/**
 * @summary List mock Xero inventory items pulled in for the quote builder
 */
export const listXeroItems = async (params?: ListXeroItemsParams, options?: Parameters<typeof customFetch>[1]): Promise<XeroItemList> => {

  return customFetch<XeroItemList>(getListXeroItemsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListXeroItemsQueryKey = (params?: ListXeroItemsParams,) => {
    return [
    `/api/xero/items`, ...(params ? [params] : [])
    ] as const;
    }


export const getListXeroItemsQueryOptions = <TData = Awaited<ReturnType<typeof listXeroItems>>, TError = unknown>(params?: ListXeroItemsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listXeroItems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListXeroItemsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listXeroItems>>> = ({ signal }) => listXeroItems(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listXeroItems>>, TError, TData> & { queryKey: QueryKey }
}

export type ListXeroItemsQueryResult = NonNullable<Awaited<ReturnType<typeof listXeroItems>>>
export type ListXeroItemsQueryError = unknown


/**
 * @summary List mock Xero inventory items pulled in for the quote builder
 */

export function useListXeroItems<TData = Awaited<ReturnType<typeof listXeroItems>>, TError = unknown>(
 params?: ListXeroItemsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listXeroItems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListXeroItemsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListXeroContactsUrl = (params?: ListXeroContactsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/xero/contacts?${stringifiedParams}` : `/api/xero/contacts`
}

/**
 * @summary List mock Xero contacts pulled in for quote requester details
 */
export const listXeroContacts = async (params?: ListXeroContactsParams, options?: Parameters<typeof customFetch>[1]): Promise<XeroContactList> => {

  return customFetch<XeroContactList>(getListXeroContactsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListXeroContactsQueryKey = (params?: ListXeroContactsParams,) => {
    return [
    `/api/xero/contacts`, ...(params ? [params] : [])
    ] as const;
    }


export const getListXeroContactsQueryOptions = <TData = Awaited<ReturnType<typeof listXeroContacts>>, TError = unknown>(params?: ListXeroContactsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listXeroContacts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListXeroContactsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listXeroContacts>>> = ({ signal }) => listXeroContacts(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listXeroContacts>>, TError, TData> & { queryKey: QueryKey }
}

export type ListXeroContactsQueryResult = NonNullable<Awaited<ReturnType<typeof listXeroContacts>>>
export type ListXeroContactsQueryError = unknown


/**
 * @summary List mock Xero contacts pulled in for quote requester details
 */

export function useListXeroContacts<TData = Awaited<ReturnType<typeof listXeroContacts>>, TError = unknown>(
 params?: ListXeroContactsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listXeroContacts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListXeroContactsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSyncXeroCatalogUrl = () => {




  return `/api/xero/catalog/sync`
}

/**
 * Regenerates the mock Xero catalogue (items and contacts), replacing existing synced rows.
 * @summary Sync inventory items and contacts from the mock Xero organisation
 */
export const syncXeroCatalog = async ( options?: Parameters<typeof customFetch>[1]): Promise<XeroCatalogSyncResult> => {

  return customFetch<XeroCatalogSyncResult>(getSyncXeroCatalogUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getSyncXeroCatalogMutationKey = () => ['syncXeroCatalog'] as const;

export const getSyncXeroCatalogMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof syncXeroCatalog>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof syncXeroCatalog>>, TError,void, TContext> => {

const mutationKey = getSyncXeroCatalogMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof syncXeroCatalog>>, void> = () => {


          return  syncXeroCatalog(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SyncXeroCatalogMutationResult = NonNullable<Awaited<ReturnType<typeof syncXeroCatalog>>>

    export type SyncXeroCatalogMutationError = Error


    /**
 * @summary Sync inventory items and contacts from the mock Xero organisation
 */
export const useSyncXeroCatalog = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof syncXeroCatalog>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof syncXeroCatalog>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getSyncXeroCatalogMutationOptions(options));
    }

export const getClearXeroCatalogUrl = () => {




  return `/api/xero/catalog`
}

/**
 * @summary Clear the synced mock Xero catalogue
 */
export const clearXeroCatalog = async ( options?: Parameters<typeof customFetch>[1]): Promise<XeroCatalogClearResult> => {

  return customFetch<XeroCatalogClearResult>(getClearXeroCatalogUrl(),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getClearXeroCatalogMutationKey = () => ['clearXeroCatalog'] as const;

export const getClearXeroCatalogMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearXeroCatalog>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof clearXeroCatalog>>, TError,void, TContext> => {

const mutationKey = getClearXeroCatalogMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clearXeroCatalog>>, void> = () => {


          return  clearXeroCatalog(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClearXeroCatalogMutationResult = NonNullable<Awaited<ReturnType<typeof clearXeroCatalog>>>

    export type ClearXeroCatalogMutationError = unknown


    /**
 * @summary Clear the synced mock Xero catalogue
 */
export const useClearXeroCatalog = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearXeroCatalog>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof clearXeroCatalog>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getClearXeroCatalogMutationOptions(options));
    }

export const getListXeroInvoicesUrl = (params?: ListXeroInvoicesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/xero/invoices?${stringifiedParams}` : `/api/xero/invoices`
}

/**
 * @summary List mock Xero invoices
 */
export const listXeroInvoices = async (params?: ListXeroInvoicesParams, options?: Parameters<typeof customFetch>[1]): Promise<XeroInvoiceList> => {

  return customFetch<XeroInvoiceList>(getListXeroInvoicesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListXeroInvoicesQueryKey = (params?: ListXeroInvoicesParams,) => {
    return [
    `/api/xero/invoices`, ...(params ? [params] : [])
    ] as const;
    }


export const getListXeroInvoicesQueryOptions = <TData = Awaited<ReturnType<typeof listXeroInvoices>>, TError = unknown>(params?: ListXeroInvoicesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listXeroInvoices>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListXeroInvoicesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listXeroInvoices>>> = ({ signal }) => listXeroInvoices(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listXeroInvoices>>, TError, TData> & { queryKey: QueryKey }
}

export type ListXeroInvoicesQueryResult = NonNullable<Awaited<ReturnType<typeof listXeroInvoices>>>
export type ListXeroInvoicesQueryError = unknown


/**
 * @summary List mock Xero invoices
 */

export function useListXeroInvoices<TData = Awaited<ReturnType<typeof listXeroInvoices>>, TError = unknown>(
 params?: ListXeroInvoicesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listXeroInvoices>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListXeroInvoicesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetXeroInvoiceUrl = (id: string,) => {




  return `/api/xero/invoices/${id}`
}

/**
 * @summary Get a mock Xero invoice with its lines
 */
export const getXeroInvoice = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<XeroInvoiceDetail> => {

  return customFetch<XeroInvoiceDetail>(getGetXeroInvoiceUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetXeroInvoiceQueryKey = (id: string,) => {
    return [
    `/api/xero/invoices/${id}`
    ] as const;
    }


export const getGetXeroInvoiceQueryOptions = <TData = Awaited<ReturnType<typeof getXeroInvoice>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getXeroInvoice>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetXeroInvoiceQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getXeroInvoice>>> = ({ signal }) => getXeroInvoice(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getXeroInvoice>>, TError, TData> & { queryKey: QueryKey }
}

export type GetXeroInvoiceQueryResult = NonNullable<Awaited<ReturnType<typeof getXeroInvoice>>>
export type GetXeroInvoiceQueryError = Error


/**
 * @summary Get a mock Xero invoice with its lines
 */

export function useGetXeroInvoice<TData = Awaited<ReturnType<typeof getXeroInvoice>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getXeroInvoice>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetXeroInvoiceQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateXeroInvoiceFromQuoteUrl = () => {




  return `/api/xero/invoices/from-quote`
}

/**
 * @summary Generate a mock Xero draft invoice from a sent or accepted quote request
 */
export const createXeroInvoiceFromQuote = async (xeroInvoiceFromQuoteCreate: XeroInvoiceFromQuoteCreate, options?: Parameters<typeof customFetch>[1]): Promise<XeroInvoiceDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<XeroInvoiceDetail>(getCreateXeroInvoiceFromQuoteUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(xeroInvoiceFromQuoteCreate)
  }
);}





export const getCreateXeroInvoiceFromQuoteMutationKey = () => ['createXeroInvoiceFromQuote'] as const;

export const getCreateXeroInvoiceFromQuoteMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createXeroInvoiceFromQuote>>, TError,CreateXeroInvoiceFromQuoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createXeroInvoiceFromQuote>>, TError,CreateXeroInvoiceFromQuoteMutationVariables, TContext> => {

const mutationKey = getCreateXeroInvoiceFromQuoteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createXeroInvoiceFromQuote>>, CreateXeroInvoiceFromQuoteMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createXeroInvoiceFromQuote(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateXeroInvoiceFromQuoteMutationResult = NonNullable<Awaited<ReturnType<typeof createXeroInvoiceFromQuote>>>
    export type CreateXeroInvoiceFromQuoteMutationBody = XeroInvoiceFromQuoteCreate
    export type CreateXeroInvoiceFromQuoteMutationError = Error
    export type CreateXeroInvoiceFromQuoteMutationVariables = {data: XeroInvoiceFromQuoteCreate}

    /**
 * @summary Generate a mock Xero draft invoice from a sent or accepted quote request
 */
export const useCreateXeroInvoiceFromQuote = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createXeroInvoiceFromQuote>>, TError,CreateXeroInvoiceFromQuoteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createXeroInvoiceFromQuote>>,
        TError,
        CreateXeroInvoiceFromQuoteMutationVariables,
        TContext
      > => {
      return useMutation(getCreateXeroInvoiceFromQuoteMutationOptions(options));
    }

export const getUpdateXeroInvoiceStatusUrl = (id: string,) => {




  return `/api/xero/invoices/${id}/status`
}

/**
 * @summary Transition a mock Xero invoice to the next lifecycle status
 */
export const updateXeroInvoiceStatus = async (id: string,
    xeroInvoiceStatusUpdate: XeroInvoiceStatusUpdate, options?: Parameters<typeof customFetch>[1]): Promise<XeroInvoiceDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<XeroInvoiceDetail>(getUpdateXeroInvoiceStatusUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(xeroInvoiceStatusUpdate)
  }
);}





export const getUpdateXeroInvoiceStatusMutationKey = () => ['updateXeroInvoiceStatus'] as const;

export const getUpdateXeroInvoiceStatusMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateXeroInvoiceStatus>>, TError,UpdateXeroInvoiceStatusMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateXeroInvoiceStatus>>, TError,UpdateXeroInvoiceStatusMutationVariables, TContext> => {

const mutationKey = getUpdateXeroInvoiceStatusMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateXeroInvoiceStatus>>, UpdateXeroInvoiceStatusMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateXeroInvoiceStatus(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateXeroInvoiceStatusMutationResult = NonNullable<Awaited<ReturnType<typeof updateXeroInvoiceStatus>>>
    export type UpdateXeroInvoiceStatusMutationBody = XeroInvoiceStatusUpdate
    export type UpdateXeroInvoiceStatusMutationError = Error
    export type UpdateXeroInvoiceStatusMutationVariables = {id: string;data: XeroInvoiceStatusUpdate}

    /**
 * @summary Transition a mock Xero invoice to the next lifecycle status
 */
export const useUpdateXeroInvoiceStatus = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateXeroInvoiceStatus>>, TError,UpdateXeroInvoiceStatusMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateXeroInvoiceStatus>>,
        TError,
        UpdateXeroInvoiceStatusMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateXeroInvoiceStatusMutationOptions(options));
    }

export const getListXeroBankTransactionsUrl = (params?: ListXeroBankTransactionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/xero/bank-transactions?${stringifiedParams}` : `/api/xero/bank-transactions`
}

/**
 * @summary List mock bank feed statement lines
 */
export const listXeroBankTransactions = async (params?: ListXeroBankTransactionsParams, options?: Parameters<typeof customFetch>[1]): Promise<XeroBankTransactionList> => {

  return customFetch<XeroBankTransactionList>(getListXeroBankTransactionsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListXeroBankTransactionsQueryKey = (params?: ListXeroBankTransactionsParams,) => {
    return [
    `/api/xero/bank-transactions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListXeroBankTransactionsQueryOptions = <TData = Awaited<ReturnType<typeof listXeroBankTransactions>>, TError = unknown>(params?: ListXeroBankTransactionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listXeroBankTransactions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListXeroBankTransactionsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listXeroBankTransactions>>> = ({ signal }) => listXeroBankTransactions(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listXeroBankTransactions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListXeroBankTransactionsQueryResult = NonNullable<Awaited<ReturnType<typeof listXeroBankTransactions>>>
export type ListXeroBankTransactionsQueryError = unknown


/**
 * @summary List mock bank feed statement lines
 */

export function useListXeroBankTransactions<TData = Awaited<ReturnType<typeof listXeroBankTransactions>>, TError = unknown>(
 params?: ListXeroBankTransactionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listXeroBankTransactions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListXeroBankTransactionsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getClearXeroBankTransactionsUrl = () => {




  return `/api/xero/bank-transactions`
}

/**
 * @summary Clear all mock bank feed statement lines
 */
export const clearXeroBankTransactions = async ( options?: Parameters<typeof customFetch>[1]): Promise<XeroBankTransactionClearResult> => {

  return customFetch<XeroBankTransactionClearResult>(getClearXeroBankTransactionsUrl(),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getClearXeroBankTransactionsMutationKey = () => ['clearXeroBankTransactions'] as const;

export const getClearXeroBankTransactionsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearXeroBankTransactions>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof clearXeroBankTransactions>>, TError,void, TContext> => {

const mutationKey = getClearXeroBankTransactionsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clearXeroBankTransactions>>, void> = () => {


          return  clearXeroBankTransactions(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClearXeroBankTransactionsMutationResult = NonNullable<Awaited<ReturnType<typeof clearXeroBankTransactions>>>

    export type ClearXeroBankTransactionsMutationError = unknown


    /**
 * @summary Clear all mock bank feed statement lines
 */
export const useClearXeroBankTransactions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearXeroBankTransactions>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof clearXeroBankTransactions>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getClearXeroBankTransactionsMutationOptions(options));
    }

export const getGenerateXeroBankFeedUrl = () => {




  return `/api/xero/bank-transactions/generate`
}

/**
 * Replaces any existing statement lines with a deterministic mix of exact matches, partial payments, typo'd references, duplicates and unrelated noise, so the reconciliation demo has realistic material.
 * @summary Regenerate the mock bank feed from authorised invoices
 */
export const generateXeroBankFeed = async ( options?: Parameters<typeof customFetch>[1]): Promise<XeroBankFeedGenerationResult> => {

  return customFetch<XeroBankFeedGenerationResult>(getGenerateXeroBankFeedUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getGenerateXeroBankFeedMutationKey = () => ['generateXeroBankFeed'] as const;

export const getGenerateXeroBankFeedMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateXeroBankFeed>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof generateXeroBankFeed>>, TError,void, TContext> => {

const mutationKey = getGenerateXeroBankFeedMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof generateXeroBankFeed>>, void> = () => {


          return  generateXeroBankFeed(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenerateXeroBankFeedMutationResult = NonNullable<Awaited<ReturnType<typeof generateXeroBankFeed>>>

    export type GenerateXeroBankFeedMutationError = unknown


    /**
 * @summary Regenerate the mock bank feed from authorised invoices
 */
export const useGenerateXeroBankFeed = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateXeroBankFeed>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof generateXeroBankFeed>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getGenerateXeroBankFeedMutationOptions(options));
    }

export const getRunXeroMatchingUrl = () => {




  return `/api/xero/reconciliation/matching/run`
}

/**
 * Scores each unmatched credit statement line against every AUTHORISED invoice using reference similarity, amount proximity and date proximity, then persists the best suggestion per transaction with a confidence tier and human-readable reasons. Previously rejected pairs are not re-suggested.
 * @summary Run the match engine against unmatched bank credits and AUTHORISED invoices
 */
export const runXeroMatching = async ( options?: Parameters<typeof customFetch>[1]): Promise<XeroMatchingRunResult> => {

  return customFetch<XeroMatchingRunResult>(getRunXeroMatchingUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRunXeroMatchingMutationKey = () => ['runXeroMatching'] as const;

export const getRunXeroMatchingMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runXeroMatching>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof runXeroMatching>>, TError,void, TContext> => {

const mutationKey = getRunXeroMatchingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof runXeroMatching>>, void> = () => {


          return  runXeroMatching(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RunXeroMatchingMutationResult = NonNullable<Awaited<ReturnType<typeof runXeroMatching>>>

    export type RunXeroMatchingMutationError = unknown


    /**
 * @summary Run the match engine against unmatched bank credits and AUTHORISED invoices
 */
export const useRunXeroMatching = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runXeroMatching>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof runXeroMatching>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getRunXeroMatchingMutationOptions(options));
    }

export const getListXeroMatchSuggestionsUrl = (params?: ListXeroMatchSuggestionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/xero/reconciliation/suggestions?${stringifiedParams}` : `/api/xero/reconciliation/suggestions`
}

/**
 * @summary List match suggestions with their transaction and invoice
 */
export const listXeroMatchSuggestions = async (params?: ListXeroMatchSuggestionsParams, options?: Parameters<typeof customFetch>[1]): Promise<XeroMatchSuggestionList> => {

  return customFetch<XeroMatchSuggestionList>(getListXeroMatchSuggestionsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListXeroMatchSuggestionsQueryKey = (params?: ListXeroMatchSuggestionsParams,) => {
    return [
    `/api/xero/reconciliation/suggestions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListXeroMatchSuggestionsQueryOptions = <TData = Awaited<ReturnType<typeof listXeroMatchSuggestions>>, TError = unknown>(params?: ListXeroMatchSuggestionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listXeroMatchSuggestions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListXeroMatchSuggestionsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listXeroMatchSuggestions>>> = ({ signal }) => listXeroMatchSuggestions(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listXeroMatchSuggestions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListXeroMatchSuggestionsQueryResult = NonNullable<Awaited<ReturnType<typeof listXeroMatchSuggestions>>>
export type ListXeroMatchSuggestionsQueryError = unknown


/**
 * @summary List match suggestions with their transaction and invoice
 */

export function useListXeroMatchSuggestions<TData = Awaited<ReturnType<typeof listXeroMatchSuggestions>>, TError = unknown>(
 params?: ListXeroMatchSuggestionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listXeroMatchSuggestions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListXeroMatchSuggestionsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getAcceptXeroMatchSuggestionUrl = (id: string,) => {




  return `/api/xero/reconciliation/suggestions/${id}/accept`
}

/**
 * Records a payment against the invoice, marks the statement line reconciled, and marks the invoice PAID once payments cover its total. Partial payments leave the invoice AUTHORISED with an amount outstanding.
 * @summary Accept a suggestion — reconciles the transaction and records a payment
 */
export const acceptXeroMatchSuggestion = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<XeroMatchSuggestion> => {

  return customFetch<XeroMatchSuggestion>(getAcceptXeroMatchSuggestionUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAcceptXeroMatchSuggestionMutationKey = () => ['acceptXeroMatchSuggestion'] as const;

export const getAcceptXeroMatchSuggestionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof acceptXeroMatchSuggestion>>, TError,AcceptXeroMatchSuggestionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof acceptXeroMatchSuggestion>>, TError,AcceptXeroMatchSuggestionMutationVariables, TContext> => {

const mutationKey = getAcceptXeroMatchSuggestionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof acceptXeroMatchSuggestion>>, AcceptXeroMatchSuggestionMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  acceptXeroMatchSuggestion(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AcceptXeroMatchSuggestionMutationResult = NonNullable<Awaited<ReturnType<typeof acceptXeroMatchSuggestion>>>

    export type AcceptXeroMatchSuggestionMutationError = Error
    export type AcceptXeroMatchSuggestionMutationVariables = {id: string}

    /**
 * @summary Accept a suggestion — reconciles the transaction and records a payment
 */
export const useAcceptXeroMatchSuggestion = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof acceptXeroMatchSuggestion>>, TError,AcceptXeroMatchSuggestionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof acceptXeroMatchSuggestion>>,
        TError,
        AcceptXeroMatchSuggestionMutationVariables,
        TContext
      > => {
      return useMutation(getAcceptXeroMatchSuggestionMutationOptions(options));
    }

export const getRejectXeroMatchSuggestionUrl = (id: string,) => {




  return `/api/xero/reconciliation/suggestions/${id}/reject`
}

/**
 * @summary Reject a suggestion so it is not re-proposed by the match engine
 */
export const rejectXeroMatchSuggestion = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<XeroMatchSuggestion> => {

  return customFetch<XeroMatchSuggestion>(getRejectXeroMatchSuggestionUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRejectXeroMatchSuggestionMutationKey = () => ['rejectXeroMatchSuggestion'] as const;

export const getRejectXeroMatchSuggestionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rejectXeroMatchSuggestion>>, TError,RejectXeroMatchSuggestionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof rejectXeroMatchSuggestion>>, TError,RejectXeroMatchSuggestionMutationVariables, TContext> => {

const mutationKey = getRejectXeroMatchSuggestionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof rejectXeroMatchSuggestion>>, RejectXeroMatchSuggestionMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  rejectXeroMatchSuggestion(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RejectXeroMatchSuggestionMutationResult = NonNullable<Awaited<ReturnType<typeof rejectXeroMatchSuggestion>>>

    export type RejectXeroMatchSuggestionMutationError = Error
    export type RejectXeroMatchSuggestionMutationVariables = {id: string}

    /**
 * @summary Reject a suggestion so it is not re-proposed by the match engine
 */
export const useRejectXeroMatchSuggestion = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rejectXeroMatchSuggestion>>, TError,RejectXeroMatchSuggestionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof rejectXeroMatchSuggestion>>,
        TError,
        RejectXeroMatchSuggestionMutationVariables,
        TContext
      > => {
      return useMutation(getRejectXeroMatchSuggestionMutationOptions(options));
    }

export const getManuallyMatchXeroTransactionUrl = () => {




  return `/api/xero/reconciliation/match`
}

/**
 * @summary Manually match a bank statement line to an AUTHORISED invoice
 */
export const manuallyMatchXeroTransaction = async (xeroManualMatchCreate: XeroManualMatchCreate, options?: Parameters<typeof customFetch>[1]): Promise<XeroMatchSuggestion> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<XeroMatchSuggestion>(getManuallyMatchXeroTransactionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(xeroManualMatchCreate)
  }
);}





export const getManuallyMatchXeroTransactionMutationKey = () => ['manuallyMatchXeroTransaction'] as const;

export const getManuallyMatchXeroTransactionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof manuallyMatchXeroTransaction>>, TError,ManuallyMatchXeroTransactionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof manuallyMatchXeroTransaction>>, TError,ManuallyMatchXeroTransactionMutationVariables, TContext> => {

const mutationKey = getManuallyMatchXeroTransactionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof manuallyMatchXeroTransaction>>, ManuallyMatchXeroTransactionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  manuallyMatchXeroTransaction(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ManuallyMatchXeroTransactionMutationResult = NonNullable<Awaited<ReturnType<typeof manuallyMatchXeroTransaction>>>
    export type ManuallyMatchXeroTransactionMutationBody = XeroManualMatchCreate
    export type ManuallyMatchXeroTransactionMutationError = Error
    export type ManuallyMatchXeroTransactionMutationVariables = {data: XeroManualMatchCreate}

    /**
 * @summary Manually match a bank statement line to an AUTHORISED invoice
 */
export const useManuallyMatchXeroTransaction = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof manuallyMatchXeroTransaction>>, TError,ManuallyMatchXeroTransactionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof manuallyMatchXeroTransaction>>,
        TError,
        ManuallyMatchXeroTransactionMutationVariables,
        TContext
      > => {
      return useMutation(getManuallyMatchXeroTransactionMutationOptions(options));
    }

export const getUnmatchXeroBankTransactionUrl = () => {




  return `/api/xero/reconciliation/unmatch`
}

/**
 * Removes the payment recorded for the statement line, returns it to unmatched, and reverts the invoice to AUTHORISED if its remaining payments no longer cover the total.
 * @summary Unmatch a previously reconciled statement line
 */
export const unmatchXeroBankTransaction = async (xeroUnmatchCreate: XeroUnmatchCreate, options?: Parameters<typeof customFetch>[1]): Promise<XeroUnmatchResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<XeroUnmatchResult>(getUnmatchXeroBankTransactionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(xeroUnmatchCreate)
  }
);}





export const getUnmatchXeroBankTransactionMutationKey = () => ['unmatchXeroBankTransaction'] as const;

export const getUnmatchXeroBankTransactionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unmatchXeroBankTransaction>>, TError,UnmatchXeroBankTransactionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof unmatchXeroBankTransaction>>, TError,UnmatchXeroBankTransactionMutationVariables, TContext> => {

const mutationKey = getUnmatchXeroBankTransactionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof unmatchXeroBankTransaction>>, UnmatchXeroBankTransactionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  unmatchXeroBankTransaction(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UnmatchXeroBankTransactionMutationResult = NonNullable<Awaited<ReturnType<typeof unmatchXeroBankTransaction>>>
    export type UnmatchXeroBankTransactionMutationBody = XeroUnmatchCreate
    export type UnmatchXeroBankTransactionMutationError = Error
    export type UnmatchXeroBankTransactionMutationVariables = {data: XeroUnmatchCreate}

    /**
 * @summary Unmatch a previously reconciled statement line
 */
export const useUnmatchXeroBankTransaction = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unmatchXeroBankTransaction>>, TError,UnmatchXeroBankTransactionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof unmatchXeroBankTransaction>>,
        TError,
        UnmatchXeroBankTransactionMutationVariables,
        TContext
      > => {
      return useMutation(getUnmatchXeroBankTransactionMutationOptions(options));
    }

export const getGetXeroReconciliationReportUrl = (params?: GetXeroReconciliationReportParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/xero/reconciliation/report?${stringifiedParams}` : `/api/xero/reconciliation/report`
}

/**
 * @summary Reconciliation validation report and summary dashboard numbers
 */
export const getXeroReconciliationReport = async (params?: GetXeroReconciliationReportParams, options?: Parameters<typeof customFetch>[1]): Promise<XeroReconciliationReport> => {

  return customFetch<XeroReconciliationReport>(getGetXeroReconciliationReportUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetXeroReconciliationReportQueryKey = (params?: GetXeroReconciliationReportParams,) => {
    return [
    `/api/xero/reconciliation/report`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetXeroReconciliationReportQueryOptions = <TData = Awaited<ReturnType<typeof getXeroReconciliationReport>>, TError = unknown>(params?: GetXeroReconciliationReportParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getXeroReconciliationReport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetXeroReconciliationReportQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getXeroReconciliationReport>>> = ({ signal }) => getXeroReconciliationReport(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getXeroReconciliationReport>>, TError, TData> & { queryKey: QueryKey }
}

export type GetXeroReconciliationReportQueryResult = NonNullable<Awaited<ReturnType<typeof getXeroReconciliationReport>>>
export type GetXeroReconciliationReportQueryError = unknown


/**
 * @summary Reconciliation validation report and summary dashboard numbers
 */

export function useGetXeroReconciliationReport<TData = Awaited<ReturnType<typeof getXeroReconciliationReport>>, TError = unknown>(
 params?: GetXeroReconciliationReportParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getXeroReconciliationReport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetXeroReconciliationReportQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListAccountingIntegrationsUrl = () => {




  return `/api/settings/accounting/integrations`
}

/**
 * @summary List accounting platform options for AUS/NZ businesses with their connection state
 */
export const listAccountingIntegrations = async ( options?: Parameters<typeof customFetch>[1]): Promise<AccountingIntegrationList> => {

  return customFetch<AccountingIntegrationList>(getListAccountingIntegrationsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAccountingIntegrationsQueryKey = () => {
    return [
    `/api/settings/accounting/integrations`
    ] as const;
    }


export const getListAccountingIntegrationsQueryOptions = <TData = Awaited<ReturnType<typeof listAccountingIntegrations>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAccountingIntegrations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAccountingIntegrationsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAccountingIntegrations>>> = ({ signal }) => listAccountingIntegrations({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAccountingIntegrations>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAccountingIntegrationsQueryResult = NonNullable<Awaited<ReturnType<typeof listAccountingIntegrations>>>
export type ListAccountingIntegrationsQueryError = unknown


/**
 * @summary List accounting platform options for AUS/NZ businesses with their connection state
 */

export function useListAccountingIntegrations<TData = Awaited<ReturnType<typeof listAccountingIntegrations>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAccountingIntegrations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAccountingIntegrationsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConnectAccountingIntegrationUrl = (platform: AccountingPlatform,) => {




  return `/api/settings/accounting/integrations/${platform}/connect`
}

/**
 * Mock only — credentials are validated for shape and stored locally; no real OAuth handshake or platform API call is made. Connecting Xero also activates the mock Xero organisation used by the CRM invoicing flow.
 * @summary Save OAuth credentials and mock-connect an accounting platform
 */
export const connectAccountingIntegration = async (platform: AccountingPlatform,
    accountingIntegrationConnect: AccountingIntegrationConnect, options?: Parameters<typeof customFetch>[1]): Promise<AccountingIntegration> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AccountingIntegration>(getConnectAccountingIntegrationUrl(platform),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(accountingIntegrationConnect)
  }
);}





export const getConnectAccountingIntegrationMutationKey = () => ['connectAccountingIntegration'] as const;

export const getConnectAccountingIntegrationMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof connectAccountingIntegration>>, TError,ConnectAccountingIntegrationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof connectAccountingIntegration>>, TError,ConnectAccountingIntegrationMutationVariables, TContext> => {

const mutationKey = getConnectAccountingIntegrationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof connectAccountingIntegration>>, ConnectAccountingIntegrationMutationVariables> = (props) => {
          const {platform,data} = props ?? {};

          return  connectAccountingIntegration(platform,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConnectAccountingIntegrationMutationResult = NonNullable<Awaited<ReturnType<typeof connectAccountingIntegration>>>
    export type ConnectAccountingIntegrationMutationBody = AccountingIntegrationConnect
    export type ConnectAccountingIntegrationMutationError = Error
    export type ConnectAccountingIntegrationMutationVariables = {platform: AccountingPlatform;data: AccountingIntegrationConnect}

    /**
 * @summary Save OAuth credentials and mock-connect an accounting platform
 */
export const useConnectAccountingIntegration = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof connectAccountingIntegration>>, TError,ConnectAccountingIntegrationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof connectAccountingIntegration>>,
        TError,
        ConnectAccountingIntegrationMutationVariables,
        TContext
      > => {
      return useMutation(getConnectAccountingIntegrationMutationOptions(options));
    }

export const getDisconnectAccountingIntegrationUrl = (platform: AccountingPlatform,) => {




  return `/api/settings/accounting/integrations/${platform}/disconnect`
}

/**
 * @summary Disconnect an accounting platform and clear its stored secret
 */
export const disconnectAccountingIntegration = async (platform: AccountingPlatform, options?: Parameters<typeof customFetch>[1]): Promise<AccountingIntegration> => {

  return customFetch<AccountingIntegration>(getDisconnectAccountingIntegrationUrl(platform),
  {
    ...options,
    method: 'POST'


  }
);}





export const getDisconnectAccountingIntegrationMutationKey = () => ['disconnectAccountingIntegration'] as const;

export const getDisconnectAccountingIntegrationMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectAccountingIntegration>>, TError,DisconnectAccountingIntegrationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof disconnectAccountingIntegration>>, TError,DisconnectAccountingIntegrationMutationVariables, TContext> => {

const mutationKey = getDisconnectAccountingIntegrationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof disconnectAccountingIntegration>>, DisconnectAccountingIntegrationMutationVariables> = (props) => {
          const {platform} = props ?? {};

          return  disconnectAccountingIntegration(platform,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DisconnectAccountingIntegrationMutationResult = NonNullable<Awaited<ReturnType<typeof disconnectAccountingIntegration>>>

    export type DisconnectAccountingIntegrationMutationError = Error
    export type DisconnectAccountingIntegrationMutationVariables = {platform: AccountingPlatform}

    /**
 * @summary Disconnect an accounting platform and clear its stored secret
 */
export const useDisconnectAccountingIntegration = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectAccountingIntegration>>, TError,DisconnectAccountingIntegrationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof disconnectAccountingIntegration>>,
        TError,
        DisconnectAccountingIntegrationMutationVariables,
        TContext
      > => {
      return useMutation(getDisconnectAccountingIntegrationMutationOptions(options));
    }

export const getListBusinessCaseTemplatesUrl = (params?: ListBusinessCaseTemplatesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/business-case-templates?${stringifiedParams}` : `/api/business-case-templates`
}

/**
 * @summary List business case templates
 */
export const listBusinessCaseTemplates = async (params?: ListBusinessCaseTemplatesParams, options?: Parameters<typeof customFetch>[1]): Promise<BusinessCaseTemplateList> => {

  return customFetch<BusinessCaseTemplateList>(getListBusinessCaseTemplatesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListBusinessCaseTemplatesQueryKey = (params?: ListBusinessCaseTemplatesParams,) => {
    return [
    `/api/business-case-templates`, ...(params ? [params] : [])
    ] as const;
    }


export const getListBusinessCaseTemplatesQueryOptions = <TData = Awaited<ReturnType<typeof listBusinessCaseTemplates>>, TError = unknown>(params?: ListBusinessCaseTemplatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listBusinessCaseTemplates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListBusinessCaseTemplatesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listBusinessCaseTemplates>>> = ({ signal }) => listBusinessCaseTemplates(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listBusinessCaseTemplates>>, TError, TData> & { queryKey: QueryKey }
}

export type ListBusinessCaseTemplatesQueryResult = NonNullable<Awaited<ReturnType<typeof listBusinessCaseTemplates>>>
export type ListBusinessCaseTemplatesQueryError = unknown


/**
 * @summary List business case templates
 */

export function useListBusinessCaseTemplates<TData = Awaited<ReturnType<typeof listBusinessCaseTemplates>>, TError = unknown>(
 params?: ListBusinessCaseTemplatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listBusinessCaseTemplates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListBusinessCaseTemplatesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateBusinessCaseTemplateUrl = () => {




  return `/api/business-case-templates`
}

/**
 * @summary Create a business case template
 */
export const createBusinessCaseTemplate = async (businessCaseTemplateCreate: BusinessCaseTemplateCreate, options?: Parameters<typeof customFetch>[1]): Promise<BusinessCaseTemplate> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<BusinessCaseTemplate>(getCreateBusinessCaseTemplateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(businessCaseTemplateCreate)
  }
);}





export const getCreateBusinessCaseTemplateMutationKey = () => ['createBusinessCaseTemplate'] as const;

export const getCreateBusinessCaseTemplateMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBusinessCaseTemplate>>, TError,CreateBusinessCaseTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createBusinessCaseTemplate>>, TError,CreateBusinessCaseTemplateMutationVariables, TContext> => {

const mutationKey = getCreateBusinessCaseTemplateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createBusinessCaseTemplate>>, CreateBusinessCaseTemplateMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createBusinessCaseTemplate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateBusinessCaseTemplateMutationResult = NonNullable<Awaited<ReturnType<typeof createBusinessCaseTemplate>>>
    export type CreateBusinessCaseTemplateMutationBody = BusinessCaseTemplateCreate
    export type CreateBusinessCaseTemplateMutationError = Error
    export type CreateBusinessCaseTemplateMutationVariables = {data: BusinessCaseTemplateCreate}

    /**
 * @summary Create a business case template
 */
export const useCreateBusinessCaseTemplate = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBusinessCaseTemplate>>, TError,CreateBusinessCaseTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createBusinessCaseTemplate>>,
        TError,
        CreateBusinessCaseTemplateMutationVariables,
        TContext
      > => {
      return useMutation(getCreateBusinessCaseTemplateMutationOptions(options));
    }

export const getGetBusinessCaseTemplateUrl = (id: string,) => {




  return `/api/business-case-templates/${id}`
}

/**
 * @summary Get a business case template
 */
export const getBusinessCaseTemplate = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<BusinessCaseTemplate> => {

  return customFetch<BusinessCaseTemplate>(getGetBusinessCaseTemplateUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetBusinessCaseTemplateQueryKey = (id: string,) => {
    return [
    `/api/business-case-templates/${id}`
    ] as const;
    }


export const getGetBusinessCaseTemplateQueryOptions = <TData = Awaited<ReturnType<typeof getBusinessCaseTemplate>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getBusinessCaseTemplate>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetBusinessCaseTemplateQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getBusinessCaseTemplate>>> = ({ signal }) => getBusinessCaseTemplate(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getBusinessCaseTemplate>>, TError, TData> & { queryKey: QueryKey }
}

export type GetBusinessCaseTemplateQueryResult = NonNullable<Awaited<ReturnType<typeof getBusinessCaseTemplate>>>
export type GetBusinessCaseTemplateQueryError = Error


/**
 * @summary Get a business case template
 */

export function useGetBusinessCaseTemplate<TData = Awaited<ReturnType<typeof getBusinessCaseTemplate>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getBusinessCaseTemplate>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetBusinessCaseTemplateQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateBusinessCaseTemplateUrl = (id: string,) => {




  return `/api/business-case-templates/${id}`
}

/**
 * @summary Update a business case template
 */
export const updateBusinessCaseTemplate = async (id: string,
    businessCaseTemplateUpdate: BusinessCaseTemplateUpdate, options?: Parameters<typeof customFetch>[1]): Promise<BusinessCaseTemplate> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<BusinessCaseTemplate>(getUpdateBusinessCaseTemplateUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(businessCaseTemplateUpdate)
  }
);}





export const getUpdateBusinessCaseTemplateMutationKey = () => ['updateBusinessCaseTemplate'] as const;

export const getUpdateBusinessCaseTemplateMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateBusinessCaseTemplate>>, TError,UpdateBusinessCaseTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateBusinessCaseTemplate>>, TError,UpdateBusinessCaseTemplateMutationVariables, TContext> => {

const mutationKey = getUpdateBusinessCaseTemplateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateBusinessCaseTemplate>>, UpdateBusinessCaseTemplateMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateBusinessCaseTemplate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateBusinessCaseTemplateMutationResult = NonNullable<Awaited<ReturnType<typeof updateBusinessCaseTemplate>>>
    export type UpdateBusinessCaseTemplateMutationBody = BusinessCaseTemplateUpdate
    export type UpdateBusinessCaseTemplateMutationError = Error
    export type UpdateBusinessCaseTemplateMutationVariables = {id: string;data: BusinessCaseTemplateUpdate}

    /**
 * @summary Update a business case template
 */
export const useUpdateBusinessCaseTemplate = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateBusinessCaseTemplate>>, TError,UpdateBusinessCaseTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateBusinessCaseTemplate>>,
        TError,
        UpdateBusinessCaseTemplateMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateBusinessCaseTemplateMutationOptions(options));
    }

export const getDeleteBusinessCaseTemplateUrl = (id: string,) => {




  return `/api/business-case-templates/${id}`
}

/**
 * @summary Delete a business case template (cascades to its saved cases)
 */
export const deleteBusinessCaseTemplate = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteBusinessCaseTemplateUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteBusinessCaseTemplateMutationKey = () => ['deleteBusinessCaseTemplate'] as const;

export const getDeleteBusinessCaseTemplateMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteBusinessCaseTemplate>>, TError,DeleteBusinessCaseTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteBusinessCaseTemplate>>, TError,DeleteBusinessCaseTemplateMutationVariables, TContext> => {

const mutationKey = getDeleteBusinessCaseTemplateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteBusinessCaseTemplate>>, DeleteBusinessCaseTemplateMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteBusinessCaseTemplate(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteBusinessCaseTemplateMutationResult = NonNullable<Awaited<ReturnType<typeof deleteBusinessCaseTemplate>>>

    export type DeleteBusinessCaseTemplateMutationError = Error
    export type DeleteBusinessCaseTemplateMutationVariables = {id: string}

    /**
 * @summary Delete a business case template (cascades to its saved cases)
 */
export const useDeleteBusinessCaseTemplate = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteBusinessCaseTemplate>>, TError,DeleteBusinessCaseTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteBusinessCaseTemplate>>,
        TError,
        DeleteBusinessCaseTemplateMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteBusinessCaseTemplateMutationOptions(options));
    }

export const getListSavedBusinessCasesUrl = (params?: ListSavedBusinessCasesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/saved-business-cases?${stringifiedParams}` : `/api/saved-business-cases`
}

/**
 * @summary List saved business cases
 */
export const listSavedBusinessCases = async (params?: ListSavedBusinessCasesParams, options?: Parameters<typeof customFetch>[1]): Promise<SavedBusinessCaseList> => {

  return customFetch<SavedBusinessCaseList>(getListSavedBusinessCasesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSavedBusinessCasesQueryKey = (params?: ListSavedBusinessCasesParams,) => {
    return [
    `/api/saved-business-cases`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSavedBusinessCasesQueryOptions = <TData = Awaited<ReturnType<typeof listSavedBusinessCases>>, TError = unknown>(params?: ListSavedBusinessCasesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSavedBusinessCases>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSavedBusinessCasesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSavedBusinessCases>>> = ({ signal }) => listSavedBusinessCases(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSavedBusinessCases>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSavedBusinessCasesQueryResult = NonNullable<Awaited<ReturnType<typeof listSavedBusinessCases>>>
export type ListSavedBusinessCasesQueryError = unknown


/**
 * @summary List saved business cases
 */

export function useListSavedBusinessCases<TData = Awaited<ReturnType<typeof listSavedBusinessCases>>, TError = unknown>(
 params?: ListSavedBusinessCasesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSavedBusinessCases>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSavedBusinessCasesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateSavedBusinessCaseUrl = () => {




  return `/api/saved-business-cases`
}

/**
 * @summary Create a saved business case from a template
 */
export const createSavedBusinessCase = async (savedBusinessCaseCreate: SavedBusinessCaseCreate, options?: Parameters<typeof customFetch>[1]): Promise<SavedBusinessCase> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SavedBusinessCase>(getCreateSavedBusinessCaseUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(savedBusinessCaseCreate)
  }
);}





export const getCreateSavedBusinessCaseMutationKey = () => ['createSavedBusinessCase'] as const;

export const getCreateSavedBusinessCaseMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSavedBusinessCase>>, TError,CreateSavedBusinessCaseMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createSavedBusinessCase>>, TError,CreateSavedBusinessCaseMutationVariables, TContext> => {

const mutationKey = getCreateSavedBusinessCaseMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createSavedBusinessCase>>, CreateSavedBusinessCaseMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createSavedBusinessCase(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateSavedBusinessCaseMutationResult = NonNullable<Awaited<ReturnType<typeof createSavedBusinessCase>>>
    export type CreateSavedBusinessCaseMutationBody = SavedBusinessCaseCreate
    export type CreateSavedBusinessCaseMutationError = Error
    export type CreateSavedBusinessCaseMutationVariables = {data: SavedBusinessCaseCreate}

    /**
 * @summary Create a saved business case from a template
 */
export const useCreateSavedBusinessCase = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSavedBusinessCase>>, TError,CreateSavedBusinessCaseMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createSavedBusinessCase>>,
        TError,
        CreateSavedBusinessCaseMutationVariables,
        TContext
      > => {
      return useMutation(getCreateSavedBusinessCaseMutationOptions(options));
    }

export const getGetSavedBusinessCaseUrl = (id: string,) => {




  return `/api/saved-business-cases/${id}`
}

/**
 * @summary Get a saved business case
 */
export const getSavedBusinessCase = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SavedBusinessCase> => {

  return customFetch<SavedBusinessCase>(getGetSavedBusinessCaseUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSavedBusinessCaseQueryKey = (id: string,) => {
    return [
    `/api/saved-business-cases/${id}`
    ] as const;
    }


export const getGetSavedBusinessCaseQueryOptions = <TData = Awaited<ReturnType<typeof getSavedBusinessCase>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSavedBusinessCase>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSavedBusinessCaseQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSavedBusinessCase>>> = ({ signal }) => getSavedBusinessCase(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSavedBusinessCase>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSavedBusinessCaseQueryResult = NonNullable<Awaited<ReturnType<typeof getSavedBusinessCase>>>
export type GetSavedBusinessCaseQueryError = Error


/**
 * @summary Get a saved business case
 */

export function useGetSavedBusinessCase<TData = Awaited<ReturnType<typeof getSavedBusinessCase>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSavedBusinessCase>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSavedBusinessCaseQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateSavedBusinessCaseUrl = (id: string,) => {




  return `/api/saved-business-cases/${id}`
}

/**
 * @summary Update a saved business case
 */
export const updateSavedBusinessCase = async (id: string,
    savedBusinessCaseUpdate: SavedBusinessCaseUpdate, options?: Parameters<typeof customFetch>[1]): Promise<SavedBusinessCase> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SavedBusinessCase>(getUpdateSavedBusinessCaseUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(savedBusinessCaseUpdate)
  }
);}





export const getUpdateSavedBusinessCaseMutationKey = () => ['updateSavedBusinessCase'] as const;

export const getUpdateSavedBusinessCaseMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSavedBusinessCase>>, TError,UpdateSavedBusinessCaseMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateSavedBusinessCase>>, TError,UpdateSavedBusinessCaseMutationVariables, TContext> => {

const mutationKey = getUpdateSavedBusinessCaseMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateSavedBusinessCase>>, UpdateSavedBusinessCaseMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateSavedBusinessCase(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateSavedBusinessCaseMutationResult = NonNullable<Awaited<ReturnType<typeof updateSavedBusinessCase>>>
    export type UpdateSavedBusinessCaseMutationBody = SavedBusinessCaseUpdate
    export type UpdateSavedBusinessCaseMutationError = Error
    export type UpdateSavedBusinessCaseMutationVariables = {id: string;data: SavedBusinessCaseUpdate}

    /**
 * @summary Update a saved business case
 */
export const useUpdateSavedBusinessCase = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSavedBusinessCase>>, TError,UpdateSavedBusinessCaseMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateSavedBusinessCase>>,
        TError,
        UpdateSavedBusinessCaseMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateSavedBusinessCaseMutationOptions(options));
    }

export const getDeleteSavedBusinessCaseUrl = (id: string,) => {




  return `/api/saved-business-cases/${id}`
}

/**
 * @summary Delete a saved business case
 */
export const deleteSavedBusinessCase = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteSavedBusinessCaseUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteSavedBusinessCaseMutationKey = () => ['deleteSavedBusinessCase'] as const;

export const getDeleteSavedBusinessCaseMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteSavedBusinessCase>>, TError,DeleteSavedBusinessCaseMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteSavedBusinessCase>>, TError,DeleteSavedBusinessCaseMutationVariables, TContext> => {

const mutationKey = getDeleteSavedBusinessCaseMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteSavedBusinessCase>>, DeleteSavedBusinessCaseMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteSavedBusinessCase(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteSavedBusinessCaseMutationResult = NonNullable<Awaited<ReturnType<typeof deleteSavedBusinessCase>>>

    export type DeleteSavedBusinessCaseMutationError = Error
    export type DeleteSavedBusinessCaseMutationVariables = {id: string}

    /**
 * @summary Delete a saved business case
 */
export const useDeleteSavedBusinessCase = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteSavedBusinessCase>>, TError,DeleteSavedBusinessCaseMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteSavedBusinessCase>>,
        TError,
        DeleteSavedBusinessCaseMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteSavedBusinessCaseMutationOptions(options));
    }

export const getSetDefaultSavedBusinessCaseUrl = (id: string,) => {




  return `/api/saved-business-cases/${id}/set-default`
}

/**
 * @summary Mark this saved case as the default for its template (clears the previous default)
 */
export const setDefaultSavedBusinessCase = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SavedBusinessCase> => {

  return customFetch<SavedBusinessCase>(getSetDefaultSavedBusinessCaseUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getSetDefaultSavedBusinessCaseMutationKey = () => ['setDefaultSavedBusinessCase'] as const;

export const getSetDefaultSavedBusinessCaseMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setDefaultSavedBusinessCase>>, TError,SetDefaultSavedBusinessCaseMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setDefaultSavedBusinessCase>>, TError,SetDefaultSavedBusinessCaseMutationVariables, TContext> => {

const mutationKey = getSetDefaultSavedBusinessCaseMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setDefaultSavedBusinessCase>>, SetDefaultSavedBusinessCaseMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  setDefaultSavedBusinessCase(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetDefaultSavedBusinessCaseMutationResult = NonNullable<Awaited<ReturnType<typeof setDefaultSavedBusinessCase>>>

    export type SetDefaultSavedBusinessCaseMutationError = Error
    export type SetDefaultSavedBusinessCaseMutationVariables = {id: string}

    /**
 * @summary Mark this saved case as the default for its template (clears the previous default)
 */
export const useSetDefaultSavedBusinessCase = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setDefaultSavedBusinessCase>>, TError,SetDefaultSavedBusinessCaseMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof setDefaultSavedBusinessCase>>,
        TError,
        SetDefaultSavedBusinessCaseMutationVariables,
        TContext
      > => {
      return useMutation(getSetDefaultSavedBusinessCaseMutationOptions(options));
    }

export const getListDocumentSuitesUrl = () => {




  return `/api/document-suites`
}

/**
 * @summary List document suites
 */
export const listDocumentSuites = async ( options?: Parameters<typeof customFetch>[1]): Promise<ListDocumentSuites200> => {

  return customFetch<ListDocumentSuites200>(getListDocumentSuitesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDocumentSuitesQueryKey = () => {
    return [
    `/api/document-suites`
    ] as const;
    }


export const getListDocumentSuitesQueryOptions = <TData = Awaited<ReturnType<typeof listDocumentSuites>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentSuites>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDocumentSuitesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDocumentSuites>>> = ({ signal }) => listDocumentSuites({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDocumentSuites>>, TError, TData> & { queryKey: QueryKey }
}

export type ListDocumentSuitesQueryResult = NonNullable<Awaited<ReturnType<typeof listDocumentSuites>>>
export type ListDocumentSuitesQueryError = unknown


/**
 * @summary List document suites
 */

export function useListDocumentSuites<TData = Awaited<ReturnType<typeof listDocumentSuites>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listDocumentSuites>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListDocumentSuitesQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateDocumentSuiteUrl = () => {




  return `/api/document-suites`
}

/**
 * @summary Create a document suite
 */
export const createDocumentSuite = async (documentSuiteCreate: DocumentSuiteCreate, options?: Parameters<typeof customFetch>[1]): Promise<DocumentSuite> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentSuite>(getCreateDocumentSuiteUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(documentSuiteCreate)
  }
);}





export const getCreateDocumentSuiteMutationKey = () => ['createDocumentSuite'] as const;

export const getCreateDocumentSuiteMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDocumentSuite>>, TError,CreateDocumentSuiteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createDocumentSuite>>, TError,CreateDocumentSuiteMutationVariables, TContext> => {

const mutationKey = getCreateDocumentSuiteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createDocumentSuite>>, CreateDocumentSuiteMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createDocumentSuite(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateDocumentSuiteMutationResult = NonNullable<Awaited<ReturnType<typeof createDocumentSuite>>>
    export type CreateDocumentSuiteMutationBody = DocumentSuiteCreate
    export type CreateDocumentSuiteMutationError = unknown
    export type CreateDocumentSuiteMutationVariables = {data: DocumentSuiteCreate}

    /**
 * @summary Create a document suite
 */
export const useCreateDocumentSuite = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createDocumentSuite>>, TError,CreateDocumentSuiteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createDocumentSuite>>,
        TError,
        CreateDocumentSuiteMutationVariables,
        TContext
      > => {
      return useMutation(getCreateDocumentSuiteMutationOptions(options));
    }

export const getGetDocumentSuiteUrl = (id: string,) => {




  return `/api/document-suites/${id}`
}

/**
 * @summary Get a document suite
 */
export const getDocumentSuite = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<DocumentSuite> => {

  return customFetch<DocumentSuite>(getGetDocumentSuiteUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDocumentSuiteQueryKey = (id: string,) => {
    return [
    `/api/document-suites/${id}`
    ] as const;
    }


export const getGetDocumentSuiteQueryOptions = <TData = Awaited<ReturnType<typeof getDocumentSuite>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentSuite>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDocumentSuiteQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDocumentSuite>>> = ({ signal }) => getDocumentSuite(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDocumentSuite>>, TError, TData> & { queryKey: QueryKey }
}

export type GetDocumentSuiteQueryResult = NonNullable<Awaited<ReturnType<typeof getDocumentSuite>>>
export type GetDocumentSuiteQueryError = Error


/**
 * @summary Get a document suite
 */

export function useGetDocumentSuite<TData = Awaited<ReturnType<typeof getDocumentSuite>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getDocumentSuite>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetDocumentSuiteQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateDocumentSuiteUrl = (id: string,) => {




  return `/api/document-suites/${id}`
}

/**
 * @summary Update a document suite
 */
export const updateDocumentSuite = async (id: string,
    documentSuiteUpdate: DocumentSuiteUpdate, options?: Parameters<typeof customFetch>[1]): Promise<DocumentSuite> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<DocumentSuite>(getUpdateDocumentSuiteUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(documentSuiteUpdate)
  }
);}





export const getUpdateDocumentSuiteMutationKey = () => ['updateDocumentSuite'] as const;

export const getUpdateDocumentSuiteMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDocumentSuite>>, TError,UpdateDocumentSuiteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateDocumentSuite>>, TError,UpdateDocumentSuiteMutationVariables, TContext> => {

const mutationKey = getUpdateDocumentSuiteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateDocumentSuite>>, UpdateDocumentSuiteMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateDocumentSuite(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateDocumentSuiteMutationResult = NonNullable<Awaited<ReturnType<typeof updateDocumentSuite>>>
    export type UpdateDocumentSuiteMutationBody = DocumentSuiteUpdate
    export type UpdateDocumentSuiteMutationError = Error
    export type UpdateDocumentSuiteMutationVariables = {id: string;data: DocumentSuiteUpdate}

    /**
 * @summary Update a document suite
 */
export const useUpdateDocumentSuite = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDocumentSuite>>, TError,UpdateDocumentSuiteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateDocumentSuite>>,
        TError,
        UpdateDocumentSuiteMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateDocumentSuiteMutationOptions(options));
    }

export const getDeleteDocumentSuiteUrl = (id: string,) => {




  return `/api/document-suites/${id}`
}

/**
 * @summary Delete a document suite
 */
export const deleteDocumentSuite = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteDocumentSuiteUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteDocumentSuiteMutationKey = () => ['deleteDocumentSuite'] as const;

export const getDeleteDocumentSuiteMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteDocumentSuite>>, TError,DeleteDocumentSuiteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteDocumentSuite>>, TError,DeleteDocumentSuiteMutationVariables, TContext> => {

const mutationKey = getDeleteDocumentSuiteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteDocumentSuite>>, DeleteDocumentSuiteMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteDocumentSuite(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteDocumentSuiteMutationResult = NonNullable<Awaited<ReturnType<typeof deleteDocumentSuite>>>

    export type DeleteDocumentSuiteMutationError = Error
    export type DeleteDocumentSuiteMutationVariables = {id: string}

    /**
 * @summary Delete a document suite
 */
export const useDeleteDocumentSuite = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteDocumentSuite>>, TError,DeleteDocumentSuiteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteDocumentSuite>>,
        TError,
        DeleteDocumentSuiteMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteDocumentSuiteMutationOptions(options));
    }

export const getListCatalogueDocumentsUrl = (params?: ListCatalogueDocumentsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/catalogue-documents?${stringifiedParams}` : `/api/catalogue-documents`
}

/**
 * @summary List catalogue documents
 */
export const listCatalogueDocuments = async (params?: ListCatalogueDocumentsParams, options?: Parameters<typeof customFetch>[1]): Promise<ListCatalogueDocuments200> => {

  return customFetch<ListCatalogueDocuments200>(getListCatalogueDocumentsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCatalogueDocumentsQueryKey = (params?: ListCatalogueDocumentsParams,) => {
    return [
    `/api/catalogue-documents`, ...(params ? [params] : [])
    ] as const;
    }


export const getListCatalogueDocumentsQueryOptions = <TData = Awaited<ReturnType<typeof listCatalogueDocuments>>, TError = unknown>(params?: ListCatalogueDocumentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCatalogueDocuments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCatalogueDocumentsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCatalogueDocuments>>> = ({ signal }) => listCatalogueDocuments(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCatalogueDocuments>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCatalogueDocumentsQueryResult = NonNullable<Awaited<ReturnType<typeof listCatalogueDocuments>>>
export type ListCatalogueDocumentsQueryError = unknown


/**
 * @summary List catalogue documents
 */

export function useListCatalogueDocuments<TData = Awaited<ReturnType<typeof listCatalogueDocuments>>, TError = unknown>(
 params?: ListCatalogueDocumentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCatalogueDocuments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCatalogueDocumentsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateCatalogueDocumentUrl = () => {




  return `/api/catalogue-documents`
}

/**
 * @summary Create a catalogue document
 */
export const createCatalogueDocument = async (catalogueDocumentCreate: CatalogueDocumentCreate, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueDocument> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CatalogueDocument>(getCreateCatalogueDocumentUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(catalogueDocumentCreate)
  }
);}





export const getCreateCatalogueDocumentMutationKey = () => ['createCatalogueDocument'] as const;

export const getCreateCatalogueDocumentMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCatalogueDocument>>, TError,CreateCatalogueDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createCatalogueDocument>>, TError,CreateCatalogueDocumentMutationVariables, TContext> => {

const mutationKey = getCreateCatalogueDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createCatalogueDocument>>, CreateCatalogueDocumentMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createCatalogueDocument(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateCatalogueDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof createCatalogueDocument>>>
    export type CreateCatalogueDocumentMutationBody = CatalogueDocumentCreate
    export type CreateCatalogueDocumentMutationError = unknown
    export type CreateCatalogueDocumentMutationVariables = {data: CatalogueDocumentCreate}

    /**
 * @summary Create a catalogue document
 */
export const useCreateCatalogueDocument = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCatalogueDocument>>, TError,CreateCatalogueDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createCatalogueDocument>>,
        TError,
        CreateCatalogueDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getCreateCatalogueDocumentMutationOptions(options));
    }

export const getGetCatalogueDocumentUrl = (id: string,) => {




  return `/api/catalogue-documents/${id}`
}

/**
 * @summary Get a catalogue document
 */
export const getCatalogueDocument = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueDocument> => {

  return customFetch<CatalogueDocument>(getGetCatalogueDocumentUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetCatalogueDocumentQueryKey = (id: string,) => {
    return [
    `/api/catalogue-documents/${id}`
    ] as const;
    }


export const getGetCatalogueDocumentQueryOptions = <TData = Awaited<ReturnType<typeof getCatalogueDocument>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCatalogueDocument>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCatalogueDocumentQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCatalogueDocument>>> = ({ signal }) => getCatalogueDocument(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCatalogueDocument>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCatalogueDocumentQueryResult = NonNullable<Awaited<ReturnType<typeof getCatalogueDocument>>>
export type GetCatalogueDocumentQueryError = Error


/**
 * @summary Get a catalogue document
 */

export function useGetCatalogueDocument<TData = Awaited<ReturnType<typeof getCatalogueDocument>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCatalogueDocument>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCatalogueDocumentQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateCatalogueDocumentUrl = (id: string,) => {




  return `/api/catalogue-documents/${id}`
}

/**
 * @summary Update a catalogue document
 */
export const updateCatalogueDocument = async (id: string,
    catalogueDocumentUpdate: CatalogueDocumentUpdate, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueDocument> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CatalogueDocument>(getUpdateCatalogueDocumentUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(catalogueDocumentUpdate)
  }
);}





export const getUpdateCatalogueDocumentMutationKey = () => ['updateCatalogueDocument'] as const;

export const getUpdateCatalogueDocumentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueDocument>>, TError,UpdateCatalogueDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueDocument>>, TError,UpdateCatalogueDocumentMutationVariables, TContext> => {

const mutationKey = getUpdateCatalogueDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateCatalogueDocument>>, UpdateCatalogueDocumentMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateCatalogueDocument(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateCatalogueDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof updateCatalogueDocument>>>
    export type UpdateCatalogueDocumentMutationBody = CatalogueDocumentUpdate
    export type UpdateCatalogueDocumentMutationError = Error
    export type UpdateCatalogueDocumentMutationVariables = {id: string;data: CatalogueDocumentUpdate}

    /**
 * @summary Update a catalogue document
 */
export const useUpdateCatalogueDocument = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueDocument>>, TError,UpdateCatalogueDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateCatalogueDocument>>,
        TError,
        UpdateCatalogueDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateCatalogueDocumentMutationOptions(options));
    }

export const getDeleteCatalogueDocumentUrl = (id: string,) => {




  return `/api/catalogue-documents/${id}`
}

/**
 * @summary Delete a catalogue document
 */
export const deleteCatalogueDocument = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteCatalogueDocumentUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteCatalogueDocumentMutationKey = () => ['deleteCatalogueDocument'] as const;

export const getDeleteCatalogueDocumentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueDocument>>, TError,DeleteCatalogueDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueDocument>>, TError,DeleteCatalogueDocumentMutationVariables, TContext> => {

const mutationKey = getDeleteCatalogueDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteCatalogueDocument>>, DeleteCatalogueDocumentMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteCatalogueDocument(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteCatalogueDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof deleteCatalogueDocument>>>

    export type DeleteCatalogueDocumentMutationError = Error
    export type DeleteCatalogueDocumentMutationVariables = {id: string}

    /**
 * @summary Delete a catalogue document
 */
export const useDeleteCatalogueDocument = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueDocument>>, TError,DeleteCatalogueDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteCatalogueDocument>>,
        TError,
        DeleteCatalogueDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteCatalogueDocumentMutationOptions(options));
    }

export const getListCatalogueArtifactsUrl = (params?: ListCatalogueArtifactsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/catalogue-artifacts?${stringifiedParams}` : `/api/catalogue-artifacts`
}

/**
 * @summary List catalogue artifacts
 */
export const listCatalogueArtifacts = async (params?: ListCatalogueArtifactsParams, options?: Parameters<typeof customFetch>[1]): Promise<ListCatalogueArtifacts200> => {

  return customFetch<ListCatalogueArtifacts200>(getListCatalogueArtifactsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCatalogueArtifactsQueryKey = (params?: ListCatalogueArtifactsParams,) => {
    return [
    `/api/catalogue-artifacts`, ...(params ? [params] : [])
    ] as const;
    }


export const getListCatalogueArtifactsQueryOptions = <TData = Awaited<ReturnType<typeof listCatalogueArtifacts>>, TError = unknown>(params?: ListCatalogueArtifactsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCatalogueArtifacts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCatalogueArtifactsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCatalogueArtifacts>>> = ({ signal }) => listCatalogueArtifacts(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCatalogueArtifacts>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCatalogueArtifactsQueryResult = NonNullable<Awaited<ReturnType<typeof listCatalogueArtifacts>>>
export type ListCatalogueArtifactsQueryError = unknown


/**
 * @summary List catalogue artifacts
 */

export function useListCatalogueArtifacts<TData = Awaited<ReturnType<typeof listCatalogueArtifacts>>, TError = unknown>(
 params?: ListCatalogueArtifactsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCatalogueArtifacts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCatalogueArtifactsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateCatalogueArtifactUrl = () => {




  return `/api/catalogue-artifacts`
}

/**
 * @summary Attach an artifact to a catalogue document
 */
export const createCatalogueArtifact = async (catalogueArtifactCreate: CatalogueArtifactCreate, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueArtifact> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CatalogueArtifact>(getCreateCatalogueArtifactUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(catalogueArtifactCreate)
  }
);}





export const getCreateCatalogueArtifactMutationKey = () => ['createCatalogueArtifact'] as const;

export const getCreateCatalogueArtifactMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCatalogueArtifact>>, TError,CreateCatalogueArtifactMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createCatalogueArtifact>>, TError,CreateCatalogueArtifactMutationVariables, TContext> => {

const mutationKey = getCreateCatalogueArtifactMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createCatalogueArtifact>>, CreateCatalogueArtifactMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createCatalogueArtifact(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateCatalogueArtifactMutationResult = NonNullable<Awaited<ReturnType<typeof createCatalogueArtifact>>>
    export type CreateCatalogueArtifactMutationBody = CatalogueArtifactCreate
    export type CreateCatalogueArtifactMutationError = unknown
    export type CreateCatalogueArtifactMutationVariables = {data: CatalogueArtifactCreate}

    /**
 * @summary Attach an artifact to a catalogue document
 */
export const useCreateCatalogueArtifact = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCatalogueArtifact>>, TError,CreateCatalogueArtifactMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createCatalogueArtifact>>,
        TError,
        CreateCatalogueArtifactMutationVariables,
        TContext
      > => {
      return useMutation(getCreateCatalogueArtifactMutationOptions(options));
    }

export const getUpdateCatalogueArtifactUrl = (id: string,) => {




  return `/api/catalogue-artifacts/${id}`
}

/**
 * @summary Update a catalogue artifact
 */
export const updateCatalogueArtifact = async (id: string,
    catalogueArtifactUpdate: CatalogueArtifactUpdate, options?: Parameters<typeof customFetch>[1]): Promise<CatalogueArtifact> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CatalogueArtifact>(getUpdateCatalogueArtifactUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(catalogueArtifactUpdate)
  }
);}





export const getUpdateCatalogueArtifactMutationKey = () => ['updateCatalogueArtifact'] as const;

export const getUpdateCatalogueArtifactMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueArtifact>>, TError,UpdateCatalogueArtifactMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueArtifact>>, TError,UpdateCatalogueArtifactMutationVariables, TContext> => {

const mutationKey = getUpdateCatalogueArtifactMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateCatalogueArtifact>>, UpdateCatalogueArtifactMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateCatalogueArtifact(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateCatalogueArtifactMutationResult = NonNullable<Awaited<ReturnType<typeof updateCatalogueArtifact>>>
    export type UpdateCatalogueArtifactMutationBody = CatalogueArtifactUpdate
    export type UpdateCatalogueArtifactMutationError = Error
    export type UpdateCatalogueArtifactMutationVariables = {id: string;data: CatalogueArtifactUpdate}

    /**
 * @summary Update a catalogue artifact
 */
export const useUpdateCatalogueArtifact = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCatalogueArtifact>>, TError,UpdateCatalogueArtifactMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateCatalogueArtifact>>,
        TError,
        UpdateCatalogueArtifactMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateCatalogueArtifactMutationOptions(options));
    }

export const getDeleteCatalogueArtifactUrl = (id: string,) => {




  return `/api/catalogue-artifacts/${id}`
}

/**
 * @summary Remove an artifact from a catalogue document
 */
export const deleteCatalogueArtifact = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteCatalogueArtifactUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteCatalogueArtifactMutationKey = () => ['deleteCatalogueArtifact'] as const;

export const getDeleteCatalogueArtifactMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueArtifact>>, TError,DeleteCatalogueArtifactMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueArtifact>>, TError,DeleteCatalogueArtifactMutationVariables, TContext> => {

const mutationKey = getDeleteCatalogueArtifactMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteCatalogueArtifact>>, DeleteCatalogueArtifactMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteCatalogueArtifact(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteCatalogueArtifactMutationResult = NonNullable<Awaited<ReturnType<typeof deleteCatalogueArtifact>>>

    export type DeleteCatalogueArtifactMutationError = Error
    export type DeleteCatalogueArtifactMutationVariables = {id: string}

    /**
 * @summary Remove an artifact from a catalogue document
 */
export const useDeleteCatalogueArtifact = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteCatalogueArtifact>>, TError,DeleteCatalogueArtifactMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteCatalogueArtifact>>,
        TError,
        DeleteCatalogueArtifactMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteCatalogueArtifactMutationOptions(options));
    }

export const getStartWebHarvestUrl = () => {




  return `/api/knowledge/web-harvest`
}

/**
 * @summary Harvest a public website into citable knowledge sources
 */
export const startWebHarvest = async (webHarvestStart: WebHarvestStart, options?: Parameters<typeof customFetch>[1]): Promise<WebHarvestRun> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebHarvestRun>(getStartWebHarvestUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(webHarvestStart)
  }
);}





export const getStartWebHarvestMutationKey = () => ['startWebHarvest'] as const;

export const getStartWebHarvestMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startWebHarvest>>, TError,StartWebHarvestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof startWebHarvest>>, TError,StartWebHarvestMutationVariables, TContext> => {

const mutationKey = getStartWebHarvestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof startWebHarvest>>, StartWebHarvestMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  startWebHarvest(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type StartWebHarvestMutationResult = NonNullable<Awaited<ReturnType<typeof startWebHarvest>>>
    export type StartWebHarvestMutationBody = WebHarvestStart
    export type StartWebHarvestMutationError = Error
    export type StartWebHarvestMutationVariables = {data: WebHarvestStart}

    /**
 * @summary Harvest a public website into citable knowledge sources
 */
export const useStartWebHarvest = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startWebHarvest>>, TError,StartWebHarvestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof startWebHarvest>>,
        TError,
        StartWebHarvestMutationVariables,
        TContext
      > => {
      return useMutation(getStartWebHarvestMutationOptions(options));
    }

export const getGetWebHarvestRunUrl = (runId: string,) => {




  return `/api/knowledge/web-harvest/${runId}`
}

/**
 * @summary Get a website harvest and its indexed sources
 */
export const getWebHarvestRun = async (runId: string, options?: Parameters<typeof customFetch>[1]): Promise<WebHarvestRun> => {

  return customFetch<WebHarvestRun>(getGetWebHarvestRunUrl(runId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWebHarvestRunQueryKey = (runId: string,) => {
    return [
    `/api/knowledge/web-harvest/${runId}`
    ] as const;
    }


export const getGetWebHarvestRunQueryOptions = <TData = Awaited<ReturnType<typeof getWebHarvestRun>>, TError = Error>(runId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebHarvestRun>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWebHarvestRunQueryKey(runId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWebHarvestRun>>> = ({ signal }) => getWebHarvestRun(runId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWebHarvestRun>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWebHarvestRunQueryResult = NonNullable<Awaited<ReturnType<typeof getWebHarvestRun>>>
export type GetWebHarvestRunQueryError = Error


/**
 * @summary Get a website harvest and its indexed sources
 */

export function useGetWebHarvestRun<TData = Awaited<ReturnType<typeof getWebHarvestRun>>, TError = Error>(
 runId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebHarvestRun>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWebHarvestRunQueryOptions(runId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDeleteWebHarvestRunUrl = (runId: string,) => {




  return `/api/knowledge/web-harvest/${runId}`
}

/**
 * @summary Remove every knowledge source created by a website harvest
 */
export const deleteWebHarvestRun = async (runId: string, options?: Parameters<typeof customFetch>[1]): Promise<DeleteWebHarvestRun200> => {

  return customFetch<DeleteWebHarvestRun200>(getDeleteWebHarvestRunUrl(runId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteWebHarvestRunMutationKey = () => ['deleteWebHarvestRun'] as const;

export const getDeleteWebHarvestRunMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteWebHarvestRun>>, TError,DeleteWebHarvestRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteWebHarvestRun>>, TError,DeleteWebHarvestRunMutationVariables, TContext> => {

const mutationKey = getDeleteWebHarvestRunMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteWebHarvestRun>>, DeleteWebHarvestRunMutationVariables> = (props) => {
          const {runId} = props ?? {};

          return  deleteWebHarvestRun(runId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteWebHarvestRunMutationResult = NonNullable<Awaited<ReturnType<typeof deleteWebHarvestRun>>>

    export type DeleteWebHarvestRunMutationError = Error
    export type DeleteWebHarvestRunMutationVariables = {runId: string}

    /**
 * @summary Remove every knowledge source created by a website harvest
 */
export const useDeleteWebHarvestRun = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteWebHarvestRun>>, TError,DeleteWebHarvestRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteWebHarvestRun>>,
        TError,
        DeleteWebHarvestRunMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteWebHarvestRunMutationOptions(options));
    }

export const getGenerateWebHarvestArticleUrl = (runId: string,) => {




  return `/api/knowledge/web-harvest/${runId}/article`
}

/**
 * @summary Generate a review-only article draft from harvested pages
 */
export const generateWebHarvestArticle = async (runId: string,
    webHarvestArticleGenerate: WebHarvestArticleGenerate, options?: Parameters<typeof customFetch>[1]): Promise<WebHarvestArticleDraft> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebHarvestArticleDraft>(getGenerateWebHarvestArticleUrl(runId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(webHarvestArticleGenerate)
  }
);}





export const getGenerateWebHarvestArticleMutationKey = () => ['generateWebHarvestArticle'] as const;

export const getGenerateWebHarvestArticleMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateWebHarvestArticle>>, TError,GenerateWebHarvestArticleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof generateWebHarvestArticle>>, TError,GenerateWebHarvestArticleMutationVariables, TContext> => {

const mutationKey = getGenerateWebHarvestArticleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof generateWebHarvestArticle>>, GenerateWebHarvestArticleMutationVariables> = (props) => {
          const {runId,data} = props ?? {};

          return  generateWebHarvestArticle(runId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenerateWebHarvestArticleMutationResult = NonNullable<Awaited<ReturnType<typeof generateWebHarvestArticle>>>
    export type GenerateWebHarvestArticleMutationBody = WebHarvestArticleGenerate
    export type GenerateWebHarvestArticleMutationError = Error
    export type GenerateWebHarvestArticleMutationVariables = {runId: string;data: WebHarvestArticleGenerate}

    /**
 * @summary Generate a review-only article draft from harvested pages
 */
export const useGenerateWebHarvestArticle = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateWebHarvestArticle>>, TError,GenerateWebHarvestArticleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof generateWebHarvestArticle>>,
        TError,
        GenerateWebHarvestArticleMutationVariables,
        TContext
      > => {
      return useMutation(getGenerateWebHarvestArticleMutationOptions(options));
    }

export const getSaveWebHarvestArticleUrl = (runId: string,) => {




  return `/api/knowledge/web-harvest/${runId}/article/save`
}

/**
 * @summary Save a reviewed website article as an indexed knowledge source
 */
export const saveWebHarvestArticle = async (runId: string,
    webHarvestArticleSave: WebHarvestArticleSave, options?: Parameters<typeof customFetch>[1]): Promise<KnowledgeFile> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<KnowledgeFile>(getSaveWebHarvestArticleUrl(runId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(webHarvestArticleSave)
  }
);}





export const getSaveWebHarvestArticleMutationKey = () => ['saveWebHarvestArticle'] as const;

export const getSaveWebHarvestArticleMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveWebHarvestArticle>>, TError,SaveWebHarvestArticleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof saveWebHarvestArticle>>, TError,SaveWebHarvestArticleMutationVariables, TContext> => {

const mutationKey = getSaveWebHarvestArticleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof saveWebHarvestArticle>>, SaveWebHarvestArticleMutationVariables> = (props) => {
          const {runId,data} = props ?? {};

          return  saveWebHarvestArticle(runId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveWebHarvestArticleMutationResult = NonNullable<Awaited<ReturnType<typeof saveWebHarvestArticle>>>
    export type SaveWebHarvestArticleMutationBody = WebHarvestArticleSave
    export type SaveWebHarvestArticleMutationError = Error
    export type SaveWebHarvestArticleMutationVariables = {runId: string;data: WebHarvestArticleSave}

    /**
 * @summary Save a reviewed website article as an indexed knowledge source
 */
export const useSaveWebHarvestArticle = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveWebHarvestArticle>>, TError,SaveWebHarvestArticleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof saveWebHarvestArticle>>,
        TError,
        SaveWebHarvestArticleMutationVariables,
        TContext
      > => {
      return useMutation(getSaveWebHarvestArticleMutationOptions(options));
    }

export const getListKnowledgeFilesUrl = (params?: ListKnowledgeFilesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/knowledge-files?${stringifiedParams}` : `/api/knowledge-files`
}

/**
 * @summary List uploaded knowledge files
 */
export const listKnowledgeFiles = async (params?: ListKnowledgeFilesParams, options?: Parameters<typeof customFetch>[1]): Promise<ListKnowledgeFiles200> => {

  return customFetch<ListKnowledgeFiles200>(getListKnowledgeFilesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListKnowledgeFilesQueryKey = (params?: ListKnowledgeFilesParams,) => {
    return [
    `/api/knowledge-files`, ...(params ? [params] : [])
    ] as const;
    }


export const getListKnowledgeFilesQueryOptions = <TData = Awaited<ReturnType<typeof listKnowledgeFiles>>, TError = unknown>(params?: ListKnowledgeFilesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeFiles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListKnowledgeFilesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listKnowledgeFiles>>> = ({ signal }) => listKnowledgeFiles(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeFiles>>, TError, TData> & { queryKey: QueryKey }
}

export type ListKnowledgeFilesQueryResult = NonNullable<Awaited<ReturnType<typeof listKnowledgeFiles>>>
export type ListKnowledgeFilesQueryError = unknown


/**
 * @summary List uploaded knowledge files
 */

export function useListKnowledgeFiles<TData = Awaited<ReturnType<typeof listKnowledgeFiles>>, TError = unknown>(
 params?: ListKnowledgeFilesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeFiles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListKnowledgeFilesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateKnowledgeFileUrl = () => {




  return `/api/knowledge-files`
}

/**
 * @summary Register an uploaded file as a knowledge file for AI analysis
 */
export const createKnowledgeFile = async (knowledgeFileCreate: KnowledgeFileCreate, options?: Parameters<typeof customFetch>[1]): Promise<KnowledgeFile> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<KnowledgeFile>(getCreateKnowledgeFileUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(knowledgeFileCreate)
  }
);}





export const getCreateKnowledgeFileMutationKey = () => ['createKnowledgeFile'] as const;

export const getCreateKnowledgeFileMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createKnowledgeFile>>, TError,CreateKnowledgeFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createKnowledgeFile>>, TError,CreateKnowledgeFileMutationVariables, TContext> => {

const mutationKey = getCreateKnowledgeFileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createKnowledgeFile>>, CreateKnowledgeFileMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createKnowledgeFile(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateKnowledgeFileMutationResult = NonNullable<Awaited<ReturnType<typeof createKnowledgeFile>>>
    export type CreateKnowledgeFileMutationBody = KnowledgeFileCreate
    export type CreateKnowledgeFileMutationError = unknown
    export type CreateKnowledgeFileMutationVariables = {data: KnowledgeFileCreate}

    /**
 * @summary Register an uploaded file as a knowledge file for AI analysis
 */
export const useCreateKnowledgeFile = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createKnowledgeFile>>, TError,CreateKnowledgeFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createKnowledgeFile>>,
        TError,
        CreateKnowledgeFileMutationVariables,
        TContext
      > => {
      return useMutation(getCreateKnowledgeFileMutationOptions(options));
    }

export const getGetKnowledgeFileUrl = (id: string,) => {




  return `/api/knowledge-files/${id}`
}

/**
 * @summary Get a knowledge file
 */
export const getKnowledgeFile = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<KnowledgeFile> => {

  return customFetch<KnowledgeFile>(getGetKnowledgeFileUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetKnowledgeFileQueryKey = (id: string,) => {
    return [
    `/api/knowledge-files/${id}`
    ] as const;
    }


export const getGetKnowledgeFileQueryOptions = <TData = Awaited<ReturnType<typeof getKnowledgeFile>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getKnowledgeFile>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetKnowledgeFileQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getKnowledgeFile>>> = ({ signal }) => getKnowledgeFile(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getKnowledgeFile>>, TError, TData> & { queryKey: QueryKey }
}

export type GetKnowledgeFileQueryResult = NonNullable<Awaited<ReturnType<typeof getKnowledgeFile>>>
export type GetKnowledgeFileQueryError = Error


/**
 * @summary Get a knowledge file
 */

export function useGetKnowledgeFile<TData = Awaited<ReturnType<typeof getKnowledgeFile>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getKnowledgeFile>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetKnowledgeFileQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDeleteKnowledgeFileUrl = (id: string,) => {




  return `/api/knowledge-files/${id}`
}

/**
 * @summary Delete a knowledge file (and its RAG document/chunks, if analysed)
 */
export const deleteKnowledgeFile = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteKnowledgeFileUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteKnowledgeFileMutationKey = () => ['deleteKnowledgeFile'] as const;

export const getDeleteKnowledgeFileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteKnowledgeFile>>, TError,DeleteKnowledgeFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteKnowledgeFile>>, TError,DeleteKnowledgeFileMutationVariables, TContext> => {

const mutationKey = getDeleteKnowledgeFileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteKnowledgeFile>>, DeleteKnowledgeFileMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteKnowledgeFile(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteKnowledgeFileMutationResult = NonNullable<Awaited<ReturnType<typeof deleteKnowledgeFile>>>

    export type DeleteKnowledgeFileMutationError = Error
    export type DeleteKnowledgeFileMutationVariables = {id: string}

    /**
 * @summary Delete a knowledge file (and its RAG document/chunks, if analysed)
 */
export const useDeleteKnowledgeFile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteKnowledgeFile>>, TError,DeleteKnowledgeFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteKnowledgeFile>>,
        TError,
        DeleteKnowledgeFileMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteKnowledgeFileMutationOptions(options));
    }

export const getAnalyzeKnowledgeFileUrl = (id: string,) => {




  return `/api/knowledge-files/${id}/analyze`
}

/**
 * @summary Run (or re-run) AI analysis, writing chunks into the shared documents/RAG store
 */
export const analyzeKnowledgeFile = async (id: string,
    knowledgeFileAnalysisRequest: KnowledgeFileAnalysisRequest, options?: Parameters<typeof customFetch>[1]): Promise<KnowledgeFileAnalysisResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<KnowledgeFileAnalysisResult>(getAnalyzeKnowledgeFileUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(knowledgeFileAnalysisRequest)
  }
);}





export const getAnalyzeKnowledgeFileMutationKey = () => ['analyzeKnowledgeFile'] as const;

export const getAnalyzeKnowledgeFileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof analyzeKnowledgeFile>>, TError,AnalyzeKnowledgeFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof analyzeKnowledgeFile>>, TError,AnalyzeKnowledgeFileMutationVariables, TContext> => {

const mutationKey = getAnalyzeKnowledgeFileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof analyzeKnowledgeFile>>, AnalyzeKnowledgeFileMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  analyzeKnowledgeFile(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AnalyzeKnowledgeFileMutationResult = NonNullable<Awaited<ReturnType<typeof analyzeKnowledgeFile>>>
    export type AnalyzeKnowledgeFileMutationBody = KnowledgeFileAnalysisRequest
    export type AnalyzeKnowledgeFileMutationError = Error
    export type AnalyzeKnowledgeFileMutationVariables = {id: string;data: KnowledgeFileAnalysisRequest}

    /**
 * @summary Run (or re-run) AI analysis, writing chunks into the shared documents/RAG store
 */
export const useAnalyzeKnowledgeFile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof analyzeKnowledgeFile>>, TError,AnalyzeKnowledgeFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof analyzeKnowledgeFile>>,
        TError,
        AnalyzeKnowledgeFileMutationVariables,
        TContext
      > => {
      return useMutation(getAnalyzeKnowledgeFileMutationOptions(options));
    }

export const getUpdateKnowledgeVoiceApprovalUrl = (id: string,) => {




  return `/api/knowledge-files/${id}/voice-approval`
}

/**
 * @summary Approve or withdraw a ready knowledge source for public website and voice answers
 */
export const updateKnowledgeVoiceApproval = async (id: string,
    knowledgeVoiceApproval: KnowledgeVoiceApproval, options?: Parameters<typeof customFetch>[1]): Promise<KnowledgeFile> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<KnowledgeFile>(getUpdateKnowledgeVoiceApprovalUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(knowledgeVoiceApproval)
  }
);}





export const getUpdateKnowledgeVoiceApprovalMutationKey = () => ['updateKnowledgeVoiceApproval'] as const;

export const getUpdateKnowledgeVoiceApprovalMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateKnowledgeVoiceApproval>>, TError,UpdateKnowledgeVoiceApprovalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateKnowledgeVoiceApproval>>, TError,UpdateKnowledgeVoiceApprovalMutationVariables, TContext> => {

const mutationKey = getUpdateKnowledgeVoiceApprovalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateKnowledgeVoiceApproval>>, UpdateKnowledgeVoiceApprovalMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateKnowledgeVoiceApproval(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateKnowledgeVoiceApprovalMutationResult = NonNullable<Awaited<ReturnType<typeof updateKnowledgeVoiceApproval>>>
    export type UpdateKnowledgeVoiceApprovalMutationBody = KnowledgeVoiceApproval
    export type UpdateKnowledgeVoiceApprovalMutationError = Error
    export type UpdateKnowledgeVoiceApprovalMutationVariables = {id: string;data: KnowledgeVoiceApproval}

    /**
 * @summary Approve or withdraw a ready knowledge source for public website and voice answers
 */
export const useUpdateKnowledgeVoiceApproval = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateKnowledgeVoiceApproval>>, TError,UpdateKnowledgeVoiceApprovalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateKnowledgeVoiceApproval>>,
        TError,
        UpdateKnowledgeVoiceApprovalMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateKnowledgeVoiceApprovalMutationOptions(options));
    }

export const getUpdateKnowledgeChannelsUrl = (id: string,) => {




  return `/api/knowledge-files/${id}/channels`
}

/**
 * @summary Set the AI channels allowed to retrieve a knowledge source
 */
export const updateKnowledgeChannels = async (id: string,
    knowledgeChannelsUpdate: KnowledgeChannelsUpdate, options?: Parameters<typeof customFetch>[1]): Promise<KnowledgeFile> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<KnowledgeFile>(getUpdateKnowledgeChannelsUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(knowledgeChannelsUpdate)
  }
);}





export const getUpdateKnowledgeChannelsMutationKey = () => ['updateKnowledgeChannels'] as const;

export const getUpdateKnowledgeChannelsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateKnowledgeChannels>>, TError,UpdateKnowledgeChannelsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateKnowledgeChannels>>, TError,UpdateKnowledgeChannelsMutationVariables, TContext> => {

const mutationKey = getUpdateKnowledgeChannelsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateKnowledgeChannels>>, UpdateKnowledgeChannelsMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateKnowledgeChannels(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateKnowledgeChannelsMutationResult = NonNullable<Awaited<ReturnType<typeof updateKnowledgeChannels>>>
    export type UpdateKnowledgeChannelsMutationBody = KnowledgeChannelsUpdate
    export type UpdateKnowledgeChannelsMutationError = Error
    export type UpdateKnowledgeChannelsMutationVariables = {id: string;data: KnowledgeChannelsUpdate}

    /**
 * @summary Set the AI channels allowed to retrieve a knowledge source
 */
export const useUpdateKnowledgeChannels = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateKnowledgeChannels>>, TError,UpdateKnowledgeChannelsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateKnowledgeChannels>>,
        TError,
        UpdateKnowledgeChannelsMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateKnowledgeChannelsMutationOptions(options));
    }

export const getListAnalysisSessionsUrl = (id: string,
    params?: ListAnalysisSessionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/knowledge-files/${id}/analysis-sessions?${stringifiedParams}` : `/api/knowledge-files/${id}/analysis-sessions`
}

/**
 * @summary List AI analysis session history for a knowledge file
 */
export const listAnalysisSessions = async (id: string,
    params?: ListAnalysisSessionsParams, options?: Parameters<typeof customFetch>[1]): Promise<ListAnalysisSessions200> => {

  return customFetch<ListAnalysisSessions200>(getListAnalysisSessionsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAnalysisSessionsQueryKey = (id: string,
    params?: ListAnalysisSessionsParams,) => {
    return [
    `/api/knowledge-files/${id}/analysis-sessions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAnalysisSessionsQueryOptions = <TData = Awaited<ReturnType<typeof listAnalysisSessions>>, TError = unknown>(id: string,
    params?: ListAnalysisSessionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAnalysisSessions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAnalysisSessionsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAnalysisSessions>>> = ({ signal }) => listAnalysisSessions(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAnalysisSessions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAnalysisSessionsQueryResult = NonNullable<Awaited<ReturnType<typeof listAnalysisSessions>>>
export type ListAnalysisSessionsQueryError = unknown


/**
 * @summary List AI analysis session history for a knowledge file
 */

export function useListAnalysisSessions<TData = Awaited<ReturnType<typeof listAnalysisSessions>>, TError = unknown>(
 id: string,
    params?: ListAnalysisSessionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAnalysisSessions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAnalysisSessionsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReplaceKnowledgeFileUrl = (id: string,) => {




  return `/api/knowledge-files/${id}/replacement`
}

/**
 * @summary Atomically replace a knowledge source and retire its active index
 */
export const replaceKnowledgeFile = async (id: string,
    knowledgeFileReplacement: KnowledgeFileReplacement, options?: Parameters<typeof customFetch>[1]): Promise<KnowledgeFile> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<KnowledgeFile>(getReplaceKnowledgeFileUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(knowledgeFileReplacement)
  }
);}





export const getReplaceKnowledgeFileMutationKey = () => ['replaceKnowledgeFile'] as const;

export const getReplaceKnowledgeFileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof replaceKnowledgeFile>>, TError,ReplaceKnowledgeFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof replaceKnowledgeFile>>, TError,ReplaceKnowledgeFileMutationVariables, TContext> => {

const mutationKey = getReplaceKnowledgeFileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof replaceKnowledgeFile>>, ReplaceKnowledgeFileMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  replaceKnowledgeFile(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReplaceKnowledgeFileMutationResult = NonNullable<Awaited<ReturnType<typeof replaceKnowledgeFile>>>
    export type ReplaceKnowledgeFileMutationBody = KnowledgeFileReplacement
    export type ReplaceKnowledgeFileMutationError = Error
    export type ReplaceKnowledgeFileMutationVariables = {id: string;data: KnowledgeFileReplacement}

    /**
 * @summary Atomically replace a knowledge source and retire its active index
 */
export const useReplaceKnowledgeFile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof replaceKnowledgeFile>>, TError,ReplaceKnowledgeFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof replaceKnowledgeFile>>,
        TError,
        ReplaceKnowledgeFileMutationVariables,
        TContext
      > => {
      return useMutation(getReplaceKnowledgeFileMutationOptions(options));
    }

export const getGetKnowledgeSourceUrl = (id: string,
    params?: GetKnowledgeSourceParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/knowledge-files/${id}/source?${stringifiedParams}` : `/api/knowledge-files/${id}/source`
}

/**
 * @summary Open an authorized indexed knowledge source and its cited chunks
 */
export const getKnowledgeSource = async (id: string,
    params?: GetKnowledgeSourceParams, options?: Parameters<typeof customFetch>[1]): Promise<KnowledgeSource> => {

  return customFetch<KnowledgeSource>(getGetKnowledgeSourceUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetKnowledgeSourceQueryKey = (id: string,
    params?: GetKnowledgeSourceParams,) => {
    return [
    `/api/knowledge-files/${id}/source`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetKnowledgeSourceQueryOptions = <TData = Awaited<ReturnType<typeof getKnowledgeSource>>, TError = Error>(id: string,
    params?: GetKnowledgeSourceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getKnowledgeSource>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetKnowledgeSourceQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getKnowledgeSource>>> = ({ signal }) => getKnowledgeSource(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getKnowledgeSource>>, TError, TData> & { queryKey: QueryKey }
}

export type GetKnowledgeSourceQueryResult = NonNullable<Awaited<ReturnType<typeof getKnowledgeSource>>>
export type GetKnowledgeSourceQueryError = Error


/**
 * @summary Open an authorized indexed knowledge source and its cited chunks
 */

export function useGetKnowledgeSource<TData = Awaited<ReturnType<typeof getKnowledgeSource>>, TError = Error>(
 id: string,
    params?: GetKnowledgeSourceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getKnowledgeSource>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetKnowledgeSourceQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetKnowledgeStoredContentUrl = (id: string,) => {




  return `/api/knowledge-files/${id}/stored-content`
}

/**
 * @summary Retrieve the private stored original with entity Knowledge permission
 */
export const getKnowledgeStoredContent = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getGetKnowledgeStoredContentUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetKnowledgeStoredContentQueryKey = (id: string,) => {
    return [
    `/api/knowledge-files/${id}/stored-content`
    ] as const;
    }


export const getGetKnowledgeStoredContentQueryOptions = <TData = Awaited<ReturnType<typeof getKnowledgeStoredContent>>, TError = void>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getKnowledgeStoredContent>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetKnowledgeStoredContentQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getKnowledgeStoredContent>>> = ({ signal }) => getKnowledgeStoredContent(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getKnowledgeStoredContent>>, TError, TData> & { queryKey: QueryKey }
}

export type GetKnowledgeStoredContentQueryResult = NonNullable<Awaited<ReturnType<typeof getKnowledgeStoredContent>>>
export type GetKnowledgeStoredContentQueryError = void


/**
 * @summary Retrieve the private stored original with entity Knowledge permission
 */

export function useGetKnowledgeStoredContent<TData = Awaited<ReturnType<typeof getKnowledgeStoredContent>>, TError = void>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getKnowledgeStoredContent>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetKnowledgeStoredContentQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListKnowledgeSharePointItemsUrl = (id: string,
    params?: ListKnowledgeSharePointItemsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/knowledge/sharepoint/connections/${id}/items?${stringifiedParams}` : `/api/knowledge/sharepoint/connections/${id}/items`
}

/**
 * @summary Reconcile current stored, indexed, retained and unsuccessful source items
 */
export const listKnowledgeSharePointItems = async (id: string,
    params?: ListKnowledgeSharePointItemsParams, options?: Parameters<typeof customFetch>[1]): Promise<KnowledgeSharePointItemPage> => {

  return customFetch<KnowledgeSharePointItemPage>(getListKnowledgeSharePointItemsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListKnowledgeSharePointItemsQueryKey = (id: string,
    params?: ListKnowledgeSharePointItemsParams,) => {
    return [
    `/api/knowledge/sharepoint/connections/${id}/items`, ...(params ? [params] : [])
    ] as const;
    }


export const getListKnowledgeSharePointItemsQueryOptions = <TData = Awaited<ReturnType<typeof listKnowledgeSharePointItems>>, TError = unknown>(id: string,
    params?: ListKnowledgeSharePointItemsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeSharePointItems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListKnowledgeSharePointItemsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listKnowledgeSharePointItems>>> = ({ signal }) => listKnowledgeSharePointItems(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeSharePointItems>>, TError, TData> & { queryKey: QueryKey }
}

export type ListKnowledgeSharePointItemsQueryResult = NonNullable<Awaited<ReturnType<typeof listKnowledgeSharePointItems>>>
export type ListKnowledgeSharePointItemsQueryError = unknown


/**
 * @summary Reconcile current stored, indexed, retained and unsuccessful source items
 */

export function useListKnowledgeSharePointItems<TData = Awaited<ReturnType<typeof listKnowledgeSharePointItems>>, TError = unknown>(
 id: string,
    params?: ListKnowledgeSharePointItemsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeSharePointItems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListKnowledgeSharePointItemsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetKnowledgeHealthUrl = () => {




  return `/api/admin/knowledge/health`
}

/**
 * @summary View ingestion and retrieval readiness without document content or secrets
 */
export const getKnowledgeHealth = async ( options?: Parameters<typeof customFetch>[1]): Promise<KnowledgeHealth> => {

  return customFetch<KnowledgeHealth>(getGetKnowledgeHealthUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetKnowledgeHealthQueryKey = () => {
    return [
    `/api/admin/knowledge/health`
    ] as const;
    }


export const getGetKnowledgeHealthQueryOptions = <TData = Awaited<ReturnType<typeof getKnowledgeHealth>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getKnowledgeHealth>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetKnowledgeHealthQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getKnowledgeHealth>>> = ({ signal }) => getKnowledgeHealth({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getKnowledgeHealth>>, TError, TData> & { queryKey: QueryKey }
}

export type GetKnowledgeHealthQueryResult = NonNullable<Awaited<ReturnType<typeof getKnowledgeHealth>>>
export type GetKnowledgeHealthQueryError = unknown


/**
 * @summary View ingestion and retrieval readiness without document content or secrets
 */

export function useGetKnowledgeHealth<TData = Awaited<ReturnType<typeof getKnowledgeHealth>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getKnowledgeHealth>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetKnowledgeHealthQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getStartKnowledgeSharePointConnectionUrl = () => {




  return `/api/knowledge/sharepoint/connections/start`
}

/**
 * @summary Start administrator consent for a read-only SharePoint knowledge connection
 */
export const startKnowledgeSharePointConnection = async ( options?: Parameters<typeof customFetch>[1]): Promise<KnowledgeSharePointAuthorization> => {

  return customFetch<KnowledgeSharePointAuthorization>(getStartKnowledgeSharePointConnectionUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getStartKnowledgeSharePointConnectionMutationKey = () => ['startKnowledgeSharePointConnection'] as const;

export const getStartKnowledgeSharePointConnectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startKnowledgeSharePointConnection>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof startKnowledgeSharePointConnection>>, TError,void, TContext> => {

const mutationKey = getStartKnowledgeSharePointConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof startKnowledgeSharePointConnection>>, void> = () => {


          return  startKnowledgeSharePointConnection(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type StartKnowledgeSharePointConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof startKnowledgeSharePointConnection>>>

    export type StartKnowledgeSharePointConnectionMutationError = Error


    /**
 * @summary Start administrator consent for a read-only SharePoint knowledge connection
 */
export const useStartKnowledgeSharePointConnection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startKnowledgeSharePointConnection>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof startKnowledgeSharePointConnection>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getStartKnowledgeSharePointConnectionMutationOptions(options));
    }

export const getReconnectKnowledgeSharePointConnectionUrl = (id: string,) => {




  return `/api/knowledge/sharepoint/connections/${id}/reconnect`
}

/**
 * @summary Reauthorize an existing SharePoint knowledge connection without changing its selected source
 */
export const reconnectKnowledgeSharePointConnection = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<KnowledgeSharePointAuthorization> => {

  return customFetch<KnowledgeSharePointAuthorization>(getReconnectKnowledgeSharePointConnectionUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getReconnectKnowledgeSharePointConnectionMutationKey = () => ['reconnectKnowledgeSharePointConnection'] as const;

export const getReconnectKnowledgeSharePointConnectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconnectKnowledgeSharePointConnection>>, TError,ReconnectKnowledgeSharePointConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reconnectKnowledgeSharePointConnection>>, TError,ReconnectKnowledgeSharePointConnectionMutationVariables, TContext> => {

const mutationKey = getReconnectKnowledgeSharePointConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconnectKnowledgeSharePointConnection>>, ReconnectKnowledgeSharePointConnectionMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  reconnectKnowledgeSharePointConnection(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconnectKnowledgeSharePointConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof reconnectKnowledgeSharePointConnection>>>

    export type ReconnectKnowledgeSharePointConnectionMutationError = Error
    export type ReconnectKnowledgeSharePointConnectionMutationVariables = {id: string}

    /**
 * @summary Reauthorize an existing SharePoint knowledge connection without changing its selected source
 */
export const useReconnectKnowledgeSharePointConnection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconnectKnowledgeSharePointConnection>>, TError,ReconnectKnowledgeSharePointConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reconnectKnowledgeSharePointConnection>>,
        TError,
        ReconnectKnowledgeSharePointConnectionMutationVariables,
        TContext
      > => {
      return useMutation(getReconnectKnowledgeSharePointConnectionMutationOptions(options));
    }

export const getCompleteKnowledgeSharePointConnectionUrl = (params: CompleteKnowledgeSharePointConnectionParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/knowledge/sharepoint/connections/callback?${stringifiedParams}` : `/api/knowledge/sharepoint/connections/callback`
}

/**
 * @summary Complete the browser-bound Microsoft authorization-code flow
 */
export const completeKnowledgeSharePointConnection = async (params: CompleteKnowledgeSharePointConnectionParams, options?: Parameters<typeof customFetch>[1]): Promise<unknown> => {

  return customFetch<unknown>(getCompleteKnowledgeSharePointConnectionUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getCompleteKnowledgeSharePointConnectionQueryKey = (params?: CompleteKnowledgeSharePointConnectionParams,) => {
    return [
    `/api/knowledge/sharepoint/connections/callback`, ...(params ? [params] : [])
    ] as const;
    }


export const getCompleteKnowledgeSharePointConnectionQueryOptions = <TData = Awaited<ReturnType<typeof completeKnowledgeSharePointConnection>>, TError = void | Error>(params: CompleteKnowledgeSharePointConnectionParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof completeKnowledgeSharePointConnection>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getCompleteKnowledgeSharePointConnectionQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof completeKnowledgeSharePointConnection>>> = ({ signal }) => completeKnowledgeSharePointConnection(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof completeKnowledgeSharePointConnection>>, TError, TData> & { queryKey: QueryKey }
}

export type CompleteKnowledgeSharePointConnectionQueryResult = NonNullable<Awaited<ReturnType<typeof completeKnowledgeSharePointConnection>>>
export type CompleteKnowledgeSharePointConnectionQueryError = void | Error


/**
 * @summary Complete the browser-bound Microsoft authorization-code flow
 */

export function useCompleteKnowledgeSharePointConnection<TData = Awaited<ReturnType<typeof completeKnowledgeSharePointConnection>>, TError = void | Error>(
 params: CompleteKnowledgeSharePointConnectionParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof completeKnowledgeSharePointConnection>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getCompleteKnowledgeSharePointConnectionQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListKnowledgeSharePointConnectionsUrl = (params?: ListKnowledgeSharePointConnectionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/knowledge/sharepoint/connections?${stringifiedParams}` : `/api/knowledge/sharepoint/connections`
}

/**
 * @summary List safe SharePoint connection metadata for the selected entity
 */
export const listKnowledgeSharePointConnections = async (params?: ListKnowledgeSharePointConnectionsParams, options?: Parameters<typeof customFetch>[1]): Promise<ListKnowledgeSharePointConnections200> => {

  return customFetch<ListKnowledgeSharePointConnections200>(getListKnowledgeSharePointConnectionsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListKnowledgeSharePointConnectionsQueryKey = (params?: ListKnowledgeSharePointConnectionsParams,) => {
    return [
    `/api/knowledge/sharepoint/connections`, ...(params ? [params] : [])
    ] as const;
    }


export const getListKnowledgeSharePointConnectionsQueryOptions = <TData = Awaited<ReturnType<typeof listKnowledgeSharePointConnections>>, TError = unknown>(params?: ListKnowledgeSharePointConnectionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeSharePointConnections>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListKnowledgeSharePointConnectionsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listKnowledgeSharePointConnections>>> = ({ signal }) => listKnowledgeSharePointConnections(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeSharePointConnections>>, TError, TData> & { queryKey: QueryKey }
}

export type ListKnowledgeSharePointConnectionsQueryResult = NonNullable<Awaited<ReturnType<typeof listKnowledgeSharePointConnections>>>
export type ListKnowledgeSharePointConnectionsQueryError = unknown


/**
 * @summary List safe SharePoint connection metadata for the selected entity
 */

export function useListKnowledgeSharePointConnections<TData = Awaited<ReturnType<typeof listKnowledgeSharePointConnections>>, TError = unknown>(
 params?: ListKnowledgeSharePointConnectionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeSharePointConnections>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListKnowledgeSharePointConnectionsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateKnowledgeSharePointConnectionUrl = (id: string,) => {




  return `/api/knowledge/sharepoint/connections/${id}`
}

/**
 * @summary Configure the selected library, optional folder, schedule, or enabled state
 */
export const updateKnowledgeSharePointConnection = async (id: string,
    knowledgeSharePointConnectionUpdate: KnowledgeSharePointConnectionUpdate, options?: Parameters<typeof customFetch>[1]): Promise<KnowledgeSharePointConnection> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<KnowledgeSharePointConnection>(getUpdateKnowledgeSharePointConnectionUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(knowledgeSharePointConnectionUpdate)
  }
);}





export const getUpdateKnowledgeSharePointConnectionMutationKey = () => ['updateKnowledgeSharePointConnection'] as const;

export const getUpdateKnowledgeSharePointConnectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateKnowledgeSharePointConnection>>, TError,UpdateKnowledgeSharePointConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateKnowledgeSharePointConnection>>, TError,UpdateKnowledgeSharePointConnectionMutationVariables, TContext> => {

const mutationKey = getUpdateKnowledgeSharePointConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateKnowledgeSharePointConnection>>, UpdateKnowledgeSharePointConnectionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateKnowledgeSharePointConnection(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateKnowledgeSharePointConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof updateKnowledgeSharePointConnection>>>
    export type UpdateKnowledgeSharePointConnectionMutationBody = KnowledgeSharePointConnectionUpdate
    export type UpdateKnowledgeSharePointConnectionMutationError = Error
    export type UpdateKnowledgeSharePointConnectionMutationVariables = {id: string;data: KnowledgeSharePointConnectionUpdate}

    /**
 * @summary Configure the selected library, optional folder, schedule, or enabled state
 */
export const useUpdateKnowledgeSharePointConnection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateKnowledgeSharePointConnection>>, TError,UpdateKnowledgeSharePointConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateKnowledgeSharePointConnection>>,
        TError,
        UpdateKnowledgeSharePointConnectionMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateKnowledgeSharePointConnectionMutationOptions(options));
    }

export const getDeleteKnowledgeSharePointConnectionUrl = (id: string,) => {




  return `/api/knowledge/sharepoint/connections/${id}`
}

/**
 * @summary Remove a SharePoint knowledge connection without changing SharePoint
 */
export const deleteKnowledgeSharePointConnection = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteKnowledgeSharePointConnectionUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteKnowledgeSharePointConnectionMutationKey = () => ['deleteKnowledgeSharePointConnection'] as const;

export const getDeleteKnowledgeSharePointConnectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteKnowledgeSharePointConnection>>, TError,DeleteKnowledgeSharePointConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteKnowledgeSharePointConnection>>, TError,DeleteKnowledgeSharePointConnectionMutationVariables, TContext> => {

const mutationKey = getDeleteKnowledgeSharePointConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteKnowledgeSharePointConnection>>, DeleteKnowledgeSharePointConnectionMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteKnowledgeSharePointConnection(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteKnowledgeSharePointConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof deleteKnowledgeSharePointConnection>>>

    export type DeleteKnowledgeSharePointConnectionMutationError = Error
    export type DeleteKnowledgeSharePointConnectionMutationVariables = {id: string}

    /**
 * @summary Remove a SharePoint knowledge connection without changing SharePoint
 */
export const useDeleteKnowledgeSharePointConnection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteKnowledgeSharePointConnection>>, TError,DeleteKnowledgeSharePointConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteKnowledgeSharePointConnection>>,
        TError,
        DeleteKnowledgeSharePointConnectionMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteKnowledgeSharePointConnectionMutationOptions(options));
    }

export const getListKnowledgeSharePointSitesUrl = (id: string,
    params: ListKnowledgeSharePointSitesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/knowledge/sharepoint/connections/${id}/sites?${stringifiedParams}` : `/api/knowledge/sharepoint/connections/${id}/sites`
}

/**
 * @summary Search SharePoint sites visible to the connected administrator
 */
export const listKnowledgeSharePointSites = async (id: string,
    params: ListKnowledgeSharePointSitesParams, options?: Parameters<typeof customFetch>[1]): Promise<KnowledgeSharePointSitePage> => {

  return customFetch<KnowledgeSharePointSitePage>(getListKnowledgeSharePointSitesUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListKnowledgeSharePointSitesQueryKey = (id: string,
    params?: ListKnowledgeSharePointSitesParams,) => {
    return [
    `/api/knowledge/sharepoint/connections/${id}/sites`, ...(params ? [params] : [])
    ] as const;
    }


export const getListKnowledgeSharePointSitesQueryOptions = <TData = Awaited<ReturnType<typeof listKnowledgeSharePointSites>>, TError = unknown>(id: string,
    params: ListKnowledgeSharePointSitesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeSharePointSites>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListKnowledgeSharePointSitesQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listKnowledgeSharePointSites>>> = ({ signal }) => listKnowledgeSharePointSites(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeSharePointSites>>, TError, TData> & { queryKey: QueryKey }
}

export type ListKnowledgeSharePointSitesQueryResult = NonNullable<Awaited<ReturnType<typeof listKnowledgeSharePointSites>>>
export type ListKnowledgeSharePointSitesQueryError = unknown


/**
 * @summary Search SharePoint sites visible to the connected administrator
 */

export function useListKnowledgeSharePointSites<TData = Awaited<ReturnType<typeof listKnowledgeSharePointSites>>, TError = unknown>(
 id: string,
    params: ListKnowledgeSharePointSitesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeSharePointSites>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListKnowledgeSharePointSitesQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSyncKnowledgeSharePointConnectionUrl = (id: string,) => {




  return `/api/knowledge/sharepoint/connections/${id}/sync`
}

/**
 * @summary Run an immediate delta sync for one SharePoint knowledge connection
 */
export const syncKnowledgeSharePointConnection = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<KnowledgeSharePointSyncRun> => {

  return customFetch<KnowledgeSharePointSyncRun>(getSyncKnowledgeSharePointConnectionUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getSyncKnowledgeSharePointConnectionMutationKey = () => ['syncKnowledgeSharePointConnection'] as const;

export const getSyncKnowledgeSharePointConnectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof syncKnowledgeSharePointConnection>>, TError,SyncKnowledgeSharePointConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof syncKnowledgeSharePointConnection>>, TError,SyncKnowledgeSharePointConnectionMutationVariables, TContext> => {

const mutationKey = getSyncKnowledgeSharePointConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof syncKnowledgeSharePointConnection>>, SyncKnowledgeSharePointConnectionMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  syncKnowledgeSharePointConnection(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SyncKnowledgeSharePointConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof syncKnowledgeSharePointConnection>>>

    export type SyncKnowledgeSharePointConnectionMutationError = Error
    export type SyncKnowledgeSharePointConnectionMutationVariables = {id: string}

    /**
 * @summary Run an immediate delta sync for one SharePoint knowledge connection
 */
export const useSyncKnowledgeSharePointConnection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof syncKnowledgeSharePointConnection>>, TError,SyncKnowledgeSharePointConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof syncKnowledgeSharePointConnection>>,
        TError,
        SyncKnowledgeSharePointConnectionMutationVariables,
        TContext
      > => {
      return useMutation(getSyncKnowledgeSharePointConnectionMutationOptions(options));
    }

export const getListKnowledgeSharePointSyncRunsUrl = (id: string,
    params?: ListKnowledgeSharePointSyncRunsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/knowledge/sharepoint/connections/${id}/runs?${stringifiedParams}` : `/api/knowledge/sharepoint/connections/${id}/runs`
}

/**
 * @summary List bounded SharePoint sync history for one connection
 */
export const listKnowledgeSharePointSyncRuns = async (id: string,
    params?: ListKnowledgeSharePointSyncRunsParams, options?: Parameters<typeof customFetch>[1]): Promise<KnowledgeSharePointSyncRunPage> => {

  return customFetch<KnowledgeSharePointSyncRunPage>(getListKnowledgeSharePointSyncRunsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListKnowledgeSharePointSyncRunsQueryKey = (id: string,
    params?: ListKnowledgeSharePointSyncRunsParams,) => {
    return [
    `/api/knowledge/sharepoint/connections/${id}/runs`, ...(params ? [params] : [])
    ] as const;
    }


export const getListKnowledgeSharePointSyncRunsQueryOptions = <TData = Awaited<ReturnType<typeof listKnowledgeSharePointSyncRuns>>, TError = Error>(id: string,
    params?: ListKnowledgeSharePointSyncRunsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeSharePointSyncRuns>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListKnowledgeSharePointSyncRunsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listKnowledgeSharePointSyncRuns>>> = ({ signal }) => listKnowledgeSharePointSyncRuns(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeSharePointSyncRuns>>, TError, TData> & { queryKey: QueryKey }
}

export type ListKnowledgeSharePointSyncRunsQueryResult = NonNullable<Awaited<ReturnType<typeof listKnowledgeSharePointSyncRuns>>>
export type ListKnowledgeSharePointSyncRunsQueryError = Error


/**
 * @summary List bounded SharePoint sync history for one connection
 */

export function useListKnowledgeSharePointSyncRuns<TData = Awaited<ReturnType<typeof listKnowledgeSharePointSyncRuns>>, TError = Error>(
 id: string,
    params?: ListKnowledgeSharePointSyncRunsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeSharePointSyncRuns>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListKnowledgeSharePointSyncRunsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListKnowledgeSharePointDrivesUrl = (id: string,
    siteId: string,
    params?: ListKnowledgeSharePointDrivesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/knowledge/sharepoint/connections/${id}/sites/${siteId}/drives?${stringifiedParams}` : `/api/knowledge/sharepoint/connections/${id}/sites/${siteId}/drives`
}

/**
 * @summary List document libraries for a selected SharePoint site
 */
export const listKnowledgeSharePointDrives = async (id: string,
    siteId: string,
    params?: ListKnowledgeSharePointDrivesParams, options?: Parameters<typeof customFetch>[1]): Promise<KnowledgeSharePointDrivePage> => {

  return customFetch<KnowledgeSharePointDrivePage>(getListKnowledgeSharePointDrivesUrl(id,siteId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListKnowledgeSharePointDrivesQueryKey = (id: string,
    siteId: string,
    params?: ListKnowledgeSharePointDrivesParams,) => {
    return [
    `/api/knowledge/sharepoint/connections/${id}/sites/${siteId}/drives`, ...(params ? [params] : [])
    ] as const;
    }


export const getListKnowledgeSharePointDrivesQueryOptions = <TData = Awaited<ReturnType<typeof listKnowledgeSharePointDrives>>, TError = unknown>(id: string,
    siteId: string,
    params?: ListKnowledgeSharePointDrivesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeSharePointDrives>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListKnowledgeSharePointDrivesQueryKey(id,siteId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listKnowledgeSharePointDrives>>> = ({ signal }) => listKnowledgeSharePointDrives(id,siteId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && siteId !== null && siteId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeSharePointDrives>>, TError, TData> & { queryKey: QueryKey }
}

export type ListKnowledgeSharePointDrivesQueryResult = NonNullable<Awaited<ReturnType<typeof listKnowledgeSharePointDrives>>>
export type ListKnowledgeSharePointDrivesQueryError = unknown


/**
 * @summary List document libraries for a selected SharePoint site
 */

export function useListKnowledgeSharePointDrives<TData = Awaited<ReturnType<typeof listKnowledgeSharePointDrives>>, TError = unknown>(
 id: string,
    siteId: string,
    params?: ListKnowledgeSharePointDrivesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeSharePointDrives>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListKnowledgeSharePointDrivesQueryOptions(id,siteId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListKnowledgeSharePointFoldersUrl = (id: string,
    driveId: string,
    itemId: string,
    params?: ListKnowledgeSharePointFoldersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/knowledge/sharepoint/connections/${id}/drives/${driveId}/items/${itemId}/children?${stringifiedParams}` : `/api/knowledge/sharepoint/connections/${id}/drives/${driveId}/items/${itemId}/children`
}

/**
 * @summary List folders below a drive root or selected folder
 */
export const listKnowledgeSharePointFolders = async (id: string,
    driveId: string,
    itemId: string,
    params?: ListKnowledgeSharePointFoldersParams, options?: Parameters<typeof customFetch>[1]): Promise<KnowledgeSharePointFolderPage> => {

  return customFetch<KnowledgeSharePointFolderPage>(getListKnowledgeSharePointFoldersUrl(id,driveId,itemId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListKnowledgeSharePointFoldersQueryKey = (id: string,
    driveId: string,
    itemId: string,
    params?: ListKnowledgeSharePointFoldersParams,) => {
    return [
    `/api/knowledge/sharepoint/connections/${id}/drives/${driveId}/items/${itemId}/children`, ...(params ? [params] : [])
    ] as const;
    }


export const getListKnowledgeSharePointFoldersQueryOptions = <TData = Awaited<ReturnType<typeof listKnowledgeSharePointFolders>>, TError = unknown>(id: string,
    driveId: string,
    itemId: string,
    params?: ListKnowledgeSharePointFoldersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeSharePointFolders>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListKnowledgeSharePointFoldersQueryKey(id,driveId,itemId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listKnowledgeSharePointFolders>>> = ({ signal }) => listKnowledgeSharePointFolders(id,driveId,itemId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && driveId !== null && driveId !== undefined && itemId !== null && itemId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeSharePointFolders>>, TError, TData> & { queryKey: QueryKey }
}

export type ListKnowledgeSharePointFoldersQueryResult = NonNullable<Awaited<ReturnType<typeof listKnowledgeSharePointFolders>>>
export type ListKnowledgeSharePointFoldersQueryError = unknown


/**
 * @summary List folders below a drive root or selected folder
 */

export function useListKnowledgeSharePointFolders<TData = Awaited<ReturnType<typeof listKnowledgeSharePointFolders>>, TError = unknown>(
 id: string,
    driveId: string,
    itemId: string,
    params?: ListKnowledgeSharePointFoldersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listKnowledgeSharePointFolders>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListKnowledgeSharePointFoldersQueryOptions(id,driveId,itemId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRecordWorkflowMapMetricsUrl = () => {




  return `/api/crm-programme/map-metrics`
}

/**
 * @summary Record a retry-safe batch of privacy-minimised workflow map events
 */
export const recordWorkflowMapMetrics = async (workflowMapMetricBatch: WorkflowMapMetricBatch, options?: Parameters<typeof customFetch>[1]): Promise<WorkflowMapMetricBatchResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WorkflowMapMetricBatchResult>(getRecordWorkflowMapMetricsUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(workflowMapMetricBatch)
  }
);}





export const getRecordWorkflowMapMetricsMutationKey = () => ['recordWorkflowMapMetrics'] as const;

export const getRecordWorkflowMapMetricsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recordWorkflowMapMetrics>>, TError,RecordWorkflowMapMetricsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof recordWorkflowMapMetrics>>, TError,RecordWorkflowMapMetricsMutationVariables, TContext> => {

const mutationKey = getRecordWorkflowMapMetricsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof recordWorkflowMapMetrics>>, RecordWorkflowMapMetricsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  recordWorkflowMapMetrics(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RecordWorkflowMapMetricsMutationResult = NonNullable<Awaited<ReturnType<typeof recordWorkflowMapMetrics>>>
    export type RecordWorkflowMapMetricsMutationBody = WorkflowMapMetricBatch
    export type RecordWorkflowMapMetricsMutationError = Error
    export type RecordWorkflowMapMetricsMutationVariables = {data: WorkflowMapMetricBatch}

    /**
 * @summary Record a retry-safe batch of privacy-minimised workflow map events
 */
export const useRecordWorkflowMapMetrics = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recordWorkflowMapMetrics>>, TError,RecordWorkflowMapMetricsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof recordWorkflowMapMetrics>>,
        TError,
        RecordWorkflowMapMetricsMutationVariables,
        TContext
      > => {
      return useMutation(getRecordWorkflowMapMetricsMutationOptions(options));
    }

export const getGetCrmProgrammeOperationsUrl = (params?: GetCrmProgrammeOperationsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/crm-programme/operations?${stringifiedParams}` : `/api/admin/crm-programme/operations`
}

/**
 * @summary Review aggregate journey, authorization, AI, knowledge, and workflow-map health without sensitive content
 */
export const getCrmProgrammeOperations = async (params?: GetCrmProgrammeOperationsParams, options?: Parameters<typeof customFetch>[1]): Promise<CrmProgrammeOperationsReport> => {

  return customFetch<CrmProgrammeOperationsReport>(getGetCrmProgrammeOperationsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetCrmProgrammeOperationsQueryKey = (params?: GetCrmProgrammeOperationsParams,) => {
    return [
    `/api/admin/crm-programme/operations`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetCrmProgrammeOperationsQueryOptions = <TData = Awaited<ReturnType<typeof getCrmProgrammeOperations>>, TError = Error>(params?: GetCrmProgrammeOperationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCrmProgrammeOperations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCrmProgrammeOperationsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCrmProgrammeOperations>>> = ({ signal }) => getCrmProgrammeOperations(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCrmProgrammeOperations>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCrmProgrammeOperationsQueryResult = NonNullable<Awaited<ReturnType<typeof getCrmProgrammeOperations>>>
export type GetCrmProgrammeOperationsQueryError = Error


/**
 * @summary Review aggregate journey, authorization, AI, knowledge, and workflow-map health without sensitive content
 */

export function useGetCrmProgrammeOperations<TData = Awaited<ReturnType<typeof getCrmProgrammeOperations>>, TError = Error>(
 params?: GetCrmProgrammeOperationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCrmProgrammeOperations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCrmProgrammeOperationsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListLegacySourceRecordsUrl = (params?: ListLegacySourceRecordsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/legacy-data?${stringifiedParams}` : `/api/admin/legacy-data`
}

/**
 * @summary List archived legacy records
 */
export const listLegacySourceRecords = async (params?: ListLegacySourceRecordsParams, options?: Parameters<typeof customFetch>[1]): Promise<LegacySourceRecordList> => {

  return customFetch<LegacySourceRecordList>(getListLegacySourceRecordsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListLegacySourceRecordsQueryKey = (params?: ListLegacySourceRecordsParams,) => {
    return [
    `/api/admin/legacy-data`, ...(params ? [params] : [])
    ] as const;
    }


export const getListLegacySourceRecordsQueryOptions = <TData = Awaited<ReturnType<typeof listLegacySourceRecords>>, TError = unknown>(params?: ListLegacySourceRecordsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listLegacySourceRecords>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListLegacySourceRecordsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listLegacySourceRecords>>> = ({ signal }) => listLegacySourceRecords(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listLegacySourceRecords>>, TError, TData> & { queryKey: QueryKey }
}

export type ListLegacySourceRecordsQueryResult = NonNullable<Awaited<ReturnType<typeof listLegacySourceRecords>>>
export type ListLegacySourceRecordsQueryError = unknown


/**
 * @summary List archived legacy records
 */

export function useListLegacySourceRecords<TData = Awaited<ReturnType<typeof listLegacySourceRecords>>, TError = unknown>(
 params?: ListLegacySourceRecordsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listLegacySourceRecords>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListLegacySourceRecordsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetLegacySourceRecordStatsUrl = () => {




  return `/api/admin/legacy-data/stats`
}

/**
 * @summary Count archived legacy records by domain and source table
 */
export const getLegacySourceRecordStats = async ( options?: Parameters<typeof customFetch>[1]): Promise<LegacySourceRecordStats> => {

  return customFetch<LegacySourceRecordStats>(getGetLegacySourceRecordStatsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetLegacySourceRecordStatsQueryKey = () => {
    return [
    `/api/admin/legacy-data/stats`
    ] as const;
    }


export const getGetLegacySourceRecordStatsQueryOptions = <TData = Awaited<ReturnType<typeof getLegacySourceRecordStats>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLegacySourceRecordStats>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetLegacySourceRecordStatsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getLegacySourceRecordStats>>> = ({ signal }) => getLegacySourceRecordStats({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getLegacySourceRecordStats>>, TError, TData> & { queryKey: QueryKey }
}

export type GetLegacySourceRecordStatsQueryResult = NonNullable<Awaited<ReturnType<typeof getLegacySourceRecordStats>>>
export type GetLegacySourceRecordStatsQueryError = unknown


/**
 * @summary Count archived legacy records by domain and source table
 */

export function useGetLegacySourceRecordStats<TData = Awaited<ReturnType<typeof getLegacySourceRecordStats>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLegacySourceRecordStats>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetLegacySourceRecordStatsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetLegacySourceRecordUrl = (id: string,) => {




  return `/api/admin/legacy-data/${id}`
}

/**
 * @summary Get one archived legacy record and its complete source payload
 */
export const getLegacySourceRecord = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<LegacySourceRecord> => {

  return customFetch<LegacySourceRecord>(getGetLegacySourceRecordUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetLegacySourceRecordQueryKey = (id: string,) => {
    return [
    `/api/admin/legacy-data/${id}`
    ] as const;
    }


export const getGetLegacySourceRecordQueryOptions = <TData = Awaited<ReturnType<typeof getLegacySourceRecord>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLegacySourceRecord>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetLegacySourceRecordQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getLegacySourceRecord>>> = ({ signal }) => getLegacySourceRecord(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getLegacySourceRecord>>, TError, TData> & { queryKey: QueryKey }
}

export type GetLegacySourceRecordQueryResult = NonNullable<Awaited<ReturnType<typeof getLegacySourceRecord>>>
export type GetLegacySourceRecordQueryError = Error


/**
 * @summary Get one archived legacy record and its complete source payload
 */

export function useGetLegacySourceRecord<TData = Awaited<ReturnType<typeof getLegacySourceRecord>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLegacySourceRecord>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetLegacySourceRecordQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetContentHubOverviewUrl = () => {




  return `/api/content-hub/overview`
}

/**
 * @summary Get unified Content Hub metrics and recent activity
 */
export const getContentHubOverview = async ( options?: Parameters<typeof customFetch>[1]): Promise<ContentHubOverview> => {

  return customFetch<ContentHubOverview>(getGetContentHubOverviewUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetContentHubOverviewQueryKey = () => {
    return [
    `/api/content-hub/overview`
    ] as const;
    }


export const getGetContentHubOverviewQueryOptions = <TData = Awaited<ReturnType<typeof getContentHubOverview>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContentHubOverview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetContentHubOverviewQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getContentHubOverview>>> = ({ signal }) => getContentHubOverview({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getContentHubOverview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetContentHubOverviewQueryResult = NonNullable<Awaited<ReturnType<typeof getContentHubOverview>>>
export type GetContentHubOverviewQueryError = unknown


/**
 * @summary Get unified Content Hub metrics and recent activity
 */

export function useGetContentHubOverview<TData = Awaited<ReturnType<typeof getContentHubOverview>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContentHubOverview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetContentHubOverviewQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetContentHubMediaReadinessUrl = () => {




  return `/api/content-hub/media/readiness`
}

/**
 * @summary Get scoped media storage, processing, scanning and generation readiness
 */
export const getContentHubMediaReadiness = async ( options?: Parameters<typeof customFetch>[1]): Promise<ContentHubMediaReadiness> => {

  return customFetch<ContentHubMediaReadiness>(getGetContentHubMediaReadinessUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetContentHubMediaReadinessQueryKey = () => {
    return [
    `/api/content-hub/media/readiness`
    ] as const;
    }


export const getGetContentHubMediaReadinessQueryOptions = <TData = Awaited<ReturnType<typeof getContentHubMediaReadiness>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContentHubMediaReadiness>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetContentHubMediaReadinessQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getContentHubMediaReadiness>>> = ({ signal }) => getContentHubMediaReadiness({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getContentHubMediaReadiness>>, TError, TData> & { queryKey: QueryKey }
}

export type GetContentHubMediaReadinessQueryResult = NonNullable<Awaited<ReturnType<typeof getContentHubMediaReadiness>>>
export type GetContentHubMediaReadinessQueryError = unknown


/**
 * @summary Get scoped media storage, processing, scanning and generation readiness
 */

export function useGetContentHubMediaReadiness<TData = Awaited<ReturnType<typeof getContentHubMediaReadiness>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContentHubMediaReadiness>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetContentHubMediaReadinessQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListContentHubVideoModelsUrl = () => {




  return `/api/content-hub/video/models`
}

/**
 * @summary List available OpenRouter video models and connection readiness
 */
export const listContentHubVideoModels = async ( options?: Parameters<typeof customFetch>[1]): Promise<ContentHubVideoModelsResponse> => {

  return customFetch<ContentHubVideoModelsResponse>(getListContentHubVideoModelsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListContentHubVideoModelsQueryKey = () => {
    return [
    `/api/content-hub/video/models`
    ] as const;
    }


export const getListContentHubVideoModelsQueryOptions = <TData = Awaited<ReturnType<typeof listContentHubVideoModels>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubVideoModels>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListContentHubVideoModelsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listContentHubVideoModels>>> = ({ signal }) => listContentHubVideoModels({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listContentHubVideoModels>>, TError, TData> & { queryKey: QueryKey }
}

export type ListContentHubVideoModelsQueryResult = NonNullable<Awaited<ReturnType<typeof listContentHubVideoModels>>>
export type ListContentHubVideoModelsQueryError = Error


/**
 * @summary List available OpenRouter video models and connection readiness
 */

export function useListContentHubVideoModels<TData = Awaited<ReturnType<typeof listContentHubVideoModels>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubVideoModels>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListContentHubVideoModelsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListVideoProjectsUrl = (params?: ListVideoProjectsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/content-hub/video/projects?${stringifiedParams}` : `/api/content-hub/video/projects`
}

/**
 * @summary List entity-scoped video projects
 */
export const listVideoProjects = async (params?: ListVideoProjectsParams, options?: Parameters<typeof customFetch>[1]): Promise<VideoProjectList> => {

  return customFetch<VideoProjectList>(getListVideoProjectsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListVideoProjectsQueryKey = (params?: ListVideoProjectsParams,) => {
    return [
    `/api/content-hub/video/projects`, ...(params ? [params] : [])
    ] as const;
    }


export const getListVideoProjectsQueryOptions = <TData = Awaited<ReturnType<typeof listVideoProjects>>, TError = Error>(params?: ListVideoProjectsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listVideoProjects>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListVideoProjectsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listVideoProjects>>> = ({ signal }) => listVideoProjects(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listVideoProjects>>, TError, TData> & { queryKey: QueryKey }
}

export type ListVideoProjectsQueryResult = NonNullable<Awaited<ReturnType<typeof listVideoProjects>>>
export type ListVideoProjectsQueryError = Error


/**
 * @summary List entity-scoped video projects
 */

export function useListVideoProjects<TData = Awaited<ReturnType<typeof listVideoProjects>>, TError = Error>(
 params?: ListVideoProjectsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listVideoProjects>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListVideoProjectsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateVideoProjectUrl = () => {




  return `/api/content-hub/video/projects`
}

/**
 * @summary Create a video project and its initial scene
 */
export const createVideoProject = async (videoProjectCreate: VideoProjectCreate, options?: Parameters<typeof customFetch>[1]): Promise<VideoProject> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<VideoProject>(getCreateVideoProjectUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(videoProjectCreate)
  }
);}





export const getCreateVideoProjectMutationKey = () => ['createVideoProject'] as const;

export const getCreateVideoProjectMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createVideoProject>>, TError,CreateVideoProjectMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createVideoProject>>, TError,CreateVideoProjectMutationVariables, TContext> => {

const mutationKey = getCreateVideoProjectMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createVideoProject>>, CreateVideoProjectMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createVideoProject(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateVideoProjectMutationResult = NonNullable<Awaited<ReturnType<typeof createVideoProject>>>
    export type CreateVideoProjectMutationBody = VideoProjectCreate
    export type CreateVideoProjectMutationError = Error
    export type CreateVideoProjectMutationVariables = {data: VideoProjectCreate}

    /**
 * @summary Create a video project and its initial scene
 */
export const useCreateVideoProject = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createVideoProject>>, TError,CreateVideoProjectMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createVideoProject>>,
        TError,
        CreateVideoProjectMutationVariables,
        TContext
      > => {
      return useMutation(getCreateVideoProjectMutationOptions(options));
    }

export const getGetVideoProjectUrl = (id: string,) => {




  return `/api/content-hub/video/projects/${id}`
}

/**
 * @summary Get a video project and ordered scenes
 */
export const getVideoProject = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<VideoProject> => {

  return customFetch<VideoProject>(getGetVideoProjectUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetVideoProjectQueryKey = (id: string,) => {
    return [
    `/api/content-hub/video/projects/${id}`
    ] as const;
    }


export const getGetVideoProjectQueryOptions = <TData = Awaited<ReturnType<typeof getVideoProject>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getVideoProject>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetVideoProjectQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getVideoProject>>> = ({ signal }) => getVideoProject(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getVideoProject>>, TError, TData> & { queryKey: QueryKey }
}

export type GetVideoProjectQueryResult = NonNullable<Awaited<ReturnType<typeof getVideoProject>>>
export type GetVideoProjectQueryError = Error


/**
 * @summary Get a video project and ordered scenes
 */

export function useGetVideoProject<TData = Awaited<ReturnType<typeof getVideoProject>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getVideoProject>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetVideoProjectQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateVideoProjectUrl = (id: string,) => {




  return `/api/content-hub/video/projects/${id}`
}

/**
 * @summary Update video project settings
 */
export const updateVideoProject = async (id: string,
    videoProjectUpdate: VideoProjectUpdate, options?: Parameters<typeof customFetch>[1]): Promise<VideoProject> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<VideoProject>(getUpdateVideoProjectUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(videoProjectUpdate)
  }
);}





export const getUpdateVideoProjectMutationKey = () => ['updateVideoProject'] as const;

export const getUpdateVideoProjectMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateVideoProject>>, TError,UpdateVideoProjectMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateVideoProject>>, TError,UpdateVideoProjectMutationVariables, TContext> => {

const mutationKey = getUpdateVideoProjectMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateVideoProject>>, UpdateVideoProjectMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateVideoProject(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateVideoProjectMutationResult = NonNullable<Awaited<ReturnType<typeof updateVideoProject>>>
    export type UpdateVideoProjectMutationBody = VideoProjectUpdate
    export type UpdateVideoProjectMutationError = Error
    export type UpdateVideoProjectMutationVariables = {id: string;data: VideoProjectUpdate}

    /**
 * @summary Update video project settings
 */
export const useUpdateVideoProject = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateVideoProject>>, TError,UpdateVideoProjectMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateVideoProject>>,
        TError,
        UpdateVideoProjectMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateVideoProjectMutationOptions(options));
    }

export const getDeleteVideoProjectUrl = (id: string,) => {




  return `/api/content-hub/video/projects/${id}`
}

/**
 * @summary Delete a video project when no generation job is active
 */
export const deleteVideoProject = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteVideoProjectUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteVideoProjectMutationKey = () => ['deleteVideoProject'] as const;

export const getDeleteVideoProjectMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteVideoProject>>, TError,DeleteVideoProjectMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteVideoProject>>, TError,DeleteVideoProjectMutationVariables, TContext> => {

const mutationKey = getDeleteVideoProjectMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteVideoProject>>, DeleteVideoProjectMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteVideoProject(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteVideoProjectMutationResult = NonNullable<Awaited<ReturnType<typeof deleteVideoProject>>>

    export type DeleteVideoProjectMutationError = Error
    export type DeleteVideoProjectMutationVariables = {id: string}

    /**
 * @summary Delete a video project when no generation job is active
 */
export const useDeleteVideoProject = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteVideoProject>>, TError,DeleteVideoProjectMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteVideoProject>>,
        TError,
        DeleteVideoProjectMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteVideoProjectMutationOptions(options));
    }

export const getChatWithVideoProjectUrl = (id: string,) => {




  return `/api/content-hub/video/projects/${id}/chat`
}

/**
 * Uses the managed text-AI connection and the current entity's authorized
 * grounding. The response is a draft only; it never renders video or
 * mutates a scene until a media manager explicitly applies the proposal.
 * @summary Propose an editable scene revision using the governed staff Agent
 */
export const chatWithVideoProject = async (id: string,
    videoProjectChatRequest: VideoProjectChatRequest, options?: Parameters<typeof customFetch>[1]): Promise<VideoProjectChatResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<VideoProjectChatResponse>(getChatWithVideoProjectUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(videoProjectChatRequest)
  }
);}





export const getChatWithVideoProjectMutationKey = () => ['chatWithVideoProject'] as const;

export const getChatWithVideoProjectMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof chatWithVideoProject>>, TError,ChatWithVideoProjectMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof chatWithVideoProject>>, TError,ChatWithVideoProjectMutationVariables, TContext> => {

const mutationKey = getChatWithVideoProjectMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof chatWithVideoProject>>, ChatWithVideoProjectMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  chatWithVideoProject(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ChatWithVideoProjectMutationResult = NonNullable<Awaited<ReturnType<typeof chatWithVideoProject>>>
    export type ChatWithVideoProjectMutationBody = VideoProjectChatRequest
    export type ChatWithVideoProjectMutationError = Error
    export type ChatWithVideoProjectMutationVariables = {id: string;data: VideoProjectChatRequest}

    /**
 * @summary Propose an editable scene revision using the governed staff Agent
 */
export const useChatWithVideoProject = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof chatWithVideoProject>>, TError,ChatWithVideoProjectMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof chatWithVideoProject>>,
        TError,
        ChatWithVideoProjectMutationVariables,
        TContext
      > => {
      return useMutation(getChatWithVideoProjectMutationOptions(options));
    }

export const getCreateVideoSceneUrl = (id: string,) => {




  return `/api/content-hub/video/projects/${id}/scenes`
}

/**
 * @summary Add a scene to a storyboard project
 */
export const createVideoScene = async (id: string,
    videoSceneCreate: VideoSceneCreate, options?: Parameters<typeof customFetch>[1]): Promise<VideoScene> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<VideoScene>(getCreateVideoSceneUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(videoSceneCreate)
  }
);}





export const getCreateVideoSceneMutationKey = () => ['createVideoScene'] as const;

export const getCreateVideoSceneMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createVideoScene>>, TError,CreateVideoSceneMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createVideoScene>>, TError,CreateVideoSceneMutationVariables, TContext> => {

const mutationKey = getCreateVideoSceneMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createVideoScene>>, CreateVideoSceneMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createVideoScene(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateVideoSceneMutationResult = NonNullable<Awaited<ReturnType<typeof createVideoScene>>>
    export type CreateVideoSceneMutationBody = VideoSceneCreate
    export type CreateVideoSceneMutationError = Error
    export type CreateVideoSceneMutationVariables = {id: string;data: VideoSceneCreate}

    /**
 * @summary Add a scene to a storyboard project
 */
export const useCreateVideoScene = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createVideoScene>>, TError,CreateVideoSceneMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createVideoScene>>,
        TError,
        CreateVideoSceneMutationVariables,
        TContext
      > => {
      return useMutation(getCreateVideoSceneMutationOptions(options));
    }

export const getUpdateVideoSceneUrl = (id: string,
    sceneId: string,) => {




  return `/api/content-hub/video/projects/${id}/scenes/${sceneId}`
}

/**
 * @summary Update a video scene and mark rendered output stale when needed
 */
export const updateVideoScene = async (id: string,
    sceneId: string,
    videoSceneUpdate: VideoSceneUpdate, options?: Parameters<typeof customFetch>[1]): Promise<VideoScene> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<VideoScene>(getUpdateVideoSceneUrl(id,sceneId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(videoSceneUpdate)
  }
);}





export const getUpdateVideoSceneMutationKey = () => ['updateVideoScene'] as const;

export const getUpdateVideoSceneMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateVideoScene>>, TError,UpdateVideoSceneMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateVideoScene>>, TError,UpdateVideoSceneMutationVariables, TContext> => {

const mutationKey = getUpdateVideoSceneMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateVideoScene>>, UpdateVideoSceneMutationVariables> = (props) => {
          const {id,sceneId,data} = props ?? {};

          return  updateVideoScene(id,sceneId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateVideoSceneMutationResult = NonNullable<Awaited<ReturnType<typeof updateVideoScene>>>
    export type UpdateVideoSceneMutationBody = VideoSceneUpdate
    export type UpdateVideoSceneMutationError = Error
    export type UpdateVideoSceneMutationVariables = {id: string;sceneId: string;data: VideoSceneUpdate}

    /**
 * @summary Update a video scene and mark rendered output stale when needed
 */
export const useUpdateVideoScene = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateVideoScene>>, TError,UpdateVideoSceneMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateVideoScene>>,
        TError,
        UpdateVideoSceneMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateVideoSceneMutationOptions(options));
    }

export const getDeleteVideoSceneUrl = (id: string,
    sceneId: string,) => {




  return `/api/content-hub/video/projects/${id}/scenes/${sceneId}`
}

/**
 * @summary Delete a storyboard scene
 */
export const deleteVideoScene = async (id: string,
    sceneId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteVideoSceneUrl(id,sceneId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteVideoSceneMutationKey = () => ['deleteVideoScene'] as const;

export const getDeleteVideoSceneMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteVideoScene>>, TError,DeleteVideoSceneMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteVideoScene>>, TError,DeleteVideoSceneMutationVariables, TContext> => {

const mutationKey = getDeleteVideoSceneMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteVideoScene>>, DeleteVideoSceneMutationVariables> = (props) => {
          const {id,sceneId} = props ?? {};

          return  deleteVideoScene(id,sceneId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteVideoSceneMutationResult = NonNullable<Awaited<ReturnType<typeof deleteVideoScene>>>

    export type DeleteVideoSceneMutationError = Error
    export type DeleteVideoSceneMutationVariables = {id: string;sceneId: string}

    /**
 * @summary Delete a storyboard scene
 */
export const useDeleteVideoScene = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteVideoScene>>, TError,DeleteVideoSceneMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteVideoScene>>,
        TError,
        DeleteVideoSceneMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteVideoSceneMutationOptions(options));
    }

export const getGetVideoSceneLastFrameUrl = (id: string,
    sceneId: string,) => {




  return `/api/content-hub/video/projects/${id}/scenes/${sceneId}/last-frame`
}

/**
 * @summary Read the extracted continuity frame for a rendered scene
 */
export const getVideoSceneLastFrame = async (id: string,
    sceneId: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getGetVideoSceneLastFrameUrl(id,sceneId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetVideoSceneLastFrameQueryKey = (id: string,
    sceneId: string,) => {
    return [
    `/api/content-hub/video/projects/${id}/scenes/${sceneId}/last-frame`
    ] as const;
    }


export const getGetVideoSceneLastFrameQueryOptions = <TData = Awaited<ReturnType<typeof getVideoSceneLastFrame>>, TError = Error>(id: string,
    sceneId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getVideoSceneLastFrame>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetVideoSceneLastFrameQueryKey(id,sceneId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getVideoSceneLastFrame>>> = ({ signal }) => getVideoSceneLastFrame(id,sceneId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && sceneId !== null && sceneId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getVideoSceneLastFrame>>, TError, TData> & { queryKey: QueryKey }
}

export type GetVideoSceneLastFrameQueryResult = NonNullable<Awaited<ReturnType<typeof getVideoSceneLastFrame>>>
export type GetVideoSceneLastFrameQueryError = Error


/**
 * @summary Read the extracted continuity frame for a rendered scene
 */

export function useGetVideoSceneLastFrame<TData = Awaited<ReturnType<typeof getVideoSceneLastFrame>>, TError = Error>(
 id: string,
    sceneId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getVideoSceneLastFrame>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetVideoSceneLastFrameQueryOptions(id,sceneId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReorderVideoScenesUrl = (id: string,) => {




  return `/api/content-hub/video/projects/${id}/scenes/reorder`
}

/**
 * @summary Replace the complete storyboard scene order
 */
export const reorderVideoScenes = async (id: string,
    videoSceneReorder: VideoSceneReorder, options?: Parameters<typeof customFetch>[1]): Promise<VideoScene[]> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<VideoScene[]>(getReorderVideoScenesUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(videoSceneReorder)
  }
);}





export const getReorderVideoScenesMutationKey = () => ['reorderVideoScenes'] as const;

export const getReorderVideoScenesMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reorderVideoScenes>>, TError,ReorderVideoScenesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reorderVideoScenes>>, TError,ReorderVideoScenesMutationVariables, TContext> => {

const mutationKey = getReorderVideoScenesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reorderVideoScenes>>, ReorderVideoScenesMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reorderVideoScenes(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReorderVideoScenesMutationResult = NonNullable<Awaited<ReturnType<typeof reorderVideoScenes>>>
    export type ReorderVideoScenesMutationBody = VideoSceneReorder
    export type ReorderVideoScenesMutationError = Error
    export type ReorderVideoScenesMutationVariables = {id: string;data: VideoSceneReorder}

    /**
 * @summary Replace the complete storyboard scene order
 */
export const useReorderVideoScenes = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reorderVideoScenes>>, TError,ReorderVideoScenesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reorderVideoScenes>>,
        TError,
        ReorderVideoScenesMutationVariables,
        TContext
      > => {
      return useMutation(getReorderVideoScenesMutationOptions(options));
    }

export const getRenderVideoSceneUrl = (id: string,
    sceneId: string,) => {




  return `/api/content-hub/video/projects/${id}/scenes/${sceneId}/render`
}

/**
 * @summary Queue an OpenRouter render and record metered usage
 */
export const renderVideoScene = async (id: string,
    sceneId: string,
    videoSceneRenderRequest: VideoSceneRenderRequest, options?: Parameters<typeof customFetch>[1]): Promise<VideoGenerationJob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<VideoGenerationJob>(getRenderVideoSceneUrl(id,sceneId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(videoSceneRenderRequest)
  }
);}





export const getRenderVideoSceneMutationKey = () => ['renderVideoScene'] as const;

export const getRenderVideoSceneMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof renderVideoScene>>, TError,RenderVideoSceneMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof renderVideoScene>>, TError,RenderVideoSceneMutationVariables, TContext> => {

const mutationKey = getRenderVideoSceneMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof renderVideoScene>>, RenderVideoSceneMutationVariables> = (props) => {
          const {id,sceneId,data} = props ?? {};

          return  renderVideoScene(id,sceneId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RenderVideoSceneMutationResult = NonNullable<Awaited<ReturnType<typeof renderVideoScene>>>
    export type RenderVideoSceneMutationBody = VideoSceneRenderRequest
    export type RenderVideoSceneMutationError = Error
    export type RenderVideoSceneMutationVariables = {id: string;sceneId: string;data: VideoSceneRenderRequest}

    /**
 * @summary Queue an OpenRouter render and record metered usage
 */
export const useRenderVideoScene = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof renderVideoScene>>, TError,RenderVideoSceneMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof renderVideoScene>>,
        TError,
        RenderVideoSceneMutationVariables,
        TContext
      > => {
      return useMutation(getRenderVideoSceneMutationOptions(options));
    }

export const getAssembleVideoProjectUrl = (id: string,) => {




  return `/api/content-hub/video/projects/${id}/assemble`
}

/**
 * @summary Queue in-app assembly after every scene is ready
 */
export const assembleVideoProject = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<VideoGenerationJob> => {

  return customFetch<VideoGenerationJob>(getAssembleVideoProjectUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAssembleVideoProjectMutationKey = () => ['assembleVideoProject'] as const;

export const getAssembleVideoProjectMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof assembleVideoProject>>, TError,AssembleVideoProjectMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof assembleVideoProject>>, TError,AssembleVideoProjectMutationVariables, TContext> => {

const mutationKey = getAssembleVideoProjectMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof assembleVideoProject>>, AssembleVideoProjectMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  assembleVideoProject(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AssembleVideoProjectMutationResult = NonNullable<Awaited<ReturnType<typeof assembleVideoProject>>>

    export type AssembleVideoProjectMutationError = Error
    export type AssembleVideoProjectMutationVariables = {id: string}

    /**
 * @summary Queue in-app assembly after every scene is ready
 */
export const useAssembleVideoProject = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof assembleVideoProject>>, TError,AssembleVideoProjectMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof assembleVideoProject>>,
        TError,
        AssembleVideoProjectMutationVariables,
        TContext
      > => {
      return useMutation(getAssembleVideoProjectMutationOptions(options));
    }

export const getListVideoProjectJobsUrl = (id: string,
    params?: ListVideoProjectJobsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/content-hub/video/projects/${id}/jobs?${stringifiedParams}` : `/api/content-hub/video/projects/${id}/jobs`
}

/**
 * @summary List generation and assembly history for a project
 */
export const listVideoProjectJobs = async (id: string,
    params?: ListVideoProjectJobsParams, options?: Parameters<typeof customFetch>[1]): Promise<VideoGenerationJobList> => {

  return customFetch<VideoGenerationJobList>(getListVideoProjectJobsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListVideoProjectJobsQueryKey = (id: string,
    params?: ListVideoProjectJobsParams,) => {
    return [
    `/api/content-hub/video/projects/${id}/jobs`, ...(params ? [params] : [])
    ] as const;
    }


export const getListVideoProjectJobsQueryOptions = <TData = Awaited<ReturnType<typeof listVideoProjectJobs>>, TError = Error>(id: string,
    params?: ListVideoProjectJobsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listVideoProjectJobs>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListVideoProjectJobsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listVideoProjectJobs>>> = ({ signal }) => listVideoProjectJobs(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listVideoProjectJobs>>, TError, TData> & { queryKey: QueryKey }
}

export type ListVideoProjectJobsQueryResult = NonNullable<Awaited<ReturnType<typeof listVideoProjectJobs>>>
export type ListVideoProjectJobsQueryError = Error


/**
 * @summary List generation and assembly history for a project
 */

export function useListVideoProjectJobs<TData = Awaited<ReturnType<typeof listVideoProjectJobs>>, TError = Error>(
 id: string,
    params?: ListVideoProjectJobsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listVideoProjectJobs>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListVideoProjectJobsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetVideoGenerationJobUrl = (jobId: string,) => {




  return `/api/content-hub/video/jobs/${jobId}`
}

/**
 * @summary Get one video generation or assembly job
 */
export const getVideoGenerationJob = async (jobId: string, options?: Parameters<typeof customFetch>[1]): Promise<VideoGenerationJob> => {

  return customFetch<VideoGenerationJob>(getGetVideoGenerationJobUrl(jobId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetVideoGenerationJobQueryKey = (jobId: string,) => {
    return [
    `/api/content-hub/video/jobs/${jobId}`
    ] as const;
    }


export const getGetVideoGenerationJobQueryOptions = <TData = Awaited<ReturnType<typeof getVideoGenerationJob>>, TError = Error>(jobId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getVideoGenerationJob>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetVideoGenerationJobQueryKey(jobId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getVideoGenerationJob>>> = ({ signal }) => getVideoGenerationJob(jobId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: jobId !== null && jobId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getVideoGenerationJob>>, TError, TData> & { queryKey: QueryKey }
}

export type GetVideoGenerationJobQueryResult = NonNullable<Awaited<ReturnType<typeof getVideoGenerationJob>>>
export type GetVideoGenerationJobQueryError = Error


/**
 * @summary Get one video generation or assembly job
 */

export function useGetVideoGenerationJob<TData = Awaited<ReturnType<typeof getVideoGenerationJob>>, TError = Error>(
 jobId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getVideoGenerationJob>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetVideoGenerationJobQueryOptions(jobId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCancelVideoGenerationJobUrl = (jobId: string,) => {




  return `/api/content-hub/video/jobs/${jobId}/cancel`
}

/**
 * @summary Cancel a queued or in-progress video job
 */
export const cancelVideoGenerationJob = async (jobId: string, options?: Parameters<typeof customFetch>[1]): Promise<VideoGenerationJob> => {

  return customFetch<VideoGenerationJob>(getCancelVideoGenerationJobUrl(jobId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelVideoGenerationJobMutationKey = () => ['cancelVideoGenerationJob'] as const;

export const getCancelVideoGenerationJobMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelVideoGenerationJob>>, TError,CancelVideoGenerationJobMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelVideoGenerationJob>>, TError,CancelVideoGenerationJobMutationVariables, TContext> => {

const mutationKey = getCancelVideoGenerationJobMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelVideoGenerationJob>>, CancelVideoGenerationJobMutationVariables> = (props) => {
          const {jobId} = props ?? {};

          return  cancelVideoGenerationJob(jobId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelVideoGenerationJobMutationResult = NonNullable<Awaited<ReturnType<typeof cancelVideoGenerationJob>>>

    export type CancelVideoGenerationJobMutationError = Error
    export type CancelVideoGenerationJobMutationVariables = {jobId: string}

    /**
 * @summary Cancel a queued or in-progress video job
 */
export const useCancelVideoGenerationJob = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelVideoGenerationJob>>, TError,CancelVideoGenerationJobMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelVideoGenerationJob>>,
        TError,
        CancelVideoGenerationJobMutationVariables,
        TContext
      > => {
      return useMutation(getCancelVideoGenerationJobMutationOptions(options));
    }

export const getDownloadVideoProjectUrl = (id: string,) => {




  return `/api/content-hub/video/projects/${id}/download`
}

/**
 * @summary Download the finished project as an authenticated MP4
 */
export const downloadVideoProject = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadVideoProjectUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadVideoProjectQueryKey = (id: string,) => {
    return [
    `/api/content-hub/video/projects/${id}/download`
    ] as const;
    }


export const getDownloadVideoProjectQueryOptions = <TData = Awaited<ReturnType<typeof downloadVideoProject>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadVideoProject>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadVideoProjectQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadVideoProject>>> = ({ signal }) => downloadVideoProject(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadVideoProject>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadVideoProjectQueryResult = NonNullable<Awaited<ReturnType<typeof downloadVideoProject>>>
export type DownloadVideoProjectQueryError = Error


/**
 * @summary Download the finished project as an authenticated MP4
 */

export function useDownloadVideoProject<TData = Awaited<ReturnType<typeof downloadVideoProject>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadVideoProject>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadVideoProjectQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetVideoGenerationUsageUrl = (params?: GetVideoGenerationUsageParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/content-hub/video/usage?${stringifiedParams}` : `/api/content-hub/video/usage`
}

/**
 * @summary Summarise metered video generation usage by model
 */
export const getVideoGenerationUsage = async (params?: GetVideoGenerationUsageParams, options?: Parameters<typeof customFetch>[1]): Promise<VideoGenerationUsageSummary> => {

  return customFetch<VideoGenerationUsageSummary>(getGetVideoGenerationUsageUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetVideoGenerationUsageQueryKey = (params?: GetVideoGenerationUsageParams,) => {
    return [
    `/api/content-hub/video/usage`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetVideoGenerationUsageQueryOptions = <TData = Awaited<ReturnType<typeof getVideoGenerationUsage>>, TError = Error>(params?: GetVideoGenerationUsageParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getVideoGenerationUsage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetVideoGenerationUsageQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getVideoGenerationUsage>>> = ({ signal }) => getVideoGenerationUsage(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getVideoGenerationUsage>>, TError, TData> & { queryKey: QueryKey }
}

export type GetVideoGenerationUsageQueryResult = NonNullable<Awaited<ReturnType<typeof getVideoGenerationUsage>>>
export type GetVideoGenerationUsageQueryError = Error


/**
 * @summary Summarise metered video generation usage by model
 */

export function useGetVideoGenerationUsage<TData = Awaited<ReturnType<typeof getVideoGenerationUsage>>, TError = Error>(
 params?: GetVideoGenerationUsageParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getVideoGenerationUsage>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetVideoGenerationUsageQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListContentHubMediaUrl = (params?: ListContentHubMediaParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/content-hub/media?${stringifiedParams}` : `/api/content-hub/media`
}

/**
 * @summary List entity-scoped Content Hub media
 */
export const listContentHubMedia = async (params?: ListContentHubMediaParams, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubMediaList> => {

  return customFetch<ContentHubMediaList>(getListContentHubMediaUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListContentHubMediaQueryKey = (params?: ListContentHubMediaParams,) => {
    return [
    `/api/content-hub/media`, ...(params ? [params] : [])
    ] as const;
    }


export const getListContentHubMediaQueryOptions = <TData = Awaited<ReturnType<typeof listContentHubMedia>>, TError = unknown>(params?: ListContentHubMediaParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubMedia>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListContentHubMediaQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listContentHubMedia>>> = ({ signal }) => listContentHubMedia(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listContentHubMedia>>, TError, TData> & { queryKey: QueryKey }
}

export type ListContentHubMediaQueryResult = NonNullable<Awaited<ReturnType<typeof listContentHubMedia>>>
export type ListContentHubMediaQueryError = unknown


/**
 * @summary List entity-scoped Content Hub media
 */

export function useListContentHubMedia<TData = Awaited<ReturnType<typeof listContentHubMedia>>, TError = unknown>(
 params?: ListContentHubMediaParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubMedia>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListContentHubMediaQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUploadContentHubMediaUrl = () => {




  return `/api/content-hub/media`
}

/**
 * @summary Upload a private original for asynchronous validation and derivative processing
 */
export const uploadContentHubMedia = async (uploadContentHubMediaBody: UploadContentHubMediaBody, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubMediaAsset> => {
    const formData = new FormData();
formData.append(`file`, uploadContentHubMediaBody.file);

  return customFetch<ContentHubMediaAsset>(getUploadContentHubMediaUrl(),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}





export const getUploadContentHubMediaMutationKey = () => ['uploadContentHubMedia'] as const;

export const getUploadContentHubMediaMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadContentHubMedia>>, TError,UploadContentHubMediaMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadContentHubMedia>>, TError,UploadContentHubMediaMutationVariables, TContext> => {

const mutationKey = getUploadContentHubMediaMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadContentHubMedia>>, UploadContentHubMediaMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  uploadContentHubMedia(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadContentHubMediaMutationResult = NonNullable<Awaited<ReturnType<typeof uploadContentHubMedia>>>
    export type UploadContentHubMediaMutationBody = UploadContentHubMediaBody
    export type UploadContentHubMediaMutationError = Error
    export type UploadContentHubMediaMutationVariables = {data: UploadContentHubMediaBody}

    /**
 * @summary Upload a private original for asynchronous validation and derivative processing
 */
export const useUploadContentHubMedia = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadContentHubMedia>>, TError,UploadContentHubMediaMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadContentHubMedia>>,
        TError,
        UploadContentHubMediaMutationVariables,
        TContext
      > => {
      return useMutation(getUploadContentHubMediaMutationOptions(options));
    }

export const getValidateContentHubMediaSelectionUrl = () => {




  return `/api/content-hub/media/selection`
}

/**
 * @summary Validate that selected media is ready in the current entity
 */
export const validateContentHubMediaSelection = async (contentHubMediaSelectionRequest: ContentHubMediaSelectionRequest, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubMediaSelection> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubMediaSelection>(getValidateContentHubMediaSelectionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubMediaSelectionRequest)
  }
);}





export const getValidateContentHubMediaSelectionMutationKey = () => ['validateContentHubMediaSelection'] as const;

export const getValidateContentHubMediaSelectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof validateContentHubMediaSelection>>, TError,ValidateContentHubMediaSelectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof validateContentHubMediaSelection>>, TError,ValidateContentHubMediaSelectionMutationVariables, TContext> => {

const mutationKey = getValidateContentHubMediaSelectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof validateContentHubMediaSelection>>, ValidateContentHubMediaSelectionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  validateContentHubMediaSelection(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ValidateContentHubMediaSelectionMutationResult = NonNullable<Awaited<ReturnType<typeof validateContentHubMediaSelection>>>
    export type ValidateContentHubMediaSelectionMutationBody = ContentHubMediaSelectionRequest
    export type ValidateContentHubMediaSelectionMutationError = Error
    export type ValidateContentHubMediaSelectionMutationVariables = {data: ContentHubMediaSelectionRequest}

    /**
 * @summary Validate that selected media is ready in the current entity
 */
export const useValidateContentHubMediaSelection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof validateContentHubMediaSelection>>, TError,ValidateContentHubMediaSelectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof validateContentHubMediaSelection>>,
        TError,
        ValidateContentHubMediaSelectionMutationVariables,
        TContext
      > => {
      return useMutation(getValidateContentHubMediaSelectionMutationOptions(options));
    }

export const getGenerateContentHubImageUrl = () => {




  return `/api/content-hub/media/generate-image`
}

/**
 * @summary Generate an image with the configured project model and save it to entity-scoped media
 */
export const generateContentHubImage = async (contentHubImageGenerationRequest: ContentHubImageGenerationRequest, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubMediaDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubMediaDetail>(getGenerateContentHubImageUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubImageGenerationRequest)
  }
);}





export const getGenerateContentHubImageMutationKey = () => ['generateContentHubImage'] as const;

export const getGenerateContentHubImageMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateContentHubImage>>, TError,GenerateContentHubImageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof generateContentHubImage>>, TError,GenerateContentHubImageMutationVariables, TContext> => {

const mutationKey = getGenerateContentHubImageMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof generateContentHubImage>>, GenerateContentHubImageMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  generateContentHubImage(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenerateContentHubImageMutationResult = NonNullable<Awaited<ReturnType<typeof generateContentHubImage>>>
    export type GenerateContentHubImageMutationBody = ContentHubImageGenerationRequest
    export type GenerateContentHubImageMutationError = Error
    export type GenerateContentHubImageMutationVariables = {data: ContentHubImageGenerationRequest}

    /**
 * @summary Generate an image with the configured project model and save it to entity-scoped media
 */
export const useGenerateContentHubImage = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateContentHubImage>>, TError,GenerateContentHubImageMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof generateContentHubImage>>,
        TError,
        GenerateContentHubImageMutationVariables,
        TContext
      > => {
      return useMutation(getGenerateContentHubImageMutationOptions(options));
    }

export const getGetContentHubMediaUrl = (id: string,) => {




  return `/api/content-hub/media/${id}`
}

/**
 * @summary Get safe media metadata and derivative states
 */
export const getContentHubMedia = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubMediaDetail> => {

  return customFetch<ContentHubMediaDetail>(getGetContentHubMediaUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetContentHubMediaQueryKey = (id: string,) => {
    return [
    `/api/content-hub/media/${id}`
    ] as const;
    }


export const getGetContentHubMediaQueryOptions = <TData = Awaited<ReturnType<typeof getContentHubMedia>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContentHubMedia>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetContentHubMediaQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getContentHubMedia>>> = ({ signal }) => getContentHubMedia(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getContentHubMedia>>, TError, TData> & { queryKey: QueryKey }
}

export type GetContentHubMediaQueryResult = NonNullable<Awaited<ReturnType<typeof getContentHubMedia>>>
export type GetContentHubMediaQueryError = Error


/**
 * @summary Get safe media metadata and derivative states
 */

export function useGetContentHubMedia<TData = Awaited<ReturnType<typeof getContentHubMedia>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContentHubMedia>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetContentHubMediaQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDeleteContentHubMediaUrl = (id: string,
    params: DeleteContentHubMediaParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/content-hub/media/${id}?${stringifiedParams}` : `/api/content-hub/media/${id}`
}

/**
 * @summary Delete unreferenced media and revoke its delivery grants
 */
export const deleteContentHubMedia = async (id: string,
    params: DeleteContentHubMediaParams, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteContentHubMediaUrl(id,params),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteContentHubMediaMutationKey = () => ['deleteContentHubMedia'] as const;

export const getDeleteContentHubMediaMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteContentHubMedia>>, TError,DeleteContentHubMediaMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteContentHubMedia>>, TError,DeleteContentHubMediaMutationVariables, TContext> => {

const mutationKey = getDeleteContentHubMediaMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteContentHubMedia>>, DeleteContentHubMediaMutationVariables> = (props) => {
          const {id,params} = props ?? {};

          return  deleteContentHubMedia(id,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteContentHubMediaMutationResult = NonNullable<Awaited<ReturnType<typeof deleteContentHubMedia>>>

    export type DeleteContentHubMediaMutationError = Error
    export type DeleteContentHubMediaMutationVariables = {id: string;params: DeleteContentHubMediaParams}

    /**
 * @summary Delete unreferenced media and revoke its delivery grants
 */
export const useDeleteContentHubMedia = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteContentHubMedia>>, TError,DeleteContentHubMediaMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteContentHubMedia>>,
        TError,
        DeleteContentHubMediaMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteContentHubMediaMutationOptions(options));
    }

export const getDownloadContentHubMediaUrl = (id: string,) => {




  return `/api/content-hub/media/${id}/download`
}

/**
 * @summary Download a ready media original with an attachment filename
 */
export const downloadContentHubMedia = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadContentHubMediaUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadContentHubMediaQueryKey = (id: string,) => {
    return [
    `/api/content-hub/media/${id}/download`
    ] as const;
    }


export const getDownloadContentHubMediaQueryOptions = <TData = Awaited<ReturnType<typeof downloadContentHubMedia>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadContentHubMedia>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadContentHubMediaQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadContentHubMedia>>> = ({ signal }) => downloadContentHubMedia(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadContentHubMedia>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadContentHubMediaQueryResult = NonNullable<Awaited<ReturnType<typeof downloadContentHubMedia>>>
export type DownloadContentHubMediaQueryError = Error


/**
 * @summary Download a ready media original with an attachment filename
 */

export function useDownloadContentHubMedia<TData = Awaited<ReturnType<typeof downloadContentHubMedia>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadContentHubMedia>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadContentHubMediaQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetContentHubMediaPreviewUrl = (id: string,) => {




  return `/api/content-hub/media/${id}/preview`
}

/**
 * @summary Download an authenticated preview derivative, never the private original
 */
export const getContentHubMediaPreview = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getGetContentHubMediaPreviewUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetContentHubMediaPreviewQueryKey = (id: string,) => {
    return [
    `/api/content-hub/media/${id}/preview`
    ] as const;
    }


export const getGetContentHubMediaPreviewQueryOptions = <TData = Awaited<ReturnType<typeof getContentHubMediaPreview>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContentHubMediaPreview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetContentHubMediaPreviewQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getContentHubMediaPreview>>> = ({ signal }) => getContentHubMediaPreview(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getContentHubMediaPreview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetContentHubMediaPreviewQueryResult = NonNullable<Awaited<ReturnType<typeof getContentHubMediaPreview>>>
export type GetContentHubMediaPreviewQueryError = Error


/**
 * @summary Download an authenticated preview derivative, never the private original
 */

export function useGetContentHubMediaPreview<TData = Awaited<ReturnType<typeof getContentHubMediaPreview>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContentHubMediaPreview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetContentHubMediaPreviewQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRetryContentHubMediaProcessingUrl = (id: string,) => {




  return `/api/content-hub/media/${id}/retry`
}

/**
 * @summary Retry a failed media processing operation
 */
export const retryContentHubMediaProcessing = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubMediaAsset> => {

  return customFetch<ContentHubMediaAsset>(getRetryContentHubMediaProcessingUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRetryContentHubMediaProcessingMutationKey = () => ['retryContentHubMediaProcessing'] as const;

export const getRetryContentHubMediaProcessingMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryContentHubMediaProcessing>>, TError,RetryContentHubMediaProcessingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof retryContentHubMediaProcessing>>, TError,RetryContentHubMediaProcessingMutationVariables, TContext> => {

const mutationKey = getRetryContentHubMediaProcessingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof retryContentHubMediaProcessing>>, RetryContentHubMediaProcessingMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  retryContentHubMediaProcessing(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RetryContentHubMediaProcessingMutationResult = NonNullable<Awaited<ReturnType<typeof retryContentHubMediaProcessing>>>

    export type RetryContentHubMediaProcessingMutationError = Error
    export type RetryContentHubMediaProcessingMutationVariables = {id: string}

    /**
 * @summary Retry a failed media processing operation
 */
export const useRetryContentHubMediaProcessing = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryContentHubMediaProcessing>>, TError,RetryContentHubMediaProcessingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof retryContentHubMediaProcessing>>,
        TError,
        RetryContentHubMediaProcessingMutationVariables,
        TContext
      > => {
      return useMutation(getRetryContentHubMediaProcessingMutationOptions(options));
    }

export const getCreateContentHubMediaDeliveryUrl = (id: string,) => {




  return `/api/content-hub/media/${id}/delivery`
}

/**
 * @summary Create an expiring provider-fetchable URL for a ready derivative
 */
export const createContentHubMediaDelivery = async (id: string,
    contentHubMediaDeliveryRequest: ContentHubMediaDeliveryRequest, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubMediaDelivery> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubMediaDelivery>(getCreateContentHubMediaDeliveryUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubMediaDeliveryRequest)
  }
);}





export const getCreateContentHubMediaDeliveryMutationKey = () => ['createContentHubMediaDelivery'] as const;

export const getCreateContentHubMediaDeliveryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createContentHubMediaDelivery>>, TError,CreateContentHubMediaDeliveryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createContentHubMediaDelivery>>, TError,CreateContentHubMediaDeliveryMutationVariables, TContext> => {

const mutationKey = getCreateContentHubMediaDeliveryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createContentHubMediaDelivery>>, CreateContentHubMediaDeliveryMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createContentHubMediaDelivery(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateContentHubMediaDeliveryMutationResult = NonNullable<Awaited<ReturnType<typeof createContentHubMediaDelivery>>>
    export type CreateContentHubMediaDeliveryMutationBody = ContentHubMediaDeliveryRequest
    export type CreateContentHubMediaDeliveryMutationError = Error
    export type CreateContentHubMediaDeliveryMutationVariables = {id: string;data: ContentHubMediaDeliveryRequest}

    /**
 * @summary Create an expiring provider-fetchable URL for a ready derivative
 */
export const useCreateContentHubMediaDelivery = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createContentHubMediaDelivery>>, TError,CreateContentHubMediaDeliveryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createContentHubMediaDelivery>>,
        TError,
        CreateContentHubMediaDeliveryMutationVariables,
        TContext
      > => {
      return useMutation(getCreateContentHubMediaDeliveryMutationOptions(options));
    }

export const getGetPublicContentHubMediaDeliveryUrl = (token: string,) => {




  return `/api/public/content-hub/media/${token}`
}

/**
 * @summary Fetch one derivative through an unguessable unexpired delivery grant
 */
export const getPublicContentHubMediaDelivery = async (token: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getGetPublicContentHubMediaDeliveryUrl(token),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPublicContentHubMediaDeliveryQueryKey = (token: string,) => {
    return [
    `/api/public/content-hub/media/${token}`
    ] as const;
    }


export const getGetPublicContentHubMediaDeliveryQueryOptions = <TData = Awaited<ReturnType<typeof getPublicContentHubMediaDelivery>>, TError = Error>(token: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicContentHubMediaDelivery>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPublicContentHubMediaDeliveryQueryKey(token);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPublicContentHubMediaDelivery>>> = ({ signal }) => getPublicContentHubMediaDelivery(token, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: token !== null && token !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPublicContentHubMediaDelivery>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPublicContentHubMediaDeliveryQueryResult = NonNullable<Awaited<ReturnType<typeof getPublicContentHubMediaDelivery>>>
export type GetPublicContentHubMediaDeliveryQueryError = Error


/**
 * @summary Fetch one derivative through an unguessable unexpired delivery grant
 */

export function useGetPublicContentHubMediaDelivery<TData = Awaited<ReturnType<typeof getPublicContentHubMediaDelivery>>, TError = Error>(
 token: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicContentHubMediaDelivery>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPublicContentHubMediaDeliveryQueryOptions(token,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListEntityProfileEnrichmentsUrl = (params?: ListEntityProfileEnrichmentsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/entity-profile/enrichments?${stringifiedParams}` : `/api/entity-profile/enrichments`
}

export const listEntityProfileEnrichments = async (params?: ListEntityProfileEnrichmentsParams, options?: Parameters<typeof customFetch>[1]): Promise<EntityProfileEnrichmentList> => {

  return customFetch<EntityProfileEnrichmentList>(getListEntityProfileEnrichmentsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListEntityProfileEnrichmentsQueryKey = (params?: ListEntityProfileEnrichmentsParams,) => {
    return [
    `/api/entity-profile/enrichments`, ...(params ? [params] : [])
    ] as const;
    }


export const getListEntityProfileEnrichmentsQueryOptions = <TData = Awaited<ReturnType<typeof listEntityProfileEnrichments>>, TError = unknown>(params?: ListEntityProfileEnrichmentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityProfileEnrichments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEntityProfileEnrichmentsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEntityProfileEnrichments>>> = ({ signal }) => listEntityProfileEnrichments(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEntityProfileEnrichments>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEntityProfileEnrichmentsQueryResult = NonNullable<Awaited<ReturnType<typeof listEntityProfileEnrichments>>>
export type ListEntityProfileEnrichmentsQueryError = unknown



export function useListEntityProfileEnrichments<TData = Awaited<ReturnType<typeof listEntityProfileEnrichments>>, TError = unknown>(
 params?: ListEntityProfileEnrichmentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEntityProfileEnrichments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEntityProfileEnrichmentsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateEntityProfileEnrichmentUrl = () => {




  return `/api/entity-profile/enrichments`
}

export const createEntityProfileEnrichment = async (entityProfileEnrichmentInput: EntityProfileEnrichmentInput, options?: Parameters<typeof customFetch>[1]): Promise<EntityProfileEnrichment> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EntityProfileEnrichment>(getCreateEntityProfileEnrichmentUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(entityProfileEnrichmentInput)
  }
);}





export const getCreateEntityProfileEnrichmentMutationKey = () => ['createEntityProfileEnrichment'] as const;

export const getCreateEntityProfileEnrichmentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEntityProfileEnrichment>>, TError,CreateEntityProfileEnrichmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createEntityProfileEnrichment>>, TError,CreateEntityProfileEnrichmentMutationVariables, TContext> => {

const mutationKey = getCreateEntityProfileEnrichmentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createEntityProfileEnrichment>>, CreateEntityProfileEnrichmentMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createEntityProfileEnrichment(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateEntityProfileEnrichmentMutationResult = NonNullable<Awaited<ReturnType<typeof createEntityProfileEnrichment>>>
    export type CreateEntityProfileEnrichmentMutationBody = EntityProfileEnrichmentInput
    export type CreateEntityProfileEnrichmentMutationError = Error
    export type CreateEntityProfileEnrichmentMutationVariables = {data: EntityProfileEnrichmentInput}

    export const useCreateEntityProfileEnrichment = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEntityProfileEnrichment>>, TError,CreateEntityProfileEnrichmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createEntityProfileEnrichment>>,
        TError,
        CreateEntityProfileEnrichmentMutationVariables,
        TContext
      > => {
      return useMutation(getCreateEntityProfileEnrichmentMutationOptions(options));
    }

export const getGetEntityProfileEnrichmentUrl = (proposalId: string,) => {




  return `/api/entity-profile/enrichments/${proposalId}`
}

export const getEntityProfileEnrichment = async (proposalId: string, options?: Parameters<typeof customFetch>[1]): Promise<EntityProfileEnrichment> => {

  return customFetch<EntityProfileEnrichment>(getGetEntityProfileEnrichmentUrl(proposalId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEntityProfileEnrichmentQueryKey = (proposalId: string,) => {
    return [
    `/api/entity-profile/enrichments/${proposalId}`
    ] as const;
    }


export const getGetEntityProfileEnrichmentQueryOptions = <TData = Awaited<ReturnType<typeof getEntityProfileEnrichment>>, TError = Error>(proposalId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityProfileEnrichment>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEntityProfileEnrichmentQueryKey(proposalId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEntityProfileEnrichment>>> = ({ signal }) => getEntityProfileEnrichment(proposalId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: proposalId !== null && proposalId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEntityProfileEnrichment>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEntityProfileEnrichmentQueryResult = NonNullable<Awaited<ReturnType<typeof getEntityProfileEnrichment>>>
export type GetEntityProfileEnrichmentQueryError = Error



export function useGetEntityProfileEnrichment<TData = Awaited<ReturnType<typeof getEntityProfileEnrichment>>, TError = Error>(
 proposalId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityProfileEnrichment>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEntityProfileEnrichmentQueryOptions(proposalId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDecideEntityProfileEnrichmentUrl = (proposalId: string,) => {




  return `/api/entity-profile/enrichments/${proposalId}`
}

export const decideEntityProfileEnrichment = async (proposalId: string,
    entityProfileEnrichmentDecision: EntityProfileEnrichmentDecision, options?: Parameters<typeof customFetch>[1]): Promise<EntityProfileEnrichment> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EntityProfileEnrichment>(getDecideEntityProfileEnrichmentUrl(proposalId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(entityProfileEnrichmentDecision)
  }
);}





export const getDecideEntityProfileEnrichmentMutationKey = () => ['decideEntityProfileEnrichment'] as const;

export const getDecideEntityProfileEnrichmentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideEntityProfileEnrichment>>, TError,DecideEntityProfileEnrichmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof decideEntityProfileEnrichment>>, TError,DecideEntityProfileEnrichmentMutationVariables, TContext> => {

const mutationKey = getDecideEntityProfileEnrichmentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof decideEntityProfileEnrichment>>, DecideEntityProfileEnrichmentMutationVariables> = (props) => {
          const {proposalId,data} = props ?? {};

          return  decideEntityProfileEnrichment(proposalId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DecideEntityProfileEnrichmentMutationResult = NonNullable<Awaited<ReturnType<typeof decideEntityProfileEnrichment>>>
    export type DecideEntityProfileEnrichmentMutationBody = EntityProfileEnrichmentDecision
    export type DecideEntityProfileEnrichmentMutationError = Error
    export type DecideEntityProfileEnrichmentMutationVariables = {proposalId: string;data: EntityProfileEnrichmentDecision}

    export const useDecideEntityProfileEnrichment = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideEntityProfileEnrichment>>, TError,DecideEntityProfileEnrichmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof decideEntityProfileEnrichment>>,
        TError,
        DecideEntityProfileEnrichmentMutationVariables,
        TContext
      > => {
      return useMutation(getDecideEntityProfileEnrichmentMutationOptions(options));
    }

export const getSelectEntityProfileEnrichmentWebsiteUrl = (proposalId: string,) => {




  return `/api/entity-profile/enrichments/${proposalId}/website`
}

export const selectEntityProfileEnrichmentWebsite = async (proposalId: string,
    entityProfileEnrichmentSelection: EntityProfileEnrichmentSelection, options?: Parameters<typeof customFetch>[1]): Promise<EntityProfileEnrichment> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EntityProfileEnrichment>(getSelectEntityProfileEnrichmentWebsiteUrl(proposalId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(entityProfileEnrichmentSelection)
  }
);}





export const getSelectEntityProfileEnrichmentWebsiteMutationKey = () => ['selectEntityProfileEnrichmentWebsite'] as const;

export const getSelectEntityProfileEnrichmentWebsiteMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof selectEntityProfileEnrichmentWebsite>>, TError,SelectEntityProfileEnrichmentWebsiteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof selectEntityProfileEnrichmentWebsite>>, TError,SelectEntityProfileEnrichmentWebsiteMutationVariables, TContext> => {

const mutationKey = getSelectEntityProfileEnrichmentWebsiteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof selectEntityProfileEnrichmentWebsite>>, SelectEntityProfileEnrichmentWebsiteMutationVariables> = (props) => {
          const {proposalId,data} = props ?? {};

          return  selectEntityProfileEnrichmentWebsite(proposalId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SelectEntityProfileEnrichmentWebsiteMutationResult = NonNullable<Awaited<ReturnType<typeof selectEntityProfileEnrichmentWebsite>>>
    export type SelectEntityProfileEnrichmentWebsiteMutationBody = EntityProfileEnrichmentSelection
    export type SelectEntityProfileEnrichmentWebsiteMutationError = Error
    export type SelectEntityProfileEnrichmentWebsiteMutationVariables = {proposalId: string;data: EntityProfileEnrichmentSelection}

    export const useSelectEntityProfileEnrichmentWebsite = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof selectEntityProfileEnrichmentWebsite>>, TError,SelectEntityProfileEnrichmentWebsiteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof selectEntityProfileEnrichmentWebsite>>,
        TError,
        SelectEntityProfileEnrichmentWebsiteMutationVariables,
        TContext
      > => {
      return useMutation(getSelectEntityProfileEnrichmentWebsiteMutationOptions(options));
    }

export const getGetEntityProfileUrl = () => {




  return `/api/entity-profile`
}

/**
 * @summary Get the entity profile used across administration and Content Hub
 */
export const getEntityProfile = async ( options?: Parameters<typeof customFetch>[1]): Promise<EntityProfile> => {

  return customFetch<EntityProfile>(getGetEntityProfileUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEntityProfileQueryKey = () => {
    return [
    `/api/entity-profile`
    ] as const;
    }


export const getGetEntityProfileQueryOptions = <TData = Awaited<ReturnType<typeof getEntityProfile>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityProfile>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEntityProfileQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEntityProfile>>> = ({ signal }) => getEntityProfile({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEntityProfile>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEntityProfileQueryResult = NonNullable<Awaited<ReturnType<typeof getEntityProfile>>>
export type GetEntityProfileQueryError = unknown


/**
 * @summary Get the entity profile used across administration and Content Hub
 */

export function useGetEntityProfile<TData = Awaited<ReturnType<typeof getEntityProfile>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityProfile>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEntityProfileQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateEntityProfileUrl = () => {




  return `/api/entity-profile`
}

/**
 * @summary Update the entity profile used across administration and Content Hub
 */
export const updateEntityProfile = async (entityProfileSave: EntityProfileSave, options?: Parameters<typeof customFetch>[1]): Promise<EntityProfile> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EntityProfile>(getUpdateEntityProfileUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(entityProfileSave)
  }
);}





export const getUpdateEntityProfileMutationKey = () => ['updateEntityProfile'] as const;

export const getUpdateEntityProfileMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEntityProfile>>, TError,UpdateEntityProfileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateEntityProfile>>, TError,UpdateEntityProfileMutationVariables, TContext> => {

const mutationKey = getUpdateEntityProfileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateEntityProfile>>, UpdateEntityProfileMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateEntityProfile(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateEntityProfileMutationResult = NonNullable<Awaited<ReturnType<typeof updateEntityProfile>>>
    export type UpdateEntityProfileMutationBody = EntityProfileSave
    export type UpdateEntityProfileMutationError = unknown
    export type UpdateEntityProfileMutationVariables = {data: EntityProfileSave}

    /**
 * @summary Update the entity profile used across administration and Content Hub
 */
export const useUpdateEntityProfile = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEntityProfile>>, TError,UpdateEntityProfileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateEntityProfile>>,
        TError,
        UpdateEntityProfileMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateEntityProfileMutationOptions(options));
    }

export const getGetEntityProfileLogoUrl = () => {




  return `/api/entity-profile/logo`
}

/**
 * @summary Download the current entity profile logo
 */
export const getEntityProfileLogo = async ( options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getGetEntityProfileLogoUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEntityProfileLogoQueryKey = () => {
    return [
    `/api/entity-profile/logo`
    ] as const;
    }


export const getGetEntityProfileLogoQueryOptions = <TData = Awaited<ReturnType<typeof getEntityProfileLogo>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityProfileLogo>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEntityProfileLogoQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEntityProfileLogo>>> = ({ signal }) => getEntityProfileLogo({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEntityProfileLogo>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEntityProfileLogoQueryResult = NonNullable<Awaited<ReturnType<typeof getEntityProfileLogo>>>
export type GetEntityProfileLogoQueryError = Error


/**
 * @summary Download the current entity profile logo
 */

export function useGetEntityProfileLogo<TData = Awaited<ReturnType<typeof getEntityProfileLogo>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEntityProfileLogo>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEntityProfileLogoQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUploadEntityProfileLogoUrl = () => {




  return `/api/entity-profile/logo`
}

/**
 * @summary Upload and select the shared entity and microsite logo
 */
export const uploadEntityProfileLogo = async (uploadEntityProfileLogoBody: UploadEntityProfileLogoBody, options?: Parameters<typeof customFetch>[1]): Promise<EntityProfileLogo> => {
    const formData = new FormData();
formData.append(`file`, uploadEntityProfileLogoBody.file);

  return customFetch<EntityProfileLogo>(getUploadEntityProfileLogoUrl(),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}





export const getUploadEntityProfileLogoMutationKey = () => ['uploadEntityProfileLogo'] as const;

export const getUploadEntityProfileLogoMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadEntityProfileLogo>>, TError,UploadEntityProfileLogoMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadEntityProfileLogo>>, TError,UploadEntityProfileLogoMutationVariables, TContext> => {

const mutationKey = getUploadEntityProfileLogoMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadEntityProfileLogo>>, UploadEntityProfileLogoMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  uploadEntityProfileLogo(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadEntityProfileLogoMutationResult = NonNullable<Awaited<ReturnType<typeof uploadEntityProfileLogo>>>
    export type UploadEntityProfileLogoMutationBody = UploadEntityProfileLogoBody
    export type UploadEntityProfileLogoMutationError = Error
    export type UploadEntityProfileLogoMutationVariables = {data: UploadEntityProfileLogoBody}

    /**
 * @summary Upload and select the shared entity and microsite logo
 */
export const useUploadEntityProfileLogo = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadEntityProfileLogo>>, TError,UploadEntityProfileLogoMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadEntityProfileLogo>>,
        TError,
        UploadEntityProfileLogoMutationVariables,
        TContext
      > => {
      return useMutation(getUploadEntityProfileLogoMutationOptions(options));
    }

export const getDeleteEntityProfileLogoUrl = () => {




  return `/api/entity-profile/logo`
}

/**
 * @summary Remove the shared entity and microsite logo
 */
export const deleteEntityProfileLogo = async ( options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteEntityProfileLogoUrl(),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteEntityProfileLogoMutationKey = () => ['deleteEntityProfileLogo'] as const;

export const getDeleteEntityProfileLogoMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteEntityProfileLogo>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteEntityProfileLogo>>, TError,void, TContext> => {

const mutationKey = getDeleteEntityProfileLogoMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteEntityProfileLogo>>, void> = () => {


          return  deleteEntityProfileLogo(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteEntityProfileLogoMutationResult = NonNullable<Awaited<ReturnType<typeof deleteEntityProfileLogo>>>

    export type DeleteEntityProfileLogoMutationError = Error


    /**
 * @summary Remove the shared entity and microsite logo
 */
export const useDeleteEntityProfileLogo = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteEntityProfileLogo>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteEntityProfileLogo>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getDeleteEntityProfileLogoMutationOptions(options));
    }

export const getSummariseEntityProfileWebsiteUrl = () => {




  return `/api/entity-profile/website-summary`
}

/**
 * @summary Produce a concise editable summary from the entity website
 */
export const summariseEntityProfileWebsite = async (entityProfileWebsiteSummaryRequest: EntityProfileWebsiteSummaryRequest, options?: Parameters<typeof customFetch>[1]): Promise<EntityProfileWebsiteSummary> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EntityProfileWebsiteSummary>(getSummariseEntityProfileWebsiteUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(entityProfileWebsiteSummaryRequest)
  }
);}





export const getSummariseEntityProfileWebsiteMutationKey = () => ['summariseEntityProfileWebsite'] as const;

export const getSummariseEntityProfileWebsiteMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof summariseEntityProfileWebsite>>, TError,SummariseEntityProfileWebsiteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof summariseEntityProfileWebsite>>, TError,SummariseEntityProfileWebsiteMutationVariables, TContext> => {

const mutationKey = getSummariseEntityProfileWebsiteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof summariseEntityProfileWebsite>>, SummariseEntityProfileWebsiteMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  summariseEntityProfileWebsite(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SummariseEntityProfileWebsiteMutationResult = NonNullable<Awaited<ReturnType<typeof summariseEntityProfileWebsite>>>
    export type SummariseEntityProfileWebsiteMutationBody = EntityProfileWebsiteSummaryRequest
    export type SummariseEntityProfileWebsiteMutationError = Error
    export type SummariseEntityProfileWebsiteMutationVariables = {data: EntityProfileWebsiteSummaryRequest}

    /**
 * @summary Produce a concise editable summary from the entity website
 */
export const useSummariseEntityProfileWebsite = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof summariseEntityProfileWebsite>>, TError,SummariseEntityProfileWebsiteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof summariseEntityProfileWebsite>>,
        TError,
        SummariseEntityProfileWebsiteMutationVariables,
        TContext
      > => {
      return useMutation(getSummariseEntityProfileWebsiteMutationOptions(options));
    }

export const getListContentHubBookingsUrl = (params?: ListContentHubBookingsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/content-hub/bookings?${stringifiedParams}` : `/api/content-hub/bookings`
}

/**
 * @summary List Content Hub bookings
 */
export const listContentHubBookings = async (params?: ListContentHubBookingsParams, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubBookingList> => {

  return customFetch<ContentHubBookingList>(getListContentHubBookingsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListContentHubBookingsQueryKey = (params?: ListContentHubBookingsParams,) => {
    return [
    `/api/content-hub/bookings`, ...(params ? [params] : [])
    ] as const;
    }


export const getListContentHubBookingsQueryOptions = <TData = Awaited<ReturnType<typeof listContentHubBookings>>, TError = unknown>(params?: ListContentHubBookingsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubBookings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListContentHubBookingsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listContentHubBookings>>> = ({ signal }) => listContentHubBookings(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listContentHubBookings>>, TError, TData> & { queryKey: QueryKey }
}

export type ListContentHubBookingsQueryResult = NonNullable<Awaited<ReturnType<typeof listContentHubBookings>>>
export type ListContentHubBookingsQueryError = unknown


/**
 * @summary List Content Hub bookings
 */

export function useListContentHubBookings<TData = Awaited<ReturnType<typeof listContentHubBookings>>, TError = unknown>(
 params?: ListContentHubBookingsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubBookings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListContentHubBookingsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateContentHubBookingUrl = () => {




  return `/api/content-hub/bookings`
}

/**
 * @summary Create a booking and link it to CRM records
 */
export const createContentHubBooking = async (contentHubBookingCreate: ContentHubBookingCreate, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubBooking> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubBooking>(getCreateContentHubBookingUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubBookingCreate)
  }
);}





export const getCreateContentHubBookingMutationKey = () => ['createContentHubBooking'] as const;

export const getCreateContentHubBookingMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createContentHubBooking>>, TError,CreateContentHubBookingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createContentHubBooking>>, TError,CreateContentHubBookingMutationVariables, TContext> => {

const mutationKey = getCreateContentHubBookingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createContentHubBooking>>, CreateContentHubBookingMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createContentHubBooking(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateContentHubBookingMutationResult = NonNullable<Awaited<ReturnType<typeof createContentHubBooking>>>
    export type CreateContentHubBookingMutationBody = ContentHubBookingCreate
    export type CreateContentHubBookingMutationError = unknown
    export type CreateContentHubBookingMutationVariables = {data: ContentHubBookingCreate}

    /**
 * @summary Create a booking and link it to CRM records
 */
export const useCreateContentHubBooking = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createContentHubBooking>>, TError,CreateContentHubBookingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createContentHubBooking>>,
        TError,
        CreateContentHubBookingMutationVariables,
        TContext
      > => {
      return useMutation(getCreateContentHubBookingMutationOptions(options));
    }

export const getUpdateContentHubBookingUrl = (id: string,) => {




  return `/api/content-hub/bookings/${id}`
}

/**
 * @summary Update a booking status or notes
 */
export const updateContentHubBooking = async (id: string,
    contentHubBookingUpdate: ContentHubBookingUpdate, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubBooking> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubBooking>(getUpdateContentHubBookingUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubBookingUpdate)
  }
);}





export const getUpdateContentHubBookingMutationKey = () => ['updateContentHubBooking'] as const;

export const getUpdateContentHubBookingMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateContentHubBooking>>, TError,UpdateContentHubBookingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateContentHubBooking>>, TError,UpdateContentHubBookingMutationVariables, TContext> => {

const mutationKey = getUpdateContentHubBookingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateContentHubBooking>>, UpdateContentHubBookingMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateContentHubBooking(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateContentHubBookingMutationResult = NonNullable<Awaited<ReturnType<typeof updateContentHubBooking>>>
    export type UpdateContentHubBookingMutationBody = ContentHubBookingUpdate
    export type UpdateContentHubBookingMutationError = Error
    export type UpdateContentHubBookingMutationVariables = {id: string;data: ContentHubBookingUpdate}

    /**
 * @summary Update a booking status or notes
 */
export const useUpdateContentHubBooking = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateContentHubBooking>>, TError,UpdateContentHubBookingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateContentHubBooking>>,
        TError,
        UpdateContentHubBookingMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateContentHubBookingMutationOptions(options));
    }

export const getListContentHubBookingHistoryUrl = (id: string,) => {




  return `/api/content-hub/bookings/${id}/history`
}

/**
 * @summary List immutable CRM-linked booking history
 */
export const listContentHubBookingHistory = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubBookingEventList> => {

  return customFetch<ContentHubBookingEventList>(getListContentHubBookingHistoryUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListContentHubBookingHistoryQueryKey = (id: string,) => {
    return [
    `/api/content-hub/bookings/${id}/history`
    ] as const;
    }


export const getListContentHubBookingHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listContentHubBookingHistory>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubBookingHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListContentHubBookingHistoryQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listContentHubBookingHistory>>> = ({ signal }) => listContentHubBookingHistory(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listContentHubBookingHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListContentHubBookingHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listContentHubBookingHistory>>>
export type ListContentHubBookingHistoryQueryError = Error


/**
 * @summary List immutable CRM-linked booking history
 */

export function useListContentHubBookingHistory<TData = Awaited<ReturnType<typeof listContentHubBookingHistory>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubBookingHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListContentHubBookingHistoryQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListContentHubBookingSlotsUrl = (params?: ListContentHubBookingSlotsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/content-hub/booking-slots?${stringifiedParams}` : `/api/content-hub/booking-slots`
}

/**
 * @summary List configured booking availability slots
 */
export const listContentHubBookingSlots = async (params?: ListContentHubBookingSlotsParams, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubBookingSlotList> => {

  return customFetch<ContentHubBookingSlotList>(getListContentHubBookingSlotsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListContentHubBookingSlotsQueryKey = (params?: ListContentHubBookingSlotsParams,) => {
    return [
    `/api/content-hub/booking-slots`, ...(params ? [params] : [])
    ] as const;
    }


export const getListContentHubBookingSlotsQueryOptions = <TData = Awaited<ReturnType<typeof listContentHubBookingSlots>>, TError = unknown>(params?: ListContentHubBookingSlotsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubBookingSlots>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListContentHubBookingSlotsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listContentHubBookingSlots>>> = ({ signal }) => listContentHubBookingSlots(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listContentHubBookingSlots>>, TError, TData> & { queryKey: QueryKey }
}

export type ListContentHubBookingSlotsQueryResult = NonNullable<Awaited<ReturnType<typeof listContentHubBookingSlots>>>
export type ListContentHubBookingSlotsQueryError = unknown


/**
 * @summary List configured booking availability slots
 */

export function useListContentHubBookingSlots<TData = Awaited<ReturnType<typeof listContentHubBookingSlots>>, TError = unknown>(
 params?: ListContentHubBookingSlotsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubBookingSlots>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListContentHubBookingSlotsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateContentHubBookingSlotUrl = () => {




  return `/api/content-hub/booking-slots`
}

/**
 * @summary Configure one capacity-controlled booking slot
 */
export const createContentHubBookingSlot = async (contentHubBookingSlotCreate: ContentHubBookingSlotCreate, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubBookingSlot> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubBookingSlot>(getCreateContentHubBookingSlotUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubBookingSlotCreate)
  }
);}





export const getCreateContentHubBookingSlotMutationKey = () => ['createContentHubBookingSlot'] as const;

export const getCreateContentHubBookingSlotMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createContentHubBookingSlot>>, TError,CreateContentHubBookingSlotMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createContentHubBookingSlot>>, TError,CreateContentHubBookingSlotMutationVariables, TContext> => {

const mutationKey = getCreateContentHubBookingSlotMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createContentHubBookingSlot>>, CreateContentHubBookingSlotMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createContentHubBookingSlot(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateContentHubBookingSlotMutationResult = NonNullable<Awaited<ReturnType<typeof createContentHubBookingSlot>>>
    export type CreateContentHubBookingSlotMutationBody = ContentHubBookingSlotCreate
    export type CreateContentHubBookingSlotMutationError = unknown
    export type CreateContentHubBookingSlotMutationVariables = {data: ContentHubBookingSlotCreate}

    /**
 * @summary Configure one capacity-controlled booking slot
 */
export const useCreateContentHubBookingSlot = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createContentHubBookingSlot>>, TError,CreateContentHubBookingSlotMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createContentHubBookingSlot>>,
        TError,
        CreateContentHubBookingSlotMutationVariables,
        TContext
      > => {
      return useMutation(getCreateContentHubBookingSlotMutationOptions(options));
    }

export const getUpdateContentHubBookingSlotUrl = (id: string,) => {




  return `/api/content-hub/booking-slots/${id}`
}

/**
 * @summary Update a booking slot without invalidating existing reservations
 */
export const updateContentHubBookingSlot = async (id: string,
    contentHubBookingSlotUpdate: ContentHubBookingSlotUpdate, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubBookingSlot> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubBookingSlot>(getUpdateContentHubBookingSlotUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubBookingSlotUpdate)
  }
);}





export const getUpdateContentHubBookingSlotMutationKey = () => ['updateContentHubBookingSlot'] as const;

export const getUpdateContentHubBookingSlotMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateContentHubBookingSlot>>, TError,UpdateContentHubBookingSlotMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateContentHubBookingSlot>>, TError,UpdateContentHubBookingSlotMutationVariables, TContext> => {

const mutationKey = getUpdateContentHubBookingSlotMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateContentHubBookingSlot>>, UpdateContentHubBookingSlotMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateContentHubBookingSlot(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateContentHubBookingSlotMutationResult = NonNullable<Awaited<ReturnType<typeof updateContentHubBookingSlot>>>
    export type UpdateContentHubBookingSlotMutationBody = ContentHubBookingSlotUpdate
    export type UpdateContentHubBookingSlotMutationError = Error
    export type UpdateContentHubBookingSlotMutationVariables = {id: string;data: ContentHubBookingSlotUpdate}

    /**
 * @summary Update a booking slot without invalidating existing reservations
 */
export const useUpdateContentHubBookingSlot = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateContentHubBookingSlot>>, TError,UpdateContentHubBookingSlotMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateContentHubBookingSlot>>,
        TError,
        UpdateContentHubBookingSlotMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateContentHubBookingSlotMutationOptions(options));
    }

export const getListContentHubAssetsUrl = (params?: ListContentHubAssetsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/content-hub/assets?${stringifiedParams}` : `/api/content-hub/assets`
}

/**
 * @summary List generated and manually created Content Hub assets
 */
export const listContentHubAssets = async (params?: ListContentHubAssetsParams, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAssetList> => {

  return customFetch<ContentHubAssetList>(getListContentHubAssetsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListContentHubAssetsQueryKey = (params?: ListContentHubAssetsParams,) => {
    return [
    `/api/content-hub/assets`, ...(params ? [params] : [])
    ] as const;
    }


export const getListContentHubAssetsQueryOptions = <TData = Awaited<ReturnType<typeof listContentHubAssets>>, TError = unknown>(params?: ListContentHubAssetsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubAssets>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListContentHubAssetsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listContentHubAssets>>> = ({ signal }) => listContentHubAssets(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listContentHubAssets>>, TError, TData> & { queryKey: QueryKey }
}

export type ListContentHubAssetsQueryResult = NonNullable<Awaited<ReturnType<typeof listContentHubAssets>>>
export type ListContentHubAssetsQueryError = unknown


/**
 * @summary List generated and manually created Content Hub assets
 */

export function useListContentHubAssets<TData = Awaited<ReturnType<typeof listContentHubAssets>>, TError = unknown>(
 params?: ListContentHubAssetsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubAssets>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListContentHubAssetsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateContentHubAssetUrl = () => {




  return `/api/content-hub/assets`
}

/**
 * @summary Create a Content Hub asset
 */
export const createContentHubAsset = async (contentHubAssetCreate: ContentHubAssetCreate, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAsset> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubAsset>(getCreateContentHubAssetUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubAssetCreate)
  }
);}





export const getCreateContentHubAssetMutationKey = () => ['createContentHubAsset'] as const;

export const getCreateContentHubAssetMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createContentHubAsset>>, TError,CreateContentHubAssetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createContentHubAsset>>, TError,CreateContentHubAssetMutationVariables, TContext> => {

const mutationKey = getCreateContentHubAssetMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createContentHubAsset>>, CreateContentHubAssetMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createContentHubAsset(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateContentHubAssetMutationResult = NonNullable<Awaited<ReturnType<typeof createContentHubAsset>>>
    export type CreateContentHubAssetMutationBody = ContentHubAssetCreate
    export type CreateContentHubAssetMutationError = unknown
    export type CreateContentHubAssetMutationVariables = {data: ContentHubAssetCreate}

    /**
 * @summary Create a Content Hub asset
 */
export const useCreateContentHubAsset = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createContentHubAsset>>, TError,CreateContentHubAssetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createContentHubAsset>>,
        TError,
        CreateContentHubAssetMutationVariables,
        TContext
      > => {
      return useMutation(getCreateContentHubAssetMutationOptions(options));
    }

export const getGenerateContentHubAssetUrl = () => {




  return `/api/content-hub/assets/generate`
}

/**
 * @summary Generate a brand-grounded Content Hub asset for human review
 */
export const generateContentHubAsset = async (contentHubAssetGenerate: ContentHubAssetGenerate, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAssetGenerationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubAssetGenerationResult>(getGenerateContentHubAssetUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubAssetGenerate)
  }
);}





export const getGenerateContentHubAssetMutationKey = () => ['generateContentHubAsset'] as const;

export const getGenerateContentHubAssetMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateContentHubAsset>>, TError,GenerateContentHubAssetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof generateContentHubAsset>>, TError,GenerateContentHubAssetMutationVariables, TContext> => {

const mutationKey = getGenerateContentHubAssetMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof generateContentHubAsset>>, GenerateContentHubAssetMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  generateContentHubAsset(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenerateContentHubAssetMutationResult = NonNullable<Awaited<ReturnType<typeof generateContentHubAsset>>>
    export type GenerateContentHubAssetMutationBody = ContentHubAssetGenerate
    export type GenerateContentHubAssetMutationError = unknown
    export type GenerateContentHubAssetMutationVariables = {data: ContentHubAssetGenerate}

    /**
 * @summary Generate a brand-grounded Content Hub asset for human review
 */
export const useGenerateContentHubAsset = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateContentHubAsset>>, TError,GenerateContentHubAssetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof generateContentHubAsset>>,
        TError,
        GenerateContentHubAssetMutationVariables,
        TContext
      > => {
      return useMutation(getGenerateContentHubAssetMutationOptions(options));
    }

export const getGetContentHubAssetUrl = (id: string,) => {




  return `/api/content-hub/assets/${id}`
}

/**
 * @summary Get an entity-scoped reusable asset
 */
export const getContentHubAsset = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAsset> => {

  return customFetch<ContentHubAsset>(getGetContentHubAssetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetContentHubAssetQueryKey = (id: string,) => {
    return [
    `/api/content-hub/assets/${id}`
    ] as const;
    }


export const getGetContentHubAssetQueryOptions = <TData = Awaited<ReturnType<typeof getContentHubAsset>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContentHubAsset>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetContentHubAssetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getContentHubAsset>>> = ({ signal }) => getContentHubAsset(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getContentHubAsset>>, TError, TData> & { queryKey: QueryKey }
}

export type GetContentHubAssetQueryResult = NonNullable<Awaited<ReturnType<typeof getContentHubAsset>>>
export type GetContentHubAssetQueryError = Error


/**
 * @summary Get an entity-scoped reusable asset
 */

export function useGetContentHubAsset<TData = Awaited<ReturnType<typeof getContentHubAsset>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContentHubAsset>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetContentHubAssetQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateContentHubAssetUrl = (id: string,) => {




  return `/api/content-hub/assets/${id}`
}

/**
 * @summary Update an asset using optimistic version control
 */
export const updateContentHubAsset = async (id: string,
    contentHubAssetUpdate: ContentHubAssetUpdate, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAsset> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubAsset>(getUpdateContentHubAssetUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubAssetUpdate)
  }
);}





export const getUpdateContentHubAssetMutationKey = () => ['updateContentHubAsset'] as const;

export const getUpdateContentHubAssetMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateContentHubAsset>>, TError,UpdateContentHubAssetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateContentHubAsset>>, TError,UpdateContentHubAssetMutationVariables, TContext> => {

const mutationKey = getUpdateContentHubAssetMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateContentHubAsset>>, UpdateContentHubAssetMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateContentHubAsset(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateContentHubAssetMutationResult = NonNullable<Awaited<ReturnType<typeof updateContentHubAsset>>>
    export type UpdateContentHubAssetMutationBody = ContentHubAssetUpdate
    export type UpdateContentHubAssetMutationError = Error
    export type UpdateContentHubAssetMutationVariables = {id: string;data: ContentHubAssetUpdate}

    /**
 * @summary Update an asset using optimistic version control
 */
export const useUpdateContentHubAsset = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateContentHubAsset>>, TError,UpdateContentHubAssetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateContentHubAsset>>,
        TError,
        UpdateContentHubAssetMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateContentHubAssetMutationOptions(options));
    }

export const getListContentHubAssetVersionsUrl = (id: string,
    params?: ListContentHubAssetVersionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/content-hub/assets/${id}/versions?${stringifiedParams}` : `/api/content-hub/assets/${id}/versions`
}

/**
 * @summary List immutable asset versions
 */
export const listContentHubAssetVersions = async (id: string,
    params?: ListContentHubAssetVersionsParams, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAssetVersionList> => {

  return customFetch<ContentHubAssetVersionList>(getListContentHubAssetVersionsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListContentHubAssetVersionsQueryKey = (id: string,
    params?: ListContentHubAssetVersionsParams,) => {
    return [
    `/api/content-hub/assets/${id}/versions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListContentHubAssetVersionsQueryOptions = <TData = Awaited<ReturnType<typeof listContentHubAssetVersions>>, TError = unknown>(id: string,
    params?: ListContentHubAssetVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubAssetVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListContentHubAssetVersionsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listContentHubAssetVersions>>> = ({ signal }) => listContentHubAssetVersions(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listContentHubAssetVersions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListContentHubAssetVersionsQueryResult = NonNullable<Awaited<ReturnType<typeof listContentHubAssetVersions>>>
export type ListContentHubAssetVersionsQueryError = unknown


/**
 * @summary List immutable asset versions
 */

export function useListContentHubAssetVersions<TData = Awaited<ReturnType<typeof listContentHubAssetVersions>>, TError = unknown>(
 id: string,
    params?: ListContentHubAssetVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubAssetVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListContentHubAssetVersionsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReuseContentHubAssetUrl = (id: string,) => {




  return `/api/content-hub/assets/${id}/reuse`
}

/**
 * @summary Reuse an asset in the shared social post editor
 */
export const reuseContentHubAsset = async (id: string,
    contentHubAssetReuse: ContentHubAssetReuse, options?: Parameters<typeof customFetch>[1]): Promise<SocialPost> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialPost>(getReuseContentHubAssetUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubAssetReuse)
  }
);}





export const getReuseContentHubAssetMutationKey = () => ['reuseContentHubAsset'] as const;

export const getReuseContentHubAssetMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reuseContentHubAsset>>, TError,ReuseContentHubAssetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reuseContentHubAsset>>, TError,ReuseContentHubAssetMutationVariables, TContext> => {

const mutationKey = getReuseContentHubAssetMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reuseContentHubAsset>>, ReuseContentHubAssetMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reuseContentHubAsset(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReuseContentHubAssetMutationResult = NonNullable<Awaited<ReturnType<typeof reuseContentHubAsset>>>
    export type ReuseContentHubAssetMutationBody = ContentHubAssetReuse
    export type ReuseContentHubAssetMutationError = unknown
    export type ReuseContentHubAssetMutationVariables = {id: string;data: ContentHubAssetReuse}

    /**
 * @summary Reuse an asset in the shared social post editor
 */
export const useReuseContentHubAsset = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reuseContentHubAsset>>, TError,ReuseContentHubAssetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reuseContentHubAsset>>,
        TError,
        ReuseContentHubAssetMutationVariables,
        TContext
      > => {
      return useMutation(getReuseContentHubAssetMutationOptions(options));
    }

export const getExportContentHubAssetUrl = (id: string,) => {




  return `/api/content-hub/assets/${id}/export`
}

/**
 * @summary Export an asset with provenance metadata
 */
export const exportContentHubAsset = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAssetExport> => {

  return customFetch<ContentHubAssetExport>(getExportContentHubAssetUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getExportContentHubAssetQueryKey = (id: string,) => {
    return [
    `/api/content-hub/assets/${id}/export`
    ] as const;
    }


export const getExportContentHubAssetQueryOptions = <TData = Awaited<ReturnType<typeof exportContentHubAsset>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof exportContentHubAsset>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getExportContentHubAssetQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof exportContentHubAsset>>> = ({ signal }) => exportContentHubAsset(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof exportContentHubAsset>>, TError, TData> & { queryKey: QueryKey }
}

export type ExportContentHubAssetQueryResult = NonNullable<Awaited<ReturnType<typeof exportContentHubAsset>>>
export type ExportContentHubAssetQueryError = unknown


/**
 * @summary Export an asset with provenance metadata
 */

export function useExportContentHubAsset<TData = Awaited<ReturnType<typeof exportContentHubAsset>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof exportContentHubAsset>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getExportContentHubAssetQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListContentHubIntelligenceSourcesUrl = () => {




  return `/api/content-hub/intelligence/sources`
}

/**
 * @summary List configured local intelligence sources and readiness
 */
export const listContentHubIntelligenceSources = async ( options?: Parameters<typeof customFetch>[1]): Promise<ContentHubIntelligenceSourceList> => {

  return customFetch<ContentHubIntelligenceSourceList>(getListContentHubIntelligenceSourcesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListContentHubIntelligenceSourcesQueryKey = () => {
    return [
    `/api/content-hub/intelligence/sources`
    ] as const;
    }


export const getListContentHubIntelligenceSourcesQueryOptions = <TData = Awaited<ReturnType<typeof listContentHubIntelligenceSources>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubIntelligenceSources>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListContentHubIntelligenceSourcesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listContentHubIntelligenceSources>>> = ({ signal }) => listContentHubIntelligenceSources({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listContentHubIntelligenceSources>>, TError, TData> & { queryKey: QueryKey }
}

export type ListContentHubIntelligenceSourcesQueryResult = NonNullable<Awaited<ReturnType<typeof listContentHubIntelligenceSources>>>
export type ListContentHubIntelligenceSourcesQueryError = unknown


/**
 * @summary List configured local intelligence sources and readiness
 */

export function useListContentHubIntelligenceSources<TData = Awaited<ReturnType<typeof listContentHubIntelligenceSources>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubIntelligenceSources>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListContentHubIntelligenceSourcesQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateContentHubIntelligenceSourceUrl = () => {




  return `/api/content-hub/intelligence/sources`
}

/**
 * @summary Configure an approved intelligence source
 */
export const createContentHubIntelligenceSource = async (contentHubIntelligenceSourceCreate: ContentHubIntelligenceSourceCreate, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubIntelligenceSource> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubIntelligenceSource>(getCreateContentHubIntelligenceSourceUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubIntelligenceSourceCreate)
  }
);}





export const getCreateContentHubIntelligenceSourceMutationKey = () => ['createContentHubIntelligenceSource'] as const;

export const getCreateContentHubIntelligenceSourceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createContentHubIntelligenceSource>>, TError,CreateContentHubIntelligenceSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createContentHubIntelligenceSource>>, TError,CreateContentHubIntelligenceSourceMutationVariables, TContext> => {

const mutationKey = getCreateContentHubIntelligenceSourceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createContentHubIntelligenceSource>>, CreateContentHubIntelligenceSourceMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createContentHubIntelligenceSource(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateContentHubIntelligenceSourceMutationResult = NonNullable<Awaited<ReturnType<typeof createContentHubIntelligenceSource>>>
    export type CreateContentHubIntelligenceSourceMutationBody = ContentHubIntelligenceSourceCreate
    export type CreateContentHubIntelligenceSourceMutationError = unknown
    export type CreateContentHubIntelligenceSourceMutationVariables = {data: ContentHubIntelligenceSourceCreate}

    /**
 * @summary Configure an approved intelligence source
 */
export const useCreateContentHubIntelligenceSource = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createContentHubIntelligenceSource>>, TError,CreateContentHubIntelligenceSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createContentHubIntelligenceSource>>,
        TError,
        CreateContentHubIntelligenceSourceMutationVariables,
        TContext
      > => {
      return useMutation(getCreateContentHubIntelligenceSourceMutationOptions(options));
    }

export const getUpdateContentHubIntelligenceSourceUrl = (id: string,) => {




  return `/api/content-hub/intelligence/sources/${id}`
}

/**
 * @summary Pause or resume an intelligence source
 */
export const updateContentHubIntelligenceSource = async (id: string,
    contentHubIntelligenceSourceUpdate: ContentHubIntelligenceSourceUpdate, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubIntelligenceSource> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubIntelligenceSource>(getUpdateContentHubIntelligenceSourceUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubIntelligenceSourceUpdate)
  }
);}





export const getUpdateContentHubIntelligenceSourceMutationKey = () => ['updateContentHubIntelligenceSource'] as const;

export const getUpdateContentHubIntelligenceSourceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateContentHubIntelligenceSource>>, TError,UpdateContentHubIntelligenceSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateContentHubIntelligenceSource>>, TError,UpdateContentHubIntelligenceSourceMutationVariables, TContext> => {

const mutationKey = getUpdateContentHubIntelligenceSourceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateContentHubIntelligenceSource>>, UpdateContentHubIntelligenceSourceMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateContentHubIntelligenceSource(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateContentHubIntelligenceSourceMutationResult = NonNullable<Awaited<ReturnType<typeof updateContentHubIntelligenceSource>>>
    export type UpdateContentHubIntelligenceSourceMutationBody = ContentHubIntelligenceSourceUpdate
    export type UpdateContentHubIntelligenceSourceMutationError = unknown
    export type UpdateContentHubIntelligenceSourceMutationVariables = {id: string;data: ContentHubIntelligenceSourceUpdate}

    /**
 * @summary Pause or resume an intelligence source
 */
export const useUpdateContentHubIntelligenceSource = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateContentHubIntelligenceSource>>, TError,UpdateContentHubIntelligenceSourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateContentHubIntelligenceSource>>,
        TError,
        UpdateContentHubIntelligenceSourceMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateContentHubIntelligenceSourceMutationOptions(options));
    }

export const getIngestContentHubIntelligenceUrl = (id: string,) => {




  return `/api/content-hub/intelligence/sources/${id}/ingest`
}

/**
 * @summary Ingest a bounded batch of untrusted attributed intelligence
 */
export const ingestContentHubIntelligence = async (id: string,
    contentHubIntelligenceIngest: ContentHubIntelligenceIngest, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubIntelligenceIngestResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubIntelligenceIngestResult>(getIngestContentHubIntelligenceUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubIntelligenceIngest)
  }
);}





export const getIngestContentHubIntelligenceMutationKey = () => ['ingestContentHubIntelligence'] as const;

export const getIngestContentHubIntelligenceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof ingestContentHubIntelligence>>, TError,IngestContentHubIntelligenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof ingestContentHubIntelligence>>, TError,IngestContentHubIntelligenceMutationVariables, TContext> => {

const mutationKey = getIngestContentHubIntelligenceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof ingestContentHubIntelligence>>, IngestContentHubIntelligenceMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  ingestContentHubIntelligence(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type IngestContentHubIntelligenceMutationResult = NonNullable<Awaited<ReturnType<typeof ingestContentHubIntelligence>>>
    export type IngestContentHubIntelligenceMutationBody = ContentHubIntelligenceIngest
    export type IngestContentHubIntelligenceMutationError = unknown
    export type IngestContentHubIntelligenceMutationVariables = {id: string;data: ContentHubIntelligenceIngest}

    /**
 * @summary Ingest a bounded batch of untrusted attributed intelligence
 */
export const useIngestContentHubIntelligence = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof ingestContentHubIntelligence>>, TError,IngestContentHubIntelligenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof ingestContentHubIntelligence>>,
        TError,
        IngestContentHubIntelligenceMutationVariables,
        TContext
      > => {
      return useMutation(getIngestContentHubIntelligenceMutationOptions(options));
    }

export const getListContentHubIntelligenceItemsUrl = (params?: ListContentHubIntelligenceItemsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/content-hub/intelligence/items?${stringifiedParams}` : `/api/content-hub/intelligence/items`
}

/**
 * @summary List attributed intelligence awaiting or after review
 */
export const listContentHubIntelligenceItems = async (params?: ListContentHubIntelligenceItemsParams, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubIntelligenceItemList> => {

  return customFetch<ContentHubIntelligenceItemList>(getListContentHubIntelligenceItemsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListContentHubIntelligenceItemsQueryKey = (params?: ListContentHubIntelligenceItemsParams,) => {
    return [
    `/api/content-hub/intelligence/items`, ...(params ? [params] : [])
    ] as const;
    }


export const getListContentHubIntelligenceItemsQueryOptions = <TData = Awaited<ReturnType<typeof listContentHubIntelligenceItems>>, TError = unknown>(params?: ListContentHubIntelligenceItemsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubIntelligenceItems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListContentHubIntelligenceItemsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listContentHubIntelligenceItems>>> = ({ signal }) => listContentHubIntelligenceItems(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listContentHubIntelligenceItems>>, TError, TData> & { queryKey: QueryKey }
}

export type ListContentHubIntelligenceItemsQueryResult = NonNullable<Awaited<ReturnType<typeof listContentHubIntelligenceItems>>>
export type ListContentHubIntelligenceItemsQueryError = unknown


/**
 * @summary List attributed intelligence awaiting or after review
 */

export function useListContentHubIntelligenceItems<TData = Awaited<ReturnType<typeof listContentHubIntelligenceItems>>, TError = unknown>(
 params?: ListContentHubIntelligenceItemsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubIntelligenceItems>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListContentHubIntelligenceItemsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewContentHubIntelligenceItemUrl = (id: string,) => {




  return `/api/content-hub/intelligence/items/${id}/review`
}

/**
 * @summary Record an explicit human review decision
 */
export const reviewContentHubIntelligenceItem = async (id: string,
    contentHubIntelligenceReview: ContentHubIntelligenceReview, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubIntelligenceItem> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubIntelligenceItem>(getReviewContentHubIntelligenceItemUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubIntelligenceReview)
  }
);}





export const getReviewContentHubIntelligenceItemMutationKey = () => ['reviewContentHubIntelligenceItem'] as const;

export const getReviewContentHubIntelligenceItemMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewContentHubIntelligenceItem>>, TError,ReviewContentHubIntelligenceItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewContentHubIntelligenceItem>>, TError,ReviewContentHubIntelligenceItemMutationVariables, TContext> => {

const mutationKey = getReviewContentHubIntelligenceItemMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewContentHubIntelligenceItem>>, ReviewContentHubIntelligenceItemMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reviewContentHubIntelligenceItem(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewContentHubIntelligenceItemMutationResult = NonNullable<Awaited<ReturnType<typeof reviewContentHubIntelligenceItem>>>
    export type ReviewContentHubIntelligenceItemMutationBody = ContentHubIntelligenceReview
    export type ReviewContentHubIntelligenceItemMutationError = unknown
    export type ReviewContentHubIntelligenceItemMutationVariables = {id: string;data: ContentHubIntelligenceReview}

    /**
 * @summary Record an explicit human review decision
 */
export const useReviewContentHubIntelligenceItem = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewContentHubIntelligenceItem>>, TError,ReviewContentHubIntelligenceItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewContentHubIntelligenceItem>>,
        TError,
        ReviewContentHubIntelligenceItemMutationVariables,
        TContext
      > => {
      return useMutation(getReviewContentHubIntelligenceItemMutationOptions(options));
    }

export const getConvertContentHubIntelligenceItemUrl = (id: string,) => {




  return `/api/content-hub/intelligence/items/${id}/convert`
}

/**
 * @summary Convert a reviewed item to a standard social draft
 */
export const convertContentHubIntelligenceItem = async (id: string,
    contentHubIntelligenceConvert: ContentHubIntelligenceConvert, options?: Parameters<typeof customFetch>[1]): Promise<SocialPost> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialPost>(getConvertContentHubIntelligenceItemUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubIntelligenceConvert)
  }
);}





export const getConvertContentHubIntelligenceItemMutationKey = () => ['convertContentHubIntelligenceItem'] as const;

export const getConvertContentHubIntelligenceItemMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof convertContentHubIntelligenceItem>>, TError,ConvertContentHubIntelligenceItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof convertContentHubIntelligenceItem>>, TError,ConvertContentHubIntelligenceItemMutationVariables, TContext> => {

const mutationKey = getConvertContentHubIntelligenceItemMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof convertContentHubIntelligenceItem>>, ConvertContentHubIntelligenceItemMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  convertContentHubIntelligenceItem(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConvertContentHubIntelligenceItemMutationResult = NonNullable<Awaited<ReturnType<typeof convertContentHubIntelligenceItem>>>
    export type ConvertContentHubIntelligenceItemMutationBody = ContentHubIntelligenceConvert
    export type ConvertContentHubIntelligenceItemMutationError = unknown
    export type ConvertContentHubIntelligenceItemMutationVariables = {id: string;data: ContentHubIntelligenceConvert}

    /**
 * @summary Convert a reviewed item to a standard social draft
 */
export const useConvertContentHubIntelligenceItem = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof convertContentHubIntelligenceItem>>, TError,ConvertContentHubIntelligenceItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof convertContentHubIntelligenceItem>>,
        TError,
        ConvertContentHubIntelligenceItemMutationVariables,
        TContext
      > => {
      return useMutation(getConvertContentHubIntelligenceItemMutationOptions(options));
    }

export const getListContentHubAdvertisingProvidersUrl = () => {




  return `/api/content-hub/advertising/providers`
}

/**
 * @summary List advertising product and account readiness without exposing credentials
 */
export const listContentHubAdvertisingProviders = async ( options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAdvertisingProviderList> => {

  return customFetch<ContentHubAdvertisingProviderList>(getListContentHubAdvertisingProvidersUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListContentHubAdvertisingProvidersQueryKey = () => {
    return [
    `/api/content-hub/advertising/providers`
    ] as const;
    }


export const getListContentHubAdvertisingProvidersQueryOptions = <TData = Awaited<ReturnType<typeof listContentHubAdvertisingProviders>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubAdvertisingProviders>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListContentHubAdvertisingProvidersQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listContentHubAdvertisingProviders>>> = ({ signal }) => listContentHubAdvertisingProviders({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listContentHubAdvertisingProviders>>, TError, TData> & { queryKey: QueryKey }
}

export type ListContentHubAdvertisingProvidersQueryResult = NonNullable<Awaited<ReturnType<typeof listContentHubAdvertisingProviders>>>
export type ListContentHubAdvertisingProvidersQueryError = unknown


/**
 * @summary List advertising product and account readiness without exposing credentials
 */

export function useListContentHubAdvertisingProviders<TData = Awaited<ReturnType<typeof listContentHubAdvertisingProviders>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubAdvertisingProviders>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListContentHubAdvertisingProvidersQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListContentHubAdvertisingCampaignsUrl = (params?: ListContentHubAdvertisingCampaignsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/content-hub/advertising/campaigns?${stringifiedParams}` : `/api/content-hub/advertising/campaigns`
}

/**
 * @summary List entity-scoped advertising campaigns
 */
export const listContentHubAdvertisingCampaigns = async (params?: ListContentHubAdvertisingCampaignsParams, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAdvertisingCampaignList> => {

  return customFetch<ContentHubAdvertisingCampaignList>(getListContentHubAdvertisingCampaignsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListContentHubAdvertisingCampaignsQueryKey = (params?: ListContentHubAdvertisingCampaignsParams,) => {
    return [
    `/api/content-hub/advertising/campaigns`, ...(params ? [params] : [])
    ] as const;
    }


export const getListContentHubAdvertisingCampaignsQueryOptions = <TData = Awaited<ReturnType<typeof listContentHubAdvertisingCampaigns>>, TError = unknown>(params?: ListContentHubAdvertisingCampaignsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubAdvertisingCampaigns>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListContentHubAdvertisingCampaignsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listContentHubAdvertisingCampaigns>>> = ({ signal }) => listContentHubAdvertisingCampaigns(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listContentHubAdvertisingCampaigns>>, TError, TData> & { queryKey: QueryKey }
}

export type ListContentHubAdvertisingCampaignsQueryResult = NonNullable<Awaited<ReturnType<typeof listContentHubAdvertisingCampaigns>>>
export type ListContentHubAdvertisingCampaignsQueryError = unknown


/**
 * @summary List entity-scoped advertising campaigns
 */

export function useListContentHubAdvertisingCampaigns<TData = Awaited<ReturnType<typeof listContentHubAdvertisingCampaigns>>, TError = unknown>(
 params?: ListContentHubAdvertisingCampaignsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubAdvertisingCampaigns>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListContentHubAdvertisingCampaignsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateContentHubAdvertisingCampaignUrl = () => {




  return `/api/content-hub/advertising/campaigns`
}

/**
 * @summary Create an inert advertising draft from approved content
 */
export const createContentHubAdvertisingCampaign = async (contentHubAdvertisingCampaignCreate: ContentHubAdvertisingCampaignCreate, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAdvertisingCampaign> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubAdvertisingCampaign>(getCreateContentHubAdvertisingCampaignUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubAdvertisingCampaignCreate)
  }
);}





export const getCreateContentHubAdvertisingCampaignMutationKey = () => ['createContentHubAdvertisingCampaign'] as const;

export const getCreateContentHubAdvertisingCampaignMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createContentHubAdvertisingCampaign>>, TError,CreateContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createContentHubAdvertisingCampaign>>, TError,CreateContentHubAdvertisingCampaignMutationVariables, TContext> => {

const mutationKey = getCreateContentHubAdvertisingCampaignMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createContentHubAdvertisingCampaign>>, CreateContentHubAdvertisingCampaignMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createContentHubAdvertisingCampaign(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateContentHubAdvertisingCampaignMutationResult = NonNullable<Awaited<ReturnType<typeof createContentHubAdvertisingCampaign>>>
    export type CreateContentHubAdvertisingCampaignMutationBody = ContentHubAdvertisingCampaignCreate
    export type CreateContentHubAdvertisingCampaignMutationError = Error
    export type CreateContentHubAdvertisingCampaignMutationVariables = {data: ContentHubAdvertisingCampaignCreate}

    /**
 * @summary Create an inert advertising draft from approved content
 */
export const useCreateContentHubAdvertisingCampaign = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createContentHubAdvertisingCampaign>>, TError,CreateContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createContentHubAdvertisingCampaign>>,
        TError,
        CreateContentHubAdvertisingCampaignMutationVariables,
        TContext
      > => {
      return useMutation(getCreateContentHubAdvertisingCampaignMutationOptions(options));
    }

export const getGetContentHubAdvertisingCampaignUrl = (id: string,) => {




  return `/api/content-hub/advertising/campaigns/${id}`
}

/**
 * @summary Get campaign settings and sourced provider result
 */
export const getContentHubAdvertisingCampaign = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAdvertisingCampaign> => {

  return customFetch<ContentHubAdvertisingCampaign>(getGetContentHubAdvertisingCampaignUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetContentHubAdvertisingCampaignQueryKey = (id: string,) => {
    return [
    `/api/content-hub/advertising/campaigns/${id}`
    ] as const;
    }


export const getGetContentHubAdvertisingCampaignQueryOptions = <TData = Awaited<ReturnType<typeof getContentHubAdvertisingCampaign>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContentHubAdvertisingCampaign>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetContentHubAdvertisingCampaignQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getContentHubAdvertisingCampaign>>> = ({ signal }) => getContentHubAdvertisingCampaign(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getContentHubAdvertisingCampaign>>, TError, TData> & { queryKey: QueryKey }
}

export type GetContentHubAdvertisingCampaignQueryResult = NonNullable<Awaited<ReturnType<typeof getContentHubAdvertisingCampaign>>>
export type GetContentHubAdvertisingCampaignQueryError = Error


/**
 * @summary Get campaign settings and sourced provider result
 */

export function useGetContentHubAdvertisingCampaign<TData = Awaited<ReturnType<typeof getContentHubAdvertisingCampaign>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContentHubAdvertisingCampaign>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetContentHubAdvertisingCampaignQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateContentHubAdvertisingCampaignUrl = (id: string,) => {




  return `/api/content-hub/advertising/campaigns/${id}`
}

/**
 * @summary Create a new immutable settings version and clear approval
 */
export const updateContentHubAdvertisingCampaign = async (id: string,
    contentHubAdvertisingCampaignUpdate: ContentHubAdvertisingCampaignUpdate, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAdvertisingCampaign> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubAdvertisingCampaign>(getUpdateContentHubAdvertisingCampaignUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubAdvertisingCampaignUpdate)
  }
);}





export const getUpdateContentHubAdvertisingCampaignMutationKey = () => ['updateContentHubAdvertisingCampaign'] as const;

export const getUpdateContentHubAdvertisingCampaignMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateContentHubAdvertisingCampaign>>, TError,UpdateContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateContentHubAdvertisingCampaign>>, TError,UpdateContentHubAdvertisingCampaignMutationVariables, TContext> => {

const mutationKey = getUpdateContentHubAdvertisingCampaignMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateContentHubAdvertisingCampaign>>, UpdateContentHubAdvertisingCampaignMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateContentHubAdvertisingCampaign(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateContentHubAdvertisingCampaignMutationResult = NonNullable<Awaited<ReturnType<typeof updateContentHubAdvertisingCampaign>>>
    export type UpdateContentHubAdvertisingCampaignMutationBody = ContentHubAdvertisingCampaignUpdate
    export type UpdateContentHubAdvertisingCampaignMutationError = Error
    export type UpdateContentHubAdvertisingCampaignMutationVariables = {id: string;data: ContentHubAdvertisingCampaignUpdate}

    /**
 * @summary Create a new immutable settings version and clear approval
 */
export const useUpdateContentHubAdvertisingCampaign = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateContentHubAdvertisingCampaign>>, TError,UpdateContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateContentHubAdvertisingCampaign>>,
        TError,
        UpdateContentHubAdvertisingCampaignMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateContentHubAdvertisingCampaignMutationOptions(options));
    }

export const getListContentHubAdvertisingCampaignVersionsUrl = (id: string,
    params?: ListContentHubAdvertisingCampaignVersionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/content-hub/advertising/campaigns/${id}/versions?${stringifiedParams}` : `/api/content-hub/advertising/campaigns/${id}/versions`
}

/**
 * @summary List immutable advertising settings history
 */
export const listContentHubAdvertisingCampaignVersions = async (id: string,
    params?: ListContentHubAdvertisingCampaignVersionsParams, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAdvertisingVersionList> => {

  return customFetch<ContentHubAdvertisingVersionList>(getListContentHubAdvertisingCampaignVersionsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListContentHubAdvertisingCampaignVersionsQueryKey = (id: string,
    params?: ListContentHubAdvertisingCampaignVersionsParams,) => {
    return [
    `/api/content-hub/advertising/campaigns/${id}/versions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListContentHubAdvertisingCampaignVersionsQueryOptions = <TData = Awaited<ReturnType<typeof listContentHubAdvertisingCampaignVersions>>, TError = unknown>(id: string,
    params?: ListContentHubAdvertisingCampaignVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubAdvertisingCampaignVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListContentHubAdvertisingCampaignVersionsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listContentHubAdvertisingCampaignVersions>>> = ({ signal }) => listContentHubAdvertisingCampaignVersions(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listContentHubAdvertisingCampaignVersions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListContentHubAdvertisingCampaignVersionsQueryResult = NonNullable<Awaited<ReturnType<typeof listContentHubAdvertisingCampaignVersions>>>
export type ListContentHubAdvertisingCampaignVersionsQueryError = unknown


/**
 * @summary List immutable advertising settings history
 */

export function useListContentHubAdvertisingCampaignVersions<TData = Awaited<ReturnType<typeof listContentHubAdvertisingCampaignVersions>>, TError = unknown>(
 id: string,
    params?: ListContentHubAdvertisingCampaignVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubAdvertisingCampaignVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListContentHubAdvertisingCampaignVersionsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListContentHubAdvertisingCampaignAttemptsUrl = (id: string,
    params?: ListContentHubAdvertisingCampaignAttemptsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/content-hub/advertising/campaigns/${id}/attempts?${stringifiedParams}` : `/api/content-hub/advertising/campaigns/${id}/attempts`
}

/**
 * @summary List provider lifecycle attempts and outcomes
 */
export const listContentHubAdvertisingCampaignAttempts = async (id: string,
    params?: ListContentHubAdvertisingCampaignAttemptsParams, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAdvertisingAttemptList> => {

  return customFetch<ContentHubAdvertisingAttemptList>(getListContentHubAdvertisingCampaignAttemptsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListContentHubAdvertisingCampaignAttemptsQueryKey = (id: string,
    params?: ListContentHubAdvertisingCampaignAttemptsParams,) => {
    return [
    `/api/content-hub/advertising/campaigns/${id}/attempts`, ...(params ? [params] : [])
    ] as const;
    }


export const getListContentHubAdvertisingCampaignAttemptsQueryOptions = <TData = Awaited<ReturnType<typeof listContentHubAdvertisingCampaignAttempts>>, TError = unknown>(id: string,
    params?: ListContentHubAdvertisingCampaignAttemptsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubAdvertisingCampaignAttempts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListContentHubAdvertisingCampaignAttemptsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listContentHubAdvertisingCampaignAttempts>>> = ({ signal }) => listContentHubAdvertisingCampaignAttempts(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listContentHubAdvertisingCampaignAttempts>>, TError, TData> & { queryKey: QueryKey }
}

export type ListContentHubAdvertisingCampaignAttemptsQueryResult = NonNullable<Awaited<ReturnType<typeof listContentHubAdvertisingCampaignAttempts>>>
export type ListContentHubAdvertisingCampaignAttemptsQueryError = unknown


/**
 * @summary List provider lifecycle attempts and outcomes
 */

export function useListContentHubAdvertisingCampaignAttempts<TData = Awaited<ReturnType<typeof listContentHubAdvertisingCampaignAttempts>>, TError = unknown>(
 id: string,
    params?: ListContentHubAdvertisingCampaignAttemptsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubAdvertisingCampaignAttempts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListContentHubAdvertisingCampaignAttemptsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSubmitContentHubAdvertisingCampaignUrl = (id: string,) => {




  return `/api/content-hub/advertising/campaigns/${id}/submit`
}

/**
 * @summary Submit the current inert draft for approval
 */
export const submitContentHubAdvertisingCampaign = async (id: string,
    contentHubAdvertisingVersionAction: ContentHubAdvertisingVersionAction, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAdvertisingCampaign> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubAdvertisingCampaign>(getSubmitContentHubAdvertisingCampaignUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubAdvertisingVersionAction)
  }
);}





export const getSubmitContentHubAdvertisingCampaignMutationKey = () => ['submitContentHubAdvertisingCampaign'] as const;

export const getSubmitContentHubAdvertisingCampaignMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitContentHubAdvertisingCampaign>>, TError,SubmitContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof submitContentHubAdvertisingCampaign>>, TError,SubmitContentHubAdvertisingCampaignMutationVariables, TContext> => {

const mutationKey = getSubmitContentHubAdvertisingCampaignMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof submitContentHubAdvertisingCampaign>>, SubmitContentHubAdvertisingCampaignMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  submitContentHubAdvertisingCampaign(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SubmitContentHubAdvertisingCampaignMutationResult = NonNullable<Awaited<ReturnType<typeof submitContentHubAdvertisingCampaign>>>
    export type SubmitContentHubAdvertisingCampaignMutationBody = ContentHubAdvertisingVersionAction
    export type SubmitContentHubAdvertisingCampaignMutationError = unknown
    export type SubmitContentHubAdvertisingCampaignMutationVariables = {id: string;data: ContentHubAdvertisingVersionAction}

    /**
 * @summary Submit the current inert draft for approval
 */
export const useSubmitContentHubAdvertisingCampaign = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitContentHubAdvertisingCampaign>>, TError,SubmitContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof submitContentHubAdvertisingCampaign>>,
        TError,
        SubmitContentHubAdvertisingCampaignMutationVariables,
        TContext
      > => {
      return useMutation(getSubmitContentHubAdvertisingCampaignMutationOptions(options));
    }

export const getApproveContentHubAdvertisingCampaignUrl = (id: string,) => {




  return `/api/content-hub/advertising/campaigns/${id}/approve`
}

/**
 * @summary Approve the immutable current settings as the authenticated actor
 */
export const approveContentHubAdvertisingCampaign = async (id: string,
    contentHubAdvertisingVersionAction: ContentHubAdvertisingVersionAction, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAdvertisingCampaign> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubAdvertisingCampaign>(getApproveContentHubAdvertisingCampaignUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubAdvertisingVersionAction)
  }
);}





export const getApproveContentHubAdvertisingCampaignMutationKey = () => ['approveContentHubAdvertisingCampaign'] as const;

export const getApproveContentHubAdvertisingCampaignMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveContentHubAdvertisingCampaign>>, TError,ApproveContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof approveContentHubAdvertisingCampaign>>, TError,ApproveContentHubAdvertisingCampaignMutationVariables, TContext> => {

const mutationKey = getApproveContentHubAdvertisingCampaignMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof approveContentHubAdvertisingCampaign>>, ApproveContentHubAdvertisingCampaignMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  approveContentHubAdvertisingCampaign(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApproveContentHubAdvertisingCampaignMutationResult = NonNullable<Awaited<ReturnType<typeof approveContentHubAdvertisingCampaign>>>
    export type ApproveContentHubAdvertisingCampaignMutationBody = ContentHubAdvertisingVersionAction
    export type ApproveContentHubAdvertisingCampaignMutationError = unknown
    export type ApproveContentHubAdvertisingCampaignMutationVariables = {id: string;data: ContentHubAdvertisingVersionAction}

    /**
 * @summary Approve the immutable current settings as the authenticated actor
 */
export const useApproveContentHubAdvertisingCampaign = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveContentHubAdvertisingCampaign>>, TError,ApproveContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof approveContentHubAdvertisingCampaign>>,
        TError,
        ApproveContentHubAdvertisingCampaignMutationVariables,
        TContext
      > => {
      return useMutation(getApproveContentHubAdvertisingCampaignMutationOptions(options));
    }

export const getRejectContentHubAdvertisingCampaignUrl = (id: string,) => {




  return `/api/content-hub/advertising/campaigns/${id}/reject`
}

/**
 * @summary Reject the current settings back to an inert draft
 */
export const rejectContentHubAdvertisingCampaign = async (id: string,
    contentHubAdvertisingReject: ContentHubAdvertisingReject, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAdvertisingCampaign> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubAdvertisingCampaign>(getRejectContentHubAdvertisingCampaignUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubAdvertisingReject)
  }
);}





export const getRejectContentHubAdvertisingCampaignMutationKey = () => ['rejectContentHubAdvertisingCampaign'] as const;

export const getRejectContentHubAdvertisingCampaignMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rejectContentHubAdvertisingCampaign>>, TError,RejectContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof rejectContentHubAdvertisingCampaign>>, TError,RejectContentHubAdvertisingCampaignMutationVariables, TContext> => {

const mutationKey = getRejectContentHubAdvertisingCampaignMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof rejectContentHubAdvertisingCampaign>>, RejectContentHubAdvertisingCampaignMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  rejectContentHubAdvertisingCampaign(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RejectContentHubAdvertisingCampaignMutationResult = NonNullable<Awaited<ReturnType<typeof rejectContentHubAdvertisingCampaign>>>
    export type RejectContentHubAdvertisingCampaignMutationBody = ContentHubAdvertisingReject
    export type RejectContentHubAdvertisingCampaignMutationError = unknown
    export type RejectContentHubAdvertisingCampaignMutationVariables = {id: string;data: ContentHubAdvertisingReject}

    /**
 * @summary Reject the current settings back to an inert draft
 */
export const useRejectContentHubAdvertisingCampaign = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rejectContentHubAdvertisingCampaign>>, TError,RejectContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof rejectContentHubAdvertisingCampaign>>,
        TError,
        RejectContentHubAdvertisingCampaignMutationVariables,
        TContext
      > => {
      return useMutation(getRejectContentHubAdvertisingCampaignMutationOptions(options));
    }

export const getLaunchContentHubAdvertisingCampaignUrl = (id: string,) => {




  return `/api/content-hub/advertising/campaigns/${id}/launch`
}

/**
 * @summary Explicitly launch approved settings through an eligible advertising product
 */
export const launchContentHubAdvertisingCampaign = async (id: string,
    contentHubAdvertisingProviderAction: ContentHubAdvertisingProviderAction, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAdvertisingCampaign> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubAdvertisingCampaign>(getLaunchContentHubAdvertisingCampaignUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubAdvertisingProviderAction)
  }
);}





export const getLaunchContentHubAdvertisingCampaignMutationKey = () => ['launchContentHubAdvertisingCampaign'] as const;

export const getLaunchContentHubAdvertisingCampaignMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof launchContentHubAdvertisingCampaign>>, TError,LaunchContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof launchContentHubAdvertisingCampaign>>, TError,LaunchContentHubAdvertisingCampaignMutationVariables, TContext> => {

const mutationKey = getLaunchContentHubAdvertisingCampaignMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof launchContentHubAdvertisingCampaign>>, LaunchContentHubAdvertisingCampaignMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  launchContentHubAdvertisingCampaign(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type LaunchContentHubAdvertisingCampaignMutationResult = NonNullable<Awaited<ReturnType<typeof launchContentHubAdvertisingCampaign>>>
    export type LaunchContentHubAdvertisingCampaignMutationBody = ContentHubAdvertisingProviderAction
    export type LaunchContentHubAdvertisingCampaignMutationError = Error
    export type LaunchContentHubAdvertisingCampaignMutationVariables = {id: string;data: ContentHubAdvertisingProviderAction}

    /**
 * @summary Explicitly launch approved settings through an eligible advertising product
 */
export const useLaunchContentHubAdvertisingCampaign = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof launchContentHubAdvertisingCampaign>>, TError,LaunchContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof launchContentHubAdvertisingCampaign>>,
        TError,
        LaunchContentHubAdvertisingCampaignMutationVariables,
        TContext
      > => {
      return useMutation(getLaunchContentHubAdvertisingCampaignMutationOptions(options));
    }

export const getPauseContentHubAdvertisingCampaignUrl = (id: string,) => {




  return `/api/content-hub/advertising/campaigns/${id}/pause`
}

/**
 * @summary Pause an active provider campaign
 */
export const pauseContentHubAdvertisingCampaign = async (id: string,
    contentHubAdvertisingProviderActionWithoutConfirmation: ContentHubAdvertisingProviderActionWithoutConfirmation, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAdvertisingCampaign> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubAdvertisingCampaign>(getPauseContentHubAdvertisingCampaignUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubAdvertisingProviderActionWithoutConfirmation)
  }
);}





export const getPauseContentHubAdvertisingCampaignMutationKey = () => ['pauseContentHubAdvertisingCampaign'] as const;

export const getPauseContentHubAdvertisingCampaignMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof pauseContentHubAdvertisingCampaign>>, TError,PauseContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof pauseContentHubAdvertisingCampaign>>, TError,PauseContentHubAdvertisingCampaignMutationVariables, TContext> => {

const mutationKey = getPauseContentHubAdvertisingCampaignMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof pauseContentHubAdvertisingCampaign>>, PauseContentHubAdvertisingCampaignMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  pauseContentHubAdvertisingCampaign(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PauseContentHubAdvertisingCampaignMutationResult = NonNullable<Awaited<ReturnType<typeof pauseContentHubAdvertisingCampaign>>>
    export type PauseContentHubAdvertisingCampaignMutationBody = ContentHubAdvertisingProviderActionWithoutConfirmation
    export type PauseContentHubAdvertisingCampaignMutationError = unknown
    export type PauseContentHubAdvertisingCampaignMutationVariables = {id: string;data: ContentHubAdvertisingProviderActionWithoutConfirmation}

    /**
 * @summary Pause an active provider campaign
 */
export const usePauseContentHubAdvertisingCampaign = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof pauseContentHubAdvertisingCampaign>>, TError,PauseContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof pauseContentHubAdvertisingCampaign>>,
        TError,
        PauseContentHubAdvertisingCampaignMutationVariables,
        TContext
      > => {
      return useMutation(getPauseContentHubAdvertisingCampaignMutationOptions(options));
    }

export const getResumeContentHubAdvertisingCampaignUrl = (id: string,) => {




  return `/api/content-hub/advertising/campaigns/${id}/resume`
}

/**
 * @summary Resume or apply newly approved settings to a paused campaign
 */
export const resumeContentHubAdvertisingCampaign = async (id: string,
    contentHubAdvertisingProviderActionWithoutConfirmation: ContentHubAdvertisingProviderActionWithoutConfirmation, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAdvertisingCampaign> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubAdvertisingCampaign>(getResumeContentHubAdvertisingCampaignUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubAdvertisingProviderActionWithoutConfirmation)
  }
);}





export const getResumeContentHubAdvertisingCampaignMutationKey = () => ['resumeContentHubAdvertisingCampaign'] as const;

export const getResumeContentHubAdvertisingCampaignMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resumeContentHubAdvertisingCampaign>>, TError,ResumeContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof resumeContentHubAdvertisingCampaign>>, TError,ResumeContentHubAdvertisingCampaignMutationVariables, TContext> => {

const mutationKey = getResumeContentHubAdvertisingCampaignMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof resumeContentHubAdvertisingCampaign>>, ResumeContentHubAdvertisingCampaignMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  resumeContentHubAdvertisingCampaign(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResumeContentHubAdvertisingCampaignMutationResult = NonNullable<Awaited<ReturnType<typeof resumeContentHubAdvertisingCampaign>>>
    export type ResumeContentHubAdvertisingCampaignMutationBody = ContentHubAdvertisingProviderActionWithoutConfirmation
    export type ResumeContentHubAdvertisingCampaignMutationError = unknown
    export type ResumeContentHubAdvertisingCampaignMutationVariables = {id: string;data: ContentHubAdvertisingProviderActionWithoutConfirmation}

    /**
 * @summary Resume or apply newly approved settings to a paused campaign
 */
export const useResumeContentHubAdvertisingCampaign = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resumeContentHubAdvertisingCampaign>>, TError,ResumeContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof resumeContentHubAdvertisingCampaign>>,
        TError,
        ResumeContentHubAdvertisingCampaignMutationVariables,
        TContext
      > => {
      return useMutation(getResumeContentHubAdvertisingCampaignMutationOptions(options));
    }

export const getCancelContentHubAdvertisingCampaignUrl = (id: string,) => {




  return `/api/content-hub/advertising/campaigns/${id}/cancel`
}

/**
 * @summary Cancel a local draft or provider campaign
 */
export const cancelContentHubAdvertisingCampaign = async (id: string,
    contentHubAdvertisingProviderActionWithoutConfirmation: ContentHubAdvertisingProviderActionWithoutConfirmation, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAdvertisingCampaign> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubAdvertisingCampaign>(getCancelContentHubAdvertisingCampaignUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubAdvertisingProviderActionWithoutConfirmation)
  }
);}





export const getCancelContentHubAdvertisingCampaignMutationKey = () => ['cancelContentHubAdvertisingCampaign'] as const;

export const getCancelContentHubAdvertisingCampaignMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelContentHubAdvertisingCampaign>>, TError,CancelContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelContentHubAdvertisingCampaign>>, TError,CancelContentHubAdvertisingCampaignMutationVariables, TContext> => {

const mutationKey = getCancelContentHubAdvertisingCampaignMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelContentHubAdvertisingCampaign>>, CancelContentHubAdvertisingCampaignMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  cancelContentHubAdvertisingCampaign(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelContentHubAdvertisingCampaignMutationResult = NonNullable<Awaited<ReturnType<typeof cancelContentHubAdvertisingCampaign>>>
    export type CancelContentHubAdvertisingCampaignMutationBody = ContentHubAdvertisingProviderActionWithoutConfirmation
    export type CancelContentHubAdvertisingCampaignMutationError = unknown
    export type CancelContentHubAdvertisingCampaignMutationVariables = {id: string;data: ContentHubAdvertisingProviderActionWithoutConfirmation}

    /**
 * @summary Cancel a local draft or provider campaign
 */
export const useCancelContentHubAdvertisingCampaign = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelContentHubAdvertisingCampaign>>, TError,CancelContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelContentHubAdvertisingCampaign>>,
        TError,
        CancelContentHubAdvertisingCampaignMutationVariables,
        TContext
      > => {
      return useMutation(getCancelContentHubAdvertisingCampaignMutationOptions(options));
    }

export const getSyncContentHubAdvertisingCampaignUrl = (id: string,) => {




  return `/api/content-hub/advertising/campaigns/${id}/sync`
}

/**
 * @summary Synchronize provider status and sourced metrics
 */
export const syncContentHubAdvertisingCampaign = async (id: string,
    contentHubAdvertisingProviderActionWithoutConfirmation: ContentHubAdvertisingProviderActionWithoutConfirmation, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAdvertisingCampaign> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubAdvertisingCampaign>(getSyncContentHubAdvertisingCampaignUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubAdvertisingProviderActionWithoutConfirmation)
  }
);}





export const getSyncContentHubAdvertisingCampaignMutationKey = () => ['syncContentHubAdvertisingCampaign'] as const;

export const getSyncContentHubAdvertisingCampaignMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof syncContentHubAdvertisingCampaign>>, TError,SyncContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof syncContentHubAdvertisingCampaign>>, TError,SyncContentHubAdvertisingCampaignMutationVariables, TContext> => {

const mutationKey = getSyncContentHubAdvertisingCampaignMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof syncContentHubAdvertisingCampaign>>, SyncContentHubAdvertisingCampaignMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  syncContentHubAdvertisingCampaign(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SyncContentHubAdvertisingCampaignMutationResult = NonNullable<Awaited<ReturnType<typeof syncContentHubAdvertisingCampaign>>>
    export type SyncContentHubAdvertisingCampaignMutationBody = ContentHubAdvertisingProviderActionWithoutConfirmation
    export type SyncContentHubAdvertisingCampaignMutationError = unknown
    export type SyncContentHubAdvertisingCampaignMutationVariables = {id: string;data: ContentHubAdvertisingProviderActionWithoutConfirmation}

    /**
 * @summary Synchronize provider status and sourced metrics
 */
export const useSyncContentHubAdvertisingCampaign = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof syncContentHubAdvertisingCampaign>>, TError,SyncContentHubAdvertisingCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof syncContentHubAdvertisingCampaign>>,
        TError,
        SyncContentHubAdvertisingCampaignMutationVariables,
        TContext
      > => {
      return useMutation(getSyncContentHubAdvertisingCampaignMutationOptions(options));
    }

export const getGetPublicContentHubAssetUrl = (slug: string,
    id: string,) => {




  return `/api/public/microsites/${slug}/resources/${id}`
}

/**
 * @summary Get public asset metadata through an active entity binding
 */
export const getPublicContentHubAsset = async (slug: string,
    id: string, options?: Parameters<typeof customFetch>[1]): Promise<PublicContentHubAsset> => {

  return customFetch<PublicContentHubAsset>(getGetPublicContentHubAssetUrl(slug,id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPublicContentHubAssetQueryKey = (slug: string,
    id: string,) => {
    return [
    `/api/public/microsites/${slug}/resources/${id}`
    ] as const;
    }


export const getGetPublicContentHubAssetQueryOptions = <TData = Awaited<ReturnType<typeof getPublicContentHubAsset>>, TError = Error>(slug: string,
    id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicContentHubAsset>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPublicContentHubAssetQueryKey(slug,id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPublicContentHubAsset>>> = ({ signal }) => getPublicContentHubAsset(slug,id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: slug !== null && slug !== undefined && id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPublicContentHubAsset>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPublicContentHubAssetQueryResult = NonNullable<Awaited<ReturnType<typeof getPublicContentHubAsset>>>
export type GetPublicContentHubAssetQueryError = Error


/**
 * @summary Get public asset metadata through an active entity binding
 */

export function useGetPublicContentHubAsset<TData = Awaited<ReturnType<typeof getPublicContentHubAsset>>, TError = Error>(
 slug: string,
    id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicContentHubAsset>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPublicContentHubAssetQueryOptions(slug,id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCapturePublicContentHubAssetUrl = (slug: string,
    id: string,) => {




  return `/api/public/microsites/${slug}/resources/${id}`
}

/**
 * @summary Capture consent and deliver an entity-scoped public asset
 */
export const capturePublicContentHubAsset = async (slug: string,
    id: string,
    publicContentHubAssetCapture: PublicContentHubAssetCapture, options?: Parameters<typeof customFetch>[1]): Promise<PublicContentHubAssetDelivery> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PublicContentHubAssetDelivery>(getCapturePublicContentHubAssetUrl(slug,id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(publicContentHubAssetCapture)
  }
);}





export const getCapturePublicContentHubAssetMutationKey = () => ['capturePublicContentHubAsset'] as const;

export const getCapturePublicContentHubAssetMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof capturePublicContentHubAsset>>, TError,CapturePublicContentHubAssetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof capturePublicContentHubAsset>>, TError,CapturePublicContentHubAssetMutationVariables, TContext> => {

const mutationKey = getCapturePublicContentHubAssetMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof capturePublicContentHubAsset>>, CapturePublicContentHubAssetMutationVariables> = (props) => {
          const {slug,id,data} = props ?? {};

          return  capturePublicContentHubAsset(slug,id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CapturePublicContentHubAssetMutationResult = NonNullable<Awaited<ReturnType<typeof capturePublicContentHubAsset>>>
    export type CapturePublicContentHubAssetMutationBody = PublicContentHubAssetCapture
    export type CapturePublicContentHubAssetMutationError = unknown
    export type CapturePublicContentHubAssetMutationVariables = {slug: string;id: string;data: PublicContentHubAssetCapture}

    /**
 * @summary Capture consent and deliver an entity-scoped public asset
 */
export const useCapturePublicContentHubAsset = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof capturePublicContentHubAsset>>, TError,CapturePublicContentHubAssetMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof capturePublicContentHubAsset>>,
        TError,
        CapturePublicContentHubAssetMutationVariables,
        TContext
      > => {
      return useMutation(getCapturePublicContentHubAssetMutationOptions(options));
    }

export const getListContentHubReviewRequestsUrl = (params?: ListContentHubReviewRequestsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/content-hub/review-requests?${stringifiedParams}` : `/api/content-hub/review-requests`
}

/**
 * @summary List review requests and delivery outcomes
 */
export const listContentHubReviewRequests = async (params?: ListContentHubReviewRequestsParams, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubReviewRequestList> => {

  return customFetch<ContentHubReviewRequestList>(getListContentHubReviewRequestsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListContentHubReviewRequestsQueryKey = (params?: ListContentHubReviewRequestsParams,) => {
    return [
    `/api/content-hub/review-requests`, ...(params ? [params] : [])
    ] as const;
    }


export const getListContentHubReviewRequestsQueryOptions = <TData = Awaited<ReturnType<typeof listContentHubReviewRequests>>, TError = unknown>(params?: ListContentHubReviewRequestsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubReviewRequests>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListContentHubReviewRequestsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listContentHubReviewRequests>>> = ({ signal }) => listContentHubReviewRequests(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listContentHubReviewRequests>>, TError, TData> & { queryKey: QueryKey }
}

export type ListContentHubReviewRequestsQueryResult = NonNullable<Awaited<ReturnType<typeof listContentHubReviewRequests>>>
export type ListContentHubReviewRequestsQueryError = unknown


/**
 * @summary List review requests and delivery outcomes
 */

export function useListContentHubReviewRequests<TData = Awaited<ReturnType<typeof listContentHubReviewRequests>>, TError = unknown>(
 params?: ListContentHubReviewRequestsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubReviewRequests>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListContentHubReviewRequestsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateContentHubReviewRequestUrl = () => {




  return `/api/content-hub/review-requests`
}

/**
 * @summary Create a review request draft
 */
export const createContentHubReviewRequest = async (contentHubReviewRequestCreate: ContentHubReviewRequestCreate, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubReviewRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubReviewRequest>(getCreateContentHubReviewRequestUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubReviewRequestCreate)
  }
);}





export const getCreateContentHubReviewRequestMutationKey = () => ['createContentHubReviewRequest'] as const;

export const getCreateContentHubReviewRequestMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createContentHubReviewRequest>>, TError,CreateContentHubReviewRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createContentHubReviewRequest>>, TError,CreateContentHubReviewRequestMutationVariables, TContext> => {

const mutationKey = getCreateContentHubReviewRequestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createContentHubReviewRequest>>, CreateContentHubReviewRequestMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createContentHubReviewRequest(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateContentHubReviewRequestMutationResult = NonNullable<Awaited<ReturnType<typeof createContentHubReviewRequest>>>
    export type CreateContentHubReviewRequestMutationBody = ContentHubReviewRequestCreate
    export type CreateContentHubReviewRequestMutationError = unknown
    export type CreateContentHubReviewRequestMutationVariables = {data: ContentHubReviewRequestCreate}

    /**
 * @summary Create a review request draft
 */
export const useCreateContentHubReviewRequest = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createContentHubReviewRequest>>, TError,CreateContentHubReviewRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createContentHubReviewRequest>>,
        TError,
        CreateContentHubReviewRequestMutationVariables,
        TContext
      > => {
      return useMutation(getCreateContentHubReviewRequestMutationOptions(options));
    }

export const getSendContentHubReviewRequestUrl = (id: string,) => {




  return `/api/content-hub/review-requests/${id}/send`
}

/**
 * @summary Send a review request through the connected mailbox
 */
export const sendContentHubReviewRequest = async (id: string,
    sendContentHubReviewRequestBody?: SendContentHubReviewRequestBody, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubReviewRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubReviewRequest>(getSendContentHubReviewRequestUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sendContentHubReviewRequestBody)
  }
);}





export const getSendContentHubReviewRequestMutationKey = () => ['sendContentHubReviewRequest'] as const;

export const getSendContentHubReviewRequestMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendContentHubReviewRequest>>, TError,SendContentHubReviewRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof sendContentHubReviewRequest>>, TError,SendContentHubReviewRequestMutationVariables, TContext> => {

const mutationKey = getSendContentHubReviewRequestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof sendContentHubReviewRequest>>, SendContentHubReviewRequestMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  sendContentHubReviewRequest(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SendContentHubReviewRequestMutationResult = NonNullable<Awaited<ReturnType<typeof sendContentHubReviewRequest>>>
    export type SendContentHubReviewRequestMutationBody = SendContentHubReviewRequestBody | undefined
    export type SendContentHubReviewRequestMutationError = Error
    export type SendContentHubReviewRequestMutationVariables = {id: string;data?: SendContentHubReviewRequestBody}

    /**
 * @summary Send a review request through the connected mailbox
 */
export const useSendContentHubReviewRequest = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendContentHubReviewRequest>>, TError,SendContentHubReviewRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof sendContentHubReviewRequest>>,
        TError,
        SendContentHubReviewRequestMutationVariables,
        TContext
      > => {
      return useMutation(getSendContentHubReviewRequestMutationOptions(options));
    }

export const getListContentHubReviewRequestHistoryUrl = (id: string,) => {




  return `/api/content-hub/review-requests/${id}/history`
}

/**
 * @summary List durable provider delivery attempts for a review request
 */
export const listContentHubReviewRequestHistory = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<EmailDeliveryJobList> => {

  return customFetch<EmailDeliveryJobList>(getListContentHubReviewRequestHistoryUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListContentHubReviewRequestHistoryQueryKey = (id: string,) => {
    return [
    `/api/content-hub/review-requests/${id}/history`
    ] as const;
    }


export const getListContentHubReviewRequestHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listContentHubReviewRequestHistory>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubReviewRequestHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListContentHubReviewRequestHistoryQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listContentHubReviewRequestHistory>>> = ({ signal }) => listContentHubReviewRequestHistory(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listContentHubReviewRequestHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListContentHubReviewRequestHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listContentHubReviewRequestHistory>>>
export type ListContentHubReviewRequestHistoryQueryError = Error


/**
 * @summary List durable provider delivery attempts for a review request
 */

export function useListContentHubReviewRequestHistory<TData = Awaited<ReturnType<typeof listContentHubReviewRequestHistory>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubReviewRequestHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListContentHubReviewRequestHistoryQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetContentHubAutomationSettingsUrl = () => {




  return `/api/content-hub/automation`
}

/**
 * @summary Get Content Hub automation settings
 */
export const getContentHubAutomationSettings = async ( options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAutomationSettings> => {

  return customFetch<ContentHubAutomationSettings>(getGetContentHubAutomationSettingsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetContentHubAutomationSettingsQueryKey = () => {
    return [
    `/api/content-hub/automation`
    ] as const;
    }


export const getGetContentHubAutomationSettingsQueryOptions = <TData = Awaited<ReturnType<typeof getContentHubAutomationSettings>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContentHubAutomationSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetContentHubAutomationSettingsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getContentHubAutomationSettings>>> = ({ signal }) => getContentHubAutomationSettings({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getContentHubAutomationSettings>>, TError, TData> & { queryKey: QueryKey }
}

export type GetContentHubAutomationSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof getContentHubAutomationSettings>>>
export type GetContentHubAutomationSettingsQueryError = unknown


/**
 * @summary Get Content Hub automation settings
 */

export function useGetContentHubAutomationSettings<TData = Awaited<ReturnType<typeof getContentHubAutomationSettings>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContentHubAutomationSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetContentHubAutomationSettingsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateContentHubAutomationSettingsUrl = () => {




  return `/api/content-hub/automation`
}

/**
 * @summary Update Content Hub automation settings
 */
export const updateContentHubAutomationSettings = async (contentHubAutomationSettingsUpdate: ContentHubAutomationSettingsUpdate, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAutomationSettings> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ContentHubAutomationSettings>(getUpdateContentHubAutomationSettingsUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(contentHubAutomationSettingsUpdate)
  }
);}





export const getUpdateContentHubAutomationSettingsMutationKey = () => ['updateContentHubAutomationSettings'] as const;

export const getUpdateContentHubAutomationSettingsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateContentHubAutomationSettings>>, TError,UpdateContentHubAutomationSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateContentHubAutomationSettings>>, TError,UpdateContentHubAutomationSettingsMutationVariables, TContext> => {

const mutationKey = getUpdateContentHubAutomationSettingsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateContentHubAutomationSettings>>, UpdateContentHubAutomationSettingsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateContentHubAutomationSettings(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateContentHubAutomationSettingsMutationResult = NonNullable<Awaited<ReturnType<typeof updateContentHubAutomationSettings>>>
    export type UpdateContentHubAutomationSettingsMutationBody = ContentHubAutomationSettingsUpdate
    export type UpdateContentHubAutomationSettingsMutationError = unknown
    export type UpdateContentHubAutomationSettingsMutationVariables = {data: ContentHubAutomationSettingsUpdate}

    /**
 * @summary Update Content Hub automation settings
 */
export const useUpdateContentHubAutomationSettings = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateContentHubAutomationSettings>>, TError,UpdateContentHubAutomationSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateContentHubAutomationSettings>>,
        TError,
        UpdateContentHubAutomationSettingsMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateContentHubAutomationSettingsMutationOptions(options));
    }

export const getListContentHubAutomationRunsUrl = (params?: ListContentHubAutomationRunsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/content-hub/automation/runs?${stringifiedParams}` : `/api/content-hub/automation/runs`
}

/**
 * @summary List paged durable Content Hub operation outcomes
 */
export const listContentHubAutomationRuns = async (params?: ListContentHubAutomationRunsParams, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubAutomationRunList> => {

  return customFetch<ContentHubAutomationRunList>(getListContentHubAutomationRunsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListContentHubAutomationRunsQueryKey = (params?: ListContentHubAutomationRunsParams,) => {
    return [
    `/api/content-hub/automation/runs`, ...(params ? [params] : [])
    ] as const;
    }


export const getListContentHubAutomationRunsQueryOptions = <TData = Awaited<ReturnType<typeof listContentHubAutomationRuns>>, TError = unknown>(params?: ListContentHubAutomationRunsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubAutomationRuns>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListContentHubAutomationRunsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listContentHubAutomationRuns>>> = ({ signal }) => listContentHubAutomationRuns(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listContentHubAutomationRuns>>, TError, TData> & { queryKey: QueryKey }
}

export type ListContentHubAutomationRunsQueryResult = NonNullable<Awaited<ReturnType<typeof listContentHubAutomationRuns>>>
export type ListContentHubAutomationRunsQueryError = unknown


/**
 * @summary List paged durable Content Hub operation outcomes
 */

export function useListContentHubAutomationRuns<TData = Awaited<ReturnType<typeof listContentHubAutomationRuns>>, TError = unknown>(
 params?: ListContentHubAutomationRunsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listContentHubAutomationRuns>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListContentHubAutomationRunsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetContentHubReportUrl = (params: GetContentHubReportParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    const explodeParameters = ["networks"];

    if (Array.isArray(value) && explodeParameters.includes(key)) {
      value.forEach((v) => {
        normalizedParams.append(key, v === null ? 'null' : String(v));
      });
      return;
    }

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/content-hub/reports?${stringifiedParams}` : `/api/content-hub/reports`
}

/**
 * @summary Get reconciled Content Hub performance and outcome reporting
 */
export const getContentHubReport = async (params: GetContentHubReportParams, options?: Parameters<typeof customFetch>[1]): Promise<ContentHubReport> => {

  return customFetch<ContentHubReport>(getGetContentHubReportUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetContentHubReportQueryKey = (params?: GetContentHubReportParams,) => {
    return [
    `/api/content-hub/reports`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetContentHubReportQueryOptions = <TData = Awaited<ReturnType<typeof getContentHubReport>>, TError = unknown>(params: GetContentHubReportParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContentHubReport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetContentHubReportQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getContentHubReport>>> = ({ signal }) => getContentHubReport(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getContentHubReport>>, TError, TData> & { queryKey: QueryKey }
}

export type GetContentHubReportQueryResult = NonNullable<Awaited<ReturnType<typeof getContentHubReport>>>
export type GetContentHubReportQueryError = unknown


/**
 * @summary Get reconciled Content Hub performance and outcome reporting
 */

export function useGetContentHubReport<TData = Awaited<ReturnType<typeof getContentHubReport>>, TError = unknown>(
 params: GetContentHubReportParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContentHubReport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetContentHubReportQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getExportContentHubReportUrl = (params: ExportContentHubReportParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    const explodeParameters = ["networks"];

    if (Array.isArray(value) && explodeParameters.includes(key)) {
      value.forEach((v) => {
        normalizedParams.append(key, v === null ? 'null' : String(v));
      });
      return;
    }

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/content-hub/reports/export?${stringifiedParams}` : `/api/content-hub/reports/export`
}

/**
 * @summary Export the same reconciled Content Hub report as CSV
 */
export const exportContentHubReport = async (params: ExportContentHubReportParams, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getExportContentHubReportUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getExportContentHubReportQueryKey = (params?: ExportContentHubReportParams,) => {
    return [
    `/api/content-hub/reports/export`, ...(params ? [params] : [])
    ] as const;
    }


export const getExportContentHubReportQueryOptions = <TData = Awaited<ReturnType<typeof exportContentHubReport>>, TError = unknown>(params: ExportContentHubReportParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof exportContentHubReport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getExportContentHubReportQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof exportContentHubReport>>> = ({ signal }) => exportContentHubReport(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof exportContentHubReport>>, TError, TData> & { queryKey: QueryKey }
}

export type ExportContentHubReportQueryResult = NonNullable<Awaited<ReturnType<typeof exportContentHubReport>>>
export type ExportContentHubReportQueryError = unknown


/**
 * @summary Export the same reconciled Content Hub report as CSV
 */

export function useExportContentHubReport<TData = Awaited<ReturnType<typeof exportContentHubReport>>, TError = unknown>(
 params: ExportContentHubReportParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof exportContentHubReport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getExportContentHubReportQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetContentHubGoLiveChecklistUrl = () => {




  return `/api/content-hub/go-live`
}

/**
 * @summary Evaluate Content Hub readiness from live configuration and data
 */
export const getContentHubGoLiveChecklist = async ( options?: Parameters<typeof customFetch>[1]): Promise<ContentHubGoLiveChecklist> => {

  return customFetch<ContentHubGoLiveChecklist>(getGetContentHubGoLiveChecklistUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetContentHubGoLiveChecklistQueryKey = () => {
    return [
    `/api/content-hub/go-live`
    ] as const;
    }


export const getGetContentHubGoLiveChecklistQueryOptions = <TData = Awaited<ReturnType<typeof getContentHubGoLiveChecklist>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContentHubGoLiveChecklist>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetContentHubGoLiveChecklistQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getContentHubGoLiveChecklist>>> = ({ signal }) => getContentHubGoLiveChecklist({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getContentHubGoLiveChecklist>>, TError, TData> & { queryKey: QueryKey }
}

export type GetContentHubGoLiveChecklistQueryResult = NonNullable<Awaited<ReturnType<typeof getContentHubGoLiveChecklist>>>
export type GetContentHubGoLiveChecklistQueryError = unknown


/**
 * @summary Evaluate Content Hub readiness from live configuration and data
 */

export function useGetContentHubGoLiveChecklist<TData = Awaited<ReturnType<typeof getContentHubGoLiveChecklist>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContentHubGoLiveChecklist>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetContentHubGoLiveChecklistQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListSocialNetworksUrl = () => {




  return `/api/social/networks`
}

/**
 * @summary List social network connection and publishing capabilities
 */
export const listSocialNetworks = async ( options?: Parameters<typeof customFetch>[1]): Promise<SocialNetworkList> => {

  return customFetch<SocialNetworkList>(getListSocialNetworksUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSocialNetworksQueryKey = () => {
    return [
    `/api/social/networks`
    ] as const;
    }


export const getListSocialNetworksQueryOptions = <TData = Awaited<ReturnType<typeof listSocialNetworks>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSocialNetworks>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSocialNetworksQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSocialNetworks>>> = ({ signal }) => listSocialNetworks({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSocialNetworks>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSocialNetworksQueryResult = NonNullable<Awaited<ReturnType<typeof listSocialNetworks>>>
export type ListSocialNetworksQueryError = unknown


/**
 * @summary List social network connection and publishing capabilities
 */

export function useListSocialNetworks<TData = Awaited<ReturnType<typeof listSocialNetworks>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSocialNetworks>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSocialNetworksQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListSocialProviderCapabilitiesUrl = () => {




  return `/api/social/provider-capabilities`
}

/**
 * Returns a fixed seven-provider matrix. Provider support, current implementation state, connection readiness and setup prerequisites are reported independently so unavailable features are never represented as empty success.
 * @summary List verified provider capabilities and implementation readiness
 */
export const listSocialProviderCapabilities = async ( options?: Parameters<typeof customFetch>[1]): Promise<SocialProviderCapabilityMatrix> => {

  return customFetch<SocialProviderCapabilityMatrix>(getListSocialProviderCapabilitiesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSocialProviderCapabilitiesQueryKey = () => {
    return [
    `/api/social/provider-capabilities`
    ] as const;
    }


export const getListSocialProviderCapabilitiesQueryOptions = <TData = Awaited<ReturnType<typeof listSocialProviderCapabilities>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSocialProviderCapabilities>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSocialProviderCapabilitiesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSocialProviderCapabilities>>> = ({ signal }) => listSocialProviderCapabilities({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSocialProviderCapabilities>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSocialProviderCapabilitiesQueryResult = NonNullable<Awaited<ReturnType<typeof listSocialProviderCapabilities>>>
export type ListSocialProviderCapabilitiesQueryError = unknown


/**
 * @summary List verified provider capabilities and implementation readiness
 */

export function useListSocialProviderCapabilities<TData = Awaited<ReturnType<typeof listSocialProviderCapabilities>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSocialProviderCapabilities>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSocialProviderCapabilitiesQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListSocialConnectionAuditUrl = (params?: ListSocialConnectionAuditParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/social/connections/audit?${stringifiedParams}` : `/api/social/connections/audit`
}

/**
 * @summary List the selected entity's social connection lifecycle audit history
 */
export const listSocialConnectionAudit = async (params?: ListSocialConnectionAuditParams, options?: Parameters<typeof customFetch>[1]): Promise<SocialConnectionAuditPage> => {

  return customFetch<SocialConnectionAuditPage>(getListSocialConnectionAuditUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSocialConnectionAuditQueryKey = (params?: ListSocialConnectionAuditParams,) => {
    return [
    `/api/social/connections/audit`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSocialConnectionAuditQueryOptions = <TData = Awaited<ReturnType<typeof listSocialConnectionAudit>>, TError = unknown>(params?: ListSocialConnectionAuditParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSocialConnectionAudit>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSocialConnectionAuditQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSocialConnectionAudit>>> = ({ signal }) => listSocialConnectionAudit(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSocialConnectionAudit>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSocialConnectionAuditQueryResult = NonNullable<Awaited<ReturnType<typeof listSocialConnectionAudit>>>
export type ListSocialConnectionAuditQueryError = unknown


/**
 * @summary List the selected entity's social connection lifecycle audit history
 */

export function useListSocialConnectionAudit<TData = Awaited<ReturnType<typeof listSocialConnectionAudit>>, TError = unknown>(
 params?: ListSocialConnectionAuditParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSocialConnectionAudit>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSocialConnectionAuditQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetSocialOAuthUrlUrl = (network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok',) => {




  return `/api/social/networks/${network}/oauth`
}

/**
 * @summary Start a provider OAuth connection
 */
export const getSocialOAuthUrl = async (network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok', options?: Parameters<typeof customFetch>[1]): Promise<SocialOAuthStart> => {

  return customFetch<SocialOAuthStart>(getGetSocialOAuthUrlUrl(network),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSocialOAuthUrlQueryKey = (network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok',) => {
    return [
    `/api/social/networks/${network}/oauth`
    ] as const;
    }


export const getGetSocialOAuthUrlQueryOptions = <TData = Awaited<ReturnType<typeof getSocialOAuthUrl>>, TError = unknown>(network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok', options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSocialOAuthUrl>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSocialOAuthUrlQueryKey(network);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSocialOAuthUrl>>> = ({ signal }) => getSocialOAuthUrl(network, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: network !== null && network !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSocialOAuthUrl>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSocialOAuthUrlQueryResult = NonNullable<Awaited<ReturnType<typeof getSocialOAuthUrl>>>
export type GetSocialOAuthUrlQueryError = unknown


/**
 * @summary Start a provider OAuth connection
 */

export function useGetSocialOAuthUrl<TData = Awaited<ReturnType<typeof getSocialOAuthUrl>>, TError = unknown>(
 network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok', options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSocialOAuthUrl>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSocialOAuthUrlQueryOptions(network,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCompleteSocialOAuthUrl = (network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok',
    params: CompleteSocialOAuthParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/social/networks/${network}/oauth/callback?${stringifiedParams}` : `/api/social/networks/${network}/oauth/callback`
}

/**
 * @summary Exchange provider authorization and stage eligible account selection
 */
export const completeSocialOAuth = async (network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok',
    params: CompleteSocialOAuthParams, options?: Parameters<typeof customFetch>[1]): Promise<unknown> => {

  return customFetch<unknown>(getCompleteSocialOAuthUrl(network,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getCompleteSocialOAuthQueryKey = (network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok',
    params?: CompleteSocialOAuthParams,) => {
    return [
    `/api/social/networks/${network}/oauth/callback`, ...(params ? [params] : [])
    ] as const;
    }


export const getCompleteSocialOAuthQueryOptions = <TData = Awaited<ReturnType<typeof completeSocialOAuth>>, TError = void | Error>(network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok',
    params: CompleteSocialOAuthParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof completeSocialOAuth>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getCompleteSocialOAuthQueryKey(network,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof completeSocialOAuth>>> = ({ signal }) => completeSocialOAuth(network,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: network !== null && network !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof completeSocialOAuth>>, TError, TData> & { queryKey: QueryKey }
}

export type CompleteSocialOAuthQueryResult = NonNullable<Awaited<ReturnType<typeof completeSocialOAuth>>>
export type CompleteSocialOAuthQueryError = void | Error


/**
 * @summary Exchange provider authorization and stage eligible account selection
 */

export function useCompleteSocialOAuth<TData = Awaited<ReturnType<typeof completeSocialOAuth>>, TError = void | Error>(
 network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok',
    params: CompleteSocialOAuthParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof completeSocialOAuth>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getCompleteSocialOAuthQueryOptions(network,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetSocialOAuthAccountSelectionUrl = (network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok',
    selectionId: string,
    params?: GetSocialOAuthAccountSelectionParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/social/networks/${network}/oauth/selections/${selectionId}?${stringifiedParams}` : `/api/social/networks/${network}/oauth/selections/${selectionId}`
}

/**
 * @summary List eligible provider targets for this authorization attempt
 */
export const getSocialOAuthAccountSelection = async (network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok',
    selectionId: string,
    params?: GetSocialOAuthAccountSelectionParams, options?: Parameters<typeof customFetch>[1]): Promise<SocialOAuthAccountSelection> => {

  return customFetch<SocialOAuthAccountSelection>(getGetSocialOAuthAccountSelectionUrl(network,selectionId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSocialOAuthAccountSelectionQueryKey = (network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok',
    selectionId: string,
    params?: GetSocialOAuthAccountSelectionParams,) => {
    return [
    `/api/social/networks/${network}/oauth/selections/${selectionId}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetSocialOAuthAccountSelectionQueryOptions = <TData = Awaited<ReturnType<typeof getSocialOAuthAccountSelection>>, TError = Error>(network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok',
    selectionId: string,
    params?: GetSocialOAuthAccountSelectionParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSocialOAuthAccountSelection>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSocialOAuthAccountSelectionQueryKey(network,selectionId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSocialOAuthAccountSelection>>> = ({ signal }) => getSocialOAuthAccountSelection(network,selectionId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: network !== null && network !== undefined && selectionId !== null && selectionId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSocialOAuthAccountSelection>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSocialOAuthAccountSelectionQueryResult = NonNullable<Awaited<ReturnType<typeof getSocialOAuthAccountSelection>>>
export type GetSocialOAuthAccountSelectionQueryError = Error


/**
 * @summary List eligible provider targets for this authorization attempt
 */

export function useGetSocialOAuthAccountSelection<TData = Awaited<ReturnType<typeof getSocialOAuthAccountSelection>>, TError = Error>(
 network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok',
    selectionId: string,
    params?: GetSocialOAuthAccountSelectionParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSocialOAuthAccountSelection>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSocialOAuthAccountSelectionQueryOptions(network,selectionId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSelectSocialOAuthAccountUrl = (network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok',
    selectionId: string,) => {




  return `/api/social/networks/${network}/oauth/selections/${selectionId}`
}

/**
 * @summary Connect one explicitly selected eligible provider target
 */
export const selectSocialOAuthAccount = async (network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok',
    selectionId: string,
    socialOAuthAccountSelectRequest: SocialOAuthAccountSelectRequest, options?: Parameters<typeof customFetch>[1]): Promise<SocialConnectedAccount> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialConnectedAccount>(getSelectSocialOAuthAccountUrl(network,selectionId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialOAuthAccountSelectRequest)
  }
);}





export const getSelectSocialOAuthAccountMutationKey = () => ['selectSocialOAuthAccount'] as const;

export const getSelectSocialOAuthAccountMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof selectSocialOAuthAccount>>, TError,SelectSocialOAuthAccountMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof selectSocialOAuthAccount>>, TError,SelectSocialOAuthAccountMutationVariables, TContext> => {

const mutationKey = getSelectSocialOAuthAccountMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof selectSocialOAuthAccount>>, SelectSocialOAuthAccountMutationVariables> = (props) => {
          const {network,selectionId,data} = props ?? {};

          return  selectSocialOAuthAccount(network,selectionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SelectSocialOAuthAccountMutationResult = NonNullable<Awaited<ReturnType<typeof selectSocialOAuthAccount>>>
    export type SelectSocialOAuthAccountMutationBody = SocialOAuthAccountSelectRequest
    export type SelectSocialOAuthAccountMutationError = Error
    export type SelectSocialOAuthAccountMutationVariables = {network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok';selectionId: string;data: SocialOAuthAccountSelectRequest}

    /**
 * @summary Connect one explicitly selected eligible provider target
 */
export const useSelectSocialOAuthAccount = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof selectSocialOAuthAccount>>, TError,SelectSocialOAuthAccountMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof selectSocialOAuthAccount>>,
        TError,
        SelectSocialOAuthAccountMutationVariables,
        TContext
      > => {
      return useMutation(getSelectSocialOAuthAccountMutationOptions(options));
    }

export const getRefreshSocialNetworkConnectionUrl = (network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok',) => {




  return `/api/social/networks/${network}/refresh`
}

/**
 * @summary Refresh provider credentials under a persisted versioned lease
 */
export const refreshSocialNetworkConnection = async (network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok', options?: Parameters<typeof customFetch>[1]): Promise<SocialConnectionRefreshResult> => {

  return customFetch<SocialConnectionRefreshResult>(getRefreshSocialNetworkConnectionUrl(network),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRefreshSocialNetworkConnectionMutationKey = () => ['refreshSocialNetworkConnection'] as const;

export const getRefreshSocialNetworkConnectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof refreshSocialNetworkConnection>>, TError,RefreshSocialNetworkConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof refreshSocialNetworkConnection>>, TError,RefreshSocialNetworkConnectionMutationVariables, TContext> => {

const mutationKey = getRefreshSocialNetworkConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof refreshSocialNetworkConnection>>, RefreshSocialNetworkConnectionMutationVariables> = (props) => {
          const {network} = props ?? {};

          return  refreshSocialNetworkConnection(network,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RefreshSocialNetworkConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof refreshSocialNetworkConnection>>>

    export type RefreshSocialNetworkConnectionMutationError = Error
    export type RefreshSocialNetworkConnectionMutationVariables = {network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok'}

    /**
 * @summary Refresh provider credentials under a persisted versioned lease
 */
export const useRefreshSocialNetworkConnection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof refreshSocialNetworkConnection>>, TError,RefreshSocialNetworkConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof refreshSocialNetworkConnection>>,
        TError,
        RefreshSocialNetworkConnectionMutationVariables,
        TContext
      > => {
      return useMutation(getRefreshSocialNetworkConnectionMutationOptions(options));
    }

export const getDisconnectSocialNetworkUrl = (network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok',) => {




  return `/api/social/networks/${network}`
}

/**
 * @summary Disconnect an entity social account and block queued external execution
 */
export const disconnectSocialNetwork = async (network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok',
    socialDisconnectRequest: SocialDisconnectRequest, options?: Parameters<typeof customFetch>[1]): Promise<SocialDisconnectResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialDisconnectResult>(getDisconnectSocialNetworkUrl(network),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialDisconnectRequest)
  }
);}





export const getDisconnectSocialNetworkMutationKey = () => ['disconnectSocialNetwork'] as const;

export const getDisconnectSocialNetworkMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectSocialNetwork>>, TError,DisconnectSocialNetworkMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof disconnectSocialNetwork>>, TError,DisconnectSocialNetworkMutationVariables, TContext> => {

const mutationKey = getDisconnectSocialNetworkMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof disconnectSocialNetwork>>, DisconnectSocialNetworkMutationVariables> = (props) => {
          const {network,data} = props ?? {};

          return  disconnectSocialNetwork(network,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DisconnectSocialNetworkMutationResult = NonNullable<Awaited<ReturnType<typeof disconnectSocialNetwork>>>
    export type DisconnectSocialNetworkMutationBody = SocialDisconnectRequest
    export type DisconnectSocialNetworkMutationError = unknown
    export type DisconnectSocialNetworkMutationVariables = {network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok';data: SocialDisconnectRequest}

    /**
 * @summary Disconnect an entity social account and block queued external execution
 */
export const useDisconnectSocialNetwork = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectSocialNetwork>>, TError,DisconnectSocialNetworkMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof disconnectSocialNetwork>>,
        TError,
        DisconnectSocialNetworkMutationVariables,
        TContext
      > => {
      return useMutation(getDisconnectSocialNetworkMutationOptions(options));
    }

export const getListCampaignOutputsUrl = (id: string,
    params?: ListCampaignOutputsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/campaigns/${id}/outputs?${stringifiedParams}` : `/api/campaigns/${id}/outputs`
}

export const listCampaignOutputs = async (id: string,
    params?: ListCampaignOutputsParams, options?: Parameters<typeof customFetch>[1]): Promise<ListCampaignOutputs200> => {

  return customFetch<ListCampaignOutputs200>(getListCampaignOutputsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCampaignOutputsQueryKey = (id: string,
    params?: ListCampaignOutputsParams,) => {
    return [
    `/api/campaigns/${id}/outputs`, ...(params ? [params] : [])
    ] as const;
    }


export const getListCampaignOutputsQueryOptions = <TData = Awaited<ReturnType<typeof listCampaignOutputs>>, TError = unknown>(id: string,
    params?: ListCampaignOutputsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCampaignOutputs>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCampaignOutputsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCampaignOutputs>>> = ({ signal }) => listCampaignOutputs(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCampaignOutputs>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCampaignOutputsQueryResult = NonNullable<Awaited<ReturnType<typeof listCampaignOutputs>>>
export type ListCampaignOutputsQueryError = unknown



export function useListCampaignOutputs<TData = Awaited<ReturnType<typeof listCampaignOutputs>>, TError = unknown>(
 id: string,
    params?: ListCampaignOutputsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCampaignOutputs>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCampaignOutputsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPrepareManualAdHandoffUrl = (id: string,
    params: PrepareManualAdHandoffParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/campaigns/${id}/ad-pack-handoffs?${stringifiedParams}` : `/api/campaigns/${id}/ad-pack-handoffs`
}

/**
 * Reads an exact authorized pack as text. Access audit only; no campaign, document or provider state changes.
 */
export const prepareManualAdHandoff = async (id: string,
    params: PrepareManualAdHandoffParams, options?: Parameters<typeof customFetch>[1]): Promise<PrepareManualAdHandoff200> => {

  return customFetch<PrepareManualAdHandoff200>(getPrepareManualAdHandoffUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPrepareManualAdHandoffQueryKey = (id: string,
    params?: PrepareManualAdHandoffParams,) => {
    return [
    `/api/campaigns/${id}/ad-pack-handoffs`, ...(params ? [params] : [])
    ] as const;
    }


export const getPrepareManualAdHandoffQueryOptions = <TData = Awaited<ReturnType<typeof prepareManualAdHandoff>>, TError = unknown>(id: string,
    params: PrepareManualAdHandoffParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof prepareManualAdHandoff>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPrepareManualAdHandoffQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof prepareManualAdHandoff>>> = ({ signal }) => prepareManualAdHandoff(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof prepareManualAdHandoff>>, TError, TData> & { queryKey: QueryKey }
}

export type PrepareManualAdHandoffQueryResult = NonNullable<Awaited<ReturnType<typeof prepareManualAdHandoff>>>
export type PrepareManualAdHandoffQueryError = unknown



export function usePrepareManualAdHandoff<TData = Awaited<ReturnType<typeof prepareManualAdHandoff>>, TError = unknown>(
 id: string,
    params: PrepareManualAdHandoffParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof prepareManualAdHandoff>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getPrepareManualAdHandoffQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewCampaignOutputUrl = () => {




  return `/api/campaign-outputs/reviews`
}

export const reviewCampaignOutput = async (reviewCampaignOutputBody: ReviewCampaignOutputBody, options?: Parameters<typeof customFetch>[1]): Promise<AgentActionProposal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentActionProposal>(getReviewCampaignOutputUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewCampaignOutputBody)
  }
);}





export const getReviewCampaignOutputMutationKey = () => ['reviewCampaignOutput'] as const;

export const getReviewCampaignOutputMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewCampaignOutput>>, TError,ReviewCampaignOutputMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewCampaignOutput>>, TError,ReviewCampaignOutputMutationVariables, TContext> => {

const mutationKey = getReviewCampaignOutputMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewCampaignOutput>>, ReviewCampaignOutputMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reviewCampaignOutput(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewCampaignOutputMutationResult = NonNullable<Awaited<ReturnType<typeof reviewCampaignOutput>>>
    export type ReviewCampaignOutputMutationBody = ReviewCampaignOutputBody
    export type ReviewCampaignOutputMutationError = unknown
    export type ReviewCampaignOutputMutationVariables = {data: ReviewCampaignOutputBody}

    export const useReviewCampaignOutput = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewCampaignOutput>>, TError,ReviewCampaignOutputMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewCampaignOutput>>,
        TError,
        ReviewCampaignOutputMutationVariables,
        TContext
      > => {
      return useMutation(getReviewCampaignOutputMutationOptions(options));
    }

export const getReviewSocialProductionUrl = () => {




  return `/api/social-production/reviews`
}

/**
 * @summary Prepare a private exact social editorial review
 */
export const reviewSocialProduction = async (reviewSocialProductionBody: ReviewSocialProductionBody, options?: Parameters<typeof customFetch>[1]): Promise<AgentActionProposal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentActionProposal>(getReviewSocialProductionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewSocialProductionBody)
  }
);}





export const getReviewSocialProductionMutationKey = () => ['reviewSocialProduction'] as const;

export const getReviewSocialProductionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewSocialProduction>>, TError,ReviewSocialProductionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewSocialProduction>>, TError,ReviewSocialProductionMutationVariables, TContext> => {

const mutationKey = getReviewSocialProductionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewSocialProduction>>, ReviewSocialProductionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reviewSocialProduction(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewSocialProductionMutationResult = NonNullable<Awaited<ReturnType<typeof reviewSocialProduction>>>
    export type ReviewSocialProductionMutationBody = ReviewSocialProductionBody
    export type ReviewSocialProductionMutationError = unknown
    export type ReviewSocialProductionMutationVariables = {data: ReviewSocialProductionBody}

    /**
 * @summary Prepare a private exact social editorial review
 */
export const useReviewSocialProduction = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewSocialProduction>>, TError,ReviewSocialProductionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewSocialProduction>>,
        TError,
        ReviewSocialProductionMutationVariables,
        TContext
      > => {
      return useMutation(getReviewSocialProductionMutationOptions(options));
    }

export const getListSocialPostsUrl = (params?: ListSocialPostsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/social/posts?${stringifiedParams}` : `/api/social/posts`
}

/**
 * @summary List social drafts, schedule and publish history
 */
export const listSocialPosts = async (params?: ListSocialPostsParams, options?: Parameters<typeof customFetch>[1]): Promise<SocialPostList> => {

  return customFetch<SocialPostList>(getListSocialPostsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSocialPostsQueryKey = (params?: ListSocialPostsParams,) => {
    return [
    `/api/social/posts`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSocialPostsQueryOptions = <TData = Awaited<ReturnType<typeof listSocialPosts>>, TError = unknown>(params?: ListSocialPostsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSocialPosts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSocialPostsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSocialPosts>>> = ({ signal }) => listSocialPosts(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSocialPosts>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSocialPostsQueryResult = NonNullable<Awaited<ReturnType<typeof listSocialPosts>>>
export type ListSocialPostsQueryError = unknown


/**
 * @summary List social drafts, schedule and publish history
 */

export function useListSocialPosts<TData = Awaited<ReturnType<typeof listSocialPosts>>, TError = unknown>(
 params?: ListSocialPostsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSocialPosts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSocialPostsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateSocialPostUrl = () => {




  return `/api/social/posts`
}

/**
 * @summary Create a manual social post draft
 */
export const createSocialPost = async (socialPostCreate: SocialPostCreate, options?: Parameters<typeof customFetch>[1]): Promise<SocialPost> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialPost>(getCreateSocialPostUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialPostCreate)
  }
);}





export const getCreateSocialPostMutationKey = () => ['createSocialPost'] as const;

export const getCreateSocialPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSocialPost>>, TError,CreateSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createSocialPost>>, TError,CreateSocialPostMutationVariables, TContext> => {

const mutationKey = getCreateSocialPostMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createSocialPost>>, CreateSocialPostMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createSocialPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateSocialPostMutationResult = NonNullable<Awaited<ReturnType<typeof createSocialPost>>>
    export type CreateSocialPostMutationBody = SocialPostCreate
    export type CreateSocialPostMutationError = unknown
    export type CreateSocialPostMutationVariables = {data: SocialPostCreate}

    /**
 * @summary Create a manual social post draft
 */
export const useCreateSocialPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSocialPost>>, TError,CreateSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createSocialPost>>,
        TError,
        CreateSocialPostMutationVariables,
        TContext
      > => {
      return useMutation(getCreateSocialPostMutationOptions(options));
    }

export const getGetSocialPostUrl = (id: string,) => {




  return `/api/social/posts/${id}`
}

/**
 * @summary Get one social post with its selected media
 */
export const getSocialPost = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SocialPost> => {

  return customFetch<SocialPost>(getGetSocialPostUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSocialPostQueryKey = (id: string,) => {
    return [
    `/api/social/posts/${id}`
    ] as const;
    }


export const getGetSocialPostQueryOptions = <TData = Awaited<ReturnType<typeof getSocialPost>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSocialPost>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSocialPostQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSocialPost>>> = ({ signal }) => getSocialPost(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSocialPost>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSocialPostQueryResult = NonNullable<Awaited<ReturnType<typeof getSocialPost>>>
export type GetSocialPostQueryError = unknown


/**
 * @summary Get one social post with its selected media
 */

export function useGetSocialPost<TData = Awaited<ReturnType<typeof getSocialPost>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSocialPost>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSocialPostQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateSocialPostUrl = (id: string,) => {




  return `/api/social/posts/${id}`
}

/**
 * @summary Update editable post content using optimistic version control
 */
export const updateSocialPost = async (id: string,
    socialPostUpdate: SocialPostUpdate, options?: Parameters<typeof customFetch>[1]): Promise<SocialPost> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialPost>(getUpdateSocialPostUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialPostUpdate)
  }
);}





export const getUpdateSocialPostMutationKey = () => ['updateSocialPost'] as const;

export const getUpdateSocialPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSocialPost>>, TError,UpdateSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateSocialPost>>, TError,UpdateSocialPostMutationVariables, TContext> => {

const mutationKey = getUpdateSocialPostMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateSocialPost>>, UpdateSocialPostMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateSocialPost(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateSocialPostMutationResult = NonNullable<Awaited<ReturnType<typeof updateSocialPost>>>
    export type UpdateSocialPostMutationBody = SocialPostUpdate
    export type UpdateSocialPostMutationError = unknown
    export type UpdateSocialPostMutationVariables = {id: string;data: SocialPostUpdate}

    /**
 * @summary Update editable post content using optimistic version control
 */
export const useUpdateSocialPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSocialPost>>, TError,UpdateSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateSocialPost>>,
        TError,
        UpdateSocialPostMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateSocialPostMutationOptions(options));
    }

export const getListSocialPostRevisionsUrl = (id: string,
    params?: ListSocialPostRevisionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/social/posts/${id}/revisions?${stringifiedParams}` : `/api/social/posts/${id}/revisions`
}

/**
 * @summary List immutable editorial revisions for a social post
 */
export const listSocialPostRevisions = async (id: string,
    params?: ListSocialPostRevisionsParams, options?: Parameters<typeof customFetch>[1]): Promise<SocialPostRevisionList> => {

  return customFetch<SocialPostRevisionList>(getListSocialPostRevisionsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSocialPostRevisionsQueryKey = (id: string,
    params?: ListSocialPostRevisionsParams,) => {
    return [
    `/api/social/posts/${id}/revisions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSocialPostRevisionsQueryOptions = <TData = Awaited<ReturnType<typeof listSocialPostRevisions>>, TError = unknown>(id: string,
    params?: ListSocialPostRevisionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSocialPostRevisions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSocialPostRevisionsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSocialPostRevisions>>> = ({ signal }) => listSocialPostRevisions(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSocialPostRevisions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSocialPostRevisionsQueryResult = NonNullable<Awaited<ReturnType<typeof listSocialPostRevisions>>>
export type ListSocialPostRevisionsQueryError = unknown


/**
 * @summary List immutable editorial revisions for a social post
 */

export function useListSocialPostRevisions<TData = Awaited<ReturnType<typeof listSocialPostRevisions>>, TError = unknown>(
 id: string,
    params?: ListSocialPostRevisionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSocialPostRevisions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSocialPostRevisionsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDuplicateSocialPostUrl = (id: string,) => {




  return `/api/social/posts/${id}/duplicate`
}

/**
 * @summary Duplicate a post and its media as a new draft
 */
export const duplicateSocialPost = async (id: string,
    socialPostVersionRequest: SocialPostVersionRequest, options?: Parameters<typeof customFetch>[1]): Promise<SocialPost> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialPost>(getDuplicateSocialPostUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialPostVersionRequest)
  }
);}





export const getDuplicateSocialPostMutationKey = () => ['duplicateSocialPost'] as const;

export const getDuplicateSocialPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof duplicateSocialPost>>, TError,DuplicateSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof duplicateSocialPost>>, TError,DuplicateSocialPostMutationVariables, TContext> => {

const mutationKey = getDuplicateSocialPostMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof duplicateSocialPost>>, DuplicateSocialPostMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  duplicateSocialPost(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DuplicateSocialPostMutationResult = NonNullable<Awaited<ReturnType<typeof duplicateSocialPost>>>
    export type DuplicateSocialPostMutationBody = SocialPostVersionRequest
    export type DuplicateSocialPostMutationError = unknown
    export type DuplicateSocialPostMutationVariables = {id: string;data: SocialPostVersionRequest}

    /**
 * @summary Duplicate a post and its media as a new draft
 */
export const useDuplicateSocialPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof duplicateSocialPost>>, TError,DuplicateSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof duplicateSocialPost>>,
        TError,
        DuplicateSocialPostMutationVariables,
        TContext
      > => {
      return useMutation(getDuplicateSocialPostMutationOptions(options));
    }

export const getSubmitSocialPostUrl = (id: string,) => {




  return `/api/social/posts/${id}/submit`
}

/**
 * @summary Submit an editable draft for approval
 */
export const submitSocialPost = async (id: string,
    socialPostVersionRequest: SocialPostVersionRequest, options?: Parameters<typeof customFetch>[1]): Promise<SocialPost> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialPost>(getSubmitSocialPostUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialPostVersionRequest)
  }
);}





export const getSubmitSocialPostMutationKey = () => ['submitSocialPost'] as const;

export const getSubmitSocialPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitSocialPost>>, TError,SubmitSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof submitSocialPost>>, TError,SubmitSocialPostMutationVariables, TContext> => {

const mutationKey = getSubmitSocialPostMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof submitSocialPost>>, SubmitSocialPostMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  submitSocialPost(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SubmitSocialPostMutationResult = NonNullable<Awaited<ReturnType<typeof submitSocialPost>>>
    export type SubmitSocialPostMutationBody = SocialPostVersionRequest
    export type SubmitSocialPostMutationError = unknown
    export type SubmitSocialPostMutationVariables = {id: string;data: SocialPostVersionRequest}

    /**
 * @summary Submit an editable draft for approval
 */
export const useSubmitSocialPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitSocialPost>>, TError,SubmitSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof submitSocialPost>>,
        TError,
        SubmitSocialPostMutationVariables,
        TContext
      > => {
      return useMutation(getSubmitSocialPostMutationOptions(options));
    }

export const getGenerateSocialStrategyUrl = () => {




  return `/api/social/strategy`
}

/**
 * @summary Generate a target-market and signal-grounded social strategy
 */
export const generateSocialStrategy = async (socialStrategyRequest: SocialStrategyRequest, options?: Parameters<typeof customFetch>[1]): Promise<SocialStrategyResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialStrategyResponse>(getGenerateSocialStrategyUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialStrategyRequest)
  }
);}





export const getGenerateSocialStrategyMutationKey = () => ['generateSocialStrategy'] as const;

export const getGenerateSocialStrategyMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateSocialStrategy>>, TError,GenerateSocialStrategyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof generateSocialStrategy>>, TError,GenerateSocialStrategyMutationVariables, TContext> => {

const mutationKey = getGenerateSocialStrategyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof generateSocialStrategy>>, GenerateSocialStrategyMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  generateSocialStrategy(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenerateSocialStrategyMutationResult = NonNullable<Awaited<ReturnType<typeof generateSocialStrategy>>>
    export type GenerateSocialStrategyMutationBody = SocialStrategyRequest
    export type GenerateSocialStrategyMutationError = unknown
    export type GenerateSocialStrategyMutationVariables = {data: SocialStrategyRequest}

    /**
 * @summary Generate a target-market and signal-grounded social strategy
 */
export const useGenerateSocialStrategy = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateSocialStrategy>>, TError,GenerateSocialStrategyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof generateSocialStrategy>>,
        TError,
        GenerateSocialStrategyMutationVariables,
        TContext
      > => {
      return useMutation(getGenerateSocialStrategyMutationOptions(options));
    }

export const getGenerateSocialDraftUrl = () => {




  return `/api/social/draft`
}

/**
 * @summary Generate per-network post variants for human approval
 */
export const generateSocialDraft = async (socialDraftRequest: SocialDraftRequest, options?: Parameters<typeof customFetch>[1]): Promise<SocialDraftGenerationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialDraftGenerationResult>(getGenerateSocialDraftUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialDraftRequest)
  }
);}





export const getGenerateSocialDraftMutationKey = () => ['generateSocialDraft'] as const;

export const getGenerateSocialDraftMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateSocialDraft>>, TError,GenerateSocialDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof generateSocialDraft>>, TError,GenerateSocialDraftMutationVariables, TContext> => {

const mutationKey = getGenerateSocialDraftMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof generateSocialDraft>>, GenerateSocialDraftMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  generateSocialDraft(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenerateSocialDraftMutationResult = NonNullable<Awaited<ReturnType<typeof generateSocialDraft>>>
    export type GenerateSocialDraftMutationBody = SocialDraftRequest
    export type GenerateSocialDraftMutationError = unknown
    export type GenerateSocialDraftMutationVariables = {data: SocialDraftRequest}

    /**
 * @summary Generate per-network post variants for human approval
 */
export const useGenerateSocialDraft = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateSocialDraft>>, TError,GenerateSocialDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof generateSocialDraft>>,
        TError,
        GenerateSocialDraftMutationVariables,
        TContext
      > => {
      return useMutation(getGenerateSocialDraftMutationOptions(options));
    }

export const getApproveSocialPostUrl = (id: string,) => {




  return `/api/social/posts/${id}/approve`
}

/**
 * @summary Human-approve a post and its variants
 */
export const approveSocialPost = async (id: string,
    approveSocialPostBody: ApproveSocialPostBody, options?: Parameters<typeof customFetch>[1]): Promise<SocialPost> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialPost>(getApproveSocialPostUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(approveSocialPostBody)
  }
);}





export const getApproveSocialPostMutationKey = () => ['approveSocialPost'] as const;

export const getApproveSocialPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveSocialPost>>, TError,ApproveSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof approveSocialPost>>, TError,ApproveSocialPostMutationVariables, TContext> => {

const mutationKey = getApproveSocialPostMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof approveSocialPost>>, ApproveSocialPostMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  approveSocialPost(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApproveSocialPostMutationResult = NonNullable<Awaited<ReturnType<typeof approveSocialPost>>>
    export type ApproveSocialPostMutationBody = ApproveSocialPostBody
    export type ApproveSocialPostMutationError = unknown
    export type ApproveSocialPostMutationVariables = {id: string;data: ApproveSocialPostBody}

    /**
 * @summary Human-approve a post and its variants
 */
export const useApproveSocialPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveSocialPost>>, TError,ApproveSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof approveSocialPost>>,
        TError,
        ApproveSocialPostMutationVariables,
        TContext
      > => {
      return useMutation(getApproveSocialPostMutationOptions(options));
    }

export const getScheduleSocialPostUrl = (id: string,) => {




  return `/api/social/posts/${id}/schedule`
}

/**
 * @summary Schedule an approved post for publishing
 */
export const scheduleSocialPost = async (id: string,
    socialPostScheduleRequest: SocialPostScheduleRequest, options?: Parameters<typeof customFetch>[1]): Promise<SocialPost> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialPost>(getScheduleSocialPostUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialPostScheduleRequest)
  }
);}





export const getScheduleSocialPostMutationKey = () => ['scheduleSocialPost'] as const;

export const getScheduleSocialPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof scheduleSocialPost>>, TError,ScheduleSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof scheduleSocialPost>>, TError,ScheduleSocialPostMutationVariables, TContext> => {

const mutationKey = getScheduleSocialPostMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof scheduleSocialPost>>, ScheduleSocialPostMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  scheduleSocialPost(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ScheduleSocialPostMutationResult = NonNullable<Awaited<ReturnType<typeof scheduleSocialPost>>>
    export type ScheduleSocialPostMutationBody = SocialPostScheduleRequest
    export type ScheduleSocialPostMutationError = unknown
    export type ScheduleSocialPostMutationVariables = {id: string;data: SocialPostScheduleRequest}

    /**
 * @summary Schedule an approved post for publishing
 */
export const useScheduleSocialPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof scheduleSocialPost>>, TError,ScheduleSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof scheduleSocialPost>>,
        TError,
        ScheduleSocialPostMutationVariables,
        TContext
      > => {
      return useMutation(getScheduleSocialPostMutationOptions(options));
    }

export const getRejectSocialPostUrl = (id: string,) => {




  return `/api/social/posts/${id}/reject`
}

/**
 * @summary Reject a post awaiting approval with an authenticated audit actor
 */
export const rejectSocialPost = async (id: string,
    socialPostRejectRequest: SocialPostRejectRequest, options?: Parameters<typeof customFetch>[1]): Promise<SocialPost> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialPost>(getRejectSocialPostUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialPostRejectRequest)
  }
);}





export const getRejectSocialPostMutationKey = () => ['rejectSocialPost'] as const;

export const getRejectSocialPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rejectSocialPost>>, TError,RejectSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof rejectSocialPost>>, TError,RejectSocialPostMutationVariables, TContext> => {

const mutationKey = getRejectSocialPostMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof rejectSocialPost>>, RejectSocialPostMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  rejectSocialPost(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RejectSocialPostMutationResult = NonNullable<Awaited<ReturnType<typeof rejectSocialPost>>>
    export type RejectSocialPostMutationBody = SocialPostRejectRequest
    export type RejectSocialPostMutationError = unknown
    export type RejectSocialPostMutationVariables = {id: string;data: SocialPostRejectRequest}

    /**
 * @summary Reject a post awaiting approval with an authenticated audit actor
 */
export const useRejectSocialPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rejectSocialPost>>, TError,RejectSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof rejectSocialPost>>,
        TError,
        RejectSocialPostMutationVariables,
        TContext
      > => {
      return useMutation(getRejectSocialPostMutationOptions(options));
    }

export const getArchiveSocialPostUrl = (id: string,) => {




  return `/api/social/posts/${id}/archive`
}

/**
 * @summary Archive a post while retaining revisions and publication history
 */
export const archiveSocialPost = async (id: string,
    socialPostArchiveRequest: SocialPostArchiveRequest, options?: Parameters<typeof customFetch>[1]): Promise<SocialPost> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialPost>(getArchiveSocialPostUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialPostArchiveRequest)
  }
);}





export const getArchiveSocialPostMutationKey = () => ['archiveSocialPost'] as const;

export const getArchiveSocialPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof archiveSocialPost>>, TError,ArchiveSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof archiveSocialPost>>, TError,ArchiveSocialPostMutationVariables, TContext> => {

const mutationKey = getArchiveSocialPostMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof archiveSocialPost>>, ArchiveSocialPostMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  archiveSocialPost(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ArchiveSocialPostMutationResult = NonNullable<Awaited<ReturnType<typeof archiveSocialPost>>>
    export type ArchiveSocialPostMutationBody = SocialPostArchiveRequest
    export type ArchiveSocialPostMutationError = unknown
    export type ArchiveSocialPostMutationVariables = {id: string;data: SocialPostArchiveRequest}

    /**
 * @summary Archive a post while retaining revisions and publication history
 */
export const useArchiveSocialPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof archiveSocialPost>>, TError,ArchiveSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof archiveSocialPost>>,
        TError,
        ArchiveSocialPostMutationVariables,
        TContext
      > => {
      return useMutation(getArchiveSocialPostMutationOptions(options));
    }

export const getRetrySocialPostUrl = (id: string,) => {




  return `/api/social/posts/${id}/retry`
}

/**
 * @summary Requeue selected failed publication targets without replaying successful targets
 */
export const retrySocialPost = async (id: string,
    socialPostRetryRequest: SocialPostRetryRequest, options?: Parameters<typeof customFetch>[1]): Promise<SocialPost> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialPost>(getRetrySocialPostUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialPostRetryRequest)
  }
);}





export const getRetrySocialPostMutationKey = () => ['retrySocialPost'] as const;

export const getRetrySocialPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retrySocialPost>>, TError,RetrySocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof retrySocialPost>>, TError,RetrySocialPostMutationVariables, TContext> => {

const mutationKey = getRetrySocialPostMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof retrySocialPost>>, RetrySocialPostMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  retrySocialPost(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RetrySocialPostMutationResult = NonNullable<Awaited<ReturnType<typeof retrySocialPost>>>
    export type RetrySocialPostMutationBody = SocialPostRetryRequest
    export type RetrySocialPostMutationError = unknown
    export type RetrySocialPostMutationVariables = {id: string;data: SocialPostRetryRequest}

    /**
 * @summary Requeue selected failed publication targets without replaying successful targets
 */
export const useRetrySocialPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retrySocialPost>>, TError,RetrySocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof retrySocialPost>>,
        TError,
        RetrySocialPostMutationVariables,
        TContext
      > => {
      return useMutation(getRetrySocialPostMutationOptions(options));
    }

export const getConfigureTikTokSocialPostUrl = (id: string,) => {




  return `/api/social/posts/${id}/tiktok-configuration`
}

/**
 * @summary Save the required creator choices and verified media URLs for a TikTok post
 */
export const configureTikTokSocialPost = async (id: string,
    tikTokPostConfigurationInput: TikTokPostConfigurationInput, options?: Parameters<typeof customFetch>[1]): Promise<SocialPost> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialPost>(getConfigureTikTokSocialPostUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tikTokPostConfigurationInput)
  }
);}





export const getConfigureTikTokSocialPostMutationKey = () => ['configureTikTokSocialPost'] as const;

export const getConfigureTikTokSocialPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof configureTikTokSocialPost>>, TError,ConfigureTikTokSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof configureTikTokSocialPost>>, TError,ConfigureTikTokSocialPostMutationVariables, TContext> => {

const mutationKey = getConfigureTikTokSocialPostMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof configureTikTokSocialPost>>, ConfigureTikTokSocialPostMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  configureTikTokSocialPost(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfigureTikTokSocialPostMutationResult = NonNullable<Awaited<ReturnType<typeof configureTikTokSocialPost>>>
    export type ConfigureTikTokSocialPostMutationBody = TikTokPostConfigurationInput
    export type ConfigureTikTokSocialPostMutationError = unknown
    export type ConfigureTikTokSocialPostMutationVariables = {id: string;data: TikTokPostConfigurationInput}

    /**
 * @summary Save the required creator choices and verified media URLs for a TikTok post
 */
export const useConfigureTikTokSocialPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof configureTikTokSocialPost>>, TError,ConfigureTikTokSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof configureTikTokSocialPost>>,
        TError,
        ConfigureTikTokSocialPostMutationVariables,
        TContext
      > => {
      return useMutation(getConfigureTikTokSocialPostMutationOptions(options));
    }

export const getReconcileSocialPostUrl = (id: string,) => {




  return `/api/social/posts/${id}/reconcile`
}

/**
 * @summary Reconcile asynchronous provider publication outcomes for a publishing post
 */
export const reconcileSocialPost = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SocialPost> => {

  return customFetch<SocialPost>(getReconcileSocialPostUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getReconcileSocialPostMutationKey = () => ['reconcileSocialPost'] as const;

export const getReconcileSocialPostMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconcileSocialPost>>, TError,ReconcileSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reconcileSocialPost>>, TError,ReconcileSocialPostMutationVariables, TContext> => {

const mutationKey = getReconcileSocialPostMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reconcileSocialPost>>, ReconcileSocialPostMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  reconcileSocialPost(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReconcileSocialPostMutationResult = NonNullable<Awaited<ReturnType<typeof reconcileSocialPost>>>

    export type ReconcileSocialPostMutationError = unknown
    export type ReconcileSocialPostMutationVariables = {id: string}

    /**
 * @summary Reconcile asynchronous provider publication outcomes for a publishing post
 */
export const useReconcileSocialPost = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reconcileSocialPost>>, TError,ReconcileSocialPostMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reconcileSocialPost>>,
        TError,
        ReconcileSocialPostMutationVariables,
        TContext
      > => {
      return useMutation(getReconcileSocialPostMutationOptions(options));
    }

export const getRunSocialPublishingQueueUrl = () => {




  return `/api/social/queue/run`
}

/**
 * @summary Publish all due approved posts
 */
export const runSocialPublishingQueue = async ( options?: Parameters<typeof customFetch>[1]): Promise<RunSocialPublishingQueue200> => {

  return customFetch<RunSocialPublishingQueue200>(getRunSocialPublishingQueueUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRunSocialPublishingQueueMutationKey = () => ['runSocialPublishingQueue'] as const;

export const getRunSocialPublishingQueueMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runSocialPublishingQueue>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof runSocialPublishingQueue>>, TError,void, TContext> => {

const mutationKey = getRunSocialPublishingQueueMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof runSocialPublishingQueue>>, void> = () => {


          return  runSocialPublishingQueue(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RunSocialPublishingQueueMutationResult = NonNullable<Awaited<ReturnType<typeof runSocialPublishingQueue>>>

    export type RunSocialPublishingQueueMutationError = unknown


    /**
 * @summary Publish all due approved posts
 */
export const useRunSocialPublishingQueue = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runSocialPublishingQueue>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof runSocialPublishingQueue>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getRunSocialPublishingQueueMutationOptions(options));
    }

export const getTestSocialNetworkConnectionUrl = (network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok',) => {




  return `/api/social/networks/${network}/test`
}

/**
 * @summary Verify stored credentials for a connected network without publishing
 */
export const testSocialNetworkConnection = async (network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok', options?: Parameters<typeof customFetch>[1]): Promise<SocialConnectionTestResult> => {

  return customFetch<SocialConnectionTestResult>(getTestSocialNetworkConnectionUrl(network),
  {
    ...options,
    method: 'POST'


  }
);}





export const getTestSocialNetworkConnectionMutationKey = () => ['testSocialNetworkConnection'] as const;

export const getTestSocialNetworkConnectionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof testSocialNetworkConnection>>, TError,TestSocialNetworkConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof testSocialNetworkConnection>>, TError,TestSocialNetworkConnectionMutationVariables, TContext> => {

const mutationKey = getTestSocialNetworkConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof testSocialNetworkConnection>>, TestSocialNetworkConnectionMutationVariables> = (props) => {
          const {network} = props ?? {};

          return  testSocialNetworkConnection(network,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TestSocialNetworkConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof testSocialNetworkConnection>>>

    export type TestSocialNetworkConnectionMutationError = unknown
    export type TestSocialNetworkConnectionMutationVariables = {network: 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube' | 'google_business' | 'tiktok'}

    /**
 * @summary Verify stored credentials for a connected network without publishing
 */
export const useTestSocialNetworkConnection = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof testSocialNetworkConnection>>, TError,TestSocialNetworkConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof testSocialNetworkConnection>>,
        TError,
        TestSocialNetworkConnectionMutationVariables,
        TContext
      > => {
      return useMutation(getTestSocialNetworkConnectionMutationOptions(options));
    }

export const getGetTikTokCreatorInfoUrl = () => {




  return `/api/social/networks/tiktok/creator-info`
}

/**
 * @summary Read current TikTok creator eligibility and required publication controls
 */
export const getTikTokCreatorInfo = async ( options?: Parameters<typeof customFetch>[1]): Promise<TikTokCreatorInfo> => {

  return customFetch<TikTokCreatorInfo>(getGetTikTokCreatorInfoUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTikTokCreatorInfoQueryKey = () => {
    return [
    `/api/social/networks/tiktok/creator-info`
    ] as const;
    }


export const getGetTikTokCreatorInfoQueryOptions = <TData = Awaited<ReturnType<typeof getTikTokCreatorInfo>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTikTokCreatorInfo>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTikTokCreatorInfoQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTikTokCreatorInfo>>> = ({ signal }) => getTikTokCreatorInfo({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTikTokCreatorInfo>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTikTokCreatorInfoQueryResult = NonNullable<Awaited<ReturnType<typeof getTikTokCreatorInfo>>>
export type GetTikTokCreatorInfoQueryError = unknown


/**
 * @summary Read current TikTok creator eligibility and required publication controls
 */

export function useGetTikTokCreatorInfo<TData = Awaited<ReturnType<typeof getTikTokCreatorInfo>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTikTokCreatorInfo>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetTikTokCreatorInfoQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRunSocialAutoGenerationUrl = () => {




  return `/api/social/auto-generate/run`
}

/**
 * @summary Generate one AI draft post from grounded strategy when open drafts are below the cap
 */
export const runSocialAutoGeneration = async ( options?: Parameters<typeof customFetch>[1]): Promise<SocialAutoGenerateResult> => {

  return customFetch<SocialAutoGenerateResult>(getRunSocialAutoGenerationUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRunSocialAutoGenerationMutationKey = () => ['runSocialAutoGeneration'] as const;

export const getRunSocialAutoGenerationMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runSocialAutoGeneration>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof runSocialAutoGeneration>>, TError,void, TContext> => {

const mutationKey = getRunSocialAutoGenerationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof runSocialAutoGeneration>>, void> = () => {


          return  runSocialAutoGeneration(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RunSocialAutoGenerationMutationResult = NonNullable<Awaited<ReturnType<typeof runSocialAutoGeneration>>>

    export type RunSocialAutoGenerationMutationError = Error


    /**
 * @summary Generate one AI draft post from grounded strategy when open drafts are below the cap
 */
export const useRunSocialAutoGeneration = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runSocialAutoGeneration>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof runSocialAutoGeneration>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getRunSocialAutoGenerationMutationOptions(options));
    }

export const getListSocialInboxUrl = (params?: ListSocialInboxParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/social/inbox?${stringifiedParams}` : `/api/social/inbox`
}

/**
 * @summary List the unified social inbox
 */
export const listSocialInbox = async (params?: ListSocialInboxParams, options?: Parameters<typeof customFetch>[1]): Promise<SocialInboxList> => {

  return customFetch<SocialInboxList>(getListSocialInboxUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSocialInboxQueryKey = (params?: ListSocialInboxParams,) => {
    return [
    `/api/social/inbox`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSocialInboxQueryOptions = <TData = Awaited<ReturnType<typeof listSocialInbox>>, TError = unknown>(params?: ListSocialInboxParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSocialInbox>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSocialInboxQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSocialInbox>>> = ({ signal }) => listSocialInbox(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSocialInbox>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSocialInboxQueryResult = NonNullable<Awaited<ReturnType<typeof listSocialInbox>>>
export type ListSocialInboxQueryError = unknown


/**
 * @summary List the unified social inbox
 */

export function useListSocialInbox<TData = Awaited<ReturnType<typeof listSocialInbox>>, TError = unknown>(
 params?: ListSocialInboxParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSocialInbox>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSocialInboxQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSyncSocialInboxUrl = () => {




  return `/api/social/inbox/sync`
}

/**
 * @summary Poll connected social networks and match authors to CRM contacts
 */
export const syncSocialInbox = async ( options?: Parameters<typeof customFetch>[1]): Promise<SocialInboxSyncResult> => {

  return customFetch<SocialInboxSyncResult>(getSyncSocialInboxUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getSyncSocialInboxMutationKey = () => ['syncSocialInbox'] as const;

export const getSyncSocialInboxMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof syncSocialInbox>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof syncSocialInbox>>, TError,void, TContext> => {

const mutationKey = getSyncSocialInboxMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof syncSocialInbox>>, void> = () => {


          return  syncSocialInbox(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SyncSocialInboxMutationResult = NonNullable<Awaited<ReturnType<typeof syncSocialInbox>>>

    export type SyncSocialInboxMutationError = unknown


    /**
 * @summary Poll connected social networks and match authors to CRM contacts
 */
export const useSyncSocialInbox = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof syncSocialInbox>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof syncSocialInbox>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getSyncSocialInboxMutationOptions(options));
    }

export const getSuggestSocialReplyUrl = (id: string,) => {




  return `/api/social/inbox/${id}/suggest-reply`
}

/**
 * @summary Generate an AI-suggested reply for human review
 */
export const suggestSocialReply = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SocialInboxItem> => {

  return customFetch<SocialInboxItem>(getSuggestSocialReplyUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getSuggestSocialReplyMutationKey = () => ['suggestSocialReply'] as const;

export const getSuggestSocialReplyMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof suggestSocialReply>>, TError,SuggestSocialReplyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof suggestSocialReply>>, TError,SuggestSocialReplyMutationVariables, TContext> => {

const mutationKey = getSuggestSocialReplyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof suggestSocialReply>>, SuggestSocialReplyMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  suggestSocialReply(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SuggestSocialReplyMutationResult = NonNullable<Awaited<ReturnType<typeof suggestSocialReply>>>

    export type SuggestSocialReplyMutationError = unknown
    export type SuggestSocialReplyMutationVariables = {id: string}

    /**
 * @summary Generate an AI-suggested reply for human review
 */
export const useSuggestSocialReply = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof suggestSocialReply>>, TError,SuggestSocialReplyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof suggestSocialReply>>,
        TError,
        SuggestSocialReplyMutationVariables,
        TContext
      > => {
      return useMutation(getSuggestSocialReplyMutationOptions(options));
    }

export const getReplySocialInboxItemUrl = (id: string,) => {




  return `/api/social/inbox/${id}/reply`
}

/**
 * @summary Send a reviewed reply through the provider adapter
 */
export const replySocialInboxItem = async (id: string,
    replySocialInboxItemBody: ReplySocialInboxItemBody, options?: Parameters<typeof customFetch>[1]): Promise<SocialInboxItem> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialInboxItem>(getReplySocialInboxItemUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(replySocialInboxItemBody)
  }
);}





export const getReplySocialInboxItemMutationKey = () => ['replySocialInboxItem'] as const;

export const getReplySocialInboxItemMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof replySocialInboxItem>>, TError,ReplySocialInboxItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof replySocialInboxItem>>, TError,ReplySocialInboxItemMutationVariables, TContext> => {

const mutationKey = getReplySocialInboxItemMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof replySocialInboxItem>>, ReplySocialInboxItemMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  replySocialInboxItem(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReplySocialInboxItemMutationResult = NonNullable<Awaited<ReturnType<typeof replySocialInboxItem>>>
    export type ReplySocialInboxItemMutationBody = ReplySocialInboxItemBody
    export type ReplySocialInboxItemMutationError = unknown
    export type ReplySocialInboxItemMutationVariables = {id: string;data: ReplySocialInboxItemBody}

    /**
 * @summary Send a reviewed reply through the provider adapter
 */
export const useReplySocialInboxItem = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof replySocialInboxItem>>, TError,ReplySocialInboxItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof replySocialInboxItem>>,
        TError,
        ReplySocialInboxItemMutationVariables,
        TContext
      > => {
      return useMutation(getReplySocialInboxItemMutationOptions(options));
    }

export const getConvertSocialInboxItemToLeadUrl = (id: string,) => {




  return `/api/social/inbox/${id}/convert-to-lead`
}

/**
 * @summary Convert an inbox item into a CRM enquiry lead
 */
export const convertSocialInboxItemToLead = async (id: string,
    socialLeadConvertRequest: SocialLeadConvertRequest, options?: Parameters<typeof customFetch>[1]): Promise<SocialLeadConvertResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialLeadConvertResponse>(getConvertSocialInboxItemToLeadUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialLeadConvertRequest)
  }
);}





export const getConvertSocialInboxItemToLeadMutationKey = () => ['convertSocialInboxItemToLead'] as const;

export const getConvertSocialInboxItemToLeadMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof convertSocialInboxItemToLead>>, TError,ConvertSocialInboxItemToLeadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof convertSocialInboxItemToLead>>, TError,ConvertSocialInboxItemToLeadMutationVariables, TContext> => {

const mutationKey = getConvertSocialInboxItemToLeadMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof convertSocialInboxItemToLead>>, ConvertSocialInboxItemToLeadMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  convertSocialInboxItemToLead(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConvertSocialInboxItemToLeadMutationResult = NonNullable<Awaited<ReturnType<typeof convertSocialInboxItemToLead>>>
    export type ConvertSocialInboxItemToLeadMutationBody = SocialLeadConvertRequest
    export type ConvertSocialInboxItemToLeadMutationError = Error
    export type ConvertSocialInboxItemToLeadMutationVariables = {id: string;data: SocialLeadConvertRequest}

    /**
 * @summary Convert an inbox item into a CRM enquiry lead
 */
export const useConvertSocialInboxItemToLead = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof convertSocialInboxItemToLead>>, TError,ConvertSocialInboxItemToLeadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof convertSocialInboxItemToLead>>,
        TError,
        ConvertSocialInboxItemToLeadMutationVariables,
        TContext
      > => {
      return useMutation(getConvertSocialInboxItemToLeadMutationOptions(options));
    }

export const getListSocialMessageRulesUrl = (params?: ListSocialMessageRulesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/social/automation/rules?${stringifiedParams}` : `/api/social/automation/rules`
}

/**
 * @summary List approved messaging automation rules for the selected entity
 */
export const listSocialMessageRules = async (params?: ListSocialMessageRulesParams, options?: Parameters<typeof customFetch>[1]): Promise<SocialMessageRuleList> => {

  return customFetch<SocialMessageRuleList>(getListSocialMessageRulesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSocialMessageRulesQueryKey = (params?: ListSocialMessageRulesParams,) => {
    return [
    `/api/social/automation/rules`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSocialMessageRulesQueryOptions = <TData = Awaited<ReturnType<typeof listSocialMessageRules>>, TError = unknown>(params?: ListSocialMessageRulesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSocialMessageRules>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSocialMessageRulesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSocialMessageRules>>> = ({ signal }) => listSocialMessageRules(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSocialMessageRules>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSocialMessageRulesQueryResult = NonNullable<Awaited<ReturnType<typeof listSocialMessageRules>>>
export type ListSocialMessageRulesQueryError = unknown


/**
 * @summary List approved messaging automation rules for the selected entity
 */

export function useListSocialMessageRules<TData = Awaited<ReturnType<typeof listSocialMessageRules>>, TError = unknown>(
 params?: ListSocialMessageRulesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSocialMessageRules>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSocialMessageRulesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateSocialMessageRuleUrl = () => {




  return `/api/social/automation/rules`
}

/**
 * @summary Create an inert draft rule
 */
export const createSocialMessageRule = async (socialMessageRuleInput: SocialMessageRuleInput, options?: Parameters<typeof customFetch>[1]): Promise<SocialMessageRule> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialMessageRule>(getCreateSocialMessageRuleUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialMessageRuleInput)
  }
);}





export const getCreateSocialMessageRuleMutationKey = () => ['createSocialMessageRule'] as const;

export const getCreateSocialMessageRuleMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSocialMessageRule>>, TError,CreateSocialMessageRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createSocialMessageRule>>, TError,CreateSocialMessageRuleMutationVariables, TContext> => {

const mutationKey = getCreateSocialMessageRuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createSocialMessageRule>>, CreateSocialMessageRuleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createSocialMessageRule(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateSocialMessageRuleMutationResult = NonNullable<Awaited<ReturnType<typeof createSocialMessageRule>>>
    export type CreateSocialMessageRuleMutationBody = SocialMessageRuleInput
    export type CreateSocialMessageRuleMutationError = unknown
    export type CreateSocialMessageRuleMutationVariables = {data: SocialMessageRuleInput}

    /**
 * @summary Create an inert draft rule
 */
export const useCreateSocialMessageRule = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSocialMessageRule>>, TError,CreateSocialMessageRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createSocialMessageRule>>,
        TError,
        CreateSocialMessageRuleMutationVariables,
        TContext
      > => {
      return useMutation(getCreateSocialMessageRuleMutationOptions(options));
    }

export const getGetSocialMessageRuleUrl = (id: string,) => {




  return `/api/social/automation/rules/${id}`
}

export const getSocialMessageRule = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SocialMessageRule> => {

  return customFetch<SocialMessageRule>(getGetSocialMessageRuleUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSocialMessageRuleQueryKey = (id: string,) => {
    return [
    `/api/social/automation/rules/${id}`
    ] as const;
    }


export const getGetSocialMessageRuleQueryOptions = <TData = Awaited<ReturnType<typeof getSocialMessageRule>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSocialMessageRule>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSocialMessageRuleQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSocialMessageRule>>> = ({ signal }) => getSocialMessageRule(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSocialMessageRule>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSocialMessageRuleQueryResult = NonNullable<Awaited<ReturnType<typeof getSocialMessageRule>>>
export type GetSocialMessageRuleQueryError = unknown



export function useGetSocialMessageRule<TData = Awaited<ReturnType<typeof getSocialMessageRule>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSocialMessageRule>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSocialMessageRuleQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateSocialMessageRuleUrl = (id: string,) => {




  return `/api/social/automation/rules/${id}`
}

/**
 * @summary Create a new immutable draft version and invalidate approval
 */
export const updateSocialMessageRule = async (id: string,
    socialMessageRuleUpdate: SocialMessageRuleUpdate, options?: Parameters<typeof customFetch>[1]): Promise<SocialMessageRule> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialMessageRule>(getUpdateSocialMessageRuleUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialMessageRuleUpdate)
  }
);}





export const getUpdateSocialMessageRuleMutationKey = () => ['updateSocialMessageRule'] as const;

export const getUpdateSocialMessageRuleMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSocialMessageRule>>, TError,UpdateSocialMessageRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateSocialMessageRule>>, TError,UpdateSocialMessageRuleMutationVariables, TContext> => {

const mutationKey = getUpdateSocialMessageRuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateSocialMessageRule>>, UpdateSocialMessageRuleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateSocialMessageRule(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateSocialMessageRuleMutationResult = NonNullable<Awaited<ReturnType<typeof updateSocialMessageRule>>>
    export type UpdateSocialMessageRuleMutationBody = SocialMessageRuleUpdate
    export type UpdateSocialMessageRuleMutationError = Error
    export type UpdateSocialMessageRuleMutationVariables = {id: string;data: SocialMessageRuleUpdate}

    /**
 * @summary Create a new immutable draft version and invalidate approval
 */
export const useUpdateSocialMessageRule = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSocialMessageRule>>, TError,UpdateSocialMessageRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateSocialMessageRule>>,
        TError,
        UpdateSocialMessageRuleMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateSocialMessageRuleMutationOptions(options));
    }

export const getPreviewSocialMessageRuleUrl = (id: string,) => {




  return `/api/social/automation/rules/${id}/preview`
}

export const previewSocialMessageRule = async (id: string,
    socialMessageRulePreviewRequest: SocialMessageRulePreviewRequest, options?: Parameters<typeof customFetch>[1]): Promise<SocialMessageRulePreview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialMessageRulePreview>(getPreviewSocialMessageRuleUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialMessageRulePreviewRequest)
  }
);}





export const getPreviewSocialMessageRuleMutationKey = () => ['previewSocialMessageRule'] as const;

export const getPreviewSocialMessageRuleMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewSocialMessageRule>>, TError,PreviewSocialMessageRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewSocialMessageRule>>, TError,PreviewSocialMessageRuleMutationVariables, TContext> => {

const mutationKey = getPreviewSocialMessageRuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewSocialMessageRule>>, PreviewSocialMessageRuleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  previewSocialMessageRule(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewSocialMessageRuleMutationResult = NonNullable<Awaited<ReturnType<typeof previewSocialMessageRule>>>
    export type PreviewSocialMessageRuleMutationBody = SocialMessageRulePreviewRequest
    export type PreviewSocialMessageRuleMutationError = unknown
    export type PreviewSocialMessageRuleMutationVariables = {id: string;data: SocialMessageRulePreviewRequest}

    export const usePreviewSocialMessageRule = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewSocialMessageRule>>, TError,PreviewSocialMessageRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewSocialMessageRule>>,
        TError,
        PreviewSocialMessageRuleMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewSocialMessageRuleMutationOptions(options));
    }

export const getSubmitSocialMessageRuleUrl = (id: string,) => {




  return `/api/social/automation/rules/${id}/submit`
}

export const submitSocialMessageRule = async (id: string,
    socialMessageRuleTransition: SocialMessageRuleTransition, options?: Parameters<typeof customFetch>[1]): Promise<SocialMessageRule> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialMessageRule>(getSubmitSocialMessageRuleUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialMessageRuleTransition)
  }
);}





export const getSubmitSocialMessageRuleMutationKey = () => ['submitSocialMessageRule'] as const;

export const getSubmitSocialMessageRuleMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitSocialMessageRule>>, TError,SubmitSocialMessageRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof submitSocialMessageRule>>, TError,SubmitSocialMessageRuleMutationVariables, TContext> => {

const mutationKey = getSubmitSocialMessageRuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof submitSocialMessageRule>>, SubmitSocialMessageRuleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  submitSocialMessageRule(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SubmitSocialMessageRuleMutationResult = NonNullable<Awaited<ReturnType<typeof submitSocialMessageRule>>>
    export type SubmitSocialMessageRuleMutationBody = SocialMessageRuleTransition
    export type SubmitSocialMessageRuleMutationError = unknown
    export type SubmitSocialMessageRuleMutationVariables = {id: string;data: SocialMessageRuleTransition}

    export const useSubmitSocialMessageRule = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitSocialMessageRule>>, TError,SubmitSocialMessageRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof submitSocialMessageRule>>,
        TError,
        SubmitSocialMessageRuleMutationVariables,
        TContext
      > => {
      return useMutation(getSubmitSocialMessageRuleMutationOptions(options));
    }

export const getApproveSocialMessageRuleUrl = (id: string,) => {




  return `/api/social/automation/rules/${id}/approve`
}

/**
 * @summary Approve the current immutable version as the authenticated administrator
 */
export const approveSocialMessageRule = async (id: string,
    socialMessageRuleTransition: SocialMessageRuleTransition, options?: Parameters<typeof customFetch>[1]): Promise<SocialMessageRule> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialMessageRule>(getApproveSocialMessageRuleUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialMessageRuleTransition)
  }
);}





export const getApproveSocialMessageRuleMutationKey = () => ['approveSocialMessageRule'] as const;

export const getApproveSocialMessageRuleMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveSocialMessageRule>>, TError,ApproveSocialMessageRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof approveSocialMessageRule>>, TError,ApproveSocialMessageRuleMutationVariables, TContext> => {

const mutationKey = getApproveSocialMessageRuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof approveSocialMessageRule>>, ApproveSocialMessageRuleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  approveSocialMessageRule(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApproveSocialMessageRuleMutationResult = NonNullable<Awaited<ReturnType<typeof approveSocialMessageRule>>>
    export type ApproveSocialMessageRuleMutationBody = SocialMessageRuleTransition
    export type ApproveSocialMessageRuleMutationError = unknown
    export type ApproveSocialMessageRuleMutationVariables = {id: string;data: SocialMessageRuleTransition}

    /**
 * @summary Approve the current immutable version as the authenticated administrator
 */
export const useApproveSocialMessageRule = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveSocialMessageRule>>, TError,ApproveSocialMessageRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof approveSocialMessageRule>>,
        TError,
        ApproveSocialMessageRuleMutationVariables,
        TContext
      > => {
      return useMutation(getApproveSocialMessageRuleMutationOptions(options));
    }

export const getActivateSocialMessageRuleUrl = (id: string,) => {




  return `/api/social/automation/rules/${id}/activate`
}

/**
 * @summary Activate only the currently approved version
 */
export const activateSocialMessageRule = async (id: string,
    socialMessageRuleTransition: SocialMessageRuleTransition, options?: Parameters<typeof customFetch>[1]): Promise<SocialMessageRule> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialMessageRule>(getActivateSocialMessageRuleUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialMessageRuleTransition)
  }
);}





export const getActivateSocialMessageRuleMutationKey = () => ['activateSocialMessageRule'] as const;

export const getActivateSocialMessageRuleMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof activateSocialMessageRule>>, TError,ActivateSocialMessageRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof activateSocialMessageRule>>, TError,ActivateSocialMessageRuleMutationVariables, TContext> => {

const mutationKey = getActivateSocialMessageRuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof activateSocialMessageRule>>, ActivateSocialMessageRuleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  activateSocialMessageRule(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ActivateSocialMessageRuleMutationResult = NonNullable<Awaited<ReturnType<typeof activateSocialMessageRule>>>
    export type ActivateSocialMessageRuleMutationBody = SocialMessageRuleTransition
    export type ActivateSocialMessageRuleMutationError = unknown
    export type ActivateSocialMessageRuleMutationVariables = {id: string;data: SocialMessageRuleTransition}

    /**
 * @summary Activate only the currently approved version
 */
export const useActivateSocialMessageRule = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof activateSocialMessageRule>>, TError,ActivateSocialMessageRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof activateSocialMessageRule>>,
        TError,
        ActivateSocialMessageRuleMutationVariables,
        TContext
      > => {
      return useMutation(getActivateSocialMessageRuleMutationOptions(options));
    }

export const getPauseSocialMessageRuleUrl = (id: string,) => {




  return `/api/social/automation/rules/${id}/pause`
}

export const pauseSocialMessageRule = async (id: string,
    socialMessageRuleTransition: SocialMessageRuleTransition, options?: Parameters<typeof customFetch>[1]): Promise<SocialMessageRule> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SocialMessageRule>(getPauseSocialMessageRuleUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(socialMessageRuleTransition)
  }
);}





export const getPauseSocialMessageRuleMutationKey = () => ['pauseSocialMessageRule'] as const;

export const getPauseSocialMessageRuleMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof pauseSocialMessageRule>>, TError,PauseSocialMessageRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof pauseSocialMessageRule>>, TError,PauseSocialMessageRuleMutationVariables, TContext> => {

const mutationKey = getPauseSocialMessageRuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof pauseSocialMessageRule>>, PauseSocialMessageRuleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  pauseSocialMessageRule(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PauseSocialMessageRuleMutationResult = NonNullable<Awaited<ReturnType<typeof pauseSocialMessageRule>>>
    export type PauseSocialMessageRuleMutationBody = SocialMessageRuleTransition
    export type PauseSocialMessageRuleMutationError = unknown
    export type PauseSocialMessageRuleMutationVariables = {id: string;data: SocialMessageRuleTransition}

    export const usePauseSocialMessageRule = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof pauseSocialMessageRule>>, TError,PauseSocialMessageRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof pauseSocialMessageRule>>,
        TError,
        PauseSocialMessageRuleMutationVariables,
        TContext
      > => {
      return useMutation(getPauseSocialMessageRuleMutationOptions(options));
    }

export const getListSocialMessageRuleHistoryUrl = (id: string,
    params?: ListSocialMessageRuleHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/social/automation/rules/${id}/history?${stringifiedParams}` : `/api/social/automation/rules/${id}/history`
}

export const listSocialMessageRuleHistory = async (id: string,
    params?: ListSocialMessageRuleHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<SocialMessageRuleHistory> => {

  return customFetch<SocialMessageRuleHistory>(getListSocialMessageRuleHistoryUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSocialMessageRuleHistoryQueryKey = (id: string,
    params?: ListSocialMessageRuleHistoryParams,) => {
    return [
    `/api/social/automation/rules/${id}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSocialMessageRuleHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listSocialMessageRuleHistory>>, TError = unknown>(id: string,
    params?: ListSocialMessageRuleHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSocialMessageRuleHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSocialMessageRuleHistoryQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSocialMessageRuleHistory>>> = ({ signal }) => listSocialMessageRuleHistory(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSocialMessageRuleHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSocialMessageRuleHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listSocialMessageRuleHistory>>>
export type ListSocialMessageRuleHistoryQueryError = unknown



export function useListSocialMessageRuleHistory<TData = Awaited<ReturnType<typeof listSocialMessageRuleHistory>>, TError = unknown>(
 id: string,
    params?: ListSocialMessageRuleHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSocialMessageRuleHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSocialMessageRuleHistoryQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListSocialMessageRuleExecutionsUrl = (params?: ListSocialMessageRuleExecutionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/social/automation/executions?${stringifiedParams}` : `/api/social/automation/executions`
}

export const listSocialMessageRuleExecutions = async (params?: ListSocialMessageRuleExecutionsParams, options?: Parameters<typeof customFetch>[1]): Promise<SocialMessageRuleExecutionList> => {

  return customFetch<SocialMessageRuleExecutionList>(getListSocialMessageRuleExecutionsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSocialMessageRuleExecutionsQueryKey = (params?: ListSocialMessageRuleExecutionsParams,) => {
    return [
    `/api/social/automation/executions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSocialMessageRuleExecutionsQueryOptions = <TData = Awaited<ReturnType<typeof listSocialMessageRuleExecutions>>, TError = unknown>(params?: ListSocialMessageRuleExecutionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSocialMessageRuleExecutions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSocialMessageRuleExecutionsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSocialMessageRuleExecutions>>> = ({ signal }) => listSocialMessageRuleExecutions(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSocialMessageRuleExecutions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSocialMessageRuleExecutionsQueryResult = NonNullable<Awaited<ReturnType<typeof listSocialMessageRuleExecutions>>>
export type ListSocialMessageRuleExecutionsQueryError = unknown



export function useListSocialMessageRuleExecutions<TData = Awaited<ReturnType<typeof listSocialMessageRuleExecutions>>, TError = unknown>(
 params?: ListSocialMessageRuleExecutionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSocialMessageRuleExecutions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSocialMessageRuleExecutionsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRunSocialMessageAutomationUrl = () => {




  return `/api/social/automation/run`
}

/**
 * @summary Process eligible inbox items through approved active rules
 */
export const runSocialMessageAutomation = async ( options?: Parameters<typeof customFetch>[1]): Promise<SocialMessageAutomationRunResult> => {

  return customFetch<SocialMessageAutomationRunResult>(getRunSocialMessageAutomationUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRunSocialMessageAutomationMutationKey = () => ['runSocialMessageAutomation'] as const;

export const getRunSocialMessageAutomationMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runSocialMessageAutomation>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof runSocialMessageAutomation>>, TError,void, TContext> => {

const mutationKey = getRunSocialMessageAutomationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof runSocialMessageAutomation>>, void> = () => {


          return  runSocialMessageAutomation(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RunSocialMessageAutomationMutationResult = NonNullable<Awaited<ReturnType<typeof runSocialMessageAutomation>>>

    export type RunSocialMessageAutomationMutationError = unknown


    /**
 * @summary Process eligible inbox items through approved active rules
 */
export const useRunSocialMessageAutomation = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runSocialMessageAutomation>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof runSocialMessageAutomation>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getRunSocialMessageAutomationMutationOptions(options));
    }

export const getGetSocialAnalyticsUrl = () => {




  return `/api/social/analytics`
}

/**
 * @summary Get per-network and per-post social metrics
 */
export const getSocialAnalytics = async ( options?: Parameters<typeof customFetch>[1]): Promise<SocialAnalytics> => {

  return customFetch<SocialAnalytics>(getGetSocialAnalyticsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSocialAnalyticsQueryKey = () => {
    return [
    `/api/social/analytics`
    ] as const;
    }


export const getGetSocialAnalyticsQueryOptions = <TData = Awaited<ReturnType<typeof getSocialAnalytics>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSocialAnalytics>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSocialAnalyticsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSocialAnalytics>>> = ({ signal }) => getSocialAnalytics({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSocialAnalytics>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSocialAnalyticsQueryResult = NonNullable<Awaited<ReturnType<typeof getSocialAnalytics>>>
export type GetSocialAnalyticsQueryError = unknown


/**
 * @summary Get per-network and per-post social metrics
 */

export function useGetSocialAnalytics<TData = Awaited<ReturnType<typeof getSocialAnalytics>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSocialAnalytics>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSocialAnalyticsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSyncSocialAnalyticsUrl = () => {




  return `/api/social/analytics/sync`
}

/**
 * @summary Refresh metrics for published posts
 */
export const syncSocialAnalytics = async ( options?: Parameters<typeof customFetch>[1]): Promise<SyncSocialAnalytics200> => {

  return customFetch<SyncSocialAnalytics200>(getSyncSocialAnalyticsUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getSyncSocialAnalyticsMutationKey = () => ['syncSocialAnalytics'] as const;

export const getSyncSocialAnalyticsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof syncSocialAnalytics>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof syncSocialAnalytics>>, TError,void, TContext> => {

const mutationKey = getSyncSocialAnalyticsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof syncSocialAnalytics>>, void> = () => {


          return  syncSocialAnalytics(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SyncSocialAnalyticsMutationResult = NonNullable<Awaited<ReturnType<typeof syncSocialAnalytics>>>

    export type SyncSocialAnalyticsMutationError = unknown


    /**
 * @summary Refresh metrics for published posts
 */
export const useSyncSocialAnalytics = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof syncSocialAnalytics>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof syncSocialAnalytics>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getSyncSocialAnalyticsMutationOptions(options));
    }

export const getGetEmailConnectionUrl = () => {




  return `/api/email/connection`
}

/**
 * @summary Get Microsoft 365 mailbox connection and throttle status
 */
export const getEmailConnection = async ( options?: Parameters<typeof customFetch>[1]): Promise<EmailConnection> => {

  return customFetch<EmailConnection>(getGetEmailConnectionUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEmailConnectionQueryKey = () => {
    return [
    `/api/email/connection`
    ] as const;
    }


export const getGetEmailConnectionQueryOptions = <TData = Awaited<ReturnType<typeof getEmailConnection>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEmailConnection>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEmailConnectionQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEmailConnection>>> = ({ signal }) => getEmailConnection({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEmailConnection>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEmailConnectionQueryResult = NonNullable<Awaited<ReturnType<typeof getEmailConnection>>>
export type GetEmailConnectionQueryError = unknown


/**
 * @summary Get Microsoft 365 mailbox connection and throttle status
 */

export function useGetEmailConnection<TData = Awaited<ReturnType<typeof getEmailConnection>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEmailConnection>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEmailConnectionQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getStartMicrosoft365EmailOAuthUrl = () => {




  return `/api/email/microsoft365/oauth`
}

/**
 * @summary Start delegated Microsoft Graph mailbox consent
 */
export const startMicrosoft365EmailOAuth = async ( options?: Parameters<typeof customFetch>[1]): Promise<EmailOAuthStart> => {

  return customFetch<EmailOAuthStart>(getStartMicrosoft365EmailOAuthUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getStartMicrosoft365EmailOAuthQueryKey = () => {
    return [
    `/api/email/microsoft365/oauth`
    ] as const;
    }


export const getStartMicrosoft365EmailOAuthQueryOptions = <TData = Awaited<ReturnType<typeof startMicrosoft365EmailOAuth>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof startMicrosoft365EmailOAuth>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getStartMicrosoft365EmailOAuthQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof startMicrosoft365EmailOAuth>>> = ({ signal }) => startMicrosoft365EmailOAuth({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof startMicrosoft365EmailOAuth>>, TError, TData> & { queryKey: QueryKey }
}

export type StartMicrosoft365EmailOAuthQueryResult = NonNullable<Awaited<ReturnType<typeof startMicrosoft365EmailOAuth>>>
export type StartMicrosoft365EmailOAuthQueryError = unknown


/**
 * @summary Start delegated Microsoft Graph mailbox consent
 */

export function useStartMicrosoft365EmailOAuth<TData = Awaited<ReturnType<typeof startMicrosoft365EmailOAuth>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof startMicrosoft365EmailOAuth>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getStartMicrosoft365EmailOAuthQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCompleteMicrosoft365EmailOAuthUrl = (params: CompleteMicrosoft365EmailOAuthParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/email/microsoft365/oauth/callback?${stringifiedParams}` : `/api/email/microsoft365/oauth/callback`
}

/**
 * @summary Complete delegated Microsoft Graph mailbox consent
 */
export const completeMicrosoft365EmailOAuth = async (params: CompleteMicrosoft365EmailOAuthParams, options?: Parameters<typeof customFetch>[1]): Promise<unknown> => {

  return customFetch<unknown>(getCompleteMicrosoft365EmailOAuthUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getCompleteMicrosoft365EmailOAuthQueryKey = (params?: CompleteMicrosoft365EmailOAuthParams,) => {
    return [
    `/api/email/microsoft365/oauth/callback`, ...(params ? [params] : [])
    ] as const;
    }


export const getCompleteMicrosoft365EmailOAuthQueryOptions = <TData = Awaited<ReturnType<typeof completeMicrosoft365EmailOAuth>>, TError = void>(params: CompleteMicrosoft365EmailOAuthParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof completeMicrosoft365EmailOAuth>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getCompleteMicrosoft365EmailOAuthQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof completeMicrosoft365EmailOAuth>>> = ({ signal }) => completeMicrosoft365EmailOAuth(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof completeMicrosoft365EmailOAuth>>, TError, TData> & { queryKey: QueryKey }
}

export type CompleteMicrosoft365EmailOAuthQueryResult = NonNullable<Awaited<ReturnType<typeof completeMicrosoft365EmailOAuth>>>
export type CompleteMicrosoft365EmailOAuthQueryError = void


/**
 * @summary Complete delegated Microsoft Graph mailbox consent
 */

export function useCompleteMicrosoft365EmailOAuth<TData = Awaited<ReturnType<typeof completeMicrosoft365EmailOAuth>>, TError = void>(
 params: CompleteMicrosoft365EmailOAuthParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof completeMicrosoft365EmailOAuth>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getCompleteMicrosoft365EmailOAuthQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSendTestEmailUrl = () => {




  return `/api/email/test`
}

/**
 * @summary Send a connection test through the configured provider
 */
export const sendTestEmail = async (emailTestRequest: EmailTestRequest, options?: Parameters<typeof customFetch>[1]): Promise<EmailMessage> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EmailMessage>(getSendTestEmailUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(emailTestRequest)
  }
);}





export const getSendTestEmailMutationKey = () => ['sendTestEmail'] as const;

export const getSendTestEmailMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendTestEmail>>, TError,SendTestEmailMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof sendTestEmail>>, TError,SendTestEmailMutationVariables, TContext> => {

const mutationKey = getSendTestEmailMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof sendTestEmail>>, SendTestEmailMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  sendTestEmail(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SendTestEmailMutationResult = NonNullable<Awaited<ReturnType<typeof sendTestEmail>>>
    export type SendTestEmailMutationBody = EmailTestRequest
    export type SendTestEmailMutationError = unknown
    export type SendTestEmailMutationVariables = {data: EmailTestRequest}

    /**
 * @summary Send a connection test through the configured provider
 */
export const useSendTestEmail = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendTestEmail>>, TError,SendTestEmailMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof sendTestEmail>>,
        TError,
        SendTestEmailMutationVariables,
        TContext
      > => {
      return useMutation(getSendTestEmailMutationOptions(options));
    }

export const getListEmailSequencesUrl = () => {




  return `/api/email/sequences`
}

export const listEmailSequences = async ( options?: Parameters<typeof customFetch>[1]): Promise<ListEmailSequences200> => {

  return customFetch<ListEmailSequences200>(getListEmailSequencesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListEmailSequencesQueryKey = () => {
    return [
    `/api/email/sequences`
    ] as const;
    }


export const getListEmailSequencesQueryOptions = <TData = Awaited<ReturnType<typeof listEmailSequences>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEmailSequences>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEmailSequencesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEmailSequences>>> = ({ signal }) => listEmailSequences({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEmailSequences>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEmailSequencesQueryResult = NonNullable<Awaited<ReturnType<typeof listEmailSequences>>>
export type ListEmailSequencesQueryError = unknown



export function useListEmailSequences<TData = Awaited<ReturnType<typeof listEmailSequences>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEmailSequences>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEmailSequencesQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateEmailSequenceUrl = () => {




  return `/api/email/sequences`
}

export const createEmailSequence = async (emailSequenceCreate: EmailSequenceCreate, options?: Parameters<typeof customFetch>[1]): Promise<EmailSequence> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EmailSequence>(getCreateEmailSequenceUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(emailSequenceCreate)
  }
);}





export const getCreateEmailSequenceMutationKey = () => ['createEmailSequence'] as const;

export const getCreateEmailSequenceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEmailSequence>>, TError,CreateEmailSequenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createEmailSequence>>, TError,CreateEmailSequenceMutationVariables, TContext> => {

const mutationKey = getCreateEmailSequenceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createEmailSequence>>, CreateEmailSequenceMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createEmailSequence(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateEmailSequenceMutationResult = NonNullable<Awaited<ReturnType<typeof createEmailSequence>>>
    export type CreateEmailSequenceMutationBody = EmailSequenceCreate
    export type CreateEmailSequenceMutationError = unknown
    export type CreateEmailSequenceMutationVariables = {data: EmailSequenceCreate}

    export const useCreateEmailSequence = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEmailSequence>>, TError,CreateEmailSequenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createEmailSequence>>,
        TError,
        CreateEmailSequenceMutationVariables,
        TContext
      > => {
      return useMutation(getCreateEmailSequenceMutationOptions(options));
    }

export const getUpdateEmailSequenceUrl = (id: string,) => {




  return `/api/email/sequences/${id}`
}

export const updateEmailSequence = async (id: string,
    emailSequenceUpdate: EmailSequenceUpdate, options?: Parameters<typeof customFetch>[1]): Promise<EmailSequence> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EmailSequence>(getUpdateEmailSequenceUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(emailSequenceUpdate)
  }
);}





export const getUpdateEmailSequenceMutationKey = () => ['updateEmailSequence'] as const;

export const getUpdateEmailSequenceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEmailSequence>>, TError,UpdateEmailSequenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateEmailSequence>>, TError,UpdateEmailSequenceMutationVariables, TContext> => {

const mutationKey = getUpdateEmailSequenceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateEmailSequence>>, UpdateEmailSequenceMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateEmailSequence(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateEmailSequenceMutationResult = NonNullable<Awaited<ReturnType<typeof updateEmailSequence>>>
    export type UpdateEmailSequenceMutationBody = EmailSequenceUpdate
    export type UpdateEmailSequenceMutationError = Error
    export type UpdateEmailSequenceMutationVariables = {id: string;data: EmailSequenceUpdate}

    export const useUpdateEmailSequence = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEmailSequence>>, TError,UpdateEmailSequenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateEmailSequence>>,
        TError,
        UpdateEmailSequenceMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateEmailSequenceMutationOptions(options));
    }

export const getAddEmailSequenceStepUrl = (id: string,) => {




  return `/api/email/sequences/${id}/steps`
}

export const addEmailSequenceStep = async (id: string,
    emailSequenceStepCreate: EmailSequenceStepCreate, options?: Parameters<typeof customFetch>[1]): Promise<EmailSequenceStep> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EmailSequenceStep>(getAddEmailSequenceStepUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(emailSequenceStepCreate)
  }
);}





export const getAddEmailSequenceStepMutationKey = () => ['addEmailSequenceStep'] as const;

export const getAddEmailSequenceStepMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addEmailSequenceStep>>, TError,AddEmailSequenceStepMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof addEmailSequenceStep>>, TError,AddEmailSequenceStepMutationVariables, TContext> => {

const mutationKey = getAddEmailSequenceStepMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof addEmailSequenceStep>>, AddEmailSequenceStepMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  addEmailSequenceStep(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AddEmailSequenceStepMutationResult = NonNullable<Awaited<ReturnType<typeof addEmailSequenceStep>>>
    export type AddEmailSequenceStepMutationBody = EmailSequenceStepCreate
    export type AddEmailSequenceStepMutationError = unknown
    export type AddEmailSequenceStepMutationVariables = {id: string;data: EmailSequenceStepCreate}

    export const useAddEmailSequenceStep = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addEmailSequenceStep>>, TError,AddEmailSequenceStepMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof addEmailSequenceStep>>,
        TError,
        AddEmailSequenceStepMutationVariables,
        TContext
      > => {
      return useMutation(getAddEmailSequenceStepMutationOptions(options));
    }

export const getEnrollEmailSequenceUrl = (id: string,) => {




  return `/api/email/sequences/${id}/enroll`
}

export const enrollEmailSequence = async (id: string,
    emailEnrollmentCreate: EmailEnrollmentCreate, options?: Parameters<typeof customFetch>[1]): Promise<EmailEnrollment> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EmailEnrollment>(getEnrollEmailSequenceUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(emailEnrollmentCreate)
  }
);}





export const getEnrollEmailSequenceMutationKey = () => ['enrollEmailSequence'] as const;

export const getEnrollEmailSequenceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof enrollEmailSequence>>, TError,EnrollEmailSequenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof enrollEmailSequence>>, TError,EnrollEmailSequenceMutationVariables, TContext> => {

const mutationKey = getEnrollEmailSequenceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof enrollEmailSequence>>, EnrollEmailSequenceMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  enrollEmailSequence(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type EnrollEmailSequenceMutationResult = NonNullable<Awaited<ReturnType<typeof enrollEmailSequence>>>
    export type EnrollEmailSequenceMutationBody = EmailEnrollmentCreate
    export type EnrollEmailSequenceMutationError = unknown
    export type EnrollEmailSequenceMutationVariables = {id: string;data: EmailEnrollmentCreate}

    export const useEnrollEmailSequence = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof enrollEmailSequence>>, TError,EnrollEmailSequenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof enrollEmailSequence>>,
        TError,
        EnrollEmailSequenceMutationVariables,
        TContext
      > => {
      return useMutation(getEnrollEmailSequenceMutationOptions(options));
    }

export const getListEmailSequenceEnrollmentsUrl = (id: string,
    params?: ListEmailSequenceEnrollmentsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/email/sequences/${id}/enrollments?${stringifiedParams}` : `/api/email/sequences/${id}/enrollments`
}

export const listEmailSequenceEnrollments = async (id: string,
    params?: ListEmailSequenceEnrollmentsParams, options?: Parameters<typeof customFetch>[1]): Promise<EmailEnrollmentList> => {

  return customFetch<EmailEnrollmentList>(getListEmailSequenceEnrollmentsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListEmailSequenceEnrollmentsQueryKey = (id: string,
    params?: ListEmailSequenceEnrollmentsParams,) => {
    return [
    `/api/email/sequences/${id}/enrollments`, ...(params ? [params] : [])
    ] as const;
    }


export const getListEmailSequenceEnrollmentsQueryOptions = <TData = Awaited<ReturnType<typeof listEmailSequenceEnrollments>>, TError = unknown>(id: string,
    params?: ListEmailSequenceEnrollmentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEmailSequenceEnrollments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEmailSequenceEnrollmentsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEmailSequenceEnrollments>>> = ({ signal }) => listEmailSequenceEnrollments(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEmailSequenceEnrollments>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEmailSequenceEnrollmentsQueryResult = NonNullable<Awaited<ReturnType<typeof listEmailSequenceEnrollments>>>
export type ListEmailSequenceEnrollmentsQueryError = unknown



export function useListEmailSequenceEnrollments<TData = Awaited<ReturnType<typeof listEmailSequenceEnrollments>>, TError = unknown>(
 id: string,
    params?: ListEmailSequenceEnrollmentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEmailSequenceEnrollments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEmailSequenceEnrollmentsQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateEmailSequenceEnrollmentUrl = (id: string,
    enrollmentId: string,) => {




  return `/api/email/sequences/${id}/enrollments/${enrollmentId}`
}

export const updateEmailSequenceEnrollment = async (id: string,
    enrollmentId: string,
    emailEnrollmentUpdate: EmailEnrollmentUpdate, options?: Parameters<typeof customFetch>[1]): Promise<EmailEnrollment> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EmailEnrollment>(getUpdateEmailSequenceEnrollmentUrl(id,enrollmentId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(emailEnrollmentUpdate)
  }
);}





export const getUpdateEmailSequenceEnrollmentMutationKey = () => ['updateEmailSequenceEnrollment'] as const;

export const getUpdateEmailSequenceEnrollmentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEmailSequenceEnrollment>>, TError,UpdateEmailSequenceEnrollmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateEmailSequenceEnrollment>>, TError,UpdateEmailSequenceEnrollmentMutationVariables, TContext> => {

const mutationKey = getUpdateEmailSequenceEnrollmentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateEmailSequenceEnrollment>>, UpdateEmailSequenceEnrollmentMutationVariables> = (props) => {
          const {id,enrollmentId,data} = props ?? {};

          return  updateEmailSequenceEnrollment(id,enrollmentId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateEmailSequenceEnrollmentMutationResult = NonNullable<Awaited<ReturnType<typeof updateEmailSequenceEnrollment>>>
    export type UpdateEmailSequenceEnrollmentMutationBody = EmailEnrollmentUpdate
    export type UpdateEmailSequenceEnrollmentMutationError = Error
    export type UpdateEmailSequenceEnrollmentMutationVariables = {id: string;enrollmentId: string;data: EmailEnrollmentUpdate}

    export const useUpdateEmailSequenceEnrollment = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEmailSequenceEnrollment>>, TError,UpdateEmailSequenceEnrollmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateEmailSequenceEnrollment>>,
        TError,
        UpdateEmailSequenceEnrollmentMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateEmailSequenceEnrollmentMutationOptions(options));
    }

export const getRunEmailSequenceEngineUrl = () => {




  return `/api/email/sequence-engine/run`
}

export const runEmailSequenceEngine = async ( options?: Parameters<typeof customFetch>[1]): Promise<RunEmailSequenceEngine200> => {

  return customFetch<RunEmailSequenceEngine200>(getRunEmailSequenceEngineUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRunEmailSequenceEngineMutationKey = () => ['runEmailSequenceEngine'] as const;

export const getRunEmailSequenceEngineMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runEmailSequenceEngine>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof runEmailSequenceEngine>>, TError,void, TContext> => {

const mutationKey = getRunEmailSequenceEngineMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof runEmailSequenceEngine>>, void> = () => {


          return  runEmailSequenceEngine(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RunEmailSequenceEngineMutationResult = NonNullable<Awaited<ReturnType<typeof runEmailSequenceEngine>>>

    export type RunEmailSequenceEngineMutationError = unknown


    export const useRunEmailSequenceEngine = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runEmailSequenceEngine>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof runEmailSequenceEngine>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getRunEmailSequenceEngineMutationOptions(options));
    }

export const getGetCampaignBriefUrl = (id: string,) => {




  return `/api/campaign-planning/${id}`
}

export const getCampaignBrief = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<CampaignBrief> => {

  return customFetch<CampaignBrief>(getGetCampaignBriefUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetCampaignBriefQueryKey = (id: string,) => {
    return [
    `/api/campaign-planning/${id}`
    ] as const;
    }


export const getGetCampaignBriefQueryOptions = <TData = Awaited<ReturnType<typeof getCampaignBrief>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCampaignBrief>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCampaignBriefQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCampaignBrief>>> = ({ signal }) => getCampaignBrief(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCampaignBrief>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCampaignBriefQueryResult = NonNullable<Awaited<ReturnType<typeof getCampaignBrief>>>
export type GetCampaignBriefQueryError = unknown



export function useGetCampaignBrief<TData = Awaited<ReturnType<typeof getCampaignBrief>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCampaignBrief>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCampaignBriefQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListCampaignBriefHistoryUrl = (id: string,
    params?: ListCampaignBriefHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/campaign-planning/${id}/history?${stringifiedParams}` : `/api/campaign-planning/${id}/history`
}

export const listCampaignBriefHistory = async (id: string,
    params?: ListCampaignBriefHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<ListCampaignBriefHistory200> => {

  return customFetch<ListCampaignBriefHistory200>(getListCampaignBriefHistoryUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCampaignBriefHistoryQueryKey = (id: string,
    params?: ListCampaignBriefHistoryParams,) => {
    return [
    `/api/campaign-planning/${id}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListCampaignBriefHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listCampaignBriefHistory>>, TError = unknown>(id: string,
    params?: ListCampaignBriefHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCampaignBriefHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCampaignBriefHistoryQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCampaignBriefHistory>>> = ({ signal }) => listCampaignBriefHistory(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCampaignBriefHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCampaignBriefHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listCampaignBriefHistory>>>
export type ListCampaignBriefHistoryQueryError = unknown



export function useListCampaignBriefHistory<TData = Awaited<ReturnType<typeof listCampaignBriefHistory>>, TError = unknown>(
 id: string,
    params?: ListCampaignBriefHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCampaignBriefHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCampaignBriefHistoryQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPreviewCampaignAudienceUrl = (id: string,
    params?: PreviewCampaignAudienceParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/campaign-planning/${id}/audience?${stringifiedParams}` : `/api/campaign-planning/${id}/audience`
}

export const previewCampaignAudience = async (id: string,
    campaignAudienceDefinition: CampaignAudienceDefinition,
    params?: PreviewCampaignAudienceParams, options?: Parameters<typeof customFetch>[1]): Promise<CampaignAudiencePreview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CampaignAudiencePreview>(getPreviewCampaignAudienceUrl(id,params),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(campaignAudienceDefinition)
  }
);}





export const getPreviewCampaignAudienceMutationKey = () => ['previewCampaignAudience'] as const;

export const getPreviewCampaignAudienceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewCampaignAudience>>, TError,PreviewCampaignAudienceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewCampaignAudience>>, TError,PreviewCampaignAudienceMutationVariables, TContext> => {

const mutationKey = getPreviewCampaignAudienceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewCampaignAudience>>, PreviewCampaignAudienceMutationVariables> = (props) => {
          const {id,data,params} = props ?? {};

          return  previewCampaignAudience(id,data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewCampaignAudienceMutationResult = NonNullable<Awaited<ReturnType<typeof previewCampaignAudience>>>
    export type PreviewCampaignAudienceMutationBody = CampaignAudienceDefinition
    export type PreviewCampaignAudienceMutationError = unknown
    export type PreviewCampaignAudienceMutationVariables = {id: string;data: CampaignAudienceDefinition;params?: PreviewCampaignAudienceParams}

    export const usePreviewCampaignAudience = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewCampaignAudience>>, TError,PreviewCampaignAudienceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewCampaignAudience>>,
        TError,
        PreviewCampaignAudienceMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewCampaignAudienceMutationOptions(options));
    }

export const getReviewCampaignMessagingUrl = () => {




  return `/api/campaign-messaging/reviews`
}

export const reviewCampaignMessaging = async (reviewCampaignMessagingBody: ReviewCampaignMessagingBody, options?: Parameters<typeof customFetch>[1]): Promise<AgentActionProposal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentActionProposal>(getReviewCampaignMessagingUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewCampaignMessagingBody)
  }
);}





export const getReviewCampaignMessagingMutationKey = () => ['reviewCampaignMessaging'] as const;

export const getReviewCampaignMessagingMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewCampaignMessaging>>, TError,ReviewCampaignMessagingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewCampaignMessaging>>, TError,ReviewCampaignMessagingMutationVariables, TContext> => {

const mutationKey = getReviewCampaignMessagingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewCampaignMessaging>>, ReviewCampaignMessagingMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reviewCampaignMessaging(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewCampaignMessagingMutationResult = NonNullable<Awaited<ReturnType<typeof reviewCampaignMessaging>>>
    export type ReviewCampaignMessagingMutationBody = ReviewCampaignMessagingBody
    export type ReviewCampaignMessagingMutationError = unknown
    export type ReviewCampaignMessagingMutationVariables = {data: ReviewCampaignMessagingBody}

    export const useReviewCampaignMessaging = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewCampaignMessaging>>, TError,ReviewCampaignMessagingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewCampaignMessaging>>,
        TError,
        ReviewCampaignMessagingMutationVariables,
        TContext
      > => {
      return useMutation(getReviewCampaignMessagingMutationOptions(options));
    }

export const getReviewCampaignBriefUrl = () => {




  return `/api/campaign-brief-reviews`
}

export const reviewCampaignBrief = async (reviewCampaignBriefBody: ReviewCampaignBriefBody, options?: Parameters<typeof customFetch>[1]): Promise<CampaignBriefReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CampaignBriefReview>(getReviewCampaignBriefUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewCampaignBriefBody)
  }
);}





export const getReviewCampaignBriefMutationKey = () => ['reviewCampaignBrief'] as const;

export const getReviewCampaignBriefMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewCampaignBrief>>, TError,ReviewCampaignBriefMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewCampaignBrief>>, TError,ReviewCampaignBriefMutationVariables, TContext> => {

const mutationKey = getReviewCampaignBriefMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewCampaignBrief>>, ReviewCampaignBriefMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reviewCampaignBrief(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewCampaignBriefMutationResult = NonNullable<Awaited<ReturnType<typeof reviewCampaignBrief>>>
    export type ReviewCampaignBriefMutationBody = ReviewCampaignBriefBody
    export type ReviewCampaignBriefMutationError = unknown
    export type ReviewCampaignBriefMutationVariables = {data: ReviewCampaignBriefBody}

    export const useReviewCampaignBrief = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewCampaignBrief>>, TError,ReviewCampaignBriefMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewCampaignBrief>>,
        TError,
        ReviewCampaignBriefMutationVariables,
        TContext
      > => {
      return useMutation(getReviewCampaignBriefMutationOptions(options));
    }

export const getGetCampaignBriefReviewUrl = (id: string,) => {




  return `/api/campaign-brief-reviews/${id}`
}

export const getCampaignBriefReview = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<CampaignBriefReview> => {

  return customFetch<CampaignBriefReview>(getGetCampaignBriefReviewUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetCampaignBriefReviewQueryKey = (id: string,) => {
    return [
    `/api/campaign-brief-reviews/${id}`
    ] as const;
    }


export const getGetCampaignBriefReviewQueryOptions = <TData = Awaited<ReturnType<typeof getCampaignBriefReview>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCampaignBriefReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCampaignBriefReviewQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCampaignBriefReview>>> = ({ signal }) => getCampaignBriefReview(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCampaignBriefReview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCampaignBriefReviewQueryResult = NonNullable<Awaited<ReturnType<typeof getCampaignBriefReview>>>
export type GetCampaignBriefReviewQueryError = unknown



export function useGetCampaignBriefReview<TData = Awaited<ReturnType<typeof getCampaignBriefReview>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCampaignBriefReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCampaignBriefReviewQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConfirmCampaignBriefUrl = (id: string,) => {




  return `/api/campaign-brief-reviews/${id}/confirm`
}

export const confirmCampaignBrief = async (id: string,
    confirmCampaignBriefBody: ConfirmCampaignBriefBody, options?: Parameters<typeof customFetch>[1]): Promise<CampaignBriefReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CampaignBriefReview>(getConfirmCampaignBriefUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(confirmCampaignBriefBody)
  }
);}





export const getConfirmCampaignBriefMutationKey = () => ['confirmCampaignBrief'] as const;

export const getConfirmCampaignBriefMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmCampaignBrief>>, TError,ConfirmCampaignBriefMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmCampaignBrief>>, TError,ConfirmCampaignBriefMutationVariables, TContext> => {

const mutationKey = getConfirmCampaignBriefMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmCampaignBrief>>, ConfirmCampaignBriefMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  confirmCampaignBrief(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmCampaignBriefMutationResult = NonNullable<Awaited<ReturnType<typeof confirmCampaignBrief>>>
    export type ConfirmCampaignBriefMutationBody = ConfirmCampaignBriefBody
    export type ConfirmCampaignBriefMutationError = unknown
    export type ConfirmCampaignBriefMutationVariables = {id: string;data: ConfirmCampaignBriefBody}

    export const useConfirmCampaignBrief = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmCampaignBrief>>, TError,ConfirmCampaignBriefMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmCampaignBrief>>,
        TError,
        ConfirmCampaignBriefMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmCampaignBriefMutationOptions(options));
    }

export const getCancelCampaignBriefUrl = (id: string,) => {




  return `/api/campaign-brief-reviews/${id}/cancel`
}

export const cancelCampaignBrief = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<CampaignBriefReview> => {

  return customFetch<CampaignBriefReview>(getCancelCampaignBriefUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelCampaignBriefMutationKey = () => ['cancelCampaignBrief'] as const;

export const getCancelCampaignBriefMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelCampaignBrief>>, TError,CancelCampaignBriefMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelCampaignBrief>>, TError,CancelCampaignBriefMutationVariables, TContext> => {

const mutationKey = getCancelCampaignBriefMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelCampaignBrief>>, CancelCampaignBriefMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  cancelCampaignBrief(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelCampaignBriefMutationResult = NonNullable<Awaited<ReturnType<typeof cancelCampaignBrief>>>

    export type CancelCampaignBriefMutationError = unknown
    export type CancelCampaignBriefMutationVariables = {id: string}

    export const useCancelCampaignBrief = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelCampaignBrief>>, TError,CancelCampaignBriefMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelCampaignBrief>>,
        TError,
        CancelCampaignBriefMutationVariables,
        TContext
      > => {
      return useMutation(getCancelCampaignBriefMutationOptions(options));
    }

export const getListEmailCampaignsUrl = (params?: ListEmailCampaignsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/email/campaigns?${stringifiedParams}` : `/api/email/campaigns`
}

export const listEmailCampaigns = async (params?: ListEmailCampaignsParams, options?: Parameters<typeof customFetch>[1]): Promise<ListEmailCampaigns200> => {

  return customFetch<ListEmailCampaigns200>(getListEmailCampaignsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListEmailCampaignsQueryKey = (params?: ListEmailCampaignsParams,) => {
    return [
    `/api/email/campaigns`, ...(params ? [params] : [])
    ] as const;
    }


export const getListEmailCampaignsQueryOptions = <TData = Awaited<ReturnType<typeof listEmailCampaigns>>, TError = unknown>(params?: ListEmailCampaignsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEmailCampaigns>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEmailCampaignsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEmailCampaigns>>> = ({ signal }) => listEmailCampaigns(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEmailCampaigns>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEmailCampaignsQueryResult = NonNullable<Awaited<ReturnType<typeof listEmailCampaigns>>>
export type ListEmailCampaignsQueryError = unknown



export function useListEmailCampaigns<TData = Awaited<ReturnType<typeof listEmailCampaigns>>, TError = unknown>(
 params?: ListEmailCampaignsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEmailCampaigns>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEmailCampaignsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateEmailCampaignUrl = () => {




  return `/api/email/campaigns`
}

export const createEmailCampaign = async (emailCampaignCreate: EmailCampaignCreate, options?: Parameters<typeof customFetch>[1]): Promise<EmailCampaign> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EmailCampaign>(getCreateEmailCampaignUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(emailCampaignCreate)
  }
);}





export const getCreateEmailCampaignMutationKey = () => ['createEmailCampaign'] as const;

export const getCreateEmailCampaignMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEmailCampaign>>, TError,CreateEmailCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createEmailCampaign>>, TError,CreateEmailCampaignMutationVariables, TContext> => {

const mutationKey = getCreateEmailCampaignMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createEmailCampaign>>, CreateEmailCampaignMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createEmailCampaign(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateEmailCampaignMutationResult = NonNullable<Awaited<ReturnType<typeof createEmailCampaign>>>
    export type CreateEmailCampaignMutationBody = EmailCampaignCreate
    export type CreateEmailCampaignMutationError = unknown
    export type CreateEmailCampaignMutationVariables = {data: EmailCampaignCreate}

    export const useCreateEmailCampaign = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEmailCampaign>>, TError,CreateEmailCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createEmailCampaign>>,
        TError,
        CreateEmailCampaignMutationVariables,
        TContext
      > => {
      return useMutation(getCreateEmailCampaignMutationOptions(options));
    }

export const getUpdateEmailCampaignUrl = (id: string,) => {




  return `/api/email/campaigns/${id}`
}

export const updateEmailCampaign = async (id: string,
    emailCampaignUpdate: EmailCampaignUpdate, options?: Parameters<typeof customFetch>[1]): Promise<EmailCampaign> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EmailCampaign>(getUpdateEmailCampaignUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(emailCampaignUpdate)
  }
);}





export const getUpdateEmailCampaignMutationKey = () => ['updateEmailCampaign'] as const;

export const getUpdateEmailCampaignMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEmailCampaign>>, TError,UpdateEmailCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateEmailCampaign>>, TError,UpdateEmailCampaignMutationVariables, TContext> => {

const mutationKey = getUpdateEmailCampaignMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateEmailCampaign>>, UpdateEmailCampaignMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateEmailCampaign(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateEmailCampaignMutationResult = NonNullable<Awaited<ReturnType<typeof updateEmailCampaign>>>
    export type UpdateEmailCampaignMutationBody = EmailCampaignUpdate
    export type UpdateEmailCampaignMutationError = Error
    export type UpdateEmailCampaignMutationVariables = {id: string;data: EmailCampaignUpdate}

    export const useUpdateEmailCampaign = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEmailCampaign>>, TError,UpdateEmailCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateEmailCampaign>>,
        TError,
        UpdateEmailCampaignMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateEmailCampaignMutationOptions(options));
    }

export const getSendEmailCampaignUrl = (id: string,) => {




  return `/api/email/campaigns/${id}/send`
}

export const sendEmailCampaign = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<EmailCampaign> => {

  return customFetch<EmailCampaign>(getSendEmailCampaignUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getSendEmailCampaignMutationKey = () => ['sendEmailCampaign'] as const;

export const getSendEmailCampaignMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendEmailCampaign>>, TError,SendEmailCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof sendEmailCampaign>>, TError,SendEmailCampaignMutationVariables, TContext> => {

const mutationKey = getSendEmailCampaignMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof sendEmailCampaign>>, SendEmailCampaignMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  sendEmailCampaign(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SendEmailCampaignMutationResult = NonNullable<Awaited<ReturnType<typeof sendEmailCampaign>>>

    export type SendEmailCampaignMutationError = unknown
    export type SendEmailCampaignMutationVariables = {id: string}

    export const useSendEmailCampaign = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendEmailCampaign>>, TError,SendEmailCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof sendEmailCampaign>>,
        TError,
        SendEmailCampaignMutationVariables,
        TContext
      > => {
      return useMutation(getSendEmailCampaignMutationOptions(options));
    }

export const getControlEmailCampaignUrl = (id: string,) => {




  return `/api/email/campaigns/${id}/control`
}

export const controlEmailCampaign = async (id: string,
    emailCampaignControl: EmailCampaignControl, options?: Parameters<typeof customFetch>[1]): Promise<EmailCampaign> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EmailCampaign>(getControlEmailCampaignUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(emailCampaignControl)
  }
);}





export const getControlEmailCampaignMutationKey = () => ['controlEmailCampaign'] as const;

export const getControlEmailCampaignMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof controlEmailCampaign>>, TError,ControlEmailCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof controlEmailCampaign>>, TError,ControlEmailCampaignMutationVariables, TContext> => {

const mutationKey = getControlEmailCampaignMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof controlEmailCampaign>>, ControlEmailCampaignMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  controlEmailCampaign(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ControlEmailCampaignMutationResult = NonNullable<Awaited<ReturnType<typeof controlEmailCampaign>>>
    export type ControlEmailCampaignMutationBody = EmailCampaignControl
    export type ControlEmailCampaignMutationError = Error
    export type ControlEmailCampaignMutationVariables = {id: string;data: EmailCampaignControl}

    export const useControlEmailCampaign = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof controlEmailCampaign>>, TError,ControlEmailCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof controlEmailCampaign>>,
        TError,
        ControlEmailCampaignMutationVariables,
        TContext
      > => {
      return useMutation(getControlEmailCampaignMutationOptions(options));
    }

export const getListEmailCampaignDeliveriesUrl = (id: string,
    params?: ListEmailCampaignDeliveriesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/email/campaigns/${id}/deliveries?${stringifiedParams}` : `/api/email/campaigns/${id}/deliveries`
}

export const listEmailCampaignDeliveries = async (id: string,
    params?: ListEmailCampaignDeliveriesParams, options?: Parameters<typeof customFetch>[1]): Promise<EmailDeliveryJobList> => {

  return customFetch<EmailDeliveryJobList>(getListEmailCampaignDeliveriesUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListEmailCampaignDeliveriesQueryKey = (id: string,
    params?: ListEmailCampaignDeliveriesParams,) => {
    return [
    `/api/email/campaigns/${id}/deliveries`, ...(params ? [params] : [])
    ] as const;
    }


export const getListEmailCampaignDeliveriesQueryOptions = <TData = Awaited<ReturnType<typeof listEmailCampaignDeliveries>>, TError = unknown>(id: string,
    params?: ListEmailCampaignDeliveriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEmailCampaignDeliveries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEmailCampaignDeliveriesQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEmailCampaignDeliveries>>> = ({ signal }) => listEmailCampaignDeliveries(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEmailCampaignDeliveries>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEmailCampaignDeliveriesQueryResult = NonNullable<Awaited<ReturnType<typeof listEmailCampaignDeliveries>>>
export type ListEmailCampaignDeliveriesQueryError = unknown



export function useListEmailCampaignDeliveries<TData = Awaited<ReturnType<typeof listEmailCampaignDeliveries>>, TError = unknown>(
 id: string,
    params?: ListEmailCampaignDeliveriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEmailCampaignDeliveries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEmailCampaignDeliveriesQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPreviewEmailRecipientsUrl = () => {




  return `/api/email/recipient-preview`
}

/**
 * @summary Preview CRM consent and suppression eligibility without sending
 */
export const previewEmailRecipients = async (previewEmailRecipientsBody: PreviewEmailRecipientsBody, options?: Parameters<typeof customFetch>[1]): Promise<EmailRecipientPreview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EmailRecipientPreview>(getPreviewEmailRecipientsUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(previewEmailRecipientsBody)
  }
);}





export const getPreviewEmailRecipientsMutationKey = () => ['previewEmailRecipients'] as const;

export const getPreviewEmailRecipientsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewEmailRecipients>>, TError,PreviewEmailRecipientsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewEmailRecipients>>, TError,PreviewEmailRecipientsMutationVariables, TContext> => {

const mutationKey = getPreviewEmailRecipientsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewEmailRecipients>>, PreviewEmailRecipientsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  previewEmailRecipients(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewEmailRecipientsMutationResult = NonNullable<Awaited<ReturnType<typeof previewEmailRecipients>>>
    export type PreviewEmailRecipientsMutationBody = PreviewEmailRecipientsBody
    export type PreviewEmailRecipientsMutationError = unknown
    export type PreviewEmailRecipientsMutationVariables = {data: PreviewEmailRecipientsBody}

    /**
 * @summary Preview CRM consent and suppression eligibility without sending
 */
export const usePreviewEmailRecipients = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewEmailRecipients>>, TError,PreviewEmailRecipientsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewEmailRecipients>>,
        TError,
        PreviewEmailRecipientsMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewEmailRecipientsMutationOptions(options));
    }

export const getRetryEmailDeliveryUrl = (id: string,) => {




  return `/api/email/deliveries/${id}/retry`
}

/**
 * @summary Retry a known pre-dispatch failure; uncertain deliveries cannot be retried
 */
export const retryEmailDelivery = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<EmailDeliveryJob> => {

  return customFetch<EmailDeliveryJob>(getRetryEmailDeliveryUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRetryEmailDeliveryMutationKey = () => ['retryEmailDelivery'] as const;

export const getRetryEmailDeliveryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryEmailDelivery>>, TError,RetryEmailDeliveryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof retryEmailDelivery>>, TError,RetryEmailDeliveryMutationVariables, TContext> => {

const mutationKey = getRetryEmailDeliveryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof retryEmailDelivery>>, RetryEmailDeliveryMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  retryEmailDelivery(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RetryEmailDeliveryMutationResult = NonNullable<Awaited<ReturnType<typeof retryEmailDelivery>>>

    export type RetryEmailDeliveryMutationError = Error
    export type RetryEmailDeliveryMutationVariables = {id: string}

    /**
 * @summary Retry a known pre-dispatch failure; uncertain deliveries cannot be retried
 */
export const useRetryEmailDelivery = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryEmailDelivery>>, TError,RetryEmailDeliveryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof retryEmailDelivery>>,
        TError,
        RetryEmailDeliveryMutationVariables,
        TContext
      > => {
      return useMutation(getRetryEmailDeliveryMutationOptions(options));
    }

export const getGetSmsProviderStatusUrl = () => {




  return `/api/sms/provider`
}

/**
 * @summary Show whether SMS uses Twilio or the network-free simulation provider
 */
export const getSmsProviderStatus = async ( options?: Parameters<typeof customFetch>[1]): Promise<SmsProviderStatus> => {

  return customFetch<SmsProviderStatus>(getGetSmsProviderStatusUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSmsProviderStatusQueryKey = () => {
    return [
    `/api/sms/provider`
    ] as const;
    }


export const getGetSmsProviderStatusQueryOptions = <TData = Awaited<ReturnType<typeof getSmsProviderStatus>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSmsProviderStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSmsProviderStatusQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSmsProviderStatus>>> = ({ signal }) => getSmsProviderStatus({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSmsProviderStatus>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSmsProviderStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getSmsProviderStatus>>>
export type GetSmsProviderStatusQueryError = unknown


/**
 * @summary Show whether SMS uses Twilio or the network-free simulation provider
 */

export function useGetSmsProviderStatus<TData = Awaited<ReturnType<typeof getSmsProviderStatus>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSmsProviderStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSmsProviderStatusQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReceiveTwilioSmsUrl = () => {




  return `/api/sms/webhooks/twilio/inbound`
}

/**
 * @summary Accept a signed inbound SMS from the connected Twilio number
 */
export const receiveTwilioSms = async (twilioInboundSms: TwilioInboundSms, options?: Parameters<typeof customFetch>[1]): Promise<string> => {
    const formUrlEncoded = new URLSearchParams();
formUrlEncoded.append(`MessageSid`, twilioInboundSms.MessageSid);
formUrlEncoded.append(`From`, twilioInboundSms.From);
formUrlEncoded.append(`To`, twilioInboundSms.To);
formUrlEncoded.append(`Body`, twilioInboundSms.Body);

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<string>(getReceiveTwilioSmsUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', ...getHeaders(options?.headers) },
    body: formUrlEncoded
  }
);}





export const getReceiveTwilioSmsMutationKey = () => ['receiveTwilioSms'] as const;

export const getReceiveTwilioSmsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof receiveTwilioSms>>, TError,ReceiveTwilioSmsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof receiveTwilioSms>>, TError,ReceiveTwilioSmsMutationVariables, TContext> => {

const mutationKey = getReceiveTwilioSmsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof receiveTwilioSms>>, ReceiveTwilioSmsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  receiveTwilioSms(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReceiveTwilioSmsMutationResult = NonNullable<Awaited<ReturnType<typeof receiveTwilioSms>>>
    export type ReceiveTwilioSmsMutationBody = TwilioInboundSms
    export type ReceiveTwilioSmsMutationError = Error
    export type ReceiveTwilioSmsMutationVariables = {data: TwilioInboundSms}

    /**
 * @summary Accept a signed inbound SMS from the connected Twilio number
 */
export const useReceiveTwilioSms = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof receiveTwilioSms>>, TError,ReceiveTwilioSmsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof receiveTwilioSms>>,
        TError,
        ReceiveTwilioSmsMutationVariables,
        TContext
      > => {
      return useMutation(getReceiveTwilioSmsMutationOptions(options));
    }

export const getReceiveTwilioSmsStatusUrl = (params: ReceiveTwilioSmsStatusParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/sms/webhooks/twilio/status?${stringifiedParams}` : `/api/sms/webhooks/twilio/status`
}

/**
 * @summary Accept a signed SMS delivery status callback
 */
export const receiveTwilioSmsStatus = async (twilioSmsStatus: TwilioSmsStatus,
    params: ReceiveTwilioSmsStatusParams, options?: Parameters<typeof customFetch>[1]): Promise<void> => {
    const formUrlEncoded = new URLSearchParams();
formUrlEncoded.append(`MessageSid`, twilioSmsStatus.MessageSid);
formUrlEncoded.append(`MessageStatus`, twilioSmsStatus.MessageStatus);
if(twilioSmsStatus.ErrorCode !== undefined && twilioSmsStatus.ErrorCode !== null) {
 formUrlEncoded.append(`ErrorCode`, twilioSmsStatus.ErrorCode);
 }
if(twilioSmsStatus.ErrorMessage !== undefined && twilioSmsStatus.ErrorMessage !== null) {
 formUrlEncoded.append(`ErrorMessage`, twilioSmsStatus.ErrorMessage);
 }

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<void>(getReceiveTwilioSmsStatusUrl(params),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', ...getHeaders(options?.headers) },
    body: formUrlEncoded
  }
);}





export const getReceiveTwilioSmsStatusMutationKey = () => ['receiveTwilioSmsStatus'] as const;

export const getReceiveTwilioSmsStatusMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof receiveTwilioSmsStatus>>, TError,ReceiveTwilioSmsStatusMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof receiveTwilioSmsStatus>>, TError,ReceiveTwilioSmsStatusMutationVariables, TContext> => {

const mutationKey = getReceiveTwilioSmsStatusMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof receiveTwilioSmsStatus>>, ReceiveTwilioSmsStatusMutationVariables> = (props) => {
          const {data,params} = props ?? {};

          return  receiveTwilioSmsStatus(data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReceiveTwilioSmsStatusMutationResult = NonNullable<Awaited<ReturnType<typeof receiveTwilioSmsStatus>>>
    export type ReceiveTwilioSmsStatusMutationBody = TwilioSmsStatus
    export type ReceiveTwilioSmsStatusMutationError = Error
    export type ReceiveTwilioSmsStatusMutationVariables = {data: TwilioSmsStatus;params: ReceiveTwilioSmsStatusParams}

    /**
 * @summary Accept a signed SMS delivery status callback
 */
export const useReceiveTwilioSmsStatus = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof receiveTwilioSmsStatus>>, TError,ReceiveTwilioSmsStatusMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof receiveTwilioSmsStatus>>,
        TError,
        ReceiveTwilioSmsStatusMutationVariables,
        TContext
      > => {
      return useMutation(getReceiveTwilioSmsStatusMutationOptions(options));
    }

export const getSendTestSmsUrl = () => {




  return `/api/sms/test`
}

export const sendTestSms = async (smsTestSend: SmsTestSend, options?: Parameters<typeof customFetch>[1]): Promise<SmsDeliveryJob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SmsDeliveryJob>(getSendTestSmsUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(smsTestSend)
  }
);}





export const getSendTestSmsMutationKey = () => ['sendTestSms'] as const;

export const getSendTestSmsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendTestSms>>, TError,SendTestSmsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof sendTestSms>>, TError,SendTestSmsMutationVariables, TContext> => {

const mutationKey = getSendTestSmsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof sendTestSms>>, SendTestSmsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  sendTestSms(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SendTestSmsMutationResult = NonNullable<Awaited<ReturnType<typeof sendTestSms>>>
    export type SendTestSmsMutationBody = SmsTestSend
    export type SendTestSmsMutationError = unknown
    export type SendTestSmsMutationVariables = {data: SmsTestSend}

    export const useSendTestSms = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendTestSms>>, TError,SendTestSmsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof sendTestSms>>,
        TError,
        SendTestSmsMutationVariables,
        TContext
      > => {
      return useMutation(getSendTestSmsMutationOptions(options));
    }

export const getUpdateSmsCampaignStepUrl = (campaignId: string,
    stepId: string,) => {




  return `/api/sms/campaigns/${campaignId}/steps/${stepId}`
}

export const updateSmsCampaignStep = async (campaignId: string,
    stepId: string,
    smsCampaignStepUpdate: SmsCampaignStepUpdate, options?: Parameters<typeof customFetch>[1]): Promise<CampaignStep> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CampaignStep>(getUpdateSmsCampaignStepUrl(campaignId,stepId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(smsCampaignStepUpdate)
  }
);}





export const getUpdateSmsCampaignStepMutationKey = () => ['updateSmsCampaignStep'] as const;

export const getUpdateSmsCampaignStepMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSmsCampaignStep>>, TError,UpdateSmsCampaignStepMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateSmsCampaignStep>>, TError,UpdateSmsCampaignStepMutationVariables, TContext> => {

const mutationKey = getUpdateSmsCampaignStepMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateSmsCampaignStep>>, UpdateSmsCampaignStepMutationVariables> = (props) => {
          const {campaignId,stepId,data} = props ?? {};

          return  updateSmsCampaignStep(campaignId,stepId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateSmsCampaignStepMutationResult = NonNullable<Awaited<ReturnType<typeof updateSmsCampaignStep>>>
    export type UpdateSmsCampaignStepMutationBody = SmsCampaignStepUpdate
    export type UpdateSmsCampaignStepMutationError = Error
    export type UpdateSmsCampaignStepMutationVariables = {campaignId: string;stepId: string;data: SmsCampaignStepUpdate}

    export const useUpdateSmsCampaignStep = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSmsCampaignStep>>, TError,UpdateSmsCampaignStepMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateSmsCampaignStep>>,
        TError,
        UpdateSmsCampaignStepMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateSmsCampaignStepMutationOptions(options));
    }

export const getPreviewSmsRecipientsUrl = (campaignId: string,
    stepId: string,
    params?: PreviewSmsRecipientsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/sms/campaigns/${campaignId}/steps/${stepId}/recipients?${stringifiedParams}` : `/api/sms/campaigns/${campaignId}/steps/${stepId}/recipients`
}

export const previewSmsRecipients = async (campaignId: string,
    stepId: string,
    params?: PreviewSmsRecipientsParams, options?: Parameters<typeof customFetch>[1]): Promise<SmsRecipientPreview> => {

  return customFetch<SmsRecipientPreview>(getPreviewSmsRecipientsUrl(campaignId,stepId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPreviewSmsRecipientsQueryKey = (campaignId: string,
    stepId: string,
    params?: PreviewSmsRecipientsParams,) => {
    return [
    `/api/sms/campaigns/${campaignId}/steps/${stepId}/recipients`, ...(params ? [params] : [])
    ] as const;
    }


export const getPreviewSmsRecipientsQueryOptions = <TData = Awaited<ReturnType<typeof previewSmsRecipients>>, TError = unknown>(campaignId: string,
    stepId: string,
    params?: PreviewSmsRecipientsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof previewSmsRecipients>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPreviewSmsRecipientsQueryKey(campaignId,stepId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof previewSmsRecipients>>> = ({ signal }) => previewSmsRecipients(campaignId,stepId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: campaignId !== null && campaignId !== undefined && stepId !== null && stepId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof previewSmsRecipients>>, TError, TData> & { queryKey: QueryKey }
}

export type PreviewSmsRecipientsQueryResult = NonNullable<Awaited<ReturnType<typeof previewSmsRecipients>>>
export type PreviewSmsRecipientsQueryError = unknown



export function usePreviewSmsRecipients<TData = Awaited<ReturnType<typeof previewSmsRecipients>>, TError = unknown>(
 campaignId: string,
    stepId: string,
    params?: PreviewSmsRecipientsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof previewSmsRecipients>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getPreviewSmsRecipientsQueryOptions(campaignId,stepId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSendSmsStepUrl = (campaignId: string,
    stepId: string,) => {




  return `/api/sms/campaigns/${campaignId}/steps/${stepId}/send`
}

export const sendSmsStep = async (campaignId: string,
    stepId: string, options?: Parameters<typeof customFetch>[1]): Promise<SmsSendResult> => {

  return customFetch<SmsSendResult>(getSendSmsStepUrl(campaignId,stepId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getSendSmsStepMutationKey = () => ['sendSmsStep'] as const;

export const getSendSmsStepMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendSmsStep>>, TError,SendSmsStepMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof sendSmsStep>>, TError,SendSmsStepMutationVariables, TContext> => {

const mutationKey = getSendSmsStepMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof sendSmsStep>>, SendSmsStepMutationVariables> = (props) => {
          const {campaignId,stepId} = props ?? {};

          return  sendSmsStep(campaignId,stepId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SendSmsStepMutationResult = NonNullable<Awaited<ReturnType<typeof sendSmsStep>>>

    export type SendSmsStepMutationError = unknown
    export type SendSmsStepMutationVariables = {campaignId: string;stepId: string}

    export const useSendSmsStep = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendSmsStep>>, TError,SendSmsStepMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof sendSmsStep>>,
        TError,
        SendSmsStepMutationVariables,
        TContext
      > => {
      return useMutation(getSendSmsStepMutationOptions(options));
    }

export const getListSmsDeliveriesUrl = (campaignId: string,
    params?: ListSmsDeliveriesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/sms/campaigns/${campaignId}/deliveries?${stringifiedParams}` : `/api/sms/campaigns/${campaignId}/deliveries`
}

export const listSmsDeliveries = async (campaignId: string,
    params?: ListSmsDeliveriesParams, options?: Parameters<typeof customFetch>[1]): Promise<SmsDeliveryJobList> => {

  return customFetch<SmsDeliveryJobList>(getListSmsDeliveriesUrl(campaignId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSmsDeliveriesQueryKey = (campaignId: string,
    params?: ListSmsDeliveriesParams,) => {
    return [
    `/api/sms/campaigns/${campaignId}/deliveries`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSmsDeliveriesQueryOptions = <TData = Awaited<ReturnType<typeof listSmsDeliveries>>, TError = unknown>(campaignId: string,
    params?: ListSmsDeliveriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSmsDeliveries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSmsDeliveriesQueryKey(campaignId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSmsDeliveries>>> = ({ signal }) => listSmsDeliveries(campaignId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: campaignId !== null && campaignId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSmsDeliveries>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSmsDeliveriesQueryResult = NonNullable<Awaited<ReturnType<typeof listSmsDeliveries>>>
export type ListSmsDeliveriesQueryError = unknown



export function useListSmsDeliveries<TData = Awaited<ReturnType<typeof listSmsDeliveries>>, TError = unknown>(
 campaignId: string,
    params?: ListSmsDeliveriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSmsDeliveries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSmsDeliveriesQueryOptions(campaignId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetSmsCampaignSummaryUrl = (campaignId: string,) => {




  return `/api/sms/campaigns/${campaignId}/summary`
}

export const getSmsCampaignSummary = async (campaignId: string, options?: Parameters<typeof customFetch>[1]): Promise<SmsCampaignSummary> => {

  return customFetch<SmsCampaignSummary>(getGetSmsCampaignSummaryUrl(campaignId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSmsCampaignSummaryQueryKey = (campaignId: string,) => {
    return [
    `/api/sms/campaigns/${campaignId}/summary`
    ] as const;
    }


export const getGetSmsCampaignSummaryQueryOptions = <TData = Awaited<ReturnType<typeof getSmsCampaignSummary>>, TError = unknown>(campaignId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSmsCampaignSummary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSmsCampaignSummaryQueryKey(campaignId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSmsCampaignSummary>>> = ({ signal }) => getSmsCampaignSummary(campaignId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: campaignId !== null && campaignId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSmsCampaignSummary>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSmsCampaignSummaryQueryResult = NonNullable<Awaited<ReturnType<typeof getSmsCampaignSummary>>>
export type GetSmsCampaignSummaryQueryError = unknown



export function useGetSmsCampaignSummary<TData = Awaited<ReturnType<typeof getSmsCampaignSummary>>, TError = unknown>(
 campaignId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSmsCampaignSummary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSmsCampaignSummaryQueryOptions(campaignId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRetrySmsDeliveryUrl = (id: string,) => {




  return `/api/sms/deliveries/${id}/retry`
}

export const retrySmsDelivery = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SmsDeliveryJob> => {

  return customFetch<SmsDeliveryJob>(getRetrySmsDeliveryUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRetrySmsDeliveryMutationKey = () => ['retrySmsDelivery'] as const;

export const getRetrySmsDeliveryMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retrySmsDelivery>>, TError,RetrySmsDeliveryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof retrySmsDelivery>>, TError,RetrySmsDeliveryMutationVariables, TContext> => {

const mutationKey = getRetrySmsDeliveryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof retrySmsDelivery>>, RetrySmsDeliveryMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  retrySmsDelivery(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RetrySmsDeliveryMutationResult = NonNullable<Awaited<ReturnType<typeof retrySmsDelivery>>>

    export type RetrySmsDeliveryMutationError = unknown
    export type RetrySmsDeliveryMutationVariables = {id: string}

    export const useRetrySmsDelivery = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retrySmsDelivery>>, TError,RetrySmsDeliveryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof retrySmsDelivery>>,
        TError,
        RetrySmsDeliveryMutationVariables,
        TContext
      > => {
      return useMutation(getRetrySmsDeliveryMutationOptions(options));
    }

export const getGetContactSmsHistoryUrl = (contactId: string,
    params?: GetContactSmsHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/sms/contacts/${contactId}/history?${stringifiedParams}` : `/api/sms/contacts/${contactId}/history`
}

export const getContactSmsHistory = async (contactId: string,
    params?: GetContactSmsHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<SmsHistoryList> => {

  return customFetch<SmsHistoryList>(getGetContactSmsHistoryUrl(contactId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetContactSmsHistoryQueryKey = (contactId: string,
    params?: GetContactSmsHistoryParams,) => {
    return [
    `/api/sms/contacts/${contactId}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetContactSmsHistoryQueryOptions = <TData = Awaited<ReturnType<typeof getContactSmsHistory>>, TError = unknown>(contactId: string,
    params?: GetContactSmsHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContactSmsHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetContactSmsHistoryQueryKey(contactId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getContactSmsHistory>>> = ({ signal }) => getContactSmsHistory(contactId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: contactId !== null && contactId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getContactSmsHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type GetContactSmsHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof getContactSmsHistory>>>
export type GetContactSmsHistoryQueryError = unknown



export function useGetContactSmsHistory<TData = Awaited<ReturnType<typeof getContactSmsHistory>>, TError = unknown>(
 contactId: string,
    params?: GetContactSmsHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getContactSmsHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetContactSmsHistoryQueryOptions(contactId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListEmailMessagesUrl = () => {




  return `/api/email/messages`
}

export const listEmailMessages = async ( options?: Parameters<typeof customFetch>[1]): Promise<ListEmailMessages200> => {

  return customFetch<ListEmailMessages200>(getListEmailMessagesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListEmailMessagesQueryKey = () => {
    return [
    `/api/email/messages`
    ] as const;
    }


export const getListEmailMessagesQueryOptions = <TData = Awaited<ReturnType<typeof listEmailMessages>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEmailMessages>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEmailMessagesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEmailMessages>>> = ({ signal }) => listEmailMessages({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEmailMessages>>, TError, TData> & { queryKey: QueryKey }
}

export type ListEmailMessagesQueryResult = NonNullable<Awaited<ReturnType<typeof listEmailMessages>>>
export type ListEmailMessagesQueryError = unknown



export function useListEmailMessages<TData = Awaited<ReturnType<typeof listEmailMessages>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listEmailMessages>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListEmailMessagesQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSyncEmailRepliesUrl = () => {




  return `/api/email/messages/sync`
}

export const syncEmailReplies = async ( options?: Parameters<typeof customFetch>[1]): Promise<SyncEmailReplies200> => {

  return customFetch<SyncEmailReplies200>(getSyncEmailRepliesUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getSyncEmailRepliesMutationKey = () => ['syncEmailReplies'] as const;

export const getSyncEmailRepliesMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof syncEmailReplies>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof syncEmailReplies>>, TError,void, TContext> => {

const mutationKey = getSyncEmailRepliesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof syncEmailReplies>>, void> = () => {


          return  syncEmailReplies(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SyncEmailRepliesMutationResult = NonNullable<Awaited<ReturnType<typeof syncEmailReplies>>>

    export type SyncEmailRepliesMutationError = unknown


    export const useSyncEmailReplies = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof syncEmailReplies>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof syncEmailReplies>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getSyncEmailRepliesMutationOptions(options));
    }

export const getDraftEmailWithAiUrl = () => {




  return `/api/email/ai/draft`
}

export const draftEmailWithAi = async (emailAiDraftRequest: EmailAiDraftRequest, options?: Parameters<typeof customFetch>[1]): Promise<EmailAiDraftResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EmailAiDraftResponse>(getDraftEmailWithAiUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(emailAiDraftRequest)
  }
);}





export const getDraftEmailWithAiMutationKey = () => ['draftEmailWithAi'] as const;

export const getDraftEmailWithAiMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof draftEmailWithAi>>, TError,DraftEmailWithAiMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof draftEmailWithAi>>, TError,DraftEmailWithAiMutationVariables, TContext> => {

const mutationKey = getDraftEmailWithAiMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof draftEmailWithAi>>, DraftEmailWithAiMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  draftEmailWithAi(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DraftEmailWithAiMutationResult = NonNullable<Awaited<ReturnType<typeof draftEmailWithAi>>>
    export type DraftEmailWithAiMutationBody = EmailAiDraftRequest
    export type DraftEmailWithAiMutationError = unknown
    export type DraftEmailWithAiMutationVariables = {data: EmailAiDraftRequest}

    export const useDraftEmailWithAi = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof draftEmailWithAi>>, TError,DraftEmailWithAiMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof draftEmailWithAi>>,
        TError,
        DraftEmailWithAiMutationVariables,
        TContext
      > => {
      return useMutation(getDraftEmailWithAiMutationOptions(options));
    }

export const getSuggestEmailReplyUrl = (id: string,) => {




  return `/api/email/messages/${id}/suggest-reply`
}

export const suggestEmailReply = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SuggestEmailReply200> => {

  return customFetch<SuggestEmailReply200>(getSuggestEmailReplyUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getSuggestEmailReplyMutationKey = () => ['suggestEmailReply'] as const;

export const getSuggestEmailReplyMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof suggestEmailReply>>, TError,SuggestEmailReplyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof suggestEmailReply>>, TError,SuggestEmailReplyMutationVariables, TContext> => {

const mutationKey = getSuggestEmailReplyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof suggestEmailReply>>, SuggestEmailReplyMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  suggestEmailReply(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SuggestEmailReplyMutationResult = NonNullable<Awaited<ReturnType<typeof suggestEmailReply>>>

    export type SuggestEmailReplyMutationError = unknown
    export type SuggestEmailReplyMutationVariables = {id: string}

    export const useSuggestEmailReply = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof suggestEmailReply>>, TError,SuggestEmailReplyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof suggestEmailReply>>,
        TError,
        SuggestEmailReplyMutationVariables,
        TContext
      > => {
      return useMutation(getSuggestEmailReplyMutationOptions(options));
    }

export const getListCommunicationExtractionsUrl = () => {




  return `/api/email/extractions`
}

export const listCommunicationExtractions = async ( options?: Parameters<typeof customFetch>[1]): Promise<ListCommunicationExtractions200> => {

  return customFetch<ListCommunicationExtractions200>(getListCommunicationExtractionsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCommunicationExtractionsQueryKey = () => {
    return [
    `/api/email/extractions`
    ] as const;
    }


export const getListCommunicationExtractionsQueryOptions = <TData = Awaited<ReturnType<typeof listCommunicationExtractions>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCommunicationExtractions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCommunicationExtractionsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCommunicationExtractions>>> = ({ signal }) => listCommunicationExtractions({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCommunicationExtractions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCommunicationExtractionsQueryResult = NonNullable<Awaited<ReturnType<typeof listCommunicationExtractions>>>
export type ListCommunicationExtractionsQueryError = unknown



export function useListCommunicationExtractions<TData = Awaited<ReturnType<typeof listCommunicationExtractions>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCommunicationExtractions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCommunicationExtractionsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewCommunicationExtractionUrl = (id: string,) => {




  return `/api/email/extractions/${id}/review`
}

export const reviewCommunicationExtraction = async (id: string,
    reviewCommunicationExtractionBody: ReviewCommunicationExtractionBody, options?: Parameters<typeof customFetch>[1]): Promise<CommunicationExtraction> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CommunicationExtraction>(getReviewCommunicationExtractionUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewCommunicationExtractionBody)
  }
);}





export const getReviewCommunicationExtractionMutationKey = () => ['reviewCommunicationExtraction'] as const;

export const getReviewCommunicationExtractionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewCommunicationExtraction>>, TError,ReviewCommunicationExtractionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewCommunicationExtraction>>, TError,ReviewCommunicationExtractionMutationVariables, TContext> => {

const mutationKey = getReviewCommunicationExtractionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewCommunicationExtraction>>, ReviewCommunicationExtractionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reviewCommunicationExtraction(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewCommunicationExtractionMutationResult = NonNullable<Awaited<ReturnType<typeof reviewCommunicationExtraction>>>
    export type ReviewCommunicationExtractionMutationBody = ReviewCommunicationExtractionBody
    export type ReviewCommunicationExtractionMutationError = unknown
    export type ReviewCommunicationExtractionMutationVariables = {id: string;data: ReviewCommunicationExtractionBody}

    export const useReviewCommunicationExtraction = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewCommunicationExtraction>>, TError,ReviewCommunicationExtractionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewCommunicationExtraction>>,
        TError,
        ReviewCommunicationExtractionMutationVariables,
        TContext
      > => {
      return useMutation(getReviewCommunicationExtractionMutationOptions(options));
    }

export const getTrackEmailOpenUrl = (token: string,) => {




  return `/api/email/track/open/${token}.gif`
}

export const trackEmailOpen = async (token: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getTrackEmailOpenUrl(token),
  {
    ...options,
    method: 'GET'


  }
);}





export const getTrackEmailOpenQueryKey = (token: string,) => {
    return [
    `/api/email/track/open/${token}.gif`
    ] as const;
    }


export const getTrackEmailOpenQueryOptions = <TData = Awaited<ReturnType<typeof trackEmailOpen>>, TError = unknown>(token: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof trackEmailOpen>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getTrackEmailOpenQueryKey(token);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof trackEmailOpen>>> = ({ signal }) => trackEmailOpen(token, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: token !== null && token !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof trackEmailOpen>>, TError, TData> & { queryKey: QueryKey }
}

export type TrackEmailOpenQueryResult = NonNullable<Awaited<ReturnType<typeof trackEmailOpen>>>
export type TrackEmailOpenQueryError = unknown



export function useTrackEmailOpen<TData = Awaited<ReturnType<typeof trackEmailOpen>>, TError = unknown>(
 token: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof trackEmailOpen>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getTrackEmailOpenQueryOptions(token,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getTrackEmailClickUrl = (token: string,
    params: TrackEmailClickParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/email/track/click/${token}?${stringifiedParams}` : `/api/email/track/click/${token}`
}

/**
 * Redirects only to the http(s) URL recorded in the tracked message body. Unknown tracking tokens and destinations that are not recorded for the token return the same 404 response and never redirect.
 * @summary Record a tracked click and redirect to the recorded destination
 */
export const trackEmailClick = async (token: string,
    params: TrackEmailClickParams, options?: Parameters<typeof customFetch>[1]): Promise<unknown> => {

  return customFetch<unknown>(getTrackEmailClickUrl(token,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getTrackEmailClickQueryKey = (token: string,
    params?: TrackEmailClickParams,) => {
    return [
    `/api/email/track/click/${token}`, ...(params ? [params] : [])
    ] as const;
    }


export const getTrackEmailClickQueryOptions = <TData = Awaited<ReturnType<typeof trackEmailClick>>, TError = void | Error>(token: string,
    params: TrackEmailClickParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof trackEmailClick>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getTrackEmailClickQueryKey(token,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof trackEmailClick>>> = ({ signal }) => trackEmailClick(token,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: token !== null && token !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof trackEmailClick>>, TError, TData> & { queryKey: QueryKey }
}

export type TrackEmailClickQueryResult = NonNullable<Awaited<ReturnType<typeof trackEmailClick>>>
export type TrackEmailClickQueryError = void | Error


/**
 * @summary Record a tracked click and redirect to the recorded destination
 */

export function useTrackEmailClick<TData = Awaited<ReturnType<typeof trackEmailClick>>, TError = void | Error>(
 token: string,
    params: TrackEmailClickParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof trackEmailClick>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getTrackEmailClickQueryOptions(token,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUnsubscribeEmailUrl = (token: string,) => {




  return `/api/email/unsubscribe/${token}`
}

export const unsubscribeEmail = async (token: string, options?: Parameters<typeof customFetch>[1]): Promise<string> => {

  return customFetch<string>(getUnsubscribeEmailUrl(token),
  {
    ...options,
    method: 'GET'


  }
);}





export const getUnsubscribeEmailQueryKey = (token: string,) => {
    return [
    `/api/email/unsubscribe/${token}`
    ] as const;
    }


export const getUnsubscribeEmailQueryOptions = <TData = Awaited<ReturnType<typeof unsubscribeEmail>>, TError = unknown>(token: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof unsubscribeEmail>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getUnsubscribeEmailQueryKey(token);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof unsubscribeEmail>>> = ({ signal }) => unsubscribeEmail(token, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: token !== null && token !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof unsubscribeEmail>>, TError, TData> & { queryKey: QueryKey }
}

export type UnsubscribeEmailQueryResult = NonNullable<Awaited<ReturnType<typeof unsubscribeEmail>>>
export type UnsubscribeEmailQueryError = unknown



export function useUnsubscribeEmail<TData = Awaited<ReturnType<typeof unsubscribeEmail>>, TError = unknown>(
 token: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof unsubscribeEmail>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getUnsubscribeEmailQueryOptions(token,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListMailboxIntakeConnectionsUrl = (params?: ListMailboxIntakeConnectionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/email/intake/connections?${stringifiedParams}` : `/api/email/intake/connections`
}

/**
 * @summary List safe Microsoft 365 intake connection readiness
 */
export const listMailboxIntakeConnections = async (params?: ListMailboxIntakeConnectionsParams, options?: Parameters<typeof customFetch>[1]): Promise<MailboxIntakeConnectionList> => {

  return customFetch<MailboxIntakeConnectionList>(getListMailboxIntakeConnectionsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMailboxIntakeConnectionsQueryKey = (params?: ListMailboxIntakeConnectionsParams,) => {
    return [
    `/api/email/intake/connections`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMailboxIntakeConnectionsQueryOptions = <TData = Awaited<ReturnType<typeof listMailboxIntakeConnections>>, TError = Error>(params?: ListMailboxIntakeConnectionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMailboxIntakeConnections>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMailboxIntakeConnectionsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMailboxIntakeConnections>>> = ({ signal }) => listMailboxIntakeConnections(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMailboxIntakeConnections>>, TError, TData> & { queryKey: QueryKey }
}

export type ListMailboxIntakeConnectionsQueryResult = NonNullable<Awaited<ReturnType<typeof listMailboxIntakeConnections>>>
export type ListMailboxIntakeConnectionsQueryError = Error


/**
 * @summary List safe Microsoft 365 intake connection readiness
 */

export function useListMailboxIntakeConnections<TData = Awaited<ReturnType<typeof listMailboxIntakeConnections>>, TError = Error>(
 params?: ListMailboxIntakeConnectionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMailboxIntakeConnections>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListMailboxIntakeConnectionsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateMailboxIntakeConnectionUrl = () => {




  return `/api/email/intake/connections`
}

/**
 * @summary Register the current delegated mailbox identity or the server-owned shared-mailbox application identity
 */
export const createMailboxIntakeConnection = async (mailboxIntakeConnectionCreate: MailboxIntakeConnectionCreate, options?: Parameters<typeof customFetch>[1]): Promise<MailboxIntakeConnection> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MailboxIntakeConnection>(getCreateMailboxIntakeConnectionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(mailboxIntakeConnectionCreate)
  }
);}





export const getCreateMailboxIntakeConnectionMutationKey = () => ['createMailboxIntakeConnection'] as const;

export const getCreateMailboxIntakeConnectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createMailboxIntakeConnection>>, TError,CreateMailboxIntakeConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createMailboxIntakeConnection>>, TError,CreateMailboxIntakeConnectionMutationVariables, TContext> => {

const mutationKey = getCreateMailboxIntakeConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createMailboxIntakeConnection>>, CreateMailboxIntakeConnectionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createMailboxIntakeConnection(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateMailboxIntakeConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof createMailboxIntakeConnection>>>
    export type CreateMailboxIntakeConnectionMutationBody = MailboxIntakeConnectionCreate
    export type CreateMailboxIntakeConnectionMutationError = Error
    export type CreateMailboxIntakeConnectionMutationVariables = {data: MailboxIntakeConnectionCreate}

    /**
 * @summary Register the current delegated mailbox identity or the server-owned shared-mailbox application identity
 */
export const useCreateMailboxIntakeConnection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createMailboxIntakeConnection>>, TError,CreateMailboxIntakeConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createMailboxIntakeConnection>>,
        TError,
        CreateMailboxIntakeConnectionMutationVariables,
        TContext
      > => {
      return useMutation(getCreateMailboxIntakeConnectionMutationOptions(options));
    }

export const getVerifyMailboxIntakeConnectionUrl = (connectionId: string,) => {




  return `/api/email/intake/connections/${connectionId}/verify`
}

/**
 * @summary Recheck the selected non-secret connection identity and consent
 */
export const verifyMailboxIntakeConnection = async (connectionId: string, options?: Parameters<typeof customFetch>[1]): Promise<MailboxIntakeConnection> => {

  return customFetch<MailboxIntakeConnection>(getVerifyMailboxIntakeConnectionUrl(connectionId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getVerifyMailboxIntakeConnectionMutationKey = () => ['verifyMailboxIntakeConnection'] as const;

export const getVerifyMailboxIntakeConnectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof verifyMailboxIntakeConnection>>, TError,VerifyMailboxIntakeConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof verifyMailboxIntakeConnection>>, TError,VerifyMailboxIntakeConnectionMutationVariables, TContext> => {

const mutationKey = getVerifyMailboxIntakeConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof verifyMailboxIntakeConnection>>, VerifyMailboxIntakeConnectionMutationVariables> = (props) => {
          const {connectionId} = props ?? {};

          return  verifyMailboxIntakeConnection(connectionId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type VerifyMailboxIntakeConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof verifyMailboxIntakeConnection>>>

    export type VerifyMailboxIntakeConnectionMutationError = Error
    export type VerifyMailboxIntakeConnectionMutationVariables = {connectionId: string}

    /**
 * @summary Recheck the selected non-secret connection identity and consent
 */
export const useVerifyMailboxIntakeConnection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof verifyMailboxIntakeConnection>>, TError,VerifyMailboxIntakeConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof verifyMailboxIntakeConnection>>,
        TError,
        VerifyMailboxIntakeConnectionMutationVariables,
        TContext
      > => {
      return useMutation(getVerifyMailboxIntakeConnectionMutationOptions(options));
    }

export const getDisconnectMailboxIntakeConnectionUrl = (connectionId: string,) => {




  return `/api/email/intake/connections/${connectionId}/disconnect`
}

/**
 * @summary Stop intake for every mailbox using the selected connection
 */
export const disconnectMailboxIntakeConnection = async (connectionId: string,
    mailboxIntakeDisconnect: MailboxIntakeDisconnect, options?: Parameters<typeof customFetch>[1]): Promise<MailboxIntakeConnection> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MailboxIntakeConnection>(getDisconnectMailboxIntakeConnectionUrl(connectionId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(mailboxIntakeDisconnect)
  }
);}





export const getDisconnectMailboxIntakeConnectionMutationKey = () => ['disconnectMailboxIntakeConnection'] as const;

export const getDisconnectMailboxIntakeConnectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectMailboxIntakeConnection>>, TError,DisconnectMailboxIntakeConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof disconnectMailboxIntakeConnection>>, TError,DisconnectMailboxIntakeConnectionMutationVariables, TContext> => {

const mutationKey = getDisconnectMailboxIntakeConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof disconnectMailboxIntakeConnection>>, DisconnectMailboxIntakeConnectionMutationVariables> = (props) => {
          const {connectionId,data} = props ?? {};

          return  disconnectMailboxIntakeConnection(connectionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DisconnectMailboxIntakeConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof disconnectMailboxIntakeConnection>>>
    export type DisconnectMailboxIntakeConnectionMutationBody = MailboxIntakeDisconnect
    export type DisconnectMailboxIntakeConnectionMutationError = Error
    export type DisconnectMailboxIntakeConnectionMutationVariables = {connectionId: string;data: MailboxIntakeDisconnect}

    /**
 * @summary Stop intake for every mailbox using the selected connection
 */
export const useDisconnectMailboxIntakeConnection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectMailboxIntakeConnection>>, TError,DisconnectMailboxIntakeConnectionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof disconnectMailboxIntakeConnection>>,
        TError,
        DisconnectMailboxIntakeConnectionMutationVariables,
        TContext
      > => {
      return useMutation(getDisconnectMailboxIntakeConnectionMutationOptions(options));
    }

export const getReceiveMailboxGraphNotificationsUrl = (params?: ReceiveMailboxGraphNotificationsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/email/intake/graph/notifications?${stringifiedParams}` : `/api/email/intake/graph/notifications`
}

/**
 * @summary Validate Microsoft Graph endpoint challenges and queue authenticated subscription hints
 */
export const receiveMailboxGraphNotifications = async (mailboxGraphNotificationBatch?: MailboxGraphNotificationBatch,
    params?: ReceiveMailboxGraphNotificationsParams, options?: Parameters<typeof customFetch>[1]): Promise<string | MailboxGraphNotificationReceipt> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<string | MailboxGraphNotificationReceipt>(getReceiveMailboxGraphNotificationsUrl(params),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(mailboxGraphNotificationBatch)
  }
);}





export const getReceiveMailboxGraphNotificationsMutationKey = () => ['receiveMailboxGraphNotifications'] as const;

export const getReceiveMailboxGraphNotificationsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof receiveMailboxGraphNotifications>>, TError,ReceiveMailboxGraphNotificationsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof receiveMailboxGraphNotifications>>, TError,ReceiveMailboxGraphNotificationsMutationVariables, TContext> => {

const mutationKey = getReceiveMailboxGraphNotificationsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof receiveMailboxGraphNotifications>>, ReceiveMailboxGraphNotificationsMutationVariables> = (props) => {
          const {data,params} = props ?? {};

          return  receiveMailboxGraphNotifications(data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReceiveMailboxGraphNotificationsMutationResult = NonNullable<Awaited<ReturnType<typeof receiveMailboxGraphNotifications>>>
    export type ReceiveMailboxGraphNotificationsMutationBody = MailboxGraphNotificationBatch | undefined
    export type ReceiveMailboxGraphNotificationsMutationError = Error
    export type ReceiveMailboxGraphNotificationsMutationVariables = {data?: MailboxGraphNotificationBatch;params?: ReceiveMailboxGraphNotificationsParams}

    /**
 * @summary Validate Microsoft Graph endpoint challenges and queue authenticated subscription hints
 */
export const useReceiveMailboxGraphNotifications = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof receiveMailboxGraphNotifications>>, TError,ReceiveMailboxGraphNotificationsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof receiveMailboxGraphNotifications>>,
        TError,
        ReceiveMailboxGraphNotificationsMutationVariables,
        TContext
      > => {
      return useMutation(getReceiveMailboxGraphNotificationsMutationOptions(options));
    }

export const getListMailboxIntakeMailboxesUrl = (params?: ListMailboxIntakeMailboxesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/email/intake/mailboxes?${stringifiedParams}` : `/api/email/intake/mailboxes`
}

/**
 * @summary List configured inbound mailboxes and truthful readiness
 */
export const listMailboxIntakeMailboxes = async (params?: ListMailboxIntakeMailboxesParams, options?: Parameters<typeof customFetch>[1]): Promise<MailboxIntakeMailboxList> => {

  return customFetch<MailboxIntakeMailboxList>(getListMailboxIntakeMailboxesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMailboxIntakeMailboxesQueryKey = (params?: ListMailboxIntakeMailboxesParams,) => {
    return [
    `/api/email/intake/mailboxes`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMailboxIntakeMailboxesQueryOptions = <TData = Awaited<ReturnType<typeof listMailboxIntakeMailboxes>>, TError = Error>(params?: ListMailboxIntakeMailboxesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMailboxIntakeMailboxes>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMailboxIntakeMailboxesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMailboxIntakeMailboxes>>> = ({ signal }) => listMailboxIntakeMailboxes(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMailboxIntakeMailboxes>>, TError, TData> & { queryKey: QueryKey }
}

export type ListMailboxIntakeMailboxesQueryResult = NonNullable<Awaited<ReturnType<typeof listMailboxIntakeMailboxes>>>
export type ListMailboxIntakeMailboxesQueryError = Error


/**
 * @summary List configured inbound mailboxes and truthful readiness
 */

export function useListMailboxIntakeMailboxes<TData = Awaited<ReturnType<typeof listMailboxIntakeMailboxes>>, TError = Error>(
 params?: ListMailboxIntakeMailboxesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMailboxIntakeMailboxes>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListMailboxIntakeMailboxesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateMailboxIntakeMailboxUrl = () => {




  return `/api/email/intake/mailboxes`
}

/**
 * @summary Add an inactive individual or shared mailbox configuration
 */
export const createMailboxIntakeMailbox = async (mailboxIntakeMailboxCreate: MailboxIntakeMailboxCreate, options?: Parameters<typeof customFetch>[1]): Promise<MailboxIntakeMailbox> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MailboxIntakeMailbox>(getCreateMailboxIntakeMailboxUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(mailboxIntakeMailboxCreate)
  }
);}





export const getCreateMailboxIntakeMailboxMutationKey = () => ['createMailboxIntakeMailbox'] as const;

export const getCreateMailboxIntakeMailboxMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createMailboxIntakeMailbox>>, TError,CreateMailboxIntakeMailboxMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createMailboxIntakeMailbox>>, TError,CreateMailboxIntakeMailboxMutationVariables, TContext> => {

const mutationKey = getCreateMailboxIntakeMailboxMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createMailboxIntakeMailbox>>, CreateMailboxIntakeMailboxMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createMailboxIntakeMailbox(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateMailboxIntakeMailboxMutationResult = NonNullable<Awaited<ReturnType<typeof createMailboxIntakeMailbox>>>
    export type CreateMailboxIntakeMailboxMutationBody = MailboxIntakeMailboxCreate
    export type CreateMailboxIntakeMailboxMutationError = Error
    export type CreateMailboxIntakeMailboxMutationVariables = {data: MailboxIntakeMailboxCreate}

    /**
 * @summary Add an inactive individual or shared mailbox configuration
 */
export const useCreateMailboxIntakeMailbox = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createMailboxIntakeMailbox>>, TError,CreateMailboxIntakeMailboxMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createMailboxIntakeMailbox>>,
        TError,
        CreateMailboxIntakeMailboxMutationVariables,
        TContext
      > => {
      return useMutation(getCreateMailboxIntakeMailboxMutationOptions(options));
    }

export const getGetMailboxIntakeMailboxUrl = (mailboxId: string,) => {




  return `/api/email/intake/mailboxes/${mailboxId}`
}

export const getMailboxIntakeMailbox = async (mailboxId: string, options?: Parameters<typeof customFetch>[1]): Promise<MailboxIntakeMailbox> => {

  return customFetch<MailboxIntakeMailbox>(getGetMailboxIntakeMailboxUrl(mailboxId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMailboxIntakeMailboxQueryKey = (mailboxId: string,) => {
    return [
    `/api/email/intake/mailboxes/${mailboxId}`
    ] as const;
    }


export const getGetMailboxIntakeMailboxQueryOptions = <TData = Awaited<ReturnType<typeof getMailboxIntakeMailbox>>, TError = Error>(mailboxId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMailboxIntakeMailbox>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMailboxIntakeMailboxQueryKey(mailboxId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMailboxIntakeMailbox>>> = ({ signal }) => getMailboxIntakeMailbox(mailboxId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: mailboxId !== null && mailboxId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMailboxIntakeMailbox>>, TError, TData> & { queryKey: QueryKey }
}

export type GetMailboxIntakeMailboxQueryResult = NonNullable<Awaited<ReturnType<typeof getMailboxIntakeMailbox>>>
export type GetMailboxIntakeMailboxQueryError = Error



export function useGetMailboxIntakeMailbox<TData = Awaited<ReturnType<typeof getMailboxIntakeMailbox>>, TError = Error>(
 mailboxId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMailboxIntakeMailbox>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetMailboxIntakeMailboxQueryOptions(mailboxId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateMailboxIntakeMailboxUrl = (mailboxId: string,) => {




  return `/api/email/intake/mailboxes/${mailboxId}`
}

export const updateMailboxIntakeMailbox = async (mailboxId: string,
    mailboxIntakeMailboxUpdate: MailboxIntakeMailboxUpdate, options?: Parameters<typeof customFetch>[1]): Promise<MailboxIntakeMailbox> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MailboxIntakeMailbox>(getUpdateMailboxIntakeMailboxUrl(mailboxId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(mailboxIntakeMailboxUpdate)
  }
);}





export const getUpdateMailboxIntakeMailboxMutationKey = () => ['updateMailboxIntakeMailbox'] as const;

export const getUpdateMailboxIntakeMailboxMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateMailboxIntakeMailbox>>, TError,UpdateMailboxIntakeMailboxMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateMailboxIntakeMailbox>>, TError,UpdateMailboxIntakeMailboxMutationVariables, TContext> => {

const mutationKey = getUpdateMailboxIntakeMailboxMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateMailboxIntakeMailbox>>, UpdateMailboxIntakeMailboxMutationVariables> = (props) => {
          const {mailboxId,data} = props ?? {};

          return  updateMailboxIntakeMailbox(mailboxId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateMailboxIntakeMailboxMutationResult = NonNullable<Awaited<ReturnType<typeof updateMailboxIntakeMailbox>>>
    export type UpdateMailboxIntakeMailboxMutationBody = MailboxIntakeMailboxUpdate
    export type UpdateMailboxIntakeMailboxMutationError = Error
    export type UpdateMailboxIntakeMailboxMutationVariables = {mailboxId: string;data: MailboxIntakeMailboxUpdate}

    export const useUpdateMailboxIntakeMailbox = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateMailboxIntakeMailbox>>, TError,UpdateMailboxIntakeMailboxMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateMailboxIntakeMailbox>>,
        TError,
        UpdateMailboxIntakeMailboxMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateMailboxIntakeMailboxMutationOptions(options));
    }

export const getDownloadMailboxAttachmentUrl = (attachmentId: string,) => {




  return `/api/email/intake/attachments/${attachmentId}/content`
}

/**
 * @summary Download one private source attachment in the active entity
 */
export const downloadMailboxAttachment = async (attachmentId: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadMailboxAttachmentUrl(attachmentId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadMailboxAttachmentQueryKey = (attachmentId: string,) => {
    return [
    `/api/email/intake/attachments/${attachmentId}/content`
    ] as const;
    }


export const getDownloadMailboxAttachmentQueryOptions = <TData = Awaited<ReturnType<typeof downloadMailboxAttachment>>, TError = Error>(attachmentId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadMailboxAttachment>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadMailboxAttachmentQueryKey(attachmentId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadMailboxAttachment>>> = ({ signal }) => downloadMailboxAttachment(attachmentId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: attachmentId !== null && attachmentId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadMailboxAttachment>>, TError, TData> & { queryKey: QueryKey }
}

export type DownloadMailboxAttachmentQueryResult = NonNullable<Awaited<ReturnType<typeof downloadMailboxAttachment>>>
export type DownloadMailboxAttachmentQueryError = Error


/**
 * @summary Download one private source attachment in the active entity
 */

export function useDownloadMailboxAttachment<TData = Awaited<ReturnType<typeof downloadMailboxAttachment>>, TError = Error>(
 attachmentId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof downloadMailboxAttachment>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getDownloadMailboxAttachmentQueryOptions(attachmentId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListMailboxRoutingPromptRevisionsUrl = (mailboxId: string,
    params?: ListMailboxRoutingPromptRevisionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/email/intake/mailboxes/${mailboxId}/prompts?${stringifiedParams}` : `/api/email/intake/mailboxes/${mailboxId}/prompts`
}

export const listMailboxRoutingPromptRevisions = async (mailboxId: string,
    params?: ListMailboxRoutingPromptRevisionsParams, options?: Parameters<typeof customFetch>[1]): Promise<MailboxRoutingPromptRevisionList> => {

  return customFetch<MailboxRoutingPromptRevisionList>(getListMailboxRoutingPromptRevisionsUrl(mailboxId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMailboxRoutingPromptRevisionsQueryKey = (mailboxId: string,
    params?: ListMailboxRoutingPromptRevisionsParams,) => {
    return [
    `/api/email/intake/mailboxes/${mailboxId}/prompts`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMailboxRoutingPromptRevisionsQueryOptions = <TData = Awaited<ReturnType<typeof listMailboxRoutingPromptRevisions>>, TError = unknown>(mailboxId: string,
    params?: ListMailboxRoutingPromptRevisionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMailboxRoutingPromptRevisions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMailboxRoutingPromptRevisionsQueryKey(mailboxId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMailboxRoutingPromptRevisions>>> = ({ signal }) => listMailboxRoutingPromptRevisions(mailboxId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: mailboxId !== null && mailboxId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMailboxRoutingPromptRevisions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListMailboxRoutingPromptRevisionsQueryResult = NonNullable<Awaited<ReturnType<typeof listMailboxRoutingPromptRevisions>>>
export type ListMailboxRoutingPromptRevisionsQueryError = unknown



export function useListMailboxRoutingPromptRevisions<TData = Awaited<ReturnType<typeof listMailboxRoutingPromptRevisions>>, TError = unknown>(
 mailboxId: string,
    params?: ListMailboxRoutingPromptRevisionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMailboxRoutingPromptRevisions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListMailboxRoutingPromptRevisionsQueryOptions(mailboxId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateMailboxRoutingPromptRevisionUrl = (mailboxId: string,) => {




  return `/api/email/intake/mailboxes/${mailboxId}/prompts`
}

export const createMailboxRoutingPromptRevision = async (mailboxId: string,
    mailboxRoutingPromptRevisionCreate: MailboxRoutingPromptRevisionCreate, options?: Parameters<typeof customFetch>[1]): Promise<MailboxRoutingPromptRevision> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MailboxRoutingPromptRevision>(getCreateMailboxRoutingPromptRevisionUrl(mailboxId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(mailboxRoutingPromptRevisionCreate)
  }
);}





export const getCreateMailboxRoutingPromptRevisionMutationKey = () => ['createMailboxRoutingPromptRevision'] as const;

export const getCreateMailboxRoutingPromptRevisionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createMailboxRoutingPromptRevision>>, TError,CreateMailboxRoutingPromptRevisionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createMailboxRoutingPromptRevision>>, TError,CreateMailboxRoutingPromptRevisionMutationVariables, TContext> => {

const mutationKey = getCreateMailboxRoutingPromptRevisionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createMailboxRoutingPromptRevision>>, CreateMailboxRoutingPromptRevisionMutationVariables> = (props) => {
          const {mailboxId,data} = props ?? {};

          return  createMailboxRoutingPromptRevision(mailboxId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateMailboxRoutingPromptRevisionMutationResult = NonNullable<Awaited<ReturnType<typeof createMailboxRoutingPromptRevision>>>
    export type CreateMailboxRoutingPromptRevisionMutationBody = MailboxRoutingPromptRevisionCreate
    export type CreateMailboxRoutingPromptRevisionMutationError = Error
    export type CreateMailboxRoutingPromptRevisionMutationVariables = {mailboxId: string;data: MailboxRoutingPromptRevisionCreate}

    export const useCreateMailboxRoutingPromptRevision = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createMailboxRoutingPromptRevision>>, TError,CreateMailboxRoutingPromptRevisionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createMailboxRoutingPromptRevision>>,
        TError,
        CreateMailboxRoutingPromptRevisionMutationVariables,
        TContext
      > => {
      return useMutation(getCreateMailboxRoutingPromptRevisionMutationOptions(options));
    }

export const getTestMailboxRoutingPromptRevisionUrl = (mailboxId: string,
    revisionId: string,) => {




  return `/api/email/intake/mailboxes/${mailboxId}/prompts/${revisionId}/test`
}

export const testMailboxRoutingPromptRevision = async (mailboxId: string,
    revisionId: string,
    mailboxRoutingPromptTestRequest: MailboxRoutingPromptTestRequest, options?: Parameters<typeof customFetch>[1]): Promise<MailboxRoutingPromptTestResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MailboxRoutingPromptTestResult>(getTestMailboxRoutingPromptRevisionUrl(mailboxId,revisionId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(mailboxRoutingPromptTestRequest)
  }
);}





export const getTestMailboxRoutingPromptRevisionMutationKey = () => ['testMailboxRoutingPromptRevision'] as const;

export const getTestMailboxRoutingPromptRevisionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof testMailboxRoutingPromptRevision>>, TError,TestMailboxRoutingPromptRevisionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof testMailboxRoutingPromptRevision>>, TError,TestMailboxRoutingPromptRevisionMutationVariables, TContext> => {

const mutationKey = getTestMailboxRoutingPromptRevisionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof testMailboxRoutingPromptRevision>>, TestMailboxRoutingPromptRevisionMutationVariables> = (props) => {
          const {mailboxId,revisionId,data} = props ?? {};

          return  testMailboxRoutingPromptRevision(mailboxId,revisionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TestMailboxRoutingPromptRevisionMutationResult = NonNullable<Awaited<ReturnType<typeof testMailboxRoutingPromptRevision>>>
    export type TestMailboxRoutingPromptRevisionMutationBody = MailboxRoutingPromptTestRequest
    export type TestMailboxRoutingPromptRevisionMutationError = Error
    export type TestMailboxRoutingPromptRevisionMutationVariables = {mailboxId: string;revisionId: string;data: MailboxRoutingPromptTestRequest}

    export const useTestMailboxRoutingPromptRevision = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof testMailboxRoutingPromptRevision>>, TError,TestMailboxRoutingPromptRevisionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof testMailboxRoutingPromptRevision>>,
        TError,
        TestMailboxRoutingPromptRevisionMutationVariables,
        TContext
      > => {
      return useMutation(getTestMailboxRoutingPromptRevisionMutationOptions(options));
    }

export const getActivateMailboxRoutingPromptRevisionUrl = (mailboxId: string,
    revisionId: string,) => {




  return `/api/email/intake/mailboxes/${mailboxId}/prompts/${revisionId}/activate`
}

export const activateMailboxRoutingPromptRevision = async (mailboxId: string,
    revisionId: string,
    mailboxRoutingPromptActivation: MailboxRoutingPromptActivation, options?: Parameters<typeof customFetch>[1]): Promise<MailboxRoutingPromptRevision> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MailboxRoutingPromptRevision>(getActivateMailboxRoutingPromptRevisionUrl(mailboxId,revisionId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(mailboxRoutingPromptActivation)
  }
);}





export const getActivateMailboxRoutingPromptRevisionMutationKey = () => ['activateMailboxRoutingPromptRevision'] as const;

export const getActivateMailboxRoutingPromptRevisionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof activateMailboxRoutingPromptRevision>>, TError,ActivateMailboxRoutingPromptRevisionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof activateMailboxRoutingPromptRevision>>, TError,ActivateMailboxRoutingPromptRevisionMutationVariables, TContext> => {

const mutationKey = getActivateMailboxRoutingPromptRevisionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof activateMailboxRoutingPromptRevision>>, ActivateMailboxRoutingPromptRevisionMutationVariables> = (props) => {
          const {mailboxId,revisionId,data} = props ?? {};

          return  activateMailboxRoutingPromptRevision(mailboxId,revisionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ActivateMailboxRoutingPromptRevisionMutationResult = NonNullable<Awaited<ReturnType<typeof activateMailboxRoutingPromptRevision>>>
    export type ActivateMailboxRoutingPromptRevisionMutationBody = MailboxRoutingPromptActivation
    export type ActivateMailboxRoutingPromptRevisionMutationError = Error
    export type ActivateMailboxRoutingPromptRevisionMutationVariables = {mailboxId: string;revisionId: string;data: MailboxRoutingPromptActivation}

    export const useActivateMailboxRoutingPromptRevision = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof activateMailboxRoutingPromptRevision>>, TError,ActivateMailboxRoutingPromptRevisionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof activateMailboxRoutingPromptRevision>>,
        TError,
        ActivateMailboxRoutingPromptRevisionMutationVariables,
        TContext
      > => {
      return useMutation(getActivateMailboxRoutingPromptRevisionMutationOptions(options));
    }

export const getPauseMailboxRoutingPromptUrl = (mailboxId: string,) => {




  return `/api/email/intake/mailboxes/${mailboxId}/prompts/pause`
}

export const pauseMailboxRoutingPrompt = async (mailboxId: string,
    mailboxRoutingPromptPause: MailboxRoutingPromptPause, options?: Parameters<typeof customFetch>[1]): Promise<MailboxRoutingPromptConfigurationState> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MailboxRoutingPromptConfigurationState>(getPauseMailboxRoutingPromptUrl(mailboxId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(mailboxRoutingPromptPause)
  }
);}





export const getPauseMailboxRoutingPromptMutationKey = () => ['pauseMailboxRoutingPrompt'] as const;

export const getPauseMailboxRoutingPromptMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof pauseMailboxRoutingPrompt>>, TError,PauseMailboxRoutingPromptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof pauseMailboxRoutingPrompt>>, TError,PauseMailboxRoutingPromptMutationVariables, TContext> => {

const mutationKey = getPauseMailboxRoutingPromptMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof pauseMailboxRoutingPrompt>>, PauseMailboxRoutingPromptMutationVariables> = (props) => {
          const {mailboxId,data} = props ?? {};

          return  pauseMailboxRoutingPrompt(mailboxId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PauseMailboxRoutingPromptMutationResult = NonNullable<Awaited<ReturnType<typeof pauseMailboxRoutingPrompt>>>
    export type PauseMailboxRoutingPromptMutationBody = MailboxRoutingPromptPause
    export type PauseMailboxRoutingPromptMutationError = Error
    export type PauseMailboxRoutingPromptMutationVariables = {mailboxId: string;data: MailboxRoutingPromptPause}

    export const usePauseMailboxRoutingPrompt = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof pauseMailboxRoutingPrompt>>, TError,PauseMailboxRoutingPromptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof pauseMailboxRoutingPrompt>>,
        TError,
        PauseMailboxRoutingPromptMutationVariables,
        TContext
      > => {
      return useMutation(getPauseMailboxRoutingPromptMutationOptions(options));
    }

export const getListMailboxRoutingCandidatesUrl = (params?: ListMailboxRoutingCandidatesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/email/intake/candidates?${stringifiedParams}` : `/api/email/intake/candidates`
}

export const listMailboxRoutingCandidates = async (params?: ListMailboxRoutingCandidatesParams, options?: Parameters<typeof customFetch>[1]): Promise<MailboxRoutingCandidateList> => {

  return customFetch<MailboxRoutingCandidateList>(getListMailboxRoutingCandidatesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMailboxRoutingCandidatesQueryKey = (params?: ListMailboxRoutingCandidatesParams,) => {
    return [
    `/api/email/intake/candidates`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMailboxRoutingCandidatesQueryOptions = <TData = Awaited<ReturnType<typeof listMailboxRoutingCandidates>>, TError = unknown>(params?: ListMailboxRoutingCandidatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMailboxRoutingCandidates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMailboxRoutingCandidatesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMailboxRoutingCandidates>>> = ({ signal }) => listMailboxRoutingCandidates(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMailboxRoutingCandidates>>, TError, TData> & { queryKey: QueryKey }
}

export type ListMailboxRoutingCandidatesQueryResult = NonNullable<Awaited<ReturnType<typeof listMailboxRoutingCandidates>>>
export type ListMailboxRoutingCandidatesQueryError = unknown



export function useListMailboxRoutingCandidates<TData = Awaited<ReturnType<typeof listMailboxRoutingCandidates>>, TError = unknown>(
 params?: ListMailboxRoutingCandidatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMailboxRoutingCandidates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListMailboxRoutingCandidatesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetMailboxRoutingCandidateUrl = (candidateId: string,) => {




  return `/api/email/intake/candidates/${candidateId}`
}

export const getMailboxRoutingCandidate = async (candidateId: string, options?: Parameters<typeof customFetch>[1]): Promise<MailboxRoutingCandidate> => {

  return customFetch<MailboxRoutingCandidate>(getGetMailboxRoutingCandidateUrl(candidateId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMailboxRoutingCandidateQueryKey = (candidateId: string,) => {
    return [
    `/api/email/intake/candidates/${candidateId}`
    ] as const;
    }


export const getGetMailboxRoutingCandidateQueryOptions = <TData = Awaited<ReturnType<typeof getMailboxRoutingCandidate>>, TError = Error>(candidateId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMailboxRoutingCandidate>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMailboxRoutingCandidateQueryKey(candidateId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMailboxRoutingCandidate>>> = ({ signal }) => getMailboxRoutingCandidate(candidateId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: candidateId !== null && candidateId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMailboxRoutingCandidate>>, TError, TData> & { queryKey: QueryKey }
}

export type GetMailboxRoutingCandidateQueryResult = NonNullable<Awaited<ReturnType<typeof getMailboxRoutingCandidate>>>
export type GetMailboxRoutingCandidateQueryError = Error



export function useGetMailboxRoutingCandidate<TData = Awaited<ReturnType<typeof getMailboxRoutingCandidate>>, TError = Error>(
 candidateId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMailboxRoutingCandidate>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetMailboxRoutingCandidateQueryOptions(candidateId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDecideMailboxRoutingCandidateUrl = (candidateId: string,) => {




  return `/api/email/intake/candidates/${candidateId}/decisions`
}

export const decideMailboxRoutingCandidate = async (candidateId: string,
    mailboxRoutingCandidateDecision: MailboxRoutingCandidateDecision, options?: Parameters<typeof customFetch>[1]): Promise<MailboxRoutingExecution> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MailboxRoutingExecution>(getDecideMailboxRoutingCandidateUrl(candidateId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(mailboxRoutingCandidateDecision)
  }
);}





export const getDecideMailboxRoutingCandidateMutationKey = () => ['decideMailboxRoutingCandidate'] as const;

export const getDecideMailboxRoutingCandidateMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideMailboxRoutingCandidate>>, TError,DecideMailboxRoutingCandidateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof decideMailboxRoutingCandidate>>, TError,DecideMailboxRoutingCandidateMutationVariables, TContext> => {

const mutationKey = getDecideMailboxRoutingCandidateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof decideMailboxRoutingCandidate>>, DecideMailboxRoutingCandidateMutationVariables> = (props) => {
          const {candidateId,data} = props ?? {};

          return  decideMailboxRoutingCandidate(candidateId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DecideMailboxRoutingCandidateMutationResult = NonNullable<Awaited<ReturnType<typeof decideMailboxRoutingCandidate>>>
    export type DecideMailboxRoutingCandidateMutationBody = MailboxRoutingCandidateDecision
    export type DecideMailboxRoutingCandidateMutationError = Error
    export type DecideMailboxRoutingCandidateMutationVariables = {candidateId: string;data: MailboxRoutingCandidateDecision}

    export const useDecideMailboxRoutingCandidate = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideMailboxRoutingCandidate>>, TError,DecideMailboxRoutingCandidateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof decideMailboxRoutingCandidate>>,
        TError,
        DecideMailboxRoutingCandidateMutationVariables,
        TContext
      > => {
      return useMutation(getDecideMailboxRoutingCandidateMutationOptions(options));
    }

export const getCreateMailboxHistoryPreviewUrl = () => {




  return `/api/email/intake/history/previews`
}

/**
 * @summary Queue a historical preview that performs no CRM writes
 */
export const createMailboxHistoryPreview = async (mailboxHistoryPreviewCreate: MailboxHistoryPreviewCreate, options?: Parameters<typeof customFetch>[1]): Promise<MailboxIntakeJob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MailboxIntakeJob>(getCreateMailboxHistoryPreviewUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(mailboxHistoryPreviewCreate)
  }
);}





export const getCreateMailboxHistoryPreviewMutationKey = () => ['createMailboxHistoryPreview'] as const;

export const getCreateMailboxHistoryPreviewMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createMailboxHistoryPreview>>, TError,CreateMailboxHistoryPreviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createMailboxHistoryPreview>>, TError,CreateMailboxHistoryPreviewMutationVariables, TContext> => {

const mutationKey = getCreateMailboxHistoryPreviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createMailboxHistoryPreview>>, CreateMailboxHistoryPreviewMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createMailboxHistoryPreview(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateMailboxHistoryPreviewMutationResult = NonNullable<Awaited<ReturnType<typeof createMailboxHistoryPreview>>>
    export type CreateMailboxHistoryPreviewMutationBody = MailboxHistoryPreviewCreate
    export type CreateMailboxHistoryPreviewMutationError = Error
    export type CreateMailboxHistoryPreviewMutationVariables = {data: MailboxHistoryPreviewCreate}

    /**
 * @summary Queue a historical preview that performs no CRM writes
 */
export const useCreateMailboxHistoryPreview = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createMailboxHistoryPreview>>, TError,CreateMailboxHistoryPreviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createMailboxHistoryPreview>>,
        TError,
        CreateMailboxHistoryPreviewMutationVariables,
        TContext
      > => {
      return useMutation(getCreateMailboxHistoryPreviewMutationOptions(options));
    }

export const getGetMailboxHistoryPreviewUrl = (previewJobId: string,
    params?: GetMailboxHistoryPreviewParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/email/intake/history/previews/${previewJobId}?${stringifiedParams}` : `/api/email/intake/history/previews/${previewJobId}`
}

export const getMailboxHistoryPreview = async (previewJobId: string,
    params?: GetMailboxHistoryPreviewParams, options?: Parameters<typeof customFetch>[1]): Promise<MailboxHistoryPreview> => {

  return customFetch<MailboxHistoryPreview>(getGetMailboxHistoryPreviewUrl(previewJobId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMailboxHistoryPreviewQueryKey = (previewJobId: string,
    params?: GetMailboxHistoryPreviewParams,) => {
    return [
    `/api/email/intake/history/previews/${previewJobId}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetMailboxHistoryPreviewQueryOptions = <TData = Awaited<ReturnType<typeof getMailboxHistoryPreview>>, TError = unknown>(previewJobId: string,
    params?: GetMailboxHistoryPreviewParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMailboxHistoryPreview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMailboxHistoryPreviewQueryKey(previewJobId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMailboxHistoryPreview>>> = ({ signal }) => getMailboxHistoryPreview(previewJobId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: previewJobId !== null && previewJobId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMailboxHistoryPreview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetMailboxHistoryPreviewQueryResult = NonNullable<Awaited<ReturnType<typeof getMailboxHistoryPreview>>>
export type GetMailboxHistoryPreviewQueryError = unknown



export function useGetMailboxHistoryPreview<TData = Awaited<ReturnType<typeof getMailboxHistoryPreview>>, TError = unknown>(
 previewJobId: string,
    params?: GetMailboxHistoryPreviewParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getMailboxHistoryPreview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetMailboxHistoryPreviewQueryOptions(previewJobId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConfirmMailboxHistoryPreviewUrl = (previewJobId: string,) => {




  return `/api/email/intake/history/previews/${previewJobId}`
}

/**
 * @summary Confirm an exact completed preview and selected candidates for import
 */
export const confirmMailboxHistoryPreview = async (previewJobId: string,
    mailboxHistoryImportConfirm: MailboxHistoryImportConfirm, options?: Parameters<typeof customFetch>[1]): Promise<MailboxIntakeJob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MailboxIntakeJob>(getConfirmMailboxHistoryPreviewUrl(previewJobId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(mailboxHistoryImportConfirm)
  }
);}





export const getConfirmMailboxHistoryPreviewMutationKey = () => ['confirmMailboxHistoryPreview'] as const;

export const getConfirmMailboxHistoryPreviewMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmMailboxHistoryPreview>>, TError,ConfirmMailboxHistoryPreviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmMailboxHistoryPreview>>, TError,ConfirmMailboxHistoryPreviewMutationVariables, TContext> => {

const mutationKey = getConfirmMailboxHistoryPreviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmMailboxHistoryPreview>>, ConfirmMailboxHistoryPreviewMutationVariables> = (props) => {
          const {previewJobId,data} = props ?? {};

          return  confirmMailboxHistoryPreview(previewJobId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmMailboxHistoryPreviewMutationResult = NonNullable<Awaited<ReturnType<typeof confirmMailboxHistoryPreview>>>
    export type ConfirmMailboxHistoryPreviewMutationBody = MailboxHistoryImportConfirm
    export type ConfirmMailboxHistoryPreviewMutationError = Error
    export type ConfirmMailboxHistoryPreviewMutationVariables = {previewJobId: string;data: MailboxHistoryImportConfirm}

    /**
 * @summary Confirm an exact completed preview and selected candidates for import
 */
export const useConfirmMailboxHistoryPreview = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmMailboxHistoryPreview>>, TError,ConfirmMailboxHistoryPreviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmMailboxHistoryPreview>>,
        TError,
        ConfirmMailboxHistoryPreviewMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmMailboxHistoryPreviewMutationOptions(options));
    }

export const getListMailboxIntakeJobsUrl = (params?: ListMailboxIntakeJobsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/email/intake/jobs?${stringifiedParams}` : `/api/email/intake/jobs`
}

export const listMailboxIntakeJobs = async (params?: ListMailboxIntakeJobsParams, options?: Parameters<typeof customFetch>[1]): Promise<MailboxIntakeJobList> => {

  return customFetch<MailboxIntakeJobList>(getListMailboxIntakeJobsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMailboxIntakeJobsQueryKey = (params?: ListMailboxIntakeJobsParams,) => {
    return [
    `/api/email/intake/jobs`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMailboxIntakeJobsQueryOptions = <TData = Awaited<ReturnType<typeof listMailboxIntakeJobs>>, TError = unknown>(params?: ListMailboxIntakeJobsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMailboxIntakeJobs>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMailboxIntakeJobsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMailboxIntakeJobs>>> = ({ signal }) => listMailboxIntakeJobs(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMailboxIntakeJobs>>, TError, TData> & { queryKey: QueryKey }
}

export type ListMailboxIntakeJobsQueryResult = NonNullable<Awaited<ReturnType<typeof listMailboxIntakeJobs>>>
export type ListMailboxIntakeJobsQueryError = unknown



export function useListMailboxIntakeJobs<TData = Awaited<ReturnType<typeof listMailboxIntakeJobs>>, TError = unknown>(
 params?: ListMailboxIntakeJobsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMailboxIntakeJobs>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListMailboxIntakeJobsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCancelMailboxIntakeJobUrl = (jobId: string,) => {




  return `/api/email/intake/jobs/${jobId}/cancel`
}

export const cancelMailboxIntakeJob = async (jobId: string,
    mailboxIntakeJobCancel: MailboxIntakeJobCancel, options?: Parameters<typeof customFetch>[1]): Promise<MailboxIntakeJob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MailboxIntakeJob>(getCancelMailboxIntakeJobUrl(jobId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(mailboxIntakeJobCancel)
  }
);}





export const getCancelMailboxIntakeJobMutationKey = () => ['cancelMailboxIntakeJob'] as const;

export const getCancelMailboxIntakeJobMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelMailboxIntakeJob>>, TError,CancelMailboxIntakeJobMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelMailboxIntakeJob>>, TError,CancelMailboxIntakeJobMutationVariables, TContext> => {

const mutationKey = getCancelMailboxIntakeJobMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelMailboxIntakeJob>>, CancelMailboxIntakeJobMutationVariables> = (props) => {
          const {jobId,data} = props ?? {};

          return  cancelMailboxIntakeJob(jobId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelMailboxIntakeJobMutationResult = NonNullable<Awaited<ReturnType<typeof cancelMailboxIntakeJob>>>
    export type CancelMailboxIntakeJobMutationBody = MailboxIntakeJobCancel
    export type CancelMailboxIntakeJobMutationError = unknown
    export type CancelMailboxIntakeJobMutationVariables = {jobId: string;data: MailboxIntakeJobCancel}

    export const useCancelMailboxIntakeJob = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelMailboxIntakeJob>>, TError,CancelMailboxIntakeJobMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelMailboxIntakeJob>>,
        TError,
        CancelMailboxIntakeJobMutationVariables,
        TContext
      > => {
      return useMutation(getCancelMailboxIntakeJobMutationOptions(options));
    }

export const getListMailboxRoutingExecutionsUrl = (params?: ListMailboxRoutingExecutionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/email/intake/executions?${stringifiedParams}` : `/api/email/intake/executions`
}

export const listMailboxRoutingExecutions = async (params?: ListMailboxRoutingExecutionsParams, options?: Parameters<typeof customFetch>[1]): Promise<MailboxRoutingExecutionList> => {

  return customFetch<MailboxRoutingExecutionList>(getListMailboxRoutingExecutionsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMailboxRoutingExecutionsQueryKey = (params?: ListMailboxRoutingExecutionsParams,) => {
    return [
    `/api/email/intake/executions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMailboxRoutingExecutionsQueryOptions = <TData = Awaited<ReturnType<typeof listMailboxRoutingExecutions>>, TError = unknown>(params?: ListMailboxRoutingExecutionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMailboxRoutingExecutions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMailboxRoutingExecutionsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMailboxRoutingExecutions>>> = ({ signal }) => listMailboxRoutingExecutions(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMailboxRoutingExecutions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListMailboxRoutingExecutionsQueryResult = NonNullable<Awaited<ReturnType<typeof listMailboxRoutingExecutions>>>
export type ListMailboxRoutingExecutionsQueryError = unknown



export function useListMailboxRoutingExecutions<TData = Awaited<ReturnType<typeof listMailboxRoutingExecutions>>, TError = unknown>(
 params?: ListMailboxRoutingExecutionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listMailboxRoutingExecutions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListMailboxRoutingExecutionsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetOutlookStyleProfileUrl = () => {




  return `/api/outlook/style-profile`
}

/**
 * @summary Get the signed-in user's writing-style profile
 */
export const getOutlookStyleProfile = async ( options?: Parameters<typeof customFetch>[1]): Promise<OutlookStyleProfileState> => {

  return customFetch<OutlookStyleProfileState>(getGetOutlookStyleProfileUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetOutlookStyleProfileQueryKey = () => {
    return [
    `/api/outlook/style-profile`
    ] as const;
    }


export const getGetOutlookStyleProfileQueryOptions = <TData = Awaited<ReturnType<typeof getOutlookStyleProfile>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getOutlookStyleProfile>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetOutlookStyleProfileQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getOutlookStyleProfile>>> = ({ signal }) => getOutlookStyleProfile({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getOutlookStyleProfile>>, TError, TData> & { queryKey: QueryKey }
}

export type GetOutlookStyleProfileQueryResult = NonNullable<Awaited<ReturnType<typeof getOutlookStyleProfile>>>
export type GetOutlookStyleProfileQueryError = unknown


/**
 * @summary Get the signed-in user's writing-style profile
 */

export function useGetOutlookStyleProfile<TData = Awaited<ReturnType<typeof getOutlookStyleProfile>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getOutlookStyleProfile>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetOutlookStyleProfileQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateOutlookStyleProfileUrl = () => {




  return `/api/outlook/style-profile`
}

/**
 * @summary Explicitly save the reviewed writing-style profile
 */
export const updateOutlookStyleProfile = async (outlookStyleProfileUpdate: OutlookStyleProfileUpdate, options?: Parameters<typeof customFetch>[1]): Promise<OutlookStyleProfileState> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OutlookStyleProfileState>(getUpdateOutlookStyleProfileUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(outlookStyleProfileUpdate)
  }
);}





export const getUpdateOutlookStyleProfileMutationKey = () => ['updateOutlookStyleProfile'] as const;

export const getUpdateOutlookStyleProfileMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateOutlookStyleProfile>>, TError,UpdateOutlookStyleProfileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateOutlookStyleProfile>>, TError,UpdateOutlookStyleProfileMutationVariables, TContext> => {

const mutationKey = getUpdateOutlookStyleProfileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateOutlookStyleProfile>>, UpdateOutlookStyleProfileMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateOutlookStyleProfile(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateOutlookStyleProfileMutationResult = NonNullable<Awaited<ReturnType<typeof updateOutlookStyleProfile>>>
    export type UpdateOutlookStyleProfileMutationBody = OutlookStyleProfileUpdate
    export type UpdateOutlookStyleProfileMutationError = unknown
    export type UpdateOutlookStyleProfileMutationVariables = {data: OutlookStyleProfileUpdate}

    /**
 * @summary Explicitly save the reviewed writing-style profile
 */
export const useUpdateOutlookStyleProfile = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateOutlookStyleProfile>>, TError,UpdateOutlookStyleProfileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateOutlookStyleProfile>>,
        TError,
        UpdateOutlookStyleProfileMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateOutlookStyleProfileMutationOptions(options));
    }

export const getClearOutlookStyleProfileUrl = () => {




  return `/api/outlook/style-profile`
}

/**
 * @summary Clear the signed-in user's writing-style profile
 */
export const clearOutlookStyleProfile = async ( options?: Parameters<typeof customFetch>[1]): Promise<ClearOutlookStyleProfile200> => {

  return customFetch<ClearOutlookStyleProfile200>(getClearOutlookStyleProfileUrl(),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getClearOutlookStyleProfileMutationKey = () => ['clearOutlookStyleProfile'] as const;

export const getClearOutlookStyleProfileMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearOutlookStyleProfile>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof clearOutlookStyleProfile>>, TError,void, TContext> => {

const mutationKey = getClearOutlookStyleProfileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clearOutlookStyleProfile>>, void> = () => {


          return  clearOutlookStyleProfile(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClearOutlookStyleProfileMutationResult = NonNullable<Awaited<ReturnType<typeof clearOutlookStyleProfile>>>

    export type ClearOutlookStyleProfileMutationError = unknown


    /**
 * @summary Clear the signed-in user's writing-style profile
 */
export const useClearOutlookStyleProfile = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearOutlookStyleProfile>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof clearOutlookStyleProfile>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getClearOutlookStyleProfileMutationOptions(options));
    }

export const getAnalyzeOutlookStyleProfileUrl = () => {




  return `/api/outlook/style-profile/analyze`
}

/**
 * Accepts stripped plain-text samples only — no Graph tokens and no raw email HTML. Requires at least three usable samples. Returns a preview without updating the saved profile; use updateOutlookStyleProfile only after explicit review.
 * @summary Analyse plain-text Sent Items samples into an unsaved writing-style preview
 */
export const analyzeOutlookStyleProfile = async (outlookStyleAnalyzeRequest: OutlookStyleAnalyzeRequest, options?: Parameters<typeof customFetch>[1]): Promise<OutlookStyleProfileState> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OutlookStyleProfileState>(getAnalyzeOutlookStyleProfileUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(outlookStyleAnalyzeRequest)
  }
);}





export const getAnalyzeOutlookStyleProfileMutationKey = () => ['analyzeOutlookStyleProfile'] as const;

export const getAnalyzeOutlookStyleProfileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof analyzeOutlookStyleProfile>>, TError,AnalyzeOutlookStyleProfileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof analyzeOutlookStyleProfile>>, TError,AnalyzeOutlookStyleProfileMutationVariables, TContext> => {

const mutationKey = getAnalyzeOutlookStyleProfileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof analyzeOutlookStyleProfile>>, AnalyzeOutlookStyleProfileMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  analyzeOutlookStyleProfile(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AnalyzeOutlookStyleProfileMutationResult = NonNullable<Awaited<ReturnType<typeof analyzeOutlookStyleProfile>>>
    export type AnalyzeOutlookStyleProfileMutationBody = OutlookStyleAnalyzeRequest
    export type AnalyzeOutlookStyleProfileMutationError = Error
    export type AnalyzeOutlookStyleProfileMutationVariables = {data: OutlookStyleAnalyzeRequest}

    /**
 * @summary Analyse plain-text Sent Items samples into an unsaved writing-style preview
 */
export const useAnalyzeOutlookStyleProfile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof analyzeOutlookStyleProfile>>, TError,AnalyzeOutlookStyleProfileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof analyzeOutlookStyleProfile>>,
        TError,
        AnalyzeOutlookStyleProfileMutationVariables,
        TContext
      > => {
      return useMutation(getAnalyzeOutlookStyleProfileMutationOptions(options));
    }

export const getGenerateOutlookSmartReplyUrl = () => {




  return `/api/outlook/smart-reply`
}

/**
 * Accepts stripped plain-text message content only — no Graph tokens and no raw email HTML. When a stored style profile exists it is applied automatically.
 * @summary Draft an AI reply for a message, personalised with the user's stored writing-style profile
 */
export const generateOutlookSmartReply = async (outlookSmartReplyRequest: OutlookSmartReplyRequest, options?: Parameters<typeof customFetch>[1]): Promise<OutlookSmartReplyResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OutlookSmartReplyResponse>(getGenerateOutlookSmartReplyUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(outlookSmartReplyRequest)
  }
);}





export const getGenerateOutlookSmartReplyMutationKey = () => ['generateOutlookSmartReply'] as const;

export const getGenerateOutlookSmartReplyMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateOutlookSmartReply>>, TError,GenerateOutlookSmartReplyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof generateOutlookSmartReply>>, TError,GenerateOutlookSmartReplyMutationVariables, TContext> => {

const mutationKey = getGenerateOutlookSmartReplyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof generateOutlookSmartReply>>, GenerateOutlookSmartReplyMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  generateOutlookSmartReply(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenerateOutlookSmartReplyMutationResult = NonNullable<Awaited<ReturnType<typeof generateOutlookSmartReply>>>
    export type GenerateOutlookSmartReplyMutationBody = OutlookSmartReplyRequest
    export type GenerateOutlookSmartReplyMutationError = Error
    export type GenerateOutlookSmartReplyMutationVariables = {data: OutlookSmartReplyRequest}

    /**
 * @summary Draft an AI reply for a message, personalised with the user's stored writing-style profile
 */
export const useGenerateOutlookSmartReply = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateOutlookSmartReply>>, TError,GenerateOutlookSmartReplyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof generateOutlookSmartReply>>,
        TError,
        GenerateOutlookSmartReplyMutationVariables,
        TContext
      > => {
      return useMutation(getGenerateOutlookSmartReplyMutationOptions(options));
    }

export const getSuggestOutlookContactUrl = () => {




  return `/api/outlook/contact-suggestion`
}

/**
 * Accepts the sender address and stripped plain-text message content only — no Graph tokens and no raw email HTML. Returns editable suggestions and a single existing matching account when one is found. Nothing is saved.
 * @summary Suggest new-contact details for an email sender using AI
 */
export const suggestOutlookContact = async (outlookContactSuggestionRequest: OutlookContactSuggestionRequest, options?: Parameters<typeof customFetch>[1]): Promise<OutlookContactSuggestion> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OutlookContactSuggestion>(getSuggestOutlookContactUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(outlookContactSuggestionRequest)
  }
);}





export const getSuggestOutlookContactMutationKey = () => ['suggestOutlookContact'] as const;

export const getSuggestOutlookContactMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof suggestOutlookContact>>, TError,SuggestOutlookContactMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof suggestOutlookContact>>, TError,SuggestOutlookContactMutationVariables, TContext> => {

const mutationKey = getSuggestOutlookContactMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof suggestOutlookContact>>, SuggestOutlookContactMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  suggestOutlookContact(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SuggestOutlookContactMutationResult = NonNullable<Awaited<ReturnType<typeof suggestOutlookContact>>>
    export type SuggestOutlookContactMutationBody = OutlookContactSuggestionRequest
    export type SuggestOutlookContactMutationError = Error
    export type SuggestOutlookContactMutationVariables = {data: OutlookContactSuggestionRequest}

    /**
 * @summary Suggest new-contact details for an email sender using AI
 */
export const useSuggestOutlookContact = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof suggestOutlookContact>>, TError,SuggestOutlookContactMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof suggestOutlookContact>>,
        TError,
        SuggestOutlookContactMutationVariables,
        TContext
      > => {
      return useMutation(getSuggestOutlookContactMutationOptions(options));
    }

export const getGetOutlookDevOpsLinkUrl = (params: GetOutlookDevOpsLinkParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/outlook/devops-link?${stringifiedParams}` : `/api/outlook/devops-link`
}

/**
 * @summary Get the signed-in user's Azure DevOps link for an Outlook message
 */
export const getOutlookDevOpsLink = async (params: GetOutlookDevOpsLinkParams, options?: Parameters<typeof customFetch>[1]): Promise<OutlookDevOpsLinkLookup> => {

  return customFetch<OutlookDevOpsLinkLookup>(getGetOutlookDevOpsLinkUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetOutlookDevOpsLinkQueryKey = (params?: GetOutlookDevOpsLinkParams,) => {
    return [
    `/api/outlook/devops-link`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetOutlookDevOpsLinkQueryOptions = <TData = Awaited<ReturnType<typeof getOutlookDevOpsLink>>, TError = Error>(params: GetOutlookDevOpsLinkParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getOutlookDevOpsLink>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetOutlookDevOpsLinkQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getOutlookDevOpsLink>>> = ({ signal }) => getOutlookDevOpsLink(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getOutlookDevOpsLink>>, TError, TData> & { queryKey: QueryKey }
}

export type GetOutlookDevOpsLinkQueryResult = NonNullable<Awaited<ReturnType<typeof getOutlookDevOpsLink>>>
export type GetOutlookDevOpsLinkQueryError = Error


/**
 * @summary Get the signed-in user's Azure DevOps link for an Outlook message
 */

export function useGetOutlookDevOpsLink<TData = Awaited<ReturnType<typeof getOutlookDevOpsLink>>, TError = Error>(
 params: GetOutlookDevOpsLinkParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getOutlookDevOpsLink>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetOutlookDevOpsLinkQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpsertOutlookDevOpsLinkUrl = () => {




  return `/api/outlook/devops-link`
}

/**
 * @summary Create or replace the signed-in user's Azure DevOps link for an Outlook message
 */
export const upsertOutlookDevOpsLink = async (outlookDevOpsLinkUpsert: OutlookDevOpsLinkUpsert, options?: Parameters<typeof customFetch>[1]): Promise<OutlookDevOpsLink> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OutlookDevOpsLink>(getUpsertOutlookDevOpsLinkUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(outlookDevOpsLinkUpsert)
  }
);}





export const getUpsertOutlookDevOpsLinkMutationKey = () => ['upsertOutlookDevOpsLink'] as const;

export const getUpsertOutlookDevOpsLinkMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertOutlookDevOpsLink>>, TError,UpsertOutlookDevOpsLinkMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertOutlookDevOpsLink>>, TError,UpsertOutlookDevOpsLinkMutationVariables, TContext> => {

const mutationKey = getUpsertOutlookDevOpsLinkMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertOutlookDevOpsLink>>, UpsertOutlookDevOpsLinkMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  upsertOutlookDevOpsLink(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertOutlookDevOpsLinkMutationResult = NonNullable<Awaited<ReturnType<typeof upsertOutlookDevOpsLink>>>
    export type UpsertOutlookDevOpsLinkMutationBody = OutlookDevOpsLinkUpsert
    export type UpsertOutlookDevOpsLinkMutationError = Error
    export type UpsertOutlookDevOpsLinkMutationVariables = {data: OutlookDevOpsLinkUpsert}

    /**
 * @summary Create or replace the signed-in user's Azure DevOps link for an Outlook message
 */
export const useUpsertOutlookDevOpsLink = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertOutlookDevOpsLink>>, TError,UpsertOutlookDevOpsLinkMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof upsertOutlookDevOpsLink>>,
        TError,
        UpsertOutlookDevOpsLinkMutationVariables,
        TContext
      > => {
      return useMutation(getUpsertOutlookDevOpsLinkMutationOptions(options));
    }

export const getDeleteOutlookDevOpsLinkUrl = (params: DeleteOutlookDevOpsLinkParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/outlook/devops-link?${stringifiedParams}` : `/api/outlook/devops-link`
}

/**
 * @summary Remove the signed-in user's Azure DevOps link for an Outlook message
 */
export const deleteOutlookDevOpsLink = async (params: DeleteOutlookDevOpsLinkParams, options?: Parameters<typeof customFetch>[1]): Promise<DeleteOutlookDevOpsLink200> => {

  return customFetch<DeleteOutlookDevOpsLink200>(getDeleteOutlookDevOpsLinkUrl(params),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteOutlookDevOpsLinkMutationKey = () => ['deleteOutlookDevOpsLink'] as const;

export const getDeleteOutlookDevOpsLinkMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteOutlookDevOpsLink>>, TError,DeleteOutlookDevOpsLinkMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteOutlookDevOpsLink>>, TError,DeleteOutlookDevOpsLinkMutationVariables, TContext> => {

const mutationKey = getDeleteOutlookDevOpsLinkMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteOutlookDevOpsLink>>, DeleteOutlookDevOpsLinkMutationVariables> = (props) => {
          const {params} = props ?? {};

          return  deleteOutlookDevOpsLink(params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteOutlookDevOpsLinkMutationResult = NonNullable<Awaited<ReturnType<typeof deleteOutlookDevOpsLink>>>

    export type DeleteOutlookDevOpsLinkMutationError = Error
    export type DeleteOutlookDevOpsLinkMutationVariables = {params: DeleteOutlookDevOpsLinkParams}

    /**
 * @summary Remove the signed-in user's Azure DevOps link for an Outlook message
 */
export const useDeleteOutlookDevOpsLink = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteOutlookDevOpsLink>>, TError,DeleteOutlookDevOpsLinkMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteOutlookDevOpsLink>>,
        TError,
        DeleteOutlookDevOpsLinkMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteOutlookDevOpsLinkMutationOptions(options));
    }

export const getListIngestionRulesUrl = () => {




  return `/api/web-signals/rules`
}

/**
 * @summary List configured web-ingestion rules
 */
export const listIngestionRules = async ( options?: Parameters<typeof customFetch>[1]): Promise<ListIngestionRules200> => {

  return customFetch<ListIngestionRules200>(getListIngestionRulesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListIngestionRulesQueryKey = () => {
    return [
    `/api/web-signals/rules`
    ] as const;
    }


export const getListIngestionRulesQueryOptions = <TData = Awaited<ReturnType<typeof listIngestionRules>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listIngestionRules>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListIngestionRulesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listIngestionRules>>> = ({ signal }) => listIngestionRules({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listIngestionRules>>, TError, TData> & { queryKey: QueryKey }
}

export type ListIngestionRulesQueryResult = NonNullable<Awaited<ReturnType<typeof listIngestionRules>>>
export type ListIngestionRulesQueryError = unknown


/**
 * @summary List configured web-ingestion rules
 */

export function useListIngestionRules<TData = Awaited<ReturnType<typeof listIngestionRules>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listIngestionRules>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListIngestionRulesQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateIngestionRuleUrl = () => {




  return `/api/web-signals/rules`
}

/**
 * @summary Create a scheduled, provenance-aware ingestion rule
 */
export const createIngestionRule = async (ingestionRuleCreate: IngestionRuleCreate, options?: Parameters<typeof customFetch>[1]): Promise<IngestionRule> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<IngestionRule>(getCreateIngestionRuleUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(ingestionRuleCreate)
  }
);}





export const getCreateIngestionRuleMutationKey = () => ['createIngestionRule'] as const;

export const getCreateIngestionRuleMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createIngestionRule>>, TError,CreateIngestionRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createIngestionRule>>, TError,CreateIngestionRuleMutationVariables, TContext> => {

const mutationKey = getCreateIngestionRuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createIngestionRule>>, CreateIngestionRuleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createIngestionRule(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateIngestionRuleMutationResult = NonNullable<Awaited<ReturnType<typeof createIngestionRule>>>
    export type CreateIngestionRuleMutationBody = IngestionRuleCreate
    export type CreateIngestionRuleMutationError = unknown
    export type CreateIngestionRuleMutationVariables = {data: IngestionRuleCreate}

    /**
 * @summary Create a scheduled, provenance-aware ingestion rule
 */
export const useCreateIngestionRule = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createIngestionRule>>, TError,CreateIngestionRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createIngestionRule>>,
        TError,
        CreateIngestionRuleMutationVariables,
        TContext
      > => {
      return useMutation(getCreateIngestionRuleMutationOptions(options));
    }

export const getUpdateIngestionRuleUrl = (id: string,) => {




  return `/api/web-signals/rules/${id}`
}

export const updateIngestionRule = async (id: string,
    ingestionRuleCreate: IngestionRuleCreate, options?: Parameters<typeof customFetch>[1]): Promise<IngestionRule> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<IngestionRule>(getUpdateIngestionRuleUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(ingestionRuleCreate)
  }
);}





export const getUpdateIngestionRuleMutationKey = () => ['updateIngestionRule'] as const;

export const getUpdateIngestionRuleMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateIngestionRule>>, TError,UpdateIngestionRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateIngestionRule>>, TError,UpdateIngestionRuleMutationVariables, TContext> => {

const mutationKey = getUpdateIngestionRuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateIngestionRule>>, UpdateIngestionRuleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateIngestionRule(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateIngestionRuleMutationResult = NonNullable<Awaited<ReturnType<typeof updateIngestionRule>>>
    export type UpdateIngestionRuleMutationBody = IngestionRuleCreate
    export type UpdateIngestionRuleMutationError = unknown
    export type UpdateIngestionRuleMutationVariables = {id: string;data: IngestionRuleCreate}

    export const useUpdateIngestionRule = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateIngestionRule>>, TError,UpdateIngestionRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateIngestionRule>>,
        TError,
        UpdateIngestionRuleMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateIngestionRuleMutationOptions(options));
    }

export const getRunIngestionRuleUrl = (id: string,) => {




  return `/api/web-signals/rules/${id}/run`
}

/**
 * @summary Fetch and classify every configured source now
 */
export const runIngestionRule = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<RunIngestionRule200> => {

  return customFetch<RunIngestionRule200>(getRunIngestionRuleUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRunIngestionRuleMutationKey = () => ['runIngestionRule'] as const;

export const getRunIngestionRuleMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runIngestionRule>>, TError,RunIngestionRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof runIngestionRule>>, TError,RunIngestionRuleMutationVariables, TContext> => {

const mutationKey = getRunIngestionRuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof runIngestionRule>>, RunIngestionRuleMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  runIngestionRule(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RunIngestionRuleMutationResult = NonNullable<Awaited<ReturnType<typeof runIngestionRule>>>

    export type RunIngestionRuleMutationError = unknown
    export type RunIngestionRuleMutationVariables = {id: string}

    /**
 * @summary Fetch and classify every configured source now
 */
export const useRunIngestionRule = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runIngestionRule>>, TError,RunIngestionRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof runIngestionRule>>,
        TError,
        RunIngestionRuleMutationVariables,
        TContext
      > => {
      return useMutation(getRunIngestionRuleMutationOptions(options));
    }

export const getIngestWebSignalDocumentUrl = (id: string,) => {




  return `/api/web-signals/rules/${id}/documents`
}

/**
 * @summary Ingest a source snapshot delivered by an approved fetch connector
 */
export const ingestWebSignalDocument = async (id: string,
    ingestedDocumentCreate: IngestedDocumentCreate, options?: Parameters<typeof customFetch>[1]): Promise<IngestWebSignalDocument201> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<IngestWebSignalDocument201>(getIngestWebSignalDocumentUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(ingestedDocumentCreate)
  }
);}





export const getIngestWebSignalDocumentMutationKey = () => ['ingestWebSignalDocument'] as const;

export const getIngestWebSignalDocumentMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof ingestWebSignalDocument>>, TError,IngestWebSignalDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof ingestWebSignalDocument>>, TError,IngestWebSignalDocumentMutationVariables, TContext> => {

const mutationKey = getIngestWebSignalDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof ingestWebSignalDocument>>, IngestWebSignalDocumentMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  ingestWebSignalDocument(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type IngestWebSignalDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof ingestWebSignalDocument>>>
    export type IngestWebSignalDocumentMutationBody = IngestedDocumentCreate
    export type IngestWebSignalDocumentMutationError = unknown
    export type IngestWebSignalDocumentMutationVariables = {id: string;data: IngestedDocumentCreate}

    /**
 * @summary Ingest a source snapshot delivered by an approved fetch connector
 */
export const useIngestWebSignalDocument = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof ingestWebSignalDocument>>, TError,IngestWebSignalDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof ingestWebSignalDocument>>,
        TError,
        IngestWebSignalDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getIngestWebSignalDocumentMutationOptions(options));
    }

export const getListIngestedDocumentsUrl = (params?: ListIngestedDocumentsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/documents?${stringifiedParams}` : `/api/web-signals/documents`
}

export const listIngestedDocuments = async (params?: ListIngestedDocumentsParams, options?: Parameters<typeof customFetch>[1]): Promise<ListIngestedDocuments200> => {

  return customFetch<ListIngestedDocuments200>(getListIngestedDocumentsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListIngestedDocumentsQueryKey = (params?: ListIngestedDocumentsParams,) => {
    return [
    `/api/web-signals/documents`, ...(params ? [params] : [])
    ] as const;
    }


export const getListIngestedDocumentsQueryOptions = <TData = Awaited<ReturnType<typeof listIngestedDocuments>>, TError = unknown>(params?: ListIngestedDocumentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listIngestedDocuments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListIngestedDocumentsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listIngestedDocuments>>> = ({ signal }) => listIngestedDocuments(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listIngestedDocuments>>, TError, TData> & { queryKey: QueryKey }
}

export type ListIngestedDocumentsQueryResult = NonNullable<Awaited<ReturnType<typeof listIngestedDocuments>>>
export type ListIngestedDocumentsQueryError = unknown



export function useListIngestedDocuments<TData = Awaited<ReturnType<typeof listIngestedDocuments>>, TError = unknown>(
 params?: ListIngestedDocumentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listIngestedDocuments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListIngestedDocumentsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListWebSignalCandidatesUrl = (params?: ListWebSignalCandidatesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/candidates?${stringifiedParams}` : `/api/web-signals/candidates`
}

export const listWebSignalCandidates = async (params?: ListWebSignalCandidatesParams, options?: Parameters<typeof customFetch>[1]): Promise<ListWebSignalCandidates200> => {

  return customFetch<ListWebSignalCandidates200>(getListWebSignalCandidatesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWebSignalCandidatesQueryKey = (params?: ListWebSignalCandidatesParams,) => {
    return [
    `/api/web-signals/candidates`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWebSignalCandidatesQueryOptions = <TData = Awaited<ReturnType<typeof listWebSignalCandidates>>, TError = unknown>(params?: ListWebSignalCandidatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebSignalCandidates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWebSignalCandidatesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWebSignalCandidates>>> = ({ signal }) => listWebSignalCandidates(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWebSignalCandidates>>, TError, TData> & { queryKey: QueryKey }
}

export type ListWebSignalCandidatesQueryResult = NonNullable<Awaited<ReturnType<typeof listWebSignalCandidates>>>
export type ListWebSignalCandidatesQueryError = unknown



export function useListWebSignalCandidates<TData = Awaited<ReturnType<typeof listWebSignalCandidates>>, TError = unknown>(
 params?: ListWebSignalCandidatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebSignalCandidates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListWebSignalCandidatesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListWebSignalCandidateIndustriesUrl = (params?: ListWebSignalCandidateIndustriesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/candidates/industries?${stringifiedParams}` : `/api/web-signals/candidates/industries`
}

/**
 * @summary Count Review queue candidates per industry, using the same filters as the Review queue
 */
export const listWebSignalCandidateIndustries = async (params?: ListWebSignalCandidateIndustriesParams, options?: Parameters<typeof customFetch>[1]): Promise<WebSignalCandidateIndustryGroupPage> => {

  return customFetch<WebSignalCandidateIndustryGroupPage>(getListWebSignalCandidateIndustriesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWebSignalCandidateIndustriesQueryKey = (params?: ListWebSignalCandidateIndustriesParams,) => {
    return [
    `/api/web-signals/candidates/industries`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWebSignalCandidateIndustriesQueryOptions = <TData = Awaited<ReturnType<typeof listWebSignalCandidateIndustries>>, TError = Error>(params?: ListWebSignalCandidateIndustriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebSignalCandidateIndustries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWebSignalCandidateIndustriesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWebSignalCandidateIndustries>>> = ({ signal }) => listWebSignalCandidateIndustries(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWebSignalCandidateIndustries>>, TError, TData> & { queryKey: QueryKey }
}

export type ListWebSignalCandidateIndustriesQueryResult = NonNullable<Awaited<ReturnType<typeof listWebSignalCandidateIndustries>>>
export type ListWebSignalCandidateIndustriesQueryError = Error


/**
 * @summary Count Review queue candidates per industry, using the same filters as the Review queue
 */

export function useListWebSignalCandidateIndustries<TData = Awaited<ReturnType<typeof listWebSignalCandidateIndustries>>, TError = Error>(
 params?: ListWebSignalCandidateIndustriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebSignalCandidateIndustries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListWebSignalCandidateIndustriesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewWebSignalCandidateUrl = (id: string,) => {




  return `/api/web-signals/candidates/${id}/review`
}

export const reviewWebSignalCandidate = async (id: string,
    webSignalReview: WebSignalReview, options?: Parameters<typeof customFetch>[1]): Promise<WebSignalCandidate> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebSignalCandidate>(getReviewWebSignalCandidateUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(webSignalReview)
  }
);}





export const getReviewWebSignalCandidateMutationKey = () => ['reviewWebSignalCandidate'] as const;

export const getReviewWebSignalCandidateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewWebSignalCandidate>>, TError,ReviewWebSignalCandidateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewWebSignalCandidate>>, TError,ReviewWebSignalCandidateMutationVariables, TContext> => {

const mutationKey = getReviewWebSignalCandidateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewWebSignalCandidate>>, ReviewWebSignalCandidateMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reviewWebSignalCandidate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewWebSignalCandidateMutationResult = NonNullable<Awaited<ReturnType<typeof reviewWebSignalCandidate>>>
    export type ReviewWebSignalCandidateMutationBody = WebSignalReview
    export type ReviewWebSignalCandidateMutationError = unknown
    export type ReviewWebSignalCandidateMutationVariables = {id: string;data: WebSignalReview}

    export const useReviewWebSignalCandidate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewWebSignalCandidate>>, TError,ReviewWebSignalCandidateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewWebSignalCandidate>>,
        TError,
        ReviewWebSignalCandidateMutationVariables,
        TContext
      > => {
      return useMutation(getReviewWebSignalCandidateMutationOptions(options));
    }

export const getGetWebSignalDigestUrl = (params?: GetWebSignalDigestParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/digest?${stringifiedParams}` : `/api/web-signals/digest`
}

export const getWebSignalDigest = async (params?: GetWebSignalDigestParams, options?: Parameters<typeof customFetch>[1]): Promise<WebSignalDigest> => {

  return customFetch<WebSignalDigest>(getGetWebSignalDigestUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWebSignalDigestQueryKey = (params?: GetWebSignalDigestParams,) => {
    return [
    `/api/web-signals/digest`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetWebSignalDigestQueryOptions = <TData = Awaited<ReturnType<typeof getWebSignalDigest>>, TError = unknown>(params?: GetWebSignalDigestParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebSignalDigest>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWebSignalDigestQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWebSignalDigest>>> = ({ signal }) => getWebSignalDigest(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWebSignalDigest>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWebSignalDigestQueryResult = NonNullable<Awaited<ReturnType<typeof getWebSignalDigest>>>
export type GetWebSignalDigestQueryError = unknown



export function useGetWebSignalDigest<TData = Awaited<ReturnType<typeof getWebSignalDigest>>, TError = unknown>(
 params?: GetWebSignalDigestParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebSignalDigest>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWebSignalDigestQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListAccountWebSignalsUrl = (accountId: string,) => {




  return `/api/web-signals/accounts/${accountId}`
}

export const listAccountWebSignals = async (accountId: string, options?: Parameters<typeof customFetch>[1]): Promise<ListAccountWebSignals200> => {

  return customFetch<ListAccountWebSignals200>(getListAccountWebSignalsUrl(accountId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAccountWebSignalsQueryKey = (accountId: string,) => {
    return [
    `/api/web-signals/accounts/${accountId}`
    ] as const;
    }


export const getListAccountWebSignalsQueryOptions = <TData = Awaited<ReturnType<typeof listAccountWebSignals>>, TError = unknown>(accountId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAccountWebSignals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAccountWebSignalsQueryKey(accountId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAccountWebSignals>>> = ({ signal }) => listAccountWebSignals(accountId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: accountId !== null && accountId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAccountWebSignals>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAccountWebSignalsQueryResult = NonNullable<Awaited<ReturnType<typeof listAccountWebSignals>>>
export type ListAccountWebSignalsQueryError = unknown



export function useListAccountWebSignals<TData = Awaited<ReturnType<typeof listAccountWebSignals>>, TError = unknown>(
 accountId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAccountWebSignals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAccountWebSignalsQueryOptions(accountId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListWebDiscoveryTargetingProfilesUrl = (params?: ListWebDiscoveryTargetingProfilesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/discovery/targeting-profiles?${stringifiedParams}` : `/api/web-signals/discovery/targeting-profiles`
}

/**
 * @summary List versioned discovery targeting profiles
 */
export const listWebDiscoveryTargetingProfiles = async (params?: ListWebDiscoveryTargetingProfilesParams, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryTargetingProfilePage> => {

  return customFetch<WebDiscoveryTargetingProfilePage>(getListWebDiscoveryTargetingProfilesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWebDiscoveryTargetingProfilesQueryKey = (params?: ListWebDiscoveryTargetingProfilesParams,) => {
    return [
    `/api/web-signals/discovery/targeting-profiles`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWebDiscoveryTargetingProfilesQueryOptions = <TData = Awaited<ReturnType<typeof listWebDiscoveryTargetingProfiles>>, TError = unknown>(params?: ListWebDiscoveryTargetingProfilesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryTargetingProfiles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWebDiscoveryTargetingProfilesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWebDiscoveryTargetingProfiles>>> = ({ signal }) => listWebDiscoveryTargetingProfiles(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryTargetingProfiles>>, TError, TData> & { queryKey: QueryKey }
}

export type ListWebDiscoveryTargetingProfilesQueryResult = NonNullable<Awaited<ReturnType<typeof listWebDiscoveryTargetingProfiles>>>
export type ListWebDiscoveryTargetingProfilesQueryError = unknown


/**
 * @summary List versioned discovery targeting profiles
 */

export function useListWebDiscoveryTargetingProfiles<TData = Awaited<ReturnType<typeof listWebDiscoveryTargetingProfiles>>, TError = unknown>(
 params?: ListWebDiscoveryTargetingProfilesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryTargetingProfiles>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListWebDiscoveryTargetingProfilesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateWebDiscoveryTargetingProposalUrl = () => {




  return `/api/web-signals/discovery/targeting-profiles/proposals`
}

/**
 * Creates a review-only draft. It never confirms targeting, enables a source, creates CRM records, or starts outreach.
 * @summary Generate an evidence-grounded targeting draft and optional public suggestions
 */
export const createWebDiscoveryTargetingProposal = async (createWebDiscoveryTargetingProposalBody: CreateWebDiscoveryTargetingProposal, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryTargetingProposalResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryTargetingProposalResult>(getCreateWebDiscoveryTargetingProposalUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createWebDiscoveryTargetingProposalBody)
  }
);}





export const getCreateWebDiscoveryTargetingProposalMutationKey = () => ['createWebDiscoveryTargetingProposal'] as const;

export const getCreateWebDiscoveryTargetingProposalMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWebDiscoveryTargetingProposal>>, TError,CreateWebDiscoveryTargetingProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createWebDiscoveryTargetingProposal>>, TError,CreateWebDiscoveryTargetingProposalMutationVariables, TContext> => {

const mutationKey = getCreateWebDiscoveryTargetingProposalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createWebDiscoveryTargetingProposal>>, CreateWebDiscoveryTargetingProposalMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createWebDiscoveryTargetingProposal(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateWebDiscoveryTargetingProposalMutationResult = NonNullable<Awaited<ReturnType<typeof createWebDiscoveryTargetingProposal>>>
    export type CreateWebDiscoveryTargetingProposalMutationBody = CreateWebDiscoveryTargetingProposal
    export type CreateWebDiscoveryTargetingProposalMutationError = Error
    export type CreateWebDiscoveryTargetingProposalMutationVariables = {data: CreateWebDiscoveryTargetingProposal}

    /**
 * @summary Generate an evidence-grounded targeting draft and optional public suggestions
 */
export const useCreateWebDiscoveryTargetingProposal = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWebDiscoveryTargetingProposal>>, TError,CreateWebDiscoveryTargetingProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createWebDiscoveryTargetingProposal>>,
        TError,
        CreateWebDiscoveryTargetingProposalMutationVariables,
        TContext
      > => {
      return useMutation(getCreateWebDiscoveryTargetingProposalMutationOptions(options));
    }

export const getUpdateWebDiscoveryTargetingProfileUrl = (profileId: string,) => {




  return `/api/web-signals/discovery/targeting-profiles/${profileId}`
}

/**
 * @summary Edit one unconfirmed targeting profile revision
 */
export const updateWebDiscoveryTargetingProfile = async (profileId: string,
    updateWebDiscoveryTargetingProfileBody: UpdateWebDiscoveryTargetingProfile, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryTargetingProfile> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryTargetingProfile>(getUpdateWebDiscoveryTargetingProfileUrl(profileId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateWebDiscoveryTargetingProfileBody)
  }
);}





export const getUpdateWebDiscoveryTargetingProfileMutationKey = () => ['updateWebDiscoveryTargetingProfile'] as const;

export const getUpdateWebDiscoveryTargetingProfileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWebDiscoveryTargetingProfile>>, TError,UpdateWebDiscoveryTargetingProfileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateWebDiscoveryTargetingProfile>>, TError,UpdateWebDiscoveryTargetingProfileMutationVariables, TContext> => {

const mutationKey = getUpdateWebDiscoveryTargetingProfileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateWebDiscoveryTargetingProfile>>, UpdateWebDiscoveryTargetingProfileMutationVariables> = (props) => {
          const {profileId,data} = props ?? {};

          return  updateWebDiscoveryTargetingProfile(profileId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateWebDiscoveryTargetingProfileMutationResult = NonNullable<Awaited<ReturnType<typeof updateWebDiscoveryTargetingProfile>>>
    export type UpdateWebDiscoveryTargetingProfileMutationBody = UpdateWebDiscoveryTargetingProfile
    export type UpdateWebDiscoveryTargetingProfileMutationError = Error
    export type UpdateWebDiscoveryTargetingProfileMutationVariables = {profileId: string;data: UpdateWebDiscoveryTargetingProfile}

    /**
 * @summary Edit one unconfirmed targeting profile revision
 */
export const useUpdateWebDiscoveryTargetingProfile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWebDiscoveryTargetingProfile>>, TError,UpdateWebDiscoveryTargetingProfileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateWebDiscoveryTargetingProfile>>,
        TError,
        UpdateWebDiscoveryTargetingProfileMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateWebDiscoveryTargetingProfileMutationOptions(options));
    }

export const getConfirmWebDiscoveryTargetingProfileUrl = (profileId: string,) => {




  return `/api/web-signals/discovery/targeting-profiles/${profileId}/confirm`
}

/**
 * @summary Confirm one exact targeting profile revision
 */
export const confirmWebDiscoveryTargetingProfile = async (profileId: string,
    confirmWebDiscoveryTargetingProfileBody: ConfirmWebDiscoveryTargetingProfile, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryTargetingProfile> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryTargetingProfile>(getConfirmWebDiscoveryTargetingProfileUrl(profileId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(confirmWebDiscoveryTargetingProfileBody)
  }
);}





export const getConfirmWebDiscoveryTargetingProfileMutationKey = () => ['confirmWebDiscoveryTargetingProfile'] as const;

export const getConfirmWebDiscoveryTargetingProfileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmWebDiscoveryTargetingProfile>>, TError,ConfirmWebDiscoveryTargetingProfileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmWebDiscoveryTargetingProfile>>, TError,ConfirmWebDiscoveryTargetingProfileMutationVariables, TContext> => {

const mutationKey = getConfirmWebDiscoveryTargetingProfileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmWebDiscoveryTargetingProfile>>, ConfirmWebDiscoveryTargetingProfileMutationVariables> = (props) => {
          const {profileId,data} = props ?? {};

          return  confirmWebDiscoveryTargetingProfile(profileId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmWebDiscoveryTargetingProfileMutationResult = NonNullable<Awaited<ReturnType<typeof confirmWebDiscoveryTargetingProfile>>>
    export type ConfirmWebDiscoveryTargetingProfileMutationBody = ConfirmWebDiscoveryTargetingProfile
    export type ConfirmWebDiscoveryTargetingProfileMutationError = Error
    export type ConfirmWebDiscoveryTargetingProfileMutationVariables = {profileId: string;data: ConfirmWebDiscoveryTargetingProfile}

    /**
 * @summary Confirm one exact targeting profile revision
 */
export const useConfirmWebDiscoveryTargetingProfile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmWebDiscoveryTargetingProfile>>, TError,ConfirmWebDiscoveryTargetingProfileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmWebDiscoveryTargetingProfile>>,
        TError,
        ConfirmWebDiscoveryTargetingProfileMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmWebDiscoveryTargetingProfileMutationOptions(options));
    }

export const getListWebDiscoverySourcesUrl = (params?: ListWebDiscoverySourcesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/discovery/sources?${stringifiedParams}` : `/api/web-signals/discovery/sources`
}

/**
 * @summary List configured discovery sources without protected session references
 */
export const listWebDiscoverySources = async (params?: ListWebDiscoverySourcesParams, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoverySourcePage> => {

  return customFetch<WebDiscoverySourcePage>(getListWebDiscoverySourcesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWebDiscoverySourcesQueryKey = (params?: ListWebDiscoverySourcesParams,) => {
    return [
    `/api/web-signals/discovery/sources`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWebDiscoverySourcesQueryOptions = <TData = Awaited<ReturnType<typeof listWebDiscoverySources>>, TError = unknown>(params?: ListWebDiscoverySourcesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoverySources>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWebDiscoverySourcesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWebDiscoverySources>>> = ({ signal }) => listWebDiscoverySources(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoverySources>>, TError, TData> & { queryKey: QueryKey }
}

export type ListWebDiscoverySourcesQueryResult = NonNullable<Awaited<ReturnType<typeof listWebDiscoverySources>>>
export type ListWebDiscoverySourcesQueryError = unknown


/**
 * @summary List configured discovery sources without protected session references
 */

export function useListWebDiscoverySources<TData = Awaited<ReturnType<typeof listWebDiscoverySources>>, TError = unknown>(
 params?: ListWebDiscoverySourcesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoverySources>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListWebDiscoverySourcesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateWebDiscoverySourceUrl = () => {




  return `/api/web-signals/discovery/sources`
}

/**
 * @summary Add an inactive discovery source for staff configuration
 */
export const createWebDiscoverySource = async (createWebDiscoverySourceBody: CreateWebDiscoverySource, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoverySource> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoverySource>(getCreateWebDiscoverySourceUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createWebDiscoverySourceBody)
  }
);}





export const getCreateWebDiscoverySourceMutationKey = () => ['createWebDiscoverySource'] as const;

export const getCreateWebDiscoverySourceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWebDiscoverySource>>, TError,CreateWebDiscoverySourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createWebDiscoverySource>>, TError,CreateWebDiscoverySourceMutationVariables, TContext> => {

const mutationKey = getCreateWebDiscoverySourceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createWebDiscoverySource>>, CreateWebDiscoverySourceMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createWebDiscoverySource(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateWebDiscoverySourceMutationResult = NonNullable<Awaited<ReturnType<typeof createWebDiscoverySource>>>
    export type CreateWebDiscoverySourceMutationBody = CreateWebDiscoverySource
    export type CreateWebDiscoverySourceMutationError = Error
    export type CreateWebDiscoverySourceMutationVariables = {data: CreateWebDiscoverySource}

    /**
 * @summary Add an inactive discovery source for staff configuration
 */
export const useCreateWebDiscoverySource = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWebDiscoverySource>>, TError,CreateWebDiscoverySourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createWebDiscoverySource>>,
        TError,
        CreateWebDiscoverySourceMutationVariables,
        TContext
      > => {
      return useMutation(getCreateWebDiscoverySourceMutationOptions(options));
    }

export const getListTenderSourceSuggestionsUrl = () => {




  return `/api/web-signals/discovery/tender-source-suggestions`
}

/**
 * @summary List built-in tender portals that can be monitored in one click, with any existing source
 */
export const listTenderSourceSuggestions = async ( options?: Parameters<typeof customFetch>[1]): Promise<TenderSourceSuggestions> => {

  return customFetch<TenderSourceSuggestions>(getListTenderSourceSuggestionsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderSourceSuggestionsQueryKey = () => {
    return [
    `/api/web-signals/discovery/tender-source-suggestions`
    ] as const;
    }


export const getListTenderSourceSuggestionsQueryOptions = <TData = Awaited<ReturnType<typeof listTenderSourceSuggestions>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderSourceSuggestions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderSourceSuggestionsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderSourceSuggestions>>> = ({ signal }) => listTenderSourceSuggestions({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderSourceSuggestions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderSourceSuggestionsQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderSourceSuggestions>>>
export type ListTenderSourceSuggestionsQueryError = unknown


/**
 * @summary List built-in tender portals that can be monitored in one click, with any existing source
 */

export function useListTenderSourceSuggestions<TData = Awaited<ReturnType<typeof listTenderSourceSuggestions>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderSourceSuggestions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderSourceSuggestionsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateTenderSourceFromSuggestionUrl = () => {




  return `/api/web-signals/discovery/sources/from-suggestion`
}

/**
 * @summary Add a monitored tender portal from a built-in suggestion (idempotent per entity)
 */
export const createTenderSourceFromSuggestion = async (createTenderSourceFromSuggestionBody: CreateTenderSourceFromSuggestion, options?: Parameters<typeof customFetch>[1]): Promise<TenderSourceFromSuggestionResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderSourceFromSuggestionResult>(getCreateTenderSourceFromSuggestionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createTenderSourceFromSuggestionBody)
  }
);}





export const getCreateTenderSourceFromSuggestionMutationKey = () => ['createTenderSourceFromSuggestion'] as const;

export const getCreateTenderSourceFromSuggestionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenderSourceFromSuggestion>>, TError,CreateTenderSourceFromSuggestionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTenderSourceFromSuggestion>>, TError,CreateTenderSourceFromSuggestionMutationVariables, TContext> => {

const mutationKey = getCreateTenderSourceFromSuggestionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTenderSourceFromSuggestion>>, CreateTenderSourceFromSuggestionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createTenderSourceFromSuggestion(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTenderSourceFromSuggestionMutationResult = NonNullable<Awaited<ReturnType<typeof createTenderSourceFromSuggestion>>>
    export type CreateTenderSourceFromSuggestionMutationBody = CreateTenderSourceFromSuggestion
    export type CreateTenderSourceFromSuggestionMutationError = Error
    export type CreateTenderSourceFromSuggestionMutationVariables = {data: CreateTenderSourceFromSuggestion}

    /**
 * @summary Add a monitored tender portal from a built-in suggestion (idempotent per entity)
 */
export const useCreateTenderSourceFromSuggestion = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenderSourceFromSuggestion>>, TError,CreateTenderSourceFromSuggestionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createTenderSourceFromSuggestion>>,
        TError,
        CreateTenderSourceFromSuggestionMutationVariables,
        TContext
      > => {
      return useMutation(getCreateTenderSourceFromSuggestionMutationOptions(options));
    }

export const getRunTenderPortalDiscoveryUrl = () => {




  return `/api/web-signals/discovery/tender-portal-discovery-runs`
}

/**
 * Requires entity.records.write. Reads a bounded number of same-website pages (robots.txt honoured, never signs in), classifies how each tender page can be read and returns up to five sample tenders per portal. Organisation or category seeds use public web search only when it is switched on. Suggestions are never scanned until approved.
 * @summary Find tender-listing portals from a website address, organisation or category and store them as suggestions
 */
export const runTenderPortalDiscovery = async (runTenderPortalDiscoveryBody: RunTenderPortalDiscovery, options?: Parameters<typeof customFetch>[1]): Promise<TenderPortalDiscoveryRun> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderPortalDiscoveryRun>(getRunTenderPortalDiscoveryUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(runTenderPortalDiscoveryBody)
  }
);}





export const getRunTenderPortalDiscoveryMutationKey = () => ['runTenderPortalDiscovery'] as const;

export const getRunTenderPortalDiscoveryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runTenderPortalDiscovery>>, TError,RunTenderPortalDiscoveryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof runTenderPortalDiscovery>>, TError,RunTenderPortalDiscoveryMutationVariables, TContext> => {

const mutationKey = getRunTenderPortalDiscoveryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof runTenderPortalDiscovery>>, RunTenderPortalDiscoveryMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  runTenderPortalDiscovery(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RunTenderPortalDiscoveryMutationResult = NonNullable<Awaited<ReturnType<typeof runTenderPortalDiscovery>>>
    export type RunTenderPortalDiscoveryMutationBody = RunTenderPortalDiscovery
    export type RunTenderPortalDiscoveryMutationError = Error
    export type RunTenderPortalDiscoveryMutationVariables = {data: RunTenderPortalDiscovery}

    /**
 * @summary Find tender-listing portals from a website address, organisation or category and store them as suggestions
 */
export const useRunTenderPortalDiscovery = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runTenderPortalDiscovery>>, TError,RunTenderPortalDiscoveryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof runTenderPortalDiscovery>>,
        TError,
        RunTenderPortalDiscoveryMutationVariables,
        TContext
      > => {
      return useMutation(getRunTenderPortalDiscoveryMutationOptions(options));
    }

export const getListTenderPortalCandidatesUrl = (params?: ListTenderPortalCandidatesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/discovery/tender-portal-candidates?${stringifiedParams}` : `/api/web-signals/discovery/tender-portal-candidates`
}

/**
 * Requires entity.records.read.
 * @summary List discovered tender portal suggestions by review status (paginated)
 */
export const listTenderPortalCandidates = async (params?: ListTenderPortalCandidatesParams, options?: Parameters<typeof customFetch>[1]): Promise<TenderPortalCandidatePage> => {

  return customFetch<TenderPortalCandidatePage>(getListTenderPortalCandidatesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenderPortalCandidatesQueryKey = (params?: ListTenderPortalCandidatesParams,) => {
    return [
    `/api/web-signals/discovery/tender-portal-candidates`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenderPortalCandidatesQueryOptions = <TData = Awaited<ReturnType<typeof listTenderPortalCandidates>>, TError = Error>(params?: ListTenderPortalCandidatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderPortalCandidates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenderPortalCandidatesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenderPortalCandidates>>> = ({ signal }) => listTenderPortalCandidates(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenderPortalCandidates>>, TError, TData> & { queryKey: QueryKey }
}

export type ListTenderPortalCandidatesQueryResult = NonNullable<Awaited<ReturnType<typeof listTenderPortalCandidates>>>
export type ListTenderPortalCandidatesQueryError = Error


/**
 * @summary List discovered tender portal suggestions by review status (paginated)
 */

export function useListTenderPortalCandidates<TData = Awaited<ReturnType<typeof listTenderPortalCandidates>>, TError = Error>(
 params?: ListTenderPortalCandidatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listTenderPortalCandidates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListTenderPortalCandidatesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getApproveTenderPortalCandidateUrl = (candidateId: string,) => {




  return `/api/web-signals/discovery/tender-portal-candidates/${candidateId}/approve`
}

/**
 * Requires entity.integrations.manage, the permission used to add tender portals. Sign-in and unreadable pages cannot be approved.
 * @summary Approve a discovered portal; it becomes a tender source in the Tender portals list
 */
export const approveTenderPortalCandidate = async (candidateId: string,
    reviewTenderPortalCandidate: ReviewTenderPortalCandidate, options?: Parameters<typeof customFetch>[1]): Promise<TenderPortalCandidateApproval> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderPortalCandidateApproval>(getApproveTenderPortalCandidateUrl(candidateId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewTenderPortalCandidate)
  }
);}





export const getApproveTenderPortalCandidateMutationKey = () => ['approveTenderPortalCandidate'] as const;

export const getApproveTenderPortalCandidateMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveTenderPortalCandidate>>, TError,ApproveTenderPortalCandidateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof approveTenderPortalCandidate>>, TError,ApproveTenderPortalCandidateMutationVariables, TContext> => {

const mutationKey = getApproveTenderPortalCandidateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof approveTenderPortalCandidate>>, ApproveTenderPortalCandidateMutationVariables> = (props) => {
          const {candidateId,data} = props ?? {};

          return  approveTenderPortalCandidate(candidateId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApproveTenderPortalCandidateMutationResult = NonNullable<Awaited<ReturnType<typeof approveTenderPortalCandidate>>>
    export type ApproveTenderPortalCandidateMutationBody = ReviewTenderPortalCandidate
    export type ApproveTenderPortalCandidateMutationError = Error
    export type ApproveTenderPortalCandidateMutationVariables = {candidateId: string;data: ReviewTenderPortalCandidate}

    /**
 * @summary Approve a discovered portal; it becomes a tender source in the Tender portals list
 */
export const useApproveTenderPortalCandidate = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveTenderPortalCandidate>>, TError,ApproveTenderPortalCandidateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof approveTenderPortalCandidate>>,
        TError,
        ApproveTenderPortalCandidateMutationVariables,
        TContext
      > => {
      return useMutation(getApproveTenderPortalCandidateMutationOptions(options));
    }

export const getRejectTenderPortalCandidateUrl = (candidateId: string,) => {




  return `/api/web-signals/discovery/tender-portal-candidates/${candidateId}/reject`
}

/**
 * Requires entity.integrations.manage.
 * @summary Reject a discovered portal; it stays rejected when found again and is never scanned
 */
export const rejectTenderPortalCandidate = async (candidateId: string,
    reviewTenderPortalCandidate: ReviewTenderPortalCandidate, options?: Parameters<typeof customFetch>[1]): Promise<TenderPortalCandidate> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderPortalCandidate>(getRejectTenderPortalCandidateUrl(candidateId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewTenderPortalCandidate)
  }
);}





export const getRejectTenderPortalCandidateMutationKey = () => ['rejectTenderPortalCandidate'] as const;

export const getRejectTenderPortalCandidateMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rejectTenderPortalCandidate>>, TError,RejectTenderPortalCandidateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof rejectTenderPortalCandidate>>, TError,RejectTenderPortalCandidateMutationVariables, TContext> => {

const mutationKey = getRejectTenderPortalCandidateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof rejectTenderPortalCandidate>>, RejectTenderPortalCandidateMutationVariables> = (props) => {
          const {candidateId,data} = props ?? {};

          return  rejectTenderPortalCandidate(candidateId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RejectTenderPortalCandidateMutationResult = NonNullable<Awaited<ReturnType<typeof rejectTenderPortalCandidate>>>
    export type RejectTenderPortalCandidateMutationBody = ReviewTenderPortalCandidate
    export type RejectTenderPortalCandidateMutationError = Error
    export type RejectTenderPortalCandidateMutationVariables = {candidateId: string;data: ReviewTenderPortalCandidate}

    /**
 * @summary Reject a discovered portal; it stays rejected when found again and is never scanned
 */
export const useRejectTenderPortalCandidate = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rejectTenderPortalCandidate>>, TError,RejectTenderPortalCandidateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof rejectTenderPortalCandidate>>,
        TError,
        RejectTenderPortalCandidateMutationVariables,
        TContext
      > => {
      return useMutation(getRejectTenderPortalCandidateMutationOptions(options));
    }

export const getRunTenderSourceScanUrl = (sourceId: string,) => {




  return `/api/web-signals/discovery/sources/${sourceId}/tender-scan`
}

/**
 * @summary Run an active tender portal source now; results appear in Tender opportunities
 */
export const runTenderSourceScan = async (sourceId: string,
    runTenderSourceScanBody: RunTenderSourceScan, options?: Parameters<typeof customFetch>[1]): Promise<TenderSourceScanRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenderSourceScanRequest>(getRunTenderSourceScanUrl(sourceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(runTenderSourceScanBody)
  }
);}





export const getRunTenderSourceScanMutationKey = () => ['runTenderSourceScan'] as const;

export const getRunTenderSourceScanMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runTenderSourceScan>>, TError,RunTenderSourceScanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof runTenderSourceScan>>, TError,RunTenderSourceScanMutationVariables, TContext> => {

const mutationKey = getRunTenderSourceScanMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof runTenderSourceScan>>, RunTenderSourceScanMutationVariables> = (props) => {
          const {sourceId,data} = props ?? {};

          return  runTenderSourceScan(sourceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RunTenderSourceScanMutationResult = NonNullable<Awaited<ReturnType<typeof runTenderSourceScan>>>
    export type RunTenderSourceScanMutationBody = RunTenderSourceScan
    export type RunTenderSourceScanMutationError = Error
    export type RunTenderSourceScanMutationVariables = {sourceId: string;data: RunTenderSourceScan}

    /**
 * @summary Run an active tender portal source now; results appear in Tender opportunities
 */
export const useRunTenderSourceScan = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runTenderSourceScan>>, TError,RunTenderSourceScanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof runTenderSourceScan>>,
        TError,
        RunTenderSourceScanMutationVariables,
        TContext
      > => {
      return useMutation(getRunTenderSourceScanMutationOptions(options));
    }

export const getDeleteTenderDiscoverySourceUrl = (sourceId: string,
    params: DeleteTenderDiscoverySourceParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/discovery/sources/${sourceId}?${stringifiedParams}` : `/api/web-signals/discovery/sources/${sourceId}`
}

/**
 * @summary Remove a tender portal source; opportunities it found are kept
 */
export const deleteTenderDiscoverySource = async (sourceId: string,
    params: DeleteTenderDiscoverySourceParams, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteTenderDiscoverySourceUrl(sourceId,params),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteTenderDiscoverySourceMutationKey = () => ['deleteTenderDiscoverySource'] as const;

export const getDeleteTenderDiscoverySourceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTenderDiscoverySource>>, TError,DeleteTenderDiscoverySourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteTenderDiscoverySource>>, TError,DeleteTenderDiscoverySourceMutationVariables, TContext> => {

const mutationKey = getDeleteTenderDiscoverySourceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteTenderDiscoverySource>>, DeleteTenderDiscoverySourceMutationVariables> = (props) => {
          const {sourceId,params} = props ?? {};

          return  deleteTenderDiscoverySource(sourceId,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteTenderDiscoverySourceMutationResult = NonNullable<Awaited<ReturnType<typeof deleteTenderDiscoverySource>>>

    export type DeleteTenderDiscoverySourceMutationError = Error
    export type DeleteTenderDiscoverySourceMutationVariables = {sourceId: string;params: DeleteTenderDiscoverySourceParams}

    /**
 * @summary Remove a tender portal source; opportunities it found are kept
 */
export const useDeleteTenderDiscoverySource = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTenderDiscoverySource>>, TError,DeleteTenderDiscoverySourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteTenderDiscoverySource>>,
        TError,
        DeleteTenderDiscoverySourceMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteTenderDiscoverySourceMutationOptions(options));
    }

export const getUpdateWebDiscoverySourceUrl = (sourceId: string,) => {




  return `/api/web-signals/discovery/sources/${sourceId}`
}

/**
 * @summary Edit, activate, or pause a discovery source with optimistic revision checking
 */
export const updateWebDiscoverySource = async (sourceId: string,
    updateWebDiscoverySourceBody: UpdateWebDiscoverySource, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoverySource> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoverySource>(getUpdateWebDiscoverySourceUrl(sourceId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateWebDiscoverySourceBody)
  }
);}





export const getUpdateWebDiscoverySourceMutationKey = () => ['updateWebDiscoverySource'] as const;

export const getUpdateWebDiscoverySourceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWebDiscoverySource>>, TError,UpdateWebDiscoverySourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateWebDiscoverySource>>, TError,UpdateWebDiscoverySourceMutationVariables, TContext> => {

const mutationKey = getUpdateWebDiscoverySourceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateWebDiscoverySource>>, UpdateWebDiscoverySourceMutationVariables> = (props) => {
          const {sourceId,data} = props ?? {};

          return  updateWebDiscoverySource(sourceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateWebDiscoverySourceMutationResult = NonNullable<Awaited<ReturnType<typeof updateWebDiscoverySource>>>
    export type UpdateWebDiscoverySourceMutationBody = UpdateWebDiscoverySource
    export type UpdateWebDiscoverySourceMutationError = Error
    export type UpdateWebDiscoverySourceMutationVariables = {sourceId: string;data: UpdateWebDiscoverySource}

    /**
 * @summary Edit, activate, or pause a discovery source with optimistic revision checking
 */
export const useUpdateWebDiscoverySource = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWebDiscoverySource>>, TError,UpdateWebDiscoverySourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateWebDiscoverySource>>,
        TError,
        UpdateWebDiscoverySourceMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateWebDiscoverySourceMutationOptions(options));
    }

export const getStartWebDiscoverySignInSessionUrl = (sourceId: string,) => {




  return `/api/web-signals/discovery/sources/${sourceId}/sign-in-sessions`
}

/**
 * @summary Start an actor-bound isolated sign-in or renewal viewer for a restricted source
 */
export const startWebDiscoverySignInSession = async (sourceId: string,
    startWebDiscoverySignInSessionBody: StartWebDiscoverySignInSession, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoverySignInSession> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoverySignInSession>(getStartWebDiscoverySignInSessionUrl(sourceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(startWebDiscoverySignInSessionBody)
  }
);}





export const getStartWebDiscoverySignInSessionMutationKey = () => ['startWebDiscoverySignInSession'] as const;

export const getStartWebDiscoverySignInSessionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startWebDiscoverySignInSession>>, TError,StartWebDiscoverySignInSessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof startWebDiscoverySignInSession>>, TError,StartWebDiscoverySignInSessionMutationVariables, TContext> => {

const mutationKey = getStartWebDiscoverySignInSessionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof startWebDiscoverySignInSession>>, StartWebDiscoverySignInSessionMutationVariables> = (props) => {
          const {sourceId,data} = props ?? {};

          return  startWebDiscoverySignInSession(sourceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type StartWebDiscoverySignInSessionMutationResult = NonNullable<Awaited<ReturnType<typeof startWebDiscoverySignInSession>>>
    export type StartWebDiscoverySignInSessionMutationBody = StartWebDiscoverySignInSession
    export type StartWebDiscoverySignInSessionMutationError = Error
    export type StartWebDiscoverySignInSessionMutationVariables = {sourceId: string;data: StartWebDiscoverySignInSession}

    /**
 * @summary Start an actor-bound isolated sign-in or renewal viewer for a restricted source
 */
export const useStartWebDiscoverySignInSession = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startWebDiscoverySignInSession>>, TError,StartWebDiscoverySignInSessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof startWebDiscoverySignInSession>>,
        TError,
        StartWebDiscoverySignInSessionMutationVariables,
        TContext
      > => {
      return useMutation(getStartWebDiscoverySignInSessionMutationOptions(options));
    }

export const getGetWebDiscoverySignInSessionUrl = (sourceId: string,
    sessionId: string,) => {




  return `/api/web-signals/discovery/sources/${sourceId}/sign-in-sessions/${sessionId}`
}

/**
 * @summary Inspect one actor-bound sign-in session without protected browser state
 */
export const getWebDiscoverySignInSession = async (sourceId: string,
    sessionId: string, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoverySignInSession> => {

  return customFetch<WebDiscoverySignInSession>(getGetWebDiscoverySignInSessionUrl(sourceId,sessionId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWebDiscoverySignInSessionQueryKey = (sourceId: string,
    sessionId: string,) => {
    return [
    `/api/web-signals/discovery/sources/${sourceId}/sign-in-sessions/${sessionId}`
    ] as const;
    }


export const getGetWebDiscoverySignInSessionQueryOptions = <TData = Awaited<ReturnType<typeof getWebDiscoverySignInSession>>, TError = Error>(sourceId: string,
    sessionId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoverySignInSession>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWebDiscoverySignInSessionQueryKey(sourceId,sessionId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWebDiscoverySignInSession>>> = ({ signal }) => getWebDiscoverySignInSession(sourceId,sessionId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: sourceId !== null && sourceId !== undefined && sessionId !== null && sessionId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoverySignInSession>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWebDiscoverySignInSessionQueryResult = NonNullable<Awaited<ReturnType<typeof getWebDiscoverySignInSession>>>
export type GetWebDiscoverySignInSessionQueryError = Error


/**
 * @summary Inspect one actor-bound sign-in session without protected browser state
 */

export function useGetWebDiscoverySignInSession<TData = Awaited<ReturnType<typeof getWebDiscoverySignInSession>>, TError = Error>(
 sourceId: string,
    sessionId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoverySignInSession>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWebDiscoverySignInSessionQueryOptions(sourceId,sessionId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetWebDiscoverySignInViewerFrameUrl = (sourceId: string,
    sessionId: string,) => {




  return `/api/web-signals/discovery/sources/${sourceId}/sign-in-sessions/${sessionId}/viewer/frame`
}

/**
 * @summary Read one transient no-store viewer frame for direct staff interaction
 */
export const getWebDiscoverySignInViewerFrame = async (sourceId: string,
    sessionId: string, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoverySignInViewerFrame> => {

  return customFetch<WebDiscoverySignInViewerFrame>(getGetWebDiscoverySignInViewerFrameUrl(sourceId,sessionId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWebDiscoverySignInViewerFrameQueryKey = (sourceId: string,
    sessionId: string,) => {
    return [
    `/api/web-signals/discovery/sources/${sourceId}/sign-in-sessions/${sessionId}/viewer/frame`
    ] as const;
    }


export const getGetWebDiscoverySignInViewerFrameQueryOptions = <TData = Awaited<ReturnType<typeof getWebDiscoverySignInViewerFrame>>, TError = Error>(sourceId: string,
    sessionId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoverySignInViewerFrame>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWebDiscoverySignInViewerFrameQueryKey(sourceId,sessionId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWebDiscoverySignInViewerFrame>>> = ({ signal }) => getWebDiscoverySignInViewerFrame(sourceId,sessionId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: sourceId !== null && sourceId !== undefined && sessionId !== null && sessionId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoverySignInViewerFrame>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWebDiscoverySignInViewerFrameQueryResult = NonNullable<Awaited<ReturnType<typeof getWebDiscoverySignInViewerFrame>>>
export type GetWebDiscoverySignInViewerFrameQueryError = Error


/**
 * @summary Read one transient no-store viewer frame for direct staff interaction
 */

export function useGetWebDiscoverySignInViewerFrame<TData = Awaited<ReturnType<typeof getWebDiscoverySignInViewerFrame>>, TError = Error>(
 sourceId: string,
    sessionId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoverySignInViewerFrame>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWebDiscoverySignInViewerFrameQueryOptions(sourceId,sessionId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSendWebDiscoverySignInViewerInputUrl = (sourceId: string,
    sessionId: string,) => {




  return `/api/web-signals/discovery/sources/${sourceId}/sign-in-sessions/${sessionId}/viewer/input`
}

/**
 * @summary Send bounded direct staff input to the isolated viewer without retaining keystrokes
 */
export const sendWebDiscoverySignInViewerInput = async (sourceId: string,
    sessionId: string,
    webDiscoverySignInViewerInput: WebDiscoverySignInViewerInput, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoverySignInSession> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoverySignInSession>(getSendWebDiscoverySignInViewerInputUrl(sourceId,sessionId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(webDiscoverySignInViewerInput)
  }
);}





export const getSendWebDiscoverySignInViewerInputMutationKey = () => ['sendWebDiscoverySignInViewerInput'] as const;

export const getSendWebDiscoverySignInViewerInputMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendWebDiscoverySignInViewerInput>>, TError,SendWebDiscoverySignInViewerInputMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof sendWebDiscoverySignInViewerInput>>, TError,SendWebDiscoverySignInViewerInputMutationVariables, TContext> => {

const mutationKey = getSendWebDiscoverySignInViewerInputMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof sendWebDiscoverySignInViewerInput>>, SendWebDiscoverySignInViewerInputMutationVariables> = (props) => {
          const {sourceId,sessionId,data} = props ?? {};

          return  sendWebDiscoverySignInViewerInput(sourceId,sessionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SendWebDiscoverySignInViewerInputMutationResult = NonNullable<Awaited<ReturnType<typeof sendWebDiscoverySignInViewerInput>>>
    export type SendWebDiscoverySignInViewerInputMutationBody = WebDiscoverySignInViewerInput
    export type SendWebDiscoverySignInViewerInputMutationError = Error
    export type SendWebDiscoverySignInViewerInputMutationVariables = {sourceId: string;sessionId: string;data: WebDiscoverySignInViewerInput}

    /**
 * @summary Send bounded direct staff input to the isolated viewer without retaining keystrokes
 */
export const useSendWebDiscoverySignInViewerInput = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendWebDiscoverySignInViewerInput>>, TError,SendWebDiscoverySignInViewerInputMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof sendWebDiscoverySignInViewerInput>>,
        TError,
        SendWebDiscoverySignInViewerInputMutationVariables,
        TContext
      > => {
      return useMutation(getSendWebDiscoverySignInViewerInputMutationOptions(options));
    }

export const getCompleteWebDiscoverySignInSessionUrl = (sourceId: string,
    sessionId: string,) => {




  return `/api/web-signals/discovery/sources/${sourceId}/sign-in-sessions/${sessionId}/complete`
}

/**
 * @summary Validate sign-in and encrypt reusable state from one exact viewer revision
 */
export const completeWebDiscoverySignInSession = async (sourceId: string,
    sessionId: string,
    transitionWebDiscoverySignInSession: TransitionWebDiscoverySignInSession, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoverySignInSession> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoverySignInSession>(getCompleteWebDiscoverySignInSessionUrl(sourceId,sessionId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(transitionWebDiscoverySignInSession)
  }
);}





export const getCompleteWebDiscoverySignInSessionMutationKey = () => ['completeWebDiscoverySignInSession'] as const;

export const getCompleteWebDiscoverySignInSessionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof completeWebDiscoverySignInSession>>, TError,CompleteWebDiscoverySignInSessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof completeWebDiscoverySignInSession>>, TError,CompleteWebDiscoverySignInSessionMutationVariables, TContext> => {

const mutationKey = getCompleteWebDiscoverySignInSessionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof completeWebDiscoverySignInSession>>, CompleteWebDiscoverySignInSessionMutationVariables> = (props) => {
          const {sourceId,sessionId,data} = props ?? {};

          return  completeWebDiscoverySignInSession(sourceId,sessionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CompleteWebDiscoverySignInSessionMutationResult = NonNullable<Awaited<ReturnType<typeof completeWebDiscoverySignInSession>>>
    export type CompleteWebDiscoverySignInSessionMutationBody = TransitionWebDiscoverySignInSession
    export type CompleteWebDiscoverySignInSessionMutationError = Error
    export type CompleteWebDiscoverySignInSessionMutationVariables = {sourceId: string;sessionId: string;data: TransitionWebDiscoverySignInSession}

    /**
 * @summary Validate sign-in and encrypt reusable state from one exact viewer revision
 */
export const useCompleteWebDiscoverySignInSession = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof completeWebDiscoverySignInSession>>, TError,CompleteWebDiscoverySignInSessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof completeWebDiscoverySignInSession>>,
        TError,
        CompleteWebDiscoverySignInSessionMutationVariables,
        TContext
      > => {
      return useMutation(getCompleteWebDiscoverySignInSessionMutationOptions(options));
    }

export const getCancelWebDiscoverySignInSessionUrl = (sourceId: string,
    sessionId: string,) => {




  return `/api/web-signals/discovery/sources/${sourceId}/sign-in-sessions/${sessionId}/cancel`
}

/**
 * @summary Cancel and dispose one actor-bound sign-in viewer
 */
export const cancelWebDiscoverySignInSession = async (sourceId: string,
    sessionId: string,
    transitionWebDiscoverySignInSession: TransitionWebDiscoverySignInSession, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoverySignInSession> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoverySignInSession>(getCancelWebDiscoverySignInSessionUrl(sourceId,sessionId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(transitionWebDiscoverySignInSession)
  }
);}





export const getCancelWebDiscoverySignInSessionMutationKey = () => ['cancelWebDiscoverySignInSession'] as const;

export const getCancelWebDiscoverySignInSessionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelWebDiscoverySignInSession>>, TError,CancelWebDiscoverySignInSessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelWebDiscoverySignInSession>>, TError,CancelWebDiscoverySignInSessionMutationVariables, TContext> => {

const mutationKey = getCancelWebDiscoverySignInSessionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelWebDiscoverySignInSession>>, CancelWebDiscoverySignInSessionMutationVariables> = (props) => {
          const {sourceId,sessionId,data} = props ?? {};

          return  cancelWebDiscoverySignInSession(sourceId,sessionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelWebDiscoverySignInSessionMutationResult = NonNullable<Awaited<ReturnType<typeof cancelWebDiscoverySignInSession>>>
    export type CancelWebDiscoverySignInSessionMutationBody = TransitionWebDiscoverySignInSession
    export type CancelWebDiscoverySignInSessionMutationError = unknown
    export type CancelWebDiscoverySignInSessionMutationVariables = {sourceId: string;sessionId: string;data: TransitionWebDiscoverySignInSession}

    /**
 * @summary Cancel and dispose one actor-bound sign-in viewer
 */
export const useCancelWebDiscoverySignInSession = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelWebDiscoverySignInSession>>, TError,CancelWebDiscoverySignInSessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelWebDiscoverySignInSession>>,
        TError,
        CancelWebDiscoverySignInSessionMutationVariables,
        TContext
      > => {
      return useMutation(getCancelWebDiscoverySignInSessionMutationOptions(options));
    }

export const getRevokeWebDiscoverySourceSessionUrl = (sourceId: string,) => {




  return `/api/web-signals/discovery/sources/${sourceId}/session/revoke`
}

/**
 * @summary Revoke reusable browser state and active viewers for a restricted source
 */
export const revokeWebDiscoverySourceSession = async (sourceId: string,
    revokeWebDiscoverySourceSessionBody: RevokeWebDiscoverySourceSession, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoverySource> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoverySource>(getRevokeWebDiscoverySourceSessionUrl(sourceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(revokeWebDiscoverySourceSessionBody)
  }
);}





export const getRevokeWebDiscoverySourceSessionMutationKey = () => ['revokeWebDiscoverySourceSession'] as const;

export const getRevokeWebDiscoverySourceSessionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeWebDiscoverySourceSession>>, TError,RevokeWebDiscoverySourceSessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokeWebDiscoverySourceSession>>, TError,RevokeWebDiscoverySourceSessionMutationVariables, TContext> => {

const mutationKey = getRevokeWebDiscoverySourceSessionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokeWebDiscoverySourceSession>>, RevokeWebDiscoverySourceSessionMutationVariables> = (props) => {
          const {sourceId,data} = props ?? {};

          return  revokeWebDiscoverySourceSession(sourceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokeWebDiscoverySourceSessionMutationResult = NonNullable<Awaited<ReturnType<typeof revokeWebDiscoverySourceSession>>>
    export type RevokeWebDiscoverySourceSessionMutationBody = RevokeWebDiscoverySourceSession
    export type RevokeWebDiscoverySourceSessionMutationError = Error
    export type RevokeWebDiscoverySourceSessionMutationVariables = {sourceId: string;data: RevokeWebDiscoverySourceSession}

    /**
 * @summary Revoke reusable browser state and active viewers for a restricted source
 */
export const useRevokeWebDiscoverySourceSession = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeWebDiscoverySourceSession>>, TError,RevokeWebDiscoverySourceSessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof revokeWebDiscoverySourceSession>>,
        TError,
        RevokeWebDiscoverySourceSessionMutationVariables,
        TContext
      > => {
      return useMutation(getRevokeWebDiscoverySourceSessionMutationOptions(options));
    }

export const getListWebDiscoveryRecipesUrl = (sourceId: string,
    params?: ListWebDiscoveryRecipesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/discovery/sources/${sourceId}/recipes?${stringifiedParams}` : `/api/web-signals/discovery/sources/${sourceId}/recipes`
}

/**
 * @summary List immutable recipe versions for one discovery source
 */
export const listWebDiscoveryRecipes = async (sourceId: string,
    params?: ListWebDiscoveryRecipesParams, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryRecipePage> => {

  return customFetch<WebDiscoveryRecipePage>(getListWebDiscoveryRecipesUrl(sourceId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWebDiscoveryRecipesQueryKey = (sourceId: string,
    params?: ListWebDiscoveryRecipesParams,) => {
    return [
    `/api/web-signals/discovery/sources/${sourceId}/recipes`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWebDiscoveryRecipesQueryOptions = <TData = Awaited<ReturnType<typeof listWebDiscoveryRecipes>>, TError = unknown>(sourceId: string,
    params?: ListWebDiscoveryRecipesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryRecipes>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWebDiscoveryRecipesQueryKey(sourceId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWebDiscoveryRecipes>>> = ({ signal }) => listWebDiscoveryRecipes(sourceId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: sourceId !== null && sourceId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryRecipes>>, TError, TData> & { queryKey: QueryKey }
}

export type ListWebDiscoveryRecipesQueryResult = NonNullable<Awaited<ReturnType<typeof listWebDiscoveryRecipes>>>
export type ListWebDiscoveryRecipesQueryError = unknown


/**
 * @summary List immutable recipe versions for one discovery source
 */

export function useListWebDiscoveryRecipes<TData = Awaited<ReturnType<typeof listWebDiscoveryRecipes>>, TError = unknown>(
 sourceId: string,
    params?: ListWebDiscoveryRecipesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryRecipes>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListWebDiscoveryRecipesQueryOptions(sourceId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateWebDiscoveryRecipeUrl = (sourceId: string,) => {




  return `/api/web-signals/discovery/sources/${sourceId}/recipes`
}

/**
 * @summary Create an editable bounded declarative recipe draft
 */
export const createWebDiscoveryRecipe = async (sourceId: string,
    createWebDiscoveryRecipeBody: CreateWebDiscoveryRecipe, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryRecipe> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryRecipe>(getCreateWebDiscoveryRecipeUrl(sourceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createWebDiscoveryRecipeBody)
  }
);}





export const getCreateWebDiscoveryRecipeMutationKey = () => ['createWebDiscoveryRecipe'] as const;

export const getCreateWebDiscoveryRecipeMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWebDiscoveryRecipe>>, TError,CreateWebDiscoveryRecipeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createWebDiscoveryRecipe>>, TError,CreateWebDiscoveryRecipeMutationVariables, TContext> => {

const mutationKey = getCreateWebDiscoveryRecipeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createWebDiscoveryRecipe>>, CreateWebDiscoveryRecipeMutationVariables> = (props) => {
          const {sourceId,data} = props ?? {};

          return  createWebDiscoveryRecipe(sourceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateWebDiscoveryRecipeMutationResult = NonNullable<Awaited<ReturnType<typeof createWebDiscoveryRecipe>>>
    export type CreateWebDiscoveryRecipeMutationBody = CreateWebDiscoveryRecipe
    export type CreateWebDiscoveryRecipeMutationError = Error
    export type CreateWebDiscoveryRecipeMutationVariables = {sourceId: string;data: CreateWebDiscoveryRecipe}

    /**
 * @summary Create an editable bounded declarative recipe draft
 */
export const useCreateWebDiscoveryRecipe = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWebDiscoveryRecipe>>, TError,CreateWebDiscoveryRecipeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createWebDiscoveryRecipe>>,
        TError,
        CreateWebDiscoveryRecipeMutationVariables,
        TContext
      > => {
      return useMutation(getCreateWebDiscoveryRecipeMutationOptions(options));
    }

export const getUpdateWebDiscoveryRecipeUrl = (sourceId: string,
    recipeId: string,) => {




  return `/api/web-signals/discovery/sources/${sourceId}/recipes/${recipeId}`
}

/**
 * @summary Edit one unconfirmed recipe draft
 */
export const updateWebDiscoveryRecipe = async (sourceId: string,
    recipeId: string,
    updateWebDiscoveryRecipeBody: UpdateWebDiscoveryRecipe, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryRecipe> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryRecipe>(getUpdateWebDiscoveryRecipeUrl(sourceId,recipeId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateWebDiscoveryRecipeBody)
  }
);}





export const getUpdateWebDiscoveryRecipeMutationKey = () => ['updateWebDiscoveryRecipe'] as const;

export const getUpdateWebDiscoveryRecipeMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWebDiscoveryRecipe>>, TError,UpdateWebDiscoveryRecipeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateWebDiscoveryRecipe>>, TError,UpdateWebDiscoveryRecipeMutationVariables, TContext> => {

const mutationKey = getUpdateWebDiscoveryRecipeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateWebDiscoveryRecipe>>, UpdateWebDiscoveryRecipeMutationVariables> = (props) => {
          const {sourceId,recipeId,data} = props ?? {};

          return  updateWebDiscoveryRecipe(sourceId,recipeId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateWebDiscoveryRecipeMutationResult = NonNullable<Awaited<ReturnType<typeof updateWebDiscoveryRecipe>>>
    export type UpdateWebDiscoveryRecipeMutationBody = UpdateWebDiscoveryRecipe
    export type UpdateWebDiscoveryRecipeMutationError = Error
    export type UpdateWebDiscoveryRecipeMutationVariables = {sourceId: string;recipeId: string;data: UpdateWebDiscoveryRecipe}

    /**
 * @summary Edit one unconfirmed recipe draft
 */
export const useUpdateWebDiscoveryRecipe = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWebDiscoveryRecipe>>, TError,UpdateWebDiscoveryRecipeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateWebDiscoveryRecipe>>,
        TError,
        UpdateWebDiscoveryRecipeMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateWebDiscoveryRecipeMutationOptions(options));
    }

export const getPreviewWebDiscoveryRecipeUrl = (sourceId: string,
    recipeId: string,) => {




  return `/api/web-signals/discovery/sources/${sourceId}/recipes/${recipeId}/preview`
}

/**
 * @summary Execute a bounded real-page readiness preview for one exact draft revision
 */
export const previewWebDiscoveryRecipe = async (sourceId: string,
    recipeId: string,
    previewWebDiscoveryRecipeBody: PreviewWebDiscoveryRecipe, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryRecipePreview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryRecipePreview>(getPreviewWebDiscoveryRecipeUrl(sourceId,recipeId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(previewWebDiscoveryRecipeBody)
  }
);}





export const getPreviewWebDiscoveryRecipeMutationKey = () => ['previewWebDiscoveryRecipe'] as const;

export const getPreviewWebDiscoveryRecipeMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewWebDiscoveryRecipe>>, TError,PreviewWebDiscoveryRecipeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewWebDiscoveryRecipe>>, TError,PreviewWebDiscoveryRecipeMutationVariables, TContext> => {

const mutationKey = getPreviewWebDiscoveryRecipeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewWebDiscoveryRecipe>>, PreviewWebDiscoveryRecipeMutationVariables> = (props) => {
          const {sourceId,recipeId,data} = props ?? {};

          return  previewWebDiscoveryRecipe(sourceId,recipeId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewWebDiscoveryRecipeMutationResult = NonNullable<Awaited<ReturnType<typeof previewWebDiscoveryRecipe>>>
    export type PreviewWebDiscoveryRecipeMutationBody = PreviewWebDiscoveryRecipe
    export type PreviewWebDiscoveryRecipeMutationError = Error
    export type PreviewWebDiscoveryRecipeMutationVariables = {sourceId: string;recipeId: string;data: PreviewWebDiscoveryRecipe}

    /**
 * @summary Execute a bounded real-page readiness preview for one exact draft revision
 */
export const usePreviewWebDiscoveryRecipe = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewWebDiscoveryRecipe>>, TError,PreviewWebDiscoveryRecipeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewWebDiscoveryRecipe>>,
        TError,
        PreviewWebDiscoveryRecipeMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewWebDiscoveryRecipeMutationOptions(options));
    }

export const getConfirmWebDiscoveryRecipeUrl = (sourceId: string,
    recipeId: string,) => {




  return `/api/web-signals/discovery/sources/${sourceId}/recipes/${recipeId}/confirm`
}

/**
 * @summary Confirm one exact recipe revision after a successful current-source preview
 */
export const confirmWebDiscoveryRecipe = async (sourceId: string,
    recipeId: string,
    confirmWebDiscoveryRecipeBody: ConfirmWebDiscoveryRecipe, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryRecipe> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryRecipe>(getConfirmWebDiscoveryRecipeUrl(sourceId,recipeId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(confirmWebDiscoveryRecipeBody)
  }
);}





export const getConfirmWebDiscoveryRecipeMutationKey = () => ['confirmWebDiscoveryRecipe'] as const;

export const getConfirmWebDiscoveryRecipeMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmWebDiscoveryRecipe>>, TError,ConfirmWebDiscoveryRecipeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmWebDiscoveryRecipe>>, TError,ConfirmWebDiscoveryRecipeMutationVariables, TContext> => {

const mutationKey = getConfirmWebDiscoveryRecipeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmWebDiscoveryRecipe>>, ConfirmWebDiscoveryRecipeMutationVariables> = (props) => {
          const {sourceId,recipeId,data} = props ?? {};

          return  confirmWebDiscoveryRecipe(sourceId,recipeId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmWebDiscoveryRecipeMutationResult = NonNullable<Awaited<ReturnType<typeof confirmWebDiscoveryRecipe>>>
    export type ConfirmWebDiscoveryRecipeMutationBody = ConfirmWebDiscoveryRecipe
    export type ConfirmWebDiscoveryRecipeMutationError = Error
    export type ConfirmWebDiscoveryRecipeMutationVariables = {sourceId: string;recipeId: string;data: ConfirmWebDiscoveryRecipe}

    /**
 * @summary Confirm one exact recipe revision after a successful current-source preview
 */
export const useConfirmWebDiscoveryRecipe = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmWebDiscoveryRecipe>>, TError,ConfirmWebDiscoveryRecipeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmWebDiscoveryRecipe>>,
        TError,
        ConfirmWebDiscoveryRecipeMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmWebDiscoveryRecipeMutationOptions(options));
    }

export const getCreateWebDiscoveryScanRunUrl = (sourceId: string,) => {




  return `/api/web-signals/discovery/sources/${sourceId}/scan-runs`
}

/**
 * @summary Queue an idempotent manual discovery scan for the current source configuration
 */
export const createWebDiscoveryScanRun = async (sourceId: string,
    createWebDiscoveryScanRunRequest: CreateWebDiscoveryScanRunRequest, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryScanRun> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryScanRun>(getCreateWebDiscoveryScanRunUrl(sourceId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createWebDiscoveryScanRunRequest)
  }
);}





export const getCreateWebDiscoveryScanRunMutationKey = () => ['createWebDiscoveryScanRun'] as const;

export const getCreateWebDiscoveryScanRunMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWebDiscoveryScanRun>>, TError,CreateWebDiscoveryScanRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createWebDiscoveryScanRun>>, TError,CreateWebDiscoveryScanRunMutationVariables, TContext> => {

const mutationKey = getCreateWebDiscoveryScanRunMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createWebDiscoveryScanRun>>, CreateWebDiscoveryScanRunMutationVariables> = (props) => {
          const {sourceId,data} = props ?? {};

          return  createWebDiscoveryScanRun(sourceId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateWebDiscoveryScanRunMutationResult = NonNullable<Awaited<ReturnType<typeof createWebDiscoveryScanRun>>>
    export type CreateWebDiscoveryScanRunMutationBody = CreateWebDiscoveryScanRunRequest
    export type CreateWebDiscoveryScanRunMutationError = Error
    export type CreateWebDiscoveryScanRunMutationVariables = {sourceId: string;data: CreateWebDiscoveryScanRunRequest}

    /**
 * @summary Queue an idempotent manual discovery scan for the current source configuration
 */
export const useCreateWebDiscoveryScanRun = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWebDiscoveryScanRun>>, TError,CreateWebDiscoveryScanRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createWebDiscoveryScanRun>>,
        TError,
        CreateWebDiscoveryScanRunMutationVariables,
        TContext
      > => {
      return useMutation(getCreateWebDiscoveryScanRunMutationOptions(options));
    }

export const getCancelWebDiscoveryScanRunUrl = (runId: string,) => {




  return `/api/web-signals/discovery/scan-runs/${runId}/cancel`
}

/**
 * @summary Cancel undispatched or in-flight discovery work without retrying it
 */
export const cancelWebDiscoveryScanRun = async (runId: string,
    cancelWebDiscoveryScanRunRequest: CancelWebDiscoveryScanRunRequest, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryScanRun> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryScanRun>(getCancelWebDiscoveryScanRunUrl(runId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(cancelWebDiscoveryScanRunRequest)
  }
);}





export const getCancelWebDiscoveryScanRunMutationKey = () => ['cancelWebDiscoveryScanRun'] as const;

export const getCancelWebDiscoveryScanRunMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelWebDiscoveryScanRun>>, TError,CancelWebDiscoveryScanRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelWebDiscoveryScanRun>>, TError,CancelWebDiscoveryScanRunMutationVariables, TContext> => {

const mutationKey = getCancelWebDiscoveryScanRunMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelWebDiscoveryScanRun>>, CancelWebDiscoveryScanRunMutationVariables> = (props) => {
          const {runId,data} = props ?? {};

          return  cancelWebDiscoveryScanRun(runId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelWebDiscoveryScanRunMutationResult = NonNullable<Awaited<ReturnType<typeof cancelWebDiscoveryScanRun>>>
    export type CancelWebDiscoveryScanRunMutationBody = CancelWebDiscoveryScanRunRequest
    export type CancelWebDiscoveryScanRunMutationError = Error
    export type CancelWebDiscoveryScanRunMutationVariables = {runId: string;data: CancelWebDiscoveryScanRunRequest}

    /**
 * @summary Cancel undispatched or in-flight discovery work without retrying it
 */
export const useCancelWebDiscoveryScanRun = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelWebDiscoveryScanRun>>, TError,CancelWebDiscoveryScanRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelWebDiscoveryScanRun>>,
        TError,
        CancelWebDiscoveryScanRunMutationVariables,
        TContext
      > => {
      return useMutation(getCancelWebDiscoveryScanRunMutationOptions(options));
    }

export const getListWebDiscoveryScanRunsUrl = (params?: ListWebDiscoveryScanRunsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/discovery/scan-runs?${stringifiedParams}` : `/api/web-signals/discovery/scan-runs`
}

/**
 * @summary List durable manual, scheduled, and readiness-preview scan runs
 */
export const listWebDiscoveryScanRuns = async (params?: ListWebDiscoveryScanRunsParams, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryScanRunPage> => {

  return customFetch<WebDiscoveryScanRunPage>(getListWebDiscoveryScanRunsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWebDiscoveryScanRunsQueryKey = (params?: ListWebDiscoveryScanRunsParams,) => {
    return [
    `/api/web-signals/discovery/scan-runs`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWebDiscoveryScanRunsQueryOptions = <TData = Awaited<ReturnType<typeof listWebDiscoveryScanRuns>>, TError = unknown>(params?: ListWebDiscoveryScanRunsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryScanRuns>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWebDiscoveryScanRunsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWebDiscoveryScanRuns>>> = ({ signal }) => listWebDiscoveryScanRuns(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryScanRuns>>, TError, TData> & { queryKey: QueryKey }
}

export type ListWebDiscoveryScanRunsQueryResult = NonNullable<Awaited<ReturnType<typeof listWebDiscoveryScanRuns>>>
export type ListWebDiscoveryScanRunsQueryError = unknown


/**
 * @summary List durable manual, scheduled, and readiness-preview scan runs
 */

export function useListWebDiscoveryScanRuns<TData = Awaited<ReturnType<typeof listWebDiscoveryScanRuns>>, TError = unknown>(
 params?: ListWebDiscoveryScanRunsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryScanRuns>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListWebDiscoveryScanRunsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListWebDiscoveryJobsUrl = (params?: ListWebDiscoveryJobsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/discovery/jobs?${stringifiedParams}` : `/api/web-signals/discovery/jobs`
}

/**
 * @summary List saved Web Discovery jobs
 */
export const listWebDiscoveryJobs = async (params?: ListWebDiscoveryJobsParams, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryJobPage> => {

  return customFetch<WebDiscoveryJobPage>(getListWebDiscoveryJobsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWebDiscoveryJobsQueryKey = (params?: ListWebDiscoveryJobsParams,) => {
    return [
    `/api/web-signals/discovery/jobs`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWebDiscoveryJobsQueryOptions = <TData = Awaited<ReturnType<typeof listWebDiscoveryJobs>>, TError = Error>(params?: ListWebDiscoveryJobsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryJobs>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWebDiscoveryJobsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWebDiscoveryJobs>>> = ({ signal }) => listWebDiscoveryJobs(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryJobs>>, TError, TData> & { queryKey: QueryKey }
}

export type ListWebDiscoveryJobsQueryResult = NonNullable<Awaited<ReturnType<typeof listWebDiscoveryJobs>>>
export type ListWebDiscoveryJobsQueryError = Error


/**
 * @summary List saved Web Discovery jobs
 */

export function useListWebDiscoveryJobs<TData = Awaited<ReturnType<typeof listWebDiscoveryJobs>>, TError = Error>(
 params?: ListWebDiscoveryJobsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryJobs>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListWebDiscoveryJobsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateWebDiscoveryJobUrl = () => {




  return `/api/web-signals/discovery/jobs`
}

/**
 * @summary Save a job whose exact definition has passed a test run
 */
export const createWebDiscoveryJob = async (createWebDiscoveryJobRequest: CreateWebDiscoveryJobRequest, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryJob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryJob>(getCreateWebDiscoveryJobUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createWebDiscoveryJobRequest)
  }
);}





export const getCreateWebDiscoveryJobMutationKey = () => ['createWebDiscoveryJob'] as const;

export const getCreateWebDiscoveryJobMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWebDiscoveryJob>>, TError,CreateWebDiscoveryJobMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createWebDiscoveryJob>>, TError,CreateWebDiscoveryJobMutationVariables, TContext> => {

const mutationKey = getCreateWebDiscoveryJobMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createWebDiscoveryJob>>, CreateWebDiscoveryJobMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createWebDiscoveryJob(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateWebDiscoveryJobMutationResult = NonNullable<Awaited<ReturnType<typeof createWebDiscoveryJob>>>
    export type CreateWebDiscoveryJobMutationBody = CreateWebDiscoveryJobRequest
    export type CreateWebDiscoveryJobMutationError = Error
    export type CreateWebDiscoveryJobMutationVariables = {data: CreateWebDiscoveryJobRequest}

    /**
 * @summary Save a job whose exact definition has passed a test run
 */
export const useCreateWebDiscoveryJob = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWebDiscoveryJob>>, TError,CreateWebDiscoveryJobMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createWebDiscoveryJob>>,
        TError,
        CreateWebDiscoveryJobMutationVariables,
        TContext
      > => {
      return useMutation(getCreateWebDiscoveryJobMutationOptions(options));
    }

export const getTestWebDiscoveryJobDefinitionUrl = () => {




  return `/api/web-signals/discovery/jobs/test`
}

/**
 * @summary Test a saved or unsaved job definition without adding anything to the Review queue
 */
export const testWebDiscoveryJobDefinition = async (testWebDiscoveryJobDefinitionRequest: TestWebDiscoveryJobDefinitionRequest, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryJobTestResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryJobTestResult>(getTestWebDiscoveryJobDefinitionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(testWebDiscoveryJobDefinitionRequest)
  }
);}





export const getTestWebDiscoveryJobDefinitionMutationKey = () => ['testWebDiscoveryJobDefinition'] as const;

export const getTestWebDiscoveryJobDefinitionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof testWebDiscoveryJobDefinition>>, TError,TestWebDiscoveryJobDefinitionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof testWebDiscoveryJobDefinition>>, TError,TestWebDiscoveryJobDefinitionMutationVariables, TContext> => {

const mutationKey = getTestWebDiscoveryJobDefinitionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof testWebDiscoveryJobDefinition>>, TestWebDiscoveryJobDefinitionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  testWebDiscoveryJobDefinition(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TestWebDiscoveryJobDefinitionMutationResult = NonNullable<Awaited<ReturnType<typeof testWebDiscoveryJobDefinition>>>
    export type TestWebDiscoveryJobDefinitionMutationBody = TestWebDiscoveryJobDefinitionRequest
    export type TestWebDiscoveryJobDefinitionMutationError = Error
    export type TestWebDiscoveryJobDefinitionMutationVariables = {data: TestWebDiscoveryJobDefinitionRequest}

    /**
 * @summary Test a saved or unsaved job definition without adding anything to the Review queue
 */
export const useTestWebDiscoveryJobDefinition = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof testWebDiscoveryJobDefinition>>, TError,TestWebDiscoveryJobDefinitionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof testWebDiscoveryJobDefinition>>,
        TError,
        TestWebDiscoveryJobDefinitionMutationVariables,
        TContext
      > => {
      return useMutation(getTestWebDiscoveryJobDefinitionMutationOptions(options));
    }

export const getGetWebDiscoveryJobUrl = (jobId: string,) => {




  return `/api/web-signals/discovery/jobs/${jobId}`
}

/**
 * @summary Get one saved job
 */
export const getWebDiscoveryJob = async (jobId: string, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryJob> => {

  return customFetch<WebDiscoveryJob>(getGetWebDiscoveryJobUrl(jobId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWebDiscoveryJobQueryKey = (jobId: string,) => {
    return [
    `/api/web-signals/discovery/jobs/${jobId}`
    ] as const;
    }


export const getGetWebDiscoveryJobQueryOptions = <TData = Awaited<ReturnType<typeof getWebDiscoveryJob>>, TError = Error>(jobId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoveryJob>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWebDiscoveryJobQueryKey(jobId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWebDiscoveryJob>>> = ({ signal }) => getWebDiscoveryJob(jobId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: jobId !== null && jobId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoveryJob>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWebDiscoveryJobQueryResult = NonNullable<Awaited<ReturnType<typeof getWebDiscoveryJob>>>
export type GetWebDiscoveryJobQueryError = Error


/**
 * @summary Get one saved job
 */

export function useGetWebDiscoveryJob<TData = Awaited<ReturnType<typeof getWebDiscoveryJob>>, TError = Error>(
 jobId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoveryJob>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWebDiscoveryJobQueryOptions(jobId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateWebDiscoveryJobUrl = (jobId: string,) => {




  return `/api/web-signals/discovery/jobs/${jobId}`
}

/**
 * @summary Edit, activate or pause a job; a changed definition needs a successful test of that definition
 */
export const updateWebDiscoveryJob = async (jobId: string,
    updateWebDiscoveryJobRequest: UpdateWebDiscoveryJobRequest, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryJob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryJob>(getUpdateWebDiscoveryJobUrl(jobId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateWebDiscoveryJobRequest)
  }
);}





export const getUpdateWebDiscoveryJobMutationKey = () => ['updateWebDiscoveryJob'] as const;

export const getUpdateWebDiscoveryJobMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWebDiscoveryJob>>, TError,UpdateWebDiscoveryJobMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateWebDiscoveryJob>>, TError,UpdateWebDiscoveryJobMutationVariables, TContext> => {

const mutationKey = getUpdateWebDiscoveryJobMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateWebDiscoveryJob>>, UpdateWebDiscoveryJobMutationVariables> = (props) => {
          const {jobId,data} = props ?? {};

          return  updateWebDiscoveryJob(jobId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateWebDiscoveryJobMutationResult = NonNullable<Awaited<ReturnType<typeof updateWebDiscoveryJob>>>
    export type UpdateWebDiscoveryJobMutationBody = UpdateWebDiscoveryJobRequest
    export type UpdateWebDiscoveryJobMutationError = Error
    export type UpdateWebDiscoveryJobMutationVariables = {jobId: string;data: UpdateWebDiscoveryJobRequest}

    /**
 * @summary Edit, activate or pause a job; a changed definition needs a successful test of that definition
 */
export const useUpdateWebDiscoveryJob = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWebDiscoveryJob>>, TError,UpdateWebDiscoveryJobMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateWebDiscoveryJob>>,
        TError,
        UpdateWebDiscoveryJobMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateWebDiscoveryJobMutationOptions(options));
    }

export const getDeleteWebDiscoveryJobUrl = (jobId: string,
    params: DeleteWebDiscoveryJobParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/discovery/jobs/${jobId}?${stringifiedParams}` : `/api/web-signals/discovery/jobs/${jobId}`
}

/**
 * @summary Delete a job and its run history; Review queue candidates are kept
 */
export const deleteWebDiscoveryJob = async (jobId: string,
    params: DeleteWebDiscoveryJobParams, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteWebDiscoveryJobUrl(jobId,params),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteWebDiscoveryJobMutationKey = () => ['deleteWebDiscoveryJob'] as const;

export const getDeleteWebDiscoveryJobMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteWebDiscoveryJob>>, TError,DeleteWebDiscoveryJobMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteWebDiscoveryJob>>, TError,DeleteWebDiscoveryJobMutationVariables, TContext> => {

const mutationKey = getDeleteWebDiscoveryJobMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteWebDiscoveryJob>>, DeleteWebDiscoveryJobMutationVariables> = (props) => {
          const {jobId,params} = props ?? {};

          return  deleteWebDiscoveryJob(jobId,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteWebDiscoveryJobMutationResult = NonNullable<Awaited<ReturnType<typeof deleteWebDiscoveryJob>>>

    export type DeleteWebDiscoveryJobMutationError = Error
    export type DeleteWebDiscoveryJobMutationVariables = {jobId: string;params: DeleteWebDiscoveryJobParams}

    /**
 * @summary Delete a job and its run history; Review queue candidates are kept
 */
export const useDeleteWebDiscoveryJob = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteWebDiscoveryJob>>, TError,DeleteWebDiscoveryJobMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteWebDiscoveryJob>>,
        TError,
        DeleteWebDiscoveryJobMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteWebDiscoveryJobMutationOptions(options));
    }

export const getListWebDiscoveryJobRunsUrl = (jobId: string,
    params?: ListWebDiscoveryJobRunsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/discovery/jobs/${jobId}/runs?${stringifiedParams}` : `/api/web-signals/discovery/jobs/${jobId}/runs`
}

/**
 * @summary List a job's execution history, newest first
 */
export const listWebDiscoveryJobRuns = async (jobId: string,
    params?: ListWebDiscoveryJobRunsParams, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryJobRunPage> => {

  return customFetch<WebDiscoveryJobRunPage>(getListWebDiscoveryJobRunsUrl(jobId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWebDiscoveryJobRunsQueryKey = (jobId: string,
    params?: ListWebDiscoveryJobRunsParams,) => {
    return [
    `/api/web-signals/discovery/jobs/${jobId}/runs`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWebDiscoveryJobRunsQueryOptions = <TData = Awaited<ReturnType<typeof listWebDiscoveryJobRuns>>, TError = Error>(jobId: string,
    params?: ListWebDiscoveryJobRunsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryJobRuns>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWebDiscoveryJobRunsQueryKey(jobId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWebDiscoveryJobRuns>>> = ({ signal }) => listWebDiscoveryJobRuns(jobId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: jobId !== null && jobId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryJobRuns>>, TError, TData> & { queryKey: QueryKey }
}

export type ListWebDiscoveryJobRunsQueryResult = NonNullable<Awaited<ReturnType<typeof listWebDiscoveryJobRuns>>>
export type ListWebDiscoveryJobRunsQueryError = Error


/**
 * @summary List a job's execution history, newest first
 */

export function useListWebDiscoveryJobRuns<TData = Awaited<ReturnType<typeof listWebDiscoveryJobRuns>>, TError = Error>(
 jobId: string,
    params?: ListWebDiscoveryJobRunsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryJobRuns>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListWebDiscoveryJobRunsQueryOptions(jobId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRunWebDiscoveryJobUrl = (jobId: string,) => {




  return `/api/web-signals/discovery/jobs/${jobId}/runs`
}

/**
 * @summary Run a saved, tested job now
 */
export const runWebDiscoveryJob = async (jobId: string,
    runWebDiscoveryJobRequest: RunWebDiscoveryJobRequest, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryJobRun> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryJobRun>(getRunWebDiscoveryJobUrl(jobId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(runWebDiscoveryJobRequest)
  }
);}





export const getRunWebDiscoveryJobMutationKey = () => ['runWebDiscoveryJob'] as const;

export const getRunWebDiscoveryJobMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runWebDiscoveryJob>>, TError,RunWebDiscoveryJobMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof runWebDiscoveryJob>>, TError,RunWebDiscoveryJobMutationVariables, TContext> => {

const mutationKey = getRunWebDiscoveryJobMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof runWebDiscoveryJob>>, RunWebDiscoveryJobMutationVariables> = (props) => {
          const {jobId,data} = props ?? {};

          return  runWebDiscoveryJob(jobId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RunWebDiscoveryJobMutationResult = NonNullable<Awaited<ReturnType<typeof runWebDiscoveryJob>>>
    export type RunWebDiscoveryJobMutationBody = RunWebDiscoveryJobRequest
    export type RunWebDiscoveryJobMutationError = Error
    export type RunWebDiscoveryJobMutationVariables = {jobId: string;data: RunWebDiscoveryJobRequest}

    /**
 * @summary Run a saved, tested job now
 */
export const useRunWebDiscoveryJob = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runWebDiscoveryJob>>, TError,RunWebDiscoveryJobMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof runWebDiscoveryJob>>,
        TError,
        RunWebDiscoveryJobMutationVariables,
        TContext
      > => {
      return useMutation(getRunWebDiscoveryJobMutationOptions(options));
    }

export const getGetWebDiscoveryJobRunUrl = (runId: string,) => {




  return `/api/web-signals/discovery/job-runs/${runId}`
}

/**
 * @summary Get one job run or test run
 */
export const getWebDiscoveryJobRun = async (runId: string, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryJobRun> => {

  return customFetch<WebDiscoveryJobRun>(getGetWebDiscoveryJobRunUrl(runId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWebDiscoveryJobRunQueryKey = (runId: string,) => {
    return [
    `/api/web-signals/discovery/job-runs/${runId}`
    ] as const;
    }


export const getGetWebDiscoveryJobRunQueryOptions = <TData = Awaited<ReturnType<typeof getWebDiscoveryJobRun>>, TError = Error>(runId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoveryJobRun>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWebDiscoveryJobRunQueryKey(runId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWebDiscoveryJobRun>>> = ({ signal }) => getWebDiscoveryJobRun(runId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoveryJobRun>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWebDiscoveryJobRunQueryResult = NonNullable<Awaited<ReturnType<typeof getWebDiscoveryJobRun>>>
export type GetWebDiscoveryJobRunQueryError = Error


/**
 * @summary Get one job run or test run
 */

export function useGetWebDiscoveryJobRun<TData = Awaited<ReturnType<typeof getWebDiscoveryJobRun>>, TError = Error>(
 runId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoveryJobRun>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWebDiscoveryJobRunQueryOptions(runId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListWebDiscoveryJobRunResultsUrl = (runId: string,
    params?: ListWebDiscoveryJobRunResultsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/discovery/job-runs/${runId}/results?${stringifiedParams}` : `/api/web-signals/discovery/job-runs/${runId}/results`
}

/**
 * @summary List the opportunities a run returned
 */
export const listWebDiscoveryJobRunResults = async (runId: string,
    params?: ListWebDiscoveryJobRunResultsParams, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryJobRunResultPage> => {

  return customFetch<WebDiscoveryJobRunResultPage>(getListWebDiscoveryJobRunResultsUrl(runId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWebDiscoveryJobRunResultsQueryKey = (runId: string,
    params?: ListWebDiscoveryJobRunResultsParams,) => {
    return [
    `/api/web-signals/discovery/job-runs/${runId}/results`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWebDiscoveryJobRunResultsQueryOptions = <TData = Awaited<ReturnType<typeof listWebDiscoveryJobRunResults>>, TError = Error>(runId: string,
    params?: ListWebDiscoveryJobRunResultsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryJobRunResults>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWebDiscoveryJobRunResultsQueryKey(runId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWebDiscoveryJobRunResults>>> = ({ signal }) => listWebDiscoveryJobRunResults(runId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryJobRunResults>>, TError, TData> & { queryKey: QueryKey }
}

export type ListWebDiscoveryJobRunResultsQueryResult = NonNullable<Awaited<ReturnType<typeof listWebDiscoveryJobRunResults>>>
export type ListWebDiscoveryJobRunResultsQueryError = Error


/**
 * @summary List the opportunities a run returned
 */

export function useListWebDiscoveryJobRunResults<TData = Awaited<ReturnType<typeof listWebDiscoveryJobRunResults>>, TError = Error>(
 runId: string,
    params?: ListWebDiscoveryJobRunResultsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryJobRunResults>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListWebDiscoveryJobRunResultsQueryOptions(runId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetWebDiscoveryCandidateEnrichmentUrl = (candidateId: string,) => {




  return `/api/web-signals/discovery/candidates/${candidateId}/enrichment`
}

/**
 * @summary Get a candidate's industry, notes, extra information, AI assessment and decision
 */
export const getWebDiscoveryCandidateEnrichment = async (candidateId: string, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryCandidateEnrichment> => {

  return customFetch<WebDiscoveryCandidateEnrichment>(getGetWebDiscoveryCandidateEnrichmentUrl(candidateId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWebDiscoveryCandidateEnrichmentQueryKey = (candidateId: string,) => {
    return [
    `/api/web-signals/discovery/candidates/${candidateId}/enrichment`
    ] as const;
    }


export const getGetWebDiscoveryCandidateEnrichmentQueryOptions = <TData = Awaited<ReturnType<typeof getWebDiscoveryCandidateEnrichment>>, TError = Error>(candidateId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoveryCandidateEnrichment>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWebDiscoveryCandidateEnrichmentQueryKey(candidateId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWebDiscoveryCandidateEnrichment>>> = ({ signal }) => getWebDiscoveryCandidateEnrichment(candidateId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: candidateId !== null && candidateId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoveryCandidateEnrichment>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWebDiscoveryCandidateEnrichmentQueryResult = NonNullable<Awaited<ReturnType<typeof getWebDiscoveryCandidateEnrichment>>>
export type GetWebDiscoveryCandidateEnrichmentQueryError = Error


/**
 * @summary Get a candidate's industry, notes, extra information, AI assessment and decision
 */

export function useGetWebDiscoveryCandidateEnrichment<TData = Awaited<ReturnType<typeof getWebDiscoveryCandidateEnrichment>>, TError = Error>(
 candidateId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoveryCandidateEnrichment>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWebDiscoveryCandidateEnrichmentQueryOptions(candidateId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateWebDiscoveryCandidateEnrichmentUrl = (candidateId: string,) => {




  return `/api/web-signals/discovery/candidates/${candidateId}/enrichment`
}

/**
 * @summary Change a candidate's industry, notes or extra information
 */
export const updateWebDiscoveryCandidateEnrichment = async (candidateId: string,
    updateWebDiscoveryCandidateEnrichmentRequest: UpdateWebDiscoveryCandidateEnrichmentRequest, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryCandidateEnrichment> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryCandidateEnrichment>(getUpdateWebDiscoveryCandidateEnrichmentUrl(candidateId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateWebDiscoveryCandidateEnrichmentRequest)
  }
);}





export const getUpdateWebDiscoveryCandidateEnrichmentMutationKey = () => ['updateWebDiscoveryCandidateEnrichment'] as const;

export const getUpdateWebDiscoveryCandidateEnrichmentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWebDiscoveryCandidateEnrichment>>, TError,UpdateWebDiscoveryCandidateEnrichmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateWebDiscoveryCandidateEnrichment>>, TError,UpdateWebDiscoveryCandidateEnrichmentMutationVariables, TContext> => {

const mutationKey = getUpdateWebDiscoveryCandidateEnrichmentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateWebDiscoveryCandidateEnrichment>>, UpdateWebDiscoveryCandidateEnrichmentMutationVariables> = (props) => {
          const {candidateId,data} = props ?? {};

          return  updateWebDiscoveryCandidateEnrichment(candidateId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateWebDiscoveryCandidateEnrichmentMutationResult = NonNullable<Awaited<ReturnType<typeof updateWebDiscoveryCandidateEnrichment>>>
    export type UpdateWebDiscoveryCandidateEnrichmentMutationBody = UpdateWebDiscoveryCandidateEnrichmentRequest
    export type UpdateWebDiscoveryCandidateEnrichmentMutationError = Error
    export type UpdateWebDiscoveryCandidateEnrichmentMutationVariables = {candidateId: string;data: UpdateWebDiscoveryCandidateEnrichmentRequest}

    /**
 * @summary Change a candidate's industry, notes or extra information
 */
export const useUpdateWebDiscoveryCandidateEnrichment = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWebDiscoveryCandidateEnrichment>>, TError,UpdateWebDiscoveryCandidateEnrichmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateWebDiscoveryCandidateEnrichment>>,
        TError,
        UpdateWebDiscoveryCandidateEnrichmentMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateWebDiscoveryCandidateEnrichmentMutationOptions(options));
    }

export const getRequestWebDiscoveryCandidateEnrichmentUrl = (candidateId: string,) => {




  return `/api/web-signals/discovery/candidates/${candidateId}/enrichment/expand`
}

/**
 * @summary Ask AI for cited extra information; nothing is saved until the reviewer keeps it
 */
export const requestWebDiscoveryCandidateEnrichment = async (candidateId: string,
    requestWebDiscoveryCandidateEnrichmentRequest: RequestWebDiscoveryCandidateEnrichmentRequest, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryCandidateEnrichmentProposal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryCandidateEnrichmentProposal>(getRequestWebDiscoveryCandidateEnrichmentUrl(candidateId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(requestWebDiscoveryCandidateEnrichmentRequest)
  }
);}





export const getRequestWebDiscoveryCandidateEnrichmentMutationKey = () => ['requestWebDiscoveryCandidateEnrichment'] as const;

export const getRequestWebDiscoveryCandidateEnrichmentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestWebDiscoveryCandidateEnrichment>>, TError,RequestWebDiscoveryCandidateEnrichmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof requestWebDiscoveryCandidateEnrichment>>, TError,RequestWebDiscoveryCandidateEnrichmentMutationVariables, TContext> => {

const mutationKey = getRequestWebDiscoveryCandidateEnrichmentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof requestWebDiscoveryCandidateEnrichment>>, RequestWebDiscoveryCandidateEnrichmentMutationVariables> = (props) => {
          const {candidateId,data} = props ?? {};

          return  requestWebDiscoveryCandidateEnrichment(candidateId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RequestWebDiscoveryCandidateEnrichmentMutationResult = NonNullable<Awaited<ReturnType<typeof requestWebDiscoveryCandidateEnrichment>>>
    export type RequestWebDiscoveryCandidateEnrichmentMutationBody = RequestWebDiscoveryCandidateEnrichmentRequest
    export type RequestWebDiscoveryCandidateEnrichmentMutationError = Error
    export type RequestWebDiscoveryCandidateEnrichmentMutationVariables = {candidateId: string;data: RequestWebDiscoveryCandidateEnrichmentRequest}

    /**
 * @summary Ask AI for cited extra information; nothing is saved until the reviewer keeps it
 */
export const useRequestWebDiscoveryCandidateEnrichment = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestWebDiscoveryCandidateEnrichment>>, TError,RequestWebDiscoveryCandidateEnrichmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof requestWebDiscoveryCandidateEnrichment>>,
        TError,
        RequestWebDiscoveryCandidateEnrichmentMutationVariables,
        TContext
      > => {
      return useMutation(getRequestWebDiscoveryCandidateEnrichmentMutationOptions(options));
    }

export const getRequestWebDiscoveryCandidateAssessmentUrl = (candidateId: string,) => {




  return `/api/web-signals/discovery/candidates/${candidateId}/assessment`
}

/**
 * @summary Ask AI to recommend whether to pursue the candidate, with reasons and citations
 */
export const requestWebDiscoveryCandidateAssessment = async (candidateId: string,
    requestWebDiscoveryCandidateAssessmentRequest: RequestWebDiscoveryCandidateAssessmentRequest, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryCandidateEnrichment> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryCandidateEnrichment>(getRequestWebDiscoveryCandidateAssessmentUrl(candidateId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(requestWebDiscoveryCandidateAssessmentRequest)
  }
);}





export const getRequestWebDiscoveryCandidateAssessmentMutationKey = () => ['requestWebDiscoveryCandidateAssessment'] as const;

export const getRequestWebDiscoveryCandidateAssessmentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestWebDiscoveryCandidateAssessment>>, TError,RequestWebDiscoveryCandidateAssessmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof requestWebDiscoveryCandidateAssessment>>, TError,RequestWebDiscoveryCandidateAssessmentMutationVariables, TContext> => {

const mutationKey = getRequestWebDiscoveryCandidateAssessmentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof requestWebDiscoveryCandidateAssessment>>, RequestWebDiscoveryCandidateAssessmentMutationVariables> = (props) => {
          const {candidateId,data} = props ?? {};

          return  requestWebDiscoveryCandidateAssessment(candidateId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RequestWebDiscoveryCandidateAssessmentMutationResult = NonNullable<Awaited<ReturnType<typeof requestWebDiscoveryCandidateAssessment>>>
    export type RequestWebDiscoveryCandidateAssessmentMutationBody = RequestWebDiscoveryCandidateAssessmentRequest
    export type RequestWebDiscoveryCandidateAssessmentMutationError = Error
    export type RequestWebDiscoveryCandidateAssessmentMutationVariables = {candidateId: string;data: RequestWebDiscoveryCandidateAssessmentRequest}

    /**
 * @summary Ask AI to recommend whether to pursue the candidate, with reasons and citations
 */
export const useRequestWebDiscoveryCandidateAssessment = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestWebDiscoveryCandidateAssessment>>, TError,RequestWebDiscoveryCandidateAssessmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof requestWebDiscoveryCandidateAssessment>>,
        TError,
        RequestWebDiscoveryCandidateAssessmentMutationVariables,
        TContext
      > => {
      return useMutation(getRequestWebDiscoveryCandidateAssessmentMutationOptions(options));
    }

export const getRecordWebDiscoveryCandidateDecisionUrl = (candidateId: string,) => {




  return `/api/web-signals/discovery/candidates/${candidateId}/decision`
}

/**
 * @summary Record Pursue or Not pursuing; nothing is created in pursuits or proposals
 */
export const recordWebDiscoveryCandidateDecision = async (candidateId: string,
    recordWebDiscoveryCandidateDecisionRequest: RecordWebDiscoveryCandidateDecisionRequest, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryCandidateEnrichment> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryCandidateEnrichment>(getRecordWebDiscoveryCandidateDecisionUrl(candidateId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(recordWebDiscoveryCandidateDecisionRequest)
  }
);}





export const getRecordWebDiscoveryCandidateDecisionMutationKey = () => ['recordWebDiscoveryCandidateDecision'] as const;

export const getRecordWebDiscoveryCandidateDecisionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recordWebDiscoveryCandidateDecision>>, TError,RecordWebDiscoveryCandidateDecisionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof recordWebDiscoveryCandidateDecision>>, TError,RecordWebDiscoveryCandidateDecisionMutationVariables, TContext> => {

const mutationKey = getRecordWebDiscoveryCandidateDecisionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof recordWebDiscoveryCandidateDecision>>, RecordWebDiscoveryCandidateDecisionMutationVariables> = (props) => {
          const {candidateId,data} = props ?? {};

          return  recordWebDiscoveryCandidateDecision(candidateId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RecordWebDiscoveryCandidateDecisionMutationResult = NonNullable<Awaited<ReturnType<typeof recordWebDiscoveryCandidateDecision>>>
    export type RecordWebDiscoveryCandidateDecisionMutationBody = RecordWebDiscoveryCandidateDecisionRequest
    export type RecordWebDiscoveryCandidateDecisionMutationError = Error
    export type RecordWebDiscoveryCandidateDecisionMutationVariables = {candidateId: string;data: RecordWebDiscoveryCandidateDecisionRequest}

    /**
 * @summary Record Pursue or Not pursuing; nothing is created in pursuits or proposals
 */
export const useRecordWebDiscoveryCandidateDecision = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recordWebDiscoveryCandidateDecision>>, TError,RecordWebDiscoveryCandidateDecisionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof recordWebDiscoveryCandidateDecision>>,
        TError,
        RecordWebDiscoveryCandidateDecisionMutationVariables,
        TContext
      > => {
      return useMutation(getRecordWebDiscoveryCandidateDecisionMutationOptions(options));
    }

export const getListWebDiscoveryCandidateDocumentsUrl = (candidateId: string,
    params?: ListWebDiscoveryCandidateDocumentsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/discovery/candidates/${candidateId}/documents?${stringifiedParams}` : `/api/web-signals/discovery/candidates/${candidateId}/documents`
}

/**
 * @summary List documents attached to a candidate
 */
export const listWebDiscoveryCandidateDocuments = async (candidateId: string,
    params?: ListWebDiscoveryCandidateDocumentsParams, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryCandidateDocumentPage> => {

  return customFetch<WebDiscoveryCandidateDocumentPage>(getListWebDiscoveryCandidateDocumentsUrl(candidateId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWebDiscoveryCandidateDocumentsQueryKey = (candidateId: string,
    params?: ListWebDiscoveryCandidateDocumentsParams,) => {
    return [
    `/api/web-signals/discovery/candidates/${candidateId}/documents`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWebDiscoveryCandidateDocumentsQueryOptions = <TData = Awaited<ReturnType<typeof listWebDiscoveryCandidateDocuments>>, TError = Error>(candidateId: string,
    params?: ListWebDiscoveryCandidateDocumentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryCandidateDocuments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWebDiscoveryCandidateDocumentsQueryKey(candidateId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWebDiscoveryCandidateDocuments>>> = ({ signal }) => listWebDiscoveryCandidateDocuments(candidateId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: candidateId !== null && candidateId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryCandidateDocuments>>, TError, TData> & { queryKey: QueryKey }
}

export type ListWebDiscoveryCandidateDocumentsQueryResult = NonNullable<Awaited<ReturnType<typeof listWebDiscoveryCandidateDocuments>>>
export type ListWebDiscoveryCandidateDocumentsQueryError = Error


/**
 * @summary List documents attached to a candidate
 */

export function useListWebDiscoveryCandidateDocuments<TData = Awaited<ReturnType<typeof listWebDiscoveryCandidateDocuments>>, TError = Error>(
 candidateId: string,
    params?: ListWebDiscoveryCandidateDocumentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryCandidateDocuments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListWebDiscoveryCandidateDocumentsQueryOptions(candidateId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getAddWebDiscoveryCandidateDocumentUrl = (candidateId: string,) => {




  return `/api/web-signals/discovery/candidates/${candidateId}/documents`
}

/**
 * @summary Attach an existing Web signals document to a candidate
 */
export const addWebDiscoveryCandidateDocument = async (candidateId: string,
    addWebDiscoveryCandidateDocumentRequest: AddWebDiscoveryCandidateDocumentRequest, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryCandidateDocument> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryCandidateDocument>(getAddWebDiscoveryCandidateDocumentUrl(candidateId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(addWebDiscoveryCandidateDocumentRequest)
  }
);}





export const getAddWebDiscoveryCandidateDocumentMutationKey = () => ['addWebDiscoveryCandidateDocument'] as const;

export const getAddWebDiscoveryCandidateDocumentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addWebDiscoveryCandidateDocument>>, TError,AddWebDiscoveryCandidateDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof addWebDiscoveryCandidateDocument>>, TError,AddWebDiscoveryCandidateDocumentMutationVariables, TContext> => {

const mutationKey = getAddWebDiscoveryCandidateDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof addWebDiscoveryCandidateDocument>>, AddWebDiscoveryCandidateDocumentMutationVariables> = (props) => {
          const {candidateId,data} = props ?? {};

          return  addWebDiscoveryCandidateDocument(candidateId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AddWebDiscoveryCandidateDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof addWebDiscoveryCandidateDocument>>>
    export type AddWebDiscoveryCandidateDocumentMutationBody = AddWebDiscoveryCandidateDocumentRequest
    export type AddWebDiscoveryCandidateDocumentMutationError = Error
    export type AddWebDiscoveryCandidateDocumentMutationVariables = {candidateId: string;data: AddWebDiscoveryCandidateDocumentRequest}

    /**
 * @summary Attach an existing Web signals document to a candidate
 */
export const useAddWebDiscoveryCandidateDocument = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addWebDiscoveryCandidateDocument>>, TError,AddWebDiscoveryCandidateDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof addWebDiscoveryCandidateDocument>>,
        TError,
        AddWebDiscoveryCandidateDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getAddWebDiscoveryCandidateDocumentMutationOptions(options));
    }

export const getUploadWebDiscoveryCandidateDocumentUrl = (candidateId: string,) => {




  return `/api/web-signals/discovery/candidates/${candidateId}/documents/upload`
}

/**
 * @summary Upload a file (PDF, Word, Excel, PowerPoint, text, CSV, Markdown or HTML, up to 10 MB) into Web signals documents and attach it to a candidate
 */
export const uploadWebDiscoveryCandidateDocument = async (candidateId: string,
    uploadWebDiscoveryCandidateDocumentRequest: UploadWebDiscoveryCandidateDocumentRequest, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryCandidateDocument> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryCandidateDocument>(getUploadWebDiscoveryCandidateDocumentUrl(candidateId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(uploadWebDiscoveryCandidateDocumentRequest)
  }
);}





export const getUploadWebDiscoveryCandidateDocumentMutationKey = () => ['uploadWebDiscoveryCandidateDocument'] as const;

export const getUploadWebDiscoveryCandidateDocumentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadWebDiscoveryCandidateDocument>>, TError,UploadWebDiscoveryCandidateDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadWebDiscoveryCandidateDocument>>, TError,UploadWebDiscoveryCandidateDocumentMutationVariables, TContext> => {

const mutationKey = getUploadWebDiscoveryCandidateDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadWebDiscoveryCandidateDocument>>, UploadWebDiscoveryCandidateDocumentMutationVariables> = (props) => {
          const {candidateId,data} = props ?? {};

          return  uploadWebDiscoveryCandidateDocument(candidateId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadWebDiscoveryCandidateDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof uploadWebDiscoveryCandidateDocument>>>
    export type UploadWebDiscoveryCandidateDocumentMutationBody = UploadWebDiscoveryCandidateDocumentRequest
    export type UploadWebDiscoveryCandidateDocumentMutationError = Error
    export type UploadWebDiscoveryCandidateDocumentMutationVariables = {candidateId: string;data: UploadWebDiscoveryCandidateDocumentRequest}

    /**
 * @summary Upload a file (PDF, Word, Excel, PowerPoint, text, CSV, Markdown or HTML, up to 10 MB) into Web signals documents and attach it to a candidate
 */
export const useUploadWebDiscoveryCandidateDocument = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadWebDiscoveryCandidateDocument>>, TError,UploadWebDiscoveryCandidateDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadWebDiscoveryCandidateDocument>>,
        TError,
        UploadWebDiscoveryCandidateDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getUploadWebDiscoveryCandidateDocumentMutationOptions(options));
    }

export const getRemoveWebDiscoveryCandidateDocumentUrl = (candidateId: string,
    documentId: string,) => {




  return `/api/web-signals/discovery/candidates/${candidateId}/documents/${documentId}`
}

/**
 * @summary Detach a document from a candidate; the document itself is kept
 */
export const removeWebDiscoveryCandidateDocument = async (candidateId: string,
    documentId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getRemoveWebDiscoveryCandidateDocumentUrl(candidateId,documentId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getRemoveWebDiscoveryCandidateDocumentMutationKey = () => ['removeWebDiscoveryCandidateDocument'] as const;

export const getRemoveWebDiscoveryCandidateDocumentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeWebDiscoveryCandidateDocument>>, TError,RemoveWebDiscoveryCandidateDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof removeWebDiscoveryCandidateDocument>>, TError,RemoveWebDiscoveryCandidateDocumentMutationVariables, TContext> => {

const mutationKey = getRemoveWebDiscoveryCandidateDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof removeWebDiscoveryCandidateDocument>>, RemoveWebDiscoveryCandidateDocumentMutationVariables> = (props) => {
          const {candidateId,documentId} = props ?? {};

          return  removeWebDiscoveryCandidateDocument(candidateId,documentId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RemoveWebDiscoveryCandidateDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof removeWebDiscoveryCandidateDocument>>>

    export type RemoveWebDiscoveryCandidateDocumentMutationError = Error
    export type RemoveWebDiscoveryCandidateDocumentMutationVariables = {candidateId: string;documentId: string}

    /**
 * @summary Detach a document from a candidate; the document itself is kept
 */
export const useRemoveWebDiscoveryCandidateDocument = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeWebDiscoveryCandidateDocument>>, TError,RemoveWebDiscoveryCandidateDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof removeWebDiscoveryCandidateDocument>>,
        TError,
        RemoveWebDiscoveryCandidateDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getRemoveWebDiscoveryCandidateDocumentMutationOptions(options));
    }

export const getListWebDiscoveryCandidateHistoryUrl = (candidateId: string,
    params?: ListWebDiscoveryCandidateHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/discovery/candidates/${candidateId}/history?${stringifiedParams}` : `/api/web-signals/discovery/candidates/${candidateId}/history`
}

/**
 * @summary List who changed a candidate's enrichment, assessment and decision, and when
 */
export const listWebDiscoveryCandidateHistory = async (candidateId: string,
    params?: ListWebDiscoveryCandidateHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryCandidateHistoryPage> => {

  return customFetch<WebDiscoveryCandidateHistoryPage>(getListWebDiscoveryCandidateHistoryUrl(candidateId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWebDiscoveryCandidateHistoryQueryKey = (candidateId: string,
    params?: ListWebDiscoveryCandidateHistoryParams,) => {
    return [
    `/api/web-signals/discovery/candidates/${candidateId}/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWebDiscoveryCandidateHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listWebDiscoveryCandidateHistory>>, TError = Error>(candidateId: string,
    params?: ListWebDiscoveryCandidateHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryCandidateHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWebDiscoveryCandidateHistoryQueryKey(candidateId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWebDiscoveryCandidateHistory>>> = ({ signal }) => listWebDiscoveryCandidateHistory(candidateId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: candidateId !== null && candidateId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryCandidateHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListWebDiscoveryCandidateHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listWebDiscoveryCandidateHistory>>>
export type ListWebDiscoveryCandidateHistoryQueryError = Error


/**
 * @summary List who changed a candidate's enrichment, assessment and decision, and when
 */

export function useListWebDiscoveryCandidateHistory<TData = Awaited<ReturnType<typeof listWebDiscoveryCandidateHistory>>, TError = Error>(
 candidateId: string,
    params?: ListWebDiscoveryCandidateHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryCandidateHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListWebDiscoveryCandidateHistoryQueryOptions(candidateId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListWebDiscoveryEvidenceUrl = (candidateId: string,
    params?: ListWebDiscoveryEvidenceParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/web-signals/discovery/candidates/${candidateId}/evidence?${stringifiedParams}` : `/api/web-signals/discovery/candidates/${candidateId}/evidence`
}

/**
 * @summary List bounded immutable evidence revisions for a discovery candidate
 */
export const listWebDiscoveryEvidence = async (candidateId: string,
    params?: ListWebDiscoveryEvidenceParams, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryEvidencePage> => {

  return customFetch<WebDiscoveryEvidencePage>(getListWebDiscoveryEvidenceUrl(candidateId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWebDiscoveryEvidenceQueryKey = (candidateId: string,
    params?: ListWebDiscoveryEvidenceParams,) => {
    return [
    `/api/web-signals/discovery/candidates/${candidateId}/evidence`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWebDiscoveryEvidenceQueryOptions = <TData = Awaited<ReturnType<typeof listWebDiscoveryEvidence>>, TError = unknown>(candidateId: string,
    params?: ListWebDiscoveryEvidenceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryEvidence>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWebDiscoveryEvidenceQueryKey(candidateId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWebDiscoveryEvidence>>> = ({ signal }) => listWebDiscoveryEvidence(candidateId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: candidateId !== null && candidateId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryEvidence>>, TError, TData> & { queryKey: QueryKey }
}

export type ListWebDiscoveryEvidenceQueryResult = NonNullable<Awaited<ReturnType<typeof listWebDiscoveryEvidence>>>
export type ListWebDiscoveryEvidenceQueryError = unknown


/**
 * @summary List bounded immutable evidence revisions for a discovery candidate
 */

export function useListWebDiscoveryEvidence<TData = Awaited<ReturnType<typeof listWebDiscoveryEvidence>>, TError = unknown>(
 candidateId: string,
    params?: ListWebDiscoveryEvidenceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWebDiscoveryEvidence>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListWebDiscoveryEvidenceQueryOptions(candidateId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetWebDiscoveryAutomationPolicyUrl = () => {




  return `/api/web-signals/discovery/automation-policy`
}

/**
 * @summary Get the review-first discovery promotion policy
 */
export const getWebDiscoveryAutomationPolicy = async ( options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryAutomationPolicy> => {

  return customFetch<WebDiscoveryAutomationPolicy>(getGetWebDiscoveryAutomationPolicyUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWebDiscoveryAutomationPolicyQueryKey = () => {
    return [
    `/api/web-signals/discovery/automation-policy`
    ] as const;
    }


export const getGetWebDiscoveryAutomationPolicyQueryOptions = <TData = Awaited<ReturnType<typeof getWebDiscoveryAutomationPolicy>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoveryAutomationPolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWebDiscoveryAutomationPolicyQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWebDiscoveryAutomationPolicy>>> = ({ signal }) => getWebDiscoveryAutomationPolicy({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoveryAutomationPolicy>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWebDiscoveryAutomationPolicyQueryResult = NonNullable<Awaited<ReturnType<typeof getWebDiscoveryAutomationPolicy>>>
export type GetWebDiscoveryAutomationPolicyQueryError = unknown


/**
 * @summary Get the review-first discovery promotion policy
 */

export function useGetWebDiscoveryAutomationPolicy<TData = Awaited<ReturnType<typeof getWebDiscoveryAutomationPolicy>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoveryAutomationPolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWebDiscoveryAutomationPolicyQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateWebDiscoveryAutomationPolicyUrl = () => {




  return `/api/web-signals/discovery/automation-policy`
}

/**
 * @summary Configure the exact administrator-approved discovery promotion policy
 */
export const updateWebDiscoveryAutomationPolicy = async (updateWebDiscoveryAutomationPolicyRequest: UpdateWebDiscoveryAutomationPolicyRequest, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryAutomationPolicy> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryAutomationPolicy>(getUpdateWebDiscoveryAutomationPolicyUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateWebDiscoveryAutomationPolicyRequest)
  }
);}





export const getUpdateWebDiscoveryAutomationPolicyMutationKey = () => ['updateWebDiscoveryAutomationPolicy'] as const;

export const getUpdateWebDiscoveryAutomationPolicyMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWebDiscoveryAutomationPolicy>>, TError,UpdateWebDiscoveryAutomationPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateWebDiscoveryAutomationPolicy>>, TError,UpdateWebDiscoveryAutomationPolicyMutationVariables, TContext> => {

const mutationKey = getUpdateWebDiscoveryAutomationPolicyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateWebDiscoveryAutomationPolicy>>, UpdateWebDiscoveryAutomationPolicyMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateWebDiscoveryAutomationPolicy(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateWebDiscoveryAutomationPolicyMutationResult = NonNullable<Awaited<ReturnType<typeof updateWebDiscoveryAutomationPolicy>>>
    export type UpdateWebDiscoveryAutomationPolicyMutationBody = UpdateWebDiscoveryAutomationPolicyRequest
    export type UpdateWebDiscoveryAutomationPolicyMutationError = Error
    export type UpdateWebDiscoveryAutomationPolicyMutationVariables = {data: UpdateWebDiscoveryAutomationPolicyRequest}

    /**
 * @summary Configure the exact administrator-approved discovery promotion policy
 */
export const useUpdateWebDiscoveryAutomationPolicy = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWebDiscoveryAutomationPolicy>>, TError,UpdateWebDiscoveryAutomationPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateWebDiscoveryAutomationPolicy>>,
        TError,
        UpdateWebDiscoveryAutomationPolicyMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateWebDiscoveryAutomationPolicyMutationOptions(options));
    }

export const getGetWebDiscoveryCandidateReviewPreviewUrl = (candidateId: string,) => {




  return `/api/web-signals/discovery/candidates/${candidateId}/review-preview`
}

/**
 * @summary Preview exact evidence, CRM matches, proposed changes and promotion eligibility
 */
export const getWebDiscoveryCandidateReviewPreview = async (candidateId: string, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryCandidateReviewPreview> => {

  return customFetch<WebDiscoveryCandidateReviewPreview>(getGetWebDiscoveryCandidateReviewPreviewUrl(candidateId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWebDiscoveryCandidateReviewPreviewQueryKey = (candidateId: string,) => {
    return [
    `/api/web-signals/discovery/candidates/${candidateId}/review-preview`
    ] as const;
    }


export const getGetWebDiscoveryCandidateReviewPreviewQueryOptions = <TData = Awaited<ReturnType<typeof getWebDiscoveryCandidateReviewPreview>>, TError = unknown>(candidateId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoveryCandidateReviewPreview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWebDiscoveryCandidateReviewPreviewQueryKey(candidateId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWebDiscoveryCandidateReviewPreview>>> = ({ signal }) => getWebDiscoveryCandidateReviewPreview(candidateId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: candidateId !== null && candidateId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoveryCandidateReviewPreview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWebDiscoveryCandidateReviewPreviewQueryResult = NonNullable<Awaited<ReturnType<typeof getWebDiscoveryCandidateReviewPreview>>>
export type GetWebDiscoveryCandidateReviewPreviewQueryError = unknown


/**
 * @summary Preview exact evidence, CRM matches, proposed changes and promotion eligibility
 */

export function useGetWebDiscoveryCandidateReviewPreview<TData = Awaited<ReturnType<typeof getWebDiscoveryCandidateReviewPreview>>, TError = unknown>(
 candidateId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoveryCandidateReviewPreview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWebDiscoveryCandidateReviewPreviewQueryOptions(candidateId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewWebDiscoveryCandidateUrl = (candidateId: string,) => {




  return `/api/web-signals/discovery/candidates/${candidateId}/review`
}

/**
 * @summary Dismiss, defer or resolve a duplicate discovery with optimistic revision checks
 */
export const reviewWebDiscoveryCandidate = async (candidateId: string,
    reviewWebDiscoveryCandidateRequest: ReviewWebDiscoveryCandidateRequest, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryCandidateReviewResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryCandidateReviewResult>(getReviewWebDiscoveryCandidateUrl(candidateId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewWebDiscoveryCandidateRequest)
  }
);}





export const getReviewWebDiscoveryCandidateMutationKey = () => ['reviewWebDiscoveryCandidate'] as const;

export const getReviewWebDiscoveryCandidateMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewWebDiscoveryCandidate>>, TError,ReviewWebDiscoveryCandidateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewWebDiscoveryCandidate>>, TError,ReviewWebDiscoveryCandidateMutationVariables, TContext> => {

const mutationKey = getReviewWebDiscoveryCandidateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewWebDiscoveryCandidate>>, ReviewWebDiscoveryCandidateMutationVariables> = (props) => {
          const {candidateId,data} = props ?? {};

          return  reviewWebDiscoveryCandidate(candidateId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewWebDiscoveryCandidateMutationResult = NonNullable<Awaited<ReturnType<typeof reviewWebDiscoveryCandidate>>>
    export type ReviewWebDiscoveryCandidateMutationBody = ReviewWebDiscoveryCandidateRequest
    export type ReviewWebDiscoveryCandidateMutationError = Error
    export type ReviewWebDiscoveryCandidateMutationVariables = {candidateId: string;data: ReviewWebDiscoveryCandidateRequest}

    /**
 * @summary Dismiss, defer or resolve a duplicate discovery with optimistic revision checks
 */
export const useReviewWebDiscoveryCandidate = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewWebDiscoveryCandidate>>, TError,ReviewWebDiscoveryCandidateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewWebDiscoveryCandidate>>,
        TError,
        ReviewWebDiscoveryCandidateMutationVariables,
        TContext
      > => {
      return useMutation(getReviewWebDiscoveryCandidateMutationOptions(options));
    }

export const getGetWebDiscoveryPromotionUrl = (candidateId: string,) => {




  return `/api/web-signals/discovery/candidates/${candidateId}/promotion`
}

/**
 * @summary Get canonical CRM promotion linkage for a discovery candidate
 */
export const getWebDiscoveryPromotion = async (candidateId: string, options?: Parameters<typeof customFetch>[1]): Promise<GetWebDiscoveryPromotion200> => {

  return customFetch<GetWebDiscoveryPromotion200>(getGetWebDiscoveryPromotionUrl(candidateId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWebDiscoveryPromotionQueryKey = (candidateId: string,) => {
    return [
    `/api/web-signals/discovery/candidates/${candidateId}/promotion`
    ] as const;
    }


export const getGetWebDiscoveryPromotionQueryOptions = <TData = Awaited<ReturnType<typeof getWebDiscoveryPromotion>>, TError = unknown>(candidateId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoveryPromotion>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWebDiscoveryPromotionQueryKey(candidateId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWebDiscoveryPromotion>>> = ({ signal }) => getWebDiscoveryPromotion(candidateId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: candidateId !== null && candidateId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoveryPromotion>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWebDiscoveryPromotionQueryResult = NonNullable<Awaited<ReturnType<typeof getWebDiscoveryPromotion>>>
export type GetWebDiscoveryPromotionQueryError = unknown


/**
 * @summary Get canonical CRM promotion linkage for a discovery candidate
 */

export function useGetWebDiscoveryPromotion<TData = Awaited<ReturnType<typeof getWebDiscoveryPromotion>>, TError = unknown>(
 candidateId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWebDiscoveryPromotion>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWebDiscoveryPromotionQueryOptions(candidateId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPromoteWebDiscoveryCandidateUrl = (candidateId: string,) => {




  return `/api/web-signals/discovery/candidates/${candidateId}/promotion`
}

/**
 * @summary Atomically accept a reviewed discovery and create or link canonical CRM records
 */
export const promoteWebDiscoveryCandidate = async (candidateId: string,
    promoteWebDiscoveryCandidateRequest: PromoteWebDiscoveryCandidateRequest, options?: Parameters<typeof customFetch>[1]): Promise<WebDiscoveryPromotionResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WebDiscoveryPromotionResult>(getPromoteWebDiscoveryCandidateUrl(candidateId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(promoteWebDiscoveryCandidateRequest)
  }
);}





export const getPromoteWebDiscoveryCandidateMutationKey = () => ['promoteWebDiscoveryCandidate'] as const;

export const getPromoteWebDiscoveryCandidateMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof promoteWebDiscoveryCandidate>>, TError,PromoteWebDiscoveryCandidateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof promoteWebDiscoveryCandidate>>, TError,PromoteWebDiscoveryCandidateMutationVariables, TContext> => {

const mutationKey = getPromoteWebDiscoveryCandidateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof promoteWebDiscoveryCandidate>>, PromoteWebDiscoveryCandidateMutationVariables> = (props) => {
          const {candidateId,data} = props ?? {};

          return  promoteWebDiscoveryCandidate(candidateId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PromoteWebDiscoveryCandidateMutationResult = NonNullable<Awaited<ReturnType<typeof promoteWebDiscoveryCandidate>>>
    export type PromoteWebDiscoveryCandidateMutationBody = PromoteWebDiscoveryCandidateRequest
    export type PromoteWebDiscoveryCandidateMutationError = Error
    export type PromoteWebDiscoveryCandidateMutationVariables = {candidateId: string;data: PromoteWebDiscoveryCandidateRequest}

    /**
 * @summary Atomically accept a reviewed discovery and create or link canonical CRM records
 */
export const usePromoteWebDiscoveryCandidate = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof promoteWebDiscoveryCandidate>>, TError,PromoteWebDiscoveryCandidateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof promoteWebDiscoveryCandidate>>,
        TError,
        PromoteWebDiscoveryCandidateMutationVariables,
        TContext
      > => {
      return useMutation(getPromoteWebDiscoveryCandidateMutationOptions(options));
    }

export const getGetVoiceStatusUrl = () => {




  return `/api/voice/status`
}

/**
 * @summary Get telephony connection state and effective voice-agent settings
 */
export const getVoiceStatus = async ( options?: Parameters<typeof customFetch>[1]): Promise<VoiceStatus> => {

  return customFetch<VoiceStatus>(getGetVoiceStatusUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetVoiceStatusQueryKey = () => {
    return [
    `/api/voice/status`
    ] as const;
    }


export const getGetVoiceStatusQueryOptions = <TData = Awaited<ReturnType<typeof getVoiceStatus>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getVoiceStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetVoiceStatusQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getVoiceStatus>>> = ({ signal }) => getVoiceStatus({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getVoiceStatus>>, TError, TData> & { queryKey: QueryKey }
}

export type GetVoiceStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getVoiceStatus>>>
export type GetVoiceStatusQueryError = unknown


/**
 * @summary Get telephony connection state and effective voice-agent settings
 */

export function useGetVoiceStatus<TData = Awaited<ReturnType<typeof getVoiceStatus>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getVoiceStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetVoiceStatusQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetVoiceLiveConfigStatusUrl = () => {




  return `/api/voice/live/config-status`
}

/**
 * @summary Check authenticated staff browser-voice availability and configuration
 */
export const getVoiceLiveConfigStatus = async ( options?: Parameters<typeof customFetch>[1]): Promise<VoiceLiveConfigStatus> => {

  return customFetch<VoiceLiveConfigStatus>(getGetVoiceLiveConfigStatusUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetVoiceLiveConfigStatusQueryKey = () => {
    return [
    `/api/voice/live/config-status`
    ] as const;
    }


export const getGetVoiceLiveConfigStatusQueryOptions = <TData = Awaited<ReturnType<typeof getVoiceLiveConfigStatus>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getVoiceLiveConfigStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetVoiceLiveConfigStatusQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getVoiceLiveConfigStatus>>> = ({ signal }) => getVoiceLiveConfigStatus({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getVoiceLiveConfigStatus>>, TError, TData> & { queryKey: QueryKey }
}

export type GetVoiceLiveConfigStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getVoiceLiveConfigStatus>>>
export type GetVoiceLiveConfigStatusQueryError = Error


/**
 * @summary Check authenticated staff browser-voice availability and configuration
 */

export function useGetVoiceLiveConfigStatus<TData = Awaited<ReturnType<typeof getVoiceLiveConfigStatus>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getVoiceLiveConfigStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetVoiceLiveConfigStatusQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateVoiceLiveTicketUrl = () => {




  return `/api/voice/live/ticket`
}

/**
 * @summary Mint a short-lived one-time ticket for an authenticated staff voice session
 */
export const createVoiceLiveTicket = async ( options?: Parameters<typeof customFetch>[1]): Promise<VoiceLiveTicket> => {

  return customFetch<VoiceLiveTicket>(getCreateVoiceLiveTicketUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCreateVoiceLiveTicketMutationKey = () => ['createVoiceLiveTicket'] as const;

export const getCreateVoiceLiveTicketMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createVoiceLiveTicket>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createVoiceLiveTicket>>, TError,void, TContext> => {

const mutationKey = getCreateVoiceLiveTicketMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createVoiceLiveTicket>>, void> = () => {


          return  createVoiceLiveTicket(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateVoiceLiveTicketMutationResult = NonNullable<Awaited<ReturnType<typeof createVoiceLiveTicket>>>

    export type CreateVoiceLiveTicketMutationError = Error


    /**
 * @summary Mint a short-lived one-time ticket for an authenticated staff voice session
 */
export const useCreateVoiceLiveTicket = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createVoiceLiveTicket>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createVoiceLiveTicket>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getCreateVoiceLiveTicketMutationOptions(options));
    }

export const getConnectStaffVoiceLiveUrl = (params: ConnectStaffVoiceLiveParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/voice/live?${stringifiedParams}` : `/api/voice/live`
}

/**
 * @summary Open an authenticated staff browser voice websocket using a one-time ticket
 */
export const connectStaffVoiceLive = async (params: ConnectStaffVoiceLiveParams, options?: Parameters<typeof customFetch>[1]): Promise<unknown> => {

  return customFetch<unknown>(getConnectStaffVoiceLiveUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getConnectStaffVoiceLiveQueryKey = (params?: ConnectStaffVoiceLiveParams,) => {
    return [
    `/api/voice/live`, ...(params ? [params] : [])
    ] as const;
    }


export const getConnectStaffVoiceLiveQueryOptions = <TData = Awaited<ReturnType<typeof connectStaffVoiceLive>>, TError = void>(params: ConnectStaffVoiceLiveParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof connectStaffVoiceLive>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getConnectStaffVoiceLiveQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof connectStaffVoiceLive>>> = ({ signal }) => connectStaffVoiceLive(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof connectStaffVoiceLive>>, TError, TData> & { queryKey: QueryKey }
}

export type ConnectStaffVoiceLiveQueryResult = NonNullable<Awaited<ReturnType<typeof connectStaffVoiceLive>>>
export type ConnectStaffVoiceLiveQueryError = void


/**
 * @summary Open an authenticated staff browser voice websocket using a one-time ticket
 */

export function useConnectStaffVoiceLive<TData = Awaited<ReturnType<typeof connectStaffVoiceLive>>, TError = void>(
 params: ConnectStaffVoiceLiveParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof connectStaffVoiceLive>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getConnectStaffVoiceLiveQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPublicVoiceStatusUrl = () => {




  return `/api/public/voice/status`
}

/**
 * @summary Get caller-safe browser voice availability and recording disclosure
 */
export const getPublicVoiceStatus = async ( options?: Parameters<typeof customFetch>[1]): Promise<PublicVoiceStatus> => {

  return customFetch<PublicVoiceStatus>(getGetPublicVoiceStatusUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPublicVoiceStatusQueryKey = () => {
    return [
    `/api/public/voice/status`
    ] as const;
    }


export const getGetPublicVoiceStatusQueryOptions = <TData = Awaited<ReturnType<typeof getPublicVoiceStatus>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicVoiceStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPublicVoiceStatusQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPublicVoiceStatus>>> = ({ signal }) => getPublicVoiceStatus({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPublicVoiceStatus>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPublicVoiceStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getPublicVoiceStatus>>>
export type GetPublicVoiceStatusQueryError = unknown


/**
 * @summary Get caller-safe browser voice availability and recording disclosure
 */

export function useGetPublicVoiceStatus<TData = Awaited<ReturnType<typeof getPublicVoiceStatus>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicVoiceStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPublicVoiceStatusQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConnectPublicVoiceLiveUrl = (params?: ConnectPublicVoiceLiveParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/public/voice/live?${stringifiedParams}` : `/api/public/voice/live`
}

/**
 * @summary Open a browser voice websocket, optionally scoped to a microsite slug
 */
export const connectPublicVoiceLive = async (params?: ConnectPublicVoiceLiveParams, options?: Parameters<typeof customFetch>[1]): Promise<unknown> => {

  return customFetch<unknown>(getConnectPublicVoiceLiveUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getConnectPublicVoiceLiveQueryKey = (params?: ConnectPublicVoiceLiveParams,) => {
    return [
    `/api/public/voice/live`, ...(params ? [params] : [])
    ] as const;
    }


export const getConnectPublicVoiceLiveQueryOptions = <TData = Awaited<ReturnType<typeof connectPublicVoiceLive>>, TError = void>(params?: ConnectPublicVoiceLiveParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof connectPublicVoiceLive>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getConnectPublicVoiceLiveQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof connectPublicVoiceLive>>> = ({ signal }) => connectPublicVoiceLive(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof connectPublicVoiceLive>>, TError, TData> & { queryKey: QueryKey }
}

export type ConnectPublicVoiceLiveQueryResult = NonNullable<Awaited<ReturnType<typeof connectPublicVoiceLive>>>
export type ConnectPublicVoiceLiveQueryError = void


/**
 * @summary Open a browser voice websocket, optionally scoped to a microsite slug
 */

export function useConnectPublicVoiceLive<TData = Awaited<ReturnType<typeof connectPublicVoiceLive>>, TError = void>(
 params?: ConnectPublicVoiceLiveParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof connectPublicVoiceLive>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getConnectPublicVoiceLiveQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPublicMicrositeVoiceStatusUrl = (slug: string,) => {




  return `/api/public/microsites/${slug}/voice/status`
}

/**
 * @summary Get caller-safe voice and callback availability for a microsite
 */
export const getPublicMicrositeVoiceStatus = async (slug: string, options?: Parameters<typeof customFetch>[1]): Promise<PublicVoiceStatus> => {

  return customFetch<PublicVoiceStatus>(getGetPublicMicrositeVoiceStatusUrl(slug),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPublicMicrositeVoiceStatusQueryKey = (slug: string,) => {
    return [
    `/api/public/microsites/${slug}/voice/status`
    ] as const;
    }


export const getGetPublicMicrositeVoiceStatusQueryOptions = <TData = Awaited<ReturnType<typeof getPublicMicrositeVoiceStatus>>, TError = unknown>(slug: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicMicrositeVoiceStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPublicMicrositeVoiceStatusQueryKey(slug);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPublicMicrositeVoiceStatus>>> = ({ signal }) => getPublicMicrositeVoiceStatus(slug, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: slug !== null && slug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPublicMicrositeVoiceStatus>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPublicMicrositeVoiceStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getPublicMicrositeVoiceStatus>>>
export type GetPublicMicrositeVoiceStatusQueryError = unknown


/**
 * @summary Get caller-safe voice and callback availability for a microsite
 */

export function useGetPublicMicrositeVoiceStatus<TData = Awaited<ReturnType<typeof getPublicMicrositeVoiceStatus>>, TError = unknown>(
 slug: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPublicMicrositeVoiceStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetPublicMicrositeVoiceStatusQueryOptions(slug,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRequestPublicMicrositeCallbackUrl = (slug: string,) => {




  return `/api/public/microsites/${slug}/callback`
}

/**
 * @summary Request an entity-scoped outbound click-to-call callback
 */
export const requestPublicMicrositeCallback = async (slug: string,
    micrositeCallbackRequest: MicrositeCallbackRequest, options?: Parameters<typeof customFetch>[1]): Promise<MicrositeCallbackResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<MicrositeCallbackResult>(getRequestPublicMicrositeCallbackUrl(slug),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(micrositeCallbackRequest)
  }
);}





export const getRequestPublicMicrositeCallbackMutationKey = () => ['requestPublicMicrositeCallback'] as const;

export const getRequestPublicMicrositeCallbackMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestPublicMicrositeCallback>>, TError,RequestPublicMicrositeCallbackMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof requestPublicMicrositeCallback>>, TError,RequestPublicMicrositeCallbackMutationVariables, TContext> => {

const mutationKey = getRequestPublicMicrositeCallbackMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof requestPublicMicrositeCallback>>, RequestPublicMicrositeCallbackMutationVariables> = (props) => {
          const {slug,data} = props ?? {};

          return  requestPublicMicrositeCallback(slug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RequestPublicMicrositeCallbackMutationResult = NonNullable<Awaited<ReturnType<typeof requestPublicMicrositeCallback>>>
    export type RequestPublicMicrositeCallbackMutationBody = MicrositeCallbackRequest
    export type RequestPublicMicrositeCallbackMutationError = unknown
    export type RequestPublicMicrositeCallbackMutationVariables = {slug: string;data: MicrositeCallbackRequest}

    /**
 * @summary Request an entity-scoped outbound click-to-call callback
 */
export const useRequestPublicMicrositeCallback = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestPublicMicrositeCallback>>, TError,RequestPublicMicrositeCallbackMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof requestPublicMicrositeCallback>>,
        TError,
        RequestPublicMicrositeCallbackMutationVariables,
        TContext
      > => {
      return useMutation(getRequestPublicMicrositeCallbackMutationOptions(options));
    }

export const getGetVoiceStyleWorkspaceUrl = () => {




  return `/api/voice/style`
}

/**
 * @summary Get the active entity voice style, editable draft, capabilities and activation evidence
 */
export const getVoiceStyleWorkspace = async ( options?: Parameters<typeof customFetch>[1]): Promise<VoiceStyleWorkspace> => {

  return customFetch<VoiceStyleWorkspace>(getGetVoiceStyleWorkspaceUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetVoiceStyleWorkspaceQueryKey = () => {
    return [
    `/api/voice/style`
    ] as const;
    }


export const getGetVoiceStyleWorkspaceQueryOptions = <TData = Awaited<ReturnType<typeof getVoiceStyleWorkspace>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getVoiceStyleWorkspace>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetVoiceStyleWorkspaceQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getVoiceStyleWorkspace>>> = ({ signal }) => getVoiceStyleWorkspace({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getVoiceStyleWorkspace>>, TError, TData> & { queryKey: QueryKey }
}

export type GetVoiceStyleWorkspaceQueryResult = NonNullable<Awaited<ReturnType<typeof getVoiceStyleWorkspace>>>
export type GetVoiceStyleWorkspaceQueryError = unknown


/**
 * @summary Get the active entity voice style, editable draft, capabilities and activation evidence
 */

export function useGetVoiceStyleWorkspace<TData = Awaited<ReturnType<typeof getVoiceStyleWorkspace>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getVoiceStyleWorkspace>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetVoiceStyleWorkspaceQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSaveVoiceStyleDraftUrl = () => {




  return `/api/voice/style/draft`
}

/**
 * @summary Optimistically create or update the entity voice-style draft
 */
export const saveVoiceStyleDraft = async (saveVoiceStyleDraftRequest: SaveVoiceStyleDraftRequest, options?: Parameters<typeof customFetch>[1]): Promise<VoiceStyleVersion> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<VoiceStyleVersion>(getSaveVoiceStyleDraftUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(saveVoiceStyleDraftRequest)
  }
);}





export const getSaveVoiceStyleDraftMutationKey = () => ['saveVoiceStyleDraft'] as const;

export const getSaveVoiceStyleDraftMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveVoiceStyleDraft>>, TError,SaveVoiceStyleDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof saveVoiceStyleDraft>>, TError,SaveVoiceStyleDraftMutationVariables, TContext> => {

const mutationKey = getSaveVoiceStyleDraftMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof saveVoiceStyleDraft>>, SaveVoiceStyleDraftMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  saveVoiceStyleDraft(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveVoiceStyleDraftMutationResult = NonNullable<Awaited<ReturnType<typeof saveVoiceStyleDraft>>>
    export type SaveVoiceStyleDraftMutationBody = SaveVoiceStyleDraftRequest
    export type SaveVoiceStyleDraftMutationError = Error
    export type SaveVoiceStyleDraftMutationVariables = {data: SaveVoiceStyleDraftRequest}

    /**
 * @summary Optimistically create or update the entity voice-style draft
 */
export const useSaveVoiceStyleDraft = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveVoiceStyleDraft>>, TError,SaveVoiceStyleDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof saveVoiceStyleDraft>>,
        TError,
        SaveVoiceStyleDraftMutationVariables,
        TContext
      > => {
      return useMutation(getSaveVoiceStyleDraftMutationOptions(options));
    }

export const getProposeVoiceStyleUrl = () => {




  return `/api/voice/style/proposals`
}

/**
 * @summary Generate a review-only AI voice-style proposal for the current draft
 */
export const proposeVoiceStyle = async (voiceStyleProposalRequest: VoiceStyleProposalRequest, options?: Parameters<typeof customFetch>[1]): Promise<VoiceStyleProposal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<VoiceStyleProposal>(getProposeVoiceStyleUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(voiceStyleProposalRequest)
  }
);}





export const getProposeVoiceStyleMutationKey = () => ['proposeVoiceStyle'] as const;

export const getProposeVoiceStyleMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof proposeVoiceStyle>>, TError,ProposeVoiceStyleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof proposeVoiceStyle>>, TError,ProposeVoiceStyleMutationVariables, TContext> => {

const mutationKey = getProposeVoiceStyleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof proposeVoiceStyle>>, ProposeVoiceStyleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  proposeVoiceStyle(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ProposeVoiceStyleMutationResult = NonNullable<Awaited<ReturnType<typeof proposeVoiceStyle>>>
    export type ProposeVoiceStyleMutationBody = VoiceStyleProposalRequest
    export type ProposeVoiceStyleMutationError = Error
    export type ProposeVoiceStyleMutationVariables = {data: VoiceStyleProposalRequest}

    /**
 * @summary Generate a review-only AI voice-style proposal for the current draft
 */
export const useProposeVoiceStyle = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof proposeVoiceStyle>>, TError,ProposeVoiceStyleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof proposeVoiceStyle>>,
        TError,
        ProposeVoiceStyleMutationVariables,
        TContext
      > => {
      return useMutation(getProposeVoiceStyleMutationOptions(options));
    }

export const getCreateVoiceStyleTestUrl = () => {




  return `/api/voice/style/tests`
}

/**
 * @summary Create an immutable live-test snapshot from the exact current draft
 */
export const createVoiceStyleTest = async ( options?: Parameters<typeof customFetch>[1]): Promise<VoiceStyleTestSnapshot> => {

  return customFetch<VoiceStyleTestSnapshot>(getCreateVoiceStyleTestUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCreateVoiceStyleTestMutationKey = () => ['createVoiceStyleTest'] as const;

export const getCreateVoiceStyleTestMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createVoiceStyleTest>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createVoiceStyleTest>>, TError,void, TContext> => {

const mutationKey = getCreateVoiceStyleTestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createVoiceStyleTest>>, void> = () => {


          return  createVoiceStyleTest(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateVoiceStyleTestMutationResult = NonNullable<Awaited<ReturnType<typeof createVoiceStyleTest>>>

    export type CreateVoiceStyleTestMutationError = unknown


    /**
 * @summary Create an immutable live-test snapshot from the exact current draft
 */
export const useCreateVoiceStyleTest = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createVoiceStyleTest>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createVoiceStyleTest>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getCreateVoiceStyleTestMutationOptions(options));
    }

export const getCreateVoiceStyleTestTicketUrl = (id: string,) => {




  return `/api/voice/style/tests/${id}/ticket`
}

/**
 * @summary Mint a one-use staff voice ticket bound to an immutable draft test snapshot
 */
export const createVoiceStyleTestTicket = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<VoiceLiveTicket> => {

  return customFetch<VoiceLiveTicket>(getCreateVoiceStyleTestTicketUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCreateVoiceStyleTestTicketMutationKey = () => ['createVoiceStyleTestTicket'] as const;

export const getCreateVoiceStyleTestTicketMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createVoiceStyleTestTicket>>, TError,CreateVoiceStyleTestTicketMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createVoiceStyleTestTicket>>, TError,CreateVoiceStyleTestTicketMutationVariables, TContext> => {

const mutationKey = getCreateVoiceStyleTestTicketMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createVoiceStyleTestTicket>>, CreateVoiceStyleTestTicketMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  createVoiceStyleTestTicket(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateVoiceStyleTestTicketMutationResult = NonNullable<Awaited<ReturnType<typeof createVoiceStyleTestTicket>>>

    export type CreateVoiceStyleTestTicketMutationError = unknown
    export type CreateVoiceStyleTestTicketMutationVariables = {id: string}

    /**
 * @summary Mint a one-use staff voice ticket bound to an immutable draft test snapshot
 */
export const useCreateVoiceStyleTestTicket = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createVoiceStyleTestTicket>>, TError,CreateVoiceStyleTestTicketMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createVoiceStyleTestTicket>>,
        TError,
        CreateVoiceStyleTestTicketMutationVariables,
        TContext
      > => {
      return useMutation(getCreateVoiceStyleTestTicketMutationOptions(options));
    }

export const getActivateVoiceStyleUrl = () => {




  return `/api/voice/style/activate`
}

/**
 * @summary Explicitly activate a tested exact voice-style draft
 */
export const activateVoiceStyle = async (activateVoiceStyleRequest: ActivateVoiceStyleRequest, options?: Parameters<typeof customFetch>[1]): Promise<VoiceStyleVersion> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<VoiceStyleVersion>(getActivateVoiceStyleUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(activateVoiceStyleRequest)
  }
);}





export const getActivateVoiceStyleMutationKey = () => ['activateVoiceStyle'] as const;

export const getActivateVoiceStyleMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof activateVoiceStyle>>, TError,ActivateVoiceStyleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof activateVoiceStyle>>, TError,ActivateVoiceStyleMutationVariables, TContext> => {

const mutationKey = getActivateVoiceStyleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof activateVoiceStyle>>, ActivateVoiceStyleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  activateVoiceStyle(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ActivateVoiceStyleMutationResult = NonNullable<Awaited<ReturnType<typeof activateVoiceStyle>>>
    export type ActivateVoiceStyleMutationBody = ActivateVoiceStyleRequest
    export type ActivateVoiceStyleMutationError = Error
    export type ActivateVoiceStyleMutationVariables = {data: ActivateVoiceStyleRequest}

    /**
 * @summary Explicitly activate a tested exact voice-style draft
 */
export const useActivateVoiceStyle = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof activateVoiceStyle>>, TError,ActivateVoiceStyleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof activateVoiceStyle>>,
        TError,
        ActivateVoiceStyleMutationVariables,
        TContext
      > => {
      return useMutation(getActivateVoiceStyleMutationOptions(options));
    }

export const getListVoiceStyleHistoryUrl = (params?: ListVoiceStyleHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/voice/style/history?${stringifiedParams}` : `/api/voice/style/history`
}

/**
 * @summary List immutable active and retired voice-style versions
 */
export const listVoiceStyleHistory = async (params?: ListVoiceStyleHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<ListVoiceStyleHistory200> => {

  return customFetch<ListVoiceStyleHistory200>(getListVoiceStyleHistoryUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListVoiceStyleHistoryQueryKey = (params?: ListVoiceStyleHistoryParams,) => {
    return [
    `/api/voice/style/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListVoiceStyleHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listVoiceStyleHistory>>, TError = unknown>(params?: ListVoiceStyleHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listVoiceStyleHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListVoiceStyleHistoryQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listVoiceStyleHistory>>> = ({ signal }) => listVoiceStyleHistory(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listVoiceStyleHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListVoiceStyleHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listVoiceStyleHistory>>>
export type ListVoiceStyleHistoryQueryError = unknown


/**
 * @summary List immutable active and retired voice-style versions
 */

export function useListVoiceStyleHistory<TData = Awaited<ReturnType<typeof listVoiceStyleHistory>>, TError = unknown>(
 params?: ListVoiceStyleHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listVoiceStyleHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListVoiceStyleHistoryQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRestoreVoiceStyleVersionUrl = (id: string,) => {




  return `/api/voice/style/history/${id}/restore`
}

/**
 * @summary Restore an earlier version into the editable draft lifecycle
 */
export const restoreVoiceStyleVersion = async (id: string,
    restoreVoiceStyleVersionBody: RestoreVoiceStyleVersionBody, options?: Parameters<typeof customFetch>[1]): Promise<VoiceStyleVersion> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<VoiceStyleVersion>(getRestoreVoiceStyleVersionUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(restoreVoiceStyleVersionBody)
  }
);}





export const getRestoreVoiceStyleVersionMutationKey = () => ['restoreVoiceStyleVersion'] as const;

export const getRestoreVoiceStyleVersionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof restoreVoiceStyleVersion>>, TError,RestoreVoiceStyleVersionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof restoreVoiceStyleVersion>>, TError,RestoreVoiceStyleVersionMutationVariables, TContext> => {

const mutationKey = getRestoreVoiceStyleVersionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof restoreVoiceStyleVersion>>, RestoreVoiceStyleVersionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  restoreVoiceStyleVersion(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RestoreVoiceStyleVersionMutationResult = NonNullable<Awaited<ReturnType<typeof restoreVoiceStyleVersion>>>
    export type RestoreVoiceStyleVersionMutationBody = RestoreVoiceStyleVersionBody
    export type RestoreVoiceStyleVersionMutationError = unknown
    export type RestoreVoiceStyleVersionMutationVariables = {id: string;data: RestoreVoiceStyleVersionBody}

    /**
 * @summary Restore an earlier version into the editable draft lifecycle
 */
export const useRestoreVoiceStyleVersion = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof restoreVoiceStyleVersion>>, TError,RestoreVoiceStyleVersionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof restoreVoiceStyleVersion>>,
        TError,
        RestoreVoiceStyleVersionMutationVariables,
        TContext
      > => {
      return useMutation(getRestoreVoiceStyleVersionMutationOptions(options));
    }

export const getUpdateVoiceSettingsUrl = () => {




  return `/api/voice/settings`
}

/**
 * @summary Update the org-scoped voice-agent behavior and consent disclosure
 */
export const updateVoiceSettings = async (voiceSettings: VoiceSettings, options?: Parameters<typeof customFetch>[1]): Promise<VoiceSettings> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<VoiceSettings>(getUpdateVoiceSettingsUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(voiceSettings)
  }
);}





export const getUpdateVoiceSettingsMutationKey = () => ['updateVoiceSettings'] as const;

export const getUpdateVoiceSettingsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateVoiceSettings>>, TError,UpdateVoiceSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateVoiceSettings>>, TError,UpdateVoiceSettingsMutationVariables, TContext> => {

const mutationKey = getUpdateVoiceSettingsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateVoiceSettings>>, UpdateVoiceSettingsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateVoiceSettings(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateVoiceSettingsMutationResult = NonNullable<Awaited<ReturnType<typeof updateVoiceSettings>>>
    export type UpdateVoiceSettingsMutationBody = VoiceSettings
    export type UpdateVoiceSettingsMutationError = unknown
    export type UpdateVoiceSettingsMutationVariables = {data: VoiceSettings}

    /**
 * @summary Update the org-scoped voice-agent behavior and consent disclosure
 */
export const useUpdateVoiceSettings = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateVoiceSettings>>, TError,UpdateVoiceSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateVoiceSettings>>,
        TError,
        UpdateVoiceSettingsMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateVoiceSettingsMutationOptions(options));
    }

export const getConnectTelephonyUrl = () => {




  return `/api/voice/connect`
}

/**
 * @summary Connect and provision a Twilio-pattern telephony account
 */
export const connectTelephony = async (telephonyConnectionRequest: TelephonyConnectionRequest, options?: Parameters<typeof customFetch>[1]): Promise<VoiceStatus> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<VoiceStatus>(getConnectTelephonyUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(telephonyConnectionRequest)
  }
);}





export const getConnectTelephonyMutationKey = () => ['connectTelephony'] as const;

export const getConnectTelephonyMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof connectTelephony>>, TError,ConnectTelephonyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof connectTelephony>>, TError,ConnectTelephonyMutationVariables, TContext> => {

const mutationKey = getConnectTelephonyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof connectTelephony>>, ConnectTelephonyMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  connectTelephony(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConnectTelephonyMutationResult = NonNullable<Awaited<ReturnType<typeof connectTelephony>>>
    export type ConnectTelephonyMutationBody = TelephonyConnectionRequest
    export type ConnectTelephonyMutationError = unknown
    export type ConnectTelephonyMutationVariables = {data: TelephonyConnectionRequest}

    /**
 * @summary Connect and provision a Twilio-pattern telephony account
 */
export const useConnectTelephony = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof connectTelephony>>, TError,ConnectTelephonyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof connectTelephony>>,
        TError,
        ConnectTelephonyMutationVariables,
        TContext
      > => {
      return useMutation(getConnectTelephonyMutationOptions(options));
    }

export const getProvisionVoiceTelephonyUrl = () => {




  return `/api/voice/provision`
}

/**
 * @summary Configure the environment-backed Twilio number to use the inbound voice webhook
 */
export const provisionVoiceTelephony = async ( options?: Parameters<typeof customFetch>[1]): Promise<VoiceStatus> => {

  return customFetch<VoiceStatus>(getProvisionVoiceTelephonyUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getProvisionVoiceTelephonyMutationKey = () => ['provisionVoiceTelephony'] as const;

export const getProvisionVoiceTelephonyMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof provisionVoiceTelephony>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof provisionVoiceTelephony>>, TError,void, TContext> => {

const mutationKey = getProvisionVoiceTelephonyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof provisionVoiceTelephony>>, void> = () => {


          return  provisionVoiceTelephony(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ProvisionVoiceTelephonyMutationResult = NonNullable<Awaited<ReturnType<typeof provisionVoiceTelephony>>>

    export type ProvisionVoiceTelephonyMutationError = unknown


    /**
 * @summary Configure the environment-backed Twilio number to use the inbound voice webhook
 */
export const useProvisionVoiceTelephony = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof provisionVoiceTelephony>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof provisionVoiceTelephony>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getProvisionVoiceTelephonyMutationOptions(options));
    }

export const getDisconnectTelephonyUrl = () => {




  return `/api/voice/disconnect`
}

/**
 * @summary Disconnect the telephony account and return to simulation mode
 */
export const disconnectTelephony = async ( options?: Parameters<typeof customFetch>[1]): Promise<VoiceStatus> => {

  return customFetch<VoiceStatus>(getDisconnectTelephonyUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getDisconnectTelephonyMutationKey = () => ['disconnectTelephony'] as const;

export const getDisconnectTelephonyMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectTelephony>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof disconnectTelephony>>, TError,void, TContext> => {

const mutationKey = getDisconnectTelephonyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof disconnectTelephony>>, void> = () => {


          return  disconnectTelephony(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DisconnectTelephonyMutationResult = NonNullable<Awaited<ReturnType<typeof disconnectTelephony>>>

    export type DisconnectTelephonyMutationError = unknown


    /**
 * @summary Disconnect the telephony account and return to simulation mode
 */
export const useDisconnectTelephony = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectTelephony>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof disconnectTelephony>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getDisconnectTelephonyMutationOptions(options));
    }

export const getListVoiceCallsUrl = (params?: ListVoiceCallsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/voice/calls?${stringifiedParams}` : `/api/voice/calls`
}

export const listVoiceCalls = async (params?: ListVoiceCallsParams, options?: Parameters<typeof customFetch>[1]): Promise<ListVoiceCalls200> => {

  return customFetch<ListVoiceCalls200>(getListVoiceCallsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListVoiceCallsQueryKey = (params?: ListVoiceCallsParams,) => {
    return [
    `/api/voice/calls`, ...(params ? [params] : [])
    ] as const;
    }


export const getListVoiceCallsQueryOptions = <TData = Awaited<ReturnType<typeof listVoiceCalls>>, TError = unknown>(params?: ListVoiceCallsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listVoiceCalls>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListVoiceCallsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listVoiceCalls>>> = ({ signal }) => listVoiceCalls(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listVoiceCalls>>, TError, TData> & { queryKey: QueryKey }
}

export type ListVoiceCallsQueryResult = NonNullable<Awaited<ReturnType<typeof listVoiceCalls>>>
export type ListVoiceCallsQueryError = unknown



export function useListVoiceCalls<TData = Awaited<ReturnType<typeof listVoiceCalls>>, TError = unknown>(
 params?: ListVoiceCallsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listVoiceCalls>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListVoiceCallsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetVoiceCallUrl = (id: string,) => {




  return `/api/voice/calls/${id}`
}

export const getVoiceCall = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<VoiceCall> => {

  return customFetch<VoiceCall>(getGetVoiceCallUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetVoiceCallQueryKey = (id: string,) => {
    return [
    `/api/voice/calls/${id}`
    ] as const;
    }


export const getGetVoiceCallQueryOptions = <TData = Awaited<ReturnType<typeof getVoiceCall>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getVoiceCall>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetVoiceCallQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getVoiceCall>>> = ({ signal }) => getVoiceCall(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getVoiceCall>>, TError, TData> & { queryKey: QueryKey }
}

export type GetVoiceCallQueryResult = NonNullable<Awaited<ReturnType<typeof getVoiceCall>>>
export type GetVoiceCallQueryError = unknown



export function useGetVoiceCall<TData = Awaited<ReturnType<typeof getVoiceCall>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getVoiceCall>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetVoiceCallQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetVoiceCallRecordingUrl = (id: string,) => {




  return `/api/voice/calls/${id}/recording`
}

export const getVoiceCallRecording = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getGetVoiceCallRecordingUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetVoiceCallRecordingQueryKey = (id: string,) => {
    return [
    `/api/voice/calls/${id}/recording`
    ] as const;
    }


export const getGetVoiceCallRecordingQueryOptions = <TData = Awaited<ReturnType<typeof getVoiceCallRecording>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getVoiceCallRecording>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetVoiceCallRecordingQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getVoiceCallRecording>>> = ({ signal }) => getVoiceCallRecording(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getVoiceCallRecording>>, TError, TData> & { queryKey: QueryKey }
}

export type GetVoiceCallRecordingQueryResult = NonNullable<Awaited<ReturnType<typeof getVoiceCallRecording>>>
export type GetVoiceCallRecordingQueryError = Error



export function useGetVoiceCallRecording<TData = Awaited<ReturnType<typeof getVoiceCallRecording>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getVoiceCallRecording>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetVoiceCallRecordingQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getStartOutboundVoiceCallUrl = () => {




  return `/api/voice/calls/outbound`
}

/**
 * @summary Start an AI or click-to-call interaction for a CRM contact
 */
export const startOutboundVoiceCall = async (outboundVoiceCallRequest: OutboundVoiceCallRequest, options?: Parameters<typeof customFetch>[1]): Promise<VoiceCall> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<VoiceCall>(getStartOutboundVoiceCallUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(outboundVoiceCallRequest)
  }
);}





export const getStartOutboundVoiceCallMutationKey = () => ['startOutboundVoiceCall'] as const;

export const getStartOutboundVoiceCallMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startOutboundVoiceCall>>, TError,StartOutboundVoiceCallMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof startOutboundVoiceCall>>, TError,StartOutboundVoiceCallMutationVariables, TContext> => {

const mutationKey = getStartOutboundVoiceCallMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof startOutboundVoiceCall>>, StartOutboundVoiceCallMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  startOutboundVoiceCall(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type StartOutboundVoiceCallMutationResult = NonNullable<Awaited<ReturnType<typeof startOutboundVoiceCall>>>
    export type StartOutboundVoiceCallMutationBody = OutboundVoiceCallRequest
    export type StartOutboundVoiceCallMutationError = unknown
    export type StartOutboundVoiceCallMutationVariables = {data: OutboundVoiceCallRequest}

    /**
 * @summary Start an AI or click-to-call interaction for a CRM contact
 */
export const useStartOutboundVoiceCall = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startOutboundVoiceCall>>, TError,StartOutboundVoiceCallMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof startOutboundVoiceCall>>,
        TError,
        StartOutboundVoiceCallMutationVariables,
        TContext
      > => {
      return useMutation(getStartOutboundVoiceCallMutationOptions(options));
    }

export const getStartHealthOutreachCallUrl = (executionId: string,) => {




  return `/api/voice/outreach-executions/${executionId}/start`
}

/**
 * Revalidates the exact active plan, reviewed call brief, recipient, contact window, live sender and entity permission before starting the canonical click-to-call workflow.
 * @summary Initiate one prepared staff-led Relationship Health call
 */
export const startHealthOutreachCall = async (executionId: string, options?: Parameters<typeof customFetch>[1]): Promise<VoiceCall> => {

  return customFetch<VoiceCall>(getStartHealthOutreachCallUrl(executionId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getStartHealthOutreachCallMutationKey = () => ['startHealthOutreachCall'] as const;

export const getStartHealthOutreachCallMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startHealthOutreachCall>>, TError,StartHealthOutreachCallMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof startHealthOutreachCall>>, TError,StartHealthOutreachCallMutationVariables, TContext> => {

const mutationKey = getStartHealthOutreachCallMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof startHealthOutreachCall>>, StartHealthOutreachCallMutationVariables> = (props) => {
          const {executionId} = props ?? {};

          return  startHealthOutreachCall(executionId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type StartHealthOutreachCallMutationResult = NonNullable<Awaited<ReturnType<typeof startHealthOutreachCall>>>

    export type StartHealthOutreachCallMutationError = Error
    export type StartHealthOutreachCallMutationVariables = {executionId: string}

    /**
 * @summary Initiate one prepared staff-led Relationship Health call
 */
export const useStartHealthOutreachCall = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startHealthOutreachCall>>, TError,StartHealthOutreachCallMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof startHealthOutreachCall>>,
        TError,
        StartHealthOutreachCallMutationVariables,
        TContext
      > => {
      return useMutation(getStartHealthOutreachCallMutationOptions(options));
    }

export const getListCallAuthHistoryUrl = (params?: ListCallAuthHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/voice/call-auth/history?${stringifiedParams}` : `/api/voice/call-auth/history`
}

/**
 * Requires Workforce authentication and entity.records.read. Raw identity claims and verification material are never returned.
 * @summary List entity-scoped telephone authentication requests and grants
 */
export const listCallAuthHistory = async (params?: ListCallAuthHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<CallAuthHistoryPage> => {

  return customFetch<CallAuthHistoryPage>(getListCallAuthHistoryUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCallAuthHistoryQueryKey = (params?: ListCallAuthHistoryParams,) => {
    return [
    `/api/voice/call-auth/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListCallAuthHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listCallAuthHistory>>, TError = Error>(params?: ListCallAuthHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCallAuthHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCallAuthHistoryQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCallAuthHistory>>> = ({ signal }) => listCallAuthHistory(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCallAuthHistory>>, TError, TData> & { queryKey: QueryKey }
}

export type ListCallAuthHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listCallAuthHistory>>>
export type ListCallAuthHistoryQueryError = Error


/**
 * @summary List entity-scoped telephone authentication requests and grants
 */

export function useListCallAuthHistory<TData = Awaited<ReturnType<typeof listCallAuthHistory>>, TError = Error>(
 params?: ListCallAuthHistoryParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listCallAuthHistory>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListCallAuthHistoryQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetCallAuthStatusUrl = (callId: string,) => {




  return `/api/voice/call-auth/calls/${callId}/status`
}

/**
 * Requires Workforce authentication and never returns provider tokens, OTP material, or raw identity claims.
 * @summary Read the safe authentication state for one entity-scoped call
 */
export const getCallAuthStatus = async (callId: string, options?: Parameters<typeof customFetch>[1]): Promise<CallAuthStatus> => {

  return customFetch<CallAuthStatus>(getGetCallAuthStatusUrl(callId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetCallAuthStatusQueryKey = (callId: string,) => {
    return [
    `/api/voice/call-auth/calls/${callId}/status`
    ] as const;
    }


export const getGetCallAuthStatusQueryOptions = <TData = Awaited<ReturnType<typeof getCallAuthStatus>>, TError = Error>(callId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCallAuthStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCallAuthStatusQueryKey(callId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCallAuthStatus>>> = ({ signal }) => getCallAuthStatus(callId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: callId !== null && callId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCallAuthStatus>>, TError, TData> & { queryKey: QueryKey }
}

export type GetCallAuthStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getCallAuthStatus>>>
export type GetCallAuthStatusQueryError = Error


/**
 * @summary Read the safe authentication state for one entity-scoped call
 */

export function useGetCallAuthStatus<TData = Awaited<ReturnType<typeof getCallAuthStatus>>, TError = Error>(
 callId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getCallAuthStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetCallAuthStatusQueryOptions(callId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getApproveStaffCallAuthRequestUrl = (requestId: string,) => {




  return `/api/voice/call-auth/requests/${requestId}/approve`
}

/**
 * The server binds the grant to the validated Workforce subject, current entity permission set, and authorization revision.
 * @summary Approve a pending staff telephone request as the signed-in Workforce user
 */
export const approveStaffCallAuthRequest = async (requestId: string,
    approveCallAuthRequestInput: ApproveCallAuthRequestInput, options?: Parameters<typeof customFetch>[1]): Promise<CallAuthGrant> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CallAuthGrant>(getApproveStaffCallAuthRequestUrl(requestId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(approveCallAuthRequestInput)
  }
);}





export const getApproveStaffCallAuthRequestMutationKey = () => ['approveStaffCallAuthRequest'] as const;

export const getApproveStaffCallAuthRequestMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveStaffCallAuthRequest>>, TError,ApproveStaffCallAuthRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof approveStaffCallAuthRequest>>, TError,ApproveStaffCallAuthRequestMutationVariables, TContext> => {

const mutationKey = getApproveStaffCallAuthRequestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof approveStaffCallAuthRequest>>, ApproveStaffCallAuthRequestMutationVariables> = (props) => {
          const {requestId,data} = props ?? {};

          return  approveStaffCallAuthRequest(requestId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApproveStaffCallAuthRequestMutationResult = NonNullable<Awaited<ReturnType<typeof approveStaffCallAuthRequest>>>
    export type ApproveStaffCallAuthRequestMutationBody = ApproveCallAuthRequestInput
    export type ApproveStaffCallAuthRequestMutationError = Error
    export type ApproveStaffCallAuthRequestMutationVariables = {requestId: string;data: ApproveCallAuthRequestInput}

    /**
 * @summary Approve a pending staff telephone request as the signed-in Workforce user
 */
export const useApproveStaffCallAuthRequest = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveStaffCallAuthRequest>>, TError,ApproveStaffCallAuthRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof approveStaffCallAuthRequest>>,
        TError,
        ApproveStaffCallAuthRequestMutationVariables,
        TContext
      > => {
      return useMutation(getApproveStaffCallAuthRequestMutationOptions(options));
    }

export const getDenyStaffCallAuthRequestUrl = (requestId: string,) => {




  return `/api/voice/call-auth/requests/${requestId}/deny`
}

/**
 * @summary Deny a pending staff telephone request
 */
export const denyStaffCallAuthRequest = async (requestId: string,
    denyCallAuthRequestInput: DenyCallAuthRequestInput, options?: Parameters<typeof customFetch>[1]): Promise<CallAuthPairingRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CallAuthPairingRequest>(getDenyStaffCallAuthRequestUrl(requestId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(denyCallAuthRequestInput)
  }
);}





export const getDenyStaffCallAuthRequestMutationKey = () => ['denyStaffCallAuthRequest'] as const;

export const getDenyStaffCallAuthRequestMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof denyStaffCallAuthRequest>>, TError,DenyStaffCallAuthRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof denyStaffCallAuthRequest>>, TError,DenyStaffCallAuthRequestMutationVariables, TContext> => {

const mutationKey = getDenyStaffCallAuthRequestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof denyStaffCallAuthRequest>>, DenyStaffCallAuthRequestMutationVariables> = (props) => {
          const {requestId,data} = props ?? {};

          return  denyStaffCallAuthRequest(requestId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DenyStaffCallAuthRequestMutationResult = NonNullable<Awaited<ReturnType<typeof denyStaffCallAuthRequest>>>
    export type DenyStaffCallAuthRequestMutationBody = DenyCallAuthRequestInput
    export type DenyStaffCallAuthRequestMutationError = Error
    export type DenyStaffCallAuthRequestMutationVariables = {requestId: string;data: DenyCallAuthRequestInput}

    /**
 * @summary Deny a pending staff telephone request
 */
export const useDenyStaffCallAuthRequest = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof denyStaffCallAuthRequest>>, TError,DenyStaffCallAuthRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof denyStaffCallAuthRequest>>,
        TError,
        DenyStaffCallAuthRequestMutationVariables,
        TContext
      > => {
      return useMutation(getDenyStaffCallAuthRequestMutationOptions(options));
    }

export const getRevokeStaffCallAuthGrantUrl = (grantId: string,) => {




  return `/api/voice/call-auth/grants/${grantId}/revoke`
}

/**
 * @summary Revoke an active staff or customer telephone grant
 */
export const revokeStaffCallAuthGrant = async (grantId: string,
    revokeCallAuthGrantInput: RevokeCallAuthGrantInput, options?: Parameters<typeof customFetch>[1]): Promise<CallAuthGrant> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CallAuthGrant>(getRevokeStaffCallAuthGrantUrl(grantId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(revokeCallAuthGrantInput)
  }
);}





export const getRevokeStaffCallAuthGrantMutationKey = () => ['revokeStaffCallAuthGrant'] as const;

export const getRevokeStaffCallAuthGrantMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeStaffCallAuthGrant>>, TError,RevokeStaffCallAuthGrantMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokeStaffCallAuthGrant>>, TError,RevokeStaffCallAuthGrantMutationVariables, TContext> => {

const mutationKey = getRevokeStaffCallAuthGrantMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokeStaffCallAuthGrant>>, RevokeStaffCallAuthGrantMutationVariables> = (props) => {
          const {grantId,data} = props ?? {};

          return  revokeStaffCallAuthGrant(grantId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokeStaffCallAuthGrantMutationResult = NonNullable<Awaited<ReturnType<typeof revokeStaffCallAuthGrant>>>
    export type RevokeStaffCallAuthGrantMutationBody = RevokeCallAuthGrantInput
    export type RevokeStaffCallAuthGrantMutationError = Error
    export type RevokeStaffCallAuthGrantMutationVariables = {grantId: string;data: RevokeCallAuthGrantInput}

    /**
 * @summary Revoke an active staff or customer telephone grant
 */
export const useRevokeStaffCallAuthGrant = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeStaffCallAuthGrant>>, TError,RevokeStaffCallAuthGrantMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof revokeStaffCallAuthGrant>>,
        TError,
        RevokeStaffCallAuthGrantMutationVariables,
        TContext
      > => {
      return useMutation(getRevokeStaffCallAuthGrantMutationOptions(options));
    }

export const getListPortalCallAuthRequestsUrl = (params?: ListPortalCallAuthRequestsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/portal/voice/call-auth/requests?${stringifiedParams}` : `/api/portal/voice/call-auth/requests`
}

/**
 * Requires External ID and a server-derived portal context. Only customer requests for that account are returned.
 * @summary List call approval requests for the signed-in customer context
 */
export const listPortalCallAuthRequests = async (params?: ListPortalCallAuthRequestsParams, options?: Parameters<typeof customFetch>[1]): Promise<CallAuthHistoryPage> => {

  return customFetch<CallAuthHistoryPage>(getListPortalCallAuthRequestsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPortalCallAuthRequestsQueryKey = (params?: ListPortalCallAuthRequestsParams,) => {
    return [
    `/api/portal/voice/call-auth/requests`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPortalCallAuthRequestsQueryOptions = <TData = Awaited<ReturnType<typeof listPortalCallAuthRequests>>, TError = Error>(params?: ListPortalCallAuthRequestsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPortalCallAuthRequests>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPortalCallAuthRequestsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPortalCallAuthRequests>>> = ({ signal }) => listPortalCallAuthRequests(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPortalCallAuthRequests>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPortalCallAuthRequestsQueryResult = NonNullable<Awaited<ReturnType<typeof listPortalCallAuthRequests>>>
export type ListPortalCallAuthRequestsQueryError = Error


/**
 * @summary List call approval requests for the signed-in customer context
 */

export function useListPortalCallAuthRequests<TData = Awaited<ReturnType<typeof listPortalCallAuthRequests>>, TError = Error>(
 params?: ListPortalCallAuthRequestsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPortalCallAuthRequests>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPortalCallAuthRequestsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getApproveCustomerCallAuthRequestUrl = (requestId: string,) => {




  return `/api/portal/voice/call-auth/requests/${requestId}/approve`
}

/**
 * The account and External ID subject are derived by the server; neither may be supplied in the request body.
 * @summary Approve a pending customer call for the signed-in portal account
 */
export const approveCustomerCallAuthRequest = async (requestId: string,
    approveCallAuthRequestInput: ApproveCallAuthRequestInput, options?: Parameters<typeof customFetch>[1]): Promise<CallAuthGrant> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CallAuthGrant>(getApproveCustomerCallAuthRequestUrl(requestId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(approveCallAuthRequestInput)
  }
);}





export const getApproveCustomerCallAuthRequestMutationKey = () => ['approveCustomerCallAuthRequest'] as const;

export const getApproveCustomerCallAuthRequestMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveCustomerCallAuthRequest>>, TError,ApproveCustomerCallAuthRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof approveCustomerCallAuthRequest>>, TError,ApproveCustomerCallAuthRequestMutationVariables, TContext> => {

const mutationKey = getApproveCustomerCallAuthRequestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof approveCustomerCallAuthRequest>>, ApproveCustomerCallAuthRequestMutationVariables> = (props) => {
          const {requestId,data} = props ?? {};

          return  approveCustomerCallAuthRequest(requestId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApproveCustomerCallAuthRequestMutationResult = NonNullable<Awaited<ReturnType<typeof approveCustomerCallAuthRequest>>>
    export type ApproveCustomerCallAuthRequestMutationBody = ApproveCallAuthRequestInput
    export type ApproveCustomerCallAuthRequestMutationError = Error
    export type ApproveCustomerCallAuthRequestMutationVariables = {requestId: string;data: ApproveCallAuthRequestInput}

    /**
 * @summary Approve a pending customer call for the signed-in portal account
 */
export const useApproveCustomerCallAuthRequest = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveCustomerCallAuthRequest>>, TError,ApproveCustomerCallAuthRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof approveCustomerCallAuthRequest>>,
        TError,
        ApproveCustomerCallAuthRequestMutationVariables,
        TContext
      > => {
      return useMutation(getApproveCustomerCallAuthRequestMutationOptions(options));
    }

export const getDenyCustomerCallAuthRequestUrl = (requestId: string,) => {




  return `/api/portal/voice/call-auth/requests/${requestId}/deny`
}

/**
 * @summary Deny a pending customer call approval request
 */
export const denyCustomerCallAuthRequest = async (requestId: string,
    denyCallAuthRequestInput: DenyCallAuthRequestInput, options?: Parameters<typeof customFetch>[1]): Promise<CallAuthPairingRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CallAuthPairingRequest>(getDenyCustomerCallAuthRequestUrl(requestId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(denyCallAuthRequestInput)
  }
);}





export const getDenyCustomerCallAuthRequestMutationKey = () => ['denyCustomerCallAuthRequest'] as const;

export const getDenyCustomerCallAuthRequestMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof denyCustomerCallAuthRequest>>, TError,DenyCustomerCallAuthRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof denyCustomerCallAuthRequest>>, TError,DenyCustomerCallAuthRequestMutationVariables, TContext> => {

const mutationKey = getDenyCustomerCallAuthRequestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof denyCustomerCallAuthRequest>>, DenyCustomerCallAuthRequestMutationVariables> = (props) => {
          const {requestId,data} = props ?? {};

          return  denyCustomerCallAuthRequest(requestId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DenyCustomerCallAuthRequestMutationResult = NonNullable<Awaited<ReturnType<typeof denyCustomerCallAuthRequest>>>
    export type DenyCustomerCallAuthRequestMutationBody = DenyCallAuthRequestInput
    export type DenyCustomerCallAuthRequestMutationError = Error
    export type DenyCustomerCallAuthRequestMutationVariables = {requestId: string;data: DenyCallAuthRequestInput}

    /**
 * @summary Deny a pending customer call approval request
 */
export const useDenyCustomerCallAuthRequest = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof denyCustomerCallAuthRequest>>, TError,DenyCustomerCallAuthRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof denyCustomerCallAuthRequest>>,
        TError,
        DenyCustomerCallAuthRequestMutationVariables,
        TContext
      > => {
      return useMutation(getDenyCustomerCallAuthRequestMutationOptions(options));
    }

export const getRevokeCustomerCallAuthGrantUrl = (grantId: string,) => {




  return `/api/portal/voice/call-auth/grants/${grantId}/revoke`
}

/**
 * @summary Revoke the signed-in customer's active call grant
 */
export const revokeCustomerCallAuthGrant = async (grantId: string,
    revokeCallAuthGrantInput: RevokeCallAuthGrantInput, options?: Parameters<typeof customFetch>[1]): Promise<CallAuthGrant> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CallAuthGrant>(getRevokeCustomerCallAuthGrantUrl(grantId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(revokeCallAuthGrantInput)
  }
);}





export const getRevokeCustomerCallAuthGrantMutationKey = () => ['revokeCustomerCallAuthGrant'] as const;

export const getRevokeCustomerCallAuthGrantMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeCustomerCallAuthGrant>>, TError,RevokeCustomerCallAuthGrantMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokeCustomerCallAuthGrant>>, TError,RevokeCustomerCallAuthGrantMutationVariables, TContext> => {

const mutationKey = getRevokeCustomerCallAuthGrantMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokeCustomerCallAuthGrant>>, RevokeCustomerCallAuthGrantMutationVariables> = (props) => {
          const {grantId,data} = props ?? {};

          return  revokeCustomerCallAuthGrant(grantId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokeCustomerCallAuthGrantMutationResult = NonNullable<Awaited<ReturnType<typeof revokeCustomerCallAuthGrant>>>
    export type RevokeCustomerCallAuthGrantMutationBody = RevokeCallAuthGrantInput
    export type RevokeCustomerCallAuthGrantMutationError = Error
    export type RevokeCustomerCallAuthGrantMutationVariables = {grantId: string;data: RevokeCallAuthGrantInput}

    /**
 * @summary Revoke the signed-in customer's active call grant
 */
export const useRevokeCustomerCallAuthGrant = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeCustomerCallAuthGrant>>, TError,RevokeCustomerCallAuthGrantMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof revokeCustomerCallAuthGrant>>,
        TError,
        RevokeCustomerCallAuthGrantMutationVariables,
        TContext
      > => {
      return useMutation(getRevokeCustomerCallAuthGrantMutationOptions(options));
    }

export const getSimulateInboundVoiceCallUrl = () => {




  return `/api/voice/simulations/inbound`
}

/**
 * @summary Exercise the complete inbound voice-agent loop without a telephony account
 */
export const simulateInboundVoiceCall = async (inboundVoiceSimulationRequest: InboundVoiceSimulationRequest, options?: Parameters<typeof customFetch>[1]): Promise<VoiceCall> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<VoiceCall>(getSimulateInboundVoiceCallUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(inboundVoiceSimulationRequest)
  }
);}





export const getSimulateInboundVoiceCallMutationKey = () => ['simulateInboundVoiceCall'] as const;

export const getSimulateInboundVoiceCallMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof simulateInboundVoiceCall>>, TError,SimulateInboundVoiceCallMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof simulateInboundVoiceCall>>, TError,SimulateInboundVoiceCallMutationVariables, TContext> => {

const mutationKey = getSimulateInboundVoiceCallMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof simulateInboundVoiceCall>>, SimulateInboundVoiceCallMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  simulateInboundVoiceCall(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SimulateInboundVoiceCallMutationResult = NonNullable<Awaited<ReturnType<typeof simulateInboundVoiceCall>>>
    export type SimulateInboundVoiceCallMutationBody = InboundVoiceSimulationRequest
    export type SimulateInboundVoiceCallMutationError = unknown
    export type SimulateInboundVoiceCallMutationVariables = {data: InboundVoiceSimulationRequest}

    /**
 * @summary Exercise the complete inbound voice-agent loop without a telephony account
 */
export const useSimulateInboundVoiceCall = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof simulateInboundVoiceCall>>, TError,SimulateInboundVoiceCallMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof simulateInboundVoiceCall>>,
        TError,
        SimulateInboundVoiceCallMutationVariables,
        TContext
      > => {
      return useMutation(getSimulateInboundVoiceCallMutationOptions(options));
    }

export const getReceiveTwilioInboundVoiceCallUrl = () => {




  return `/api/voice/webhooks/twilio/inbound`
}

/**
 * @summary Twilio-compatible inbound call webhook
 */
export const receiveTwilioInboundVoiceCall = async (receiveTwilioInboundVoiceCallBody?: ReceiveTwilioInboundVoiceCallBody, options?: Parameters<typeof customFetch>[1]): Promise<string> => {
    const formUrlEncoded = new URLSearchParams();
if(receiveTwilioInboundVoiceCallBody?.CallSid !== undefined) {
 formUrlEncoded.append(`CallSid`, receiveTwilioInboundVoiceCallBody.CallSid);
 }
if(receiveTwilioInboundVoiceCallBody?.From !== undefined) {
 formUrlEncoded.append(`From`, receiveTwilioInboundVoiceCallBody.From);
 }
if(receiveTwilioInboundVoiceCallBody?.To !== undefined) {
 formUrlEncoded.append(`To`, receiveTwilioInboundVoiceCallBody.To);
 }

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<string>(getReceiveTwilioInboundVoiceCallUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', ...getHeaders(options?.headers) },
    body: formUrlEncoded
  }
);}





export const getReceiveTwilioInboundVoiceCallMutationKey = () => ['receiveTwilioInboundVoiceCall'] as const;

export const getReceiveTwilioInboundVoiceCallMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof receiveTwilioInboundVoiceCall>>, TError,ReceiveTwilioInboundVoiceCallMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof receiveTwilioInboundVoiceCall>>, TError,ReceiveTwilioInboundVoiceCallMutationVariables, TContext> => {

const mutationKey = getReceiveTwilioInboundVoiceCallMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof receiveTwilioInboundVoiceCall>>, ReceiveTwilioInboundVoiceCallMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  receiveTwilioInboundVoiceCall(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReceiveTwilioInboundVoiceCallMutationResult = NonNullable<Awaited<ReturnType<typeof receiveTwilioInboundVoiceCall>>>
    export type ReceiveTwilioInboundVoiceCallMutationBody = ReceiveTwilioInboundVoiceCallBody | undefined
    export type ReceiveTwilioInboundVoiceCallMutationError = unknown
    export type ReceiveTwilioInboundVoiceCallMutationVariables = {data?: ReceiveTwilioInboundVoiceCallBody}

    /**
 * @summary Twilio-compatible inbound call webhook
 */
export const useReceiveTwilioInboundVoiceCall = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof receiveTwilioInboundVoiceCall>>, TError,ReceiveTwilioInboundVoiceCallMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof receiveTwilioInboundVoiceCall>>,
        TError,
        ReceiveTwilioInboundVoiceCallMutationVariables,
        TContext
      > => {
      return useMutation(getReceiveTwilioInboundVoiceCallMutationOptions(options));
    }

export const getAnswerTwilioOutboundVoiceCallUrl = (params: AnswerTwilioOutboundVoiceCallParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/voice/webhooks/twilio/answer?${stringifiedParams}` : `/api/voice/webhooks/twilio/answer`
}

export const answerTwilioOutboundVoiceCall = async (params: AnswerTwilioOutboundVoiceCallParams,
    answerTwilioOutboundVoiceCallBody?: AnswerTwilioOutboundVoiceCallBody, options?: Parameters<typeof customFetch>[1]): Promise<string> => {
    const formUrlEncoded = new URLSearchParams();
if(answerTwilioOutboundVoiceCallBody?.CallSid !== undefined) {
 formUrlEncoded.append(`CallSid`, answerTwilioOutboundVoiceCallBody.CallSid);
 }

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<string>(getAnswerTwilioOutboundVoiceCallUrl(params),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', ...getHeaders(options?.headers) },
    body: formUrlEncoded
  }
);}





export const getAnswerTwilioOutboundVoiceCallMutationKey = () => ['answerTwilioOutboundVoiceCall'] as const;

export const getAnswerTwilioOutboundVoiceCallMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof answerTwilioOutboundVoiceCall>>, TError,AnswerTwilioOutboundVoiceCallMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof answerTwilioOutboundVoiceCall>>, TError,AnswerTwilioOutboundVoiceCallMutationVariables, TContext> => {

const mutationKey = getAnswerTwilioOutboundVoiceCallMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof answerTwilioOutboundVoiceCall>>, AnswerTwilioOutboundVoiceCallMutationVariables> = (props) => {
          const {params,data} = props ?? {};

          return  answerTwilioOutboundVoiceCall(params,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AnswerTwilioOutboundVoiceCallMutationResult = NonNullable<Awaited<ReturnType<typeof answerTwilioOutboundVoiceCall>>>
    export type AnswerTwilioOutboundVoiceCallMutationBody = AnswerTwilioOutboundVoiceCallBody | undefined
    export type AnswerTwilioOutboundVoiceCallMutationError = unknown
    export type AnswerTwilioOutboundVoiceCallMutationVariables = {params: AnswerTwilioOutboundVoiceCallParams;data?: AnswerTwilioOutboundVoiceCallBody}

    export const useAnswerTwilioOutboundVoiceCall = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof answerTwilioOutboundVoiceCall>>, TError,AnswerTwilioOutboundVoiceCallMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof answerTwilioOutboundVoiceCall>>,
        TError,
        AnswerTwilioOutboundVoiceCallMutationVariables,
        TContext
      > => {
      return useMutation(getAnswerTwilioOutboundVoiceCallMutationOptions(options));
    }

export const getContinueTwilioVoiceTurnUrl = (params: ContinueTwilioVoiceTurnParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/voice/webhooks/twilio/turn?${stringifiedParams}` : `/api/voice/webhooks/twilio/turn`
}

export const continueTwilioVoiceTurn = async (params: ContinueTwilioVoiceTurnParams,
    continueTwilioVoiceTurnBody?: ContinueTwilioVoiceTurnBody, options?: Parameters<typeof customFetch>[1]): Promise<string> => {
    const formUrlEncoded = new URLSearchParams();
if(continueTwilioVoiceTurnBody?.CallSid !== undefined) {
 formUrlEncoded.append(`CallSid`, continueTwilioVoiceTurnBody.CallSid);
 }
if(continueTwilioVoiceTurnBody?.SpeechResult !== undefined) {
 formUrlEncoded.append(`SpeechResult`, continueTwilioVoiceTurnBody.SpeechResult);
 }

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<string>(getContinueTwilioVoiceTurnUrl(params),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', ...getHeaders(options?.headers) },
    body: formUrlEncoded
  }
);}





export const getContinueTwilioVoiceTurnMutationKey = () => ['continueTwilioVoiceTurn'] as const;

export const getContinueTwilioVoiceTurnMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof continueTwilioVoiceTurn>>, TError,ContinueTwilioVoiceTurnMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof continueTwilioVoiceTurn>>, TError,ContinueTwilioVoiceTurnMutationVariables, TContext> => {

const mutationKey = getContinueTwilioVoiceTurnMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof continueTwilioVoiceTurn>>, ContinueTwilioVoiceTurnMutationVariables> = (props) => {
          const {params,data} = props ?? {};

          return  continueTwilioVoiceTurn(params,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ContinueTwilioVoiceTurnMutationResult = NonNullable<Awaited<ReturnType<typeof continueTwilioVoiceTurn>>>
    export type ContinueTwilioVoiceTurnMutationBody = ContinueTwilioVoiceTurnBody | undefined
    export type ContinueTwilioVoiceTurnMutationError = unknown
    export type ContinueTwilioVoiceTurnMutationVariables = {params: ContinueTwilioVoiceTurnParams;data?: ContinueTwilioVoiceTurnBody}

    export const useContinueTwilioVoiceTurn = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof continueTwilioVoiceTurn>>, TError,ContinueTwilioVoiceTurnMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof continueTwilioVoiceTurn>>,
        TError,
        ContinueTwilioVoiceTurnMutationVariables,
        TContext
      > => {
      return useMutation(getContinueTwilioVoiceTurnMutationOptions(options));
    }

export const getUpdateTwilioVoiceCallStatusUrl = (params?: UpdateTwilioVoiceCallStatusParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/voice/webhooks/twilio/status?${stringifiedParams}` : `/api/voice/webhooks/twilio/status`
}

/**
 * @summary Accept generic Twilio call status callbacks and resolve the CRM call by CallSid
 */
export const updateTwilioVoiceCallStatus = async (updateTwilioVoiceCallStatusBody: UpdateTwilioVoiceCallStatusBody,
    params?: UpdateTwilioVoiceCallStatusParams, options?: Parameters<typeof customFetch>[1]): Promise<void> => {
    const formUrlEncoded = new URLSearchParams();
formUrlEncoded.append(`CallSid`, updateTwilioVoiceCallStatusBody.CallSid);
formUrlEncoded.append(`CallStatus`, updateTwilioVoiceCallStatusBody.CallStatus);
if(updateTwilioVoiceCallStatusBody.CallDuration !== undefined) {
 formUrlEncoded.append(`CallDuration`, updateTwilioVoiceCallStatusBody.CallDuration);
 }
if(updateTwilioVoiceCallStatusBody.SequenceNumber !== undefined) {
 formUrlEncoded.append(`SequenceNumber`, updateTwilioVoiceCallStatusBody.SequenceNumber);
 }

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<void>(getUpdateTwilioVoiceCallStatusUrl(params),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', ...getHeaders(options?.headers) },
    body: formUrlEncoded
  }
);}





export const getUpdateTwilioVoiceCallStatusMutationKey = () => ['updateTwilioVoiceCallStatus'] as const;

export const getUpdateTwilioVoiceCallStatusMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTwilioVoiceCallStatus>>, TError,UpdateTwilioVoiceCallStatusMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateTwilioVoiceCallStatus>>, TError,UpdateTwilioVoiceCallStatusMutationVariables, TContext> => {

const mutationKey = getUpdateTwilioVoiceCallStatusMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateTwilioVoiceCallStatus>>, UpdateTwilioVoiceCallStatusMutationVariables> = (props) => {
          const {data,params} = props ?? {};

          return  updateTwilioVoiceCallStatus(data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateTwilioVoiceCallStatusMutationResult = NonNullable<Awaited<ReturnType<typeof updateTwilioVoiceCallStatus>>>
    export type UpdateTwilioVoiceCallStatusMutationBody = UpdateTwilioVoiceCallStatusBody
    export type UpdateTwilioVoiceCallStatusMutationError = unknown
    export type UpdateTwilioVoiceCallStatusMutationVariables = {data: UpdateTwilioVoiceCallStatusBody;params?: UpdateTwilioVoiceCallStatusParams}

    /**
 * @summary Accept generic Twilio call status callbacks and resolve the CRM call by CallSid
 */
export const useUpdateTwilioVoiceCallStatus = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTwilioVoiceCallStatus>>, TError,UpdateTwilioVoiceCallStatusMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateTwilioVoiceCallStatus>>,
        TError,
        UpdateTwilioVoiceCallStatusMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateTwilioVoiceCallStatusMutationOptions(options));
    }

export const getUpdateTwilioVoiceRecordingUrl = (params: UpdateTwilioVoiceRecordingParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/voice/webhooks/twilio/recording?${stringifiedParams}` : `/api/voice/webhooks/twilio/recording`
}

export const updateTwilioVoiceRecording = async (params: UpdateTwilioVoiceRecordingParams,
    updateTwilioVoiceRecordingBody?: UpdateTwilioVoiceRecordingBody, options?: Parameters<typeof customFetch>[1]): Promise<void> => {
    const formUrlEncoded = new URLSearchParams();
if(updateTwilioVoiceRecordingBody?.CallSid !== undefined) {
 formUrlEncoded.append(`CallSid`, updateTwilioVoiceRecordingBody.CallSid);
 }
if(updateTwilioVoiceRecordingBody?.RecordingSid !== undefined) {
 formUrlEncoded.append(`RecordingSid`, updateTwilioVoiceRecordingBody.RecordingSid);
 }
if(updateTwilioVoiceRecordingBody?.RecordingUrl !== undefined) {
 formUrlEncoded.append(`RecordingUrl`, updateTwilioVoiceRecordingBody.RecordingUrl);
 }

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<void>(getUpdateTwilioVoiceRecordingUrl(params),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', ...getHeaders(options?.headers) },
    body: formUrlEncoded
  }
);}





export const getUpdateTwilioVoiceRecordingMutationKey = () => ['updateTwilioVoiceRecording'] as const;

export const getUpdateTwilioVoiceRecordingMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTwilioVoiceRecording>>, TError,UpdateTwilioVoiceRecordingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateTwilioVoiceRecording>>, TError,UpdateTwilioVoiceRecordingMutationVariables, TContext> => {

const mutationKey = getUpdateTwilioVoiceRecordingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateTwilioVoiceRecording>>, UpdateTwilioVoiceRecordingMutationVariables> = (props) => {
          const {params,data} = props ?? {};

          return  updateTwilioVoiceRecording(params,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateTwilioVoiceRecordingMutationResult = NonNullable<Awaited<ReturnType<typeof updateTwilioVoiceRecording>>>
    export type UpdateTwilioVoiceRecordingMutationBody = UpdateTwilioVoiceRecordingBody | undefined
    export type UpdateTwilioVoiceRecordingMutationError = unknown
    export type UpdateTwilioVoiceRecordingMutationVariables = {params: UpdateTwilioVoiceRecordingParams;data?: UpdateTwilioVoiceRecordingBody}

    export const useUpdateTwilioVoiceRecording = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateTwilioVoiceRecording>>, TError,UpdateTwilioVoiceRecordingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateTwilioVoiceRecording>>,
        TError,
        UpdateTwilioVoiceRecordingMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateTwilioVoiceRecordingMutationOptions(options));
    }

export const getVerifyTwilioCallAuthDigitsUrl = (params: VerifyTwilioCallAuthDigitsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/voice/webhooks/twilio/call-auth/dtmf?${stringifiedParams}` : `/api/voice/webhooks/twilio/call-auth/dtmf`
}

/**
 * Public only at the HTTP boundary. The exact callback URL and form payload must pass Twilio signature validation. Digits are never returned, persisted, logged, recorded, transcribed, or sent to the AI.
 * @summary Accept bounded keypad digits for one customer verification challenge
 */
export const verifyTwilioCallAuthDigits = async (twilioCallAuthDtmfInput: TwilioCallAuthDtmfInput,
    params: VerifyTwilioCallAuthDigitsParams, options?: Parameters<typeof customFetch>[1]): Promise<string> => {
    const formUrlEncoded = new URLSearchParams();
formUrlEncoded.append(`CallSid`, twilioCallAuthDtmfInput.CallSid);
formUrlEncoded.append(`Digits`, twilioCallAuthDtmfInput.Digits);

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<string>(getVerifyTwilioCallAuthDigitsUrl(params),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', ...getHeaders(options?.headers) },
    body: formUrlEncoded
  }
);}





export const getVerifyTwilioCallAuthDigitsMutationKey = () => ['verifyTwilioCallAuthDigits'] as const;

export const getVerifyTwilioCallAuthDigitsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof verifyTwilioCallAuthDigits>>, TError,VerifyTwilioCallAuthDigitsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof verifyTwilioCallAuthDigits>>, TError,VerifyTwilioCallAuthDigitsMutationVariables, TContext> => {

const mutationKey = getVerifyTwilioCallAuthDigitsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof verifyTwilioCallAuthDigits>>, VerifyTwilioCallAuthDigitsMutationVariables> = (props) => {
          const {data,params} = props ?? {};

          return  verifyTwilioCallAuthDigits(data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type VerifyTwilioCallAuthDigitsMutationResult = NonNullable<Awaited<ReturnType<typeof verifyTwilioCallAuthDigits>>>
    export type VerifyTwilioCallAuthDigitsMutationBody = TwilioCallAuthDtmfInput
    export type VerifyTwilioCallAuthDigitsMutationError = Error
    export type VerifyTwilioCallAuthDigitsMutationVariables = {data: TwilioCallAuthDtmfInput;params: VerifyTwilioCallAuthDigitsParams}

    /**
 * @summary Accept bounded keypad digits for one customer verification challenge
 */
export const useVerifyTwilioCallAuthDigits = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof verifyTwilioCallAuthDigits>>, TError,VerifyTwilioCallAuthDigitsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof verifyTwilioCallAuthDigits>>,
        TError,
        VerifyTwilioCallAuthDigitsMutationVariables,
        TContext
      > => {
      return useMutation(getVerifyTwilioCallAuthDigitsMutationOptions(options));
    }

export const getReceiveTwilioCallAuthSmsStatusUrl = (params: ReceiveTwilioCallAuthSmsStatusParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/voice/webhooks/twilio/call-auth/sms-status?${stringifiedParams}` : `/api/voice/webhooks/twilio/call-auth/sms-status`
}

/**
 * The callback is signature-validated and intentionally stores no message body, destination, code, or campaign interaction.
 * @summary Accept signed provider delivery status for an authentication SMS
 */
export const receiveTwilioCallAuthSmsStatus = async (receiveTwilioCallAuthSmsStatusBody: ReceiveTwilioCallAuthSmsStatusBody,
    params: ReceiveTwilioCallAuthSmsStatusParams, options?: Parameters<typeof customFetch>[1]): Promise<void> => {
    const formUrlEncoded = new URLSearchParams();
formUrlEncoded.append(`MessageSid`, receiveTwilioCallAuthSmsStatusBody.MessageSid);
formUrlEncoded.append(`MessageStatus`, receiveTwilioCallAuthSmsStatusBody.MessageStatus);

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<void>(getReceiveTwilioCallAuthSmsStatusUrl(params),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', ...getHeaders(options?.headers) },
    body: formUrlEncoded
  }
);}





export const getReceiveTwilioCallAuthSmsStatusMutationKey = () => ['receiveTwilioCallAuthSmsStatus'] as const;

export const getReceiveTwilioCallAuthSmsStatusMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof receiveTwilioCallAuthSmsStatus>>, TError,ReceiveTwilioCallAuthSmsStatusMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof receiveTwilioCallAuthSmsStatus>>, TError,ReceiveTwilioCallAuthSmsStatusMutationVariables, TContext> => {

const mutationKey = getReceiveTwilioCallAuthSmsStatusMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof receiveTwilioCallAuthSmsStatus>>, ReceiveTwilioCallAuthSmsStatusMutationVariables> = (props) => {
          const {data,params} = props ?? {};

          return  receiveTwilioCallAuthSmsStatus(data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReceiveTwilioCallAuthSmsStatusMutationResult = NonNullable<Awaited<ReturnType<typeof receiveTwilioCallAuthSmsStatus>>>
    export type ReceiveTwilioCallAuthSmsStatusMutationBody = ReceiveTwilioCallAuthSmsStatusBody
    export type ReceiveTwilioCallAuthSmsStatusMutationError = Error
    export type ReceiveTwilioCallAuthSmsStatusMutationVariables = {data: ReceiveTwilioCallAuthSmsStatusBody;params: ReceiveTwilioCallAuthSmsStatusParams}

    /**
 * @summary Accept signed provider delivery status for an authentication SMS
 */
export const useReceiveTwilioCallAuthSmsStatus = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof receiveTwilioCallAuthSmsStatus>>, TError,ReceiveTwilioCallAuthSmsStatusMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof receiveTwilioCallAuthSmsStatus>>,
        TError,
        ReceiveTwilioCallAuthSmsStatusMutationVariables,
        TContext
      > => {
      return useMutation(getReceiveTwilioCallAuthSmsStatusMutationOptions(options));
    }

export const getListPlatformConfigUrl = () => {




  return `/api/foundation/config`
}

/**
 * @summary List effective org-scoped platform configuration
 */
export const listPlatformConfig = async ( options?: Parameters<typeof customFetch>[1]): Promise<PlatformConfigList> => {

  return customFetch<PlatformConfigList>(getListPlatformConfigUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformConfigQueryKey = () => {
    return [
    `/api/foundation/config`
    ] as const;
    }


export const getListPlatformConfigQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformConfig>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformConfig>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformConfigQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformConfig>>> = ({ signal }) => listPlatformConfig({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformConfig>>, TError, TData> & { queryKey: QueryKey }
}

export type ListPlatformConfigQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformConfig>>>
export type ListPlatformConfigQueryError = unknown


/**
 * @summary List effective org-scoped platform configuration
 */

export function useListPlatformConfig<TData = Awaited<ReturnType<typeof listPlatformConfig>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listPlatformConfig>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListPlatformConfigQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetJourneyWorkforceUrl = (journeyKey: JourneyKey,
    params?: GetJourneyWorkforceParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/journeys/${journeyKey}/workforce?${stringifiedParams}` : `/api/journeys/${journeyKey}/workforce`
}

export const getJourneyWorkforce = async (journeyKey: JourneyKey,
    params?: GetJourneyWorkforceParams, options?: Parameters<typeof customFetch>[1]): Promise<JourneyWorkforce> => {

  return customFetch<JourneyWorkforce>(getGetJourneyWorkforceUrl(journeyKey,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetJourneyWorkforceQueryKey = (journeyKey: JourneyKey,
    params?: GetJourneyWorkforceParams,) => {
    return [
    `/api/journeys/${journeyKey}/workforce`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetJourneyWorkforceQueryOptions = <TData = Awaited<ReturnType<typeof getJourneyWorkforce>>, TError = unknown>(journeyKey: JourneyKey,
    params?: GetJourneyWorkforceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getJourneyWorkforce>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetJourneyWorkforceQueryKey(journeyKey,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getJourneyWorkforce>>> = ({ signal }) => getJourneyWorkforce(journeyKey,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: journeyKey !== null && journeyKey !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getJourneyWorkforce>>, TError, TData> & { queryKey: QueryKey }
}

export type GetJourneyWorkforceQueryResult = NonNullable<Awaited<ReturnType<typeof getJourneyWorkforce>>>
export type GetJourneyWorkforceQueryError = unknown



export function useGetJourneyWorkforce<TData = Awaited<ReturnType<typeof getJourneyWorkforce>>, TError = unknown>(
 journeyKey: JourneyKey,
    params?: GetJourneyWorkforceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getJourneyWorkforce>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetJourneyWorkforceQueryOptions(journeyKey,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetJourneyFloorplanUrl = (journeyKey: JourneyKey,
    params?: GetJourneyFloorplanParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/journeys/${journeyKey}/floorplan?${stringifiedParams}` : `/api/journeys/${journeyKey}/floorplan`
}

/**
 * @summary Get named agent and human desks grouped by business function
 */
export const getJourneyFloorplan = async (journeyKey: JourneyKey,
    params?: GetJourneyFloorplanParams, options?: Parameters<typeof customFetch>[1]): Promise<JourneyFloorplan> => {

  return customFetch<JourneyFloorplan>(getGetJourneyFloorplanUrl(journeyKey,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetJourneyFloorplanQueryKey = (journeyKey: JourneyKey,
    params?: GetJourneyFloorplanParams,) => {
    return [
    `/api/journeys/${journeyKey}/floorplan`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetJourneyFloorplanQueryOptions = <TData = Awaited<ReturnType<typeof getJourneyFloorplan>>, TError = Error>(journeyKey: JourneyKey,
    params?: GetJourneyFloorplanParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getJourneyFloorplan>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetJourneyFloorplanQueryKey(journeyKey,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getJourneyFloorplan>>> = ({ signal }) => getJourneyFloorplan(journeyKey,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: journeyKey !== null && journeyKey !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getJourneyFloorplan>>, TError, TData> & { queryKey: QueryKey }
}

export type GetJourneyFloorplanQueryResult = NonNullable<Awaited<ReturnType<typeof getJourneyFloorplan>>>
export type GetJourneyFloorplanQueryError = Error


/**
 * @summary Get named agent and human desks grouped by business function
 */

export function useGetJourneyFloorplan<TData = Awaited<ReturnType<typeof getJourneyFloorplan>>, TError = Error>(
 journeyKey: JourneyKey,
    params?: GetJourneyFloorplanParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getJourneyFloorplan>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetJourneyFloorplanQueryOptions(journeyKey,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetJourneyBacklogUrl = (journeyKey: JourneyKey,
    params?: GetJourneyBacklogParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/journeys/${journeyKey}/backlog?${stringifiedParams}` : `/api/journeys/${journeyKey}/backlog`
}

export const getJourneyBacklog = async (journeyKey: JourneyKey,
    params?: GetJourneyBacklogParams, options?: Parameters<typeof customFetch>[1]): Promise<JourneyBacklog> => {

  return customFetch<JourneyBacklog>(getGetJourneyBacklogUrl(journeyKey,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetJourneyBacklogQueryKey = (journeyKey: JourneyKey,
    params?: GetJourneyBacklogParams,) => {
    return [
    `/api/journeys/${journeyKey}/backlog`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetJourneyBacklogQueryOptions = <TData = Awaited<ReturnType<typeof getJourneyBacklog>>, TError = Error>(journeyKey: JourneyKey,
    params?: GetJourneyBacklogParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getJourneyBacklog>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetJourneyBacklogQueryKey(journeyKey,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getJourneyBacklog>>> = ({ signal }) => getJourneyBacklog(journeyKey,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: journeyKey !== null && journeyKey !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getJourneyBacklog>>, TError, TData> & { queryKey: QueryKey }
}

export type GetJourneyBacklogQueryResult = NonNullable<Awaited<ReturnType<typeof getJourneyBacklog>>>
export type GetJourneyBacklogQueryError = Error



export function useGetJourneyBacklog<TData = Awaited<ReturnType<typeof getJourneyBacklog>>, TError = Error>(
 journeyKey: JourneyKey,
    params?: GetJourneyBacklogParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getJourneyBacklog>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetJourneyBacklogQueryOptions(journeyKey,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetStageAuthorityUrl = (journeyKey: JourneyKey,
    stageId: string,
    params?: GetStageAuthorityParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/ai-authority/journeys/${journeyKey}/stages/${stageId}?${stringifiedParams}` : `/api/admin/ai-authority/journeys/${journeyKey}/stages/${stageId}`
}

export const getStageAuthority = async (journeyKey: JourneyKey,
    stageId: string,
    params?: GetStageAuthorityParams, options?: Parameters<typeof customFetch>[1]): Promise<StageAuthorityOverride> => {

  return customFetch<StageAuthorityOverride>(getGetStageAuthorityUrl(journeyKey,stageId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetStageAuthorityQueryKey = (journeyKey: JourneyKey,
    stageId: string,
    params?: GetStageAuthorityParams,) => {
    return [
    `/api/admin/ai-authority/journeys/${journeyKey}/stages/${stageId}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetStageAuthorityQueryOptions = <TData = Awaited<ReturnType<typeof getStageAuthority>>, TError = unknown>(journeyKey: JourneyKey,
    stageId: string,
    params?: GetStageAuthorityParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getStageAuthority>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetStageAuthorityQueryKey(journeyKey,stageId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getStageAuthority>>> = ({ signal }) => getStageAuthority(journeyKey,stageId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: journeyKey !== null && journeyKey !== undefined && stageId !== null && stageId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getStageAuthority>>, TError, TData> & { queryKey: QueryKey }
}

export type GetStageAuthorityQueryResult = NonNullable<Awaited<ReturnType<typeof getStageAuthority>>>
export type GetStageAuthorityQueryError = unknown



export function useGetStageAuthority<TData = Awaited<ReturnType<typeof getStageAuthority>>, TError = unknown>(
 journeyKey: JourneyKey,
    stageId: string,
    params?: GetStageAuthorityParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getStageAuthority>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetStageAuthorityQueryOptions(journeyKey,stageId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateStageAuthorityUrl = (journeyKey: JourneyKey,
    stageId: string,) => {




  return `/api/admin/ai-authority/journeys/${journeyKey}/stages/${stageId}`
}

export const updateStageAuthority = async (journeyKey: JourneyKey,
    stageId: string,
    updateStageAuthorityBody: UpdateStageAuthorityBody, options?: Parameters<typeof customFetch>[1]): Promise<StageAuthorityOverride> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<StageAuthorityOverride>(getUpdateStageAuthorityUrl(journeyKey,stageId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateStageAuthorityBody)
  }
);}





export const getUpdateStageAuthorityMutationKey = () => ['updateStageAuthority'] as const;

export const getUpdateStageAuthorityMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateStageAuthority>>, TError,UpdateStageAuthorityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateStageAuthority>>, TError,UpdateStageAuthorityMutationVariables, TContext> => {

const mutationKey = getUpdateStageAuthorityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateStageAuthority>>, UpdateStageAuthorityMutationVariables> = (props) => {
          const {journeyKey,stageId,data} = props ?? {};

          return  updateStageAuthority(journeyKey,stageId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateStageAuthorityMutationResult = NonNullable<Awaited<ReturnType<typeof updateStageAuthority>>>
    export type UpdateStageAuthorityMutationBody = UpdateStageAuthorityBody
    export type UpdateStageAuthorityMutationError = unknown
    export type UpdateStageAuthorityMutationVariables = {journeyKey: JourneyKey;stageId: string;data: UpdateStageAuthorityBody}

    export const useUpdateStageAuthority = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateStageAuthority>>, TError,UpdateStageAuthorityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateStageAuthority>>,
        TError,
        UpdateStageAuthorityMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateStageAuthorityMutationOptions(options));
    }

export const getListAgentInstructionsUrl = () => {




  return `/api/admin/ai-instructions`
}

export const listAgentInstructions = async ( options?: Parameters<typeof customFetch>[1]): Promise<AgentInstructionVersion[]> => {

  return customFetch<AgentInstructionVersion[]>(getListAgentInstructionsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgentInstructionsQueryKey = () => {
    return [
    `/api/admin/ai-instructions`
    ] as const;
    }


export const getListAgentInstructionsQueryOptions = <TData = Awaited<ReturnType<typeof listAgentInstructions>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentInstructions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgentInstructionsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgentInstructions>>> = ({ signal }) => listAgentInstructions({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgentInstructions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAgentInstructionsQueryResult = NonNullable<Awaited<ReturnType<typeof listAgentInstructions>>>
export type ListAgentInstructionsQueryError = unknown



export function useListAgentInstructions<TData = Awaited<ReturnType<typeof listAgentInstructions>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentInstructions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAgentInstructionsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListAgentInstructionVersionsUrl = (actionType: string,
    params?: ListAgentInstructionVersionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/ai-instructions/${actionType}/versions?${stringifiedParams}` : `/api/admin/ai-instructions/${actionType}/versions`
}

export const listAgentInstructionVersions = async (actionType: string,
    params?: ListAgentInstructionVersionsParams, options?: Parameters<typeof customFetch>[1]): Promise<ListAgentInstructionVersions200> => {

  return customFetch<ListAgentInstructionVersions200>(getListAgentInstructionVersionsUrl(actionType,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgentInstructionVersionsQueryKey = (actionType: string,
    params?: ListAgentInstructionVersionsParams,) => {
    return [
    `/api/admin/ai-instructions/${actionType}/versions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAgentInstructionVersionsQueryOptions = <TData = Awaited<ReturnType<typeof listAgentInstructionVersions>>, TError = unknown>(actionType: string,
    params?: ListAgentInstructionVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentInstructionVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgentInstructionVersionsQueryKey(actionType,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgentInstructionVersions>>> = ({ signal }) => listAgentInstructionVersions(actionType,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: actionType !== null && actionType !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgentInstructionVersions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAgentInstructionVersionsQueryResult = NonNullable<Awaited<ReturnType<typeof listAgentInstructionVersions>>>
export type ListAgentInstructionVersionsQueryError = unknown



export function useListAgentInstructionVersions<TData = Awaited<ReturnType<typeof listAgentInstructionVersions>>, TError = unknown>(
 actionType: string,
    params?: ListAgentInstructionVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentInstructionVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAgentInstructionVersionsQueryOptions(actionType,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateAgentInstructionVersionUrl = (actionType: string,
    params?: CreateAgentInstructionVersionParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/ai-instructions/${actionType}/versions?${stringifiedParams}` : `/api/admin/ai-instructions/${actionType}/versions`
}

export const createAgentInstructionVersion = async (actionType: string,
    createAgentInstructionVersionBody: CreateAgentInstructionVersionBody,
    params?: CreateAgentInstructionVersionParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentInstructionVersion> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentInstructionVersion>(getCreateAgentInstructionVersionUrl(actionType,params),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createAgentInstructionVersionBody)
  }
);}





export const getCreateAgentInstructionVersionMutationKey = () => ['createAgentInstructionVersion'] as const;

export const getCreateAgentInstructionVersionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAgentInstructionVersion>>, TError,CreateAgentInstructionVersionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createAgentInstructionVersion>>, TError,CreateAgentInstructionVersionMutationVariables, TContext> => {

const mutationKey = getCreateAgentInstructionVersionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAgentInstructionVersion>>, CreateAgentInstructionVersionMutationVariables> = (props) => {
          const {actionType,data,params} = props ?? {};

          return  createAgentInstructionVersion(actionType,data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateAgentInstructionVersionMutationResult = NonNullable<Awaited<ReturnType<typeof createAgentInstructionVersion>>>
    export type CreateAgentInstructionVersionMutationBody = CreateAgentInstructionVersionBody
    export type CreateAgentInstructionVersionMutationError = unknown
    export type CreateAgentInstructionVersionMutationVariables = {actionType: string;data: CreateAgentInstructionVersionBody;params?: CreateAgentInstructionVersionParams}

    export const useCreateAgentInstructionVersion = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAgentInstructionVersion>>, TError,CreateAgentInstructionVersionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createAgentInstructionVersion>>,
        TError,
        CreateAgentInstructionVersionMutationVariables,
        TContext
      > => {
      return useMutation(getCreateAgentInstructionVersionMutationOptions(options));
    }

export const getActivateAgentInstructionVersionUrl = (actionType: string,
    version: number,
    params?: ActivateAgentInstructionVersionParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/ai-instructions/${actionType}/versions/${version}/activate?${stringifiedParams}` : `/api/admin/ai-instructions/${actionType}/versions/${version}/activate`
}

export const activateAgentInstructionVersion = async (actionType: string,
    version: number,
    params?: ActivateAgentInstructionVersionParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentInstructionVersion> => {

  return customFetch<AgentInstructionVersion>(getActivateAgentInstructionVersionUrl(actionType,version,params),
  {
    ...options,
    method: 'POST'


  }
);}





export const getActivateAgentInstructionVersionMutationKey = () => ['activateAgentInstructionVersion'] as const;

export const getActivateAgentInstructionVersionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof activateAgentInstructionVersion>>, TError,ActivateAgentInstructionVersionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof activateAgentInstructionVersion>>, TError,ActivateAgentInstructionVersionMutationVariables, TContext> => {

const mutationKey = getActivateAgentInstructionVersionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof activateAgentInstructionVersion>>, ActivateAgentInstructionVersionMutationVariables> = (props) => {
          const {actionType,version,params} = props ?? {};

          return  activateAgentInstructionVersion(actionType,version,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ActivateAgentInstructionVersionMutationResult = NonNullable<Awaited<ReturnType<typeof activateAgentInstructionVersion>>>

    export type ActivateAgentInstructionVersionMutationError = unknown
    export type ActivateAgentInstructionVersionMutationVariables = {actionType: string;version: number;params?: ActivateAgentInstructionVersionParams}

    export const useActivateAgentInstructionVersion = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof activateAgentInstructionVersion>>, TError,ActivateAgentInstructionVersionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof activateAgentInstructionVersion>>,
        TError,
        ActivateAgentInstructionVersionMutationVariables,
        TContext
      > => {
      return useMutation(getActivateAgentInstructionVersionMutationOptions(options));
    }

export const getDeclineAgentInstructionVersionUrl = (actionType: string,
    version: number,
    params: DeclineAgentInstructionVersionParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/ai-instructions/${actionType}/versions/${version}/decline?${stringifiedParams}` : `/api/admin/ai-instructions/${actionType}/versions/${version}/decline`
}

export const declineAgentInstructionVersion = async (actionType: string,
    version: number,
    declineAgentInstructionVersionBody: DeclineAgentInstructionVersionBody,
    params: DeclineAgentInstructionVersionParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentInstructionVersion> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentInstructionVersion>(getDeclineAgentInstructionVersionUrl(actionType,version,params),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(declineAgentInstructionVersionBody)
  }
);}





export const getDeclineAgentInstructionVersionMutationKey = () => ['declineAgentInstructionVersion'] as const;

export const getDeclineAgentInstructionVersionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof declineAgentInstructionVersion>>, TError,DeclineAgentInstructionVersionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof declineAgentInstructionVersion>>, TError,DeclineAgentInstructionVersionMutationVariables, TContext> => {

const mutationKey = getDeclineAgentInstructionVersionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof declineAgentInstructionVersion>>, DeclineAgentInstructionVersionMutationVariables> = (props) => {
          const {actionType,version,data,params} = props ?? {};

          return  declineAgentInstructionVersion(actionType,version,data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeclineAgentInstructionVersionMutationResult = NonNullable<Awaited<ReturnType<typeof declineAgentInstructionVersion>>>
    export type DeclineAgentInstructionVersionMutationBody = DeclineAgentInstructionVersionBody
    export type DeclineAgentInstructionVersionMutationError = Error
    export type DeclineAgentInstructionVersionMutationVariables = {actionType: string;version: number;data: DeclineAgentInstructionVersionBody;params: DeclineAgentInstructionVersionParams}

    export const useDeclineAgentInstructionVersion = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof declineAgentInstructionVersion>>, TError,DeclineAgentInstructionVersionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof declineAgentInstructionVersion>>,
        TError,
        DeclineAgentInstructionVersionMutationVariables,
        TContext
      > => {
      return useMutation(getDeclineAgentInstructionVersionMutationOptions(options));
    }

export const getGetAiAuthorityPolicyUrl = (params?: GetAiAuthorityPolicyParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/ai-authority?${stringifiedParams}` : `/api/admin/ai-authority`
}

/**
 * @summary Read the validated AI action catalogue, effective authority levels, and versioned change history
 */
export const getAiAuthorityPolicy = async (params?: GetAiAuthorityPolicyParams, options?: Parameters<typeof customFetch>[1]): Promise<AiAuthorityPolicyWithHistory> => {

  return customFetch<AiAuthorityPolicyWithHistory>(getGetAiAuthorityPolicyUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAiAuthorityPolicyQueryKey = (params?: GetAiAuthorityPolicyParams,) => {
    return [
    `/api/admin/ai-authority`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetAiAuthorityPolicyQueryOptions = <TData = Awaited<ReturnType<typeof getAiAuthorityPolicy>>, TError = unknown>(params?: GetAiAuthorityPolicyParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAiAuthorityPolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAiAuthorityPolicyQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAiAuthorityPolicy>>> = ({ signal }) => getAiAuthorityPolicy(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAiAuthorityPolicy>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAiAuthorityPolicyQueryResult = NonNullable<Awaited<ReturnType<typeof getAiAuthorityPolicy>>>
export type GetAiAuthorityPolicyQueryError = unknown


/**
 * @summary Read the validated AI action catalogue, effective authority levels, and versioned change history
 */

export function useGetAiAuthorityPolicy<TData = Awaited<ReturnType<typeof getAiAuthorityPolicy>>, TError = unknown>(
 params?: GetAiAuthorityPolicyParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAiAuthorityPolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAiAuthorityPolicyQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateAiAuthorityPolicyUrl = () => {




  return `/api/admin/ai-authority`
}

/**
 * @summary Replace every supported AI action level using optimistic version validation
 */
export const updateAiAuthorityPolicy = async (aiAuthorityPolicyUpdate: AiAuthorityPolicyUpdate, options?: Parameters<typeof customFetch>[1]): Promise<AiAuthorityPolicy> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AiAuthorityPolicy>(getUpdateAiAuthorityPolicyUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(aiAuthorityPolicyUpdate)
  }
);}





export const getUpdateAiAuthorityPolicyMutationKey = () => ['updateAiAuthorityPolicy'] as const;

export const getUpdateAiAuthorityPolicyMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAiAuthorityPolicy>>, TError,UpdateAiAuthorityPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateAiAuthorityPolicy>>, TError,UpdateAiAuthorityPolicyMutationVariables, TContext> => {

const mutationKey = getUpdateAiAuthorityPolicyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAiAuthorityPolicy>>, UpdateAiAuthorityPolicyMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateAiAuthorityPolicy(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateAiAuthorityPolicyMutationResult = NonNullable<Awaited<ReturnType<typeof updateAiAuthorityPolicy>>>
    export type UpdateAiAuthorityPolicyMutationBody = AiAuthorityPolicyUpdate
    export type UpdateAiAuthorityPolicyMutationError = Error
    export type UpdateAiAuthorityPolicyMutationVariables = {data: AiAuthorityPolicyUpdate}

    /**
 * @summary Replace every supported AI action level using optimistic version validation
 */
export const useUpdateAiAuthorityPolicy = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAiAuthorityPolicy>>, TError,UpdateAiAuthorityPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateAiAuthorityPolicy>>,
        TError,
        UpdateAiAuthorityPolicyMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateAiAuthorityPolicyMutationOptions(options));
    }

export const getUpdatePlatformConfigUrl = (namespace: string,
    key: string,) => {




  return `/api/foundation/config/${namespace}/${key}`
}

/**
 * @summary Create or replace one org-scoped configuration value
 */
export const updatePlatformConfig = async (namespace: string,
    key: string,
    platformConfigUpdate: PlatformConfigUpdate, options?: Parameters<typeof customFetch>[1]): Promise<PlatformConfigEntry> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PlatformConfigEntry>(getUpdatePlatformConfigUrl(namespace,key),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(platformConfigUpdate)
  }
);}





export const getUpdatePlatformConfigMutationKey = () => ['updatePlatformConfig'] as const;

export const getUpdatePlatformConfigMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePlatformConfig>>, TError,UpdatePlatformConfigMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updatePlatformConfig>>, TError,UpdatePlatformConfigMutationVariables, TContext> => {

const mutationKey = getUpdatePlatformConfigMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updatePlatformConfig>>, UpdatePlatformConfigMutationVariables> = (props) => {
          const {namespace,key,data} = props ?? {};

          return  updatePlatformConfig(namespace,key,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdatePlatformConfigMutationResult = NonNullable<Awaited<ReturnType<typeof updatePlatformConfig>>>
    export type UpdatePlatformConfigMutationBody = PlatformConfigUpdate
    export type UpdatePlatformConfigMutationError = unknown
    export type UpdatePlatformConfigMutationVariables = {namespace: string;key: string;data: PlatformConfigUpdate}

    /**
 * @summary Create or replace one org-scoped configuration value
 */
export const useUpdatePlatformConfig = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePlatformConfig>>, TError,UpdatePlatformConfigMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updatePlatformConfig>>,
        TError,
        UpdatePlatformConfigMutationVariables,
        TContext
      > => {
      return useMutation(getUpdatePlatformConfigMutationOptions(options));
    }

export const getListChannelConnectionsUrl = () => {




  return `/api/foundation/channels`
}

/**
 * @summary List every planned channel and its connection status
 */
export const listChannelConnections = async ( options?: Parameters<typeof customFetch>[1]): Promise<ChannelConnectionList> => {

  return customFetch<ChannelConnectionList>(getListChannelConnectionsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListChannelConnectionsQueryKey = () => {
    return [
    `/api/foundation/channels`
    ] as const;
    }


export const getListChannelConnectionsQueryOptions = <TData = Awaited<ReturnType<typeof listChannelConnections>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listChannelConnections>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListChannelConnectionsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listChannelConnections>>> = ({ signal }) => listChannelConnections({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listChannelConnections>>, TError, TData> & { queryKey: QueryKey }
}

export type ListChannelConnectionsQueryResult = NonNullable<Awaited<ReturnType<typeof listChannelConnections>>>
export type ListChannelConnectionsQueryError = unknown


/**
 * @summary List every planned channel and its connection status
 */

export function useListChannelConnections<TData = Awaited<ReturnType<typeof listChannelConnections>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listChannelConnections>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListChannelConnectionsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConnectChannelUrl = (provider: ChannelProvider,) => {




  return `/api/foundation/channels/${provider}/connect`
}

/**
 * @summary Connect or stage a provider account
 */
export const connectChannel = async (provider: ChannelProvider,
    channelConnectRequest?: ChannelConnectRequest, options?: Parameters<typeof customFetch>[1]): Promise<ChannelConnection> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ChannelConnection>(getConnectChannelUrl(provider),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(channelConnectRequest)
  }
);}





export const getConnectChannelMutationKey = () => ['connectChannel'] as const;

export const getConnectChannelMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof connectChannel>>, TError,ConnectChannelMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof connectChannel>>, TError,ConnectChannelMutationVariables, TContext> => {

const mutationKey = getConnectChannelMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof connectChannel>>, ConnectChannelMutationVariables> = (props) => {
          const {provider,data} = props ?? {};

          return  connectChannel(provider,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConnectChannelMutationResult = NonNullable<Awaited<ReturnType<typeof connectChannel>>>
    export type ConnectChannelMutationBody = ChannelConnectRequest | undefined
    export type ConnectChannelMutationError = unknown
    export type ConnectChannelMutationVariables = {provider: ChannelProvider;data?: ChannelConnectRequest}

    /**
 * @summary Connect or stage a provider account
 */
export const useConnectChannel = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof connectChannel>>, TError,ConnectChannelMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof connectChannel>>,
        TError,
        ConnectChannelMutationVariables,
        TContext
      > => {
      return useMutation(getConnectChannelMutationOptions(options));
    }

export const getDisconnectChannelUrl = (provider: ChannelProvider,) => {




  return `/api/foundation/channels/${provider}/disconnect`
}

/**
 * @summary Disconnect a provider and erase its credentials
 */
export const disconnectChannel = async (provider: ChannelProvider, options?: Parameters<typeof customFetch>[1]): Promise<ChannelConnection> => {

  return customFetch<ChannelConnection>(getDisconnectChannelUrl(provider),
  {
    ...options,
    method: 'POST'


  }
);}





export const getDisconnectChannelMutationKey = () => ['disconnectChannel'] as const;

export const getDisconnectChannelMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectChannel>>, TError,DisconnectChannelMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof disconnectChannel>>, TError,DisconnectChannelMutationVariables, TContext> => {

const mutationKey = getDisconnectChannelMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof disconnectChannel>>, DisconnectChannelMutationVariables> = (props) => {
          const {provider} = props ?? {};

          return  disconnectChannel(provider,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DisconnectChannelMutationResult = NonNullable<Awaited<ReturnType<typeof disconnectChannel>>>

    export type DisconnectChannelMutationError = unknown
    export type DisconnectChannelMutationVariables = {provider: ChannelProvider}

    /**
 * @summary Disconnect a provider and erase its credentials
 */
export const useDisconnectChannel = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectChannel>>, TError,DisconnectChannelMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof disconnectChannel>>,
        TError,
        DisconnectChannelMutationVariables,
        TContext
      > => {
      return useMutation(getDisconnectChannelMutationOptions(options));
    }

export const getListUnifiedTimelineUrl = (params?: ListUnifiedTimelineParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/foundation/timeline?${stringifiedParams}` : `/api/foundation/timeline`
}

/**
 * @summary Unified timeline merging normalized interactions, activities and enquiries
 */
export const listUnifiedTimeline = async (params?: ListUnifiedTimelineParams, options?: Parameters<typeof customFetch>[1]): Promise<TimelinePage> => {

  return customFetch<TimelinePage>(getListUnifiedTimelineUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListUnifiedTimelineQueryKey = (params?: ListUnifiedTimelineParams,) => {
    return [
    `/api/foundation/timeline`, ...(params ? [params] : [])
    ] as const;
    }


export const getListUnifiedTimelineQueryOptions = <TData = Awaited<ReturnType<typeof listUnifiedTimeline>>, TError = unknown>(params?: ListUnifiedTimelineParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listUnifiedTimeline>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListUnifiedTimelineQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listUnifiedTimeline>>> = ({ signal }) => listUnifiedTimeline(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listUnifiedTimeline>>, TError, TData> & { queryKey: QueryKey }
}

export type ListUnifiedTimelineQueryResult = NonNullable<Awaited<ReturnType<typeof listUnifiedTimeline>>>
export type ListUnifiedTimelineQueryError = unknown


/**
 * @summary Unified timeline merging normalized interactions, activities and enquiries
 */

export function useListUnifiedTimeline<TData = Awaited<ReturnType<typeof listUnifiedTimeline>>, TError = unknown>(
 params?: ListUnifiedTimelineParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listUnifiedTimeline>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListUnifiedTimelineQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateInteractionUrl = () => {




  return `/api/foundation/interactions`
}

/**
 * @summary Record a normalized cross-channel interaction and recompute lead scores
 */
export const createInteraction = async (interactionCreate: InteractionCreate, options?: Parameters<typeof customFetch>[1]): Promise<Interaction> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Interaction>(getCreateInteractionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(interactionCreate)
  }
);}





export const getCreateInteractionMutationKey = () => ['createInteraction'] as const;

export const getCreateInteractionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createInteraction>>, TError,CreateInteractionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createInteraction>>, TError,CreateInteractionMutationVariables, TContext> => {

const mutationKey = getCreateInteractionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createInteraction>>, CreateInteractionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createInteraction(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateInteractionMutationResult = NonNullable<Awaited<ReturnType<typeof createInteraction>>>
    export type CreateInteractionMutationBody = InteractionCreate
    export type CreateInteractionMutationError = unknown
    export type CreateInteractionMutationVariables = {data: InteractionCreate}

    /**
 * @summary Record a normalized cross-channel interaction and recompute lead scores
 */
export const useCreateInteraction = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createInteraction>>, TError,CreateInteractionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createInteraction>>,
        TError,
        CreateInteractionMutationVariables,
        TContext
      > => {
      return useMutation(getCreateInteractionMutationOptions(options));
    }

export const getPublishSignalUrl = () => {




  return `/api/foundation/signals`
}

/**
 * @summary Publish a signal and recompute linked lead scores
 */
export const publishSignal = async (signalCreate: SignalCreate, options?: Parameters<typeof customFetch>[1]): Promise<Signal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Signal>(getPublishSignalUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(signalCreate)
  }
);}





export const getPublishSignalMutationKey = () => ['publishSignal'] as const;

export const getPublishSignalMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof publishSignal>>, TError,PublishSignalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof publishSignal>>, TError,PublishSignalMutationVariables, TContext> => {

const mutationKey = getPublishSignalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof publishSignal>>, PublishSignalMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  publishSignal(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PublishSignalMutationResult = NonNullable<Awaited<ReturnType<typeof publishSignal>>>
    export type PublishSignalMutationBody = SignalCreate
    export type PublishSignalMutationError = unknown
    export type PublishSignalMutationVariables = {data: SignalCreate}

    /**
 * @summary Publish a signal and recompute linked lead scores
 */
export const usePublishSignal = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof publishSignal>>, TError,PublishSignalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof publishSignal>>,
        TError,
        PublishSignalMutationVariables,
        TContext
      > => {
      return useMutation(getPublishSignalMutationOptions(options));
    }

export const getGetLeadScoreUrl = (params: GetLeadScoreParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/foundation/lead-scores?${stringifiedParams}` : `/api/foundation/lead-scores`
}

/**
 * @summary Get a account or contact lead score
 */
export const getLeadScore = async (params: GetLeadScoreParams, options?: Parameters<typeof customFetch>[1]): Promise<LeadScore> => {

  return customFetch<LeadScore>(getGetLeadScoreUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetLeadScoreQueryKey = (params?: GetLeadScoreParams,) => {
    return [
    `/api/foundation/lead-scores`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetLeadScoreQueryOptions = <TData = Awaited<ReturnType<typeof getLeadScore>>, TError = unknown>(params: GetLeadScoreParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLeadScore>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetLeadScoreQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getLeadScore>>> = ({ signal }) => getLeadScore(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getLeadScore>>, TError, TData> & { queryKey: QueryKey }
}

export type GetLeadScoreQueryResult = NonNullable<Awaited<ReturnType<typeof getLeadScore>>>
export type GetLeadScoreQueryError = unknown


/**
 * @summary Get a account or contact lead score
 */

export function useGetLeadScore<TData = Awaited<ReturnType<typeof getLeadScore>>, TError = unknown>(
 params: GetLeadScoreParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getLeadScore>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetLeadScoreQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRequestAiActionUrl = () => {




  return `/api/foundation/ai-actions`
}

/**
 * @summary Enforce configured authority and create an audited AI action
 */
export const requestAiAction = async (aiActionRequest: AiActionRequest, options?: Parameters<typeof customFetch>[1]): Promise<AiActionAudit> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AiActionAudit>(getRequestAiActionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(aiActionRequest)
  }
);}





export const getRequestAiActionMutationKey = () => ['requestAiAction'] as const;

export const getRequestAiActionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestAiAction>>, TError,RequestAiActionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof requestAiAction>>, TError,RequestAiActionMutationVariables, TContext> => {

const mutationKey = getRequestAiActionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof requestAiAction>>, RequestAiActionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  requestAiAction(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RequestAiActionMutationResult = NonNullable<Awaited<ReturnType<typeof requestAiAction>>>
    export type RequestAiActionMutationBody = AiActionRequest
    export type RequestAiActionMutationError = unknown
    export type RequestAiActionMutationVariables = {data: AiActionRequest}

    /**
 * @summary Enforce configured authority and create an audited AI action
 */
export const useRequestAiAction = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestAiAction>>, TError,RequestAiActionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof requestAiAction>>,
        TError,
        RequestAiActionMutationVariables,
        TContext
      > => {
      return useMutation(getRequestAiActionMutationOptions(options));
    }

export const getListAiActionAuditUrl = () => {




  return `/api/foundation/ai-actions`
}

/**
 * @summary List AI action audit records
 */
export const listAiActionAudit = async ( options?: Parameters<typeof customFetch>[1]): Promise<ListAiActionAudit200> => {

  return customFetch<ListAiActionAudit200>(getListAiActionAuditUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAiActionAuditQueryKey = () => {
    return [
    `/api/foundation/ai-actions`
    ] as const;
    }


export const getListAiActionAuditQueryOptions = <TData = Awaited<ReturnType<typeof listAiActionAudit>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAiActionAudit>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAiActionAuditQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAiActionAudit>>> = ({ signal }) => listAiActionAudit({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAiActionAudit>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAiActionAuditQueryResult = NonNullable<Awaited<ReturnType<typeof listAiActionAudit>>>
export type ListAiActionAuditQueryError = unknown


/**
 * @summary List AI action audit records
 */

export function useListAiActionAudit<TData = Awaited<ReturnType<typeof listAiActionAudit>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAiActionAudit>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAiActionAuditQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getApproveAiActionUrl = (id: string,) => {




  return `/api/foundation/ai-actions/${id}/approve`
}

/**
 * @summary Approve or reject a prepared AI action
 */
export const approveAiAction = async (id: string,
    approveAiActionBody: ApproveAiActionBody, options?: Parameters<typeof customFetch>[1]): Promise<AiActionAudit> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AiActionAudit>(getApproveAiActionUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(approveAiActionBody)
  }
);}





export const getApproveAiActionMutationKey = () => ['approveAiAction'] as const;

export const getApproveAiActionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveAiAction>>, TError,ApproveAiActionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof approveAiAction>>, TError,ApproveAiActionMutationVariables, TContext> => {

const mutationKey = getApproveAiActionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof approveAiAction>>, ApproveAiActionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  approveAiAction(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApproveAiActionMutationResult = NonNullable<Awaited<ReturnType<typeof approveAiAction>>>
    export type ApproveAiActionMutationBody = ApproveAiActionBody
    export type ApproveAiActionMutationError = unknown
    export type ApproveAiActionMutationVariables = {id: string;data: ApproveAiActionBody}

    /**
 * @summary Approve or reject a prepared AI action
 */
export const useApproveAiAction = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof approveAiAction>>, TError,ApproveAiActionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof approveAiAction>>,
        TError,
        ApproveAiActionMutationVariables,
        TContext
      > => {
      return useMutation(getApproveAiActionMutationOptions(options));
    }

export const getDecideAiActionUrl = (id: string,) => {




  return `/api/foundation/ai-actions/${id}/decision`
}

/**
 * @summary Approve, reject, defer, dismiss, edit, or provide feedback on governed AI work
 */
export const decideAiAction = async (id: string,
    aiActionDecisionRequest: AiActionDecisionRequest, options?: Parameters<typeof customFetch>[1]): Promise<AiActionAudit> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AiActionAudit>(getDecideAiActionUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(aiActionDecisionRequest)
  }
);}





export const getDecideAiActionMutationKey = () => ['decideAiAction'] as const;

export const getDecideAiActionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideAiAction>>, TError,DecideAiActionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof decideAiAction>>, TError,DecideAiActionMutationVariables, TContext> => {

const mutationKey = getDecideAiActionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof decideAiAction>>, DecideAiActionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  decideAiAction(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DecideAiActionMutationResult = NonNullable<Awaited<ReturnType<typeof decideAiAction>>>
    export type DecideAiActionMutationBody = AiActionDecisionRequest
    export type DecideAiActionMutationError = Error
    export type DecideAiActionMutationVariables = {id: string;data: AiActionDecisionRequest}

    /**
 * @summary Approve, reject, defer, dismiss, edit, or provide feedback on governed AI work
 */
export const useDecideAiAction = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideAiAction>>, TError,DecideAiActionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof decideAiAction>>,
        TError,
        DecideAiActionMutationVariables,
        TContext
      > => {
      return useMutation(getDecideAiActionMutationOptions(options));
    }

export const getListQueueItemStatesUrl = (params?: ListQueueItemStatesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/queue-states?${stringifiedParams}` : `/api/queue-states`
}

/**
 * Expired snoozes are omitted so their items return to the queue at the due time.
 * @summary List the signed-in user's active Focus-queue snoozes, dismissals and done marks
 */
export const listQueueItemStates = async (params?: ListQueueItemStatesParams, options?: Parameters<typeof customFetch>[1]): Promise<QueueItemStateList> => {

  return customFetch<QueueItemStateList>(getListQueueItemStatesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListQueueItemStatesQueryKey = (params?: ListQueueItemStatesParams,) => {
    return [
    `/api/queue-states`, ...(params ? [params] : [])
    ] as const;
    }


export const getListQueueItemStatesQueryOptions = <TData = Awaited<ReturnType<typeof listQueueItemStates>>, TError = Error>(params?: ListQueueItemStatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listQueueItemStates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListQueueItemStatesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listQueueItemStates>>> = ({ signal }) => listQueueItemStates(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listQueueItemStates>>, TError, TData> & { queryKey: QueryKey }
}

export type ListQueueItemStatesQueryResult = NonNullable<Awaited<ReturnType<typeof listQueueItemStates>>>
export type ListQueueItemStatesQueryError = Error


/**
 * @summary List the signed-in user's active Focus-queue snoozes, dismissals and done marks
 */

export function useListQueueItemStates<TData = Awaited<ReturnType<typeof listQueueItemStates>>, TError = Error>(
 params?: ListQueueItemStatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listQueueItemStates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListQueueItemStatesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSetQueueItemStateUrl = (itemType: string,
    itemId: string,) => {




  return `/api/queue-states/${itemType}/${itemId}`
}

/**
 * @summary Snooze, dismiss or mark one queue item done for the signed-in user
 */
export const setQueueItemState = async (itemType: string,
    itemId: string,
    queueItemStateWrite: QueueItemStateWrite, options?: Parameters<typeof customFetch>[1]): Promise<QueueItemState> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<QueueItemState>(getSetQueueItemStateUrl(itemType,itemId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(queueItemStateWrite)
  }
);}





export const getSetQueueItemStateMutationKey = () => ['setQueueItemState'] as const;

export const getSetQueueItemStateMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setQueueItemState>>, TError,SetQueueItemStateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setQueueItemState>>, TError,SetQueueItemStateMutationVariables, TContext> => {

const mutationKey = getSetQueueItemStateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setQueueItemState>>, SetQueueItemStateMutationVariables> = (props) => {
          const {itemType,itemId,data} = props ?? {};

          return  setQueueItemState(itemType,itemId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetQueueItemStateMutationResult = NonNullable<Awaited<ReturnType<typeof setQueueItemState>>>
    export type SetQueueItemStateMutationBody = QueueItemStateWrite
    export type SetQueueItemStateMutationError = Error
    export type SetQueueItemStateMutationVariables = {itemType: string;itemId: string;data: QueueItemStateWrite}

    /**
 * @summary Snooze, dismiss or mark one queue item done for the signed-in user
 */
export const useSetQueueItemState = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setQueueItemState>>, TError,SetQueueItemStateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof setQueueItemState>>,
        TError,
        SetQueueItemStateMutationVariables,
        TContext
      > => {
      return useMutation(getSetQueueItemStateMutationOptions(options));
    }

export const getClearQueueItemStateUrl = (itemType: string,
    itemId: string,) => {




  return `/api/queue-states/${itemType}/${itemId}`
}

/**
 * @summary Return one item to the signed-in user's queue
 */
export const clearQueueItemState = async (itemType: string,
    itemId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getClearQueueItemStateUrl(itemType,itemId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getClearQueueItemStateMutationKey = () => ['clearQueueItemState'] as const;

export const getClearQueueItemStateMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearQueueItemState>>, TError,ClearQueueItemStateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof clearQueueItemState>>, TError,ClearQueueItemStateMutationVariables, TContext> => {

const mutationKey = getClearQueueItemStateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof clearQueueItemState>>, ClearQueueItemStateMutationVariables> = (props) => {
          const {itemType,itemId} = props ?? {};

          return  clearQueueItemState(itemType,itemId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ClearQueueItemStateMutationResult = NonNullable<Awaited<ReturnType<typeof clearQueueItemState>>>

    export type ClearQueueItemStateMutationError = Error
    export type ClearQueueItemStateMutationVariables = {itemType: string;itemId: string}

    /**
 * @summary Return one item to the signed-in user's queue
 */
export const useClearQueueItemState = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof clearQueueItemState>>, TError,ClearQueueItemStateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof clearQueueItemState>>,
        TError,
        ClearQueueItemStateMutationVariables,
        TContext
      > => {
      return useMutation(getClearQueueItemStateMutationOptions(options));
    }

export const getListSavedSharedMailboxesUrl = (params?: ListSavedSharedMailboxesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/shared-mailboxes?${stringifiedParams}` : `/api/shared-mailboxes`
}

/**
 * Each address appears once. Your own saved entry wins over a colleague's shared entry. Saving an address never grants mailbox access; Microsoft 365 decides who can open it.
 * @summary List the shared mailboxes the signed-in user saved plus those colleagues made available to the entity
 */
export const listSavedSharedMailboxes = async (params?: ListSavedSharedMailboxesParams, options?: Parameters<typeof customFetch>[1]): Promise<SavedSharedMailboxList> => {

  return customFetch<SavedSharedMailboxList>(getListSavedSharedMailboxesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSavedSharedMailboxesQueryKey = (params?: ListSavedSharedMailboxesParams,) => {
    return [
    `/api/shared-mailboxes`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSavedSharedMailboxesQueryOptions = <TData = Awaited<ReturnType<typeof listSavedSharedMailboxes>>, TError = Error>(params?: ListSavedSharedMailboxesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSavedSharedMailboxes>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSavedSharedMailboxesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSavedSharedMailboxes>>> = ({ signal }) => listSavedSharedMailboxes(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSavedSharedMailboxes>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSavedSharedMailboxesQueryResult = NonNullable<Awaited<ReturnType<typeof listSavedSharedMailboxes>>>
export type ListSavedSharedMailboxesQueryError = Error


/**
 * @summary List the shared mailboxes the signed-in user saved plus those colleagues made available to the entity
 */

export function useListSavedSharedMailboxes<TData = Awaited<ReturnType<typeof listSavedSharedMailboxes>>, TError = Error>(
 params?: ListSavedSharedMailboxesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSavedSharedMailboxes>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSavedSharedMailboxesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSaveSharedMailboxUrl = () => {




  return `/api/shared-mailboxes`
}

/**
 * Saving the same address again returns the existing entry. Each person can save up to 20 addresses.
 * @summary Save a shared mailbox address to the signed-in user's list
 */
export const saveSharedMailbox = async (savedSharedMailboxCreate: SavedSharedMailboxCreate, options?: Parameters<typeof customFetch>[1]): Promise<SavedSharedMailbox> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SavedSharedMailbox>(getSaveSharedMailboxUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(savedSharedMailboxCreate)
  }
);}





export const getSaveSharedMailboxMutationKey = () => ['saveSharedMailbox'] as const;

export const getSaveSharedMailboxMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveSharedMailbox>>, TError,SaveSharedMailboxMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof saveSharedMailbox>>, TError,SaveSharedMailboxMutationVariables, TContext> => {

const mutationKey = getSaveSharedMailboxMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof saveSharedMailbox>>, SaveSharedMailboxMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  saveSharedMailbox(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveSharedMailboxMutationResult = NonNullable<Awaited<ReturnType<typeof saveSharedMailbox>>>
    export type SaveSharedMailboxMutationBody = SavedSharedMailboxCreate
    export type SaveSharedMailboxMutationError = Error
    export type SaveSharedMailboxMutationVariables = {data: SavedSharedMailboxCreate}

    /**
 * @summary Save a shared mailbox address to the signed-in user's list
 */
export const useSaveSharedMailbox = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveSharedMailbox>>, TError,SaveSharedMailboxMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof saveSharedMailbox>>,
        TError,
        SaveSharedMailboxMutationVariables,
        TContext
      > => {
      return useMutation(getSaveSharedMailboxMutationOptions(options));
    }

export const getImportSavedSharedMailboxesUrl = () => {




  return `/api/shared-mailboxes/import`
}

/**
 * Addresses already saved are skipped, as are addresses beyond the 20-address limit. Imported entries are private.
 * @summary Move shared mailbox addresses previously remembered in a browser into the signed-in user's private list
 */
export const importSavedSharedMailboxes = async (savedSharedMailboxImport: SavedSharedMailboxImport, options?: Parameters<typeof customFetch>[1]): Promise<SavedSharedMailboxImportResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SavedSharedMailboxImportResult>(getImportSavedSharedMailboxesUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(savedSharedMailboxImport)
  }
);}





export const getImportSavedSharedMailboxesMutationKey = () => ['importSavedSharedMailboxes'] as const;

export const getImportSavedSharedMailboxesMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importSavedSharedMailboxes>>, TError,ImportSavedSharedMailboxesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof importSavedSharedMailboxes>>, TError,ImportSavedSharedMailboxesMutationVariables, TContext> => {

const mutationKey = getImportSavedSharedMailboxesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof importSavedSharedMailboxes>>, ImportSavedSharedMailboxesMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  importSavedSharedMailboxes(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ImportSavedSharedMailboxesMutationResult = NonNullable<Awaited<ReturnType<typeof importSavedSharedMailboxes>>>
    export type ImportSavedSharedMailboxesMutationBody = SavedSharedMailboxImport
    export type ImportSavedSharedMailboxesMutationError = Error
    export type ImportSavedSharedMailboxesMutationVariables = {data: SavedSharedMailboxImport}

    /**
 * @summary Move shared mailbox addresses previously remembered in a browser into the signed-in user's private list
 */
export const useImportSavedSharedMailboxes = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importSavedSharedMailboxes>>, TError,ImportSavedSharedMailboxesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof importSavedSharedMailboxes>>,
        TError,
        ImportSavedSharedMailboxesMutationVariables,
        TContext
      > => {
      return useMutation(getImportSavedSharedMailboxesMutationOptions(options));
    }

export const getUpdateSavedSharedMailboxUrl = (id: string,) => {




  return `/api/shared-mailboxes/${id}`
}

/**
 * @summary Make a mailbox you saved available to everyone in the entity, or private again
 */
export const updateSavedSharedMailbox = async (id: string,
    savedSharedMailboxUpdate: SavedSharedMailboxUpdate, options?: Parameters<typeof customFetch>[1]): Promise<SavedSharedMailbox> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SavedSharedMailbox>(getUpdateSavedSharedMailboxUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(savedSharedMailboxUpdate)
  }
);}





export const getUpdateSavedSharedMailboxMutationKey = () => ['updateSavedSharedMailbox'] as const;

export const getUpdateSavedSharedMailboxMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSavedSharedMailbox>>, TError,UpdateSavedSharedMailboxMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateSavedSharedMailbox>>, TError,UpdateSavedSharedMailboxMutationVariables, TContext> => {

const mutationKey = getUpdateSavedSharedMailboxMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateSavedSharedMailbox>>, UpdateSavedSharedMailboxMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateSavedSharedMailbox(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateSavedSharedMailboxMutationResult = NonNullable<Awaited<ReturnType<typeof updateSavedSharedMailbox>>>
    export type UpdateSavedSharedMailboxMutationBody = SavedSharedMailboxUpdate
    export type UpdateSavedSharedMailboxMutationError = Error
    export type UpdateSavedSharedMailboxMutationVariables = {id: string;data: SavedSharedMailboxUpdate}

    /**
 * @summary Make a mailbox you saved available to everyone in the entity, or private again
 */
export const useUpdateSavedSharedMailbox = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSavedSharedMailbox>>, TError,UpdateSavedSharedMailboxMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateSavedSharedMailbox>>,
        TError,
        UpdateSavedSharedMailboxMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateSavedSharedMailboxMutationOptions(options));
    }

export const getDeleteSavedSharedMailboxUrl = (id: string,) => {




  return `/api/shared-mailboxes/${id}`
}

/**
 * @summary Remove a mailbox you saved; if it was shared, colleagues no longer see your entry
 */
export const deleteSavedSharedMailbox = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteSavedSharedMailboxUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteSavedSharedMailboxMutationKey = () => ['deleteSavedSharedMailbox'] as const;

export const getDeleteSavedSharedMailboxMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteSavedSharedMailbox>>, TError,DeleteSavedSharedMailboxMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteSavedSharedMailbox>>, TError,DeleteSavedSharedMailboxMutationVariables, TContext> => {

const mutationKey = getDeleteSavedSharedMailboxMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteSavedSharedMailbox>>, DeleteSavedSharedMailboxMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteSavedSharedMailbox(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteSavedSharedMailboxMutationResult = NonNullable<Awaited<ReturnType<typeof deleteSavedSharedMailbox>>>

    export type DeleteSavedSharedMailboxMutationError = Error
    export type DeleteSavedSharedMailboxMutationVariables = {id: string}

    /**
 * @summary Remove a mailbox you saved; if it was shared, colleagues no longer see your entry
 */
export const useDeleteSavedSharedMailbox = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteSavedSharedMailbox>>, TError,DeleteSavedSharedMailboxMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteSavedSharedMailbox>>,
        TError,
        DeleteSavedSharedMailboxMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteSavedSharedMailboxMutationOptions(options));
    }

export const getHideSavedSharedMailboxUrl = (id: string,) => {




  return `/api/shared-mailboxes/${id}/hidden`
}

/**
 * @summary Hide a colleague's shared mailbox from your own list only
 */
export const hideSavedSharedMailbox = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getHideSavedSharedMailboxUrl(id),
  {
    ...options,
    method: 'PUT'


  }
);}





export const getHideSavedSharedMailboxMutationKey = () => ['hideSavedSharedMailbox'] as const;

export const getHideSavedSharedMailboxMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof hideSavedSharedMailbox>>, TError,HideSavedSharedMailboxMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof hideSavedSharedMailbox>>, TError,HideSavedSharedMailboxMutationVariables, TContext> => {

const mutationKey = getHideSavedSharedMailboxMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof hideSavedSharedMailbox>>, HideSavedSharedMailboxMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  hideSavedSharedMailbox(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type HideSavedSharedMailboxMutationResult = NonNullable<Awaited<ReturnType<typeof hideSavedSharedMailbox>>>

    export type HideSavedSharedMailboxMutationError = Error
    export type HideSavedSharedMailboxMutationVariables = {id: string}

    /**
 * @summary Hide a colleague's shared mailbox from your own list only
 */
export const useHideSavedSharedMailbox = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof hideSavedSharedMailbox>>, TError,HideSavedSharedMailboxMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof hideSavedSharedMailbox>>,
        TError,
        HideSavedSharedMailboxMutationVariables,
        TContext
      > => {
      return useMutation(getHideSavedSharedMailboxMutationOptions(options));
    }

export const getUnhideSavedSharedMailboxUrl = (id: string,) => {




  return `/api/shared-mailboxes/${id}/hidden`
}

/**
 * @summary Show a hidden shared mailbox in your list again
 */
export const unhideSavedSharedMailbox = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getUnhideSavedSharedMailboxUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getUnhideSavedSharedMailboxMutationKey = () => ['unhideSavedSharedMailbox'] as const;

export const getUnhideSavedSharedMailboxMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unhideSavedSharedMailbox>>, TError,UnhideSavedSharedMailboxMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof unhideSavedSharedMailbox>>, TError,UnhideSavedSharedMailboxMutationVariables, TContext> => {

const mutationKey = getUnhideSavedSharedMailboxMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof unhideSavedSharedMailbox>>, UnhideSavedSharedMailboxMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  unhideSavedSharedMailbox(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UnhideSavedSharedMailboxMutationResult = NonNullable<Awaited<ReturnType<typeof unhideSavedSharedMailbox>>>

    export type UnhideSavedSharedMailboxMutationError = Error
    export type UnhideSavedSharedMailboxMutationVariables = {id: string}

    /**
 * @summary Show a hidden shared mailbox in your list again
 */
export const useUnhideSavedSharedMailbox = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unhideSavedSharedMailbox>>, TError,UnhideSavedSharedMailboxMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof unhideSavedSharedMailbox>>,
        TError,
        UnhideSavedSharedMailboxMutationVariables,
        TContext
      > => {
      return useMutation(getUnhideSavedSharedMailboxMutationOptions(options));
    }

export const getUndoAgentExecutionUrl = (id: string,) => {




  return `/api/agent/undo/${id}`
}

/**
 * Replays the stored reverse request through the same native API permission guards. Each undo can be used once.
 * @summary Reverse one instant low-risk Agent change within 24 hours
 */
export const undoAgentExecution = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<AgentUndoResult> => {

  return customFetch<AgentUndoResult>(getUndoAgentExecutionUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getUndoAgentExecutionMutationKey = () => ['undoAgentExecution'] as const;

export const getUndoAgentExecutionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof undoAgentExecution>>, TError,UndoAgentExecutionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof undoAgentExecution>>, TError,UndoAgentExecutionMutationVariables, TContext> => {

const mutationKey = getUndoAgentExecutionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof undoAgentExecution>>, UndoAgentExecutionMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  undoAgentExecution(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UndoAgentExecutionMutationResult = NonNullable<Awaited<ReturnType<typeof undoAgentExecution>>>

    export type UndoAgentExecutionMutationError = Error
    export type UndoAgentExecutionMutationVariables = {id: string}

    /**
 * @summary Reverse one instant low-risk Agent change within 24 hours
 */
export const useUndoAgentExecution = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof undoAgentExecution>>, TError,UndoAgentExecutionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof undoAgentExecution>>,
        TError,
        UndoAgentExecutionMutationVariables,
        TContext
      > => {
      return useMutation(getUndoAgentExecutionMutationOptions(options));
    }

export const getGetHomepageLayoutUrl = (params?: GetHomepageLayoutParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/homepage/layout?${stringifiedParams}` : `/api/homepage/layout`
}

/**
 * @summary Get the signed-in user's effective desktop and mobile homepage layout
 */
export const getHomepageLayout = async (params?: GetHomepageLayoutParams, options?: Parameters<typeof customFetch>[1]): Promise<HomepageLayoutResponse> => {

  return customFetch<HomepageLayoutResponse>(getGetHomepageLayoutUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetHomepageLayoutQueryKey = (params?: GetHomepageLayoutParams,) => {
    return [
    `/api/homepage/layout`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetHomepageLayoutQueryOptions = <TData = Awaited<ReturnType<typeof getHomepageLayout>>, TError = Error>(params?: GetHomepageLayoutParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHomepageLayout>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetHomepageLayoutQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getHomepageLayout>>> = ({ signal }) => getHomepageLayout(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getHomepageLayout>>, TError, TData> & { queryKey: QueryKey }
}

export type GetHomepageLayoutQueryResult = NonNullable<Awaited<ReturnType<typeof getHomepageLayout>>>
export type GetHomepageLayoutQueryError = Error


/**
 * @summary Get the signed-in user's effective desktop and mobile homepage layout
 */

export function useGetHomepageLayout<TData = Awaited<ReturnType<typeof getHomepageLayout>>, TError = Error>(
 params?: GetHomepageLayoutParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHomepageLayout>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetHomepageLayoutQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSaveHomepageLayoutUrl = (params?: SaveHomepageLayoutParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/homepage/layout?${stringifiedParams}` : `/api/homepage/layout`
}

/**
 * @summary Save the signed-in user's desktop and mobile homepage layout
 */
export const saveHomepageLayout = async (homepageLayoutWrite: HomepageLayoutWrite,
    params?: SaveHomepageLayoutParams, options?: Parameters<typeof customFetch>[1]): Promise<HomepageLayoutResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<HomepageLayoutResponse>(getSaveHomepageLayoutUrl(params),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(homepageLayoutWrite)
  }
);}





export const getSaveHomepageLayoutMutationKey = () => ['saveHomepageLayout'] as const;

export const getSaveHomepageLayoutMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveHomepageLayout>>, TError,SaveHomepageLayoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof saveHomepageLayout>>, TError,SaveHomepageLayoutMutationVariables, TContext> => {

const mutationKey = getSaveHomepageLayoutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof saveHomepageLayout>>, SaveHomepageLayoutMutationVariables> = (props) => {
          const {data,params} = props ?? {};

          return  saveHomepageLayout(data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveHomepageLayoutMutationResult = NonNullable<Awaited<ReturnType<typeof saveHomepageLayout>>>
    export type SaveHomepageLayoutMutationBody = HomepageLayoutWrite
    export type SaveHomepageLayoutMutationError = Error
    export type SaveHomepageLayoutMutationVariables = {data: HomepageLayoutWrite;params?: SaveHomepageLayoutParams}

    /**
 * @summary Save the signed-in user's desktop and mobile homepage layout
 */
export const useSaveHomepageLayout = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveHomepageLayout>>, TError,SaveHomepageLayoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof saveHomepageLayout>>,
        TError,
        SaveHomepageLayoutMutationVariables,
        TContext
      > => {
      return useMutation(getSaveHomepageLayoutMutationOptions(options));
    }

export const getResetHomepageLayoutUrl = (params?: ResetHomepageLayoutParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/homepage/layout/reset?${stringifiedParams}` : `/api/homepage/layout/reset`
}

/**
 * @summary Reset the signed-in user's layout to current defaults and administrator requirements
 */
export const resetHomepageLayout = async (homepageLayoutReset: HomepageLayoutReset,
    params?: ResetHomepageLayoutParams, options?: Parameters<typeof customFetch>[1]): Promise<HomepageLayoutResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<HomepageLayoutResponse>(getResetHomepageLayoutUrl(params),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(homepageLayoutReset)
  }
);}





export const getResetHomepageLayoutMutationKey = () => ['resetHomepageLayout'] as const;

export const getResetHomepageLayoutMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resetHomepageLayout>>, TError,ResetHomepageLayoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof resetHomepageLayout>>, TError,ResetHomepageLayoutMutationVariables, TContext> => {

const mutationKey = getResetHomepageLayoutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof resetHomepageLayout>>, ResetHomepageLayoutMutationVariables> = (props) => {
          const {data,params} = props ?? {};

          return  resetHomepageLayout(data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResetHomepageLayoutMutationResult = NonNullable<Awaited<ReturnType<typeof resetHomepageLayout>>>
    export type ResetHomepageLayoutMutationBody = HomepageLayoutReset
    export type ResetHomepageLayoutMutationError = Error
    export type ResetHomepageLayoutMutationVariables = {data: HomepageLayoutReset;params?: ResetHomepageLayoutParams}

    /**
 * @summary Reset the signed-in user's layout to current defaults and administrator requirements
 */
export const useResetHomepageLayout = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resetHomepageLayout>>, TError,ResetHomepageLayoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof resetHomepageLayout>>,
        TError,
        ResetHomepageLayoutMutationVariables,
        TContext
      > => {
      return useMutation(getResetHomepageLayoutMutationOptions(options));
    }

export const getGetAdminHomepagePolicyUrl = () => {




  return `/api/admin/homepage-policy`
}

/**
 * @summary Get the administrator homepage component policy
 */
export const getAdminHomepagePolicy = async ( options?: Parameters<typeof customFetch>[1]): Promise<HomepagePolicyResponse> => {

  return customFetch<HomepagePolicyResponse>(getGetAdminHomepagePolicyUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAdminHomepagePolicyQueryKey = () => {
    return [
    `/api/admin/homepage-policy`
    ] as const;
    }


export const getGetAdminHomepagePolicyQueryOptions = <TData = Awaited<ReturnType<typeof getAdminHomepagePolicy>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAdminHomepagePolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAdminHomepagePolicyQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAdminHomepagePolicy>>> = ({ signal }) => getAdminHomepagePolicy({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAdminHomepagePolicy>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAdminHomepagePolicyQueryResult = NonNullable<Awaited<ReturnType<typeof getAdminHomepagePolicy>>>
export type GetAdminHomepagePolicyQueryError = Error


/**
 * @summary Get the administrator homepage component policy
 */

export function useGetAdminHomepagePolicy<TData = Awaited<ReturnType<typeof getAdminHomepagePolicy>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAdminHomepagePolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAdminHomepagePolicyQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateAdminHomepagePolicyUrl = () => {




  return `/api/admin/homepage-policy`
}

/**
 * @summary Update required locked components and their desktop/mobile placement
 */
export const updateAdminHomepagePolicy = async (homepagePolicyWrite: HomepagePolicyWrite, options?: Parameters<typeof customFetch>[1]): Promise<HomepagePolicyResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<HomepagePolicyResponse>(getUpdateAdminHomepagePolicyUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(homepagePolicyWrite)
  }
);}





export const getUpdateAdminHomepagePolicyMutationKey = () => ['updateAdminHomepagePolicy'] as const;

export const getUpdateAdminHomepagePolicyMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAdminHomepagePolicy>>, TError,UpdateAdminHomepagePolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateAdminHomepagePolicy>>, TError,UpdateAdminHomepagePolicyMutationVariables, TContext> => {

const mutationKey = getUpdateAdminHomepagePolicyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAdminHomepagePolicy>>, UpdateAdminHomepagePolicyMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateAdminHomepagePolicy(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateAdminHomepagePolicyMutationResult = NonNullable<Awaited<ReturnType<typeof updateAdminHomepagePolicy>>>
    export type UpdateAdminHomepagePolicyMutationBody = HomepagePolicyWrite
    export type UpdateAdminHomepagePolicyMutationError = Error
    export type UpdateAdminHomepagePolicyMutationVariables = {data: HomepagePolicyWrite}

    /**
 * @summary Update required locked components and their desktop/mobile placement
 */
export const useUpdateAdminHomepagePolicy = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAdminHomepagePolicy>>, TError,UpdateAdminHomepagePolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateAdminHomepagePolicy>>,
        TError,
        UpdateAdminHomepagePolicyMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateAdminHomepagePolicyMutationOptions(options));
    }

export const getGetNavigationLayoutsUrl = () => {




  return `/api/navigation/layouts`
}

/**
 * @summary Get every saved sidebar layout for the active entity
 */
export const getNavigationLayouts = async ( options?: Parameters<typeof customFetch>[1]): Promise<NavigationLayoutsResponse> => {

  return customFetch<NavigationLayoutsResponse>(getGetNavigationLayoutsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetNavigationLayoutsQueryKey = () => {
    return [
    `/api/navigation/layouts`
    ] as const;
    }


export const getGetNavigationLayoutsQueryOptions = <TData = Awaited<ReturnType<typeof getNavigationLayouts>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getNavigationLayouts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetNavigationLayoutsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getNavigationLayouts>>> = ({ signal }) => getNavigationLayouts({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getNavigationLayouts>>, TError, TData> & { queryKey: QueryKey }
}

export type GetNavigationLayoutsQueryResult = NonNullable<Awaited<ReturnType<typeof getNavigationLayouts>>>
export type GetNavigationLayoutsQueryError = Error


/**
 * @summary Get every saved sidebar layout for the active entity
 */

export function useGetNavigationLayouts<TData = Awaited<ReturnType<typeof getNavigationLayouts>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getNavigationLayouts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetNavigationLayoutsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetAdminEntityNavigationCatalogueUrl = () => {




  return `/api/admin/entity/navigation/catalogue`
}

/**
 * @summary Get standard app screens, available icons, and published custom pages
 */
export const getAdminEntityNavigationCatalogue = async ( options?: Parameters<typeof customFetch>[1]): Promise<NavigationCatalogue> => {

  return customFetch<NavigationCatalogue>(getGetAdminEntityNavigationCatalogueUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAdminEntityNavigationCatalogueQueryKey = () => {
    return [
    `/api/admin/entity/navigation/catalogue`
    ] as const;
    }


export const getGetAdminEntityNavigationCatalogueQueryOptions = <TData = Awaited<ReturnType<typeof getAdminEntityNavigationCatalogue>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAdminEntityNavigationCatalogue>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAdminEntityNavigationCatalogueQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAdminEntityNavigationCatalogue>>> = ({ signal }) => getAdminEntityNavigationCatalogue({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAdminEntityNavigationCatalogue>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAdminEntityNavigationCatalogueQueryResult = NonNullable<Awaited<ReturnType<typeof getAdminEntityNavigationCatalogue>>>
export type GetAdminEntityNavigationCatalogueQueryError = Error


/**
 * @summary Get standard app screens, available icons, and published custom pages
 */

export function useGetAdminEntityNavigationCatalogue<TData = Awaited<ReturnType<typeof getAdminEntityNavigationCatalogue>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAdminEntityNavigationCatalogue>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAdminEntityNavigationCatalogueQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSaveEntityNavigationLayoutUrl = (view: 'all' | 'b2c' | 'b2b' | 'partner' | 'investor',) => {




  return `/api/admin/entity/navigation/layouts/${view}`
}

/**
 * @summary Save one entity sidebar layout using optimistic concurrency
 */
export const saveEntityNavigationLayout = async (view: 'all' | 'b2c' | 'b2b' | 'partner' | 'investor',
    navigationLayoutWrite: NavigationLayoutWrite, options?: Parameters<typeof customFetch>[1]): Promise<NavigationLayoutMutationResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<NavigationLayoutMutationResponse>(getSaveEntityNavigationLayoutUrl(view),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(navigationLayoutWrite)
  }
);}





export const getSaveEntityNavigationLayoutMutationKey = () => ['saveEntityNavigationLayout'] as const;

export const getSaveEntityNavigationLayoutMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveEntityNavigationLayout>>, TError,SaveEntityNavigationLayoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof saveEntityNavigationLayout>>, TError,SaveEntityNavigationLayoutMutationVariables, TContext> => {

const mutationKey = getSaveEntityNavigationLayoutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof saveEntityNavigationLayout>>, SaveEntityNavigationLayoutMutationVariables> = (props) => {
          const {view,data} = props ?? {};

          return  saveEntityNavigationLayout(view,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveEntityNavigationLayoutMutationResult = NonNullable<Awaited<ReturnType<typeof saveEntityNavigationLayout>>>
    export type SaveEntityNavigationLayoutMutationBody = NavigationLayoutWrite
    export type SaveEntityNavigationLayoutMutationError = Error
    export type SaveEntityNavigationLayoutMutationVariables = {view: 'all' | 'b2c' | 'b2b' | 'partner' | 'investor';data: NavigationLayoutWrite}

    /**
 * @summary Save one entity sidebar layout using optimistic concurrency
 */
export const useSaveEntityNavigationLayout = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveEntityNavigationLayout>>, TError,SaveEntityNavigationLayoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof saveEntityNavigationLayout>>,
        TError,
        SaveEntityNavigationLayoutMutationVariables,
        TContext
      > => {
      return useMutation(getSaveEntityNavigationLayoutMutationOptions(options));
    }

export const getResetEntityNavigationLayoutUrl = (view: 'all' | 'b2c' | 'b2b' | 'partner' | 'investor',) => {




  return `/api/admin/entity/navigation/layouts/${view}`
}

/**
 * @summary Remove one custom entity sidebar layout
 */
export const resetEntityNavigationLayout = async (view: 'all' | 'b2c' | 'b2b' | 'partner' | 'investor',
    navigationLayoutReset: NavigationLayoutReset, options?: Parameters<typeof customFetch>[1]): Promise<NavigationLayoutMutationResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<NavigationLayoutMutationResponse>(getResetEntityNavigationLayoutUrl(view),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(navigationLayoutReset)
  }
);}





export const getResetEntityNavigationLayoutMutationKey = () => ['resetEntityNavigationLayout'] as const;

export const getResetEntityNavigationLayoutMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resetEntityNavigationLayout>>, TError,ResetEntityNavigationLayoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof resetEntityNavigationLayout>>, TError,ResetEntityNavigationLayoutMutationVariables, TContext> => {

const mutationKey = getResetEntityNavigationLayoutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof resetEntityNavigationLayout>>, ResetEntityNavigationLayoutMutationVariables> = (props) => {
          const {view,data} = props ?? {};

          return  resetEntityNavigationLayout(view,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResetEntityNavigationLayoutMutationResult = NonNullable<Awaited<ReturnType<typeof resetEntityNavigationLayout>>>
    export type ResetEntityNavigationLayoutMutationBody = NavigationLayoutReset
    export type ResetEntityNavigationLayoutMutationError = Error
    export type ResetEntityNavigationLayoutMutationVariables = {view: 'all' | 'b2c' | 'b2b' | 'partner' | 'investor';data: NavigationLayoutReset}

    /**
 * @summary Remove one custom entity sidebar layout
 */
export const useResetEntityNavigationLayout = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resetEntityNavigationLayout>>, TError,ResetEntityNavigationLayoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof resetEntityNavigationLayout>>,
        TError,
        ResetEntityNavigationLayoutMutationVariables,
        TContext
      > => {
      return useMutation(getResetEntityNavigationLayoutMutationOptions(options));
    }

export const getCopyEntityNavigationLayoutUrl = (view: 'all' | 'b2c' | 'b2b' | 'partner' | 'investor',) => {




  return `/api/admin/entity/navigation/layouts/${view}/copy`
}

/**
 * @summary Copy one saved entity sidebar layout to other workspace views
 */
export const copyEntityNavigationLayout = async (view: 'all' | 'b2c' | 'b2b' | 'partner' | 'investor',
    navigationLayoutCopy: NavigationLayoutCopy, options?: Parameters<typeof customFetch>[1]): Promise<NavigationLayoutsResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<NavigationLayoutsResponse>(getCopyEntityNavigationLayoutUrl(view),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(navigationLayoutCopy)
  }
);}





export const getCopyEntityNavigationLayoutMutationKey = () => ['copyEntityNavigationLayout'] as const;

export const getCopyEntityNavigationLayoutMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof copyEntityNavigationLayout>>, TError,CopyEntityNavigationLayoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof copyEntityNavigationLayout>>, TError,CopyEntityNavigationLayoutMutationVariables, TContext> => {

const mutationKey = getCopyEntityNavigationLayoutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof copyEntityNavigationLayout>>, CopyEntityNavigationLayoutMutationVariables> = (props) => {
          const {view,data} = props ?? {};

          return  copyEntityNavigationLayout(view,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CopyEntityNavigationLayoutMutationResult = NonNullable<Awaited<ReturnType<typeof copyEntityNavigationLayout>>>
    export type CopyEntityNavigationLayoutMutationBody = NavigationLayoutCopy
    export type CopyEntityNavigationLayoutMutationError = Error
    export type CopyEntityNavigationLayoutMutationVariables = {view: 'all' | 'b2c' | 'b2b' | 'partner' | 'investor';data: NavigationLayoutCopy}

    /**
 * @summary Copy one saved entity sidebar layout to other workspace views
 */
export const useCopyEntityNavigationLayout = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof copyEntityNavigationLayout>>, TError,CopyEntityNavigationLayoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof copyEntityNavigationLayout>>,
        TError,
        CopyEntityNavigationLayoutMutationVariables,
        TContext
      > => {
      return useMutation(getCopyEntityNavigationLayoutMutationOptions(options));
    }

export const getListResearchThreadsUrl = (params?: ListResearchThreadsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/research/threads?${stringifiedParams}` : `/api/research/threads`
}

/**
 * @summary List shared research threads in the selected entity
 */
export const listResearchThreads = async (params?: ListResearchThreadsParams, options?: Parameters<typeof customFetch>[1]): Promise<ResearchThreadPage> => {

  return customFetch<ResearchThreadPage>(getListResearchThreadsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListResearchThreadsQueryKey = (params?: ListResearchThreadsParams,) => {
    return [
    `/api/research/threads`, ...(params ? [params] : [])
    ] as const;
    }


export const getListResearchThreadsQueryOptions = <TData = Awaited<ReturnType<typeof listResearchThreads>>, TError = Error>(params?: ListResearchThreadsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchThreads>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListResearchThreadsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listResearchThreads>>> = ({ signal }) => listResearchThreads(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listResearchThreads>>, TError, TData> & { queryKey: QueryKey }
}

export type ListResearchThreadsQueryResult = NonNullable<Awaited<ReturnType<typeof listResearchThreads>>>
export type ListResearchThreadsQueryError = Error


/**
 * @summary List shared research threads in the selected entity
 */

export function useListResearchThreads<TData = Awaited<ReturnType<typeof listResearchThreads>>, TError = Error>(
 params?: ListResearchThreadsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchThreads>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListResearchThreadsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateResearchThreadUrl = () => {




  return `/api/research/threads`
}

/**
 * @summary Create a shared research thread
 */
export const createResearchThread = async (createResearchThreadBody: CreateResearchThread, options?: Parameters<typeof customFetch>[1]): Promise<ResearchThread> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ResearchThread>(getCreateResearchThreadUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createResearchThreadBody)
  }
);}





export const getCreateResearchThreadMutationKey = () => ['createResearchThread'] as const;

export const getCreateResearchThreadMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createResearchThread>>, TError,CreateResearchThreadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createResearchThread>>, TError,CreateResearchThreadMutationVariables, TContext> => {

const mutationKey = getCreateResearchThreadMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createResearchThread>>, CreateResearchThreadMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createResearchThread(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateResearchThreadMutationResult = NonNullable<Awaited<ReturnType<typeof createResearchThread>>>
    export type CreateResearchThreadMutationBody = CreateResearchThread
    export type CreateResearchThreadMutationError = Error
    export type CreateResearchThreadMutationVariables = {data: CreateResearchThread}

    /**
 * @summary Create a shared research thread
 */
export const useCreateResearchThread = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createResearchThread>>, TError,CreateResearchThreadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createResearchThread>>,
        TError,
        CreateResearchThreadMutationVariables,
        TContext
      > => {
      return useMutation(getCreateResearchThreadMutationOptions(options));
    }

export const getGetResearchThreadUrl = (threadId: string,) => {




  return `/api/research/threads/${threadId}`
}

/**
 * @summary Get one shared research thread
 */
export const getResearchThread = async (threadId: string, options?: Parameters<typeof customFetch>[1]): Promise<ResearchThread> => {

  return customFetch<ResearchThread>(getGetResearchThreadUrl(threadId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetResearchThreadQueryKey = (threadId: string,) => {
    return [
    `/api/research/threads/${threadId}`
    ] as const;
    }


export const getGetResearchThreadQueryOptions = <TData = Awaited<ReturnType<typeof getResearchThread>>, TError = Error>(threadId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getResearchThread>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetResearchThreadQueryKey(threadId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getResearchThread>>> = ({ signal }) => getResearchThread(threadId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: threadId !== null && threadId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getResearchThread>>, TError, TData> & { queryKey: QueryKey }
}

export type GetResearchThreadQueryResult = NonNullable<Awaited<ReturnType<typeof getResearchThread>>>
export type GetResearchThreadQueryError = Error


/**
 * @summary Get one shared research thread
 */

export function useGetResearchThread<TData = Awaited<ReturnType<typeof getResearchThread>>, TError = Error>(
 threadId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getResearchThread>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetResearchThreadQueryOptions(threadId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateResearchThreadUrl = (threadId: string,) => {




  return `/api/research/threads/${threadId}`
}

/**
 * @summary Rename or archive a research thread using its current revision
 */
export const updateResearchThread = async (threadId: string,
    updateResearchThreadBody: UpdateResearchThread, options?: Parameters<typeof customFetch>[1]): Promise<ResearchThread> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ResearchThread>(getUpdateResearchThreadUrl(threadId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateResearchThreadBody)
  }
);}





export const getUpdateResearchThreadMutationKey = () => ['updateResearchThread'] as const;

export const getUpdateResearchThreadMutationOptions = <TError = Error | ResearchRevisionConflict,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateResearchThread>>, TError,UpdateResearchThreadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateResearchThread>>, TError,UpdateResearchThreadMutationVariables, TContext> => {

const mutationKey = getUpdateResearchThreadMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateResearchThread>>, UpdateResearchThreadMutationVariables> = (props) => {
          const {threadId,data} = props ?? {};

          return  updateResearchThread(threadId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateResearchThreadMutationResult = NonNullable<Awaited<ReturnType<typeof updateResearchThread>>>
    export type UpdateResearchThreadMutationBody = UpdateResearchThread
    export type UpdateResearchThreadMutationError = Error | ResearchRevisionConflict
    export type UpdateResearchThreadMutationVariables = {threadId: string;data: UpdateResearchThread}

    /**
 * @summary Rename or archive a research thread using its current revision
 */
export const useUpdateResearchThread = <TError = Error | ResearchRevisionConflict,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateResearchThread>>, TError,UpdateResearchThreadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateResearchThread>>,
        TError,
        UpdateResearchThreadMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateResearchThreadMutationOptions(options));
    }

export const getDeleteResearchThreadUrl = (threadId: string,) => {




  return `/api/research/threads/${threadId}`
}

/**
 * @summary Permanently delete a research thread after explicit confirmation
 */
export const deleteResearchThread = async (threadId: string,
    researchDeleteConfirmation: ResearchDeleteConfirmation, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<void>(getDeleteResearchThreadUrl(threadId),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(researchDeleteConfirmation)
  }
);}





export const getDeleteResearchThreadMutationKey = () => ['deleteResearchThread'] as const;

export const getDeleteResearchThreadMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteResearchThread>>, TError,DeleteResearchThreadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteResearchThread>>, TError,DeleteResearchThreadMutationVariables, TContext> => {

const mutationKey = getDeleteResearchThreadMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteResearchThread>>, DeleteResearchThreadMutationVariables> = (props) => {
          const {threadId,data} = props ?? {};

          return  deleteResearchThread(threadId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteResearchThreadMutationResult = NonNullable<Awaited<ReturnType<typeof deleteResearchThread>>>
    export type DeleteResearchThreadMutationBody = ResearchDeleteConfirmation
    export type DeleteResearchThreadMutationError = Error
    export type DeleteResearchThreadMutationVariables = {threadId: string;data: ResearchDeleteConfirmation}

    /**
 * @summary Permanently delete a research thread after explicit confirmation
 */
export const useDeleteResearchThread = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteResearchThread>>, TError,DeleteResearchThreadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteResearchThread>>,
        TError,
        DeleteResearchThreadMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteResearchThreadMutationOptions(options));
    }

export const getListResearchMessagesUrl = (threadId: string,
    params?: ListResearchMessagesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/research/threads/${threadId}/messages?${stringifiedParams}` : `/api/research/threads/${threadId}/messages`
}

/**
 * @summary List paged durable message history
 */
export const listResearchMessages = async (threadId: string,
    params?: ListResearchMessagesParams, options?: Parameters<typeof customFetch>[1]): Promise<ResearchMessagePage> => {

  return customFetch<ResearchMessagePage>(getListResearchMessagesUrl(threadId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListResearchMessagesQueryKey = (threadId: string,
    params?: ListResearchMessagesParams,) => {
    return [
    `/api/research/threads/${threadId}/messages`, ...(params ? [params] : [])
    ] as const;
    }


export const getListResearchMessagesQueryOptions = <TData = Awaited<ReturnType<typeof listResearchMessages>>, TError = Error>(threadId: string,
    params?: ListResearchMessagesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchMessages>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListResearchMessagesQueryKey(threadId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listResearchMessages>>> = ({ signal }) => listResearchMessages(threadId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: threadId !== null && threadId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listResearchMessages>>, TError, TData> & { queryKey: QueryKey }
}

export type ListResearchMessagesQueryResult = NonNullable<Awaited<ReturnType<typeof listResearchMessages>>>
export type ListResearchMessagesQueryError = Error


/**
 * @summary List paged durable message history
 */

export function useListResearchMessages<TData = Awaited<ReturnType<typeof listResearchMessages>>, TError = Error>(
 threadId: string,
    params?: ListResearchMessagesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchMessages>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListResearchMessagesQueryOptions(threadId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListResearchThreadAttachmentsUrl = (threadId: string,
    params?: ListResearchThreadAttachmentsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/research/threads/${threadId}/attachments?${stringifiedParams}` : `/api/research/threads/${threadId}/attachments`
}

/**
 * @summary List private files attached to a research thread
 */
export const listResearchThreadAttachments = async (threadId: string,
    params?: ListResearchThreadAttachmentsParams, options?: Parameters<typeof customFetch>[1]): Promise<ResearchAttachmentPage> => {

  return customFetch<ResearchAttachmentPage>(getListResearchThreadAttachmentsUrl(threadId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListResearchThreadAttachmentsQueryKey = (threadId: string,
    params?: ListResearchThreadAttachmentsParams,) => {
    return [
    `/api/research/threads/${threadId}/attachments`, ...(params ? [params] : [])
    ] as const;
    }


export const getListResearchThreadAttachmentsQueryOptions = <TData = Awaited<ReturnType<typeof listResearchThreadAttachments>>, TError = unknown>(threadId: string,
    params?: ListResearchThreadAttachmentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchThreadAttachments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListResearchThreadAttachmentsQueryKey(threadId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listResearchThreadAttachments>>> = ({ signal }) => listResearchThreadAttachments(threadId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: threadId !== null && threadId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listResearchThreadAttachments>>, TError, TData> & { queryKey: QueryKey }
}

export type ListResearchThreadAttachmentsQueryResult = NonNullable<Awaited<ReturnType<typeof listResearchThreadAttachments>>>
export type ListResearchThreadAttachmentsQueryError = unknown


/**
 * @summary List private files attached to a research thread
 */

export function useListResearchThreadAttachments<TData = Awaited<ReturnType<typeof listResearchThreadAttachments>>, TError = unknown>(
 threadId: string,
    params?: ListResearchThreadAttachmentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchThreadAttachments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListResearchThreadAttachmentsQueryOptions(threadId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUploadResearchThreadAttachmentUrl = (threadId: string,) => {




  return `/api/research/threads/${threadId}/attachments`
}

/**
 * @summary Validate and privately attach one file to a thread
 */
export const uploadResearchThreadAttachment = async (threadId: string,
    uploadResearchThreadAttachmentBody: UploadResearchThreadAttachmentBody, options?: Parameters<typeof customFetch>[1]): Promise<ResearchAttachment> => {
    const formData = new FormData();
formData.append(`file`, uploadResearchThreadAttachmentBody.file);

  return customFetch<ResearchAttachment>(getUploadResearchThreadAttachmentUrl(threadId),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}





export const getUploadResearchThreadAttachmentMutationKey = () => ['uploadResearchThreadAttachment'] as const;

export const getUploadResearchThreadAttachmentMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadResearchThreadAttachment>>, TError,UploadResearchThreadAttachmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadResearchThreadAttachment>>, TError,UploadResearchThreadAttachmentMutationVariables, TContext> => {

const mutationKey = getUploadResearchThreadAttachmentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadResearchThreadAttachment>>, UploadResearchThreadAttachmentMutationVariables> = (props) => {
          const {threadId,data} = props ?? {};

          return  uploadResearchThreadAttachment(threadId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadResearchThreadAttachmentMutationResult = NonNullable<Awaited<ReturnType<typeof uploadResearchThreadAttachment>>>
    export type UploadResearchThreadAttachmentMutationBody = UploadResearchThreadAttachmentBody
    export type UploadResearchThreadAttachmentMutationError = Error
    export type UploadResearchThreadAttachmentMutationVariables = {threadId: string;data: UploadResearchThreadAttachmentBody}

    /**
 * @summary Validate and privately attach one file to a thread
 */
export const useUploadResearchThreadAttachment = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadResearchThreadAttachment>>, TError,UploadResearchThreadAttachmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof uploadResearchThreadAttachment>>,
        TError,
        UploadResearchThreadAttachmentMutationVariables,
        TContext
      > => {
      return useMutation(getUploadResearchThreadAttachmentMutationOptions(options));
    }

export const getDeleteResearchThreadAttachmentUrl = (threadId: string,
    attachmentId: string,) => {




  return `/api/research/threads/${threadId}/attachments/${attachmentId}`
}

/**
 * @summary Remove a private attachment from a thread
 */
export const deleteResearchThreadAttachment = async (threadId: string,
    attachmentId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteResearchThreadAttachmentUrl(threadId,attachmentId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteResearchThreadAttachmentMutationKey = () => ['deleteResearchThreadAttachment'] as const;

export const getDeleteResearchThreadAttachmentMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteResearchThreadAttachment>>, TError,DeleteResearchThreadAttachmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteResearchThreadAttachment>>, TError,DeleteResearchThreadAttachmentMutationVariables, TContext> => {

const mutationKey = getDeleteResearchThreadAttachmentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteResearchThreadAttachment>>, DeleteResearchThreadAttachmentMutationVariables> = (props) => {
          const {threadId,attachmentId} = props ?? {};

          return  deleteResearchThreadAttachment(threadId,attachmentId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteResearchThreadAttachmentMutationResult = NonNullable<Awaited<ReturnType<typeof deleteResearchThreadAttachment>>>

    export type DeleteResearchThreadAttachmentMutationError = unknown
    export type DeleteResearchThreadAttachmentMutationVariables = {threadId: string;attachmentId: string}

    /**
 * @summary Remove a private attachment from a thread
 */
export const useDeleteResearchThreadAttachment = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteResearchThreadAttachment>>, TError,DeleteResearchThreadAttachmentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteResearchThreadAttachment>>,
        TError,
        DeleteResearchThreadAttachmentMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteResearchThreadAttachmentMutationOptions(options));
    }

export const getGetResearchWebSearchStatusUrl = () => {




  return `/api/research/web-search/status`
}

/**
 * @summary Read safe configuration and monthly-limit readiness for Azure AI Foundry web search
 */
export const getResearchWebSearchStatus = async ( options?: Parameters<typeof customFetch>[1]): Promise<ResearchWebSearchStatus> => {

  return customFetch<ResearchWebSearchStatus>(getGetResearchWebSearchStatusUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetResearchWebSearchStatusQueryKey = () => {
    return [
    `/api/research/web-search/status`
    ] as const;
    }


export const getGetResearchWebSearchStatusQueryOptions = <TData = Awaited<ReturnType<typeof getResearchWebSearchStatus>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getResearchWebSearchStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetResearchWebSearchStatusQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getResearchWebSearchStatus>>> = ({ signal }) => getResearchWebSearchStatus({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getResearchWebSearchStatus>>, TError, TData> & { queryKey: QueryKey }
}

export type GetResearchWebSearchStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getResearchWebSearchStatus>>>
export type GetResearchWebSearchStatusQueryError = unknown


/**
 * @summary Read safe configuration and monthly-limit readiness for Azure AI Foundry web search
 */

export function useGetResearchWebSearchStatus<TData = Awaited<ReturnType<typeof getResearchWebSearchStatus>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getResearchWebSearchStatus>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetResearchWebSearchStatusQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListResearchRunsUrl = (threadId: string,
    params?: ListResearchRunsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/research/threads/${threadId}/runs?${stringifiedParams}` : `/api/research/threads/${threadId}/runs`
}

/**
 * @summary List paged run history for a thread
 */
export const listResearchRuns = async (threadId: string,
    params?: ListResearchRunsParams, options?: Parameters<typeof customFetch>[1]): Promise<ResearchRunPage> => {

  return customFetch<ResearchRunPage>(getListResearchRunsUrl(threadId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListResearchRunsQueryKey = (threadId: string,
    params?: ListResearchRunsParams,) => {
    return [
    `/api/research/threads/${threadId}/runs`, ...(params ? [params] : [])
    ] as const;
    }


export const getListResearchRunsQueryOptions = <TData = Awaited<ReturnType<typeof listResearchRuns>>, TError = Error>(threadId: string,
    params?: ListResearchRunsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchRuns>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListResearchRunsQueryKey(threadId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listResearchRuns>>> = ({ signal }) => listResearchRuns(threadId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: threadId !== null && threadId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listResearchRuns>>, TError, TData> & { queryKey: QueryKey }
}

export type ListResearchRunsQueryResult = NonNullable<Awaited<ReturnType<typeof listResearchRuns>>>
export type ListResearchRunsQueryError = Error


/**
 * @summary List paged run history for a thread
 */

export function useListResearchRuns<TData = Awaited<ReturnType<typeof listResearchRuns>>, TError = Error>(
 threadId: string,
    params?: ListResearchRunsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchRuns>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListResearchRunsQueryOptions(threadId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getStreamResearchRunUrl = (threadId: string,) => {




  return `/api/research/threads/${threadId}/runs`
}

/**
 * Requires entity.ai.use in addition to entity record write access. The client request ID is unique within the thread.
 * @summary Start a deduplicated grounded research run and stream typed events
 */
export const streamResearchRun = async (threadId: string,
    createResearchRun: CreateResearchRun, options?: Parameters<typeof customFetch>[1]): Promise<ResearchRunStreamEvent> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ResearchRunStreamEvent>(getStreamResearchRunUrl(threadId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createResearchRun)
  }
);}





export const getStreamResearchRunMutationKey = () => ['streamResearchRun'] as const;

export const getStreamResearchRunMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof streamResearchRun>>, TError,StreamResearchRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof streamResearchRun>>, TError,StreamResearchRunMutationVariables, TContext> => {

const mutationKey = getStreamResearchRunMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof streamResearchRun>>, StreamResearchRunMutationVariables> = (props) => {
          const {threadId,data} = props ?? {};

          return  streamResearchRun(threadId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type StreamResearchRunMutationResult = NonNullable<Awaited<ReturnType<typeof streamResearchRun>>>
    export type StreamResearchRunMutationBody = CreateResearchRun
    export type StreamResearchRunMutationError = Error
    export type StreamResearchRunMutationVariables = {threadId: string;data: CreateResearchRun}

    /**
 * @summary Start a deduplicated grounded research run and stream typed events
 */
export const useStreamResearchRun = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof streamResearchRun>>, TError,StreamResearchRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof streamResearchRun>>,
        TError,
        StreamResearchRunMutationVariables,
        TContext
      > => {
      return useMutation(getStreamResearchRunMutationOptions(options));
    }

export const getCancelResearchRunUrl = (threadId: string,
    runId: string,) => {




  return `/api/research/threads/${threadId}/runs/${runId}/cancel`
}

/**
 * @summary Request cancellation of a queued or running research run
 */
export const cancelResearchRun = async (threadId: string,
    runId: string, options?: Parameters<typeof customFetch>[1]): Promise<ResearchRun> => {

  return customFetch<ResearchRun>(getCancelResearchRunUrl(threadId,runId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelResearchRunMutationKey = () => ['cancelResearchRun'] as const;

export const getCancelResearchRunMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelResearchRun>>, TError,CancelResearchRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelResearchRun>>, TError,CancelResearchRunMutationVariables, TContext> => {

const mutationKey = getCancelResearchRunMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelResearchRun>>, CancelResearchRunMutationVariables> = (props) => {
          const {threadId,runId} = props ?? {};

          return  cancelResearchRun(threadId,runId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelResearchRunMutationResult = NonNullable<Awaited<ReturnType<typeof cancelResearchRun>>>

    export type CancelResearchRunMutationError = Error
    export type CancelResearchRunMutationVariables = {threadId: string;runId: string}

    /**
 * @summary Request cancellation of a queued or running research run
 */
export const useCancelResearchRun = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelResearchRun>>, TError,CancelResearchRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelResearchRun>>,
        TError,
        CancelResearchRunMutationVariables,
        TContext
      > => {
      return useMutation(getCancelResearchRunMutationOptions(options));
    }

export const getRetryResearchRunUrl = (threadId: string,
    runId: string,) => {




  return `/api/research/threads/${threadId}/runs/${runId}/retry`
}

/**
 * @summary Retry a failed or cancelled run with a new client request ID
 */
export const retryResearchRun = async (threadId: string,
    runId: string,
    retryResearchRunBody: RetryResearchRun, options?: Parameters<typeof customFetch>[1]): Promise<ResearchRunStreamEvent> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ResearchRunStreamEvent>(getRetryResearchRunUrl(threadId,runId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(retryResearchRunBody)
  }
);}





export const getRetryResearchRunMutationKey = () => ['retryResearchRun'] as const;

export const getRetryResearchRunMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryResearchRun>>, TError,RetryResearchRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof retryResearchRun>>, TError,RetryResearchRunMutationVariables, TContext> => {

const mutationKey = getRetryResearchRunMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof retryResearchRun>>, RetryResearchRunMutationVariables> = (props) => {
          const {threadId,runId,data} = props ?? {};

          return  retryResearchRun(threadId,runId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RetryResearchRunMutationResult = NonNullable<Awaited<ReturnType<typeof retryResearchRun>>>
    export type RetryResearchRunMutationBody = RetryResearchRun
    export type RetryResearchRunMutationError = Error
    export type RetryResearchRunMutationVariables = {threadId: string;runId: string;data: RetryResearchRun}

    /**
 * @summary Retry a failed or cancelled run with a new client request ID
 */
export const useRetryResearchRun = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryResearchRun>>, TError,RetryResearchRunMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof retryResearchRun>>,
        TError,
        RetryResearchRunMutationVariables,
        TContext
      > => {
      return useMutation(getRetryResearchRunMutationOptions(options));
    }

export const getListResearchPromptsUrl = (params?: ListResearchPromptsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/research/prompts?${stringifiedParams}` : `/api/research/prompts`
}

/**
 * @summary List paged entity prompt shortcuts
 */
export const listResearchPrompts = async (params?: ListResearchPromptsParams, options?: Parameters<typeof customFetch>[1]): Promise<ResearchPromptPage> => {

  return customFetch<ResearchPromptPage>(getListResearchPromptsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListResearchPromptsQueryKey = (params?: ListResearchPromptsParams,) => {
    return [
    `/api/research/prompts`, ...(params ? [params] : [])
    ] as const;
    }


export const getListResearchPromptsQueryOptions = <TData = Awaited<ReturnType<typeof listResearchPrompts>>, TError = unknown>(params?: ListResearchPromptsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchPrompts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListResearchPromptsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listResearchPrompts>>> = ({ signal }) => listResearchPrompts(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listResearchPrompts>>, TError, TData> & { queryKey: QueryKey }
}

export type ListResearchPromptsQueryResult = NonNullable<Awaited<ReturnType<typeof listResearchPrompts>>>
export type ListResearchPromptsQueryError = unknown


/**
 * @summary List paged entity prompt shortcuts
 */

export function useListResearchPrompts<TData = Awaited<ReturnType<typeof listResearchPrompts>>, TError = unknown>(
 params?: ListResearchPromptsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchPrompts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListResearchPromptsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateResearchPromptUrl = () => {




  return `/api/research/prompts`
}

/**
 * @summary Create an entity prompt shortcut
 */
export const createResearchPrompt = async (researchPromptWrite: ResearchPromptWrite, options?: Parameters<typeof customFetch>[1]): Promise<ResearchPrompt> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ResearchPrompt>(getCreateResearchPromptUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(researchPromptWrite)
  }
);}





export const getCreateResearchPromptMutationKey = () => ['createResearchPrompt'] as const;

export const getCreateResearchPromptMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createResearchPrompt>>, TError,CreateResearchPromptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createResearchPrompt>>, TError,CreateResearchPromptMutationVariables, TContext> => {

const mutationKey = getCreateResearchPromptMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createResearchPrompt>>, CreateResearchPromptMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createResearchPrompt(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateResearchPromptMutationResult = NonNullable<Awaited<ReturnType<typeof createResearchPrompt>>>
    export type CreateResearchPromptMutationBody = ResearchPromptWrite
    export type CreateResearchPromptMutationError = Error
    export type CreateResearchPromptMutationVariables = {data: ResearchPromptWrite}

    /**
 * @summary Create an entity prompt shortcut
 */
export const useCreateResearchPrompt = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createResearchPrompt>>, TError,CreateResearchPromptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createResearchPrompt>>,
        TError,
        CreateResearchPromptMutationVariables,
        TContext
      > => {
      return useMutation(getCreateResearchPromptMutationOptions(options));
    }

export const getUpdateResearchPromptUrl = (promptId: string,) => {




  return `/api/research/prompts/${promptId}`
}

/**
 * @summary Update a prompt shortcut using its current revision
 */
export const updateResearchPrompt = async (promptId: string,
    researchPromptUpdate: ResearchPromptUpdate, options?: Parameters<typeof customFetch>[1]): Promise<ResearchPrompt> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ResearchPrompt>(getUpdateResearchPromptUrl(promptId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(researchPromptUpdate)
  }
);}





export const getUpdateResearchPromptMutationKey = () => ['updateResearchPrompt'] as const;

export const getUpdateResearchPromptMutationOptions = <TError = ResearchRevisionConflict,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateResearchPrompt>>, TError,UpdateResearchPromptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateResearchPrompt>>, TError,UpdateResearchPromptMutationVariables, TContext> => {

const mutationKey = getUpdateResearchPromptMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateResearchPrompt>>, UpdateResearchPromptMutationVariables> = (props) => {
          const {promptId,data} = props ?? {};

          return  updateResearchPrompt(promptId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateResearchPromptMutationResult = NonNullable<Awaited<ReturnType<typeof updateResearchPrompt>>>
    export type UpdateResearchPromptMutationBody = ResearchPromptUpdate
    export type UpdateResearchPromptMutationError = ResearchRevisionConflict
    export type UpdateResearchPromptMutationVariables = {promptId: string;data: ResearchPromptUpdate}

    /**
 * @summary Update a prompt shortcut using its current revision
 */
export const useUpdateResearchPrompt = <TError = ResearchRevisionConflict,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateResearchPrompt>>, TError,UpdateResearchPromptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateResearchPrompt>>,
        TError,
        UpdateResearchPromptMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateResearchPromptMutationOptions(options));
    }

export const getDeleteResearchPromptUrl = (promptId: string,) => {




  return `/api/research/prompts/${promptId}`
}

/**
 * @summary Delete a prompt shortcut
 */
export const deleteResearchPrompt = async (promptId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteResearchPromptUrl(promptId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteResearchPromptMutationKey = () => ['deleteResearchPrompt'] as const;

export const getDeleteResearchPromptMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteResearchPrompt>>, TError,DeleteResearchPromptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteResearchPrompt>>, TError,DeleteResearchPromptMutationVariables, TContext> => {

const mutationKey = getDeleteResearchPromptMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteResearchPrompt>>, DeleteResearchPromptMutationVariables> = (props) => {
          const {promptId} = props ?? {};

          return  deleteResearchPrompt(promptId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteResearchPromptMutationResult = NonNullable<Awaited<ReturnType<typeof deleteResearchPrompt>>>

    export type DeleteResearchPromptMutationError = unknown
    export type DeleteResearchPromptMutationVariables = {promptId: string}

    /**
 * @summary Delete a prompt shortcut
 */
export const useDeleteResearchPrompt = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteResearchPrompt>>, TError,DeleteResearchPromptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteResearchPrompt>>,
        TError,
        DeleteResearchPromptMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteResearchPromptMutationOptions(options));
    }

export const getListResearchPresenceUrl = () => {




  return `/api/research/presence`
}

/**
 * @summary List collaborators whose presence has not expired
 */
export const listResearchPresence = async ( options?: Parameters<typeof customFetch>[1]): Promise<ResearchPresenceList> => {

  return customFetch<ResearchPresenceList>(getListResearchPresenceUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListResearchPresenceQueryKey = () => {
    return [
    `/api/research/presence`
    ] as const;
    }


export const getListResearchPresenceQueryOptions = <TData = Awaited<ReturnType<typeof listResearchPresence>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchPresence>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListResearchPresenceQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listResearchPresence>>> = ({ signal }) => listResearchPresence({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listResearchPresence>>, TError, TData> & { queryKey: QueryKey }
}

export type ListResearchPresenceQueryResult = NonNullable<Awaited<ReturnType<typeof listResearchPresence>>>
export type ListResearchPresenceQueryError = unknown


/**
 * @summary List collaborators whose presence has not expired
 */

export function useListResearchPresence<TData = Awaited<ReturnType<typeof listResearchPresence>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchPresence>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListResearchPresenceQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getHeartbeatResearchPresenceUrl = () => {




  return `/api/research/presence`
}

/**
 * @summary Refresh the authenticated collaborator's TTL presence
 */
export const heartbeatResearchPresence = async (researchPresenceHeartbeat: ResearchPresenceHeartbeat, options?: Parameters<typeof customFetch>[1]): Promise<ResearchPresence> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ResearchPresence>(getHeartbeatResearchPresenceUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(researchPresenceHeartbeat)
  }
);}





export const getHeartbeatResearchPresenceMutationKey = () => ['heartbeatResearchPresence'] as const;

export const getHeartbeatResearchPresenceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof heartbeatResearchPresence>>, TError,HeartbeatResearchPresenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof heartbeatResearchPresence>>, TError,HeartbeatResearchPresenceMutationVariables, TContext> => {

const mutationKey = getHeartbeatResearchPresenceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof heartbeatResearchPresence>>, HeartbeatResearchPresenceMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  heartbeatResearchPresence(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type HeartbeatResearchPresenceMutationResult = NonNullable<Awaited<ReturnType<typeof heartbeatResearchPresence>>>
    export type HeartbeatResearchPresenceMutationBody = ResearchPresenceHeartbeat
    export type HeartbeatResearchPresenceMutationError = unknown
    export type HeartbeatResearchPresenceMutationVariables = {data: ResearchPresenceHeartbeat}

    /**
 * @summary Refresh the authenticated collaborator's TTL presence
 */
export const useHeartbeatResearchPresence = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof heartbeatResearchPresence>>, TError,HeartbeatResearchPresenceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof heartbeatResearchPresence>>,
        TError,
        HeartbeatResearchPresenceMutationVariables,
        TContext
      > => {
      return useMutation(getHeartbeatResearchPresenceMutationOptions(options));
    }

export const getListResearchDocumentsUrl = (params?: ListResearchDocumentsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/research/documents?${stringifiedParams}` : `/api/research/documents`
}

/**
 * @summary List shared internal documents in the selected entity
 */
export const listResearchDocuments = async (params?: ListResearchDocumentsParams, options?: Parameters<typeof customFetch>[1]): Promise<ResearchDocumentPage> => {

  return customFetch<ResearchDocumentPage>(getListResearchDocumentsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListResearchDocumentsQueryKey = (params?: ListResearchDocumentsParams,) => {
    return [
    `/api/research/documents`, ...(params ? [params] : [])
    ] as const;
    }


export const getListResearchDocumentsQueryOptions = <TData = Awaited<ReturnType<typeof listResearchDocuments>>, TError = unknown>(params?: ListResearchDocumentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchDocuments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListResearchDocumentsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listResearchDocuments>>> = ({ signal }) => listResearchDocuments(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listResearchDocuments>>, TError, TData> & { queryKey: QueryKey }
}

export type ListResearchDocumentsQueryResult = NonNullable<Awaited<ReturnType<typeof listResearchDocuments>>>
export type ListResearchDocumentsQueryError = unknown


/**
 * @summary List shared internal documents in the selected entity
 */

export function useListResearchDocuments<TData = Awaited<ReturnType<typeof listResearchDocuments>>, TError = unknown>(
 params?: ListResearchDocumentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchDocuments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListResearchDocumentsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateResearchDocumentUrl = () => {




  return `/api/research/documents`
}

/**
 * @summary Create a blank or research-derived internal document and its immutable first version
 */
export const createResearchDocument = async (createResearchDocumentBody: CreateResearchDocument, options?: Parameters<typeof customFetch>[1]): Promise<ResearchDocument> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ResearchDocument>(getCreateResearchDocumentUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createResearchDocumentBody)
  }
);}





export const getCreateResearchDocumentMutationKey = () => ['createResearchDocument'] as const;

export const getCreateResearchDocumentMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createResearchDocument>>, TError,CreateResearchDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createResearchDocument>>, TError,CreateResearchDocumentMutationVariables, TContext> => {

const mutationKey = getCreateResearchDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createResearchDocument>>, CreateResearchDocumentMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createResearchDocument(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateResearchDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof createResearchDocument>>>
    export type CreateResearchDocumentMutationBody = CreateResearchDocument
    export type CreateResearchDocumentMutationError = unknown
    export type CreateResearchDocumentMutationVariables = {data: CreateResearchDocument}

    /**
 * @summary Create a blank or research-derived internal document and its immutable first version
 */
export const useCreateResearchDocument = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createResearchDocument>>, TError,CreateResearchDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createResearchDocument>>,
        TError,
        CreateResearchDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getCreateResearchDocumentMutationOptions(options));
    }

export const getGetResearchDocumentUrl = (documentId: string,) => {




  return `/api/research/documents/${documentId}`
}

/**
 * @summary Get an internal document and its linked threads
 */
export const getResearchDocument = async (documentId: string, options?: Parameters<typeof customFetch>[1]): Promise<ResearchDocumentDetail> => {

  return customFetch<ResearchDocumentDetail>(getGetResearchDocumentUrl(documentId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetResearchDocumentQueryKey = (documentId: string,) => {
    return [
    `/api/research/documents/${documentId}`
    ] as const;
    }


export const getGetResearchDocumentQueryOptions = <TData = Awaited<ReturnType<typeof getResearchDocument>>, TError = Error>(documentId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getResearchDocument>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetResearchDocumentQueryKey(documentId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getResearchDocument>>> = ({ signal }) => getResearchDocument(documentId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: documentId !== null && documentId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getResearchDocument>>, TError, TData> & { queryKey: QueryKey }
}

export type GetResearchDocumentQueryResult = NonNullable<Awaited<ReturnType<typeof getResearchDocument>>>
export type GetResearchDocumentQueryError = Error


/**
 * @summary Get an internal document and its linked threads
 */

export function useGetResearchDocument<TData = Awaited<ReturnType<typeof getResearchDocument>>, TError = Error>(
 documentId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getResearchDocument>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetResearchDocumentQueryOptions(documentId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSaveResearchDocumentUrl = (documentId: string,) => {




  return `/api/research/documents/${documentId}`
}

/**
 * @summary Save Markdown using the current base revision
 */
export const saveResearchDocument = async (documentId: string,
    saveResearchDocumentBody: SaveResearchDocument, options?: Parameters<typeof customFetch>[1]): Promise<ResearchDocument> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ResearchDocument>(getSaveResearchDocumentUrl(documentId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(saveResearchDocumentBody)
  }
);}





export const getSaveResearchDocumentMutationKey = () => ['saveResearchDocument'] as const;

export const getSaveResearchDocumentMutationOptions = <TError = ResearchRevisionConflict,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveResearchDocument>>, TError,SaveResearchDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof saveResearchDocument>>, TError,SaveResearchDocumentMutationVariables, TContext> => {

const mutationKey = getSaveResearchDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof saveResearchDocument>>, SaveResearchDocumentMutationVariables> = (props) => {
          const {documentId,data} = props ?? {};

          return  saveResearchDocument(documentId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveResearchDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof saveResearchDocument>>>
    export type SaveResearchDocumentMutationBody = SaveResearchDocument
    export type SaveResearchDocumentMutationError = ResearchRevisionConflict
    export type SaveResearchDocumentMutationVariables = {documentId: string;data: SaveResearchDocument}

    /**
 * @summary Save Markdown using the current base revision
 */
export const useSaveResearchDocument = <TError = ResearchRevisionConflict,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveResearchDocument>>, TError,SaveResearchDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof saveResearchDocument>>,
        TError,
        SaveResearchDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getSaveResearchDocumentMutationOptions(options));
    }

export const getAppendResearchDocumentUrl = (documentId: string,) => {




  return `/api/research/documents/${documentId}/append`
}

/**
 * @summary Explicitly append Markdown using the current base revision
 */
export const appendResearchDocument = async (documentId: string,
    appendResearchDocumentBody: AppendResearchDocument, options?: Parameters<typeof customFetch>[1]): Promise<ResearchDocument> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ResearchDocument>(getAppendResearchDocumentUrl(documentId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(appendResearchDocumentBody)
  }
);}





export const getAppendResearchDocumentMutationKey = () => ['appendResearchDocument'] as const;

export const getAppendResearchDocumentMutationOptions = <TError = ResearchRevisionConflict,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof appendResearchDocument>>, TError,AppendResearchDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof appendResearchDocument>>, TError,AppendResearchDocumentMutationVariables, TContext> => {

const mutationKey = getAppendResearchDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof appendResearchDocument>>, AppendResearchDocumentMutationVariables> = (props) => {
          const {documentId,data} = props ?? {};

          return  appendResearchDocument(documentId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AppendResearchDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof appendResearchDocument>>>
    export type AppendResearchDocumentMutationBody = AppendResearchDocument
    export type AppendResearchDocumentMutationError = ResearchRevisionConflict
    export type AppendResearchDocumentMutationVariables = {documentId: string;data: AppendResearchDocument}

    /**
 * @summary Explicitly append Markdown using the current base revision
 */
export const useAppendResearchDocument = <TError = ResearchRevisionConflict,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof appendResearchDocument>>, TError,AppendResearchDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof appendResearchDocument>>,
        TError,
        AppendResearchDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getAppendResearchDocumentMutationOptions(options));
    }

export const getListResearchDocumentVersionsUrl = (documentId: string,
    params?: ListResearchDocumentVersionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/research/documents/${documentId}/versions?${stringifiedParams}` : `/api/research/documents/${documentId}/versions`
}

/**
 * @summary List immutable document versions
 */
export const listResearchDocumentVersions = async (documentId: string,
    params?: ListResearchDocumentVersionsParams, options?: Parameters<typeof customFetch>[1]): Promise<ResearchDocumentVersionPage> => {

  return customFetch<ResearchDocumentVersionPage>(getListResearchDocumentVersionsUrl(documentId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListResearchDocumentVersionsQueryKey = (documentId: string,
    params?: ListResearchDocumentVersionsParams,) => {
    return [
    `/api/research/documents/${documentId}/versions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListResearchDocumentVersionsQueryOptions = <TData = Awaited<ReturnType<typeof listResearchDocumentVersions>>, TError = unknown>(documentId: string,
    params?: ListResearchDocumentVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchDocumentVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListResearchDocumentVersionsQueryKey(documentId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listResearchDocumentVersions>>> = ({ signal }) => listResearchDocumentVersions(documentId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: documentId !== null && documentId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listResearchDocumentVersions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListResearchDocumentVersionsQueryResult = NonNullable<Awaited<ReturnType<typeof listResearchDocumentVersions>>>
export type ListResearchDocumentVersionsQueryError = unknown


/**
 * @summary List immutable document versions
 */

export function useListResearchDocumentVersions<TData = Awaited<ReturnType<typeof listResearchDocumentVersions>>, TError = unknown>(
 documentId: string,
    params?: ListResearchDocumentVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchDocumentVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListResearchDocumentVersionsQueryOptions(documentId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCompareResearchDocumentVersionsUrl = (documentId: string,
    params: CompareResearchDocumentVersionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/research/documents/${documentId}/versions/compare?${stringifiedParams}` : `/api/research/documents/${documentId}/versions/compare`
}

/**
 * @summary Compare two authorized immutable versions
 */
export const compareResearchDocumentVersions = async (documentId: string,
    params: CompareResearchDocumentVersionsParams, options?: Parameters<typeof customFetch>[1]): Promise<ResearchVersionComparison> => {

  return customFetch<ResearchVersionComparison>(getCompareResearchDocumentVersionsUrl(documentId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getCompareResearchDocumentVersionsQueryKey = (documentId: string,
    params?: CompareResearchDocumentVersionsParams,) => {
    return [
    `/api/research/documents/${documentId}/versions/compare`, ...(params ? [params] : [])
    ] as const;
    }


export const getCompareResearchDocumentVersionsQueryOptions = <TData = Awaited<ReturnType<typeof compareResearchDocumentVersions>>, TError = unknown>(documentId: string,
    params: CompareResearchDocumentVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof compareResearchDocumentVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getCompareResearchDocumentVersionsQueryKey(documentId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof compareResearchDocumentVersions>>> = ({ signal }) => compareResearchDocumentVersions(documentId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: documentId !== null && documentId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof compareResearchDocumentVersions>>, TError, TData> & { queryKey: QueryKey }
}

export type CompareResearchDocumentVersionsQueryResult = NonNullable<Awaited<ReturnType<typeof compareResearchDocumentVersions>>>
export type CompareResearchDocumentVersionsQueryError = unknown


/**
 * @summary Compare two authorized immutable versions
 */

export function useCompareResearchDocumentVersions<TData = Awaited<ReturnType<typeof compareResearchDocumentVersions>>, TError = unknown>(
 documentId: string,
    params: CompareResearchDocumentVersionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof compareResearchDocumentVersions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getCompareResearchDocumentVersionsQueryOptions(documentId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRestoreResearchDocumentVersionUrl = (documentId: string,
    versionId: string,) => {




  return `/api/research/documents/${documentId}/versions/${versionId}/restore`
}

/**
 * @summary Restore an old version as a new current version
 */
export const restoreResearchDocumentVersion = async (documentId: string,
    versionId: string,
    restoreResearchDocument: RestoreResearchDocument, options?: Parameters<typeof customFetch>[1]): Promise<ResearchDocument> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ResearchDocument>(getRestoreResearchDocumentVersionUrl(documentId,versionId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(restoreResearchDocument)
  }
);}





export const getRestoreResearchDocumentVersionMutationKey = () => ['restoreResearchDocumentVersion'] as const;

export const getRestoreResearchDocumentVersionMutationOptions = <TError = ResearchRevisionConflict,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof restoreResearchDocumentVersion>>, TError,RestoreResearchDocumentVersionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof restoreResearchDocumentVersion>>, TError,RestoreResearchDocumentVersionMutationVariables, TContext> => {

const mutationKey = getRestoreResearchDocumentVersionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof restoreResearchDocumentVersion>>, RestoreResearchDocumentVersionMutationVariables> = (props) => {
          const {documentId,versionId,data} = props ?? {};

          return  restoreResearchDocumentVersion(documentId,versionId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RestoreResearchDocumentVersionMutationResult = NonNullable<Awaited<ReturnType<typeof restoreResearchDocumentVersion>>>
    export type RestoreResearchDocumentVersionMutationBody = RestoreResearchDocument
    export type RestoreResearchDocumentVersionMutationError = ResearchRevisionConflict
    export type RestoreResearchDocumentVersionMutationVariables = {documentId: string;versionId: string;data: RestoreResearchDocument}

    /**
 * @summary Restore an old version as a new current version
 */
export const useRestoreResearchDocumentVersion = <TError = ResearchRevisionConflict,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof restoreResearchDocumentVersion>>, TError,RestoreResearchDocumentVersionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof restoreResearchDocumentVersion>>,
        TError,
        RestoreResearchDocumentVersionMutationVariables,
        TContext
      > => {
      return useMutation(getRestoreResearchDocumentVersionMutationOptions(options));
    }

export const getListResearchDocumentThreadsUrl = (documentId: string,) => {




  return `/api/research/documents/${documentId}/threads`
}

/**
 * @summary List threads linked to an internal document
 */
export const listResearchDocumentThreads = async (documentId: string, options?: Parameters<typeof customFetch>[1]): Promise<ResearchThread[]> => {

  return customFetch<ResearchThread[]>(getListResearchDocumentThreadsUrl(documentId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListResearchDocumentThreadsQueryKey = (documentId: string,) => {
    return [
    `/api/research/documents/${documentId}/threads`
    ] as const;
    }


export const getListResearchDocumentThreadsQueryOptions = <TData = Awaited<ReturnType<typeof listResearchDocumentThreads>>, TError = unknown>(documentId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchDocumentThreads>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListResearchDocumentThreadsQueryKey(documentId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listResearchDocumentThreads>>> = ({ signal }) => listResearchDocumentThreads(documentId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: documentId !== null && documentId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listResearchDocumentThreads>>, TError, TData> & { queryKey: QueryKey }
}

export type ListResearchDocumentThreadsQueryResult = NonNullable<Awaited<ReturnType<typeof listResearchDocumentThreads>>>
export type ListResearchDocumentThreadsQueryError = unknown


/**
 * @summary List threads linked to an internal document
 */

export function useListResearchDocumentThreads<TData = Awaited<ReturnType<typeof listResearchDocumentThreads>>, TError = unknown>(
 documentId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchDocumentThreads>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListResearchDocumentThreadsQueryOptions(documentId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getLinkResearchDocumentThreadUrl = (documentId: string,) => {




  return `/api/research/documents/${documentId}/threads`
}

/**
 * @summary Link another entity thread to a document
 */
export const linkResearchDocumentThread = async (documentId: string,
    researchDocumentThreadLink: ResearchDocumentThreadLink, options?: Parameters<typeof customFetch>[1]): Promise<ResearchDocumentThreadLinkResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ResearchDocumentThreadLinkResult>(getLinkResearchDocumentThreadUrl(documentId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(researchDocumentThreadLink)
  }
);}





export const getLinkResearchDocumentThreadMutationKey = () => ['linkResearchDocumentThread'] as const;

export const getLinkResearchDocumentThreadMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof linkResearchDocumentThread>>, TError,LinkResearchDocumentThreadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof linkResearchDocumentThread>>, TError,LinkResearchDocumentThreadMutationVariables, TContext> => {

const mutationKey = getLinkResearchDocumentThreadMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof linkResearchDocumentThread>>, LinkResearchDocumentThreadMutationVariables> = (props) => {
          const {documentId,data} = props ?? {};

          return  linkResearchDocumentThread(documentId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type LinkResearchDocumentThreadMutationResult = NonNullable<Awaited<ReturnType<typeof linkResearchDocumentThread>>>
    export type LinkResearchDocumentThreadMutationBody = ResearchDocumentThreadLink
    export type LinkResearchDocumentThreadMutationError = unknown
    export type LinkResearchDocumentThreadMutationVariables = {documentId: string;data: ResearchDocumentThreadLink}

    /**
 * @summary Link another entity thread to a document
 */
export const useLinkResearchDocumentThread = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof linkResearchDocumentThread>>, TError,LinkResearchDocumentThreadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof linkResearchDocumentThread>>,
        TError,
        LinkResearchDocumentThreadMutationVariables,
        TContext
      > => {
      return useMutation(getLinkResearchDocumentThreadMutationOptions(options));
    }

export const getUnlinkResearchDocumentThreadUrl = (documentId: string,
    threadId: string,) => {




  return `/api/research/documents/${documentId}/threads/${threadId}`
}

/**
 * @summary Remove a document/thread link without deleting either record
 */
export const unlinkResearchDocumentThread = async (documentId: string,
    threadId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getUnlinkResearchDocumentThreadUrl(documentId,threadId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getUnlinkResearchDocumentThreadMutationKey = () => ['unlinkResearchDocumentThread'] as const;

export const getUnlinkResearchDocumentThreadMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unlinkResearchDocumentThread>>, TError,UnlinkResearchDocumentThreadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof unlinkResearchDocumentThread>>, TError,UnlinkResearchDocumentThreadMutationVariables, TContext> => {

const mutationKey = getUnlinkResearchDocumentThreadMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof unlinkResearchDocumentThread>>, UnlinkResearchDocumentThreadMutationVariables> = (props) => {
          const {documentId,threadId} = props ?? {};

          return  unlinkResearchDocumentThread(documentId,threadId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UnlinkResearchDocumentThreadMutationResult = NonNullable<Awaited<ReturnType<typeof unlinkResearchDocumentThread>>>

    export type UnlinkResearchDocumentThreadMutationError = unknown
    export type UnlinkResearchDocumentThreadMutationVariables = {documentId: string;threadId: string}

    /**
 * @summary Remove a document/thread link without deleting either record
 */
export const useUnlinkResearchDocumentThread = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unlinkResearchDocumentThread>>, TError,UnlinkResearchDocumentThreadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof unlinkResearchDocumentThread>>,
        TError,
        UnlinkResearchDocumentThreadMutationVariables,
        TContext
      > => {
      return useMutation(getUnlinkResearchDocumentThreadMutationOptions(options));
    }

export const getListResearchDocumentProposalsUrl = (documentId: string,
    params?: ListResearchDocumentProposalsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/research/documents/${documentId}/proposals?${stringifiedParams}` : `/api/research/documents/${documentId}/proposals`
}

/**
 * @summary List reviewable document proposals
 */
export const listResearchDocumentProposals = async (documentId: string,
    params?: ListResearchDocumentProposalsParams, options?: Parameters<typeof customFetch>[1]): Promise<ResearchProposalPage> => {

  return customFetch<ResearchProposalPage>(getListResearchDocumentProposalsUrl(documentId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListResearchDocumentProposalsQueryKey = (documentId: string,
    params?: ListResearchDocumentProposalsParams,) => {
    return [
    `/api/research/documents/${documentId}/proposals`, ...(params ? [params] : [])
    ] as const;
    }


export const getListResearchDocumentProposalsQueryOptions = <TData = Awaited<ReturnType<typeof listResearchDocumentProposals>>, TError = unknown>(documentId: string,
    params?: ListResearchDocumentProposalsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchDocumentProposals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListResearchDocumentProposalsQueryKey(documentId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listResearchDocumentProposals>>> = ({ signal }) => listResearchDocumentProposals(documentId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: documentId !== null && documentId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listResearchDocumentProposals>>, TError, TData> & { queryKey: QueryKey }
}

export type ListResearchDocumentProposalsQueryResult = NonNullable<Awaited<ReturnType<typeof listResearchDocumentProposals>>>
export type ListResearchDocumentProposalsQueryError = unknown


/**
 * @summary List reviewable document proposals
 */

export function useListResearchDocumentProposals<TData = Awaited<ReturnType<typeof listResearchDocumentProposals>>, TError = unknown>(
 documentId: string,
    params?: ListResearchDocumentProposalsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchDocumentProposals>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListResearchDocumentProposalsQueryOptions(documentId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateResearchDocumentProposalUrl = (documentId: string,) => {




  return `/api/research/documents/${documentId}/proposals`
}

/**
 * Requires entity.ai.use in addition to entity record write access.
 * @summary Generate a non-mutating AI proposal bound to a revision and selection fingerprint
 */
export const createResearchDocumentProposal = async (documentId: string,
    createResearchProposal: CreateResearchProposal, options?: Parameters<typeof customFetch>[1]): Promise<ResearchProposal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ResearchProposal>(getCreateResearchDocumentProposalUrl(documentId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createResearchProposal)
  }
);}





export const getCreateResearchDocumentProposalMutationKey = () => ['createResearchDocumentProposal'] as const;

export const getCreateResearchDocumentProposalMutationOptions = <TError = ResearchRevisionConflict,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createResearchDocumentProposal>>, TError,CreateResearchDocumentProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createResearchDocumentProposal>>, TError,CreateResearchDocumentProposalMutationVariables, TContext> => {

const mutationKey = getCreateResearchDocumentProposalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createResearchDocumentProposal>>, CreateResearchDocumentProposalMutationVariables> = (props) => {
          const {documentId,data} = props ?? {};

          return  createResearchDocumentProposal(documentId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateResearchDocumentProposalMutationResult = NonNullable<Awaited<ReturnType<typeof createResearchDocumentProposal>>>
    export type CreateResearchDocumentProposalMutationBody = CreateResearchProposal
    export type CreateResearchDocumentProposalMutationError = ResearchRevisionConflict
    export type CreateResearchDocumentProposalMutationVariables = {documentId: string;data: CreateResearchProposal}

    /**
 * @summary Generate a non-mutating AI proposal bound to a revision and selection fingerprint
 */
export const useCreateResearchDocumentProposal = <TError = ResearchRevisionConflict,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createResearchDocumentProposal>>, TError,CreateResearchDocumentProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createResearchDocumentProposal>>,
        TError,
        CreateResearchDocumentProposalMutationVariables,
        TContext
      > => {
      return useMutation(getCreateResearchDocumentProposalMutationOptions(options));
    }

export const getAcceptResearchDocumentProposalUrl = (documentId: string,
    proposalId: string,) => {




  return `/api/research/documents/${documentId}/proposals/${proposalId}/accept`
}

/**
 * @summary Accept a still-current proposal and create a new immutable version
 */
export const acceptResearchDocumentProposal = async (documentId: string,
    proposalId: string,
    acceptResearchProposal: AcceptResearchProposal, options?: Parameters<typeof customFetch>[1]): Promise<ResearchDocument> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ResearchDocument>(getAcceptResearchDocumentProposalUrl(documentId,proposalId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(acceptResearchProposal)
  }
);}





export const getAcceptResearchDocumentProposalMutationKey = () => ['acceptResearchDocumentProposal'] as const;

export const getAcceptResearchDocumentProposalMutationOptions = <TError = ResearchRevisionConflict,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof acceptResearchDocumentProposal>>, TError,AcceptResearchDocumentProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof acceptResearchDocumentProposal>>, TError,AcceptResearchDocumentProposalMutationVariables, TContext> => {

const mutationKey = getAcceptResearchDocumentProposalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof acceptResearchDocumentProposal>>, AcceptResearchDocumentProposalMutationVariables> = (props) => {
          const {documentId,proposalId,data} = props ?? {};

          return  acceptResearchDocumentProposal(documentId,proposalId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AcceptResearchDocumentProposalMutationResult = NonNullable<Awaited<ReturnType<typeof acceptResearchDocumentProposal>>>
    export type AcceptResearchDocumentProposalMutationBody = AcceptResearchProposal
    export type AcceptResearchDocumentProposalMutationError = ResearchRevisionConflict
    export type AcceptResearchDocumentProposalMutationVariables = {documentId: string;proposalId: string;data: AcceptResearchProposal}

    /**
 * @summary Accept a still-current proposal and create a new immutable version
 */
export const useAcceptResearchDocumentProposal = <TError = ResearchRevisionConflict,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof acceptResearchDocumentProposal>>, TError,AcceptResearchDocumentProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof acceptResearchDocumentProposal>>,
        TError,
        AcceptResearchDocumentProposalMutationVariables,
        TContext
      > => {
      return useMutation(getAcceptResearchDocumentProposalMutationOptions(options));
    }

export const getRejectResearchDocumentProposalUrl = (documentId: string,
    proposalId: string,) => {




  return `/api/research/documents/${documentId}/proposals/${proposalId}/reject`
}

/**
 * @summary Reject a pending proposal without changing the document
 */
export const rejectResearchDocumentProposal = async (documentId: string,
    proposalId: string, options?: Parameters<typeof customFetch>[1]): Promise<ResearchProposal> => {

  return customFetch<ResearchProposal>(getRejectResearchDocumentProposalUrl(documentId,proposalId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRejectResearchDocumentProposalMutationKey = () => ['rejectResearchDocumentProposal'] as const;

export const getRejectResearchDocumentProposalMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rejectResearchDocumentProposal>>, TError,RejectResearchDocumentProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof rejectResearchDocumentProposal>>, TError,RejectResearchDocumentProposalMutationVariables, TContext> => {

const mutationKey = getRejectResearchDocumentProposalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof rejectResearchDocumentProposal>>, RejectResearchDocumentProposalMutationVariables> = (props) => {
          const {documentId,proposalId} = props ?? {};

          return  rejectResearchDocumentProposal(documentId,proposalId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RejectResearchDocumentProposalMutationResult = NonNullable<Awaited<ReturnType<typeof rejectResearchDocumentProposal>>>

    export type RejectResearchDocumentProposalMutationError = unknown
    export type RejectResearchDocumentProposalMutationVariables = {documentId: string;proposalId: string}

    /**
 * @summary Reject a pending proposal without changing the document
 */
export const useRejectResearchDocumentProposal = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof rejectResearchDocumentProposal>>, TError,RejectResearchDocumentProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof rejectResearchDocumentProposal>>,
        TError,
        RejectResearchDocumentProposalMutationVariables,
        TContext
      > => {
      return useMutation(getRejectResearchDocumentProposalMutationOptions(options));
    }

export const getGetSalesFloorTwinDefinitionUrl = () => {




  return `/api/sales-floor-twin/definition`
}

/**
 * Requires entity.records.read.
 * @summary Get the validated sales-floor process definition
 */
export const getSalesFloorTwinDefinition = async ( options?: Parameters<typeof customFetch>[1]): Promise<SalesFloorTwinDefinition> => {

  return customFetch<SalesFloorTwinDefinition>(getGetSalesFloorTwinDefinitionUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSalesFloorTwinDefinitionQueryKey = () => {
    return [
    `/api/sales-floor-twin/definition`
    ] as const;
    }


export const getGetSalesFloorTwinDefinitionQueryOptions = <TData = Awaited<ReturnType<typeof getSalesFloorTwinDefinition>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSalesFloorTwinDefinition>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSalesFloorTwinDefinitionQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSalesFloorTwinDefinition>>> = ({ signal }) => getSalesFloorTwinDefinition({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSalesFloorTwinDefinition>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSalesFloorTwinDefinitionQueryResult = NonNullable<Awaited<ReturnType<typeof getSalesFloorTwinDefinition>>>
export type GetSalesFloorTwinDefinitionQueryError = Error


/**
 * @summary Get the validated sales-floor process definition
 */

export function useGetSalesFloorTwinDefinition<TData = Awaited<ReturnType<typeof getSalesFloorTwinDefinition>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSalesFloorTwinDefinition>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSalesFloorTwinDefinitionQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetSalesFloorTwinSnapshotUrl = (params?: GetSalesFloorTwinSnapshotParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/sales-floor-twin/snapshot?${stringifiedParams}` : `/api/sales-floor-twin/snapshot`
}

/**
 * Requires entity.records.read. The response is capped at 400 enquiry tokens and 200 events.
 * @summary Get a bounded operational snapshot of enquiries and linked opportunities
 */
export const getSalesFloorTwinSnapshot = async (params?: GetSalesFloorTwinSnapshotParams, options?: Parameters<typeof customFetch>[1]): Promise<SalesFloorTwinSnapshot> => {

  return customFetch<SalesFloorTwinSnapshot>(getGetSalesFloorTwinSnapshotUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSalesFloorTwinSnapshotQueryKey = (params?: GetSalesFloorTwinSnapshotParams,) => {
    return [
    `/api/sales-floor-twin/snapshot`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetSalesFloorTwinSnapshotQueryOptions = <TData = Awaited<ReturnType<typeof getSalesFloorTwinSnapshot>>, TError = Error>(params?: GetSalesFloorTwinSnapshotParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSalesFloorTwinSnapshot>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSalesFloorTwinSnapshotQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSalesFloorTwinSnapshot>>> = ({ signal }) => getSalesFloorTwinSnapshot(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSalesFloorTwinSnapshot>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSalesFloorTwinSnapshotQueryResult = NonNullable<Awaited<ReturnType<typeof getSalesFloorTwinSnapshot>>>
export type GetSalesFloorTwinSnapshotQueryError = Error


/**
 * @summary Get a bounded operational snapshot of enquiries and linked opportunities
 */

export function useGetSalesFloorTwinSnapshot<TData = Awaited<ReturnType<typeof getSalesFloorTwinSnapshot>>, TError = Error>(
 params?: GetSalesFloorTwinSnapshotParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSalesFloorTwinSnapshot>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSalesFloorTwinSnapshotQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListGeographicProvidersUrl = () => {




  return `/api/geography/providers`
}

/**
 * @summary List safe basemap and address-lookup readiness
 */
export const listGeographicProviders = async ( options?: Parameters<typeof customFetch>[1]): Promise<GeographicProviderList> => {

  return customFetch<GeographicProviderList>(getListGeographicProvidersUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListGeographicProvidersQueryKey = () => {
    return [
    `/api/geography/providers`
    ] as const;
    }


export const getListGeographicProvidersQueryOptions = <TData = Awaited<ReturnType<typeof listGeographicProviders>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listGeographicProviders>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListGeographicProvidersQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listGeographicProviders>>> = ({ signal }) => listGeographicProviders({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listGeographicProviders>>, TError, TData> & { queryKey: QueryKey }
}

export type ListGeographicProvidersQueryResult = NonNullable<Awaited<ReturnType<typeof listGeographicProviders>>>
export type ListGeographicProvidersQueryError = unknown


/**
 * @summary List safe basemap and address-lookup readiness
 */

export function useListGeographicProviders<TData = Awaited<ReturnType<typeof listGeographicProviders>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listGeographicProviders>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListGeographicProvidersQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetAzureGeographicBasemapTileUrl = (z: number,
    x: number,
    y: number,) => {




  return `/api/geography/basemaps/azure/${z}/${x}/${y}`
}

/**
 * @summary Proxy one authorized Azure Maps viewport tile without exposing credentials
 */
export const getAzureGeographicBasemapTile = async (z: number,
    x: number,
    y: number, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getGetAzureGeographicBasemapTileUrl(z,x,y),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAzureGeographicBasemapTileQueryKey = (z: number,
    x: number,
    y: number,) => {
    return [
    `/api/geography/basemaps/azure/${z}/${x}/${y}`
    ] as const;
    }


export const getGetAzureGeographicBasemapTileQueryOptions = <TData = Awaited<ReturnType<typeof getAzureGeographicBasemapTile>>, TError = Error>(z: number,
    x: number,
    y: number, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAzureGeographicBasemapTile>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAzureGeographicBasemapTileQueryKey(z,x,y);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAzureGeographicBasemapTile>>> = ({ signal }) => getAzureGeographicBasemapTile(z,x,y, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: z !== null && z !== undefined && x !== null && x !== undefined && y !== null && y !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAzureGeographicBasemapTile>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAzureGeographicBasemapTileQueryResult = NonNullable<Awaited<ReturnType<typeof getAzureGeographicBasemapTile>>>
export type GetAzureGeographicBasemapTileQueryError = Error


/**
 * @summary Proxy one authorized Azure Maps viewport tile without exposing credentials
 */

export function useGetAzureGeographicBasemapTile<TData = Awaited<ReturnType<typeof getAzureGeographicBasemapTile>>, TError = Error>(
 z: number,
    x: number,
    y: number, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAzureGeographicBasemapTile>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAzureGeographicBasemapTileQueryOptions(z,x,y,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListGeographicLayersUrl = (params?: ListGeographicLayersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/geography/layers?${stringifiedParams}` : `/api/geography/layers`
}

export const listGeographicLayers = async (params?: ListGeographicLayersParams, options?: Parameters<typeof customFetch>[1]): Promise<GeographicLayerPage> => {

  return customFetch<GeographicLayerPage>(getListGeographicLayersUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListGeographicLayersQueryKey = (params?: ListGeographicLayersParams,) => {
    return [
    `/api/geography/layers`, ...(params ? [params] : [])
    ] as const;
    }


export const getListGeographicLayersQueryOptions = <TData = Awaited<ReturnType<typeof listGeographicLayers>>, TError = unknown>(params?: ListGeographicLayersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listGeographicLayers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListGeographicLayersQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listGeographicLayers>>> = ({ signal }) => listGeographicLayers(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listGeographicLayers>>, TError, TData> & { queryKey: QueryKey }
}

export type ListGeographicLayersQueryResult = NonNullable<Awaited<ReturnType<typeof listGeographicLayers>>>
export type ListGeographicLayersQueryError = unknown



export function useListGeographicLayers<TData = Awaited<ReturnType<typeof listGeographicLayers>>, TError = unknown>(
 params?: ListGeographicLayersParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listGeographicLayers>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListGeographicLayersQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateGeographicLayerUrl = () => {




  return `/api/geography/layers`
}

export const createGeographicLayer = async (geographicLayerWrite: GeographicLayerWrite, options?: Parameters<typeof customFetch>[1]): Promise<GeographicLayer> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<GeographicLayer>(getCreateGeographicLayerUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(geographicLayerWrite)
  }
);}





export const getCreateGeographicLayerMutationKey = () => ['createGeographicLayer'] as const;

export const getCreateGeographicLayerMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createGeographicLayer>>, TError,CreateGeographicLayerMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createGeographicLayer>>, TError,CreateGeographicLayerMutationVariables, TContext> => {

const mutationKey = getCreateGeographicLayerMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createGeographicLayer>>, CreateGeographicLayerMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createGeographicLayer(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateGeographicLayerMutationResult = NonNullable<Awaited<ReturnType<typeof createGeographicLayer>>>
    export type CreateGeographicLayerMutationBody = GeographicLayerWrite
    export type CreateGeographicLayerMutationError = unknown
    export type CreateGeographicLayerMutationVariables = {data: GeographicLayerWrite}

    export const useCreateGeographicLayer = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createGeographicLayer>>, TError,CreateGeographicLayerMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createGeographicLayer>>,
        TError,
        CreateGeographicLayerMutationVariables,
        TContext
      > => {
      return useMutation(getCreateGeographicLayerMutationOptions(options));
    }

export const getUpdateGeographicLayerUrl = (id: string,) => {




  return `/api/geography/layers/${id}`
}

export const updateGeographicLayer = async (id: string,
    geographicLayerUpdate: GeographicLayerUpdate, options?: Parameters<typeof customFetch>[1]): Promise<GeographicLayer> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<GeographicLayer>(getUpdateGeographicLayerUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(geographicLayerUpdate)
  }
);}





export const getUpdateGeographicLayerMutationKey = () => ['updateGeographicLayer'] as const;

export const getUpdateGeographicLayerMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateGeographicLayer>>, TError,UpdateGeographicLayerMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateGeographicLayer>>, TError,UpdateGeographicLayerMutationVariables, TContext> => {

const mutationKey = getUpdateGeographicLayerMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateGeographicLayer>>, UpdateGeographicLayerMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateGeographicLayer(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateGeographicLayerMutationResult = NonNullable<Awaited<ReturnType<typeof updateGeographicLayer>>>
    export type UpdateGeographicLayerMutationBody = GeographicLayerUpdate
    export type UpdateGeographicLayerMutationError = Error
    export type UpdateGeographicLayerMutationVariables = {id: string;data: GeographicLayerUpdate}

    export const useUpdateGeographicLayer = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateGeographicLayer>>, TError,UpdateGeographicLayerMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateGeographicLayer>>,
        TError,
        UpdateGeographicLayerMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateGeographicLayerMutationOptions(options));
    }

export const getDeleteGeographicLayerUrl = (id: string,
    params: DeleteGeographicLayerParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/geography/layers/${id}?${stringifiedParams}` : `/api/geography/layers/${id}`
}

export const deleteGeographicLayer = async (id: string,
    params: DeleteGeographicLayerParams, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteGeographicLayerUrl(id,params),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteGeographicLayerMutationKey = () => ['deleteGeographicLayer'] as const;

export const getDeleteGeographicLayerMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteGeographicLayer>>, TError,DeleteGeographicLayerMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteGeographicLayer>>, TError,DeleteGeographicLayerMutationVariables, TContext> => {

const mutationKey = getDeleteGeographicLayerMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteGeographicLayer>>, DeleteGeographicLayerMutationVariables> = (props) => {
          const {id,params} = props ?? {};

          return  deleteGeographicLayer(id,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteGeographicLayerMutationResult = NonNullable<Awaited<ReturnType<typeof deleteGeographicLayer>>>

    export type DeleteGeographicLayerMutationError = unknown
    export type DeleteGeographicLayerMutationVariables = {id: string;params: DeleteGeographicLayerParams}

    export const useDeleteGeographicLayer = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteGeographicLayer>>, TError,DeleteGeographicLayerMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteGeographicLayer>>,
        TError,
        DeleteGeographicLayerMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteGeographicLayerMutationOptions(options));
    }

export const getListGeographicFeaturesUrl = (params?: ListGeographicFeaturesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/geography/features?${stringifiedParams}` : `/api/geography/features`
}

export const listGeographicFeatures = async (params?: ListGeographicFeaturesParams, options?: Parameters<typeof customFetch>[1]): Promise<GeographicFeaturePage> => {

  return customFetch<GeographicFeaturePage>(getListGeographicFeaturesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListGeographicFeaturesQueryKey = (params?: ListGeographicFeaturesParams,) => {
    return [
    `/api/geography/features`, ...(params ? [params] : [])
    ] as const;
    }


export const getListGeographicFeaturesQueryOptions = <TData = Awaited<ReturnType<typeof listGeographicFeatures>>, TError = unknown>(params?: ListGeographicFeaturesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listGeographicFeatures>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListGeographicFeaturesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listGeographicFeatures>>> = ({ signal }) => listGeographicFeatures(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listGeographicFeatures>>, TError, TData> & { queryKey: QueryKey }
}

export type ListGeographicFeaturesQueryResult = NonNullable<Awaited<ReturnType<typeof listGeographicFeatures>>>
export type ListGeographicFeaturesQueryError = unknown



export function useListGeographicFeatures<TData = Awaited<ReturnType<typeof listGeographicFeatures>>, TError = unknown>(
 params?: ListGeographicFeaturesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listGeographicFeatures>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListGeographicFeaturesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateGeographicFeatureUrl = () => {




  return `/api/geography/features`
}

export const createGeographicFeature = async (geographicFeatureWrite: GeographicFeatureWrite, options?: Parameters<typeof customFetch>[1]): Promise<GeographicFeature> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<GeographicFeature>(getCreateGeographicFeatureUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(geographicFeatureWrite)
  }
);}





export const getCreateGeographicFeatureMutationKey = () => ['createGeographicFeature'] as const;

export const getCreateGeographicFeatureMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createGeographicFeature>>, TError,CreateGeographicFeatureMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createGeographicFeature>>, TError,CreateGeographicFeatureMutationVariables, TContext> => {

const mutationKey = getCreateGeographicFeatureMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createGeographicFeature>>, CreateGeographicFeatureMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createGeographicFeature(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateGeographicFeatureMutationResult = NonNullable<Awaited<ReturnType<typeof createGeographicFeature>>>
    export type CreateGeographicFeatureMutationBody = GeographicFeatureWrite
    export type CreateGeographicFeatureMutationError = unknown
    export type CreateGeographicFeatureMutationVariables = {data: GeographicFeatureWrite}

    export const useCreateGeographicFeature = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createGeographicFeature>>, TError,CreateGeographicFeatureMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createGeographicFeature>>,
        TError,
        CreateGeographicFeatureMutationVariables,
        TContext
      > => {
      return useMutation(getCreateGeographicFeatureMutationOptions(options));
    }

export const getGetGeographicFeatureUrl = (id: string,) => {




  return `/api/geography/features/${id}`
}

export const getGeographicFeature = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<GeographicFeature> => {

  return customFetch<GeographicFeature>(getGetGeographicFeatureUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetGeographicFeatureQueryKey = (id: string,) => {
    return [
    `/api/geography/features/${id}`
    ] as const;
    }


export const getGetGeographicFeatureQueryOptions = <TData = Awaited<ReturnType<typeof getGeographicFeature>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGeographicFeature>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetGeographicFeatureQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getGeographicFeature>>> = ({ signal }) => getGeographicFeature(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getGeographicFeature>>, TError, TData> & { queryKey: QueryKey }
}

export type GetGeographicFeatureQueryResult = NonNullable<Awaited<ReturnType<typeof getGeographicFeature>>>
export type GetGeographicFeatureQueryError = unknown



export function useGetGeographicFeature<TData = Awaited<ReturnType<typeof getGeographicFeature>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGeographicFeature>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetGeographicFeatureQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateGeographicFeatureUrl = (id: string,) => {




  return `/api/geography/features/${id}`
}

export const updateGeographicFeature = async (id: string,
    geographicFeatureUpdate: GeographicFeatureUpdate, options?: Parameters<typeof customFetch>[1]): Promise<GeographicFeature> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<GeographicFeature>(getUpdateGeographicFeatureUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(geographicFeatureUpdate)
  }
);}





export const getUpdateGeographicFeatureMutationKey = () => ['updateGeographicFeature'] as const;

export const getUpdateGeographicFeatureMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateGeographicFeature>>, TError,UpdateGeographicFeatureMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateGeographicFeature>>, TError,UpdateGeographicFeatureMutationVariables, TContext> => {

const mutationKey = getUpdateGeographicFeatureMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateGeographicFeature>>, UpdateGeographicFeatureMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateGeographicFeature(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateGeographicFeatureMutationResult = NonNullable<Awaited<ReturnType<typeof updateGeographicFeature>>>
    export type UpdateGeographicFeatureMutationBody = GeographicFeatureUpdate
    export type UpdateGeographicFeatureMutationError = Error
    export type UpdateGeographicFeatureMutationVariables = {id: string;data: GeographicFeatureUpdate}

    export const useUpdateGeographicFeature = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateGeographicFeature>>, TError,UpdateGeographicFeatureMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateGeographicFeature>>,
        TError,
        UpdateGeographicFeatureMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateGeographicFeatureMutationOptions(options));
    }

export const getDeleteGeographicFeatureUrl = (id: string,
    params: DeleteGeographicFeatureParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/geography/features/${id}?${stringifiedParams}` : `/api/geography/features/${id}`
}

export const deleteGeographicFeature = async (id: string,
    params: DeleteGeographicFeatureParams, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteGeographicFeatureUrl(id,params),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteGeographicFeatureMutationKey = () => ['deleteGeographicFeature'] as const;

export const getDeleteGeographicFeatureMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteGeographicFeature>>, TError,DeleteGeographicFeatureMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteGeographicFeature>>, TError,DeleteGeographicFeatureMutationVariables, TContext> => {

const mutationKey = getDeleteGeographicFeatureMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteGeographicFeature>>, DeleteGeographicFeatureMutationVariables> = (props) => {
          const {id,params} = props ?? {};

          return  deleteGeographicFeature(id,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteGeographicFeatureMutationResult = NonNullable<Awaited<ReturnType<typeof deleteGeographicFeature>>>

    export type DeleteGeographicFeatureMutationError = unknown
    export type DeleteGeographicFeatureMutationVariables = {id: string;params: DeleteGeographicFeatureParams}

    export const useDeleteGeographicFeature = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteGeographicFeature>>, TError,DeleteGeographicFeatureMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteGeographicFeature>>,
        TError,
        DeleteGeographicFeatureMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteGeographicFeatureMutationOptions(options));
    }

export const getGetGeographicAddressResolutionUrl = (recordType: 'account' | 'contact',
    recordId: string,) => {




  return `/api/geography/address-resolutions/${recordType}/${recordId}`
}

export const getGeographicAddressResolution = async (recordType: 'account' | 'contact',
    recordId: string, options?: Parameters<typeof customFetch>[1]): Promise<GeographicAddressResolution> => {

  return customFetch<GeographicAddressResolution>(getGetGeographicAddressResolutionUrl(recordType,recordId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetGeographicAddressResolutionQueryKey = (recordType: 'account' | 'contact',
    recordId: string,) => {
    return [
    `/api/geography/address-resolutions/${recordType}/${recordId}`
    ] as const;
    }


export const getGetGeographicAddressResolutionQueryOptions = <TData = Awaited<ReturnType<typeof getGeographicAddressResolution>>, TError = unknown>(recordType: 'account' | 'contact',
    recordId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGeographicAddressResolution>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetGeographicAddressResolutionQueryKey(recordType,recordId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getGeographicAddressResolution>>> = ({ signal }) => getGeographicAddressResolution(recordType,recordId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: recordType !== null && recordType !== undefined && recordId !== null && recordId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getGeographicAddressResolution>>, TError, TData> & { queryKey: QueryKey }
}

export type GetGeographicAddressResolutionQueryResult = NonNullable<Awaited<ReturnType<typeof getGeographicAddressResolution>>>
export type GetGeographicAddressResolutionQueryError = unknown



export function useGetGeographicAddressResolution<TData = Awaited<ReturnType<typeof getGeographicAddressResolution>>, TError = unknown>(
 recordType: 'account' | 'contact',
    recordId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGeographicAddressResolution>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetGeographicAddressResolutionQueryOptions(recordType,recordId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCorrectGeographicAddressResolutionUrl = (recordType: 'account' | 'contact',
    recordId: string,) => {




  return `/api/geography/address-resolutions/${recordType}/${recordId}`
}

export const correctGeographicAddressResolution = async (recordType: 'account' | 'contact',
    recordId: string,
    geographicAddressCorrection: GeographicAddressCorrection, options?: Parameters<typeof customFetch>[1]): Promise<GeographicAddressResolution> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<GeographicAddressResolution>(getCorrectGeographicAddressResolutionUrl(recordType,recordId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(geographicAddressCorrection)
  }
);}





export const getCorrectGeographicAddressResolutionMutationKey = () => ['correctGeographicAddressResolution'] as const;

export const getCorrectGeographicAddressResolutionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof correctGeographicAddressResolution>>, TError,CorrectGeographicAddressResolutionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof correctGeographicAddressResolution>>, TError,CorrectGeographicAddressResolutionMutationVariables, TContext> => {

const mutationKey = getCorrectGeographicAddressResolutionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof correctGeographicAddressResolution>>, CorrectGeographicAddressResolutionMutationVariables> = (props) => {
          const {recordType,recordId,data} = props ?? {};

          return  correctGeographicAddressResolution(recordType,recordId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CorrectGeographicAddressResolutionMutationResult = NonNullable<Awaited<ReturnType<typeof correctGeographicAddressResolution>>>
    export type CorrectGeographicAddressResolutionMutationBody = GeographicAddressCorrection
    export type CorrectGeographicAddressResolutionMutationError = unknown
    export type CorrectGeographicAddressResolutionMutationVariables = {recordType: 'account' | 'contact';recordId: string;data: GeographicAddressCorrection}

    export const useCorrectGeographicAddressResolution = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof correctGeographicAddressResolution>>, TError,CorrectGeographicAddressResolutionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof correctGeographicAddressResolution>>,
        TError,
        CorrectGeographicAddressResolutionMutationVariables,
        TContext
      > => {
      return useMutation(getCorrectGeographicAddressResolutionMutationOptions(options));
    }

export const getStartGeographicAddressLookupUrl = () => {




  return `/api/geography/address-lookups`
}

/**
 * @summary Explicitly request a bounded Azure lookup after separate setup
 */
export const startGeographicAddressLookup = async (geographicAddressLookupRequest: GeographicAddressLookupRequest, options?: Parameters<typeof customFetch>[1]): Promise<GeographicAddressLookupJob> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<GeographicAddressLookupJob>(getStartGeographicAddressLookupUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(geographicAddressLookupRequest)
  }
);}





export const getStartGeographicAddressLookupMutationKey = () => ['startGeographicAddressLookup'] as const;

export const getStartGeographicAddressLookupMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startGeographicAddressLookup>>, TError,StartGeographicAddressLookupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof startGeographicAddressLookup>>, TError,StartGeographicAddressLookupMutationVariables, TContext> => {

const mutationKey = getStartGeographicAddressLookupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof startGeographicAddressLookup>>, StartGeographicAddressLookupMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  startGeographicAddressLookup(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type StartGeographicAddressLookupMutationResult = NonNullable<Awaited<ReturnType<typeof startGeographicAddressLookup>>>
    export type StartGeographicAddressLookupMutationBody = GeographicAddressLookupRequest
    export type StartGeographicAddressLookupMutationError = Error
    export type StartGeographicAddressLookupMutationVariables = {data: GeographicAddressLookupRequest}

    /**
 * @summary Explicitly request a bounded Azure lookup after separate setup
 */
export const useStartGeographicAddressLookup = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startGeographicAddressLookup>>, TError,StartGeographicAddressLookupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof startGeographicAddressLookup>>,
        TError,
        StartGeographicAddressLookupMutationVariables,
        TContext
      > => {
      return useMutation(getStartGeographicAddressLookupMutationOptions(options));
    }

export const getGetGeographicAddressLookupUrl = (id: string,) => {




  return `/api/geography/address-lookups/${id}`
}

export const getGeographicAddressLookup = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<GeographicAddressLookupJob> => {

  return customFetch<GeographicAddressLookupJob>(getGetGeographicAddressLookupUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetGeographicAddressLookupQueryKey = (id: string,) => {
    return [
    `/api/geography/address-lookups/${id}`
    ] as const;
    }


export const getGetGeographicAddressLookupQueryOptions = <TData = Awaited<ReturnType<typeof getGeographicAddressLookup>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGeographicAddressLookup>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetGeographicAddressLookupQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getGeographicAddressLookup>>> = ({ signal }) => getGeographicAddressLookup(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getGeographicAddressLookup>>, TError, TData> & { queryKey: QueryKey }
}

export type GetGeographicAddressLookupQueryResult = NonNullable<Awaited<ReturnType<typeof getGeographicAddressLookup>>>
export type GetGeographicAddressLookupQueryError = unknown



export function useGetGeographicAddressLookup<TData = Awaited<ReturnType<typeof getGeographicAddressLookup>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGeographicAddressLookup>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetGeographicAddressLookupQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCancelGeographicAddressLookupUrl = (id: string,) => {




  return `/api/geography/address-lookups/${id}`
}

export const cancelGeographicAddressLookup = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<GeographicAddressLookupJob> => {

  return customFetch<GeographicAddressLookupJob>(getCancelGeographicAddressLookupUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getCancelGeographicAddressLookupMutationKey = () => ['cancelGeographicAddressLookup'] as const;

export const getCancelGeographicAddressLookupMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelGeographicAddressLookup>>, TError,CancelGeographicAddressLookupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelGeographicAddressLookup>>, TError,CancelGeographicAddressLookupMutationVariables, TContext> => {

const mutationKey = getCancelGeographicAddressLookupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelGeographicAddressLookup>>, CancelGeographicAddressLookupMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  cancelGeographicAddressLookup(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelGeographicAddressLookupMutationResult = NonNullable<Awaited<ReturnType<typeof cancelGeographicAddressLookup>>>

    export type CancelGeographicAddressLookupMutationError = unknown
    export type CancelGeographicAddressLookupMutationVariables = {id: string}

    export const useCancelGeographicAddressLookup = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelGeographicAddressLookup>>, TError,CancelGeographicAddressLookupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelGeographicAddressLookup>>,
        TError,
        CancelGeographicAddressLookupMutationVariables,
        TContext
      > => {
      return useMutation(getCancelGeographicAddressLookupMutationOptions(options));
    }

export const getRunGeographicAddressLookupUrl = (id: string,) => {




  return `/api/geography/address-lookups/${id}/run`
}

/**
 * @summary Process the next bounded page of a resumable lookup job
 */
export const runGeographicAddressLookup = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<GeographicAddressLookupJob> => {

  return customFetch<GeographicAddressLookupJob>(getRunGeographicAddressLookupUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRunGeographicAddressLookupMutationKey = () => ['runGeographicAddressLookup'] as const;

export const getRunGeographicAddressLookupMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runGeographicAddressLookup>>, TError,RunGeographicAddressLookupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof runGeographicAddressLookup>>, TError,RunGeographicAddressLookupMutationVariables, TContext> => {

const mutationKey = getRunGeographicAddressLookupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof runGeographicAddressLookup>>, RunGeographicAddressLookupMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  runGeographicAddressLookup(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RunGeographicAddressLookupMutationResult = NonNullable<Awaited<ReturnType<typeof runGeographicAddressLookup>>>

    export type RunGeographicAddressLookupMutationError = unknown
    export type RunGeographicAddressLookupMutationVariables = {id: string}

    /**
 * @summary Process the next bounded page of a resumable lookup job
 */
export const useRunGeographicAddressLookup = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runGeographicAddressLookup>>, TError,RunGeographicAddressLookupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof runGeographicAddressLookup>>,
        TError,
        RunGeographicAddressLookupMutationVariables,
        TContext
      > => {
      return useMutation(getRunGeographicAddressLookupMutationOptions(options));
    }

export const getPreviewGeographicImportUrl = (params: PreviewGeographicImportParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/geography/imports/preview?${stringifiedParams}` : `/api/geography/imports/preview`
}

export const previewGeographicImport = async (previewGeographicImportBody: PreviewGeographicImportBody,
    params: PreviewGeographicImportParams, options?: Parameters<typeof customFetch>[1]): Promise<GeographicImportPreview> => {
    const formData = new FormData();
formData.append(`file`, previewGeographicImportBody.file);

  return customFetch<GeographicImportPreview>(getPreviewGeographicImportUrl(params),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}





export const getPreviewGeographicImportMutationKey = () => ['previewGeographicImport'] as const;

export const getPreviewGeographicImportMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewGeographicImport>>, TError,PreviewGeographicImportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewGeographicImport>>, TError,PreviewGeographicImportMutationVariables, TContext> => {

const mutationKey = getPreviewGeographicImportMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewGeographicImport>>, PreviewGeographicImportMutationVariables> = (props) => {
          const {data,params} = props ?? {};

          return  previewGeographicImport(data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewGeographicImportMutationResult = NonNullable<Awaited<ReturnType<typeof previewGeographicImport>>>
    export type PreviewGeographicImportMutationBody = PreviewGeographicImportBody
    export type PreviewGeographicImportMutationError = unknown
    export type PreviewGeographicImportMutationVariables = {data: PreviewGeographicImportBody;params: PreviewGeographicImportParams}

    export const usePreviewGeographicImport = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewGeographicImport>>, TError,PreviewGeographicImportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewGeographicImport>>,
        TError,
        PreviewGeographicImportMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewGeographicImportMutationOptions(options));
    }

export const getGetGeographicImportUrl = (id: string,) => {




  return `/api/geography/imports/${id}`
}

export const getGeographicImport = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<GeographicImport> => {

  return customFetch<GeographicImport>(getGetGeographicImportUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetGeographicImportQueryKey = (id: string,) => {
    return [
    `/api/geography/imports/${id}`
    ] as const;
    }


export const getGetGeographicImportQueryOptions = <TData = Awaited<ReturnType<typeof getGeographicImport>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGeographicImport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetGeographicImportQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getGeographicImport>>> = ({ signal }) => getGeographicImport(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getGeographicImport>>, TError, TData> & { queryKey: QueryKey }
}

export type GetGeographicImportQueryResult = NonNullable<Awaited<ReturnType<typeof getGeographicImport>>>
export type GetGeographicImportQueryError = unknown



export function useGetGeographicImport<TData = Awaited<ReturnType<typeof getGeographicImport>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getGeographicImport>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetGeographicImportQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCommitGeographicImportUrl = (id: string,) => {




  return `/api/geography/imports/${id}/commit`
}

export const commitGeographicImport = async (id: string,
    commitGeographicImportBody: CommitGeographicImportBody, options?: Parameters<typeof customFetch>[1]): Promise<GeographicImport> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<GeographicImport>(getCommitGeographicImportUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(commitGeographicImportBody)
  }
);}





export const getCommitGeographicImportMutationKey = () => ['commitGeographicImport'] as const;

export const getCommitGeographicImportMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof commitGeographicImport>>, TError,CommitGeographicImportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof commitGeographicImport>>, TError,CommitGeographicImportMutationVariables, TContext> => {

const mutationKey = getCommitGeographicImportMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof commitGeographicImport>>, CommitGeographicImportMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  commitGeographicImport(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CommitGeographicImportMutationResult = NonNullable<Awaited<ReturnType<typeof commitGeographicImport>>>
    export type CommitGeographicImportMutationBody = CommitGeographicImportBody
    export type CommitGeographicImportMutationError = unknown
    export type CommitGeographicImportMutationVariables = {id: string;data: CommitGeographicImportBody}

    export const useCommitGeographicImport = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof commitGeographicImport>>, TError,CommitGeographicImportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof commitGeographicImport>>,
        TError,
        CommitGeographicImportMutationVariables,
        TContext
      > => {
      return useMutation(getCommitGeographicImportMutationOptions(options));
    }

export const getGetIcloudConnectionUrl = () => {




  return `/api/icloud/connection`
}

/**
 * @summary Get the signed-in user's private iCloud connection status
 */
export const getIcloudConnection = async ( options?: Parameters<typeof customFetch>[1]): Promise<IcloudConnection> => {

  return customFetch<IcloudConnection>(getGetIcloudConnectionUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetIcloudConnectionQueryKey = () => {
    return [
    `/api/icloud/connection`
    ] as const;
    }


export const getGetIcloudConnectionQueryOptions = <TData = Awaited<ReturnType<typeof getIcloudConnection>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getIcloudConnection>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetIcloudConnectionQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getIcloudConnection>>> = ({ signal }) => getIcloudConnection({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getIcloudConnection>>, TError, TData> & { queryKey: QueryKey }
}

export type GetIcloudConnectionQueryResult = NonNullable<Awaited<ReturnType<typeof getIcloudConnection>>>
export type GetIcloudConnectionQueryError = unknown


/**
 * @summary Get the signed-in user's private iCloud connection status
 */

export function useGetIcloudConnection<TData = Awaited<ReturnType<typeof getIcloudConnection>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getIcloudConnection>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetIcloudConnectionQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConnectIcloudUrl = () => {




  return `/api/icloud/connection`
}

/**
 * @summary Connect and import the signed-in user's own iCloud address book
 */
export const connectIcloud = async (icloudConnectInput: IcloudConnectInput, options?: Parameters<typeof customFetch>[1]): Promise<IcloudConnection> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<IcloudConnection>(getConnectIcloudUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(icloudConnectInput)
  }
);}





export const getConnectIcloudMutationKey = () => ['connectIcloud'] as const;

export const getConnectIcloudMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof connectIcloud>>, TError,ConnectIcloudMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof connectIcloud>>, TError,ConnectIcloudMutationVariables, TContext> => {

const mutationKey = getConnectIcloudMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof connectIcloud>>, ConnectIcloudMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  connectIcloud(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConnectIcloudMutationResult = NonNullable<Awaited<ReturnType<typeof connectIcloud>>>
    export type ConnectIcloudMutationBody = IcloudConnectInput
    export type ConnectIcloudMutationError = Error
    export type ConnectIcloudMutationVariables = {data: IcloudConnectInput}

    /**
 * @summary Connect and import the signed-in user's own iCloud address book
 */
export const useConnectIcloud = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof connectIcloud>>, TError,ConnectIcloudMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof connectIcloud>>,
        TError,
        ConnectIcloudMutationVariables,
        TContext
      > => {
      return useMutation(getConnectIcloudMutationOptions(options));
    }

export const getDisconnectIcloudUrl = () => {




  return `/api/icloud/connection`
}

/**
 * @summary Disconnect and delete the signed-in user's private iCloud data
 */
export const disconnectIcloud = async ( options?: Parameters<typeof customFetch>[1]): Promise<IcloudDisconnectResult> => {

  return customFetch<IcloudDisconnectResult>(getDisconnectIcloudUrl(),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDisconnectIcloudMutationKey = () => ['disconnectIcloud'] as const;

export const getDisconnectIcloudMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectIcloud>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof disconnectIcloud>>, TError,void, TContext> => {

const mutationKey = getDisconnectIcloudMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof disconnectIcloud>>, void> = () => {


          return  disconnectIcloud(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DisconnectIcloudMutationResult = NonNullable<Awaited<ReturnType<typeof disconnectIcloud>>>

    export type DisconnectIcloudMutationError = unknown


    /**
 * @summary Disconnect and delete the signed-in user's private iCloud data
 */
export const useDisconnectIcloud = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectIcloud>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof disconnectIcloud>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getDisconnectIcloudMutationOptions(options));
    }

export const getSyncIcloudUrl = () => {




  return `/api/icloud/sync`
}

/**
 * @summary Manually refresh the signed-in user's private iCloud snapshot
 */
export const syncIcloud = async ( options?: Parameters<typeof customFetch>[1]): Promise<IcloudConnection> => {

  return customFetch<IcloudConnection>(getSyncIcloudUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getSyncIcloudMutationKey = () => ['syncIcloud'] as const;

export const getSyncIcloudMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof syncIcloud>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof syncIcloud>>, TError,void, TContext> => {

const mutationKey = getSyncIcloudMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof syncIcloud>>, void> = () => {


          return  syncIcloud(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SyncIcloudMutationResult = NonNullable<Awaited<ReturnType<typeof syncIcloud>>>

    export type SyncIcloudMutationError = Error


    /**
 * @summary Manually refresh the signed-in user's private iCloud snapshot
 */
export const useSyncIcloud = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof syncIcloud>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof syncIcloud>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getSyncIcloudMutationOptions(options));
    }

export const getListIcloudContactsUrl = (params?: ListIcloudContactsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/icloud/contacts?${stringifiedParams}` : `/api/icloud/contacts`
}

/**
 * @summary List the signed-in user's private imported iCloud contacts
 */
export const listIcloudContacts = async (params?: ListIcloudContactsParams, options?: Parameters<typeof customFetch>[1]): Promise<IcloudContactList> => {

  return customFetch<IcloudContactList>(getListIcloudContactsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListIcloudContactsQueryKey = (params?: ListIcloudContactsParams,) => {
    return [
    `/api/icloud/contacts`, ...(params ? [params] : [])
    ] as const;
    }


export const getListIcloudContactsQueryOptions = <TData = Awaited<ReturnType<typeof listIcloudContacts>>, TError = unknown>(params?: ListIcloudContactsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listIcloudContacts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListIcloudContactsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listIcloudContacts>>> = ({ signal }) => listIcloudContacts(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listIcloudContacts>>, TError, TData> & { queryKey: QueryKey }
}

export type ListIcloudContactsQueryResult = NonNullable<Awaited<ReturnType<typeof listIcloudContacts>>>
export type ListIcloudContactsQueryError = unknown


/**
 * @summary List the signed-in user's private imported iCloud contacts
 */

export function useListIcloudContacts<TData = Awaited<ReturnType<typeof listIcloudContacts>>, TError = unknown>(
 params?: ListIcloudContactsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listIcloudContacts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListIcloudContactsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPreviewIcloudContactPromotionUrl = () => {




  return `/api/icloud/contacts/promotion-preview`
}

/**
 * @summary Preview shared CRM matches for selected private iCloud contacts
 */
export const previewIcloudContactPromotion = async (icloudPromotionPreviewRequest: IcloudPromotionPreviewRequest, options?: Parameters<typeof customFetch>[1]): Promise<IcloudPromotionPreview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<IcloudPromotionPreview>(getPreviewIcloudContactPromotionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(icloudPromotionPreviewRequest)
  }
);}





export const getPreviewIcloudContactPromotionMutationKey = () => ['previewIcloudContactPromotion'] as const;

export const getPreviewIcloudContactPromotionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewIcloudContactPromotion>>, TError,PreviewIcloudContactPromotionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewIcloudContactPromotion>>, TError,PreviewIcloudContactPromotionMutationVariables, TContext> => {

const mutationKey = getPreviewIcloudContactPromotionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewIcloudContactPromotion>>, PreviewIcloudContactPromotionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  previewIcloudContactPromotion(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewIcloudContactPromotionMutationResult = NonNullable<Awaited<ReturnType<typeof previewIcloudContactPromotion>>>
    export type PreviewIcloudContactPromotionMutationBody = IcloudPromotionPreviewRequest
    export type PreviewIcloudContactPromotionMutationError = Error
    export type PreviewIcloudContactPromotionMutationVariables = {data: IcloudPromotionPreviewRequest}

    /**
 * @summary Preview shared CRM matches for selected private iCloud contacts
 */
export const usePreviewIcloudContactPromotion = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewIcloudContactPromotion>>, TError,PreviewIcloudContactPromotionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewIcloudContactPromotion>>,
        TError,
        PreviewIcloudContactPromotionMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewIcloudContactPromotionMutationOptions(options));
    }

export const getPromoteIcloudContactsUrl = () => {




  return `/api/icloud/contacts/promote`
}

/**
 * @summary Explicitly create or link selected private iCloud contacts in the shared CRM
 */
export const promoteIcloudContacts = async (icloudPromotionRequest: IcloudPromotionRequest, options?: Parameters<typeof customFetch>[1]): Promise<IcloudPromotionResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<IcloudPromotionResult>(getPromoteIcloudContactsUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(icloudPromotionRequest)
  }
);}





export const getPromoteIcloudContactsMutationKey = () => ['promoteIcloudContacts'] as const;

export const getPromoteIcloudContactsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof promoteIcloudContacts>>, TError,PromoteIcloudContactsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof promoteIcloudContacts>>, TError,PromoteIcloudContactsMutationVariables, TContext> => {

const mutationKey = getPromoteIcloudContactsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof promoteIcloudContacts>>, PromoteIcloudContactsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  promoteIcloudContacts(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PromoteIcloudContactsMutationResult = NonNullable<Awaited<ReturnType<typeof promoteIcloudContacts>>>
    export type PromoteIcloudContactsMutationBody = IcloudPromotionRequest
    export type PromoteIcloudContactsMutationError = Error
    export type PromoteIcloudContactsMutationVariables = {data: IcloudPromotionRequest}

    /**
 * @summary Explicitly create or link selected private iCloud contacts in the shared CRM
 */
export const usePromoteIcloudContacts = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof promoteIcloudContacts>>, TError,PromoteIcloudContactsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof promoteIcloudContacts>>,
        TError,
        PromoteIcloudContactsMutationVariables,
        TContext
      > => {
      return useMutation(getPromoteIcloudContactsMutationOptions(options));
    }

export const getGetIcloudBackupUrl = () => {




  return `/api/icloud/backup`
}

/**
 * @summary Build a vCard backup of the signed-in user's current snapshot
 */
export const getIcloudBackup = async ( options?: Parameters<typeof customFetch>[1]): Promise<IcloudBackup> => {

  return customFetch<IcloudBackup>(getGetIcloudBackupUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetIcloudBackupQueryKey = () => {
    return [
    `/api/icloud/backup`
    ] as const;
    }


export const getGetIcloudBackupQueryOptions = <TData = Awaited<ReturnType<typeof getIcloudBackup>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getIcloudBackup>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetIcloudBackupQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getIcloudBackup>>> = ({ signal }) => getIcloudBackup({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getIcloudBackup>>, TError, TData> & { queryKey: QueryKey }
}

export type GetIcloudBackupQueryResult = NonNullable<Awaited<ReturnType<typeof getIcloudBackup>>>
export type GetIcloudBackupQueryError = Error


/**
 * @summary Build a vCard backup of the signed-in user's current snapshot
 */

export function useGetIcloudBackup<TData = Awaited<ReturnType<typeof getIcloudBackup>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getIcloudBackup>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetIcloudBackupQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListIcloudPromptsUrl = () => {




  return `/api/icloud/prompts`
}

/**
 * @summary List the signed-in user's private duplicate-analysis prompts
 */
export const listIcloudPrompts = async ( options?: Parameters<typeof customFetch>[1]): Promise<IcloudPrompt[]> => {

  return customFetch<IcloudPrompt[]>(getListIcloudPromptsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListIcloudPromptsQueryKey = () => {
    return [
    `/api/icloud/prompts`
    ] as const;
    }


export const getListIcloudPromptsQueryOptions = <TData = Awaited<ReturnType<typeof listIcloudPrompts>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listIcloudPrompts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListIcloudPromptsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listIcloudPrompts>>> = ({ signal }) => listIcloudPrompts({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listIcloudPrompts>>, TError, TData> & { queryKey: QueryKey }
}

export type ListIcloudPromptsQueryResult = NonNullable<Awaited<ReturnType<typeof listIcloudPrompts>>>
export type ListIcloudPromptsQueryError = unknown


/**
 * @summary List the signed-in user's private duplicate-analysis prompts
 */

export function useListIcloudPrompts<TData = Awaited<ReturnType<typeof listIcloudPrompts>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listIcloudPrompts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListIcloudPromptsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateIcloudPromptUrl = () => {




  return `/api/icloud/prompts`
}

/**
 * @summary Save a private duplicate-analysis prompt
 */
export const createIcloudPrompt = async (icloudPromptInput: IcloudPromptInput, options?: Parameters<typeof customFetch>[1]): Promise<IcloudPrompt> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<IcloudPrompt>(getCreateIcloudPromptUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(icloudPromptInput)
  }
);}





export const getCreateIcloudPromptMutationKey = () => ['createIcloudPrompt'] as const;

export const getCreateIcloudPromptMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createIcloudPrompt>>, TError,CreateIcloudPromptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createIcloudPrompt>>, TError,CreateIcloudPromptMutationVariables, TContext> => {

const mutationKey = getCreateIcloudPromptMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createIcloudPrompt>>, CreateIcloudPromptMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createIcloudPrompt(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateIcloudPromptMutationResult = NonNullable<Awaited<ReturnType<typeof createIcloudPrompt>>>
    export type CreateIcloudPromptMutationBody = IcloudPromptInput
    export type CreateIcloudPromptMutationError = unknown
    export type CreateIcloudPromptMutationVariables = {data: IcloudPromptInput}

    /**
 * @summary Save a private duplicate-analysis prompt
 */
export const useCreateIcloudPrompt = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createIcloudPrompt>>, TError,CreateIcloudPromptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createIcloudPrompt>>,
        TError,
        CreateIcloudPromptMutationVariables,
        TContext
      > => {
      return useMutation(getCreateIcloudPromptMutationOptions(options));
    }

export const getUpdateIcloudPromptUrl = (id: string,) => {




  return `/api/icloud/prompts/${id}`
}

export const updateIcloudPrompt = async (id: string,
    icloudPromptUpdate: IcloudPromptUpdate, options?: Parameters<typeof customFetch>[1]): Promise<IcloudPrompt> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<IcloudPrompt>(getUpdateIcloudPromptUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(icloudPromptUpdate)
  }
);}





export const getUpdateIcloudPromptMutationKey = () => ['updateIcloudPrompt'] as const;

export const getUpdateIcloudPromptMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateIcloudPrompt>>, TError,UpdateIcloudPromptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateIcloudPrompt>>, TError,UpdateIcloudPromptMutationVariables, TContext> => {

const mutationKey = getUpdateIcloudPromptMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateIcloudPrompt>>, UpdateIcloudPromptMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateIcloudPrompt(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateIcloudPromptMutationResult = NonNullable<Awaited<ReturnType<typeof updateIcloudPrompt>>>
    export type UpdateIcloudPromptMutationBody = IcloudPromptUpdate
    export type UpdateIcloudPromptMutationError = Error
    export type UpdateIcloudPromptMutationVariables = {id: string;data: IcloudPromptUpdate}

    export const useUpdateIcloudPrompt = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateIcloudPrompt>>, TError,UpdateIcloudPromptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateIcloudPrompt>>,
        TError,
        UpdateIcloudPromptMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateIcloudPromptMutationOptions(options));
    }

export const getDeleteIcloudPromptUrl = (id: string,) => {




  return `/api/icloud/prompts/${id}`
}

export const deleteIcloudPrompt = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteIcloudPromptUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteIcloudPromptMutationKey = () => ['deleteIcloudPrompt'] as const;

export const getDeleteIcloudPromptMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteIcloudPrompt>>, TError,DeleteIcloudPromptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteIcloudPrompt>>, TError,DeleteIcloudPromptMutationVariables, TContext> => {

const mutationKey = getDeleteIcloudPromptMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteIcloudPrompt>>, DeleteIcloudPromptMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteIcloudPrompt(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteIcloudPromptMutationResult = NonNullable<Awaited<ReturnType<typeof deleteIcloudPrompt>>>

    export type DeleteIcloudPromptMutationError = Error
    export type DeleteIcloudPromptMutationVariables = {id: string}

    export const useDeleteIcloudPrompt = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteIcloudPrompt>>, TError,DeleteIcloudPromptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteIcloudPrompt>>,
        TError,
        DeleteIcloudPromptMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteIcloudPromptMutationOptions(options));
    }

export const getListIcloudRulesUrl = () => {




  return `/api/icloud/rules`
}

/**
 * @summary List the signed-in user's private merge rules
 */
export const listIcloudRules = async ( options?: Parameters<typeof customFetch>[1]): Promise<IcloudMergeRule[]> => {

  return customFetch<IcloudMergeRule[]>(getListIcloudRulesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListIcloudRulesQueryKey = () => {
    return [
    `/api/icloud/rules`
    ] as const;
    }


export const getListIcloudRulesQueryOptions = <TData = Awaited<ReturnType<typeof listIcloudRules>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listIcloudRules>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListIcloudRulesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listIcloudRules>>> = ({ signal }) => listIcloudRules({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listIcloudRules>>, TError, TData> & { queryKey: QueryKey }
}

export type ListIcloudRulesQueryResult = NonNullable<Awaited<ReturnType<typeof listIcloudRules>>>
export type ListIcloudRulesQueryError = unknown


/**
 * @summary List the signed-in user's private merge rules
 */

export function useListIcloudRules<TData = Awaited<ReturnType<typeof listIcloudRules>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listIcloudRules>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListIcloudRulesQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateIcloudRuleUrl = () => {




  return `/api/icloud/rules`
}

export const createIcloudRule = async (icloudMergeRuleInput: IcloudMergeRuleInput, options?: Parameters<typeof customFetch>[1]): Promise<IcloudMergeRule> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<IcloudMergeRule>(getCreateIcloudRuleUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(icloudMergeRuleInput)
  }
);}





export const getCreateIcloudRuleMutationKey = () => ['createIcloudRule'] as const;

export const getCreateIcloudRuleMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createIcloudRule>>, TError,CreateIcloudRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createIcloudRule>>, TError,CreateIcloudRuleMutationVariables, TContext> => {

const mutationKey = getCreateIcloudRuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createIcloudRule>>, CreateIcloudRuleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createIcloudRule(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateIcloudRuleMutationResult = NonNullable<Awaited<ReturnType<typeof createIcloudRule>>>
    export type CreateIcloudRuleMutationBody = IcloudMergeRuleInput
    export type CreateIcloudRuleMutationError = unknown
    export type CreateIcloudRuleMutationVariables = {data: IcloudMergeRuleInput}

    export const useCreateIcloudRule = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createIcloudRule>>, TError,CreateIcloudRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createIcloudRule>>,
        TError,
        CreateIcloudRuleMutationVariables,
        TContext
      > => {
      return useMutation(getCreateIcloudRuleMutationOptions(options));
    }

export const getReorderIcloudRulesUrl = () => {




  return `/api/icloud/rules/reorder`
}

export const reorderIcloudRules = async (icloudMergeRuleOrder: IcloudMergeRuleOrder, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<void>(getReorderIcloudRulesUrl(),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(icloudMergeRuleOrder)
  }
);}





export const getReorderIcloudRulesMutationKey = () => ['reorderIcloudRules'] as const;

export const getReorderIcloudRulesMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reorderIcloudRules>>, TError,ReorderIcloudRulesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reorderIcloudRules>>, TError,ReorderIcloudRulesMutationVariables, TContext> => {

const mutationKey = getReorderIcloudRulesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reorderIcloudRules>>, ReorderIcloudRulesMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  reorderIcloudRules(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReorderIcloudRulesMutationResult = NonNullable<Awaited<ReturnType<typeof reorderIcloudRules>>>
    export type ReorderIcloudRulesMutationBody = IcloudMergeRuleOrder
    export type ReorderIcloudRulesMutationError = Error
    export type ReorderIcloudRulesMutationVariables = {data: IcloudMergeRuleOrder}

    export const useReorderIcloudRules = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reorderIcloudRules>>, TError,ReorderIcloudRulesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reorderIcloudRules>>,
        TError,
        ReorderIcloudRulesMutationVariables,
        TContext
      > => {
      return useMutation(getReorderIcloudRulesMutationOptions(options));
    }

export const getUpdateIcloudRuleUrl = (id: string,) => {




  return `/api/icloud/rules/${id}`
}

export const updateIcloudRule = async (id: string,
    icloudMergeRuleUpdate: IcloudMergeRuleUpdate, options?: Parameters<typeof customFetch>[1]): Promise<IcloudMergeRule> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<IcloudMergeRule>(getUpdateIcloudRuleUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(icloudMergeRuleUpdate)
  }
);}





export const getUpdateIcloudRuleMutationKey = () => ['updateIcloudRule'] as const;

export const getUpdateIcloudRuleMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateIcloudRule>>, TError,UpdateIcloudRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateIcloudRule>>, TError,UpdateIcloudRuleMutationVariables, TContext> => {

const mutationKey = getUpdateIcloudRuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateIcloudRule>>, UpdateIcloudRuleMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateIcloudRule(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateIcloudRuleMutationResult = NonNullable<Awaited<ReturnType<typeof updateIcloudRule>>>
    export type UpdateIcloudRuleMutationBody = IcloudMergeRuleUpdate
    export type UpdateIcloudRuleMutationError = Error
    export type UpdateIcloudRuleMutationVariables = {id: string;data: IcloudMergeRuleUpdate}

    export const useUpdateIcloudRule = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateIcloudRule>>, TError,UpdateIcloudRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateIcloudRule>>,
        TError,
        UpdateIcloudRuleMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateIcloudRuleMutationOptions(options));
    }

export const getDeleteIcloudRuleUrl = (id: string,) => {




  return `/api/icloud/rules/${id}`
}

export const deleteIcloudRule = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteIcloudRuleUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteIcloudRuleMutationKey = () => ['deleteIcloudRule'] as const;

export const getDeleteIcloudRuleMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteIcloudRule>>, TError,DeleteIcloudRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteIcloudRule>>, TError,DeleteIcloudRuleMutationVariables, TContext> => {

const mutationKey = getDeleteIcloudRuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteIcloudRule>>, DeleteIcloudRuleMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteIcloudRule(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteIcloudRuleMutationResult = NonNullable<Awaited<ReturnType<typeof deleteIcloudRule>>>

    export type DeleteIcloudRuleMutationError = Error
    export type DeleteIcloudRuleMutationVariables = {id: string}

    export const useDeleteIcloudRule = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteIcloudRule>>, TError,DeleteIcloudRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteIcloudRule>>,
        TError,
        DeleteIcloudRuleMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteIcloudRuleMutationOptions(options));
    }

export const getListIcloudBatchesUrl = (params?: ListIcloudBatchesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/icloud/batches?${stringifiedParams}` : `/api/icloud/batches`
}

export const listIcloudBatches = async (params?: ListIcloudBatchesParams, options?: Parameters<typeof customFetch>[1]): Promise<IcloudMergeBatchList> => {

  return customFetch<IcloudMergeBatchList>(getListIcloudBatchesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListIcloudBatchesQueryKey = (params?: ListIcloudBatchesParams,) => {
    return [
    `/api/icloud/batches`, ...(params ? [params] : [])
    ] as const;
    }


export const getListIcloudBatchesQueryOptions = <TData = Awaited<ReturnType<typeof listIcloudBatches>>, TError = unknown>(params?: ListIcloudBatchesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listIcloudBatches>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListIcloudBatchesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listIcloudBatches>>> = ({ signal }) => listIcloudBatches(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listIcloudBatches>>, TError, TData> & { queryKey: QueryKey }
}

export type ListIcloudBatchesQueryResult = NonNullable<Awaited<ReturnType<typeof listIcloudBatches>>>
export type ListIcloudBatchesQueryError = unknown



export function useListIcloudBatches<TData = Awaited<ReturnType<typeof listIcloudBatches>>, TError = unknown>(
 params?: ListIcloudBatchesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listIcloudBatches>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListIcloudBatchesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateIcloudBatchUrl = () => {




  return `/api/icloud/batches`
}

export const createIcloudBatch = async (icloudMergeBatchCreate: IcloudMergeBatchCreate, options?: Parameters<typeof customFetch>[1]): Promise<IcloudMergeBatchDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<IcloudMergeBatchDetail>(getCreateIcloudBatchUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(icloudMergeBatchCreate)
  }
);}





export const getCreateIcloudBatchMutationKey = () => ['createIcloudBatch'] as const;

export const getCreateIcloudBatchMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createIcloudBatch>>, TError,CreateIcloudBatchMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createIcloudBatch>>, TError,CreateIcloudBatchMutationVariables, TContext> => {

const mutationKey = getCreateIcloudBatchMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createIcloudBatch>>, CreateIcloudBatchMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createIcloudBatch(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateIcloudBatchMutationResult = NonNullable<Awaited<ReturnType<typeof createIcloudBatch>>>
    export type CreateIcloudBatchMutationBody = IcloudMergeBatchCreate
    export type CreateIcloudBatchMutationError = unknown
    export type CreateIcloudBatchMutationVariables = {data: IcloudMergeBatchCreate}

    export const useCreateIcloudBatch = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createIcloudBatch>>, TError,CreateIcloudBatchMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createIcloudBatch>>,
        TError,
        CreateIcloudBatchMutationVariables,
        TContext
      > => {
      return useMutation(getCreateIcloudBatchMutationOptions(options));
    }

export const getGetIcloudBatchUrl = (id: string,) => {




  return `/api/icloud/batches/${id}`
}

export const getIcloudBatch = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<IcloudMergeBatchDetail> => {

  return customFetch<IcloudMergeBatchDetail>(getGetIcloudBatchUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetIcloudBatchQueryKey = (id: string,) => {
    return [
    `/api/icloud/batches/${id}`
    ] as const;
    }


export const getGetIcloudBatchQueryOptions = <TData = Awaited<ReturnType<typeof getIcloudBatch>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getIcloudBatch>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetIcloudBatchQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getIcloudBatch>>> = ({ signal }) => getIcloudBatch(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getIcloudBatch>>, TError, TData> & { queryKey: QueryKey }
}

export type GetIcloudBatchQueryResult = NonNullable<Awaited<ReturnType<typeof getIcloudBatch>>>
export type GetIcloudBatchQueryError = Error



export function useGetIcloudBatch<TData = Awaited<ReturnType<typeof getIcloudBatch>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getIcloudBatch>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetIcloudBatchQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDeleteIcloudBatchUrl = (id: string,) => {




  return `/api/icloud/batches/${id}`
}

export const deleteIcloudBatch = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteIcloudBatchUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteIcloudBatchMutationKey = () => ['deleteIcloudBatch'] as const;

export const getDeleteIcloudBatchMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteIcloudBatch>>, TError,DeleteIcloudBatchMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteIcloudBatch>>, TError,DeleteIcloudBatchMutationVariables, TContext> => {

const mutationKey = getDeleteIcloudBatchMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteIcloudBatch>>, DeleteIcloudBatchMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteIcloudBatch(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteIcloudBatchMutationResult = NonNullable<Awaited<ReturnType<typeof deleteIcloudBatch>>>

    export type DeleteIcloudBatchMutationError = Error
    export type DeleteIcloudBatchMutationVariables = {id: string}

    export const useDeleteIcloudBatch = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteIcloudBatch>>, TError,DeleteIcloudBatchMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof deleteIcloudBatch>>,
        TError,
        DeleteIcloudBatchMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteIcloudBatchMutationOptions(options));
    }

export const getGenerateIcloudBatchUrl = (id: string,) => {




  return `/api/icloud/batches/${id}/generate`
}

/**
 * @summary Generate AI-assisted duplicate proposals from the current private snapshot
 */
export const generateIcloudBatch = async (id: string,
    icloudMergeBatchGenerate: IcloudMergeBatchGenerate, options?: Parameters<typeof customFetch>[1]): Promise<IcloudMergeBatchDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<IcloudMergeBatchDetail>(getGenerateIcloudBatchUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(icloudMergeBatchGenerate)
  }
);}





export const getGenerateIcloudBatchMutationKey = () => ['generateIcloudBatch'] as const;

export const getGenerateIcloudBatchMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateIcloudBatch>>, TError,GenerateIcloudBatchMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof generateIcloudBatch>>, TError,GenerateIcloudBatchMutationVariables, TContext> => {

const mutationKey = getGenerateIcloudBatchMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof generateIcloudBatch>>, GenerateIcloudBatchMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  generateIcloudBatch(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenerateIcloudBatchMutationResult = NonNullable<Awaited<ReturnType<typeof generateIcloudBatch>>>
    export type GenerateIcloudBatchMutationBody = IcloudMergeBatchGenerate
    export type GenerateIcloudBatchMutationError = Error
    export type GenerateIcloudBatchMutationVariables = {id: string;data: IcloudMergeBatchGenerate}

    /**
 * @summary Generate AI-assisted duplicate proposals from the current private snapshot
 */
export const useGenerateIcloudBatch = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateIcloudBatch>>, TError,GenerateIcloudBatchMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof generateIcloudBatch>>,
        TError,
        GenerateIcloudBatchMutationVariables,
        TContext
      > => {
      return useMutation(getGenerateIcloudBatchMutationOptions(options));
    }

export const getDecideIcloudBatchItemUrl = (id: string,
    itemId: string,) => {




  return `/api/icloud/batches/${id}/items/${itemId}`
}

export const decideIcloudBatchItem = async (id: string,
    itemId: string,
    icloudMergeDecision: IcloudMergeDecision, options?: Parameters<typeof customFetch>[1]): Promise<IcloudMergeBatchItem> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<IcloudMergeBatchItem>(getDecideIcloudBatchItemUrl(id,itemId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(icloudMergeDecision)
  }
);}





export const getDecideIcloudBatchItemMutationKey = () => ['decideIcloudBatchItem'] as const;

export const getDecideIcloudBatchItemMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideIcloudBatchItem>>, TError,DecideIcloudBatchItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof decideIcloudBatchItem>>, TError,DecideIcloudBatchItemMutationVariables, TContext> => {

const mutationKey = getDecideIcloudBatchItemMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof decideIcloudBatchItem>>, DecideIcloudBatchItemMutationVariables> = (props) => {
          const {id,itemId,data} = props ?? {};

          return  decideIcloudBatchItem(id,itemId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DecideIcloudBatchItemMutationResult = NonNullable<Awaited<ReturnType<typeof decideIcloudBatchItem>>>
    export type DecideIcloudBatchItemMutationBody = IcloudMergeDecision
    export type DecideIcloudBatchItemMutationError = Error
    export type DecideIcloudBatchItemMutationVariables = {id: string;itemId: string;data: IcloudMergeDecision}

    export const useDecideIcloudBatchItem = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideIcloudBatchItem>>, TError,DecideIcloudBatchItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof decideIcloudBatchItem>>,
        TError,
        DecideIcloudBatchItemMutationVariables,
        TContext
      > => {
      return useMutation(getDecideIcloudBatchItemMutationOptions(options));
    }

export const getExecuteIcloudBatchUrl = (id: string,) => {




  return `/api/icloud/batches/${id}/execute`
}

/**
 * @summary Apply approved merges to the signed-in user's verified iCloud address book
 */
export const executeIcloudBatch = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<IcloudMergeExecution> => {

  return customFetch<IcloudMergeExecution>(getExecuteIcloudBatchUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getExecuteIcloudBatchMutationKey = () => ['executeIcloudBatch'] as const;

export const getExecuteIcloudBatchMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof executeIcloudBatch>>, TError,ExecuteIcloudBatchMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof executeIcloudBatch>>, TError,ExecuteIcloudBatchMutationVariables, TContext> => {

const mutationKey = getExecuteIcloudBatchMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof executeIcloudBatch>>, ExecuteIcloudBatchMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  executeIcloudBatch(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ExecuteIcloudBatchMutationResult = NonNullable<Awaited<ReturnType<typeof executeIcloudBatch>>>

    export type ExecuteIcloudBatchMutationError = Error
    export type ExecuteIcloudBatchMutationVariables = {id: string}

    /**
 * @summary Apply approved merges to the signed-in user's verified iCloud address book
 */
export const useExecuteIcloudBatch = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof executeIcloudBatch>>, TError,ExecuteIcloudBatchMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof executeIcloudBatch>>,
        TError,
        ExecuteIcloudBatchMutationVariables,
        TContext
      > => {
      return useMutation(getExecuteIcloudBatchMutationOptions(options));
    }

export const getRestoreIcloudBatchItemUrl = (id: string,
    itemId: string,) => {




  return `/api/icloud/batches/${id}/items/${itemId}/restore`
}

/**
 * @summary Restore an applied merge within its 30-day retention window
 */
export const restoreIcloudBatchItem = async (id: string,
    itemId: string, options?: Parameters<typeof customFetch>[1]): Promise<IcloudMergeBatchItem> => {

  return customFetch<IcloudMergeBatchItem>(getRestoreIcloudBatchItemUrl(id,itemId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRestoreIcloudBatchItemMutationKey = () => ['restoreIcloudBatchItem'] as const;

export const getRestoreIcloudBatchItemMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof restoreIcloudBatchItem>>, TError,RestoreIcloudBatchItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof restoreIcloudBatchItem>>, TError,RestoreIcloudBatchItemMutationVariables, TContext> => {

const mutationKey = getRestoreIcloudBatchItemMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof restoreIcloudBatchItem>>, RestoreIcloudBatchItemMutationVariables> = (props) => {
          const {id,itemId} = props ?? {};

          return  restoreIcloudBatchItem(id,itemId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RestoreIcloudBatchItemMutationResult = NonNullable<Awaited<ReturnType<typeof restoreIcloudBatchItem>>>

    export type RestoreIcloudBatchItemMutationError = Error
    export type RestoreIcloudBatchItemMutationVariables = {id: string;itemId: string}

    /**
 * @summary Restore an applied merge within its 30-day retention window
 */
export const useRestoreIcloudBatchItem = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof restoreIcloudBatchItem>>, TError,RestoreIcloudBatchItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof restoreIcloudBatchItem>>,
        TError,
        RestoreIcloudBatchItemMutationVariables,
        TContext
      > => {
      return useMutation(getRestoreIcloudBatchItemMutationOptions(options));
    }

export const getListIcloudWritebackLogUrl = (params?: ListIcloudWritebackLogParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/icloud/writeback-log?${stringifiedParams}` : `/api/icloud/writeback-log`
}

export const listIcloudWritebackLog = async (params?: ListIcloudWritebackLogParams, options?: Parameters<typeof customFetch>[1]): Promise<IcloudWritebackLogList> => {

  return customFetch<IcloudWritebackLogList>(getListIcloudWritebackLogUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListIcloudWritebackLogQueryKey = (params?: ListIcloudWritebackLogParams,) => {
    return [
    `/api/icloud/writeback-log`, ...(params ? [params] : [])
    ] as const;
    }


export const getListIcloudWritebackLogQueryOptions = <TData = Awaited<ReturnType<typeof listIcloudWritebackLog>>, TError = unknown>(params?: ListIcloudWritebackLogParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listIcloudWritebackLog>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListIcloudWritebackLogQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listIcloudWritebackLog>>> = ({ signal }) => listIcloudWritebackLog(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listIcloudWritebackLog>>, TError, TData> & { queryKey: QueryKey }
}

export type ListIcloudWritebackLogQueryResult = NonNullable<Awaited<ReturnType<typeof listIcloudWritebackLog>>>
export type ListIcloudWritebackLogQueryError = unknown



export function useListIcloudWritebackLog<TData = Awaited<ReturnType<typeof listIcloudWritebackLog>>, TError = unknown>(
 params?: ListIcloudWritebackLogParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listIcloudWritebackLog>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListIcloudWritebackLogQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetWhatsAppCampaignReadinessUrl = () => {




  return `/api/whatsapp/campaign-readiness`
}

/**
 * Public-safe business sender and setup gaps for campaign staff. No credential administration.
 */
export const getWhatsAppCampaignReadiness = async ( options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppSettings> => {

  return customFetch<WhatsAppSettings>(getGetWhatsAppCampaignReadinessUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWhatsAppCampaignReadinessQueryKey = () => {
    return [
    `/api/whatsapp/campaign-readiness`
    ] as const;
    }


export const getGetWhatsAppCampaignReadinessQueryOptions = <TData = Awaited<ReturnType<typeof getWhatsAppCampaignReadiness>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppCampaignReadiness>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWhatsAppCampaignReadinessQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWhatsAppCampaignReadiness>>> = ({ signal }) => getWhatsAppCampaignReadiness({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppCampaignReadiness>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWhatsAppCampaignReadinessQueryResult = NonNullable<Awaited<ReturnType<typeof getWhatsAppCampaignReadiness>>>
export type GetWhatsAppCampaignReadinessQueryError = unknown



export function useGetWhatsAppCampaignReadiness<TData = Awaited<ReturnType<typeof getWhatsAppCampaignReadiness>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppCampaignReadiness>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWhatsAppCampaignReadinessQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetWhatsAppSettingsUrl = () => {




  return `/api/whatsapp/settings`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.integrations.manage. Owner resolved from authorized server context.
 */
export const getWhatsAppSettings = async ( options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppSettings> => {

  return customFetch<WhatsAppSettings>(getGetWhatsAppSettingsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWhatsAppSettingsQueryKey = () => {
    return [
    `/api/whatsapp/settings`
    ] as const;
    }


export const getGetWhatsAppSettingsQueryOptions = <TData = Awaited<ReturnType<typeof getWhatsAppSettings>>, TError = Error>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWhatsAppSettingsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWhatsAppSettings>>> = ({ signal }) => getWhatsAppSettings({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppSettings>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWhatsAppSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof getWhatsAppSettings>>>
export type GetWhatsAppSettingsQueryError = Error



export function useGetWhatsAppSettings<TData = Awaited<ReturnType<typeof getWhatsAppSettings>>, TError = Error>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppSettings>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWhatsAppSettingsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConfigureWhatsAppSenderUrl = () => {




  return `/api/whatsapp/settings`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.integrations.manage. Owner resolved from authorized server context.
 */
export const configureWhatsAppSender = async (whatsAppSenderConfigure: WhatsAppSenderConfigure, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppSettings> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WhatsAppSettings>(getConfigureWhatsAppSenderUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(whatsAppSenderConfigure)
  }
);}





export const getConfigureWhatsAppSenderMutationKey = () => ['configureWhatsAppSender'] as const;

export const getConfigureWhatsAppSenderMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof configureWhatsAppSender>>, TError,ConfigureWhatsAppSenderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof configureWhatsAppSender>>, TError,ConfigureWhatsAppSenderMutationVariables, TContext> => {

const mutationKey = getConfigureWhatsAppSenderMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof configureWhatsAppSender>>, ConfigureWhatsAppSenderMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  configureWhatsAppSender(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfigureWhatsAppSenderMutationResult = NonNullable<Awaited<ReturnType<typeof configureWhatsAppSender>>>
    export type ConfigureWhatsAppSenderMutationBody = WhatsAppSenderConfigure
    export type ConfigureWhatsAppSenderMutationError = Error
    export type ConfigureWhatsAppSenderMutationVariables = {data: WhatsAppSenderConfigure}

    export const useConfigureWhatsAppSender = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof configureWhatsAppSender>>, TError,ConfigureWhatsAppSenderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof configureWhatsAppSender>>,
        TError,
        ConfigureWhatsAppSenderMutationVariables,
        TContext
      > => {
      return useMutation(getConfigureWhatsAppSenderMutationOptions(options));
    }

export const getVerifyWhatsAppSenderUrl = () => {




  return `/api/whatsapp/settings/verify`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.integrations.manage. Owner resolved from authorized server context.
 */
export const verifyWhatsAppSender = async (whatsAppRevisionAction: WhatsAppRevisionAction, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppSettings> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WhatsAppSettings>(getVerifyWhatsAppSenderUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(whatsAppRevisionAction)
  }
);}





export const getVerifyWhatsAppSenderMutationKey = () => ['verifyWhatsAppSender'] as const;

export const getVerifyWhatsAppSenderMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof verifyWhatsAppSender>>, TError,VerifyWhatsAppSenderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof verifyWhatsAppSender>>, TError,VerifyWhatsAppSenderMutationVariables, TContext> => {

const mutationKey = getVerifyWhatsAppSenderMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof verifyWhatsAppSender>>, VerifyWhatsAppSenderMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  verifyWhatsAppSender(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type VerifyWhatsAppSenderMutationResult = NonNullable<Awaited<ReturnType<typeof verifyWhatsAppSender>>>
    export type VerifyWhatsAppSenderMutationBody = WhatsAppRevisionAction
    export type VerifyWhatsAppSenderMutationError = Error
    export type VerifyWhatsAppSenderMutationVariables = {data: WhatsAppRevisionAction}

    export const useVerifyWhatsAppSender = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof verifyWhatsAppSender>>, TError,VerifyWhatsAppSenderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof verifyWhatsAppSender>>,
        TError,
        VerifyWhatsAppSenderMutationVariables,
        TContext
      > => {
      return useMutation(getVerifyWhatsAppSenderMutationOptions(options));
    }

export const getDisconnectWhatsAppSenderUrl = () => {




  return `/api/whatsapp/settings/disconnect`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.integrations.manage. Owner resolved from authorized server context.
 */
export const disconnectWhatsAppSender = async (whatsAppRevisionAction: WhatsAppRevisionAction, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppSettings> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WhatsAppSettings>(getDisconnectWhatsAppSenderUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(whatsAppRevisionAction)
  }
);}





export const getDisconnectWhatsAppSenderMutationKey = () => ['disconnectWhatsAppSender'] as const;

export const getDisconnectWhatsAppSenderMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectWhatsAppSender>>, TError,DisconnectWhatsAppSenderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof disconnectWhatsAppSender>>, TError,DisconnectWhatsAppSenderMutationVariables, TContext> => {

const mutationKey = getDisconnectWhatsAppSenderMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof disconnectWhatsAppSender>>, DisconnectWhatsAppSenderMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  disconnectWhatsAppSender(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DisconnectWhatsAppSenderMutationResult = NonNullable<Awaited<ReturnType<typeof disconnectWhatsAppSender>>>
    export type DisconnectWhatsAppSenderMutationBody = WhatsAppRevisionAction
    export type DisconnectWhatsAppSenderMutationError = Error
    export type DisconnectWhatsAppSenderMutationVariables = {data: WhatsAppRevisionAction}

    export const useDisconnectWhatsAppSender = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof disconnectWhatsAppSender>>, TError,DisconnectWhatsAppSenderMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof disconnectWhatsAppSender>>,
        TError,
        DisconnectWhatsAppSenderMutationVariables,
        TContext
      > => {
      return useMutation(getDisconnectWhatsAppSenderMutationOptions(options));
    }

export const getBeginWhatsAppOnboardingUrl = () => {




  return `/api/whatsapp/onboarding`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.integrations.manage. Owner resolved from authorized server context.
 */
export const beginWhatsAppOnboarding = async (whatsAppOnboardingRequest: WhatsAppOnboardingRequest, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppOnboardingSession> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WhatsAppOnboardingSession>(getBeginWhatsAppOnboardingUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(whatsAppOnboardingRequest)
  }
);}





export const getBeginWhatsAppOnboardingMutationKey = () => ['beginWhatsAppOnboarding'] as const;

export const getBeginWhatsAppOnboardingMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof beginWhatsAppOnboarding>>, TError,BeginWhatsAppOnboardingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof beginWhatsAppOnboarding>>, TError,BeginWhatsAppOnboardingMutationVariables, TContext> => {

const mutationKey = getBeginWhatsAppOnboardingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof beginWhatsAppOnboarding>>, BeginWhatsAppOnboardingMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  beginWhatsAppOnboarding(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type BeginWhatsAppOnboardingMutationResult = NonNullable<Awaited<ReturnType<typeof beginWhatsAppOnboarding>>>
    export type BeginWhatsAppOnboardingMutationBody = WhatsAppOnboardingRequest
    export type BeginWhatsAppOnboardingMutationError = Error
    export type BeginWhatsAppOnboardingMutationVariables = {data: WhatsAppOnboardingRequest}

    export const useBeginWhatsAppOnboarding = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof beginWhatsAppOnboarding>>, TError,BeginWhatsAppOnboardingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof beginWhatsAppOnboarding>>,
        TError,
        BeginWhatsAppOnboardingMutationVariables,
        TContext
      > => {
      return useMutation(getBeginWhatsAppOnboardingMutationOptions(options));
    }

export const getCompleteWhatsAppOnboardingUrl = () => {




  return `/api/whatsapp/onboarding/complete`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.integrations.manage. Owner resolved from authorized server context.
 */
export const completeWhatsAppOnboarding = async (whatsAppOnboardingComplete: WhatsAppOnboardingComplete, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppSettings> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WhatsAppSettings>(getCompleteWhatsAppOnboardingUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(whatsAppOnboardingComplete)
  }
);}





export const getCompleteWhatsAppOnboardingMutationKey = () => ['completeWhatsAppOnboarding'] as const;

export const getCompleteWhatsAppOnboardingMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof completeWhatsAppOnboarding>>, TError,CompleteWhatsAppOnboardingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof completeWhatsAppOnboarding>>, TError,CompleteWhatsAppOnboardingMutationVariables, TContext> => {

const mutationKey = getCompleteWhatsAppOnboardingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof completeWhatsAppOnboarding>>, CompleteWhatsAppOnboardingMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  completeWhatsAppOnboarding(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CompleteWhatsAppOnboardingMutationResult = NonNullable<Awaited<ReturnType<typeof completeWhatsAppOnboarding>>>
    export type CompleteWhatsAppOnboardingMutationBody = WhatsAppOnboardingComplete
    export type CompleteWhatsAppOnboardingMutationError = Error
    export type CompleteWhatsAppOnboardingMutationVariables = {data: WhatsAppOnboardingComplete}

    export const useCompleteWhatsAppOnboarding = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof completeWhatsAppOnboarding>>, TError,CompleteWhatsAppOnboardingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof completeWhatsAppOnboarding>>,
        TError,
        CompleteWhatsAppOnboardingMutationVariables,
        TContext
      > => {
      return useMutation(getCompleteWhatsAppOnboardingMutationOptions(options));
    }

export const getListWhatsAppTemplatesUrl = (params?: ListWhatsAppTemplatesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/whatsapp/templates?${stringifiedParams}` : `/api/whatsapp/templates`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.records.read. Owner resolved from authorized server context.
 */
export const listWhatsAppTemplates = async (params?: ListWhatsAppTemplatesParams, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppTemplateList> => {

  return customFetch<WhatsAppTemplateList>(getListWhatsAppTemplatesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWhatsAppTemplatesQueryKey = (params?: ListWhatsAppTemplatesParams,) => {
    return [
    `/api/whatsapp/templates`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWhatsAppTemplatesQueryOptions = <TData = Awaited<ReturnType<typeof listWhatsAppTemplates>>, TError = Error>(params?: ListWhatsAppTemplatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWhatsAppTemplates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWhatsAppTemplatesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWhatsAppTemplates>>> = ({ signal }) => listWhatsAppTemplates(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWhatsAppTemplates>>, TError, TData> & { queryKey: QueryKey }
}

export type ListWhatsAppTemplatesQueryResult = NonNullable<Awaited<ReturnType<typeof listWhatsAppTemplates>>>
export type ListWhatsAppTemplatesQueryError = Error



export function useListWhatsAppTemplates<TData = Awaited<ReturnType<typeof listWhatsAppTemplates>>, TError = Error>(
 params?: ListWhatsAppTemplatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWhatsAppTemplates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListWhatsAppTemplatesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateWhatsAppTemplateUrl = () => {




  return `/api/whatsapp/templates`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.content.manage. Owner resolved from authorized server context.
 */
export const createWhatsAppTemplate = async (whatsAppTemplateCreate: WhatsAppTemplateCreate, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppTemplate> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WhatsAppTemplate>(getCreateWhatsAppTemplateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(whatsAppTemplateCreate)
  }
);}





export const getCreateWhatsAppTemplateMutationKey = () => ['createWhatsAppTemplate'] as const;

export const getCreateWhatsAppTemplateMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWhatsAppTemplate>>, TError,CreateWhatsAppTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createWhatsAppTemplate>>, TError,CreateWhatsAppTemplateMutationVariables, TContext> => {

const mutationKey = getCreateWhatsAppTemplateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createWhatsAppTemplate>>, CreateWhatsAppTemplateMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createWhatsAppTemplate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateWhatsAppTemplateMutationResult = NonNullable<Awaited<ReturnType<typeof createWhatsAppTemplate>>>
    export type CreateWhatsAppTemplateMutationBody = WhatsAppTemplateCreate
    export type CreateWhatsAppTemplateMutationError = Error
    export type CreateWhatsAppTemplateMutationVariables = {data: WhatsAppTemplateCreate}

    export const useCreateWhatsAppTemplate = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWhatsAppTemplate>>, TError,CreateWhatsAppTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createWhatsAppTemplate>>,
        TError,
        CreateWhatsAppTemplateMutationVariables,
        TContext
      > => {
      return useMutation(getCreateWhatsAppTemplateMutationOptions(options));
    }

export const getSyncWhatsAppTemplatesUrl = () => {




  return `/api/whatsapp/templates/sync`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.integrations.manage. Owner resolved from authorized server context.
 */
export const syncWhatsAppTemplates = async (whatsAppRevisionAction: WhatsAppRevisionAction, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppReadiness> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WhatsAppReadiness>(getSyncWhatsAppTemplatesUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(whatsAppRevisionAction)
  }
);}





export const getSyncWhatsAppTemplatesMutationKey = () => ['syncWhatsAppTemplates'] as const;

export const getSyncWhatsAppTemplatesMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof syncWhatsAppTemplates>>, TError,SyncWhatsAppTemplatesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof syncWhatsAppTemplates>>, TError,SyncWhatsAppTemplatesMutationVariables, TContext> => {

const mutationKey = getSyncWhatsAppTemplatesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof syncWhatsAppTemplates>>, SyncWhatsAppTemplatesMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  syncWhatsAppTemplates(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SyncWhatsAppTemplatesMutationResult = NonNullable<Awaited<ReturnType<typeof syncWhatsAppTemplates>>>
    export type SyncWhatsAppTemplatesMutationBody = WhatsAppRevisionAction
    export type SyncWhatsAppTemplatesMutationError = Error
    export type SyncWhatsAppTemplatesMutationVariables = {data: WhatsAppRevisionAction}

    export const useSyncWhatsAppTemplates = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof syncWhatsAppTemplates>>, TError,SyncWhatsAppTemplatesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof syncWhatsAppTemplates>>,
        TError,
        SyncWhatsAppTemplatesMutationVariables,
        TContext
      > => {
      return useMutation(getSyncWhatsAppTemplatesMutationOptions(options));
    }

export const getGetWhatsAppPermissionUrl = (contactId: string,
    params: GetWhatsAppPermissionParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/whatsapp/contacts/${contactId}/permission?${stringifiedParams}` : `/api/whatsapp/contacts/${contactId}/permission`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.records.read. Owner resolved from authorized server context.
 */
export const getWhatsAppPermission = async (contactId: string,
    params: GetWhatsAppPermissionParams, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppPermission> => {

  return customFetch<WhatsAppPermission>(getGetWhatsAppPermissionUrl(contactId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWhatsAppPermissionQueryKey = (contactId: string,
    params?: GetWhatsAppPermissionParams,) => {
    return [
    `/api/whatsapp/contacts/${contactId}/permission`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetWhatsAppPermissionQueryOptions = <TData = Awaited<ReturnType<typeof getWhatsAppPermission>>, TError = Error>(contactId: string,
    params: GetWhatsAppPermissionParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppPermission>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWhatsAppPermissionQueryKey(contactId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWhatsAppPermission>>> = ({ signal }) => getWhatsAppPermission(contactId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: contactId !== null && contactId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppPermission>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWhatsAppPermissionQueryResult = NonNullable<Awaited<ReturnType<typeof getWhatsAppPermission>>>
export type GetWhatsAppPermissionQueryError = Error



export function useGetWhatsAppPermission<TData = Awaited<ReturnType<typeof getWhatsAppPermission>>, TError = Error>(
 contactId: string,
    params: GetWhatsAppPermissionParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppPermission>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWhatsAppPermissionQueryOptions(contactId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRecordWhatsAppPermissionUrl = (contactId: string,) => {




  return `/api/whatsapp/contacts/${contactId}/permission`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.records.write. Owner resolved from authorized server context.
 */
export const recordWhatsAppPermission = async (contactId: string,
    whatsAppPermissionRecord: WhatsAppPermissionRecord, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppPermission> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WhatsAppPermission>(getRecordWhatsAppPermissionUrl(contactId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(whatsAppPermissionRecord)
  }
);}





export const getRecordWhatsAppPermissionMutationKey = () => ['recordWhatsAppPermission'] as const;

export const getRecordWhatsAppPermissionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recordWhatsAppPermission>>, TError,RecordWhatsAppPermissionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof recordWhatsAppPermission>>, TError,RecordWhatsAppPermissionMutationVariables, TContext> => {

const mutationKey = getRecordWhatsAppPermissionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof recordWhatsAppPermission>>, RecordWhatsAppPermissionMutationVariables> = (props) => {
          const {contactId,data} = props ?? {};

          return  recordWhatsAppPermission(contactId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RecordWhatsAppPermissionMutationResult = NonNullable<Awaited<ReturnType<typeof recordWhatsAppPermission>>>
    export type RecordWhatsAppPermissionMutationBody = WhatsAppPermissionRecord
    export type RecordWhatsAppPermissionMutationError = Error
    export type RecordWhatsAppPermissionMutationVariables = {contactId: string;data: WhatsAppPermissionRecord}

    export const useRecordWhatsAppPermission = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof recordWhatsAppPermission>>, TError,RecordWhatsAppPermissionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof recordWhatsAppPermission>>,
        TError,
        RecordWhatsAppPermissionMutationVariables,
        TContext
      > => {
      return useMutation(getRecordWhatsAppPermissionMutationOptions(options));
    }

export const getWithdrawWhatsAppPermissionUrl = (contactId: string,) => {




  return `/api/whatsapp/contacts/${contactId}/permission/withdraw`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.records.write. Owner resolved from authorized server context.
 */
export const withdrawWhatsAppPermission = async (contactId: string,
    whatsAppPermissionWithdrawal: WhatsAppPermissionWithdrawal, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppPermission> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WhatsAppPermission>(getWithdrawWhatsAppPermissionUrl(contactId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(whatsAppPermissionWithdrawal)
  }
);}





export const getWithdrawWhatsAppPermissionMutationKey = () => ['withdrawWhatsAppPermission'] as const;

export const getWithdrawWhatsAppPermissionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof withdrawWhatsAppPermission>>, TError,WithdrawWhatsAppPermissionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof withdrawWhatsAppPermission>>, TError,WithdrawWhatsAppPermissionMutationVariables, TContext> => {

const mutationKey = getWithdrawWhatsAppPermissionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof withdrawWhatsAppPermission>>, WithdrawWhatsAppPermissionMutationVariables> = (props) => {
          const {contactId,data} = props ?? {};

          return  withdrawWhatsAppPermission(contactId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type WithdrawWhatsAppPermissionMutationResult = NonNullable<Awaited<ReturnType<typeof withdrawWhatsAppPermission>>>
    export type WithdrawWhatsAppPermissionMutationBody = WhatsAppPermissionWithdrawal
    export type WithdrawWhatsAppPermissionMutationError = Error
    export type WithdrawWhatsAppPermissionMutationVariables = {contactId: string;data: WhatsAppPermissionWithdrawal}

    export const useWithdrawWhatsAppPermission = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof withdrawWhatsAppPermission>>, TError,WithdrawWhatsAppPermissionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof withdrawWhatsAppPermission>>,
        TError,
        WithdrawWhatsAppPermissionMutationVariables,
        TContext
      > => {
      return useMutation(getWithdrawWhatsAppPermissionMutationOptions(options));
    }

export const getListWhatsAppPermissionEvidenceUrl = (contactId: string,
    params?: ListWhatsAppPermissionEvidenceParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/whatsapp/contacts/${contactId}/permission/history?${stringifiedParams}` : `/api/whatsapp/contacts/${contactId}/permission/history`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.records.read. Owner resolved from authorized server context.
 */
export const listWhatsAppPermissionEvidence = async (contactId: string,
    params?: ListWhatsAppPermissionEvidenceParams, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppPermissionEvidenceList> => {

  return customFetch<WhatsAppPermissionEvidenceList>(getListWhatsAppPermissionEvidenceUrl(contactId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWhatsAppPermissionEvidenceQueryKey = (contactId: string,
    params?: ListWhatsAppPermissionEvidenceParams,) => {
    return [
    `/api/whatsapp/contacts/${contactId}/permission/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWhatsAppPermissionEvidenceQueryOptions = <TData = Awaited<ReturnType<typeof listWhatsAppPermissionEvidence>>, TError = Error>(contactId: string,
    params?: ListWhatsAppPermissionEvidenceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWhatsAppPermissionEvidence>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWhatsAppPermissionEvidenceQueryKey(contactId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWhatsAppPermissionEvidence>>> = ({ signal }) => listWhatsAppPermissionEvidence(contactId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: contactId !== null && contactId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWhatsAppPermissionEvidence>>, TError, TData> & { queryKey: QueryKey }
}

export type ListWhatsAppPermissionEvidenceQueryResult = NonNullable<Awaited<ReturnType<typeof listWhatsAppPermissionEvidence>>>
export type ListWhatsAppPermissionEvidenceQueryError = Error



export function useListWhatsAppPermissionEvidence<TData = Awaited<ReturnType<typeof listWhatsAppPermissionEvidence>>, TError = Error>(
 contactId: string,
    params?: ListWhatsAppPermissionEvidenceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWhatsAppPermissionEvidence>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListWhatsAppPermissionEvidenceQueryOptions(contactId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetWhatsAppCampaignStepUrl = (campaignId: string,
    stepId: string,) => {




  return `/api/whatsapp/campaigns/${campaignId}/steps/${stepId}`
}

export const getWhatsAppCampaignStep = async (campaignId: string,
    stepId: string, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppCampaignStep | null> => {

  return customFetch<WhatsAppCampaignStep | null>(getGetWhatsAppCampaignStepUrl(campaignId,stepId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWhatsAppCampaignStepQueryKey = (campaignId: string,
    stepId: string,) => {
    return [
    `/api/whatsapp/campaigns/${campaignId}/steps/${stepId}`
    ] as const;
    }


export const getGetWhatsAppCampaignStepQueryOptions = <TData = Awaited<ReturnType<typeof getWhatsAppCampaignStep>>, TError = void>(campaignId: string,
    stepId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppCampaignStep>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWhatsAppCampaignStepQueryKey(campaignId,stepId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWhatsAppCampaignStep>>> = ({ signal }) => getWhatsAppCampaignStep(campaignId,stepId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: campaignId !== null && campaignId !== undefined && stepId !== null && stepId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppCampaignStep>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWhatsAppCampaignStepQueryResult = NonNullable<Awaited<ReturnType<typeof getWhatsAppCampaignStep>>>
export type GetWhatsAppCampaignStepQueryError = void



export function useGetWhatsAppCampaignStep<TData = Awaited<ReturnType<typeof getWhatsAppCampaignStep>>, TError = void>(
 campaignId: string,
    stepId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppCampaignStep>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWhatsAppCampaignStepQueryOptions(campaignId,stepId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateWhatsAppCampaignStepUrl = (campaignId: string,
    stepId: string,) => {




  return `/api/whatsapp/campaigns/${campaignId}/steps/${stepId}`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.content.manage. Owner resolved from authorized server context.
 */
export const updateWhatsAppCampaignStep = async (campaignId: string,
    stepId: string,
    whatsAppCampaignStepUpdate: WhatsAppCampaignStepUpdate, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppCampaignStep> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WhatsAppCampaignStep>(getUpdateWhatsAppCampaignStepUrl(campaignId,stepId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(whatsAppCampaignStepUpdate)
  }
);}





export const getUpdateWhatsAppCampaignStepMutationKey = () => ['updateWhatsAppCampaignStep'] as const;

export const getUpdateWhatsAppCampaignStepMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWhatsAppCampaignStep>>, TError,UpdateWhatsAppCampaignStepMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateWhatsAppCampaignStep>>, TError,UpdateWhatsAppCampaignStepMutationVariables, TContext> => {

const mutationKey = getUpdateWhatsAppCampaignStepMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateWhatsAppCampaignStep>>, UpdateWhatsAppCampaignStepMutationVariables> = (props) => {
          const {campaignId,stepId,data} = props ?? {};

          return  updateWhatsAppCampaignStep(campaignId,stepId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateWhatsAppCampaignStepMutationResult = NonNullable<Awaited<ReturnType<typeof updateWhatsAppCampaignStep>>>
    export type UpdateWhatsAppCampaignStepMutationBody = WhatsAppCampaignStepUpdate
    export type UpdateWhatsAppCampaignStepMutationError = Error
    export type UpdateWhatsAppCampaignStepMutationVariables = {campaignId: string;stepId: string;data: WhatsAppCampaignStepUpdate}

    export const useUpdateWhatsAppCampaignStep = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWhatsAppCampaignStep>>, TError,UpdateWhatsAppCampaignStepMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateWhatsAppCampaignStep>>,
        TError,
        UpdateWhatsAppCampaignStepMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateWhatsAppCampaignStepMutationOptions(options));
    }

export const getPreviewWhatsAppRecipientsUrl = (campaignId: string,
    stepId: string,
    params?: PreviewWhatsAppRecipientsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/whatsapp/campaigns/${campaignId}/steps/${stepId}/recipients?${stringifiedParams}` : `/api/whatsapp/campaigns/${campaignId}/steps/${stepId}/recipients`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.content.manage. Owner resolved from authorized server context.
 */
export const previewWhatsAppRecipients = async (campaignId: string,
    stepId: string,
    params?: PreviewWhatsAppRecipientsParams, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppRecipientPreview> => {

  return customFetch<WhatsAppRecipientPreview>(getPreviewWhatsAppRecipientsUrl(campaignId,stepId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPreviewWhatsAppRecipientsQueryKey = (campaignId: string,
    stepId: string,
    params?: PreviewWhatsAppRecipientsParams,) => {
    return [
    `/api/whatsapp/campaigns/${campaignId}/steps/${stepId}/recipients`, ...(params ? [params] : [])
    ] as const;
    }


export const getPreviewWhatsAppRecipientsQueryOptions = <TData = Awaited<ReturnType<typeof previewWhatsAppRecipients>>, TError = Error>(campaignId: string,
    stepId: string,
    params?: PreviewWhatsAppRecipientsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof previewWhatsAppRecipients>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPreviewWhatsAppRecipientsQueryKey(campaignId,stepId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof previewWhatsAppRecipients>>> = ({ signal }) => previewWhatsAppRecipients(campaignId,stepId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: campaignId !== null && campaignId !== undefined && stepId !== null && stepId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof previewWhatsAppRecipients>>, TError, TData> & { queryKey: QueryKey }
}

export type PreviewWhatsAppRecipientsQueryResult = NonNullable<Awaited<ReturnType<typeof previewWhatsAppRecipients>>>
export type PreviewWhatsAppRecipientsQueryError = Error



export function usePreviewWhatsAppRecipients<TData = Awaited<ReturnType<typeof previewWhatsAppRecipients>>, TError = Error>(
 campaignId: string,
    stepId: string,
    params?: PreviewWhatsAppRecipientsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof previewWhatsAppRecipients>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getPreviewWhatsAppRecipientsQueryOptions(campaignId,stepId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSendWhatsAppCampaignStepUrl = (campaignId: string,
    stepId: string,) => {




  return `/api/whatsapp/campaigns/${campaignId}/steps/${stepId}/send`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.content.manage. Owner resolved from authorized server context.
 */
export const sendWhatsAppCampaignStep = async (campaignId: string,
    stepId: string,
    whatsAppSendRequest: WhatsAppSendRequest, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppSendResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WhatsAppSendResult>(getSendWhatsAppCampaignStepUrl(campaignId,stepId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(whatsAppSendRequest)
  }
);}





export const getSendWhatsAppCampaignStepMutationKey = () => ['sendWhatsAppCampaignStep'] as const;

export const getSendWhatsAppCampaignStepMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendWhatsAppCampaignStep>>, TError,SendWhatsAppCampaignStepMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof sendWhatsAppCampaignStep>>, TError,SendWhatsAppCampaignStepMutationVariables, TContext> => {

const mutationKey = getSendWhatsAppCampaignStepMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof sendWhatsAppCampaignStep>>, SendWhatsAppCampaignStepMutationVariables> = (props) => {
          const {campaignId,stepId,data} = props ?? {};

          return  sendWhatsAppCampaignStep(campaignId,stepId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SendWhatsAppCampaignStepMutationResult = NonNullable<Awaited<ReturnType<typeof sendWhatsAppCampaignStep>>>
    export type SendWhatsAppCampaignStepMutationBody = WhatsAppSendRequest
    export type SendWhatsAppCampaignStepMutationError = Error
    export type SendWhatsAppCampaignStepMutationVariables = {campaignId: string;stepId: string;data: WhatsAppSendRequest}

    export const useSendWhatsAppCampaignStep = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendWhatsAppCampaignStep>>, TError,SendWhatsAppCampaignStepMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof sendWhatsAppCampaignStep>>,
        TError,
        SendWhatsAppCampaignStepMutationVariables,
        TContext
      > => {
      return useMutation(getSendWhatsAppCampaignStepMutationOptions(options));
    }

export const getReviewWhatsAppCampaignStepUrl = (campaignId: string,
    stepId: string,) => {




  return `/api/whatsapp/campaigns/${campaignId}/steps/${stepId}/review`
}

/**
 * Persist a ten-minute actor-bound review of the entire current recipient set. No send is performed.
 */
export const reviewWhatsAppCampaignStep = async (campaignId: string,
    stepId: string, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppRecipientPreview> => {

  return customFetch<WhatsAppRecipientPreview>(getReviewWhatsAppCampaignStepUrl(campaignId,stepId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getReviewWhatsAppCampaignStepMutationKey = () => ['reviewWhatsAppCampaignStep'] as const;

export const getReviewWhatsAppCampaignStepMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewWhatsAppCampaignStep>>, TError,ReviewWhatsAppCampaignStepMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewWhatsAppCampaignStep>>, TError,ReviewWhatsAppCampaignStepMutationVariables, TContext> => {

const mutationKey = getReviewWhatsAppCampaignStepMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewWhatsAppCampaignStep>>, ReviewWhatsAppCampaignStepMutationVariables> = (props) => {
          const {campaignId,stepId} = props ?? {};

          return  reviewWhatsAppCampaignStep(campaignId,stepId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewWhatsAppCampaignStepMutationResult = NonNullable<Awaited<ReturnType<typeof reviewWhatsAppCampaignStep>>>

    export type ReviewWhatsAppCampaignStepMutationError = unknown
    export type ReviewWhatsAppCampaignStepMutationVariables = {campaignId: string;stepId: string}

    export const useReviewWhatsAppCampaignStep = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewWhatsAppCampaignStep>>, TError,ReviewWhatsAppCampaignStepMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewWhatsAppCampaignStep>>,
        TError,
        ReviewWhatsAppCampaignStepMutationVariables,
        TContext
      > => {
      return useMutation(getReviewWhatsAppCampaignStepMutationOptions(options));
    }

export const getSendWhatsAppTestUrl = () => {




  return `/api/whatsapp/test`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.content.manage. Owner resolved from authorized server context.
 */
export const sendWhatsAppTest = async (whatsAppTestSend: WhatsAppTestSend, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppDelivery> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WhatsAppDelivery>(getSendWhatsAppTestUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(whatsAppTestSend)
  }
);}





export const getSendWhatsAppTestMutationKey = () => ['sendWhatsAppTest'] as const;

export const getSendWhatsAppTestMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendWhatsAppTest>>, TError,SendWhatsAppTestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof sendWhatsAppTest>>, TError,SendWhatsAppTestMutationVariables, TContext> => {

const mutationKey = getSendWhatsAppTestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof sendWhatsAppTest>>, SendWhatsAppTestMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  sendWhatsAppTest(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SendWhatsAppTestMutationResult = NonNullable<Awaited<ReturnType<typeof sendWhatsAppTest>>>
    export type SendWhatsAppTestMutationBody = WhatsAppTestSend
    export type SendWhatsAppTestMutationError = Error
    export type SendWhatsAppTestMutationVariables = {data: WhatsAppTestSend}

    export const useSendWhatsAppTest = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendWhatsAppTest>>, TError,SendWhatsAppTestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof sendWhatsAppTest>>,
        TError,
        SendWhatsAppTestMutationVariables,
        TContext
      > => {
      return useMutation(getSendWhatsAppTestMutationOptions(options));
    }

export const getListWhatsAppDeliveriesUrl = (campaignId: string,
    params?: ListWhatsAppDeliveriesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/whatsapp/campaigns/${campaignId}/deliveries?${stringifiedParams}` : `/api/whatsapp/campaigns/${campaignId}/deliveries`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.content.manage. Owner resolved from authorized server context.
 */
export const listWhatsAppDeliveries = async (campaignId: string,
    params?: ListWhatsAppDeliveriesParams, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppDeliveryList> => {

  return customFetch<WhatsAppDeliveryList>(getListWhatsAppDeliveriesUrl(campaignId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWhatsAppDeliveriesQueryKey = (campaignId: string,
    params?: ListWhatsAppDeliveriesParams,) => {
    return [
    `/api/whatsapp/campaigns/${campaignId}/deliveries`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWhatsAppDeliveriesQueryOptions = <TData = Awaited<ReturnType<typeof listWhatsAppDeliveries>>, TError = Error>(campaignId: string,
    params?: ListWhatsAppDeliveriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWhatsAppDeliveries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWhatsAppDeliveriesQueryKey(campaignId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWhatsAppDeliveries>>> = ({ signal }) => listWhatsAppDeliveries(campaignId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: campaignId !== null && campaignId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWhatsAppDeliveries>>, TError, TData> & { queryKey: QueryKey }
}

export type ListWhatsAppDeliveriesQueryResult = NonNullable<Awaited<ReturnType<typeof listWhatsAppDeliveries>>>
export type ListWhatsAppDeliveriesQueryError = Error



export function useListWhatsAppDeliveries<TData = Awaited<ReturnType<typeof listWhatsAppDeliveries>>, TError = Error>(
 campaignId: string,
    params?: ListWhatsAppDeliveriesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWhatsAppDeliveries>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListWhatsAppDeliveriesQueryOptions(campaignId,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetWhatsAppCampaignSummaryUrl = (campaignId: string,) => {




  return `/api/whatsapp/campaigns/${campaignId}/summary`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.content.manage. Owner resolved from authorized server context.
 */
export const getWhatsAppCampaignSummary = async (campaignId: string, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppCampaignSummary> => {

  return customFetch<WhatsAppCampaignSummary>(getGetWhatsAppCampaignSummaryUrl(campaignId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWhatsAppCampaignSummaryQueryKey = (campaignId: string,) => {
    return [
    `/api/whatsapp/campaigns/${campaignId}/summary`
    ] as const;
    }


export const getGetWhatsAppCampaignSummaryQueryOptions = <TData = Awaited<ReturnType<typeof getWhatsAppCampaignSummary>>, TError = Error>(campaignId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppCampaignSummary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWhatsAppCampaignSummaryQueryKey(campaignId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWhatsAppCampaignSummary>>> = ({ signal }) => getWhatsAppCampaignSummary(campaignId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: campaignId !== null && campaignId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppCampaignSummary>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWhatsAppCampaignSummaryQueryResult = NonNullable<Awaited<ReturnType<typeof getWhatsAppCampaignSummary>>>
export type GetWhatsAppCampaignSummaryQueryError = Error



export function useGetWhatsAppCampaignSummary<TData = Awaited<ReturnType<typeof getWhatsAppCampaignSummary>>, TError = Error>(
 campaignId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppCampaignSummary>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWhatsAppCampaignSummaryQueryOptions(campaignId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPauseWhatsAppCampaignUrl = (campaignId: string,) => {




  return `/api/whatsapp/campaigns/${campaignId}/pause`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.content.manage. Owner resolved from authorized server context.
 */
export const pauseWhatsAppCampaign = async (campaignId: string,
    whatsAppCampaignPause: WhatsAppCampaignPause, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppSendResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WhatsAppSendResult>(getPauseWhatsAppCampaignUrl(campaignId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(whatsAppCampaignPause)
  }
);}





export const getPauseWhatsAppCampaignMutationKey = () => ['pauseWhatsAppCampaign'] as const;

export const getPauseWhatsAppCampaignMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof pauseWhatsAppCampaign>>, TError,PauseWhatsAppCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof pauseWhatsAppCampaign>>, TError,PauseWhatsAppCampaignMutationVariables, TContext> => {

const mutationKey = getPauseWhatsAppCampaignMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof pauseWhatsAppCampaign>>, PauseWhatsAppCampaignMutationVariables> = (props) => {
          const {campaignId,data} = props ?? {};

          return  pauseWhatsAppCampaign(campaignId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PauseWhatsAppCampaignMutationResult = NonNullable<Awaited<ReturnType<typeof pauseWhatsAppCampaign>>>
    export type PauseWhatsAppCampaignMutationBody = WhatsAppCampaignPause
    export type PauseWhatsAppCampaignMutationError = Error
    export type PauseWhatsAppCampaignMutationVariables = {campaignId: string;data: WhatsAppCampaignPause}

    export const usePauseWhatsAppCampaign = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof pauseWhatsAppCampaign>>, TError,PauseWhatsAppCampaignMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof pauseWhatsAppCampaign>>,
        TError,
        PauseWhatsAppCampaignMutationVariables,
        TContext
      > => {
      return useMutation(getPauseWhatsAppCampaignMutationOptions(options));
    }

export const getRetryWhatsAppDeliveryUrl = (id: string,) => {




  return `/api/whatsapp/deliveries/${id}/retry`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.content.manage. Owner resolved from authorized server context.
 */
export const retryWhatsAppDelivery = async (id: string,
    whatsAppRevisionAction: WhatsAppRevisionAction, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppDelivery> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WhatsAppDelivery>(getRetryWhatsAppDeliveryUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(whatsAppRevisionAction)
  }
);}





export const getRetryWhatsAppDeliveryMutationKey = () => ['retryWhatsAppDelivery'] as const;

export const getRetryWhatsAppDeliveryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryWhatsAppDelivery>>, TError,RetryWhatsAppDeliveryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof retryWhatsAppDelivery>>, TError,RetryWhatsAppDeliveryMutationVariables, TContext> => {

const mutationKey = getRetryWhatsAppDeliveryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof retryWhatsAppDelivery>>, RetryWhatsAppDeliveryMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  retryWhatsAppDelivery(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RetryWhatsAppDeliveryMutationResult = NonNullable<Awaited<ReturnType<typeof retryWhatsAppDelivery>>>
    export type RetryWhatsAppDeliveryMutationBody = WhatsAppRevisionAction
    export type RetryWhatsAppDeliveryMutationError = Error
    export type RetryWhatsAppDeliveryMutationVariables = {id: string;data: WhatsAppRevisionAction}

    export const useRetryWhatsAppDelivery = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retryWhatsAppDelivery>>, TError,RetryWhatsAppDeliveryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof retryWhatsAppDelivery>>,
        TError,
        RetryWhatsAppDeliveryMutationVariables,
        TContext
      > => {
      return useMutation(getRetryWhatsAppDeliveryMutationOptions(options));
    }

export const getListWhatsAppConversationsUrl = (params?: ListWhatsAppConversationsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/whatsapp/conversations?${stringifiedParams}` : `/api/whatsapp/conversations`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.records.read. Owner resolved from authorized server context.
 */
export const listWhatsAppConversations = async (params?: ListWhatsAppConversationsParams, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppConversationList> => {

  return customFetch<WhatsAppConversationList>(getListWhatsAppConversationsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWhatsAppConversationsQueryKey = (params?: ListWhatsAppConversationsParams,) => {
    return [
    `/api/whatsapp/conversations`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWhatsAppConversationsQueryOptions = <TData = Awaited<ReturnType<typeof listWhatsAppConversations>>, TError = Error>(params?: ListWhatsAppConversationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWhatsAppConversations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWhatsAppConversationsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWhatsAppConversations>>> = ({ signal }) => listWhatsAppConversations(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWhatsAppConversations>>, TError, TData> & { queryKey: QueryKey }
}

export type ListWhatsAppConversationsQueryResult = NonNullable<Awaited<ReturnType<typeof listWhatsAppConversations>>>
export type ListWhatsAppConversationsQueryError = Error



export function useListWhatsAppConversations<TData = Awaited<ReturnType<typeof listWhatsAppConversations>>, TError = Error>(
 params?: ListWhatsAppConversationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWhatsAppConversations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListWhatsAppConversationsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetWhatsAppConversationUrl = (id: string,) => {




  return `/api/whatsapp/conversations/${id}`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.records.read. Owner resolved from authorized server context.
 */
export const getWhatsAppConversation = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppConversation> => {

  return customFetch<WhatsAppConversation>(getGetWhatsAppConversationUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWhatsAppConversationQueryKey = (id: string,) => {
    return [
    `/api/whatsapp/conversations/${id}`
    ] as const;
    }


export const getGetWhatsAppConversationQueryOptions = <TData = Awaited<ReturnType<typeof getWhatsAppConversation>>, TError = Error>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppConversation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWhatsAppConversationQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWhatsAppConversation>>> = ({ signal }) => getWhatsAppConversation(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppConversation>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWhatsAppConversationQueryResult = NonNullable<Awaited<ReturnType<typeof getWhatsAppConversation>>>
export type GetWhatsAppConversationQueryError = Error



export function useGetWhatsAppConversation<TData = Awaited<ReturnType<typeof getWhatsAppConversation>>, TError = Error>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppConversation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWhatsAppConversationQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateWhatsAppConversationUrl = (id: string,) => {




  return `/api/whatsapp/conversations/${id}`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.records.write. Owner resolved from authorized server context.
 */
export const updateWhatsAppConversation = async (id: string,
    whatsAppConversationUpdate: WhatsAppConversationUpdate, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppConversation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WhatsAppConversation>(getUpdateWhatsAppConversationUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(whatsAppConversationUpdate)
  }
);}





export const getUpdateWhatsAppConversationMutationKey = () => ['updateWhatsAppConversation'] as const;

export const getUpdateWhatsAppConversationMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWhatsAppConversation>>, TError,UpdateWhatsAppConversationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateWhatsAppConversation>>, TError,UpdateWhatsAppConversationMutationVariables, TContext> => {

const mutationKey = getUpdateWhatsAppConversationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateWhatsAppConversation>>, UpdateWhatsAppConversationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateWhatsAppConversation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateWhatsAppConversationMutationResult = NonNullable<Awaited<ReturnType<typeof updateWhatsAppConversation>>>
    export type UpdateWhatsAppConversationMutationBody = WhatsAppConversationUpdate
    export type UpdateWhatsAppConversationMutationError = Error
    export type UpdateWhatsAppConversationMutationVariables = {id: string;data: WhatsAppConversationUpdate}

    export const useUpdateWhatsAppConversation = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWhatsAppConversation>>, TError,UpdateWhatsAppConversationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateWhatsAppConversation>>,
        TError,
        UpdateWhatsAppConversationMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateWhatsAppConversationMutationOptions(options));
    }

export const getListWhatsAppMessagesUrl = (id: string,
    params?: ListWhatsAppMessagesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/whatsapp/conversations/${id}/messages?${stringifiedParams}` : `/api/whatsapp/conversations/${id}/messages`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.records.read. Owner resolved from authorized server context.
 */
export const listWhatsAppMessages = async (id: string,
    params?: ListWhatsAppMessagesParams, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppMessageList> => {

  return customFetch<WhatsAppMessageList>(getListWhatsAppMessagesUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListWhatsAppMessagesQueryKey = (id: string,
    params?: ListWhatsAppMessagesParams,) => {
    return [
    `/api/whatsapp/conversations/${id}/messages`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWhatsAppMessagesQueryOptions = <TData = Awaited<ReturnType<typeof listWhatsAppMessages>>, TError = Error>(id: string,
    params?: ListWhatsAppMessagesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWhatsAppMessages>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWhatsAppMessagesQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWhatsAppMessages>>> = ({ signal }) => listWhatsAppMessages(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWhatsAppMessages>>, TError, TData> & { queryKey: QueryKey }
}

export type ListWhatsAppMessagesQueryResult = NonNullable<Awaited<ReturnType<typeof listWhatsAppMessages>>>
export type ListWhatsAppMessagesQueryError = Error



export function useListWhatsAppMessages<TData = Awaited<ReturnType<typeof listWhatsAppMessages>>, TError = Error>(
 id: string,
    params?: ListWhatsAppMessagesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listWhatsAppMessages>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListWhatsAppMessagesQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getMarkWhatsAppConversationReadUrl = (id: string,) => {




  return `/api/whatsapp/conversations/${id}/read`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.records.read. Owner resolved from authorized server context.
 */
export const markWhatsAppConversationRead = async (id: string,
    whatsAppMarkRead: WhatsAppMarkRead, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppConversation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WhatsAppConversation>(getMarkWhatsAppConversationReadUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(whatsAppMarkRead)
  }
);}





export const getMarkWhatsAppConversationReadMutationKey = () => ['markWhatsAppConversationRead'] as const;

export const getMarkWhatsAppConversationReadMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markWhatsAppConversationRead>>, TError,MarkWhatsAppConversationReadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof markWhatsAppConversationRead>>, TError,MarkWhatsAppConversationReadMutationVariables, TContext> => {

const mutationKey = getMarkWhatsAppConversationReadMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof markWhatsAppConversationRead>>, MarkWhatsAppConversationReadMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  markWhatsAppConversationRead(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MarkWhatsAppConversationReadMutationResult = NonNullable<Awaited<ReturnType<typeof markWhatsAppConversationRead>>>
    export type MarkWhatsAppConversationReadMutationBody = WhatsAppMarkRead
    export type MarkWhatsAppConversationReadMutationError = Error
    export type MarkWhatsAppConversationReadMutationVariables = {id: string;data: WhatsAppMarkRead}

    export const useMarkWhatsAppConversationRead = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markWhatsAppConversationRead>>, TError,MarkWhatsAppConversationReadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof markWhatsAppConversationRead>>,
        TError,
        MarkWhatsAppConversationReadMutationVariables,
        TContext
      > => {
      return useMutation(getMarkWhatsAppConversationReadMutationOptions(options));
    }

export const getReplyWhatsAppConversationUrl = (id: string,) => {




  return `/api/whatsapp/conversations/${id}/reply`
}

/**
 * Foundation contract; service enabled in ordered follow-up task. Requires entity.records.write. Owner resolved from authorized server context.
 */
export const replyWhatsAppConversation = async (id: string,
    whatsAppStaffReply: WhatsAppStaffReply, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppDelivery> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WhatsAppDelivery>(getReplyWhatsAppConversationUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(whatsAppStaffReply)
  }
);}





export const getReplyWhatsAppConversationMutationKey = () => ['replyWhatsAppConversation'] as const;

export const getReplyWhatsAppConversationMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof replyWhatsAppConversation>>, TError,ReplyWhatsAppConversationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof replyWhatsAppConversation>>, TError,ReplyWhatsAppConversationMutationVariables, TContext> => {

const mutationKey = getReplyWhatsAppConversationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof replyWhatsAppConversation>>, ReplyWhatsAppConversationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  replyWhatsAppConversation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReplyWhatsAppConversationMutationResult = NonNullable<Awaited<ReturnType<typeof replyWhatsAppConversation>>>
    export type ReplyWhatsAppConversationMutationBody = WhatsAppStaffReply
    export type ReplyWhatsAppConversationMutationError = Error
    export type ReplyWhatsAppConversationMutationVariables = {id: string;data: WhatsAppStaffReply}

    export const useReplyWhatsAppConversation = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof replyWhatsAppConversation>>, TError,ReplyWhatsAppConversationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof replyWhatsAppConversation>>,
        TError,
        ReplyWhatsAppConversationMutationVariables,
        TContext
      > => {
      return useMutation(getReplyWhatsAppConversationMutationOptions(options));
    }

export const getVerifyWhatsAppWebhookUrl = (params: VerifyWhatsAppWebhookParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/whatsapp/webhooks/meta?${stringifiedParams}` : `/api/whatsapp/webhooks/meta`
}

/**
 * Validate provider challenge with server secret; no entity selector.
 */
export const verifyWhatsAppWebhook = async (params: VerifyWhatsAppWebhookParams, options?: Parameters<typeof customFetch>[1]): Promise<string> => {

  return customFetch<string>(getVerifyWhatsAppWebhookUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getVerifyWhatsAppWebhookQueryKey = (params?: VerifyWhatsAppWebhookParams,) => {
    return [
    `/api/whatsapp/webhooks/meta`, ...(params ? [params] : [])
    ] as const;
    }


export const getVerifyWhatsAppWebhookQueryOptions = <TData = Awaited<ReturnType<typeof verifyWhatsAppWebhook>>, TError = void>(params: VerifyWhatsAppWebhookParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof verifyWhatsAppWebhook>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getVerifyWhatsAppWebhookQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof verifyWhatsAppWebhook>>> = ({ signal }) => verifyWhatsAppWebhook(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof verifyWhatsAppWebhook>>, TError, TData> & { queryKey: QueryKey }
}

export type VerifyWhatsAppWebhookQueryResult = NonNullable<Awaited<ReturnType<typeof verifyWhatsAppWebhook>>>
export type VerifyWhatsAppWebhookQueryError = void



export function useVerifyWhatsAppWebhook<TData = Awaited<ReturnType<typeof verifyWhatsAppWebhook>>, TError = void>(
 params: VerifyWhatsAppWebhookParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof verifyWhatsAppWebhook>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getVerifyWhatsAppWebhookQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReceiveWhatsAppWebhookUrl = () => {




  return `/api/whatsapp/webhooks/meta`
}

/**
 * Verify raw-body signature before parsing; resolve entity from verified globally unique sender mapping; bounded events, no automated reply.
 */
export const receiveWhatsAppWebhook = async (whatsAppWebhookEnvelope: WhatsAppWebhookEnvelope, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<void>(getReceiveWhatsAppWebhookUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(whatsAppWebhookEnvelope)
  }
);}





export const getReceiveWhatsAppWebhookMutationKey = () => ['receiveWhatsAppWebhook'] as const;

export const getReceiveWhatsAppWebhookMutationOptions = <TError = void | Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof receiveWhatsAppWebhook>>, TError,ReceiveWhatsAppWebhookMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof receiveWhatsAppWebhook>>, TError,ReceiveWhatsAppWebhookMutationVariables, TContext> => {

const mutationKey = getReceiveWhatsAppWebhookMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof receiveWhatsAppWebhook>>, ReceiveWhatsAppWebhookMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  receiveWhatsAppWebhook(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReceiveWhatsAppWebhookMutationResult = NonNullable<Awaited<ReturnType<typeof receiveWhatsAppWebhook>>>
    export type ReceiveWhatsAppWebhookMutationBody = WhatsAppWebhookEnvelope
    export type ReceiveWhatsAppWebhookMutationError = void | Error
    export type ReceiveWhatsAppWebhookMutationVariables = {data: WhatsAppWebhookEnvelope}

    export const useReceiveWhatsAppWebhook = <TError = void | Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof receiveWhatsAppWebhook>>, TError,ReceiveWhatsAppWebhookMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof receiveWhatsAppWebhook>>,
        TError,
        ReceiveWhatsAppWebhookMutationVariables,
        TContext
      > => {
      return useMutation(getReceiveWhatsAppWebhookMutationOptions(options));
    }

export const getGetWhatsAppMediaContentUrl = (id: string,) => {




  return `/api/whatsapp/messages/${id}/content`
}

/**
 * Download privately stored supported media with current business records access.
 */
export const getWhatsAppMediaContent = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getGetWhatsAppMediaContentUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWhatsAppMediaContentQueryKey = (id: string,) => {
    return [
    `/api/whatsapp/messages/${id}/content`
    ] as const;
    }


export const getGetWhatsAppMediaContentQueryOptions = <TData = Awaited<ReturnType<typeof getWhatsAppMediaContent>>, TError = void>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppMediaContent>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWhatsAppMediaContentQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWhatsAppMediaContent>>> = ({ signal }) => getWhatsAppMediaContent(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppMediaContent>>, TError, TData> & { queryKey: QueryKey }
}

export type GetWhatsAppMediaContentQueryResult = NonNullable<Awaited<ReturnType<typeof getWhatsAppMediaContent>>>
export type GetWhatsAppMediaContentQueryError = void



export function useGetWhatsAppMediaContent<TData = Awaited<ReturnType<typeof getWhatsAppMediaContent>>, TError = void>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getWhatsAppMediaContent>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetWhatsAppMediaContentQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getOptOutWhatsAppConversationUrl = (id: string,) => {




  return `/api/whatsapp/conversations/${id}/opt-out`
}

/**
 * Record a staff-marked WhatsApp marketing opt-out without changing other channels.
 */
export const optOutWhatsAppConversation = async (id: string,
    whatsAppRevisionAction: WhatsAppRevisionAction, options?: Parameters<typeof customFetch>[1]): Promise<WhatsAppConversation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WhatsAppConversation>(getOptOutWhatsAppConversationUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(whatsAppRevisionAction)
  }
);}





export const getOptOutWhatsAppConversationMutationKey = () => ['optOutWhatsAppConversation'] as const;

export const getOptOutWhatsAppConversationMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof optOutWhatsAppConversation>>, TError,OptOutWhatsAppConversationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof optOutWhatsAppConversation>>, TError,OptOutWhatsAppConversationMutationVariables, TContext> => {

const mutationKey = getOptOutWhatsAppConversationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof optOutWhatsAppConversation>>, OptOutWhatsAppConversationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  optOutWhatsAppConversation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type OptOutWhatsAppConversationMutationResult = NonNullable<Awaited<ReturnType<typeof optOutWhatsAppConversation>>>
    export type OptOutWhatsAppConversationMutationBody = WhatsAppRevisionAction
    export type OptOutWhatsAppConversationMutationError = void
    export type OptOutWhatsAppConversationMutationVariables = {id: string;data: WhatsAppRevisionAction}

    export const useOptOutWhatsAppConversation = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof optOutWhatsAppConversation>>, TError,OptOutWhatsAppConversationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof optOutWhatsAppConversation>>,
        TError,
        OptOutWhatsAppConversationMutationVariables,
        TContext
      > => {
      return useMutation(getOptOutWhatsAppConversationMutationOptions(options));
    }

export const getListAgentDocumentReviewsUrl = (params: ListAgentDocumentReviewsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/document-reviews?${stringifiedParams}` : `/api/agent/document-reviews`
}

export const listAgentDocumentReviews = async (params: ListAgentDocumentReviewsParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentDocumentReviewPage> => {

  return customFetch<AgentDocumentReviewPage>(getListAgentDocumentReviewsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAgentDocumentReviewsQueryKey = (params?: ListAgentDocumentReviewsParams,) => {
    return [
    `/api/agent/document-reviews`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAgentDocumentReviewsQueryOptions = <TData = Awaited<ReturnType<typeof listAgentDocumentReviews>>, TError = unknown>(params: ListAgentDocumentReviewsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentDocumentReviews>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAgentDocumentReviewsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgentDocumentReviews>>> = ({ signal }) => listAgentDocumentReviews(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAgentDocumentReviews>>, TError, TData> & { queryKey: QueryKey }
}

export type ListAgentDocumentReviewsQueryResult = NonNullable<Awaited<ReturnType<typeof listAgentDocumentReviews>>>
export type ListAgentDocumentReviewsQueryError = unknown



export function useListAgentDocumentReviews<TData = Awaited<ReturnType<typeof listAgentDocumentReviews>>, TError = unknown>(
 params: ListAgentDocumentReviewsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listAgentDocumentReviews>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListAgentDocumentReviewsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateAgentDocumentReviewUrl = () => {




  return `/api/agent/document-reviews`
}

export const createAgentDocumentReview = async (agentDocumentReviewCreate: AgentDocumentReviewCreate, options?: Parameters<typeof customFetch>[1]): Promise<AgentDocumentReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentDocumentReview>(getCreateAgentDocumentReviewUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentDocumentReviewCreate)
  }
);}





export const getCreateAgentDocumentReviewMutationKey = () => ['createAgentDocumentReview'] as const;

export const getCreateAgentDocumentReviewMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAgentDocumentReview>>, TError,CreateAgentDocumentReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createAgentDocumentReview>>, TError,CreateAgentDocumentReviewMutationVariables, TContext> => {

const mutationKey = getCreateAgentDocumentReviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAgentDocumentReview>>, CreateAgentDocumentReviewMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createAgentDocumentReview(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateAgentDocumentReviewMutationResult = NonNullable<Awaited<ReturnType<typeof createAgentDocumentReview>>>
    export type CreateAgentDocumentReviewMutationBody = AgentDocumentReviewCreate
    export type CreateAgentDocumentReviewMutationError = unknown
    export type CreateAgentDocumentReviewMutationVariables = {data: AgentDocumentReviewCreate}

    export const useCreateAgentDocumentReview = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAgentDocumentReview>>, TError,CreateAgentDocumentReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createAgentDocumentReview>>,
        TError,
        CreateAgentDocumentReviewMutationVariables,
        TContext
      > => {
      return useMutation(getCreateAgentDocumentReviewMutationOptions(options));
    }

export const getGetAgentDocumentReviewUrl = (id: string,
    params: GetAgentDocumentReviewParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/agent/document-reviews/${id}?${stringifiedParams}` : `/api/agent/document-reviews/${id}`
}

export const getAgentDocumentReview = async (id: string,
    params: GetAgentDocumentReviewParams, options?: Parameters<typeof customFetch>[1]): Promise<AgentDocumentReview> => {

  return customFetch<AgentDocumentReview>(getGetAgentDocumentReviewUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAgentDocumentReviewQueryKey = (id: string,
    params?: GetAgentDocumentReviewParams,) => {
    return [
    `/api/agent/document-reviews/${id}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetAgentDocumentReviewQueryOptions = <TData = Awaited<ReturnType<typeof getAgentDocumentReview>>, TError = unknown>(id: string,
    params: GetAgentDocumentReviewParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentDocumentReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAgentDocumentReviewQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAgentDocumentReview>>> = ({ signal }) => getAgentDocumentReview(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAgentDocumentReview>>, TError, TData> & { queryKey: QueryKey }
}

export type GetAgentDocumentReviewQueryResult = NonNullable<Awaited<ReturnType<typeof getAgentDocumentReview>>>
export type GetAgentDocumentReviewQueryError = unknown



export function useGetAgentDocumentReview<TData = Awaited<ReturnType<typeof getAgentDocumentReview>>, TError = unknown>(
 id: string,
    params: GetAgentDocumentReviewParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getAgentDocumentReview>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetAgentDocumentReviewQueryOptions(id,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCancelAgentDocumentReviewUrl = (id: string,) => {




  return `/api/agent/document-reviews/${id}`
}

export const cancelAgentDocumentReview = async (id: string,
    cancelAgentDocumentReviewBody: CancelAgentDocumentReviewBody, options?: Parameters<typeof customFetch>[1]): Promise<AgentDocumentReview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentDocumentReview>(getCancelAgentDocumentReviewUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(cancelAgentDocumentReviewBody)
  }
);}





export const getCancelAgentDocumentReviewMutationKey = () => ['cancelAgentDocumentReview'] as const;

export const getCancelAgentDocumentReviewMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelAgentDocumentReview>>, TError,CancelAgentDocumentReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelAgentDocumentReview>>, TError,CancelAgentDocumentReviewMutationVariables, TContext> => {

const mutationKey = getCancelAgentDocumentReviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelAgentDocumentReview>>, CancelAgentDocumentReviewMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  cancelAgentDocumentReview(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelAgentDocumentReviewMutationResult = NonNullable<Awaited<ReturnType<typeof cancelAgentDocumentReview>>>
    export type CancelAgentDocumentReviewMutationBody = CancelAgentDocumentReviewBody
    export type CancelAgentDocumentReviewMutationError = unknown
    export type CancelAgentDocumentReviewMutationVariables = {id: string;data: CancelAgentDocumentReviewBody}

    export const useCancelAgentDocumentReview = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelAgentDocumentReview>>, TError,CancelAgentDocumentReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelAgentDocumentReview>>,
        TError,
        CancelAgentDocumentReviewMutationVariables,
        TContext
      > => {
      return useMutation(getCancelAgentDocumentReviewMutationOptions(options));
    }

export const getPreviewAgentReviewCommentsUrl = (id: string,) => {




  return `/api/agent/document-reviews/${id}/comments-preview`
}

export const previewAgentReviewComments = async (id: string,
    agentReviewCommentsSelection: AgentReviewCommentsSelection, options?: Parameters<typeof customFetch>[1]): Promise<AgentReviewCommentsPreview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentReviewCommentsPreview>(getPreviewAgentReviewCommentsUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentReviewCommentsSelection)
  }
);}





export const getPreviewAgentReviewCommentsMutationKey = () => ['previewAgentReviewComments'] as const;

export const getPreviewAgentReviewCommentsMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewAgentReviewComments>>, TError,PreviewAgentReviewCommentsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewAgentReviewComments>>, TError,PreviewAgentReviewCommentsMutationVariables, TContext> => {

const mutationKey = getPreviewAgentReviewCommentsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewAgentReviewComments>>, PreviewAgentReviewCommentsMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  previewAgentReviewComments(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewAgentReviewCommentsMutationResult = NonNullable<Awaited<ReturnType<typeof previewAgentReviewComments>>>
    export type PreviewAgentReviewCommentsMutationBody = AgentReviewCommentsSelection
    export type PreviewAgentReviewCommentsMutationError = void
    export type PreviewAgentReviewCommentsMutationVariables = {id: string;data: AgentReviewCommentsSelection}

    export const usePreviewAgentReviewComments = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewAgentReviewComments>>, TError,PreviewAgentReviewCommentsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewAgentReviewComments>>,
        TError,
        PreviewAgentReviewCommentsMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewAgentReviewCommentsMutationOptions(options));
    }

export const getApplyAgentReviewCommentsUrl = (id: string,) => {




  return `/api/agent/document-reviews/${id}/comments`
}

export const applyAgentReviewComments = async (id: string,
    agentReviewCommentsApply: AgentReviewCommentsApply, options?: Parameters<typeof customFetch>[1]): Promise<AgentReviewCommentsReceipt> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentReviewCommentsReceipt>(getApplyAgentReviewCommentsUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(agentReviewCommentsApply)
  }
);}





export const getApplyAgentReviewCommentsMutationKey = () => ['applyAgentReviewComments'] as const;

export const getApplyAgentReviewCommentsMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyAgentReviewComments>>, TError,ApplyAgentReviewCommentsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof applyAgentReviewComments>>, TError,ApplyAgentReviewCommentsMutationVariables, TContext> => {

const mutationKey = getApplyAgentReviewCommentsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof applyAgentReviewComments>>, ApplyAgentReviewCommentsMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  applyAgentReviewComments(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApplyAgentReviewCommentsMutationResult = NonNullable<Awaited<ReturnType<typeof applyAgentReviewComments>>>
    export type ApplyAgentReviewCommentsMutationBody = AgentReviewCommentsApply
    export type ApplyAgentReviewCommentsMutationError = void
    export type ApplyAgentReviewCommentsMutationVariables = {id: string;data: AgentReviewCommentsApply}

    export const useApplyAgentReviewComments = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyAgentReviewComments>>, TError,ApplyAgentReviewCommentsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof applyAgentReviewComments>>,
        TError,
        ApplyAgentReviewCommentsMutationVariables,
        TContext
      > => {
      return useMutation(getApplyAgentReviewCommentsMutationOptions(options));
    }

export const getListSowSessionsUrl = (params?: ListSowSessionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/sow/sessions?${stringifiedParams}` : `/api/sow/sessions`
}

export const listSowSessions = async (params?: ListSowSessionsParams, options?: Parameters<typeof customFetch>[1]): Promise<ListSowSessions200> => {

  return customFetch<ListSowSessions200>(getListSowSessionsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSowSessionsQueryKey = (params?: ListSowSessionsParams,) => {
    return [
    `/api/sow/sessions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSowSessionsQueryOptions = <TData = Awaited<ReturnType<typeof listSowSessions>>, TError = unknown>(params?: ListSowSessionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSowSessions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSowSessionsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSowSessions>>> = ({ signal }) => listSowSessions(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSowSessions>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSowSessionsQueryResult = NonNullable<Awaited<ReturnType<typeof listSowSessions>>>
export type ListSowSessionsQueryError = unknown



export function useListSowSessions<TData = Awaited<ReturnType<typeof listSowSessions>>, TError = unknown>(
 params?: ListSowSessionsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSowSessions>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSowSessionsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getStartSowSessionUrl = () => {




  return `/api/sow/sessions`
}

export const startSowSession = async (startSowSessionBody: StartSowSessionBody, options?: Parameters<typeof customFetch>[1]): Promise<SowSession> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowSession>(getStartSowSessionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(startSowSessionBody)
  }
);}





export const getStartSowSessionMutationKey = () => ['startSowSession'] as const;

export const getStartSowSessionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startSowSession>>, TError,StartSowSessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof startSowSession>>, TError,StartSowSessionMutationVariables, TContext> => {

const mutationKey = getStartSowSessionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof startSowSession>>, StartSowSessionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  startSowSession(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type StartSowSessionMutationResult = NonNullable<Awaited<ReturnType<typeof startSowSession>>>
    export type StartSowSessionMutationBody = StartSowSessionBody
    export type StartSowSessionMutationError = unknown
    export type StartSowSessionMutationVariables = {data: StartSowSessionBody}

    export const useStartSowSession = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof startSowSession>>, TError,StartSowSessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof startSowSession>>,
        TError,
        StartSowSessionMutationVariables,
        TContext
      > => {
      return useMutation(getStartSowSessionMutationOptions(options));
    }

export const getGetSowSessionUrl = (id: string,) => {




  return `/api/sow/sessions/${id}`
}

export const getSowSession = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SowSession> => {

  return customFetch<SowSession>(getGetSowSessionUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSowSessionQueryKey = (id: string,) => {
    return [
    `/api/sow/sessions/${id}`
    ] as const;
    }


export const getGetSowSessionQueryOptions = <TData = Awaited<ReturnType<typeof getSowSession>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowSession>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSowSessionQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSowSession>>> = ({ signal }) => getSowSession(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSowSession>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSowSessionQueryResult = NonNullable<Awaited<ReturnType<typeof getSowSession>>>
export type GetSowSessionQueryError = unknown



export function useGetSowSession<TData = Awaited<ReturnType<typeof getSowSession>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowSession>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSowSessionQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateSowSessionUrl = (id: string,) => {




  return `/api/sow/sessions/${id}`
}

export const updateSowSession = async (id: string,
    updateSowSessionBody: UpdateSowSessionBody, options?: Parameters<typeof customFetch>[1]): Promise<SowSession> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowSession>(getUpdateSowSessionUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateSowSessionBody)
  }
);}





export const getUpdateSowSessionMutationKey = () => ['updateSowSession'] as const;

export const getUpdateSowSessionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSowSession>>, TError,UpdateSowSessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateSowSession>>, TError,UpdateSowSessionMutationVariables, TContext> => {

const mutationKey = getUpdateSowSessionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateSowSession>>, UpdateSowSessionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateSowSession(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateSowSessionMutationResult = NonNullable<Awaited<ReturnType<typeof updateSowSession>>>
    export type UpdateSowSessionMutationBody = UpdateSowSessionBody
    export type UpdateSowSessionMutationError = unknown
    export type UpdateSowSessionMutationVariables = {id: string;data: UpdateSowSessionBody}

    export const useUpdateSowSession = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSowSession>>, TError,UpdateSowSessionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateSowSession>>,
        TError,
        UpdateSowSessionMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateSowSessionMutationOptions(options));
    }

export const getPrepareSowSessionReviewUrl = (id: string,) => {




  return `/api/sow/sessions/${id}/review`
}

export const prepareSowSessionReview = async (id: string,
    prepareSowSessionReviewBody: PrepareSowSessionReviewBody, options?: Parameters<typeof customFetch>[1]): Promise<AgentActionProposal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentActionProposal>(getPrepareSowSessionReviewUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(prepareSowSessionReviewBody)
  }
);}





export const getPrepareSowSessionReviewMutationKey = () => ['prepareSowSessionReview'] as const;

export const getPrepareSowSessionReviewMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof prepareSowSessionReview>>, TError,PrepareSowSessionReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof prepareSowSessionReview>>, TError,PrepareSowSessionReviewMutationVariables, TContext> => {

const mutationKey = getPrepareSowSessionReviewMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof prepareSowSessionReview>>, PrepareSowSessionReviewMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  prepareSowSessionReview(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PrepareSowSessionReviewMutationResult = NonNullable<Awaited<ReturnType<typeof prepareSowSessionReview>>>
    export type PrepareSowSessionReviewMutationBody = PrepareSowSessionReviewBody
    export type PrepareSowSessionReviewMutationError = unknown
    export type PrepareSowSessionReviewMutationVariables = {id: string;data: PrepareSowSessionReviewBody}

    export const usePrepareSowSessionReview = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof prepareSowSessionReview>>, TError,PrepareSowSessionReviewMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof prepareSowSessionReview>>,
        TError,
        PrepareSowSessionReviewMutationVariables,
        TContext
      > => {
      return useMutation(getPrepareSowSessionReviewMutationOptions(options));
    }

export const getGetSowApprovalPolicyUrl = (scope: SowApprovalScope,) => {




  return `/api/sow/approval-policy/${scope}`
}

export const getSowApprovalPolicy = async (scope: SowApprovalScope, options?: Parameters<typeof customFetch>[1]): Promise<SowApprovalPolicyView> => {

  return customFetch<SowApprovalPolicyView>(getGetSowApprovalPolicyUrl(scope),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSowApprovalPolicyQueryKey = (scope: SowApprovalScope,) => {
    return [
    `/api/sow/approval-policy/${scope}`
    ] as const;
    }


export const getGetSowApprovalPolicyQueryOptions = <TData = Awaited<ReturnType<typeof getSowApprovalPolicy>>, TError = unknown>(scope: SowApprovalScope, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowApprovalPolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSowApprovalPolicyQueryKey(scope);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSowApprovalPolicy>>> = ({ signal }) => getSowApprovalPolicy(scope, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: scope !== null && scope !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSowApprovalPolicy>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSowApprovalPolicyQueryResult = NonNullable<Awaited<ReturnType<typeof getSowApprovalPolicy>>>
export type GetSowApprovalPolicyQueryError = unknown



export function useGetSowApprovalPolicy<TData = Awaited<ReturnType<typeof getSowApprovalPolicy>>, TError = unknown>(
 scope: SowApprovalScope, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowApprovalPolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSowApprovalPolicyQueryOptions(scope,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getWriteSowApprovalPolicyUrl = (scope: SowApprovalScope,) => {




  return `/api/sow/approval-policy/${scope}`
}

export const writeSowApprovalPolicy = async (scope: SowApprovalScope,
    sowApprovalPolicyWrite: SowApprovalPolicyWrite, options?: Parameters<typeof customFetch>[1]): Promise<SowApprovalPolicyView> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowApprovalPolicyView>(getWriteSowApprovalPolicyUrl(scope),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowApprovalPolicyWrite)
  }
);}





export const getWriteSowApprovalPolicyMutationKey = () => ['writeSowApprovalPolicy'] as const;

export const getWriteSowApprovalPolicyMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writeSowApprovalPolicy>>, TError,WriteSowApprovalPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof writeSowApprovalPolicy>>, TError,WriteSowApprovalPolicyMutationVariables, TContext> => {

const mutationKey = getWriteSowApprovalPolicyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof writeSowApprovalPolicy>>, WriteSowApprovalPolicyMutationVariables> = (props) => {
          const {scope,data} = props ?? {};

          return  writeSowApprovalPolicy(scope,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type WriteSowApprovalPolicyMutationResult = NonNullable<Awaited<ReturnType<typeof writeSowApprovalPolicy>>>
    export type WriteSowApprovalPolicyMutationBody = SowApprovalPolicyWrite
    export type WriteSowApprovalPolicyMutationError = unknown
    export type WriteSowApprovalPolicyMutationVariables = {scope: SowApprovalScope;data: SowApprovalPolicyWrite}

    export const useWriteSowApprovalPolicy = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof writeSowApprovalPolicy>>, TError,WriteSowApprovalPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof writeSowApprovalPolicy>>,
        TError,
        WriteSowApprovalPolicyMutationVariables,
        TContext
      > => {
      return useMutation(getWriteSowApprovalPolicyMutationOptions(options));
    }

export const getListSowApprovalCandidatesUrl = (scope: SowApprovalScope,
    params?: ListSowApprovalCandidatesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/sow/approval-policy/${scope}/approvers?${stringifiedParams}` : `/api/sow/approval-policy/${scope}/approvers`
}

export const listSowApprovalCandidates = async (scope: SowApprovalScope,
    params?: ListSowApprovalCandidatesParams, options?: Parameters<typeof customFetch>[1]): Promise<SowApprovalCandidatePage> => {

  return customFetch<SowApprovalCandidatePage>(getListSowApprovalCandidatesUrl(scope,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSowApprovalCandidatesQueryKey = (scope: SowApprovalScope,
    params?: ListSowApprovalCandidatesParams,) => {
    return [
    `/api/sow/approval-policy/${scope}/approvers`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSowApprovalCandidatesQueryOptions = <TData = Awaited<ReturnType<typeof listSowApprovalCandidates>>, TError = unknown>(scope: SowApprovalScope,
    params?: ListSowApprovalCandidatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSowApprovalCandidates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSowApprovalCandidatesQueryKey(scope,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSowApprovalCandidates>>> = ({ signal }) => listSowApprovalCandidates(scope,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: scope !== null && scope !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSowApprovalCandidates>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSowApprovalCandidatesQueryResult = NonNullable<Awaited<ReturnType<typeof listSowApprovalCandidates>>>
export type ListSowApprovalCandidatesQueryError = unknown



export function useListSowApprovalCandidates<TData = Awaited<ReturnType<typeof listSowApprovalCandidates>>, TError = unknown>(
 scope: SowApprovalScope,
    params?: ListSowApprovalCandidatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSowApprovalCandidates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSowApprovalCandidatesQueryOptions(scope,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetEffectiveSowApprovalPolicyUrl = () => {




  return `/api/sow/approval-policy`
}

export const getEffectiveSowApprovalPolicy = async ( options?: Parameters<typeof customFetch>[1]): Promise<SowApprovalPolicyView> => {

  return customFetch<SowApprovalPolicyView>(getGetEffectiveSowApprovalPolicyUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetEffectiveSowApprovalPolicyQueryKey = () => {
    return [
    `/api/sow/approval-policy`
    ] as const;
    }


export const getGetEffectiveSowApprovalPolicyQueryOptions = <TData = Awaited<ReturnType<typeof getEffectiveSowApprovalPolicy>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEffectiveSowApprovalPolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEffectiveSowApprovalPolicyQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEffectiveSowApprovalPolicy>>> = ({ signal }) => getEffectiveSowApprovalPolicy({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEffectiveSowApprovalPolicy>>, TError, TData> & { queryKey: QueryKey }
}

export type GetEffectiveSowApprovalPolicyQueryResult = NonNullable<Awaited<ReturnType<typeof getEffectiveSowApprovalPolicy>>>
export type GetEffectiveSowApprovalPolicyQueryError = unknown



export function useGetEffectiveSowApprovalPolicy<TData = Awaited<ReturnType<typeof getEffectiveSowApprovalPolicy>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getEffectiveSowApprovalPolicy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetEffectiveSowApprovalPolicyQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListSowEmailEvidenceUrl = (params: ListSowEmailEvidenceParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/sow/email-evidence?${stringifiedParams}` : `/api/sow/email-evidence`
}

export const listSowEmailEvidence = async (params: ListSowEmailEvidenceParams, options?: Parameters<typeof customFetch>[1]): Promise<ListSowEmailEvidence200> => {

  return customFetch<ListSowEmailEvidence200>(getListSowEmailEvidenceUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSowEmailEvidenceQueryKey = (params?: ListSowEmailEvidenceParams,) => {
    return [
    `/api/sow/email-evidence`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSowEmailEvidenceQueryOptions = <TData = Awaited<ReturnType<typeof listSowEmailEvidence>>, TError = unknown>(params: ListSowEmailEvidenceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSowEmailEvidence>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSowEmailEvidenceQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSowEmailEvidence>>> = ({ signal }) => listSowEmailEvidence(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSowEmailEvidence>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSowEmailEvidenceQueryResult = NonNullable<Awaited<ReturnType<typeof listSowEmailEvidence>>>
export type ListSowEmailEvidenceQueryError = unknown



export function useListSowEmailEvidence<TData = Awaited<ReturnType<typeof listSowEmailEvidence>>, TError = unknown>(
 params: ListSowEmailEvidenceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSowEmailEvidence>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSowEmailEvidenceQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateSowQuoteRequestUrl = (id: string,) => {




  return `/api/quotes/${id}/sow-request`
}

export const createSowQuoteRequest = async (id: string,
    sowQuoteRequestInput: SowQuoteRequestInput, options?: Parameters<typeof customFetch>[1]): Promise<SowRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowRequest>(getCreateSowQuoteRequestUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowQuoteRequestInput)
  }
);}





export const getCreateSowQuoteRequestMutationKey = () => ['createSowQuoteRequest'] as const;

export const getCreateSowQuoteRequestMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSowQuoteRequest>>, TError,CreateSowQuoteRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createSowQuoteRequest>>, TError,CreateSowQuoteRequestMutationVariables, TContext> => {

const mutationKey = getCreateSowQuoteRequestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createSowQuoteRequest>>, CreateSowQuoteRequestMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createSowQuoteRequest(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateSowQuoteRequestMutationResult = NonNullable<Awaited<ReturnType<typeof createSowQuoteRequest>>>
    export type CreateSowQuoteRequestMutationBody = SowQuoteRequestInput
    export type CreateSowQuoteRequestMutationError = unknown
    export type CreateSowQuoteRequestMutationVariables = {id: string;data: SowQuoteRequestInput}

    export const useCreateSowQuoteRequest = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSowQuoteRequest>>, TError,CreateSowQuoteRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createSowQuoteRequest>>,
        TError,
        CreateSowQuoteRequestMutationVariables,
        TContext
      > => {
      return useMutation(getCreateSowQuoteRequestMutationOptions(options));
    }

export const getCreateSowEmailRequestUrl = () => {




  return `/api/sow/requests`
}

export const createSowEmailRequest = async (sowEmailRequestInput: SowEmailRequestInput, options?: Parameters<typeof customFetch>[1]): Promise<SowRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowRequest>(getCreateSowEmailRequestUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowEmailRequestInput)
  }
);}





export const getCreateSowEmailRequestMutationKey = () => ['createSowEmailRequest'] as const;

export const getCreateSowEmailRequestMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSowEmailRequest>>, TError,CreateSowEmailRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createSowEmailRequest>>, TError,CreateSowEmailRequestMutationVariables, TContext> => {

const mutationKey = getCreateSowEmailRequestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createSowEmailRequest>>, CreateSowEmailRequestMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createSowEmailRequest(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateSowEmailRequestMutationResult = NonNullable<Awaited<ReturnType<typeof createSowEmailRequest>>>
    export type CreateSowEmailRequestMutationBody = SowEmailRequestInput
    export type CreateSowEmailRequestMutationError = unknown
    export type CreateSowEmailRequestMutationVariables = {data: SowEmailRequestInput}

    export const useCreateSowEmailRequest = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSowEmailRequest>>, TError,CreateSowEmailRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createSowEmailRequest>>,
        TError,
        CreateSowEmailRequestMutationVariables,
        TContext
      > => {
      return useMutation(getCreateSowEmailRequestMutationOptions(options));
    }

export const getListSowRequestsUrl = (params?: ListSowRequestsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/sow/requests?${stringifiedParams}` : `/api/sow/requests`
}

export const listSowRequests = async (params?: ListSowRequestsParams, options?: Parameters<typeof customFetch>[1]): Promise<ListSowRequests200> => {

  return customFetch<ListSowRequests200>(getListSowRequestsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSowRequestsQueryKey = (params?: ListSowRequestsParams,) => {
    return [
    `/api/sow/requests`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSowRequestsQueryOptions = <TData = Awaited<ReturnType<typeof listSowRequests>>, TError = unknown>(params?: ListSowRequestsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSowRequests>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSowRequestsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSowRequests>>> = ({ signal }) => listSowRequests(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSowRequests>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSowRequestsQueryResult = NonNullable<Awaited<ReturnType<typeof listSowRequests>>>
export type ListSowRequestsQueryError = unknown



export function useListSowRequests<TData = Awaited<ReturnType<typeof listSowRequests>>, TError = unknown>(
 params?: ListSowRequestsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSowRequests>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSowRequestsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetSowRequestUrl = (id: string,) => {




  return `/api/sow/requests/${id}`
}

export const getSowRequest = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SowRequest> => {

  return customFetch<SowRequest>(getGetSowRequestUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSowRequestQueryKey = (id: string,) => {
    return [
    `/api/sow/requests/${id}`
    ] as const;
    }


export const getGetSowRequestQueryOptions = <TData = Awaited<ReturnType<typeof getSowRequest>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowRequest>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSowRequestQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSowRequest>>> = ({ signal }) => getSowRequest(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSowRequest>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSowRequestQueryResult = NonNullable<Awaited<ReturnType<typeof getSowRequest>>>
export type GetSowRequestQueryError = unknown



export function useGetSowRequest<TData = Awaited<ReturnType<typeof getSowRequest>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowRequest>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSowRequestQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getAnalyzeSowEmailRequestUrl = (id: string,) => {




  return `/api/sow/requests/${id}/analysis`
}

export const analyzeSowEmailRequest = async (id: string,
    sowRequestAttemptInput: SowRequestAttemptInput, options?: Parameters<typeof customFetch>[1]): Promise<SowRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowRequest>(getAnalyzeSowEmailRequestUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowRequestAttemptInput)
  }
);}





export const getAnalyzeSowEmailRequestMutationKey = () => ['analyzeSowEmailRequest'] as const;

export const getAnalyzeSowEmailRequestMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof analyzeSowEmailRequest>>, TError,AnalyzeSowEmailRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof analyzeSowEmailRequest>>, TError,AnalyzeSowEmailRequestMutationVariables, TContext> => {

const mutationKey = getAnalyzeSowEmailRequestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof analyzeSowEmailRequest>>, AnalyzeSowEmailRequestMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  analyzeSowEmailRequest(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AnalyzeSowEmailRequestMutationResult = NonNullable<Awaited<ReturnType<typeof analyzeSowEmailRequest>>>
    export type AnalyzeSowEmailRequestMutationBody = SowRequestAttemptInput
    export type AnalyzeSowEmailRequestMutationError = unknown
    export type AnalyzeSowEmailRequestMutationVariables = {id: string;data: SowRequestAttemptInput}

    export const useAnalyzeSowEmailRequest = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof analyzeSowEmailRequest>>, TError,AnalyzeSowEmailRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof analyzeSowEmailRequest>>,
        TError,
        AnalyzeSowEmailRequestMutationVariables,
        TContext
      > => {
      return useMutation(getAnalyzeSowEmailRequestMutationOptions(options));
    }

export const getPrepareSowRequestDraftUrl = (id: string,) => {




  return `/api/sow/requests/${id}/review`
}

export const prepareSowRequestDraft = async (id: string,
    sowRequestReviewInput: SowRequestReviewInput, options?: Parameters<typeof customFetch>[1]): Promise<AgentActionProposal> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AgentActionProposal>(getPrepareSowRequestDraftUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowRequestReviewInput)
  }
);}





export const getPrepareSowRequestDraftMutationKey = () => ['prepareSowRequestDraft'] as const;

export const getPrepareSowRequestDraftMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof prepareSowRequestDraft>>, TError,PrepareSowRequestDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof prepareSowRequestDraft>>, TError,PrepareSowRequestDraftMutationVariables, TContext> => {

const mutationKey = getPrepareSowRequestDraftMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof prepareSowRequestDraft>>, PrepareSowRequestDraftMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  prepareSowRequestDraft(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PrepareSowRequestDraftMutationResult = NonNullable<Awaited<ReturnType<typeof prepareSowRequestDraft>>>
    export type PrepareSowRequestDraftMutationBody = SowRequestReviewInput
    export type PrepareSowRequestDraftMutationError = unknown
    export type PrepareSowRequestDraftMutationVariables = {id: string;data: SowRequestReviewInput}

    export const usePrepareSowRequestDraft = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof prepareSowRequestDraft>>, TError,PrepareSowRequestDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof prepareSowRequestDraft>>,
        TError,
        PrepareSowRequestDraftMutationVariables,
        TContext
      > => {
      return useMutation(getPrepareSowRequestDraftMutationOptions(options));
    }

export const getGetSowTemplateSourceUrl = (params?: GetSowTemplateSourceParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/sow/template-source?${stringifiedParams}` : `/api/sow/template-source`
}

export const getSowTemplateSource = async (params?: GetSowTemplateSourceParams, options?: Parameters<typeof customFetch>[1]): Promise<GetSowTemplateSource200> => {

  return customFetch<GetSowTemplateSource200>(getGetSowTemplateSourceUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSowTemplateSourceQueryKey = (params?: GetSowTemplateSourceParams,) => {
    return [
    `/api/sow/template-source`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetSowTemplateSourceQueryOptions = <TData = Awaited<ReturnType<typeof getSowTemplateSource>>, TError = unknown>(params?: GetSowTemplateSourceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowTemplateSource>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSowTemplateSourceQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSowTemplateSource>>> = ({ signal }) => getSowTemplateSource(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSowTemplateSource>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSowTemplateSourceQueryResult = NonNullable<Awaited<ReturnType<typeof getSowTemplateSource>>>
export type GetSowTemplateSourceQueryError = unknown



export function useGetSowTemplateSource<TData = Awaited<ReturnType<typeof getSowTemplateSource>>, TError = unknown>(
 params?: GetSowTemplateSourceParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowTemplateSource>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSowTemplateSourceQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListSowPreparationsUrl = (params?: ListSowPreparationsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/sow/preparations?${stringifiedParams}` : `/api/sow/preparations`
}

/**
 * Shared, entity-scoped SOW preparation records visible to current record readers. Opening the list never creates a record, thread, document or template.
 */
export const listSowPreparations = async (params?: ListSowPreparationsParams, options?: Parameters<typeof customFetch>[1]): Promise<SowPreparationPage> => {

  return customFetch<SowPreparationPage>(getListSowPreparationsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSowPreparationsQueryKey = (params?: ListSowPreparationsParams,) => {
    return [
    `/api/sow/preparations`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSowPreparationsQueryOptions = <TData = Awaited<ReturnType<typeof listSowPreparations>>, TError = unknown>(params?: ListSowPreparationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSowPreparations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSowPreparationsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSowPreparations>>> = ({ signal }) => listSowPreparations(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSowPreparations>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSowPreparationsQueryResult = NonNullable<Awaited<ReturnType<typeof listSowPreparations>>>
export type ListSowPreparationsQueryError = unknown



export function useListSowPreparations<TData = Awaited<ReturnType<typeof listSowPreparations>>, TError = unknown>(
 params?: ListSowPreparationsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSowPreparations>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSowPreparationsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateSowPreparationUrl = () => {




  return `/api/sow/preparations`
}

/**
 * Explicitly creates a shared preparation pinned to an exact template version. Replaying the same clientRequestId returns the original outcome; a different body is rejected.
 */
export const createSowPreparation = async (sowPreparationCreate: SowPreparationCreate, options?: Parameters<typeof customFetch>[1]): Promise<SowPreparationMutation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowPreparationMutation>(getCreateSowPreparationUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowPreparationCreate)
  }
);}





export const getCreateSowPreparationMutationKey = () => ['createSowPreparation'] as const;

export const getCreateSowPreparationMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSowPreparation>>, TError,CreateSowPreparationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createSowPreparation>>, TError,CreateSowPreparationMutationVariables, TContext> => {

const mutationKey = getCreateSowPreparationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createSowPreparation>>, CreateSowPreparationMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createSowPreparation(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateSowPreparationMutationResult = NonNullable<Awaited<ReturnType<typeof createSowPreparation>>>
    export type CreateSowPreparationMutationBody = SowPreparationCreate
    export type CreateSowPreparationMutationError = void
    export type CreateSowPreparationMutationVariables = {data: SowPreparationCreate}

    export const useCreateSowPreparation = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSowPreparation>>, TError,CreateSowPreparationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createSowPreparation>>,
        TError,
        CreateSowPreparationMutationVariables,
        TContext
      > => {
      return useMutation(getCreateSowPreparationMutationOptions(options));
    }

export const getGetSowPreparationUrl = (id: string,) => {




  return `/api/sow/preparations/${id}`
}

export const getSowPreparation = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SowPreparation> => {

  return customFetch<SowPreparation>(getGetSowPreparationUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSowPreparationQueryKey = (id: string,) => {
    return [
    `/api/sow/preparations/${id}`
    ] as const;
    }


export const getGetSowPreparationQueryOptions = <TData = Awaited<ReturnType<typeof getSowPreparation>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowPreparation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSowPreparationQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSowPreparation>>> = ({ signal }) => getSowPreparation(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSowPreparation>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSowPreparationQueryResult = NonNullable<Awaited<ReturnType<typeof getSowPreparation>>>
export type GetSowPreparationQueryError = unknown



export function useGetSowPreparation<TData = Awaited<ReturnType<typeof getSowPreparation>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowPreparation>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSowPreparationQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateSowPreparationUrl = (id: string,) => {




  return `/api/sow/preparations/${id}`
}

/**
 * Compare-and-swap update by a current record writer. A stale expectedRevision retains the current record.
 */
export const updateSowPreparation = async (id: string,
    sowPreparationUpdate: SowPreparationUpdate, options?: Parameters<typeof customFetch>[1]): Promise<SowPreparationMutation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowPreparationMutation>(getUpdateSowPreparationUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowPreparationUpdate)
  }
);}





export const getUpdateSowPreparationMutationKey = () => ['updateSowPreparation'] as const;

export const getUpdateSowPreparationMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSowPreparation>>, TError,UpdateSowPreparationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateSowPreparation>>, TError,UpdateSowPreparationMutationVariables, TContext> => {

const mutationKey = getUpdateSowPreparationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateSowPreparation>>, UpdateSowPreparationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateSowPreparation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateSowPreparationMutationResult = NonNullable<Awaited<ReturnType<typeof updateSowPreparation>>>
    export type UpdateSowPreparationMutationBody = SowPreparationUpdate
    export type UpdateSowPreparationMutationError = void
    export type UpdateSowPreparationMutationVariables = {id: string;data: SowPreparationUpdate}

    export const useUpdateSowPreparation = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSowPreparation>>, TError,UpdateSowPreparationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof updateSowPreparation>>,
        TError,
        UpdateSowPreparationMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateSowPreparationMutationOptions(options));
    }

export const getLinkSowPreparationDocumentUrl = (id: string,) => {




  return `/api/sow/preparations/${id}/document`
}

/**
 * Explicitly links an existing template-bound canonical Files SOW under its original ID. No document, chat or history is copied and document access is not broadened.
 */
export const linkSowPreparationDocument = async (id: string,
    sowPreparationDocumentLink: SowPreparationDocumentLink, options?: Parameters<typeof customFetch>[1]): Promise<SowPreparationMutation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowPreparationMutation>(getLinkSowPreparationDocumentUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowPreparationDocumentLink)
  }
);}





export const getLinkSowPreparationDocumentMutationKey = () => ['linkSowPreparationDocument'] as const;

export const getLinkSowPreparationDocumentMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof linkSowPreparationDocument>>, TError,LinkSowPreparationDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof linkSowPreparationDocument>>, TError,LinkSowPreparationDocumentMutationVariables, TContext> => {

const mutationKey = getLinkSowPreparationDocumentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof linkSowPreparationDocument>>, LinkSowPreparationDocumentMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  linkSowPreparationDocument(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type LinkSowPreparationDocumentMutationResult = NonNullable<Awaited<ReturnType<typeof linkSowPreparationDocument>>>
    export type LinkSowPreparationDocumentMutationBody = SowPreparationDocumentLink
    export type LinkSowPreparationDocumentMutationError = void
    export type LinkSowPreparationDocumentMutationVariables = {id: string;data: SowPreparationDocumentLink}

    export const useLinkSowPreparationDocument = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof linkSowPreparationDocument>>, TError,LinkSowPreparationDocumentMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof linkSowPreparationDocument>>,
        TError,
        LinkSowPreparationDocumentMutationVariables,
        TContext
      > => {
      return useMutation(getLinkSowPreparationDocumentMutationOptions(options));
    }

export const getArchiveSowPreparationUrl = (id: string,) => {




  return `/api/sow/preparations/${id}/archive`
}

/**
 * Archives the preparation only. Linked Files documents, their versions, approvals and history are preserved.
 */
export const archiveSowPreparation = async (id: string,
    sowPreparationArchive: SowPreparationArchive, options?: Parameters<typeof customFetch>[1]): Promise<SowPreparationMutation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowPreparationMutation>(getArchiveSowPreparationUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowPreparationArchive)
  }
);}





export const getArchiveSowPreparationMutationKey = () => ['archiveSowPreparation'] as const;

export const getArchiveSowPreparationMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof archiveSowPreparation>>, TError,ArchiveSowPreparationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof archiveSowPreparation>>, TError,ArchiveSowPreparationMutationVariables, TContext> => {

const mutationKey = getArchiveSowPreparationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof archiveSowPreparation>>, ArchiveSowPreparationMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  archiveSowPreparation(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ArchiveSowPreparationMutationResult = NonNullable<Awaited<ReturnType<typeof archiveSowPreparation>>>
    export type ArchiveSowPreparationMutationBody = SowPreparationArchive
    export type ArchiveSowPreparationMutationError = void
    export type ArchiveSowPreparationMutationVariables = {id: string;data: SowPreparationArchive}

    export const useArchiveSowPreparation = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof archiveSowPreparation>>, TError,ArchiveSowPreparationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof archiveSowPreparation>>,
        TError,
        ArchiveSowPreparationMutationVariables,
        TContext
      > => {
      return useMutation(getArchiveSowPreparationMutationOptions(options));
    }

export const getGetSowPreparationScopeUrl = (id: string,) => {




  return `/api/sow/preparations/${id}/scope`
}

/**
 * Read-only suggested and confirmed SOW scope with exact brief/evidence provenance, current freshness for the caller and visible gaps. Excerpts the caller can no longer access are withheld. Never creates or analyses anything.
 */
export const getSowPreparationScope = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SowPreparationScope> => {

  return customFetch<SowPreparationScope>(getGetSowPreparationScopeUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSowPreparationScopeQueryKey = (id: string,) => {
    return [
    `/api/sow/preparations/${id}/scope`
    ] as const;
    }


export const getGetSowPreparationScopeQueryOptions = <TData = Awaited<ReturnType<typeof getSowPreparationScope>>, TError = void>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowPreparationScope>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSowPreparationScopeQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSowPreparationScope>>> = ({ signal }) => getSowPreparationScope(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSowPreparationScope>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSowPreparationScopeQueryResult = NonNullable<Awaited<ReturnType<typeof getSowPreparationScope>>>
export type GetSowPreparationScopeQueryError = void



export function useGetSowPreparationScope<TData = Awaited<ReturnType<typeof getSowPreparationScope>>, TError = void>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowPreparationScope>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSowPreparationScopeQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSuggestSowPreparationScopeUrl = (id: string,) => {




  return `/api/sow/preparations/${id}/scope/suggestions`
}

/**
 * Explicitly analyses the saved brief and, optionally, the caller's own reviewed evidence snapshot from this preparation's workspace. Produces suggestions only; unconfirmed items replace earlier unconfirmed suggestions and confirmed items are kept. Missing values stay as unanswered questions.
 */
export const suggestSowPreparationScope = async (id: string,
    sowScopeSuggest: SowScopeSuggest, options?: Parameters<typeof customFetch>[1]): Promise<SowScopeMutation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowScopeMutation>(getSuggestSowPreparationScopeUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowScopeSuggest)
  }
);}





export const getSuggestSowPreparationScopeMutationKey = () => ['suggestSowPreparationScope'] as const;

export const getSuggestSowPreparationScopeMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof suggestSowPreparationScope>>, TError,SuggestSowPreparationScopeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof suggestSowPreparationScope>>, TError,SuggestSowPreparationScopeMutationVariables, TContext> => {

const mutationKey = getSuggestSowPreparationScopeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof suggestSowPreparationScope>>, SuggestSowPreparationScopeMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  suggestSowPreparationScope(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SuggestSowPreparationScopeMutationResult = NonNullable<Awaited<ReturnType<typeof suggestSowPreparationScope>>>
    export type SuggestSowPreparationScopeMutationBody = SowScopeSuggest
    export type SuggestSowPreparationScopeMutationError = void
    export type SuggestSowPreparationScopeMutationVariables = {id: string;data: SowScopeSuggest}

    export const useSuggestSowPreparationScope = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof suggestSowPreparationScope>>, TError,SuggestSowPreparationScopeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof suggestSowPreparationScope>>,
        TError,
        SuggestSowPreparationScopeMutationVariables,
        TContext
      > => {
      return useMutation(getSuggestSowPreparationScopeMutationOptions(options));
    }

export const getAddSowPreparationScopeItemUrl = (id: string,) => {




  return `/api/sow/preparations/${id}/scope/items`
}

/**
 * Adds one staff-authored suggested scope item with an optional verbatim brief excerpt or one cited passage from the caller's reviewed evidence snapshot. It stays suggested until explicitly confirmed.
 */
export const addSowPreparationScopeItem = async (id: string,
    sowScopeItemCreate: SowScopeItemCreate, options?: Parameters<typeof customFetch>[1]): Promise<SowScopeMutation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowScopeMutation>(getAddSowPreparationScopeItemUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowScopeItemCreate)
  }
);}





export const getAddSowPreparationScopeItemMutationKey = () => ['addSowPreparationScopeItem'] as const;

export const getAddSowPreparationScopeItemMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addSowPreparationScopeItem>>, TError,AddSowPreparationScopeItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof addSowPreparationScopeItem>>, TError,AddSowPreparationScopeItemMutationVariables, TContext> => {

const mutationKey = getAddSowPreparationScopeItemMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof addSowPreparationScopeItem>>, AddSowPreparationScopeItemMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  addSowPreparationScopeItem(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AddSowPreparationScopeItemMutationResult = NonNullable<Awaited<ReturnType<typeof addSowPreparationScopeItem>>>
    export type AddSowPreparationScopeItemMutationBody = SowScopeItemCreate
    export type AddSowPreparationScopeItemMutationError = void
    export type AddSowPreparationScopeItemMutationVariables = {id: string;data: SowScopeItemCreate}

    export const useAddSowPreparationScopeItem = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addSowPreparationScopeItem>>, TError,AddSowPreparationScopeItemMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof addSowPreparationScopeItem>>,
        TError,
        AddSowPreparationScopeItemMutationVariables,
        TContext
      > => {
      return useMutation(getAddSowPreparationScopeItemMutationOptions(options));
    }

export const getReviewSowPreparationScopeUrl = (id: string,) => {




  return `/api/sow/preparations/${id}/scope/review`
}

/**
 * Explicitly confirms or rejects exact reviewed scope items by item hash, recording the actor. Stale or unavailable evidence and unanswered questions cannot be confirmed.
 */
export const reviewSowPreparationScope = async (id: string,
    sowScopeReview: SowScopeReview, options?: Parameters<typeof customFetch>[1]): Promise<SowScopeMutation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowScopeMutation>(getReviewSowPreparationScopeUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowScopeReview)
  }
);}





export const getReviewSowPreparationScopeMutationKey = () => ['reviewSowPreparationScope'] as const;

export const getReviewSowPreparationScopeMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewSowPreparationScope>>, TError,ReviewSowPreparationScopeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewSowPreparationScope>>, TError,ReviewSowPreparationScopeMutationVariables, TContext> => {

const mutationKey = getReviewSowPreparationScopeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewSowPreparationScope>>, ReviewSowPreparationScopeMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reviewSowPreparationScope(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewSowPreparationScopeMutationResult = NonNullable<Awaited<ReturnType<typeof reviewSowPreparationScope>>>
    export type ReviewSowPreparationScopeMutationBody = SowScopeReview
    export type ReviewSowPreparationScopeMutationError = void
    export type ReviewSowPreparationScopeMutationVariables = {id: string;data: SowScopeReview}

    export const useReviewSowPreparationScope = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewSowPreparationScope>>, TError,ReviewSowPreparationScopeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviewSowPreparationScope>>,
        TError,
        ReviewSowPreparationScopeMutationVariables,
        TContext
      > => {
      return useMutation(getReviewSowPreparationScopeMutationOptions(options));
    }

export const getGetSowPreparationPlanUrl = (id: string,) => {




  return `/api/sow/preparations/${id}/plan`
}

/**
 * Read-only currently approved reviewed preparation plan for this SOW, with its exact pins, linked human activities and whether current template, document, scope, evidence, owners or model policy still match. The private Agent review and execution receipt are never included. Never creates anything.
 */
export const getSowPreparationPlan = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SowPreparationPlanStatus> => {

  return customFetch<SowPreparationPlanStatus>(getGetSowPreparationPlanUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSowPreparationPlanQueryKey = (id: string,) => {
    return [
    `/api/sow/preparations/${id}/plan`
    ] as const;
    }


export const getGetSowPreparationPlanQueryOptions = <TData = Awaited<ReturnType<typeof getSowPreparationPlan>>, TError = void>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowPreparationPlan>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSowPreparationPlanQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSowPreparationPlan>>> = ({ signal }) => getSowPreparationPlan(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSowPreparationPlan>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSowPreparationPlanQueryResult = NonNullable<Awaited<ReturnType<typeof getSowPreparationPlan>>>
export type GetSowPreparationPlanQueryError = void



export function useGetSowPreparationPlan<TData = Awaited<ReturnType<typeof getSowPreparationPlan>>, TError = void>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowPreparationPlan>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSowPreparationPlanQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPreviewSowPreparationPlanUrl = (id: string,) => {




  return `/api/sow/preparations/${id}/plan/preview`
}

/**
 * Builds the exact reviewed SOW preparation plan without any write, provider call or task creation. Shows the document target, confirmed scope and evidence fingerprint, unresolved gaps, proposed human activities with explicitly chosen owners/dates and the current model eligibility. Confirmation happens only through the private reviewed Agent action entity.sow.preparation_plan.
 */
export const previewSowPreparationPlan = async (id: string,
    sowPreparationPlanInput: SowPreparationPlanInput, options?: Parameters<typeof customFetch>[1]): Promise<SowPreparationPlanPreview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowPreparationPlanPreview>(getPreviewSowPreparationPlanUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowPreparationPlanInput)
  }
);}





export const getPreviewSowPreparationPlanMutationKey = () => ['previewSowPreparationPlan'] as const;

export const getPreviewSowPreparationPlanMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewSowPreparationPlan>>, TError,PreviewSowPreparationPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewSowPreparationPlan>>, TError,PreviewSowPreparationPlanMutationVariables, TContext> => {

const mutationKey = getPreviewSowPreparationPlanMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewSowPreparationPlan>>, PreviewSowPreparationPlanMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  previewSowPreparationPlan(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewSowPreparationPlanMutationResult = NonNullable<Awaited<ReturnType<typeof previewSowPreparationPlan>>>
    export type PreviewSowPreparationPlanMutationBody = SowPreparationPlanInput
    export type PreviewSowPreparationPlanMutationError = void
    export type PreviewSowPreparationPlanMutationVariables = {id: string;data: SowPreparationPlanInput}

    export const usePreviewSowPreparationPlan = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewSowPreparationPlan>>, TError,PreviewSowPreparationPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewSowPreparationPlan>>,
        TError,
        PreviewSowPreparationPlanMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewSowPreparationPlanMutationOptions(options));
    }

export const getConfirmSowPreparationPlanUrl = (id: string,) => {




  return `/api/sow/preparations/${id}/plan/confirm`
}

/**
 * Explicitly confirms the exact previewed SOW preparation plan. The server rebuilds the preview and requires the identical snapshot hash, current preparation revision, SOW write and Agent permission. Only then does it save the approved plan pins and create or reassign the listed human activities through the native activity owner. Never approves terms, publishes, generates content or assigns an owner that was not explicitly chosen. Reusing the same clientRequestId replays the original outcome.
 */
export const confirmSowPreparationPlan = async (id: string,
    sowPlanConfirm: SowPlanConfirm, options?: Parameters<typeof customFetch>[1]): Promise<SowPlanConfirmResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowPlanConfirmResult>(getConfirmSowPreparationPlanUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowPlanConfirm)
  }
);}





export const getConfirmSowPreparationPlanMutationKey = () => ['confirmSowPreparationPlan'] as const;

export const getConfirmSowPreparationPlanMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmSowPreparationPlan>>, TError,ConfirmSowPreparationPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmSowPreparationPlan>>, TError,ConfirmSowPreparationPlanMutationVariables, TContext> => {

const mutationKey = getConfirmSowPreparationPlanMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmSowPreparationPlan>>, ConfirmSowPreparationPlanMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  confirmSowPreparationPlan(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmSowPreparationPlanMutationResult = NonNullable<Awaited<ReturnType<typeof confirmSowPreparationPlan>>>
    export type ConfirmSowPreparationPlanMutationBody = SowPlanConfirm
    export type ConfirmSowPreparationPlanMutationError = void
    export type ConfirmSowPreparationPlanMutationVariables = {id: string;data: SowPlanConfirm}

    export const useConfirmSowPreparationPlan = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmSowPreparationPlan>>, TError,ConfirmSowPreparationPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof confirmSowPreparationPlan>>,
        TError,
        ConfirmSowPreparationPlanMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmSowPreparationPlanMutationOptions(options));
    }

export const getGetSowPreparationDraftingUrl = (id: string,) => {




  return `/api/sow/preparations/${id}/drafting`
}

/**
 * Read-only grounded SOW drafting state. Lists recent proposal runs with their exact template, confirmed scope, document and lesson pins, per-proposal staleness against the saved document, citations re-read under the caller's own access (inaccessible excerpts are withheld), unresolved gaps and an advisory completeness review. Advisory review never approves or publishes; the canonical Files/SOW approval policy remains the approval owner. Never calls a provider or writes.
 */
export const getSowPreparationDrafting = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SowPreparationDrafting> => {

  return customFetch<SowPreparationDrafting>(getGetSowPreparationDraftingUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSowPreparationDraftingQueryKey = (id: string,) => {
    return [
    `/api/sow/preparations/${id}/drafting`
    ] as const;
    }


export const getGetSowPreparationDraftingQueryOptions = <TData = Awaited<ReturnType<typeof getSowPreparationDrafting>>, TError = void>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowPreparationDrafting>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSowPreparationDraftingQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSowPreparationDrafting>>> = ({ signal }) => getSowPreparationDrafting(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSowPreparationDrafting>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSowPreparationDraftingQueryResult = NonNullable<Awaited<ReturnType<typeof getSowPreparationDrafting>>>
export type GetSowPreparationDraftingQueryError = void



export function useGetSowPreparationDrafting<TData = Awaited<ReturnType<typeof getSowPreparationDrafting>>, TError = void>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowPreparationDrafting>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSowPreparationDraftingQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getProposeSowPreparationDraftUrl = (id: string,) => {




  return `/api/sow/preparations/${id}/drafting/proposals`
}

/**
 * Explicitly generates grounded section proposals for the template-editable portions of this SOW from confirmed scope, its currently accessible evidence, the exact pinned template, applicable approved lessons and an optional instruction. Missing scope, deliverables, acceptance, dates and prices stay unresolved. Pricing, quote lineage, protected clauses and signatures are never proposed. Nothing changes the document until a proposal is explicitly adopted. Reusing the clientRequestId replays the original outcome; an unconfirmed earlier attempt is never re-sent to the provider.
 */
export const proposeSowPreparationDraft = async (id: string,
    sowDraftProposeInput: SowDraftProposeInput, options?: Parameters<typeof customFetch>[1]): Promise<SowDraftMutation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowDraftMutation>(getProposeSowPreparationDraftUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowDraftProposeInput)
  }
);}





export const getProposeSowPreparationDraftMutationKey = () => ['proposeSowPreparationDraft'] as const;

export const getProposeSowPreparationDraftMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof proposeSowPreparationDraft>>, TError,ProposeSowPreparationDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof proposeSowPreparationDraft>>, TError,ProposeSowPreparationDraftMutationVariables, TContext> => {

const mutationKey = getProposeSowPreparationDraftMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof proposeSowPreparationDraft>>, ProposeSowPreparationDraftMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  proposeSowPreparationDraft(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ProposeSowPreparationDraftMutationResult = NonNullable<Awaited<ReturnType<typeof proposeSowPreparationDraft>>>
    export type ProposeSowPreparationDraftMutationBody = SowDraftProposeInput
    export type ProposeSowPreparationDraftMutationError = void
    export type ProposeSowPreparationDraftMutationVariables = {id: string;data: SowDraftProposeInput}

    export const useProposeSowPreparationDraft = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof proposeSowPreparationDraft>>, TError,ProposeSowPreparationDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof proposeSowPreparationDraft>>,
        TError,
        ProposeSowPreparationDraftMutationVariables,
        TContext
      > => {
      return useMutation(getProposeSowPreparationDraftMutationOptions(options));
    }

export const getDecideSowDraftProposalUrl = (id: string,
    proposalId: string,) => {




  return `/api/sow/preparations/${id}/drafting/proposals/${proposalId}/decision`
}

/**
 * Explicitly adopts or rejects one exact proposal. Adoption re-checks current write authority, template, confirmed scope, lessons and the proposal's saved target value inside the canonical transaction, then saves through the Files SOW owner (native rendering, protected terms, quote and pricing checks) and creates a checkpoint. Manual edits to other sections are preserved; a changed target is rejected as stale. Saving resets native review readiness; adoption never approves or publishes. Reusing the clientRequestId replays the original outcome.
 */
export const decideSowDraftProposal = async (id: string,
    proposalId: string,
    sowDraftDecisionInput: SowDraftDecisionInput, options?: Parameters<typeof customFetch>[1]): Promise<SowDraftMutation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowDraftMutation>(getDecideSowDraftProposalUrl(id,proposalId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowDraftDecisionInput)
  }
);}





export const getDecideSowDraftProposalMutationKey = () => ['decideSowDraftProposal'] as const;

export const getDecideSowDraftProposalMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideSowDraftProposal>>, TError,DecideSowDraftProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof decideSowDraftProposal>>, TError,DecideSowDraftProposalMutationVariables, TContext> => {

const mutationKey = getDecideSowDraftProposalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof decideSowDraftProposal>>, DecideSowDraftProposalMutationVariables> = (props) => {
          const {id,proposalId,data} = props ?? {};

          return  decideSowDraftProposal(id,proposalId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DecideSowDraftProposalMutationResult = NonNullable<Awaited<ReturnType<typeof decideSowDraftProposal>>>
    export type DecideSowDraftProposalMutationBody = SowDraftDecisionInput
    export type DecideSowDraftProposalMutationError = void
    export type DecideSowDraftProposalMutationVariables = {id: string;proposalId: string;data: SowDraftDecisionInput}

    export const useDecideSowDraftProposal = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideSowDraftProposal>>, TError,DecideSowDraftProposalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof decideSowDraftProposal>>,
        TError,
        DecideSowDraftProposalMutationVariables,
        TContext
      > => {
      return useMutation(getDecideSowDraftProposalMutationOptions(options));
    }

export const getCancelSowPreparationDraftUrl = (id: string,) => {




  return `/api/sow/preparations/${id}/drafting/cancel`
}

/**
 * Cancels the caller's own in-flight drafting request. A late provider result for that request is discarded and never saved. Earlier proposals are kept.
 */
export const cancelSowPreparationDraft = async (id: string,
    sowDraftCancelInput: SowDraftCancelInput, options?: Parameters<typeof customFetch>[1]): Promise<SowPreparationDrafting> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowPreparationDrafting>(getCancelSowPreparationDraftUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowDraftCancelInput)
  }
);}





export const getCancelSowPreparationDraftMutationKey = () => ['cancelSowPreparationDraft'] as const;

export const getCancelSowPreparationDraftMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelSowPreparationDraft>>, TError,CancelSowPreparationDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelSowPreparationDraft>>, TError,CancelSowPreparationDraftMutationVariables, TContext> => {

const mutationKey = getCancelSowPreparationDraftMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelSowPreparationDraft>>, CancelSowPreparationDraftMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  cancelSowPreparationDraft(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelSowPreparationDraftMutationResult = NonNullable<Awaited<ReturnType<typeof cancelSowPreparationDraft>>>
    export type CancelSowPreparationDraftMutationBody = SowDraftCancelInput
    export type CancelSowPreparationDraftMutationError = void
    export type CancelSowPreparationDraftMutationVariables = {id: string;data: SowDraftCancelInput}

    export const useCancelSowPreparationDraft = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelSowPreparationDraft>>, TError,CancelSowPreparationDraftMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof cancelSowPreparationDraft>>,
        TError,
        CancelSowPreparationDraftMutationVariables,
        TContext
      > => {
      return useMutation(getCancelSowPreparationDraftMutationOptions(options));
    }

export const getGetSowPreparationOutcomeUrl = (clientRequestId: string,) => {




  return `/api/sow/preparation-outcomes/${clientRequestId}`
}

/**
 * Reads the caller's own original preparation request outcome after an uncertain response, without replaying it.
 */
export const getSowPreparationOutcome = async (clientRequestId: string, options?: Parameters<typeof customFetch>[1]): Promise<SowPreparationMutation> => {

  return customFetch<SowPreparationMutation>(getGetSowPreparationOutcomeUrl(clientRequestId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSowPreparationOutcomeQueryKey = (clientRequestId: string,) => {
    return [
    `/api/sow/preparation-outcomes/${clientRequestId}`
    ] as const;
    }


export const getGetSowPreparationOutcomeQueryOptions = <TData = Awaited<ReturnType<typeof getSowPreparationOutcome>>, TError = void>(clientRequestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowPreparationOutcome>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSowPreparationOutcomeQueryKey(clientRequestId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSowPreparationOutcome>>> = ({ signal }) => getSowPreparationOutcome(clientRequestId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: clientRequestId !== null && clientRequestId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSowPreparationOutcome>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSowPreparationOutcomeQueryResult = NonNullable<Awaited<ReturnType<typeof getSowPreparationOutcome>>>
export type GetSowPreparationOutcomeQueryError = void



export function useGetSowPreparationOutcome<TData = Awaited<ReturnType<typeof getSowPreparationOutcome>>, TError = void>(
 clientRequestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowPreparationOutcome>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSowPreparationOutcomeQueryOptions(clientRequestId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListSowPreparationDocumentsUrl = (params?: ListSowPreparationDocumentsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/sow/preparation-documents?${stringifiedParams}` : `/api/sow/preparation-documents`
}

/**
 * Discovers existing canonical Files SOW documents in the current entity under their original IDs, with any active preparation link. Never creates or links anything.
 */
export const listSowPreparationDocuments = async (params?: ListSowPreparationDocumentsParams, options?: Parameters<typeof customFetch>[1]): Promise<SowPreparationDocumentPage> => {

  return customFetch<SowPreparationDocumentPage>(getListSowPreparationDocumentsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSowPreparationDocumentsQueryKey = (params?: ListSowPreparationDocumentsParams,) => {
    return [
    `/api/sow/preparation-documents`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSowPreparationDocumentsQueryOptions = <TData = Awaited<ReturnType<typeof listSowPreparationDocuments>>, TError = unknown>(params?: ListSowPreparationDocumentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSowPreparationDocuments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSowPreparationDocumentsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSowPreparationDocuments>>> = ({ signal }) => listSowPreparationDocuments(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSowPreparationDocuments>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSowPreparationDocumentsQueryResult = NonNullable<Awaited<ReturnType<typeof listSowPreparationDocuments>>>
export type ListSowPreparationDocumentsQueryError = unknown



export function useListSowPreparationDocuments<TData = Awaited<ReturnType<typeof listSowPreparationDocuments>>, TError = unknown>(
 params?: ListSowPreparationDocumentsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSowPreparationDocuments>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSowPreparationDocumentsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListSowTemplatesUrl = (params?: ListSowTemplatesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/sow/templates?${stringifiedParams}` : `/api/sow/templates`
}

/**
 * Scoped immutable template versions, newest first. The optional search matches the template key or custom template name.
 */
export const listSowTemplates = async (params?: ListSowTemplatesParams, options?: Parameters<typeof customFetch>[1]): Promise<SowTemplatePage> => {

  return customFetch<SowTemplatePage>(getListSowTemplatesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSowTemplatesQueryKey = (params?: ListSowTemplatesParams,) => {
    return [
    `/api/sow/templates`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSowTemplatesQueryOptions = <TData = Awaited<ReturnType<typeof listSowTemplates>>, TError = unknown>(params?: ListSowTemplatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSowTemplates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSowTemplatesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSowTemplates>>> = ({ signal }) => listSowTemplates(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSowTemplates>>, TError, TData> & { queryKey: QueryKey }
}

export type ListSowTemplatesQueryResult = NonNullable<Awaited<ReturnType<typeof listSowTemplates>>>
export type ListSowTemplatesQueryError = unknown



export function useListSowTemplates<TData = Awaited<ReturnType<typeof listSowTemplates>>, TError = unknown>(
 params?: ListSowTemplatesParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listSowTemplates>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListSowTemplatesQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateSowTemplateUrl = () => {




  return `/api/sow/templates`
}

/**
 * A current Circle T entity administrator creates version 1 of an additional custom SOW template from exact reviewed content. Replaying the same reviewed request returns the original version; a different request for an existing key is rejected.
 */
export const createSowTemplate = async (sowCustomTemplateCreate: SowCustomTemplateCreate, options?: Parameters<typeof customFetch>[1]): Promise<SowTemplate> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowTemplate>(getCreateSowTemplateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowCustomTemplateCreate)
  }
);}





export const getCreateSowTemplateMutationKey = () => ['createSowTemplate'] as const;

export const getCreateSowTemplateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSowTemplate>>, TError,CreateSowTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createSowTemplate>>, TError,CreateSowTemplateMutationVariables, TContext> => {

const mutationKey = getCreateSowTemplateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createSowTemplate>>, CreateSowTemplateMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createSowTemplate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateSowTemplateMutationResult = NonNullable<Awaited<ReturnType<typeof createSowTemplate>>>
    export type CreateSowTemplateMutationBody = SowCustomTemplateCreate
    export type CreateSowTemplateMutationError = unknown
    export type CreateSowTemplateMutationVariables = {data: SowCustomTemplateCreate}

    export const useCreateSowTemplate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSowTemplate>>, TError,CreateSowTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof createSowTemplate>>,
        TError,
        CreateSowTemplateMutationVariables,
        TContext
      > => {
      return useMutation(getCreateSowTemplateMutationOptions(options));
    }

export const getPreviewSowTemplateDefinitionUrl = () => {




  return `/api/sow/templates/preview`
}

/**
 * Validate and deterministically render a proposed custom template with clearly labelled sample facts. Performs no business write.
 */
export const previewSowTemplateDefinition = async (sowCustomTemplatePreviewInput: SowCustomTemplatePreviewInput, options?: Parameters<typeof customFetch>[1]): Promise<SowCustomTemplatePreview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowCustomTemplatePreview>(getPreviewSowTemplateDefinitionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowCustomTemplatePreviewInput)
  }
);}





export const getPreviewSowTemplateDefinitionMutationKey = () => ['previewSowTemplateDefinition'] as const;

export const getPreviewSowTemplateDefinitionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewSowTemplateDefinition>>, TError,PreviewSowTemplateDefinitionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewSowTemplateDefinition>>, TError,PreviewSowTemplateDefinitionMutationVariables, TContext> => {

const mutationKey = getPreviewSowTemplateDefinitionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewSowTemplateDefinition>>, PreviewSowTemplateDefinitionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  previewSowTemplateDefinition(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewSowTemplateDefinitionMutationResult = NonNullable<Awaited<ReturnType<typeof previewSowTemplateDefinition>>>
    export type PreviewSowTemplateDefinitionMutationBody = SowCustomTemplatePreviewInput
    export type PreviewSowTemplateDefinitionMutationError = unknown
    export type PreviewSowTemplateDefinitionMutationVariables = {data: SowCustomTemplatePreviewInput}

    export const usePreviewSowTemplateDefinition = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewSowTemplateDefinition>>, TError,PreviewSowTemplateDefinitionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewSowTemplateDefinition>>,
        TError,
        PreviewSowTemplateDefinitionMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewSowTemplateDefinitionMutationOptions(options));
    }

export const getReviseCustomSowTemplateUrl = (id: string,) => {




  return `/api/sow/templates/${id}/versions`
}

/**
 * Append the next immutable version of a custom template from exact reviewed content. Requires the current version, a change reason and a current Circle T administrator. Existing documents stay pinned.
 */
export const reviseCustomSowTemplate = async (id: string,
    sowCustomTemplateRevision: SowCustomTemplateRevision, options?: Parameters<typeof customFetch>[1]): Promise<SowTemplate> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowTemplate>(getReviseCustomSowTemplateUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowCustomTemplateRevision)
  }
);}





export const getReviseCustomSowTemplateMutationKey = () => ['reviseCustomSowTemplate'] as const;

export const getReviseCustomSowTemplateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviseCustomSowTemplate>>, TError,ReviseCustomSowTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviseCustomSowTemplate>>, TError,ReviseCustomSowTemplateMutationVariables, TContext> => {

const mutationKey = getReviseCustomSowTemplateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviseCustomSowTemplate>>, ReviseCustomSowTemplateMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reviseCustomSowTemplate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviseCustomSowTemplateMutationResult = NonNullable<Awaited<ReturnType<typeof reviseCustomSowTemplate>>>
    export type ReviseCustomSowTemplateMutationBody = SowCustomTemplateRevision
    export type ReviseCustomSowTemplateMutationError = unknown
    export type ReviseCustomSowTemplateMutationVariables = {id: string;data: SowCustomTemplateRevision}

    export const useReviseCustomSowTemplate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviseCustomSowTemplate>>, TError,ReviseCustomSowTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviseCustomSowTemplate>>,
        TError,
        ReviseCustomSowTemplateMutationVariables,
        TContext
      > => {
      return useMutation(getReviseCustomSowTemplateMutationOptions(options));
    }

export const getPrepareSowTemplateCopyUrl = (id: string,) => {




  return `/api/sow/templates/${id}/copy-draft`
}

/**
 * Prepare an unsaved custom-template draft copied from an existing built-in or custom version for administrator review. Performs no write.
 */
export const prepareSowTemplateCopy = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SowCustomTemplateDraft> => {

  return customFetch<SowCustomTemplateDraft>(getPrepareSowTemplateCopyUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getPrepareSowTemplateCopyQueryKey = (id: string,) => {
    return [
    `/api/sow/templates/${id}/copy-draft`
    ] as const;
    }


export const getPrepareSowTemplateCopyQueryOptions = <TData = Awaited<ReturnType<typeof prepareSowTemplateCopy>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof prepareSowTemplateCopy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getPrepareSowTemplateCopyQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof prepareSowTemplateCopy>>> = ({ signal }) => prepareSowTemplateCopy(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof prepareSowTemplateCopy>>, TError, TData> & { queryKey: QueryKey }
}

export type PrepareSowTemplateCopyQueryResult = NonNullable<Awaited<ReturnType<typeof prepareSowTemplateCopy>>>
export type PrepareSowTemplateCopyQueryError = unknown



export function usePrepareSowTemplateCopy<TData = Awaited<ReturnType<typeof prepareSowTemplateCopy>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof prepareSowTemplateCopy>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getPrepareSowTemplateCopyQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getInstallSowTemplateUrl = () => {




  return `/api/sow/templates/install`
}

export const installSowTemplate = async (installSowTemplateBody: InstallSowTemplateBody, options?: Parameters<typeof customFetch>[1]): Promise<SowTemplate> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowTemplate>(getInstallSowTemplateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(installSowTemplateBody)
  }
);}





export const getInstallSowTemplateMutationKey = () => ['installSowTemplate'] as const;

export const getInstallSowTemplateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof installSowTemplate>>, TError,InstallSowTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof installSowTemplate>>, TError,InstallSowTemplateMutationVariables, TContext> => {

const mutationKey = getInstallSowTemplateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof installSowTemplate>>, InstallSowTemplateMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  installSowTemplate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type InstallSowTemplateMutationResult = NonNullable<Awaited<ReturnType<typeof installSowTemplate>>>
    export type InstallSowTemplateMutationBody = InstallSowTemplateBody
    export type InstallSowTemplateMutationError = unknown
    export type InstallSowTemplateMutationVariables = {data: InstallSowTemplateBody}

    export const useInstallSowTemplate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof installSowTemplate>>, TError,InstallSowTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof installSowTemplate>>,
        TError,
        InstallSowTemplateMutationVariables,
        TContext
      > => {
      return useMutation(getInstallSowTemplateMutationOptions(options));
    }

export const getGetSowTemplateUrl = (id: string,) => {




  return `/api/sow/templates/${id}`
}

export const getSowTemplate = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SowTemplate> => {

  return customFetch<SowTemplate>(getGetSowTemplateUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSowTemplateQueryKey = (id: string,) => {
    return [
    `/api/sow/templates/${id}`
    ] as const;
    }


export const getGetSowTemplateQueryOptions = <TData = Awaited<ReturnType<typeof getSowTemplate>>, TError = unknown>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowTemplate>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSowTemplateQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSowTemplate>>> = ({ signal }) => getSowTemplate(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSowTemplate>>, TError, TData> & { queryKey: QueryKey }
}

export type GetSowTemplateQueryResult = NonNullable<Awaited<ReturnType<typeof getSowTemplate>>>
export type GetSowTemplateQueryError = unknown



export function useGetSowTemplate<TData = Awaited<ReturnType<typeof getSowTemplate>>, TError = unknown>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getSowTemplate>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetSowTemplateQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviseSowTemplateUrl = (id: string,) => {




  return `/api/sow/templates/${id}`
}

export const reviseSowTemplate = async (id: string,
    reviseSowTemplateBody: ReviseSowTemplateBody, options?: Parameters<typeof customFetch>[1]): Promise<SowTemplate> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SowTemplate>(getReviseSowTemplateUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviseSowTemplateBody)
  }
);}





export const getReviseSowTemplateMutationKey = () => ['reviseSowTemplate'] as const;

export const getReviseSowTemplateMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviseSowTemplate>>, TError,ReviseSowTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviseSowTemplate>>, TError,ReviseSowTemplateMutationVariables, TContext> => {

const mutationKey = getReviseSowTemplateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviseSowTemplate>>, ReviseSowTemplateMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reviseSowTemplate(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviseSowTemplateMutationResult = NonNullable<Awaited<ReturnType<typeof reviseSowTemplate>>>
    export type ReviseSowTemplateMutationBody = ReviseSowTemplateBody
    export type ReviseSowTemplateMutationError = unknown
    export type ReviseSowTemplateMutationVariables = {id: string;data: ReviseSowTemplateBody}

    export const useReviseSowTemplate = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviseSowTemplate>>, TError,ReviseSowTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof reviseSowTemplate>>,
        TError,
        ReviseSowTemplateMutationVariables,
        TContext
      > => {
      return useMutation(getReviseSowTemplateMutationOptions(options));
    }

export const getValidateSowFactsUrl = () => {




  return `/api/sow/validate`
}

export const validateSowFacts = async (sowBindingInput: SowBindingInput, options?: Parameters<typeof customFetch>[1]): Promise<ValidateSowFacts200> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ValidateSowFacts200>(getValidateSowFactsUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sowBindingInput)
  }
);}





export const getValidateSowFactsMutationKey = () => ['validateSowFacts'] as const;

export const getValidateSowFactsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof validateSowFacts>>, TError,ValidateSowFactsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof validateSowFacts>>, TError,ValidateSowFactsMutationVariables, TContext> => {

const mutationKey = getValidateSowFactsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof validateSowFacts>>, ValidateSowFactsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  validateSowFacts(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ValidateSowFactsMutationResult = NonNullable<Awaited<ReturnType<typeof validateSowFacts>>>
    export type ValidateSowFactsMutationBody = SowBindingInput
    export type ValidateSowFactsMutationError = unknown
    export type ValidateSowFactsMutationVariables = {data: SowBindingInput}

    export const useValidateSowFacts = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof validateSowFacts>>, TError,ValidateSowFactsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof validateSowFacts>>,
        TError,
        ValidateSowFactsMutationVariables,
        TContext
      > => {
      return useMutation(getValidateSowFactsMutationOptions(options));
    }

export const getListResearchGroundingUrl = (params?: ListResearchGroundingParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/research/grounding?${stringifiedParams}` : `/api/research/grounding`
}

export const listResearchGrounding = async (params?: ListResearchGroundingParams, options?: Parameters<typeof customFetch>[1]): Promise<ResearchGroundingPage> => {

  return customFetch<ResearchGroundingPage>(getListResearchGroundingUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListResearchGroundingQueryKey = (params?: ListResearchGroundingParams,) => {
    return [
    `/api/research/grounding`, ...(params ? [params] : [])
    ] as const;
    }


export const getListResearchGroundingQueryOptions = <TData = Awaited<ReturnType<typeof listResearchGrounding>>, TError = unknown>(params?: ListResearchGroundingParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchGrounding>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListResearchGroundingQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listResearchGrounding>>> = ({ signal }) => listResearchGrounding(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listResearchGrounding>>, TError, TData> & { queryKey: QueryKey }
}

export type ListResearchGroundingQueryResult = NonNullable<Awaited<ReturnType<typeof listResearchGrounding>>>
export type ListResearchGroundingQueryError = unknown



export function useListResearchGrounding<TData = Awaited<ReturnType<typeof listResearchGrounding>>, TError = unknown>(
 params?: ListResearchGroundingParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof listResearchGrounding>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getListResearchGroundingQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSaveResearchGroundingUrl = () => {




  return `/api/research/grounding`
}

export const saveResearchGrounding = async (researchGroundingSave: ResearchGroundingSave, options?: Parameters<typeof customFetch>[1]): Promise<ResearchControlReceipt> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ResearchControlReceipt>(getSaveResearchGroundingUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(researchGroundingSave)
  }
);}





export const getSaveResearchGroundingMutationKey = () => ['saveResearchGrounding'] as const;

export const getSaveResearchGroundingMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveResearchGrounding>>, TError,SaveResearchGroundingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof saveResearchGrounding>>, TError,SaveResearchGroundingMutationVariables, TContext> => {

const mutationKey = getSaveResearchGroundingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof saveResearchGrounding>>, SaveResearchGroundingMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  saveResearchGrounding(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveResearchGroundingMutationResult = NonNullable<Awaited<ReturnType<typeof saveResearchGrounding>>>
    export type SaveResearchGroundingMutationBody = ResearchGroundingSave
    export type SaveResearchGroundingMutationError = unknown
    export type SaveResearchGroundingMutationVariables = {data: ResearchGroundingSave}

    export const useSaveResearchGrounding = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveResearchGrounding>>, TError,SaveResearchGroundingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof saveResearchGrounding>>,
        TError,
        SaveResearchGroundingMutationVariables,
        TContext
      > => {
      return useMutation(getSaveResearchGroundingMutationOptions(options));
    }

export const getGetResearchControlOutcomeUrl = (requestId: string,) => {




  return `/api/research/controls/outcomes/${requestId}`
}

export const getResearchControlOutcome = async (requestId: string, options?: Parameters<typeof customFetch>[1]): Promise<ResearchControlReceipt> => {

  return customFetch<ResearchControlReceipt>(getGetResearchControlOutcomeUrl(requestId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetResearchControlOutcomeQueryKey = (requestId: string,) => {
    return [
    `/api/research/controls/outcomes/${requestId}`
    ] as const;
    }


export const getGetResearchControlOutcomeQueryOptions = <TData = Awaited<ReturnType<typeof getResearchControlOutcome>>, TError = unknown>(requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getResearchControlOutcome>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetResearchControlOutcomeQueryKey(requestId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getResearchControlOutcome>>> = ({ signal }) => getResearchControlOutcome(requestId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: requestId !== null && requestId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getResearchControlOutcome>>, TError, TData> & { queryKey: QueryKey }
}

export type GetResearchControlOutcomeQueryResult = NonNullable<Awaited<ReturnType<typeof getResearchControlOutcome>>>
export type GetResearchControlOutcomeQueryError = unknown



export function useGetResearchControlOutcome<TData = Awaited<ReturnType<typeof getResearchControlOutcome>>, TError = unknown>(
 requestId: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getResearchControlOutcome>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getGetResearchControlOutcomeQueryOptions(requestId,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getExportResearchPromptsUrl = () => {




  return `/api/research/prompts/transfer`
}

export const exportResearchPrompts = async ( options?: Parameters<typeof customFetch>[1]): Promise<ResearchPromptTransfer> => {

  return customFetch<ResearchPromptTransfer>(getExportResearchPromptsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getExportResearchPromptsQueryKey = () => {
    return [
    `/api/research/prompts/transfer`
    ] as const;
    }


export const getExportResearchPromptsQueryOptions = <TData = Awaited<ReturnType<typeof exportResearchPrompts>>, TError = unknown>( options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof exportResearchPrompts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getExportResearchPromptsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof exportResearchPrompts>>> = ({ signal }) => exportResearchPrompts({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof exportResearchPrompts>>, TError, TData> & { queryKey: QueryKey }
}

export type ExportResearchPromptsQueryResult = NonNullable<Awaited<ReturnType<typeof exportResearchPrompts>>>
export type ExportResearchPromptsQueryError = unknown



export function useExportResearchPrompts<TData = Awaited<ReturnType<typeof exportResearchPrompts>>, TError = unknown>(
  options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof exportResearchPrompts>>, TError, TData>, request?: SecondParameter<typeof customFetch>}

 ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } {

  const queryOptions = getExportResearchPromptsQueryOptions(options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPreviewResearchPromptTransferUrl = () => {




  return `/api/research/prompts/transfer`
}

export const previewResearchPromptTransfer = async (researchPromptTransfer: ResearchPromptTransfer, options?: Parameters<typeof customFetch>[1]): Promise<ResearchPromptTransferPreview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ResearchPromptTransferPreview>(getPreviewResearchPromptTransferUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(researchPromptTransfer)
  }
);}





export const getPreviewResearchPromptTransferMutationKey = () => ['previewResearchPromptTransfer'] as const;

export const getPreviewResearchPromptTransferMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewResearchPromptTransfer>>, TError,PreviewResearchPromptTransferMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewResearchPromptTransfer>>, TError,PreviewResearchPromptTransferMutationVariables, TContext> => {

const mutationKey = getPreviewResearchPromptTransferMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewResearchPromptTransfer>>, PreviewResearchPromptTransferMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  previewResearchPromptTransfer(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewResearchPromptTransferMutationResult = NonNullable<Awaited<ReturnType<typeof previewResearchPromptTransfer>>>
    export type PreviewResearchPromptTransferMutationBody = ResearchPromptTransfer
    export type PreviewResearchPromptTransferMutationError = unknown
    export type PreviewResearchPromptTransferMutationVariables = {data: ResearchPromptTransfer}

    export const usePreviewResearchPromptTransfer = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewResearchPromptTransfer>>, TError,PreviewResearchPromptTransferMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof previewResearchPromptTransfer>>,
        TError,
        PreviewResearchPromptTransferMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewResearchPromptTransferMutationOptions(options));
    }

export const getApplyResearchPromptTransferUrl = () => {




  return `/api/research/prompts/transfer/apply`
}

export const applyResearchPromptTransfer = async (researchPromptTransferApply: ResearchPromptTransferApply, options?: Parameters<typeof customFetch>[1]): Promise<ResearchControlReceipt> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ResearchControlReceipt>(getApplyResearchPromptTransferUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(researchPromptTransferApply)
  }
);}





export const getApplyResearchPromptTransferMutationKey = () => ['applyResearchPromptTransfer'] as const;

export const getApplyResearchPromptTransferMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyResearchPromptTransfer>>, TError,ApplyResearchPromptTransferMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof applyResearchPromptTransfer>>, TError,ApplyResearchPromptTransferMutationVariables, TContext> => {

const mutationKey = getApplyResearchPromptTransferMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof applyResearchPromptTransfer>>, ApplyResearchPromptTransferMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  applyResearchPromptTransfer(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApplyResearchPromptTransferMutationResult = NonNullable<Awaited<ReturnType<typeof applyResearchPromptTransfer>>>
    export type ApplyResearchPromptTransferMutationBody = ResearchPromptTransferApply
    export type ApplyResearchPromptTransferMutationError = unknown
    export type ApplyResearchPromptTransferMutationVariables = {data: ResearchPromptTransferApply}

    export const useApplyResearchPromptTransfer = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyResearchPromptTransfer>>, TError,ApplyResearchPromptTransferMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 ): UseMutationResult<
        Awaited<ReturnType<typeof applyResearchPromptTransfer>>,
        TError,
        ApplyResearchPromptTransferMutationVariables,
        TContext
      > => {
      return useMutation(getApplyResearchPromptTransferMutationOptions(options));
    }
